// src/index.tsx
import L4 from "leaflet";

// src/components/BarGraphic/index.tsx
import { Fragment as Fragment6 } from "react";
import {
  ResponsiveBar
} from "@nivo/bar";

// src/components/BarGraphic/Label/index.tsx
import { useTheme } from "@chakra-ui/react";

// src/components/BarGraphic/Label/utils.ts
var getToLocaleString = (value) => {
  return value.toLocaleString(void 0, {
    minimumFractionDigits: 0,
    style: "currency",
    currency: "USD"
  });
};
var getFixedUniversalString = (value) => {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};
var formattedTextValue = (value, signOnLabel) => {
  return signOnLabel ? getToLocaleString(value) : getFixedUniversalString(value);
};

// src/components/BarGraphic/Label/index.tsx
import { Fragment, jsx } from "react/jsx-runtime";
var Label = (layout, signOnLabel, startAtMode) => ({ bars }) => {
  const labelYMargin = 17;
  const labelXMargin = 3;
  const chakraTheme2 = useTheme();
  const offset = startAtMode ? 17 : 0;
  const firstVisibleBarIndex = bars.length / 2;
  const getLabelColor = (index, height) => {
    if (startAtMode) {
      const barHeightLimitToRenderLabel = 17;
      if (height < barHeightLimitToRenderLabel) return "#ffffff00";
      if (index !== firstVisibleBarIndex) {
        return chakraTheme2.colors.white;
      }
    }
    return chakraTheme2.colors.mediumGrey;
  };
  const formatTextForStartAtMode = (data) => {
    return data.data.percentage;
  };
  const shouldRenderPercantage = (startAtMode2, i) => {
    return Boolean(
      startAtMode2 && i > firstVisibleBarIndex && i < bars.length - 1
    );
  };
  return /* @__PURE__ */ jsx(Fragment, { children: bars.map(({ data, width, height, x, y }, i) => {
    const value = data?.value && Number(data?.value);
    if (data.id === "value" && (value || value === 0)) {
      let textValue;
      if (shouldRenderPercantage(startAtMode, i)) {
        textValue = formatTextForStartAtMode(data);
      } else {
        textValue = formattedTextValue(value, signOnLabel);
      }
      return /* @__PURE__ */ jsx(
        "g",
        {
          transform: `translate(${x + (layout === "horizontal" ? width + labelXMargin + textValue.toString().length : 0)}, ${y + (layout === "horizontal" ? 0 : -labelYMargin)})`,
          "data-testid": `bar-graphic--label-${data.indexValue}`,
          width,
          children: /* @__PURE__ */ jsx(
            "text",
            {
              x: layout === "horizontal" ? height / 2 + labelXMargin : width / 2,
              y: layout === "horizontal" ? height / 2 : labelYMargin / 2 + offset,
              textAnchor: "middle",
              alignmentBaseline: "central",
              style: {
                fontFamily: chakraTheme2.fonts.semiBold,
                fill: getLabelColor(i, height),
                fontSize: 10
              },
              "data-testid": `bar-graphic--label-text-${data.value}`,
              children: textValue
            }
          )
        },
        `${data.indexValue}-${i}`
      );
    }
    return null;
  }) });
};
var Label_default = Label;

// src/components/BarGraphic/Line/index.tsx
import { Fragment as Fragment2 } from "react";
import { useTheme as useTheme4 } from "@chakra-ui/react";

// src/components/Tooltip/Tooltip.tsx
import { Box, Tooltip as TooptipLib, useTheme as useTheme2 } from "@chakra-ui/react";
import { jsx as jsx2 } from "react/jsx-runtime";
var Tooltip = ({
  placement,
  label,
  padding,
  labelProps = {},
  dataTestId,
  ...props
}) => {
  const chakraTheme2 = useTheme2();
  const { mediumGrey, white } = chakraTheme2.colors;
  return /* @__PURE__ */ jsx2(
    TooptipLib,
    {
      borderRadius: "3px",
      fontSize: "14px",
      lineHeight: "19px",
      textAlign: "left",
      zIndex: 60,
      color: mediumGrey,
      boxShadow: chakraTheme2.shadow.mediumShadow,
      placement: placement || "top",
      padding,
      bg: chakraTheme2.colors?.mediumGrey,
      "data-testid": dataTestId || "cj-tooltip",
      maxWidth: "inherit",
      hasArrow: true,
      label: /* @__PURE__ */ jsx2(
        Box,
        {
          as: "div",
          py: "8px",
          px: "16px",
          fontFamily: chakraTheme2.fonts.interRegular,
          color: white,
          textAlign: "left",
          ...labelProps,
          children: label
        }
      ),
      ...props
    }
  );
};
var Tooltip_default = Tooltip;

// src/components/BarGraphic/Tooltip/index.tsx
import { Box as Box2, useTheme as useTheme3 } from "@chakra-ui/react";
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
var Tooltip2 = ({ label, value }) => {
  const theme = useTheme3();
  const formattedValue = value.toLocaleString(void 0, {
    minimumFractionDigits: 0,
    style: "currency",
    currency: "USD"
  });
  return /* @__PURE__ */ jsxs(
    Box2,
    {
      minHeight: "38px",
      minWidth: "110px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "space-around",
      backgroundColor: theme.colors.white,
      borderRadius: "3px",
      "data-testid": "tooltip--tooltip-container",
      children: [
        /* @__PURE__ */ jsx3(Box2, { fontSize: "10px", color: theme.colors.mediumGrey, children: label }),
        /* @__PURE__ */ jsx3(
          Box2,
          {
            fontSize: "10px",
            fontFamily: theme.fonts.semiBold,
            color: theme.colors.mediumGrey,
            "data-testid": "tooltip--value-container",
            children: formattedValue
          }
        )
      ]
    }
  );
};
var Tooltip_default2 = Tooltip2;

// src/components/BarGraphic/Line/index.tsx
import { Fragment as Fragment3, jsx as jsx4, jsxs as jsxs2 } from "react/jsx-runtime";
var Line = (CustomTooltip2, layout, benchmarkLineWidth = 2) => ({ bars, xScale, yScale }) => {
  const extraBarWidth = 5;
  const chakraTheme2 = useTheme4();
  const isHorizontal = layout === "horizontal";
  return /* @__PURE__ */ jsx4(Fragment3, { children: bars.map((bar) => {
    const benchmarkValue = bar?.data?.data?.benchmark && Number(bar?.data?.data?.benchmark);
    if (benchmarkValue || benchmarkValue === 0) {
      const benchmarkYPosition = isHorizontal ? yScale(bar.data.indexValue || 0) : yScale(benchmarkValue);
      const benchmarkXPosition = isHorizontal ? xScale(benchmarkValue) : xScale(bar.data.indexValue || 0);
      const barLong = isHorizontal ? bar.height : bar.width;
      const x1 = isHorizontal ? benchmarkXPosition : benchmarkXPosition - extraBarWidth;
      const y1 = isHorizontal ? benchmarkYPosition - extraBarWidth : benchmarkYPosition;
      const x2 = isHorizontal ? benchmarkXPosition : benchmarkXPosition + barLong + extraBarWidth;
      const y2 = isHorizontal ? benchmarkYPosition + barLong + extraBarWidth : benchmarkYPosition;
      const extraWidth = isHorizontal && bar.width < 5 ? 8 : 0;
      const extraHeight = !isHorizontal && bar.height < 5 ? 8 : 0;
      const defaultTooltipProps = {
        placement: "top",
        bg: chakraTheme2.colors?.white
      };
      const tooltipProps = CustomTooltip2 ? {
        label: /* @__PURE__ */ jsx4(CustomTooltip2, { ...bar }),
        ...defaultTooltipProps
      } : {
        label: /* @__PURE__ */ jsx4(
          Tooltip_default2,
          {
            value: benchmarkValue,
            label: "County Benchmark"
          }
        ),
        ...defaultTooltipProps
      };
      return /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsx4(Tooltip_default, { ...tooltipProps, children: /* @__PURE__ */ jsx4(
          "line",
          {
            "data-testid": `bar-graphic--line-${bar.data.indexValue}`,
            x1,
            y1,
            x2,
            y2,
            stroke: chakraTheme2.colors.gold,
            strokeWidth: benchmarkLineWidth
          }
        ) }),
        /* @__PURE__ */ jsx4(Tooltip_default, { ...tooltipProps, children: /* @__PURE__ */ jsx4(
          "rect",
          {
            "data-testid": "bar-icon--rect",
            x: bar.x,
            y: bar.y,
            width: bar.width + extraWidth,
            height: bar.height + extraHeight,
            rx: 5,
            fill: "transparent"
          }
        ) })
      ] }, bar.key);
    } else {
      return /* @__PURE__ */ jsx4(Fragment2, {}, bar.key);
    }
  }) });
};
var Line_default = Line;

// src/components/BarGraphic/Tick/index.tsx
import { useTheme as useTheme5 } from "@chakra-ui/react";
import { jsx as jsx5 } from "react/jsx-runtime";
var Tick = (layout, monetaryTick = true) => (tick) => {
  const value = monetaryTick ? tick.value.toLocaleString(void 0, {
    minimumFractionDigits: 0,
    style: "currency",
    currency: "USD"
  }) : tick.value;
  const chakraTheme2 = useTheme5();
  const isHorizontal = layout === "horizontal";
  return /* @__PURE__ */ jsx5(
    "g",
    {
      transform: `translate(${tick.x + (isHorizontal ? 0 : 10)},${tick.y + (isHorizontal ? 10 : 0)})`,
      children: /* @__PURE__ */ jsx5(
        "text",
        {
          dominantBaseline: "middle",
          style: {
            fill: chakraTheme2.colors.lightGrey,
            fontSize: 10
          },
          children: value
        }
      )
    }
  );
};
var Tick_default = Tick;

// src/components/BarGraphic/CustomBar/index.tsx
import { useTheme as useTheme6 } from "@chakra-ui/react";

// src/components/BarGraphic/BarIcon/index.tsx
import { useTooltip } from "@nivo/tooltip";
import { Fragment as Fragment4, jsx as jsx6, jsxs as jsxs3 } from "react/jsx-runtime";
var BarIcon = ({
  barColor,
  diagonalStripes,
  diagonalStripeColor,
  x = 0,
  y = 0,
  height = 100,
  width = 10,
  tooltipValue,
  layout
}) => {
  const { showTooltipFromEvent, hideTooltip } = useTooltip();
  const isLayoutHorizontal = layout === "horizontal";
  return /* @__PURE__ */ jsx6("svg", { children: diagonalStripes ? /* @__PURE__ */ jsxs3(Fragment4, { children: [
    /* @__PURE__ */ jsxs3(
      "pattern",
      {
        id: "diagonalStripes",
        width: "10",
        height: "10",
        patternTransform: "rotate(45 0 0)",
        patternUnits: "userSpaceOnUse",
        children: [
          /* @__PURE__ */ jsx6(
            "line",
            {
              x1: "10",
              y1: "10",
              x2: "0",
              y2: "0",
              stroke: diagonalStripeColor,
              strokeWidth: "15"
            }
          ),
          /* @__PURE__ */ jsx6(
            "line",
            {
              x1: "0",
              y1: "10",
              x2: "0",
              y2: "0",
              stroke: barColor,
              strokeWidth: "10"
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx6(
      "rect",
      {
        "data-testid": "bar-icon--lines-rect",
        x,
        y,
        width,
        height,
        rx: 5,
        fill: "url(#diagonalStripes)",
        onMouseEnter: (event) => tooltipValue && showTooltipFromEvent(
          /* @__PURE__ */ jsx6(Tooltip_default2, { label: "Last Year Value:", value: tooltipValue }),
          event
        ),
        onMouseLeave: () => hideTooltip(),
        onMouseMove: (event) => tooltipValue && showTooltipFromEvent(
          /* @__PURE__ */ jsx6(Tooltip_default2, { label: "Last Year Value:", value: tooltipValue }),
          event
        )
      }
    )
  ] }) : /* @__PURE__ */ jsx6(
    "rect",
    {
      "data-testid": "bar-icon--rect",
      x: x - (isLayoutHorizontal ? 0 : 3),
      y: y - (isLayoutHorizontal ? -2 : 0),
      width: width + (isLayoutHorizontal ? 0 : 13),
      height: height + (isLayoutHorizontal ? 13 : 0),
      rx: 5,
      fill: barColor
    }
  ) });
};

// src/components/BarGraphic/CustomBar/index.tsx
import { Fragment as Fragment5, jsx as jsx7 } from "react/jsx-runtime";
var CustomBarComponent = (layout) => ({ bar: { data, x, y, height, width } }) => {
  const chakraTheme2 = useTheme6();
  const isHorizontal = layout === "horizontal";
  return /* @__PURE__ */ jsx7(Fragment5, { children: data.id === "lastYear" ? /* @__PURE__ */ jsx7(
    BarIcon,
    {
      barColor: chakraTheme2.colors.lightGrey,
      diagonalStripes: true,
      diagonalStripeColor: chakraTheme2.colors.grey7,
      x,
      y,
      height,
      width: isHorizontal ? width : void 0,
      tooltipValue: data.data.lastYear,
      layout
    }
  ) : /* @__PURE__ */ jsx7(
    BarIcon,
    {
      barColor: "#3690C0",
      x,
      y,
      height,
      width: isHorizontal ? width : void 0,
      layout
    }
  ) });
};
var CustomBar_default = CustomBarComponent;

// src/components/BarGraphic/index.tsx
import { useTheme as useTheme7 } from "@chakra-ui/react";

// src/components/BarGraphic/config.ts
var DIFFERENCE_BAR_GRAPH_DEFS = [
  {
    id: "initial",
    type: "patternDots",
    background: "#EAEBEC",
    color: "#000000",
    size: 0,
    padding: 1,
    stagger: true
  },
  {
    id: "dots",
    type: "patternDots",
    background: "#ffffff00",
    color: "#ffffff",
    size: 0,
    padding: 1,
    stagger: true
  },
  {
    id: "decrement",
    type: "patternDots",
    background: "#AE2824",
    color: "#000000",
    size: 0,
    padding: 1,
    stagger: true
  },
  {
    id: "increment",
    type: "patternDots",
    background: "#39C671",
    color: "#000000",
    size: 0,
    padding: 1,
    stagger: true
  },
  {
    id: "final",
    type: "patternDots",
    background: "#3992C6",
    color: "#000000",
    size: 0,
    padding: 1,
    stagger: true
  }
];

// src/components/BarGraphic/index.tsx
import { jsx as jsx8 } from "react/jsx-runtime";
var BarGraphic = ({
  info,
  hasBenchmark = false,
  benchmarkTooltip,
  hasLabel = false,
  margin = {
    top: 25,
    right: 50,
    bottom: 25,
    left: 5
  },
  barsColor,
  lastYearLegend = void 0,
  customBar = void 0,
  dataKeys = ["value"],
  groupMode = "grouped",
  layout = "vertical",
  gridYValues = 5,
  gridXValues = 0,
  axisBottom = null,
  axisRight = null,
  axisLeft = null,
  axisTop = null,
  enableGridX,
  enableGridY,
  padding = 0.5,
  CustomHTick,
  signOnLabel = true,
  signOnTick = true,
  startAtMode = false,
  borderRadius = 5,
  benchmarkLineWidth = 2,
  showAxes = true,
  showGrid = true,
  defs = [],
  fill = []
}) => {
  const chakraTheme2 = useTheme7();
  const { data, max } = info || {};
  const isLayoutVertical = layout === "vertical";
  const customLegend = {
    dataFrom: "keys",
    data: [
      {
        id: "lastYear",
        label: "Last Year Value"
      }
    ],
    symbolShape: () => /* @__PURE__ */ jsx8(
      BarIcon,
      {
        barColor: chakraTheme2.colors.lightGrey,
        diagonalStripes: true,
        diagonalStripeColor: chakraTheme2.colors.grey7,
        height: 20,
        width: 20
      }
    ),
    anchor: "top-right",
    justify: false,
    translateX: 20,
    translateY: -50,
    itemWidth: 100,
    itemHeight: 20,
    symbolSize: 20,
    direction: "column"
  };
  if (startAtMode && !dataKeys.includes("startAt")) {
    dataKeys = ["startAt", ...dataKeys];
    const decrements = data.reduce((acc, curr, index) => {
      if (index > 0) {
        const differenceBetweenPreviousAndCurrent = curr.difference;
        if (differenceBetweenPreviousAndCurrent < 0) {
          acc = [...acc, curr.id];
        }
      }
      return acc;
    }, []);
    const increments = data.reduce((acc, curr, index) => {
      if (index > 0) {
        const differenceBetweenPreviousAndCurrent = curr.difference;
        if (differenceBetweenPreviousAndCurrent >= 0) {
          acc = [...acc, curr.id];
        }
      }
      return acc;
    }, []);
    defs = [...DIFFERENCE_BAR_GRAPH_DEFS, ...defs];
    fill = [
      {
        match: (d) => d.data.id === "startAt",
        id: "dots"
      },
      {
        match: (d) => d.data.id === "value" && d.data.indexValue === data[data.length - 1].id,
        id: "final"
      },
      {
        match: (d) => d.data.id === "value" && d.data.indexValue === data[0].id,
        id: "initial"
      },
      {
        match: (d) => d.data.id === "value" && decrements.includes(d.data.indexValue),
        id: "decrement"
      },
      {
        match: (d) => d.data.id === "value" && increments.includes(d.data.indexValue),
        id: "increment"
      },
      ...fill
    ];
  }
  return /* @__PURE__ */ jsx8(
    ResponsiveBar,
    {
      data,
      keys: dataKeys,
      barComponent: customBar ? CustomBar_default(layout) : void 0,
      groupMode,
      maxValue: max,
      padding,
      margin,
      layout,
      animate: false,
      indexBy: "id",
      enableLabel: false,
      colors: barsColor,
      borderRadius,
      enableGridX,
      enableGridY,
      gridYValues,
      gridXValues,
      defs,
      fill,
      axisRight: axisRight || (isLayoutVertical ? {
        renderTick: Tick_default(layout),
        tickValues: 6,
        tickSize: 0
      } : null),
      axisLeft: axisLeft || (!isLayoutVertical ? CustomHTick ? null : { tickSize: 0 } : null),
      axisTop,
      axisBottom: axisBottom || (!isLayoutVertical ? {
        renderTick: Tick_default(layout, signOnTick),
        tickValues: 6,
        tickSize: 0
      } : { tickSize: 0 }),
      tooltip: () => /* @__PURE__ */ jsx8(Fragment6, {}),
      legends: lastYearLegend ? [customLegend] : void 0,
      layers: [
        ...showGrid ? ["grid"] : [],
        ...showAxes ? ["axes"] : [],
        "markers",
        "bars",
        "legends",
        (props) => hasBenchmark ? Line_default(benchmarkTooltip, layout, benchmarkLineWidth)(props) : null,
        (props) => hasLabel ? Label_default(layout, signOnLabel, startAtMode)(props) : null,
        (props) => CustomHTick && !isLayoutVertical ? CustomHTick(props) : null
      ]
    }
  );
};
var BarGraphic_default = BarGraphic;

// src/components/Breadcrumbs/index.tsx
import { Fragment as Fragment8 } from "react";

// src/components/Breadcrumbs/config.ts
var NEW_BREADCRUMB_DIVIDER = ">";
var BREADCRUMB_DIVIDER_SIZE = 22;

// src/components/Breadcrumbs/index.tsx
import { Flex, Text as Text2, useTheme as useTheme9 } from "@chakra-ui/react";

// src/components/Breadcrumbs/BreadcrumbText/BreadcrumbText.tsx
import { Text, useTheme as useTheme8 } from "@chakra-ui/react";
import { Fragment as Fragment7 } from "react";

// src/hooks/useEllipsisTooltip/index.ts
import { useState, useRef, useLayoutEffect } from "react";
var useEllipsisTooltip = () => {
  const ref = useRef(null);
  const [overflowActive, setOverflowActive] = useState(false);
  const isOverFlowActive = () => {
    const element = ref.current;
    const { offsetWidth, scrollWidth } = element;
    if (element) {
      setOverflowActive(offsetWidth < scrollWidth);
    }
  };
  useLayoutEffect(() => {
    window.addEventListener("resize", isOverFlowActive);
    return () => {
      window.removeEventListener("resize", isOverFlowActive);
    };
  }, []);
  useLayoutEffect(() => {
    isOverFlowActive();
  }, [ref?.current?.offsetWidth]);
  return [ref, overflowActive];
};

// src/components/Breadcrumbs/BreadcrumbText/BreadcrumbText.tsx
import { jsx as jsx9 } from "react/jsx-runtime";
var BreadcrumbText = ({
  breadcrumb,
  handleClick,
  index,
  isActive,
  activeTextColor,
  width,
  useArcadiaColors
}) => {
  const [breadcrumbTextRef, overflowActive] = useEllipsisTooltip();
  const theme = useTheme8();
  const hasTooltip = overflowActive;
  const WrapperComponent = hasTooltip ? Tooltip_default : Fragment7;
  const wrapperComponentProps = hasTooltip ? {
    label: breadcrumb.label || null,
    placement: "top",
    dataTestId: `${breadcrumb.label}-${index}`
  } : {};
  const textColor = useArcadiaColors ? theme.colors.arcadiaPurple : theme.colors.arcadiaMainGrey;
  return /* @__PURE__ */ jsx9(WrapperComponent, { ...wrapperComponentProps, children: /* @__PURE__ */ jsx9(
    Text,
    {
      textStyle: isActive ? "inter-regular-14-17" : "inter-semiBold-14-17",
      ref: breadcrumbTextRef,
      color: isActive ? activeTextColor || textColor : textColor,
      cursor: isActive ? "default" : "pointer",
      onClick: () => !isActive && handleClick && handleClick(index, breadcrumb),
      "data-testid": `breadcrumb-text${isActive ? "-active" : ""}`,
      width,
      textOverflow: "ellipsis",
      overflow: "hidden",
      whiteSpace: "nowrap",
      maxWidth: "fit-content",
      flexGrow: 2,
      children: breadcrumb.label
    }
  ) });
};
var BreadcrumbText_default = BreadcrumbText;

// src/components/Breadcrumbs/index.tsx
import { jsx as jsx10, jsxs as jsxs4 } from "react/jsx-runtime";
var Breadcrumbs = ({
  breadcrumbs,
  updateBreadcrumb,
  breadcrumbActiveTextColor,
  useArcadiaColors = false
}) => {
  const theme = useTheme9();
  return /* @__PURE__ */ jsx10(Flex, { alignItems: "center", height: "100%", children: breadcrumbs.map((breadcrumb, index) => {
    const notLastBreadcrumbText = index !== breadcrumbs.length - 1;
    const textWidth = `calc(${100 / breadcrumbs.length}% - ${notLastBreadcrumbText ? BREADCRUMB_DIVIDER_SIZE : 0}px)`;
    if (notLastBreadcrumbText) {
      return /* @__PURE__ */ jsxs4(Fragment8, { children: [
        /* @__PURE__ */ jsx10(
          BreadcrumbText_default,
          {
            breadcrumb,
            handleClick: updateBreadcrumb,
            index,
            width: textWidth,
            useArcadiaColors
          }
        ),
        /* @__PURE__ */ jsx10(
          Text2,
          {
            textStyle: "regular-14-19",
            textAlign: "center",
            lineHeight: `${BREADCRUMB_DIVIDER_SIZE}px`,
            color: theme.colors.grey7,
            minWidth: `${BREADCRUMB_DIVIDER_SIZE}px`,
            maxWidth: `${BREADCRUMB_DIVIDER_SIZE}px`,
            children: NEW_BREADCRUMB_DIVIDER
          }
        )
      ] }, `${index}_${breadcrumb.label}`);
    } else {
      return /* @__PURE__ */ jsx10(
        BreadcrumbText_default,
        {
          breadcrumb,
          handleClick: updateBreadcrumb,
          index,
          width: textWidth,
          activeTextColor: breadcrumbActiveTextColor,
          isActive: true,
          useArcadiaColors
        },
        `${index}_${breadcrumb.label}`
      );
    }
  }) });
};
var Breadcrumbs_default = Breadcrumbs;

// src/components/Button/Button.tsx
import { Box as Box3, Button, useTheme as useTheme10 } from "@chakra-ui/react";
import { jsx as jsx11 } from "react/jsx-runtime";
var ButtonCJ = ({
  backgroundColor: backgroundColorProp,
  dataTestId,
  isDisabled,
  ...props
}) => {
  const chakraTheme2 = useTheme10();
  const { gold, lightGrey, white } = chakraTheme2.colors;
  const backgroundColor = isDisabled ? lightGrey : backgroundColorProp || gold;
  return /* @__PURE__ */ jsx11(Box3, { display: "flex", children: /* @__PURE__ */ jsx11(
    Button,
    {
      backgroundColor,
      fontFamily: chakraTheme2.fonts.semiBold,
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
      alignItems: "center",
      width: "105px",
      height: "29px",
      border: "none",
      borderRadius: "3px",
      color: white,
      fontSize: "14px",
      lineHeight: "19px",
      transition: "none",
      _hover: { backgroundColor },
      _focus: { backgroundColor },
      _active: { backgroundColor },
      isDisabled,
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? "not-allowed" : "pointer",
      "data-testid": dataTestId || "cj-button",
      ...props
    }
  ) });
};
var Button_default = ButtonCJ;

// src/components/Checkbox/Checkbox.tsx
import { Checkbox as CheckboxLib, Text as Text3, useTheme as useTheme11 } from "@chakra-ui/react";
import { useState as useState2 } from "react";
import { jsx as jsx12 } from "react/jsx-runtime";
var Checkbox = ({
  dataTestId = "cj-checkbox",
  dataTestIdText = "cj-checkbox-text",
  name,
  children,
  textProps,
  colorScheme,
  borderColor,
  hoveredBorderColor,
  borderRadius,
  ...props
}) => {
  const theme = useTheme11();
  const [isHovered, setIsHovered] = useState2(false);
  const borderC = borderColor || theme.colors.lightGrey;
  const hoveredBC = hoveredBorderColor || theme.colors.blueTwo;
  const colorBorder = isHovered ? hoveredBC : borderC;
  return /* @__PURE__ */ jsx12(
    CheckboxLib,
    {
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      colorScheme: colorScheme || "blueTwoScheme",
      sx: {
        'input[type="checkbox"] + span': {
          borderRadius: `${borderRadius || "3px"}`,
          border: "1px solid " + colorBorder,
          width: "14px",
          height: "14px"
        },
        'input[type="checkbox"]:checked + span': {
          border: "1px transparent"
        }
      },
      value: name,
      cursor: "pointer",
      marginBottom: "10px",
      marginLeft: 0,
      "data-testid": dataTestId,
      iconSize: "3px",
      ...props,
      children: /* @__PURE__ */ jsx12(
        Text3,
        {
          "data-testid": dataTestIdText,
          textStyle: "regular-12-16",
          height: "14px",
          width: "-webkit-max-content",
          ...textProps,
          children
        }
      )
    }
  );
};
var Checkbox_default = Checkbox;

// src/components/ComparisonBar/index.tsx
import {
  ResponsiveBar as ResponsiveBar2
} from "@nivo/bar";

// src/components/ComparisonBar/utils.ts
var DEFAULT_BASEBARPROPS = {
  radiusxAxis: "3",
  radiusyAxis: "3",
  textFontSize: "0.7rem",
  baseWidth: 50
};
var getRandomColor = () => {
  const letters = "23456789ABD";
  const color = "#" + Array.from({ length: 10 }, (_3, i) => i + 1).map(() => letters[Math.floor(Math.random() * 11)]).join();
  return color;
};
var getColor = (bar) => {
  if (!bar.data[`${bar.id}Color`]) getRandomColor();
  return bar.data[`${bar.id}Color`];
};
var calculateSVGProps = (groupedBars, baseBarProps, innerPadding) => {
  const svgProps = {
    width: baseBarProps?.baseWidth || DEFAULT_BASEBARPROPS.baseWidth,
    height: baseBarProps?.extraHeight || 0,
    x: 0,
    y: 0
  };
  if (groupedBars.length === 0) return svgProps;
  const offsetX = svgProps.height ? svgProps.height / 2 : 0;
  return {
    ...svgProps,
    height: groupedBars.reduce((prev, curr) => prev + curr.height, 0) + innerPadding + (baseBarProps?.extraHeight || 0),
    x: groupedBars.reduce((prev, curr) => prev.x < curr.x ? prev : curr).x,
    y: groupedBars.reduce((prev, curr) => prev.y < curr.y ? prev : curr).y - offsetX
  };
};
var getPercentageLabel = (groupedBars) => {
  const barWithPercentage = groupedBars.find(
    (bar) => bar.data.data.percentage !== void 0
  );
  if (!barWithPercentage) return "";
  return barWithPercentage.data.data.percentage;
};

// src/components/ComparisonBar/BaseBar/index.tsx
import { useTheme as useTheme12 } from "@chakra-ui/react";
import { Fragment as Fragment9, jsx as jsx13, jsxs as jsxs5 } from "react/jsx-runtime";
var BaseBar = ({
  bars,
  innerPadding = 0,
  baseBarProps
}) => {
  const chakraTheme2 = useTheme12();
  const groupedBarsArray = {};
  bars.forEach((bar) => {
    const {
      data: { indexValue }
    } = bar;
    if (!groupedBarsArray[indexValue]) groupedBarsArray[indexValue] = [];
    groupedBarsArray[indexValue].push(bar);
  });
  return /* @__PURE__ */ jsx13(Fragment9, { children: groupedBarsArray ? Object.keys(groupedBarsArray).map((key) => {
    const { width, height, x, y } = calculateSVGProps(
      groupedBarsArray[key],
      baseBarProps,
      innerPadding
    );
    const xPoint = x - (width || DEFAULT_BASEBARPROPS.baseWidth) - innerPadding;
    return /* @__PURE__ */ jsxs5("g", { transform: "translate(0,0)", children: [
      /* @__PURE__ */ jsx13(
        "rect",
        {
          "data-testid": `comparisonbar-chart-g-rect-${key}`,
          rx: baseBarProps?.radiusxAxis || DEFAULT_BASEBARPROPS.radiusxAxis,
          ry: baseBarProps?.radiusyAxis || DEFAULT_BASEBARPROPS.radiusyAxis,
          width,
          height,
          fill: chakraTheme2.colors.blueTwo,
          x: xPoint,
          y
        }
      ),
      /* @__PURE__ */ jsx13("svg", { width, height, x: xPoint, y, children: /* @__PURE__ */ jsx13(
        "text",
        {
          "data-testid": `comparisonbar-chart-g-text-${key}`,
          x: "50%",
          y: "50%",
          alignmentBaseline: "middle",
          textAnchor: "middle",
          fill: chakraTheme2.colors.white,
          fontFamily: chakraTheme2.fonts.semiBold,
          fontSize: baseBarProps?.textFontSize || DEFAULT_BASEBARPROPS.textFontSize,
          children: getPercentageLabel(groupedBarsArray[key])
        }
      ) })
    ] }, key);
  }) : null });
};
var BaseBar_default = BaseBar;

// src/components/ComparisonBar/index.tsx
import { Box as Box4 } from "@chakra-ui/react";
import { jsx as jsx14 } from "react/jsx-runtime";
var DEFAULT_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
var LABEL_COLOR = {
  from: "color",
  modifiers: [["opacity", 1]]
};
var ComparisonBar = ({
  data,
  width,
  height,
  backgroundColor,
  marginContainer,
  margin,
  enableGridX = false,
  enableGridY = false,
  axisLeft = null,
  axisBottom = null,
  axisTop = null,
  axisRight = null,
  groupMode = "grouped",
  layout = "horizontal",
  legends,
  keys,
  indexBy,
  borderColor,
  fill,
  defs,
  labelTextColor = LABEL_COLOR,
  innerPadding,
  baseBarProps,
  borderBarRadius = 3,
  paddingBetweenGroupBars = 0.2,
  labelFormat = (value) => `${value}`,
  label = (value) => `${value}`,
  ...props
}) => {
  return /* @__PURE__ */ jsx14(
    Box4,
    {
      width,
      height,
      backgroundColor,
      margin: marginContainer,
      "data-testid": "comparisonbar-chart",
      children: /* @__PURE__ */ jsx14(
        ResponsiveBar2,
        {
          data,
          keys,
          indexBy,
          borderRadius: borderBarRadius,
          colors: getColor,
          enableGridX,
          enableGridY,
          margin: margin || DEFAULT_MARGIN,
          padding: paddingBetweenGroupBars,
          innerPadding,
          groupMode,
          layout,
          valueScale: { type: "linear" },
          indexScale: { type: "band", round: true },
          defs,
          fill,
          borderColor,
          axisTop,
          axisRight,
          axisBottom,
          axisLeft,
          labelSkipWidth: 12,
          labelSkipHeight: 12,
          labelTextColor,
          labelFormat: (value) => labelFormat(value),
          legends,
          role: "application",
          layers: [
            "grid",
            "bars",
            (props2) => BaseBar_default({ ...props2, innerPadding, baseBarProps })
          ],
          label: (value) => label(value),
          ...props
        }
      )
    }
  );
};
var ComparisonBar_default = ComparisonBar;

// src/components/CSVModal/index.tsx
import {
  Box as Box5,
  Button as Button2,
  Flex as Flex3,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text as Text5,
  Textarea
} from "@chakra-ui/react";

// src/icons/AngleIcon/AngleIcon.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var AngleIcon = ({
  width = 14,
  height = 9,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "angle-icon"
}) => {
  const directionRotation = {
    up: "180deg",
    right: "270deg",
    down: "0deg",
    left: "90deg"
  };
  return /* @__PURE__ */ jsx15(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 14 9",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx15("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx15(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M6.307 8.203l-6.02-6.02A.98.98 0 111.674.797L7 6.123 12.326.797a.98.98 0 011.387 1.387l-6.02 6.02a.977.977 0 01-1.386 0z",
          fill: color
        }
      ) })
    }
  );
};
var AngleIcon_default = AngleIcon;

// src/icons/ArcadiaIcon/ArcadiaIcon.tsx
import { jsx as jsx16, jsxs as jsxs6 } from "react/jsx-runtime";
var ArcadiaIcon = ({
  width = 30,
  height = 29,
  margin = "0",
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "arcadia-icon"
}) => {
  const directionRotation = {
    up: "90deg",
    right: "180deg",
    down: "270deg",
    left: "0deg"
  };
  return /* @__PURE__ */ jsx16(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 30 29",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs6("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx16(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M29.9524 27.9654L19.2886 15.9718L16.3688 17.7969L13.6235 11.5058L0.0637207 27.9401L15.0041 0L29.9524 27.9654Z",
            fill: "#68FFAD"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M16.3687 17.7968L0.0159912 28.0241L0.0635972 27.94L13.6234 11.5056L16.3687 17.7968Z",
            fill: "#D6FFEC"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M30 28.058H20.8437L16.3688 17.797L19.2886 15.9719L29.9524 27.9655L30 28.058Z",
            fill: "#FF6005"
          }
        ),
        /* @__PURE__ */ jsx16(
          "path",
          {
            d: "M20.8435 28.0579H0L0.0158687 28.0242L16.3685 17.7969L20.8435 28.0579Z",
            fill: "#8800FF"
          }
        )
      ] })
    }
  );
};
var ArcadiaIcon_default = ArcadiaIcon;

// src/icons/ArcadiaLogoIcon/ArcadiaLogoIcon.tsx
import { jsx as jsx17, jsxs as jsxs7 } from "react/jsx-runtime";
var ArcadiaLogoIcon = ({
  width = 200,
  height = 48,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  dataTestId = "arcadia-logo-icon"
}) => {
  return /* @__PURE__ */ jsxs7(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width,
      height,
      style: { margin, padding, cursor },
      "data-testid": dataTestId,
      onClick: handleClick,
      viewBox: "0 0 507 123",
      fill: "none",
      children: [
        /* @__PURE__ */ jsxs7("g", { "clip-path": "url(#clip0_1282_53272)", children: [
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M168.684 113.865C157.848 113.865 149.856 105.443 149.856 94.446C149.856 83.61 157.849 75.241 168.684 75.241C175.927 75.241 182.258 78.942 184.404 84.308L179.79 86.561C178.019 82.753 173.727 80.23 168.684 80.23C160.691 80.23 155.167 86.347 155.167 94.447C155.167 102.708 160.692 108.877 168.684 108.877C173.995 108.877 178.502 106.089 180.164 101.797L184.832 103.943C182.793 109.842 176.249 113.866 168.683 113.866L168.684 113.865Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M214.817 113.329H209.881V109.573C208.432 112.041 204.731 113.865 201.191 113.865C193.95 113.865 188.371 107.911 188.371 100.239C188.371 92.567 193.95 86.615 201.191 86.615C204.785 86.615 208.432 88.438 209.881 90.907V87.151H214.817V113.329ZM202.104 109.198C205.269 109.198 208.432 107.427 209.881 104.961V95.572C208.432 93.052 205.322 91.28 202.104 91.28C197.061 91.28 193.253 95.088 193.253 100.238C193.253 105.336 197.061 109.198 202.104 109.198Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M221.734 87.15H226.723V90.958C228.172 88.331 231.069 86.614 233.589 86.614C234.77 86.614 235.735 86.827 236.486 87.043L236.218 91.709C235.36 91.387 234.233 91.228 233.266 91.228C231.12 91.228 228.277 92.945 226.722 95.894V113.328H221.733L221.734 87.15Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M237.612 100.132C237.612 92.568 243.513 86.615 250.968 86.615C257.996 86.615 263.468 92.087 263.468 99.113C263.468 99.651 263.414 100.671 263.361 101.259H242.385C242.923 106.302 246.356 109.467 251.184 109.467C254.51 109.467 257.515 108.018 258.854 105.659L262.771 108.073C260.786 111.667 256.388 113.865 251.023 113.865C243.566 113.865 237.612 108.018 237.612 100.132ZM258.425 97.504C258.05 93.694 254.831 90.905 250.755 90.905C246.516 90.905 243.459 93.48 242.601 97.504H258.425Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M267.864 108.93C271.513 108.93 273.445 106.837 273.445 101.902V75.778H278.647V103.189C278.647 109.949 274.946 113.811 269.26 113.811C268.563 113.811 267.489 113.757 266.685 113.65L267.005 108.875C267.275 108.929 267.543 108.929 267.863 108.929L267.864 108.93Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M311.316 100.293C311.316 107.963 305.308 113.865 297.744 113.865C290.18 113.865 284.227 107.964 284.227 100.293C284.227 92.568 290.181 86.615 297.744 86.615C305.307 86.615 311.316 92.569 311.316 100.293ZM297.744 109.144C302.626 109.144 306.488 105.282 306.488 100.293C306.488 95.25 302.626 91.335 297.744 91.335C292.862 91.335 289.054 95.25 289.054 100.293C289.054 105.282 292.862 109.144 297.744 109.144Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M336.098 109.466C334.436 112.148 331.109 113.864 327.515 113.864C321.023 113.864 316.786 109.252 316.786 102.116V87.15H321.775V101.419C321.775 106.033 324.511 108.983 328.641 108.983C331.752 108.983 334.649 107.375 336.098 104.746L336.152 87.151H341.086V113.329H336.097V109.467L336.098 109.466Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M347.95 87.15H352.939V90.958C354.388 88.331 357.285 86.614 359.805 86.614C360.986 86.614 361.951 86.827 362.702 87.043L362.434 91.709C361.576 91.387 360.449 91.228 359.482 91.228C357.336 91.228 354.493 92.945 352.938 95.894V113.328H347.949V87.15H347.95Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M372.251 91.067C373.913 88.383 377.24 86.614 380.886 86.614C387.378 86.614 391.615 91.228 391.615 98.362V113.328H386.626V99.059C386.626 94.445 383.89 91.495 379.76 91.495C376.649 91.495 373.697 93.157 372.251 95.787V113.328H367.315V87.15H372.251V91.067Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M397.085 100.132C397.085 92.568 402.984 86.615 410.441 86.615C417.469 86.615 422.941 92.087 422.941 99.113C422.941 99.651 422.887 100.671 422.834 101.259H401.858C402.396 106.302 405.829 109.467 410.657 109.467C413.983 109.467 416.988 108.018 418.327 105.659L422.244 108.073C420.259 111.667 415.859 113.865 410.496 113.865C403.039 113.865 397.085 108.018 397.085 100.132ZM417.898 97.504C417.523 93.694 414.304 90.905 410.228 90.905C405.991 90.905 402.932 93.48 402.074 97.504H417.898Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M434.955 112.416L424.387 87.15H429.698L437.477 106.678L445.309 87.15H450.62L435.654 122.876H430.343L434.955 112.416Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M128.399 113.091L82.687 64.59L70.167 71.978L58.375 46.511L0.249023 113.002L64.306 0L128.399 113.091Z",
              fill: "#68FFAD"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M70.167 71.978L0.0709839 113.34L0.248984 113.002L58.375 46.511L70.167 71.978Z",
              fill: "#D6FFEC"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M128.612 113.464H89.347L70.167 71.978L82.687 64.59L128.399 113.091L128.612 113.464Z",
              fill: "#FF6005"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M89.347 113.464H0L0.071 113.34L70.167 71.978L89.347 113.464Z",
              fill: "#8800FF"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M241.706 39.037C246.866 36.879 250.256 31.867 250.35 26.278C250.042 18.183 243.247 11.858 235.139 12.126H213.616V58.322H219.968V40.417H235.34L243.676 58.322H250.699L241.706 39.023V39.037ZM239.066 33.368C237.967 33.837 236.788 34.065 235.595 34.065H219.928V18.479H235.595C240.058 18.332 243.797 21.816 243.958 26.279C243.904 29.415 241.975 32.215 239.066 33.368Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M432.615 12.139H438.968V58.336H432.615V12.139Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M418.892 35.231C418.892 41.57 416.305 47.628 411.695 51.983C411.373 52.291 411.025 52.599 410.676 52.894L407.862 46.836C414.268 40.162 414.054 29.561 407.393 23.155C404.27 20.153 400.116 18.478 395.787 18.478H385.387V51.969H395.787C398.132 51.969 400.451 51.487 402.582 50.535L405.262 56.298C402.287 57.638 399.057 58.335 395.8 58.322H379.048V12.139H395.8C408.559 12.139 418.891 22.485 418.905 35.244L418.892 35.231Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M301.009 45.148L303.957 51.447C295.005 60.547 280.383 60.667 271.283 51.715C262.183 42.763 262.063 28.141 271.015 19.041C279.967 9.941 294.589 9.821 303.689 18.773C303.783 18.867 303.863 18.947 303.957 19.041L301.009 25.34C295.541 17.875 285.061 16.254 277.596 21.722C270.131 27.19 268.51 37.67 273.978 45.135C279.446 52.6 289.926 54.221 297.391 48.753C298.771 47.734 299.991 46.515 301.009 45.135V45.148Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M199.892 58.336H192.883L176.077 22.432L159.271 58.336H152.262L172.566 14.94L173.879 12.139H178.275L179.588 14.94L199.892 58.336Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M365.324 58.336H358.302L341.509 22.432L324.704 58.336H317.694L337.998 14.94L339.312 12.139H343.694L345.007 14.94L365.324 58.336Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M500.321 58.336H493.299L476.506 22.432L459.7 58.336H452.678L472.995 14.94L474.308 12.139H478.691L480.004 14.94L500.321 58.336Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M497.695 15.021H500.255C501.394 15.021 502.346 15.906 502.346 17.045C502.346 17.983 501.85 18.693 501.046 18.975L502.909 21.843H501.529L499.84 19.109H498.835V21.843H497.696V15.021H497.695ZM500.214 18.009C500.777 18.009 501.192 17.594 501.192 17.044C501.192 16.494 500.777 16.119 500.214 16.119H498.834V18.009H500.214Z",
              fill: "black"
            }
          ),
          /* @__PURE__ */ jsx17(
            "path",
            {
              d: "M499.839 24.938C496.301 24.938 493.433 22.07 493.433 18.532C493.433 14.994 496.301 12.126 499.839 12.126C503.377 12.126 506.245 14.994 506.245 18.532C506.245 22.07 503.377 24.938 499.839 24.938ZM499.839 12.836C496.69 12.836 494.143 15.396 494.143 18.532C494.143 21.668 496.703 24.228 499.839 24.228C502.975 24.228 505.535 21.668 505.535 18.532C505.535 15.396 502.975 12.836 499.839 12.836Z",
              fill: "black"
            }
          )
        ] }),
        /* @__PURE__ */ jsx17("defs", { children: /* @__PURE__ */ jsx17("clipPath", { id: "clip0_1282_53272", children: /* @__PURE__ */ jsx17("rect", { width: "506.245", height: "122.877", fill: "white" }) }) })
      ]
    }
  );
};
var ArcadiaLogoIcon_default = ArcadiaLogoIcon;

// src/icons/ArcadiaVerticalLogoIcon/ArcadiaVerticalLogoIcon.tsx
import { jsx as jsx18, jsxs as jsxs8 } from "react/jsx-runtime";
var ArcadiaVerticalLogoIcon = ({
  width = 451,
  height = 346,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  dataTestId = "arcadia-vertical-logo-icon"
}) => {
  return /* @__PURE__ */ jsxs8(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor
      },
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 451 346",
      fill: "none",
      "data-testid": dataTestId,
      onClick: handleClick,
      children: [
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M93.905 290.239C83.069 290.239 75.077 281.817 75.077 270.82C75.077 259.984 83.07 251.615 93.905 251.615C101.148 251.615 107.479 255.316 109.625 260.682L105.011 262.935C103.24 259.127 98.948 256.604 93.905 256.604C85.912 256.604 80.388 262.721 80.388 270.821C80.388 279.082 85.913 285.251 93.905 285.251C99.216 285.251 103.723 282.463 105.385 278.171L110.053 280.317C108.014 286.216 101.47 290.24 93.904 290.24L93.905 290.239Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M140.038 289.703H135.102V285.947C133.653 288.415 129.952 290.239 126.412 290.239C119.171 290.239 113.592 284.285 113.592 276.613C113.592 268.941 119.171 262.989 126.412 262.989C130.006 262.989 133.653 264.812 135.102 267.281V263.525H140.038V289.703ZM127.325 285.573C130.49 285.573 133.653 283.802 135.102 281.336V271.947C133.653 269.427 130.543 267.655 127.325 267.655C122.282 267.655 118.474 271.463 118.474 276.613C118.474 281.711 122.282 285.573 127.325 285.573Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M146.955 263.525H151.944V267.333C153.393 264.706 156.29 262.989 158.81 262.989C159.991 262.989 160.956 263.202 161.707 263.418L161.439 268.084C160.581 267.762 159.454 267.603 158.487 267.603C156.341 267.603 153.498 269.32 151.943 272.269V289.703H146.954V263.525H146.955Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M162.833 276.506C162.833 268.942 168.734 262.989 176.189 262.989C183.217 262.989 188.689 268.461 188.689 275.487C188.689 276.025 188.635 277.045 188.582 277.633H167.606C168.144 282.676 171.577 285.841 176.405 285.841C179.731 285.841 182.736 284.392 184.075 282.033L187.992 284.447C186.007 288.041 181.609 290.239 176.244 290.239C168.787 290.239 162.833 284.392 162.833 276.506ZM183.646 273.879C183.271 270.069 180.052 267.28 175.976 267.28C171.737 267.28 168.68 269.855 167.822 273.879H183.646Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M193.085 285.305C196.734 285.305 198.666 283.212 198.666 278.277V252.153H203.868V279.564C203.868 286.324 200.167 290.186 194.481 290.186C193.784 290.186 192.71 290.132 191.906 290.025L192.226 285.25C192.496 285.304 192.764 285.304 193.084 285.304L193.085 285.305Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M236.537 276.668C236.537 284.338 230.529 290.24 222.965 290.24C215.401 290.24 209.448 284.339 209.448 276.668C209.448 268.943 215.402 262.99 222.965 262.99C230.528 262.99 236.537 268.944 236.537 276.668ZM222.965 285.519C227.847 285.519 231.709 281.657 231.709 276.668C231.709 271.625 227.847 267.71 222.965 267.71C218.083 267.71 214.275 271.625 214.275 276.668C214.275 281.657 218.083 285.519 222.965 285.519Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M261.319 285.841C259.657 288.523 256.33 290.239 252.736 290.239C246.244 290.239 242.007 285.627 242.007 278.491V263.525H246.996V277.794C246.996 282.408 249.732 285.358 253.862 285.358C256.973 285.358 259.87 283.75 261.319 281.121L261.373 263.526H266.307V289.704H261.318V285.842L261.319 285.841Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M273.171 263.525H278.16V267.333C279.609 264.706 282.506 262.989 285.026 262.989C286.207 262.989 287.172 263.202 287.923 263.418L287.655 268.084C286.797 267.762 285.67 267.603 284.703 267.603C282.557 267.603 279.714 269.32 278.159 272.269V289.703H273.17L273.171 263.525Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M297.472 267.442C299.134 264.758 302.461 262.989 306.107 262.989C312.599 262.989 316.836 267.603 316.836 274.737V289.703H311.847V275.434C311.847 270.82 309.111 267.87 304.981 267.87C301.87 267.87 298.918 269.532 297.472 272.162V289.703H292.536V263.525H297.472V267.442Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M322.306 276.506C322.306 268.942 328.205 262.989 335.662 262.989C342.69 262.989 348.162 268.461 348.162 275.487C348.162 276.025 348.108 277.045 348.055 277.633H327.079C327.617 282.676 331.05 285.841 335.878 285.841C339.204 285.841 342.209 284.392 343.548 282.033L347.465 284.447C345.48 288.041 341.08 290.239 335.717 290.239C328.26 290.239 322.306 284.392 322.306 276.506ZM343.119 273.879C342.744 270.069 339.525 267.28 335.449 267.28C331.212 267.28 328.153 269.855 327.295 273.879H343.119Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M360.176 288.791L349.608 263.525H354.919L362.698 283.053L370.53 263.525H375.841L360.875 299.251H355.564L360.176 288.791Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M283.654 147.194L242.148 103.157L230.78 109.865L220.073 86.742L167.296 147.114L225.459 44.511L283.654 147.194Z",
            fill: "#68FFAD"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M230.78 109.865L167.135 147.42L167.296 147.114L220.073 86.7419L230.78 109.865Z",
            fill: "#D6FFEC"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M283.848 147.533H248.195L230.78 109.865L242.148 103.157L283.654 147.194L283.848 147.533Z",
            fill: "#FF6005"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M248.195 147.533H167.071L167.135 147.42L230.78 109.865L248.195 147.533Z",
            fill: "#8800FF"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M137.912 215.412C143.072 213.254 146.462 208.242 146.556 202.653C146.248 194.558 139.453 188.233 131.345 188.501H109.822V234.697H116.174V216.792H131.546L139.882 234.697H146.905L137.912 215.398V215.411V215.412ZM135.271 209.743C134.172 210.212 132.993 210.44 131.8 210.44H116.133V194.854H131.8C136.263 194.707 140.002 198.191 140.163 202.654C140.109 205.79 138.18 208.591 135.271 209.744V209.743Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M328.821 188.514H335.173V234.71H328.821V188.514Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M315.097 211.606C315.097 217.945 312.51 224.003 307.9 228.358C307.578 228.666 307.23 228.974 306.881 229.269L304.067 223.211C310.473 216.537 310.259 205.936 303.598 199.53C300.475 196.528 296.321 194.853 291.992 194.853H281.592V228.344H291.992C294.337 228.344 296.656 227.862 298.787 226.91L301.467 232.673C298.492 234.013 295.262 234.71 292.005 234.697H275.253V188.514H292.005C304.764 188.514 315.096 198.86 315.11 211.619L315.097 211.606Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M197.215 221.523L200.163 227.822C191.211 236.922 176.589 237.042 167.489 228.09C158.389 219.138 158.269 204.516 167.221 195.416C176.173 186.316 190.795 186.196 199.895 195.148C199.989 195.242 200.069 195.322 200.163 195.416L197.215 201.715C191.747 194.25 181.267 192.629 173.802 198.097C166.337 203.565 164.716 214.045 170.184 221.51C175.652 228.975 186.132 230.596 193.597 225.128C194.977 224.109 196.197 222.89 197.215 221.51V221.523Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M96.098 234.71H89.089L72.283 198.807L55.477 234.71H48.468L68.772 191.315L70.085 188.514H74.481L75.794 191.315L96.098 234.71Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M261.53 234.71H254.508L237.715 198.807L220.909 234.71H213.9L234.204 191.315L235.517 188.514H239.9L241.213 191.315L261.53 234.71Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M396.527 234.71H389.504L372.712 198.807L355.906 234.71H348.883L369.201 191.315L370.514 188.514H374.896L376.21 191.315L396.527 234.71Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M393.9 191.396H396.46C397.599 191.396 398.551 192.281 398.551 193.42C398.551 194.358 398.055 195.068 397.251 195.35L399.114 198.218H397.734L396.045 195.484H395.04V198.218H393.901V191.396H393.9ZM396.42 194.384C396.983 194.384 397.398 193.969 397.398 193.419C397.398 192.869 396.983 192.494 396.42 192.494H395.04V194.384H396.42Z",
            fill: "black"
          }
        ),
        /* @__PURE__ */ jsx18(
          "path",
          {
            d: "M396.045 201.313C392.507 201.313 389.639 198.445 389.639 194.907C389.639 191.369 392.507 188.501 396.045 188.501C399.583 188.501 402.451 191.369 402.451 194.907C402.451 198.445 399.583 201.313 396.045 201.313ZM396.045 189.211C392.896 189.211 390.349 191.771 390.349 194.907C390.349 198.043 392.909 200.603 396.045 200.603C399.181 200.603 401.741 198.043 401.741 194.907C401.741 191.771 399.181 189.211 396.045 189.211Z",
            fill: "black"
          }
        )
      ]
    }
  );
};
var ArcadiaVerticalLogoIcon_default = ArcadiaVerticalLogoIcon;

// src/icons/ArcadiaLoadingIcon/ArcadiaLoadingIcon.tsx
import { jsx as jsx19, jsxs as jsxs9 } from "react/jsx-runtime";
var ArcadiaLoadingIcon = ({
  width = 60,
  height = 60,
  margin = 0,
  padding = 0,
  cursor = "default",
  dataTestId = "arcadia-loading-icon"
}) => {
  return /* @__PURE__ */ jsxs9(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        animation: "fade-in-out 3s ease-in-out infinite"
      },
      viewBox: "0 0 171 150",
      fill: "none",
      "data-testid": dataTestId,
      children: [
        /* @__PURE__ */ jsx19("style", { children: `
      @keyframes fade-in-out {
        0%, 100% { opacity: 0; }
        50% { opacity: 1; }
      }
    ` }),
        /* @__PURE__ */ jsx19(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M169.744 149.507L109.313 85.3883L92.7612 95.1553L77.1721 61.4878L0.329208 149.389L85.0129 0L169.744 149.507Z",
            fill: "#68FFAD"
          }
        ),
        /* @__PURE__ */ jsx19(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M92.7611 95.1553L0.0938416 149.836L0.329158 149.389L77.172 61.4878L92.7611 95.1553Z",
            fill: "#D6FFEC"
          }
        ),
        /* @__PURE__ */ jsx19(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M170.026 150H118.117L92.7611 95.1552L109.313 85.3882L169.744 149.507L170.026 150Z",
            fill: "#FF6005"
          }
        ),
        /* @__PURE__ */ jsx19(
          "path",
          {
            "fill-rule": "evenodd",
            "clip-rule": "evenodd",
            d: "M118.117 150H0L0.0938624 149.836L92.7611 95.1553L118.117 150Z",
            fill: "#8800FF"
          }
        )
      ]
    }
  );
};
var ArcadiaLoadingIcon_default = ArcadiaLoadingIcon;

// src/icons/ArcadiaFooterIcon/ArcadiaFooterIcon.tsx
import { jsx as jsx20, jsxs as jsxs10 } from "react/jsx-runtime";
var ArcadiaFooterIcon = ({
  width = 80,
  height = 28,
  margin = 0,
  padding = 0,
  cursor = "default",
  dataTestId = "arcadia-footer-icon"
}) => {
  return /* @__PURE__ */ jsxs10(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width,
      height,
      style: { margin, padding, cursor },
      "data-testid": dataTestId,
      viewBox: "0 0 80 28",
      fill: "none",
      children: [
        /* @__PURE__ */ jsxs10("g", { "clip-path": "url(#clip0_1282_53596)", children: [
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M3.97018 25.9464C1.52955 25.9464 -0.270508 24.0273 -0.270508 21.5214C-0.270508 19.0522 1.52978 17.1451 3.97018 17.1451C5.60154 17.1451 7.02749 17.9885 7.51084 19.2112L6.47161 19.7246C6.07272 18.8569 5.10603 18.282 3.97018 18.282C2.16989 18.282 0.925705 19.6759 0.925705 21.5216C0.925705 23.4041 2.17012 24.8098 3.97018 24.8098C5.16639 24.8098 6.18151 24.1745 6.55585 23.1965L7.60723 23.6855C7.14799 25.0297 5.67406 25.9466 3.96995 25.9466L3.97018 25.9464Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M14.3608 25.8243H13.2491V24.9684C12.9227 25.5308 12.0891 25.9464 11.2918 25.9464C9.66087 25.9464 8.4043 24.5897 8.4043 22.8414C8.4043 21.0932 9.66087 19.7369 11.2918 19.7369C12.1013 19.7369 12.9227 20.1523 13.2491 20.715V19.8591H14.3608V25.8243ZM11.4974 24.8829C12.2103 24.8829 12.9227 24.4794 13.2491 23.9174V21.778C12.9227 21.2037 12.2222 20.8 11.4974 20.8C10.3616 20.8 9.50388 21.6677 9.50388 22.8412C9.50388 24.0029 10.3616 24.8829 11.4974 24.8829Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M15.9187 19.8588H17.0424V20.7266C17.3687 20.1279 18.0212 19.7367 18.5888 19.7367C18.8548 19.7367 19.0722 19.7852 19.2413 19.8345L19.181 20.8977C18.9877 20.8243 18.7339 20.7881 18.5161 20.7881C18.0327 20.7881 17.3924 21.1793 17.0421 21.8513V25.824H15.9185L15.9187 19.8588Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M19.4951 22.8171C19.4951 21.0935 20.8242 19.7369 22.5033 19.7369C24.0863 19.7369 25.3187 20.9838 25.3187 22.5849C25.3187 22.7075 25.3066 22.9399 25.2946 23.0739H20.5702C20.6913 24.223 21.4646 24.9442 22.552 24.9442C23.3011 24.9442 23.9779 24.614 24.2795 24.0765L25.1617 24.6266C24.7147 25.4455 23.7241 25.9464 22.5157 25.9464C20.8362 25.9464 19.4951 24.614 19.4951 22.8171ZM24.1829 22.2182C24.0984 21.35 23.3734 20.7145 22.4554 20.7145C21.5006 20.7145 20.8121 21.3013 20.6188 22.2182H24.1829Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M26.3088 24.8218C27.1306 24.8218 27.5658 24.3449 27.5658 23.2203V17.2675H28.7374V23.5136C28.7374 25.054 27.9039 25.934 26.6232 25.934C26.4662 25.934 26.2243 25.9217 26.0432 25.8974L26.1153 24.8093C26.1761 24.8216 26.2365 24.8216 26.3085 24.8216L26.3088 24.8218Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M36.0955 22.8537C36.0955 24.6015 34.7423 25.9464 33.0386 25.9464C31.335 25.9464 29.9941 24.6017 29.9941 22.8537C29.9941 21.0934 31.3352 19.7369 33.0386 19.7369C34.742 19.7369 36.0955 21.0937 36.0955 22.8537ZM33.0386 24.8706C34.1382 24.8706 35.0081 23.9906 35.0081 22.8537C35.0081 21.7046 34.1382 20.8125 33.0386 20.8125C31.939 20.8125 31.0813 21.7046 31.0813 22.8537C31.0813 23.9906 31.939 24.8706 33.0386 24.8706Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M41.6773 24.944C41.303 25.5552 40.5536 25.9462 39.7442 25.9462C38.2819 25.9462 37.3276 24.8953 37.3276 23.2692V19.8589H38.4513V23.1104C38.4513 24.1618 39.0676 24.834 39.9978 24.834C40.6985 24.834 41.351 24.4676 41.6773 23.8685L41.6895 19.8591H42.8008V25.8243H41.6771V24.9443L41.6773 24.944Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M44.3467 19.8588H45.4703V20.7266C45.7967 20.1279 46.4492 19.7367 47.0168 19.7367C47.2828 19.7367 47.5001 19.7852 47.6693 19.8345L47.6089 20.8977C47.4157 20.8243 47.1618 20.7881 46.944 20.7881C46.4607 20.7881 45.8204 21.1793 45.4701 21.8513V25.824H44.3464V19.8588H44.3467Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M49.8202 20.7514C50.1946 20.1398 50.9439 19.7367 51.7651 19.7367C53.2273 19.7367 54.1817 20.7881 54.1817 22.4137V25.824H53.058V22.5725C53.058 21.5211 52.4417 20.8489 51.5115 20.8489C50.8108 20.8489 50.1459 21.2276 49.8202 21.8269V25.824H48.7085V19.8588H49.8202V20.7514Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M55.4136 22.8171C55.4136 21.0935 56.7422 19.7369 58.4218 19.7369C60.0047 19.7369 61.2372 20.9838 61.2372 22.5849C61.2372 22.7075 61.225 22.9399 61.2131 23.0739H56.4886C56.6098 24.223 57.383 24.9442 58.4704 24.9442C59.2196 24.9442 59.8964 24.614 60.198 24.0765L61.0802 24.6266C60.6331 25.4455 59.6421 25.9464 58.4342 25.9464C56.7546 25.9464 55.4136 24.614 55.4136 22.8171ZM60.1013 22.2182C60.0169 21.35 59.2919 20.7145 58.3738 20.7145C57.4195 20.7145 56.7305 21.3013 56.5373 22.2182H60.1013Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M63.9432 25.6163L61.563 19.8589H62.7592L64.5113 24.3087L66.2753 19.8589H67.4715L64.1007 27.9998H62.9045L63.9432 25.6163Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M20.4171 8.89532C21.5793 8.40357 22.3429 7.26149 22.364 5.98792C22.2947 4.14331 20.7642 2.70203 18.938 2.7631H14.0903V13.2898H15.521V9.20978H18.9833L20.8608 13.2898H22.4426L20.4171 8.89212V8.89532ZM19.8225 7.60352C19.575 7.71039 19.3094 7.76234 19.0407 7.76234H15.512V4.21076H19.0407C20.0459 4.17726 20.8881 4.97116 20.9243 5.98815C20.9122 6.70275 20.4777 7.34079 19.8225 7.60352Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M63.4163 2.76611H64.8472V13.293H63.4163V2.76611Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M60.3252 8.02809C60.3252 9.47256 59.7426 10.853 58.7043 11.8454C58.6317 11.9156 58.5533 11.9857 58.4747 12.053L57.8409 10.6725C59.2838 9.15172 59.2356 6.73607 57.7353 5.27633C57.0319 4.59226 56.0963 4.21058 55.1212 4.21058H52.7788V11.8422H55.1212C55.6494 11.8422 56.1717 11.7323 56.6517 11.5154L57.2553 12.8286C56.5853 13.134 55.8578 13.2928 55.1242 13.2898H51.3511V2.76611H55.1242C57.9979 2.76611 60.325 5.12366 60.3282 8.03105L60.3252 8.02809Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M33.7743 10.2878L34.4383 11.7231C32.422 13.7968 29.1286 13.8241 27.079 11.7842C25.0294 9.74432 25.0024 6.4124 27.0186 4.33878C29.0349 2.26516 32.3283 2.23782 34.3779 4.27771C34.3991 4.29913 34.4171 4.31736 34.4383 4.33878L33.7743 5.77414C32.5427 4.07309 30.1823 3.70371 28.5009 4.9497C26.8195 6.1957 26.4544 8.58378 27.686 10.2848C28.9176 11.9859 31.278 12.3553 32.9594 11.1093C33.2702 10.8771 33.545 10.5993 33.7743 10.2848V10.2878Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M10.9993 13.2932H9.42067L5.6354 5.1117L1.85014 13.2932H0.271484L4.84461 3.4045L5.14034 2.76624H6.13047L6.4262 3.4045L10.9993 13.2932Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M48.2601 13.293H46.6785L42.8961 5.11158L39.1111 13.293H37.5322L42.1054 3.40438L42.4013 2.76611H43.3883L43.684 3.40438L48.2601 13.293Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              "fill-rule": "evenodd",
              "clip-rule": "evenodd",
              d: "M78.6658 13.293H77.0842L73.3019 5.11158L69.5166 13.293H67.9351L72.5111 3.40438L72.8068 2.76611H73.794L74.0898 3.40438L78.6658 13.293Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M78.0742 3.42273H78.6508C78.9074 3.42273 79.1218 3.62439 79.1218 3.88394C79.1218 4.09768 79.0101 4.25947 78.829 4.32373L79.2486 4.97726H78.9378L78.5573 4.35426H78.331V4.97726H78.0744V3.42273H78.0742ZM78.6416 4.10361C78.7684 4.10361 78.8619 4.00904 78.8619 3.88371C78.8619 3.75838 78.7684 3.67293 78.6416 3.67293H78.3308V4.10361H78.6416Z",
              fill: "#191919"
            }
          ),
          /* @__PURE__ */ jsx20(
            "path",
            {
              d: "M78.5573 5.68253C77.7605 5.68253 77.1145 5.029 77.1145 4.2228C77.1145 3.41659 77.7605 2.76306 78.5573 2.76306C79.3542 2.76306 80.0002 3.41659 80.0002 4.2228C80.0002 5.029 79.3542 5.68253 78.5573 5.68253ZM78.5573 2.92485C77.8481 2.92485 77.2744 3.5082 77.2744 4.2228C77.2744 4.9374 77.851 5.52075 78.5573 5.52075C79.2637 5.52075 79.8403 4.9374 79.8403 4.2228C79.8403 3.5082 79.2637 2.92485 78.5573 2.92485Z",
              fill: "#191919"
            }
          )
        ] }),
        /* @__PURE__ */ jsx20("defs", { children: /* @__PURE__ */ jsx20("clipPath", { id: "clip0_1282_53596", children: /* @__PURE__ */ jsx20("rect", { width: "80", height: "28", fill: "white" }) }) })
      ]
    }
  );
};
var ArcadiaFooterIcon_default = ArcadiaFooterIcon;

// src/icons/ArcadiaMenuIcon/ArcadiaMenuIcon.tsx
import { jsx as jsx21 } from "react/jsx-runtime";
var ArcadiaMenuIcon = ({
  width = 21,
  height = 18,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "",
  handleClick,
  direction = "up",
  dataTestId = "arcadia-menu-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx21(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      xmlns: "http://www.w3.org/2000/svg",
      viewBox: "0 0 41.263 40.07",
      children: /* @__PURE__ */ jsx21(
        "path",
        {
          fill: color || "currentColor",
          d: "M20.634,3.46L1.794,36.609h37.676L20.634,3.46ZM20.634,6.727l14.149,24.9-8.739-9.282-3.673,2.163-3.407-7.413-12.07,13.804L20.634,6.727ZM18.534,20.034l2.435,5.3-14.565,8.579,12.13-13.879ZM7.745,34.995l13.905-8.19,3.763,8.19H7.745ZM27.19,34.995l-4.142-9.013,2.7-1.592,9.984,10.605h-8.542Z"
        }
      )
    }
  );
};
var ArcadiaMenuIcon_default = ArcadiaMenuIcon;

// src/icons/ArrowIcon/ArrowIcon.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
var ArrowIcon = ({
  width = 19,
  height = 19,
  margin = "0",
  padding = 0,
  cursor = "default",
  color = "white",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "arrow-icon"
}) => {
  const directionRotation = {
    up: "90deg",
    right: "180deg",
    down: "270deg",
    left: "0deg"
  };
  return /* @__PURE__ */ jsx22(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 19 19",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx22("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx22(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M8.56004 0.38993L0.390328 8.55937C0.130629 8.81911 0.000653174 9.15959 0.000653144 9.50015C0.000653114 9.84054 0.130378 10.1808 0.390116 10.4406L8.55983 18.6102C9.07953 19.1299 9.92208 19.1299 10.4416 18.6102C10.961 18.0907 10.961 17.2483 10.4416 16.7286L4.63777 10.925L17.4022 10.925C18.1455 10.925 18.748 10.3224 18.748 9.57916C18.748 8.83588 18.1455 8.23333 17.4022 8.23333L4.47965 8.23333L10.4418 2.2714C10.9613 1.75196 10.9613 0.90941 10.4418 0.38993C9.92233 -0.130015 9.07974 -0.130015 8.56004 0.38993Z",
          fill: color
        }
      ) })
    }
  );
};
var ArrowIcon_default = ArrowIcon;

// src/icons/BuildANetworkIcon/Variants/BuildANetworkIconDefault.tsx
import { jsx as jsx23, jsxs as jsxs11 } from "react/jsx-runtime";
var BuildANetworkIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "build-a-network-icon",
  // Extra Properties
  variant,
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx23(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs11("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx23("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx23(
          "path",
          {
            "data-testid": "path-with-background-color",
            d: "M59.4551 2.68945C52.4037 2.68945 46.6875 8.40569 46.6875 15.457C46.6875 19.6162 48.6762 23.3109 51.7547 25.642L47.3848 44.7967C45.9759 44.9652 44.6281 45.3321 43.3699 45.8687L32.9444 33.7995C33.7633 32.1151 34.2227 30.2235 34.2227 28.2246C34.2227 21.1733 28.5064 15.457 21.4551 15.457C14.4037 15.457 8.6875 21.1733 8.6875 28.2246C8.6875 35.2759 14.4037 40.9922 21.4551 40.9922C22.8439 40.9922 24.1809 40.7704 25.4327 40.3604L36.05 52.6516C34.9904 54.6882 34.3916 57.0027 34.3916 59.457C34.3916 61.0387 34.6403 62.5623 35.1007 63.9908L22.3082 75.8897C20.8318 75.2932 19.2184 74.9648 17.5283 74.9648C10.477 74.9648 4.76074 80.6811 4.76074 87.7324C4.76074 94.7838 10.477 100.5 17.5283 100.5C24.5797 100.5 30.2959 94.7838 30.2959 87.7324C30.2959 86.0467 29.9692 84.4373 29.3757 82.964L41.2437 71.9252C43.054 73.0766 45.1321 73.8446 47.3637 74.1148L47.6716 93.523C43.1137 95.4721 39.9199 99.9969 39.9199 105.268C39.9199 112.319 45.6362 118.035 52.6875 118.035C59.7388 118.035 65.4551 112.319 65.4551 105.268C65.4551 99.9845 62.2463 95.4509 57.6713 93.5093L57.3263 71.7589C59.5882 70.2539 61.4114 68.1416 62.563 65.655C61.1813 62.2085 60.4199 58.4447 60.4199 54.5015C60.4199 53.0649 60.5211 51.6518 60.7167 50.269C59.709 49.0029 58.4977 47.9062 57.1323 47.0282L61.4577 28.0685C67.5577 27.1075 72.2227 21.827 72.2227 15.457C72.2227 8.40569 66.5064 2.68945 59.4551 2.68945Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx23("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx23(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M78.4199 34.9425C78.4199 31.3846 75.2589 28.3679 72.4141 30.5046C65.15 35.9609 60.4199 44.6804 60.4199 54.5018C60.4199 63.395 64.2929 71.3761 70.4196 76.8609V94.6524C70.4196 105.698 79.3739 114.652 90.4196 114.652C101.465 114.652 110.42 105.698 110.42 94.6525V76.8615C116.547 71.3766 120.42 63.3953 120.42 54.5018C120.42 44.6804 115.69 35.9609 108.426 30.5046C105.581 28.3679 102.42 31.3846 102.42 34.9425V52.2449C102.42 58.8724 97.0473 64.2449 90.4199 64.2449C83.7925 64.2449 78.4199 58.8723 78.4199 52.2449V34.9425Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var BuildANetworkIconDefault_default = BuildANetworkIconDefaultVariant;

// src/icons/BuildANetworkIcon/Variants/BuildANetworkIconOutlined.tsx
import { jsx as jsx24 } from "react/jsx-runtime";
var BuildANetworkIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "build-a-network-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx24(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx24("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx24(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M42.0798 13.457C42.0798 6.40569 47.7961 0.689453 54.8474 0.689453C61.8988 0.689453 67.615 6.40569 67.615 13.457C67.615 19.827 62.95 25.1075 56.85 26.0685L52.5246 45.0282C53.89 45.9062 55.1013 47.0029 56.109 48.269C57.2503 40.199 61.6049 33.1625 67.8065 28.5044C70.8361 26.2288 74.8915 25.8609 78.2812 27.5542C81.6708 29.2474 83.8123 32.711 83.8123 36.5001V48.1946C83.8123 49.2992 84.7077 50.1946 85.8123 50.1946C86.9168 50.1946 87.8123 49.2992 87.8123 48.1946V36.5001C87.8123 32.711 89.9537 29.2474 93.3434 27.5542C96.733 25.8609 100.788 26.2288 103.818 28.5044C111.082 33.9607 115.812 42.6802 115.812 52.5015C115.812 61.395 111.939 69.3764 105.812 74.8612V92.6522C105.812 103.698 96.8577 112.652 85.812 112.652C74.7663 112.652 65.812 103.698 65.812 92.6522V74.8607C62.4017 71.8078 59.6898 67.9815 57.9553 63.655C56.8037 66.1416 54.9806 68.2539 52.7186 69.7589L53.0637 91.5093C57.6386 93.4509 60.8474 97.9845 60.8474 103.268C60.8474 110.319 55.1312 116.035 48.0798 116.035C41.0285 116.035 35.3123 110.319 35.3123 103.268C35.3123 97.9969 38.506 93.4721 43.0639 91.523L42.756 72.1148C40.5245 71.8446 38.4464 71.0766 36.636 69.9252L24.768 80.964C25.3615 82.4373 25.6882 84.0467 25.6882 85.7324C25.6882 92.7838 19.972 98.5 12.9207 98.5C5.86932 98.5 0.153076 92.7838 0.153076 85.7324C0.153076 78.6811 5.86932 72.9648 12.9207 72.9648C14.6107 72.9648 16.2241 73.2932 17.7005 73.8897L30.493 61.9908C30.0326 60.5623 29.7839 59.0387 29.7839 57.457C29.7839 55.0027 30.3828 52.6882 31.4423 50.6516L20.825 38.3604C19.5732 38.7704 18.2362 38.9922 16.8474 38.9922C9.79607 38.9922 4.07983 33.2759 4.07983 26.2246C4.07983 19.1733 9.79607 13.457 16.8474 13.457C23.8988 13.457 29.615 19.1733 29.615 26.2246C29.615 28.2235 29.1557 30.1151 28.3368 31.7995L38.7622 43.8687C40.0204 43.3321 41.3683 42.9652 42.7771 42.7967L47.147 23.642C44.0686 21.3109 42.0798 17.6162 42.0798 13.457ZM54.8474 10.8424C53.4034 10.8424 52.2327 12.013 52.2327 13.457C52.2327 14.9011 53.4034 16.0717 54.8474 16.0717C56.2915 16.0717 57.4621 14.9011 57.4621 13.457C57.4621 12.013 56.2915 10.8424 54.8474 10.8424ZM16.8474 23.6099C15.4034 23.6099 14.2327 24.7806 14.2327 26.2246C14.2327 27.6687 15.4034 28.8393 16.8474 28.8393C18.2915 28.8393 19.4621 27.6687 19.4621 26.2246C19.4621 24.7806 18.2915 23.6099 16.8474 23.6099ZM10.306 85.7324C10.306 84.2884 11.4766 83.1177 12.9207 83.1177C14.3647 83.1177 15.5353 84.2884 15.5353 85.7324C15.5353 87.1765 14.3647 88.3471 12.9207 88.3471C11.4766 88.3471 10.306 87.1765 10.306 85.7324ZM48.0798 100.653C46.6358 100.653 45.4652 101.824 45.4652 103.268C45.4652 104.712 46.6358 105.882 48.0798 105.882C49.5239 105.882 50.6945 104.712 50.6945 103.268C50.6945 101.824 49.5239 100.653 48.0798 100.653ZM44.5496 53.0742C42.129 53.0742 40.1667 55.0365 40.1667 57.457C40.1667 59.8776 42.129 61.8398 44.5496 61.8398C46.9701 61.8398 48.9324 59.8776 48.9324 57.457C48.9324 55.0365 46.9701 53.0742 44.5496 53.0742ZM75.812 92.6522V69.8257C72.0029 67.6222 68.988 64.1985 67.3017 60.0891C66.3416 57.7493 65.8123 55.1872 65.8123 52.5015C65.8123 51.0229 65.9727 49.5818 66.2772 48.1946C67.32 43.4431 70.0522 39.3243 73.8123 36.5001V48.1946C73.8123 49.7126 74.0941 51.1647 74.6083 52.5015C75.0177 53.5657 75.5742 54.5568 76.2531 55.4498C78.4448 58.3331 81.9111 60.1946 85.8123 60.1946C89.7134 60.1946 93.1797 58.3331 95.3714 55.4498C96.0503 54.5568 96.6069 53.5657 97.0162 52.5015C97.5304 51.1647 97.8123 49.7126 97.8123 48.1946V36.5001C101.572 39.3244 104.305 43.4431 105.347 48.1946C105.652 49.5818 105.812 51.0229 105.812 52.5015C105.812 55.1872 105.283 57.7493 104.323 60.0891C102.636 64.1987 99.6213 67.6225 95.812 69.826V92.6522C95.812 98.1751 91.3348 102.652 85.812 102.652C80.2891 102.652 75.812 98.1751 75.812 92.6522Z",
          fill: color
        }
      ) })
    }
  );
};
var BuildANetworkIconOutlined_default = BuildANetworkIconOutlinedVariant;

// src/icons/BuildANetworkIcon/BuildANetworkIcon.tsx
import { jsx as jsx25 } from "react/jsx-runtime";
var BuildANetworkIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx25(BuildANetworkIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx25(BuildANetworkIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var BuildANetworkIcon_default = BuildANetworkIcon;

// src/icons/CardiologyIcon/CardiologyIcon.tsx
import { jsx as jsx26 } from "react/jsx-runtime";
var CardiologyIcon = ({
  width = 49,
  height = 44,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cardiology-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx26(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 49 44",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx26("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx26(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M8.59774 6.98513C6.77277 9.3075 5.66699 12.4805 5.66699 15.7931C5.66699 17.7153 5.99757 19.5544 6.57952 21.3055H17.436L21.671 13.7765L24.3174 23.0385L27.5556 18.875H35.667V21.375H28.7784L23.2666 28.4615L20.913 20.2235L18.898 23.8055H0.666992V21.3055H3.96183C3.4549 19.5571 3.16699 17.7165 3.16699 15.7931C3.16699 11.949 4.44353 8.2255 6.63202 5.4405C8.82649 2.64779 11.9871 0.75 15.706 0.75C20.0855 0.75 23.4159 3.20421 25.667 6.66725C27.918 3.20438 31.2484 0.75 35.628 0.75C39.3472 0.75 42.5079 2.64812 44.7022 5.44088C46.8906 8.22612 48.167 11.9496 48.167 15.7931C48.167 24.0212 42.4874 30.8924 37.114 35.5825C34.3965 37.9544 31.685 39.8319 29.6555 41.1156C28.6395 41.7584 27.7911 42.2544 27.1941 42.591C26.8955 42.7594 26.6595 42.8881 26.4965 42.9755C26.4151 43.0192 26.3519 43.0526 26.3081 43.0755L26.2575 43.1019L26.2434 43.1091L26.2392 43.1114L26.2379 43.112C26.2379 43.112 26.237 43.1125 25.667 42C25.1247 43.1263 25.1244 43.1261 25.1244 43.1261L25.1169 43.1225L25.0995 43.114L25.0364 43.083C24.9821 43.0561 24.9035 43.0168 24.8025 42.9654C24.6005 42.8624 24.3086 42.7105 23.9415 42.5114C23.2075 42.1131 22.171 41.5249 20.9492 40.7585C18.5102 39.2285 15.3109 36.9754 12.3022 34.0926C10.6974 32.555 9.13862 30.8305 7.77648 28.9326C7.37394 28.3717 7.50229 27.5907 8.06314 27.1883C8.62399 26.7858 9.40499 26.9141 9.80749 27.475C11.0631 29.2244 12.5147 30.834 14.0317 32.2875C16.8805 35.0169 19.9311 37.1686 22.2777 38.6406C23.4489 39.3753 24.4391 39.9371 25.1336 40.3139C25.3307 40.4209 25.504 40.5129 25.6505 40.5895C25.7449 40.5375 25.8502 40.4788 25.9661 40.4134C26.5311 40.0948 27.3429 39.6204 28.3191 39.0029C30.274 37.7662 32.875 35.9641 35.47 33.699C40.7216 29.1152 45.667 22.8829 45.667 15.7931C45.667 12.481 44.5614 9.308 42.7365 6.9855C40.9176 4.67063 38.4337 3.25 35.628 3.25C31.7495 3.25 28.7412 5.776 26.789 9.75225L25.667 12.0376L24.545 9.75225C22.5926 5.77575 19.5844 3.25 15.706 3.25C12.9005 3.25 10.4166 4.67038 8.59774 6.98513ZM25.667 42L25.1244 43.1261L25.6844 43.3956L26.2379 43.112L25.667 42Z",
          fill: color
        }
      ) })
    }
  );
};
var CardiologyIcon_default = CardiologyIcon;

// src/icons/CareAcceleratorIcon/variants/CareAcceleratorIconDefault.tsx
import { jsx as jsx27, jsxs as jsxs12 } from "react/jsx-runtime";
var CareAcceleratorIconOutlined = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "care-accelerator-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx27(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 120 120",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      children: /* @__PURE__ */ jsxs12("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx27("g", { opacity: backgroundOpacity, "data-testid": "g-background-path", children: /* @__PURE__ */ jsx27(
          "path",
          {
            opacity: "0.5",
            d: "M0 34.1429C0 18.0477 13.4315 5 30 5H60C76.5685 5 90 18.0477 90 34.1429V77.8571C90 93.9523 76.5685 107 60 107H30C13.4315 107 0 93.9523 0 77.8571V34.1429Z",
            fill: backgroundColor,
            "data-testid": "path-with-background-color"
          }
        ) }),
        /* @__PURE__ */ jsx27("rect", { x: "27", width: "36", height: "10", rx: "5", fill: "#59595B" }),
        /* @__PURE__ */ jsx27("rect", { x: "13", y: "46", width: "29", height: "10", rx: "5", fill: "#59595B" }),
        /* @__PURE__ */ jsx27("rect", { x: "13", y: "29.7407", width: "46", height: "10", rx: "5", fill: "#59595B" }),
        /* @__PURE__ */ jsx27("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx27(
          "path",
          {
            d: "M102.481 83.8337C102.502 83.8383 102.523 83.8431 102.543 83.8478C102.523 83.8431 102.502 83.8384 102.482 83.8337C102.482 83.8337 102.482 83.8337 102.481 83.8337ZM65.367 83.8336C65.367 83.8336 65.367 83.8336 65.367 83.8336C65.4175 83.8222 65.4681 83.8111 65.5188 83.8003C65.4681 83.8111 65.4175 83.8222 65.367 83.8336ZM98.4533 82.0778L94.9594 85.351L99.8463 85.5058C103.859 85.6328 107.492 87.8513 109.296 91.3043L115.827 103.804C119.475 110.786 114.17 119 106.013 119H61.8354C53.6786 119 48.3733 110.786 52.0212 103.804L58.5525 91.3043C60.3567 87.8513 63.9898 85.6327 68.0023 85.5058L72.8892 85.3511L69.3953 82.0778C65.542 78.4676 63.1547 73.4399 63.1547 67.875C63.1547 56.8983 72.4536 48 83.9243 48C95.395 48 104.694 56.8983 104.694 67.875C104.694 73.4399 102.307 78.4676 98.4533 82.0778Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ) })
      ] })
    }
  );
};
var CareAcceleratorIconDefault_default = CareAcceleratorIconOutlined;

// src/icons/CareAcceleratorIcon/variants/CareAcceleratorIconOutlined.tsx
import { jsx as jsx28, jsxs as jsxs13 } from "react/jsx-runtime";
var CareAcceleratorIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "care-accelerator-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx28("g", { opacity, "data-testid": "g-path", children: /* @__PURE__ */ jsxs13(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 120 117",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: [
        /* @__PURE__ */ jsx28(
          "path",
          {
            d: "M27 5C27 2.23858 29.2386 0 32 0H58C60.7614 0 63 2.23858 63 5C63 7.76142 60.7614 10 58 10H32C29.2386 10 27 7.76142 27 5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx28(
          "path",
          {
            d: "M13 51C13 48.2386 15.2386 46 18 46H37C39.7614 46 42 48.2386 42 51C42 53.7614 39.7614 56 37 56H18C15.2386 56 13 53.7614 13 51Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx28(
          "path",
          {
            d: "M13 34.7406C13 31.9792 15.2386 29.7406 18 29.7406H54C56.7614 29.7406 59 31.9792 59 34.7406C59 37.502 56.7614 39.7406 54 39.7406H18C15.2386 39.7406 13 37.502 13 34.7406Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx28(
          "path",
          {
            d: "M30 13H60C72.3704 13 82 22.683 82 34.1429V48.0842C82.6336 48.0285 83.2755 48 83.9243 48C86.0381 48 88.0783 48.3022 90 48.8639V34.1429C90 18.0477 76.5685 5 60 5H30C13.4315 5 0 18.0477 0 34.1429V77.8571C0 93.9523 13.4315 107 30 107H50.963C51.1277 105.928 51.4736 104.852 52.0212 103.804L54.5315 99H30C17.6296 99 8 89.317 8 77.8571V34.1429C8 22.683 17.6296 13 30 13Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx28(
          "path",
          {
            d: "M97.0175 91H64.9775C64.2158 91 63.5203 91.4326 63.1836 92.1158L56.2834 106.116C55.6283 107.445 56.5956 109 58.0773 109H104.411C105.917 109 106.882 107.398 106.179 106.066L98.7861 92.0661C98.4398 91.4103 97.7591 91 97.0175 91ZM64.9775 83C61.1692 83 57.6914 85.1631 56.0078 88.5791L49.1076 102.579C45.8323 109.225 50.6685 117 58.0773 117H104.411C111.942 117 116.77 108.99 113.253 102.33L105.86 88.3304C104.129 85.0516 100.725 83 97.0175 83H64.9775Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx28(
          "path",
          {
            d: "M82 80C89.1797 80 95 74.1797 95 67C95 59.8203 89.1797 54 82 54C74.8203 54 69 59.8203 69 67C69 74.1797 74.8203 80 82 80ZM82 88C93.598 88 103 78.598 103 67C103 55.402 93.598 46 82 46C70.402 46 61 55.402 61 67C61 78.598 70.402 88 82 88Z",
            fill: color
          }
        )
      ]
    }
  ) });
};
var CareAcceleratorIconOutlined_default = CareAcceleratorIconOutlinedVariant;

// src/icons/CareAcceleratorIcon/CareAcceleratorIcon.tsx
import { jsx as jsx29 } from "react/jsx-runtime";
var CareAcceleratorIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx29(CareAcceleratorIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx29(CareAcceleratorIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var CareAcceleratorIcon_default = CareAcceleratorIcon;

// src/icons/CareRetentionIcon/Variants/CareRetentionIconDefault.tsx
import { jsx as jsx30, jsxs as jsxs14 } from "react/jsx-runtime";
var CareRetentionIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "care-retention-icon",
  // Extra Properties
  variant,
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx30(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs14("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx30("g", { opacity: backgroundOpacity, "data-testid": "g-background-path", children: /* @__PURE__ */ jsx30(
          "path",
          {
            "data-testid": "path-with-background-color",
            d: "M87.1875 12.5V27.0312H101.688C105.553 27.0312 108.688 30.1653 108.688 34.0312V42.9688C108.688 46.8347 105.553 49.9688 101.688 49.9688H87.1875V64.5C87.1875 68.366 84.0535 71.5 80.1875 71.5H71.1875C67.3215 71.5 64.1875 68.366 64.1875 64.5V49.9688H49.6875C45.8215 49.9688 42.6875 46.8347 42.6875 42.9688V34.0312C42.6875 30.1653 45.8215 27.0312 49.6875 27.0312H64.1875V12.5C64.1875 8.63401 67.3215 5.5 71.1875 5.5H80.1875C84.0535 5.5 87.1875 8.63401 87.1875 12.5Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx30("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx30(
          "path",
          {
            d: "M15.6797 111.895C12.9238 111.895 10.6875 109.658 10.6875 106.902V79.1113C10.7217 76.3828 12.9453 74.1836 15.6797 74.1836C20.0469 74.1836 25.7119 73.1641 30.7236 72.1367L30.8594 72.1094C35.167 71.2266 39.4199 70.3555 42.916 70.3555C43.832 70.3555 44.8809 70.4922 46.0146 70.7305C46.999 70.9355 48.0479 71.2168 49.1309 71.5488C51.4912 72.2734 54.123 73.2773 56.792 74.3984C59.8584 75.6875 63 77.1426 65.873 78.5371C68.0039 79.5703 69.9873 80.5703 71.6816 81.4434C73.0107 82.1289 74.1621 83.0098 75.127 84.041C75.9736 85.0352 76.6768 86.1016 77.0664 86.8633C77.8105 88.3242 78.0176 90.1152 76.9619 92.8398C76.2383 94.709 73.9004 96.4395 69.1514 96.4395C68.3301 96.4395 67.4297 96.3379 66.4434 96.1133C56.2666 93.7754 51.7295 92.3047 51.3359 92.1797L51.3213 92.1738L51.3066 92.1699C49.9941 91.7383 48.582 92.4531 48.1523 93.7656C47.7451 95.0078 48.3643 96.3398 49.5439 96.8438L49.6094 96.875L49.749 96.9199L49.7695 96.9277C50.1719 97.0586 54.833 98.5762 65.3252 100.986C66.6465 101.289 67.9229 101.439 69.1514 101.439C74.8896 101.439 79.8301 99.2773 81.624 94.6445C82.9053 91.3398 83.0244 88.3926 82.0186 85.7188L100.058 81.3418L100.075 81.3398C106.652 79.9297 112.795 82.9824 114.846 88.9766C116.607 94.125 114.938 101.254 107.456 104.852L106.301 105.406C80.0332 118.039 74.916 120.5 62.9736 120.5C60.8145 120.5 58.2051 120.051 55.335 119.383C52.457 118.713 49.2246 117.799 45.7969 116.83L45.7793 116.824C37.4492 114.469 28.2979 111.895 21.5156 111.895H15.6797Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ) })
      ] })
    }
  );
};
var CareRetentionIconDefault_default = CareRetentionIconDefaultVariant;

// src/icons/CareRetentionIcon/Variants/CareRetentionIconOutlined.tsx
import { jsx as jsx31, jsxs as jsxs15 } from "react/jsx-runtime";
var CareRetentionIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "care-retention-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx31(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs15("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx31(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M92.3125 12.5V22.0312H101.812C108.44 22.0312 113.812 27.4038 113.812 34.0312V42.9688C113.812 49.5962 108.44 54.9688 101.812 54.9688H92.3125V64.5C92.3125 71.1274 86.9399 76.5 80.3125 76.5H71.3125C64.6851 76.5 59.3125 71.1274 59.3125 64.5V54.9688H49.8125C43.1851 54.9688 37.8125 49.5962 37.8125 42.9688V34.0312C37.8125 27.4038 43.1851 22.0312 49.8125 22.0312H59.3125V12.5C59.3125 5.87258 64.6851 0.5 71.3125 0.5H80.3125C86.9399 0.5 92.3125 5.87258 92.3125 12.5ZM84.3125 32.0312C83.2079 32.0312 82.3125 31.1358 82.3125 30.0312V12.5C82.3125 11.3954 81.4171 10.5 80.3125 10.5H71.3125C70.2079 10.5 69.3125 11.3954 69.3125 12.5V30.0312C69.3125 31.1358 68.4171 32.0312 67.3125 32.0312H49.8125C48.7079 32.0312 47.8125 32.9267 47.8125 34.0312V42.9688C47.8125 44.0733 48.7079 44.9688 49.8125 44.9688H67.3125C68.4171 44.9688 69.3125 45.8642 69.3125 46.9688V64.5C69.3125 65.6046 70.2079 66.5 71.3125 66.5H80.3125C81.4171 66.5 82.3125 65.6046 82.3125 64.5V46.9688C82.3125 45.8642 83.2079 44.9688 84.3125 44.9688H101.812C102.917 44.9688 103.812 44.0733 103.812 42.9688V34.0312C103.812 32.9267 102.917 32.0312 101.812 32.0312H84.3125Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx31(
          "path",
          {
            d: "M22.8541 81.9179C26.9453 81.0818 30.5899 80.3425 33.0424 80.3425C33.5928 80.3425 34.6252 80.5407 36.1676 81.0149C37.669 81.4766 39.5357 82.1614 41.6977 83.0452C46.0189 84.8118 51.455 87.3467 57.3907 90.404C58.5309 90.991 58.8654 92.3508 58.4823 93.409C58.2702 93.9857 57.9127 94.3304 57.3868 94.521C56.8065 94.7312 55.9218 94.7837 54.6812 94.5008C43.3908 91.9063 38.1084 90.2339 37.4768 90.0333C34.8517 89.156 32.0296 90.5883 31.1628 93.2007C30.2941 95.8184 31.7134 98.6412 34.3248 99.5116L34.3455 99.5184C34.73 99.6439 40.1436 101.411 52.4424 104.236C53.8034 104.548 55.0878 104.696 56.2975 104.696C62.4836 104.696 66.2901 100.888 67.7759 97.0524L92.4 91.0809C93.3639 90.8753 94.0836 90.9571 94.5653 91.1629C95.0178 91.3562 95.3409 91.6889 95.5198 92.2071C95.6765 92.6695 95.694 93.2755 95.4146 93.8967C95.1395 94.5083 94.528 95.2388 93.2501 95.8542L92.5188 96.2063C79.4722 102.481 72.0358 106.055 66.6087 108.073C61.271 110.058 57.9379 110.515 53.0972 110.515C51.7821 110.515 49.7361 110.146 47.1575 109.523C44.6119 108.908 41.6742 108.078 38.6192 107.215L38.5195 107.186C29.8883 104.746 19.8595 101.91 11.6402 101.91H5.80517C3.04849 101.91 0.8125 104.146 0.8125 106.902C0.8125 109.659 3.0485 111.895 5.80517 111.895H11.6402C18.4232 111.895 27.5742 114.47 35.9043 116.824C39.3379 117.795 42.5771 118.711 45.4603 119.383C48.3304 120.051 50.9391 120.5 53.099 120.5C65.041 120.5 70.1582 118.039 96.4255 105.407L97.5808 104.851C105.063 101.255 106.733 94.1249 104.97 88.9767C102.92 82.9823 96.7771 79.9292 90.2 81.3389L90.1823 81.3427L67.2603 86.9033C66.1047 84.6372 64.2459 82.7014 61.8063 81.4438C57.827 79.3947 52.2577 76.6418 46.9173 74.3982C44.2479 73.2766 41.6159 72.274 39.2561 71.5496C36.9236 70.8336 34.7516 70.3555 33.0406 70.3555C29.5452 70.3555 25.2917 71.2266 20.9841 72.1088L20.8491 72.1365C15.8366 73.1639 10.8326 74.1828 6.46624 74.1828C3.70957 74.1828 1.47357 76.4188 1.47357 79.1755C1.47357 81.9321 3.70956 84.1681 6.46624 84.1681C11.8706 84.1681 17.7436 82.9649 22.7837 81.9323L22.8541 81.9179Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var CareRetentionIconOutlined_default = CareRetentionIconOutlinedVariant;

// src/icons/CareRetentionIcon/CareRetentionIcon.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var CareRetentionIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx32(CareRetentionIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx32(CareRetentionIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var CareRetentionIcon_default = CareRetentionIcon;

// src/icons/CBSAIcon/CBSAIcon.tsx
import { jsx as jsx33, jsxs as jsxs16 } from "react/jsx-runtime";
var CBSAIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cbsa-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx33(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs16("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx33(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M12.0714 15.3618C12.0714 14.8095 12.5191 14.3618 13.0714 14.3618C13.6236 14.3618 14.0714 14.8095 14.0714 15.3618C14.0714 15.9141 13.6236 16.3618 13.0714 16.3618C12.5191 16.3618 12.0714 15.9141 12.0714 15.3618Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx33(
          "path",
          {
            d: "M9.73484 12.4909C9.73484 11.9386 9.28713 11.4909 8.73484 11.4909C8.18256 11.4909 7.73484 11.9386 7.73484 12.4909C7.73484 13.0431 8.18256 13.4909 8.73484 13.4909C9.28713 13.4909 9.73484 13.0431 9.73484 12.4909Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx33(
          "path",
          {
            d: "M9.67628 9.80309C9.67628 9.25081 10.124 8.80309 10.6763 8.80309C11.2286 8.80309 11.6763 9.25081 11.6763 9.80309C11.6763 10.3554 11.2286 10.8031 10.6763 10.8031C10.124 10.8031 9.67628 10.3554 9.67628 9.80309Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx33(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M23.609 4.38906C23.3573 4.13828 23.0226 4 22.6668 4C22.4446 4 22.2239 4.05625 22.0289 4.16219C21.8339 4.26812 21.667 4.42281 21.5465 4.60891L20.7646 5.81781C20.66 5.97944 20.5043 6.10131 20.3247 6.16539C20.3001 6.17418 20.2201 6.19229 20.1378 6.21093C20.0594 6.22868 19.9788 6.24693 19.9421 6.25802C19.6109 6.35814 19.3131 6.55169 19.087 6.81719C18.8095 7.14297 18.6562 7.55828 18.6562 7.98578V9.09437C18.6562 9.22141 18.6084 9.34234 18.5217 9.43563C18.435 9.52656 18.3257 9.57766 18.2053 9.58187C18.1992 9.58234 18.1461 9.58052 18.1382 9.57967C17.9033 9.55469 17.7191 9.35627 17.7187 9.11683V8.67292C17.7187 8.30355 17.579 7.95292 17.3254 7.68527C17.2012 7.55355 17.055 7.44855 16.89 7.37308C16.7293 7.29888 16.5587 7.25716 16.3829 7.24834C16.371 7.24773 16.3082 7.24652 16.3082 7.24652C16.1151 7.24652 15.9262 7.29058 15.7617 7.37355C15.5489 7.48042 15.3651 7.65339 15.2151 7.88683C15.0787 8.10105 14.9925 8.32417 14.9325 8.49527C14.9189 8.53605 14.5682 9.10277 14.4318 9.26636C14.2959 9.4323 14.1473 9.57433 14.0821 9.61558C13.9945 9.67652 13.8389 9.73745 13.716 9.73558C13.6509 9.73511 13.5989 9.72292 13.5468 9.69573C13.5023 9.67183 13.4418 9.63011 13.3701 9.52136C13.3406 9.47308 13.326 9.43417 13.3242 9.34464C13.3181 9.14308 13.4601 8.76292 13.7048 8.32792C13.7878 8.17839 13.874 8.01527 13.9443 7.83527C14.0315 7.6098 14.0704 7.4237 14.0714 7.23245C14.0718 7.05667 14.032 6.88511 13.957 6.73511C13.8778 6.5762 13.7526 6.42175 13.5951 6.30597C13.3495 6.12456 13.0453 6.01558 12.7378 6.01558H12.7359C12.5418 6.01558 12.352 6.06222 12.172 6.13581C11.9878 6.21128 11.8178 6.32505 11.6763 6.46619L11.4843 6.65781V5.97719C11.4843 5.74281 11.4016 5.51875 11.2432 5.34578C11.0852 5.17328 10.8723 5.06641 10.6388 5.04531C10.6388 5.04531 4.08544 4.44953 4.01372 4.44953C3.76575 4.44953 3.52163 4.48844 3.28631 4.56531C3.05569 4.64078 2.83941 4.75047 2.64253 4.8925C2.23753 5.18406 1.94189 5.58906 1.7872 6.06297C1.55705 6.76891 1.32933 7.41344 1.11042 7.97875C0.829172 8.70578 0.556359 9.31141 0.321094 9.72953C0.111094 10.1045 0 10.533 0 10.9689C0 11.3481 0.0867187 11.7292 0.250781 12.0714C0.414375 12.4136 0.657656 12.7202 0.952969 12.9587L1.83094 13.6656C2.115 13.8944 2.29312 14.2338 2.32031 14.5966C2.34938 14.9894 2.48203 15.37 2.70469 15.6967C2.92594 16.0216 3.23016 16.2855 3.58406 16.4598C4.22391 16.7758 4.87313 17.2342 5.51297 17.8225C5.73703 18.0283 6.01453 18.1637 6.31453 18.2148C6.40125 18.2294 6.48984 18.2369 6.57703 18.2369C7.01578 18.2369 7.43766 18.0517 7.73484 17.7292L8.13 17.2994L8.13141 17.2998L8.83922 18.3498C9.10781 18.7488 9.53578 19.0162 10.0125 19.0832C10.2731 19.3579 10.6448 19.5156 11.0231 19.5156C11.2294 19.5156 11.4427 19.4676 11.6302 19.3771C11.8294 19.281 11.9986 19.1426 12.1327 18.9659L12.367 18.6578C12.4973 18.4825 12.7486 18.3114 13.0748 18.1769C13.3186 18.0742 14.3437 17.7343 14.9142 17.6875C15.0043 17.68 15.1758 17.7093 15.2691 17.7557C15.3581 17.8016 15.4341 17.8603 15.5142 18.0018C15.885 18.6768 16.3223 19.2286 16.8141 19.6425C17.063 19.852 17.3227 20.0215 17.5861 20.1472C17.8603 20.278 18.1406 20.3618 18.4191 20.3965C18.4725 20.403 18.5264 20.4062 18.5841 20.4062C18.75 20.4044 19.0012 20.3842 19.2638 20.2257C19.3584 20.1676 19.493 20.0635 19.5966 19.8957C19.6656 19.7849 19.7141 19.6557 19.7394 19.5172C19.7467 19.4771 19.7597 19.3314 19.7597 19.299C19.7587 18.9793 19.6308 18.676 19.3988 18.4445L18.7439 17.7915C18.4791 17.5276 18.3328 17.176 18.3323 16.802C18.3328 16.5085 18.4233 16.2273 18.5944 15.9887L19.4433 14.806C19.6369 14.536 19.815 14.251 19.972 13.9581C19.9806 13.942 19.9883 13.9256 19.9949 13.9089C20.0441 13.8292 23.7722 6.7248 23.7722 6.7248C23.9213 6.50402 24 6.24714 24 5.98089V5.33027C23.9995 4.97453 23.8607 4.64031 23.609 4.38906ZM19.1971 13.4163C19.1823 13.4403 19.1699 13.4652 19.1597 13.4909C19.155 13.4986 19.1504 13.5066 19.1461 13.5146C19.0994 13.6015 19.0506 13.6876 18.9999 13.7726V13.2031C18.9999 12.6508 18.5522 12.2031 17.9999 12.2031H15.9999C15.4476 12.2031 14.9999 12.6508 14.9999 13.2031C14.9999 13.7554 15.4476 14.2031 15.9999 14.2031C16.5522 14.2031 16.9999 14.6508 16.9999 15.2031V16.7738C16.9999 17.0109 17.1922 17.2031 17.4293 17.2031C17.4579 17.3675 17.5043 17.5286 17.5682 17.6852C17.6868 17.9748 17.8593 18.2341 18.082 18.4558L18.7368 19.1083C18.8774 19.2314 18.8053 19.3919 18.7992 19.4022C18.7903 19.4153 18.7251 19.4819 18.5339 19.4659C18.172 19.4219 17.7857 19.2348 17.4178 18.925C17.0203 18.5912 16.6462 18.1155 16.3359 17.5497C16.1835 17.2694 15.952 17.043 15.6829 16.9127C15.4556 16.8011 15.2039 16.7467 14.9128 16.7467C14.5271 16.7484 14.1776 16.8404 13.9197 16.918L12.9942 17.2043C12.9016 17.2369 12.8067 17.2726 12.7115 17.3125C12.4832 17.4095 12.3 17.506 12.1359 17.6157C11.9282 17.755 11.76 17.9101 11.6212 18.0901L11.3868 18.3981C11.2978 18.5139 11.1717 18.5762 11.0235 18.5776C10.8637 18.5757 10.7278 18.5031 10.6415 18.3728C10.5543 18.241 10.4081 18.1623 10.2496 18.1623C9.99511 18.1623 9.75839 18.0367 9.61636 17.8262L8.90855 16.7753C8.82136 16.6459 8.70183 16.539 8.56308 16.4673C8.42995 16.3984 8.27794 16.3618 8.13028 16.3618C7.87059 16.3618 7.61625 16.472 7.43995 16.6646L7.0448 17.0945C7.03043 17.1101 7.01532 17.1248 6.99955 17.1388L6.99746 13.2012C6.99717 12.6488 6.5491 12.2013 5.99673 12.2017L4.50012 12.2028C4.22393 12.203 3.99993 11.9791 3.99993 11.7029C3.99993 11.4269 3.77615 11.2031 3.50011 11.2031H2.99993C2.44765 11.2031 1.99993 11.6508 1.99993 12.2031V12.598L1.5412 12.2284C1.1573 11.9195 0.937453 11.4602 0.937453 10.9689C0.937453 10.6933 1.0073 10.4233 1.13902 10.1884C1.20427 10.0719 1.27036 9.94652 1.33753 9.81189L1.72598 8.95834C1.75846 8.88121 1.87299 8.58239 2.01486 8.20312H6.49993C7.05222 8.20312 7.49993 7.75541 7.49993 7.20312V5.70027L10.5468 5.97906V7.78656C10.5468 7.97687 10.6647 8.14656 10.8405 8.21969C10.8977 8.24312 10.9604 8.25531 11.0223 8.25531C11.147 8.25531 11.2657 8.20703 11.3543 8.11844L12.3421 7.135C12.4457 7.03094 12.5894 6.97141 12.7357 6.97141C12.8482 6.97187 12.9455 7.01205 13.0381 7.06984C13.1328 7.12891 13.1315 7.28073 13.126 7.31003C13.116 7.36417 13.0967 7.42858 13.0706 7.49453C13.0317 7.59531 12.9727 7.71578 12.885 7.87328C12.5902 8.41188 12.3923 8.86844 12.3867 9.34469C12.3853 9.59359 12.4556 9.83828 12.585 10.0338C12.7954 10.3572 13.3275 10.6575 13.716 10.6562C14.0292 10.6562 14.3287 10.568 14.6053 10.3847C14.7248 10.3037 14.8364 10.2094 14.9779 10.0594C15.1476 9.87897 15.3192 9.66405 15.4753 9.43389C15.5985 9.24873 15.7359 9.03044 15.8175 8.80309C15.8939 8.57997 15.9925 8.40456 16.0687 8.30491C16.1914 8.1445 16.3923 8.18983 16.4252 8.19836C16.5072 8.21973 16.5817 8.26413 16.6444 8.3297C16.733 8.42298 16.7812 8.54495 16.7812 8.67292V9.11683C16.7825 10.0816 17.9184 10.7916 18.7758 10.389C18.939 10.3126 19.0838 10.2076 19.2061 10.0758C19.2736 10.0038 19.3329 9.92578 19.3836 9.84304C19.5834 9.51656 19.8856 9.20312 20.2684 9.20312C20.6724 9.20312 20.9999 8.87559 20.9999 8.47156V7.42331C20.9999 7.08049 21.1922 6.77426 21.4139 6.51284C21.4637 6.45413 21.51 6.39202 21.5522 6.32678L22.3336 5.11792C22.4068 5.00495 22.531 4.93745 22.6669 4.93745C22.8844 4.93792 23.0621 5.11417 23.0625 5.33027V5.98089C23.0625 6.05917 23.0391 6.13464 22.9955 6.19933C22.9955 6.19933 19.2519 13.3272 19.1971 13.4163Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var CBSAIcon_default = CBSAIcon;

// src/icons/CheckIcon/CheckIcon.tsx
import { jsx as jsx34 } from "react/jsx-runtime";
var CheckIcon = ({
  width = 17,
  height = 13,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "check-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx34(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 17 13",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx34("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx34(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M16.0313 3.23346L7.59068 11.6735C6.96563 12.2987 5.95172 12.2987 5.32609 11.6735L0.968959 7.31609C0.34368 6.69093 0.34368 5.6769 0.968959 5.05162C1.59435 4.42622 2.60819 4.42622 3.23331 5.05138L6.45872 8.27683L13.7666 0.96887C14.392 0.343473 15.4059 0.343947 16.0311 0.96887C16.6563 1.59415 16.6563 2.60783 16.0313 3.23346Z",
          fill: color
        }
      ) })
    }
  );
};
var CheckIcon_default = CheckIcon;

// src/icons/CheckSquareIcon/CheckSquareIcon.tsx
import { jsx as jsx35, jsxs as jsxs17 } from "react/jsx-runtime";
var CheckSquareIcon = ({
  width = 44,
  height = 44,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#1A91CF",
  handleClick,
  direction = "up",
  dataTestId = "check-square-icon",
  shadowColor = "#84CBF0"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx35(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 44 44",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs17("g", { children: [
        /* @__PURE__ */ jsx35(
          "rect",
          {
            opacity: "0.5",
            x: "4",
            y: "4",
            width: "40",
            height: "40",
            rx: "11",
            fill: shadowColor
          }
        ),
        /* @__PURE__ */ jsx35("rect", { width: "40", height: "40", rx: "13", fill: "white" }),
        /* @__PURE__ */ jsx35(
          "path",
          {
            d: "M28.668 0C35.446 0 40 4.756 40 11.832V28.168C40 35.244 35.446 40 28.666 40H11.33C4.552 40 0 35.244 0 28.168V11.832C0 4.756 4.552 0 11.33 0H28.668ZM28.668 3H11.33C6.27 3 3 6.466 3 11.832V28.168C3 33.534 6.27 37 11.33 37H28.666C33.728 37 37 33.534 37 28.168V11.832C37 6.466 33.728 3 28.668 3Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx35(
          "path",
          {
            d: "M26.5606 16.4376L18.6475 24.3502C18.0615 24.9363 17.111 24.9363 16.5245 24.3502L12.4396 20.2651C11.8535 19.679 11.8535 18.7283 12.4396 18.1421C13.026 17.5558 13.9764 17.5558 14.5625 18.1419L17.5863 21.1658L24.4375 14.3146C25.0238 13.7283 25.9743 13.7287 26.5604 14.3146C27.1465 14.9008 27.1465 15.8511 26.5606 16.4376Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var CheckSquareIcon_default = CheckSquareIcon;

// src/icons/CheckUpMedicalReportClipBoardIcon/CheckUpMedicalReportClipBoardIcon.tsx
import { jsx as jsx36, jsxs as jsxs18 } from "react/jsx-runtime";
var CheckUpMedicalReportClipBoardIcon = ({
  width = 46,
  height = 46,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#DDE8EE",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "clipboard-icon",
  backgroundColor = "#DDE8EE"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx36(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 35 34",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs18("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx36(
          "path",
          {
            d: "M0.125 6.25C0.125 5.25544 0.520088 4.30161 1.22335 3.59835C1.92661 2.89509 2.88044 2.5 3.875 2.5H25.125C26.1196 2.5 27.0734 2.89509 27.7767 3.59835C28.4799 4.30161 28.875 5.25544 28.875 6.25V31.25C28.875 32.2446 28.4799 33.1984 27.7767 33.9017C27.0734 34.6049 26.1196 35 25.125 35H3.875C2.88044 35 1.92661 34.6049 1.22335 33.9017C0.520088 33.1984 0.125 32.2446 0.125 31.25V6.25Z",
            fill: backgroundColor
          }
        ),
        /* @__PURE__ */ jsx36(
          "path",
          {
            fill: color,
            "data-testid": "path-with-color",
            d: "M8.25 2.5C8.25 1.83696 8.51339 1.20107 8.98223 0.732233C9.45107 0.263392 10.087 0 10.75 0L18.25 0C18.913 0 19.5489 0.263392 20.0178 0.732233C20.4866 1.20107 20.75 1.83696 20.75 2.5V3.75C20.75 4.41304 20.4866 5.04893 20.0178 5.51777C19.5489 5.98661 18.913 6.25 18.25 6.25H10.75C10.087 6.25 9.45107 5.98661 8.98223 5.51777C8.51339 5.04893 8.25 4.41304 8.25 3.75V2.5Z"
          }
        ),
        /* @__PURE__ */ jsx36(
          "path",
          {
            fill: color,
            d: "M12.9074 12.6626C12.7783 12.6623 12.6504 12.6875 12.531 12.7367C12.4117 12.786 12.3033 12.8583 12.212 12.9496C12.1207 13.0409 12.0483 13.1494 11.999 13.2687C11.9498 13.3881 11.9246 13.516 11.9249 13.6451V17.5451H8.02492C7.76501 17.5451 7.51574 17.6483 7.33196 17.8321C7.14817 18.0159 7.04492 18.2652 7.04492 18.5251V21.7126C7.04492 22.2551 7.48242 22.6951 8.02492 22.6951H11.9249V26.5951C11.9249 27.1376 12.3649 27.5751 12.9049 27.5751H16.0949C16.2236 27.5751 16.3511 27.5498 16.47 27.5005C16.5889 27.4513 16.6969 27.3791 16.7879 27.2881C16.8789 27.1971 16.9511 27.089 17.0003 26.9701C17.0496 26.8512 17.0749 26.7238 17.0749 26.5951V22.6951H20.9749C21.1038 22.6951 21.2315 22.6697 21.3505 22.6203C21.4696 22.5709 21.5777 22.4984 21.6688 22.4072C21.7598 22.3159 21.8319 22.2076 21.881 22.0884C21.9301 21.9692 21.9553 21.8415 21.9549 21.7126V18.5251C21.9549 18.2652 21.8517 18.0159 21.6679 17.8321C21.4841 17.6483 21.2348 17.5451 20.9749 17.5451H17.0749V13.6451C17.0752 13.5162 17.0501 13.3885 17.001 13.2693C16.9519 13.1501 16.8798 13.0418 16.7888 12.9505C16.6977 12.8593 16.5896 12.7868 16.4705 12.7374C16.3515 12.688 16.2238 12.6626 16.0949 12.6626H12.9074Z"
          }
        )
      ] })
    }
  );
};
var CheckUpMedicalReportClipBoardIcon_default = CheckUpMedicalReportClipBoardIcon;

// src/icons/CircularDollarSignIcon/CircularDollarSignIcon.tsx
import { jsx as jsx37, jsxs as jsxs19 } from "react/jsx-runtime";
var CircularDollarSignIcon = ({
  width = 46,
  height = 50,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#015F94",
  backgroundColor = "#DDE8EE",
  backgroundOpacity = "1",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "circular-dollar-sign-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsxs19(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 35 35",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: [
        /* @__PURE__ */ jsx37(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M17.5 1.95781C8.9162 1.95781 1.95776 8.91625 1.95776 17.5C1.95776 26.0836 8.9162 33.0422 17.5 33.0422C26.0836 33.0422 33.0421 26.0836 33.0421 17.5C33.0421 8.91625 26.0836 1.95781 17.5 1.95781Z",
            fill: backgroundColor,
            opacity: backgroundOpacity
          }
        ),
        /* @__PURE__ */ jsx37(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M17.5 3.28407C9.64871 3.28407 3.28407 9.64871 3.28407 17.5C3.28407 25.3512 9.64873 31.7159 17.5 31.7159C25.3511 31.7159 31.7159 25.3511 31.7159 17.5C31.7159 9.64873 25.3512 3.28407 17.5 3.28407ZM0 17.5C0 7.83497 7.83497 0 17.5 0C27.1649 0 35 7.83497 35 17.5C35 27.1649 27.1649 35 17.5 35C7.83497 35 0 27.1649 0 17.5Z",
            fill: backgroundColor,
            opacity: backgroundOpacity
          }
        ),
        /* @__PURE__ */ jsx37("mask", { id: "path-3-inside-1_6945_6926", fill: "white", children: /* @__PURE__ */ jsx37(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18.6085 8.10133C18.6085 7.49315 18.1122 7 17.5001 7C16.888 7 16.3917 7.49315 16.3917 8.10133V9.72132C15.6304 9.83367 14.8848 10.0953 14.2526 10.571C13.2591 11.3189 12.6973 12.4822 12.6973 13.9749C12.6973 14.7022 12.8676 15.3488 13.2032 15.9075C13.5349 16.4595 13.9923 16.8666 14.4805 17.1754C15.386 17.7478 16.5403 18.0603 17.4937 18.3184L17.5779 18.3413C18.6409 18.6292 19.4767 18.8661 20.0685 19.2403C20.3422 19.4135 20.5198 19.5913 20.6325 19.7791C20.7415 19.9604 20.8251 20.2088 20.8251 20.5826C20.8251 21.4771 20.4625 22.0394 19.9322 22.4085C19.3573 22.8086 18.5065 23.0274 17.5398 22.9929C16.0594 22.94 14.6493 22.3122 13.9534 21.3902C13.5861 20.9035 12.8914 20.805 12.4017 21.17C11.912 21.5348 11.8129 22.2251 12.1802 22.7117C13.1695 24.0226 14.7772 24.8042 16.3917 25.0827V27.1903C16.3917 27.7985 16.888 28.2917 17.5001 28.2917C18.1122 28.2917 18.6085 27.7985 18.6085 27.1903V25.1478C19.536 25.0372 20.4431 24.742 21.2039 24.2126C22.3226 23.4342 23.0417 22.2004 23.0417 20.5826C23.0417 19.8552 22.8714 19.2088 22.5359 18.6503C22.2042 18.0979 21.7466 17.6908 21.2586 17.3821C20.3531 16.8096 19.1988 16.4971 18.2454 16.239L18.1611 16.2162C17.0981 15.9283 16.2624 15.6913 15.6706 15.3171C15.397 15.144 15.2193 14.9661 15.1065 14.7783C14.9976 14.5971 14.914 14.3488 14.914 13.9749C14.914 13.0741 15.2247 12.6025 15.5911 12.3268C16.0038 12.0162 16.6488 11.8362 17.4702 11.8581C18.8723 11.8957 20.369 12.5095 21.1497 13.2852C21.5826 13.7153 22.2843 13.7153 22.7171 13.2852C23.1499 12.8551 23.1499 12.1578 22.7171 11.7278C21.7087 10.7257 20.1582 10.0198 18.6085 9.76186V8.10133Z"
          }
        ) }),
        /* @__PURE__ */ jsx37(
          "path",
          {
            d: "M16.3917 9.72132L17.8516 19.6142L26.3917 18.354V9.72132H16.3917ZM14.2526 10.571L8.2401 2.5804L8.2385 2.58161L14.2526 10.571ZM13.2032 15.9075L4.63067 21.0565L4.63182 21.0584L13.2032 15.9075ZM14.4805 17.1754L9.13582 25.6272L9.13661 25.6277L14.4805 17.1754ZM17.4937 18.3184L20.1106 8.66692L20.1069 8.66592L17.4937 18.3184ZM17.5779 18.3413L14.961 27.9928L14.9635 27.9935L17.5779 18.3413ZM20.0685 19.2403L25.415 10.7896L25.413 10.7883L20.0685 19.2403ZM20.6325 19.7791L12.0573 24.9238L12.0619 24.9313L20.6325 19.7791ZM19.9322 22.4085L25.6446 30.6163L25.6448 30.6162L19.9322 22.4085ZM17.5398 22.9929L17.1828 32.9865L17.1832 32.9865L17.5398 22.9929ZM13.9534 21.3902L5.9713 27.414L5.97142 27.4141L13.9534 21.3902ZM12.4017 21.17L18.3762 29.1891L18.3776 29.188L12.4017 21.17ZM12.1802 22.7117L20.162 16.6875L20.1615 16.6869L12.1802 22.7117ZM16.3917 25.0827H26.3917V16.6601L18.0917 15.2282L16.3917 25.0827ZM18.6085 25.1478L17.4244 15.2181L8.60847 16.2694V25.1478H18.6085ZM21.2039 24.2126L15.4927 16.0039L15.4922 16.0043L21.2039 24.2126ZM22.5359 18.6503L31.1088 13.5019L31.1084 13.5012L22.5359 18.6503ZM21.2586 17.3821L26.6043 8.93088L26.6025 8.92971L21.2586 17.3821ZM18.2454 16.239L20.8586 6.58647L20.8584 6.58644L18.2454 16.239ZM18.1611 16.2162L15.5467 25.8684L15.548 25.8687L18.1611 16.2162ZM15.6706 15.3171L10.3241 23.7678L10.3261 23.7691L15.6706 15.3171ZM15.1065 14.7783L23.6789 9.62915L23.6782 9.628L15.1065 14.7783ZM15.5911 12.3268L21.6042 20.317L21.6045 20.3167L15.5911 12.3268ZM17.4702 11.8581L17.7382 1.86168L17.7365 1.86163L17.4702 11.8581ZM21.1497 13.2852L14.1012 20.3788L14.1023 20.3799L21.1497 13.2852ZM18.6085 9.76186H8.60847V18.2347L16.9663 19.6261L18.6085 9.76186ZM28.6085 8.10133C28.6085 1.9103 23.5748 -3 17.5001 -3V17C12.6495 17 8.60847 13.076 8.60847 8.10133H28.6085ZM17.5001 -3C11.4254 -3 6.39172 1.91032 6.39172 8.10133H26.3917C26.3917 13.076 22.3507 17 17.5001 17V-3ZM6.39172 8.10133V9.72132H26.3917V8.10133H6.39172ZM14.9319 -0.171544C12.921 0.125192 10.4987 0.880901 8.2401 2.5804L20.2652 18.5616C19.271 19.3097 18.3398 19.5422 17.8516 19.6142L14.9319 -0.171544ZM8.2385 2.58161C4.40355 5.46843 2.69727 9.78059 2.69727 13.9749H22.6973C22.6973 14.4891 22.5972 15.2886 22.175 16.1994C21.7419 17.1337 21.0753 17.9517 20.2668 18.5604L8.2385 2.58161ZM2.69727 13.9749C2.69727 16.3173 3.26365 18.7806 4.63067 21.0565L21.7757 10.7584C22.4716 11.917 22.6973 13.0872 22.6973 13.9749H2.69727ZM4.63182 21.0584C5.95855 23.2661 7.68446 24.7094 9.13582 25.6272L19.8252 8.7235C20.3002 9.02385 21.1113 9.65296 21.7745 10.7565L4.63182 21.0584ZM9.13661 25.6277C11.5459 27.151 14.2399 27.7975 14.8806 27.971L20.1069 8.66592C19.9844 8.63274 19.8848 8.60576 19.7869 8.57875C19.6914 8.55242 19.6108 8.52969 19.5392 8.50887C19.3928 8.46633 19.3095 8.43908 19.2665 8.42404C19.2229 8.40882 19.2539 8.4176 19.3371 8.45544C19.4192 8.49273 19.5933 8.57688 19.8245 8.723L9.13661 25.6277ZM14.8769 27.97L14.961 27.9928L20.1948 8.68974L20.1106 8.66692L14.8769 27.97ZM14.9635 27.9935C15.2246 28.0642 15.4056 28.114 15.5548 28.1571C15.6253 28.1775 15.677 28.1931 15.7143 28.2046C15.7325 28.2103 15.746 28.2146 15.7552 28.2175C15.7643 28.2205 15.7685 28.2219 15.7681 28.2218C15.7677 28.2217 15.762 28.2197 15.7516 28.216C15.7411 28.2122 15.7251 28.2063 15.7042 28.1982C15.6631 28.1824 15.5972 28.1559 15.5117 28.1179C15.3461 28.0442 15.0662 27.9087 14.7239 27.6923L25.413 10.7883C23.2811 9.44022 20.8017 8.85411 20.1923 8.68906L14.9635 27.9935ZM14.7219 27.691C14.0322 27.2547 12.9399 26.3947 12.0573 24.9238L29.2076 14.6345C28.0997 12.7879 26.6521 11.5723 25.415 10.7896L14.7219 27.691ZM12.0619 24.9313C11.6419 24.2327 11.3116 23.4634 11.0977 22.6489C10.8888 21.8537 10.8251 21.1459 10.8251 20.5826H30.8251C30.8251 18.589 30.3432 16.5235 29.203 14.6269L12.0619 24.9313ZM10.8251 20.5826C10.8251 19.6423 11.0269 18.3693 11.7261 17.0393C12.4255 15.7089 13.3777 14.7867 14.2196 14.2008L25.6448 30.6162C28.8287 28.4001 30.8251 24.7891 30.8251 20.5826H10.8251ZM14.2198 14.2007C15.1049 13.5847 15.9192 13.2956 16.4789 13.1569C17.0568 13.0138 17.5379 12.9865 17.8964 12.9993L17.1832 32.9865C19.6639 33.0751 22.8254 32.5784 25.6446 30.6163L14.2198 14.2007ZM17.8968 12.9993C18.4135 13.0177 18.9562 13.1304 19.5287 13.386C20.0491 13.6183 21.0309 14.1678 21.9354 15.3663L5.97142 27.4141C8.98241 31.4039 13.6358 32.8598 17.1828 32.9865L17.8968 12.9993ZM21.9356 15.3665C18.2448 10.4758 11.3107 9.51121 6.42583 13.1519L18.3776 29.188C14.4721 32.0988 8.92751 31.3313 5.9713 27.414L21.9356 15.3665ZM6.42724 13.1509C1.50055 16.8214 0.480638 23.8109 4.19884 28.7365L20.1615 16.6869C23.1451 20.6394 22.3235 26.2482 18.3762 29.1891L6.42724 13.1509ZM4.19837 28.7359C7.13121 32.6218 11.3402 34.359 14.6917 34.9371L18.0917 15.2282C18.2519 15.2559 18.4967 15.3193 18.8149 15.4917C19.1373 15.6664 19.6619 16.025 20.162 16.6875L4.19837 28.7359ZM6.39172 25.0827V27.1903H26.3917V25.0827H6.39172ZM6.39172 27.1903C6.39172 33.3814 11.4254 38.2917 17.5001 38.2917V18.2917C22.3507 18.2917 26.3917 22.2157 26.3917 27.1903H6.39172ZM17.5001 38.2917C23.5748 38.2917 28.6085 33.3814 28.6085 27.1903H8.60847C8.60847 22.2157 12.6495 18.2917 17.5001 18.2917V38.2917ZM28.6085 27.1903V25.1478H8.60847V27.1903H28.6085ZM19.7926 35.0774C21.9818 34.8164 24.5365 34.0764 26.9155 32.421L15.4922 16.0043C16.3497 15.4076 17.0901 15.258 17.4244 15.2181L19.7926 35.0774ZM26.915 32.4213C30.6895 29.7953 33.0417 25.5116 33.0417 20.5826H13.0417C13.0417 19.8747 13.209 18.9759 13.6909 18.0593C14.1677 17.1522 14.8265 16.4674 15.4927 16.0039L26.915 32.4213ZM33.0417 20.5826C33.0417 18.24 32.4753 15.7772 31.1088 13.5019L13.9631 23.7987C13.2675 22.6404 13.0417 21.4704 13.0417 20.5826H33.0417ZM31.1084 13.5012C29.7811 11.2914 28.0541 9.84791 26.6043 8.93088L15.9128 25.8333C15.4392 25.5337 14.6273 24.9044 13.9635 23.7994L31.1084 13.5012ZM26.6025 8.92971C24.1932 7.40644 21.4992 6.7599 20.8586 6.58647L15.6322 25.8915C15.7547 25.9247 15.8543 25.9517 15.9522 25.9787C16.0477 26.005 16.1283 26.0278 16.2 26.0486C16.3463 26.0911 16.4296 26.1184 16.4727 26.1334C16.5162 26.1486 16.4853 26.1398 16.402 26.102C16.32 26.0647 16.1458 25.9806 15.9147 25.8344L26.6025 8.92971ZM20.8584 6.58644L20.7741 6.56362L15.548 25.8687L15.6323 25.8916L20.8584 6.58644ZM20.7754 6.56397C20.5145 6.49329 20.3335 6.44349 20.1843 6.40036C20.1138 6.37997 20.0621 6.36443 20.0249 6.35288C20.0067 6.34723 19.9932 6.34294 19.9841 6.33997C19.9749 6.33699 19.9708 6.33559 19.9712 6.33572C19.9716 6.33585 19.9773 6.3378 19.9877 6.34158C19.9982 6.34536 20.0142 6.35126 20.0351 6.35934C20.0763 6.37519 20.1422 6.40162 20.2276 6.43966C20.3932 6.51339 20.6731 6.64882 21.0152 6.86515L10.3261 23.7691C12.4579 25.1171 14.9368 25.7032 15.5467 25.8684L20.7754 6.56397ZM21.0172 6.86643C21.7077 7.30329 22.7977 8.16209 23.6789 9.62915L6.53413 19.9275C7.64094 21.7701 9.08621 22.9847 10.3241 23.7678L21.0172 6.86643ZM23.6782 9.628C24.098 10.3267 24.428 11.0958 24.6417 11.9097C24.8504 12.7045 24.914 13.4118 24.914 13.9749H4.91402C4.91402 15.9677 5.39549 18.0325 6.53482 19.9287L23.6782 9.628ZM24.914 13.9749C24.914 14.6735 24.7911 15.8274 24.1793 17.1404C23.5394 18.5134 22.5912 19.5741 21.6042 20.317L9.57797 4.33665C6.39146 6.7347 4.91402 10.3413 4.91402 13.9749H24.914ZM21.6045 20.3167C20.5673 21.0974 19.5707 21.4721 18.8665 21.653C18.1497 21.8372 17.5739 21.8644 17.204 21.8545L17.7365 1.86163C15.5369 1.80305 12.3937 2.21745 9.57761 4.33693L21.6045 20.3167ZM17.2023 21.8545C16.6072 21.8385 16.1832 21.7194 15.8749 21.5978C15.6995 21.5286 15.473 21.4237 15.2056 21.2592C14.9503 21.1023 14.5511 20.8258 14.1012 20.3788L28.1982 6.19163C25.3262 3.33787 21.2578 1.95603 17.7382 1.86168L17.2023 21.8545ZM14.1023 20.3799C18.4344 24.6831 25.4321 24.6847 29.7656 20.3788L15.6686 6.19163C19.1364 2.74584 24.7309 2.74742 28.1971 6.19053L14.1023 20.3799ZM29.7656 20.3788C34.1337 16.0384 34.1337 8.97459 29.7656 4.63419L15.6686 18.8213C12.1661 15.341 12.1661 9.6719 15.6686 6.19163L29.7656 20.3788ZM29.7656 4.63419C26.9799 1.86623 23.3749 0.41772 20.2506 -0.102387L16.9663 19.6261C16.8445 19.6058 16.7343 19.5737 16.5965 19.5086C16.4759 19.4517 16.118 19.2679 15.6686 18.8213L29.7656 4.63419ZM28.6085 9.76186V8.10133H8.60847V9.76186H28.6085Z",
            fill: color,
            opacity,
            mask: "url(#path-3-inside-1_6945_6926)"
          }
        )
      ]
    }
  );
};
var CircularDollarSignIcon_default = CircularDollarSignIcon;

// src/icons/CityIcon/CityIcon.tsx
import { jsx as jsx38, jsxs as jsxs20 } from "react/jsx-runtime";
var CityIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "city-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx38(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs20("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx38(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M3.793 16.4066C3.51042 16.4066 3.28133 16.6357 3.28133 16.9182V17.4651C3.28133 17.7477 3.51042 17.9768 3.793 17.9768C4.07558 17.9768 4.30468 17.7477 4.30468 17.4651V16.9182C4.30468 16.6357 4.07558 16.4066 3.793 16.4066Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M3.28133 19.6527C3.28133 19.3701 3.51042 19.141 3.793 19.141C4.07558 19.141 4.30468 19.3701 4.30468 19.6527V20.1996C4.30468 20.4821 4.07558 20.7112 3.793 20.7112C3.51042 20.7112 3.28133 20.4821 3.28133 20.1996V19.6527Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M5.9805 19.141C5.69792 19.141 5.46882 19.3701 5.46882 19.6527V20.1996C5.46882 20.4821 5.69792 20.7112 5.9805 20.7112C6.26308 20.7112 6.49217 20.4821 6.49217 20.1996V19.6527C6.49217 19.3701 6.26308 19.141 5.9805 19.141Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M8.16806 19.141C7.88548 19.141 7.65639 19.3701 7.65639 19.6527V20.1996C7.65639 20.4821 7.88548 20.7112 8.16806 20.7112C8.45064 20.7112 8.67973 20.4821 8.67973 20.1996V19.6527C8.67973 19.3701 8.45064 19.141 8.16806 19.141Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M5.9805 16.4066C5.69792 16.4066 5.46882 16.6357 5.46882 16.9182V17.4651C5.46882 17.7477 5.69792 17.9768 5.9805 17.9768C6.26308 17.9768 6.49217 17.7477 6.49217 17.4651V16.9182C6.49217 16.6357 6.26308 16.4066 5.9805 16.4066Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M8.16806 16.4066C7.88548 16.4066 7.65639 16.6357 7.65639 16.9182V17.4651C7.65639 17.7477 7.88548 17.9768 8.16806 17.9768C8.45064 17.9768 8.67973 17.7477 8.67973 17.4651V16.9182C8.67973 16.6357 8.45064 16.4066 8.16806 16.4066Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M15.8246 7.65633H15.83C16.1126 7.65633 16.3417 7.88549 16.3417 8.168C16.3417 8.45058 16.1126 8.67968 15.83 8.67968C15.5475 8.67968 15.3156 8.45058 15.3156 8.168C15.3156 7.88542 15.542 7.65633 15.8246 7.65633Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M15.8246 14.219H15.83C16.1126 14.219 16.3417 14.448 16.3417 14.7307C16.3417 15.0133 16.1126 15.2424 15.83 15.2424C15.5475 15.2424 15.3156 15.0133 15.3156 14.7307C15.3156 14.4481 15.542 14.219 15.8246 14.219Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M15.8246 12.0315H15.83C16.1126 12.0315 16.3417 12.2605 16.3417 12.5431C16.3417 12.8257 16.1126 13.0548 15.83 13.0548C15.5475 13.0548 15.3156 12.8257 15.3156 12.5431C15.3156 12.2606 15.542 12.0315 15.8246 12.0315Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M15.8246 9.8439H15.83C16.1126 9.8439 16.3417 10.0731 16.3417 10.3556C16.3417 10.6382 16.1126 10.8673 15.83 10.8673C15.5475 10.8673 15.3156 10.6382 15.3156 10.3556C15.3156 10.073 15.542 9.8439 15.8246 9.8439Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M13.6369 7.65633H13.6424C13.925 7.65633 14.1541 7.88549 14.1541 8.168C14.1541 8.45058 13.925 8.67968 13.6424 8.67968C13.3598 8.67968 13.128 8.45058 13.128 8.168C13.128 7.88542 13.3544 7.65633 13.6369 7.65633Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M14.2191 19.1058C14.2191 18.8232 14.4482 18.5941 14.7308 18.5941C15.0134 18.5941 15.2424 18.8231 15.2424 19.1058V19.6527C15.2424 19.9352 15.0133 20.1643 14.7308 20.1643C14.4482 20.1643 14.2191 19.9352 14.2191 19.6527V19.1058Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            d: "M16.4066 19.1058C16.4066 18.8232 16.6357 18.5941 16.9182 18.5941C17.2008 18.5941 17.4299 18.8231 17.4299 19.1058V19.6527C17.4299 19.9352 17.2008 20.1643 16.9182 20.1643C16.6357 20.1643 16.4066 19.9352 16.4066 19.6527V19.1058Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx38(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M24 23.9925L23.9926 11.4494C23.9926 11.1668 23.7635 10.9377 23.4809 10.9377H18.5238V5.98058C18.5238 5.698 18.2947 5.46891 18.0121 5.46891H17.3814C17.1735 4.39009 16.3212 3.53839 15.2424 3.33052V0.511674C15.2424 0.229093 15.0133 0 14.7307 0C14.4482 0 14.2191 0.229093 14.2191 0.511674V3.33045C13.1403 3.53833 12.288 4.39002 12.0801 5.46884H11.4494C11.1668 5.46884 10.9377 5.69793 10.9377 5.98051V9.84392H7.07433C6.79175 9.84392 6.56266 10.073 6.56266 10.3556V14.219H1.60543C1.32285 14.219 1.09375 14.4481 1.09375 14.7307V22.9692H0.511674C0.229093 22.9692 0 23.1983 0 23.4808C0 23.7634 0.229093 23.9925 0.511674 23.9925H24ZM7.58594 10.8673H13.1253V12.0314H7.58594V10.8673ZM9.84392 22.9692H8.67969V22.3872C8.67969 22.1046 8.4506 21.8755 8.16802 21.8755C7.88544 21.8755 7.65635 22.1046 7.65635 22.3872V22.9692H6.49219V22.3872C6.49219 22.1046 6.26309 21.8755 5.98051 21.8755C5.69793 21.8755 5.46884 22.1046 5.46884 22.3872V22.9692H4.30468V22.3872C4.30468 22.1046 4.07558 21.8755 3.793 21.8755C3.51042 21.8755 3.28133 22.1046 3.28133 22.3872V22.9692H2.1171V15.2424H9.84392V22.9692ZM7.58594 14.2189V13.0548H13.1253V14.2189H7.58594ZM10.8673 22.9692V21.805H12.0315V22.9692H10.8673ZM10.8673 20.7816V19.6174H12.0315V20.7816H10.8673ZM10.8673 18.5941V17.4299H12.0315V18.5941H10.8673ZM10.8673 16.4065V15.2424H13.1253V16.4065H10.8673ZM22.9691 13.1252H21.2933C21.0107 13.1252 20.7816 13.3543 20.7816 13.6369C20.7816 13.9194 21.0107 14.1485 21.2933 14.1485H22.9691V15.3127H21.2933C21.0107 15.3127 20.7816 15.5418 20.7816 15.8244C20.7816 16.1069 21.0107 16.336 21.2933 16.336H22.9691V17.5002H21.2933C21.0107 17.5002 20.7816 17.7293 20.7816 18.0119C20.7816 18.2945 21.0107 18.5235 21.2933 18.5235H22.9691V19.6877H21.2933C21.0107 19.6877 20.7816 19.9168 20.7816 20.1994C20.7816 20.482 21.0107 20.7111 21.2933 20.7111H22.9691V22.969H19.6174V22.387C19.6174 22.1044 19.3883 21.8753 19.1057 21.8753C18.8231 21.8753 18.594 22.1044 18.594 22.387V22.969H17.4299V21.8401C17.4299 21.5576 17.2008 21.3285 16.9182 21.3285C16.6356 21.3285 16.4065 21.5576 16.4065 21.8401V22.9692H15.2424V21.8401C15.2424 21.5576 15.0133 21.3285 14.7307 21.3285C14.4481 21.3285 14.219 21.5576 14.219 21.8401V22.9692H13.0548V17.4299H18.5941V20.1995C18.5941 20.4821 18.8232 20.7112 19.1058 20.7112C19.3883 20.7112 19.6174 20.4821 19.6174 20.1995V16.9182C19.6174 16.6356 19.3883 16.4065 19.1058 16.4065H18.5237V14.7307C18.5237 14.4482 18.2946 14.2191 18.012 14.2191C17.7294 14.2191 17.5003 14.4482 17.5003 14.7307V16.4065H14.1486V10.3556C14.1486 10.073 13.9195 9.84392 13.6369 9.84392H11.9611V6.49219H12.5432C12.8258 6.49219 13.0548 6.26309 13.0548 5.98051C13.0548 5.05643 13.8067 4.30461 14.7307 4.30461C15.6547 4.30461 16.4065 5.05643 16.4065 5.98051C16.4065 6.26309 16.6356 6.49219 16.9182 6.49219H17.5003V12.5432C17.5003 12.8258 17.7294 13.0549 18.0119 13.0549C18.2945 13.0549 18.5236 12.8258 18.5236 12.5432V11.961H22.9691V13.1252Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var CityIcon_default = CityIcon;

// src/icons/CJAnimatedLogoBreath/CJAnimatedLogoBreath.tsx
import { jsx as jsx39, jsxs as jsxs21 } from "react/jsx-runtime";
var CJAnimatedLogoBreath = ({
  width = 60,
  height = 60,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cj-animated-logo-breath-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx39(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 60 60",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs21("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsxs21(
          "path",
          {
            d: "M10.9165 15.2316L18.5066 18.1879L33.1233 12.6178L48.1734 18.1446L55.7591 15.275L32.8675 7L10.9165 15.2316Z",
            fill: "#DEDEDE",
            children: [
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#DEDEDE",
                  to: "#3992C6",
                  begin: "0s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              ),
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#3992C6",
                  to: "#DEDEDE",
                  begin: "2s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs21(
          "path",
          {
            d: "M8.85754 16.4757V22.2625L33.0366 31.3524L57.8615 22.1758L57.6881 16.519L33.21 25.5222L8.85754 16.4757Z",
            fill: "#DEDEDE",
            children: [
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#DEDEDE",
                  to: "#009FD3",
                  begin: "0s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              ),
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#009FD3",
                  to: "#DEDEDE",
                  begin: "2s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs21(
          "path",
          {
            d: "M2 27.3211V33.108L26.179 42.1979L50.9996 33.0213L50.8306 27.3645L26.3524 36.3677L2 27.3211Z",
            fill: "#DEDEDE",
            children: [
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#DEDEDE",
                  to: "#EC9F24",
                  begin: "0s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              ),
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#EC9F24",
                  to: "#DEDEDE",
                  begin: "2s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs21(
          "path",
          {
            d: "M9.03088 38.1233V43.9101L33.2099 53L58.0305 43.8278L57.8614 38.1666L33.3833 47.1698L9.03088 38.1233Z",
            fill: "#DEDEDE",
            children: [
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#DEDEDE",
                  to: "#015F94",
                  begin: "0s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              ),
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#015F94",
                  to: "#DEDEDE",
                  begin: "2s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs21(
          "path",
          {
            d: "M3.95062 26.0337L8.96589 24.2348L16.4693 27.1911L11.5797 29.1417L3.95062 26.0337Z",
            fill: "#DEDEDE",
            children: [
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#DEDEDE",
                  to: "#EC9F24",
                  begin: "0s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              ),
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#EC9F24",
                  to: "#DEDEDE",
                  begin: "2s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs21(
          "path",
          {
            d: "M55.6941 37.0179L50.6788 35.2146L43.1754 38.1753L48.065 40.1259L55.6941 37.0179Z",
            fill: "#DEDEDE",
            children: [
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#DEDEDE",
                  to: "#005980",
                  begin: "0s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              ),
              /* @__PURE__ */ jsx39(
                "animate",
                {
                  attributeName: "fill",
                  from: "#005980",
                  to: "#DEDEDE",
                  begin: "2s",
                  dur: "2s",
                  fill: "freeze",
                  repeatCount: "indefinite"
                }
              )
            ]
          }
        )
      ] })
    }
  );
};
var CJAnimatedLogoBreath_default = CJAnimatedLogoBreath;

// src/icons/CJIcon/CJIcon.tsx
import { jsx as jsx40, jsxs as jsxs22 } from "react/jsx-runtime";
var CJIcon = ({
  width = 192,
  height = 36,
  margin = 0,
  padding = 0,
  color = "",
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cj-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx40(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 192 36",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs22("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx40(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M58.0906 6.96118C62.1346 6.96118 64.204 9.1798 64.204 9.1798L63.0374 10.9472C63.0374 10.9472 61.0864 9.10856 58.1853 9.10856C54.4253 9.10856 51.9772 11.9717 51.9772 15.5541C51.9772 19.1568 54.476 22.2134 58.2089 22.2134C61.3738 22.2134 63.3958 20.0185 63.3958 20.0185L64.6571 21.7147C64.6571 21.7147 62.351 24.3879 58.1379 24.3879C53.093 24.3879 49.5494 20.5206 49.5494 15.5812C49.5494 10.686 53.2114 6.96118 58.0906 6.96118Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M73.8475 16.5548H74.3716V16.3411C74.3716 14.36 73.2524 13.6917 71.7308 13.6917C69.8745 13.6917 68.3765 14.862 68.3765 14.862L67.4264 13.1658C67.4264 13.1658 69.1644 11.7343 71.9235 11.7343C74.9701 11.7343 76.681 13.4067 76.681 16.4598V24.0995H74.5406V22.9529C74.5406 22.4033 74.588 21.9996 74.588 21.9996H74.5406C74.5406 21.9996 73.5668 24.3878 70.686 24.3878C68.6166 24.3878 66.5946 23.1225 66.5946 20.7105C66.5912 16.7245 71.8255 16.5548 73.8475 16.5548ZM71.183 22.522C73.134 22.522 74.3953 20.4696 74.3953 18.6785V18.2985H73.8001C72.0622 18.2985 68.9243 18.4173 68.9243 20.5884C68.9209 21.5688 69.6817 22.522 71.183 22.522Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M80.1941 12.0226H82.4291V14.1225C82.4291 14.6245 82.3818 15.0282 82.3818 15.0282H82.4291C82.9769 13.2608 84.3801 11.8767 86.2365 11.8767C86.5442 11.8767 86.8316 11.9242 86.8316 11.9242V14.2141C86.8316 14.2141 86.5239 14.1428 86.1655 14.1428C84.6912 14.1428 83.3353 15.1945 82.7875 16.9822C82.5745 17.6743 82.5035 18.4138 82.5035 19.1534V24.0927H80.1975V12.0226H80.1941Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M94.1622 11.7343C97.5638 11.7343 99.3728 14.265 99.3728 17.3928C99.3728 17.7015 99.3018 18.3969 99.3018 18.3969H90.5239C90.6423 21.0464 92.5223 22.383 94.6626 22.383C96.732 22.383 98.2299 20.9751 98.2299 20.9751L99.1801 22.6713C99.1801 22.6713 97.3947 24.3913 94.5173 24.3913C90.7336 24.3913 88.1401 21.6468 88.1401 18.0644C88.1435 14.2175 90.737 11.7343 94.1622 11.7343ZM97.016 16.6498C96.945 14.5737 95.6601 13.5695 94.1149 13.5695C92.3532 13.5695 90.9263 14.6687 90.5949 16.6498H97.016Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M101.253 18.4172H103.586V19.133C103.586 21.4229 104.918 22.2133 106.25 22.2133C107.559 22.2133 108.867 21.45 108.867 19.2314V9.29849H104.634V7.24609H111.224V19.2517C111.224 22.9053 108.678 24.3844 106.227 24.3844C103.775 24.3844 101.253 22.8816 101.253 19.2517V18.4172Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M120.702 11.7343C124.293 11.7343 127.197 14.3837 127.197 18.0339C127.197 21.7113 124.296 24.3845 120.702 24.3845C117.108 24.3845 114.207 21.7113 114.207 18.0373C114.207 14.3837 117.111 11.7343 120.702 11.7343ZM120.702 22.3796C122.984 22.3796 124.841 20.5646 124.841 18.0339C124.841 15.5269 122.984 13.7392 120.702 13.7392C118.443 13.7392 116.563 15.5303 116.563 18.0339C116.563 20.5646 118.443 22.3796 120.702 22.3796Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M129.899 12.0226H132.209V19.2314C132.209 20.9039 132.54 22.2405 134.467 22.2405C136.919 22.2405 138.369 20.0693 138.369 17.6811V12.0226H140.675V24.0995H138.44V22.5017C138.44 21.9996 138.488 21.6196 138.488 21.6196H138.44C137.94 22.8138 136.371 24.3878 134.014 24.3878C131.302 24.3878 129.899 22.9562 129.899 19.7572V12.0226Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M144.26 12.0226H146.495V14.1225C146.495 14.6245 146.447 15.0282 146.447 15.0282H146.495C147.042 13.2608 148.446 11.8767 150.302 11.8767C150.613 11.8767 150.897 11.9242 150.897 11.9242V14.2141C150.897 14.2141 150.586 14.1428 150.231 14.1428C148.757 14.1428 147.401 15.1945 146.853 16.9822C146.64 17.6743 146.569 18.4138 146.569 19.1534V24.0927H144.263V12.0226H144.26Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M153.247 12.0226H155.482V13.6204C155.482 14.0987 155.435 14.5024 155.435 14.5024H155.482C155.959 13.4508 157.386 11.7343 160.003 11.7343C162.833 11.7343 164.141 13.2846 164.141 16.3649V24.0995H161.832V16.8907C161.832 15.1945 161.477 13.8579 159.55 13.8579C157.717 13.8579 156.243 15.0758 155.742 16.816C155.6 17.2944 155.553 17.8439 155.553 18.441V24.0961H153.247V12.0226Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M172.777 11.7343C176.179 11.7343 177.988 14.265 177.988 17.3928C177.988 17.7015 177.917 18.3969 177.917 18.3969H169.139C169.257 21.0464 171.137 22.383 173.278 22.383C175.347 22.383 176.845 20.9751 176.845 20.9751L177.795 22.6713C177.795 22.6713 176.01 24.3913 173.132 24.3913C169.349 24.3913 166.755 21.6468 166.755 18.0644C166.759 14.2175 169.352 11.7343 172.777 11.7343ZM175.631 16.6498C175.56 14.5737 174.275 13.5695 172.73 13.5695C170.968 13.5695 169.541 14.6687 169.21 16.6498H175.631Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M180.013 26.6032C180.013 26.6032 180.679 27.1052 181.417 27.1052C182.343 27.1052 183.131 26.4369 183.581 25.3378L184.176 23.9537L179.131 12.0193H181.724L184.747 19.9202C184.984 20.541 185.2 21.3755 185.2 21.3755H185.248C185.248 21.3755 185.437 20.5647 185.653 19.9439L188.578 12.0193H191.077L185.44 26.2673C184.703 28.1297 183.229 29.1067 181.538 29.1067C180.182 29.1067 179.205 28.3672 179.205 28.3672L180.013 26.6032Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M6.95532 6.44214L12.876 8.75575L24.2777 4.39653L36.0175 8.72182L41.9348 6.47606L24.0782 0L6.95532 6.44214Z",
            fill: color || "#3992C6"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M5.34924 7.41577V11.9446L24.2101 19.0584L43.5748 11.8768L43.4395 7.44969L24.3454 14.4957L5.34924 7.41577Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M0 15.9036V20.4324L18.8609 27.5462L38.2221 20.3646L38.0903 15.9375L18.9961 22.9835L0 15.9036Z",
            fill: color || "#EC9F24"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M5.4845 24.3573V28.8861L24.3454 36L43.7066 28.8217L43.5748 24.3912L24.4806 31.4372L5.4845 24.3573Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M1.52161 14.896L5.43376 13.4882L11.2868 15.8018L7.47268 17.3283L1.52161 14.896Z",
            fill: color || "#EC9F24"
          }
        ),
        /* @__PURE__ */ jsx40(
          "path",
          {
            d: "M41.8841 23.4923L37.9719 22.0811L32.1189 24.3981L35.933 25.9246L41.8841 23.4923Z",
            fill: color || "#005980"
          }
        )
      ] })
    }
  );
};
var CJIcon_default = CJIcon;

// src/icons/CJIconByArcadia/CJIconByArcadia.tsx
import { Fragment as Fragment10, jsx as jsx41, jsxs as jsxs23 } from "react/jsx-runtime";
var CJIconByArcadia = ({
  width = 192,
  height = 36,
  margin = 0,
  padding = 0,
  color = "",
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cj-icon-by-arcadia",
  hideCJLogo = false
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  const viewBoxWidth = hideCJLogo ? 120 : 192;
  const viewBoxX = hideCJLogo ? 42 : 0;
  return /* @__PURE__ */ jsx41(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: `${viewBoxX} 0 ${viewBoxWidth} 36`,
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs23("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx41(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M51.683 6.1712C55.2809 6.1712 57.122 8.13804 57.122 8.13804L56.0842 9.7049C56.0842 9.7049 54.3484 8.07489 51.7672 8.07489C48.422 8.07489 46.244 10.6131 46.244 13.7889C46.244 16.9828 48.4671 19.6925 51.7883 19.6925C54.6041 19.6925 56.403 17.7467 56.403 17.7467L57.5251 19.2504C57.5251 19.2504 55.4735 21.6202 51.7251 21.6202C47.2367 21.6202 44.084 18.1918 44.084 13.813C44.084 9.47333 47.342 6.1712 51.683 6.1712Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M65.7019 14.6761H66.1682V14.4866C66.1682 12.7303 65.1724 12.1379 63.8187 12.1379C62.1671 12.1379 60.8344 13.1754 60.8344 13.1754L59.9891 11.6717C59.9891 11.6717 61.5353 10.4026 63.9901 10.4026C66.7006 10.4026 68.2228 11.8852 68.2228 14.5919V21.3646H66.3186V20.3481C66.3186 19.8609 66.3607 19.503 66.3607 19.503H66.3186C66.3186 19.503 65.4522 21.6202 62.8891 21.6202C61.048 21.6202 59.249 20.4984 59.249 18.3602C59.246 14.8265 63.9029 14.6761 65.7019 14.6761ZM63.3313 19.9661C65.0671 19.9661 66.1892 18.1466 66.1892 16.5587V16.2219H65.6597C64.1135 16.2219 61.3218 16.3272 61.3218 18.2519C61.3187 19.121 61.9956 19.9661 63.3313 19.9661Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M71.3484 10.6582H73.3369V12.5198C73.3369 12.9649 73.2948 13.3228 73.2948 13.3228H73.3369C73.8242 11.756 75.0727 10.5289 76.7243 10.5289C76.998 10.5289 77.2537 10.571 77.2537 10.571V12.601C77.2537 12.601 76.98 12.5379 76.6611 12.5379C75.3495 12.5379 74.1431 13.4702 73.6558 15.0551C73.4662 15.6686 73.4031 16.3242 73.4031 16.9798V21.3586H71.3514V10.6582H71.3484Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M83.7758 10.4026C86.8021 10.4026 88.4116 12.6461 88.4116 15.4189C88.4116 15.6926 88.3484 16.3091 88.3484 16.3091H80.5388C80.6441 18.6579 82.3167 19.8428 84.221 19.8428C86.0621 19.8428 87.3948 18.5947 87.3948 18.5947L88.2401 20.0984C88.2401 20.0984 86.6517 21.6232 84.0917 21.6232C80.7253 21.6232 78.418 19.1902 78.418 16.0144C78.421 12.604 80.7284 10.4026 83.7758 10.4026ZM86.3148 14.7603C86.2516 12.9198 85.1085 12.0296 83.7337 12.0296C82.1663 12.0296 80.8968 13.004 80.602 14.7603H86.3148Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M90.0842 16.3272H92.16V16.9618C92.16 18.9918 93.3452 19.6925 94.5305 19.6925C95.6947 19.6925 96.859 19.0158 96.859 17.049V8.24331H93.0925V6.42383H98.9558V17.067C98.9558 20.306 96.6905 21.6172 94.5095 21.6172C92.3284 21.6172 90.0842 20.2849 90.0842 17.067V16.3272V16.3272Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M107.388 10.4026C110.583 10.4026 113.167 12.7514 113.167 15.9873C113.167 19.2474 110.586 21.6172 107.388 21.6172C104.19 21.6172 101.609 19.2474 101.609 15.9903C101.609 12.7514 104.193 10.4026 107.388 10.4026ZM107.388 19.8398C109.419 19.8398 111.07 18.2309 111.07 15.9873C111.07 13.7649 109.419 12.18 107.388 12.18C105.379 12.18 103.706 13.7679 103.706 15.9873C103.706 18.2309 105.379 19.8398 107.388 19.8398Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M115.571 10.6583H117.625V17.049C117.625 18.5316 117.92 19.7166 119.635 19.7166C121.816 19.7166 123.106 17.7918 123.106 15.6746V10.6583H125.158V21.3646H123.17V19.9481C123.17 19.503 123.212 19.1662 123.212 19.1662H123.17C122.724 20.2248 121.328 21.6202 119.232 21.6202C116.819 21.6202 115.571 20.3511 115.571 17.5151V10.6583Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M128.347 10.6582H130.335V12.5198C130.335 12.9649 130.293 13.3228 130.293 13.3228H130.335C130.823 11.756 132.071 10.5289 133.723 10.5289C134 10.5289 134.252 10.571 134.252 10.571V12.601C134.252 12.601 133.975 12.5379 133.66 12.5379C132.348 12.5379 131.142 13.4702 130.654 15.0551C130.465 15.6686 130.402 16.3242 130.402 16.9798V21.3586H128.35V10.6582H128.347Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M136.343 10.6582H138.332V12.0747C138.332 12.4987 138.289 12.8566 138.289 12.8566H138.332C138.756 11.9243 140.025 10.4026 142.354 10.4026C144.872 10.4026 146.036 11.777 146.036 14.5077V21.3646H143.981V14.9738C143.981 13.4701 143.665 12.2852 141.951 12.2852C140.32 12.2852 139.008 13.3649 138.563 14.9077C138.437 15.3317 138.395 15.8189 138.395 16.3482V21.3616H136.343V10.6582V10.6582Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M153.719 10.4026C156.746 10.4026 158.355 12.6461 158.355 15.4189C158.355 15.6926 158.292 16.3091 158.292 16.3091H150.482C150.587 18.6579 152.26 19.8428 154.164 19.8428C156.005 19.8428 157.338 18.5947 157.338 18.5947L158.183 20.0984C158.183 20.0984 156.595 21.6232 154.035 21.6232C150.669 21.6232 148.361 19.1902 148.361 16.0144C148.364 12.604 150.672 10.4026 153.719 10.4026ZM156.258 14.7603C156.195 12.9198 155.052 12.0296 153.677 12.0296C152.11 12.0296 150.84 13.004 150.545 14.7603H156.258Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M160.157 23.584C160.157 23.584 160.75 24.0291 161.405 24.0291C162.23 24.0291 162.931 23.4367 163.331 22.4623L163.86 21.2352L159.372 10.6552H161.679L164.369 17.6594C164.579 18.2098 164.772 18.9496 164.772 18.9496H164.814C164.814 18.9496 164.982 18.2309 165.175 17.6805L167.777 10.6552H170L164.985 23.2863C164.33 24.9374 163.018 25.8035 161.514 25.8035C160.307 25.8035 159.438 25.1479 159.438 25.1479L160.157 23.584Z",
            fill: color || "#015F94"
          }
        ),
        !hideCJLogo && /* @__PURE__ */ jsxs23(Fragment10, { children: [
          /* @__PURE__ */ jsx41(
            "path",
            {
              d: "M6.18823 5.71105L11.4558 7.7621L21.5998 3.89759L32.0447 7.73203L37.3093 5.74113L21.4223 0L6.18823 5.71105Z",
              fill: color || "#3992C6"
            }
          ),
          /* @__PURE__ */ jsx41(
            "path",
            {
              d: "M4.75928 6.57416V10.589L21.5397 16.8956L38.7683 10.5289L38.648 6.60423L21.66 12.8506L4.75928 6.57416Z",
              fill: color || "#009FD3"
            }
          ),
          /* @__PURE__ */ jsx41(
            "path",
            {
              d: "M0 14.0987V18.1136L16.7804 24.4201L34.006 18.0534L33.8887 14.1288L16.9007 20.3751L0 14.0987Z",
              fill: color || "#EC9F24"
            }
          ),
          /* @__PURE__ */ jsx41(
            "path",
            {
              d: "M4.87939 21.5931V25.608L21.6598 31.9145L38.8854 25.5509L38.7681 21.6232L21.7801 27.8696L4.87939 21.5931Z",
              fill: color || "#015F94"
            }
          ),
          /* @__PURE__ */ jsx41(
            "path",
            {
              d: "M1.35376 13.2055L4.83438 11.9575L10.0418 14.0085L6.6484 15.3618L1.35376 13.2055Z",
              fill: color || "#EC9F24"
            }
          ),
          /* @__PURE__ */ jsx41(
            "path",
            {
              d: "M37.2639 20.8262L33.7833 19.5751L28.5759 21.6292L31.9693 22.9825L37.2639 20.8262Z",
              fill: color || "#015F94"
            }
          )
        ] }),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M83.6259 26.1691V30.8594H82.6304V26.1691H83.6259ZM83.1345 24.3727C83.287 24.3727 83.4184 24.4136 83.5285 24.4956C83.6415 24.5775 83.698 24.7187 83.698 24.9192C83.698 25.117 83.6415 25.2582 83.5285 25.3429C83.4184 25.4249 83.287 25.4658 83.1345 25.4658C82.9763 25.4658 82.8422 25.4249 82.732 25.3429C82.6247 25.2582 82.571 25.117 82.571 24.9192C82.571 24.7187 82.6247 24.5775 82.732 24.4956C82.8422 24.4136 82.9763 24.3727 83.1345 24.3727Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M78.9474 30.9442C78.3882 30.9442 77.9391 30.7408 77.6002 30.3341C77.2641 29.9245 76.949 29.3214 76.949 28.5249C76.949 27.7199 77.2669 27.1112 77.6087 26.6988C77.9532 26.2864 78.4065 26.0802 78.9686 26.0802C79.2058 26.0802 79.4498 26.0882 79.6277 26.1532C79.8056 26.2153 79.9418 26.2664 80.0689 26.3738C80.1988 26.4811 80.2026 26.5208 80.3497 26.6679C80.3497 26.575 80.3497 26.6679 80.3497 26.4319C80.3497 26.1958 80.3497 26.1532 80.3497 25.9531C80.3497 25.753 80.3497 24.2668 80.3497 24.2668H81.3495V30.8594H80.51V30.2197L80.4365 30.1976C80.3518 30.3332 80.1576 30.4781 80.0277 30.5883C79.9006 30.6956 79.7481 30.7818 79.5702 30.8467C79.3951 30.9117 79.1875 30.9442 78.9474 30.9442ZM79.227 30.1349C79.6535 30.1349 79.9543 30.0121 80.1294 29.7663C80.3045 29.5206 80.3949 29.152 80.4005 28.6605V28.5291C80.4005 28.0038 80.3158 27.6012 80.1464 27.3216C79.9769 27.0391 79.6676 26.8979 79.2185 26.8979C78.8599 26.8979 78.5859 27.0434 78.3967 27.3343C78.2102 27.6224 77.97 28.0249 77.97 28.5418C77.97 29.0587 78.2102 29.4542 78.3967 29.7282C78.5859 29.9993 78.8627 30.1349 79.227 30.1349Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M74.3241 26.0802C74.9173 26.0802 75.3649 26.2115 75.6671 26.4742C75.9722 26.7369 76.1247 27.1465 76.1247 27.7029V30.8595H75.2265V30.1943H75.1926C75.0599 30.3637 74.9229 30.5036 74.7817 30.6137C74.6405 30.7239 74.4767 30.8058 74.2903 30.8595C74.1067 30.9159 73.8821 30.9442 73.6166 30.9442C73.337 30.9442 73.0871 30.8933 72.8668 30.7917C72.6465 30.6872 72.4728 30.529 72.3457 30.3171C72.2186 30.1053 72.155 29.8369 72.155 29.5121C72.155 29.0291 72.3344 28.6661 72.6931 28.4232C73.0546 28.1803 73.5955 27.9434 74.3241 27.918H75.0689V27.6974C75.0689 27.3726 75.0008 27.3211 74.8483 27.1827C74.6986 27.0443 74.5341 26.9621 74.2601 26.9621C74.0257 26.9621 73.8186 26.9678 73.5983 27.0356C73.378 27.1034 73.072 27.3044 72.863 27.4033L72.4219 26.6679C72.6506 26.5465 73.1181 26.2709 73.409 26.1946C73.7028 26.1183 74.0078 26.0802 74.3241 26.0802ZM75.0689 28.6393H74.5317C74.0346 28.6563 73.6858 28.741 73.4853 28.8935C73.2848 29.046 73.1845 29.2551 73.1845 29.5206C73.1845 29.7522 73.2537 29.9217 73.3921 30.029C73.5305 30.1335 73.7127 30.1858 73.9386 30.1858C74.2832 30.1858 74.4753 30.0249 74.7013 29.83C74.93 29.6322 75.0689 29.3152 75.0689 29.0946C75.0689 28.874 75.0689 28.6393 75.0689 28.6393Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M86.6498 26.0061C87.243 26.0061 87.6906 26.1374 87.9928 26.4001C88.2979 26.6628 88.4504 27.0724 88.4504 27.6288V30.7854H87.5522V30.1202H87.5183C87.3856 30.2896 87.2486 30.4295 87.1074 30.5396C86.9662 30.6498 86.8023 30.7317 86.6159 30.7854C86.4324 30.8418 86.2078 30.8701 85.9423 30.8701C85.6627 30.8701 85.4128 30.8193 85.1925 30.7176C84.9722 30.6131 84.7985 30.4549 84.6714 30.243C84.5443 30.0312 84.4807 29.7628 84.4807 29.438C84.4807 28.955 84.6601 28.592 85.0188 28.3491C85.3803 28.1062 85.9211 27.8693 86.6498 27.8439H87.3946V27.6233C87.3946 27.2985 87.3265 27.247 87.174 27.1086C87.0243 26.9702 86.8597 26.888 86.5858 26.888C86.3514 26.888 86.1443 26.8937 85.924 26.9615C85.7037 27.0293 85.3977 27.2303 85.1887 27.3292L84.7475 26.5938C84.9763 26.4724 85.4438 26.1968 85.7347 26.1205C86.0285 26.0442 86.3335 26.0061 86.6498 26.0061ZM87.3946 28.5652H86.8574C86.3603 28.5822 86.0115 28.6669 85.811 28.8194C85.6105 28.972 85.5102 29.181 85.5102 29.4465C85.5102 29.6781 85.5794 29.8476 85.7178 29.9549C85.8562 30.0594 86.0384 30.1117 86.2643 30.1117C86.6089 30.1117 86.801 29.9508 87.027 29.7559C87.2557 29.5581 87.3946 29.2411 87.3946 29.0205C87.3946 28.7999 87.3946 28.5652 87.3946 28.5652Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M69.7013 31.0065C69.1866 31.0065 68.6591 30.8111 68.3343 30.6388C68.0095 30.4665 67.7441 30.1859 67.5689 29.83C67.3938 29.4741 67.3091 29.0856 67.3091 28.5376C67.3091 27.967 67.4504 27.6177 67.6425 27.2562C67.8345 26.8946 68.3042 26.4859 68.5248 26.3738C68.7454 26.2616 69.0182 26.0802 69.5672 26.0802C70.1162 26.0802 70.5837 26.1532 70.8778 26.3002C71.1719 26.4473 71.4549 26.7359 71.6131 26.815C71.6131 26.815 71.1719 27.4033 71.0248 27.5503C70.8778 27.4033 70.6572 27.2562 70.5101 27.1827C70.3631 27.1091 69.9415 27.0356 69.7748 27.0356C69.5009 27.0356 69.1698 27.0952 69.0395 27.1827C68.9093 27.2702 68.5984 27.5503 68.5248 27.6974C68.4513 27.8445 68.3343 28.1732 68.3343 28.5291C68.3343 28.8737 68.3778 29.2417 68.4513 29.3887C68.5248 29.5358 68.6944 29.6947 68.8925 29.83C69.0906 29.9652 69.3337 30.1241 69.6278 30.1241C69.9219 30.1241 70.0862 30.1127 70.2895 30.0506C70.4929 29.9884 70.9513 29.83 71.2454 29.5358L71.6866 30.2712C71.5395 30.4182 70.9513 30.7859 70.7307 30.8595C70.5101 30.933 70.216 31.0065 69.7013 31.0065Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M66.415 26.1268C66.4941 26.1268 66.5497 26.1268 66.655 26.1268C66.7603 26.1268 66.8338 26.1268 66.9073 26.1268V27.1827C66.848 27.1657 66.7007 27.1911 66.6132 27.1827C66.5285 27.1742 66.5339 27.1827 66.4661 27.1827C66.2882 27.1827 66.2595 27.1969 66.0985 27.2562C65.9375 27.3127 65.8079 27.4033 65.7308 27.4768C65.6538 27.5503 65.4758 27.7476 65.4367 27.8445C65.3977 27.9414 65.2805 28.1747 65.2805 28.4063V30.8595H64.2806L64.2603 26.2267H65.2161L65.1957 26.9954H65.2423C65.3355 26.8287 65.4513 26.6762 65.5897 26.5378C65.7281 26.3994 65.8044 26.3002 65.9514 26.2267C66.0985 26.1531 66.2906 26.1268 66.415 26.1268Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M62.6312 30.8595L62.0254 29.1393H59.6572L59.0513 30.8595H57.9795L60.2895 24.4619H61.3189L63.7116 30.8595H62.6312ZM61.7627 28.2665L61.1739 26.5717C61.1513 26.4983 61.1188 26.3952 61.0764 26.2624C61.034 26.1268 60.9917 25.9898 60.9493 25.8514C60.907 25.7102 60.8716 25.5916 60.8434 25.4955C60.8152 25.6113 60.7799 25.7413 60.7375 25.8853C60.6979 26.0265 60.6584 26.1593 60.6189 26.2836C60.5821 26.4079 60.5539 26.5039 60.5341 26.5717L59.941 28.2665H61.7627Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M51.0867 26.1692H52.1713L53.133 28.8469C53.1754 28.9684 53.2135 29.0884 53.2474 29.2071C53.2841 29.3229 53.3152 29.4373 53.3406 29.5503C53.3688 29.6632 53.3914 29.6292 53.4084 29.7421H53.4338C53.462 29.5953 53.5016 29.5785 53.5524 29.3977C53.6061 29.2141 53.6654 29.0305 53.7303 28.8469L54.6539 26.1692H55.7258L53.7134 31.5035C53.5976 31.8085 53.4738 31.9684 53.3043 32.1831C53.1377 32.4006 52.998 32.524 52.7896 32.6243C52.5812 32.7247 52.2014 32.7714 51.9808 32.7714C51.7602 32.7714 51.5258 32.7645 51.3925 32.6979C51.2593 32.6312 51.172 32.6243 51.0249 32.4773L51.3925 31.6684C51.4661 31.7419 51.5237 31.7707 51.6131 31.8154C51.7026 31.8602 51.8199 31.889 51.9073 31.889C51.9946 31.889 52.0715 31.886 52.2014 31.8154C52.3313 31.7448 52.3974 31.7328 52.4955 31.5948C52.5936 31.4569 52.6426 31.3742 52.7161 31.2271C52.7896 31.0801 52.8631 30.7859 52.8631 30.7859L51.0867 26.1692Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx41(
          "path",
          {
            d: "M46.1765 24.3883H48.0745C48.8738 24.3883 49.3337 24.4618 49.7749 24.756C50.216 25.0501 50.4385 25.6438 50.4385 26.2116C50.4385 26.4488 50.3743 26.6229 50.2896 26.815C50.2077 27.0042 50.1425 27.1091 49.9954 27.2562C49.8484 27.4033 49.7749 27.4768 49.7013 27.4768C49.8484 27.4768 49.9954 27.5503 50.1425 27.6974C50.2896 27.8445 50.4039 28.0379 50.5102 28.2857C50.6164 28.5335 50.6164 28.7608 50.6164 29.08C50.6164 29.4585 50.526 29.7805 50.3453 30.046C50.1673 30.3115 49.9117 30.5135 49.5785 30.6519C49.248 30.7903 48.8554 30.8595 48.4007 30.8595H46.1765V24.3883ZM47.1933 27.1091H48.1572C48.6176 27.1091 48.8616 27.1118 49.0396 26.9621C49.2175 26.8124 49.3337 26.5883 49.3337 26.3002C49.3337 26.0065 49.3119 25.714 49.1131 25.5649C48.819 25.3443 48.532 25.3443 48.0745 25.3443H47.1933V27.1091ZM47.1933 28.0419V30.0078H48.2651C48.7396 30.0078 49.0729 29.916 49.265 29.7324C49.457 29.5488 49.553 29.3003 49.553 28.9867C49.553 28.7947 49.5093 28.628 49.4217 28.4868C49.337 28.3456 49.1972 28.2368 49.0023 28.1605C48.8074 28.0815 48.5447 28.0419 48.2143 28.0419H47.1933Z",
            fill: color || "#009FD3"
          }
        )
      ] })
    }
  );
};
var CJIconByArcadia_default = CJIconByArcadia;

// src/icons/CJLoadingIcon/CJLoadingIcon.tsx
import { jsx as jsx42, jsxs as jsxs24 } from "react/jsx-runtime";
var CJLoadingIcon = ({
  width = 60,
  height = 60,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cj-loading-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsxs24(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 60 60",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: [
        /* @__PURE__ */ jsxs24("defs", { children: [
          /* @__PURE__ */ jsxs24("linearGradient", { id: "left-to-right-blue", children: [
            /* @__PURE__ */ jsxs24("stop", { offset: "0", stopColor: "#DEDEDE", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "0s;anim12.end+2s",
                  id: "anim11",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+2.75s",
                  id: "anim12",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs24("stop", { offset: "0", stopColor: "#015F94", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "0s;anim22.end+2s",
                  id: "anim21",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim21.end+2.75s",
                  id: "anim22",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs24("linearGradient", { id: "right-to-left-blue", children: [
            /* @__PURE__ */ jsxs24("stop", { offset: "1", stopColor: "#015F94", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "anim11.end",
                  id: "blue-rtl11",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "blue-rtl11.end++2.2s",
                  id: "blue-rtl12",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs24("stop", { offset: "1", stopColor: "#DEDEDE", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "anim11.end",
                  id: "blue-rtl21",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "blue-rtl21.end+2.2s",
                  id: "blue-rtl22",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs24("linearGradient", { id: "right-to-left-orange", children: [
            /* @__PURE__ */ jsxs24("stop", { offset: "1", stopColor: "#EC9F24", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+0.25s",
                  id: "orange-rtl11",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "orange-rtl11.end+1.55s",
                  id: "orange-rtl12",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs24("stop", { offset: "1", stopColor: "#DEDEDE", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+0.25s",
                  id: "orange-rtl21",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "orange-rtl21.end+1.55s",
                  id: "orange-rtl22",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs24("linearGradient", { id: "left-to-right-orange", children: [
            /* @__PURE__ */ jsxs24("stop", { offset: "0", stopColor: "#DEDEDE", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "anim11.end+0.40s",
                  id: "orange-lte11",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "orange-lte11.end+1.30s",
                  id: "orange-lte12",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs24("stop", { offset: "0", stopColor: "#EC9F24", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "anim11.end+0.40s",
                  id: "orange-lte21",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.15s",
                  begin: "orange-lte21.end+1.30s",
                  id: "orange-lte22",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs24("linearGradient", { id: "left-to-right-cyan", children: [
            /* @__PURE__ */ jsxs24("stop", { offset: "0", stopColor: "#DEDEDE", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+0.65s",
                  id: "cyan-lte11",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "cyan-lte11.end+0.75s",
                  id: "cyan-lte12",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs24("stop", { offset: "0", stopColor: "#009FD3", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+0.65s",
                  id: "cyan-lte21",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "cyan-lte21.end+0.75s",
                  id: "cyan-lte22",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs24("linearGradient", { id: "right-to-left-cyan", children: [
            /* @__PURE__ */ jsxs24("stop", { offset: "1", stopColor: "#3992C6", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+0.9s",
                  id: "cyan-rtl11",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "cyan-rtl11.end+0.25s",
                  id: "cyan-rtl12",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              )
            ] }),
            /* @__PURE__ */ jsxs24("stop", { offset: "1", stopColor: "#DEDEDE", children: [
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "anim11.end+0.9s",
                  id: "cyan-rtl21",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "1",
                  to: "0"
                }
              ),
              /* @__PURE__ */ jsx42(
                "animate",
                {
                  dur: "0.25s",
                  begin: "cyan-rtl21.end+0.25s",
                  id: "cyan-rtl22",
                  attributeName: "offset",
                  fill: "freeze",
                  from: "0",
                  to: "1"
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs24("g", { "data-testid": "g-path", opacity, children: [
          /* @__PURE__ */ jsx42(
            "path",
            {
              d: "M10.9165 15.2316L18.5066 18.1879L33.1233 12.6178L48.1734 18.1446L55.7591 15.275L32.8675 7L10.9165 15.2316Z",
              fill: "url(#right-to-left-cyan)"
            }
          ),
          /* @__PURE__ */ jsx42(
            "path",
            {
              d: "M8.85742 16.4756V22.2624L33.0365 31.3523L57.8614 22.1757L57.688 16.5189L33.2099 25.5221L8.85742 16.4756Z",
              fill: "url(#left-to-right-cyan)"
            }
          ),
          /* @__PURE__ */ jsx42(
            "path",
            {
              d: "M2 27.3213V33.1081L26.179 42.198L50.9996 33.0214L50.8306 27.3646L26.3524 36.3678L2 27.3213Z",
              fill: "url(#right-to-left-orange)"
            }
          ),
          /* @__PURE__ */ jsx42(
            "path",
            {
              d: "M9.03076 38.123V43.9099L33.2098 52.9998L58.0304 43.8275L57.8613 38.1664L33.3832 47.1696L9.03076 38.123Z",
              fill: "url(#left-to-right-blue)"
            }
          ),
          /* @__PURE__ */ jsx42(
            "path",
            {
              d: "M3.95068 26.0338L8.96595 24.2349L16.4693 27.1911L11.5798 29.1418L3.95068 26.0338Z",
              fill: "url(#left-to-right-orange)"
            }
          ),
          /* @__PURE__ */ jsx42(
            "path",
            {
              d: "M55.6939 37.0181L50.6787 35.2148L43.1753 38.1755L48.0648 40.1261L55.6939 37.0181Z",
              fill: "url(#right-to-left-blue)"
            }
          )
        ] })
      ]
    }
  );
};
var CJLoadingIcon_default = CJLoadingIcon;

// src/icons/CJLogoIcon/CJLogoIcon.tsx
import { jsx as jsx43, jsxs as jsxs25 } from "react/jsx-runtime";
var CJLogoIcon = ({
  width = 21,
  height = 18,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cj-logo-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx43(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 21 18",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs25("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx43(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M3.66455 3.5171L6.3584 4.60065L11.5461 2.55907L16.8876 4.58477L19.5799 3.53299L11.4553 0.5L3.66455 3.5171Z",
            fill: color || "#3992C6"
          }
        ),
        /* @__PURE__ */ jsx43(
          "path",
          {
            d: "M2.93408 3.97266V6.09368L11.5156 9.42537L20.3264 6.06191L20.2648 3.98854L11.5772 7.28845L2.93408 3.97266Z",
            fill: color || "#009FD3"
          }
        ),
        /* @__PURE__ */ jsx43(
          "path",
          {
            d: "M0.5 7.94727V10.0683L9.08154 13.4L17.8908 10.0365L17.8308 7.96315L9.14308 11.2631L0.5 7.94727Z",
            fill: color || "#EC9F24"
          }
        ),
        /* @__PURE__ */ jsx43(
          "path",
          {
            d: "M2.99561 11.9082V14.0292L11.5771 17.3609L20.3864 13.999L20.3264 11.9241L11.6387 15.224L2.99561 11.9082Z",
            fill: color || "#015F94"
          }
        ),
        /* @__PURE__ */ jsx43(
          "path",
          {
            d: "M1.19238 7.47575L2.97238 6.81641L5.63546 7.89996L3.90008 8.61491L1.19238 7.47575Z",
            fill: color || "#EC9F24"
          }
        ),
        /* @__PURE__ */ jsx43(
          "path",
          {
            d: "M19.5568 11.5027L17.7768 10.8418L15.1138 11.9269L16.8492 12.6419L19.5568 11.5027Z",
            fill: color || "#005980"
          }
        )
      ] })
    }
  );
};
var CJLogoIcon_default = CJLogoIcon;

// src/icons/ClassicIcon/Variants/ClassicIconDefault.tsx
import { jsx as jsx44, jsxs as jsxs26 } from "react/jsx-runtime";
var ClassicIconDefaultVariant = ({
  color = "#EC9F24",
  width = "120",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "classic-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx44(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      viewBox: "0 0 120 121",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs26("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsxs26("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: [
          /* @__PURE__ */ jsx44(
            "path",
            {
              "data-testid": "path-with-background-color",
              d: "M102 0.783691H36C26.0589 0.783691 18 8.84257 18 18.7837C18 28.7248 26.0589 36.7837 36 36.7837H102C111.941 36.7837 120 28.7248 120 18.7837C120 8.84256 111.941 0.783691 102 0.783691Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx44(
            "path",
            {
              d: "M102 84.7837H36C26.0589 84.7837 18 92.8426 18 102.784C18 112.725 26.0589 120.784 36 120.784H102C111.941 120.784 120 112.725 120 102.784C120 92.8426 111.941 84.7837 102 84.7837Z",
              fill: backgroundColor
            }
          )
        ] }),
        /* @__PURE__ */ jsx44("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx44(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18 42.7839H84C93.9411 42.7839 102 50.8428 102 60.7839C102 70.7251 93.9411 78.7839 84 78.7839H18C8.05888 78.7839 0 70.7251 0 60.7839C0 50.8428 8.05887 42.7839 18 42.7839Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var ClassicIconDefault_default = ClassicIconDefaultVariant;

// src/icons/ClassicIcon/Variants/ClassicIconOutlined.tsx
import { jsx as jsx45, jsxs as jsxs27 } from "react/jsx-runtime";
var ClassicIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "classic-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx45(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 120 121",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs27("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx45(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M36 0.783936H102C111.941 0.783936 120 8.84281 120 18.7839C120 28.7251 111.941 36.7839 102 36.7839H36C26.0589 36.7839 18 28.7251 18 18.7839C18 8.84281 26.0589 0.783936 36 0.783936ZM36 10.7839C31.5817 10.7839 28 14.3657 28 18.7839C28 23.2022 31.5817 26.7839 36 26.7839H102C106.418 26.7839 110 23.2022 110 18.7839C110 14.3657 106.418 10.7839 102 10.7839H36Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx45(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M36 84.7839H102C111.941 84.7839 120 92.8428 120 102.784C120 112.725 111.941 120.784 102 120.784H36C26.0589 120.784 18 112.725 18 102.784C18 92.8428 26.0589 84.7839 36 84.7839ZM36 94.7839C31.5817 94.7839 28 98.3657 28 102.784C28 107.202 31.5817 110.784 36 110.784H102C106.418 110.784 110 107.202 110 102.784C110 98.3657 106.418 94.7839 102 94.7839H36Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx45(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18 42.7839H84C93.9411 42.7839 102 50.8428 102 60.7839C102 70.7251 93.9411 78.7839 84 78.7839H18C8.05888 78.7839 0 70.7251 0 60.7839C0 50.8428 8.05887 42.7839 18 42.7839ZM18 52.7839C13.5817 52.7839 10 56.3657 10 60.7839C10 65.2022 13.5817 68.7839 18 68.7839H84C88.4183 68.7839 92 65.2022 92 60.7839C92 56.3657 88.4183 52.7839 84 52.7839H18Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var ClassicIconOutlined_default = ClassicIconOutlinedVariant;

// src/icons/ClassicIcon/ClassicIcon.tsx
import { jsx as jsx46 } from "react/jsx-runtime";
var ClassicIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx46(ClassicIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx46(ClassicIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var ClassicIcon_default = ClassicIcon;

// src/icons/ClinicalPerformanceManagementIcon/Variants/ClinicalPerformanceManagementIconDefault.tsx
import { jsx as jsx47, jsxs as jsxs28 } from "react/jsx-runtime";
var CareRetentionIconDefaultVariant2 = ({
  color = "#EC9F24",
  width = "110",
  height = "120",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "clinical-performance-management",
  // Extra Properties
  variant,
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx47(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      viewBox: "0 0 110 120",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs28("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx47("g", { opacity: backgroundOpacity, "data-testid": "g-background-path", children: /* @__PURE__ */ jsx47(
          "path",
          {
            "data-testid": "path-with-background-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M31.375 26.1082H20C8.95431 26.1082 0 35.0625 0 46.1082V99.7834C0 110.829 8.95431 119.783 20 119.783H32V95.7834C32 84.7378 40.9543 75.7834 52 75.7834H58C69.0457 75.7834 78 84.7378 78 95.7834V119.783H90C101.046 119.783 110 110.829 110 99.7834V46.1082C110 35.0625 101.046 26.1082 90 26.1082H80.9521V51.7839H31.375V26.1082Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx47("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx47(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M43 0.0546875C32.5066 0.0546875 24 8.56128 24 19.0547V43.0547C24 53.5481 32.5066 62.0547 43 62.0547H67C77.4934 62.0547 86 53.5481 86 43.0547V19.0547C86 8.56128 77.4934 0.0546875 67 0.0546875H43ZM55 47.7837C52.2386 47.7837 50 45.5451 50 42.7837V35.7837H43C40.2386 35.7837 38 33.5451 38 30.7837C38 28.0223 40.2386 25.7837 43 25.7837H50V18.7837C50 16.0223 52.2386 13.7837 55 13.7837C57.7614 13.7837 60 16.0223 60 18.7837V25.7837H67C69.7614 25.7837 72 28.0223 72 30.7837C72 33.5451 69.7614 35.7837 67 35.7837H60V42.7837C60 45.5451 57.7614 47.7837 55 47.7837Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var ClinicalPerformanceManagementIconDefault_default = CareRetentionIconDefaultVariant2;

// src/icons/ClinicalPerformanceManagementIcon/Variants/ClinicalPerformanceManagementIconOutlined.tsx
import { jsx as jsx48 } from "react/jsx-runtime";
var CareRetentionIconOutlinedVariant2 = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "clinical-performance-management",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx48(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx48("g", { opacity, "data-testid": "g-path", children: /* @__PURE__ */ jsx48(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M49.4037 10.758H71.6055C77.179 10.758 81.6973 15.2235 81.6973 20.732V42.6749C81.6973 48.1833 77.179 52.6489 71.6055 52.6489H49.4037C43.8302 52.6489 39.3119 48.1833 39.3119 42.6749V20.732C39.3119 15.2235 43.8302 10.758 49.4037 10.758ZM29.2202 20.732C29.2202 9.71498 38.2566 0.783936 49.4037 0.783936H71.6055C82.7525 0.783936 91.789 9.71498 91.789 20.732V27.847C105.102 30.992 115 42.8287 115 56.9502V90.8619C115 107.387 101.445 120.784 84.7248 120.784H35.2752C18.5547 120.784 5 107.387 5 90.8619V56.9502C5 42.4741 15.4011 30.399 29.2202 27.6266V20.732ZM29.2202 37.9154C21.0315 40.4573 15.0917 48.0182 15.0917 56.9502V90.8619C15.0917 101.879 24.1282 110.81 35.2752 110.81V110.498V94.5398C35.2752 83.5228 44.3117 74.5917 55.4587 74.5917H65.5505C76.6975 74.5917 85.7339 83.5228 85.7339 94.5398V110.498V110.785C96.4121 110.266 104.908 101.544 104.908 90.8619V56.9502C104.908 48.3894 99.452 41.0882 91.789 38.2581V42.6749C91.789 53.6919 82.7525 62.6229 71.6055 62.6229H49.4037C38.2566 62.6229 29.2202 53.6919 29.2202 42.6749V37.9154ZM75.6422 94.5398C75.6422 89.0313 71.124 84.5658 65.5505 84.5658H55.4587C49.8852 84.5658 45.367 89.0313 45.367 94.5398V110.498H75.6422V94.5398ZM77.6606 31.7034C77.6606 34.4577 75.4014 36.6904 72.6147 36.6904H65.5505V43.6722C65.5505 46.4265 63.2913 48.6593 60.5046 48.6593C57.7178 48.6593 55.4587 46.4265 55.4587 43.6722V36.6904H48.3945C45.6077 36.6904 43.3486 34.4577 43.3486 31.7034C43.3486 28.9492 45.6077 26.7164 48.3945 26.7164H55.4587V19.7346C55.4587 16.9803 57.7178 14.7476 60.5046 14.7476C63.2913 14.7476 65.5505 16.9803 65.5505 19.7346V26.7164H72.6147C75.4014 26.7164 77.6606 28.9492 77.6606 31.7034Z",
          fill: color
        }
      ) })
    }
  );
};
var ClinicalPerformanceManagementIconOutlined_default = CareRetentionIconOutlinedVariant2;

// src/icons/ClinicalPerformanceManagementIcon/ClinicalPerformanceManagementIcon.tsx
import { jsx as jsx49 } from "react/jsx-runtime";
var ClinicalPerformanceManagementIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx49(
      ClinicalPerformanceManagementIconDefault_default,
      {
        variant: "default",
        ...props
      }
    ),
    outlined: /* @__PURE__ */ jsx49(
      ClinicalPerformanceManagementIconOutlined_default,
      {
        variant: "outlined",
        ...props
      }
    )
  };
  return variants[variant];
};
var ClinicalPerformanceManagementIcon_default = ClinicalPerformanceManagementIcon;

// src/icons/CloseIcon/CloseIcon.tsx
import { jsx as jsx50 } from "react/jsx-runtime";
var CloseIcon = ({
  width = 12,
  height = 12,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "close-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx50(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 12 12",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsx50("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx50(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M11.2071 0.706956C10.8166 0.316431 10.1834 0.316431 9.7929 0.706956L5.91422 4.58564L2.12133 0.792742C1.7308 0.402218 1.09764 0.402218 0.707112 0.792742C0.316588 1.18327 0.316588 1.81643 0.707112 2.20696L4.50001 5.99985L0.60051 9.89934C0.209986 10.2899 0.209986 10.923 0.60051 11.3136C0.991035 11.7041 1.6242 11.7041 2.01472 11.3136L5.91422 7.41406L9.8995 11.3993C10.29 11.7899 10.9232 11.7899 11.3137 11.3993C11.7042 11.0088 11.7042 10.3757 11.3137 9.98513L7.32843 5.99985L11.2071 2.12117C11.5976 1.73065 11.5976 1.09748 11.2071 0.706956Z",
          fill: color
        }
      ) })
    }
  );
};
var CloseIcon_default = CloseIcon;

// src/icons/CloseSquareIcon/CloseSquareIcon.tsx
import { jsx as jsx51, jsxs as jsxs29 } from "react/jsx-runtime";
var CloseSquareIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#1A91CF",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "close-square-icon",
  // Extra Properties
  backgroundColor = "white",
  backgroundOpacity = "1",
  shadowColor = "#84CBF0",
  shadowOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx51(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 25",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs29("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx51("g", { "data-testid": "g-shadow-path", opacity: shadowOpacity, children: /* @__PURE__ */ jsx51(
          "rect",
          {
            "data-testid": "path-with-shadow-color",
            x: "4",
            y: "4.83984",
            width: "20",
            height: "20",
            rx: "5",
            fill: shadowColor
          }
        ) }),
        /* @__PURE__ */ jsx51("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx51(
          "rect",
          {
            "data-testid": "path-with-background-color",
            x: "2",
            y: "2.83984",
            width: "20",
            height: "20",
            rx: "6",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx51("g", { "data-testid": "g-border-path", opacity: borderOpacity, children: /* @__PURE__ */ jsx51(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M16.334 2.83984C19.723 2.83984 22 5.21784 22 8.75584V16.9238C22 20.4618 19.723 22.8398 16.333 22.8398H7.665C4.276 22.8398 2 20.4618 2 16.9238V8.75584C2 5.21784 4.276 2.83984 7.665 2.83984H16.334ZM16.334 4.33984H7.665C5.135 4.33984 3.5 6.07284 3.5 8.75584V16.9238C3.5 19.6068 5.135 21.3398 7.665 21.3398H16.333C18.864 21.3398 20.5 19.6068 20.5 16.9238V8.75584C20.5 6.07284 18.864 4.33984 16.334 4.33984ZM10.1305 9.90244L11.998 11.7698L13.8645 9.90454C14.1575 9.61154 14.6315 9.61154 14.9245 9.90454C15.2175 10.1975 15.2175 10.6715 14.9245 10.9645L13.058 12.8298L14.9265 14.6994C15.2195 14.9924 15.2195 15.4664 14.9265 15.7594C14.7805 15.9064 14.5875 15.9794 14.3965 15.9794C14.2045 15.9794 14.0125 15.9064 13.8665 15.7594L11.998 13.8898L10.1325 15.7565C9.9865 15.9035 9.7945 15.9765 9.6025 15.9765C9.4105 15.9765 9.2185 15.9035 9.0725 15.7565C8.7795 15.4635 8.7795 14.9895 9.0725 14.6965L10.938 12.8298L9.0705 10.9624C8.7775 10.6694 8.7775 10.1954 9.0705 9.90244C9.3645 9.60944 9.8385 9.60944 10.1305 9.90244Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var CloseSquareIcon_default = CloseSquareIcon;

// src/icons/CodingIcon/Variants/CodingIconDefault.tsx
import { jsx as jsx52, jsxs as jsxs30 } from "react/jsx-runtime";
var CodingIconDefaultVariant = ({
  color = "#EC9F24",
  width = "120",
  height = "110",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "coding-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx52(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      viewBox: "0 0 120 110",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs30("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx52("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx52(
          "path",
          {
            "data-testid": "path-with-background-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M120 30.7969L120 79.7969C120 96.3654 106.569 109.797 90 109.797L30 109.797C13.4315 109.797 -4.17743e-06 96.3654 -3.4532e-06 79.7969L-1.31134e-06 30.7969C-5.87108e-07 14.2283 13.4315 0.79687 30 0.796871L90 0.796874C106.569 0.796874 120 14.2283 120 30.7969Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsxs30("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx52(
            "path",
            {
              "data-testid": "path-with-color",
              d: "M27.5355 29.7613C25.5829 27.8087 22.4171 27.8087 20.4645 29.7613C18.5118 31.714 18.5118 34.8798 20.4645 36.8324L38.9289 55.2969L20.4645 73.7613C18.5118 75.714 18.5118 78.8798 20.4645 80.8324C22.4171 82.785 25.5829 82.785 27.5355 80.8324L49.5355 58.8324C51.4882 56.8798 51.4882 53.714 49.5355 51.7613L27.5355 29.7613Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx52(
            "path",
            {
              d: "M61 72.2969C58.2386 72.2969 56 74.5354 56 77.2969C56 80.0583 58.2386 82.2969 61 82.2969H95C97.7614 82.2969 100 80.0583 100 77.2969C100 74.5354 97.7614 72.2969 95 72.2969H61Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var CodingIconDefault_default = CodingIconDefaultVariant;

// src/icons/CodingIcon/Variants/CodingIconOutlined.tsx
import { jsx as jsx53, jsxs as jsxs31 } from "react/jsx-runtime";
var CodingIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "coding-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx53(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs31("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx53(
          "path",
          {
            d: "M21.277 34.9645C23.2296 33.0118 26.3954 33.0118 28.348 34.9645L50.348 56.9645C52.3007 58.9171 52.3007 62.0829 50.348 64.0355L28.348 86.0355C26.3954 87.9882 23.2296 87.9882 21.277 86.0355C19.3244 84.0829 19.3244 80.9171 21.277 78.9645L39.7414 60.5L21.277 42.0355C19.3244 40.0829 19.3244 36.9171 21.277 34.9645Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx53(
          "path",
          {
            d: "M61.8125 77.5C59.0511 77.5 56.8125 79.7386 56.8125 82.5C56.8125 85.2614 59.0511 87.5 61.8125 87.5H95.8125C98.5739 87.5 100.812 85.2614 100.812 82.5C100.812 79.7386 98.5739 77.5 95.8125 77.5H61.8125Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx53(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M120.812 36V85C120.812 101.569 107.381 115 90.8125 115H30.8125C14.244 115 0.812499 101.569 0.8125 85L0.812503 36C0.812503 19.4315 14.244 6 30.8125 6L90.8125 6C107.381 6 120.812 19.4315 120.812 36ZM110.812 36C110.812 24.9543 101.858 16 90.8125 16L30.8125 16C19.7668 16 10.8125 24.9543 10.8125 36L10.8125 85C10.8125 96.0457 19.7668 105 30.8125 105H90.8125C101.858 105 110.812 96.0457 110.812 85V36Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var CodingIconOutlined_default = CodingIconOutlinedVariant;

// src/icons/CodingIcon/CodingIcon.tsx
import { jsx as jsx54 } from "react/jsx-runtime";
var CodingIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx54(CodingIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx54(CodingIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var CodingIcon_default = CodingIcon;

// src/icons/CohortsIcon/Variants/CohortsIconDefault.tsx
import { jsx as jsx55, jsxs as jsxs32 } from "react/jsx-runtime";
var CohortIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "113",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cohorts-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx55(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      viewBox: "0 0 120 113",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs32("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx55("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx55(
          "path",
          {
            "data-testid": "path-with-background-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M45.8756 42.215C44.3109 42.587 42.6785 42.7839 41 42.7839C39.3215 42.7839 37.6891 42.587 36.1244 42.215C38.3224 44.4962 40.009 47.2735 41 50.3623C41.991 47.2735 43.6776 44.4962 45.8756 42.215ZM56.1244 36.3528C57.6891 35.9809 59.3215 35.7839 61 35.7839C62.2391 35.7839 63.4531 35.8913 64.6332 36.0971C62.8822 34.2179 61.4716 32.0172 60.5 29.5932C59.4839 32.128 57.9879 34.4187 56.1244 36.3528ZM60.5 13.9747C57.4012 6.24382 49.8382 0.783936 41 0.783936C29.402 0.783936 20 10.186 20 21.7839C20 27.4416 22.2374 32.5768 25.8756 36.3528C24.3109 35.9809 22.6785 35.7839 21 35.7839C9.40202 35.7839 0 45.186 0 56.7839C0 68.3819 9.40202 77.7839 21 77.7839C22.6785 77.7839 24.3109 77.587 25.8756 77.215C22.2374 80.9911 20 86.1262 20 91.7839C20 103.382 29.402 112.784 41 112.784C49.8382 112.784 57.4012 107.324 60.5 99.5932C63.5988 107.324 71.1618 112.784 80 112.784C91.598 112.784 101 103.382 101 91.7839C101 86.2529 98.8617 81.2214 95.3668 77.4708C96.5469 77.6766 97.7609 77.7839 99 77.7839C110.598 77.7839 120 68.3819 120 56.7839C120 45.186 110.598 35.7839 99 35.7839C97.7609 35.7839 96.5469 35.8913 95.3668 36.0971C98.8617 32.3465 101 27.3149 101 21.7839C101 10.186 91.598 0.783936 80 0.783936C71.1618 0.783936 63.5988 6.24382 60.5 13.9747ZM83.6332 71.0971C82.1625 69.5188 80.932 67.7136 80 65.7398C79.068 67.7136 77.8375 69.5188 76.3668 71.0971C77.5469 70.8913 78.7609 70.7839 80 70.7839C81.2391 70.7839 82.4531 70.8913 83.6332 71.0971ZM64.6333 77.4708C63.4531 77.6766 62.2391 77.7839 61 77.7839C59.3215 77.7839 57.689 77.587 56.1244 77.215C57.9879 79.1492 59.4839 81.4398 60.5 83.9747C61.4716 81.5507 62.8822 79.3499 64.6333 77.4708ZM45.8756 71.3528C43.6776 69.0716 41.991 66.2944 41 63.2056C40.009 66.2944 38.3224 69.0716 36.1244 71.3528C37.6891 70.9809 39.3215 70.7839 41 70.7839C42.6785 70.7839 44.3109 70.9809 45.8756 71.3528ZM80 42.7839C81.2391 42.7839 82.4531 42.6766 83.6332 42.4708C82.1625 44.0491 80.932 45.8543 80 47.8281C79.068 45.8543 77.8375 44.0491 76.3668 42.4708C77.5469 42.6766 78.7609 42.7839 80 42.7839Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx55("g", { "data-testid": "g-border-path", opacity: borderOpacity, children: /* @__PURE__ */ jsx55(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M95.3668 36.0971C98.8617 32.3465 101 27.3149 101 21.7839C101 10.186 91.598 0.783936 80 0.783936C68.402 0.783936 59 10.186 59 21.7839C59 27.3149 61.1383 32.3465 64.6332 36.0971C63.4531 35.8913 62.2391 35.7839 61 35.7839C49.402 35.7839 40 45.186 40 56.7839C40 68.3819 49.402 77.7839 61 77.7839C69.3945 77.7839 76.6387 72.8584 80 65.7398C83.3613 72.8584 90.6055 77.7839 99 77.7839C110.598 77.7839 120 68.3819 120 56.7839C120 45.186 110.598 35.7839 99 35.7839C97.7609 35.7839 96.5469 35.8913 95.3668 36.0971ZM83.6332 42.4708C82.4531 42.6766 81.2391 42.7839 80 42.7839C78.7609 42.7839 77.5469 42.6766 76.3668 42.4708C77.8375 44.0491 79.068 45.8543 80 47.8281C80.932 45.8543 82.1625 44.0491 83.6332 42.4708Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var CohortsIconDefault_default = CohortIconDefaultVariant;

// src/icons/CohortsIcon/Variants/CohortsIconOutlined.tsx
import { jsx as jsx56 } from "react/jsx-runtime";
var CohortIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "cohorts-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx56(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 120 121",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx56("g", { opacity, "data-testid": "g-path", children: /* @__PURE__ */ jsx56(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M41 36.7839C47.0751 36.7839 52 31.8591 52 25.7839C52 19.7088 47.0751 14.7839 41 14.7839C34.9249 14.7839 30 19.7088 30 25.7839C30 31.8591 34.9249 36.7839 41 36.7839ZM41 46.7839C42.2391 46.7839 43.4531 46.6766 44.6332 46.4708C42.8822 48.3499 41.4716 50.5507 40.5 52.9747C39.4839 50.4398 37.9879 48.1492 36.1244 46.215C37.6891 46.587 39.3215 46.7839 41 46.7839ZM60 39.7839C58.7609 39.7839 57.5469 39.8913 56.3668 40.0971C58.1178 38.2179 59.5284 36.0172 60.5 33.5932C61.5161 36.128 63.0121 38.4187 64.8756 40.3528C63.3109 39.9809 61.6785 39.7839 60 39.7839ZM80 46.7839C78.3215 46.7839 76.6891 46.587 75.1244 46.215C76.9879 48.1492 78.4839 50.4398 79.5 52.9747C80.4716 50.5507 81.8821 48.3499 83.6332 46.4708C82.4531 46.6766 81.2391 46.7839 80 46.7839ZM95.3668 40.0971C98.8617 36.3465 101 31.3149 101 25.7839C101 14.186 91.598 4.78394 80 4.78394C71.1618 4.78394 63.5988 10.2438 60.5 17.9747C57.4012 10.2438 49.8382 4.78394 41 4.78394C29.402 4.78394 20 14.186 20 25.7839C20 31.4416 22.2374 36.5768 25.8756 40.3528C24.3109 39.9809 22.6785 39.7839 21 39.7839C9.40202 39.7839 0 49.186 0 60.7839C0 72.3819 9.40202 81.7839 21 81.7839C22.6785 81.7839 24.3109 81.587 25.8756 81.215C22.2374 84.9911 20 90.1262 20 95.7839C20 107.382 29.402 116.784 41 116.784C49.8382 116.784 57.4012 111.324 60.5 103.593C63.5988 111.324 71.1618 116.784 80 116.784C91.598 116.784 101 107.382 101 95.7839C101 90.2529 98.8617 85.2214 95.3668 81.4708C96.5469 81.6766 97.7609 81.7839 99 81.7839C110.598 81.7839 120 72.3819 120 60.7839C120 49.186 110.598 39.7839 99 39.7839C97.7609 39.7839 96.5469 39.8913 95.3668 40.0971ZM83.6332 75.0971C81.8821 73.2179 80.4716 71.0172 79.5 68.5932C78.4839 71.128 76.9879 73.4187 75.1244 75.3528C76.689 74.9809 78.3215 74.7839 80 74.7839C81.2391 74.7839 82.4531 74.8913 83.6332 75.0971ZM60 81.7839C61.6785 81.7839 63.311 81.587 64.8756 81.215C63.0121 83.1491 61.5161 85.4398 60.5 87.9747C59.5284 85.5507 58.1178 83.3499 56.3667 81.4708C57.5469 81.6766 58.7609 81.7839 60 81.7839ZM40.5 68.5932C41.4716 71.0172 42.8822 73.2179 44.6332 75.0971C43.4531 74.8912 42.2391 74.7839 41 74.7839C39.3215 74.7839 37.6891 74.9809 36.1244 75.3528C37.9879 73.4187 39.4839 71.128 40.5 68.5932ZM41 106.784C47.0751 106.784 52 101.859 52 95.7839C52 89.7088 47.0751 84.7839 41 84.7839C34.9249 84.7839 30 89.7088 30 95.7839C30 101.859 34.9249 106.784 41 106.784ZM32 60.7839C32 66.8591 27.0751 71.7839 21 71.7839C14.9249 71.7839 10 66.8591 10 60.7839C10 54.7088 14.9249 49.7839 21 49.7839C27.0751 49.7839 32 54.7088 32 60.7839ZM80 36.7839C86.0751 36.7839 91 31.8591 91 25.7839C91 19.7088 86.0751 14.7839 80 14.7839C73.9249 14.7839 69 19.7088 69 25.7839C69 31.8591 73.9249 36.7839 80 36.7839ZM91 95.7839C91 101.859 86.0751 106.784 80 106.784C73.9249 106.784 69 101.859 69 95.7839C69 89.7088 73.9249 84.7839 80 84.7839C86.0751 84.7839 91 89.7088 91 95.7839ZM60 71.7839C66.0751 71.7839 71 66.8591 71 60.7839C71 54.7088 66.0751 49.7839 60 49.7839C53.9249 49.7839 49 54.7088 49 60.7839C49 66.8591 53.9249 71.7839 60 71.7839ZM110 60.7839C110 66.8591 105.075 71.7839 99 71.7839C92.9249 71.7839 88 66.8591 88 60.7839C88 54.7088 92.9249 49.7839 99 49.7839C105.075 49.7839 110 54.7088 110 60.7839Z",
          fill: color
        }
      ) })
    }
  );
};
var CohortsIconOutlined_default = CohortIconOutlinedVariant;

// src/icons/CohortsIcon/CohortsIcon.tsx
import { jsx as jsx57 } from "react/jsx-runtime";
var CohortsIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx57(CohortsIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx57(CohortsIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var CohortsIcon_default = CohortsIcon;

// src/icons/CommunityIcon/Variants/CommunityIconDefault.tsx
import { jsx as jsx58, jsxs as jsxs33 } from "react/jsx-runtime";
var CommunityIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "community-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx58(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs33("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx58("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx58(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            "data-testid": "path-with-background-color",
            d: "M83.0918 62.436C90.7986 56.0272 95.6875 46.3582 95.6875 35.5C95.6875 16.0957 80.0837 0.5 60.6875 0.5C41.2913 0.5 25.6875 16.0957 25.6875 35.5C25.6875 46.3581 30.5763 56.0271 38.2831 62.4359C34.323 64.3185 31.0133 67.4872 28.9791 71.5557L18.9791 91.5557C12.33 104.854 22 120.5 36.8676 120.5H84.5069C99.3746 120.5 109.044 104.854 102.395 91.5557L92.3955 71.5557C90.3613 67.4873 87.0517 64.3186 83.0918 62.436Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsxs33("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx58(
            "path",
            {
              "data-testid": "path-with-color",
              d: "M17.5599 95.2347C17.9031 93.998 18.3734 92.7671 18.9791 91.5557L28.9791 71.5557C31.0133 67.4872 34.323 64.3185 38.2831 62.4359C30.5763 56.0271 25.6875 46.3581 25.6875 35.5C25.6875 21.003 34.3971 8.63171 46.8906 3.30165C43.1481 1.50567 38.9474 0.5 34.4967 0.5C18.5747 0.5 5.85395 13.3699 5.85395 29.25C5.85395 37.0914 8.95741 44.2011 14.0175 49.3885C11.5109 51.2318 9.42346 53.6669 7.97906 56.5557L2.97906 66.5557C-3.16259 78.839 4.61923 93.1259 17.5599 95.2347Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx58(
            "path",
            {
              d: "M118.395 66.9525C124.521 79.2036 116.796 93.4479 103.916 95.6146C103.566 94.2501 103.063 92.8911 102.395 91.5557L92.3955 71.5557C90.3613 67.4873 87.0517 64.3186 83.0918 62.436C90.7986 56.0272 95.6875 46.3582 95.6875 35.5C95.6875 20.8879 86.8391 8.43545 74.1861 3.17606C77.8691 1.45872 81.9838 0.5 86.337 0.5C102.314 0.5 115.078 13.414 115.078 29.3492C115.078 37.1548 112.014 44.2376 107.008 49.4319C109.635 51.2969 111.82 53.8021 113.316 56.7938L118.395 66.9525Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var CommunityIconDefault_default = CommunityIconDefaultVariant;

// src/icons/CommunityIcon/Variants/CommunityIconOutlined.tsx
import { jsx as jsx59 } from "react/jsx-runtime";
var CommunityIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "community-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx59(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx59("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx59(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M5.97852 29.25C5.97852 37.0915 9.08205 44.2012 14.1423 49.3887C11.6357 51.2319 9.54841 53.667 8.10406 56.5557L3.10406 66.5557C-3.03759 78.839 4.74423 93.1259 17.6849 95.2347C14.2599 107.574 23.4793 120.5 36.9926 120.5H84.6319C98.0065 120.5 107.175 107.838 104.041 95.6149C116.921 93.4481 124.646 79.2038 118.52 66.9527L113.441 56.794C111.945 53.8023 109.76 51.2971 107.133 49.432C112.139 44.2378 115.203 37.1549 115.203 29.3492C115.203 13.414 102.439 0.5 86.462 0.5C82.1089 0.5 77.9942 1.45873 74.3112 3.17607C70.1615 1.45121 65.6027 0.5 60.8125 0.5C55.9072 0.5 51.2445 1.49747 47.0154 3.30174C43.2728 1.50571 39.0721 0.5 34.6212 0.5C18.6992 0.5 5.97852 13.3699 5.97852 29.25ZM56.6558 10.8392C52.9736 11.4474 49.5708 12.8513 46.6282 14.8706C40.0813 19.3634 35.8125 26.9022 35.8125 35.5C35.8125 39.0617 36.5456 42.4423 37.8702 45.5C39.6745 49.665 42.5762 53.2309 46.2171 55.8396C47.5927 56.8252 49.0738 57.6742 50.6411 58.3673C53.7437 59.7392 57.1841 60.5 60.8125 60.5C64.4504 60.5 67.8993 59.7352 71.0082 58.3565C72.4137 57.7332 73.7497 56.9844 75.0023 56.1242C78.7628 53.5415 81.7712 49.9537 83.65 45.7383C85.0407 42.6183 85.8125 39.1545 85.8125 35.5C85.8125 26.7638 81.4051 19.121 74.6789 14.6561C71.6703 12.659 68.1978 11.2977 64.4483 10.7587C63.2621 10.5882 62.0482 10.5 60.8125 10.5C59.3953 10.5 58.0067 10.616 56.6558 10.8392ZM85.3909 10.5298C91.8312 16.8687 95.8125 25.6985 95.8125 35.5C95.8125 39.0654 95.2854 42.5026 94.3044 45.7383H95.5526L95.6181 45.7384L95.6781 45.7387L95.7656 45.7394C101.417 42.5048 105.203 36.3975 105.203 29.3492C105.203 18.8841 96.8632 10.5 86.462 10.5C86.1024 10.5 85.7453 10.51 85.3909 10.5298ZM87.5172 58.1795C86.2096 59.7195 84.7704 61.144 83.2168 62.436C87.1767 64.3186 90.4863 67.4873 92.5205 71.5557L99.6911 85.897H100.632C108.066 85.897 112.901 78.0738 109.576 71.4248L104.497 61.2661C103.119 58.5114 100.573 56.5816 97.6325 55.9566C94.5102 57.2719 91.1015 58.0512 87.5172 58.1795ZM88.5107 85.897L83.5762 76.0279C81.8823 72.64 78.4196 70.5 74.6319 70.5H68.0113H60.8125H53.4045H46.9926C43.2049 70.5 39.7422 72.64 38.0483 76.0279L28.0483 96.0279C24.7238 102.677 29.5588 110.5 36.9926 110.5H84.6319C92.0657 110.5 96.9007 102.677 93.5762 96.0278L93.5107 95.897H93.5105L88.5105 85.897H88.5107ZM27.2493 45.5C26.3141 42.3341 25.8125 38.978 25.8125 35.5C25.8125 25.7167 29.779 16.9015 36.1982 10.5651C35.6785 10.522 35.1526 10.5 34.6212 10.5C24.2747 10.5 15.9785 18.84 15.9785 29.25C15.9785 36.231 19.7122 42.2839 25.2939 45.5122C25.5261 45.5041 25.759 45.5 25.9926 45.5H27.2493ZM33.95 57.9924C35.2993 59.6042 36.7917 61.0917 38.4081 62.4359C34.448 64.3185 31.1383 67.4872 29.1041 71.5557L22.1319 85.5H20.9926C13.5588 85.5 8.72382 77.6769 12.0483 71.0279L17.0483 61.0279C18.3714 58.3818 20.7733 56.497 23.5665 55.7984C26.7673 57.1357 30.2684 57.9084 33.95 57.9924Z",
          "data-testid": "path-with-color",
          fill: color
        }
      ) })
    }
  );
};
var CommunityIconOutlined_default = CommunityIconOutlinedVariant;

// src/icons/CommunityIcon/CommunityIcon.tsx
import { jsx as jsx60 } from "react/jsx-runtime";
var CommunityIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx60(CommunityIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx60(CommunityIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var CommunityIcon_default = CommunityIcon;

// src/icons/CountyIcon/CountyIcon.tsx
import { jsx as jsx61 } from "react/jsx-runtime";
var CountyIcon = ({
  width = 24,
  height = 18,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "county-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx61(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 18",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx61("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx61(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M23.609 1.18594C23.3573 0.935156 23.0226 0.796875 22.6668 0.796875C22.4446 0.796875 22.2239 0.853125 22.0289 0.959062C21.8339 1.065 21.667 1.21969 21.5465 1.40578L20.7646 2.61469C20.66 2.77631 20.5043 2.89819 20.3247 2.96227C20.2766 2.97942 20.0174 3.03216 19.9421 3.05489C19.6109 3.15502 19.3131 3.34856 19.087 3.61406C18.8095 3.93984 18.6562 4.35516 18.6562 4.78266V5.89125C18.6562 6.01828 18.6084 6.13922 18.5217 6.2325C18.435 6.32344 18.3257 6.37453 18.2053 6.37875C18.1992 6.37922 18.1461 6.37739 18.1382 6.37655C17.9033 6.35156 17.7191 6.15314 17.7187 5.9137V5.4698C17.7187 5.10042 17.579 4.7498 17.3254 4.48214C17.2012 4.35042 17.055 4.24542 16.89 4.16995C16.7293 4.09575 16.5587 4.05403 16.3829 4.04522C16.371 4.04461 16.3082 4.04339 16.3082 4.04339C16.1151 4.04339 15.9262 4.08745 15.7617 4.17042C15.5489 4.2773 15.3651 4.45027 15.2151 4.6837C15.0787 4.89792 14.9925 5.12105 14.9325 5.29214C14.9189 5.33292 14.5682 5.89964 14.4318 6.06323C14.2959 6.22917 14.1473 6.3712 14.0821 6.41245C13.9945 6.47339 13.8389 6.53433 13.716 6.53245C13.6509 6.53198 13.5989 6.5198 13.5468 6.49261C13.5023 6.4687 13.4418 6.42698 13.3701 6.31823C13.3406 6.26995 13.326 6.23105 13.3242 6.14152C13.3181 5.93995 13.4601 5.5598 13.7048 5.1248C13.7878 4.97527 13.874 4.81214 13.9443 4.63214C14.0315 4.40667 14.0704 4.22058 14.0714 4.02933C14.0718 3.85355 14.032 3.68198 13.957 3.53198C13.8778 3.37308 13.7526 3.21863 13.5951 3.10284C13.3495 2.92144 13.0453 2.81245 12.7378 2.81245H12.7359C12.5418 2.81245 12.352 2.85909 12.172 2.93269C11.9878 3.00816 11.8178 3.12192 11.6763 3.26306L11.4843 3.45469V2.77406C11.4843 2.53969 11.4016 2.31562 11.2432 2.14266C11.0852 1.97016 10.8723 1.86328 10.6388 1.84219C10.6388 1.84219 4.08544 1.24641 4.01372 1.24641C3.76575 1.24641 3.52163 1.28531 3.28631 1.36219C3.05569 1.43766 2.83941 1.54734 2.64253 1.68938C2.23753 1.98094 1.94189 2.38594 1.7872 2.85984C1.55705 3.56578 1.32933 4.21031 1.11042 4.77563C0.829172 5.50266 0.556359 6.10828 0.321094 6.52641C0.111094 6.90141 0 7.32984 0 7.76578C0 8.145 0.0867187 8.52609 0.250781 8.86828C0.414375 9.21047 0.657656 9.51703 0.952969 9.75562L1.83094 10.4625C2.115 10.6913 2.29312 11.0306 2.32031 11.3934C2.34938 11.7863 2.48203 12.1669 2.70469 12.4936C2.92594 12.8184 3.23016 13.0823 3.58406 13.2567C4.22391 13.5727 4.87313 14.0311 5.51297 14.6194C5.73703 14.8252 6.01453 14.9606 6.31453 15.0117C6.40125 15.0262 6.48984 15.0338 6.57703 15.0338C7.01578 15.0338 7.43766 14.8486 7.73484 14.5261L8.12953 14.0967L8.13 14.0963C8.13047 14.0963 8.13094 14.0963 8.13141 14.0967L8.83922 15.1467C9.10781 15.5456 9.53578 15.813 10.0125 15.8801C10.2731 16.1548 10.6448 16.3125 11.0231 16.3125C11.2294 16.3125 11.4427 16.2645 11.6302 16.174C11.8294 16.0779 11.9986 15.9395 12.1327 15.7628L12.367 15.4547C12.4973 15.2794 12.7486 15.1083 13.0748 14.9738C13.3186 14.8711 14.3437 14.5312 14.9142 14.4843C15.0043 14.4769 15.1758 14.5061 15.2691 14.5525C15.3581 14.5985 15.4341 14.6572 15.5142 14.7987C15.885 15.4737 16.3223 16.0255 16.8141 16.4394C17.063 16.6489 17.3227 16.8184 17.5861 16.944C17.8603 17.0748 18.1406 17.1586 18.4191 17.1933C18.4725 17.1999 18.5264 17.2031 18.5841 17.2031C18.75 17.2013 19.0012 17.181 19.2638 17.0226C19.3584 16.9645 19.493 16.8604 19.5966 16.6926C19.6656 16.5818 19.7141 16.4526 19.7394 16.3141C19.7467 16.274 19.7597 16.1282 19.7597 16.0959C19.7587 15.7762 19.6308 15.4729 19.3988 15.2414L18.7439 14.5884C18.4791 14.3245 18.3328 13.9729 18.3323 13.5989C18.3328 13.3054 18.4233 13.0242 18.5944 12.7856L19.4433 11.6029C19.6369 11.3329 19.815 11.0479 19.972 10.755C20.0311 10.6448 20.0437 10.5182 20.0077 10.3982C19.9716 10.2782 19.8909 10.1798 19.7808 10.1203C19.7128 10.0837 19.6359 10.0645 19.5595 10.0645C19.3861 10.0645 19.2277 10.1592 19.1461 10.3115C19.0476 10.4949 18.9396 10.6748 18.8243 10.8488L16.8447 10.753L18.0415 7.30908C18.0897 7.314 18.2222 7.3162 18.2377 7.31569C18.4247 7.30912 18.6061 7.26553 18.7758 7.18584C18.939 7.10944 19.0838 7.00444 19.2061 6.87272C19.456 6.606 19.5938 6.25725 19.5938 5.89116V4.78256C19.5933 4.356 19.9008 3.996 20.3246 3.92663C20.828 3.84413 21.2752 3.55116 21.5522 3.12366L22.3336 1.9148C22.4068 1.80183 22.531 1.73433 22.6669 1.73433C22.8844 1.7348 23.0621 1.91105 23.0625 2.12714V2.77777C23.0625 2.85605 23.0391 2.93152 22.9955 2.9962C22.9955 2.9962 19.2519 10.1241 19.1971 10.2131C19.1315 10.32 19.1108 10.4456 19.1399 10.5675C19.1685 10.6894 19.243 10.7925 19.3494 10.8586C19.4235 10.9041 19.5088 10.9284 19.5955 10.9284C19.7591 10.9284 19.9087 10.8455 19.9949 10.7058C20.0441 10.6261 23.7722 3.52167 23.7722 3.52167C23.9213 3.30089 24 3.04402 24 2.77777V2.12714C23.9995 1.77141 23.8607 1.43719 23.609 1.18594ZM10.5468 2.77594V4.58344C10.5468 4.77375 10.6647 4.94344 10.8405 5.01656C10.8977 5.04 10.9604 5.05219 11.0223 5.05219C11.147 5.05219 11.2657 5.00391 11.3543 4.91531L12.3421 3.93188C12.4457 3.82781 12.5894 3.76828 12.7357 3.76828C12.8482 3.76875 12.9455 3.80892 13.0381 3.86672C13.1328 3.92578 13.1315 4.07761 13.126 4.10691C13.116 4.16105 13.0967 4.22545 13.0706 4.29141C13.0317 4.39219 12.9727 4.51266 12.885 4.67016C12.5902 5.20875 12.3923 5.66531 12.3867 6.14156C12.3853 6.39047 12.4556 6.63516 12.585 6.83063C12.6094 6.86817 12.6356 6.9038 12.6625 6.93853L11.935 7.72345L8.82483 5.53059C8.61558 5.38308 8.32659 5.43075 8.1758 5.63775L6.31659 8.19056L4.716 7.24945L7.095 2.46009L10.5468 2.77594ZM11.3794 8.47884L10.2562 10.4168L7.13447 8.66002L8.66184 6.56283L11.3794 8.47884ZM2.67839 3.15047C2.86636 2.5725 3.40355 2.18391 4.0148 2.18391C4.05792 2.18391 6.09773 2.36855 6.09773 2.36855L3.8888 6.80723L1.72598 5.75522C1.81003 5.55562 2.44355 3.87141 2.67839 3.15047ZM3.99886 12.4162C3.57417 12.2062 3.28917 11.7876 3.25495 11.324C3.23198 11.0142 3.14714 10.717 3.0023 10.4418C2.85792 10.1662 2.66152 9.92761 2.41917 9.73261L1.5412 9.02527C1.1573 8.71636 0.937453 8.25703 0.937453 7.76578C0.937453 7.49016 1.0073 7.22016 1.13902 6.98531C1.20427 6.86878 1.27036 6.74339 1.33753 6.60877L5.87217 9.01683L4.53778 12.7113C4.35703 12.6029 4.17684 12.5039 3.99886 12.4162ZM12.7115 14.1093C12.4832 14.2064 12.3 14.3029 12.1359 14.4126C11.9282 14.5518 11.76 14.707 11.6212 14.887L11.3868 15.195C11.2978 15.3107 11.1717 15.3731 11.0235 15.3745C10.8637 15.3726 10.7278 15.3 10.6415 15.1696C10.5543 15.0379 10.4081 14.9592 10.2496 14.9592C9.99511 14.9592 9.75839 14.8335 9.61636 14.6231L8.90855 13.5721C8.82136 13.4428 8.70183 13.3359 8.56308 13.2642C8.42995 13.1953 8.27794 13.1587 8.13028 13.1587C7.87059 13.1587 7.61625 13.2689 7.43995 13.4615L7.0448 13.8914C6.90089 14.0475 6.68245 14.1225 6.47058 14.0873C6.34964 14.0667 6.23761 14.0118 6.14714 13.9289C5.88211 13.6852 5.61122 13.4595 5.33859 13.2538L6.69802 9.49012L10.1998 11.4608C10.2574 11.4932 10.3189 11.5116 10.3807 11.5181C10.3934 11.5199 12.882 11.5211 12.882 11.5211L12.9942 14.0011C12.9016 14.0338 12.8067 14.0694 12.7115 14.1093ZM18.1792 11.7562L17.8326 12.2391C17.6887 12.4397 17.5781 12.66 17.5035 12.8944C17.4314 13.1227 17.3948 13.3598 17.3948 13.5989C17.3948 13.9027 17.4529 14.1998 17.5682 14.482C17.6868 14.7717 17.8593 15.0309 18.082 15.2527L18.7368 15.9052C18.8774 16.0283 18.8053 16.1887 18.7992 16.1991C18.7903 16.2122 18.7251 16.2788 18.5339 16.2628C18.172 16.2188 17.7857 16.0317 17.4178 15.7219C17.0203 15.3881 16.6462 14.9123 16.3359 14.3466C16.1835 14.0662 15.952 13.8398 15.6829 13.7095C15.4556 13.598 15.2039 13.5436 14.9128 13.5436C14.5271 13.5452 14.1776 13.6373 13.9197 13.7149L13.8216 11.5454L18.1792 11.7562ZM15.8685 10.7058C15.8685 10.7058 13.9741 10.5994 13.3419 10.5841C12.8174 10.5714 11.2431 10.5835 11.2431 10.5835L12.3793 8.62313L13.4693 7.43855C13.5494 7.45359 13.716 7.45303 13.716 7.45303C14.0292 7.45303 14.3287 7.36491 14.6053 7.18162C14.7248 7.10053 14.8364 7.00631 14.9779 6.85631C15.1476 6.67584 15.3192 6.46092 15.4753 6.23077C15.5985 6.04561 15.7359 5.82731 15.8175 5.59997C15.8939 5.37684 15.9925 5.20144 16.0687 5.10178C16.1914 4.94137 16.3923 4.9867 16.4252 4.99523C16.5072 5.01661 16.5817 5.061 16.6444 5.12658C16.733 5.21986 16.7812 5.34183 16.7812 5.4698V5.9137C16.7817 6.28702 16.9267 6.63797 17.1903 6.90244L15.8685 10.7058Z",
          fill: color
        }
      ) })
    }
  );
};
var CountyIcon_default = CountyIcon;

// src/icons/DashboardIcon/DashboardIcon.tsx
import { jsx as jsx62 } from "react/jsx-runtime";
var DashboardIcon = ({
  width = 20,
  height = 20,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "dashboard-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx62(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx62("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx62(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M0.874512 10.0556C0.874512 10.6078 1.32223 11.0556 1.87451 11.0556H8.31896C8.87124 11.0556 9.31896 10.6078 9.31896 10.0556V1.5C9.31896 0.947716 8.87124 0.5 8.31896 0.5H1.87451C1.32223 0.5 0.874512 0.947715 0.874512 1.5V10.0556ZM0.874512 18.5C0.874512 19.0523 1.32223 19.5 1.87451 19.5H8.31896C8.87124 19.5 9.31896 19.0523 9.31896 18.5V14.1667C9.31896 13.6144 8.87124 13.1667 8.31896 13.1667H1.87451C1.32223 13.1667 0.874512 13.6144 0.874512 14.1667V18.5ZM11.4301 18.5C11.4301 19.0523 11.8778 19.5 12.4301 19.5H18.8745C19.4268 19.5 19.8745 19.0523 19.8745 18.5V9.94445C19.8745 9.39216 19.4268 8.94444 18.8745 8.94444H12.4301C11.8778 8.94444 11.4301 9.39216 11.4301 9.94444V18.5ZM12.4301 0.5C11.8778 0.5 11.4301 0.947715 11.4301 1.5V5.83333C11.4301 6.38562 11.8778 6.83333 12.4301 6.83333H18.8745C19.4268 6.83333 19.8745 6.38562 19.8745 5.83333V1.5C19.8745 0.947716 19.4268 0.5 18.8745 0.5H12.4301Z",
          fill: color
        }
      ) })
    }
  );
};
var DashboardIcon_default = DashboardIcon;

// src/icons/DataIcon/Variants/DataIconDefault.tsx
import { jsx as jsx63, jsxs as jsxs34 } from "react/jsx-runtime";
var DataIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "data-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx63(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs34("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsxs34("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: [
          /* @__PURE__ */ jsx63(
            "path",
            {
              "data-testid": "path-with-background-color",
              d: "M66.3584 12.9201C65.4713 10.9494 64.1135 9.3788 62.7971 8.22282C60.5644 6.26216 57.7597 4.8341 54.9324 3.78397C49.2212 1.66267 41.8264 0.5 34.0208 0.5C26.2153 0.5 18.8205 1.66267 13.1093 3.78397C10.282 4.8341 7.47725 6.26216 5.24454 8.22282C3.02497 10.172 0.6875 13.3 0.6875 17.537C0.6875 21.7741 3.02497 24.9021 5.24454 26.8513C7.47725 28.8119 10.282 30.24 13.1093 31.2901C18.0084 33.1098 24.1464 34.224 30.7197 34.5041C30.6985 34.8309 30.6875 35.1628 30.6875 35.5C30.6875 41.2201 33.8431 45.4429 36.8395 48.0742C39.8537 50.7211 43.6401 52.649 47.4569 54.0666C55.167 56.9304 65.15 58.5 75.6875 58.5C86.225 58.5 96.208 56.9304 103.918 54.0666C107.735 52.649 111.521 50.7211 114.535 48.0742C117.532 45.4429 120.688 41.2201 120.688 35.5C120.688 29.7799 117.532 25.5571 114.535 22.9258C111.521 20.2789 107.735 18.351 103.918 16.9334C96.208 14.0696 86.225 12.5 75.6875 12.5C72.5118 12.5 69.3864 12.6426 66.3584 12.9201Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx63(
            "path",
            {
              d: "M30.6875 57.7168V50.7991C24.2019 50.52 18.1451 49.4288 13.2865 47.6517C10.4702 46.6217 7.68961 45.2286 5.45581 43.3301C3.32749 41.5211 1.03954 38.656 0.72388 34.7327C0.699819 34.4897 0.6875 34.2433 0.6875 33.9939V48.8087C0.6875 49.0581 0.699819 49.3045 0.72388 49.5475C1.03954 53.4708 3.32749 56.336 5.45581 58.1449C7.68961 60.0434 10.4702 61.4365 13.2865 62.4665C18.1451 64.2436 24.2019 65.3348 30.6875 65.6139V77.7168C30.6875 78.0534 30.7041 78.3861 30.7366 78.7142C31.1628 84.0105 34.2515 87.8785 37.1247 90.3206C40.1404 92.8837 43.8942 94.7642 47.6961 96.1548C55.3678 98.9608 65.2521 100.5 75.6875 100.5C86.1229 100.5 96.0072 98.9608 103.679 96.1548C107.481 94.7642 111.235 92.8837 114.25 90.3206C117.124 87.8785 120.212 84.0105 120.638 78.7141C120.671 78.386 120.688 78.0534 120.688 77.7168V57.7168C120.688 58.0534 120.671 58.386 120.638 58.7141C120.212 64.0105 117.124 67.8785 114.25 70.3206C111.235 72.8837 107.481 74.7642 103.679 76.1548C96.0072 78.9608 86.1229 80.5 75.6875 80.5C65.2521 80.5 55.3678 78.9608 47.6961 76.1548C43.8942 74.7642 40.1404 72.8837 37.1247 70.3206C34.2515 67.8785 31.1628 64.0105 30.7366 58.7142C30.7041 58.3861 30.6875 58.0534 30.6875 57.7168Z",
              fill: backgroundColor
            }
          )
        ] }),
        /* @__PURE__ */ jsxs34("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx63(
            "path",
            {
              "data-testid": "path-with-color",
              d: "M30.3171 8.64902C30.3171 6.60352 31.9753 4.94531 34.0208 4.94531C40.8481 4.94531 47.2039 5.87766 52.1997 7.49667C54.1456 8.12729 55.2118 10.2159 54.5812 12.1618C53.9506 14.1077 51.8619 15.1739 49.916 14.5433C45.7994 13.2092 40.2369 12.3527 34.0208 12.3527C31.9753 12.3527 30.3171 10.6945 30.3171 8.64902Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx63(
            "path",
            {
              d: "M30.6875 77.7168V65.6147C24.2019 65.3356 18.1451 64.2445 13.2865 62.4674C10.4702 61.4373 7.68961 60.0443 5.45581 58.1457C3.32749 56.3368 1.03954 53.4716 0.72388 49.5484C0.699819 49.3054 0.6875 49.0589 0.6875 48.8096V63.6244C0.6875 63.8738 0.699819 64.1202 0.72388 64.3632C1.03954 68.2865 3.32749 71.1516 5.45581 72.9606C7.68961 74.8591 10.4702 76.2521 13.2865 77.2822C18.1451 79.0593 24.2019 80.1505 30.6875 80.4296V97.7168C30.6875 98.0534 30.7041 98.3861 30.7366 98.7142C31.1628 104.011 34.2515 107.879 37.1247 110.321C40.1404 112.884 43.8942 114.764 47.6961 116.155C55.3678 118.961 65.2521 120.5 75.6875 120.5C86.1229 120.5 96.0072 118.961 103.679 116.155C107.481 114.764 111.235 112.884 114.25 110.321C117.124 107.879 120.212 104.01 120.638 98.7141C120.671 98.386 120.688 98.0534 120.688 97.7168V77.7168C120.688 78.0534 120.671 78.386 120.638 78.7141C120.212 84.0105 117.124 87.8785 114.25 90.3206C111.235 92.8837 107.481 94.7642 103.679 96.1548C96.0072 98.9608 86.1229 100.5 75.6875 100.5C65.2521 100.5 55.3678 98.9608 47.6961 96.1548C43.8942 94.7642 40.1404 92.8837 37.1247 90.3206C34.2515 87.8785 31.1628 84.0105 30.7366 78.7142C30.7041 78.3861 30.6875 78.0534 30.6875 77.7168Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx63(
            "path",
            {
              d: "M30.6875 50.8004C24.2019 50.5213 18.1451 49.4302 13.2865 47.6531C10.4702 46.623 7.68961 45.23 5.45581 43.3314C3.32749 41.5225 1.03954 38.6573 0.72388 34.7341C0.699819 34.4911 0.6875 34.2446 0.6875 33.9953V17.5384C0.6875 21.7755 3.02497 24.9035 5.24454 26.8526C7.47725 28.8133 10.282 30.2413 13.1093 31.2915C18.0004 33.1082 24.1263 34.2218 30.6875 34.5041V34.5007C30.7197 34.5055 30.7197 34.5055 30.7197 34.5055C30.6985 34.832 30.6875 35.1637 30.6875 35.5007C30.6875 41.2207 33.8431 45.4435 36.8395 48.0749C39.8537 50.7217 43.6401 52.6496 47.4569 54.0673C55.167 56.9311 65.15 58.5007 75.6875 58.5007C86.225 58.5007 96.208 56.9311 103.918 54.0673C107.735 52.6496 111.521 50.7217 114.535 48.0749C117.532 45.4435 120.688 41.2207 120.688 35.5007V57.7175C120.688 58.054 120.671 58.3867 120.638 58.7147C120.212 64.0112 117.124 67.8792 114.25 70.3212C111.235 72.8843 107.481 74.7649 103.679 76.1555C96.0072 78.9615 86.1229 80.5007 75.6875 80.5007C65.2521 80.5007 55.3678 78.9615 47.6961 76.1555C43.8942 74.7649 40.1404 72.8843 37.1247 70.3212C34.2515 67.8792 31.1628 64.0112 30.7366 58.7148C30.7041 58.3868 30.6875 58.0541 30.6875 57.7175V50.8004Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx63(
            "path",
            {
              d: "M120.688 34.5007V35.5007C120.688 30.9879 120.688 28.6327 120.688 34.5007Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx63(
            "path",
            {
              d: "M0.6875 16.7977V17.5384C0.6875 14.1956 0.6875 12.451 0.6875 16.7977Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx63(
            "path",
            {
              d: "M75.6875 18.5C72.9261 18.5 70.6875 20.7386 70.6875 23.5C70.6875 26.2614 72.9261 28.5 75.6875 28.5C84.0791 28.5 91.5886 29.6562 97.146 31.4573C99.773 32.3086 102.593 30.8692 103.444 28.2423C104.295 25.6153 102.856 22.7957 100.229 21.9443C93.4847 19.7587 84.9043 18.5 75.6875 18.5Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var DataIconDefault_default = DataIconDefaultVariant;

// src/icons/DataIcon/Variants/DataIconOutlined.tsx
import { jsx as jsx64, jsxs as jsxs35 } from "react/jsx-runtime";
var DataIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "data-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx64(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs35("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx64(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M70.8125 32.5C70.8125 29.7386 73.0511 27.5 75.8125 27.5C85.0293 27.5 93.6097 28.7587 100.354 30.9443C102.981 31.7957 104.42 34.6153 103.569 37.2423C102.718 39.8692 99.898 41.3086 97.271 40.4573C91.7136 38.6562 84.2041 37.5 75.8125 37.5C73.0511 37.5 70.8125 35.2614 70.8125 32.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx64(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M0.8125 16.805C0.8125 15.6916 1.05802 14.6355 1.49788 13.6879C2.35472 11.3559 3.88962 9.52867 5.37197 8.22693C7.60587 6.26522 10.4121 4.83641 13.2409 3.78572C18.9551 1.66329 26.3539 0.5 34.1636 0.5C41.9733 0.5 49.372 1.66329 55.0863 3.78572C57.9151 4.83641 60.7213 6.26522 62.9552 8.22693C64.2702 9.38168 65.6265 10.95 66.5141 12.9173C69.5325 12.6416 72.6475 12.5 75.8125 12.5C86.35 12.5 96.333 14.0696 104.043 16.9334C107.86 18.351 111.646 20.2789 114.66 22.9258C116.661 24.6822 118.732 27.1477 119.888 30.2943C120.481 31.5728 120.812 32.9977 120.812 34.5V97.7168C120.812 98.0534 120.796 98.386 120.763 98.7141C120.337 104.01 117.249 107.879 114.375 110.321C111.36 112.884 107.606 114.764 103.804 116.155C96.1322 118.961 86.2479 120.5 75.8125 120.5C65.3771 120.5 55.4928 118.961 47.8211 116.155C44.0192 114.764 40.2654 112.884 37.2497 110.321C34.3765 107.879 31.2878 104.011 30.8616 98.7142C30.8291 98.3861 30.8125 98.0534 30.8125 97.7168V80.4705C24.3295 80.1902 18.2755 79.0988 13.4182 77.3222C10.6004 76.2916 7.81834 74.8978 5.58335 72.9983C3.45389 71.1884 1.16472 68.3217 0.8489 64.3963C0.824825 64.1532 0.8125 63.9066 0.8125 63.6572V16.805ZM30.8125 73.0516V65.6478C24.3295 65.3675 18.2755 64.2761 13.4182 62.4995C11.6287 61.845 9.85357 61.044 8.22385 60.06V63.6572H8.22738C8.43987 68.484 18.2089 72.4478 30.8125 73.0516ZM30.8125 58.2289V50.8251C24.3295 50.5448 18.2755 49.4534 13.4182 47.6768C11.6287 47.0223 9.85357 46.2213 8.22385 45.2373V48.8344H8.22738C8.43987 53.6613 18.2089 57.6251 30.8125 58.2289ZM30.8125 43.4062V34.5202C24.2538 34.2365 18.1306 33.1227 13.2409 31.3065C11.5188 30.6669 9.80515 29.8871 8.22385 28.9323V34.0117H8.22738C8.43987 38.8386 18.2089 42.8024 30.8125 43.4062ZM33.2871 27.1755C34.3685 25.4719 35.6811 24.0529 36.9645 22.9258C38.4588 21.6136 40.1429 20.4781 41.9264 19.4939C39.5104 19.1943 36.9003 19.0284 34.1636 19.0284C32.117 19.0284 30.4579 17.3693 30.4579 15.3227C30.4579 13.2761 32.117 11.617 34.1636 11.617C40.9944 11.617 47.3537 12.5499 52.3521 14.1697C52.907 14.3496 53.3904 14.6479 53.7833 15.0276C55.2498 14.659 56.7682 14.3287 58.3302 14.0381C56.3992 12.2042 52.9987 10.6439 48.6667 9.55682C48.445 9.50117 48.2208 9.44676 47.9942 9.39362C43.9909 8.45471 39.2481 7.91135 34.1636 7.91135C29.079 7.91135 24.3362 8.45471 20.333 9.39362C13.5854 10.9762 8.93907 13.6827 8.29945 16.805C8.24936 17.0496 8.22385 17.2967 8.22385 17.5461C8.22385 22.7583 19.3668 27.0038 33.2871 27.1755ZM75.8125 58.5C86.35 58.5 96.333 56.9304 104.043 54.0666C106.367 53.2036 108.679 52.1515 110.812 50.8632V57.7168H110.808C110.496 64.7965 94.9475 70.5 75.8125 70.5C56.6775 70.5 41.1289 64.7965 40.8173 57.7168H40.8125V50.8632C42.9461 52.1515 45.2584 53.2036 47.5819 54.0666C55.292 56.9304 65.275 58.5 75.8125 58.5ZM40.8125 35.5C40.8125 42.6797 56.4825 48.5 75.8125 48.5C95.1425 48.5 110.812 42.6797 110.812 35.5C110.812 35.1635 110.778 34.8301 110.71 34.5C109.877 30.4285 103.993 26.8812 95.3813 24.7202C95.0821 24.6451 94.7796 24.5717 94.4739 24.5C89.0724 23.2331 82.673 22.5 75.8125 22.5C68.952 22.5 62.5526 23.2331 57.1511 24.5C48.0468 26.6353 41.7775 30.2871 40.9145 34.5C40.8469 34.83 40.8125 35.1635 40.8125 35.5ZM40.8125 77.7168V72.8632C43.0115 74.1909 45.4066 75.2717 47.8211 76.1548C55.4928 78.9608 65.3771 80.5 75.8125 80.5C86.2479 80.5 96.1322 78.9608 103.804 76.1548C106.218 75.2717 108.614 74.1909 110.812 72.8632V77.7168H110.808C110.496 84.7965 94.9475 90.5 75.8125 90.5C56.6775 90.5 41.1289 84.7965 40.8173 77.7168H40.8125ZM40.8125 92.8632V97.7168H40.8173C41.1289 104.797 56.6775 110.5 75.8125 110.5C94.9475 110.5 110.496 104.797 110.808 97.7168H110.812V92.8632C108.614 94.1909 106.218 95.2717 103.804 96.1548C96.1322 98.9608 86.2479 100.5 75.8125 100.5C65.3771 100.5 55.4928 98.9608 47.8211 96.1548C45.4066 95.2717 43.0115 94.1909 40.8125 92.8632Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var DataIconOutlined_default = DataIconOutlinedVariant;

// src/icons/DataIcon/DataIcon.tsx
import { jsx as jsx65 } from "react/jsx-runtime";
var DataIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx65(DataIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx65(DataIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var DataIcon_default = DataIcon;

// src/icons/DataverseYourDataIcon/Variants/DataverseYourDataIconDefault.tsx
import { jsx as jsx66, jsxs as jsxs36 } from "react/jsx-runtime";
var DataIconDefaultVariant2 = ({
  color = "#3E3E3C",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "dataverse-your-data-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx66(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs36("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsxs36("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: [
          /* @__PURE__ */ jsx66(
            "path",
            {
              "data-testid": "path-with-background-color",
              d: "M0.53418 65.8747C0.538557 66.1575 0.554677 66.4375 0.582073 66.7142C1.00821 72.0106 4.09694 75.8785 6.97018 78.3206C9.98581 80.8837 13.7396 82.7642 17.5416 84.1548C25.2133 86.9608 35.0976 88.5 45.533 88.5C55.9683 88.5 65.8526 86.9608 73.5243 84.1548C77.3263 82.7642 81.0801 80.8837 84.0957 78.3206C86.969 75.8785 90.0577 72.0105 90.4839 66.7141C90.5163 66.3861 90.533 66.0534 90.533 65.7168V45.7168C90.533 46.0534 90.5163 46.386 90.4839 46.7141C90.0577 52.0105 86.969 55.8785 84.0957 58.3206C81.0801 60.8836 77.3263 62.7642 73.5243 64.1548C65.8526 66.9608 55.9683 68.5 45.533 68.5C35.0976 68.5 25.2133 66.9608 17.5416 64.1548C13.7396 62.7642 9.98581 60.8836 6.97018 58.3206C4.09694 55.8785 1.00821 52.0105 0.582073 46.7142C0.554677 46.4375 0.538557 46.1575 0.53418 45.8747V65.8747Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx66(
            "path",
            {
              d: "M0.53418 23.6947C0.604748 29.3159 3.72263 33.4728 6.68497 36.0742C9.69912 38.7211 13.4855 40.649 17.3024 42.0666C25.0125 44.9304 34.9955 46.5 45.533 46.5C56.0704 46.5 66.0534 44.9304 73.7636 42.0666C77.5804 40.649 81.3668 38.7211 84.381 36.0742C87.3774 33.4429 90.533 29.2201 90.533 23.5C90.533 17.7799 87.3774 13.5571 84.381 10.9258C81.3668 8.27891 77.5804 6.35104 73.7636 4.93336C66.0534 2.0696 56.0704 0.5 45.533 0.5C18.9458 0.5 0.53418 8.57408 0.53418 23.6947Z",
              fill: backgroundColor
            }
          )
        ] }),
        /* @__PURE__ */ jsxs36("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx66(
            "path",
            {
              "data-testid": "path-with-color",
              d: "M104.06 71.3026C109.681 65.8298 113.162 58.1694 113.162 49.6667C113.162 32.944 99.711 19.5 82.9954 19.5C66.2798 19.5 52.8288 32.944 52.8288 49.6667C52.8288 58.1693 56.3095 65.8297 61.9307 71.3025C59.3071 72.9877 57.126 75.3685 55.6773 78.2659L47.344 94.9326C41.4707 106.679 50.0124 120.5 63.1455 120.5H102.845C115.978 120.5 124.52 106.679 118.646 94.9325L110.313 78.2659C108.865 75.3686 106.684 72.9879 104.06 71.3026Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx66(
            "path",
            {
              d: "M45.5332 46.5005C46.3572 46.5005 47.1778 46.4909 47.9942 46.4718C47.8848 47.5213 47.8288 48.5871 47.8288 49.6667C47.8288 56.6405 50.1703 63.0477 54.114 68.146C51.3209 68.3804 48.4485 68.5005 45.5332 68.5005C35.0978 68.5005 25.2135 66.9613 17.5418 64.1553C13.7399 62.7647 9.98606 60.8841 6.97042 58.3211C4.09719 55.879 1.00845 52.011 0.582317 46.7146C0.554921 46.438 0.538801 46.158 0.534424 45.8751V23.6952C0.604992 29.3164 3.72288 33.4733 6.68521 36.0747C9.69937 38.7216 13.4858 40.6495 17.3026 42.0671C25.0127 44.9309 34.9957 46.5005 45.5332 46.5005Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx66(
            "path",
            {
              d: "M45.5332 88.4998H45.5603L42.344 94.9326C40.1001 99.4203 39.9602 104.211 41.3766 108.418C32.4915 108.065 24.1867 106.585 17.5418 104.155C13.7399 102.764 9.98606 100.883 6.97042 98.3204C4.09719 95.8784 1.00845 92.0104 0.582317 86.714C0.549833 86.3859 0.533203 86.0532 0.533203 85.7166V72.3161H0.534424V65.8745C0.538801 66.1573 0.554921 66.4373 0.582317 66.714C1.00845 72.0104 4.09719 75.8784 6.97042 78.3204C9.98606 80.8835 13.7399 82.764 17.5418 84.1546C25.2135 86.9606 35.0978 88.4998 45.5332 88.4998Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var DataverseYourDataIconDefault_default = DataIconDefaultVariant2;

// src/icons/DataverseYourDataIcon/Variants/DataverseYourDataIconOutlined.tsx
import { jsx as jsx67 } from "react/jsx-runtime";
var DataIconOutlinedVariant2 = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "dataverse-your-data-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx67(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx67("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx67(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M45.9668 46.5C48.5021 46.5 51.0054 46.4091 53.4526 46.2312C53.867 42.5391 54.9419 39.0534 56.5571 35.8943C53.2162 36.2877 49.6582 36.5 45.9668 36.5C26.6368 36.5 10.9668 30.6797 10.9668 23.5C10.9668 23.1635 11.0013 22.8298 11.0689 22.4998C11.9319 18.2869 18.2011 14.6353 27.3054 12.5C32.7069 11.2331 39.1063 10.5 45.9668 10.5C52.8273 10.5 59.2267 11.2331 64.6282 12.5C64.9339 12.5717 65.2364 12.6451 65.5356 12.7202C72.1423 14.378 77.1432 16.8517 79.4903 19.7521C80.4565 19.6273 81.4373 19.5479 82.4305 19.516C66.181 20.0388 53.2624 33.2783 53.2624 49.6667C53.2624 58.1693 56.7431 65.8297 62.3643 71.3025C59.7407 72.9877 57.5596 75.3685 56.1109 78.2659L47.7776 94.9326C41.9043 106.679 50.446 120.5 63.5791 120.5H103.279C116.412 120.5 124.953 106.679 119.08 94.9325L110.747 78.2659C109.298 75.3686 107.117 72.9879 104.494 71.3026C110.115 65.8298 113.596 58.1694 113.596 49.6667C113.596 35.4691 103.9 23.6347 90.7426 20.3846C90.5855 19.655 90.3488 18.9551 90.042 18.2943C88.8859 15.1477 86.8149 12.6822 84.8148 10.9258C81.8006 8.27891 78.0142 6.35104 74.1974 4.93336C66.4873 2.0696 56.5043 0.5 45.9668 0.5C35.4293 0.5 25.4463 2.0696 17.7362 4.93336C13.9194 6.35104 10.133 8.27891 7.1188 10.9258C5.1187 12.6822 3.04768 15.1477 1.89157 18.2943C1.29808 19.5728 0.966797 20.9977 0.966797 22.5V85.7168C0.966797 86.0534 0.983427 86.3861 1.01591 86.7142C1.44205 92.0105 4.53078 95.8785 7.40402 98.3206C10.4197 100.884 14.1735 102.764 17.9754 104.155C25.6471 106.961 35.5314 108.5 45.9668 108.5C46.2569 108.5 46.5465 108.499 46.8357 108.496C45.7663 105.369 45.5583 101.914 46.438 98.4988C46.2812 98.4996 46.1241 98.5 45.9668 98.5C26.8318 98.5 11.2832 92.7965 10.9716 85.7168H10.9668V80.8632C13.1658 82.1909 15.5609 83.2717 17.9754 84.1548C25.6471 86.9608 35.5314 88.5 45.9668 88.5C47.6799 88.5 49.3782 88.4585 51.0542 88.3768L56.1097 78.2659C56.1667 78.1518 56.2249 78.0385 56.2842 77.9259C53.0228 78.2991 49.5579 78.5 45.9668 78.5C26.8318 78.5 11.2832 72.7965 10.9716 65.7168H10.9668V60.8632C13.1658 62.1909 15.5609 63.2717 17.9754 64.1548C25.6471 66.9608 35.5314 68.5 45.9668 68.5C50.5175 68.5 54.9634 68.2073 59.1667 67.6445C57.0685 64.8116 55.4561 61.5949 54.4503 58.1155C51.7341 58.3667 48.8924 58.5 45.9668 58.5C26.8318 58.5 11.2832 52.7965 10.9716 45.7168H10.9668V38.8632C13.1004 40.1515 15.4127 41.2036 17.7362 42.0666C25.4463 44.9304 35.4293 46.5 45.9668 46.5ZM71.9124 78.8333C69.1348 78.8333 66.5955 80.4027 65.3533 82.8871L57.02 99.5538C54.582 104.43 58.1276 110.167 63.5791 110.167H103.279C108.73 110.167 112.276 104.43 109.838 99.5538L101.504 82.8871C100.262 80.4027 97.7229 78.8333 94.9452 78.8333H71.9124ZM89.7031 68.5C97.6058 65.8945 103.262 58.4824 103.262 49.6667C103.262 38.6524 94.4391 29.8333 83.429 29.8333C72.4189 29.8333 63.5957 38.6524 63.5957 49.6667C63.5957 58.4824 69.2523 65.8945 77.155 68.5H89.7031Z",
          fill: color
        }
      ) })
    }
  );
};
var DataverseYourDataIconOutlined_default = DataIconOutlinedVariant2;

// src/icons/DataverseYourDataIcon/DataverseYourDataIcon.tsx
import { jsx as jsx68 } from "react/jsx-runtime";
var DataverseYourDataIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx68(DataverseYourDataIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx68(DataverseYourDataIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var DataverseYourDataIcon_default = DataverseYourDataIcon;

// src/icons/DataSourcesIcon/DataSourcesIcon.tsx
import { jsx as jsx69, jsxs as jsxs37 } from "react/jsx-runtime";
var DataSourcesIcon = ({
  width = 14,
  height = 16,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#009FD3",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "data-sources-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx69(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 14 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs37("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx69(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M9.64836 0H2.2406C1.00324 0 0 1.00287 0 2.2406V12.7901C0 14.0278 1.00324 15.0307 2.2406 15.0307H8.42248L6.92875 13.5369H2.2406C1.82814 13.5369 1.49373 13.2029 1.49373 12.7901V10.5312H8.98883V9.03752H1.49373V6.08069H10.3952V7.6809H11.889V2.2406C11.889 1.00287 10.8857 0 9.64836 0ZM10.3952 4.58695H1.49373V2.2406C1.49373 1.82778 1.82814 1.49373 2.2406 1.49373H9.64836C9.81575 1.49373 9.97019 1.54843 10.0947 1.64179C10.2771 1.77818 10.3952 1.99553 10.3952 2.2406V4.58695Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx69(
          "path",
          {
            d: "M11.8837 13.5391H13.8498L11.1366 16L8.42339 13.5391H10.3899V9.03752H11.8837V13.5391Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var DataSourcesIcon_default = DataSourcesIcon;

// src/icons/DataVisualizationIcon/DataVisualizationIcon.tsx
import { jsx as jsx70 } from "react/jsx-runtime";
var DataVisualizationIcon = ({
  width = 53,
  height = 48,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "data-visualization-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx70(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 53 48",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx70("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx70(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M49.7262 2.28596H49.7276L49.7301 2.28597H49.7308C50.0072 2.28597 50.2316 2.51913 50.2316 2.8053V2.80573V2.80762V5.50741H2.74675V2.8053C2.74675 2.51922 2.97147 2.28597 3.24751 2.28597L49.723 2.28597L49.7237 2.28597L49.7262 2.28596ZM51.3392 47.6878C51.3415 47.6878 51.3439 47.6879 51.346 47.688C51.3474 47.6881 51.3488 47.6881 51.35 47.6882C51.3489 47.6881 51.3478 47.6881 51.3467 47.688C51.3443 47.6879 51.3418 47.6878 51.3392 47.6878ZM49.7276 1.14739e-05H49.7262L49.7237 8.9214e-07H3.23969C3.23506 8.9214e-07 3.23043 3.06549e-05 3.22581 9.0007e-05C1.74429 0.0122539 0.542531 1.26557 0.542531 2.8053V6.60725C0.54201 6.6216 0.541748 6.63604 0.541748 6.65044L0.548009 46.8567C0.548036 47.4885 1.04207 47.9996 1.65012 47.9996H51.3352L51.338 47.9998C51.3412 47.9999 51.3506 48.0002 51.3621 47.9998C51.3638 47.9998 51.3658 47.9997 51.368 47.9996C51.9717 47.9925 52.457 47.4838 52.4581 46.8572V46.8567C52.4581 46.7911 52.4527 46.7252 52.442 46.6612L52.4358 6.65034C52.4358 6.61543 52.4343 6.58079 52.4313 6.54651C52.4343 6.51219 52.4358 6.47749 52.4358 6.44251L52.4358 2.80078C52.4334 1.25561 51.2208 8.9214e-07 49.7308 0L49.7301 1.78428e-06L49.7276 1.14739e-05ZM6.95715 38.5451V45.7137H2.75296L2.7468 7.79338H50.2237V8.72772C50.2237 8.77449 50.2265 8.82073 50.2318 8.86628L50.2378 45.7137H46.7447C46.7447 43.4588 46.7447 41.2485 46.7447 38.9802C46.7447 38.7513 46.7447 38.6986 46.7447 38.5451C46.7447 37.9131 46.2728 37.4021 45.6238 37.4021H8.05926C7.45031 37.4021 6.95715 37.913 6.95715 38.5451ZM34.2166 45.7137V43.8398H44.5405V45.7137H34.2166ZM21.6885 45.7137V43.8398H32.0124V45.7137H21.6885ZM9.16137 45.7137V43.8398H19.4843V45.7137H9.16137ZM2.95975 3.74078C2.95975 4.37337 3.45845 4.88376 4.06577 4.88376C4.67354 4.88376 5.16788 4.37169 5.16788 3.74078C5.16788 3.10956 4.67442 2.59779 4.06577 2.59779H4.05716C3.44621 2.59779 2.95975 3.11195 2.95975 3.74078ZM5.36406 3.74078C5.36406 4.37337 5.86276 4.88376 6.47008 4.88376C7.07785 4.88376 7.57219 4.37169 7.57219 3.74078C7.57219 3.10956 7.07873 2.59779 6.47008 2.59779H6.46147C5.85052 2.59779 5.36406 3.11195 5.36406 3.74078ZM7.76837 3.74078C7.76837 4.37337 8.26707 4.88376 8.87439 4.88376C9.48216 4.88376 9.9765 4.37169 9.9765 3.74078C9.9765 3.10956 9.48304 2.59779 8.87439 2.59779H8.86578C8.25483 2.59779 7.76837 3.11195 7.76837 3.74078ZM9.16137 39.6881H19.4843V41.5538H9.16137V39.6881ZM21.6885 39.6881H32.0124V41.5538H21.6885V39.6881ZM34.2166 39.6881H44.5405V41.5538H34.2166V39.6881ZM17.3173 32.4327C19.697 34.8582 22.9581 36.3565 26.55 36.3565C33.8166 36.3565 39.7295 30.2244 39.7295 22.6883C39.7295 15.1522 33.8166 9.02003 26.55 9.02003C19.2834 9.02003 13.3705 15.1522 13.3705 22.6883C13.3705 26.4126 14.8146 29.794 17.1526 32.2619C17.1771 32.2928 17.2033 32.3227 17.2311 32.3515C17.2588 32.3803 17.2876 32.4074 17.3173 32.4327ZM19.8221 31.4217C21.6677 32.954 24.0074 33.8714 26.55 33.8714C32.4962 33.8714 37.3332 28.8538 37.3332 22.6883C37.3332 16.9426 33.1326 12.1938 27.7481 11.5737V19.1735C29.1429 19.6863 30.1444 21.0682 30.1444 22.6883C30.1444 24.7432 28.5321 26.416 26.55 26.416C25.9958 26.416 25.4704 26.2851 25.0011 26.0516L19.8221 31.4217ZM18.128 29.6645L23.3069 24.2945C23.0818 23.8078 22.9556 23.263 22.9556 22.6883C22.9556 21.0683 23.9574 19.6864 25.3519 19.1736V11.5737C19.9675 12.1938 15.7668 16.9426 15.7668 22.6883C15.7668 25.3243 16.651 27.7505 18.128 29.6645ZM25.3519 22.6883C25.3519 22.0035 25.8886 21.4461 26.5493 21.4457H26.55H26.5507C27.2121 21.4461 27.7482 22.0035 27.7482 22.6883C27.7482 23.3727 27.2117 23.9308 26.55 23.9308C25.8889 23.9308 25.3519 23.3727 25.3519 22.6883Z",
          fill: color
        }
      ) })
    }
  );
};
var DataVisualizationIcon_default = DataVisualizationIcon;

// src/icons/DoctorMaleIcon/DoctorMaleIcon.tsx
import { jsx as jsx71, jsxs as jsxs38 } from "react/jsx-runtime";
var DoctorMaleIcon = ({
  width = 46,
  height = 47,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#009FD3",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "doctor-male-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx71(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 46 47",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs38("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx71(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M15.5 30.2796C15.4651 30.2134 15.4295 30.1422 15.3926 30.0687C15.1281 29.5412 14.8016 28.8901 14.25 29.0157C7.49475 30.5541 0.5 34.9931 0.5 39.7123V46.4999H45.5V39.7123C45.5 34.9931 38.5052 30.5541 31.75 29.0157C31.1215 28.8726 30.4719 29.7376 30.1866 30.2796H25.1064C23.9882 30.2798 23.4225 30.2796 22.8634 30.2796C22.2908 30.2795 21.725 30.2794 20.5799 30.2796H15.5ZM25.1064 32.7796C25.1063 32.7796 25.1065 32.7796 25.1064 32.7796C23.9731 32.7798 23.4141 32.7796 22.862 32.7796C22.2969 32.7795 21.739 32.7794 20.5805 32.7796H13.9909L13.4735 31.7969C10.8366 32.5562 8.2715 33.7636 6.31537 35.1909C3.91215 36.9443 3 38.5836 3 39.7123V43.9999H43V39.7123C43 38.5836 42.0879 36.9443 39.6846 35.1909C37.6611 33.7144 34.9858 32.4732 32.2536 31.7199L31.6958 32.7796H25.1064Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx71(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M13 40.2682C13.673 40.2682 14.25 39.7178 14.25 38.9999C14.25 38.2821 13.673 37.7315 13 37.7315C12.327 37.7315 11.75 38.2821 11.75 38.9999C11.75 39.7178 12.327 40.2682 13 40.2682ZM13 42.7499C15.0711 42.7499 16.75 41.071 16.75 38.9999C16.75 36.9289 15.0711 35.2499 13 35.2499C10.9289 35.2499 9.25 36.9289 9.25 38.9999C9.25 41.071 10.9289 42.7499 13 42.7499Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx71(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M30.632 35.9409C30.8438 35.5174 31.2765 35.2499 31.75 35.2499H36.75C37.2235 35.2499 37.6563 35.5174 37.868 35.9409L39.118 38.4409C39.2049 38.6145 39.25 38.8059 39.25 38.9999V41.4999C39.25 42.1903 38.6904 42.7499 38 42.7499H35.5V40.2499H36.75V39.295L35.9775 37.7499H32.5225L31.75 39.295V40.2499H33V42.7499H30.5C29.8096 42.7499 29.25 42.1903 29.25 41.4999V38.9999C29.25 38.8059 29.2951 38.6145 29.382 38.4409L30.632 35.9409Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx71(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M35.8992 35.7952C35.7559 36.2866 35.5497 36.8502 35.25 37.2499L33.25 35.7499C33.2484 35.7521 33.2484 35.7526 33.25 35.7499C33.2562 35.7394 33.2857 35.6896 33.3306 35.5835C33.3827 35.46 33.4406 35.2961 33.4992 35.0953C33.6165 34.6931 33.7219 34.1945 33.7927 33.6747C33.8637 33.1535 33.896 32.642 33.8797 32.2109C33.8624 31.7463 33.7927 31.5055 33.757 31.4339L35.993 30.3159C36.2697 30.8694 36.3564 31.54 36.378 32.1171C36.4009 32.7276 36.355 33.388 36.2697 34.0125C36.1844 34.6386 36.0554 35.2597 35.8992 35.7952Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx71(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M10.9846 35.7952C11.128 36.2866 11.3341 36.8502 11.6339 37.2499L13.6339 35.7499C13.6355 35.7521 13.6355 35.7526 13.6339 35.7499C13.6276 35.7394 13.5981 35.6896 13.5533 35.5835C13.5011 35.46 13.4432 35.2961 13.3846 35.0953C13.2674 34.6931 13.162 34.1945 13.0911 33.6747C13.0201 33.1535 12.9879 32.642 13.0041 32.2109C13.0215 31.7463 13.0911 31.5055 13.1269 31.4339L10.8909 30.3159C10.6141 30.8694 10.5275 31.54 10.5059 32.1171C10.483 32.7276 10.5289 33.388 10.6141 34.0125C10.6995 34.6386 10.8285 35.2597 10.9846 35.7952Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx71(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M13.7775 5.80513C13.7877 5.7614 13.7988 5.71266 13.8115 5.65648C13.83 5.57493 13.852 5.47771 13.8803 5.35745C14.0113 4.79986 14.2269 3.98039 14.6705 3.17274C15.1225 2.35005 15.8239 1.51623 16.9235 0.96775C18.0194 0.42115 19.3937 0.216338 21.0924 0.442813C22.9671 0.692763 26.7339 1.3129 30.0607 3.00529C33.4077 4.70792 36.5421 7.63575 36.542 12.5134C36.542 15.0333 35.5664 17.737 34.6231 19.4997C34.1691 20.348 33.6295 21.1575 33.0903 21.5942C32.9464 21.7109 32.7321 21.8599 32.4589 21.9476C32.1679 22.041 31.7174 22.0877 31.2726 21.8285C30.8545 21.5846 30.6745 21.2036 30.5975 20.9591C30.5186 20.7086 30.5 20.4576 30.5 20.2499C30.5 18.1798 30.2129 16.7354 29.9447 15.8366C29.8852 15.637 29.8265 15.4639 29.7718 15.3162C29.7385 15.3175 29.7045 15.3187 29.6698 15.3197C29.0049 15.339 28.0611 15.305 26.8803 15.1056C24.7343 14.7433 21.8274 13.8382 18.4055 11.7422C18.3306 11.91 18.2555 12.0984 18.1811 12.3085C17.9354 13.0027 17.734 13.8263 17.5598 14.6955C17.406 15.462 17.2794 16.2326 17.1604 16.9566C17.1446 17.0524 17.129 17.1471 17.1135 17.2411C16.9855 18.0169 16.8565 18.7769 16.706 19.3288C16.3573 20.6076 15.9961 21.3909 15.6454 21.8828C15.4658 22.1346 15.2729 22.3329 15.0634 22.4777C14.9579 22.5506 14.8492 22.6091 14.7389 22.6531C14.6839 22.675 14.6294 22.6929 14.5756 22.7071C14.5489 22.7141 14.5224 22.7203 14.4964 22.7255C14.4834 22.728 14.4704 22.7304 14.4576 22.7326L14.4386 22.7356L14.4291 22.737L14.4245 22.7378C14.4221 22.738 14.4197 22.7384 14.25 21.4999L14.4197 22.7384L13.7286 22.8331L13.2851 22.2946L14.25 21.4999C13.2851 22.2946 13.2855 22.2951 13.2851 22.2946L13.2835 22.2926L13.2811 22.2897L13.2745 22.2816L13.2536 22.2558C13.2366 22.2344 13.2131 22.2047 13.1839 22.167C13.1254 22.0916 13.044 21.9843 12.945 21.8474C12.7474 21.5741 12.4785 21.1819 12.1831 20.6926C11.5958 19.7197 10.8859 18.3348 10.4332 16.7189C9.98037 15.1023 9.77225 13.2075 10.2476 11.253C10.7105 9.34987 11.801 7.47663 13.7775 5.80513ZM14.1617 19.1266C13.6896 18.3067 13.1754 17.2394 12.8406 16.0445C12.4675 14.7126 12.3301 13.2692 12.6769 11.8437C13.0142 10.4564 13.831 8.997 15.5104 7.61513C15.8124 7.41038 15.9503 7.135 15.9874 7.06087L15.988 7.0595C16.0674 6.901 16.1159 6.74087 16.1446 6.63862C16.1847 6.49568 16.2286 6.30226 16.2689 6.12541C16.2846 6.05619 16.2998 5.98951 16.3139 5.92939C16.4323 5.4261 16.5866 4.87713 16.8616 4.37649C17.1285 3.89089 17.4946 3.47661 18.0394 3.2049C18.5878 2.93133 19.4346 2.74391 20.762 2.92089C22.5788 3.1631 25.9946 3.74173 28.9273 5.23355C31.8398 6.71513 34.0421 8.957 34.042 12.5134C34.042 14.1515 33.4914 16.0129 32.8296 17.4829C32.7048 16.5253 32.5254 15.7421 32.3404 15.122C32.171 14.5539 31.9975 14.125 31.8585 13.826C31.7891 13.6766 31.7284 13.5599 31.6812 13.4746C31.6577 13.432 31.6376 13.3974 31.6215 13.3704L31.6004 13.3358L31.5922 13.3229L31.5889 13.3174L31.5873 13.315L31.5865 13.3139C31.5862 13.3133 31.5859 13.3128 30.5351 13.9899L31.5859 13.3128L31.1239 12.596L30.2961 12.7629L30.2787 12.7659C30.2569 12.7694 30.2164 12.7754 30.158 12.7822C30.0412 12.7961 29.8527 12.8134 29.5972 12.8207C29.0865 12.8356 28.3069 12.8111 27.2965 12.6405C25.2808 12.3001 22.3186 11.3733 18.7201 8.97663L17.8455 8.39412L17.1214 9.15575C16.5247 9.78325 16.1196 10.6402 15.8244 11.4744C15.5215 12.3302 15.2927 13.2853 15.1085 14.204C14.9456 15.0166 14.8115 15.8326 14.6931 16.5526C14.6775 16.6483 14.662 16.742 14.6469 16.8339C14.5126 17.6475 14.4038 18.2686 14.294 18.671C14.2489 18.8367 14.2046 18.9882 14.1617 19.1266ZM33.0015 20.2919C33.0015 20.2919 33.0014 20.291 33.0011 20.2891L33.0015 20.2919ZM31.5176 19.6509C31.5109 19.6558 31.5104 19.6567 31.5166 19.6516C31.517 19.6514 31.5172 19.6511 31.5176 19.6509Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx71(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M15.6135 20.307L14.25 22.125L13.6978 22.6771C15.163 26.3804 18.7756 28.9999 23 28.9999C27.7415 28.9999 31.7124 25.6999 32.7409 21.2712L31.75 20.875L30.3669 20.4139C29.7056 23.88 26.6588 26.4999 23 26.4999C19.3038 26.4999 16.232 23.826 15.6135 20.307Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var DoctorMaleIcon_default = DoctorMaleIcon;

// src/icons/DotIcon/DotIcon.tsx
import { jsx as jsx72 } from "react/jsx-runtime";
var DotIcon = ({
  width = 5,
  height = 5,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "dot-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx72(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 5 5",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx72("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx72(
        "circle",
        {
          "data-testid": "path-with-color",
          cx: "2.5",
          cy: "2.5",
          r: "2.5",
          fill: color
        }
      ) })
    }
  );
};
var DotIcon_default = DotIcon;

// src/icons/EmptyFolderIcon/EmptyFolderIcon.tsx
import { jsx as jsx73, jsxs as jsxs39 } from "react/jsx-runtime";
var EmptyFolderIcon = ({
  width = 56,
  height = 54,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "empty-folder-icon",
  // Extra Properties
  backgroundColor = "white",
  backgroundOpacity = "1",
  shadowColor = "#F7F7FC",
  shadowOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx73(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 56 54",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs39("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsxs39("g", { "data-testid": "g-shadow-path", opacity: shadowOpacity, children: [
          /* @__PURE__ */ jsx73(
            "path",
            {
              "data-testid": "path-with-shadow-color",
              d: "M33.5781 12.8112C33.5781 12.1539 34.0724 11.6211 34.682 11.6211C35.2917 11.6211 35.7859 12.1539 35.7859 12.8112C35.7859 13.4685 35.2917 14.0013 34.682 14.0013C34.0724 14.0013 33.5781 13.4685 33.5781 12.8112Z",
              fill: shadowColor
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M27.4413 16.1017C27.8724 15.637 28.5712 15.637 29.0023 16.1017L29.1921 16.3063L29.3819 16.1017C29.813 15.637 30.5118 15.637 30.9429 16.1017C31.3739 16.5665 31.374 17.3199 30.9429 17.7846L30.7531 17.9892L30.9429 18.1939C31.374 18.6586 31.374 19.412 30.9429 19.8767C30.7275 20.1089 30.4443 20.2254 30.1624 20.2254C29.8805 20.2254 29.5973 20.1089 29.3819 19.8767L29.1921 19.6721L29.0023 19.8767C28.7869 20.1089 28.5037 20.2254 28.2218 20.2254C27.94 20.2254 27.6568 20.109 27.4414 19.8769C27.01 19.412 27.0101 18.6587 27.4413 18.1939L27.6311 17.9892L27.4413 17.7846C27.0102 17.3199 27.0102 16.5665 27.4413 16.1017Z",
              fill: shadowColor
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M11.6191 22.7101C11.6191 20.6416 13.2049 18.9728 15.1398 18.9728H22.7856C23.924 18.9728 25.0019 19.5566 25.6687 20.5408C25.7086 20.5995 25.7433 20.662 25.7723 20.7272L26.9932 23.4577H46.3467C48.4691 23.4577 50.1954 25.3189 50.1954 27.607V29.3497C53.4799 30.4475 55.872 33.7523 55.872 37.6503C55.872 41.5489 53.4793 44.8532 50.1954 45.9508V47.8364C50.1954 49.4189 49.2998 50.7661 48.0336 51.375C47.6876 52.8505 46.4528 53.95 44.9743 53.95H17.5264C16.1557 53.95 14.9695 52.9854 14.5426 51.6329C12.8849 51.2669 11.6191 49.7199 11.6191 47.8362V22.7101Z",
              fill: shadowColor
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M35.6357 20.9485C35.6357 20.2912 36.13 19.7584 36.7396 19.7584C37.3493 19.7584 37.8435 20.2912 37.8435 20.9485C37.8435 21.6058 37.3493 22.1386 36.7396 22.1386C36.13 22.1386 35.6357 21.6058 35.6357 20.9485Z",
              fill: shadowColor
            }
          )
        ] }),
        /* @__PURE__ */ jsxs39("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: [
          /* @__PURE__ */ jsx73(
            "path",
            {
              "data-testid": "path-with-background-color",
              d: "M25.4682 1.82593C25.4682 1.12383 26.0159 0.554688 26.6914 0.554688C27.367 0.554688 27.9147 1.12383 27.9147 1.82593C27.9147 2.52802 27.367 3.09716 26.6914 3.09716C26.0159 3.09716 25.4682 2.52802 25.4682 1.82593Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M18.6678 5.3408C19.1455 4.84437 19.9199 4.84437 20.3976 5.3408L20.6079 5.55937L20.8182 5.3408C21.2959 4.84437 22.0703 4.84437 22.548 5.3408C23.0257 5.83724 23.0257 6.64201 22.548 7.13845L22.3377 7.35702L22.548 7.57559C23.0257 8.07202 23.0257 8.8768 22.548 9.37323C22.3093 9.62125 21.9954 9.74565 21.6831 9.74565C21.3707 9.74565 21.0569 9.62125 20.8182 9.37323L20.6079 9.15466L20.3976 9.37323C20.1589 9.62125 19.845 9.74565 19.5327 9.74565C19.2204 9.74565 18.9065 9.62129 18.6679 9.37334C18.1898 8.87682 18.19 8.07216 18.6678 7.57559L18.8781 7.35702L18.6678 7.13845C18.1901 6.64201 18.1901 5.83724 18.6678 5.3408Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M1.13477 12.3998C1.13477 10.1902 2.89194 8.4076 5.03613 8.4076H13.5086C14.7701 8.4076 15.9646 9.03128 16.7035 10.0826C16.7478 10.1453 16.7861 10.212 16.8183 10.2817L18.1712 13.1984H39.6174C41.9693 13.1984 43.8822 15.1864 43.8822 17.6305V19.4921C47.522 20.6648 50.1727 24.1948 50.1727 28.3586C50.1727 32.523 47.5213 36.0526 43.8822 37.2251V39.2392C43.8822 40.9297 42.8899 42.3687 41.4867 43.0191C41.1033 44.5952 39.735 45.7696 38.0966 45.7696H7.68078C6.16187 45.7696 4.84744 44.7393 4.37434 43.2945C2.53739 42.9037 1.13477 41.2512 1.13477 39.239V12.3998Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M27.7483 10.5181C27.7483 9.81598 28.2959 9.24684 28.9715 9.24684C29.6471 9.24684 30.1948 9.81598 30.1948 10.5181C30.1948 11.2202 29.6471 11.7893 28.9715 11.7893C28.2959 11.7893 27.7483 11.2202 27.7483 10.5181Z",
              fill: backgroundColor
            }
          )
        ] }),
        /* @__PURE__ */ jsxs39("g", { "data-testid": "g-border-path", opacity: borderOpacity, children: [
          /* @__PURE__ */ jsx73(
            "path",
            {
              "data-testid": "path-with-color",
              d: "M27.1155 2.61952C27.5519 2.61952 27.9058 2.26572 27.9058 1.82929C27.9058 1.39286 27.5519 1.03906 27.1155 1.03906C26.6791 1.03906 26.3253 1.39286 26.3253 1.82929C26.3253 2.26572 26.6791 2.61952 27.1155 2.61952Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M19.6759 9.26801C19.4738 9.26801 19.2715 9.19076 19.1172 9.03647C18.8084 8.72788 18.8084 8.22786 19.1172 7.91908L19.6759 7.36038L19.1172 6.80169C18.8086 6.4931 18.8086 5.99288 19.1172 5.6843C19.4258 5.37571 19.926 5.37571 20.2346 5.6843L20.7933 6.24299L21.352 5.6843C21.6605 5.37571 22.1608 5.37571 22.4693 5.6843C22.7779 5.99288 22.7779 6.4931 22.4693 6.80169L21.9107 7.36038L22.4693 7.91908C22.7779 8.22766 22.7779 8.72788 22.4693 9.03647C22.3151 9.19076 22.1128 9.26801 21.9107 9.26801C21.7086 9.26801 21.5063 9.19076 21.352 9.03647L20.7933 8.47777L20.2346 9.03647C20.0803 9.19076 19.878 9.26801 19.6759 9.26801Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M30.2753 10.5214C30.2753 10.9579 29.9215 11.3117 29.485 11.3117C29.0486 11.3117 28.6948 10.9579 28.6948 10.5214C28.6948 10.085 29.0486 9.73121 29.485 9.73121C29.9215 9.73121 30.2753 10.085 30.2753 10.5214Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M45.6485 25.1845C45.2905 24.8265 44.7103 24.8265 44.3523 25.1845L42.3198 27.217L40.2873 25.1845C39.9294 24.8265 39.3491 24.8265 38.9912 25.1845C38.6332 25.5424 38.6332 26.1227 38.9912 26.4807L41.0237 28.5131L38.9912 30.5456C38.6332 30.9036 38.6332 31.4838 38.9912 31.8418C39.1702 32.0208 39.4048 32.1104 39.6393 32.1104C39.8737 32.1104 40.1084 32.0208 40.2873 31.8418L42.3198 29.8093L44.3523 31.8418C44.5313 32.0208 44.766 32.1104 45.0004 32.1104C45.2348 32.1104 45.4695 32.0208 45.6485 31.8418C46.0064 31.4838 46.0064 30.9036 45.6485 30.5456L43.616 28.5131L45.6485 26.4807C46.0064 26.1227 46.0064 25.5424 45.6485 25.1845Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              fillRule: "evenodd",
              clipRule: "evenodd",
              d: "M17.9589 13.6828H40.5486C42.7271 13.6828 44.4998 15.4554 44.4998 17.6339V19.6099H43.1533C47.5767 20.0698 51.0371 23.8187 51.0371 28.362C51.0371 32.4326 48.2593 35.8655 44.4998 36.8666V39.2426C44.4998 40.8131 43.4877 42.1471 42.0742 42.6735C41.8152 44.1578 40.5253 45.292 38.9682 45.292H7.35895C5.89703 45.292 4.63957 44.269 4.29958 42.8733C2.46427 42.6378 1.03711 41.1085 1.03711 39.2424V12.4032C1.03711 10.4671 2.63989 8.89198 4.61053 8.89198H13.4155C14.5753 8.89198 15.6716 9.44435 16.3476 10.3701C16.3759 10.4086 16.4006 10.4499 16.4213 10.4932L17.9589 13.6828ZM42.9193 37.1141H41.32C36.8966 36.6542 33.4362 32.905 33.4362 28.362C33.4362 23.819 36.8966 20.0698 41.32 19.6099H42.9193V17.6339C42.9193 16.3267 41.8559 15.2632 40.5486 15.2632H12.1003C11.6639 15.2632 11.3101 14.9094 11.3101 14.473C11.3101 14.0366 11.6639 13.6828 12.1003 13.6828H16.2042L15.0313 11.25C14.6492 10.7619 14.05 10.4724 13.4153 10.4724H4.61053C3.51152 10.4724 2.61757 11.3385 2.61757 12.4032V39.2426C2.61757 40.3997 3.58837 41.3408 4.78182 41.3408H8.95957C9.39597 41.3408 9.7498 41.6946 9.7498 42.1311C9.7498 42.5675 9.39597 42.9213 8.95957 42.9213H5.99363C6.27021 43.395 6.78367 43.7115 7.35895 43.7115H38.9684C39.5508 43.7115 40.055 43.3915 40.329 42.9213H13.0407C12.6043 42.9213 12.2505 42.5675 12.2505 42.1311C12.2505 41.6946 12.6043 41.3408 13.0407 41.3408H40.7551C41.9485 41.3408 42.9193 40.3997 42.9193 39.2426V37.1141ZM35.2695 28.362C35.2695 32.204 38.3949 35.3291 42.2366 35.3291C46.0784 35.3291 49.2038 32.204 49.2038 28.362C49.2038 24.52 46.0782 21.3948 42.2366 21.3948C38.3951 21.3948 35.2695 24.52 35.2695 28.362Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M27.1155 2.61952C27.5519 2.61952 27.9058 2.26572 27.9058 1.82929C27.9058 1.39286 27.5519 1.03906 27.1155 1.03906C26.6791 1.03906 26.3253 1.39286 26.3253 1.82929C26.3253 2.26572 26.6791 2.61952 27.1155 2.61952Z",
              stroke: color,
              strokeWidth: "0.5"
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M19.6759 9.26801C19.4738 9.26801 19.2715 9.19076 19.1172 9.03647C18.8084 8.72788 18.8084 8.22786 19.1172 7.91908L19.6759 7.36038L19.1172 6.80169C18.8086 6.4931 18.8086 5.99288 19.1172 5.6843C19.4258 5.37571 19.926 5.37571 20.2346 5.6843L20.7933 6.24299L21.352 5.6843C21.6605 5.37571 22.1608 5.37571 22.4693 5.6843C22.7779 5.99288 22.7779 6.4931 22.4693 6.80169L21.9107 7.36038L22.4693 7.91908C22.7779 8.22766 22.7779 8.72788 22.4693 9.03647C22.3151 9.19076 22.1128 9.26801 21.9107 9.26801C21.7086 9.26801 21.5063 9.19076 21.352 9.03647L20.7933 8.47777L20.2346 9.03647C20.0803 9.19076 19.878 9.26801 19.6759 9.26801Z",
              stroke: color,
              strokeWidth: "0.5"
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M30.2753 10.5214C30.2753 10.9579 29.9215 11.3117 29.485 11.3117C29.0486 11.3117 28.6948 10.9579 28.6948 10.5214C28.6948 10.085 29.0486 9.73121 29.485 9.73121C29.9215 9.73121 30.2753 10.085 30.2753 10.5214Z",
              stroke: color,
              strokeWidth: "0.5"
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              d: "M45.6485 25.1845C45.2905 24.8265 44.7103 24.8265 44.3523 25.1845L42.3198 27.217L40.2873 25.1845C39.9294 24.8265 39.3491 24.8265 38.9912 25.1845C38.6332 25.5424 38.6332 26.1227 38.9912 26.4807L41.0237 28.5131L38.9912 30.5456C38.6332 30.9036 38.6332 31.4838 38.9912 31.8418C39.1702 32.0208 39.4048 32.1104 39.6393 32.1104C39.8737 32.1104 40.1084 32.0208 40.2873 31.8418L42.3198 29.8093L44.3523 31.8418C44.5313 32.0208 44.766 32.1104 45.0004 32.1104C45.2348 32.1104 45.4695 32.0208 45.6485 31.8418C46.0064 31.4838 46.0064 30.9036 45.6485 30.5456L43.616 28.5131L45.6485 26.4807C46.0064 26.1227 46.0064 25.5424 45.6485 25.1845Z",
              stroke: color,
              strokeWidth: "0.5"
            }
          ),
          /* @__PURE__ */ jsx73(
            "path",
            {
              fillRule: "evenodd",
              clipRule: "evenodd",
              d: "M17.9589 13.6828H40.5486C42.7271 13.6828 44.4998 15.4554 44.4998 17.6339V19.6099H43.1533C47.5767 20.0698 51.0371 23.8187 51.0371 28.362C51.0371 32.4326 48.2593 35.8655 44.4998 36.8666V39.2426C44.4998 40.8131 43.4877 42.1471 42.0742 42.6735C41.8152 44.1578 40.5253 45.292 38.9682 45.292H7.35895C5.89703 45.292 4.63957 44.269 4.29958 42.8733C2.46427 42.6378 1.03711 41.1085 1.03711 39.2424V12.4032C1.03711 10.4671 2.63989 8.89198 4.61053 8.89198H13.4155C14.5753 8.89198 15.6716 9.44435 16.3476 10.3701C16.3759 10.4086 16.4006 10.4499 16.4213 10.4932L17.9589 13.6828ZM42.9193 37.1141H41.32C36.8966 36.6542 33.4362 32.905 33.4362 28.362C33.4362 23.819 36.8966 20.0698 41.32 19.6099H42.9193V17.6339C42.9193 16.3267 41.8559 15.2632 40.5486 15.2632H12.1003C11.6639 15.2632 11.3101 14.9094 11.3101 14.473C11.3101 14.0366 11.6639 13.6828 12.1003 13.6828H16.2042L15.0313 11.25C14.6492 10.7619 14.05 10.4724 13.4153 10.4724H4.61053C3.51152 10.4724 2.61757 11.3385 2.61757 12.4032V39.2426C2.61757 40.3997 3.58837 41.3408 4.78182 41.3408H8.95957C9.39597 41.3408 9.7498 41.6946 9.7498 42.1311C9.7498 42.5675 9.39597 42.9213 8.95957 42.9213H5.99363C6.27021 43.395 6.78367 43.7115 7.35895 43.7115H38.9684C39.5508 43.7115 40.055 43.3915 40.329 42.9213H13.0407C12.6043 42.9213 12.2505 42.5675 12.2505 42.1311C12.2505 41.6946 12.6043 41.3408 13.0407 41.3408H40.7551C41.9485 41.3408 42.9193 40.3997 42.9193 39.2426V37.1141ZM35.2695 28.362C35.2695 32.204 38.3949 35.3291 42.2366 35.3291C46.0784 35.3291 49.2038 32.204 49.2038 28.362C49.2038 24.52 46.0782 21.3948 42.2366 21.3948C38.3951 21.3948 35.2695 24.52 35.2695 28.362Z",
              stroke: color,
              strokeWidth: "0.5"
            }
          )
        ] })
      ] })
    }
  );
};
var EmptyFolderIcon_default = EmptyFolderIcon;

// src/icons/EmptyStateIcon/Variants/EmptyStateIconDefaultVariant.tsx
import { jsx as jsx74 } from "react/jsx-runtime";
var EmptyStateIconDefaultVariant = ({
  width = 50,
  height = 59,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "empty-state-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx74(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 18 22",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx74("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx74(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M11.9914 0.162583L11.9858 0.157345C11.8836 0.0555297 11.7426 -0.00143451 11.5954 0.000202394C11.5773 -0.000616057 11.559 0.00102084 11.5412 0.00560415H3.21148C1.7142 0.00593155 0.500678 1.17795 0.5 2.62399V19.3816C0.500678 20.8275 1.7142 21.9995 3.21148 22H15.2885C16.7858 21.9995 17.9995 20.8275 18 19.3816V6.17427C17.999 6.03562 17.9427 5.90254 17.8427 5.80269L11.9914 0.162583ZM12.1377 1.78606L16.1615 5.66126H13.7646C12.8672 5.65865 12.1402 4.95674 12.1377 4.09033V1.78606ZM15.2939 20.9475H3.21148C2.31409 20.945 1.5874 20.2429 1.58452 19.3764V2.61859C1.5874 1.75218 2.31409 1.05011 3.21148 1.04766H11.0532V4.09033C11.0537 5.53604 12.2674 6.70806 13.7646 6.70872H16.9155V19.3764C16.9172 20.2426 16.191 20.9463 15.2939 20.9475Z",
          fill: color
        }
      ) })
    }
  );
};
var EmptyStateIconDefaultVariant_default = EmptyStateIconDefaultVariant;

// src/icons/EmptyStateIcon/Variants/EmptyStateIconWithShadowVariant.tsx
import { jsx as jsx75, jsxs as jsxs40 } from "react/jsx-runtime";
var EmptyStateIconWithShadowVariant = ({
  width = 50,
  height = 59,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "empty-state-icon",
  // Extra properties
  variant = "default",
  backgroundColor = "white",
  backgroundOpacity = "1",
  shadowColor = "#DDE8EE",
  shadowOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx75(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 50 59",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs40("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsxs40("g", { "data-testid": "g-shadow-path", opacity: shadowOpacity, children: [
          /* @__PURE__ */ jsx75(
            "path",
            {
              "data-testid": "path-with-shadow-color",
              d: "M43.8147 56.4177H16.1977C14.1465 56.4123 12.4855 54.8565 12.4789 52.9363V15.8026C12.4855 13.8827 14.1465 12.327 16.1977 12.3215H34.1215V19.0638C34.1227 22.2674 36.8968 24.8645 40.3192 24.8659H47.5211V52.9363C47.525 54.8558 45.8651 56.4152 43.8147 56.4177Z",
              fill: shadowColor
            }
          ),
          /* @__PURE__ */ jsx75(
            "path",
            {
              d: "M36.6004 13.9578L45.7977 22.5448H40.3192C38.268 22.539 36.6062 20.9837 36.6004 19.0638V13.9578Z",
              fill: shadowColor
            }
          ),
          /* @__PURE__ */ jsx75(
            "path",
            {
              fillRule: "evenodd",
              clipRule: "evenodd",
              d: "M36.266 10.3603L36.2533 10.3487C36.0196 10.123 35.6973 9.99682 35.361 10.0004C35.3195 9.99863 35.2777 10.0023 35.237 10.0124H16.1977C12.7753 10.0131 10.0015 12.6102 10 15.8145V52.9479C10.0015 56.1518 12.7753 58.7489 16.1977 58.75H43.8023C47.2247 58.7489 49.9988 56.1518 50 52.9479V23.6816C49.9977 23.3744 49.869 23.0795 49.6404 22.8582L36.266 10.3603ZM45.7977 22.5448L36.6004 13.9578V19.0638C36.6062 20.9837 38.268 22.539 40.3192 22.5448H45.7977ZM16.1977 56.4177H43.8147C45.8651 56.4152 47.525 54.8558 47.5211 52.9363V24.8659H40.3192C36.8968 24.8645 34.1227 22.2674 34.1215 19.0638V12.3215H16.1977C14.1465 12.327 12.4855 13.8827 12.4789 15.8026V52.9363C12.4855 54.8565 14.1465 56.4123 16.1977 56.4177Z",
              fill: shadowColor
            }
          )
        ] }),
        /* @__PURE__ */ jsx75("g", { "data-testid": "g-border-path", opacity: borderOpacity, children: /* @__PURE__ */ jsx75(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M27.0869 0.378745L27.0737 0.366542C26.8327 0.129359 26.5003 -0.00334175 26.1535 0.000471487C26.1107 -0.00143513 26.0676 0.0023781 26.0256 0.0130551H6.39133C2.86204 0.0138178 0.00159823 2.7441 0 6.11271V45.1503C0.00159823 48.5186 2.86204 51.2489 6.39133 51.25H34.8587C38.388 51.2489 41.2488 48.5186 41.25 45.1503V14.3832C41.2476 14.0603 41.115 13.7502 40.8792 13.5176L27.0869 0.378745ZM27.4317 4.16071L36.9164 13.1882H31.2666C29.1514 13.1821 27.4377 11.547 27.4317 9.52861V4.16071ZM34.8715 48.7981H6.39133C4.27607 48.7924 2.56317 47.1569 2.55637 45.1381V6.10013C2.56317 4.08178 4.27607 2.44628 6.39133 2.44056H24.8753V9.52861C24.8765 12.8965 27.7373 15.6267 31.2666 15.6283H38.6936V45.1381C38.6976 47.1561 36.9859 48.7954 34.8715 48.7981Z",
            fill: color
          }
        ) }),
        /* @__PURE__ */ jsxs40("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: [
          /* @__PURE__ */ jsx75(
            "path",
            {
              "data-testid": "path-with-background-color",
              d: "M27.4317 4.16071L36.9164 13.1882H31.2666C29.1514 13.1821 27.4377 11.547 27.4317 9.52861V4.16071Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx75(
            "path",
            {
              d: "M34.8715 48.7981H6.39133C4.27607 48.7924 2.56317 47.1569 2.55637 45.1381V6.10013C2.56317 4.08178 4.27607 2.44628 6.39133 2.44056H24.8753V9.52861C24.8765 12.8965 27.7373 15.6267 31.2666 15.6283H38.6936V45.1381C38.6976 47.1561 36.9859 48.7954 34.8715 48.7981Z",
              fill: backgroundColor
            }
          )
        ] })
      ] })
    }
  );
};
var EmptyStateIconWithShadowVariant_default = EmptyStateIconWithShadowVariant;

// src/icons/EmptyStateIcon/EmptyStateIcon.tsx
import { jsx as jsx76 } from "react/jsx-runtime";
var EmptyStateIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx76(EmptyStateIconDefaultVariant_default, { variant: "default", ...props }),
    withShadow: /* @__PURE__ */ jsx76(EmptyStateIconWithShadowVariant_default, { variant: "withShadow", ...props })
  };
  return variants[variant];
};
var EmptyStateIcon_default = EmptyStateIcon;

// src/icons/ExpirationIcon/ExpirationIcon.tsx
import { jsx as jsx77, jsxs as jsxs41 } from "react/jsx-runtime";
var ExpirationIcon = ({
  width = 208,
  height = 149,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "expiration-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx77(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 208 149",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsxs41("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M92.4389 57.175H45.6475C42.4173 57.175 39.7986 54.6151 39.7986 51.4575V28.5875C39.7986 12.8244 52.9177 0 69.0432 0C85.1687 0 98.2879 12.8244 98.2879 28.5875V51.4575C98.2879 54.6151 95.6691 57.175 92.4389 57.175ZM51.4964 45.74H86.59V28.5875C86.59 19.1296 78.7185 11.435 69.0432 11.435C59.3679 11.435 51.4964 19.1296 51.4964 28.5875V45.74Z",
            fill: "#57606E"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M69.0432 0V11.435C78.7185 11.435 86.59 19.1296 86.59 28.5875V45.74H69.0432V57.175H92.4389C95.6691 57.175 98.2879 54.6151 98.2879 51.4575V28.5875C98.2879 12.8244 85.1687 0 69.0432 0Z",
            fill: "#2F3542"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M100.238 45.7383H37.8489C34.6188 45.7383 32 48.2982 32 51.4558V97.1958C32 100.353 34.6188 102.913 37.8489 102.913H100.238C103.468 102.913 106.086 100.353 106.086 97.1958V51.4558C106.086 48.2982 103.468 45.7383 100.238 45.7383Z",
            fill: "#FAD557"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M100.237 45.7383H69.043V102.913H100.237C103.467 102.913 106.086 100.353 106.086 97.1958V51.4558C106.086 48.2982 103.467 45.7383 100.237 45.7383Z",
            fill: "#FCB12B"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M69.0433 85.7606C65.8131 85.7606 63.1943 83.2007 63.1943 80.0431V68.6081C63.1943 65.4505 65.8131 62.8906 69.0433 62.8906C72.2734 62.8906 74.8922 65.4505 74.8922 68.6081V80.0431C74.8922 83.2007 72.2734 85.7606 69.0433 85.7606Z",
            fill: "#FDA429"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M69.043 62.8906V85.7606C72.2731 85.7606 74.8919 83.2007 74.8919 80.0431V68.6081C74.8919 65.4505 72.2731 62.8906 69.043 62.8906Z",
            fill: "#E28117"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M154.063 93.3054H4.64978C2.08186 93.3054 0 95.3873 0 97.9552V135.153C0 137.721 2.08186 139.803 4.64978 139.803H154.063C156.631 139.803 158.712 137.721 158.712 135.153V97.9552C158.712 95.3873 156.631 93.3054 154.063 93.3054Z",
            fill: "#F9F9F9"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M154.063 93.3054H79.3562V139.803H154.063C156.631 139.803 158.712 137.721 158.712 135.153V97.9552C158.712 95.3873 156.631 93.3054 154.063 93.3054Z",
            fill: "#DFE4EA"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M79.3562 122.754C82.7802 122.754 85.5559 119.978 85.5559 116.554C85.5559 113.13 82.7802 110.355 79.3562 110.355C75.9322 110.355 73.1565 113.13 73.1565 116.554C73.1565 119.978 75.9322 122.754 79.3562 122.754Z",
            fill: "#A4B0BE"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M54.5574 122.754C57.9814 122.754 60.7571 119.978 60.7571 116.554C60.7571 113.13 57.9814 110.355 54.5574 110.355C51.1334 110.355 48.3577 113.13 48.3577 116.554C48.3577 119.978 51.1334 122.754 54.5574 122.754Z",
            fill: "#A4B0BE"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M29.7585 122.754C33.1825 122.754 35.9582 119.978 35.9582 116.554C35.9582 113.13 33.1825 110.355 29.7585 110.355C26.3345 110.355 23.5588 113.13 23.5588 116.554C23.5588 119.978 26.3345 122.754 29.7585 122.754Z",
            fill: "#A4B0BE"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M79.3562 110.355V122.754C82.7803 122.754 85.5559 119.979 85.5559 116.554C85.5559 113.13 82.7803 110.355 79.3562 110.355Z",
            fill: "#747D8B"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M104.155 122.754C107.579 122.754 110.355 119.978 110.355 116.554C110.355 113.13 107.579 110.355 104.155 110.355C100.731 110.355 97.9553 113.13 97.9553 116.554C97.9553 119.978 100.731 122.754 104.155 122.754Z",
            fill: "#747D8B"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M128.954 122.754C132.378 122.754 135.154 119.978 135.154 116.554C135.154 113.13 132.378 110.355 128.954 110.355C125.53 110.355 122.754 113.13 122.754 116.554C122.754 119.978 125.53 122.754 128.954 122.754Z",
            fill: "#747D8B"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M176.072 148.793C193.705 148.793 208 134.359 208 116.554C208 98.7496 193.705 84.3159 176.072 84.3159C158.438 84.3159 144.143 98.7496 144.143 116.554C144.143 134.359 158.438 148.793 176.072 148.793Z",
            fill: "#EAB14D"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M176.071 84.3159C174.828 84.3159 173.603 84.3902 172.397 84.5299C188.299 86.3702 200.65 100.005 200.65 116.554C200.65 133.104 188.299 146.739 172.397 148.579C173.603 148.719 174.828 148.793 176.071 148.793C193.705 148.793 208 134.359 208 116.554C208 98.7497 193.705 84.3159 176.071 84.3159Z",
            fill: "#E49542"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M175.487 123.495C173.441 123.495 171.783 121.82 171.783 119.755V97.4269C171.783 95.3615 173.441 93.687 175.487 93.687C177.532 93.687 179.191 95.3615 179.191 97.4269V119.755C179.19 121.821 177.532 123.495 175.487 123.495Z",
            fill: "#F4FBFF"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M175.486 137.955C173.441 137.955 171.782 136.281 171.782 134.215V131.833C171.782 129.768 173.441 128.093 175.486 128.093C177.532 128.093 179.19 129.768 179.19 131.833V134.215C179.19 136.281 177.532 137.955 175.486 137.955Z",
            fill: "#F4FBFF"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M175.487 93.687C174.811 93.687 174.18 93.8726 173.635 94.1914C174.741 94.8383 175.487 96.0433 175.487 97.4269V119.755C175.487 121.139 174.741 122.344 173.635 122.991C174.18 123.309 174.811 123.495 175.487 123.495C177.532 123.495 179.19 121.821 179.19 119.755V97.4269C179.19 95.3615 177.532 93.687 175.487 93.687Z",
            fill: "#DAF1F4"
          }
        ),
        /* @__PURE__ */ jsx77(
          "path",
          {
            d: "M175.487 128.093C174.811 128.093 174.18 128.279 173.635 128.598C174.741 129.245 175.487 130.45 175.487 131.833V134.215C175.487 135.599 174.741 136.804 173.635 137.451C174.18 137.77 174.811 137.955 175.487 137.955C177.532 137.955 179.191 136.281 179.191 134.215V131.833C179.19 129.768 177.532 128.093 175.487 128.093Z",
            fill: "#DAF1F4"
          }
        )
      ] })
    }
  );
};
var ExpirationIcon_default = ExpirationIcon;

// src/icons/ExpiredIcon/ExpiredIcon.tsx
import { jsx as jsx78, jsxs as jsxs42 } from "react/jsx-runtime";
var NoResultsIcon = ({
  width = 190,
  height = 215,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "expired-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx78(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 190 215",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs42("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M185.579 2.74463H95.681V24.7009H185.579V2.74463Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M95.681 2.74463H4.42065V24.7009H95.681V2.74463Z",
            fill: "#26ACD8"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M95.681 26.073H4.42065V142.715H95.681V26.073Z",
            fill: "#F6F8FA"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M186.941 24.7007H95.681V141.343H186.941V24.7007Z",
            fill: "#EAEBEC"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M108.05 138.817H5.53342V28.8095H184.465V139.987C184.465 141.443 185.629 142.622 187.065 142.622C188.501 142.622 189.665 141.443 189.665 139.987V2.63555C189.665 1.17992 188.501 0 187.065 0H2.93463C1.49848 0 0.334351 1.17992 0.334351 2.63555V141.452C0.334351 142.907 1.49848 144.088 2.93463 144.088L103.205 144.085H108.05C109.486 144.085 110.65 142.906 110.65 141.45C110.649 139.996 109.485 138.817 108.05 138.817ZM184.47 5.27036V23.537L5.53761 23.539V5.27219L184.47 5.27036Z",
            fill: "#CFCFCD"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M18.7227 17.8393C20.6002 17.8393 22.1279 16.301 22.1279 14.4087C22.1279 12.5172 20.6002 10.978 18.7227 10.978C16.8452 10.978 15.3174 12.5172 15.3174 14.4087C15.3174 16.3001 16.8452 17.8393 18.7227 17.8393Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M32.3437 17.8393C34.2213 17.8393 35.7488 16.301 35.7488 14.4087C35.7488 12.5172 34.2211 10.978 32.3437 10.978C30.4662 10.978 28.9384 12.5172 28.9384 14.4087C28.9384 16.3001 30.4662 17.8393 32.3437 17.8393Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M45.9646 17.8393C47.8422 17.8393 49.3699 16.301 49.3699 14.4087C49.3699 12.5172 47.8421 10.978 45.9646 10.978C44.0871 10.978 42.5594 12.5172 42.5594 14.4087C42.5594 16.3001 44.0871 17.8393 45.9646 17.8393Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M52.4892 64.7155C51.7301 63.9496 50.4992 63.9496 49.74 64.7155L42.1998 72.322L34.8264 64.8837C34.0672 64.1179 32.8363 64.1179 32.0772 64.8837C31.318 65.6496 31.318 66.8913 32.0772 67.6572L39.4505 75.0954L31.8699 82.7427C31.1107 83.5086 31.1107 84.7503 31.8699 85.5161C32.6291 86.282 33.86 86.282 34.6192 85.5161L42.1998 77.8689L49.9472 85.6844C50.7064 86.4502 51.9372 86.4502 52.6964 85.6844C53.4557 84.9185 53.4557 83.6768 52.6964 82.911L44.9491 75.0954L52.4892 67.4889C53.2484 66.7231 53.2484 65.4813 52.4892 64.7155Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M42.1998 77.8688V72.322L34.8264 64.8837C34.0672 64.1179 32.8363 64.1179 32.0772 64.8837C31.318 65.6496 31.318 66.8913 32.0772 67.6572L39.4505 75.0954L31.8699 82.7427C31.1107 83.5086 31.1107 84.7502 31.8699 85.5161C32.6291 86.282 33.86 86.282 34.6192 85.5161L42.1998 77.8688Z",
            fill: "#41B5DB"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M86.5355 64.7155C85.7763 63.9496 84.5454 63.9496 83.7862 64.7155L76.2461 72.322L68.8727 64.8837C68.1135 64.1179 66.8826 64.1179 66.1235 64.8837C65.3643 65.6496 65.3643 66.8913 66.1235 67.6572L73.4968 75.0954L65.9162 82.7427C65.157 83.5086 65.157 84.7503 65.9162 85.5161C66.6753 86.282 67.9062 86.282 68.6654 85.5161L76.2461 77.8689L83.9935 85.6844C84.7527 86.4502 85.9835 86.4502 86.7427 85.6844C87.5019 84.9185 87.5019 83.6768 86.7427 82.911L78.9953 75.0954L86.5355 67.4889C87.2947 66.7231 87.2947 65.4813 86.5355 64.7155Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M76.2461 77.8688V72.322L68.8727 64.8837C68.1135 64.1179 66.8826 64.1179 66.1235 64.8837C65.3643 65.6496 65.3643 66.8913 66.1235 67.6572L73.4968 75.0954L65.9162 82.7427C65.157 83.5086 65.157 84.7502 65.9162 85.5161C66.6753 86.282 67.9062 86.282 68.6654 85.5161L76.2461 77.8688Z",
            fill: "#41B5DB"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M121.68 64.7155C120.921 63.9496 119.69 63.9496 118.931 64.7155L111.391 72.322L104.017 64.8837C103.258 64.1179 102.027 64.1179 101.268 64.8837C100.509 65.6496 100.509 66.8913 101.268 67.6572L108.641 75.0954L101.061 82.7427C100.302 83.5086 100.302 84.7503 101.061 85.5161C101.82 86.282 103.051 86.282 103.81 85.5161L111.391 77.8689L119.138 85.6844C119.897 86.4502 121.128 86.4502 121.887 85.6844C122.646 84.9185 122.646 83.6768 121.887 82.911L114.14 75.0954L121.68 67.4889C122.439 66.7231 122.439 65.4813 121.68 64.7155Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M111.391 77.8688V72.322L104.017 64.8837C103.258 64.1179 102.027 64.1179 101.268 64.8837C100.509 65.6496 100.509 66.8913 101.268 67.6572L108.641 75.0954L101.061 82.7427C100.302 83.5086 100.302 84.7502 101.061 85.5161C101.82 86.282 103.051 86.282 103.81 85.5161L111.391 77.8688Z",
            fill: "#41B5DB"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M156.825 64.7155C156.065 63.9496 154.835 63.9496 154.075 64.7155L146.535 72.322L139.162 64.8837C138.403 64.1179 137.172 64.1179 136.413 64.8837C135.653 65.6496 135.653 66.8913 136.413 67.6572L143.786 75.0954L136.205 82.7427C135.446 83.5086 135.446 84.7503 136.205 85.5161C136.964 86.282 138.195 86.282 138.954 85.5161L146.535 77.8689L154.283 85.6844C155.042 86.4502 156.273 86.4502 157.032 85.6844C157.791 84.9185 157.791 83.6768 157.032 82.911L149.284 75.0954L156.825 67.4889C157.584 66.7231 157.584 65.4813 156.825 64.7155Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M146.535 77.8688V72.322L139.162 64.8837C138.403 64.1179 137.172 64.1179 136.413 64.8837C135.653 65.6496 135.653 66.8913 136.413 67.6572L143.786 75.0954L136.205 82.7427C135.446 83.5086 135.446 84.7502 136.205 85.5161C136.964 86.282 138.195 86.282 138.954 85.5161L146.535 77.8688Z",
            fill: "#41B5DB"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M159.699 141.343C159.699 139.828 160.919 138.598 162.424 138.598H189.665V141.343C189.665 142.858 188.446 144.088 186.942 144.088H162.424C160.919 144.088 159.699 142.858 159.699 141.343Z",
            fill: "#CFCFCD"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M162.995 155.238L142.518 121.728C141.351 119.731 139.18 118.497 136.845 118.497C134.511 118.497 132.34 119.731 131.2 121.677L110.757 155.14C110.067 156.175 109.702 157.41 109.702 158.718C109.702 162.386 112.68 165.375 116.341 165.375H157.35C161.01 165.375 163.988 162.386 163.988 158.718C163.988 157.41 163.624 156.175 162.995 155.238V155.238Z",
            fill: "#FFC107"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M136.845 149.526C135.24 149.526 133.937 148.214 133.937 146.596V133.549C133.937 131.931 135.24 130.619 136.845 130.619C138.451 130.619 139.753 131.931 139.753 133.549V146.6C139.753 148.214 138.451 149.526 136.845 149.526Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M139.753 154.987C139.753 156.605 138.451 157.917 136.845 157.917C135.24 157.917 133.937 156.605 133.937 154.987C133.937 153.37 135.24 152.058 136.845 152.058C138.451 152.058 139.753 153.37 139.753 154.987Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M136.845 118.497C134.511 118.497 132.34 119.731 131.2 121.677L110.757 155.14C110.067 156.175 109.702 157.41 109.702 158.718C109.702 162.386 112.68 165.375 116.341 165.375H136.845V157.917C135.24 157.917 133.937 156.605 133.937 154.988C133.937 153.37 135.24 152.058 136.845 152.058V149.526C135.24 149.526 133.937 148.214 133.937 146.596V133.549C133.937 131.931 135.24 130.619 136.845 130.619V118.497Z",
            fill: "#DEA806"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M136.845 130.619C135.24 130.619 133.937 131.931 133.937 133.549V146.6C133.937 148.218 135.24 149.53 136.845 149.53V130.619Z",
            fill: "#DEDEDE"
          }
        ),
        /* @__PURE__ */ jsx78(
          "path",
          {
            d: "M136.845 152.058C135.24 152.058 133.937 153.37 133.937 154.987C133.937 156.605 135.24 157.917 136.845 157.917V152.058Z",
            fill: "#DEDEDE"
          }
        ),
        /* @__PURE__ */ jsx78(
          "rect",
          {
            x: "30.2023",
            y: "91.7882",
            width: "24.1618",
            height: "3.31765",
            rx: "1.65882",
            fill: "#EAEBEC"
          }
        ),
        /* @__PURE__ */ jsx78(
          "rect",
          {
            x: "64.4644",
            y: "92.2909",
            width: "24.1618",
            height: "3.31765",
            rx: "1.65882",
            fill: "#EAEBEC"
          }
        ),
        /* @__PURE__ */ jsx78(
          "rect",
          {
            x: "99.5238",
            y: "92.2909",
            width: "24.1618",
            height: "3.31765",
            rx: "1.65882",
            fill: "#CFCFCD"
          }
        ),
        /* @__PURE__ */ jsx78(
          "rect",
          {
            x: "134.583",
            y: "92.2909",
            width: "24.1618",
            height: "3.31765",
            rx: "1.65882",
            fill: "#CFCFCD"
          }
        ),
        /* @__PURE__ */ jsx78("circle", { cx: "57.5", cy: "206.5", r: "8.5", fill: "#1A91CF" }),
        /* @__PURE__ */ jsx78("circle", { cx: "84.5", cy: "206.5", r: "8.5", fill: "#1A91CF" }),
        /* @__PURE__ */ jsx78("circle", { cx: "111.5", cy: "206.5", r: "8.5", fill: "#1A91CF" }),
        /* @__PURE__ */ jsx78("circle", { cx: "135.5", cy: "206.5", r: "5.5", fill: "#1A91CF" })
      ] })
    }
  );
};
var ExpiredIcon_default = NoResultsIcon;

// src/icons/EyeIcon/EyeIcon.tsx
import { jsx as jsx79, jsxs as jsxs43 } from "react/jsx-runtime";
var EyeIcon = ({
  width = 51,
  height = 30,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "eye-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx79(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 51 30",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs43("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx79(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M25.7541 2.5C15.6374 2.5 8.05563 8.18475 4.0357 12.7354C2.87742 14.0465 2.87742 15.9535 4.0357 17.2646C8.05563 21.8153 15.6374 27.5 25.7541 27.5C35.8707 27.5 43.4525 21.8153 47.4724 17.2646C48.6307 15.9535 48.6307 14.0465 47.4724 12.7354C43.4525 8.18475 35.8707 2.5 25.7541 2.5ZM2.16205 11.0803C6.4621 6.2125 14.6716 0 25.7541 0C36.8365 0 45.046 6.2125 49.3461 11.0803C51.3395 13.3369 51.3395 16.6631 49.3461 18.9198C45.046 23.7875 36.8365 30 25.7541 30C14.6716 30 6.4621 23.7875 2.16207 18.9198C0.168642 16.6631 0.16863 13.3369 2.16205 11.0803Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx79(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M30.7539 15C30.7539 17.7614 28.5154 20 25.7539 20C22.9925 20 20.7539 17.7614 20.7539 15C20.7539 12.2386 22.9925 10 25.7539 10C26.2104 10 26.6527 10.0611 27.0728 10.1758C27.3337 9.30188 27.9045 8.56162 28.6578 8.08287C27.7647 7.7075 26.7835 7.5 25.7539 7.5C21.6118 7.5 18.2539 10.8579 18.2539 15C18.2539 19.1421 21.6118 22.5 25.7539 22.5C29.896 22.5 33.2539 19.1421 33.2539 15C33.2539 14.669 33.2325 14.3429 33.1909 14.0232C32.5447 14.6119 31.6918 14.9786 30.7539 15C30.7539 14.9996 30.7539 15.0004 30.7539 15Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx79(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M25.667 2.5C18.7635 2.5 13.167 8.0965 13.167 15C13.167 21.9035 18.7635 27.5 25.667 27.5C32.5705 27.5 38.167 21.9035 38.167 15C38.167 8.0965 32.5705 2.5 25.667 2.5ZM10.667 15C10.667 6.71575 17.3827 0 25.667 0C33.9512 0 40.667 6.71575 40.667 15C40.667 23.2843 33.9512 30 25.667 30C17.3827 30 10.667 23.2843 10.667 15Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx79(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M30.667 12.5C31.3574 12.5 31.917 11.9404 31.917 11.25C31.917 10.5596 31.3574 10 30.667 10C29.9766 10 29.417 10.5596 29.417 11.25C29.417 11.9404 29.9766 12.5 30.667 12.5ZM30.667 15C32.7381 15 34.417 13.3211 34.417 11.25C34.417 9.17887 32.7381 7.5 30.667 7.5C28.5959 7.5 26.917 9.17887 26.917 11.25C26.917 13.3211 28.5959 15 30.667 15Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var EyeIcon_default = EyeIcon;

// src/icons/FilterIcon/Variants/FilterIconDefaultVariant.tsx
import { jsx as jsx80 } from "react/jsx-runtime";
var FilterIconDefaultVariant = ({
  width = 23,
  height = 23,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "white",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "filter-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx80(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 13 14",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx80("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx80(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M12.7384 0.82276C12.7384 0.399101 12.4262 0.0556641 12.0411 0.0556641H0.708585C0.564981 0.0556641 0.424842 0.104459 0.30739 0.195326C-0.00763693 0.43905 -0.083378 0.917582 0.13819 1.26411L4.24707 7.68991L4.25861 12.3647C4.25933 12.6565 4.33821 12.9416 4.48534 13.1845C4.89892 13.8669 5.73714 14.0513 6.35755 13.5964L7.8251 12.5202C8.21502 12.2342 8.44836 11.752 8.44617 11.2365L8.43126 7.71817L12.609 1.26789C12.6932 1.1379 12.7384 0.982307 12.7384 0.82276Z",
          fill: color
        }
      ) })
    }
  );
};
var FilterIconDefaultVariant_default = FilterIconDefaultVariant;

// src/icons/FilterIcon/Variants/FilterIconOutlinedVariant.tsx
import { jsx as jsx81 } from "react/jsx-runtime";
var FilterIconOutlinedVariant = ({
  width = 23,
  height = 23,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "white",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "filter-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx81(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      "data-testid": dataTestId,
      viewBox: "0 0 23 23",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx81("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx81(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M22.4429 1.91433C22.4429 1.24858 21.9032 0.708893 21.2374 0.708893H1.6484C1.40017 0.708893 1.15793 0.785571 0.954909 0.928361C0.410363 1.31136 0.279439 2.06334 0.662435 2.60788L7.76492 12.7056L7.78487 20.0517C7.78611 20.5103 7.92246 20.9583 8.17679 21.3399C8.8917 22.4123 10.3406 22.7021 11.413 21.9871L13.9498 20.296C14.6238 19.8467 15.0272 19.0888 15.0234 18.2788L14.9976 12.75L22.2192 2.61381C22.3647 2.40955 22.4429 2.16505 22.4429 1.91433ZM12.5832 11.9839L12.6125 18.2899L10.1955 19.9013L10.1738 11.9396L3.97011 3.11977H18.8985L12.5832 11.9839Z",
          fill: color
        }
      ) })
    }
  );
};
var FilterIconOutlinedVariant_default = FilterIconOutlinedVariant;

// src/icons/FilterIcon/FilterIcon.tsx
import { jsx as jsx82 } from "react/jsx-runtime";
var FilterIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx82(FilterIconDefaultVariant_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx82(FilterIconOutlinedVariant_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var FilterIcon_default = FilterIcon;

// src/icons/FilterPlusIcon/FilterPlusIcon.tsx
import { jsx as jsx83, jsxs as jsxs44 } from "react/jsx-runtime";
var FilterPlusIcon = ({
  width = 14,
  height = 14,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#005980",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "filter-plus-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx83(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 14 14",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs44("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx83(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M13.4657 0.822638C13.4657 0.398979 13.1535 0.055542 12.7683 0.055542H1.43583C1.29223 0.055542 1.15209 0.104337 1.03464 0.195204C0.719612 0.438928 0.643871 0.91746 0.865439 1.26399L4.97432 7.68979L4.98586 12.3646C4.98658 12.6564 5.06545 12.9415 5.21259 13.1843C5.62617 13.8668 6.46439 14.0512 7.0848 13.5962L8.55235 12.5201C8.94227 12.2341 9.17561 11.7518 9.17342 11.2364L9.16232 8.61774H7.76761C7.76761 8.61774 7.48013 8.64496 7.29868 8.61774C5.96839 8.41815 5.82199 4.83598 7.29868 4.83598H8.96162V3.7329C8.96162 2.2346 10.8654 2.4386 10.8654 2.4386C11.1712 2.37722 11.784 2.62504 12.1342 3.1237L13.3363 1.26776C13.4205 1.13778 13.4657 0.982185 13.4657 0.822638Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx83(
          "path",
          {
            d: "M13.4657 6.66413C13.4657 6.37125 13.2499 6.13382 12.9836 6.13382L11.0623 6.13383V4.08476C11.0623 3.79188 10.8465 3.55445 10.5802 3.55445C10.314 3.55445 10.0981 3.79188 10.0981 4.08476V6.13382H8.16266C7.89641 6.13382 7.68057 6.37125 7.68057 6.66413C7.68057 6.95701 7.89641 7.19443 8.16266 7.19443L10.0981 7.19444V9.38779C10.0981 9.68066 10.314 9.91809 10.5802 9.91809C10.8465 9.91809 11.0623 9.68066 11.0623 9.38779L11.0623 7.19444L12.9836 7.19443C13.2499 7.19443 13.4657 6.95701 13.4657 6.66413Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var FilterPlusIcon_default = FilterPlusIcon;

// src/icons/FinancialBenchmarkingIcon/Variants/FinancialBenchmarkingIconDefault.tsx
import { jsx as jsx84, jsxs as jsxs45 } from "react/jsx-runtime";
var FinancialBenchmarkingIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "financial-benchmarking-icon",
  // Extra Properties
  variant,
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx84(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs45("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx84("g", { opacity: backgroundOpacity, "data-testid": "g-background-path", children: /* @__PURE__ */ jsx84(
          "path",
          {
            "data-testid": "path-with-background-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M15.6875 53.5C7.40323 53.5 0.6875 60.2157 0.6875 68.5C0.6875 76.7843 7.40323 83.5 15.6875 83.5C19.6886 83.5 23.3238 81.9335 26.0132 79.3803L45.8503 88.2823C45.7431 89.0059 45.6875 89.7465 45.6875 90.5C45.6875 98.7843 52.4032 105.5 60.6875 105.5C66.0132 105.5 70.6906 102.725 73.3523 98.5412L90.7026 103.822C90.6926 104.047 90.6875 104.273 90.6875 104.5C90.6875 112.784 97.4032 119.5 105.688 119.5C113.972 119.5 120.688 112.784 120.688 104.5C120.688 96.2157 113.972 89.5 105.688 89.5C101.235 89.5 97.2353 91.4401 94.4882 94.521L75.5887 88.7689C74.7304 81.3 68.3863 75.5 60.6875 75.5C56.7838 75.5 53.2285 76.9912 50.5599 79.435L30.5606 70.4602C30.6443 69.8187 30.6875 69.1644 30.6875 68.5C30.6875 60.2157 23.9718 53.5 15.6875 53.5Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx84("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx84(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M105.688 7.5C97.4032 7.5 90.6875 14.2157 90.6875 22.5C90.6875 23.4624 90.7781 24.4036 90.9513 25.3155L69.9355 38.6892C67.3874 36.6913 64.1766 35.5 60.6875 35.5C56.1424 35.5 52.0694 37.5215 49.3186 40.7145L30.6574 33.4574C30.6774 33.1408 30.6875 32.8216 30.6875 32.5C30.6875 24.2157 23.9718 17.5 15.6875 17.5C7.40323 17.5 0.6875 24.2157 0.6875 32.5C0.6875 40.7843 7.40323 47.5 15.6875 47.5C20.0535 47.5 23.9837 45.6347 26.725 42.6576L45.6946 50.0347C45.6899 50.1892 45.6875 50.3443 45.6875 50.5C45.6875 58.7843 52.4032 65.5 60.6875 65.5C68.9718 65.5 75.6875 58.7843 75.6875 50.5C75.6875 49.3394 75.5557 48.2095 75.3062 47.1246L96.0022 33.9544C98.6154 36.1663 101.996 37.5 105.688 37.5C113.972 37.5 120.688 30.7843 120.688 22.5C120.688 14.2157 113.972 7.5 105.688 7.5Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var FinancialBenchmarkingIconDefault_default = FinancialBenchmarkingIconDefaultVariant;

// src/icons/FinancialBenchmarkingIcon/Variants/FinancialBenchmarkingIconOutlined.tsx
import { jsx as jsx85, jsxs as jsxs46 } from "react/jsx-runtime";
var FinancialBenchmarkingIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "financial-benchmarking-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx85(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs46("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx85(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M105.812 7.5C97.5282 7.5 90.8125 14.2157 90.8125 22.5C90.8125 23.4625 90.9032 24.4038 91.0764 25.3159L70.0608 38.6894C67.5126 36.6913 64.3017 35.5 60.8125 35.5C56.2672 35.5 52.1941 37.5217 49.4433 40.7148L30.7824 33.4578C30.8024 33.1411 30.8125 32.8217 30.8125 32.5C30.8125 24.2157 24.0968 17.5 15.8125 17.5C7.52823 17.5 0.8125 24.2157 0.8125 32.5C0.8125 40.7843 7.52823 47.5 15.8125 47.5C20.1783 47.5 24.1084 45.6349 26.8496 42.658L45.8196 50.0352C45.8149 50.1895 45.8125 50.3445 45.8125 50.5C45.8125 58.7843 52.5282 65.5 60.8125 65.5C69.0968 65.5 75.8125 58.7843 75.8125 50.5C75.8125 49.3395 75.6807 48.2097 75.4313 47.1249L96.1274 33.9546C98.7406 36.1664 102.121 37.5 105.812 37.5C114.097 37.5 120.812 30.7843 120.812 22.5C120.812 14.2157 114.097 7.5 105.812 7.5ZM100.812 22.5C100.812 19.7386 103.051 17.5 105.812 17.5C108.574 17.5 110.812 19.7386 110.812 22.5C110.812 25.2614 108.574 27.5 105.812 27.5C103.051 27.5 100.812 25.2614 100.812 22.5ZM15.8125 27.5C13.0511 27.5 10.8125 29.7386 10.8125 32.5C10.8125 35.2614 13.0511 37.5 15.8125 37.5C18.5739 37.5 20.8125 35.2614 20.8125 32.5C20.8125 29.7386 18.5739 27.5 15.8125 27.5ZM55.8125 50.5C55.8125 47.7386 58.0511 45.5 60.8125 45.5C63.5739 45.5 65.8125 47.7386 65.8125 50.5C65.8125 53.2614 63.5739 55.5 60.8125 55.5C58.0511 55.5 55.8125 53.2614 55.8125 50.5Z",
            "data-testid": "path-with-color",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx85(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M0.8125 68.5C0.8125 60.2157 7.52823 53.5 15.8125 53.5C24.0968 53.5 30.8125 60.2157 30.8125 68.5C30.8125 69.1645 30.7693 69.819 30.6855 70.4607L50.6846 79.4353C53.3532 76.9913 56.9087 75.5 60.8125 75.5C68.5115 75.5 74.8557 81.3003 75.7137 88.7694L94.6128 94.5214C97.36 91.4402 101.36 89.5 105.812 89.5C114.097 89.5 120.812 96.2157 120.812 104.5C120.812 112.784 114.097 119.5 105.812 119.5C97.5282 119.5 90.8125 112.784 90.8125 104.5C90.8125 104.273 90.8176 104.047 90.8275 103.822L73.477 98.5416C70.8153 102.725 66.138 105.5 60.8125 105.5C52.5282 105.5 45.8125 98.7843 45.8125 90.5C45.8125 89.7466 45.868 89.0063 45.9752 88.2827L26.1378 79.3806C23.4484 81.9336 19.8134 83.5 15.8125 83.5C7.52823 83.5 0.8125 76.7843 0.8125 68.5ZM15.8125 63.5C13.0511 63.5 10.8125 65.7386 10.8125 68.5C10.8125 71.2614 13.0511 73.5 15.8125 73.5C18.5739 73.5 20.8125 71.2614 20.8125 68.5C20.8125 65.7386 18.5739 63.5 15.8125 63.5ZM60.8125 85.5C58.0511 85.5 55.8125 87.7386 55.8125 90.5C55.8125 93.2614 58.0511 95.5 60.8125 95.5C63.5739 95.5 65.8125 93.2614 65.8125 90.5C65.8125 87.7386 63.5739 85.5 60.8125 85.5ZM100.812 104.5C100.812 101.739 103.051 99.5 105.812 99.5C108.574 99.5 110.812 101.739 110.812 104.5C110.812 107.261 108.574 109.5 105.812 109.5C103.051 109.5 100.812 107.261 100.812 104.5Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var FinancialBenchmarkingIconOutlined_default = FinancialBenchmarkingIconOutlinedVariant;

// src/icons/FinancialBenchmarkingIcon/FinancialBenchmarkingIcon.tsx
import { jsx as jsx86 } from "react/jsx-runtime";
var FinancialBenchmarkingIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx86(FinancialBenchmarkingIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx86(FinancialBenchmarkingIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var FinancialBenchmarkingIcon_default = FinancialBenchmarkingIcon;

// src/icons/HospitalSignIcon/HospitalSignIcon.tsx
import { jsx as jsx87, jsxs as jsxs47 } from "react/jsx-runtime";
var HospitalSignIcon = ({
  width = 46,
  height = 46,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#DDE8EE",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "hospital-sign-icon",
  backgroundColor = "#DDE8EE"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx87(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 35 34",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs47("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx87(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M2.5 0C1.83696 0 1.20107 0.263392 0.732233 0.732233C0.263392 1.20107 0 1.83696 0 2.5L0 32.5C0 33.163 0.263392 33.7989 0.732233 34.2678C1.20107 34.7366 1.83696 35 2.5 35H32.5C33.163 35 33.7989 34.7366 34.2678 34.2678C34.7366 33.7989 35 33.163 35 32.5V2.5C35 1.83696 34.7366 1.20107 34.2678 0.732233C33.7989 0.263392 33.163 0 32.5 0L2.5 0Z",
            fill: backgroundColor
          }
        ),
        /* @__PURE__ */ jsx87(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M12.5 9.6875C13.3625 9.6875 14.0625 10.3875 14.0625 11.25V15.9375H20.9375V11.25C20.9375 10.8356 21.1021 10.4382 21.3951 10.1451C21.6882 9.85212 22.0856 9.6875 22.5 9.6875C22.9144 9.6875 23.3118 9.85212 23.6049 10.1451C23.8979 10.4382 24.0625 10.8356 24.0625 11.25V23.75C24.0625 24.1644 23.8979 24.5618 23.6049 24.8549C23.3118 25.1479 22.9144 25.3125 22.5 25.3125C22.0856 25.3125 21.6882 25.1479 21.3951 24.8549C21.1021 24.5618 20.9375 24.1644 20.9375 23.75V19.0625H14.0625V23.75C14.0625 24.1644 13.8979 24.5618 13.6049 24.8549C13.3118 25.1479 12.9144 25.3125 12.5 25.3125C12.0856 25.3125 11.6882 25.1479 11.3951 24.8549C11.1021 24.5618 10.9375 24.1644 10.9375 23.75V11.25C10.9375 10.3875 11.6375 9.6875 12.5 9.6875Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        )
      ] })
    }
  );
};
var HospitalSignIcon_default = HospitalSignIcon;

// src/icons/GzipFileIcon/GzipFileIcon.tsx
import { jsx as jsx88, jsxs as jsxs48 } from "react/jsx-runtime";
var GzipFileIcon = ({
  width = 45,
  height = 48,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "gzip-file-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx88(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 45 48",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsxs48("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx88(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M15.2904 37H21.327C21.8827 37 22.3331 36.5523 22.3331 36C22.3331 35.4477 21.8827 35 21.327 35H17.1704L22.1641 27.5547C22.6097 26.8906 22.1287 26 21.327 26H15.2904C14.7348 26 14.2843 26.4477 14.2843 27C14.2843 27.5523 14.7348 28 15.2904 28H19.447L14.4534 35.4453C14.0078 36.1094 14.4888 37 15.2904 37Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M28.3697 27C28.3697 26.4477 28.8202 26 29.3758 26H32.8971C34.8388 26 36.4185 27.5701 36.4185 29.5C36.4185 31.4299 34.8388 33 32.8971 33H30.3819V36C30.3819 36.5523 29.9314 37 29.3758 37C28.8202 37 28.3697 36.5523 28.3697 36V27ZM30.3819 31H32.8971C33.7294 31 34.4063 30.3271 34.4063 29.5C34.4063 28.6729 33.7294 28 32.8971 28H30.3819V31Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            d: "M8.7508 37C5.69961 37 3.21727 34.5327 3.21727 31.5C3.21727 28.4673 5.69961 26 8.7508 26C9.85107 26 10.9144 26.3205 11.8257 26.9268C12.2875 27.234 12.4112 27.8551 12.1022 28.314C11.7931 28.773 11.1684 28.896 10.7065 28.5888C10.1274 28.2036 9.45125 28 8.7508 28C6.80914 28 5.22946 29.5701 5.22946 31.5C5.22946 33.4299 6.80914 35 8.7508 35C10.343 35 11.6917 33.9442 12.1258 32.5H9.25385C8.69829 32.5 8.24776 32.0523 8.24776 31.5C8.24776 30.9477 8.69829 30.5 9.25385 30.5H13.2782C13.8339 30.5 14.2843 30.9477 14.2843 31.5C14.2843 34.5327 11.802 37 8.7508 37Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            d: "M24.3453 36C24.3453 36.5523 24.7958 37 25.3514 37C25.907 37 26.3575 36.5523 26.3575 36V27C26.3575 26.4477 25.907 26 25.3514 26C24.7958 26 24.3453 26.4477 24.3453 27V36Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            d: "M23.3392 8C23.3392 8.5523 22.8887 9 22.3331 9H14.2843C13.7287 9 13.2782 8.5523 13.2782 8C13.2782 7.44771 13.7287 7 14.2843 7H22.3331C22.8887 7 23.3392 7.44771 23.3392 8Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            d: "M30.3819 13C30.9375 13 31.388 12.5523 31.388 12C31.388 11.4477 30.9375 11 30.3819 11H14.2843C13.7287 11 13.2782 11.4477 13.2782 12C13.2782 12.5523 13.7287 13 14.2843 13H30.3819Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            d: "M14.2843 17C13.7287 17 13.2782 16.5523 13.2782 16C13.2782 15.4477 13.7287 15 14.2843 15H30.3819C30.9375 15 31.388 15.4477 31.388 16C31.388 16.5523 30.9375 17 30.3819 17H14.2843Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx88(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M40.4429 48C42.6619 48 44.4673 46.2056 44.4673 44V9C44.4673 8.73481 44.3612 8.48042 44.1726 8.29292L36.1238 0.29292C35.9351 0.10542 35.6792 0 35.4124 0H12.2721C10.0531 0 8.24776 1.79438 8.24776 4V16H4.22336C2.00432 16 0.198975 17.7944 0.198975 20V35.5667C0.198975 38.5626 2.65124 41 5.66541 41H8.24776V44C8.24776 46.2056 10.0531 48 12.2721 48H40.4429ZM2.21117 23.4632V35.5667C2.21117 37.4598 3.76066 39 5.66541 39H35.9154C36.7477 39 37.4246 38.3271 37.4246 37.5V25.5C37.4246 24.6729 36.7477 24 35.9154 24H4.22336C3.49087 24 2.80346 23.8045 2.21117 23.4632ZM8.24776 18H4.22336C3.11384 18 2.21117 18.8972 2.21117 20C2.21117 21.1028 3.11384 22 4.22336 22H8.24776V18ZM10.26 22H35.9154C37.8571 22 39.4368 23.5701 39.4368 25.5V37.5C39.4368 39.4299 37.8571 41 35.9154 41H10.26V44C10.26 45.1028 11.1626 46 12.2721 46H40.4429C41.5524 46 42.4551 45.1028 42.4551 44V10H35.4124C34.8568 10 34.4063 9.5523 34.4063 9V2H12.2721C11.1626 2 10.26 2.89722 10.26 4V22ZM36.4185 8V3.41431L41.0322 8H36.4185Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var GzipFileIcon_default = GzipFileIcon;

// src/icons/InfoIcon/Variants/InfoIconDefaultVariant.tsx
import { jsx as jsx89 } from "react/jsx-runtime";
var InfoIconDefaultVariant = ({
  width = 14,
  height = 14,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "info-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx89(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx89("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx89(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M8 15.5C3.85775 15.5 0.5 12.1423 0.5 8C0.5 3.85775 3.85775 0.5 8 0.5C12.1423 0.5 15.5 3.85775 15.5 8C15.5 12.1423 12.1423 15.5 8 15.5ZM7.25 7.25V11.75H8.75V7.25H7.25ZM7.25 4.25V5.75H8.75V4.25H7.25Z",
          fill: color
        }
      ) })
    }
  );
};
var InfoIconDefaultVariant_default = InfoIconDefaultVariant;

// src/icons/InfoIcon/Variants/InfoIconOutlinedVariant.tsx
import { jsx as jsx90, jsxs as jsxs49 } from "react/jsx-runtime";
var InfoIconOutlinedVariant = ({
  width = 14,
  height = 14,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "info-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx90(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 14 14",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs49("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx90(
          "rect",
          {
            "data-testid": "path-with-color",
            width: "6.41665",
            height: "2.04167",
            rx: "1.02083",
            transform: "matrix(-4.37115e-08 -1 -1 4.37113e-08 7.875 11.375)",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx90(
          "rect",
          {
            width: "2.04166",
            height: "2.04167",
            rx: "1.02083",
            transform: "matrix(-4.37115e-08 -1 -1 4.37113e-08 7.875 4.375)",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx90(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M7 12.5416C10.0606 12.5416 12.5417 10.0606 12.5417 6.99998C12.5417 3.93941 10.0606 1.45833 7 1.45833C3.93942 1.45833 1.45833 3.93941 1.45833 6.99998C1.45833 10.0606 3.93942 12.5416 7 12.5416ZM7 14C10.866 14 14 10.866 14 6.99998C14 3.134 10.866 0 7 0C3.13401 0 0 3.134 0 6.99998C0 10.866 3.13401 14 7 14Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var InfoIconOutlinedVariant_default = InfoIconOutlinedVariant;

// src/icons/InfoIcon/InfoIcon.tsx
import { jsx as jsx91 } from "react/jsx-runtime";
var InfoIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx91(InfoIconDefaultVariant_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx91(InfoIconOutlinedVariant_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var InfoIcon_default = InfoIcon;

// src/icons/QuestionIcon/Variants/QuestionIconDefaultVariant.tsx
import { jsx as jsx92, jsxs as jsxs50 } from "react/jsx-runtime";
var QuestionIconDefaultVariant = ({
  width = 20,
  height = 20,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#015F94",
  handleClick,
  opacity = "0.4",
  direction = "up",
  dataTestId = "question-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx92(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs50("g", { opacity, children: [
        /* @__PURE__ */ jsx92("path", { "fill-rule": "evenodd", fill: color, "clip-rule": "evenodd", d: "M7.08329 7.91699C7.08329 6.30866 8.39163 5.00033 9.99996 5.00033C11.6083 5.00033 12.9166 6.30866 12.9166 7.91699C12.9166 9.23366 12.0333 10.3362 10.8333 10.6978V11.667C10.8333 12.1278 10.4608 12.5003 9.99996 12.5003C9.53913 12.5003 9.16663 12.1278 9.16663 11.667V10.0003C9.16663 9.53949 9.53913 9.16699 9.99996 9.16699C10.6891 9.16699 11.25 8.60616 11.25 7.91699C11.25 7.22783 10.6891 6.66699 9.99996 6.66699C9.31079 6.66699 8.74996 7.22783 8.74996 7.91699C8.74996 8.37783 8.37746 8.75033 7.91663 8.75033C7.45579 8.75033 7.08329 8.37783 7.08329 7.91699ZM9.16663 14.167C9.16663 13.7062 9.53913 13.3337 9.99996 13.3337C10.4608 13.3337 10.8333 13.7062 10.8333 14.167C10.8333 14.6278 10.4608 15.0003 9.99996 15.0003C9.53913 15.0003 9.16663 14.6278 9.16663 14.167ZM9.99996 16.667C6.32413 16.667 3.33329 13.6762 3.33329 10.0003C3.33329 6.32449 6.32413 3.33366 9.99996 3.33366C13.6758 3.33366 16.6666 6.32449 16.6666 10.0003C16.6666 13.6762 13.6758 16.667 9.99996 16.667ZM9.99996 1.66699C5.40496 1.66699 1.66663 5.40533 1.66663 10.0003C1.66663 14.5953 5.40496 18.3337 9.99996 18.3337C14.595 18.3337 18.3333 14.5953 18.3333 10.0003C18.3333 5.40533 14.595 1.66699 9.99996 1.66699Z" }),
        /* @__PURE__ */ jsx92("mask", { id: "mask0_6300_180999", maskUnits: "userSpaceOnUse", x: "1", y: "1", width: "18", height: "18", children: /* @__PURE__ */ jsx92("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M7.08329 7.91699C7.08329 6.30866 8.39163 5.00033 9.99996 5.00033C11.6083 5.00033 12.9166 6.30866 12.9166 7.91699C12.9166 9.23366 12.0333 10.3362 10.8333 10.6978V11.667C10.8333 12.1278 10.4608 12.5003 9.99996 12.5003C9.53913 12.5003 9.16663 12.1278 9.16663 11.667V10.0003C9.16663 9.53949 9.53913 9.16699 9.99996 9.16699C10.6891 9.16699 11.25 8.60616 11.25 7.91699C11.25 7.22783 10.6891 6.66699 9.99996 6.66699C9.31079 6.66699 8.74996 7.22783 8.74996 7.91699C8.74996 8.37783 8.37746 8.75033 7.91663 8.75033C7.45579 8.75033 7.08329 8.37783 7.08329 7.91699ZM9.16663 14.167C9.16663 13.7062 9.53913 13.3337 9.99996 13.3337C10.4608 13.3337 10.8333 13.7062 10.8333 14.167C10.8333 14.6278 10.4608 15.0003 9.99996 15.0003C9.53913 15.0003 9.16663 14.6278 9.16663 14.167ZM9.99996 16.667C6.32413 16.667 3.33329 13.6762 3.33329 10.0003C3.33329 6.32449 6.32413 3.33366 9.99996 3.33366C13.6758 3.33366 16.6666 6.32449 16.6666 10.0003C16.6666 13.6762 13.6758 16.667 9.99996 16.667ZM9.99996 1.66699C5.40496 1.66699 1.66663 5.40533 1.66663 10.0003C1.66663 14.5953 5.40496 18.3337 9.99996 18.3337C14.595 18.3337 18.3333 14.5953 18.3333 10.0003C18.3333 5.40533 14.595 1.66699 9.99996 1.66699Z", fill: "white" }) }),
        /* @__PURE__ */ jsx92("g", { mask: "url(#mask0_6300_180999)" })
      ] })
    }
  );
};
var QuestionIconDefaultVariant_default = QuestionIconDefaultVariant;

// src/icons/QuestionIcon/Variants/QuestionIconOutlinedVariant.tsx
import { jsx as jsx93, jsxs as jsxs51 } from "react/jsx-runtime";
var QuestionIconOutlinedVariant = ({
  width = 14,
  height = 14,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#015F94",
  handleClick,
  opacity = "0.4",
  direction = "up",
  dataTestId = "question-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx93(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 14 14",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs51("g", { opacity, children: [
        /* @__PURE__ */ jsx93("path", { "fill-rule": "evenodd", fill: color, "clip-rule": "evenodd", d: "M7.08329 7.91699C7.08329 6.30866 8.39163 5.00033 9.99996 5.00033C11.6083 5.00033 12.9166 6.30866 12.9166 7.91699C12.9166 9.23366 12.0333 10.3362 10.8333 10.6978V11.667C10.8333 12.1278 10.4608 12.5003 9.99996 12.5003C9.53913 12.5003 9.16663 12.1278 9.16663 11.667V10.0003C9.16663 9.53949 9.53913 9.16699 9.99996 9.16699C10.6891 9.16699 11.25 8.60616 11.25 7.91699C11.25 7.22783 10.6891 6.66699 9.99996 6.66699C9.31079 6.66699 8.74996 7.22783 8.74996 7.91699C8.74996 8.37783 8.37746 8.75033 7.91663 8.75033C7.45579 8.75033 7.08329 8.37783 7.08329 7.91699ZM9.16663 14.167C9.16663 13.7062 9.53913 13.3337 9.99996 13.3337C10.4608 13.3337 10.8333 13.7062 10.8333 14.167C10.8333 14.6278 10.4608 15.0003 9.99996 15.0003C9.53913 15.0003 9.16663 14.6278 9.16663 14.167ZM9.99996 16.667C6.32413 16.667 3.33329 13.6762 3.33329 10.0003C3.33329 6.32449 6.32413 3.33366 9.99996 3.33366C13.6758 3.33366 16.6666 6.32449 16.6666 10.0003C16.6666 13.6762 13.6758 16.667 9.99996 16.667ZM9.99996 1.66699C5.40496 1.66699 1.66663 5.40533 1.66663 10.0003C1.66663 14.5953 5.40496 18.3337 9.99996 18.3337C14.595 18.3337 18.3333 14.5953 18.3333 10.0003C18.3333 5.40533 14.595 1.66699 9.99996 1.66699Z" }),
        /* @__PURE__ */ jsx93("mask", { id: "mask0_6300_180999", maskUnits: "userSpaceOnUse", x: "1", y: "1", width: "18", height: "18", children: /* @__PURE__ */ jsx93("path", { "fill-rule": "evenodd", "clip-rule": "evenodd", d: "M7.08329 7.91699C7.08329 6.30866 8.39163 5.00033 9.99996 5.00033C11.6083 5.00033 12.9166 6.30866 12.9166 7.91699C12.9166 9.23366 12.0333 10.3362 10.8333 10.6978V11.667C10.8333 12.1278 10.4608 12.5003 9.99996 12.5003C9.53913 12.5003 9.16663 12.1278 9.16663 11.667V10.0003C9.16663 9.53949 9.53913 9.16699 9.99996 9.16699C10.6891 9.16699 11.25 8.60616 11.25 7.91699C11.25 7.22783 10.6891 6.66699 9.99996 6.66699C9.31079 6.66699 8.74996 7.22783 8.74996 7.91699C8.74996 8.37783 8.37746 8.75033 7.91663 8.75033C7.45579 8.75033 7.08329 8.37783 7.08329 7.91699ZM9.16663 14.167C9.16663 13.7062 9.53913 13.3337 9.99996 13.3337C10.4608 13.3337 10.8333 13.7062 10.8333 14.167C10.8333 14.6278 10.4608 15.0003 9.99996 15.0003C9.53913 15.0003 9.16663 14.6278 9.16663 14.167ZM9.99996 16.667C6.32413 16.667 3.33329 13.6762 3.33329 10.0003C3.33329 6.32449 6.32413 3.33366 9.99996 3.33366C13.6758 3.33366 16.6666 6.32449 16.6666 10.0003C16.6666 13.6762 13.6758 16.667 9.99996 16.667ZM9.99996 1.66699C5.40496 1.66699 1.66663 5.40533 1.66663 10.0003C1.66663 14.5953 5.40496 18.3337 9.99996 18.3337C14.595 18.3337 18.3333 14.5953 18.3333 10.0003C18.3333 5.40533 14.595 1.66699 9.99996 1.66699Z", fill: "white" }) }),
        /* @__PURE__ */ jsx93("g", { mask: "url(#mask0_6300_180999)" })
      ] })
    }
  );
};
var QuestionIconOutlinedVariant_default = QuestionIconOutlinedVariant;

// src/icons/QuestionIcon/QuestionIcon.tsx
import { jsx as jsx94 } from "react/jsx-runtime";
var QuestionIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx94(QuestionIconDefaultVariant_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx94(QuestionIconOutlinedVariant_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var QuestionIcon_default = QuestionIcon;

// src/icons/JointsIcon/JointsIcon.tsx
import { jsx as jsx95, jsxs as jsxs52 } from "react/jsx-runtime";
var JointsIcon = ({
  width = 46,
  height = 50,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "joints-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx95(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 46 50",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs52("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx95(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18.0001 0C18.6904 0 19.2501 0.55965 19.2501 1.25V11.6789C19.2501 13.3365 18.5916 14.9263 17.4194 16.0984L16.7897 16.7283C15.6731 17.8448 15.6731 19.6552 16.7897 20.7717C17.6599 21.642 18.9893 21.8577 20.0902 21.3074L20.3932 21.1559C22.0342 20.3354 23.9659 20.3354 25.6069 21.1559L25.9099 21.3074C27.0108 21.8577 28.3402 21.642 29.2104 20.7717C30.3271 19.6552 30.3271 17.8448 29.2104 16.7283L28.5807 16.0984C27.4086 14.9263 26.7501 13.3365 26.7501 11.6789V1.25C26.7501 0.55965 27.3097 0 28.0001 0C28.6904 0 29.2501 0.55965 29.2501 1.25V11.6789C29.2501 12.6735 29.6452 13.6274 30.3484 14.3306L30.9783 14.9604C33.0712 17.0534 33.0712 20.4466 30.9783 22.5396C29.3471 24.1707 26.8552 24.5751 24.7919 23.5435L24.4889 23.392C23.5517 22.9234 22.4484 22.9234 21.5112 23.392L21.2082 23.5435C19.1449 24.5751 16.6531 24.1707 15.0218 22.5396C12.9289 20.4466 12.9289 17.0534 15.0218 14.9604L15.6517 14.3306C16.3549 13.6274 16.7501 12.6735 16.7501 11.6789V1.25C16.7501 0.55965 17.3097 0 18.0001 0Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx95(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18.0001 50.0001C18.6904 50.0001 19.2501 49.4404 19.2501 48.7501V38.3212C19.2501 36.6636 18.5916 35.0738 17.4194 33.9017L16.7897 33.2718C15.6731 32.1553 15.6731 30.3448 16.7897 29.2283C17.6599 28.3581 18.9893 28.1423 20.0902 28.6927L20.3932 28.8442C22.0342 29.6647 23.9659 29.6647 25.6069 28.8442L25.9099 28.6927C27.0108 28.1423 28.3402 28.3581 29.2104 29.2283C30.3271 30.3448 30.3271 32.1553 29.2104 33.2718L28.5807 33.9017C27.4086 35.0738 26.7501 36.6636 26.7501 38.3212V48.7501C26.7501 49.4404 27.3097 50.0001 28.0001 50.0001C28.6904 50.0001 29.2501 49.4404 29.2501 48.7501V38.3212C29.2501 37.3266 29.6452 36.3727 30.3484 35.6694L30.9783 35.0397C33.0712 32.9467 33.0712 29.5534 30.9783 27.4604C29.3471 25.8293 26.8552 25.4249 24.7919 26.4566L24.4889 26.6081C23.5517 27.0767 22.4484 27.0767 21.5112 26.6081L21.2082 26.4566C19.1449 25.4249 16.6531 25.8293 15.0218 27.4604C12.9289 29.5534 12.9289 32.9467 15.0218 35.0397L15.6517 35.6694C16.3549 36.3727 16.7501 37.3266 16.7501 38.3212V48.7501C16.7501 49.4404 17.3097 50.0001 18.0001 50.0001Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx95(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18 3.05762C7.98 5.33127 0.5 14.2922 0.5 25.0001C0.5 35.7081 7.98 44.6689 18 46.9426V44.3699C9.37387 42.1498 3 34.3193 3 25.0001C3 15.6809 9.37387 7.85044 18 5.63017V3.05762ZM28 44.3699C36.6261 42.1498 43 34.3193 43 25.0001C43 15.6809 36.6261 7.85044 28 5.63017V3.05762C38.02 5.33127 45.5 14.2922 45.5 25.0001C45.5 35.7081 38.02 44.6689 28 46.9426V44.3699Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var JointsIcon_default = JointsIcon;

// src/icons/KidneysIcon/KidneysIcon.tsx
import { jsx as jsx96, jsxs as jsxs53 } from "react/jsx-runtime";
var KidneysIcon = ({
  width = 46,
  height = 42,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "kidneys-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx96(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 46 42",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs53("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx96(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M8.68497 29.4423C-4.89532 23.7144 0.464182 4.38093 9.89122 1.18654C13.802 -0.138606 18.1172 4.25768 18.0896 6.98731C18.0776 8.16756 17.4686 9.31831 16.8341 10.5173C16.0011 12.0913 15.1241 13.7484 15.4965 15.6643C16.1526 19.0394 16.8875 24.7342 14.994 27.4374C13.1003 30.1407 11.1961 30.5014 8.68497 29.4423ZM9.65647 27.1388C10.6348 27.5514 11.1155 27.5243 11.3791 27.4484C11.6497 27.3704 12.178 27.1001 12.9463 26.0032C13.4122 25.3379 13.7295 23.9343 13.7015 21.8361C13.6753 19.8659 13.3577 17.7634 13.0425 16.1413C12.537 13.5409 13.5751 11.3687 14.3158 9.93581C14.4433 9.68893 14.5598 9.46906 14.6671 9.26631C14.9145 8.79906 15.1133 8.42331 15.2891 8.02043C15.5233 7.48356 15.5876 7.16543 15.5897 6.96206C15.5903 6.89718 15.5465 6.56656 15.2223 6.02231C14.9178 5.51131 14.4556 4.96693 13.888 4.50443C12.684 3.52331 11.5188 3.27465 10.6936 3.55431C7.1811 4.74456 3.87892 9.32781 3.1369 14.7061C2.425 19.8659 4.1883 24.8324 9.65647 27.1388Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx96(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M35.9342 1.18654C45.3613 4.38093 50.7208 23.7144 37.1406 29.4423C34.6293 30.5014 32.7251 30.1407 30.8316 27.4374C28.9381 24.7342 29.673 19.0394 30.329 15.6643C30.7013 13.7484 29.8245 12.0913 28.9913 10.5173C28.3568 9.31831 27.7478 8.16756 27.736 6.98731C27.7083 4.25768 32.0236 -0.138606 35.9342 1.18654ZM34.4463 27.4484C34.7101 27.5243 35.1907 27.5514 36.169 27.1388C41.6372 24.8324 43.4005 19.8659 42.6886 14.7061C41.9466 9.32781 38.6445 4.74456 35.132 3.55431C34.3066 3.27465 33.1415 3.52331 31.9375 4.50443C31.3698 4.96693 30.9076 5.51131 30.6032 6.02231C30.2791 6.56656 30.2351 6.89718 30.2358 6.96206C30.2378 7.16543 30.3022 7.48356 30.5365 8.02043C30.7122 8.42331 30.9111 8.79906 31.1583 9.26631C31.2657 9.46906 31.3821 9.68893 31.5097 9.93581C32.2503 11.3687 33.2885 13.5409 32.7831 16.1413C32.4677 17.7634 32.1502 19.8659 32.124 21.8361C32.0961 23.9343 32.4133 25.3379 32.8792 26.0032C33.6476 27.1001 34.1758 27.3704 34.4463 27.4484Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx96(
          "path",
          {
            d: "M28.7726 19.4745C28.8583 20.7215 28.8591 20.7215 28.8598 20.7214L28.8613 20.7213L28.8641 20.7212L28.869 20.7208L28.8758 20.7202L28.8795 20.7198C28.8773 20.72 28.8667 20.7213 28.8488 20.7242C28.813 20.7299 28.7501 20.7419 28.6696 20.7648C28.5057 20.8112 28.2897 20.8963 28.0782 21.0469C27.7161 21.3048 27.1836 21.8775 27.1836 23.3228V41.4054C27.1836 41.4055 27.1836 41.4057 25.9336 41.4057C24.6836 41.4057 24.6836 41.405 24.6836 41.4049V23.3228C24.6836 21.1149 25.5706 19.7635 26.628 19.0105C27.1262 18.6558 27.62 18.4637 27.9883 18.3594C28.1741 18.3068 28.3331 18.275 28.4523 18.2558C28.5121 18.2462 28.5626 18.2397 28.602 18.2353C28.6217 18.233 28.6388 18.2314 28.653 18.2302L28.6721 18.2285L28.6801 18.2279L28.6836 18.2277L28.6852 18.2275C28.6861 18.2275 28.6868 18.2274 28.7726 19.4745Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx96(
          "path",
          {
            d: "M17.0531 19.4745C16.9673 20.7215 16.9664 20.7215 16.9657 20.7214L16.9643 20.7213L16.9614 20.7212L16.9567 20.7208L16.9497 20.7202C16.9473 20.7199 16.9461 20.7198 16.9458 20.7198C16.9479 20.72 16.9589 20.7213 16.9767 20.7242C17.0127 20.7299 17.0756 20.7419 17.1559 20.7648C17.3198 20.8112 17.5358 20.8963 17.7474 21.0469C18.1096 21.3048 18.6421 21.8775 18.6421 23.3228V41.4054C18.6421 41.4055 18.6421 41.4057 19.8921 41.4057C21.1421 41.4057 21.1421 41.405 21.1421 41.4049V23.3228C21.1421 21.1149 20.2549 19.7635 19.1976 19.0105C18.6994 18.6558 18.2057 18.4637 17.8372 18.3594C17.6516 18.3068 17.4926 18.275 17.3733 18.2558C17.3134 18.2462 17.2631 18.2397 17.2236 18.2353C17.2038 18.233 17.1868 18.2314 17.1726 18.2302L17.1534 18.2285L17.1456 18.2279L17.1421 18.2277L17.1403 18.2275C17.1396 18.2275 17.1387 18.2274 17.0531 19.4745Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var KidneysIcon_default = KidneysIcon;

// src/icons/LifeScienceStrategyIcon/Variants/LifeScienceStrategyIconDefault.tsx
import { jsx as jsx97, jsxs as jsxs54 } from "react/jsx-runtime";
var LifeScienceStrategyIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "life-science-strategy-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx97(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs54("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx97("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx97(
          "path",
          {
            "data-testid": "path-with-background-color",
            d: "M75.6875 0.5H35.6875C19.119 0.5 5.6875 13.9315 5.6875 30.5V90.5C5.6875 107.069 19.119 120.5 35.6875 120.5H85.6875C102.256 120.5 115.688 107.069 115.688 90.5V40.5C115.688 23.9315 92.256 0.5 75.6875 0.5Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsxs54("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx97(
            "path",
            {
              "data-testid": "path-with-color",
              fillRule: "evenodd",
              clipRule: "evenodd",
              d: "M104.912 67.0555C106.865 65.1031 110.031 65.1035 111.983 67.0564L119.057 74.1383C121.009 76.0911 121.009 79.257 119.056 81.2094C117.103 83.1617 113.937 83.1613 111.985 81.2085L104.911 74.1266C102.959 72.1737 102.959 69.0079 104.912 67.0555Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx97(
            "path",
            {
              fillRule: "evenodd",
              clipRule: "evenodd",
              d: "M56.3055 80.6621C49.9518 76.9944 44.6249 71.7463 40.8622 65.4551H29.2861C26.5247 65.4551 24.2861 63.2165 24.2861 60.4551C24.2861 57.6937 26.5247 55.4551 29.2861 55.4551H36.5021C35.3939 51.6435 34.7998 47.613 34.7998 43.4438C34.7998 42.3994 34.8371 41.3637 34.9104 40.3379H29.2861C26.5247 40.3379 24.2861 38.0993 24.2861 35.3379C24.2861 32.5765 26.5247 30.3379 29.2861 30.3379H36.8364C42.3769 13.0303 58.597 0.5 77.7437 0.5C101.461 0.5 120.688 19.7266 120.688 43.4438C120.688 60.4141 110.844 75.0853 96.5551 82.0591C97.5002 82.9688 98.0883 84.2468 98.0883 85.6621C98.0883 88.4235 95.8497 90.6621 93.0883 90.6621H29.2861C26.5247 90.6621 24.2861 88.4235 24.2861 85.6621C24.2861 82.9007 26.5247 80.6621 29.2861 80.6621H56.3055ZM110.688 43.4438C110.688 61.6382 95.938 76.3877 77.7437 76.3877C59.5493 76.3877 44.7998 61.6382 44.7998 43.4438C44.7998 25.2495 59.5493 10.5 77.7437 10.5C95.938 10.5 110.688 25.2495 110.688 43.4438Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var LifeScienceStrategyIconDefault_default = LifeScienceStrategyIconDefaultVariant;

// src/icons/LifeScienceStrategyIcon/Variants/LifeScienceStrategyIconOutlined.tsx
import { jsx as jsx98, jsxs as jsxs55 } from "react/jsx-runtime";
var LifeScienceStrategyIconOutlinedVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "life-science-strategy-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx98(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs55("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx98(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M15.8123 25.5C15.8123 17.2157 22.528 10.5 30.8123 10.5H40.9885C43.7499 10.5 45.9885 8.26142 45.9885 5.5C45.9885 2.73858 43.7499 0.5 40.9885 0.5H30.8123C17.0051 0.5 5.81226 11.6929 5.81226 25.5V95.5C5.81226 109.307 17.0051 120.5 30.8123 120.5H90.8123C104.619 120.5 115.812 109.307 115.812 95.5V93.2444C115.812 90.483 113.574 88.2444 110.812 88.2444C108.051 88.2444 105.812 90.483 105.812 93.2444V95.5C105.812 103.784 99.0965 110.5 90.8123 110.5H30.8123C22.528 110.5 15.8123 103.784 15.8123 95.5V25.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx98(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M106.16 75.7521C103.215 78.3327 99.9148 80.5171 96.3418 82.2223C97.1917 83.1186 97.713 84.3295 97.713 85.6621C97.713 88.4235 95.4744 90.6621 92.713 90.6621H28.9109C26.1495 90.6621 23.9109 88.4235 23.9109 85.6621C23.9109 82.9007 26.1495 80.6621 28.9109 80.6621H56.4303C50.0765 76.9944 44.7497 71.7463 40.9869 65.4551H28.9109C26.1495 65.4551 23.9109 63.2165 23.9109 60.4551C23.9109 57.6937 26.1495 55.4551 28.9109 55.4551H36.6269C35.5187 51.6435 34.9246 47.613 34.9246 43.4438C34.9246 42.3994 34.9618 41.3637 35.0352 40.3379H28.9109C26.1495 40.3379 23.9109 38.0993 23.9109 35.3379C23.9109 32.5765 26.1495 30.3379 28.9109 30.3379H36.9612C42.5017 13.0303 58.7218 0.5 77.8684 0.5C101.586 0.5 120.812 19.7266 120.812 43.4438C120.812 52.7176 117.873 61.3047 112.875 68.3243L118.682 74.1383C120.634 76.0911 120.634 79.257 118.681 81.2094C116.728 83.1617 113.562 83.1613 111.61 81.2085L106.16 75.7521ZM110.812 43.4438C110.812 61.6382 96.0628 76.3877 77.8684 76.3877C59.674 76.3877 44.9246 61.6382 44.9246 43.4438C44.9246 25.2495 59.674 10.5 77.8684 10.5C96.0628 10.5 110.812 25.2495 110.812 43.4438Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var LifeScienceStrategyIconOutlined_default = LifeScienceStrategyIconOutlinedVariant;

// src/icons/LifeScienceStrategyIcon/LifeScienceStrategyIcon.tsx
import { jsx as jsx99 } from "react/jsx-runtime";
var LifeScienceStrategyIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx99(LifeScienceStrategyIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx99(LifeScienceStrategyIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var LifeScienceStrategyIcon_default = LifeScienceStrategyIcon;

// src/icons/ListIcon/ListIcon.tsx
import { jsx as jsx100, jsxs as jsxs56 } from "react/jsx-runtime";
var ListIcon = ({
  width = 20,
  height = 20,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "list-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx100(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs56("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx100(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M5.83301 0.5C5.28072 0.5 4.83301 0.947715 4.83301 1.5V4.25C4.83301 4.80229 5.28072 5.25 5.83301 5.25H18.8747C19.427 5.25 19.8747 4.80228 19.8747 4.25V1.5C19.8747 0.947715 19.427 0.5 18.8747 0.5H5.83301Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx100(
          "path",
          {
            d: "M5.83301 7.625C5.28072 7.625 4.83301 8.07272 4.83301 8.625V11.375C4.83301 11.9273 5.28072 12.375 5.83301 12.375H18.8747C19.427 12.375 19.8747 11.9273 19.8747 11.375V8.625C19.8747 8.07272 19.427 7.625 18.8747 7.625H5.83301Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx100(
          "path",
          {
            d: "M5.83301 14.75C5.28072 14.75 4.83301 15.1977 4.83301 15.75V18.5C4.83301 19.0523 5.28072 19.5 5.83301 19.5H18.8747C19.427 19.5 19.8747 19.0523 19.8747 18.5V15.75C19.8747 15.1977 19.427 14.75 18.8747 14.75H5.83301Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx100(
          "path",
          {
            d: "M1.87451 0.5C1.32223 0.5 0.874512 0.947715 0.874512 1.5V4.25C0.874512 4.80229 1.32223 5.25 1.87451 5.25H3.04118C3.59346 5.25 4.04118 4.80228 4.04118 4.25V1.5C4.04118 0.947715 3.59346 0.5 3.04118 0.5H1.87451Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx100(
          "path",
          {
            d: "M1.87451 7.625C1.32223 7.625 0.874512 8.07272 0.874512 8.625V11.375C0.874512 11.9273 1.32223 12.375 1.87451 12.375H3.04118C3.59346 12.375 4.04118 11.9273 4.04118 11.375V8.625C4.04118 8.07272 3.59346 7.625 3.04118 7.625H1.87451Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx100(
          "path",
          {
            d: "M1.87451 14.75C1.32223 14.75 0.874512 15.1977 0.874512 15.75V18.5C0.874512 19.0523 1.32223 19.5 1.87451 19.5H3.04118C3.59346 19.5 4.04118 19.0523 4.04118 18.5V15.75C4.04118 15.1977 3.59346 14.75 3.04118 14.75H1.87451Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var ListIcon_default = ListIcon;

// src/icons/LocationIcon/Variants/LocationIcon.tsx
import { jsx as jsx101, jsxs as jsxs57 } from "react/jsx-runtime";
var LocationIconOutlinedVariant = ({
  width = 16,
  height = 16,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "location-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx101(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs57("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx101(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M6.08399 6.07593C5.92183 6.52044 5.83333 7.00008 5.83333 7.5C5.83333 8.24391 6.0293 8.94292 6.37232 9.54811C6.37842 9.55994 6.38484 9.57168 6.39156 9.58333C6.77065 10.2399 7.30269 10.7544 7.91695 11.1077C8.53011 11.4631 9.24173 11.6667 10 11.6667C10.973 11.6667 11.892 11.3369 12.633 10.7298C13.6083 9.93561 14.1508 8.75548 14.1655 7.54371C14.1663 7.52923 14.1667 7.51466 14.1667 7.5C14.1667 7.12668 14.1173 6.76467 14.0248 6.42015C13.9327 6.07643 13.7946 5.73915 13.6084 5.41667C13.2293 4.75998 12.6972 4.2455 12.0828 3.8922C11.4697 3.53688 10.7582 3.33333 10 3.33333C9.62777 3.33333 9.26679 3.3824 8.92318 3.47439C8.57842 3.56653 8.24009 3.70483 7.91667 3.89156C7.05635 4.38826 6.41674 5.1544 6.08399 6.07593ZM7.57081 6.90784C7.52453 7.09773 7.5 7.29605 7.5 7.5C7.5 8.42369 8.00325 9.23181 8.75003 9.66463C9.4983 10.0951 10.4499 10.127 11.25 9.66506C11.3675 9.5972 11.4775 9.52118 11.5795 9.43813C11.6525 9.37833 11.7227 9.31405 11.7896 9.24542C11.7955 9.2394 11.8014 9.23348 11.8074 9.22768C12.4076 8.59937 12.6406 7.69376 12.4149 6.85218C12.2416 6.20636 11.8155 5.66304 11.2496 5.33519C10.6834 5.00953 10.0008 4.91213 9.35554 5.08416C8.56986 5.29386 7.93543 5.8775 7.65531 6.63202C7.62527 6.71376 7.59927 6.79757 7.57747 6.88322C7.57536 6.89149 7.57314 6.8997 7.57081 6.90784Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx101(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M10 0C5.86458 0 2.5 3.36458 2.5 7.5C2.5 11.3721 8.65792 18.8596 9.36042 19.7008C9.51875 19.8904 9.75292 20 10 20C10.2471 20 10.4813 19.8904 10.6396 19.7008C11.3421 18.8596 17.5 11.3721 17.5 7.5C17.5 3.36458 14.1354 0 10 0ZM10 17.845C7.92583 15.2333 4.16667 9.985 4.16667 7.5C4.16667 4.28333 6.78333 1.66667 10 1.66667C13.2167 1.66667 15.8333 4.28333 15.8333 7.5C15.8333 9.985 12.0742 15.2333 10 17.845Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var LocationIcon_default = LocationIconOutlinedVariant;

// src/icons/LocationIcon/Variants/LocationIconDefaultVariant.tsx
import { jsx as jsx102, jsxs as jsxs58 } from "react/jsx-runtime";
var LocationIconDefaultVariant = ({
  width = 16,
  height = 16,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "location-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx102(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 16 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs58("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx102(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M8 0C4.69167 0 2 2.69167 2 6C2 9.09767 6.92633 15.0877 7.48833 15.7607C7.615 15.9123 7.80233 16 8 16C8.19767 16 8.385 15.9123 8.51167 15.7607C9.07367 15.0877 14 9.09767 14 6C14 2.69167 11.3083 0 8 0ZM8 14.276C6.34067 12.1867 3.33333 7.988 3.33333 6C3.33333 3.42667 5.42667 1.33333 8 1.33333C10.5733 1.33333 12.6667 3.42667 12.6667 6C12.6667 7.988 9.65933 12.1867 8 14.276Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx102(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M3.33333 6C3.33333 7.988 6.34067 12.1867 8 14.276C9.65933 12.1867 12.6667 7.988 12.6667 6C12.6667 3.42667 10.5733 1.33333 8 1.33333C5.42667 1.33333 3.33333 3.42667 3.33333 6ZM4.86719 4.86074C4.73747 5.21635 4.66667 5.60006 4.66667 6C4.66667 6.59513 4.82344 7.15434 5.09786 7.63849C5.10274 7.64795 5.10787 7.65735 5.11325 7.66667C5.41652 8.19194 5.84215 8.60348 6.33356 8.88612C6.82409 9.17045 7.39338 9.33333 8 9.33333C8.77844 9.33333 9.51357 9.06954 10.1064 8.58381C10.8867 7.94849 11.3206 7.00439 11.3324 6.03497C11.333 6.02339 11.3333 6.01173 11.3333 6C11.3333 5.70134 11.2939 5.41173 11.2198 5.13612C11.1461 4.86115 11.0357 4.59132 10.8868 4.33333C10.5834 3.80798 10.1577 3.3964 9.66623 3.11376C9.17575 2.8295 8.60653 2.66667 8 2.66667C7.70222 2.66667 7.41343 2.70592 7.13854 2.77952C6.86273 2.85322 6.59208 2.96386 6.33333 3.11325C5.64508 3.51061 5.13339 4.12352 4.86719 4.86074Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx102(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M8 14.812L7.73897 14.4833C6.90429 13.4323 5.72902 11.849 4.7605 10.2747C4.27639 9.4877 3.84001 8.69663 3.52362 7.97127C3.21083 7.25415 3 6.56747 3 6C3 3.24257 5.24257 1 8 1C10.7574 1 13 3.24257 13 6C13 6.56747 12.7892 7.25415 12.4764 7.97127C12.16 8.69663 11.7236 9.4877 11.2395 10.2747C10.271 11.849 9.09571 13.4323 8.26103 14.4833L8 14.812ZM8 13.7367C7.20327 12.7135 6.18183 11.3128 5.32833 9.92535C4.85319 9.15297 4.43404 8.39104 4.13469 7.70473C3.83175 7.01019 3.66667 6.42653 3.66667 6C3.66667 3.61076 5.61076 1.66667 8 1.66667C10.3892 1.66667 12.3333 3.61076 12.3333 6C12.3333 6.42653 12.1683 7.01019 11.8653 7.70473C11.566 8.39104 11.1468 9.15297 10.6717 9.92535C9.81817 11.3128 8.79673 12.7135 8 13.7367ZM8 0.666667C5.05986 0.666667 2.66667 3.05986 2.66667 6C2.66667 6.62474 2.92315 7.47152 3.38256 8.45519C3.83408 9.42197 4.4482 10.452 5.08951 11.4219C6.37133 13.3605 7.7253 15.0043 8 15.3333C8.2747 15.0043 9.62867 13.3605 10.9105 11.4219C11.5518 10.452 12.1659 9.42197 12.6174 8.45519C13.0768 7.47152 13.3333 6.62474 13.3333 6C13.3333 3.05986 10.9401 0.666667 8 0.666667ZM7.48833 15.7607C6.92633 15.0877 2 9.09767 2 6C2 2.69167 4.69167 0 8 0C11.3083 0 14 2.69167 14 6C14 9.09767 9.07367 15.0877 8.51167 15.7607C8.385 15.9123 8.19767 16 8 16C7.80233 16 7.615 15.9123 7.48833 15.7607ZM4.86719 4.86074C5.13339 4.12352 5.64508 3.51061 6.33333 3.11325C6.59208 2.96386 6.86273 2.85322 7.13854 2.77952C7.41343 2.70592 7.70222 2.66667 8 2.66667C8.60653 2.66667 9.17575 2.8295 9.66623 3.11376C10.1577 3.3964 10.5834 3.80798 10.8868 4.33333C11.0357 4.59132 11.1461 4.86115 11.2198 5.13612C11.2939 5.41173 11.3333 5.70134 11.3333 6C11.3333 6.01173 11.333 6.02339 11.3324 6.03497C11.3206 7.00439 10.8867 7.94849 10.1064 8.58381C9.51357 9.06954 8.77844 9.33333 8 9.33333C7.39338 9.33333 6.82409 9.17045 6.33356 8.88612C5.84215 8.60348 5.41652 8.19194 5.11325 7.66667C5.10787 7.65735 5.10274 7.64795 5.09786 7.63849C4.82344 7.15434 4.66667 6.59513 4.66667 6C4.66667 5.60006 4.73747 5.21635 4.86719 4.86074ZM4.51222 7.9572C4.18612 7.378 4 6.70954 4 6C4 5.52126 4.08476 5.06064 4.24051 4.63331C4.56044 3.74805 5.17629 3.01146 6 2.5359C6.30975 2.35706 6.63458 2.22416 6.96612 2.13553M4.51222 7.9572C4.51983 7.97157 4.52772 7.98584 4.5359 8.00001C4.89936 8.62954 5.41041 9.124 6.00015 9.46343C6.58905 9.80457 7.27292 10 8 10C8.93154 10 9.81579 9.68347 10.5282 9.10014C11.4627 8.33876 11.9815 7.21126 11.9989 6.0546C11.9996 6.03648 12 6.01827 12 6C12 5.64246 11.9527 5.29484 11.8638 4.96351C11.7751 4.63287 11.6425 4.30894 11.4641 4C11.1006 3.37032 10.5894 2.87578 9.99946 2.53635C9.41064 2.19534 8.72691 2 8 2C7.64351 2 7.29688 2.047 6.96642 2.13545",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx102(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M8.00033 8.66663C9.47308 8.66663 10.667 7.47272 10.667 5.99996C10.667 4.5272 9.47308 3.33329 8.00033 3.33329C6.52757 3.33329 5.33366 4.5272 5.33366 5.99996C5.33366 7.47272 6.52757 8.66663 8.00033 8.66663ZM8.00033 9.33329C9.84127 9.33329 11.3337 7.84091 11.3337 5.99996C11.3337 4.15901 9.84127 2.66663 8.00033 2.66663C6.15938 2.66663 4.66699 4.15901 4.66699 5.99996C4.66699 7.84091 6.15938 9.33329 8.00033 9.33329Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var LocationIconDefaultVariant_default = LocationIconDefaultVariant;

// src/icons/LocationIcon/LocationIcon.tsx
import { jsx as jsx103 } from "react/jsx-runtime";
var LocationIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx103(LocationIconDefaultVariant_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx103(LocationIcon_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var LocationIcon_default2 = LocationIcon;

// src/icons/LockIcon/LockIcon.tsx
import { jsx as jsx104, jsxs as jsxs59 } from "react/jsx-runtime";
var LockIcon = ({
  width = 60,
  height = 60,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "lock-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx104(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs59("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx104(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M12 16C11.448 16 11 15.552 11 15C11 14.448 11.448 14 12 14C12.552 14 13 14.448 13 15C13 15.552 12.552 16 12 16ZM12 12C10.346 12 9 13.346 9 15C9 16.654 10.346 18 12 18C13.654 18 15 16.654 15 15C15 13.346 13.654 12 12 12ZM18 19C18 19.552 17.552 20 17 20H7C6.448 20 6 19.552 6 19V11C6 10.448 6.448 10 7 10H8H10H14H16H17C17.552 10 18 10.448 18 11V19ZM10 6.111C10 4.947 10.897 4 12 4C13.103 4 14 4.947 14 6.111V8H10V6.111ZM17 8H16V6.111C16 3.845 14.206 2 12 2C9.794 2 8 3.845 8 6.111V8H7C5.346 8 4 9.346 4 11V19C4 20.654 5.346 22 7 22H17C18.654 22 20 20.654 20 19V11C20 9.346 18.654 8 17 8Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx104("mask", { id: "mask0_1028_2335", maskUnits: "userSpaceOnUse", x: "0", y: "0", children: /* @__PURE__ */ jsx104(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M12 16C11.448 16 11 15.552 11 15C11 14.448 11.448 14 12 14C12.552 14 13 14.448 13 15C13 15.552 12.552 16 12 16ZM12 12C10.346 12 9 13.346 9 15C9 16.654 10.346 18 12 18C13.654 18 15 16.654 15 15C15 13.346 13.654 12 12 12ZM18 19C18 19.552 17.552 20 17 20H7C6.448 20 6 19.552 6 19V11C6 10.448 6.448 10 7 10H8H10H14H16H17C17.552 10 18 10.448 18 11V19ZM10 6.111C10 4.947 10.897 4 12 4C13.103 4 14 4.947 14 6.111V8H10V6.111ZM17 8H16V6.111C16 3.845 14.206 2 12 2C9.794 2 8 3.845 8 6.111V8H7C5.346 8 4 9.346 4 11V19C4 20.654 5.346 22 7 22H17C18.654 22 20 20.654 20 19V11C20 9.346 18.654 8 17 8Z",
            fill: color
          }
        ) }),
        /* @__PURE__ */ jsx104("g", { mask: "url(#mask0_1028_2335)" })
      ] })
    }
  );
};
var LockIcon_default = LockIcon;

// src/icons/LogoutIcon/LogoutIcon.tsx
import { jsx as jsx105, jsxs as jsxs60 } from "react/jsx-runtime";
var LogoutIcon = ({
  width = 24,
  height = 18,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#AE2824",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "logout-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx105(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 18",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsxs60("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx105(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M3.72458 10.0907H16.3635C16.966 10.0907 17.4544 9.60231 17.4544 8.99983C17.4544 8.39736 16.966 7.90892 16.3635 7.90892H3.72458L5.13505 6.49852C5.56109 6.07256 5.56109 5.38179 5.13505 4.95576C4.70916 4.52972 4.0184 4.52972 3.59229 4.95576L0.319782 8.22827C0.294473 8.25351 0.270618 8.27991 0.247927 8.30754C0.2424 8.31431 0.237673 8.32158 0.232291 8.32849C0.215636 8.34965 0.199273 8.37089 0.184291 8.39329C0.179927 8.39983 0.176364 8.40674 0.172073 8.41336C0.157091 8.43678 0.1424 8.46042 0.129164 8.48507C0.126618 8.48994 0.124509 8.49511 0.121964 8.49998C0.108218 8.52667 0.0949818 8.55373 0.0834182 8.58165C0.0819636 8.58507 0.0809455 8.58871 0.0795636 8.5922C0.0677091 8.62158 0.0567273 8.65133 0.0474182 8.68194C0.0461818 8.6858 0.0455273 8.6898 0.0444364 8.69373C0.0356364 8.72376 0.0275636 8.75409 0.0213818 8.78514C0.0195636 8.79438 0.0187636 8.80391 0.0170909 8.81322C0.0125818 8.83896 0.00807273 8.86471 0.00552727 8.89096C0.00181818 8.92696 0 8.96333 0 8.99983C0 9.03634 0.00181818 9.07271 0.00552727 9.10878C0.00807273 9.1354 0.0127273 9.16136 0.0171636 9.18733C0.0187636 9.19634 0.0195636 9.20558 0.0213818 9.21453C0.0276364 9.24587 0.0356364 9.27649 0.0445091 9.30682C0.0456 9.31038 0.0462545 9.31416 0.0473455 9.31773C0.0567273 9.34849 0.0677091 9.37853 0.0797091 9.4082C0.0810182 9.4114 0.0819636 9.41482 0.0833455 9.41802C0.0949818 9.44616 0.108291 9.47336 0.122182 9.5002C0.124582 9.50493 0.126618 9.50987 0.129091 9.5146C0.142473 9.5394 0.157236 9.56325 0.172436 9.58689C0.176509 9.59329 0.18 9.60005 0.184218 9.60631C0.199273 9.62878 0.215782 9.65016 0.232364 9.6714C0.237673 9.67823 0.2424 9.68544 0.247855 9.69213C0.270618 9.71969 0.294473 9.74616 0.319709 9.7714L3.59222 13.0439C3.80524 13.257 4.08444 13.3635 4.36364 13.3635C4.64284 13.3635 4.92204 13.257 5.13498 13.0439C5.56102 12.6179 5.56102 11.9272 5.13498 11.5011L3.72458 10.0907Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx105(
          "path",
          {
            d: "M15.0763 0.0761719C12.0985 0.0761719 9.32806 1.55341 7.66515 4.02766C7.32915 4.52766 7.4621 5.20548 7.9621 5.54155C8.46217 5.87755 9.13992 5.74475 9.47606 5.2446C10.7329 3.37442 12.8264 2.25792 15.0763 2.25792C18.7937 2.25799 21.8182 5.28235 21.8182 8.99987C21.8182 12.7174 18.7937 15.7418 15.0763 15.7418C12.833 15.7418 10.7429 14.6303 9.48515 12.7686C9.14777 12.2693 8.46981 12.1381 7.97046 12.4754C7.47126 12.8127 7.33992 13.4908 7.67723 13.99C9.3413 16.4531 12.1073 17.9236 15.0763 17.9236C19.9968 17.9236 24 13.9205 24 8.99987C24 4.0793 19.9968 0.0761719 15.0763 0.0761719Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var LogoutIcon_default = LogoutIcon;

// src/icons/MarketIntelligenceIcon/Variants/MarketingIntelligenceIconDefault.tsx
import { jsx as jsx106, jsxs as jsxs61 } from "react/jsx-runtime";
var MarketingIntelligenceIconDefault = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "market-intelligence-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx106(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs61("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx106("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx106(
          "path",
          {
            "data-testid": "path-with-background-color",
            d: "M3.71552 60.4639C1.19017 57.1906 0.713489 52.7776 2.48158 49.0405L13.2826 26.2109C15.282 21.9848 19.7342 19.4843 24.3835 19.9763L54.9619 23.2122C58.6206 23.5994 61.6264 26.183 62.6047 29.6574C67.7932 28.9796 72.5913 33.5721 71.5664 39.1534L71.3788 40.1752L72.3549 39.1456C76.3975 34.8814 82.6928 34.7236 86.9824 37.9032C88.299 33.353 92.4889 30.262 97.1997 30.262C97.4616 30.262 97.7013 30.1152 97.8204 29.882L100.844 23.9602C104.909 15.9984 116.427 16.4266 119.889 24.6683C120.869 27.0006 120.975 29.6082 120.186 32.012L111.691 57.9177C111.384 58.8554 110.952 59.7474 110.406 60.5698L96.6386 81.3297C96.3835 81.7145 96.4248 82.2239 96.7387 82.5624L100.354 86.4611C100.513 86.6329 100.66 86.8155 100.794 87.0079L104.195 91.9035C110.286 100.67 101.576 112.102 91.507 108.555L88.0985 107.354C86.1555 106.67 84.4437 105.454 83.1581 103.844L73.7194 92.0262C73.4319 91.6662 72.9356 91.5492 72.5176 91.743L67.2788 94.1708C67.2246 94.1959 67.1728 94.2258 67.1239 94.2602L62.3039 97.6511C57.8896 100.757 51.8742 100.201 48.1038 96.3386L42.4799 90.5781C42.1519 90.2421 41.6343 90.1796 41.2357 90.4278L40.0554 91.1629C35.17 94.2052 28.7652 92.9674 25.3652 88.3238L24.5794 87.2505C24.438 87.0574 24.2319 86.9213 23.9988 86.8672L20.9975 86.1701C17.1041 85.2657 14.0075 82.3205 12.9095 78.4772L10.196 68.98C10.1612 68.8582 10.1036 68.7441 10.0262 68.6439L3.71552 60.4639Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsxs61("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx106(
            "path",
            {
              d: "M40.6875 37.5C40.6875 44.6797 34.8672 50.5 27.6875 50.5C20.5078 50.5 14.6875 44.6797 14.6875 37.5C14.6875 30.3203 20.5078 24.5 27.6875 24.5C34.8672 24.5 40.6875 30.3203 40.6875 37.5Z",
              fill: color,
              "data-testid": "path-with-color"
            }
          ),
          /* @__PURE__ */ jsx106(
            "path",
            {
              d: "M100.688 62.8418C100.688 70.0215 94.8672 75.8418 87.6875 75.8418C80.5078 75.8418 74.6875 70.0215 74.6875 62.8418C74.6875 55.6621 80.5078 49.8418 87.6875 49.8418C94.8672 49.8418 100.688 55.6621 100.688 62.8418Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx106(
            "path",
            {
              d: "M34.6973 84.5C38.5687 84.5 41.707 81.3616 41.707 77.4902C41.707 73.6189 38.5687 70.4805 34.6973 70.4805C30.8259 70.4805 27.6875 73.6189 27.6875 77.4902C27.6875 81.3616 30.8259 84.5 34.6973 84.5Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx106(
            "path",
            {
              d: "M48.707 60.5C48.707 63.2614 46.4685 65.5 43.707 65.5C40.9456 65.5 38.707 63.2614 38.707 60.5C38.707 57.7386 40.9456 55.5 43.707 55.5C46.4685 55.5 48.707 57.7386 48.707 60.5Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx106(
            "path",
            {
              d: "M55.7529 77.4902C58.5144 77.4902 60.7529 75.2517 60.7529 72.4902C60.7529 69.7288 58.5144 67.4902 55.7529 67.4902C52.9915 67.4902 50.7529 69.7288 50.7529 72.4902C50.7529 75.2517 52.9915 77.4902 55.7529 77.4902Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var MarketingIntelligenceIconDefault_default = MarketingIntelligenceIconDefault;

// src/icons/MarketIntelligenceIcon/Variants/MarketingIntelligenceIconOutline.tsx
import { jsx as jsx107, jsxs as jsxs62 } from "react/jsx-runtime";
var MarketingIntelligenceIconOutline = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "market-intelligence-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx107(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs62("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx107(
          "path",
          {
            d: "M48.832 60.5C48.832 63.2614 46.5935 65.5 43.832 65.5C41.0706 65.5 38.832 63.2614 38.832 60.5C38.832 57.7386 41.0706 55.5 43.832 55.5C46.5935 55.5 48.832 57.7386 48.832 60.5Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx107(
          "path",
          {
            d: "M55.8779 77.4902C58.6394 77.4902 60.8779 75.2517 60.8779 72.4902C60.8779 69.7288 58.6394 67.4902 55.8779 67.4902C53.1165 67.4902 50.8779 69.7288 50.8779 72.4902C50.8779 75.2517 53.1165 77.4902 55.8779 77.4902Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx107(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M100.969 23.9602C105.034 15.9984 116.552 16.4266 120.014 24.6683C120.994 27.0006 121.1 29.6082 120.311 32.012L111.816 57.9177C111.509 58.8554 111.077 59.7474 110.531 60.5698L96.7636 81.3297C96.5085 81.7145 96.5498 82.2239 96.8637 82.5624L100.479 86.4611C100.638 86.6329 100.785 86.8155 100.919 87.0079L104.32 91.9035C110.411 100.67 101.701 112.102 91.632 108.555L88.2235 107.354C86.2805 106.67 84.5687 105.454 83.2831 103.844L73.8444 92.0262C73.5569 91.6662 73.0606 91.5492 72.6426 91.743L67.4038 94.1708C67.3496 94.1959 67.2977 94.2259 67.2489 94.2602L62.4289 97.6511C58.0146 100.757 51.9992 100.201 48.2288 96.3386L42.6049 90.5781C42.2769 90.2421 41.7593 90.1796 41.3607 90.4278L40.1804 91.1628C35.295 94.2052 28.8902 92.9674 25.4902 88.3238L24.7044 87.2505C24.5629 87.0573 24.3569 86.9213 24.1238 86.8672L21.1225 86.1701C17.229 85.2657 14.1325 82.3205 13.0345 78.4772L10.321 68.98C10.2862 68.8582 10.2286 68.7441 10.1512 68.6439L3.84052 60.4639C1.31517 57.1906 0.838489 52.7776 2.60658 49.0405L13.4076 26.2109C15.407 21.9848 19.8592 19.4843 24.5085 19.9763L55.0869 23.2122C58.7456 23.5994 61.7514 26.183 62.7297 29.6574C67.9182 28.9796 72.7163 33.5721 71.6914 39.1534L71.5038 40.1752L72.4799 39.1456C76.5225 34.8814 82.8178 34.7236 87.1074 37.9032C88.424 33.353 92.6139 30.262 97.3247 30.262C97.5866 30.262 97.8263 30.1152 97.9454 29.882L100.969 23.9602ZM110.06 28.2911C110.019 28.3138 109.951 28.3591 109.875 28.5073L106.852 34.4291C105.024 38.0089 101.344 40.262 97.3247 40.262C96.9836 40.262 96.7543 40.4881 96.7021 40.7274C96.2223 42.9271 95.7381 45.2738 95.3974 47.1892C95.2261 48.1522 95.101 48.9513 95.0292 49.5431C94.9923 49.8473 94.9794 50.0232 94.9742 50.0998C95.0078 50.4803 95.0226 51.1453 94.8274 51.895C97.1875 53.4105 99.0254 55.6684 100.012 58.3389L102.197 55.0429C102.247 54.9681 102.286 54.8871 102.314 54.8018L110.809 28.8961C110.847 28.7801 110.842 28.6542 110.795 28.5415C110.73 28.3881 110.666 28.3378 110.627 28.3121C110.568 28.2738 110.472 28.2357 110.346 28.231C110.22 28.2263 110.121 28.2571 110.06 28.2911ZM88.413 75.8282C88.214 75.8372 88.0138 75.8418 87.8125 75.8418C80.6328 75.8418 74.8125 70.0215 74.8125 62.8418C74.8125 57.0592 78.588 52.1585 83.8084 50.4701C83.1003 49.2566 82.2821 47.7948 81.4596 46.2978C81.0737 45.5954 80.1837 45.5543 79.7369 46.0256L76.9735 48.9405C76.8784 49.0408 76.7791 49.1372 76.6759 49.2294L70.2694 54.952C64.7223 59.907 56.7643 53.2959 60.0656 47.0968L61.3428 40.1411C57.4815 40.1928 53.8984 37.4358 53.2461 33.313L53.2075 33.0691L39.4039 31.6084C40.3046 33.377 40.8125 35.3792 40.8125 37.5C40.8125 44.6797 34.9922 50.5 27.8125 50.5C22.8026 50.5 18.4545 47.666 16.2841 43.5137L11.646 53.3171C11.4852 53.6569 11.5286 54.0581 11.7581 54.3556L18.0688 62.5355C18.9198 63.6386 19.5535 64.8932 19.9362 66.2328L22.6497 75.73C22.7495 76.0794 23.031 76.3472 23.385 76.4294L26.3862 77.1265C26.8735 77.2396 27.35 77.3853 27.8129 77.5616C27.8126 77.5379 27.8125 77.5141 27.8125 77.4902C27.8125 73.6189 30.9509 70.4805 34.8223 70.4805C38.6937 70.4805 41.832 73.6189 41.832 77.4902C41.832 78.4874 41.6238 79.436 41.2485 80.2948C44.3473 80.1154 47.4728 81.2494 49.7603 83.5924L55.3842 89.3529C55.727 89.704 56.2738 89.7545 56.6751 89.4722L61.4951 86.0814C62.0325 85.7033 62.6028 85.3741 63.199 85.0978L68.4378 82.6699C73.0361 80.5389 78.4953 81.8255 81.6581 85.7855L91.0969 97.6034C91.2137 97.7497 91.3693 97.8603 91.546 97.9225L94.9544 99.1232C95.276 99.2365 95.4638 99.203 95.5827 99.1635C95.7383 99.1118 95.9257 98.9913 96.0821 98.7861C96.2385 98.5808 96.3049 98.3682 96.3135 98.2044C96.32 98.0794 96.3024 97.8894 96.1079 97.6094L92.9071 93.0026L89.531 89.3617C86.0851 85.6455 85.625 80.0572 88.413 75.8282Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var MarketingIntelligenceIconOutline_default = MarketingIntelligenceIconOutline;

// src/icons/MarketIntelligenceIcon/MarketIntelligenceIcon.tsx
import { jsx as jsx108 } from "react/jsx-runtime";
var MarketIntelligenceIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx108(MarketingIntelligenceIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx108(MarketingIntelligenceIconOutline_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var MarketIntelligenceIcon_default = MarketIntelligenceIcon;

// src/icons/MinusIcon/MinusIcon.tsx
import { jsx as jsx109 } from "react/jsx-runtime";
var MinusIcon = ({
  width = 48,
  height = 9,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#59595B",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "minus-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx109(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 48 9",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx109("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx109(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M44 0.948242C46.2091 0.948242 48 2.73914 48 4.94824C48 7.15741 46.2091 8.94824 44 8.94824C20.5545 8.94833 29.9786 8.94834 4 8.94824C1.79086 8.94824 0 7.15741 0 4.94824C0 2.73914 1.79086 0.948242 4 0.948242C29.1684 0.948242 21.2146 0.948329 44 0.948242Z",
          fill: color
        }
      ) })
    }
  );
};
var MinusIcon_default = MinusIcon;

// src/icons/NetworkIcon/NetworkIcon.tsx
import { jsx as jsx110, jsxs as jsxs63 } from "react/jsx-runtime";
var NetworkIcon = ({
  width = 45,
  height = 49,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#44546A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "network-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx110(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 45 49",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs63("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx110(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M22.5 0C19.4802 0 17.0461 2.38896 17.0461 5.35274C17.0461 8.31653 19.4802 10.7055 22.5 10.7055C25.5198 10.7055 27.9539 8.31653 27.9539 5.35274C27.9539 2.38896 25.5198 0 22.5 0ZM20.3778 5.35274C20.3778 4.18977 21.3151 3.26988 22.5 3.26988C23.6849 3.26988 24.6222 4.18977 24.6222 5.35274C24.6222 6.51572 23.6849 7.43561 22.5 7.43561C21.3151 7.43561 20.3778 6.51572 20.3778 5.35274Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            d: "M16.4064 8.44097C15.9555 7.66653 14.9102 7.40163 14.1309 7.83867L14.1291 7.83974L12.1599 8.95471C11.3718 9.39763 11.1023 10.4229 11.5474 11.1873L11.5532 11.1968C11.8795 11.7306 12.4339 12.0067 12.992 12.0067C13.2568 12.0067 13.543 11.9534 13.8284 11.7865L15.7937 10.6736C16.582 10.2308 16.8515 9.20548 16.4064 8.44097Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            d: "M30.8691 7.83867C30.0898 7.40163 29.0445 7.66653 28.5936 8.44097C28.1485 9.20552 28.4189 10.2314 29.2073 10.6742L31.1716 11.7864C31.457 11.9534 31.7432 12.0067 32.008 12.0067C32.533 12.0067 33.1575 11.7537 33.4597 11.1748C33.8947 10.4117 33.6228 9.39449 32.8388 8.95401L30.871 7.83974L30.8691 7.83867Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M5.45385 9.29451C2.43408 9.29451 0 11.6835 0 14.6473C0 17.611 2.43408 20 5.45385 20C8.47362 20 10.9077 17.611 10.9077 14.6473C10.9077 11.6835 8.47362 9.29451 5.45385 9.29451ZM3.33164 14.6473C3.33164 13.4843 4.26891 12.5644 5.45385 12.5644C6.6388 12.5644 7.57606 13.4843 7.57606 14.6473C7.57606 15.8102 6.6388 16.7301 5.45385 16.7301C4.26891 16.7301 3.33164 15.8102 3.33164 14.6473Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M39.5461 9.29451C36.5264 9.29451 34.0923 11.6835 34.0923 14.6473C34.0923 17.611 36.5264 20 39.5461 20C42.5659 20 45 17.611 45 14.6473C45 11.6835 42.5659 9.29451 39.5461 9.29451ZM37.4239 14.6473C37.4239 13.4843 38.3612 12.5644 39.5461 12.5644C40.7311 12.5644 41.6684 13.4843 41.6684 14.6473C41.6684 15.8102 40.7311 16.7301 39.5461 16.7301C38.3612 16.7301 37.4239 15.8102 37.4239 14.6473Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M22.8511 16.618C17.5373 16.618 13.2157 20.9396 13.2157 26.2534C13.2157 28.4013 13.9226 30.3869 15.1139 31.9898C11.1151 32.484 8.01079 35.9023 8.01079 40.0333V46.7689C8.01048 47.8627 8.89729 48.7491 9.9907 48.7491L16.4019 48.7491L16.4337 48.75L16.4517 48.7491L29.2619 48.7491L29.294 48.75L29.3125 48.7491H35.7112C36.805 48.7491 37.6914 47.8624 37.6914 46.7689V40.0333C37.6914 35.9023 34.5871 32.484 30.5883 31.9898C31.7796 30.3869 32.4865 28.4013 32.4865 26.2534C32.4865 20.9396 28.1649 16.618 22.8511 16.618ZM17.1759 26.2534C17.1759 23.1251 19.7225 20.5784 22.8509 20.5784C25.9794 20.5784 28.5259 23.1249 28.5259 26.2534C28.5259 29.3819 25.9794 31.9284 22.8509 31.9284C19.7224 31.9284 17.1759 29.3819 17.1759 26.2534ZM11.9709 40.0333C11.9709 37.7486 13.8305 35.8888 16.1138 35.8888H29.5878C31.8712 35.8888 33.7306 37.7486 33.7306 40.0333V44.7886H31.2613V43.7073C31.2613 42.6137 30.3749 41.727 29.281 41.727C28.1872 41.727 27.3008 42.6137 27.3008 43.7073V44.7886H18.4007V43.7073C18.4007 42.6137 17.5144 41.727 16.4205 41.727C15.3267 41.727 14.4403 42.6137 14.4403 43.7073V44.7886H11.9709V40.0333Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            d: "M8.98974 23.071C8.34358 22.4356 7.25185 22.4233 6.60635 23.0694C5.96909 23.7073 5.96218 24.7904 6.61571 25.4339L8.24164 27.0459C8.47648 27.2853 8.74215 27.4129 9.00091 27.4823C9.51412 27.6198 10.1914 27.5337 10.6399 27.0416C11.2668 26.4025 11.27 25.3275 10.6202 24.6875L8.99131 23.0726L8.98974 23.071Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx110(
          "path",
          {
            d: "M38.1996 25.4368C38.835 24.7907 38.8473 23.6989 38.2012 23.0534C37.5633 22.4162 36.4802 22.4093 35.8367 23.0628L34.2247 24.6887C33.9852 24.9236 33.8576 25.1892 33.7883 25.448C33.6508 25.9612 33.7369 26.6385 34.229 27.087C34.8681 27.7139 35.9431 27.7171 36.5831 27.0673L38.198 25.4384L38.1996 25.4368Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var NetworkIcon_default = NetworkIcon;

// src/icons/NetworkManagementIcon/Variant/NetworkManagementIconDefault.tsx
import { jsx as jsx111, jsxs as jsxs64 } from "react/jsx-runtime";
var NetworkManagementDefaultIcon = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "network-management-icon",
  // Extra Properties
  variant,
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx111(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs64("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsxs64("g", { opacity: backgroundOpacity, "data-testid": "g-background-path", children: [
          /* @__PURE__ */ jsx111(
            "path",
            {
              "data-testid": "path-with-background-color",
              d: "M80.6875 60.5C80.6875 71.5457 71.7332 80.5 60.6875 80.5C49.6418 80.5 40.6875 71.5457 40.6875 60.5C40.6875 49.4543 49.6418 40.5 60.6875 40.5C71.7332 40.5 80.6875 49.4543 80.6875 60.5Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M31.6875 39.5801C31.6875 47.8643 24.9718 54.5801 16.6875 54.5801C8.40323 54.5801 1.6875 47.8643 1.6875 39.5801C1.6875 31.2958 8.40323 24.5801 16.6875 24.5801C24.9718 24.5801 31.6875 31.2958 31.6875 39.5801Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M73.6875 16.5C73.6875 24.7843 66.9718 31.5 58.6875 31.5C50.4032 31.5 43.6875 24.7843 43.6875 16.5C43.6875 8.21573 50.4032 1.5 58.6875 1.5C66.9718 1.5 73.6875 8.21573 73.6875 16.5Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M119.688 17.5C119.688 25.7843 112.972 32.5 104.688 32.5C96.4032 32.5 89.6875 25.7843 89.6875 17.5C89.6875 9.21573 96.4032 2.5 104.688 2.5C112.972 2.5 119.688 9.21573 119.688 17.5Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M117.688 77.5C117.688 85.7843 110.972 92.5 102.688 92.5C94.4032 92.5 87.6875 85.7843 87.6875 77.5C87.6875 69.2157 94.4032 62.5 102.688 62.5C110.972 62.5 117.688 69.2157 117.688 77.5Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M37.6875 83.5C37.6875 91.7843 30.9718 98.5 22.6875 98.5C14.4032 98.5 7.6875 91.7843 7.6875 83.5C7.6875 75.2157 14.4032 68.5 22.6875 68.5C30.9718 68.5 37.6875 75.2157 37.6875 83.5Z",
              fill: backgroundColor
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M78.3989 104.5C78.3989 112.784 71.6832 119.5 63.3989 119.5C55.1147 119.5 48.3989 112.784 48.3989 104.5C48.3989 96.2157 55.1147 89.5 63.3989 89.5C71.6832 89.5 78.3989 96.2157 78.3989 104.5Z",
              fill: backgroundColor
            }
          )
        ] }),
        /* @__PURE__ */ jsxs64("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: [
          /* @__PURE__ */ jsx111(
            "path",
            {
              "data-testid": "path-with-color",
              d: "M63.7816 30.6131C62.1913 31.1872 60.476 31.5002 58.6877 31.5002C57.339 31.5002 56.0319 31.3222 54.7885 30.9884L55.3632 41.2167C57.0585 40.7497 58.844 40.5002 60.6877 40.5002C61.9407 40.5002 63.1668 40.6154 64.356 40.8358L63.7816 30.6131Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M71.8927 43.9313C74.4034 45.6325 76.5069 47.8894 78.0266 50.5253L97.7488 30.8022C95.0323 29.3823 92.8049 27.1546 91.3851 24.438L71.8927 43.9313Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M80.2235 64.804C79.5589 67.8345 78.2071 70.6076 76.3398 72.9518L87.6886 77.3388C87.7222 74.1447 88.7543 71.1899 90.4874 68.7717L80.2235 64.804Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M66.0265 79.7797C64.3269 80.2493 62.5366 80.5002 60.6877 80.5002C59.4401 80.5002 58.2191 80.3859 57.0347 80.1673L57.4409 90.7301C59.2673 89.9388 61.282 89.5002 63.3991 89.5002C64.431 89.5002 65.4386 89.6044 66.4119 89.8028L66.0265 79.7797Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M46.6112 74.7076C44.4733 72.5893 42.811 69.9918 41.7986 67.0896L32.6335 72.2714C34.8943 74.2753 36.5455 76.9514 37.274 79.9866L46.6112 74.7076Z",
              fill: color
            }
          ),
          /* @__PURE__ */ jsx111(
            "path",
            {
              d: "M40.9603 57.1902C41.4732 54.111 42.6895 51.2697 44.4385 48.8372L31.522 41.8173C31.0565 44.9304 29.6345 47.7307 27.5645 49.9097L40.9603 57.1902Z",
              fill: color
            }
          )
        ] })
      ] })
    }
  );
};
var NetworkManagementIconDefault_default = NetworkManagementDefaultIcon;

// src/icons/NetworkManagementIcon/Variant/NetworkManagementIconOutlined.tsx
import { jsx as jsx112, jsxs as jsxs65 } from "react/jsx-runtime";
var NetworkManagementOutlinedIcon = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "network-management-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx112(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs65("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx112(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M60.4551 89.7444C59.6199 89.8969 58.8088 90.1186 58.028 90.4032C52.2745 92.501 48.1665 98.0208 48.1665 104.5C48.1665 112.784 54.8822 119.5 63.1665 119.5C71.4508 119.5 78.1665 112.784 78.1665 104.5C78.1665 97.377 73.2017 91.4137 66.5438 89.8817C65.4583 89.632 64.3278 89.5 63.1665 89.5C62.2407 89.5 61.3345 89.5839 60.4551 89.7444ZM68.1665 104.5C68.1665 107.261 65.9279 109.5 63.1665 109.5C60.4051 109.5 58.1665 107.261 58.1665 104.5C58.1665 101.739 60.4051 99.5 63.1665 99.5C65.9279 99.5 68.1665 101.739 68.1665 104.5Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx112(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M56.8023 80.1672L57.2085 90.7296C59.0348 89.9383 61.0493 89.5 63.1665 89.5C64.1984 89.5 65.2062 89.6039 66.1795 89.8023L65.794 79.7795C65.9142 79.7463 66.034 79.712 66.1532 79.6766C70.129 78.497 73.5876 76.1147 76.1074 72.9513L87.4559 77.3382C87.4554 77.3921 87.4551 77.446 87.4551 77.5C87.4551 85.7843 94.1708 92.5 102.455 92.5C110.739 92.5 117.455 85.7843 117.455 77.5C117.455 69.2157 110.739 62.5 102.455 62.5C97.4269 62.5 92.9765 64.974 90.255 68.7712L79.9911 64.8036C79.5955 66.6072 78.9565 68.3196 78.1104 69.9047C79.6068 67.1013 80.4551 63.8997 80.4551 60.5C80.4551 50.7232 73.4399 42.5849 64.1686 40.8441C62.9653 40.6182 61.724 40.5 60.4551 40.5C59.7801 40.5 59.1129 40.5334 58.4551 40.5988C57.2613 40.7173 56.0985 40.9408 54.9759 41.2598C50.5828 42.5085 46.806 45.2211 44.2061 48.8368L31.2896 41.8168C31.0026 43.736 30.352 45.5364 29.4102 47.1457C30.7101 44.9246 31.4551 42.3393 31.4551 39.5801C31.4551 31.2958 24.7393 24.5801 16.4551 24.5801C8.17081 24.5801 1.45508 31.2958 1.45508 39.5801C1.45508 47.8643 8.17081 54.5801 16.4551 54.5801C20.7366 54.5801 24.5991 52.7863 27.3321 49.9092L40.7278 57.1896C40.5484 58.2664 40.4551 59.3723 40.4551 60.5C40.4551 62.8087 40.8463 65.026 41.5659 67.0893L32.4011 72.2709C34.6618 74.2748 36.3131 76.951 37.0415 79.9861L46.3784 74.7073C49.1711 77.4745 52.7753 79.424 56.8023 80.1672ZM70.4551 60.5C70.4551 66.0229 65.9779 70.5 60.4551 70.5C54.9322 70.5 50.4551 66.0229 50.4551 60.5C50.4551 54.9772 54.9322 50.5 60.4551 50.5C65.9779 50.5 70.4551 54.9772 70.4551 60.5ZM21.4551 39.5801C21.4551 42.3415 19.2165 44.5801 16.4551 44.5801C13.6937 44.5801 11.4551 42.3415 11.4551 39.5801C11.4551 36.8187 13.6937 34.5801 16.4551 34.5801C19.2165 34.5801 21.4551 36.8187 21.4551 39.5801ZM107.455 77.5C107.455 80.2614 105.216 82.5 102.455 82.5C99.6937 82.5 97.4551 80.2614 97.4551 77.5C97.4551 74.7386 99.6937 72.5 102.455 72.5C105.216 72.5 107.455 74.7386 107.455 77.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx112(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M54.5561 30.9883C48.1622 29.2719 43.4551 23.4357 43.4551 16.5C43.4551 8.21573 50.1708 1.5 58.4551 1.5C66.7393 1.5 73.4551 8.21573 73.4551 16.5C73.4551 22.9959 69.326 28.5273 63.5492 30.6128L64.1236 40.8354C62.9344 40.6149 61.7081 40.5 60.4551 40.5C58.6113 40.5 56.8261 40.7492 55.1308 41.2162L54.5561 30.9883ZM63.4551 16.5C63.4551 19.2614 61.2165 21.5 58.4551 21.5C55.6937 21.5 53.4551 19.2614 53.4551 16.5C53.4551 13.7386 55.6937 11.5 58.4551 11.5C61.2165 11.5 63.4551 13.7386 63.4551 16.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx112(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M33.1824 73.0155C34.7746 74.6444 35.9989 76.6345 36.7208 78.8513C37.1974 80.3149 37.4551 81.8774 37.4551 83.5C37.4551 91.7843 30.7393 98.5 22.4551 98.5C14.1708 98.5 7.45508 91.7843 7.45508 83.5C7.45508 75.2157 14.1708 68.5 22.4551 68.5C26.659 68.5 30.459 70.2294 33.1824 73.0155ZM27.4551 83.5C27.4551 86.2614 25.2165 88.5 22.4551 88.5C19.6937 88.5 17.4551 86.2614 17.4551 83.5C17.4551 80.7386 19.6937 78.5 22.4551 78.5C25.2165 78.5 27.4551 80.7386 27.4551 83.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx112(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M104.455 32.5C112.739 32.5 119.455 25.7843 119.455 17.5C119.455 9.21573 112.739 2.5 104.455 2.5C96.1708 2.5 89.4551 9.21573 89.4551 17.5C89.4551 20.0031 90.0682 22.363 91.1525 24.4378L71.6603 43.9308C74.171 45.632 76.2745 47.8889 77.7942 50.5248L97.5161 30.802C99.5911 31.8866 101.951 32.5 104.455 32.5ZM109.455 17.5C109.455 20.2614 107.216 22.5 104.455 22.5C101.694 22.5 99.4551 20.2614 99.4551 17.5C99.4551 14.7386 101.694 12.5 104.455 12.5C107.216 12.5 109.455 14.7386 109.455 17.5Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var NetworkManagementIconOutlined_default = NetworkManagementOutlinedIcon;

// src/icons/NetworkManagementIcon/NetworkManagementIcon.tsx
import { jsx as jsx113 } from "react/jsx-runtime";
var NetworkManagementIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx113(NetworkManagementIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx113(NetworkManagementIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var NetworkManagementIcon_default = NetworkManagementIcon;

// src/icons/NoConnectionIcon/NoConnectionIcon.tsx
import { jsx as jsx114, jsxs as jsxs66 } from "react/jsx-runtime";
var NoConnectionIcon = ({
  width = 164,
  height = 205,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "no-connection-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx114(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 164 205",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsxs66("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M115.163 128.42L100.997 114.253C98.9944 112.251 95.7486 112.251 93.7464 114.253L86.9131 121.087C84.9109 123.089 84.9109 126.335 86.9131 128.337C86.9131 128.337 100.082 141.506 101.08 142.503L115.163 128.42Z",
            fill: "#CFD8DC"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M161.312 162.167C161.312 162.167 132.487 133.342 127.145 128L113.061 142.084L147.228 176.25C148.226 177.248 149.545 177.753 150.85 177.753C152.155 177.753 153.474 177.255 154.471 176.25L157.888 172.834L161.305 169.417C163.314 167.408 163.314 164.169 161.312 162.167Z",
            fill: "#CFD8DC"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M63.4133 45.1L50.1566 31.8433C44.4303 26.1033 34.1393 26.117 28.4266 31.8433L4.50997 55.7532C1.63997 58.6163 -3.05176e-05 62.5797 -3.05176e-05 66.625C-3.05176e-05 70.6703 1.63997 74.6337 4.50997 77.49L17.7666 90.7467C18.7301 91.7102 20.0353 92.25 21.3883 92.25C22.7413 92.25 24.0533 91.7102 25.01 90.7467L63.4133 52.3433C64.3768 51.3867 64.9166 50.0815 64.9166 48.7217C64.9166 47.3618 64.3768 46.0567 63.4133 45.1Z",
            fill: "#CFD8DC"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M105.848 92.3184L98.6459 85.116L97.2861 72.6657C96.9034 68.4222 94.9969 64.3837 91.9083 61.295L75.0983 44.485C70.6088 39.9887 62.6411 39.9887 58.1516 44.485L17.1516 85.4782C14.9103 87.7195 13.6666 90.733 13.6666 93.9584C13.6666 97.1837 14.9103 100.197 17.1516 102.432L33.9548 119.242C37.0434 122.33 41.0819 124.237 45.2298 124.606L57.7826 125.979L64.9849 133.182C67.2263 135.43 70.2466 136.667 73.4583 136.667C76.6768 136.667 79.6903 135.43 81.9316 133.182L105.848 109.272C108.09 107.031 109.333 104.01 109.333 100.792C109.333 97.5732 108.09 94.5529 105.848 92.3184Z",
            fill: "#607D8B"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M15.3749 61.5C16.6801 61.5 17.9989 61.0012 18.9966 59.9967C20.9988 57.9945 20.9988 54.7487 18.9966 52.7465L13.2566 47.0134L6.00644 54.2567L11.7533 59.9967C12.7509 61.0012 14.0698 61.5 15.3749 61.5Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M29.0417 47.8333C30.3469 47.8333 31.6657 47.3345 32.6634 46.33C34.6655 44.3278 34.6655 41.082 32.6634 39.0798L26.9234 33.3467L19.68 40.59L25.42 46.33C26.4177 47.3345 27.7365 47.8333 29.0417 47.8333Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M93.8422 121.278L86.8517 128.262C86.8722 128.282 86.8927 128.31 86.92 128.33L101.087 142.497L108.057 135.5L93.8422 121.278Z",
            fill: "#B4BCC0"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M119.99 135.025L113 142.008C113.02 142.029 113.041 142.056 113.068 142.077L147.235 176.243C148.233 177.241 149.551 177.747 150.857 177.747C152.162 177.747 153.481 177.248 154.478 176.243L157.84 172.881L119.99 135.025Z",
            fill: "#B4BCC0"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M16.4136 43.8563L6.65564 53.6075L13.2566 47.0133L18.9966 52.7533C20.9988 54.7555 20.9988 58.0013 18.9966 60.0035C17.999 61.0012 16.6801 61.5 15.375 61.5C14.0698 61.5 12.751 61.0012 11.7533 59.9967L6.00647 54.2567L4.50997 55.7532C1.63997 58.6163 -3.05176e-05 62.5797 -3.05176e-05 66.625C-3.05176e-05 70.6703 1.63997 74.6337 4.50997 77.49L15.1495 88.1363C15.6825 87.1728 16.3521 86.2777 17.1516 85.4782L37.597 65.0397L16.4136 43.8563Z",
            fill: "#B4BCC0"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M37.5969 65.0328L17.1516 85.4782C16.3521 86.2777 15.6824 87.166 15.1494 88.1363C14.1859 89.8857 13.6666 91.881 13.6666 93.9583C13.6666 97.1837 14.9103 100.197 17.1516 102.432L33.9548 119.242C37.0434 122.33 41.0819 124.237 45.2298 124.606L57.7826 125.979L64.9849 133.182C67.2263 135.43 70.2466 136.667 73.4583 136.667C76.6768 136.667 79.6903 135.43 81.9316 133.182L86.8516 128.262L93.8421 121.278L37.5969 65.0328Z",
            fill: "#546D79"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M13.2566 47.0134L6.65561 53.6075L6.00644 54.2567L11.7533 59.9967C12.7509 61.0012 14.0698 61.5 15.3749 61.5C16.6801 61.5 17.9989 61.0012 18.9966 59.9967C20.9988 57.9945 20.9988 54.7487 18.9966 52.7465L13.2566 47.0134Z",
            fill: "#DEDEDE"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M162.251 64.2675L126.164 5.65117C124.107 2.15933 120.28 0 116.167 0C112.053 0 108.226 2.15933 106.217 5.56233L70.192 64.0967C68.9757 65.9075 68.3333 68.0668 68.3333 70.356C68.3333 76.7725 73.5813 82 80.032 82H152.301C158.752 82 164 76.7725 164 70.356C164 68.0668 163.358 65.9075 162.251 64.2675Z",
            fill: "#FFC107"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M116.167 54.2772C113.338 54.2772 111.042 51.9812 111.042 49.1522V26.3289C111.042 23.4999 113.338 21.2039 116.167 21.2039C118.996 21.2039 121.292 23.4999 121.292 26.3289V49.159C121.292 51.9812 118.996 54.2772 116.167 54.2772Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M121.292 63.8302C121.292 66.6592 118.996 68.9552 116.167 68.9552C113.338 68.9552 111.042 66.6592 111.042 63.8302C111.042 61.0012 113.338 58.7052 116.167 58.7052C118.996 58.7052 121.292 61.0012 121.292 63.8302Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M116.167 0C112.053 0 108.226 2.15933 106.217 5.56233L70.192 64.0967C68.9757 65.9075 68.3333 68.0668 68.3333 70.356C68.3333 76.7725 73.5813 82 80.032 82H116.167V68.9552C113.338 68.9552 111.042 66.6592 111.042 63.8302C111.042 61.0012 113.338 58.7052 116.167 58.7052V54.2772C113.338 54.2772 111.042 51.9812 111.042 49.1522V26.3288C111.042 23.4998 113.338 21.2038 116.167 21.2038V0Z",
            fill: "#DEA806"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M116.167 21.2039C113.338 21.2039 111.042 23.4999 111.042 26.3289V49.159C111.042 51.988 113.338 54.284 116.167 54.284V21.2039Z",
            fill: "#DEDEDE"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M116.167 58.7052C113.338 58.7052 111.042 61.0012 111.042 63.8302C111.042 66.6592 113.338 68.9552 116.167 68.9552V58.7052Z",
            fill: "#DEDEDE"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M102 149L94 157",
            stroke: "#DEA806",
            strokeWidth: "3",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M135 114L127 122",
            stroke: "#FFC107",
            strokeWidth: "3",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M107.657 156L107.657 167.314",
            stroke: "#DEA806",
            strokeWidth: "3",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx114(
          "path",
          {
            d: "M119.657 103L119.657 114.314",
            stroke: "#FFC107",
            strokeWidth: "3",
            strokeLinecap: "round",
            strokeLinejoin: "round"
          }
        ),
        /* @__PURE__ */ jsx114("circle", { cx: "44.5", cy: "196.5", r: "8.5", fill: "#015F94" }),
        /* @__PURE__ */ jsx114("circle", { cx: "71.5", cy: "196.5", r: "8.5", fill: "#015F94" }),
        /* @__PURE__ */ jsx114("circle", { cx: "98.5", cy: "196.5", r: "8.5", fill: "#015F94" }),
        /* @__PURE__ */ jsx114("circle", { cx: "122.5", cy: "196.5", r: "5.5", fill: "#015F94" })
      ] })
    }
  );
};
var NoConnectionIcon_default = NoConnectionIcon;

// src/icons/NoResultsIcon/NoResultsIcon.tsx
import { jsx as jsx115, jsxs as jsxs67 } from "react/jsx-runtime";
var NoResultsIcon2 = ({
  width = 168,
  height = 187,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "no-results-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx115(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 168 187",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsxs67("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M164.091 2.40881H84.6022V21.6789H164.091V2.40881Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M84.6022 2.40881H3.90881V21.6789H84.6022V2.40881Z",
            fill: "#26ACD8"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M84.6022 22.8832H3.90881V125.255H84.6022V22.8832Z",
            fill: "#F6F8FA"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M165.296 21.6788H84.6022V124.051H165.296V21.6788Z",
            fill: "#EAEBEC"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M95.5392 121.834H4.89273V25.2849H163.106V122.861C163.106 124.139 164.135 125.174 165.405 125.174C166.675 125.174 167.704 124.139 167.704 122.861V2.31312C167.704 1.03557 166.675 0 165.405 0H2.59485C1.32499 0 0.295654 1.03557 0.295654 2.31312V124.147C0.295654 125.424 1.32499 126.46 2.59485 126.46L91.2547 126.458H95.5391C96.8089 126.458 97.8383 125.423 97.8383 124.145C97.8367 122.869 96.8075 121.834 95.5392 121.834ZM163.11 4.62558V20.6575L4.89643 20.6592V4.62719L163.11 4.62558Z",
            fill: "#CFCFCD"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M16.5548 15.6569C18.2149 15.6569 19.5657 14.3068 19.5657 12.646C19.5657 10.9859 18.2149 9.63501 16.5548 9.63501C14.8947 9.63501 13.5438 10.9859 13.5438 12.646C13.5438 14.306 14.8947 15.6569 16.5548 15.6569Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M28.5986 15.6569C30.2588 15.6569 31.6095 14.3068 31.6095 12.646C31.6095 10.9859 30.2587 9.63501 28.5986 9.63501C26.9385 9.63501 25.5876 10.9859 25.5876 12.646C25.5876 14.306 26.9385 15.6569 28.5986 15.6569Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M40.6424 15.6569C42.3026 15.6569 43.6534 14.3068 43.6534 12.646C43.6534 10.9859 42.3025 9.63501 40.6424 9.63501C38.9823 9.63501 37.6315 10.9859 37.6315 12.646C37.6315 14.306 38.9823 15.6569 40.6424 15.6569Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M106.256 126.899L123.632 115.894L145.933 151.104L128.556 162.109L106.256 126.899Z",
            fill: "#DDE8EE"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M106.256 126.899L115.103 121.296L137.404 156.506L128.556 162.109L106.256 126.899Z",
            fill: "#EBF7FC"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M83.3979 114.416C106.013 114.416 124.347 96.0825 124.347 73.4671C124.347 50.8516 106.013 32.5182 83.3979 32.5182C60.7824 32.5182 42.449 50.8516 42.449 73.4671C42.449 96.0825 60.7824 114.416 83.3979 114.416Z",
            fill: "#F6F8FA"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M84.0001 73.4671C84.0001 96.0825 84.6023 88.5218 84.6023 114.416C61.9868 114.416 42.449 96.0825 42.449 73.4671C42.449 50.8516 61.9868 32.5182 84.6023 32.5182C84.6023 49.9817 84.0001 50.8516 84.0001 73.4671Z",
            fill: "white"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M84 105.985C102.292 105.985 117.12 91.1568 117.12 72.8649C117.12 54.573 102.292 39.7445 84 39.7445C65.7081 39.7445 50.8796 54.573 50.8796 72.8649C50.8796 91.1568 65.7081 105.985 84 105.985Z",
            fill: "#DDE8EE"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M84.0001 73.4671C84.0001 91.759 84.0001 87.9197 84.0001 105.985C65.7082 105.985 50.8796 91.1569 50.8796 72.8649C50.8796 54.573 65.7082 39.7445 84.0001 39.7445C84.0001 56.6058 84.0001 55.1752 84.0001 73.4671Z",
            fill: "#EBF7FC"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M149.265 149.426L128.924 118.271C127.556 116.173 125.444 114.732 122.979 114.212C121.703 113.942 120.414 113.95 119.174 114.197L113.18 105.016C113.112 104.914 113.012 104.853 112.932 104.765C119.472 98.7793 124.013 90.9831 125.906 82.1744C128.321 70.9346 126.18 59.4331 119.88 49.7957C118.006 46.9031 115.717 44.195 113.086 41.7496C106.344 35.4405 97.5111 31.4053 88.219 30.3896C78.5822 29.3031 69.1368 31.3944 60.995 36.3701C60.5938 36.5912 60.2378 36.8012 59.8657 37.0383C59.7915 37.0848 59.7223 37.1345 59.6562 37.189C54.4269 40.5655 50.0837 44.9466 46.8071 50.121C46.5591 50.472 46.3544 50.8117 46.1176 51.2043C42.7475 56.8369 40.692 63.2728 40.1731 69.8174C39.6625 76.2467 40.6291 82.7831 42.9617 88.6979C44.0282 91.4927 45.3927 94.1562 47.0101 96.6033C55.3099 109.33 69.2692 116.266 83.5052 116.266C89.2357 116.266 95.0045 115.117 100.472 112.797C100.521 112.901 100.533 113.015 100.598 113.114L106.587 122.288C105.848 123.319 105.313 124.489 105.042 125.763C104.524 128.202 104.996 130.695 106.367 132.775L126.708 163.954C127.15 164.631 127.891 165 128.648 165C129.078 165 129.513 164.881 129.9 164.631L148.584 152.608C149.1 152.276 149.46 151.754 149.589 151.159C149.715 150.561 149.599 149.938 149.265 149.426ZM50.8765 94.0931C49.4363 91.9153 48.2265 89.5517 47.2711 87.0468C45.1833 81.7522 44.3215 75.9206 44.7773 70.1805C45.2397 64.3346 47.0762 58.5863 50.1016 53.5321C50.261 53.2677 50.406 53.0209 50.6493 52.6699C53.6554 47.9267 57.5716 43.9892 62.2904 40.9686C62.3565 40.9254 62.4209 40.8805 62.4821 40.8308C62.7286 40.6786 62.9799 40.5311 63.3215 40.3404C70.6644 35.8549 79.1028 33.9928 87.7069 34.9577C96.0149 35.8647 103.902 39.4654 109.926 45.1029C112.286 47.2952 114.328 49.7116 116.002 52.2949C121.632 60.9067 123.545 71.1771 121.389 81.2153C119.225 91.2773 113.237 99.8989 104.523 105.492L104.522 105.493C86.5737 117.049 62.5136 111.934 50.8753 94.0931H50.8765ZM107.03 109.352C107.854 108.825 108.622 108.238 109.4 107.663L114.934 116.139L110.103 119.238L104.593 110.798C105.413 110.341 106.228 109.868 107.03 109.352ZM129.325 159.525L110.233 130.261C109.536 129.205 109.297 127.944 109.558 126.712C109.824 125.471 110.563 124.406 111.749 123.649L118.409 119.374C119.197 118.863 120.095 118.6 121.015 118.6C121.349 118.6 121.685 118.635 122.021 118.706C123.277 118.97 124.352 119.704 125.051 120.774L144.13 149.998L129.325 159.525Z",
            fill: "#59595B"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M117.544 80.0152C119.493 71.0606 117.763 61.8873 112.681 54.1941C111.188 51.9099 109.343 49.752 107.205 47.7863C105.379 46.096 103.358 44.5993 101.194 43.3394C96.9706 40.8775 92.1333 39.2944 87.2086 38.7629C79.476 37.8985 71.8995 39.5617 65.3082 43.5459L64.336 44.1286C60.0955 46.8193 56.5743 50.3232 53.8751 54.5378L53.3581 55.3398C50.6477 59.8248 48.9927 64.9504 48.5765 70.1624C48.1668 75.2752 48.9537 80.4983 50.8446 85.246C51.6933 87.4486 52.7843 89.5536 54.08 91.4919C57.0407 95.9884 60.899 99.6396 65.5424 102.348C71.0864 105.58 77.239 107.19 83.3795 107.19C89.9741 107.19 96.5554 105.335 102.361 101.637C110.201 96.6523 115.593 88.9747 117.544 80.0152ZM99.83 97.7784C90.1092 103.968 77.8827 104.207 67.9142 98.3963C63.8951 96.0528 60.5554 92.8896 57.9802 88.9807C56.8633 87.3096 55.9284 85.5024 55.1903 83.5913C53.5465 79.4664 52.8669 74.9473 53.2213 70.5213C53.5823 66.0071 55.0147 61.5684 57.325 57.7427L57.8095 56.9904C60.1492 53.3391 63.193 50.3089 66.807 48.017L67.737 47.4599C72.462 44.6042 77.7671 43.1318 83.2542 43.1318C84.3939 43.1318 85.5401 43.1958 86.6912 43.3239C90.9687 43.7849 95.1605 45.1567 98.817 47.2888C100.685 48.3773 102.433 49.6707 104.015 51.1369C105.878 52.8497 107.475 54.7145 108.766 56.6899C113.173 63.3599 114.671 71.3027 112.983 79.0566C111.295 86.8121 106.624 93.4609 99.83 97.7784Z",
            fill: "#59595B"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M98.1106 58.522C97.0524 57.4637 95.3366 57.4637 94.2783 58.522L83.7677 69.0326L73.4895 58.7544C72.4312 57.6962 70.7154 57.6962 69.6572 58.7544C68.5989 59.8127 68.5989 61.5285 69.6572 62.5868L79.9353 72.8649L69.3683 83.432C68.31 84.4903 68.31 86.206 69.3683 87.2643C70.4265 88.3226 72.1423 88.3226 73.2006 87.2643L83.7677 76.6973L94.5672 87.4968C95.6255 88.555 97.3412 88.555 98.3995 87.4968C99.4578 86.4385 99.4578 84.7227 98.3995 83.6645L87.6 72.8649L98.1106 62.3543C99.1689 61.296 99.1689 59.5802 98.1106 58.522Z",
            fill: "#0192C3"
          }
        ),
        /* @__PURE__ */ jsx115(
          "path",
          {
            d: "M141.208 124.051C141.208 122.721 142.286 121.642 143.617 121.642H167.704V124.051C167.704 125.381 166.626 126.46 165.296 126.46H143.617C142.286 126.46 141.208 125.381 141.208 124.051Z",
            fill: "#CFCFCD"
          }
        ),
        /* @__PURE__ */ jsx115("circle", { cx: "46.5", cy: "178.5", r: "8.5", fill: "#1A91CF" }),
        /* @__PURE__ */ jsx115("circle", { cx: "73.5", cy: "178.5", r: "8.5", fill: "#1A91CF" }),
        /* @__PURE__ */ jsx115("circle", { cx: "100.5", cy: "178.5", r: "8.5", fill: "#1A91CF" }),
        /* @__PURE__ */ jsx115("circle", { cx: "124.5", cy: "178.5", r: "5.5", fill: "#1A91CF" })
      ] })
    }
  );
};
var NoResultsIcon_default = NoResultsIcon2;

// src/icons/PencilIcon/PencilIcon.tsx
import { jsx as jsx116, jsxs as jsxs68 } from "react/jsx-runtime";
var PencilIcon = ({
  width = 50,
  height = 50,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "pencil-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsxs68(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 50 50",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: [
        /* @__PURE__ */ jsx116(
          "g",
          {
            "data-testid": "g-path",
            clipPath: "url(#clip0_2829_8431)",
            opacity,
            children: /* @__PURE__ */ jsx116(
              "path",
              {
                d: "M6 34.5V42H13.5L35.62 19.88L28.12 12.38L6 34.5ZM41.42 14.08C42.2 13.3 42.2 12.04 41.42 11.26L36.74 6.58C35.96 5.8 34.7 5.8 33.92 6.58L30.26 10.24L37.76 17.74L41.42 14.08Z",
                fill: "#59595B"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx116("defs", { children: /* @__PURE__ */ jsx116("clipPath", { id: "clip0_2829_8431", children: /* @__PURE__ */ jsx116("rect", { width: "48", height: "48", fill: "white" }) }) })
      ]
    }
  );
};
var PencilIcon_default = PencilIcon;

// src/icons/PlusIcon/PlusIcon.tsx
import { jsx as jsx117 } from "react/jsx-runtime";
var PlusIcon = ({
  width = 48,
  height = 49,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#59595B",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "plus-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx117(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 48 49",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx117("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx117(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M44 19.948C46.2091 19.948 48 21.7389 48 23.948C48 26.1572 46.2091 27.948 44 27.948L28.0589 27.9481L28.0589 44.4922C28.0589 46.7013 26.268 48.4922 24.0589 48.4922C21.8497 48.4922 20.0589 46.7013 20.0589 44.4922V27.9481L4 27.948C1.79086 27.948 0 26.1572 0 23.948C0 21.7389 1.79086 19.948 4 19.948H20.0589V4.49219C20.0589 2.28302 21.8497 0.492188 24.0589 0.492188C26.268 0.492188 28.0589 2.28302 28.0589 4.49219V19.9481L44 19.948Z",
          fill: color
        }
      ) })
    }
  );
};
var PlusIcon_default = PlusIcon;

// src/icons/PracticeGroupIcon/PracticeGroupIcon.tsx
import { jsx as jsx118, jsxs as jsxs69 } from "react/jsx-runtime";
var PracticeGroupIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#118A7E",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "practice-group-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx118(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs69("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx118(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M6.50965 5.9923C6.5384 5.6376 6.6308 5.3006 6.7756 4.99231H3V6.2331L5 7.74845V21.0077H19V7.76605L21.25 6.2507V4.99231H12.2244C12.3692 5.3006 12.4616 5.6376 12.4903 5.9923H19.8435L18 7.2339V20.0077H6V7.25155L4.33808 5.9923H6.50965Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx118(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M10 13H8V15H10V13ZM7 12V16H11V12H7Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx118(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M16 13H13V20H16V13ZM12 12V21H17V12H12Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx118(
          "path",
          {
            d: "M3 20.5C3 20.7761 3.22386 21 3.5 21H20.5C20.7761 21 21 20.7761 21 20.5C21 20.2239 20.7761 20 20.5 20H3.5C3.22386 20 3 20.2239 3 20.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx118(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M7.5 8H5V7H7.5V8Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx118(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M18.75 8H11.5V7H18.75V8Z",
            fill: "#118A7E"
          }
        ),
        /* @__PURE__ */ jsx118(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M12.5 6C12.5 7.65685 11.1568 9 9.5 9C7.84315 9 6.5 7.65685 6.5 6C6.5 4.34315 7.84315 3 9.5 3C11.1568 3 12.5 4.34315 12.5 6ZM10 4.5V5.5H11V6.5H10V7.5H9V6.5H8V5.5H9V4.5H10Z",
            fill: "#118A7E"
          }
        )
      ] })
    }
  );
};
var PracticeGroupIcon_default = PracticeGroupIcon;

// src/icons/ProviderPerformanceIcon/Variants/ProviderPerformanceIconDefault.tsx
import { jsx as jsx119, jsxs as jsxs70 } from "react/jsx-runtime";
var ProviderPerformanceIconOutlined = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "provider-performance-icon",
  // Extra Properties
  variant,
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx119(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs70("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx119("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx119(
          "path",
          {
            "data-testid": "path-with-background-color",
            d: "M85.1712 60.511C91.6605 54.1576 95.6875 45.2991 95.6875 35.5C95.6875 16.17 80.0175 0.5 60.6875 0.5C41.3575 0.5 25.6875 16.17 25.6875 35.5C25.6875 45.2991 29.7145 54.1576 36.2038 60.511C28.8896 60.7528 22.2677 64.9785 18.9791 71.5557L8.97906 91.5557C2.33005 104.854 12 120.5 26.8676 120.5H94.5069C109.375 120.5 119.044 104.854 112.395 91.5557L102.395 71.5557C99.1069 64.9786 92.4852 60.753 85.1712 60.511Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx119("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx119(
          "path",
          {
            fillRule: "evenodd",
            "data-testid": "path-with-color",
            clipRule: "evenodd",
            d: "M94.4741 26.325C95.255 29.0716 95.9456 31.5004 89.8596 31.5004H31.5138C25.2344 31.5004 25.8961 29.4449 26.6904 26.9774C26.7926 26.66 26.8969 26.3357 26.989 26.0082C31.1263 11.2903 44.6464 0.5 60.6867 0.5C76.727 0.5 90.2471 11.2903 94.3843 26.0082C94.4141 26.1142 94.4442 26.2198 94.4741 26.325ZM60.6871 10.9844C63.4485 10.9844 65.6871 13.223 65.6871 15.9844V16.1594C65.6871 18.9208 63.4485 21.1594 60.6871 21.1594C57.9257 21.1594 55.6871 18.9208 55.6871 16.1594V15.9844C55.6871 13.223 57.9257 10.9844 60.6871 10.9844ZM37.3521 62.5743C36.6374 59.907 33.8957 58.3241 31.2284 59.0388C28.5611 59.7535 26.9782 62.4952 27.6929 65.1625L30.5389 75.7838C29.1377 77.2805 28.1938 79.0753 27.5884 80.8408C26.4489 84.1637 26.277 87.9183 26.4089 91.1161C26.5441 94.3933 27.021 97.5178 27.4507 99.7864C27.6674 100.931 27.8768 101.881 28.034 102.553C28.1128 102.89 28.1787 103.158 28.2264 103.347C28.2503 103.442 28.2697 103.517 28.2838 103.572L28.3009 103.637L28.3065 103.658C28.6088 104.792 29.6407 106.13 30.6394 106.706C31.7879 107.37 33.1526 107.549 34.4335 107.206L37.8756 106.284C40.5429 105.569 42.1258 102.827 41.4111 100.16C40.8521 98.0736 39.0528 96.6507 37.0191 96.4719C36.7354 94.743 36.4839 92.7279 36.4004 90.704C36.289 88.0014 36.4977 85.6885 37.0477 84.0845C37.5573 82.5985 38.0903 82.3639 38.4521 82.267C38.814 82.17 39.3928 82.1067 40.5772 83.1388C41.8555 84.2529 43.1927 86.1516 44.4475 88.5478C45.3443 90.2606 46.1047 92.0431 46.7101 93.6272C44.5006 94.6092 43.2715 97.0792 43.9171 99.4885C44.6318 102.156 47.3735 103.739 50.0408 103.024L54.4018 101.855C55.6827 101.512 56.7748 100.674 57.4379 99.5259C58.2841 98.0601 57.9382 95.7347 57.9367 95.7294L57.934 95.7192L57.9284 95.6985L57.9105 95.6331C57.8956 95.579 57.8747 95.5043 57.848 95.4103C57.7947 95.2225 57.7177 94.9572 57.6176 94.6264C57.4177 93.9656 57.1238 93.038 56.7394 91.9385C55.9772 89.759 54.8279 86.8146 53.3064 83.909C51.8217 81.0736 49.7956 77.908 47.1473 75.6C45.2552 73.951 42.8067 72.5623 39.9494 72.2674L37.3521 62.5743ZM93.4096 60.413C95.801 61.7937 96.6204 64.8516 95.2397 67.2431L89.1522 77.7869L89.1425 77.8037C91.7012 81.6362 92.0348 86.7432 89.5748 91.0039C86.123 96.9826 78.4781 99.031 72.4995 95.5792C66.5208 92.1275 64.4724 84.4826 67.9242 78.5039C70.5876 73.8907 75.7475 71.6175 80.7122 72.4055L86.5794 62.2431C87.9602 59.8516 91.0181 59.0323 93.4096 60.413ZM79.9995 82.5889C78.8038 81.8985 77.2748 82.3082 76.5844 83.5039C75.8941 84.6996 76.3038 86.2286 77.4995 86.919C78.6952 87.6093 80.2242 87.1996 80.9146 86.0039C81.6049 84.8082 81.1952 83.2792 79.9995 82.5889Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var ProviderPerformanceIconDefault_default = ProviderPerformanceIconOutlined;

// src/icons/ProviderPerformanceIcon/Variants/ProviderPerformanceIconOutlined.tsx
import { jsx as jsx120 } from "react/jsx-runtime";
var ProviderPerformanceIconOutlined2 = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "provider-performance-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx120(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 109 121",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx120("g", { opacity, "data-testid": "g-path", children: /* @__PURE__ */ jsx120(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M21.7719 62.7521C18.0939 64.6634 15.0282 67.707 13.1038 71.5557L3.10381 91.5557C-3.5452 104.854 6.12472 120.5 20.9924 120.5H88.6317C103.499 120.5 113.169 104.854 106.52 91.5557L96.5202 71.5557C94.9812 68.4777 92.7122 65.9146 89.9838 64.0339C89.773 62.5681 88.916 61.21 87.534 60.4121C85.726 59.3683 83.5371 59.5819 81.9799 60.7817C81.0993 60.6323 80.2029 60.541 79.296 60.511C85.7853 54.1576 89.8123 45.2991 89.8123 35.5C89.8123 16.17 74.1422 0.5 54.8123 0.5C35.4823 0.5 19.8123 16.17 19.8123 35.5C19.8123 45.0408 23.6298 53.69 29.8206 60.0034C28.6169 59.0165 26.9712 58.6043 25.3528 59.0379C23.5018 59.5339 22.173 61.0061 21.7719 62.7521ZM24.0091 73.3418C23.2225 74.109 22.5558 75.0124 22.0481 76.0279L12.0481 96.0279C8.72358 102.677 13.5585 110.5 20.9924 110.5H88.6317C96.0655 110.5 100.9 102.677 97.5759 96.0278L87.576 76.0279C87.0996 75.0752 86.4834 74.2212 85.7595 73.4857L83.2766 77.7861C83.2734 77.7917 83.2701 77.7973 83.2669 77.8028C85.8256 81.6353 86.1592 86.7423 83.6992 91.0031C80.2474 96.9817 72.6026 99.0302 66.6239 95.5784C60.6452 92.1266 58.5968 84.4817 62.0486 78.5031C64.712 73.8899 69.8719 71.6166 74.8366 72.4047L75.9362 70.5H54.8123H33.6005L34.0738 72.2665C36.9311 72.5615 39.3796 73.9501 41.2717 75.5992C43.92 77.9072 45.9461 81.0728 47.4308 83.9081C48.9523 86.8138 50.1016 89.7582 50.8638 91.9376C51.2482 93.0371 51.5421 93.9647 51.742 94.6255C51.8421 94.9564 51.9191 95.2216 51.9725 95.4095C51.9991 95.5035 52.02 95.5782 52.0349 95.6322L52.0528 95.6976L52.0584 95.7183L52.0611 95.7285C52.0626 95.7339 52.4086 98.0592 51.5623 99.525C50.8992 100.673 49.8071 101.511 48.5262 101.855L44.1652 103.023C41.4979 103.738 38.7562 102.155 38.0415 99.4876C37.3959 97.0783 38.625 94.6083 40.8345 93.6263C40.2291 92.0423 39.4688 90.2598 38.5719 88.5469C37.3171 86.1507 35.9799 84.252 34.7016 83.138C33.5172 82.1058 32.9384 82.1691 32.5765 82.2661C32.2147 82.3631 31.6817 82.5976 31.1721 84.0837C30.6221 85.6876 30.4134 88.0006 30.5248 90.7031C30.6083 92.727 30.8598 94.7421 31.1435 96.471C33.1772 96.6498 34.9765 98.0727 35.5355 100.159C36.2502 102.826 34.6673 105.568 32 106.283L28.5579 107.205C27.277 107.548 25.9123 107.369 24.7638 106.706C23.7652 106.129 22.7332 104.791 22.4309 103.657L22.4254 103.636L22.4082 103.571C22.3941 103.516 22.3747 103.441 22.3509 103.347C22.3031 103.157 22.2372 102.889 22.1584 102.552C22.0012 101.88 21.7919 100.93 21.5751 99.7855C21.1454 97.517 20.6685 94.3924 20.5333 91.1152C20.4014 87.9174 20.5733 84.1629 21.7128 80.8399C22.3183 79.0744 23.2621 77.2797 24.6633 75.783L24.0091 73.3418ZM79.8123 35.5C79.8123 49.3071 68.6194 60.5 54.8123 60.5C41.0051 60.5 29.8123 49.3071 29.8123 35.5C29.8123 34.1385 29.9211 32.8024 30.1305 31.5H79.494C79.7034 32.8024 79.8123 34.1385 79.8123 35.5ZM75.5276 21.5C71.033 14.8625 63.4321 10.5 54.8123 10.5C46.1924 10.5 38.5915 14.8625 34.0969 21.5H75.5276ZM54.8123 10.9844C57.5737 10.9844 59.8123 13.223 59.8123 15.9844V16.1594C59.8123 18.9208 57.5737 21.1594 54.8123 21.1594C52.0508 21.1594 49.8123 18.9208 49.8123 16.1594V15.9844C49.8123 13.223 52.0508 10.9844 54.8123 10.9844ZM70.7088 83.5031C71.3992 82.3073 72.9282 81.8976 74.1239 82.588C75.3196 83.2783 75.7293 84.8073 75.039 86.0031C74.3486 87.1988 72.8196 87.6085 71.6239 86.9181C70.4282 86.2278 70.0185 84.6988 70.7088 83.5031Z",
          fill: color
        }
      ) })
    }
  );
};
var ProviderPerformanceIconOutlined_default = ProviderPerformanceIconOutlined2;

// src/icons/ProviderPerformanceIcon/ProviderPerformanceIcon.tsx
import { jsx as jsx121 } from "react/jsx-runtime";
var ProviderPerformanceIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx121(ProviderPerformanceIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx121(ProviderPerformanceIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var ProviderPerformanceIcon_default = ProviderPerformanceIcon;

// src/icons/RepeatIcon/RepeatIcon.tsx
import { jsx as jsx122, jsxs as jsxs71 } from "react/jsx-runtime";
var RepeatIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "repeat-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx122(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs71("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx122(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M15.7936 3.09472H18.3754C21.4771 3.09472 24.0004 5.61809 24.0004 8.71971V15.2822C24.0004 18.3838 21.4771 20.9072 18.3754 20.9072H14.0161C13.4983 20.9072 13.0786 20.4875 13.0786 19.9697C13.0786 19.4519 13.4983 19.0322 14.0161 19.0322H18.3754C20.4432 19.0322 22.1254 17.3499 22.1254 15.2822V8.71971C22.1254 6.65196 20.4432 4.96971 18.3754 4.96971H15.7463L17.3003 6.50618C17.6684 6.87016 17.6718 7.46379 17.3078 7.83194C17.1244 8.01738 16.8827 8.11029 16.6411 8.11029C16.4028 8.11029 16.1647 8.0201 15.982 7.83944L14.2103 6.08777C13.6618 5.54557 13.3598 4.82393 13.3598 4.05565C13.3598 3.28737 13.6619 2.56569 14.2103 2.02353L15.982 0.271864C16.3501 -0.0922599 16.9436 -0.0888381 17.3077 0.279364C17.6718 0.64752 17.6684 1.2411 17.3002 1.60513L15.7936 3.09472Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx122(
          "path",
          {
            d: "M8.20726 20.9072H5.62548C2.52386 20.9072 0.000488057 18.3838 0.000488281 15.2822L0.000488902 8.71971C0.000489173 5.61809 2.52386 3.09472 5.62548 3.09472L9.98484 3.09472C10.5026 3.09472 10.9223 3.51444 10.9223 4.03222C10.9223 4.55 10.5026 4.96972 9.98484 4.96972L5.62548 4.96972C3.55773 4.96972 1.87548 6.65197 1.87548 8.71971L1.87548 15.2822C1.87548 17.3499 3.55773 19.0322 5.62548 19.0322H8.25458L6.70065 17.4957C6.33249 17.1317 6.32907 16.5381 6.69315 16.17C6.87648 15.9845 7.11816 15.8916 7.35985 15.8916C7.59807 15.8916 7.83624 15.9818 8.01896 16.1625L9.79064 17.9141C10.3391 18.4563 10.6411 19.178 10.6411 19.9463C10.6411 20.7145 10.339 21.4362 9.79064 21.9784L8.01896 23.73C7.65085 24.0942 7.05727 24.0907 6.69319 23.7225C6.32912 23.3544 6.33254 22.7608 6.70069 22.3968L8.20726 20.9072Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var RepeatIcon_default = RepeatIcon;

// src/icons/RedirectIcon/RedirectIcon.tsx
import { jsx as jsx123, jsxs as jsxs72 } from "react/jsx-runtime";
var RedirectIcon = ({
  width = 89,
  height = 45,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "redirect-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx123(
    "svg",
    {
      width,
      height,
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 89 45",
      children: /* @__PURE__ */ jsxs72("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx123(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M88.2579 21.3009L68.2943 0.566194C67.9438 0.200939 67.4642 0.00170898 66.9624 0.00170898H46.9951C46.2573 0.00170898 45.5858 0.444443 45.3017 1.1233C45.0102 1.80216 45.1541 2.5917 45.6706 3.12298L64.3761 22.5553L45.6928 41.2423C45.1652 41.7699 45.0065 42.5632 45.2906 43.2531C45.5784 43.943 46.2536 44.3931 46.9951 44.3931H66.9624C67.4531 44.3931 67.9217 44.1976 68.2685 43.8471L88.2321 23.8835C88.9442 23.1751 88.9552 22.024 88.2579 21.3009Z",
            fill: "#015F94"
          }
        ),
        /* @__PURE__ */ jsx123(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M53.3195 21.3009L33.3522 0.566194C33.0017 0.200939 32.5221 0.00170898 32.024 0.00170898H17.0485C16.307 0.00170898 15.6392 0.444443 15.3514 1.1233C15.0599 1.80216 15.2075 2.5917 15.7203 3.12298L34.4295 22.5553L15.7462 41.2423C15.2186 41.7699 15.0562 42.5632 15.344 43.2531C15.6281 43.943 16.3033 44.3931 17.0485 44.3931H32.024C32.511 44.3931 32.9796 44.1976 33.3264 43.8471L53.2937 23.8835C54.0057 23.1751 54.0168 22.024 53.3195 21.3009Z",
            fill: "#3992C6"
          }
        ),
        /* @__PURE__ */ jsx123(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M23.368 21.301L3.40076 0.566261C2.69608 -0.167939 1.52284 -0.190076 0.792325 0.510919C0.0581251 1.22298 0.0359883 2.38885 0.740673 3.12305L19.4535 22.5554L0.766499 41.2387C0.0470564 41.9619 0.0470564 43.1277 0.766499 43.8472C1.12806 44.2087 1.60031 44.3895 2.07625 44.3895C2.54112 44.3895 3.01337 44.2087 3.37863 43.8472L23.3422 23.8836C24.058 23.1752 24.0654 22.0241 23.368 21.301Z",
            fill: "#EC9F24"
          }
        )
      ] })
    }
  );
};
var RedirectIcon_default = RedirectIcon;

// src/icons/RibbonIcon/RibbonIcon.tsx
import { jsx as jsx124, jsxs as jsxs73 } from "react/jsx-runtime";
var RibbonIcon = ({
  width = 35,
  height = 50,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "ribbon-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx124(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 35 50",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs73("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx124(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M24.3224 0.843263C23.3206 0.270813 22.1975 0 20.9879 0H14.4284C12.1479 0 9.84238 1.06219 8.455 2.98371C8.40875 3.04216 8.36338 3.10111 8.319 3.16055L5.3985 6.94636L4.38438 8.29875L4.3825 8.30125C1.21888 12.4796 1.2195 18.2974 4.38438 22.475L4.39175 22.4848L16.6133 37.9879L24.8334 48.4686C25.7268 49.6076 27.3978 49.7522 28.4735 48.7835L33.5559 44.2067C34.5296 43.3299 34.6611 41.8499 33.8571 40.8151L24.5695 28.8614L18.5795 21.2891L10.4345 10.7686L10.4283 10.7607C9.71775 9.8725 9.34513 8.68487 9.34513 7.58337C9.34513 6.56459 9.66538 5.5434 10.3123 4.6698L10.456 4.48338L10.4694 4.46464C11.3463 3.23708 12.8748 2.5 14.4284 2.5H20.9879C21.8141 2.5 22.5005 2.68156 23.082 3.01388C23.8095 3.42956 24.4213 3.887 24.8339 4.46464L26.8682 3.01155C26.1499 2.00585 25.1783 1.33234 24.3224 0.843263ZM6.9795 9.00537C7.19975 10.175 7.69225 11.3396 8.46963 12.3144L16.6146 22.8351L22.6088 30.4124L31.883 42.349L26.8005 46.9258L18.5786 36.4429L6.37 20.956C3.888 17.671 3.89038 13.091 6.37713 9.80837L6.38075 9.80363L6.9795 9.00537Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx124(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M2.84968 42.8652L12.5862 30.4066L10.6164 28.8672L0.879934 41.3257C0.0206841 42.4252 0.227809 44.0152 1.34006 44.8578L6.81318 49.0041C7.90068 49.8279 9.44794 49.6278 10.2899 48.5542L18.5789 37.9857L16.6118 36.4428L8.32281 47.0113L2.84968 42.8652Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx124(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M26.8671 3.04156C26.8173 2.97495 26.7664 2.909 26.7142 2.84375L24.7484 4.38812L24.8736 4.55028C26.2053 6.34931 26.1682 8.88969 24.7621 10.6473L24.7558 10.6551L16.6069 21.1808L18.5837 22.7113L26.7207 12.2009C27.5026 11.2208 28.0022 10.0839 28.2197 8.90356L28.8132 9.69494C31.3011 12.9789 31.3024 17.5613 28.8172 20.8468L22.6012 28.8711L24.5776 30.4021L30.8059 22.3617C33.9709 18.1839 33.9714 12.3661 30.8078 8.18781L29.7816 6.81938L26.8671 3.04156Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx124(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M14.3153 11.8867C13.016 11.8867 11.7665 12.3521 10.9015 13.2171L9.13379 11.4493C10.5308 10.0523 12.4479 9.38672 14.3153 9.38672H20.8748C22.7973 9.38672 24.6718 10.201 26.0087 11.4041L24.3363 13.2623C23.4112 12.4298 22.119 11.8867 20.8748 11.8867H14.3153Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var RibbonIcon_default = RibbonIcon;

// src/icons/RiskContractIcon/Variants/RiskContractIconDefault.tsx
import { jsx as jsx125, jsxs as jsxs74 } from "react/jsx-runtime";
var RiskContractIconDefault = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "risk-contract-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx125(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs74("g", { opacity, "data-testid": "g-path", children: [
        /* @__PURE__ */ jsx125("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx125(
          "path",
          {
            "data-testid": "path-with-background-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M101.688 37.5H99.6875C91.9555 37.5 85.6875 43.768 85.6875 51.5V106.5C85.6875 114.232 91.9555 120.5 99.6875 120.5H101.688C109.419 120.5 115.688 114.232 115.688 106.5V51.5C115.688 43.768 109.419 37.5 101.688 37.5ZM61.6875 50.5H59.6875C51.9555 50.5 45.6875 56.768 45.6875 64.5V106.5C45.6875 114.232 51.9555 120.5 59.6875 120.5H61.6875C69.4195 120.5 75.6875 114.232 75.6875 106.5V64.5C75.6875 56.768 69.4195 50.5 61.6875 50.5ZM21.6875 60.5H19.6875C11.9555 60.5 5.6875 66.768 5.6875 74.5V106.5C5.6875 114.232 11.9555 120.5 19.6875 120.5H21.6875C29.4195 120.5 35.6875 114.232 35.6875 106.5V74.5C35.6875 66.768 29.4195 60.5 21.6875 60.5Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx125("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx125(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M85.1131 28.5054C87.4717 30.7035 89.6257 32.5129 91.2556 33.6365C91.2882 33.6692 93.4476 35.1021 94.2769 35.4686C95.5043 35.9696 96.8946 36.0463 98.1829 35.6876C99.1738 35.3599 100.034 34.8366 100.758 34.1126C101.3 33.5171 101.953 32.2922 102.382 31.3822L102.726 30.6122C104.948 25.4606 107.211 13.6268 106.983 8.00085L106.978 7.59727L106.908 5.75053C106.734 4.45722 106.133 3.26433 105.168 2.29968C104.304 1.43551 103.176 0.835053 101.951 0.664115C101.566 0.595754 101.022 0.559826 100.55 0.540948L98.7807 0.500515C92.7915 0.461266 82.1223 2.67458 77.3827 4.61243C77.3526 4.58229 74.411 5.7905 73.3906 6.74553L73.2588 6.87739C71.7436 8.39251 71.1922 10.6723 71.865 12.793C72.049 13.3726 72.6073 14.3103 73.1199 15.1115L74.1009 16.6113C75.0784 17.9294 76.4414 19.5673 78.0621 21.3599C66.3718 30.9605 56.0447 36.1781 46.8076 39.06C35.0102 42.7407 24.6718 42.7355 14.557 42.7305L14.5341 42.7304C14.1667 42.7303 13.7995 42.7301 13.4326 42.7301C10.6712 42.7301 8.43262 44.9687 8.43262 47.7301C8.43262 50.4915 10.6712 52.7301 13.4326 52.7301C13.8858 52.7301 14.3426 52.7305 14.8029 52.7309L14.8138 52.7309C24.8168 52.74 36.5022 52.7506 49.786 48.6062C60.603 45.2313 72.277 39.1807 85.1131 28.5054Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var RiskContractIconDefault_default = RiskContractIconDefault;

// src/icons/RiskContractIcon/Variants/RiskContractIconOutlined.tsx
import { jsx as jsx126, jsxs as jsxs75 } from "react/jsx-runtime";
var RiskContractIconOutlined = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "risk-contract-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx126(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 121 121",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs75("g", { opacity, children: [
        /* @__PURE__ */ jsx126(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M85.2378 28.5054C87.5965 30.7035 89.7504 32.5129 91.3803 33.6365C91.413 33.6692 93.5724 35.1021 94.4017 35.4686C95.6291 35.9696 97.0194 36.0463 98.3076 35.6876C99.2985 35.3599 100.158 34.8366 100.882 34.1126C101.425 33.5171 102.077 32.2922 102.507 31.3822L102.85 30.6122C105.073 25.4606 107.335 13.6268 107.108 8.00085L107.103 7.59727L107.032 5.75053C106.859 4.45722 106.258 3.26433 105.293 2.29968C104.429 1.43551 103.301 0.835053 102.075 0.664115C101.691 0.595754 101.146 0.559826 100.675 0.540948L98.9054 0.500515C92.9163 0.461266 82.2471 2.67458 77.5075 4.61243C77.4773 4.58229 74.5357 5.7905 73.5154 6.74553L73.3835 6.87739C71.8684 8.39251 71.317 10.6723 71.9897 12.793C72.1738 13.3726 72.7321 14.3103 73.2447 15.1115L74.2257 16.6113C75.2031 17.9294 76.5662 19.5673 78.1869 21.3599C66.4965 30.9605 56.1695 36.1781 46.9324 39.06C35.1349 42.7407 24.7965 42.7355 14.6818 42.7305L14.6589 42.7304C14.2917 42.7303 13.924 42.7301 13.5574 42.7301C10.7959 42.7301 8.55737 44.9687 8.55737 47.7301C8.55737 50.4915 10.7959 52.7301 13.5574 52.7301C14.0106 52.7301 14.4674 52.7305 14.9277 52.7309L14.9386 52.7309C24.9416 52.74 36.627 52.7506 49.9107 48.6062C60.7277 45.2313 72.4017 39.1807 85.2378 28.5054Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx126(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M99.8123 37.5H101.812C109.544 37.5 115.812 43.768 115.812 51.5V106.5C115.812 114.232 109.544 120.5 101.812 120.5H99.8123C92.0803 120.5 85.8123 114.232 85.8123 106.5V51.5C85.8123 43.768 92.0803 37.5 99.8123 37.5ZM105.812 51.5C105.812 49.2909 104.021 47.5 101.812 47.5H99.8123C97.6031 47.5 95.8123 49.2909 95.8123 51.5V106.5C95.8123 108.709 97.6031 110.5 99.8123 110.5H101.812C104.021 110.5 105.812 108.709 105.812 106.5V51.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx126(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M59.8123 50.5H61.8123C69.5442 50.5 75.8123 56.768 75.8123 64.5V106.5C75.8123 114.232 69.5442 120.5 61.8123 120.5H59.8123C52.0803 120.5 45.8123 114.232 45.8123 106.5V64.5C45.8123 56.768 52.0803 50.5 59.8123 50.5ZM65.8123 64.5C65.8123 62.2909 64.0214 60.5 61.8123 60.5H59.8123C57.6031 60.5 55.8123 62.2909 55.8123 64.5V106.5C55.8123 108.709 57.6031 110.5 59.8123 110.5H61.8123C64.0214 110.5 65.8123 108.709 65.8123 106.5V64.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx126(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M21.8123 60.5H19.8123C12.0803 60.5 5.81226 66.768 5.81226 74.5V106.5C5.81226 114.232 12.0803 120.5 19.8123 120.5H21.8123C29.5442 120.5 35.8123 114.232 35.8123 106.5V74.5C35.8123 66.768 29.5442 60.5 21.8123 60.5ZM25.8123 106.5V74.5C25.8123 72.2909 24.0214 70.5 21.8123 70.5H19.8123C17.6031 70.5 15.8123 72.2909 15.8123 74.5V106.5C15.8123 108.709 17.6031 110.5 19.8123 110.5H21.8123C24.0214 110.5 25.8123 108.709 25.8123 106.5Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var RiskContractIconOutlined_default = RiskContractIconOutlined;

// src/icons/RiskContractIcon/RiskContractIcon.tsx
import { jsx as jsx127 } from "react/jsx-runtime";
var RiskContractIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx127(RiskContractIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx127(RiskContractIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var RiskContractIcon_default = RiskContractIcon;

// src/icons/ScoreTableIcon/ScoreTableIcon.tsx
import { jsx as jsx128, jsxs as jsxs76 } from "react/jsx-runtime";
var ScoreTableIcon = ({
  width = 14,
  height = 16,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "score-table-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx128(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 14 16",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs76("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx128(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M1.79598 4.95236C1.74144 4.95236 1.69722 4.99658 1.69722 5.05113V14.2606C1.69722 14.3151 1.74144 14.3593 1.79598 14.3593H11.6384C11.6929 14.3593 11.7372 14.3151 11.7372 14.2606V5.05113C11.7372 4.99658 11.6929 4.95236 11.6384 4.95236H1.79598ZM2.38671 7.11041V5.64185H3.34363V7.11041H2.38671ZM8.44105 7.11041V5.64185H9.39798V7.11041H8.44105ZM4.03312 7.11369V5.64513H7.75157V7.11369H4.03312ZM10.0907 7.11369V5.64513H11.0476V7.11369H10.0907ZM2.38671 9.30782V7.83926H3.34363V9.30782H2.38671ZM8.44105 9.30782V7.83926H9.39798V9.30782H8.44105ZM4.03312 9.3111V7.84254H7.75157V9.3111H4.03312ZM10.0907 9.3111V7.84254H11.0444V9.3111H10.0907ZM2.38671 11.4724V10.0039H3.34363V11.4724H2.38671ZM4.03312 11.4724V10.0039H7.75157V11.4724H4.03312ZM8.44105 11.4724V10.0039H9.39798V11.4724H8.44105ZM10.0875 11.4724V10.0039H11.0444V11.4724H10.0875ZM2.38671 13.6698V12.2013H3.34363V13.6698H2.38671ZM4.03312 13.6698V12.2013H7.75157V13.6698H4.03312ZM8.44105 13.6698V12.2013H9.39798V13.6698H8.44105ZM10.0875 13.6698V12.2013H11.0444V13.6698H10.0875Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx128(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M8.35064 1.17168C8.22103 0.771299 7.94476 0.436799 7.56152 0.226777C7.04279 -0.0755068 6.3795 -0.0757014 5.86368 0.226848C5.4805 0.436875 5.20428 0.771345 5.0747 1.17168H3.99706C3.94252 1.17168 3.8983 1.2159 3.8983 1.27044V1.74235H0.432018C0.405772 1.74235 0.380605 1.75279 0.362074 1.77138C0.343544 1.78997 0.333173 1.81517 0.333252 1.84141L0.375889 15.9015C0.376054 15.956 0.420224 16 0.474654 16H12.9933C13.0479 16 13.0921 15.9558 13.0921 15.9012V1.84111C13.0921 1.78656 13.0479 1.74235 12.9933 1.74235H9.52703V1.27044C9.52703 1.2159 9.48281 1.17168 9.42827 1.17168H8.35064ZM6.2001 0.827864L6.20769 0.822802C6.51341 0.642862 6.90543 0.642863 7.21115 0.822804L7.21852 0.828196L7.22632 0.832443C7.38154 0.916899 7.50614 1.03361 7.59333 1.17168H5.82701C5.91587 1.03209 6.04018 0.914297 6.19206 0.832691C6.19481 0.83121 6.19749 0.829599 6.2001 0.827864ZM4.58451 2.27038V1.86117H8.83755V2.27038H4.58451ZM1.05852 15.3105L1.01976 2.43183H3.89174V2.86438C3.89174 2.91893 3.93596 2.96315 3.9905 2.96315H9.42499C9.47953 2.96315 9.52375 2.91893 9.52375 2.86438V2.43183H12.396V15.3105H1.05852Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var ScoreTableIcon_default = ScoreTableIcon;

// src/icons/SearchIcon/SearchIcon.tsx
import { jsx as jsx129 } from "react/jsx-runtime";
var SearchIcon = ({
  width = 20,
  height = 20,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "search-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx129(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 20 20",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx129("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx129(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M13.3566 2.28852C10.3056 -0.762507 5.33981 -0.762507 2.28878 2.28852C-0.761584 5.34021 -0.761584 10.3053 2.28878 13.357C5.0058 16.0734 9.23742 16.3644 12.2865 14.2433C12.3506 14.5469 12.4975 14.8366 12.7336 15.0727L17.1768 19.5159C17.8243 20.1621 18.8707 20.1621 19.5149 19.5159C20.1617 18.8691 20.1617 17.8228 19.5149 17.1779L15.0716 12.7333C14.8368 12.4992 14.5465 12.3517 14.2429 12.2875C16.3653 9.23782 16.0743 5.00686 13.3566 2.28852ZM11.9538 11.9542C9.67593 14.232 5.9688 14.232 3.69161 11.9542C1.41507 9.67633 1.41507 5.96986 3.69161 3.69201C5.9688 1.41481 9.67593 1.41481 11.9538 3.69201C14.2316 5.96986 14.2316 9.67633 11.9538 11.9542Z",
          fill: color
        }
      ) })
    }
  );
};
var SearchIcon_default = SearchIcon;

// src/icons/SortColumnIcon/SortColumnIcon.tsx
import { jsx as jsx130, jsxs as jsxs77 } from "react/jsx-runtime";
var ArrowUpIcon = ({
  width = 8,
  height = 6,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#1F6F78",
  opacity = "1",
  direction = "up",
  handleClick,
  dataTestId = "sort-column-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx130(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      onClick: handleClick,
      viewBox: "0 0 8 6",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      children: /* @__PURE__ */ jsxs77("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx130(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M6.80793 6.00019H1.19182C0.743918 6.00019 0.338673 5.73225 0.135384 5.30101C-0.094566 4.81245 -0.0279139 4.23458 0.307346 3.828L3.11607 0.429407C3.56397 -0.115141 4.43645 -0.115141 4.88368 0.429407L7.69174 3.82733C8.02767 4.23458 8.09499 4.81245 7.86437 5.30101C7.66108 5.73225 7.2565 6.00019 6.80793 6.00019Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx130(
          "mask",
          {
            id: "mask0_6079_34973",
            maskUnits: "userSpaceOnUse",
            x: "0",
            y: "0",
            width,
            height,
            children: /* @__PURE__ */ jsx130(
              "path",
              {
                fillRule: "evenodd",
                clipRule: "evenodd",
                d: "M1.34313 4.66719H6.60532L3.99855 1.45322L1.34313 4.66719ZM6.80793 6.00019H1.19182C0.743918 6.00019 0.338673 5.73225 0.135384 5.30101C-0.094566 4.81245 -0.0279139 4.23458 0.307346 3.828L3.11607 0.429407C3.56397 -0.115141 4.43645 -0.115141 4.88368 0.429407L7.69174 3.82733C8.02767 4.23458 8.09499 4.81245 7.86437 5.30101C7.66108 5.73225 7.2565 6.00019 6.80793 6.00019Z",
                fill: "white"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx130("g", { mask: "url(#mask0_6079_34973)" })
      ] })
    }
  );
};
var SortColumnIcon_default = ArrowUpIcon;

// src/icons/StateIcon/StateIcon.tsx
import { jsx as jsx131 } from "react/jsx-runtime";
var StateIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "state-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx131(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx131("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx131(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M22 21H21.5V20.5C21.5 20.224 21.276 20 21 20H19.5V15H20C20.827 15 21.5 14.327 21.5 13.5C21.5 12.673 20.827 12 20 12H19.475C19.248 8.586 16.736 5.79098 13.453 5.14402C13.332 4.653 12.971 4.26 12.5 4.092V3H15C15.202 3 15.385 2.87798 15.462 2.691C15.54 2.50402 15.497 2.289 15.354 2.14598L14.708 1.5L15.354 0.854016C15.497 0.711 15.54 0.496031 15.462 0.309C15.385 0.122016 15.202 0 15 0H12C11.724 0 11.5 0.224016 11.5 0.500016V4.092C11.029 4.26 10.668 4.653 10.547 5.14402C7.264 5.79098 4.75201 8.586 4.525 12H4C3.17298 12 2.5 12.673 2.5 13.5C2.5 14.327 3.17298 15 4 15H4.50001V20H3.00001C2.72401 20 2.5 20.224 2.5 20.5V21H2.00003C1.17301 21 0.500031 21.673 0.500031 22.5V23.5C0.500031 23.776 0.724046 24 1.00005 24H23C23.276 24 23.5 23.776 23.5 23.5V22.5C23.5 21.673 22.827 21 22 21ZM12.5 1.00102H13.793L13.647 1.14703C13.452 1.34203 13.452 1.65905 13.647 1.85405L13.793 2.00006H12.5V1.00102ZM12 6C15.416 6 18.225 8.64802 18.481 12H5.51903C5.77501 8.64802 8.584 6 12 6ZM4 14C3.724 14 3.49998 13.775 3.49998 13.5C3.49998 13.225 3.724 13 4 13H20C20.276 13 20.5 13.225 20.5 13.5C20.5 13.775 20.276 14 20 14H4ZM18.5 15V20H17.5V15H18.5ZM16.5 15V20H15.5V15H16.5ZM14.5 15V20H13.5V15H14.5ZM12.5 15V20H11.5V15H12.5ZM10.5 15V20H9.50003V15H10.5ZM8.5 15V20H7.50002V15H8.5ZM6.50003 15V20H5.50005V15H6.50003ZM22.5 23H1.50001V22.5C1.50001 22.225 1.72403 22 2.00003 22H3.00001C3.27601 22 3.50003 21.776 3.50003 21.5V21H20.5V21.5C20.5 21.776 20.7241 22 21.0001 22H22C22.276 22 22.5001 22.225 22.5001 22.5V23H22.5Z",
          fill: color
        }
      ) })
    }
  );
};
var StateIcon_default = StateIcon;

// src/icons/StarIcon/Variants/StarIconDefault.tsx
import { useTheme as useTheme13 } from "@chakra-ui/react";
import { jsx as jsx132 } from "react/jsx-runtime";
var StarIconDefaultVariant = ({
  color,
  width = 12,
  height = 11,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  direction = "up",
  dataTestId = "star-icon",
  opacity,
  // Extra Properties
  variant,
  backgroundColor,
  backgroundOpacity = "1",
  borderOpacity = "1"
}) => {
  const theme = useTheme13();
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx132(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 12 11",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx132("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx132(
        "path",
        {
          d: "M5.7366 1.06036C5.84434 0.841004 6.15566 0.841004 6.2634 1.06036L7.65818 3.89992C7.70097 3.98704 7.78369 4.04742 7.87935 4.06139L10.9982 4.51674C11.2391 4.55191 11.3353 4.84939 11.161 5.02013L8.90415 7.23044C8.83494 7.29825 8.80335 7.39593 8.81969 7.49168L9.35245 10.6127C9.3936 10.8538 9.14175 11.0376 8.92626 10.9238L6.13668 9.45025C6.05113 9.40504 5.9489 9.40504 5.86333 9.45025L3.07374 10.9238C2.85825 11.0376 2.6064 10.8538 2.64755 10.6127L3.18031 7.49168C3.19665 7.39593 3.16506 7.29825 3.09585 7.23044L0.839026 5.02013C0.664688 4.84939 0.760891 4.55191 1.00182 4.51674L4.12065 4.06139C4.21631 4.04742 4.29903 3.98704 4.34182 3.89992L5.7366 1.06036Z",
          opacity: backgroundOpacity,
          fill: backgroundColor || theme.colors.gold,
          "data-testid": "path-with-color",
          stroke: color,
          strokeOpacity: borderOpacity
        }
      ) })
    }
  );
};
var StarIconDefault_default = StarIconDefaultVariant;

// src/icons/StarIcon/Variants/StarIconOutlined.tsx
import { useTheme as useTheme14 } from "@chakra-ui/react";
import { jsx as jsx133 } from "react/jsx-runtime";
var StarIconOutlinedVariant = ({
  color,
  width = 12,
  height = 11,
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "star-icon"
}) => {
  const theme = useTheme14();
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx133(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 12 11",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx133("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx133(
        "path",
        {
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M6 1.84722L4.86539 4.15711C4.73822 4.416 4.49168 4.59674 4.20491 4.6386L1.66219 5.00983L3.504 6.8137C3.7103 7.01574 3.80378 7.30601 3.75533 7.58983L3.32113 10.1334L5.59085 8.93447C5.8469 8.79919 6.1531 8.79919 6.40915 8.93447L8.67887 10.1334L8.24467 7.58983C8.19622 7.30601 8.2897 7.01574 8.496 6.8137L10.3378 5.00983L7.79509 4.6386C7.50833 4.59674 7.26178 4.416 7.13461 4.15711L6 1.84722ZM6.2634 1.06036C6.15566 0.841004 5.84434 0.841004 5.7366 1.06036L4.34182 3.89992C4.29903 3.98704 4.21631 4.04742 4.12065 4.06139L1.00182 4.51674C0.760891 4.55191 0.664688 4.84939 0.839026 5.02013L3.09585 7.23044C3.16506 7.29825 3.19665 7.39593 3.18031 7.49168L2.64755 10.6127C2.6064 10.8538 2.85825 11.0376 3.07374 10.9238L5.86333 9.45025C5.9489 9.40504 6.05113 9.40504 6.13668 9.45025L8.92626 10.9238C9.14175 11.0376 9.3936 10.8538 9.35245 10.6127L8.81969 7.49168C8.80335 7.39593 8.83494 7.29825 8.90415 7.23044L11.161 5.02013C11.3353 4.84939 11.2391 4.55191 10.9982 4.51674L7.87935 4.06139C7.78369 4.04742 7.70097 3.98704 7.65818 3.89992L6.2634 1.06036Z",
          fill: color || theme.colors.gold,
          "data-testid": "path-with-color"
        }
      ) })
    }
  );
};
var StarIconOutlined_default = StarIconOutlinedVariant;

// src/icons/StarIcon/StarIcon.tsx
import { jsx as jsx134 } from "react/jsx-runtime";
var StarIcon = ({ variant = "default", ...props }) => {
  const variants = {
    default: /* @__PURE__ */ jsx134(StarIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx134(StarIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var StarIcon_default = StarIcon;

// src/icons/StethoscopeIcon/StethoscopeIcon.tsx
import { jsx as jsx135, jsxs as jsxs78 } from "react/jsx-runtime";
var StethoscopeIcon = ({
  width = 46,
  height = 46,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "stethoscope-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx135(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 46 46",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs78("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx135(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M8.3335 5.5C9.71425 5.5 10.8335 4.38071 10.8335 3C10.8335 1.61929 9.71425 0.5 8.3335 0.5C7.40812 0.5 6.60025 1.00275 6.168 1.75H5.8335C3.76243 1.75 2.0835 3.42894 2.0835 5.5V16.75H4.5835V5.5C4.5835 4.80965 5.14315 4.25 5.8335 4.25H6.168C6.60025 4.99725 7.40812 5.5 8.3335 5.5Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx135(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M6.03396 16.75H4.58371H2.08371C1.39336 16.75 0.827843 17.3109 0.890493 17.9984C1.18111 21.1874 2.57831 24.19 4.86103 26.4727C6.53496 28.1466 8.59595 29.3444 10.8337 29.9788V33C10.8337 34.633 11.8775 36.0221 13.3342 36.5368C13.3875 41.4961 17.4243 45.5 22.3962 45.5C27.4013 45.5 31.4587 41.4426 31.4587 36.4375V31.4375C31.4587 28.8486 33.5573 26.75 36.1462 26.75C38.7351 26.75 40.8337 28.8486 40.8337 31.4375V34.4634C39.3772 34.9781 38.3337 36.3672 38.3337 38C38.3337 40.0711 40.0126 41.75 42.0837 41.75C44.1548 41.75 45.8337 40.0711 45.8337 38C45.8337 36.3672 44.7902 34.9781 43.3337 34.4634V31.4375C43.3337 27.468 40.1157 24.25 36.1462 24.25C32.1767 24.25 28.9587 27.468 28.9587 31.4375V36.4375C28.9587 40.0619 26.0206 43 22.3962 43C18.8048 43 15.8873 40.1151 15.8345 36.5364C17.2906 36.0214 18.3337 34.6325 18.3337 33V29.9788C20.5715 29.3444 22.6326 28.1466 24.3065 26.4727C26.5892 24.19 27.9863 21.1874 28.277 17.9984C28.3397 17.3109 27.7741 16.75 27.0837 16.75V5.5C27.0837 3.42894 25.4048 1.75 23.3337 1.75H22.9992C22.567 1.00275 21.7591 0.5 20.8337 0.5C19.453 0.5 18.3337 1.61929 18.3337 3C18.3337 4.38071 19.453 5.5 20.8337 5.5C21.7591 5.5 22.567 4.99725 22.9992 4.25H23.3337C24.0241 4.25 24.5837 4.80965 24.5837 5.5V16.75H23.1336C22.4432 16.75 21.8945 17.3136 21.7768 17.994C21.5218 19.4682 20.8177 20.8396 19.7455 21.9118C18.3766 23.2808 16.5198 24.0499 14.5837 24.0499C12.6477 24.0499 10.791 23.2808 9.42196 21.9118C8.34983 20.8396 7.64558 19.4682 7.39058 17.994C7.27295 17.3136 6.72421 16.75 6.03396 16.75ZM24.0593 19.25H25.5525C25.0853 21.2995 24.0488 23.1949 22.5387 24.705C20.429 26.8148 17.5675 28 14.5837 28C11.6001 28 8.73858 26.8148 6.62883 24.705C5.11867 23.1949 4.08217 21.2995 3.61498 19.25H5.10812C5.5465 20.9114 6.4182 22.4435 7.6542 23.6795C9.49208 25.5174 11.9847 26.5499 14.5837 26.5499C17.1828 26.5499 19.6755 25.5174 21.5133 23.6795C22.7493 22.4435 23.621 20.9114 24.0593 19.25ZM42.0837 36.75C42.7741 36.75 43.3337 37.3096 43.3337 38C43.3337 38.6904 42.7741 39.25 42.0837 39.25C41.3933 39.25 40.8337 38.6904 40.8337 38C40.8337 37.3096 41.3933 36.75 42.0837 36.75ZM15.8337 30.5V33C15.8337 33.6904 15.2741 34.25 14.5837 34.25C13.8933 34.25 13.3337 33.6904 13.3337 33V30.5H15.8337Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var StethoscopeIcon_default = StethoscopeIcon;

// src/icons/StethoscopeIconSolid/StethoscopeIconSolid.tsx
import { jsx as jsx136, jsxs as jsxs79 } from "react/jsx-runtime";
var StethoscopeIconSolid = ({
  width = 46,
  height = 46,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#DDE8EE",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "stethoscope-icon-solid",
  backgroundColor = "#8DBAD5"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx136(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 35 34",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs79("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx136(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M2.00586 0.640137C1.50858 0.640137 1.03166 0.837681 0.680034 1.18931C0.328404 1.54094 0.130859 2.01786 0.130859 2.51514V11.5001C0.130715 13.8338 0.952899 16.0929 2.45298 17.8806C3.95307 19.6682 6.03511 20.8701 8.33336 21.2751V22.6451C8.33336 25.5947 9.50506 28.4234 11.5907 30.5091C13.6763 32.5947 16.5051 33.7664 19.4546 33.7664C22.4041 33.7664 25.2329 32.5947 27.3185 30.5091C29.4042 28.4234 30.5759 25.5947 30.5759 22.6451V14.8601C30.5759 14.3629 30.3783 13.8859 30.0267 13.5343C29.6751 13.1827 29.1981 12.9851 28.7009 12.9851C28.2036 12.9851 27.7267 13.1827 27.375 13.5343C27.0234 13.8859 26.8259 14.3629 26.8259 14.8601V22.6451C26.8259 24.6001 26.0492 26.475 24.6669 27.8574C23.2845 29.2398 21.4096 30.0164 19.4546 30.0164C17.4996 30.0164 15.6247 29.2398 14.2423 27.8574C12.86 26.475 12.0834 24.6001 12.0834 22.6451V21.2151C14.314 20.7478 16.3158 19.5271 17.7526 17.758C19.1894 15.9889 19.9736 13.7792 19.9734 11.5001V2.51514C19.9734 2.01786 19.7758 1.54094 19.4242 1.18931C19.0726 0.837681 18.5956 0.640137 18.0984 0.640137H15.3659C15.1196 0.640137 14.8758 0.688635 14.6483 0.782863C14.4208 0.87709 14.2141 1.0152 14.04 1.18931C13.8659 1.36342 13.7278 1.57012 13.6336 1.79761C13.5394 2.02509 13.4909 2.26891 13.4909 2.51514C13.4909 2.76137 13.5394 3.00518 13.6336 3.23267C13.7278 3.46015 13.8659 3.66685 14.04 3.84096C14.2141 4.01507 14.4208 4.15318 14.6483 4.24741C14.8758 4.34164 15.1196 4.39014 15.3659 4.39014H16.2234V11.5001C16.1908 13.1153 15.5264 14.6534 14.3726 15.7842C13.2188 16.915 11.6676 17.5484 10.0521 17.5484C8.43657 17.5484 6.88543 16.915 5.73163 15.7842C4.57784 14.6534 3.91337 13.1153 3.88086 11.5001V4.39014H4.74086C5.23814 4.39014 5.71505 4.19259 6.06668 3.84096C6.41832 3.48933 6.61586 3.01242 6.61586 2.51514C6.61586 2.01786 6.41832 1.54094 6.06668 1.18931C5.71505 0.837681 5.23814 0.640137 4.74086 0.640137H2.00586Z",
            fill: backgroundColor
          }
        ),
        /* @__PURE__ */ jsx136(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M30.0786 3.95787C29.2774 3.76135 28.4451 3.72618 27.6302 3.85439C26.8153 3.98261 26.034 4.27166 25.3319 4.7047C24.6297 5.13775 24.0207 5.70615 23.5403 6.3768C23.0599 7.04746 22.7178 7.80698 22.5337 8.61114C22.3497 9.4153 22.3274 10.248 22.4683 11.0609C22.6092 11.8737 22.9103 12.6504 23.3542 13.3458C23.7981 14.0411 24.3759 14.6412 25.0539 15.1111C25.732 15.581 26.4967 15.9113 27.3036 16.0829C28.9009 16.4224 30.5675 16.1202 31.9439 15.2415C33.3203 14.3629 34.2959 12.9782 34.6602 11.3865C35.0245 9.79472 34.7482 8.12355 33.8911 6.73371C33.0339 5.34387 31.6646 4.34685 30.0786 3.95787Z",
            "data-testid": "path-with-color",
            fill: color
          }
        )
      ] })
    }
  );
};
var StethoscopeIconSolid_default = StethoscopeIconSolid;

// src/icons/StomachIcon/StomachIcon.tsx
import { jsx as jsx137 } from "react/jsx-runtime";
var StomachIcon = ({
  width = 46,
  height = 46,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "stomach-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx137(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 46 46",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx137("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx137(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M13.8944 11.9021C15.3692 13.5099 17.861 14.1995 22.4275 14.2973C22.6879 10.8485 24.0163 8.13176 25.9821 6.22476C28.1674 4.1052 31.0654 3.05957 33.9404 3.05957C36.8153 3.05957 39.7133 4.1052 41.8985 6.22476C44.0985 8.35863 45.5 11.5066 45.5 15.5596V29.3096C45.5 33.1624 44.8584 37.1861 42.9169 40.2856C40.9189 43.4753 37.6189 45.5596 32.679 45.5596C27.6278 45.5596 24.1106 42.5619 20.8947 39.7873L20.7547 39.6665C17.5164 36.8716 14.499 34.2676 10.2086 33.6849C8.10475 33.7008 6.6885 34.3436 5.69975 35.2238C4.67429 36.1365 4.01296 37.391 3.60012 38.7383C3.18809 40.083 3.04554 41.4499 3.00989 42.4966C2.9922 43.0161 3.00112 43.4471 3.01415 43.7446C3.02065 43.8933 3.02815 44.0079 3.03376 44.0829C3.03657 44.1204 3.03891 44.1479 3.04041 44.1648L3.04199 44.182L3.04212 44.1834C3.04209 44.183 3.04204 44.1825 1.79851 44.3096C0.554974 44.4366 0.554924 44.436 0.554862 44.4355L0.554724 44.434L0.554362 44.4305L0.553349 44.42L0.55025 44.3863C0.54775 44.3583 0.544462 44.319 0.540749 44.2696C0.533337 44.1706 0.524249 44.0301 0.516537 43.854C0.501137 43.5021 0.491124 43.0054 0.511337 42.4115C0.551512 41.2318 0.712187 39.6299 1.20982 38.0059C1.70667 36.3844 2.56147 34.6701 4.03762 33.3563C5.53787 32.021 7.58537 31.1846 10.2887 31.1846C10.9175 31.1846 11.5099 31.1989 12.079 31.2125C13.1365 31.238 14.1137 31.2615 15.0944 31.188C16.5376 31.0799 17.728 30.7654 18.7116 30.0281C20.5944 28.617 22.2264 25.2479 22.3704 16.7973C17.7216 16.7009 14.2699 16.0098 12.0521 13.592C10.8476 12.279 10.104 10.5606 9.6555 8.43476C9.20862 6.31676 9.03875 3.71581 9.03875 0.55957H11.5387C11.5387 3.65333 11.7079 6.05238 12.1016 7.91876C12.4937 9.77726 13.0922 11.0276 13.8944 11.9021ZM22.2132 37.6226C20.5779 36.2094 18.8289 34.6979 16.8055 33.4828C17.9854 33.2534 19.1496 32.8241 20.211 32.0285C21.8365 30.8103 23.0615 28.8616 23.8481 25.8689C25.9064 24.4801 27.5984 24.1869 29.0841 24.3504C30.6733 24.5251 32.1429 25.2283 33.763 26.0495L34.0326 26.1865C35.5039 26.935 37.1637 27.7794 38.9277 28.0015C40.2675 28.1703 41.6188 27.9823 43 27.29V29.3096C43 32.9568 42.3803 36.433 40.7983 38.9585C39.2728 41.3939 36.7873 43.0596 32.679 43.0596C28.682 43.0596 25.8314 40.7448 22.5279 37.8944L22.2132 37.6226ZM43 24.3428V15.5596C43 12.1125 41.8241 9.63551 40.1579 8.01938C38.4771 6.38901 36.2202 5.55963 33.9404 5.55963C31.6605 5.55963 29.4036 6.38901 27.7227 8.01938C26.0566 9.63551 24.8808 12.1125 24.8808 15.5596C24.8808 18.3025 24.7407 20.6548 24.467 22.6671C26.1952 21.8993 27.8155 21.6958 29.3574 21.8653C31.4596 22.0965 33.3104 23.0171 34.8934 23.8196L35.0062 23.8769C36.62 24.6953 37.921 25.355 39.2402 25.5211C40.3531 25.6614 41.5578 25.4515 43 24.3428Z",
          fill: color
        }
      ) })
    }
  );
};
var StomachIcon_default = StomachIcon;

// src/icons/SuiteIcon/SuiteIcon.tsx
import { jsx as jsx138, jsxs as jsxs80 } from "react/jsx-runtime";
var SuiteIcon = ({
  width = 18,
  height = 17,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#59595B",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "suite-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx138(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 18 17",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs80("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx138(
          "rect",
          {
            "data-testid": "path-with-color",
            x: "0.666656",
            y: "0.166626",
            width: "7.40019",
            height: "7.40019",
            rx: "2",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx138(
          "rect",
          {
            x: "0.666656",
            y: "9.41699",
            width: "7.40019",
            height: "7.40019",
            rx: "2",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx138(
          "rect",
          {
            x: "9.91702",
            y: "0.166626",
            width: "7.40019",
            height: "7.40019",
            rx: "2",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx138(
          "rect",
          {
            x: "9.91702",
            y: "9.41699",
            width: "7.40019",
            height: "7.40019",
            rx: "2",
            fill: color
          }
        )
      ] })
    }
  );
};
var SuiteIcon_default = SuiteIcon;

// src/icons/TriangleIcon/TriangleIcon.tsx
import { jsx as jsx139 } from "react/jsx-runtime";
var TriangleIcon = ({
  width = 8,
  height = 7,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "triangle-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx139(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 8 7",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsx139("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx139(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M2.95005 0.500001C3.33495 -0.166666 4.2972 -0.166666 4.6821 0.5L7.49668 5.375C7.88158 6.04167 7.40046 6.875 6.63066 6.875L1.00149 6.875C0.231691 6.875 -0.249435 6.04167 0.135466 5.375L2.95005 0.500001Z",
          fill: color
        }
      ) })
    }
  );
};
var TriangleIcon_default = TriangleIcon;

// src/icons/UserIcon/UserIcon.tsx
import { jsx as jsx140, jsxs as jsxs81 } from "react/jsx-runtime";
var UserIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#191919",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "user-icon",
  backgroundColor = "#FFFFFF"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx140(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs81("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx140(
          "path",
          {
            d: "M12.5 15.75C14.4891 15.75 16.3968 14.9598 17.8033 13.5533C19.2098 12.1468 20 10.2391 20 8.25C20 6.26088 19.2098 4.35322 17.8033 2.9467C16.3968 1.54018 14.4891 0.75 12.5 0.75C10.5109 0.75 8.60322 1.54018 7.1967 2.9467C5.79018 4.35322 5 6.26088 5 8.25C5 10.2391 5.79018 12.1468 7.1967 13.5533C8.60322 14.9598 10.5109 15.75 12.5 15.75Z",
            fill: backgroundColor
          }
        ),
        /* @__PURE__ */ jsx140(
          "path",
          {
            d: "M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 6C13.93 6 15.5 7.57 15.5 9.5C15.5 11.43 13.93 13 12 13C10.07 13 8.5 11.43 8.5 9.5C8.5 7.57 10.07 6 12 6ZM12 20C9.97 20 7.57 19.18 5.86 17.12C7.55 15.8 9.68 15 12 15C14.32 15 16.45 15.8 18.14 17.12C16.43 19.18 14.03 20 12 20Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        )
      ] })
    }
  );
};
var UserIcon_default = UserIcon;

// src/icons/UploadIcon/UploadIcon.tsx
import { jsx as jsx141, jsxs as jsxs82 } from "react/jsx-runtime";
var UploadIcon = ({
  width = 49,
  height = 49,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#CFCFCD",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "upload-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx141(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 49 49",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs82("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx141(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M13.4932 36.4507C13.4932 37.5542 12.5972 38.4507 11.4932 38.4507C5.42822 38.4507 0.492188 33.5161 0.492188 27.4507C0.492188 21.6108 5.06836 16.8179 10.8242 16.4722C12.2031 10.1587 17.877 5.45068 24.4922 5.45068C31.1074 5.45068 36.7812 10.1597 38.1611 16.4722C43.916 16.8179 48.4922 21.6099 48.4922 27.4507C48.4922 33.5161 43.5571 38.4507 37.4922 38.4507C36.3882 38.4507 35.4922 37.5542 35.4922 36.4507C35.4922 35.3472 36.3882 34.4507 37.4922 34.4507C41.3511 34.4507 44.4922 31.3101 44.4922 27.4507C44.4922 23.5913 41.3511 20.4507 37.4922 20.4507C37.2163 20.4507 36.9453 20.4722 36.6772 20.5024C36.1372 20.5659 35.5942 20.4067 35.1743 20.061C34.7544 19.7144 34.4932 19.2124 34.4512 18.6704C34.0522 13.5005 29.6772 9.45068 24.4922 9.45068C19.3071 9.45068 14.9331 13.5005 14.5342 18.6704C14.4922 19.2124 14.231 19.7144 13.811 20.061C13.3921 20.4048 12.8491 20.564 12.3091 20.5024C12.041 20.4722 11.77 20.4507 11.4932 20.4507C7.6333 20.4507 4.49316 23.5913 4.49316 27.4507C4.49316 31.3101 7.6333 34.4507 11.4932 34.4507C12.5972 34.4507 13.4932 35.3472 13.4932 36.4507Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx141(
          "path",
          {
            d: "M26.4932 41.9507C26.4932 43.0542 25.5972 43.9507 24.4932 43.9507C23.3892 43.9507 22.4932 43.0542 22.4932 41.9507V29.0698L20.1992 31.3647C19.8096 31.7554 19.2974 31.9507 18.7852 31.9507C18.2734 31.9507 17.7622 31.7554 17.3716 31.3647C16.5903 30.5835 16.5903 29.3179 17.3716 28.5366L23.0796 22.8276C23.8296 22.0776 25.1572 22.0776 25.9072 22.8276L31.6162 28.5366C32.3975 29.3179 32.3975 30.5835 31.6162 31.3647C30.8354 32.146 29.5693 32.146 28.7886 31.3647L26.4932 29.0698V41.9507Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var UploadIcon_default = UploadIcon;

// src/icons/UserGroupIcon/UserGroupIcon.tsx
import { jsx as jsx142, jsxs as jsxs83 } from "react/jsx-runtime";
var UserGroupIcon = ({
  width = 46,
  height = 46,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#DDE8EE",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "user-group-icon",
  backgroundColor = "#8DBAD5"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx142(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 35 34",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs83("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx142(
          "path",
          {
            d: "M12.5 15.75C14.4891 15.75 16.3968 14.9598 17.8033 13.5533C19.2098 12.1468 20 10.2391 20 8.25C20 6.26088 19.2098 4.35322 17.8033 2.9467C16.3968 1.54018 14.4891 0.75 12.5 0.75C10.5109 0.75 8.60322 1.54018 7.1967 2.9467C5.79018 4.35322 5 6.26088 5 8.25C5 10.2391 5.79018 12.1468 7.1967 13.5533C8.60322 14.9598 10.5109 15.75 12.5 15.75Z",
            fill: backgroundColor
          }
        ),
        /* @__PURE__ */ jsx142(
          "path",
          {
            d: "M0 30.75C0 27.4348 1.31696 24.2554 3.66117 21.9112C6.00537 19.567 9.18479 18.25 12.5 18.25C15.8152 18.25 18.9946 19.567 21.3388 21.9112C23.683 24.2554 25 27.4348 25 30.75C25 31.0815 24.8683 31.3995 24.6339 31.6339C24.3995 31.8683 24.0815 32 23.75 32H1.25C0.918479 32 0.600537 31.8683 0.366117 31.6339C0.131696 31.3995 0 31.0815 0 30.75Z",
            fill: backgroundColor
          }
        ),
        /* @__PURE__ */ jsx142(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M27.9451 32.0002H33.7501C34.0816 32.0002 34.3995 31.8685 34.634 31.6341C34.8684 31.3997 35.0001 31.0817 35.0001 30.7502C35 29.0578 34.6563 27.383 33.9897 25.8273C33.3231 24.2717 32.3476 22.8676 31.1222 21.7002C29.8969 20.5328 28.4472 19.6264 26.8611 19.036C25.275 18.4455 23.5855 18.1833 21.8951 18.2652C23.8314 19.7199 25.4024 21.6054 26.4837 23.7724C27.5651 25.9394 28.127 28.3284 28.1251 30.7502C28.1251 31.1852 28.0626 31.6052 27.9451 32.0002ZM20.3301 15.4327C21.4503 15.7702 22.6339 15.8409 23.7863 15.6392C24.9387 15.4375 26.0279 14.969 26.9669 14.2712C27.9059 13.5734 28.6686 12.6656 29.1941 11.6203C29.7196 10.5751 29.9933 9.42138 29.9933 8.25147C29.9933 7.08156 29.7196 5.92787 29.1941 4.88262C28.6686 3.83737 27.9059 2.92956 26.9669 2.23173C26.0279 1.53391 24.9387 1.06543 23.7863 0.863751C22.6339 0.662069 21.4503 0.732774 20.3301 1.07022C22.1304 3.02758 23.1282 5.59079 23.1251 8.25022C23.1289 10.9105 22.131 13.4747 20.3301 15.4327Z",
            "data-testid": "path-with-color",
            fill: color
          }
        )
      ] })
    }
  );
};
var UserGroupIcon_default = UserGroupIcon;

// src/icons/WarningTriangleIcon/WarningTriangleIcon.tsx
import { jsx as jsx143, jsxs as jsxs84 } from "react/jsx-runtime";
var TriangleIcon2 = ({
  width = 116,
  height = 82,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#AE2824",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "warning-triangle-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx143(
    "svg",
    {
      width,
      height,
      viewBox: "0 0 116 82",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      opacity,
      children: /* @__PURE__ */ jsxs84("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx143(
          "path",
          {
            d: "M27.2528 70.2702C25.4918 70.2688 23.7621 69.8047 22.2369 68.9244C20.7116 68.044 19.4445 66.7784 18.5624 65.2542C17.6803 63.73 17.2142 62.0008 17.2108 60.2398C17.2073 58.4788 17.6667 56.7478 18.5428 55.2202L47.3328 5.00018C48.2075 3.47295 49.4719 2.20532 50.9969 1.32684C52.522 0.448369 54.2529 -0.00944463 56.0128 0.000182239C57.7729 -0.0104799 59.5043 0.446891 61.0295 1.32547C62.5547 2.20405 63.819 3.47225 64.6928 5.00018L93.7928 55.0502C94.6952 56.5721 95.1776 58.3061 95.1909 60.0754C95.2041 61.8446 94.7477 63.5857 93.8683 65.121C92.9888 66.6562 91.7178 67.9306 90.1849 68.8142C88.652 69.6977 86.9121 70.1587 85.1428 70.1502L27.2428 70.3302L27.2528 70.2702ZM56.0528 2.95018C54.8154 2.94931 53.5999 3.27648 52.5301 3.89833C51.4603 4.52018 50.5745 5.4145 49.9628 6.49017L21.1728 56.7102C20.5576 57.7819 20.2348 58.9964 20.2369 60.2321C20.239 61.4678 20.5659 62.6813 21.1848 63.7509C21.8036 64.8205 22.6927 65.7086 23.763 66.3263C24.8333 66.9439 26.0471 67.2694 27.2828 67.2702L85.1828 67.0902C86.4256 67.101 87.6488 66.7807 88.7268 66.1623C89.8048 65.5439 90.6988 64.6497 91.3168 63.5714C91.9348 62.4932 92.2546 61.2699 92.2434 60.0271C92.2321 58.7844 91.8902 57.5671 91.2528 56.5002L62.1528 6.50018C61.5416 5.42117 60.6548 4.52388 59.583 3.90013C58.5112 3.27638 57.2929 2.94854 56.0528 2.95018Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx143(
          "path",
          {
            d: "M52.0428 54.0101C52.0411 53.51 52.1405 53.0148 52.335 52.5542C52.5296 52.0935 52.8152 51.6769 53.1749 51.3294C53.5345 50.982 53.9606 50.7108 54.4277 50.5322C54.8948 50.3536 55.3931 50.2712 55.8928 50.2901C56.3965 50.2602 56.901 50.3331 57.3756 50.5043C57.8502 50.6755 58.285 50.9415 58.6536 51.286C59.0222 51.6306 59.3169 52.0465 59.5196 52.5085C59.7224 52.9705 59.8291 53.469 59.8331 53.9735C59.8372 54.4781 59.7386 54.9782 59.5433 55.4434C59.348 55.9086 59.0601 56.3293 58.6972 56.6797C58.3342 57.0302 57.9037 57.3031 57.4319 57.482C56.9601 57.6609 56.4569 57.7419 55.9528 57.7201C55.4501 57.743 54.9479 57.6642 54.4763 57.4884C54.0047 57.3126 53.5735 57.0434 53.2084 56.697C52.8434 56.3506 52.5519 55.9341 52.3517 55.4724C52.1514 55.0107 52.0463 54.5134 52.0428 54.0101ZM52.1628 23.8501H59.2628L58.2628 46.3001H53.5128L52.1628 23.8501Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx143(
          "path",
          {
            d: "M19.8723 81.59C19.4215 81.4914 18.9848 81.3369 18.5723 81.13C17.4186 80.6343 16.3138 80.0316 15.2723 79.33C11.7934 77.0017 9.05507 73.7247 7.38192 69.8874C5.70877 66.0502 5.171 61.8136 5.83235 57.68C6.01736 56.4401 6.32211 55.2211 6.74234 54.04C6.87673 53.5971 7.06127 53.171 7.29234 52.77C6.82131 54.412 6.45392 56.082 6.19233 57.77C5.64552 61.829 6.22251 65.9602 7.8605 69.714C9.4985 73.4678 12.1348 76.7004 15.4823 79.06C16.8922 79.9929 18.3583 80.8379 19.8723 81.59Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx143(
          "path",
          {
            d: "M7.52275 80.3702C7.52275 80.3702 7.26276 80.2202 6.85276 79.8602C6.27545 79.3619 5.74031 78.8167 5.25276 78.2302C3.60567 76.308 2.30928 74.1112 1.42276 71.7402C0.530558 69.3699 0.0568414 66.8626 0.0227535 64.3302C0.0125636 63.5681 0.0560091 62.8062 0.152758 62.0502C0.165394 61.7663 0.222765 61.4862 0.322757 61.2202C0.412757 61.2202 0.322764 62.4202 0.392764 64.3202C0.650888 69.3019 2.4466 74.0811 5.53276 78.0002C6.76276 79.4902 7.58275 80.3002 7.52275 80.3702Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx143(
          "path",
          {
            d: "M108.533 45.5898C108.706 45.9385 108.834 46.3083 108.913 46.6898C109.052 47.1185 109.163 47.5562 109.243 47.9998C109.374 48.5874 109.475 49.1816 109.543 49.7798C109.75 51.3306 109.814 52.8973 109.733 54.4598C109.618 56.3746 109.317 58.2737 108.833 60.1298C108.311 61.9785 107.615 63.7733 106.753 65.4898C106.035 66.8967 105.192 68.2355 104.233 69.4898C103.843 70.0298 103.423 70.4898 103.073 70.8698C102.78 71.225 102.462 71.5591 102.123 71.8698C101.858 72.1506 101.566 72.4051 101.253 72.6298C101.173 72.5498 102.383 71.4299 103.933 69.2999C104.839 68.0354 105.639 66.6974 106.323 65.2999C108.005 61.9152 109.001 58.2311 109.253 54.4598C109.353 52.9238 109.33 51.3821 109.183 49.8498C108.913 47.1498 108.433 45.5898 108.533 45.5898Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        ),
        /* @__PURE__ */ jsx143(
          "path",
          {
            d: "M115.082 56.5898C115.253 57.4554 115.301 58.341 115.222 59.2198C115.073 63.4878 113.911 67.6591 111.832 71.3898C111.444 72.1796 110.946 72.9103 110.352 73.5598C110.688 72.7575 111.072 71.976 111.502 71.2198C113.37 67.4684 114.507 63.3961 114.852 59.2198C114.877 58.3394 114.953 57.4612 115.082 56.5898Z",
            fill: color,
            "data-testid": "path-with-color"
          }
        )
      ] })
    }
  );
};
var WarningTriangleIcon_default = TriangleIcon2;

// src/icons/WarningIcon/Variants/WarningIconDefaultVariant.tsx
import { jsx as jsx144 } from "react/jsx-runtime";
var WarningIconDefaultVariant = ({
  width = 26,
  height = 26,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#AE2824",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "warning-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx144(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      viewBox: "0 0 14 15",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx144("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx144(
        "path",
        {
          "data-testid": "path-with-color",
          d: "M13.6668 7.5C13.6668 11.1827 10.6818 14.1667 7.00016 14.1667C3.31852 14.1667 0.333496 11.1827 0.333496 7.5C0.333496 3.81944 3.31852 0.833333 7.00016 0.833333C10.6818 0.833333 13.6668 3.81944 13.6668 7.5ZM7.00016 8.84409C6.31723 8.84409 5.7636 9.39771 5.7636 10.0806C5.7636 10.7636 6.31723 11.3172 7.00016 11.3172C7.68309 11.3172 8.23672 10.7636 8.23672 10.0806C8.23672 9.39771 7.68309 8.84409 7.00016 8.84409ZM5.82616 4.3993L6.02557 8.05521C6.03489 8.22629 6.17635 8.36021 6.34766 8.36021H7.65266C7.82398 8.36021 7.96543 8.22629 7.97476 8.05521L8.17417 4.3993C8.18425 4.21452 8.03713 4.05914 7.85207 4.05914H6.14823C5.96317 4.05914 5.81608 4.21452 5.82616 4.3993Z",
          fill: color
        }
      ) })
    }
  );
};
var WarningIconDefaultVariant_default = WarningIconDefaultVariant;

// src/icons/WarningIcon/Variants/WarningIconOutlinedVariant.tsx
import { jsx as jsx145, jsxs as jsxs85 } from "react/jsx-runtime";
var WarningIconOutlinedVariant = ({
  width = 26,
  height = 26,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#AE2824",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "warning-icon",
  // Extra properties
  variant = "default"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx145(
    "svg",
    {
      width,
      height,
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      viewBox: "0 0 16 15",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs85("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx145(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M6.70843 4.08923L6.92778 7.7774C6.93804 7.96559 7.09364 8.1129 7.28209 8.1129H8.71759C8.90604 8.1129 9.06163 7.96559 9.07189 7.7774L9.29124 3.7559C9.30233 3.55263 9.1405 3.38172 8.93694 3.38172H7.06271C6.85915 3.38172 6.69734 3.55263 6.70843 4.08923Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx145(
          "path",
          {
            d: "M7.99984 9.31183C7.24861 9.31183 6.63962 9.92082 6.63962 10.672C6.63962 11.4233 7.24861 12.0323 7.99984 12.0323C8.75106 12.0323 9.36005 11.4233 9.36005 10.672C9.36005 9.92082 8.75106 9.31183 7.99984 9.31183Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx145(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M7.99984 13.5C11.3135 13.5 13.9998 10.8137 13.9998 7.5C13.9998 4.18629 11.3135 1.5 7.99984 1.5C4.68613 1.5 1.99984 4.18629 1.99984 7.5C1.99984 10.8137 4.68613 13.5 7.99984 13.5ZM7.99984 14.8333C12.0499 14.8333 15.3332 11.5501 15.3332 7.5C15.3332 3.44991 12.0499 0.166667 7.99984 0.166667C3.94975 0.166667 0.666504 3.44991 0.666504 7.5C0.666504 11.5501 3.94975 14.8333 7.99984 14.8333Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var WarningIconOutlinedVariant_default = WarningIconOutlinedVariant;

// src/icons/WarningIcon/WarningIcon.tsx
import { jsx as jsx146 } from "react/jsx-runtime";
var WarningIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx146(WarningIconDefaultVariant_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx146(WarningIconOutlinedVariant_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var WarningIcon_default = WarningIcon;

// src/icons/WebinarIcon/Variants/WebinarIconDefault.tsx
import { jsx as jsx147, jsxs as jsxs86 } from "react/jsx-runtime";
var WebinarIconDefaultVariant = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "webinar-icon",
  // Extra Properties
  backgroundColor = "#8DBAD5",
  backgroundOpacity = "1",
  borderOpacity = "1",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx147(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsxs86("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx147("g", { "data-testid": "g-background-path", opacity: backgroundOpacity, children: /* @__PURE__ */ jsx147(
          "path",
          {
            "data-testid": "path-with-background-color",
            d: "M61.6875 13.5H26.6875C12.3281 13.5 0.6875 25.1406 0.6875 39.5V81.5C0.6875 95.8594 12.3281 107.5 26.6875 107.5H61.6875C76.0469 107.5 87.6875 95.8594 87.6875 81.5V39.5C87.6875 25.1406 76.0469 13.5 61.6875 13.5Z",
            fill: backgroundColor
          }
        ) }),
        /* @__PURE__ */ jsx147("g", { opacity: borderOpacity, "data-testid": "g-border-path", children: /* @__PURE__ */ jsx147(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M87.6875 38.5759L101.371 28.0502C109.262 21.9798 120.688 27.6055 120.688 37.5616V83.4382C120.688 93.3943 109.262 99.02 101.371 92.9497L87.6875 82.4239V38.5759Z",
            fill: color
          }
        ) })
      ] })
    }
  );
};
var WebinarIconDefault_default = WebinarIconDefaultVariant;

// src/icons/WebinarIcon/Variants/WebinarIconOutlined.tsx
import { jsx as jsx148 } from "react/jsx-runtime";
var WebinarIconDefaultVariant2 = ({
  color = "#EC9F24",
  width = "121",
  height = "121",
  margin = 0,
  padding = 0,
  cursor = "default",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "webinar-icon",
  variant
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx148(
    "svg",
    {
      viewBox: "0 0 121 121",
      width,
      height,
      fill: "none",
      style: {
        margin,
        padding,
        cursor,
        rotate: directionRotation[direction]
      },
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      "data-variant": variant,
      children: /* @__PURE__ */ jsx148("g", { "data-testid": "g-path", opacity, children: /* @__PURE__ */ jsx148(
        "path",
        {
          "data-testid": "path-with-color",
          fillRule: "evenodd",
          clipRule: "evenodd",
          d: "M66.8125 13.5H26.8125C12.4531 13.5 0.8125 25.1406 0.8125 39.5V81.5C0.8125 95.8594 12.4531 107.5 26.8125 107.5H66.8125C78.7753 107.5 88.851 99.4209 91.8812 88.4213L102.985 94.59C110.983 99.0335 120.812 93.2499 120.812 84.1001V36.8981C120.812 27.7483 110.983 21.9647 102.985 26.4082L91.8807 32.5772C88.8501 21.5784 78.7747 13.5 66.8125 13.5ZM92.8125 44.6759V76.3223C92.8125 77.0486 93.2063 77.7179 93.8412 78.0706L107.841 85.8484C109.174 86.589 110.812 85.625 110.812 84.1001V36.8981C110.812 35.3731 109.174 34.4092 107.841 35.1498L93.8412 42.9276C93.2063 43.2803 92.8125 43.9496 92.8125 44.6759ZM82.8125 39.5C82.8125 30.6634 75.6491 23.5 66.8125 23.5H26.8125C17.9759 23.5 10.8125 30.6634 10.8125 39.5V81.5C10.8125 90.3366 17.9759 97.5 26.8125 97.5H66.8125C75.6491 97.5 82.8125 90.3366 82.8125 81.5V39.5Z",
          fill: color
        }
      ) })
    }
  );
};
var WebinarIconOutlined_default = WebinarIconDefaultVariant2;

// src/icons/WebinarIcon/WebinarIcon.tsx
import { jsx as jsx149 } from "react/jsx-runtime";
var WebinarIcon = ({
  variant = "default",
  ...props
}) => {
  const variants = {
    default: /* @__PURE__ */ jsx149(WebinarIconDefault_default, { variant: "default", ...props }),
    outlined: /* @__PURE__ */ jsx149(WebinarIconOutlined_default, { variant: "outlined", ...props })
  };
  return variants[variant];
};
var WebinarIcon_default = WebinarIcon;

// src/icons/ZipCodeIcon/ZipCodeIcon.tsx
import { jsx as jsx150, jsxs as jsxs87 } from "react/jsx-runtime";
var ZipCodeIcon = ({
  width = 24,
  height = 24,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#58585A",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "zipcode-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx150(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 24 24",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: handleClick,
      children: /* @__PURE__ */ jsxs87("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx150(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M13.6406 14.9297C14.2878 14.9297 14.8125 14.405 14.8125 13.7578C14.8125 13.1106 14.2878 12.5859 13.6406 12.5859C12.9934 12.5859 12.4688 13.1106 12.4688 13.7578C12.4688 14.405 12.9934 14.9297 13.6406 14.9297Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx150(
          "path",
          {
            d: "M16.9219 14.9297C17.5691 14.9297 18.0938 14.405 18.0938 13.7578C18.0938 13.1106 17.5691 12.5859 16.9219 12.5859C16.2747 12.5859 15.75 13.1106 15.75 13.7578C15.75 14.405 16.2747 14.9297 16.9219 14.9297Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx150(
          "path",
          {
            d: "M10.3594 14.9297C11.0066 14.9297 11.5312 14.405 11.5312 13.7578C11.5312 13.1106 11.0066 12.5859 10.3594 12.5859C9.71217 12.5859 9.1875 13.1106 9.1875 13.7578C9.1875 14.405 9.71217 14.9297 10.3594 14.9297Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx150(
          "path",
          {
            d: "M7.07812 14.9297C7.72533 14.9297 8.25 14.405 8.25 13.7578C8.25 13.1106 7.72533 12.5859 7.07812 12.5859C6.43092 12.5859 5.90625 13.1106 5.90625 13.7578C5.90625 14.405 6.43092 14.9297 7.07812 14.9297Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx150(
          "path",
          {
            d: "M23.0625 3.5625H0.9375C0.419719 3.5625 0 3.98222 0 4.5V19.5C0 20.0178 0.419719 20.4375 0.9375 20.4375H23.0625C23.5803 20.4375 24 20.0178 24 19.5V4.5C24 3.98222 23.5803 3.5625 23.0625 3.5625ZM22.125 18.5625H1.875V5.4375H22.125V18.5625Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx150(
          "path",
          {
            d: "M7.07812 10.0078H16.9219C17.4397 10.0078 17.8594 9.58809 17.8594 9.07031C17.8594 8.55253 17.4397 8.13281 16.9219 8.13281H7.07812C6.56034 8.13281 6.14062 8.55253 6.14062 9.07031C6.14062 9.58809 6.56034 10.0078 7.07812 10.0078Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var ZipCodeIcon_default = ZipCodeIcon;

// src/icons/ZipFileIcon/ZipFileIcon.tsx
import { jsx as jsx151, jsxs as jsxs88 } from "react/jsx-runtime";
var ZipFileIcon = ({
  width = 45,
  height = 48,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#3992C6",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "zip-file-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx151(
    "svg",
    {
      width,
      height,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      viewBox: "0 0 45 48",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": dataTestId,
      "data-direction": direction,
      onClick: (e) => handleClick ? handleClick(e) : null,
      children: /* @__PURE__ */ jsxs88("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx151(
          "path",
          {
            "data-testid": "path-with-color",
            d: "M11.9856 37H18.0221C18.5778 37 19.0282 36.5523 19.0282 36C19.0282 35.4477 18.5778 35 18.0221 35H13.8656L18.8592 27.5547C19.3048 26.8906 18.8238 26 18.0221 26H11.9856C11.4299 26 10.9795 26.4477 10.9795 27C10.9795 27.5523 11.4299 28 11.9856 28H16.1421L11.1485 35.4453C10.7029 36.1094 11.1839 37 11.9856 37Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx151(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M25.0648 27C25.0648 26.4477 25.5154 26 26.0709 26H29.5923C31.5339 26 33.1136 27.5701 33.1136 29.5C33.1136 31.4299 31.5339 33 29.5923 33H27.077V36C27.077 36.5523 26.6265 37 26.0709 37C25.5154 37 25.0648 36.5523 25.0648 36V27ZM27.077 31H29.5923C30.4245 31 31.1014 30.3271 31.1014 29.5C31.1014 28.6729 30.4245 28 29.5923 28H27.077V31Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx151(
          "path",
          {
            d: "M21.0404 36C21.0404 36.5523 21.491 37 22.0465 37C22.6021 37 23.0526 36.5523 23.0526 36V27C23.0526 26.4477 22.6021 26 22.0465 26C21.491 26 21.0404 26.4477 21.0404 27V36Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx151(
          "path",
          {
            d: "M23.8392 8C23.8392 8.55229 23.3887 9 22.8331 9H14.7843C14.2287 9 13.7782 8.55229 13.7782 8C13.7782 7.4477 14.2287 7 14.7843 7H22.8331C23.3887 7 23.8392 7.4477 23.8392 8Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx151(
          "path",
          {
            d: "M30.8819 13C31.4375 13 31.888 12.5523 31.888 12C31.888 11.4477 31.4375 11 30.8819 11H14.7843C14.2287 11 13.7782 11.4477 13.7782 12C13.7782 12.5523 14.2287 13 14.7843 13H30.8819Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx151(
          "path",
          {
            d: "M14.7843 17C14.2287 17 13.7782 16.5523 13.7782 16C13.7782 15.4477 14.2287 15 14.7843 15H30.8819C31.4375 15 31.888 15.4477 31.888 16C31.888 16.5523 31.4375 17 30.8819 17H14.7843Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx151(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M40.9429 48C43.1619 48 44.9673 46.2056 44.9673 44V9C44.9673 8.73481 44.8612 8.48042 44.6726 8.29292L36.6238 0.29292C36.4351 0.10542 36.1792 0 35.9124 0H12.7721C10.5531 0 8.74775 1.79438 8.74775 4V16H4.72336C2.50432 16 0.698975 17.7944 0.698975 20V35.5667C0.698975 38.5626 3.15124 41 6.16541 41H8.74775V44C8.74775 46.2056 10.5531 48 12.7721 48H40.9429ZM2.71117 23.4632V35.5667C2.71117 37.4598 4.26066 39 6.16541 39H36.4154C37.2477 39 37.9246 38.3271 37.9246 37.5V25.5C37.9246 24.6729 37.2477 24 36.4154 24H4.72336C3.99087 24 3.30346 23.8045 2.71117 23.4632ZM8.74775 18H4.72336C3.61384 18 2.71117 18.8972 2.71117 20C2.71117 21.1028 3.61384 22 4.72336 22H8.74775V18ZM10.7599 22H36.4154C38.3571 22 39.9368 23.5701 39.9368 25.5V37.5C39.9368 39.4299 38.3571 41 36.4154 41H10.7599V44C10.7599 45.1028 11.6626 46 12.7721 46H40.9429C42.0524 46 42.9551 45.1028 42.9551 44V10H35.9124C35.3568 10 34.9063 9.55229 34.9063 9V2H12.7721C11.6626 2 10.7599 2.89722 10.7599 4V22ZM36.9185 8V3.41431L41.5322 8H36.9185Z",
            fill: color
          }
        )
      ] })
    }
  );
};
var ZipFileIcon_default = ZipFileIcon;

// src/icons/AlertTriangleFillIcon/AlertTriangleFillIcon.tsx
import { jsx as jsx152, jsxs as jsxs89 } from "react/jsx-runtime";
var AlertTriangleFillIcon = ({
  width = 12,
  height = 12,
  margin = 0,
  padding = 0,
  cursor = "default",
  color = "#EC9F24",
  handleClick,
  opacity = "1",
  direction = "up",
  dataTestId = "alert-triangle-fill-icon"
}) => {
  const directionRotation = {
    up: "0deg",
    right: "90deg",
    down: "180deg",
    left: "270deg"
  };
  return /* @__PURE__ */ jsx152(
    "svg",
    {
      "data-direction": direction,
      opacity,
      xmlns: "http://www.w3.org/2000/svg",
      width,
      height,
      viewBox: "0 0 12 12",
      fill: "none",
      "data-testid": dataTestId,
      onClick: handleClick,
      style: { margin, padding, cursor, rotate: directionRotation[direction] },
      children: /* @__PURE__ */ jsxs89("g", { "data-testid": "g-path", opacity, children: [
        /* @__PURE__ */ jsx152(
          "path",
          {
            "data-testid": "path-with-color",
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M6.50015 6.5C6.50015 6.776 6.27615 7 6.00015 7C5.72415 7 5.50015 6.776 5.50015 6.5V4.5C5.50015 4.224 5.72415 4 6.00015 4C6.27615 4 6.50015 4.224 6.50015 4.5V6.5ZM6.00015 8.5C5.72415 8.5 5.50015 8.276 5.50015 8C5.50015 7.724 5.72415 7.5 6.00015 7.5C6.27615 7.5 6.50015 7.724 6.50015 8C6.50015 8.276 6.27615 8.5 6.00015 8.5ZM11.2807 8.1515L7.44465 1.792C7.14515 1.296 6.60515 1 6.00015 1C5.39515 1 4.85515 1.296 4.55565 1.792L0.719651 8.1515C0.435651 8.623 0.427151 9.19 0.697151 9.668C0.986651 10.1815 1.54915 10.5 2.16415 10.5H9.83615C10.4512 10.5 11.0137 10.1815 11.3032 9.668C11.5732 9.19 11.5647 8.623 11.2807 8.1515Z",
            fill: color
          }
        ),
        /* @__PURE__ */ jsx152(
          "mask",
          {
            id: "mask0_9125_105759",
            maskUnits: "userSpaceOnUse",
            x: "0",
            y: "1",
            width: "12",
            height: "10",
            children: /* @__PURE__ */ jsx152(
              "path",
              {
                fillRule: "evenodd",
                clipRule: "evenodd",
                d: "M6.50015 6.5C6.50015 6.776 6.27615 7 6.00015 7C5.72415 7 5.50015 6.776 5.50015 6.5V4.5C5.50015 4.224 5.72415 4 6.00015 4C6.27615 4 6.50015 4.224 6.50015 4.5V6.5ZM6.00015 8.5C5.72415 8.5 5.50015 8.276 5.50015 8C5.50015 7.724 5.72415 7.5 6.00015 7.5C6.27615 7.5 6.50015 7.724 6.50015 8C6.50015 8.276 6.27615 8.5 6.00015 8.5ZM11.2807 8.1515L7.44465 1.792C7.14515 1.296 6.60515 1 6.00015 1C5.39515 1 4.85515 1.296 4.55565 1.792L0.719651 8.1515C0.435651 8.623 0.427151 9.19 0.697151 9.668C0.986651 10.1815 1.54915 10.5 2.16415 10.5H9.83615C10.4512 10.5 11.0137 10.1815 11.3032 9.668C11.5732 9.19 11.5647 8.623 11.2807 8.1515Z",
                fill: "white"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx152("g", { mask: "url(#mask0_9125_105759)" })
      ] })
    }
  );
};
var AlertTriangleFillIcon_default = AlertTriangleFillIcon;

// src/components/Select/Select.tsx
import Select, { components as selectComponents } from "react-select";
import { Flex as Flex2, useTheme as useTheme15, Text as Text4 } from "@chakra-ui/react";
import { jsx as jsx153, jsxs as jsxs90 } from "react/jsx-runtime";
var CJSelect = ({
  styles = {},
  components = {},
  dataTestId = "cj-select--component",
  height = "33px",
  maxHeight = "33px",
  minHeight = "33px",
  ...props
}) => {
  const chakraTheme2 = useTheme15();
  const defaultStyles = {
    control: (provided, state) => ({
      ...provided,
      marginTop: "0",
      height,
      maxHeight,
      minHeight,
      borderRadius: "6px",
      borderBottomLeftRadius: state.menuIsOpen && "0",
      borderBottomRightRadius: state.menuIsOpen && "0",
      outline: state.isFocused && "none",
      boxShadow: state.isFocused && "none",
      backgroundColor: chakraTheme2.colors?.white,
      border: `1px solid ${state.isFocused ? chakraTheme2.colors?.blueTwo : chakraTheme2.colors?.lightGrey}`,
      "&:focus": {
        outline: "none"
      },
      "&:hover": {
        outline: "none",
        borderColor: chakraTheme2.colors?.blueTwo
      }
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: "33px",
      padding: "0 10px"
    }),
    input: (baseStyles) => ({
      ...baseStyles,
      color: "transparent",
      width: 0
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isFocused ? chakraTheme2.colors?.explorerTableHeaderColor : chakraTheme2.colors?.white,
      "&:active": {
        // style when the option in the menu is clicked
        backgroundColor: chakraTheme2.colors?.explorerTableHeaderColor
      }
    }),
    singleValue: (provided) => ({
      ...provided,
      display: "none"
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: "none"
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      display: "none"
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: "50",
      marginTop: "0"
    }),
    menuList: (provided) => ({
      ...provided,
      backgroundColor: chakraTheme2.colors?.white,
      borderRadius: "6px",
      border: `1px solid ${chakraTheme2.colors?.blueTwo}`,
      borderTopLeftRadius: "0",
      borderTopRightRadius: "0",
      borderTopWidth: "0",
      padding: "0"
    })
  };
  return /* @__PURE__ */ jsx153(
    Select,
    {
      components: {
        ...{
          SelectContainer: (props2) => {
            return /* @__PURE__ */ jsx153(selectComponents.SelectContainer, { ...props2, children: /* @__PURE__ */ jsx153("div", { "data-testid": dataTestId, children: props2.children }) });
          },
          Option: (optionProps) => {
            return /* @__PURE__ */ jsx153(selectComponents.Option, { ...optionProps, children: /* @__PURE__ */ jsx153(
              Flex2,
              {
                "data-testid": "select--option-container",
                "min-height": minHeight,
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                children: /* @__PURE__ */ jsx153(
                  Text4,
                  {
                    textStyle: "inter-regular-14-17",
                    color: chakraTheme2.colors?.arcadiaMainGrey,
                    children: optionProps?.data?.label || ""
                  }
                )
              }
            ) });
          },
          ValueContainer: ({ children, ...valueContainerProps }) => {
            return /* @__PURE__ */ jsx153(selectComponents.ValueContainer, { ...valueContainerProps, children: /* @__PURE__ */ jsxs90(
              Flex2,
              {
                "data-testid": "select--value-container",
                height: "33px",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                w: "100%",
                children: [
                  /* @__PURE__ */ jsxs90(Flex2, { alignItems: "center", children: [
                    children,
                    /* @__PURE__ */ jsx153(
                      Text4,
                      {
                        noOfLines: 1,
                        wordBreak: "break-all",
                        color: valueContainerProps?.isDisabled ? "rgba(88, 88, 90, 0.5)" : chakraTheme2.colors?.arcadiaMainGrey,
                        textStyle: "inter-regular-14-17",
                        children: valueContainerProps?.selectProps?.value?.label
                      }
                    )
                  ] }),
                  /* @__PURE__ */ jsx153(Flex2, { alignItems: "center", children: /* @__PURE__ */ jsx153(
                    AngleIcon_default,
                    {
                      width: 11,
                      height: 7,
                      color: valueContainerProps?.isDisabled ? "rgba(88, 88, 90, 0.5)" : chakraTheme2.colors?.arcadiaMainGrey,
                      direction: valueContainerProps?.selectProps?.menuIsOpen ? "up" : "down",
                      margin: "2px 0 0 0"
                    }
                  ) })
                ]
              }
            ) });
          }
        },
        ...components
      },
      styles: {
        ...defaultStyles,
        ...styles
      },
      ...props
    }
  );
};
var Select_default = CJSelect;

// src/components/CSVModal/index.tsx
import { intersection } from "lodash";
import { useEffect, useRef as useRef2, useState as useState3 } from "react";

// src/themes/chakraTheme.ts
import { extendTheme } from "@chakra-ui/react";

// src/themes/baseTheme.ts
var baseTheme = {
  fonts: {
    regular: "OpenSans Regular",
    bold: "OpenSans Bold",
    semiBold: "OpenSans Semi Bold",
    extraBold: "OpenSans Extra Bold",
    italic: "OpenSans Italic",
    semiBoldItalic: "OpenSans Semi Bold Italic",
    light: "OpenSans Light",
    extraBoldItalic: "OpenSans Extra Bold Italic",
    interRegular: "Inter Regular",
    interBold: "Inter Bold",
    interSemiBold: "Inter Semi Bold",
    interMedium: "Inter Medium",
    interExtraBold: "Inter Extra Bold",
    interItalic: "Inter Italic",
    interSemiBoldItalic: "Inter Semi Bold Italic",
    interLight: "Inter Light",
    interExtraBoldItalic: "Inter Extra Bold Italic",
    helveticaNeueRegular: "Helvetica Neue Regular",
    helveticaNeueBold: "Helvetica Neue Bold"
  },
  colors: {
    gold: "#EC9F24",
    lightGold: "#FDF5E9",
    blueOne: "#3992C6",
    blueTwo: "#009FD3",
    blueThree: "#1A91CF",
    blue04: "#7AC7E2",
    blue08: "#07A1D4",
    blue14: "#014667",
    blue12: "#01658D",
    darkBlue: "#015F94",
    lightGrey: "#CFCFCD",
    lightGrey2: "#CDC3B4",
    mediumGrey: "#58585A",
    mediumGrey2: "#747474",
    mediumGrey3: "#6386A0",
    darkGrey: "#3C3C3E",
    darkGrey2: "#58585A",
    darkGrey3: "#44546A",
    greyDarkDisable: "#ACACAD",
    red: "#AE2824",
    orange: "#F57E20",
    darkerBlue: "#005980",
    darkestBlue: "#142A56",
    lighterGrey: "#EAEBEC",
    bgGrey: "#E6E6EE",
    bgGrey2: "#F6F8FA",
    green: "#39C671",
    graphic02: "#ECE7F2",
    graphic03: "#D0D1E6",
    graphic04: "#A6BDDB",
    graphic05: "#74A9CF",
    graphic06: "#3690C0",
    graphic07: "#0570B0",
    graphic08: "#045A8D",
    graphicGreen: "#B0DB72",
    graphicRed: "#BD2038",
    graphicYellow: "#F1D665",
    lightBlue: "#CCDEE6",
    lightBlue2: "#E5F5FB",
    lightBlue3: "#8697A7",
    lightBlue4: "#CDEDFF",
    explorerTableHeaderColor: "#EBF7FC",
    lightBlueBackground: "rgba(0, 89, 128, 0.2)",
    borderGrey: "rgba(88, 88, 90, 0.75)",
    greyBackground: "rgb(247, 247, 252)",
    white: "#FFFFFF",
    black: "#000000",
    smoky: "#59595b",
    stormGrey: "#797d89",
    grey7: "#8C8C8C",
    grey8: "#595959",
    grey9: "rgba(230, 230, 238, 0.2)",
    greenDark: "#1F6F78",
    greenMedium: "#118A7E",
    greenExplorer: "#3BAEA0",
    lightGreenExplorer: "#93E4C1",
    lightGreyBackground: "#E5EDF2",
    tableHeaderBlue: "#DDE8EE",
    explorerBackground: "#f6f8fa",
    explorerHomeHeader: "#EEF2F4",
    ghostWhite: "#F3F3F7",
    panache: "#EFF8F4",
    arcadiaMainGrey: "#191919",
    arcadiaBgGrey: "#F9FAFF",
    arcadiaPpi1: "#EDC5CE",
    arcadiaPpi2: "#F5DACB",
    arcadiaPpi3: "#F6EEC9",
    arcadiaPpi4: "#E0F0CE",
    arcadiaPpi5: "#B6E8CE",
    newBackgroundGrey: "#F9FAFF",
    arcadiaPurple: "#8800FF",
    lightPurple: "#F4E6FF"
  },
  schemes: {
    blue01: [
      "#D6E6ED",
      "#B6DBE9",
      "#98D1E6",
      "#7AC7E2",
      "#5DBEDF",
      "#41B5DB",
      "#26ACD8",
      "#07A1D4",
      "#0192C3",
      "#0181AE",
      "#01739E",
      "#01658D",
      "#01567A",
      "#014667",
      "#013856"
    ],
    greenBlue01: [
      "#173D36",
      "#1C5952",
      "#118A7E",
      "#3BAEA0",
      "#49BA9F",
      "#66C9AB",
      "#93E4C1",
      "#ABF3D4",
      "#C0FFE4",
      "#DFFFF1",
      "#1F6F78",
      "#288A95",
      "#2E9FAC",
      "#33B8C6",
      "#47C2CF",
      "#63CBD7",
      "#7AD2DC",
      "#99DDE5",
      "#AFE4EA",
      "#C6ECF0"
    ]
  },
  shadow: {
    smallShadow: "0px 33px 80px rgba(0, 0, 0, 0.12), 0px 20.8247px 21.281px rgba(0, 0, 0, 0.0712147), 0px 4.70689px 7.10974px rgba(0, 0, 0, 0.0479366)",
    mediumShadow: "0px 9px 19px rgba(0, 0, 0, 0.05), 0px 6.03094px 10.3972px rgba(0, 0, 0, 0.025), 0px 2.15775px 3.40155px rgba(0, 0, 0, 0.0167612)",
    mediumShadowLight: "0px 9px 19px rgba(0, 0, 0, 0.03), 0px 6.03094px 10.3972px rgba(0, 0, 0, 0.015), 0px 2.15775px 3.40155px rgba(0, 0, 0, 0.005)",
    bigShadow: "0px 100px 74px rgba(0, 0, 0, 0.05), 0px 41.7776px 30.9154px rgba(0, 0, 0, 0.04), 0px 22.3363px 16.5289px rgba(0, 0, 0, 0.04), 0px 12.5216px 9.26595px rgba(0, 0, 0, 0.03), 0px 6.6501px 4.92108px rgba(0, 0, 0, 0.03), 0px 2.76726px 2.04777px rgba(0, 0, 0, 0.02)",
    xs: "0px 1px 2px rgba(0, 0, 0, 0.05)",
    sm: "0px 1px 2px rgba(0, 0, 0, 0.06), 0px 1px 3px rgba(0, 0, 0, 0.1)",
    md: "0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.1)",
    lg: "0px 4px 6px -2px rgba(0, 0, 0, 0.05), 0px 10px 15px -3px rgba(0, 0, 0, 0.1)",
    xl: "0px 10px 10px -5px rgba(0, 0, 0, 0.04), 0px 20px 25px -5px rgba(0, 0, 0, 0.1)",
    "2xl": "0px 25px 50px -12px rgba(0, 0, 0, 0.25)",
    darkLg: "0px 100px 74px rgba(0, 0, 0, 0.05), 0px 40px 30px rgba(0, 0, 0, 0.04), 0px 22px 16px rgba(0, 0, 0, 0.04), 0px 13px 10px rgba(0, 0, 0, 0.03), 0px 7px 5px rgba(0, 0, 0, 0.03), 0px 3px 2px rgba(0, 0, 0, 0.02)",
    outline: " 0px 0px 0px 3px rgba(63, 153, 225, 0.6)",
    inner: "inset 0px 2px 4px rgba(0, 0, 0, 0.06)",
    kbdInner: "inset 0px -2px 0px #E2E8F0"
  },
  transition: "all 0.1s"
};
var baseTheme_default = baseTheme;

// src/themes/chakraTheme.ts
var chakraColorSchemes = {};
var colors = baseTheme_default.colors;
if (colors) {
  Object.keys(colors).map((color, i) => {
    const scheme = {
      500: Object.values(colors)[i]
    };
    chakraColorSchemes[`${color}Scheme`] = scheme;
  });
  const rainbowScheme = {
    50: "black",
    100: "cyan",
    200: "yellow",
    300: "magenta",
    400: "red",
    500: "blue",
    600: "grey",
    700: "white",
    800: "green",
    900: "orange"
  };
  chakraColorSchemes.rainbowScheme = rainbowScheme;
}
var baseStyle = {
  tablist: {
    w: "1146px",
    display: "flex",
    justifyContent: "center",
    alignContent: "center",
    flexDirection: "row",
    listStyleType: "none",
    borderBottom: `1px solid ${baseTheme_default?.colors?.lightGrey}`
  },
  tab: {
    position: "relative",
    minW: "170px",
    maxW: "170px",
    fontFamily: baseTheme_default.fonts?.regular,
    fontSize: "20px",
    textAlign: "center",
    color: baseTheme_default.colors?.mediumGrey,
    cursor: "pointer",
    padding: "11px",
    _selected: {
      fontFamily: baseTheme_default.fonts?.bold,
      cursor: "auto",
      _after: { opacity: 1 }
    },
    _after: {
      width: "170px",
      height: "5px",
      position: "absolute",
      left: "0px",
      bottom: "-2.5px",
      content: '" "',
      borderRadius: "2.5px",
      opacity: 0
    }
  }
};
var defaultSecondary = {
  tablist: {
    height: "52px",
    justifyContent: "space-around",
    borderBottom: "none",
    padding: 0,
    margin: 0
  },
  tab: {
    fontSize: "16px",
    maxW: "none",
    _after: { display: "none" }
  }
};
var defaultSmallPrimary = {
  tablist: {
    maxW: "1146px",
    height: "36px",
    display: "flex",
    justifyContent: "flex-start",
    alignContent: "left",
    flexDirection: "row",
    listStyleType: "none",
    borderBottom: `1px solid ${baseTheme_default?.colors?.lightGrey}`
  },
  tab: {
    fontSize: "16px",
    lineHeight: "16px",
    textAlign: "flex-start",
    justifyContent: "flex-start",
    minW: "75px",
    maxW: "117px",
    minHeight: "26px",
    padding: "5px 10px 5px 10px",
    marginLeft: "4px",
    _selected: {
      fontFamily: baseTheme_default.fonts?.bold,
      cursor: "auto",
      _after: { opacity: 1 }
    },
    _after: {
      // maxWidth: '117px',
      // minWidth: '75px',
      width: "100%",
      height: "5px",
      position: "absolute",
      left: "0px",
      bottom: "-2.5px",
      content: '" "',
      borderRadius: "5px",
      opacity: 0
    }
  }
};
var defaultSmallSecondary = {
  tablist: {
    height: "42px",
    maxW: "1146px",
    justifyContent: "flex-start",
    alignContent: "left",
    borderBottom: "none",
    padding: 0,
    margin: 0
  },
  tab: {
    fontSize: "14px",
    lineHeigh: "14px",
    _after: { display: "none" }
  }
};
var baseStyleTT = {
  zIndex: 1400
};
var chakraTheme = extendTheme({
  ...baseTheme_default,
  shadows: {
    outline: "none"
  },
  colors: {
    ...chakraColorSchemes,
    // include both new schemes and previus colors
    ...baseTheme_default.colors
  },
  textStyles: {
    "regular-8-8": {
      fontFamily: "OpenSans Regular",
      fontSize: "8px",
      lineHeight: "8px"
    },
    "bold-8-8": {
      fontFamily: "OpenSans Bold",
      fontSize: "8px",
      lineHeight: "8px"
    },
    "regular-8-10": {
      fontFamily: "OpenSans Regular",
      fontSize: "8px",
      lineHeight: "10px"
    },
    "regular-10-10": {
      fontFamily: "OpenSans Regular",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "regularItalic-10-10": {
      fontFamily: "OpenSans Italic",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "semiBold-8-8": {
      fontFamily: "OpenSans Bold",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "semiBold-10-10": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "regular-10-12": {
      fontFamily: "OpenSans Regular",
      fontSize: "10px",
      lineHeight: "12px"
    },
    "semiBold-10-12": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "10px",
      lineHeight: "12px"
    },
    "bold-10-12": {
      fontFamily: "OpenSans Bold",
      fontSize: "10px",
      lineHeight: "12px"
    },
    "regular-10-14": {
      fontFamily: "OpenSans Regular",
      fontSize: "10px",
      lineHeight: "14px"
    },
    "semiBold-10-14": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "10px",
      lineHeight: "14px"
    },
    "semiBold-10-16": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "10px",
      lineHeight: "16px"
    },
    "regular-11-14": {
      fontFamily: "OpenSans Regular",
      fontSize: "11px",
      lineHeight: "14px"
    },
    "regular-11-15": {
      fontFamily: "OpenSans Regular",
      fontSize: "11px",
      lineHeight: "15px"
    },
    "regular-11-16": {
      fontFamily: "OpenSans Regular",
      fontSize: "11px",
      lineHeight: "16px"
    },
    "regular-12-12": {
      fontFamily: "OpenSans Regular",
      fontSize: "12px",
      lineHeight: "12px"
    },
    "semiBold-12-12": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "12px",
      lineHeight: "12px"
    },
    "bold-12-12": {
      fontFamily: "OpenSans Bold",
      fontSize: "12px",
      lineHeight: "12px"
    },
    "regular-12-14": {
      fontFamily: "OpenSans Regular",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "semiBold-12-14": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "bold-12-14": {
      fontFamily: "OpenSans Bold",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "regular-12-16": {
      fontFamily: "OpenSans Regular",
      fontSize: "12px",
      lineHeight: "16px"
    },
    "semiBold-12-16": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "12px",
      lineHeight: "16px"
    },
    "semiBoldItalic-12-16": {
      fontFamily: "OpenSans Semi Bold Italic",
      fontSize: "12px",
      lineHeight: "16px"
    },
    "bold-12-16": {
      fontFamily: "OpenSans Bold",
      fontSize: "12px",
      lineHeight: "16px"
    },
    "regular-12-19": {
      fontFamily: "OpenSans Regular",
      fontSize: "12px",
      lineHeight: "19px"
    },
    "semiBold-12-19": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "12px",
      lineHeight: "19px"
    },
    "regular-12-20": {
      fontFamily: "OpenSans Regular",
      fontSize: "12px",
      lineHeight: "20px"
    },
    "regular-14-14": {
      fontFamily: "OpenSans Regular",
      fontSize: "14px",
      lineHeight: "14px"
    },
    "regular-14-16": {
      fontFamily: "OpenSans Regular",
      fontSize: "14px",
      lineHeight: "16px"
    },
    "semiBold-14-16": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "14px",
      lineHeight: "16px"
    },
    "bold-14-16": {
      fontFamily: "OpenSans Bold",
      fontSize: "14px",
      lineHeight: "16px"
    },
    "semiBold-14-18": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "14px",
      lineHeight: "18px"
    },
    "semiBold-14-19": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "14px",
      lineHeight: "19px"
    },
    "regular-14-19": {
      fontFamily: "OpenSans Regular",
      fontSize: "14px",
      lineHeight: "19px"
    },
    "semiBoldItalic-14-19": {
      fontFamily: "OpenSans Semi Bold Italic",
      fontSize: "14px",
      lineHeight: "19px"
    },
    "regularItalic-14-19": {
      fontFamily: "OpenSans Italic",
      fontSize: "14px",
      lineHeight: "19px"
    },
    "bold-14-19": {
      fontFamily: "OpenSans Bold",
      fontSize: "14px",
      lineHeight: "19px"
    },
    "light-14-19": {
      fontFamily: "OpenSans Light",
      fontSize: "14px",
      lineHeight: "19px"
    },
    "regular-14-20": {
      fontFamily: "OpenSans Regular",
      fontSize: "14px",
      lineHeight: "20px"
    },
    "semiBoldItalic-14-20": {
      fontFamily: "OpenSans Semi Bold Italic",
      fontSize: "14px",
      lineHeight: "20px"
    },
    "regularItalic-14-20": {
      fontFamily: "OpenSans Italic",
      fontSize: "14px",
      lineHeight: "20px"
    },
    "regular-14-30": {
      fontFamily: "OpenSans Regular",
      fontSize: "14px",
      lineHeight: "30px"
    },
    "semiBold-15-20": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "15px",
      lineHeight: "20px"
    },
    "regular-16-16": {
      fontFamily: "OpenSans Regular",
      fontSize: "16px",
      lineHeight: "16px"
    },
    "semiBold-16-16": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "16px",
      lineHeight: "16px"
    },
    "bold-16-16": {
      fontFamily: "OpenSans Bold",
      fontSize: "16px",
      lineHeight: "16px"
    },
    "regular-16-20": {
      fontFamily: "OpenSans Regular",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "regular-16-22": {
      fontFamily: "OpenSans Regular",
      fontSize: "16px",
      lineHeight: "22px"
    },
    "semiBold-16-22": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "16px",
      lineHeight: "22px"
    },
    "bold-16-22": {
      fontFamily: "OpenSans Bold",
      fontSize: "16px",
      lineHeight: "22px"
    },
    "semiBold-16-24": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "16px",
      lineHeight: "24px"
    },
    "regular-18-25": {
      fontFamily: "OpenSans Regular",
      fontSize: "18px",
      lineHeight: "25px"
    },
    "semiBold-18-18": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "18px",
      lineHeight: "18px"
    },
    "semiBold-18-25": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "18px",
      lineHeight: "25px"
    },
    "bold-18-25": {
      fontFamily: "OpenSans Bold",
      fontSize: "18px",
      lineHeight: "25px"
    },
    "regular-20-24": {
      fontFamily: "OpenSans Regular",
      fontSize: "20px",
      lineHeight: "24px"
    },
    "semiBold-20-26": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "20px",
      lineHeight: "26px"
    },
    "semiBold-20-27": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "20px",
      lineHeight: "27px"
    },
    "bold-20-27": {
      fontFamily: "OpenSans Bold",
      fontSize: "20px",
      lineHeight: "27px"
    },
    "semiBold-22-24": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "22px",
      lineHeight: "24px"
    },
    "bold-24-24": {
      fontFamily: "OpenSans Bold",
      fontSize: "24px",
      lineHeight: "24px"
    },
    "regular-24-30": {
      fontFamily: "OpenSans Regular",
      fontSize: "24px",
      lineHeight: "30px"
    },
    "semiBold-24-30": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "24px",
      lineHeight: "30px"
    },
    "semiBold-26-26": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "26px",
      lineHeight: "26px"
    },
    "bold-26-50": {
      fontFamily: "OpenSans Bold",
      fontSize: "26px",
      lineHeight: "50px"
    },
    "bold-32-32": {
      fontFamily: "OpenSans Bold",
      fontSize: "32px",
      lineHeight: "32px"
    },
    "regular-30-30": {
      fontFamily: "OpenSans Regular",
      fontSize: "30px",
      lineHeight: "30px"
    },
    "semiBold-30-36": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "30px",
      lineHeight: "36px"
    },
    "regular-35-34": {
      fontFamily: "OpenSans Regular",
      fontSize: "35px",
      lineHeight: "34px"
    },
    "regular-36-40": {
      fontFamily: "OpenSans Regular",
      fontSize: "36px",
      lineHeight: "40px"
    },
    "semiBold-36-40": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "36px",
      lineHeight: "40px"
    },
    "bold-36-40": {
      fontFamily: "OpenSans Bold",
      fontSize: "36px",
      lineHeight: "40px"
    },
    "semiBold-40-40": {
      fontFamily: "OpenSans Semi Bold",
      fontSize: "40px",
      lineHeight: "40px"
    },
    "bold-40-40": {
      fontFamily: "OpenSans Bold",
      fontSize: "40px",
      lineHeight: "40px"
    },
    "regular-180-245": {
      fontFamily: "OpenSans Regular",
      fontSize: "180px",
      lineHeight: "245px"
    },
    "regular-250-340": {
      fontFamily: "OpenSans Regular",
      fontSize: "250px",
      lineHeight: "340px"
    },
    "inter-regular-10-10": {
      fontFamily: "Inter Regular",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-regular-11-14": {
      fontFamily: "Inter Regular",
      fontSize: "11px",
      lineHeight: "14px"
    },
    "inter-regular-11-16": {
      fontFamily: "Inter Regular",
      fontSize: "11px",
      lineHeight: "16px"
    },
    "inter-regular-12-14": {
      fontFamily: "Inter Regular",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-regular-12-16": {
      fontFamily: "Inter Regular",
      fontSize: "12px",
      lineHeight: "16px"
    },
    "inter-regular-14-17": {
      fontFamily: "Inter Regular",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-regular-16-20": {
      fontFamily: "Inter Regular",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-regular-24-30": {
      fontFamily: "Inter Regular",
      fontSize: "24px",
      lineHeight: "30px"
    },
    "inter-medium-250-340": {
      fontFamily: "Inter Medium",
      fontSize: "250px",
      lineHeight: "340px"
    },
    "inter-medium-10-10": {
      fontFamily: "Inter Medium",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-medium-12-14": {
      fontFamily: "Inter Medium",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-medium-14-17": {
      fontFamily: "Inter Medium",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-medium-16-20": {
      fontFamily: "Inter Medium",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-semiBold-10-10": {
      fontFamily: "Inter Semi Bold",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-semiBold-10-12": {
      fontFamily: "Inter Semi Bold",
      fontSize: "10px",
      lineHeight: "12px"
    },
    "inter-semiBold-10-16": {
      fontFamily: "Inter Semi Bold",
      fontSize: "10px",
      lineHeight: "16px"
    },
    "inter-semiBold-12-14": {
      fontFamily: "Inter Semi Bold",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-semiBold-14-17": {
      fontFamily: "Inter Semi Bold",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-semiBold-14-18": {
      fontFamily: "Inter Semi Bold",
      fontSize: "14px",
      lineHeight: "18px"
    },
    "inter-semiBold-16-16": {
      fontFamily: "Inter Semi Bold",
      fontSize: "16px",
      lineHeight: "16px"
    },
    "inter-semiBold-16-20": {
      fontFamily: "Inter Semi Bold",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-semiBold-24-30": {
      fontFamily: "Inter Semi Bold",
      fontSize: "24px",
      lineHeight: "30px"
    },
    "inter-semiBold-26-26": {
      fontFamily: "Inter Semi Bold",
      fontSize: "26px",
      lineHeight: "26px"
    },
    "inter-semiBold-40-40": {
      fontFamily: "Inter Semi Bold",
      fontSize: "40px",
      lineHeight: "40px"
    },
    "inter-bold-10-10": {
      fontFamily: "Inter Bold",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-bold-12-14": {
      fontFamily: "Inter Bold",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-bold-14-17": {
      fontFamily: "Inter Bold",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-bold-16-20": {
      fontFamily: "Inter Bold",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-regularItalic-10-10": {
      fontFamily: "Inter Italic",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-regularItalic-12-14": {
      fontFamily: "Inter Italic",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-regularItalic-14-17": {
      fontFamily: "Inter Italic",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-regularItalic-16-20": {
      fontFamily: "Inter Italic",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-boldItalic-10-10": {
      fontFamily: "Inter Bold Italic",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-boldItalic-12-14": {
      fontFamily: "Inter Bold Italic",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-boldItalic-14-17": {
      fontFamily: "Inter Bold Italic",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-boldItalic-16-20": {
      fontFamily: "Inter Bold Italic",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-mediumItalic-10-10": {
      fontFamily: "Inter Medium Italic",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-mediumItalic-12-14": {
      fontFamily: "Inter Medium Italic",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-mediumItalic-14-17": {
      fontFamily: "Inter Medium Italic",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-mediumItalic-16-20": {
      fontFamily: "Inter Medium Italic",
      fontSize: "16px",
      lineHeight: "20px"
    },
    "inter-semiBoldItalic-10-10": {
      fontFamily: "Inter Semi Bold Italic",
      fontSize: "10px",
      lineHeight: "10px"
    },
    "inter-semiBoldItalic-12-14": {
      fontFamily: "Inter Semi Bold Italic",
      fontSize: "12px",
      lineHeight: "14px"
    },
    "inter-semiBoldItalic-14-17": {
      fontFamily: "Inter Semi Bold Italic",
      fontSize: "14px",
      lineHeight: "17px"
    },
    "inter-semiBoldItalic-16-20": {
      fontFamily: "Inter Semi Bold Italic",
      fontSize: "16px",
      lineHeight: "20px"
    }
  },
  components: {
    Checkbox: {
      baseStyle: {
        label: {
          marginStart: "5px"
        }
      }
    },
    Tabs: {
      parts: ["tablist", "tab"],
      baseStyle,
      variants: {
        arcadiaSmallPrimary: {
          tab: {
            ...defaultSmallPrimary.tab,
            color: baseTheme_default.colors?.arcadiaMainGrey,
            fontFamily: baseTheme_default.fonts?.interRegular,
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            },
            _selected: {
              color: baseTheme_default.colors?.blueOne,
              _after: { width: "100%" },
              fontFamily: baseTheme_default.fonts?.interSemiBold
            },
            _after: {
              width: "100%",
              height: "2px",
              position: "absolute",
              left: "0px",
              bottom: "-2.5px",
              content: '" "',
              borderRadius: "5px",
              opacity: 0,
              backgroundColor: baseTheme_default.colors?.blueOne
            }
          },
          tablist: {
            ...defaultSmallPrimary.tablist,
            color: baseTheme_default.colors?.arcadiaMainGrey
          }
        },
        arcadiaSmallSecondary: {
          tab: {
            ...defaultSmallSecondary.tab,
            fontFamily: baseTheme_default.fonts?.interRegular,
            color: baseTheme_default.colors?.arcadiaMainGrey,
            maxWidth: "190px",
            minWidth: "170px",
            _selected: {
              color: baseTheme_default.colors?.blueOne,
              fontFamily: baseTheme_default.fonts?.interSemiBold
            },
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            }
          },
          tablist: {
            ...defaultSmallSecondary.tablist,
            background: "rgba(0, 159, 211, 0.1)"
          }
        },
        primaryGreen: {
          tab: {
            _selected: {
              color: baseTheme_default.colors?.greenMedium
            },
            _after: {
              backgroundColor: baseTheme_default.colors?.greenMedium
            }
          }
        },
        secondaryGreen: {
          tab: {
            ...defaultSecondary.tab,
            _selected: {
              color: baseTheme_default.colors?.greenMedium
            }
          },
          tablist: {
            ...defaultSecondary.tablist,
            background: "rgba(147,228,193,0.2)"
          }
        },
        primaryBlue: {
          tab: {
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            },
            _selected: {
              color: baseTheme_default.colors?.blueOne
            },
            _after: {
              backgroundColor: baseTheme_default.colors?.blueOne
            }
          }
        },
        secondaryBlue: {
          tab: {
            ...defaultSecondary.tab,
            _selected: {
              color: baseTheme_default.colors?.blueOne
            },
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            }
          },
          tablist: {
            ...defaultSecondary.tablist,
            background: "rgba(0, 159, 211, 0.1)"
          }
        },
        smallPrimaryBlue: {
          tab: {
            ...defaultSmallPrimary.tab,
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            },
            _selected: {
              color: baseTheme_default.colors?.blueOne,
              _after: { width: "100%" }
            },
            _after: {
              backgroundColor: baseTheme_default.colors?.blueOne
            }
          },
          tablist: {
            ...defaultSmallPrimary.tablist
          }
        },
        smallSecondaryBlue: {
          tab: {
            ...defaultSmallSecondary.tab,
            _selected: {
              color: baseTheme_default.colors?.blueOne
            },
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            }
          },
          tablist: {
            ...defaultSmallSecondary.tablist,
            background: "rgba(0, 159, 211, 0.1)"
          }
        },
        primaryBlueThin: {
          tab: {
            _hover: {
              color: baseTheme_default.colors?.blueTwo
            },
            _selected: {
              color: baseTheme_default.colors?.blueOne
            },
            _after: {
              backgroundColor: baseTheme_default.colors?.blueOne,
              height: "2px"
            }
          }
        }
      },
      sizes: {},
      defaultProps: {
        size: ""
      }
    },
    Tooltip: {
      baseStyle: baseStyleTT
    }
  }
});

// src/themes/styledComponentsTheme.ts
var styledComponentsTheme = {
  ...baseTheme_default,
  dimensions: {
    container: {
      maxWidth: "1200px"
    }
  }
};

// src/components/CSVModal/CsvBuilder.ts
var createCSVFile = () => {
  const defaultValues = [
    "1937025128",
    "8316330522",
    "2037025128",
    "8457145437",
    "4031930773",
    "4031930773"
  ];
  const csvContent = `IDENTIFIER
${defaultValues.join("\n")}`;
  const blob = new Blob([csvContent], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "csv_sample.csv";
  a.click();
  URL.revokeObjectURL(a.href);
};

// src/components/CSVModal/index.tsx
import { Fragment as Fragment11, jsx as jsx154, jsxs as jsxs91 } from "react/jsx-runtime";
var CsvModal = ({
  isOpen = true,
  setIsOpen,
  handleUploadFile,
  handleUploadText,
  selectValues,
  validNPIS = null,
  isLoading,
  handleApplyCsvValues,
  selectOnChangeHandler,
  filtersAreApplied,
  hasNpiLimit,
  labelForWarning = "NPIs"
}) => {
  const [textAreaValue, setTextAreaValue] = useState3("");
  const [focused, setIsFocused] = useState3(false);
  const [dragOver, setDragOver] = useState3(false);
  const [selectedFile, setSelectedFile] = useState3(null);
  const [selectValue, setSelectValue] = useState3();
  const [isAttachemntState, setIsAttachemntState] = useState3(true);
  const [parsedValue, setParsedValue] = useState3();
  const [validNPISList, setValidNPISList] = useState3(
    validNPIS
  );
  const fileInputRef = useRef2(null);
  const valueExceded = textAreaValue.length > 200;
  const hasContent = textAreaValue !== "" || selectedFile;
  const handleInputChange = (event) => {
    const inputValue = event.target.value;
    const cleanValues = inputValue.replace(/\r\n/g, "\n").split(/,|\n/).map((value) => value.trim()).filter((value) => value !== "" && !/^[a-zA-Z_]+$/.test(value));
    const uniqueValues = [...new Set(cleanValues)];
    setParsedValue(uniqueValues);
    setTextAreaValue(inputValue);
  };
  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    setSelectedFile(event.dataTransfer?.files[0]);
  };
  const handleFileInputChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };
  useEffect(() => {
    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = () => {
        const content = reader.result;
        const rows = content?.split("\n");
        const values = [];
        rows.forEach((row, index) => {
          if (index !== 0) {
            const columns = row.trim().split(",");
            if (columns.length > 0) {
              values.push(columns[0]);
            }
          }
        });
        const uniqueValues = [...new Set(values)].filter(
          (value) => value !== ""
        );
        setParsedValue(uniqueValues);
      };
      reader.readAsText(selectedFile);
    }
  }, [selectedFile]);
  const removeFile = () => {
    setParsedValue(null);
    setSelectedFile(null);
    setValidNPISList(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  const handleCloseModal = () => {
    setIsOpen(false);
    setSelectedFile(null);
    setDragOver(false);
    setTextAreaValue("");
    setIsAttachemntState(true);
    setParsedValue(null);
    setValidNPISList(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };
  useEffect(() => {
    if (validNPIS) {
      setValidNPISList(validNPIS);
      setIsAttachemntState(false);
    } else {
      setSelectedFile(null);
      setDragOver(false);
      setTextAreaValue("");
      setIsAttachemntState(true);
      setParsedValue(null);
      setValidNPISList(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [validNPIS]);
  const handleSelectOnChange = (value) => {
    setSelectValue(value);
    if (selectOnChangeHandler) {
      selectOnChangeHandler(value);
    }
  };
  const removeNPI = (value) => {
    const finalArray = parsedValue?.filter((val) => value !== val) || [];
    setTextAreaValue(finalArray.join(", "));
    setParsedValue(finalArray);
  };
  const mappedItems = (textAreaOrCSVContent = []) => {
    return /* @__PURE__ */ jsxs91(Flex3, { flexDirection: "column", gap: "6px", children: [
      textAreaOrCSVContent.some(
        (value) => !validNPISList?.includes(value.trim())
      ) && /* @__PURE__ */ jsx154(
        Text5,
        {
          textStyle: "inter-regular-10-10",
          color: chakraTheme.colors?.red,
          children: "Some values from your list were not found in our database. Results will be shown for the ones found."
        }
      ),
      /* @__PURE__ */ jsx154(
        Flex3,
        {
          flexDirection: "column",
          gap: "6px",
          height: "200px",
          overflowY: "auto",
          css: {
            "::-webkit-scrollbar": {
              width: "8px",
              backgroundColor: "transparent"
            },
            "::-webkit-scrollbar-track": {
              backgroundColor: "transparent"
            },
            "::-webkit-scrollbar-thumb": {
              borderRadius: "5px",
              backgroundColor: chakraTheme.colors?.lightGrey
            }
          },
          children: textAreaOrCSVContent.map((value) => /* @__PURE__ */ jsxs91(
            Box5,
            {
              display: "grid",
              gridTemplateColumns: "auto 2fr",
              alignItems: "center",
              gap: "5px",
              children: [
                /* @__PURE__ */ jsx154(Text5, { minWidth: "100px", width: "fit-content", children: value }),
                /* @__PURE__ */ jsx154(
                  Button2,
                  {
                    fontSize: "12px",
                    padding: "4px 15px",
                    pointerEvents: validNPISList?.includes(value.trim()) ? "all" : "none",
                    width: "68px",
                    height: "fit-content",
                    onClick: () => removeNPI(value),
                    color: chakraTheme.colors?.white,
                    backgroundColor: validNPISList?.includes(value.trim()) ? chakraTheme.colors?.blueTwo : chakraTheme.colors?.lightGrey,
                    children: validNPISList?.includes(value.trim()) ? "REMOVE" : "N/A"
                  }
                )
              ]
            },
            value
          ))
        }
      )
    ] });
  };
  const applyHandler = () => {
    handleApplyCsvValues(intersection(parsedValue, validNPISList));
  };
  const downloadCsv = () => {
    createCSVFile();
  };
  return /* @__PURE__ */ jsxs91(Modal, { isCentered: true, onClose: handleCloseModal, isOpen, children: [
    /* @__PURE__ */ jsx154(ModalOverlay, {}),
    /* @__PURE__ */ jsxs91(ModalContent, { children: [
      /* @__PURE__ */ jsx154(
        ModalHeader,
        {
          color: chakraTheme.colors?.mediumGrey,
          padding: "10px",
          fontWeight: 500,
          fontSize: "20px",
          fontFamily: chakraTheme.fonts?.interMedium,
          children: "Search Multiple Values"
        }
      ),
      /* @__PURE__ */ jsx154(
        ModalCloseButton,
        {
          color: chakraTheme.colors?.lightGrey,
          fontSize: "23px",
          margin: "5px"
        }
      ),
      /* @__PURE__ */ jsxs91(ModalBody, { children: [
        isAttachemntState ? /* @__PURE__ */ jsxs91(Fragment11, { children: [
          /* @__PURE__ */ jsx154(
            Box5,
            {
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              marginBottom: "10px",
              justifyContent: "flex-start",
              children: selectValues ? /* @__PURE__ */ jsxs91(Fragment11, { children: [
                /* @__PURE__ */ jsx154(
                  Text5,
                  {
                    textStyle: "inter-bold-12-14",
                    color: chakraTheme.colors?.darkBlue,
                    children: "VALUE"
                  }
                ),
                /* @__PURE__ */ jsx154(Box5, { flex: "1", children: /* @__PURE__ */ jsx154(
                  Select_default,
                  {
                    onChange: handleSelectOnChange,
                    styles: { flex: 1 },
                    defaultValue: selectValues[0],
                    value: selectValue,
                    options: selectValues
                  }
                ) })
              ] }) : /* @__PURE__ */ jsx154(Fragment11, {})
            }
          ),
          /* @__PURE__ */ jsxs91(Text5, { textStyle: "inter-regular-12-14", marginBottom: "10px", children: [
            "You can search for a value (or a comma-separated list of values), or you can upload a list",
            " ",
            hasNpiLimit && `of up to 200 ${labelForWarning}`,
            " using a csv file",
            " ",
            /* @__PURE__ */ jsx154(
              Text5,
              {
                display: "inline",
                textDecoration: "underline",
                onClick: downloadCsv,
                cursor: "pointer",
                "data-testid": "download-sample-link",
                children: "(download sample here)."
              }
            )
          ] }),
          filtersAreApplied && /* @__PURE__ */ jsx154(
            Box5,
            {
              padding: "10px",
              backgroundColor: chakraTheme.colors?.bgGrey2,
              margin: "10px auto",
              borderRadius: "3px",
              "data-testid": "filters-are-applied",
              children: /* @__PURE__ */ jsx154(
                Text5,
                {
                  textStyle: "inter-regular-12-14",
                  color: chakraTheme.colors?.red,
                  children: "Please note that you already have a Multiple Search filter active. Uploading a new one will replace the current."
                }
              )
            }
          ),
          /* @__PURE__ */ jsx154(
            Box5,
            {
              width: "100%",
              margin: "0 auto 2rem auto",
              "data-testid": "drop-area",
              onDragEnter: () => setDragOver(true),
              onDrop: handleDrop,
              children: /* @__PURE__ */ jsx154(Box5, { children: /* @__PURE__ */ jsxs91(
                Box5,
                {
                  position: "relative",
                  height: "140px",
                  border: focused || hasContent || dragOver ? `1px solid ${valueExceded ? chakraTheme.colors?.red : chakraTheme.colors?.blueOne}` : "1px dashed #CCC",
                  padding: "6px 6px 24px 6px",
                  borderRadius: "6px",
                  children: [
                    /* @__PURE__ */ jsx154(
                      Textarea,
                      {
                        fontSize: "12px",
                        border: "none",
                        padding: "0",
                        _focus: { border: "none" },
                        width: "100%",
                        height: "100%",
                        value: textAreaValue,
                        onFocus: () => setIsFocused(true),
                        onBlur: () => setIsFocused(false),
                        zIndex: 0,
                        background: "none",
                        autoComplete: "off",
                        autoCapitalize: "off",
                        pointerEvents: selectedFile || isLoading ? "none" : "all",
                        name: "",
                        resize: "none",
                        cols: 30,
                        "data-testid": "text-area",
                        rows: 5,
                        onChange: handleInputChange
                      }
                    ),
                    textAreaValue && /* @__PURE__ */ jsxs91(
                      Box5,
                      {
                        position: "absolute",
                        display: "flex",
                        width: "97%",
                        justifyContent: "space-between",
                        alignItems: "center",
                        bottom: "6px",
                        left: "6px",
                        color: valueExceded ? chakraTheme.colors?.red : chakraTheme.colors?.mediumGrey,
                        children: [
                          /* @__PURE__ */ jsx154(Text5, { textStyle: "regular-12-12", "data-testid": "chars-num", children: 200 - textAreaValue.length }),
                          /* @__PURE__ */ jsx154(
                            Button2,
                            {
                              backgroundColor: chakraTheme.colors?.blueTwo,
                              height: "fit-content",
                              width: "fit-content",
                              padding: "4px 14px",
                              fontFamily: chakraTheme.fonts?.interRegular,
                              fontSize: "12px",
                              disabled: valueExceded,
                              borderRadius: "3px",
                              isLoading,
                              color: chakraTheme.colors?.white,
                              onClick: () => {
                                handleUploadText(parsedValue);
                              },
                              children: "Upload"
                            }
                          )
                        ]
                      }
                    ),
                    /* @__PURE__ */ jsxs91(
                      Box5,
                      {
                        opacity: focused || textAreaValue !== "" ? "0.15" : "1",
                        pointerEvents: "none",
                        position: "absolute",
                        "data-testid": "text-area-container",
                        top: 0,
                        zIndex: 1,
                        bottom: 0,
                        left: 0,
                        right: 0,
                        textAlign: "center",
                        transition: "opacity 0.25s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                        css: dragOver ? {
                          boxShadow: "0px 4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
                        } : {},
                        children: [
                          /* @__PURE__ */ jsx154(Box5, { display: "flex", justifyContent: "center", children: /* @__PURE__ */ jsx154(
                            UploadIcon_default,
                            {
                              color: dragOver || selectedFile ? chakraTheme.colors?.blueTwo : "#CFCFCD"
                            }
                          ) }),
                          selectedFile ? /* @__PURE__ */ jsxs91(Fragment11, { children: [
                            /* @__PURE__ */ jsxs91(
                              Box5,
                              {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "5px",
                                children: [
                                  /* @__PURE__ */ jsx154(
                                    Text5,
                                    {
                                      textStyle: "inter-regular-12-14",
                                      marginBottom: "5px",
                                      children: selectedFile.name
                                    }
                                  ),
                                  /* @__PURE__ */ jsx154(
                                    Button2,
                                    {
                                      position: "static",
                                      color: chakraTheme.colors?.red,
                                      fontSize: "8px",
                                      "data-testid": "remove-file",
                                      padding: "0",
                                      background: "transparent",
                                      pointerEvents: "all",
                                      onClick: removeFile,
                                      height: "unset",
                                      minWidth: "fit-content",
                                      marginBottom: "5px",
                                      cursor: "pointer",
                                      children: /* @__PURE__ */ jsx154(
                                        CloseIcon_default,
                                        {
                                          cursor: "pointer",
                                          width: 8,
                                          height: 8,
                                          color: chakraTheme.colors?.red
                                        }
                                      )
                                    }
                                  )
                                ]
                              }
                            ),
                            /* @__PURE__ */ jsx154(
                              Button2,
                              {
                                fontSize: "12px",
                                fontFamily: chakraTheme.fonts?.interRegular,
                                padding: "4px 15px",
                                pointerEvents: "all",
                                width: "164px",
                                height: "24px",
                                color: chakraTheme.colors?.white,
                                isLoading,
                                disabled: !selectedFile.type.includes("csv"),
                                backgroundColor: chakraTheme.colors?.blueTwo,
                                onClick: () => {
                                  handleUploadFile(parsedValue);
                                },
                                children: "Upload"
                              }
                            )
                          ] }) : /* @__PURE__ */ jsxs91(Box5, { marginTop: "8px", children: [
                            /* @__PURE__ */ jsxs91(
                              Text5,
                              {
                                textStyle: "inter-bold-12-14",
                                color: dragOver ? chakraTheme.colors?.blueTwo : chakraTheme.colors?.darkGrey,
                                children: [
                                  "Drag & drop a file to import, or",
                                  " ",
                                  /* @__PURE__ */ jsx154(
                                    "label",
                                    {
                                      htmlFor: "upload",
                                      style: {
                                        cursor: "pointer",
                                        textDecoration: "underline",
                                        position: "relative",
                                        pointerEvents: hasContent || focused ? "none" : "all"
                                      },
                                      children: "browse"
                                    }
                                  )
                                ]
                              }
                            ),
                            /* @__PURE__ */ jsx154(
                              Text5,
                              {
                                mt: 1,
                                color: chakraTheme.colors?.mediumGrey2,
                                textStyle: "inter-regular-12-14",
                                children: "Alternatively, you can paste into this field"
                              }
                            )
                          ] }),
                          /* @__PURE__ */ jsx154(Box5, { children: /* @__PURE__ */ jsx154(
                            "input",
                            {
                              ref: fileInputRef,
                              "data-testid": "file-input",
                              id: "upload",
                              type: "file",
                              accept: ".csv",
                              hidden: true,
                              onChange: handleFileInputChange
                            }
                          ) })
                        ]
                      }
                    )
                  ]
                }
              ) })
            }
          )
        ] }) : /* @__PURE__ */ jsxs91(Fragment11, { children: [
          /* @__PURE__ */ jsx154(Text5, { textStyle: "inter-regular-14-17", children: "Selected Items" }),
          mappedItems(parsedValue || [])
        ] }),
        /* @__PURE__ */ jsxs91(
          Box5,
          {
            display: "flex",
            justifyContent: "space-between",
            gap: "10px",
            padding: "4px",
            children: [
              /* @__PURE__ */ jsx154(
                Button2,
                {
                  height: "32px",
                  backgroundColor: "transparent",
                  padding: "7px 15px",
                  fontWeight: 500,
                  borderRadius: "6px",
                  textStyle: "inter-medium-14-17",
                  color: chakraTheme.colors?.arcadiaMainGrey,
                  border: `solid 1px ${chakraTheme.colors?.lightGrey}`,
                  onClick: handleCloseModal,
                  _hover: {
                    backgroundColor: chakraTheme.colors?.arcadiaBgGrey
                  },
                  children: "Cancel"
                }
              ),
              /* @__PURE__ */ jsx154(
                Button2,
                {
                  padding: "7px 42px",
                  disabled: !intersection(parsedValue, validNPISList).length,
                  borderRadius: "6px",
                  backgroundColor: chakraTheme.colors?.gold,
                  height: "32px",
                  onClick: applyHandler,
                  fontWeight: 500,
                  textStyle: "inter-medium-14-17",
                  color: chakraTheme.colors?.white,
                  children: "Apply"
                }
              )
            ]
          }
        )
      ] })
    ] })
  ] });
};
var CSVModal_default = CsvModal;

// src/components/DataGrid/index.tsx
import { useRef as useRef4, useEffect as useEffect2, memo } from "react";

// src/components/DataGrid/config.ts
var DEFAULT_COLUMN_PROPS = {
  textAlign: "left",
  padding: "0 10px 0 5px"
};
var defaultCellTextProps = (theme) => ({
  textAlign: "left",
  fontFamily: theme.fonts?.regular,
  fontWeight: "normal",
  fontSize: "12px",
  lineHeight: "16px",
  color: theme.colors?.mediumGrey,
  padding: "16px 10px"
});
var defaultCellBadgeProps = (theme) => ({
  badgeColor: theme.colors?.graphic02
});

// src/components/Table/context/index.tsx
import { createContext, useState as useState4, useContext } from "react";
import { jsx as jsx155 } from "react/jsx-runtime";
var TableModalContext = createContext(
  null
);
var TableModalProvider = ({
  children
}) => {
  const [isTableModalOpen, setIsTableModalOpen] = useState4(false);
  const [tableModalContent, setTableModalContent] = useState4(null);
  const context = {
    isTableModalOpen,
    setIsTableModalOpen,
    tableModalContent,
    setTableModalContent
  };
  return /* @__PURE__ */ jsx155(TableModalContext.Provider, { value: context, children });
};
function useTableModalContext() {
  const context = TableModalContext;
  if (!context) {
    console.error("There is an error with tableModal context");
  }
  return useContext(context);
}

// src/components/Table/TableWrapper/TableWrapper.tsx
import {
  Box as Box6,
  Table,
  useTheme as useTheme17,
  Modal as Modal2,
  ModalContent as ModalContent2,
  ModalOverlay as ModalOverlay2
} from "@chakra-ui/react";

// src/components/Table/TableLegend/TableLegend.tsx
import { Flex as Flex4, Text as Text6, useTheme as useTheme16 } from "@chakra-ui/react";
import { jsx as jsx156 } from "react/jsx-runtime";
var TableLegend = ({
  borderColor,
  children,
  dataTestId
}) => {
  const chakraTheme2 = useTheme16();
  return /* @__PURE__ */ jsx156(Flex4, { marginTop: "7px", justifyContent: "flex-end", children: /* @__PURE__ */ jsx156(Flex4, { borderLeft: `1px solid ${borderColor || chakraTheme2.colors.gold}`, children: /* @__PURE__ */ jsx156(
    Text6,
    {
      style: {
        fontSize: 12,
        color: chakraTheme2.colors.mediumGrey
      },
      paddingLeft: "5px",
      "data-testid": dataTestId || "cj-tablelegend-text",
      children
    }
  ) }) });
};
var TableLegend_default = TableLegend;

// src/components/Table/TableWrapper/TableWrapper.tsx
import { Fragment as Fragment12, jsx as jsx157, jsxs as jsxs92 } from "react/jsx-runtime";
var TableWrapper = ({
  dataTestId,
  tableLayout,
  borderSpacing,
  borderCollapse,
  legendText,
  legendBorderColor,
  ...props
}) => {
  const theme = useTheme17();
  const { isTableModalOpen, setIsTableModalOpen, tableModalContent } = useTableModalContext();
  const handleCloseModal = () => {
    setIsTableModalOpen(false);
  };
  return /* @__PURE__ */ jsxs92(Fragment12, { children: [
    /* @__PURE__ */ jsx157(
      Table,
      {
        "data-testid": dataTestId || "cjtable-table-id",
        maxWidth: "100%",
        css: {
          tableLayout: tableLayout || "default",
          borderSpacing: borderSpacing || "0px 5px",
          borderCollapse: borderCollapse || "separate"
        },
        width: "100%",
        ...props
      }
    ),
    legendText && /* @__PURE__ */ jsx157(TableLegend_default, { borderColor: legendBorderColor, children: legendText }),
    /* @__PURE__ */ jsxs92(Modal2, { isCentered: true, isOpen: isTableModalOpen, onClose: handleCloseModal, children: [
      /* @__PURE__ */ jsx157(ModalOverlay2, {}),
      /* @__PURE__ */ jsx157(ModalContent2, { children: /* @__PURE__ */ jsxs92(
        Box6,
        {
          width: "500px",
          height: "fit-content",
          "max-height": "520px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-end",
          padding: "10px",
          background: theme.colors.white,
          boxShadow: theme.shadow.mediumShadow,
          borderRadius: "6px",
          boxSizing: "content-box",
          children: [
            /* @__PURE__ */ jsx157(Box6, { as: "div", marginLeft: "10px", cursor: "pointer", children: /* @__PURE__ */ jsx157(CloseIcon_default, { handleClick: handleCloseModal }) }),
            /* @__PURE__ */ jsx157(
              Box6,
              {
                width: "500px",
                height: "fit-content",
                maxHeight: "520px",
                overflowY: "scroll",
                overflowX: "auto",
                css: {
                  "scrollbar-width": "none",
                  "-ms-overflow-style": "none",
                  "::-webkit-scrollbar": {
                    width: "0",
                    height: "1"
                  }
                },
                children: tableModalContent
              }
            )
          ]
        }
      ) })
    ] })
  ] });
};
var TableWrapper_default = TableWrapper;

// src/components/Table/index.tsx
import { jsx as jsx158 } from "react/jsx-runtime";
var Table2 = ({
  dataTestId,
  children,
  width,
  height,
  maxWidth,
  tableLayout,
  borderSpacing,
  borderCollapse,
  legendText,
  legendBorderColor
}) => {
  return /* @__PURE__ */ jsx158(TableModalProvider, { children: /* @__PURE__ */ jsx158(
    TableWrapper_default,
    {
      dataTestId,
      width,
      height,
      maxWidth,
      tableLayout,
      borderSpacing,
      borderCollapse,
      legendText,
      legendBorderColor,
      children
    }
  ) });
};
var Table_default = Table2;

// src/components/Table/TableHead/TableHead.tsx
import { Thead } from "@chakra-ui/react";
import { jsx as jsx159 } from "react/jsx-runtime";
var TableHead = (props) => /* @__PURE__ */ jsx159(Thead, { width: "100%", "data-testid": props.dataTestId || "cj-table-head", children: props.children });
var TableHead_default = TableHead;

// src/components/Table/TableBody/TableBody.tsx
import { Tbody } from "@chakra-ui/react";
import { jsx as jsx160 } from "react/jsx-runtime";
var TableBody = (props) => /* @__PURE__ */ jsx160(
  Tbody,
  {
    width: "100%",
    "data-testid": props.dataTestId || "cj-table-body",
    ...props
  }
);
var TableBody_default = TableBody;

// src/components/Table/TableRow/TableRow.tsx
import { Tr } from "@chakra-ui/react";
import { jsx as jsx161 } from "react/jsx-runtime";
var TableRow = ({
  sticky,
  dataTestId,
  height,
  ...props
}) => /* @__PURE__ */ jsx161(
  Tr,
  {
    borderRadius: "3px",
    height: height || "34px",
    border: 0,
    borderWidth: 0,
    backgroundColor: "transparent",
    ...sticky ? { position: "sticky", top: 0, zIndex: 8 } : {},
    "data-testid": dataTestId || "cjtable-row-id",
    ...props
  }
);
var TableRow_default = TableRow;

// src/components/Table/TableTitle/context/index.tsx
import { createContext as createContext2, useContext as useContext2 } from "react";
import { jsx as jsx162 } from "react/jsx-runtime";
var TableTitleContext = createContext2(
  null
);
var TableTitleProvider = ({
  children,
  titleContextProps
}) => {
  return /* @__PURE__ */ jsx162(TableTitleContext.Provider, { value: titleContextProps, children });
};
function useTableTitleContext() {
  const context = TableTitleContext;
  if (!context) {
    console.error("There is an error with tableTitle context");
  }
  return useContext2(context);
}

// src/components/Table/TableTitle/TableTitleWrapper.tsx
import { Th, useTheme as useTheme20 } from "@chakra-ui/react";

// src/helpers/createScaleArray.ts
var NUMBER_TO_DIVIDE = 8e3;
var NUMBER_TO_FINISH_SCALE = 8;
var createScaleArray = (max) => {
  const divitions = Math.ceil(max / NUMBER_TO_DIVIDE);
  const arr = [];
  for (let i = 0; i < NUMBER_TO_FINISH_SCALE; i++) {
    const number = i * divitions > max / 1e3 ? max / 1e3 : i * divitions;
    arr.push(`$${number}K`);
  }
  return arr;
};
var createScaleArray_default = createScaleArray;

// src/components/Table/TableTitle/TableTitleScale/TableTitleScale.tsx
import { Box as Box7, useTheme as useTheme18 } from "@chakra-ui/react";
import { jsx as jsx163 } from "react/jsx-runtime";
var TableTitleScale = () => {
  const theme = useTheme18();
  const context = useTableTitleContext();
  if (context.titleType === "scale") {
    const {
      scaleMax,
      backgroundColor,
      margin,
      height,
      minHeight,
      padding,
      fontFamily,
      fontSize,
      color,
      lineHeight,
      textAlign,
      fontWeight,
      dataTestId
    } = context;
    const arr = createScaleArray_default(scaleMax);
    const num = arr.length;
    return /* @__PURE__ */ jsx163(
      Box7,
      {
        display: "flex",
        gap: `${num}px` || "22px",
        alignItems: "flex-end",
        justifyContent: "space-around",
        height: height || "35px",
        minHeight: minHeight || "35px",
        backgroundColor: backgroundColor || theme.colors.white,
        borderRadius: "3px",
        padding: padding || 0,
        margin: margin || "0 2px 0 0",
        "data-testid": dataTestId || "cjtable-title-scale-id",
        children: arr.map((el, i) => /* @__PURE__ */ jsx163(
          Box7,
          {
            width: "100%",
            color: color || theme.colors.mediumGrey,
            fontFamily: fontFamily || theme.fonts.regular,
            fontSize: fontSize || "10px",
            fontWeight: fontWeight || "normal",
            lineHeight: lineHeight || "14px",
            textAlign: textAlign || "left",
            children: el
          },
          i
        ))
      }
    );
  }
  return null;
};
var TableTitleScale_default = TableTitleScale;

// src/components/Table/TableTitle/TableTitleText/SortingArrows/SortingArrows.tsx
import { Box as Box8 } from "@chakra-ui/react";
import { jsx as jsx164, jsxs as jsxs93 } from "react/jsx-runtime";
var SortingArrows = () => {
  const context = useTableTitleContext();
  if (context.titleType === "text" || !context.titleType) {
    const {
      handleArrowUpClick,
      handleArrowDownClick,
      typeOfSortingActive,
      sortActiveArrowColor
    } = context;
    return /* @__PURE__ */ jsxs93(
      Box8,
      {
        display: "flex",
        flexDirection: "column",
        gap: "3px",
        "data-testid": "sorting-arrows-container-id",
        children: [
          /* @__PURE__ */ jsx164(
            SortColumnIcon_default,
            {
              handleClick: handleArrowUpClick,
              opacity: typeOfSortingActive === "asc" ? "1" : "0.35",
              color: sortActiveArrowColor,
              dataTestId: "arrow-up-icon"
            }
          ),
          /* @__PURE__ */ jsx164(
            SortColumnIcon_default,
            {
              direction: "down",
              handleClick: handleArrowDownClick,
              opacity: typeOfSortingActive === "desc" ? "1" : "0.35",
              color: sortActiveArrowColor,
              dataTestId: "arrow-down-icon"
            }
          )
        ]
      }
    );
  }
  return null;
};
var SortingArrows_default = SortingArrows;

// src/components/Table/TableTitle/TableTitleText/index.tsx
import { Box as Box9, Divider, useTheme as useTheme19 } from "@chakra-ui/react";
import { Fragment as Fragment13, jsx as jsx165, jsxs as jsxs94 } from "react/jsx-runtime";
var TableTitleText = ({ handleFilterClick }) => {
  const theme = useTheme19();
  const context = useTableTitleContext();
  if (context.titleType === "text" || !context.titleType) {
    const {
      title,
      margin,
      height,
      minHeight,
      borderRadius,
      fontFamily,
      fontSize,
      color,
      lineHeight,
      textAlign,
      textTransform,
      fontWeight,
      dataTestId,
      isSortable,
      isFilterable,
      whiteSpace,
      isFiltered,
      filterActiveColor,
      separatorColor
    } = context;
    return /* @__PURE__ */ jsxs94(
      Box9,
      {
        display: "flex",
        alignItems: "center",
        width: "100%",
        margin: margin || "0",
        height: height || "max-content",
        minHeight: minHeight || "max-content",
        borderRadius: borderRadius || "3px",
        "data-testid": dataTestId || "cjtable-title-text-id",
        children: [
          /* @__PURE__ */ jsx165(
            Box9,
            {
              width: "100%",
              color: color || theme.colors.mediumGrey,
              fontFamily: fontFamily || theme.fonts.regular,
              fontSize: fontSize || "12px",
              fontWeight: fontWeight || "normal",
              lineHeight: lineHeight || "16px",
              textAlign: textAlign || "center",
              "data-testid": "cjtable-title-text-text",
              paddingRight: "4px",
              whiteSpace: whiteSpace || "nowrap",
              textTransform: "initial",
              letterSpacing: "initial",
              children: title
            }
          ),
          isSortable && /* @__PURE__ */ jsx165(SortingArrows_default, {}),
          isSortable && isFilterable && /* @__PURE__ */ jsx165(
            Divider,
            {
              marginLeft: "5px",
              marginRight: "3px",
              borderColor: separatorColor || "#86ADC2",
              orientation: "vertical",
              minH: "21px",
              maxH: "21px",
              borderWidth: "1px"
            }
          ),
          isFilterable && /* @__PURE__ */ jsx165(Fragment13, { children: isFiltered ? /* @__PURE__ */ jsx165(
            FilterIcon_default,
            {
              handleClick: handleFilterClick,
              color: filterActiveColor || theme.colors?.darkerBlue,
              height: 14,
              width: 13,
              cursor: "pointer"
            }
          ) : /* @__PURE__ */ jsx165(
            FilterPlusIcon_default,
            {
              handleClick: handleFilterClick,
              color: filterActiveColor || theme.colors?.darkerBlue,
              cursor: "pointer",
              opacity: "0.35"
            }
          ) })
        ]
      }
    );
  }
  return null;
};
var TableTitleText_default = TableTitleText;

// src/components/Table/TableTitle/TableTitleCustomChildren/TableTitleCustomChildren.tsx
import { Flex as Flex5 } from "@chakra-ui/react";
import { jsx as jsx166 } from "react/jsx-runtime";
var TableTitleCustomChildren = () => {
  const context = useTableTitleContext();
  if (context.titleType === "customChildren") {
    const { childs, dataTestId, justifyContent, ...props } = context;
    return /* @__PURE__ */ jsx166(
      Flex5,
      {
        alignItems: "center",
        justifyContent: justifyContent || "flex-start",
        height: "max-content",
        width: "max-content",
        minHeight: "max-content",
        borderRadius: 0,
        border: "none",
        borderWidth: 0,
        padding: 0,
        margin: 0,
        "data-testid": dataTestId || "cjtable-title-custom-id",
        ...props,
        children: childs
      }
    );
  }
  return null;
};
var TableTitleCustomChildren_default = TableTitleCustomChildren;

// src/components/Table/TableTitle/TableTitleWrapper.tsx
import { jsx as jsx167 } from "react/jsx-runtime";
var TableTitleWrapper = (props) => {
  const theme = useTheme20();
  const { lightBlue, greenMedium } = theme.colors;
  const {
    width,
    maxWidth,
    minWidth,
    padding,
    height,
    minHeight,
    backgroundColor,
    borderRadius,
    border,
    borderWidth,
    sticky,
    titleType,
    rowSpan,
    colSpan
  } = useTableTitleContext();
  const HEADS = {
    text: /* @__PURE__ */ jsx167(TableTitleText_default, { handleFilterClick: props.handleFilterClick }),
    scale: /* @__PURE__ */ jsx167(TableTitleScale_default, {}),
    customChildren: /* @__PURE__ */ jsx167(TableTitleCustomChildren_default, {})
  };
  return /* @__PURE__ */ jsx167(
    Th,
    {
      width: width || "fit-content",
      maxWidth: maxWidth || "fit-content",
      minWidth: minWidth || 0,
      padding: padding || 0,
      height: height || "35px",
      minHeight: minHeight || "35px",
      backgroundColor: backgroundColor || lightBlue,
      borderRadius: borderRadius || "3px",
      border: border || props.typeOfSortingActive || props.isFiltered ? `1px solid ${props.sortActiveBorderColor || greenMedium}` : "1px solid transparent",
      borderWidth: borderWidth || (props.typeOfSortingActive || props.isFiltered ? "1px" : "0 1px"),
      backgroundClip: "padding-box",
      ...sticky ? { position: "sticky", top: 0, zIndex: 9 } : {},
      "data-testid": props.dataTestTHId || "cj-tabletitle-container-id",
      letterSpacing: "initial",
      rowSpan,
      colSpan,
      children: HEADS[titleType || "text"]
    }
  );
};
var TableTitleWrapper_default = TableTitleWrapper;

// src/components/Table/TableTitle/index.tsx
import { jsx as jsx168 } from "react/jsx-runtime";
var TableTitle = (props) => /* @__PURE__ */ jsx168(TableTitleProvider, { titleContextProps: props, children: /* @__PURE__ */ jsx168(TableTitleWrapper_default, { ...props }) });
var TableTitle_default = TableTitle;

// src/components/Table/TableCell/context/index.tsx
import { createContext as createContext3, useContext as useContext3 } from "react";
import { jsx as jsx169 } from "react/jsx-runtime";
var TableCellContext = createContext3(
  null
);
var TableCellProvider = ({
  children,
  cellContextProps
}) => {
  return /* @__PURE__ */ jsx169(TableCellContext.Provider, { value: cellContextProps, children });
};
function useTableCellContext() {
  const context = TableCellContext;
  if (!context) {
    console.error("There is an error with tableCell context");
  }
  return useContext3(context);
}

// src/hooks/useEllipsisTooltipForTableCells/index.ts
import { useState as useState5, useRef as useRef3, useLayoutEffect as useLayoutEffect2 } from "react";
var useEllipsisTooltipForTableCells = () => {
  const ref = useRef3(null);
  const [overflowActive, setOverflowActive] = useState5(false);
  const isOverFlowActive = () => {
    if (!overflowActive) {
      const element = ref.current;
      if (element) {
        setOverflowActive(
          element.offsetWidth < element.scrollWidth || element.offsetHeight < element.scrollHeight
        );
      }
    }
  };
  useLayoutEffect2(() => {
    window.addEventListener("resize", isOverFlowActive);
    return () => {
      window.removeEventListener("resize", isOverFlowActive);
    };
  }, []);
  useLayoutEffect2(() => {
    isOverFlowActive();
  }, [ref?.current?.offsetWidth]);
  return [ref, overflowActive];
};

// src/components/Table/TableCell/TableCellText/TableCellTextModal.tsx
import { Box as Box10, useTheme as useTheme21 } from "@chakra-ui/react";
import { jsx as jsx170 } from "react/jsx-runtime";
var TableCellTextModalContent = ({
  fullText
}) => {
  const theme = useTheme21();
  return /* @__PURE__ */ jsx170(
    Box10,
    {
      fontFamily: theme.fonts.regular,
      fontSize: "12px",
      lineHeight: "16px",
      color: theme.colors.mediumGrey,
      children: fullText
    }
  );
};
var TableCellTextModal_default = TableCellTextModalContent;

// src/components/Table/TableCell/TableCellText/TableCellText.tsx
import { Box as Box11, useTheme as useTheme22 } from "@chakra-ui/react";
import { jsx as jsx171 } from "react/jsx-runtime";
var TableCellText = () => {
  const theme = useTheme22();
  const cellContext = useTableCellContext();
  if (cellContext.cellType === "text" || !cellContext.cellType) {
    const {
      title,
      borderRadius,
      cellTextHeight,
      cellTextMinHeight,
      alignItems,
      justifyContent,
      margin,
      fontFamily,
      fontSize,
      color,
      lineHeight,
      textAlign,
      textDecoration,
      fontWeight,
      useTwoLineEllipsis = title?.length && title?.length > 30,
      hoverEllipsisBackgroundColor,
      dataTestId,
      onClick,
      underlineOnHover,
      maxWidth,
      cursor
    } = cellContext;
    const { setTableModalContent, setIsTableModalOpen } = useTableModalContext();
    const [cellTextRef, overflowActive] = useEllipsisTooltipForTableCells();
    const handleCellClick = () => {
      if (useTwoLineEllipsis && overflowActive) {
        setTableModalContent(/* @__PURE__ */ jsx171(TableCellTextModal_default, { fullText: title }));
        setIsTableModalOpen(true);
      }
      if (onClick) {
        onClick();
      }
    };
    const useCursorPointer = () => {
      if (onClick) {
        return true;
      }
      if (useTwoLineEllipsis && overflowActive) {
        return true;
      }
      if (cursor && cursor === "pointer") {
        return true;
      }
      return false;
    };
    return /* @__PURE__ */ jsx171(
      Box11,
      {
        display: "flex",
        alignItems: alignItems || "center",
        justifyContent: justifyContent || "flex-start",
        height: cellTextHeight || "max-content",
        minHeight: cellTextMinHeight || "max-content",
        margin: margin || 0,
        borderRadius: borderRadius || 0,
        cursor: useCursorPointer() ? "pointer" : "default",
        maxW: maxWidth || 300,
        _hover: useCursorPointer() ? { backgroundColor: hoverEllipsisBackgroundColor || "transparent" } : {},
        onClick: handleCellClick,
        "data-testid": dataTestId || "cjtable-cell-text-id",
        children: /* @__PURE__ */ jsx171(
          Box11,
          {
            color: color || theme.colors.mediumGrey,
            fontFamily: fontFamily || theme.fonts.interRegular,
            fontSize: fontSize || "12px",
            fontWeight: fontWeight || "normal",
            lineHeight: lineHeight || "14px",
            textAlign: textAlign || "center",
            textDecoration: textDecoration || "initial",
            ...useTwoLineEllipsis ? {
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box"
            } : {},
            sx: useTwoLineEllipsis ? {
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: "2"
            } : {},
            _hover: underlineOnHover ? {
              textDecoration: `underline ${color || theme.colors.mediumGrey}`
            } : {},
            ref: cellTextRef,
            "data-testid": "cjtable-cell-text-text",
            children: title
          }
        )
      }
    );
  }
  return null;
};
var TableCellText_default = TableCellText;

// src/components/Table/TableCell/TableCellBar/TableCellBar.tsx
import { Box as Box12, useTheme as useTheme23 } from "@chakra-ui/react";
import { jsx as jsx172, jsxs as jsxs95 } from "react/jsx-runtime";
var TableCellBar = () => {
  const theme = useTheme23();
  const { lighterGrey, graphicRed, graphicGreen } = theme.colors;
  const context = useTableCellContext();
  if (context.cellType === "bar") {
    const { valueOutside, valueBar, dataTestId } = context;
    return /* @__PURE__ */ jsxs95(
      Box12,
      {
        width: "100%",
        display: "block",
        paddingLeft: "2px",
        "data-testid": dataTestId || "cjtable-cell-bar-id",
        children: [
          /* @__PURE__ */ jsx172(
            Box12,
            {
              display: "block",
              width: valueOutside,
              height: "14px",
              position: "relative",
              background: lighterGrey,
              borderTopRightRadius: "3px",
              borderBottomRightRadius: "3px",
              "data-testid": "cjtable-cell-bar-outside"
            }
          ),
          /* @__PURE__ */ jsx172(
            Box12,
            {
              display: "block",
              width: valueBar,
              height: "4px",
              background: valueBar > valueOutside ? graphicRed : graphicGreen,
              position: "relative",
              bottom: "9px",
              borderTopRightRadius: "3px",
              borderBottomRightRadius: "3px",
              "data-testid": "cjtable-cell-bar-inside"
            }
          )
        ]
      }
    );
  }
  return null;
};
var TableCellBar_default = TableCellBar;

// src/components/Table/TableCell/TableCellProgressBar/TableCellProgressBar.tsx
import { Box as Box14, useTheme as useTheme25 } from "@chakra-ui/react";

// src/components/Table/TableCell/TableCellProgressBar/ProgressBar.tsx
import { Box as Box13, useTheme as useTheme24 } from "@chakra-ui/react";
import { jsx as jsx173 } from "react/jsx-runtime";
var ProgressBar = ({ percentile, completed, children, progressBarColor }) => {
  const theme = useTheme24();
  return /* @__PURE__ */ jsx173(
    Box13,
    {
      height: "4px",
      marginTop: "0.5em",
      width: "100%",
      backgroundColor: theme.colors.bgGrey,
      borderRadius: "50px",
      children: /* @__PURE__ */ jsx173(
        Box13,
        {
          height: "100%",
          position: "relative",
          width: `${completed}%`,
          backgroundColor: progressBarColor || (percentile > 0.5 ? theme.colors.graphicGreen : theme.colors.graphicRed),
          borderRadius: "inherit",
          textAlign: "right",
          children
        }
      )
    }
  );
};
var ProgressBar_default = ProgressBar;

// src/components/Table/TableCell/TableCellProgressBar/TableCellProgressBar.tsx
import { jsx as jsx174, jsxs as jsxs96 } from "react/jsx-runtime";
var TableCellProgressBar = () => {
  const cellContext = useTableCellContext();
  if (cellContext.cellType === "progress") {
    const theme = useTheme25();
    const { percentile, dataTestId, progressBarColor } = cellContext;
    const valueToUse = percentile || 0;
    return /* @__PURE__ */ jsxs96(
      Box14,
      {
        display: "flex",
        "data-testid": dataTestId || "cjtable-cell-progress-id",
        children: [
          /* @__PURE__ */ jsx174(
            ProgressBar_default,
            {
              progressBarColor,
              completed: valueToUse * 100,
              percentile: valueToUse,
              children: /* @__PURE__ */ jsx174(
                Box14,
                {
                  width: "13px",
                  height: "13px",
                  position: "absolute",
                  right: "-12px",
                  left: valueToUse === 0 ? 0 : void 0,
                  bottom: "-5px",
                  borderRadius: "100%",
                  backgroundColor: progressBarColor || (valueToUse > 0.5 ? theme.colors.graphicGreen : theme.colors.graphicRed)
                }
              )
            }
          ),
          /* @__PURE__ */ jsx174(
            Box14,
            {
              as: "span",
              width: "24px",
              fontFamily: theme.fonts.light,
              fontSize: "12px",
              color: theme.colors.mediumGrey,
              margin: "0 15px",
              children: valueToUse
            }
          )
        ]
      }
    );
  }
  return null;
};
var TableCellProgressBar_default = TableCellProgressBar;

// src/components/Table/TableCell/TableCellBenchmark/TableCellBenchmark.tsx
import { Box as Box19 } from "@chakra-ui/react";

// src/components/Table/TableCell/TableCellBenchmark/benchMark.tsx
import { useTheme as useTheme28, Box as Box18 } from "@chakra-ui/react";
import { Fragment as Fragment14 } from "react";

// src/components/Table/TableCell/TableCellBenchmark/StyledPercent.tsx
import { Flex as Flex6, useTheme as useTheme26 } from "@chakra-ui/react";
import { jsx as jsx175 } from "react/jsx-runtime";
var StyledPercent = ({
  fontSize,
  fontFamily,
  colorPositive,
  dataTestId = "",
  ...props
}) => {
  const theme = useTheme26();
  return /* @__PURE__ */ jsx175(
    Flex6,
    {
      fontSize: fontSize || "14px",
      fontFamily: fontFamily || theme.fonts.semiBold,
      my: 0,
      mx: "4px",
      color: colorPositive || theme.colors.green || theme.colors.black,
      "data-testid": dataTestId,
      ...props
    }
  );
};
var StyledPercent_default = StyledPercent;

// src/components/Table/TableCell/TableCellBenchmark/StyledInfoContainer.tsx
import { Box as Box15 } from "@chakra-ui/react";
import { jsx as jsx176 } from "react/jsx-runtime";
var StyledInfoContainer = (props) => {
  return /* @__PURE__ */ jsx176(
    Box15,
    {
      width: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      ...props
    }
  );
};
var StyledInfoContainer_default = StyledInfoContainer;

// src/components/Table/TableCell/TableCellBenchmark/StyledPercentContainer.tsx
import { Box as Box16 } from "@chakra-ui/react";
import { jsx as jsx177 } from "react/jsx-runtime";
var StyledPercentContainer = ({ dataTestId = "", ...props }) => {
  return /* @__PURE__ */ jsx177(Box16, { width: "calc(50% - 1px)", flex: 1, "data-testid": dataTestId, ...props });
};
var StyledPercentContainer_default = StyledPercentContainer;

// src/components/Table/TableCell/TableCellBenchmark/StyledBarPercent.tsx
import { Box as Box17, useTheme as useTheme27 } from "@chakra-ui/react";
import { jsx as jsx178 } from "react/jsx-runtime";
var StyledBarPercent = ({
  dataTestId,
  percent,
  isNegative = false,
  isZeroPercent = false,
  colorPositive,
  ...props
}) => {
  const theme = useTheme27();
  return /* @__PURE__ */ jsx178(
    Box17,
    {
      height: "10px",
      width: `${percent > 100 ? 100 : percent}px`,
      borderRadius: isZeroPercent ? "2px" : isNegative ? "2px 0 0 2px" : "0 2px 2px 0",
      clipPath: percent > 100 ? "polygon(0% 0%, 95% 0%, 100% 50%, 95% 100%, 0% 100%)" : "none",
      ...isZeroPercent ? { position: "absolute", top: "25%", left: "-1px" } : {},
      backgroundColor: colorPositive || theme.colors.green,
      "data-testid": dataTestId,
      ...props
    }
  );
};
var StyledBarPercent_default = StyledBarPercent;

// src/components/Table/TableCell/TableCellBenchmark/benchMark.tsx
import { Fragment as Fragment15, jsx as jsx179, jsxs as jsxs97 } from "react/jsx-runtime";
var BenchMark = ({
  colorPositive,
  colorNegative,
  colorCentralBar,
  fontFamily,
  fontSize = "14px",
  percent,
  tooltipText,
  tooltipId,
  tooltipPlace = "bottom",
  dataTestId,
  tooltipProps = {},
  maxValue
}) => {
  const theme = useTheme28();
  const checkNumber = (number) => Math.abs(number) > 0 && Math.abs(number) < 1;
  const WrapperComponent = tooltipId ? Tooltip_default : Fragment14;
  const wrapperComponentProps = tooltipId ? {
    label: tooltipText,
    dataTestId: tooltipId,
    placement: tooltipPlace,
    bg: theme.colors?.white,
    ...tooltipProps
  } : {};
  return /* @__PURE__ */ jsx179(WrapperComponent, { ...wrapperComponentProps, children: /* @__PURE__ */ jsxs97(
    Box18,
    {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      children: [
        /* @__PURE__ */ jsx179(
          StyledPercentContainer_default,
          {
            dataTestId: dataTestId || "cjtable-cell-benchmark-id",
            children: percent <= 0 && /* @__PURE__ */ jsxs97(StyledInfoContainer_default, { children: [
              /* @__PURE__ */ jsx179(
                StyledPercent_default,
                {
                  fontSize,
                  fontFamily,
                  colorPositive,
                  dataTestId: "cjtable-cell-benchmark-percent",
                  children: `${checkNumber(percent) ? "<" : ""}${percent !== 0 ? "-" : ""}${Math.floor(Math.abs(percent))}%`
                }
              ),
              /* @__PURE__ */ jsx179(
                StyledBarPercent_default,
                {
                  colorPositive,
                  dataTestId: "cjtable-cell-negative-benchmark-bar",
                  percent: Math.floor(Math.abs(percent)),
                  isNegative: true
                }
              )
            ] })
          }
        ),
        /* @__PURE__ */ jsx179(
          Box18,
          {
            height: "33px",
            width: "0px",
            backgroundColor: colorCentralBar,
            position: "relative",
            border: `0.5px solid ${theme.colors.lightGrey}`,
            children: percent === 0 && /* @__PURE__ */ jsx179(
              StyledBarPercent_default,
              {
                colorPositive,
                dataTestId: "cjtable-cell-benchmark-zero-percent-bar",
                bgColor: theme.colors.green,
                percent: 3,
                isZeroPercent: true
              }
            )
          }
        ),
        /* @__PURE__ */ jsx179(StyledPercentContainer_default, { children: percent > 0 && /* @__PURE__ */ jsxs97(StyledInfoContainer_default, { children: [
          /* @__PURE__ */ jsx179(
            StyledBarPercent_default,
            {
              colorPositive,
              dataTestId: "cjtable-cell-positive-benchmark-bar",
              bgColor: colorNegative || theme.colors.red,
              percent: Math.floor(percent)
            }
          ),
          /* @__PURE__ */ jsx179(
            StyledPercent_default,
            {
              colorPositive,
              dataTestId: "cjtable-cell-benchmark-percent",
              fontSize,
              fontFamily,
              color: colorNegative || theme.colors.red,
              children: maxValue && percent >= maxValue ? /* @__PURE__ */ jsxs97(Fragment15, { children: [
                /* @__PURE__ */ jsx179(
                  Box18,
                  {
                    as: "span",
                    fontSize: "10px",
                    fontFamily: theme.fonts.regular,
                    color: theme.colors.mediumGrey2,
                    margin: "0 5px 0 0",
                    children: "[Over]"
                  }
                ),
                "+",
                maxValue,
                "%"
              ] }) : `+${Math.floor(percent)}% ${checkNumber(percent) ? ">" : ""}`
            }
          )
        ] }) })
      ]
    }
  ) });
};
var benchMark_default = BenchMark;

// src/components/Table/TableCell/TableCellBenchmark/TableCellBenchmark.tsx
import { jsx as jsx180 } from "react/jsx-runtime";
var TableCellBenchmark = () => {
  const context = useTableCellContext();
  if (context.cellType === "benchmark") {
    const {
      height,
      minHeight,
      colorPositive,
      colorNegative,
      colorCentralBar,
      percent,
      tooltipId,
      tooltipText,
      tooltipPlace,
      fontSize,
      fontFamily,
      dataTestId,
      tooltipProps,
      maxValue
    } = context;
    return /* @__PURE__ */ jsx180(
      Box19,
      {
        display: "flex",
        alignItems: "center",
        height: height || "35px",
        minHeight: minHeight || height || "35px",
        children: /* @__PURE__ */ jsx180(
          benchMark_default,
          {
            dataTestId,
            colorCentralBar,
            colorPositive,
            colorNegative,
            percent,
            tooltipId,
            tooltipText,
            tooltipPlace,
            fontSize,
            fontFamily,
            tooltipProps,
            maxValue
          }
        )
      }
    );
  }
  return null;
};
var TableCellBenchmark_default = TableCellBenchmark;

// src/components/Table/TableCell/TableCellMultipleValues/TableCellMultipleValues.tsx
import { Box as Box21, useTheme as useTheme30 } from "@chakra-ui/react";

// src/components/Table/TableCell/TableCellMultipleValues/TableCellMultipleValuesModalContent.tsx
import { Box as Box20, useTheme as useTheme29 } from "@chakra-ui/react";
import { jsx as jsx181 } from "react/jsx-runtime";
var TableCellMultipleValuesModalContent = ({ fullText }) => {
  const theme = useTheme29();
  return /* @__PURE__ */ jsx181(
    Box20,
    {
      fontFamily: theme.fonts.regular,
      fontSize: "12px",
      lineHeight: "16px",
      color: theme.colors.mediumGrey,
      children: fullText
    }
  );
};
var TableCellMultipleValuesModalContent_default = TableCellMultipleValuesModalContent;

// src/components/Table/TableCell/TableCellMultipleValues/TableCellMultipleValues.tsx
import { jsx as jsx182, jsxs as jsxs98 } from "react/jsx-runtime";
var TableCellMultipleValues = () => {
  const theme = useTheme30();
  const cellContext = useTableCellContext();
  if (cellContext.cellType === "multipleValues") {
    const {
      title,
      borderRadius,
      multipleValuesHeight,
      multipleValuesMinHeight,
      margin,
      textFontFamily,
      textFontSize,
      textLineHeight,
      textFontWeight,
      textColor,
      textTextAlign,
      moreNumberFontFamily,
      moreNumberFontSize,
      moreNumberLineHeight,
      moreNumberFontWeight,
      moreNumberColor,
      moreNumberTextAlign,
      handleClick,
      dataTestId,
      multipleValuesJustifyContent,
      multipleValuesAlignItems
    } = cellContext;
    const { setTableModalContent, setIsTableModalOpen } = useTableModalContext();
    const [cellMultipleValuesRef, overflowActive] = useEllipsisTooltipForTableCells();
    const handleCellClick = () => {
      if (overflowActive) {
        const text = Array.isArray(title) ? title[0] : title;
        setTableModalContent(
          /* @__PURE__ */ jsx182(TableCellMultipleValuesModalContent_default, { fullText: text })
        );
        setIsTableModalOpen(true);
      }
    };
    return /* @__PURE__ */ jsxs98(
      Box21,
      {
        display: "flex",
        alignItems: multipleValuesAlignItems || "center",
        justifyContent: multipleValuesJustifyContent || "space-between",
        height: multipleValuesHeight || "28px",
        minHeight: multipleValuesMinHeight || "28px",
        margin: margin || 0,
        "border-radius": borderRadius || 0,
        borderRadius,
        "data-testid": dataTestId || "cjtable-multiple-values-cell-id",
        children: [
          /* @__PURE__ */ jsx182(
            Box21,
            {
              color: textColor || theme.colors.mediumGrey,
              fontFamily: textFontFamily || theme.fonts.regular,
              fontSize: textFontSize || "12px",
              fontWeight: textFontWeight || "normal",
              textAlign: textTextAlign || "left",
              cursor: overflowActive ? "pointer" : "auto",
              lineHeight: textLineHeight || "16px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              sx: {
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: "2"
              },
              ref: cellMultipleValuesRef,
              "data-testid": "cjtable-multiple-values-cell-text",
              onClick: handleCellClick,
              children: title && title[0]
            }
          ),
          title && title.length > 1 && /* @__PURE__ */ jsxs98(
            Box21,
            {
              color: moreNumberColor || theme.colors?.greenMedium,
              fontFamily: moreNumberFontFamily || theme.fonts.semiBold,
              fontSize: moreNumberFontSize || "12px",
              fontWeight: moreNumberFontWeight || "600",
              textAlign: moreNumberTextAlign || "right",
              paddingLeft: "5px",
              cursor: "pointer",
              "data-testid": "cjtable-multiple-values-cell-moreNumber",
              lineHeight: moreNumberLineHeight,
              onClick: handleClick,
              children: [
                "+",
                title.length - 1
              ]
            }
          )
        ]
      }
    );
  }
  return null;
};
var TableCellMultipleValues_default = TableCellMultipleValues;

// src/components/Table/TableCell/TableCellCustomChildren/TableCellCustomChildren.tsx
import { Box as Box22 } from "@chakra-ui/react";
import { jsx as jsx183 } from "react/jsx-runtime";
var TableCellCustomChildren = () => {
  const context = useTableCellContext();
  if (context.cellType === "customChildren") {
    const { childs, dataTestId, justifyContent, ...props } = context;
    return /* @__PURE__ */ jsx183(
      Box22,
      {
        display: "flex",
        alignItems: "center",
        justifyContent: justifyContent || "flex-start",
        height: "max-content",
        width: "max-content",
        minHeight: "max-content",
        borderRadius: 0,
        border: "none",
        borderWidth: 0,
        padding: 0,
        margin: 0,
        "data-testid": dataTestId || "cjtable-cell-custom-id",
        ...props,
        children: childs
      }
    );
  }
  return null;
};
var TableCellCustomChildren_default = TableCellCustomChildren;

// src/components/Table/TableCell/TableCellStarChildren/TableCellStarChildren.tsx
import { Box as Box23 } from "@chakra-ui/react";
import { jsx as jsx184, jsxs as jsxs99 } from "react/jsx-runtime";
var TableCellStarChildren = () => {
  const context = useTableCellContext();
  if (context.cellType === "star") {
    const { value, dataTestId, justifyContent, ...props } = context;
    const numberFilledStar = value ? value <= 0 ? 0 : Math.round(value) : 0;
    return /* @__PURE__ */ jsxs99(
      Box23,
      {
        display: "flex",
        alignItems: "center",
        justifyContent: justifyContent || "flex-start",
        height: "max-content",
        width: "max-content",
        minHeight: "max-content",
        borderRadius: 0,
        border: "none",
        borderWidth: 0,
        padding: 0,
        margin: 0,
        "data-testid": dataTestId || "cjtable-cell-star-id",
        ...props,
        children: [
          [...Array(numberFilledStar).keys()].map((key) => /* @__PURE__ */ jsx184(StarIcon_default, {}, `star-${key}`)),
          [...Array(5 - numberFilledStar).keys()].map((key) => /* @__PURE__ */ jsx184(StarIcon_default, { variant: "outlined" }, `star-${5 - key}`))
        ]
      }
    );
  }
  return null;
};
var TableCellStarChildren_default = TableCellStarChildren;

// src/components/Table/TableCell/TableCellScore/TableCellScore.tsx
import { Box as Box24, useTheme as useTheme31 } from "@chakra-ui/react";
import { jsx as jsx185, jsxs as jsxs100 } from "react/jsx-runtime";
var TableCellScore = () => {
  const theme = useTheme31();
  const context = useTableCellContext();
  if (context.cellType === "score") {
    const {
      score,
      maxScore,
      height,
      minHeight,
      margin,
      flexJustifyContent,
      scoreFontFamily,
      scoreFontSize,
      scoreLineHeight,
      scoreFontWeight,
      scoreColor,
      maxScoreFontFamily,
      maxScoreFontSize,
      maxScoreLineHeight,
      maxScoreFontWeight,
      maxScoreColor,
      dataTestId,
      onClick,
      width,
      padding
    } = context;
    const handleCellClick = () => {
      if (onClick) {
        onClick();
      }
    };
    const scoreCellsBackgrounds = {
      1: theme.colors?.arcadiaPpi1,
      2: theme.colors?.arcadiaPpi2,
      3: theme.colors?.arcadiaPpi3,
      4: theme.colors?.arcadiaPpi4,
      5: theme.colors?.arcadiaPpi5
    };
    return /* @__PURE__ */ jsxs100(
      Box24,
      {
        display: "flex",
        backgroundColor: scoreCellsBackgrounds[score],
        justifyContent: flexJustifyContent || "center",
        height: height || "max-content",
        minHeight: minHeight || "max-content",
        margin: margin || 0,
        width,
        padding,
        borderRadius: "6px",
        cursor: onClick ? "pointer" : "default",
        "data-testid": dataTestId || "cjtable-score-cell-id",
        onClick: handleCellClick,
        children: [
          /* @__PURE__ */ jsx185(
            Box24,
            {
              color: scoreColor || theme.colors?.greenExplorer,
              fontFamily: scoreFontFamily || theme.fonts.semiBold,
              fontSize: scoreFontSize || "24px",
              fontWeight: scoreFontWeight || "600",
              lineHeight: scoreLineHeight || "24px",
              "data-testid": "cjtable-score-cell-score",
              children: score
            }
          ),
          /* @__PURE__ */ jsx185(
            Box24,
            {
              color: maxScoreColor || theme.colors.mediumGrey,
              fontFamily: maxScoreFontFamily || theme.fonts.semiBold,
              fontSize: maxScoreFontSize || "14px",
              fontWeight: maxScoreFontWeight || "600",
              lineHeight: maxScoreLineHeight || "24px",
              marginLeft: "4px",
              "data-testid": "cjtable-score-cell-maxScore",
              children: `/ ${maxScore || "5"}`
            }
          )
        ]
      }
    );
  }
  return null;
};
var TableCellScore_default = TableCellScore;

// src/components/Table/TableCell/TableCellBadge/TableCellBadge.tsx
import { Box as Box25, useTheme as useTheme32 } from "@chakra-ui/react";
import { jsx as jsx186 } from "react/jsx-runtime";
var TableCellBadge = () => {
  const theme = useTheme32();
  const { graphic02, mediumGrey } = theme.colors;
  const context = useTableCellContext();
  if (context.cellType === "badge") {
    const {
      title,
      badgeBorderRadius,
      height,
      minHeight,
      margin,
      fontFamily,
      fontSize,
      color,
      lineHeight,
      textAlign,
      fontWeight,
      badgeColor,
      badgePadding,
      dataTestId
    } = context;
    return /* @__PURE__ */ jsx186(
      Box25,
      {
        display: "flex",
        alignItems: "center",
        height: height || "max-content",
        minHeight: minHeight || "max-content",
        margin: margin || "6px",
        padding: badgePadding || "10px",
        borderRadius: badgeBorderRadius || "3px",
        backgroundColor: badgeColor || graphic02,
        "data-testid": dataTestId || "cjtable-cell-badge-id",
        children: /* @__PURE__ */ jsx186(
          Box25,
          {
            color: color || mediumGrey,
            fontFamily: fontFamily || theme.fonts.regular,
            fontSize: fontSize || "12px",
            fontWeight: fontWeight || "normal",
            lineHeight: lineHeight || "16px",
            textAlign: textAlign || "center",
            "data-testid": "cjtable-cell-badge-text",
            children: title
          }
        )
      }
    );
  }
  return null;
};
var TableCellBadge_default = TableCellBadge;

// src/components/Table/TableCell/TableCellWrapper.tsx
import { Td, useTheme as useTheme33 } from "@chakra-ui/react";
import { jsx as jsx187 } from "react/jsx-runtime";
var CELLS = {
  text: /* @__PURE__ */ jsx187(TableCellText_default, {}),
  bar: /* @__PURE__ */ jsx187(TableCellBar_default, {}),
  progress: /* @__PURE__ */ jsx187(TableCellProgressBar_default, {}),
  benchmark: /* @__PURE__ */ jsx187(TableCellBenchmark_default, {}),
  multipleValues: /* @__PURE__ */ jsx187(TableCellMultipleValues_default, {}),
  score: /* @__PURE__ */ jsx187(TableCellScore_default, {}),
  badge: /* @__PURE__ */ jsx187(TableCellBadge_default, {}),
  customChildren: /* @__PURE__ */ jsx187(TableCellCustomChildren_default, {}),
  star: /* @__PURE__ */ jsx187(TableCellStarChildren_default, {})
};
var TableCellWrapper = () => {
  const theme = useTheme33();
  const {
    width,
    maxWidth,
    padding,
    height,
    minHeight,
    backgroundColor,
    borderRadius,
    border,
    borderWidth,
    blur,
    colSpan,
    rowSpan,
    dataTestTDId,
    cellType
  } = useTableCellContext();
  return /* @__PURE__ */ jsx187(
    Td,
    {
      width: width || "fit-content",
      maxWidth: maxWidth || "fit-content",
      height: height || "initial",
      minHeight: minHeight || "initial",
      backgroundColor: backgroundColor || theme.colors.white,
      borderRadius: borderRadius || 0,
      border: border || "none",
      borderWidth: borderWidth || 0,
      padding: padding || 0,
      px: "6px",
      css: blur ? {
        WebkitFilter: `blur(${blur})`,
        MozFilter: `blur(${blur})`,
        OFilter: `blur(${blur})`,
        msFilter: `blur(${blur})`,
        filter: `blur(${blur})`
      } : {},
      blur,
      colSpan,
      rowSpan,
      "data-testid": dataTestTDId || "cj-tablecell-container-id",
      children: CELLS[cellType || "text"]
    }
  );
};
var TableCellWrapper_default = TableCellWrapper;

// src/components/Table/TableCell/index.tsx
import { jsx as jsx188 } from "react/jsx-runtime";
var TableCell = (props) => /* @__PURE__ */ jsx188(TableCellProvider, { cellContextProps: props, children: /* @__PURE__ */ jsx188(TableCellWrapper_default, {}) });
var TableCell_default = TableCell;

// src/components/DataGrid/utils.tsx
import { useTheme as useTheme34 } from "@chakra-ui/react";
import { jsx as jsx189 } from "react/jsx-runtime";
var getCell = (cell, cellIndex, rowIndex, cellsLength, backgroundColor) => {
  const theme = useTheme34();
  const borderRadius = cell.borderRadius;
  const tableCell = {
    text: () => {
      if (cell.cellType === "text") {
        const cellProps = {
          ...defaultCellTextProps(theme),
          ...cell
        };
        return /* @__PURE__ */ jsx189(
          TableCell_default,
          {
            backgroundColor,
            borderRadius: cellIndex === 0 ? "10px 0 0 10px" : cellIndex === cellsLength - 1 ? "0 10px 10px 0" : "0",
            rowIndex: cellProps.columnIndex,
            title: cellProps.value,
            cellType: cellProps.cellType,
            textAlign: cellProps.textAlign,
            fontFamily: cellProps.fontFamily,
            fontWeight: cellProps.fontWeight,
            fontSize: cellProps.fontSize,
            lineHeight: cellProps.lineHeight,
            color: cellProps.color,
            padding: cellProps.padding,
            onClick: cellProps.onClick,
            blur: cell.blur,
            colSpan: cell.colSpan,
            justifyContent: cell.justifyContent,
            useTwoLineEllipsis: cellProps.useTwoLineEllipsis,
            dataTestId: cell.dataTestId ? `${cell.dataTestId}--${rowIndex}` : null
          },
          `${cell.columnIndex}--${cellIndex}`
        );
      }
      return null;
    },
    multipleValues: () => {
      if (cell.cellType === "multipleValues") {
        return /* @__PURE__ */ jsx189(
          TableCell_default,
          {
            backgroundColor,
            borderRadius: cellIndex === 0 ? "10px 0 0 10px" : cellIndex === cellsLength - 1 ? "0 10px 10px 0" : "0",
            moreNumberColor: cell.moreNumberColor,
            title: cell.value,
            cellType: "multipleValues",
            handleClick: cell.handleClick,
            padding: "0 5px",
            textColor: cell.textColor,
            textFontFamily: cell.textFontFamily,
            dataTestId: cell.dataTestId ? `${cell.dataTestId}--${rowIndex}` : null
          },
          `${cell.columnIndex}--${rowIndex}`
        );
      }
      return null;
    },
    score: () => {
      if (cell.cellType === "score") {
        return /* @__PURE__ */ jsx189(
          TableCell_default,
          {
            borderRadius: cellIndex === 0 ? "10px 0 0 10px" : cellIndex === cellsLength - 1 ? "0 10px 10px 0" : borderRadius,
            score: cell.value,
            width: cell.width,
            margin: "auto",
            maxScore: cell.maxScore,
            scoreColor: cell.scoreColor,
            maxScoreFontSize: cell.maxScoreFontSize,
            scoreFontSize: cell.scoreFontSize,
            maxScoreLineHeight: cell.maxScoreLineHeight,
            scoreLineHeight: cell.scoreLineHeight,
            maxScoreColor: cell.maxScoreColor,
            scoreFontFamily: cell.scoreFontFamily,
            backgroundColor: cell.backgroundColor,
            maxScoreFontFamily: cell.maxScoreFontFamily,
            scoreFontWeight: cell.scoreFontWeight,
            maxScoreFontWeight: cell.maxScoreFontWeight,
            flexJustifyContent: cell.flexJustifyContent,
            padding: cell.padding,
            cellType: "score",
            blur: cell.blur,
            dataTestId: cell.dataTestId ? `${cell.dataTestId}--${rowIndex}` : null
          },
          `${cell.columnIndex}--${rowIndex}`
        );
      }
      return null;
    },
    badge: () => {
      if (cell.cellType === "badge") {
        const cellProps = {
          ...defaultCellBadgeProps(theme),
          ...cell
        };
        return /* @__PURE__ */ jsx189(
          TableCell_default,
          {
            backgroundColor,
            borderRadius: cellIndex === 0 ? "10px 0 0 10px" : cellIndex === cellsLength - 1 ? "0 10px 10px 0" : "0",
            title: cellProps.value,
            cellType: "badge",
            badgeColor: cellProps.badgeColor,
            padding: cellProps.padding,
            dataTestId: cell.dataTestId ? `${cell.dataTestId}--${rowIndex}` : null
          },
          `${cell.columnIndex}--${cellIndex}`
        );
      }
      return null;
    },
    customChildren: () => {
      if (cell.cellType === "customChildren") {
        return /* @__PURE__ */ jsx189(
          TableCell_default,
          {
            backgroundColor,
            cellType: "customChildren",
            borderRadius: cellIndex === 0 ? "10px 0 0 10px" : cellIndex === cellsLength - 1 ? "0 10px 10px 0" : "0",
            rowIndex: cell.columnIndex,
            childs: cell.childs,
            colSpan: cell.colSpan,
            dataTestId: cell.dataTestId ? `${cell.dataTestId}--${rowIndex}` : null,
            width: cell.width || "100%"
          },
          `${cell.columnIndex}--${rowIndex}`
        );
      }
      return null;
    }
  };
  return tableCell[cell.cellType]();
};

// src/components/DataGrid/index.tsx
import { Box as Box26, useTheme as useTheme35 } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { jsx as jsx190, jsxs as jsxs101 } from "react/jsx-runtime";
var DataGrid = ({
  columns,
  rows,
  updateSortedColumn,
  updateFilteredColumn,
  sortBy: sortBy2,
  rowBackground
}) => {
  const theme = useTheme35();
  const scrollableRef = useRef4(null);
  useEffect2(() => {
    const scrollableDiv = scrollableRef.current;
    if (scrollableDiv) {
      scrollableDiv.addEventListener("wheel", (evt) => {
        scrollableDiv.scrollLeft += evt.deltaX;
      });
    }
  }, []);
  const handleSortColumn = (field, order) => {
    if (field === sortBy2.field && order === sortBy2.order) {
      updateSortedColumn?.({
        action: "resetSort",
        field: "RELEVANCE",
        order: "desc"
      });
      return;
    }
    updateSortedColumn?.({
      action: "sort",
      field,
      order
    });
  };
  return /* @__PURE__ */ jsx190(
    Box26,
    {
      width: "100%",
      height: "100%",
      overflowX: "auto",
      css: {
        "::-webkit-scrollbar": {
          height: "10px",
          width: "10px",
          backgroundColor: "transparent"
        },
        "::-webkit-scrollbar-track": {
          borderRadius: "5px",
          backgroundColor: theme.colors.arcadiaBgGrey
        },
        "::-webkit-scrollbar-thumb": {
          borderRadius: "5px",
          backgroundColor: theme.colors.mediumGrey2,
          boxShadow: theme.shadow.mediumShadow
        }
      },
      ref: scrollableRef,
      "data-testid": "data-grid-container-id",
      children: /* @__PURE__ */ jsxs101(Table_default, { width: "fit-content", children: [
        /* @__PURE__ */ jsx190(TableHead_default, { children: /* @__PURE__ */ jsx190(TableRow_default, { sticky: true, children: columns.map((column) => {
          const columnProps = {
            ...DEFAULT_COLUMN_PROPS,
            ...column,
            fontFamily: "Inter Regular"
          };
          return /* @__PURE__ */ jsx190(
            TableTitle_default,
            {
              whiteSpace: columnProps.whiteSpace,
              sticky: true,
              title: columnProps.title,
              width: columnProps.width,
              minWidth: columnProps.minWidth,
              textAlign: columnProps.textAlign,
              padding: columnProps.padding,
              backgroundColor: theme.colors?.tableHeaderBlue,
              typeOfSortingActive: sortBy2.field === columnProps.index && sortBy2.order,
              isFiltered: columnProps.isFiltered,
              isSortable: columnProps.isSortable,
              sortActiveBorderColor: columnProps.sortActiveBorderColor,
              sortActiveArrowColor: columnProps.sortActiveArrowColor,
              separatorColor: columnProps.separatorColor,
              filterActiveColor: columnProps.filterActiveColor,
              handleArrowUpClick: () => handleSortColumn(columnProps.index, "asc"),
              handleArrowDownClick: () => handleSortColumn(columnProps.index, "desc"),
              isFilterable: columnProps.isFilterable,
              fontFamily: columnProps.fontFamily,
              fontSize: columnProps.fontSize,
              color: columnProps.color,
              lineHeight: columnProps.lineHeight,
              handleFilterClick: updateFilteredColumn ? () => updateFilteredColumn(columnProps) : void 0
            },
            columnProps.index
          );
        }) }) }),
        /* @__PURE__ */ jsx190(TableBody_default, { children: rows.map((row, rowIndex) => /* @__PURE__ */ jsx190(
          TableRow_default,
          {
            backgroundColor: rowBackground || theme.colors.white,
            dataTestId: "results-table-row-container-id",
            children: row.cells.map((cell, cellIndex) => {
              return getCell(
                cell,
                cellIndex,
                rowIndex,
                row.cells.length,
                rowBackground
              );
            })
          },
          rowIndex
        )) })
      ] })
    }
  );
};
var DataGrid_default = memo(DataGrid, (prevProps, nextProps) => {
  return isEqual(prevProps.rows, nextProps.rows);
});

// src/components/DataGridSkeleton/index.tsx
import { Skeleton, HStack, VStack, Box as Box27, useTheme as useTheme36 } from "@chakra-ui/react";
import { jsx as jsx191, jsxs as jsxs102 } from "react/jsx-runtime";
var DataGridSkeleton = ({ skeletonColumns }) => {
  const chakraTheme2 = useTheme36();
  return /* @__PURE__ */ jsx191(Box27, { width: "100%", overflow: "auto", children: /* @__PURE__ */ jsx191(HStack, { children: skeletonColumns.map((column, index) => {
    const {
      columnBackgroundColor,
      columnStartColor,
      columnEndColor,
      width,
      padding,
      skeletonHeight,
      numberOfRows,
      rowBackgroundColor,
      rowStartColor,
      rowEndColor,
      borderRadius
    } = column;
    return /* @__PURE__ */ jsxs102(VStack, { children: [
      /* @__PURE__ */ jsx191(
        Box27,
        {
          borderRadius,
          "data-testid": "dataGridSkeleton-column",
          bg: columnBackgroundColor || columnStartColor || chakraTheme2.colors.tableHeaderBlue,
          w: width || "200px",
          p: padding || "5px",
          children: /* @__PURE__ */ jsx191(
            Skeleton,
            {
              height: skeletonHeight || "20px",
              startColor: columnStartColor,
              endColor: columnEndColor
            }
          )
        }
      ),
      [...Array(numberOfRows)].map((row) => /* @__PURE__ */ jsx191(
        Box27,
        {
          "data-testid": "dataGridSkeleton-row-cell",
          bg: rowBackgroundColor || rowStartColor || chakraTheme2.colors.tableHeaderBlue,
          w: width || "200px",
          p: padding || "5px",
          borderRadius,
          children: /* @__PURE__ */ jsx191(
            Skeleton,
            {
              height: skeletonHeight || "20px",
              startColor: rowStartColor,
              endColor: rowEndColor
            }
          )
        },
        `${column}-column-${row}-row`
      ))
    ] }, `${index}-column`);
  }) }) });
};
var DataGridSkeleton_default = DataGridSkeleton;

// src/components/DetailMarker/index.tsx
import { Box as Box28, useTheme as useTheme37 } from "@chakra-ui/react";
import { jsx as jsx192 } from "react/jsx-runtime";
var DetailMarker = ({
  currentCostVsAvg,
  transparent,
  graphic04,
  graphic05,
  graphic07,
  graphic08,
  border,
  ...props
}) => {
  const theme = useTheme37();
  const {
    white,
    darkBlue,
    darkGrey,
    graphicRed,
    lighterGrey,
    graphicGreen,
    backgroundColor
  } = theme.colors;
  return /* @__PURE__ */ jsx192(
    Box28,
    {
      fontFamily: theme.fonts.bold,
      fontSize: "24px",
      lineHeight: "30px",
      display: "flex",
      justifyContent: "center",
      color: currentCostVsAvg === "higherData" ? white : transparent ? darkBlue : darkGrey,
      alignItems: "center",
      position: "static",
      width: "100%",
      height: "40px",
      background: backgroundColor || (currentCostVsAvg ? currentCostVsAvg === "averageData" ? graphicGreen : currentCostVsAvg === "higherData" ? graphicRed : lighterGrey : graphic04 ? theme.colors.graphic04 : graphic05 ? theme.colors.graphic05 : graphic07 ? theme.colors.graphic07 : graphic08 ? theme.colors.graphic08 : theme.colors.graphic04),
      borderRadius: "10px",
      border: transparent ? `1px solid ${darkBlue}` : border,
      "data-testid": "detail-marker",
      ...props
    }
  );
};
var DetailMarker_default = DetailMarker;

// src/components/EmptyFolderError/index.tsx
import { Flex as Flex7, Text as Text7, useTheme as useTheme38 } from "@chakra-ui/react";
import { jsx as jsx193, jsxs as jsxs103 } from "react/jsx-runtime";
var EmptyFolderError = ({ title, subTitle }) => {
  const chakraTheme2 = useTheme38();
  return /* @__PURE__ */ jsxs103(
    Flex7,
    {
      flexDirection: "column",
      alignItems: "center",
      "data-testid": "error-container-id",
      children: [
        /* @__PURE__ */ jsx193(EmptyFolderIcon_default, {}),
        /* @__PURE__ */ jsx193(
          Text7,
          {
            textStyle: "semiBold-20-26",
            color: chakraTheme2.colors.mediumGrey,
            margin: "12px",
            children: title
          }
        ),
        /* @__PURE__ */ jsx193(Text7, { textStyle: "regular-16-22", color: chakraTheme2.colors.mediumGrey, children: subTitle })
      ]
    }
  );
};
var EmptyFolderError_default = EmptyFolderError;

// src/components/Filters/CheckboxFilter/index.tsx
import { useState as useState6, useEffect as useEffect3 } from "react";
import { Box as Box29, Flex as Flex8, Text as Text8, useTheme as useTheme39 } from "@chakra-ui/react";
import _ from "lodash";
import { Fragment as Fragment16, jsx as jsx194, jsxs as jsxs104 } from "react/jsx-runtime";
var MAX_CHECKBOXES_TO_SHOW = 5;
var CheckboxFilter = ({
  filterName,
  filterKey,
  tooltipMessage,
  handleCheckboxInputChange,
  filterObject,
  handleResetFilter,
  handleApplyFilter,
  filtersApplied,
  filtersChecked,
  onSeeAllClick,
  checkboxProps
}) => {
  const theme = useTheme39();
  const quantityOfFiltersApplied = filtersApplied?.length || 0;
  const [resetButtonState, setResetButtonState] = useState6(
    quantityOfFiltersApplied > 0
  );
  const [applyBtnState, setApplyBtnState] = useState6(false);
  const resultKeys = filterObject && Object.keys(filterObject);
  const checkboxsToRender = resultKeys?.slice(0, MAX_CHECKBOXES_TO_SHOW);
  const showSeeAll = resultKeys && resultKeys.length > MAX_CHECKBOXES_TO_SHOW && Boolean(onSeeAllClick);
  const handleApplyClick = () => {
    setApplyBtnState(false);
    const sameFilters = _.isEqual(
      filtersChecked.sort(),
      filtersApplied?.sort()
    );
    if (!sameFilters) {
      setResetButtonState(true);
      if (handleApplyFilter) {
        handleApplyFilter(filterKey);
      }
    }
  };
  const handleReset = () => {
    setResetButtonState(false);
    setApplyBtnState(false);
    if (handleResetFilter) {
      handleResetFilter(filterKey);
    }
  };
  const handleChange = (e) => {
    handleCheckboxInputChange(e, filterKey);
  };
  useEffect3(() => {
    setResetButtonState(quantityOfFiltersApplied !== 0);
  }, [quantityOfFiltersApplied]);
  useEffect3(() => {
    const areFiltersTheSame = !_.isEqual(filtersChecked, filtersApplied);
    setApplyBtnState(areFiltersTheSame);
  }, [filtersChecked, filtersApplied]);
  return /* @__PURE__ */ jsxs104(
    Flex8,
    {
      "data-testid": "checkbox-filter--container",
      width: "100%",
      direction: "column",
      justify: "flex-start",
      children: [
        /* @__PURE__ */ jsxs104(Flex8, { direction: "row", alignItems: "center", width: "100%", marginBottom: "5px", children: [
          /* @__PURE__ */ jsx194(
            Text8,
            {
              textStyle: "semiBold-12-16",
              textTransform: "uppercase",
              margin: "0",
              padding: "0",
              color: theme.colors?.mediumGrey,
              children: filterName
            }
          ),
          tooltipMessage && /* @__PURE__ */ jsx194(Fragment16, { children: /* @__PURE__ */ jsx194(
            Tooltip_default,
            {
              label: tooltipMessage,
              placement: "right",
              dataTestId: "subspecialty-filter-tooltip",
              maxWidth: "inherit",
              children: /* @__PURE__ */ jsx194(
                Flex8,
                {
                  alignItems: "center",
                  justify: "flex-start",
                  height: "14px",
                  width: "14px",
                  cursor: "pointer",
                  marginLeft: "5px",
                  children: /* @__PURE__ */ jsx194(InfoIcon_default, { color: theme.colors?.mediumGrey, variant: "outlined" })
                }
              )
            }
          ) })
        ] }),
        /* @__PURE__ */ jsx194(
          Flex8,
          {
            marginTop: "5px",
            display: "flex",
            direction: "column",
            alignItems: "flex-start",
            children: checkboxsToRender?.map((k) => /* @__PURE__ */ jsxs104(
              Box29,
              {
                position: "relative",
                display: "flex",
                flexDirection: "row",
                alignItems: "flexs-start",
                justifyContent: "flex-start",
                minHeight: "30px",
                children: [
                  /* @__PURE__ */ jsx194(
                    Checkbox_default,
                    {
                      id: `${filterKey}-${k}`,
                      isChecked: filtersChecked.includes(k),
                      name: k,
                      onChange: handleChange,
                      dataTestId: "checkbox-id",
                      marginBottom: 0,
                      textProps: {
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: "180px",
                        textStyle: "regular-14-30",
                        color: theme.colors?.mediumGrey,
                        height: "100%"
                      },
                      ...checkboxProps,
                      children: k
                    }
                  ),
                  filterObject && (filterObject[k] || filterObject[k] === 0) && /* @__PURE__ */ jsx194(
                    Text8,
                    {
                      as: "span",
                      textStyle: "regular-12-16",
                      marginLeft: "5px",
                      lineHeight: "30px",
                      color: theme.colors?.mediumGrey,
                      opacity: "0.5",
                      children: `(${filterObject[k]})`
                    }
                  )
                ]
              },
              `${filterKey}-${k}`
            ))
          }
        ),
        /* @__PURE__ */ jsxs104(
          Flex8,
          {
            width: "100%",
            height: "26px",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: showSeeAll ? "space-between" : "flex-end",
            marginTop: "5px",
            children: [
              showSeeAll && onSeeAllClick && /* @__PURE__ */ jsx194(
                Text8,
                {
                  textStyle: "semiBold-12-16",
                  fontSize: "14px",
                  textTransform: "uppercase",
                  marginLeft: "2px",
                  cursor: "pointer",
                  "data-testid": "checkbox-filters-see-all-link",
                  onClick: () => onSeeAllClick(),
                  color: checkboxProps?.colorScheme ? `${checkboxProps?.colorScheme}.500` : theme.colors?.blueTwo,
                  children: "See all"
                }
              ),
              /* @__PURE__ */ jsxs104(Flex8, { direction: "row", children: [
                resetButtonState && handleResetFilter && /* @__PURE__ */ jsx194(
                  Button_default,
                  {
                    height: "26px",
                    width: "67px",
                    padding: 0,
                    margin: 0,
                    boxSizing: "border-box",
                    cursor: "pointer",
                    backgroundColor: theme.colors?.red,
                    marginRight: "10px",
                    textTransform: "uppercase",
                    fontSize: "12px",
                    lineHeight: "16px",
                    onClick: handleReset,
                    dataTestId: "checkbox-filter-reset-button-id",
                    children: "Reset"
                  }
                ),
                handleApplyFilter && /* @__PURE__ */ jsx194(
                  Button_default,
                  {
                    height: "26px",
                    width: "65px",
                    padding: 0,
                    margin: 0,
                    "box-sizing": "border-box",
                    isDisabled: !applyBtnState,
                    textTransform: "uppercase",
                    fontSize: "12px",
                    lineHeight: "16px",
                    dataTestId: "apply-button",
                    onClick: handleApplyClick,
                    children: "Apply"
                  }
                )
              ] })
            ]
          }
        )
      ]
    }
  );
};
var CheckboxFilter_default = CheckboxFilter;

// src/components/Footer/config.ts
var PRIVACY_POLICY_URL = "https://carejourney.com/privacy-policy/";
var PRIVACY_POLICY_TEXT = "Privacy Policy";
var COPYRIGHT_TEXT = "\xA9 Copyright";
var ALL_RIGHTS_RESERVED_TEXT = "CareJourney - All Rights Reserved";
var ALL_RIGHTS_RESERVED_TEXT_BY_ARCADIA = "CareJourney by Arcadia. All Rights Reserved.";
var SUPPORT_EMAIL = "support@arcadiasolutions.com";
var TERM_OF_USE_TEXT = "Term of Use";
var TERM_OF_USE_LINK = "https://carejourney.com/end-user-agreement/";

// src/components/Footer/index.tsx
import { Box as Box31, useTheme as useTheme41 } from "@chakra-ui/react";

// src/components/Footer/FooterLink.tsx
import { Box as Box30, useTheme as useTheme40 } from "@chakra-ui/react";
import { jsx as jsx195 } from "react/jsx-runtime";
var FooterLink = ({ dataTestId, ...props }) => {
  const theme = useTheme40();
  return /* @__PURE__ */ jsx195(
    Box30,
    {
      as: "a",
      color: theme.colors.arcadiaMainGrey,
      textDecoration: "none",
      _focus: {
        borderColor: theme.colors.blueTwo,
        backgroundColor: theme.colors.white
      },
      _hover: {
        backgroundColor: theme.colors.arcadiaBgGrey
      },
      textStyle: "inter-medium-14-17",
      border: `solid 1px ${theme.colors.lightGrey}`,
      borderRadius: "4px",
      "data-testid": dataTestId,
      p: "6px 16px",
      backgroundColor: theme.colors.white,
      ...props
    }
  );
};
var FooterLink_default = FooterLink;

// src/components/Footer/index.tsx
import { jsx as jsx196, jsxs as jsxs105 } from "react/jsx-runtime";
var Footer = (props) => {
  const theme = useTheme41();
  const { showNewBrand, useArcadiaLogo } = props;
  return /* @__PURE__ */ jsxs105(
    Box31,
    {
      as: "footer",
      display: "flex",
      flexDirection: "row",
      padding: "15px 0",
      marginTop: "auto",
      borderTop: `solid 1px ${theme.colors.lightGrey}`,
      marginLeft: "auto",
      marginRight: "auto",
      justifyContent: "space-between",
      maxWidth: "1140px",
      backgroundColor: theme.colors.arcadiaBgGrey,
      width: "100%",
      alignItems: "center",
      ...props,
      children: [
        /* @__PURE__ */ jsxs105(
          Box31,
          {
            fontFamily: theme.fonts.interRegular,
            width: "unset",
            fontSize: "10px",
            lineHeight: "16px",
            color: theme.colors.arcadiaMainGrey,
            display: "flex",
            alignItems: "center",
            "data-testid": "explorer-footer-carejourney-logo-container",
            children: [
              useArcadiaLogo ? /* @__PURE__ */ jsx196(
                ArcadiaFooterIcon_default,
                {
                  width: 125,
                  height: 28,
                  dataTestId: "arcadia-logo"
                }
              ) : /* @__PURE__ */ jsx196(
                CJIconByArcadia_default,
                {
                  width: 125,
                  height: 28,
                  dataTestId: "arcadia-logo",
                  hideCJLogo: true,
                  color: theme.colors.arcadiaMainGrey
                }
              ),
              /* @__PURE__ */ jsxs105(Box31, { as: "span", marginLeft: "2px", children: [
                `${COPYRIGHT_TEXT} ${(/* @__PURE__ */ new Date()).getFullYear()} ${showNewBrand ? ALL_RIGHTS_RESERVED_TEXT_BY_ARCADIA : ALL_RIGHTS_RESERVED_TEXT}`,
                " ",
                /* @__PURE__ */ jsx196(
                  Box31,
                  {
                    as: "a",
                    color: theme.colors.arcadiaMainGrey,
                    textDecoration: "underline",
                    href: TERM_OF_USE_LINK,
                    target: "_blank",
                    rel: "noreferrer noopener",
                    "data-testid": "term-of-use-footer-button",
                    children: TERM_OF_USE_TEXT
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsxs105(
          Box31,
          {
            width: "unset",
            textAlign: "right",
            fontFamily: theme.fonts.regular,
            fontSize: "12px",
            lineHeight: "16px",
            color: theme.colors.mediumGrey3,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: "15px",
            children: [
              /* @__PURE__ */ jsx196(
                Box31,
                {
                  as: "a",
                  color: theme.colors.arcadiaMainGrey,
                  textDecoration: "underline",
                  _focus: {
                    borderColor: theme.colors.blueTwo,
                    backgroundColor: theme.colors.white
                  },
                  _hover: {
                    backgroundColor: theme.colors.arcadiaBgGrey
                  },
                  textStyle: "inter-medium-14-17",
                  href: "mailto:support@arcadiasolutions.com",
                  target: "_blank",
                  rel: "noreferrer noopener",
                  children: SUPPORT_EMAIL
                }
              ),
              /* @__PURE__ */ jsx196(
                FooterLink_default,
                {
                  target: "_blank",
                  rel: "noreferrer noopener",
                  href: PRIVACY_POLICY_URL,
                  dataTestId: "explorer-footer-privacy-policy-button",
                  children: PRIVACY_POLICY_TEXT
                }
              )
            ]
          }
        )
      ]
    }
  );
};
var Footer_default = Footer;

// src/components/FilterChip/index.tsx
import { Tag, TagCloseButton, TagLabel, Text as Text9 } from "@chakra-ui/react";
import { jsx as jsx197, jsxs as jsxs106 } from "react/jsx-runtime";
var FilterChip = ({
  textContent,
  handleClick,
  backgroundColor,
  color,
  closeColor = "rgba(255, 255, 255, 0.75)",
  backgroundCloseColor = "white"
}) => /* @__PURE__ */ jsxs106(
  Tag,
  {
    size: "md",
    borderRadius: "3px",
    variant: "solid",
    backgroundColor,
    "data-testid": "column-filter-tag-id",
    minHeight: "24px",
    height: "18px",
    padding: "5px 7px",
    boxShadow: "none",
    children: [
      /* @__PURE__ */ jsx197(TagLabel, { display: "flex", children: /* @__PURE__ */ jsx197(
        Text9,
        {
          textStyle: "inter-semiBold-12-14",
          margin: "0 5px 0 7px",
          color,
          userSelect: "none",
          children: textContent
        }
      ) }),
      /* @__PURE__ */ jsx197(
        TagCloseButton,
        {
          width: "14px",
          height: "14px",
          color: closeColor,
          background: backgroundCloseColor,
          opacity: "1",
          onClick: handleClick,
          "data-testid": "tag-close-button",
          margin: "2px 2px 2px auto",
          p: "2"
        }
      )
    ]
  },
  `tag-${textContent}`
);
var FilterChip_default = FilterChip;

// src/components/Glossary/GlossaryWrapper.tsx
import { useEffect as useEffect7 } from "react";
import { Flex as Flex15 } from "@chakra-ui/react";

// src/components/Glossary/context/index.tsx
import { createContext as createContext4, useContext as useContext4, useState as useState7 } from "react";
import { jsx as jsx198 } from "react/jsx-runtime";
var GlossaryContext = createContext4(null);
var GlossaryProvider = ({
  children
}) => {
  const [selectedEntry, setSelectedEntry] = useState7("");
  const [selectedLetter, setSelectedLetter] = useState7("");
  const [searchTerm, setSearchTerm] = useState7("");
  const [nothingFound, setNothingFound] = useState7(false);
  const context = {
    selectedEntry,
    setSelectedEntry,
    selectedLetter,
    setSelectedLetter,
    searchTerm,
    setSearchTerm,
    nothingFound,
    setNothingFound
  };
  return /* @__PURE__ */ jsx198(GlossaryContext.Provider, { value: context, children });
};
function useGlossaryContext() {
  const context = GlossaryContext;
  if (!context) {
    console.error("There is an error with GlossaryContext");
  }
  const ctx = useContext4(context);
  return ctx;
}

// src/components/Glossary/Header/Header.tsx
import { Flex as Flex9, Text as Text10, Box as Box32, useTheme as useTheme42 } from "@chakra-ui/react";

// src/components/Glossary/utils.ts
var formatArrayToStringForCsv = (objArray) => {
  const array = typeof objArray !== "object" ? JSON.parse(objArray) : objArray;
  const str = `${Object.keys(array[0]).map((value) => `"${value}"`).join(",")}\r
`;
  return array.reduce((str2, next) => {
    str2 += `${Object.values(next).map((value) => `"${value}"`).join(",")}\r
`;
    return str2;
  }, str);
};
var generateCsv = (metricData) => {
  const fields = Object.keys(metricData).map((name) => ({
    Metric: name,
    Definition: metricData[name].definition,
    Methodology: metricData[name].methodology?.replace(/<[^>]+>/g, "") || "",
    Source: metricData[name].source?.replace(/<[^>]+>/g, "") || ""
  }));
  const csvFields = formatArrayToStringForCsv(fields);
  const element = document.createElement("a");
  element.href = "data:text/csv;charset=utf-8," + encodeURIComponent(csvFields);
  element.target = "_blank";
  element.download = "glossary_export.csv";
  element.click();
};
var getAvailableLetters = (entries) => {
  return Object.keys(entries).reduce((letters, entry) => {
    if (!letters.includes(entry[0])) letters.push(entry[0]);
    return letters;
  }, []);
};
var search = ({
  term,
  entries,
  fullTextSearch = false
}) => {
  return Object.keys(entries).filter((entry) => {
    const cleanedEntry = entry.toLowerCase();
    if (!fullTextSearch) return cleanedEntry.includes(term);
    const entryObj = entries[entry];
    const entryInText = entryObj.definition.toLowerCase().includes(term);
    return cleanedEntry.includes(term) || entryInText;
  });
};

// src/components/Glossary/Header/Header.tsx
import { jsx as jsx199, jsxs as jsxs107 } from "react/jsx-runtime";
var Header = ({
  title = "glossary",
  dataTestId = "glossary--header",
  onClose,
  glossaryEntries,
  mixpanel
}) => {
  const theme = useTheme42();
  const handleGenerateCSV = (entries) => {
    mixpanel?.track("Export Glossary CSV click rate");
    generateCsv(entries);
  };
  return /* @__PURE__ */ jsxs107(
    Flex9,
    {
      w: "100%",
      justify: "space-between",
      align: "center",
      p: "5px 15px 20px",
      "data-testid": dataTestId,
      children: [
        /* @__PURE__ */ jsx199(
          Text10,
          {
            fontFamily: theme.fonts.regular,
            fontSize: "20px",
            lineHeight: "27px",
            color: theme.colors.mediumGrey,
            "data-testid": "glossary--title",
            children: title
          }
        ),
        /* @__PURE__ */ jsxs107(Flex9, { columnGap: "32px", children: [
          /* @__PURE__ */ jsx199(
            Text10,
            {
              textStyle: "semiBold-16-22",
              color: theme.colors?.blueOne,
              cursor: "pointer",
              "data-testid": "glossary--export",
              onClick: () => handleGenerateCSV(glossaryEntries),
              children: "Export Glossary"
            }
          ),
          /* @__PURE__ */ jsx199(Box32, { cursor: "pointer", "data-testid": "glossary--close", children: /* @__PURE__ */ jsx199(
            CloseIcon_default,
            {
              width: 22.5,
              height: 22.5,
              handleClick: () => {
                if (onClose) onClose();
              }
            }
          ) })
        ] })
      ]
    }
  );
};
var Header_default = Header;

// src/components/Glossary/SearchBar/SearchBar.tsx
import { useState as useState8, useEffect as useEffect5, useRef as useRef5 } from "react";
import {
  Flex as Flex10,
  Box as Box33,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useTheme as useTheme43
} from "@chakra-ui/react";

// src/hooks/useClickAway/index.ts
import { useEffect as useEffect4 } from "react";
var useClickAway = (ref, handler) => {
  useEffect4(() => {
    const listener = (event) => {
      const el = ref?.current;
      if (!el || el.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener("mousedown", listener);
    document.addEventListener("touchstart", listener);
    return () => {
      document.removeEventListener("mousedown", listener);
      document.removeEventListener("touchstart", listener);
    };
  }, [ref, handler]);
};

// src/components/Glossary/SearchBar/SearchBar.tsx
import { Fragment as Fragment17, jsx as jsx200, jsxs as jsxs108 } from "react/jsx-runtime";
var SearchBar = ({
  glossaryEntries,
  dataTestId = "glossary--search"
}) => {
  const theme = useTheme43();
  const {
    selectedLetter,
    searchTerm,
    setSearchTerm,
    setSelectedLetter,
    setNothingFound
  } = useGlossaryContext();
  const [partialSearchTerm, setPartialSearchTerm] = useState8("");
  const [searchSuggestions, setSearchSuggestions] = useState8([]);
  const [showSuggestions, setShowSuggestions] = useState8(false);
  const suggestionContainerRef = useRef5();
  const validSearchTerm = (str) => str.trim().length !== 0;
  const partialSearchTermChanged = (e) => {
    setPartialSearchTerm(e.target.value);
    setShowSuggestions(Boolean(e.target.value));
  };
  const searchTermChanged = () => {
    if (!validSearchTerm(partialSearchTerm)) return;
    const term = partialSearchTerm.toLowerCase();
    const matches = search({
      term,
      entries: glossaryEntries,
      fullTextSearch: true
    });
    setNothingFound(matches.length === 0);
    setSearchTerm(partialSearchTerm);
    setSelectedLetter("");
    setShowSuggestions(false);
  };
  const resetSearch = () => {
    setPartialSearchTerm("");
    setSearchTerm("");
    setNothingFound(false);
  };
  const setSelectedSuggestion = (suggestion) => {
    setShowSuggestions(false);
    setPartialSearchTerm(suggestion);
  };
  useEffect5(() => {
    if (partialSearchTerm !== "") {
      const term = partialSearchTerm.toLowerCase();
      const matches = search({ term, entries: glossaryEntries });
      setSearchSuggestions(matches);
      if (matches.length === 0) setShowSuggestions(false);
    }
  }, [partialSearchTerm]);
  useEffect5(() => {
    if (selectedLetter) resetSearch();
  }, [selectedLetter]);
  useClickAway(suggestionContainerRef, () => {
    setShowSuggestions(false);
  });
  return /* @__PURE__ */ jsx200(Fragment17, { children: /* @__PURE__ */ jsxs108(Flex10, { align: "center", columnGap: "10px", "data-testid": dataTestId, children: [
    /* @__PURE__ */ jsxs108(
      InputGroup,
      {
        w: "859px",
        border: `1px solid ${theme.colors?.darkBlue}`,
        borderRadius: "6px",
        children: [
          /* @__PURE__ */ jsx200(InputLeftElement, { pointerEvents: "none", children: /* @__PURE__ */ jsx200(SearchIcon_default, { width: 14, height: 14, margin: "0px 10px 0px 10px" }) }),
          /* @__PURE__ */ jsx200(
            Input,
            {
              onKeyUp: (e) => {
                if (e.key === "Enter" && validSearchTerm(partialSearchTerm)) {
                  searchTermChanged();
                }
              },
              value: partialSearchTerm,
              onChange: partialSearchTermChanged,
              "data-testid": "glossary--search-input",
              type: "text",
              placeholder: "Search for keywords or metrics",
              h: "42px",
              fontFamily: theme.fonts.regular,
              fontSize: "14px",
              lineHeight: "19px",
              border: "none",
              borderRadius: "6px",
              outline: "none",
              focusBorderColor: "none",
              color: theme.colors.black,
              _placeholder: {
                color: theme.colors.lightGrey,
                fontFamily: theme.fonts.italic
              }
            }
          ),
          searchTerm && /* @__PURE__ */ jsx200(
            InputRightElement,
            {
              onClick: resetSearch,
              color: theme.colors?.black,
              children: /* @__PURE__ */ jsx200(
                Flex10,
                {
                  h: "14px",
                  w: "14px",
                  backgroundColor: "rgba(88, 88, 90, 0.75)",
                  borderRadius: "50%",
                  justifyContent: "center",
                  alignItems: "center",
                  children: /* @__PURE__ */ jsx200(
                    CloseIcon_default,
                    {
                      height: 7.5,
                      width: 7.5,
                      color: theme.colors?.white
                    }
                  )
                }
              )
            }
          ),
          showSuggestions && /* @__PURE__ */ jsx200(
            Box33,
            {
              "data-testid": "glossary--search-suggestions",
              ref: suggestionContainerRef,
              w: "859px",
              maxHeight: "400px",
              position: "absolute",
              zIndex: "1",
              top: "41px",
              left: "-1px",
              overflowY: "scroll",
              p: "0px 5px 0px 5px",
              backgroundColor: theme.colors.white,
              border: "1px solid",
              borderColor: theme.colors?.darkBlue,
              borderRadius: "0px 0px 6px 6px",
              boxSizing: "border-box",
              boxShadow: theme.shadow.mediumShadow,
              css: {
                scrollbarWidth: "none",
                // firefox
                "-ms-overflow-style": "none",
                // ie 10
                "&::-webkit-scrollbar": {
                  display: "none"
                }
              },
              children: searchSuggestions.map((match, i) => /* @__PURE__ */ jsx200(
                Flex10,
                {
                  "data-testid": "glossary--suggestion-item",
                  h: "29px",
                  align: "center",
                  pl: "20px",
                  color: theme.colors.black,
                  backgroundColor: theme.colors.white,
                  fontSize: "14px",
                  borderRadius: "0px 0px 6px 6px",
                  _hover: { backgroundColor: theme.colors.grey9 },
                  onClick: () => setSelectedSuggestion(match),
                  children: match
                },
                i
              ))
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsx200(
      Button_default,
      {
        width: "159px",
        height: "42px",
        m: "0",
        p: "0",
        fontSize: "16px",
        lineHeight: "22px",
        "data-testid": "glossary--search-button",
        isDisabled: !validSearchTerm(partialSearchTerm),
        onClick: searchTermChanged,
        children: "Search"
      }
    )
  ] }) });
};
var SearchBar_default = SearchBar;

// src/components/Glossary/LetterSelector/LetterSelector.tsx
import { Flex as Flex11, Link, useTheme as useTheme44 } from "@chakra-ui/react";

// src/components/Glossary/config.ts
var alphabet = Array.from(Array(26)).map((_3, i) => i + 65).map((x) => String.fromCharCode(x).toUpperCase());

// src/components/Glossary/LetterSelector/LetterSelector.tsx
import { jsx as jsx201 } from "react/jsx-runtime";
var LetterSelector = ({
  availableLetters,
  dataTestId = "glossary--letter-selector"
}) => {
  const theme = useTheme44();
  const { selectedLetter, setSelectedLetter } = useGlossaryContext();
  const getLetterColor = (letter) => {
    if (letter === selectedLetter) {
      return theme.colors?.blueTwo;
    } else if (availableLetters.includes(letter)) {
      return theme.colors.mediumGrey;
    } else {
      return theme.colors.lightGrey;
    }
  };
  const onLetterSelected = (letter) => {
    setSelectedLetter(letter);
  };
  return /* @__PURE__ */ jsx201(
    Flex11,
    {
      "data-testid": dataTestId,
      h: "36px",
      mt: "15px",
      align: "center",
      gap: "30.12px",
      children: alphabet.map((letter) => {
        return /* @__PURE__ */ jsx201(
          Link,
          {
            "data-testid": `glossary--letter-${letter}`,
            fontFamily: theme.fonts.normal,
            fontSize: selectedLetter === letter ? "26px" : "14px",
            color: getLetterColor(letter),
            cursor: availableLetters.includes(letter) ? "pointer" : "",
            onClick: () => availableLetters.includes(letter) ? onLetterSelected(letter) : "",
            _hover: { textDecoration: "none" },
            children: letter
          },
          letter
        );
      })
    }
  );
};
var LetterSelector_default = LetterSelector;

// src/components/Glossary/EntriesContainer/EntriesContainer.tsx
import { Flex as Flex14 } from "@chakra-ui/react";

// src/components/Glossary/EntriesContainer/EntrySelector/EntrySelector.tsx
import { useRef as useRef6, useState as useState9, useEffect as useEffect6 } from "react";
import { Flex as Flex12, useTheme as useTheme45 } from "@chakra-ui/react";
import { jsx as jsx202 } from "react/jsx-runtime";
var EntrySelector = ({
  glossaryEntries,
  dataTestId = "glossary--entry-selector"
}) => {
  const {
    selectedEntry,
    setSelectedEntry,
    selectedLetter,
    searchTerm,
    nothingFound
  } = useGlossaryContext();
  const letterAnchorRef = useRef6([]);
  const [entriesShowed, setEntriesShowed] = useState9(
    Object.keys(glossaryEntries)
  );
  const theme = useTheme45();
  const prevSearchTermRef = useRef6();
  useEffect6(() => {
    const text = letterAnchorRef.current[selectedLetter]?.textContent;
    if (letterAnchorRef.current[selectedLetter] && selectedEntry === text) {
      letterAnchorRef.current[selectedLetter].scrollIntoView({
        block: "start",
        inline: "nearest",
        behavior: "smooth"
      });
    }
  });
  useEffect6(() => {
    if (selectedLetter !== "") {
      const el = Object.keys(glossaryEntries).find(
        (entry) => entry[0] === selectedLetter
      );
      setSelectedEntry(el);
    }
  }, [selectedLetter]);
  useEffect6(() => {
    if (searchTerm !== "") {
      const term = searchTerm.toLowerCase();
      const matches = search({
        term,
        entries: glossaryEntries,
        fullTextSearch: true
      });
      setEntriesShowed(
        matches.length !== 0 ? matches : Object.keys(glossaryEntries)
      );
    } else {
      setEntriesShowed(Object.keys(glossaryEntries));
    }
    if (prevSearchTermRef.current?.length !== 0 && searchTerm.length === 0) {
      Object.keys(letterAnchorRef.current).forEach((letter) => {
        letterAnchorRef.current[letter] = null;
      });
    }
    prevSearchTermRef.current = searchTerm;
    if (nothingFound) setSelectedEntry(null);
  }, [searchTerm]);
  useEffect6(() => {
    if (entriesShowed[0][0] === selectedLetter || selectedLetter === "" && !nothingFound) {
      setSelectedEntry(entriesShowed[0]);
    }
  }, [entriesShowed]);
  const handleRef = (node, entry) => {
    const letter = entry[0];
    if (!letterAnchorRef.current[letter]) {
      letterAnchorRef.current[letter] = node;
    }
  };
  return /* @__PURE__ */ jsx202(
    Flex12,
    {
      "data-testid": dataTestId,
      w: "290px",
      h: "398px",
      overflowY: "scroll",
      overflowX: "hidden",
      flexDir: "column",
      rowGap: "10px",
      css: {
        "&::-webkit-scrollbar": {
          backgroundColor: "rgba(0, 0, 0, 0.0)",
          width: "8px"
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.colors.lighterGrey,
          borderRadius: "5px"
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "rgba(230, 230, 238, 0)"
        }
      },
      children: entriesShowed.map((entry, i) => {
        return /* @__PURE__ */ jsx202(
          Flex12,
          {
            "data-testid": `glossary--entry-${entry.replaceAll(" ", "_").toLowerCase()}`,
            w: "264px",
            minHeight: "60px",
            align: "center",
            p: "0 10px",
            textTransform: "uppercase",
            fontSize: "14px",
            fontFamily: entry === selectedEntry ? theme.fonts.semiBold : theme.fonts.regular,
            backgroundColor: entry === selectedEntry ? theme.colors?.explorerTableHeaderColor : theme.colors.white,
            cursor: entry === selectedEntry ? "default" : "pointer",
            borderLeft: entry === selectedEntry ? `5px solid ${theme.colors?.blueThree}` : "none",
            color: theme.colors?.blueOne,
            onClick: () => setSelectedEntry(entry),
            ref: (el) => handleRef(el, entry),
            children: entry
          },
          i
        );
      })
    }
  );
};
var EntrySelector_default = EntrySelector;

// src/components/Glossary/EntriesContainer/EntryData/EntryData.tsx
import { Box as Box35, Text as Text12, useTheme as useTheme47, chakra } from "@chakra-ui/react";

// src/components/Glossary/EntriesContainer/EntryData/components.tsx
import { Flex as Flex13, Box as Box34, Text as Text11, useTheme as useTheme46 } from "@chakra-ui/react";
import { jsx as jsx203, jsxs as jsxs109 } from "react/jsx-runtime";
var DataSubtitle = ({
  subtitle,
  theme,
  dataTestId = `glossary--entry-data-subtitle-${subtitle}`
}) => {
  return /* @__PURE__ */ jsx203(
    Text11,
    {
      "data-testid": dataTestId,
      textStyle: "light-14-19",
      textTransform: "capitalize",
      color: theme.colors?.darkBlue,
      children: subtitle
    }
  );
};
var DataInfo = ({
  text,
  theme,
  dataTestId = "glossary--entry-data-info"
}) => /* @__PURE__ */ jsx203(
  Box34,
  {
    "data-testid": dataTestId,
    textStyle: "light-14-19",
    mb: "30px",
    color: theme.colors.mediumGrey,
    whiteSpace: "pre-line",
    dangerouslySetInnerHTML: { __html: text }
  }
);
var NotFound = ({ term }) => {
  const theme = useTheme46();
  return /* @__PURE__ */ jsxs109(
    Flex13,
    {
      "data-testid": "glossary--nothing-found-container",
      w: "430px",
      m: "0 auto",
      flexDirection: "column",
      align: "center",
      children: [
        /* @__PURE__ */ jsx203(EmptyStateIcon_default, { variant: "withShadow" }),
        /* @__PURE__ */ jsxs109(
          Text11,
          {
            w: "100%",
            textStyle: "regular-14-20",
            textAlign: "center",
            color: theme.colors?.mediumGrey,
            mt: "10px",
            noOfLines: 10,
            children: [
              "0 results found for your search ",
              `"${term}"`,
              "."
            ]
          }
        ),
        /* @__PURE__ */ jsx203(
          Text11,
          {
            textStyle: "regular-14-20",
            textAlign: "center",
            color: theme.colors?.mediumGrey,
            mt: "10px",
            children: "Check your spelling and try again, or select a metric from the list."
          }
        )
      ]
    }
  );
};

// src/components/Glossary/EntriesContainer/EntryData/EntryData.tsx
import { jsx as jsx204, jsxs as jsxs110 } from "react/jsx-runtime";
var EntryData = ({
  glossaryEntries,
  dataTestId = "glossary--entry-data"
}) => {
  const theme = useTheme47();
  const { selectedEntry, nothingFound, searchTerm } = useGlossaryContext();
  if (nothingFound) return /* @__PURE__ */ jsx204(NotFound, { term: searchTerm });
  const getHighlightedText = (text) => {
    const term = searchTerm.toLowerCase();
    if (term) {
      const regexp = new RegExp(
        `(${term.replace(/[/.*+?^${}()|[\]\\]/g, "\\$&")})`,
        "gi"
      );
      const parts = text.split(regexp);
      return /* @__PURE__ */ jsx204("span", { children: parts?.map((part, i) => {
        const doHigh = part?.toLowerCase() === term;
        return /* @__PURE__ */ jsx204(
          chakra.span,
          {
            color: doHigh ? theme.colors?.darkBlue : theme.colors?.mediumGrey,
            fontWeight: doHigh ? "bold" : "regular",
            "data-testId": doHigh ? "glossary--entry-data--highlighted-text" : "glossary--entry-data--text",
            children: part
          },
          i
        );
      }) });
    } else {
      return text;
    }
  };
  return /* @__PURE__ */ jsxs110(Box35, { "data-testid": dataTestId, w: "773px", h: "398px", children: [
    /* @__PURE__ */ jsx204(
      Text12,
      {
        "data-testid": "glossary--entry-data--title",
        fontSize: "20px",
        lineHeight: "27px",
        color: theme.colors?.darkBlue,
        fontFamily: theme.fonts.light,
        children: selectedEntry && selectedEntry
      }
    ),
    /* @__PURE__ */ jsx204(
      Text12,
      {
        "data-testid": "glossary--entry-data--definition",
        whiteSpace: "pre-line",
        fontFamily: theme.fonts.light,
        fontSize: "14px",
        lineHeight: "19px",
        color: theme.colors?.mediumGrey,
        mb: "30px",
        children: selectedEntry && getHighlightedText(glossaryEntries[selectedEntry]?.definition)
      }
    ),
    selectedEntry && Object.keys(glossaryEntries[selectedEntry]).filter((field) => field !== "definition").map((field, i) => {
      return /* @__PURE__ */ jsxs110(Box35, { "data-testid": "glossary--entry-data--extrainfo", children: [
        /* @__PURE__ */ jsx204(
          DataSubtitle,
          {
            subtitle: field,
            theme
          },
          `subtitile-${i}`
        ),
        /* @__PURE__ */ jsx204(
          DataInfo,
          {
            text: glossaryEntries[selectedEntry][field],
            theme
          },
          `info-${i}`
        )
      ] }, i);
    })
  ] });
};
var EntryData_default = EntryData;

// src/components/Glossary/EntriesContainer/EntriesContainer.tsx
import { jsx as jsx205, jsxs as jsxs111 } from "react/jsx-runtime";
var EntriesContainer = ({
  glossaryEntries,
  dataTestId = "glossary--entries-container"
}) => {
  return /* @__PURE__ */ jsxs111(
    Flex14,
    {
      "data-testid": dataTestId,
      h: "max-content",
      w: "100%",
      flexDir: "row",
      align: "flex-start",
      mt: "40px",
      p: "0px 15px",
      columnGap: "18px",
      css: {
        scrollbarWidth: "none",
        // firefox
        "-ms-overflow-style": "none",
        // ie 10
        "&::-webkit-scrollbar": {
          width: 0,
          height: 0
        }
      },
      children: [
        /* @__PURE__ */ jsx205(EntrySelector_default, { glossaryEntries }),
        /* @__PURE__ */ jsx205(EntryData_default, { glossaryEntries })
      ]
    }
  );
};
var EntriesContainer_default = EntriesContainer;

// src/components/Glossary/GlossaryWrapper.tsx
import { jsx as jsx206, jsxs as jsxs112 } from "react/jsx-runtime";
var GlossaryWrapper = ({
  dataTestId,
  title,
  glossaryEntries,
  onClose,
  mixpanel
}) => {
  const { selectedEntry, searchTerm } = useGlossaryContext();
  useEffect7(() => {
    mixpanel?.init("glossary");
  }, []);
  useEffect7(() => {
    mixpanel?.track("Glossary Metric selected", {
      metric: selectedEntry
    });
  }, [selectedEntry]);
  useEffect7(() => {
    mixpanel?.track("Glossary Search Term", {
      term: searchTerm
    });
  }, [searchTerm]);
  return /* @__PURE__ */ jsxs112(
    Flex15,
    {
      "data-testid": dataTestId,
      w: "1084px",
      maxHeight: "666px",
      flexDirection: "column",
      align: "center",
      justify: "flex-start",
      bgColor: "white",
      p: "15px",
      children: [
        /* @__PURE__ */ jsx206(
          Header_default,
          {
            title,
            glossaryEntries,
            onClose,
            mixpanel
          }
        ),
        /* @__PURE__ */ jsx206(SearchBar_default, { glossaryEntries }),
        /* @__PURE__ */ jsx206(LetterSelector_default, { availableLetters: getAvailableLetters(glossaryEntries) }),
        /* @__PURE__ */ jsx206(EntriesContainer_default, { glossaryEntries })
      ]
    }
  );
};
var GlossaryWrapper_default = GlossaryWrapper;

// src/components/Glossary/Glossary.tsx
import { jsx as jsx207 } from "react/jsx-runtime";
var Glossary = ({
  dataTestId = "glossary--container",
  title = "Glossary",
  glossaryEntries,
  onClose,
  mixpanel
}) => /* @__PURE__ */ jsx207(GlossaryProvider, { children: /* @__PURE__ */ jsx207(
  GlossaryWrapper_default,
  {
    dataTestId,
    title,
    glossaryEntries,
    onClose,
    mixpanel
  }
) });
var Glossary_default = Glossary;

// src/components/LoadingOverlay/index.tsx
import { Box as Box36, Flex as Flex16, Text as Text13, useTheme as useTheme48 } from "@chakra-ui/react";

// src/components/LoadingOverlay/config.ts
var defaultText = "We are loading the information. Please wait a few seconds";

// src/components/LoadingOverlay/index.tsx
import { jsx as jsx208, jsxs as jsxs113 } from "react/jsx-runtime";
var LoadingOverlay = ({ text = defaultText, useArcadiaLogo = false }) => {
  const chakraTheme2 = useTheme48();
  return /* @__PURE__ */ jsx208(
    Box36,
    {
      position: "fixed",
      width: "100%",
      height: "100%",
      top: "0",
      left: "0",
      right: "0",
      bottom: "0",
      backgroundColor: "rgba(0,0,0,0.5)",
      zIndex: "10",
      "data-testid": "loading-overlay",
      children: /* @__PURE__ */ jsx208(Flex16, { alignItems: "center", height: "100%", justifyContent: "center", children: /* @__PURE__ */ jsxs113(Box36, { children: [
        useArcadiaLogo ? /* @__PURE__ */ jsx208(ArcadiaLoadingIcon_default, { margin: "auto", width: 80, height: 80 }) : /* @__PURE__ */ jsx208(CJLoadingIcon_default, { margin: "auto", width: 80, height: 80 }),
        /* @__PURE__ */ jsx208(Text13, { textStyle: "normal-14-19", color: chakraTheme2.colors.white, children: text })
      ] }) })
    }
  );
};
var LoadingOverlay_default = LoadingOverlay;

// src/components/MapComponent/MapComponent.tsx
import { Map } from "react-leaflet";
import "leaflet/dist/leaflet.css";

// src/components/MapComponent/MapLayers/MapLayers.tsx
import { useEffect as useEffect9 } from "react";
import { GeoJSON, useLeaflet as useLeaflet2 } from "react-leaflet";
import ReactDOMServer2 from "react-dom/server";

// src/components/MapComponent/MapLayers/LayersData/states.json
var states_default = {
  type: "FeatureCollection",
  features: [
    {
      type: "Feature",
      id: "01",
      properties: {
        name: "Alabama",
        postal: "AL",
        density: 94.65
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-87.359296, 35.00118],
            [-85.606675, 34.984749],
            [-85.431413, 34.124869],
            [-85.184951, 32.859696],
            [-85.069935, 32.580372],
            [-84.960397, 32.421541],
            [-85.004212, 32.322956],
            [-84.889196, 32.262709],
            [-85.058981, 32.13674],
            [-85.053504, 32.01077],
            [-85.141136, 31.840985],
            [-85.042551, 31.539753],
            [-85.113751, 31.27686],
            [-85.004212, 31.003013],
            [-85.497137, 30.997536],
            [-87.600282, 30.997536],
            [-87.633143, 30.86609],
            [-87.408589, 30.674397],
            [-87.446927, 30.510088],
            [-87.37025, 30.427934],
            [-87.518128, 30.280057],
            [-87.655051, 30.247195],
            [-87.90699, 30.411504],
            [-87.934375, 30.657966],
            [-88.011052, 30.685351],
            [-88.10416, 30.499135],
            [-88.137022, 30.318396],
            [-88.394438, 30.367688],
            [-88.471115, 31.895754],
            [-88.241084, 33.796253],
            [-88.098683, 34.891641],
            [-88.202745, 34.995703],
            [-87.359296, 35.00118]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "02",
      properties: {
        name: "Alaska",
        postal: "AK",
        density: 1.264,
        tooltipLatLng: {
          lat: 21.12474693219995,
          lng: -110.49121093750001
        },
        popupLatLng: { lat: 21.12474693219995, lng: -110.49121093750001 }
      },
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          [
            [
              [-91.60202100000001, 11.117981999999998],
              [-91.56915900000001, 11.282290000000003],
              [-91.355558, 11.183705000000003],
              [-91.38842, 11.013919999999999],
              [-91.645836, 11.035826999999998],
              [-91.60202100000001, 11.117981999999998]
            ],
            [
              [-91.832052, 11.424689999999998],
              [-91.645836, 11.304197000000002],
              [-91.749898, 11.128934999999998],
              [-91.832052, 11.189182000000002],
              [-91.832052, 11.424689999999998]
            ],
            [
              [-92.976733, 12.437924000000002],
              [-92.735747, 12.459831999999999],
              [-92.631685, 12.421492999999998],
              [-92.664547, 12.273615999999997],
              [-92.87814800000001, 12.240754000000003],
              [-93.069841, 12.333862000000003],
              [-92.976733, 12.437924000000002]
            ],
            [
              [-93.59562700000001, 12.350293],
              [-93.162949, 12.317431],
              [-93.05341000000001, 12.125739000000003],
              [-92.620732, 11.912137999999999],
              [-92.47285400000001, 11.780690999999997],
              [-92.46190000000001, 11.671152],
              [-92.35783799999999, 11.649245],
              [-92.341408, 11.506844000000001],
              [-92.166146, 11.364443999999999],
              [-92.144238, 11.238473999999997],
              [-92.029222, 11.276812999999997],
              [-91.97993, 11.178227999999997],
              [-91.958022, 10.789364999999997],
              [-92.029222, 10.701734000000002],
              [-92.308546, 10.718164999999999],
              [-92.385223, 10.915334999999999],
              [-92.48380800000001, 10.898904000000002],
              [-92.686455, 11.046781000000003],
              [-92.746701, 10.997489000000002],
              [-92.91648599999999, 11.046781000000003],
              [-92.88910200000001, 10.898904000000002],
              [-92.73026999999999, 10.937241999999998],
              [-92.62620899999999, 10.882472999999997],
              [-92.675501, 10.679825999999998],
              [-92.86719400000001, 10.701734000000002],
              [-93.15747200000001, 10.959150000000001],
              [-93.23962599999999, 11.090597000000002],
              [-93.223195, 11.227519999999998],
              [-93.453227, 11.216566],
              [-93.453227, 11.320628],
              [-93.277964, 11.331581999999997],
              [-93.102702, 11.424689999999998],
              [-93.17938000000001, 11.588997999999997],
              [-93.38750300000001, 11.621859999999998],
              [-93.420365, 11.884753000000003],
              [-93.497042, 12.016199999999998],
              [-93.639442, 11.923091999999997],
              [-93.694212, 12.070968999999998],
              [-93.546335, 12.142169000000003],
              [-93.66682700000001, 12.311954999999998],
              [-93.59562700000001, 12.350293]
            ],
            [
              [-93.73802699999999, 11.556137],
              [-93.546335, 11.490412999999997],
              [-93.41488799999999, 11.572567999999997],
              [-93.28344100000001, 11.534229000000003],
              [-93.420365, 11.386352000000002],
              [-93.63396599999999, 11.430166999999997],
              [-93.73802699999999, 11.556137]
            ],
            [
              [-93.907813, 12.930849000000002],
              [-94.05021300000001, 13.029434000000002],
              [-93.88590500000001, 13.095157],
              [-93.34368799999999, 13.002049],
              [-93.102702, 13.007525999999999],
              [-92.932917, 12.821309999999997],
              [-92.620732, 12.667955999999997],
              [-92.653593, 12.55294],
              [-92.817901, 12.492694],
              [-93.04245599999999, 12.520077999999998],
              [-93.20128700000001, 12.448878],
              [-93.420365, 12.492694],
              [-93.66135, 12.448878],
              [-93.710643, 12.684386000000003],
              [-93.68873500000001, 12.837741000000001],
              [-93.869474, 12.843218],
              [-93.907813, 12.930849000000002]
            ],
            [
              [-94.115936, 12.481740000000002],
              [-94.25286, 12.558416999999999],
              [-94.40073699999999, 12.722724999999997],
              [-94.417168, 12.848695],
              [-94.296675, 12.908940999999999],
              [-94.170706, 12.848695],
              [-94.14332099999999, 12.952756999999998],
              [-93.74898099999999, 12.772016999999998],
              [-93.710643, 12.596755000000002],
              [-93.847566, 12.574848000000003],
              [-93.93519699999999, 12.377678000000003],
              [-93.836612, 12.322907999999998],
              [-93.95710500000001, 12.092877000000001],
              [-94.11045899999999, 12.142169000000003],
              [-94.13236699999999, 11.999769],
              [-94.230952, 12.070968999999998],
              [-94.29119800000001, 12.350293],
              [-94.115936, 12.481740000000002]
            ],
            [
              [-94.636246, 12.284570000000002],
              [-94.669107, 12.169553999999998],
              [-94.80603099999999, 12.235277000000004],
              [-95.178463, 12.678910000000002],
              [-95.413971, 12.810355999999999],
              [-95.331817, 12.914417999999998],
              [-95.424925, 13.166356999999998],
              [-95.687818, 13.369003999999997],
              [-95.41944799999999, 13.566173999999997],
              [-95.298955, 13.484020000000001],
              [-95.063447, 13.418295999999998],
              [-94.84984600000001, 13.407342999999997],
              [-94.844369, 13.248511],
              [-94.636246, 12.728202000000003],
              [-94.636246, 12.284570000000002]
            ],
            [
              [-94.71292299999999, 14.223407000000002],
              [-94.37335300000001, 14.146729999999998],
              [-94.17618300000001, 14.157682999999999],
              [-94.187137, 14.081006000000002],
              [-93.90233599999999, 13.807158999999999],
              [-94.099505, 13.850974999999998],
              [-94.148798, 13.757866999999997],
              [-93.93519699999999, 13.615465999999998],
              [-93.869474, 13.363526999999998],
              [-94.08307500000001, 13.297804],
              [-94.15427500000001, 13.210172999999998],
              [-94.499322, 13.029434000000002],
              [-94.603384, 13.034911000000001],
              [-94.6472, 13.226604000000002],
              [-94.575999, 13.341619000000001],
              [-94.60886099999999, 13.511403999999999],
              [-94.729354, 13.719527999999997],
              [-94.707446, 13.829067000000002],
              [-94.784123, 14.097437],
              [-94.91557, 14.212452999999996],
              [-94.95390800000001, 14.409623000000003],
              [-94.71292299999999, 14.223407000000002]
            ],
            [
              [-95.85760300000001, 13.330665000000003],
              [-95.715203, 13.330665000000003],
              [-95.56732600000001, 13.149926],
              [-95.633049, 13.023957000000003],
              [-95.85760300000001, 12.996572],
              [-95.82474199999999, 13.193742],
              [-95.85760300000001, 13.330665000000003]
            ],
            [
              [-96.27932799999999, 14.206975999999997],
              [-95.978096, 14.201498999999998],
              [-95.780926, 14.28913],
              [-95.496125, 14.168636999999997],
              [-95.64948000000001, 14.037191],
              [-95.59470999999999, 13.987898000000001],
              [-95.45231000000001, 14.135776],
              [-95.10726299999999, 14.086483000000001],
              [-94.91557, 13.976944000000003],
              [-95.02510799999999, 13.779775],
              [-94.937477, 13.763343999999996],
              [-94.822462, 13.500450999999998],
              [-95.08535499999999, 13.462111999999998],
              [-95.572802, 13.675713000000002],
              [-95.55637200000001, 13.456634999999999],
              [-95.70972599999999, 13.369003999999997],
              [-95.890465, 13.407342999999997],
              [-96.00000399999999, 13.544266],
              [-96.20812799999999, 13.637374000000001],
              [-96.36695900000001, 13.829067000000002],
              [-96.569606, 13.916697999999997],
              [-96.558652, 14.075529000000003],
              [-96.421728, 14.130299],
              [-96.377913, 14.267221999999997],
              [-96.27932799999999, 14.206975999999997]
            ],
            [
              [-107.07985400000001, 16.200581999999997],
              [-107.50157899999999, 15.948642999999997],
              [-107.53443999999999, 15.850057999999997],
              [-107.874011, 15.784334999999999],
              [-107.80281, 15.937688999999999],
              [-107.435855, 16.096519999999998],
              [-107.205824, 16.271782],
              [-107.07985400000001, 16.200581999999997]
            ],
            [
              [-107.561825, 16.578491],
              [-107.61659399999999, 16.370367],
              [-107.758995, 16.156767000000002],
              [-107.956165, 16.227967],
              [-107.791856, 16.474429],
              [-107.561825, 16.578491]
            ],
            [
              [-107.78637900000001, 26.245290999999995],
              [-107.68231800000001, 26.201475000000002],
              [-107.16200799999999, 26.157660000000007],
              [-106.888161, 26.185044000000005],
              [-106.51025200000001, 26.185044000000005],
              [-106.099482, 26.146705999999995],
              [-105.858496, 26.168614000000005],
              [-105.62298799999999, 26.086460000000002],
              [-105.195787, 25.993352],
              [-104.62070800000001, 25.971444000000005],
              [-104.46187699999999, 26.026213],
              [-104.07849100000001, 26.059075000000007],
              [-103.91418300000001, 26.130274999999997],
              [-103.49793500000001, 26.141228999999996],
              [-103.503412, 26.091936000000004],
              [-103.25694999999999, 26.119321],
              [-102.74759399999999, 26.042643999999996],
              [-102.402547, 25.916674],
              [-102.079408, 25.856427999999994],
              [-102.008207, 25.801659],
              [-101.71245300000001, 25.790705000000003],
              [-101.43312900000001, 25.697597000000002],
              [-101.37835899999999, 25.637349999999998],
              [-101.208574, 25.686643000000004],
              [-101.00045, 25.648303999999996],
              [-101.00045, 16.304644000000003],
              [-100.53491, 16.22249],
              [-100.47466399999999, 16.310121000000002],
              [-99.98721599999999, 16.184151],
              [-99.69693899999999, 16.342982999999997],
              [-99.088998, 16.359413000000004],
              [-99.19853699999999, 16.091043],
              [-99.04518300000001, 15.997934999999998],
              [-98.70013499999999, 15.910303999999996],
              [-98.623458, 15.767904000000001],
              [-97.604747, 15.242117999999998],
              [-97.44591600000001, 14.908023999999997],
              [-97.265177, 15.001131999999998],
              [-96.827022, 15.159962999999998],
              [-96.580559, 15.165439999999997],
              [-96.465544, 15.285933],
              [-96.47649799999999, 15.466672000000003],
              [-96.30123599999999, 15.466672000000003],
              [-96.25742, 15.625503000000002],
              [-95.945234, 15.663842000000002],
              [-95.479694, 15.800766000000003],
              [-95.02510799999999, 15.565257000000003],
              [-95.06892400000001, 15.422857],
              [-94.959385, 15.280456000000001],
              [-94.70196899999999, 15.247594999999997],
              [-94.378829, 15.033994],
              [-94.40073699999999, 14.973748],
              [-94.25286, 14.858732000000003],
              [-93.84208899999999, 14.727285000000002],
              [-93.173903, 14.152206],
              [-93.07531800000001, 13.998852],
              [-92.86719400000001, 13.845498],
              [-92.560485, 13.505927999999997],
              [-92.25377700000001, 13.215649999999997],
              [-92.36879200000001, 13.095157],
              [-92.05113, 13.051341],
              [-92.12780699999999, 12.876078999999997],
              [-91.87039100000001, 12.804879],
              [-91.83752899999999, 12.602232],
              [-91.58011300000001, 12.613185999999999],
              [-91.087188, 12.405062000000001],
              [-90.78048000000001, 12.366723999999998],
              [-90.621648, 12.268138999999998],
              [-90.46829399999999, 12.240754000000003],
              [-90.424478, 12.142169000000003],
              [-90.101339, 12.114784999999998],
              [-90.00275400000001, 11.994292000000002],
              [-90.150631, 11.769737],
              [-90.128724, 11.583520999999998],
              [-89.986323, 11.276812999999997],
              [-90.09586200000001, 11.200136],
              [-90.336847, 10.920811999999998],
              [-90.68737200000001, 10.718164999999999],
              [-90.785957, 10.822226999999998],
              [-90.91740300000001, 10.789364999999997],
              [-91.01051100000001, 10.997489000000002],
              [-90.983126, 11.085120000000003],
              [-91.09266500000001, 11.189182000000002],
              [-90.86263400000001, 11.298721],
              [-90.928357, 11.337059000000004],
              [-91.158389, 11.200136],
              [-91.284358, 11.287767000000002],
              [-91.426759, 11.238473999999997],
              [-91.843006, 11.457552],
              [-91.700606, 11.698537000000002],
              [-91.96349900000001, 11.616382999999999],
              [-91.97445300000001, 11.495890000000003],
              [-92.18257600000001, 11.588997999999997],
              [-92.226392, 11.704014],
              [-92.083991, 11.829984000000003],
              [-92.12780699999999, 11.955953000000001],
              [-92.32497699999999, 11.851892],
              [-92.52214699999999, 12.076445999999997],
              [-92.642639, 12.032631000000002],
              [-92.71931699999999, 12.218846999999997],
              [-92.527624, 12.339339000000002],
              [-92.341408, 12.339339000000002],
              [-92.396177, 12.487217000000001],
              [-92.29759200000001, 12.678910000000002],
              [-92.45094599999999, 12.673433000000003],
              [-92.768609, 12.837741000000001],
              [-92.99316400000001, 13.034911000000001],
              [-93.51894999999999, 13.177311000000003],
              [-93.50799599999999, 13.577128000000002],
              [-93.67778100000001, 13.626420000000003],
              [-93.639442, 13.790728000000001],
              [-93.814705, 13.834544000000001],
              [-94.07212100000001, 14.053621999999997],
              [-94.14332099999999, 14.168636999999997],
              [-94.586953, 14.206975999999997],
              [-95.074401, 14.502730999999997],
              [-95.28252499999999, 15.192825],
              [-95.38111, 15.033994],
              [-95.33729400000001, 14.891593],
              [-95.14012399999999, 14.617745999999997],
              [-95.18941699999999, 14.573931000000002],
              [-95.05797000000001, 14.349376],
              [-95.08535499999999, 14.201498999999998],
              [-95.27704800000001, 14.234361],
              [-95.43040199999999, 14.398668999999998],
              [-95.633049, 14.426053000000003],
              [-95.91784999999999, 14.382238000000001],
              [-95.912373, 14.617745999999997],
              [-96.087635, 14.814915999999997],
              [-96.246466, 14.754669999999997],
              [-96.87631400000001, 14.962794000000002],
              [-96.931084, 14.902546999999998],
              [-96.58603600000001, 14.836824],
              [-96.317666, 14.672516000000002],
              [-96.213604, 14.667039000000003],
              [-96.180743, 14.535592000000001],
              [-96.04381900000001, 14.382238000000001],
              [-96.388867, 14.294607],
              [-96.59151299999999, 14.349376],
              [-96.59699, 14.212452999999996],
              [-96.859883, 14.316515000000003],
              [-96.94751400000001, 14.393191999999999],
              [-97.11182299999999, 14.393191999999999],
              [-97.566409, 14.590361999999999],
              [-97.90050199999999, 14.765624000000003],
              [-97.93336400000001, 14.869686000000002],
              [-98.11958000000001, 15.023040000000002],
              [-98.634412, 15.132579],
              [-98.91921300000001, 15.247594999999997],
              [-99.41761500000001, 15.379041],
              [-99.746231, 15.505011000000003],
              [-99.71884600000001, 15.641934],
              [-99.62573800000001, 15.598118999999997],
              [-99.5162, 15.685749999999999],
              [-99.62573800000001, 15.882919999999999],
              [-99.48881499999999, 15.992458],
              [-99.55453800000001, 16.041750999999998],
              [-99.80099999999999, 15.833627],
              [-100.315833, 15.696703999999997],
              [-100.92925, 15.745995999999998],
              [-101.444083, 15.871966],
              [-101.46599, 15.970551],
              [-101.706976, 15.948642999999997],
              [-101.964392, 16.019843],
              [-102.53947099999999, 16.085566],
              [-102.87356399999999, 16.091043],
              [-103.62390500000001, 16.036274],
              [-103.89227500000001, 15.997934999999998],
              [-104.23184499999999, 16.140335999999998],
              [-104.65357, 16.206059000000003],
              [-104.78501600000001, 16.293689999999998],
              [-104.83430899999999, 16.441567999999997],
              [-105.124586, 16.430614],
              [-105.22317100000001, 16.299166999999997],
              [-105.73800399999999, 16.474429],
              [-105.82015799999999, 16.551105999999997],
              [-106.35142099999999, 16.408706000000002],
              [-106.608837, 16.238920999999998],
              [-106.718376, 16.397751999999997],
              [-106.608837, 16.485383],
              [-106.45548299999999, 16.463475000000003],
              [-105.951604, 16.578491],
              [-106.01732799999999, 16.666122],
              [-106.252836, 16.622307],
              [-106.345944, 16.737322],
              [-106.565022, 16.753753000000003],
              [-106.784099, 17.044030999999997],
              [-106.866253, 16.972831],
              [-107.17296200000001, 16.934492],
              [-107.271547, 16.972831],
              [-107.375609, 16.879723],
              [-107.758995, 16.912584000000003],
              [-107.77542600000001, 16.808523],
              [-108.03284199999999, 16.781138],
              [-108.153334, 16.819476],
              [-108.06570300000001, 17.005692000000003],
              [-108.175242, 17.000214999999997],
              [-108.350504, 16.803046000000002],
              [-108.109519, 16.737322],
              [-108.08761100000001, 16.594921999999997],
              [-107.93973399999999, 16.441567999999997],
              [-108.027365, 16.277259],
              [-108.21905799999999, 16.332029],
              [-108.27382700000001, 16.249875000000003],
              [-108.08761100000001, 16.217013],
              [-107.98354900000001, 15.997934999999998],
              [-108.25191899999999, 15.954120000000003],
              [-108.399797, 15.997934999999998],
              [-108.63530499999999, 15.937688999999999],
              [-108.755798, 15.986981],
              [-109.067984, 15.981504999999999],
              [-109.05703, 16.063659],
              [-109.20490699999999, 16.008889000000003],
              [-109.287061, 15.904826999999997],
              [-109.418508, 15.997934999999998],
              [-109.58281600000001, 15.866489000000001],
              [-109.511616, 15.806241999999997],
              [-109.741647, 15.729565000000001],
              [-109.949771, 15.718611000000003],
              [-110.031925, 15.614550000000001],
              [-110.25648000000001, 15.521442],
              [-110.40983399999999, 15.554302999999997],
              [-110.57961900000001, 15.444764],
              [-110.716543, 15.450240999999998],
              [-111.00134299999999, 15.225687],
              [-111.308052, 15.209256000000003],
              [-111.40663699999999, 15.280456000000001],
              [-111.59285299999999, 15.159962999999998],
              [-111.97623899999999, 15.253070999999998],
              [-111.888608, 15.422857],
              [-111.63666900000001, 15.483103],
              [-111.47236000000001, 15.472149000000002],
              [-111.423068, 15.537872],
              [-111.12731299999999, 15.669319000000002],
              [-111.11635899999999, 15.778858],
              [-111.505222, 15.630980000000001],
              [-111.828361, 15.718611000000003],
              [-111.86670000000001, 15.778858],
              [-111.702392, 16.030797],
              [-111.423068, 16.211536000000002],
              [-111.37925200000001, 16.359413000000004],
              [-111.297098, 16.386798],
              [-111.26423700000001, 16.545628999999998],
              [-111.40663699999999, 16.720892],
              [-111.06159, 16.786614999999998],
              [-110.404357, 17.038553999999998],
              [-110.24552600000001, 16.939968999999998],
              [-110.042879, 16.912584000000003],
              [-109.741647, 17.016646],
              [-110.075741, 17.153570000000002],
              [-110.207187, 17.257632],
              [-110.47008, 17.246678000000003],
              [-110.656296, 17.295969999999997],
              [-110.71106599999999, 17.252155000000002],
              [-111.02325099999999, 17.180954],
              [-111.165652, 17.044030999999997],
              [-111.477837, 17.011169000000002],
              [-111.80097699999999, 16.852338000000003],
              [-111.83383799999999, 16.748275999999997],
              [-112.08030099999999, 16.693506999999997],
              [-112.13507000000001, 16.578491],
              [-112.31033199999999, 16.507291000000002],
              [-112.39248599999999, 16.304644000000003],
              [-112.732057, 16.173197000000002],
              [-112.567748, 16.069136],
              [-112.70467199999999, 15.915781000000003],
              [-113.022334, 15.888396999999998],
              [-113.04971900000001, 15.691226999999998],
              [-113.345474, 15.620026000000003],
              [-113.438582, 15.702181000000003],
              [-113.58645899999999, 15.548825999999998],
              [-113.761721, 15.543349],
              [-113.72886, 15.433810999999999],
              [-114.11772300000001, 15.368087000000003],
              [-114.1944, 15.066856000000001],
              [-113.750768, 15.050424999999997],
              [-113.40024299999999, 14.968271000000001],
              [-113.301658, 14.869686000000002],
              [-113.44405900000001, 14.710853999999998],
              [-113.67956699999999, 14.612268999999998],
              [-113.89864499999999, 14.606793000000003],
              [-113.92055300000001, 14.519160999999997],
              [-114.062953, 14.4863],
              [-113.99723, 14.376761000000002],
              [-114.145107, 14.212452999999996],
              [-114.46277, 14.059097999999999],
              [-114.643509, 14.059097999999999],
              [-114.818771, 14.004328999999998],
              [-114.98855599999999, 14.015282999999997],
              [-115.120003, 13.955036999999997],
              [-115.08166399999999, 13.872883000000002],
              [-115.328126, 13.829067000000002],
              [-115.377419, 13.708573999999999],
              [-115.547204, 13.785251000000002],
              [-115.73342, 13.549743],
              [-116.04560599999999, 13.566173999999997],
              [-116.023698, 13.440204000000001],
              [-116.209914, 13.473066000000003],
              [-116.34136000000001, 13.418295999999998],
              [-116.34136000000001, 13.248511],
              [-116.549484, 12.985618000000002],
              [-116.883577, 12.952756999999998],
              [-117.15742399999999, 12.832264000000002],
              [-117.20124000000001, 12.766540999999997],
              [-117.37650199999999, 12.859648999999997],
              [-117.672257, 12.607709],
              [-117.754411, 12.678910000000002],
              [-117.91871900000001, 12.657001999999999],
              [-117.95705799999999, 12.514600999999999],
              [-118.12684300000001, 12.459831999999999],
              [-118.32948999999999, 12.481740000000002],
              [-118.48832100000001, 12.339339000000002],
              [-118.20899700000001, 12.295524],
              [-118.51022900000001, 11.977860999999997],
              [-119.375585, 11.873798999999998],
              [-119.616571, 11.594475000000003],
              [-119.676817, 11.654722],
              [-119.643955, 11.829984000000003],
              [-119.813741, 11.857368000000001],
              [-120.027341, 11.791645000000003],
              [-120.060203, 11.720444999999998],
              [-120.394296, 11.605429],
              [-120.536697, 11.473982999999997],
              [-120.580512, 11.567090999999998],
              [-120.66814299999999, 11.457552],
              [-120.86531299999999, 11.528751999999997],
              [-121.232268, 11.358967],
              [-121.506115, 11.364443999999999],
              [-121.46777599999999, 11.495890000000003],
              [-121.588269, 11.621859999999998],
              [-121.69780800000001, 11.517797999999999],
              [-121.68685400000001, 11.408259000000001],
              [-122.053809, 11.074165999999998],
              [-122.179779, 11.156320000000001],
              [-122.218117, 11.030349999999999],
              [-122.470057, 11.052258000000002],
              [-122.50839500000001, 11.249428000000002],
              [-122.66174899999999, 11.293244000000001],
              [-122.716519, 11.222043],
              [-122.57959500000001, 11.134411999999998],
              [-122.645319, 10.997489000000002],
              [-122.84796499999999, 10.926288999999997],
              [-123.00131999999999, 11.079642999999997],
              [-123.187536, 11.090597000000002],
              [-123.22039699999999, 11.030349999999999],
              [-123.03418099999999, 10.942718999999997],
              [-123.373752, 10.800319000000002],
              [-123.14372, 10.761980000000001],
              [-123.13824299999999, 10.696257000000003],
              [-123.329936, 10.745550000000001],
              [-123.58735200000001, 10.614103],
              [-124.08575400000001, 10.61958],
              [-124.33221599999999, 10.531948999999997],
              [-124.35412400000001, 10.466225999999999],
              [-124.638925, 10.389547999999998],
              [-124.847049, 10.416933],
              [-124.918249, 10.603149000000002],
              [-124.710125, 10.663395000000001],
              [-124.55129400000001, 10.887949999999996],
              [-124.34316999999999, 10.893427000000003],
              [-123.894061, 11.041303999999997],
              [-123.53258299999999, 11.046781000000003],
              [-123.39565999999999, 10.904381],
              [-123.291598, 11.008443],
              [-123.31350499999999, 11.128934999999998],
              [-123.10538199999999, 11.183705000000003],
              [-122.88082700000001, 11.183705000000003],
              [-122.57959500000001, 11.446598000000002],
              [-122.24550199999999, 11.682105999999997],
              [-121.807347, 11.890230000000003],
              [-121.29251400000001, 11.983338000000003],
              [-121.078914, 11.939523000000001],
              [-120.87079, 11.999769],
              [-120.816021, 11.912137999999999],
              [-120.931036, 11.813552999999999],
              [-120.80506700000001, 11.736876000000002],
              [-120.766728, 11.857368000000001],
              [-120.509312, 11.868322],
              [-120.43811199999999, 11.791645000000003],
              [-120.27928, 11.76426],
              [-120.27380299999999, 11.857368000000001],
              [-120.536697, 11.939523000000001],
              [-120.558604, 11.994292000000002],
              [-120.383342, 12.251708],
              [-120.14783399999999, 12.399586],
              [-119.830171, 12.541986000000001],
              [-119.32629299999999, 12.667955999999997],
              [-118.959338, 12.848695],
              [-118.784076, 12.782971000000003],
              [-118.64167499999999, 12.810355999999999],
              [-118.701922, 12.925372000000003],
              [-118.658106, 13.034911000000001],
              [-118.378782, 13.264941999999998],
              [-117.995396, 13.412820000000004],
              [-117.68868800000001, 13.609988999999999],
              [-117.705118, 13.719527999999997],
              [-117.45865599999999, 14.497253999999998],
              [-117.07526999999999, 14.705376999999999],
              [-117.11908600000001, 14.869686000000002],
              [-118.03921199999999, 14.634177000000001],
              [-118.32948999999999, 14.661562000000004],
              [-118.40069, 14.760147000000003],
              [-118.564998, 14.803961999999999],
              [-118.619768, 14.913500999999997],
              [-118.76764499999999, 14.864209000000002],
              [-118.86075299999999, 14.694423999999998],
              [-118.701922, 14.480823000000001],
              [-118.89361500000001, 14.387715],
              [-119.0634, 14.420577000000002],
              [-119.39201600000001, 14.760147000000003],
              [-119.616571, 14.929932],
              [-119.731586, 14.929932],
              [-119.80826400000001, 14.803961999999999],
              [-119.906848, 14.782055],
              [-120.05472599999999, 14.886116000000001],
              [-120.235465, 14.902546999999998],
              [-120.31761900000001, 15.072332000000003],
              [-120.85435899999999, 14.88064],
              [-121.33633, 14.743716],
              [-121.37466900000001, 14.667039000000003],
              [-121.752577, 14.552022999999998],
              [-121.938793, 14.656084999999997],
              [-121.76900800000001, 14.776578],
              [-121.82925499999999, 15.061379000000002],
              [-121.95522399999999, 15.362609999999997],
              [-121.703285, 15.488579999999999],
              [-121.91140899999999, 15.740518999999999],
              [-122.09214800000001, 15.882919999999999],
              [-122.23454799999999, 16.091043],
              [-122.448149, 16.178674],
              [-122.502918, 15.997934999999998],
              [-122.760334, 15.959597000000002],
              [-123.17110500000001, 15.844580999999998],
              [-123.66403, 15.795288999999997],
              [-123.9324, 15.806241999999997],
              [-124.162431, 15.866489000000001],
              [-124.18981600000001, 16.02532],
              [-124.38698600000001, 16.074613],
              [-124.699171, 16.293689999999998],
              [-124.962064, 16.337505999999998],
              [-125.26877300000001, 16.578491],
              [-125.06064900000001, 16.688029999999998],
              [-125.01683399999999, 16.890676999999997],
              [-125.17566500000001, 16.846860999999997],
              [-125.197573, 16.972831],
              [-125.12089599999999, 17.076893],
              [-125.323543, 17.170001],
              [-125.34545, 17.071416],
              [-125.591913, 17.109754000000002],
              [-125.624774, 17.279539],
              [-125.81646699999999, 17.301447000000003],
              [-125.92052899999999, 17.416463],
              [-125.915052, 17.558863000000002],
              [-126.10674499999999, 17.493139999999997],
              [-126.13960700000001, 17.630063999999997],
              [-125.904098, 17.662925],
              [-126.09579099999999, 17.81628],
              [-125.75622100000001, 17.827233],
              [-125.75622100000001, 18.013449],
              [-125.67406700000001, 18.139418999999997],
              [-125.044219, 18.539236000000002],
              [-124.91277199999999, 18.659728],
              [-124.81966399999999, 18.637821000000002],
              [-124.87443300000001, 18.807606],
              [-124.63344799999999, 19.097884],
              [-124.42532399999999, 19.212899],
              [-124.036462, 19.262192],
              [-123.73523, 19.212899],
              [-123.31350499999999, 19.037636999999997],
              [-123.039658, 19.059545],
              [-122.66174899999999, 19.229329999999997],
              [-122.272887, 19.486745999999997],
              [-122.075717, 19.514131],
              [-122.02642399999999, 19.448408],
              [-121.555408, 19.448408],
              [-121.13916, 19.503177],
              [-120.766728, 19.771546999999998],
              [-120.766728, 19.837271],
              [-120.952944, 20.089209999999994],
              [-120.974852, 20.237087000000002],
              [-121.26513, 20.395917999999995],
              [-121.37466900000001, 20.532842000000002],
              [-121.078914, 20.494502999999995],
              [-120.79959, 20.609519000000006],
              [-120.78315900000001, 20.719058000000004],
              [-121.14463699999999, 20.921705000000003],
              [-121.413007, 20.762873],
              [-121.66494599999999, 20.790257999999994],
              [-121.900455, 20.702627000000007],
              [-122.168825, 20.680718999999996],
              [-122.23454799999999, 20.620473000000004],
              [-122.541257, 20.532842000000002],
              [-122.634365, 20.384964999999994],
              [-122.78771900000001, 20.324718000000004],
              [-122.85891899999999, 20.499979999999994],
              [-123.04513499999999, 20.538319],
              [-123.176582, 20.401394999999994],
              [-123.25325900000001, 20.467118999999997],
              [-123.59830600000001, 20.565703999999997],
              [-124.304832, 20.560226999999998],
              [-124.80870999999999, 20.450688],
              [-125.000403, 20.434257000000002],
              [-125.41117399999999, 20.499979999999994],
              [-126.18889899999999, 20.576657999999995],
              [-126.391546, 20.636904],
              [-126.484654, 20.735489],
              [-126.413454, 20.872411999999997],
              [-126.692778, 20.987427999999994],
              [-126.63800800000001, 21.113398000000004],
              [-126.46274600000001, 21.179120999999995],
              [-126.517516, 21.337952],
              [-126.796839, 21.337952],
              [-127.026871, 21.381767999999994],
              [-127.47597999999999, 21.414629000000005],
              [-127.711489, 21.496784000000005],
              [-128.072967, 21.578937999999994],
              [-128.105828, 21.682998999999995],
              [-127.54170300000001, 21.819923000000003],
              [-126.829701, 22.049954],
              [-126.3313, 22.186877999999993],
              [-126.04649900000001, 22.110201000000004],
              [-125.75622100000001, 22.093770000000006],
              [-125.69049799999999, 22.203309000000004],
              [-125.86576, 22.21974],
              [-125.88219000000001, 22.312848000000002],
              [-125.18661900000001, 22.466201999999996],
              [-124.40341699999999, 22.581218000000007],
              [-123.98169200000001, 22.592172000000005],
              [-123.75166100000001, 22.553832999999997],
              [-123.872153, 22.389525000000006],
              [-123.828338, 22.274508999999995],
              [-123.91596899999999, 22.192355000000006],
              [-123.768091, 22.060907999999998],
              [-123.49424400000001, 22.082815999999994],
              [-123.14919699999999, 22.060907999999998],
              [-122.749381, 22.088292999999993],
              [-122.634365, 22.039001],
              [-122.37147200000001, 22.028047],
              [-122.14143999999999, 22.077338999999995],
              [-121.84020799999999, 22.02257],
              [-121.54993099999999, 22.241647],
              [-121.34180699999999, 22.252601],
              [-121.19940600000001, 22.208786000000003],
              [-121.128206, 22.334755],
              [-121.52802299999999, 22.395002000000005],
              [-121.91140899999999, 22.345709],
              [-121.87307000000001, 22.510017000000005],
              [-122.17430200000001, 22.685280000000006],
              [-122.502918, 22.740049],
              [-122.60150300000001, 22.898880000000005],
              [-122.344087, 22.937219],
              [-122.01547099999999, 22.778388000000007],
              [-122.075717, 22.652417999999997],
              [-121.916886, 22.553832999999997],
              [-121.57183800000001, 22.438817],
              [-121.48968400000001, 22.559309999999996],
              [-121.88402400000001, 22.718141000000003],
              [-121.71423899999999, 23.002942000000004],
              [-121.85116199999999, 23.052234999999996],
              [-122.240025, 22.991988000000006],
              [-122.63984199999999, 23.008419000000004],
              [-122.700088, 23.057711999999995],
              [-122.902735, 23.008419000000004],
              [-123.74070699999999, 23.128912],
              [-123.757138, 23.254880999999997],
              [-124.00907699999999, 23.534205],
              [-124.211724, 23.638267],
              [-124.534863, 23.725898],
              [-125.19209599999999, 23.966883999999993],
              [-125.49332799999999, 24.059991999999994],
              [-125.79455899999999, 24.081899000000007],
              [-126.24366800000001, 24.246207999999996],
              [-126.681824, 24.339315999999997],
              [-126.703731, 24.372176999999994],
              [-126.375115, 24.42147],
              [-126.227238, 24.574824000000007],
              [-126.216284, 24.881533000000005],
              [-125.32901899999999, 24.859624999999994],
              [-124.255539, 24.930825],
              [-123.976215, 24.985595000000004],
              [-123.53258299999999, 25.138948999999997],
              [-123.110859, 25.374457000000007],
              [-123.02322799999999, 25.609966],
              [-122.842489, 25.812613],
              [-122.470057, 25.982398000000003],
              [-122.31122500000001, 26.108367],
              [-121.85116199999999, 26.311014],
              [-121.77996200000001, 26.256245000000007],
              [-121.39657600000001, 26.239813999999996],
              [-120.837928, 26.343875999999995],
              [-120.487404, 26.453415000000007],
              [-119.64943199999999, 26.792985],
              [-119.33177, 26.809416],
              [-119.29890800000001, 26.760122999999993],
              [-118.97576900000001, 26.798462],
              [-118.658106, 26.787508000000003],
              [-118.03373500000001, 26.831322999999998],
              [-117.42031800000001, 26.979201000000003],
              [-116.812377, 27.285909000000004],
              [-116.56591499999999, 27.351633000000007],
              [-116.522099, 27.296863000000002],
              [-115.585543, 27.170894000000004],
              [-115.50886499999999, 27.083263000000002],
              [-115.83200500000001, 26.968247000000005],
              [-115.979882, 26.962770000000006],
              [-115.97440499999999, 26.809416],
              [-115.503388, 26.858707999999993],
              [-115.47600399999999, 26.940861999999996],
              [-115.262403, 27.017539],
              [-115.191203, 26.973724000000004],
              [-115.03237200000001, 27.148985999999994],
              [-114.566832, 26.990155],
              [-114.643509, 26.869662000000005],
              [-114.353231, 26.836799999999997],
              [-114.183446, 26.765600000000006],
              [-113.93150700000001, 26.880616000000003],
              [-113.487874, 26.886093000000002],
              [-113.23593500000001, 26.924431],
              [-112.58965599999999, 26.886093000000002],
              [-112.26104000000001, 26.842276999999996],
              [-112.419871, 26.606769],
              [-111.817408, 26.546522999999993],
              [-111.77359200000001, 26.486276000000004],
              [-111.187559, 26.382214000000005],
              [-111.18208200000001, 26.431506999999996],
              [-110.760358, 26.497230000000002],
              [-110.355064, 26.491753000000003],
              [-110.34958800000001, 26.436983999999995],
              [-110.114079, 26.431506999999996],
              [-109.867617, 26.508184],
              [-109.462323, 26.519137999999998],
              [-109.17752200000001, 26.486276000000004],
              [-108.78866, 26.404122],
              [-108.607921, 26.420552999999998],
              [-108.350504, 26.305537],
              [-108.202627, 26.349352999999994],
              [-107.96164200000001, 26.316491],
              [-107.78637900000001, 26.245290999999995]
            ],
            [
              [-112.94018, 14.026237000000002],
              [-112.94565700000001, 13.982421000000002],
              [-113.290705, 14.048144999999998],
              [-113.044242, 14.305560999999997],
              [-112.81968800000001, 14.327469],
              [-112.66633300000001, 14.562976999999997],
              [-112.49654799999999, 14.354852999999999],
              [-112.35414800000001, 14.426053000000003],
              [-112.08030099999999, 14.311038000000003],
              [-112.08030099999999, 14.152206],
              [-112.480117, 14.130299],
              [-112.65537900000001, 14.059097999999999],
              [-112.94018, 14.026237000000002]
            ],
            [
              [-113.958891, 13.538789000000001],
              [-113.67409, 13.670236000000003],
              [-113.93150700000001, 13.69762],
              [-113.936983, 13.812635999999998],
              [-113.72338300000001, 13.889313000000001],
              [-113.57002800000001, 13.834544000000001],
              [-113.54812100000001, 13.719527999999997],
              [-113.46049, 13.796205],
              [-113.45501300000001, 13.965989999999998],
              [-113.268797, 13.889313000000001],
              [-113.23593500000001, 13.998852],
              [-113.071627, 13.933129000000001],
              [-112.874457, 13.933129000000001],
              [-112.721103, 13.993375],
              [-112.46916300000001, 13.889313000000001],
              [-112.46916300000001, 13.599035],
              [-112.151501, 13.620942999999997],
              [-112.359625, 13.429250000000003],
              [-112.74301, 13.505927999999997],
              [-112.60060999999999, 13.379958000000002],
              [-112.710149, 13.275896000000003],
              [-112.907319, 13.325187999999997],
              [-112.91279599999999, 13.128019000000002],
              [-113.21402699999999, 13.073248999999997],
              [-113.312612, 12.991095000000001],
              [-113.498828, 13.067771999999998],
              [-113.695998, 12.859648999999997],
              [-113.84935200000001, 12.837741000000001],
              [-114.01366100000001, 12.744633],
              [-114.07390699999999, 12.969186999999998],
              [-114.30393799999999, 12.848695],
              [-114.31489199999999, 12.919894999999997],
              [-114.52301600000001, 12.991095000000001],
              [-114.539447, 13.193742],
              [-114.74209400000001, 13.275896000000003],
              [-114.62707800000001, 13.511403999999999],
              [-114.227261, 13.659281999999997],
              [-113.98079899999999, 13.648328],
              [-113.958891, 13.538789000000001]
            ],
            [
              [-114.53397000000001, 12.602232],
              [-114.74209400000001, 12.399586],
              [-114.807817, 12.432447000000003],
              [-114.53397000000001, 12.602232]
            ],
            [
              [-115.63483500000001, 11.923091999999997],
              [-115.47600399999999, 11.912137999999999],
              [-115.53077300000001, 11.704014],
              [-115.793666, 11.731399000000003],
              [-115.837482, 11.802599],
              [-115.63483500000001, 11.923091999999997]
            ],
            [
              [-119.89041800000001, 11.282290000000003],
              [-119.95066399999999, 11.068689],
              [-120.257373, 10.893427000000003],
              [-120.10949500000001, 11.161797],
              [-120.00543300000001, 11.134411999999998],
              [-119.89041800000001, 11.282290000000003]
            ],
            [
              [-120.52026599999999, 11.358967],
              [-120.33404999999999, 11.358967],
              [-120.339527, 11.249428000000002],
              [-120.525743, 11.128934999999998],
              [-120.69005100000001, 11.211089000000001],
              [-120.79411300000001, 11.134411999999998],
              [-120.85435899999999, 11.320628],
              [-120.79959, 11.380875000000003],
              [-120.52026599999999, 11.358967]
            ],
            [
              [-122.25645599999999, 10.981057999999997],
              [-122.23454799999999, 10.893427000000003],
              [-122.34956399999999, 10.838658000000002],
              [-122.437195, 10.931766000000003],
              [-122.25645599999999, 10.981057999999997]
            ],
            [
              [-122.415287, 19.634624000000002],
              [-122.563165, 19.536039000000002],
              [-122.612457, 19.623669999999997],
              [-122.415287, 19.634624000000002]
            ],
            [
              [-122.80414999999999, 10.488132999999998],
              [-122.59054900000001, 10.449795000000002],
              [-122.612457, 10.367640999999999],
              [-122.782242, 10.373117999999998],
              [-122.80414999999999, 10.488132999999998]
            ],
            [
              [-125.54809700000001, 10.296439999999997],
              [-125.47689700000001, 10.181424999999997],
              [-125.63025099999999, 10.132131999999999],
              [-125.685021, 10.252625000000002],
              [-125.54809700000001, 10.296439999999997]
            ],
            [
              [-125.73979, 10.154040000000002],
              [-126.04649900000001, 10.044500999999997],
              [-126.112222, 10.121178],
              [-125.980775, 10.219763],
              [-125.73979, 10.154040000000002]
            ],
            [
              [-126.364161, 16.359413000000004],
              [-126.13413, 16.397751999999997],
              [-126.084837, 16.326552],
              [-125.88219000000001, 16.342982999999997],
              [-125.685021, 16.277259],
              [-125.646682, 15.992458],
              [-125.750744, 15.899349999999998],
              [-126.00816, 15.844580999999998],
              [-126.062929, 15.745995999999998],
              [-126.44083800000001, 15.855535000000003],
              [-126.61609999999999, 15.850057999999997],
              [-126.994009, 15.992458],
              [-127.12545600000001, 15.992458],
              [-127.34453400000001, 16.074613],
              [-127.421211, 16.206059000000003],
              [-127.31167199999999, 16.238920999999998],
              [-126.93924000000001, 16.206059000000003],
              [-126.76397800000001, 16.310121000000002],
              [-126.577762, 16.321075],
              [-126.495608, 16.392274999999998],
              [-126.364161, 16.359413000000004]
            ],
            [
              [-126.375115, 10.01164],
              [-126.21080699999999, 9.934961999999999],
              [-126.54490000000001, 9.748745999999997],
              [-126.539423, 9.715885],
              [-126.11769899999999, 9.852808000000003],
              [-126.112222, 9.776131],
              [-126.282007, 9.683022999999999],
              [-126.55585400000001, 9.622777],
              [-126.58323899999999, 9.529668999999998],
              [-126.878994, 9.431083999999998],
              [-127.13641000000001, 9.425607],
              [-127.306195, 9.332498999999999],
              [-127.62385699999999, 9.250345000000003],
              [-127.793643, 9.337975999999998],
              [-127.45954900000001, 9.442037999999997],
              [-127.35548700000001, 9.425607],
              [-127.10354799999999, 9.513238000000001],
              [-127.163794, 9.611823000000001],
              [-127.02139399999999, 9.715885],
              [-126.807793, 9.666592000000001],
              [-126.785886, 9.732315999999997],
              [-127.015917, 9.754223000000003],
              [-127.141887, 9.825423999999998],
              [-127.03234800000001, 9.945915999999997],
              [-126.643485, 10.017116000000001],
              [-126.561331, 9.880192999999998],
              [-126.375115, 10.01164]
            ],
            [
              [-128.790446, 9.157237000000002],
              [-128.40706, 9.348930000000003],
              [-128.385152, 9.431083999999998],
              [-128.237275, 9.524192],
              [-128.007243, 9.568007000000001],
              [-127.886751, 9.518715],
              [-127.84293500000001, 9.387267999999999],
              [-128.270136, 9.244867999999997],
              [-128.500168, 9.036743999999999],
              [-128.686384, 8.965544000000001],
              [-128.790446, 9.157237000000002]
            ],
            [
              [-129.74891, 8.894343999999997],
              [-129.705095, 8.795758999999997],
              [-129.962511, 8.790281999999998],
              [-129.989896, 8.856005000000003],
              [-129.74891, 8.894343999999997]
            ],
            [
              [-130.148727, 13.221127000000003],
              [-130.28565, 13.128019000000002],
              [-130.313035, 13.221127000000003],
              [-130.148727, 13.221127000000003]
            ],
            [
              [-130.669036, 8.697173999999997],
              [-130.603313, 8.604066000000003],
              [-130.789529, 8.538342999999998],
              [-130.816914, 8.636927999999997],
              [-130.669036, 8.697173999999997]
            ],
            [
              [-131.742517, 19.716777999999998],
              [-130.94836, 19.5689],
              [-130.488297, 19.69487],
              [-130.280174, 19.683916000000004],
              [-130.093958, 19.612716],
              [-130.044665, 19.492223000000003],
              [-129.644848, 19.426499999999997],
              [-129.518879, 19.366253999999998],
              [-128.99857, 19.338869000000003],
              [-128.686384, 19.295053000000003],
              [-128.856169, 19.147176],
              [-129.108108, 19.180038000000003],
              [-129.376478, 19.152653],
              [-129.513402, 19.086930000000002],
              [-129.639372, 18.939051999999997],
              [-129.831064, 19.075975999999997],
              [-130.055619, 19.169083999999998],
              [-130.263743, 19.180038000000003],
              [-130.362328, 19.284100000000002],
              [-130.866206, 19.415546],
              [-131.101715, 19.421022999999998],
              [-131.463193, 19.306007],
              [-131.73704, 19.366253999999998],
              [-131.852055, 19.486745999999997],
              [-131.742517, 19.716777999999998]
            ],
            [
              [-132.432611, 8.390464999999999],
              [-132.41618, 8.275449000000002],
              [-132.607873, 8.253542000000003],
              [-132.569535, 8.352127000000003],
              [-132.432611, 8.390464999999999]
            ],
            [
              [-133.626584, 8.149479999999997],
              [-133.495138, 8.105663999999997],
              [-133.122706, 8.111141000000003],
              [-133.106275, 8.07828],
              [-133.549907, 8.028987],
              [-133.626584, 8.149479999999997]
            ],
            [
              [-134.322156, 8.280926000000001],
              [-134.327632, 8.379511],
              [-134.185232, 8.417850000000001],
              [-133.982585, 8.319265000000001],
              [-134.059262, 8.226157],
              [-134.179755, 8.231634],
              [-134.141417, 8.127572],
              [-134.333109, 8.116618000000003],
              [-134.738403, 8.007078999999997],
              [-134.968435, 8.039940999999999],
              [-134.902711, 8.116618000000003],
              [-134.656249, 8.105663999999997],
              [-134.322156, 8.280926000000001]
            ],
            [
              [-136.469116, 7.853724999999997],
              [-136.288377, 7.8701560000000015],
              [-136.288377, 7.744185999999999],
              [-136.518409, 7.760617000000003],
              [-136.80321, 7.612740000000002],
              [-136.912748, 7.809910000000002],
              [-136.792256, 7.815385999999997],
              [-136.775825, 7.963264000000002],
              [-136.627947, 7.968741000000001],
              [-136.627947, 7.859202000000003],
              [-136.469116, 7.853724999999997]
            ],
            [
              [-137.153734, 7.946832999999998],
              [-137.044195, 7.897540999999997],
              [-137.120872, 7.727755000000002],
              [-137.274226, 7.678463000000001],
              [-137.279703, 7.782525],
              [-137.153734, 7.946832999999998]
            ],
            [
              [-138.123152, 7.919448000000003],
              [-137.953367, 7.9139709999999965],
              [-137.800013, 7.793478999999998],
              [-137.964321, 7.651077999999998],
              [-138.123152, 7.919448000000003]
            ],
            [
              [-147.107557, 8.992928999999997],
              [-147.293773, 8.927205],
              [-147.304726, 8.823143000000002],
              [-148.90491, 8.762897000000002],
              [-148.642017, 8.927205],
              [-148.642017, 9.003883000000002],
              [-147.107557, 8.992928999999997]
            ]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "04",
      properties: {
        name: "Arizona",
        postal: "AZ",
        density: 57.05
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-109.042503, 37.000263],
            [-109.04798, 31.331629],
            [-111.074448, 31.331629],
            [-112.246513, 31.704061],
            [-114.815198, 32.492741],
            [-114.72209, 32.717295],
            [-114.524921, 32.755634],
            [-114.470151, 32.843265],
            [-114.524921, 33.029481],
            [-114.661844, 33.034958],
            [-114.727567, 33.40739],
            [-114.524921, 33.54979],
            [-114.497536, 33.697668],
            [-114.535874, 33.933176],
            [-114.415382, 34.108438],
            [-114.256551, 34.174162],
            [-114.136058, 34.305608],
            [-114.333228, 34.448009],
            [-114.470151, 34.710902],
            [-114.634459, 34.87521],
            [-114.634459, 35.00118],
            [-114.574213, 35.138103],
            [-114.596121, 35.324319],
            [-114.678275, 35.516012],
            [-114.738521, 36.102045],
            [-114.371566, 36.140383],
            [-114.251074, 36.01989],
            [-114.152489, 36.025367],
            [-114.048427, 36.195153],
            [-114.048427, 37.000263],
            [-110.499369, 37.00574],
            [-109.042503, 37.000263]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "05",
      properties: {
        name: "Arkansas",
        postal: "AR",
        density: 56.43
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-94.473842, 36.501861],
            [-90.152536, 36.496384],
            [-90.064905, 36.304691],
            [-90.218259, 36.184199],
            [-90.377091, 35.997983],
            [-89.730812, 35.997983],
            [-89.763673, 35.811767],
            [-89.911551, 35.756997],
            [-89.944412, 35.603643],
            [-90.130628, 35.439335],
            [-90.114197, 35.198349],
            [-90.212782, 35.023087],
            [-90.311367, 34.995703],
            [-90.251121, 34.908072],
            [-90.409952, 34.831394],
            [-90.481152, 34.661609],
            [-90.585214, 34.617794],
            [-90.568783, 34.420624],
            [-90.749522, 34.365854],
            [-90.744046, 34.300131],
            [-90.952169, 34.135823],
            [-90.891923, 34.026284],
            [-91.072662, 33.867453],
            [-91.231493, 33.560744],
            [-91.056231, 33.429298],
            [-91.143862, 33.347144],
            [-91.089093, 33.13902],
            [-91.16577, 33.002096],
            [-93.608485, 33.018527],
            [-94.041164, 33.018527],
            [-94.041164, 33.54979],
            [-94.183564, 33.593606],
            [-94.380734, 33.544313],
            [-94.484796, 33.637421],
            [-94.430026, 35.395519],
            [-94.616242, 36.501861],
            [-94.473842, 36.501861]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "06",
      properties: {
        name: "California",
        postal: "CA",
        density: 241.7
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-123.233256, 42.006186],
            [-122.378853, 42.011663],
            [-121.037003, 41.995232],
            [-120.001861, 41.995232],
            [-119.996384, 40.264519],
            [-120.001861, 38.999346],
            [-118.71478, 38.101128],
            [-117.498899, 37.21934],
            [-116.540435, 36.501861],
            [-115.85034, 35.970598],
            [-114.634459, 35.00118],
            [-114.634459, 34.87521],
            [-114.470151, 34.710902],
            [-114.333228, 34.448009],
            [-114.136058, 34.305608],
            [-114.256551, 34.174162],
            [-114.415382, 34.108438],
            [-114.535874, 33.933176],
            [-114.497536, 33.697668],
            [-114.524921, 33.54979],
            [-114.727567, 33.40739],
            [-114.661844, 33.034958],
            [-114.524921, 33.029481],
            [-114.470151, 32.843265],
            [-114.524921, 32.755634],
            [-114.72209, 32.717295],
            [-116.04751, 32.624187],
            [-117.126467, 32.536556],
            [-117.24696, 32.668003],
            [-117.252437, 32.876127],
            [-117.329114, 33.122589],
            [-117.471515, 33.297851],
            [-117.7837, 33.538836],
            [-118.183517, 33.763391],
            [-118.260194, 33.703145],
            [-118.413548, 33.741483],
            [-118.391641, 33.840068],
            [-118.566903, 34.042715],
            [-118.802411, 33.998899],
            [-119.218659, 34.146777],
            [-119.278905, 34.26727],
            [-119.558229, 34.415147],
            [-119.875891, 34.40967],
            [-120.138784, 34.475393],
            [-120.472878, 34.448009],
            [-120.64814, 34.579455],
            [-120.609801, 34.858779],
            [-120.670048, 34.902595],
            [-120.631709, 35.099764],
            [-120.894602, 35.247642],
            [-120.905556, 35.450289],
            [-121.004141, 35.461243],
            [-121.168449, 35.636505],
            [-121.283465, 35.674843],
            [-121.332757, 35.784382],
            [-121.716143, 36.195153],
            [-121.896882, 36.315645],
            [-121.935221, 36.638785],
            [-121.858544, 36.6114],
            [-121.787344, 36.803093],
            [-121.929744, 36.978355],
            [-122.105006, 36.956447],
            [-122.335038, 37.115279],
            [-122.417192, 37.241248],
            [-122.400761, 37.361741],
            [-122.515777, 37.520572],
            [-122.515777, 37.783465],
            [-122.329561, 37.783465],
            [-122.406238, 38.15042],
            [-122.488392, 38.112082],
            [-122.504823, 37.931343],
            [-122.701993, 37.893004],
            [-122.937501, 38.029928],
            [-122.97584, 38.265436],
            [-123.129194, 38.451652],
            [-123.331841, 38.566668],
            [-123.44138, 38.698114],
            [-123.737134, 38.95553],
            [-123.687842, 39.032208],
            [-123.824765, 39.366301],
            [-123.764519, 39.552517],
            [-123.85215, 39.831841],
            [-124.109566, 40.105688],
            [-124.361506, 40.259042],
            [-124.410798, 40.439781],
            [-124.158859, 40.877937],
            [-124.109566, 41.025814],
            [-124.158859, 41.14083],
            [-124.065751, 41.442061],
            [-124.147905, 41.715908],
            [-124.257444, 41.781632],
            [-124.213628, 42.000709],
            [-123.233256, 42.006186]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "08",
      properties: {
        name: "Colorado",
        postal: "CO",
        density: 49.33
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-107.919731, 41.003906],
            [-105.728954, 40.998429],
            [-104.053011, 41.003906],
            [-102.053927, 41.003906],
            [-102.053927, 40.001626],
            [-102.042974, 36.994786],
            [-103.001438, 37.000263],
            [-104.337812, 36.994786],
            [-106.868158, 36.994786],
            [-107.421329, 37.000263],
            [-109.042503, 37.000263],
            [-109.042503, 38.166851],
            [-109.058934, 38.27639],
            [-109.053457, 39.125316],
            [-109.04798, 40.998429],
            [-107.919731, 41.003906]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "09",
      properties: {
        name: "Connecticut",
        postal: "CT",
        density: 739.1
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-73.053528, 42.039048],
            [-71.799309, 42.022617],
            [-71.799309, 42.006186],
            [-71.799309, 41.414677],
            [-71.859555, 41.321569],
            [-71.947186, 41.338],
            [-72.385341, 41.261322],
            [-72.905651, 41.28323],
            [-73.130205, 41.146307],
            [-73.371191, 41.102491],
            [-73.655992, 40.987475],
            [-73.727192, 41.102491],
            [-73.48073, 41.21203],
            [-73.55193, 41.294184],
            [-73.486206, 42.050002],
            [-73.053528, 42.039048]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "10",
      properties: {
        name: "Delaware",
        postal: "DE",
        density: 464.3
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-75.414089, 39.804456],
            [-75.507197, 39.683964],
            [-75.611259, 39.61824],
            [-75.589352, 39.459409],
            [-75.441474, 39.311532],
            [-75.403136, 39.065069],
            [-75.189535, 38.807653],
            [-75.09095, 38.796699],
            [-75.047134, 38.451652],
            [-75.693413, 38.462606],
            [-75.786521, 39.722302],
            [-75.616736, 39.831841],
            [-75.414089, 39.804456]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "11",
      properties: {
        name: "District of Columbia",
        postal: "DC",
        density: 10065
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-77.035264, 38.993869],
            [-76.909294, 38.895284],
            [-77.040741, 38.791222],
            [-77.117418, 38.933623],
            [-77.035264, 38.993869]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "12",
      properties: {
        name: "Florida",
        postal: "FL",
        density: 353.4,
        tooltipLatLng: {
          lat: 28.70171810699327,
          lng: -81.63032273110946
        },
        popupLatLng: { lat: 28.70171810699327, lng: -81.63032273110946 }
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-85.497137, 30.997536],
            [-85.004212, 31.003013],
            [-84.867289, 30.712735],
            [-83.498053, 30.647012],
            [-82.216449, 30.570335],
            [-82.167157, 30.356734],
            [-82.046664, 30.362211],
            [-82.002849, 30.564858],
            [-82.041187, 30.751074],
            [-81.948079, 30.827751],
            [-81.718048, 30.745597],
            [-81.444201, 30.707258],
            [-81.383954, 30.27458],
            [-81.257985, 29.787132],
            [-80.967707, 29.14633],
            [-80.524075, 28.461713],
            [-80.589798, 28.41242],
            [-80.56789, 28.094758],
            [-80.381674, 27.738757],
            [-80.091397, 27.021277],
            [-80.03115, 26.796723],
            [-80.036627, 26.566691],
            [-80.146166, 25.739673],
            [-80.239274, 25.723243],
            [-80.337859, 25.465826],
            [-80.304997, 25.383672],
            [-80.49669, 25.197456],
            [-80.573367, 25.241272],
            [-80.759583, 25.164595],
            [-81.077246, 25.120779],
            [-81.170354, 25.224841],
            [-81.126538, 25.378195],
            [-81.351093, 25.821827],
            [-81.526355, 25.903982],
            [-81.679709, 25.843735],
            [-81.800202, 26.090198],
            [-81.833064, 26.292844],
            [-82.041187, 26.517399],
            [-82.09048, 26.665276],
            [-82.057618, 26.878877],
            [-82.172634, 26.917216],
            [-82.145249, 26.791246],
            [-82.249311, 26.758384],
            [-82.566974, 27.300601],
            [-82.692943, 27.437525],
            [-82.391711, 27.837342],
            [-82.588881, 27.815434],
            [-82.720328, 27.689464],
            [-82.851774, 27.886634],
            [-82.676512, 28.434328],
            [-82.643651, 28.888914],
            [-82.764143, 28.998453],
            [-82.802482, 29.14633],
            [-82.994175, 29.179192],
            [-83.218729, 29.420177],
            [-83.399469, 29.518762],
            [-83.410422, 29.66664],
            [-83.536392, 29.721409],
            [-83.640454, 29.885717],
            [-84.02384, 30.104795],
            [-84.357933, 30.055502],
            [-84.341502, 29.902148],
            [-84.451041, 29.929533],
            [-84.867289, 29.743317],
            [-85.310921, 29.699501],
            [-85.299967, 29.80904],
            [-85.404029, 29.940487],
            [-85.924338, 30.236241],
            [-86.29677, 30.362211],
            [-86.630863, 30.395073],
            [-86.910187, 30.373165],
            [-87.518128, 30.280057],
            [-87.37025, 30.427934],
            [-87.446927, 30.510088],
            [-87.408589, 30.674397],
            [-87.633143, 30.86609],
            [-87.600282, 30.997536],
            [-85.497137, 30.997536]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "13",
      properties: {
        name: "Georgia",
        postal: "GA",
        density: 169.5
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.109191, 35.00118],
            [-83.322791, 34.787579],
            [-83.339222, 34.683517],
            [-83.005129, 34.469916],
            [-82.901067, 34.486347],
            [-82.747713, 34.26727],
            [-82.714851, 34.152254],
            [-82.55602, 33.94413],
            [-82.325988, 33.81816],
            [-82.194542, 33.631944],
            [-81.926172, 33.462159],
            [-81.937125, 33.347144],
            [-81.761863, 33.160928],
            [-81.493493, 33.007573],
            [-81.42777, 32.843265],
            [-81.416816, 32.629664],
            [-81.279893, 32.558464],
            [-81.121061, 32.290094],
            [-81.115584, 32.120309],
            [-80.885553, 32.032678],
            [-81.132015, 31.693108],
            [-81.175831, 31.517845],
            [-81.279893, 31.364491],
            [-81.290846, 31.20566],
            [-81.400385, 31.13446],
            [-81.444201, 30.707258],
            [-81.718048, 30.745597],
            [-81.948079, 30.827751],
            [-82.041187, 30.751074],
            [-82.002849, 30.564858],
            [-82.046664, 30.362211],
            [-82.167157, 30.356734],
            [-82.216449, 30.570335],
            [-83.498053, 30.647012],
            [-84.867289, 30.712735],
            [-85.004212, 31.003013],
            [-85.113751, 31.27686],
            [-85.042551, 31.539753],
            [-85.141136, 31.840985],
            [-85.053504, 32.01077],
            [-85.058981, 32.13674],
            [-84.889196, 32.262709],
            [-85.004212, 32.322956],
            [-84.960397, 32.421541],
            [-85.069935, 32.580372],
            [-85.184951, 32.859696],
            [-85.431413, 34.124869],
            [-85.606675, 34.984749],
            [-84.319594, 34.990226],
            [-83.618546, 34.984749],
            [-83.109191, 35.00118]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "15",
      properties: {
        name: "Hawaii",
        postal: "HI",
        density: 214.1
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-87.63483500000001, 18.948267],
            [-87.88129699999999, 19.035898],
            [-87.919636, 19.123529],
            [-87.886774, 19.348084],
            [-88.062036, 19.73147],
            [-87.92511300000001, 19.857439],
            [-87.826528, 20.032702],
            [-87.897728, 20.147717],
            [-87.87582, 20.26821],
            [-87.596496, 20.12581],
            [-87.284311, 20.021748],
            [-87.09261799999999, 19.868393],
            [-87.09261799999999, 19.736947],
            [-86.807817, 19.523346],
            [-86.983079, 19.348084],
            [-87.295265, 19.26593],
            [-87.514342, 19.134483],
            [-87.63483500000001, 18.948267]
          ],
          [
            [-88.58782299999999, 21.029505],
            [-88.47280699999999, 20.892581],
            [-88.324929, 20.952827],
            [-88.00179, 20.793996],
            [-88.05108200000001, 20.651596],
            [-88.37969899999999, 20.580396],
            [-88.44542200000001, 20.60778],
            [-88.46185299999999, 20.783042],
            [-88.63163800000001, 20.821381],
            [-88.697361, 20.919966],
            [-88.58782299999999, 21.029505]
          ],
          [
            [-88.98216199999999, 21.210244],
            [-89.080747, 21.106182],
            [-89.310779, 21.106182],
            [-89.23957899999999, 21.221198],
            [-88.98216199999999, 21.210244]
          ],
          [
            [-89.951581, 21.697691],
            [-89.84204199999999, 21.462183],
            [-89.89681100000001, 21.325259],
            [-90.110412, 21.303352],
            [-90.252813, 21.582676],
            [-90.12684300000001, 21.588153],
            [-89.951581, 21.697691]
          ],
          [
            [-91.468693, 22.228955],
            [-91.353678, 22.218001],
            [-91.29890800000001, 22.113939],
            [-91.33177, 21.966061],
            [-91.446786, 21.872953],
            [-91.76444799999999, 21.987969],
            [-91.72610900000001, 22.152277],
            [-91.468693, 22.228955]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "16",
      properties: {
        name: "Idaho",
        postal: "ID",
        density: 19.15
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-116.04751, 49.000239],
            [-116.04751, 47.976051],
            [-115.724371, 47.696727],
            [-115.718894, 47.42288],
            [-115.527201, 47.302388],
            [-115.324554, 47.258572],
            [-115.302646, 47.187372],
            [-114.930214, 46.919002],
            [-114.886399, 46.809463],
            [-114.623506, 46.705401],
            [-114.612552, 46.639678],
            [-114.322274, 46.645155],
            [-114.464674, 46.272723],
            [-114.492059, 46.037214],
            [-114.387997, 45.88386],
            [-114.568736, 45.774321],
            [-114.497536, 45.670259],
            [-114.546828, 45.560721],
            [-114.333228, 45.456659],
            [-114.086765, 45.593582],
            [-113.98818, 45.703121],
            [-113.807441, 45.604536],
            [-113.834826, 45.522382],
            [-113.736241, 45.330689],
            [-113.571933, 45.128042],
            [-113.45144, 45.056842],
            [-113.456917, 44.865149],
            [-113.341901, 44.782995],
            [-113.133778, 44.772041],
            [-113.002331, 44.448902],
            [-112.887315, 44.394132],
            [-112.783254, 44.48724],
            [-112.471068, 44.481763],
            [-112.241036, 44.569394],
            [-112.104113, 44.520102],
            [-111.868605, 44.563917],
            [-111.819312, 44.509148],
            [-111.616665, 44.547487],
            [-111.386634, 44.75561],
            [-111.227803, 44.580348],
            [-111.047063, 44.476286],
            [-111.047063, 42.000709],
            [-112.164359, 41.995232],
            [-114.04295, 41.995232],
            [-117.027882, 42.000709],
            [-117.027882, 43.830007],
            [-116.896436, 44.158624],
            [-116.97859, 44.240778],
            [-117.170283, 44.257209],
            [-117.241483, 44.394132],
            [-117.038836, 44.750133],
            [-116.934774, 44.782995],
            [-116.830713, 44.930872],
            [-116.847143, 45.02398],
            [-116.732128, 45.144473],
            [-116.671881, 45.319735],
            [-116.463758, 45.61549],
            [-116.545912, 45.752413],
            [-116.78142, 45.823614],
            [-116.918344, 45.993399],
            [-116.92382, 46.168661],
            [-117.055267, 46.343923],
            [-117.038836, 46.426077],
            [-117.044313, 47.762451],
            [-117.033359, 49.000239],
            [-116.04751, 49.000239]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "17",
      properties: {
        name: "Illinois",
        postal: "IL",
        density: 231.5
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-90.639984, 42.510065],
            [-88.788778, 42.493634],
            [-87.802929, 42.493634],
            [-87.83579, 42.301941],
            [-87.682436, 42.077386],
            [-87.523605, 41.710431],
            [-87.529082, 39.34987],
            [-87.63862, 39.169131],
            [-87.512651, 38.95553],
            [-87.49622, 38.780268],
            [-87.62219, 38.637868],
            [-87.655051, 38.506421],
            [-87.83579, 38.292821],
            [-87.950806, 38.27639],
            [-87.923421, 38.15042],
            [-88.000098, 38.101128],
            [-88.060345, 37.865619],
            [-88.027483, 37.799896],
            [-88.15893, 37.657496],
            [-88.065822, 37.482234],
            [-88.476592, 37.389126],
            [-88.514931, 37.285064],
            [-88.421823, 37.153617],
            [-88.547792, 37.071463],
            [-88.914747, 37.224817],
            [-89.029763, 37.213863],
            [-89.183118, 37.038601],
            [-89.133825, 36.983832],
            [-89.292656, 36.994786],
            [-89.517211, 37.279587],
            [-89.435057, 37.34531],
            [-89.517211, 37.537003],
            [-89.517211, 37.690357],
            [-89.84035, 37.903958],
            [-89.949889, 37.88205],
            [-90.059428, 38.013497],
            [-90.355183, 38.216144],
            [-90.349706, 38.374975],
            [-90.179921, 38.632391],
            [-90.207305, 38.725499],
            [-90.10872, 38.845992],
            [-90.251121, 38.917192],
            [-90.470199, 38.961007],
            [-90.585214, 38.867899],
            [-90.661891, 38.928146],
            [-90.727615, 39.256762],
            [-91.061708, 39.470363],
            [-91.368417, 39.727779],
            [-91.494386, 40.034488],
            [-91.50534, 40.237135],
            [-91.417709, 40.379535],
            [-91.401278, 40.560274],
            [-91.121954, 40.669813],
            [-91.09457, 40.823167],
            [-90.963123, 40.921752],
            [-90.946692, 41.097014],
            [-91.111001, 41.239415],
            [-91.045277, 41.414677],
            [-90.656414, 41.463969],
            [-90.344229, 41.589939],
            [-90.311367, 41.743293],
            [-90.179921, 41.809016],
            [-90.141582, 42.000709],
            [-90.168967, 42.126679],
            [-90.393521, 42.225264],
            [-90.420906, 42.329326],
            [-90.639984, 42.510065]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "18",
      properties: {
        name: "Indiana",
        postal: "IN",
        density: 181.7
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-85.990061, 41.759724],
            [-84.807042, 41.759724],
            [-84.807042, 41.694001],
            [-84.801565, 40.500028],
            [-84.817996, 39.103408],
            [-84.894673, 39.059592],
            [-84.812519, 38.785745],
            [-84.987781, 38.780268],
            [-85.173997, 38.68716],
            [-85.431413, 38.730976],
            [-85.42046, 38.533806],
            [-85.590245, 38.451652],
            [-85.655968, 38.325682],
            [-85.83123, 38.27639],
            [-85.924338, 38.024451],
            [-86.039354, 37.958727],
            [-86.263908, 38.051835],
            [-86.302247, 38.166851],
            [-86.521325, 38.040881],
            [-86.504894, 37.931343],
            [-86.729448, 37.893004],
            [-86.795172, 37.991589],
            [-87.047111, 37.893004],
            [-87.129265, 37.788942],
            [-87.381204, 37.93682],
            [-87.512651, 37.903958],
            [-87.600282, 37.975158],
            [-87.682436, 37.903958],
            [-87.934375, 37.893004],
            [-88.027483, 37.799896],
            [-88.060345, 37.865619],
            [-88.000098, 38.101128],
            [-87.923421, 38.15042],
            [-87.950806, 38.27639],
            [-87.83579, 38.292821],
            [-87.655051, 38.506421],
            [-87.62219, 38.637868],
            [-87.49622, 38.780268],
            [-87.512651, 38.95553],
            [-87.63862, 39.169131],
            [-87.529082, 39.34987],
            [-87.523605, 41.710431],
            [-87.42502, 41.644708],
            [-87.118311, 41.644708],
            [-86.822556, 41.759724],
            [-85.990061, 41.759724]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "19",
      properties: {
        name: "Iowa",
        postal: "IA",
        density: 54.81
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-91.368417, 43.501391],
            [-91.215062, 43.501391],
            [-91.204109, 43.353514],
            [-91.056231, 43.254929],
            [-91.176724, 43.134436],
            [-91.143862, 42.909881],
            [-91.067185, 42.75105],
            [-90.711184, 42.636034],
            [-90.639984, 42.510065],
            [-90.420906, 42.329326],
            [-90.393521, 42.225264],
            [-90.168967, 42.126679],
            [-90.141582, 42.000709],
            [-90.179921, 41.809016],
            [-90.311367, 41.743293],
            [-90.344229, 41.589939],
            [-90.656414, 41.463969],
            [-91.045277, 41.414677],
            [-91.111001, 41.239415],
            [-90.946692, 41.097014],
            [-90.963123, 40.921752],
            [-91.09457, 40.823167],
            [-91.121954, 40.669813],
            [-91.401278, 40.560274],
            [-91.417709, 40.379535],
            [-91.527248, 40.412397],
            [-91.729895, 40.615043],
            [-91.833957, 40.609566],
            [-93.257961, 40.582182],
            [-94.632673, 40.571228],
            [-95.7664, 40.587659],
            [-95.881416, 40.719105],
            [-95.826646, 40.976521],
            [-95.925231, 41.201076],
            [-95.919754, 41.453015],
            [-96.095016, 41.540646],
            [-96.122401, 41.67757],
            [-96.062155, 41.798063],
            [-96.127878, 41.973325],
            [-96.264801, 42.039048],
            [-96.44554, 42.488157],
            [-96.631756, 42.707235],
            [-96.544125, 42.855112],
            [-96.511264, 43.052282],
            [-96.434587, 43.123482],
            [-96.560556, 43.222067],
            [-96.527695, 43.397329],
            [-96.582464, 43.479483],
            [-96.451017, 43.501391],
            [-91.368417, 43.501391]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "20",
      properties: {
        name: "Kansas",
        postal: "KS",
        density: 35.09
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-101.90605, 40.001626],
            [-95.306337, 40.001626],
            [-95.207752, 39.908518],
            [-94.884612, 39.831841],
            [-95.109167, 39.541563],
            [-94.983197, 39.442978],
            [-94.824366, 39.20747],
            [-94.610765, 39.158177],
            [-94.616242, 37.000263],
            [-100.087706, 37.000263],
            [-102.042974, 36.994786],
            [-102.053927, 40.001626],
            [-101.90605, 40.001626]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "21",
      properties: {
        name: "Kentucky",
        postal: "KY",
        density: 110
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-83.903347, 38.769315],
            [-83.678792, 38.632391],
            [-83.519961, 38.703591],
            [-83.142052, 38.626914],
            [-83.032514, 38.725499],
            [-82.890113, 38.758361],
            [-82.846298, 38.588575],
            [-82.731282, 38.561191],
            [-82.594358, 38.424267],
            [-82.621743, 38.123036],
            [-82.50125, 37.931343],
            [-82.342419, 37.783465],
            [-82.293127, 37.668449],
            [-82.101434, 37.553434],
            [-81.969987, 37.537003],
            [-82.353373, 37.268633],
            [-82.720328, 37.120755],
            [-82.720328, 37.044078],
            [-82.868205, 36.978355],
            [-82.879159, 36.890724],
            [-83.070852, 36.852385],
            [-83.136575, 36.742847],
            [-83.673316, 36.600446],
            [-83.689746, 36.584015],
            [-84.544149, 36.594969],
            [-85.289013, 36.627831],
            [-85.486183, 36.616877],
            [-86.592525, 36.655216],
            [-87.852221, 36.633308],
            [-88.071299, 36.677123],
            [-88.054868, 36.496384],
            [-89.298133, 36.507338],
            [-89.418626, 36.496384],
            [-89.363857, 36.622354],
            [-89.215979, 36.578538],
            [-89.133825, 36.983832],
            [-89.183118, 37.038601],
            [-89.029763, 37.213863],
            [-88.914747, 37.224817],
            [-88.547792, 37.071463],
            [-88.421823, 37.153617],
            [-88.514931, 37.285064],
            [-88.476592, 37.389126],
            [-88.065822, 37.482234],
            [-88.15893, 37.657496],
            [-88.027483, 37.799896],
            [-87.934375, 37.893004],
            [-87.682436, 37.903958],
            [-87.600282, 37.975158],
            [-87.512651, 37.903958],
            [-87.381204, 37.93682],
            [-87.129265, 37.788942],
            [-87.047111, 37.893004],
            [-86.795172, 37.991589],
            [-86.729448, 37.893004],
            [-86.504894, 37.931343],
            [-86.521325, 38.040881],
            [-86.302247, 38.166851],
            [-86.263908, 38.051835],
            [-86.039354, 37.958727],
            [-85.924338, 38.024451],
            [-85.83123, 38.27639],
            [-85.655968, 38.325682],
            [-85.590245, 38.451652],
            [-85.42046, 38.533806],
            [-85.431413, 38.730976],
            [-85.173997, 38.68716],
            [-84.987781, 38.780268],
            [-84.812519, 38.785745],
            [-84.894673, 39.059592],
            [-84.817996, 39.103408],
            [-84.43461, 39.103408],
            [-84.231963, 38.895284],
            [-84.215533, 38.807653],
            [-83.903347, 38.769315]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "22",
      properties: {
        name: "Louisiana",
        postal: "LA",
        density: 105
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-93.608485, 33.018527],
            [-91.16577, 33.002096],
            [-91.072662, 32.887081],
            [-91.143862, 32.843265],
            [-91.154816, 32.640618],
            [-91.006939, 32.514649],
            [-90.985031, 32.218894],
            [-91.105524, 31.988862],
            [-91.341032, 31.846462],
            [-91.401278, 31.621907],
            [-91.499863, 31.643815],
            [-91.516294, 31.27686],
            [-91.636787, 31.265906],
            [-91.565587, 31.068736],
            [-91.636787, 30.997536],
            [-89.747242, 30.997536],
            [-89.845827, 30.66892],
            [-89.681519, 30.449842],
            [-89.643181, 30.285534],
            [-89.522688, 30.181472],
            [-89.818443, 30.044549],
            [-89.84035, 29.945964],
            [-89.599365, 29.88024],
            [-89.495303, 30.039072],
            [-89.287179, 29.88024],
            [-89.30361, 29.754271],
            [-89.424103, 29.699501],
            [-89.648657, 29.748794],
            [-89.621273, 29.655686],
            [-89.69795, 29.513285],
            [-89.506257, 29.387316],
            [-89.199548, 29.348977],
            [-89.09001, 29.2011],
            [-89.002379, 29.179192],
            [-89.16121, 29.009407],
            [-89.336472, 29.042268],
            [-89.484349, 29.217531],
            [-89.851304, 29.310638],
            [-89.851304, 29.480424],
            [-90.032043, 29.425654],
            [-90.021089, 29.283254],
            [-90.103244, 29.151807],
            [-90.23469, 29.129899],
            [-90.333275, 29.277777],
            [-90.563307, 29.283254],
            [-90.645461, 29.129899],
            [-90.798815, 29.086084],
            [-90.963123, 29.179192],
            [-91.09457, 29.190146],
            [-91.220539, 29.436608],
            [-91.445094, 29.546147],
            [-91.532725, 29.529716],
            [-91.620356, 29.73784],
            [-91.883249, 29.710455],
            [-91.888726, 29.836425],
            [-92.146142, 29.715932],
            [-92.113281, 29.622824],
            [-92.31045, 29.535193],
            [-92.617159, 29.579009],
            [-92.97316, 29.715932],
            [-93.2251, 29.776178],
            [-93.767317, 29.726886],
            [-93.838517, 29.688547],
            [-93.926148, 29.787132],
            [-93.690639, 30.143133],
            [-93.767317, 30.334826],
            [-93.696116, 30.438888],
            [-93.728978, 30.575812],
            [-93.630393, 30.679874],
            [-93.526331, 30.93729],
            [-93.542762, 31.15089],
            [-93.816609, 31.556184],
            [-93.822086, 31.775262],
            [-94.041164, 31.994339],
            [-94.041164, 33.018527],
            [-93.608485, 33.018527]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "23",
      properties: {
        name: "Maine",
        postal: "ME",
        density: 43.04
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-70.703921, 43.057759],
            [-70.824413, 43.128959],
            [-70.807983, 43.227544],
            [-70.966814, 43.34256],
            [-71.032537, 44.657025],
            [-71.08183, 45.303304],
            [-70.649151, 45.440228],
            [-70.720352, 45.511428],
            [-70.556043, 45.664782],
            [-70.386258, 45.735983],
            [-70.41912, 45.796229],
            [-70.260289, 45.889337],
            [-70.309581, 46.064599],
            [-70.210996, 46.327492],
            [-70.057642, 46.415123],
            [-69.997395, 46.694447],
            [-69.225147, 47.461219],
            [-69.044408, 47.428357],
            [-69.033454, 47.242141],
            [-68.902007, 47.176418],
            [-68.578868, 47.285957],
            [-68.376221, 47.285957],
            [-68.233821, 47.357157],
            [-67.954497, 47.198326],
            [-67.790188, 47.066879],
            [-67.779235, 45.944106],
            [-67.801142, 45.675736],
            [-67.456095, 45.604536],
            [-67.505388, 45.48952],
            [-67.417757, 45.379982],
            [-67.488957, 45.281397],
            [-67.346556, 45.128042],
            [-67.16034, 45.160904],
            [-66.979601, 44.804903],
            [-67.187725, 44.646072],
            [-67.308218, 44.706318],
            [-67.406803, 44.596779],
            [-67.549203, 44.624164],
            [-67.565634, 44.531056],
            [-67.75185, 44.54201],
            [-68.047605, 44.328409],
            [-68.118805, 44.476286],
            [-68.222867, 44.48724],
            [-68.173574, 44.328409],
            [-68.403606, 44.251732],
            [-68.458375, 44.377701],
            [-68.567914, 44.311978],
            [-68.82533, 44.311978],
            [-68.830807, 44.459856],
            [-68.984161, 44.426994],
            [-68.956777, 44.322932],
            [-69.099177, 44.103854],
            [-69.071793, 44.043608],
            [-69.258008, 43.923115],
            [-69.444224, 43.966931],
            [-69.553763, 43.840961],
            [-69.707118, 43.82453],
            [-69.833087, 43.720469],
            [-69.986442, 43.742376],
            [-70.030257, 43.851915],
            [-70.254812, 43.676653],
            [-70.194565, 43.567114],
            [-70.358873, 43.528776],
            [-70.369827, 43.435668],
            [-70.556043, 43.320652],
            [-70.703921, 43.057759]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "24",
      properties: {
        name: "Maryland",
        postal: "MD",
        density: 596.3
      },
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          [
            [
              [-75.994645, 37.95325],
              [-76.016553, 37.95325],
              [-76.043938, 37.95325],
              [-75.994645, 37.95325]
            ]
          ],
          [
            [
              [-79.477979, 39.722302],
              [-75.786521, 39.722302],
              [-75.693413, 38.462606],
              [-75.047134, 38.451652],
              [-75.244304, 38.029928],
              [-75.397659, 38.013497],
              [-75.671506, 37.95325],
              [-75.885106, 37.909435],
              [-75.879629, 38.073743],
              [-75.961783, 38.139466],
              [-75.846768, 38.210667],
              [-76.000122, 38.374975],
              [-76.049415, 38.303775],
              [-76.257538, 38.320205],
              [-76.328738, 38.500944],
              [-76.263015, 38.500944],
              [-76.257538, 38.736453],
              [-76.191815, 38.829561],
              [-76.279446, 39.147223],
              [-76.169907, 39.333439],
              [-76.000122, 39.366301],
              [-75.972737, 39.557994],
              [-76.098707, 39.536086],
              [-76.104184, 39.437501],
              [-76.367077, 39.311532],
              [-76.443754, 39.196516],
              [-76.460185, 38.906238],
              [-76.55877, 38.769315],
              [-76.514954, 38.539283],
              [-76.383508, 38.380452],
              [-76.399939, 38.259959],
              [-76.317785, 38.139466],
              [-76.3616, 38.057312],
              [-76.591632, 38.216144],
              [-76.920248, 38.292821],
              [-77.018833, 38.446175],
              [-77.205049, 38.358544],
              [-77.276249, 38.479037],
              [-77.128372, 38.632391],
              [-77.040741, 38.791222],
              [-76.909294, 38.895284],
              [-77.035264, 38.993869],
              [-77.117418, 38.933623],
              [-77.248864, 39.026731],
              [-77.456988, 39.076023],
              [-77.456988, 39.223901],
              [-77.566527, 39.306055],
              [-77.719881, 39.322485],
              [-77.834897, 39.601809],
              [-78.004682, 39.601809],
              [-78.174467, 39.694917],
              [-78.267575, 39.61824],
              [-78.431884, 39.623717],
              [-78.470222, 39.514178],
              [-78.765977, 39.585379],
              [-78.963147, 39.437501],
              [-79.094593, 39.470363],
              [-79.291763, 39.300578],
              [-79.488933, 39.20747],
              [-79.477979, 39.722302]
            ]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "25",
      properties: {
        name: "Massachusetts",
        postal: "MA",
        density: 840.2
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-70.917521, 42.887974],
            [-70.818936, 42.871543],
            [-70.780598, 42.696281],
            [-70.824413, 42.55388],
            [-70.983245, 42.422434],
            [-70.988722, 42.269079],
            [-70.769644, 42.247172],
            [-70.638197, 42.08834],
            [-70.660105, 41.962371],
            [-70.550566, 41.929509],
            [-70.539613, 41.814493],
            [-70.260289, 41.715908],
            [-69.937149, 41.809016],
            [-70.008349, 41.672093],
            [-70.484843, 41.5516],
            [-70.660105, 41.546123],
            [-70.764167, 41.639231],
            [-70.928475, 41.611847],
            [-70.933952, 41.540646],
            [-71.120168, 41.496831],
            [-71.196845, 41.67757],
            [-71.22423, 41.710431],
            [-71.328292, 41.781632],
            [-71.383061, 42.01714],
            [-71.530939, 42.01714],
            [-71.799309, 42.006186],
            [-71.799309, 42.022617],
            [-73.053528, 42.039048],
            [-73.486206, 42.050002],
            [-73.508114, 42.08834],
            [-73.267129, 42.745573],
            [-72.456542, 42.729142],
            [-71.29543, 42.696281],
            [-71.185891, 42.789389],
            [-70.917521, 42.887974]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "26",
      properties: {
        name: "Michigan",
        postal: "MI",
        density: 173.9
      },
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          [
            [
              [-83.454238, 41.732339],
              [-84.807042, 41.694001],
              [-84.807042, 41.759724],
              [-85.990061, 41.759724],
              [-86.822556, 41.759724],
              [-86.619909, 41.891171],
              [-86.482986, 42.115725],
              [-86.357016, 42.252649],
              [-86.263908, 42.444341],
              [-86.209139, 42.718189],
              [-86.231047, 43.013943],
              [-86.526801, 43.594499],
              [-86.433693, 43.813577],
              [-86.499417, 44.07647],
              [-86.269385, 44.34484],
              [-86.220093, 44.569394],
              [-86.252954, 44.689887],
              [-86.088646, 44.73918],
              [-86.066738, 44.903488],
              [-85.809322, 44.947303],
              [-85.612152, 45.128042],
              [-85.628583, 44.766564],
              [-85.524521, 44.750133],
              [-85.393075, 44.930872],
              [-85.387598, 45.237581],
              [-85.305444, 45.314258],
              [-85.031597, 45.363551],
              [-85.119228, 45.577151],
              [-84.938489, 45.75789],
              [-84.713934, 45.768844],
              [-84.461995, 45.653829],
              [-84.215533, 45.637398],
              [-84.09504, 45.494997],
              [-83.908824, 45.484043],
              [-83.596638, 45.352597],
              [-83.4871, 45.358074],
              [-83.317314, 45.144473],
              [-83.454238, 45.029457],
              [-83.322791, 44.88158],
              [-83.273499, 44.711795],
              [-83.333745, 44.339363],
              [-83.536392, 44.246255],
              [-83.585684, 44.054562],
              [-83.82667, 43.988839],
              [-83.958116, 43.758807],
              [-83.908824, 43.671176],
              [-83.667839, 43.589022],
              [-83.481623, 43.714992],
              [-83.262545, 43.972408],
              [-82.917498, 44.070993],
              [-82.747713, 43.994316],
              [-82.643651, 43.851915],
              [-82.539589, 43.435668],
              [-82.523158, 43.227544],
              [-82.413619, 42.975605],
              [-82.517681, 42.614127],
              [-82.681989, 42.559357],
              [-82.687466, 42.690804],
              [-82.797005, 42.652465],
              [-82.922975, 42.351234],
              [-83.125621, 42.236218],
              [-83.185868, 42.006186],
              [-83.437807, 41.814493],
              [-83.454238, 41.732339]
            ]
          ],
          [
            [
              [-85.508091, 45.730506],
              [-85.49166, 45.610013],
              [-85.623106, 45.588105],
              [-85.568337, 45.75789],
              [-85.508091, 45.730506]
            ]
          ],
          [
            [
              [-87.589328, 45.095181],
              [-87.742682, 45.199243],
              [-87.649574, 45.341643],
              [-87.885083, 45.363551],
              [-87.791975, 45.500474],
              [-87.781021, 45.675736],
              [-87.989145, 45.796229],
              [-88.10416, 45.922199],
              [-88.531362, 46.020784],
              [-88.662808, 45.987922],
              [-89.09001, 46.135799],
              [-90.119674, 46.338446],
              [-90.229213, 46.508231],
              [-90.415429, 46.568478],
              [-90.026566, 46.672539],
              [-89.851304, 46.793032],
              [-89.413149, 46.842325],
              [-89.128348, 46.990202],
              [-88.996902, 46.995679],
              [-88.887363, 47.099741],
              [-88.575177, 47.247618],
              [-88.416346, 47.373588],
              [-88.180837, 47.455742],
              [-87.956283, 47.384542],
              [-88.350623, 47.077833],
              [-88.443731, 46.973771],
              [-88.438254, 46.787555],
              [-88.246561, 46.929956],
              [-87.901513, 46.908048],
              [-87.633143, 46.809463],
              [-87.392158, 46.535616],
              [-87.260711, 46.486323],
              [-87.008772, 46.530139],
              [-86.948526, 46.469893],
              [-86.696587, 46.437031],
              [-86.159846, 46.667063],
              [-85.880522, 46.68897],
              [-85.508091, 46.678016],
              [-85.256151, 46.754694],
              [-85.064458, 46.760171],
              [-85.02612, 46.480847],
              [-84.82895, 46.442508],
              [-84.63178, 46.486323],
              [-84.549626, 46.4206],
              [-84.418179, 46.502754],
              [-84.127902, 46.530139],
              [-84.122425, 46.179615],
              [-83.990978, 46.031737],
              [-83.793808, 45.993399],
              [-83.7719, 46.091984],
              [-83.580208, 46.091984],
              [-83.476146, 45.987922],
              [-83.563777, 45.911245],
              [-84.111471, 45.976968],
              [-84.374364, 45.933153],
              [-84.659165, 46.053645],
              [-84.741319, 45.944106],
              [-84.70298, 45.850998],
              [-84.82895, 45.872906],
              [-85.015166, 46.00983],
              [-85.338305, 46.091984],
              [-85.502614, 46.097461],
              [-85.661445, 45.966014],
              [-85.924338, 45.933153],
              [-86.209139, 45.960537],
              [-86.324155, 45.905768],
              [-86.351539, 45.796229],
              [-86.663725, 45.703121],
              [-86.647294, 45.834568],
              [-86.784218, 45.861952],
              [-86.838987, 45.725029],
              [-87.069019, 45.719552],
              [-87.17308, 45.659305],
              [-87.326435, 45.423797],
              [-87.611236, 45.122565],
              [-87.589328, 45.095181]
            ]
          ],
          [
            [
              [-88.805209, 47.976051],
              [-89.057148, 47.850082],
              [-89.188594, 47.833651],
              [-89.177641, 47.937713],
              [-88.547792, 48.173221],
              [-88.668285, 48.008913],
              [-88.805209, 47.976051]
            ]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "27",
      properties: {
        name: "Minnesota",
        postal: "MN",
        density: 67.14
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-92.014696, 46.705401],
            [-92.091373, 46.749217],
            [-92.29402, 46.667063],
            [-92.29402, 46.075553],
            [-92.354266, 46.015307],
            [-92.639067, 45.933153],
            [-92.869098, 45.719552],
            [-92.885529, 45.577151],
            [-92.770513, 45.566198],
            [-92.644544, 45.440228],
            [-92.75956, 45.286874],
            [-92.737652, 45.117088],
            [-92.808852, 44.750133],
            [-92.545959, 44.569394],
            [-92.337835, 44.552964],
            [-92.233773, 44.443425],
            [-91.927065, 44.333886],
            [-91.877772, 44.202439],
            [-91.592971, 44.032654],
            [-91.43414, 43.994316],
            [-91.242447, 43.775238],
            [-91.269832, 43.616407],
            [-91.215062, 43.501391],
            [-91.368417, 43.501391],
            [-96.451017, 43.501391],
            [-96.451017, 45.297827],
            [-96.681049, 45.412843],
            [-96.856311, 45.604536],
            [-96.582464, 45.818137],
            [-96.560556, 45.933153],
            [-96.598895, 46.332969],
            [-96.719387, 46.437031],
            [-96.801542, 46.656109],
            [-96.785111, 46.924479],
            [-96.823449, 46.968294],
            [-96.856311, 47.609096],
            [-97.053481, 47.948667],
            [-97.130158, 48.140359],
            [-97.16302, 48.545653],
            [-97.097296, 48.682577],
            [-97.228743, 49.000239],
            [-95.152983, 49.000239],
            [-95.152983, 49.383625],
            [-94.955813, 49.372671],
            [-94.824366, 49.295994],
            [-94.69292, 48.775685],
            [-94.588858, 48.715438],
            [-94.260241, 48.699007],
            [-94.221903, 48.649715],
            [-93.838517, 48.627807],
            [-93.794701, 48.518268],
            [-93.466085, 48.545653],
            [-93.466085, 48.589469],
            [-93.208669, 48.644238],
            [-92.984114, 48.62233],
            [-92.726698, 48.540176],
            [-92.655498, 48.436114],
            [-92.50762, 48.447068],
            [-92.370697, 48.222514],
            [-92.304974, 48.315622],
            [-92.053034, 48.359437],
            [-92.009219, 48.266329],
            [-91.713464, 48.200606],
            [-91.713464, 48.112975],
            [-91.565587, 48.041775],
            [-91.264355, 48.080113],
            [-91.083616, 48.178698],
            [-90.837154, 48.238944],
            [-90.749522, 48.091067],
            [-90.579737, 48.123929],
            [-90.377091, 48.091067],
            [-90.141582, 48.112975],
            [-89.873212, 47.987005],
            [-89.615796, 48.008913],
            [-89.637704, 47.954144],
            [-89.971797, 47.828174],
            [-90.437337, 47.729589],
            [-90.738569, 47.625527],
            [-91.171247, 47.368111],
            [-91.357463, 47.20928],
            [-91.642264, 47.028541],
            [-92.091373, 46.787555],
            [-92.014696, 46.705401]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "28",
      properties: {
        name: "Mississippi",
        postal: "MS",
        density: 63.5
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-88.471115, 34.995703],
            [-88.202745, 34.995703],
            [-88.098683, 34.891641],
            [-88.241084, 33.796253],
            [-88.471115, 31.895754],
            [-88.394438, 30.367688],
            [-88.503977, 30.323872],
            [-88.744962, 30.34578],
            [-88.843547, 30.411504],
            [-89.084533, 30.367688],
            [-89.418626, 30.252672],
            [-89.522688, 30.181472],
            [-89.643181, 30.285534],
            [-89.681519, 30.449842],
            [-89.845827, 30.66892],
            [-89.747242, 30.997536],
            [-91.636787, 30.997536],
            [-91.565587, 31.068736],
            [-91.636787, 31.265906],
            [-91.516294, 31.27686],
            [-91.499863, 31.643815],
            [-91.401278, 31.621907],
            [-91.341032, 31.846462],
            [-91.105524, 31.988862],
            [-90.985031, 32.218894],
            [-91.006939, 32.514649],
            [-91.154816, 32.640618],
            [-91.143862, 32.843265],
            [-91.072662, 32.887081],
            [-91.16577, 33.002096],
            [-91.089093, 33.13902],
            [-91.143862, 33.347144],
            [-91.056231, 33.429298],
            [-91.231493, 33.560744],
            [-91.072662, 33.867453],
            [-90.891923, 34.026284],
            [-90.952169, 34.135823],
            [-90.744046, 34.300131],
            [-90.749522, 34.365854],
            [-90.568783, 34.420624],
            [-90.585214, 34.617794],
            [-90.481152, 34.661609],
            [-90.409952, 34.831394],
            [-90.251121, 34.908072],
            [-90.311367, 34.995703],
            [-88.471115, 34.995703]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "29",
      properties: {
        name: "Missouri",
        postal: "MO",
        density: 87.26
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-91.833957, 40.609566],
            [-91.729895, 40.615043],
            [-91.527248, 40.412397],
            [-91.417709, 40.379535],
            [-91.50534, 40.237135],
            [-91.494386, 40.034488],
            [-91.368417, 39.727779],
            [-91.061708, 39.470363],
            [-90.727615, 39.256762],
            [-90.661891, 38.928146],
            [-90.585214, 38.867899],
            [-90.470199, 38.961007],
            [-90.251121, 38.917192],
            [-90.10872, 38.845992],
            [-90.207305, 38.725499],
            [-90.179921, 38.632391],
            [-90.349706, 38.374975],
            [-90.355183, 38.216144],
            [-90.059428, 38.013497],
            [-89.949889, 37.88205],
            [-89.84035, 37.903958],
            [-89.517211, 37.690357],
            [-89.517211, 37.537003],
            [-89.435057, 37.34531],
            [-89.517211, 37.279587],
            [-89.292656, 36.994786],
            [-89.133825, 36.983832],
            [-89.215979, 36.578538],
            [-89.363857, 36.622354],
            [-89.418626, 36.496384],
            [-89.484349, 36.496384],
            [-89.539119, 36.496384],
            [-89.533642, 36.249922],
            [-89.730812, 35.997983],
            [-90.377091, 35.997983],
            [-90.218259, 36.184199],
            [-90.064905, 36.304691],
            [-90.152536, 36.496384],
            [-94.473842, 36.501861],
            [-94.616242, 36.501861],
            [-94.616242, 37.000263],
            [-94.610765, 39.158177],
            [-94.824366, 39.20747],
            [-94.983197, 39.442978],
            [-95.109167, 39.541563],
            [-94.884612, 39.831841],
            [-95.207752, 39.908518],
            [-95.306337, 40.001626],
            [-95.552799, 40.264519],
            [-95.7664, 40.587659],
            [-94.632673, 40.571228],
            [-93.257961, 40.582182],
            [-91.833957, 40.609566]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "30",
      properties: {
        name: "Montana",
        postal: "MT",
        density: 6.858
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-104.047534, 49.000239],
            [-104.042057, 47.861036],
            [-104.047534, 45.944106],
            [-104.042057, 44.996596],
            [-104.058488, 44.996596],
            [-105.91517, 45.002073],
            [-109.080842, 45.002073],
            [-111.05254, 45.002073],
            [-111.047063, 44.476286],
            [-111.227803, 44.580348],
            [-111.386634, 44.75561],
            [-111.616665, 44.547487],
            [-111.819312, 44.509148],
            [-111.868605, 44.563917],
            [-112.104113, 44.520102],
            [-112.241036, 44.569394],
            [-112.471068, 44.481763],
            [-112.783254, 44.48724],
            [-112.887315, 44.394132],
            [-113.002331, 44.448902],
            [-113.133778, 44.772041],
            [-113.341901, 44.782995],
            [-113.456917, 44.865149],
            [-113.45144, 45.056842],
            [-113.571933, 45.128042],
            [-113.736241, 45.330689],
            [-113.834826, 45.522382],
            [-113.807441, 45.604536],
            [-113.98818, 45.703121],
            [-114.086765, 45.593582],
            [-114.333228, 45.456659],
            [-114.546828, 45.560721],
            [-114.497536, 45.670259],
            [-114.568736, 45.774321],
            [-114.387997, 45.88386],
            [-114.492059, 46.037214],
            [-114.464674, 46.272723],
            [-114.322274, 46.645155],
            [-114.612552, 46.639678],
            [-114.623506, 46.705401],
            [-114.886399, 46.809463],
            [-114.930214, 46.919002],
            [-115.302646, 47.187372],
            [-115.324554, 47.258572],
            [-115.527201, 47.302388],
            [-115.718894, 47.42288],
            [-115.724371, 47.696727],
            [-116.04751, 47.976051],
            [-116.04751, 49.000239],
            [-111.50165, 48.994762],
            [-109.453274, 49.000239],
            [-104.047534, 49.000239]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "31",
      properties: {
        name: "Nebraska",
        postal: "NE",
        density: 23.97
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-103.324578, 43.002989],
            [-101.626726, 42.997512],
            [-98.499393, 42.997512],
            [-98.466531, 42.94822],
            [-97.951699, 42.767481],
            [-97.831206, 42.866066],
            [-97.688806, 42.844158],
            [-97.217789, 42.844158],
            [-96.692003, 42.657942],
            [-96.626279, 42.515542],
            [-96.44554, 42.488157],
            [-96.264801, 42.039048],
            [-96.127878, 41.973325],
            [-96.062155, 41.798063],
            [-96.122401, 41.67757],
            [-96.095016, 41.540646],
            [-95.919754, 41.453015],
            [-95.925231, 41.201076],
            [-95.826646, 40.976521],
            [-95.881416, 40.719105],
            [-95.7664, 40.587659],
            [-95.552799, 40.264519],
            [-95.306337, 40.001626],
            [-101.90605, 40.001626],
            [-102.053927, 40.001626],
            [-102.053927, 41.003906],
            [-104.053011, 41.003906],
            [-104.053011, 43.002989],
            [-103.324578, 43.002989]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "32",
      properties: {
        name: "Nevada",
        postal: "NV",
        density: 24.8
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-117.027882, 42.000709],
            [-114.04295, 41.995232],
            [-114.048427, 37.000263],
            [-114.048427, 36.195153],
            [-114.152489, 36.025367],
            [-114.251074, 36.01989],
            [-114.371566, 36.140383],
            [-114.738521, 36.102045],
            [-114.678275, 35.516012],
            [-114.596121, 35.324319],
            [-114.574213, 35.138103],
            [-114.634459, 35.00118],
            [-115.85034, 35.970598],
            [-116.540435, 36.501861],
            [-117.498899, 37.21934],
            [-118.71478, 38.101128],
            [-120.001861, 38.999346],
            [-119.996384, 40.264519],
            [-120.001861, 41.995232],
            [-118.698349, 41.989755],
            [-117.027882, 42.000709]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "33",
      properties: {
        name: "New Hampshire",
        postal: "NH",
        density: 147
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-71.08183, 45.303304],
            [-71.032537, 44.657025],
            [-70.966814, 43.34256],
            [-70.807983, 43.227544],
            [-70.824413, 43.128959],
            [-70.703921, 43.057759],
            [-70.818936, 42.871543],
            [-70.917521, 42.887974],
            [-71.185891, 42.789389],
            [-71.29543, 42.696281],
            [-72.456542, 42.729142],
            [-72.544173, 42.80582],
            [-72.533219, 42.953697],
            [-72.445588, 43.008466],
            [-72.456542, 43.150867],
            [-72.379864, 43.572591],
            [-72.204602, 43.769761],
            [-72.116971, 43.994316],
            [-72.02934, 44.07647],
            [-72.034817, 44.322932],
            [-71.700724, 44.41604],
            [-71.536416, 44.585825],
            [-71.629524, 44.750133],
            [-71.4926, 44.914442],
            [-71.503554, 45.013027],
            [-71.361154, 45.270443],
            [-71.131122, 45.243058],
            [-71.08183, 45.303304]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "34",
      properties: {
        name: "New Jersey",
        postal: "NJ",
        density: 1189
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-74.236547, 41.14083],
            [-73.902454, 40.998429],
            [-74.022947, 40.708151],
            [-74.187255, 40.642428],
            [-74.274886, 40.489074],
            [-74.001039, 40.412397],
            [-73.979131, 40.297381],
            [-74.099624, 39.760641],
            [-74.411809, 39.360824],
            [-74.614456, 39.245808],
            [-74.795195, 38.993869],
            [-74.888303, 39.158177],
            [-75.178581, 39.240331],
            [-75.534582, 39.459409],
            [-75.55649, 39.607286],
            [-75.561967, 39.629194],
            [-75.507197, 39.683964],
            [-75.414089, 39.804456],
            [-75.145719, 39.88661],
            [-75.129289, 39.963288],
            [-74.82258, 40.127596],
            [-74.773287, 40.215227],
            [-75.058088, 40.417874],
            [-75.069042, 40.543843],
            [-75.195012, 40.576705],
            [-75.205966, 40.691721],
            [-75.052611, 40.866983],
            [-75.134765, 40.971045],
            [-74.882826, 41.179168],
            [-74.828057, 41.288707],
            [-74.69661, 41.359907],
            [-74.236547, 41.14083]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "35",
      properties: {
        name: "New Mexico",
        postal: "NM",
        density: 17.16
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-107.421329, 37.000263],
            [-106.868158, 36.994786],
            [-104.337812, 36.994786],
            [-103.001438, 37.000263],
            [-103.001438, 36.501861],
            [-103.039777, 36.501861],
            [-103.045254, 34.01533],
            [-103.067161, 33.002096],
            [-103.067161, 31.999816],
            [-106.616219, 31.999816],
            [-106.643603, 31.901231],
            [-106.528588, 31.786216],
            [-108.210008, 31.786216],
            [-108.210008, 31.331629],
            [-109.04798, 31.331629],
            [-109.042503, 37.000263],
            [-107.421329, 37.000263]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "36",
      properties: {
        name: "New York",
        postal: "NY",
        density: 412.3
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-73.343806, 45.013027],
            [-73.332852, 44.804903],
            [-73.387622, 44.618687],
            [-73.294514, 44.437948],
            [-73.321898, 44.246255],
            [-73.436914, 44.043608],
            [-73.349283, 43.769761],
            [-73.404052, 43.687607],
            [-73.245221, 43.523299],
            [-73.278083, 42.833204],
            [-73.267129, 42.745573],
            [-73.508114, 42.08834],
            [-73.486206, 42.050002],
            [-73.55193, 41.294184],
            [-73.48073, 41.21203],
            [-73.727192, 41.102491],
            [-73.655992, 40.987475],
            [-73.22879, 40.905321],
            [-73.141159, 40.965568],
            [-72.774204, 40.965568],
            [-72.587988, 40.998429],
            [-72.28128, 41.157261],
            [-72.259372, 41.042245],
            [-72.100541, 40.992952],
            [-72.467496, 40.845075],
            [-73.239744, 40.625997],
            [-73.562884, 40.582182],
            [-73.776484, 40.593136],
            [-73.935316, 40.543843],
            [-74.022947, 40.708151],
            [-73.902454, 40.998429],
            [-74.236547, 41.14083],
            [-74.69661, 41.359907],
            [-74.740426, 41.431108],
            [-74.89378, 41.436584],
            [-75.074519, 41.60637],
            [-75.052611, 41.754247],
            [-75.173104, 41.869263],
            [-75.249781, 41.863786],
            [-75.35932, 42.000709],
            [-79.76278, 42.000709],
            [-79.76278, 42.252649],
            [-79.76278, 42.269079],
            [-79.149363, 42.55388],
            [-79.050778, 42.690804],
            [-78.853608, 42.783912],
            [-78.930285, 42.953697],
            [-79.012439, 42.986559],
            [-79.072686, 43.260406],
            [-78.486653, 43.375421],
            [-77.966344, 43.369944],
            [-77.75822, 43.34256],
            [-77.533665, 43.233021],
            [-77.391265, 43.276836],
            [-76.958587, 43.271359],
            [-76.695693, 43.34256],
            [-76.41637, 43.523299],
            [-76.235631, 43.528776],
            [-76.230154, 43.802623],
            [-76.137046, 43.961454],
            [-76.3616, 44.070993],
            [-76.312308, 44.196962],
            [-75.912491, 44.366748],
            [-75.764614, 44.514625],
            [-75.282643, 44.848718],
            [-74.828057, 45.018503],
            [-74.148916, 44.991119],
            [-73.343806, 45.013027]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "37",
      properties: {
        name: "North Carolina",
        postal: "NC",
        density: 198.2
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-80.978661, 36.562108],
            [-80.294043, 36.545677],
            [-79.510841, 36.5402],
            [-75.868676, 36.551154],
            [-75.75366, 36.151337],
            [-76.032984, 36.189676],
            [-76.071322, 36.140383],
            [-76.410893, 36.080137],
            [-76.460185, 36.025367],
            [-76.68474, 36.008937],
            [-76.673786, 35.937736],
            [-76.399939, 35.987029],
            [-76.3616, 35.943213],
            [-76.060368, 35.992506],
            [-75.961783, 35.899398],
            [-75.781044, 35.937736],
            [-75.715321, 35.696751],
            [-75.775568, 35.581735],
            [-75.89606, 35.570781],
            [-76.147999, 35.324319],
            [-76.482093, 35.313365],
            [-76.536862, 35.14358],
            [-76.394462, 34.973795],
            [-76.279446, 34.940933],
            [-76.493047, 34.661609],
            [-76.673786, 34.694471],
            [-76.991448, 34.667086],
            [-77.210526, 34.60684],
            [-77.555573, 34.415147],
            [-77.82942, 34.163208],
            [-77.971821, 33.845545],
            [-78.179944, 33.916745],
            [-78.541422, 33.851022],
            [-79.675149, 34.80401],
            [-80.797922, 34.820441],
            [-80.781491, 34.935456],
            [-80.934845, 35.105241],
            [-81.038907, 35.044995],
            [-81.044384, 35.149057],
            [-82.276696, 35.198349],
            [-82.550543, 35.160011],
            [-82.764143, 35.066903],
            [-83.109191, 35.00118],
            [-83.618546, 34.984749],
            [-84.319594, 34.990226],
            [-84.29221, 35.225734],
            [-84.09504, 35.247642],
            [-84.018363, 35.41195],
            [-83.7719, 35.559827],
            [-83.498053, 35.565304],
            [-83.251591, 35.718659],
            [-82.994175, 35.773428],
            [-82.775097, 35.997983],
            [-82.638174, 36.063706],
            [-82.610789, 35.965121],
            [-82.216449, 36.156814],
            [-82.03571, 36.118475],
            [-81.909741, 36.304691],
            [-81.723525, 36.353984],
            [-81.679709, 36.589492],
            [-80.978661, 36.562108]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "38",
      properties: {
        name: "North Dakota",
        postal: "ND",
        density: 9.916
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-97.228743, 49.000239],
            [-97.097296, 48.682577],
            [-97.16302, 48.545653],
            [-97.130158, 48.140359],
            [-97.053481, 47.948667],
            [-96.856311, 47.609096],
            [-96.823449, 46.968294],
            [-96.785111, 46.924479],
            [-96.801542, 46.656109],
            [-96.719387, 46.437031],
            [-96.598895, 46.332969],
            [-96.560556, 45.933153],
            [-104.047534, 45.944106],
            [-104.042057, 47.861036],
            [-104.047534, 49.000239],
            [-97.228743, 49.000239]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "39",
      properties: {
        name: "Ohio",
        postal: "OH",
        density: 281.9
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-80.518598, 41.978802],
            [-80.518598, 40.636951],
            [-80.666475, 40.582182],
            [-80.595275, 40.472643],
            [-80.600752, 40.319289],
            [-80.737675, 40.078303],
            [-80.830783, 39.711348],
            [-81.219646, 39.388209],
            [-81.345616, 39.344393],
            [-81.455155, 39.410117],
            [-81.57017, 39.267716],
            [-81.685186, 39.273193],
            [-81.811156, 39.0815],
            [-81.783771, 38.966484],
            [-81.887833, 38.873376],
            [-82.03571, 39.026731],
            [-82.221926, 38.785745],
            [-82.172634, 38.632391],
            [-82.293127, 38.577622],
            [-82.331465, 38.446175],
            [-82.594358, 38.424267],
            [-82.731282, 38.561191],
            [-82.846298, 38.588575],
            [-82.890113, 38.758361],
            [-83.032514, 38.725499],
            [-83.142052, 38.626914],
            [-83.519961, 38.703591],
            [-83.678792, 38.632391],
            [-83.903347, 38.769315],
            [-84.215533, 38.807653],
            [-84.231963, 38.895284],
            [-84.43461, 39.103408],
            [-84.817996, 39.103408],
            [-84.801565, 40.500028],
            [-84.807042, 41.694001],
            [-83.454238, 41.732339],
            [-83.065375, 41.595416],
            [-82.933929, 41.513262],
            [-82.835344, 41.589939],
            [-82.616266, 41.431108],
            [-82.479343, 41.381815],
            [-82.013803, 41.513262],
            [-81.739956, 41.485877],
            [-81.444201, 41.672093],
            [-81.011523, 41.852832],
            [-80.518598, 41.978802],
            [-80.518598, 41.978802]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "40",
      properties: {
        name: "Oklahoma",
        postal: "OK",
        density: 55.22
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-100.087706, 37.000263],
            [-94.616242, 37.000263],
            [-94.616242, 36.501861],
            [-94.430026, 35.395519],
            [-94.484796, 33.637421],
            [-94.868182, 33.74696],
            [-94.966767, 33.861976],
            [-95.224183, 33.960561],
            [-95.289906, 33.87293],
            [-95.547322, 33.878407],
            [-95.602092, 33.933176],
            [-95.8376, 33.834591],
            [-95.936185, 33.889361],
            [-96.149786, 33.840068],
            [-96.346956, 33.686714],
            [-96.423633, 33.774345],
            [-96.631756, 33.845545],
            [-96.850834, 33.845545],
            [-96.922034, 33.960561],
            [-97.173974, 33.736006],
            [-97.256128, 33.861976],
            [-97.371143, 33.823637],
            [-97.458774, 33.905791],
            [-97.694283, 33.982469],
            [-97.869545, 33.851022],
            [-97.946222, 33.987946],
            [-98.088623, 34.004376],
            [-98.170777, 34.113915],
            [-98.36247, 34.157731],
            [-98.488439, 34.064623],
            [-98.570593, 34.146777],
            [-98.767763, 34.135823],
            [-98.986841, 34.223454],
            [-99.189488, 34.2125],
            [-99.260688, 34.404193],
            [-99.57835, 34.415147],
            [-99.698843, 34.382285],
            [-99.923398, 34.573978],
            [-100.000075, 34.563024],
            [-100.000075, 36.501861],
            [-101.812942, 36.501861],
            [-103.001438, 36.501861],
            [-103.001438, 37.000263],
            [-102.042974, 36.994786],
            [-100.087706, 37.000263]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "41",
      properties: {
        name: "Oregon",
        postal: "OR",
        density: 40.33
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-123.211348, 46.174138],
            [-123.11824, 46.185092],
            [-122.904639, 46.08103],
            [-122.811531, 45.960537],
            [-122.762239, 45.659305],
            [-122.247407, 45.549767],
            [-121.809251, 45.708598],
            [-121.535404, 45.725029],
            [-121.217742, 45.670259],
            [-121.18488, 45.604536],
            [-120.637186, 45.746937],
            [-120.505739, 45.697644],
            [-120.209985, 45.725029],
            [-119.963522, 45.823614],
            [-119.525367, 45.911245],
            [-119.125551, 45.933153],
            [-118.988627, 45.998876],
            [-116.918344, 45.993399],
            [-116.78142, 45.823614],
            [-116.545912, 45.752413],
            [-116.463758, 45.61549],
            [-116.671881, 45.319735],
            [-116.732128, 45.144473],
            [-116.847143, 45.02398],
            [-116.830713, 44.930872],
            [-116.934774, 44.782995],
            [-117.038836, 44.750133],
            [-117.241483, 44.394132],
            [-117.170283, 44.257209],
            [-116.97859, 44.240778],
            [-116.896436, 44.158624],
            [-117.027882, 43.830007],
            [-117.027882, 42.000709],
            [-118.698349, 41.989755],
            [-120.001861, 41.995232],
            [-121.037003, 41.995232],
            [-122.378853, 42.011663],
            [-123.233256, 42.006186],
            [-124.213628, 42.000709],
            [-124.356029, 42.115725],
            [-124.432706, 42.438865],
            [-124.416275, 42.663419],
            [-124.553198, 42.838681],
            [-124.454613, 43.002989],
            [-124.383413, 43.271359],
            [-124.235536, 43.55616],
            [-124.169813, 43.8081],
            [-124.060274, 44.657025],
            [-124.076705, 44.772041],
            [-123.97812, 45.144473],
            [-123.939781, 45.659305],
            [-123.994551, 45.944106],
            [-123.945258, 46.113892],
            [-123.545441, 46.261769],
            [-123.370179, 46.146753],
            [-123.211348, 46.174138]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "42",
      properties: {
        name: "Pennsylvania",
        postal: "PA",
        density: 284.3
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-79.76278, 42.252649],
            [-79.76278, 42.000709],
            [-75.35932, 42.000709],
            [-75.249781, 41.863786],
            [-75.173104, 41.869263],
            [-75.052611, 41.754247],
            [-75.074519, 41.60637],
            [-74.89378, 41.436584],
            [-74.740426, 41.431108],
            [-74.69661, 41.359907],
            [-74.828057, 41.288707],
            [-74.882826, 41.179168],
            [-75.134765, 40.971045],
            [-75.052611, 40.866983],
            [-75.205966, 40.691721],
            [-75.195012, 40.576705],
            [-75.069042, 40.543843],
            [-75.058088, 40.417874],
            [-74.773287, 40.215227],
            [-74.82258, 40.127596],
            [-75.129289, 39.963288],
            [-75.145719, 39.88661],
            [-75.414089, 39.804456],
            [-75.616736, 39.831841],
            [-75.786521, 39.722302],
            [-79.477979, 39.722302],
            [-80.518598, 39.722302],
            [-80.518598, 40.636951],
            [-80.518598, 41.978802],
            [-80.518598, 41.978802],
            [-80.332382, 42.033571],
            [-79.76278, 42.269079],
            [-79.76278, 42.252649]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "44",
      properties: {
        name: "Rhode Island",
        postal: "RI",
        density: 1006
      },
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          [
            [
              [-71.196845, 41.67757],
              [-71.120168, 41.496831],
              [-71.317338, 41.474923],
              [-71.196845, 41.67757]
            ]
          ],
          [
            [
              [-71.530939, 42.01714],
              [-71.383061, 42.01714],
              [-71.328292, 41.781632],
              [-71.22423, 41.710431],
              [-71.344723, 41.726862],
              [-71.448785, 41.578985],
              [-71.481646, 41.370861],
              [-71.859555, 41.321569],
              [-71.799309, 41.414677],
              [-71.799309, 42.006186],
              [-71.530939, 42.01714]
            ]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "45",
      properties: {
        name: "South Carolina",
        postal: "SC",
        density: 155.4
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-82.764143, 35.066903],
            [-82.550543, 35.160011],
            [-82.276696, 35.198349],
            [-81.044384, 35.149057],
            [-81.038907, 35.044995],
            [-80.934845, 35.105241],
            [-80.781491, 34.935456],
            [-80.797922, 34.820441],
            [-79.675149, 34.80401],
            [-78.541422, 33.851022],
            [-78.716684, 33.80173],
            [-78.935762, 33.637421],
            [-79.149363, 33.380005],
            [-79.187701, 33.171881],
            [-79.357487, 33.007573],
            [-79.582041, 33.007573],
            [-79.631334, 32.887081],
            [-79.866842, 32.755634],
            [-79.998289, 32.613234],
            [-80.206412, 32.552987],
            [-80.430967, 32.399633],
            [-80.452875, 32.328433],
            [-80.660998, 32.246279],
            [-80.885553, 32.032678],
            [-81.115584, 32.120309],
            [-81.121061, 32.290094],
            [-81.279893, 32.558464],
            [-81.416816, 32.629664],
            [-81.42777, 32.843265],
            [-81.493493, 33.007573],
            [-81.761863, 33.160928],
            [-81.937125, 33.347144],
            [-81.926172, 33.462159],
            [-82.194542, 33.631944],
            [-82.325988, 33.81816],
            [-82.55602, 33.94413],
            [-82.714851, 34.152254],
            [-82.747713, 34.26727],
            [-82.901067, 34.486347],
            [-83.005129, 34.469916],
            [-83.339222, 34.683517],
            [-83.322791, 34.787579],
            [-83.109191, 35.00118],
            [-82.764143, 35.066903]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "46",
      properties: {
        name: "South Dakota",
        postal: "SD",
        density: 98.07
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-104.047534, 45.944106],
            [-96.560556, 45.933153],
            [-96.582464, 45.818137],
            [-96.856311, 45.604536],
            [-96.681049, 45.412843],
            [-96.451017, 45.297827],
            [-96.451017, 43.501391],
            [-96.582464, 43.479483],
            [-96.527695, 43.397329],
            [-96.560556, 43.222067],
            [-96.434587, 43.123482],
            [-96.511264, 43.052282],
            [-96.544125, 42.855112],
            [-96.631756, 42.707235],
            [-96.44554, 42.488157],
            [-96.626279, 42.515542],
            [-96.692003, 42.657942],
            [-97.217789, 42.844158],
            [-97.688806, 42.844158],
            [-97.831206, 42.866066],
            [-97.951699, 42.767481],
            [-98.466531, 42.94822],
            [-98.499393, 42.997512],
            [-101.626726, 42.997512],
            [-103.324578, 43.002989],
            [-104.053011, 43.002989],
            [-104.058488, 44.996596],
            [-104.042057, 44.996596],
            [-104.047534, 45.944106]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "47",
      properties: {
        name: "Tennessee",
        postal: "TN",
        density: 88.08
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-88.054868, 36.496384],
            [-88.071299, 36.677123],
            [-87.852221, 36.633308],
            [-86.592525, 36.655216],
            [-85.486183, 36.616877],
            [-85.289013, 36.627831],
            [-84.544149, 36.594969],
            [-83.689746, 36.584015],
            [-83.673316, 36.600446],
            [-81.679709, 36.589492],
            [-81.723525, 36.353984],
            [-81.909741, 36.304691],
            [-82.03571, 36.118475],
            [-82.216449, 36.156814],
            [-82.610789, 35.965121],
            [-82.638174, 36.063706],
            [-82.775097, 35.997983],
            [-82.994175, 35.773428],
            [-83.251591, 35.718659],
            [-83.498053, 35.565304],
            [-83.7719, 35.559827],
            [-84.018363, 35.41195],
            [-84.09504, 35.247642],
            [-84.29221, 35.225734],
            [-84.319594, 34.990226],
            [-85.606675, 34.984749],
            [-87.359296, 35.00118],
            [-88.202745, 34.995703],
            [-88.471115, 34.995703],
            [-90.311367, 34.995703],
            [-90.212782, 35.023087],
            [-90.114197, 35.198349],
            [-90.130628, 35.439335],
            [-89.944412, 35.603643],
            [-89.911551, 35.756997],
            [-89.763673, 35.811767],
            [-89.730812, 35.997983],
            [-89.533642, 36.249922],
            [-89.539119, 36.496384],
            [-89.484349, 36.496384],
            [-89.418626, 36.496384],
            [-89.298133, 36.507338],
            [-88.054868, 36.496384]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "48",
      properties: {
        name: "Texas",
        postal: "TX",
        density: 98.07
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-101.812942, 36.501861],
            [-100.000075, 36.501861],
            [-100.000075, 34.563024],
            [-99.923398, 34.573978],
            [-99.698843, 34.382285],
            [-99.57835, 34.415147],
            [-99.260688, 34.404193],
            [-99.189488, 34.2125],
            [-98.986841, 34.223454],
            [-98.767763, 34.135823],
            [-98.570593, 34.146777],
            [-98.488439, 34.064623],
            [-98.36247, 34.157731],
            [-98.170777, 34.113915],
            [-98.088623, 34.004376],
            [-97.946222, 33.987946],
            [-97.869545, 33.851022],
            [-97.694283, 33.982469],
            [-97.458774, 33.905791],
            [-97.371143, 33.823637],
            [-97.256128, 33.861976],
            [-97.173974, 33.736006],
            [-96.922034, 33.960561],
            [-96.850834, 33.845545],
            [-96.631756, 33.845545],
            [-96.423633, 33.774345],
            [-96.346956, 33.686714],
            [-96.149786, 33.840068],
            [-95.936185, 33.889361],
            [-95.8376, 33.834591],
            [-95.602092, 33.933176],
            [-95.547322, 33.878407],
            [-95.289906, 33.87293],
            [-95.224183, 33.960561],
            [-94.966767, 33.861976],
            [-94.868182, 33.74696],
            [-94.484796, 33.637421],
            [-94.380734, 33.544313],
            [-94.183564, 33.593606],
            [-94.041164, 33.54979],
            [-94.041164, 33.018527],
            [-94.041164, 31.994339],
            [-93.822086, 31.775262],
            [-93.816609, 31.556184],
            [-93.542762, 31.15089],
            [-93.526331, 30.93729],
            [-93.630393, 30.679874],
            [-93.728978, 30.575812],
            [-93.696116, 30.438888],
            [-93.767317, 30.334826],
            [-93.690639, 30.143133],
            [-93.926148, 29.787132],
            [-93.838517, 29.688547],
            [-94.002825, 29.68307],
            [-94.523134, 29.546147],
            [-94.70935, 29.622824],
            [-94.742212, 29.787132],
            [-94.873659, 29.672117],
            [-94.966767, 29.699501],
            [-95.016059, 29.557101],
            [-94.911997, 29.496854],
            [-94.895566, 29.310638],
            [-95.081782, 29.113469],
            [-95.383014, 28.867006],
            [-95.985477, 28.604113],
            [-96.045724, 28.647929],
            [-96.226463, 28.582205],
            [-96.23194, 28.642452],
            [-96.478402, 28.598636],
            [-96.593418, 28.724606],
            [-96.664618, 28.697221],
            [-96.401725, 28.439805],
            [-96.593418, 28.357651],
            [-96.774157, 28.406943],
            [-96.801542, 28.226204],
            [-97.026096, 28.039988],
            [-97.256128, 27.694941],
            [-97.404005, 27.333463],
            [-97.513544, 27.360848],
            [-97.540929, 27.229401],
            [-97.425913, 27.262263],
            [-97.480682, 26.99937],
            [-97.557359, 26.988416],
            [-97.562836, 26.840538],
            [-97.469728, 26.758384],
            [-97.442344, 26.457153],
            [-97.332805, 26.353091],
            [-97.30542, 26.161398],
            [-97.217789, 25.991613],
            [-97.524498, 25.887551],
            [-97.650467, 26.018997],
            [-97.885976, 26.06829],
            [-98.198161, 26.057336],
            [-98.466531, 26.221644],
            [-98.669178, 26.238075],
            [-98.822533, 26.369522],
            [-99.030656, 26.413337],
            [-99.173057, 26.539307],
            [-99.266165, 26.840538],
            [-99.446904, 27.021277],
            [-99.424996, 27.174632],
            [-99.50715, 27.33894],
            [-99.479765, 27.48134],
            [-99.605735, 27.640172],
            [-99.709797, 27.656603],
            [-99.879582, 27.799003],
            [-99.934351, 27.979742],
            [-100.082229, 28.14405],
            [-100.29583, 28.280974],
            [-100.399891, 28.582205],
            [-100.498476, 28.66436],
            [-100.629923, 28.905345],
            [-100.673738, 29.102515],
            [-100.799708, 29.244915],
            [-101.013309, 29.370885],
            [-101.062601, 29.458516],
            [-101.259771, 29.535193],
            [-101.413125, 29.754271],
            [-101.851281, 29.803563],
            [-102.114174, 29.792609],
            [-102.338728, 29.869286],
            [-102.388021, 29.765225],
            [-102.629006, 29.732363],
            [-102.809745, 29.524239],
            [-102.919284, 29.190146],
            [-102.97953, 29.184669],
            [-103.116454, 28.987499],
            [-103.280762, 28.982022],
            [-103.527224, 29.135376],
            [-104.146119, 29.381839],
            [-104.266611, 29.513285],
            [-104.507597, 29.639255],
            [-104.677382, 29.924056],
            [-104.688336, 30.181472],
            [-104.858121, 30.389596],
            [-104.896459, 30.570335],
            [-105.005998, 30.685351],
            [-105.394861, 30.855136],
            [-105.602985, 31.085167],
            [-105.77277, 31.167321],
            [-105.953509, 31.364491],
            [-106.205448, 31.468553],
            [-106.38071, 31.731446],
            [-106.528588, 31.786216],
            [-106.643603, 31.901231],
            [-106.616219, 31.999816],
            [-103.067161, 31.999816],
            [-103.067161, 33.002096],
            [-103.045254, 34.01533],
            [-103.039777, 36.501861],
            [-103.001438, 36.501861],
            [-101.812942, 36.501861]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "49",
      properties: {
        name: "Utah",
        postal: "UT",
        density: 34.3
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-112.164359, 41.995232],
            [-111.047063, 42.000709],
            [-111.047063, 40.998429],
            [-109.04798, 40.998429],
            [-109.053457, 39.125316],
            [-109.058934, 38.27639],
            [-109.042503, 38.166851],
            [-109.042503, 37.000263],
            [-110.499369, 37.00574],
            [-114.048427, 37.000263],
            [-114.04295, 41.995232],
            [-112.164359, 41.995232]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "50",
      properties: {
        name: "Vermont",
        postal: "VT",
        density: 67.73
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-71.503554, 45.013027],
            [-71.4926, 44.914442],
            [-71.629524, 44.750133],
            [-71.536416, 44.585825],
            [-71.700724, 44.41604],
            [-72.034817, 44.322932],
            [-72.02934, 44.07647],
            [-72.116971, 43.994316],
            [-72.204602, 43.769761],
            [-72.379864, 43.572591],
            [-72.456542, 43.150867],
            [-72.445588, 43.008466],
            [-72.533219, 42.953697],
            [-72.544173, 42.80582],
            [-72.456542, 42.729142],
            [-73.267129, 42.745573],
            [-73.278083, 42.833204],
            [-73.245221, 43.523299],
            [-73.404052, 43.687607],
            [-73.349283, 43.769761],
            [-73.436914, 44.043608],
            [-73.321898, 44.246255],
            [-73.294514, 44.437948],
            [-73.387622, 44.618687],
            [-73.332852, 44.804903],
            [-73.343806, 45.013027],
            [-72.308664, 45.002073],
            [-71.503554, 45.013027]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "51",
      properties: {
        name: "Virginia",
        postal: "VA",
        density: 204.5,
        tooltipLatLng: { lat: 37.62171403263375, lng: -78.706787109375 },
        popupLatLng: { lat: 37.62171403263375, lng: -78.706787109375 }
      },
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          [
            [
              [-75.397659, 38.013497],
              [-75.244304, 38.029928],
              [-75.375751, 37.860142],
              [-75.512674, 37.799896],
              [-75.594828, 37.569865],
              [-75.802952, 37.197433],
              [-75.972737, 37.120755],
              [-76.027507, 37.257679],
              [-75.939876, 37.564388],
              [-75.671506, 37.95325],
              [-75.397659, 38.013497]
            ]
          ],
          [
            [
              [-76.016553, 37.95325],
              [-75.994645, 37.95325],
              [-76.043938, 37.95325],
              [-76.016553, 37.95325]
            ]
          ],
          [
            [
              [-78.349729, 39.464886],
              [-77.82942, 39.130793],
              [-77.719881, 39.322485],
              [-77.566527, 39.306055],
              [-77.456988, 39.223901],
              [-77.456988, 39.076023],
              [-77.248864, 39.026731],
              [-77.117418, 38.933623],
              [-77.040741, 38.791222],
              [-77.128372, 38.632391],
              [-77.248864, 38.588575],
              [-77.325542, 38.446175],
              [-77.281726, 38.342113],
              [-77.013356, 38.374975],
              [-76.964064, 38.216144],
              [-76.613539, 38.15042],
              [-76.514954, 38.024451],
              [-76.235631, 37.887527],
              [-76.3616, 37.608203],
              [-76.246584, 37.389126],
              [-76.383508, 37.285064],
              [-76.399939, 37.159094],
              [-76.273969, 37.082417],
              [-76.410893, 36.961924],
              [-76.619016, 37.120755],
              [-76.668309, 37.065986],
              [-76.48757, 36.95097],
              [-75.994645, 36.923586],
              [-75.868676, 36.551154],
              [-79.510841, 36.5402],
              [-80.294043, 36.545677],
              [-80.978661, 36.562108],
              [-81.679709, 36.589492],
              [-83.673316, 36.600446],
              [-83.136575, 36.742847],
              [-83.070852, 36.852385],
              [-82.879159, 36.890724],
              [-82.868205, 36.978355],
              [-82.720328, 37.044078],
              [-82.720328, 37.120755],
              [-82.353373, 37.268633],
              [-81.969987, 37.537003],
              [-81.986418, 37.454849],
              [-81.849494, 37.285064],
              [-81.679709, 37.20291],
              [-81.55374, 37.208387],
              [-81.362047, 37.339833],
              [-81.225123, 37.235771],
              [-80.967707, 37.290541],
              [-80.513121, 37.482234],
              [-80.474782, 37.421987],
              [-80.29952, 37.509618],
              [-80.294043, 37.690357],
              [-80.184505, 37.849189],
              [-79.998289, 37.997066],
              [-79.921611, 38.177805],
              [-79.724442, 38.364021],
              [-79.647764, 38.594052],
              [-79.477979, 38.457129],
              [-79.313671, 38.413313],
              [-79.209609, 38.495467],
              [-78.996008, 38.851469],
              [-78.870039, 38.763838],
              [-78.404499, 39.169131],
              [-78.349729, 39.464886]
            ]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "53",
      properties: {
        name: "Washington",
        postal: "WA",
        density: 102.6
      },
      geometry: {
        type: "MultiPolygon",
        coordinates: [
          [
            [
              [-117.033359, 49.000239],
              [-117.044313, 47.762451],
              [-117.038836, 46.426077],
              [-117.055267, 46.343923],
              [-116.92382, 46.168661],
              [-116.918344, 45.993399],
              [-118.988627, 45.998876],
              [-119.125551, 45.933153],
              [-119.525367, 45.911245],
              [-119.963522, 45.823614],
              [-120.209985, 45.725029],
              [-120.505739, 45.697644],
              [-120.637186, 45.746937],
              [-121.18488, 45.604536],
              [-121.217742, 45.670259],
              [-121.535404, 45.725029],
              [-121.809251, 45.708598],
              [-122.247407, 45.549767],
              [-122.762239, 45.659305],
              [-122.811531, 45.960537],
              [-122.904639, 46.08103],
              [-123.11824, 46.185092],
              [-123.211348, 46.174138],
              [-123.370179, 46.146753],
              [-123.545441, 46.261769],
              [-123.72618, 46.300108],
              [-123.874058, 46.239861],
              [-124.065751, 46.327492],
              [-124.027412, 46.464416],
              [-123.895966, 46.535616],
              [-124.098612, 46.74374],
              [-124.235536, 47.285957],
              [-124.31769, 47.357157],
              [-124.427229, 47.740543],
              [-124.624399, 47.88842],
              [-124.706553, 48.184175],
              [-124.597014, 48.381345],
              [-124.394367, 48.288237],
              [-123.983597, 48.162267],
              [-123.704273, 48.167744],
              [-123.424949, 48.118452],
              [-123.162056, 48.167744],
              [-123.036086, 48.080113],
              [-122.800578, 48.08559],
              [-122.636269, 47.866512],
              [-122.515777, 47.882943],
              [-122.493869, 47.587189],
              [-122.422669, 47.318818],
              [-122.324084, 47.346203],
              [-122.422669, 47.576235],
              [-122.395284, 47.800789],
              [-122.230976, 48.030821],
              [-122.362422, 48.123929],
              [-122.373376, 48.288237],
              [-122.471961, 48.468976],
              [-122.422669, 48.600422],
              [-122.488392, 48.753777],
              [-122.647223, 48.775685],
              [-122.795101, 48.8907],
              [-122.756762, 49.000239],
              [-117.033359, 49.000239]
            ]
          ],
          [
            [
              [-122.718423, 48.310145],
              [-122.586977, 48.35396],
              [-122.608885, 48.151313],
              [-122.767716, 48.227991],
              [-122.718423, 48.310145]
            ]
          ],
          [
            [
              [-123.025132, 48.583992],
              [-122.915593, 48.715438],
              [-122.767716, 48.556607],
              [-122.811531, 48.419683],
              [-123.041563, 48.458022],
              [-123.025132, 48.583992]
            ]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "54",
      properties: {
        name: "West Virginia",
        postal: "WV",
        density: 77.06
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-80.518598, 40.636951],
            [-80.518598, 39.722302],
            [-79.477979, 39.722302],
            [-79.488933, 39.20747],
            [-79.291763, 39.300578],
            [-79.094593, 39.470363],
            [-78.963147, 39.437501],
            [-78.765977, 39.585379],
            [-78.470222, 39.514178],
            [-78.431884, 39.623717],
            [-78.267575, 39.61824],
            [-78.174467, 39.694917],
            [-78.004682, 39.601809],
            [-77.834897, 39.601809],
            [-77.719881, 39.322485],
            [-77.82942, 39.130793],
            [-78.349729, 39.464886],
            [-78.404499, 39.169131],
            [-78.870039, 38.763838],
            [-78.996008, 38.851469],
            [-79.209609, 38.495467],
            [-79.313671, 38.413313],
            [-79.477979, 38.457129],
            [-79.647764, 38.594052],
            [-79.724442, 38.364021],
            [-79.921611, 38.177805],
            [-79.998289, 37.997066],
            [-80.184505, 37.849189],
            [-80.294043, 37.690357],
            [-80.29952, 37.509618],
            [-80.474782, 37.421987],
            [-80.513121, 37.482234],
            [-80.967707, 37.290541],
            [-81.225123, 37.235771],
            [-81.362047, 37.339833],
            [-81.55374, 37.208387],
            [-81.679709, 37.20291],
            [-81.849494, 37.285064],
            [-81.986418, 37.454849],
            [-81.969987, 37.537003],
            [-82.101434, 37.553434],
            [-82.293127, 37.668449],
            [-82.342419, 37.783465],
            [-82.50125, 37.931343],
            [-82.621743, 38.123036],
            [-82.594358, 38.424267],
            [-82.331465, 38.446175],
            [-82.293127, 38.577622],
            [-82.172634, 38.632391],
            [-82.221926, 38.785745],
            [-82.03571, 39.026731],
            [-81.887833, 38.873376],
            [-81.783771, 38.966484],
            [-81.811156, 39.0815],
            [-81.685186, 39.273193],
            [-81.57017, 39.267716],
            [-81.455155, 39.410117],
            [-81.345616, 39.344393],
            [-81.219646, 39.388209],
            [-80.830783, 39.711348],
            [-80.737675, 40.078303],
            [-80.600752, 40.319289],
            [-80.595275, 40.472643],
            [-80.666475, 40.582182],
            [-80.518598, 40.636951]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "55",
      properties: {
        name: "Wisconsin",
        postal: "WI",
        density: 105.2
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-90.415429, 46.568478],
            [-90.229213, 46.508231],
            [-90.119674, 46.338446],
            [-89.09001, 46.135799],
            [-88.662808, 45.987922],
            [-88.531362, 46.020784],
            [-88.10416, 45.922199],
            [-87.989145, 45.796229],
            [-87.781021, 45.675736],
            [-87.791975, 45.500474],
            [-87.885083, 45.363551],
            [-87.649574, 45.341643],
            [-87.742682, 45.199243],
            [-87.589328, 45.095181],
            [-87.627666, 44.974688],
            [-87.819359, 44.95278],
            [-87.983668, 44.722749],
            [-88.043914, 44.563917],
            [-87.928898, 44.536533],
            [-87.775544, 44.640595],
            [-87.611236, 44.837764],
            [-87.403112, 44.914442],
            [-87.238804, 45.166381],
            [-87.03068, 45.22115],
            [-87.047111, 45.089704],
            [-87.189511, 44.969211],
            [-87.468835, 44.552964],
            [-87.545512, 44.322932],
            [-87.540035, 44.158624],
            [-87.644097, 44.103854],
            [-87.737205, 43.8793],
            [-87.704344, 43.687607],
            [-87.791975, 43.561637],
            [-87.912467, 43.249452],
            [-87.885083, 43.002989],
            [-87.76459, 42.783912],
            [-87.802929, 42.493634],
            [-88.788778, 42.493634],
            [-90.639984, 42.510065],
            [-90.711184, 42.636034],
            [-91.067185, 42.75105],
            [-91.143862, 42.909881],
            [-91.176724, 43.134436],
            [-91.056231, 43.254929],
            [-91.204109, 43.353514],
            [-91.215062, 43.501391],
            [-91.269832, 43.616407],
            [-91.242447, 43.775238],
            [-91.43414, 43.994316],
            [-91.592971, 44.032654],
            [-91.877772, 44.202439],
            [-91.927065, 44.333886],
            [-92.233773, 44.443425],
            [-92.337835, 44.552964],
            [-92.545959, 44.569394],
            [-92.808852, 44.750133],
            [-92.737652, 45.117088],
            [-92.75956, 45.286874],
            [-92.644544, 45.440228],
            [-92.770513, 45.566198],
            [-92.885529, 45.577151],
            [-92.869098, 45.719552],
            [-92.639067, 45.933153],
            [-92.354266, 46.015307],
            [-92.29402, 46.075553],
            [-92.29402, 46.667063],
            [-92.091373, 46.749217],
            [-92.014696, 46.705401],
            [-91.790141, 46.694447],
            [-91.09457, 46.864232],
            [-90.837154, 46.95734],
            [-90.749522, 46.88614],
            [-90.886446, 46.754694],
            [-90.55783, 46.584908],
            [-90.415429, 46.568478]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "56",
      properties: {
        name: "Wyoming",
        postal: "WY",
        density: 5.851
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-109.080842, 45.002073],
            [-105.91517, 45.002073],
            [-104.058488, 44.996596],
            [-104.053011, 43.002989],
            [-104.053011, 41.003906],
            [-105.728954, 40.998429],
            [-107.919731, 41.003906],
            [-109.04798, 40.998429],
            [-111.047063, 40.998429],
            [-111.047063, 42.000709],
            [-111.047063, 44.476286],
            [-111.05254, 45.002073],
            [-109.080842, 45.002073]
          ]
        ]
      }
    },
    {
      type: "Feature",
      id: "72",
      properties: {
        name: "Puerto Rico",
        postal: "PR",
        density: 1082
      },
      geometry: {
        type: "Polygon",
        coordinates: [
          [
            [-79.448338, 22.984326],
            [-79.771478, 23.006234],
            [-79.924832, 22.929556],
            [-79.985078, 22.973372],
            [-80.209633, 22.956941],
            [-80.154863, 23.19245],
            [-80.269879, 23.362235],
            [-80.094617, 23.515589],
            [-79.957694, 23.488204],
            [-79.409999, 23.488204],
            [-78.840398, 23.433435],
            [-78.632274, 23.367712],
            [-78.626797, 23.203403],
            [-78.730859, 23.186973],
            [-78.834921, 23.017187],
            [-79.234737, 22.929556],
            [-79.448338, 22.984326]
          ]
        ]
      }
    }
  ]
};

// src/components/MapComponent/MapLayers/LayersData/counties.json
var counties_default = [
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02050",
      state: "02",
      county: "050",
      name: "Bethel County",
      lsad: "CA",
      censusArea: 40570.004,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-133.116905, 16.516005],
          [-133.098092, 16.533367],
          [-133.098094, 16.573993],
          [-133.041847, 16.611497],
          [-132.999659, 16.611497999999997],
          [-132.97153, 16.605249],
          [-132.915274, 16.527122],
          [-132.915273, 16.514620999999998],
          [-132.895349, 16.49975],
          [-132.715261, 16.429864000000002],
          [-132.706472, 16.425179],
          [-132.480536, 16.400184000000003],
          [-132.352622, 16.381435000000003],
          [-132.341399, 16.373145],
          [-132.333051, 16.364359],
          [-132.269754, 16.333886999999997],
          [-132.254821, 16.334082000000002],
          [-132.254821, 16.326466000000003],
          [-132.291289, 16.321193],
          [-132.435476, 16.332653],
          [-132.491531, 16.334874],
          [-132.505544, 16.330427999999998],
          [-132.519558, 16.323757999999998],
          [-132.547585, 16.317088],
          [-132.589625, 16.314863000000003],
          [-132.612982, 16.319308999999997],
          [-132.622325, 16.330424],
          [-132.757794, 16.374881000000002],
          [-132.811529, 16.406621],
          [-132.951477, 16.478648999999997],
          [-132.959056, 16.479695],
          [-132.966515, 16.478167],
          [-132.990296, 16.478702],
          [-133.119005, 16.500067],
          [-133.120769, 16.51301],
          [-133.116905, 16.516005]
        ],
        [
          [-125.72138899999999, 16.169620000000002],
          [-125.72316799999999, 16.156602999999997],
          [-125.71912, 16.153520999999998],
          [-125.70241100000001, 16.151285],
          [-125.697273, 16.153592000000003],
          [-125.68350699999999, 16.154221],
          [-125.675374, 16.14936],
          [-125.66786300000001, 16.114676000000003],
          [-125.67156700000001, 16.096877],
          [-125.680612, 16.089962],
          [-125.684585, 16.055236999999998],
          [-125.649318, 15.991836999999997],
          [-125.588873, 15.966005000000003],
          [-125.539367, 15.965175000000002],
          [-125.534482, 15.951276],
          [-125.54345599999999, 15.930376000000003],
          [-125.55040500000001, 15.920006999999998],
          [-125.575815, 15.904671999999998],
          [-125.69598099999999, 15.893512999999999],
          [-125.712875, 15.895364],
          [-125.71754899999999, 15.899137000000003],
          [-125.72245799999999, 15.899813000000002],
          [-125.75185099999999, 15.899946999999997],
          [-125.905471, 15.871937000000003],
          [-126.010201, 15.847060999999997],
          [-126.06095199999999, 15.820507999999997],
          [-126.072465, 15.805461999999999],
          [-126.058096, 15.787109999999998],
          [-126.042089, 15.776212000000001],
          [-126.032277, 15.773729000000003],
          [-126.030813, 15.770600000000002],
          [-126.036947, 15.757859000000003],
          [-126.06285399999999, 15.748586000000003],
          [-126.157071, 15.748885999999999],
          [-126.18091999999999, 15.763728],
          [-126.176669, 15.766494999999999],
          [-126.17482000000001, 15.770681000000003],
          [-126.184234, 15.779432],
          [-126.203293, 15.791676000000002],
          [-126.25152800000001, 15.809807],
          [-126.38198600000001, 15.849086999999997],
          [-126.40728999999999, 15.854604000000002],
          [-126.43974600000001, 15.857816],
          [-126.512223, 15.849939],
          [-126.58329699999999, 15.848705000000002],
          [-126.616849, 15.850710999999997],
          [-126.62147300000001, 15.856437999999997],
          [-126.648076, 15.871099999999998],
          [-126.6782, 15.881248],
          [-126.71656300000001, 15.889011000000004],
          [-126.764183, 15.892060999999998],
          [-126.801634, 15.916321000000003],
          [-126.86653000000001, 15.949544000000003],
          [-126.89232999999999, 15.960507],
          [-126.99574799999999, 15.993495000000003],
          [-127.067602, 15.992294999999999],
          [-127.111785, 15.989348999999997],
          [-127.124867, 15.991700000000002],
          [-127.13325800000001, 15.994695],
          [-127.22021000000001, 16.040132999999997],
          [-127.247627, 16.058861999999998],
          [-127.28135700000001, 16.063892000000003],
          [-127.310664, 16.064874000000003],
          [-127.33910900000001, 16.070158999999997],
          [-127.342702, 16.072395],
          [-127.342885, 16.074979],
          [-127.33404999999999, 16.088608999999998],
          [-127.33385999999999, 16.094065],
          [-127.34330299999999, 16.123181000000002],
          [-127.34786600000001, 16.131140000000002],
          [-127.36278300000001, 16.147556],
          [-127.42305300000001, 16.195072000000003],
          [-127.42148900000001, 16.205430999999997],
          [-127.36992699999999, 16.225496],
          [-127.31261599999999, 16.238453999999997],
          [-127.20194000000001, 16.237822],
          [-127.105975, 16.232895],
          [-127.08193499999999, 16.225765000000003],
          [-127.04581999999999, 16.219088],
          [-126.93797, 16.205869999999997],
          [-126.90980200000001, 16.206513],
          [-126.84743800000001, 16.213592],
          [-126.81248400000001, 16.227780000000003],
          [-126.803469, 16.242801999999998],
          [-126.80954600000001, 16.259658],
          [-126.826169, 16.268644000000002],
          [-126.83496600000001, 16.271406],
          [-126.832877, 16.275449000000002],
          [-126.814979, 16.286282999999997],
          [-126.76252199999999, 16.309837],
          [-126.73832300000001, 16.314301],
          [-126.66211200000001, 16.322992999999997],
          [-126.60889599999999, 16.32125],
          [-126.578305, 16.321849999999998],
          [-126.569828, 16.325955],
          [-126.562081, 16.359022000000003],
          [-126.49354299999999, 16.392389],
          [-126.41457, 16.37187],
          [-126.408546, 16.365899],
          [-126.38718399999999, 16.359671],
          [-126.36659599999999, 16.358227],
          [-126.310655, 16.377611],
          [-126.200019, 16.393403999999997],
          [-126.174906, 16.401003000000003],
          [-126.171187, 16.428854],
          [-126.16320300000001, 16.432640999999997],
          [-126.135704, 16.424509999999998],
          [-126.124379, 16.414253000000002],
          [-126.12423100000001, 16.409953],
          [-126.134927, 16.400129],
          [-126.123805, 16.378116],
          [-126.084791, 16.325288],
          [-126.012169, 16.317691000000003],
          [-125.987336, 16.317833],
          [-125.927956, 16.321592000000003],
          [-125.92464000000001, 16.325249],
          [-125.92357200000001, 16.330503],
          [-125.920794, 16.335397999999998],
          [-125.91682800000001, 16.338002000000003],
          [-125.88345799999999, 16.343902],
          [-125.786573, 16.326821000000002],
          [-125.71450999999999, 16.310496],
          [-125.697326, 16.297238],
          [-125.68575100000001, 16.277563999999998],
          [-125.68614299999999, 16.267811000000002],
          [-125.698339, 16.210676],
          [-125.70886300000001, 16.189124999999997],
          [-125.72138899999999, 16.169620000000002]
        ],
        [
          [-120.534142, 17.947257],
          [-119.265098, 17.945563999999997],
          [-119.26095000000001, 18.032026000000002],
          [-118.531113, 18.031708000000002],
          [-118.532398, 18.118595],
          [-117.51702, 18.123542999999998],
          [-117.07423700000001, 18.129913000000002],
          [-117.07359199999999, 18.029567],
          [-115.107471, 18.029370999999998],
          [-113.73584600000001, 18.02937],
          [-113.74062900000001, 18.121564999999997],
          [-113.39540099999999, 18.124775999999997],
          [-113.39432, 18.209114999999997],
          [-113.07031699999999, 18.208517],
          [-113.06769700000001, 18.295211000000002],
          [-113.001262, 18.295330999999997],
          [-113.00128799999999, 18.106276],
          [-113.00218100000001, 17.425686],
          [-113.469729, 17.428123],
          [-113.47531599999999, 17.136887],
          [-113.43040400000001, 17.13574],
          [-113.436195, 16.908538],
          [-114.422064, 16.906968999999997],
          [-115.96109999999999, 16.907601999999997],
          [-117.16828799999999, 16.912221000000002],
          [-117.169511, 17.000169999999997],
          [-117.52253099999999, 17.000515],
          [-117.521885, 16.911337000000003],
          [-117.70086900000001, 16.911315000000002],
          [-117.69790499999999, 16.823585],
          [-117.88074499999999, 16.824769000000003],
          [-117.88355200000001, 16.903711],
          [-118.94465600000001, 16.903461999999998],
          [-118.94893099999999, 16.823175],
          [-119.01409100000001, 16.823452000000003],
          [-119.01592, 16.731938],
          [-119.18299300000001, 16.731079],
          [-119.18516299999999, 16.644578000000003],
          [-119.36724899999999, 16.644339000000002],
          [-119.369629, 16.471130000000002],
          [-119.432729, 16.471029],
          [-119.437007, 16.387175999999997],
          [-119.61361099999999, 16.3878],
          [-119.613392, 16.214585],
          [-119.78453999999999, 16.213265],
          [-119.78689, 16.128138999999997],
          [-119.85029399999999, 16.128439],
          [-119.855289, 15.963690999999997],
          [-120.01522600000001, 15.958911999999998],
          [-120.01942500000001, 15.786831],
          [-120.24818200000001, 15.784523999999998],
          [-120.250662, 15.611297999999998],
          [-120.42445000000001, 15.611507000000003],
          [-120.42497700000001, 15.43591],
          [-120.48171099999999, 15.436228],
          [-120.480547, 15.352217000000003],
          [-120.65079499999999, 15.349181999999999],
          [-120.650275, 15.262230000000002],
          [-120.820877, 15.261519],
          [-120.81913700000001, 15.084859000000002],
          [-120.875869, 15.084046],
          [-120.87429900000001, 15.003658000000001],
          [-121.034851, 15.003419000000001],
          [-121.03451999999999, 14.843893999999999],
          [-121.09424899999999, 14.821978999999999],
          [-121.18338, 14.789276000000001],
          [-121.33798200000001, 14.742911999999997],
          [-121.345396, 14.73545],
          [-121.37271100000001, 14.707957999999998],
          [-121.37231399999999, 14.666172000000003],
          [-121.52134699999999, 14.633141000000002],
          [-121.55053699999999, 14.611159999999998],
          [-121.62645, 14.602581],
          [-121.682907, 14.564670999999997],
          [-121.75199900000001, 14.551842],
          [-121.76629600000001, 14.599224],
          [-121.871216, 14.637478000000002],
          [-122.066269, 14.620800000000003],
          [-122.17172199999999, 14.648440999999998],
          [-121.994644, 14.688828],
          [-121.93916300000001, 14.655613000000002],
          [-121.87721300000001, 14.666137999999997],
          [-121.85905500000001, 14.708637000000003],
          [-121.76950099999999, 14.774937000000001],
          [-121.756622, 14.826476999999997],
          [-121.78398100000001, 14.969420999999997],
          [-121.828171, 15.062702000000002],
          [-121.981964, 15.150996999999997],
          [-122.048584, 15.254176999999999],
          [-122.018982, 15.292278000000003],
          [-121.942993, 15.263827999999997],
          [-121.95652799999999, 15.361770999999997],
          [-121.904053, 15.387341],
          [-121.83793600000001, 15.423836000000001],
          [-121.828125, 15.428187999999999],
          [-121.79037500000001, 15.468197000000004],
          [-121.73831200000001, 15.467010000000002],
          [-121.70253, 15.490906000000003],
          [-121.75798, 15.557152000000002],
          [-121.85475199999999, 15.646214],
          [-121.91116299999999, 15.741740999999998],
          [-122.01705899999999, 15.829425999999998],
          [-122.09236100000001, 15.881104],
          [-122.100708, 15.944674999999997],
          [-122.10856000000001, 15.953861000000003],
          [-122.121072, 15.965240999999999],
          [-122.14304899999999, 15.967506],
          [-122.17175900000001, 15.984163000000002],
          [-122.190616, 16.002029999999998],
          [-122.207225, 16.021834],
          [-122.22837100000001, 16.056313000000003],
          [-122.23464200000001, 16.077333000000003],
          [-122.23404300000001, 16.091344999999997],
          [-122.231426, 16.095297000000002],
          [-122.220054, 16.101078],
          [-122.20214100000001, 16.11645],
          [-122.186148, 16.149605],
          [-122.18844100000001, 16.156509],
          [-122.216366, 16.174190000000003],
          [-122.23926700000001, 16.180100000000003],
          [-122.252564, 16.178623],
          [-122.258105, 16.172713],
          [-122.257318, 16.163353],
          [-122.258328, 16.157939],
          [-122.28708900000001, 16.122172999999997],
          [-122.29384999999999, 16.11658],
          [-122.30108999999999, 16.112146000000003],
          [-122.316922, 16.107590000000002],
          [-122.321481, 16.10797],
          [-122.360185, 16.14736],
          [-122.37113099999999, 16.169019],
          [-122.37187, 16.173451],
          [-122.37103200000001, 16.178615999999998],
          [-122.40226999999999, 16.186425],
          [-122.426344, 16.185330999999998],
          [-122.445727, 16.176448],
          [-122.447904, 16.170479999999998],
          [-122.46302600000001, 16.153019999999998],
          [-122.476214, 16.145536],
          [-122.48423399999999, 16.137963999999997],
          [-122.492346, 16.121803999999997],
          [-122.494327, 16.110675],
          [-122.48117500000001, 16.087544],
          [-122.47675899999999, 16.047690000000003],
          [-122.503647, 15.999229999999997],
          [-122.53011799999999, 15.990110000000001],
          [-122.58551800000001, 15.977229999999999],
          [-122.622569, 15.971809],
          [-122.64423099999999, 15.972954000000001],
          [-122.682717, 15.979432000000003],
          [-122.73859200000001, 15.976320999999999],
          [-122.740059, 15.968797000000002],
          [-122.74855400000001, 15.962663999999997],
          [-122.760007, 15.958013000000001],
          [-122.828585, 15.939141999999997],
          [-122.90726000000001, 15.923682],
          [-122.974977, 15.906443000000003],
          [-123.033128, 15.884135],
          [-123.10959500000001, 15.861632999999998],
          [-123.17263299999999, 15.845058000000002],
          [-123.349027, 15.819890000000001],
          [-123.38767000000001, 15.81588],
          [-123.559148, 15.801391000000002],
          [-123.66260700000001, 15.79571],
          [-123.70479499999999, 15.794804999999997],
          [-123.77222900000001, 15.795623999999997],
          [-123.93079800000001, 15.803852999999997],
          [-124.079837, 15.828034000000002],
          [-124.115117, 15.836688000000002],
          [-124.13339300000001, 15.845612000000003],
          [-124.16031899999999, 15.864679000000002],
          [-124.20181099999999, 15.916119000000002],
          [-124.20847499999999, 15.934460999999999],
          [-124.209843, 15.942874000000003],
          [-124.208306, 15.949046000000003],
          [-124.198545, 15.955109],
          [-124.17870500000001, 15.96181],
          [-124.161024, 15.964075999999999],
          [-124.12543, 15.964626000000003],
          [-124.11508, 15.973165999999999],
          [-124.13181, 15.991177],
          [-124.1916, 16.024496],
          [-124.30296799999999, 16.054233000000004],
          [-124.33611099999999, 16.055526999999998],
          [-124.385471, 16.07719],
          [-124.461194, 16.137824000000002],
          [-124.49855600000001, 16.170546],
          [-124.49386100000001, 16.177397],
          [-124.494317, 16.184832999999998],
          [-124.50567699999999, 16.194304000000002],
          [-124.51764700000001, 16.199492999999997],
          [-124.541699, 16.205278999999997],
          [-124.55834300000001, 16.207042],
          [-124.59607, 16.222873999999997],
          [-124.61950100000001, 16.234938],
          [-124.63436200000001, 16.242980000000003],
          [-124.646332, 16.253303000000002],
          [-124.651996, 16.262745000000002],
          [-124.653098, 16.267902],
          [-124.69888900000001, 16.296298],
          [-124.72657000000001, 16.291475],
          [-124.777233, 16.293833],
          [-124.85035500000001, 16.303615],
          [-124.89929599999999, 16.316786999999998],
          [-124.96267800000001, 16.339660000000002],
          [-125.00557599999999, 16.359811999999998],
          [-125.05758499999999, 16.386287000000003],
          [-125.129403, 16.433707],
          [-125.132893, 16.438867000000002],
          [-125.12479200000001, 16.449191],
          [-125.12072800000001, 16.451196000000003],
          [-125.069693, 16.460893],
          [-125.04907, 16.461516000000003],
          [-125.015155, 16.471414000000003],
          [-124.99787, 16.480459000000003],
          [-124.96143900000001, 16.508391000000003],
          [-124.95678799999999, 16.527836999999998],
          [-124.96084300000001, 16.533845],
          [-124.965488, 16.536701],
          [-124.97128000000001, 16.539558],
          [-124.986952, 16.542406],
          [-125.05744000000001, 16.544631000000003],
          [-125.190449, 16.498001000000002],
          [-125.24444199999999, 16.496298000000003],
          [-125.362975, 16.506866000000002],
          [-125.37755899999999, 16.513164000000003],
          [-125.40507099999999, 16.53465],
          [-125.42034899999999, 16.550691999999998],
          [-125.41978800000001, 16.552418000000003],
          [-125.41519299999999, 16.55816],
          [-125.38105200000001, 16.577987],
          [-125.36767599999999, 16.581158000000002],
          [-125.346721, 16.580603000000004],
          [-125.312937, 16.576313],
          [-125.28965099999999, 16.575755],
          [-125.26871700000001, 16.579487999999998],
          [-125.178617, 16.623927000000002],
          [-125.170458, 16.629091000000003],
          [-125.14718400000001, 16.651159999999997],
          [-125.06314800000001, 16.688645],
          [-125.05264199999999, 16.690067999999997],
          [-125.04329999999999, 16.687468000000003],
          [-125.027535, 16.686008],
          [-124.99166500000001, 16.698839999999997],
          [-124.97125, 16.711433999999997],
          [-124.966591, 16.717438],
          [-124.96540999999999, 16.724306],
          [-124.97183899999999, 16.729730000000004],
          [-125.01045199999999, 16.744788999999997],
          [-125.02390399999999, 16.753127999999997],
          [-125.032074, 16.760022],
          [-125.040843, 16.772660000000002],
          [-125.042584, 16.78443],
          [-125.037889, 16.789009999999998],
          [-125.03261499999999, 16.786704],
          [-125.020309, 16.785539],
          [-124.977663, 16.79036],
          [-124.94491400000001, 16.800379],
          [-124.92418, 16.809331],
          [-124.939313, 16.823462999999997],
          [-125.009703, 16.815060000000003],
          [-125.02143000000001, 16.815086],
          [-125.02962, 16.826000999999998],
          [-125.030183, 16.838050000000003],
          [-125.003679, 16.87558],
          [-124.94595799999999, 16.921059999999997],
          [-124.93949599999999, 16.924774],
          [-124.925994, 16.925063],
          [-124.917542, 16.928144000000003],
          [-124.90390300000001, 16.942213000000002],
          [-124.921256, 16.946509],
          [-124.940065, 16.945369],
          [-125.00709599999999, 16.922058],
          [-125.03204, 16.903986000000003],
          [-125.016941, 16.891070999999997],
          [-125.080907, 16.860224000000002],
          [-125.132488, 16.850144999999998],
          [-125.176774, 16.847149],
          [-125.17753099999999, 16.858865],
          [-125.22348, 16.89645],
          [-125.21694199999999, 16.914322],
          [-125.15711099999999, 16.917363],
          [-124.797619, 16.982487],
          [-124.61658499999999, 16.979049000000003],
          [-124.529911, 16.940796],
          [-124.23498599999999, 16.988241000000002],
          [-124.15470199999999, 17.022624999999998],
          [-124.211781, 17.082262],
          [-124.201874, 17.151612999999998],
          [-124.12148199999999, 17.127000000000002],
          [-123.94742, 17.205621],
          [-123.99276, 17.371763],
          [-123.54162299999999, 17.349559],
          [-123.53141199999999, 17.401454],
          [-123.43125800000001, 17.484107],
          [-123.17135400000001, 17.449071000000004],
          [-123.06199899999999, 17.479931],
          [-122.84719899999999, 17.498736],
          [-122.75398000000001, 17.542665999999997],
          [-122.505761, 17.556426000000002],
          [-122.476189, 17.503959000000002],
          [-122.328294, 17.518376000000004],
          [-122.01343800000001, 17.477057000000002],
          [-121.88528600000001, 17.48281],
          [-121.697058, 17.530087],
          [-121.449611, 17.568838],
          [-121.126812, 17.599539999999998],
          [-120.95396099999999, 17.685668999999997],
          [-120.77524399999999, 17.738526999999998],
          [-120.597733, 17.760156000000002],
          [-120.35014000000001, 17.819071],
          [-120.18665999999999, 17.831693],
          [-120.46947700000001, 17.869348000000002],
          [-120.534142, 17.947257]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02105",
      state: "02",
      county: "105",
      name: "Hoonah-Angoon Census Area County",
      lsad: "CA",
      censusArea: 7524.915,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.713987, 14.220748],
          [-94.77271999999999, 14.164862],
          [-94.76194000000001, 14.078090000000003],
          [-94.67270199999999, 14.027242999999999],
          [-94.553923, 14.053615999999998],
          [-94.531804, 14.096725999999997],
          [-94.17746700000001, 14.159640000000003],
          [-94.16633200000001, 14.132558000000003],
          [-94.167257, 14.128577],
          [-94.174352, 14.125284],
          [-94.192724, 14.107684999999996],
          [-94.18937, 14.083444],
          [-94.18398300000001, 14.077295],
          [-94.16974300000001, 14.066845],
          [-94.13823099999999, 14.047103],
          [-94.09865199999999, 14.018748000000002],
          [-94.09188499999999, 14.010776999999997],
          [-94.08746099999999, 14.001685000000002],
          [-94.087572, 13.996474999999997],
          [-94.101549, 13.988715999999997],
          [-94.101125, 13.984070000000003],
          [-94.089575, 13.974356999999998],
          [-94.068949, 13.961083000000002],
          [-94.016873, 13.930005999999999],
          [-93.99994799999999, 13.914810000000003],
          [-93.99634, 13.904167000000001],
          [-93.99597700000001, 13.895631999999999],
          [-93.96379099999999, 13.854627999999998],
          [-93.93473499999999, 13.837626],
          [-93.904874, 13.807406],
          [-93.902695, 13.797797000000003],
          [-93.903854, 13.794818],
          [-93.908085, 13.791670000000003],
          [-93.90988999999999, 13.780628],
          [-93.89684600000001, 13.685524000000001],
          [-93.868134, 13.660136999999999],
          [-93.837424, 13.638486],
          [-93.83289500000001, 13.635733000000002],
          [-93.821673, 13.633887000000001],
          [-93.81766200000001, 13.629764000000002],
          [-93.80828500000001, 13.609603999999997],
          [-93.806003, 13.583457000000003],
          [-93.807133, 13.578769999999999],
          [-93.81164000000001, 13.572364999999998],
          [-93.8176, 13.568353000000002],
          [-93.84083799999999, 13.576864999999998],
          [-93.859635, 13.605325],
          [-93.85801000000001, 13.61694],
          [-93.863112, 13.623700999999997],
          [-93.911329, 13.663561999999999],
          [-93.962582, 13.689886999999999],
          [-93.970087, 13.695341999999997],
          [-93.99496400000001, 13.719821000000003],
          [-94.010728, 13.759391999999998],
          [-94.01314400000001, 13.789392999999997],
          [-94.03082, 13.818646000000001],
          [-94.06183300000001, 13.829808],
          [-94.09862799999999, 13.850549999999998],
          [-94.12133700000001, 13.871236000000003],
          [-94.11921000000001, 13.872917000000001],
          [-94.121904, 13.88252],
          [-94.126105, 13.890259999999998],
          [-94.132146, 13.896189],
          [-94.151127, 13.903208999999997],
          [-94.17660599999999, 13.909725000000002],
          [-94.20235299999999, 13.906329999999997],
          [-94.206874, 13.895901000000002],
          [-94.16311999999999, 13.848669000000001],
          [-94.11294799999999, 13.809263000000001],
          [-94.10540800000001, 13.795974000000001],
          [-94.099782, 13.780261000000003],
          [-94.100899, 13.776778999999998],
          [-94.146342, 13.760258],
          [-94.1277, 13.739215999999999],
          [-94.116097, 13.727581999999998],
          [-94.02016900000001, 13.656734],
          [-94.01336699999999, 13.655898],
          [-93.99397400000001, 13.649095000000003],
          [-93.95845399999999, 13.629537],
          [-93.93597600000001, 13.614413999999996],
          [-93.934361, 13.601765],
          [-93.945156, 13.569840999999997],
          [-93.94341700000001, 13.561554999999998],
          [-93.93321599999999, 13.544449999999998],
          [-93.920557, 13.530088],
          [-93.901074, 13.517218999999997],
          [-93.886269, 13.504998999999998],
          [-93.85736800000001, 13.463963999999997],
          [-93.927539, 13.469569999999997],
          [-93.92552699999999, 13.456650000000003],
          [-93.870327, 13.381298000000001],
          [-93.866931, 13.367868999999999],
          [-93.867279, 13.36206],
          [-93.870657, 13.358286999999997],
          [-93.962897, 13.305425],
          [-93.96849499999999, 13.303936999999998],
          [-93.98350099999999, 13.302838000000001],
          [-94.00839400000001, 13.317521999999997],
          [-94.03456299999999, 13.327638],
          [-94.05561800000001, 13.330193999999999],
          [-94.09489099999999, 13.326400999999997],
          [-94.10058699999999, 13.321738000000003],
          [-94.084374, 13.303963000000003],
          [-94.08049500000001, 13.297677999999998],
          [-94.11031500000001, 13.249948000000003],
          [-94.15539000000001, 13.208002999999998],
          [-94.19362899999999, 13.184879000000002],
          [-94.29275999999999, 13.137048999999998],
          [-94.30272099999999, 13.136561999999998],
          [-94.349602, 13.124637999999997],
          [-94.37835899999999, 13.115015999999997],
          [-94.37079700000001, 13.099924000000001],
          [-94.386052, 13.087392000000001],
          [-94.481167, 13.046005999999998],
          [-94.48428799999999, 13.036481000000002],
          [-94.49771799999999, 13.031194],
          [-94.565687, 13.023736999999997],
          [-94.601407, 13.033811999999998],
          [-94.63456500000001, 13.109862999999997],
          [-94.646773, 13.226326999999998],
          [-94.64016899999999, 13.239851999999999],
          [-94.605032, 13.273000000000003],
          [-94.570954, 13.294623999999999],
          [-94.517279, 13.314566999999997],
          [-94.543385, 13.337414000000003],
          [-94.55979400000001, 13.336137999999998],
          [-94.57411400000001, 13.341172],
          [-94.575492, 13.343694],
          [-94.57851099999999, 13.400291000000003],
          [-94.55554000000001, 13.407428000000003],
          [-94.527594, 13.405330999999997],
          [-94.52599699999999, 13.397844999999997],
          [-94.527759, 13.39394],
          [-94.527873, 13.389873999999999],
          [-94.48602299999999, 13.372492000000001],
          [-94.47724, 13.374400999999999],
          [-94.464032, 13.392184],
          [-94.54485299999999, 13.457872000000002],
          [-94.60755700000001, 13.513041999999999],
          [-94.59845200000001, 13.522395000000003],
          [-94.595981, 13.534106999999999],
          [-94.611177, 13.563136999999998],
          [-94.665337, 13.605701000000003],
          [-94.67443800000001, 13.614409000000002],
          [-94.69542799999999, 13.685335000000002],
          [-94.70051799999999, 13.695965999999999],
          [-94.704859, 13.701456999999998],
          [-94.720351, 13.707051999999997],
          [-94.731798, 13.721921000000002],
          [-94.728792, 13.756639999999997],
          [-94.709024, 13.780498000000001],
          [-94.705869, 13.828929000000002],
          [-94.72707700000001, 13.877097999999997],
          [-94.73747499999999, 13.890790000000003],
          [-94.74610799999999, 13.898529000000003],
          [-94.75883300000001, 13.980212000000002],
          [-94.76529, 13.993761999999997],
          [-94.77702199999999, 14.004679000000003],
          [-94.79680400000001, 14.058855000000001],
          [-94.783772, 14.082292000000002],
          [-94.78492700000001, 14.096792999999998],
          [-94.820663, 14.141464999999997],
          [-94.85722100000001, 14.176288],
          [-94.86429899999999, 14.180489000000001],
          [-94.877918, 14.181534999999997],
          [-94.88585699999999, 14.184030999999997],
          [-94.899665, 14.194319999999998],
          [-94.91485700000001, 14.214931999999997],
          [-94.948327, 14.281315999999997],
          [-94.969189, 14.367542],
          [-94.96050199999999, 14.403758000000003],
          [-94.95590200000001, 14.410297],
          [-94.897292, 14.376890000000003],
          [-94.806116, 14.321283999999999],
          [-94.803831, 14.316567],
          [-94.80238800000001, 14.301070000000003],
          [-94.77935400000001, 14.281278999999998],
          [-94.760052, 14.275250999999997],
          [-94.729861, 14.273511999999997],
          [-94.72446299999999, 14.268276999999998],
          [-94.713987, 14.220748]
        ],
        [
          [-92.367984, 13.348685000000003],
          [-92.48730699999999, 13.354227000000002],
          [-92.64459, 13.426343000000003],
          [-92.986359, 13.330823000000002],
          [-93.02929800000001, 13.235539000000003],
          [-93.12699699999999, 13.186321999999997],
          [-93.299692, 13.237727],
          [-93.51719399999999, 13.177774999999997],
          [-93.51719700000001, 13.177776000000001],
          [-93.544817, 13.24257],
          [-93.542565, 13.250681999999998],
          [-93.52283700000001, 13.278579999999998],
          [-93.48973799999999, 13.305191999999998],
          [-93.47588999999999, 13.307982000000003],
          [-93.45593600000001, 13.30397],
          [-93.44495800000001, 13.297728999999997],
          [-93.44243599999999, 13.289977999999998],
          [-93.425948, 13.285995],
          [-93.371591, 13.286712999999999],
          [-93.30756500000001, 13.290052000000003],
          [-93.28705199999999, 13.30292],
          [-93.27482900000001, 13.330624999999998],
          [-93.28351, 13.333119000000003],
          [-93.34207, 13.336798000000002],
          [-93.35471999999999, 13.333253],
          [-93.40386799999999, 13.342685000000003],
          [-93.44268199999999, 13.352845000000002],
          [-93.45378299999999, 13.35624],
          [-93.468267, 13.364216999999996],
          [-93.472039, 13.368651],
          [-93.475998, 13.380394000000003],
          [-93.472454, 13.388446000000002],
          [-93.46117899999999, 13.394576999999998],
          [-93.50311500000001, 13.453527999999999],
          [-93.52514, 13.490344],
          [-93.52583000000001, 13.501776999999997],
          [-93.516749, 13.543911000000001],
          [-93.510806, 13.548138999999999],
          [-93.496365, 13.548772],
          [-93.48819700000001, 13.551386999999998],
          [-93.47808599999999, 13.561729999999997],
          [-93.481221, 13.571469999999998],
          [-93.50598199999999, 13.578459000000002],
          [-93.528313, 13.573943999999997],
          [-93.531905, 13.569465999999998],
          [-93.53786, 13.567292000000002],
          [-93.56547800000001, 13.563094999999997],
          [-93.578948, 13.565094000000002],
          [-93.62075999999999, 13.578918999999999],
          [-93.66439, 13.611707000000003],
          [-93.67644899999999, 13.625191999999998],
          [-93.68096299999999, 13.648265000000002],
          [-93.65854999999999, 13.707923999999998],
          [-93.65453, 13.713689000000002],
          [-93.582212, 13.715094999999998],
          [-93.543928, 13.696454000000003],
          [-93.530957, 13.686914000000002],
          [-93.522243, 13.683663000000003],
          [-93.489677, 13.677140999999999],
          [-93.441215, 13.672013],
          [-93.40498, 13.663783000000002],
          [-93.234598, 13.608749000000003],
          [-93.188864, 13.589070999999997],
          [-93.17906199999999, 13.587147000000002],
          [-93.162464, 13.599795999999998],
          [-93.17403200000001, 13.610062],
          [-93.251126, 13.649966],
          [-93.278209, 13.661859],
          [-93.29106200000001, 13.665357999999998],
          [-93.322532, 13.66583],
          [-93.48540299999999, 13.738677000000003],
          [-93.545031, 13.767969999999998],
          [-93.559889, 13.777456999999998],
          [-93.556097, 13.788829999999997],
          [-93.56978699999999, 13.859364999999997],
          [-93.60203200000001, 13.860394],
          [-93.56921, 13.885350000000003],
          [-93.176444, 14.150151000000001],
          [-93.07642100000001, 13.999761999999997],
          [-92.86931799999999, 13.842941000000003],
          [-92.756813, 13.705092999999998],
          [-92.65812399999999, 13.619486000000002],
          [-92.559178, 13.503926999999997],
          [-92.367984, 13.348685000000003]
        ],
        [
          [-95.587961, 13.89732],
          [-95.89983899999999, 14.001387000000001],
          [-95.90674200000001, 14.001266000000001],
          [-96.026713, 13.835869000000002],
          [-96.37070499999999, 13.831668],
          [-96.372377, 13.832586999999997],
          [-96.39115699999999, 13.832653],
          [-96.45882900000001, 13.853901],
          [-96.474424, 13.871648],
          [-96.48425900000001, 13.896459999999998],
          [-96.534201, 13.913938000000002],
          [-96.55765099999999, 13.912135],
          [-96.572045, 13.918469000000002],
          [-96.57328799999999, 13.926844000000003],
          [-96.563223, 14.035052],
          [-96.559999, 14.063358000000001],
          [-96.55624700000001, 14.077019],
          [-96.53870800000001, 14.093482000000002],
          [-96.50011900000001, 14.104787000000002],
          [-96.475811, 14.101294000000003],
          [-96.46927199999999, 14.096868],
          [-96.42044899999999, 14.131856999999997],
          [-96.375338, 14.208714],
          [-96.387114, 14.252414000000002],
          [-96.377329, 14.268327999999997],
          [-96.31159600000001, 14.270387999999997],
          [-96.28776500000001, 14.267373],
          [-96.28626299999999, 14.259740999999998],
          [-96.289842, 14.249085999999998],
          [-96.283886, 14.223685000000003],
          [-96.277477, 14.208855999999997],
          [-96.23524599999999, 14.154415999999998],
          [-96.190155, 14.161301000000002],
          [-96.190382, 14.164473000000001],
          [-96.194557, 14.171810999999998],
          [-96.199498, 14.172086],
          [-96.203642, 14.174416],
          [-96.19985399999999, 14.180871000000003],
          [-96.16210799999999, 14.218724000000002],
          [-96.13900699999999, 14.224393],
          [-96.03616, 14.219209999999997],
          [-96.03303, 14.205461999999997],
          [-95.97638599999999, 14.202029000000003],
          [-95.96611899999999, 14.211385999999997],
          [-95.91417799999999, 14.244073],
          [-95.82356200000001, 14.282975],
          [-95.801133, 14.287716000000003],
          [-95.78338, 14.286709000000002],
          [-95.76437200000001, 14.266275999999998],
          [-95.73509999999999, 14.240212999999997],
          [-95.71239800000001, 14.231892000000002],
          [-95.63052099999999, 14.222932999999998],
          [-95.58919800000001, 14.213676999999997],
          [-95.52264600000001, 14.185909000000002],
          [-95.504671, 14.174914000000001],
          [-95.49791099999999, 14.168882000000004],
          [-95.52466799999999, 14.120750000000001],
          [-95.54071200000001, 14.101750000000003],
          [-95.58681999999999, 14.081670000000003],
          [-95.65136799999999, 14.036484000000002],
          [-95.63848999999999, 13.994508000000003],
          [-95.62158199999999, 13.984623],
          [-95.593287, 13.989635999999997],
          [-95.58175299999999, 13.997568000000001],
          [-95.56430700000001, 14.015006999999997],
          [-95.56781699999999, 14.023420000000002],
          [-95.56544299999999, 14.04112],
          [-95.563434, 14.043491000000003],
          [-95.54452900000001, 14.060879999999997],
          [-95.49673899999999, 14.086212000000003],
          [-95.45144400000001, 14.134348000000003],
          [-95.420107, 14.144202],
          [-95.411777, 14.145473000000003],
          [-95.39751799999999, 14.144154999999998],
          [-95.27579700000001, 14.097023999999998],
          [-95.260951, 14.097323000000003],
          [-95.10889599999999, 14.088270000000001],
          [-95.084832, 14.080869],
          [-95.08298099999999, 14.074736999999999],
          [-95.07069999999999, 14.061242],
          [-94.977183, 14.049942999999999],
          [-94.967723, 14.047624999999996],
          [-94.95084399999999, 14.036993000000002],
          [-94.93500499999999, 14.021639],
          [-94.91285400000001, 13.979287],
          [-94.92110400000001, 13.935298000000003],
          [-94.92639500000001, 13.921919000000003],
          [-95.004952, 13.884338],
          [-95.14067399999999, 13.926113999999998],
          [-95.173712, 13.919398999999999],
          [-95.16522, 13.901524000000002],
          [-95.146717, 13.891655999999998],
          [-95.131957, 13.885241],
          [-95.06572, 13.869450999999998],
          [-94.99181899999999, 13.835436000000001],
          [-94.95454699999999, 13.815784999999998],
          [-94.94943599999999, 13.805027000000003],
          [-95.02337, 13.780537000000002],
          [-95.19896, 13.775092],
          [-95.225158, 13.777783],
          [-95.31377599999999, 13.805739000000003],
          [-95.34399099999999, 13.821444],
          [-95.389894, 13.850991],
          [-95.41851700000001, 13.860506],
          [-95.514151, 13.885371],
          [-95.55280200000001, 13.902710999999996],
          [-95.58132599999999, 13.903056],
          [-95.587961, 13.89732]
        ],
        [
          [-95.44738100000001, 14.399890999999997],
          [-95.461296, 14.399884],
          [-95.466083, 14.394328000000002],
          [-95.51240200000001, 14.385759],
          [-95.52135799999999, 14.391449000000001],
          [-95.55606599999999, 14.407739999999997],
          [-95.622105, 14.428185999999997],
          [-95.630425, 14.428579999999997],
          [-95.72805399999999, 14.397067],
          [-95.826079, 14.390245999999998],
          [-95.90731, 14.380839000000002],
          [-95.91791699999999, 14.381236999999999],
          [-95.921134, 14.385772000000003],
          [-95.920299, 14.389083999999997],
          [-95.897255, 14.416131999999998],
          [-95.89716899999999, 14.450001],
          [-95.916112, 14.463858000000002],
          [-95.92326800000001, 14.462919],
          [-95.93454700000001, 14.451953000000003],
          [-95.93992600000001, 14.4516],
          [-95.98756399999999, 14.464419999999997],
          [-95.99741800000001, 14.470374999999997],
          [-95.99953, 14.480280999999998],
          [-95.990948, 14.487315000000002],
          [-95.968087, 14.494669000000002],
          [-95.955625, 14.492764999999999],
          [-95.945121, 14.494836],
          [-95.90694099999999, 14.505809999999997],
          [-95.89315199999999, 14.513928999999997],
          [-95.89508799999999, 14.534077000000003],
          [-95.91400300000001, 14.540582999999998],
          [-95.928572, 14.572924999999998],
          [-95.91218699999999, 14.6188],
          [-96.012226, 14.712246999999998],
          [-96.015761, 14.7226],
          [-96.008929, 14.73191],
          [-96.01166900000001, 14.743276000000002],
          [-96.04617200000001, 14.781796],
          [-96.08293699999999, 14.808383],
          [-96.08960300000001, 14.815728999999997],
          [-96.07727600000001, 14.824983000000003],
          [-96.045279, 14.836074000000004],
          [-96.050351, 14.913432999999998],
          [-96.06072800000001, 14.927579999999999],
          [-96.120307, 14.968418],
          [-96.145306, 14.976705000000003],
          [-96.162725, 14.977260999999999],
          [-96.163648, 14.973204000000003],
          [-96.160293, 14.961998999999999],
          [-96.15029999999999, 14.947111],
          [-96.124491, 14.924542000000002],
          [-96.106997, 14.864441],
          [-96.15077199999999, 14.757266000000001],
          [-96.16194300000001, 14.752170999999997],
          [-96.21366, 14.751153000000002],
          [-96.247343, 14.752935],
          [-96.397322, 14.813018999999997],
          [-96.43105499999999, 14.818416],
          [-96.47473500000001, 14.830787999999998],
          [-96.493716, 14.838963],
          [-96.52816100000001, 14.928483999999997],
          [-96.52652, 14.954523000000002],
          [-96.54489899999999, 14.967314000000002],
          [-96.55983599999999, 14.963414],
          [-96.57216299999999, 14.957292000000002],
          [-96.575516, 14.946599999999997],
          [-96.57554099999999, 14.928941000000002],
          [-96.586289, 14.909363999999997],
          [-96.63049699999999, 14.890256],
          [-96.670412, 14.893223999999996],
          [-96.676898, 14.894973],
          [-96.69460000000001, 14.904080999999998],
          [-96.704848, 14.914394999999999],
          [-96.72499400000001, 14.923513999999997],
          [-96.75042199999999, 14.930439],
          [-96.78290799999999, 14.936658999999999],
          [-96.78871000000001, 14.936318],
          [-96.802832, 14.923118000000002],
          [-96.84098599999999, 14.919742],
          [-96.860014, 14.931997000000003],
          [-96.857823, 14.942867999999997],
          [-96.877826, 14.962392000000001],
          [-96.91853, 14.947217000000002],
          [-96.91599500000001, 14.938384],
          [-96.93235200000001, 14.916252],
          [-96.934841, 14.916345],
          [-96.933458, 14.908558],
          [-96.928643, 14.900131000000002],
          [-96.86818400000001, 14.885243000000003],
          [-96.76793, 14.870607999999997],
          [-96.744507, 14.876626000000002],
          [-96.676388, 14.856347999999997],
          [-96.612807, 14.846226999999999],
          [-96.58342999999999, 14.838825999999997],
          [-96.538029, 14.819777000000002],
          [-96.463258, 14.781607000000001],
          [-96.356786, 14.692580999999997],
          [-96.354222, 14.684303999999997],
          [-96.37277499999999, 14.667409999999997],
          [-96.396076, 14.654421],
          [-96.409876, 14.649250000000002],
          [-96.42230900000001, 14.647412000000003],
          [-96.449827, 14.637816],
          [-96.482395, 14.616739000000003],
          [-96.45943600000001, 14.607880000000002],
          [-96.38332700000001, 14.629987],
          [-96.342827, 14.645029999999998],
          [-96.331366, 14.663545],
          [-96.317193, 14.671230999999999],
          [-96.24636799999999, 14.663184999999999],
          [-96.232481, 14.677143999999998],
          [-96.223308, 14.675027999999998],
          [-96.213659, 14.665053999999998],
          [-96.194207, 14.581730999999998],
          [-96.18159600000001, 14.535018999999998],
          [-96.17049800000001, 14.527323000000003],
          [-96.100303, 14.500672999999999],
          [-96.103925, 14.478000999999999],
          [-96.083551, 14.447114999999997],
          [-96.062165, 14.435794999999999],
          [-96.05302800000001, 14.417375],
          [-96.041818, 14.380161000000001],
          [-96.092646, 14.34899],
          [-96.11193, 14.342529999999996],
          [-96.265906, 14.314498999999998],
          [-96.276769, 14.313893999999998],
          [-96.28825499999999, 14.316144000000001],
          [-96.296281, 14.318446999999999],
          [-96.304158, 14.323450000000001],
          [-96.30512100000001, 14.328691],
          [-96.30309199999999, 14.336277000000003],
          [-96.29871800000001, 14.342941000000003],
          [-96.290055, 14.351447],
          [-96.281631, 14.353090000000002],
          [-96.27392900000001, 14.363408999999997],
          [-96.28260399999999, 14.367261],
          [-96.28886700000001, 14.369649000000003],
          [-96.336728, 14.377569999999999],
          [-96.382035, 14.362693999999998],
          [-96.365148, 14.346663],
          [-96.36041599999999, 14.344076999999999],
          [-96.357115, 14.328837999999998],
          [-96.370979, 14.301642999999999],
          [-96.376464, 14.298625000000001],
          [-96.38996399999999, 14.297069999999998],
          [-96.47201999999999, 14.306356000000001],
          [-96.532824, 14.334071999999999],
          [-96.550003, 14.340370999999998],
          [-96.593029, 14.349792999999998],
          [-96.61349999999999, 14.347889000000002],
          [-96.630425, 14.340606999999999],
          [-96.638634, 14.328457999999998],
          [-96.62634499999999, 14.320590000000003],
          [-96.576799, 14.277951000000002],
          [-96.569831, 14.268700000000003],
          [-96.56795600000001, 14.245153000000002],
          [-96.591924, 14.217886],
          [-96.59719799999999, 14.215006000000002],
          [-96.619824, 14.209899],
          [-96.658638, 14.207323000000002],
          [-96.70124999999999, 14.219416000000002],
          [-96.72339099999999, 14.244926],
          [-96.730885, 14.256495999999999],
          [-96.717093, 14.273508],
          [-96.73021800000001, 14.286152999999999],
          [-96.76219800000001, 14.286765000000003],
          [-96.784326, 14.290497000000002],
          [-96.857605, 14.316360000000003],
          [-96.84899200000001, 14.328994000000002],
          [-96.946663, 14.393185000000003],
          [-96.98638399999999, 14.404043000000001],
          [-97.00941499999999, 14.408876999999997],
          [-97.01840899999999, 14.409140999999998],
          [-97.07810900000001, 14.397474000000003],
          [-97.11180200000001, 14.392594000000003],
          [-97.13445300000001, 14.406596],
          [-97.18002899999999, 14.429938999999997],
          [-97.23936599999999, 14.453159],
          [-97.25271000000001, 14.456338000000002],
          [-97.27861200000001, 14.459484000000003],
          [-97.29578799999999, 14.466178999999997],
          [-97.408758, 14.515822],
          [-97.49700200000001, 14.557721],
          [-97.568216, 14.587989],
          [-97.60880399999999, 14.601233999999998],
          [-97.632889, 14.599981999999997],
          [-97.67169000000001, 14.615523000000003],
          [-97.680811, 14.621834999999997],
          [-97.67685700000001, 14.646769999999997],
          [-97.683516, 14.660266999999997],
          [-97.68762699999999, 14.664988999999998],
          [-97.79503700000001, 14.724854999999998],
          [-97.87535, 14.757232000000002],
          [-97.90167500000001, 14.765315999999999],
          [-97.928156, 14.780532999999998],
          [-97.94182799999999, 14.794322000000001],
          [-97.94425899999999, 14.802349],
          [-97.525295, 14.906872],
          [-97.447383, 14.909512999999997],
          [-97.26475199999999, 15.002352000000002],
          [-96.86389600000001, 15.138472],
          [-96.82663299999999, 15.158389],
          [-96.58152100000001, 15.164909000000002],
          [-96.48660899999999, 15.261108],
          [-96.36885000000001, 15.232652000000002],
          [-96.122285, 15.207774999999998],
          [-96.05353400000001, 15.177537999999998],
          [-95.892056, 15.172783000000003],
          [-95.70186799999999, 15.104076],
          [-95.68824799999999, 15.016725999999998],
          [-95.58530099999999, 14.986032999999999],
          [-95.604265, 14.909481],
          [-95.522232, 14.889400000000002],
          [-95.682038, 14.85819],
          [-95.75708599999999, 14.742314],
          [-95.548348, 14.698345000000003],
          [-95.52815000000001, 14.655985000000001],
          [-95.40194500000001, 14.616146],
          [-95.413387, 14.584665000000001],
          [-95.318523, 14.500523000000001],
          [-95.496814, 14.498601999999998],
          [-95.44738100000001, 14.399890999999997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02122",
      state: "02",
      county: "122",
      name: "Kenai Peninsula County",
      lsad: "Borough",
      censusArea: 16075.331,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.745578, 16.733880999999997],
          [-108.65544399999999, 16.734541999999998],
          [-108.666606, 16.423056000000003],
          [-108.56236799999999, 16.422593],
          [-108.58364, 15.961477000000002],
          [-108.673822, 15.944828000000001],
          [-108.67617200000001, 15.947215999999997],
          [-108.67548500000001, 15.952069999999999],
          [-108.66428300000001, 15.965128],
          [-108.661876, 15.967436],
          [-108.66083, 15.969735],
          [-108.662712, 15.971825000000003],
          [-108.66814600000001, 15.973497000000002],
          [-108.67484099999999, 15.972698999999999],
          [-108.688758, 15.968006000000003],
          [-108.71551099999999, 15.962119999999999],
          [-108.72587200000001, 15.963726999999999],
          [-108.727688, 15.965049999999998],
          [-108.72973099999999, 15.972375999999997],
          [-108.73745299999999, 15.980879000000002],
          [-108.74322900000001, 15.985388999999998],
          [-108.749742, 15.988497000000002],
          [-108.75536600000001, 15.989237000000003],
          [-108.759807, 15.988940999999997],
          [-108.763803, 15.987904999999998],
          [-108.766911, 15.986868999999999],
          [-108.769871, 15.983463999999998],
          [-108.79792699999999, 15.977400000000003],
          [-108.801223, 15.975844000000002],
          [-108.810654, 15.963104999999999],
          [-108.838304, 15.9499],
          [-108.87218100000001, 15.950321000000002],
          [-108.884265, 15.955388],
          [-108.88853700000001, 15.966361999999997],
          [-108.901981, 15.973318999999996],
          [-108.91308900000001, 15.983207],
          [-108.914569, 15.986891],
          [-108.912676, 15.992894],
          [-108.91781900000001, 15.995595000000002],
          [-108.93832699999999, 15.999074],
          [-108.95755399999999, 15.998370999999999],
          [-108.977589, 15.989880999999997],
          [-108.99030099999999, 15.981012999999997],
          [-109.01678999999999, 15.976134000000002],
          [-109.02841799999999, 15.978821000000003],
          [-109.029394, 15.984098000000003],
          [-109.027274, 15.991698],
          [-109.031273, 15.998491999999999],
          [-109.04326499999999, 15.999115000000003],
          [-109.06783300000001, 15.982177],
          [-109.09506999999999, 15.983018999999999],
          [-109.100687, 15.985726999999997],
          [-109.101785, 15.990943000000001],
          [-109.10232099999999, 15.993149000000003],
          [-109.10171700000001, 15.994155],
          [-109.09869900000001, 15.996167],
          [-109.09527800000001, 15.997374],
          [-109.09007199999999, 15.999445000000001],
          [-109.089077, 16.004503999999997],
          [-109.07271600000001, 16.019652999999998],
          [-109.04159899999999, 16.030726],
          [-109.037439, 16.040529999999997],
          [-109.040358, 16.048744],
          [-109.04929000000001, 16.0587],
          [-109.05869999999999, 16.061419],
          [-109.096621, 16.044631000000003],
          [-109.133115, 16.044918000000003],
          [-109.20485300000001, 16.009211999999998],
          [-109.223781, 15.982762999999998],
          [-109.23539, 15.938792],
          [-109.287588, 15.906506],
          [-109.32702900000001, 15.987029],
          [-109.32582199999999, 16.001033],
          [-109.34158400000001, 16.076762000000002],
          [-109.36041399999999, 16.101664999999997],
          [-109.393171, 16.099845000000002],
          [-109.416829, 16.088926],
          [-109.416829, 15.997934],
          [-109.42774800000001, 15.996113999999999],
          [-109.43502699999999, 15.990654999999997],
          [-109.46285499999999, 15.950150999999998],
          [-109.47565900000001, 15.952697999999998],
          [-109.507429, 15.964519000000003],
          [-109.549677, 15.919680999999997],
          [-109.58425399999999, 15.866905000000003],
          [-109.54239799999999, 15.823228999999998],
          [-109.511461, 15.806851000000002],
          [-109.527839, 15.750436],
          [-109.598813, 15.755895000000002],
          [-109.640669, 15.799571],
          [-109.666147, 15.850527],
          [-109.70618400000001, 15.910581999999998],
          [-109.7535, 15.861446],
          [-109.73894100000001, 15.732236999999998],
          [-109.75168, 15.717678],
          [-109.75910999999999, 15.697825000000002],
          [-109.77802399999999, 15.685929999999999],
          [-109.798304, 15.677545000000002],
          [-109.842672, 15.701300000000003],
          [-109.839032, 15.752254999999998],
          [-109.88998799999999, 15.785013],
          [-109.97734, 15.801391000000002],
          [-110.02829600000001, 15.788651999999999],
          [-110.042854, 15.772274000000003],
          [-110.015557, 15.754075],
          [-109.97916000000001, 15.744976000000001],
          [-109.94822300000001, 15.717678],
          [-109.962781, 15.697659999999999],
          [-110.00281799999999, 15.683101],
          [-110.02283600000001, 15.655804000000003],
          [-110.031935, 15.613947000000003],
          [-110.068332, 15.637605],
          [-110.09563, 15.648524000000002],
          [-110.10836900000001, 15.603028000000002],
          [-110.16842399999999, 15.561172],
          [-110.223019, 15.539333999999997],
          [-110.25395599999999, 15.521135999999998],
          [-110.310371, 15.508397000000002],
          [-110.34312800000001, 15.512036000000002],
          [-110.33402899999999, 15.541153999999999],
          [-110.315831, 15.562992000000001],
          [-110.31765100000001, 15.599389000000002],
          [-110.34769600000001, 15.600928000000003],
          [-110.355493, 15.598412000000003],
          [-110.41244800000001, 15.554628000000001],
          [-110.43151800000001, 15.514287000000003],
          [-110.47874200000001, 15.458497999999999],
          [-110.49889999999999, 15.456297999999997],
          [-110.51631699999999, 15.462325999999997],
          [-110.521537, 15.467923999999996],
          [-110.521626, 15.474671999999998],
          [-110.518382, 15.477136000000002],
          [-110.51586699999999, 15.482166999999997],
          [-110.51628600000001, 15.486778000000001],
          [-110.52299400000001, 15.494743999999997],
          [-110.53611900000001, 15.498457000000002],
          [-110.54353699999999, 15.510255999999998],
          [-110.55024499999999, 15.527026],
          [-110.549825, 15.538764],
          [-110.53305599999999, 15.56308],
          [-110.537248, 15.581108],
          [-110.54186000000001, 15.586976999999997],
          [-110.547729, 15.590330999999999],
          [-110.55611400000001, 15.590330999999999],
          [-110.575818, 15.579431],
          [-110.589315, 15.565154],
          [-110.59454299999999, 15.553167000000002],
          [-110.60222999999999, 15.545890999999997],
          [-110.61480800000001, 15.545471999999997],
          [-110.631158, 15.549244999999999],
          [-110.639543, 15.547148999999997],
          [-110.63870399999999, 15.532055999999997],
          [-110.631577, 15.521574999999999],
          [-110.615152, 15.510199],
          [-110.595056, 15.499777000000002],
          [-110.58964499999999, 15.500082999999997],
          [-110.58463599999999, 15.494509999999998],
          [-110.57959500000001, 15.47954],
          [-110.579869, 15.475709000000002],
          [-110.58434199999999, 15.467714999999998],
          [-110.585567, 15.450057000000001],
          [-110.58118200000001, 15.445233000000002],
          [-110.60116199999999, 15.425657000000001],
          [-110.650046, 15.420884999999998],
          [-110.65158600000001, 15.421751],
          [-110.65401, 15.445458000000002],
          [-110.656329, 15.454436999999999],
          [-110.66721100000001, 15.457900000000002],
          [-110.683235, 15.458540999999997],
          [-110.716564, 15.450848999999998],
          [-110.729062, 15.442197],
          [-110.739958, 15.425210999999997],
          [-110.745004, 15.400728999999998],
          [-110.76985300000001, 15.372965999999998],
          [-110.79547, 15.362845],
          [-110.81956500000001, 15.357275999999999],
          [-110.83462700000001, 15.351979999999998],
          [-110.87744699999999, 15.31812],
          [-110.911598, 15.311613999999999],
          [-110.91281699999999, 15.305214],
          [-110.89555200000001, 15.286226999999997],
          [-110.88782499999999, 15.273525999999997],
          [-110.887821, 15.267919999999997],
          [-110.897808, 15.255648],
          [-110.94221200000001, 15.233136000000002],
          [-110.959531, 15.232537],
          [-110.975164, 15.236141000000003],
          [-110.98839699999999, 15.230549000000003],
          [-110.995406, 15.224148999999997],
          [-111.001196, 15.224148999999997],
          [-111.00668200000001, 15.233291999999999],
          [-111.008815, 15.245787],
          [-111.007292, 15.251576999999997],
          [-110.99680799999999, 15.257739],
          [-110.999063, 15.271082],
          [-111.023097, 15.269044999999998],
          [-111.03243, 15.275762],
          [-111.044411, 15.293610999999999],
          [-111.0461, 15.299359000000003],
          [-111.05775600000001, 15.301721],
          [-111.07030499999999, 15.287852],
          [-111.06816599999999, 15.284101999999997],
          [-111.071902, 15.281058000000002],
          [-111.08731900000001, 15.268790000000003],
          [-111.091532, 15.269187000000002],
          [-111.105635, 15.263143],
          [-111.101102, 15.240605000000002],
          [-111.102395, 15.228712999999999],
          [-111.10755800000001, 15.217792000000003],
          [-111.126247, 15.209923000000003],
          [-111.163408, 15.202635999999998],
          [-111.18625399999999, 15.202812999999999],
          [-111.19094799999999, 15.206631999999999],
          [-111.192634, 15.211208],
          [-111.206053, 15.219318999999999],
          [-111.22362899999999, 15.224347000000002],
          [-111.26163600000001, 15.220303999999999],
          [-111.27377899999999, 15.229663000000002],
          [-111.28054399999999, 15.230476000000003],
          [-111.28487999999999, 15.227586000000002],
          [-111.28706299999999, 15.224789000000001],
          [-111.28777099999999, 15.219417],
          [-111.292812, 15.214272999999999],
          [-111.305724, 15.209544000000001],
          [-111.341601, 15.222231],
          [-111.37961200000001, 15.242024],
          [-111.387261, 15.25045],
          [-111.390544, 15.264916999999997],
          [-111.39954900000001, 15.276004999999998],
          [-111.40720300000001, 15.279349000000003],
          [-111.429415, 15.268552],
          [-111.43769499999999, 15.253988999999997],
          [-111.449207, 15.248457000000002],
          [-111.48861199999999, 15.237713999999997],
          [-111.50955099999999, 15.234395],
          [-111.51848799999999, 15.230308999999998],
          [-111.525127, 15.224947],
          [-111.52024499999999, 15.216929999999998],
          [-111.50469899999999, 15.212690000000002],
          [-111.499592, 15.207839],
          [-111.497805, 15.204264000000002],
          [-111.49882600000001, 15.200178000000001],
          [-111.502657, 15.195070999999999],
          [-111.521455, 15.195483000000003],
          [-111.55815100000001, 15.200085000000001],
          [-111.57466400000001, 15.195326999999999],
          [-111.579261, 15.187666],
          [-111.57645199999999, 15.172601],
          [-111.58035100000001, 15.165233],
          [-111.59072900000001, 15.161724999999997],
          [-111.69887499999999, 15.163080999999998],
          [-111.710625, 15.158096999999998],
          [-111.72093100000001, 15.156078],
          [-111.739068, 15.156005],
          [-111.74845099999999, 15.158600999999997],
          [-111.76490799999999, 15.175510000000003],
          [-111.761451, 15.205235000000002],
          [-111.75853000000001, 15.215743000000003],
          [-111.761301, 15.221327000000002],
          [-111.838335, 15.209135000000003],
          [-111.874356, 15.211931],
          [-111.915684, 15.227522],
          [-111.917248, 15.231254],
          [-111.910958, 15.236707000000003],
          [-111.906191, 15.237963],
          [-111.90510599999999, 15.247075000000002],
          [-111.925051, 15.254427999999997],
          [-111.95272299999999, 15.250447000000001],
          [-111.95927900000001, 15.247625],
          [-111.978748, 15.253779000000002],
          [-111.99161799999999, 15.313617],
          [-111.96313, 15.344957999999998],
          [-111.95270500000001, 15.349412999999998],
          [-111.92401799999999, 15.354416999999998],
          [-111.903021, 15.360453999999997],
          [-111.890738, 15.373156000000002],
          [-111.887102, 15.382531999999998],
          [-111.908015, 15.395274],
          [-111.90515300000001, 15.401035],
          [-111.88651300000001, 15.421033000000001],
          [-111.82604699999999, 15.439048999999997],
          [-111.77087499999999, 15.447916999999997],
          [-111.75142, 15.446553999999999],
          [-111.74053799999999, 15.438431999999999],
          [-111.728486, 15.439678999999998],
          [-111.72042099999999, 15.443117],
          [-111.70646199999999, 15.462811000000002],
          [-111.694726, 15.46837],
          [-111.63447199999999, 15.482443000000004],
          [-111.570032, 15.468944999999998],
          [-111.542349, 15.467061000000001],
          [-111.528493, 15.472338],
          [-111.50589, 15.477048000000003],
          [-111.485624, 15.475459],
          [-111.470992, 15.472250000000003],
          [-111.466272, 15.484050000000003],
          [-111.46963, 15.502811000000001],
          [-111.43635900000001, 15.530329000000002],
          [-111.42096599999999, 15.537728000000001],
          [-111.36577600000001, 15.541255],
          [-111.32366999999999, 15.550942999999997],
          [-111.272459, 15.555822999999997],
          [-111.26673299999999, 15.562632],
          [-111.26481100000001, 15.568598000000001],
          [-111.271737, 15.576467999999998],
          [-111.27890500000001, 15.589028999999996],
          [-111.278827, 15.592979999999997],
          [-111.27479500000001, 15.596986000000001],
          [-111.20167799999999, 15.591503000000003],
          [-111.203835, 15.577961000000002],
          [-111.20912999999999, 15.573622999999998],
          [-111.20836399999999, 15.562061],
          [-111.192803, 15.562432000000001],
          [-111.16425899999999, 15.587012999999999],
          [-111.158254, 15.594141],
          [-111.165427, 15.601329],
          [-111.18803199999999, 15.608687000000003],
          [-111.20545899999999, 15.630284000000003],
          [-111.207639, 15.64067],
          [-111.20318599999999, 15.645989],
          [-111.17398399999999, 15.651792999999998],
          [-111.12612200000001, 15.668335999999996],
          [-111.121362, 15.674734999999998],
          [-111.122791, 15.677782],
          [-111.11649, 15.696131999999999],
          [-111.098253, 15.709442000000003],
          [-111.018888, 15.756593000000002],
          [-110.927312, 15.793430999999998],
          [-110.94813199999999, 15.792194000000002],
          [-110.98299600000001, 15.783543000000002],
          [-111.00166300000001, 15.788390999999997],
          [-111.00671700000001, 15.792985999999999],
          [-111.02775600000001, 15.796196000000002],
          [-111.063758, 15.793146],
          [-111.113845, 15.777231],
          [-111.172439, 15.751345999999998],
          [-111.214539, 15.729847],
          [-111.329812, 15.683644000000001],
          [-111.37705399999999, 15.681313000000003],
          [-111.42483999999999, 15.670521],
          [-111.43661, 15.666359999999997],
          [-111.439187, 15.663246999999998],
          [-111.441127, 15.653542999999999],
          [-111.448669, 15.648170999999998],
          [-111.461253, 15.643039000000002],
          [-111.50382200000001, 15.633662000000001],
          [-111.64306099999999, 15.646965999999999],
          [-111.686486, 15.660863999999997],
          [-111.746815, 15.686233999999999],
          [-111.7963, 15.704155999999998],
          [-111.829137, 15.720151000000001],
          [-111.85027199999999, 15.739035000000001],
          [-111.85932700000001, 15.749566999999999],
          [-111.86946800000001, 15.769159000000002],
          [-111.86771300000001, 15.778410999999998],
          [-111.857339, 15.791145],
          [-111.83333999999999, 15.814129000000001],
          [-111.81361899999999, 15.844296999999997],
          [-111.80305899999999, 15.878532999999997],
          [-111.79259400000001, 15.88881],
          [-111.77785499999999, 15.897492999999997],
          [-111.75769299999999, 15.917637],
          [-111.74274199999999, 15.944626],
          [-111.71800999999999, 16.009473],
          [-111.702898, 16.032252999999997],
          [-111.661437, 16.057139],
          [-111.62379899999999, 16.088033000000003],
          [-111.60688099999999, 16.099558000000002],
          [-111.545579, 16.128394],
          [-111.517887, 16.145007999999997],
          [-111.488721, 16.167616000000002],
          [-111.42170200000001, 16.212930999999998],
          [-111.40660700000001, 16.228183],
          [-111.38791900000001, 16.267066],
          [-111.381959, 16.296951],
          [-111.383231, 16.326348000000003],
          [-111.38160400000001, 16.358728],
          [-111.37728100000001, 16.365522],
          [-111.366874, 16.372655],
          [-111.30609000000001, 16.387256999999998],
          [-111.30186800000001, 16.384712],
          [-111.299782, 16.385481],
          [-111.29307399999999, 16.416162999999997],
          [-111.28681900000001, 16.434648000000003],
          [-111.28396699999999, 16.452196],
          [-111.280992, 16.512627000000002],
          [-111.27881, 16.520107000000003],
          [-111.26446100000001, 16.543263000000003],
          [-111.268373, 16.548977],
          [-111.303125, 16.561326],
          [-111.323951, 16.574135],
          [-111.330409, 16.580539],
          [-111.339069, 16.594244000000003],
          [-111.34447700000001, 16.613458],
          [-111.345508, 16.622954],
          [-111.350154, 16.634659999999997],
          [-111.36239699999999, 16.653526],
          [-111.38783900000001, 16.674501],
          [-111.404451, 16.695003999999997],
          [-111.41027299999999, 16.711022999999997],
          [-111.40926999999999, 16.720557999999997],
          [-111.38480000000001, 16.729945999999998],
          [-111.37051500000001, 16.733572000000002],
          [-111.30923000000001, 16.740724],
          [-111.27963500000001, 16.747676],
          [-111.27050500000001, 16.751286],
          [-111.261383, 16.757768],
          [-111.259343, 16.762895999999998],
          [-111.26131899999999, 16.769801],
          [-111.252902, 16.773992999999997],
          [-111.212186, 16.780341999999997],
          [-111.106079, 16.783749],
          [-111.062558, 16.787429000000003],
          [-111.03700699999999, 16.793649000000002],
          [-111.025634, 16.797497],
          [-111.024799, 16.801786999999997],
          [-111.01201599999999, 16.80934],
          [-110.895508, 16.853166],
          [-110.886964, 16.858187],
          [-110.88377399999999, 16.861865],
          [-110.845731, 16.877893],
          [-110.80841799999999, 16.891336000000003],
          [-110.77059399999999, 16.911361999999997],
          [-110.70581200000001, 16.937792],
          [-110.678438, 16.958267],
          [-110.603069, 16.974434000000002],
          [-110.582471, 16.982095],
          [-110.51505800000001, 16.999443],
          [-110.511099, 17.005145],
          [-110.501923, 17.007956999999998],
          [-110.45466099999999, 17.016565999999997],
          [-110.431873, 17.023939],
          [-110.401859, 17.036226999999997],
          [-110.377171, 17.039144],
          [-110.353702, 17.031822],
          [-110.34170900000001, 17.024200999999998],
          [-110.31033400000001, 16.989547],
          [-110.28636900000001, 16.966696],
          [-110.26209600000001, 16.947839000000002],
          [-110.24407199999999, 16.938585000000003],
          [-110.21717899999999, 16.930000999999997],
          [-110.187657, 16.924796],
          [-110.08516599999999, 16.91402],
          [-110.070289, 16.913679000000002],
          [-110.04557, 16.910004],
          [-110.049148, 16.915816],
          [-110.047088, 16.918923999999997],
          [-110.03986599999999, 16.920777],
          [-109.952655, 16.930393000000002],
          [-109.91216600000001, 16.937843],
          [-109.87518800000001, 16.960244000000003],
          [-109.85369299999999, 16.967395000000003],
          [-109.83558, 16.968854999999998],
          [-109.81681699999999, 16.966946999999998],
          [-109.770264, 16.967607],
          [-109.76496599999999, 16.963411999999998],
          [-109.753082, 16.962059000000004],
          [-109.735376, 16.987813000000003],
          [-109.73639499999999, 16.994692],
          [-109.373299, 16.907624],
          [-109.188571, 16.905486000000003],
          [-109.038568, 16.849342],
          [-109.042881, 16.734797],
          [-108.745578, 16.733880999999997]
        ],
        [
          [-113.00218100000001, 17.425686],
          [-111.33417499999999, 17.424601000000003],
          [-111.332978, 17.254634000000003],
          [-110.97266099999999, 17.253489000000002],
          [-110.97177500000001, 17.192041000000003],
          [-110.99008599999999, 17.188907],
          [-111.01262, 17.183258000000002],
          [-111.02490499999999, 17.178390999999998],
          [-111.07277500000001, 17.141669],
          [-111.07849999999999, 17.133381],
          [-111.119722, 17.091116999999997],
          [-111.121692, 17.083574],
          [-111.12735699999999, 17.076895999999998],
          [-111.14258699999999, 17.062778],
          [-111.166606, 17.046404000000003],
          [-111.19031799999999, 17.042737000000002],
          [-111.252384, 17.039968000000002],
          [-111.293622, 17.035715000000003],
          [-111.307796, 17.031008],
          [-111.31265300000001, 17.026364],
          [-111.33091999999999, 17.015124],
          [-111.34900400000001, 17.010004000000002],
          [-111.362243, 17.009411999999998],
          [-111.42511999999999, 17.013106999999998],
          [-111.467851, 17.012423],
          [-111.4803, 17.010902],
          [-111.538227, 16.991835000000002],
          [-111.57369800000001, 16.975876],
          [-111.621005, 16.957453],
          [-111.63734600000001, 16.946727000000003],
          [-111.641066, 16.942177],
          [-111.679518, 16.922491],
          [-111.692644, 16.917743],
          [-111.71391299999999, 16.916545999999997],
          [-111.72081499999999, 16.904257],
          [-111.73601500000001, 16.891506999999997],
          [-111.78327100000001, 16.868713],
          [-111.791698, 16.863059999999997],
          [-111.800264, 16.853672000000003],
          [-111.79672299999999, 16.838734000000002],
          [-111.787394, 16.822307000000002],
          [-111.77731, 16.810460999999997],
          [-111.75181699999999, 16.788728999999996],
          [-111.703802, 16.732376000000002],
          [-111.702833, 16.727778],
          [-111.70555300000001, 16.718052],
          [-111.710444, 16.712657],
          [-111.71637899999999, 16.710414999999998],
          [-111.74432100000001, 16.712403000000002],
          [-111.749493, 16.714174999999997],
          [-111.760301, 16.721441],
          [-111.784039, 16.726813999999997],
          [-111.80381399999999, 16.729004000000003],
          [-111.811286, 16.732222],
          [-111.822596, 16.742351999999997],
          [-111.831185, 16.747303000000002],
          [-111.851967, 16.754074000000003],
          [-111.86017899999999, 16.753282],
          [-111.864958, 16.750458000000002],
          [-111.84963400000001, 16.738286000000002],
          [-111.84796499999999, 16.735694000000002],
          [-111.848614, 16.733976],
          [-111.87047100000001, 16.727283999999997],
          [-111.91691399999999, 16.717916000000002],
          [-111.917922, 16.722808999999998],
          [-111.915911, 16.730244999999996],
          [-111.918758, 16.73227],
          [-111.923384, 16.733735000000003],
          [-111.94443899999999, 16.73305],
          [-111.94459900000001, 16.728175999999998],
          [-111.948287, 16.722476],
          [-111.961757, 16.721431000000003],
          [-111.968381, 16.728340000000003],
          [-112.079433, 16.692804000000002],
          [-112.11413999999999, 16.634068999999997],
          [-112.13615999999999, 16.578474999999997],
          [-112.14843400000001, 16.575977],
          [-112.16351700000001, 16.576934],
          [-112.19508400000001, 16.569674999999997],
          [-112.26149699999999, 16.538237000000002],
          [-112.30922100000001, 16.506383999999997],
          [-112.31514899999999, 16.499823999999997],
          [-112.331365, 16.473525000000002],
          [-112.33337499999999, 16.460641000000003],
          [-112.330263, 16.443134],
          [-112.32582099999999, 16.434806000000002],
          [-112.31222600000001, 16.420397],
          [-112.30195, 16.414327999999998],
          [-112.23419899999999, 16.393887999999997],
          [-112.300622, 16.369604000000002],
          [-112.307615, 16.366489],
          [-112.315855, 16.359071],
          [-112.352294, 16.356101000000002],
          [-112.36621299999999, 16.353304],
          [-112.371475, 16.350175999999998],
          [-112.376743, 16.345613],
          [-112.386334, 16.327889],
          [-112.38597899999999, 16.315845000000003],
          [-112.392009, 16.302107999999997],
          [-112.411281, 16.287864],
          [-112.42113, 16.285331],
          [-112.444165, 16.285716999999998],
          [-112.456291, 16.284042],
          [-112.48179400000001, 16.274681],
          [-112.52820600000001, 16.251345999999998],
          [-112.53984299999999, 16.241644],
          [-112.54923600000001, 16.227631000000002],
          [-112.55675199999999, 16.224217000000003],
          [-112.62464800000001, 16.218687000000003],
          [-112.62627499999999, 16.220852],
          [-112.626901, 16.222727999999996],
          [-112.62768299999999, 16.225700000000003],
          [-112.642361, 16.228766],
          [-112.660055, 16.242004],
          [-112.670403, 16.244320000000002],
          [-112.698634, 16.240661000000003],
          [-112.715881, 16.241273999999997],
          [-112.747026, 16.233311],
          [-112.74338800000001, 16.224323],
          [-112.754884, 16.21091],
          [-112.754884, 16.202900999999997],
          [-112.74954500000001, 16.189552],
          [-112.734251, 16.174801000000002],
          [-112.69987900000001, 16.165272],
          [-112.688392, 16.165819999999997],
          [-112.67808500000001, 16.163504000000003],
          [-112.67417599999999, 16.151730999999998],
          [-112.68748500000001, 16.140304999999998],
          [-112.686373, 16.137717000000002],
          [-112.65841800000001, 16.121591000000002],
          [-112.634972, 16.115799000000003],
          [-112.59678400000001, 16.101070999999997],
          [-112.57527099999999, 16.082363],
          [-112.569121, 16.071748],
          [-112.575153, 16.04826],
          [-112.590169, 16.035978],
          [-112.608599, 16.025429000000003],
          [-112.612721, 16.015115],
          [-112.611651, 16.008521000000002],
          [-112.64947900000001, 15.988253],
          [-112.67940200000001, 15.968054000000002],
          [-112.69367399999999, 15.932772999999997],
          [-112.70082199999999, 15.920309000000003],
          [-112.70643100000001, 15.915284],
          [-112.745083, 15.904232],
          [-112.79358400000001, 15.896720000000002],
          [-112.80693400000001, 15.888029000000003],
          [-112.810058, 15.878321999999997],
          [-112.86086700000001, 15.875033000000002],
          [-112.875167, 15.877471],
          [-112.88267200000001, 15.881985999999998],
          [-112.90041400000001, 15.881811999999996],
          [-112.92041699999999, 15.877741],
          [-112.950662, 15.876759],
          [-112.96726699999999, 15.881494000000004],
          [-113.002521, 15.886726000000003],
          [-113.01997700000001, 15.886229999999998],
          [-113.046986, 15.882424999999998],
          [-113.07918699999999, 15.871102999999998],
          [-113.144747, 15.859828999999998],
          [-113.212865, 15.862783999999998],
          [-113.22593699999999, 15.858342999999998],
          [-113.22861499999999, 15.853355],
          [-113.256944, 15.836489999999998],
          [-113.278808, 15.828066],
          [-113.28580199999999, 15.820535],
          [-113.28541200000001, 15.816755],
          [-113.278535, 15.810924],
          [-113.257736, 15.810806999999997],
          [-113.23655600000001, 15.821866999999997],
          [-113.21748099999999, 15.824720999999997],
          [-113.197352, 15.824826999999999],
          [-113.18230700000001, 15.822389000000001],
          [-113.144372, 15.807616000000003],
          [-113.113586, 15.815631000000003],
          [-113.088515, 15.833376000000001],
          [-113.02194499999999, 15.834133000000001],
          [-113.009084, 15.830643000000002],
          [-113.003964, 15.826746999999997],
          [-112.99212600000001, 15.810026999999998],
          [-112.99446599999999, 15.791260999999999],
          [-113.031319, 15.723624999999998],
          [-113.051559, 15.691561999999998],
          [-113.10893999999999, 15.678316000000002],
          [-113.12173999999999, 15.678009000000003],
          [-113.15501900000001, 15.654344000000002],
          [-113.214156, 15.634270999999998],
          [-113.24001799999999, 15.632426000000002],
          [-113.25340800000001, 15.638415000000002],
          [-113.26274000000001, 15.643425999999998],
          [-113.27517499999999, 15.667302999999997],
          [-113.28652500000001, 15.670251],
          [-113.30168699999999, 15.668717000000001],
          [-113.31400199999999, 15.666336000000001],
          [-113.315123, 15.664895999999999],
          [-113.31508299999999, 15.662489999999998],
          [-113.307199, 15.653953999999999],
          [-113.298205, 15.636344999999999],
          [-113.298047, 15.632502000000002],
          [-113.30275599999999, 15.627679],
          [-113.30883700000001, 15.625706000000001],
          [-113.342938, 15.621312000000003],
          [-113.366613, 15.633729000000002],
          [-113.381595, 15.638032000000003],
          [-113.392022, 15.638855999999997],
          [-113.409422, 15.636327999999999],
          [-113.414898, 15.638599999999997],
          [-113.41809900000001, 15.642147000000001],
          [-113.41550699999999, 15.650917999999997],
          [-113.39384899999999, 15.658847000000002],
          [-113.384886, 15.667188000000003],
          [-113.37823499999999, 15.688935999999998],
          [-113.37477799999999, 15.731586999999998],
          [-113.439977, 15.784652000000001],
          [-113.454972, 15.792099],
          [-113.458549, 15.764467000000003],
          [-113.44962000000001, 15.743810000000003],
          [-113.445336, 15.728864999999999],
          [-113.442219, 15.717419999999997],
          [-113.441214, 15.701315999999998],
          [-113.44400300000001, 15.689957],
          [-113.46455599999999, 15.651712000000003],
          [-113.47609800000001, 15.64273],
          [-113.54246599999999, 15.630235999999996],
          [-113.56386599999999, 15.638902999999999],
          [-113.58651800000001, 15.651541000000002],
          [-113.60481300000001, 15.638734],
          [-113.610739, 15.628340999999999],
          [-113.609253, 15.62164],
          [-113.59219300000001, 15.610841999999998],
          [-113.558292, 15.605789999999999],
          [-113.55316300000001, 15.597045999999999],
          [-113.555148, 15.587857999999997],
          [-113.57782800000001, 15.555990999999999],
          [-113.585406, 15.551475000000003],
          [-113.61815100000001, 15.552571],
          [-113.63526200000001, 15.555694000000003],
          [-113.65094300000001, 15.555427000000002],
          [-113.71930900000001, 15.550263999999999],
          [-113.76148, 15.543410999999999],
          [-113.766242, 15.522342000000002],
          [-113.73385300000001, 15.505754000000003],
          [-113.70641900000001, 15.477994000000002],
          [-113.699025, 15.463602999999999],
          [-113.72754599999999, 15.435346000000003],
          [-113.74720099999999, 15.429656999999999],
          [-113.807119, 15.419466],
          [-113.823384, 15.418035000000003],
          [-113.862199, 15.424123999999999],
          [-113.89657600000001, 15.418486000000001],
          [-113.911268, 15.413732000000003],
          [-113.925307, 15.405254],
          [-113.945539, 15.386060999999998],
          [-113.951389, 15.387017],
          [-113.95471699999999, 15.392532000000003],
          [-113.959893, 15.39685],
          [-113.990003, 15.396777],
          [-113.99399399999999, 15.394049000000003],
          [-113.996261, 15.390819999999998],
          [-113.99850599999999, 15.384723000000001],
          [-114.007207, 15.382528],
          [-114.02569600000001, 15.381521],
          [-114.035965, 15.386361999999998],
          [-114.04456300000001, 15.388295],
          [-114.05215000000001, 15.387138],
          [-114.062453, 15.382753000000001],
          [-114.08780300000001, 15.367967],
          [-114.100989, 15.366016000000002],
          [-114.117672, 15.365507999999998],
          [-114.12180799999999, 15.360543999999997],
          [-114.12139400000001, 15.353099],
          [-114.103014, 15.342719000000002],
          [-114.033703, 15.343998999999997],
          [-114.028739, 15.337381],
          [-114.03080700000001, 15.327039999999997],
          [-114.07794200000001, 15.313364],
          [-114.11357699999999, 15.299627000000001],
          [-114.122681, 15.287621999999999],
          [-114.13684, 15.262666000000003],
          [-114.14119199999999, 15.216597999999998],
          [-114.130585, 15.210503000000003],
          [-114.172944, 15.172496000000002],
          [-114.21481800000001, 15.151561999999998],
          [-114.24422000000001, 15.144160999999997],
          [-114.260121, 15.14302],
          [-114.26329100000001, 15.138461999999997],
          [-114.256528, 15.118462000000001],
          [-114.25123300000001, 15.111238999999998],
          [-114.243785, 15.114868000000001],
          [-114.23984200000001, 15.119323999999999],
          [-114.227238, 15.125407000000003],
          [-114.180691, 15.123235000000001],
          [-114.17366899999999, 15.120199999999997],
          [-114.16674499999999, 15.100548000000003],
          [-114.166406, 15.090581999999998],
          [-114.171502, 15.083423000000003],
          [-114.19527099999999, 15.069491],
          [-114.19742199999999, 15.061155],
          [-114.18958, 15.044207],
          [-114.17146199999999, 15.021963],
          [-114.15983499999999, 15.010595000000002],
          [-114.158207, 15.017853000000002],
          [-114.13244900000001, 15.024745000000003],
          [-114.10827800000001, 15.036827000000002],
          [-114.063489, 15.072139999999997],
          [-114.031822, 15.073681],
          [-114.008547, 15.072904000000001],
          [-113.94995800000001, 15.066782000000003],
          [-113.93282400000001, 15.062677],
          [-113.85023799999999, 15.052917],
          [-113.83818, 15.055295999999998],
          [-113.81572399999999, 15.064850999999997],
          [-113.809866, 15.070796999999999],
          [-113.80278200000001, 15.072223999999999],
          [-113.793972, 15.071416],
          [-113.750936, 15.052840000000003],
          [-113.74868000000001, 15.058729],
          [-113.74620100000001, 15.065199],
          [-113.704162, 15.075780000000002],
          [-113.695664, 15.073993999999999],
          [-113.64802900000001, 15.028924000000004],
          [-113.61606599999999, 15.006737000000001],
          [-113.547283, 14.983716000000001],
          [-113.52352200000001, 14.979221000000003],
          [-113.505618, 14.981734000000003],
          [-113.479939, 14.995286],
          [-113.463266, 14.986902999999998],
          [-113.450672, 14.976118999999997],
          [-113.438144, 14.969911000000003],
          [-113.42201499999999, 14.970647999999997],
          [-113.39847900000001, 14.966056000000002],
          [-113.393101, 14.951096999999997],
          [-113.388765, 14.945337000000002],
          [-113.36537100000001, 14.927753000000003],
          [-113.352283, 14.921705000000003],
          [-113.33478, 14.920521],
          [-113.322843, 14.907848999999999],
          [-113.304788, 14.878919000000003],
          [-113.305216, 14.874637],
          [-113.30243300000001, 14.871212],
          [-113.294726, 14.865431999999998],
          [-113.28616299999999, 14.863076999999997],
          [-113.26740699999999, 14.867218000000001],
          [-113.254798, 14.861756],
          [-113.25225, 14.855849999999997],
          [-113.25266199999999, 14.855797000000003],
          [-113.45510200000001, 14.855665000000002],
          [-113.45219800000001, 14.734772999999997],
          [-113.816565, 14.734254],
          [-113.815055, 14.647216],
          [-114.305926, 14.646822],
          [-114.63727, 14.646810000000002],
          [-114.63942, 14.734693],
          [-114.692714, 14.734597999999998],
          [-114.692093, 15.075955999999998],
          [-114.745361, 15.076991999999997],
          [-114.747693, 15.253095000000002],
          [-114.40931599999999, 15.254640000000002],
          [-114.414557, 15.428680999999997],
          [-114.12126900000001, 15.431294999999999],
          [-114.12437399999999, 15.519551],
          [-113.954485, 15.520072999999996],
          [-113.95588900000001, 15.696098999999997],
          [-113.783649, 15.696598000000002],
          [-113.78294299999999, 15.785049],
          [-113.65489299999999, 15.784914],
          [-113.656579, 16.121433000000003],
          [-113.531136, 16.122545000000002],
          [-113.52848399999999, 16.470610999999998],
          [-113.394293, 16.472296],
          [-113.395016, 16.823290999999998],
          [-113.436195, 16.908538],
          [-113.43040400000001, 17.13574],
          [-113.47531599999999, 17.136887],
          [-113.469729, 17.428123],
          [-113.00218100000001, 17.425686]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02150",
      state: "02",
      county: "150",
      name: "Kodiak Island County",
      lsad: "Borough",
      censusArea: 6549.579,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.355401, 13.159678999999997],
          [-116.75081499999999, 13.162878],
          [-116.75077999999999, 13.235795000000003],
          [-116.672751, 13.234909000000002],
          [-116.61739499999999, 13.334941999999998],
          [-116.50222600000001, 13.362606],
          [-116.508759, 13.434831000000003],
          [-116.458121, 13.478904999999997],
          [-116.37023500000001, 13.522368],
          [-116.13203200000001, 13.509692000000001],
          [-116.071148, 13.584423999999999],
          [-115.95277200000001, 13.598213000000001],
          [-115.921548, 13.554333999999997],
          [-115.87199000000001, 13.599963000000002],
          [-115.89845500000001, 13.668100000000003],
          [-115.768022, 13.668261000000001],
          [-115.693773, 13.797987],
          [-115.53882999999999, 13.799805999999997],
          [-115.44625099999999, 13.827094000000002],
          [-115.44602, 13.871459000000002],
          [-115.33024499999999, 13.876342999999999],
          [-115.33167800000001, 14.048211000000002],
          [-115.280169, 14.108590999999997],
          [-115.331999, 14.107967000000002],
          [-115.33194499999999, 14.193283000000001],
          [-115.22131300000001, 14.193193],
          [-115.221463, 14.236407999999997],
          [-115.057741, 14.236209000000002],
          [-115.00813199999999, 14.290905000000002],
          [-114.730369, 14.304668999999997],
          [-114.729769, 14.332836999999998],
          [-114.57010700000001, 14.333055000000002],
          [-114.46574100000001, 14.361356],
          [-114.432332, 14.418998000000002],
          [-114.303447, 14.460385000000002],
          [-114.305926, 14.646822],
          [-113.815055, 14.647216],
          [-113.816565, 14.734254],
          [-113.45219800000001, 14.734772999999997],
          [-113.45510200000001, 14.855665000000002],
          [-113.25266199999999, 14.855797000000003],
          [-113.317823, 14.847441000000003],
          [-113.32613799999999, 14.849319999999999],
          [-113.336826, 14.848878],
          [-113.34483, 14.846953],
          [-113.350964, 14.843550999999998],
          [-113.35891699999999, 14.836767000000002],
          [-113.36938900000001, 14.821255],
          [-113.37073000000001, 14.799301],
          [-113.38512600000001, 14.766173000000002],
          [-113.40247199999999, 14.742607],
          [-113.42664099999999, 14.721127000000003],
          [-113.44500199999999, 14.709310000000002],
          [-113.45881600000001, 14.708561000000003],
          [-113.468963, 14.712234000000002],
          [-113.477755, 14.712767],
          [-113.55265, 14.687176000000001],
          [-113.57754399999999, 14.670532000000001],
          [-113.58779899999999, 14.651741999999999],
          [-113.591635, 14.640084000000002],
          [-113.601257, 14.634633000000001],
          [-113.67759699999999, 14.611603000000002],
          [-113.771636, 14.605638999999996],
          [-113.806232, 14.606594999999999],
          [-113.83283700000001, 14.611671000000001],
          [-113.85143199999999, 14.611871999999998],
          [-113.897155, 14.606237],
          [-113.902558, 14.597377000000002],
          [-113.91913400000001, 14.516658999999997],
          [-113.90958800000001, 14.514561999999998],
          [-113.930473, 14.497481999999998],
          [-113.934852, 14.494413999999999],
          [-113.96037000000001, 14.487831],
          [-113.97402, 14.488348000000002],
          [-113.97761700000001, 14.491931000000001],
          [-114.00191799999999, 14.492345999999998],
          [-114.05652599999999, 14.489221999999998],
          [-114.065121, 14.486429999999999],
          [-114.07303200000001, 14.478259000000001],
          [-114.075051, 14.472188000000003],
          [-114.07523499999999, 14.458106],
          [-114.07359199999999, 14.446866],
          [-114.07066, 14.440018000000002],
          [-114.055759, 14.418782],
          [-114.04001299999999, 14.404297],
          [-114.03414699999999, 14.402638000000003],
          [-114.02371299999999, 14.403951999999997],
          [-114.007305, 14.402186999999998],
          [-113.98541599999999, 14.390877000000003],
          [-113.999323, 14.376372000000003],
          [-114.09725399999999, 14.345322000000003],
          [-114.13386600000001, 14.350631999999997],
          [-114.13922299999999, 14.354107999999997],
          [-114.150373, 14.357581000000003],
          [-114.16799700000001, 14.358378000000002],
          [-114.175194, 14.344527],
          [-114.17716100000001, 14.321469999999998],
          [-114.17499900000001, 14.320107],
          [-114.16664800000001, 14.320568999999999],
          [-114.14907299999999, 14.314539000000003],
          [-114.10341199999999, 14.280161],
          [-114.145277, 14.210931000000002],
          [-114.177652, 14.189832000000003],
          [-114.21975499999999, 14.184631000000003],
          [-114.241874, 14.156328000000002],
          [-114.21625, 14.142848999999998],
          [-114.21007800000001, 14.136062000000003],
          [-114.222465, 14.132565999999997],
          [-114.269027, 14.128770000000003],
          [-114.29116300000001, 14.13568],
          [-114.340449, 14.090921000000002],
          [-114.38432700000001, 14.120823000000001],
          [-114.42657, 14.144900999999997],
          [-114.436518, 14.148294],
          [-114.46012099999999, 14.146067000000002],
          [-114.46643599999999, 14.142328],
          [-114.45938899999999, 14.129089],
          [-114.45209600000001, 14.121581999999997],
          [-114.44921199999999, 14.093218],
          [-114.462929, 14.058349999999997],
          [-114.477979, 14.052379000000002],
          [-114.50124600000001, 14.050111999999999],
          [-114.539367, 14.055886999999998],
          [-114.56862699999999, 14.025787000000001],
          [-114.581547, 14.019285000000004],
          [-114.64396500000001, 14.033147],
          [-114.65061800000001, 14.045386999999998],
          [-114.644666, 14.056432999999998],
          [-114.64622299999999, 14.060141000000002],
          [-114.65338299999999, 14.064036999999999],
          [-114.66889499999999, 14.065272],
          [-114.676108, 14.065114000000001],
          [-114.716162, 14.055256],
          [-114.72188399999999, 14.050544000000002],
          [-114.72898, 14.038454999999999],
          [-114.73105899999999, 14.028454000000004],
          [-114.730726, 14.021836999999998],
          [-114.74558099999999, 14.01222],
          [-114.765287, 14.003709999999998],
          [-114.774719, 14.002167999999998],
          [-114.80776700000001, 14.000939000000002],
          [-114.81951799999999, 14.003494000000003],
          [-114.823518, 14.009348000000003],
          [-114.825051, 14.016585999999997],
          [-114.82822999999999, 14.018849000000003],
          [-114.87655899999999, 14.027721999999997],
          [-114.89181199999999, 14.027676],
          [-114.990431, 14.013424],
          [-115.026275, 13.999302],
          [-115.11864800000001, 13.953924999999998],
          [-115.094686, 13.929279999999999],
          [-115.072566, 13.911968000000002],
          [-115.064199, 13.909294000000003],
          [-115.06180599999999, 13.904330000000002],
          [-115.06814800000001, 13.883772999999998],
          [-115.08213900000001, 13.872247999999999],
          [-115.097095, 13.865355999999998],
          [-115.15242, 13.855375000000002],
          [-115.237933, 13.827131000000001],
          [-115.272917, 13.823981000000003],
          [-115.298385, 13.82602],
          [-115.303044, 13.828454],
          [-115.326369, 13.830545],
          [-115.338153, 13.825384],
          [-115.34123500000001, 13.819643999999997],
          [-115.33494400000001, 13.780692000000002],
          [-115.310981, 13.764811000000002],
          [-115.30278899999999, 13.761657999999997],
          [-115.285339, 13.758726000000003],
          [-115.28469100000001, 13.757387999999999],
          [-115.291651, 13.735809000000003],
          [-115.305814, 13.724049999999998],
          [-115.37861000000001, 13.710766],
          [-115.468287, 13.744636999999997],
          [-115.50653299999999, 13.76097],
          [-115.533627, 13.776879999999998],
          [-115.53976599999999, 13.783942000000003],
          [-115.54567599999999, 13.786769],
          [-115.56843699999999, 13.789510999999997],
          [-115.585411, 13.786980999999997],
          [-115.596857, 13.783538999999998],
          [-115.609353, 13.777698999999998],
          [-115.617188, 13.769714999999998],
          [-115.635323, 13.715440999999998],
          [-115.63454300000001, 13.704763999999997],
          [-115.626373, 13.693623000000002],
          [-115.61520300000001, 13.688074],
          [-115.62991199999999, 13.656376000000002],
          [-115.699986, 13.642085000000002],
          [-115.724167, 13.633445000000002],
          [-115.73550900000001, 13.594149000000002],
          [-115.730951, 13.588562000000003],
          [-115.731412, 13.555546],
          [-115.732779, 13.549731999999999],
          [-115.78693899999999, 13.547007],
          [-115.87785600000001, 13.547173],
          [-115.91526099999999, 13.535331],
          [-115.94581199999999, 13.539248999999998],
          [-115.96789000000001, 13.544429000000001],
          [-115.98598799999999, 13.553721000000003],
          [-115.988113, 13.558328000000003],
          [-116.010818, 13.571379],
          [-116.033806, 13.569882999999997],
          [-116.04403099999999, 13.564455000000002],
          [-116.048584, 13.500808],
          [-116.045324, 13.487037],
          [-116.036722, 13.470941000000003],
          [-116.014396, 13.455285000000003],
          [-116.01284100000001, 13.451394],
          [-116.021875, 13.439660000000003],
          [-116.091668, 13.439829000000003],
          [-116.09906699999999, 13.443691000000001],
          [-116.13748000000001, 13.471733999999998],
          [-116.183932, 13.482112],
          [-116.19574, 13.480058999999997],
          [-116.210883, 13.474409000000001],
          [-116.21148500000001, 13.459474999999998],
          [-116.22010499999999, 13.445295000000002],
          [-116.22688600000001, 13.440666999999998],
          [-116.25446199999999, 13.438960999999999],
          [-116.339425, 13.417641000000003],
          [-116.36203900000001, 13.400474000000003],
          [-116.37743900000001, 13.390864999999998],
          [-116.48163199999999, 13.338704999999997],
          [-116.511412, 13.33502],
          [-116.533544, 13.328527000000001],
          [-116.539718, 13.320059],
          [-116.55123900000001, 13.290799999999997],
          [-116.53868399999999, 13.283040999999997],
          [-116.50730100000001, 13.281163999999997],
          [-116.420864, 13.311141999999997],
          [-116.33642699999999, 13.336081],
          [-116.32191, 13.293368999999998],
          [-116.332718, 13.265191999999999],
          [-116.34294299999999, 13.248055999999998],
          [-116.358139, 13.252187999999997],
          [-116.376507, 13.252284000000003],
          [-116.39942300000001, 13.241627000000001],
          [-116.401488, 13.233168999999997],
          [-116.398751, 13.214756000000001],
          [-116.38859199999999, 13.206620000000001],
          [-116.35575600000001, 13.192844000000001],
          [-116.34129999999999, 13.191856999999999],
          [-116.33842999999999, 13.190325000000001],
          [-116.334404, 13.182299999999998],
          [-116.355401, 13.159678999999997]
        ],
        [
          [-115.62056000000001, 11.907508999999997],
          [-115.568207, 11.907508999999997],
          [-115.56059300000001, 11.897041000000002],
          [-115.55989500000001, 11.882897999999997],
          [-115.584396, 11.848498999999997],
          [-115.56440699999999, 11.809474999999999],
          [-115.566315, 11.78949],
          [-115.589157, 11.761887000000002],
          [-115.61103800000001, 11.759982999999998],
          [-115.64910900000001, 11.776164999999999],
          [-115.71859699999999, 11.772357999999997],
          [-115.728104, 11.779018],
          [-115.75, 11.821849999999998],
          [-115.74312499999999, 11.831488],
          [-115.663374, 11.860487999999997],
          [-115.63769500000001, 11.894184000000003],
          [-115.62056000000001, 11.907508999999997]
        ],
        [
          [-114.40401499999999, 12.572287000000003],
          [-114.393868, 12.562387999999999],
          [-114.39129399999999, 12.557931000000004],
          [-114.39248, 12.554053000000003],
          [-114.43679399999999, 12.534556000000002],
          [-114.529507, 12.502654999999997],
          [-114.57170099999999, 12.494165000000002],
          [-114.63358600000001, 12.471817000000001],
          [-114.66851700000001, 12.452544000000003],
          [-114.691485, 12.436711000000003],
          [-114.704129, 12.424230000000001],
          [-114.73655, 12.403848000000004],
          [-114.742887, 12.401677999999997],
          [-114.76502099999999, 12.401361000000001],
          [-114.775766, 12.404074999999999],
          [-114.78900300000001, 12.411014999999999],
          [-114.79990699999999, 12.419387],
          [-114.80548099999999, 12.427487999999997],
          [-114.80611400000001, 12.434182],
          [-114.77750499999999, 12.462198999999998],
          [-114.739644, 12.496332000000002],
          [-114.70614, 12.521273],
          [-114.534726, 12.600540000000002],
          [-114.524629, 12.603924999999997],
          [-114.51407800000001, 12.604059],
          [-114.44996499999999, 12.600361],
          [-114.41343499999999, 12.586768],
          [-114.402289, 12.580542999999999],
          [-114.39938900000001, 12.576411],
          [-114.40401499999999, 12.572287000000003]
        ],
        [
          [-113.940505, 12.558317000000002],
          [-113.915288, 12.564920999999998],
          [-113.87876399999999, 12.565925],
          [-113.87080399999999, 12.558014999999997],
          [-113.868461, 12.551493],
          [-113.887678, 12.533636999999999],
          [-113.952958, 12.507174],
          [-113.993909, 12.501795999999999],
          [-114.12024400000001, 12.501838],
          [-114.143711, 12.506172],
          [-114.16398699999999, 12.507843999999999],
          [-114.19728, 12.502001999999997],
          [-114.232464, 12.491052000000003],
          [-114.304371, 12.502322],
          [-114.343096, 12.510171],
          [-114.3474, 12.512045999999998],
          [-114.361378, 12.525640000000003],
          [-114.36236099999999, 12.542512000000002],
          [-114.34140099999999, 12.563704999999999],
          [-114.310913, 12.585447000000002],
          [-114.29002, 12.595376000000002],
          [-114.244234, 12.609194000000002],
          [-114.223759, 12.612955],
          [-114.21033600000001, 12.609684000000001],
          [-114.20600099999999, 12.606907999999997],
          [-114.184819, 12.603772999999997],
          [-114.136739, 12.60935],
          [-114.11339699999999, 12.616745000000002],
          [-114.10324299999999, 12.617694999999998],
          [-114.095833, 12.617786000000002],
          [-114.090014, 12.614798],
          [-114.081829, 12.603715999999999],
          [-114.079016, 12.589976999999998],
          [-114.075187, 12.583745],
          [-114.041572, 12.556209000000003],
          [-114.02533399999999, 12.551763000000001],
          [-114.009274, 12.551445000000001],
          [-113.940505, 12.558317000000002]
        ],
        [
          [-112.41742400000001, 13.815463999999999],
          [-112.364079, 13.829372],
          [-112.35115200000001, 13.834767999999997],
          [-112.324284, 13.824444],
          [-112.31092699999999, 13.783451999999997],
          [-112.31726699999999, 13.771987000000003],
          [-112.322172, 13.768315000000001],
          [-112.34267399999999, 13.762306000000002],
          [-112.34864400000001, 13.764392999999998],
          [-112.34916899999999, 13.768479999999997],
          [-112.35723300000001, 13.773918000000002],
          [-112.38107600000001, 13.776744],
          [-112.44378599999999, 13.776142],
          [-112.46555000000001, 13.767169000000003],
          [-112.471, 13.763466000000001],
          [-112.49731399999999, 13.738596000000001],
          [-112.49705599999999, 13.734386999999998],
          [-112.467679, 13.68139],
          [-112.44303, 13.668049000000003],
          [-112.40149199999999, 13.686512999999998],
          [-112.39856900000001, 13.68721],
          [-112.394474, 13.684665000000003],
          [-112.428946, 13.642161999999999],
          [-112.461018, 13.606310999999998],
          [-112.46817200000001, 13.600996000000002],
          [-112.46775600000001, 13.598221000000002],
          [-112.45992899999999, 13.594372999999997],
          [-112.43966699999999, 13.590398999999998],
          [-112.426062, 13.593356999999997],
          [-112.40247, 13.607981000000002],
          [-112.38713999999999, 13.612428000000001],
          [-112.36190300000001, 13.6188],
          [-112.322733, 13.623401999999999],
          [-112.265346, 13.62643],
          [-112.179531, 13.624808999999999],
          [-112.161617, 13.623286999999998],
          [-112.15239299999999, 13.619484999999997],
          [-112.15967699999999, 13.593614000000002],
          [-112.163996, 13.584606999999998],
          [-112.25964099999999, 13.527155999999998],
          [-112.29147, 13.517102999999999],
          [-112.314889, 13.486065000000004],
          [-112.32368299999999, 13.467861],
          [-112.326134, 13.441513999999998],
          [-112.361592, 13.427760999999997],
          [-112.416473, 13.435293000000001],
          [-112.495215, 13.452379],
          [-112.51700399999999, 13.432184],
          [-112.570527, 13.448909],
          [-112.60037500000001, 13.468832999999997],
          [-112.646017, 13.466133999999997],
          [-112.66283100000001, 13.463678999999999],
          [-112.684413, 13.466597],
          [-112.71676500000001, 13.478467000000002],
          [-112.720471, 13.481572],
          [-112.719447, 13.488028],
          [-112.722846, 13.494087],
          [-112.74308400000001, 13.50571],
          [-112.770196, 13.504289999999997],
          [-112.798914, 13.494255000000003],
          [-112.80903599999999, 13.494504999999997],
          [-112.825515, 13.497048],
          [-112.83890500000001, 13.502270000000003],
          [-112.88620499999999, 13.510697],
          [-112.939629, 13.520088000000001],
          [-112.954939, 13.520449],
          [-112.96629999999999, 13.512169999999998],
          [-112.96722199999999, 13.509993000000001],
          [-112.94900999999999, 13.498212000000002],
          [-112.939573, 13.497762999999999],
          [-112.92174800000001, 13.501396999999997],
          [-112.890173, 13.486705],
          [-112.762676, 13.45756],
          [-112.74267800000001, 13.447851999999997],
          [-112.72265100000001, 13.433352],
          [-112.67325, 13.413246000000001],
          [-112.630018, 13.405572999999997],
          [-112.62037699999999, 13.401601],
          [-112.601148, 13.382165],
          [-112.60652200000001, 13.363660000000003],
          [-112.63044099999999, 13.322668],
          [-112.657569, 13.303550999999999],
          [-112.695698, 13.281317999999999],
          [-112.70776799999999, 13.276046000000001],
          [-112.712008, 13.278120000000001],
          [-112.77415500000001, 13.290432000000003],
          [-112.787994, 13.279905],
          [-112.818187, 13.265368000000002],
          [-112.88638399999999, 13.291336999999999],
          [-112.900688, 13.302976000000001],
          [-112.909051, 13.324221999999999],
          [-112.984715, 13.339917999999997],
          [-113.00852499999999, 13.339733000000003],
          [-113.056007, 13.329228999999998],
          [-113.07928799999999, 13.321959999999997],
          [-113.09904, 13.310513],
          [-113.11627999999999, 13.297311999999998],
          [-113.10132200000001, 13.286901],
          [-113.09613300000001, 13.286866000000003],
          [-113.03913399999999, 13.293314000000002],
          [-113.01764299999999, 13.297714999999997],
          [-113.015994, 13.300230999999997],
          [-113.012992, 13.299453],
          [-112.97091, 13.282623999999998],
          [-112.94420099999999, 13.259082999999997],
          [-112.94346300000001, 13.256956000000002],
          [-112.95098200000001, 13.248990999999997],
          [-112.997739, 13.231175999999998],
          [-113.056971, 13.214756000000001],
          [-113.07791599999999, 13.211444],
          [-113.12547699999999, 13.211841],
          [-113.163333, 13.216712999999999],
          [-113.169724, 13.220236],
          [-113.20172199999999, 13.221679000000002],
          [-113.209732, 13.218772999999999],
          [-113.21510699999999, 13.213355999999997],
          [-113.215967, 13.209297],
          [-113.21380199999999, 13.205058999999999],
          [-113.16600199999999, 13.180643000000003],
          [-113.123865, 13.175445000000003],
          [-113.09701899999999, 13.183289000000002],
          [-113.073982, 13.187091000000002],
          [-112.949333, 13.187345999999998],
          [-112.88032100000001, 13.164797999999998],
          [-112.87483900000001, 13.16095],
          [-112.869797, 13.150849000000001],
          [-112.90054, 13.132075999999998],
          [-112.911371, 13.126812999999999],
          [-112.950736, 13.119788],
          [-112.99724599999999, 13.119490999999996],
          [-113.118673, 13.091033000000003],
          [-113.128881, 13.092571],
          [-113.13270800000001, 13.094935999999997],
          [-113.13398799999999, 13.099350999999999],
          [-113.14636100000001, 13.100883000000003],
          [-113.18001000000001, 13.094523000000002],
          [-113.21544, 13.075943000000002],
          [-113.22095300000001, 13.068238999999998],
          [-113.22224, 13.061798000000003],
          [-113.221204, 13.060367],
          [-113.21331799999999, 13.055891000000003],
          [-113.20538400000001, 13.056148],
          [-113.20021700000001, 13.042039000000003],
          [-113.204319, 13.033639999999998],
          [-113.23528200000001, 13.007398000000002],
          [-113.301142, 12.991191999999998],
          [-113.31258299999999, 12.991486000000002],
          [-113.34870699999999, 13.008372999999999],
          [-113.34903700000001, 13.011195999999998],
          [-113.320929, 13.036838000000003],
          [-113.324265, 13.043308000000003],
          [-113.365239, 13.07208],
          [-113.39692099999999, 13.060398999999997],
          [-113.40260799999999, 13.070092000000002],
          [-113.40426299999999, 13.080511000000001],
          [-113.38469900000001, 13.115354000000004],
          [-113.38038900000001, 13.120468000000002],
          [-113.34553299999999, 13.139564999999997],
          [-113.328206, 13.141993],
          [-113.28401199999999, 13.173867000000001],
          [-113.282735, 13.175815],
          [-113.286351, 13.184288000000002],
          [-113.31014300000001, 13.194426],
          [-113.350266, 13.192338999999997],
          [-113.36818, 13.185336999999997],
          [-113.368921, 13.180832000000002],
          [-113.366525, 13.176707999999998],
          [-113.48652000000001, 13.085915],
          [-113.4896, 13.074702000000002],
          [-113.49885, 13.065362999999998],
          [-113.535942, 13.077987999999998],
          [-113.56356199999999, 13.089768999999997],
          [-113.57700600000001, 13.093176999999997],
          [-113.65449699999999, 13.084601999999997],
          [-113.67598100000001, 13.069830000000003],
          [-113.675736, 13.054777999999999],
          [-113.66381000000001, 13.053694],
          [-113.601294, 13.056655999999997],
          [-113.595819, 13.056308999999999],
          [-113.58083099999999, 13.049047999999999],
          [-113.543429, 12.995244999999997],
          [-113.55676199999999, 12.968862000000001],
          [-113.600664, 12.942628999999997],
          [-113.62748300000001, 12.937126999999997],
          [-113.67131699999999, 12.932926000000002],
          [-113.70119700000001, 12.926394000000002],
          [-113.73071300000001, 12.893996000000001],
          [-113.715263, 12.878442],
          [-113.70460299999999, 12.878045999999998],
          [-113.695896, 12.881008999999999],
          [-113.688713, 12.871974999999999],
          [-113.69669300000001, 12.861519000000001],
          [-113.71464399999999, 12.852924999999999],
          [-113.778199, 12.834386000000002],
          [-113.796111, 12.842655],
          [-113.80093500000001, 12.846893999999999],
          [-113.807353, 12.848584000000002],
          [-113.81797800000001, 12.848650999999997],
          [-113.849289, 12.838915],
          [-113.854196, 12.836412000000003],
          [-113.90358, 12.793418000000003],
          [-113.92404099999999, 12.767215999999998],
          [-113.96327400000001, 12.747599999999998],
          [-113.97178, 12.744861],
          [-113.99015800000001, 12.743262999999999],
          [-114.016213, 12.743465999999998],
          [-114.02260999999999, 12.755946000000002],
          [-114.03715299999999, 12.763413999999997],
          [-114.05051800000001, 12.763523],
          [-114.064292, 12.760091000000003],
          [-114.08508800000001, 12.751193],
          [-114.10656499999999, 12.745572000000003],
          [-114.129017, 12.742168],
          [-114.136965, 12.742359],
          [-114.14874499999999, 12.745677],
          [-114.12543099999999, 12.783298000000002],
          [-114.072878, 12.841099],
          [-114.06742499999999, 12.845303000000001],
          [-114.055228, 12.850465],
          [-114.04094799999999, 12.854135],
          [-114.03050200000001, 12.855052],
          [-113.98454699999999, 12.889626],
          [-113.935992, 12.915771999999997],
          [-113.894564, 12.926986],
          [-113.862954, 12.944374000000003],
          [-113.85046399999999, 12.957278000000002],
          [-113.873411, 12.963403],
          [-113.90280200000001, 12.968445000000003],
          [-113.91362699999999, 12.965390999999997],
          [-113.91770299999999, 12.962169000000003],
          [-113.93478099999999, 12.958928],
          [-113.97687099999999, 12.955143999999997],
          [-113.97974300000001, 12.962189000000002],
          [-113.976869, 12.996831],
          [-113.932221, 13.06297],
          [-113.887461, 13.086958000000003],
          [-113.87535600000001, 13.089801999999999],
          [-113.858891, 13.088844000000002],
          [-113.804787, 13.113157999999999],
          [-113.783465, 13.131822],
          [-113.77670699999999, 13.142857999999997],
          [-113.779087, 13.158821000000003],
          [-113.788521, 13.161380999999999],
          [-113.80628999999999, 13.157423999999999],
          [-113.82287500000001, 13.142372000000002],
          [-113.82397800000001, 13.139789999999998],
          [-113.86171100000001, 13.119224000000003],
          [-113.98279199999999, 13.066277],
          [-113.993807, 13.050502000000002],
          [-114.02428800000001, 13.016607999999998],
          [-114.055554, 12.987209],
          [-114.07662300000001, 12.970588999999997],
          [-114.123489, 12.95617],
          [-114.14516699999999, 12.945034],
          [-114.15901400000001, 12.945323000000002],
          [-114.16540900000001, 12.943244],
          [-114.21211, 12.909748999999998],
          [-114.22355999999999, 12.896064000000003],
          [-114.227193, 12.883026000000001],
          [-114.226494, 12.876257000000003],
          [-114.23177100000001, 12.872293999999997],
          [-114.27673899999999, 12.853648],
          [-114.29896500000001, 12.846479000000002],
          [-114.305713, 12.846871],
          [-114.30019300000001, 12.852023000000003],
          [-114.29842199999999, 12.863176000000003],
          [-114.300002, 12.892252],
          [-114.30693600000001, 12.911783],
          [-114.31288799999999, 12.918672999999998],
          [-114.38528500000001, 12.959767],
          [-114.40749, 12.968333999999999],
          [-114.476315, 12.984203999999998],
          [-114.511672, 12.988548000000002],
          [-114.52469500000001, 12.991622999999997],
          [-114.528538, 13.001891999999998],
          [-114.516842, 13.030312000000002],
          [-114.51521299999999, 13.077984999999998],
          [-114.529844, 13.168882000000004],
          [-114.53369900000001, 13.183512999999998],
          [-114.53955199999999, 13.196351],
          [-114.574343, 13.239919],
          [-114.594977, 13.257161000000004],
          [-114.613723, 13.267800000000001],
          [-114.691855, 13.284109999999998],
          [-114.698264, 13.284294000000003],
          [-114.740161, 13.276516999999998],
          [-114.777368, 13.280008000000002],
          [-114.79205400000001, 13.286696],
          [-114.79383999999999, 13.288862000000002],
          [-114.75153700000001, 13.307780999999999],
          [-114.74309, 13.314770000000003],
          [-114.70059800000001, 13.401162],
          [-114.69962899999999, 13.412872999999998],
          [-114.70258799999999, 13.420527999999997],
          [-114.69331, 13.446084999999997],
          [-114.62967800000001, 13.510196999999998],
          [-114.61870400000001, 13.514972],
          [-114.60254599999999, 13.518751000000002],
          [-114.591678, 13.518597],
          [-114.54092299999999, 13.539620999999997],
          [-114.52206000000001, 13.577786000000003],
          [-114.511233, 13.578645999999999],
          [-114.500282, 13.574423000000003],
          [-114.46832800000001, 13.570338999999997],
          [-114.43184099999999, 13.584783000000002],
          [-114.411385, 13.598452000000002],
          [-114.344244, 13.630901000000001],
          [-114.22566, 13.661366000000001],
          [-114.19695899999999, 13.664639000000001],
          [-114.186597, 13.658577999999999],
          [-114.08613, 13.649054],
          [-114.05622600000001, 13.652430000000003],
          [-114.03159199999999, 13.660854],
          [-113.994572, 13.656905000000002],
          [-113.983015, 13.649835000000003],
          [-113.98258100000001, 13.648251000000002],
          [-113.984847, 13.604595000000003],
          [-113.98219900000001, 13.553156000000001],
          [-113.971114, 13.539436000000002],
          [-113.94755, 13.540244000000001],
          [-113.939099, 13.538271000000002],
          [-113.92926499999999, 13.533253000000002],
          [-113.925905, 13.529051000000003],
          [-113.92298199999999, 13.520153],
          [-113.92218299999999, 13.499035999999997],
          [-113.91989699999999, 13.485202000000001],
          [-113.909415, 13.442413000000002],
          [-113.89580000000001, 13.422108000000001],
          [-113.802932, 13.350895999999999],
          [-113.795299, 13.349046999999999],
          [-113.77427499999999, 13.360242999999997],
          [-113.773191, 13.372442],
          [-113.81150600000001, 13.412374999999997],
          [-113.87292199999999, 13.445743],
          [-113.874177, 13.447817],
          [-113.888891, 13.504682000000003],
          [-113.87594999999999, 13.542769],
          [-113.86909600000001, 13.551844000000003],
          [-113.848082, 13.560589],
          [-113.82482300000001, 13.577616999999996],
          [-113.81313599999999, 13.588580999999998],
          [-113.82375300000001, 13.597650999999999],
          [-113.84682799999999, 13.612648],
          [-113.85250199999999, 13.613517000000002],
          [-113.877756, 13.629528999999998],
          [-113.879943, 13.634072000000003],
          [-113.87428600000001, 13.64611],
          [-113.86827500000001, 13.649687999999998],
          [-113.85854499999999, 13.651138000000003],
          [-113.749178, 13.646223999999997],
          [-113.705322, 13.640923],
          [-113.667261, 13.639007999999997],
          [-113.663007, 13.639857999999997],
          [-113.64869300000001, 13.654125],
          [-113.658008, 13.661479999999997],
          [-113.67672099999999, 13.669663],
          [-113.79797099999999, 13.696508000000001],
          [-113.862886, 13.706943000000003],
          [-113.88809900000001, 13.705447],
          [-113.91834399999999, 13.695663000000003],
          [-113.93027900000001, 13.696790999999997],
          [-113.932964, 13.703778],
          [-113.93521999999999, 13.813046999999997],
          [-113.823385, 13.865012999999998],
          [-113.755054, 13.883564999999997],
          [-113.72117600000001, 13.890614999999997],
          [-113.648798, 13.880102999999998],
          [-113.571362, 13.832101000000002],
          [-113.55082300000001, 13.78689],
          [-113.551088, 13.763109999999998],
          [-113.55325099999999, 13.759512],
          [-113.557647, 13.734741],
          [-113.554226, 13.722450000000002],
          [-113.54960500000001, 13.717967000000002],
          [-113.51520500000001, 13.716504999999998],
          [-113.493401, 13.728316],
          [-113.46989199999999, 13.766536000000002],
          [-113.46246300000001, 13.795292000000003],
          [-113.480377, 13.814664999999998],
          [-113.48734999999999, 13.834274],
          [-113.479457, 13.842019999999998],
          [-113.45156, 13.839284],
          [-113.40671599999999, 13.828662999999999],
          [-113.35358, 13.809731],
          [-113.34340800000001, 13.810865999999997],
          [-113.324872, 13.831048000000003],
          [-113.322687, 13.836190000000002],
          [-113.324881, 13.848421000000002],
          [-113.328137, 13.849851000000001],
          [-113.395813, 13.858772000000002],
          [-113.446406, 13.875034999999997],
          [-113.46201099999999, 13.880588000000003],
          [-113.512024, 13.909156000000003],
          [-113.528697, 13.921717000000001],
          [-113.53652399999999, 13.930770000000003],
          [-113.53320400000001, 13.941116999999998],
          [-113.52039199999999, 13.963386999999997],
          [-113.51334700000001, 13.968750999999997],
          [-113.48460299999999, 13.976500000000001],
          [-113.46942100000001, 13.977282000000002],
          [-113.46111300000001, 13.972769],
          [-113.45264499999999, 13.963509000000002],
          [-113.273676, 13.890408],
          [-113.268149, 13.888741000000003],
          [-113.236952, 13.891818],
          [-113.12727799999999, 13.856748000000003],
          [-113.122513, 13.856639000000001],
          [-113.09342000000001, 13.861569000000003],
          [-113.08941899999999, 13.865233000000003],
          [-113.19861800000001, 13.929923000000002],
          [-113.233229, 13.940992999999999],
          [-113.27032500000001, 13.958565999999998],
          [-113.29900900000001, 13.985626000000003],
          [-113.302198, 13.991706],
          [-113.29775599999999, 13.996425000000002],
          [-113.276536, 13.998446999999999],
          [-113.23473000000001, 13.996972],
          [-113.221576, 13.989319000000002],
          [-113.21730600000001, 13.983659000000003],
          [-113.129494, 13.946551],
          [-113.06985700000001, 13.934427999999997],
          [-113.052671, 13.936711000000003],
          [-113.050941, 13.939998000000003],
          [-113.02442500000001, 13.956954000000003],
          [-112.87619699999999, 13.932445999999999],
          [-112.87166300000001, 13.933278999999999],
          [-112.85628399999999, 13.947384999999997],
          [-112.85278500000001, 13.974583000000003],
          [-112.855096, 13.994501],
          [-112.84089599999999, 13.996758999999997],
          [-112.72342499999999, 13.99172],
          [-112.72252399999999, 13.987364],
          [-112.739766, 13.944797999999999],
          [-112.75197800000001, 13.933466000000003],
          [-112.80480700000001, 13.899175],
          [-112.82329899999999, 13.890928000000002],
          [-112.892517, 13.842525000000002],
          [-112.90263300000001, 13.830146],
          [-112.90979100000001, 13.810405000000003],
          [-112.916334, 13.771216000000003],
          [-112.904312, 13.750824999999999],
          [-112.892875, 13.742012000000003],
          [-112.88199800000001, 13.738320000000002],
          [-112.87449799999999, 13.737960999999999],
          [-112.850336, 13.740040999999998],
          [-112.84781100000001, 13.746625000000002],
          [-112.852269, 13.752318000000002],
          [-112.85471799999999, 13.770271000000001],
          [-112.849997, 13.821461999999997],
          [-112.841361, 13.830221000000002],
          [-112.822543, 13.843203000000003],
          [-112.790211, 13.858058],
          [-112.75816800000001, 13.840271999999999],
          [-112.75343699999999, 13.834451999999999],
          [-112.725302, 13.8354],
          [-112.65045599999999, 13.863720999999998],
          [-112.625607, 13.881231999999997],
          [-112.626441, 13.890450000000001],
          [-112.63988699999999, 13.899687999999998],
          [-112.641805, 13.902498999999999],
          [-112.639375, 13.91422],
          [-112.635378, 13.918610000000001],
          [-112.587705, 13.926960999999999],
          [-112.585985, 13.908101000000002],
          [-112.567395, 13.900357999999997],
          [-112.54966099999999, 13.900137],
          [-112.526283, 13.913266],
          [-112.48766599999999, 13.941968000000003],
          [-112.470336, 13.962099000000002],
          [-112.43260799999999, 13.976028999999997],
          [-112.42140800000001, 13.975682999999997],
          [-112.415177, 13.973081],
          [-112.411618, 13.969282],
          [-112.422573, 13.948661999999999],
          [-112.437604, 13.939833999999998],
          [-112.43741600000001, 13.936978000000003],
          [-112.426458, 13.930850999999997],
          [-112.38862599999999, 13.924486000000002],
          [-112.36216099999999, 13.926200000000001],
          [-112.32410300000001, 13.916604],
          [-112.33320900000001, 13.902549999999998],
          [-112.364777, 13.883921],
          [-112.37706299999999, 13.886727999999998],
          [-112.38613000000001, 13.890706000000002],
          [-112.39474999999999, 13.894601999999999],
          [-112.40369999999999, 13.901145999999997],
          [-112.414977, 13.902231],
          [-112.44824, 13.902605000000001],
          [-112.468511, 13.888621],
          [-112.43365299999999, 13.824314000000001],
          [-112.429326, 13.820113999999997],
          [-112.41742400000001, 13.815463999999999]
        ],
        [
          [-112.24288999999999, 14.241191999999998],
          [-112.280629, 14.242344000000003],
          [-112.31141500000001, 14.221114999999998],
          [-112.26511099999999, 14.135731999999997],
          [-112.273605, 14.125630000000001],
          [-112.34352200000001, 14.119174000000001],
          [-112.401892, 14.120755000000003],
          [-112.42539099999999, 14.127614000000001],
          [-112.482674, 14.129812999999999],
          [-112.514794, 14.114320999999997],
          [-112.52903599999999, 14.093778999999998],
          [-112.53038799999999, 14.087766000000002],
          [-112.54153299999999, 14.083666000000001],
          [-112.554461, 14.084620000000001],
          [-112.55723699999999, 14.086461999999997],
          [-112.56959499999999, 14.114800000000002],
          [-112.55749700000001, 14.160682999999999],
          [-112.55988400000001, 14.170941],
          [-112.562829, 14.177979],
          [-112.58422200000001, 14.187477000000001],
          [-112.59750600000001, 14.179685999999997],
          [-112.615103, 14.116224000000003],
          [-112.631214, 14.081924],
          [-112.656801, 14.061048999999997],
          [-112.706831, 14.050576999999997],
          [-112.77130299999999, 14.046883000000001],
          [-112.777906, 14.050364000000002],
          [-112.779473, 14.065269],
          [-112.79204100000001, 14.072665],
          [-112.80906200000001, 14.078916999999997],
          [-112.88242299999999, 14.096074000000002],
          [-112.95867999999999, 14.116441000000002],
          [-112.973149, 14.125427000000002],
          [-112.983857, 14.134357999999999],
          [-112.99734000000001, 14.134341],
          [-113.07574600000001, 14.099570999999997],
          [-113.07648499999999, 14.096077000000001],
          [-113.05720099999999, 14.073576000000003],
          [-113.03613100000001, 14.055619],
          [-113.020589, 14.045202000000003],
          [-112.9647, 14.03465],
          [-112.876788, 14.002307000000002],
          [-112.871836, 13.999274999999997],
          [-112.87141600000001, 13.997157000000001],
          [-112.94754699999999, 13.983519000000001],
          [-112.982406, 13.984696999999997],
          [-113.09746200000001, 14.004516000000002],
          [-113.20252500000001, 14.030121999999999],
          [-113.20988500000001, 14.034925000000001],
          [-113.21456800000001, 14.042417999999998],
          [-113.21811500000001, 14.043909],
          [-113.28970100000001, 14.050330000000002],
          [-113.344807, 14.040619],
          [-113.36557400000001, 14.039051999999998],
          [-113.419783, 14.059638],
          [-113.418343, 14.064053000000001],
          [-113.41293300000001, 14.069811000000001],
          [-113.316127, 14.140389999999996],
          [-113.28187399999999, 14.147554999999997],
          [-113.274215, 14.148102000000002],
          [-113.262643, 14.145099000000002],
          [-113.227567, 14.123364000000002],
          [-113.199117, 14.102004999999998],
          [-113.16861700000001, 14.088385000000002],
          [-113.15640200000001, 14.090086999999997],
          [-113.14874, 14.106121000000002],
          [-113.16760500000001, 14.127817999999998],
          [-113.20967200000001, 14.150350000000003],
          [-113.22370900000001, 14.162120000000002],
          [-113.202801, 14.208080000000002],
          [-113.17010099999999, 14.216704],
          [-113.073927, 14.195107],
          [-113.060846, 14.194502],
          [-113.036662, 14.199235000000002],
          [-113.00057899999999, 14.211768],
          [-112.99809400000001, 14.214122000000003],
          [-113.006979, 14.221846999999997],
          [-113.061678, 14.235649000000002],
          [-113.08250699999999, 14.244495],
          [-113.10184100000001, 14.257938000000003],
          [-113.10240999999999, 14.260344000000003],
          [-113.09928400000001, 14.264065000000002],
          [-113.04431600000001, 14.306336000000002],
          [-113.00439, 14.300134999999997],
          [-112.99321699999999, 14.296253999999998],
          [-112.98235600000001, 14.287495],
          [-112.94127, 14.279614000000002],
          [-112.888204, 14.283099999999997],
          [-112.87885800000001, 14.288533000000001],
          [-112.869811, 14.304906000000003],
          [-112.88402300000001, 14.307087000000003],
          [-112.91245, 14.307191000000003],
          [-112.921122, 14.313268],
          [-112.936757, 14.330513000000003],
          [-112.93644, 14.334923000000003],
          [-112.92558600000001, 14.339686],
          [-112.895407, 14.345305000000003],
          [-112.870555, 14.335743],
          [-112.82196400000001, 14.328501000000003],
          [-112.804789, 14.339509999999997],
          [-112.774048, 14.366826000000003],
          [-112.78742, 14.369014999999997],
          [-112.839234, 14.372477000000003],
          [-112.883107, 14.400443000000003],
          [-112.88886, 14.409384000000003],
          [-112.886358, 14.410584999999998],
          [-112.86493899999999, 14.404339999999998],
          [-112.84417300000001, 14.402842],
          [-112.812207, 14.403464],
          [-112.78777600000001, 14.411313],
          [-112.774509, 14.419721000000003],
          [-112.771106, 14.429515000000002],
          [-112.733845, 14.460662],
          [-112.72316900000001, 14.46208],
          [-112.68994, 14.459860999999997],
          [-112.61095499999999, 14.475774999999999],
          [-112.601666, 14.490423],
          [-112.60053400000001, 14.494945999999999],
          [-112.60902999999999, 14.496167],
          [-112.61919700000001, 14.493673999999999],
          [-112.622794, 14.494188999999999],
          [-112.653673, 14.506571999999998],
          [-112.66622000000001, 14.544086999999998],
          [-112.665999, 14.564492999999999],
          [-112.63856899999999, 14.587448000000002],
          [-112.61613, 14.601852000000001],
          [-112.56771, 14.621304000000002],
          [-112.560171, 14.619680000000002],
          [-112.55041800000001, 14.610996],
          [-112.549635, 14.601024000000002],
          [-112.545009, 14.594253000000002],
          [-112.50282000000001, 14.593451000000002],
          [-112.45381699999999, 14.618515000000002],
          [-112.35470900000001, 14.638280000000002],
          [-112.337964, 14.637403999999997],
          [-112.329835, 14.632102000000003],
          [-112.337212, 14.589095],
          [-112.37231700000001, 14.531174999999998],
          [-112.38761, 14.522869999999998],
          [-112.41826699999999, 14.515244000000003],
          [-112.467197, 14.476609000000003],
          [-112.498571, 14.449537999999997],
          [-112.505516, 14.441876],
          [-112.512483, 14.427349],
          [-112.493991, 14.354683999999999],
          [-112.47681399999999, 14.350954999999999],
          [-112.38734299999999, 14.359499],
          [-112.36468199999999, 14.364612999999999],
          [-112.34486000000001, 14.39163],
          [-112.348389, 14.401502],
          [-112.355073, 14.413052],
          [-112.358724, 14.415585],
          [-112.35609, 14.423470000000002],
          [-112.32806299999999, 14.434372000000003],
          [-112.32055399999999, 14.433829000000003],
          [-112.301713, 14.428697],
          [-112.27950799999999, 14.415872],
          [-112.227835, 14.376424],
          [-112.23471799999999, 14.362023999999998],
          [-112.224965, 14.357371999999998],
          [-112.200953, 14.355331999999997],
          [-112.129257, 14.396414],
          [-112.125339, 14.396396000000003],
          [-112.09043700000001, 14.372627999999999],
          [-112.08924999999999, 14.367643999999999],
          [-112.11953, 14.329770000000003],
          [-112.138294, 14.295712000000002],
          [-112.147142, 14.266992000000002],
          [-112.14651900000001, 14.249119999999998],
          [-112.116569, 14.248536999999999],
          [-112.10796199999999, 14.260525000000001],
          [-112.10763499999999, 14.28024],
          [-112.08234200000001, 14.309944999999999],
          [-111.98617100000001, 14.350413000000003],
          [-111.98178100000001, 14.347971000000001],
          [-111.966218, 14.332737000000002],
          [-111.963817, 14.328999000000003],
          [-111.964103, 14.269049000000003],
          [-111.97205299999999, 14.230702],
          [-111.98612700000001, 14.213774],
          [-112.081083, 14.154274999999998],
          [-112.11220499999999, 14.148558999999999],
          [-112.194827, 14.174128000000003],
          [-112.223175, 14.194794000000002],
          [-112.22443899999999, 14.202365],
          [-112.21982600000001, 14.206288999999998],
          [-112.20748800000001, 14.206283999999997],
          [-112.203699, 14.212055],
          [-112.23383000000001, 14.243329000000003],
          [-112.24288999999999, 14.241191999999998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02164",
      state: "02",
      county: "164",
      name: "Lake And Peninsula County",
      lsad: "Borough",
      censusArea: 23652.009,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.02607, 12.559756999999998],
          [-116.990969, 12.547939],
          [-116.975549, 12.540446000000003],
          [-116.97289599999999, 12.536504999999998],
          [-116.98608999999999, 12.532749000000003],
          [-117.003409, 12.535639000000003],
          [-117.00652299999999, 12.538910000000001],
          [-117.01771099999999, 12.543081],
          [-117.053384, 12.550424999999997],
          [-117.113193, 12.552658000000001],
          [-117.12139300000001, 12.551963],
          [-117.14221900000001, 12.542389999999997],
          [-117.150309, 12.5336],
          [-117.168777, 12.530209999999997],
          [-117.32605899999999, 12.525168999999998],
          [-117.32889800000001, 12.528154999999998],
          [-117.32611, 12.540374999999997],
          [-117.29863499999999, 12.560051000000001],
          [-117.288702, 12.566039000000004],
          [-117.25009800000001, 12.582141999999997],
          [-117.146636, 12.583651000000003],
          [-117.09114600000001, 12.581133999999999],
          [-117.077383, 12.579034999999998],
          [-117.02607, 12.559756999999998]
        ],
        [
          [-114.305926, 14.646822],
          [-114.303447, 14.460385000000002],
          [-114.432332, 14.418998000000002],
          [-114.46574100000001, 14.361356],
          [-114.57010700000001, 14.333055000000002],
          [-114.729769, 14.332836999999998],
          [-114.730369, 14.304668999999997],
          [-115.00813199999999, 14.290905000000002],
          [-115.057741, 14.236209000000002],
          [-115.221463, 14.236407999999997],
          [-115.22131300000001, 14.193193],
          [-115.33194499999999, 14.193283000000001],
          [-115.331999, 14.107967000000002],
          [-115.280169, 14.108590999999997],
          [-115.33167800000001, 14.048211000000002],
          [-115.33024499999999, 13.876342999999999],
          [-115.44602, 13.871459000000002],
          [-115.44625099999999, 13.827094000000002],
          [-115.53882999999999, 13.799805999999997],
          [-115.693773, 13.797987],
          [-115.768022, 13.668261000000001],
          [-115.89845500000001, 13.668100000000003],
          [-115.87199000000001, 13.599963000000002],
          [-115.921548, 13.554333999999997],
          [-115.95277200000001, 13.598213000000001],
          [-116.071148, 13.584423999999999],
          [-116.13203200000001, 13.509692000000001],
          [-116.37023500000001, 13.522368],
          [-116.458121, 13.478904999999997],
          [-116.508759, 13.434831000000003],
          [-116.50222600000001, 13.362606],
          [-116.61739499999999, 13.334941999999998],
          [-116.672751, 13.234909000000002],
          [-116.75077999999999, 13.235795000000003],
          [-116.75081499999999, 13.162878],
          [-116.355401, 13.159678999999997],
          [-116.357358, 13.15757],
          [-116.36852400000001, 13.149985999999998],
          [-116.43511000000001, 13.127429999999997],
          [-116.45649700000001, 13.106040999999998],
          [-116.44461000000001, 13.100087000000002],
          [-116.441566, 13.094698000000001],
          [-116.441599, 13.085158],
          [-116.50923900000001, 13.054910999999997],
          [-116.53558699999999, 13.047905],
          [-116.562827, 13.020313999999999],
          [-116.54766699999999, 13.004629000000001],
          [-116.5472, 12.986488000000001],
          [-116.55052, 12.984610000000004],
          [-116.55507700000001, 12.983550000000001],
          [-116.63784000000001, 12.993904999999998],
          [-116.75364200000001, 12.991225],
          [-116.762718, 12.986342],
          [-116.781421, 12.971879000000001],
          [-116.7869, 12.965035],
          [-116.788341, 12.960693],
          [-116.786802, 12.941443],
          [-116.79731000000001, 12.911717000000003],
          [-116.80443199999999, 12.905881],
          [-116.82598200000001, 12.897666999999998],
          [-116.83932200000001, 12.901854],
          [-116.876316, 12.942827999999999],
          [-116.88537199999999, 12.953283999999996],
          [-116.88568599999999, 12.957965000000002],
          [-116.882464, 12.960071999999997],
          [-116.882893, 12.962581999999998],
          [-116.88630699999999, 12.964869],
          [-116.89368300000001, 12.965964999999997],
          [-116.90972500000001, 12.965581],
          [-116.91879599999999, 12.963583],
          [-116.93569199999999, 12.954873],
          [-116.935629, 12.920087000000002],
          [-116.98617100000001, 12.911130999999997],
          [-117.00595000000001, 12.904220000000002],
          [-117.01566500000001, 12.898485999999998],
          [-117.03462400000001, 12.884487],
          [-117.073453, 12.838344999999997],
          [-117.159494, 12.833477000000002],
          [-117.16381100000001, 12.826065999999997],
          [-117.163272, 12.823542000000003],
          [-117.14098999999999, 12.802275000000002],
          [-117.140277, 12.790874000000002],
          [-117.161372, 12.774133999999997],
          [-117.183636, 12.769078999999998],
          [-117.20172400000001, 12.767510999999999],
          [-117.28376399999999, 12.800766000000003],
          [-117.29051100000001, 12.804713],
          [-117.29123100000001, 12.811076999999997],
          [-117.298283, 12.818567000000002],
          [-117.33273500000001, 12.838397999999998],
          [-117.378771, 12.861696000000002],
          [-117.39466300000001, 12.864426000000002],
          [-117.40567899999999, 12.864215999999999],
          [-117.43693200000001, 12.858522],
          [-117.45759000000001, 12.848204000000003],
          [-117.472407, 12.833356000000002],
          [-117.46992499999999, 12.824888999999999],
          [-117.46236099999999, 12.809603000000003],
          [-117.45762199999999, 12.804291],
          [-117.447768, 12.801245999999999],
          [-117.43635800000001, 12.803781],
          [-117.42112, 12.801690999999998],
          [-117.41856000000001, 12.799821000000001],
          [-117.41148799999999, 12.778351],
          [-117.41344000000001, 12.769185],
          [-117.51747800000001, 12.760838999999997],
          [-117.530765, 12.753774999999997],
          [-117.54485500000001, 12.738945000000001],
          [-117.551196, 12.730314],
          [-117.56380200000001, 12.703426],
          [-117.550792, 12.681029000000002],
          [-117.542295, 12.67532],
          [-117.507589, 12.667169000000001],
          [-117.49868900000001, 12.667285],
          [-117.48098999999999, 12.671419],
          [-117.479153, 12.670079999999999],
          [-117.45215999999999, 12.64322],
          [-117.45219599999999, 12.638863],
          [-117.45486, 12.634748000000002],
          [-117.46210500000001, 12.625684999999997],
          [-117.466497, 12.623266000000001],
          [-117.496523, 12.616897000000002],
          [-117.536486, 12.615316999999997],
          [-117.546085, 12.619025],
          [-117.589315, 12.622262],
          [-117.605231, 12.621315000000003],
          [-117.61504099999999, 12.620019999999997],
          [-117.636018, 12.612838000000004],
          [-117.674587, 12.609507],
          [-117.70538199999999, 12.628779999999999],
          [-117.71428, 12.640574999999998],
          [-117.71599800000001, 12.648491999999997],
          [-117.71904799999999, 12.653084],
          [-117.73679899999999, 12.675615999999998],
          [-117.754141, 12.679468],
          [-117.763698, 12.679246999999997],
          [-117.92004499999999, 12.658636000000001],
          [-117.933988, 12.654570999999997],
          [-118.042012, 12.596744000000001],
          [-118.04283899999999, 12.581850000000003],
          [-118.039356, 12.574883999999997],
          [-118.00104099999999, 12.572097],
          [-117.975222, 12.585721999999997],
          [-117.958745, 12.588304],
          [-117.90664699999999, 12.590924999999999],
          [-117.83842000000001, 12.560760000000002],
          [-117.828139, 12.546332],
          [-117.817826, 12.514209999999999],
          [-117.823072, 12.501981999999998],
          [-117.85976600000001, 12.483668000000002],
          [-117.86564200000001, 12.483285000000002],
          [-117.88612599999999, 12.487805000000002],
          [-117.954625, 12.515844999999999],
          [-118.02762100000001, 12.511778999999997],
          [-118.11227600000001, 12.521321999999998],
          [-118.11957000000001, 12.518346000000001],
          [-118.131729, 12.501944000000002],
          [-118.12335200000001, 12.496457],
          [-118.113709, 12.493001],
          [-118.111603, 12.490110000000001],
          [-118.118682, 12.466558],
          [-118.12744, 12.460805],
          [-118.24614399999999, 12.466124],
          [-118.28469899999999, 12.481088999999997],
          [-118.328798, 12.484208000000002],
          [-118.402954, 12.455193000000001],
          [-118.49883700000001, 12.380110000000002],
          [-118.50170499999999, 12.375860000000003],
          [-118.50204, 12.365178],
          [-118.48954599999999, 12.341864999999999],
          [-118.432795, 12.343505],
          [-118.397337, 12.328921000000001],
          [-118.33813699999999, 12.323923],
          [-118.329735, 12.326028],
          [-118.322563, 12.325242000000003],
          [-118.20738700000001, 12.294353999999998],
          [-118.20308299999999, 12.283833000000001],
          [-118.21654000000001, 12.269450999999997],
          [-118.253331, 12.253124999999997],
          [-118.27684199999999, 12.248697999999997],
          [-118.334506, 12.23294],
          [-118.339765, 12.217807],
          [-118.331039, 12.213608999999998],
          [-118.264792, 12.209597000000002],
          [-118.19095999999999, 12.226407000000002],
          [-118.17492999999999, 12.236227],
          [-118.119493, 12.241995000000003],
          [-118.11528200000001, 12.242102000000003],
          [-118.112718, 12.240285999999998],
          [-118.117797, 12.230742],
          [-118.23702499999999, 12.187387000000001],
          [-118.31412800000001, 12.163696999999999],
          [-118.374324, 12.134521999999997],
          [-118.395996, 12.109834],
          [-118.39438799999999, 12.091949],
          [-118.39492200000001, 12.064720999999999],
          [-118.398324, 12.062956999999997],
          [-118.424451, 12.068899000000002],
          [-118.43864400000001, 12.093671999999998],
          [-118.43831499999999, 12.095702000000003],
          [-118.431105, 12.100264000000003],
          [-118.432229, 12.102880999999996],
          [-118.439944, 12.107779999999998],
          [-118.455297, 12.108742],
          [-118.46181000000001, 12.106644000000003],
          [-118.475258, 12.093404999999997],
          [-118.47270599999999, 12.087583000000002],
          [-118.44841299999999, 12.055278000000001],
          [-118.417889, 12.036796000000002],
          [-118.407723, 12.014521000000002],
          [-118.413645, 12.004950999999998],
          [-118.43147099999999, 11.994452000000003],
          [-118.43933000000001, 11.99362],
          [-118.445696, 11.99758],
          [-118.46733499999999, 12.027219000000002],
          [-118.47554299999999, 12.028365999999998],
          [-118.50196700000001, 12.025170000000003],
          [-118.50485, 12.015543999999998],
          [-118.496366, 12.010601000000001],
          [-118.49401499999999, 12.00732],
          [-118.495114, 11.989207],
          [-118.49905000000001, 11.981684999999999],
          [-118.50984, 11.979616999999998],
          [-118.59562, 12.045251999999998],
          [-118.598367, 12.048822000000001],
          [-118.594188, 12.110444999999999],
          [-118.584362, 12.115656999999999],
          [-118.574659, 12.11864],
          [-118.575042, 12.121129000000003],
          [-118.600405, 12.130443999999997],
          [-118.62830299999999, 12.120942999999997],
          [-118.640447, 12.114078999999997],
          [-118.659738, 12.098553000000003],
          [-118.666818, 12.078415],
          [-118.66091399999999, 12.034928],
          [-118.65167400000001, 12.031357999999997],
          [-118.643216, 12.023415],
          [-118.63870399999999, 12.015932],
          [-118.63668899999999, 12.005006999999999],
          [-118.639497, 11.986069999999998],
          [-118.65321399999999, 11.958615000000002],
          [-118.673246, 11.951484999999998],
          [-118.737009, 11.953313000000001],
          [-118.74856, 11.959364999999998],
          [-118.751215, 11.963759000000003],
          [-118.73534799999999, 11.996208000000003],
          [-118.729567, 12.002854],
          [-118.74730500000001, 12.009908000000003],
          [-118.85413199999999, 12.003343000000001],
          [-118.89811599999999, 11.951040999999996],
          [-118.90939599999999, 11.934887000000003],
          [-118.99959799999999, 11.927011],
          [-119.13874799999999, 11.909320000000001],
          [-119.271514, 11.890704],
          [-119.347681, 11.877802000000003],
          [-119.374842, 11.871521999999999],
          [-119.3964, 11.856766999999998],
          [-119.40009599999999, 11.852356999999998],
          [-119.406126, 11.831955999999998],
          [-119.40938, 11.810434],
          [-119.404326, 11.796992000000003],
          [-119.411505, 11.788910999999999],
          [-119.42346800000001, 11.789025000000002],
          [-119.434787, 11.792909000000002],
          [-119.470216, 11.828910999999998],
          [-119.472801, 11.83905],
          [-119.47197299999999, 11.843505999999998],
          [-119.465282, 11.852845000000002],
          [-119.453945, 11.896819999999998],
          [-119.482226, 11.901826],
          [-119.49388300000001, 11.900109],
          [-119.52834899999999, 11.888458],
          [-119.534415, 11.881298999999999],
          [-119.49802199999999, 11.855299000000002],
          [-119.494404, 11.765797999999997],
          [-119.50376800000001, 11.747878],
          [-119.521589, 11.736021000000001],
          [-119.53715199999999, 11.728459],
          [-119.551432, 11.711542999999999],
          [-119.55201600000001, 11.704794],
          [-119.53596099999999, 11.689830999999998],
          [-119.53011699999999, 11.665394],
          [-119.54511500000001, 11.646517000000003],
          [-119.564413, 11.633045000000003],
          [-119.56466900000001, 11.888483999999998],
          [-119.868099, 11.888480999999999],
          [-119.86785800000001, 11.982593999999999],
          [-119.80526699999999, 11.982595000000003],
          [-119.809651, 12.321694],
          [-119.903652, 12.322594000000002],
          [-119.89942400000001, 12.491981000000003],
          [-119.59341, 12.494523999999998],
          [-119.592657, 12.580917],
          [-119.27549099999999, 12.580920999999996],
          [-119.275496, 12.671762000000001],
          [-119.02858900000001, 12.669418999999998],
          [-119.028594, 12.754058],
          [-118.89103399999999, 12.754219999999997],
          [-118.89321100000001, 12.805788],
          [-118.868797, 12.796647999999998],
          [-118.853294, 12.79262],
          [-118.78359, 12.780749999999998],
          [-118.66029800000001, 12.789015],
          [-118.642293, 12.812849999999997],
          [-118.642845, 12.836607999999998],
          [-118.64681200000001, 12.846992],
          [-118.663659, 12.857055000000003],
          [-118.69978800000001, 12.927362000000002],
          [-118.679293, 12.988624999999999],
          [-118.659945, 13.034585],
          [-118.63736399999999, 13.061363999999998],
          [-118.518429, 13.16055],
          [-118.453711, 13.21179],
          [-118.376249, 13.265542000000003],
          [-118.355066, 13.27485],
          [-118.229883, 13.321534],
          [-118.14971, 13.344915999999998],
          [-118.06702999999999, 13.382914999999997],
          [-118.06004100000001, 13.387456],
          [-118.04993200000001, 13.390141],
          [-118.034246, 13.390230000000003],
          [-118.010538, 13.401456000000003],
          [-117.99467000000001, 13.414234],
          [-117.95623900000001, 13.449382999999997],
          [-117.937241, 13.472048000000001],
          [-117.931624, 13.476208],
          [-117.786046, 13.542189],
          [-117.77249599999999, 13.547055],
          [-117.70385200000001, 13.563454999999998],
          [-117.678891, 13.563887999999999],
          [-117.684833, 13.557746000000002],
          [-117.68041600000001, 13.537726999999997],
          [-117.64938900000001, 13.500331000000003],
          [-117.615137, 13.488691000000003],
          [-117.58690999999999, 13.487155999999999],
          [-117.573129, 13.514524999999999],
          [-117.57347200000001, 13.522731999999998],
          [-117.58833899999999, 13.582152],
          [-117.59964400000001, 13.607950000000002],
          [-117.60738699999999, 13.612537000000003],
          [-117.65220199999999, 13.614794000000003],
          [-117.684282, 13.609974000000001],
          [-117.691291, 13.611131],
          [-117.710645, 13.639946000000002],
          [-117.70378199999999, 13.721767999999997],
          [-117.67106100000001, 13.772866],
          [-117.642226, 13.868777000000001],
          [-117.623886, 13.960501999999998],
          [-117.61180200000001, 14.034263000000003],
          [-117.59660099999999, 14.08867],
          [-117.58363600000001, 14.124307000000002],
          [-117.58092400000001, 14.128096],
          [-117.556556, 14.148445000000002],
          [-117.53332900000001, 14.160335000000003],
          [-117.514474, 14.162978000000003],
          [-117.493784, 14.162148000000002],
          [-117.39734999999999, 14.173383000000001],
          [-117.38309899999999, 14.184607],
          [-117.352316, 14.219096999999998],
          [-117.366928, 14.232669000000001],
          [-117.37451100000001, 14.232117000000002],
          [-117.38923700000001, 14.228091],
          [-117.407918, 14.211871000000002],
          [-117.423325, 14.211359999999999],
          [-117.442712, 14.218874999999997],
          [-117.51547500000001, 14.255637999999998],
          [-117.541564, 14.271883000000003],
          [-117.54720900000001, 14.277535],
          [-117.556343, 14.303749000000003],
          [-117.55686499999999, 14.330714999999998],
          [-117.53617600000001, 14.391596999999997],
          [-117.52447699999999, 14.414506000000003],
          [-117.48810800000001, 14.471705],
          [-117.48148699999999, 14.480770999999997],
          [-117.46088, 14.499693],
          [-117.451918, 14.505617999999998],
          [-117.397197, 14.527332999999999],
          [-117.380259, 14.524397999999998],
          [-117.358487, 14.533876],
          [-117.330683, 14.551516],
          [-117.313572, 14.565043000000003],
          [-117.281327, 14.600236000000002],
          [-117.267437, 14.609794],
          [-116.31767099999999, 14.609610000000004],
          [-116.31813500000001, 14.894948],
          [-116.98583300000001, 14.888654000000002],
          [-116.980888, 14.891030999999998],
          [-116.96664899999999, 14.904074000000001],
          [-116.975946, 14.940896000000002],
          [-117.029517, 14.956203000000002],
          [-117.03920600000001, 14.945920999999998],
          [-117.040625, 14.913390999999997],
          [-117.070584, 14.887816],
          [-117.07060100000001, 14.887808],
          [-117.11686599999999, 14.867533000000002],
          [-117.158008, 14.860227000000002],
          [-117.24415099999999, 15.246208000000003],
          [-117.074194, 15.244404000000003],
          [-117.07325599999999, 15.331538000000002],
          [-116.72937300000001, 15.328803],
          [-116.73118400000001, 15.416170000000001],
          [-116.635005, 15.415107999999996],
          [-116.634489, 15.503988],
          [-116.46489600000001, 15.502988000000002],
          [-116.464798, 15.589388],
          [-116.12330700000001, 15.588205000000002],
          [-116.12205, 15.675184999999999],
          [-115.95585, 15.677304999999997],
          [-115.95362399999999, 16.106237999999998],
          [-115.96109999999999, 16.907601999999997],
          [-114.422064, 16.906968999999997],
          [-113.436195, 16.908538],
          [-113.395016, 16.823290999999998],
          [-113.394293, 16.472296],
          [-113.52848399999999, 16.470610999999998],
          [-113.531136, 16.122545000000002],
          [-113.656579, 16.121433000000003],
          [-113.65489299999999, 15.784914],
          [-113.78294299999999, 15.785049],
          [-113.783649, 15.696598000000002],
          [-113.95588900000001, 15.696098999999997],
          [-113.954485, 15.520072999999996],
          [-114.12437399999999, 15.519551],
          [-114.12126900000001, 15.431294999999999],
          [-114.414557, 15.428680999999997],
          [-114.40931599999999, 15.254640000000002],
          [-114.747693, 15.253095000000002],
          [-114.745361, 15.076991999999997],
          [-114.692093, 15.075955999999998],
          [-114.692714, 14.734597999999998],
          [-114.63942, 14.734693],
          [-114.63727, 14.646810000000002],
          [-114.305926, 14.646822]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02180",
      state: "02",
      county: "180",
      name: "Nome County",
      lsad: "CA",
      censusArea: 22961.761,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-129.267598, 19.343995],
          [-129.101961, 19.338022000000002],
          [-129.087914, 19.340936999999997],
          [-129.05195, 19.343127000000003],
          [-128.999241, 19.341248999999998],
          [-128.937385, 19.333789000000003],
          [-128.936333, 19.330621999999998],
          [-128.932623, 19.329140000000002],
          [-128.796086, 19.308781000000003],
          [-128.692939, 19.302281999999998],
          [-128.685145, 19.296427],
          [-128.686675, 19.293022],
          [-128.716872, 19.256315999999998],
          [-128.751537, 19.217962],
          [-128.783239, 19.184131],
          [-128.789266, 19.179645999999998],
          [-128.818344, 19.163224],
          [-128.85875, 19.146957999999998],
          [-128.871465, 19.146009],
          [-128.889683, 19.147708],
          [-128.950091, 19.160894999999996],
          [-128.963577, 19.167104000000002],
          [-128.983024, 19.169671],
          [-129.042674, 19.176510999999998],
          [-129.105808, 19.178803000000002],
          [-129.198398, 19.176011000000003],
          [-129.262039, 19.169936],
          [-129.303477, 19.164439],
          [-129.375667, 19.151269],
          [-129.396308, 19.136617],
          [-129.436748, 19.113579],
          [-129.471949, 19.098565],
          [-129.51365, 19.084716999999998],
          [-129.534984, 19.074354999999997],
          [-129.561131, 19.055177999999998],
          [-129.575873, 19.036450000000002],
          [-129.576965, 19.027025000000002],
          [-129.572777, 19.022118],
          [-129.568016, 18.976878999999997],
          [-129.638309, 18.937527000000003],
          [-129.746736, 18.955990999999997],
          [-129.757249, 18.960087],
          [-129.757514, 18.963721999999997],
          [-129.734938, 18.974468],
          [-129.734938, 18.976616999999997],
          [-129.788466, 19.043014999999997],
          [-129.829912, 19.07855],
          [-129.838511, 19.084339],
          [-129.88123, 19.105848],
          [-129.944056, 19.13236],
          [-129.987936, 19.142975],
          [-130.006196, 19.14454],
          [-130.021208, 19.149500000000003],
          [-130.049622, 19.163376999999997],
          [-130.051062, 19.167489000000003],
          [-130.053402, 19.168858],
          [-130.101301, 19.179299999999998],
          [-130.124354, 19.183664999999998],
          [-130.154072, 19.186402],
          [-130.174421, 19.185464000000003],
          [-130.186485, 19.181618],
          [-130.181985, 19.178804],
          [-130.193695, 19.177433999999998],
          [-130.263032, 19.179147],
          [-130.281388, 19.186821000000002],
          [-130.285648, 19.19457],
          [-130.279881, 19.197108],
          [-130.277915, 19.200239000000003],
          [-130.277721, 19.208819],
          [-130.30363, 19.238692],
          [-130.337275, 19.266308000000002],
          [-130.364806, 19.285595999999998],
          [-130.430656, 19.314284],
          [-130.512102, 19.341881],
          [-130.55895, 19.354989000000003],
          [-130.712572, 19.385975000000002],
          [-130.865412, 19.414229],
          [-130.92345, 19.420859],
          [-130.967475, 19.42373],
          [-131.067663, 19.424579],
          [-131.100855, 19.42342],
          [-131.269249, 19.385385999999997],
          [-131.280185, 19.381543],
          [-131.287157, 19.376641999999997],
          [-131.288265, 19.374833000000002],
          [-131.285411, 19.366464],
          [-131.290324, 19.355383000000003],
          [-131.333089, 19.335393000000003],
          [-131.433319, 19.307578],
          [-131.464455, 19.306914999999996],
          [-131.562263, 19.334591000000003],
          [-131.667115, 19.356166],
          [-131.739321, 19.366114000000003],
          [-131.795297, 19.407853000000003],
          [-131.818259, 19.429451999999998],
          [-131.824872, 19.437140999999997],
          [-131.849984, 19.485039],
          [-131.840382, 19.547724000000002],
          [-131.833681, 19.580074000000003],
          [-131.791881, 19.620624999999997],
          [-131.757081, 19.640251999999997],
          [-131.743979, 19.654905],
          [-131.742338, 19.665494000000002],
          [-131.755552, 19.701172999999997],
          [-131.754336, 19.718960000000003],
          [-131.739918, 19.717095999999998],
          [-131.733206, 19.720326999999997],
          [-131.727986, 19.726791],
          [-131.725748, 19.734744999999997],
          [-131.727986, 19.744937999999998],
          [-131.737432, 19.760350000000003],
          [-131.743398, 19.782971000000003],
          [-131.738178, 19.784711],
          [-131.699647, 19.781728],
          [-131.692686, 19.782598],
          [-131.682494, 19.787570000000002],
          [-131.673296, 19.788066999999998],
          [-131.66733, 19.785581],
          [-131.659873, 19.775762],
          [-131.643963, 19.770791000000003],
          [-131.638991, 19.759231],
          [-131.638246, 19.749536],
          [-131.641477, 19.745559],
          [-131.652912, 19.739220000000003],
          [-131.652664, 19.73661],
          [-131.648935, 19.73487],
          [-131.646692, 19.729425],
          [-131.65263, 19.708523],
          [-131.649923, 19.70254],
          [-131.640027, 19.69343],
          [-131.632194, 19.688601],
          [-131.609439, 19.679831999999998],
          [-131.521859, 19.658797],
          [-131.381677, 19.630646],
          [-131.202557, 19.606896999999996],
          [-131.103558, 19.589267999999997],
          [-131.044486, 19.580430999999997],
          [-130.950817, 19.570127],
          [-130.907197, 19.572107000000003],
          [-130.897581, 19.574675999999997],
          [-130.859032, 19.587502999999998],
          [-130.816581, 19.606329000000002],
          [-130.698156, 19.646777999999998],
          [-130.606282, 19.672732000000003],
          [-130.488192, 19.696723],
          [-130.472181, 19.698677000000004],
          [-130.462947, 19.698022],
          [-130.441066, 19.691981],
          [-130.373871, 19.687322],
          [-130.359363, 19.687320999999997],
          [-130.354527, 19.691924],
          [-130.344855, 19.694225000000003],
          [-130.315839, 19.691923000000003],
          [-130.281988, 19.68502],
          [-130.26748, 19.675815999999998],
          [-130.257808, 19.666611000000003],
          [-130.176413, 19.625489],
          [-130.154754, 19.619072000000003],
          [-130.14004, 19.616695999999997],
          [-130.113066, 19.616245],
          [-130.095833, 19.612701],
          [-130.076689, 19.587988000000003],
          [-130.040919, 19.523411000000003],
          [-130.047114, 19.490135000000002],
          [-130.026953, 19.480702],
          [-130.007943, 19.475428],
          [-129.974858, 19.470618],
          [-129.906304, 19.457518999999998],
          [-129.857078, 19.441975],
          [-129.747634, 19.432755999999998],
          [-129.656474, 19.429929],
          [-129.643167, 19.427802],
          [-129.579892, 19.40287],
          [-129.566562, 19.388725],
          [-129.565439, 19.385562999999998],
          [-129.554375, 19.377158],
          [-129.546934, 19.372791999999997],
          [-129.520524, 19.365941],
          [-129.415329, 19.355943000000003],
          [-129.38408, 19.356733],
          [-129.31297, 19.353335],
          [-129.281422, 19.348381000000003],
          [-129.267598, 19.343995]
        ],
        [
          [-122.614621, 19.621831999999998],
          [-122.587527, 19.625115],
          [-122.558234, 19.634307999999997],
          [-122.54138900000001, 19.635727000000003],
          [-122.51229799999999, 19.629784],
          [-122.498175, 19.622069000000003],
          [-122.451929, 19.621270000000003],
          [-122.44022899999999, 19.622490999999997],
          [-122.430304, 19.625745000000002],
          [-122.425419, 19.62995],
          [-122.425265, 19.631653999999997],
          [-122.427696, 19.633134],
          [-122.424205, 19.636215],
          [-122.40120300000001, 19.634366999999997],
          [-122.374243, 19.626424999999998],
          [-122.341892, 19.594062],
          [-122.345179, 19.551785000000002],
          [-122.37798799999999, 19.543813],
          [-122.41680199999999, 19.547389000000003],
          [-122.47002900000001, 19.5475],
          [-122.55270100000001, 19.540951],
          [-122.562007, 19.537104999999997],
          [-122.614949, 19.540601000000002],
          [-122.676581, 19.555647999999998],
          [-122.680973, 19.556859000000003],
          [-122.707559, 19.577607],
          [-122.68262899999999, 19.584066],
          [-122.64451299999999, 19.602598999999998],
          [-122.614621, 19.621831999999998]
        ],
        [
          [-128.21012, 20.998959999999997],
          [-128.207906, 21.003291000000004],
          [-128.16909, 21.024067000000002],
          [-128.156162, 21.029173999999998],
          [-128.129109, 21.034565],
          [-128.076108, 21.043289],
          [-128.004872, 21.038897000000006],
          [-127.972262, 21.030534000000003],
          [-127.95307600000001, 21.023478999999995],
          [-127.924296, 21.002728000000005],
          [-127.916447, 20.988618000000002],
          [-127.91382899999999, 20.969943],
          [-127.91906, 20.960813],
          [-127.953094, 20.932993999999994],
          [-127.97614899999999, 20.922871999999998],
          [-128.037032, 20.909284],
          [-128.090879, 20.911113],
          [-128.137033, 20.918431999999996],
          [-128.187035, 20.934901999999994],
          [-128.210114, 20.951372000000006],
          [-128.213962, 20.967844999999997],
          [-128.213963, 20.984318000000002],
          [-128.21012, 20.998959999999997]
        ],
        [
          [-128.952766, 21.758910999999998],
          [-128.947278, 21.763817000000003],
          [-128.93724, 21.767116],
          [-128.915518, 21.770483999999996],
          [-128.902235, 21.769665000000003],
          [-128.893219, 21.744704999999996],
          [-128.898754, 21.739709000000005],
          [-128.903439, 21.738454000000004],
          [-128.93122, 21.73894],
          [-128.94076, 21.742714000000007],
          [-128.951388, 21.749319],
          [-128.954515, 21.757143999999997],
          [-128.952766, 21.758910999999998]
        ],
        [
          [-126.03187, 22.264639000000003],
          [-126.031342, 22.264630999999994],
          [-126.01992100000001, 22.252753999999996],
          [-126.066397, 22.246531000000004],
          [-126.096969, 22.237832999999995],
          [-126.121659, 22.214772999999994],
          [-126.13838999999999, 22.210088999999996],
          [-126.15301199999999, 22.212289],
          [-126.170909, 22.218436999999994],
          [-126.17207099999999, 22.219909],
          [-126.178474, 22.228015999999997],
          [-126.177606, 22.234731999999994],
          [-126.165412, 22.239084000000005],
          [-126.15237400000001, 22.246373000000006],
          [-126.129652, 22.252813000000003],
          [-126.10178099999999, 22.258742999999996],
          [-126.06609700000001, 22.263721000000004],
          [-126.05315300000001, 22.264944],
          [-126.03187, 22.264639000000003]
        ],
        [
          [-120.851144, 19.012689],
          [-121.990906, 19.012675],
          [-121.99003200000001, 19.096024],
          [-122.177514, 19.096466999999997],
          [-122.184665, 19.186448],
          [-122.374087, 19.186422999999998],
          [-122.37771000000001, 19.272891],
          [-122.58775399999999, 19.275727000000003],
          [-122.571695, 19.285556],
          [-122.437059, 19.377836000000002],
          [-122.43216899999999, 19.382606000000003],
          [-122.426095, 19.393651],
          [-122.428744, 19.401055],
          [-122.42152999999999, 19.409014],
          [-122.384625, 19.435797],
          [-122.352274, 19.454068999999997],
          [-122.30186900000001, 19.473422],
          [-122.27108899999999, 19.487710999999997],
          [-122.26824199999999, 19.490799000000003],
          [-122.267833, 19.495084],
          [-122.288532, 19.526412],
          [-122.30147099999999, 19.537350000000004],
          [-122.296731, 19.540107999999996],
          [-122.252411, 19.541753],
          [-122.190145, 19.529885999999998],
          [-122.15157400000001, 19.517952],
          [-122.12324899999999, 19.512807000000002],
          [-122.108597, 19.511927],
          [-122.07315600000001, 19.513768],
          [-122.041687, 19.489649999999997],
          [-122.04570899999999, 19.475434],
          [-122.05013199999999, 19.47285],
          [-122.050543, 19.470588999999997],
          [-122.039444, 19.458930000000002],
          [-122.025552, 19.447539],
          [-121.930714, 19.444843],
          [-121.83989700000001, 19.447313],
          [-121.76583199999999, 19.453803],
          [-121.70563000000001, 19.464061],
          [-121.676526, 19.465003000000003],
          [-121.591632, 19.454244000000003],
          [-121.58377200000001, 19.447857],
          [-121.553077, 19.449216999999997],
          [-121.45046300000001, 19.457178],
          [-121.310181, 19.471311999999998],
          [-121.19116299999999, 19.490071999999998],
          [-121.13675799999999, 19.504525],
          [-121.13423, 19.506735],
          [-121.11996400000001, 19.532544],
          [-121.102721, 19.547800000000002],
          [-121.03604899999999, 19.579566],
          [-120.92487700000001, 19.644813999999997],
          [-120.90435299999999, 19.658023999999997],
          [-120.809089, 19.731332000000002],
          [-120.78330399999999, 19.752893],
          [-120.76556, 19.773552000000002],
          [-120.76197400000001, 19.793453],
          [-120.766291, 19.835189],
          [-120.787624, 19.869196000000002],
          [-120.810798, 19.904646],
          [-120.851979, 19.954409],
          [-120.87768600000001, 19.977265000000003],
          [-120.89245500000001, 19.985943],
          [-120.93374, 20.049729],
          [-120.951641, 20.090067000000005],
          [-120.95513199999999, 20.13803],
          [-120.956425, 20.191732000000002],
          [-120.953596, 20.197774999999993],
          [-120.946857, 20.204158000000007],
          [-120.97603799999999, 20.235760999999997],
          [-121.22894099999999, 20.370746999999994],
          [-121.263519, 20.398166000000003],
          [-121.313668, 20.400874],
          [-121.410382, 20.422106999999997],
          [-121.46302600000001, 20.420074],
          [-121.470182, 20.418813999999998],
          [-121.49292600000001, 20.407850999999994],
          [-121.50490300000001, 20.423074],
          [-121.479093, 20.486401],
          [-121.46904599999999, 20.506574999999998],
          [-121.388621, 20.532782999999995],
          [-121.373572, 20.535027999999997],
          [-121.362901, 20.526912999999993],
          [-121.351145, 20.521382000000003],
          [-121.32134300000001, 20.513864999999996],
          [-121.234092, 20.500365000000002],
          [-121.19802899999999, 20.496626000000006],
          [-121.155518, 20.494687],
          [-121.07803100000001, 20.494094000000004],
          [-121.02418499999999, 20.499719],
          [-121.015095, 20.502123999999995],
          [-121.013228, 20.507520999999997],
          [-121.01714000000001, 20.517123999999995],
          [-121.037534, 20.522245999999996],
          [-121.04594700000001, 20.524947999999995],
          [-121.05305999999999, 20.528503999999998],
          [-121.05554900000001, 20.532415999999998],
          [-121.052348, 20.537395000000004],
          [-121.049148, 20.540952000000004],
          [-120.992894, 20.541295000000005],
          [-120.97055499999999, 20.543884000000006],
          [-120.940493, 20.549999999999997],
          [-120.80204800000001, 20.610352000000006],
          [-120.79335599999999, 20.619316999999995],
          [-120.79161400000001, 20.623054999999994],
          [-120.78357, 20.680581000000004],
          [-120.783398, 20.717160000000007],
          [-120.86957100000001, 20.783797000000007],
          [-120.93597399999999, 20.822370000000006],
          [-120.98641699999999, 20.833984],
          [-121.07971800000001, 20.869549000000006],
          [-121.102755, 20.880661000000003],
          [-121.133062, 20.898218999999997],
          [-121.149655, 20.911985],
          [-121.14936599999999, 20.916557999999995],
          [-121.145725, 20.920534000000004],
          [-121.176009, 20.927160999999998],
          [-121.19211999999999, 20.921366000000006],
          [-121.195202, 20.918177999999997],
          [-121.20089300000001, 20.905795999999995],
          [-121.200964, 20.898658999999995],
          [-121.198586, 20.894402999999997],
          [-121.21375599999999, 20.883324],
          [-121.264283, 20.86197],
          [-121.293049, 20.853243000000006],
          [-121.32784799999999, 20.829836],
          [-121.357867, 20.805921999999995],
          [-121.36680799999999, 20.793777000000006],
          [-121.364438, 20.782099000000002],
          [-121.367483, 20.778907000000004],
          [-121.37698499999999, 20.773036000000005],
          [-121.41349299999999, 20.762722999999994],
          [-121.42985999999999, 20.759027000000003],
          [-121.51821100000001, 20.753249999999994],
          [-121.595506, 20.764477999999997],
          [-121.63028700000001, 20.771289999999993],
          [-121.64552, 20.776452000000006],
          [-121.667261, 20.788981000000007],
          [-121.87836300000001, 20.709475999999995],
          [-121.90242900000001, 20.703851],
          [-121.939279, 20.699118999999996],
          [-122.096528, 20.690983000000003],
          [-122.13883200000001, 20.685934000000003],
          [-122.16851600000001, 20.68029],
          [-122.18814599999999, 20.672394999999995],
          [-122.21662, 20.656212999999994],
          [-122.219718, 20.644176],
          [-122.234477, 20.619336000000004],
          [-122.270025, 20.608710000000002],
          [-122.290571, 20.605496000000002],
          [-122.342308, 20.592240000000004],
          [-122.539996, 20.530930999999995],
          [-122.55487500000001, 20.520341000000002],
          [-122.61421999999999, 20.470702000000003],
          [-122.615452, 20.467077000000003],
          [-122.60217800000001, 20.456868999999998],
          [-122.60301999999999, 20.448666000000003],
          [-122.63224199999999, 20.385734],
          [-122.64515599999999, 20.379783000000003],
          [-122.66767999999999, 20.375355999999996],
          [-122.71921800000001, 20.359971],
          [-122.76842400000001, 20.333516000000003],
          [-122.790167, 20.325181999999998],
          [-122.795636, 20.327715999999995],
          [-122.805385, 20.336022999999997],
          [-122.81000399999999, 20.352647000000005],
          [-122.80720500000001, 20.364643],
          [-122.80035000000001, 20.374695000000003],
          [-122.802266, 20.395326999999995],
          [-122.806612, 20.405608],
          [-122.83654000000001, 20.436701999999997],
          [-122.858556, 20.474863999999997],
          [-122.857562, 20.49978],
          [-122.940776, 20.542417],
          [-122.96924999999999, 20.54687],
          [-123.03065699999999, 20.542353000000006],
          [-123.042618, 20.540046000000004],
          [-123.03205700000001, 20.525608000000005],
          [-123.028887, 20.511908000000005],
          [-123.02715799999999, 20.477945000000005],
          [-123.091486, 20.437736],
          [-123.107459, 20.409192000000004],
          [-123.11945, 20.403807999999998],
          [-123.150789, 20.397249000000002],
          [-123.17533599999999, 20.399333999999996],
          [-123.229206, 20.430019],
          [-123.24909199999999, 20.456222999999994],
          [-123.253027, 20.469500999999994],
          [-123.35092599999999, 20.505859],
          [-123.41290000000001, 20.524986],
          [-123.451482, 20.534819999999996],
          [-123.597834, 20.563356],
          [-123.65194299999999, 20.567299000000006],
          [-123.82973899999999, 20.574965000000006],
          [-123.875774, 20.572935],
          [-123.89617999999999, 20.564482999999996],
          [-123.97435200000001, 20.551370000000006],
          [-123.99453199999999, 20.551742000000004],
          [-124.044839, 20.559167000000002],
          [-124.07199700000001, 20.561279999999996],
          [-124.14705900000001, 20.564552000000006],
          [-124.260064, 20.564220000000006],
          [-124.30727300000001, 20.561487999999997],
          [-124.42187100000001, 20.545255999999995],
          [-124.49132700000001, 20.529824000000005],
          [-124.54829799999999, 20.516738000000004],
          [-124.807747, 20.449432],
          [-124.835679, 20.444917000000004],
          [-124.87442100000001, 20.441194999999993],
          [-125.001961, 20.433916999999994],
          [-125.01651899999999, 20.434392000000003],
          [-125.214182, 20.469725999999994],
          [-125.413443, 20.497939000000002],
          [-125.55057300000001, 20.512235000000004],
          [-125.751093, 20.536437000000006],
          [-125.81959499999999, 20.540171],
          [-125.919704, 20.548659999999998],
          [-126.189546, 20.575798000000006],
          [-126.236939, 20.583557999999996],
          [-126.392403, 20.638160999999997],
          [-126.413926, 20.651229],
          [-126.451788, 20.691761],
          [-126.474714, 20.719267000000002],
          [-126.483801, 20.733418999999998],
          [-126.481076, 20.786156000000005],
          [-126.47897800000001, 20.797036000000006],
          [-126.43051600000001, 20.807715],
          [-126.41702799999999, 20.818740000000005],
          [-126.41019800000001, 20.827968],
          [-126.40730300000001, 20.834277999999998],
          [-126.40731500000001, 20.852281000000005],
          [-126.40933100000001, 20.859755000000007],
          [-126.41562400000001, 20.871927999999997],
          [-126.43224599999999, 20.883160000000004],
          [-126.530518, 20.937113999999994],
          [-126.58606599999999, 20.955712000000005],
          [-126.61510999999999, 20.964330000000004],
          [-126.636843, 20.968113000000002],
          [-126.69081399999999, 20.985371999999998],
          [-126.70482999999999, 20.997051],
          [-126.70528300000001, 20.999846000000005],
          [-126.69520600000001, 21.005184],
          [-126.688762, 21.018029],
          [-126.69242600000001, 21.029629],
          [-126.69645299999999, 21.035856999999993],
          [-126.73279400000001, 21.053498000000005],
          [-126.82091, 21.077053000000006],
          [-126.860402, 21.090866000000005],
          [-126.88545099999999, 21.105856000000003],
          [-126.911922, 21.125964999999994],
          [-126.910131, 21.134023999999997],
          [-126.906687, 21.136319999999998],
          [-126.89771999999999, 21.139027999999996],
          [-126.88667699999999, 21.138762999999997],
          [-126.87266600000001, 21.136927999999997],
          [-126.83749599999999, 21.128146],
          [-126.826753, 21.119777999999997],
          [-126.81679, 21.117089000000007],
          [-126.75555399999999, 21.110585],
          [-126.67032, 21.109719999999996],
          [-126.63841099999999, 21.113585999999998],
          [-126.63444899999999, 21.125873],
          [-126.60606999999999, 21.135992],
          [-126.52150599999999, 21.149242],
          [-126.509566, 21.152719000000005],
          [-126.47991300000001, 21.167248999999998],
          [-126.464192, 21.177086000000003],
          [-126.45998399999999, 21.183284],
          [-126.46005, 21.189897000000002],
          [-126.46534199999999, 21.194817999999998],
          [-126.474839, 21.217663],
          [-126.47529700000001, 21.224334999999996],
          [-126.45171099999999, 21.236177999999995],
          [-126.386271, 21.254143],
          [-126.34718899999999, 21.276341000000002],
          [-126.36061799999999, 21.288630999999995],
          [-126.37772100000001, 21.297983000000002],
          [-126.439404, 21.319058],
          [-126.48596800000001, 21.3309],
          [-126.51864, 21.335824000000002],
          [-126.551097, 21.338406000000006],
          [-126.572735, 21.338155],
          [-126.59696400000001, 21.336246000000003],
          [-126.62598700000001, 21.325120999999996],
          [-126.655179, 21.324938000000003],
          [-126.67971700000001, 21.326856000000006],
          [-126.79600099999999, 21.337183999999993],
          [-126.85164599999999, 21.348484999999997],
          [-126.89968099999999, 21.360642],
          [-127.026782, 21.381967000000003],
          [-127.10185999999999, 21.387737],
          [-127.17046500000001, 21.389269],
          [-127.398458, 21.400259000000005],
          [-127.47402399999999, 21.412744000000004],
          [-127.57463899999999, 21.444979000000004],
          [-127.62038799999999, 21.463463000000004],
          [-127.62137100000001, 21.466589],
          [-127.71088800000001, 21.498524000000003],
          [-127.841836, 21.530248999999998],
          [-127.851234, 21.538180999999994],
          [-127.90959899999999, 21.550876000000002],
          [-127.96706499999999, 21.558599],
          [-127.99717799999999, 21.559346000000005],
          [-128.04762, 21.569148999999996],
          [-128.0752, 21.576355000000007],
          [-128.099046, 21.592239000000006],
          [-128.099356, 21.599045000000004],
          [-128.09614, 21.600882],
          [-128.100003, 21.610972000000004],
          [-128.127044, 21.626583999999994],
          [-128.134663, 21.640839999999997],
          [-128.12893, 21.655744],
          [-128.103708, 21.685552],
          [-127.97988899999999, 21.727971999999994],
          [-127.857216, 21.754340999999997],
          [-127.73569, 21.776123999999996],
          [-127.53964300000001, 21.820836],
          [-127.31493499999999, 21.885039000000006],
          [-127.24614600000001, 21.911407999999994],
          [-127.139524, 21.948094999999995],
          [-126.97787199999999, 21.996246999999997],
          [-126.95608899999999, 22.007711],
          [-126.827684, 22.051277],
          [-126.59724299999999, 22.118919000000005],
          [-126.526162, 22.141848999999993],
          [-126.330971, 22.189514000000003],
          [-126.308287, 22.180287000000007],
          [-126.28212300000001, 22.177088999999995],
          [-126.24569299999999, 22.169920000000005],
          [-126.223186, 22.171032999999994],
          [-126.19069200000001, 22.167410000000004],
          [-126.181579, 22.164626],
          [-126.143876, 22.159265000000005],
          [-126.127228, 22.155203999999998],
          [-126.12224800000001, 22.152621999999994],
          [-126.12248600000001, 22.149417],
          [-126.13902300000001, 22.146991],
          [-126.14986999999999, 22.141302999999994],
          [-126.148866, 22.137747000000005],
          [-126.138305, 22.131966000000006],
          [-126.101618, 22.125197999999997],
          [-126.087392, 22.119609999999994],
          [-126.08517900000001, 22.115369],
          [-126.080015, 22.112601999999995],
          [-126.046471, 22.107833],
          [-126.00989799999999, 22.110749999999996],
          [-125.95974100000001, 22.109409999999997],
          [-125.92510100000001, 22.111065999999994],
          [-125.892999, 22.115767000000005],
          [-125.87674000000001, 22.114801999999997],
          [-125.84508199999999, 22.107971000000006],
          [-125.79909599999999, 22.100823000000005],
          [-125.76351, 22.098884999999996],
          [-125.75878399999999, 22.092915000000005],
          [-125.73361600000001, 22.096231000000003],
          [-125.697845, 22.104040999999995],
          [-125.693048, 22.103302999999997],
          [-125.661901, 22.117943999999994],
          [-125.64934400000001, 22.119562000000002],
          [-125.64674099999999, 22.118151999999995],
          [-125.65021999999999, 22.114745999999997],
          [-125.647717, 22.113487000000006],
          [-125.63079300000001, 22.117643],
          [-125.607237, 22.119397000000006],
          [-125.59980300000001, 22.117686000000006],
          [-125.58851200000001, 22.119694999999993],
          [-125.584292, 22.129489000000007],
          [-125.54488599999999, 22.142900999999995],
          [-125.54466, 22.151933],
          [-125.551682, 22.161584000000005],
          [-125.57890599999999, 22.163269999999997],
          [-125.59438599999999, 22.168082999999996],
          [-125.620879, 22.180571999999998],
          [-125.65381400000001, 22.192453],
          [-125.69050899999999, 22.202028999999996],
          [-125.76051799999999, 22.209560999999994],
          [-125.81347299999999, 22.207173999999995],
          [-125.84831399999999, 22.211366999999996],
          [-125.85329100000001, 22.212683999999996],
          [-125.86714799999999, 22.220871000000002],
          [-125.874462, 22.227998999999997],
          [-125.87751399999999, 22.240308999999996],
          [-125.866468, 22.247073],
          [-125.853577, 22.263184999999993],
          [-125.85993300000001, 22.27422],
          [-125.87249700000001, 22.284271000000004],
          [-125.88045399999999, 22.295159999999996],
          [-125.88212899999999, 22.299766000000005],
          [-125.882496, 22.311603000000005],
          [-125.86923300000001, 22.316112000000004],
          [-125.80502999999999, 22.333309999999997],
          [-125.6686, 22.361970999999997],
          [-125.40720400000001, 22.420440999999997],
          [-125.187082, 22.465154],
          [-125.12402599999999, 22.473179000000002],
          [-124.71100899999999, 22.542541],
          [-124.40072699999999, 22.581109999999995],
          [-124.39664400000001, 22.476291000000003],
          [-124.319703, 22.475975000000005],
          [-124.31779, 22.130578999999997],
          [-124.244449, 22.129158000000004],
          [-124.24670599999999, 21.782439999999994],
          [-123.97093100000001, 21.782922999999997],
          [-123.971115, 21.608348000000007],
          [-123.758871, 21.608054999999993],
          [-123.75800100000001, 21.436206],
          [-122.032746, 21.43839],
          [-119.804395, 21.435857],
          [-119.80101199999999, 21.522685999999993],
          [-119.59357600000001, 21.522689],
          [-119.38671199999999, 21.523139999999998],
          [-119.38476499999999, 21.261674],
          [-119.578562, 21.262692],
          [-119.576911, 20.923743],
          [-119.777177, 20.923088000000007],
          [-119.77380299999999, 20.750296000000006],
          [-119.97809799999999, 20.748491],
          [-119.956447, 20.051022000000003],
          [-119.731224, 20.052366000000006],
          [-119.69929099999999, 19.792346000000002],
          [-119.93077299999999, 19.793808],
          [-119.92924400000001, 19.703494],
          [-120.103703, 19.702263000000002],
          [-120.09787399999999, 19.614393],
          [-120.49346499999999, 19.613655],
          [-120.49497099999999, 19.537148000000002],
          [-120.69205099999999, 19.536617999999997],
          [-120.67130499999999, 19.270829999999997],
          [-120.847328, 19.269229000000003],
          [-120.851144, 19.012689]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02188",
      state: "02",
      county: "188",
      name: "Northwest Arctic County",
      lsad: "Borough",
      censusArea: 35572.584,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.59357600000001, 21.522689],
          [-119.80101199999999, 21.522685999999993],
          [-119.804395, 21.435857],
          [-122.032746, 21.43839],
          [-123.75800100000001, 21.436206],
          [-123.758871, 21.608054999999993],
          [-123.971115, 21.608348000000007],
          [-123.97093100000001, 21.782922999999997],
          [-124.24670599999999, 21.782439999999994],
          [-124.244449, 22.129158000000004],
          [-124.31779, 22.130578999999997],
          [-124.319703, 22.475975000000005],
          [-124.39664400000001, 22.476291000000003],
          [-124.40072699999999, 22.581109999999995],
          [-124.400724, 22.581109999999995],
          [-124.34501499999999, 22.580833999999996],
          [-124.09455399999999, 22.592281],
          [-123.979581, 22.593953],
          [-123.978095, 22.592616000000007],
          [-123.97849199999999, 22.591012000000006],
          [-123.985713, 22.576085000000006],
          [-123.90834100000001, 22.555970000000002],
          [-123.875235, 22.558248000000006],
          [-123.85047599999999, 22.563102],
          [-123.75417100000001, 22.551283999999995],
          [-123.727179, 22.516388000000006],
          [-123.728308, 22.498552000000004],
          [-123.73024699999999, 22.491372],
          [-123.761967, 22.454874000000004],
          [-123.798687, 22.436875],
          [-123.844221, 22.418977999999996],
          [-123.856359, 22.409295999999998],
          [-123.873106, 22.389015],
          [-123.873096, 22.328550000000007],
          [-123.849163, 22.307638999999995],
          [-123.83982499999999, 22.304079],
          [-123.82997700000001, 22.280398000000005],
          [-123.83007699999999, 22.272000000000006],
          [-123.843108, 22.259868999999995],
          [-123.90481299999999, 22.230303000000006],
          [-123.95590100000001, 22.217169999999996],
          [-124.04693700000001, 22.209404000000006],
          [-124.078765, 22.201763999999997],
          [-124.092715, 22.184537000000006],
          [-124.089237, 22.182338],
          [-124.078677, 22.181019000000006],
          [-124.00750300000001, 22.179385999999994],
          [-123.916551, 22.190494],
          [-123.87830600000001, 22.160279000000003],
          [-123.86140599999999, 22.136664999999994],
          [-123.84740099999999, 22.122106000000002],
          [-123.80358000000001, 22.100059],
          [-123.772467, 22.081922000000006],
          [-123.76835700000001, 22.073662],
          [-123.76750999999999, 22.060803000000007],
          [-123.623921, 22.058280999999994],
          [-123.54011499999999, 22.069920999999994],
          [-123.502704, 22.081165],
          [-123.495845, 22.085387999999995],
          [-123.34475900000001, 22.084936999999996],
          [-123.31384299999999, 22.075287000000003],
          [-123.287768, 22.069229000000007],
          [-123.168568, 22.059290000000004],
          [-123.146726, 22.059487000000004],
          [-123.09300300000001, 22.062960000000004],
          [-123.04714000000001, 22.068326999999996],
          [-122.99747300000001, 22.076845000000006],
          [-122.87601599999999, 22.082832999999994],
          [-122.79123200000001, 22.089138000000005],
          [-122.75070500000001, 22.090159999999997],
          [-122.68130400000001, 22.061573999999993],
          [-122.680204, 22.058128999999994],
          [-122.673584, 22.053685],
          [-122.635985, 22.040366000000006],
          [-122.62228400000001, 22.039525999999995],
          [-122.530755, 22.045062],
          [-122.45767000000001, 22.058578999999995],
          [-122.42372599999999, 22.048984000000004],
          [-122.413452, 22.035084999999995],
          [-122.39189200000001, 22.028723999999997],
          [-122.37214700000001, 22.027985],
          [-122.33128400000001, 22.031402999999997],
          [-122.26966999999999, 22.042103999999995],
          [-122.20588900000001, 22.056753],
          [-122.13951599999999, 22.078818999999996],
          [-122.13742400000001, 22.078547],
          [-122.12970899999999, 22.069486999999995],
          [-122.12178800000001, 22.067391],
          [-122.025701, 22.062828999999994],
          [-121.950043, 22.040301999999997],
          [-121.838018, 22.022582],
          [-121.817091, 22.033089000000004],
          [-121.812306, 22.041687999999994],
          [-121.798585, 22.055317000000002],
          [-121.77553700000001, 22.073732000000007],
          [-121.680577, 22.111587999999998],
          [-121.6653, 22.122176999999994],
          [-121.62700799999999, 22.153194],
          [-121.62398300000001, 22.162081999999998],
          [-121.613943, 22.176693],
          [-121.548429, 22.239912000000004],
          [-121.48453900000001, 22.262426000000005],
          [-121.367875, 22.258652999999995],
          [-121.34118900000001, 22.2551],
          [-121.33726899999999, 22.243162999999996],
          [-121.33212, 22.236430999999996],
          [-121.32077799999999, 22.223591],
          [-121.31302500000001, 22.221051000000003],
          [-121.263655, 22.214153999999994],
          [-121.198971, 22.210949],
          [-121.145397, 22.220179],
          [-121.087342, 22.234207999999995],
          [-121.067871, 22.235163999999997],
          [-121.05273199999999, 22.231018000000006],
          [-121.035866, 22.229437000000004],
          [-121.00002599999999, 22.233126],
          [-120.993965, 22.234443999999996],
          [-120.99106599999999, 22.236816000000005],
          [-120.990275, 22.239715000000004],
          [-120.995905, 22.251962000000006],
          [-120.99853999999999, 22.254935000000003],
          [-121.061034, 22.283804000000003],
          [-121.079328, 22.307125999999997],
          [-121.08916099999999, 22.31514],
          [-121.10799499999999, 22.328367],
          [-121.129512, 22.336247999999998],
          [-121.16309999999999, 22.342291000000003],
          [-121.21983399999999, 22.348917999999998],
          [-121.36074300000001, 22.375943000000007],
          [-121.525554, 22.397046000000003],
          [-121.69440399999999, 22.396174000000002],
          [-121.81753800000001, 22.360815000000002],
          [-121.912946, 22.344436],
          [-121.91630900000001, 22.349480999999997],
          [-121.8809, 22.398763000000002],
          [-121.872447, 22.414131999999995],
          [-121.86338699999999, 22.440782999999996],
          [-121.86415600000001, 22.488195000000005],
          [-121.87487999999999, 22.511446000000007],
          [-122.029043, 22.586504000000005],
          [-122.091453, 22.605963000000003],
          [-122.09791000000001, 22.609863000000004],
          [-122.10564099999999, 22.622584000000003],
          [-122.11331100000001, 22.639595999999997],
          [-122.124348, 22.651291],
          [-122.140377, 22.664737000000002],
          [-122.175398, 22.687788999999995],
          [-122.228635, 22.709770000000006],
          [-122.268767, 22.717905000000002],
          [-122.349774, 22.726713000000004],
          [-122.422414, 22.731581000000006],
          [-122.50141500000001, 22.742503],
          [-122.50052, 22.749751000000003],
          [-122.50250199999999, 22.758875000000003],
          [-122.512617, 22.777732999999998],
          [-122.54438099999999, 22.804872000000003],
          [-122.572224, 22.825363999999993],
          [-122.59423699999999, 22.837647000000004],
          [-122.61473799999999, 22.846475999999996],
          [-122.621564, 22.850859999999997],
          [-122.624945, 22.855030999999997],
          [-122.62669600000001, 22.859572],
          [-122.623054, 22.874325],
          [-122.61458999999999, 22.885941000000003],
          [-122.60105200000001, 22.898455],
          [-122.58285599999999, 22.904291999999998],
          [-122.49743799999999, 22.91986],
          [-122.346352, 22.934792],
          [-122.281116, 22.915678999999997],
          [-122.271769, 22.904144000000002],
          [-122.22867500000001, 22.866623000000004],
          [-122.11730399999999, 22.798482000000007],
          [-122.096878, 22.7885],
          [-122.073714, 22.783323999999993],
          [-122.04912300000001, 22.780638999999994],
          [-122.013623, 22.779405999999994],
          [-122.01145500000001, 22.759062999999998],
          [-122.029615, 22.734579999999994],
          [-122.041314, 22.723764000000003],
          [-122.05882500000001, 22.716252999999995],
          [-122.068253, 22.709857],
          [-122.074634, 22.703681000000003],
          [-122.081515, 22.693051999999994],
          [-122.07801, 22.664047999999994],
          [-122.07362, 22.651217000000003],
          [-122.06906799999999, 22.645700000000005],
          [-122.03315599999999, 22.631585],
          [-121.968863, 22.602610999999996],
          [-121.95341300000001, 22.592365],
          [-121.93264199999999, 22.572547],
          [-121.92544000000001, 22.561215000000004],
          [-121.91585599999999, 22.551339],
          [-121.87709799999999, 22.536877000000004],
          [-121.765368, 22.496933999999996],
          [-121.624334, 22.450142999999997],
          [-121.574129, 22.438565999999994],
          [-121.516449, 22.441839],
          [-121.43531200000001, 22.454161999999997],
          [-121.326349, 22.478370999999996],
          [-121.27980299999999, 22.505179],
          [-121.29320999999999, 22.520590999999996],
          [-121.39900600000001, 22.523528999999996],
          [-121.454092, 22.522205],
          [-121.46922699999999, 22.522842999999995],
          [-121.48360400000001, 22.525626000000003],
          [-121.49350899999999, 22.530976999999993],
          [-121.494988, 22.534442999999996],
          [-121.48630800000001, 22.538037000000003],
          [-121.482869, 22.543723999999997],
          [-121.4904, 22.560844000000003],
          [-121.54175599999999, 22.581379],
          [-121.598953, 22.593181],
          [-121.665368, 22.610433],
          [-121.69312500000001, 22.620562000000007],
          [-121.86618000000001, 22.704977999999997],
          [-121.88167100000001, 22.716796000000002],
          [-121.86153999999999, 22.797076000000004],
          [-121.846258, 22.813647000000003],
          [-121.82417000000001, 22.817888999999994],
          [-121.79630700000001, 22.833125999999993],
          [-121.785495, 22.846547],
          [-121.782218, 22.859955999999997],
          [-121.71958699999999, 22.916898000000003],
          [-121.69214600000001, 22.945032999999995],
          [-121.685775, 22.955793999999997],
          [-121.68850599999999, 22.959799000000004],
          [-121.68628000000001, 22.961366999999996],
          [-121.67435900000001, 22.961965000000006],
          [-121.566678, 22.934775000000002],
          [-121.489169, 22.936949999999996],
          [-121.48512099999999, 22.945646999999994],
          [-121.48538300000001, 22.960818000000003],
          [-121.50574700000001, 22.974314000000007],
          [-121.53052500000001, 22.984950999999995],
          [-121.62216000000001, 23.008145999999996],
          [-121.69739200000001, 23.010848999999993],
          [-121.711715, 23.001043999999993],
          [-121.75964099999999, 23.030572000000006],
          [-121.79917499999999, 23.047501999999994],
          [-121.81025600000001, 23.050281],
          [-121.83632499999999, 23.051777],
          [-121.850188, 23.052186000000006],
          [-121.89370199999999, 23.049075000000002],
          [-122.12318099999999, 23.02579],
          [-122.211633, 23.006709999999998],
          [-122.23396399999999, 22.999567999999996],
          [-122.23430200000001, 22.994580999999997],
          [-122.23922999999999, 22.993814],
          [-122.35395399999999, 22.995127999999994],
          [-122.385074, 22.991235000000003],
          [-122.415866, 22.984710000000007],
          [-122.432615, 22.985089000000002],
          [-122.449219, 22.988390999999993],
          [-122.462616, 22.994322999999994],
          [-122.46685500000001, 22.999339000000006],
          [-122.46182200000001, 23.004425999999995],
          [-122.46552199999999, 23.026629],
          [-122.47276500000001, 23.038368000000006],
          [-122.481257, 23.043113000000005],
          [-122.49055200000001, 23.043330999999995],
          [-122.504523, 23.039629000000005],
          [-122.51487800000001, 23.031069000000002],
          [-122.519046, 23.016552000000004],
          [-122.60356200000001, 23.010490000000004],
          [-122.64026000000001, 23.010869],
          [-122.65409399999999, 23.013098999999997],
          [-122.66166100000001, 23.01889],
          [-122.660733, 23.026770999999997],
          [-122.658706, 23.030334999999994],
          [-122.66074, 23.033884],
          [-122.67665600000001, 23.046789000000004],
          [-122.69906900000001, 23.055476],
          [-122.782401, 23.044466999999997],
          [-122.83042, 23.036173000000005],
          [-122.839402, 23.032956],
          [-122.850964, 23.017922],
          [-122.86594400000001, 23.012542999999994],
          [-122.90134800000001, 23.006833],
          [-123.09785400000001, 23.041190999999998],
          [-123.29926599999999, 23.060748000000004],
          [-123.399048, 23.074167000000003],
          [-123.44119800000001, 23.081458999999995],
          [-123.49520100000001, 23.087502999999998],
          [-123.57701, 23.092490999999995],
          [-123.62495899999999, 23.099390999999997],
          [-123.69887, 23.114442999999994],
          [-123.730671, 23.123773999999997],
          [-123.741345, 23.129123000000007],
          [-123.73772399999999, 23.136802000000003],
          [-123.73690099999999, 23.16323],
          [-123.73746399999999, 23.184753999999998],
          [-123.74082000000001, 23.209959999999995],
          [-123.758588, 23.256439],
          [-123.82218499999999, 23.349812],
          [-123.85358400000001, 23.388101000000006],
          [-123.878781, 23.416124999999994],
          [-124.00703200000001, 23.535698999999994],
          [-124.07951399999999, 23.585856000000007],
          [-124.10871599999999, 23.601992999999993],
          [-124.14438000000001, 23.617227999999997],
          [-124.20981599999999, 23.639078999999995],
          [-124.53393700000001, 23.725606],
          [-124.77833100000001, 23.820865999999995],
          [-124.907297, 23.867844000000005],
          [-125.05751599999999, 23.921694000000002],
          [-125.12956700000001, 23.941833000000003],
          [-125.19091499999999, 23.966071],
          [-125.227228, 23.985321999999996],
          [-125.23161999999999, 23.994512],
          [-125.240848, 23.998714000000007],
          [-125.35998000000001, 24.028053999999997],
          [-124.494202, 24.024330000000006],
          [-124.49987100000001, 24.227513000000002],
          [-123.04523, 24.228720999999993],
          [-122.719806, 24.232729000000006],
          [-122.720616, 24.301027000000005],
          [-121.995486, 24.292164],
          [-121.99314799999999, 24.223274000000004],
          [-121.516254, 24.220811999999995],
          [-121.51996399999999, 24.289553999999995],
          [-121.095913, 24.289367999999996],
          [-121.08953500000001, 24.221231000000003],
          [-120.11833999999999, 24.220066000000003],
          [-120.127816, 24.287307999999996],
          [-119.722546, 24.284088999999994],
          [-119.716147, 24.216796000000002],
          [-118.27472700000001, 24.211915000000005],
          [-118.10010299999999, 24.217806999999993],
          [-117.155836, 24.210442],
          [-117.15029899999999, 24.120780999999994],
          [-116.23810700000001, 24.130761000000007],
          [-116.235534, 24.028751],
          [-115.299012, 24.029940999999994],
          [-115.29802000000001, 24.001771000000005],
          [-115.30699200000001, 23.864033000000006],
          [-115.352249, 23.775553000000002],
          [-115.129726, 23.774969999999996],
          [-115.13235499999999, 23.686611999999997],
          [-114.90473600000001, 23.688032000000007],
          [-114.91102699999999, 23.601856999999995],
          [-114.68669600000001, 23.602172999999993],
          [-114.68835899999999, 23.512480999999994],
          [-114.747501, 23.512680000000003],
          [-114.748759, 23.254419],
          [-114.306093, 23.251225000000005],
          [-114.302303, 23.160767000000007],
          [-114.14522, 23.161924999999997],
          [-114.14159900000001, 22.804852999999994],
          [-114.20154199999999, 22.804620999999997],
          [-114.202144, 22.716586000000007],
          [-114.862595, 22.71691],
          [-114.865962, 22.567723],
          [-115.51393100000001, 22.569278999999995],
          [-115.554608, 22.481911999999994],
          [-115.56243, 22.309487000000004],
          [-115.981123, 22.310447999999994],
          [-115.981539, 22.398030000000006],
          [-116.19717299999999, 22.394757999999996],
          [-116.196191, 22.479445999999996],
          [-116.62429599999999, 22.478785000000002],
          [-116.626712, 22.304930999999996],
          [-117.05153000000001, 22.302054],
          [-117.051694, 22.478750000000005],
          [-117.892416, 22.476794999999996],
          [-117.894181, 22.125283999999994],
          [-118.965461, 22.123439000000005],
          [-118.966298, 21.957306000000003],
          [-119.59518700000001, 21.95809],
          [-119.59357600000001, 21.522689]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02198",
      state: "02",
      county: "198",
      name: "Prince of Wales-Hyder County",
      lsad: "CA",
      censusArea: 3922.873,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.56956, 11.284114000000002],
          [-91.56264999999999, 11.284011999999997],
          [-91.550916, 11.280915],
          [-91.516651, 11.261645000000001],
          [-91.49264, 11.257748999999997],
          [-91.48225199999999, 11.254109999999997],
          [-91.48152200000001, 11.244447999999998],
          [-91.46270100000001, 11.223438000000002],
          [-91.44529299999999, 11.216977],
          [-91.43050099999999, 11.218175000000002],
          [-91.41695100000001, 11.217298],
          [-91.39769000000001, 11.210915999999997],
          [-91.35564199999999, 11.182944999999997],
          [-91.341407, 11.165658999999998],
          [-91.352654, 11.164822000000001],
          [-91.36231900000001, 11.155895999999998],
          [-91.35057499999999, 11.067042],
          [-91.356314, 11.041210999999997],
          [-91.37857199999999, 11.017308],
          [-91.38856899999999, 11.012222000000001],
          [-91.484995, 11.010454000000003],
          [-91.498863, 11.016137999999998],
          [-91.50759, 11.025427],
          [-91.50844900000001, 11.029165999999996],
          [-91.53214, 11.037945],
          [-91.579882, 11.017575999999998],
          [-91.59006299999999, 11.007745],
          [-91.605661, 11.004403000000003],
          [-91.615632, 11.006999],
          [-91.64103499999999, 11.026584999999997],
          [-91.646276, 11.035578999999998],
          [-91.620745, 11.049259],
          [-91.58938699999999, 11.088940000000001],
          [-91.605302, 11.107436],
          [-91.594978, 11.125501999999997],
          [-91.57777300000001, 11.131093999999997],
          [-91.558603, 11.125508000000004],
          [-91.548093, 11.143138],
          [-91.565677, 11.155354000000003],
          [-91.588368, 11.169961],
          [-91.598454, 11.179566000000001],
          [-91.607383, 11.240437],
          [-91.58906999999999, 11.273950999999997],
          [-91.56956, 11.284114000000002]
        ],
        [
          [-93.34484699999999, 11.569327000000001],
          [-93.30740599999999, 11.542248999999998],
          [-93.29234600000001, 11.539735999999998],
          [-93.28531699999999, 11.535314999999997],
          [-93.28191699999999, 11.524486000000003],
          [-93.28985399999999, 11.501869999999997],
          [-93.305747, 11.484115000000003],
          [-93.358993, 11.453831999999998],
          [-93.373595, 11.460656],
          [-93.391243, 11.471978999999997],
          [-93.396511, 11.473168999999999],
          [-93.42651599999999, 11.466701999999998],
          [-93.474054, 11.437154999999997],
          [-93.46530000000001, 11.423369999999998],
          [-93.42582300000001, 11.425511],
          [-93.416649, 11.422145999999998],
          [-93.41252399999999, 11.417560000000002],
          [-93.41158200000001, 11.406683000000001],
          [-93.41938400000001, 11.386105],
          [-93.427366, 11.381832000000003],
          [-93.446281, 11.382174999999997],
          [-93.497028, 11.368158999999999],
          [-93.576808, 11.324795000000002],
          [-93.59580500000001, 11.293765999999998],
          [-93.60349099999999, 11.271738999999997],
          [-93.609073, 11.241486000000002],
          [-93.61277999999999, 11.239736999999998],
          [-93.623457, 11.242570999999998],
          [-93.63450900000001, 11.250042999999998],
          [-93.69017400000001, 11.304409],
          [-93.690944, 11.308703000000001],
          [-93.68915899999999, 11.320284999999998],
          [-93.67224300000001, 11.326765000000002],
          [-93.64772500000001, 11.345480000000002],
          [-93.633006, 11.361299000000002],
          [-93.622422, 11.396473999999998],
          [-93.636291, 11.428423000000002],
          [-93.73907700000001, 11.472323000000003],
          [-93.749371, 11.471817999999999],
          [-93.76434699999999, 11.468592999999998],
          [-93.78905499999999, 11.457892000000001],
          [-93.75913, 11.537371],
          [-93.75287, 11.544282000000003],
          [-93.73302899999999, 11.558757],
          [-93.615623, 11.550775999999999],
          [-93.584326, 11.539707],
          [-93.58200500000001, 11.537363],
          [-93.59016299999999, 11.507235999999999],
          [-93.595632, 11.501483],
          [-93.611671, 11.495584999999998],
          [-93.624912, 11.494674000000003],
          [-93.63297800000001, 11.491664],
          [-93.644202, 11.470815000000002],
          [-93.644565, 11.464680000000001],
          [-93.63867300000001, 11.458902000000002],
          [-93.618367, 11.457045],
          [-93.57369800000001, 11.472082999999998],
          [-93.54408799999999, 11.491784000000003],
          [-93.54232200000001, 11.507083999999999],
          [-93.52912, 11.527700000000003],
          [-93.525646, 11.529097],
          [-93.480965, 11.512737999999999],
          [-93.44367, 11.518639],
          [-93.454576, 11.532423000000001],
          [-93.44265999999999, 11.553012000000003],
          [-93.436102, 11.560872000000003],
          [-93.413499, 11.570383999999997],
          [-93.34484699999999, 11.569327000000001]
        ],
        [
          [-93.62491800000001, 12.343905999999997],
          [-93.593728, 12.352192000000002],
          [-93.58211600000001, 12.352505999999998],
          [-93.41837000000001, 12.332132000000001],
          [-93.328577, 12.332797],
          [-93.28482600000001, 12.327117999999999],
          [-93.235354, 12.324275],
          [-93.19700900000001, 12.333016],
          [-93.16321199999999, 12.317445],
          [-93.094977, 12.250582999999999],
          [-93.07822999999999, 12.246802000000002],
          [-93.07143500000001, 12.238484],
          [-93.040979, 12.184536000000001],
          [-93.05200400000001, 12.155585000000002],
          [-93.05999399999999, 12.150761000000003],
          [-93.062175, 12.141162999999999],
          [-93.061465, 12.135305000000002],
          [-93.05552, 12.125258000000002],
          [-92.92730499999999, 12.034458999999998],
          [-92.89767499999999, 12.020166000000003],
          [-92.837592, 12.024327],
          [-92.635291, 11.921765999999998],
          [-92.61846399999999, 11.911476],
          [-92.615103, 11.908082],
          [-92.614757, 11.899635000000004],
          [-92.592085, 11.877152000000002],
          [-92.543398, 11.845927000000003],
          [-92.50479999999999, 11.815165999999998],
          [-92.470697, 11.782162],
          [-92.46128100000001, 11.683399999999999],
          [-92.46253100000001, 11.673853999999999],
          [-92.44885500000001, 11.667337000000003],
          [-92.41523699999999, 11.667262999999998],
          [-92.39426599999999, 11.669114],
          [-92.38250500000001, 11.665336000000003],
          [-92.35855799999999, 11.648758999999998],
          [-92.332401, 11.595070999999997],
          [-92.32928000000001, 11.578935999999999],
          [-92.301119, 11.550960000000003],
          [-92.24092099999999, 11.526533],
          [-92.19828899999999, 11.513044999999998],
          [-92.188771, 11.508443],
          [-92.146062, 11.470346],
          [-92.142649, 11.460966999999997],
          [-92.142945, 11.457940999999998],
          [-92.16475700000001, 11.451213000000003],
          [-92.17860999999999, 11.452829000000001],
          [-92.219413, 11.472211000000001],
          [-92.230752, 11.479944000000003],
          [-92.23193599999999, 11.483960000000003],
          [-92.24732700000001, 11.492950999999998],
          [-92.31577300000001, 11.514547],
          [-92.325728, 11.515394999999998],
          [-92.34210100000001, 11.504669999999997],
          [-92.31857, 11.469208000000002],
          [-92.28858500000001, 11.451365000000003],
          [-92.28126900000001, 11.444189000000001],
          [-92.28444200000001, 11.442172999999997],
          [-92.30332899999999, 11.438268],
          [-92.41934, 11.432054],
          [-92.470036, 11.427028],
          [-92.47968800000001, 11.420918],
          [-92.47553199999999, 11.418061999999999],
          [-92.45497399999999, 11.413600000000002],
          [-92.390782, 11.401739],
          [-92.273503, 11.419189000000003],
          [-92.25805600000001, 11.416142],
          [-92.22595999999999, 11.374918999999998],
          [-92.166857, 11.363039],
          [-92.14986400000001, 11.328637],
          [-92.126398, 11.288418],
          [-92.11556100000001, 11.281236999999997],
          [-92.09853100000001, 11.280560000000001],
          [-92.10271599999999, 11.268175],
          [-92.109531, 11.260240000000003],
          [-92.142742, 11.238211999999997],
          [-92.164031, 11.237617],
          [-92.214912, 11.2457],
          [-92.229963, 11.238079999999997],
          [-92.21435299999999, 11.222562000000003],
          [-92.20743100000001, 11.218273000000003],
          [-92.12737000000001, 11.199570000000001],
          [-92.104376, 11.200899],
          [-92.08812699999999, 11.206516],
          [-92.075095, 11.222389999999997],
          [-92.07275100000001, 11.233718000000003],
          [-92.078869, 11.240335000000002],
          [-92.07592399999999, 11.246276000000002],
          [-92.06162499999999, 11.260052000000002],
          [-92.03712200000001, 11.275143999999997],
          [-92.028163, 11.27695],
          [-92.0035, 11.265253999999999],
          [-91.99590799999999, 11.259053999999999],
          [-91.979818, 11.211787000000001],
          [-91.977397, 11.180948999999998],
          [-91.98881499999999, 11.165464],
          [-92.031417, 11.151671],
          [-92.039009, 11.14407],
          [-92.040874, 11.127325999999996],
          [-92.03897799999999, 11.125011],
          [-92.016932, 11.120970999999997],
          [-92.03900200000001, 11.086962],
          [-92.09402399999999, 11.039451999999997],
          [-92.15406100000001, 11.018197],
          [-92.16807600000001, 11.015574],
          [-92.18033399999999, 11.015557000000001],
          [-92.196215, 11.008046999999998],
          [-92.19761399999999, 11.005158000000002],
          [-92.19258099999999, 10.989654999999999],
          [-92.14326299999999, 10.984633000000002],
          [-92.13554400000001, 10.985976],
          [-92.072544, 11.016956],
          [-92.03921700000001, 11.036765000000003],
          [-92.028288, 11.038671999999998],
          [-92.000449, 11.035711999999997],
          [-91.98459199999999, 11.027977999999997],
          [-91.98289, 10.853068],
          [-91.97684699999999, 10.848894000000001],
          [-91.965904, 10.835538999999997],
          [-91.957382, 10.804279000000001],
          [-91.95791399999999, 10.791238999999997],
          [-91.99959100000001, 10.731974999999998],
          [-92.01865699999999, 10.710109000000003],
          [-92.02974699999999, 10.701189],
          [-92.142277, 10.691673999999999],
          [-92.16518199999999, 10.694049999999997],
          [-92.17963499999999, 10.705897999999998],
          [-92.199566, 10.715443999999998],
          [-92.256092, 10.734617],
          [-92.26310000000001, 10.734312000000003],
          [-92.279597, 10.728230000000003],
          [-92.280701, 10.726184000000003],
          [-92.280103, 10.715988000000003],
          [-92.281803, 10.715290000000003],
          [-92.307943, 10.718713999999999],
          [-92.35100399999999, 10.818182],
          [-92.350629, 10.821314000000001],
          [-92.34100900000001, 10.826718],
          [-92.332661, 10.826321999999998],
          [-92.314146, 10.835120000000003],
          [-92.309213, 10.847534000000003],
          [-92.387494, 10.913663999999997],
          [-92.484579, 10.899301000000001],
          [-92.55839, 10.932611999999999],
          [-92.581367, 10.946005],
          [-92.60978600000001, 10.965727999999999],
          [-92.61253099999999, 10.969923999999999],
          [-92.614836, 10.980094999999999],
          [-92.60990000000001, 10.991517000000002],
          [-92.598557, 10.990301000000002],
          [-92.57783, 10.994233999999999],
          [-92.57500099999999, 10.998317],
          [-92.54180199999999, 11.097763999999998],
          [-92.548994, 11.113556000000003],
          [-92.550502, 11.114246999999999],
          [-92.56181900000001, 11.114897],
          [-92.59456800000001, 11.105378000000002],
          [-92.619912, 11.069094],
          [-92.624575, 11.061352],
          [-92.62429599999999, 11.056162999999998],
          [-92.626687, 11.053314],
          [-92.630865, 11.052945999999999],
          [-92.63330500000001, 11.054954000000002],
          [-92.637866, 11.073602000000001],
          [-92.624518, 11.114418999999998],
          [-92.60689500000001, 11.141722000000001],
          [-92.59867499999999, 11.150481999999997],
          [-92.591084, 11.155695000000001],
          [-92.58786799999999, 11.155971000000001],
          [-92.581133, 11.166075999999997],
          [-92.605219, 11.194063999999997],
          [-92.620123, 11.199617000000003],
          [-92.637926, 11.186076999999997],
          [-92.63004900000001, 11.177618000000002],
          [-92.62964299999999, 11.174232000000003],
          [-92.63575800000001, 11.152513999999996],
          [-92.66371899999999, 11.143932],
          [-92.69522699999999, 11.137711000000003],
          [-92.735645, 11.134217999999997],
          [-92.71219500000001, 11.098736000000002],
          [-92.68400500000001, 11.045603999999997],
          [-92.68966699999999, 11.027901],
          [-92.692917, 11.025060000000003],
          [-92.748854, 10.996006999999999],
          [-92.81374299999999, 11.010987],
          [-92.864117, 11.024833000000001],
          [-92.916414, 11.044465000000002],
          [-92.926772, 11.055022999999998],
          [-92.93229299999999, 11.070199000000002],
          [-92.931172, 11.074154],
          [-92.909355, 11.081277],
          [-92.88108299999999, 11.110374999999998],
          [-92.86982699999999, 11.124104000000003],
          [-92.87340800000001, 11.134241000000003],
          [-92.894283, 11.169373],
          [-92.935449, 11.209898000000003],
          [-92.93984499999999, 11.211481],
          [-92.982166, 11.215882],
          [-93.00815600000001, 11.2057],
          [-93.04074800000001, 11.232968999999997],
          [-93.043938, 11.248569000000003],
          [-93.10470799999999, 11.263800000000003],
          [-93.119294, 11.251404999999998],
          [-93.07338100000001, 11.184491999999999],
          [-93.01227499999999, 11.128056],
          [-92.99086, 11.079112000000002],
          [-92.990395, 11.06673],
          [-93.01135099999999, 11.056849],
          [-93.01974000000001, 11.048366000000001],
          [-93.01417799999999, 11.035502000000001],
          [-92.959508, 11.021045],
          [-92.909706, 10.923594000000001],
          [-92.91465, 10.914009],
          [-92.889474, 10.896619000000001],
          [-92.82834, 10.868746999999999],
          [-92.80452500000001, 10.870938000000002],
          [-92.80301399999999, 10.873725],
          [-92.803742, 10.897205999999997],
          [-92.807952, 10.904378000000001],
          [-92.81428199999999, 10.909356000000002],
          [-92.817385, 10.923639999999999],
          [-92.800784, 10.933782999999998],
          [-92.730931, 10.939483000000003],
          [-92.650001, 10.904387],
          [-92.628612, 10.883316],
          [-92.625563, 10.807502999999997],
          [-92.61485099999999, 10.777169999999998],
          [-92.619024, 10.766565],
          [-92.63903199999999, 10.753250999999999],
          [-92.66725, 10.763368999999997],
          [-92.681165, 10.773696999999999],
          [-92.70120299999999, 10.798445999999998],
          [-92.701153, 10.811011999999998],
          [-92.70926700000001, 10.817521],
          [-92.72353799999999, 10.825128],
          [-92.72936899999999, 10.826981000000004],
          [-92.754875, 10.821235999999999],
          [-92.75977900000001, 10.816997999999998],
          [-92.759364, 10.808276999999997],
          [-92.74745100000001, 10.800742999999997],
          [-92.72201100000001, 10.763258999999998],
          [-92.67622600000001, 10.680864999999997],
          [-92.67432400000001, 10.674652000000002],
          [-92.67586800000001, 10.672812],
          [-92.69397000000001, 10.669063999999999],
          [-92.775733, 10.674844],
          [-92.866355, 10.700386000000002],
          [-92.86960400000001, 10.702416],
          [-92.86408399999999, 10.716917000000002],
          [-92.865598, 10.728907999999997],
          [-92.87720999999999, 10.753771999999998],
          [-92.89155299999999, 10.768191000000002],
          [-92.909517, 10.780515999999999],
          [-92.91875099999999, 10.783253000000002],
          [-92.940485, 10.783831],
          [-92.95025000000001, 10.788333000000002],
          [-93.03275199999999, 10.855055],
          [-93.09904700000001, 10.919007],
          [-93.123941, 10.940064999999997],
          [-93.15975599999999, 10.958759999999998],
          [-93.16478799999999, 10.976909],
          [-93.15732600000001, 11.000365000000002],
          [-93.16893099999999, 11.030889000000002],
          [-93.17236, 11.032744999999998],
          [-93.197719, 11.033403999999997],
          [-93.21042, 11.040269000000002],
          [-93.23969500000001, 11.092415000000003],
          [-93.22392300000001, 11.099986000000001],
          [-93.21810099999999, 11.101042],
          [-93.14495400000001, 11.099155000000003],
          [-93.134987, 11.099473000000003],
          [-93.128705, 11.101429000000003],
          [-93.15477999999999, 11.12811],
          [-93.18680900000001, 11.136372000000001],
          [-93.193242, 11.134107999999998],
          [-93.201053, 11.134098999999999],
          [-93.20770200000001, 11.13422],
          [-93.21508600000001, 11.136876],
          [-93.236908, 11.183326000000001],
          [-93.187745, 11.193638999999997],
          [-93.18252000000001, 11.201963999999997],
          [-93.223791, 11.229317000000002],
          [-93.254478, 11.231149000000002],
          [-93.281979, 11.217117000000002],
          [-93.32302100000001, 11.207030000000003],
          [-93.34125900000001, 11.205700999999998],
          [-93.35371900000001, 11.221215],
          [-93.36156199999999, 11.225560000000002],
          [-93.37661499999999, 11.228288],
          [-93.40449699999999, 11.214992000000002],
          [-93.415257, 11.212159],
          [-93.42286899999999, 11.210659],
          [-93.44107399999999, 11.211654000000003],
          [-93.453777, 11.218497],
          [-93.471938, 11.247526999999998],
          [-93.473593, 11.255547],
          [-93.47286600000001, 11.267873000000002],
          [-93.459753, 11.306780000000003],
          [-93.45281800000001, 11.319980000000001],
          [-93.43635, 11.330157999999997],
          [-93.39537100000001, 11.341752999999997],
          [-93.352083, 11.347380999999999],
          [-93.33236, 11.345357],
          [-93.313491, 11.331556999999997],
          [-93.310529, 11.320256999999998],
          [-93.311114, 11.317301],
          [-93.29240200000001, 11.291376999999997],
          [-93.26899900000001, 11.286971000000001],
          [-93.24434299999999, 11.291865000000001],
          [-93.235129, 11.304161999999998],
          [-93.23357200000001, 11.308822],
          [-93.254639, 11.318806000000002],
          [-93.279271, 11.333264],
          [-93.280701, 11.341535999999998],
          [-93.27844300000001, 11.349184000000001],
          [-93.269068, 11.359341],
          [-93.257734, 11.367026000000003],
          [-93.226844, 11.38185],
          [-93.20879400000001, 11.384236999999999],
          [-93.18328, 11.379392000000003],
          [-93.121513, 11.414535999999998],
          [-93.10430400000001, 11.426907],
          [-93.13812899999999, 11.458373000000002],
          [-93.157704, 11.497782],
          [-93.13329300000001, 11.502507999999999],
          [-93.132409, 11.513579],
          [-93.156566, 11.563589],
          [-93.17676, 11.586722000000002],
          [-93.26304200000001, 11.568793],
          [-93.275972, 11.580188],
          [-93.29415800000001, 11.588679999999997],
          [-93.3869, 11.619346],
          [-93.43817999999999, 11.643861999999999],
          [-93.43893299999999, 11.645167],
          [-93.408432, 11.667924999999997],
          [-93.39988500000001, 11.665132999999997],
          [-93.394486, 11.666880999999997],
          [-93.38643200000001, 11.693365],
          [-93.416549, 11.739646999999998],
          [-93.43861899999999, 11.748365999999997],
          [-93.49143000000001, 11.764082000000002],
          [-93.512632, 11.755898000000002],
          [-93.509704, 11.744183],
          [-93.496509, 11.733258],
          [-93.49408700000001, 11.733158000000003],
          [-93.490882, 11.726576000000001],
          [-93.48925299999999, 11.713081000000003],
          [-93.49243799999999, 11.706525],
          [-93.527387, 11.695107999999998],
          [-93.55450400000001, 11.698354000000002],
          [-93.58329699999999, 11.705337999999998],
          [-93.62512000000001, 11.719926999999998],
          [-93.643324, 11.729036999999998],
          [-93.694546, 11.780599000000002],
          [-93.695749, 11.783118000000002],
          [-93.646377, 11.823413000000002],
          [-93.627534, 11.831629999999997],
          [-93.561973, 11.835543000000001],
          [-93.550028, 11.832180999999999],
          [-93.44787500000001, 11.791738000000002],
          [-93.422064, 11.788820999999999],
          [-93.347915, 11.803942999999997],
          [-93.34205800000001, 11.805764000000003],
          [-93.32366400000001, 11.818632000000001],
          [-93.335139, 11.866118],
          [-93.34656899999999, 11.875808999999997],
          [-93.37947199999999, 11.880716],
          [-93.39102600000001, 11.878751000000001],
          [-93.41879700000001, 11.883277999999997],
          [-93.471044, 11.947381],
          [-93.49505199999999, 12.017088999999999],
          [-93.541041, 11.977322000000001],
          [-93.63812200000001, 11.920982000000002],
          [-93.69990000000001, 11.912205999999998],
          [-93.79993099999999, 11.925348999999997],
          [-93.826236, 11.948749999999997],
          [-93.83437, 11.963577999999998],
          [-93.81277, 11.974134999999997],
          [-93.798092, 11.975096],
          [-93.77784600000001, 11.982875999999997],
          [-93.74880300000001, 12.009535999999997],
          [-93.69376500000001, 12.070562000000002],
          [-93.68420900000001, 12.075507000000002],
          [-93.65924100000001, 12.083818],
          [-93.548802, 12.14284],
          [-93.54283000000001, 12.160794000000003],
          [-93.55345399999999, 12.169015000000002],
          [-93.60149999999999, 12.191924999999998],
          [-93.63928200000001, 12.198813000000001],
          [-93.664218, 12.310504000000002],
          [-93.65641500000001, 12.326909],
          [-93.64991599999999, 12.326791999999998],
          [-93.643182, 12.324458999999997],
          [-93.62465800000001, 12.337069999999997],
          [-93.62512100000001, 12.339846999999999],
          [-93.62491800000001, 12.343905999999997]
        ],
        [
          [-93.846128, 11.904622000000003],
          [-93.840833, 11.892726000000003],
          [-93.84029799999999, 11.886769999999999],
          [-93.847565, 11.869599000000001],
          [-93.861039, 11.848844],
          [-93.86698799999999, 11.845886],
          [-93.89470600000001, 11.845641],
          [-93.90318400000001, 11.848101],
          [-93.92025000000001, 11.860295],
          [-93.929325, 11.869537999999999],
          [-93.945619, 11.896216000000003],
          [-93.943499, 11.912446000000003],
          [-93.94029800000001, 11.917506000000003],
          [-93.93501599999999, 11.920689000000003],
          [-93.891851, 11.936680000000003],
          [-93.87649400000001, 11.937683],
          [-93.86409900000001, 11.936286000000003],
          [-93.85429099999999, 11.931581000000001],
          [-93.84676300000001, 11.91167],
          [-93.846128, 11.904622000000003]
        ],
        [
          [-90.24554, 12.096876000000002],
          [-90.10276099999999, 12.116695999999997],
          [-90.03157300000001, 12.036791000000001],
          [-90.016874, 12.017322999999998],
          [-90.00425999999999, 11.993378999999997],
          [-90.01319799999999, 11.916381999999999],
          [-90.08451, 11.823996999999999],
          [-90.12371999999999, 11.80704],
          [-90.12853799999999, 11.802148000000003],
          [-90.15059500000001, 11.767031000000003],
          [-90.23464899999999, 11.814309999999999],
          [-90.38873699999999, 11.943317999999998],
          [-90.285132, 11.997546],
          [-90.22006400000001, 11.998911999999997],
          [-90.29111700000001, 12.054246],
          [-90.24554, 12.096876000000002]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02261",
      state: "02",
      county: "261",
      name: "Valdez-Cordova County",
      lsad: "CA",
      censusArea: 34239.88,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.56280100000001, 16.579821000000003],
          [-107.55539200000001, 16.574059],
          [-107.55170899999999, 16.559612],
          [-107.565775, 16.534713000000004],
          [-107.607756, 16.50692],
          [-107.62383500000001, 16.465877999999996],
          [-107.61997199999999, 16.436821000000002],
          [-107.674351, 16.414430000000003],
          [-107.69077300000001, 16.405054],
          [-107.68188799999999, 16.388167000000003],
          [-107.63008099999999, 16.38955],
          [-107.62201999999999, 16.383794],
          [-107.61890600000001, 16.368848],
          [-107.639474, 16.340578999999998],
          [-107.67113499999999, 16.308929],
          [-107.703599, 16.285589],
          [-107.69860800000001, 16.245552000000004],
          [-107.70473100000001, 16.227874],
          [-107.720124, 16.202002],
          [-107.760681, 16.156396],
          [-107.76648399999999, 16.154179999999997],
          [-107.783583, 16.161073000000002],
          [-107.82015899999999, 16.179555],
          [-107.84568100000001, 16.195434],
          [-107.83228500000001, 16.197854999999997],
          [-107.827991, 16.200629999999997],
          [-107.82896199999999, 16.207442],
          [-107.85545300000001, 16.216419000000002],
          [-107.908985, 16.224359],
          [-107.94515799999999, 16.222324],
          [-107.95622800000001, 16.228667],
          [-107.95053200000001, 16.243791],
          [-107.933269, 16.273632],
          [-107.837456, 16.414451999999997],
          [-107.792822, 16.476193000000002],
          [-107.78254799999999, 16.4833],
          [-107.778269, 16.484007],
          [-107.765825, 16.476505000000003],
          [-107.77932899999999, 16.457078000000003],
          [-107.750864, 16.440981],
          [-107.73815099999999, 16.441277],
          [-107.71531200000001, 16.447915000000002],
          [-107.70916, 16.451883000000002],
          [-107.717097, 16.467281999999997],
          [-107.726642, 16.472216000000003],
          [-107.72646, 16.502533],
          [-107.721824, 16.508634999999998],
          [-107.613843, 16.565906],
          [-107.566372, 16.580849],
          [-107.56280100000001, 16.579821000000003]
        ],
        [
          [-107.444468, 16.699654000000002],
          [-107.38477900000001, 16.710017],
          [-107.308537, 16.666793],
          [-107.30291500000001, 16.660673000000003],
          [-107.33742899999999, 16.632685000000002],
          [-107.344494, 16.628543999999998],
          [-107.45740699999999, 16.61909],
          [-107.48604499999999, 16.619348000000002],
          [-107.50160299999999, 16.654772],
          [-107.48648399999999, 16.686582],
          [-107.479814, 16.693593],
          [-107.449559, 16.700046],
          [-107.444468, 16.699654000000002]
        ],
        [
          [-107.95203900000001, 16.741878999999997],
          [-107.90602100000001, 16.735515],
          [-107.848176, 16.698116],
          [-107.846103, 16.694508999999996],
          [-107.86005700000001, 16.677233],
          [-107.868067, 16.670825],
          [-107.932931, 16.655714000000003],
          [-107.970684, 16.673799000000002],
          [-108.02025900000001, 16.72495],
          [-107.965419, 16.751996],
          [-107.95723899999999, 16.747706],
          [-107.95203900000001, 16.741878999999997]
        ],
        [
          [-107.13131899999999, 16.912931999999998],
          [-107.11533600000001, 16.911938],
          [-107.07777200000001, 16.899503000000003],
          [-107.071788, 16.893833],
          [-107.08964499999999, 16.874693],
          [-107.126799, 16.858010999999998],
          [-107.14180200000001, 16.853991],
          [-107.192354, 16.861635],
          [-107.217749, 16.869740999999998],
          [-107.253128, 16.872968999999998],
          [-107.30908600000001, 16.873924000000002],
          [-107.32563999999999, 16.877153],
          [-107.32108400000001, 16.880198],
          [-107.226303, 16.910421],
          [-107.21032400000001, 16.908776000000003],
          [-107.193399, 16.902949],
          [-107.178969, 16.903703999999998],
          [-107.13131899999999, 16.912931999999998]
        ],
        [
          [-107.217704, 16.293504],
          [-107.19494, 16.304563],
          [-107.183277, 16.320680000000003],
          [-107.18524300000001, 16.323082999999997],
          [-107.195608, 16.326224000000003],
          [-107.211625, 16.324936],
          [-107.21531200000001, 16.327109],
          [-107.21879899999999, 16.334726000000003],
          [-107.21467899999999, 16.343792999999998],
          [-107.21158199999999, 16.34626],
          [-107.147514, 16.37247],
          [-107.11266699999999, 16.380982000000003],
          [-107.098703, 16.378697000000003],
          [-107.09189699999999, 16.374969],
          [-107.08936299999999, 16.369126],
          [-107.092717, 16.363396],
          [-107.10400999999999, 16.356727],
          [-107.095483, 16.341963999999997],
          [-107.07393200000001, 16.338982],
          [-107.01316399999999, 16.343863],
          [-107.00613799999999, 16.343119],
          [-107.003839, 16.340310000000002],
          [-107.00409099999999, 16.334348],
          [-107.015307, 16.322547999999998],
          [-107.089866, 16.292105999999997],
          [-107.103118, 16.280189999999997],
          [-107.104107, 16.277155],
          [-107.08046300000001, 16.265355],
          [-107.06476799999999, 16.270187999999997],
          [-106.96263300000001, 16.311911000000002],
          [-106.94402400000001, 16.31602],
          [-106.91616400000001, 16.314898],
          [-106.91177200000001, 16.309807],
          [-106.91137699999999, 16.297593],
          [-106.94808900000001, 16.269973999999998],
          [-107.00206700000001, 16.232453],
          [-107.13964100000001, 16.179057999999998],
          [-107.202416, 16.151128],
          [-107.25386800000001, 16.113852],
          [-107.36542399999999, 16.042867],
          [-107.388508, 16.011116],
          [-107.388686, 16.004300999999998],
          [-107.438369, 15.97468],
          [-107.493235, 15.955388],
          [-107.49978300000001, 15.951068999999997],
          [-107.505311, 15.937494000000001],
          [-107.49962500000001, 15.926296],
          [-107.494317, 15.922756],
          [-107.483056, 15.920873],
          [-107.47631200000001, 15.921562999999999],
          [-107.448681, 15.915892],
          [-107.443678, 15.913542999999997],
          [-107.45236, 15.880518000000002],
          [-107.46691999999999, 15.8701],
          [-107.533041, 15.852401],
          [-107.541643, 15.853282],
          [-107.540437, 15.857264],
          [-107.542664, 15.861336000000001],
          [-107.571278, 15.868709000000003],
          [-107.596339, 15.868872000000003],
          [-107.62198000000001, 15.866484],
          [-107.651738, 15.851222999999997],
          [-107.657015, 15.844034999999998],
          [-107.657048, 15.839497000000001],
          [-107.66038800000001, 15.832248],
          [-107.67713900000001, 15.821368],
          [-107.693869, 15.819070000000004],
          [-107.727499, 15.819780000000002],
          [-107.738538, 15.822949000000001],
          [-107.745417, 15.821843999999999],
          [-107.810788, 15.802233000000001],
          [-107.831008, 15.790042999999997],
          [-107.874097, 15.783259999999999],
          [-107.912883, 15.79224],
          [-107.924906, 15.799712],
          [-107.928064, 15.803496000000003],
          [-107.90824699999999, 15.852832],
          [-107.895411, 15.869145000000003],
          [-107.877936, 15.879677999999998],
          [-107.856693, 15.886660999999997],
          [-107.80827199999999, 15.888148000000001],
          [-107.797213, 15.909477000000003],
          [-107.809035, 15.923479999999998],
          [-107.80425199999999, 15.936497000000003],
          [-107.73963499999999, 15.968007999999998],
          [-107.728869, 15.969509000000002],
          [-107.707445, 15.964151000000001],
          [-107.690642, 15.963236000000002],
          [-107.68628000000001, 15.964475999999998],
          [-107.659569, 15.980063999999999],
          [-107.662645, 15.986676000000003],
          [-107.647042, 16.005649],
          [-107.555529, 16.051130999999998],
          [-107.479543, 16.076653],
          [-107.45149, 16.087919999999997],
          [-107.433254, 16.096159],
          [-107.406, 16.112631],
          [-107.388701, 16.126472999999997],
          [-107.319715, 16.190581],
          [-107.31569099999999, 16.202891],
          [-107.32284100000001, 16.214784],
          [-107.312081, 16.222870999999998],
          [-107.281373, 16.226812000000002],
          [-107.249786, 16.228589],
          [-107.24431000000001, 16.231298000000002],
          [-107.219108, 16.253512999999998],
          [-107.20615000000001, 16.269508000000002],
          [-107.217704, 16.293504]
        ],
        [
          [-101.00184999999999, 16.391688000000002],
          [-101.213615, 16.392456000000003],
          [-101.21369100000001, 16.435809],
          [-101.76369600000001, 16.435026999999998],
          [-101.765581, 16.527746],
          [-101.963079, 16.521375999999997],
          [-102.81732400000001, 16.515849000000003],
          [-103.16706099999999, 16.518816],
          [-103.889952, 16.042299999999997],
          [-103.88547399999999, 15.996786999999998],
          [-103.89142899999999, 15.995798],
          [-103.93121099999999, 15.998342999999998],
          [-103.978568, 16.006346999999998],
          [-103.98923500000001, 16.008830000000003],
          [-103.988639, 16.013993],
          [-103.991356, 16.017725],
          [-104.00124400000001, 16.020905999999997],
          [-104.013823, 16.024397999999998],
          [-104.04353599999999, 16.025174],
          [-104.055365, 16.037003],
          [-104.052424, 16.047001],
          [-104.033324, 16.059533000000002],
          [-104.140503, 16.114400000000003],
          [-104.204654, 16.137301],
          [-104.229803, 16.142788000000003],
          [-104.25387799999999, 16.165894],
          [-104.25397899999999, 16.182934000000003],
          [-104.31885600000001, 16.189295],
          [-104.35877400000001, 16.188132000000003],
          [-104.36931100000001, 16.185659],
          [-104.37922499999999, 16.169089999999997],
          [-104.441936, 16.163069],
          [-104.453957, 16.166004],
          [-104.47816, 16.181595],
          [-104.501059, 16.189815000000003],
          [-104.53489200000001, 16.18942],
          [-104.54510099999999, 16.186999],
          [-104.553786, 16.181914],
          [-104.555093, 16.178485000000002],
          [-104.55816300000001, 16.177796999999998],
          [-104.59625600000001, 16.181666],
          [-104.654899, 16.204881999999998],
          [-104.66655600000001, 16.222572],
          [-104.66268500000001, 16.229295999999998],
          [-104.662364, 16.23948],
          [-104.666134, 16.243885],
          [-104.715474, 16.271214999999998],
          [-104.75344999999999, 16.283515],
          [-104.782521, 16.291972],
          [-104.89281500000001, 16.292821000000004],
          [-104.914016, 16.280934000000002],
          [-104.94213400000001, 16.289727999999997],
          [-104.91270700000001, 16.363177999999998],
          [-104.87142800000001, 16.407269],
          [-104.855457, 16.416885999999998],
          [-104.834059, 16.443751],
          [-104.84866199999999, 16.455191999999997],
          [-104.874451, 16.457304],
          [-104.88734199999999, 16.456048000000003],
          [-104.90329600000001, 16.442580999999997],
          [-104.964135, 16.444466],
          [-104.983585, 16.446902],
          [-105.01240899999999, 16.447920000000003],
          [-105.12555, 16.429389],
          [-105.152365, 16.421557999999997],
          [-105.181041, 16.407531],
          [-105.191183, 16.395238999999997],
          [-105.202891, 16.374915],
          [-105.16993400000001, 16.329987000000003],
          [-105.112199, 16.2962],
          [-105.12244100000001, 16.289876],
          [-105.22289599999999, 16.297911],
          [-105.364296, 16.335960999999998],
          [-105.50331499999999, 16.387954],
          [-105.510457, 16.408988],
          [-105.50393, 16.410607],
          [-105.50154900000001, 16.416798999999997],
          [-105.502351, 16.420811],
          [-105.53694200000001, 16.430532999999997],
          [-105.561523, 16.443123999999997],
          [-105.594158, 16.45183],
          [-105.66884099999999, 16.465431000000002],
          [-105.735938, 16.47466],
          [-105.799318, 16.462031000000003],
          [-105.85346899999999, 16.44563],
          [-105.882293, 16.444633000000003],
          [-105.9469, 16.455395000000003],
          [-105.957404, 16.461101],
          [-105.96106, 16.465017000000003],
          [-105.960508, 16.467509999999997],
          [-105.914403, 16.492350000000002],
          [-105.863092, 16.501821],
          [-105.80238700000001, 16.520173],
          [-105.71289100000001, 16.583249000000002],
          [-105.736494, 16.59521],
          [-105.76404500000001, 16.591588],
          [-105.78062800000001, 16.579182000000003],
          [-105.79881900000001, 16.561918],
          [-105.820659, 16.550051000000003],
          [-105.828622, 16.549746],
          [-105.96454299999999, 16.513557],
          [-106.039209, 16.492967999999998],
          [-106.074409, 16.480085000000003],
          [-106.109713, 16.470345000000002],
          [-106.21678600000001, 16.450228000000003],
          [-106.24714399999999, 16.451188000000002],
          [-106.31255200000001, 16.457438000000003],
          [-106.317354, 16.460608],
          [-106.351292, 16.454293],
          [-106.355334, 16.449348999999998],
          [-106.35625200000001, 16.425527000000002],
          [-106.35010399999999, 16.407780000000002],
          [-106.330118, 16.407097999999998],
          [-106.308784, 16.414246],
          [-106.284201, 16.417656],
          [-106.13395700000001, 16.431523],
          [-106.127029, 16.430815000000003],
          [-106.123595, 16.428030999999997],
          [-106.12619900000001, 16.42429],
          [-106.09424899999999, 16.410296000000002],
          [-106.133058, 16.383774000000003],
          [-106.19868, 16.359927999999996],
          [-106.26805200000001, 16.347958],
          [-106.302167, 16.349234000000003],
          [-106.39285899999999, 16.327475999999997],
          [-106.45832200000001, 16.307250000000003],
          [-106.490407, 16.294939],
          [-106.539897, 16.270968000000003],
          [-106.607295, 16.241182000000002],
          [-106.62798000000001, 16.239632999999998],
          [-106.64103, 16.240896999999997],
          [-106.650455, 16.242981999999998],
          [-106.68912599999999, 16.271279],
          [-106.693637, 16.279608000000003],
          [-106.693149, 16.284593],
          [-106.68146999999999, 16.292247000000003],
          [-106.649855, 16.305062999999997],
          [-106.594979, 16.321202999999997],
          [-106.571617, 16.321755000000003],
          [-106.54030599999999, 16.338811],
          [-106.524194, 16.350667],
          [-106.54270399999999, 16.357974],
          [-106.57543799999999, 16.357272000000002],
          [-106.60703799999999, 16.351672999999998],
          [-106.62421699999999, 16.341406999999997],
          [-106.65589399999999, 16.340462000000002],
          [-106.717151, 16.349598],
          [-106.72511399999999, 16.359938999999997],
          [-106.723671, 16.387605999999998],
          [-106.721082, 16.396416000000002],
          [-106.63738599999999, 16.467177999999997],
          [-106.610443, 16.485613],
          [-106.590633, 16.491039],
          [-106.528853, 16.492134999999998],
          [-106.523866, 16.487332000000002],
          [-106.50544500000001, 16.476961000000003],
          [-106.45504700000001, 16.465318000000003],
          [-106.36800299999999, 16.480671],
          [-106.316364, 16.497498999999998],
          [-106.28968599999999, 16.515692],
          [-106.15589299999999, 16.526296000000002],
          [-106.07996299999999, 16.543056],
          [-105.95133999999999, 16.576779000000002],
          [-105.88652300000001, 16.585713],
          [-105.87257199999999, 16.584836000000003],
          [-105.84422900000001, 16.586509],
          [-105.800411, 16.593995999999997],
          [-105.79474400000001, 16.601121],
          [-105.795287, 16.603150999999997],
          [-105.815708, 16.612217],
          [-105.83232000000001, 16.614851],
          [-105.85604599999999, 16.610936000000002],
          [-105.88835, 16.610304],
          [-105.896266, 16.611789],
          [-105.897785, 16.613653],
          [-105.89616000000001, 16.628684],
          [-105.895243, 16.629213],
          [-105.897145, 16.651214000000003],
          [-105.883904, 16.658185000000003],
          [-105.86777000000001, 16.666784],
          [-105.84134499999999, 16.685893],
          [-105.841418, 16.689787000000003],
          [-105.84454, 16.690168999999997],
          [-105.85178300000001, 16.689858],
          [-105.85816, 16.688484000000003],
          [-105.87582, 16.683453],
          [-105.89425700000001, 16.674163999999998],
          [-105.89920799999999, 16.671118],
          [-105.92200600000001, 16.651954000000003],
          [-105.924923, 16.648898000000003],
          [-105.93692100000001, 16.632053],
          [-105.93706700000001, 16.63049],
          [-105.96555900000001, 16.622748],
          [-106.002533, 16.615082],
          [-106.00462099999999, 16.616231],
          [-106.005755, 16.616853999999996],
          [-106.007675, 16.619742000000002],
          [-106.007747, 16.625326],
          [-105.999198, 16.640832000000003],
          [-105.96873400000001, 16.668235000000003],
          [-105.93703099999999, 16.682822],
          [-105.93143599999999, 16.685478000000003],
          [-105.91153800000001, 16.696647],
          [-105.89916199999999, 16.705641999999997],
          [-105.90127899999999, 16.715373],
          [-105.90547699999999, 16.715045000000003],
          [-105.978105, 16.684711999999998],
          [-106.016402, 16.667222000000002],
          [-106.02502000000001, 16.665311000000003],
          [-106.05566999999999, 16.658685],
          [-106.08629300000001, 16.652030000000003],
          [-106.11691200000001, 16.643327],
          [-106.130941, 16.639181],
          [-106.143249, 16.633868999999997],
          [-106.14723599999999, 16.631407000000003],
          [-106.18767600000001, 16.624521],
          [-106.253074, 16.622315],
          [-106.25837999999999, 16.626288000000002],
          [-106.26314199999999, 16.631932],
          [-106.26868400000001, 16.641240000000003],
          [-106.27025699999999, 16.644928],
          [-106.27025, 16.648035],
          [-106.262982, 16.651569000000002],
          [-106.18815900000001, 16.687333000000002],
          [-106.178676, 16.691482999999998],
          [-106.14063999999999, 16.707652000000003],
          [-106.12407300000001, 16.712417000000002],
          [-106.10106099999999, 16.719276999999998],
          [-106.043736, 16.743636000000002],
          [-106.04532599999999, 16.754040000000003],
          [-106.04870399999999, 16.76377],
          [-106.06455700000001, 16.776944],
          [-106.06722099999999, 16.773918000000002],
          [-106.070967, 16.770955999999998],
          [-106.085107, 16.761063],
          [-106.08977999999999, 16.758156],
          [-106.12038999999999, 16.741981000000003],
          [-106.16022000000001, 16.726383],
          [-106.168059, 16.72535],
          [-106.191156, 16.731990000000003],
          [-106.199026, 16.734358999999998],
          [-106.201912, 16.735912],
          [-106.20009999999999, 16.743080999999997],
          [-106.20874, 16.744390000000003],
          [-106.216811, 16.7417],
          [-106.22825, 16.735643000000003],
          [-106.230706, 16.722008000000002],
          [-106.23951199999999, 16.716889000000002],
          [-106.25766300000001, 16.713068],
          [-106.30339799999999, 16.713214],
          [-106.31385800000001, 16.717926],
          [-106.31729999999999, 16.721124000000003],
          [-106.317949, 16.723816999999997],
          [-106.31228300000001, 16.734401],
          [-106.346573, 16.735747000000003],
          [-106.38689199999999, 16.714598000000002],
          [-106.402873, 16.693084],
          [-106.41252, 16.69045],
          [-106.474142, 16.681539],
          [-106.49984900000001, 16.680134000000002],
          [-106.51784799999999, 16.688102],
          [-106.532396, 16.689748],
          [-106.578813, 16.690212000000002],
          [-106.60715300000001, 16.686377],
          [-106.623266, 16.680419999999998],
          [-106.649059, 16.683438000000002],
          [-106.667754, 16.692760999999997],
          [-106.699219, 16.732176000000003],
          [-106.703597, 16.741903],
          [-106.605008, 16.758608000000002],
          [-106.566731, 16.751198000000002],
          [-106.500678, 16.772112999999997],
          [-106.464824, 16.770722],
          [-106.358618, 16.786192999999997],
          [-106.30444499999999, 16.798038],
          [-106.255415, 16.809962],
          [-106.18315799999999, 16.846969],
          [-106.171897, 16.862823],
          [-106.17313100000001, 16.866070999999998],
          [-106.188185, 16.869374],
          [-106.262572, 16.867787],
          [-106.26865900000001, 16.863841999999998],
          [-106.290202, 16.842694],
          [-106.31375700000001, 16.827833],
          [-106.33342400000001, 16.821921000000003],
          [-106.39436900000001, 16.812271000000003],
          [-106.550577, 16.809402],
          [-106.55596399999999, 16.810066],
          [-106.620419, 16.869019],
          [-106.664368, 16.870854],
          [-106.700741, 16.848345000000002],
          [-106.718132, 16.835667],
          [-106.71953199999999, 16.830166],
          [-106.71474799999999, 16.820385],
          [-106.71978999999999, 16.814475],
          [-106.72484399999999, 16.81212],
          [-106.75484700000001, 16.807882],
          [-106.800612, 16.80516],
          [-106.819018, 16.816346000000003],
          [-106.819008, 16.841568000000002],
          [-106.816305, 16.855628000000003],
          [-106.787431, 16.865597],
          [-106.77415500000001, 16.876224999999998],
          [-106.757004, 16.878453999999998],
          [-106.727226, 16.86627],
          [-106.69769, 16.872534],
          [-106.711287, 16.896465],
          [-106.73602500000001, 16.910300999999997],
          [-106.746758, 16.935454],
          [-106.745543, 16.957582000000002],
          [-106.701356, 16.987009],
          [-106.65342999999999, 17.047752000000003],
          [-106.661518, 17.060775999999997],
          [-106.690552, 17.064076],
          [-106.78372999999999, 17.042935999999997],
          [-106.84811199999999, 17.000587000000003],
          [-106.862709, 16.982523],
          [-106.86196100000001, 16.976177],
          [-106.86882600000001, 16.971448000000002],
          [-106.879853, 16.965161000000002],
          [-106.929789, 16.944263],
          [-106.973072, 16.934835],
          [-107.038952, 16.942079],
          [-107.05575400000001, 16.945467999999998],
          [-107.07055199999999, 16.963312000000002],
          [-107.06323, 16.974057000000002],
          [-107.047088, 16.991208999999998],
          [-107.062671, 17.004336000000002],
          [-107.094863, 17.010188999999997],
          [-107.112607, 17.002974000000002],
          [-107.13688400000001, 16.980967999999997],
          [-107.14463900000001, 16.963492000000002],
          [-107.135571, 16.946247999999997],
          [-107.143314, 16.939830999999998],
          [-107.17162400000001, 16.932876999999998],
          [-107.18125699999999, 16.933099],
          [-107.20492999999999, 16.942659999999997],
          [-107.215273, 16.948076999999998],
          [-107.220809, 16.953121000000003],
          [-107.22648699999999, 16.962159999999997],
          [-107.21957499999999, 16.969504999999998],
          [-107.220325, 16.981702],
          [-107.22161600000001, 16.983541000000002],
          [-107.252984, 16.979621],
          [-107.27364600000001, 16.974595],
          [-107.27800400000001, 16.961063000000003],
          [-107.280437, 16.916963000000003],
          [-107.378086, 16.877845],
          [-107.451569, 16.894219],
          [-107.45290399999999, 16.897365999999998],
          [-107.45169899999999, 16.92588],
          [-107.453406, 16.941468],
          [-107.47309000000001, 16.957552],
          [-107.491546, 16.957998000000003],
          [-107.50726800000001, 16.927235000000003],
          [-107.50601, 16.921169999999996],
          [-107.502365, 16.920429],
          [-107.49381199999999, 16.912379],
          [-107.517424, 16.894819],
          [-107.525056, 16.896057],
          [-107.543002, 16.903331],
          [-107.549756, 16.908009],
          [-107.536798, 17.019346],
          [-107.53403399999999, 17.03109],
          [-107.515782, 17.061408],
          [-107.50232299999999, 17.072056000000003],
          [-107.51377600000001, 17.096127000000003],
          [-107.52509699999999, 17.101176000000002],
          [-107.558201, 17.099797000000002],
          [-107.554439, 17.091718999999998],
          [-107.55722600000001, 17.081401999999997],
          [-107.59126, 17.016591],
          [-107.6188, 16.970039999999997],
          [-107.613846, 16.951496],
          [-107.597749, 16.913905],
          [-107.587309, 16.874463],
          [-107.602405, 16.849978],
          [-107.626585, 16.845064999999998],
          [-107.66859299999999, 16.841563],
          [-107.67192800000001, 16.845283000000002],
          [-107.677292, 16.86996],
          [-107.66296, 16.874951000000003],
          [-107.665855, 16.883774000000003],
          [-107.730391, 16.911256],
          [-107.760843, 16.913227],
          [-107.787115, 16.873511],
          [-107.779717, 16.863435000000003],
          [-107.76696100000001, 16.853544],
          [-107.750212, 16.852141000000003],
          [-107.732124, 16.824711],
          [-107.729421, 16.818252],
          [-107.733252, 16.816975],
          [-107.743256, 16.813887],
          [-107.77715699999999, 16.811017999999997],
          [-107.828765, 16.815947],
          [-107.85592, 16.820881999999997],
          [-107.913399, 16.825152000000003],
          [-107.91511600000001, 16.818955000000003],
          [-107.920445, 16.812441999999997],
          [-108.033953, 16.783198],
          [-108.09814800000001, 16.786555999999997],
          [-108.13398699999999, 16.791268000000002],
          [-108.14435499999999, 16.797089],
          [-108.15159700000001, 16.818122000000002],
          [-108.14829800000001, 16.828701000000002],
          [-108.10184100000001, 16.899347],
          [-108.08521999999999, 16.918613],
          [-108.065132, 16.937963000000003],
          [-108.01727600000001, 16.971807],
          [-107.95061899999999, 17.029210999999997],
          [-107.94778500000001, 17.040625],
          [-107.96196900000001, 17.059537],
          [-107.97339199999999, 17.067391],
          [-107.982673, 17.070245999999997],
          [-107.991241, 17.069533],
          [-108.00163699999999, 17.060102999999998],
          [-108.00321600000001, 17.053797000000003],
          [-108.065505, 17.003979],
          [-108.09009, 17.005110000000002],
          [-108.09500399999999, 17.011384],
          [-108.105388, 17.035123],
          [-108.12512799999999, 17.070698],
          [-108.149024, 17.076672000000002],
          [-108.16596899999999, 17.069277],
          [-108.16445200000001, 17.042665],
          [-108.177649, 16.999608000000002],
          [-108.19897, 16.971584],
          [-108.218954, 16.953573],
          [-108.24166399999999, 16.937738000000003],
          [-108.26558399999999, 16.936331000000003],
          [-108.281248, 16.917792],
          [-108.294475, 16.862751000000003],
          [-108.30971, 16.837736999999997],
          [-108.340711, 16.809072],
          [-108.35046, 16.803991000000003],
          [-108.375416, 16.803469999999997],
          [-108.38951700000001, 16.805622],
          [-108.396614, 16.813693999999998],
          [-108.42655500000001, 16.827112999999997],
          [-108.446278, 16.808705000000003],
          [-108.450122, 16.796405],
          [-108.45088999999999, 16.789487],
          [-108.441666, 16.777956000000003],
          [-108.43107800000001, 16.771842],
          [-108.405576, 16.780301],
          [-108.395962, 16.779701000000003],
          [-108.38199900000001, 16.775768],
          [-108.36640700000001, 16.765833],
          [-108.36531400000001, 16.740969],
          [-108.384094, 16.687753999999998],
          [-108.374018, 16.67264],
          [-108.347881, 16.680327],
          [-108.326357, 16.709539],
          [-108.280136, 16.753337000000002],
          [-108.26952299999999, 16.757389000000003],
          [-108.22975600000001, 16.764139999999998],
          [-108.147662, 16.758536],
          [-108.12036599999999, 16.748952000000003],
          [-108.106987, 16.73977],
          [-108.09131500000001, 16.676249],
          [-108.09373199999999, 16.661533],
          [-108.142596, 16.622632000000003],
          [-108.229961, 16.595062],
          [-108.23782800000001, 16.600206],
          [-108.254852, 16.595124],
          [-108.29283699999999, 16.565496000000003],
          [-108.30612300000001, 16.550702],
          [-108.32816700000001, 16.531913000000003],
          [-108.333245, 16.530464000000002],
          [-108.34562700000001, 16.502972999999997],
          [-108.34637799999999, 16.488729999999997],
          [-108.32898700000001, 16.476182],
          [-108.293534, 16.483001],
          [-108.255425, 16.493409999999997],
          [-108.24786700000001, 16.500504],
          [-108.25013200000001, 16.507573],
          [-108.238554, 16.521442999999998],
          [-108.19203300000001, 16.557371000000003],
          [-108.114766, 16.596029],
          [-108.102747, 16.598025999999997],
          [-108.086378, 16.595518],
          [-107.979019, 16.519146],
          [-107.963617, 16.50275],
          [-107.97119799999999, 16.486479000000003],
          [-107.97725, 16.480936999999997],
          [-107.941709, 16.444029],
          [-107.992939, 16.352596],
          [-108.025597, 16.279029],
          [-108.064494, 16.276232999999998],
          [-108.11586399999999, 16.279522],
          [-108.15013200000001, 16.296210000000002],
          [-108.153122, 16.303061999999997],
          [-108.15362400000001, 16.3116],
          [-108.15010899999999, 16.320739000000003],
          [-108.15023099999999, 16.324182],
          [-108.17088100000001, 16.335265999999997],
          [-108.192669, 16.339103],
          [-108.21692100000001, 16.331594000000003],
          [-108.215848, 16.318269],
          [-108.211194, 16.306961],
          [-108.21515199999999, 16.301014000000002],
          [-108.284807, 16.270971000000003],
          [-108.312625, 16.263084],
          [-108.325572, 16.261823999999997],
          [-108.3621, 16.221849],
          [-108.339155, 16.21335],
          [-108.332652, 16.214],
          [-108.32120800000001, 16.218469],
          [-108.27575200000001, 16.249270000000003],
          [-108.21686299999999, 16.260005999999997],
          [-108.208328, 16.259341999999997],
          [-108.19200000000001, 16.252499999999998],
          [-108.188298, 16.248573999999998],
          [-108.19256899999999, 16.241826000000003],
          [-108.20077900000001, 16.236711],
          [-108.22893099999999, 16.235911],
          [-108.234227, 16.223689],
          [-108.19053, 16.206957000000003],
          [-108.165099, 16.206173],
          [-108.15894700000001, 16.211675],
          [-108.153775, 16.226236],
          [-108.15303700000001, 16.235528000000002],
          [-108.139869, 16.241081],
          [-108.090238, 16.215863],
          [-108.1173, 16.191335000000002],
          [-108.133624, 16.18839],
          [-108.13498999999999, 16.184596],
          [-108.130641, 16.169789],
          [-108.12629100000001, 16.167063],
          [-108.09087600000001, 16.168649000000002],
          [-108.08018799999999, 16.171568],
          [-108.07988399999999, 16.184233999999996],
          [-108.085791, 16.190041],
          [-108.082874, 16.192357],
          [-108.06186500000001, 16.200094],
          [-108.051918, 16.202061999999998],
          [-108.003433, 16.176167],
          [-108.00665599999999, 16.168461],
          [-108.018404, 16.157418999999997],
          [-108.02887000000001, 16.150517999999998],
          [-108.03249500000001, 16.148423],
          [-108.064401, 16.148508],
          [-108.09723, 16.12012],
          [-108.12179699999999, 16.073548000000002],
          [-108.095719, 16.065935000000003],
          [-108.079466, 16.073251],
          [-108.05343099999999, 16.090761999999998],
          [-108.040178, 16.112361],
          [-108.01227, 16.124733999999997],
          [-107.961701, 16.140900000000002],
          [-107.912824, 16.132576],
          [-107.89232000000001, 16.119515],
          [-107.887734, 16.109682999999997],
          [-107.89257599999999, 16.101160999999998],
          [-107.97311199999999, 16.064636],
          [-107.998659, 16.062305000000002],
          [-108.013551, 16.038159],
          [-108.02381700000001, 16.007108000000002],
          [-108.01603499999999, 15.999344],
          [-107.986164, 15.999344],
          [-107.92440099999999, 16.036230000000003],
          [-107.89222899999999, 16.063265],
          [-107.87967900000001, 16.069592999999998],
          [-107.86060699999999, 16.076355999999997],
          [-107.848072, 16.078961999999997],
          [-107.82204300000001, 16.066955],
          [-107.816451, 16.061824],
          [-107.815238, 16.058562000000002],
          [-107.82373799999999, 16.045035],
          [-107.86392900000001, 16.006619],
          [-107.91753800000001, 15.985996999999998],
          [-108.00582700000001, 15.961542000000001],
          [-108.03177, 15.955862000000003],
          [-108.036776, 15.956664000000004],
          [-108.05070599999999, 15.968907999999999],
          [-108.05014299999999, 15.976767000000002],
          [-108.05637300000001, 15.990423],
          [-108.069919, 15.989097999999998],
          [-108.08582000000001, 15.977511999999997],
          [-108.104818, 15.969237],
          [-108.135095, 15.959055],
          [-108.153742, 15.954642999999997],
          [-108.224838, 15.950195],
          [-108.25109900000001, 15.952416],
          [-108.252869, 15.955613999999997],
          [-108.21349699999999, 15.994643000000003],
          [-108.19384199999999, 15.998978999999999],
          [-108.17954499999999, 15.996653000000002],
          [-108.147614, 15.994951999999998],
          [-108.13229999999999, 15.997697000000002],
          [-108.128693, 16.003442],
          [-108.13891799999999, 16.012416],
          [-108.17580799999999, 16.026336999999998],
          [-108.21266800000001, 16.018101],
          [-108.273844, 16.013317999999998],
          [-108.31794099999999, 16.028731],
          [-108.305725, 16.054468],
          [-108.290367, 16.059995],
          [-108.29281599999999, 16.151289],
          [-108.305868, 16.161606999999997],
          [-108.31684899999999, 16.165436999999997],
          [-108.34653499999999, 16.162108000000003],
          [-108.35818800000001, 16.156509999999997],
          [-108.36910900000001, 16.120922],
          [-108.38232500000001, 16.063299],
          [-108.401204, 15.997599999999998],
          [-108.42910599999999, 15.969158],
          [-108.44558799999999, 15.963754999999999],
          [-108.47020499999999, 15.973708000000002],
          [-108.48017100000001, 15.979652999999999],
          [-108.482505, 15.983308000000001],
          [-108.47697400000001, 15.989514999999997],
          [-108.480436, 15.999394000000002],
          [-108.508825, 16.002875000000003],
          [-108.54413400000001, 16.002809],
          [-108.55629400000001, 15.984931000000003],
          [-108.555253, 15.980657],
          [-108.599121, 15.950888999999997],
          [-108.635842, 15.939661000000001],
          [-108.673822, 15.944828000000001],
          [-108.58364, 15.961477000000002],
          [-108.56236799999999, 16.422593],
          [-108.666606, 16.423056000000003],
          [-108.65544399999999, 16.734541999999998],
          [-108.745578, 16.733880999999997],
          [-108.74602300000001, 16.84614],
          [-108.47346099999999, 16.850630000000002],
          [-108.47958700000001, 17.165059],
          [-108.46000699999999, 17.426972],
          [-107.206028, 17.425265000000003],
          [-107.20570699999999, 17.475073000000002],
          [-106.94512399999999, 17.474348999999997],
          [-106.941834, 17.811906999999998],
          [-106.961638, 18.160210999999997],
          [-106.981731, 18.248680999999998],
          [-106.426334, 18.247221000000003],
          [-106.42864800000001, 18.863287999999997],
          [-106.48411899999999, 19.173325],
          [-106.32926, 19.190562],
          [-106.140827, 19.184013],
          [-106.111233, 19.221249999999998],
          [-105.14888099999999, 19.222211],
          [-105.14842200000001, 19.133567999999997],
          [-104.57542699999999, 19.133381],
          [-104.576439, 19.117871],
          [-103.876857, 19.113152999999997],
          [-103.12685400000001, 19.115887999999998],
          [-103.088616, 19.008567],
          [-103.00121200000001, 18.993786999999998],
          [-103.02748299999999, 18.935423999999998],
          [-103.196314, 18.826152],
          [-103.00772799999999, 18.766011],
          [-103.13845900000001, 18.691159],
          [-103.15919299999999, 18.636952],
          [-103.10475299999999, 18.613456],
          [-102.84215499999999, 18.597198],
          [-102.736106, 18.702022999999997],
          [-102.629088, 18.683656999999997],
          [-102.31453099999999, 18.683951999999998],
          [-102.32059799999999, 18.598671000000003],
          [-101.96370300000001, 18.510627999999997],
          [-101.97841700000001, 18.165011],
          [-102.017011, 18.127586],
          [-101.830194, 18.127580000000002],
          [-101.82864799999999, 17.90103],
          [-101.00201999999999, 17.901922],
          [-101.00184999999999, 16.391688000000002]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02270",
      state: "02",
      county: "270",
      name: "Wade Hampton County",
      lsad: "CA",
      censusArea: 17081.433,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.534142, 17.947257],
          [-120.46947700000001, 17.869348000000002],
          [-120.18665999999999, 17.831693],
          [-120.35014000000001, 17.819071],
          [-120.597733, 17.760156000000002],
          [-120.77524399999999, 17.738526999999998],
          [-120.95396099999999, 17.685668999999997],
          [-121.126812, 17.599539999999998],
          [-121.449611, 17.568838],
          [-121.697058, 17.530087],
          [-121.88528600000001, 17.48281],
          [-122.01343800000001, 17.477057000000002],
          [-122.328294, 17.518376000000004],
          [-122.476189, 17.503959000000002],
          [-122.505761, 17.556426000000002],
          [-122.75398000000001, 17.542665999999997],
          [-122.84719899999999, 17.498736],
          [-123.06199899999999, 17.479931],
          [-123.17135400000001, 17.449071000000004],
          [-123.43125800000001, 17.484107],
          [-123.53141199999999, 17.401454],
          [-123.54162299999999, 17.349559],
          [-123.99276, 17.371763],
          [-123.94742, 17.205621],
          [-124.12148199999999, 17.127000000000002],
          [-124.201874, 17.151612999999998],
          [-124.211781, 17.082262],
          [-124.15470199999999, 17.022624999999998],
          [-124.23498599999999, 16.988241000000002],
          [-124.529911, 16.940796],
          [-124.61658499999999, 16.979049000000003],
          [-124.797619, 16.982487],
          [-125.15711099999999, 16.917363],
          [-125.15523200000001, 16.929186],
          [-125.17246700000001, 16.940328],
          [-125.19494499999999, 16.9739],
          [-125.194964, 16.979915],
          [-125.190271, 16.983072999999997],
          [-125.133937, 17.011249999999997],
          [-125.115681, 17.016097000000002],
          [-125.09742499999999, 17.016658],
          [-125.09682799999999, 17.014944],
          [-125.083282, 17.012932999999997],
          [-125.020265, 17.011153],
          [-124.99817200000001, 17.013826],
          [-124.96152699999999, 17.024166],
          [-124.95110299999999, 17.031019999999998],
          [-124.95057299999999, 17.048079],
          [-124.92782500000001, 17.084392],
          [-124.902245, 17.077902],
          [-124.87045, 17.079563999999998],
          [-124.868009, 17.096393999999997],
          [-124.883441, 17.105924],
          [-124.89128600000001, 17.108246],
          [-124.94125299999999, 17.110863000000002],
          [-124.981718, 17.109690999999998],
          [-124.991273, 17.107232000000003],
          [-124.997636, 17.10443],
          [-124.998547, 17.079492000000002],
          [-124.99359899999999, 17.076241000000003],
          [-124.99122700000001, 17.072192],
          [-124.99569500000001, 17.058034999999997],
          [-125.011271, 17.051983999999997],
          [-125.029551, 17.054009999999998],
          [-125.057842, 17.059745999999997],
          [-125.11978099999999, 17.07864],
          [-125.16763599999999, 17.113501999999997],
          [-125.175321, 17.120925999999997],
          [-125.17711, 17.125494000000003],
          [-125.16707199999999, 17.133487000000002],
          [-125.16585699999999, 17.136567],
          [-125.16886, 17.144913000000003],
          [-125.17299399999999, 17.146918999999997],
          [-125.203757, 17.150340999999997],
          [-125.28970000000001, 17.181714],
          [-125.307976, 17.181823],
          [-125.32555199999999, 17.169306],
          [-125.344389, 17.123691],
          [-125.350154, 17.104545],
          [-125.350113, 17.097406999999997],
          [-125.347082, 17.084847000000003],
          [-125.342321, 17.079994],
          [-125.336996, 17.077709],
          [-125.33813599999999, 17.073431999999997],
          [-125.34344200000001, 17.070563999999997],
          [-125.370544, 17.066820999999997],
          [-125.403007, 17.067059999999998],
          [-125.498726, 17.079149],
          [-125.549613, 17.088161999999997],
          [-125.578127, 17.100361],
          [-125.59068199999999, 17.111168999999997],
          [-125.63199599999999, 17.220708000000002],
          [-125.634048, 17.237557000000002],
          [-125.62754899999999, 17.258125],
          [-125.620589, 17.268586],
          [-125.62331699999999, 17.278430999999998],
          [-125.63579100000001, 17.285456000000003],
          [-125.662892, 17.29457],
          [-125.787442, 17.310063],
          [-125.809373, 17.306827],
          [-125.81643399999999, 17.303362999999997],
          [-125.831365, 17.306719],
          [-125.858993, 17.318865000000002],
          [-125.87959900000001, 17.335044000000003],
          [-125.915445, 17.387686000000002],
          [-125.92119400000001, 17.403080000000003],
          [-125.92195000000001, 17.409638],
          [-125.918612, 17.419086999999998],
          [-125.844525, 17.440601],
          [-125.800525, 17.449657000000002],
          [-125.79108500000001, 17.449852],
          [-125.767226, 17.45695],
          [-125.748503, 17.476446000000003],
          [-125.746352, 17.489303999999997],
          [-125.75431699999999, 17.498703999999996],
          [-125.807627, 17.529170999999998],
          [-125.912496, 17.556199999999997],
          [-125.964035, 17.555919000000003],
          [-125.98187899999999, 17.551249],
          [-125.98594800000001, 17.54665],
          [-125.99953500000001, 17.539720000000003],
          [-126.03474800000001, 17.535221],
          [-126.075524, 17.532671999999998],
          [-126.08868000000001, 17.522885000000002],
          [-126.079983, 17.513464],
          [-126.058242, 17.500419],
          [-126.075398, 17.492980000000003],
          [-126.108269, 17.492475],
          [-126.124202, 17.504644999999996],
          [-126.158345, 17.541536999999998],
          [-126.165232, 17.550618],
          [-126.178627, 17.574807],
          [-126.18185, 17.581342],
          [-126.18268800000001, 17.588481],
          [-126.158976, 17.700437],
          [-126.153178, 17.714931],
          [-126.143757, 17.724352000000003],
          [-126.134285, 17.723919000000002],
          [-126.13301999999999, 17.721918000000002],
          [-126.134402, 17.709068000000002],
          [-126.13868400000001, 17.667101000000002],
          [-126.14013299999999, 17.639561999999998],
          [-126.139409, 17.632315],
          [-126.132162, 17.631590000000003],
          [-126.05398299999999, 17.638201000000002],
          [-126.031834, 17.641199],
          [-126.01513399999999, 17.645865999999998],
          [-125.967894, 17.654432],
          [-125.903783, 17.663632],
          [-125.82213999999999, 17.670610000000003],
          [-125.809933, 17.673029],
          [-125.81, 17.68936],
          [-125.85679099999999, 17.690734],
          [-125.934968, 17.706299],
          [-125.993851, 17.723104999999997],
          [-126.00669300000001, 17.729878999999997],
          [-126.09208100000001, 17.800733],
          [-126.094045, 17.805296],
          [-126.094312, 17.813859],
          [-126.08533399999999, 17.816498000000003],
          [-125.955265, 17.832408],
          [-125.870982, 17.826013000000003],
          [-125.75841299999999, 17.825443999999997],
          [-125.74708999999999, 17.82772],
          [-125.73690400000001, 17.832901],
          [-125.73642899999999, 17.839188],
          [-125.73043899999999, 17.842075],
          [-125.69603799999999, 17.847054999999997],
          [-125.64021600000001, 17.848041000000002],
          [-125.608427, 17.855891999999997],
          [-125.600043, 17.859662999999998],
          [-125.612337, 17.871907],
          [-125.66793899999999, 17.900275],
          [-125.70348200000001, 17.921571999999998],
          [-125.725818, 17.947184],
          [-125.741481, 17.971392],
          [-125.75680600000001, 18.006337000000002],
          [-125.75638599999999, 18.014032],
          [-125.74864099999999, 18.047145],
          [-125.74352200000001, 18.06228],
          [-125.734117, 18.076873],
          [-125.706155, 18.108365],
          [-125.67203699999999, 18.13989],
          [-125.620746, 18.172615999999998],
          [-125.50032200000001, 18.255451],
          [-125.37371300000001, 18.338196000000003],
          [-125.33772200000001, 18.359031],
          [-125.31196700000001, 18.378812000000003],
          [-125.294962, 18.403353000000003],
          [-125.26927, 18.427352],
          [-125.199804, 18.469637],
          [-125.09615500000001, 18.522452],
          [-125.04604499999999, 18.540419999999997],
          [-124.86805899999999, 18.571142000000002],
          [-124.770232, 18.592081999999998],
          [-124.777244, 18.609083],
          [-124.796056, 18.611486],
          [-124.84284600000001, 18.604504],
          [-124.89374000000001, 18.599671],
          [-124.914064, 18.60253],
          [-124.92881299999999, 18.608992],
          [-124.935992, 18.613456],
          [-124.94046399999999, 18.618431],
          [-124.942068, 18.624955999999997],
          [-124.93214499999999, 18.651071],
          [-124.91291000000001, 18.657496000000002],
          [-124.86589599999999, 18.645817],
          [-124.85710900000001, 18.635513000000003],
          [-124.83733699999999, 18.631445999999997],
          [-124.81711000000001, 18.636696999999998],
          [-124.857182, 18.76883],
          [-124.86364, 18.772620000000003],
          [-124.870348, 18.774135],
          [-124.87729999999999, 18.78432],
          [-124.87777299999999, 18.797773999999997],
          [-124.87564, 18.806254000000003],
          [-124.85083800000001, 18.839509999999997],
          [-124.836318, 18.852168],
          [-124.795229, 18.858486],
          [-124.76825299999999, 18.871330999999998],
          [-124.751982, 18.893169],
          [-124.742134, 18.920144],
          [-124.74551399999999, 18.944865],
          [-124.765769, 18.954907],
          [-124.76611700000001, 18.958228],
          [-124.71684099999999, 19.006264],
          [-124.685213, 19.022191],
          [-124.58373499999999, 19.058456999999997],
          [-124.58020099999999, 19.070127],
          [-124.611616, 19.077672999999997],
          [-124.641186, 19.07268],
          [-124.64367200000001, 19.074975000000002],
          [-124.64488600000001, 19.079268],
          [-124.64032399999999, 19.091257],
          [-124.63394299999999, 19.09782],
          [-124.49311800000001, 19.17767],
          [-124.423449, 19.211976999999997],
          [-124.36359200000001, 19.226280000000003],
          [-124.209475, 19.251472],
          [-124.140096, 19.259335999999998],
          [-124.066991, 19.262276],
          [-124.036565, 19.261204],
          [-123.97026600000001, 19.248291000000002],
          [-123.90940499999999, 19.232514000000002],
          [-123.88505900000001, 19.222307999999998],
          [-123.788882, 19.217481999999997],
          [-123.75528299999999, 19.217461],
          [-123.73265, 19.213257],
          [-123.72580500000001, 19.21062],
          [-123.724072, 19.206592],
          [-123.70398, 19.188107000000002],
          [-123.650294, 19.157564],
          [-123.61627200000001, 19.141213],
          [-123.59012200000001, 19.146091],
          [-123.52993799999999, 19.135399999999997],
          [-123.520806, 19.12328],
          [-123.507217, 19.113684999999997],
          [-123.474794, 19.099052999999998],
          [-123.433968, 19.089295999999997],
          [-123.41768300000001, 19.083874],
          [-123.364979, 19.055805],
          [-123.316203, 19.037762999999998],
          [-123.130853, 19.049387000000003],
          [-123.05399600000001, 19.058334000000002],
          [-123.04050000000001, 19.062151],
          [-122.998302, 19.089286],
          [-122.919727, 19.120153000000002],
          [-122.901643, 19.125597],
          [-122.844559, 19.154190999999997],
          [-122.83785, 19.159224000000002],
          [-122.834926, 19.164620999999997],
          [-122.84018699999999, 19.187579],
          [-122.839167, 19.193004000000002],
          [-122.83435399999999, 19.198076],
          [-122.821122, 19.205596],
          [-122.76953599999999, 19.217069000000002],
          [-122.75874099999999, 19.217187000000003],
          [-122.74762100000001, 19.213572],
          [-122.72407999999999, 19.214615000000002],
          [-122.688083, 19.220608],
          [-122.66261399999999, 19.229906],
          [-122.58775399999999, 19.275727000000003],
          [-122.37771000000001, 19.272891],
          [-122.374087, 19.186422999999998],
          [-122.184665, 19.186448],
          [-122.177514, 19.096466999999997],
          [-121.99003200000001, 19.096024],
          [-121.990906, 19.012675],
          [-120.851144, 19.012689],
          [-120.941899, 18.998883],
          [-120.942287, 18.898826999999997],
          [-120.99324100000001, 18.897962],
          [-121.00039699999999, 18.551764],
          [-121.04527999999999, 18.550449],
          [-121.047821, 18.206673000000002],
          [-120.910169, 18.204383],
          [-120.913973, 17.949378000000003],
          [-120.534142, 17.947257]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02070",
      state: "02",
      county: "070",
      name: "Dillingham County",
      lsad: "CA",
      censusArea: 18568.778,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.96109999999999, 16.907601999999997],
          [-115.95362399999999, 16.106237999999998],
          [-115.95585, 15.677304999999997],
          [-116.12205, 15.675184999999999],
          [-116.12330700000001, 15.588205000000002],
          [-116.464798, 15.589388],
          [-116.46489600000001, 15.502988000000002],
          [-116.634489, 15.503988],
          [-116.635005, 15.415107999999996],
          [-116.73118400000001, 15.416170000000001],
          [-116.72937300000001, 15.328803],
          [-117.07325599999999, 15.331538000000002],
          [-117.074194, 15.244404000000003],
          [-117.24415099999999, 15.246208000000003],
          [-117.158008, 14.860227000000002],
          [-117.11686599999999, 14.867533000000002],
          [-117.18955399999999, 14.847724],
          [-117.200998, 14.845011999999997],
          [-117.21571, 14.841526000000002],
          [-117.24139600000001, 14.837558000000001],
          [-117.25966299999999, 14.835664999999999],
          [-117.275451, 14.836136000000003],
          [-117.35313199999999, 14.817729],
          [-117.429531, 14.791071000000002],
          [-117.484062, 14.785961999999998],
          [-117.53265400000001, 14.772638],
          [-117.537543, 14.768541999999997],
          [-117.542326, 14.760962],
          [-117.550603, 14.754514],
          [-117.696472, 14.729975000000003],
          [-117.72178600000001, 14.723211999999997],
          [-117.75358299999999, 14.711924000000003],
          [-117.799597, 14.695590000000003],
          [-117.85539600000001, 14.678277000000001],
          [-118.00101599999999, 14.642893],
          [-118.03659300000001, 14.634248],
          [-118.10164599999999, 14.621090000000002],
          [-118.140307, 14.615020000000001],
          [-118.190283, 14.613709999999998],
          [-118.21386100000001, 14.615828],
          [-118.23227600000001, 14.619902000000003],
          [-118.27303599999999, 14.633470000000003],
          [-118.297189, 14.643146999999999],
          [-118.32703799999999, 14.659835000000001],
          [-118.33209299999999, 14.665312999999998],
          [-118.33286000000001, 14.669274000000001],
          [-118.33021600000001, 14.675043000000002],
          [-118.332394, 14.686813999999998],
          [-118.343545, 14.713633999999999],
          [-118.351481, 14.727693000000002],
          [-118.37687299999999, 14.748043000000003],
          [-118.400475, 14.761181999999998],
          [-118.42382799999999, 14.769846999999999],
          [-118.45521, 14.776972],
          [-118.51254700000001, 14.78311],
          [-118.53851599999999, 14.788393999999997],
          [-118.550626, 14.792915],
          [-118.564833, 14.802715],
          [-118.566397, 14.807136999999997],
          [-118.56586999999999, 14.815429000000002],
          [-118.55949899999999, 14.841819000000001],
          [-118.559132, 14.860776000000001],
          [-118.565718, 14.875827999999998],
          [-118.598645, 14.901229999999998],
          [-118.619684, 14.911048000000001],
          [-118.71743599999999, 14.872461999999999],
          [-118.729581, 14.871217999999999],
          [-118.745305, 14.874097999999996],
          [-118.76774800000001, 14.864263999999999],
          [-118.78963200000001, 14.814256999999998],
          [-118.790786, 14.808424000000002],
          [-118.790378, 14.804712000000002],
          [-118.782365, 14.791156999999998],
          [-118.77462600000001, 14.778593],
          [-118.7698, 14.774141],
          [-118.77124599999999, 14.765109000000002],
          [-118.784886, 14.747739000000003],
          [-118.800959, 14.732841999999998],
          [-118.812116, 14.727845000000002],
          [-118.82710499999999, 14.724494999999997],
          [-118.84822500000001, 14.722735999999998],
          [-118.86120700000001, 14.69558],
          [-118.82785200000001, 14.626432000000001],
          [-118.76913099999999, 14.548650000000002],
          [-118.721173, 14.497971],
          [-118.70405199999999, 14.482759000000001],
          [-118.79531600000001, 14.408031999999999],
          [-118.83059800000001, 14.397095],
          [-118.88092700000001, 14.39067],
          [-118.89606699999999, 14.390065],
          [-118.944154, 14.396884999999997],
          [-119.04610500000001, 14.417465999999997],
          [-119.063346, 14.423138999999999],
          [-119.08049600000001, 14.444256000000003],
          [-119.18734699999999, 14.555608999999997],
          [-119.24229, 14.619067000000001],
          [-119.35762500000001, 14.734520000000003],
          [-119.39066399999999, 14.762362000000003],
          [-119.450831, 14.797736],
          [-119.501768, 14.824303999999998],
          [-119.53234699999999, 14.833609000000003],
          [-119.556355, 14.837414000000003],
          [-119.580287, 14.840691],
          [-119.64354900000001, 14.845063000000003],
          [-119.601899, 14.884670999999997],
          [-119.589811, 14.890358999999997],
          [-119.58696599999999, 14.900314000000002],
          [-119.594788, 14.912402],
          [-119.60261, 14.920935],
          [-119.61612, 14.931601],
          [-119.64242999999999, 14.938712000000002],
          [-119.657362, 14.938712000000002],
          [-119.69149300000001, 14.931601],
          [-119.71211400000001, 14.929468],
          [-119.723491, 14.932312000000003],
          [-119.732932, 14.930739000000003],
          [-119.75375399999999, 14.855724000000002],
          [-119.80877699999999, 14.861542],
          [-119.792923, 14.823971],
          [-119.80630500000001, 14.805594999999997],
          [-119.90838600000001, 14.779902999999997],
          [-119.995667, 14.848301],
          [-119.985352, 14.870463999999998],
          [-120.054047, 14.887000999999998],
          [-120.093109, 14.860798000000003],
          [-120.15052800000001, 14.866062],
          [-120.15448, 14.916874],
          [-120.232788, 14.901127000000002],
          [-120.28634600000001, 14.945006999999997],
          [-120.322922, 14.953952999999998],
          [-120.25659200000001, 14.994480000000003],
          [-120.31778, 15.070476999999997],
          [-120.47657799999999, 15.026046999999998],
          [-120.641785, 14.964489],
          [-120.75306699999999, 14.910431000000003],
          [-120.83535800000001, 14.823493999999997],
          [-120.85519400000001, 14.882522999999999],
          [-120.999741, 14.856655000000003],
          [-121.03451999999999, 14.843893999999999],
          [-121.034851, 15.003419000000001],
          [-120.87429900000001, 15.003658000000001],
          [-120.875869, 15.084046],
          [-120.81913700000001, 15.084859000000002],
          [-120.820877, 15.261519],
          [-120.650275, 15.262230000000002],
          [-120.65079499999999, 15.349181999999999],
          [-120.480547, 15.352217000000003],
          [-120.48171099999999, 15.436228],
          [-120.42497700000001, 15.43591],
          [-120.42445000000001, 15.611507000000003],
          [-120.250662, 15.611297999999998],
          [-120.24818200000001, 15.784523999999998],
          [-120.01942500000001, 15.786831],
          [-120.01522600000001, 15.958911999999998],
          [-119.855289, 15.963690999999997],
          [-119.85029399999999, 16.128439],
          [-119.78689, 16.128138999999997],
          [-119.78453999999999, 16.213265],
          [-119.613392, 16.214585],
          [-119.61361099999999, 16.3878],
          [-119.437007, 16.387175999999997],
          [-119.432729, 16.471029],
          [-119.369629, 16.471130000000002],
          [-119.36724899999999, 16.644339000000002],
          [-119.18516299999999, 16.644578000000003],
          [-119.18299300000001, 16.731079],
          [-119.01592, 16.731938],
          [-119.01409100000001, 16.823452000000003],
          [-118.94893099999999, 16.823175],
          [-118.94465600000001, 16.903461999999998],
          [-117.88355200000001, 16.903711],
          [-117.88074499999999, 16.824769000000003],
          [-117.69790499999999, 16.823585],
          [-117.70086900000001, 16.911315000000002],
          [-117.521885, 16.911337000000003],
          [-117.52253099999999, 17.000515],
          [-117.169511, 17.000169999999997],
          [-117.16828799999999, 16.912221000000002],
          [-115.96109999999999, 16.907601999999997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02110",
      state: "02",
      county: "110",
      name: "Juneau County",
      lsad: "Cty&Bor",
      censusArea: 2701.931,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.713987, 14.220748],
          [-94.71280100000001, 14.215369000000003],
          [-94.71051299999999, 14.192557],
          [-94.70372699999999, 14.166794000000003],
          [-94.69995599999999, 14.161493999999998],
          [-94.689515, 14.158825],
          [-94.68281200000001, 14.158842999999997],
          [-94.63143400000001, 14.162197999999997],
          [-94.608911, 14.171636999999997],
          [-94.60835800000001, 14.173471999999997],
          [-94.59480400000001, 14.183511000000003],
          [-94.55924099999999, 14.195121],
          [-94.541609, 14.184327000000003],
          [-94.519644, 14.175770999999997],
          [-94.50074000000001, 14.172545999999997],
          [-94.47980999999999, 14.171199000000001],
          [-94.46263300000001, 14.173850999999999],
          [-94.44665699999999, 14.173583],
          [-94.41395299999999, 14.167546000000002],
          [-94.401512, 14.163426999999999],
          [-94.371445, 14.148966000000001],
          [-94.32736, 14.143880000000003],
          [-94.317037, 14.14544],
          [-94.30648299999999, 14.15249],
          [-94.259705, 14.157711999999997],
          [-94.215981, 14.162128000000003],
          [-94.17746700000001, 14.159640000000003],
          [-94.531804, 14.096725999999997],
          [-94.553923, 14.053615999999998],
          [-94.67270199999999, 14.027242999999999],
          [-94.76194000000001, 14.078090000000003],
          [-94.77271999999999, 14.164862],
          [-94.713987, 14.220748]
        ],
        [
          [-93.60203200000001, 13.860394],
          [-93.61017799999999, 13.860653999999997],
          [-93.631303, 13.846766000000002],
          [-93.63393099999999, 13.838028999999999],
          [-93.641996, 13.811214999999997],
          [-93.63889900000001, 13.803322999999999],
          [-93.63557800000001, 13.799900000000001],
          [-93.637054, 13.792203],
          [-93.63967500000001, 13.790360999999997],
          [-93.65811299999999, 13.786368000000003],
          [-93.67743300000001, 13.786593000000003],
          [-93.69678400000001, 13.795074999999997],
          [-93.70309700000001, 13.792152000000002],
          [-93.70914099999999, 13.792738999999997],
          [-93.814706, 13.832979000000002],
          [-93.99933100000001, 13.999015999999997],
          [-94.05752100000001, 14.039470999999999],
          [-94.07172, 14.054636000000002],
          [-94.08920699999999, 14.082534000000003],
          [-94.144813, 14.171242],
          [-94.58710099999999, 14.205801999999998],
          [-94.703351, 14.276536999999998],
          [-94.83930699999999, 14.359260999999996],
          [-95.074096, 14.502122999999997],
          [-95.196338, 14.907874999999997],
          [-95.21912800000001, 14.974972000000001],
          [-95.17576, 14.973866999999998],
          [-94.36377300000001, 14.963890999999997],
          [-94.32896400000001, 14.919592999999999],
          [-94.25052600000001, 14.858046000000002],
          [-93.99208100000001, 14.774580999999998],
          [-93.84039200000001, 14.727991000000003],
          [-93.723635, 14.626004000000002],
          [-93.69983500000001, 14.607289999999999],
          [-93.559942, 14.522317999999999],
          [-93.44337300000001, 14.442414999999997],
          [-93.43607499999999, 14.391323999999997],
          [-93.43769599999999, 14.358074000000002],
          [-93.359691, 14.284789000000004],
          [-93.343725, 14.270915000000002],
          [-93.22289799999999, 14.186368000000002],
          [-93.176444, 14.150151000000001],
          [-93.56921, 13.885350000000003],
          [-93.60203200000001, 13.860394]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02130",
      state: "02",
      county: "130",
      name: "Ketchikan Gateway County",
      lsad: "Borough",
      censusArea: 4858.414,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.24601799999999, 10.989555000000003],
          [-91.257421, 10.979010000000002],
          [-91.242771, 10.929639000000002],
          [-91.233001, 10.926814],
          [-91.217805, 10.927422999999997],
          [-91.197924, 10.921489000000001],
          [-91.19519700000001, 10.919767],
          [-91.19541100000001, 10.918249000000003],
          [-91.20016100000001, 10.910169000000003],
          [-91.253671, 10.866779000000001],
          [-91.26604900000001, 10.859369000000001],
          [-91.35323299999999, 10.859009],
          [-91.469097, 10.913153000000001],
          [-91.49150399999999, 10.930391999999998],
          [-91.486616, 10.950394000000003],
          [-91.482676, 10.952658999999997],
          [-91.409738, 10.971151999999996],
          [-91.266084, 10.998806000000002],
          [-91.248909, 10.997190000000003],
          [-91.24601799999999, 10.989555000000003]
        ],
        [
          [-91.759896, 11.381844999999998],
          [-91.71614, 11.34957],
          [-91.685192, 11.333500999999998],
          [-91.66673800000001, 11.327492],
          [-91.64723599999999, 11.30614],
          [-91.65813, 11.292512000000002],
          [-91.68864400000001, 11.282113000000003],
          [-91.69448700000001, 11.223739000000002],
          [-91.718468, 11.200099000000002],
          [-91.748334, 11.128588],
          [-91.79894300000001, 11.162351000000001],
          [-91.82958500000001, 11.191915999999999],
          [-91.83071799999999, 11.194991000000002],
          [-91.828395, 11.198481999999998],
          [-91.85083900000001, 11.274363999999998],
          [-91.86216200000001, 11.289284000000002],
          [-91.87056799999999, 11.364553],
          [-91.854297, 11.421073999999997],
          [-91.85233, 11.423782000000003],
          [-91.83321799999999, 11.422013999999997],
          [-91.81169700000001, 11.414048000000001],
          [-91.809721, 11.412554999999998],
          [-91.80679, 11.405175],
          [-91.798555, 11.399386],
          [-91.759896, 11.381844999999998]
        ],
        [
          [-92.19954899999999, 11.638593],
          [-92.09117800000001, 11.660702999999998],
          [-92.05882500000001, 11.710929999999998],
          [-91.96274399999999, 11.700757000000003],
          [-91.93563499999999, 11.798431],
          [-92.01103699999999, 11.854788],
          [-91.871938, 11.948883000000002],
          [-91.90461300000001, 11.972971999999999],
          [-91.82960800000001, 12.056306999999997],
          [-91.69269299999999, 12.050604],
          [-91.653403, 12.103850999999999],
          [-91.498849, 12.019097000000002],
          [-91.37185299999999, 12.013252000000001],
          [-91.369226, 12.06991],
          [-91.437095, 12.140003999999998],
          [-91.25335799999999, 12.206850000000003],
          [-91.23856599999999, 12.170968000000002],
          [-91.10669899999999, 12.195124],
          [-90.99865399999999, 12.274040999999997],
          [-91.09622999999999, 12.335166000000001],
          [-91.087433, 12.407420000000002],
          [-91.08570399999999, 12.40654],
          [-90.782231, 12.367511],
          [-90.74061900000001, 12.342953000000001],
          [-90.62248199999999, 12.267938999999998],
          [-90.54117299999999, 12.248016999999997],
          [-90.46687399999999, 12.239789000000002],
          [-90.42557500000001, 12.140676],
          [-90.24554, 12.096876000000002],
          [-90.29111700000001, 12.054246],
          [-90.22006400000001, 11.998911999999997],
          [-90.285132, 11.997546],
          [-90.38873699999999, 11.943317999999998],
          [-90.23464899999999, 11.814309999999999],
          [-90.15059500000001, 11.767031000000003],
          [-90.151509, 11.746029],
          [-90.150061, 11.727099000000003],
          [-90.14804000000001, 11.715041],
          [-90.12951799999999, 11.699806000000002],
          [-90.11167699999999, 11.682051000000001],
          [-90.126743, 11.581282000000002],
          [-90.12013200000001, 11.563918999999999],
          [-90.08541299999999, 11.491517000000002],
          [-90.04430300000001, 11.451970000000003],
          [-90.030182, 11.367696000000002],
          [-90.02355800000001, 11.338259],
          [-89.982348, 11.302078999999999],
          [-89.98048700000001, 11.296334000000002],
          [-89.98005800000001, 11.28423],
          [-89.985379, 11.27776],
          [-90.001735, 11.264557000000003],
          [-90.03016199999999, 11.246592],
          [-90.09654599999999, 11.197952999999998],
          [-90.118919, 11.176074],
          [-90.144723, 11.146037999999997],
          [-90.158117, 11.117103999999998],
          [-90.18754100000001, 11.064664999999998],
          [-90.22151199999999, 11.02599],
          [-90.25907900000001, 10.987642000000001],
          [-90.27556000000001, 10.972929999999998],
          [-90.339504, 10.921376000000002],
          [-90.474605, 10.838102],
          [-90.569366, 10.790869],
          [-90.63674499999999, 10.778455999999998],
          [-90.65775400000001, 10.761828000000001],
          [-90.62807000000001, 10.739341000000003],
          [-90.64447899999999, 10.736896999999999],
          [-90.685213, 10.720090999999996],
          [-90.686192, 10.716909999999999],
          [-90.695817, 10.719346000000002],
          [-90.737423, 10.753545000000003],
          [-90.74722700000001, 10.772599999999997],
          [-90.73320899999999, 10.779609999999998],
          [-90.732201, 10.782620000000001],
          [-90.73629500000001, 10.794798],
          [-90.74231599999999, 10.801913999999996],
          [-90.773606, 10.820844999999998],
          [-90.787444, 10.822904999999999],
          [-90.78856999999999, 10.794643],
          [-90.792122, 10.784784000000002],
          [-90.806815, 10.776862000000001],
          [-90.83685299999999, 10.765436999999999],
          [-90.85496599999999, 10.766340999999997],
          [-90.86686599999999, 10.769067999999997],
          [-90.901801, 10.780876],
          [-90.91593599999999, 10.789617],
          [-90.932454, 10.806938000000002],
          [-90.94709800000001, 10.826047000000003],
          [-90.94102899999999, 10.841586999999997],
          [-90.947338, 10.886733],
          [-90.959732, 10.918678],
          [-90.96039999999999, 10.933684999999997],
          [-90.949104, 10.967846000000002],
          [-90.953507, 10.972102],
          [-90.97503, 10.974853000000003],
          [-91.00778700000001, 10.991300000000003],
          [-91.01206099999999, 10.996237999999998],
          [-91.00421600000001, 11.029604999999997],
          [-90.98680200000001, 11.065221999999999],
          [-90.98373000000001, 11.068945999999997],
          [-90.98415700000001, 11.084409999999998],
          [-91.013215, 11.090069],
          [-91.029676, 11.099477999999998],
          [-91.05229800000001, 11.118160000000003],
          [-91.07069200000001, 11.138143],
          [-91.07664600000001, 11.146177999999999],
          [-91.085579, 11.158233000000003],
          [-91.08749700000001, 11.163035999999998],
          [-91.093806, 11.191335000000002],
          [-91.09260499999999, 11.192711000000003],
          [-90.98530400000001, 11.247286000000003],
          [-90.952956, 11.273091999999998],
          [-90.951572, 11.291648000000002],
          [-90.92506900000001, 11.300713000000002],
          [-90.90994800000001, 11.299878],
          [-90.871329, 11.293779999999998],
          [-90.86491799999999, 11.298366999999999],
          [-90.86491799999999, 11.309469],
          [-90.871857, 11.313991000000001],
          [-90.882146, 11.358831000000002],
          [-90.92080000000001, 11.428721000000003],
          [-90.92298500000001, 11.435113000000001],
          [-90.92029500000001, 11.446084999999997],
          [-90.910744, 11.459981999999997],
          [-90.89812900000001, 11.470177],
          [-90.88129699999999, 11.495581999999999],
          [-90.87052399999999, 11.533768000000002],
          [-90.88001299999999, 11.598953999999999],
          [-90.901872, 11.697380000000003],
          [-90.939017, 11.754831000000003],
          [-90.98477399999999, 11.799349],
          [-91.09395599999999, 11.895674999999997],
          [-91.17140599999999, 11.942951999999998],
          [-91.18742900000001, 11.95601],
          [-91.216475, 11.984341999999998],
          [-91.229971, 11.984341999999998],
          [-91.23693599999999, 11.982292999999999],
          [-91.243491, 11.973689],
          [-91.245949, 11.965905],
          [-91.241704, 11.955069000000002],
          [-91.156834, 11.901147000000002],
          [-91.07013799999999, 11.828550999999997],
          [-91.05321699999999, 11.799843000000003],
          [-91.04352700000001, 11.766997000000003],
          [-91.040966, 11.762836999999998],
          [-90.998638, 11.723537999999998],
          [-90.965994, 11.688974000000002],
          [-90.94683, 11.650716000000003],
          [-90.927651, 11.576585000000001],
          [-90.945177, 11.557730999999997],
          [-90.978917, 11.550835],
          [-90.98710299999999, 11.539872000000003],
          [-90.99437599999999, 11.472396000000003],
          [-90.96958799999999, 11.393281000000002],
          [-90.947498, 11.380823],
          [-90.92817199999999, 11.339426000000003],
          [-90.93339900000001, 11.331905999999996],
          [-90.946597, 11.322395999999998],
          [-90.95977199999999, 11.315891999999998],
          [-90.96832599999999, 11.316626],
          [-90.964088, 11.332664000000001],
          [-91.000594, 11.398012000000001],
          [-91.008726, 11.404817999999999],
          [-91.029045, 11.408394999999999],
          [-91.03305399999999, 11.393118000000001],
          [-91.03419099999999, 11.379358000000003],
          [-91.030521, 11.376916999999999],
          [-91.027301, 11.371392],
          [-91.019881, 11.347904999999997],
          [-91.03135700000001, 11.284785],
          [-91.072348, 11.253822],
          [-91.160492, 11.197481000000003],
          [-91.188747, 11.192745000000002],
          [-91.21123, 11.192379000000003],
          [-91.23551599999999, 11.197574000000003],
          [-91.26308900000001, 11.208317999999998],
          [-91.29716199999999, 11.235045999999997],
          [-91.302697, 11.250217],
          [-91.278302, 11.260319000000003],
          [-91.24019000000001, 11.287156000000003],
          [-91.23043200000001, 11.297801999999997],
          [-91.191595, 11.360526999999998],
          [-91.191933, 11.368333999999997],
          [-91.19748899999999, 11.391050999999997],
          [-91.20247699999999, 11.392834],
          [-91.272447, 11.387774],
          [-91.292102, 11.383946000000002],
          [-91.29304300000001, 11.378684],
          [-91.287016, 11.358260000000001],
          [-91.26460800000001, 11.345638999999998],
          [-91.25446099999999, 11.329698],
          [-91.25510700000001, 11.322104000000003],
          [-91.284986, 11.286437],
          [-91.291203, 11.281751],
          [-91.326989, 11.265911000000003],
          [-91.402931, 11.238064999999999],
          [-91.42450199999999, 11.238764000000003],
          [-91.428234, 11.239415999999999],
          [-91.43785700000001, 11.248407],
          [-91.44479899999999, 11.264491],
          [-91.46353199999999, 11.283389],
          [-91.47560200000001, 11.303263000000001],
          [-91.46296799999999, 11.312648000000003],
          [-91.47197600000001, 11.323386],
          [-91.494146, 11.33231],
          [-91.50981100000001, 11.33231],
          [-91.51525699999999, 11.327938000000003],
          [-91.528201, 11.295349000000002],
          [-91.53650999999999, 11.292352000000001],
          [-91.55004400000001, 11.293388999999998],
          [-91.566677, 11.306068000000003],
          [-91.58484200000001, 11.309587999999998],
          [-91.63903099999999, 11.339481],
          [-91.69874300000001, 11.354872999999998],
          [-91.732441, 11.377552999999999],
          [-91.735939, 11.381905000000003],
          [-91.73665399999999, 11.392206000000002],
          [-91.74183400000001, 11.398074000000001],
          [-91.82844600000001, 11.445214],
          [-91.844157, 11.456741999999998],
          [-91.845542, 11.522119000000004],
          [-91.84168299999999, 11.526747999999998],
          [-91.79371699999999, 11.541682000000002],
          [-91.76637299999999, 11.54005],
          [-91.73305199999999, 11.548558999999997],
          [-91.66462899999999, 11.581525],
          [-91.65417199999999, 11.592430999999998],
          [-91.671471, 11.606572999999997],
          [-91.682849, 11.610487999999997],
          [-91.70109099999999, 11.613684],
          [-91.72435899999999, 11.632559],
          [-91.72632200000001, 11.635930000000002],
          [-91.72661500000001, 11.640999999999998],
          [-91.71954600000001, 11.650281999999997],
          [-91.71210199999999, 11.665796999999998],
          [-91.70114699999999, 11.696959999999997],
          [-91.70674399999999, 11.706434999999999],
          [-91.72646700000001, 11.720826000000002],
          [-91.733408, 11.730832],
          [-91.71930800000001, 11.749099000000001],
          [-91.70490699999999, 11.755541000000001],
          [-91.64962600000001, 11.768728000000003],
          [-91.64029400000001, 11.785274000000001],
          [-91.640141, 11.789355],
          [-91.65312399999999, 11.795735],
          [-91.678213, 11.799836999999997],
          [-91.691058, 11.797561000000002],
          [-91.69721100000001, 11.793768],
          [-91.700951, 11.788977000000003],
          [-91.70525900000001, 11.789938999999997],
          [-91.71044800000001, 11.806620000000002],
          [-91.713742, 11.853262999999998],
          [-91.70148699999999, 11.860942999999999],
          [-91.689044, 11.875487999999997],
          [-91.68731700000001, 11.886772],
          [-91.68950899999999, 11.890231999999997],
          [-91.720384, 11.894658999999997],
          [-91.776737, 11.878784000000003],
          [-91.82817600000001, 11.877284000000003],
          [-91.836962, 11.875472000000002],
          [-91.830547, 11.856616000000002],
          [-91.81630999999999, 11.837449],
          [-91.77703299999999, 11.823261000000002],
          [-91.77124800000001, 11.810028000000003],
          [-91.779908, 11.791904000000002],
          [-91.81475900000001, 11.731349999999999],
          [-91.82615999999999, 11.718580000000003],
          [-91.83140700000001, 11.681342],
          [-91.82888700000001, 11.667147999999997],
          [-91.865395, 11.630679999999998],
          [-91.897413, 11.603914000000003],
          [-91.93931799999999, 11.623843999999998],
          [-91.963121, 11.615262999999999],
          [-91.96264199999999, 11.608708],
          [-91.945303, 11.572440999999998],
          [-91.936689, 11.535150999999999],
          [-91.971792, 11.498278999999997],
          [-91.986493, 11.500619],
          [-92.014613, 11.515237999999997],
          [-92.04377199999999, 11.535741999999999],
          [-92.06050400000001, 11.543030000000002],
          [-92.098521, 11.550015000000002],
          [-92.114654, 11.550623000000002],
          [-92.141118, 11.55901],
          [-92.148383, 11.562480999999998],
          [-92.18320700000001, 11.588127999999998],
          [-92.19865200000001, 11.615721],
          [-92.20150000000001, 11.626376],
          [-92.197869, 11.633966999999998],
          [-92.19954899999999, 11.638593]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02185",
      state: "02",
      county: "185",
      name: "North Slope County",
      lsad: "Borough",
      censusArea: 88695.413,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-125.35998000000001, 24.028053999999997],
          [-125.430442, 24.045407999999995],
          [-125.49355, 24.059282999999994],
          [-125.66169, 24.075998999999996],
          [-125.749518, 24.077693999999994],
          [-125.792146, 24.080866999999998],
          [-125.863428, 24.093367],
          [-125.96553399999999, 24.121920000000003],
          [-125.97362100000001, 24.125985999999997],
          [-125.98792900000001, 24.142737999999994],
          [-125.99437699999999, 24.147305000000003],
          [-126.245281, 24.245275000000007],
          [-126.307977, 24.264948000000004],
          [-126.36854600000001, 24.281769999999995],
          [-126.47460799999999, 24.305273],
          [-126.56721999999999, 24.323288000000005],
          [-126.68084200000001, 24.340911000000006],
          [-126.78457800000001, 24.340430999999995],
          [-126.829715, 24.336324000000005],
          [-126.84645599999999, 24.332508000000004],
          [-126.85064, 24.333089],
          [-126.838178, 24.339714],
          [-126.811836, 24.348135999999997],
          [-126.70613900000001, 24.371782999999994],
          [-126.377564, 24.422405999999995],
          [-126.362135, 24.426240000000007],
          [-126.34238099999999, 24.433965999999998],
          [-126.32845900000001, 24.442261000000002],
          [-126.305962, 24.46154],
          [-126.30346399999999, 24.464682999999994],
          [-126.30203, 24.470412999999994],
          [-126.295343, 24.510900000000007],
          [-126.24449000000001, 24.553888],
          [-126.23378, 24.564262999999997],
          [-126.226111, 24.576186000000007],
          [-126.22556700000001, 24.579015],
          [-126.23143200000001, 24.587338000000003],
          [-126.229761, 24.613771],
          [-126.21363500000001, 24.664323999999993],
          [-126.199826, 24.678556],
          [-126.19736499999999, 24.690019000000007],
          [-126.187795, 24.778706],
          [-126.19020900000001, 24.790436999999997],
          [-126.19537399999999, 24.80399],
          [-126.20375000000001, 24.818220999999994],
          [-126.222496, 24.860440999999994],
          [-126.224187, 24.873175000000003],
          [-126.21443300000001, 24.879524000000004],
          [-125.81493800000001, 24.864158000000003],
          [-125.66656599999999, 24.855386999999993],
          [-125.572483, 24.852946000000003],
          [-125.522358, 24.855839000000003],
          [-125.327043, 24.858110999999994],
          [-124.96754200000001, 24.883030000000005],
          [-124.883745, 24.891649],
          [-124.812671, 24.893541999999997],
          [-124.655317, 24.909360000000007],
          [-124.52688699999999, 24.917908999999995],
          [-124.299092, 24.927569000000005],
          [-124.25315699999999, 24.930937999999998],
          [-124.161249, 24.944772999999998],
          [-124.130742, 24.951001000000005],
          [-124.06936200000001, 24.969651],
          [-123.973678, 24.985044000000002],
          [-123.893881, 25.011961999999997],
          [-123.858069, 25.028859999999995],
          [-123.827447, 25.040632000000002],
          [-123.72418400000001, 25.066712999999993],
          [-123.65586400000001, 25.090566999999993],
          [-123.57403400000001, 25.124077],
          [-123.535314, 25.141655999999998],
          [-123.452685, 25.194630000000004],
          [-123.297956, 25.274725000000004],
          [-123.236121, 25.282661000000004],
          [-123.23090199999999, 25.284464],
          [-123.13761400000001, 25.352177999999995],
          [-123.110318, 25.375343],
          [-123.10316599999999, 25.392261000000005],
          [-123.104387, 25.401349999999994],
          [-123.10056900000001, 25.414221999999995],
          [-123.07034100000001, 25.459872000000004],
          [-123.05206799999999, 25.481971],
          [-123.04696100000001, 25.482892000000007],
          [-123.03631100000001, 25.489028000000005],
          [-123.02617000000001, 25.50689],
          [-123.016456, 25.538141999999993],
          [-123.02000100000001, 25.545145000000005],
          [-123.03029000000001, 25.556590999999997],
          [-123.074128, 25.570272000000003],
          [-123.118176, 25.589156000000003],
          [-123.116622, 25.593416000000005],
          [-123.111605, 25.596604999999997],
          [-123.02458999999999, 25.608609],
          [-122.916958, 25.692511999999994],
          [-122.922009, 25.700372],
          [-122.961086, 25.717164999999994],
          [-123.01054500000001, 25.728109000000003],
          [-123.01817500000001, 25.729073999999997],
          [-123.03539, 25.727406000000002],
          [-123.012595, 25.757462000000004],
          [-122.96024499999999, 25.783327999999997],
          [-122.911869, 25.799470999999997],
          [-122.87716499999999, 25.804411],
          [-122.84060199999999, 25.811763],
          [-122.76721, 25.852179000000007],
          [-122.70955000000001, 25.879126],
          [-122.616345, 25.916996999999995],
          [-122.60629700000001, 25.918988],
          [-122.60128399999999, 25.914568000000003],
          [-122.593773, 25.914096],
          [-122.587906, 25.915637000000004],
          [-122.48101600000001, 25.975241999999994],
          [-122.47154900000001, 25.983131999999998],
          [-122.46230399999999, 26.002437999999998],
          [-122.46833899999999, 26.015783999999996],
          [-122.46277799999999, 26.042216999999994],
          [-122.454541, 26.043958000000003],
          [-122.394531, 26.044573999999997],
          [-122.350558, 26.058800000000005],
          [-122.35473999999999, 26.065478999999996],
          [-122.356469, 26.076391],
          [-122.312491, 26.109280999999996],
          [-122.15815599999999, 26.16153],
          [-122.098377, 26.187044999999998],
          [-122.01926499999999, 26.224044000000006],
          [-121.98488800000001, 26.247681],
          [-121.95960299999999, 26.268873],
          [-121.92294899999999, 26.291599000000005],
          [-121.859745, 26.308048],
          [-121.849998, 26.309430000000006],
          [-121.844213, 26.308530000000005],
          [-121.842162, 26.304429],
          [-121.84468799999999, 26.300054000000003],
          [-121.84740300000001, 26.295877000000004],
          [-121.84890899999999, 26.282183000000003],
          [-121.83465100000001, 26.272503999999998],
          [-121.801603, 26.260633999999996],
          [-121.77979400000001, 26.255410999999995],
          [-121.769496, 26.262497999999994],
          [-121.76783800000001, 26.268337000000002],
          [-121.759176, 26.272442999999996],
          [-121.692195, 26.267092000000005],
          [-121.67622, 26.258021],
          [-121.66359299999999, 26.246187000000006],
          [-121.63388800000001, 26.240692999999993],
          [-121.522941, 26.236887999999993],
          [-121.39675700000001, 26.240606],
          [-121.30911800000001, 26.248091000000002],
          [-121.25472300000001, 26.256612000000004],
          [-121.08028200000001, 26.306679000000003],
          [-121.01641599999999, 26.327743999999996],
          [-120.992764, 26.316226],
          [-120.97912600000001, 26.317661],
          [-120.83953600000001, 26.344533999999996],
          [-120.73270299999999, 26.374381999999997],
          [-120.530362, 26.440751000000006],
          [-120.489778, 26.454463000000004],
          [-120.48553000000001, 26.457121],
          [-120.480062, 26.465970999999996],
          [-120.21482800000001, 26.559087000000005],
          [-120.056727, 26.632834000000003],
          [-119.913805, 26.690673000000004],
          [-119.79851400000001, 26.731226000000007],
          [-119.648383, 26.794368000000006],
          [-119.585714, 26.809475000000006],
          [-119.528682, 26.820848999999995],
          [-119.209082, 26.870067000000006],
          [-119.17181, 26.875102999999996],
          [-119.14763400000001, 26.876653000000005],
          [-119.156511, 26.859221000000005],
          [-119.152026, 26.849542999999997],
          [-119.13248300000001, 26.828359000000006],
          [-119.16083599999999, 26.81796],
          [-119.29057700000001, 26.811262],
          [-119.33102099999999, 26.807394000000002],
          [-119.335837, 26.800078999999997],
          [-119.343075, 26.783114999999995],
          [-119.299304, 26.760012000000003],
          [-119.275634, 26.759530999999996],
          [-119.13779, 26.758609000000007],
          [-119.000676, 26.764336],
          [-118.97664700000001, 26.766972999999993],
          [-118.95457099999999, 26.772712],
          [-118.9656, 26.786851999999996],
          [-118.97645600000001, 26.789863999999994],
          [-118.97661500000001, 26.796377000000007],
          [-118.656101, 26.787954999999997],
          [-118.60731999999999, 26.789098999999993],
          [-118.3895, 26.799729],
          [-118.38579200000001, 26.811468000000005],
          [-118.38926900000001, 26.822047999999995],
          [-118.385816, 26.825704],
          [-118.25031999999999, 26.817734],
          [-118.157725, 26.820806000000005],
          [-118.032397, 26.832262999999998],
          [-117.884086, 26.853468000000007],
          [-117.84099699999999, 26.861024999999998],
          [-117.768452, 26.875842000000006],
          [-117.70878200000001, 26.89139],
          [-117.50245899999999, 26.948659000000006],
          [-117.42100099999999, 26.976805],
          [-117.39280199999999, 26.987908000000004],
          [-117.24908300000001, 27.052537],
          [-117.119621, 27.128681999999998],
          [-117.072487, 27.154521000000003],
          [-116.96255500000001, 27.211884999999995],
          [-116.809653, 27.286885999999996],
          [-116.77393699999999, 27.299505999999994],
          [-116.64561499999999, 27.338012000000006],
          [-116.62013999999999, 27.344209000000006],
          [-116.56864999999999, 27.352560999999994],
          [-116.566383, 27.334016000000005],
          [-116.561512, 27.316809000000006],
          [-116.55649600000001, 27.311795000000004],
          [-116.531124, 27.296338000000006],
          [-116.52449899999999, 27.294469000000007],
          [-116.402659, 27.267944999999997],
          [-116.35673600000001, 27.261273000000003],
          [-116.32070200000001, 27.258951999999994],
          [-116.301938, 27.260565999999997],
          [-116.2142, 27.259392000000005],
          [-116.074411, 27.242489000000006],
          [-116.02920499999999, 27.203209],
          [-116.038116, 27.196506],
          [-116.04475400000001, 27.186769999999996],
          [-116.044615, 27.184701000000004],
          [-116.018574, 27.172040999999993],
          [-115.95796100000001, 27.186211],
          [-115.92020199999999, 27.207156999999995],
          [-115.895105, 27.193899000000002],
          [-115.88417999999999, 27.190056999999996],
          [-115.82903400000001, 27.192088],
          [-115.803853, 27.196420000000003],
          [-115.76080200000001, 27.194661999999994],
          [-115.65717799999999, 27.182471000000007],
          [-115.58770200000001, 27.172560000000004],
          [-115.566925, 27.165138999999996],
          [-115.56776500000001, 27.141130000000004],
          [-115.56177199999999, 27.128457999999995],
          [-115.520737, 27.102475999999996],
          [-115.51398699999999, 27.096794000000003],
          [-115.51112499999999, 27.090348000000006],
          [-115.510637, 27.081152000000003],
          [-115.53334699999999, 27.067683000000002],
          [-115.548283, 27.060685000000007],
          [-115.638994, 27.042360000000002],
          [-115.705487, 27.020152999999993],
          [-115.711852, 27.012473],
          [-115.762068, 26.985643999999994],
          [-115.830881, 26.965584000000007],
          [-115.87894600000001, 26.967684000000006],
          [-115.95205000000001, 26.964831000000004],
          [-115.97840500000001, 26.962197000000003],
          [-115.99568099999999, 26.947795999999997],
          [-116.014769, 26.903132999999997],
          [-116.01442499999999, 26.898644000000004],
          [-116.01351199999999, 26.895983],
          [-115.968559, 26.862931000000003],
          [-115.96919399999999, 26.827982000000006],
          [-115.978421, 26.825558],
          [-115.980975, 26.817355000000006],
          [-115.97897800000001, 26.808750000000003],
          [-115.971935, 26.806827999999996],
          [-115.92795799999999, 26.80601],
          [-115.90661499999999, 26.809988000000004],
          [-115.88214500000001, 26.822056000000003],
          [-115.87509600000001, 26.828895000000003],
          [-115.731842, 26.831159999999997],
          [-115.643516, 26.824208999999996],
          [-115.54303100000001, 26.847174999999993],
          [-115.50420199999999, 26.860303000000002],
          [-115.489811, 26.871740000000003],
          [-115.48798, 26.875299],
          [-115.485915, 26.885904999999994],
          [-115.487574, 26.902679000000006],
          [-115.493044, 26.917371000000003],
          [-115.47594000000001, 26.943546999999995],
          [-115.454991, 26.947490000000002],
          [-115.404225, 26.967477000000002],
          [-115.382646, 26.978972999999996],
          [-115.36416, 26.994195000000005],
          [-115.34387100000001, 27.004448999999994],
          [-115.260042, 27.015226999999996],
          [-115.260084, 27.011280999999997],
          [-115.25617700000001, 27.004762],
          [-115.211434, 26.978022999999993],
          [-115.20146600000001, 26.974306],
          [-115.19224600000001, 26.974056000000004],
          [-115.18277900000001, 26.978218],
          [-115.16893400000001, 26.987947000000005],
          [-115.161735, 26.995715000000004],
          [-115.159922, 27.002775],
          [-115.163938, 27.013801],
          [-115.17699999999999, 27.02745],
          [-115.273764, 27.064728000000002],
          [-115.275814, 27.067042],
          [-115.27598900000001, 27.070464],
          [-115.26260199999999, 27.079149],
          [-115.25685999999999, 27.081119],
          [-115.15052399999999, 27.112049999999996],
          [-115.14694800000001, 27.110958999999994],
          [-115.146931, 27.103459],
          [-115.14285799999999, 27.097254000000007],
          [-115.12599399999999, 27.077495],
          [-115.120317, 27.073415999999995],
          [-115.108509, 27.070475000000002],
          [-115.07536200000001, 27.072041999999996],
          [-115.06400400000001, 27.083911999999998],
          [-115.061428, 27.091999],
          [-115.064558, 27.108006000000003],
          [-115.085782, 27.127572],
          [-115.060764, 27.145421999999996],
          [-115.03174000000001, 27.146473],
          [-114.94286399999999, 27.126264000000006],
          [-114.777335, 27.083230999999998],
          [-114.61605, 27.026182000000006],
          [-114.604413, 27.021501999999998],
          [-114.581129, 27.007321000000005],
          [-114.56759299999999, 26.989929000000004],
          [-114.59404799999999, 26.976992999999993],
          [-114.608294, 26.961715999999996],
          [-114.65437499999999, 26.903318],
          [-114.645793, 26.869167000000004],
          [-114.57738599999999, 26.835335],
          [-114.51903999999999, 26.822799000000003],
          [-114.501866, 26.821765],
          [-114.48570000000001, 26.825304000000003],
          [-114.430229, 26.831258000000005],
          [-114.35260400000001, 26.834828],
          [-114.26079899999999, 26.815163999999996],
          [-114.22862699999999, 26.802417000000005],
          [-114.223307, 26.795230000000004],
          [-114.23916600000001, 26.776866],
          [-114.18186299999999, 26.768325000000004],
          [-114.16963100000001, 26.768603999999996],
          [-114.127487, 26.778132999999997],
          [-114.06998200000001, 26.793702999999994],
          [-113.99557899999999, 26.821876000000003],
          [-113.97601399999999, 26.833924999999994],
          [-113.95137, 26.854028],
          [-113.93545, 26.869727999999995],
          [-113.93294900000001, 26.874201],
          [-113.93435099999999, 26.876609000000002],
          [-113.932921, 26.878676999999996],
          [-113.89048, 26.885718999999995],
          [-113.774169, 26.894583999999995],
          [-113.747253, 26.895016999999996],
          [-113.52597600000001, 26.885499999999993],
          [-113.48598899999999, 26.885873000000004],
          [-113.426265, 26.890130999999997],
          [-113.35911200000001, 26.898291999999998],
          [-113.326202, 26.904111],
          [-113.253386, 26.920775000000006],
          [-113.23848000000001, 26.922466999999997],
          [-113.13731100000001, 26.925438],
          [-113.049207, 26.913101999999995],
          [-113.01703800000001, 26.904004],
          [-112.774415, 26.885278999999997],
          [-112.735892, 26.884545000000003],
          [-112.590148, 26.886933],
          [-112.45695, 26.871787999999995],
          [-112.25996599999999, 26.842820000000003],
          [-112.22646399999999, 26.831042999999994],
          [-112.187197, 26.801546000000002],
          [-112.188649, 26.798140000000004],
          [-112.19246000000001, 26.795294],
          [-112.23934399999999, 26.793415999999993],
          [-112.26334600000001, 26.790777000000006],
          [-112.283763, 26.785600000000002],
          [-112.37080800000001, 26.730068000000003],
          [-112.377274, 26.714681999999996],
          [-112.471531, 26.68884],
          [-112.47334799999999, 26.683668999999995],
          [-112.46050500000001, 26.646107],
          [-112.43378100000001, 26.616926000000007],
          [-112.42077499999999, 26.608982999999995],
          [-112.365736, 26.601242],
          [-112.35567900000001, 26.603793999999994],
          [-112.34159199999999, 26.612193000000005],
          [-112.332608, 26.612871],
          [-112.264049, 26.592654999999993],
          [-112.20064400000001, 26.586070000000007],
          [-112.16994399999999, 26.585218999999995],
          [-112.146165, 26.586754],
          [-112.06068400000001, 26.574934999999996],
          [-112.064546, 26.568541999999994],
          [-112.06574800000001, 26.563074],
          [-112.02607, 26.559344999999993],
          [-111.975785, 26.563215],
          [-111.88014100000001, 26.554868999999997],
          [-111.816701, 26.545698],
          [-111.77470299999999, 26.547925000000006],
          [-111.745047, 26.554040999999998],
          [-111.71821499999999, 26.555285999999995],
          [-111.70146700000001, 26.553219999999996],
          [-111.69516200000001, 26.549674999999993],
          [-111.697258, 26.547741000000002],
          [-111.709462, 26.546490000000006],
          [-111.722711, 26.541607999999997],
          [-111.75155799999999, 26.524105000000006],
          [-111.76024799999999, 26.516711],
          [-111.734287, 26.503491999999994],
          [-111.728579, 26.495374999999996],
          [-111.77553700000001, 26.485353000000003],
          [-111.82411099999999, 26.484010999999995],
          [-111.91920999999999, 26.472685999999996],
          [-111.93678299999999, 26.463564000000005],
          [-111.946384, 26.452523],
          [-111.90003300000001, 26.434134999999998],
          [-111.87612200000001, 26.430761000000004],
          [-111.84437500000001, 26.434959000000006],
          [-111.78565699999999, 26.436935000000005],
          [-111.65318400000001, 26.434799999999996],
          [-111.605581, 26.437331999999998],
          [-111.55464699999999, 26.435895000000002],
          [-111.444897, 26.425404999999998],
          [-111.34320199999999, 26.408877000000004],
          [-111.297598, 26.400747999999993],
          [-111.229919, 26.37984],
          [-111.18739400000001, 26.384775000000005],
          [-111.188592, 26.401754999999994],
          [-111.18651600000001, 26.418208000000007],
          [-111.18043599999999, 26.430401000000003],
          [-111.12310500000001, 26.439374],
          [-111.11860100000001, 26.438087999999993],
          [-111.114352, 26.432885999999996],
          [-111.11609899999999, 26.422403000000003],
          [-111.06043, 26.418734],
          [-111.02633700000001, 26.441455000000005],
          [-110.95781299999999, 26.452610000000007],
          [-110.895452, 26.458157999999997],
          [-110.87732199999999, 26.455181999999994],
          [-110.83497299999999, 26.460171000000003],
          [-110.787069, 26.477117000000007],
          [-110.78002900000001, 26.485985999999997],
          [-110.762035, 26.497219],
          [-110.71984499999999, 26.494997999999995],
          [-110.651175, 26.494928],
          [-110.614734, 26.498292000000006],
          [-110.429915, 26.498171999999997],
          [-110.357384, 26.493737999999993],
          [-110.35405600000001, 26.492723999999995],
          [-110.338851, 26.471075],
          [-110.370283, 26.447857999999997],
          [-110.35054099999999, 26.435997999999998],
          [-110.296287, 26.441136],
          [-110.24532500000001, 26.441658000000004],
          [-110.185078, 26.435370000000006],
          [-110.112899, 26.431371999999996],
          [-110.104388, 26.432091],
          [-110.07446100000001, 26.439333000000005],
          [-109.86669800000001, 26.510768999999996],
          [-109.81974, 26.491428],
          [-109.810924, 26.490477],
          [-109.790427, 26.491190000000003],
          [-109.74018799999999, 26.498150999999993],
          [-109.72824700000001, 26.502792999999997],
          [-109.71607499999999, 26.504968000000005],
          [-109.66116500000001, 26.509203],
          [-109.65680599999999, 26.508713],
          [-109.64955599999999, 26.504756999999998],
          [-109.58134799999999, 26.495891],
          [-109.565278, 26.496449999999996],
          [-109.536891, 26.499397000000002],
          [-109.524235, 26.502128],
          [-109.50985399999999, 26.508746000000002],
          [-109.46175500000001, 26.518271],
          [-109.432083, 26.503749999999997],
          [-109.41872699999999, 26.492256999999995],
          [-109.400623, 26.489931],
          [-109.31447299999999, 26.495324999999994],
          [-109.179148, 26.485699999999994],
          [-109.082079, 26.464894],
          [-108.959443, 26.423944000000006],
          [-108.928979, 26.426834999999997],
          [-108.858069, 26.422916999999998],
          [-108.78957700000001, 26.402745999999993],
          [-108.728082, 26.413034999999994],
          [-108.69877, 26.425877999999997],
          [-108.69676799999999, 26.429159999999996],
          [-108.66701699999999, 26.430083999999994],
          [-108.610566, 26.422588000000005],
          [-108.59000700000001, 26.416956],
          [-108.580356, 26.412546000000006],
          [-108.477044, 26.359067999999994],
          [-108.46454299999999, 26.340159],
          [-108.47702799999999, 26.320871999999994],
          [-108.46615, 26.313609],
          [-108.450639, 26.308436999999998],
          [-108.41125299999999, 26.302627],
          [-108.36319599999999, 26.302627],
          [-108.351437, 26.304452999999995],
          [-108.2698, 26.329617],
          [-108.25819999999999, 26.336996],
          [-108.20347699999999, 26.348187999999993],
          [-108.15267600000001, 26.347148000000004],
          [-108.10723100000001, 26.342800999999994],
          [-108.09057999999999, 26.337432000000007],
          [-108.089576, 26.335423000000006],
          [-108.089676, 26.332812000000004],
          [-108.08917400000001, 26.331005000000005],
          [-108.076865, 26.327510000000004],
          [-107.9615, 26.314200999999997],
          [-107.863719, 26.293317000000002],
          [-107.81763699999999, 26.276938],
          [-107.789357, 26.247972000000004],
          [-107.76510400000001, 26.219806000000005],
          [-107.68172200000001, 26.199954000000005],
          [-107.648, 26.203299],
          [-107.585678, 26.203398000000007],
          [-107.50527, 26.200384],
          [-107.431532, 26.188826000000006],
          [-107.40228300000001, 26.185272999999995],
          [-107.38527099999999, 26.185255999999995],
          [-107.350145, 26.187683000000007],
          [-107.24411900000001, 26.218963000000002],
          [-107.233327, 26.207553000000004],
          [-107.231008, 26.195397999999997],
          [-107.21921, 26.178826],
          [-107.18212299999999, 26.160349999999994],
          [-107.16160099999999, 26.155612000000005],
          [-106.991109, 26.14761],
          [-106.97321199999999, 26.151857000000007],
          [-106.909516, 26.173259],
          [-106.885771, 26.185917000000003],
          [-106.73402100000001, 26.175475000000006],
          [-106.713053, 26.175398],
          [-106.624761, 26.182398000000006],
          [-106.50813299999999, 26.186043999999995],
          [-106.44886, 26.183397999999997],
          [-106.413197, 26.178250000000006],
          [-106.335147, 26.176235000000005],
          [-106.30955800000001, 26.178906999999995],
          [-106.272965, 26.176944000000006],
          [-106.172672, 26.165893999999994],
          [-106.129579, 26.158947999999995],
          [-106.114124, 26.154956],
          [-106.09682699999999, 26.145151],
          [-106.00641100000001, 26.140401999999995],
          [-105.955164, 26.140198999999996],
          [-105.917674, 26.142525000000006],
          [-105.87292299999999, 26.148829000000006],
          [-105.858297, 26.165996000000007],
          [-105.84268900000001, 26.164102],
          [-105.79038600000001, 26.148568999999995],
          [-105.78332700000001, 26.139454],
          [-105.76709199999999, 26.128716999999995],
          [-105.76044300000001, 26.126113000000004],
          [-105.62330600000001, 26.084374999999994],
          [-105.579972, 26.076803999999996],
          [-105.52238399999999, 26.077465000000004],
          [-105.50568200000001, 26.074528],
          [-105.46950799999999, 26.059135999999995],
          [-105.46885599999999, 26.048336000000006],
          [-105.43483, 26.036994000000007],
          [-105.40818200000001, 26.031571999999997],
          [-105.33155300000001, 26.022595999999993],
          [-105.24716699999999, 26.017891000000006],
          [-105.218593, 26.007279999999994],
          [-105.19733099999999, 25.994954000000007],
          [-105.175073, 25.991707000000005],
          [-105.01171099999999, 25.981144],
          [-104.99013099999999, 25.977680000000007],
          [-104.96676099999999, 25.964361999999994],
          [-104.95363699999999, 25.959261999999995],
          [-104.90230399999999, 25.964510000000004],
          [-104.86762300000001, 25.972266000000005],
          [-104.863111, 25.973523999999998],
          [-104.856954, 25.985986999999994],
          [-104.85453899999999, 25.986363999999995],
          [-104.79261399999999, 25.979795999999993],
          [-104.73897600000001, 25.970112],
          [-104.672305, 25.966876],
          [-104.618671, 25.969314999999995],
          [-104.58917199999999, 25.977610999999996],
          [-104.512258, 26.004478000000006],
          [-104.46328600000001, 26.025734999999997],
          [-104.34407300000001, 26.034374],
          [-104.32839100000001, 26.032555000000002],
          [-104.23105100000001, 26.035911999999996],
          [-104.17819399999999, 26.041742],
          [-104.13028299999999, 26.057951000000003],
          [-104.12264099999999, 26.059138000000004],
          [-104.079634, 26.058960999999996],
          [-104.053709, 26.073182000000003],
          [-103.911494, 26.129836999999995],
          [-103.887688, 26.130736],
          [-103.839879, 26.125827],
          [-103.78221300000001, 26.124668],
          [-103.769015, 26.135065999999995],
          [-103.75306499999999, 26.137242],
          [-103.66225, 26.142516999999998],
          [-103.61740699999999, 26.139915000000002],
          [-103.595181, 26.142521000000002],
          [-103.597879, 26.147204000000002],
          [-103.59381300000001, 26.152603999999997],
          [-103.574986, 26.154597999999993],
          [-103.54323, 26.149742000000003],
          [-103.49805799999999, 26.140100000000004],
          [-103.49798200000001, 26.136875000000003],
          [-103.511617, 26.125191],
          [-103.516098, 26.116361999999995],
          [-103.517248, 26.104293],
          [-103.51008100000001, 26.096435999999997],
          [-103.503487, 26.093458],
          [-103.455354, 26.092934],
          [-103.35796099999999, 26.094970000000004],
          [-103.327114, 26.103127],
          [-103.31790000000001, 26.111144999999993],
          [-103.26589200000001, 26.119286000000002],
          [-103.25557599999999, 26.119330000000005],
          [-103.20014699999999, 26.110322999999994],
          [-103.159929, 26.099203000000003],
          [-103.140019, 26.092996999999997],
          [-103.11295100000001, 26.078271],
          [-103.05129099999999, 26.078187999999997],
          [-103.03809999999999, 26.093888000000007],
          [-102.93955500000001, 26.074380000000005],
          [-102.74680699999999, 26.042530999999997],
          [-102.49803600000001, 25.973611000000005],
          [-102.45292699999999, 25.958124999999995],
          [-102.40996200000001, 25.933645999999996],
          [-102.40436600000001, 25.916511],
          [-102.272156, 25.907044],
          [-102.23987299999999, 25.896597999999997],
          [-102.11271400000001, 25.862161999999998],
          [-102.081696, 25.855497999999997],
          [-102.075612, 25.853319],
          [-102.07306299999999, 25.846625000000003],
          [-102.05873399999999, 25.83533],
          [-102.009321, 25.800725999999997],
          [-101.96075300000001, 25.799875999999998],
          [-101.937176, 25.804805],
          [-101.919828, 25.811307999999997],
          [-101.866738, 25.813005000000004],
          [-101.84284299999999, 25.811926999999997],
          [-101.713369, 25.789496999999997],
          [-101.60622900000001, 25.761695000000003],
          [-101.528197, 25.736024999999998],
          [-101.43083999999999, 25.695144],
          [-101.43487200000001, 25.675245000000004],
          [-101.428856, 25.662657999999993],
          [-101.394082, 25.640845999999996],
          [-101.37771799999999, 25.634631],
          [-101.28084899999999, 25.631024999999994],
          [-101.258558, 25.632247000000007],
          [-101.254547, 25.637052999999995],
          [-101.243946, 25.652482000000006],
          [-101.210456, 25.68419],
          [-101.11923300000001, 25.673527000000007],
          [-101.00267199999999, 25.645608999999993],
          [-101.00269399999999, 24.498390999999998],
          [-102.360513, 24.489642000000003],
          [-104.29705200000001, 24.507296999999994],
          [-105.99821600000001, 24.489870999999994],
          [-105.994865, 24.001284999999996],
          [-107.389781, 24.001041],
          [-109.62064799999999, 23.998940000000005],
          [-111.606277, 24.006861999999998],
          [-115.29802000000001, 24.001771000000005],
          [-115.299012, 24.029940999999994],
          [-116.235534, 24.028751],
          [-116.23810700000001, 24.130761000000007],
          [-117.15029899999999, 24.120780999999994],
          [-117.155836, 24.210442],
          [-118.10010299999999, 24.217806999999993],
          [-118.27472700000001, 24.211915000000005],
          [-119.716147, 24.216796000000002],
          [-119.722546, 24.284088999999994],
          [-120.127816, 24.287307999999996],
          [-120.11833999999999, 24.220066000000003],
          [-121.08953500000001, 24.221231000000003],
          [-121.095913, 24.289367999999996],
          [-121.51996399999999, 24.289553999999995],
          [-121.516254, 24.220811999999995],
          [-121.99314799999999, 24.223274000000004],
          [-121.995486, 24.292164],
          [-122.720616, 24.301027000000005],
          [-122.719806, 24.232729000000006],
          [-123.04523, 24.228720999999993],
          [-124.49987100000001, 24.227513000000002],
          [-124.494202, 24.024330000000006],
          [-125.35998000000001, 24.028053999999997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02195",
      state: "02",
      county: "195",
      name: "Petersburg County",
      lsad: "CA",
      censusArea: 3281.982,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.283312, 11.925175000000003],
          [-94.265872, 11.917371000000003],
          [-94.230613, 11.905628999999998],
          [-94.173104, 11.918519000000003],
          [-94.15221600000001, 11.920915999999998],
          [-94.122839, 11.918653999999997],
          [-94.11806200000001, 11.914642],
          [-94.12552099999999, 11.902095000000003],
          [-94.13054399999999, 11.897511999999999],
          [-94.13664700000001, 11.895392999999999],
          [-94.145803, 11.896712999999998],
          [-94.168363, 11.892319],
          [-94.20825099999999, 11.876708999999998],
          [-94.231157, 11.864747000000001],
          [-94.24053699999999, 11.853921999999997],
          [-94.273156, 11.833058000000001],
          [-94.282453, 11.828667000000003],
          [-94.327238, 11.836440000000003],
          [-94.34441799999999, 11.843198000000001],
          [-94.348153, 11.892817],
          [-94.336063, 11.926614],
          [-94.31578200000001, 11.923003000000001],
          [-94.283312, 11.925175000000003]
        ],
        [
          [-94.12151399999999, 12.069847000000003],
          [-94.10721799999999, 12.048155999999999],
          [-94.118132, 12.016922000000001],
          [-94.126903, 12.003591999999998],
          [-94.13090700000001, 12.000104],
          [-94.13418100000001, 12.000971],
          [-94.156721, 12.021079999999998],
          [-94.171731, 12.040227000000002],
          [-94.19086300000001, 12.056539999999998],
          [-94.224073, 12.065223000000003],
          [-94.230449, 12.068340999999997],
          [-94.25991300000001, 12.121274999999997],
          [-94.26221699999999, 12.133222000000004],
          [-94.23826399999999, 12.140863000000003],
          [-94.22794200000001, 12.162835000000001],
          [-94.25747799999999, 12.221411000000003],
          [-94.246307, 12.267767999999997],
          [-94.281387, 12.286217999999998],
          [-94.295634, 12.289228000000001],
          [-94.298292, 12.291992999999998],
          [-94.29235299999999, 12.352643999999998],
          [-94.23813899999999, 12.382806000000002],
          [-94.19392199999999, 12.409906],
          [-94.190494, 12.429749000000001],
          [-94.196394, 12.439799999999998],
          [-94.21096800000001, 12.450498000000003],
          [-94.236978, 12.452362999999998],
          [-94.24662699999999, 12.457135000000001],
          [-94.24617900000001, 12.463510999999997],
          [-94.24466699999999, 12.4651],
          [-94.222814, 12.475402000000003],
          [-94.200909, 12.463296999999997],
          [-94.186476, 12.450570999999997],
          [-94.179214, 12.440305000000002],
          [-94.14613600000001, 12.411068999999998],
          [-94.067466, 12.390987000000003],
          [-94.04622699999999, 12.402275000000003],
          [-94.04270399999999, 12.407696999999999],
          [-94.04093800000001, 12.421529999999997],
          [-94.08960400000001, 12.472582000000003],
          [-94.133892, 12.487825],
          [-94.252386, 12.560861000000003],
          [-94.281093, 12.585555],
          [-94.276735, 12.620289999999997],
          [-94.30986899999999, 12.650509],
          [-94.369035, 12.678021999999999],
          [-94.401407, 12.725419000000002],
          [-94.419791, 12.838385000000002],
          [-94.41893999999999, 12.846524000000002],
          [-94.411471, 12.855837000000001],
          [-94.37697499999999, 12.880054999999999],
          [-94.339168, 12.901829999999997],
          [-94.316868, 12.909090999999997],
          [-94.294362, 12.908714000000003],
          [-94.28681800000001, 12.906534],
          [-94.19094999999999, 12.861674999999998],
          [-94.187063, 12.856713999999997],
          [-94.171675, 12.851218000000003],
          [-94.14716000000001, 12.859357000000003],
          [-94.141933, 12.874187999999997],
          [-94.150935, 12.884957999999997],
          [-94.18138300000001, 12.892572000000001],
          [-94.220002, 12.905084000000002],
          [-94.273113, 12.933822999999997],
          [-94.27166399999999, 12.935927999999997],
          [-94.249327, 12.937373999999998],
          [-94.223939, 12.932552999999999],
          [-94.178507, 12.914017999999999],
          [-94.158108, 12.915028999999997],
          [-94.13623999999999, 12.928842000000003],
          [-94.14123599999999, 12.942081000000002],
          [-94.14138500000001, 12.950887000000002],
          [-94.126562, 12.947381],
          [-94.083291, 12.931579],
          [-94.03514000000001, 12.894509999999997],
          [-93.97225700000001, 12.826934000000001],
          [-93.946767, 12.798924],
          [-93.925648, 12.758912000000002],
          [-93.94466299999999, 12.736432999999998],
          [-93.941801, 12.733493000000003],
          [-93.907795, 12.72184],
          [-93.88078100000001, 12.721395999999999],
          [-93.86933400000001, 12.734081000000003],
          [-93.86298099999999, 12.755650000000003],
          [-93.86505600000001, 12.773240000000001],
          [-93.867616, 12.782024999999997],
          [-93.87225000000001, 12.786186],
          [-93.877395, 12.789192],
          [-93.88475500000001, 12.788656000000003],
          [-93.890005, 12.792468],
          [-93.89386099999999, 12.802526],
          [-93.890805, 12.80798],
          [-93.86147199999999, 12.808836999999997],
          [-93.83792500000001, 12.801788000000002],
          [-93.818194, 12.791452999999997],
          [-93.76777999999999, 12.780468999999997],
          [-93.748129, 12.773919],
          [-93.74337600000001, 12.762290999999998],
          [-93.744002, 12.749772],
          [-93.766143, 12.728886000000003],
          [-93.766143, 12.718241999999996],
          [-93.76204899999999, 12.696134999999998],
          [-93.76277999999999, 12.687179999999998],
          [-93.75965, 12.675913000000001],
          [-93.74567300000001, 12.674028],
          [-93.728479, 12.642915000000002],
          [-93.72772800000001, 12.630845999999998],
          [-93.71333100000001, 12.598298],
          [-93.71333100000001, 12.592664],
          [-93.723142, 12.583962],
          [-93.731617, 12.572304000000003],
          [-93.848207, 12.573056999999999],
          [-93.895746, 12.511217000000002],
          [-93.89858899999999, 12.489877],
          [-93.88108, 12.480803000000002],
          [-93.86862500000001, 12.470919000000002],
          [-93.83911699999999, 12.434885000000001],
          [-93.839238, 12.432018],
          [-93.859104, 12.430348000000002],
          [-93.869729, 12.431795999999999],
          [-93.89304000000001, 12.427363999999997],
          [-93.93351200000001, 12.375428],
          [-93.89435800000001, 12.360988999999996],
          [-93.850314, 12.347417],
          [-93.839099, 12.335467000000001],
          [-93.83467099999999, 12.322381999999998],
          [-93.89094, 12.234467000000002],
          [-93.912992, 12.208351],
          [-93.92342, 12.211725000000001],
          [-93.942205, 12.20946],
          [-93.953825, 12.206660999999997],
          [-93.957471, 12.202584000000002],
          [-93.95757499999999, 12.199886999999997],
          [-93.93734900000001, 12.166128999999998],
          [-93.956411, 12.095483999999999],
          [-93.971228, 12.083292999999998],
          [-93.98935900000001, 12.081347000000001],
          [-94.01392000000001, 12.085374000000002],
          [-94.021028, 12.088745000000003],
          [-94.03869499999999, 12.105806999999999],
          [-94.041765, 12.116337000000001],
          [-94.029643, 12.134864],
          [-94.024495, 12.148164000000001],
          [-94.02435600000001, 12.160381999999998],
          [-94.03096400000001, 12.193213999999998],
          [-94.05441099999999, 12.224854],
          [-94.05849900000001, 12.227213999999996],
          [-94.08731700000001, 12.225423999999997],
          [-94.095013, 12.214751],
          [-94.105098, 12.180940999999997],
          [-94.153663, 12.169699999999999],
          [-94.161971, 12.170152999999999],
          [-94.180057, 12.161634999999997],
          [-94.190371, 12.152571000000002],
          [-94.16614100000001, 12.144564000000003],
          [-94.125902, 12.139803999999998],
          [-94.11075500000001, 12.141053],
          [-94.10923600000001, 12.078732000000002],
          [-94.12151399999999, 12.069847000000003]
        ],
        [
          [-92.54646299999999, 12.606563000000001],
          [-92.53969799999999, 12.593198999999998],
          [-92.541282, 12.584572999999999],
          [-92.61082999999999, 12.556252],
          [-92.62743499999999, 12.552427999999999],
          [-92.628511, 12.553553],
          [-92.65091000000001, 12.552126999999999],
          [-92.70127500000001, 12.529580000000003],
          [-92.724189, 12.515371000000002],
          [-92.796627, 12.496471],
          [-92.81804299999999, 12.494934],
          [-92.874282, 12.509107999999998],
          [-92.98475099999999, 12.512639999999998],
          [-92.986907, 12.510784000000001],
          [-93.04172600000001, 12.518355999999997],
          [-93.07182800000001, 12.553483],
          [-93.07549599999999, 12.578684000000003],
          [-93.025091, 12.605047999999996],
          [-93.11032900000001, 12.669727000000002],
          [-93.14319800000001, 12.682979000000003],
          [-93.18436299999999, 12.706525999999997],
          [-93.212185, 12.736821999999997],
          [-93.22529800000001, 12.755591000000003],
          [-93.23333099999999, 12.771095000000003],
          [-93.235251, 12.775638999999998],
          [-93.233646, 12.779446999999998],
          [-93.244689, 12.792648999999997],
          [-93.264047, 12.814543999999998],
          [-93.27735200000001, 12.816431999999999],
          [-93.325392, 12.791863999999997],
          [-93.334308, 12.773401999999997],
          [-93.33284499999999, 12.761662999999999],
          [-93.31986, 12.737693],
          [-93.303752, 12.729878999999997],
          [-93.27295799999999, 12.733652999999997],
          [-93.26684399999999, 12.734881999999999],
          [-93.26560799999999, 12.738236999999998],
          [-93.26676800000001, 12.741943],
          [-93.261381, 12.747459],
          [-93.250686, 12.751430999999997],
          [-93.21968100000001, 12.706887000000002],
          [-93.221517, 12.697885999999997],
          [-93.232516, 12.681336000000002],
          [-93.23368400000001, 12.649285999999996],
          [-93.21422799999999, 12.649591999999998],
          [-93.151444, 12.637672000000002],
          [-93.10631599999999, 12.618392999999998],
          [-93.103363, 12.614645000000003],
          [-93.08938800000001, 12.535474],
          [-93.089215, 12.523916],
          [-93.112907, 12.529294999999998],
          [-93.138307, 12.532922999999997],
          [-93.142099, 12.528590999999999],
          [-93.142482, 12.519697],
          [-93.139228, 12.515994],
          [-93.12472600000001, 12.511439000000003],
          [-93.12224499999999, 12.492019999999997],
          [-93.14873, 12.467357],
          [-93.16099600000001, 12.460256999999999],
          [-93.203584, 12.447657],
          [-93.34850399999999, 12.469568000000002],
          [-93.3563, 12.471794000000003],
          [-93.361615, 12.486072999999998],
          [-93.37188900000001, 12.493689000000003],
          [-93.37624500000001, 12.495592000000002],
          [-93.40179699999999, 12.496355000000001],
          [-93.41779500000001, 12.494146999999998],
          [-93.428585, 12.479247],
          [-93.460634, 12.454120000000003],
          [-93.51268400000001, 12.437015000000002],
          [-93.53296900000001, 12.434750999999999],
          [-93.603669, 12.435412999999997],
          [-93.638349, 12.438881000000002],
          [-93.65546800000001, 12.442279],
          [-93.663094, 12.448073],
          [-93.67165299999999, 12.487656000000001],
          [-93.675836, 12.522714],
          [-93.67569599999999, 12.538671999999998],
          [-93.67226199999999, 12.544151999999997],
          [-93.66450900000001, 12.550727000000002],
          [-93.662631, 12.555734999999999],
          [-93.66306399999999, 12.562069999999999],
          [-93.66763900000001, 12.576390000000004],
          [-93.662631, 12.598923999999997],
          [-93.66763900000001, 12.607686999999999],
          [-93.673621, 12.611801],
          [-93.679352, 12.633088999999998],
          [-93.689178, 12.643734000000002],
          [-93.698309, 12.649624000000003],
          [-93.70581999999999, 12.658386999999998],
          [-93.709576, 12.683424000000002],
          [-93.70637199999999, 12.696953999999998],
          [-93.69982200000001, 12.710054],
          [-93.69654700000001, 12.744442999999997],
          [-93.68999600000001, 12.761637],
          [-93.68641600000001, 12.779817000000001],
          [-93.68099000000001, 12.795206999999998],
          [-93.679352, 12.810763999999999],
          [-93.68999600000001, 12.839421000000002],
          [-93.69982200000001, 12.839421000000002],
          [-93.71292199999999, 12.827958000000002],
          [-93.741579, 12.813220000000001],
          [-93.76532399999999, 12.807488999999997],
          [-93.772302, 12.809494999999998],
          [-93.787484, 12.821365999999998],
          [-93.795364, 12.824585999999996],
          [-93.86904000000001, 12.845937999999997],
          [-93.887426, 12.868391000000003],
          [-93.916358, 12.914447000000003],
          [-93.90363400000001, 12.927269000000003],
          [-93.92145099999999, 12.961511000000002],
          [-93.96706, 12.988044000000002],
          [-94.049218, 13.029203000000003],
          [-94.03397899999999, 13.063281000000003],
          [-94.00885600000001, 13.074578000000002],
          [-93.887957, 13.097743999999999],
          [-93.77346399999999, 13.078676000000002],
          [-93.702925, 13.065206000000003],
          [-93.61390800000001, 13.057977999999999],
          [-93.536258, 13.038699999999999],
          [-93.446204, 13.020124000000003],
          [-93.362502, 13.007424],
          [-93.34632099999999, 13.004117999999998],
          [-93.34421699999999, 13.001834000000002],
          [-93.334272, 13.002442000000002],
          [-93.32505800000001, 13.011710999999998],
          [-93.30831699999999, 13.014761999999997],
          [-93.104611, 13.005701000000002],
          [-92.99743000000001, 12.942200999999997],
          [-92.98137, 12.92738],
          [-92.947081, 12.870963000000003],
          [-92.935933, 12.839962999999997],
          [-92.935258, 12.822941],
          [-92.903211, 12.803609999999999],
          [-92.872512, 12.798144],
          [-92.85315, 12.797809999999998],
          [-92.82055600000001, 12.793787000000002],
          [-92.76296400000001, 12.753227000000003],
          [-92.74170899999999, 12.724277999999998],
          [-92.74320700000001, 12.713720000000002],
          [-92.665874, 12.680241000000002],
          [-92.628129, 12.66883],
          [-92.62278800000001, 12.668709999999997],
          [-92.619258, 12.660778],
          [-92.62545, 12.653210000000001],
          [-92.62820099999999, 12.645642000000002],
          [-92.62545, 12.642201999999997],
          [-92.61650599999999, 12.639449999999997],
          [-92.60549800000001, 12.623626999999999],
          [-92.595179, 12.613308000000004],
          [-92.57454000000001, 12.61262],
          [-92.56078, 12.607804000000002],
          [-92.553213, 12.608491999999998],
          [-92.54646299999999, 12.606563000000001]
        ],
        [
          [-92.08011200000001, 12.924152999999997],
          [-92.23404099999999, 12.881504],
          [-92.322505, 12.738582000000001],
          [-92.52239800000001, 12.716256000000001],
          [-92.51712699999999, 12.728631999999998],
          [-92.43238500000001, 12.782384999999998],
          [-92.37103200000001, 12.816412999999997],
          [-92.373615, 12.820352999999997],
          [-92.390129, 12.825837],
          [-92.404564, 12.825505999999997],
          [-92.43833000000001, 12.821117000000001],
          [-92.50551300000001, 12.785485000000001],
          [-92.51945699999999, 12.775455000000001],
          [-92.520712, 12.760115999999996],
          [-92.532002, 12.757140999999997],
          [-92.556758, 12.757241999999998],
          [-92.63745800000001, 12.780909999999999],
          [-92.77040400000001, 12.837485999999998],
          [-92.792089, 12.856152000000002],
          [-92.79710700000001, 12.864922],
          [-92.793601, 12.866363999999997],
          [-92.79272700000001, 12.871673000000001],
          [-92.796999, 12.877789999999997],
          [-92.81789, 12.896901],
          [-92.82934599999999, 12.903573000000002],
          [-92.846744, 12.910635],
          [-92.87034, 12.925682000000002],
          [-92.91197, 12.966650999999999],
          [-92.91896700000001, 12.993673000000001],
          [-92.89238800000001, 12.993015999999997],
          [-92.853636, 13.005343000000003],
          [-92.813684, 13.030217999999998],
          [-92.83222, 13.070408],
          [-92.853284, 13.080077000000003],
          [-92.870116, 13.078423999999998],
          [-92.87519699999999, 13.069577000000002],
          [-92.91648699999999, 13.040086000000002],
          [-92.93752, 13.048321000000001],
          [-92.984307, 13.054845],
          [-92.993944, 13.032353],
          [-93.02505, 13.057322],
          [-93.076481, 13.081733],
          [-93.125306, 13.088890999999997],
          [-93.16144800000001, 13.086264],
          [-93.188074, 13.088973000000003],
          [-93.20872600000001, 13.109698999999999],
          [-93.210261, 13.118453000000002],
          [-93.20665500000001, 13.123834000000002],
          [-93.22465600000001, 13.136522],
          [-93.23488, 13.137937],
          [-93.24741399999999, 13.136802000000003],
          [-93.322359, 13.112727],
          [-93.46693200000001, 13.159356000000002],
          [-93.51719399999999, 13.177774999999997],
          [-93.299692, 13.237727],
          [-93.12699699999999, 13.186321999999997],
          [-93.02929800000001, 13.235539000000003],
          [-92.986359, 13.330823000000002],
          [-92.64459, 13.426343000000003],
          [-92.48730699999999, 13.354227000000002],
          [-92.367984, 13.348685000000003],
          [-92.25218699999999, 13.215654999999998],
          [-92.21444199999999, 13.108167000000002],
          [-92.11507499999999, 13.045602000000002],
          [-92.08011200000001, 12.924152999999997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02220",
      state: "02",
      county: "220",
      name: "Sitka County",
      lsad: "Cty&Bor",
      censusArea: 2870.339,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.37070499999999, 13.831668],
          [-96.026713, 13.835869000000002],
          [-95.90674200000001, 14.001266000000001],
          [-95.89983899999999, 14.001387000000001],
          [-95.587961, 13.89732],
          [-95.596769, 13.889705],
          [-95.58917, 13.886409],
          [-95.575718, 13.883547999999998],
          [-95.51888199999999, 13.857382999999999],
          [-95.50349800000001, 13.84086],
          [-95.46947700000001, 13.834465999999999],
          [-95.377758, 13.802211999999997],
          [-95.37644399999999, 13.792039000000003],
          [-95.36422099999999, 13.775346999999996],
          [-95.32888700000001, 13.748420000000003],
          [-95.29156, 13.737468],
          [-95.201461, 13.728171000000003],
          [-95.151475, 13.738295999999998],
          [-95.098422, 13.754897999999997],
          [-95.063972, 13.750847],
          [-95.02483699999999, 13.742989999999999],
          [-95.012577, 13.746105],
          [-94.97811999999999, 13.763125000000002],
          [-94.93992399999999, 13.763612000000002],
          [-94.92972599999999, 13.759203],
          [-94.921604, 13.742376],
          [-94.91479000000001, 13.705821],
          [-94.91816700000001, 13.682271999999998],
          [-94.867433, 13.623438999999998],
          [-94.85638599999999, 13.598967000000002],
          [-94.82489100000001, 13.500067000000001],
          [-94.83712, 13.482761000000004],
          [-94.874346, 13.464405999999997],
          [-94.90268, 13.459349000000003],
          [-95.025148, 13.454315000000001],
          [-95.084338, 13.464671000000003],
          [-95.091216, 13.466352],
          [-95.09681800000001, 13.469915999999998],
          [-95.134063, 13.497681],
          [-95.24285499999999, 13.546301999999997],
          [-95.325849, 13.577178000000004],
          [-95.39249899999999, 13.599908999999997],
          [-95.474703, 13.631380999999998],
          [-95.514801, 13.650348999999999],
          [-95.571606, 13.674396999999999],
          [-95.593447, 13.663795],
          [-95.650701, 13.652520000000003],
          [-95.68495100000001, 13.677508000000003],
          [-95.71231700000001, 13.672648000000002],
          [-95.704444, 13.658361],
          [-95.69742199999999, 13.649711000000003],
          [-95.660902, 13.629196],
          [-95.59277800000001, 13.602474],
          [-95.57118600000001, 13.605367000000001],
          [-95.546143, 13.613826000000003],
          [-95.52630300000001, 13.606388000000003],
          [-95.511012, 13.595906],
          [-95.51947100000001, 13.588614999999997],
          [-95.52761000000001, 13.589002999999998],
          [-95.55781200000001, 13.583449000000002],
          [-95.570574, 13.579613000000002],
          [-95.58747500000001, 13.568829000000001],
          [-95.58847700000001, 13.552081000000001],
          [-95.57572400000001, 13.522874000000002],
          [-95.577831, 13.52053],
          [-95.578215, 13.516444999999997],
          [-95.565271, 13.510209000000003],
          [-95.54962699999999, 13.482016000000002],
          [-95.55031, 13.470140999999998],
          [-95.55390299999999, 13.45487],
          [-95.55776499999999, 13.451965999999999],
          [-95.610454, 13.442231999999997],
          [-95.613576, 13.438172999999999],
          [-95.611786, 13.431154],
          [-95.61288200000001, 13.424906999999997],
          [-95.621725, 13.419244999999997],
          [-95.62655799999999, 13.409961000000003],
          [-95.647179, 13.406132],
          [-95.66444899999999, 13.407229000000001],
          [-95.66798499999999, 13.405124],
          [-95.669837, 13.400578000000003],
          [-95.670267, 13.396214999999998],
          [-95.66815299999999, 13.392831999999999],
          [-95.66941600000001, 13.389296000000002],
          [-95.69104300000001, 13.383518000000002],
          [-95.69239999999999, 13.375267000000001],
          [-95.711401, 13.367759999999997],
          [-95.72047800000001, 13.373663],
          [-95.76801800000001, 13.383434999999999],
          [-95.84380999999999, 13.390895999999998],
          [-95.892131, 13.408048],
          [-95.90081599999999, 13.418180999999997],
          [-95.89697899999999, 13.440719000000001],
          [-95.94376600000001, 13.458779999999997],
          [-95.96698599999999, 13.472759000000003],
          [-96.01452900000001, 13.506227000000003],
          [-96.03045800000001, 13.521540000000002],
          [-96.02128300000001, 13.531551],
          [-96.00171700000001, 13.544499000000002],
          [-96.061747, 13.592067999999998],
          [-96.106796, 13.616567000000003],
          [-96.13061099999999, 13.624606999999997],
          [-96.18789699999999, 13.625729999999997],
          [-96.207876, 13.638106999999998],
          [-96.280809, 13.717379000000001],
          [-96.31463099999999, 13.758702999999997],
          [-96.30468400000001, 13.771051],
          [-96.309429, 13.779465000000002],
          [-96.36505199999999, 13.828561],
          [-96.37070499999999, 13.831668]
        ],
        [
          [-94.66658699999999, 12.169947],
          [-94.668151, 12.167026],
          [-94.67402799999999, 12.166924999999999],
          [-94.705007, 12.175722],
          [-94.806163, 12.235532999999997],
          [-94.82490200000001, 12.279691999999997],
          [-94.83941100000001, 12.309403000000003],
          [-94.915911, 12.360554999999998],
          [-94.945704, 12.389710000000001],
          [-94.99076299999999, 12.457998000000003],
          [-95.02377999999999, 12.499977000000001],
          [-95.05404899999999, 12.527658000000002],
          [-95.05823799999999, 12.529452999999997],
          [-95.060878, 12.541964999999998],
          [-95.05675600000001, 12.570611],
          [-95.045054, 12.583392000000003],
          [-95.02673999999999, 12.594625],
          [-95.00524899999999, 12.602252],
          [-94.997669, 12.597271999999997],
          [-94.985881, 12.595844],
          [-94.967164, 12.603026],
          [-94.969842, 12.615392],
          [-94.979386, 12.627676000000001],
          [-94.998369, 12.630017000000002],
          [-95.02974699999999, 12.628917999999999],
          [-95.03643199999999, 12.619627999999999],
          [-95.06569300000001, 12.607852000000001],
          [-95.091611, 12.600068],
          [-95.103271, 12.597926999999999],
          [-95.118709, 12.597727999999996],
          [-95.123389, 12.602823],
          [-95.14883599999999, 12.648915000000002],
          [-95.147761, 12.651932000000002],
          [-95.175826, 12.677875999999998],
          [-95.224142, 12.687857999999999],
          [-95.27491, 12.701633000000001],
          [-95.30507700000001, 12.726382000000001],
          [-95.317429, 12.743310999999999],
          [-95.318082, 12.748688000000001],
          [-95.29642999999999, 12.756293999999997],
          [-95.281608, 12.758527999999998],
          [-95.28087500000001, 12.768357000000002],
          [-95.310159, 12.778092999999998],
          [-95.35217599999999, 12.771196000000003],
          [-95.39867799999999, 12.779201],
          [-95.41298900000001, 12.810079000000002],
          [-95.40854200000001, 12.826014],
          [-95.368899, 12.892589000000001],
          [-95.34724399999999, 12.89998],
          [-95.332356, 12.913950999999997],
          [-95.35409000000001, 12.936500000000002],
          [-95.37972500000001, 12.953038999999997],
          [-95.383376, 12.981544],
          [-95.360232, 13.002231000000002],
          [-95.34280100000001, 13.001874],
          [-95.32949199999999, 13.004506999999997],
          [-95.32521600000001, 13.010866],
          [-95.31278, 13.044984],
          [-95.33399800000001, 13.050403000000003],
          [-95.34922900000001, 13.041535000000003],
          [-95.3741, 13.045391000000002],
          [-95.37776299999999, 13.052909999999997],
          [-95.369858, 13.057923000000002],
          [-95.36571, 13.066062000000002],
          [-95.37833699999999, 13.081622000000003],
          [-95.38461699999999, 13.087724999999999],
          [-95.388859, 13.089025999999997],
          [-95.40172100000001, 13.098654000000003],
          [-95.40414000000001, 13.109318000000002],
          [-95.405293, 13.114508],
          [-95.406158, 13.119697000000002],
          [-95.405293, 13.138148999999999],
          [-95.40970300000001, 13.148860999999997],
          [-95.42274599999999, 13.167724],
          [-95.39931200000001, 13.185003000000002],
          [-95.37244100000001, 13.198062999999998],
          [-95.37758, 13.206302],
          [-95.37202099999999, 13.228003000000001],
          [-95.361771, 13.233590999999997],
          [-95.35871499999999, 13.243775999999997],
          [-95.366177, 13.248852],
          [-95.40608599999999, 13.251677999999998],
          [-95.41479899999999, 13.251323999999997],
          [-95.42922899999999, 13.247213000000002],
          [-95.449073, 13.248103999999998],
          [-95.470189, 13.255752000000001],
          [-95.47768300000001, 13.256824000000002],
          [-95.503121, 13.253808999999997],
          [-95.51124100000001, 13.247076999999997],
          [-95.55257399999999, 13.233193999999997],
          [-95.561883, 13.241867999999997],
          [-95.56185300000001, 13.244067000000001],
          [-95.56895399999999, 13.255186000000002],
          [-95.602297, 13.285707000000002],
          [-95.604161, 13.290436999999997],
          [-95.60068799999999, 13.294074000000002],
          [-95.60178199999999, 13.300772000000002],
          [-95.631415, 13.311568000000001],
          [-95.63742300000001, 13.315275999999997],
          [-95.63820899999999, 13.320278000000002],
          [-95.674687, 13.336747000000003],
          [-95.684597, 13.350327999999998],
          [-95.69556299999999, 13.357460000000003],
          [-95.69512, 13.363076999999997],
          [-95.68769599999999, 13.367477000000001],
          [-95.659614, 13.373907000000003],
          [-95.639556, 13.371882999999997],
          [-95.630627, 13.379595000000002],
          [-95.628016, 13.383392999999998],
          [-95.62702200000001, 13.388207000000001],
          [-95.63357300000001, 13.394210999999999],
          [-95.633654, 13.399102],
          [-95.62600699999999, 13.399577999999998],
          [-95.604184, 13.409931999999998],
          [-95.585395, 13.416811000000003],
          [-95.57087200000001, 13.417459000000001],
          [-95.55454800000001, 13.426636000000002],
          [-95.55532099999999, 13.436737999999998],
          [-95.557806, 13.438236000000003],
          [-95.56099, 13.437455],
          [-95.561916, 13.438505],
          [-95.56223399999999, 13.440413],
          [-95.56223399999999, 13.442720000000001],
          [-95.55849599999999, 13.444788000000003],
          [-95.55610899999999, 13.446458],
          [-95.54015799999999, 13.446241],
          [-95.534221, 13.450251999999999],
          [-95.532069, 13.481116999999998],
          [-95.54879399999999, 13.508977000000002],
          [-95.526036, 13.509697000000003],
          [-95.491537, 13.531686999999998],
          [-95.44340399999999, 13.556876000000003],
          [-95.419205, 13.564158999999997],
          [-95.355749, 13.553567999999999],
          [-95.34759700000001, 13.550232000000001],
          [-95.322925, 13.533476],
          [-95.294491, 13.510835999999998],
          [-95.294837, 13.508719999999997],
          [-95.308458, 13.495201000000002],
          [-95.29778400000001, 13.483721000000003],
          [-95.265557, 13.48086],
          [-95.246114, 13.483598],
          [-95.227293, 13.489837999999999],
          [-95.209123, 13.488753000000003],
          [-95.16101699999999, 13.460712999999998],
          [-95.168056, 13.454211999999998],
          [-95.15920600000001, 13.445568000000002],
          [-95.145949, 13.439048999999997],
          [-95.06505000000001, 13.418971999999997],
          [-95.039634, 13.418131000000002],
          [-95.026566, 13.420496999999997],
          [-94.92651599999999, 13.422266999999998],
          [-94.89344, 13.420043999999997],
          [-94.84947700000001, 13.409669999999998],
          [-94.806352, 13.341887999999997],
          [-94.812769, 13.299765],
          [-94.829365, 13.294097999999998],
          [-94.83521099999999, 13.300314999999998],
          [-94.88478, 13.330002999999998],
          [-94.921651, 13.330713000000003],
          [-94.951267, 13.32866],
          [-94.95395099999999, 13.323723000000001],
          [-94.955014, 13.309432999999999],
          [-94.953011, 13.306131],
          [-94.94662600000001, 13.303164000000002],
          [-94.92232899999999, 13.303708],
          [-94.879535, 13.288176999999997],
          [-94.85494800000001, 13.264766000000002],
          [-94.84177399999999, 13.248587999999998],
          [-94.755886, 13.058034999999997],
          [-94.73787999999999, 13.009934999999999],
          [-94.73830000000001, 12.968058999999997],
          [-94.74013500000001, 12.948481000000001],
          [-94.71927500000001, 12.929031000000002],
          [-94.69573500000001, 12.900790999999998],
          [-94.69909899999999, 12.850864000000001],
          [-94.674238, 12.825176999999996],
          [-94.663434, 12.804687000000001],
          [-94.633997, 12.728721999999998],
          [-94.619799, 12.665888000000002],
          [-94.61595500000001, 12.637289000000003],
          [-94.62694300000001, 12.553868000000001],
          [-94.634207, 12.540968],
          [-94.63973200000001, 12.538834999999999],
          [-94.67104699999999, 12.541200000000003],
          [-94.66977800000001, 12.524129000000002],
          [-94.663839, 12.503183],
          [-94.65862200000001, 12.494675999999998],
          [-94.65517399999999, 12.492033],
          [-94.645308, 12.472214999999998],
          [-94.636506, 12.405625],
          [-94.63400999999999, 12.315840000000001],
          [-94.634411, 12.285341000000003],
          [-94.634668, 12.265832000000003],
          [-94.653827, 12.198385000000002],
          [-94.65987000000001, 12.182493999999998],
          [-94.66658699999999, 12.169947]
        ],
        [
          [-95.703464, 13.322040000000001],
          [-95.70291900000001, 13.315582],
          [-95.70548299999999, 13.308557],
          [-95.688233, 13.276142999999998],
          [-95.657272, 13.262027000000003],
          [-95.65500800000001, 13.25824],
          [-95.654898, 13.254742],
          [-95.658859, 13.247964000000003],
          [-95.6586, 13.245541000000003],
          [-95.656363, 13.243035999999996],
          [-95.63728900000001, 13.236123999999997],
          [-95.62134599999999, 13.223692999999997],
          [-95.620735, 13.217885000000003],
          [-95.604953, 13.184204000000001],
          [-95.59692100000001, 13.161195999999997],
          [-95.602057, 13.149161],
          [-95.5986, 13.145359999999997],
          [-95.592379, 13.142682999999998],
          [-95.58147299999999, 13.150599],
          [-95.57567399999999, 13.150530000000003],
          [-95.565707, 13.147312],
          [-95.565756, 13.137343999999999],
          [-95.57572200000001, 13.104230999999999],
          [-95.574693, 13.094338999999998],
          [-95.635347, 13.022410999999998],
          [-95.65054, 13.015138999999998],
          [-95.68052, 13.017342],
          [-95.753682, 13.008257999999998],
          [-95.764949, 13.006093999999997],
          [-95.767654, 13.000428],
          [-95.77243200000001, 12.997301],
          [-95.82191499999999, 12.988214999999997],
          [-95.854131, 12.995043000000003],
          [-95.85702800000001, 12.997287],
          [-95.852146, 13.029699],
          [-95.84181899999999, 13.070898999999997],
          [-95.83489, 13.091386999999997],
          [-95.80594500000001, 13.106777999999998],
          [-95.776975, 13.112555],
          [-95.75599700000001, 13.121225000000003],
          [-95.735559, 13.151755000000001],
          [-95.73540499999999, 13.155403],
          [-95.752591, 13.168833999999997],
          [-95.76224099999999, 13.173341],
          [-95.785583, 13.177928999999999],
          [-95.800824, 13.173654999999997],
          [-95.824388, 13.192861999999998],
          [-95.83389600000001, 13.254233],
          [-95.82883100000001, 13.301980999999998],
          [-95.840359, 13.314342000000003],
          [-95.855849, 13.319960000000002],
          [-95.85957199999999, 13.323107999999998],
          [-95.860558, 13.330854000000002],
          [-95.85116199999999, 13.336916000000002],
          [-95.83856800000001, 13.338755999999997],
          [-95.761471, 13.342630999999997],
          [-95.730323, 13.337285999999999],
          [-95.71523300000001, 13.330722000000002],
          [-95.703464, 13.322040000000001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02230",
      state: "02",
      county: "230",
      name: "Skagway County",
      lsad: "Muny",
      censusArea: 452.325,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.00518, 15.357216999999999],
          [-95.029245, 15.345363999999996],
          [-95.37582, 15.340620999999999],
          [-95.72246000000001, 15.729526],
          [-95.47743600000001, 15.799626000000004],
          [-95.284921, 15.714892999999996],
          [-95.16673599999999, 15.632240000000003],
          [-95.15311299999999, 15.625158999999996],
          [-95.114588, 15.623415000000001],
          [-95.027456, 15.563692000000003],
          [-95.02976899999999, 15.482444000000001],
          [-95.04003700000001, 15.444792],
          [-95.02976899999999, 15.407139999999998],
          [-95.00518, 15.357216999999999]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02020",
      state: "02",
      county: "020",
      name: "Anchorage County",
      lsad: "Muny",
      censusArea: 1704.683,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.73639499999999, 16.994692],
          [-109.73969199999999, 17.016961000000002],
          [-109.75194300000001, 17.024071999999997],
          [-109.784393, 17.038147000000002],
          [-109.78512599999999, 17.040798000000002],
          [-109.805816, 17.058641],
          [-109.83192199999999, 17.076197],
          [-109.857168, 17.07902],
          [-109.91566599999999, 17.101428],
          [-109.96010000000001, 17.115346000000002],
          [-110.03930399999999, 17.144291000000003],
          [-110.06564599999999, 17.151079000000003],
          [-110.07545099999999, 17.156269],
          [-110.068004, 17.166131999999998],
          [-110.009941, 17.203637],
          [-109.98700199999999, 17.222752999999997],
          [-109.98657600000001, 17.228323000000003],
          [-109.897888, 17.267043],
          [-109.79242300000001, 17.388896000000003],
          [-109.359442, 17.483938000000002],
          [-109.23755600000001, 17.483144000000003],
          [-109.18744699999999, 17.425285000000002],
          [-108.46000699999999, 17.426972],
          [-108.47958700000001, 17.165059],
          [-108.47346099999999, 16.850630000000002],
          [-108.74602300000001, 16.84614],
          [-108.745578, 16.733880999999997],
          [-109.042881, 16.734797],
          [-109.038568, 16.849342],
          [-109.188571, 16.905486000000003],
          [-109.373299, 16.907624],
          [-109.73639499999999, 16.994692]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02068",
      state: "02",
      county: "068",
      name: "Denali County",
      lsad: "Borough",
      censusArea: 12751.411,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.001339, 18.72744],
          [-112.434247, 19.169794000000003],
          [-112.43462500000001, 19.305936000000003],
          [-112.812007, 19.351232000000003],
          [-112.85343, 19.65171],
          [-112.241918, 19.656678999999997],
          [-112.23355699999999, 19.823684],
          [-112.055519, 19.823679],
          [-112.04737700000001, 20.000575999999995],
          [-111.82505800000001, 20.005364999999998],
          [-111.790558, 20.08229],
          [-111.579286, 20.08538],
          [-111.52511100000001, 20.032465000000002],
          [-111.301289, 20.009970999999993],
          [-111.13619499999999, 20.133083999999997],
          [-110.74973600000001, 20.365138],
          [-109.667993, 20.357812999999993],
          [-109.119968, 20.358795999999998],
          [-109.12070399999999, 20.343615],
          [-107.995157, 20.341690999999997],
          [-107.76155700000001, 20.257853999999995],
          [-107.003185, 20.258927999999997],
          [-106.972014, 19.911791],
          [-106.97548900000001, 19.479746],
          [-108.032727, 19.469757],
          [-108.035867, 19.330788],
          [-109.50698399999999, 19.332164],
          [-111.38790399999999, 18.910327000000002],
          [-111.88896299999999, 18.795347999999997],
          [-111.893149, 18.722991999999998],
          [-113.001339, 18.72744]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02013",
      state: "02",
      county: "013",
      name: "Aleutians East County",
      lsad: "Borough",
      censusArea: 6981.943,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.255031, 10.978352999999998],
          [-122.249682, 10.975900000000003],
          [-122.23567499999999, 10.962601],
          [-122.23296199999999, 10.890984000000003],
          [-122.236806, 10.881630000000001],
          [-122.275316, 10.845565],
          [-122.28294399999999, 10.841216000000003],
          [-122.30058, 10.832594],
          [-122.32109399999999, 10.827928],
          [-122.34931499999999, 10.836049000000003],
          [-122.41737, 10.877491],
          [-122.42524399999999, 10.885021000000002],
          [-122.437501, 10.927627000000001],
          [-122.435473, 10.929248999999999],
          [-122.33743100000001, 10.981636000000002],
          [-122.32681099999999, 10.985329999999998],
          [-122.26674299999999, 10.982132999999997],
          [-122.255031, 10.978352999999998]
        ],
        [
          [-119.324364, 10.928328999999998],
          [-119.317681, 10.933706999999998],
          [-119.278696, 10.948514000000003],
          [-119.20567, 10.927438000000002],
          [-119.202857, 10.9255],
          [-119.203228, 10.914842],
          [-119.212627, 10.896065999999998],
          [-119.236066, 10.87648],
          [-119.272354, 10.864204],
          [-119.30586400000001, 10.863698],
          [-119.30968100000001, 10.865813000000003],
          [-119.32787300000001, 10.884749],
          [-119.32073199999999, 10.897269000000001],
          [-119.31352799999999, 10.903388],
          [-119.31206, 10.909601000000002],
          [-119.31273300000001, 10.918686000000001],
          [-119.324364, 10.928328999999998]
        ],
        [
          [-120.0179, 11.156129999999997],
          [-120.047358, 11.180878999999997],
          [-120.06146899999999, 11.200378],
          [-120.052941, 11.203035],
          [-120.02525700000001, 11.203913999999997],
          [-120.00215499999999, 11.194479999999999],
          [-119.99402699999999, 11.185246999999997],
          [-119.983499, 11.180379000000002],
          [-119.95435, 11.189290999999997],
          [-119.93124, 11.220059999999997],
          [-119.889174, 11.287137999999999],
          [-119.87969000000001, 11.290182999999999],
          [-119.87059099999999, 11.284889],
          [-119.84861900000001, 11.267547999999998],
          [-119.84385900000001, 11.249367],
          [-119.84462199999999, 11.243828],
          [-119.855302, 11.230378000000002],
          [-119.86099100000001, 11.227884000000003],
          [-119.866624, 11.231202000000003],
          [-119.88652300000001, 11.229149],
          [-119.89532600000001, 11.217872],
          [-119.90536499999999, 11.164689000000003],
          [-119.901569, 11.156858],
          [-119.884997, 11.145598],
          [-119.86089100000001, 11.149337000000003],
          [-119.859568, 11.164912000000001],
          [-119.86130800000001, 11.171748000000001],
          [-119.86078599999999, 11.177086000000003],
          [-119.84626399999999, 11.180833999999997],
          [-119.816419, 11.178051000000004],
          [-119.82371, 11.144129999999997],
          [-119.83089000000001, 11.126466999999998],
          [-119.855444, 11.100757999999999],
          [-119.86858000000001, 11.094887999999997],
          [-119.886109, 11.102558000000002],
          [-119.88647599999999, 11.107087],
          [-119.906609, 11.112544],
          [-119.947575, 11.105215000000001],
          [-119.951281, 11.100644000000003],
          [-119.94541000000001, 11.087938999999999],
          [-119.95112700000001, 11.066642000000002],
          [-120.05209300000001, 11.005777000000002],
          [-120.080659, 10.994425],
          [-120.11577500000001, 10.985078000000001],
          [-120.18346600000001, 10.915680000000002],
          [-120.181636, 10.902991999999998],
          [-120.200831, 10.875281999999999],
          [-120.226967, 10.864075],
          [-120.25476499999999, 10.895974000000002],
          [-120.25081399999999, 10.929816000000002],
          [-120.24425299999999, 10.933532],
          [-120.223411, 10.939124],
          [-120.177607, 10.981445999999998],
          [-120.13216800000001, 11.013742999999998],
          [-120.10683700000001, 11.027002000000003],
          [-120.082168, 11.033577999999999],
          [-120.07787200000001, 11.037599999999998],
          [-120.08757399999999, 11.049967000000002],
          [-120.094288, 11.052996],
          [-120.13341600000001, 11.043947000000003],
          [-120.17436599999999, 11.052577],
          [-120.19139200000001, 11.108573999999997],
          [-120.187261, 11.118375999999998],
          [-120.10986399999999, 11.160777000000003],
          [-120.07730799999999, 11.146495000000002],
          [-120.05959899999999, 11.133662999999999],
          [-120.05254500000001, 11.121716],
          [-120.05282, 11.119373000000003],
          [-120.05703299999999, 11.118488],
          [-120.057797, 11.115352999999999],
          [-120.055642, 11.111579999999996],
          [-120.03032999999999, 11.116109000000002],
          [-120.01222000000001, 11.122945999999999],
          [-120.00516999999999, 11.129378000000003],
          [-120.004129, 11.134481999999998],
          [-120.0179, 11.156129999999997]
        ],
        [
          [-119.455311, 11.061452000000003],
          [-119.448473, 11.064343000000001],
          [-119.38184100000001, 11.064031999999997],
          [-119.34527600000001, 11.059396999999997],
          [-119.33847, 11.046683000000002],
          [-119.328791, 10.980598],
          [-119.330164, 10.976377999999997],
          [-119.33177, 10.974598],
          [-119.426615, 10.942265999999996],
          [-119.447982, 10.941374000000003],
          [-119.45799500000001, 10.945729999999998],
          [-119.459551, 10.948652000000003],
          [-119.45125100000001, 10.975285],
          [-119.440057, 10.988501999999997],
          [-119.416786, 11.000295999999999],
          [-119.41798700000001, 11.022013000000001],
          [-119.455434, 11.035809],
          [-119.455311, 11.061452000000003]
        ],
        [
          [-119.533457, 11.184761000000002],
          [-119.504325, 11.176822000000001],
          [-119.499502, 11.173991000000001],
          [-119.49339800000001, 11.155054999999997],
          [-119.49477200000001, 11.136049999999997],
          [-119.503592, 11.131373000000004],
          [-119.50936100000001, 11.130206000000001],
          [-119.530302, 11.106194000000002],
          [-119.53362999999999, 11.083987],
          [-119.52444399999999, 11.077357999999997],
          [-119.48419999999999, 11.057695000000002],
          [-119.48498000000001, 11.054049999999997],
          [-119.489589, 11.049228999999997],
          [-119.50967399999999, 11.041407999999997],
          [-119.51782399999999, 11.044735000000003],
          [-119.53546499999999, 11.058734999999999],
          [-119.57236499999999, 11.060122],
          [-119.597824, 11.046999999999997],
          [-119.63522599999999, 11.037294000000003],
          [-119.638905, 11.038744999999999],
          [-119.64402899999999, 11.042597],
          [-119.64906199999999, 11.049532],
          [-119.650859, 11.06579],
          [-119.63541000000001, 11.116653999999997],
          [-119.616961, 11.127665999999998],
          [-119.586075, 11.157651999999999],
          [-119.57314400000001, 11.187562],
          [-119.521096, 11.253393000000003],
          [-119.519196, 11.253692999999998],
          [-119.503196, 11.234993000000003],
          [-119.50527, 11.222268999999997],
          [-119.51235, 11.208353000000002],
          [-119.533457, 11.184761000000002]
        ],
        [
          [-121.718614, 11.154165999999996],
          [-121.69709700000001, 11.137132999999999],
          [-121.67838900000001, 11.131746999999997],
          [-121.66361800000001, 11.13026],
          [-121.65156300000001, 11.130916],
          [-121.608634, 11.116906],
          [-121.57664299999999, 11.103831],
          [-121.57052300000001, 11.100493],
          [-121.549901, 11.082563999999998],
          [-121.548924, 11.080115],
          [-121.55035699999999, 11.065733999999999],
          [-121.55731499999999, 11.061193000000003],
          [-121.56503599999999, 11.058874000000003],
          [-121.582584, 11.058224000000003],
          [-121.591069, 11.060817999999998],
          [-121.606359, 11.070264000000002],
          [-121.606482, 11.074207999999999],
          [-121.59830299999999, 11.081341000000002],
          [-121.59695099999999, 11.086584000000002],
          [-121.60251400000001, 11.098267],
          [-121.61586700000001, 11.104388],
          [-121.63239100000001, 11.104895999999997],
          [-121.65491800000001, 11.103243999999997],
          [-121.67453900000001, 11.095911999999998],
          [-121.678537, 11.092829000000002],
          [-121.67817099999999, 11.087741000000001],
          [-121.690346, 11.078499999999998],
          [-121.737922, 11.054054],
          [-121.76416900000001, 11.059508999999998],
          [-121.791606, 11.077306999999998],
          [-121.81498400000001, 11.098638999999999],
          [-121.81648200000001, 11.111319000000002],
          [-121.81986900000001, 11.113965],
          [-121.85115200000001, 11.126378000000003],
          [-121.862504, 11.127597999999999],
          [-121.886278, 11.126933000000001],
          [-121.89784599999999, 11.135767999999999],
          [-121.90068500000001, 11.142139],
          [-121.888936, 11.160724000000002],
          [-121.85005699999999, 11.175260000000002],
          [-121.82784000000001, 11.178472999999997],
          [-121.73781, 11.161935],
          [-121.718614, 11.154165999999996]
        ],
        [
          [-120.50692699999999, 11.327730000000003],
          [-120.528864, 11.343501000000003],
          [-120.534943, 11.343536999999998],
          [-120.564427, 11.332504],
          [-120.57969, 11.314292000000002],
          [-120.580668, 11.307195999999998],
          [-120.58008799999999, 11.302503000000002],
          [-120.56592900000001, 11.273136999999998],
          [-120.550759, 11.264302],
          [-120.52761699999999, 11.256374000000001],
          [-120.486174, 11.193617000000003],
          [-120.48651100000001, 11.181950999999998],
          [-120.496306, 11.166398999999998],
          [-120.525226, 11.129871000000001],
          [-120.655577, 11.160260999999998],
          [-120.675871, 11.173622000000002],
          [-120.68837199999999, 11.195588],
          [-120.690293, 11.210473999999998],
          [-120.756587, 11.195143000000002],
          [-120.767393, 11.185398999999997],
          [-120.765229, 11.176715999999999],
          [-120.794198, 11.134399000000002],
          [-120.80600899999999, 11.12567],
          [-120.821381, 11.117851000000002],
          [-120.824468, 11.120153000000002],
          [-120.81586200000001, 11.141556000000001],
          [-120.807468, 11.155579000000003],
          [-120.807558, 11.168160999999998],
          [-120.81948700000001, 11.187457000000002],
          [-120.841917, 11.204439999999998],
          [-120.84122099999999, 11.293956999999999],
          [-120.85662099999999, 11.318488000000002],
          [-120.840251, 11.339776999999998],
          [-120.797147, 11.381521],
          [-120.77780000000001, 11.388638999999998],
          [-120.710298, 11.403075000000001],
          [-120.687442, 11.402197999999999],
          [-120.66592700000001, 11.399025000000002],
          [-120.649234, 11.388779999999997],
          [-120.64621399999999, 11.383142],
          [-120.64615599999999, 11.357689],
          [-120.65101100000001, 11.343649999999997],
          [-120.61425600000001, 11.348019],
          [-120.57271600000001, 11.388978000000002],
          [-120.567384, 11.390078000000003],
          [-120.54522700000001, 11.387911000000003],
          [-120.51751300000001, 11.379378000000003],
          [-120.522063, 11.374369000000002],
          [-120.52239900000001, 11.370370999999999],
          [-120.52230700000001, 11.364367000000001],
          [-120.518955, 11.361552000000003],
          [-120.425658, 11.338883000000003],
          [-120.408614, 11.341676999999997],
          [-120.40334999999999, 11.346299000000002],
          [-120.36175800000001, 11.363230000000001],
          [-120.344369, 11.362962000000003],
          [-120.33369200000001, 11.360138999999997],
          [-120.32646299999999, 11.353189],
          [-120.317665, 11.338532999999998],
          [-120.30654999999999, 11.303275],
          [-120.33072200000001, 11.261045000000003],
          [-120.341217, 11.251798999999998],
          [-120.372603, 11.259909999999998],
          [-120.37888699999999, 11.266691999999999],
          [-120.380472, 11.272803000000003],
          [-120.390421, 11.286575999999997],
          [-120.44189600000001, 11.298264000000003],
          [-120.44979900000001, 11.297080999999999],
          [-120.457765, 11.291893000000002],
          [-120.46826200000001, 11.288924999999999],
          [-120.475493, 11.289230000000003],
          [-120.481536, 11.291778999999998],
          [-120.518148, 11.309102000000003],
          [-120.52694500000001, 11.319043999999998],
          [-120.50692699999999, 11.327730000000003]
        ],
        [
          [-120.21178, 11.455862000000003],
          [-120.137032, 11.450709000000003],
          [-120.14183399999999, 11.387154000000002],
          [-120.142505, 11.383491],
          [-120.14799300000001, 11.377575999999998],
          [-120.15403800000001, 11.377518000000002],
          [-120.20361, 11.391739000000001],
          [-120.27996999999999, 11.395904999999999],
          [-120.308921, 11.393174000000002],
          [-120.321132, 11.393676999999997],
          [-120.33985799999999, 11.409692],
          [-120.349526, 11.420476999999998],
          [-120.347609, 11.426186999999999],
          [-120.323237, 11.444633000000003],
          [-120.26683399999999, 11.462789],
          [-120.26056500000001, 11.463673999999997],
          [-120.22750400000001, 11.460340000000002],
          [-120.21178, 11.455862000000003]
        ],
        [
          [-125.79052300000001, 10.171757999999997],
          [-125.747893, 10.161296999999998],
          [-125.742613, 10.158352],
          [-125.73260200000001, 10.148121000000003],
          [-125.71419800000001, 10.120815],
          [-125.66732300000001, 10.132123],
          [-125.66137900000001, 10.130935000000001],
          [-125.655573, 10.119100000000003],
          [-125.67147700000001, 10.096235],
          [-125.76717300000001, 10.065935000000003],
          [-125.81074699999999, 10.074764000000002],
          [-125.87512899999999, 10.03642],
          [-125.896308, 10.055714000000002],
          [-125.89726099999999, 10.060634],
          [-125.90164899999999, 10.062869999999997],
          [-125.916235, 10.065708],
          [-125.93024199999999, 10.066553999999996],
          [-125.98441500000001, 10.061722000000003],
          [-126.01986099999999, 10.051440999999997],
          [-126.02773300000001, 10.045917000000003],
          [-126.046438, 10.044186000000003],
          [-126.098255, 10.103538],
          [-126.11224200000001, 10.122528000000003],
          [-126.10140200000001, 10.144148000000001],
          [-126.08202800000001, 10.175184000000002],
          [-126.002465, 10.213628999999997],
          [-125.98320000000001, 10.221175000000002],
          [-125.94462999999999, 10.220855],
          [-125.873076, 10.216455000000003],
          [-125.868192, 10.214883999999998],
          [-125.86514, 10.212159999999997],
          [-125.865872, 10.200014000000003],
          [-125.871973, 10.189782999999998],
          [-125.88045600000001, 10.183647999999998],
          [-125.868076, 10.168731000000001],
          [-125.863518, 10.166162],
          [-125.83727400000001, 10.161028000000002],
          [-125.83242100000001, 10.161332999999999],
          [-125.82515900000001, 10.164499],
          [-125.797147, 10.183245999999997],
          [-125.793781, 10.183433],
          [-125.79256899999999, 10.181604999999998],
          [-125.79052300000001, 10.171757999999997]
        ],
        [
          [-125.27104800000001, 10.095664999999997],
          [-125.267012, 10.095467],
          [-125.234364, 10.065423000000003],
          [-125.235149, 10.062767000000001],
          [-125.24583000000001, 10.056109999999997],
          [-125.32441499999999, 10.063907],
          [-125.336836, 10.070126000000002],
          [-125.365768, 10.073317000000003],
          [-125.458179, 10.066313000000001],
          [-125.48274699999999, 10.072218],
          [-125.483373, 10.075035999999997],
          [-125.468221, 10.079641000000002],
          [-125.438972, 10.084136],
          [-125.32226800000001, 10.094634],
          [-125.27104800000001, 10.095664999999997]
        ],
        [
          [-124.97619900000001, 10.134594999999997],
          [-124.93776600000001, 10.136682],
          [-124.92130700000001, 10.128568999999999],
          [-124.919689, 10.116079999999997],
          [-124.92146399999999, 10.111083],
          [-124.95316500000001, 10.078055999999997],
          [-124.96058099999999, 10.076025999999999],
          [-125.044322, 10.066628999999999],
          [-125.20341300000001, 10.087752000000002],
          [-125.212264, 10.090158000000002],
          [-125.22087099999999, 10.101574],
          [-125.198746, 10.116473999999997],
          [-125.14097799999999, 10.131079],
          [-125.08848599999999, 10.128005000000002],
          [-125.067428, 10.123173999999999],
          [-125.05015499999999, 10.121707999999998],
          [-125.023065, 10.121918999999998],
          [-125.00791000000001, 10.134934000000001],
          [-124.97619900000001, 10.134594999999997]
        ],
        [
          [-125.52346600000001, 10.299895],
          [-125.50277500000001, 10.299469000000002],
          [-125.478452, 10.295333],
          [-125.47774999999999, 10.283707],
          [-125.47998100000001, 10.281838],
          [-125.5137, 10.274085999999997],
          [-125.557581, 10.254137999999998],
          [-125.55883499999999, 10.250763],
          [-125.55795, 10.246825999999999],
          [-125.55325099999999, 10.239601],
          [-125.51278199999999, 10.212929000000003],
          [-125.49627899999999, 10.210937999999999],
          [-125.478816, 10.21331],
          [-125.40537699999999, 10.212837],
          [-125.39144099999999, 10.204253000000001],
          [-125.38371900000001, 10.196731],
          [-125.39998499999999, 10.177740999999997],
          [-125.412925, 10.179220999999998],
          [-125.42235600000001, 10.182799000000003],
          [-125.47619, 10.182701000000002],
          [-125.48131699999999, 10.179962000000003],
          [-125.53600399999999, 10.129606000000003],
          [-125.549217, 10.112195999999997],
          [-125.56542200000001, 10.108122000000002],
          [-125.57564500000001, 10.108618],
          [-125.613214, 10.120908],
          [-125.62972500000001, 10.132558000000003],
          [-125.637081, 10.199435999999999],
          [-125.62185400000001, 10.208105000000003],
          [-125.59365600000001, 10.218375000000002],
          [-125.585782, 10.223067],
          [-125.579801, 10.229574999999997],
          [-125.58715699999999, 10.238166],
          [-125.595732, 10.242713000000002],
          [-125.61208199999999, 10.246536999999996],
          [-125.61562900000001, 10.244833999999997],
          [-125.625609, 10.233756],
          [-125.64001300000001, 10.229672999999998],
          [-125.66938300000001, 10.229036],
          [-125.68145799999999, 10.236913999999999],
          [-125.685823, 10.243406],
          [-125.684114, 10.249907],
          [-125.67544699999999, 10.264639000000003],
          [-125.636383, 10.297567],
          [-125.62555, 10.298963999999998],
          [-125.61556999999999, 10.297445000000003],
          [-125.60522499999999, 10.294218999999998],
          [-125.586509, 10.284360999999997],
          [-125.52346600000001, 10.299895]
        ],
        [
          [-122.80186499999999, 10.489440000000002],
          [-122.79629, 10.492254000000003],
          [-122.72841500000001, 10.475354000000003],
          [-122.58888300000001, 10.450063999999998],
          [-122.556667, 10.424621000000002],
          [-122.552718, 10.416113000000003],
          [-122.55161799999999, 10.392217000000002],
          [-122.562726, 10.382840000000002],
          [-122.61189100000001, 10.368077],
          [-122.72279700000001, 10.40034],
          [-122.76039599999999, 10.373254000000003],
          [-122.75947199999999, 10.371116],
          [-122.781239, 10.375084999999999],
          [-122.86005, 10.425452],
          [-122.827621, 10.490859],
          [-122.80186499999999, 10.489440000000002]
        ],
        [
          [-118.89321100000001, 12.805788],
          [-118.89103399999999, 12.754219999999997],
          [-119.028594, 12.754058],
          [-119.02858900000001, 12.669418999999998],
          [-119.275496, 12.671762000000001],
          [-119.27549099999999, 12.580920999999996],
          [-119.592657, 12.580917],
          [-119.59341, 12.494523999999998],
          [-119.89942400000001, 12.491981000000003],
          [-119.903652, 12.322594000000002],
          [-119.809651, 12.321694],
          [-119.80526699999999, 11.982595000000003],
          [-119.86785800000001, 11.982593999999999],
          [-119.868099, 11.888480999999999],
          [-119.56466900000001, 11.888483999999998],
          [-119.564413, 11.633045000000003],
          [-119.61777000000001, 11.595798000000002],
          [-119.69671299999999, 11.573306000000002],
          [-119.729333, 11.569650000000003],
          [-119.73389900000001, 11.569985000000003],
          [-119.744495, 11.600017999999999],
          [-119.743282, 11.603624000000003],
          [-119.735196, 11.610933000000003],
          [-119.72415000000001, 11.614548999999997],
          [-119.67343199999999, 11.617350000000002],
          [-119.66751099999999, 11.614825000000003],
          [-119.63961900000001, 11.617915000000004],
          [-119.62677199999999, 11.629412000000002],
          [-119.635866, 11.644398000000002],
          [-119.644656, 11.652469000000004],
          [-119.679201, 11.655895000000001],
          [-119.680635, 11.681339999999999],
          [-119.676761, 11.737357000000003],
          [-119.673191, 11.750960999999997],
          [-119.62748199999999, 11.803248000000004],
          [-119.62488400000001, 11.804693999999998],
          [-119.602148, 11.805003999999997],
          [-119.60797299999999, 11.812899999999999],
          [-119.64373900000001, 11.830424],
          [-119.712816, 11.846392000000002],
          [-119.81107, 11.856569999999998],
          [-119.83898099999999, 11.852412000000001],
          [-119.85325499999999, 11.847161999999997],
          [-119.85845599999999, 11.841793000000003],
          [-119.85075, 11.824075999999998],
          [-119.84735900000001, 11.802529999999997],
          [-119.862484, 11.787629000000003],
          [-119.87599399999999, 11.784607999999999],
          [-119.89231899999999, 11.785096000000003],
          [-119.93708899999999, 11.803306],
          [-120.010322, 11.797086999999998],
          [-120.02628200000001, 11.792295000000003],
          [-120.048711, 11.772061],
          [-120.052525, 11.76643],
          [-120.05194499999999, 11.760593999999998],
          [-120.049417, 11.757587999999998],
          [-120.05844300000001, 11.721733999999998],
          [-120.13044500000001, 11.681418999999998],
          [-120.185712, 11.658644000000002],
          [-120.27982700000001, 11.641384000000002],
          [-120.32541900000001, 11.644207000000002],
          [-120.35349400000001, 11.649731000000003],
          [-120.410823, 11.665379999999999],
          [-120.41645199999999, 11.665301999999997],
          [-120.421853, 11.662700999999998],
          [-120.42972700000001, 11.658045999999999],
          [-120.43360200000001, 11.648975],
          [-120.433022, 11.639978999999997],
          [-120.39258699999999, 11.602770999999997],
          [-120.435859, 11.573692000000001],
          [-120.44827699999999, 11.559049000000002],
          [-120.464301, 11.533242999999999],
          [-120.46518599999999, 11.527360999999999],
          [-120.45981499999999, 11.514986],
          [-120.46274500000001, 11.506653999999997],
          [-120.48163299999999, 11.489068000000003],
          [-120.50134600000001, 11.478518000000001],
          [-120.521335, 11.474420000000002],
          [-120.536654, 11.474938000000002],
          [-120.54422400000001, 11.502350999999997],
          [-120.55417299999999, 11.522965],
          [-120.580083, 11.564385000000001],
          [-120.59577100000001, 11.575539999999997],
          [-120.615305, 11.575516],
          [-120.638371, 11.557426],
          [-120.65277499999999, 11.548668],
          [-120.666966, 11.544417000000003],
          [-120.706883, 11.556066000000001],
          [-120.73709500000001, 11.555447999999998],
          [-120.75103999999999, 11.552906999999998],
          [-120.76623699999999, 11.547559],
          [-120.77295000000001, 11.538998],
          [-120.771433, 11.529429999999998],
          [-120.73214999999999, 11.523595999999998],
          [-120.66011700000001, 11.518475000000002],
          [-120.65411700000001, 11.512596000000002],
          [-120.64746400000001, 11.500861999999998],
          [-120.64630399999999, 11.492851000000002],
          [-120.651523, 11.474173999999998],
          [-120.66691700000001, 11.459775999999998],
          [-120.78140099999999, 11.45178],
          [-120.795988, 11.454946],
          [-120.836725, 11.473135],
          [-120.84340700000001, 11.489781999999998],
          [-120.83602300000001, 11.497259],
          [-120.82177300000001, 11.506216000000002],
          [-120.82081, 11.507973999999997],
          [-120.828273, 11.516111000000002],
          [-120.849145, 11.523916],
          [-120.86537999999999, 11.526967999999997],
          [-120.909625, 11.524140000000003],
          [-120.922934, 11.519300000000001],
          [-120.944265, 11.507824999999997],
          [-120.976551, 11.472735999999998],
          [-120.979298, 11.466273999999999],
          [-120.97737599999999, 11.461185],
          [-120.98271700000001, 11.454326000000002],
          [-120.99733499999999, 11.440264999999997],
          [-121.01366200000001, 11.431002],
          [-121.23153500000001, 11.357452000000002],
          [-121.280675, 11.354038000000003],
          [-121.32532499999999, 11.359855000000003],
          [-121.317545, 11.362758],
          [-121.31198900000001, 11.372836],
          [-121.31494900000001, 11.379230999999997],
          [-121.34608, 11.385781999999999],
          [-121.364577, 11.384194],
          [-121.44519600000001, 11.368102999999998],
          [-121.46039200000001, 11.359070000000003],
          [-121.48611399999999, 11.359321999999999],
          [-121.507657, 11.362786],
          [-121.51421099999999, 11.385254000000003],
          [-121.50930600000001, 11.390625999999997],
          [-121.49612300000001, 11.396966999999997],
          [-121.484588, 11.417994],
          [-121.47830300000001, 11.440600000000003],
          [-121.47146799999999, 11.478588000000002],
          [-121.477114, 11.485194999999997],
          [-121.46927099999999, 11.496830000000003],
          [-121.376102, 11.569794000000002],
          [-121.36740499999999, 11.579484],
          [-121.35568599999999, 11.606378],
          [-121.35767000000001, 11.612603],
          [-121.36337800000001, 11.618478000000003],
          [-121.39261300000001, 11.628221000000003],
          [-121.416235, 11.632323999999997],
          [-121.48206400000001, 11.633978999999997],
          [-121.526162, 11.630498000000003],
          [-121.58704700000001, 11.620060000000002],
          [-121.602825, 11.613810999999998],
          [-121.61292599999999, 11.606158],
          [-121.69886, 11.519399999999997],
          [-121.70006900000001, 11.514389999999999],
          [-121.696719, 11.423307000000001],
          [-121.688357, 11.416379999999997],
          [-121.68649500000001, 11.408040999999997],
          [-121.72009600000001, 11.376690000000004],
          [-121.82754299999999, 11.287872],
          [-121.833891, 11.284399999999998],
          [-121.845473, 11.281249000000003],
          [-121.853418, 11.277633999999999],
          [-121.863339, 11.266989000000002],
          [-121.875606, 11.249921],
          [-121.87954199999999, 11.240803999999997],
          [-121.87575899999999, 11.232591999999997],
          [-121.875238, 11.227224],
          [-121.878076, 11.223599],
          [-121.90340699999999, 11.204940999999998],
          [-121.91951900000001, 11.208208999999997],
          [-121.95745500000001, 11.227998999999997],
          [-121.97878800000001, 11.236131],
          [-122.02963600000001, 11.239491999999998],
          [-122.041236, 11.236806000000001],
          [-122.045694, 11.232774999999997],
          [-122.046242, 11.225605000000002],
          [-122.001711, 11.169235999999998],
          [-121.966974, 11.154831000000001],
          [-121.949882, 11.126686],
          [-121.956595, 11.112174000000003],
          [-121.96086600000001, 11.106734000000003],
          [-122.053281, 11.074212000000003],
          [-122.11874, 11.102910999999999],
          [-122.131878, 11.122776000000002],
          [-122.119033, 11.141115999999997],
          [-122.12636900000001, 11.153407999999999],
          [-122.141084, 11.157339],
          [-122.177427, 11.154403000000002],
          [-122.21819199999999, 11.118903000000003],
          [-122.22404700000001, 11.108657999999998],
          [-122.22352799999999, 11.102210999999997],
          [-122.20679999999999, 11.082391000000001],
          [-122.190348, 11.066980999999998],
          [-122.189247, 11.06026],
          [-122.219326, 11.028975000000003],
          [-122.24794600000001, 11.020439000000003],
          [-122.2535, 11.020454],
          [-122.267754, 11.021552999999997],
          [-122.28051199999999, 11.026207],
          [-122.29251099999999, 11.033428999999998],
          [-122.299619, 11.040151999999999],
          [-122.300378, 11.042926999999999],
          [-122.36196899999999, 11.042679],
          [-122.41351, 11.036560000000001],
          [-122.471364, 11.051932],
          [-122.489735, 11.064849000000002],
          [-122.512104, 11.086227000000001],
          [-122.52168800000001, 11.104011],
          [-122.520986, 11.115417],
          [-122.506887, 11.118927],
          [-122.460958, 11.125839999999997],
          [-122.453451, 11.123947999999999],
          [-122.442556, 11.118226],
          [-122.43736799999999, 11.112122],
          [-122.42479599999999, 11.104813],
          [-122.4168, 11.104095999999998],
          [-122.410574, 11.105614000000003],
          [-122.406191, 11.120497999999998],
          [-122.44518199999999, 11.151521000000002],
          [-122.48097999999999, 11.161271],
          [-122.49447, 11.183914999999999],
          [-122.49792, 11.199052000000002],
          [-122.499019, 11.213769999999997],
          [-122.510435, 11.250177],
          [-122.51312100000001, 11.252374000000003],
          [-122.58487199999999, 11.298386],
          [-122.626101, 11.304085],
          [-122.64917299999999, 11.299118],
          [-122.66196, 11.294294999999998],
          [-122.68240499999999, 11.276449999999997],
          [-122.70285100000001, 11.252775],
          [-122.714607, 11.231611000000001],
          [-122.718077, 11.219911000000003],
          [-122.711128, 11.211267],
          [-122.692309, 11.197313000000001],
          [-122.66834600000001, 11.193444999999997],
          [-122.644734, 11.197114999999997],
          [-122.638791, 11.194769999999998],
          [-122.614497, 11.174734999999998],
          [-122.57976500000001, 11.136938999999998],
          [-122.582908, 11.13024],
          [-122.585533, 11.128599999999999],
          [-122.59560300000001, 11.124845999999998],
          [-122.604454, 11.126027999999998],
          [-122.61891800000001, 11.097096],
          [-122.59981199999999, 11.054806],
          [-122.56929199999999, 11.015873999999997],
          [-122.569289, 10.971240000000002],
          [-122.58796699999999, 10.972009999999997],
          [-122.615159, 10.987841000000003],
          [-122.64647199999999, 10.997163],
          [-122.688131, 10.996125999999997],
          [-122.70708300000001, 10.991159000000003],
          [-122.71617699999999, 10.986679000000002],
          [-122.720404, 10.980223000000002],
          [-122.70720299999999, 10.972023],
          [-122.705096, 10.966009999999997],
          [-122.70845299999999, 10.958480000000002],
          [-122.770983, 10.932735999999998],
          [-122.83424500000001, 10.926851],
          [-122.845475, 10.926988999999999],
          [-122.881639, 10.934784999999998],
          [-122.91368399999999, 10.950273000000003],
          [-122.970632, 11.001038999999999],
          [-122.96587199999999, 11.017373999999997],
          [-122.95897500000001, 11.020150999999998],
          [-122.95782600000001, 11.031826000000002],
          [-122.96489700000001, 11.042200999999999],
          [-123.00155000000001, 11.080043000000003],
          [-123.05163099999999, 11.103267000000002],
          [-123.07146800000001, 11.110477000000003],
          [-123.07900599999999, 11.111652],
          [-123.11150699999999, 11.109704999999998],
          [-123.16503599999999, 11.099214000000003],
          [-123.18842799999999, 11.090902999999997],
          [-123.213009, 11.066741999999998],
          [-123.22509199999999, 11.049683000000002],
          [-123.226313, 11.042693999999997],
          [-123.219018, 11.030281000000002],
          [-123.213281, 11.026138000000003],
          [-123.189447, 11.016677999999999],
          [-123.17482999999999, 11.013100000000001],
          [-123.148615, 11.014023000000002],
          [-123.06700799999999, 10.979301999999997],
          [-123.050467, 10.969071],
          [-123.03606199999999, 10.942543999999998],
          [-123.06560200000001, 10.926172000000001],
          [-123.14957999999999, 10.885905999999999],
          [-123.21439799999999, 10.847487000000001],
          [-123.29980900000001, 10.829231999999998],
          [-123.35299699999999, 10.810174000000004],
          [-123.37320700000001, 10.800840999999998],
          [-123.372806, 10.790936000000002],
          [-123.34265500000001, 10.765104000000001],
          [-123.32284899999999, 10.750279999999997],
          [-123.22839099999999, 10.753512999999998],
          [-123.219765, 10.755071999999998],
          [-123.188853, 10.773716999999998],
          [-123.18429499999999, 10.774912],
          [-123.14408900000001, 10.761499],
          [-123.10755800000001, 10.732751999999998],
          [-123.05722800000001, 10.688101000000003],
          [-123.05097, 10.672263000000001],
          [-123.05908500000001, 10.661071999999997],
          [-123.096744, 10.661597],
          [-123.12573800000001, 10.66818],
          [-123.14092500000001, 10.694828999999999],
          [-123.18540100000001, 10.700398],
          [-123.194952, 10.699024999999999],
          [-123.208775, 10.693136000000003],
          [-123.280633, 10.695366999999997],
          [-123.317996, 10.719937999999999],
          [-123.32745700000001, 10.743414000000001],
          [-123.331516, 10.747518],
          [-123.34479099999999, 10.751210999999998],
          [-123.36462599999999, 10.749464000000003],
          [-123.380618, 10.746175999999998],
          [-123.39196999999999, 10.741979999999998],
          [-123.423067, 10.720426000000003],
          [-123.42837700000001, 10.714818999999999],
          [-123.425477, 10.710081000000002],
          [-123.43936099999999, 10.655928000000003],
          [-123.472016, 10.656467999999997],
          [-123.48886100000001, 10.65511],
          [-123.572383, 10.623210999999998],
          [-123.581481, 10.616863000000002],
          [-123.58596700000001, 10.611643999999998],
          [-123.670838, 10.627825000000001],
          [-123.74731600000001, 10.635010999999999],
          [-123.80359000000001, 10.636498000000003],
          [-123.86120600000001, 10.632911],
          [-123.952391, 10.630460999999997],
          [-123.966307, 10.631681],
          [-124.08489399999999, 10.620131],
          [-124.17961700000001, 10.599187999999998],
          [-124.23247, 10.585493999999997],
          [-124.257585, 10.572721999999999],
          [-124.33140399999999, 10.530431],
          [-124.337538, 10.524259],
          [-124.341474, 10.495266],
          [-124.33604199999999, 10.484509000000003],
          [-124.33653000000001, 10.480977000000003],
          [-124.35270399999999, 10.465023000000002],
          [-124.41682, 10.431713000000002],
          [-124.45655400000001, 10.419856000000003],
          [-124.499034, 10.414225000000002],
          [-124.51997, 10.414651999999997],
          [-124.58277799999999, 10.405701999999998],
          [-124.601607, 10.402451],
          [-124.640457, 10.391165999999998],
          [-124.743977, 10.394216],
          [-124.789357, 10.402012],
          [-124.844931, 10.417583],
          [-124.87607499999999, 10.443494999999999],
          [-124.877373, 10.449908],
          [-124.904077, 10.499195],
          [-124.91005899999999, 10.507542],
          [-124.93612200000001, 10.521253000000002],
          [-124.944636, 10.532902999999997],
          [-124.949781, 10.575696999999998],
          [-124.948789, 10.579877000000003],
          [-124.932187, 10.598745000000001],
          [-124.91875999999999, 10.605305999999999],
          [-124.83193600000001, 10.629027999999998],
          [-124.761347, 10.640633999999999],
          [-124.741815, 10.645440999999998],
          [-124.727654, 10.650956999999998],
          [-124.709465, 10.661518000000001],
          [-124.629661, 10.756031],
          [-124.576896, 10.824564000000002],
          [-124.56154599999999, 10.850834999999996],
          [-124.56405000000001, 10.875539000000003],
          [-124.55025599999999, 10.888784999999999],
          [-124.48678000000001, 10.922441],
          [-124.43528, 10.933126000000001],
          [-124.427303, 10.932848999999997],
          [-124.37344100000001, 10.915348999999999],
          [-124.36163099999999, 10.907390999999997],
          [-124.35333, 10.898327000000002],
          [-124.343534, 10.894139000000003],
          [-124.29503299999999, 10.902121999999999],
          [-124.20706999999999, 10.927577999999997],
          [-124.20489699999999, 10.931240000000003],
          [-124.164342, 10.953532000000003],
          [-124.11919599999999, 10.969416000000002],
          [-124.10933299999999, 10.963999000000001],
          [-124.08679799999999, 10.963396000000003],
          [-124.06116399999999, 10.964708000000002],
          [-124.030708, 10.969817999999997],
          [-123.994179, 10.983314999999997],
          [-123.96473, 10.997337000000002],
          [-123.93036900000001, 11.017646],
          [-123.909222, 11.032089],
          [-123.89469500000001, 11.039115000000002],
          [-123.88486900000001, 11.039909000000002],
          [-123.87214399999999, 11.037399],
          [-123.85426000000001, 11.037796],
          [-123.81577899999999, 11.044625000000003],
          [-123.79073299999999, 11.052582999999998],
          [-123.774093, 11.055779999999999],
          [-123.740737, 11.048265999999998],
          [-123.64683400000001, 11.044466999999997],
          [-123.56815900000001, 11.049145000000003],
          [-123.532962, 11.048881000000002],
          [-123.527109, 11.040871000000003],
          [-123.534638, 11.025305000000003],
          [-123.53008700000001, 11.016660000000002],
          [-123.4615, 10.982511000000002],
          [-123.44285400000001, 10.969875000000002],
          [-123.42954800000001, 10.954759000000003],
          [-123.41804200000001, 10.938499],
          [-123.39829399999999, 10.902371000000002],
          [-123.399292, 10.894011999999996],
          [-123.408027, 10.88458],
          [-123.41587200000001, 10.859651999999997],
          [-123.414691, 10.856090000000002],
          [-123.410594, 10.854576000000002],
          [-123.39139700000001, 10.855331],
          [-123.33423400000001, 10.872948000000001],
          [-123.318885, 10.880119999999998],
          [-123.31916100000001, 10.899026],
          [-123.31995599999999, 10.903084999999997],
          [-123.336739, 10.91749],
          [-123.344402, 10.919333000000002],
          [-123.34773000000001, 10.925092999999997],
          [-123.34373500000001, 10.950415999999997],
          [-123.33839499999999, 10.956190999999997],
          [-123.323106, 10.959929000000002],
          [-123.314592, 10.958862000000003],
          [-123.290908, 10.945977],
          [-123.279586, 10.944848999999998],
          [-123.2399, 10.954616999999999],
          [-123.237414, 10.959291999999998],
          [-123.24000799999999, 10.962885],
          [-123.293205, 11.006864999999998],
          [-123.297876, 11.057619000000003],
          [-123.31069400000001, 11.105612],
          [-123.31406899999999, 11.105145999999998],
          [-123.31873100000001, 11.110810999999998],
          [-123.319873, 11.119622],
          [-123.314652, 11.126311999999999],
          [-123.268767, 11.145465000000002],
          [-123.20086699999999, 11.166057000000002],
          [-123.157532, 11.175510000000003],
          [-123.10501099999999, 11.183979],
          [-123.08163400000001, 11.180408999999997],
          [-123.08071899999999, 11.176861000000002],
          [-123.070494, 11.174114000000003],
          [-123.03225599999999, 11.172147000000002],
          [-122.95718199999999, 11.171270999999997],
          [-122.882292, 11.183250999999998],
          [-122.86152000000001, 11.198338999999997],
          [-122.84013999999999, 11.224043000000002],
          [-122.84317200000001, 11.242564000000002],
          [-122.85183900000001, 11.247317000000002],
          [-122.85659999999999, 11.248721000000003],
          [-122.86947799999999, 11.248086],
          [-122.88089199999999, 11.239564000000001],
          [-122.89402000000001, 11.243046],
          [-122.900454, 11.246416000000004],
          [-122.901644, 11.247652000000002],
          [-122.900027, 11.252465999999998],
          [-122.88811799999999, 11.270423999999998],
          [-122.881779, 11.273775999999998],
          [-122.750371, 11.307623],
          [-122.73181600000001, 11.307828999999998],
          [-122.704747, 11.320295999999999],
          [-122.680487, 11.337004],
          [-122.64991, 11.364151],
          [-122.61988299999999, 11.40867],
          [-122.580524, 11.446514],
          [-122.575826, 11.446832999999998],
          [-122.53321600000001, 11.467624999999998],
          [-122.504041, 11.482556000000002],
          [-122.483687, 11.495237000000003],
          [-122.403581, 11.557465],
          [-122.326436, 11.613917999999998],
          [-122.286002, 11.649287999999999],
          [-122.246972, 11.680013000000002],
          [-122.19307800000001, 11.702178000000004],
          [-122.14705900000001, 11.718415],
          [-122.090664, 11.753427000000002],
          [-122.079678, 11.763399999999997],
          [-121.972624, 11.800525999999998],
          [-121.898956, 11.833464],
          [-121.85843, 11.865402000000003],
          [-121.816225, 11.888993],
          [-121.80783299999999, 11.891953999999998],
          [-121.77340899999999, 11.897309999999997],
          [-121.71228300000001, 11.904232],
          [-121.640007, 11.919502999999999],
          [-121.58560399999999, 11.937323999999997],
          [-121.45044200000001, 11.954484999999998],
          [-121.38055700000001, 11.965618],
          [-121.29077699999999, 11.983130000000003],
          [-121.280307, 11.979323],
          [-121.27833000000001, 11.974912000000003],
          [-121.262763, 11.958734],
          [-121.230444, 11.947467000000003],
          [-121.211273, 11.951712],
          [-121.09661700000001, 11.954752],
          [-121.07638299999999, 11.942079],
          [-121.049162, 11.945407000000003],
          [-121.027739, 11.954554000000002],
          [-121.02337600000001, 11.959468000000001],
          [-120.89868200000001, 11.999014000000003],
          [-120.87322900000001, 12.001448000000003],
          [-120.86325, 11.996237],
          [-120.81420499999999, 11.953834],
          [-120.812893, 11.950857999999997],
          [-120.81411299999999, 11.930633],
          [-120.81820099999999, 11.910749000000003],
          [-120.83507700000001, 11.915747000000003],
          [-120.84966499999999, 11.916227],
          [-120.923856, 11.900573999999999],
          [-120.95156, 11.852772000000002],
          [-120.94640000000001, 11.834881000000003],
          [-120.940845, 11.822529000000003],
          [-120.93059099999999, 11.814357999999999],
          [-120.806014, 11.738241000000002],
          [-120.76522800000001, 11.757174],
          [-120.730726, 11.747664],
          [-120.668102, 11.723556000000002],
          [-120.661205, 11.723427000000001],
          [-120.65556000000001, 11.730041],
          [-120.65546800000001, 11.739868000000001],
          [-120.663037, 11.745491000000001],
          [-120.675794, 11.751410999999997],
          [-120.69522699999999, 11.755074999999998],
          [-120.751236, 11.779364000000001],
          [-120.75770499999999, 11.785840999999998],
          [-120.76915500000001, 11.858268000000002],
          [-120.734182, 11.870995],
          [-120.69759099999999, 11.862395999999997],
          [-120.63908799999999, 11.8583],
          [-120.56401399999999, 11.863719000000003],
          [-120.550343, 11.867548999999997],
          [-120.53258199999999, 11.869891000000003],
          [-120.508433, 11.869379000000002],
          [-120.494678, 11.864193],
          [-120.477892, 11.841099],
          [-120.479355, 11.822361],
          [-120.43873500000001, 11.789608000000001],
          [-120.38587799999999, 11.796444999999999],
          [-120.34287599999999, 11.778165999999999],
          [-120.293924, 11.765555999999997],
          [-120.27738199999999, 11.765861000000001],
          [-120.264568, 11.775723],
          [-120.26893000000001, 11.784278],
          [-120.293498, 11.801788000000002],
          [-120.31565499999999, 11.814543999999998],
          [-120.317826, 11.818983000000003],
          [-120.27253300000001, 11.831673000000002],
          [-120.273176, 11.856881000000001],
          [-120.325637, 11.867857999999998],
          [-120.380573, 11.889456000000003],
          [-120.42073500000001, 11.908619999999999],
          [-120.486594, 11.924168000000002],
          [-120.53575900000001, 11.939616999999998],
          [-120.53368499999999, 11.95995],
          [-120.527094, 11.973011],
          [-120.526362, 11.982433],
          [-120.529292, 11.986103],
          [-120.53454099999999, 11.989497999999998],
          [-120.559597, 11.996837999999997],
          [-120.56760399999999, 11.99167],
          [-120.57089500000001, 11.988928999999999],
          [-120.574397, 11.986552000000003],
          [-120.57665499999999, 11.985416],
          [-120.58084, 11.984079000000001],
          [-120.58349100000001, 11.986468000000002],
          [-120.568356, 12.004061999999998],
          [-120.488708, 12.077213999999998],
          [-120.48220800000001, 12.085234],
          [-120.45141699999999, 12.125563999999997],
          [-120.411381, 12.194138000000002],
          [-120.39633799999999, 12.231774999999999],
          [-120.383094, 12.251351999999997],
          [-120.357156, 12.279581999999998],
          [-120.340249, 12.291271000000002],
          [-120.31589600000001, 12.302227000000002],
          [-120.27460400000001, 12.317151000000003],
          [-120.22287800000001, 12.346868],
          [-120.208383, 12.358021999999998],
          [-120.19632899999999, 12.372549999999997],
          [-120.146252, 12.400176000000002],
          [-120.082592, 12.411093999999999],
          [-120.001477, 12.442200999999997],
          [-119.97675799999999, 12.453951000000004],
          [-119.93833699999999, 12.474192000000002],
          [-119.828049, 12.543934999999998],
          [-119.81547699999999, 12.548941],
          [-119.636156, 12.597389999999997],
          [-119.53496100000001, 12.626528999999998],
          [-119.43938, 12.641331999999998],
          [-119.36943400000001, 12.657072999999997],
          [-119.324421, 12.670355999999998],
          [-119.264871, 12.703136],
          [-119.27989400000001, 12.715667000000003],
          [-119.263113, 12.723320999999999],
          [-119.156455, 12.763323999999997],
          [-119.106652, 12.781126],
          [-119.093468, 12.783704],
          [-119.038354, 12.806005999999996],
          [-119.018304, 12.815094000000002],
          [-118.957471, 12.851184000000003],
          [-118.95533800000001, 12.849761999999998],
          [-118.953543, 12.843418],
          [-118.93358900000001, 12.827905000000001],
          [-118.91073, 12.814796999999999],
          [-118.893212, 12.805788],
          [-118.89321100000001, 12.805788]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02275",
      state: "02",
      county: "275",
      name: "Wrangell County",
      lsad: "Cty&Bor",
      censusArea: 2541.483,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.19954899999999, 11.638593],
          [-92.215409, 11.682270000000003],
          [-92.224167, 11.701766],
          [-92.23753199999999, 11.711347000000004],
          [-92.260119, 11.732292999999999],
          [-92.283594, 11.761774000000003],
          [-92.280431, 11.765599000000002],
          [-92.265071, 11.762174000000002],
          [-92.251732, 11.756247000000002],
          [-92.229647, 11.740488],
          [-92.206951, 11.736987],
          [-92.19047900000001, 11.742500999999997],
          [-92.18547799999999, 11.753160999999999],
          [-92.18498199999999, 11.778776],
          [-92.18749399999999, 11.785595],
          [-92.18316300000001, 11.800829999999998],
          [-92.130413, 11.811419],
          [-92.113361, 11.812717999999997],
          [-92.08605, 11.832436000000001],
          [-92.06741199999999, 11.875078000000002],
          [-92.04179500000001, 11.958795000000002],
          [-92.07033999999999, 12.046733000000003],
          [-92.038364, 12.091296999999997],
          [-92.033368, 12.095571999999997],
          [-92.01129700000001, 12.077359000000001],
          [-91.970294, 12.114041999999998],
          [-91.93572800000001, 12.177207000000003],
          [-91.94340199999999, 12.192557],
          [-91.95883799999999, 12.194761999999997],
          [-91.99389400000001, 12.193351],
          [-92.01813000000001, 12.183155],
          [-92.03484900000001, 12.133431999999999],
          [-92.060993, 12.129354999999997],
          [-92.10401999999999, 12.108108999999999],
          [-92.17695499999999, 12.055706],
          [-92.129697, 11.957855000000002],
          [-92.13547399999999, 11.941626],
          [-92.159064, 11.922559999999997],
          [-92.170198, 11.919231000000003],
          [-92.191893, 11.921717000000001],
          [-92.224241, 11.930421000000003],
          [-92.27996200000001, 11.924838999999999],
          [-92.32048700000001, 11.887647999999999],
          [-92.31979899999999, 11.874347],
          [-92.30930599999999, 11.865059000000002],
          [-92.30994899999999, 11.862301000000002],
          [-92.323242, 11.851878],
          [-92.372298, 11.850358999999997],
          [-92.376518, 11.853377000000002],
          [-92.39730399999999, 11.878867],
          [-92.398349, 11.884051999999997],
          [-92.39707999999999, 11.905546000000001],
          [-92.40119200000001, 11.950467000000003],
          [-92.446166, 12.018666000000003],
          [-92.492795, 12.066436000000003],
          [-92.522076, 12.077035000000002],
          [-92.573677, 12.070700000000002],
          [-92.621793, 12.05614],
          [-92.62149199999999, 12.049174],
          [-92.629155, 12.037424999999999],
          [-92.64007899999999, 12.033194000000002],
          [-92.68462, 12.082323000000002],
          [-92.708697, 12.112124000000001],
          [-92.72339600000001, 12.145814000000001],
          [-92.718342, 12.217703999999998],
          [-92.689888, 12.238743999999997],
          [-92.672471, 12.239438999999997],
          [-92.66421199999999, 12.236331999999997],
          [-92.64425, 12.232807000000001],
          [-92.61579699999999, 12.234172000000001],
          [-92.601495, 12.240065000000001],
          [-92.58206999999999, 12.278815999999999],
          [-92.582033, 12.285456000000003],
          [-92.57502299999999, 12.296467999999997],
          [-92.54307600000001, 12.332276],
          [-92.52936, 12.338555],
          [-92.44183899999999, 12.353983],
          [-92.43163100000001, 12.352162999999997],
          [-92.42204100000001, 12.349341000000003],
          [-92.40367800000001, 12.334811000000002],
          [-92.381766, 12.310755999999998],
          [-92.380574, 12.307785000000003],
          [-92.38279299999999, 12.299202999999999],
          [-92.363966, 12.287126],
          [-92.35871, 12.290799999999997],
          [-92.34914900000001, 12.304456000000002],
          [-92.340678, 12.341754000000002],
          [-92.361132, 12.380626999999997],
          [-92.39426800000001, 12.485579000000001],
          [-92.38937999999999, 12.491366999999997],
          [-92.38237899999999, 12.491971999999997],
          [-92.36255600000001, 12.487904],
          [-92.25339299999999, 12.449539000000001],
          [-92.24547899999999, 12.441215],
          [-92.233927, 12.416736],
          [-92.24199999999999, 12.41366],
          [-92.238473, 12.398705999999997],
          [-92.22313600000001, 12.384017],
          [-92.20436699999999, 12.372086000000003],
          [-92.19926899999999, 12.371054],
          [-92.18115800000001, 12.387127999999997],
          [-92.17935, 12.390822999999997],
          [-92.181647, 12.399335999999998],
          [-92.20856800000001, 12.457124999999998],
          [-92.23904300000001, 12.476671000000003],
          [-92.259611, 12.487630000000003],
          [-92.279753, 12.485881],
          [-92.29047499999999, 12.487017000000002],
          [-92.357564, 12.529007999999997],
          [-92.36129299999999, 12.534232000000003],
          [-92.367088, 12.574578000000002],
          [-92.36383599999999, 12.588613000000002],
          [-92.35840999999999, 12.595266000000002],
          [-92.31930299999999, 12.607115999999998],
          [-92.29728800000001, 12.629818999999998],
          [-92.28421599999999, 12.636699],
          [-92.280089, 12.651834000000001],
          [-92.281464, 12.665593000000001],
          [-92.298664, 12.677976999999998],
          [-92.31379899999999, 12.676600999999998],
          [-92.32480699999999, 12.673848999999997],
          [-92.348886, 12.664217],
          [-92.371589, 12.672472999999997],
          [-92.389476, 12.672472999999997],
          [-92.40392299999999, 12.669721000000003],
          [-92.45208099999999, 12.672472999999997],
          [-92.467904, 12.680729],
          [-92.528446, 12.702055999999999],
          [-92.52239800000001, 12.716256000000001],
          [-92.322505, 12.738582000000001],
          [-92.23404099999999, 12.881504],
          [-92.08011200000001, 12.924152999999997],
          [-92.00510700000001, 12.84292],
          [-91.93658199999999, 12.817425],
          [-91.88650000000001, 12.776083],
          [-91.818813, 12.664693999999997],
          [-91.736007, 12.613169999999997],
          [-91.581221, 12.613275000000002],
          [-91.428413, 12.537135],
          [-91.167925, 12.448360999999998],
          [-91.087433, 12.407420000000002],
          [-91.09622999999999, 12.335166000000001],
          [-90.99865399999999, 12.274040999999997],
          [-91.10669899999999, 12.195124],
          [-91.23856599999999, 12.170968000000002],
          [-91.25335799999999, 12.206850000000003],
          [-91.437095, 12.140003999999998],
          [-91.369226, 12.06991],
          [-91.37185299999999, 12.013252000000001],
          [-91.498849, 12.019097000000002],
          [-91.653403, 12.103850999999999],
          [-91.69269299999999, 12.050604],
          [-91.82960800000001, 12.056306999999997],
          [-91.90461300000001, 11.972971999999999],
          [-91.871938, 11.948883000000002],
          [-92.01103699999999, 11.854788],
          [-91.93563499999999, 11.798431],
          [-91.96274399999999, 11.700757000000003],
          [-92.05882500000001, 11.710929999999998],
          [-92.09117800000001, 11.660702999999998],
          [-92.19954899999999, 11.638593]
        ],
        [
          [-92.97716299999999, 12.439672999999999],
          [-92.95736400000001, 12.448963],
          [-92.927663, 12.456859000000001],
          [-92.896342, 12.457977999999997],
          [-92.87191899999999, 12.457037999999997],
          [-92.84318400000001, 12.444826999999997],
          [-92.819256, 12.439511000000003],
          [-92.808145, 12.440801],
          [-92.79187200000001, 12.449168999999998],
          [-92.78286399999999, 12.451529999999998],
          [-92.734466, 12.458514999999998],
          [-92.71605600000001, 12.454861000000001],
          [-92.668127, 12.440278999999997],
          [-92.634335, 12.422173999999998],
          [-92.628592, 12.416283999999997],
          [-92.620608, 12.391199999999998],
          [-92.65238, 12.375878999999998],
          [-92.66217800000001, 12.369134000000003],
          [-92.67940100000001, 12.354298999999997],
          [-92.684112, 12.345671000000003],
          [-92.67655300000001, 12.333105000000003],
          [-92.662478, 12.320450999999998],
          [-92.65546699999999, 12.303756],
          [-92.65551300000001, 12.295575],
          [-92.662081, 12.274794999999997],
          [-92.72125399999999, 12.258463999999996],
          [-92.77604500000001, 12.254584999999999],
          [-92.843716, 12.238933000000003],
          [-92.87758199999999, 12.240321999999999],
          [-93.01058699999999, 12.309491999999999],
          [-93.04538299999999, 12.320782999999999],
          [-93.067556, 12.333573000000001],
          [-93.070863, 12.354194],
          [-93.06944100000001, 12.356425999999999],
          [-93.060361, 12.358378000000002],
          [-93.045714, 12.371451],
          [-93.006575, 12.415880999999999],
          [-93.006314, 12.417777999999998],
          [-93.01087100000001, 12.421404000000003],
          [-93.010817, 12.424264],
          [-93.00235699999999, 12.430655000000002],
          [-92.97716299999999, 12.439672999999999]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02240",
      state: "02",
      county: "240",
      name: "Southeast Fairbanks County",
      lsad: "CA",
      censusArea: 24768.808,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.48411899999999, 19.173325],
          [-106.489541, 19.482913000000003],
          [-106.97548900000001, 19.479746],
          [-106.972014, 19.911791],
          [-107.003185, 20.258927999999997],
          [-106.35735, 20.278453],
          [-106.22706400000001, 20.363752000000005],
          [-104.04652999999999, 20.666770999999997],
          [-104.061159, 20.683442],
          [-103.751216, 20.647777000000005],
          [-103.647213, 20.669506],
          [-103.433715, 20.821827999999996],
          [-103.50364300000001, 20.858250999999996],
          [-103.525172, 20.960080000000005],
          [-103.36447799999999, 20.996995999999996],
          [-103.081816, 21.120936999999998],
          [-102.84708900000001, 21.145563999999993],
          [-102.7319, 21.233638999999997],
          [-102.72636399999999, 21.307742000000005],
          [-102.62997100000001, 21.385368],
          [-102.487301, 21.371898],
          [-102.30708100000001, 21.440633000000005],
          [-102.12137999999999, 21.471689999999995],
          [-101.855761, 21.445786],
          [-101.78905, 21.501546000000005],
          [-101.387842, 21.614395000000002],
          [-101.33622399999999, 21.711613999999997],
          [-101.093233, 21.827388],
          [-101.002465, 21.839421],
          [-101.00201999999999, 17.901922],
          [-101.82864799999999, 17.90103],
          [-101.830194, 18.127580000000002],
          [-102.017011, 18.127586],
          [-101.97841700000001, 18.165011],
          [-101.96370300000001, 18.510627999999997],
          [-102.32059799999999, 18.598671000000003],
          [-102.31453099999999, 18.683951999999998],
          [-102.629088, 18.683656999999997],
          [-102.736106, 18.702022999999997],
          [-102.84215499999999, 18.597198],
          [-103.10475299999999, 18.613456],
          [-103.15919299999999, 18.636952],
          [-103.13845900000001, 18.691159],
          [-103.00772799999999, 18.766011],
          [-103.196314, 18.826152],
          [-103.02748299999999, 18.935423999999998],
          [-103.00121200000001, 18.993786999999998],
          [-103.088616, 19.008567],
          [-103.12685400000001, 19.115887999999998],
          [-103.876857, 19.113152999999997],
          [-104.576439, 19.117871],
          [-104.57542699999999, 19.133381],
          [-105.14842200000001, 19.133567999999997],
          [-105.14888099999999, 19.222211],
          [-106.111233, 19.221249999999998],
          [-106.140827, 19.184013],
          [-106.32926, 19.190562],
          [-106.48411899999999, 19.173325]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02090",
      state: "02",
      county: "090",
      name: "Fairbanks North Star County",
      lsad: "Borough",
      censusArea: 7338.209,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.995157, 20.341690999999997],
          [-108.229784, 20.436621000000002],
          [-108.242175, 20.483425999999994],
          [-108.38050899999999, 20.520561999999998],
          [-108.441058, 20.577791000000005],
          [-108.65236999999999, 20.591053000000002],
          [-108.642697, 20.865694000000005],
          [-108.66384400000001, 21.211354999999998],
          [-107.55024499999999, 21.211022999999997],
          [-107.334764, 21.273291],
          [-107.017089, 21.290431999999996],
          [-106.919725, 21.27297],
          [-106.56881899999999, 21.344746],
          [-106.490362, 21.386877999999996],
          [-106.192406, 21.454475000000002],
          [-106.11975799999999, 21.405822999999998],
          [-105.994261, 21.406031],
          [-106.13753, 21.309850999999995],
          [-106.078123, 21.245244],
          [-105.873843, 21.214026000000004],
          [-105.615545, 21.14407],
          [-105.74968100000001, 21.098232999999993],
          [-105.64643699999999, 21.033682],
          [-105.41748000000001, 21.084835999999996],
          [-105.20990599999999, 21.072978000000006],
          [-104.89138700000001, 21.137197],
          [-104.44544300000001, 21.062319000000002],
          [-104.24718999999999, 21.118774000000002],
          [-103.977969, 21.119043000000005],
          [-103.884795, 21.090281000000004],
          [-104.105524, 21.015541999999996],
          [-103.99204499999999, 20.977011000000005],
          [-104.12008900000001, 20.798316999999997],
          [-104.061159, 20.683442],
          [-104.04652999999999, 20.666770999999997],
          [-106.22706400000001, 20.363752000000005],
          [-106.35735, 20.278453],
          [-107.003185, 20.258927999999997],
          [-107.76155700000001, 20.257853999999995],
          [-107.995157, 20.341690999999997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02100",
      state: "02",
      county: "100",
      name: "Haines County",
      lsad: "Borough",
      censusArea: 2318.6,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.21912800000001, 14.974972000000001],
          [-95.175756, 14.973868000000003],
          [-95.180116, 14.997871000000004],
          [-95.208585, 15.076824000000002],
          [-95.23826700000001, 15.130133999999998],
          [-95.283964, 15.192532],
          [-95.36833100000001, 15.263275],
          [-95.430331, 15.327199999999998],
          [-95.43643800000001, 15.315790999999997],
          [-95.444526, 15.277689000000002],
          [-95.42960099999999, 15.242722],
          [-95.40368699999999, 15.222766999999997],
          [-95.36406, 15.211402999999997],
          [-95.342254, 15.177824999999999],
          [-95.32945799999999, 15.152700000000003],
          [-95.377826, 15.099262000000003],
          [-95.38235399999999, 15.060749000000001],
          [-95.382641, 15.033366000000001],
          [-95.33937499999999, 15.017667000000003],
          [-95.32671099999999, 15.011468999999998],
          [-95.32260400000001, 15.007812000000001],
          [-95.297293, 14.959859999999999],
          [-95.29862700000001, 14.919262000000003],
          [-95.30590699999999, 14.901339999999998],
          [-95.322622, 14.900661],
          [-95.33994799999999, 14.888955000000003],
          [-95.28465700000001, 14.818114000000001],
          [-95.274203, 14.813122],
          [-95.248985, 14.790878],
          [-95.2432, 14.783112000000003],
          [-95.233878, 14.735487],
          [-95.14232200000001, 14.616370000000003],
          [-95.135843, 14.588225000000001],
          [-95.137516, 14.577835],
          [-95.14216099999999, 14.577106999999998],
          [-95.145521, 14.578332000000003],
          [-95.153827, 14.586626000000003],
          [-95.159062, 14.595525000000002],
          [-95.18635699999999, 14.597769999999997],
          [-95.19054399999999, 14.592416999999998],
          [-95.189368, 14.576244000000003],
          [-95.165861, 14.546605],
          [-95.132273, 14.496535999999999],
          [-95.08898300000001, 14.423022000000003],
          [-95.05807100000001, 14.349446999999998],
          [-95.04906199999999, 14.309294999999999],
          [-95.05348799999999, 14.290498],
          [-95.05655200000001, 14.288699000000001],
          [-95.060452, 14.290337999999998],
          [-95.06977499999999, 14.302694000000002],
          [-95.09581399999999, 14.297232999999999],
          [-95.10121000000001, 14.292606999999997],
          [-95.1077, 14.265034],
          [-95.099106, 14.245095999999997],
          [-95.056227, 14.189884],
          [-95.07326900000001, 14.190575000000003],
          [-95.087872, 14.200073000000003],
          [-95.11286799999999, 14.201552999999997],
          [-95.159055, 14.210177999999999],
          [-95.220281, 14.235584000000003],
          [-95.227736, 14.236899999999999],
          [-95.24670900000001, 14.236367999999999],
          [-95.277198, 14.233634000000002],
          [-95.2877, 14.234932999999998],
          [-95.30650700000001, 14.242916000000001],
          [-95.34486799999999, 14.270795],
          [-95.39826, 14.327689],
          [-95.40805900000001, 14.342998999999999],
          [-95.43306100000001, 14.399898999999998],
          [-95.44738100000001, 14.399890999999997],
          [-95.496814, 14.498601999999998],
          [-95.318523, 14.500523000000001],
          [-95.413387, 14.584665000000001],
          [-95.40194500000001, 14.616146],
          [-95.52815000000001, 14.655985000000001],
          [-95.548348, 14.698345000000003],
          [-95.75708599999999, 14.742314],
          [-95.682038, 14.85819],
          [-95.522232, 14.889400000000002],
          [-95.604265, 14.909481],
          [-95.58530099999999, 14.986032999999999],
          [-95.68824799999999, 15.016725999999998],
          [-95.70186799999999, 15.104076],
          [-95.892056, 15.172783000000003],
          [-96.05353400000001, 15.177537999999998],
          [-96.122285, 15.207774999999998],
          [-96.36885000000001, 15.232652000000002],
          [-96.48660899999999, 15.261108],
          [-96.466815, 15.284252000000002],
          [-96.47294099999999, 15.421683000000002],
          [-96.365825, 15.448008000000002],
          [-96.35814099999999, 15.449798999999999],
          [-96.30184600000001, 15.464128000000002],
          [-96.234229, 15.524731000000003],
          [-96.23733999999999, 15.558734000000001],
          [-96.22574800000001, 15.604573000000002],
          [-96.19035199999999, 15.639854],
          [-96.005158, 15.658160000000002],
          [-95.85416599999999, 15.691845999999998],
          [-95.72246000000001, 15.729526],
          [-95.37582, 15.340620999999999],
          [-95.029245, 15.345363999999996],
          [-95.00518, 15.357216999999999],
          [-94.99211700000001, 15.330694999999999],
          [-94.961972, 15.280375999999997],
          [-94.76620399999999, 15.231430000000003],
          [-94.68192400000001, 15.190843000000001],
          [-94.566689, 15.128278000000002],
          [-94.48124100000001, 15.128070999999998],
          [-94.38979800000001, 15.032673000000003],
          [-94.383637, 15.001866999999997],
          [-94.369945, 14.971744999999999],
          [-94.36377300000001, 14.963890999999997],
          [-95.17576, 14.973866999999998],
          [-95.21912800000001, 14.974972000000001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02170",
      state: "02",
      county: "170",
      name: "Matanuska-Susitna County",
      lsad: "Borough",
      censusArea: 24607.898,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.97177500000001, 17.192041000000003],
          [-110.97266099999999, 17.253489000000002],
          [-111.332978, 17.254634000000003],
          [-111.33417499999999, 17.424601000000003],
          [-113.00218100000001, 17.425686],
          [-113.00128799999999, 18.106276],
          [-113.001262, 18.295330999999997],
          [-113.001339, 18.72744],
          [-111.893149, 18.722991999999998],
          [-111.88896299999999, 18.795347999999997],
          [-111.38790399999999, 18.910327000000002],
          [-109.50698399999999, 19.332164],
          [-108.035867, 19.330788],
          [-108.032727, 19.469757],
          [-106.97548900000001, 19.479746],
          [-106.489541, 19.482913000000003],
          [-106.48411899999999, 19.173325],
          [-106.42864800000001, 18.863287999999997],
          [-106.426334, 18.247221000000003],
          [-106.981731, 18.248680999999998],
          [-106.961638, 18.160210999999997],
          [-106.941834, 17.811906999999998],
          [-106.94512399999999, 17.474348999999997],
          [-107.20570699999999, 17.475073000000002],
          [-107.206028, 17.425265000000003],
          [-108.46000699999999, 17.426972],
          [-109.18744699999999, 17.425285000000002],
          [-109.23755600000001, 17.483144000000003],
          [-109.359442, 17.483938000000002],
          [-109.79242300000001, 17.388896000000003],
          [-109.897888, 17.267043],
          [-109.98657600000001, 17.228323000000003],
          [-109.985874, 17.237515000000002],
          [-110.074793, 17.253500000000003],
          [-110.132634, 17.257914999999997],
          [-110.204894, 17.259548000000002],
          [-110.25429600000001, 17.254500999999998],
          [-110.26612399999999, 17.251237000000003],
          [-110.273575, 17.251559],
          [-110.286978, 17.257443000000002],
          [-110.303688, 17.257467],
          [-110.303639, 17.255871],
          [-110.31222600000001, 17.254029000000003],
          [-110.334576, 17.251300999999998],
          [-110.39441099999999, 17.249107000000002],
          [-110.42500000000001, 17.245552000000004],
          [-110.46881200000001, 17.244627],
          [-110.48439099999999, 17.247262],
          [-110.49572599999999, 17.251244999999997],
          [-110.56166999999999, 17.281487],
          [-110.59016600000001, 17.281487],
          [-110.59184200000001, 17.279485],
          [-110.60696100000001, 17.277732],
          [-110.62845899999999, 17.284407],
          [-110.646221, 17.296689],
          [-110.65580399999999, 17.298173],
          [-110.66262, 17.295355999999998],
          [-110.67124999999999, 17.273652],
          [-110.679902, 17.265887999999997],
          [-110.690497, 17.259296999999997],
          [-110.71129099999999, 17.251089],
          [-110.82729499999999, 17.228389999999997],
          [-110.84241, 17.224213],
          [-110.84884199999999, 17.220588],
          [-110.895905, 17.208914999999998],
          [-110.926773, 17.206350999999998],
          [-110.93925100000001, 17.210299],
          [-110.941944, 17.209947],
          [-110.94624300000001, 17.208644],
          [-110.94715500000001, 17.206299],
          [-110.94742500000001, 17.203139999999998],
          [-110.951153, 17.198777999999997],
          [-110.960114, 17.194037],
          [-110.97177500000001, 17.192041000000003]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02016",
      state: "02",
      county: "016",
      name: "Aleutians West County",
      lsad: "CA",
      censusArea: 4390.276,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-139.069176, 7.2628739999999965],
          [-139.07232, 7.250962999999999],
          [-139.097619, 7.226134999999999],
          [-139.126856, 7.219861999999999],
          [-139.136196, 7.229216000000001],
          [-139.14734, 7.276781],
          [-139.137239, 7.286006],
          [-139.113495, 7.300801],
          [-139.094665, 7.301228999999999],
          [-139.075466, 7.284618999999999],
          [-139.069176, 7.2628739999999965]
        ],
        [
          [-138.954338, 7.339247],
          [-138.95446, 7.3327310000000026],
          [-138.965171, 7.322682],
          [-138.979179, 7.31438],
          [-138.987236, 7.3110380000000035],
          [-138.990684, 7.311647999999998],
          [-138.992094, 7.381310999999997],
          [-138.977782, 7.3989290000000025],
          [-138.964323, 7.402492000000002],
          [-138.926874, 7.38364],
          [-138.914207, 7.363992000000003],
          [-138.908883, 7.340581999999998],
          [-138.954338, 7.339247]
        ],
        [
          [218.785825, 7.633434000000001],
          [218.804128, 7.635033999999997],
          [218.864937, 7.623133000000003],
          [218.90391, 7.614913999999999],
          [218.919136, 7.605545999999997],
          [218.917608, 7.594949],
          [218.918827, 7.588337000000003],
          [218.920826, 7.586137000000001],
          [219.002896, 7.552486000000002],
          [219.076803, 7.498517999999997],
          [219.101442, 7.485497000000002],
          [219.143656, 7.469442000000001],
          [219.164531, 7.464635000000001],
          [219.178081, 7.464812000000002],
          [219.191082, 7.462935000000002],
          [219.218031, 7.438940000000002],
          [219.226883, 7.423940999999999],
          [219.23027, 7.4134189999999975],
          [219.236253, 7.409605999999997],
          [219.263934, 7.405838000000003],
          [219.33498, 7.404933],
          [219.386221, 7.404401],
          [219.400809, 7.400556999999999],
          [219.462765, 7.376176000000001],
          [219.467581, 7.371628999999999],
          [219.450191, 7.365141999999999],
          [219.399469, 7.359433000000003],
          [219.389855, 7.361004000000001],
          [219.384679, 7.36421],
          [219.366788, 7.371836999999999],
          [219.261618, 7.357688000000003],
          [219.220471, 7.376666999999998],
          [219.218185, 7.387377000000001],
          [219.2065, 7.393284000000001],
          [219.097477, 7.440579999999997],
          [219.060354, 7.454875000000001],
          [219.034532, 7.478529999999999],
          [219.013516, 7.4972800000000035],
          [218.969019, 7.531236999999997],
          [218.951626, 7.541963000000003],
          [218.843631, 7.578642000000002],
          [218.831825, 7.580534],
          [218.795194, 7.575429],
          [218.785061, 7.571866],
          [218.767787, 7.576123000000003],
          [218.706047, 7.593181999999999],
          [218.634021, 7.623981000000001],
          [218.631609, 7.625782000000001],
          [218.625536, 7.637303000000003],
          [218.645511, 7.657634000000002],
          [218.664013, 7.661935],
          [218.675528, 7.659064000000001],
          [218.681998, 7.649946],
          [218.689903, 7.644421999999999],
          [218.738019, 7.632733999999999],
          [218.785825, 7.633434000000001]
        ],
        [
          [-136.762478, 7.8678779999999975],
          [-136.797799, 7.908512000000002],
          [-136.810433, 7.927089000000002],
          [-136.789558, 7.957211000000001],
          [-136.774023, 7.965895000000003],
          [-136.736549, 7.9698080000000004],
          [-136.72078, 7.969518000000001],
          [-136.698771, 7.9644540000000035],
          [-136.63051, 7.970351999999998],
          [-136.627155, 7.978293999999998],
          [-136.603598, 7.997056000000001],
          [-136.589955, 8.002741],
          [-136.579975, 8.003238000000003],
          [-136.560565, 7.9967320000000015],
          [-136.554398, 7.990659999999998],
          [-136.544867, 7.927244999999999],
          [-136.554661, 7.9098339999999965],
          [-136.558376, 7.908724999999997],
          [-136.566275, 7.914701999999998],
          [-136.568916, 7.921300000000002],
          [-136.565559, 7.925862000000002],
          [-136.569683, 7.951324],
          [-136.575635, 7.952641],
          [-136.578931, 7.951327999999997],
          [-136.582927, 7.947963999999999],
          [-136.616095, 7.903013000000001],
          [-136.620015, 7.895629999999997],
          [-136.623452, 7.883204999999997],
          [-136.625463, 7.859824000000003],
          [-136.576381, 7.842275000000001],
          [-136.543309, 7.838624000000003],
          [-136.517599, 7.839556999999999],
          [-136.507989, 7.845970000000001],
          [-136.398062, 7.867842000000003],
          [-136.311573, 7.872463000000003],
          [-136.290728, 7.872135999999998],
          [-136.287188, 7.870313000000003],
          [-136.281694, 7.863919000000003],
          [-136.26649, 7.817715999999997],
          [-136.268243, 7.785497999999997],
          [-136.273792, 7.772019],
          [-136.289921, 7.741678],
          [-136.343756, 7.731520000000003],
          [-136.474132, 7.7472080000000005],
          [-136.497054, 7.761426],
          [-136.509655, 7.763325999999999],
          [-136.51933, 7.758482000000001],
          [-136.582933, 7.691822000000002],
          [-136.608482, 7.693348999999998],
          [-136.70266, 7.685403999999998],
          [-136.713062, 7.683329999999998],
          [-136.735912, 7.662154000000001],
          [-136.751817, 7.635016999999998],
          [-136.801675, 7.613487999999997],
          [-136.809, 7.616235000000003],
          [-136.823682, 7.634011000000001],
          [-136.826252, 7.640931999999999],
          [-136.814437, 7.660249999999998],
          [-136.837514, 7.682744999999997],
          [-136.863062, 7.684921000000003],
          [-136.903184, 7.635648000000003],
          [-136.930952, 7.592469999999999],
          [-136.938917, 7.590981999999997],
          [-136.954147, 7.592568],
          [-136.984331, 7.602134999999997],
          [-136.987383, 7.606872000000003],
          [-136.991322, 7.6290520000000015],
          [-136.984489, 7.657411000000003],
          [-136.976249, 7.666400000000003],
          [-136.950128, 7.6867189999999965],
          [-136.930872, 7.697195000000001],
          [-136.906884, 7.696638999999998],
          [-136.896966, 7.700423999999998],
          [-136.873924, 7.724071000000002],
          [-136.870997, 7.7294100000000014],
          [-136.8707, 7.731968999999999],
          [-136.882018, 7.766627999999997],
          [-136.90503, 7.7715320000000006],
          [-136.918065, 7.788003000000003],
          [-136.917088, 7.797015999999999],
          [-136.911016, 7.807597000000001],
          [-136.904302, 7.811771999999998],
          [-136.856205, 7.8183659999999975],
          [-136.790163, 7.817216999999999],
          [-136.762478, 7.8678779999999975]
        ],
        [
          [-137.800647, 7.7782940000000025],
          [-137.796308, 7.770831000000001],
          [-137.813886, 7.754280000000001],
          [-137.842267, 7.7324800000000025],
          [-137.842419, 7.722645],
          [-137.838054, 7.717198000000003],
          [-137.827524, 7.712085999999999],
          [-137.826997, 7.705972000000003],
          [-137.841411, 7.68956],
          [-137.856332, 7.681015000000002],
          [-137.86796, 7.679374000000003],
          [-137.876811, 7.681410999999997],
          [-137.887768, 7.689483000000003],
          [-137.899416, 7.692557000000001],
          [-137.902693, 7.691580999999999],
          [-137.918806, 7.6743900000000025],
          [-137.928907, 7.655368000000003],
          [-137.929023, 7.65052],
          [-137.92564, 7.6424809999999965],
          [-137.915445, 7.630684000000002],
          [-137.903083, 7.606496999999997],
          [-137.906072, 7.597670000000001],
          [-137.909185, 7.596671000000001],
          [-137.930123, 7.601498999999997],
          [-137.944957, 7.6115390000000005],
          [-137.950665, 7.620001000000002],
          [-137.953024, 7.638174999999997],
          [-137.957443, 7.647148999999999],
          [-137.963852, 7.650230999999998],
          [-138.069823, 7.670676],
          [-138.086304, 7.663618],
          [-138.109378, 7.670461000000003],
          [-138.117864, 7.677830999999998],
          [-138.104285, 7.701538999999997],
          [-138.021818, 7.7069059999999965],
          [-137.962426, 7.719771999999999],
          [-137.956443, 7.722861999999999],
          [-137.947777, 7.740380999999999],
          [-137.946649, 7.752681000000003],
          [-137.950283, 7.765681999999998],
          [-137.956998, 7.772540999999997],
          [-137.965031, 7.778162000000002],
          [-137.995272, 7.781534999999998],
          [-138.039344, 7.778925000000001],
          [-138.059335, 7.786828999999997],
          [-138.08064, 7.798738999999998],
          [-138.086074, 7.808047000000002],
          [-138.172666, 7.839984999999999],
          [-138.215124, 7.857801000000002],
          [-138.224129, 7.864880999999997],
          [-138.227822, 7.873525999999998],
          [-138.224618, 7.8806749999999965],
          [-138.220742, 7.8848410000000015],
          [-138.19709, 7.905464000000002],
          [-138.175023, 7.911583999999998],
          [-138.145326, 7.917216000000003],
          [-138.124786, 7.920093000000001],
          [-138.090632, 7.9193989999999985],
          [-138.070548, 7.917408000000002],
          [-138.061147, 7.912539000000002],
          [-138.002345, 7.909967999999999],
          [-137.963723, 7.917918999999998],
          [-137.952094, 7.915348000000002],
          [-137.913269, 7.879747999999999],
          [-137.924315, 7.857522000000003],
          [-137.921569, 7.853883000000003],
          [-137.859763, 7.8269439999999975],
          [-137.852285, 7.826045000000001],
          [-137.759641, 7.831195000000001],
          [-137.691714, 7.843975],
          [-137.615311, 7.855080000000001],
          [-137.614511, 7.8530330000000035],
          [-137.625008, 7.8375290000000035],
          [-137.649208, 7.820726999999998],
          [-137.685555, 7.812745],
          [-137.692118, 7.813896999999997],
          [-137.735909, 7.807991000000001],
          [-137.797719, 7.793297000000003],
          [-137.800647, 7.7782940000000025]
        ],
        [
          [-137.360408, 7.727533000000001],
          [-137.39076, 7.733525],
          [-137.417678, 7.7308749999999975],
          [-137.444717, 7.725419000000002],
          [-137.463577, 7.713943],
          [-137.490005, 7.705106000000001],
          [-137.540393, 7.6987549999999985],
          [-137.570973, 7.698219999999999],
          [-137.608055, 7.705184000000003],
          [-137.616753, 7.703977999999999],
          [-137.631523, 7.696843999999999],
          [-137.640524, 7.672083999999998],
          [-137.635883, 7.659540999999997],
          [-137.651386, 7.653604000000001],
          [-137.670951, 7.663980000000002],
          [-137.707802, 7.703268000000001],
          [-137.705261, 7.707239999999999],
          [-137.697662, 7.713123000000003],
          [-137.639983, 7.736060999999999],
          [-137.597498, 7.726464],
          [-137.555197, 7.721125000000001],
          [-137.536977, 7.7214699999999965],
          [-137.515591, 7.724978],
          [-137.497974, 7.7386240000000015],
          [-137.4612, 7.750717999999999],
          [-137.281479, 7.784075000000001],
          [-137.238175, 7.7985200000000034],
          [-137.21193, 7.812331],
          [-137.205675, 7.820639],
          [-137.200825, 7.844605000000001],
          [-137.19912, 7.883141999999999],
          [-137.199764, 7.924816],
          [-137.197506, 7.931339000000001],
          [-137.191399, 7.938001],
          [-137.181271, 7.9431670000000025],
          [-137.154842, 7.944381],
          [-137.099266, 7.936118999999998],
          [-137.054768, 7.908943999999998],
          [-137.04509, 7.898605000000003],
          [-137.08101, 7.855497],
          [-137.120377, 7.839686999999998],
          [-137.128617, 7.8338350000000005],
          [-137.136977, 7.814492999999999],
          [-137.13096, 7.762771999999998],
          [-137.120581, 7.739815],
          [-137.122808, 7.729354999999998],
          [-137.145675, 7.707293999999997],
          [-137.261631, 7.6808460000000025],
          [-137.275121, 7.680509999999998],
          [-137.296369, 7.684244999999997],
          [-137.316501, 7.690353000000002],
          [-137.317888, 7.693446999999999],
          [-137.317939, 7.696866],
          [-137.316353, 7.700811000000002],
          [-137.322977, 7.711416],
          [-137.342784, 7.721395000000001],
          [-137.360408, 7.727533000000001]
        ],
        [
          [-138.792409, 7.746071000000001],
          [-138.808157, 7.747078000000002],
          [-138.815757, 7.7491759999999985],
          [-138.873024, 7.782623000000001],
          [-138.870118, 7.7952610000000035],
          [-138.858248, 7.8209659999999985],
          [-138.828645, 7.8361500000000035],
          [-138.819459, 7.8395750000000035],
          [-138.811249, 7.839018000000003],
          [-138.788541, 7.832602000000001],
          [-138.767695, 7.823179000000003],
          [-138.748283, 7.8099419999999995],
          [-138.733355, 7.783946999999998],
          [-138.750414, 7.7577520000000035],
          [-138.776661, 7.748612000000001],
          [-138.792409, 7.746071000000001]
        ],
        [
          [218.380741, 7.763907000000003],
          [218.367465, 7.772758000000003],
          [218.36368, 7.773947999999997],
          [218.339082, 7.771529000000001],
          [218.335664, 7.769925999999998],
          [218.318757, 7.772322000000003],
          [218.308563, 7.775700999999998],
          [218.304892, 7.7774339999999995],
          [218.246209, 7.817078000000002],
          [218.236931, 7.828209000000001],
          [218.305568, 7.8217479999999995],
          [218.310298, 7.819992999999997],
          [218.319389, 7.815736999999999],
          [218.33219, 7.8090369999999965],
          [218.335631, 7.807031000000002],
          [218.372348, 7.774146000000002],
          [218.380741, 7.763907000000003]
        ],
        [
          [-135.971562, 7.888630999999997],
          [-135.957546, 7.893455000000003],
          [-135.953251, 7.881376000000003],
          [-135.954287, 7.868380999999999],
          [-135.963041, 7.846252999999997],
          [-135.983742, 7.852352000000003],
          [-136.047892, 7.846308999999998],
          [-136.10107, 7.8106089999999995],
          [-136.123965, 7.802745000000002],
          [-136.139622, 7.8023859999999985],
          [-136.183142, 7.807099000000001],
          [-136.216957, 7.812714],
          [-136.235544, 7.823157000000002],
          [-136.236246, 7.8259649999999965],
          [-136.217544, 7.874626999999997],
          [-136.206069, 7.883088999999998],
          [-136.173871, 7.882449000000001],
          [-136.169751, 7.880138000000002],
          [-136.168775, 7.877330000000001],
          [-136.161052, 7.869684999999997],
          [-136.140908, 7.859561999999997],
          [-136.099137, 7.855533000000001],
          [-136.080442, 7.858567000000001],
          [-136.072225, 7.867938000000002],
          [-136.073431, 7.870311999999998],
          [-136.078865, 7.874777999999999],
          [-136.115489, 7.887014999999998],
          [-136.111452, 7.889747999999997],
          [-136.065288, 7.9029859999999985],
          [-136.020182, 7.9113729999999975],
          [-135.99265, 7.912655000000001],
          [-135.984993, 7.908445],
          [-135.971562, 7.888630999999997]
        ],
        [
          [217.601645, 8.016376999999999],
          [217.577226, 8.00497],
          [217.572068, 8.001812000000001],
          [217.538223, 7.9788970000000035],
          [217.532729, 7.97007],
          [217.539627, 7.959417999999999],
          [217.543534, 7.956175000000002],
          [217.571796, 7.951590000000003],
          [217.579823, 7.9508360000000025],
          [217.607535, 7.954720000000002],
          [217.611553, 7.950828999999999],
          [217.610618, 7.9367129999999975],
          [217.606529, 7.925069000000001],
          [217.601005, 7.922254000000002],
          [217.560513, 7.9163640000000015],
          [217.484313, 7.9234129999999965],
          [217.409536, 7.930821000000002],
          [217.373934, 7.919759999999997],
          [217.348816, 7.904468999999999],
          [217.326781, 7.873635999999998],
          [217.327179, 7.871048999999999],
          [217.334229, 7.866768999999998],
          [217.334017, 7.844444000000003],
          [217.321687, 7.828543000000003],
          [217.311768, 7.825971000000003],
          [217.303314, 7.8294580000000025],
          [217.294035, 7.8373009999999965],
          [217.296018, 7.839866000000001],
          [217.293424, 7.845610000000001],
          [217.27337, 7.857123000000001],
          [217.235523, 7.873260000000002],
          [217.212422, 7.876430999999997],
          [217.203996, 7.880530999999998],
          [217.200423, 7.894745999999998],
          [217.203323, 7.896562000000003],
          [217.233904, 7.909624000000001],
          [217.272695, 7.920054],
          [217.291312, 7.919429999999998],
          [217.341518, 7.9550160000000005],
          [217.345577, 7.9630050000000026],
          [217.413484, 7.979723999999997],
          [217.483712, 7.984876999999997],
          [217.497441, 7.993327999999998],
          [217.503441, 8.008828999999999],
          [217.505747, 8.016373999999999],
          [217.505319, 8.038767999999997],
          [217.545604, 8.101090999999997],
          [217.563396, 8.121958999999997],
          [217.602673, 8.137320000000003],
          [217.661607, 8.112746000000001],
          [217.675952, 8.092167000000003],
          [217.667256, 8.076273999999998],
          [217.659451, 8.069439000000003],
          [217.653614, 8.070323000000002],
          [217.641864, 8.068316000000003],
          [217.632555, 8.064844],
          [217.609087, 8.028517999999998],
          [217.601645, 8.016376999999999]
        ],
        [
          [219.758993, 7.946595000000002],
          [219.751525, 7.923932999999998],
          [219.743012, 7.911749],
          [219.734772, 7.907606000000001],
          [219.649484, 7.873669999999997],
          [219.639077, 7.8719309999999965],
          [219.614364, 7.871772],
          [219.521868, 7.896765000000002],
          [219.484634, 7.921267999999998],
          [219.475569, 7.937455999999997],
          [219.482464, 7.982833999999997],
          [219.486565, 7.983958999999999],
          [219.515025, 7.983750999999998],
          [219.526743, 7.981164],
          [219.539223, 7.985177999999998],
          [219.571049, 8.011111],
          [219.582857, 8.016841],
          [219.622283, 8.024974999999998],
          [219.647641, 8.026259000000003],
          [219.663327, 8.022941000000003],
          [219.704433, 8.004877],
          [219.773922, 7.970692999999997],
          [219.77847, 7.9622170000000025],
          [219.777158, 7.9587],
          [219.767251, 7.947572000000001],
          [219.758993, 7.946595000000002]
        ],
        [
          [218.446964, 7.978222000000002],
          [218.463385, 7.987848999999997],
          [218.478586, 7.987549000000001],
          [218.552612, 7.973967999999999],
          [218.570619, 7.968063999999998],
          [218.591597, 7.9526520000000005],
          [218.590245, 7.9454569999999975],
          [218.567447, 7.925939],
          [218.539395, 7.903246000000003],
          [218.518861, 7.899759000000003],
          [218.502493, 7.899644000000002],
          [218.484831, 7.909897999999998],
          [218.468045, 7.931635],
          [218.454664, 7.960501000000001],
          [218.446964, 7.978222000000002]
        ],
        [
          [-135.680144, 7.968969999999999],
          [-135.67264, 7.972470999999999],
          [-135.669707, 7.972166000000001],
          [-135.655056, 7.966650999999999],
          [-135.652493, 7.9648129999999995],
          [-135.653194, 7.9616690000000006],
          [-135.717436, 7.933695],
          [-135.730011, 7.933816999999998],
          [-135.747438, 7.9461999999999975],
          [-135.747836, 7.950654999999998],
          [-135.742618, 7.966631999999997],
          [-135.735477, 7.973331000000002],
          [-135.726245, 7.975968999999999],
          [-135.680144, 7.968969999999999]
        ],
        [
          [-136.018089, 8.020099000000002],
          [-136.044001, 8.009331000000003],
          [-136.032156, 7.993667000000002],
          [-136.027546, 7.991630000000001],
          [-136.021839, 7.9848479999999995],
          [-136.022663, 7.980620999999999],
          [-136.027667, 7.975112000000003],
          [-136.057085, 7.967824999999998],
          [-136.079181, 7.968884000000003],
          [-136.180356, 8.000425999999997],
          [-136.185086, 8.005704999999999],
          [-136.201935, 8.040211999999997],
          [-136.211855, 8.065533000000002],
          [-136.205324, 8.076245999999998],
          [-136.173155, 8.102314],
          [-136.143914, 8.116097000000003],
          [-136.058103, 8.106467000000002],
          [-135.988653, 8.035508999999998],
          [-135.999044, 8.025385],
          [-136.018089, 8.020099000000002]
        ],
        [
          [218.1176, 8.048611999999999],
          [218.119144, 8.051659],
          [218.141695, 8.051034000000001],
          [218.175781, 8.036777],
          [218.179962, 8.033247000000003],
          [218.190963, 8.003546],
          [218.174473, 7.991683999999999],
          [218.132547, 7.986981999999998],
          [218.105874, 7.998356999999999],
          [218.10273, 8.003926999999997],
          [218.09461, 8.033293999999998],
          [218.107266, 8.045744],
          [218.1176, 8.048611999999999]
        ],
        [
          [-134.301818, 8.278948999999997],
          [-134.323471, 8.283990000000003],
          [-134.346089, 8.285035999999998],
          [-134.349404, 8.281336000000003],
          [-134.368754, 8.280405000000002],
          [-134.408277, 8.289872000000003],
          [-134.451554, 8.305557],
          [-134.455979, 8.313690000000001],
          [-134.45366, 8.319367],
          [-134.443132, 8.325654],
          [-134.432846, 8.328004],
          [-134.384199, 8.321139000000002],
          [-134.367047, 8.314104999999998],
          [-134.358624, 8.314190000000004],
          [-134.340679, 8.322284000000003],
          [-134.331065, 8.328465000000001],
          [-134.3177, 8.344869000000003],
          [-134.320813, 8.355725999999997],
          [-134.330494, 8.366439],
          [-134.329818, 8.373548],
          [-134.324935, 8.378095000000002],
          [-134.185347, 8.417788000000002],
          [-134.155774, 8.416041],
          [-134.068248, 8.390331000000003],
          [-134.016822, 8.348537],
          [-133.989415, 8.325274999999998],
          [-133.985203, 8.317599999999999],
          [-133.986421, 8.298565000000004],
          [-133.987917, 8.295344999999998],
          [-134.036222, 8.245010999999998],
          [-134.060451, 8.225326000000003],
          [-134.084042, 8.223677000000002],
          [-134.106533, 8.228392],
          [-134.177679, 8.233638],
          [-134.182857, 8.232762000000001],
          [-134.198624, 8.219244000000003],
          [-134.200389, 8.211860999999999],
          [-134.196836, 8.195856],
          [-134.1901, 8.19032],
          [-134.175044, 8.181835],
          [-134.135217, 8.168514000000002],
          [-134.090169, 8.139119],
          [-134.082814, 8.132069000000001],
          [-134.080677, 8.128025999999998],
          [-134.0891, 8.107250999999998],
          [-134.09447, 8.104273999999997],
          [-134.102161, 8.104534000000001],
          [-134.109089, 8.113117000000003],
          [-134.11437, 8.117106999999997],
          [-134.142262, 8.125452000000003],
          [-134.206353, 8.116554],
          [-134.218469, 8.104880000000001],
          [-134.302947, 8.111325],
          [-134.334424, 8.115198],
          [-134.348463, 8.109245000000001],
          [-134.365667, 8.097237999999997],
          [-134.382661, 8.081657999999997],
          [-134.411255, 8.048757000000002],
          [-134.45276, 8.061047000000002],
          [-134.507816, 8.054955],
          [-134.508822, 8.048623],
          [-134.556278, 8.036732999999998],
          [-134.580676, 8.040453],
          [-134.593635, 8.045247000000003],
          [-134.615943, 8.032665000000001],
          [-134.71461, 8.009863000000003],
          [-134.736592, 8.007308000000002],
          [-134.783189, 8.032293000000003],
          [-134.885554, 8.043000999999997],
          [-134.967907, 8.037202999999998],
          [-135.000792, 8.028354],
          [-135.014748, 8.020584],
          [-135.014807, 8.006999999999998],
          [-135.09551, 8.000796999999999],
          [-135.104889, 8.003548000000002],
          [-135.155673, 8.011512000000003],
          [-135.27485, 8.018619000000001],
          [-135.292821, 8.018790000000003],
          [-135.300639, 8.014969999999998],
          [-135.302683, 8.011499],
          [-135.323322, 8.007488000000002],
          [-135.341624, 8.021588000000001],
          [-135.32707, 8.027031999999998],
          [-135.1959, 8.051406999999998],
          [-135.156744, 8.057642000000001],
          [-135.132635, 8.059223000000003],
          [-135.117115, 8.054499],
          [-135.11768, 8.053234000000003],
          [-135.113721, 8.046308000000003],
          [-135.092867, 8.034793999999998],
          [-135.044344, 8.057519],
          [-134.995237, 8.061416999999999],
          [-134.992309, 8.058602999999998],
          [-134.980497, 8.061470999999997],
          [-134.937497, 8.078333999999998],
          [-134.922299, 8.09158],
          [-134.927549, 8.101415000000003],
          [-134.920042, 8.109274],
          [-134.905409, 8.116509],
          [-134.866725, 8.103172],
          [-134.839715, 8.091338],
          [-134.786809, 8.091324],
          [-134.656294, 8.107962],
          [-134.604871, 8.122124],
          [-134.568402, 8.138426000000003],
          [-134.55708, 8.153637000000003],
          [-134.55467, 8.160404999999997],
          [-134.527081, 8.17472],
          [-134.49688, 8.179150999999997],
          [-134.465189, 8.180711000000002],
          [-134.455707, 8.176802000000002],
          [-134.424054, 8.169052999999998],
          [-134.41529, 8.169376],
          [-134.404588, 8.181330000000003],
          [-134.405464, 8.18356],
          [-134.457804, 8.202831000000003],
          [-134.462962, 8.213031],
          [-134.453746, 8.218823],
          [-134.400139, 8.219053000000002],
          [-134.360631, 8.212994000000002],
          [-134.328599, 8.211647],
          [-134.299044, 8.214669999999998],
          [-134.249848, 8.243693999999998],
          [-134.255832, 8.274152],
          [-134.301818, 8.278948999999997]
        ],
        [
          [-133.602446, 8.153773000000001],
          [-133.59056, 8.145392999999999],
          [-133.514171, 8.108348],
          [-133.49702, 8.103641000000003],
          [-133.467877, 8.116422999999998],
          [-133.375229, 8.108227999999997],
          [-133.375595, 8.106343000000003],
          [-133.372574, 8.10275],
          [-133.357498, 8.096128999999998],
          [-133.238295, 8.100442999999999],
          [-133.173206, 8.109136],
          [-133.124504, 8.10942],
          [-133.119255, 8.107627999999998],
          [-133.107373, 8.09928],
          [-133.06643, 8.096330000000002],
          [-133.019588, 8.097881000000001],
          [-132.958523, 8.093648000000002],
          [-132.960751, 8.087018],
          [-133.033166, 8.074610999999997],
          [-133.04754, 8.073329000000001],
          [-133.107933, 8.078828000000001],
          [-133.206837, 8.063532000000002],
          [-133.313705, 8.058701],
          [-133.424178, 8.046298],
          [-133.511915, 8.031278],
          [-133.548385, 8.029308],
          [-133.612014, 8.051147999999998],
          [-133.718, 8.063068999999999],
          [-133.799574, 8.053649999999998],
          [-133.816999, 8.048538],
          [-133.820692, 8.043312],
          [-133.831555, 8.040762999999998],
          [-133.901075, 8.049435000000003],
          [-133.937239, 8.057513],
          [-133.935561, 8.064731000000002],
          [-133.97133, 8.099428000000003],
          [-133.992274, 8.100589999999997],
          [-134.001866, 8.097641000000003],
          [-134.011338, 8.098861999999997],
          [-134.035082, 8.112952],
          [-134.04675, 8.122402999999998],
          [-134.052296, 8.130400000000002],
          [-134.048451, 8.132911],
          [-134.036854, 8.135877999999998],
          [-133.984245, 8.127854999999997],
          [-133.890733, 8.12547],
          [-133.830906, 8.11045],
          [-133.824087, 8.105891999999997],
          [-133.818277, 8.105362999999997],
          [-133.802339, 8.106389999999998],
          [-133.721266, 8.130206999999999],
          [-133.654404, 8.146192],
          [-133.624771, 8.152213000000003],
          [-133.602446, 8.153773000000001]
        ],
        [
          [217.355215, 8.071396],
          [217.332629, 8.075209000000001],
          [217.321366, 8.080427999999998],
          [217.30519, 8.090324000000003],
          [217.2906, 8.102150000000002],
          [217.283488, 8.114159],
          [217.28016, 8.127578999999997],
          [217.281807, 8.135126],
          [217.288827, 8.144190000000002],
          [217.306009, 8.156108000000003],
          [217.329296, 8.166668999999999],
          [217.340528, 8.170323000000003],
          [217.367356, 8.175299000000003],
          [217.383655, 8.174275999999999],
          [217.408835, 8.167631999999998],
          [217.425775, 8.157203000000003],
          [217.437924, 8.146873],
          [217.445983, 8.135129999999997],
          [217.446563, 8.114452999999997],
          [217.443023, 8.105258999999997],
          [217.435577, 8.097011000000002],
          [217.428801, 8.091738],
          [217.402034, 8.079667999999998],
          [217.386805, 8.075195999999998],
          [217.355215, 8.071396]
        ],
        [
          [-132.633153, 8.266215000000003],
          [-132.620261, 8.298751000000003],
          [-132.574154, 8.345323],
          [-132.568051, 8.349420000000002],
          [-132.47461, 8.383763000000002],
          [-132.448182, 8.391438999999998],
          [-132.405243, 8.389442000000003],
          [-132.326444, 8.366472000000002],
          [-132.311427, 8.356456000000001],
          [-132.302393, 8.342357],
          [-132.301445, 8.329951000000001],
          [-132.313133, 8.320697000000003],
          [-132.414419, 8.276739999999997],
          [-132.528095, 8.254336000000002],
          [-132.608935, 8.253014],
          [-132.616839, 8.255316999999998],
          [-132.633153, 8.266215000000003]
        ],
        [
          [215.911286, 8.334831000000001],
          [215.90277, 8.336823000000003],
          [215.890684, 8.344513999999997],
          [215.873317, 8.361137999999997],
          [215.874353, 8.371004],
          [215.906734, 8.375650999999998],
          [215.95056, 8.368357000000003],
          [215.966521, 8.359727999999997],
          [215.94418, 8.336436999999997],
          [215.911286, 8.334831000000001]
        ],
        [
          [213.587554, 8.476785],
          [213.623883, 8.506948000000001],
          [213.680586, 8.512878],
          [213.73627, 8.512422],
          [213.769503, 8.512072000000003],
          [213.772799, 8.509905000000003],
          [213.772402, 8.506877000000003],
          [213.754979, 8.496127000000001],
          [213.739385, 8.493257],
          [213.707741, 8.477376999999997],
          [213.695719, 8.458934999999997],
          [213.691601, 8.445934999999999],
          [213.69386, 8.438693999999998],
          [213.702252, 8.434804],
          [213.704299, 8.432192],
          [213.712323, 8.421033000000001],
          [213.719161, 8.397703],
          [213.725696, 8.356579000000004],
          [213.651293, 8.356369999999998],
          [213.644793, 8.357598000000003],
          [213.640825, 8.359428000000001],
          [213.606767, 8.378248999999997],
          [213.595397, 8.391893000000003],
          [213.588794, 8.400973],
          [213.559891, 8.401164999999999],
          [213.543778, 8.392665999999998],
          [213.512162, 8.385035000000002],
          [213.483843, 8.383485],
          [213.465442, 8.384621000000003],
          [213.455586, 8.389656000000002],
          [213.3955, 8.402647000000002],
          [213.385704, 8.404072],
          [213.356927, 8.403872999999997],
          [213.356103, 8.405563],
          [213.380058, 8.431843],
          [213.440111, 8.453664000000003],
          [213.445696, 8.455030999999998],
          [213.467698, 8.444473000000002],
          [213.476243, 8.441909000000003],
          [213.501022, 8.440925999999997],
          [213.525161, 8.448137000000003],
          [213.530105, 8.449967999999998],
          [213.550002, 8.467067],
          [213.549605, 8.469988999999998],
          [213.545302, 8.475999999999999],
          [213.555739, 8.479472000000001],
          [213.587554, 8.476785]
        ],
        [
          [-131.294554, 8.451104999999998],
          [-131.299348, 8.448715999999997],
          [-131.30417, 8.449952000000003],
          [-131.313083, 8.472932],
          [-131.312658, 8.493502],
          [-131.3075, 8.501514],
          [-131.291387, 8.514812999999997],
          [-131.277165, 8.522633999999996],
          [-131.252053, 8.529953999999996],
          [-131.196013, 8.500106000000002],
          [-131.194639, 8.498038999999999],
          [-131.208919, 8.469023],
          [-131.214565, 8.463299999999997],
          [-131.236843, 8.450527000000001],
          [-131.252316, 8.449466000000001],
          [-131.294554, 8.451104999999998]
        ],
        [
          [-130.841936, 8.558171000000002],
          [-130.833364, 8.599984999999997],
          [-130.820641, 8.633091],
          [-130.817943, 8.636274999999998],
          [-130.727717, 8.679977999999998],
          [-130.671545, 8.698082],
          [-130.633753, 8.697468999999998],
          [-130.579913, 8.682029],
          [-130.562734, 8.674785],
          [-130.559523, 8.667907],
          [-130.557324, 8.652104999999999],
          [-130.56361, 8.640706000000002],
          [-130.603862, 8.601731999999998],
          [-130.635419, 8.595711000000001],
          [-130.659041, 8.593811000000002],
          [-130.665266, 8.595260000000003],
          [-130.668075, 8.600676999999997],
          [-130.674453, 8.603385000000003],
          [-130.683854, 8.602485000000001],
          [-130.696488, 8.598363999999997],
          [-130.735824, 8.580823000000002],
          [-130.767378, 8.558253999999998],
          [-130.777143, 8.546664],
          [-130.788495, 8.540239999999997],
          [-130.841936, 8.558171000000002]
        ],
        [
          [214.069186, 8.734887999999998],
          [214.092073, 8.742060000000002],
          [214.09665, 8.743485],
          [214.13315, 8.733786000000002],
          [214.145326, 8.728549999999998],
          [214.155764, 8.715375000000002],
          [214.159252, 8.707386999999997],
          [214.158146, 8.706059000000003],
          [214.109409, 8.708559999999999],
          [214.071842, 8.718294999999998],
          [214.066195, 8.731042000000002],
          [214.069186, 8.734887999999998]
        ],
        [
          [-130.170683, 8.784917999999998],
          [-130.128714, 8.787424999999999],
          [-130.061868, 8.773525],
          [-130.053443, 8.769075999999998],
          [-130.052922, 8.758744999999998],
          [-130.055363, 8.745887000000003],
          [-130.070287, 8.724300999999997],
          [-130.077734, 8.720416],
          [-130.114087, 8.716172],
          [-130.170646, 8.717359000000002],
          [-130.184564, 8.721936999999997],
          [-130.185684, 8.723007000000003],
          [-130.170683, 8.784917999999998]
        ],
        [
          [214.004827, 8.719856999999998],
          [213.9726, 8.729422999999997],
          [213.96088, 8.738135999999997],
          [213.952793, 8.747884999999997],
          [213.954075, 8.75141],
          [213.983432, 8.749053000000004],
          [214.003651, 8.744283000000003],
          [214.021702, 8.730286],
          [214.004827, 8.719856999999998]
        ],
        [
          [213.932926, 8.746648999999998],
          [213.930912, 8.750227000000002],
          [213.925271, 8.752433000000003],
          [213.894753, 8.750779999999999],
          [213.875585, 8.761898000000002],
          [213.861653, 8.773578999999998],
          [213.867436, 8.775128000000002],
          [213.881412, 8.775027999999999],
          [213.897452, 8.77178],
          [213.931553, 8.758574000000003],
          [213.940037, 8.75186],
          [213.932926, 8.746648999999998]
        ],
        [
          [212.763366, 8.823656],
          [212.76739, 8.848371999999998],
          [212.766693, 8.862668999999997],
          [212.754236, 8.877490000000002],
          [212.640372, 8.925441],
          [212.585075, 8.921326999999998],
          [212.5487, 8.914321999999999],
          [212.512996, 8.905180999999999],
          [212.469022, 8.911337000000003],
          [212.461667, 8.92716],
          [212.629077, 9.001323999999997],
          [212.643266, 9.004978999999999],
          [212.746566, 9.010750000000002],
          [213.107249, 8.993228000000002],
          [213.121988, 8.990352000000001],
          [213.13151, 8.987521000000001],
          [213.159648, 8.974162999999997],
          [213.172406, 8.960545000000003],
          [213.211752, 8.939489000000002],
          [213.235265, 8.943627999999997],
          [213.251326, 8.944361999999998],
          [213.295399, 8.926986999999997],
          [213.421682, 8.845477000000002],
          [213.42767, 8.830762999999997],
          [213.423819, 8.828798999999997],
          [213.413016, 8.827891000000001],
          [213.302331, 8.823286000000003],
          [213.284417, 8.827933000000002],
          [213.22907, 8.856155999999999],
          [213.224051, 8.856403],
          [213.204948, 8.848911000000001],
          [213.187952, 8.831499999999998],
          [213.173543, 8.804378],
          [213.166899, 8.795228999999999],
          [213.142678, 8.786254],
          [213.134521, 8.784357],
          [213.11856, 8.784439999999996],
          [212.998472, 8.796979],
          [212.903628, 8.761667000000003],
          [212.809387, 8.789290000000001],
          [212.763366, 8.823656]
        ],
        [
          [-129.943521, 8.861099000000003],
          [-129.905631, 8.85324],
          [-129.860214, 8.858376999999997],
          [-129.818139, 8.878445999999997],
          [-129.773504, 8.894449999999999],
          [-129.749177, 8.893268999999997],
          [-129.704736, 8.886271999999998],
          [-129.666512, 8.864348999999997],
          [-129.683482, 8.826618000000003],
          [-129.704105, 8.793937999999997],
          [-129.750136, 8.790576000000001],
          [-129.838232, 8.817279999999997],
          [-129.879866, 8.816088],
          [-129.886671, 8.808563],
          [-129.897078, 8.802131000000003],
          [-129.927446, 8.792675000000003],
          [-129.951498, 8.788615],
          [-129.962883, 8.789881999999999],
          [-129.995422, 8.804676],
          [-130.012487, 8.831161000000002],
          [-130.004218, 8.846742999999996],
          [-129.990149, 8.856265999999998],
          [-129.975345, 8.858884000000003],
          [-129.943521, 8.861099000000003]
        ],
        [
          [-128.211705, 9.256183999999998],
          [-128.226915, 9.254821999999997],
          [-128.270744, 9.242811000000003],
          [-128.296229, 9.227235],
          [-128.312376, 9.215231000000003],
          [-128.341678, 9.185910999999997],
          [-128.344468, 9.155214999999998],
          [-128.37315, 9.128891000000003],
          [-128.392379, 9.123609000000002],
          [-128.412522, 9.110683000000002],
          [-128.433734, 9.093933999999997],
          [-128.442859, 9.085562000000003],
          [-128.451161, 9.075130999999999],
          [-128.457103, 9.055838999999999],
          [-128.49749, 9.035403000000002],
          [-128.527404, 9.028588],
          [-128.546059, 9.029580000000003],
          [-128.553195, 9.033296],
          [-128.578895, 9.029915000000003],
          [-128.587808, 9.027175],
          [-128.613964, 9.008775999999997],
          [-128.625257, 8.998213999999997],
          [-128.688468, 8.9664],
          [-128.741851, 8.951442],
          [-128.808854, 8.926102],
          [-128.907003, 8.884006],
          [-129.041338, 8.839348000000001],
          [-129.102465, 8.824348999999998],
          [-129.054243, 8.863266000000003],
          [-129.038767, 8.869661999999998],
          [-128.992403, 8.873440000000002],
          [-128.97171, 8.878028],
          [-128.958983, 8.886048000000002],
          [-128.861078, 8.968046000000001],
          [-128.785236, 9.045037999999998],
          [-128.763689, 9.070960999999997],
          [-128.759691, 9.081460999999997],
          [-128.768544, 9.093684000000003],
          [-128.776176, 9.097766],
          [-128.789424, 9.100969999999997],
          [-128.80203, 9.108226000000002],
          [-128.804901, 9.120015000000002],
          [-128.799469, 9.143794],
          [-128.792327, 9.155720000000002],
          [-128.788756, 9.160749000000003],
          [-128.763331, 9.182811999999998],
          [-128.617143, 9.260984999999998],
          [-128.539398, 9.251669999999997],
          [-128.524991, 9.252310999999999],
          [-128.501365, 9.25734],
          [-128.490957, 9.264009000000001],
          [-128.445083, 9.265329999999999],
          [-128.412851, 9.257859000000003],
          [-128.366519, 9.252023999999999],
          [-128.361758, 9.252253000000003],
          [-128.343994, 9.262149999999998],
          [-128.365388, 9.309105000000002],
          [-128.371218, 9.316575],
          [-128.375674, 9.318291000000002],
          [-128.406531, 9.346392999999999],
          [-128.386886, 9.431496000000003],
          [-128.342127, 9.475991999999998],
          [-128.315847, 9.481729000000001],
          [-128.295793, 9.489061999999997],
          [-128.239572, 9.518490999999997],
          [-128.238321, 9.521901999999997],
          [-128.200443, 9.534078999999998],
          [-128.14462, 9.545341999999998],
          [-128.004624, 9.566052999999997],
          [-127.98103800000001, 9.561714000000002],
          [-127.96272300000001, 9.554068999999998],
          [-127.960861, 9.552549999999997],
          [-127.96571399999999, 9.543439999999997],
          [-127.96503799999999, 9.538913],
          [-127.93898100000001, 9.526907000000001],
          [-127.901871, 9.520508],
          [-127.888901, 9.519691000000002],
          [-127.81699800000001, 9.517947],
          [-127.796866, 9.521113],
          [-127.79102599999999, 9.521076],
          [-127.789164, 9.519328999999999],
          [-127.78638699999999, 9.513896000000003],
          [-127.784099, 9.501047999999997],
          [-127.78806599999999, 9.492410999999997],
          [-127.80811700000001, 9.473861],
          [-127.84361100000001, 9.453893],
          [-127.85322500000001, 9.445469000000003],
          [-127.858337, 9.437910000000002],
          [-127.85683700000001, 9.428609000000002],
          [-127.851698, 9.421236],
          [-127.84479999999999, 9.417496999999997],
          [-127.83952, 9.410325],
          [-127.839887, 9.394432000000002],
          [-127.84232800000001, 9.386488999999997],
          [-127.852217, 9.378293999999997],
          [-127.87287900000001, 9.367359999999998],
          [-127.878128, 9.366902000000003],
          [-127.95909599999999, 9.341788000000001],
          [-127.98848699999999, 9.329577999999998],
          [-128.009301, 9.317262999999997],
          [-128.03976, 9.304276000000002],
          [-128.158943, 9.267710000000001],
          [-128.211705, 9.256183999999998]
        ],
        [
          [-129.996712, 8.891475],
          [-129.999094, 8.884034],
          [-130.002368, 8.880239000000003],
          [-130.015514, 8.870260000000002],
          [-130.050274, 8.857433],
          [-130.095331, 8.870851000000002],
          [-130.113189, 8.891078],
          [-130.112853, 8.902042999999999],
          [-130.092221, 8.919387],
          [-130.083985, 8.923639999999999],
          [-130.04656, 8.923853000000001],
          [-130.020493, 8.917171000000003],
          [-130.002071, 8.910043000000002],
          [-129.995982, 8.902377999999999],
          [-129.996712, 8.891475]
        ],
        [
          [-129.721744, 8.947116999999999],
          [-129.741096, 8.951512000000001],
          [-129.758008, 8.967246000000003],
          [-129.760725, 8.971556],
          [-129.76274, 8.978050000000003],
          [-129.745743, 9.02147],
          [-129.742538, 9.024071999999997],
          [-129.698128, 9.033779000000003],
          [-129.680033, 9.035074999999999],
          [-129.66493, 9.023972999999998],
          [-129.663576, 9.021258000000003],
          [-129.666078, 8.997067999999999],
          [-129.698274, 8.958267],
          [-129.721744, 8.947116999999999]
        ],
        [
          [-126.728918, 10.003110999999997],
          [-126.67663999999999, 10.017418999999997],
          [-126.64462700000001, 10.014494999999997],
          [-126.63693599999999, 10.012],
          [-126.619754, 10.001263999999999],
          [-126.59994699999999, 9.983694999999997],
          [-126.58739299999999, 9.959831000000001],
          [-126.60543799999999, 9.955354],
          [-126.62197900000001, 9.953744],
          [-126.64678599999999, 9.923785000000002],
          [-126.640466, 9.912519000000003],
          [-126.61900299999999, 9.893514000000003],
          [-126.597182, 9.883989999999997],
          [-126.57508999999999, 9.879235999999999],
          [-126.56054599999999, 9.878774999999997],
          [-126.48784699999999, 9.895448000000002],
          [-126.44369900000001, 9.909726999999997],
          [-126.43652599999999, 9.916151],
          [-126.43515300000001, 9.920414999999998],
          [-126.437083, 9.955644],
          [-126.37368900000001, 10.01003],
          [-126.36746, 10.008902999999997],
          [-126.35711699999999, 10.002343000000003],
          [-126.354614, 9.999039000000003],
          [-126.354341, 9.995514999999997],
          [-126.359925, 9.977136000000002],
          [-126.319895, 9.960126000000002],
          [-126.27940699999999, 9.982531999999999],
          [-126.264519, 9.97755],
          [-126.21096399999999, 9.933557],
          [-126.208767, 9.929110000000001],
          [-126.211207, 9.912334000000001],
          [-126.236513, 9.881343000000001],
          [-126.250935, 9.876851000000002],
          [-126.32000400000001, 9.869526999999998],
          [-126.351999, 9.858531999999997],
          [-126.389196, 9.832343000000002],
          [-126.40489600000001, 9.809345],
          [-126.434846, 9.798012],
          [-126.547438, 9.749403999999998],
          [-126.552078, 9.728498000000002],
          [-126.54053099999999, 9.715926000000003],
          [-126.469112, 9.735934999999998],
          [-126.46032400000001, 9.745838],
          [-126.42047099999999, 9.762087999999999],
          [-126.336768, 9.78709],
          [-126.303201, 9.791538000000003],
          [-126.212603, 9.817127],
          [-126.214312, 9.820430000000002],
          [-126.21233000000001, 9.827769000000004],
          [-126.19875099999999, 9.836100000000002],
          [-126.119922, 9.855047999999996],
          [-126.11303699999999, 9.853715999999999],
          [-126.097565, 9.843989999999998],
          [-126.09414699999999, 9.839199999999998],
          [-126.11131700000001, 9.776856000000002],
          [-126.16670300000001, 9.733401999999998],
          [-126.19906, 9.727328],
          [-126.26297400000001, 9.703710000000001],
          [-126.26518200000001, 9.698248],
          [-126.27489600000001, 9.687252999999998],
          [-126.283267, 9.684218999999999],
          [-126.444909, 9.640645999999997],
          [-126.46758299999999, 9.646574000000001],
          [-126.53263899999999, 9.630533],
          [-126.55398299999999, 9.623448000000003],
          [-126.58101099999999, 9.530448999999997],
          [-126.65623400000001, 9.487119],
          [-126.66227599999999, 9.485349],
          [-126.667921, 9.486027],
          [-126.71247500000001, 9.498444999999997],
          [-126.735039, 9.506639999999997],
          [-126.743054, 9.51482],
          [-126.77265499999999, 9.496371000000003],
          [-126.789062, 9.4531],
          [-126.86311900000001, 9.443877999999998],
          [-126.878087, 9.429884000000001],
          [-126.922674, 9.441136],
          [-126.959082, 9.455753000000001],
          [-126.994329, 9.429200999999999],
          [-127.036104, 9.449289],
          [-127.04821000000001, 9.448844000000001],
          [-127.050025, 9.433067000000001],
          [-127.07538600000001, 9.424979],
          [-127.112008, 9.416775000000001],
          [-127.124277, 9.425533999999999],
          [-127.13413399999999, 9.426448],
          [-127.20143200000001, 9.3979],
          [-127.291831, 9.364102000000003],
          [-127.30298199999999, 9.336911],
          [-127.30812599999999, 9.334330000000001],
          [-127.34865300000001, 9.333261999999998],
          [-127.38698400000001, 9.340671],
          [-127.442804, 9.321015000000003],
          [-127.46630400000001, 9.295887999999998],
          [-127.488215, 9.269120999999998],
          [-127.51547, 9.267876000000001],
          [-127.53088399999999, 9.275658999999997],
          [-127.53924699999999, 9.277864000000001],
          [-127.58918, 9.288697999999997],
          [-127.59842800000001, 9.288048000000003],
          [-127.609903, 9.2853],
          [-127.622173, 9.250362000000003],
          [-127.64417900000001, 9.250841999999999],
          [-127.79898399999999, 9.284756999999999],
          [-127.83509000000001, 9.299619999999997],
          [-127.85151099999999, 9.308667999999997],
          [-127.85233299999999, 9.315598999999999],
          [-127.79092800000001, 9.335520000000002],
          [-127.71044599999999, 9.381326000000001],
          [-127.69448399999999, 9.388033999999998],
          [-127.65311299999999, 9.392276000000003],
          [-127.62208899999999, 9.385328999999999],
          [-127.48825199999999, 9.420001],
          [-127.474457, 9.431781999999998],
          [-127.47332800000001, 9.438001],
          [-127.45736600000001, 9.442793000000002],
          [-127.39398499999999, 9.439751999999999],
          [-127.373527, 9.432775999999997],
          [-127.355624, 9.424498],
          [-127.33279200000001, 9.433107],
          [-127.319143, 9.451317000000003],
          [-127.30129, 9.466006],
          [-127.278827, 9.478565000000003],
          [-127.26790199999999, 9.478115000000003],
          [-127.226182, 9.468691999999997],
          [-127.21760599999999, 9.465389000000002],
          [-127.19996599999999, 9.463039000000002],
          [-127.19380100000001, 9.467007000000002],
          [-127.15852000000001, 9.503746999999997],
          [-127.102305, 9.515076999999998],
          [-127.105816, 9.540506999999998],
          [-127.131239, 9.547266999999998],
          [-127.135695, 9.551226999999997],
          [-127.16164, 9.605908999999997],
          [-127.163196, 9.613813],
          [-127.159808, 9.617308000000001],
          [-127.14043000000001, 9.626967999999998],
          [-127.10783599999999, 9.633056000000003],
          [-127.091377, 9.633437999999998],
          [-127.08457899999999, 9.626502000000002],
          [-127.07008200000001, 9.619857000000003],
          [-127.062187, 9.620058],
          [-127.009635, 9.635344000000003],
          [-127.00867099999999, 9.642040000000001],
          [-127.017863, 9.648606999999998],
          [-127.030011, 9.653559000000001],
          [-127.071823, 9.66556],
          [-127.06767400000001, 9.687266999999999],
          [-127.057695, 9.698864],
          [-127.041245, 9.707929],
          [-127.02238500000001, 9.715466999999997],
          [-126.999282, 9.718519999999998],
          [-126.92332400000001, 9.719718999999998],
          [-126.89497600000001, 9.717745999999998],
          [-126.85902200000001, 9.674439],
          [-126.86176900000001, 9.659233999999998],
          [-126.83272500000001, 9.657376],
          [-126.80587399999999, 9.665733000000003],
          [-126.779991, 9.719126000000003],
          [-126.787318, 9.734577000000002],
          [-126.856491, 9.747301],
          [-126.942766, 9.769562],
          [-126.960681, 9.776840999999997],
          [-126.97563500000001, 9.775253999999997],
          [-126.983294, 9.771348000000003],
          [-126.99284599999999, 9.762604000000003],
          [-127.00577799999999, 9.755446],
          [-127.016863, 9.754936],
          [-127.024981, 9.757241],
          [-127.07585900000001, 9.786271999999997],
          [-127.141966, 9.826932],
          [-127.14099200000001, 9.866774],
          [-127.058168, 9.929777999999999],
          [-127.031252, 9.945203999999997],
          [-126.930452, 9.976090999999997],
          [-126.87948800000001, 9.988715999999997],
          [-126.818635, 9.993198],
          [-126.75168099999999, 10.01605],
          [-126.746095, 10.016936000000001],
          [-126.74258699999999, 10.015501],
          [-126.728918, 10.003110999999997]
        ],
        [
          [-129.553937, 12.608682000000002],
          [-129.528659, 12.612181],
          [-129.507415, 12.610702000000003],
          [-129.473138, 12.601740999999997],
          [-129.47155, 12.598863999999999],
          [-129.490133, 12.583481999999997],
          [-129.568984, 12.540934999999998],
          [-129.582624, 12.536938999999997],
          [-129.640735, 12.542161999999998],
          [-129.650135, 12.544229999999999],
          [-129.657736, 12.547319000000002],
          [-129.667749, 12.554535000000001],
          [-129.672818, 12.560865999999997],
          [-129.671324, 12.567328000000003],
          [-129.675327, 12.578414000000002],
          [-129.683639, 12.58334],
          [-129.75575, 12.591921999999997],
          [-129.785692, 12.613245],
          [-129.789659, 12.618217000000001],
          [-129.763506, 12.620739],
          [-129.679305, 12.611593],
          [-129.611548, 12.606924],
          [-129.553937, 12.608682000000002]
        ],
        [
          [-130.286318, 13.128169],
          [-130.290793, 13.145052],
          [-130.303963, 13.154910000000001],
          [-130.32484, 13.156768999999997],
          [-130.359817, 13.156118],
          [-130.421867, 13.161202000000003],
          [-130.423548, 13.169327000000003],
          [-130.42041, 13.191760000000002],
          [-130.418919, 13.192844000000001],
          [-130.402772, 13.201932999999997],
          [-130.390121, 13.206248000000002],
          [-130.33188, 13.217488000000003],
          [-130.311707, 13.219121999999999],
          [-130.291916, 13.212055999999997],
          [-130.267664, 13.210648999999997],
          [-130.239557, 13.214658],
          [-130.161647, 13.229655999999999],
          [-130.150813, 13.223168000000001],
          [-130.170848, 13.1811],
          [-130.286318, 13.128169]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02060",
      state: "02",
      county: "060",
      name: "Bristol Bay Borough County",
      lsad: "Borough",
      censusArea: 503.843,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.98583300000001, 14.888654000000002],
          [-116.31813500000001, 14.894948],
          [-116.31767099999999, 14.609610000000004],
          [-117.267437, 14.609794],
          [-117.251462, 14.620786000000003],
          [-117.178834, 14.660440000000001],
          [-117.07791399999999, 14.708103000000001],
          [-117.061928, 14.726101999999997],
          [-117.00822600000001, 14.817138999999997],
          [-117.003401, 14.836821999999998],
          [-117.00360699999999, 14.839306],
          [-117.01098400000001, 14.848399999999998],
          [-117.016088, 14.863489999999999],
          [-117.012392, 14.875889],
          [-116.98583300000001, 14.888654000000002]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02290",
      state: "02",
      county: "290",
      name: "Yukon-Koyukuk County",
      lsad: "CA",
      censusArea: 145504.789,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.001339, 18.72744],
          [-113.001262, 18.295330999999997],
          [-113.06769700000001, 18.295211000000002],
          [-113.07031699999999, 18.208517],
          [-113.39432, 18.209114999999997],
          [-113.39540099999999, 18.124775999999997],
          [-113.74062900000001, 18.121564999999997],
          [-113.73584600000001, 18.02937],
          [-115.107471, 18.029370999999998],
          [-117.07359199999999, 18.029567],
          [-117.07423700000001, 18.129913000000002],
          [-117.51702, 18.123542999999998],
          [-118.532398, 18.118595],
          [-118.531113, 18.031708000000002],
          [-119.26095000000001, 18.032026000000002],
          [-119.265098, 17.945563999999997],
          [-120.534142, 17.947257],
          [-120.913973, 17.949378000000003],
          [-120.910169, 18.204383],
          [-121.047821, 18.206673000000002],
          [-121.04527999999999, 18.550449],
          [-121.00039699999999, 18.551764],
          [-120.99324100000001, 18.897962],
          [-120.942287, 18.898826999999997],
          [-120.941899, 18.998883],
          [-120.851144, 19.012689],
          [-120.847328, 19.269229000000003],
          [-120.67130499999999, 19.270829999999997],
          [-120.69205099999999, 19.536617999999997],
          [-120.49497099999999, 19.537148000000002],
          [-120.49346499999999, 19.613655],
          [-120.09787399999999, 19.614393],
          [-120.103703, 19.702263000000002],
          [-119.92924400000001, 19.703494],
          [-119.93077299999999, 19.793808],
          [-119.69929099999999, 19.792346000000002],
          [-119.731224, 20.052366000000006],
          [-119.956447, 20.051022000000003],
          [-119.97809799999999, 20.748491],
          [-119.77380299999999, 20.750296000000006],
          [-119.777177, 20.923088000000007],
          [-119.576911, 20.923743],
          [-119.578562, 21.262692],
          [-119.38476499999999, 21.261674],
          [-119.38671199999999, 21.523139999999998],
          [-119.59357600000001, 21.522689],
          [-119.59518700000001, 21.95809],
          [-118.966298, 21.957306000000003],
          [-118.965461, 22.123439000000005],
          [-117.894181, 22.125283999999994],
          [-117.892416, 22.476794999999996],
          [-117.051694, 22.478750000000005],
          [-117.05153000000001, 22.302054],
          [-116.626712, 22.304930999999996],
          [-116.62429599999999, 22.478785000000002],
          [-116.196191, 22.479445999999996],
          [-116.19717299999999, 22.394757999999996],
          [-115.981539, 22.398030000000006],
          [-115.981123, 22.310447999999994],
          [-115.56243, 22.309487000000004],
          [-115.554608, 22.481911999999994],
          [-115.51393100000001, 22.569278999999995],
          [-114.865962, 22.567723],
          [-114.862595, 22.71691],
          [-114.202144, 22.716586000000007],
          [-114.20154199999999, 22.804620999999997],
          [-114.14159900000001, 22.804852999999994],
          [-114.14522, 23.161924999999997],
          [-114.302303, 23.160767000000007],
          [-114.306093, 23.251225000000005],
          [-114.748759, 23.254419],
          [-114.747501, 23.512680000000003],
          [-114.68835899999999, 23.512480999999994],
          [-114.68669600000001, 23.602172999999993],
          [-114.91102699999999, 23.601856999999995],
          [-114.90473600000001, 23.688032000000007],
          [-115.13235499999999, 23.686611999999997],
          [-115.129726, 23.774969999999996],
          [-115.352249, 23.775553000000002],
          [-115.30699200000001, 23.864033000000006],
          [-115.29802000000001, 24.001771000000005],
          [-111.606277, 24.006861999999998],
          [-109.62064799999999, 23.998940000000005],
          [-107.389781, 24.001041],
          [-105.994865, 24.001284999999996],
          [-105.99821600000001, 24.489870999999994],
          [-104.29705200000001, 24.507296999999994],
          [-102.360513, 24.489642000000003],
          [-101.00269399999999, 24.498390999999998],
          [-101.002465, 21.840075],
          [-101.002465, 21.839421],
          [-101.093233, 21.827388],
          [-101.33622399999999, 21.711613999999997],
          [-101.387842, 21.614395000000002],
          [-101.78905, 21.501546000000005],
          [-101.855761, 21.445786],
          [-102.12137999999999, 21.471689999999995],
          [-102.30708100000001, 21.440633000000005],
          [-102.487301, 21.371898],
          [-102.62997100000001, 21.385368],
          [-102.72636399999999, 21.307742000000005],
          [-102.7319, 21.233638999999997],
          [-102.84708900000001, 21.145563999999993],
          [-103.081816, 21.120936999999998],
          [-103.36447799999999, 20.996995999999996],
          [-103.525172, 20.960080000000005],
          [-103.50364300000001, 20.858250999999996],
          [-103.433715, 20.821827999999996],
          [-103.647213, 20.669506],
          [-103.751216, 20.647777000000005],
          [-104.061159, 20.683442],
          [-104.12008900000001, 20.798316999999997],
          [-103.99204499999999, 20.977011000000005],
          [-104.105524, 21.015541999999996],
          [-103.884795, 21.090281000000004],
          [-103.977969, 21.119043000000005],
          [-104.24718999999999, 21.118774000000002],
          [-104.44544300000001, 21.062319000000002],
          [-104.89138700000001, 21.137197],
          [-105.20990599999999, 21.072978000000006],
          [-105.41748000000001, 21.084835999999996],
          [-105.64643699999999, 21.033682],
          [-105.74968100000001, 21.098232999999993],
          [-105.615545, 21.14407],
          [-105.873843, 21.214026000000004],
          [-106.078123, 21.245244],
          [-106.13753, 21.309850999999995],
          [-105.994261, 21.406031],
          [-106.11975799999999, 21.405822999999998],
          [-106.192406, 21.454475000000002],
          [-106.490362, 21.386877999999996],
          [-106.56881899999999, 21.344746],
          [-106.919725, 21.27297],
          [-107.017089, 21.290431999999996],
          [-107.334764, 21.273291],
          [-107.55024499999999, 21.211022999999997],
          [-108.66384400000001, 21.211354999999998],
          [-108.642697, 20.865694000000005],
          [-108.65236999999999, 20.591053000000002],
          [-108.441058, 20.577791000000005],
          [-108.38050899999999, 20.520561999999998],
          [-108.242175, 20.483425999999994],
          [-108.229784, 20.436621000000002],
          [-107.995157, 20.341690999999997],
          [-109.12070399999999, 20.343615],
          [-109.119968, 20.358795999999998],
          [-109.667993, 20.357812999999993],
          [-110.74973600000001, 20.365138],
          [-111.13619499999999, 20.133083999999997],
          [-111.301289, 20.009970999999993],
          [-111.52511100000001, 20.032465000000002],
          [-111.579286, 20.08538],
          [-111.790558, 20.08229],
          [-111.82505800000001, 20.005364999999998],
          [-112.04737700000001, 20.000575999999995],
          [-112.055519, 19.823679],
          [-112.23355699999999, 19.823684],
          [-112.241918, 19.656678999999997],
          [-112.85343, 19.65171],
          [-112.812007, 19.351232000000003],
          [-112.43462500000001, 19.305936000000003],
          [-112.434247, 19.169794000000003],
          [-113.001339, 18.72744]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US02282",
      state: "02",
      county: "282",
      name: "Yakutat County",
      lsad: "Cty&Bor",
      censusArea: 7649.457,
      stateName: "Alaska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.525295, 14.906872],
          [-97.94425899999999, 14.802349],
          [-97.94495699999999, 14.804651999999997],
          [-97.93935300000001, 14.813721000000001],
          [-97.931565, 14.819786999999998],
          [-97.92762400000001, 14.827187000000002],
          [-97.924608, 14.843927999999998],
          [-97.932593, 14.868493999999998],
          [-97.95199500000001, 14.886029],
          [-97.985198, 14.909525000000002],
          [-98.06633199999999, 14.957126000000002],
          [-98.136246, 14.989026000000003],
          [-98.131, 15.002612999999997],
          [-98.11755099999999, 15.012493999999997],
          [-98.118853, 15.021307],
          [-98.14459400000001, 15.028072000000002],
          [-98.250531, 15.047030999999997],
          [-98.63670200000001, 15.130585000000004],
          [-98.70590000000001, 15.162548999999999],
          [-98.76346699999999, 15.191319999999997],
          [-98.847498, 15.224834999999999],
          [-98.919749, 15.248531],
          [-99.04459299999999, 15.280341],
          [-99.271031, 15.337420999999999],
          [-99.34304900000001, 15.356608000000001],
          [-99.42016799999999, 15.379759999999997],
          [-99.541156, 15.423071],
          [-99.59518600000001, 15.445413000000002],
          [-99.746478, 15.503414999999997],
          [-99.85556500000001, 15.536659999999998],
          [-99.862547, 15.544258],
          [-99.86130600000001, 15.546678],
          [-99.84723600000001, 15.557304000000002],
          [-99.837817, 15.561984000000002],
          [-99.80716100000001, 15.554333],
          [-99.785068, 15.564042999999998],
          [-99.76867200000001, 15.597610000000003],
          [-99.737189, 15.632629000000001],
          [-99.732271, 15.636859999999999],
          [-99.71895799999999, 15.641156000000002],
          [-99.70809, 15.642657],
          [-99.667235, 15.638803000000003],
          [-99.65521799999999, 15.633496999999998],
          [-99.656432, 15.621209999999998],
          [-99.671436, 15.615682],
          [-99.67631, 15.611249],
          [-99.67240799999999, 15.602894],
          [-99.65457900000001, 15.598014999999997],
          [-99.62312499999999, 15.595908999999999],
          [-99.61451299999999, 15.597135000000002],
          [-99.58713499999999, 15.605958999999999],
          [-99.581447, 15.609171000000003],
          [-99.582528, 15.613542000000002],
          [-99.589369, 15.618673999999999],
          [-99.59348800000001, 15.624316999999998],
          [-99.585789, 15.642764999999997],
          [-99.51818, 15.687814000000003],
          [-99.524011, 15.712510000000002],
          [-99.552648, 15.706834999999998],
          [-99.579024, 15.712198],
          [-99.587176, 15.715649999999997],
          [-99.59335999999999, 15.721502999999998],
          [-99.59198799999999, 15.725026],
          [-99.58157800000001, 15.735062999999997],
          [-99.576389, 15.738179000000002],
          [-99.55937599999999, 15.742699000000002],
          [-99.54611, 15.752667000000002],
          [-99.562049, 15.774887],
          [-99.57740999999999, 15.789332000000002],
          [-99.59043, 15.810232],
          [-99.61721399999999, 15.863576000000002],
          [-99.624665, 15.884388000000001],
          [-99.62131299999999, 15.906725000000002],
          [-99.61691200000001, 15.911999000000002],
          [-99.610762, 15.913758999999999],
          [-99.59723199999999, 15.913651999999999],
          [-99.57723200000001, 15.918264999999998],
          [-99.535902, 15.935248000000001],
          [-99.527455, 15.940047],
          [-99.48870199999999, 15.995033999999997],
          [-99.486032, 16.012407000000003],
          [-99.50538900000001, 16.039428],
          [-99.55515700000001, 16.039243],
          [-99.576819, 16.015425],
          [-99.60185200000001, 15.959865999999998],
          [-99.60579000000001, 15.955599999999997],
          [-99.65745100000001, 15.944727],
          [-99.682456, 15.943984],
          [-99.693423, 15.940730000000002],
          [-99.70532800000001, 15.934826000000001],
          [-99.76953700000001, 15.878107999999997],
          [-99.76861199999999, 15.85116],
          [-99.77551700000001, 15.845210000000002],
          [-99.801197, 15.832586],
          [-99.811185, 15.829332],
          [-99.909851, 15.806069999999998],
          [-100.10259099999999, 15.754910000000002],
          [-100.14108999999999, 15.747979],
          [-100.164657, 15.741878],
          [-100.178132, 15.735627999999998],
          [-100.18861000000001, 15.725248],
          [-100.21090699999999, 15.715535000000003],
          [-100.256351, 15.703052],
          [-100.272266, 15.700609],
          [-100.28555700000001, 15.698717000000002],
          [-100.3144, 15.698301999999998],
          [-100.38502199999999, 15.699480000000001],
          [-100.60167200000001, 15.712952999999999],
          [-100.636639, 15.711409000000003],
          [-100.72198, 15.718563000000003],
          [-100.88358299999999, 15.737613000000003],
          [-100.92722, 15.745708999999998],
          [-101.013338, 15.773338000000003],
          [-101.156497, 15.813581999999997],
          [-101.216148, 15.827285000000003],
          [-101.423923, 15.867361000000002],
          [-101.442023, 15.873323999999997],
          [-101.45050599999999, 15.890419000000001],
          [-101.46752799999999, 15.971271999999999],
          [-101.530295, 15.977654999999999],
          [-101.631361, 15.962761999999998],
          [-101.70905400000001, 15.948937999999998],
          [-101.869766, 15.998834000000002],
          [-101.912218, 16.009779],
          [-101.96617800000001, 16.019129],
          [-101.998818, 16.022606000000003],
          [-102.062454, 16.023781],
          [-102.10005899999999, 16.026772],
          [-102.13004000000001, 16.030327],
          [-102.24518, 16.049778000000003],
          [-102.537534, 16.083953],
          [-102.589676, 16.088182000000003],
          [-102.698419, 16.093333],
          [-102.809852, 16.095216999999998],
          [-102.875248, 16.092427999999998],
          [-102.908859, 16.090328],
          [-103.0687, 16.068603000000003],
          [-103.135616, 16.062081999999997],
          [-103.194276, 16.061995000000003],
          [-103.413377, 16.051924],
          [-103.62415200000001, 16.037256999999997],
          [-103.69899000000001, 16.027760999999998],
          [-103.88547399999999, 15.996786999999998],
          [-103.889952, 16.042299999999997],
          [-103.16706099999999, 16.518816],
          [-102.81732400000001, 16.515849000000003],
          [-101.963079, 16.521375999999997],
          [-101.765581, 16.527746],
          [-101.76369600000001, 16.435026999999998],
          [-101.21369100000001, 16.435809],
          [-101.213615, 16.392456000000003],
          [-101.00184999999999, 16.391688000000002],
          [-101.00183999999999, 16.306105000000002],
          [-100.53509, 16.224224],
          [-100.47229200000001, 16.310589999999998],
          [-99.98914199999999, 16.18524],
          [-99.698361, 16.340421],
          [-99.086669, 16.357653999999997],
          [-99.082246, 16.323825],
          [-99.200346, 16.090701000000003],
          [-99.046426, 15.998235000000001],
          [-98.79608300000001, 15.928700999999997],
          [-98.702053, 15.910245000000003],
          [-98.662769, 15.813718999999999],
          [-98.662972, 15.810225000000003],
          [-98.62093100000001, 15.770558999999999],
          [-98.560226, 15.741200999999997],
          [-98.001128, 15.452164000000003],
          [-97.604277, 15.243057],
          [-97.498558, 14.986694],
          [-97.52642399999999, 14.906834000000003],
          [-97.525295, 14.906872]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01001",
      state: "01",
      county: "001",
      name: "Autauga County",
      censusArea: 594.436,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.496774, 32.344437],
          [-86.717897, 32.402814],
          [-86.814912, 32.340803],
          [-86.890581, 32.502974],
          [-86.917595, 32.664169],
          [-86.71339, 32.661732],
          [-86.714219, 32.705694],
          [-86.413116, 32.707386],
          [-86.411172, 32.409937],
          [-86.496774, 32.344437]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01009",
      state: "01",
      county: "009",
      name: "Blount County",
      censusArea: 644.776,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.577799, 33.765316],
          [-86.759144, 33.840617],
          [-86.953664, 33.815297],
          [-86.954305, 33.844862],
          [-86.96296, 33.844865],
          [-86.963358, 33.858221],
          [-86.924387, 33.909222],
          [-86.793914, 33.952059],
          [-86.685365, 34.05914],
          [-86.692061, 34.092654],
          [-86.599632, 34.119914],
          [-86.514881, 34.25437],
          [-86.45302, 34.259317],
          [-86.303516, 34.099073],
          [-86.332723, 33.986109],
          [-86.370152, 33.93977],
          [-86.325622, 33.940147],
          [-86.377532, 33.861706],
          [-86.577528, 33.801977],
          [-86.577799, 33.765316]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01017",
      state: "01",
      county: "017",
      name: "Chambers County",
      censusArea: 596.531,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.184131, 32.870525],
          [-85.123421, 32.772248],
          [-85.13204, 32.764211],
          [-85.136776, 32.746512],
          [-85.285043, 32.730731],
          [-85.593151, 32.72853],
          [-85.593177, 33.10734],
          [-85.232378, 33.108077],
          [-85.223261, 33.06258],
          [-85.221868, 33.055538],
          [-85.184131, 32.870525]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01021",
      state: "01",
      county: "021",
      name: "Chilton County",
      censusArea: 692.854,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.517344, 33.020566],
          [-86.515959, 32.929361],
          [-86.457015, 32.813899],
          [-86.374974, 32.75358],
          [-86.413335, 32.750591],
          [-86.413116, 32.707386],
          [-86.714219, 32.705694],
          [-86.71339, 32.661732],
          [-86.917595, 32.664169],
          [-87.01766, 32.663269],
          [-87.017762, 32.729532],
          [-87.019157, 32.837034],
          [-86.876118, 32.836264],
          [-86.881182, 33.049901],
          [-86.881638, 33.071861],
          [-86.610003, 33.070003],
          [-86.517344, 33.020566]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01033",
      state: "01",
      county: "033",
      name: "Colbert County",
      censusArea: 592.619,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.139988, 34.581703],
          [-88.139246, 34.587795],
          [-88.138719, 34.589215],
          [-88.118407, 34.724292],
          [-88.097888, 34.892202],
          [-87.973222, 34.88255],
          [-87.835833, 34.741239],
          [-87.524363, 34.832384],
          [-87.42651, 34.800022],
          [-87.416484, 34.767544],
          [-87.529667, 34.567081],
          [-88.139988, 34.581703]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01045",
      state: "01",
      county: "045",
      name: "Dale County",
      censusArea: 561.15,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.416437, 31.619466],
          [-85.417434, 31.314973],
          [-85.485854, 31.246096],
          [-85.659985, 31.270889],
          [-85.710866, 31.195179],
          [-85.791402, 31.196349],
          [-85.789142, 31.617964],
          [-85.749941, 31.617914],
          [-85.748251, 31.618048],
          [-85.416437, 31.619466]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01051",
      state: "01",
      county: "051",
      name: "Elmore County",
      censusArea: 618.485,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.336776, 32.767469],
          [-86.31948, 32.753698],
          [-86.007187, 32.754984],
          [-85.87986, 32.754528],
          [-85.886148, 32.493053],
          [-85.89442, 32.447578],
          [-86.023012, 32.419978],
          [-86.192284, 32.43613],
          [-86.237068, 32.49498],
          [-86.411172, 32.409937],
          [-86.413116, 32.707386],
          [-86.413335, 32.750591],
          [-86.374974, 32.75358],
          [-86.336768, 32.753783],
          [-86.336776, 32.767469]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01065",
      state: "01",
      county: "065",
      name: "Hale County",
      censusArea: 643.943,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.4212, 32.874508],
          [-87.472174, 32.83062],
          [-87.473915, 32.655867],
          [-87.525198, 32.655713],
          [-87.524485, 32.482028],
          [-87.728744, 32.480918],
          [-87.812559, 32.52456],
          [-87.736786, 32.58919],
          [-87.813401, 32.633875],
          [-87.796043, 32.715632],
          [-87.828542, 32.768903],
          [-87.870464, 32.762442],
          [-87.715709, 33.006824],
          [-87.421936, 33.003379],
          [-87.420132, 32.902605],
          [-87.4212, 32.874508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01079",
      state: "01",
      county: "079",
      name: "Lawrence County",
      censusArea: 690.678,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.105073, 34.686037],
          [-87.105913, 34.587643],
          [-87.110111, 34.313799],
          [-87.109911, 34.299299],
          [-87.529722, 34.304598],
          [-87.529667, 34.567081],
          [-87.416484, 34.767544],
          [-87.42651, 34.800022],
          [-87.260676, 34.758626],
          [-87.105073, 34.686037]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01083",
      state: "01",
      county: "083",
      name: "Limestone County",
      censusArea: 559.936,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.836306, 34.991764],
          [-86.820657, 34.991764],
          [-86.783648, 34.991925],
          [-86.783628, 34.991925],
          [-86.790056, 34.55079],
          [-86.942899, 34.598053],
          [-87.105073, 34.686037],
          [-87.260676, 34.758626],
          [-87.214914, 34.816011],
          [-87.210759, 34.999024],
          [-87.011174, 34.995162],
          [-87.000007, 34.995121],
          [-86.974412, 34.994513],
          [-86.972613, 34.99461],
          [-86.970236, 34.994546],
          [-86.96712, 34.9944],
          [-86.862147, 34.991956],
          [-86.849794, 34.991924],
          [-86.846466, 34.99186],
          [-86.83637, 34.991764],
          [-86.836306, 34.991764]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01099",
      state: "01",
      county: "099",
      name: "Monroe County",
      censusArea: 1025.675,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.905899, 31.753035],
          [-87.052284, 31.716761],
          [-87.135051, 31.642417],
          [-87.166581, 31.519561],
          [-87.427455, 31.260386],
          [-87.61589, 31.244458],
          [-87.765152, 31.297346],
          [-87.784796, 31.324672],
          [-87.665572, 31.423166],
          [-87.603137, 31.409556],
          [-87.565413, 31.553573],
          [-87.566841, 31.697115],
          [-87.516131, 31.697824],
          [-87.50093, 31.829251],
          [-86.906899, 31.830628],
          [-86.905899, 31.753035]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01107",
      state: "01",
      county: "107",
      name: "Pickens County",
      censusArea: 881.408,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.291127, 33.399061],
          [-88.277421, 33.512436],
          [-88.276805, 33.516463],
          [-88.274619, 33.534008],
          [-87.946519, 33.524065],
          [-87.85856, 33.525326],
          [-87.840683, 33.524839],
          [-87.837521, 33.153637],
          [-88.026971, 33.063602],
          [-88.171852, 32.99586],
          [-88.340432, 32.991199],
          [-88.304514, 33.28832],
          [-88.291127, 33.399061]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01121",
      state: "01",
      county: "121",
      name: "Talladega County",
      censusArea: 736.775,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.17437, 33.104394],
          [-86.226271, 33.104115],
          [-86.491029, 33.102944],
          [-86.503127, 33.179144],
          [-86.35734, 33.296917],
          [-86.378665, 33.390983],
          [-86.281999, 33.509895],
          [-86.235328, 33.494532],
          [-86.145562, 33.679098],
          [-86.050669, 33.67459],
          [-85.994935, 33.586475],
          [-85.794559, 33.585565],
          [-85.796054, 33.55622],
          [-85.796852, 33.541849],
          [-85.85189, 33.498742],
          [-85.904909, 33.498655],
          [-85.923762, 33.396206],
          [-85.976525, 33.38187],
          [-85.980293, 33.29419],
          [-86.118198, 33.29632],
          [-86.120567, 33.194511],
          [-86.17283, 33.195681],
          [-86.17437, 33.104394]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05141",
      state: "05",
      county: "141",
      name: "Van Buren County",
      censusArea: 708.143,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.850624, 35.46295],
          [-92.813417, 35.549],
          [-92.809763, 35.722401],
          [-92.631207, 35.718365],
          [-92.629837, 35.790823],
          [-92.416719, 35.78859],
          [-92.418408, 35.716046],
          [-92.24052, 35.71219],
          [-92.252759, 35.363718],
          [-92.481469, 35.368392],
          [-92.479268, 35.455357],
          [-92.850624, 35.46295]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05145",
      state: "05",
      county: "145",
      name: "White County",
      censusArea: 1035.075,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.465114, 35.089454],
          [-91.584005, 35.091584],
          [-91.5851, 35.025234],
          [-91.695463, 35.062344],
          [-91.802505, 35.030425],
          [-91.87888, 35.024577],
          [-92.013056, 35.076317],
          [-92.119719, 35.066493],
          [-92.111463, 35.362725],
          [-91.851958, 35.358287],
          [-91.850144, 35.436442],
          [-91.795855, 35.455574],
          [-91.793958, 35.534114],
          [-91.58188, 35.530447],
          [-91.584687, 35.442311],
          [-91.347257, 35.439106],
          [-91.438202, 35.369378],
          [-91.390375, 35.251624],
          [-91.371035, 35.262981],
          [-91.465114, 35.089454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06005",
      state: "06",
      county: "005",
      name: "Amador County",
      censusArea: 594.583,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.995497, 38.225405],
          [-121.027084, 38.300252],
          [-121.027507, 38.508292],
          [-120.813554, 38.562193],
          [-120.627604, 38.503143],
          [-120.510596, 38.511467],
          [-120.301721, 38.549109],
          [-120.098322, 38.709029],
          [-120.072392, 38.702767],
          [-120.072484, 38.509869],
          [-120.380707, 38.461042],
          [-120.423215, 38.473322],
          [-120.893792, 38.221277],
          [-120.995497, 38.225405]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06021",
      state: "06",
      county: "021",
      name: "Glenn County",
      censusArea: 1313.947,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.937654, 39.798156],
          [-122.046471, 39.797648],
          [-121.950951, 39.715004],
          [-121.994021, 39.533926],
          [-121.856532, 39.536904],
          [-121.890013, 39.383864],
          [-122.136161, 39.414499],
          [-122.136006, 39.385494],
          [-122.739062, 39.383266],
          [-122.735639, 39.580668],
          [-122.885352, 39.580108],
          [-122.892676, 39.708898],
          [-122.937654, 39.798156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06033",
      state: "06",
      county: "033",
      name: "Lake County",
      censusArea: 1256.464,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.627396, 38.667506],
          [-122.821592, 38.85014],
          [-122.948865, 38.90022],
          [-122.98664, 38.997283],
          [-123.028035, 38.996593],
          [-123.093013, 39.071994],
          [-123.077812, 39.173791],
          [-122.994839, 39.235924],
          [-123.075114, 39.407683],
          [-123.063181, 39.503539],
          [-122.89031, 39.529014],
          [-122.885352, 39.580108],
          [-122.735639, 39.580668],
          [-122.739062, 39.383266],
          [-122.78509, 39.382975],
          [-122.775043, 39.311778],
          [-122.680927, 39.238726],
          [-122.476083, 39.172609],
          [-122.492215, 39.054092],
          [-122.340172, 38.924246],
          [-122.395056, 38.864245],
          [-122.46389, 38.705203],
          [-122.627396, 38.667506]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06043",
      state: "06",
      county: "043",
      name: "Mariposa County",
      censusArea: 1448.816,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.38767, 37.633364],
          [-120.387329, 37.633499],
          [-120.387613, 37.633704],
          [-120.391931, 37.683559],
          [-120.325943, 37.648966],
          [-120.345437, 37.724787],
          [-120.127226, 37.781566],
          [-120.079508, 37.828808],
          [-119.907013, 37.757926],
          [-119.809409, 37.755025],
          [-119.667203, 37.801224],
          [-119.646203, 37.846823],
          [-119.535699, 37.904122],
          [-119.308995, 37.777986],
          [-119.583585, 37.560335],
          [-119.58422, 37.494696],
          [-119.651172, 37.461351],
          [-119.651191, 37.417832],
          [-119.761809, 37.417114],
          [-120.052055, 37.183108],
          [-120.143826, 37.239192],
          [-120.26601, 37.400607],
          [-120.38767, 37.633364]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06055",
      state: "06",
      county: "055",
      name: "Napa County",
      censusArea: 748.362,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.103281, 38.513348],
          [-122.088835, 38.384151],
          [-122.064779, 38.315912],
          [-122.205982, 38.315713],
          [-122.19538, 38.155017],
          [-122.406786, 38.155632],
          [-122.378987, 38.250714],
          [-122.543893, 38.519966],
          [-122.646268, 38.59912],
          [-122.627396, 38.667506],
          [-122.46389, 38.705203],
          [-122.395056, 38.864245],
          [-122.287999, 38.839932],
          [-122.164944, 38.642462],
          [-122.168349, 38.619297],
          [-122.151772, 38.625297],
          [-122.128123, 38.583697],
          [-122.111563, 38.524051],
          [-122.103281, 38.513348]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06089",
      state: "06",
      county: "089",
      name: "Shasta County",
      censusArea: 3775.402,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.065426, 40.286971],
          [-123.068789, 40.332233],
          [-122.998682, 40.418142],
          [-122.903738, 40.445071],
          [-122.845954, 40.505181],
          [-122.695298, 40.572514],
          [-122.752266, 40.689642],
          [-122.445453, 41.15793],
          [-122.498376, 41.182675],
          [-122.376123, 41.182904],
          [-121.446495, 41.183484],
          [-121.331786, 41.183886],
          [-121.319972, 40.905886],
          [-121.327826, 40.445367],
          [-121.497635, 40.445591],
          [-121.64633, 40.434726],
          [-121.806539, 40.44495],
          [-121.940989, 40.415331],
          [-122.010316, 40.426558],
          [-122.310412, 40.371064],
          [-122.445021, 40.373398],
          [-122.523997, 40.3945],
          [-122.651341, 40.328288],
          [-122.872769, 40.348848],
          [-122.918472, 40.306758],
          [-123.065426, 40.286971]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06099",
      state: "06",
      county: "099",
      name: "Stanislaus County",
      censusArea: 1494.827,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.931374, 37.421753],
          [-120.964223, 37.345782],
          [-121.226804, 37.134774],
          [-121.282271, 37.183675],
          [-121.404636, 37.155989],
          [-121.459051, 37.282739],
          [-121.405753, 37.31099],
          [-121.472648, 37.48217],
          [-121.471925, 37.481783],
          [-121.333388, 37.59105],
          [-121.273751, 37.63833],
          [-121.258171, 37.653495],
          [-121.155849, 37.719844],
          [-120.995754, 37.760152],
          [-120.920665, 37.738392],
          [-120.926449, 38.077421],
          [-120.652673, 37.831422],
          [-120.387613, 37.633704],
          [-120.387329, 37.633499],
          [-120.38767, 37.633364],
          [-120.931374, 37.421753]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06115",
      state: "06",
      county: "115",
      name: "Yuba County",
      censusArea: 631.839,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.333427, 39.468729],
          [-121.305122, 39.519585],
          [-121.149907, 39.526446],
          [-121.076695, 39.597264],
          [-121.05267, 39.612334],
          [-121.050922, 39.613801],
          [-121.009477, 39.639459],
          [-121.034406, 39.514509],
          [-121.022085, 39.391558],
          [-121.127481, 39.380237],
          [-121.266911, 39.271609],
          [-121.279533, 39.034618],
          [-121.414885, 38.996429],
          [-121.571297, 38.938918],
          [-121.62376, 39.295621],
          [-121.407527, 39.339754],
          [-121.333427, 39.468729]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08003",
      state: "08",
      county: "003",
      name: "Alamosa County",
      censusArea: 722.643,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.485516, 37.577899],
          [-105.743339, 37.356798],
          [-106.038912, 37.356953],
          [-106.039331, 37.400852],
          [-106.038413, 37.748353],
          [-105.457255, 37.751464],
          [-105.485516, 37.577899]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08013",
      state: "08",
      county: "013",
      name: "Boulder County",
      censusArea: 726.289,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.055089, 40.261793],
          [-105.055375, 40.144929],
          [-105.055376, 40.144692],
          [-105.055432, 40.000251],
          [-105.052823, 40.00026],
          [-105.052774, 39.986398],
          [-105.052792, 39.979489],
          [-105.052793, 39.97879],
          [-105.052794, 39.978581],
          [-105.052797, 39.977574],
          [-105.054688, 39.976889],
          [-105.057654, 39.97586],
          [-105.05784, 39.975787],
          [-105.058567, 39.975502],
          [-105.060445, 39.974768],
          [-105.060968, 39.974563],
          [-105.063527, 39.973444],
          [-105.066609, 39.971925],
          [-105.06742, 39.971289],
          [-105.069605, 39.970227],
          [-105.070468, 39.969721],
          [-105.071886, 39.968544],
          [-105.073059, 39.967308],
          [-105.074735, 39.965752],
          [-105.0757, 39.964623],
          [-105.078632, 39.962182],
          [-105.079885, 39.961657],
          [-105.07999, 39.961613],
          [-105.080562, 39.961373],
          [-105.081957, 39.961003],
          [-105.082093, 39.960967],
          [-105.084027, 39.960454],
          [-105.085583, 39.960123],
          [-105.085764, 39.960084],
          [-105.085877, 39.960061],
          [-105.085942, 39.960055],
          [-105.08595, 39.960055],
          [-105.085961, 39.960054],
          [-105.085982, 39.960052],
          [-105.086002, 39.96005],
          [-105.086082, 39.960043],
          [-105.086858, 39.959972],
          [-105.086951, 39.959741],
          [-105.087033, 39.959538],
          [-105.08731, 39.959551],
          [-105.087393, 39.959555],
          [-105.087748, 39.959571],
          [-105.087748, 39.95888],
          [-105.087748, 39.958707],
          [-105.087748, 39.958197],
          [-105.088636, 39.958182],
          [-105.088616, 39.958052],
          [-105.088615, 39.958042],
          [-105.088614, 39.958037],
          [-105.088613, 39.95803],
          [-105.088597, 39.957919],
          [-105.088588, 39.957865],
          [-105.088441, 39.957858],
          [-105.088332, 39.957853],
          [-105.088298, 39.957851],
          [-105.08787, 39.95784],
          [-105.087841, 39.957839],
          [-105.087834, 39.957839],
          [-105.087724, 39.957838],
          [-105.087315, 39.957833],
          [-105.086948, 39.957843],
          [-105.086861, 39.957849],
          [-105.085846, 39.957893],
          [-105.085794, 39.957938],
          [-105.085754, 39.957971],
          [-105.085734, 39.957988],
          [-105.085717, 39.958003],
          [-105.085702, 39.958016],
          [-105.085683, 39.95802],
          [-105.085673, 39.958022],
          [-105.085663, 39.958024],
          [-105.08565, 39.958027],
          [-105.085638, 39.95803],
          [-105.085611, 39.958036],
          [-105.08555, 39.95805],
          [-105.085476, 39.958066],
          [-105.08541, 39.958081],
          [-105.085379, 39.958088],
          [-105.084891, 39.958201],
          [-105.083963, 39.958302],
          [-105.083267, 39.958267],
          [-105.083261, 39.957915],
          [-105.082801, 39.957912],
          [-105.082768, 39.957912],
          [-105.082756, 39.957912],
          [-105.082534, 39.957911],
          [-105.082463, 39.957908],
          [-105.082455, 39.957908],
          [-105.082448, 39.957908],
          [-105.080966, 39.957839],
          [-105.080602, 39.957822],
          [-105.080305, 39.957808],
          [-105.080028, 39.957847],
          [-105.079729, 39.95784],
          [-105.079302, 39.957829],
          [-105.079166, 39.957826],
          [-105.077331, 39.95781],
          [-105.076823, 39.957805],
          [-105.076586, 39.957805],
          [-105.075858, 39.957803],
          [-105.075312, 39.957802],
          [-105.074639, 39.9578],
          [-105.072278, 39.957787],
          [-105.071933, 39.957785],
          [-105.071916, 39.957785],
          [-105.071854, 39.957785],
          [-105.07178, 39.957785],
          [-105.071778, 39.957502],
          [-105.071738, 39.950585],
          [-105.071893, 39.950585],
          [-105.072241, 39.950586],
          [-105.072518, 39.950586],
          [-105.075827, 39.950592],
          [-105.076104, 39.950592],
          [-105.076102, 39.947539],
          [-105.076102, 39.947048],
          [-105.076102, 39.946892],
          [-105.076102, 39.946353],
          [-105.076101, 39.944607],
          [-105.076101, 39.943904],
          [-105.076101, 39.943726],
          [-105.076101, 39.943456],
          [-105.076101, 39.943185],
          [-105.085739, 39.943185],
          [-105.090305, 39.943137],
          [-105.090324, 39.943137],
          [-105.090532, 39.943137],
          [-105.090541, 39.943137],
          [-105.090677, 39.943135],
          [-105.090897, 39.943134],
          [-105.090912, 39.941583],
          [-105.090799, 39.941546],
          [-105.090849, 39.939979],
          [-105.090819, 39.939971],
          [-105.090798, 39.939966],
          [-105.090782, 39.939962],
          [-105.090787, 39.939887],
          [-105.090787, 39.939879],
          [-105.09083, 39.938827],
          [-105.090851, 39.937836],
          [-105.090854, 39.936386],
          [-105.090944, 39.936387],
          [-105.09099, 39.936387],
          [-105.09101, 39.936386],
          [-105.093793, 39.936385],
          [-105.102719, 39.936386],
          [-105.102808, 39.936386],
          [-105.107539, 39.936386],
          [-105.107886, 39.936411],
          [-105.107985, 39.936417],
          [-105.10802, 39.936419],
          [-105.108678, 39.936461],
          [-105.108704, 39.936463],
          [-105.108727, 39.936464],
          [-105.108746, 39.936465],
          [-105.10879, 39.936468],
          [-105.109036, 39.936484],
          [-105.109038, 39.936134],
          [-105.109038, 39.936124],
          [-105.109039, 39.936119],
          [-105.109039, 39.936114],
          [-105.109039, 39.936095],
          [-105.109039, 39.936061],
          [-105.109041, 39.935754],
          [-105.109041, 39.935664],
          [-105.109224, 39.935546],
          [-105.109358, 39.935463],
          [-105.109979, 39.935062],
          [-105.110014, 39.935039],
          [-105.112293, 39.933567],
          [-105.112311, 39.933555],
          [-105.112368, 39.933518],
          [-105.112504, 39.933426],
          [-105.112775, 39.933219],
          [-105.112874, 39.933133],
          [-105.112975, 39.933034],
          [-105.113155, 39.93283],
          [-105.113232, 39.932731],
          [-105.113301, 39.932627],
          [-105.113388, 39.932479],
          [-105.113488, 39.932275],
          [-105.113541, 39.932126],
          [-105.113544, 39.932118],
          [-105.113566, 39.932055],
          [-105.113597, 39.931942],
          [-105.113626, 39.931805],
          [-105.113652, 39.931597],
          [-105.113657, 39.93144],
          [-105.11365, 39.931279],
          [-105.113636, 39.931163],
          [-105.113606, 39.930994],
          [-105.113579, 39.930895],
          [-105.113575, 39.930882],
          [-105.113569, 39.930862],
          [-105.113515, 39.930702],
          [-105.113446, 39.930541],
          [-105.113296, 39.930249],
          [-105.112408, 39.928626],
          [-105.112407, 39.928625],
          [-105.112269, 39.928399],
          [-105.11219, 39.928284],
          [-105.112132, 39.9282],
          [-105.11212, 39.928185],
          [-105.111966, 39.927989],
          [-105.111784, 39.927785],
          [-105.111558, 39.92756],
          [-105.111479, 39.927488],
          [-105.111189, 39.927252],
          [-105.110917, 39.927058],
          [-105.110584, 39.92685],
          [-105.109267, 39.926027],
          [-105.109259, 39.926022],
          [-105.109023, 39.925874],
          [-105.109043, 39.925744],
          [-105.109052, 39.925619],
          [-105.109141, 39.924586],
          [-105.109142, 39.924356],
          [-105.109412, 39.924438],
          [-105.109527, 39.924473],
          [-105.109641, 39.924507],
          [-105.109826, 39.924575],
          [-105.109921, 39.92462],
          [-105.109965, 39.924641],
          [-105.10998, 39.924648],
          [-105.110027, 39.924671],
          [-105.110043, 39.92468],
          [-105.110061, 39.92469],
          [-105.11007, 39.924695],
          [-105.110077, 39.924698],
          [-105.110214, 39.92477],
          [-105.110454, 39.924908],
          [-105.110628, 39.925009],
          [-105.110747, 39.925082],
          [-105.110767, 39.925094],
          [-105.110782, 39.925103],
          [-105.110794, 39.92511],
          [-105.1108, 39.925114],
          [-105.11087, 39.925157],
          [-105.110903, 39.925177],
          [-105.110958, 39.925211],
          [-105.110974, 39.925221],
          [-105.110983, 39.925227],
          [-105.110993, 39.925233],
          [-105.111315, 39.925433],
          [-105.111376, 39.925471],
          [-105.111383, 39.925475],
          [-105.111475, 39.925532],
          [-105.111631, 39.925636],
          [-105.111645, 39.925645],
          [-105.111659, 39.925654],
          [-105.111682, 39.925669],
          [-105.111823, 39.925771],
          [-105.111845, 39.925787],
          [-105.111851, 39.925791],
          [-105.111921, 39.925841],
          [-105.112246, 39.926106],
          [-105.112265, 39.926122],
          [-105.112274, 39.926129],
          [-105.112355, 39.926195],
          [-105.112362, 39.926201],
          [-105.112369, 39.926207],
          [-105.112653, 39.926453],
          [-105.112755, 39.926541],
          [-105.112799, 39.926579],
          [-105.112883, 39.926656],
          [-105.112887, 39.92666],
          [-105.112897, 39.926669],
          [-105.112903, 39.926674],
          [-105.112934, 39.926702],
          [-105.11298, 39.926744],
          [-105.112985, 39.926748],
          [-105.113051, 39.926808],
          [-105.113159, 39.926912],
          [-105.113212, 39.926969],
          [-105.113261, 39.927021],
          [-105.113325, 39.927097],
          [-105.113403, 39.927204],
          [-105.113472, 39.927321],
          [-105.113523, 39.92743],
          [-105.113529, 39.927449],
          [-105.113532, 39.927457],
          [-105.113542, 39.927488],
          [-105.113586, 39.927621],
          [-105.113594, 39.927659],
          [-105.113595, 39.927665],
          [-105.113597, 39.927673],
          [-105.113599, 39.927681],
          [-105.113665, 39.927993],
          [-105.11369, 39.928108],
          [-105.113691, 39.928115],
          [-105.113707, 39.928188],
          [-105.113759, 39.928362],
          [-105.113771, 39.928394],
          [-105.113808, 39.928493],
          [-105.113827, 39.928538],
          [-105.11383, 39.928545],
          [-105.113833, 39.928551],
          [-105.113835, 39.928556],
          [-105.113859, 39.928611],
          [-105.113867, 39.928629],
          [-105.113969, 39.928841],
          [-105.114036, 39.928981],
          [-105.114072, 39.929052],
          [-105.114064, 39.929181],
          [-105.114057, 39.9294],
          [-105.114437, 39.930166],
          [-105.116589, 39.931964],
          [-105.116795, 39.932137],
          [-105.116828, 39.932164],
          [-105.117035, 39.932337],
          [-105.118828, 39.933841],
          [-105.118947, 39.933783],
          [-105.118946, 39.933833],
          [-105.118946, 39.933841],
          [-105.118934, 39.93394],
          [-105.118904, 39.934077],
          [-105.118861, 39.934198],
          [-105.118801, 39.934323],
          [-105.11873, 39.934449],
          [-105.118727, 39.934454],
          [-105.118507, 39.934844],
          [-105.118468, 39.934947],
          [-105.118459, 39.934996],
          [-105.118449, 39.935048],
          [-105.118448, 39.935068],
          [-105.118446, 39.935113],
          [-105.118466, 39.935216],
          [-105.11848, 39.935249],
          [-105.118482, 39.935254],
          [-105.118545, 39.935402],
          [-105.118569, 39.935451],
          [-105.118593, 39.9355],
          [-105.118598, 39.93551],
          [-105.118601, 39.935517],
          [-105.118607, 39.93553],
          [-105.118659, 39.93564],
          [-105.118671, 39.935671],
          [-105.118674, 39.935679],
          [-105.118676, 39.935685],
          [-105.118694, 39.93573],
          [-105.118714, 39.93578],
          [-105.118729, 39.935834],
          [-105.11873, 39.935839],
          [-105.118732, 39.935845],
          [-105.118734, 39.935851],
          [-105.118735, 39.935856],
          [-105.118738, 39.935874],
          [-105.118743, 39.93591],
          [-105.118745, 39.935979],
          [-105.118752, 39.936194],
          [-105.118752, 39.936199],
          [-105.118747, 39.936383],
          [-105.118735, 39.936811],
          [-105.118722, 39.937324],
          [-105.118722, 39.937527],
          [-105.118722, 39.938463],
          [-105.118721, 39.939033],
          [-105.118721, 39.939174],
          [-105.118721, 39.939248],
          [-105.118721, 39.939269],
          [-105.118721, 39.939338],
          [-105.118718, 39.941225],
          [-105.118712, 39.942004],
          [-105.118711, 39.942011],
          [-105.118711, 39.942018],
          [-105.118711, 39.942029],
          [-105.118711, 39.942047],
          [-105.118711, 39.942071],
          [-105.118711, 39.942096],
          [-105.118704, 39.942904],
          [-105.118708, 39.943013],
          [-105.118715, 39.943061],
          [-105.118733, 39.94311],
          [-105.11877, 39.943165],
          [-105.118774, 39.943169],
          [-105.118792, 39.943187],
          [-105.118557, 39.943213],
          [-105.118377, 39.943213],
          [-105.110274, 39.943204],
          [-105.110094, 39.943204],
          [-105.109353, 39.94319],
          [-105.109338, 39.94319],
          [-105.109329, 39.94319],
          [-105.109309, 39.943189],
          [-105.109261, 39.943188],
          [-105.109057, 39.943184],
          [-105.10906, 39.943255],
          [-105.109103, 39.944125],
          [-105.10911, 39.944152],
          [-105.109321, 39.945021],
          [-105.109322, 39.945026],
          [-105.109323, 39.945031],
          [-105.109327, 39.945048],
          [-105.109337, 39.945088],
          [-105.109361, 39.945185],
          [-105.10936, 39.945481],
          [-105.10936, 39.945571],
          [-105.109358, 39.94605],
          [-105.109354, 39.946216],
          [-105.109354, 39.946225],
          [-105.109353, 39.946231],
          [-105.109353, 39.946244],
          [-105.109352, 39.946282],
          [-105.10933, 39.94721],
          [-105.109329, 39.947465],
          [-105.109329, 39.947478],
          [-105.109328, 39.947569],
          [-105.109328, 39.947658],
          [-105.109327, 39.9478],
          [-105.109327, 39.947889],
          [-105.109323, 39.948834],
          [-105.109323, 39.948924],
          [-105.109323, 39.948953],
          [-105.109323, 39.948989],
          [-105.109323, 39.949032],
          [-105.109323, 39.949082],
          [-105.109323, 39.949137],
          [-105.109323, 39.949194],
          [-105.109323, 39.94922],
          [-105.109323, 39.949235],
          [-105.109323, 39.949242],
          [-105.109323, 39.949247],
          [-105.10932, 39.949529],
          [-105.109321, 39.950183],
          [-105.109321, 39.950264],
          [-105.109321, 39.950311],
          [-105.109321, 39.950341],
          [-105.109321, 39.950363],
          [-105.109321, 39.950379],
          [-105.109321, 39.950391],
          [-105.109321, 39.950401],
          [-105.109321, 39.950409],
          [-105.109321, 39.950415],
          [-105.109321, 39.950421],
          [-105.109321, 39.950426],
          [-105.109321, 39.950433],
          [-105.109321, 39.950439],
          [-105.109321, 39.950445],
          [-105.109321, 39.950451],
          [-105.109321, 39.950456],
          [-105.109321, 39.950461],
          [-105.109322, 39.950485],
          [-105.109435, 39.950486],
          [-105.109446, 39.950486],
          [-105.115419, 39.950539],
          [-105.11543, 39.950539],
          [-105.117017, 39.950553],
          [-105.118366, 39.950566],
          [-105.118577, 39.950568],
          [-105.118671, 39.950569],
          [-105.118701, 39.950569],
          [-105.118708, 39.950569],
          [-105.118715, 39.950569],
          [-105.118732, 39.95057],
          [-105.118749, 39.95057],
          [-105.118761, 39.95057],
          [-105.118833, 39.950571],
          [-105.118983, 39.950572],
          [-105.120336, 39.950585],
          [-105.120347, 39.950585],
          [-105.120532, 39.950569],
          [-105.120541, 39.950568],
          [-105.120776, 39.950548],
          [-105.121037, 39.951248],
          [-105.121045, 39.951269],
          [-105.121047, 39.951275],
          [-105.12105, 39.951283],
          [-105.121058, 39.951305],
          [-105.121089, 39.951387],
          [-105.120845, 39.951538],
          [-105.120738, 39.951613],
          [-105.120568, 39.951732],
          [-105.119781, 39.952167],
          [-105.11965, 39.952223],
          [-105.119624, 39.952234],
          [-105.119614, 39.952238],
          [-105.119574, 39.952255],
          [-105.119554, 39.952264],
          [-105.119477, 39.952297],
          [-105.119347, 39.952353],
          [-105.11784, 39.953034],
          [-105.11764, 39.953104],
          [-105.116061, 39.953657],
          [-105.113353, 39.954548],
          [-105.111034, 39.955378],
          [-105.109342, 39.955974],
          [-105.109372, 39.956506],
          [-105.109385, 39.95674],
          [-105.109391, 39.956849],
          [-105.109685, 39.956752],
          [-105.109693, 39.956749],
          [-105.110913, 39.956348],
          [-105.111782, 39.956051],
          [-105.11339, 39.955583],
          [-105.114622, 39.955152],
          [-105.115301, 39.954903],
          [-105.117838, 39.953889],
          [-105.117945, 39.953849],
          [-105.118697, 39.953543],
          [-105.118706, 39.953539],
          [-105.119345, 39.953279],
          [-105.120574, 39.952709],
          [-105.121061, 39.952481],
          [-105.121069, 39.952477],
          [-105.121241, 39.952397],
          [-105.121437, 39.952305],
          [-105.122141, 39.954166],
          [-105.122294, 39.954571],
          [-105.127321, 39.954533],
          [-105.127598, 39.954533],
          [-105.127597, 39.954179],
          [-105.127597, 39.954062],
          [-105.127593, 39.952407],
          [-105.127593, 39.95229],
          [-105.127589, 39.950717],
          [-105.127586, 39.949522],
          [-105.127579, 39.948868],
          [-105.127565, 39.947484],
          [-105.127562, 39.94685],
          [-105.127559, 39.94628],
          [-105.127557, 39.945758],
          [-105.127555, 39.945459],
          [-105.127555, 39.945363],
          [-105.127554, 39.945143],
          [-105.127553, 39.945138],
          [-105.127553, 39.945133],
          [-105.127553, 39.945123],
          [-105.127553, 39.945108],
          [-105.127553, 39.945089],
          [-105.127553, 39.94507],
          [-105.127551, 39.94461],
          [-105.127551, 39.944596],
          [-105.127545, 39.943301],
          [-105.127544, 39.943283],
          [-105.127544, 39.943266],
          [-105.127533, 39.943045],
          [-105.127532, 39.943025],
          [-105.127426, 39.94091],
          [-105.127421, 39.940817],
          [-105.127419, 39.940795],
          [-105.12741, 39.940716],
          [-105.127428, 39.939981],
          [-105.127435, 39.939818],
          [-105.127679, 39.939823],
          [-105.127884, 39.939823],
          [-105.136859, 39.939855],
          [-105.137572, 39.939858],
          [-105.137579, 39.939688],
          [-105.137592, 39.939543],
          [-105.137593, 39.939443],
          [-105.137603, 39.939249],
          [-105.137578, 39.937109],
          [-105.137631, 39.929825],
          [-105.137604, 39.928931],
          [-105.138407, 39.928938],
          [-105.138417, 39.928938],
          [-105.138625, 39.928935],
          [-105.138894, 39.928935],
          [-105.139722, 39.928935],
          [-105.141792, 39.92893],
          [-105.141848, 39.92893],
          [-105.142258, 39.928929],
          [-105.14235, 39.928929],
          [-105.142428, 39.928929],
          [-105.142523, 39.928929],
          [-105.143251, 39.92893],
          [-105.143875, 39.928931],
          [-105.146805, 39.928937],
          [-105.147015, 39.928938],
          [-105.14712, 39.928939],
          [-105.147127, 39.928939],
          [-105.147129, 39.928817],
          [-105.147134, 39.928479],
          [-105.147134, 39.928467],
          [-105.147134, 39.928453],
          [-105.147137, 39.92823],
          [-105.147147, 39.927419],
          [-105.147149, 39.927309],
          [-105.147159, 39.926641],
          [-105.147159, 39.926636],
          [-105.147179, 39.925204],
          [-105.147214, 39.922788],
          [-105.14734, 39.913991],
          [-105.147342, 39.913886],
          [-105.397849, 39.912886],
          [-105.397971, 39.934867],
          [-105.675798, 39.932445],
          [-105.638797, 40.038787],
          [-105.653321, 40.260457],
          [-105.196476, 40.261085],
          [-105.055089, 40.261793]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08014",
      state: "08",
      county: "014",
      name: "Broomfield County",
      censusArea: 33.034,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.147342, 39.913886],
          [-105.14734, 39.913991],
          [-105.147214, 39.922788],
          [-105.147179, 39.925204],
          [-105.147159, 39.926636],
          [-105.147159, 39.926641],
          [-105.147149, 39.927309],
          [-105.147147, 39.927419],
          [-105.147137, 39.92823],
          [-105.147134, 39.928453],
          [-105.147134, 39.928467],
          [-105.147134, 39.928479],
          [-105.147129, 39.928817],
          [-105.147127, 39.928939],
          [-105.14712, 39.928939],
          [-105.147015, 39.928938],
          [-105.146805, 39.928937],
          [-105.143875, 39.928931],
          [-105.143251, 39.92893],
          [-105.142523, 39.928929],
          [-105.142428, 39.928929],
          [-105.14235, 39.928929],
          [-105.142258, 39.928929],
          [-105.141848, 39.92893],
          [-105.141792, 39.92893],
          [-105.139722, 39.928935],
          [-105.138894, 39.928935],
          [-105.138625, 39.928935],
          [-105.138417, 39.928938],
          [-105.138407, 39.928938],
          [-105.137604, 39.928931],
          [-105.137631, 39.929825],
          [-105.137578, 39.937109],
          [-105.137603, 39.939249],
          [-105.137593, 39.939443],
          [-105.137592, 39.939543],
          [-105.137579, 39.939688],
          [-105.137572, 39.939858],
          [-105.136859, 39.939855],
          [-105.127884, 39.939823],
          [-105.127679, 39.939823],
          [-105.127435, 39.939818],
          [-105.127428, 39.939981],
          [-105.12741, 39.940716],
          [-105.127419, 39.940795],
          [-105.127421, 39.940817],
          [-105.127426, 39.94091],
          [-105.127532, 39.943025],
          [-105.127533, 39.943045],
          [-105.127544, 39.943266],
          [-105.127544, 39.943283],
          [-105.127545, 39.943301],
          [-105.127551, 39.944596],
          [-105.127551, 39.94461],
          [-105.127553, 39.94507],
          [-105.127553, 39.945089],
          [-105.127553, 39.945108],
          [-105.127553, 39.945123],
          [-105.127553, 39.945133],
          [-105.127553, 39.945138],
          [-105.127554, 39.945143],
          [-105.127555, 39.945363],
          [-105.127555, 39.945459],
          [-105.127557, 39.945758],
          [-105.127559, 39.94628],
          [-105.127562, 39.94685],
          [-105.127565, 39.947484],
          [-105.127579, 39.948868],
          [-105.127586, 39.949522],
          [-105.127589, 39.950717],
          [-105.127593, 39.95229],
          [-105.127593, 39.952407],
          [-105.127597, 39.954062],
          [-105.127597, 39.954179],
          [-105.127598, 39.954533],
          [-105.127321, 39.954533],
          [-105.122294, 39.954571],
          [-105.122141, 39.954166],
          [-105.121437, 39.952305],
          [-105.121241, 39.952397],
          [-105.121069, 39.952477],
          [-105.121061, 39.952481],
          [-105.120574, 39.952709],
          [-105.119345, 39.953279],
          [-105.118706, 39.953539],
          [-105.118697, 39.953543],
          [-105.117945, 39.953849],
          [-105.117838, 39.953889],
          [-105.115301, 39.954903],
          [-105.114622, 39.955152],
          [-105.11339, 39.955583],
          [-105.111782, 39.956051],
          [-105.110913, 39.956348],
          [-105.109693, 39.956749],
          [-105.109685, 39.956752],
          [-105.109391, 39.956849],
          [-105.109385, 39.95674],
          [-105.109372, 39.956506],
          [-105.109342, 39.955974],
          [-105.111034, 39.955378],
          [-105.113353, 39.954548],
          [-105.116061, 39.953657],
          [-105.11764, 39.953104],
          [-105.11784, 39.953034],
          [-105.119347, 39.952353],
          [-105.119477, 39.952297],
          [-105.119554, 39.952264],
          [-105.119574, 39.952255],
          [-105.119614, 39.952238],
          [-105.119624, 39.952234],
          [-105.11965, 39.952223],
          [-105.119781, 39.952167],
          [-105.120568, 39.951732],
          [-105.120738, 39.951613],
          [-105.120845, 39.951538],
          [-105.121089, 39.951387],
          [-105.121058, 39.951305],
          [-105.12105, 39.951283],
          [-105.121047, 39.951275],
          [-105.121045, 39.951269],
          [-105.121037, 39.951248],
          [-105.120776, 39.950548],
          [-105.120541, 39.950568],
          [-105.120532, 39.950569],
          [-105.120347, 39.950585],
          [-105.120336, 39.950585],
          [-105.118983, 39.950572],
          [-105.118833, 39.950571],
          [-105.118761, 39.95057],
          [-105.118749, 39.95057],
          [-105.118732, 39.95057],
          [-105.118715, 39.950569],
          [-105.118708, 39.950569],
          [-105.118701, 39.950569],
          [-105.118671, 39.950569],
          [-105.118577, 39.950568],
          [-105.118366, 39.950566],
          [-105.117017, 39.950553],
          [-105.11543, 39.950539],
          [-105.115419, 39.950539],
          [-105.109446, 39.950486],
          [-105.109435, 39.950486],
          [-105.109322, 39.950485],
          [-105.109321, 39.950461],
          [-105.109321, 39.950456],
          [-105.109321, 39.950451],
          [-105.109321, 39.950445],
          [-105.109321, 39.950439],
          [-105.109321, 39.950433],
          [-105.109321, 39.950426],
          [-105.109321, 39.950421],
          [-105.109321, 39.950415],
          [-105.109321, 39.950409],
          [-105.109321, 39.950401],
          [-105.109321, 39.950391],
          [-105.109321, 39.950379],
          [-105.109321, 39.950363],
          [-105.109321, 39.950341],
          [-105.109321, 39.950311],
          [-105.109321, 39.950264],
          [-105.109321, 39.950183],
          [-105.10932, 39.949529],
          [-105.109323, 39.949247],
          [-105.109323, 39.949242],
          [-105.109323, 39.949235],
          [-105.109323, 39.94922],
          [-105.109323, 39.949194],
          [-105.109323, 39.949137],
          [-105.109323, 39.949082],
          [-105.109323, 39.949032],
          [-105.109323, 39.948989],
          [-105.109323, 39.948953],
          [-105.109323, 39.948924],
          [-105.109323, 39.948834],
          [-105.109327, 39.947889],
          [-105.109327, 39.9478],
          [-105.109328, 39.947658],
          [-105.109328, 39.947569],
          [-105.109329, 39.947478],
          [-105.109329, 39.947465],
          [-105.10933, 39.94721],
          [-105.109352, 39.946282],
          [-105.109353, 39.946244],
          [-105.109353, 39.946231],
          [-105.109354, 39.946225],
          [-105.109354, 39.946216],
          [-105.109358, 39.94605],
          [-105.10936, 39.945571],
          [-105.10936, 39.945481],
          [-105.109361, 39.945185],
          [-105.109337, 39.945088],
          [-105.109327, 39.945048],
          [-105.109323, 39.945031],
          [-105.109322, 39.945026],
          [-105.109321, 39.945021],
          [-105.10911, 39.944152],
          [-105.109103, 39.944125],
          [-105.10906, 39.943255],
          [-105.109057, 39.943184],
          [-105.109261, 39.943188],
          [-105.109309, 39.943189],
          [-105.109329, 39.94319],
          [-105.109338, 39.94319],
          [-105.109353, 39.94319],
          [-105.110094, 39.943204],
          [-105.110274, 39.943204],
          [-105.118377, 39.943213],
          [-105.118557, 39.943213],
          [-105.118792, 39.943187],
          [-105.118774, 39.943169],
          [-105.11877, 39.943165],
          [-105.118733, 39.94311],
          [-105.118715, 39.943061],
          [-105.118708, 39.943013],
          [-105.118704, 39.942904],
          [-105.118711, 39.942096],
          [-105.118711, 39.942071],
          [-105.118711, 39.942047],
          [-105.118711, 39.942029],
          [-105.118711, 39.942018],
          [-105.118711, 39.942011],
          [-105.118712, 39.942004],
          [-105.118718, 39.941225],
          [-105.118721, 39.939338],
          [-105.118721, 39.939269],
          [-105.118721, 39.939248],
          [-105.118721, 39.939174],
          [-105.118721, 39.939033],
          [-105.118722, 39.938463],
          [-105.118722, 39.937527],
          [-105.118722, 39.937324],
          [-105.118735, 39.936811],
          [-105.118747, 39.936383],
          [-105.118752, 39.936199],
          [-105.118752, 39.936194],
          [-105.118745, 39.935979],
          [-105.118743, 39.93591],
          [-105.118738, 39.935874],
          [-105.118735, 39.935856],
          [-105.118734, 39.935851],
          [-105.118732, 39.935845],
          [-105.11873, 39.935839],
          [-105.118729, 39.935834],
          [-105.118714, 39.93578],
          [-105.118694, 39.93573],
          [-105.118676, 39.935685],
          [-105.118674, 39.935679],
          [-105.118671, 39.935671],
          [-105.118659, 39.93564],
          [-105.118607, 39.93553],
          [-105.118601, 39.935517],
          [-105.118598, 39.93551],
          [-105.118593, 39.9355],
          [-105.118569, 39.935451],
          [-105.118545, 39.935402],
          [-105.118482, 39.935254],
          [-105.11848, 39.935249],
          [-105.118466, 39.935216],
          [-105.118446, 39.935113],
          [-105.118448, 39.935068],
          [-105.118449, 39.935048],
          [-105.118459, 39.934996],
          [-105.118468, 39.934947],
          [-105.118507, 39.934844],
          [-105.118727, 39.934454],
          [-105.11873, 39.934449],
          [-105.118801, 39.934323],
          [-105.118861, 39.934198],
          [-105.118904, 39.934077],
          [-105.118934, 39.93394],
          [-105.118946, 39.933841],
          [-105.118946, 39.933833],
          [-105.118947, 39.933783],
          [-105.118828, 39.933841],
          [-105.117035, 39.932337],
          [-105.116828, 39.932164],
          [-105.116795, 39.932137],
          [-105.116589, 39.931964],
          [-105.114437, 39.930166],
          [-105.114057, 39.9294],
          [-105.114064, 39.929181],
          [-105.114072, 39.929052],
          [-105.114036, 39.928981],
          [-105.113969, 39.928841],
          [-105.113867, 39.928629],
          [-105.113859, 39.928611],
          [-105.113835, 39.928556],
          [-105.113833, 39.928551],
          [-105.11383, 39.928545],
          [-105.113827, 39.928538],
          [-105.113808, 39.928493],
          [-105.113771, 39.928394],
          [-105.113759, 39.928362],
          [-105.113707, 39.928188],
          [-105.113691, 39.928115],
          [-105.11369, 39.928108],
          [-105.113665, 39.927993],
          [-105.113599, 39.927681],
          [-105.113597, 39.927673],
          [-105.113595, 39.927665],
          [-105.113594, 39.927659],
          [-105.113586, 39.927621],
          [-105.113542, 39.927488],
          [-105.113532, 39.927457],
          [-105.113529, 39.927449],
          [-105.113523, 39.92743],
          [-105.113472, 39.927321],
          [-105.113403, 39.927204],
          [-105.113325, 39.927097],
          [-105.113261, 39.927021],
          [-105.113212, 39.926969],
          [-105.113159, 39.926912],
          [-105.113051, 39.926808],
          [-105.112985, 39.926748],
          [-105.11298, 39.926744],
          [-105.112934, 39.926702],
          [-105.112903, 39.926674],
          [-105.112897, 39.926669],
          [-105.112887, 39.92666],
          [-105.112883, 39.926656],
          [-105.112799, 39.926579],
          [-105.112755, 39.926541],
          [-105.112653, 39.926453],
          [-105.112369, 39.926207],
          [-105.112362, 39.926201],
          [-105.112355, 39.926195],
          [-105.112274, 39.926129],
          [-105.112265, 39.926122],
          [-105.112246, 39.926106],
          [-105.111921, 39.925841],
          [-105.111851, 39.925791],
          [-105.111845, 39.925787],
          [-105.111823, 39.925771],
          [-105.111682, 39.925669],
          [-105.111659, 39.925654],
          [-105.111645, 39.925645],
          [-105.111631, 39.925636],
          [-105.111475, 39.925532],
          [-105.111383, 39.925475],
          [-105.111376, 39.925471],
          [-105.111315, 39.925433],
          [-105.110993, 39.925233],
          [-105.110983, 39.925227],
          [-105.110974, 39.925221],
          [-105.110958, 39.925211],
          [-105.110903, 39.925177],
          [-105.11087, 39.925157],
          [-105.1108, 39.925114],
          [-105.110794, 39.92511],
          [-105.110782, 39.925103],
          [-105.110767, 39.925094],
          [-105.110747, 39.925082],
          [-105.110628, 39.925009],
          [-105.110454, 39.924908],
          [-105.110214, 39.92477],
          [-105.110077, 39.924698],
          [-105.11007, 39.924695],
          [-105.110061, 39.92469],
          [-105.110043, 39.92468],
          [-105.110027, 39.924671],
          [-105.10998, 39.924648],
          [-105.109965, 39.924641],
          [-105.109921, 39.92462],
          [-105.109826, 39.924575],
          [-105.109641, 39.924507],
          [-105.109527, 39.924473],
          [-105.109412, 39.924438],
          [-105.109142, 39.924356],
          [-105.109141, 39.924586],
          [-105.109052, 39.925619],
          [-105.109043, 39.925744],
          [-105.109023, 39.925874],
          [-105.109259, 39.926022],
          [-105.109267, 39.926027],
          [-105.110584, 39.92685],
          [-105.110917, 39.927058],
          [-105.111189, 39.927252],
          [-105.111479, 39.927488],
          [-105.111558, 39.92756],
          [-105.111784, 39.927785],
          [-105.111966, 39.927989],
          [-105.11212, 39.928185],
          [-105.112132, 39.9282],
          [-105.11219, 39.928284],
          [-105.112269, 39.928399],
          [-105.112407, 39.928625],
          [-105.112408, 39.928626],
          [-105.113296, 39.930249],
          [-105.113446, 39.930541],
          [-105.113515, 39.930702],
          [-105.113569, 39.930862],
          [-105.113575, 39.930882],
          [-105.113579, 39.930895],
          [-105.113606, 39.930994],
          [-105.113636, 39.931163],
          [-105.11365, 39.931279],
          [-105.113657, 39.93144],
          [-105.113652, 39.931597],
          [-105.113626, 39.931805],
          [-105.113597, 39.931942],
          [-105.113566, 39.932055],
          [-105.113544, 39.932118],
          [-105.113541, 39.932126],
          [-105.113488, 39.932275],
          [-105.113388, 39.932479],
          [-105.113301, 39.932627],
          [-105.113232, 39.932731],
          [-105.113155, 39.93283],
          [-105.112975, 39.933034],
          [-105.112874, 39.933133],
          [-105.112775, 39.933219],
          [-105.112504, 39.933426],
          [-105.112368, 39.933518],
          [-105.112311, 39.933555],
          [-105.112293, 39.933567],
          [-105.110014, 39.935039],
          [-105.109979, 39.935062],
          [-105.109358, 39.935463],
          [-105.109224, 39.935546],
          [-105.109041, 39.935664],
          [-105.109041, 39.935754],
          [-105.109039, 39.936061],
          [-105.109039, 39.936095],
          [-105.109039, 39.936114],
          [-105.109039, 39.936119],
          [-105.109038, 39.936124],
          [-105.109038, 39.936134],
          [-105.109036, 39.936484],
          [-105.10879, 39.936468],
          [-105.108746, 39.936465],
          [-105.108727, 39.936464],
          [-105.108704, 39.936463],
          [-105.108678, 39.936461],
          [-105.10802, 39.936419],
          [-105.107985, 39.936417],
          [-105.107886, 39.936411],
          [-105.107539, 39.936386],
          [-105.102808, 39.936386],
          [-105.102719, 39.936386],
          [-105.093793, 39.936385],
          [-105.09101, 39.936386],
          [-105.09099, 39.936387],
          [-105.090944, 39.936387],
          [-105.090854, 39.936386],
          [-105.090851, 39.937836],
          [-105.09083, 39.938827],
          [-105.090787, 39.939879],
          [-105.090787, 39.939887],
          [-105.090782, 39.939962],
          [-105.090798, 39.939966],
          [-105.090819, 39.939971],
          [-105.090849, 39.939979],
          [-105.090799, 39.941546],
          [-105.090912, 39.941583],
          [-105.090897, 39.943134],
          [-105.090677, 39.943135],
          [-105.090541, 39.943137],
          [-105.090532, 39.943137],
          [-105.090324, 39.943137],
          [-105.090305, 39.943137],
          [-105.085739, 39.943185],
          [-105.076101, 39.943185],
          [-105.076101, 39.943456],
          [-105.076101, 39.943726],
          [-105.076101, 39.943904],
          [-105.076101, 39.944607],
          [-105.076102, 39.946353],
          [-105.076102, 39.946892],
          [-105.076102, 39.947048],
          [-105.076102, 39.947539],
          [-105.076104, 39.950592],
          [-105.075827, 39.950592],
          [-105.072518, 39.950586],
          [-105.072241, 39.950586],
          [-105.071893, 39.950585],
          [-105.071738, 39.950585],
          [-105.071778, 39.957502],
          [-105.07178, 39.957785],
          [-105.071854, 39.957785],
          [-105.071916, 39.957785],
          [-105.071933, 39.957785],
          [-105.072278, 39.957787],
          [-105.074639, 39.9578],
          [-105.075312, 39.957802],
          [-105.075858, 39.957803],
          [-105.076586, 39.957805],
          [-105.076823, 39.957805],
          [-105.077331, 39.95781],
          [-105.079166, 39.957826],
          [-105.079302, 39.957829],
          [-105.079729, 39.95784],
          [-105.080028, 39.957847],
          [-105.080305, 39.957808],
          [-105.080602, 39.957822],
          [-105.080966, 39.957839],
          [-105.082448, 39.957908],
          [-105.082455, 39.957908],
          [-105.082463, 39.957908],
          [-105.082534, 39.957911],
          [-105.082756, 39.957912],
          [-105.082768, 39.957912],
          [-105.082801, 39.957912],
          [-105.083261, 39.957915],
          [-105.083267, 39.958267],
          [-105.083963, 39.958302],
          [-105.084891, 39.958201],
          [-105.085379, 39.958088],
          [-105.08541, 39.958081],
          [-105.085476, 39.958066],
          [-105.08555, 39.95805],
          [-105.085611, 39.958036],
          [-105.085638, 39.95803],
          [-105.08565, 39.958027],
          [-105.085663, 39.958024],
          [-105.085673, 39.958022],
          [-105.085683, 39.95802],
          [-105.085702, 39.958016],
          [-105.085717, 39.958003],
          [-105.085734, 39.957988],
          [-105.085754, 39.957971],
          [-105.085794, 39.957938],
          [-105.085846, 39.957893],
          [-105.086861, 39.957849],
          [-105.086948, 39.957843],
          [-105.087315, 39.957833],
          [-105.087724, 39.957838],
          [-105.087834, 39.957839],
          [-105.087841, 39.957839],
          [-105.08787, 39.95784],
          [-105.088298, 39.957851],
          [-105.088332, 39.957853],
          [-105.088441, 39.957858],
          [-105.088588, 39.957865],
          [-105.088597, 39.957919],
          [-105.088613, 39.95803],
          [-105.088614, 39.958037],
          [-105.088615, 39.958042],
          [-105.088616, 39.958052],
          [-105.088636, 39.958182],
          [-105.087748, 39.958197],
          [-105.087748, 39.958707],
          [-105.087748, 39.95888],
          [-105.087748, 39.959571],
          [-105.087393, 39.959555],
          [-105.08731, 39.959551],
          [-105.087033, 39.959538],
          [-105.086951, 39.959741],
          [-105.086858, 39.959972],
          [-105.086082, 39.960043],
          [-105.086002, 39.96005],
          [-105.085982, 39.960052],
          [-105.085961, 39.960054],
          [-105.08595, 39.960055],
          [-105.085942, 39.960055],
          [-105.085877, 39.960061],
          [-105.085764, 39.960084],
          [-105.085583, 39.960123],
          [-105.084027, 39.960454],
          [-105.082093, 39.960967],
          [-105.081957, 39.961003],
          [-105.080562, 39.961373],
          [-105.07999, 39.961613],
          [-105.079885, 39.961657],
          [-105.078632, 39.962182],
          [-105.0757, 39.964623],
          [-105.074735, 39.965752],
          [-105.073059, 39.967308],
          [-105.071886, 39.968544],
          [-105.070468, 39.969721],
          [-105.069605, 39.970227],
          [-105.06742, 39.971289],
          [-105.066609, 39.971925],
          [-105.063527, 39.973444],
          [-105.060968, 39.974563],
          [-105.060445, 39.974768],
          [-105.058567, 39.975502],
          [-105.05784, 39.975787],
          [-105.057654, 39.97586],
          [-105.054688, 39.976889],
          [-105.052797, 39.977574],
          [-105.052794, 39.978581],
          [-105.052793, 39.97879],
          [-105.052792, 39.979489],
          [-105.052774, 39.986398],
          [-105.052823, 40.00026],
          [-105.052809, 40.00026],
          [-105.047106, 40.000264],
          [-105.046237, 40.000265],
          [-105.046049, 40.000265],
          [-105.045672, 40.000266],
          [-105.044051, 40.000267],
          [-105.04392, 40.000267],
          [-105.041853, 40.000267],
          [-105.038601, 40.000268],
          [-105.037075, 40.00028],
          [-105.02762, 40.000263],
          [-105.022384, 40.000286],
          [-105.017772, 40.000307],
          [-105.017365, 40.000307],
          [-105.016009, 40.000308],
          [-105.015186, 40.00029],
          [-105.014591, 40.000285],
          [-105.01179, 40.000292],
          [-105.011684, 40.000292],
          [-105.011008, 40.000294],
          [-105.010347, 40.000289],
          [-105.010221, 40.000288],
          [-105.009432, 40.000289],
          [-105.008903, 40.00029],
          [-105.008926, 40.002527],
          [-105.008928, 40.002694],
          [-105.00893, 40.002954],
          [-105.008944, 40.00426],
          [-105.008961, 40.005764],
          [-105.008979, 40.007435],
          [-105.008555, 40.007444],
          [-105.008475, 40.007445],
          [-105.008426, 40.005772],
          [-105.008402, 40.002531],
          [-105.008402, 40.00029],
          [-105.007904, 40.000292],
          [-105.007929, 40.002536],
          [-105.007954, 40.00578],
          [-105.007999, 40.007455],
          [-104.999082, 40.007648],
          [-104.998528, 40.007646],
          [-104.998519, 40.007646],
          [-104.998486, 40.007646],
          [-104.996945, 40.007653],
          [-104.996885, 40.007653],
          [-104.996879, 40.007653],
          [-104.996858, 40.007653],
          [-104.996849, 40.007653],
          [-104.996841, 40.007654],
          [-104.996822, 40.007654],
          [-104.99681, 40.007654],
          [-104.996801, 40.007654],
          [-104.996779, 40.007654],
          [-104.996716, 40.007654],
          [-104.996653, 40.007654],
          [-104.996609, 40.007654],
          [-104.996576, 40.007654],
          [-104.996551, 40.007654],
          [-104.996532, 40.007654],
          [-104.996517, 40.007654],
          [-104.996506, 40.007654],
          [-104.996497, 40.007654],
          [-104.99649, 40.007654],
          [-104.996484, 40.007654],
          [-104.995974, 40.007651],
          [-104.995175, 40.007656],
          [-104.994335, 40.007655],
          [-104.992009, 40.007666],
          [-104.989819, 40.007681],
          [-104.989727, 40.00768],
          [-104.989647, 40.007679],
          [-104.989645, 40.007971],
          [-104.989614, 40.01489],
          [-104.992735, 40.014899],
          [-104.997215, 40.014887],
          [-104.999065, 40.014887],
          [-104.999073, 40.015907],
          [-104.999073, 40.015939],
          [-104.999001, 40.015997],
          [-104.99899, 40.016004],
          [-104.998787, 40.016129],
          [-104.998566, 40.016393],
          [-104.998452, 40.016519],
          [-104.998016, 40.016789],
          [-104.997888, 40.016943],
          [-104.997874, 40.017107],
          [-104.997788, 40.017333],
          [-104.997746, 40.017668],
          [-104.997603, 40.017832],
          [-104.997389, 40.017986],
          [-104.996989, 40.018283],
          [-104.996868, 40.018393],
          [-104.996718, 40.01864],
          [-104.996611, 40.018745],
          [-104.996404, 40.018981],
          [-104.996225, 40.019157],
          [-104.996118, 40.019366],
          [-104.995983, 40.019553],
          [-104.995897, 40.019761],
          [-104.995819, 40.019926],
          [-104.995719, 40.020052],
          [-104.995633, 40.020195],
          [-104.995683, 40.020437],
          [-104.995826, 40.020574],
          [-104.995984, 40.020624],
          [-104.996248, 40.02064],
          [-104.996627, 40.020585],
          [-104.997168, 40.020598],
          [-104.997284, 40.0206],
          [-104.997312, 40.020601],
          [-104.997391, 40.020625],
          [-104.997415, 40.020633],
          [-104.997816, 40.020631],
          [-104.999047, 40.020627],
          [-104.99911, 40.020633],
          [-104.999109, 40.021002],
          [-104.998729, 40.021001],
          [-104.998587, 40.020911],
          [-104.997427, 40.020935],
          [-104.997428, 40.020946],
          [-104.997391, 40.020936],
          [-104.996769, 40.02095],
          [-104.996753, 40.022423],
          [-104.996742, 40.023509],
          [-104.997354, 40.023511],
          [-104.997537, 40.02351],
          [-104.999116, 40.023509],
          [-104.999116, 40.023657],
          [-104.999117, 40.023998],
          [-104.999117, 40.025288],
          [-104.999122, 40.025928],
          [-104.994836, 40.025954],
          [-104.994802, 40.025954],
          [-104.994559, 40.025956],
          [-104.994316, 40.025958],
          [-104.991594, 40.025973],
          [-104.991419, 40.025974],
          [-104.991244, 40.025975],
          [-104.989739, 40.025984],
          [-104.986513, 40.026003],
          [-104.986523, 40.028536],
          [-104.986482, 40.028536],
          [-104.986503, 40.029405],
          [-104.986048, 40.029406],
          [-104.98542, 40.029407],
          [-104.981995, 40.029412],
          [-104.980994, 40.029409],
          [-104.980653, 40.029406],
          [-104.980629, 40.029406],
          [-104.98033, 40.029407],
          [-104.980196, 40.029409],
          [-104.980048, 40.029411],
          [-104.980049, 40.029647],
          [-104.980051, 40.029704],
          [-104.980071, 40.03042],
          [-104.980072, 40.031203],
          [-104.980059, 40.03242],
          [-104.980058, 40.032708],
          [-104.980058, 40.032882],
          [-104.980054, 40.035048],
          [-104.980054, 40.035136],
          [-104.980054, 40.035179],
          [-104.980051, 40.036828],
          [-104.98005, 40.037516],
          [-104.980048, 40.038342],
          [-104.980047, 40.038969],
          [-104.980046, 40.039173],
          [-104.980044, 40.03956],
          [-104.980041, 40.040316],
          [-104.980037, 40.041142],
          [-104.980037, 40.041206],
          [-104.980028, 40.041676],
          [-104.980001, 40.041976],
          [-104.979967, 40.04216],
          [-104.979914, 40.042353],
          [-104.979427, 40.043679],
          [-104.979344, 40.043951],
          [-104.976919, 40.043987],
          [-104.976435, 40.043994],
          [-104.973879, 40.044033],
          [-104.973448, 40.044039],
          [-104.972908, 40.04404],
          [-104.971161, 40.044061],
          [-104.970727, 40.044069],
          [-104.969589, 40.044089],
          [-104.968129, 40.044096],
          [-104.967371, 40.044109],
          [-104.966526, 40.044123],
          [-104.964109, 40.044169],
          [-104.961158, 40.044227],
          [-104.961156, 40.043999],
          [-104.961151, 40.043651],
          [-104.961195, 40.041298],
          [-104.961207, 40.040774],
          [-104.961233, 40.039603],
          [-104.961249, 40.039093],
          [-104.961296, 40.037638],
          [-104.961342, 40.035757],
          [-104.961358, 40.034687],
          [-104.961375, 40.033481],
          [-104.961392, 40.030224],
          [-104.961401, 40.029459],
          [-104.961394, 40.028348],
          [-104.961393, 40.026667],
          [-104.961401, 40.026273],
          [-104.961404, 40.024959],
          [-104.961389, 40.023669],
          [-104.961388, 40.023611],
          [-104.961385, 40.021967],
          [-104.961373, 40.015292],
          [-104.96136, 40.01523],
          [-104.961325, 40.015151],
          [-104.96132, 40.014982],
          [-104.961315, 40.01477],
          [-104.961313, 40.014677],
          [-104.961312, 40.014644],
          [-104.961306, 40.014533],
          [-104.9613, 40.014321],
          [-104.961291, 40.014055],
          [-104.961291, 40.014052],
          [-104.961265, 40.013729],
          [-104.961296, 40.013687],
          [-104.961344, 40.013606],
          [-104.961375, 40.013511],
          [-104.961391, 40.013431],
          [-104.961399, 40.013205],
          [-104.961402, 40.012901],
          [-104.961398, 40.012227],
          [-104.961411, 40.010341],
          [-104.961415, 40.009766],
          [-104.961416, 40.00761],
          [-104.961426, 40.006441],
          [-104.961426, 40.005019],
          [-104.961427, 40.002654],
          [-104.961427, 40.001777],
          [-104.961413, 40.000939],
          [-104.961408, 40.000337],
          [-105.052884, 39.913795],
          [-105.147342, 39.913886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08025",
      state: "08",
      county: "025",
      name: "Crowley County",
      censusArea: 787.421,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.501764, 38.265016],
          [-103.509364, 38.172514],
          [-103.619283, 38.171455],
          [-103.619172, 38.113355],
          [-103.835616, 38.11334],
          [-104.058242, 38.146492],
          [-104.053921, 38.522393],
          [-103.504666, 38.516409],
          [-103.501764, 38.265016]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08031",
      state: "08",
      county: "031",
      name: "Denver County",
      censusArea: 153,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.053439, 39.621423],
          [-105.059305, 39.614337],
          [-105.096909, 39.614423],
          [-105.092839, 39.618327],
          [-105.092543, 39.618328],
          [-105.091657, 39.618333],
          [-105.091362, 39.618336],
          [-105.091123, 39.618331],
          [-105.090115, 39.618312],
          [-105.087826, 39.618319],
          [-105.087252, 39.618317],
          [-105.087031, 39.618339],
          [-105.08682, 39.618412],
          [-105.08668, 39.618478],
          [-105.086553, 39.61858],
          [-105.086314, 39.618808],
          [-105.086115, 39.618979],
          [-105.085928, 39.618668],
          [-105.085262, 39.618283],
          [-105.084839, 39.618411],
          [-105.084573, 39.618526],
          [-105.084221, 39.618568],
          [-105.084043, 39.618568],
          [-105.083827, 39.618569],
          [-105.083616, 39.618569],
          [-105.082983, 39.618571],
          [-105.082773, 39.618573],
          [-105.082643, 39.618574],
          [-105.082476, 39.618504],
          [-105.079398, 39.618473],
          [-105.077553, 39.618485],
          [-105.075873, 39.618508],
          [-105.074796, 39.618511],
          [-105.074518, 39.61852],
          [-105.073364, 39.618559],
          [-105.073332, 39.618559],
          [-105.072844, 39.618555],
          [-105.072819, 39.618549],
          [-105.072799, 39.618546],
          [-105.072783, 39.618546],
          [-105.072763, 39.618548],
          [-105.072746, 39.618552],
          [-105.072703, 39.618568],
          [-105.072687, 39.618573],
          [-105.072673, 39.618576],
          [-105.072655, 39.618577],
          [-105.072638, 39.618577],
          [-105.072626, 39.618576],
          [-105.072611, 39.618573],
          [-105.072508, 39.618549],
          [-105.069229, 39.618571],
          [-105.068107, 39.618589],
          [-105.06788, 39.61859],
          [-105.066937, 39.618585],
          [-105.066206, 39.618575],
          [-105.065638, 39.61859],
          [-105.065572, 39.618592],
          [-105.065519, 39.618592],
          [-105.065492, 39.618588],
          [-105.065467, 39.618588],
          [-105.06542, 39.618583],
          [-105.065141, 39.618585],
          [-105.065115, 39.61943],
          [-105.065086, 39.619588],
          [-105.06505, 39.619779],
          [-105.065039, 39.619842],
          [-105.064966, 39.620238],
          [-105.065739, 39.620586],
          [-105.065746, 39.620588],
          [-105.066141, 39.620731],
          [-105.075952, 39.624143],
          [-105.076578, 39.624365],
          [-105.076594, 39.624378],
          [-105.076072, 39.624426],
          [-105.075132, 39.62451],
          [-105.07594, 39.625274],
          [-105.07624, 39.625587],
          [-105.08024, 39.630402],
          [-105.080309, 39.630486],
          [-105.08145, 39.631858],
          [-105.081514, 39.631863],
          [-105.081639, 39.631873],
          [-105.081685, 39.631872],
          [-105.081779, 39.631873],
          [-105.083556, 39.631872],
          [-105.083546, 39.631387],
          [-105.083535, 39.630848],
          [-105.083529, 39.630155],
          [-105.083274, 39.630154],
          [-105.08251, 39.630153],
          [-105.082256, 39.630153],
          [-105.082176, 39.630152],
          [-105.081936, 39.63015],
          [-105.081856, 39.63015],
          [-105.081693, 39.630149],
          [-105.081693, 39.630115],
          [-105.081695, 39.630037],
          [-105.081701, 39.629738],
          [-105.08173, 39.628225],
          [-105.08173, 39.628168],
          [-105.081733, 39.627999],
          [-105.081735, 39.627943],
          [-105.081735, 39.627879],
          [-105.081738, 39.627687],
          [-105.08174, 39.627624],
          [-105.08174, 39.627612],
          [-105.08174, 39.627579],
          [-105.081741, 39.627568],
          [-105.082334, 39.627571],
          [-105.083897, 39.627582],
          [-105.083881, 39.627799],
          [-105.08384, 39.628391],
          [-105.083894, 39.628877],
          [-105.083939, 39.629282],
          [-105.084999, 39.629237],
          [-105.085489, 39.629217],
          [-105.085496, 39.628501],
          [-105.085508, 39.627488],
          [-105.0855, 39.626465],
          [-105.085608, 39.626465],
          [-105.086324, 39.626468],
          [-105.086449, 39.626467],
          [-105.086823, 39.626465],
          [-105.086949, 39.626465],
          [-105.087395, 39.626463],
          [-105.087647, 39.626463],
          [-105.087662, 39.627143],
          [-105.087666, 39.62732],
          [-105.087894, 39.627308],
          [-105.08834, 39.627287],
          [-105.08869, 39.627287],
          [-105.089354, 39.627286],
          [-105.089654, 39.627287],
          [-105.089902, 39.627287],
          [-105.089988, 39.627286],
          [-105.090745, 39.627288],
          [-105.09121, 39.627291],
          [-105.091214, 39.625898],
          [-105.091216, 39.624983],
          [-105.091243, 39.624593],
          [-105.091253, 39.624341],
          [-105.091256, 39.624198],
          [-105.092998, 39.624197],
          [-105.093213, 39.624197],
          [-105.093228, 39.624196],
          [-105.094461, 39.624195],
          [-105.094598, 39.624195],
          [-105.094649, 39.624196],
          [-105.09491, 39.624197],
          [-105.095563, 39.624202],
          [-105.095694, 39.624203],
          [-105.095956, 39.624205],
          [-105.096253, 39.624207],
          [-105.097144, 39.624213],
          [-105.097441, 39.624215],
          [-105.097656, 39.624216],
          [-105.098301, 39.62422],
          [-105.098516, 39.624222],
          [-105.0987, 39.624222],
          [-105.099252, 39.624222],
          [-105.099437, 39.624222],
          [-105.099738, 39.624222],
          [-105.100642, 39.624222],
          [-105.100944, 39.624223],
          [-105.10117, 39.624223],
          [-105.101848, 39.624223],
          [-105.102075, 39.624224],
          [-105.102129, 39.624224],
          [-105.102291, 39.624224],
          [-105.102346, 39.624224],
          [-105.102373, 39.624224],
          [-105.102454, 39.624224],
          [-105.102482, 39.624224],
          [-105.102524, 39.624224],
          [-105.10265, 39.624224],
          [-105.102693, 39.624224],
          [-105.10302, 39.624223],
          [-105.104002, 39.624222],
          [-105.10433, 39.624222],
          [-105.104612, 39.624221],
          [-105.105458, 39.62422],
          [-105.105741, 39.62422],
          [-105.106931, 39.624218],
          [-105.107496, 39.624218],
          [-105.107537, 39.624218],
          [-105.107894, 39.624208],
          [-105.107924, 39.624208],
          [-105.107942, 39.624208],
          [-105.108374, 39.624205],
          [-105.108376, 39.624205],
          [-105.108377, 39.624205],
          [-105.1086, 39.624208],
          [-105.108599, 39.624262],
          [-105.108597, 39.624914],
          [-105.10859, 39.626987],
          [-105.10874, 39.626987],
          [-105.109192, 39.626988],
          [-105.109343, 39.626989],
          [-105.109927, 39.626988],
          [-105.109926, 39.62708],
          [-105.109925, 39.6271],
          [-105.109922, 39.627345],
          [-105.109885, 39.629741],
          [-105.109883, 39.629897],
          [-105.109889, 39.631412],
          [-105.109893, 39.632105],
          [-105.109893, 39.632676],
          [-105.109901, 39.63404],
          [-105.109902, 39.634263],
          [-105.109901, 39.634272],
          [-105.109887, 39.634485],
          [-105.109875, 39.635069],
          [-105.109875, 39.63507],
          [-105.109875, 39.635072],
          [-105.109874, 39.635186],
          [-105.109814, 39.635188],
          [-105.101427, 39.635187],
          [-105.100841, 39.635187],
          [-105.100611, 39.635148],
          [-105.100541, 39.635136],
          [-105.099958, 39.635038],
          [-105.099708, 39.635011],
          [-105.099641, 39.634987],
          [-105.099019, 39.635037],
          [-105.098527, 39.635075],
          [-105.097052, 39.635108],
          [-105.095546, 39.635121],
          [-105.095462, 39.635121],
          [-105.093134, 39.635158],
          [-105.091582, 39.635178],
          [-105.091617, 39.635458],
          [-105.091609, 39.636546],
          [-105.0916, 39.637331],
          [-105.091598, 39.637574],
          [-105.091593, 39.638086],
          [-105.091577, 39.638729],
          [-105.091258, 39.638722],
          [-105.0911, 39.638719],
          [-105.091023, 39.638718],
          [-105.088969, 39.638703],
          [-105.08849, 39.6387],
          [-105.088349, 39.638698],
          [-105.088094, 39.638696],
          [-105.087975, 39.638696],
          [-105.087853, 39.638695],
          [-105.087804, 39.638695],
          [-105.087597, 39.638694],
          [-105.087553, 39.638693],
          [-105.087423, 39.638692],
          [-105.08738, 39.638692],
          [-105.086885, 39.638696],
          [-105.08622, 39.638703],
          [-105.085402, 39.638698],
          [-105.084908, 39.638695],
          [-105.084809, 39.638694],
          [-105.084512, 39.638693],
          [-105.084414, 39.638693],
          [-105.083866, 39.638705],
          [-105.082223, 39.638745],
          [-105.081676, 39.638759],
          [-105.081578, 39.638667],
          [-105.081589, 39.638469],
          [-105.081646, 39.637479],
          [-105.081646, 39.637475],
          [-105.081682, 39.637145],
          [-105.081683, 39.637127],
          [-105.081688, 39.637076],
          [-105.08169, 39.637059],
          [-105.081693, 39.637031],
          [-105.081702, 39.636953],
          [-105.081702, 39.63695],
          [-105.081706, 39.636924],
          [-105.081716, 39.636867],
          [-105.081657, 39.636302],
          [-105.081507, 39.636377],
          [-105.080629, 39.6368],
          [-105.078087, 39.636644],
          [-105.07724, 39.636593],
          [-105.073951, 39.636831],
          [-105.073985, 39.638739],
          [-105.074358, 39.638742],
          [-105.074846, 39.638744],
          [-105.074866, 39.638745],
          [-105.076313, 39.638737],
          [-105.076803, 39.638735],
          [-105.076807, 39.638967],
          [-105.076797, 39.639153],
          [-105.076773, 39.639353],
          [-105.076722, 39.639549],
          [-105.076653, 39.639713],
          [-105.076546, 39.639906],
          [-105.076098, 39.640739],
          [-105.07601, 39.640913],
          [-105.075946, 39.641058],
          [-105.075897, 39.64118],
          [-105.075897, 39.641182],
          [-105.075872, 39.641277],
          [-105.075857, 39.641359],
          [-105.075837, 39.641689],
          [-105.075819, 39.641842],
          [-105.075789, 39.641968],
          [-105.07576, 39.64205],
          [-105.075679, 39.642203],
          [-105.075393, 39.642666],
          [-105.075363, 39.642709],
          [-105.07486, 39.64346],
          [-105.07469, 39.643727],
          [-105.074563, 39.643945],
          [-105.074454, 39.644104],
          [-105.074292, 39.644274],
          [-105.074145, 39.644403],
          [-105.073998, 39.644513],
          [-105.073816, 39.644628],
          [-105.073665, 39.644709],
          [-105.073285, 39.644872],
          [-105.072902, 39.645038],
          [-105.072523, 39.64522],
          [-105.072434, 39.645271],
          [-105.072326, 39.64535],
          [-105.072189, 39.645493],
          [-105.072133, 39.645574],
          [-105.072093, 39.645647],
          [-105.07206, 39.645751],
          [-105.072053, 39.645789],
          [-105.072049, 39.645815],
          [-105.072044, 39.646033],
          [-105.072042, 39.64609],
          [-105.06894, 39.646087],
          [-105.063539, 39.645988],
          [-105.063539, 39.646188],
          [-105.063532, 39.646404],
          [-105.063522, 39.646672],
          [-105.06352, 39.646749],
          [-105.063503, 39.647236],
          [-105.063454, 39.648699],
          [-105.063439, 39.649187],
          [-105.063435, 39.649273],
          [-105.063425, 39.649532],
          [-105.063422, 39.649619],
          [-105.063399, 39.65008],
          [-105.062614, 39.650086],
          [-105.061679, 39.650068],
          [-105.061578, 39.650066],
          [-105.061452, 39.650063],
          [-105.057345, 39.649984],
          [-105.057257, 39.651723],
          [-105.055521, 39.651737],
          [-105.054513, 39.65176],
          [-105.054654, 39.651798],
          [-105.054703, 39.651812],
          [-105.054882, 39.651895],
          [-105.055042, 39.652003],
          [-105.055061, 39.652016],
          [-105.055136, 39.652115],
          [-105.057266, 39.652086],
          [-105.057336, 39.652085],
          [-105.057319, 39.652201],
          [-105.05724, 39.652759],
          [-105.057215, 39.652946],
          [-105.05782, 39.652948],
          [-105.057873, 39.652948],
          [-105.058031, 39.652959],
          [-105.058311, 39.652979],
          [-105.058399, 39.652982],
          [-105.058543, 39.652979],
          [-105.058527, 39.652781],
          [-105.058465, 39.652008],
          [-105.058439, 39.651687],
          [-105.058939, 39.651687],
          [-105.058839, 39.651387],
          [-105.059209, 39.651386],
          [-105.059977, 39.651386],
          [-105.060321, 39.651385],
          [-105.060648, 39.651385],
          [-105.060692, 39.651385],
          [-105.061555, 39.651385],
          [-105.062137, 39.651384],
          [-105.062133, 39.651791],
          [-105.062586, 39.651899],
          [-105.062586, 39.651859],
          [-105.062606, 39.651077],
          [-105.062606, 39.65107],
          [-105.06261, 39.650598],
          [-105.063938, 39.650486],
          [-105.063939, 39.649887],
          [-105.063926, 39.64962],
          [-105.063924, 39.649566],
          [-105.071029, 39.649579],
          [-105.071034, 39.649731],
          [-105.07104, 39.649887],
          [-105.070953, 39.650411],
          [-105.07094, 39.650486],
          [-105.070352, 39.650413],
          [-105.07014, 39.650386],
          [-105.06884, 39.651587],
          [-105.068137, 39.652364],
          [-105.067979, 39.652444],
          [-105.067921, 39.652471],
          [-105.067681, 39.652589],
          [-105.067481, 39.652641],
          [-105.067307, 39.652752],
          [-105.067283, 39.652785],
          [-105.067144, 39.652981],
          [-105.06714, 39.652987],
          [-105.067137, 39.652996],
          [-105.067089, 39.65314],
          [-105.067057, 39.653233],
          [-105.067026, 39.653327],
          [-105.06697, 39.653494],
          [-105.066941, 39.653594],
          [-105.066956, 39.654029],
          [-105.067045, 39.656587],
          [-105.068556, 39.656591],
          [-105.069183, 39.656591],
          [-105.069505, 39.656592],
          [-105.070727, 39.656594],
          [-105.070768, 39.65822],
          [-105.070793, 39.65917],
          [-105.070923, 39.659193],
          [-105.071314, 39.659265],
          [-105.071445, 39.659289],
          [-105.071479, 39.65943],
          [-105.071582, 39.659853],
          [-105.071617, 39.659995],
          [-105.07324, 39.659787],
          [-105.074427, 39.659515],
          [-105.075423, 39.659286],
          [-105.076255, 39.659096],
          [-105.077642, 39.658779],
          [-105.077479, 39.659644],
          [-105.077342, 39.660374],
          [-105.077338, 39.660389],
          [-105.077863, 39.661004],
          [-105.07907, 39.661521],
          [-105.079071, 39.661521],
          [-105.079086, 39.661526],
          [-105.079534, 39.661814],
          [-105.079733, 39.662061],
          [-105.079771, 39.662163],
          [-105.079822, 39.66216],
          [-105.079822, 39.662164],
          [-105.081454, 39.662074],
          [-105.081454, 39.662896],
          [-105.081454, 39.664987],
          [-105.081449, 39.665055],
          [-105.081455, 39.665498],
          [-105.081455, 39.665551],
          [-105.081451, 39.667743],
          [-105.081454, 39.668215],
          [-105.081457, 39.668577],
          [-105.081434, 39.669103],
          [-105.081389, 39.669628],
          [-105.08135, 39.670099],
          [-105.081192, 39.670074],
          [-105.080948, 39.67001],
          [-105.080528, 39.669902],
          [-105.080157, 39.669826],
          [-105.079876, 39.669782],
          [-105.079739, 39.669765],
          [-105.079651, 39.669755],
          [-105.079345, 39.66974],
          [-105.079329, 39.669739],
          [-105.079299, 39.669738],
          [-105.079212, 39.669738],
          [-105.079183, 39.669738],
          [-105.079175, 39.669601],
          [-105.079164, 39.669358],
          [-105.0775, 39.66933],
          [-105.076938, 39.66932],
          [-105.076939, 39.66952],
          [-105.076939, 39.669523],
          [-105.076939, 39.669686],
          [-105.076039, 39.669587],
          [-105.07614, 39.669987],
          [-105.07494, 39.670687],
          [-105.07334, 39.670013],
          [-105.07328, 39.669987],
          [-105.073253, 39.669976],
          [-105.0731, 39.669912],
          [-105.07304, 39.669887],
          [-105.072872, 39.669813],
          [-105.072371, 39.669594],
          [-105.072204, 39.669521],
          [-105.072203, 39.669499],
          [-105.072202, 39.669435],
          [-105.072202, 39.669414],
          [-105.072197, 39.669083],
          [-105.072197, 39.669058],
          [-105.072186, 39.66828],
          [-105.072183, 39.668093],
          [-105.072179, 39.667763],
          [-105.072044, 39.667763],
          [-105.071643, 39.667763],
          [-105.071509, 39.667764],
          [-105.071483, 39.667764],
          [-105.07145, 39.667764],
          [-105.071406, 39.667764],
          [-105.071381, 39.667764],
          [-105.070805, 39.667765],
          [-105.06908, 39.667769],
          [-105.068505, 39.667771],
          [-105.068263, 39.667771],
          [-105.067538, 39.667774],
          [-105.067297, 39.667775],
          [-105.067149, 39.667775],
          [-105.066705, 39.667775],
          [-105.066558, 39.667776],
          [-105.06648, 39.667776],
          [-105.066247, 39.667776],
          [-105.06617, 39.667777],
          [-105.066151, 39.667777],
          [-105.066097, 39.667777],
          [-105.066079, 39.667777],
          [-105.065885, 39.667777],
          [-105.065304, 39.667777],
          [-105.065111, 39.667778],
          [-105.064897, 39.667778],
          [-105.064257, 39.667779],
          [-105.064044, 39.66778],
          [-105.064027, 39.66778],
          [-105.063976, 39.66778],
          [-105.063959, 39.66778],
          [-105.063759, 39.66778],
          [-105.063161, 39.667782],
          [-105.062962, 39.667783],
          [-105.062534, 39.667782],
          [-105.06125, 39.66778],
          [-105.060844, 39.66778],
          [-105.060823, 39.66778],
          [-105.06068, 39.66778],
          [-105.060252, 39.66778],
          [-105.06011, 39.66778],
          [-105.059894, 39.667779],
          [-105.059248, 39.667778],
          [-105.059033, 39.667778],
          [-105.058821, 39.667778],
          [-105.058185, 39.667782],
          [-105.057974, 39.667784],
          [-105.057765, 39.667784],
          [-105.05714, 39.667784],
          [-105.056932, 39.667785],
          [-105.056725, 39.667784],
          [-105.056104, 39.667781],
          [-105.055898, 39.667781],
          [-105.055691, 39.667781],
          [-105.055072, 39.667784],
          [-105.054866, 39.667785],
          [-105.054676, 39.667785],
          [-105.054106, 39.667785],
          [-105.053916, 39.667785],
          [-105.053784, 39.667785],
          [-105.053388, 39.667785],
          [-105.053256, 39.667785],
          [-105.053257, 39.668604],
          [-105.053257, 39.669076],
          [-105.05326, 39.671063],
          [-105.053262, 39.671883],
          [-105.053262, 39.671896],
          [-105.053262, 39.671935],
          [-105.053262, 39.671949],
          [-105.053262, 39.672255],
          [-105.053251, 39.673116],
          [-105.053261, 39.673271],
          [-105.053261, 39.67328],
          [-105.053255, 39.673483],
          [-105.053247, 39.673778],
          [-105.053239, 39.674095],
          [-105.053234, 39.674299],
          [-105.053237, 39.674437],
          [-105.053246, 39.674851],
          [-105.05325, 39.674989],
          [-105.054053, 39.674969],
          [-105.056462, 39.674909],
          [-105.057265, 39.674889],
          [-105.057573, 39.674888],
          [-105.0585, 39.674887],
          [-105.058809, 39.674887],
          [-105.058955, 39.674887],
          [-105.059393, 39.674887],
          [-105.059539, 39.674887],
          [-105.060047, 39.674903],
          [-105.061573, 39.674952],
          [-105.062082, 39.674969],
          [-105.062245, 39.674973],
          [-105.062638, 39.674985],
          [-105.062734, 39.674985],
          [-105.062898, 39.674987],
          [-105.062897, 39.675082],
          [-105.062894, 39.675369],
          [-105.062894, 39.675465],
          [-105.062891, 39.675853],
          [-105.062887, 39.676224],
          [-105.062772, 39.677608],
          [-105.062835, 39.678446],
          [-105.062907, 39.679223],
          [-105.062929, 39.679259],
          [-105.062991, 39.679363],
          [-105.063177, 39.679681],
          [-105.062777, 39.679441],
          [-105.06258, 39.679293],
          [-105.062442, 39.67919],
          [-105.062086, 39.679063],
          [-105.060732, 39.678279],
          [-105.058505, 39.678615],
          [-105.058175, 39.678666],
          [-105.05806, 39.680649],
          [-105.058049, 39.681817],
          [-105.058054, 39.681825],
          [-105.058044, 39.68227],
          [-105.058034, 39.682275],
          [-105.058033, 39.682275],
          [-105.057913, 39.682277],
          [-105.057912, 39.682276],
          [-105.057783, 39.682272],
          [-105.057265, 39.682272],
          [-105.0572, 39.682271],
          [-105.056518, 39.682265],
          [-105.055588, 39.68226],
          [-105.053221, 39.682291],
          [-105.053191, 39.682397],
          [-105.053183, 39.683498],
          [-105.053183, 39.683528],
          [-105.053195, 39.68438],
          [-105.053197, 39.685563],
          [-105.053196, 39.68565],
          [-105.053194, 39.685911],
          [-105.053194, 39.685999],
          [-105.053193, 39.686347],
          [-105.053193, 39.686873],
          [-105.053208, 39.687353],
          [-105.053212, 39.68739],
          [-105.053248, 39.687654],
          [-105.053254, 39.687736],
          [-105.053264, 39.687883],
          [-105.053264, 39.6881],
          [-105.053265, 39.689194],
          [-105.053266, 39.689559],
          [-105.053251, 39.689919],
          [-105.053208, 39.691002],
          [-105.053194, 39.691364],
          [-105.053198, 39.691586],
          [-105.05321, 39.692252],
          [-105.053215, 39.692475],
          [-105.053212, 39.692542],
          [-105.053207, 39.692745],
          [-105.053205, 39.692813],
          [-105.053202, 39.692885],
          [-105.053196, 39.693103],
          [-105.053194, 39.693176],
          [-105.053198, 39.693538],
          [-105.053213, 39.694625],
          [-105.053218, 39.694988],
          [-105.053219, 39.695344],
          [-105.053223, 39.696412],
          [-105.053225, 39.696769],
          [-105.053225, 39.696779],
          [-105.053225, 39.696809],
          [-105.053225, 39.69682],
          [-105.053225, 39.696889],
          [-105.053236, 39.699078],
          [-105.053241, 39.69991],
          [-105.053245, 39.700438],
          [-105.053245, 39.702269],
          [-105.053249, 39.702562],
          [-105.053247, 39.702703],
          [-105.053245, 39.703126],
          [-105.053245, 39.703268],
          [-105.053247, 39.703985],
          [-105.053252, 39.704542],
          [-105.053258, 39.70505],
          [-105.053246, 39.705814],
          [-105.053246, 39.705921],
          [-105.053246, 39.706029],
          [-105.053249, 39.706674],
          [-105.05325, 39.706889],
          [-105.05325, 39.707054],
          [-105.053251, 39.70755],
          [-105.053252, 39.707716],
          [-105.053253, 39.707902],
          [-105.053258, 39.708463],
          [-105.05326, 39.70865],
          [-105.053259, 39.708841],
          [-105.053257, 39.709416],
          [-105.053257, 39.709608],
          [-105.053258, 39.709746],
          [-105.053262, 39.710161],
          [-105.053264, 39.7103],
          [-105.05325, 39.711343],
          [-105.053263, 39.713181],
          [-105.053271, 39.715004],
          [-105.05327, 39.71663],
          [-105.05327, 39.716647],
          [-105.053279, 39.718233],
          [-105.053278, 39.718627],
          [-105.053276, 39.718984],
          [-105.053275, 39.719071],
          [-105.053272, 39.720056],
          [-105.053271, 39.720414],
          [-105.05327, 39.720544],
          [-105.05327, 39.720933],
          [-105.05327, 39.721064],
          [-105.053268, 39.721477],
          [-105.053264, 39.722719],
          [-105.053263, 39.723133],
          [-105.053259, 39.724083],
          [-105.05324, 39.725441],
          [-105.053253, 39.7258],
          [-105.053253, 39.725828],
          [-105.053253, 39.725899],
          [-105.053253, 39.725951],
          [-105.053252, 39.726112],
          [-105.053252, 39.726184],
          [-105.053254, 39.727233],
          [-105.053254, 39.727349],
          [-105.053252, 39.727772],
          [-105.053251, 39.728522],
          [-105.053246, 39.728968],
          [-105.053244, 39.729539],
          [-105.053242, 39.72989],
          [-105.053239, 39.730942],
          [-105.053238, 39.731294],
          [-105.053235, 39.731657],
          [-105.053228, 39.732746],
          [-105.053227, 39.73311],
          [-105.053404, 39.733108],
          [-105.053935, 39.733104],
          [-105.054113, 39.733103],
          [-105.05417, 39.733102],
          [-105.05434, 39.733102],
          [-105.054398, 39.733102],
          [-105.0544, 39.733172],
          [-105.054406, 39.733384],
          [-105.054409, 39.733455],
          [-105.054411, 39.733541],
          [-105.054418, 39.73372],
          [-105.054499, 39.733706],
          [-105.054585, 39.733693],
          [-105.054587, 39.733734],
          [-105.054593, 39.73386],
          [-105.054595, 39.733902],
          [-105.054671, 39.7339],
          [-105.054903, 39.733897],
          [-105.05498, 39.733896],
          [-105.054979, 39.733854],
          [-105.054977, 39.733728],
          [-105.054977, 39.733687],
          [-105.054975, 39.733569],
          [-105.054969, 39.733217],
          [-105.054968, 39.7331],
          [-105.055082, 39.733099],
          [-105.055424, 39.733098],
          [-105.055539, 39.733098],
          [-105.055539, 39.733215],
          [-105.055539, 39.733569],
          [-105.055539, 39.733687],
          [-105.055574, 39.734508],
          [-105.055585, 39.734707],
          [-105.055586, 39.73474],
          [-105.055567, 39.734977],
          [-105.053228, 39.734965],
          [-105.053231, 39.735161],
          [-105.05323, 39.735185],
          [-105.053227, 39.735259],
          [-105.053227, 39.735284],
          [-105.053227, 39.735349],
          [-105.053227, 39.735544],
          [-105.053227, 39.735609],
          [-105.053227, 39.735841],
          [-105.053227, 39.736537],
          [-105.053227, 39.73677],
          [-105.053223, 39.737115],
          [-105.05321, 39.738152],
          [-105.053207, 39.738498],
          [-105.053206, 39.73887],
          [-105.053206, 39.739987],
          [-105.053206, 39.74036],
          [-105.053201, 39.741935],
          [-105.0532, 39.741935],
          [-105.053195, 39.741938],
          [-105.053199, 39.742204],
          [-105.053204, 39.742203],
          [-105.053209, 39.742697],
          [-105.053209, 39.742781],
          [-105.05321, 39.743339],
          [-105.053211, 39.743486],
          [-105.053211, 39.743584],
          [-105.053211, 39.743881],
          [-105.053212, 39.74398],
          [-105.053214, 39.745187],
          [-105.053226, 39.74568],
          [-105.053219, 39.747616],
          [-105.053219, 39.747687],
          [-105.053217, 39.748157],
          [-105.053213, 39.749458],
          [-105.053215, 39.749816],
          [-105.053224, 39.75089],
          [-105.053227, 39.751249],
          [-105.053227, 39.751608],
          [-105.05323, 39.752686],
          [-105.053231, 39.753046],
          [-105.053236, 39.754848],
          [-105.053236, 39.755553],
          [-105.053237, 39.755787],
          [-105.053238, 39.756939],
          [-105.053238, 39.757245],
          [-105.053238, 39.758165],
          [-105.053239, 39.758472],
          [-105.053239, 39.758836],
          [-105.05324, 39.759928],
          [-105.053241, 39.760293],
          [-105.053241, 39.760651],
          [-105.053243, 39.761725],
          [-105.053244, 39.762084],
          [-105.053243, 39.762265],
          [-105.053241, 39.762807],
          [-105.053241, 39.762989],
          [-105.053241, 39.763171],
          [-105.053241, 39.76372],
          [-105.053241, 39.763903],
          [-105.053241, 39.764085],
          [-105.053242, 39.764631],
          [-105.053243, 39.764814],
          [-105.05324, 39.764991],
          [-105.053232, 39.765521],
          [-105.05323, 39.765699],
          [-105.053242, 39.766622],
          [-105.053243, 39.767278],
          [-105.053241, 39.767409],
          [-105.053241, 39.767512],
          [-105.05324, 39.767876],
          [-105.05324, 39.768968],
          [-105.05324, 39.769333],
          [-105.053241, 39.769435],
          [-105.053245, 39.769741],
          [-105.053247, 39.769844],
          [-105.05325, 39.770102],
          [-105.053261, 39.770876],
          [-105.053265, 39.771134],
          [-105.05326, 39.771494],
          [-105.053245, 39.772577],
          [-105.053241, 39.772938],
          [-105.053241, 39.773303],
          [-105.053241, 39.774398],
          [-105.053241, 39.774763],
          [-105.053241, 39.775127],
          [-105.053244, 39.776219],
          [-105.053245, 39.776583],
          [-105.053244, 39.776943],
          [-105.053242, 39.778023],
          [-105.053242, 39.778384],
          [-105.053235, 39.778748],
          [-105.053214, 39.77984],
          [-105.053208, 39.780205],
          [-105.053214, 39.780502],
          [-105.053235, 39.781393],
          [-105.053243, 39.78169],
          [-105.053243, 39.781732],
          [-105.053243, 39.781861],
          [-105.053243, 39.781904],
          [-105.053243, 39.782075],
          [-105.053243, 39.78219],
          [-105.053211, 39.782588],
          [-105.053198, 39.782759],
          [-105.053196, 39.782852],
          [-105.05319, 39.783134],
          [-105.053189, 39.783228],
          [-105.053187, 39.783288],
          [-105.053185, 39.783435],
          [-105.053184, 39.783471],
          [-105.053184, 39.783533],
          [-105.053184, 39.783585],
          [-105.053184, 39.783687],
          [-105.053183, 39.783744],
          [-105.053183, 39.783797],
          [-105.054442, 39.783814],
          [-105.055588, 39.783818],
          [-105.056742, 39.783821],
          [-105.05675, 39.783822],
          [-105.057894, 39.78383],
          [-105.057912, 39.783831],
          [-105.058863, 39.78383],
          [-105.059054, 39.78383],
          [-105.059628, 39.78383],
          [-105.05982, 39.78383],
          [-105.060009, 39.783832],
          [-105.060346, 39.783836],
          [-105.060576, 39.783839],
          [-105.060765, 39.783842],
          [-105.061711, 39.783845],
          [-105.061848, 39.783845],
          [-105.062176, 39.783846],
          [-105.062425, 39.783847],
          [-105.062659, 39.783847],
          [-105.062659, 39.7839],
          [-105.062659, 39.784059],
          [-105.062659, 39.784112],
          [-105.062656, 39.785666],
          [-105.062655, 39.785822],
          [-105.062655, 39.786187],
          [-105.062653, 39.787],
          [-105.062653, 39.787193],
          [-105.062556, 39.787408],
          [-105.062546, 39.787425],
          [-105.062451, 39.787597],
          [-105.062865, 39.788011],
          [-105.062944, 39.788087],
          [-105.065083, 39.788086],
          [-105.065106, 39.788813],
          [-105.065123, 39.789269],
          [-105.065115, 39.78968],
          [-105.064777, 39.789691],
          [-105.064559, 39.789695],
          [-105.064552, 39.789999],
          [-105.064545, 39.790686],
          [-105.064542, 39.790989],
          [-105.064541, 39.79108],
          [-105.064243, 39.791079],
          [-105.064203, 39.791079],
          [-105.064161, 39.791079],
          [-105.063382, 39.791077],
          [-105.063306, 39.791077],
          [-105.063304, 39.790775],
          [-105.063303, 39.790737],
          [-105.063302, 39.790568],
          [-105.063302, 39.790494],
          [-105.063301, 39.790476],
          [-105.063301, 39.790445],
          [-105.063298, 39.790081],
          [-105.063059, 39.790084],
          [-105.062966, 39.790086],
          [-105.061917, 39.790083],
          [-105.061899, 39.790083],
          [-105.061722, 39.790582],
          [-105.061608, 39.790928],
          [-105.06148, 39.791075],
          [-105.060678, 39.791073],
          [-105.060417, 39.791073],
          [-105.059696, 39.791072],
          [-105.059338, 39.791072],
          [-105.05663, 39.791067],
          [-105.056162, 39.791067],
          [-105.055627, 39.791066],
          [-105.055272, 39.791065],
          [-105.055083, 39.791065],
          [-105.053246, 39.791063],
          [-104.79091, 39.79825],
          [-104.692934, 39.914183],
          [-104.600435, 39.899459],
          [-104.619906, 39.826633],
          [-104.76247, 39.82298],
          [-104.734601, 39.769181],
          [-104.856377, 39.768673],
          [-104.884646, 39.740156],
          [-104.848332, 39.659161],
          [-104.848336, 39.657891],
          [-104.848048, 39.657842],
          [-104.84794, 39.657824],
          [-104.84773, 39.657789],
          [-104.84773, 39.657239],
          [-104.847731, 39.656927],
          [-104.847733, 39.656788],
          [-104.847604, 39.656786],
          [-104.847536, 39.656785],
          [-104.847449, 39.656783],
          [-104.847119, 39.65678],
          [-104.847639, 39.656712],
          [-104.847697, 39.656704],
          [-104.84812, 39.656648],
          [-104.848967, 39.656538],
          [-104.849397, 39.656482],
          [-104.850762, 39.656305],
          [-104.852944, 39.655706],
          [-104.854474, 39.655011],
          [-104.854747, 39.654888],
          [-104.856038, 39.654178],
          [-104.856753, 39.653456],
          [-104.85683, 39.653419],
          [-104.857203, 39.653246],
          [-104.875552, 39.644617],
          [-104.876224, 39.644301],
          [-104.87613, 39.646499],
          [-104.876149, 39.647004],
          [-104.876151, 39.647061],
          [-104.876195, 39.648583],
          [-104.876195, 39.6486],
          [-104.87624, 39.65011],
          [-104.876281, 39.651459],
          [-104.876297, 39.652021],
          [-104.876306, 39.652295],
          [-104.877218, 39.652875],
          [-104.877497, 39.653052],
          [-104.877498, 39.653051],
          [-104.878034, 39.653051],
          [-104.880078, 39.653055],
          [-104.88014, 39.653057],
          [-104.880086, 39.650773],
          [-104.880228, 39.647994],
          [-104.880246, 39.647751],
          [-104.880246, 39.64653],
          [-104.880239, 39.646068],
          [-104.880239, 39.645993],
          [-104.880313, 39.645902],
          [-104.880313, 39.643949],
          [-104.880372, 39.642605],
          [-104.880381, 39.642475],
          [-104.880368, 39.642193],
          [-104.88034, 39.641993],
          [-104.880338, 39.641955],
          [-104.880326, 39.641819],
          [-104.880326, 39.64173],
          [-104.880322, 39.641651],
          [-104.880329, 39.641491],
          [-104.880929, 39.641216],
          [-104.882827, 39.640345],
          [-104.882917, 39.640301],
          [-104.88368, 39.639939],
          [-104.884765, 39.639425],
          [-104.884936, 39.639344],
          [-104.885112, 39.639262],
          [-104.885119, 39.638961],
          [-104.885129, 39.638788],
          [-104.885128, 39.638637],
          [-104.885124, 39.638444],
          [-104.885121, 39.638283],
          [-104.885132, 39.638093],
          [-104.885167, 39.636493],
          [-104.885173, 39.636268],
          [-104.885156, 39.633467],
          [-104.885231, 39.631345],
          [-104.885218, 39.629513],
          [-104.885213, 39.629087],
          [-104.88528, 39.627128],
          [-104.885282, 39.624129],
          [-104.887773, 39.624116],
          [-104.888331, 39.624143],
          [-104.888758, 39.624165],
          [-104.889037, 39.624176],
          [-104.889661, 39.6242],
          [-104.889813, 39.624191],
          [-104.891733, 39.624115],
          [-104.892667, 39.624135],
          [-104.894522, 39.624175],
          [-104.894642, 39.624173],
          [-104.897084, 39.624127],
          [-104.898639, 39.624099],
          [-104.899263, 39.624097],
          [-104.899339, 39.624097],
          [-104.899371, 39.624097],
          [-104.900933, 39.623906],
          [-104.901561, 39.623905],
          [-104.901719, 39.623905],
          [-104.901763, 39.623907],
          [-104.902451, 39.623939],
          [-104.90403, 39.623995],
          [-104.90549, 39.624158],
          [-104.906394, 39.624259],
          [-104.906942, 39.624268],
          [-104.908587, 39.624297],
          [-104.908862, 39.624301],
          [-104.911686, 39.624342],
          [-104.911935, 39.624346],
          [-104.913341, 39.624213],
          [-104.931703, 39.653044],
          [-105.015696, 39.678684],
          [-105.032038, 39.657087],
          [-105.034822, 39.627809],
          [-105.053411, 39.631586],
          [-105.053439, 39.621423]
        ],
        [
          [-105.081564, 39.634728],
          [-105.080803, 39.634723],
          [-105.080907, 39.635605],
          [-105.081959, 39.63517],
          [-105.081564, 39.634728]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08035",
      state: "08",
      county: "035",
      name: "Douglas County",
      censusArea: 840.248,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.04874, 39.566088],
          [-104.660626, 39.565909],
          [-104.662896, 39.129527],
          [-105.033544, 39.129819],
          [-105.32922, 39.129689],
          [-105.260054, 39.210445],
          [-105.04874, 39.566088]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08047",
      state: "08",
      county: "047",
      name: "Gilpin County",
      censusArea: 149.896,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.397849, 39.912886],
          [-105.397649, 39.883187],
          [-105.398043, 39.872186],
          [-105.397568, 39.866061],
          [-105.397949, 39.860387],
          [-105.39745, 39.853697],
          [-105.397934, 39.841137],
          [-105.397949, 39.747087],
          [-105.545076, 39.773635],
          [-105.690348, 39.851996],
          [-105.675798, 39.932445],
          [-105.397971, 39.934867],
          [-105.397849, 39.912886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12083",
      state: "12",
      county: "083",
      name: "Marion County",
      censusArea: 1584.546,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.311697, 28.960391],
          [-82.426997, 29.021385],
          [-82.535591, 29.044855],
          [-82.53486, 29.21468],
          [-82.403237, 29.215623],
          [-82.404594, 29.27886],
          [-82.405152, 29.33026],
          [-82.40662, 29.485048],
          [-82.22501, 29.484298],
          [-82.213245, 29.431764],
          [-82.105688, 29.435966],
          [-82.055899, 29.471232],
          [-81.843009, 29.521004],
          [-81.776205, 29.487448],
          [-81.741422, 29.371049],
          [-81.680903, 29.32443],
          [-81.675523, 29.310628],
          [-81.667885, 29.301488],
          [-81.655677, 29.29994],
          [-81.648438, 29.290173],
          [-81.641916, 29.276766],
          [-81.640517, 29.1316],
          [-81.639151, 29.06288],
          [-81.658698, 28.960345],
          [-81.842194, 28.961024],
          [-81.843025, 28.959884],
          [-81.95419, 28.960053],
          [-82.311697, 28.960391]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12087",
      state: "12",
      county: "087",
      name: "Monroe County",
      censusArea: 983.282,
      stateName: "Florida"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-80.250581, 25.34193],
            [-80.254916, 25.336336],
            [-80.260137, 25.324641],
            [-80.268138, 25.320675],
            [-80.288184, 25.282835],
            [-80.307584, 25.257561],
            [-80.351399, 25.190615],
            [-80.354019, 25.184306],
            [-80.349855, 25.168825],
            [-80.35857, 25.154073],
            [-80.377084, 25.130487],
            [-80.399767, 25.108536],
            [-80.428318, 25.095547],
            [-80.431032, 25.08925],
            [-80.443375, 25.076084],
            [-80.462011, 25.069935],
            [-80.47387, 25.060253],
            [-80.493881, 25.038502],
            [-80.48912, 25.031301],
            [-80.494781, 25.023019],
            [-80.537995, 24.990244],
            [-80.565831, 24.958155],
            [-80.571668, 24.953659],
            [-80.588272, 24.951153],
            [-80.596073, 24.948173],
            [-80.611693, 24.93842],
            [-80.635571, 24.913003],
            [-80.659395, 24.897433],
            [-80.66128, 24.899704],
            [-80.660198, 24.90498],
            [-80.650765, 24.908121],
            [-80.641306, 24.914311],
            [-80.623866, 24.931236],
            [-80.622896, 24.935587],
            [-80.624172, 24.939058],
            [-80.621658, 24.944265],
            [-80.597074, 24.958492],
            [-80.581131, 24.964738],
            [-80.578185, 24.962811],
            [-80.570813, 24.962215],
            [-80.558785, 24.971505],
            [-80.54411, 24.999916],
            [-80.543254, 25.007337],
            [-80.545971, 25.01477],
            [-80.524498, 25.016945],
            [-80.509136, 25.028317],
            [-80.501326, 25.041436],
            [-80.495569, 25.047497],
            [-80.4889, 25.05011],
            [-80.481197, 25.056604],
            [-80.460652, 25.078904],
            [-80.465496, 25.086609],
            [-80.470211, 25.089438],
            [-80.481763, 25.091905],
            [-80.494715, 25.102269],
            [-80.484188, 25.10943],
            [-80.47748, 25.107407],
            [-80.476174, 25.099454],
            [-80.463987, 25.09321],
            [-80.450399, 25.088751],
            [-80.444887, 25.092966],
            [-80.433575, 25.106317],
            [-80.433499, 25.114665],
            [-80.447659, 25.147729],
            [-80.446473, 25.151287],
            [-80.41326, 25.137053],
            [-80.403177, 25.141798],
            [-80.395467, 25.150694],
            [-80.387164, 25.170859],
            [-80.38835, 25.182721],
            [-80.391909, 25.19221],
            [-80.387164, 25.198141],
            [-80.369965, 25.206444],
            [-80.358696, 25.207037],
            [-80.3498, 25.210595],
            [-80.337345, 25.231353],
            [-80.333787, 25.253891],
            [-80.336159, 25.261601],
            [-80.342683, 25.268125],
            [-80.368186, 25.282359],
            [-80.364034, 25.28651],
            [-80.339421, 25.290069],
            [-80.334676, 25.285917],
            [-80.328746, 25.28651],
            [-80.315698, 25.29422],
            [-80.292567, 25.314385],
            [-80.289602, 25.325061],
            [-80.275961, 25.344039],
            [-80.264614, 25.354322],
            [-80.256982, 25.361239],
            [-80.25461, 25.38081],
            [-80.251052, 25.391486],
            [-80.246307, 25.398603],
            [-80.226142, 25.406313],
            [-80.219025, 25.411058],
            [-80.21428, 25.416988],
            [-80.20657, 25.434188],
            [-80.199453, 25.458504],
            [-80.192336, 25.473331],
            [-80.189964, 25.485786],
            [-80.191743, 25.495275],
            [-80.188778, 25.50773],
            [-80.179288, 25.518999],
            [-80.174544, 25.518406],
            [-80.173951, 25.482821],
            [-80.184033, 25.468587],
            [-80.204198, 25.412244],
            [-80.221991, 25.397417],
            [-80.238004, 25.361832],
            [-80.240376, 25.347005],
            [-80.249865, 25.342853],
            [-80.250581, 25.34193]
          ]
        ],
        [
          [
            [-81.582923, 24.658732],
            [-81.580534, 24.66914],
            [-81.562917, 24.692912],
            [-81.55761, 24.692488],
            [-81.542116, 24.681026],
            [-81.535323, 24.67954],
            [-81.51898, 24.687818],
            [-81.516433, 24.700341],
            [-81.5124, 24.703737],
            [-81.490962, 24.710105],
            [-81.476642, 24.711244],
            [-81.474186, 24.706332],
            [-81.469275, 24.704286],
            [-81.459043, 24.707355],
            [-81.454132, 24.710834],
            [-81.451881, 24.714518],
            [-81.4527, 24.736209],
            [-81.456588, 24.740097],
            [-81.451267, 24.747464],
            [-81.440831, 24.73539],
            [-81.43858, 24.727],
            [-81.435715, 24.723931],
            [-81.432032, 24.722908],
            [-81.423028, 24.731911],
            [-81.421595, 24.737641],
            [-81.427121, 24.745827],
            [-81.430599, 24.747259],
            [-81.431009, 24.751761],
            [-81.425483, 24.752989],
            [-81.402769, 24.749101],
            [-81.392947, 24.743371],
            [-81.390287, 24.73846],
            [-81.389468, 24.731298],
            [-81.38558, 24.726182],
            [-81.36041, 24.708788],
            [-81.345881, 24.70756],
            [-81.319282, 24.701238],
            [-81.314787, 24.691764],
            [-81.313933, 24.680707],
            [-81.309664, 24.665017],
            [-81.298028, 24.656774],
            [-81.298369, 24.654326],
            [-81.303113, 24.651665],
            [-81.332831, 24.639528],
            [-81.395096, 24.621062],
            [-81.401946, 24.623564],
            [-81.403319, 24.640294],
            [-81.414187, 24.647167],
            [-81.432315, 24.645949],
            [-81.448623, 24.640172],
            [-81.470411, 24.641985],
            [-81.480951, 24.645121],
            [-81.48183, 24.647369],
            [-81.477915, 24.649893],
            [-81.47641, 24.653197],
            [-81.480504, 24.659757],
            [-81.49858, 24.66498],
            [-81.502992, 24.660877],
            [-81.505585, 24.654609],
            [-81.50874, 24.64421],
            [-81.509028, 24.631516],
            [-81.511165, 24.625135],
            [-81.518595, 24.620304],
            [-81.54645, 24.614895],
            [-81.602998, 24.586444],
            [-81.664209, 24.573143],
            [-81.674694, 24.564359],
            [-81.685278, 24.558739],
            [-81.691575, 24.559886],
            [-81.732511, 24.556423],
            [-81.765993, 24.552103],
            [-81.786157, 24.54658],
            [-81.810333, 24.544701],
            [-81.81289, 24.546468],
            [-81.814446, 24.56358],
            [-81.811386, 24.56975],
            [-81.800676, 24.570989],
            [-81.794057, 24.586],
            [-81.773808, 24.584977],
            [-81.748071, 24.590199],
            [-81.739241, 24.589973],
            [-81.734573, 24.584148],
            [-81.730473, 24.58196],
            [-81.715944, 24.587956],
            [-81.71548, 24.592498],
            [-81.705364, 24.597647],
            [-81.699349, 24.597647],
            [-81.694235, 24.591932],
            [-81.687017, 24.592534],
            [-81.678595, 24.597647],
            [-81.66897, 24.607873],
            [-81.655735, 24.616295],
            [-81.637087, 24.621408],
            [-81.614829, 24.642764],
            [-81.614529, 24.650584],
            [-81.597685, 24.655397],
            [-81.587759, 24.655998],
            [-81.582923, 24.658732]
          ]
        ],
        [
          [
            [-82.15068, 24.576331],
            [-82.143075, 24.593395],
            [-82.135423, 24.596879],
            [-82.125268, 24.597426],
            [-82.104187, 24.588256],
            [-82.101051, 24.584679],
            [-82.099417, 24.572522],
            [-82.104429, 24.561167],
            [-82.116787, 24.549144],
            [-82.159439, 24.548212],
            [-82.165206, 24.552159],
            [-82.164426, 24.563375],
            [-82.15068, 24.576331]
          ]
        ],
        [
          [
            [-81.249799, 24.673357],
            [-81.246095, 24.675832],
            [-81.243232, 24.673998],
            [-81.244761, 24.669202],
            [-81.281778, 24.65375],
            [-81.278312, 24.660448],
            [-81.260006, 24.674848],
            [-81.249799, 24.673357]
          ]
        ],
        [
          [
            [-80.909954, 24.781154],
            [-80.906288, 24.769867],
            [-80.912042, 24.76505],
            [-80.938543, 24.767535],
            [-81.015933, 24.719881],
            [-81.023794, 24.716901],
            [-81.028616, 24.720618],
            [-81.032447, 24.727323],
            [-81.03429, 24.727341],
            [-81.064554, 24.715453],
            [-81.071034, 24.711722],
            [-81.075855, 24.704266],
            [-81.078716, 24.696557],
            [-81.078439, 24.692382],
            [-81.108041, 24.688592],
            [-81.124094, 24.704873],
            [-81.125371, 24.708291],
            [-81.107355, 24.71276],
            [-81.105287, 24.71128],
            [-81.099135, 24.711993],
            [-81.066816, 24.723926],
            [-81.05057, 24.737581],
            [-81.041797, 24.742965],
            [-81.036698, 24.742827],
            [-81.035192, 24.739982],
            [-81.02217, 24.733091],
            [-81.016918, 24.734676],
            [-80.994426, 24.743991],
            [-80.986454, 24.752749],
            [-80.960129, 24.764226],
            [-80.910431, 24.782324],
            [-80.909954, 24.781154]
          ]
        ],
        [
          [
            [-81.317673, 24.75729],
            [-81.305468, 24.756612],
            [-81.290801, 24.736862],
            [-81.288259, 24.720881],
            [-81.302984, 24.714199],
            [-81.310744, 24.727068],
            [-81.318505, 24.729477],
            [-81.326844, 24.728375],
            [-81.350162, 24.746524],
            [-81.357417, 24.756834],
            [-81.342695, 24.75625],
            [-81.327555, 24.762315],
            [-81.324637, 24.76721],
            [-81.317673, 24.75729]
          ]
        ],
        [
          [
            [-80.89054, 24.791678],
            [-80.884572, 24.791561],
            [-80.88402, 24.790414],
            [-80.892649, 24.785991],
            [-80.906874, 24.783744],
            [-80.89054, 24.791678]
          ]
        ],
        [
          [
            [-80.788263, 24.824218],
            [-80.790497, 24.817789],
            [-80.796053, 24.81194],
            [-80.822342, 24.812629],
            [-80.846191, 24.802968],
            [-80.850338, 24.8026],
            [-80.850866, 24.803701],
            [-80.846142, 24.807488],
            [-80.830158, 24.81428],
            [-80.814551, 24.827953],
            [-80.79278, 24.843918],
            [-80.780564, 24.84052],
            [-80.788263, 24.824218]
          ]
        ],
        [
          [
            [-80.729275, 24.865361],
            [-80.719977, 24.864644],
            [-80.703028, 24.880873],
            [-80.691762, 24.885759],
            [-80.690354, 24.881539],
            [-80.703176, 24.869495],
            [-80.71185, 24.863323],
            [-80.761359, 24.836225],
            [-80.766966, 24.836158],
            [-80.745468, 24.850652],
            [-80.740611, 24.857421],
            [-80.732343, 24.86481],
            [-80.729275, 24.865361]
          ]
        ],
        [
          [
            [-80.873096, 25.805377],
            [-80.87319, 25.363993],
            [-80.858167, 25.176576],
            [-80.858801, 25.176493],
            [-80.874323, 25.174469],
            [-80.874815, 25.174405],
            [-80.87546, 25.174321],
            [-80.875731, 25.174185],
            [-80.878982, 25.172562],
            [-80.879235, 25.172436],
            [-80.891681, 25.166221],
            [-80.899459, 25.162337],
            [-80.900066, 25.162034],
            [-80.900124, 25.161726],
            [-80.90111, 25.156496],
            [-80.901592, 25.153933],
            [-80.901617, 25.153803],
            [-80.90022, 25.150627],
            [-80.898911, 25.147652],
            [-80.900559, 25.139755],
            [-80.900577, 25.139669],
            [-80.900668, 25.139679],
            [-80.902311, 25.139853],
            [-80.906578, 25.140307],
            [-80.915924, 25.141301],
            [-80.915965, 25.141291],
            [-80.931353, 25.137424],
            [-80.93675, 25.136068],
            [-80.939272, 25.135434],
            [-80.940988, 25.135003],
            [-80.943164, 25.134456],
            [-80.943216, 25.134443],
            [-80.954567, 25.13545],
            [-80.955577, 25.13554],
            [-80.95615, 25.135591],
            [-80.957427, 25.135704],
            [-80.958727, 25.135546],
            [-80.967465, 25.134482],
            [-80.967506, 25.134477],
            [-80.96774, 25.134448],
            [-80.967832, 25.134437],
            [-80.970185, 25.13415],
            [-80.970727, 25.134084],
            [-80.970797, 25.134076],
            [-80.971585, 25.13398],
            [-80.971664, 25.13397],
            [-80.971765, 25.133958],
            [-80.973129, 25.133473],
            [-80.977198, 25.132028],
            [-80.991949, 25.126789],
            [-80.994096, 25.126026],
            [-80.999176, 25.124222],
            [-80.999772, 25.12429],
            [-81.009598, 25.125403],
            [-81.022989, 25.129393],
            [-81.025154, 25.129305],
            [-81.033404, 25.128969],
            [-81.038021, 25.128781],
            [-81.049308, 25.128322],
            [-81.049344, 25.12832],
            [-81.049445, 25.128316],
            [-81.049896, 25.128298],
            [-81.050505, 25.128273],
            [-81.079859, 25.118797],
            [-81.094524, 25.127054],
            [-81.111943, 25.14547],
            [-81.120616, 25.152302],
            [-81.133567, 25.156295],
            [-81.141024, 25.163868],
            [-81.142278, 25.183],
            [-81.142471, 25.18344],
            [-81.142897, 25.184406],
            [-81.146737, 25.193139],
            [-81.155252, 25.207706],
            [-81.155481, 25.208098],
            [-81.15582, 25.208389],
            [-81.168046, 25.218854],
            [-81.171265, 25.221609],
            [-81.172044, 25.222276],
            [-81.171978, 25.223648],
            [-81.171455, 25.234483],
            [-81.171369, 25.236268],
            [-81.170953, 25.244898],
            [-81.170907, 25.245857],
            [-81.169709, 25.249231],
            [-81.169509, 25.249795],
            [-81.169294, 25.250398],
            [-81.168307, 25.253178],
            [-81.16207, 25.289833],
            [-81.159293, 25.298595],
            [-81.1523, 25.305543],
            [-81.148915, 25.318067],
            [-81.151916, 25.324766],
            [-81.148103, 25.332793],
            [-81.140099, 25.341117],
            [-81.133913, 25.342996],
            [-81.12141, 25.33875],
            [-81.118208, 25.34522],
            [-81.117265, 25.354953],
            [-81.128492, 25.380511],
            [-81.141395, 25.381358],
            [-81.150508, 25.387255],
            [-81.150656, 25.399206],
            [-81.147144, 25.404297],
            [-81.146765, 25.407577],
            [-81.168652, 25.463848],
            [-81.179406, 25.475427],
            [-81.191924, 25.484745],
            [-81.208201, 25.504937],
            [-81.210149, 25.516888],
            [-81.203175, 25.53416],
            [-81.204389, 25.538908],
            [-81.209321, 25.548611],
            [-81.225557, 25.55847],
            [-81.232705, 25.573366],
            [-81.233051, 25.586587],
            [-81.240519, 25.599041],
            [-81.240677, 25.613629],
            [-81.253951, 25.638181],
            [-81.268924, 25.656927],
            [-81.277374, 25.66498],
            [-81.290328, 25.687506],
            [-81.328935, 25.717233],
            [-81.335037, 25.715649],
            [-81.346078, 25.721473],
            [-81.345972, 25.736536],
            [-81.343984, 25.747668],
            [-81.346767, 25.754029],
            [-81.355116, 25.76039],
            [-81.359489, 25.766354],
            [-81.361875, 25.772715],
            [-81.344779, 25.782257],
            [-81.340406, 25.786631],
            [-81.341598, 25.794582],
            [-81.344564, 25.803322],
            [-80.873096, 25.805377]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12095",
      state: "12",
      county: "095",
      name: "Orange County",
      censusArea: 903.429,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.65739, 28.553122],
          [-81.658114, 28.68271],
          [-81.624731, 28.785927],
          [-81.592971, 28.78597],
          [-81.414446, 28.784955],
          [-81.459695, 28.644475],
          [-81.459501, 28.640285],
          [-81.457975, 28.640336],
          [-81.365289, 28.640468],
          [-81.357965, 28.639187],
          [-81.331099, 28.639581],
          [-81.32865, 28.639334],
          [-81.327911, 28.611268],
          [-81.326991, 28.610298],
          [-80.98725, 28.612997],
          [-80.88089, 28.503633],
          [-80.862908, 28.347487],
          [-81.309571, 28.348051],
          [-81.513859, 28.347374],
          [-81.531837, 28.347384],
          [-81.657268, 28.347098],
          [-81.65739, 28.553122]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12111",
      state: "12",
      county: "111",
      name: "St. Lucie County",
      censusArea: 571.926,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.677862, 27.205988],
          [-80.678609, 27.448598],
          [-80.679288, 27.455697],
          [-80.679821, 27.55847],
          [-80.321271, 27.557378],
          [-80.311757, 27.524625],
          [-80.30117, 27.500314],
          [-80.293171, 27.500314],
          [-80.265535, 27.420542],
          [-80.253665, 27.37979],
          [-80.233538, 27.341307],
          [-80.226753, 27.322736],
          [-80.199288, 27.263022],
          [-80.284771, 27.263723],
          [-80.28551, 27.206025],
          [-80.481413, 27.20637],
          [-80.481534, 27.20637],
          [-80.677862, 27.205988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12119",
      state: "12",
      county: "119",
      name: "Sumter County",
      censusArea: 546.933,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.95419, 28.960053],
          [-81.95358, 28.82094],
          [-81.957641, 28.34518],
          [-82.055819, 28.312794],
          [-82.054694, 28.478397],
          [-82.054469, 28.521367],
          [-82.208184, 28.572054],
          [-82.263052, 28.667634],
          [-82.170848, 28.79019],
          [-82.311697, 28.960391],
          [-81.95419, 28.960053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12125",
      state: "12",
      county: "125",
      name: "Union County",
      censusArea: 243.556,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.418728, 29.923093],
          [-82.529705, 29.940881],
          [-82.57601, 29.991468],
          [-82.458364, 30.136449],
          [-82.142578, 30.143117],
          [-82.307409, 29.956156],
          [-82.418728, 29.923093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13001",
      state: "13",
      county: "001",
      name: "Appling County",
      censusArea: 507.081,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.52142, 31.710796],
          [-82.520251, 31.838388],
          [-82.431362, 31.837993],
          [-82.431531, 31.966182],
          [-82.225042, 31.913074],
          [-82.142843, 31.8993],
          [-82.048582, 31.827075],
          [-82.133013, 31.773404],
          [-82.132794, 31.471262],
          [-82.226585, 31.530699],
          [-82.52142, 31.710796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13013",
      state: "13",
      county: "013",
      name: "Barrow County",
      censusArea: 160.309,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.647031, 33.906198],
          [-83.763835, 33.896682],
          [-83.799104, 33.929844],
          [-83.869115, 34.004316],
          [-83.817682, 34.127493],
          [-83.767532, 34.066448],
          [-83.563215, 34.031757],
          [-83.537385, 33.965912],
          [-83.647031, 33.906198]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13029",
      state: "13",
      county: "029",
      name: "Bryan County",
      censusArea: 435.967,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.391698, 32.095886],
          [-81.323529, 32.019854],
          [-81.285467, 31.942994],
          [-81.138533, 31.855921],
          [-81.177027, 31.816113],
          [-81.154686, 31.726203],
          [-81.16067, 31.728144],
          [-81.192784, 31.733245],
          [-81.198394, 31.72607],
          [-81.355837, 31.8149],
          [-81.407737, 31.944302],
          [-81.475761, 31.974255],
          [-81.593429, 31.97135],
          [-81.604431, 32.010948],
          [-81.718658, 32.089351],
          [-81.780858, 32.15289],
          [-81.43583, 32.241289],
          [-81.391698, 32.095886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13043",
      state: "13",
      county: "043",
      name: "Candler County",
      censusArea: 243.044,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.969069, 32.268783],
          [-81.999843, 32.276189],
          [-82.025339, 32.278883],
          [-82.232788, 32.318838],
          [-82.252757, 32.35331],
          [-82.148338, 32.520429],
          [-82.030233, 32.53877],
          [-81.918671, 32.41541],
          [-81.969069, 32.268783]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13055",
      state: "13",
      county: "055",
      name: "Chattooga County",
      censusArea: 313.338,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.513699, 34.524133],
          [-85.513709, 34.52417],
          [-85.51393, 34.525192],
          [-85.527127, 34.588684],
          [-85.107748, 34.587483],
          [-85.180548, 34.435996],
          [-85.334881, 34.369748],
          [-85.387351, 34.286097],
          [-85.462304, 34.286365],
          [-85.502316, 34.473954],
          [-85.502454, 34.474527],
          [-85.508384, 34.501212],
          [-85.512108, 34.518252],
          [-85.513699, 34.524133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13059",
      state: "13",
      county: "059",
      name: "Clarke County",
      censusArea: 119.2,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.360028, 34.040572],
          [-83.258413, 33.999098],
          [-83.24086, 33.90443],
          [-83.275933, 33.847977],
          [-83.537385, 33.965912],
          [-83.360028, 34.040572]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13071",
      state: "13",
      county: "071",
      name: "Colquitt County",
      censusArea: 544.153,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.99943, 31.334966],
          [-83.65409, 31.330661],
          [-83.512607, 31.327405],
          [-83.50981, 31.206181],
          [-83.574857, 31.078135],
          [-83.573959, 31.033875],
          [-83.736158, 31.037679],
          [-84.003876, 31.041582],
          [-84.003627, 31.07729],
          [-83.99943, 31.334966]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13079",
      state: "13",
      county: "079",
      name: "Crawford County",
      censusArea: 324.888,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.70109, 32.691598],
          [-83.766787, 32.692622],
          [-84.003363, 32.529927],
          [-84.104966, 32.673385],
          [-84.202628, 32.690018],
          [-84.124275, 32.849562],
          [-83.891922, 32.848351],
          [-83.824398, 32.741861],
          [-83.701152, 32.692169],
          [-83.70109, 32.691598]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13091",
      state: "13",
      county: "091",
      name: "Dodge County",
      censusArea: 495.89,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.346528, 32.272489],
          [-83.30475, 32.341958],
          [-83.173252, 32.452533],
          [-83.138991, 32.423069],
          [-82.990967, 32.147274],
          [-82.884803, 32.196072],
          [-82.87179, 32.18273],
          [-82.927865, 32.135274],
          [-82.934566, 32.153274],
          [-82.955466, 32.135274],
          [-82.942066, 32.124074],
          [-83.205739, 31.900325],
          [-83.292061, 31.984506],
          [-83.337313, 32.105885],
          [-83.291106, 32.178825],
          [-83.346528, 32.272489]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13107",
      state: "13",
      county: "107",
      name: "Emanuel County",
      censusArea: 680.604,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.647733, 32.512507],
          [-82.594439, 32.579158],
          [-82.465065, 32.646833],
          [-82.499431, 32.68634],
          [-82.434149, 32.762258],
          [-82.316512, 32.835772],
          [-82.144977, 32.812741],
          [-82.08244, 32.663061],
          [-82.001236, 32.60691],
          [-82.026842, 32.555163],
          [-82.030233, 32.53877],
          [-82.148338, 32.520429],
          [-82.252757, 32.35331],
          [-82.232788, 32.318838],
          [-82.354546, 32.292194],
          [-82.409118, 32.35383],
          [-82.38194, 32.440596],
          [-82.488941, 32.49183],
          [-82.647733, 32.512507]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13113",
      state: "13",
      county: "113",
      name: "Fayette County",
      censusArea: 194.342,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.497527, 33.257422],
          [-84.62722, 33.440078],
          [-84.60954, 33.502511],
          [-84.458665, 33.550933],
          [-84.381759, 33.463414],
          [-84.388118, 33.352465],
          [-84.432907, 33.2565],
          [-84.497527, 33.257422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13123",
      state: "13",
      county: "123",
      name: "Gilmer County",
      censusArea: 426.54,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.654366, 34.548946],
          [-84.654523, 34.583187],
          [-84.657098, 34.728905],
          [-84.582306, 34.825141],
          [-84.618637, 34.855398],
          [-84.429093, 34.855036],
          [-84.314653, 34.807328],
          [-84.196754, 34.617924],
          [-84.255112, 34.568333],
          [-84.345553, 34.562735],
          [-84.654366, 34.548946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08053",
      state: "08",
      county: "053",
      name: "Hinsdale County",
      censusArea: 1117.251,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.482131, 37.422673],
          [-107.482179, 37.639501],
          [-107.468843, 37.809726],
          [-107.516547, 37.826451],
          [-107.568875, 37.965015],
          [-107.51088, 38.060877],
          [-107.56862, 38.147267],
          [-107.001119, 38.147072],
          [-107.000602, 37.956041],
          [-107.138191, 37.938848],
          [-107.145138, 37.675497],
          [-107.123868, 37.675029],
          [-107.12868, 37.422942],
          [-107.482131, 37.422673]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08057",
      state: "08",
      county: "057",
      name: "Jackson County",
      censusArea: 1613.723,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.321165, 40.999123],
          [-106.19055, 40.998056],
          [-106.185307, 40.933973],
          [-106.057715, 40.81712],
          [-105.854926, 40.486252],
          [-105.891754, 40.476661],
          [-105.959659, 40.348149],
          [-106.018622, 40.373295],
          [-106.230074, 40.323382],
          [-106.353758, 40.357536],
          [-106.44342, 40.346533],
          [-106.489842, 40.411578],
          [-106.612321, 40.379383],
          [-106.652112, 40.445231],
          [-106.706149, 40.616597],
          [-106.636922, 40.789489],
          [-106.654541, 40.849117],
          [-106.82503, 40.932119],
          [-106.852349, 40.925183],
          [-106.857772, 41.003082],
          [-106.453859, 41.002057],
          [-106.439563, 41.001978],
          [-106.437419, 41.001795],
          [-106.43095, 41.001752],
          [-106.391852, 41.001176],
          [-106.386356, 41.001144],
          [-106.321165, 40.999123]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08059",
      state: "08",
      county: "059",
      name: "Jefferson County",
      censusArea: 764.208,
      stateName: "Colorado"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-105.081564, 39.634728],
            [-105.081959, 39.63517],
            [-105.080907, 39.635605],
            [-105.080803, 39.634723],
            [-105.081564, 39.634728]
          ]
        ],
        [
          [
            [-105.092839, 39.618327],
            [-105.096909, 39.614423],
            [-105.059305, 39.614337],
            [-105.053439, 39.621423],
            [-105.05414, 39.566088],
            [-105.04874, 39.566088],
            [-105.260054, 39.210445],
            [-105.32922, 39.129689],
            [-105.330444, 39.129685],
            [-105.397875, 39.129564],
            [-105.398453, 39.363516],
            [-105.398949, 39.566056],
            [-105.397949, 39.747087],
            [-105.397934, 39.841137],
            [-105.39745, 39.853697],
            [-105.397949, 39.860387],
            [-105.397568, 39.866061],
            [-105.398043, 39.872186],
            [-105.397649, 39.883187],
            [-105.397849, 39.912886],
            [-105.147342, 39.913886],
            [-105.052884, 39.913795],
            [-105.053245, 39.860018],
            [-105.0529, 39.852042],
            [-105.05319, 39.849251],
            [-105.05327, 39.820028],
            [-105.053256, 39.806497],
            [-105.052829, 39.803874],
            [-105.053162, 39.799239],
            [-105.053246, 39.791063],
            [-105.055083, 39.791065],
            [-105.055272, 39.791065],
            [-105.055627, 39.791066],
            [-105.056162, 39.791067],
            [-105.05663, 39.791067],
            [-105.059338, 39.791072],
            [-105.059696, 39.791072],
            [-105.060417, 39.791073],
            [-105.060678, 39.791073],
            [-105.06148, 39.791075],
            [-105.061608, 39.790928],
            [-105.061722, 39.790582],
            [-105.061899, 39.790083],
            [-105.061917, 39.790083],
            [-105.062966, 39.790086],
            [-105.063059, 39.790084],
            [-105.063298, 39.790081],
            [-105.063301, 39.790445],
            [-105.063301, 39.790476],
            [-105.063302, 39.790494],
            [-105.063302, 39.790568],
            [-105.063303, 39.790737],
            [-105.063304, 39.790775],
            [-105.063306, 39.791077],
            [-105.063382, 39.791077],
            [-105.064161, 39.791079],
            [-105.064203, 39.791079],
            [-105.064243, 39.791079],
            [-105.064541, 39.79108],
            [-105.064542, 39.790989],
            [-105.064545, 39.790686],
            [-105.064552, 39.789999],
            [-105.064559, 39.789695],
            [-105.064777, 39.789691],
            [-105.065115, 39.78968],
            [-105.065123, 39.789269],
            [-105.065106, 39.788813],
            [-105.065083, 39.788086],
            [-105.062944, 39.788087],
            [-105.062865, 39.788011],
            [-105.062451, 39.787597],
            [-105.062546, 39.787425],
            [-105.062556, 39.787408],
            [-105.062653, 39.787193],
            [-105.062653, 39.787],
            [-105.062655, 39.786187],
            [-105.062655, 39.785822],
            [-105.062656, 39.785666],
            [-105.062659, 39.784112],
            [-105.062659, 39.784059],
            [-105.062659, 39.7839],
            [-105.062659, 39.783847],
            [-105.062425, 39.783847],
            [-105.062176, 39.783846],
            [-105.061848, 39.783845],
            [-105.061711, 39.783845],
            [-105.060765, 39.783842],
            [-105.060576, 39.783839],
            [-105.060346, 39.783836],
            [-105.060009, 39.783832],
            [-105.05982, 39.78383],
            [-105.059628, 39.78383],
            [-105.059054, 39.78383],
            [-105.058863, 39.78383],
            [-105.057912, 39.783831],
            [-105.057894, 39.78383],
            [-105.05675, 39.783822],
            [-105.056742, 39.783821],
            [-105.055588, 39.783818],
            [-105.054442, 39.783814],
            [-105.053183, 39.783797],
            [-105.053183, 39.783744],
            [-105.053184, 39.783687],
            [-105.053184, 39.783585],
            [-105.053184, 39.783533],
            [-105.053184, 39.783471],
            [-105.053185, 39.783435],
            [-105.053187, 39.783288],
            [-105.053189, 39.783228],
            [-105.05319, 39.783134],
            [-105.053196, 39.782852],
            [-105.053198, 39.782759],
            [-105.053211, 39.782588],
            [-105.053243, 39.78219],
            [-105.053243, 39.782075],
            [-105.053243, 39.781904],
            [-105.053243, 39.781861],
            [-105.053243, 39.781732],
            [-105.053243, 39.78169],
            [-105.053235, 39.781393],
            [-105.053214, 39.780502],
            [-105.053208, 39.780205],
            [-105.053214, 39.77984],
            [-105.053235, 39.778748],
            [-105.053242, 39.778384],
            [-105.053242, 39.778023],
            [-105.053244, 39.776943],
            [-105.053245, 39.776583],
            [-105.053244, 39.776219],
            [-105.053241, 39.775127],
            [-105.053241, 39.774763],
            [-105.053241, 39.774398],
            [-105.053241, 39.773303],
            [-105.053241, 39.772938],
            [-105.053245, 39.772577],
            [-105.05326, 39.771494],
            [-105.053265, 39.771134],
            [-105.053261, 39.770876],
            [-105.05325, 39.770102],
            [-105.053247, 39.769844],
            [-105.053245, 39.769741],
            [-105.053241, 39.769435],
            [-105.05324, 39.769333],
            [-105.05324, 39.768968],
            [-105.05324, 39.767876],
            [-105.053241, 39.767512],
            [-105.053241, 39.767409],
            [-105.053243, 39.767278],
            [-105.053242, 39.766622],
            [-105.05323, 39.765699],
            [-105.053232, 39.765521],
            [-105.05324, 39.764991],
            [-105.053243, 39.764814],
            [-105.053242, 39.764631],
            [-105.053241, 39.764085],
            [-105.053241, 39.763903],
            [-105.053241, 39.76372],
            [-105.053241, 39.763171],
            [-105.053241, 39.762989],
            [-105.053241, 39.762807],
            [-105.053243, 39.762265],
            [-105.053244, 39.762084],
            [-105.053243, 39.761725],
            [-105.053241, 39.760651],
            [-105.053241, 39.760293],
            [-105.05324, 39.759928],
            [-105.053239, 39.758836],
            [-105.053239, 39.758472],
            [-105.053238, 39.758165],
            [-105.053238, 39.757245],
            [-105.053238, 39.756939],
            [-105.053237, 39.755787],
            [-105.053236, 39.755553],
            [-105.053236, 39.754848],
            [-105.053231, 39.753046],
            [-105.05323, 39.752686],
            [-105.053227, 39.751608],
            [-105.053227, 39.751249],
            [-105.053224, 39.75089],
            [-105.053215, 39.749816],
            [-105.053213, 39.749458],
            [-105.053217, 39.748157],
            [-105.053219, 39.747687],
            [-105.053219, 39.747616],
            [-105.053226, 39.74568],
            [-105.053214, 39.745187],
            [-105.053212, 39.74398],
            [-105.053211, 39.743881],
            [-105.053211, 39.743584],
            [-105.053211, 39.743486],
            [-105.05321, 39.743339],
            [-105.053209, 39.742781],
            [-105.053209, 39.742697],
            [-105.053204, 39.742203],
            [-105.053199, 39.742204],
            [-105.053195, 39.741938],
            [-105.0532, 39.741935],
            [-105.053201, 39.741935],
            [-105.053206, 39.74036],
            [-105.053206, 39.739987],
            [-105.053206, 39.73887],
            [-105.053207, 39.738498],
            [-105.05321, 39.738152],
            [-105.053223, 39.737115],
            [-105.053227, 39.73677],
            [-105.053227, 39.736537],
            [-105.053227, 39.735841],
            [-105.053227, 39.735609],
            [-105.053227, 39.735544],
            [-105.053227, 39.735349],
            [-105.053227, 39.735284],
            [-105.053227, 39.735259],
            [-105.05323, 39.735185],
            [-105.053231, 39.735161],
            [-105.053228, 39.734965],
            [-105.055567, 39.734977],
            [-105.055586, 39.73474],
            [-105.055585, 39.734707],
            [-105.055574, 39.734508],
            [-105.055539, 39.733687],
            [-105.055539, 39.733569],
            [-105.055539, 39.733215],
            [-105.055539, 39.733098],
            [-105.055424, 39.733098],
            [-105.055082, 39.733099],
            [-105.054968, 39.7331],
            [-105.054969, 39.733217],
            [-105.054975, 39.733569],
            [-105.054977, 39.733687],
            [-105.054977, 39.733728],
            [-105.054979, 39.733854],
            [-105.05498, 39.733896],
            [-105.054903, 39.733897],
            [-105.054671, 39.7339],
            [-105.054595, 39.733902],
            [-105.054593, 39.73386],
            [-105.054587, 39.733734],
            [-105.054585, 39.733693],
            [-105.054499, 39.733706],
            [-105.054418, 39.73372],
            [-105.054411, 39.733541],
            [-105.054409, 39.733455],
            [-105.054406, 39.733384],
            [-105.0544, 39.733172],
            [-105.054398, 39.733102],
            [-105.05434, 39.733102],
            [-105.05417, 39.733102],
            [-105.054113, 39.733103],
            [-105.053935, 39.733104],
            [-105.053404, 39.733108],
            [-105.053227, 39.73311],
            [-105.053228, 39.732746],
            [-105.053235, 39.731657],
            [-105.053238, 39.731294],
            [-105.053239, 39.730942],
            [-105.053242, 39.72989],
            [-105.053244, 39.729539],
            [-105.053246, 39.728968],
            [-105.053251, 39.728522],
            [-105.053252, 39.727772],
            [-105.053254, 39.727349],
            [-105.053254, 39.727233],
            [-105.053252, 39.726184],
            [-105.053252, 39.726112],
            [-105.053253, 39.725951],
            [-105.053253, 39.725899],
            [-105.053253, 39.725828],
            [-105.053253, 39.7258],
            [-105.05324, 39.725441],
            [-105.053259, 39.724083],
            [-105.053263, 39.723133],
            [-105.053264, 39.722719],
            [-105.053268, 39.721477],
            [-105.05327, 39.721064],
            [-105.05327, 39.720933],
            [-105.05327, 39.720544],
            [-105.053271, 39.720414],
            [-105.053272, 39.720056],
            [-105.053275, 39.719071],
            [-105.053276, 39.718984],
            [-105.053278, 39.718627],
            [-105.053279, 39.718233],
            [-105.05327, 39.716647],
            [-105.05327, 39.71663],
            [-105.053271, 39.715004],
            [-105.053263, 39.713181],
            [-105.05325, 39.711343],
            [-105.053264, 39.7103],
            [-105.053262, 39.710161],
            [-105.053258, 39.709746],
            [-105.053257, 39.709608],
            [-105.053257, 39.709416],
            [-105.053259, 39.708841],
            [-105.05326, 39.70865],
            [-105.053258, 39.708463],
            [-105.053253, 39.707902],
            [-105.053252, 39.707716],
            [-105.053251, 39.70755],
            [-105.05325, 39.707054],
            [-105.05325, 39.706889],
            [-105.053249, 39.706674],
            [-105.053246, 39.706029],
            [-105.053246, 39.705921],
            [-105.053246, 39.705814],
            [-105.053258, 39.70505],
            [-105.053252, 39.704542],
            [-105.053247, 39.703985],
            [-105.053245, 39.703268],
            [-105.053245, 39.703126],
            [-105.053247, 39.702703],
            [-105.053249, 39.702562],
            [-105.053245, 39.702269],
            [-105.053245, 39.700438],
            [-105.053241, 39.69991],
            [-105.053236, 39.699078],
            [-105.053225, 39.696889],
            [-105.053225, 39.69682],
            [-105.053225, 39.696809],
            [-105.053225, 39.696779],
            [-105.053225, 39.696769],
            [-105.053223, 39.696412],
            [-105.053219, 39.695344],
            [-105.053218, 39.694988],
            [-105.053213, 39.694625],
            [-105.053198, 39.693538],
            [-105.053194, 39.693176],
            [-105.053196, 39.693103],
            [-105.053202, 39.692885],
            [-105.053205, 39.692813],
            [-105.053207, 39.692745],
            [-105.053212, 39.692542],
            [-105.053215, 39.692475],
            [-105.05321, 39.692252],
            [-105.053198, 39.691586],
            [-105.053194, 39.691364],
            [-105.053208, 39.691002],
            [-105.053251, 39.689919],
            [-105.053266, 39.689559],
            [-105.053265, 39.689194],
            [-105.053264, 39.6881],
            [-105.053264, 39.687883],
            [-105.053254, 39.687736],
            [-105.053248, 39.687654],
            [-105.053212, 39.68739],
            [-105.053208, 39.687353],
            [-105.053193, 39.686873],
            [-105.053193, 39.686347],
            [-105.053194, 39.685999],
            [-105.053194, 39.685911],
            [-105.053196, 39.68565],
            [-105.053197, 39.685563],
            [-105.053195, 39.68438],
            [-105.053183, 39.683528],
            [-105.053183, 39.683498],
            [-105.053191, 39.682397],
            [-105.053221, 39.682291],
            [-105.055588, 39.68226],
            [-105.056518, 39.682265],
            [-105.0572, 39.682271],
            [-105.057265, 39.682272],
            [-105.057783, 39.682272],
            [-105.057912, 39.682276],
            [-105.057913, 39.682277],
            [-105.058033, 39.682275],
            [-105.058034, 39.682275],
            [-105.058044, 39.68227],
            [-105.058054, 39.681825],
            [-105.058049, 39.681817],
            [-105.05806, 39.680649],
            [-105.058175, 39.678666],
            [-105.058505, 39.678615],
            [-105.060732, 39.678279],
            [-105.062086, 39.679063],
            [-105.062442, 39.67919],
            [-105.06258, 39.679293],
            [-105.062777, 39.679441],
            [-105.063177, 39.679681],
            [-105.062991, 39.679363],
            [-105.062929, 39.679259],
            [-105.062907, 39.679223],
            [-105.062835, 39.678446],
            [-105.062772, 39.677608],
            [-105.062887, 39.676224],
            [-105.062891, 39.675853],
            [-105.062894, 39.675465],
            [-105.062894, 39.675369],
            [-105.062897, 39.675082],
            [-105.062898, 39.674987],
            [-105.062734, 39.674985],
            [-105.062638, 39.674985],
            [-105.062245, 39.674973],
            [-105.062082, 39.674969],
            [-105.061573, 39.674952],
            [-105.060047, 39.674903],
            [-105.059539, 39.674887],
            [-105.059393, 39.674887],
            [-105.058955, 39.674887],
            [-105.058809, 39.674887],
            [-105.0585, 39.674887],
            [-105.057573, 39.674888],
            [-105.057265, 39.674889],
            [-105.056462, 39.674909],
            [-105.054053, 39.674969],
            [-105.05325, 39.674989],
            [-105.053246, 39.674851],
            [-105.053237, 39.674437],
            [-105.053234, 39.674299],
            [-105.053239, 39.674095],
            [-105.053247, 39.673778],
            [-105.053255, 39.673483],
            [-105.053261, 39.67328],
            [-105.053261, 39.673271],
            [-105.053251, 39.673116],
            [-105.053262, 39.672255],
            [-105.053262, 39.671949],
            [-105.053262, 39.671935],
            [-105.053262, 39.671896],
            [-105.053262, 39.671883],
            [-105.05326, 39.671063],
            [-105.053257, 39.669076],
            [-105.053257, 39.668604],
            [-105.053256, 39.667785],
            [-105.053388, 39.667785],
            [-105.053784, 39.667785],
            [-105.053916, 39.667785],
            [-105.054106, 39.667785],
            [-105.054676, 39.667785],
            [-105.054866, 39.667785],
            [-105.055072, 39.667784],
            [-105.055691, 39.667781],
            [-105.055898, 39.667781],
            [-105.056104, 39.667781],
            [-105.056725, 39.667784],
            [-105.056932, 39.667785],
            [-105.05714, 39.667784],
            [-105.057765, 39.667784],
            [-105.057974, 39.667784],
            [-105.058185, 39.667782],
            [-105.058821, 39.667778],
            [-105.059033, 39.667778],
            [-105.059248, 39.667778],
            [-105.059894, 39.667779],
            [-105.06011, 39.66778],
            [-105.060252, 39.66778],
            [-105.06068, 39.66778],
            [-105.060823, 39.66778],
            [-105.060844, 39.66778],
            [-105.06125, 39.66778],
            [-105.062534, 39.667782],
            [-105.062962, 39.667783],
            [-105.063161, 39.667782],
            [-105.063759, 39.66778],
            [-105.063959, 39.66778],
            [-105.063976, 39.66778],
            [-105.064027, 39.66778],
            [-105.064044, 39.66778],
            [-105.064257, 39.667779],
            [-105.064897, 39.667778],
            [-105.065111, 39.667778],
            [-105.065304, 39.667777],
            [-105.065885, 39.667777],
            [-105.066079, 39.667777],
            [-105.066097, 39.667777],
            [-105.066151, 39.667777],
            [-105.06617, 39.667777],
            [-105.066247, 39.667776],
            [-105.06648, 39.667776],
            [-105.066558, 39.667776],
            [-105.066705, 39.667775],
            [-105.067149, 39.667775],
            [-105.067297, 39.667775],
            [-105.067538, 39.667774],
            [-105.068263, 39.667771],
            [-105.068505, 39.667771],
            [-105.06908, 39.667769],
            [-105.070805, 39.667765],
            [-105.071381, 39.667764],
            [-105.071406, 39.667764],
            [-105.07145, 39.667764],
            [-105.071483, 39.667764],
            [-105.071509, 39.667764],
            [-105.071643, 39.667763],
            [-105.072044, 39.667763],
            [-105.072179, 39.667763],
            [-105.072183, 39.668093],
            [-105.072186, 39.66828],
            [-105.072197, 39.669058],
            [-105.072197, 39.669083],
            [-105.072202, 39.669414],
            [-105.072202, 39.669435],
            [-105.072203, 39.669499],
            [-105.072204, 39.669521],
            [-105.072371, 39.669594],
            [-105.072872, 39.669813],
            [-105.07304, 39.669887],
            [-105.0731, 39.669912],
            [-105.073253, 39.669976],
            [-105.07328, 39.669987],
            [-105.07334, 39.670013],
            [-105.07494, 39.670687],
            [-105.07614, 39.669987],
            [-105.076039, 39.669587],
            [-105.076939, 39.669686],
            [-105.076939, 39.669523],
            [-105.076939, 39.66952],
            [-105.076938, 39.66932],
            [-105.0775, 39.66933],
            [-105.079164, 39.669358],
            [-105.079175, 39.669601],
            [-105.079183, 39.669738],
            [-105.079212, 39.669738],
            [-105.079299, 39.669738],
            [-105.079329, 39.669739],
            [-105.079345, 39.66974],
            [-105.079651, 39.669755],
            [-105.079739, 39.669765],
            [-105.079876, 39.669782],
            [-105.080157, 39.669826],
            [-105.080528, 39.669902],
            [-105.080948, 39.67001],
            [-105.081192, 39.670074],
            [-105.08135, 39.670099],
            [-105.081389, 39.669628],
            [-105.081434, 39.669103],
            [-105.081457, 39.668577],
            [-105.081454, 39.668215],
            [-105.081451, 39.667743],
            [-105.081455, 39.665551],
            [-105.081455, 39.665498],
            [-105.081449, 39.665055],
            [-105.081454, 39.664987],
            [-105.081454, 39.662896],
            [-105.081454, 39.662074],
            [-105.079822, 39.662164],
            [-105.079822, 39.66216],
            [-105.079771, 39.662163],
            [-105.079733, 39.662061],
            [-105.079534, 39.661814],
            [-105.079086, 39.661526],
            [-105.079071, 39.661521],
            [-105.07907, 39.661521],
            [-105.077863, 39.661004],
            [-105.077338, 39.660389],
            [-105.077342, 39.660374],
            [-105.077479, 39.659644],
            [-105.077642, 39.658779],
            [-105.076255, 39.659096],
            [-105.075423, 39.659286],
            [-105.074427, 39.659515],
            [-105.07324, 39.659787],
            [-105.071617, 39.659995],
            [-105.071582, 39.659853],
            [-105.071479, 39.65943],
            [-105.071445, 39.659289],
            [-105.071314, 39.659265],
            [-105.070923, 39.659193],
            [-105.070793, 39.65917],
            [-105.070768, 39.65822],
            [-105.070727, 39.656594],
            [-105.069505, 39.656592],
            [-105.069183, 39.656591],
            [-105.068556, 39.656591],
            [-105.067045, 39.656587],
            [-105.066956, 39.654029],
            [-105.066941, 39.653594],
            [-105.06697, 39.653494],
            [-105.067026, 39.653327],
            [-105.067057, 39.653233],
            [-105.067089, 39.65314],
            [-105.067137, 39.652996],
            [-105.06714, 39.652987],
            [-105.067144, 39.652981],
            [-105.067283, 39.652785],
            [-105.067307, 39.652752],
            [-105.067481, 39.652641],
            [-105.067681, 39.652589],
            [-105.067921, 39.652471],
            [-105.067979, 39.652444],
            [-105.068137, 39.652364],
            [-105.06884, 39.651587],
            [-105.07014, 39.650386],
            [-105.070352, 39.650413],
            [-105.07094, 39.650486],
            [-105.070953, 39.650411],
            [-105.07104, 39.649887],
            [-105.071034, 39.649731],
            [-105.071029, 39.649579],
            [-105.063924, 39.649566],
            [-105.063926, 39.64962],
            [-105.063939, 39.649887],
            [-105.063938, 39.650486],
            [-105.06261, 39.650598],
            [-105.062606, 39.65107],
            [-105.062606, 39.651077],
            [-105.062586, 39.651859],
            [-105.062586, 39.651899],
            [-105.062133, 39.651791],
            [-105.062137, 39.651384],
            [-105.061555, 39.651385],
            [-105.060692, 39.651385],
            [-105.060648, 39.651385],
            [-105.060321, 39.651385],
            [-105.059977, 39.651386],
            [-105.059209, 39.651386],
            [-105.058839, 39.651387],
            [-105.058939, 39.651687],
            [-105.058439, 39.651687],
            [-105.058465, 39.652008],
            [-105.058527, 39.652781],
            [-105.058543, 39.652979],
            [-105.058399, 39.652982],
            [-105.058311, 39.652979],
            [-105.058031, 39.652959],
            [-105.057873, 39.652948],
            [-105.05782, 39.652948],
            [-105.057215, 39.652946],
            [-105.05724, 39.652759],
            [-105.057319, 39.652201],
            [-105.057336, 39.652085],
            [-105.057266, 39.652086],
            [-105.055136, 39.652115],
            [-105.055061, 39.652016],
            [-105.055042, 39.652003],
            [-105.054882, 39.651895],
            [-105.054703, 39.651812],
            [-105.054654, 39.651798],
            [-105.054513, 39.65176],
            [-105.055521, 39.651737],
            [-105.057257, 39.651723],
            [-105.057345, 39.649984],
            [-105.061452, 39.650063],
            [-105.061578, 39.650066],
            [-105.061679, 39.650068],
            [-105.062614, 39.650086],
            [-105.063399, 39.65008],
            [-105.063422, 39.649619],
            [-105.063425, 39.649532],
            [-105.063435, 39.649273],
            [-105.063439, 39.649187],
            [-105.063454, 39.648699],
            [-105.063503, 39.647236],
            [-105.06352, 39.646749],
            [-105.063522, 39.646672],
            [-105.063532, 39.646404],
            [-105.063539, 39.646188],
            [-105.063539, 39.645988],
            [-105.06894, 39.646087],
            [-105.072042, 39.64609],
            [-105.072044, 39.646033],
            [-105.072049, 39.645815],
            [-105.072053, 39.645789],
            [-105.07206, 39.645751],
            [-105.072093, 39.645647],
            [-105.072133, 39.645574],
            [-105.072189, 39.645493],
            [-105.072326, 39.64535],
            [-105.072434, 39.645271],
            [-105.072523, 39.64522],
            [-105.072902, 39.645038],
            [-105.073285, 39.644872],
            [-105.073665, 39.644709],
            [-105.073816, 39.644628],
            [-105.073998, 39.644513],
            [-105.074145, 39.644403],
            [-105.074292, 39.644274],
            [-105.074454, 39.644104],
            [-105.074563, 39.643945],
            [-105.07469, 39.643727],
            [-105.07486, 39.64346],
            [-105.075363, 39.642709],
            [-105.075393, 39.642666],
            [-105.075679, 39.642203],
            [-105.07576, 39.64205],
            [-105.075789, 39.641968],
            [-105.075819, 39.641842],
            [-105.075837, 39.641689],
            [-105.075857, 39.641359],
            [-105.075872, 39.641277],
            [-105.075897, 39.641182],
            [-105.075897, 39.64118],
            [-105.075946, 39.641058],
            [-105.07601, 39.640913],
            [-105.076098, 39.640739],
            [-105.076546, 39.639906],
            [-105.076653, 39.639713],
            [-105.076722, 39.639549],
            [-105.076773, 39.639353],
            [-105.076797, 39.639153],
            [-105.076807, 39.638967],
            [-105.076803, 39.638735],
            [-105.076313, 39.638737],
            [-105.074866, 39.638745],
            [-105.074846, 39.638744],
            [-105.074358, 39.638742],
            [-105.073985, 39.638739],
            [-105.073951, 39.636831],
            [-105.07724, 39.636593],
            [-105.078087, 39.636644],
            [-105.080629, 39.6368],
            [-105.081507, 39.636377],
            [-105.081657, 39.636302],
            [-105.081716, 39.636867],
            [-105.081706, 39.636924],
            [-105.081702, 39.63695],
            [-105.081702, 39.636953],
            [-105.081693, 39.637031],
            [-105.08169, 39.637059],
            [-105.081688, 39.637076],
            [-105.081683, 39.637127],
            [-105.081682, 39.637145],
            [-105.081646, 39.637475],
            [-105.081646, 39.637479],
            [-105.081589, 39.638469],
            [-105.081578, 39.638667],
            [-105.081676, 39.638759],
            [-105.082223, 39.638745],
            [-105.083866, 39.638705],
            [-105.084414, 39.638693],
            [-105.084512, 39.638693],
            [-105.084809, 39.638694],
            [-105.084908, 39.638695],
            [-105.085402, 39.638698],
            [-105.08622, 39.638703],
            [-105.086885, 39.638696],
            [-105.08738, 39.638692],
            [-105.087423, 39.638692],
            [-105.087553, 39.638693],
            [-105.087597, 39.638694],
            [-105.087804, 39.638695],
            [-105.087853, 39.638695],
            [-105.087975, 39.638696],
            [-105.088094, 39.638696],
            [-105.088349, 39.638698],
            [-105.08849, 39.6387],
            [-105.088969, 39.638703],
            [-105.091023, 39.638718],
            [-105.0911, 39.638719],
            [-105.091258, 39.638722],
            [-105.091577, 39.638729],
            [-105.091593, 39.638086],
            [-105.091598, 39.637574],
            [-105.0916, 39.637331],
            [-105.091609, 39.636546],
            [-105.091617, 39.635458],
            [-105.091582, 39.635178],
            [-105.093134, 39.635158],
            [-105.095462, 39.635121],
            [-105.095546, 39.635121],
            [-105.097052, 39.635108],
            [-105.098527, 39.635075],
            [-105.099019, 39.635037],
            [-105.099641, 39.634987],
            [-105.099708, 39.635011],
            [-105.099958, 39.635038],
            [-105.100541, 39.635136],
            [-105.100611, 39.635148],
            [-105.100841, 39.635187],
            [-105.101427, 39.635187],
            [-105.109814, 39.635188],
            [-105.109874, 39.635186],
            [-105.109875, 39.635072],
            [-105.109875, 39.63507],
            [-105.109875, 39.635069],
            [-105.109887, 39.634485],
            [-105.109901, 39.634272],
            [-105.109902, 39.634263],
            [-105.109901, 39.63404],
            [-105.109893, 39.632676],
            [-105.109893, 39.632105],
            [-105.109889, 39.631412],
            [-105.109883, 39.629897],
            [-105.109885, 39.629741],
            [-105.109922, 39.627345],
            [-105.109925, 39.6271],
            [-105.109926, 39.62708],
            [-105.109927, 39.626988],
            [-105.109343, 39.626989],
            [-105.109192, 39.626988],
            [-105.10874, 39.626987],
            [-105.10859, 39.626987],
            [-105.108597, 39.624914],
            [-105.108599, 39.624262],
            [-105.1086, 39.624208],
            [-105.108377, 39.624205],
            [-105.108376, 39.624205],
            [-105.108374, 39.624205],
            [-105.107942, 39.624208],
            [-105.107924, 39.624208],
            [-105.107894, 39.624208],
            [-105.107537, 39.624218],
            [-105.107496, 39.624218],
            [-105.106931, 39.624218],
            [-105.105741, 39.62422],
            [-105.105458, 39.62422],
            [-105.104612, 39.624221],
            [-105.10433, 39.624222],
            [-105.104002, 39.624222],
            [-105.10302, 39.624223],
            [-105.102693, 39.624224],
            [-105.10265, 39.624224],
            [-105.102524, 39.624224],
            [-105.102482, 39.624224],
            [-105.102454, 39.624224],
            [-105.102373, 39.624224],
            [-105.102346, 39.624224],
            [-105.102291, 39.624224],
            [-105.102129, 39.624224],
            [-105.102075, 39.624224],
            [-105.101848, 39.624223],
            [-105.10117, 39.624223],
            [-105.100944, 39.624223],
            [-105.100642, 39.624222],
            [-105.099738, 39.624222],
            [-105.099437, 39.624222],
            [-105.099252, 39.624222],
            [-105.0987, 39.624222],
            [-105.098516, 39.624222],
            [-105.098301, 39.62422],
            [-105.097656, 39.624216],
            [-105.097441, 39.624215],
            [-105.097144, 39.624213],
            [-105.096253, 39.624207],
            [-105.095956, 39.624205],
            [-105.095694, 39.624203],
            [-105.095563, 39.624202],
            [-105.09491, 39.624197],
            [-105.094649, 39.624196],
            [-105.094598, 39.624195],
            [-105.094461, 39.624195],
            [-105.093228, 39.624196],
            [-105.093213, 39.624197],
            [-105.092998, 39.624197],
            [-105.091256, 39.624198],
            [-105.091253, 39.624341],
            [-105.091243, 39.624593],
            [-105.091216, 39.624983],
            [-105.091214, 39.625898],
            [-105.09121, 39.627291],
            [-105.090745, 39.627288],
            [-105.089988, 39.627286],
            [-105.089902, 39.627287],
            [-105.089654, 39.627287],
            [-105.089354, 39.627286],
            [-105.08869, 39.627287],
            [-105.08834, 39.627287],
            [-105.087894, 39.627308],
            [-105.087666, 39.62732],
            [-105.087662, 39.627143],
            [-105.087647, 39.626463],
            [-105.087395, 39.626463],
            [-105.086949, 39.626465],
            [-105.086823, 39.626465],
            [-105.086449, 39.626467],
            [-105.086324, 39.626468],
            [-105.085608, 39.626465],
            [-105.0855, 39.626465],
            [-105.085508, 39.627488],
            [-105.085496, 39.628501],
            [-105.085489, 39.629217],
            [-105.084999, 39.629237],
            [-105.083939, 39.629282],
            [-105.083894, 39.628877],
            [-105.08384, 39.628391],
            [-105.083881, 39.627799],
            [-105.083897, 39.627582],
            [-105.082334, 39.627571],
            [-105.081741, 39.627568],
            [-105.08174, 39.627579],
            [-105.08174, 39.627612],
            [-105.08174, 39.627624],
            [-105.081738, 39.627687],
            [-105.081735, 39.627879],
            [-105.081735, 39.627943],
            [-105.081733, 39.627999],
            [-105.08173, 39.628168],
            [-105.08173, 39.628225],
            [-105.081701, 39.629738],
            [-105.081695, 39.630037],
            [-105.081693, 39.630115],
            [-105.081693, 39.630149],
            [-105.081856, 39.63015],
            [-105.081936, 39.63015],
            [-105.082176, 39.630152],
            [-105.082256, 39.630153],
            [-105.08251, 39.630153],
            [-105.083274, 39.630154],
            [-105.083529, 39.630155],
            [-105.083535, 39.630848],
            [-105.083546, 39.631387],
            [-105.083556, 39.631872],
            [-105.081779, 39.631873],
            [-105.081685, 39.631872],
            [-105.081639, 39.631873],
            [-105.081514, 39.631863],
            [-105.08145, 39.631858],
            [-105.080309, 39.630486],
            [-105.08024, 39.630402],
            [-105.07624, 39.625587],
            [-105.07594, 39.625274],
            [-105.075132, 39.62451],
            [-105.076072, 39.624426],
            [-105.076594, 39.624378],
            [-105.076578, 39.624365],
            [-105.075952, 39.624143],
            [-105.066141, 39.620731],
            [-105.065746, 39.620588],
            [-105.065739, 39.620586],
            [-105.064966, 39.620238],
            [-105.065039, 39.619842],
            [-105.06505, 39.619779],
            [-105.065086, 39.619588],
            [-105.065115, 39.61943],
            [-105.065141, 39.618585],
            [-105.06542, 39.618583],
            [-105.065467, 39.618588],
            [-105.065492, 39.618588],
            [-105.065519, 39.618592],
            [-105.065572, 39.618592],
            [-105.065638, 39.61859],
            [-105.066206, 39.618575],
            [-105.066937, 39.618585],
            [-105.06788, 39.61859],
            [-105.068107, 39.618589],
            [-105.069229, 39.618571],
            [-105.072508, 39.618549],
            [-105.072611, 39.618573],
            [-105.072626, 39.618576],
            [-105.072638, 39.618577],
            [-105.072655, 39.618577],
            [-105.072673, 39.618576],
            [-105.072687, 39.618573],
            [-105.072703, 39.618568],
            [-105.072746, 39.618552],
            [-105.072763, 39.618548],
            [-105.072783, 39.618546],
            [-105.072799, 39.618546],
            [-105.072819, 39.618549],
            [-105.072844, 39.618555],
            [-105.073332, 39.618559],
            [-105.073364, 39.618559],
            [-105.074518, 39.61852],
            [-105.074796, 39.618511],
            [-105.075873, 39.618508],
            [-105.077553, 39.618485],
            [-105.079398, 39.618473],
            [-105.082476, 39.618504],
            [-105.082643, 39.618574],
            [-105.082773, 39.618573],
            [-105.082983, 39.618571],
            [-105.083616, 39.618569],
            [-105.083827, 39.618569],
            [-105.084043, 39.618568],
            [-105.084221, 39.618568],
            [-105.084573, 39.618526],
            [-105.084839, 39.618411],
            [-105.085262, 39.618283],
            [-105.085928, 39.618668],
            [-105.086115, 39.618979],
            [-105.086314, 39.618808],
            [-105.086553, 39.61858],
            [-105.08668, 39.618478],
            [-105.08682, 39.618412],
            [-105.087031, 39.618339],
            [-105.087252, 39.618317],
            [-105.087826, 39.618319],
            [-105.090115, 39.618312],
            [-105.091123, 39.618331],
            [-105.091362, 39.618336],
            [-105.091657, 39.618333],
            [-105.092543, 39.618328],
            [-105.092839, 39.618327]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08073",
      state: "08",
      county: "073",
      name: "Lincoln County",
      censusArea: 2577.626,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.504666, 38.516409],
          [-104.053921, 38.522393],
          [-104.055528, 38.868868],
          [-103.719664, 38.866827],
          [-103.722007, 39.084085],
          [-103.715254, 39.128246],
          [-103.714355, 39.566331],
          [-103.707072, 39.566295],
          [-103.154955, 39.565949],
          [-103.154376, 39.565654],
          [-103.163025, 39.03761],
          [-103.172943, 38.61245],
          [-103.172878, 38.525315],
          [-103.504666, 38.516409]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08083",
      state: "08",
      county: "083",
      name: "Montezuma County",
      censusArea: 2029.527,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.04581, 37.374993],
          [-109.043053, 37.485161],
          [-108.917081, 37.631902],
          [-107.970086, 37.639587],
          [-108.023006, 37.590999],
          [-108.038409, 37.451931],
          [-108.197197, 37.355475],
          [-108.379203, 36.999459],
          [-108.619689, 36.999249],
          [-108.620309, 36.999287],
          [-108.954404, 36.998906],
          [-108.958868, 36.998913],
          [-109.045223, 36.999084],
          [-109.045166, 37.072742],
          [-109.045058, 37.074661],
          [-109.044995, 37.086429],
          [-109.045189, 37.096271],
          [-109.045173, 37.109464],
          [-109.045203, 37.111958],
          [-109.045156, 37.112064],
          [-109.04581, 37.374993]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08095",
      state: "08",
      county: "095",
      name: "Phillips County",
      censusArea: 687.928,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.051398, 40.697542],
          [-102.051725, 40.537839],
          [-102.051519, 40.520094],
          [-102.051465, 40.440008],
          [-102.664804, 40.438473],
          [-102.65131, 40.749402],
          [-102.051292, 40.749586],
          [-102.051398, 40.697542]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08105",
      state: "08",
      county: "105",
      name: "Rio Grande County",
      censusArea: 911.958,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.710775, 37.404228],
          [-106.711624, 37.664273],
          [-106.693845, 37.835244],
          [-106.587139, 37.834426],
          [-106.586374, 37.747809],
          [-106.038413, 37.748353],
          [-106.039331, 37.400852],
          [-106.678373, 37.403596],
          [-106.710775, 37.404228]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08111",
      state: "08",
      county: "111",
      name: "San Juan County",
      censusArea: 387.488,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.738283, 37.905432],
          [-107.568875, 37.965015],
          [-107.516547, 37.826451],
          [-107.468843, 37.809726],
          [-107.482179, 37.639501],
          [-107.970086, 37.639587],
          [-107.968875, 37.696485],
          [-107.860845, 37.775517],
          [-107.738283, 37.905432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08123",
      state: "08",
      county: "123",
      name: "Weld County",
      censusArea: 3987.238,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.882452, 40.998186],
          [-104.497149, 41.001828],
          [-104.497058, 41.001805],
          [-104.214692, 41.001657],
          [-104.214191, 41.001568],
          [-104.211473, 41.001591],
          [-104.123586, 41.001626],
          [-104.10459, 41.001543],
          [-104.086068, 41.001563],
          [-104.066961, 41.001504],
          [-104.053249, 41.001406],
          [-104.039238, 41.001502],
          [-104.023383, 41.001887],
          [-104.018223, 41.001617],
          [-103.972642, 41.001615],
          [-103.971373, 41.001524],
          [-103.953525, 41.001596],
          [-103.896207, 41.00175],
          [-103.877967, 41.001673],
          [-103.858449, 41.001681],
          [-103.574522, 41.001721],
          [-103.581795, 40.52335],
          [-104.147901, 40.524348],
          [-104.147536, 40.350245],
          [-104.150494, 40.350245],
          [-104.150332, 40.000856],
          [-104.922153, 40.00033],
          [-104.923689, 40.000327],
          [-104.961408, 40.000337],
          [-104.961413, 40.000939],
          [-104.961427, 40.001777],
          [-104.961427, 40.002654],
          [-104.961426, 40.005019],
          [-104.961426, 40.006441],
          [-104.961416, 40.00761],
          [-104.961415, 40.009766],
          [-104.961411, 40.010341],
          [-104.961398, 40.012227],
          [-104.961402, 40.012901],
          [-104.961399, 40.013205],
          [-104.961391, 40.013431],
          [-104.961375, 40.013511],
          [-104.961344, 40.013606],
          [-104.961296, 40.013687],
          [-104.961265, 40.013729],
          [-104.961291, 40.014052],
          [-104.961291, 40.014055],
          [-104.9613, 40.014321],
          [-104.961306, 40.014533],
          [-104.961312, 40.014644],
          [-104.961313, 40.014677],
          [-104.961315, 40.01477],
          [-104.96132, 40.014982],
          [-104.961325, 40.015151],
          [-104.96136, 40.01523],
          [-104.961373, 40.015292],
          [-104.961385, 40.021967],
          [-104.961388, 40.023611],
          [-104.961389, 40.023669],
          [-104.961404, 40.024959],
          [-104.961401, 40.026273],
          [-104.961393, 40.026667],
          [-104.961394, 40.028348],
          [-104.961401, 40.029459],
          [-104.961392, 40.030224],
          [-104.961375, 40.033481],
          [-104.961358, 40.034687],
          [-104.961342, 40.035757],
          [-104.961296, 40.037638],
          [-104.961249, 40.039093],
          [-104.961233, 40.039603],
          [-104.961207, 40.040774],
          [-104.961195, 40.041298],
          [-104.961151, 40.043651],
          [-104.961156, 40.043999],
          [-104.961158, 40.044227],
          [-104.964109, 40.044169],
          [-104.966526, 40.044123],
          [-104.967371, 40.044109],
          [-104.968129, 40.044096],
          [-104.969589, 40.044089],
          [-104.970727, 40.044069],
          [-104.971161, 40.044061],
          [-104.972908, 40.04404],
          [-104.973448, 40.044039],
          [-104.973879, 40.044033],
          [-104.976435, 40.043994],
          [-104.976919, 40.043987],
          [-104.979344, 40.043951],
          [-104.979427, 40.043679],
          [-104.979914, 40.042353],
          [-104.979967, 40.04216],
          [-104.980001, 40.041976],
          [-104.980028, 40.041676],
          [-104.980037, 40.041206],
          [-104.980037, 40.041142],
          [-104.980041, 40.040316],
          [-104.980044, 40.03956],
          [-104.980046, 40.039173],
          [-104.980047, 40.038969],
          [-104.980048, 40.038342],
          [-104.98005, 40.037516],
          [-104.980051, 40.036828],
          [-104.980054, 40.035179],
          [-104.980054, 40.035136],
          [-104.980054, 40.035048],
          [-104.980058, 40.032882],
          [-104.980058, 40.032708],
          [-104.980059, 40.03242],
          [-104.980072, 40.031203],
          [-104.980071, 40.03042],
          [-104.980051, 40.029704],
          [-104.980049, 40.029647],
          [-104.980048, 40.029411],
          [-104.980196, 40.029409],
          [-104.98033, 40.029407],
          [-104.980629, 40.029406],
          [-104.980653, 40.029406],
          [-104.980994, 40.029409],
          [-104.981995, 40.029412],
          [-104.98542, 40.029407],
          [-104.986048, 40.029406],
          [-104.986503, 40.029405],
          [-104.986482, 40.028536],
          [-104.986523, 40.028536],
          [-104.986513, 40.026003],
          [-104.989739, 40.025984],
          [-104.991244, 40.025975],
          [-104.991419, 40.025974],
          [-104.991594, 40.025973],
          [-104.994316, 40.025958],
          [-104.994559, 40.025956],
          [-104.994802, 40.025954],
          [-104.994836, 40.025954],
          [-104.999122, 40.025928],
          [-104.999117, 40.025288],
          [-104.999117, 40.023998],
          [-104.999116, 40.023657],
          [-104.999116, 40.023509],
          [-104.997537, 40.02351],
          [-104.997354, 40.023511],
          [-104.996742, 40.023509],
          [-104.996753, 40.022423],
          [-104.996769, 40.02095],
          [-104.997391, 40.020936],
          [-104.997428, 40.020946],
          [-104.997427, 40.020935],
          [-104.998587, 40.020911],
          [-104.998729, 40.021001],
          [-104.999109, 40.021002],
          [-104.99911, 40.020633],
          [-104.999047, 40.020627],
          [-104.997816, 40.020631],
          [-104.997415, 40.020633],
          [-104.997391, 40.020625],
          [-104.997312, 40.020601],
          [-104.997284, 40.0206],
          [-104.997168, 40.020598],
          [-104.996627, 40.020585],
          [-104.996248, 40.02064],
          [-104.995984, 40.020624],
          [-104.995826, 40.020574],
          [-104.995683, 40.020437],
          [-104.995633, 40.020195],
          [-104.995719, 40.020052],
          [-104.995819, 40.019926],
          [-104.995897, 40.019761],
          [-104.995983, 40.019553],
          [-104.996118, 40.019366],
          [-104.996225, 40.019157],
          [-104.996404, 40.018981],
          [-104.996611, 40.018745],
          [-104.996718, 40.01864],
          [-104.996868, 40.018393],
          [-104.996989, 40.018283],
          [-104.997389, 40.017986],
          [-104.997603, 40.017832],
          [-104.997746, 40.017668],
          [-104.997788, 40.017333],
          [-104.997874, 40.017107],
          [-104.997888, 40.016943],
          [-104.998016, 40.016789],
          [-104.998452, 40.016519],
          [-104.998566, 40.016393],
          [-104.998787, 40.016129],
          [-104.99899, 40.016004],
          [-104.999001, 40.015997],
          [-104.999073, 40.015939],
          [-104.999073, 40.015907],
          [-104.999065, 40.014887],
          [-104.997215, 40.014887],
          [-104.992735, 40.014899],
          [-104.989614, 40.01489],
          [-104.989645, 40.007971],
          [-104.989647, 40.007679],
          [-104.989727, 40.00768],
          [-104.989819, 40.007681],
          [-104.992009, 40.007666],
          [-104.994335, 40.007655],
          [-104.995175, 40.007656],
          [-104.995974, 40.007651],
          [-104.996484, 40.007654],
          [-104.99649, 40.007654],
          [-104.996497, 40.007654],
          [-104.996506, 40.007654],
          [-104.996517, 40.007654],
          [-104.996532, 40.007654],
          [-104.996551, 40.007654],
          [-104.996576, 40.007654],
          [-104.996609, 40.007654],
          [-104.996653, 40.007654],
          [-104.996716, 40.007654],
          [-104.996779, 40.007654],
          [-104.996801, 40.007654],
          [-104.99681, 40.007654],
          [-104.996822, 40.007654],
          [-104.996841, 40.007654],
          [-104.996849, 40.007653],
          [-104.996858, 40.007653],
          [-104.996879, 40.007653],
          [-104.996885, 40.007653],
          [-104.996945, 40.007653],
          [-104.998486, 40.007646],
          [-104.998519, 40.007646],
          [-104.998528, 40.007646],
          [-104.999082, 40.007648],
          [-105.007999, 40.007455],
          [-105.007954, 40.00578],
          [-105.007929, 40.002536],
          [-105.007904, 40.000292],
          [-105.008402, 40.00029],
          [-105.008402, 40.002531],
          [-105.008426, 40.005772],
          [-105.008475, 40.007445],
          [-105.008555, 40.007444],
          [-105.008979, 40.007435],
          [-105.008961, 40.005764],
          [-105.008944, 40.00426],
          [-105.00893, 40.002954],
          [-105.008928, 40.002694],
          [-105.008926, 40.002527],
          [-105.008903, 40.00029],
          [-105.009432, 40.000289],
          [-105.010221, 40.000288],
          [-105.010347, 40.000289],
          [-105.011008, 40.000294],
          [-105.011684, 40.000292],
          [-105.01179, 40.000292],
          [-105.014591, 40.000285],
          [-105.015186, 40.00029],
          [-105.016009, 40.000308],
          [-105.017365, 40.000307],
          [-105.017772, 40.000307],
          [-105.022384, 40.000286],
          [-105.02762, 40.000263],
          [-105.037075, 40.00028],
          [-105.038601, 40.000268],
          [-105.041853, 40.000267],
          [-105.04392, 40.000267],
          [-105.044051, 40.000267],
          [-105.045672, 40.000266],
          [-105.046049, 40.000265],
          [-105.046237, 40.000265],
          [-105.047106, 40.000264],
          [-105.052809, 40.00026],
          [-105.052823, 40.00026],
          [-105.055432, 40.000251],
          [-105.055376, 40.144692],
          [-105.055375, 40.144929],
          [-105.055089, 40.261793],
          [-105.05672, 40.349281],
          [-104.945551, 40.349099],
          [-104.943371, 40.99819],
          [-104.882452, 40.998186]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09015",
      state: "09",
      county: "015",
      name: "Windham County",
      censusArea: 512.91,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.099229, 41.958948],
          [-72.10216, 42.028962],
          [-72.059752, 42.027339],
          [-71.80065, 42.023569],
          [-71.799242, 42.008065],
          [-71.796822, 41.928552],
          [-71.796089, 41.904468],
          [-71.794161, 41.841101],
          [-71.794161, 41.840141],
          [-71.792786, 41.80867],
          [-71.792767, 41.807001],
          [-71.789678, 41.724734],
          [-71.789678, 41.724568],
          [-71.789465, 41.640017],
          [-71.954983, 41.634799],
          [-72.062051, 41.652239],
          [-72.103523, 41.659305],
          [-72.105651, 41.659528],
          [-72.120328, 41.661925],
          [-72.153391, 41.665643],
          [-72.157546, 41.657627],
          [-72.186651, 41.676283],
          [-72.237947, 41.713167],
          [-72.239559, 41.714191],
          [-72.156321, 41.753491],
          [-72.156893, 41.753818],
          [-72.164558, 41.834913],
          [-72.217719, 41.835184],
          [-72.21698, 41.958161],
          [-72.151654, 41.959302],
          [-72.12452, 41.95905],
          [-72.123632, 41.95898],
          [-72.099229, 41.958948]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12001",
      state: "12",
      county: "001",
      name: "Alachua County",
      censusArea: 875.02,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.556511, 29.511982],
          [-82.656301, 29.564811],
          [-82.658128, 29.828068],
          [-82.658554, 29.830144],
          [-82.529705, 29.940881],
          [-82.418728, 29.923093],
          [-82.272563, 29.843011],
          [-82.133126, 29.835949],
          [-82.055625, 29.718232],
          [-82.050829, 29.716098],
          [-82.050291, 29.709735],
          [-82.055027, 29.669608],
          [-82.055899, 29.471232],
          [-82.105688, 29.435966],
          [-82.213245, 29.431764],
          [-82.22501, 29.484298],
          [-82.40662, 29.485048],
          [-82.556766, 29.480055],
          [-82.556511, 29.511982]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12013",
      state: "12",
      county: "013",
      name: "Calhoun County",
      censusArea: 567.334,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.389679, 30.200973],
          [-85.383948, 30.566856],
          [-85.171891, 30.564336],
          [-85.167713, 30.608026],
          [-84.932519, 30.606393],
          [-84.990017, 30.524795],
          [-84.983203, 30.441837],
          [-85.059092, 30.263229],
          [-85.11566, 30.199976],
          [-85.386263, 30.201385],
          [-85.389679, 30.200973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12023",
      state: "12",
      county: "023",
      name: "Columbia County",
      censusArea: 797.574,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.584002, 30.591796],
          [-82.569237, 30.590965],
          [-82.565476, 30.590622],
          [-82.553159, 30.589934],
          [-82.545055, 30.589361],
          [-82.536233, 30.588885],
          [-82.524899, 30.588189],
          [-82.459792, 30.584287],
          [-82.458364, 30.136449],
          [-82.57601, 29.991468],
          [-82.529705, 29.940881],
          [-82.658554, 29.830144],
          [-82.800477, 29.932126],
          [-82.794594, 30.337024],
          [-82.681473, 30.342805],
          [-82.645768, 30.407992],
          [-82.726583, 30.559486],
          [-82.689539, 30.597734],
          [-82.689271, 30.597719],
          [-82.584002, 30.591796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12039",
      state: "12",
      county: "039",
      name: "Gadsden County",
      censusArea: 516.332,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.864693, 30.711542],
          [-84.863465, 30.711487],
          [-84.606386, 30.699865],
          [-84.606249, 30.699872],
          [-84.53937, 30.696775],
          [-84.535042, 30.696523],
          [-84.380719, 30.689673],
          [-84.282562, 30.685316],
          [-84.378384, 30.573467],
          [-84.420474, 30.463905],
          [-84.646408, 30.388281],
          [-84.781111, 30.459794],
          [-84.781813, 30.518693],
          [-84.882616, 30.533094],
          [-84.932519, 30.606393],
          [-84.864693, 30.711542]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12055",
      state: "12",
      county: "055",
      name: "Highlands County",
      censusArea: 1016.615,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.943186, 27.210599],
          [-81.169138, 27.209585],
          [-81.168281, 27.121734],
          [-81.267677, 27.121097],
          [-81.267152, 27.032461],
          [-81.562202, 27.033836],
          [-81.563696, 27.34068],
          [-81.563318, 27.646647],
          [-81.142164, 27.643238],
          [-81.213717, 27.528654],
          [-80.970916, 27.217698],
          [-80.951656, 27.224825],
          [-80.943186, 27.210599]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12059",
      state: "12",
      county: "059",
      name: "Holmes County",
      censusArea: 478.777,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.599934, 30.830552],
          [-85.619011, 30.743868],
          [-85.736051, 30.74637],
          [-85.821616, 30.790775],
          [-85.844421, 30.70313],
          [-86.037591, 30.704572],
          [-86.035039, 30.99332],
          [-85.498272, 30.996928],
          [-85.497992, 30.996931],
          [-85.599934, 30.830552]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12073",
      state: "12",
      county: "073",
      name: "Leon County",
      censusArea: 666.852,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.039707, 30.673819],
          [-84.007454, 30.6721],
          [-83.977165, 30.521987],
          [-84.041192, 30.483967],
          [-84.041197, 30.483827],
          [-84.041488, 30.463308],
          [-84.074691, 30.434302],
          [-84.074892, 30.413106],
          [-84.075191, 30.381902],
          [-84.075819, 30.369819],
          [-84.075539, 30.273617],
          [-84.241222, 30.274244],
          [-84.247673, 30.303493],
          [-84.713351, 30.30017],
          [-84.646408, 30.388281],
          [-84.420474, 30.463905],
          [-84.378384, 30.573467],
          [-84.282562, 30.685316],
          [-84.28121, 30.685256],
          [-84.083753, 30.675954],
          [-84.057228, 30.674705],
          [-84.046605, 30.6742],
          [-84.04181, 30.673878],
          [-84.039707, 30.673819]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04003",
      state: "04",
      county: "003",
      name: "Cochise County",
      censusArea: 6165.69,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.047926, 32.426376],
          [-109.048286, 32.089114],
          [-109.048296, 32.084093],
          [-109.048731, 32.028174],
          [-109.048599, 32.013651],
          [-109.04859, 31.870791],
          [-109.048769, 31.861383],
          [-109.048763, 31.810776],
          [-109.049195, 31.796551],
          [-109.049813, 31.499528],
          [-109.049843, 31.499515],
          [-109.050044, 31.332502],
          [-109.426931, 31.334033],
          [-110.460172, 31.333051],
          [-110.451736, 31.731267],
          [-110.451503, 32.427354],
          [-109.227815, 32.424475],
          [-109.113987, 32.426177],
          [-109.047926, 32.426376]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04013",
      state: "04",
      county: "013",
      name: "Maricopa County",
      censusArea: 9200.143,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.580626, 33.465902],
          [-111.583047, 33.20545],
          [-111.686035, 33.204637],
          [-111.893085, 33.204811],
          [-112.083488, 33.20456],
          [-112.191784, 33.265039],
          [-112.203288, 33.310533],
          [-112.203719, 32.50676],
          [-113.333897, 32.504938],
          [-113.333212, 33.22721],
          [-113.33392, 33.377425],
          [-113.333313, 33.56571],
          [-113.333823, 33.911661],
          [-113.333753, 33.999227],
          [-112.743951, 33.999898],
          [-112.341876, 33.898958],
          [-112.27604, 33.882312],
          [-112.264425, 33.899094],
          [-112.26047, 33.903592],
          [-112.256668, 33.905526],
          [-112.164758, 34.04817],
          [-111.861997, 34.015108],
          [-111.72531, 33.999849],
          [-111.494778, 33.999817],
          [-111.433271, 33.983321],
          [-111.450904, 33.919877],
          [-111.372317, 33.823924],
          [-111.389444, 33.776663],
          [-111.26283, 33.623157],
          [-111.222055, 33.602446],
          [-111.153354, 33.677606],
          [-111.039905, 33.466104],
          [-111.250681, 33.464902],
          [-111.26976, 33.465538],
          [-111.580626, 33.465902]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05001",
      state: "05",
      county: "001",
      name: "Arkansas County",
      censusArea: 988.77,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.056276, 34.336605],
          [-91.060626, 34.216766],
          [-91.117905, 34.119105],
          [-91.194278, 34.070938],
          [-91.132686, 33.955221],
          [-91.347628, 33.954941],
          [-91.421715, 34.014472],
          [-91.431239, 34.020363],
          [-91.437131, 34.034663],
          [-91.434621, 34.052702],
          [-91.435887, 34.06844],
          [-91.445614, 34.080147],
          [-91.540178, 34.224599],
          [-91.711795, 34.233919],
          [-91.705185, 34.48267],
          [-91.678037, 34.48192],
          [-91.59552, 34.48051],
          [-91.594429, 34.56805],
          [-91.37782, 34.56348],
          [-91.209161, 34.483221],
          [-91.107762, 34.353404],
          [-91.056276, 34.336605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05009",
      state: "05",
      county: "009",
      name: "Boone County",
      censusArea: 590.231,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.296117, 36.498389],
          [-93.088988, 36.498184],
          [-93.087635, 36.498239],
          [-93.069512, 36.498242],
          [-93.068455, 36.49825],
          [-93.013742, 36.49813],
          [-92.894336, 36.497867],
          [-92.894001, 36.49785],
          [-92.854049, 36.497983],
          [-92.884954, 36.490891],
          [-92.890695, 36.114132],
          [-92.944666, 36.115123],
          [-92.948256, 36.115526],
          [-93.300072, 36.121264],
          [-93.300828, 36.125038],
          [-93.296117, 36.498389]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05019",
      state: "05",
      county: "019",
      name: "Clark County",
      censusArea: 866.074,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.373406, 33.95707],
          [-93.434165, 34.049739],
          [-93.478646, 34.340851],
          [-93.439996, 34.340048],
          [-93.439461, 34.340212],
          [-93.433178, 34.339866],
          [-93.408124, 34.339527],
          [-93.35684, 34.280306],
          [-93.198278, 34.277337],
          [-93.199428, 34.248057],
          [-93.035039, 34.236664],
          [-93.042025, 34.159852],
          [-92.886285, 34.155877],
          [-92.893116, 33.983284],
          [-92.961185, 33.97482],
          [-92.89314, 33.902751],
          [-92.893534, 33.809943],
          [-93.10429, 33.777011],
          [-93.290571, 33.845133],
          [-93.373406, 33.95707]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05029",
      state: "05",
      county: "029",
      name: "Conway County",
      censusArea: 552.25,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.040525, 35.076923],
          [-92.896109, 35.170678],
          [-92.855, 35.172777],
          [-92.850624, 35.46295],
          [-92.479268, 35.455357],
          [-92.481469, 35.368392],
          [-92.484259, 35.280706],
          [-92.554685, 35.113277],
          [-92.716331, 35.071742],
          [-93.040525, 35.076923]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05043",
      state: "05",
      county: "043",
      name: "Drew County",
      censusArea: 828.356,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.975844, 33.704414],
          [-91.975974, 33.791787],
          [-91.663738, 33.792246],
          [-91.557648, 33.783384],
          [-91.450325, 33.780415],
          [-91.453438, 33.563703],
          [-91.45737, 33.388974],
          [-91.990167, 33.398214],
          [-92.00785, 33.529396],
          [-91.97608, 33.529687],
          [-91.975844, 33.704414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05051",
      state: "05",
      county: "051",
      name: "Garland County",
      censusArea: 677.781,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.394289, 34.743393],
          [-93.394456, 34.772355],
          [-93.287785, 34.773193],
          [-93.269886, 34.771993],
          [-93.075178, 34.771892],
          [-92.96925, 34.772266],
          [-92.979902, 34.637222],
          [-92.875456, 34.635124],
          [-92.808028, 34.590406],
          [-92.793197, 34.503606],
          [-92.915079, 34.506114],
          [-92.948071, 34.389255],
          [-93.407227, 34.396853],
          [-93.394289, 34.743393]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05061",
      state: "05",
      county: "061",
      name: "Howard County",
      censusArea: 588.554,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.935371, 34.350097],
          [-93.92583, 34.186496],
          [-93.820722, 34.183802],
          [-93.823076, 34.008893],
          [-93.831452, 33.747709],
          [-93.957315, 33.75094],
          [-93.958828, 33.750994],
          [-93.964699, 33.796211],
          [-94.070085, 33.926994],
          [-94.090096, 34.187141],
          [-94.243739, 34.192501],
          [-94.254668, 34.355481],
          [-93.935371, 34.350097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05071",
      state: "05",
      county: "071",
      name: "Johnson County",
      censusArea: 659.804,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.710076, 35.375242],
          [-93.695981, 35.768649],
          [-93.519204, 35.763521],
          [-93.519575, 35.734422],
          [-93.164452, 35.728831],
          [-93.1678, 35.597695],
          [-93.22117, 35.598938],
          [-93.223633, 35.468593],
          [-93.29591, 35.469915],
          [-93.299014, 35.327425],
          [-93.417895, 35.326779],
          [-93.43041, 35.423737],
          [-93.710076, 35.375242]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05085",
      state: "05",
      county: "085",
      name: "Lonoke County",
      censusArea: 770.728,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.121336, 35.01221],
          [-92.119719, 35.066493],
          [-92.013056, 35.076317],
          [-91.87888, 35.024577],
          [-91.802505, 35.030425],
          [-91.803703, 34.921524],
          [-91.693705, 34.919605],
          [-91.700828, 34.672029],
          [-91.638531, 34.626093],
          [-91.678037, 34.48192],
          [-91.705185, 34.48267],
          [-92.029996, 34.489352],
          [-92.02954, 34.621403],
          [-92.113313, 34.622966],
          [-92.078711, 34.729983],
          [-92.070805, 35.011802],
          [-92.121336, 35.01221]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05097",
      state: "05",
      county: "097",
      name: "Montgomery County",
      censusArea: 779.88,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.710297, 34.745296],
          [-93.394289, 34.743393],
          [-93.407227, 34.396853],
          [-93.408124, 34.339527],
          [-93.433178, 34.339866],
          [-93.439461, 34.340212],
          [-93.439996, 34.340048],
          [-93.478646, 34.340851],
          [-93.935371, 34.350097],
          [-93.930509, 34.665608],
          [-93.870197, 34.712699],
          [-93.710297, 34.745296]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05101",
      state: "05",
      county: "101",
      name: "Newton County",
      censusArea: 820.896,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.944666, 36.115123],
          [-92.951315, 35.724663],
          [-93.164452, 35.728831],
          [-93.519575, 35.734422],
          [-93.519204, 35.763521],
          [-93.523639, 35.881592],
          [-93.450715, 35.967493],
          [-93.479528, 36.125072],
          [-93.300072, 36.121264],
          [-92.948256, 36.115526],
          [-92.944666, 36.115123]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05111",
      state: "05",
      county: "111",
      name: "Poinsett County",
      censusArea: 758.389,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.036967, 35.708418],
          [-90.286736, 35.699914],
          [-90.287039, 35.439933],
          [-90.286899, 35.438573],
          [-90.500104, 35.441763],
          [-90.502429, 35.441726],
          [-91.03923, 35.444717],
          [-91.036967, 35.708418]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05123",
      state: "05",
      county: "123",
      name: "St. Francis County",
      censusArea: 634.771,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.40854, 34.904121],
          [-91.099201, 34.911796],
          [-91.099961, 34.86712],
          [-91.152457, 34.911847],
          [-91.149842, 35.00247],
          [-91.04543, 35.00104],
          [-91.04267, 35.148189],
          [-90.502993, 35.144214],
          [-90.400434, 35.148196],
          [-90.40854, 34.904121]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05129",
      state: "05",
      county: "129",
      name: "Searcy County",
      censusArea: 666.095,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.414324, 35.975865],
          [-92.416719, 35.78859],
          [-92.629837, 35.790823],
          [-92.631207, 35.718365],
          [-92.809763, 35.722401],
          [-92.951315, 35.724663],
          [-92.944666, 36.115123],
          [-92.890695, 36.114132],
          [-92.837776, 36.070124],
          [-92.411502, 36.062756],
          [-92.412542, 36.027228],
          [-92.414324, 35.975865]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13309",
      state: "13",
      county: "309",
      name: "Wheeler County",
      censusArea: 295.484,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.655486, 32.297561],
          [-82.612876, 32.161549],
          [-82.598068, 32.014429],
          [-82.543655, 31.958914],
          [-82.645659, 31.918883],
          [-82.729112, 32.006878],
          [-82.887657, 32.079891],
          [-82.927865, 32.135274],
          [-82.87179, 32.18273],
          [-82.884803, 32.196072],
          [-82.721964, 32.309283],
          [-82.655486, 32.297561]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13313",
      state: "13",
      county: "313",
      name: "Whitfield County",
      censusArea: 290.464,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.97986, 34.987647],
          [-84.976973, 34.987669],
          [-84.955623, 34.98783],
          [-84.94442, 34.987864],
          [-84.939306, 34.987916],
          [-84.861314, 34.987791],
          [-84.858032, 34.987746],
          [-84.831799, 34.988004],
          [-84.82401, 34.987707],
          [-84.820478, 34.987913],
          [-84.817279, 34.987753],
          [-84.810742, 34.987615],
          [-84.810477, 34.987607],
          [-84.818715, 34.87695],
          [-84.878499, 34.77961],
          [-84.864945, 34.707356],
          [-84.933722, 34.706799],
          [-84.913456, 34.634128],
          [-85.050446, 34.622482],
          [-85.050847, 34.71998],
          [-85.168851, 34.720279],
          [-85.144789, 34.767639],
          [-85.061648, 34.819478],
          [-84.97986, 34.987647]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13319",
      state: "13",
      county: "319",
      name: "Wilkinson County",
      censusArea: 447.308,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.357685, 32.926141],
          [-83.159585, 33.002803],
          [-83.073648, 32.946562],
          [-82.946966, 32.759358],
          [-82.946366, 32.741258],
          [-82.957066, 32.708318],
          [-82.95757, 32.708198],
          [-83.226536, 32.584201],
          [-83.40699, 32.89822],
          [-83.40682, 32.898446],
          [-83.357685, 32.926141]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16015",
      state: "16",
      county: "015",
      name: "Boise County",
      censusArea: 1899.237,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.975767, 43.591338],
          [-116.122476, 43.691256],
          [-116.281687, 43.806937],
          [-116.273069, 44.151227],
          [-116.212132, 44.151354],
          [-116.112427, 44.147834],
          [-116.103075, 44.236624],
          [-115.52015, 44.235196],
          [-115.401528, 44.259374],
          [-115.294462, 44.339411],
          [-115.178952, 44.29638],
          [-115.167118, 44.202711],
          [-115.026206, 44.158417],
          [-115.04647, 44.056242],
          [-114.99075, 43.949725],
          [-115.067174, 43.94035],
          [-115.147722, 44.002364],
          [-115.182416, 44.092262],
          [-115.230685, 44.098685],
          [-115.197061, 44.068328],
          [-115.287258, 43.935501],
          [-115.513842, 43.880723],
          [-115.54754, 43.792655],
          [-115.59297, 43.798087],
          [-115.645864, 43.700272],
          [-115.698219, 43.665088],
          [-115.975767, 43.591338]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16021",
      state: "16",
      county: "021",
      name: "Boundary County",
      censusArea: 1268.56,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.032351, 48.999188],
          [-116.757185, 48.999791],
          [-116.757234, 48.999943],
          [-116.049193, 49.000912],
          [-116.049025, 48.958351],
          [-116.049023, 48.957947],
          [-116.049226, 48.502058],
          [-116.784843, 48.500617],
          [-116.785141, 48.847428],
          [-117.033177, 48.846563],
          [-117.032351, 48.999188]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16045",
      state: "16",
      county: "045",
      name: "Gem County",
      censusArea: 560.9,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.157096, 44.498966],
          [-116.152289, 44.324388],
          [-116.212071, 44.324277],
          [-116.212132, 44.151354],
          [-116.273069, 44.151227],
          [-116.281687, 43.806937],
          [-116.512559, 43.807216],
          [-116.707332, 43.807279],
          [-116.712672, 43.807161],
          [-116.712591, 43.981259],
          [-116.572245, 43.980334],
          [-116.532176, 44.06606],
          [-116.452526, 44.065345],
          [-116.452012, 44.151417],
          [-116.358532, 44.151066],
          [-116.299757, 44.444107],
          [-116.157096, 44.498966]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16051",
      state: "16",
      county: "051",
      name: "Jefferson County",
      censusArea: 1093.501,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.976833, 43.927431],
          [-111.983381, 43.754138],
          [-111.932811, 43.766845],
          [-111.626438, 43.626748],
          [-112.519945, 43.62656],
          [-112.697408, 43.623011],
          [-112.695027, 43.97194],
          [-112.694674, 44.058454],
          [-112.15595, 44.057677],
          [-112.15637, 43.971382],
          [-111.97709, 43.970929],
          [-111.976833, 43.927431]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16057",
      state: "16",
      county: "057",
      name: "Latah County",
      censusArea: 1075.996,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.039783, 46.541785],
          [-117.039828, 46.815443],
          [-117.039657, 46.825798],
          [-117.039821, 47.127265],
          [-116.835438, 47.032576],
          [-116.329586, 47.022442],
          [-116.329587, 46.934502],
          [-116.329279, 46.6279],
          [-116.455881, 46.629097],
          [-116.625985, 46.628041],
          [-116.719183, 46.542789],
          [-117.039783, 46.541785]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16067",
      state: "16",
      county: "067",
      name: "Minidoka County",
      censusArea: 757.591,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.931799, 42.535275],
          [-113.932904, 42.765032],
          [-113.763862, 42.764508],
          [-113.713928, 42.849733],
          [-113.714701, 43.20003],
          [-113.413693, 43.199785],
          [-113.413026, 42.84925],
          [-113.472155, 42.849218],
          [-113.472177, 42.669251],
          [-113.557609, 42.656416],
          [-113.655338, 42.535663],
          [-113.779811, 42.55687],
          [-113.931799, 42.535275]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16081",
      state: "16",
      county: "081",
      name: "Teton County",
      censusArea: 449.456,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.048107, 43.983096],
          [-111.046715, 43.815832],
          [-111.04634, 43.726957],
          [-111.046435, 43.726545],
          [-111.046421, 43.722059],
          [-111.04611, 43.687848],
          [-111.046051, 43.685812],
          [-111.046118, 43.684902],
          [-111.04588, 43.681033],
          [-111.045706, 43.659112],
          [-111.045205, 43.501136],
          [-111.199111, 43.539686],
          [-111.243885, 43.621694],
          [-111.399915, 43.622015],
          [-111.398883, 43.923086],
          [-111.18711, 43.932202],
          [-111.048107, 43.983096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17005",
      state: "17",
      county: "005",
      name: "Bond County",
      censusArea: 380.279,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.250447, 39.028145],
          [-89.254237, 38.742019],
          [-89.481642, 38.740532],
          [-89.597321, 38.743236],
          [-89.599593, 38.87453],
          [-89.636874, 38.874278],
          [-89.639265, 38.999129],
          [-89.586088, 39.028246],
          [-89.250447, 39.028145]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17009",
      state: "17",
      county: "009",
      name: "Brown County",
      censusArea: 305.607,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.513747, 39.987891],
          [-90.536721, 39.923027],
          [-90.583534, 39.87675],
          [-90.582435, 39.854574],
          [-90.571754, 39.839326],
          [-90.893651, 39.84092],
          [-90.916609, 39.845075],
          [-90.913616, 40.104452],
          [-90.695884, 40.103794],
          [-90.607134, 39.981658],
          [-90.513747, 39.987891]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17019",
      state: "17",
      county: "019",
      name: "Champaign County",
      censusArea: 996.266,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.459957, 40.39885],
          [-87.932858, 40.399401],
          [-87.937645, 39.879803],
          [-88.462207, 39.879092],
          [-88.460418, 40.281935],
          [-88.459957, 40.39885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17029",
      state: "17",
      county: "029",
      name: "Coles County",
      censusArea: 508.291,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.472073, 39.651588],
          [-88.063437, 39.652555],
          [-87.9666, 39.685928],
          [-87.960179, 39.481309],
          [-88.014205, 39.480762],
          [-88.012121, 39.378968],
          [-88.47083, 39.374515],
          [-88.470505, 39.447041],
          [-88.472073, 39.651588]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17035",
      state: "17",
      county: "035",
      name: "Cumberland County",
      censusArea: 346.024,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.012121, 39.378968],
          [-88.007766, 39.173925],
          [-88.360654, 39.171118],
          [-88.470865, 39.171463],
          [-88.470906, 39.215029],
          [-88.471147, 39.220615],
          [-88.47083, 39.374515],
          [-88.012121, 39.378968]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17049",
      state: "17",
      county: "049",
      name: "Effingham County",
      censusArea: 478.777,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.360654, 39.171118],
          [-88.361745, 38.910847],
          [-88.620486, 38.915303],
          [-88.693531, 38.914617],
          [-88.806794, 38.911656],
          [-88.805325, 39.216263],
          [-88.804484, 39.216131],
          [-88.470906, 39.215029],
          [-88.470865, 39.171463],
          [-88.360654, 39.171118]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17055",
      state: "17",
      county: "055",
      name: "Franklin County",
      censusArea: 408.889,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.151176, 37.861999],
          [-89.150834, 37.950196],
          [-89.177597, 37.950311],
          [-89.129637, 38.124747],
          [-88.704606, 38.125195],
          [-88.706622, 37.906797],
          [-88.706665, 37.888866],
          [-88.706747, 37.887706],
          [-88.706688, 37.886951],
          [-88.70676, 37.863338],
          [-89.151176, 37.861999]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17065",
      state: "17",
      county: "065",
      name: "Hamilton County",
      censusArea: 434.665,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.485265, 37.907032],
          [-88.706622, 37.906797],
          [-88.704606, 38.125195],
          [-88.702391, 38.256661],
          [-88.370491, 38.255413],
          [-88.37452, 37.908432],
          [-88.37453, 37.907678],
          [-88.375019, 37.90775],
          [-88.484314, 37.906945],
          [-88.485265, 37.907032]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18051",
      state: "18",
      county: "051",
      name: "Gibson County",
      censusArea: 487.486,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.462892, 38.533519],
          [-87.407583, 38.375501],
          [-87.31596, 38.375293],
          [-87.316893, 38.246077],
          [-87.31703, 38.201753],
          [-87.466744, 38.201286],
          [-87.467284, 38.165403],
          [-87.688374, 38.168419],
          [-87.799169, 38.22858],
          [-87.955344, 38.230008],
          [-87.951715, 38.239632],
          [-87.923958, 38.258409],
          [-87.863032, 38.280172],
          [-87.750028, 38.403027],
          [-87.740001, 38.415698],
          [-87.599317, 38.450721],
          [-87.462892, 38.533519]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18059",
      state: "18",
      county: "059",
      name: "Hancock County",
      censusArea: 306.016,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.938077, 39.887183],
          [-85.938084, 39.923196],
          [-85.937587, 39.927137],
          [-85.862395, 39.928943],
          [-85.862489, 39.943618],
          [-85.576192, 39.945756],
          [-85.596916, 39.786519],
          [-85.633491, 39.786177],
          [-85.633228, 39.698462],
          [-85.951721, 39.697136],
          [-85.951699, 39.726419],
          [-85.938077, 39.887183]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18069",
      state: "18",
      county: "069",
      name: "Huntington County",
      censusArea: 382.651,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.643841, 41.002305],
          [-85.335643, 41.00525],
          [-85.335374, 41.002047],
          [-85.33603, 40.917082],
          [-85.334667, 40.654413],
          [-85.448825, 40.653607],
          [-85.638587, 40.653129],
          [-85.643841, 41.002305]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18079",
      state: "18",
      county: "079",
      name: "Jennings County",
      censusArea: 376.583,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.683839, 38.815406],
          [-85.795357, 38.807508],
          [-85.798804, 39.068536],
          [-85.799834, 39.128897],
          [-85.686783, 39.130859],
          [-85.56632, 39.132761],
          [-85.440054, 39.195617],
          [-85.444897, 38.912998],
          [-85.683839, 38.815406]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18085",
      state: "18",
      county: "085",
      name: "Kosciusko County",
      censusArea: 531.381,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.651928, 41.294776],
          [-85.686574, 41.178376],
          [-85.684181, 41.046716],
          [-85.927265, 41.042574],
          [-85.945561, 41.042876],
          [-86.075939, 41.084878],
          [-86.077551, 41.172868],
          [-86.054047, 41.173147],
          [-86.05923, 41.435812],
          [-85.653507, 41.436856],
          [-85.651928, 41.294776]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18095",
      state: "18",
      county: "095",
      name: "Madison County",
      censusArea: 451.915,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.576192, 39.945756],
          [-85.862489, 39.943618],
          [-85.861903, 40.218936],
          [-85.86116, 40.22531],
          [-85.862296, 40.378367],
          [-85.587859, 40.379757],
          [-85.578589, 40.379524],
          [-85.576197, 40.077143],
          [-85.576192, 39.945756]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18105",
      state: "18",
      county: "105",
      name: "Monroe County",
      censusArea: 394.509,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.573548, 38.992147],
          [-86.682277, 38.992404],
          [-86.683002, 39.165746],
          [-86.68561, 39.336043],
          [-86.630818, 39.346948],
          [-86.630844, 39.340755],
          [-86.57428, 39.339681],
          [-86.381395, 39.339752],
          [-86.370066, 39.048375],
          [-86.318133, 39.04881],
          [-86.317799, 39.002733],
          [-86.31738, 39.00005],
          [-86.317474, 38.99087],
          [-86.573548, 38.992147]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18115",
      state: "18",
      county: "115",
      name: "Ohio County",
      censusArea: 86.14,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.849445, 39.000923],
          [-84.813182, 38.930583],
          [-84.870749, 38.900905],
          [-85.140312, 38.903194],
          [-85.135832, 38.929578],
          [-85.132508, 38.948055],
          [-84.878614, 39.030296],
          [-84.849445, 39.000923]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18125",
      state: "18",
      county: "125",
      name: "Pike County",
      censusArea: 334.238,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.316893, 38.246077],
          [-87.31596, 38.375293],
          [-87.407583, 38.375501],
          [-87.462892, 38.533519],
          [-87.242097, 38.54493],
          [-87.072369, 38.5147],
          [-87.073067, 38.232596],
          [-87.316893, 38.246077]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18131",
      state: "18",
      county: "131",
      name: "Pulaski County",
      censusArea: 433.649,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.77585, 40.91246],
          [-86.930759, 40.912418],
          [-86.930314, 41.170899],
          [-86.93017, 41.172318],
          [-86.467046, 41.171442],
          [-86.468509, 40.909889],
          [-86.581532, 40.910925],
          [-86.77585, 40.91246]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18139",
      state: "18",
      county: "139",
      name: "Rush County",
      censusArea: 408.124,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.29811, 39.525476],
          [-85.298623, 39.520258],
          [-85.297575, 39.453275],
          [-85.629327, 39.45275],
          [-85.632525, 39.697504],
          [-85.633228, 39.698462],
          [-85.633491, 39.786177],
          [-85.596916, 39.786519],
          [-85.301584, 39.787579],
          [-85.29811, 39.525476]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18153",
      state: "18",
      county: "153",
      name: "Sullivan County",
      censusArea: 447.142,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.52791, 38.908209],
          [-87.518826, 38.923205],
          [-87.512187, 38.954417],
          [-87.572588, 39.057286],
          [-87.614881, 39.102655],
          [-87.658746, 39.135997],
          [-87.651719, 39.150602],
          [-87.642174, 39.157097],
          [-87.628727, 39.157427],
          [-87.605173, 39.185897],
          [-87.587507, 39.249282],
          [-87.593486, 39.247452],
          [-87.604076, 39.259459],
          [-87.240379, 39.259064],
          [-87.240737, 39.171773],
          [-87.241027, 38.907874],
          [-87.528108, 38.908027],
          [-87.52791, 38.908209]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18173",
      state: "18",
      county: "173",
      name: "Warrick County",
      censusArea: 384.815,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.269561, 37.877155],
          [-87.302324, 37.898445],
          [-87.380247, 37.935596],
          [-87.402632, 37.942267],
          [-87.418585, 37.944763],
          [-87.436859, 37.944192],
          [-87.450458, 37.941451],
          [-87.451185, 37.941034],
          [-87.467284, 38.165403],
          [-87.466744, 38.201286],
          [-87.31703, 38.201753],
          [-87.316893, 38.246077],
          [-87.073067, 38.232596],
          [-87.073141, 38.203821],
          [-87.01749, 38.20358],
          [-87.017453, 38.118301],
          [-87.072332, 38.11828],
          [-87.268003, 37.924766],
          [-87.269561, 37.877155]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18179",
      state: "18",
      county: "179",
      name: "Wells County",
      censusArea: 368.087,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.33603, 40.917082],
          [-85.167063, 40.916585],
          [-85.073861, 40.917823],
          [-85.068479, 40.56824],
          [-85.201146, 40.567242],
          [-85.447014, 40.566929],
          [-85.448993, 40.651271],
          [-85.448825, 40.653607],
          [-85.334667, 40.654413],
          [-85.33603, 40.917082]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18183",
      state: "18",
      county: "183",
      name: "Whitley County",
      censusArea: 335.569,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.651928, 41.294776],
          [-85.53718, 41.266157],
          [-85.307781, 41.264158],
          [-85.338552, 41.17912],
          [-85.335643, 41.00525],
          [-85.643841, 41.002305],
          [-85.683198, 41.001909],
          [-85.684181, 41.046716],
          [-85.686574, 41.178376],
          [-85.651928, 41.294776]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19009",
      state: "19",
      county: "009",
      name: "Audubon County",
      censusArea: 442.961,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.700629, 41.504148],
          [-95.04077, 41.50469],
          [-95.040748, 41.602152],
          [-95.09252, 41.602055],
          [-95.092861, 41.863374],
          [-94.744876, 41.862394],
          [-94.744204, 41.602185],
          [-94.700629, 41.504148]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19017",
      state: "19",
      county: "017",
      name: "Bremer County",
      censusArea: 435.477,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.554211, 42.907112],
          [-92.081568, 42.907013],
          [-92.081661, 42.642056],
          [-92.554492, 42.642314],
          [-92.554211, 42.907112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19021",
      state: "19",
      county: "021",
      name: "Buena Vista County",
      censusArea: 574.915,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.91389, 42.9097],
          [-94.914485, 42.560309],
          [-95.38801, 42.561742],
          [-95.388182, 42.909897],
          [-94.91389, 42.9097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19031",
      state: "19",
      county: "031",
      name: "Cedar County",
      censusArea: 579.436,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.366079, 41.860068],
          [-91.365789, 41.94741],
          [-90.898484, 41.946245],
          [-90.898373, 41.771392],
          [-90.899853, 41.597133],
          [-91.366448, 41.598373],
          [-91.366079, 41.860068]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19039",
      state: "19",
      county: "039",
      name: "Clarke County",
      censusArea: 431.167,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.014189, 41.156762],
          [-93.898813, 41.15661],
          [-93.789196, 41.162035],
          [-93.557556, 41.161271],
          [-93.55654, 40.898295],
          [-94.014803, 40.897031],
          [-94.014189, 41.156762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17073",
      state: "17",
      county: "073",
      name: "Henry County",
      censusArea: 822.985,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.856902, 41.408836],
          [-89.856619, 41.320956],
          [-89.857571, 41.234768],
          [-89.857798, 41.234483],
          [-89.868384, 41.148955],
          [-89.984559, 41.149366],
          [-90.323178, 41.151288],
          [-90.437657, 41.151252],
          [-90.433771, 41.326983],
          [-90.43213, 41.456234],
          [-90.332657, 41.514001],
          [-90.196535, 41.540126],
          [-90.185609, 41.584653],
          [-89.862351, 41.584005],
          [-89.856902, 41.408836]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17077",
      state: "17",
      county: "077",
      name: "Jackson County",
      censusArea: 584.081,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.521407, 37.572143],
          [-89.521274, 37.578971],
          [-89.518007, 37.583962],
          [-89.511936, 37.584564],
          [-89.506563, 37.62505],
          [-89.515436, 37.67137],
          [-89.671255, 37.801144],
          [-89.674304, 37.803179],
          [-89.595084, 37.95531],
          [-89.177597, 37.950311],
          [-89.150834, 37.950196],
          [-89.151176, 37.861999],
          [-89.153668, 37.600469],
          [-89.450594, 37.600022],
          [-89.521407, 37.572143]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17079",
      state: "17",
      county: "079",
      name: "Jasper County",
      censusArea: 494.51,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.007766, 39.173925],
          [-87.950385, 39.174882],
          [-87.945923, 38.850108],
          [-88.258608, 38.847521],
          [-88.36176, 38.851949],
          [-88.361745, 38.910847],
          [-88.360654, 39.171118],
          [-88.007766, 39.173925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17089",
      state: "17",
      county: "089",
      name: "Kane County",
      censusArea: 520.058,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.263068, 41.81229],
          [-88.263071, 41.799328],
          [-88.26267, 41.779629],
          [-88.26247, 41.777429],
          [-88.261952, 41.724652],
          [-88.601933, 41.719563],
          [-88.601291, 41.981336],
          [-88.588657, 42.15359],
          [-88.238369, 42.154253],
          [-88.262876, 41.986227],
          [-88.263068, 41.81229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17103",
      state: "17",
      county: "103",
      name: "Lee County",
      censusArea: 724.896,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.628371, 41.585014],
          [-89.631494, 41.584949],
          [-89.631368, 41.771297],
          [-89.62933, 41.901617],
          [-89.360682, 41.88807],
          [-88.941279, 41.891752],
          [-88.938618, 41.628319],
          [-89.1672, 41.628707],
          [-89.166561, 41.585289],
          [-89.628371, 41.585014]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17107",
      state: "17",
      county: "107",
      name: "Logan County",
      censusArea: 618.059,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.26374, 40.325344],
          [-89.26265, 40.280919],
          [-89.148764, 40.282038],
          [-89.144764, 40.048853],
          [-89.143457, 39.91792],
          [-89.217846, 39.91699],
          [-89.483419, 39.93314],
          [-89.578289, 39.976127],
          [-89.601604, 40.122432],
          [-89.602979, 40.320129],
          [-89.26374, 40.325344]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17121",
      state: "17",
      county: "121",
      name: "Marion County",
      censusArea: 572.363,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.144388, 38.473878],
          [-89.144258, 38.474861],
          [-89.143898, 38.503088],
          [-89.138393, 38.736331],
          [-89.138138, 38.824244],
          [-88.695165, 38.826299],
          [-88.698457, 38.606304],
          [-88.698961, 38.474914],
          [-89.144388, 38.473878]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17129",
      state: "17",
      county: "129",
      name: "Menard County",
      censusArea: 314.437,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.994724, 40.108373],
          [-89.97049, 40.142082],
          [-89.749196, 40.126852],
          [-89.638262, 40.15487],
          [-89.601604, 40.122432],
          [-89.578289, 39.976127],
          [-89.698259, 39.975309],
          [-89.701864, 39.916787],
          [-89.994506, 39.901925],
          [-89.994724, 40.108373]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17139",
      state: "17",
      county: "139",
      name: "Moultrie County",
      censusArea: 335.943,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.473182, 39.791837],
          [-88.472073, 39.651588],
          [-88.470505, 39.447041],
          [-88.584273, 39.447582],
          [-88.641468, 39.520958],
          [-88.716875, 39.521209],
          [-88.717539, 39.579299],
          [-88.809051, 39.580241],
          [-88.810575, 39.653222],
          [-88.812709, 39.740486],
          [-88.745671, 39.792146],
          [-88.473182, 39.791837]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17145",
      state: "17",
      county: "145",
      name: "Perry County",
      censusArea: 441.761,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.592797, 38.219271],
          [-89.147416, 38.212896],
          [-89.149739, 38.124945],
          [-89.129637, 38.124747],
          [-89.177597, 37.950311],
          [-89.595084, 37.95531],
          [-89.592797, 38.219271]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17155",
      state: "17",
      county: "155",
      name: "Putnam County",
      censusArea: 160.161,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.466421, 41.148558],
          [-89.466534, 41.233873],
          [-89.356671, 41.233235],
          [-89.334901, 41.300877],
          [-89.163705, 41.310187],
          [-89.162238, 41.10408],
          [-89.358597, 41.103665],
          [-89.32927, 41.147931],
          [-89.466421, 41.148558]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17169",
      state: "17",
      county: "169",
      name: "Schuyler County",
      censusArea: 437.273,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.909756, 40.284394],
          [-90.450227, 40.276335],
          [-90.451502, 40.188892],
          [-90.199556, 40.183945],
          [-90.355198, 40.124245],
          [-90.433523, 40.024001],
          [-90.513747, 39.987891],
          [-90.607134, 39.981658],
          [-90.695884, 40.103794],
          [-90.913616, 40.104452],
          [-90.911969, 40.193088],
          [-90.909756, 40.284394]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17175",
      state: "17",
      county: "175",
      name: "Stark County",
      censusArea: 288.079,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.638864, 41.233862],
          [-89.638597, 41.14976],
          [-89.638429, 41.148591],
          [-89.638727, 40.973707],
          [-89.982549, 40.974707],
          [-89.985479, 40.974495],
          [-89.984559, 41.149366],
          [-89.868384, 41.148955],
          [-89.857798, 41.234483],
          [-89.638864, 41.233862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17179",
      state: "17",
      county: "179",
      name: "Tazewell County",
      censusArea: 648.974,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.26939, 40.594329],
          [-89.26374, 40.325344],
          [-89.602979, 40.320129],
          [-89.714927, 40.319218],
          [-89.717104, 40.435655],
          [-89.92468, 40.435921],
          [-89.872463, 40.513127],
          [-89.658003, 40.567438],
          [-89.554994, 40.747637],
          [-89.330167, 40.748257],
          [-89.327343, 40.615566],
          [-89.26939, 40.594329]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17189",
      state: "17",
      county: "189",
      name: "Washington County",
      censusArea: 562.572,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.704417, 38.415667],
          [-89.615212, 38.472072],
          [-89.481556, 38.468465],
          [-89.360248, 38.516522],
          [-89.143898, 38.503088],
          [-89.144258, 38.474861],
          [-89.144388, 38.473878],
          [-89.147416, 38.212896],
          [-89.592797, 38.219271],
          [-89.703256, 38.219404],
          [-89.704417, 38.415667]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17203",
      state: "17",
      county: "203",
      name: "Woodford County",
      censusArea: 527.799,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.986896, 40.752297],
          [-88.9847, 40.664954],
          [-89.145649, 40.662057],
          [-89.155175, 40.596417],
          [-89.26939, 40.594329],
          [-89.327343, 40.615566],
          [-89.330167, 40.748257],
          [-89.554994, 40.747637],
          [-89.552766, 40.810469],
          [-89.47233, 40.921206],
          [-89.047718, 40.925749],
          [-88.93139, 40.92774],
          [-88.929331, 40.753337],
          [-88.986896, 40.752297]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18009",
      state: "18",
      county: "009",
      name: "Blackford County",
      censusArea: 165.08,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.219901, 40.379034],
          [-85.44433, 40.37914],
          [-85.447014, 40.566929],
          [-85.201146, 40.567242],
          [-85.206831, 40.379182],
          [-85.219901, 40.379034]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18011",
      state: "18",
      county: "011",
      name: "Boone County",
      censusArea: 422.913,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.242365, 40.180778],
          [-86.239739, 39.926055],
          [-86.24109, 39.926092],
          [-86.263305, 39.924889],
          [-86.326293, 39.924029],
          [-86.695071, 39.92277],
          [-86.695804, 40.178657],
          [-86.39885, 40.17719],
          [-86.242365, 40.180778]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18021",
      state: "18",
      county: "021",
      name: "Clay County",
      censusArea: 357.542,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.240737, 39.171773],
          [-87.240379, 39.259064],
          [-87.238964, 39.52062],
          [-87.199012, 39.607137],
          [-87.013062, 39.604787],
          [-87.014533, 39.473572],
          [-86.939981, 39.473345],
          [-86.942473, 39.342043],
          [-87.053646, 39.342621],
          [-87.054578, 39.168086],
          [-87.068341, 39.167965],
          [-87.240737, 39.171773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18033",
      state: "18",
      county: "033",
      name: "De Kalb County",
      censusArea: 362.824,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.803956, 41.426044],
          [-84.804015, 41.411655],
          [-84.804046, 41.408361],
          [-84.804133, 41.408292],
          [-84.803926, 41.367959],
          [-84.803582, 41.271273],
          [-84.806471, 41.270904],
          [-85.192094, 41.264209],
          [-85.194084, 41.526437],
          [-84.804729, 41.530135],
          [-84.804729, 41.530092],
          [-84.804551, 41.500364],
          [-84.804457, 41.488224],
          [-84.803919, 41.435531],
          [-84.803956, 41.426128],
          [-84.803956, 41.426044]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13125",
      state: "13",
      county: "125",
      name: "Glascock County",
      censusArea: 143.74,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.431957, 33.27483],
          [-82.661917, 33.126331],
          [-82.748311, 33.238348],
          [-82.747238, 33.247663],
          [-82.755846, 33.25344],
          [-82.559753, 33.32727],
          [-82.431957, 33.27483]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13129",
      state: "13",
      county: "129",
      name: "Gordon County",
      censusArea: 355.808,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.654523, 34.583187],
          [-84.654366, 34.548946],
          [-84.653232, 34.41259],
          [-84.862863, 34.396601],
          [-85.005775, 34.392446],
          [-85.086761, 34.401264],
          [-85.069159, 34.587184],
          [-85.060499, 34.587184],
          [-85.06038, 34.62245],
          [-85.055206, 34.622383],
          [-85.055095, 34.62239],
          [-85.050446, 34.622482],
          [-84.913456, 34.634128],
          [-84.863793, 34.608597],
          [-84.716498, 34.6229],
          [-84.654523, 34.583187]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13131",
      state: "13",
      county: "131",
      name: "Grady County",
      censusArea: 454.527,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.380719, 30.689673],
          [-84.376612, 31.078883],
          [-84.116644, 31.077971],
          [-84.119058, 30.980956],
          [-84.076964, 30.91557],
          [-84.083753, 30.675954],
          [-84.28121, 30.685256],
          [-84.282562, 30.685316],
          [-84.380719, 30.689673]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13139",
      state: "13",
      county: "139",
      name: "Hall County",
      censusArea: 392.782,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.062841, 34.167873],
          [-83.989059, 34.195732],
          [-83.927284, 34.279399],
          [-83.957077, 34.334011],
          [-83.980649, 34.418389],
          [-83.843405, 34.505494],
          [-83.666413, 34.503598],
          [-83.615251, 34.431748],
          [-83.669473, 34.366689],
          [-83.620115, 34.295276],
          [-83.817682, 34.127493],
          [-83.86803, 34.098281],
          [-84.062841, 34.167873]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13157",
      state: "13",
      county: "157",
      name: "Jackson County",
      censusArea: 339.663,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.360028, 34.040572],
          [-83.537385, 33.965912],
          [-83.563215, 34.031757],
          [-83.767532, 34.066448],
          [-83.817682, 34.127493],
          [-83.620115, 34.295276],
          [-83.480361, 34.262038],
          [-83.402428, 34.197499],
          [-83.360028, 34.040572]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13169",
      state: "13",
      county: "169",
      name: "Jones County",
      censusArea: 393.933,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.357685, 32.926141],
          [-83.40682, 32.898446],
          [-83.40699, 32.89822],
          [-83.407668, 32.897822],
          [-83.513511, 32.844868],
          [-83.658211, 32.887913],
          [-83.710685, 32.952792],
          [-83.747004, 33.063373],
          [-83.816048, 33.131816],
          [-83.545876, 33.171944],
          [-83.42909, 33.185352],
          [-83.414997, 33.112833],
          [-83.380383, 32.998077],
          [-83.357685, 32.926141]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13175",
      state: "13",
      county: "175",
      name: "Laurens County",
      censusArea: 807.296,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.226536, 32.584201],
          [-82.95757, 32.708198],
          [-82.957066, 32.708318],
          [-82.862763, 32.71576],
          [-82.668557, 32.612164],
          [-82.647733, 32.512507],
          [-82.737137, 32.33803],
          [-82.721964, 32.309283],
          [-82.884803, 32.196072],
          [-82.990967, 32.147274],
          [-83.138991, 32.423069],
          [-83.226536, 32.584201]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13187",
      state: "13",
      county: "187",
      name: "Lumpkin County",
      censusArea: 282.933,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.843405, 34.505494],
          [-83.980649, 34.418389],
          [-84.102037, 34.464544],
          [-84.19104, 34.539181],
          [-84.188557, 34.602692],
          [-84.158035, 34.648243],
          [-84.03651, 34.641934],
          [-83.939007, 34.740859],
          [-83.856506, 34.722191],
          [-83.877276, 34.629597],
          [-83.843405, 34.505494]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13197",
      state: "13",
      county: "197",
      name: "Marion County",
      censusArea: 366.003,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.649319, 32.232953],
          [-84.65892, 32.232852],
          [-84.657325, 32.49494],
          [-84.637323, 32.534855],
          [-84.444353, 32.562083],
          [-84.392316, 32.414046],
          [-84.430215, 32.166257],
          [-84.431214, 32.134058],
          [-84.527117, 32.134556],
          [-84.649319, 32.232953]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13201",
      state: "13",
      county: "201",
      name: "Miller County",
      censusArea: 282.421,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.922423, 31.07259],
          [-84.91743, 31.25599],
          [-84.641673, 31.258967],
          [-84.537101, 31.255932],
          [-84.542653, 31.079029],
          [-84.730855, 31.06919],
          [-84.922423, 31.07259]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13217",
      state: "13",
      county: "217",
      name: "Newton County",
      censusArea: 272.161,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.682196, 33.52622],
          [-83.674644, 33.487225],
          [-83.824922, 33.437263],
          [-83.863058, 33.368278],
          [-83.923913, 33.444194],
          [-84.044493, 33.525776],
          [-84.024854, 33.547507],
          [-84.024279, 33.548226],
          [-83.972655, 33.605482],
          [-83.914823, 33.744203],
          [-83.77735, 33.658301],
          [-83.680896, 33.596919],
          [-83.687386, 33.588559],
          [-83.687085, 33.582375],
          [-83.681443, 33.577913],
          [-83.677669, 33.571176],
          [-83.682196, 33.52622]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13225",
      state: "13",
      county: "225",
      name: "Peach County",
      censusArea: 150.267,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.846676, 32.468514],
          [-84.00849, 32.521769],
          [-84.001069, 32.520586],
          [-84.003363, 32.529927],
          [-83.766787, 32.692622],
          [-83.70109, 32.691598],
          [-83.719859, 32.53176],
          [-83.846676, 32.468514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13233",
      state: "13",
      county: "233",
      name: "Polk County",
      censusArea: 310.331,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.922742, 34.082497],
          [-84.978683, 33.951393],
          [-85.05031, 33.904488],
          [-85.386693, 33.901697],
          [-85.398837, 33.964129],
          [-85.421731, 34.080821],
          [-85.257624, 34.100668],
          [-85.046871, 34.096412],
          [-84.922742, 34.082497]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13243",
      state: "13",
      county: "243",
      name: "Randolph County",
      censusArea: 428.236,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.95864, 31.777854],
          [-84.910338, 31.776841],
          [-84.907006, 31.924465],
          [-84.65582, 31.920308],
          [-84.599776, 31.920171],
          [-84.603086, 31.772001],
          [-84.535527, 31.681828],
          [-84.546854, 31.621199],
          [-84.817843, 31.619771],
          [-84.942353, 31.618669],
          [-84.95864, 31.777854]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13253",
      state: "13",
      county: "253",
      name: "Seminole County",
      censusArea: 235.226,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.022121, 31.07548],
          [-85.001928, 31.074983],
          [-84.999869, 31.074736],
          [-84.922423, 31.07259],
          [-84.730855, 31.06919],
          [-84.755917, 30.88527],
          [-84.863465, 30.711487],
          [-84.864693, 30.711542],
          [-84.896122, 30.750591],
          [-84.941925, 30.887988],
          [-84.959626, 30.910587],
          [-84.983527, 30.935486],
          [-84.998628, 30.971386],
          [-85.002368, 31.000682],
          [-84.999428, 31.013843],
          [-85.022121, 31.07548]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13265",
      state: "13",
      county: "265",
      name: "Taliaferro County",
      censusArea: 194.608,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.679975, 33.599792],
          [-82.751275, 33.510143],
          [-82.815315, 33.521137],
          [-82.851954, 33.443543],
          [-83.012853, 33.469178],
          [-83.014038, 33.580383],
          [-82.951142, 33.658032],
          [-82.995602, 33.693583],
          [-82.98888, 33.706315],
          [-82.949046, 33.733333],
          [-82.890599, 33.632877],
          [-82.812958, 33.655836],
          [-82.679975, 33.599792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13277",
      state: "13",
      county: "277",
      name: "Tift County",
      censusArea: 258.915,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.338728, 31.475991],
          [-83.434492, 31.350361],
          [-83.460644, 31.326263],
          [-83.512607, 31.327405],
          [-83.65409, 31.330661],
          [-83.649384, 31.567977],
          [-83.648972, 31.596466],
          [-83.500702, 31.59399],
          [-83.338728, 31.475991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13285",
      state: "13",
      county: "285",
      name: "Troup County",
      censusArea: 413.988,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.236509, 33.129562],
          [-84.939015, 33.224693],
          [-84.862359, 33.191173],
          [-84.861768, 32.872495],
          [-85.184131, 32.870525],
          [-85.221868, 33.055538],
          [-85.223261, 33.06258],
          [-85.232378, 33.108077],
          [-85.236509, 33.129562]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13293",
      state: "13",
      county: "293",
      name: "Upson County",
      censusArea: 323.437,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.202628, 32.690018],
          [-84.286246, 32.747626],
          [-84.391017, 32.786956],
          [-84.506888, 32.881788],
          [-84.52702, 32.970548],
          [-84.489708, 32.993729],
          [-84.27014, 32.991011],
          [-84.122361, 32.989576],
          [-84.12334, 32.932184],
          [-84.124275, 32.849562],
          [-84.202628, 32.690018]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13303",
      state: "13",
      county: "303",
      name: "Washington County",
      censusArea: 678.452,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.661917, 33.126331],
          [-82.550197, 33.019247],
          [-82.510851, 32.917754],
          [-82.521052, 32.822356],
          [-82.768365, 32.769108],
          [-82.802456, 32.809756],
          [-82.946966, 32.759358],
          [-83.073648, 32.946562],
          [-83.052197, 33.080682],
          [-82.888866, 33.138603],
          [-82.855046, 33.196427],
          [-82.748311, 33.238348],
          [-82.661917, 33.126331]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20027",
      state: "20",
      county: "027",
      name: "Clay County",
      censusArea: 645.301,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.368574, 39.567018],
          [-96.958859, 39.5664],
          [-96.961387, 39.220073],
          [-96.96138, 39.132325],
          [-96.963177, 39.1323],
          [-97.371154, 39.131961],
          [-97.369839, 39.306011],
          [-97.368574, 39.567018]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20029",
      state: "20",
      county: "029",
      name: "Cloud County",
      censusArea: 715.342,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.368574, 39.567018],
          [-97.369839, 39.306011],
          [-97.926096, 39.306517],
          [-97.929097, 39.306397],
          [-97.928462, 39.566915],
          [-97.931844, 39.566921],
          [-97.931482, 39.653767],
          [-97.36867, 39.654043],
          [-97.368653, 39.583833],
          [-97.368374, 39.577145],
          [-97.368635, 39.575926],
          [-97.368574, 39.567018]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20039",
      state: "20",
      county: "039",
      name: "Decatur County",
      censusArea: 893.517,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.738826, 40.002228],
          [-100.733296, 40.00227],
          [-100.729904, 40.002111],
          [-100.721128, 40.002069],
          [-100.683435, 40.002234],
          [-100.66023, 40.002162],
          [-100.645445, 40.001883],
          [-100.600945, 40.001906],
          [-100.594757, 40.001977],
          [-100.567238, 40.001889],
          [-100.551886, 40.001889],
          [-100.511065, 40.00184],
          [-100.487159, 40.001767],
          [-100.477018, 40.001752],
          [-100.475854, 40.001768],
          [-100.468773, 40.001724],
          [-100.447072, 40.001795],
          [-100.439081, 40.001774],
          [-100.39008, 40.001809],
          [-100.231652, 40.001623],
          [-100.229479, 40.001693],
          [-100.215406, 40.001629],
          [-100.196959, 40.001494],
          [-100.193597, 40.001573],
          [-100.19359, 40.001573],
          [-100.190323, 40.001586],
          [-100.188181, 40.001541],
          [-100.177823, 40.001593],
          [-100.177795, 40.001593],
          [-100.178276, 39.998801],
          [-100.180351, 39.56689],
          [-100.720213, 39.568034],
          [-100.740873, 39.568043],
          [-100.738826, 40.002228]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20049",
      state: "20",
      county: "049",
      name: "Elk County",
      censusArea: 644.265,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.52569, 37.476405],
          [-96.5253, 37.607015],
          [-95.961002, 37.603761],
          [-95.961605, 37.386636],
          [-95.964666, 37.386656],
          [-95.964399, 37.299232],
          [-96.524873, 37.30273],
          [-96.52569, 37.476405]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20065",
      state: "20",
      county: "065",
      name: "Graham County",
      censusArea: 898.522,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.605187, 39.132481],
          [-100.14794, 39.132179],
          [-100.162393, 39.131926],
          [-100.1642, 39.132071],
          [-100.161667, 39.567277],
          [-99.627953, 39.567437],
          [-99.602255, 39.567328],
          [-99.605187, 39.132481]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20075",
      state: "20",
      county: "075",
      name: "Hamilton County",
      censusArea: 996.508,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.567482, 38.263145],
          [-101.561294, 38.263179],
          [-101.560373, 38.263122],
          [-101.542312, 38.263207],
          [-101.542757, 37.827594],
          [-101.527063, 37.736314],
          [-102.04199, 37.738541],
          [-102.042158, 37.760164],
          [-102.042668, 37.788758],
          [-102.042953, 37.803535],
          [-102.043033, 37.824146],
          [-102.043219, 37.867929],
          [-102.043845, 37.926135],
          [-102.043844, 37.928102],
          [-102.044398, 38.250015],
          [-102.04451, 38.262412],
          [-101.567482, 38.263145]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20085",
      state: "20",
      county: "085",
      name: "Jackson County",
      censusArea: 656.217,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.788941, 39.653001],
          [-95.564126, 39.652872],
          [-95.570351, 39.41905],
          [-95.589295, 39.419086],
          [-95.589472, 39.216081],
          [-96.035573, 39.21652],
          [-96.035952, 39.5661],
          [-95.789101, 39.565916],
          [-95.788941, 39.653001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20095",
      state: "20",
      county: "095",
      name: "Kingman County",
      censusArea: 863.363,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.8076, 37.474184],
          [-97.807576, 37.473888],
          [-97.807057, 37.386293],
          [-98.349804, 37.384056],
          [-98.464951, 37.384086],
          [-98.464663, 37.471013],
          [-98.464804, 37.732675],
          [-97.807823, 37.733855],
          [-97.8076, 37.474184]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20105",
      state: "20",
      county: "105",
      name: "Lincoln County",
      censusArea: 719.396,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.92856, 38.958395],
          [-97.928702, 38.886208],
          [-97.928967, 38.882176],
          [-97.928595, 38.871102],
          [-98.484861, 38.870791],
          [-98.483748, 39.132671],
          [-98.489997, 39.132697],
          [-98.490011, 39.213048],
          [-98.489893, 39.213515],
          [-98.489864, 39.21465],
          [-98.490063, 39.215379],
          [-98.490161, 39.218033],
          [-98.490149, 39.21978],
          [-97.929746, 39.219273],
          [-97.92856, 38.958395]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20115",
      state: "20",
      county: "115",
      name: "Marion County",
      censusArea: 944.29,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.819723, 38.522464],
          [-96.840772, 38.085622],
          [-97.152913, 38.087704],
          [-97.153093, 38.174634],
          [-97.37175, 38.173673],
          [-97.371911, 38.609353],
          [-97.364088, 38.609146],
          [-96.930286, 38.609362],
          [-96.929969, 38.522412],
          [-96.819723, 38.522464]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20127",
      state: "20",
      county: "127",
      name: "Morris County",
      censusArea: 695.279,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.501556, 38.869704],
          [-96.501397, 38.826188],
          [-96.390398, 38.825858],
          [-96.352613, 38.739021],
          [-96.35378, 38.521657],
          [-96.81783, 38.522396],
          [-96.819723, 38.522464],
          [-96.929969, 38.522412],
          [-96.930286, 38.609362],
          [-96.927234, 38.81216],
          [-96.890245, 38.870067],
          [-96.501556, 38.869704]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20135",
      state: "20",
      county: "135",
      name: "Ness County",
      censusArea: 1074.75,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.584207, 38.262179],
          [-100.227137, 38.262237],
          [-100.244393, 38.26229],
          [-100.2472, 38.698165],
          [-100.153823, 38.697341],
          [-99.598323, 38.696514],
          [-99.585087, 38.696537],
          [-99.584794, 38.349386],
          [-99.584207, 38.262179]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20145",
      state: "20",
      county: "145",
      name: "Pawnee County",
      censusArea: 754.263,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.912583, 38.261088],
          [-98.913136, 38.000451],
          [-99.022136, 38.000236],
          [-99.350289, 38.000745],
          [-99.350359, 38.087594],
          [-99.569533, 38.087372],
          [-99.570543, 38.261957],
          [-99.584207, 38.262179],
          [-99.584794, 38.349386],
          [-99.032408, 38.348334],
          [-99.032309, 38.261227],
          [-98.912583, 38.261088]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20155",
      state: "20",
      county: "155",
      name: "Reno County",
      censusArea: 1255.346,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.472257, 37.824497],
          [-98.472794, 38.172757],
          [-97.922136, 38.173713],
          [-97.701841, 38.173814],
          [-97.701969, 37.911325],
          [-97.698692, 37.735056],
          [-97.807823, 37.733855],
          [-98.464804, 37.732675],
          [-98.464899, 37.824512],
          [-98.472257, 37.824497]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20163",
      state: "20",
      county: "163",
      name: "Rooks County",
      censusArea: 890.533,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.591776, 39.132357],
          [-99.593688, 39.13244],
          [-99.605187, 39.132481],
          [-99.602255, 39.567328],
          [-99.066203, 39.568093],
          [-99.044398, 39.568035],
          [-99.047687, 39.133014],
          [-99.591776, 39.132357]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20173",
      state: "20",
      county: "173",
      name: "Sedgwick County",
      censusArea: 997.512,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.8076, 37.474184],
          [-97.807823, 37.733855],
          [-97.698692, 37.735056],
          [-97.701969, 37.911325],
          [-97.152476, 37.912733],
          [-97.153331, 37.475544],
          [-97.8076, 37.474184]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20185",
      state: "20",
      county: "185",
      name: "Stafford County",
      censusArea: 792.046,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.472257, 37.824497],
          [-99.013318, 37.825336],
          [-99.022627, 37.825358],
          [-99.022136, 38.000236],
          [-98.913136, 38.000451],
          [-98.912583, 38.261088],
          [-98.479841, 38.26079],
          [-98.472794, 38.172757],
          [-98.472257, 37.824497]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20195",
      state: "20",
      county: "195",
      name: "Trego County",
      censusArea: 889.478,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.598323, 38.696514],
          [-100.153823, 38.697341],
          [-100.14794, 39.132179],
          [-99.605187, 39.132481],
          [-99.593688, 39.13244],
          [-99.591776, 39.132357],
          [-99.598323, 38.696514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20203",
      state: "20",
      county: "203",
      name: "Wichita County",
      censusArea: 718.568,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.567094, 38.699669],
          [-101.484383, 38.700166],
          [-101.128379, 38.700603],
          [-101.125438, 38.264509],
          [-101.542312, 38.263207],
          [-101.560373, 38.263122],
          [-101.561294, 38.263179],
          [-101.567482, 38.263145],
          [-101.567094, 38.699669]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20205",
      state: "20",
      county: "205",
      name: "Wilson County",
      censusArea: 570.418,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.961605, 37.386636],
          [-95.961002, 37.603761],
          [-95.960876, 37.734304],
          [-95.525499, 37.732759],
          [-95.52556, 37.383979],
          [-95.961605, 37.386636]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21183",
      state: "21",
      county: "183",
      name: "Ohio County",
      censusArea: 587.274,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.618233, 37.391348],
          [-86.759533, 37.304099],
          [-86.817168, 37.327616],
          [-86.899268, 37.212313],
          [-87.099803, 37.416042],
          [-87.103111, 37.4809],
          [-87.049376, 37.55186],
          [-87.03826, 37.560803],
          [-86.94876, 37.630548],
          [-86.817514, 37.673814],
          [-86.823429, 37.737754],
          [-86.638935, 37.661877],
          [-86.59281, 37.56436],
          [-86.664055, 37.546105],
          [-86.612095, 37.395123],
          [-86.618233, 37.391348]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21187",
      state: "21",
      county: "187",
      name: "Owen County",
      censusArea: 351.103,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.740594, 38.352415],
          [-84.870482, 38.356751],
          [-84.880248, 38.41618],
          [-84.963265, 38.437358],
          [-84.977166, 38.512989],
          [-85.074583, 38.596838],
          [-84.934536, 38.66213],
          [-84.785788, 38.720459],
          [-84.774322, 38.618594],
          [-84.580539, 38.473039],
          [-84.740594, 38.352415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21189",
      state: "21",
      county: "189",
      name: "Owsley County",
      censusArea: 197.407,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.526907, 37.258529],
          [-83.65394, 37.358061],
          [-83.784975, 37.348717],
          [-83.885262, 37.517688],
          [-83.82934, 37.486821],
          [-83.630412, 37.53746],
          [-83.579186, 37.506249],
          [-83.547628, 37.334418],
          [-83.526907, 37.258529]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21199",
      state: "21",
      county: "199",
      name: "Pulaski County",
      censusArea: 658.408,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.720712, 37.238328],
          [-84.648083, 37.290416],
          [-84.556461, 37.355729],
          [-84.502932, 37.328801],
          [-84.289076, 37.15174],
          [-84.358024, 36.9594],
          [-84.578158, 36.867133],
          [-84.679165, 36.980914],
          [-84.770172, 36.957735],
          [-84.835712, 36.997612],
          [-84.905778, 37.047187],
          [-84.901112, 37.116297],
          [-84.809338, 37.175034],
          [-84.720712, 37.238328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21209",
      state: "21",
      county: "209",
      name: "Scott County",
      censusArea: 281.766,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.725168, 38.195426],
          [-84.730289, 38.208302],
          [-84.739684, 38.338035],
          [-84.740594, 38.352415],
          [-84.580539, 38.473039],
          [-84.560674, 38.492378],
          [-84.557375, 38.492917],
          [-84.443756, 38.32431],
          [-84.432831, 38.299442],
          [-84.440725, 38.296385],
          [-84.442661, 38.283236],
          [-84.401847, 38.207889],
          [-84.62065, 38.129527],
          [-84.625079, 38.116397],
          [-84.725168, 38.195426]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21219",
      state: "21",
      county: "219",
      name: "Todd County",
      censusArea: 374.496,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.114983, 36.642414],
          [-87.23053, 36.641895],
          [-87.231037, 36.641888],
          [-87.247655, 36.641841],
          [-87.278398, 36.641718],
          [-87.281506, 36.641761],
          [-87.33598, 36.641543],
          [-87.259371, 37.072401],
          [-87.250256, 37.040557],
          [-87.053164, 37.061019],
          [-87.060843, 36.643412],
          [-87.114976, 36.642414],
          [-87.114983, 36.642414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21229",
      state: "21",
      county: "229",
      name: "Washington County",
      censusArea: 297.266,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.401784, 37.730658],
          [-85.299042, 37.831214],
          [-85.152797, 37.897661],
          [-85.030528, 37.891538],
          [-85.000529, 37.854642],
          [-85.025021, 37.678854],
          [-85.030172, 37.63127],
          [-85.257298, 37.630903],
          [-85.373639, 37.673343],
          [-85.401784, 37.730658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22001",
      state: "22",
      county: "001",
      name: "Acadia County",
      lsad: "Parish",
      censusArea: 655.119,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.284675, 30.147443],
          [-92.426039, 30.049369],
          [-92.534638, 30.107852],
          [-92.625484, 30.09233],
          [-92.591129, 30.182365],
          [-92.626612, 30.230272],
          [-92.631987, 30.481147],
          [-92.629063, 30.480614],
          [-92.493259, 30.480499],
          [-92.244795, 30.480193],
          [-92.176444, 30.436852],
          [-92.142184, 30.298817],
          [-92.284675, 30.147443]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22013",
      state: "22",
      county: "013",
      name: "Bienville County",
      lsad: "Parish",
      censusArea: 811.268,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.42819, 32.235897],
          [-93.372781, 32.410153],
          [-93.236656, 32.410071],
          [-93.147193, 32.455173],
          [-93.178767, 32.584916],
          [-92.880805, 32.585277],
          [-92.879268, 32.454118],
          [-92.777197, 32.453455],
          [-92.774936, 32.237055],
          [-92.814737, 32.146907],
          [-92.93908, 32.147942],
          [-93.187162, 32.148412],
          [-93.213988, 32.236065],
          [-93.42819, 32.235897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22033",
      state: "22",
      county: "033",
      name: "E. Baton Rouge County",
      lsad: "Parish",
      censusArea: 455.374,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.021014, 30.321489],
          [-91.142305, 30.319893],
          [-91.142332, 30.320437],
          [-91.142088, 30.322028],
          [-91.142042, 30.322718],
          [-91.142105, 30.323293],
          [-91.241508, 30.357592],
          [-91.197208, 30.507088],
          [-91.262734, 30.505238],
          [-91.297658, 30.649548],
          [-91.254419, 30.705294],
          [-90.849041, 30.719311],
          [-90.910701, 30.649385],
          [-90.980939, 30.589988],
          [-90.991529, 30.461818],
          [-90.891728, 30.345244],
          [-91.021014, 30.321489]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22053",
      state: "22",
      county: "053",
      name: "Jeffrson Davis County",
      lsad: "Parish",
      censusArea: 651.328,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.131155, 30.423612],
          [-93.065543, 30.438569],
          [-92.775554, 30.437656],
          [-92.722339, 30.488208],
          [-92.630347, 30.487873],
          [-92.629285, 30.486869],
          [-92.629471, 30.485586],
          [-92.630212, 30.485105],
          [-92.630028, 30.483569],
          [-92.63069, 30.48334],
          [-92.631933, 30.483707],
          [-92.633839, 30.483319],
          [-92.633495, 30.482287],
          [-92.631987, 30.481147],
          [-92.626612, 30.230272],
          [-92.591129, 30.182365],
          [-92.625484, 30.09233],
          [-92.738604, 30.037323],
          [-92.756546, 30.039326],
          [-92.996371, 30.038479],
          [-92.887605, 30.082475],
          [-92.893324, 30.156314],
          [-92.9963, 30.156955],
          [-93.030955, 30.37939],
          [-93.131109, 30.403057],
          [-93.131155, 30.423612]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22073",
      state: "22",
      county: "073",
      name: "Ouachita County",
      lsad: "Parish",
      censusArea: 610.406,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.065179, 32.723003],
          [-91.910952, 32.661119],
          [-91.913057, 32.505902],
          [-92.00434, 32.380499],
          [-92.034397, 32.277068],
          [-92.311851, 32.277441],
          [-92.311829, 32.320755],
          [-92.415418, 32.407812],
          [-92.41535, 32.495486],
          [-92.415071, 32.582845],
          [-92.272313, 32.580975],
          [-92.065179, 32.723003]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22077",
      state: "22",
      county: "077",
      name: "Pointe Coupee County",
      lsad: "Parish",
      censusArea: 557.346,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.414924, 30.535166],
          [-91.485089, 30.497138],
          [-91.700933, 30.497433],
          [-91.756193, 30.497738],
          [-91.733673, 30.70173],
          [-91.815258, 30.848107],
          [-91.805963, 30.972558],
          [-91.750119, 31.018814],
          [-91.662506, 30.970362],
          [-91.661372, 30.857099],
          [-91.515626, 30.861378],
          [-91.553019, 30.730798],
          [-91.38062, 30.758804],
          [-91.353092, 30.72177],
          [-91.340652, 30.670238],
          [-91.33087, 30.658677],
          [-91.414924, 30.535166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22089",
      state: "22",
      county: "089",
      name: "St. Charles County",
      lsad: "Parish",
      censusArea: 279.083,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.173202, 29.827524],
          [-90.228157, 29.692028],
          [-90.346191, 29.693302],
          [-90.371978, 29.759239],
          [-90.475418, 29.81221],
          [-90.541943, 29.891522],
          [-90.538132, 30.016884],
          [-90.449133, 30.032863],
          [-90.277855, 30.230888],
          [-90.27922, 30.049118],
          [-90.279739, 30.013338],
          [-90.27951, 30.006448],
          [-90.280143, 29.992744],
          [-90.280296, 29.972618],
          [-90.279533, 29.967971],
          [-90.240173, 29.862117],
          [-90.173202, 29.827524]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22105",
      state: "22",
      county: "105",
      name: "Tangipahoa County",
      lsad: "Parish",
      censusArea: 791.275,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.369371, 31.000335],
          [-90.347241, 31.000359],
          [-90.255648, 30.711905],
          [-90.243034, 30.224447],
          [-90.277855, 30.230888],
          [-90.297375, 30.293658],
          [-90.401254, 30.285534],
          [-90.546118, 30.430283],
          [-90.567165, 30.650023],
          [-90.565857, 30.663902],
          [-90.567195, 30.999733],
          [-90.547615, 30.999723],
          [-90.486749, 30.999693],
          [-90.485876, 30.99974],
          [-90.477284, 30.999717],
          [-90.475928, 30.99974],
          [-90.474094, 30.999798],
          [-90.442479, 30.999722],
          [-90.441725, 30.999729],
          [-90.437351, 30.99973],
          [-90.426849, 30.999776],
          [-90.422117, 30.99981],
          [-90.369371, 31.000335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22115",
      state: "22",
      county: "115",
      name: "Vernon County",
      lsad: "Parish",
      censusArea: 1327.91,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.554576, 30.87747],
          [-93.530936, 30.924534],
          [-93.540354, 31.008135],
          [-93.527644, 31.074509],
          [-93.531744, 31.180817],
          [-93.535097, 31.185614],
          [-93.548931, 31.186601],
          [-93.55254, 31.185605],
          [-93.534919, 31.276579],
          [-93.442554, 31.276716],
          [-93.390495, 31.364147],
          [-93.236104, 31.364507],
          [-92.981464, 31.346644],
          [-92.879756, 31.317373],
          [-92.829651, 31.252281],
          [-92.823932, 30.89],
          [-92.978453, 30.878175],
          [-93.554576, 30.87747]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21003",
      state: "21",
      county: "003",
      name: "Allen County",
      censusArea: 344.338,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.976421, 36.6284],
          [-86.03277, 36.630367],
          [-86.033139, 36.630413],
          [-86.038366, 36.630804],
          [-86.080666, 36.63394],
          [-86.081944, 36.633848],
          [-86.197573, 36.639363],
          [-86.204859, 36.639741],
          [-86.205468, 36.639783],
          [-86.216183, 36.640527],
          [-86.21641, 36.640595],
          [-86.219081, 36.640824],
          [-86.222151, 36.640891],
          [-86.333051, 36.648778],
          [-86.411387, 36.65055],
          [-86.405769, 36.776187],
          [-86.16674, 36.934015],
          [-85.97691, 36.722722],
          [-85.976421, 36.6284]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21009",
      state: "21",
      county: "009",
      name: "Barren County",
      censusArea: 487.541,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.113512, 37.061072],
          [-86.074267, 37.053285],
          [-86.056443, 37.167208],
          [-85.906336, 37.148377],
          [-85.744221, 37.169813],
          [-85.739255, 36.841476],
          [-85.97691, 36.722722],
          [-86.16674, 36.934015],
          [-86.113512, 37.061072]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21017",
      state: "21",
      county: "017",
      name: "Bourbon County",
      censusArea: 289.719,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.19405, 38.37175],
          [-84.120185, 38.275516],
          [-83.977924, 38.192118],
          [-84.080527, 38.115118],
          [-84.286461, 38.067028],
          [-84.380928, 38.113563],
          [-84.401847, 38.207889],
          [-84.442661, 38.283236],
          [-84.345476, 38.285099],
          [-84.19405, 38.37175]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21025",
      state: "21",
      county: "025",
      name: "Breathitt County",
      censusArea: 492.413,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.124348, 37.404973],
          [-83.214735, 37.443538],
          [-83.38165, 37.338266],
          [-83.449454, 37.378853],
          [-83.547628, 37.334418],
          [-83.579186, 37.506249],
          [-83.522308, 37.638516],
          [-83.418474, 37.690729],
          [-83.248692, 37.669616],
          [-83.134949, 37.606812],
          [-83.089796, 37.632167],
          [-82.948544, 37.503167],
          [-83.062266, 37.526231],
          [-83.134711, 37.457181],
          [-83.124348, 37.404973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21031",
      state: "21",
      county: "031",
      name: "Butler County",
      censusArea: 426.088,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.618233, 37.391348],
          [-86.612095, 37.395123],
          [-86.487152, 37.362501],
          [-86.469126, 37.321099],
          [-86.399165, 37.169896],
          [-86.618158, 37.183906],
          [-86.674462, 36.999766],
          [-86.84535, 37.056513],
          [-86.941391, 37.068981],
          [-86.893911, 37.088478],
          [-86.899268, 37.212313],
          [-86.817168, 37.327616],
          [-86.759533, 37.304099],
          [-86.618233, 37.391348]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21043",
      state: "21",
      county: "043",
      name: "Carter County",
      censusArea: 409.496,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.341347, 38.319521],
          [-83.23068, 38.339286],
          [-83.237181, 38.426246],
          [-83.166419, 38.503965],
          [-83.051751, 38.465404],
          [-82.97859, 38.398857],
          [-82.817454, 38.373935],
          [-82.771737, 38.362588],
          [-82.794206, 38.243772],
          [-82.924805, 38.175114],
          [-83.053081, 38.189545],
          [-83.159191, 38.264537],
          [-83.244306, 38.19238],
          [-83.306662, 38.225241],
          [-83.341347, 38.319521]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21053",
      state: "21",
      county: "053",
      name: "Clinton County",
      censusArea: 197.246,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.974888, 36.614327],
          [-85.086415, 36.621913],
          [-85.096128, 36.622483],
          [-85.276284, 36.626511],
          [-85.276289, 36.626511],
          [-85.29599, 36.625488],
          [-85.246357, 36.744294],
          [-85.215405, 36.854467],
          [-85.118411, 36.827334],
          [-85.064307, 36.858743],
          [-85.004099, 36.756237],
          [-84.974888, 36.614327]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21063",
      state: "21",
      county: "063",
      name: "Elliott County",
      censusArea: 234.315,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.924805, 38.175114],
          [-82.889059, 38.111587],
          [-83.022115, 38.007047],
          [-83.194445, 38.010649],
          [-83.263289, 38.115386],
          [-83.244306, 38.19238],
          [-83.159191, 38.264537],
          [-83.053081, 38.189545],
          [-82.924805, 38.175114]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21077",
      state: "21",
      county: "077",
      name: "Gallatin County",
      censusArea: 101.234,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.660107, 38.777293],
          [-84.780766, 38.765208],
          [-84.785788, 38.720459],
          [-84.934536, 38.66213],
          [-85.024205, 38.763311],
          [-84.995939, 38.776756],
          [-84.984627, 38.779072],
          [-84.973432, 38.778847],
          [-84.944296, 38.775184],
          [-84.844412, 38.787078],
          [-84.81335, 38.79833],
          [-84.791078, 38.855859],
          [-84.660107, 38.777293]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21087",
      state: "21",
      county: "087",
      name: "Green County",
      censusArea: 286.034,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.657332, 37.421996],
          [-85.583497, 37.469917],
          [-85.584477, 37.432091],
          [-85.478081, 37.363673],
          [-85.352771, 37.192432],
          [-85.380258, 37.168314],
          [-85.526881, 37.10945],
          [-85.632313, 37.125056],
          [-85.686482, 37.182427],
          [-85.697325, 37.301838],
          [-85.657332, 37.421996]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21099",
      state: "21",
      county: "099",
      name: "Hart County",
      censusArea: 412.086,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.047598, 37.449933],
          [-85.892804, 37.440308],
          [-85.657332, 37.421996],
          [-85.697325, 37.301838],
          [-85.686482, 37.182427],
          [-85.743919, 37.169861],
          [-85.744221, 37.169813],
          [-85.906336, 37.148377],
          [-86.056443, 37.167208],
          [-86.049133, 37.215285],
          [-86.157589, 37.335649],
          [-86.064554, 37.348613],
          [-86.072351, 37.381071],
          [-86.019089, 37.394537],
          [-86.047598, 37.449933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21103",
      state: "21",
      county: "103",
      name: "Henry County",
      censusArea: 286.276,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.870482, 38.356751],
          [-84.997667, 38.335586],
          [-85.283082, 38.358054],
          [-85.34608, 38.45954],
          [-85.314006, 38.492592],
          [-85.16827, 38.585448],
          [-85.074583, 38.596838],
          [-84.977166, 38.512989],
          [-84.963265, 38.437358],
          [-84.880248, 38.41618],
          [-84.870482, 38.356751]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21115",
      state: "21",
      county: "115",
      name: "Johnson County",
      censusArea: 261.953,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.940763, 37.716338],
          [-83.004649, 37.859247],
          [-82.989014, 37.963968],
          [-82.947936, 38.000431],
          [-82.612393, 37.879272],
          [-82.670887, 37.851823],
          [-82.64075, 37.720182],
          [-82.763313, 37.75612],
          [-82.940763, 37.716338]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21121",
      state: "21",
      county: "121",
      name: "Knox County",
      censusArea: 386.297,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.589521, 36.955786],
          [-83.581094, 36.904667],
          [-83.798248, 36.785197],
          [-83.877503, 36.687259],
          [-83.958534, 36.727245],
          [-84.065755, 36.853961],
          [-84.091853, 36.955992],
          [-83.959385, 36.977128],
          [-83.871315, 37.054919],
          [-83.589521, 36.955786]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21137",
      state: "21",
      county: "137",
      name: "Lincoln County",
      censusArea: 334.095,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.648083, 37.290416],
          [-84.720712, 37.238328],
          [-84.722826, 37.360043],
          [-84.850816, 37.427143],
          [-84.847326, 37.547894],
          [-84.801112, 37.568776],
          [-84.658296, 37.634501],
          [-84.446719, 37.485893],
          [-84.502932, 37.328801],
          [-84.556461, 37.355729],
          [-84.648083, 37.290416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21143",
      state: "21",
      county: "143",
      name: "Lyon County",
      censusArea: 213.84,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.095781, 37.179684],
          [-88.042142, 37.174756],
          [-87.876996, 36.960116],
          [-88.157347, 36.867183],
          [-88.240371, 36.981911],
          [-88.209221, 36.999976],
          [-88.231145, 37.081575],
          [-88.193519, 37.147256],
          [-88.095781, 37.179684]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21151",
      state: "21",
      county: "151",
      name: "Madison County",
      censusArea: 437.293,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.347805, 37.539064],
          [-84.525643, 37.769497],
          [-84.43572, 37.847202],
          [-84.337393, 37.892007],
          [-84.080337, 37.853066],
          [-84.090364, 37.566227],
          [-84.199489, 37.522858],
          [-84.347805, 37.539064]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21157",
      state: "21",
      county: "157",
      name: "Marshall County",
      censusArea: 301.253,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.485697, 36.750328],
          [-88.484103, 36.942096],
          [-88.485254, 37.001109],
          [-88.482957, 37.022566],
          [-88.365595, 37.064183],
          [-88.240371, 36.981911],
          [-88.157347, 36.867183],
          [-88.110831, 36.74715],
          [-88.485697, 36.750328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21169",
      state: "21",
      county: "169",
      name: "Metcalfe County",
      censusArea: 289.645,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.744221, 37.169813],
          [-85.743919, 37.169861],
          [-85.686482, 37.182427],
          [-85.632313, 37.125056],
          [-85.526881, 37.10945],
          [-85.45183, 36.938138],
          [-85.596214, 36.818039],
          [-85.739255, 36.841476],
          [-85.744221, 37.169813]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21175",
      state: "21",
      county: "175",
      name: "Morgan County",
      censusArea: 381.127,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.022115, 38.007047],
          [-83.016058, 37.974221],
          [-82.989014, 37.963968],
          [-83.004649, 37.859247],
          [-83.113277, 37.864121],
          [-83.269662, 37.766012],
          [-83.262172, 37.712657],
          [-83.49508, 37.861906],
          [-83.433942, 38.033974],
          [-83.263289, 38.115386],
          [-83.194445, 38.010649],
          [-83.022115, 38.007047]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19045",
      state: "19",
      county: "045",
      name: "Clinton County",
      censusArea: 694.915,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.152104, 41.928947],
          [-90.181973, 41.80707],
          [-90.187969, 41.803163],
          [-90.20844, 41.797176],
          [-90.222263, 41.793133],
          [-90.242747, 41.783767],
          [-90.278633, 41.767358],
          [-90.302782, 41.750031],
          [-90.309826, 41.743321],
          [-90.31522, 41.734264],
          [-90.317041, 41.729104],
          [-90.473617, 41.773465],
          [-90.898373, 41.771392],
          [-90.898484, 41.946245],
          [-90.898373, 42.033548],
          [-90.154221, 42.033073],
          [-90.150916, 42.02944],
          [-90.141167, 42.008931],
          [-90.140613, 41.995999],
          [-90.1516, 41.931002],
          [-90.152104, 41.928947]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19047",
      state: "19",
      county: "047",
      name: "Crawford County",
      censusArea: 714.19,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.092861, 41.863374],
          [-95.557277, 41.863449],
          [-95.672771, 41.863147],
          [-95.670822, 42.211408],
          [-95.323497, 42.210932],
          [-95.090851, 42.210405],
          [-95.092861, 41.863374]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19055",
      state: "19",
      county: "055",
      name: "Delaware County",
      censusArea: 577.756,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.363263, 42.296445],
          [-91.596981, 42.296408],
          [-91.607059, 42.643973],
          [-91.132766, 42.645844],
          [-91.130079, 42.295761],
          [-91.363263, 42.296445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19063",
      state: "19",
      county: "063",
      name: "Emmet County",
      censusArea: 395.883,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.854555, 43.500614],
          [-94.615916, 43.500544],
          [-94.565665, 43.50033],
          [-94.560838, 43.500377],
          [-94.47042, 43.50034],
          [-94.447048, 43.500639],
          [-94.442848, 43.500583],
          [-94.443137, 43.255014],
          [-94.913723, 43.255054],
          [-94.914843, 43.499103],
          [-94.914634, 43.50045],
          [-94.914523, 43.50045],
          [-94.887291, 43.500502],
          [-94.874235, 43.500557],
          [-94.872725, 43.500564],
          [-94.860192, 43.500546],
          [-94.857867, 43.500615],
          [-94.854555, 43.500614]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19067",
      state: "19",
      county: "067",
      name: "Floyd County",
      censusArea: 500.632,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.025144, 42.907549],
          [-93.024092, 43.212812],
          [-92.554381, 43.212813],
          [-92.554211, 42.907112],
          [-93.025144, 42.907549]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19079",
      state: "19",
      county: "079",
      name: "Hamilton County",
      censusArea: 576.751,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.69839, 42.209339],
          [-93.931581, 42.210095],
          [-93.931684, 42.472011],
          [-93.971583, 42.558139],
          [-93.500212, 42.557836],
          [-93.499485, 42.5577],
          [-93.462635, 42.470802],
          [-93.463043, 42.209302],
          [-93.69839, 42.209339]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19087",
      state: "19",
      county: "087",
      name: "Henry County",
      censusArea: 434.328,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.37071, 41.074112],
          [-91.372095, 40.812631],
          [-91.409324, 40.812682],
          [-91.718787, 40.813544],
          [-91.718268, 40.900805],
          [-91.715893, 41.162822],
          [-91.485717, 41.162405],
          [-91.370298, 41.161247],
          [-91.37071, 41.074112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19095",
      state: "19",
      county: "095",
      name: "Iowa County",
      censusArea: 586.457,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.297494, 41.50979],
          [-92.298164, 41.862779],
          [-91.831379, 41.861851],
          [-91.829231, 41.511457],
          [-91.946043, 41.510749],
          [-92.297494, 41.50979]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19105",
      state: "19",
      county: "105",
      name: "Jones County",
      censusArea: 575.621,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.365789, 41.94741],
          [-91.363263, 42.296445],
          [-91.130079, 42.295761],
          [-90.89802, 42.295236],
          [-90.898373, 42.033548],
          [-90.898484, 41.946245],
          [-91.365789, 41.94741]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19113",
      state: "19",
      county: "113",
      name: "Linn County",
      censusArea: 716.88,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.365789, 41.94741],
          [-91.366079, 41.860068],
          [-91.831379, 41.861851],
          [-91.831089, 42.299063],
          [-91.596981, 42.296408],
          [-91.363263, 42.296445],
          [-91.365789, 41.94741]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19121",
      state: "19",
      county: "121",
      name: "Madison County",
      censusArea: 561.006,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.241637, 41.157134],
          [-94.241593, 41.503679],
          [-93.896807, 41.503098],
          [-93.790612, 41.511916],
          [-93.789196, 41.162035],
          [-93.898813, 41.15661],
          [-94.014189, 41.156762],
          [-94.241637, 41.157134]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19125",
      state: "19",
      county: "125",
      name: "Marion County",
      censusArea: 554.534,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.327886, 41.160659],
          [-93.328407, 41.490921],
          [-93.328486, 41.49134],
          [-93.328614, 41.507824],
          [-92.871421, 41.508522],
          [-92.869771, 41.161066],
          [-93.099217, 41.160867],
          [-93.327886, 41.160659]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19137",
      state: "19",
      county: "137",
      name: "Montgomery County",
      censusArea: 424.097,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.927587, 41.158508],
          [-94.928459, 40.900653],
          [-95.384964, 40.90155],
          [-95.384349, 41.160011],
          [-95.155851, 41.159236],
          [-95.154115, 41.159145],
          [-94.927587, 41.158508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19141",
      state: "19",
      county: "141",
      name: "Obrien County",
      censusArea: 573.035,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.388078, 43.255221],
          [-95.388182, 42.909897],
          [-95.859377, 42.909097],
          [-95.861914, 43.257566],
          [-95.388078, 43.255221]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19151",
      state: "19",
      county: "151",
      name: "Pocahontas County",
      censusArea: 577.241,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.443083, 42.645164],
          [-94.443024, 42.558843],
          [-94.914485, 42.560309],
          [-94.91389, 42.9097],
          [-94.442954, 42.908073],
          [-94.443083, 42.645164]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19161",
      state: "19",
      county: "161",
      name: "Sac County",
      censusArea: 575.012,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.914485, 42.560309],
          [-94.914487, 42.47419],
          [-94.856702, 42.473967],
          [-94.858412, 42.209692],
          [-95.090851, 42.210405],
          [-95.323497, 42.210932],
          [-95.322357, 42.474737],
          [-95.387445, 42.474796],
          [-95.38801, 42.561742],
          [-94.914485, 42.560309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19169",
      state: "19",
      county: "169",
      name: "Story County",
      censusArea: 572.818,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.231858, 41.862711],
          [-93.347933, 41.863104],
          [-93.698032, 41.86337],
          [-93.69839, 42.209339],
          [-93.463043, 42.209302],
          [-93.231722, 42.208886],
          [-93.231858, 41.862711]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19175",
      state: "19",
      county: "175",
      name: "Union County",
      censusArea: 423.645,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.241637, 41.157134],
          [-94.014189, 41.156762],
          [-94.014803, 40.897031],
          [-94.470779, 40.899502],
          [-94.470603, 41.157566],
          [-94.241637, 41.157134]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19181",
      state: "19",
      county: "181",
      name: "Warren County",
      censusArea: 569.827,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.327886, 41.160659],
          [-93.557556, 41.161271],
          [-93.789196, 41.162035],
          [-93.790612, 41.511916],
          [-93.41226, 41.505549],
          [-93.328407, 41.490921],
          [-93.327886, 41.160659]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19195",
      state: "19",
      county: "195",
      name: "Worth County",
      censusArea: 400.123,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.049192, 43.499571],
          [-93.024429, 43.499572],
          [-93.024348, 43.499572],
          [-93.024143, 43.255538],
          [-93.497635, 43.255468],
          [-93.49735, 43.499456],
          [-93.488261, 43.499417],
          [-93.482009, 43.499482],
          [-93.472804, 43.4994],
          [-93.468563, 43.499473],
          [-93.428509, 43.499478],
          [-93.399035, 43.499485],
          [-93.2718, 43.499356],
          [-93.228861, 43.499567],
          [-93.049192, 43.499571]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20005",
      state: "20",
      county: "005",
      name: "Atchison County",
      censusArea: 431.172,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.069592, 39.615106],
          [-95.107454, 39.573843],
          [-95.113077, 39.559133],
          [-95.113557, 39.553941],
          [-95.109304, 39.542285],
          [-95.102888, 39.533347],
          [-94.969061, 39.418876],
          [-95.180891, 39.419218],
          [-95.570351, 39.41905],
          [-95.564126, 39.652872],
          [-95.33974, 39.65298],
          [-95.115189, 39.652549],
          [-95.069592, 39.615106]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20015",
      state: "20",
      county: "015",
      name: "Butler County",
      censusArea: 1429.863,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.522782, 38.08637],
          [-96.523755, 37.955899],
          [-96.5253, 37.607015],
          [-96.52569, 37.476405],
          [-97.153331, 37.475544],
          [-97.152476, 37.912733],
          [-97.152913, 38.087704],
          [-96.840772, 38.085622],
          [-96.522782, 38.08637]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27073",
      state: "27",
      county: "073",
      name: "Lac Qui Parle County",
      censusArea: 765.021,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.452092, 44.977494],
          [-96.45224, 45.042347],
          [-96.452177, 45.050185],
          [-96.45221, 45.051602],
          [-96.452219, 45.093836],
          [-96.452026, 45.095138],
          [-96.452418, 45.122677],
          [-96.452353, 45.124071],
          [-96.452304, 45.178563],
          [-96.452162, 45.203109],
          [-96.452152, 45.204849],
          [-96.452315, 45.208986],
          [-96.452948, 45.268925],
          [-96.28347, 45.246972],
          [-96.103614, 45.176675],
          [-96.084646, 45.165762],
          [-96.075686, 45.165935],
          [-96.053511, 45.156453],
          [-96.048217, 45.15843],
          [-96.036698, 45.152206],
          [-95.737015, 44.93618],
          [-95.737309, 44.891479],
          [-95.847452, 44.891799],
          [-95.849009, 44.805347],
          [-96.45156, 44.805569],
          [-96.452009, 44.89008],
          [-96.451853, 44.906672],
          [-96.452047, 44.910695],
          [-96.452347, 44.962734],
          [-96.452092, 44.977475],
          [-96.452092, 44.977494]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27083",
      state: "27",
      county: "083",
      name: "Lyon County",
      censusArea: 714.559,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.063778, 44.196425],
          [-96.07858, 44.19662],
          [-96.09295, 44.630486],
          [-95.604001, 44.629945],
          [-95.594817, 44.542305],
          [-95.59357, 44.19553],
          [-96.063778, 44.196425]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27085",
      state: "27",
      county: "085",
      name: "Mc Leod County",
      censusArea: 491.471,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.502395, 44.892295],
          [-94.501885, 44.979695],
          [-94.256074, 44.979465],
          [-94.012236, 44.978712],
          [-94.010494, 44.717464],
          [-94.254076, 44.717855],
          [-94.254719, 44.630311],
          [-94.497835, 44.629921],
          [-94.498074, 44.717171],
          [-94.49773, 44.892334],
          [-94.502395, 44.892295]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27093",
      state: "27",
      county: "093",
      name: "Meeker County",
      censusArea: 608.178,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.256074, 44.979465],
          [-94.501885, 44.979695],
          [-94.502395, 44.892295],
          [-94.758189, 44.892097],
          [-94.76308, 45.3261],
          [-94.383556, 45.326705],
          [-94.383449, 45.282797],
          [-94.260821, 45.283941],
          [-94.256074, 44.979465]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27103",
      state: "27",
      county: "103",
      name: "Nicollet County",
      censusArea: 448.494,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.78063, 44.45665],
          [-94.6242, 44.45603],
          [-93.92955, 44.45664],
          [-93.932685, 44.344885],
          [-94.011834, 44.239192],
          [-94.055349, 44.154018],
          [-94.371731, 44.264448],
          [-94.522958, 44.365391],
          [-94.666062, 44.394048],
          [-94.78063, 44.45665]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27113",
      state: "27",
      county: "113",
      name: "Pennington County",
      censusArea: 616.57,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.500843, 48.174108],
          [-95.593788, 48.172916],
          [-95.592487, 48.020558],
          [-95.582887, 48.020557],
          [-95.582155, 47.933966],
          [-95.709619, 47.935805],
          [-95.70984, 47.96433],
          [-96.48255, 47.963595],
          [-96.500843, 48.174108]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27121",
      state: "27",
      county: "121",
      name: "Pope County",
      censusArea: 669.713,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.747402, 45.412313],
          [-95.758508, 45.759932],
          [-95.13967, 45.758891],
          [-95.13173, 45.412402],
          [-95.255206, 45.412525],
          [-95.747402, 45.412313]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27127",
      state: "27",
      county: "127",
      name: "Redwood County",
      censusArea: 878.572,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.865824, 44.498218],
          [-94.866259, 44.282372],
          [-95.10748, 44.281768],
          [-95.108777, 44.195044],
          [-95.462547, 44.195942],
          [-95.59357, 44.19553],
          [-95.594817, 44.542305],
          [-95.3604, 44.541762],
          [-95.360177, 44.69854],
          [-95.132652, 44.596443],
          [-94.865824, 44.498218]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27147",
      state: "27",
      county: "147",
      name: "Steele County",
      censusArea: 429.645,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.04591, 44.196716],
          [-93.04557, 43.850021],
          [-93.045966, 43.848467],
          [-93.049413, 43.848454],
          [-93.049524, 43.848443],
          [-93.406642, 43.84812],
          [-93.406565, 44.196377],
          [-93.04591, 44.196716]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27153",
      state: "27",
      county: "153",
      name: "Todd County",
      censusArea: 944.982,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.64399, 45.77395],
          [-94.766899, 45.773911],
          [-95.139731, 45.773416],
          [-95.14588, 46.106761],
          [-95.15557, 46.36888],
          [-94.731442, 46.368605],
          [-94.722769, 46.352321],
          [-94.653462, 46.348678],
          [-94.64399, 45.77395]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27161",
      state: "27",
      county: "161",
      name: "Waseca County",
      censusArea: 423.356,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.767971, 44.195836],
          [-93.727389, 44.196104],
          [-93.646696, 44.195993],
          [-93.52523, 44.195975],
          [-93.406565, 44.196377],
          [-93.406642, 43.84812],
          [-93.64829, 43.848139],
          [-93.767761, 43.847956],
          [-93.767971, 44.195836]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28003",
      state: "28",
      county: "003",
      name: "Alcorn County",
      censusArea: 400.038,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.786612, 34.995252],
          [-88.469877, 34.996033],
          [-88.469801, 34.996052],
          [-88.380508, 34.99561],
          [-88.363531, 34.99559],
          [-88.365472, 34.755604],
          [-88.718508, 34.756781],
          [-88.752956, 34.858775],
          [-88.823473, 34.858629],
          [-88.823049, 34.995157],
          [-88.786612, 34.995252]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28013",
      state: "28",
      county: "013",
      name: "Calhoun County",
      censusArea: 586.573,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.507135, 33.721819],
          [-89.507353, 33.867469],
          [-89.509748, 34.162082],
          [-89.250072, 34.161022],
          [-89.245485, 34.161186],
          [-89.244436, 34.073942],
          [-89.139158, 34.074116],
          [-89.137876, 33.812144],
          [-89.191781, 33.738602],
          [-89.191924, 33.722158],
          [-89.507135, 33.721819]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28017",
      state: "28",
      county: "017",
      name: "Chickasaw County",
      censusArea: 501.779,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.824523, 34.074948],
          [-88.725125, 34.074815],
          [-88.718964, 34.074521],
          [-88.719979, 33.81133],
          [-88.931176, 33.812374],
          [-88.931604, 33.754312],
          [-89.034498, 33.73943],
          [-89.191781, 33.738602],
          [-89.137876, 33.812144],
          [-89.139158, 34.074116],
          [-88.824523, 34.074948]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28029",
      state: "28",
      county: "029",
      name: "Copiah County",
      censusArea: 777.244,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.736824, 31.698491],
          [-90.736282, 31.786134],
          [-90.716785, 31.844583],
          [-90.718297, 32.048352],
          [-90.230331, 32.049728],
          [-90.132552, 31.856923],
          [-90.122565, 31.75268],
          [-90.245191, 31.717524],
          [-90.279953, 31.699865],
          [-90.736824, 31.698491]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28037",
      state: "28",
      county: "037",
      name: "Franklin County",
      censusArea: 563.779,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.73733, 31.611124],
          [-90.633231, 31.611409],
          [-90.633302, 31.349306],
          [-91.018012, 31.347502],
          [-91.095398, 31.320975],
          [-91.15815, 31.346695],
          [-91.153864, 31.610068],
          [-90.73733, 31.611124]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28049",
      state: "28",
      county: "049",
      name: "Hinds County",
      censusArea: 869.741,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.450037, 32.573783],
          [-90.44978, 32.487983],
          [-90.243641, 32.487533],
          [-90.243764, 32.400059],
          [-90.146688, 32.399859],
          [-90.141232, 32.399495],
          [-90.065697, 32.399529],
          [-90.160477, 32.30834],
          [-90.24538, 32.171396],
          [-90.230331, 32.049728],
          [-90.718297, 32.048352],
          [-90.728622, 32.225119],
          [-90.694517, 32.352996],
          [-90.636162, 32.374498],
          [-90.553821, 32.507574],
          [-90.450037, 32.573783]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28061",
      state: "28",
      county: "061",
      name: "Jasper County",
      censusArea: 676.243,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.321127, 31.885149],
          [-89.322692, 32.223472],
          [-89.117675, 32.223972],
          [-88.913847, 32.224186],
          [-88.910459, 31.826649],
          [-88.926058, 31.825658],
          [-88.932017, 31.825472],
          [-88.943355, 31.824565],
          [-89.31656, 31.80209],
          [-89.317086, 31.874305],
          [-89.321127, 31.885149]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28065",
      state: "28",
      county: "065",
      name: "Jefferson Davis County",
      censusArea: 408.443,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.95894, 31.390489],
          [-89.976052, 31.522477],
          [-89.974586, 31.761655],
          [-89.755103, 31.774654],
          [-89.755614, 31.609751],
          [-89.586919, 31.513444],
          [-89.586819, 31.433688],
          [-89.654212, 31.433805],
          [-89.824236, 31.434093],
          [-89.823332, 31.390021],
          [-89.95894, 31.390489]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28075",
      state: "28",
      county: "075",
      name: "Lauderdale County",
      censusArea: 703.634,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.914516, 32.576955],
          [-88.388739, 32.578116],
          [-88.403789, 32.449885],
          [-88.403789, 32.44977],
          [-88.421453, 32.30868],
          [-88.431707, 32.227662],
          [-88.913847, 32.224186],
          [-88.914516, 32.576955]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22121",
      state: "22",
      county: "121",
      name: "W. Baton Rouge County",
      lsad: "Parish",
      censusArea: 192.394,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.414924, 30.535166],
          [-91.33087, 30.658677],
          [-91.322256, 30.656025],
          [-91.310061, 30.653961],
          [-91.309395, 30.651996],
          [-91.303767, 30.652029],
          [-91.301179, 30.651572],
          [-91.298918, 30.650462],
          [-91.297658, 30.649548],
          [-91.262734, 30.505238],
          [-91.197208, 30.507088],
          [-91.241508, 30.357592],
          [-91.142105, 30.323293],
          [-91.31166, 30.322471],
          [-91.485089, 30.497138],
          [-91.414924, 30.535166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22127",
      state: "22",
      county: "127",
      name: "Winn County",
      lsad: "Parish",
      censusArea: 950.086,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.362608, 31.796633],
          [-92.619224, 31.797149],
          [-92.619976, 31.709572],
          [-92.973529, 31.708922],
          [-92.9668, 31.781299],
          [-92.872174, 31.945856],
          [-92.93908, 32.147942],
          [-92.814737, 32.146907],
          [-92.312218, 32.146123],
          [-92.312623, 31.927382],
          [-92.362608, 31.796633]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23009",
      state: "23",
      county: "009",
      name: "Hancock County",
      censusArea: 1586.893,
      stateName: "Maine"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-68.499465, 44.12419],
            [-68.492892, 44.116921],
            [-68.491521, 44.109833],
            [-68.502942, 44.099722],
            [-68.51706, 44.10341],
            [-68.518703, 44.113222],
            [-68.511266, 44.125082],
            [-68.506979, 44.127237],
            [-68.499465, 44.12419]
          ]
        ],
        [
          [
            [-68.358388, 44.125082],
            [-68.35301, 44.127884],
            [-68.346724, 44.127749],
            [-68.330716, 44.110598],
            [-68.331032, 44.10758],
            [-68.338012, 44.101473],
            [-68.365176, 44.101464],
            [-68.376593, 44.112207],
            [-68.376591, 44.113762],
            [-68.375382, 44.11646],
            [-68.365514, 44.124079],
            [-68.358388, 44.125082]
          ]
        ],
        [
          [
            [-68.453236, 44.189998],
            [-68.437789, 44.188216],
            [-68.424441, 44.190753],
            [-68.416434, 44.187047],
            [-68.408207, 44.176298],
            [-68.384903, 44.154955],
            [-68.396634, 44.14069],
            [-68.427534, 44.119266],
            [-68.438518, 44.11618],
            [-68.448646, 44.125581],
            [-68.447505, 44.133493],
            [-68.456813, 44.145268],
            [-68.479934, 44.1478],
            [-68.484696, 44.146495],
            [-68.496639, 44.146855],
            [-68.502096, 44.152388],
            [-68.500817, 44.160026],
            [-68.495511, 44.162429],
            [-68.474365, 44.181875],
            [-68.453236, 44.189998]
          ]
        ],
        [
          [
            [-68.680773, 44.279242],
            [-68.675416, 44.279753],
            [-68.668213, 44.276511],
            [-68.658849, 44.268588],
            [-68.623554, 44.255622],
            [-68.611669, 44.244818],
            [-68.605906, 44.230772],
            [-68.612749, 44.207722],
            [-68.624994, 44.197637],
            [-68.625715, 44.194756],
            [-68.619592, 44.189354],
            [-68.618511, 44.186472],
            [-68.618872, 44.18107],
            [-68.643002, 44.15766],
            [-68.652366, 44.153698],
            [-68.670014, 44.151537],
            [-68.671454, 44.138572],
            [-68.675056, 44.137131],
            [-68.681899, 44.138212],
            [-68.686581, 44.147216],
            [-68.692343, 44.153698],
            [-68.700987, 44.15838],
            [-68.709631, 44.158741],
            [-68.713232, 44.160541],
            [-68.716474, 44.162702],
            [-68.720435, 44.169185],
            [-68.718995, 44.183231],
            [-68.715033, 44.191154],
            [-68.714313, 44.20376],
            [-68.721156, 44.212404],
            [-68.722956, 44.219607],
            [-68.722956, 44.223568],
            [-68.718635, 44.228611],
            [-68.711792, 44.228971],
            [-68.700627, 44.234013],
            [-68.694144, 44.248779],
            [-68.680458, 44.262105],
            [-68.677577, 44.268948],
            [-68.677577, 44.275431],
            [-68.680773, 44.279242]
          ]
        ],
        [
          [
            [-68.355279, 44.199096],
            [-68.333227, 44.207308],
            [-68.32423, 44.205732],
            [-68.31606, 44.200244],
            [-68.314789, 44.197157],
            [-68.318476, 44.196608],
            [-68.321178, 44.199032],
            [-68.332639, 44.192131],
            [-68.339029, 44.171839],
            [-68.347416, 44.169459],
            [-68.378872, 44.184222],
            [-68.371235, 44.193003],
            [-68.364469, 44.197534],
            [-68.355279, 44.199096]
          ]
        ],
        [
          [
            [-68.472831, 44.219767],
            [-68.460205, 44.212498],
            [-68.453843, 44.201683],
            [-68.454224, 44.199534],
            [-68.459182, 44.197681],
            [-68.48452, 44.202886],
            [-68.487227, 44.209517],
            [-68.482726, 44.227058],
            [-68.480565, 44.228591],
            [-68.470323, 44.22832],
            [-68.468572, 44.223999],
            [-68.472831, 44.219767]
          ]
        ],
        [
          [
            [-68.792139, 44.237819],
            [-68.769833, 44.222787],
            [-68.769047, 44.213351],
            [-68.780055, 44.203129],
            [-68.789884, 44.203915],
            [-68.801285, 44.208633],
            [-68.809149, 44.212565],
            [-68.815439, 44.214137],
            [-68.822909, 44.216496],
            [-68.829593, 44.21689],
            [-68.837849, 44.227112],
            [-68.839422, 44.236547],
            [-68.833524, 44.240872],
            [-68.827627, 44.242838],
            [-68.825631, 44.242556],
            [-68.792139, 44.237819]
          ]
        ],
        [
          [
            [-68.23638, 44.266254],
            [-68.214641, 44.263156],
            [-68.211329, 44.257074],
            [-68.212474, 44.255405],
            [-68.221383, 44.257254],
            [-68.231469, 44.256016],
            [-68.23713, 44.25343],
            [-68.24031, 44.251622],
            [-68.241545, 44.247543],
            [-68.240806, 44.239723],
            [-68.248913, 44.235443],
            [-68.266669, 44.234033],
            [-68.274427, 44.237099],
            [-68.276857, 44.240794],
            [-68.274719, 44.258675],
            [-68.262128, 44.260774],
            [-68.254153, 44.257836],
            [-68.246598, 44.257836],
            [-68.241142, 44.260354],
            [-68.23638, 44.266254]
          ]
        ],
        [
          [
            [-68.498637, 44.369686],
            [-68.478785, 44.319563],
            [-68.489641, 44.313705],
            [-68.515173, 44.324797],
            [-68.530394, 44.333583],
            [-68.528635, 44.344605],
            [-68.520671, 44.358357],
            [-68.52193, 44.367591],
            [-68.518573, 44.381022],
            [-68.512697, 44.384799],
            [-68.50766, 44.385219],
            [-68.501364, 44.382281],
            [-68.498637, 44.369686]
          ]
        ],
        [
          [
            [-68.841821, 44.611062],
            [-68.846945, 44.625307],
            [-68.822416, 44.664005],
            [-68.811815, 44.670465],
            [-68.815793, 44.683754],
            [-68.69093, 44.721668],
            [-68.647134, 44.703636],
            [-68.616673, 44.740992],
            [-68.528542, 44.752167],
            [-68.445219, 44.763014],
            [-68.454889, 44.80155],
            [-68.477952, 44.889091],
            [-68.501095, 44.980718],
            [-68.375892, 44.997373],
            [-68.396903, 45.083296],
            [-68.270497, 45.100121],
            [-68.295438, 45.190443],
            [-68.303714, 45.221531],
            [-68.054985, 45.252577],
            [-67.981851, 44.955885],
            [-68.105017, 44.939088],
            [-67.993551, 44.592162],
            [-67.957089, 44.415408],
            [-67.961613, 44.4125],
            [-67.961613, 44.39907],
            [-67.978876, 44.387034],
            [-67.985668, 44.386917],
            [-67.997288, 44.399909],
            [-68.000646, 44.406624],
            [-68.006102, 44.409562],
            [-68.010719, 44.407464],
            [-68.019533, 44.396971],
            [-68.01399, 44.390255],
            [-68.034223, 44.360456],
            [-68.039679, 44.360876],
            [-68.044296, 44.357938],
            [-68.044716, 44.351222],
            [-68.043037, 44.343667],
            [-68.049334, 44.33073],
            [-68.060356, 44.331988],
            [-68.067047, 44.335692],
            [-68.076066, 44.347925],
            [-68.077873, 44.373047],
            [-68.086268, 44.376405],
            [-68.090045, 44.371369],
            [-68.092983, 44.370949],
            [-68.103818, 44.385111],
            [-68.11229, 44.401588],
            [-68.11271, 44.421314],
            [-68.116487, 44.429289],
            [-68.119845, 44.445658],
            [-68.119425, 44.459508],
            [-68.115228, 44.467903],
            [-68.117746, 44.475038],
            [-68.123203, 44.478815],
            [-68.150904, 44.482383],
            [-68.159298, 44.479445],
            [-68.162656, 44.477346],
            [-68.163075, 44.473149],
            [-68.17105, 44.470211],
            [-68.194554, 44.47189],
            [-68.189517, 44.478605],
            [-68.189937, 44.484901],
            [-68.192036, 44.487419],
            [-68.213861, 44.492456],
            [-68.223934, 44.487],
            [-68.227292, 44.479865],
            [-68.224354, 44.464335],
            [-68.22939, 44.463496],
            [-68.2445, 44.471051],
            [-68.252474, 44.483222],
            [-68.261708, 44.484062],
            [-68.268004, 44.47147],
            [-68.270522, 44.459718],
            [-68.281015, 44.451324],
            [-68.298223, 44.449225],
            [-68.299063, 44.437893],
            [-68.294865, 44.432857],
            [-68.268423, 44.440411],
            [-68.247438, 44.433276],
            [-68.2445, 44.429919],
            [-68.24366, 44.420685],
            [-68.249956, 44.417747],
            [-68.249956, 44.414809],
            [-68.21554, 44.390466],
            [-68.209664, 44.392984],
            [-68.20354, 44.392365],
            [-68.196937, 44.386352],
            [-68.184532, 44.369145],
            [-68.174687, 44.343604],
            [-68.173608, 44.328397],
            [-68.191924, 44.306675],
            [-68.233435, 44.288578],
            [-68.275139, 44.288895],
            [-68.289409, 44.283858],
            [-68.298223, 44.276303],
            [-68.298643, 44.26665],
            [-68.297641, 44.263035],
            [-68.295265, 44.261722],
            [-68.290818, 44.247673],
            [-68.317588, 44.225101],
            [-68.339498, 44.222893],
            [-68.343132, 44.229505],
            [-68.365364, 44.237871],
            [-68.369759, 44.243311],
            [-68.377982, 44.247563],
            [-68.389848, 44.247066],
            [-68.401268, 44.252244],
            [-68.41965, 44.274612],
            [-68.421302, 44.284468],
            [-68.426107, 44.295102],
            [-68.430946, 44.298624],
            [-68.430853, 44.312609],
            [-68.411965, 44.322262],
            [-68.409027, 44.32562],
            [-68.409867, 44.329397],
            [-68.421619, 44.336113],
            [-68.421471, 44.337754],
            [-68.409867, 44.356259],
            [-68.406089, 44.356679],
            [-68.396552, 44.363941],
            [-68.395516, 44.369561],
            [-68.398035, 44.376191],
            [-68.367565, 44.39071],
            [-68.36372, 44.388935],
            [-68.360318, 44.389674],
            [-68.3581, 44.392337],
            [-68.359082, 44.402847],
            [-68.372445, 44.42369],
            [-68.3791, 44.430049],
            [-68.387678, 44.430936],
            [-68.390932, 44.427387],
            [-68.392559, 44.41807],
            [-68.416412, 44.397973],
            [-68.421783, 44.396411],
            [-68.427874, 44.3968],
            [-68.433901, 44.401534],
            [-68.432556, 44.426594],
            [-68.429648, 44.439136],
            [-68.439281, 44.448043],
            [-68.448006, 44.449497],
            [-68.455095, 44.447498],
            [-68.460003, 44.443317],
            [-68.46382, 44.436592],
            [-68.458849, 44.412141],
            [-68.464106, 44.398078],
            [-68.464262, 44.391081],
            [-68.461072, 44.385639],
            [-68.461072, 44.378504],
            [-68.466109, 44.377245],
            [-68.47828, 44.378084],
            [-68.483317, 44.388157],
            [-68.480798, 44.397391],
            [-68.472824, 44.404106],
            [-68.480379, 44.432647],
            [-68.485415, 44.434326],
            [-68.494649, 44.429709],
            [-68.499686, 44.414179],
            [-68.505562, 44.411661],
            [-68.51452, 44.41334],
            [-68.529905, 44.39907],
            [-68.534522, 44.397811],
            [-68.555088, 44.403687],
            [-68.560964, 44.402847],
            [-68.565161, 44.39907],
            [-68.56642, 44.394453],
            [-68.564741, 44.385219],
            [-68.559285, 44.374307],
            [-68.550051, 44.371788],
            [-68.545434, 44.355],
            [-68.553873, 44.346256],
            [-68.563209, 44.333039],
            [-68.566936, 44.317603],
            [-68.566203, 44.313007],
            [-68.564005, 44.308022],
            [-68.556236, 44.300819],
            [-68.538595, 44.299902],
            [-68.531532, 44.290388],
            [-68.532266, 44.28634],
            [-68.528611, 44.276117],
            [-68.519516, 44.265046],
            [-68.519819, 44.260209],
            [-68.529802, 44.249594],
            [-68.528153, 44.241263],
            [-68.52348, 44.235819],
            [-68.525302, 44.227554],
            [-68.534595, 44.229331],
            [-68.550802, 44.236534],
            [-68.551162, 44.238335],
            [-68.562687, 44.248059],
            [-68.572772, 44.252741],
            [-68.603385, 44.27471],
            [-68.61563, 44.275431],
            [-68.626075, 44.280473],
            [-68.627515, 44.284435],
            [-68.630036, 44.286235],
            [-68.682979, 44.299201],
            [-68.725657, 44.321591],
            [-68.733004, 44.328388],
            [-68.746164, 44.331148],
            [-68.762021, 44.329597],
            [-68.766197, 44.327015],
            [-68.771489, 44.320523],
            [-68.795063, 44.30786],
            [-68.827197, 44.31216],
            [-68.828377, 44.316549],
            [-68.825419, 44.334547],
            [-68.821311, 44.349594],
            [-68.817647, 44.353093],
            [-68.814811, 44.362194],
            [-68.818703, 44.375077],
            [-68.821767, 44.40894],
            [-68.815325, 44.42808],
            [-68.801634, 44.434803],
            [-68.785898, 44.462611],
            [-68.783679, 44.473879],
            [-68.796206, 44.471437],
            [-68.841821, 44.611062]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23029",
      state: "23",
      county: "029",
      name: "Washington County",
      censusArea: 2562.66,
      stateName: "Maine"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-67.619761, 44.519754],
            [-67.61541, 44.521973],
            [-67.587738, 44.516196],
            [-67.582113, 44.513459],
            [-67.589259, 44.50084],
            [-67.590627, 44.49415],
            [-67.580288, 44.488068],
            [-67.562651, 44.472104],
            [-67.569189, 44.455531],
            [-67.571774, 44.453403],
            [-67.574206, 44.45173],
            [-67.588346, 44.449754],
            [-67.592755, 44.458572],
            [-67.604919, 44.502056],
            [-67.607199, 44.503576],
            [-67.614954, 44.503576],
            [-67.619211, 44.506009],
            [-67.619761, 44.519754]
          ]
        ],
        [
          [
            [-67.805483, 45.680241],
            [-67.803313, 45.677886],
            [-67.754245, 45.667791],
            [-67.64581, 45.613597],
            [-67.561359, 45.594906],
            [-67.51858, 45.587925],
            [-67.499444, 45.587014],
            [-67.490923, 45.591488],
            [-67.488452, 45.594643],
            [-67.491061, 45.598917],
            [-67.489793, 45.60118],
            [-67.476704, 45.604157],
            [-67.455406, 45.604665],
            [-67.449674, 45.60286],
            [-67.425452, 45.579086],
            [-67.423646, 45.572153],
            [-67.420875, 45.55056],
            [-67.425674, 45.502917],
            [-67.448729, 45.510576],
            [-67.463481, 45.508516],
            [-67.503771, 45.488522],
            [-67.473366, 45.425328],
            [-67.460281, 45.419594],
            [-67.430001, 45.392965],
            [-67.422153, 45.379661],
            [-67.429161, 45.344247],
            [-67.460101, 45.301179],
            [-67.465833, 45.297223],
            [-67.485683, 45.291433],
            [-67.489333, 45.281282],
            [-67.46357, 45.244097],
            [-67.405208, 45.165686],
            [-67.354306, 45.129886],
            [-67.345004, 45.12621],
            [-67.339869, 45.125594],
            [-67.305472, 45.144826],
            [-67.302568, 45.161348],
            [-67.290603, 45.187589],
            [-67.283619, 45.192022],
            [-67.271076, 45.191081],
            [-67.161247, 45.162879],
            [-67.128935, 45.132168],
            [-67.112414, 45.112323],
            [-67.090786, 45.068721],
            [-67.105899, 45.065786],
            [-67.117688, 45.05673],
            [-67.099749, 45.04501],
            [-67.082074, 45.029608],
            [-67.074914, 45.019254],
            [-67.072753, 45.008329],
            [-67.068274, 45.001014],
            [-67.05461, 44.986764],
            [-67.038299, 44.945433],
            [-67.033474, 44.939923],
            [-67.002118, 44.918836],
            [-66.990937, 44.917835],
            [-66.984466, 44.912557],
            [-66.983558, 44.903277],
            [-66.985901, 44.89715],
            [-66.989235, 44.89648],
            [-66.990351, 44.882551],
            [-66.981008, 44.862813],
            [-66.978142, 44.856963],
            [-66.99296, 44.849181],
            [-66.996523, 44.844654],
            [-66.986318, 44.820657],
            [-66.975009, 44.815495],
            [-66.966468, 44.819063],
            [-66.952112, 44.82007],
            [-66.949895, 44.817419],
            [-66.950569, 44.814539],
            [-66.961068, 44.807269],
            [-66.970026, 44.805713],
            [-66.97626, 44.808315],
            [-66.979708, 44.80736],
            [-66.989351, 44.79878],
            [-66.995154, 44.791073],
            [-67.01995, 44.771427],
            [-67.02615, 44.768199],
            [-67.04335, 44.765071],
            [-67.05516, 44.771442],
            [-67.062239, 44.769543],
            [-67.063308, 44.758238],
            [-67.073439, 44.741957],
            [-67.083477, 44.739899],
            [-67.092542, 44.742693],
            [-67.098931, 44.741311],
            [-67.103957, 44.717444],
            [-67.128792, 44.695421],
            [-67.139209, 44.693849],
            [-67.148061, 44.684114],
            [-67.155119, 44.66944],
            [-67.154479, 44.668114],
            [-67.169857, 44.662105],
            [-67.181785, 44.663699],
            [-67.186612, 44.66265],
            [-67.192068, 44.655515],
            [-67.191438, 44.64775],
            [-67.189427, 44.645533],
            [-67.213025, 44.63922],
            [-67.234275, 44.637201],
            [-67.24726, 44.641664],
            [-67.251247, 44.640825],
            [-67.274122, 44.626345],
            [-67.27706, 44.61795],
            [-67.273076, 44.610873],
            [-67.27916, 44.606782],
            [-67.293403, 44.599265],
            [-67.302427, 44.597323],
            [-67.314938, 44.598215],
            [-67.32297, 44.609394],
            [-67.322491, 44.612458],
            [-67.310745, 44.613212],
            [-67.293665, 44.634316],
            [-67.292462, 44.648455],
            [-67.298449, 44.654377],
            [-67.309627, 44.659316],
            [-67.307909, 44.691295],
            [-67.300144, 44.696752],
            [-67.299176, 44.705705],
            [-67.308538, 44.707454],
            [-67.347782, 44.69948],
            [-67.355966, 44.69906],
            [-67.376742, 44.681852],
            [-67.381149, 44.66947],
            [-67.37905, 44.665483],
            [-67.374014, 44.662965],
            [-67.367298, 44.652472],
            [-67.363158, 44.631825],
            [-67.368269, 44.624672],
            [-67.377554, 44.619757],
            [-67.386605, 44.626974],
            [-67.388704, 44.626554],
            [-67.395839, 44.612914],
            [-67.398987, 44.602631],
            [-67.405492, 44.594236],
            [-67.411815, 44.596954],
            [-67.418923, 44.60347],
            [-67.420602, 44.607877],
            [-67.428367, 44.609136],
            [-67.443686, 44.605779],
            [-67.447464, 44.60326],
            [-67.448513, 44.600322],
            [-67.457747, 44.598014],
            [-67.492373, 44.61795],
            [-67.493632, 44.628863],
            [-67.505804, 44.636837],
            [-67.522802, 44.63306],
            [-67.524061, 44.626554],
            [-67.530777, 44.621938],
            [-67.533925, 44.621518],
            [-67.54022, 44.626345],
            [-67.543368, 44.626554],
            [-67.551133, 44.621938],
            [-67.552392, 44.619629],
            [-67.575056, 44.560659],
            [-67.569836, 44.556788],
            [-67.562321, 44.539435],
            [-67.568159, 44.531117],
            [-67.648506, 44.525403],
            [-67.653123, 44.525823],
            [-67.656901, 44.535896],
            [-67.660678, 44.537575],
            [-67.685861, 44.537155],
            [-67.696354, 44.533798],
            [-67.702649, 44.527922],
            [-67.698872, 44.51575],
            [-67.703489, 44.504837],
            [-67.71419, 44.495238],
            [-67.722876, 44.498524],
            [-67.733986, 44.496252],
            [-67.743353, 44.497418],
            [-67.742789, 44.506176],
            [-67.740076, 44.508944],
            [-67.742942, 44.526453],
            [-67.753854, 44.543661],
            [-67.758891, 44.546599],
            [-67.767285, 44.548278],
            [-67.774001, 44.547438],
            [-67.779457, 44.543661],
            [-67.781975, 44.534008],
            [-67.781556, 44.520577],
            [-67.79726, 44.520685],
            [-67.802541, 44.523934],
            [-67.805479, 44.52981],
            [-67.805479, 44.536946],
            [-67.808837, 44.544081],
            [-67.829823, 44.557512],
            [-67.839896, 44.558771],
            [-67.844513, 44.556252],
            [-67.845772, 44.551636],
            [-67.843254, 44.542822],
            [-67.856684, 44.523934],
            [-67.853746, 44.497492],
            [-67.851228, 44.492456],
            [-67.851648, 44.484901],
            [-67.855579, 44.478676],
            [-67.860994, 44.477576],
            [-67.866801, 44.471812],
            [-67.868774, 44.465272],
            [-67.868875, 44.456881],
            [-67.851764, 44.428695],
            [-67.851697, 44.424282],
            [-67.855108, 44.419434],
            [-67.868856, 44.424672],
            [-67.878509, 44.435585],
            [-67.887323, 44.433066],
            [-67.887323, 44.426351],
            [-67.892033, 44.409668],
            [-67.899571, 44.394078],
            [-67.911667, 44.419216],
            [-67.913346, 44.430128],
            [-67.92132, 44.433066],
            [-67.926357, 44.431807],
            [-67.930554, 44.428869],
            [-67.927616, 44.421314],
            [-67.931453, 44.411848],
            [-67.936531, 44.411187],
            [-67.947342, 44.415858],
            [-67.955737, 44.416278],
            [-67.957089, 44.415408],
            [-67.993551, 44.592162],
            [-68.105017, 44.939088],
            [-67.981851, 44.955885],
            [-68.054985, 45.252577],
            [-67.93976, 45.267845],
            [-68.048012, 45.639897],
            [-67.805483, 45.680241]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24001",
      state: "24",
      county: "001",
      name: "Allegany County",
      censusArea: 424.158,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.468639, 39.516789],
          [-78.565929, 39.519444],
          [-78.655984, 39.534695],
          [-78.657324, 39.535087],
          [-78.676219, 39.540622],
          [-78.689455, 39.54577],
          [-78.760196, 39.582154],
          [-78.76749, 39.587487],
          [-78.772048, 39.593833],
          [-78.769029, 39.599871],
          [-78.760497, 39.609984],
          [-78.787461, 39.627492],
          [-78.824788, 39.590233],
          [-78.874744, 39.522611],
          [-78.968996, 39.441543],
          [-79.025681, 39.465539],
          [-79.04244, 39.479339],
          [-79.067072, 39.474658],
          [-79.067933, 39.479189],
          [-78.931175, 39.722775],
          [-78.808387, 39.722726],
          [-78.380599, 39.722554],
          [-78.342834, 39.722539],
          [-78.334044, 39.63577],
          [-78.355218, 39.640576],
          [-78.43025, 39.62329],
          [-78.457187, 39.587379],
          [-78.458456, 39.581158],
          [-78.454376, 39.574319],
          [-78.450207, 39.570889],
          [-78.432033, 39.561053],
          [-78.436939, 39.538959],
          [-78.468639, 39.516789]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24003",
      state: "24",
      county: "003",
      name: "Anne Arundel County",
      censusArea: 414.902,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.438845, 39.0529],
          [-76.438928, 39.052788],
          [-76.437044, 39.051698],
          [-76.422933, 39.043536],
          [-76.407398, 39.034551],
          [-76.407213, 39.034444],
          [-76.407205, 39.034439],
          [-76.405338, 39.03336],
          [-76.405081, 39.033211],
          [-76.405021, 39.033124],
          [-76.4026, 39.029609],
          [-76.39778, 39.022611],
          [-76.397738, 39.022482],
          [-76.396417, 39.018449],
          [-76.396395, 39.01838],
          [-76.395639, 39.016074],
          [-76.395338, 39.015154],
          [-76.394699, 39.0132],
          [-76.394358, 39.01216],
          [-76.39408, 39.011311],
          [-76.412326, 38.996832],
          [-76.414631, 38.995002],
          [-76.421535, 38.989524],
          [-76.422181, 38.989011],
          [-76.427301, 38.987827],
          [-76.448928, 38.982823],
          [-76.448981, 38.982811],
          [-76.449188, 38.982092],
          [-76.450152, 38.978751],
          [-76.450481, 38.977612],
          [-76.451599, 38.976767],
          [-76.454581, 38.974512],
          [-76.474198, 38.972647],
          [-76.474198, 38.972646],
          [-76.47445, 38.970683],
          [-76.474882, 38.967312],
          [-76.474743, 38.966895],
          [-76.473243, 38.962397],
          [-76.471281, 38.956512],
          [-76.463081, 38.948612],
          [-76.460234, 38.948505],
          [-76.457781, 38.948412],
          [-76.457229, 38.947875],
          [-76.451695, 38.94249],
          [-76.45028, 38.941113],
          [-76.450756, 38.940412],
          [-76.46188, 38.924013],
          [-76.461747, 38.923674],
          [-76.460272, 38.919909],
          [-76.458991, 38.916639],
          [-76.458759, 38.916045],
          [-76.45808, 38.914313],
          [-76.459479, 38.907113],
          [-76.460253, 38.907152],
          [-76.460716, 38.907175],
          [-76.462312, 38.907256],
          [-76.46528, 38.907406],
          [-76.46938, 38.907613],
          [-76.46948, 38.911513],
          [-76.475761, 38.914469],
          [-76.49368, 38.910013],
          [-76.493791, 38.904225],
          [-76.493798, 38.903865],
          [-76.493806, 38.903443],
          [-76.493856, 38.900846],
          [-76.49388, 38.899614],
          [-76.49278, 38.895614],
          [-76.49088, 38.894514],
          [-76.490403, 38.892254],
          [-76.490339, 38.891955],
          [-76.489933, 38.890031],
          [-76.489713, 38.888991],
          [-76.489675, 38.888811],
          [-76.489667, 38.888772],
          [-76.48938, 38.887414],
          [-76.49068, 38.884814],
          [-76.491107, 38.884492],
          [-76.491442, 38.884239],
          [-76.4944, 38.88201],
          [-76.494588, 38.881868],
          [-76.494858, 38.881665],
          [-76.498467, 38.878944],
          [-76.507858, 38.871866],
          [-76.511135, 38.869396],
          [-76.519442, 38.863135],
          [-76.516944, 38.851157],
          [-76.516582, 38.851026],
          [-76.509285, 38.848388],
          [-76.509211, 38.848416],
          [-76.499158, 38.852156],
          [-76.497562, 38.852749],
          [-76.496579, 38.853115],
          [-76.496474, 38.852953],
          [-76.493639, 38.848595],
          [-76.492761, 38.847246],
          [-76.492017, 38.846102],
          [-76.491581, 38.845433],
          [-76.491494, 38.845299],
          [-76.489919, 38.842878],
          [-76.489878, 38.842815],
          [-76.489878, 38.838715],
          [-76.490107, 38.838176],
          [-76.491387, 38.835161],
          [-76.491606, 38.834644],
          [-76.491684, 38.834462],
          [-76.491831, 38.834116],
          [-76.496965, 38.822022],
          [-76.497692, 38.820309],
          [-76.498878, 38.817516],
          [-76.499056, 38.817258],
          [-76.50291, 38.811648],
          [-76.50653, 38.80638],
          [-76.506876, 38.805875],
          [-76.509314, 38.802328],
          [-76.510078, 38.801216],
          [-76.51113, 38.800769],
          [-76.514296, 38.799425],
          [-76.51583, 38.798774],
          [-76.523405, 38.795557],
          [-76.524679, 38.795016],
          [-76.524806, 38.794871],
          [-76.524999, 38.79465],
          [-76.525039, 38.794605],
          [-76.52521, 38.794409],
          [-76.525531, 38.794043],
          [-76.527455, 38.791844],
          [-76.527479, 38.791816],
          [-76.527463, 38.791667],
          [-76.527372, 38.790788],
          [-76.52737, 38.790774],
          [-76.527105, 38.788223],
          [-76.527045, 38.787645],
          [-76.526979, 38.787016],
          [-76.527315, 38.78666],
          [-76.528136, 38.78579],
          [-76.532487, 38.781181],
          [-76.533975, 38.779604],
          [-76.535207, 38.778298],
          [-76.535352, 38.778145],
          [-76.535379, 38.778116],
          [-76.535426, 38.778095],
          [-76.535642, 38.778],
          [-76.536284, 38.777719],
          [-76.536819, 38.777484],
          [-76.536849, 38.777471],
          [-76.53723, 38.777304],
          [-76.539088, 38.776488],
          [-76.542973, 38.774783],
          [-76.544372, 38.774169],
          [-76.544413, 38.774151],
          [-76.554443, 38.769749],
          [-76.554693, 38.769639],
          [-76.559697, 38.767443],
          [-76.559884, 38.767361],
          [-76.559778, 38.766342],
          [-76.558933, 38.758177],
          [-76.557535, 38.744687],
          [-76.5573, 38.744229],
          [-76.557091, 38.743823],
          [-76.556954, 38.743554],
          [-76.553978, 38.737756],
          [-76.552743, 38.73535],
          [-76.551971, 38.734636],
          [-76.544561, 38.727784],
          [-76.544475, 38.727705],
          [-76.543853, 38.727736],
          [-76.54357, 38.72775],
          [-76.543435, 38.727757],
          [-76.542053, 38.727826],
          [-76.539769, 38.72794],
          [-76.529868, 38.728435],
          [-76.529284, 38.728137],
          [-76.52718, 38.727062],
          [-76.526655, 38.72443],
          [-76.529237, 38.713561],
          [-76.615902, 38.720804],
          [-76.623079, 38.767278],
          [-76.686203, 38.74861],
          [-76.686358, 38.748475],
          [-76.710639, 38.815694],
          [-76.693286, 38.951416],
          [-76.840362, 39.103142],
          [-76.824608, 39.117217],
          [-76.786966, 39.128782],
          [-76.776962, 39.150883],
          [-76.763738, 39.166229],
          [-76.740631, 39.179221],
          [-76.722606, 39.182857],
          [-76.719217, 39.19148],
          [-76.697085, 39.214092],
          [-76.69675, 39.217399],
          [-76.687745, 39.221292],
          [-76.680177, 39.220163],
          [-76.668744, 39.228989],
          [-76.66173, 39.231135],
          [-76.647219, 39.229036],
          [-76.639404, 39.224029],
          [-76.630674, 39.224187],
          [-76.618612, 39.237398],
          [-76.611695, 39.2344],
          [-76.582386, 39.207151],
          [-76.550086, 39.197207],
          [-76.533103, 39.20763],
          [-76.533085, 39.207608],
          [-76.533349, 39.207172],
          [-76.53476, 39.204841],
          [-76.535385, 39.203808],
          [-76.534185, 39.190608],
          [-76.525785, 39.177908],
          [-76.525454, 39.177746],
          [-76.525031, 39.17754],
          [-76.524221, 39.177144],
          [-76.52027, 39.175214],
          [-76.519292, 39.174736],
          [-76.511834, 39.171093],
          [-76.508384, 39.169408],
          [-76.500926, 39.161286],
          [-76.500512, 39.161362],
          [-76.484023, 39.164407],
          [-76.483845, 39.164334],
          [-76.475983, 39.161109],
          [-76.474807, 39.159436],
          [-76.473802, 39.158007],
          [-76.471483, 39.154709],
          [-76.468898, 39.153161],
          [-76.459119, 39.147304],
          [-76.458873, 39.147157],
          [-76.458649, 39.147023],
          [-76.45835, 39.146844],
          [-76.458192, 39.146749],
          [-76.452782, 39.143509],
          [-76.44011, 39.137305],
          [-76.430946, 39.132818],
          [-76.43013, 39.132419],
          [-76.428681, 39.131709],
          [-76.432481, 39.126709],
          [-76.432702, 39.120752],
          [-76.432981, 39.113209],
          [-76.432942, 39.113098],
          [-76.427196, 39.096685],
          [-76.427065, 39.096309],
          [-76.426456, 39.094571],
          [-76.426283, 39.094076],
          [-76.426276, 39.094057],
          [-76.423609, 39.086438],
          [-76.423321, 39.085615],
          [-76.423271, 39.085473],
          [-76.422714, 39.083881],
          [-76.422212, 39.082448],
          [-76.42208, 39.08207],
          [-76.42186, 39.081442],
          [-76.423081, 39.07421],
          [-76.432374, 39.061648],
          [-76.438845, 39.0529]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24009",
      state: "24",
      county: "009",
      name: "Calvert County",
      censusArea: 213.152,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.403101, 38.343157],
          [-76.40494, 38.341089],
          [-76.409291, 38.325891],
          [-76.405716, 38.317794],
          [-76.406617, 38.317455],
          [-76.41326, 38.315356],
          [-76.41578, 38.314559],
          [-76.418789, 38.313609],
          [-76.42179, 38.312661],
          [-76.421816, 38.312653],
          [-76.423408, 38.31215],
          [-76.425005, 38.311646],
          [-76.425033, 38.311636],
          [-76.434128, 38.308763],
          [-76.444886, 38.305363],
          [-76.445334, 38.30521],
          [-76.445622, 38.305205],
          [-76.447132, 38.305176],
          [-76.447253, 38.305174],
          [-76.447913, 38.305162],
          [-76.448019, 38.30516],
          [-76.448074, 38.305159],
          [-76.44808, 38.305159],
          [-76.448084, 38.305159],
          [-76.448085, 38.305158],
          [-76.448087, 38.305158],
          [-76.448089, 38.305158],
          [-76.4481, 38.305158],
          [-76.448107, 38.305158],
          [-76.448146, 38.305157],
          [-76.448365, 38.305153],
          [-76.448846, 38.305144],
          [-76.449024, 38.305141],
          [-76.453453, 38.305057],
          [-76.454295, 38.305332],
          [-76.458306, 38.306641],
          [-76.461609, 38.30854],
          [-76.464047, 38.310946],
          [-76.464365, 38.31126],
          [-76.464481, 38.311437],
          [-76.465623, 38.313162],
          [-76.465981, 38.313702],
          [-76.466434, 38.314833],
          [-76.466882, 38.315947],
          [-76.467101, 38.316494],
          [-76.467104, 38.316501],
          [-76.46711, 38.316516],
          [-76.467111, 38.316518],
          [-76.467155, 38.316628],
          [-76.467165, 38.316653],
          [-76.467185, 38.316703],
          [-76.467329, 38.317061],
          [-76.4676, 38.317736],
          [-76.467872, 38.318412],
          [-76.467959, 38.318628],
          [-76.468045, 38.318844],
          [-76.46848, 38.319928],
          [-76.46962, 38.322367],
          [-76.469869, 38.3229],
          [-76.471712, 38.324867],
          [-76.47247, 38.325256],
          [-76.472557, 38.325299],
          [-76.472644, 38.325342],
          [-76.473026, 38.32553],
          [-76.473581, 38.325804],
          [-76.475027, 38.326516],
          [-76.476192, 38.326726],
          [-76.478044, 38.32706],
          [-76.483167, 38.326668],
          [-76.487575, 38.326257],
          [-76.488421, 38.3267],
          [-76.48852, 38.326752],
          [-76.488976, 38.327163],
          [-76.489211, 38.327375],
          [-76.489232, 38.327453],
          [-76.489381, 38.328005],
          [-76.489029, 38.328874],
          [-76.486925, 38.332422],
          [-76.486431, 38.333335],
          [-76.486084, 38.333977],
          [-76.485737, 38.334619],
          [-76.485678, 38.33473],
          [-76.485618, 38.33484],
          [-76.485047, 38.335895],
          [-76.484336, 38.33721],
          [-76.483481, 38.340093],
          [-76.483432, 38.340415],
          [-76.483375, 38.340797],
          [-76.48313, 38.342446],
          [-76.483154, 38.342668],
          [-76.483223, 38.34329],
          [-76.483449, 38.34531],
          [-76.483575, 38.345855],
          [-76.484085, 38.34807],
          [-76.485188, 38.351097],
          [-76.485755, 38.352265],
          [-76.48606, 38.352891],
          [-76.486364, 38.353517],
          [-76.486618, 38.354038],
          [-76.489235, 38.358393],
          [-76.491157, 38.361246],
          [-76.493466, 38.363996],
          [-76.495675, 38.3666],
          [-76.497293, 38.368347],
          [-76.498484, 38.369633],
          [-76.499675, 38.370919],
          [-76.499675, 38.370906],
          [-76.500449, 38.371635],
          [-76.501475, 38.372497],
          [-76.502376, 38.372889],
          [-76.503802, 38.373359],
          [-76.50504, 38.373947],
          [-76.505576, 38.374403],
          [-76.506041, 38.374799],
          [-76.506271, 38.375096],
          [-76.506542, 38.37575],
          [-76.507042, 38.376553],
          [-76.507399, 38.376983],
          [-76.507831, 38.377503],
          [-76.510057, 38.378903],
          [-76.510961, 38.379188],
          [-76.511296, 38.379294],
          [-76.511631, 38.379399],
          [-76.512609, 38.379708],
          [-76.51356, 38.380413],
          [-76.514949, 38.380835],
          [-76.516663, 38.381668],
          [-76.518939, 38.383363],
          [-76.52002, 38.38433],
          [-76.520078, 38.384382],
          [-76.521153, 38.385704],
          [-76.522594, 38.387997],
          [-76.523635, 38.389211],
          [-76.524138, 38.389775],
          [-76.524645, 38.390343],
          [-76.525826, 38.39113],
          [-76.526904, 38.392012],
          [-76.527968, 38.39308],
          [-76.528305, 38.393406],
          [-76.529924, 38.394972],
          [-76.53127, 38.395934],
          [-76.533073, 38.397223],
          [-76.534075, 38.397766],
          [-76.53566, 38.397727],
          [-76.536028, 38.397661],
          [-76.536676, 38.397545],
          [-76.537325, 38.397429],
          [-76.537701, 38.397361],
          [-76.54027, 38.396631],
          [-76.542273, 38.396144],
          [-76.545623, 38.395695],
          [-76.548842, 38.395409],
          [-76.550652, 38.395248],
          [-76.551129, 38.395212],
          [-76.553435, 38.395043],
          [-76.554929, 38.395035],
          [-76.556652, 38.395295],
          [-76.558388, 38.395861],
          [-76.559227, 38.396378],
          [-76.559957, 38.397321],
          [-76.560617, 38.398707],
          [-76.561353, 38.400376],
          [-76.562002, 38.402286],
          [-76.562054, 38.402409],
          [-76.562178, 38.4027],
          [-76.562179, 38.402702],
          [-76.562185, 38.402716],
          [-76.562362, 38.403133],
          [-76.562669, 38.403858],
          [-76.563902, 38.406002],
          [-76.565615, 38.407051],
          [-76.567296, 38.407494],
          [-76.568129, 38.407713],
          [-76.571807, 38.408823],
          [-76.573311, 38.409365],
          [-76.573981, 38.409607],
          [-76.574427, 38.409768],
          [-76.575543, 38.41017],
          [-76.578631, 38.411283],
          [-76.584586, 38.414739],
          [-76.586742, 38.415991],
          [-76.588899, 38.417243],
          [-76.591674, 38.418854],
          [-76.593918, 38.420952],
          [-76.595223, 38.422172],
          [-76.597528, 38.423425],
          [-76.599445, 38.423896],
          [-76.600651, 38.424018],
          [-76.601125, 38.424066],
          [-76.601599, 38.424113],
          [-76.60219, 38.424173],
          [-76.605265, 38.423833],
          [-76.606263, 38.423973],
          [-76.607855, 38.424197],
          [-76.608373, 38.424445],
          [-76.608893, 38.42503],
          [-76.610135, 38.426486],
          [-76.610605, 38.42702],
          [-76.610799, 38.427239],
          [-76.61146, 38.427991],
          [-76.612999, 38.428837],
          [-76.613984, 38.429863],
          [-76.614797, 38.431355],
          [-76.61582, 38.433591],
          [-76.616427, 38.434612],
          [-76.617222, 38.43533],
          [-76.61782, 38.435602],
          [-76.618428, 38.436054],
          [-76.618831, 38.436354],
          [-76.619985, 38.437575],
          [-76.621546, 38.438921],
          [-76.622068, 38.440152],
          [-76.622486, 38.441366],
          [-76.623616, 38.442869],
          [-76.624192, 38.443498],
          [-76.624681, 38.44367],
          [-76.626272, 38.444648],
          [-76.627864, 38.445625],
          [-76.628002, 38.445707],
          [-76.628951, 38.446264],
          [-76.630009, 38.44729],
          [-76.632226, 38.449313],
          [-76.632348, 38.449425],
          [-76.632387, 38.44946],
          [-76.632388, 38.449461],
          [-76.632397, 38.449469],
          [-76.632398, 38.44947],
          [-76.632399, 38.449471],
          [-76.632402, 38.449474],
          [-76.632409, 38.449481],
          [-76.632415, 38.449486],
          [-76.632427, 38.449497],
          [-76.63245, 38.449518],
          [-76.63252, 38.449582],
          [-76.632601, 38.449659],
          [-76.634487, 38.451457],
          [-76.635464, 38.452173],
          [-76.636393, 38.452855],
          [-76.639174, 38.454969],
          [-76.639263, 38.455037],
          [-76.639814, 38.455456],
          [-76.644319, 38.458268],
          [-76.64648, 38.459593],
          [-76.649131, 38.460822],
          [-76.651307, 38.461829],
          [-76.652588, 38.462422],
          [-76.653783, 38.463294],
          [-76.653872, 38.463359],
          [-76.654146, 38.463908],
          [-76.654345, 38.465174],
          [-76.653656, 38.466988],
          [-76.653007, 38.468051],
          [-76.652977, 38.468101],
          [-76.652095, 38.469519],
          [-76.651956, 38.471261],
          [-76.652142, 38.473753],
          [-76.65265, 38.474552],
          [-76.652894, 38.474958],
          [-76.653383, 38.475774],
          [-76.65343, 38.475852],
          [-76.653906, 38.476645],
          [-76.653909, 38.47665],
          [-76.653967, 38.476746],
          [-76.656646, 38.48121],
          [-76.659326, 38.485675],
          [-76.660341, 38.487368],
          [-76.66112, 38.488356],
          [-76.661214, 38.488476],
          [-76.661309, 38.488596],
          [-76.663333, 38.491153],
          [-76.66536, 38.493657],
          [-76.665949, 38.494389],
          [-76.666427, 38.494785],
          [-76.667081, 38.495172],
          [-76.672663, 38.49816],
          [-76.67305, 38.498548],
          [-76.673584, 38.499085],
          [-76.674118, 38.499622],
          [-76.662995, 38.526616],
          [-76.675457, 38.535876],
          [-76.672642, 38.546818],
          [-76.672274, 38.552846],
          [-76.675468, 38.558824],
          [-76.678891, 38.567954],
          [-76.677799, 38.572374],
          [-76.680702, 38.580499],
          [-76.67948, 38.585211],
          [-76.675166, 38.592106],
          [-76.672557, 38.599271],
          [-76.673479, 38.607249],
          [-76.672334, 38.617533],
          [-76.677562, 38.626234],
          [-76.679355, 38.627949],
          [-76.686138, 38.630182],
          [-76.692021, 38.634932],
          [-76.692853, 38.636437],
          [-76.69163, 38.648506],
          [-76.685127, 38.654771],
          [-76.683186, 38.658813],
          [-76.683329, 38.661178],
          [-76.68737, 38.663793],
          [-76.694236, 38.663841],
          [-76.700381, 38.667022],
          [-76.700667, 38.670131],
          [-76.690461, 38.673683],
          [-76.687424, 38.678746],
          [-76.687147, 38.681371],
          [-76.69549, 38.685781],
          [-76.698429, 38.690443],
          [-76.697932, 38.694782],
          [-76.694983, 38.700307],
          [-76.694946, 38.702468],
          [-76.697743, 38.70703],
          [-76.70196, 38.710864],
          [-76.693589, 38.724761],
          [-76.695555, 38.731544],
          [-76.694996, 38.735075],
          [-76.687009, 38.735897],
          [-76.684018, 38.738348],
          [-76.686358, 38.748475],
          [-76.686203, 38.74861],
          [-76.623079, 38.767278],
          [-76.615902, 38.720804],
          [-76.529237, 38.713561],
          [-76.529532, 38.712317],
          [-76.529721, 38.711523],
          [-76.530575, 38.707929],
          [-76.530799, 38.706983],
          [-76.530824, 38.706881],
          [-76.532465, 38.699974],
          [-76.532537, 38.699669],
          [-76.532527, 38.698207],
          [-76.532432, 38.6836],
          [-76.532409, 38.680064],
          [-76.532398, 38.678363],
          [-76.532056, 38.676936],
          [-76.530454, 38.670263],
          [-76.52785, 38.659416],
          [-76.527428, 38.657655],
          [-76.525007, 38.647568],
          [-76.52462, 38.645956],
          [-76.524122, 38.645044],
          [-76.517862, 38.633586],
          [-76.516988, 38.631985],
          [-76.515554, 38.629361],
          [-76.511278, 38.615745],
          [-76.512452, 38.609827],
          [-76.512583, 38.609168],
          [-76.514812, 38.59793],
          [-76.515741, 38.593248],
          [-76.51576, 38.593154],
          [-76.515764, 38.593131],
          [-76.51634, 38.590229],
          [-76.515106, 38.555763],
          [-76.515376, 38.553895],
          [-76.515409, 38.553667],
          [-76.515415, 38.553623],
          [-76.51552, 38.5529],
          [-76.517506, 38.539149],
          [-76.515754, 38.52926],
          [-76.515706, 38.528988],
          [-76.51535, 38.528093],
          [-76.507489, 38.5083],
          [-76.50686, 38.506716],
          [-76.506023, 38.50461],
          [-76.499256, 38.493559],
          [-76.498792, 38.4928],
          [-76.498222, 38.491869],
          [-76.492699, 38.482849],
          [-76.491642, 38.481944],
          [-76.473372, 38.46629],
          [-76.471253, 38.464474],
          [-76.467342, 38.461123],
          [-76.467194, 38.460996],
          [-76.467186, 38.46099],
          [-76.46401, 38.458268],
          [-76.461078, 38.455756],
          [-76.455799, 38.451233],
          [-76.456002, 38.447891],
          [-76.45414, 38.44588],
          [-76.450937, 38.442422],
          [-76.445376, 38.439012],
          [-76.442441, 38.437212],
          [-76.442431, 38.437206],
          [-76.44118, 38.436439],
          [-76.438919, 38.435053],
          [-76.436271, 38.433429],
          [-76.435674, 38.432893],
          [-76.415384, 38.414682],
          [-76.40271, 38.396003],
          [-76.393378, 38.389477],
          [-76.388348, 38.387781],
          [-76.388328, 38.387727],
          [-76.386229, 38.382013],
          [-76.386243, 38.381631],
          [-76.386931, 38.363184],
          [-76.386957, 38.362462],
          [-76.387002, 38.361267],
          [-76.387408, 38.360811],
          [-76.388806, 38.359238],
          [-76.388915, 38.359115],
          [-76.398261, 38.348602],
          [-76.400871, 38.345666],
          [-76.401532, 38.344923],
          [-76.402043, 38.344348],
          [-76.403101, 38.343157]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24017",
      state: "24",
      county: "017",
      name: "Charles County",
      censusArea: 457.749,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.125191, 38.619096],
          [-77.12463, 38.619778],
          [-77.1302, 38.635017],
          [-77.135901, 38.649817],
          [-77.132501, 38.673816],
          [-77.121101, 38.686616],
          [-77.1059, 38.696815],
          [-77.086113, 38.705792],
          [-77.078599, 38.694114],
          [-77.077027, 38.690168],
          [-77.011028, 38.651975],
          [-76.862955, 38.658649],
          [-76.747335, 38.616677],
          [-76.675457, 38.535876],
          [-76.662995, 38.526616],
          [-76.674118, 38.499622],
          [-76.68382, 38.495251],
          [-76.699317, 38.497125],
          [-76.700122, 38.497634],
          [-76.700275, 38.49794],
          [-76.700012, 38.499129],
          [-76.701033, 38.499646],
          [-76.701475, 38.500195],
          [-76.702203, 38.501668],
          [-76.703316, 38.502401],
          [-76.703727, 38.502297],
          [-76.704443, 38.501835],
          [-76.705353, 38.502019],
          [-76.705541, 38.502959],
          [-76.70652, 38.50425],
          [-76.708448, 38.504838],
          [-76.708441, 38.505545],
          [-76.709169, 38.50736],
          [-76.70995, 38.508065],
          [-76.710413, 38.507453],
          [-76.71053, 38.50917],
          [-76.71184, 38.509871],
          [-76.713459, 38.5095],
          [-76.713835, 38.510482],
          [-76.71526, 38.509498],
          [-76.71592, 38.509474],
          [-76.716407, 38.510112],
          [-76.717758, 38.51038],
          [-76.718537, 38.509784],
          [-76.719146, 38.509825],
          [-76.720401, 38.510692],
          [-76.72265, 38.510684],
          [-76.726627, 38.511769],
          [-76.730088, 38.51183],
          [-76.733669, 38.512464],
          [-76.734922, 38.512189],
          [-76.738652, 38.51234],
          [-76.740048, 38.512576],
          [-76.742151, 38.512487],
          [-76.744138, 38.512191],
          [-76.745861, 38.512627],
          [-76.748052, 38.512469],
          [-76.752051, 38.513733],
          [-76.753855, 38.513525],
          [-76.756546, 38.513532],
          [-76.758, 38.513088],
          [-76.759769, 38.513023],
          [-76.760202, 38.513292],
          [-76.762551, 38.513172],
          [-76.764753, 38.513382],
          [-76.766899, 38.513278],
          [-76.768654, 38.512614],
          [-76.770193, 38.510478],
          [-76.771045, 38.509889],
          [-76.774924, 38.50892],
          [-76.87181, 38.388973],
          [-76.849933, 38.365815],
          [-76.853623, 38.3401],
          [-76.834046, 38.29968],
          [-76.845846, 38.297783],
          [-76.846118, 38.297739],
          [-76.846252, 38.297718],
          [-76.846221, 38.29196],
          [-76.84622, 38.291768],
          [-76.841703, 38.289768],
          [-76.841241, 38.289564],
          [-76.840383, 38.289184],
          [-76.838539, 38.28417],
          [-76.834908, 38.274299],
          [-76.834803, 38.274012],
          [-76.835136, 38.272628],
          [-76.837697, 38.26199],
          [-76.837789, 38.261609],
          [-76.842038, 38.254657],
          [-76.842139, 38.254491],
          [-76.843422, 38.254925],
          [-76.844221, 38.255195],
          [-76.844885, 38.25542],
          [-76.846036, 38.255809],
          [-76.847074, 38.25616],
          [-76.864292, 38.268945],
          [-76.866302, 38.269673],
          [-76.866416, 38.269715],
          [-76.886535, 38.277004],
          [-76.908506, 38.28843],
          [-76.920778, 38.291529],
          [-76.920932, 38.291568],
          [-76.922161, 38.311086],
          [-76.922177, 38.311339],
          [-76.923629, 38.314932],
          [-76.924668, 38.316011],
          [-76.927019, 38.318454],
          [-76.929554, 38.321088],
          [-76.942132, 38.329601],
          [-76.953928, 38.333282],
          [-76.966349, 38.341372],
          [-76.975092, 38.347067],
          [-76.975492, 38.347327],
          [-76.983582, 38.362999],
          [-76.986464, 38.382618],
          [-76.986699, 38.384213],
          [-76.986996, 38.386237],
          [-76.987838, 38.391965],
          [-76.98828, 38.394975],
          [-76.989271, 38.396352],
          [-76.989306, 38.396401],
          [-76.990305, 38.397788],
          [-76.993839, 38.402699],
          [-76.996663, 38.406623],
          [-76.996724, 38.406708],
          [-76.998585, 38.409294],
          [-76.998843, 38.410362],
          [-77.000997, 38.419293],
          [-77.001541, 38.421548],
          [-77.001638, 38.421952],
          [-77.001902, 38.422376],
          [-77.00257, 38.423447],
          [-77.006495, 38.429738],
          [-77.006767, 38.430176],
          [-77.016371, 38.445572],
          [-77.016674, 38.44556],
          [-77.040638, 38.444618],
          [-77.042046, 38.443983],
          [-77.042879, 38.443607],
          [-77.044188, 38.443016],
          [-77.053187, 38.437753],
          [-77.05329, 38.437693],
          [-77.053445, 38.437602],
          [-77.053663, 38.437475],
          [-77.053979, 38.43729],
          [-77.054972, 38.436709],
          [-77.055162, 38.436598],
          [-77.05532, 38.436505],
          [-77.056892, 38.435586],
          [-77.057277, 38.435361],
          [-77.05746, 38.435254],
          [-77.057684, 38.435123],
          [-77.062607, 38.432244],
          [-77.074174, 38.425479],
          [-77.074339, 38.425382],
          [-77.074703, 38.42517],
          [-77.075489, 38.42471],
          [-77.081252, 38.419449],
          [-77.081418, 38.419297],
          [-77.086393, 38.414755],
          [-77.091073, 38.407546],
          [-77.106571, 38.406237],
          [-77.106968, 38.406531],
          [-77.107631, 38.407022],
          [-77.107881, 38.407207],
          [-77.109055, 38.408076],
          [-77.109171, 38.408162],
          [-77.110586, 38.40921],
          [-77.110623, 38.409214],
          [-77.123325, 38.410646],
          [-77.125933, 38.404845],
          [-77.127392, 38.4016],
          [-77.127737, 38.400833],
          [-77.128377, 38.40019],
          [-77.128649, 38.399917],
          [-77.128823, 38.399742],
          [-77.128872, 38.399692],
          [-77.12909, 38.399474],
          [-77.130287, 38.398271],
          [-77.135224, 38.393311],
          [-77.136434, 38.392094],
          [-77.136728, 38.391799],
          [-77.136947, 38.391684],
          [-77.139968, 38.390102],
          [-77.143152, 38.388435],
          [-77.147541, 38.386136],
          [-77.163531, 38.377761],
          [-77.168697, 38.375055],
          [-77.177, 38.370706],
          [-77.1798, 38.369239],
          [-77.182638, 38.367753],
          [-77.183268, 38.367423],
          [-77.184917, 38.366559],
          [-77.18668, 38.365636],
          [-77.189412, 38.364872],
          [-77.205009, 38.360511],
          [-77.207214, 38.359894],
          [-77.207312, 38.359867],
          [-77.207371, 38.359888],
          [-77.208334, 38.360224],
          [-77.212189, 38.361572],
          [-77.216729, 38.363159],
          [-77.216834, 38.363221],
          [-77.236231, 38.374601],
          [-77.250172, 38.382781],
          [-77.250862, 38.384325],
          [-77.25122, 38.385127],
          [-77.253202, 38.389567],
          [-77.256412, 38.396755],
          [-77.257177, 38.39847],
          [-77.261937, 38.409131],
          [-77.264238, 38.414282],
          [-77.26129, 38.42367],
          [-77.259962, 38.427902],
          [-77.259962, 38.435821],
          [-77.26076, 38.438394],
          [-77.263682, 38.44781],
          [-77.266845, 38.458004],
          [-77.269474, 38.466476],
          [-77.274021, 38.481127],
          [-77.27417, 38.481608],
          [-77.27422, 38.48177],
          [-77.274125, 38.482044],
          [-77.271099, 38.490753],
          [-77.263599, 38.512344],
          [-77.257378, 38.521847],
          [-77.254882, 38.52566],
          [-77.246089, 38.539093],
          [-77.242736, 38.544214],
          [-77.237724, 38.55187],
          [-77.236728, 38.552071],
          [-77.226465, 38.554139],
          [-77.221117, 38.555217],
          [-77.209905, 38.56887],
          [-77.205261, 38.574525],
          [-77.183767, 38.600699],
          [-77.175969, 38.604113],
          [-77.169968, 38.60674],
          [-77.169671, 38.60687],
          [-77.167523, 38.60674],
          [-77.163945, 38.606524],
          [-77.163409, 38.606492],
          [-77.16316, 38.606477],
          [-77.162649, 38.606446],
          [-77.161585, 38.606381],
          [-77.148651, 38.6056],
          [-77.146087, 38.606748],
          [-77.135539, 38.611473],
          [-77.129213, 38.614306],
          [-77.129084, 38.614364],
          [-77.128841, 38.61466],
          [-77.12634, 38.6177],
          [-77.125191, 38.619096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24019",
      state: "24",
      county: "019",
      name: "Dorchester County",
      censusArea: 540.765,
      stateName: "Maryland"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-76.048373, 38.12055],
            [-76.056811, 38.125123],
            [-76.061, 38.127002],
            [-76.061111, 38.126963],
            [-76.06147, 38.126839],
            [-76.063661, 38.126078],
            [-76.064683, 38.125723],
            [-76.081127, 38.120014],
            [-76.081587, 38.119854],
            [-76.08523, 38.118589],
            [-76.085311, 38.118561],
            [-76.085542, 38.117869],
            [-76.085885, 38.116844],
            [-76.085947, 38.116658],
            [-76.086224, 38.116534],
            [-76.089018, 38.115287],
            [-76.089166, 38.115221],
            [-76.090872, 38.11446],
            [-76.095548, 38.125123],
            [-76.090649, 38.131185],
            [-76.09064, 38.13124],
            [-76.090527, 38.131919],
            [-76.089017, 38.141033],
            [-76.089918, 38.143837],
            [-76.090216, 38.144765],
            [-76.092334, 38.151355],
            [-76.088639, 38.192649],
            [-76.07147, 38.203502],
            [-76.067797, 38.203539],
            [-76.0671, 38.203546],
            [-76.066994, 38.203547],
            [-76.061906, 38.203599],
            [-76.059731, 38.203621],
            [-76.059562, 38.203622],
            [-76.059218, 38.203626],
            [-76.059158, 38.203626],
            [-76.05902, 38.203628],
            [-76.058925, 38.203629],
            [-76.053753, 38.203681],
            [-76.052165, 38.203697],
            [-76.050511, 38.203714],
            [-76.048945, 38.203729],
            [-76.048786, 38.203731],
            [-76.046539, 38.201549],
            [-76.046273, 38.20129],
            [-76.042691, 38.197811],
            [-76.042615, 38.197737],
            [-76.042385, 38.197515],
            [-76.04228, 38.197412],
            [-76.041952, 38.197094],
            [-76.040718, 38.195896],
            [-76.038929, 38.194158],
            [-76.036757, 38.192049],
            [-76.02858, 38.184107],
            [-76.028411, 38.183943],
            [-76.0269, 38.182476],
            [-76.025899, 38.181504],
            [-76.02217, 38.177882],
            [-76.021941, 38.171976],
            [-76.025892, 38.170946],
            [-76.032767, 38.169154],
            [-76.032816, 38.169141],
            [-76.034038, 38.157902],
            [-76.033978, 38.157636],
            [-76.031945, 38.148631],
            [-76.031868, 38.148288],
            [-76.031442, 38.147612],
            [-76.048373, 38.12055]
          ]
        ],
        [
          [
            [-75.925492, 38.26667],
            [-75.938577, 38.272329],
            [-75.940302, 38.271611],
            [-75.942462, 38.270711],
            [-75.942703, 38.270611],
            [-75.948346, 38.268261],
            [-75.95136, 38.267006],
            [-75.951497, 38.266949],
            [-75.951512, 38.266936],
            [-75.951563, 38.266892],
            [-75.951647, 38.266819],
            [-75.954483, 38.264366],
            [-75.954568, 38.264293],
            [-75.954701, 38.264177],
            [-75.954737, 38.264146],
            [-75.954824, 38.264071],
            [-75.954908, 38.263998],
            [-75.954908, 38.263997],
            [-75.95481, 38.261016],
            [-75.954582, 38.254108],
            [-75.954561, 38.253476],
            [-75.954542, 38.252894],
            [-75.950056, 38.249699],
            [-75.949458, 38.249273],
            [-75.948796, 38.248802],
            [-75.947876, 38.248875],
            [-75.945678, 38.249051],
            [-75.9445, 38.249145],
            [-75.943032, 38.248279],
            [-75.942465, 38.247945],
            [-75.942263, 38.247826],
            [-75.940697, 38.246902],
            [-75.94119, 38.246211],
            [-75.942804, 38.243949],
            [-75.946414, 38.23889],
            [-75.948197, 38.238504],
            [-75.951156, 38.237862],
            [-75.955164, 38.236994],
            [-75.955833, 38.236849],
            [-75.958338, 38.236306],
            [-75.958794, 38.236208],
            [-75.961972, 38.235519],
            [-75.962111, 38.235489],
            [-75.962729, 38.235355],
            [-75.962931, 38.235311],
            [-75.970514, 38.233668],
            [-75.971487, 38.233457],
            [-75.972212, 38.2333],
            [-75.9704, 38.235043],
            [-75.970115, 38.235317],
            [-75.969383, 38.236021],
            [-75.969326, 38.236076],
            [-75.964528, 38.240692],
            [-75.964119, 38.241085],
            [-75.963969, 38.241598],
            [-75.962235, 38.24754],
            [-75.963453, 38.251793],
            [-75.984274, 38.265155],
            [-75.985815, 38.276466],
            [-75.987814, 38.279287],
            [-75.988731, 38.280581],
            [-75.990385, 38.282915],
            [-75.991162, 38.283894],
            [-75.991687, 38.284555],
            [-75.992669, 38.285792],
            [-75.992829, 38.285994],
            [-75.993391, 38.286702],
            [-76.000241, 38.295331],
            [-76.00401, 38.300079],
            [-76.007118, 38.303994],
            [-76.007254, 38.304165],
            [-76.007375, 38.304318],
            [-76.007478, 38.304351],
            [-76.011245, 38.305572],
            [-76.011458, 38.30564],
            [-76.016291, 38.307206],
            [-76.016314, 38.307247],
            [-76.016514, 38.3076],
            [-76.017308, 38.309007],
            [-76.017364, 38.309106],
            [-76.009377, 38.311997],
            [-76.008647, 38.312261],
            [-75.991151, 38.31411],
            [-75.990485, 38.314181],
            [-75.986025, 38.314652],
            [-75.984784, 38.314783],
            [-75.984433, 38.314821],
            [-75.983186, 38.314952],
            [-75.983171, 38.314954],
            [-75.982523, 38.315022],
            [-75.981345, 38.315147],
            [-75.969577, 38.320044],
            [-75.96929, 38.320164],
            [-75.96888, 38.320498],
            [-75.964237, 38.324285],
            [-75.961944, 38.332572],
            [-75.961945, 38.33583],
            [-75.961946, 38.336018],
            [-75.961948, 38.341431],
            [-75.965366, 38.348429],
            [-75.966397, 38.35054],
            [-75.968881, 38.355623],
            [-75.969161, 38.356197],
            [-75.97084, 38.359635],
            [-75.971019, 38.360002],
            [-75.971541, 38.361069],
            [-75.97191, 38.361826],
            [-75.972174, 38.362365],
            [-75.972281, 38.362584],
            [-75.973876, 38.36585],
            [-75.979727, 38.367627],
            [-75.98018, 38.367765],
            [-75.980829, 38.367962],
            [-75.995706, 38.37248],
            [-76.001839, 38.374343],
            [-76.002156, 38.374439],
            [-76.002282, 38.374477],
            [-76.004946, 38.372045],
            [-76.006949, 38.370216],
            [-76.011869, 38.360582],
            [-76.012149, 38.357077],
            [-76.011033, 38.354844],
            [-76.010737, 38.354251],
            [-76.010255, 38.353287],
            [-76.010217, 38.353211],
            [-76.010366, 38.352732],
            [-76.010437, 38.352504],
            [-76.016682, 38.332429],
            [-76.033947, 38.323211],
            [-76.041431, 38.322163],
            [-76.041618, 38.322137],
            [-76.041659, 38.322097],
            [-76.04318, 38.32061],
            [-76.043923, 38.319884],
            [-76.044649, 38.319175],
            [-76.045057, 38.318776],
            [-76.045599, 38.318246],
            [-76.045964, 38.317452],
            [-76.046367, 38.316576],
            [-76.047401, 38.314329],
            [-76.047992, 38.313044],
            [-76.048637, 38.311643],
            [-76.048965, 38.31093],
            [-76.049207, 38.310404],
            [-76.049523, 38.309718],
            [-76.04958, 38.309594],
            [-76.049609, 38.309348],
            [-76.049647, 38.309017],
            [-76.05022, 38.304101],
            [-76.047147, 38.301582],
            [-76.040019, 38.295738],
            [-76.030532, 38.28796],
            [-76.028234, 38.282035],
            [-76.027557, 38.280288],
            [-76.027487, 38.280108],
            [-76.032595, 38.268875],
            [-76.032664, 38.268722],
            [-76.032771, 38.268487],
            [-76.032998, 38.267989],
            [-76.03361, 38.266644],
            [-76.033737, 38.266363],
            [-76.038935, 38.254932],
            [-76.039738, 38.254093],
            [-76.043927, 38.249712],
            [-76.044251, 38.249373],
            [-76.044141, 38.243447],
            [-76.044108, 38.241682],
            [-76.043814, 38.241294],
            [-76.03796, 38.233551],
            [-76.037109, 38.232426],
            [-76.037103, 38.232418],
            [-76.036102, 38.231094],
            [-76.035695, 38.230556],
            [-76.033455, 38.222046],
            [-76.033338, 38.221599],
            [-76.032044, 38.216684],
            [-76.05801, 38.227079],
            [-76.069502, 38.238455],
            [-76.070831, 38.241836],
            [-76.071154, 38.242657],
            [-76.073493, 38.248609],
            [-76.073725, 38.2492],
            [-76.074491, 38.251148],
            [-76.074515, 38.251208],
            [-76.074568, 38.251344],
            [-76.074607, 38.251443],
            [-76.074726, 38.251745],
            [-76.07476, 38.251832],
            [-76.074897, 38.252181],
            [-76.082268, 38.252616],
            [-76.082549, 38.252633],
            [-76.089797, 38.253061],
            [-76.092417, 38.253216],
            [-76.092723, 38.253234],
            [-76.094689, 38.25335],
            [-76.09972, 38.253647],
            [-76.107592, 38.262525],
            [-76.102549, 38.277153],
            [-76.111296, 38.286946],
            [-76.12694, 38.283751],
            [-76.131085, 38.282904],
            [-76.137238, 38.281648],
            [-76.137442, 38.281606],
            [-76.138479, 38.281394],
            [-76.138524, 38.281385],
            [-76.138596, 38.281406],
            [-76.140674, 38.282006],
            [-76.140947, 38.282085],
            [-76.143127, 38.282714],
            [-76.149398, 38.284525],
            [-76.149876, 38.284663],
            [-76.160474, 38.290983],
            [-76.160838, 38.290948],
            [-76.161348, 38.290898],
            [-76.166154, 38.290431],
            [-76.173418, 38.285326],
            [-76.173555, 38.28523],
            [-76.173844, 38.284869],
            [-76.174011, 38.284659],
            [-76.174104, 38.284543],
            [-76.174152, 38.284483],
            [-76.174484, 38.284068],
            [-76.174598, 38.283924],
            [-76.174683, 38.283818],
            [-76.180115, 38.277019],
            [-76.180165, 38.276956],
            [-76.180103, 38.276739],
            [-76.179123, 38.273291],
            [-76.178282, 38.270336],
            [-76.175783, 38.261551],
            [-76.171254, 38.256984],
            [-76.164388, 38.250061],
            [-76.163249, 38.248913],
            [-76.146297, 38.249678],
            [-76.135169, 38.245872],
            [-76.13498, 38.245807],
            [-76.132544, 38.244974],
            [-76.130952, 38.24443],
            [-76.126623, 38.242949],
            [-76.126453, 38.242046],
            [-76.125856, 38.23888],
            [-76.125756, 38.238348],
            [-76.128452, 38.235704],
            [-76.128898, 38.235267],
            [-76.131332, 38.23288],
            [-76.140068, 38.231305],
            [-76.151035, 38.234215],
            [-76.17335, 38.247037],
            [-76.188644, 38.267434],
            [-76.189627, 38.272487],
            [-76.189655, 38.272633],
            [-76.190531, 38.277139],
            [-76.197155, 38.28522],
            [-76.197693, 38.285877],
            [-76.197871, 38.286094],
            [-76.201591, 38.290633],
            [-76.201603, 38.290647],
            [-76.202334, 38.291539],
            [-76.202628, 38.291898],
            [-76.202897, 38.292226],
            [-76.20343, 38.292876],
            [-76.203909, 38.293461],
            [-76.211446, 38.302656],
            [-76.216266, 38.305023],
            [-76.217616, 38.305686],
            [-76.218051, 38.305899],
            [-76.226376, 38.309988],
            [-76.226408, 38.309989],
            [-76.243897, 38.310313],
            [-76.254473, 38.31512],
            [-76.258189, 38.318373],
            [-76.266602, 38.339502],
            [-76.264186, 38.346436],
            [-76.259286, 38.341619],
            [-76.259261, 38.341595],
            [-76.259235, 38.341603],
            [-76.258383, 38.341865],
            [-76.257898, 38.342014],
            [-76.238452, 38.347986],
            [-76.238478, 38.348355],
            [-76.238541, 38.349238],
            [-76.238611, 38.350233],
            [-76.23901, 38.350738],
            [-76.239056, 38.350795],
            [-76.239601, 38.351486],
            [-76.239902, 38.351866],
            [-76.240196, 38.352238],
            [-76.24041, 38.352508],
            [-76.24735, 38.361285],
            [-76.248766, 38.363076],
            [-76.249666, 38.364214],
            [-76.256788, 38.366712],
            [-76.257479, 38.366702],
            [-76.258022, 38.366695],
            [-76.273003, 38.366483],
            [-76.278722, 38.382919],
            [-76.278813, 38.383179],
            [-76.280749, 38.388743],
            [-76.281214, 38.390079],
            [-76.28132, 38.390384],
            [-76.281396, 38.390604],
            [-76.281697, 38.39147],
            [-76.282144, 38.392754],
            [-76.282271, 38.393118],
            [-76.280551, 38.403143],
            [-76.28302, 38.413512],
            [-76.290681, 38.424404],
            [-76.300186, 38.437916],
            [-76.301488, 38.439767],
            [-76.3116, 38.450266],
            [-76.311767, 38.450439],
            [-76.320843, 38.459862],
            [-76.331383, 38.473323],
            [-76.331469, 38.473432],
            [-76.331559, 38.473548],
            [-76.331758, 38.474324],
            [-76.33199, 38.475225],
            [-76.33636, 38.492235],
            [-76.327257, 38.500121],
            [-76.318054, 38.498199],
            [-76.289507, 38.503906],
            [-76.283595, 38.504157],
            [-76.281761, 38.502165],
            [-76.263968, 38.503452],
            [-76.262133, 38.504874],
            [-76.26035, 38.506255],
            [-76.247894, 38.523019],
            [-76.2473, 38.523818],
            [-76.244396, 38.536966],
            [-76.248885, 38.539023],
            [-76.250265, 38.539131],
            [-76.251033, 38.539191],
            [-76.253624, 38.539393],
            [-76.274057, 38.531207],
            [-76.278106, 38.532468],
            [-76.281047, 38.53613],
            [-76.277461, 38.541851],
            [-76.275913, 38.548809],
            [-76.27964, 38.557231],
            [-76.283189, 38.5613],
            [-76.283632, 38.561807],
            [-76.289017, 38.567982],
            [-76.290043, 38.569158],
            [-76.290667, 38.569247],
            [-76.296469, 38.570076],
            [-76.299301, 38.57048],
            [-76.308321, 38.571769],
            [-76.305172, 38.575293],
            [-76.291998, 38.581988],
            [-76.290895, 38.582548],
            [-76.273496, 38.59139],
            [-76.268633, 38.597753],
            [-76.272584, 38.601997],
            [-76.274611, 38.604174],
            [-76.274888, 38.604471],
            [-76.275246, 38.604856],
            [-76.275282, 38.604895],
            [-76.275658, 38.605298],
            [-76.279589, 38.60952],
            [-76.278146, 38.610662],
            [-76.276734, 38.611779],
            [-76.271827, 38.615661],
            [-76.264155, 38.615109],
            [-76.263577, 38.615475],
            [-76.262791, 38.615973],
            [-76.261135, 38.617021],
            [-76.260317, 38.617539],
            [-76.253926, 38.621586],
            [-76.247896, 38.625404],
            [-76.24651, 38.626282],
            [-76.23665, 38.628598],
            [-76.235987, 38.626827],
            [-76.234929, 38.624002],
            [-76.231187, 38.61401],
            [-76.229358, 38.613301],
            [-76.229159, 38.613224],
            [-76.22882, 38.613093],
            [-76.222982, 38.61083],
            [-76.220701, 38.609945],
            [-76.212427, 38.606738],
            [-76.212414, 38.606744],
            [-76.203065, 38.610741],
            [-76.202598, 38.613011],
            [-76.190902, 38.621092],
            [-76.174969, 38.628791],
            [-76.170066, 38.629225],
            [-76.160148, 38.625452],
            [-76.152885, 38.631819],
            [-76.023445, 38.573875],
            [-75.991127, 38.592483],
            [-75.945243, 38.674114],
            [-75.76268, 38.686222],
            [-75.707352, 38.635359],
            [-75.707346, 38.63528],
            [-75.706585, 38.626125],
            [-75.706235, 38.621296],
            [-75.70586, 38.616268],
            [-75.705774, 38.61474],
            [-75.703981, 38.592066],
            [-75.703445, 38.58512],
            [-75.701565, 38.560736],
            [-75.70734, 38.559524],
            [-75.711893, 38.550876],
            [-75.718793, 38.546184],
            [-75.72449, 38.54123],
            [-75.72976, 38.538704],
            [-75.737763, 38.537649],
            [-75.740586, 38.538364],
            [-75.746515, 38.536785],
            [-75.749655, 38.531688],
            [-75.753636, 38.5281],
            [-75.756022, 38.515316],
            [-75.759754, 38.510122],
            [-75.764994, 38.506479],
            [-75.782064, 38.501596],
            [-75.784972, 38.500115],
            [-75.79466, 38.492373],
            [-75.800496, 38.49102],
            [-75.815193, 38.488794],
            [-75.823706, 38.481022],
            [-75.824765, 38.479095],
            [-75.822902, 38.47601],
            [-75.818709, 38.472332],
            [-75.815772, 38.469373],
            [-75.814901, 38.466459],
            [-75.81669, 38.463307],
            [-75.821994, 38.460959],
            [-75.828492, 38.459614],
            [-75.83316, 38.45762],
            [-75.835728, 38.45329],
            [-75.836743, 38.448088],
            [-75.835994, 38.445207],
            [-75.833127, 38.443443],
            [-75.826639, 38.442231],
            [-75.821342, 38.439261],
            [-75.819829, 38.435886],
            [-75.821287, 38.430135],
            [-75.82496, 38.425372],
            [-75.828869, 38.423417],
            [-75.843343, 38.422745],
            [-75.845573, 38.421868],
            [-75.847281, 38.417287],
            [-75.850952, 38.404536],
            [-75.851258, 38.400348],
            [-75.846741, 38.397058],
            [-75.842399, 38.393082],
            [-75.838369, 38.387476],
            [-75.838282, 38.385878],
            [-75.840348, 38.384784],
            [-75.846202, 38.384405],
            [-75.85529, 38.385345],
            [-75.860851, 38.383577],
            [-75.865397, 38.379064],
            [-75.867311, 38.375118],
            [-75.866042, 38.373053],
            [-75.859499, 38.36675],
            [-75.857656, 38.363498],
            [-75.857207, 38.359942],
            [-75.857673, 38.357011],
            [-75.860258, 38.353868],
            [-75.864213, 38.350782],
            [-75.928628, 38.297272],
            [-75.925492, 38.26667]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24029",
      state: "24",
      county: "029",
      name: "Kent County",
      censusArea: 277.03,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.867722, 39.259483],
          [-75.874084, 39.249798],
          [-75.925022, 39.244108],
          [-75.930708, 39.239023],
          [-75.937902, 39.235502],
          [-75.953404, 39.235264],
          [-75.967236, 39.239318],
          [-75.976744, 39.240519],
          [-75.989844, 39.238367],
          [-75.998411, 39.234773],
          [-76.012797, 39.234453],
          [-76.014792, 39.234152],
          [-76.020454, 39.227401],
          [-76.027191, 39.226722],
          [-76.056556, 39.210338],
          [-76.068794, 39.192345],
          [-76.065186, 39.185204],
          [-76.046661, 39.180751],
          [-76.039726, 39.174102],
          [-76.04763, 39.162666],
          [-76.081596, 39.147659],
          [-76.072418, 39.138206],
          [-76.117058, 39.107098],
          [-76.15809, 39.093998],
          [-76.167347, 39.094839],
          [-76.169087, 39.094997],
          [-76.169414, 39.095027],
          [-76.169891, 39.09507],
          [-76.183908, 39.096344],
          [-76.189907, 39.093042],
          [-76.194703, 39.090403],
          [-76.203333, 39.085654],
          [-76.203383, 39.085626],
          [-76.203548, 39.085101],
          [-76.205108, 39.080129],
          [-76.207604, 39.072176],
          [-76.210041, 39.06441],
          [-76.210251, 39.062511],
          [-76.210876, 39.056874],
          [-76.211838, 39.048184],
          [-76.212563, 39.041641],
          [-76.212602, 39.041288],
          [-76.212616, 39.041158],
          [-76.21204, 39.03887],
          [-76.211589, 39.03708],
          [-76.210477, 39.032664],
          [-76.209827, 39.030081],
          [-76.209517, 39.028848],
          [-76.208502, 39.024818],
          [-76.208346, 39.024614],
          [-76.206852, 39.022649],
          [-76.206796, 39.022575],
          [-76.205958, 39.021475],
          [-76.200666, 39.01452],
          [-76.201005, 39.014339],
          [-76.202176, 39.013714],
          [-76.209114, 39.01001],
          [-76.209285, 39.010074],
          [-76.21685, 39.012916],
          [-76.221148, 39.01453],
          [-76.223671, 39.015478],
          [-76.231765, 39.018518],
          [-76.240226, 39.026581],
          [-76.241932, 39.028206],
          [-76.242687, 39.028926],
          [-76.240905, 39.039798],
          [-76.239443, 39.041977],
          [-76.2375, 39.044872],
          [-76.237065, 39.045521],
          [-76.231212, 39.060769],
          [-76.231117, 39.061017],
          [-76.231427, 39.071714],
          [-76.231748, 39.082826],
          [-76.233457, 39.091385],
          [-76.240603, 39.106856],
          [-76.24505, 39.116484],
          [-76.252946, 39.133577],
          [-76.252968, 39.133626],
          [-76.260343, 39.142722],
          [-76.260894, 39.143402],
          [-76.264417, 39.143874],
          [-76.26654, 39.144158],
          [-76.268096, 39.144367],
          [-76.274907, 39.145279],
          [-76.276721, 39.145522],
          [-76.278527, 39.145764],
          [-76.276807, 39.154484],
          [-76.27566, 39.160304],
          [-76.274741, 39.164961],
          [-76.274637, 39.16549],
          [-76.274303, 39.166115],
          [-76.269467, 39.175163],
          [-76.268631, 39.176727],
          [-76.266963, 39.179848],
          [-76.266602, 39.180523],
          [-76.255831, 39.191595],
          [-76.251032, 39.199214],
          [-76.248741, 39.203333],
          [-76.232612, 39.232333],
          [-76.232051, 39.233341],
          [-76.22622, 39.246485],
          [-76.220475, 39.259433],
          [-76.219338, 39.261997],
          [-76.219312, 39.262022],
          [-76.218339, 39.262963],
          [-76.21752, 39.263754],
          [-76.211306, 39.269761],
          [-76.211253, 39.269812],
          [-76.211179, 39.269813],
          [-76.210748, 39.269816],
          [-76.203031, 39.269871],
          [-76.20259, 39.270265],
          [-76.193029, 39.278801],
          [-76.191285, 39.280358],
          [-76.189868, 39.281623],
          [-76.185909, 39.285157],
          [-76.185674, 39.285367],
          [-76.181496, 39.291797],
          [-76.181478, 39.291829],
          [-76.177712, 39.298686],
          [-76.177704, 39.298701],
          [-76.177178, 39.303099],
          [-76.176804, 39.306229],
          [-76.176778, 39.306447],
          [-76.176865, 39.306584],
          [-76.178983, 39.309926],
          [-76.179092, 39.310098],
          [-76.186024, 39.312462],
          [-76.186637, 39.315426],
          [-76.186647, 39.315475],
          [-76.186001, 39.317814],
          [-76.185581, 39.319334],
          [-76.185507, 39.319396],
          [-76.170588, 39.331954],
          [-76.170541, 39.331994],
          [-76.170422, 39.332094],
          [-76.169411, 39.332453],
          [-76.168365, 39.332824],
          [-76.159673, 39.335909],
          [-76.157163, 39.335641],
          [-76.152722, 39.335167],
          [-76.145524, 39.334399],
          [-76.133225, 39.340491],
          [-76.135105, 39.34246],
          [-76.136971, 39.344414],
          [-76.13495, 39.35107],
          [-76.129983, 39.353702],
          [-76.116698, 39.360744],
          [-76.116356, 39.360925],
          [-76.115935, 39.361744],
          [-76.113215, 39.367032],
          [-76.110598, 39.372119],
          [-76.110527, 39.372257],
          [-76.108942, 39.372215],
          [-76.108373, 39.3722],
          [-76.074992, 39.371312],
          [-76.065887, 39.37107],
          [-76.065716, 39.371066],
          [-76.061504, 39.370954],
          [-76.059335, 39.370896],
          [-76.05872, 39.37088],
          [-76.057876, 39.370857],
          [-76.054157, 39.370759],
          [-76.049846, 39.370644],
          [-76.032923, 39.367414],
          [-76.031767, 39.366772],
          [-76.030064, 39.365826],
          [-76.02299, 39.361896],
          [-76.019091, 39.362958],
          [-76.006546, 39.366374],
          [-76.002408, 39.367501],
          [-76.002463, 39.37644],
          [-76.002361, 39.376448],
          [-76.002197, 39.376462],
          [-76.001704, 39.376507],
          [-76.001541, 39.376522],
          [-76.001113, 39.376579],
          [-76.000221, 39.376651],
          [-75.999397, 39.376636],
          [-75.99855, 39.376453],
          [-75.996147, 39.376235],
          [-75.995094, 39.376106],
          [-75.994178, 39.375904],
          [-75.993932, 39.375848],
          [-75.993125, 39.375667],
          [-75.992317, 39.375347],
          [-75.992193, 39.375263],
          [-75.991996, 39.375129],
          [-75.990951, 39.37471],
          [-75.988998, 39.373771],
          [-75.987136, 39.372577],
          [-75.985267, 39.370807],
          [-75.984054, 39.36927],
          [-75.983985, 39.369182],
          [-75.982696, 39.367492],
          [-75.981506, 39.36684],
          [-75.981109, 39.366569],
          [-75.980539, 39.366571],
          [-75.979034, 39.366577],
          [-75.977714, 39.366874],
          [-75.976249, 39.367458],
          [-75.974418, 39.368621],
          [-75.97364, 39.369632],
          [-75.972793, 39.370891],
          [-75.971952, 39.371644],
          [-75.970603, 39.372852],
          [-75.96941, 39.373424],
          [-75.968269, 39.373973],
          [-75.966011, 39.374504],
          [-75.964388, 39.374688],
          [-75.964065, 39.374725],
          [-75.961448, 39.374694],
          [-75.960143, 39.374697],
          [-75.960083, 39.374698],
          [-75.958488, 39.374553],
          [-75.95697, 39.374603],
          [-75.954299, 39.374145],
          [-75.95182, 39.373638],
          [-75.951219, 39.373432],
          [-75.950401, 39.373153],
          [-75.94934, 39.372509],
          [-75.949246, 39.372421],
          [-75.947769, 39.371032],
          [-75.94683, 39.369842],
          [-75.946425, 39.369494],
          [-75.94616, 39.369266],
          [-75.945365, 39.368583],
          [-75.944429, 39.368272],
          [-75.943893, 39.368095],
          [-75.94375, 39.368064],
          [-75.942337, 39.367752],
          [-75.941032, 39.367675],
          [-75.939849, 39.367786],
          [-75.93779, 39.368715],
          [-75.937728, 39.368743],
          [-75.936424, 39.369544],
          [-75.934112, 39.370857],
          [-75.932022, 39.371505],
          [-75.930638, 39.371623],
          [-75.929962, 39.371681],
          [-75.92794, 39.371456],
          [-75.927154, 39.371187],
          [-75.926589, 39.370994],
          [-75.925285, 39.36975],
          [-75.92395, 39.368011],
          [-75.923884, 39.367968],
          [-75.922775, 39.367248],
          [-75.92115, 39.367057],
          [-75.919914, 39.366958],
          [-75.919789, 39.366948],
          [-75.918914, 39.366878],
          [-75.918515, 39.366868],
          [-75.917606, 39.366844],
          [-75.916699, 39.366821],
          [-75.916557, 39.366817],
          [-75.915029, 39.366587],
          [-75.915008, 39.366584],
          [-75.913581, 39.366096],
          [-75.912002, 39.365413],
          [-75.911805, 39.365367],
          [-75.910202, 39.364999],
          [-75.909337, 39.3648],
          [-75.90847, 39.364601],
          [-75.908035, 39.364501],
          [-75.903793, 39.364345],
          [-75.902687, 39.364478],
          [-75.90036, 39.364757],
          [-75.899827, 39.364914],
          [-75.898292, 39.365367],
          [-75.898187, 39.36539],
          [-75.897782, 39.365478],
          [-75.897376, 39.365567],
          [-75.89598, 39.365871],
          [-75.894911, 39.3657],
          [-75.894855, 39.365691],
          [-75.894523, 39.365638],
          [-75.892288, 39.36462],
          [-75.891222, 39.363808],
          [-75.890174, 39.36301],
          [-75.888874, 39.362131],
          [-75.888542, 39.361907],
          [-75.888499, 39.361889],
          [-75.885864, 39.360797],
          [-75.885101, 39.360843],
          [-75.884391, 39.361354],
          [-75.883628, 39.362251],
          [-75.882827, 39.363452],
          [-75.882141, 39.364322],
          [-75.882131, 39.364327],
          [-75.880935, 39.364929],
          [-75.879839, 39.365329],
          [-75.879463, 39.365467],
          [-75.877965, 39.36558],
          [-75.877952, 39.365581],
          [-75.877848, 39.36559],
          [-75.876636, 39.365707],
          [-75.875801, 39.365787],
          [-75.875024, 39.365789],
          [-75.874412, 39.365791],
          [-75.873341, 39.366043],
          [-75.872575, 39.366223],
          [-75.872062, 39.366344],
          [-75.868377, 39.367721],
          [-75.868066, 39.367759],
          [-75.867614, 39.367813],
          [-75.867355, 39.367644],
          [-75.86678, 39.367268],
          [-75.866206, 39.366893],
          [-75.865669, 39.366542],
          [-75.864978, 39.366503],
          [-75.863754, 39.366436],
          [-75.862518, 39.36737],
          [-75.861415, 39.367549],
          [-75.861038, 39.36761],
          [-75.860832, 39.367466],
          [-75.859169, 39.366306],
          [-75.858592, 39.36593],
          [-75.857246, 39.365055],
          [-75.856646, 39.364892],
          [-75.85543, 39.364562],
          [-75.853652, 39.365333],
          [-75.853064, 39.36589],
          [-75.852394, 39.366527],
          [-75.851468, 39.366866],
          [-75.849392, 39.367627],
          [-75.84897, 39.367902],
          [-75.848845, 39.367984],
          [-75.84872, 39.368065],
          [-75.848501, 39.368208],
          [-75.847037, 39.367584],
          [-75.846901, 39.367526],
          [-75.846776, 39.367541],
          [-75.846694, 39.367551],
          [-75.846611, 39.367561],
          [-75.846048, 39.367628],
          [-75.845476, 39.367698],
          [-75.84432, 39.369544],
          [-75.844304, 39.369571],
          [-75.843785, 39.369951],
          [-75.842385, 39.370978],
          [-75.841727, 39.371213],
          [-75.841682, 39.37123],
          [-75.840283, 39.371734],
          [-75.839605, 39.371978],
          [-75.836611, 39.372763],
          [-75.836593, 39.37277],
          [-75.835719, 39.373103],
          [-75.835534, 39.373174],
          [-75.834014, 39.373757],
          [-75.8334, 39.373834],
          [-75.832316, 39.37397],
          [-75.831533, 39.374109],
          [-75.831173, 39.374173],
          [-75.8308, 39.374469],
          [-75.830661, 39.374579],
          [-75.830523, 39.374689],
          [-75.830023, 39.375086],
          [-75.830183, 39.375337],
          [-75.830062, 39.375659],
          [-75.830031, 39.375742],
          [-75.829736, 39.376173],
          [-75.829513, 39.376499],
          [-75.829369, 39.376632],
          [-75.828161, 39.377762],
          [-75.827991, 39.377921],
          [-75.82742, 39.378236],
          [-75.825426, 39.379846],
          [-75.824458, 39.380961],
          [-75.824392, 39.380998],
          [-75.823761, 39.381354],
          [-75.823658, 39.381413],
          [-75.822289, 39.381609],
          [-75.821266, 39.381755],
          [-75.819956, 39.382061],
          [-75.819576, 39.382141],
          [-75.818829, 39.382299],
          [-75.818696, 39.382239],
          [-75.818345, 39.382081],
          [-75.817975, 39.381916],
          [-75.817925, 39.381874],
          [-75.817646, 39.381638],
          [-75.817365, 39.3814],
          [-75.816968, 39.381064],
          [-75.816356, 39.380681],
          [-75.816313, 39.380654],
          [-75.814614, 39.38027],
          [-75.812984, 39.380026],
          [-75.811537, 39.379984],
          [-75.810108, 39.37983],
          [-75.809989, 39.379806],
          [-75.809817, 39.379768],
          [-75.809634, 39.379758],
          [-75.809452, 39.379749],
          [-75.809259, 39.379521],
          [-75.809237, 39.379495],
          [-75.809359, 39.378894],
          [-75.809425, 39.378832],
          [-75.809396, 39.378425],
          [-75.8092, 39.378101],
          [-75.808879, 39.378048],
          [-75.808741, 39.378066],
          [-75.808631, 39.378081],
          [-75.808087, 39.377991],
          [-75.807976, 39.377972],
          [-75.807898, 39.37796],
          [-75.807696, 39.377804],
          [-75.807608, 39.377737],
          [-75.807564, 39.377703],
          [-75.807435, 39.377604],
          [-75.807392, 39.377571],
          [-75.807328, 39.377522],
          [-75.807139, 39.377376],
          [-75.807076, 39.377328],
          [-75.806992, 39.377264],
          [-75.806958, 39.377254],
          [-75.806552, 39.377135],
          [-75.806417, 39.377096],
          [-75.806165, 39.376799],
          [-75.806184, 39.37673],
          [-75.806333, 39.376206],
          [-75.806229, 39.375897],
          [-75.806318, 39.375583],
          [-75.806472, 39.375483],
          [-75.806498, 39.375467],
          [-75.806484, 39.375384],
          [-75.806378, 39.375274],
          [-75.806201, 39.375198],
          [-75.806073, 39.375258],
          [-75.805925, 39.375439],
          [-75.805861, 39.375467],
          [-75.805741, 39.3755],
          [-75.805684, 39.375505],
          [-75.805514, 39.375483],
          [-75.805141, 39.375464],
          [-75.804955, 39.375456],
          [-75.804757, 39.375417],
          [-75.804686, 39.375434],
          [-75.804495, 39.375609],
          [-75.804354, 39.375818],
          [-75.804311, 39.375939],
          [-75.804304, 39.376291],
          [-75.804162, 39.376576],
          [-75.803794, 39.376972],
          [-75.80337, 39.377219],
          [-75.802917, 39.377318],
          [-75.802874, 39.377334],
          [-75.802761, 39.377488],
          [-75.802752, 39.377493],
          [-75.802605, 39.377592],
          [-75.802237, 39.377653],
          [-75.80182, 39.377922],
          [-75.801695, 39.377912],
          [-75.801098, 39.377867],
          [-75.800907, 39.377746],
          [-75.800701, 39.377691],
          [-75.800588, 39.377608],
          [-75.800617, 39.377317],
          [-75.800567, 39.377229],
          [-75.800461, 39.377202],
          [-75.800263, 39.377224],
          [-75.799852, 39.377361],
          [-75.799678, 39.377368],
          [-75.799605, 39.377372],
          [-75.799222, 39.377224],
          [-75.798847, 39.377119],
          [-75.798748, 39.37713],
          [-75.798607, 39.37724],
          [-75.798225, 39.377279],
          [-75.797998, 39.377251],
          [-75.797779, 39.377141],
          [-75.797566, 39.377268],
          [-75.79729, 39.377273],
          [-75.796965, 39.377295],
          [-75.79666, 39.377564],
          [-75.796547, 39.377586],
          [-75.796483, 39.377575],
          [-75.796441, 39.377542],
          [-75.796356, 39.377229],
          [-75.79625, 39.377229],
          [-75.796038, 39.377322],
          [-75.795825, 39.377245],
          [-75.795726, 39.37724],
          [-75.795606, 39.377317],
          [-75.795443, 39.377586],
          [-75.795216, 39.377701],
          [-75.795018, 39.377789],
          [-75.79482, 39.378009],
          [-75.794664, 39.378756],
          [-75.794494, 39.378959],
          [-75.794423, 39.379102],
          [-75.794381, 39.379541],
          [-75.79431, 39.379641],
          [-75.794239, 39.379744],
          [-75.793907, 39.379936],
          [-75.7938, 39.37998],
          [-75.792994, 39.380129],
          [-75.79295, 39.380116],
          [-75.79288, 39.380096],
          [-75.792534, 39.379843],
          [-75.792484, 39.379826],
          [-75.792357, 39.379804],
          [-75.792279, 39.379799],
          [-75.792043, 39.379822],
          [-75.791833, 39.379843],
          [-75.791182, 39.379925],
          [-75.790996, 39.380043],
          [-75.790531, 39.380342],
          [-75.789971, 39.380815],
          [-75.789865, 39.380891],
          [-75.789667, 39.381012],
          [-75.78949, 39.381095],
          [-75.789398, 39.381128],
          [-75.788768, 39.381111],
          [-75.788584, 39.381061],
          [-75.788478, 39.380919],
          [-75.788381, 39.38061],
          [-75.788351, 39.380512],
          [-75.78823, 39.380424],
          [-75.788032, 39.380353],
          [-75.78741, 39.380296],
          [-75.78731, 39.380287],
          [-75.787202, 39.380267],
          [-75.786585, 39.380158],
          [-75.786567, 39.380155],
          [-75.786383, 39.380105],
          [-75.786036, 39.380061],
          [-75.785732, 39.380094],
          [-75.78538, 39.380091],
          [-75.784968, 39.380088],
          [-75.784656, 39.380165],
          [-75.784415, 39.380363],
          [-75.78433, 39.380687],
          [-75.784309, 39.38094],
          [-75.78433, 39.381385],
          [-75.784486, 39.382143],
          [-75.784465, 39.38231],
          [-75.784443, 39.3825],
          [-75.784415, 39.382527],
          [-75.784224, 39.382549],
          [-75.783891, 39.382505],
          [-75.78365, 39.382439],
          [-75.782957, 39.382164],
          [-75.782235, 39.381856],
          [-75.782023, 39.381867],
          [-75.781846, 39.38195],
          [-75.781753, 39.381954],
          [-75.781548, 39.381966],
          [-75.781131, 39.382125],
          [-75.781095, 39.382125],
          [-75.780473, 39.381944],
          [-75.780409, 39.381889],
          [-75.780331, 39.381653],
          [-75.780126, 39.381471],
          [-75.779503, 39.381169],
          [-75.77922, 39.380916],
          [-75.77806, 39.38029],
          [-75.777918, 39.380246],
          [-75.777671, 39.38024],
          [-75.776998, 39.380405],
          [-75.776757, 39.3803],
          [-75.776503, 39.380306],
          [-75.776355, 39.380268],
          [-75.776304, 39.380256],
          [-75.776248, 39.38019],
          [-75.776213, 39.380026],
          [-75.776369, 39.379059],
          [-75.776312, 39.378993],
          [-75.776093, 39.378971],
          [-75.775909, 39.379015],
          [-75.775668, 39.37896],
          [-75.775449, 39.37896],
          [-75.775088, 39.37907],
          [-75.775045, 39.37907],
          [-75.774635, 39.379042],
          [-75.774387, 39.378921],
          [-75.774253, 39.378916],
          [-75.77414, 39.379003],
          [-75.773962, 39.379294],
          [-75.77387, 39.379371],
          [-75.773651, 39.379486],
          [-75.773508, 39.379549],
          [-75.773451, 39.379515],
          [-75.773305, 39.37945],
          [-75.773171, 39.379497],
          [-75.773112, 39.379544],
          [-75.772688, 39.379525],
          [-75.772462, 39.379431],
          [-75.77199, 39.37915],
          [-75.771968, 39.379143],
          [-75.77187, 39.379115],
          [-75.77187, 39.379002],
          [-75.771845, 39.378927],
          [-75.771699, 39.378899],
          [-75.771578, 39.378833],
          [-75.77148, 39.378739],
          [-75.771334, 39.378664],
          [-75.771116, 39.378693],
          [-75.770994, 39.378684],
          [-75.770836, 39.378647],
          [-75.770545, 39.378657],
          [-75.770485, 39.378724],
          [-75.770388, 39.378743],
          [-75.770047, 39.37864],
          [-75.769865, 39.378622],
          [-75.769792, 39.378622],
          [-75.769695, 39.378603],
          [-75.769621, 39.378546],
          [-75.769535, 39.37857],
          [-75.769474, 39.37857],
          [-75.769437, 39.378532],
          [-75.769443, 39.378489],
          [-75.769461, 39.378461],
          [-75.769455, 39.378394],
          [-75.769423, 39.378294],
          [-75.769386, 39.378237],
          [-75.769331, 39.378194],
          [-75.769294, 39.378213],
          [-75.769233, 39.378228],
          [-75.769165, 39.378209],
          [-75.769116, 39.378166],
          [-75.768999, 39.378114],
          [-75.7689, 39.378086],
          [-75.768783, 39.378043],
          [-75.768691, 39.378034],
          [-75.768642, 39.378053],
          [-75.768569, 39.378106],
          [-75.768526, 39.378111],
          [-75.768489, 39.378101],
          [-75.768439, 39.37804],
          [-75.768365, 39.377968],
          [-75.768304, 39.377978],
          [-75.768249, 39.378007],
          [-75.768102, 39.378117],
          [-75.768059, 39.378126],
          [-75.767985, 39.378079],
          [-75.767831, 39.377937],
          [-75.767769, 39.377875],
          [-75.767665, 39.377913],
          [-75.767542, 39.377937],
          [-75.767413, 39.3779],
          [-75.767358, 39.377871],
          [-75.767253, 39.377833],
          [-75.767105, 39.377796],
          [-75.767044, 39.377739],
          [-75.7668, 39.377586],
          [-75.766731, 39.377537],
          [-75.766714, 39.377537],
          [-75.766693, 39.377537],
          [-75.766667, 39.377216],
          [-75.760104, 39.296817],
          [-75.755962, 39.246069],
          [-75.867722, 39.259483]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24035",
      state: "24",
      county: "035",
      name: "Queen Annes County",
      censusArea: 371.908,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.15809, 39.093998],
          [-76.117058, 39.107098],
          [-76.072418, 39.138206],
          [-76.081596, 39.147659],
          [-76.04763, 39.162666],
          [-76.039726, 39.174102],
          [-76.046661, 39.180751],
          [-76.065186, 39.185204],
          [-76.068794, 39.192345],
          [-76.056556, 39.210338],
          [-76.027191, 39.226722],
          [-76.020454, 39.227401],
          [-76.014792, 39.234152],
          [-76.012797, 39.234453],
          [-75.998411, 39.234773],
          [-75.989844, 39.238367],
          [-75.976744, 39.240519],
          [-75.967236, 39.239318],
          [-75.953404, 39.235264],
          [-75.937902, 39.235502],
          [-75.930708, 39.239023],
          [-75.925022, 39.244108],
          [-75.874084, 39.249798],
          [-75.867722, 39.259483],
          [-75.755962, 39.246069],
          [-75.755953, 39.245958],
          [-75.747671, 39.143345],
          [-75.834948, 39.108437],
          [-75.929729, 38.990654],
          [-75.949335, 38.918333],
          [-76.096956, 38.944259],
          [-76.103512, 38.882308],
          [-76.199544, 38.850817],
          [-76.200728, 38.855389],
          [-76.202598, 38.862616],
          [-76.202314, 38.864906],
          [-76.200082, 38.882885],
          [-76.205063, 38.892726],
          [-76.204544, 38.905715],
          [-76.204129, 38.916096],
          [-76.204122, 38.916269],
          [-76.204091, 38.917058],
          [-76.203638, 38.928382],
          [-76.207695, 38.931954],
          [-76.213731, 38.937269],
          [-76.213842, 38.937366],
          [-76.22813, 38.941412],
          [-76.228405, 38.941489],
          [-76.232038, 38.942518],
          [-76.233895, 38.942123],
          [-76.234681, 38.941956],
          [-76.250157, 38.938667],
          [-76.250868, 38.92825],
          [-76.250838, 38.928204],
          [-76.249622, 38.926297],
          [-76.249246, 38.925707],
          [-76.248896, 38.925159],
          [-76.248844, 38.925076],
          [-76.248702, 38.924855],
          [-76.248574, 38.924655],
          [-76.248472, 38.924494],
          [-76.248023, 38.92379],
          [-76.249163, 38.9218],
          [-76.249285, 38.921587],
          [-76.249355, 38.921465],
          [-76.249674, 38.920907],
          [-76.249889, 38.920841],
          [-76.255819, 38.919008],
          [-76.25619, 38.918893],
          [-76.256397, 38.918829],
          [-76.25808, 38.91916],
          [-76.258206, 38.919185],
          [-76.262226, 38.919976],
          [-76.264683, 38.924576],
          [-76.264692, 38.924764],
          [-76.264943, 38.930297],
          [-76.265772, 38.931482],
          [-76.26626, 38.932179],
          [-76.269179, 38.936349],
          [-76.270954, 38.938886],
          [-76.271625, 38.939844],
          [-76.273022, 38.94184],
          [-76.273083, 38.941927],
          [-76.273258, 38.941826],
          [-76.276033, 38.940213],
          [-76.284227, 38.935452],
          [-76.288813, 38.932787],
          [-76.291211, 38.931394],
          [-76.29558, 38.928855],
          [-76.295911, 38.928663],
          [-76.298208, 38.92206],
          [-76.299431, 38.918542],
          [-76.299406, 38.918505],
          [-76.298492, 38.917121],
          [-76.293963, 38.910266],
          [-76.293867, 38.910121],
          [-76.293843, 38.909826],
          [-76.293829, 38.909649],
          [-76.293794, 38.909222],
          [-76.293492, 38.905499],
          [-76.293358, 38.903854],
          [-76.293341, 38.903635],
          [-76.293328, 38.903479],
          [-76.293309, 38.903244],
          [-76.293255, 38.902582],
          [-76.293254, 38.902568],
          [-76.308425, 38.898404],
          [-76.317947, 38.911312],
          [-76.317977, 38.911311],
          [-76.31853, 38.911299],
          [-76.319272, 38.911282],
          [-76.322715, 38.911205],
          [-76.323252, 38.911193],
          [-76.324097, 38.911174],
          [-76.324102, 38.911172],
          [-76.336104, 38.905977],
          [-76.336116, 38.905907],
          [-76.336827, 38.901905],
          [-76.336966, 38.901118],
          [-76.337097, 38.90038],
          [-76.337227, 38.899653],
          [-76.337358, 38.898915],
          [-76.338047, 38.895034],
          [-76.338123, 38.894601],
          [-76.338416, 38.892954],
          [-76.338501, 38.892474],
          [-76.337946, 38.891332],
          [-76.336986, 38.889357],
          [-76.33656, 38.888481],
          [-76.336326, 38.888001],
          [-76.335681, 38.886673],
          [-76.335364, 38.886021],
          [-76.335341, 38.885907],
          [-76.333801, 38.878194],
          [-76.333258, 38.875478],
          [-76.333211, 38.875241],
          [-76.332651, 38.872438],
          [-76.331103, 38.864686],
          [-76.33103, 38.86432],
          [-76.334019, 38.860238],
          [-76.337901, 38.857579],
          [-76.338136, 38.857418],
          [-76.339667, 38.85637],
          [-76.340587, 38.85574],
          [-76.348826, 38.857134],
          [-76.35039, 38.857399],
          [-76.356361, 38.854396],
          [-76.35996, 38.852586],
          [-76.360095, 38.852518],
          [-76.361141, 38.851992],
          [-76.36132, 38.851589],
          [-76.366501, 38.839936],
          [-76.368164, 38.836194],
          [-76.368195, 38.836125],
          [-76.368359, 38.836205],
          [-76.372719, 38.838324],
          [-76.373122, 38.838519],
          [-76.375086, 38.839474],
          [-76.375138, 38.839981],
          [-76.375303, 38.841611],
          [-76.375443, 38.842988],
          [-76.376031, 38.848777],
          [-76.376202, 38.850461],
          [-76.376183, 38.8505],
          [-76.375874, 38.851126],
          [-76.372553, 38.85786],
          [-76.367549, 38.868009],
          [-76.367344, 38.868424],
          [-76.364678, 38.873831],
          [-76.365225, 38.892614],
          [-76.365388, 38.898219],
          [-76.365658, 38.907477],
          [-76.36513, 38.911736],
          [-76.36478, 38.914553],
          [-76.364523, 38.916632],
          [-76.36205, 38.936568],
          [-76.361727, 38.939175],
          [-76.359669, 38.94388],
          [-76.357933, 38.947848],
          [-76.357752, 38.948262],
          [-76.354949, 38.954672],
          [-76.353828, 38.957234],
          [-76.351637, 38.960214],
          [-76.343672, 38.971048],
          [-76.333703, 38.984607],
          [-76.33129, 38.98789],
          [-76.323293, 38.998767],
          [-76.322679, 38.999602],
          [-76.322346, 39.005488],
          [-76.322296, 39.006375],
          [-76.323557, 39.008961],
          [-76.32259, 39.0131],
          [-76.320277, 39.022998],
          [-76.320274, 39.023013],
          [-76.320269, 39.02302],
          [-76.311766, 39.035257],
          [-76.302029, 39.039571],
          [-76.301847, 39.039651],
          [-76.301645, 39.037663],
          [-76.301506, 39.036298],
          [-76.301054, 39.031862],
          [-76.30104, 39.031721],
          [-76.301027, 39.031595],
          [-76.302846, 39.025828],
          [-76.30265, 39.025346],
          [-76.29409, 39.004263],
          [-76.293962, 39.003948],
          [-76.2919, 39.001264],
          [-76.289326, 38.997914],
          [-76.286484, 38.994215],
          [-76.284893, 38.992143],
          [-76.28328, 38.990044],
          [-76.280355, 38.986236],
          [-76.278058, 38.983246],
          [-76.277478, 38.982492],
          [-76.277457, 38.982493],
          [-76.275964, 38.982587],
          [-76.258813, 38.983664],
          [-76.258724, 38.983646],
          [-76.246049, 38.981035],
          [-76.243619, 38.980534],
          [-76.229993, 38.977728],
          [-76.229277, 38.97758],
          [-76.228748, 38.97722],
          [-76.218929, 38.970538],
          [-76.218773, 38.970562],
          [-76.20236, 38.973079],
          [-76.202081, 38.973272],
          [-76.187165, 38.983558],
          [-76.182461, 38.986802],
          [-76.180663, 38.988042],
          [-76.168332, 38.996546],
          [-76.164004, 38.99953],
          [-76.163988, 38.999541],
          [-76.163988, 38.999542],
          [-76.163956, 39.000437],
          [-76.163898, 39.002096],
          [-76.163616, 39.010057],
          [-76.167574, 39.018273],
          [-76.173393, 39.02555],
          [-76.174633, 39.0271],
          [-76.177832, 39.031102],
          [-76.178115, 39.031456],
          [-76.178281, 39.031663],
          [-76.184207, 39.046264],
          [-76.179456, 39.052941],
          [-76.177046, 39.056329],
          [-76.175284, 39.058805],
          [-76.16906, 39.062787],
          [-76.15896, 39.065486],
          [-76.156572, 39.069433],
          [-76.153465, 39.074568],
          [-76.150528, 39.079421],
          [-76.145174, 39.092824],
          [-76.15809, 39.093998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24037",
      state: "24",
      county: "037",
      name: "St. Marys County",
      censusArea: 357.18,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.469795, 38.119061],
          [-76.469787, 38.118606],
          [-76.469784, 38.118381],
          [-76.46976, 38.116874],
          [-76.46974, 38.115653],
          [-76.469738, 38.115534],
          [-76.467333, 38.112546],
          [-76.466973, 38.112099],
          [-76.466568, 38.111596],
          [-76.466404, 38.111392],
          [-76.466387, 38.111302],
          [-76.466364, 38.111183],
          [-76.465892, 38.108738],
          [-76.465479, 38.106603],
          [-76.46533, 38.10583],
          [-76.467879, 38.104932],
          [-76.473266, 38.103035],
          [-76.475448, 38.104271],
          [-76.476222, 38.104709],
          [-76.478168, 38.109221],
          [-76.47861, 38.110247],
          [-76.478797, 38.11068],
          [-76.478817, 38.110726],
          [-76.479056, 38.111281],
          [-76.479799, 38.113005],
          [-76.481, 38.115789],
          [-76.481036, 38.115873],
          [-76.48105, 38.115889],
          [-76.481073, 38.115915],
          [-76.482007, 38.116974],
          [-76.48403, 38.119266],
          [-76.485149, 38.120535],
          [-76.485961, 38.121456],
          [-76.486274, 38.12181],
          [-76.48632, 38.121862],
          [-76.492029, 38.128333],
          [-76.49504, 38.131746],
          [-76.497081, 38.134059],
          [-76.497866, 38.13495],
          [-76.499018, 38.136255],
          [-76.499842, 38.137189],
          [-76.499923, 38.137221],
          [-76.500332, 38.137381],
          [-76.501258, 38.137744],
          [-76.502306, 38.138156],
          [-76.508825, 38.140713],
          [-76.514824, 38.141219],
          [-76.522418, 38.139391],
          [-76.52899, 38.134708],
          [-76.529439, 38.134388],
          [-76.529868, 38.134083],
          [-76.54038, 38.152991],
          [-76.545335, 38.165373],
          [-76.547333, 38.175673],
          [-76.547455, 38.175923],
          [-76.547902, 38.176839],
          [-76.548256, 38.177566],
          [-76.548946, 38.178982],
          [-76.552957, 38.187209],
          [-76.566297, 38.198492],
          [-76.57444, 38.203751],
          [-76.588683, 38.21295],
          [-76.590637, 38.214212],
          [-76.624387, 38.223429],
          [-76.625659, 38.223777],
          [-76.628078, 38.224437],
          [-76.629703, 38.224881],
          [-76.629893, 38.224933],
          [-76.631958, 38.225497],
          [-76.631997, 38.225508],
          [-76.632464, 38.225635],
          [-76.632544, 38.225657],
          [-76.643929, 38.22508],
          [-76.643999, 38.225102],
          [-76.645034, 38.225429],
          [-76.645166, 38.22547],
          [-76.659182, 38.229894],
          [-76.673462, 38.234401],
          [-76.678687, 38.234259],
          [-76.687075, 38.23403],
          [-76.712594, 38.233334],
          [-76.716139, 38.233237],
          [-76.716376, 38.233231],
          [-76.717537, 38.233329],
          [-76.719602, 38.233503],
          [-76.740055, 38.235227],
          [-76.744265, 38.230716],
          [-76.749384, 38.225231],
          [-76.752017, 38.222409],
          [-76.752085, 38.222336],
          [-76.752286, 38.222121],
          [-76.769988, 38.226388],
          [-76.778625, 38.22847],
          [-76.779865, 38.229026],
          [-76.791932, 38.234441],
          [-76.79659, 38.236531],
          [-76.797452, 38.236918],
          [-76.811647, 38.250129],
          [-76.811815, 38.251318],
          [-76.811927, 38.252115],
          [-76.809009, 38.252193],
          [-76.80888, 38.252197],
          [-76.808411, 38.252209],
          [-76.805949, 38.252275],
          [-76.80577, 38.252615],
          [-76.805224, 38.253648],
          [-76.802947, 38.275094],
          [-76.802347, 38.280743],
          [-76.804922, 38.2833],
          [-76.805037, 38.283414],
          [-76.805363, 38.283738],
          [-76.80547, 38.283844],
          [-76.806596, 38.284962],
          [-76.810596, 38.288934],
          [-76.811182, 38.289516],
          [-76.815098, 38.293404],
          [-76.816323, 38.29462],
          [-76.820799, 38.299064],
          [-76.821569, 38.299829],
          [-76.82167, 38.299869],
          [-76.824834, 38.30113],
          [-76.824889, 38.301152],
          [-76.834046, 38.29968],
          [-76.853623, 38.3401],
          [-76.849933, 38.365815],
          [-76.87181, 38.388973],
          [-76.774924, 38.50892],
          [-76.771045, 38.509889],
          [-76.770193, 38.510478],
          [-76.768654, 38.512614],
          [-76.766899, 38.513278],
          [-76.764753, 38.513382],
          [-76.762551, 38.513172],
          [-76.760202, 38.513292],
          [-76.759769, 38.513023],
          [-76.758, 38.513088],
          [-76.756546, 38.513532],
          [-76.753855, 38.513525],
          [-76.752051, 38.513733],
          [-76.748052, 38.512469],
          [-76.745861, 38.512627],
          [-76.744138, 38.512191],
          [-76.742151, 38.512487],
          [-76.740048, 38.512576],
          [-76.738652, 38.51234],
          [-76.734922, 38.512189],
          [-76.733669, 38.512464],
          [-76.730088, 38.51183],
          [-76.726627, 38.511769],
          [-76.72265, 38.510684],
          [-76.720401, 38.510692],
          [-76.719146, 38.509825],
          [-76.718537, 38.509784],
          [-76.717758, 38.51038],
          [-76.716407, 38.510112],
          [-76.71592, 38.509474],
          [-76.71526, 38.509498],
          [-76.713835, 38.510482],
          [-76.713459, 38.5095],
          [-76.71184, 38.509871],
          [-76.71053, 38.50917],
          [-76.710413, 38.507453],
          [-76.70995, 38.508065],
          [-76.709169, 38.50736],
          [-76.708441, 38.505545],
          [-76.708448, 38.504838],
          [-76.70652, 38.50425],
          [-76.705541, 38.502959],
          [-76.705353, 38.502019],
          [-76.704443, 38.501835],
          [-76.703727, 38.502297],
          [-76.703316, 38.502401],
          [-76.702203, 38.501668],
          [-76.701475, 38.500195],
          [-76.701033, 38.499646],
          [-76.700012, 38.499129],
          [-76.700275, 38.49794],
          [-76.700122, 38.497634],
          [-76.699317, 38.497125],
          [-76.68382, 38.495251],
          [-76.674118, 38.499622],
          [-76.673584, 38.499085],
          [-76.67305, 38.498548],
          [-76.672663, 38.49816],
          [-76.667081, 38.495172],
          [-76.666427, 38.494785],
          [-76.665949, 38.494389],
          [-76.66536, 38.493657],
          [-76.663333, 38.491153],
          [-76.661309, 38.488596],
          [-76.661214, 38.488476],
          [-76.66112, 38.488356],
          [-76.660341, 38.487368],
          [-76.659326, 38.485675],
          [-76.656646, 38.48121],
          [-76.653967, 38.476746],
          [-76.653909, 38.47665],
          [-76.653906, 38.476645],
          [-76.65343, 38.475852],
          [-76.653383, 38.475774],
          [-76.652894, 38.474958],
          [-76.65265, 38.474552],
          [-76.652142, 38.473753],
          [-76.651956, 38.471261],
          [-76.652095, 38.469519],
          [-76.652977, 38.468101],
          [-76.653007, 38.468051],
          [-76.653656, 38.466988],
          [-76.654345, 38.465174],
          [-76.654146, 38.463908],
          [-76.653872, 38.463359],
          [-76.653783, 38.463294],
          [-76.652588, 38.462422],
          [-76.651307, 38.461829],
          [-76.649131, 38.460822],
          [-76.64648, 38.459593],
          [-76.644319, 38.458268],
          [-76.639814, 38.455456],
          [-76.639263, 38.455037],
          [-76.639174, 38.454969],
          [-76.636393, 38.452855],
          [-76.635464, 38.452173],
          [-76.634487, 38.451457],
          [-76.632601, 38.449659],
          [-76.63252, 38.449582],
          [-76.63245, 38.449518],
          [-76.632427, 38.449497],
          [-76.632415, 38.449486],
          [-76.632409, 38.449481],
          [-76.632402, 38.449474],
          [-76.632399, 38.449471],
          [-76.632398, 38.44947],
          [-76.632397, 38.449469],
          [-76.632388, 38.449461],
          [-76.632387, 38.44946],
          [-76.632348, 38.449425],
          [-76.632226, 38.449313],
          [-76.630009, 38.44729],
          [-76.628951, 38.446264],
          [-76.628002, 38.445707],
          [-76.627864, 38.445625],
          [-76.626272, 38.444648],
          [-76.624681, 38.44367],
          [-76.624192, 38.443498],
          [-76.623616, 38.442869],
          [-76.622486, 38.441366],
          [-76.622068, 38.440152],
          [-76.621546, 38.438921],
          [-76.619985, 38.437575],
          [-76.618831, 38.436354],
          [-76.618428, 38.436054],
          [-76.61782, 38.435602],
          [-76.617222, 38.43533],
          [-76.616427, 38.434612],
          [-76.61582, 38.433591],
          [-76.614797, 38.431355],
          [-76.613984, 38.429863],
          [-76.612999, 38.428837],
          [-76.61146, 38.427991],
          [-76.610799, 38.427239],
          [-76.610605, 38.42702],
          [-76.610135, 38.426486],
          [-76.608893, 38.42503],
          [-76.608373, 38.424445],
          [-76.607855, 38.424197],
          [-76.606263, 38.423973],
          [-76.605265, 38.423833],
          [-76.60219, 38.424173],
          [-76.601599, 38.424113],
          [-76.601125, 38.424066],
          [-76.600651, 38.424018],
          [-76.599445, 38.423896],
          [-76.597528, 38.423425],
          [-76.595223, 38.422172],
          [-76.593918, 38.420952],
          [-76.591674, 38.418854],
          [-76.588899, 38.417243],
          [-76.586742, 38.415991],
          [-76.584586, 38.414739],
          [-76.578631, 38.411283],
          [-76.575543, 38.41017],
          [-76.574427, 38.409768],
          [-76.573981, 38.409607],
          [-76.573311, 38.409365],
          [-76.571807, 38.408823],
          [-76.568129, 38.407713],
          [-76.567296, 38.407494],
          [-76.565615, 38.407051],
          [-76.563902, 38.406002],
          [-76.562669, 38.403858],
          [-76.562362, 38.403133],
          [-76.562185, 38.402716],
          [-76.562179, 38.402702],
          [-76.562178, 38.4027],
          [-76.562054, 38.402409],
          [-76.562002, 38.402286],
          [-76.561353, 38.400376],
          [-76.560617, 38.398707],
          [-76.559957, 38.397321],
          [-76.559227, 38.396378],
          [-76.558388, 38.395861],
          [-76.556652, 38.395295],
          [-76.554929, 38.395035],
          [-76.553435, 38.395043],
          [-76.551129, 38.395212],
          [-76.550652, 38.395248],
          [-76.548842, 38.395409],
          [-76.545623, 38.395695],
          [-76.542273, 38.396144],
          [-76.54027, 38.396631],
          [-76.537701, 38.397361],
          [-76.537325, 38.397429],
          [-76.536676, 38.397545],
          [-76.536028, 38.397661],
          [-76.53566, 38.397727],
          [-76.534075, 38.397766],
          [-76.533073, 38.397223],
          [-76.53127, 38.395934],
          [-76.529924, 38.394972],
          [-76.528305, 38.393406],
          [-76.527968, 38.39308],
          [-76.526904, 38.392012],
          [-76.525826, 38.39113],
          [-76.524645, 38.390343],
          [-76.524138, 38.389775],
          [-76.523635, 38.389211],
          [-76.522594, 38.387997],
          [-76.521153, 38.385704],
          [-76.520078, 38.384382],
          [-76.52002, 38.38433],
          [-76.518939, 38.383363],
          [-76.516663, 38.381668],
          [-76.514949, 38.380835],
          [-76.51356, 38.380413],
          [-76.512609, 38.379708],
          [-76.511631, 38.379399],
          [-76.511296, 38.379294],
          [-76.510961, 38.379188],
          [-76.510057, 38.378903],
          [-76.507831, 38.377503],
          [-76.507399, 38.376983],
          [-76.507042, 38.376553],
          [-76.506542, 38.37575],
          [-76.506271, 38.375096],
          [-76.506041, 38.374799],
          [-76.505576, 38.374403],
          [-76.50504, 38.373947],
          [-76.503802, 38.373359],
          [-76.502376, 38.372889],
          [-76.501475, 38.372497],
          [-76.500449, 38.371635],
          [-76.499675, 38.370906],
          [-76.499675, 38.370919],
          [-76.498484, 38.369633],
          [-76.497293, 38.368347],
          [-76.495675, 38.3666],
          [-76.493466, 38.363996],
          [-76.491157, 38.361246],
          [-76.489235, 38.358393],
          [-76.486618, 38.354038],
          [-76.486364, 38.353517],
          [-76.48606, 38.352891],
          [-76.485755, 38.352265],
          [-76.485188, 38.351097],
          [-76.484085, 38.34807],
          [-76.483575, 38.345855],
          [-76.483449, 38.34531],
          [-76.483223, 38.34329],
          [-76.483154, 38.342668],
          [-76.48313, 38.342446],
          [-76.483375, 38.340797],
          [-76.483432, 38.340415],
          [-76.483481, 38.340093],
          [-76.484336, 38.33721],
          [-76.485047, 38.335895],
          [-76.485618, 38.33484],
          [-76.485678, 38.33473],
          [-76.485737, 38.334619],
          [-76.486084, 38.333977],
          [-76.486431, 38.333335],
          [-76.486925, 38.332422],
          [-76.489029, 38.328874],
          [-76.489381, 38.328005],
          [-76.489232, 38.327453],
          [-76.489211, 38.327375],
          [-76.488976, 38.327163],
          [-76.48852, 38.326752],
          [-76.488421, 38.3267],
          [-76.487575, 38.326257],
          [-76.483167, 38.326668],
          [-76.478044, 38.32706],
          [-76.476192, 38.326726],
          [-76.475027, 38.326516],
          [-76.473581, 38.325804],
          [-76.473026, 38.32553],
          [-76.472644, 38.325342],
          [-76.472557, 38.325299],
          [-76.47247, 38.325256],
          [-76.471712, 38.324867],
          [-76.469869, 38.3229],
          [-76.46962, 38.322367],
          [-76.46848, 38.319928],
          [-76.468045, 38.318844],
          [-76.467959, 38.318628],
          [-76.467872, 38.318412],
          [-76.4676, 38.317736],
          [-76.467329, 38.317061],
          [-76.467185, 38.316703],
          [-76.467165, 38.316653],
          [-76.467155, 38.316628],
          [-76.467111, 38.316518],
          [-76.46711, 38.316516],
          [-76.467104, 38.316501],
          [-76.467101, 38.316494],
          [-76.466882, 38.315947],
          [-76.466434, 38.314833],
          [-76.465981, 38.313702],
          [-76.465623, 38.313162],
          [-76.464481, 38.311437],
          [-76.464365, 38.31126],
          [-76.464047, 38.310946],
          [-76.461609, 38.30854],
          [-76.458306, 38.306641],
          [-76.454295, 38.305332],
          [-76.453453, 38.305057],
          [-76.449024, 38.305141],
          [-76.448846, 38.305144],
          [-76.448365, 38.305153],
          [-76.448146, 38.305157],
          [-76.448107, 38.305158],
          [-76.4481, 38.305158],
          [-76.448089, 38.305158],
          [-76.448087, 38.305158],
          [-76.448085, 38.305158],
          [-76.448084, 38.305159],
          [-76.44808, 38.305159],
          [-76.448074, 38.305159],
          [-76.448019, 38.30516],
          [-76.447913, 38.305162],
          [-76.447253, 38.305174],
          [-76.447132, 38.305176],
          [-76.445622, 38.305205],
          [-76.445334, 38.30521],
          [-76.444886, 38.305363],
          [-76.434128, 38.308763],
          [-76.425033, 38.311636],
          [-76.425005, 38.311646],
          [-76.423408, 38.31215],
          [-76.421816, 38.312653],
          [-76.42179, 38.312661],
          [-76.418789, 38.313609],
          [-76.41578, 38.314559],
          [-76.41326, 38.315356],
          [-76.406617, 38.317455],
          [-76.405716, 38.317794],
          [-76.402894, 38.311402],
          [-76.382163, 38.303389],
          [-76.381493, 38.30313],
          [-76.38012, 38.302343],
          [-76.37531, 38.299583],
          [-76.375023, 38.299419],
          [-76.374517, 38.296556],
          [-76.374481, 38.296348],
          [-76.376607, 38.294393],
          [-76.382051, 38.289384],
          [-76.394171, 38.278233],
          [-76.396913, 38.27165],
          [-76.398852, 38.266997],
          [-76.399013, 38.264338],
          [-76.399049, 38.263753],
          [-76.399078, 38.263275],
          [-76.399313, 38.259398],
          [-76.39932, 38.259284],
          [-76.399078, 38.258569],
          [-76.392464, 38.239055],
          [-76.39228, 38.238511],
          [-76.391659, 38.23668],
          [-76.391623, 38.236573],
          [-76.385244, 38.217751],
          [-76.378104, 38.209897],
          [-76.377693, 38.209445],
          [-76.372535, 38.203771],
          [-76.364135, 38.194532],
          [-76.361877, 38.192048],
          [-76.359887, 38.188737],
          [-76.353799, 38.178606],
          [-76.353516, 38.178135],
          [-76.352561, 38.177214],
          [-76.352395, 38.177054],
          [-76.347983, 38.172802],
          [-76.343517, 38.168497],
          [-76.337611, 38.162805],
          [-76.336106, 38.161353],
          [-76.329711, 38.15519],
          [-76.329705, 38.155184],
          [-76.329688, 38.155155],
          [-76.328864, 38.153703],
          [-76.328805, 38.1536],
          [-76.328778, 38.153552],
          [-76.328419, 38.152921],
          [-76.327124, 38.150641],
          [-76.320492, 38.138966],
          [-76.320136, 38.138339],
          [-76.324108, 38.134266],
          [-76.330982, 38.127217],
          [-76.331812, 38.126366],
          [-76.335243, 38.122848],
          [-76.335637, 38.122444],
          [-76.337342, 38.120696],
          [-76.338161, 38.119856],
          [-76.338535, 38.119472],
          [-76.337411, 38.110888],
          [-76.337402, 38.11082],
          [-76.331807, 38.101092],
          [-76.331484, 38.100531],
          [-76.33124, 38.100106],
          [-76.330807, 38.099354],
          [-76.330794, 38.099331],
          [-76.330787, 38.099201],
          [-76.329433, 38.073986],
          [-76.329308, 38.07166],
          [-76.329165, 38.071247],
          [-76.325044, 38.059368],
          [-76.320592, 38.046531],
          [-76.319476, 38.043315],
          [-76.321499, 38.03805],
          [-76.32195, 38.036874],
          [-76.322093, 38.036503],
          [-76.326994, 38.045105],
          [-76.332812, 38.049938],
          [-76.341172, 38.053596],
          [-76.341404, 38.053697],
          [-76.344327, 38.053565],
          [-76.345697, 38.053502],
          [-76.350656, 38.053277],
          [-76.35275, 38.053182],
          [-76.361237, 38.059542],
          [-76.361538, 38.060114],
          [-76.361668, 38.06036],
          [-76.361779, 38.06057],
          [-76.369306, 38.074853],
          [-76.370204, 38.076556],
          [-76.370845, 38.077771],
          [-76.37179, 38.079565],
          [-76.378292, 38.086949],
          [-76.380991, 38.090014],
          [-76.381298, 38.090362],
          [-76.381752, 38.090878],
          [-76.381842, 38.090981],
          [-76.383896, 38.093312],
          [-76.384886, 38.094437],
          [-76.390917, 38.101286],
          [-76.391823, 38.102315],
          [-76.392058, 38.102581],
          [-76.392335, 38.102896],
          [-76.393121, 38.103142],
          [-76.394006, 38.103419],
          [-76.396478, 38.104192],
          [-76.397656, 38.104561],
          [-76.398067, 38.10469],
          [-76.405368, 38.106974],
          [-76.405972, 38.106967],
          [-76.41316, 38.106884],
          [-76.414475, 38.105943],
          [-76.414994, 38.105572],
          [-76.41655, 38.104459],
          [-76.421066, 38.105989],
          [-76.421214, 38.106039],
          [-76.421896, 38.107026],
          [-76.429384, 38.117875],
          [-76.429471, 38.118001],
          [-76.430425, 38.119383],
          [-76.430412, 38.119488],
          [-76.43013, 38.121753],
          [-76.429744, 38.124856],
          [-76.429581, 38.126165],
          [-76.437242, 38.135699],
          [-76.439841, 38.138933],
          [-76.459236, 38.139471],
          [-76.459689, 38.139484],
          [-76.460072, 38.138717],
          [-76.460673, 38.137515],
          [-76.465727, 38.127408],
          [-76.469798, 38.119264],
          [-76.469795, 38.119061]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24039",
      state: "24",
      county: "039",
      name: "Somerset County",
      censusArea: 319.723,
      stateName: "Maryland"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-76.031442, 38.147612],
            [-76.022515, 38.133453],
            [-76.020982, 38.132776],
            [-76.020476, 38.132553],
            [-76.019637, 38.132183],
            [-76.018574, 38.131713],
            [-76.01779, 38.131367],
            [-76.015155, 38.131548],
            [-76.013903, 38.131634],
            [-76.012487, 38.131731],
            [-76.011916, 38.122214],
            [-76.013363, 38.121342],
            [-76.014698, 38.120537],
            [-76.015702, 38.119933],
            [-76.020496, 38.117044],
            [-76.021305, 38.108608],
            [-76.021353, 38.108108],
            [-76.02137, 38.107934],
            [-76.020996, 38.107578],
            [-76.017394, 38.104154],
            [-76.015477, 38.102332],
            [-76.008168, 38.095385],
            [-76.005904, 38.07717],
            [-76.011544, 38.072312],
            [-76.012701, 38.072159],
            [-76.015235, 38.071825],
            [-76.0233, 38.07076],
            [-76.036676, 38.076509],
            [-76.052207, 38.089716],
            [-76.05831, 38.094906],
            [-76.059304, 38.095751],
            [-76.050156, 38.107758],
            [-76.047291, 38.108505],
            [-76.046494, 38.108712],
            [-76.045279, 38.109029],
            [-76.044482, 38.109237],
            [-76.042083, 38.109862],
            [-76.041083, 38.110726],
            [-76.040874, 38.110906],
            [-76.03962, 38.11199],
            [-76.039696, 38.112108],
            [-76.040665, 38.113606],
            [-76.043423, 38.117868],
            [-76.048373, 38.12055],
            [-76.031442, 38.147612]
          ]
        ],
        [
          [
            [-75.624341, 37.994211],
            [-75.626129, 37.9925],
            [-75.630222, 37.988584],
            [-75.635502, 37.983531],
            [-75.639786, 37.979432],
            [-75.644545, 37.974877],
            [-75.644591, 37.974833],
            [-75.644665, 37.974763],
            [-75.645096, 37.97435],
            [-75.645251, 37.974202],
            [-75.646289, 37.973209],
            [-75.646507, 37.973],
            [-75.660956, 37.959174],
            [-75.669711, 37.950796],
            [-75.665057, 37.956282],
            [-75.663095, 37.961195],
            [-75.66502, 37.962401],
            [-75.669374, 37.96513],
            [-75.671681, 37.966576],
            [-75.685995, 37.967607],
            [-75.686525, 37.967783],
            [-75.708179, 37.974972],
            [-75.71315, 37.976623],
            [-75.722085, 37.973416],
            [-75.722662, 37.97131],
            [-75.724692, 37.969754],
            [-75.72533, 37.969266],
            [-75.727952, 37.967256],
            [-75.735125, 37.964592],
            [-75.737514, 37.963705],
            [-75.737997, 37.963526],
            [-75.750244, 37.968873],
            [-75.759091, 37.970663],
            [-75.776773, 37.972044],
            [-75.778975, 37.972216],
            [-75.783444, 37.972565],
            [-75.783815, 37.972594],
            [-75.785007, 37.971714],
            [-75.785209, 37.971564],
            [-75.785273, 37.971517],
            [-75.785487, 37.971359],
            [-75.788351, 37.969244],
            [-75.789943, 37.968068],
            [-75.806147, 37.9561],
            [-75.80693, 37.955522],
            [-75.807755, 37.954912],
            [-75.819088, 37.946542],
            [-75.820774, 37.945297],
            [-75.822901, 37.943726],
            [-75.824242, 37.942736],
            [-75.824448, 37.942584],
            [-75.829901, 37.938556],
            [-75.830154, 37.938028],
            [-75.831707, 37.934789],
            [-75.831836, 37.934519],
            [-75.832414, 37.933313],
            [-75.832793, 37.933112],
            [-75.843768, 37.927297],
            [-75.845992, 37.926118],
            [-75.846621, 37.925785],
            [-75.847207, 37.925474],
            [-75.847473, 37.925333],
            [-75.847817, 37.925151],
            [-75.847933, 37.925089],
            [-75.848133, 37.924983],
            [-75.849103, 37.92447],
            [-75.860727, 37.91831],
            [-75.881913, 37.912563],
            [-75.883708, 37.912076],
            [-75.885032, 37.911717],
            [-75.892686, 37.916848],
            [-75.895791, 37.921406],
            [-75.89594, 37.921625],
            [-75.898316, 37.925114],
            [-75.894065, 37.93079],
            [-75.893655, 37.933879],
            [-75.89281, 37.940239],
            [-75.890871, 37.954847],
            [-75.898956, 37.974514],
            [-75.882768, 38.002995],
            [-75.875297, 38.011965],
            [-75.875399, 38.028241],
            [-75.87319, 38.034375],
            [-75.869513, 38.035407],
            [-75.865912, 38.036418],
            [-75.864648, 38.036773],
            [-75.858891, 38.03839],
            [-75.857507, 38.038778],
            [-75.856854, 38.038583],
            [-75.855468, 38.03817],
            [-75.854711, 38.037944],
            [-75.852655, 38.037331],
            [-75.850531, 38.036697],
            [-75.850343, 38.035877],
            [-75.84998, 38.034294],
            [-75.847922, 38.03437],
            [-75.847716, 38.034468],
            [-75.836018, 38.040007],
            [-75.834643, 38.040657],
            [-75.833657, 38.041125],
            [-75.833418, 38.041238],
            [-75.830023, 38.042845],
            [-75.829375, 38.043152],
            [-75.829276, 38.043247],
            [-75.826492, 38.045915],
            [-75.825987, 38.0464],
            [-75.812913, 38.058932],
            [-75.813378, 38.059481],
            [-75.813468, 38.059587],
            [-75.819415, 38.066606],
            [-75.819479, 38.066682],
            [-75.819591, 38.066814],
            [-75.830017, 38.06912],
            [-75.831103, 38.069361],
            [-75.832742, 38.069723],
            [-75.839935, 38.071314],
            [-75.841057, 38.071562],
            [-75.841206, 38.071595],
            [-75.844265, 38.072272],
            [-75.84718, 38.071289],
            [-75.852305, 38.069561],
            [-75.858944, 38.067323],
            [-75.859026, 38.067208],
            [-75.859568, 38.066448],
            [-75.860072, 38.065743],
            [-75.859717, 38.064073],
            [-75.85944, 38.062769],
            [-75.859281, 38.062018],
            [-75.859005, 38.060717],
            [-75.858881, 38.060135],
            [-75.860629, 38.05996],
            [-75.860946, 38.059928],
            [-75.861033, 38.059919],
            [-75.86153, 38.05987],
            [-75.866194, 38.059402],
            [-75.867267, 38.059295],
            [-75.867511, 38.05927],
            [-75.868899, 38.059131],
            [-75.871503, 38.05887],
            [-75.874189, 38.060288],
            [-75.880515, 38.075011],
            [-75.871394, 38.089183],
            [-75.870282, 38.090911],
            [-75.865697, 38.098036],
            [-75.865146, 38.098893],
            [-75.86381, 38.100968],
            [-75.842604, 38.113111],
            [-75.837563, 38.113753],
            [-75.837204, 38.114468],
            [-75.837165, 38.114546],
            [-75.837048, 38.114777],
            [-75.827993, 38.132803],
            [-75.827892, 38.133004],
            [-75.827674, 38.133438],
            [-75.827712, 38.133464],
            [-75.843862, 38.144599],
            [-75.849919, 38.144414],
            [-75.854507, 38.142567],
            [-75.858667, 38.140893],
            [-75.85954, 38.140542],
            [-75.866, 38.134886],
            [-75.868636, 38.134381],
            [-75.870056, 38.134684],
            [-75.871037, 38.134893],
            [-75.880707, 38.136957],
            [-75.880978, 38.137015],
            [-75.900355, 38.14115],
            [-75.901058, 38.140826],
            [-75.902004, 38.14039],
            [-75.903442, 38.139726],
            [-75.905599, 38.138732],
            [-75.906497, 38.138317],
            [-75.907264, 38.137964],
            [-75.923797, 38.130339],
            [-75.932738, 38.126216],
            [-75.936773, 38.124355],
            [-75.937089, 38.124209],
            [-75.937055, 38.123077],
            [-75.937015, 38.121749],
            [-75.936866, 38.116745],
            [-75.936663, 38.109956],
            [-75.938484, 38.109976],
            [-75.945297, 38.113091],
            [-75.949557, 38.118127],
            [-75.956428, 38.131115],
            [-75.956434, 38.131126],
            [-75.958786, 38.135572],
            [-75.959496, 38.136915],
            [-75.959616, 38.137141],
            [-75.952411, 38.158513],
            [-75.951425, 38.161436],
            [-75.951273, 38.161887],
            [-75.949752, 38.164486],
            [-75.947534, 38.168274],
            [-75.947487, 38.168736],
            [-75.947417, 38.169439],
            [-75.94728, 38.170792],
            [-75.948129, 38.171778],
            [-75.94822, 38.171884],
            [-75.948566, 38.172285],
            [-75.948709, 38.172451],
            [-75.951812, 38.176053],
            [-75.951972, 38.176239],
            [-75.95185, 38.176794],
            [-75.951583, 38.178014],
            [-75.951566, 38.178093],
            [-75.949575, 38.180037],
            [-75.94942, 38.180188],
            [-75.948891, 38.180704],
            [-75.948738, 38.180854],
            [-75.946586, 38.182955],
            [-75.945419, 38.184094],
            [-75.942375, 38.187066],
            [-75.941448, 38.187352],
            [-75.933932, 38.18967],
            [-75.888073, 38.203813],
            [-75.886217, 38.203309],
            [-75.88494, 38.200493],
            [-75.884603, 38.199751],
            [-75.884544, 38.199739],
            [-75.878293, 38.198407],
            [-75.877751, 38.198292],
            [-75.87725, 38.198386],
            [-75.870975, 38.199566],
            [-75.86847, 38.200037],
            [-75.864104, 38.200858],
            [-75.862444, 38.201759],
            [-75.85696, 38.204734],
            [-75.854406, 38.20612],
            [-75.852258, 38.207286],
            [-75.848473, 38.20934],
            [-75.847299, 38.209977],
            [-75.847032, 38.210122],
            [-75.846713, 38.210295],
            [-75.846377, 38.210477],
            [-75.851396, 38.226432],
            [-75.851528, 38.22655],
            [-75.85916, 38.233407],
            [-75.864628, 38.23832],
            [-75.768671, 38.285183],
            [-75.612764, 38.278626],
            [-75.545898, 38.17874],
            [-75.54087, 38.08948],
            [-75.661827, 38.041853],
            [-75.624341, 37.994211]
          ]
        ],
        [
          [
            [-75.993905, 37.953489],
            [-75.994739, 37.953501],
            [-76.005888, 37.953662],
            [-76.017686, 37.953832],
            [-76.020796, 37.953877],
            [-76.020932, 37.953879],
            [-76.021714, 37.953887],
            [-76.022325, 37.953878],
            [-76.029405, 37.953776],
            [-76.029463, 37.953775],
            [-76.030122, 37.953655],
            [-76.038026, 37.953901],
            [-76.041402, 37.954006],
            [-76.041691, 37.954],
            [-76.045561, 37.953669],
            [-76.04653, 37.953586],
            [-76.049608, 37.983628],
            [-76.049562, 37.985065],
            [-76.049549, 37.985495],
            [-76.049527, 37.986184],
            [-76.049396, 37.990315],
            [-76.048617, 38.014843],
            [-76.046213, 38.025533],
            [-76.041668, 38.032148],
            [-76.020827, 38.039124],
            [-76.013128, 38.039762],
            [-76.008835, 38.038275],
            [-76.008695, 38.038226],
            [-76.004592, 38.036805],
            [-75.991846, 38.025497],
            [-75.981195, 38.02246],
            [-75.977246, 38.021334],
            [-75.975494, 38.020834],
            [-75.975487, 38.020827],
            [-75.973432, 38.018841],
            [-75.972304, 38.01775],
            [-75.970172, 38.015688],
            [-75.970168, 38.015685],
            [-75.970168, 38.015683],
            [-75.970178, 38.01526],
            [-75.970345, 38.008222],
            [-75.97039, 38.006327],
            [-75.970917, 38.006239],
            [-75.979879, 38.004738],
            [-75.980683, 38.004603],
            [-75.98288, 38.004236],
            [-75.984251, 38.004006],
            [-75.984479, 38.003369],
            [-75.984675, 38.002821],
            [-75.98502, 38.001855],
            [-75.985723, 37.999889],
            [-75.986249, 37.998416],
            [-75.986571, 37.997516],
            [-75.988541, 37.992006],
            [-75.988719, 37.991509],
            [-75.989101, 37.99044],
            [-75.992468, 37.981021],
            [-75.99473, 37.974694],
            [-75.99473, 37.974693],
            [-75.992789, 37.969932],
            [-75.991543, 37.966874],
            [-75.988879, 37.960337],
            [-75.993905, 37.953489]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24041",
      state: "24",
      county: "041",
      name: "Talbot County",
      censusArea: 268.538,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.147577, 38.637893],
          [-76.148202, 38.639463],
          [-76.148559, 38.640362],
          [-76.148955, 38.641356],
          [-76.149353, 38.642356],
          [-76.152554, 38.6504],
          [-76.152603, 38.650523],
          [-76.152622, 38.650572],
          [-76.15299, 38.651495],
          [-76.153414, 38.652562],
          [-76.153769, 38.653455],
          [-76.154093, 38.654269],
          [-76.154581, 38.655495],
          [-76.154604, 38.655553],
          [-76.154889, 38.656268],
          [-76.154924, 38.656358],
          [-76.155611, 38.658083],
          [-76.160539, 38.661903],
          [-76.164465, 38.664946],
          [-76.168824, 38.668325],
          [-76.174611, 38.672811],
          [-76.175159, 38.673236],
          [-76.196716, 38.67286],
          [-76.199722, 38.671127],
          [-76.199948, 38.670997],
          [-76.200334, 38.670774],
          [-76.212808, 38.681892],
          [-76.226343, 38.698057],
          [-76.229242, 38.701519],
          [-76.231392, 38.704088],
          [-76.231758, 38.704524],
          [-76.232127, 38.704964],
          [-76.232365, 38.705249],
          [-76.232535, 38.705452],
          [-76.232814, 38.705785],
          [-76.232845, 38.705823],
          [-76.232875, 38.705859],
          [-76.232893, 38.70588],
          [-76.234249, 38.707499],
          [-76.234379, 38.707655],
          [-76.234392, 38.70767],
          [-76.234828, 38.708191],
          [-76.237818, 38.711762],
          [-76.237916, 38.711879],
          [-76.238077, 38.712071],
          [-76.238172, 38.712185],
          [-76.238306, 38.712344],
          [-76.238725, 38.712845],
          [-76.239377, 38.71688],
          [-76.239427, 38.717191],
          [-76.239841, 38.719756],
          [-76.23704, 38.724518],
          [-76.238685, 38.735434],
          [-76.255093, 38.736476],
          [-76.255348, 38.736273],
          [-76.257335, 38.734691],
          [-76.257423, 38.734621],
          [-76.257437, 38.734609],
          [-76.257851, 38.73428],
          [-76.258738, 38.733573],
          [-76.259238, 38.733176],
          [-76.259665, 38.732836],
          [-76.259741, 38.732775],
          [-76.259867, 38.732675],
          [-76.259876, 38.732667],
          [-76.260001, 38.732568],
          [-76.260976, 38.731791],
          [-76.261863, 38.731085],
          [-76.268288, 38.725969],
          [-76.268739, 38.725609],
          [-76.26937, 38.725107],
          [-76.26952, 38.724988],
          [-76.270277, 38.724385],
          [-76.271243, 38.716209],
          [-76.271447, 38.71448],
          [-76.271553, 38.713576],
          [-76.271596, 38.713216],
          [-76.275015, 38.712714],
          [-76.275019, 38.712715],
          [-76.298499, 38.718005],
          [-76.299171, 38.719287],
          [-76.296603, 38.723183],
          [-76.296537, 38.723283],
          [-76.295957, 38.724162],
          [-76.298186, 38.726255],
          [-76.29935, 38.727347],
          [-76.299401, 38.727395],
          [-76.312756, 38.730708],
          [-76.316146, 38.729586],
          [-76.321803, 38.723565],
          [-76.321885, 38.723478],
          [-76.322019, 38.723335],
          [-76.322028, 38.723326],
          [-76.322663, 38.72265],
          [-76.322705, 38.722605],
          [-76.322706, 38.722604],
          [-76.32273, 38.722578],
          [-76.322807, 38.722496],
          [-76.32286, 38.722439],
          [-76.322975, 38.722317],
          [-76.323214, 38.722063],
          [-76.32353, 38.721727],
          [-76.326135, 38.718954],
          [-76.327162, 38.717861],
          [-76.328035, 38.716932],
          [-76.328338, 38.716609],
          [-76.330149, 38.714682],
          [-76.331479, 38.713266],
          [-76.332517, 38.709118],
          [-76.333066, 38.706927],
          [-76.333532, 38.705063],
          [-76.333777, 38.704086],
          [-76.334017, 38.703127],
          [-76.333862, 38.702953],
          [-76.332079, 38.700956],
          [-76.321865, 38.689512],
          [-76.321881, 38.689217],
          [-76.321898, 38.688902],
          [-76.321905, 38.688768],
          [-76.321915, 38.688584],
          [-76.322418, 38.679304],
          [-76.33861, 38.672023],
          [-76.340065, 38.671369],
          [-76.340341, 38.671245],
          [-76.34322, 38.67688],
          [-76.343277, 38.676992],
          [-76.343784, 38.677985],
          [-76.344199, 38.678798],
          [-76.347998, 38.686234],
          [-76.345072, 38.703546],
          [-76.344597, 38.706352],
          [-76.340543, 38.730338],
          [-76.340567, 38.731008],
          [-76.340608, 38.732179],
          [-76.340618, 38.732464],
          [-76.340628, 38.732756],
          [-76.340632, 38.732857],
          [-76.340635, 38.73296],
          [-76.340637, 38.733003],
          [-76.340641, 38.733123],
          [-76.340645, 38.733225],
          [-76.340677, 38.734136],
          [-76.340715, 38.735215],
          [-76.340732, 38.735712],
          [-76.340739, 38.735894],
          [-76.34074, 38.735932],
          [-76.341093, 38.745959],
          [-76.341096, 38.746058],
          [-76.3411, 38.746148],
          [-76.341288, 38.751505],
          [-76.341293, 38.751654],
          [-76.341297, 38.751756],
          [-76.341302, 38.751901],
          [-76.334619, 38.772911],
          [-76.333752, 38.77342],
          [-76.329721, 38.775789],
          [-76.323768, 38.779287],
          [-76.3227, 38.780657],
          [-76.312886, 38.793247],
          [-76.310743, 38.795996],
          [-76.310664, 38.796098],
          [-76.310626, 38.796147],
          [-76.310556, 38.796237],
          [-76.310081, 38.796846],
          [-76.308922, 38.813346],
          [-76.308306, 38.814331],
          [-76.308294, 38.814349],
          [-76.308127, 38.814618],
          [-76.307267, 38.815993],
          [-76.301886, 38.824595],
          [-76.300889, 38.82619],
          [-76.300886, 38.826192],
          [-76.298956, 38.827519],
          [-76.2978, 38.828314],
          [-76.296564, 38.828186],
          [-76.296471, 38.828176],
          [-76.296394, 38.828168],
          [-76.296364, 38.828165],
          [-76.296329, 38.828162],
          [-76.29632, 38.828161],
          [-76.296259, 38.828155],
          [-76.295992, 38.828127],
          [-76.29555, 38.828081],
          [-76.295454, 38.828071],
          [-76.295436, 38.828069],
          [-76.293648, 38.827884],
          [-76.292144, 38.827729],
          [-76.288455, 38.827347],
          [-76.284768, 38.828706],
          [-76.277854, 38.831256],
          [-76.277748, 38.831295],
          [-76.277411, 38.831419],
          [-76.27742, 38.831468],
          [-76.278151, 38.835494],
          [-76.27804, 38.835768],
          [-76.274405, 38.844766],
          [-76.271575, 38.851771],
          [-76.267175, 38.851652],
          [-76.265999, 38.85162],
          [-76.264221, 38.851572],
          [-76.265759, 38.847638],
          [-76.265808, 38.847512],
          [-76.265678, 38.847326],
          [-76.262179, 38.842325],
          [-76.261907, 38.841936],
          [-76.261627, 38.841536],
          [-76.257353, 38.835428],
          [-76.255787, 38.833189],
          [-76.255343, 38.832554],
          [-76.255322, 38.832525],
          [-76.250364, 38.825438],
          [-76.250296, 38.825389],
          [-76.245886, 38.822232],
          [-76.23877, 38.81959],
          [-76.229199, 38.816036],
          [-76.228071, 38.815617],
          [-76.221162, 38.813052],
          [-76.219328, 38.812371],
          [-76.198138, 38.81444],
          [-76.197432, 38.815541],
          [-76.196488, 38.817015],
          [-76.196426, 38.817111],
          [-76.19537, 38.818759],
          [-76.19343, 38.821787],
          [-76.193272, 38.822319],
          [-76.193073, 38.822989],
          [-76.19302, 38.823165],
          [-76.192924, 38.823489],
          [-76.192865, 38.823687],
          [-76.192765, 38.824024],
          [-76.192537, 38.82479],
          [-76.19109, 38.82966],
          [-76.191172, 38.829834],
          [-76.192874, 38.83345],
          [-76.192946, 38.833603],
          [-76.192973, 38.833661],
          [-76.197705, 38.843712],
          [-76.197736, 38.84383],
          [-76.199544, 38.850817],
          [-76.103512, 38.882308],
          [-76.096956, 38.944259],
          [-75.949335, 38.918333],
          [-75.944256, 38.916517],
          [-75.943976, 38.914294],
          [-75.948243, 38.914515],
          [-75.948726, 38.912037],
          [-75.943145, 38.907257],
          [-75.943862, 38.906204],
          [-75.94812, 38.90456],
          [-75.948677, 38.902389],
          [-75.944908, 38.900348],
          [-75.944358, 38.897548],
          [-75.945945, 38.893913],
          [-75.948616, 38.89225],
          [-75.94831, 38.891323],
          [-75.944587, 38.890068],
          [-75.940399, 38.88507],
          [-75.941535, 38.884075],
          [-75.94548, 38.884029],
          [-75.949638, 38.884807],
          [-75.950904, 38.884143],
          [-75.951469, 38.880237],
          [-75.948753, 38.878444],
          [-75.943847, 38.879344],
          [-75.941482, 38.881179],
          [-75.939224, 38.881332],
          [-75.937644, 38.879802],
          [-75.937583, 38.877994],
          [-75.938919, 38.876392],
          [-75.943572, 38.87638],
          [-75.944396, 38.875087],
          [-75.943077, 38.870494],
          [-75.941551, 38.868022],
          [-75.939292, 38.866558],
          [-75.934707, 38.867511],
          [-75.932914, 38.866035],
          [-75.930229, 38.861286],
          [-75.924331, 38.860389],
          [-75.922187, 38.857257],
          [-75.922431, 38.856369],
          [-75.922797, 38.854785],
          [-75.928336, 38.852428],
          [-75.930373, 38.850872],
          [-75.929312, 38.849021],
          [-75.926772, 38.847038],
          [-75.919455, 38.846897],
          [-75.918975, 38.846084],
          [-75.919348, 38.845016],
          [-75.928099, 38.841068],
          [-75.927771, 38.838893],
          [-75.919432, 38.835205],
          [-75.915183, 38.834835],
          [-75.913711, 38.832965],
          [-75.912918, 38.829532],
          [-75.904341, 38.826431],
          [-75.901267, 38.824737],
          [-75.901747, 38.822883],
          [-75.905494, 38.821567],
          [-75.906455, 38.819705],
          [-75.903251, 38.816764],
          [-75.896774, 38.815109],
          [-75.903778, 38.806934],
          [-75.911369, 38.806285],
          [-75.920799, 38.802635],
          [-75.927963, 38.799201],
          [-75.932518, 38.796138],
          [-75.937347, 38.787242],
          [-75.949455, 38.778419],
          [-75.954551, 38.777496],
          [-75.966369, 38.779762],
          [-75.968238, 38.777729],
          [-75.96775, 38.774398],
          [-75.976409, 38.765506],
          [-75.979965, 38.76371],
          [-75.993759, 38.759284],
          [-75.996902, 38.757507],
          [-75.998947, 38.755596],
          [-75.999336, 38.752075],
          [-75.99459, 38.741886],
          [-75.995376, 38.736679],
          [-75.999671, 38.734836],
          [-76.007354, 38.736751],
          [-76.009979, 38.736034],
          [-76.013206, 38.733291],
          [-76.015045, 38.728687],
          [-75.945243, 38.674114],
          [-75.991127, 38.592483],
          [-76.023445, 38.573875],
          [-76.152885, 38.631819],
          [-76.147158, 38.63684],
          [-76.147577, 38.637893]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25011",
      state: "25",
      county: "011",
      name: "Franklin County",
      censusArea: 699.319,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.876948, 42.541213],
          [-72.975379, 42.555933],
          [-72.951039, 42.641006],
          [-72.949742, 42.705103],
          [-73.022903, 42.741133],
          [-73.018646, 42.741025],
          [-72.930271, 42.738789],
          [-72.927291, 42.738714],
          [-72.86418, 42.737117],
          [-72.458519, 42.726853],
          [-72.451257, 42.726653],
          [-72.412011, 42.725569],
          [-72.282968, 42.722008],
          [-72.224839, 42.638981],
          [-72.244952, 42.513552],
          [-72.314599, 42.343526],
          [-72.356583, 42.303275],
          [-72.355396, 42.340678],
          [-72.375022, 42.420819],
          [-72.54421, 42.427081],
          [-72.581106, 42.422504],
          [-72.704345, 42.405536],
          [-72.763746, 42.46361],
          [-72.871157, 42.484173],
          [-72.876948, 42.541213]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26009",
      state: "26",
      county: "009",
      name: "Antrim County",
      censusArea: 475.703,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.332836, 44.812372],
          [-85.44225, 44.859817],
          [-85.425804, 44.881646],
          [-85.423003, 44.895019],
          [-85.406173, 44.911773],
          [-85.3958, 44.931018],
          [-85.378286, 44.998587],
          [-85.381654, 45.018407],
          [-85.380659, 45.046319],
          [-85.377586, 45.055713],
          [-85.366412, 45.069023],
          [-85.366908, 45.116938],
          [-85.372571, 45.126241],
          [-85.376948, 45.142881],
          [-85.380464, 45.180876],
          [-85.386726, 45.189497],
          [-85.387463, 45.207565],
          [-85.222705, 45.205551],
          [-85.223679, 45.117751],
          [-84.857452, 45.116707],
          [-84.847309, 44.858037],
          [-85.291716, 44.859608],
          [-85.332836, 44.812372]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26015",
      state: "26",
      county: "015",
      name: "Barry County",
      censusArea: 553.092,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.074245, 42.770784],
          [-85.071609, 42.421428],
          [-85.298879, 42.419849],
          [-85.543191, 42.421433],
          [-85.545564, 42.768139],
          [-85.309626, 42.769878],
          [-85.074245, 42.770784]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26023",
      state: "26",
      county: "023",
      name: "Branch County",
      censusArea: 506.369,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.259847, 42.071851],
          [-84.826491, 42.072468],
          [-84.82513, 41.759991],
          [-84.825196, 41.75999],
          [-84.932484, 41.759691],
          [-84.96086, 41.759438],
          [-84.961562, 41.759552],
          [-84.971551, 41.759527],
          [-84.972803, 41.759366],
          [-85.037817, 41.759801],
          [-85.039436, 41.759985],
          [-85.117267, 41.7597],
          [-85.123102, 41.759743],
          [-85.17223, 41.759618],
          [-85.196637, 41.759735],
          [-85.196774, 41.759735],
          [-85.232835, 41.759839],
          [-85.272216, 41.759999],
          [-85.272951, 41.759911],
          [-85.273713, 41.75977],
          [-85.292099, 41.759962],
          [-85.292178, 41.759963],
          [-85.293626, 42.071553],
          [-85.259847, 42.071851]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26035",
      state: "26",
      county: "035",
      name: "Clare County",
      censusArea: 564.315,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.608104, 44.160482],
          [-84.608001, 44.15859],
          [-84.606035, 43.815218],
          [-85.088811, 43.813676],
          [-85.08704, 44.164239],
          [-84.851705, 44.161375],
          [-84.608104, 44.160482]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26037",
      state: "26",
      county: "037",
      name: "Clinton County",
      censusArea: 566.408,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.367795, 43.117938],
          [-84.365712, 42.95512],
          [-84.363659, 42.775778],
          [-84.36366, 42.769971],
          [-84.602761, 42.76988],
          [-84.837085, 42.770479],
          [-84.836889, 43.118851],
          [-84.367795, 43.117938]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26045",
      state: "26",
      county: "045",
      name: "Eaton County",
      censusArea: 575.175,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.600403, 42.421984],
          [-84.718493, 42.42152],
          [-85.071609, 42.421428],
          [-85.074245, 42.770784],
          [-84.837085, 42.770479],
          [-84.602761, 42.76988],
          [-84.600403, 42.421984]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26057",
      state: "26",
      county: "057",
      name: "Gratiot County",
      censusArea: 568.464,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.60754, 43.466006],
          [-84.369876, 43.466044],
          [-84.367891, 43.128452],
          [-84.367893, 43.124671],
          [-84.367863, 43.124456],
          [-84.367795, 43.117938],
          [-84.836889, 43.118851],
          [-84.845962, 43.466158],
          [-84.60754, 43.466006]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26061",
      state: "26",
      county: "061",
      name: "Houghton County",
      censusArea: 1009.099,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.450823, 46.939038],
          [-88.4512, 46.938976],
          [-88.552185, 46.938533],
          [-88.551943, 46.851795],
          [-88.678983, 46.851822],
          [-88.679392, 46.420139],
          [-88.991236, 46.419704],
          [-88.989529, 46.680119],
          [-88.864474, 46.679245],
          [-88.863906, 46.765013],
          [-88.93248, 46.764787],
          [-88.933329, 47.0322],
          [-88.925586, 47.040923],
          [-88.924492, 47.042156],
          [-88.923458, 47.043872],
          [-88.914976, 47.05794],
          [-88.914189, 47.059246],
          [-88.908288, 47.074396],
          [-88.903933, 47.085579],
          [-88.903706, 47.086161],
          [-88.899857, 47.089969],
          [-88.890708, 47.099024],
          [-88.890422, 47.099306],
          [-88.890122, 47.099603],
          [-88.88914, 47.100575],
          [-88.885418, 47.102084],
          [-88.885361, 47.102107],
          [-88.872087, 47.107487],
          [-88.855372, 47.114263],
          [-88.855126, 47.11429],
          [-88.854656, 47.114343],
          [-88.848176, 47.115065],
          [-88.826028, 47.132558],
          [-88.825619, 47.132881],
          [-88.825475, 47.132994],
          [-88.816684, 47.139938],
          [-88.816553, 47.140042],
          [-88.815403, 47.14095],
          [-88.815355, 47.140987],
          [-88.814834, 47.141399],
          [-88.813748, 47.141813],
          [-88.798779, 47.147511],
          [-88.789813, 47.150925],
          [-88.784612, 47.150722],
          [-88.783821, 47.150691],
          [-88.779188, 47.15051],
          [-88.778022, 47.150465],
          [-88.764351, 47.155762],
          [-88.750496, 47.167782],
          [-88.746724, 47.171055],
          [-88.74665, 47.171118],
          [-88.729688, 47.185834],
          [-88.728541, 47.18656],
          [-88.728327, 47.186695],
          [-88.712138, 47.196937],
          [-88.702086, 47.203296],
          [-88.702029, 47.203333],
          [-88.700047, 47.204586],
          [-88.69966, 47.204831],
          [-88.698919, 47.20522],
          [-88.698282, 47.205554],
          [-88.676624, 47.216918],
          [-88.674917, 47.217814],
          [-88.673771, 47.218415],
          [-88.673172, 47.218729],
          [-88.672395, 47.219137],
          [-88.666614, 47.221475],
          [-88.657496, 47.225164],
          [-88.656359, 47.225624],
          [-88.648001, 47.226229],
          [-88.642047, 47.226659],
          [-88.640323, 47.226784],
          [-88.633915, 47.228915],
          [-88.625119, 47.23184],
          [-88.623579, 47.232352],
          [-88.623302, 47.232484],
          [-88.618613, 47.234715],
          [-88.609867, 47.238877],
          [-88.60983, 47.238894],
          [-88.608087, 47.239137],
          [-88.598622, 47.240454],
          [-88.585196, 47.242321],
          [-88.584912, 47.242361],
          [-88.584652, 47.242447],
          [-88.573997, 47.245989],
          [-88.573326, 47.246424],
          [-88.573301, 47.246441],
          [-88.57172, 47.247466],
          [-88.526764, 47.276641],
          [-88.518091, 47.282269],
          [-88.515276, 47.284096],
          [-88.514786, 47.284414],
          [-88.512421, 47.285948],
          [-88.298992, 47.286097],
          [-88.298436, 47.199782],
          [-88.227552, 47.199938],
          [-88.228987, 47.199042],
          [-88.229132, 47.198862],
          [-88.235041, 47.191532],
          [-88.236892, 47.189236],
          [-88.237024, 47.188862],
          [-88.240295, 47.179609],
          [-88.242006, 47.174767],
          [-88.242109, 47.172184],
          [-88.242142, 47.171358],
          [-88.242157, 47.170988],
          [-88.242561, 47.160902],
          [-88.24266, 47.158426],
          [-88.242062, 47.157059],
          [-88.239487, 47.151176],
          [-88.23947, 47.151137],
          [-88.238408, 47.150423],
          [-88.236961, 47.149449],
          [-88.236721, 47.149287],
          [-88.236566, 47.149297],
          [-88.234295, 47.149446],
          [-88.231797, 47.149609],
          [-88.232164, 47.145975],
          [-88.239895, 47.139436],
          [-88.247628, 47.135981],
          [-88.249571, 47.136231],
          [-88.249838, 47.137106],
          [-88.250645, 47.139752],
          [-88.250785, 47.140209],
          [-88.250814, 47.140231],
          [-88.253834, 47.142524],
          [-88.254205, 47.142807],
          [-88.254267, 47.142853],
          [-88.255303, 47.14364],
          [-88.255676, 47.143715],
          [-88.262537, 47.145087],
          [-88.262972, 47.145174],
          [-88.263292, 47.145115],
          [-88.272017, 47.143511],
          [-88.281652, 47.138239],
          [-88.281701, 47.138212],
          [-88.281743, 47.138163],
          [-88.288994, 47.129743],
          [-88.28904, 47.129689],
          [-88.289058, 47.129581],
          [-88.289543, 47.126604],
          [-88.28865, 47.125947],
          [-88.288481, 47.125823],
          [-88.28787, 47.125374],
          [-88.287792, 47.124822],
          [-88.28726, 47.121041],
          [-88.287173, 47.12042],
          [-88.287313, 47.119721],
          [-88.287822, 47.117174],
          [-88.287901, 47.116779],
          [-88.287981, 47.116376],
          [-88.288309, 47.114738],
          [-88.288347, 47.114547],
          [-88.28869, 47.113954],
          [-88.28893, 47.113539],
          [-88.289005, 47.11341],
          [-88.289534, 47.112494],
          [-88.290598, 47.110655],
          [-88.290708, 47.110465],
          [-88.291217, 47.109585],
          [-88.291688, 47.10877],
          [-88.294082, 47.10463],
          [-88.296645, 47.100199],
          [-88.297547, 47.098639],
          [-88.297625, 47.098505],
          [-88.297772, 47.098443],
          [-88.313033, 47.091964],
          [-88.315051, 47.091108],
          [-88.333076, 47.083455],
          [-88.337406, 47.081617],
          [-88.340052, 47.080494],
          [-88.344658, 47.079718],
          [-88.346501, 47.079407],
          [-88.346709, 47.079372],
          [-88.347197, 47.078921],
          [-88.349952, 47.076377],
          [-88.353191, 47.069063],
          [-88.353952, 47.058047],
          [-88.354011, 47.057835],
          [-88.356884, 47.047524],
          [-88.357556, 47.045113],
          [-88.357661, 47.044739],
          [-88.358201, 47.042798],
          [-88.35855, 47.041546],
          [-88.358873, 47.040389],
          [-88.359054, 47.039739],
          [-88.359139, 47.039536],
          [-88.367624, 47.019213],
          [-88.368062, 47.018733],
          [-88.368358, 47.018408],
          [-88.373966, 47.012262],
          [-88.374238, 47.012081],
          [-88.379268, 47.008736],
          [-88.384442, 47.005296],
          [-88.385338, 47.0047],
          [-88.385507, 47.004588],
          [-88.385606, 47.004522],
          [-88.386153, 47.003909],
          [-88.386538, 47.003477],
          [-88.387612, 47.002275],
          [-88.388564, 47.001208],
          [-88.389698, 46.999937],
          [-88.404498, 46.983353],
          [-88.405352, 46.982663],
          [-88.406229, 46.981955],
          [-88.410157, 46.978782],
          [-88.41089, 46.97819],
          [-88.411145, 46.977984],
          [-88.41616, 46.977106],
          [-88.416364, 46.977071],
          [-88.443901, 46.972251],
          [-88.44857, 46.946769],
          [-88.450823, 46.939038]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26073",
      state: "26",
      county: "073",
      name: "Isabella County",
      censusArea: 572.677,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.084996, 43.46619],
          [-85.088811, 43.813676],
          [-84.606035, 43.815218],
          [-84.60754, 43.466006],
          [-84.845962, 43.466158],
          [-85.084996, 43.46619]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26077",
      state: "26",
      county: "077",
      name: "Kalamazoo County",
      censusArea: 561.658,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.298879, 42.419849],
          [-85.29948, 42.416794],
          [-85.296996, 42.246071],
          [-85.293626, 42.071553],
          [-85.762943, 42.069327],
          [-85.764352, 42.420669],
          [-85.543191, 42.421433],
          [-85.298879, 42.419849]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26083",
      state: "26",
      county: "083",
      name: "Keweenaw County",
      censusArea: 540.112,
      stateName: "Michigan"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-88.684434, 48.115785],
            [-88.675628, 48.120444],
            [-88.676395, 48.124876],
            [-88.674192, 48.127165],
            [-88.656915, 48.139225],
            [-88.614026, 48.154797],
            [-88.578413, 48.16237],
            [-88.547033, 48.174891],
            [-88.524753, 48.165291],
            [-88.501088, 48.168181],
            [-88.491961, 48.175466],
            [-88.482039, 48.179915],
            [-88.459735, 48.183807],
            [-88.447236, 48.182916],
            [-88.422601, 48.190975],
            [-88.418244, 48.18037],
            [-88.419875, 48.170731],
            [-88.427373, 48.166764],
            [-88.449502, 48.163312],
            [-88.459697, 48.158551],
            [-88.469573, 48.152879],
            [-88.4857, 48.137683],
            [-88.511902, 48.121699],
            [-88.566938, 48.093719],
            [-88.578053, 48.084373],
            [-88.578395, 48.078003],
            [-88.575869, 48.075166],
            [-88.573924, 48.068861],
            [-88.575048, 48.064154],
            [-88.579784, 48.058669],
            [-88.670073, 48.011446],
            [-88.718555, 47.995134],
            [-88.772357, 47.981126],
            [-88.791959, 47.978938],
            [-88.832063, 47.965213],
            [-88.852923, 47.965322],
            [-88.899184, 47.9533],
            [-88.918029, 47.945605],
            [-88.923573, 47.937976],
            [-88.962664, 47.923512],
            [-88.968903, 47.909474],
            [-88.968903, 47.901675],
            [-88.957985, 47.895436],
            [-88.942387, 47.895436],
            [-88.899698, 47.902445],
            [-88.898986, 47.900685],
            [-88.911665, 47.891344],
            [-88.998939, 47.86749],
            [-89.022736, 47.858532],
            [-89.044463, 47.85575],
            [-89.056412, 47.852598],
            [-89.107991, 47.835705],
            [-89.124134, 47.828616],
            [-89.157738, 47.824015],
            [-89.19017, 47.831603],
            [-89.192681, 47.83343],
            [-89.192207, 47.84106],
            [-89.201812, 47.850243],
            [-89.234533, 47.851718],
            [-89.235552, 47.853774],
            [-89.234535, 47.855373],
            [-89.228507, 47.858039],
            [-89.246774, 47.871016],
            [-89.250936, 47.870377],
            [-89.255202, 47.876102],
            [-89.247127, 47.888503],
            [-89.226327, 47.895438],
            [-89.22071, 47.90085],
            [-89.221332, 47.908069],
            [-89.214499, 47.913895],
            [-89.179154, 47.93503],
            [-89.095207, 47.967922],
            [-89.018303, 47.992525],
            [-88.994163, 48.00229],
            [-88.940886, 48.01959],
            [-88.931487, 48.021637],
            [-88.927529, 48.019615],
            [-88.915032, 48.020681],
            [-88.895069, 48.029059],
            [-88.896327, 48.031801],
            [-88.893701, 48.03477],
            [-88.835714, 48.056752],
            [-88.816084, 48.057006],
            [-88.810461, 48.055247],
            [-88.787556, 48.063035],
            [-88.772077, 48.070502],
            [-88.77183, 48.079457],
            [-88.764256, 48.085189],
            [-88.744458, 48.092769],
            [-88.728198, 48.101914],
            [-88.705586, 48.111013],
            [-88.695353, 48.110549],
            [-88.684434, 48.115785]
          ]
        ],
        [
          [
            [-88.512421, 47.285948],
            [-88.504898, 47.290831],
            [-88.50116, 47.293256],
            [-88.50078, 47.293503],
            [-88.498756, 47.295256],
            [-88.487116, 47.305335],
            [-88.478951, 47.312405],
            [-88.477733, 47.31346],
            [-88.471103, 47.326442],
            [-88.470484, 47.327653],
            [-88.465888, 47.33267],
            [-88.465089, 47.333542],
            [-88.459262, 47.339903],
            [-88.458952, 47.340142],
            [-88.432162, 47.360791],
            [-88.431753, 47.361106],
            [-88.431446, 47.361343],
            [-88.418841, 47.371058],
            [-88.418673, 47.371188],
            [-88.400224, 47.379551],
            [-88.399726, 47.379777],
            [-88.394239, 47.382264],
            [-88.39144, 47.383533],
            [-88.389459, 47.384431],
            [-88.378917, 47.387513],
            [-88.360313, 47.392951],
            [-88.324083, 47.403542],
            [-88.303447, 47.412204],
            [-88.301214, 47.413451],
            [-88.285635, 47.422146],
            [-88.285195, 47.422392],
            [-88.28406, 47.422579],
            [-88.274709, 47.424118],
            [-88.23944, 47.429923],
            [-88.239161, 47.429969],
            [-88.238091, 47.430437],
            [-88.228645, 47.434568],
            [-88.227446, 47.435093],
            [-88.225797, 47.436279],
            [-88.218424, 47.441585],
            [-88.216977, 47.445493],
            [-88.217822, 47.448738],
            [-88.217662, 47.448778],
            [-88.213141, 47.449898],
            [-88.213059, 47.449918],
            [-88.207853, 47.451208],
            [-88.207239, 47.45136],
            [-88.206036, 47.451658],
            [-88.18182, 47.457657],
            [-88.181763, 47.457661],
            [-88.179998, 47.457799],
            [-88.169383, 47.458627],
            [-88.167703, 47.458758],
            [-88.167009, 47.458812],
            [-88.15076, 47.460078],
            [-88.150571, 47.460093],
            [-88.150437, 47.460125],
            [-88.143573, 47.461759],
            [-88.140537, 47.462482],
            [-88.139651, 47.462693],
            [-88.137698, 47.462918],
            [-88.129274, 47.463889],
            [-88.12848, 47.46398],
            [-88.090299, 47.46838],
            [-88.089966, 47.468418],
            [-88.085252, 47.468961],
            [-88.08485, 47.468906],
            [-88.08358, 47.468733],
            [-88.081892, 47.468503],
            [-88.081277, 47.468419],
            [-88.080916, 47.46837],
            [-88.080796, 47.468353],
            [-88.080271, 47.468282],
            [-88.079183, 47.468133],
            [-88.076388, 47.467752],
            [-88.074681, 47.46788],
            [-88.073982, 47.467933],
            [-88.07358, 47.467963],
            [-88.067043, 47.468454],
            [-88.049326, 47.469785],
            [-88.048226, 47.470008],
            [-88.048077, 47.474973],
            [-88.047857, 47.475002],
            [-88.046665, 47.475159],
            [-88.040291, 47.475999],
            [-88.040242, 47.476002],
            [-88.03659, 47.476205],
            [-88.03113, 47.47651],
            [-88.031089, 47.476512],
            [-88.027085, 47.476735],
            [-88.021555, 47.477044],
            [-88.017268, 47.477283],
            [-88.015502, 47.477381],
            [-88.014516, 47.477436],
            [-88.01083, 47.477642],
            [-88.010415, 47.477665],
            [-88.009704, 47.477704],
            [-87.982227, 47.479236],
            [-87.98174, 47.479264],
            [-87.978934, 47.47942],
            [-87.978121, 47.479409],
            [-87.962546, 47.479195],
            [-87.959166, 47.479148],
            [-87.958042, 47.479133],
            [-87.957582, 47.479126],
            [-87.95669, 47.479114],
            [-87.955374, 47.479096],
            [-87.954672, 47.479086],
            [-87.954458, 47.479083],
            [-87.951912, 47.479048],
            [-87.948287, 47.478999],
            [-87.929672, 47.478743],
            [-87.929269, 47.478737],
            [-87.928726, 47.478703],
            [-87.928137, 47.478666],
            [-87.927548, 47.478629],
            [-87.922224, 47.478293],
            [-87.921103, 47.478222],
            [-87.920321, 47.478173],
            [-87.920259, 47.478169],
            [-87.902416, 47.477045],
            [-87.898036, 47.474872],
            [-87.816958, 47.471998],
            [-87.801184, 47.473301],
            [-87.756739, 47.460717],
            [-87.730804, 47.449112],
            [-87.715942, 47.439816],
            [-87.710471, 47.4062],
            [-87.712421, 47.4014],
            [-87.721274, 47.401032],
            [-87.742417, 47.405823],
            [-87.75138, 47.405066],
            [-87.759057, 47.403013],
            [-87.765019, 47.398652],
            [-87.800294, 47.392148],
            [-87.815371, 47.38479],
            [-87.827115, 47.38616],
            [-87.834822, 47.390478],
            [-87.848252, 47.394864],
            [-87.8567, 47.395387],
            [-87.882245, 47.395588],
            [-87.941613, 47.390073],
            [-87.957058, 47.38726],
            [-87.965063, 47.37443],
            [-87.965598, 47.368645],
            [-87.962567, 47.362543],
            [-87.954796, 47.356809],
            [-87.947397, 47.355461],
            [-87.938787, 47.346777],
            [-87.93825, 47.342299],
            [-87.94336, 47.335899],
            [-87.946352, 47.334254],
            [-87.958386, 47.334435],
            [-87.968604, 47.332582],
            [-87.989133, 47.322633],
            [-88.016478, 47.306275],
            [-88.054849, 47.29824],
            [-88.06009, 47.295796],
            [-88.071476, 47.286768],
            [-88.096851, 47.261351],
            [-88.108833, 47.259131],
            [-88.117456, 47.255174],
            [-88.131943, 47.239554],
            [-88.163059, 47.216278],
            [-88.194218, 47.209242],
            [-88.204849, 47.210498],
            [-88.212361, 47.209423],
            [-88.227552, 47.199938],
            [-88.298436, 47.199782],
            [-88.298992, 47.286097],
            [-88.512421, 47.285948]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26085",
      state: "26",
      county: "085",
      name: "Lake County",
      censusArea: 567.37,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.041988, 44.167091],
          [-85.821186, 44.164197],
          [-85.819811, 44.164301],
          [-85.564554, 44.164893],
          [-85.562439, 43.815634],
          [-86.037884, 43.815611],
          [-86.038948, 43.818977],
          [-86.041988, 44.167091]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26091",
      state: "26",
      county: "091",
      name: "Lenawee County",
      censusArea: 749.555,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.880539, 41.720081],
          [-84.360419, 41.706925],
          [-84.363297, 42.073456],
          [-84.131963, 42.071577],
          [-83.773922, 42.08243],
          [-83.763155, 41.72391],
          [-83.880387, 41.720086],
          [-83.880539, 41.720081]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26097",
      state: "26",
      county: "097",
      name: "Mackinac County",
      censusArea: 1021.569,
      stateName: "Michigan"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-84.593232, 45.817389],
            [-84.594241, 45.818776],
            [-84.597043, 45.822629],
            [-84.61184, 45.833429],
            [-84.611893, 45.833487],
            [-84.612845, 45.834528],
            [-84.623863, 45.846579],
            [-84.629239, 45.850014],
            [-84.6397, 45.852624],
            [-84.650783, 45.85921],
            [-84.651336, 45.862844],
            [-84.646876, 45.884642],
            [-84.641804, 45.885486],
            [-84.629437, 45.882578],
            [-84.622515, 45.87753],
            [-84.6082, 45.861631],
            [-84.602922, 45.85164],
            [-84.589272, 45.825795],
            [-84.578328, 45.820092],
            [-84.538395, 45.807843],
            [-84.514441, 45.81012],
            [-84.492951, 45.805343],
            [-84.432472, 45.786732],
            [-84.426724, 45.788194],
            [-84.421267, 45.792694],
            [-84.419696, 45.799823],
            [-84.42159, 45.805651],
            [-84.419335, 45.806747],
            [-84.41091, 45.797217],
            [-84.403208, 45.784394],
            [-84.35602, 45.771895],
            [-84.356312, 45.769495],
            [-84.370241, 45.75619],
            [-84.372248, 45.745784],
            [-84.394292, 45.735301],
            [-84.3956, 45.732925],
            [-84.394038, 45.727623],
            [-84.405852, 45.722417],
            [-84.418902, 45.721712],
            [-84.430026, 45.726307],
            [-84.45104, 45.727952],
            [-84.469183, 45.732246],
            [-84.484128, 45.73071],
            [-84.500892, 45.737259],
            [-84.507476, 45.744644],
            [-84.509301, 45.754336],
            [-84.52506, 45.764168],
            [-84.549902, 45.789859],
            [-84.561562, 45.796213],
            [-84.58195, 45.802633],
            [-84.587572, 45.8067],
            [-84.590198, 45.813217],
            [-84.593232, 45.817389]
          ]
        ],
        [
          [
            [-84.114284, 45.978322],
            [-84.140461, 45.975348],
            [-84.140816, 45.975308],
            [-84.169368, 45.966919],
            [-84.17225, 45.966072],
            [-84.174763, 45.967414],
            [-84.175244, 45.967671],
            [-84.17806, 45.969175],
            [-84.203135, 45.968516],
            [-84.213885, 45.968233],
            [-84.218951, 45.9681],
            [-84.238174, 45.967595],
            [-84.253993, 45.956727],
            [-84.254952, 45.956068],
            [-84.2613, 45.956083],
            [-84.261858, 45.956084],
            [-84.266673, 45.956096],
            [-84.281663, 45.956132],
            [-84.317422, 45.956217],
            [-84.319565, 45.956222],
            [-84.320017, 45.956223],
            [-84.320494, 45.956224],
            [-84.324647, 45.956234],
            [-84.325115, 45.956235],
            [-84.325787, 45.956237],
            [-84.326413, 45.956238],
            [-84.327177, 45.95624],
            [-84.330021, 45.956247],
            [-84.330346, 45.956043],
            [-84.331572, 45.955274],
            [-84.345451, 45.946569],
            [-84.353259, 45.941671],
            [-84.353272, 45.941663],
            [-84.353714, 45.941478],
            [-84.357525, 45.939881],
            [-84.376429, 45.931962],
            [-84.376431, 45.931963],
            [-84.387383, 45.937497],
            [-84.391464, 45.93956],
            [-84.398007, 45.942866],
            [-84.398323, 45.943026],
            [-84.39854, 45.943136],
            [-84.399134, 45.943436],
            [-84.39946, 45.9436],
            [-84.399582, 45.943662],
            [-84.411212, 45.949539],
            [-84.428239, 45.958144],
            [-84.428689, 45.958371],
            [-84.436454, 45.971722],
            [-84.436574, 45.97193],
            [-84.437633, 45.97375],
            [-84.443086, 45.977825],
            [-84.443138, 45.977863],
            [-84.44314, 45.977862],
            [-84.443336, 45.977775],
            [-84.459956, 45.970343],
            [-84.463128, 45.968925],
            [-84.473694, 45.974321],
            [-84.478576, 45.976814],
            [-84.480436, 45.977764],
            [-84.4808, 45.978385],
            [-84.482563, 45.981391],
            [-84.483062, 45.982242],
            [-84.482506, 45.985109],
            [-84.482442, 45.985441],
            [-84.482773, 45.986035],
            [-84.484009, 45.98825],
            [-84.485625, 45.988453],
            [-84.488536, 45.98882],
            [-84.507201, 45.991169],
            [-84.514123, 45.987242],
            [-84.514071, 45.971292],
            [-84.525052, 45.968578],
            [-84.532392, 45.969448],
            [-84.534422, 45.972762],
            [-84.534648, 45.978132],
            [-84.530444, 45.991385],
            [-84.533426, 46.00572],
            [-84.540995, 46.019501],
            [-84.544405, 46.02286],
            [-84.563891, 46.032459],
            [-84.581081, 46.031041],
            [-84.586592, 46.026584],
            [-84.609063, 46.026418],
            [-84.647609, 46.049704],
            [-84.656567, 46.052654],
            [-84.66671, 46.050486],
            [-84.675835, 46.046009],
            [-84.687322, 46.03488],
            [-84.692735, 46.027019],
            [-84.6927, 46.016963],
            [-84.686269, 45.979144],
            [-84.684368, 45.977499],
            [-84.685254, 45.973454],
            [-84.687712, 45.97126],
            [-84.703948, 45.970901],
            [-84.723039, 45.967279],
            [-84.730179, 45.961198],
            [-84.738849, 45.945792],
            [-84.73937, 45.941816],
            [-84.733041, 45.932837],
            [-84.718955, 45.927449],
            [-84.713614, 45.920366],
            [-84.713251, 45.916047],
            [-84.734002, 45.907026],
            [-84.730252, 45.897267],
            [-84.729482, 45.895263],
            [-84.721277, 45.873911],
            [-84.721276, 45.873908],
            [-84.715501, 45.865962],
            [-84.715481, 45.865934],
            [-84.715365, 45.86583],
            [-84.709727, 45.860766],
            [-84.702122, 45.853935],
            [-84.701594, 45.853461],
            [-84.701183, 45.853092],
            [-84.702256, 45.850557],
            [-84.702295, 45.850464],
            [-84.704647, 45.849425],
            [-84.705036, 45.849253],
            [-84.705923, 45.848861],
            [-84.705978, 45.848837],
            [-84.706383, 45.848658],
            [-84.707676, 45.848609],
            [-84.709261, 45.848548],
            [-84.720609, 45.848116],
            [-84.720836, 45.848107],
            [-84.721011, 45.847972],
            [-84.721468, 45.84762],
            [-84.722764, 45.846621],
            [-84.723005, 45.845845],
            [-84.723552, 45.84408],
            [-84.725734, 45.837045],
            [-84.726921, 45.836964],
            [-84.746985, 45.835597],
            [-84.747033, 45.835621],
            [-84.751507, 45.837878],
            [-84.754672, 45.839475],
            [-84.758333, 45.841322],
            [-84.773761, 45.849105],
            [-84.7754, 45.849932],
            [-84.792763, 45.858691],
            [-84.820557, 45.868293],
            [-84.828996, 45.871209],
            [-84.831396, 45.872038],
            [-84.831947, 45.872776],
            [-84.834213, 45.87581],
            [-84.834236, 45.875841],
            [-84.835147, 45.87706],
            [-84.838282, 45.881258],
            [-84.838427, 45.881452],
            [-84.838472, 45.881512],
            [-84.838228, 45.883685],
            [-84.837841, 45.887123],
            [-84.837717, 45.888226],
            [-84.837624, 45.889054],
            [-84.837635, 45.889075],
            [-84.839639, 45.893042],
            [-84.840666, 45.895074],
            [-84.842147, 45.898005],
            [-84.842243, 45.898194],
            [-84.843087, 45.898346],
            [-84.844615, 45.89862],
            [-84.846035, 45.898875],
            [-84.850592, 45.899694],
            [-84.852916, 45.900111],
            [-84.853303, 45.900296],
            [-84.855982, 45.901574],
            [-84.859025, 45.903026],
            [-84.860544, 45.903751],
            [-84.861494, 45.904204],
            [-84.867851, 45.907237],
            [-84.870355, 45.908432],
            [-84.873254, 45.909815],
            [-84.873273, 45.909832],
            [-84.877253, 45.91348],
            [-84.878969, 45.915053],
            [-84.879507, 45.915546],
            [-84.879835, 45.915847],
            [-84.902572, 45.923557],
            [-84.902913, 45.923673],
            [-84.917288, 45.930576],
            [-84.917484, 45.93067],
            [-84.936565, 45.955217],
            [-84.936953, 45.955716],
            [-84.937134, 45.955949],
            [-84.971232, 45.984208],
            [-84.973556, 45.986134],
            [-84.974668, 45.986874],
            [-84.974714, 45.986905],
            [-84.992511, 45.998751],
            [-85.003597, 46.00613],
            [-85.010208, 46.009084],
            [-85.010941, 46.009412],
            [-85.01399, 46.010774],
            [-85.020951, 46.012845],
            [-85.047028, 46.020603],
            [-85.047974, 46.020885],
            [-85.048972, 46.021182],
            [-85.054297, 46.022766],
            [-85.055581, 46.023148],
            [-85.081934, 46.027295],
            [-85.088345, 46.028304],
            [-85.088818, 46.028378],
            [-85.102862, 46.032477],
            [-85.102899, 46.032488],
            [-85.105148, 46.033598],
            [-85.106684, 46.034356],
            [-85.113601, 46.037769],
            [-85.124796, 46.043294],
            [-85.125243, 46.043515],
            [-85.127046, 46.044404],
            [-85.127202, 46.044481],
            [-85.130433, 46.046076],
            [-85.133704, 46.047184],
            [-85.13765, 46.048522],
            [-85.139572, 46.049173],
            [-85.140835, 46.049601],
            [-85.142456, 46.049764],
            [-85.14516, 46.050035],
            [-85.147103, 46.05023],
            [-85.151738, 46.050696],
            [-85.151825, 46.050705],
            [-85.152027, 46.050725],
            [-85.19063, 46.047622],
            [-85.194724, 46.045992],
            [-85.195201, 46.045802],
            [-85.197523, 46.044878],
            [-85.210834, 46.053301],
            [-85.214657, 46.055719],
            [-85.217705, 46.057648],
            [-85.220289, 46.059283],
            [-85.221206, 46.059864],
            [-85.222416, 46.060629],
            [-85.222511, 46.060689],
            [-85.222959, 46.060741],
            [-85.225052, 46.060984],
            [-85.248414, 46.063694],
            [-85.255375, 46.064502],
            [-85.266385, 46.065779],
            [-85.268308, 46.066365],
            [-85.278345, 46.069426],
            [-85.287693, 46.072276],
            [-85.287936, 46.072398],
            [-85.290046, 46.073456],
            [-85.295321, 46.076103],
            [-85.304238, 46.080576],
            [-85.310613, 46.083773],
            [-85.316264, 46.086608],
            [-85.335466, 46.092459],
            [-85.335911, 46.092595],
            [-85.33628, 46.092586],
            [-85.3561, 46.092089],
            [-85.356214, 46.092086],
            [-85.356339, 46.092022],
            [-85.366622, 46.086778],
            [-85.367158, 46.086606],
            [-85.381263, 46.082086],
            [-85.381394, 46.082044],
            [-85.382312, 46.083035],
            [-85.382781, 46.08354],
            [-85.382927, 46.083698],
            [-85.390047, 46.091381],
            [-85.393832, 46.095465],
            [-85.404194, 46.098859],
            [-85.409463, 46.100585],
            [-85.412064, 46.101437],
            [-85.426916, 46.101964],
            [-85.42921, 46.101021],
            [-85.429533, 46.100888],
            [-85.432064, 46.099848],
            [-85.437352, 46.097675],
            [-85.437768, 46.097504],
            [-85.44157, 46.095942],
            [-85.441879, 46.095815],
            [-85.441932, 46.095793],
            [-85.441934, 46.095783],
            [-85.442112, 46.094867],
            [-85.442293, 46.093941],
            [-85.442255, 46.093916],
            [-85.440908, 46.093053],
            [-85.440191, 46.092593],
            [-85.445835, 46.086426],
            [-85.445976, 46.086272],
            [-85.44699, 46.085164],
            [-85.45315, 46.087219],
            [-85.466828, 46.091783],
            [-85.468899, 46.092474],
            [-85.476097, 46.094876],
            [-85.480315, 46.096283],
            [-85.480603, 46.096379],
            [-85.48072, 46.096382],
            [-85.486316, 46.096543],
            [-85.499422, 46.09692],
            [-85.5001, 46.09694],
            [-85.50024, 46.096915],
            [-85.512696, 46.094727],
            [-85.521424, 46.091314],
            [-85.52157, 46.091257],
            [-85.521636, 46.091217],
            [-85.528403, 46.087121],
            [-85.528947, 46.086791],
            [-85.529058, 46.086724],
            [-85.533341, 46.084131],
            [-85.539479, 46.080416],
            [-85.540858, 46.079581],
            [-85.542974, 46.077926],
            [-85.54595, 46.075598],
            [-85.561097, 46.063751],
            [-85.563567, 46.06182],
            [-85.603785, 46.030363],
            [-85.615661, 46.01168],
            [-85.617577, 46.008665],
            [-85.617709, 46.008458],
            [-85.625118, 46.002515],
            [-85.628293, 45.999968],
            [-85.628998, 45.999403],
            [-85.62933, 45.999136],
            [-85.646279, 45.985541],
            [-85.646359, 45.985477],
            [-85.64743, 45.984619],
            [-85.647554, 45.984519],
            [-85.648581, 45.983695],
            [-85.648942, 45.983103],
            [-85.650434, 45.980656],
            [-85.650942, 45.979824],
            [-85.654686, 45.973686],
            [-85.654738, 45.973648],
            [-85.663966, 45.967013],
            [-85.664609, 45.96688],
            [-85.688149, 45.962025],
            [-85.697203, 45.960158],
            [-85.697609, 45.960237],
            [-85.701627, 45.961017],
            [-85.704237, 45.961524],
            [-85.704951, 45.961662],
            [-85.706548, 45.961973],
            [-85.716228, 45.963852],
            [-85.724246, 45.965409],
            [-85.725153, 45.965398],
            [-85.731776, 45.965317],
            [-85.74353, 45.965174],
            [-85.743618, 45.965173],
            [-85.760165, 45.968914],
            [-85.770938, 45.971349],
            [-85.774055, 45.972337],
            [-85.790279, 45.97748],
            [-85.790639, 45.977594],
            [-85.791841, 45.977745],
            [-85.8092, 45.979931],
            [-85.810442, 45.980087],
            [-85.811054, 45.980032],
            [-85.817066, 45.979491],
            [-85.817558, 45.979447],
            [-85.818006, 45.979276],
            [-85.820721, 45.978239],
            [-85.825819, 45.976292],
            [-85.826034, 45.976021],
            [-85.826278, 45.975714],
            [-85.82978, 45.9713],
            [-85.832261, 45.968172],
            [-85.832603, 45.967742],
            [-85.834504, 45.967258],
            [-85.835217, 45.967077],
            [-85.835456, 45.967016],
            [-85.838133, 45.966334],
            [-85.838366, 45.966275],
            [-85.839025, 45.966107],
            [-85.842404, 45.965247],
            [-85.843016, 45.965342],
            [-85.86019, 45.968016],
            [-85.861157, 45.968167],
            [-85.865402, 45.968257],
            [-85.865025, 46.244631],
            [-85.237839, 46.245421],
            [-85.113126, 46.245667],
            [-85.112438, 46.158191],
            [-84.363503, 46.157984],
            [-84.36309, 46.071581],
            [-84.238836, 46.071457],
            [-84.239313, 45.98488],
            [-84.114284, 45.978322]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26103",
      state: "26",
      county: "103",
      name: "Marquette County",
      censusArea: 1808.401,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.044551, 46.912473],
          [-88.032911, 46.909038],
          [-88.032408, 46.90889],
          [-88.030053, 46.90873],
          [-88.029373, 46.908684],
          [-88.004298, 46.906982],
          [-88.003693, 46.906948],
          [-87.98629, 46.905967],
          [-87.986113, 46.905957],
          [-87.98334, 46.906242],
          [-87.982258, 46.906353],
          [-87.97549, 46.907048],
          [-87.958255, 46.908819],
          [-87.957115, 46.908936],
          [-87.956, 46.909051],
          [-87.955745, 46.909054],
          [-87.954687, 46.909066],
          [-87.915943, 46.909508],
          [-87.914489, 46.909525],
          [-87.911474, 46.909559],
          [-87.910624, 46.909569],
          [-87.908791, 46.90959],
          [-87.900695, 46.909682],
          [-87.900339, 46.909686],
          [-87.90007, 46.909508],
          [-87.88792, 46.901451],
          [-87.875051, 46.892918],
          [-87.874538, 46.892578],
          [-87.873893, 46.892381],
          [-87.847037, 46.884163],
          [-87.846195, 46.883905],
          [-87.844637, 46.884049],
          [-87.841228, 46.884363],
          [-87.838889, 46.885252],
          [-87.830204, 46.888556],
          [-87.827162, 46.889713],
          [-87.816794, 46.891154],
          [-87.81412, 46.888808],
          [-87.813228, 46.888025],
          [-87.813226, 46.888023],
          [-87.793194, 46.880822],
          [-87.788186, 46.880373],
          [-87.783216, 46.879927],
          [-87.782461, 46.879859],
          [-87.781969, 46.87958],
          [-87.777749, 46.87719],
          [-87.777527, 46.877064],
          [-87.777156, 46.876854],
          [-87.77693, 46.876726],
          [-87.776672, 46.874999],
          [-87.776567, 46.874293],
          [-87.776313, 46.872591],
          [-87.776346, 46.872562],
          [-87.778289, 46.870834],
          [-87.778752, 46.870422],
          [-87.776804, 46.866823],
          [-87.766243, 46.861446],
          [-87.765989, 46.861316],
          [-87.765835, 46.861303],
          [-87.755937, 46.860459],
          [-87.755868, 46.860453],
          [-87.755748, 46.860518],
          [-87.754448, 46.861219],
          [-87.748498, 46.864428],
          [-87.746646, 46.865427],
          [-87.745665, 46.865396],
          [-87.744893, 46.865371],
          [-87.744403, 46.865355],
          [-87.741857, 46.865274],
          [-87.741014, 46.865247],
          [-87.740917, 46.865007],
          [-87.740846, 46.864834],
          [-87.73487, 46.85012],
          [-87.735039, 46.849856],
          [-87.735106, 46.849751],
          [-87.736732, 46.847216],
          [-87.736539, 46.846393],
          [-87.736469, 46.846096],
          [-87.734325, 46.836955],
          [-87.731522, 46.831196],
          [-87.727358, 46.827656],
          [-87.72588, 46.827426],
          [-87.725649, 46.82739],
          [-87.713737, 46.825534],
          [-87.69459, 46.827182],
          [-87.685787, 46.832477],
          [-87.685698, 46.83253],
          [-87.685735, 46.832639],
          [-87.68793, 46.839159],
          [-87.687875, 46.839343],
          [-87.68728, 46.841351],
          [-87.687164, 46.841742],
          [-87.68672, 46.841794],
          [-87.681561, 46.842392],
          [-87.680668, 46.842496],
          [-87.679573, 46.841507],
          [-87.675046, 46.83742],
          [-87.674541, 46.836964],
          [-87.673469, 46.829598],
          [-87.673433, 46.82935],
          [-87.673177, 46.827593],
          [-87.673274, 46.8273],
          [-87.673973, 46.825179],
          [-87.674345, 46.82405],
          [-87.672015, 46.820415],
          [-87.662261, 46.815157],
          [-87.65151, 46.812411],
          [-87.646539, 46.813094],
          [-87.642789, 46.813609],
          [-87.641887, 46.813733],
          [-87.640839, 46.813534],
          [-87.6333, 46.812107],
          [-87.628786, 46.806096],
          [-87.628284, 46.805428],
          [-87.628081, 46.805157],
          [-87.627356, 46.804553],
          [-87.627124, 46.80436],
          [-87.619747, 46.79821],
          [-87.617852, 46.796631],
          [-87.6168, 46.795753],
          [-87.613654, 46.793131],
          [-87.613586, 46.793074],
          [-87.610582, 46.790571],
          [-87.609008, 46.789258],
          [-87.607988, 46.788408],
          [-87.595307, 46.78295],
          [-87.595276, 46.782745],
          [-87.594384, 46.776863],
          [-87.593148, 46.768713],
          [-87.592227, 46.762635],
          [-87.59222, 46.762594],
          [-87.591973, 46.760965],
          [-87.591907, 46.760525],
          [-87.591455, 46.757544],
          [-87.591362, 46.756932],
          [-87.591082, 46.755087],
          [-87.590865, 46.753653],
          [-87.590767, 46.753009],
          [-87.590502, 46.752267],
          [-87.589671, 46.749937],
          [-87.587755, 46.744568],
          [-87.587626, 46.744206],
          [-87.584568, 46.735637],
          [-87.58357, 46.732839],
          [-87.582745, 46.730527],
          [-87.581674, 46.729399],
          [-87.573203, 46.720471],
          [-87.567343, 46.716714],
          [-87.557436, 46.710364],
          [-87.553048, 46.707551],
          [-87.551989, 46.706872],
          [-87.549097, 46.705019],
          [-87.54392, 46.7017],
          [-87.534504, 46.695664],
          [-87.533529, 46.69504],
          [-87.532638, 46.694469],
          [-87.523308, 46.688488],
          [-87.523361, 46.687977],
          [-87.523744, 46.6843],
          [-87.524419, 46.67783],
          [-87.524444, 46.677586],
          [-87.519176, 46.670186],
          [-87.506657, 46.652599],
          [-87.503238, 46.647796],
          [-87.503025, 46.647497],
          [-87.501511, 46.646762],
          [-87.493405, 46.642826],
          [-87.49286, 46.642561],
          [-87.492409, 46.642435],
          [-87.469023, 46.635918],
          [-87.467965, 46.635623],
          [-87.466537, 46.631555],
          [-87.467563, 46.626228],
          [-87.464108, 46.614811],
          [-87.451368, 46.605923],
          [-87.442612, 46.602776],
          [-87.411167, 46.601669],
          [-87.403275, 46.595215],
          [-87.383961, 46.59307],
          [-87.381649, 46.580059],
          [-87.392974, 46.572523],
          [-87.392828, 46.570852],
          [-87.382206, 46.553681],
          [-87.375613, 46.54714],
          [-87.3903, 46.542577],
          [-87.393985, 46.533183],
          [-87.38929, 46.524472],
          [-87.381349, 46.517292],
          [-87.366767, 46.507303],
          [-87.352448, 46.501324],
          [-87.351071, 46.500749],
          [-87.312109, 46.49231],
          [-87.310755, 46.492017],
          [-87.309402, 46.491919],
          [-87.259116, 46.488283],
          [-87.258732, 46.488255],
          [-87.258145, 46.488282],
          [-87.205147, 46.490702],
          [-87.202404, 46.490827],
          [-87.200147, 46.491382],
          [-87.175105, 46.497538],
          [-87.175065, 46.497548],
          [-87.145946, 46.495387],
          [-87.12744, 46.494014],
          [-87.116618, 46.495163],
          [-87.11608, 46.158945],
          [-87.241555, 46.158901],
          [-87.242885, 45.985087],
          [-87.367843, 45.985313],
          [-87.617039, 45.986006],
          [-87.615914, 46.246794],
          [-88.117407, 46.246618],
          [-88.116571, 46.419951],
          [-88.115843, 46.678381],
          [-87.991546, 46.679113],
          [-88.044709, 46.765353],
          [-88.044551, 46.912473]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26107",
      state: "26",
      county: "107",
      name: "Mecosta County",
      censusArea: 555.071,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.562392, 43.468247],
          [-85.562439, 43.815634],
          [-85.088811, 43.813676],
          [-85.084996, 43.46619],
          [-85.562392, 43.468247]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26111",
      state: "26",
      county: "111",
      name: "Midland County",
      censusArea: 516.253,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.369876, 43.466044],
          [-84.60754, 43.466006],
          [-84.606035, 43.815218],
          [-84.167318, 43.825902],
          [-84.168127, 43.568899],
          [-84.170576, 43.481969],
          [-84.369876, 43.466044]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26117",
      state: "26",
      county: "117",
      name: "Montcalm County",
      censusArea: 705.399,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.312513, 43.118793],
          [-85.312376, 43.294205],
          [-85.562538, 43.294271],
          [-85.562392, 43.468247],
          [-85.084996, 43.46619],
          [-84.845962, 43.466158],
          [-84.836889, 43.118851],
          [-85.312513, 43.118793]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26123",
      state: "26",
      county: "123",
      name: "Newaygo County",
      censusArea: 813.204,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.562538, 43.294271],
          [-85.571636, 43.294252],
          [-85.790448, 43.293003],
          [-86.038953, 43.293519],
          [-86.039491, 43.467447],
          [-86.037884, 43.815611],
          [-85.562439, 43.815634],
          [-85.562392, 43.468247],
          [-85.562538, 43.294271]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26133",
      state: "26",
      county: "133",
      name: "Osceola County",
      censusArea: 566.391,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.08704, 44.164239],
          [-85.088811, 43.813676],
          [-85.562439, 43.815634],
          [-85.564554, 44.164893],
          [-85.334777, 44.165118],
          [-85.08704, 44.164239]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26143",
      state: "26",
      county: "143",
      name: "Roscommon County",
      censusArea: 519.636,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.608104, 44.160482],
          [-84.851705, 44.161375],
          [-84.855795, 44.421984],
          [-84.850929, 44.511069],
          [-84.370643, 44.507223],
          [-84.366324, 44.175508],
          [-84.368039, 44.160538],
          [-84.608104, 44.160482]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26155",
      state: "26",
      county: "155",
      name: "Shiawassee County",
      censusArea: 530.668,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.365712, 42.95512],
          [-84.367795, 43.117938],
          [-84.367863, 43.124456],
          [-84.367893, 43.124671],
          [-84.367891, 43.128452],
          [-83.929079, 43.132782],
          [-83.927555, 42.88075],
          [-83.922516, 42.780821],
          [-84.158189, 42.776639],
          [-84.363659, 42.775778],
          [-84.365712, 42.95512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26161",
      state: "26",
      county: "161",
      name: "Washtenaw County",
      censusArea: 705.965,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.542102, 42.174344],
          [-83.539396, 42.085598],
          [-83.773922, 42.08243],
          [-84.131963, 42.071577],
          [-84.131136, 42.424567],
          [-83.94465, 42.424149],
          [-83.664808, 42.431179],
          [-83.551907, 42.435166],
          [-83.547548, 42.349217],
          [-83.542102, 42.174344]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26165",
      state: "26",
      county: "165",
      name: "Wexford County",
      censusArea: 565.002,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.334777, 44.165118],
          [-85.564554, 44.164893],
          [-85.819811, 44.164301],
          [-85.821186, 44.164197],
          [-85.817802, 44.512969],
          [-85.334, 44.512303],
          [-85.334777, 44.165118]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27009",
      state: "27",
      county: "009",
      name: "Benton County",
      censusArea: 408.301,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.759435, 45.559646],
          [-94.149467, 45.559066],
          [-94.203666, 45.619556],
          [-94.215712, 45.72656],
          [-94.271776, 45.775208],
          [-94.353165, 45.822131],
          [-93.763948, 45.822143],
          [-93.763316, 45.815872],
          [-93.759435, 45.559646]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27019",
      state: "27",
      county: "019",
      name: "Carver County",
      censusArea: 354.325,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.767367, 44.977964],
          [-93.767041, 44.890862],
          [-93.520706, 44.891502],
          [-93.520632, 44.864108],
          [-93.520725, 44.861999],
          [-93.520632, 44.858473],
          [-93.520431, 44.804263],
          [-93.621967, 44.760281],
          [-93.618141, 44.707096],
          [-93.768039, 44.640303],
          [-93.888636, 44.717044],
          [-94.010494, 44.717464],
          [-94.012236, 44.978712],
          [-93.767367, 44.977964]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27029",
      state: "27",
      county: "029",
      name: "Clearwater County",
      censusArea: 998.94,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.582155, 47.933966],
          [-95.582887, 48.020557],
          [-95.229133, 48.019958],
          [-95.277834, 47.925346],
          [-95.193705, 47.872544],
          [-95.183216, 47.412773],
          [-95.169172, 47.152515],
          [-95.551186, 47.151453],
          [-95.551458, 47.50002],
          [-95.554248, 47.499893],
          [-95.578839, 47.673677],
          [-95.582155, 47.933966]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27039",
      state: "27",
      county: "039",
      name: "Dodge County",
      censusArea: 439.276,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.678717, 44.195516],
          [-92.678412, 43.848487],
          [-92.689391, 43.84857],
          [-93.045966, 43.848467],
          [-93.04557, 43.850021],
          [-93.04591, 44.196716],
          [-93.041159, 44.196726],
          [-92.678717, 44.195516]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27051",
      state: "27",
      county: "051",
      name: "Grant County",
      censusArea: 548.16,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.26614, 46.02161],
          [-96.26603, 46.05742],
          [-96.266048, 46.057771],
          [-96.26608, 46.05796],
          [-96.265366, 46.10861],
          [-95.76975, 46.10745],
          [-95.758508, 45.759932],
          [-96.254022, 45.75982],
          [-96.26614, 46.02161]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27057",
      state: "27",
      county: "057",
      name: "Hubbard County",
      censusArea: 925.675,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.183216, 47.412773],
          [-94.799934, 47.409848],
          [-94.78893, 47.40979],
          [-94.760529, 47.410452],
          [-94.718396, 47.409542],
          [-94.670214, 47.410151],
          [-94.659942, 46.803716],
          [-94.787126, 46.803943],
          [-95.16301, 46.80472],
          [-95.169172, 47.152515],
          [-95.183216, 47.412773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27067",
      state: "27",
      county: "067",
      name: "Kandiyohi County",
      censusArea: 796.785,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.13173, 45.412402],
          [-94.762934, 45.412861],
          [-94.76308, 45.3261],
          [-94.758189, 44.892097],
          [-95.24852, 44.89131],
          [-95.24649, 45.152384],
          [-95.255206, 45.412525],
          [-95.13173, 45.412402]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29097",
      state: "29",
      county: "097",
      name: "Jasper County",
      censusArea: 638.487,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.082083, 37.349291],
          [-94.083833, 37.290851],
          [-94.052313, 37.290078],
          [-94.059211, 37.048127],
          [-94.617875, 37.056797],
          [-94.617875, 37.056798],
          [-94.617982, 37.075077],
          [-94.61812, 37.085934],
          [-94.618082, 37.086432],
          [-94.61809, 37.093494],
          [-94.618088, 37.093671],
          [-94.618059, 37.096676],
          [-94.618151, 37.103968],
          [-94.618212, 37.113169],
          [-94.618075, 37.129755],
          [-94.618072, 37.132345],
          [-94.618351, 37.160211],
          [-94.618473, 37.174782],
          [-94.618505, 37.181184],
          [-94.618319, 37.188774],
          [-94.618305, 37.207337],
          [-94.618219, 37.207772],
          [-94.61815, 37.228121],
          [-94.618123, 37.229334],
          [-94.618158, 37.237597],
          [-94.618075, 37.240436],
          [-94.617648, 37.323589],
          [-94.617695, 37.336842],
          [-94.617636, 37.338415],
          [-94.617636, 37.338417],
          [-94.617538, 37.364167],
          [-94.082083, 37.349291]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29103",
      state: "29",
      county: "103",
      name: "Knox County",
      censusArea: 504.011,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.345064, 40.03721],
          [-92.347801, 40.302756],
          [-91.949747, 40.300832],
          [-91.950812, 40.25721],
          [-91.952726, 39.949426],
          [-92.177328, 39.954494],
          [-92.287882, 39.95249],
          [-92.344708, 39.953297],
          [-92.345064, 40.03721]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29109",
      state: "29",
      county: "109",
      name: "Lawrence County",
      censusArea: 611.737,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.059211, 37.048127],
          [-94.052313, 37.290078],
          [-93.625844, 37.282011],
          [-93.605095, 37.281482],
          [-93.608899, 37.098153],
          [-93.610126, 36.99581],
          [-93.611003, 36.922831],
          [-94.06274, 36.931775],
          [-94.059211, 37.048127]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29117",
      state: "29",
      county: "117",
      name: "Livingston County",
      censusArea: 532.329,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.761973, 39.959552],
          [-93.361957, 39.967606],
          [-93.364808, 39.703803],
          [-93.266968, 39.703562],
          [-93.278098, 39.615632],
          [-93.758357, 39.611407],
          [-93.758992, 39.784674],
          [-93.761973, 39.959552]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29131",
      state: "29",
      county: "131",
      name: "Miller County",
      censusArea: 592.595,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.604976, 38.156815],
          [-92.695886, 38.222031],
          [-92.689834, 38.348472],
          [-92.633756, 38.348082],
          [-92.625189, 38.430778],
          [-92.623858, 38.430639],
          [-92.495185, 38.427829],
          [-92.403484, 38.425331],
          [-92.408407, 38.341344],
          [-92.194476, 38.335536],
          [-92.195675, 38.292237],
          [-92.18537, 38.016338],
          [-92.406275, 38.021179],
          [-92.515979, 38.022336],
          [-92.604976, 38.156815]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29137",
      state: "29",
      county: "137",
      name: "Monroe County",
      censusArea: 647.653,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.715247, 39.657644],
          [-91.717968, 39.339122],
          [-92.310028, 39.347795],
          [-92.300871, 39.605348],
          [-92.299316, 39.605344],
          [-92.186691, 39.604082],
          [-92.184986, 39.662932],
          [-91.846743, 39.658513],
          [-91.715247, 39.657644]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29151",
      state: "29",
      county: "151",
      name: "Osage County",
      censusArea: 604.346,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.644729, 38.28851],
          [-92.195675, 38.292237],
          [-92.194476, 38.335536],
          [-92.114977, 38.401937],
          [-92.168883, 38.466812],
          [-92.043489, 38.466479],
          [-92.025857, 38.552017],
          [-91.944238, 38.596925],
          [-91.869489, 38.66394],
          [-91.647171, 38.703396],
          [-91.645546, 38.703678],
          [-91.641376, 38.703939],
          [-91.640372, 38.703792],
          [-91.644729, 38.28851]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29159",
      state: "29",
      county: "159",
      name: "Pettis County",
      censusArea: 682.222,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.497278, 38.92843],
          [-93.496377, 38.942837],
          [-93.069819, 38.930042],
          [-93.050453, 38.928244],
          [-93.059974, 38.693077],
          [-93.067294, 38.529995],
          [-93.290454, 38.535388],
          [-93.291851, 38.506321],
          [-93.512743, 38.512476],
          [-93.511303, 38.548565],
          [-93.511364, 38.548889],
          [-93.51103, 38.55621],
          [-93.497278, 38.92843]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29169",
      state: "29",
      county: "169",
      name: "Pulaski County",
      censusArea: 547.099,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.406872, 37.977802],
          [-92.406275, 38.021179],
          [-92.18537, 38.016338],
          [-92.18537, 38.015138],
          [-92.021165, 38.010639],
          [-92.029258, 37.602542],
          [-92.249463, 37.604543],
          [-92.248865, 37.648442],
          [-92.413669, 37.713941],
          [-92.409231, 37.85918],
          [-92.406872, 37.977802]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29179",
      state: "29",
      county: "179",
      name: "Reynolds County",
      censusArea: 808.478,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.779553, 37.050324],
          [-90.964466, 37.053584],
          [-91.017766, 37.095677],
          [-91.034346, 37.169343],
          [-91.159323, 37.254936],
          [-91.175405, 37.414805],
          [-91.211863, 37.415277],
          [-91.210984, 37.501911],
          [-91.314236, 37.505132],
          [-91.312458, 37.592824],
          [-91.155073, 37.588092],
          [-90.768695, 37.603011],
          [-90.772949, 37.373659],
          [-90.735453, 37.27104],
          [-90.737146, 37.166413],
          [-90.779553, 37.050324]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29195",
      state: "29",
      county: "195",
      name: "Saline County",
      censusArea: 755.504,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.849224, 39.226218],
          [-92.94165, 39.125892],
          [-92.934569, 39.064547],
          [-93.048471, 38.972418],
          [-93.050453, 38.928244],
          [-93.069819, 38.930042],
          [-93.496377, 38.942837],
          [-93.477233, 39.292796],
          [-93.333337, 39.250012],
          [-93.332252, 39.309606],
          [-93.230079, 39.327877],
          [-93.200708, 39.401787],
          [-93.104485, 39.383656],
          [-93.072915, 39.33552],
          [-92.959801, 39.312526],
          [-92.906142, 39.222339],
          [-92.849224, 39.226218]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29205",
      state: "29",
      county: "205",
      name: "Shelby County",
      censusArea: 500.864,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.299316, 39.605344],
          [-92.287882, 39.95249],
          [-92.177328, 39.954494],
          [-91.952726, 39.949426],
          [-91.840353, 39.948235],
          [-91.846743, 39.658513],
          [-92.184986, 39.662932],
          [-92.186691, 39.604082],
          [-92.299316, 39.605344]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29211",
      state: "29",
      county: "211",
      name: "Sullivan County",
      censusArea: 647.981,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.367214, 40.266314],
          [-93.366935, 40.382999],
          [-92.855313, 40.385894],
          [-92.855629, 40.342736],
          [-92.856191, 40.037253],
          [-93.361951, 40.033098],
          [-93.366556, 40.033099],
          [-93.367214, 40.266314]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29221",
      state: "29",
      county: "221",
      name: "Washington County",
      censusArea: 759.913,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.63998, 38.076548],
          [-90.645135, 37.734813],
          [-91.100017, 37.740012],
          [-91.095765, 38.204083],
          [-90.780185, 38.204112],
          [-90.63998, 38.076548]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29229",
      state: "29",
      county: "229",
      name: "Wright County",
      censusArea: 681.771,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.252261, 37.472944],
          [-92.251359, 37.059653],
          [-92.685867, 37.067051],
          [-92.686671, 37.481545],
          [-92.252261, 37.472944]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30013",
      state: "30",
      county: "013",
      name: "Cascade County",
      censusArea: 2698.158,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.409117, 47.698277],
          [-110.976382, 47.698605],
          [-111.049832, 47.598325],
          [-110.863942, 47.52506],
          [-110.768771, 47.524837],
          [-110.769218, 47.445996],
          [-110.639934, 47.445332],
          [-110.639564, 47.416259],
          [-110.63928, 47.358998],
          [-110.767125, 47.359128],
          [-110.772254, 47.095871],
          [-110.645353, 47.095735],
          [-110.652877, 46.823626],
          [-110.751665, 46.844681],
          [-110.761016, 46.900286],
          [-110.85718, 46.922879],
          [-110.913285, 46.980853],
          [-111.104738, 47.029105],
          [-111.080855, 47.088138],
          [-111.158602, 47.088184],
          [-111.158518, 47.011501],
          [-111.539141, 47.000386],
          [-111.539341, 46.913427],
          [-111.658131, 46.913364],
          [-111.790092, 46.91393],
          [-111.789326, 47.128993],
          [-112.044923, 47.19271],
          [-112.047308, 47.516028],
          [-111.92253, 47.505078],
          [-111.922936, 47.611231],
          [-111.6668, 47.611241],
          [-111.667035, 47.697885],
          [-111.409117, 47.698277]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30037",
      state: "30",
      county: "037",
      name: "Golden Valley County",
      censusArea: 1175.342,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.78014, 46.132402],
          [-108.924129, 46.132211],
          [-109.415506, 46.132261],
          [-109.417297, 46.044485],
          [-109.604968, 46.045377],
          [-109.654304, 46.219402],
          [-109.402906, 46.219706],
          [-109.389163, 46.693661],
          [-109.388963, 46.751009],
          [-109.009802, 46.750432],
          [-108.988585, 46.578572],
          [-108.904779, 46.577394],
          [-108.861315, 46.364825],
          [-108.779115, 46.27672],
          [-108.78014, 46.132402]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30043",
      state: "30",
      county: "043",
      name: "Jefferson County",
      censusArea: 1656.257,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.659995, 45.833512],
          [-111.805134, 45.798018],
          [-111.930144, 45.857148],
          [-112.106378, 45.841188],
          [-112.190269, 45.748503],
          [-112.399399, 45.831089],
          [-112.504818, 46.183767],
          [-112.57685, 46.178859],
          [-112.558457, 46.265925],
          [-112.31061, 46.421006],
          [-112.17579, 46.456606],
          [-112.188399, 46.499211],
          [-112.029312, 46.569096],
          [-111.787875, 46.568998],
          [-111.784333, 46.050004],
          [-111.660441, 46.049449],
          [-111.659995, 45.833512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31121",
      state: "31",
      county: "121",
      name: "Merrick County",
      censusArea: 484.881,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.703765, 41.394876],
          [-97.598461, 41.395068],
          [-97.598253, 41.333119],
          [-97.828243, 41.17375],
          [-97.892531, 41.111496],
          [-98.019385, 41.066392],
          [-98.282569, 40.872296],
          [-98.282359, 41.046355],
          [-98.287168, 41.046363],
          [-98.286584, 41.393898],
          [-98.275408, 41.264341],
          [-98.101546, 41.264041],
          [-98.053137, 41.293123],
          [-97.705497, 41.309134],
          [-97.703765, 41.394876]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31125",
      state: "31",
      county: "125",
      name: "Nance County",
      censusArea: 441.633,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.703765, 41.394876],
          [-97.705497, 41.309134],
          [-98.053137, 41.293123],
          [-98.101546, 41.264041],
          [-98.275408, 41.264341],
          [-98.286584, 41.393898],
          [-98.291419, 41.393894],
          [-98.291344, 41.480727],
          [-98.099523, 41.480777],
          [-98.041328, 41.510534],
          [-97.829846, 41.526174],
          [-97.703627, 41.526796],
          [-97.703765, 41.394876]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31135",
      state: "31",
      county: "135",
      name: "Perkins County",
      censusArea: 883.335,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.051761, 41.00389],
          [-101.249975, 41.00395],
          [-101.247963, 40.697802],
          [-101.345365, 40.698093],
          [-102.051398, 40.697542],
          [-102.051292, 40.749586],
          [-102.051292, 40.749591],
          [-102.051614, 41.002377],
          [-102.051718, 41.002377],
          [-102.051761, 41.00389]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31137",
      state: "31",
      county: "137",
      name: "Phelps County",
      censusArea: 539.79,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.417316, 40.670825],
          [-99.179248, 40.660541],
          [-99.179403, 40.350681],
          [-99.630459, 40.35112],
          [-99.630619, 40.351206],
          [-99.642209, 40.3512],
          [-99.64328, 40.682639],
          [-99.643462, 40.684911],
          [-99.417316, 40.670825]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31147",
      state: "31",
      county: "147",
      name: "Richardson County",
      censusArea: 551.84,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.010176, 40.261482],
          [-95.552473, 40.261904],
          [-95.521925, 40.24947],
          [-95.312211, 40.009395],
          [-95.30829, 39.999998],
          [-95.339896, 39.999999],
          [-95.375257, 40],
          [-95.784575, 40.000463],
          [-95.788024, 40.000452],
          [-95.788111, 40.000452],
          [-95.882524, 40.00047],
          [-95.958139, 40.000521],
          [-96.010678, 40.000638],
          [-96.01068, 40.000638],
          [-96.010176, 40.261482]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31159",
      state: "31",
      county: "159",
      name: "Seward County",
      censusArea: 571.428,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.91094, 41.046116],
          [-96.911116, 40.697975],
          [-96.913493, 40.697948],
          [-97.368401, 40.698625],
          [-97.368118, 41.046947],
          [-96.91094, 41.046116]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31163",
      state: "31",
      county: "163",
      name: "Sherman County",
      censusArea: 565.828,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.748526, 41.04664],
          [-99.207564, 41.047003],
          [-99.203261, 41.39414],
          [-98.751558, 41.394084],
          [-98.74433, 41.394178],
          [-98.748526, 41.04664]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31171",
      state: "31",
      county: "171",
      name: "Thomas County",
      censusArea: 713.236,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.846129, 42.088166],
          [-100.267622, 42.086147],
          [-100.268125, 42.07219],
          [-100.265474, 41.739856],
          [-100.711711, 41.739761],
          [-100.842459, 41.740404],
          [-100.846129, 42.088166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31183",
      state: "31",
      county: "183",
      name: "Wheeler County",
      censusArea: 575.182,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.29576, 41.741178],
          [-98.752225, 41.740372],
          [-98.759133, 41.740382],
          [-98.761155, 42.088518],
          [-98.300592, 42.088853],
          [-98.295438, 42.088873],
          [-98.295602, 41.914954],
          [-98.29576, 41.741178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32009",
      state: "32",
      county: "009",
      name: "Esmeralda County",
      censusArea: 3581.876,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.691021, 38.473797],
          [-117.24091, 38.069902],
          [-117.241063, 38.050116],
          [-117.219264, 38.050348],
          [-117.165612, 38.001692],
          [-117.166187, 36.970862],
          [-117.500909, 37.220282],
          [-117.832726, 37.464929],
          [-118.039849, 37.615245],
          [-118.039798, 37.615273],
          [-118.428581, 37.895613],
          [-118.351484, 37.893705],
          [-117.691021, 38.473797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32021",
      state: "32",
      county: "021",
      name: "Mineral County",
      censusArea: 3752.844,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.691021, 38.473797],
          [-118.351484, 37.893705],
          [-118.428581, 37.895613],
          [-118.746598, 38.124926],
          [-119.125982, 38.39317],
          [-119.156863, 38.414743],
          [-118.906861, 38.414677],
          [-118.902124, 38.852158],
          [-119.014528, 38.8513],
          [-119.014837, 38.943323],
          [-118.929737, 39.073698],
          [-118.753835, 39.07463],
          [-117.865116, 39.073654],
          [-118.196274, 38.999926],
          [-118.196235, 38.919058],
          [-117.691021, 38.473797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32033",
      state: "32",
      county: "033",
      name: "White Pine County",
      censusArea: 8875.648,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.046555, 40.116931],
          [-114.047134, 39.906037],
          [-114.047728, 39.542742],
          [-114.049893, 38.677365],
          [-115.000846, 38.677322],
          [-115.601585, 38.999933],
          [-115.907071, 39.161848],
          [-115.907659, 39.464432],
          [-115.898339, 39.45389],
          [-115.813476, 39.593521],
          [-115.834969, 40.127842],
          [-115.263729, 40.122543],
          [-114.046555, 40.116931]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34007",
      state: "34",
      county: "007",
      name: "Camden County",
      censusArea: 221.263,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.13322, 39.922512],
          [-75.085754, 39.967572],
          [-75.072017, 39.980612],
          [-75.056039, 39.991795],
          [-75.035461, 39.995951],
          [-75.010146, 39.979768],
          [-75.019212, 39.967462],
          [-75.018723, 39.963664],
          [-75.017468, 39.963509],
          [-75.016653, 39.960623],
          [-75.017537, 39.959087],
          [-75.013114, 39.956433],
          [-75.01345, 39.954188],
          [-75.014966, 39.946546],
          [-74.994442, 39.939266],
          [-74.988925, 39.94105],
          [-74.974879, 39.938382],
          [-74.984549, 39.931238],
          [-74.983717, 39.92694],
          [-74.964146, 39.920683],
          [-74.951823, 39.90147],
          [-74.932174, 39.887532],
          [-74.926935, 39.873839],
          [-74.903267, 39.791247],
          [-74.902546, 39.790797],
          [-74.889062, 39.781804],
          [-74.876499, 39.781647],
          [-74.845183, 39.79],
          [-74.812471, 39.785269],
          [-74.791478, 39.772119],
          [-74.772894, 39.750929],
          [-74.758064, 39.743695],
          [-74.736309, 39.729909],
          [-74.801051, 39.674102],
          [-74.877189, 39.608288],
          [-74.930585, 39.69085],
          [-75.08707, 39.779576],
          [-75.153925, 39.906144],
          [-75.13322, 39.922512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34015",
      state: "34",
      county: "015",
      name: "Gloucester County",
      censusArea: 322.005,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.210972, 39.865706],
          [-75.210425, 39.865913],
          [-75.184952, 39.881615],
          [-75.153925, 39.906144],
          [-75.08707, 39.779576],
          [-74.930585, 39.69085],
          [-74.877189, 39.608288],
          [-74.911566, 39.57862],
          [-74.985223, 39.514802],
          [-75.061973, 39.568643],
          [-75.199706, 39.666912],
          [-75.326452, 39.689992],
          [-75.378232, 39.745763],
          [-75.440623, 39.780926],
          [-75.437938, 39.783413],
          [-75.416095, 39.79583],
          [-75.415041, 39.801786],
          [-75.390169, 39.816549],
          [-75.374635, 39.82577],
          [-75.3065, 39.849812],
          [-75.271159, 39.84944],
          [-75.235026, 39.856613],
          [-75.210972, 39.865706]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34031",
      state: "34",
      county: "031",
      name: "Passaic County",
      censusArea: 184.593,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.234473, 41.142883],
          [-74.213373, 41.133828],
          [-74.270521, 41.022131],
          [-74.170988, 40.976997],
          [-74.17021, 40.983865],
          [-74.141048, 40.966348],
          [-74.146429, 40.948441],
          [-74.148158, 40.942339],
          [-74.148059, 40.94159],
          [-74.130031, 40.819962],
          [-74.246691, 40.880799],
          [-74.259813, 40.883769],
          [-74.270702, 40.89692],
          [-74.300327, 41.002743],
          [-74.309838, 40.998484],
          [-74.315226, 41.001991],
          [-74.334605, 41.003719],
          [-74.502892, 41.085904],
          [-74.365897, 41.202894],
          [-74.365664, 41.2034],
          [-74.234473, 41.142883]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35001",
      state: "35",
      county: "001",
      name: "Bernalillo County",
      censusArea: 1160.828,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.414475, 34.892225],
          [-106.685889, 34.90615],
          [-106.720666, 34.870053],
          [-107.024784, 34.870254],
          [-107.066694, 34.957189],
          [-107.19617, 35.219323],
          [-106.962038, 35.218392],
          [-106.763162, 35.218141],
          [-106.729852, 35.218073],
          [-106.598652, 35.217939],
          [-106.244827, 35.215204],
          [-106.243683, 35.041219],
          [-106.243866, 35.040088],
          [-106.243866, 34.953293],
          [-106.150155, 34.953449],
          [-106.149575, 34.870142],
          [-106.410977, 34.870254],
          [-106.410974, 34.889494],
          [-106.414475, 34.892225]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35006",
      state: "35",
      county: "006",
      name: "Cibola County",
      censusArea: 4539.48,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.045851, 34.959718],
          [-108.469264, 34.958972],
          [-108.468585, 35.299239],
          [-108.468586, 35.306755],
          [-107.734212, 35.304934],
          [-107.734079, 35.348332],
          [-107.62821, 35.348333],
          [-107.628344, 35.304766],
          [-107.310041, 35.305192],
          [-107.23916, 35.305368],
          [-107.19617, 35.219323],
          [-107.066694, 34.957189],
          [-107.203039, 34.958064],
          [-107.20441, 34.578813],
          [-107.724854, 34.57806],
          [-109.046156, 34.579291],
          [-109.045851, 34.959718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30051",
      state: "30",
      county: "051",
      name: "Liberty County",
      censusArea: 1430.045,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.270702, 48.997895],
          [-110.887459, 48.998087],
          [-110.886706, 48.998124],
          [-110.743209, 48.998363],
          [-110.755649, 48.219671],
          [-110.827034, 48.219669],
          [-110.827168, 48.133173],
          [-111.409097, 48.132218],
          [-111.409005, 48.21838],
          [-111.408922, 48.219476],
          [-111.274733, 48.219444],
          [-111.270702, 48.997895]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30059",
      state: "30",
      county: "059",
      name: "Meagher County",
      censusArea: 2391.913,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.28186, 46.18421],
          [-110.783982, 46.192408],
          [-110.790022, 46.192567],
          [-111.06256, 46.192897],
          [-111.12707, 46.273754],
          [-111.057211, 46.40345],
          [-111.126362, 46.441322],
          [-111.246288, 46.437997],
          [-111.276801, 46.516821],
          [-111.34497, 46.545832],
          [-111.330541, 46.633458],
          [-111.412842, 46.646878],
          [-111.497961, 46.762469],
          [-111.645393, 46.841937],
          [-111.658131, 46.913364],
          [-111.539341, 46.913427],
          [-111.539141, 47.000386],
          [-111.158518, 47.011501],
          [-111.158602, 47.088184],
          [-111.080855, 47.088138],
          [-111.104738, 47.029105],
          [-110.913285, 46.980853],
          [-110.85718, 46.922879],
          [-110.761016, 46.900286],
          [-110.751665, 46.844681],
          [-110.652877, 46.823626],
          [-110.620062, 46.771547],
          [-110.457163, 46.67872],
          [-110.273374, 46.71064],
          [-110.281858, 46.220519],
          [-110.28186, 46.18421]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30069",
      state: "30",
      county: "069",
      name: "Petroleum County",
      censusArea: 1654.871,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.63095, 46.749414],
          [-108.608546, 47.097826],
          [-108.736711, 47.098196],
          [-108.719223, 47.270267],
          [-108.590492, 47.270463],
          [-108.590901, 47.306262],
          [-108.314955, 47.314908],
          [-108.312615, 47.583452],
          [-108.065418, 47.598294],
          [-107.931105, 47.569073],
          [-107.908907, 47.450638],
          [-107.962875, 47.354368],
          [-107.968483, 47.250033],
          [-107.919544, 47.017133],
          [-107.882588, 47.000151],
          [-107.939113, 46.876459],
          [-107.892117, 46.851537],
          [-107.827755, 46.755986],
          [-108.63095, 46.749414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30093",
      state: "30",
      county: "093",
      name: "Silver Bow County",
      censusArea: 718.477,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.190269, 45.748503],
          [-112.515453, 45.746702],
          [-112.549534, 45.684317],
          [-112.687054, 45.624854],
          [-112.759339, 45.748209],
          [-113.085474, 45.860586],
          [-112.923572, 45.935524],
          [-112.938373, 45.993224],
          [-112.777105, 46.048363],
          [-112.777685, 46.135807],
          [-112.57685, 46.178859],
          [-112.504818, 46.183767],
          [-112.399399, 45.831089],
          [-112.190269, 45.748503]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30099",
      state: "30",
      county: "099",
      name: "Teton County",
      censusArea: 2272.375,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.408495, 47.987045],
          [-111.409117, 47.698277],
          [-111.667035, 47.697885],
          [-111.6668, 47.611241],
          [-111.922936, 47.611231],
          [-111.92253, 47.505078],
          [-112.047308, 47.516028],
          [-112.278406, 47.505955],
          [-112.55231, 47.652231],
          [-112.818574, 47.607849],
          [-112.91422, 47.694958],
          [-112.913256, 47.826216],
          [-112.984705, 47.953982],
          [-112.878615, 47.975766],
          [-113.016894, 48.13246],
          [-112.177757, 48.130932],
          [-112.177751, 48.087466],
          [-112.047253, 48.086892],
          [-111.9835, 48.043204],
          [-111.983753, 47.984771],
          [-111.408495, 47.987045]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30107",
      state: "30",
      county: "107",
      name: "Wheatland County",
      censusArea: 1423.195,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.654304, 46.219402],
          [-110.281858, 46.220519],
          [-110.273374, 46.71064],
          [-109.969223, 46.752655],
          [-109.746006, 46.693744],
          [-109.389163, 46.693661],
          [-109.402906, 46.219706],
          [-109.654304, 46.219402]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31003",
      state: "31",
      county: "003",
      name: "Antelope County",
      censusArea: 857.218,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.300592, 42.088853],
          [-98.300235, 42.436913],
          [-97.834431, 42.437684],
          [-97.834699, 42.091108],
          [-97.834536, 42.08975],
          [-97.834336, 42.088987],
          [-97.833288, 41.916286],
          [-98.295602, 41.914954],
          [-98.295438, 42.088873],
          [-98.300592, 42.088853]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31009",
      state: "31",
      county: "009",
      name: "Blaine County",
      censusArea: 710.874,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.686959, 42.086075],
          [-99.686834, 41.740326],
          [-100.251224, 41.739817],
          [-100.265474, 41.739856],
          [-100.268125, 42.07219],
          [-100.267622, 42.086147],
          [-100.167605, 42.085848],
          [-99.686959, 42.086075]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31023",
      state: "31",
      county: "023",
      name: "Butler County",
      censusArea: 584.907,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.90591, 41.456436],
          [-96.905898, 41.455074],
          [-96.905859, 41.454625],
          [-96.905862, 41.453389],
          [-96.908507, 41.046091],
          [-96.91094, 41.046116],
          [-97.368118, 41.046947],
          [-97.368186, 41.396416],
          [-97.253519, 41.384212],
          [-97.175613, 41.377083],
          [-96.90591, 41.456436]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31035",
      state: "31",
      county: "035",
      name: "Clay County",
      censusArea: 572.289,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.273571, 40.350359],
          [-98.27809, 40.350347],
          [-98.278103, 40.698291],
          [-97.825723, 40.698555],
          [-97.82478, 40.698537],
          [-97.824114, 40.350541],
          [-98.273571, 40.350359]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31037",
      state: "31",
      county: "037",
      name: "Colfax County",
      censusArea: 411.66,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.90591, 41.456436],
          [-97.175613, 41.377083],
          [-97.253519, 41.384212],
          [-97.252425, 41.743085],
          [-97.019911, 41.74298],
          [-96.905922, 41.742763],
          [-96.90591, 41.456436]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31047",
      state: "31",
      county: "047",
      name: "Dawson County",
      censusArea: 1013.096,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.223311, 40.700254],
          [-100.224235, 41.046597],
          [-99.425986, 41.046471],
          [-99.417316, 40.670825],
          [-99.643462, 40.684911],
          [-99.981743, 40.700003],
          [-100.223311, 40.700254]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31053",
      state: "31",
      county: "053",
      name: "Dodge County",
      censusArea: 528.706,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.90591, 41.456436],
          [-96.905922, 41.742763],
          [-96.555172, 41.742018],
          [-96.444217, 41.741775],
          [-96.444785, 41.683633],
          [-96.443898, 41.523933],
          [-96.329012, 41.393136],
          [-96.47072, 41.393239],
          [-96.570351, 41.433426],
          [-96.905862, 41.453389],
          [-96.905859, 41.454625],
          [-96.905898, 41.455074],
          [-96.90591, 41.456436]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31063",
      state: "31",
      county: "063",
      name: "Frontier County",
      censusArea: 974.592,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.197807, 40.350027],
          [-100.758435, 40.349507],
          [-100.777372, 40.349447],
          [-100.777376, 40.349466],
          [-100.779227, 40.692008],
          [-100.777833, 40.700135],
          [-100.223311, 40.700254],
          [-99.981743, 40.700003],
          [-99.981193, 40.438291],
          [-100.095095, 40.438656],
          [-100.094798, 40.351245],
          [-100.197807, 40.350027]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31073",
      state: "31",
      county: "073",
      name: "Gosper County",
      censusArea: 458.155,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.642209, 40.3512],
          [-100.094798, 40.351245],
          [-100.095095, 40.438656],
          [-99.981193, 40.438291],
          [-99.981743, 40.700003],
          [-99.643462, 40.684911],
          [-99.64328, 40.682639],
          [-99.642209, 40.3512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31085",
      state: "31",
      county: "085",
      name: "Hayes County",
      censusArea: 713.062,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.777376, 40.349466],
          [-101.323353, 40.350553],
          [-101.342788, 40.350444],
          [-101.345365, 40.698093],
          [-101.247963, 40.697802],
          [-100.777833, 40.700135],
          [-100.779227, 40.692008],
          [-100.777376, 40.349466]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31093",
      state: "31",
      county: "093",
      name: "Howard County",
      censusArea: 569.337,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.291419, 41.393894],
          [-98.286584, 41.393898],
          [-98.287168, 41.046363],
          [-98.721975, 41.046674],
          [-98.74076, 41.046702],
          [-98.740957, 41.046651],
          [-98.748526, 41.04664],
          [-98.74433, 41.394178],
          [-98.291419, 41.393894]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31097",
      state: "31",
      county: "097",
      name: "Johnson County",
      censusArea: 376.051,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.067012, 40.523148],
          [-96.066736, 40.262038],
          [-96.463667, 40.262173],
          [-96.463632, 40.523013],
          [-96.455766, 40.522803],
          [-96.067012, 40.523148]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31117",
      state: "31",
      county: "117",
      name: "Mc Pherson County",
      censusArea: 858.976,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.269695, 41.394934],
          [-101.40726, 41.395416],
          [-101.406466, 41.7432],
          [-100.842459, 41.740404],
          [-100.711711, 41.739761],
          [-100.713132, 41.394575],
          [-100.713243, 41.393511],
          [-101.269695, 41.394934]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28079",
      state: "28",
      county: "079",
      name: "Leake County",
      censusArea: 583,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.730304, 32.634228],
          [-89.73014, 32.685119],
          [-89.730016, 32.884773],
          [-89.730154, 32.929924],
          [-89.473311, 32.931115],
          [-89.319163, 32.931654],
          [-89.317588, 32.576458],
          [-89.322598, 32.576433],
          [-89.730152, 32.577248],
          [-89.730304, 32.634228]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28083",
      state: "28",
      county: "083",
      name: "Leflore County",
      censusArea: 592.539,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.136787, 33.721648],
          [-90.137011, 33.676944],
          [-90.127775, 33.676956],
          [-90.101401, 33.45894],
          [-90.174756, 33.417003],
          [-90.174614, 33.333378],
          [-90.321513, 33.375121],
          [-90.331589, 33.302825],
          [-90.3735, 33.242209],
          [-90.453801, 33.267302],
          [-90.453233, 33.329484],
          [-90.451643, 33.81139],
          [-90.182584, 33.809322],
          [-90.185599, 33.765514],
          [-90.294502, 33.750981],
          [-90.269105, 33.697208],
          [-90.136787, 33.721648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28095",
      state: "28",
      county: "095",
      name: "Monroe County",
      censusArea: 765.094,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.719979, 33.81133],
          [-88.718964, 34.074521],
          [-88.718693, 34.08934],
          [-88.543352, 34.089044],
          [-88.203597, 34.08653],
          [-88.207229, 34.058333],
          [-88.226428, 33.912875],
          [-88.226517, 33.911665],
          [-88.226517, 33.911551],
          [-88.24839, 33.744908],
          [-88.325119, 33.745591],
          [-88.37033, 33.685918],
          [-88.513917, 33.650209],
          [-88.496414, 33.680075],
          [-88.719626, 33.680253],
          [-88.719979, 33.81133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28099",
      state: "28",
      county: "099",
      name: "Neshoba County",
      censusArea: 570.141,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.914413, 32.9266],
          [-88.914516, 32.576955],
          [-89.317588, 32.576458],
          [-89.319163, 32.931654],
          [-88.914413, 32.9266]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28103",
      state: "28",
      county: "103",
      name: "Noxubee County",
      censusArea: 695.138,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.669076, 33.286922],
          [-88.304514, 33.28832],
          [-88.340432, 32.991199],
          [-88.347692, 32.929059],
          [-88.812782, 32.925973],
          [-88.813053, 33.286424],
          [-88.669076, 33.286922]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28115",
      state: "28",
      county: "115",
      name: "Pontotoc County",
      censusArea: 497.689,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.824966, 34.364759],
          [-88.824523, 34.074948],
          [-89.139158, 34.074116],
          [-89.244436, 34.073942],
          [-89.245485, 34.161186],
          [-89.246339, 34.379344],
          [-88.928901, 34.379361],
          [-88.824966, 34.364759]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28117",
      state: "28",
      county: "117",
      name: "Prentiss County",
      censusArea: 414.98,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.736465, 34.50891],
          [-88.73544, 34.596483],
          [-88.717794, 34.596472],
          [-88.718508, 34.756781],
          [-88.365472, 34.755604],
          [-88.330699, 34.740721],
          [-88.329193, 34.463571],
          [-88.542079, 34.465276],
          [-88.541749, 34.509192],
          [-88.736465, 34.50891]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28127",
      state: "28",
      county: "127",
      name: "Simpson County",
      censusArea: 589.163,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.730066, 32.047072],
          [-89.662086, 32.047239],
          [-89.653025, 31.780653],
          [-89.755103, 31.774654],
          [-89.974586, 31.761655],
          [-90.122565, 31.75268],
          [-90.132552, 31.856923],
          [-90.230331, 32.049728],
          [-89.730066, 32.047072]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28139",
      state: "28",
      county: "139",
      name: "Tippah County",
      censusArea: 457.815,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.017128, 34.994649],
          [-88.823049, 34.995157],
          [-88.823473, 34.858629],
          [-88.752956, 34.858775],
          [-88.718508, 34.756781],
          [-88.717794, 34.596472],
          [-88.73544, 34.596483],
          [-89.088488, 34.598247],
          [-89.088065, 34.815307],
          [-89.017551, 34.858723],
          [-89.017128, 34.994649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28151",
      state: "28",
      county: "151",
      name: "Washington County",
      censusArea: 724.741,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.716277, 33.270032],
          [-90.680319, 33.201971],
          [-90.701845, 33.096294],
          [-90.914465, 33.097417],
          [-90.914337, 33.007697],
          [-91.157662, 33.011217],
          [-91.125656, 33.038276],
          [-91.087589, 33.145177],
          [-91.085984, 33.221644],
          [-91.110561, 33.24593],
          [-91.141216, 33.298397],
          [-91.184427, 33.419576],
          [-91.131885, 33.430063],
          [-91.118495, 33.449116],
          [-91.125109, 33.472842],
          [-91.215671, 33.529423],
          [-90.763509, 33.529389],
          [-90.763773, 33.270359],
          [-90.716277, 33.270032]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29001",
      state: "29",
      county: "001",
      name: "Adair County",
      censusArea: 567.324,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.345064, 40.03721],
          [-92.847477, 40.037301],
          [-92.856191, 40.037253],
          [-92.855629, 40.342736],
          [-92.684167, 40.343466],
          [-92.349975, 40.346505],
          [-92.349897, 40.302733],
          [-92.347801, 40.302756],
          [-92.345064, 40.03721]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29009",
      state: "29",
      county: "009",
      name: "Barry County",
      censusArea: 778.251,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.068898, 36.747818],
          [-94.06274, 36.931775],
          [-93.611003, 36.922831],
          [-93.5738, 36.820032],
          [-93.584282, 36.498896],
          [-93.700171, 36.499135],
          [-93.709956, 36.499179],
          [-93.718893, 36.499178],
          [-93.727552, 36.499055],
          [-93.728022, 36.499037],
          [-93.866758, 36.498789],
          [-93.906128, 36.498718],
          [-93.92184, 36.498718],
          [-93.95919, 36.498717],
          [-93.96392, 36.498717],
          [-94.077089, 36.49873],
          [-94.068898, 36.747818]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29021",
      state: "29",
      county: "021",
      name: "Buchanan County",
      censusArea: 408.027,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.602257, 39.747195],
          [-94.601246, 39.530372],
          [-94.601326, 39.532138],
          [-95.102888, 39.533347],
          [-95.109304, 39.542285],
          [-95.113557, 39.553941],
          [-95.113077, 39.559133],
          [-95.107454, 39.573843],
          [-95.069592, 39.615106],
          [-95.01531, 39.674262],
          [-95.011103, 39.677572],
          [-94.937924, 39.725471],
          [-94.916932, 39.728186],
          [-94.895268, 39.76321],
          [-94.908267, 39.812172],
          [-94.876544, 39.820594],
          [-94.602819, 39.819608],
          [-94.602257, 39.747195]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29033",
      state: "29",
      county: "033",
      name: "Carroll County",
      censusArea: 694.621,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.758357, 39.611407],
          [-93.278098, 39.615632],
          [-93.275932, 39.484371],
          [-93.104485, 39.383656],
          [-93.200708, 39.401787],
          [-93.230079, 39.327877],
          [-93.332252, 39.309606],
          [-93.333337, 39.250012],
          [-93.477233, 39.292796],
          [-93.487885, 39.228875],
          [-93.650526, 39.248214],
          [-93.758463, 39.207021],
          [-93.758651, 39.209623],
          [-93.759183, 39.524558],
          [-93.758357, 39.611407]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29043",
      state: "29",
      county: "043",
      name: "Christian County",
      censusArea: 562.645,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.909336, 36.809178],
          [-93.146854, 36.81382],
          [-93.304359, 36.816866],
          [-93.342041, 36.817301],
          [-93.337451, 36.992494],
          [-93.610126, 36.99581],
          [-93.608899, 37.098153],
          [-93.065274, 37.088694],
          [-92.903273, 37.070651],
          [-92.909336, 36.809178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29053",
      state: "29",
      county: "053",
      name: "Cooper County",
      censusArea: 564.765,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.050453, 38.928244],
          [-93.048471, 38.972418],
          [-92.934569, 39.064547],
          [-92.841617, 38.979821],
          [-92.558086, 38.970692],
          [-92.544946, 38.959691],
          [-92.540118, 38.953718],
          [-92.526437, 38.944776],
          [-92.514987, 38.938114],
          [-92.508657, 38.93271],
          [-92.498377, 38.922084],
          [-92.617147, 38.680013],
          [-92.839837, 38.683974],
          [-92.950502, 38.686028],
          [-93.059974, 38.693077],
          [-93.050453, 38.928244]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29065",
      state: "29",
      county: "065",
      name: "Dent County",
      censusArea: 752.79,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.528797, 37.788989],
          [-91.308311, 37.787186],
          [-91.310655, 37.700048],
          [-91.153345, 37.69734],
          [-91.155073, 37.588092],
          [-91.312458, 37.592824],
          [-91.314236, 37.505132],
          [-91.210984, 37.501911],
          [-91.211863, 37.415277],
          [-91.646626, 37.422731],
          [-91.75504, 37.42411],
          [-91.754795, 37.598768],
          [-91.809105, 37.598863],
          [-91.806219, 37.791286],
          [-91.528797, 37.788989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29075",
      state: "29",
      county: "075",
      name: "Gentry County",
      censusArea: 491.422,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.216696, 40.136626],
          [-94.216494, 40.12503],
          [-94.2188, 40.034885],
          [-94.605377, 40.039217],
          [-94.606575, 40.042049],
          [-94.605942, 40.126127],
          [-94.605593, 40.127767],
          [-94.598487, 40.386714],
          [-94.214278, 40.384254],
          [-94.216696, 40.136626]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29085",
      state: "29",
      county: "085",
      name: "Hickory County",
      censusArea: 399.091,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.065199, 38.062479],
          [-93.072447, 37.902627],
          [-93.182706, 37.904144],
          [-93.187915, 37.802737],
          [-93.574103, 37.813336],
          [-93.573202, 37.828035],
          [-93.568709, 37.914918],
          [-93.512103, 37.913535],
          [-93.504025, 38.071757],
          [-93.497132, 38.072573],
          [-93.065199, 38.062479]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37001",
      state: "37",
      county: "001",
      name: "Alamance County",
      censusArea: 423.943,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.259481, 36.137273],
          [-79.249475, 35.876788],
          [-79.318849, 35.844078],
          [-79.54249, 35.843399],
          [-79.541978, 35.899627],
          [-79.541622, 35.902097],
          [-79.532365, 36.241342],
          [-79.532329, 36.241427],
          [-79.532096, 36.248584],
          [-79.523399, 36.249851],
          [-79.365366, 36.246411],
          [-79.315828, 36.245331],
          [-79.256964, 36.243866],
          [-79.259481, 36.137273]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37005",
      state: "37",
      county: "005",
      name: "Alleghany County",
      censusArea: 235.059,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.353169, 36.574724],
          [-81.307511, 36.575024],
          [-81.061866, 36.56702],
          [-81.058844, 36.566976],
          [-80.945988, 36.563196],
          [-80.944338, 36.563058],
          [-80.901836, 36.561754],
          [-80.974619, 36.452846],
          [-80.967066, 36.4016],
          [-81.013615, 36.371271],
          [-81.094829, 36.440433],
          [-81.253482, 36.365351],
          [-81.353169, 36.574724]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37017",
      state: "37",
      county: "017",
      name: "Bladen County",
      censusArea: 874.328,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.256316, 34.399763],
          [-78.337248, 34.367089],
          [-78.44623, 34.376839],
          [-78.677447, 34.469435],
          [-78.87153, 34.482668],
          [-78.805744, 34.689267],
          [-78.901096, 34.834789],
          [-78.49461, 34.856464],
          [-78.254556, 34.553963],
          [-78.176496, 34.465264],
          [-78.256316, 34.399763]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37021",
      state: "37",
      county: "021",
      name: "Buncombe County",
      censusArea: 656.672,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.169018, 35.527841],
          [-82.266847, 35.465854],
          [-82.570269, 35.431255],
          [-82.672392, 35.459534],
          [-82.744383, 35.423342],
          [-82.800276, 35.470536],
          [-82.766726, 35.566764],
          [-82.883665, 35.67819],
          [-82.806917, 35.685686],
          [-82.514519, 35.803194],
          [-82.408728, 35.818061],
          [-82.34816, 35.810581],
          [-82.275394, 35.704423],
          [-82.290797, 35.591288],
          [-82.169018, 35.527841]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37027",
      state: "37",
      county: "027",
      name: "Caldwell County",
      censusArea: 471.566,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.334255, 35.79646],
          [-81.34634, 35.794072],
          [-81.355709, 35.780385],
          [-81.353862, 35.768895],
          [-81.364055, 35.767865],
          [-81.55596, 35.777332],
          [-81.80712, 35.962057],
          [-81.735974, 36.066826],
          [-81.81152, 36.111544],
          [-81.545455, 36.117398],
          [-81.328385, 35.99655],
          [-81.334255, 35.79646]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37055",
      state: "37",
      county: "055",
      name: "Dare County",
      censusArea: 383.42,
      stateName: "North Carolina"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-75.753765, 35.199612],
            [-75.74522, 35.20303],
            [-75.734171, 35.204347],
            [-75.718015, 35.209377],
            [-75.708947, 35.213912],
            [-75.698972, 35.221166],
            [-75.694437, 35.22298],
            [-75.68749, 35.231171],
            [-75.684006, 35.232913],
            [-75.681916, 35.232913],
            [-75.675394, 35.228421],
            [-75.664512, 35.227514],
            [-75.640934, 35.233862],
            [-75.630358, 35.238487],
            [-75.615378, 35.248938],
            [-75.599005, 35.256253],
            [-75.598312, 35.261067],
            [-75.59796, 35.266704],
            [-75.596915, 35.269491],
            [-75.585419, 35.266356],
            [-75.581935, 35.263917],
            [-75.561033, 35.266008],
            [-75.535741, 35.272856],
            [-75.529393, 35.288272],
            [-75.523952, 35.318198],
            [-75.518511, 35.336335],
            [-75.51261, 35.362853],
            [-75.506722, 35.387118],
            [-75.500374, 35.424298],
            [-75.494933, 35.454224],
            [-75.487678, 35.485056],
            [-75.488585, 35.497752],
            [-75.489618, 35.508471],
            [-75.487528, 35.525889],
            [-75.482237, 35.53856],
            [-75.47861, 35.553069],
            [-75.47861, 35.599318],
            [-75.48133, 35.622896],
            [-75.487678, 35.648287],
            [-75.498675, 35.666281],
            [-75.507385, 35.680564],
            [-75.515745, 35.721671],
            [-75.515397, 35.73038],
            [-75.533512, 35.773577],
            [-75.528992, 35.776289],
            [-75.522232, 35.774178],
            [-75.502427, 35.742913],
            [-75.496086, 35.728515],
            [-75.479128, 35.678634],
            [-75.458659, 35.596597],
            [-75.460061, 35.581314],
            [-75.462491, 35.553556],
            [-75.471355, 35.479615],
            [-75.486771, 35.391652],
            [-75.502188, 35.320012],
            [-75.52592, 35.233839],
            [-75.533627, 35.225825],
            [-75.544809, 35.228421],
            [-75.560225, 35.232048],
            [-75.580176, 35.231142],
            [-75.610101, 35.227514],
            [-75.635493, 35.22026],
            [-75.672673, 35.208471],
            [-75.728897, 35.190334],
            [-75.749406, 35.185207],
            [-75.757916, 35.183079],
            [-75.769705, 35.180359],
            [-75.789655, 35.172197],
            [-75.840438, 35.15134],
            [-75.912985, 35.1196],
            [-75.944725, 35.105091],
            [-75.963768, 35.092395],
            [-75.982812, 35.081513],
            [-76.00151, 35.06723],
            [-76.013145, 35.061855],
            [-76.014954, 35.065349],
            [-76.013561, 35.068832],
            [-76.000949, 35.084234],
            [-75.99188, 35.092395],
            [-75.989175, 35.100882],
            [-75.990569, 35.108546],
            [-75.989175, 35.115165],
            [-75.98395, 35.120042],
            [-75.973499, 35.121087],
            [-75.966489, 35.117787],
            [-75.9547, 35.1196],
            [-75.923867, 35.135017],
            [-75.910265, 35.142271],
            [-75.893942, 35.150433],
            [-75.839531, 35.172197],
            [-75.819172, 35.176826],
            [-75.812902, 35.178568],
            [-75.801444, 35.183079],
            [-75.793283, 35.18852],
            [-75.785729, 35.194244],
            [-75.754289, 35.199402],
            [-75.753765, 35.199612]
          ]
        ],
        [
          [
            [-75.773329, 36.231529],
            [-75.77251, 36.22944],
            [-75.759637, 36.204705],
            [-75.738431, 36.154282],
            [-75.71831, 36.113674],
            [-75.696742, 36.077497],
            [-75.658537, 36.02043],
            [-75.569794, 35.863301],
            [-75.552299, 35.822173],
            [-75.538739, 35.797396],
            [-75.533012, 35.787377],
            [-75.536428, 35.780118],
            [-75.543259, 35.779691],
            [-75.546675, 35.787377],
            [-75.553934, 35.799332],
            [-75.566238, 35.813072],
            [-75.573083, 35.828867],
            [-75.588878, 35.844926],
            [-75.60125, 35.867302],
            [-75.619151, 35.889415],
            [-75.617045, 35.906],
            [-75.617552, 35.914186],
            [-75.620114, 35.925288],
            [-75.631215, 35.941512],
            [-75.648899, 35.965758],
            [-75.668379, 35.978394],
            [-75.671801, 35.985238],
            [-75.678909, 35.993925],
            [-75.723662, 36.003139],
            [-75.727084, 36.01051],
            [-75.726558, 36.02104],
            [-75.722082, 36.03236],
            [-75.722609, 36.037362],
            [-75.726821, 36.040521],
            [-75.737088, 36.040784],
            [-75.74051, 36.046839],
            [-75.741563, 36.055526],
            [-75.739457, 36.066846],
            [-75.73972, 36.07527],
            [-75.75025, 36.121076],
            [-75.750479, 36.131208],
            [-75.752226, 36.140817],
            [-75.75572, 36.153922],
            [-75.775814, 36.201097],
            [-75.783676, 36.215949],
            [-75.793286, 36.226432],
            [-75.794371, 36.227336],
            [-75.773329, 36.231529]
          ]
        ],
        [
          [
            [-75.675245, 35.929024],
            [-75.65954, 35.919564],
            [-75.662938, 35.916166],
            [-75.662019, 35.906522],
            [-75.653478, 35.904686],
            [-75.648519, 35.906982],
            [-75.64512, 35.905788],
            [-75.62767, 35.883149],
            [-75.616833, 35.856331],
            [-75.619772, 35.847606],
            [-75.614361, 35.815659],
            [-75.620454, 35.809253],
            [-75.624235, 35.809387],
            [-75.63898, 35.818639],
            [-75.667891, 35.82354],
            [-75.675054, 35.830204],
            [-75.660086, 35.83861],
            [-75.660598, 35.862541],
            [-75.663356, 35.869835],
            [-75.67283, 35.882423],
            [-75.681415, 35.88398],
            [-75.697672, 35.901639],
            [-75.696871, 35.909556],
            [-75.702165, 35.915428],
            [-75.723782, 35.925569],
            [-75.727251, 35.93362],
            [-75.726807, 35.935844],
            [-75.718266, 35.939714],
            [-75.705323, 35.939403],
            [-75.69115, 35.936932],
            [-75.686358, 35.932973],
            [-75.675245, 35.929024]
          ]
        ],
        [
          [
            [-75.999386, 35.903521],
            [-75.97783, 35.897181],
            [-75.966247, 35.899287],
            [-75.962562, 35.901393],
            [-75.94782, 35.920347],
            [-75.934131, 35.928244],
            [-75.929919, 35.928771],
            [-75.927286, 35.93193],
            [-75.92676, 35.940354],
            [-75.937816, 35.950884],
            [-75.943608, 35.952464],
            [-75.946767, 35.955623],
            [-75.947293, 35.959835],
            [-75.938343, 35.9651],
            [-75.899382, 35.977209],
            [-75.879374, 35.978789],
            [-75.86042, 35.978262],
            [-75.84989, 35.976156],
            [-75.80935, 35.959308],
            [-75.805138, 35.954043],
            [-75.800926, 35.944566],
            [-75.782498, 35.935615],
            [-75.782498, 35.928244],
            [-75.778813, 35.918241],
            [-75.768809, 35.901393],
            [-75.751961, 35.878227],
            [-75.753014, 35.871382],
            [-75.748276, 35.852428],
            [-75.734587, 35.839266],
            [-75.727216, 35.822703],
            [-75.726689, 35.811361],
            [-75.732612, 35.790666],
            [-75.738233, 35.778301],
            [-75.739357, 35.770994],
            [-75.735422, 35.767622],
            [-75.724743, 35.742892],
            [-75.719123, 35.714227],
            [-75.715188, 35.708045],
            [-75.71294, 35.69849],
            [-75.713502, 35.693993],
            [-75.741605, 35.672073],
            [-75.742167, 35.655212],
            [-75.737109, 35.63835],
            [-75.729802, 35.628795],
            [-75.729802, 35.625985],
            [-75.747225, 35.610248],
            [-75.762963, 35.603503],
            [-75.778138, 35.592262],
            [-75.775328, 35.579335],
            [-75.797248, 35.574276],
            [-75.837154, 35.570904],
            [-75.851767, 35.578773],
            [-75.859636, 35.586641],
            [-75.869869, 35.582743],
            [-75.901164, 35.668998],
            [-76.045987, 35.666117],
            [-75.999386, 35.903521]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37057",
      state: "37",
      county: "057",
      name: "Davidson County",
      censusArea: 552.675,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.395389, 35.97321],
          [-80.26692, 35.996552],
          [-80.213906, 35.996876],
          [-80.213833, 36.026772],
          [-80.149775, 36.026697],
          [-80.137549, 36.024857],
          [-80.043272, 36.01061],
          [-80.045563, 35.956436],
          [-80.04575, 35.955596],
          [-80.046663, 35.935236],
          [-80.046709, 35.920663],
          [-80.046923, 35.916808],
          [-80.066842, 35.505661],
          [-80.182682, 35.504335],
          [-80.208176, 35.580401],
          [-80.326578, 35.675475],
          [-80.332228, 35.719047],
          [-80.339465, 35.721385],
          [-80.369852, 35.716236],
          [-80.391729, 35.722258],
          [-80.413844, 35.722471],
          [-80.458904, 35.742811],
          [-80.464086, 35.827538],
          [-80.365681, 35.895787],
          [-80.395389, 35.97321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37067",
      state: "37",
      county: "067",
      name: "Forsyth County",
      censusArea: 408.148,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.137549, 36.024857],
          [-80.149775, 36.026697],
          [-80.213833, 36.026772],
          [-80.213906, 35.996876],
          [-80.26692, 35.996552],
          [-80.395389, 35.97321],
          [-80.496403, 36.046536],
          [-80.516138, 36.092619],
          [-80.439622, 36.136552],
          [-80.452319, 36.241222],
          [-80.452452, 36.241356],
          [-80.452379, 36.241957],
          [-80.451954, 36.261635],
          [-80.03554, 36.257351],
          [-80.043272, 36.01061],
          [-80.137549, 36.024857]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37081",
      state: "37",
      county: "081",
      name: "Guilford County",
      censusArea: 645.704,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.03554, 36.257351],
          [-80.027352, 36.257507],
          [-79.808899, 36.249619],
          [-79.797538, 36.248806],
          [-79.700569, 36.246043],
          [-79.532365, 36.241342],
          [-79.541622, 35.902097],
          [-79.541978, 35.899627],
          [-80.020377, 35.919611],
          [-80.046709, 35.920663],
          [-80.046663, 35.935236],
          [-80.04575, 35.955596],
          [-80.045563, 35.956436],
          [-80.043272, 36.01061],
          [-80.03554, 36.257351]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37091",
      state: "37",
      county: "091",
      name: "Hertford County",
      censusArea: 353.06,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.706556, 36.243869],
          [-77.183194, 36.242692],
          [-77.20879, 36.246875],
          [-77.119577, 36.474139],
          [-77.1645, 36.54633],
          [-76.916048, 36.543815],
          [-76.916001, 36.543818],
          [-76.931821, 36.40107],
          [-76.762156, 36.352687],
          [-76.696601, 36.296211],
          [-76.690392, 36.267269],
          [-76.706556, 36.243869]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37103",
      state: "37",
      county: "103",
      name: "Jones County",
      censusArea: 470.708,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.473716, 35.228983],
          [-77.339077, 35.155076],
          [-77.125021, 35.077561],
          [-77.108813, 34.887572],
          [-77.090323, 34.802755],
          [-77.165803, 34.784945],
          [-77.221606, 34.887088],
          [-77.328398, 34.938317],
          [-77.367414, 34.906631],
          [-77.452144, 34.946963],
          [-77.675373, 34.973417],
          [-77.68338, 34.979123],
          [-77.73103, 35.00814],
          [-77.730784, 35.008259],
          [-77.601831, 35.071193],
          [-77.506638, 35.172177],
          [-77.527206, 35.243433],
          [-77.52321, 35.241725],
          [-77.478741, 35.230011],
          [-77.473716, 35.228983]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37117",
      state: "37",
      county: "117",
      name: "Martin County",
      censusArea: 461.217,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.327686, 36.074512],
          [-77.206169, 36.004737],
          [-77.196689, 35.937433],
          [-77.043684, 35.943146],
          [-77.004306, 35.858714],
          [-76.918531, 35.895647],
          [-76.888624, 35.812287],
          [-76.830195, 35.885922],
          [-76.760842, 35.864822],
          [-76.845119, 35.705409],
          [-76.985749, 35.658485],
          [-77.174034, 35.733026],
          [-77.350602, 35.819214],
          [-77.342462, 35.905346],
          [-77.402701, 36.004933],
          [-77.339263, 36.064681],
          [-77.327686, 36.074512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37127",
      state: "37",
      county: "127",
      name: "Nash County",
      censusArea: 540.407,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.915581, 35.83659],
          [-77.927859, 35.831508],
          [-77.931355, 35.830165],
          [-78.19207, 35.730443],
          [-78.254903, 35.81773],
          [-78.172179, 35.956301],
          [-78.006554, 36.202663],
          [-77.887083, 36.14384],
          [-77.697879, 36.152981],
          [-77.774125, 35.998742],
          [-77.777751, 35.984635],
          [-77.8091, 35.909272],
          [-77.822385, 35.878216],
          [-77.828444, 35.867208],
          [-77.915581, 35.83659]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37141",
      state: "37",
      county: "141",
      name: "Pender County",
      censusArea: 869.795,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.51796, 34.440483],
          [-77.556943, 34.417218],
          [-77.582323, 34.400506],
          [-77.635034, 34.359555],
          [-77.661673, 34.341868],
          [-77.687226, 34.320444],
          [-77.713322, 34.294879],
          [-77.715916, 34.292719],
          [-77.822995, 34.389359],
          [-78.029922, 34.331768],
          [-78.16235, 34.357007],
          [-78.256316, 34.399763],
          [-78.176496, 34.465264],
          [-78.254556, 34.553963],
          [-78.113208, 34.721969],
          [-77.68056, 34.72064],
          [-77.576411, 34.478737],
          [-77.51796, 34.440483]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37151",
      state: "37",
      county: "151",
      name: "Randolph County",
      censusArea: 782.517,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.046923, 35.916808],
          [-80.046709, 35.920663],
          [-80.020377, 35.919611],
          [-79.541978, 35.899627],
          [-79.54249, 35.843399],
          [-79.555896, 35.515085],
          [-79.769285, 35.511457],
          [-80.066842, 35.505661],
          [-80.046923, 35.916808]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37165",
      state: "37",
      county: "165",
      name: "Scotland County",
      censusArea: 318.845,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.461754, 34.630432],
          [-79.468717, 34.635323],
          [-79.471599, 34.6372],
          [-79.479305, 34.64464],
          [-79.490201, 34.653819],
          [-79.519043, 34.677321],
          [-79.520269, 34.678327],
          [-79.554454, 34.706363],
          [-79.561691, 34.711996],
          [-79.631577, 34.768835],
          [-79.634216, 34.771012],
          [-79.688088, 34.804897],
          [-79.690201, 34.804937],
          [-79.692948, 34.804973],
          [-79.561826, 34.910363],
          [-79.572684, 34.991968],
          [-79.459176, 35.043096],
          [-79.354278, 34.945641],
          [-79.349143, 34.838476],
          [-79.359305, 34.743079],
          [-79.364296, 34.737549],
          [-79.461754, 34.630432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37167",
      state: "37",
      county: "167",
      name: "Stanly County",
      censusArea: 395.086,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.428251, 35.167347],
          [-80.438682, 35.161758],
          [-80.468247, 35.166815],
          [-80.48259, 35.180278],
          [-80.505334, 35.184048],
          [-80.295236, 35.502947],
          [-80.182682, 35.504335],
          [-80.051739, 35.372585],
          [-80.098185, 35.255173],
          [-80.07539, 35.143081],
          [-80.174176, 35.147992],
          [-80.277052, 35.196136],
          [-80.428251, 35.167347]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39055",
      state: "39",
      county: "055",
      name: "Geauga County",
      censusArea: 400.164,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.003066, 41.425928],
          [-81.003194, 41.424482],
          [-81.003319, 41.34786],
          [-81.391694, 41.348272],
          [-81.391005, 41.569716],
          [-81.29588, 41.56995],
          [-81.294669, 41.640889],
          [-81.101422, 41.641457],
          [-81.102258, 41.714307],
          [-81.003631, 41.715137],
          [-81.00299, 41.700009],
          [-81.003125, 41.501685],
          [-81.003066, 41.425928]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39063",
      state: "39",
      county: "063",
      name: "Hancock County",
      censusArea: 531.358,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.421054, 41.16678],
          [-83.420319, 40.991889],
          [-83.515883, 40.818134],
          [-83.880063, 40.819919],
          [-83.880423, 40.920429],
          [-83.881149, 41.167824],
          [-83.421054, 41.16678]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39065",
      state: "39",
      county: "065",
      name: "Hardin County",
      censusArea: 470.405,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.520227, 40.504077],
          [-83.879932, 40.538708],
          [-83.880194, 40.64469],
          [-83.880063, 40.819919],
          [-83.515883, 40.818134],
          [-83.495799, 40.701617],
          [-83.419836, 40.686823],
          [-83.415303, 40.515495],
          [-83.418378, 40.505232],
          [-83.520227, 40.504077]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39075",
      state: "39",
      county: "075",
      name: "Holmes County",
      censusArea: 422.533,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.126199, 40.668229],
          [-81.650045, 40.668117],
          [-81.650048, 40.65526],
          [-81.649199, 40.635106],
          [-81.669654, 40.443866],
          [-81.709132, 40.444775],
          [-82.184607, 40.45628],
          [-82.179069, 40.573112],
          [-82.22011, 40.568206],
          [-82.220665, 40.66758],
          [-82.126199, 40.668229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39089",
      state: "39",
      county: "089",
      name: "Licking County",
      censusArea: 682.5,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.581813, 39.930156],
          [-82.782495, 39.939662],
          [-82.761827, 40.125855],
          [-82.750747, 40.276996],
          [-82.476114, 40.264703],
          [-82.364344, 40.240957],
          [-82.326025, 40.239725],
          [-82.195911, 40.239071],
          [-82.182823, 40.238624],
          [-82.187105, 40.16688],
          [-82.198772, 39.95014],
          [-82.233974, 39.91326],
          [-82.462813, 39.93038],
          [-82.473423, 39.925065],
          [-82.581813, 39.930156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39097",
      state: "39",
      county: "097",
      name: "Madison County",
      censusArea: 465.875,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.503714, 40.111468],
          [-83.206273, 40.107733],
          [-83.25383, 40.049218],
          [-83.243702, 39.812503],
          [-83.252435, 39.695438],
          [-83.65333, 39.716876],
          [-83.648882, 39.761115],
          [-83.648641, 39.768478],
          [-83.647169, 39.773029],
          [-83.588041, 39.768778],
          [-83.516155, 40.010188],
          [-83.503714, 40.111468]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39109",
      state: "39",
      county: "109",
      name: "Miami County",
      censusArea: 406.58,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.157223, 39.88563],
          [-84.157671, 39.92297],
          [-84.425902, 39.919622],
          [-84.432575, 40.197037],
          [-84.022919, 40.183945],
          [-84.036069, 40.040182],
          [-84.051039, 39.879807],
          [-84.056413, 39.879875],
          [-84.098237, 39.882529],
          [-84.10483, 39.882652],
          [-84.154452, 39.885587],
          [-84.157223, 39.88563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39121",
      state: "39",
      county: "121",
      name: "Noble County",
      censusArea: 398.012,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.588185, 39.586973],
          [-81.586132, 39.663993],
          [-81.6432, 39.665681],
          [-81.639886, 39.75342],
          [-81.697442, 39.755572],
          [-81.694146, 39.842636],
          [-81.580313, 39.839104],
          [-81.57859, 39.897683],
          [-81.464108, 39.89455],
          [-81.386006, 39.950694],
          [-81.234053, 39.95127],
          [-81.237316, 39.867828],
          [-81.313591, 39.869471],
          [-81.319125, 39.70736],
          [-81.283075, 39.605796],
          [-81.394934, 39.601547],
          [-81.47316, 39.645976],
          [-81.473471, 39.583318],
          [-81.588185, 39.586973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39131",
      state: "39",
      county: "131",
      name: "Pike County",
      censusArea: 440.282,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.04362, 38.956939],
          [-83.212107, 38.960148],
          [-83.270817, 39.015791],
          [-83.385637, 39.055197],
          [-83.353531, 39.197585],
          [-82.785891, 39.168769],
          [-82.80709, 38.948073],
          [-83.04362, 38.956939]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39143",
      state: "39",
      county: "143",
      name: "Sandusky County",
      censusArea: 408.453,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.414626, 41.500228],
          [-83.338196, 41.457508],
          [-82.952224, 41.458239],
          [-82.847713, 41.430326],
          [-82.841475, 41.290023],
          [-82.840087, 41.255337],
          [-83.419843, 41.254003],
          [-83.414626, 41.500228]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39147",
      state: "39",
      county: "147",
      name: "Seneca County",
      censusArea: 551.017,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.305004, 40.992787],
          [-83.420319, 40.991889],
          [-83.421054, 41.16678],
          [-83.419843, 41.254003],
          [-82.840087, 41.255337],
          [-82.829512, 40.996624],
          [-83.112737, 40.993447],
          [-83.305004, 40.992787]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39159",
      state: "39",
      county: "159",
      name: "Union County",
      censusArea: 431.73,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.551338, 40.22937],
          [-83.520227, 40.504077],
          [-83.418378, 40.505232],
          [-83.248183, 40.507034],
          [-83.248595, 40.443989],
          [-83.248428, 40.244466],
          [-83.17233, 40.244257],
          [-83.169981, 40.143087],
          [-83.170208, 40.106927],
          [-83.206273, 40.107733],
          [-83.503714, 40.111468],
          [-83.494498, 40.225467],
          [-83.551338, 40.22937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39169",
      state: "39",
      county: "169",
      name: "Wayne County",
      censusArea: 554.929,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.1279, 40.900902],
          [-82.128544, 40.929977],
          [-82.127904, 40.937321],
          [-82.129334, 40.991807],
          [-81.688491, 40.98859],
          [-81.647736, 40.914386],
          [-81.650045, 40.668117],
          [-82.126199, 40.668229],
          [-82.12501, 40.886263],
          [-82.1279, 40.900902]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39173",
      state: "39",
      county: "173",
      name: "Wood County",
      censusArea: 617.205,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.703336, 41.528952],
          [-83.569934, 41.61715],
          [-83.415919, 41.618938],
          [-83.414626, 41.500228],
          [-83.419843, 41.254003],
          [-83.421054, 41.16678],
          [-83.881149, 41.167824],
          [-83.883234, 41.414503],
          [-83.712432, 41.492856],
          [-83.703336, 41.528952]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40009",
      state: "40",
      county: "009",
      name: "Beckham County",
      censusArea: 901.805,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.000385, 35.182702],
          [-100.000389, 35.422364],
          [-99.57582, 35.421655],
          [-99.575598, 35.508861],
          [-99.364141, 35.508793],
          [-99.364217, 35.465328],
          [-99.360226, 35.116738],
          [-99.409268, 35.116493],
          [-99.409291, 35.114509],
          [-99.782727, 35.11678],
          [-99.782949, 35.030111],
          [-99.888571, 35.030262],
          [-100.000384, 35.030385],
          [-100.000385, 35.182702]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40019",
      state: "40",
      county: "019",
      name: "Carter County",
      censusArea: 822.175,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.933671, 34.172553],
          [-96.969627, 34.07102],
          [-97.560764, 34.071089],
          [-97.562616, 34.082921],
          [-97.562575, 34.289182],
          [-97.562323, 34.507036],
          [-97.35211, 34.506788],
          [-97.352158, 34.376227],
          [-97.036488, 34.375415],
          [-96.933457, 34.332661],
          [-96.933671, 34.172553]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40031",
      state: "40",
      county: "031",
      name: "Comanche County",
      censusArea: 1069.287,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.142148, 34.506994],
          [-98.24412, 34.507121],
          [-98.295928, 34.449043],
          [-98.504958, 34.449414],
          [-98.504988, 34.420452],
          [-98.661956, 34.405948],
          [-98.661982, 34.50746],
          [-98.82607, 34.50737],
          [-98.826008, 34.594413],
          [-98.826124, 34.855466],
          [-98.619816, 34.854953],
          [-98.092645, 34.854919],
          [-98.089558, 34.854929],
          [-98.089098, 34.681119],
          [-98.141799, 34.681158],
          [-98.142148, 34.506994]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40043",
      state: "40",
      county: "043",
      name: "Dewey County",
      censusArea: 999.476,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.381022, 36.012925],
          [-99.382074, 36.164301],
          [-98.955849, 36.161577],
          [-98.636899, 36.164894],
          [-98.631985, 35.812402],
          [-99.375817, 35.812188],
          [-99.381022, 36.012925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40053",
      state: "40",
      county: "053",
      name: "Grant County",
      censusArea: 1000.871,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.462459, 36.593627],
          [-98.104286, 36.593578],
          [-98.109311, 36.593588],
          [-98.111985, 36.998133],
          [-98.045342, 36.998327],
          [-98.03989, 36.998349],
          [-98.033955, 36.998366],
          [-97.802313, 36.998713],
          [-97.802298, 36.998713],
          [-97.783489, 36.998847],
          [-97.783432, 36.998961],
          [-97.768704, 36.99875],
          [-97.697104, 36.998826],
          [-97.650466, 36.999004],
          [-97.637137, 36.99909],
          [-97.606549, 36.998682],
          [-97.564536, 36.998711],
          [-97.546498, 36.998747],
          [-97.5459, 36.998709],
          [-97.527292, 36.99875],
          [-97.472861, 36.998721],
          [-97.462346, 36.998685],
          [-97.462459, 36.593627]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37181",
      state: "37",
      county: "181",
      name: "Vance County",
      censusArea: 253.517,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.496474, 36.175217],
          [-78.508635, 36.183152],
          [-78.510319, 36.240763],
          [-78.5113, 36.267521],
          [-78.510658, 36.309712],
          [-78.511276, 36.430753],
          [-78.45697, 36.542474],
          [-78.441199, 36.542687],
          [-78.436333, 36.542666],
          [-78.323912, 36.543809],
          [-78.28767, 36.358369],
          [-78.307012, 36.266159],
          [-78.422426, 36.16416],
          [-78.496474, 36.175217]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37191",
      state: "37",
      county: "191",
      name: "Wayne County",
      censusArea: 553.087,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.805564, 35.369768],
          [-77.831055, 35.23344],
          [-77.83425, 35.177814],
          [-77.893043, 35.14656],
          [-78.043617, 35.192769],
          [-78.163539, 35.18937],
          [-78.304662, 35.286097],
          [-78.153233, 35.351421],
          [-78.154889, 35.417627],
          [-78.064603, 35.585419],
          [-77.823471, 35.585523],
          [-77.824291, 35.498663],
          [-77.826207, 35.476813],
          [-77.827, 35.431151],
          [-77.818414, 35.415911],
          [-77.80559, 35.407469],
          [-77.799961, 35.382462],
          [-77.802742, 35.372945],
          [-77.805258, 35.370218],
          [-77.805564, 35.369768]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37197",
      state: "37",
      county: "197",
      name: "Yadkin County",
      censusArea: 334.829,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.496403, 36.046536],
          [-80.693198, 36.051114],
          [-80.88218, 36.055731],
          [-80.873259, 36.236383],
          [-80.652865, 36.252571],
          [-80.547478, 36.285246],
          [-80.452319, 36.241222],
          [-80.439622, 36.136552],
          [-80.516138, 36.092619],
          [-80.496403, 36.046536]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38007",
      state: "38",
      county: "007",
      name: "Billings County",
      censusArea: 1148.853,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.60921, 46.629797],
          [-103.609545, 46.979902],
          [-103.666986, 46.979789],
          [-103.666723, 47.329354],
          [-103.099197, 47.328714],
          [-103.032569, 47.328692],
          [-103.032681, 46.98058],
          [-103.230991, 46.980296],
          [-103.231566, 46.629918],
          [-103.60921, 46.629797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38019",
      state: "38",
      county: "019",
      name: "Cavalier County",
      censusArea: 1488.751,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.999819, 48.999769],
          [-97.950205, 49.000459],
          [-97.949577, 48.717693],
          [-97.927154, 48.543124],
          [-98.318098, 48.543773],
          [-98.970392, 48.544232],
          [-98.970368, 48.718089],
          [-98.99989, 48.718129],
          [-98.999819, 48.999769]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38031",
      state: "38",
      county: "031",
      name: "Foster County",
      censusArea: 635.453,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.265846, 47.587618],
          [-98.500085, 47.587228],
          [-98.499246, 47.326538],
          [-99.250392, 47.326894],
          [-99.26628, 47.327194],
          [-99.265846, 47.587618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38035",
      state: "38",
      county: "035",
      name: "Grand Forks County",
      censusArea: 1436.376,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.903434, 48.194897],
          [-97.147401, 48.193779],
          [-97.147498, 48.173227],
          [-97.14751, 48.170572],
          [-97.123135, 48.109497],
          [-97.098275, 48.07131],
          [-97.088158, 48.059515],
          [-97.023156, 47.873978],
          [-97.000356, 47.860915],
          [-96.980391, 47.815662],
          [-96.890671, 47.672153],
          [-97.472745, 47.672051],
          [-97.882405, 47.672179],
          [-97.882179, 48.020803],
          [-97.903434, 48.194897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38045",
      state: "38",
      county: "045",
      name: "La Moure County",
      censusArea: 1145.943,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.034573, 46.282796],
          [-99.003118, 46.282898],
          [-99.037367, 46.283151],
          [-99.036639, 46.630575],
          [-98.439056, 46.63112],
          [-98.033862, 46.630727],
          [-98.034573, 46.282796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38061",
      state: "38",
      county: "061",
      name: "Mountrail County",
      censusArea: 1825.298,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.385758, 47.758008],
          [-102.561965, 47.780111],
          [-102.642266, 47.823713],
          [-102.644764, 47.906715],
          [-102.552981, 47.998646],
          [-102.663487, 48.076651],
          [-102.828477, 48.123664],
          [-102.828061, 48.37292],
          [-102.885737, 48.372578],
          [-102.885763, 48.54639],
          [-102.233892, 48.546339],
          [-101.973322, 48.547133],
          [-101.973399, 48.372095],
          [-101.922237, 48.37233],
          [-101.922123, 48.023173],
          [-101.871537, 48.023176],
          [-101.871862, 47.847589],
          [-102.385565, 47.84775],
          [-102.385758, 47.758008]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38073",
      state: "38",
      county: "073",
      name: "Ransom County",
      censusArea: 862.357,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.006715, 46.282626],
          [-98.029816, 46.282636],
          [-98.034573, 46.282796],
          [-98.033862, 46.630727],
          [-97.682006, 46.629928],
          [-97.279948, 46.629348],
          [-97.280568, 46.282321],
          [-98.006715, 46.282626]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38079",
      state: "38",
      county: "079",
      name: "Rolette County",
      censusArea: 903.079,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.5257, 48.999423],
          [-99.525895, 48.718384],
          [-99.493002, 48.718359],
          [-99.493189, 48.544834],
          [-100.145858, 48.545211],
          [-100.145494, 48.718875],
          [-100.183034, 48.718909],
          [-100.182707, 48.99923],
          [-99.91378, 48.999049],
          [-99.913705, 48.999049],
          [-99.861488, 48.999156],
          [-99.861454, 48.999202],
          [-99.5257, 48.999423]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38091",
      state: "38",
      county: "091",
      name: "Steele County",
      censusArea: 712.215,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.706034, 47.239978],
          [-97.961208, 47.240512],
          [-97.986585, 47.3261],
          [-97.98481, 47.6723],
          [-97.882405, 47.672179],
          [-97.472745, 47.672051],
          [-97.474337, 47.324776],
          [-97.451512, 47.239061],
          [-97.706034, 47.239978]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38103",
      state: "38",
      county: "103",
      name: "Wells County",
      censusArea: 1271.047,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.265846, 47.587618],
          [-99.26628, 47.327194],
          [-99.480726, 47.327076],
          [-100.033292, 47.327192],
          [-100.032849, 47.673621],
          [-100.069514, 47.67365],
          [-100.069206, 47.847301],
          [-99.812167, 47.847257],
          [-99.297204, 47.846811],
          [-99.297318, 47.673004],
          [-99.265846, 47.587618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39003",
      state: "39",
      county: "003",
      name: "Allen County",
      censusArea: 402.496,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.340531, 40.859099],
          [-84.109586, 40.860994],
          [-84.109516, 40.90473],
          [-83.880423, 40.920429],
          [-83.880063, 40.819919],
          [-83.880194, 40.64469],
          [-84.107787, 40.643069],
          [-84.222799, 40.685957],
          [-84.396778, 40.684926],
          [-84.397374, 40.815941],
          [-84.340531, 40.859099]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39015",
      state: "39",
      county: "015",
      name: "Brown County",
      censusArea: 490.016,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.903918, 38.76815],
          [-83.903971, 38.76816],
          [-83.943978, 38.783616],
          [-83.962123, 38.787384],
          [-83.978814, 38.787104],
          [-84.044486, 38.770572],
          [-84.052707, 38.770542],
          [-83.995284, 39.228189],
          [-83.99312, 39.254235],
          [-83.865679, 39.247333],
          [-83.872214, 39.021304],
          [-83.673018, 39.02043],
          [-83.705311, 38.639853],
          [-83.706043, 38.637101],
          [-83.762445, 38.652103],
          [-83.769347, 38.65522],
          [-83.773682, 38.661309],
          [-83.777823, 38.674453],
          [-83.85333, 38.752572],
          [-83.859028, 38.756793],
          [-83.870443, 38.761608],
          [-83.903918, 38.76815]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39027",
      state: "39",
      county: "027",
      name: "Clinton County",
      censusArea: 408.684,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.988613, 39.444397],
          [-83.977005, 39.569169],
          [-83.670196, 39.550254],
          [-83.576286, 39.544547],
          [-83.590878, 39.378736],
          [-83.784793, 39.262888],
          [-83.865679, 39.247333],
          [-83.99312, 39.254235],
          [-84.006782, 39.255068],
          [-83.988613, 39.444397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39039",
      state: "39",
      county: "039",
      name: "Defiance County",
      censusArea: 411.46,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.803926, 41.367959],
          [-84.804133, 41.408292],
          [-84.804046, 41.408361],
          [-84.804015, 41.411655],
          [-84.803956, 41.426044],
          [-84.341664, 41.427568],
          [-84.228454, 41.42781],
          [-84.228222, 41.165862],
          [-84.341602, 41.165524],
          [-84.341878, 41.209197],
          [-84.457059, 41.209434],
          [-84.457217, 41.253485],
          [-84.803492, 41.252562],
          [-84.80358, 41.270942],
          [-84.803581, 41.271079],
          [-84.803582, 41.271273],
          [-84.803926, 41.367959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39047",
      state: "39",
      county: "047",
      name: "Fayette County",
      censusArea: 406.357,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.266737, 39.516249],
          [-83.317496, 39.449993],
          [-83.372714, 39.377416],
          [-83.375389, 39.377521],
          [-83.590878, 39.378736],
          [-83.576286, 39.544547],
          [-83.670196, 39.550254],
          [-83.65333, 39.716876],
          [-83.252435, 39.695438],
          [-83.266737, 39.516249]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35015",
      state: "35",
      county: "015",
      name: "Eddy County",
      censusArea: 4175.726,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.024521, 32.00001],
          [-104.531756, 32.000117],
          [-104.531937, 32.000311],
          [-104.640918, 32.000396],
          [-104.643526, 32.000443],
          [-104.847757, 32.000482],
          [-104.851525, 32.520505],
          [-104.842026, 32.520705],
          [-104.841229, 32.963303],
          [-103.828855, 32.96546],
          [-103.8146, 32.965203],
          [-103.813959, 32.522464],
          [-103.722938, 32.522587],
          [-103.722853, 32.000221],
          [-103.980179, 32.000125],
          [-104.024521, 32.00001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35027",
      state: "35",
      county: "027",
      name: "Lincoln County",
      censusArea: 4831.091,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.312968, 34.34672],
          [-104.89185, 34.346989],
          [-104.893141, 34.088304],
          [-104.885186, 33.306267],
          [-104.90382, 33.306184],
          [-104.905762, 33.138626],
          [-105.316882, 33.132218],
          [-105.319045, 33.30509],
          [-105.725957, 33.304834],
          [-105.727343, 33.390524],
          [-106.345851, 33.390815],
          [-106.372235, 33.390883],
          [-106.373082, 33.480116],
          [-106.373929, 33.660411],
          [-106.052794, 33.650301],
          [-106.052804, 33.825862],
          [-105.924667, 33.82559],
          [-105.923959, 34.259991],
          [-105.313218, 34.260015],
          [-105.312968, 34.34672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35028",
      state: "35",
      county: "028",
      name: "Los Alamos County",
      censusArea: 109.167,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.24825, 35.755739],
          [-106.4007, 35.807567],
          [-106.399644, 35.960055],
          [-106.247949, 35.966335],
          [-106.248096, 35.959386],
          [-106.245466, 35.959346],
          [-106.245439, 35.93093],
          [-106.248257, 35.839178],
          [-106.171656, 35.828176],
          [-106.24825, 35.755739]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35045",
      state: "35",
      county: "045",
      name: "San Juan County",
      censusArea: 5513.067,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.62425, 36.219799],
          [-107.62651, 36.00032],
          [-109.045973, 36.002338],
          [-109.045433, 36.874589],
          [-109.045407, 36.874998],
          [-109.045272, 36.968871],
          [-109.045244, 36.969489],
          [-109.045223, 36.999084],
          [-108.958868, 36.998913],
          [-108.954404, 36.998906],
          [-108.620309, 36.999287],
          [-108.619689, 36.999249],
          [-108.379203, 36.999459],
          [-108.320721, 36.99951],
          [-108.320464, 36.999499],
          [-108.2884, 36.99952],
          [-108.288086, 36.999555],
          [-107.481737, 36.999973],
          [-107.420915, 37.000005],
          [-107.451445, 36.904036],
          [-107.605517, 36.798039],
          [-107.618348, 36.804739],
          [-107.62425, 36.219799]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35053",
      state: "35",
      county: "053",
      name: "Socorro County",
      censusArea: 6646.679,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.20441, 34.578813],
          [-106.882561, 34.579457],
          [-106.77306, 34.53329],
          [-106.41699, 34.439797],
          [-106.415861, 34.259715],
          [-105.923959, 34.259991],
          [-105.924667, 33.82559],
          [-106.052804, 33.825862],
          [-106.052794, 33.650301],
          [-106.373929, 33.660411],
          [-106.373082, 33.480116],
          [-106.474295, 33.475786],
          [-107.712713, 33.476892],
          [-107.716301, 34.259856],
          [-107.725461, 34.259775],
          [-107.724854, 34.57806],
          [-107.20441, 34.578813]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36003",
      state: "36",
      county: "003",
      name: "Allegany County",
      censusArea: 1029.308,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.206606, 41.999989],
          [-78.308128, 41.999415],
          [-78.308839, 42.521217],
          [-78.040241, 42.520968],
          [-78.038261, 42.521522],
          [-77.840901, 42.517767],
          [-77.840231, 42.474576],
          [-77.722964, 42.471216],
          [-77.749931, 41.998782],
          [-77.822799, 41.998547],
          [-77.83203, 41.998524],
          [-78.030963, 41.999392],
          [-78.031177, 41.999415],
          [-78.12473, 42.000452],
          [-78.206606, 41.999989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36021",
      state: "36",
      county: "021",
      name: "Columbia County",
      censusArea: 634.705,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.929626, 42.078778],
          [-73.921465, 42.110025],
          [-73.910675, 42.127293],
          [-73.789502, 42.267738],
          [-73.783721, 42.464231],
          [-73.352527, 42.510002],
          [-73.410647, 42.351738],
          [-73.508142, 42.086257],
          [-73.496879, 42.049675],
          [-73.527072, 41.97798],
          [-73.71093, 42.005488],
          [-73.929626, 42.078778]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36037",
      state: "36",
      county: "037",
      name: "Genesee County",
      censusArea: 492.936,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.464381, 42.867461],
          [-78.463887, 42.924325],
          [-78.464449, 43.088703],
          [-78.464306, 43.091514],
          [-78.465505, 43.128619],
          [-78.410876, 43.130643],
          [-77.99729, 43.132981],
          [-77.905934, 43.133561],
          [-77.951044, 43.039544],
          [-77.909832, 42.987762],
          [-77.954964, 42.862754],
          [-78.464381, 42.867461]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36043",
      state: "36",
      county: "043",
      name: "Herkimer County",
      censusArea: 1411.47,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.212158, 42.879973],
          [-75.219106, 43.052469],
          [-75.069165, 43.227333],
          [-75.16035, 43.255805],
          [-75.076581, 43.330705],
          [-75.086851, 43.41701],
          [-75.11016, 43.615229],
          [-75.170159, 44.096959],
          [-75.062779, 44.0504],
          [-74.854171, 44.070089],
          [-74.775617, 43.486677],
          [-74.867712, 43.339826],
          [-74.712615, 43.286143],
          [-74.696333, 43.173515],
          [-74.759895, 43.047423],
          [-74.763303, 42.863237],
          [-74.878822, 42.898274],
          [-74.906738, 42.824943],
          [-75.100999, 42.908363],
          [-75.13987, 42.85976],
          [-75.212158, 42.879973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36057",
      state: "36",
      county: "057",
      name: "Montgomery County",
      censusArea: 403.043,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.289304, 42.984415],
          [-74.097467, 42.982934],
          [-74.093814, 42.959378],
          [-74.09298, 42.955868],
          [-74.083883, 42.897354],
          [-74.263314, 42.796534],
          [-74.454911, 42.772979],
          [-74.648298, 42.829558],
          [-74.650213, 42.829941],
          [-74.702054, 42.845305],
          [-74.763303, 42.863237],
          [-74.759895, 43.047423],
          [-74.542367, 42.98553],
          [-74.488844, 42.985118],
          [-74.289304, 42.984415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36069",
      state: "36",
      county: "069",
      name: "Ontario County",
      censusArea: 644.065,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.971392, 42.764223],
          [-77.018626, 42.7638],
          [-77.313004, 42.761265],
          [-77.367106, 42.667866],
          [-77.366505, 42.576368],
          [-77.490889, 42.577288],
          [-77.486875, 42.670279],
          [-77.598815, 42.671965],
          [-77.61167, 42.763169],
          [-77.580377, 42.943963],
          [-77.482517, 42.943164],
          [-77.485418, 43.034564],
          [-77.371478, 43.034696],
          [-77.134335, 43.039926],
          [-77.133397, 43.012463],
          [-76.963926, 43.013157],
          [-76.96335, 42.90302],
          [-76.971392, 42.764223]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36083",
      state: "36",
      county: "083",
      name: "Rensselaer County",
      censusArea: 652.431,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.659663, 42.818978],
          [-73.68461, 42.892399],
          [-73.635463, 42.94129],
          [-73.274294, 42.943652],
          [-73.274393, 42.942482],
          [-73.274466, 42.940361],
          [-73.278673, 42.83341],
          [-73.285388, 42.834093],
          [-73.290944, 42.80192],
          [-73.276421, 42.746019],
          [-73.264957, 42.74594],
          [-73.307004, 42.632653],
          [-73.352527, 42.510002],
          [-73.783721, 42.464231],
          [-73.784594, 42.489947],
          [-73.773161, 42.509377],
          [-73.761265, 42.610379],
          [-73.676762, 42.783277],
          [-73.673463, 42.790276],
          [-73.672355, 42.795791],
          [-73.661362, 42.802977],
          [-73.659663, 42.818978]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36095",
      state: "36",
      county: "095",
      name: "Schoharie County",
      censusArea: 621.819,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.443506, 42.355017],
          [-74.618895, 42.424389],
          [-74.71158, 42.517799],
          [-74.630631, 42.626674],
          [-74.667512, 42.75071],
          [-74.648298, 42.829558],
          [-74.454911, 42.772979],
          [-74.263314, 42.796534],
          [-74.272295, 42.71427],
          [-74.180274, 42.729979],
          [-74.169725, 42.667426],
          [-74.241572, 42.550802],
          [-74.254303, 42.408207],
          [-74.244692, 42.377159],
          [-74.443506, 42.355017]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36109",
      state: "36",
      county: "109",
      name: "Tompkins County",
      censusArea: 474.649,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.253359, 42.407568],
          [-76.250149, 42.296676],
          [-76.39465, 42.318509],
          [-76.415305, 42.318368],
          [-76.416199, 42.262976],
          [-76.538349, 42.281755],
          [-76.561601, 42.281986],
          [-76.619303, 42.282853],
          [-76.691406, 42.284307],
          [-76.696655, 42.54679],
          [-76.585989, 42.54991],
          [-76.666543, 42.623457],
          [-76.265584, 42.623588],
          [-76.253359, 42.407568]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36121",
      state: "36",
      county: "121",
      name: "Wyoming County",
      censusArea: 592.746,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.46394, 42.536332],
          [-78.464381, 42.867461],
          [-77.954964, 42.862754],
          [-77.956334, 42.667322],
          [-78.048247, 42.579306],
          [-78.038261, 42.521522],
          [-78.040241, 42.520968],
          [-78.308839, 42.521217],
          [-78.464556, 42.519166],
          [-78.46394, 42.536332]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36123",
      state: "36",
      county: "123",
      name: "Yates County",
      censusArea: 338.143,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.366505, 42.576368],
          [-77.367106, 42.667866],
          [-77.313004, 42.761265],
          [-77.018626, 42.7638],
          [-76.971392, 42.764223],
          [-76.895349, 42.656255],
          [-76.895596, 42.541537],
          [-76.889805, 42.463054],
          [-77.107203, 42.483771],
          [-77.143795, 42.576869],
          [-77.366505, 42.576368]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42013",
      state: "42",
      county: "013",
      name: "Blair County",
      censusArea: 525.8,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.542303, 40.468851],
          [-78.35043, 40.724827],
          [-78.359912, 40.732592],
          [-78.131834, 40.743017],
          [-78.116521, 40.739127],
          [-78.22197, 40.674539],
          [-78.197083, 40.558444],
          [-78.130081, 40.48199],
          [-78.257305, 40.297883],
          [-78.288764, 40.242107],
          [-78.434288, 40.302301],
          [-78.447895, 40.244395],
          [-78.620383, 40.327025],
          [-78.564085, 40.370609],
          [-78.542303, 40.468851]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42015",
      state: "42",
      county: "015",
      name: "Bradford County",
      censusArea: 1147.399,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.874714, 41.596919],
          [-76.878699, 41.620344],
          [-76.883511, 41.666314],
          [-76.893201, 41.748463],
          [-76.927084, 42.001674],
          [-76.921884, 42.001674],
          [-76.920784, 42.001774],
          [-76.835079, 42.001773],
          [-76.815878, 42.001673],
          [-76.558118, 42.000155],
          [-76.557624, 42.000149],
          [-76.46654, 41.999025],
          [-76.462155, 41.998934],
          [-76.349898, 41.99841],
          [-76.343722, 41.998346],
          [-76.145519, 41.998913],
          [-76.115172, 41.651825],
          [-76.197998, 41.647823],
          [-76.220139, 41.541285],
          [-76.813731, 41.590034],
          [-76.874714, 41.596919]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42021",
      state: "42",
      county: "021",
      name: "Cambria County",
      censusArea: 688.351,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.768593, 40.243441],
          [-78.884862, 40.244631],
          [-78.927601, 40.285041],
          [-79.055983, 40.285089],
          [-78.974649, 40.395972],
          [-78.922748, 40.48377],
          [-78.896938, 40.53316],
          [-78.860105, 40.604665],
          [-78.846656, 40.635638],
          [-78.801697, 40.724539],
          [-78.712747, 40.72435],
          [-78.513712, 40.725968],
          [-78.35043, 40.724827],
          [-78.542303, 40.468851],
          [-78.564085, 40.370609],
          [-78.620383, 40.327025],
          [-78.658409, 40.242933],
          [-78.760311, 40.243171],
          [-78.768593, 40.243441]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42031",
      state: "42",
      county: "031",
      name: "Clarion County",
      censusArea: 600.834,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.604161, 41.321478],
          [-79.47814, 41.335708],
          [-79.477822, 41.386479],
          [-79.400269, 41.436145],
          [-79.207196, 41.430922],
          [-79.208878, 41.331861],
          [-79.215214, 41.050515],
          [-79.284998, 41.009295],
          [-79.618352, 40.974142],
          [-79.690711, 41.170691],
          [-79.69235, 41.172028],
          [-79.694984, 41.172865],
          [-79.604161, 41.321478]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42047",
      state: "42",
      county: "047",
      name: "Elk County",
      censusArea: 827.358,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.956042, 41.623402],
          [-78.956056, 41.623863],
          [-78.483409, 41.629898],
          [-78.419118, 41.602188],
          [-78.420028, 41.405046],
          [-78.253959, 41.404721],
          [-78.236817, 41.230475],
          [-78.646666, 41.253778],
          [-78.710248, 41.201859],
          [-78.73528, 41.262915],
          [-78.974478, 41.375476],
          [-79.095096, 41.340658],
          [-79.06352, 41.453207],
          [-78.958957, 41.437061],
          [-78.956042, 41.623402]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42063",
      state: "42",
      county: "063",
      name: "Indiana County",
      censusArea: 827.03,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.846656, 40.635638],
          [-78.860105, 40.604665],
          [-78.896938, 40.53316],
          [-78.922748, 40.48377],
          [-78.974649, 40.395972],
          [-79.058481, 40.380868],
          [-79.192722, 40.414862],
          [-79.199233, 40.41078],
          [-79.206513, 40.415032],
          [-79.214783, 40.424458],
          [-79.233203, 40.430741],
          [-79.240175, 40.429991],
          [-79.247568, 40.43208],
          [-79.250605, 40.428726],
          [-79.454011, 40.485507],
          [-79.450176, 40.530149],
          [-79.251517, 40.737758],
          [-79.215215, 40.775942],
          [-79.21531, 40.911346],
          [-78.805167, 40.90598],
          [-78.806363, 40.845035],
          [-78.801697, 40.724539],
          [-78.846656, 40.635638]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42069",
      state: "42",
      county: "069",
      name: "Lackawanna County",
      censusArea: 459.078,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.687244, 41.339216],
          [-75.741539, 41.348179],
          [-75.760518, 41.360942],
          [-75.76786, 41.365841],
          [-75.795286, 41.378127],
          [-75.832631, 41.399934],
          [-75.834695, 41.427246],
          [-75.719887, 41.642263],
          [-75.469259, 41.64141],
          [-75.462411, 41.641587],
          [-75.441421, 41.260053],
          [-75.505607, 41.232539],
          [-75.600736, 41.161497],
          [-75.649257, 41.190951],
          [-75.687244, 41.339216]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42087",
      state: "42",
      county: "087",
      name: "Mifflin County",
      censusArea: 411.031,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.667819, 40.735335],
          [-77.36418, 40.846937],
          [-77.357113, 40.844484],
          [-77.356628, 40.807334],
          [-77.354097, 40.701667],
          [-77.287941, 40.693595],
          [-77.323053, 40.683082],
          [-77.36069, 40.669991],
          [-77.49776, 40.609484],
          [-77.611315, 40.515017],
          [-77.75212, 40.378545],
          [-77.913844, 40.398679],
          [-77.816513, 40.500074],
          [-77.841827, 40.550035],
          [-77.68708, 40.67675],
          [-77.681334, 40.729758],
          [-77.667819, 40.735335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42093",
      state: "42",
      county: "093",
      name: "Montour County",
      censusArea: 130.242,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.640767, 41.155718],
          [-76.618971, 41.063759],
          [-76.514951, 40.958959],
          [-76.528034, 40.882515],
          [-76.557013, 40.938617],
          [-76.661348, 40.967996],
          [-76.792878, 40.946583],
          [-76.732672, 41.17204],
          [-76.678776, 41.154172],
          [-76.640767, 41.155718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42105",
      state: "42",
      county: "105",
      name: "Potter County",
      censusArea: 1081.323,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.599278, 41.542271],
          [-77.598129, 41.478576],
          [-77.989194, 41.474822],
          [-78.050442, 41.475462],
          [-78.203422, 41.618157],
          [-78.206606, 41.999989],
          [-78.12473, 42.000452],
          [-78.031177, 41.999415],
          [-78.030963, 41.999392],
          [-77.83203, 41.998524],
          [-77.822799, 41.998547],
          [-77.749931, 41.998782],
          [-77.610028, 41.999519],
          [-77.599278, 41.542271]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42113",
      state: "42",
      county: "113",
      name: "Sullivan County",
      censusArea: 449.94,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.447597, 41.275629],
          [-76.592532, 41.304332],
          [-76.749497, 41.405572],
          [-76.813731, 41.590034],
          [-76.220139, 41.541285],
          [-76.223866, 41.524544],
          [-76.2831, 41.376517],
          [-76.284865, 41.372724],
          [-76.310133, 41.310199],
          [-76.407934, 41.308418],
          [-76.447597, 41.275629]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42123",
      state: "42",
      county: "123",
      name: "Warren County",
      censusArea: 884.135,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.402653, 41.625968],
          [-79.51207, 41.624559],
          [-79.612873, 41.623858],
          [-79.612598, 41.77503],
          [-79.612072, 41.850056],
          [-79.610839, 41.998989],
          [-79.551385, 41.998666],
          [-79.538445, 41.998527],
          [-79.061265, 41.999259],
          [-79.052473, 41.999179],
          [-78.918854, 41.997961],
          [-78.948689, 41.83048],
          [-78.956056, 41.623863],
          [-78.956042, 41.623402],
          [-79.402653, 41.625968]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45005",
      state: "45",
      county: "005",
      name: "Allendale County",
      censusArea: 408.09,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.082291, 33.02663],
          [-81.236956, 32.940244],
          [-81.261662, 32.839139],
          [-81.405134, 32.744964],
          [-81.404949, 32.747027],
          [-81.423772, 32.810514],
          [-81.49983, 32.963816],
          [-81.494736, 32.978998],
          [-81.491495, 32.998572],
          [-81.496637, 33.010224],
          [-81.51169, 33.024506],
          [-81.541831, 33.045654],
          [-81.615756, 33.092534],
          [-81.541881, 33.158279],
          [-81.364857, 33.108061],
          [-81.193099, 33.118668],
          [-81.082291, 33.02663]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45009",
      state: "45",
      county: "009",
      name: "Bamberg County",
      censusArea: 393.37,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.22011, 33.439493],
          [-80.948756, 33.308047],
          [-80.797912, 33.176944],
          [-81.082291, 33.02663],
          [-81.193099, 33.118668],
          [-81.227804, 33.160697],
          [-81.222673, 33.44],
          [-81.22155, 33.439044],
          [-81.22011, 33.439493]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45027",
      state: "45",
      county: "027",
      name: "Clarendon County",
      censusArea: 606.936,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.534085, 33.643911],
          [-80.478709, 33.714265],
          [-80.392003, 33.71555],
          [-80.397131, 33.767821],
          [-80.255904, 33.803708],
          [-79.974601, 33.946527],
          [-79.945584, 33.885717],
          [-79.87562, 33.885245],
          [-79.995638, 33.767906],
          [-79.974382, 33.72159],
          [-80.101697, 33.496891],
          [-80.222267, 33.443716],
          [-80.354539, 33.430408],
          [-80.496273, 33.558481],
          [-80.534085, 33.643911]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45039",
      state: "45",
      county: "039",
      name: "Fairfield County",
      censusArea: 686.277,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.825921, 34.26876],
          [-81.095863, 34.213872],
          [-81.178293, 34.174552],
          [-81.316911, 34.239458],
          [-81.423241, 34.494379],
          [-81.421139, 34.503224],
          [-81.424907, 34.524073],
          [-81.427545, 34.529482],
          [-81.424304, 34.55599],
          [-81.419829, 34.566588],
          [-81.422706, 34.572029],
          [-80.949774, 34.545341],
          [-80.877491, 34.543146],
          [-80.879227, 34.457776],
          [-80.769926, 34.377111],
          [-80.825921, 34.26876]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47005",
      state: "47",
      county: "005",
      name: "Benton County",
      censusArea: 394.142,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.98074, 36.353307],
          [-87.949603, 36.24316],
          [-87.919043, 36.130049],
          [-88.000809, 36.028089],
          [-88.012932, 35.946648],
          [-87.925835, 35.930432],
          [-87.96274, 35.840921],
          [-87.968987, 35.828827],
          [-87.970738, 35.815705],
          [-88.177859, 35.845841],
          [-88.217436, 35.846582],
          [-88.212559, 36.12029],
          [-88.083157, 36.256724],
          [-88.087674, 36.359166],
          [-87.99092, 36.360133],
          [-87.988562, 36.359746],
          [-87.982743, 36.356083],
          [-87.98074, 36.353307]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47007",
      state: "47",
      county: "007",
      name: "Bledsoe County",
      censusArea: 406.425,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.423804, 35.567422],
          [-85.265387, 35.721237],
          [-85.254062, 35.765611],
          [-84.916062, 35.761939],
          [-85.107762, 35.571359],
          [-85.135191, 35.458649],
          [-85.225877, 35.354276],
          [-85.403298, 35.49228],
          [-85.423804, 35.567422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47013",
      state: "47",
      county: "013",
      name: "Campbell County",
      censusArea: 480.191,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.004317, 36.270378],
          [-84.069918, 36.230378],
          [-84.227527, 36.244879],
          [-84.241825, 36.177578],
          [-84.372731, 36.216778],
          [-84.329927, 36.285086],
          [-84.349939, 36.466292],
          [-84.259593, 36.513985],
          [-84.261333, 36.591981],
          [-84.227295, 36.591685],
          [-83.987842, 36.5896],
          [-83.987612, 36.589595],
          [-83.905638, 36.419979],
          [-84.004317, 36.270378]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47027",
      state: "47",
      county: "027",
      name: "Clay County",
      censusArea: 236.536,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.813885, 36.499473],
          [-85.788613, 36.621845],
          [-85.552017, 36.615782],
          [-85.551483, 36.615727],
          [-85.508605, 36.61502],
          [-85.471338, 36.61638],
          [-85.436418, 36.618194],
          [-85.29599, 36.625488],
          [-85.276289, 36.626511],
          [-85.276284, 36.626511],
          [-85.284878, 36.531293],
          [-85.438808, 36.501102],
          [-85.495422, 36.403074],
          [-85.704075, 36.522395],
          [-85.813885, 36.499473]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47031",
      state: "47",
      county: "031",
      name: "Coffee County",
      censusArea: 428.957,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.876962, 35.524095],
          [-85.913593, 35.289189],
          [-86.158018, 35.362607],
          [-86.261489, 35.333579],
          [-86.25759, 35.412476],
          [-86.245165, 35.631913],
          [-86.207147, 35.70379],
          [-86.143652, 35.661747],
          [-85.985062, 35.660108],
          [-85.876962, 35.524095]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47045",
      state: "47",
      county: "045",
      name: "Dyer County",
      censusArea: 512.327,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.482971, 36.212659],
          [-89.154464, 36.204678],
          [-89.163406, 36.026044],
          [-89.188844, 35.99976],
          [-89.280188, 35.886416],
          [-89.342829, 35.880934],
          [-89.406965, 35.946599],
          [-89.687939, 35.905384],
          [-89.686924, 35.947716],
          [-89.71997, 35.97462],
          [-89.733095, 36.000608],
          [-89.706932, 36.000981],
          [-89.690306, 36.024835],
          [-89.687254, 36.034048],
          [-89.684439, 36.051719],
          [-89.624235, 36.108626],
          [-89.601936, 36.11947],
          [-89.594, 36.12719],
          [-89.591605, 36.144096],
          [-89.594397, 36.155457],
          [-89.607004, 36.171179],
          [-89.618228, 36.179966],
          [-89.629452, 36.185382],
          [-89.482971, 36.212659]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47057",
      state: "47",
      county: "057",
      name: "Grainger County",
      censusArea: 280.6,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.66746, 36.080587],
          [-83.732704, 36.164583],
          [-83.667408, 36.344381],
          [-83.532504, 36.353385],
          [-83.3868, 36.413186],
          [-83.280595, 36.394689],
          [-83.255292, 36.289092],
          [-83.321695, 36.29459],
          [-83.467097, 36.174688],
          [-83.541099, 36.181987],
          [-83.674688, 36.124463],
          [-83.674454, 36.116399],
          [-83.659387, 36.085533],
          [-83.66746, 36.080587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47069",
      state: "47",
      county: "069",
      name: "Hardeman County",
      censusArea: 667.768,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.017128, 34.994649],
          [-89.138997, 34.99433],
          [-89.139136, 34.994307],
          [-89.198288, 34.994484],
          [-89.183944, 35.397126],
          [-89.182551, 35.432816],
          [-89.078876, 35.431428],
          [-88.841607, 35.427826],
          [-88.781768, 35.247587],
          [-88.786924, 35.115809],
          [-88.786612, 34.995252],
          [-88.823049, 34.995157],
          [-89.017128, 34.994649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47081",
      state: "47",
      county: "081",
      name: "Hickman County",
      censusArea: 612.499,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.710185, 35.70861],
          [-87.71676, 35.83875],
          [-87.70696, 35.905884],
          [-87.53453, 35.993074],
          [-87.204242, 35.959186],
          [-87.205665, 35.940967],
          [-87.201426, 35.940579],
          [-87.202092, 35.936075],
          [-87.20507, 35.914639],
          [-87.215099, 35.850651],
          [-87.234143, 35.724691],
          [-87.339232, 35.65911],
          [-87.459587, 35.615124],
          [-87.586867, 35.653304],
          [-87.658189, 35.609349],
          [-87.74375, 35.663952],
          [-87.710185, 35.70861]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47093",
      state: "47",
      county: "093",
      name: "Knox County",
      censusArea: 508.215,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.732704, 36.164583],
          [-83.66746, 36.080587],
          [-83.673297, 36.038486],
          [-83.654605, 35.971937],
          [-83.794275, 35.887125],
          [-83.853457, 35.841138],
          [-83.994677, 35.883749],
          [-84.166267, 35.805106],
          [-84.263383, 35.89694],
          [-84.268393, 35.902563],
          [-84.271587, 35.910165],
          [-84.195151, 35.993008],
          [-84.058585, 36.036304],
          [-84.094011, 36.068911],
          [-83.941711, 36.186378],
          [-83.732704, 36.164583]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47105",
      state: "47",
      county: "105",
      name: "Loudon County",
      censusArea: 229.216,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.166267, 35.805106],
          [-84.133072, 35.660733],
          [-84.188319, 35.610549],
          [-84.423549, 35.671735],
          [-84.52585, 35.624186],
          [-84.525984, 35.628113],
          [-84.534006, 35.629555],
          [-84.535386, 35.633696],
          [-84.547603, 35.641327],
          [-84.54923, 35.636896],
          [-84.557926, 35.636973],
          [-84.557872, 35.629651],
          [-84.566934, 35.629773],
          [-84.566915, 35.63324],
          [-84.584296, 35.633086],
          [-84.584472, 35.644424],
          [-84.263383, 35.89694],
          [-84.166267, 35.805106]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47117",
      state: "47",
      county: "117",
      name: "Marshall County",
      censusArea: 375.46,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.828301, 35.263677],
          [-86.906451, 35.314075],
          [-86.960804, 35.417021],
          [-86.849573, 35.525515],
          [-86.782016, 35.706595],
          [-86.686193, 35.710051],
          [-86.682628, 35.704097],
          [-86.673683, 35.703306],
          [-86.674255, 35.696962],
          [-86.641978, 35.689037],
          [-86.639436, 35.6859],
          [-86.658247, 35.435281],
          [-86.599478, 35.36495],
          [-86.602613, 35.327013],
          [-86.746899, 35.252878],
          [-86.828301, 35.263677]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47129",
      state: "47",
      county: "129",
      name: "Morgan County",
      censusArea: 522.18,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.700785, 36.370824],
          [-84.611042, 36.270377],
          [-84.441034, 36.163478],
          [-84.341812, 36.048977],
          [-84.446054, 35.985984],
          [-84.606659, 35.951145],
          [-84.680633, 35.908454],
          [-84.720727, 35.994914],
          [-84.907753, 36.156293],
          [-84.878553, 36.280204],
          [-84.797861, 36.296894],
          [-84.700785, 36.370824]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47139",
      state: "47",
      county: "139",
      name: "Polk County",
      censusArea: 434.676,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.495529, 35.285472],
          [-84.292321, 35.206677],
          [-84.308437, 35.093173],
          [-84.308576, 35.092761],
          [-84.321869, 34.988408],
          [-84.393935, 34.988068],
          [-84.394903, 34.98803],
          [-84.509052, 34.988033],
          [-84.509886, 34.98801],
          [-84.621483, 34.988329],
          [-84.727434, 34.98802],
          [-84.731022, 34.988088],
          [-84.775852, 34.9878],
          [-84.699937, 35.170974],
          [-84.703607, 35.241043],
          [-84.495529, 35.285472]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47151",
      state: "47",
      county: "151",
      name: "Scott County",
      censusArea: 532.297,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.785341, 36.603372],
          [-84.778455, 36.603222],
          [-84.261333, 36.591981],
          [-84.259593, 36.513985],
          [-84.349939, 36.466292],
          [-84.329927, 36.285086],
          [-84.372731, 36.216778],
          [-84.441034, 36.163478],
          [-84.611042, 36.270377],
          [-84.700785, 36.370824],
          [-84.659082, 36.395227],
          [-84.731839, 36.524674],
          [-84.74994, 36.553523],
          [-84.785399, 36.603374],
          [-84.785341, 36.603372]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47173",
      state: "47",
      county: "173",
      name: "Union County",
      censusArea: 223.549,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.667408, 36.344381],
          [-83.732704, 36.164583],
          [-83.941711, 36.186378],
          [-84.004317, 36.270378],
          [-83.905638, 36.419979],
          [-83.805515, 36.435879],
          [-83.73301, 36.34498],
          [-83.667408, 36.344381]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47179",
      state: "47",
      county: "179",
      name: "Washington County",
      censusArea: 326.465,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.34046, 36.252807],
          [-82.437251, 36.154709],
          [-82.527605, 36.148951],
          [-82.595065, 36.096205],
          [-82.647769, 36.146404],
          [-82.629641, 36.41154],
          [-82.702566, 36.410412],
          [-82.676177, 36.4234],
          [-82.680677, 36.4318],
          [-82.438266, 36.445703],
          [-82.299859, 36.396505],
          [-82.34046, 36.252807]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45049",
      state: "45",
      county: "049",
      name: "Hampton County",
      censusArea: 559.896,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.826309, 32.704321],
          [-80.837033, 32.700038],
          [-80.869705, 32.660935],
          [-80.902448, 32.621561],
          [-81.014625, 32.753058],
          [-81.286776, 32.544831],
          [-81.318255, 32.559722],
          [-81.328753, 32.561228],
          [-81.348155, 32.569294],
          [-81.389258, 32.595385],
          [-81.389338, 32.595436],
          [-81.411906, 32.61841],
          [-81.41866, 32.629392],
          [-81.418431, 32.634704],
          [-81.414761, 32.63744],
          [-81.405134, 32.744964],
          [-81.261662, 32.839139],
          [-81.236956, 32.940244],
          [-81.082291, 33.02663],
          [-80.900196, 32.832813],
          [-80.826309, 32.704321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45053",
      state: "45",
      county: "053",
      name: "Jasper County",
      censusArea: 655.318,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.878111, 32.079792],
          [-80.905378, 32.051943],
          [-80.892344, 32.043764],
          [-80.885517, 32.0346],
          [-81.004115, 32.072225],
          [-81.042833, 32.084508],
          [-81.06185, 32.087935],
          [-81.113334, 32.113205],
          [-81.117234, 32.117605],
          [-81.122034, 32.161803],
          [-81.125457, 32.22713],
          [-81.128034, 32.276297],
          [-81.120333, 32.285796],
          [-81.122333, 32.305395],
          [-81.134332, 32.341693],
          [-81.140932, 32.349393],
          [-81.168032, 32.368391],
          [-81.199029, 32.467286],
          [-81.275415, 32.539457],
          [-81.286776, 32.544831],
          [-81.014625, 32.753058],
          [-80.902448, 32.621561],
          [-80.869705, 32.660935],
          [-80.829489, 32.411836],
          [-80.934935, 32.300369],
          [-81.006432, 32.306196],
          [-81.016341, 32.241923],
          [-80.878111, 32.079792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45075",
      state: "45",
      county: "075",
      name: "Orangeburg County",
      censusArea: 1106.101,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.22011, 33.439493],
          [-81.22155, 33.439044],
          [-81.222673, 33.44],
          [-81.372931, 33.490417],
          [-81.373494, 33.49051],
          [-81.187271, 33.652937],
          [-81.086803, 33.695315],
          [-81.042854, 33.707131],
          [-80.93979, 33.608798],
          [-80.879528, 33.619285],
          [-80.807631, 33.604081],
          [-80.65902, 33.470982],
          [-80.552272, 33.565414],
          [-80.496273, 33.558481],
          [-80.354539, 33.430408],
          [-80.222267, 33.443716],
          [-80.253836, 33.29926],
          [-80.361851, 33.257443],
          [-80.484578, 33.280034],
          [-80.50279, 33.334496],
          [-80.790296, 33.18084],
          [-80.791667, 33.181077],
          [-80.793002, 33.17984],
          [-80.793711, 33.178648],
          [-80.795864, 33.17693],
          [-80.797912, 33.176944],
          [-80.948756, 33.308047],
          [-81.22011, 33.439493]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45081",
      state: "45",
      county: "081",
      name: "Saluda County",
      censusArea: 452.778,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.571485, 33.87723],
          [-81.651759, 33.81451],
          [-81.836803, 33.866501],
          [-81.894595, 33.975415],
          [-82.008298, 33.961646],
          [-81.870084, 34.135357],
          [-81.86064, 34.17933],
          [-81.731614, 34.187613],
          [-81.538899, 34.081744],
          [-81.471972, 34.076552],
          [-81.571485, 33.87723]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46003",
      state: "46",
      county: "003",
      name: "Aurora County",
      censusArea: 708.425,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.331508, 43.937708],
          [-98.331484, 43.850988],
          [-98.325242, 43.851058],
          [-98.320595, 43.498651],
          [-98.705782, 43.499319],
          [-98.714946, 43.499121],
          [-98.794588, 43.499187],
          [-98.807771, 43.935223],
          [-98.331508, 43.937708]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46007",
      state: "46",
      county: "007",
      name: "Bennett County",
      censusArea: 1184.709,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.228104, 42.997874],
          [-101.229203, 42.997854],
          [-101.230325, 42.997899],
          [-101.625424, 42.996238],
          [-101.875424, 42.999298],
          [-102.082486, 42.999356],
          [-102.108892, 43.129258],
          [-102.110819, 43.389902],
          [-101.228179, 43.389185],
          [-101.227749, 43.002087],
          [-101.228104, 42.997874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46017",
      state: "46",
      county: "017",
      name: "Buffalo County",
      censusArea: 471.384,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.576571, 44.192421],
          [-99.30018, 44.19483],
          [-98.925953, 44.196575],
          [-98.926997, 43.935143],
          [-99.350695, 43.933752],
          [-99.355864, 43.934371],
          [-99.353717, 43.995321],
          [-99.562536, 44.101519],
          [-99.576571, 44.192421]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46029",
      state: "46",
      county: "029",
      name: "Codington County",
      censusArea: 688.495,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.882345, 44.97687],
          [-96.88457, 44.804436],
          [-97.491346, 44.804035],
          [-97.494254, 45.151631],
          [-97.226281, 45.151826],
          [-96.883948, 45.150224],
          [-96.882345, 44.97687]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46035",
      state: "46",
      county: "035",
      name: "Davison County",
      censusArea: 435.555,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.965012, 43.49904],
          [-98.114758, 43.498297],
          [-98.320595, 43.498651],
          [-98.325242, 43.851058],
          [-97.967279, 43.850686],
          [-97.967444, 43.849325],
          [-97.965012, 43.49904]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46043",
      state: "46",
      county: "043",
      name: "Douglas County",
      censusArea: 431.802,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.705782, 43.499319],
          [-98.320595, 43.498651],
          [-98.114758, 43.498297],
          [-98.113196, 43.484439],
          [-98.109492, 43.196838],
          [-98.680688, 43.373157],
          [-98.705782, 43.499319]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46055",
      state: "46",
      county: "055",
      name: "Haakon County",
      censusArea: 1810.534,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.136838, 44.747126],
          [-101.154516, 44.689947],
          [-101.166753, 44.168085],
          [-101.046916, 44.168484],
          [-101.046786, 43.99497],
          [-101.064068, 43.994929],
          [-102.007336, 43.99453],
          [-102.001068, 44.510926],
          [-102.000841, 44.511259],
          [-102.000432, 44.511663],
          [-101.889038, 44.538458],
          [-101.811198, 44.522373],
          [-101.715038, 44.579483],
          [-101.531035, 44.567875],
          [-101.359893, 44.689019],
          [-101.235139, 44.690574],
          [-101.136838, 44.747126]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46061",
      state: "46",
      county: "061",
      name: "Hanson County",
      censusArea: 434.513,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.608517, 43.849059],
          [-97.607012, 43.499826],
          [-97.965012, 43.49904],
          [-97.967444, 43.849325],
          [-97.967279, 43.850686],
          [-97.85111, 43.849857],
          [-97.608517, 43.849059]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46087",
      state: "46",
      county: "087",
      name: "Mc Cook County",
      censusArea: 574.204,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.369463, 43.848526],
          [-97.129089, 43.847973],
          [-97.129478, 43.499683],
          [-97.40145, 43.499783],
          [-97.607012, 43.499826],
          [-97.608517, 43.849059],
          [-97.369463, 43.848526]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46095",
      state: "46",
      county: "095",
      name: "Mellette County",
      censusArea: 1307.306,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.33897, 43.716942],
          [-100.230845, 43.713856],
          [-100.214221, 43.390321],
          [-101.228179, 43.389185],
          [-101.228264, 43.788844],
          [-101.064326, 43.84109],
          [-101.031377, 43.857896],
          [-100.795732, 43.754055],
          [-100.789635, 43.725438],
          [-100.735003, 43.707621],
          [-100.58338, 43.768012],
          [-100.473105, 43.720891],
          [-100.33897, 43.716942]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46103",
      state: "46",
      county: "103",
      name: "Pennington County",
      censusArea: 2776.553,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.055104, 43.853478],
          [-104.055077, 43.936535],
          [-104.05495, 43.93809],
          [-104.054562, 44.141081],
          [-103.452453, 44.140772],
          [-102.388045, 44.140183],
          [-102.306223, 44.434085],
          [-102.164179, 44.427466],
          [-102.001068, 44.510926],
          [-102.007336, 43.99453],
          [-102.018887, 43.708618],
          [-102.139158, 43.700948],
          [-102.175231, 43.687756],
          [-102.810419, 43.688132],
          [-102.817114, 43.689658],
          [-102.695526, 43.79815],
          [-102.687192, 43.855415],
          [-104.055104, 43.853478]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46111",
      state: "46",
      county: "111",
      name: "Sanborn County",
      censusArea: 569.321,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.85366, 44.195233],
          [-97.85315, 44.19522],
          [-97.849492, 44.195235],
          [-97.85111, 43.849857],
          [-97.967279, 43.850686],
          [-98.325242, 43.851058],
          [-98.331484, 43.850988],
          [-98.331508, 43.937708],
          [-98.332042, 44.19662],
          [-97.85366, 44.195233]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46125",
      state: "46",
      county: "125",
      name: "Turner County",
      censusArea: 617.057,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.924841, 43.500007],
          [-96.924142, 43.083733],
          [-97.160543, 43.083145],
          [-97.160544, 43.169978],
          [-97.399191, 43.169419],
          [-97.40145, 43.499783],
          [-97.129478, 43.499683],
          [-96.924841, 43.500007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46137",
      state: "46",
      county: "137",
      name: "Ziebach County",
      censusArea: 1961.272,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.99979, 45.472414],
          [-101.47019, 45.472416],
          [-101.486348, 45.38559],
          [-101.500988, 44.993649],
          [-101.136162, 44.994074],
          [-101.136838, 44.747126],
          [-101.235139, 44.690574],
          [-101.359893, 44.689019],
          [-101.531035, 44.567875],
          [-101.715038, 44.579483],
          [-101.811198, 44.522373],
          [-101.889038, 44.538458],
          [-102.000432, 44.511663],
          [-101.999993, 45.037999],
          [-101.99979, 45.472414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40059",
      state: "40",
      county: "059",
      name: "Harper County",
      censusArea: 1039.017,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.002563, 37.001706],
          [-100.001286, 37.001699],
          [-99.995201, 37.001631],
          [-99.786016, 37.000931],
          [-99.774816, 37.000841],
          [-99.774255, 37.000837],
          [-99.657658, 37.000197],
          [-99.625399, 36.999671],
          [-99.558068, 36.999528],
          [-99.541116, 36.999573],
          [-99.508574, 36.999658],
          [-99.504093, 36.999648],
          [-99.502665, 36.999645],
          [-99.500395, 36.999637],
          [-99.500395, 36.999576],
          [-99.484333, 36.999626],
          [-99.456203, 36.999471],
          [-99.372996, 36.87861],
          [-99.295186, 36.819566],
          [-99.295257, 36.595123],
          [-99.605565, 36.592753],
          [-100.00316, 36.59325],
          [-100.002866, 37.000029],
          [-100.002563, 37.001706]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40063",
      state: "40",
      county: "063",
      name: "Hughes County",
      censusArea: 804.647,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.490786, 34.910585],
          [-96.490434, 35.115858],
          [-96.441401, 35.11577],
          [-96.441368, 35.290122],
          [-95.981376, 35.289899],
          [-95.98371, 35.151557],
          [-96.088843, 35.049863],
          [-96.092001, 34.767479],
          [-96.406483, 34.76759],
          [-96.406187, 34.908371],
          [-96.490786, 34.910585]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40073",
      state: "40",
      county: "073",
      name: "Kingfisher County",
      censusArea: 898.161,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.103904, 36.164877],
          [-97.675617, 36.164663],
          [-97.674026, 35.72597],
          [-98.207104, 35.72514],
          [-98.210544, 36.16489],
          [-98.103904, 36.164877]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40081",
      state: "40",
      county: "081",
      name: "Lincoln County",
      censusArea: 952.314,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.620648, 35.639005],
          [-96.624865, 35.462706],
          [-97.14149, 35.463977],
          [-97.141072, 35.724441],
          [-97.140583, 35.941991],
          [-96.621097, 35.941519],
          [-96.620648, 35.639005]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40087",
      state: "40",
      county: "087",
      name: "Mcclain County",
      censusArea: 570.701,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.671595, 35.335934],
          [-97.671529, 35.337508],
          [-97.556987, 35.28844],
          [-97.54146, 35.214165],
          [-97.354192, 35.076656],
          [-97.341509, 34.964001],
          [-97.142349, 34.928176],
          [-97.01593, 34.907348],
          [-96.930586, 34.964364],
          [-96.930872, 34.854632],
          [-96.932471, 34.854455],
          [-97.668099, 34.855164],
          [-97.671595, 35.335934]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40097",
      state: "40",
      county: "097",
      name: "Mayes County",
      censusArea: 655.386,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.43997, 36.0752],
          [-95.43561, 36.51016],
          [-95.32817, 36.51024],
          [-95.00573, 36.50989],
          [-95.011303, 36.161815],
          [-95.1185, 36.16163],
          [-95.118377, 36.074544],
          [-95.207946, 36.074708],
          [-95.43997, 36.0752]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40109",
      state: "40",
      county: "109",
      name: "Oklahoma County",
      censusArea: 708.819,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.264992, 35.377368],
          [-97.423566, 35.377316],
          [-97.671369, 35.377151],
          [-97.671319, 35.464338],
          [-97.674026, 35.72597],
          [-97.141072, 35.724441],
          [-97.14149, 35.463977],
          [-97.142114, 35.37686],
          [-97.264992, 35.377368]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40123",
      state: "40",
      county: "123",
      name: "Pontotoc County",
      censusArea: 720.435,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.930586, 34.964364],
          [-96.821488, 34.946112],
          [-96.775681, 34.899958],
          [-96.722891, 34.856782],
          [-96.556889, 34.914184],
          [-96.490786, 34.910585],
          [-96.406187, 34.908371],
          [-96.406483, 34.76759],
          [-96.406357, 34.680023],
          [-96.512171, 34.680065],
          [-96.513865, 34.505389],
          [-96.827288, 34.505989],
          [-96.82729, 34.59322],
          [-96.932423, 34.593261],
          [-96.932328, 34.636811],
          [-96.932471, 34.854455],
          [-96.930872, 34.854632],
          [-96.930586, 34.964364]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40127",
      state: "40",
      county: "127",
      name: "Pushmataha County",
      censusArea: 1395.836,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.779376, 34.156699],
          [-95.779425, 34.506556],
          [-95.6717, 34.506799],
          [-95.671976, 34.593852],
          [-95.514315, 34.594153],
          [-95.514514, 34.681142],
          [-95.059616, 34.680736],
          [-94.937752, 34.680877],
          [-94.937605, 34.506831],
          [-95.046746, 34.507229],
          [-95.046585, 34.243671],
          [-95.151233, 34.243906],
          [-95.156521, 34.156965],
          [-95.779376, 34.156699]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40147",
      state: "40",
      county: "147",
      name: "Washington County",
      censusArea: 415.454,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.001171, 36.423686],
          [-96.00081, 36.99886],
          [-95.96427, 36.999094],
          [-95.936992, 36.999268],
          [-95.928122, 36.999245],
          [-95.91018, 36.999336],
          [-95.877151, 36.999304],
          [-95.875257, 36.999302],
          [-95.873944, 36.9993],
          [-95.866899, 36.999261],
          [-95.80798, 36.999124],
          [-95.786762, 36.99931],
          [-95.80982, 36.94193],
          [-95.809617, 36.597476],
          [-95.812449, 36.597516],
          [-95.81268, 36.42356],
          [-96.001171, 36.423686]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40153",
      state: "40",
      county: "153",
      name: "Woodward County",
      censusArea: 1242.399,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.605565, 36.592753],
          [-99.295257, 36.595123],
          [-99.295186, 36.819566],
          [-99.139855, 36.78304],
          [-98.959973, 36.598991],
          [-98.960334, 36.506914],
          [-98.955849, 36.161577],
          [-99.382074, 36.164301],
          [-99.596079, 36.164061],
          [-99.605565, 36.592753]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41017",
      state: "41",
      county: "017",
      name: "Deschutes County",
      censusArea: 3018.186,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.799359, 44.258138],
          [-121.842667, 44.39244],
          [-121.107344, 44.390542],
          [-121.102637, 44.138042],
          [-120.987333, 44.133843],
          [-120.986527, 43.960943],
          [-120.748317, 43.957068],
          [-120.747946, 43.871266],
          [-120.3789, 43.871955],
          [-120.378508, 43.785033],
          [-120.257893, 43.785259],
          [-120.258018, 43.698762],
          [-119.898172, 43.698323],
          [-119.896365, 43.610259],
          [-120.368029, 43.611472],
          [-120.378194, 43.611059],
          [-120.378194, 43.615452],
          [-121.332982, 43.616629],
          [-122.002675, 43.615228],
          [-121.964854, 43.626826],
          [-121.986187, 43.661633],
          [-121.960872, 43.763805],
          [-121.975479, 43.856875],
          [-121.92018, 43.913816],
          [-121.869269, 43.911893],
          [-121.76855, 44.101437],
          [-121.799359, 44.258138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41031",
      state: "41",
      county: "031",
      name: "Jefferson County",
      censusArea: 1780.785,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.794077, 44.68394],
          [-121.75948, 44.82564],
          [-120.371422, 44.821568],
          [-120.385526, 44.563954],
          [-120.827342, 44.562849],
          [-120.827177, 44.476633],
          [-120.988721, 44.476444],
          [-120.98854, 44.390045],
          [-121.107344, 44.390542],
          [-121.842667, 44.39244],
          [-121.794077, 44.68394]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41037",
      state: "41",
      county: "037",
      name: "Lake County",
      censusArea: 8138.984,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.332982, 43.616629],
          [-120.378194, 43.615452],
          [-120.378194, 43.611059],
          [-120.368029, 43.611472],
          [-119.896365, 43.610259],
          [-119.896776, 43.179006],
          [-119.932439, 43.178982],
          [-119.943888, 42.74632],
          [-119.365284, 42.749038],
          [-119.360177, 41.994384],
          [-119.790087, 41.997544],
          [-119.986678, 41.995842],
          [-119.999168, 41.99454],
          [-120.286424, 41.993058],
          [-120.326005, 41.993122],
          [-120.692219, 41.993677],
          [-120.693941, 41.993676],
          [-120.879481, 41.993781],
          [-120.88334, 42.743883],
          [-121.34988, 42.746671],
          [-121.348576, 43.356691],
          [-121.332708, 43.43857],
          [-121.332982, 43.616629]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41053",
      state: "41",
      county: "053",
      name: "Polk County",
      censusArea: 740.787,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.149058, 44.720277],
          [-123.347514, 44.720105],
          [-123.580757, 44.719441],
          [-123.602566, 44.721191],
          [-123.724916, 44.73908],
          [-123.725389, 45.043987],
          [-123.724213, 45.076098],
          [-123.069955, 45.075108],
          [-123.039507, 44.951741],
          [-123.113081, 44.928577],
          [-123.178687, 44.833551],
          [-123.092503, 44.813538],
          [-123.143556, 44.74967],
          [-123.133529, 44.738662],
          [-123.150429, 44.727611],
          [-123.149058, 44.720277]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41069",
      state: "41",
      county: "069",
      name: "Wheeler County",
      censusArea: 1714.749,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.371422, 44.821568],
          [-120.455389, 44.866046],
          [-120.495247, 45.068549],
          [-119.790426, 45.067761],
          [-119.791055, 44.994636],
          [-119.671987, 44.994424],
          [-119.652128, 44.823171],
          [-119.655586, 44.306964],
          [-119.898796, 44.306662],
          [-119.89901, 44.389537],
          [-120.020663, 44.389651],
          [-120.021841, 44.438558],
          [-120.385823, 44.437556],
          [-120.385526, 44.563954],
          [-120.371422, 44.821568]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42005",
      state: "42",
      county: "005",
      name: "Armstrong County",
      censusArea: 653.203,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.21531, 40.911346],
          [-79.215215, 40.775942],
          [-79.251517, 40.737758],
          [-79.450176, 40.530149],
          [-79.56111, 40.564113],
          [-79.638137, 40.665601],
          [-79.692587, 40.669732],
          [-79.69293, 40.669744],
          [-79.692357, 40.672857],
          [-79.692794, 40.674604],
          [-79.692615, 40.688784],
          [-79.692331, 40.716656],
          [-79.689994, 40.741362],
          [-79.690711, 41.170691],
          [-79.618352, 40.974142],
          [-79.284998, 41.009295],
          [-79.215214, 41.050515],
          [-79.21531, 40.911346]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48357",
      state: "48",
      county: "357",
      name: "Ochiltree County",
      censusArea: 917.627,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.936058, 36.499602],
          [-100.918513, 36.499621],
          [-100.884174, 36.499682],
          [-100.88408, 36.499682],
          [-100.859657, 36.499687],
          [-100.85084, 36.4997],
          [-100.824236, 36.499618],
          [-100.824218, 36.499618],
          [-100.80619, 36.499674],
          [-100.806172, 36.499634],
          [-100.802909, 36.499621],
          [-100.802886, 36.499621],
          [-100.761811, 36.499618],
          [-100.761811, 36.49958],
          [-100.724362, 36.49958],
          [-100.724361, 36.499558],
          [-100.708626, 36.499553],
          [-100.708628, 36.499521],
          [-100.657763, 36.499483],
          [-100.657763, 36.4995],
          [-100.648343, 36.499495],
          [-100.648344, 36.499463],
          [-100.592614, 36.499469],
          [-100.592556, 36.499469],
          [-100.592551, 36.499429],
          [-100.583539, 36.499483],
          [-100.583379, 36.499443],
          [-100.578114, 36.499463],
          [-100.578114, 36.499439],
          [-100.546145, 36.499343],
          [-100.546724, 36.056536],
          [-101.085716, 36.057572],
          [-101.085156, 36.499244],
          [-101.052418, 36.499563],
          [-101.045331, 36.49954],
          [-100.977088, 36.499595],
          [-100.954153, 36.499599],
          [-100.936058, 36.499602]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48359",
      state: "48",
      county: "359",
      name: "Oldham County",
      censusArea: 1500.533,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.042377, 35.183156],
          [-103.042497, 35.211862],
          [-103.041554, 35.622487],
          [-102.162809, 35.627515],
          [-102.162808, 35.625044],
          [-102.162752, 35.620035],
          [-102.167471, 35.183233],
          [-103.042377, 35.183156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48367",
      state: "48",
      county: "367",
      name: "Parker County",
      censusArea: 903.478,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.544181, 32.994177],
          [-97.550368, 32.580323],
          [-97.550582, 32.555391],
          [-97.617066, 32.555484],
          [-98.066836, 32.558822],
          [-98.056094, 33.003332],
          [-97.921642, 33.001284],
          [-97.545383, 32.994662],
          [-97.544181, 32.994177]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48379",
      state: "48",
      county: "379",
      name: "Rains County",
      censusArea: 229.452,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.665389, 32.960434],
          [-95.635017, 32.72038],
          [-95.829581, 32.766967],
          [-95.934433, 32.837217],
          [-95.987259, 32.876415],
          [-95.9453, 32.979877],
          [-95.862521, 32.979571],
          [-95.665389, 32.960434]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48381",
      state: "48",
      county: "381",
      name: "Randall County",
      censusArea: 911.544,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.168839, 34.747417],
          [-102.167471, 35.183233],
          [-101.622941, 35.183117],
          [-101.629396, 34.750056],
          [-101.629257, 34.747649],
          [-101.998493, 34.74819],
          [-102.168839, 34.747417]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48393",
      state: "48",
      county: "393",
      name: "Roberts County",
      censusArea: 924.058,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.540158, 35.619296],
          [-101.085935, 35.619102],
          [-101.086068, 35.625267],
          [-101.085735, 36.055276],
          [-101.085716, 36.057572],
          [-100.546724, 36.056536],
          [-100.540221, 36.056491],
          [-100.540158, 35.619296]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48397",
      state: "48",
      county: "397",
      name: "Rockwall County",
      censusArea: 127.036,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.51897, 32.813616],
          [-96.518586, 32.873389],
          [-96.518314, 32.877153],
          [-96.518273, 32.891534],
          [-96.516866, 32.982308],
          [-96.297227, 32.981752],
          [-96.297322, 32.841723],
          [-96.297376, 32.814185],
          [-96.51897, 32.813616]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48411",
      state: "48",
      county: "411",
      name: "San Saba County",
      censusArea: 1135.297,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.439687, 31.029537],
          [-98.445782, 30.921439],
          [-98.964612, 30.921368],
          [-99.092431, 30.921968],
          [-99.092317, 30.941002],
          [-99.090622, 31.460927],
          [-98.991608, 31.484071],
          [-98.967159, 31.435903],
          [-98.8725, 31.444299],
          [-98.854292, 31.413425],
          [-98.773, 31.410926],
          [-98.762589, 31.375282],
          [-98.726094, 31.428278],
          [-98.562739, 31.230582],
          [-98.510341, 31.155666],
          [-98.523459, 31.088886],
          [-98.439687, 31.029537]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48425",
      state: "48",
      county: "425",
      name: "Somervell County",
      censusArea: 186.463,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.864864, 32.087328],
          [-97.940474, 32.221738],
          [-97.945625, 32.233468],
          [-97.78276, 32.316493],
          [-97.615286, 32.318618],
          [-97.615055, 32.203581],
          [-97.864864, 32.087328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48429",
      state: "48",
      county: "429",
      name: "Stephens County",
      censusArea: 896.719,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.575616, 32.954349],
          [-98.576256, 32.515163],
          [-99.09605, 32.514768],
          [-99.096016, 32.957037],
          [-98.950875, 32.956918],
          [-98.575616, 32.954349]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48433",
      state: "48",
      county: "433",
      name: "Stonewall County",
      censusArea: 916.313,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.988827, 32.960121],
          [-100.144224, 32.959978],
          [-100.519208, 32.962926],
          [-100.517449, 33.397866],
          [-99.99098, 33.397404],
          [-99.988827, 32.960121]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48445",
      state: "48",
      county: "445",
      name: "Terry County",
      censusArea: 888.839,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.20852, 32.958956],
          [-102.595023, 32.958831],
          [-102.594836, 33.388489],
          [-102.075929, 33.389586],
          [-102.076214, 32.959702],
          [-102.20852, 32.958956]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48449",
      state: "48",
      county: "449",
      name: "Titus County",
      censusArea: 406.054,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.125451, 33.389454],
          [-94.808783, 33.363636],
          [-94.819828, 32.982886],
          [-94.926546, 33.069287],
          [-95.12613, 33.034581],
          [-95.125451, 33.389454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48459",
      state: "48",
      county: "459",
      name: "Upshur County",
      censusArea: 582.947,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.15341, 32.570115],
          [-95.15211, 32.902641],
          [-94.719942, 32.904502],
          [-94.720065, 32.90045],
          [-94.705177, 32.892062],
          [-94.705878, 32.879177],
          [-94.70214, 32.793088],
          [-94.70179, 32.652209],
          [-94.986935, 32.537246],
          [-95.15341, 32.570115]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48463",
      state: "48",
      county: "463",
      name: "Uvalde County",
      censusArea: 1551.946,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.112098, 29.623263],
          [-100.014188, 29.623495],
          [-99.60313, 29.627181],
          [-99.411817, 29.627514],
          [-99.41388, 29.091335],
          [-100.111406, 29.086318],
          [-100.112098, 29.623263]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48475",
      state: "48",
      county: "475",
      name: "Ward County",
      censusArea: 835.602,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.798939, 31.651783],
          [-102.767246, 31.651714],
          [-102.767688, 31.640496],
          [-102.767365, 31.293803],
          [-102.827087, 31.267909],
          [-103.01104, 31.371306],
          [-103.459426, 31.428494],
          [-103.530927, 31.642284],
          [-103.57489, 31.632662],
          [-103.610887, 31.651802],
          [-103.327538, 31.65142],
          [-102.801793, 31.651306],
          [-102.798939, 31.651783]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48491",
      state: "48",
      county: "491",
      name: "Williamson County",
      censusArea: 1118.303,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.369539, 30.419563],
          [-97.576167, 30.501799],
          [-97.775211, 30.429993],
          [-97.956734, 30.628249],
          [-98.049886, 30.624155],
          [-97.96286, 30.785641],
          [-97.828512, 30.906188],
          [-97.625288, 30.87043],
          [-97.315507, 30.752371],
          [-97.271418, 30.735692],
          [-97.155219, 30.457344],
          [-97.334463, 30.402843],
          [-97.369539, 30.419563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48497",
      state: "48",
      county: "497",
      name: "Wise County",
      censusArea: 904.422,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.487065, 33.433681],
          [-97.383015, 33.43289],
          [-97.383095, 33.43045],
          [-97.39848, 32.990839],
          [-97.414843, 32.992029],
          [-97.544181, 32.994177],
          [-97.545383, 32.994662],
          [-97.921642, 33.001284],
          [-97.918189, 33.433873],
          [-97.500154, 33.434057],
          [-97.487065, 33.433681]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48507",
      state: "48",
      county: "507",
      name: "Zavala County",
      censusArea: 1297.406,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.41388, 29.091335],
          [-99.409021, 28.640566],
          [-100.114336, 28.648122],
          [-100.111406, 29.086318],
          [-99.41388, 29.091335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49011",
      state: "49",
      county: "011",
      name: "Davis County",
      censusArea: 298.778,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.738575, 40.861055],
          [-111.915865, 40.822045],
          [-112.006597, 40.921845],
          [-112.26351, 40.77345],
          [-112.493393, 41.076735],
          [-112.189578, 41.152741],
          [-111.856305, 41.139118],
          [-111.815186, 40.957533],
          [-111.738575, 40.861055]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47185",
      state: "47",
      county: "185",
      name: "White County",
      censusArea: 376.673,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.602876, 35.794996],
          [-85.607594, 35.803246],
          [-85.622195, 35.80181],
          [-85.634845, 35.808616],
          [-85.641655, 35.816717],
          [-85.653072, 35.816431],
          [-85.671427, 35.820708],
          [-85.682602, 35.827246],
          [-85.682095, 35.831254],
          [-85.637327, 35.881824],
          [-85.644604, 36.015053],
          [-85.509323, 36.081937],
          [-85.309363, 36.031592],
          [-85.264206, 35.979154],
          [-85.213115, 35.910379],
          [-85.269359, 35.793388],
          [-85.474213, 35.822656],
          [-85.602876, 35.794996]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47189",
      state: "47",
      county: "189",
      name: "Wilson County",
      censusArea: 570.826,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.153214, 35.954399],
          [-86.310363, 35.978563],
          [-86.417189, 36.041517],
          [-86.515589, 36.10061],
          [-86.594286, 36.245105],
          [-86.543434, 36.320602],
          [-86.451721, 36.335629],
          [-86.298452, 36.313634],
          [-86.283256, 36.3475],
          [-86.174666, 36.328],
          [-86.164045, 36.320899],
          [-86.144659, 36.304502],
          [-86.136803, 36.295859],
          [-86.059706, 36.086024],
          [-86.014849, 35.96106],
          [-86.153214, 35.954399]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48009",
      state: "48",
      county: "009",
      name: "Archer County",
      censusArea: 903.11,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.953088, 33.834],
          [-98.742358, 33.834552],
          [-98.423577, 33.836047],
          [-98.423145, 33.610163],
          [-98.421419, 33.466797],
          [-98.420666, 33.396195],
          [-98.953938, 33.397534],
          [-98.953088, 33.834]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48011",
      state: "48",
      county: "011",
      name: "Armstrong County",
      censusArea: 909.109,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.471562, 34.747462],
          [-101.629257, 34.747649],
          [-101.629396, 34.750056],
          [-101.622941, 35.183117],
          [-101.086281, 35.18214],
          [-101.090749, 34.748246],
          [-101.471562, 34.747462]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48021",
      state: "48",
      county: "021",
      name: "Bastrop County",
      censusArea: 888.15,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.04821, 30.137947],
          [-97.024461, 30.051435],
          [-97.315823, 29.786541],
          [-97.648043, 30.067262],
          [-97.649449, 30.067866],
          [-97.492679, 30.209946],
          [-97.486329, 30.220528],
          [-97.409875, 30.351329],
          [-97.377227, 30.407086],
          [-97.369539, 30.419563],
          [-97.334463, 30.402843],
          [-97.081834, 30.259357],
          [-97.04821, 30.137947]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48031",
      state: "48",
      county: "031",
      name: "Blanco County",
      censusArea: 709.251,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.59167, 30.499875],
          [-98.411462, 30.502095],
          [-98.351041, 30.486096],
          [-98.125556, 30.426186],
          [-98.172977, 30.356312],
          [-98.2976, 30.037994],
          [-98.414018, 29.937557],
          [-98.587897, 30.138954],
          [-98.59167, 30.499875]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48047",
      state: "48",
      county: "047",
      name: "Brooks County",
      censusArea: 943.364,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.232464, 27.262487],
          [-98.058078, 27.260981],
          [-97.985887, 27.209308],
          [-97.985494, 26.780917],
          [-98.32067, 26.783081],
          [-98.422616, 26.783535],
          [-98.417883, 27.055285],
          [-98.466542, 27.054711],
          [-98.492943, 27.235012],
          [-98.523723, 27.265115],
          [-98.232464, 27.262487]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48053",
      state: "48",
      county: "053",
      name: "Burnet County",
      censusArea: 994.258,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.911684, 31.034919],
          [-97.840365, 30.929318],
          [-97.828512, 30.906188],
          [-97.96286, 30.785641],
          [-98.049886, 30.624155],
          [-98.123111, 30.484723],
          [-98.125556, 30.426186],
          [-98.351041, 30.486096],
          [-98.438995, 30.673285],
          [-98.371905, 30.740485],
          [-98.432098, 30.78631],
          [-98.376877, 30.83489],
          [-98.445782, 30.921439],
          [-98.439687, 31.029537],
          [-97.911684, 31.034919]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48063",
      state: "48",
      county: "063",
      name: "Camp County",
      censusArea: 195.826,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.15211, 32.902641],
          [-95.152206, 33.01345],
          [-95.147452, 33.020871],
          [-95.138714, 33.026226],
          [-95.137213, 33.030938],
          [-95.131884, 33.029809],
          [-95.12613, 33.034581],
          [-94.926546, 33.069287],
          [-94.819828, 32.982886],
          [-94.719942, 32.904502],
          [-95.15211, 32.902641]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48069",
      state: "48",
      county: "069",
      name: "Castro County",
      censusArea: 894.434,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.168839, 34.747417],
          [-101.998493, 34.74819],
          [-101.99802, 34.313039],
          [-102.090415, 34.313132],
          [-102.525631, 34.313034],
          [-102.525184, 34.746931],
          [-102.168839, 34.747417]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48079",
      state: "48",
      county: "079",
      name: "Cochran County",
      censusArea: 775.15,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.047346, 33.824675],
          [-102.615447, 33.825121],
          [-102.594836, 33.388489],
          [-103.056655, 33.388416],
          [-103.056655, 33.388438],
          [-103.05261, 33.570599],
          [-103.051664, 33.629489],
          [-103.051363, 33.64195],
          [-103.051535, 33.650487],
          [-103.051087, 33.658186],
          [-103.050532, 33.672408],
          [-103.050148, 33.701971],
          [-103.049096, 33.74627],
          [-103.047346, 33.824675]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48095",
      state: "48",
      county: "095",
      name: "Concho County",
      censusArea: 983.799,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.111234, 31.580267],
          [-99.721698, 31.576759],
          [-99.60185, 31.49195],
          [-99.603223, 31.087296],
          [-100.115216, 31.087994],
          [-100.111234, 31.580267]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48099",
      state: "48",
      county: "099",
      name: "Coryell County",
      censusArea: 1052.066,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.766431, 31.672461],
          [-97.689834, 31.711019],
          [-97.60523, 31.587762],
          [-97.418606, 31.320202],
          [-97.9071, 31.069374],
          [-98.180006, 31.463717],
          [-97.766431, 31.672461]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48103",
      state: "48",
      county: "103",
      name: "Crane County",
      censusArea: 785.069,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.767246, 31.651714],
          [-102.31805, 31.651327],
          [-102.301212, 31.086212],
          [-102.388804, 31.087156],
          [-102.429545, 31.193557],
          [-102.676497, 31.326823],
          [-102.767365, 31.293803],
          [-102.767688, 31.640496],
          [-102.767246, 31.651714]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48115",
      state: "48",
      county: "115",
      name: "Dawson County",
      censusArea: 900.306,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.20852, 32.958956],
          [-102.076214, 32.959702],
          [-101.691284, 32.961838],
          [-101.68874, 32.525222],
          [-102.202703, 32.523271],
          [-102.20852, 32.958956]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48123",
      state: "48",
      county: "123",
      name: "De Witt County",
      censusArea: 908.975,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.574639, 28.8133],
          [-97.75511, 29.007113],
          [-97.613132, 29.109765],
          [-97.240108, 29.384488],
          [-96.976378, 29.104046],
          [-97.305916, 28.864037],
          [-97.41734, 28.925228],
          [-97.574639, 28.8133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48127",
      state: "48",
      county: "127",
      name: "Dimmit County",
      censusArea: 1328.884,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.114336, 28.648122],
          [-99.409021, 28.640566],
          [-99.395736, 28.640784],
          [-99.394177, 28.204627],
          [-100.113719, 28.197819],
          [-100.114336, 28.648122]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48137",
      state: "48",
      county: "137",
      name: "Edwards County",
      censusArea: 2117.858,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.381329, 30.288984],
          [-100.116461, 30.290296],
          [-99.754142, 30.290698],
          [-99.757621, 30.074132],
          [-99.967626, 30.082356],
          [-100.03088, 29.848273],
          [-100.014188, 29.623495],
          [-100.112098, 29.623263],
          [-100.699932, 29.623897],
          [-100.700393, 30.288276],
          [-100.381329, 30.288984]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48149",
      state: "48",
      county: "149",
      name: "Fayette County",
      censusArea: 950.008,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.317893, 29.784658],
          [-97.315823, 29.786541],
          [-97.024461, 30.051435],
          [-97.019163, 30.032],
          [-96.794552, 30.160545],
          [-96.647223, 30.145174],
          [-96.62198, 30.044283],
          [-96.569844, 29.961516],
          [-96.754307, 29.755502],
          [-96.874222, 29.632706],
          [-97.142643, 29.628101],
          [-97.130517, 29.643296],
          [-97.317893, 29.784658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48159",
      state: "48",
      county: "159",
      name: "Franklin County",
      censusArea: 284.391,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.125451, 33.389454],
          [-95.12613, 33.034581],
          [-95.131884, 33.029809],
          [-95.137213, 33.030938],
          [-95.138714, 33.026226],
          [-95.147452, 33.020871],
          [-95.152206, 33.01345],
          [-95.172947, 32.961829],
          [-95.308957, 32.962572],
          [-95.308593, 33.37719],
          [-95.30777, 33.377769],
          [-95.30664, 33.378027],
          [-95.125451, 33.389454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48169",
      state: "48",
      county: "169",
      name: "Garza County",
      censusArea: 893.412,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.03866, 32.970225],
          [-101.173378, 32.963597],
          [-101.557434, 32.961025],
          [-101.556884, 33.39476],
          [-101.038788, 33.397214],
          [-101.03866, 32.970225]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48177",
      state: "48",
      county: "177",
      name: "Gonzales County",
      censusArea: 1066.688,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.240108, 29.384488],
          [-97.613132, 29.109765],
          [-97.728443, 29.221748],
          [-97.858782, 29.35289],
          [-97.840383, 29.376791],
          [-97.633176, 29.649863],
          [-97.597743, 29.630718],
          [-97.317893, 29.784658],
          [-97.130517, 29.643296],
          [-97.142643, 29.628101],
          [-97.204087, 29.580613],
          [-97.240108, 29.384488]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48179",
      state: "48",
      county: "179",
      name: "Gray County",
      censusArea: 925.974,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.085935, 35.619102],
          [-100.540158, 35.619296],
          [-100.538978, 35.183144],
          [-101.086281, 35.18214],
          [-101.085935, 35.619102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48185",
      state: "48",
      county: "185",
      name: "Grimes County",
      censusArea: 787.459,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.804306, 30.24557],
          [-95.804872, 30.245645],
          [-95.842138, 30.243445],
          [-95.871864, 30.241108],
          [-95.872022, 30.241097],
          [-96.093165, 30.225187],
          [-96.085893, 30.288983],
          [-96.15463, 30.330288],
          [-96.147255, 30.38284],
          [-96.079969, 30.43003],
          [-96.121806, 30.443426],
          [-96.187765, 30.598737],
          [-96.16837, 30.822997],
          [-95.863059, 30.864125],
          [-95.83024, 30.630284],
          [-95.804351, 30.3378],
          [-95.804306, 30.24557]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48191",
      state: "48",
      county: "191",
      name: "Hall County",
      censusArea: 883.492,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.415895, 34.747525],
          [-100.417783, 34.313524],
          [-100.498193, 34.313527],
          [-100.500398, 34.314074],
          [-100.51734, 34.314102],
          [-100.946132, 34.312759],
          [-100.944939, 34.748281],
          [-100.540703, 34.747723],
          [-100.415895, 34.747525]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48195",
      state: "48",
      county: "195",
      name: "Hansford County",
      censusArea: 919.81,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.085156, 36.499244],
          [-101.085716, 36.057572],
          [-101.085735, 36.055276],
          [-101.623466, 36.055405],
          [-101.623915, 36.499528],
          [-101.085156, 36.499244]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48217",
      state: "48",
      county: "217",
      name: "Hill County",
      censusArea: 958.864,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.719114, 31.814887],
          [-96.735543, 31.806667],
          [-96.932215, 31.708887],
          [-97.037266, 31.863079],
          [-97.277265, 31.745492],
          [-97.32653, 31.78836],
          [-97.28206, 31.843152],
          [-97.382848, 31.870788],
          [-97.438765, 31.933506],
          [-97.379992, 31.97274],
          [-97.485968, 32.017611],
          [-97.476086, 32.17346],
          [-97.086191, 32.265451],
          [-96.940656, 32.052087],
          [-96.896209, 32.073977],
          [-96.719114, 31.814887]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48227",
      state: "48",
      county: "227",
      name: "Howard County",
      censusArea: 900.791,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.68874, 32.525222],
          [-101.174571, 32.524112],
          [-101.183997, 32.087208],
          [-101.264216, 32.087136],
          [-101.695011, 32.087532],
          [-101.68874, 32.525222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48233",
      state: "48",
      county: "233",
      name: "Hutchinson County",
      censusArea: 887.419,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.086068, 35.625267],
          [-101.622831, 35.624057],
          [-101.623466, 36.055405],
          [-101.085735, 36.055276],
          [-101.086068, 35.625267]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48247",
      state: "48",
      county: "247",
      name: "Jim Hogg County",
      censusArea: 1136.138,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.95423, 26.785694],
          [-98.954669, 27.269397],
          [-98.798087, 27.268012],
          [-98.798323, 27.354029],
          [-98.522293, 27.343284],
          [-98.523723, 27.265115],
          [-98.492943, 27.235012],
          [-98.466542, 27.054711],
          [-98.417883, 27.055285],
          [-98.422616, 26.783535],
          [-98.95423, 26.785694]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48251",
      state: "48",
      county: "251",
      name: "Johnson County",
      censusArea: 724.692,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.476086, 32.17346],
          [-97.516121, 32.133612],
          [-97.615055, 32.203581],
          [-97.615286, 32.318618],
          [-97.617066, 32.555484],
          [-97.550582, 32.555391],
          [-97.086834, 32.549428],
          [-97.086191, 32.265451],
          [-97.476086, 32.17346]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48263",
      state: "48",
      county: "263",
      name: "Kent County",
      censusArea: 902.507,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.65587, 32.963469],
          [-101.03866, 32.970225],
          [-101.038788, 33.397214],
          [-100.517449, 33.397866],
          [-100.519208, 32.962926],
          [-100.65587, 32.963469]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48265",
      state: "48",
      county: "265",
      name: "Kerr County",
      censusArea: 1103.318,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.757621, 30.074132],
          [-99.754142, 30.290698],
          [-99.301718, 30.286653],
          [-99.300988, 30.134298],
          [-98.920147, 30.13829],
          [-98.917725, 29.781398],
          [-99.176988, 29.895063],
          [-99.600862, 29.907662],
          [-99.691215, 29.908582],
          [-99.689879, 30.073948],
          [-99.757621, 30.074132]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48275",
      state: "48",
      county: "275",
      name: "Knox County",
      censusArea: 850.621,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.474498, 33.733849],
          [-99.472444, 33.399023],
          [-99.99098, 33.397404],
          [-99.996434, 33.835967],
          [-99.835143, 33.8358],
          [-99.599863, 33.787634],
          [-99.474498, 33.733849]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48283",
      state: "48",
      county: "283",
      name: "La Salle County",
      censusArea: 1486.691,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.803325, 28.05748],
          [-99.388946, 28.030419],
          [-99.394177, 28.204627],
          [-99.395736, 28.640784],
          [-98.800841, 28.647487],
          [-98.800848, 28.647306],
          [-98.803325, 28.05748]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48297",
      state: "48",
      county: "297",
      name: "Live Oak County",
      censusArea: 1039.696,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.883148, 28.0569],
          [-98.235497, 28.057966],
          [-98.334323, 28.0578],
          [-98.335031, 28.612658],
          [-98.098315, 28.786949],
          [-98.005252, 28.690239],
          [-98.089764, 28.662979],
          [-97.808774, 28.183415],
          [-97.817716, 28.176848],
          [-97.904081, 28.114228],
          [-97.883148, 28.0569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48303",
      state: "48",
      county: "303",
      name: "Lubbock County",
      censusArea: 895.599,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.56358, 33.830451],
          [-101.556884, 33.39476],
          [-102.075929, 33.389586],
          [-102.085733, 33.824675],
          [-101.56358, 33.830451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48309",
      state: "48",
      county: "309",
      name: "Mc Lennan County",
      censusArea: 1037.101,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.801123, 31.522269],
          [-97.278113, 31.279799],
          [-97.343426, 31.244215],
          [-97.418606, 31.320202],
          [-97.60523, 31.587762],
          [-97.580232, 31.598796],
          [-97.277265, 31.745492],
          [-97.037266, 31.863079],
          [-96.932215, 31.708887],
          [-96.801123, 31.522269]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48319",
      state: "48",
      county: "319",
      name: "Mason County",
      censusArea: 928.803,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.964612, 30.921368],
          [-98.964112, 30.500742],
          [-98.964232, 30.498482],
          [-99.303996, 30.499832],
          [-99.484493, 30.499641],
          [-99.483869, 30.710771],
          [-99.484755, 30.940605],
          [-99.092317, 30.941002],
          [-99.092431, 30.921968],
          [-98.964612, 30.921368]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48329",
      state: "48",
      county: "329",
      name: "Midland County",
      censusArea: 900.299,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.775802, 31.651319],
          [-102.287345, 31.651276],
          [-102.287048, 32.086991],
          [-102.211249, 32.0868],
          [-101.776085, 32.086925],
          [-101.775802, 31.651319]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48339",
      state: "48",
      county: "339",
      name: "Montgomery County",
      censusArea: 1041.735,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.096708, 30.167214],
          [-95.264013, 30.032217],
          [-95.505125, 30.145153],
          [-95.551421, 30.170606],
          [-95.65543, 30.108994],
          [-95.80333, 30.090096],
          [-95.804306, 30.24557],
          [-95.804351, 30.3378],
          [-95.83024, 30.630284],
          [-95.598971, 30.509002],
          [-95.359156, 30.504369],
          [-95.308395, 30.40575],
          [-95.165897, 30.344976],
          [-95.096708, 30.167214]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48349",
      state: "48",
      county: "349",
      name: "Navarro County",
      censusArea: 1009.627,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.896209, 32.073977],
          [-96.383082, 32.328851],
          [-96.115693, 32.160915],
          [-96.05478, 32.012526],
          [-96.496713, 31.796191],
          [-96.719114, 31.814887],
          [-96.896209, 32.073977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48353",
      state: "48",
      county: "353",
      name: "Nolan County",
      censusArea: 911.997,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.665353, 32.085407],
          [-100.662215, 32.380297],
          [-100.660626, 32.525312],
          [-100.379054, 32.523798],
          [-100.146543, 32.522793],
          [-100.151911, 32.082638],
          [-100.235137, 32.082372],
          [-100.665353, 32.085407]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51121",
      state: "51",
      county: "121",
      name: "Montgomery County",
      censusArea: 387.014,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.261984, 37.340694],
          [-80.25852, 37.308344],
          [-80.205224, 37.248042],
          [-80.178032, 37.113435],
          [-80.347447, 37.021842],
          [-80.543933, 36.984639],
          [-80.580067, 37.089603],
          [-80.55626, 37.099551],
          [-80.555171, 37.11742],
          [-80.532631, 37.118064],
          [-80.507317, 37.135752],
          [-80.524885, 37.14836],
          [-80.527625, 37.134427],
          [-80.529678, 37.134066],
          [-80.606874, 37.244911],
          [-80.433361, 37.314901],
          [-80.327898, 37.366792],
          [-80.261984, 37.340694]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51125",
      state: "51",
      county: "125",
      name: "Nelson County",
      censusArea: 470.856,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.171926, 37.803135],
          [-79.157423, 37.890995],
          [-79.062454, 37.9176],
          [-79.005129, 37.88169],
          [-78.907278, 37.945959],
          [-78.839211, 38.047565],
          [-78.644036, 37.733143],
          [-78.739437, 37.631542],
          [-78.82124, 37.631842],
          [-78.825238, 37.552542],
          [-78.830938, 37.535642],
          [-78.869245, 37.542095],
          [-78.986017, 37.695546],
          [-79.067355, 37.720255],
          [-79.071417, 37.778303],
          [-79.171926, 37.803135]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51141",
      state: "51",
      county: "141",
      name: "Patrick County",
      censusArea: 483.096,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.235455, 36.87213],
          [-80.066449, 36.841057],
          [-80.041493, 36.794365],
          [-80.094389, 36.779552],
          [-80.053455, 36.542623],
          [-80.169535, 36.54319],
          [-80.171636, 36.543219],
          [-80.225408, 36.543748],
          [-80.228263, 36.543867],
          [-80.431605, 36.550219],
          [-80.432628, 36.550302],
          [-80.4401, 36.55063],
          [-80.612158, 36.558127],
          [-80.578148, 36.662118],
          [-80.525522, 36.644114],
          [-80.46418, 36.708718],
          [-80.386909, 36.797676],
          [-80.235455, 36.87213]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51145",
      state: "51",
      county: "145",
      name: "Powhatan County",
      censusArea: 260.221,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.072256, 37.657357],
          [-77.957822, 37.624882],
          [-77.923063, 37.691369],
          [-77.814415, 37.606375],
          [-77.653987, 37.562631],
          [-77.855148, 37.418363],
          [-77.899761, 37.475422],
          [-78.014437, 37.492081],
          [-78.132048, 37.454686],
          [-78.072256, 37.657357]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51155",
      state: "51",
      county: "155",
      name: "Pulaski County",
      censusArea: 319.858,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.529678, 37.134066],
          [-80.548837, 37.146557],
          [-80.573087, 37.139876],
          [-80.592875, 37.127866],
          [-80.596389, 37.118897],
          [-80.590406, 37.102],
          [-80.580067, 37.089603],
          [-80.543933, 36.984639],
          [-80.633987, 36.93178],
          [-80.740483, 36.879001],
          [-80.912674, 37.073349],
          [-80.854627, 37.147482],
          [-80.606874, 37.244911],
          [-80.529678, 37.134066]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51157",
      state: "51",
      county: "157",
      name: "Rappahannock County",
      censusArea: 266.227,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.028075, 38.759293],
          [-77.935454, 38.695853],
          [-78.168792, 38.523809],
          [-78.231978, 38.53255],
          [-78.33832, 38.627282],
          [-78.284877, 38.759229],
          [-78.130209, 38.862118],
          [-78.129874, 38.862807],
          [-78.130574, 38.864865],
          [-78.12361, 38.864902],
          [-78.122166, 38.856607],
          [-78.028075, 38.759293]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51173",
      state: "51",
      county: "173",
      name: "Smyth County",
      censusArea: 450.928,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.780539, 36.958614],
          [-81.683038, 36.933014],
          [-81.576936, 36.989515],
          [-81.437182, 37.010091],
          [-81.379427, 36.952417],
          [-81.26162, 36.76312],
          [-81.544536, 36.701617],
          [-81.605835, 36.638717],
          [-81.699441, 36.789614],
          [-81.836842, 36.929013],
          [-81.819041, 36.947513],
          [-81.780539, 36.958614]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51187",
      state: "51",
      county: "187",
      name: "Warren County",
      censusArea: 213.465,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.130209, 38.862118],
          [-78.284877, 38.759229],
          [-78.386682, 38.830152],
          [-78.300544, 38.94353],
          [-78.314196, 39.007977],
          [-78.151981, 39.036277],
          [-78.004167, 38.979479],
          [-78.130574, 38.864865],
          [-78.129874, 38.862807],
          [-78.130209, 38.862118]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51197",
      state: "51",
      county: "197",
      name: "Wythe County",
      censusArea: 461.824,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.379427, 36.952417],
          [-81.224613, 37.04784],
          [-81.114319, 37.022612],
          [-80.912674, 37.073349],
          [-80.740483, 36.879001],
          [-81.042406, 36.806422],
          [-81.08851, 36.767422],
          [-81.26162, 36.76312],
          [-81.379427, 36.952417]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51510",
      state: "51",
      county: "510",
      name: "Alexandria City County",
      lsad: "city",
      censusArea: 15.027,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.039239, 38.7852],
          [-77.088047, 38.824973],
          [-77.087931, 38.82745],
          [-77.085609, 38.84419],
          [-77.063871, 38.845011],
          [-77.05455, 38.840995],
          [-77.032986, 38.8395],
          [-77.038598, 38.791513],
          [-77.039239, 38.7852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51530",
      state: "51",
      county: "530",
      name: "Buena Vista City County",
      lsad: "city",
      censusArea: 6.704,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.366811, 37.72722],
          [-79.380389, 37.743771],
          [-79.328768, 37.760576],
          [-79.359875, 37.702359],
          [-79.382642, 37.706345],
          [-79.366811, 37.72722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51540",
      state: "51",
      county: "540",
      name: "Charlottesville City County",
      lsad: "city",
      censusArea: 10.238,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.520375, 38.018355],
          [-78.495034, 38.065337],
          [-78.44905, 38.058728],
          [-78.469733, 38.011439],
          [-78.520375, 38.018355]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51595",
      state: "51",
      county: "595",
      name: "Emporia City County",
      lsad: "city",
      censusArea: 6.894,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.556524, 36.674874],
          [-77.557359, 36.713166],
          [-77.511921, 36.719122],
          [-77.509246, 36.682998],
          [-77.556524, 36.674874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51660",
      state: "51",
      county: "660",
      name: "Harrisonburg City County",
      lsad: "city",
      censusArea: 17.418,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.870651, 38.482606],
          [-78.824253, 38.435631],
          [-78.914591, 38.389755],
          [-78.872978, 38.48851],
          [-78.870651, 38.482606]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51670",
      state: "51",
      county: "670",
      name: "Hopewell City County",
      lsad: "city",
      censusArea: 10.278,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.284019, 37.313012],
          [-77.310532, 37.261595],
          [-77.334549, 37.312468],
          [-77.322813, 37.308792],
          [-77.30988, 37.311556],
          [-77.284019, 37.313012]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51760",
      state: "51",
      county: "760",
      name: "Richmond City County",
      lsad: "city",
      censusArea: 59.805,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.420849, 37.447079],
          [-77.459219, 37.46427],
          [-77.486388, 37.459024],
          [-77.500745, 37.463831],
          [-77.514425, 37.478934],
          [-77.59611, 37.55572],
          [-77.483092, 37.573832],
          [-77.413196, 37.579928],
          [-77.420849, 37.447079]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51775",
      state: "51",
      county: "775",
      name: "Salem City County",
      lsad: "city",
      censusArea: 14.437,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.022428, 37.308156],
          [-80.01876, 37.264336],
          [-80.033476, 37.262811],
          [-80.12049, 37.287053],
          [-80.022428, 37.308156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51820",
      state: "51",
      county: "820",
      name: "Waynesboro City County",
      lsad: "city",
      censusArea: 15.039,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.949568, 38.057183],
          [-78.903534, 38.099978],
          [-78.855796, 38.074375],
          [-78.872163, 38.039587],
          [-78.949568, 38.057183]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53013",
      state: "53",
      county: "013",
      name: "Columbia County",
      censusArea: 868.629,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.228928, 46.593606],
          [-118.224468, 46.591022],
          [-118.21566, 46.588924],
          [-118.172552, 46.556947],
          [-117.851844, 46.624772],
          [-117.863261, 46.470935],
          [-117.737301, 46.471366],
          [-117.675113, 46.337628],
          [-117.611815, 46.338496],
          [-117.603163, 45.998887],
          [-117.977766, 46.000695],
          [-117.996911, 46.000787],
          [-117.991292, 46.207843],
          [-118.116493, 46.207912],
          [-118.116139, 46.295455],
          [-118.241611, 46.295072],
          [-118.228928, 46.593606]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53021",
      state: "53",
      county: "021",
      name: "Franklin County",
      censusArea: 1242.171,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.081621, 46.738019],
          [-118.209807, 46.738938],
          [-118.249122, 46.734138],
          [-118.21566, 46.588924],
          [-118.224468, 46.591022],
          [-118.228928, 46.593606],
          [-118.452637, 46.596973],
          [-118.615592, 46.504543],
          [-118.633826, 46.414022],
          [-118.746199, 46.359512],
          [-118.777228, 46.290177],
          [-118.972827, 46.238705],
          [-119.041418, 46.192856],
          [-119.268928, 46.277957],
          [-119.258292, 46.489457],
          [-119.45322, 46.679243],
          [-119.368939, 46.678204],
          [-119.369387, 46.737696],
          [-119.081621, 46.738019]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55029",
      state: "55",
      county: "029",
      name: "Door County",
      censusArea: 481.978,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-86.945964, 45.407673],
            [-86.943041, 45.41525],
            [-86.942901, 45.415392],
            [-86.941237, 45.417075],
            [-86.937393, 45.420966],
            [-86.936224, 45.421035],
            [-86.934724, 45.421123],
            [-86.934276, 45.421149],
            [-86.934091, 45.420972],
            [-86.930511, 45.417536],
            [-86.929028, 45.41377],
            [-86.928246, 45.411784],
            [-86.928045, 45.411273],
            [-86.927768, 45.411183],
            [-86.917686, 45.40789],
            [-86.914004, 45.408052],
            [-86.909743, 45.408239],
            [-86.902739, 45.408547],
            [-86.898049, 45.408753],
            [-86.892893, 45.40898],
            [-86.883364, 45.412076],
            [-86.880629, 45.412965],
            [-86.877502, 45.413981],
            [-86.865002, 45.412489],
            [-86.862174, 45.412151],
            [-86.861472, 45.412067],
            [-86.861317, 45.411946],
            [-86.859922, 45.410853],
            [-86.855993, 45.407777],
            [-86.853145, 45.405547],
            [-86.853082, 45.405562],
            [-86.830353, 45.410852],
            [-86.830331, 45.410955],
            [-86.829741, 45.413705],
            [-86.829352, 45.415516],
            [-86.829143, 45.41649],
            [-86.829905, 45.420623],
            [-86.830123, 45.421805],
            [-86.830175, 45.422092],
            [-86.8309, 45.426023],
            [-86.830755, 45.426186],
            [-86.828731, 45.428461],
            [-86.828661, 45.428539],
            [-86.828071, 45.428459],
            [-86.82177, 45.427602],
            [-86.817069, 45.426963],
            [-86.816741, 45.42676],
            [-86.81551, 45.425998],
            [-86.810055, 45.422619],
            [-86.80991, 45.422283],
            [-86.805978, 45.413159],
            [-86.805868, 45.412903],
            [-86.805843, 45.412592],
            [-86.805767, 45.411661],
            [-86.805652, 45.410247],
            [-86.805415, 45.407324],
            [-86.808303, 45.406067],
            [-86.808658, 45.406143],
            [-86.814956, 45.407483],
            [-86.815881, 45.40768],
            [-86.817148, 45.40795],
            [-86.818073, 45.408147],
            [-86.81954, 45.407679],
            [-86.822083, 45.406868],
            [-86.824383, 45.406135],
            [-86.82473, 45.405798],
            [-86.837482, 45.393432],
            [-86.841432, 45.389601],
            [-86.853103, 45.370861],
            [-86.853182, 45.370816],
            [-86.857308, 45.36846],
            [-86.858384, 45.367846],
            [-86.863367, 45.365],
            [-86.863563, 45.364888],
            [-86.8636, 45.364783],
            [-86.863694, 45.364518],
            [-86.863719, 45.364446],
            [-86.864314, 45.362764],
            [-86.867743, 45.353065],
            [-86.86771, 45.35289],
            [-86.866244, 45.344992],
            [-86.866126, 45.344356],
            [-86.865528, 45.341136],
            [-86.865499, 45.340981],
            [-86.865634, 45.340686],
            [-86.866054, 45.339765],
            [-86.869031, 45.333244],
            [-86.869041, 45.333223],
            [-86.86951, 45.333041],
            [-86.872296, 45.331962],
            [-86.874685, 45.331037],
            [-86.874761, 45.331008],
            [-86.875117, 45.33087],
            [-86.879361, 45.331335],
            [-86.880572, 45.331467],
            [-86.887802, 45.332259],
            [-86.895055, 45.329035],
            [-86.899488, 45.322588],
            [-86.896667, 45.307275],
            [-86.896922, 45.298521],
            [-86.896928, 45.2983],
            [-86.899891, 45.295185],
            [-86.900636, 45.295431],
            [-86.901602, 45.29575],
            [-86.904362, 45.296662],
            [-86.904898, 45.296839],
            [-86.913995, 45.31211],
            [-86.925681, 45.3242],
            [-86.937368, 45.333065],
            [-86.940715, 45.333762],
            [-86.94107, 45.333836],
            [-86.946796, 45.335027],
            [-86.946943, 45.335058],
            [-86.948087, 45.335296],
            [-86.95155, 45.338298],
            [-86.95499, 45.34128],
            [-86.955456, 45.341684],
            [-86.955577, 45.341788],
            [-86.955691, 45.341888],
            [-86.956054, 45.342202],
            [-86.956129, 45.342267],
            [-86.95614, 45.343785],
            [-86.956192, 45.351179],
            [-86.956198, 45.352006],
            [-86.954435, 45.353706],
            [-86.953389, 45.354715],
            [-86.95336, 45.35472],
            [-86.948272, 45.355682],
            [-86.946297, 45.35869],
            [-86.946475, 45.359826],
            [-86.946503, 45.360004],
            [-86.946791, 45.361845],
            [-86.947142, 45.364085],
            [-86.94751, 45.366434],
            [-86.947604, 45.366645],
            [-86.947684, 45.366825],
            [-86.948731, 45.369178],
            [-86.948743, 45.369205],
            [-86.948803, 45.369338],
            [-86.95497, 45.383194],
            [-86.955204, 45.383721],
            [-86.954329, 45.385989],
            [-86.953773, 45.38743],
            [-86.951866, 45.392373],
            [-86.951176, 45.394162],
            [-86.947346, 45.40409],
            [-86.947074, 45.404796],
            [-86.946837, 45.40541],
            [-86.945964, 45.407673]
          ]
        ],
        [
          [
            [-87.736058, 44.677025],
            [-87.733701, 44.678916],
            [-87.729836, 44.682015],
            [-87.71978, 44.693246],
            [-87.718413, 44.707765],
            [-87.718409, 44.707811],
            [-87.718427, 44.70787],
            [-87.720248, 44.713833],
            [-87.720599, 44.714984],
            [-87.721816, 44.718969],
            [-87.721252, 44.722361],
            [-87.721059, 44.723522],
            [-87.720889, 44.724548],
            [-87.720312, 44.725073],
            [-87.714401, 44.730449],
            [-87.705852, 44.738225],
            [-87.705733, 44.738364],
            [-87.704653, 44.73963],
            [-87.703557, 44.740914],
            [-87.701022, 44.743882],
            [-87.700778, 44.744169],
            [-87.700539, 44.744448],
            [-87.698653, 44.746657],
            [-87.698584, 44.746738],
            [-87.698246, 44.747133],
            [-87.698167, 44.747226],
            [-87.688207, 44.758892],
            [-87.687497, 44.759567],
            [-87.679709, 44.766973],
            [-87.678368, 44.768247],
            [-87.677995, 44.768602],
            [-87.662056, 44.783758],
            [-87.647764, 44.797347],
            [-87.6463, 44.798739],
            [-87.645229, 44.800466],
            [-87.644646, 44.801407],
            [-87.644637, 44.801423],
            [-87.644545, 44.801571],
            [-87.637104, 44.813575],
            [-87.635416, 44.815124],
            [-87.635265, 44.815263],
            [-87.634476, 44.815986],
            [-87.63439, 44.816065],
            [-87.634381, 44.816073],
            [-87.634059, 44.816369],
            [-87.633418, 44.816956],
            [-87.632977, 44.817361],
            [-87.632892, 44.817439],
            [-87.632699, 44.817616],
            [-87.622014, 44.827419],
            [-87.616384, 44.832585],
            [-87.616242, 44.832715],
            [-87.611852, 44.836743],
            [-87.611118, 44.837416],
            [-87.610245, 44.838217],
            [-87.610063, 44.838384],
            [-87.610018, 44.838405],
            [-87.609784, 44.838514],
            [-87.581635, 44.851638],
            [-87.581306, 44.851791],
            [-87.573175, 44.853118],
            [-87.570851, 44.852932],
            [-87.560183, 44.85208],
            [-87.550288, 44.85129],
            [-87.550003, 44.851381],
            [-87.534723, 44.85625],
            [-87.530999, 44.857437],
            [-87.530794, 44.857594],
            [-87.515142, 44.869596],
            [-87.51459, 44.86938],
            [-87.513775, 44.869061],
            [-87.513413, 44.868919],
            [-87.512129, 44.868416],
            [-87.504868, 44.865573],
            [-87.504405, 44.865392],
            [-87.504303, 44.865352],
            [-87.502431, 44.864619],
            [-87.501578, 44.864285],
            [-87.499063, 44.864207],
            [-87.478489, 44.863572],
            [-87.472639, 44.86769],
            [-87.456688, 44.878918],
            [-87.441339, 44.889723],
            [-87.437084, 44.892718],
            [-87.433128, 44.892741],
            [-87.433005, 44.892691],
            [-87.428144, 44.890738],
            [-87.421007, 44.887869],
            [-87.420327, 44.887596],
            [-87.419106, 44.885378],
            [-87.419114, 44.885285],
            [-87.419135, 44.885052],
            [-87.419951, 44.87594],
            [-87.419253, 44.87496],
            [-87.410919, 44.863259],
            [-87.410015, 44.86199],
            [-87.408779, 44.861453],
            [-87.405658, 44.860098],
            [-87.405541, 44.860047],
            [-87.384821, 44.865532],
            [-87.384593, 44.869277],
            [-87.383874, 44.881116],
            [-87.383839, 44.881685],
            [-87.383833, 44.881788],
            [-87.38367, 44.884475],
            [-87.383655, 44.884723],
            [-87.383631, 44.885115],
            [-87.385396, 44.889964],
            [-87.386111, 44.890699],
            [-87.386375, 44.890972],
            [-87.386396, 44.890993],
            [-87.38649, 44.89109],
            [-87.390604, 44.895323],
            [-87.390651, 44.895372],
            [-87.3908, 44.895524],
            [-87.39107, 44.895803],
            [-87.39198, 44.896739],
            [-87.39227, 44.897037],
            [-87.393399, 44.898199],
            [-87.396972, 44.899955],
            [-87.397184, 44.900059],
            [-87.397359, 44.900145],
            [-87.400488, 44.901683],
            [-87.401947, 44.9024],
            [-87.406199, 44.90449],
            [-87.405361, 44.909626],
            [-87.405005, 44.911806],
            [-87.398368, 44.925226],
            [-87.393752, 44.933751],
            [-87.393672, 44.933899],
            [-87.393405, 44.934393],
            [-87.393058, 44.934677],
            [-87.389006, 44.937988],
            [-87.387253, 44.939421],
            [-87.385066, 44.942445],
            [-87.384669, 44.942993],
            [-87.382436, 44.94608],
            [-87.374805, 44.956631],
            [-87.373046, 44.960388],
            [-87.372599, 44.961343],
            [-87.372568, 44.96141],
            [-87.371854, 44.962935],
            [-87.370075, 44.966736],
            [-87.363492, 44.980798],
            [-87.360288, 44.987643],
            [-87.351487, 44.997203],
            [-87.345091, 45.004151],
            [-87.336548, 45.013431],
            [-87.336457, 45.01353],
            [-87.322117, 45.034201],
            [-87.322022, 45.03429],
            [-87.315879, 45.040103],
            [-87.307394, 45.04813],
            [-87.303475, 45.051838],
            [-87.30342, 45.05189],
            [-87.303149, 45.052146],
            [-87.302831, 45.052447],
            [-87.30247, 45.052666],
            [-87.300887, 45.053625],
            [-87.300724, 45.053724],
            [-87.300146, 45.054075],
            [-87.28428, 45.063694],
            [-87.284208, 45.06376],
            [-87.269231, 45.077396],
            [-87.268737, 45.077847],
            [-87.268695, 45.077884],
            [-87.264877, 45.081361],
            [-87.260631, 45.092355],
            [-87.260542, 45.092585],
            [-87.260595, 45.106007],
            [-87.257449, 45.121644],
            [-87.254072, 45.126323],
            [-87.250487, 45.131289],
            [-87.240813, 45.137559],
            [-87.240308, 45.137886],
            [-87.242331, 45.146773],
            [-87.242924, 45.149377],
            [-87.238426, 45.166492],
            [-87.238229, 45.167238],
            [-87.238224, 45.167259],
            [-87.23822, 45.167262],
            [-87.231925, 45.172316],
            [-87.231214, 45.172887],
            [-87.224065, 45.174551],
            [-87.222316, 45.174959],
            [-87.221971, 45.175039],
            [-87.221707, 45.174716],
            [-87.220937, 45.173773],
            [-87.21437, 45.165735],
            [-87.213483, 45.165614],
            [-87.207963, 45.164857],
            [-87.200385, 45.163819],
            [-87.195876, 45.163201],
            [-87.195213, 45.16311],
            [-87.17517, 45.173],
            [-87.175068, 45.17305],
            [-87.173722, 45.17485],
            [-87.171272, 45.178123],
            [-87.171233, 45.178175],
            [-87.170266, 45.179468],
            [-87.170062, 45.179741],
            [-87.168298, 45.182099],
            [-87.168252, 45.18216],
            [-87.167179, 45.183594],
            [-87.163169, 45.185331],
            [-87.147709, 45.190711],
            [-87.147657, 45.190719],
            [-87.142708, 45.191437],
            [-87.13303, 45.192843],
            [-87.132874, 45.192794],
            [-87.12981, 45.191825],
            [-87.128597, 45.191441],
            [-87.124549, 45.190162],
            [-87.123689, 45.18989],
            [-87.119972, 45.191103],
            [-87.119971, 45.191125],
            [-87.119968, 45.191198],
            [-87.119925, 45.19228],
            [-87.119921, 45.192386],
            [-87.119894, 45.193071],
            [-87.119887, 45.193242],
            [-87.119876, 45.193527],
            [-87.119874, 45.193581],
            [-87.119868, 45.193724],
            [-87.119864, 45.193843],
            [-87.119825, 45.194814],
            [-87.119822, 45.194908],
            [-87.119784, 45.195866],
            [-87.11978, 45.195965],
            [-87.119768, 45.196271],
            [-87.119534, 45.202184],
            [-87.119522, 45.202488],
            [-87.119511, 45.202754],
            [-87.119413, 45.205237],
            [-87.119405, 45.20544],
            [-87.11945, 45.205529],
            [-87.119654, 45.20593],
            [-87.121541, 45.209649],
            [-87.121566, 45.209698],
            [-87.121609, 45.209783],
            [-87.122098, 45.215121],
            [-87.122194, 45.216168],
            [-87.122226, 45.216519],
            [-87.122248, 45.216764],
            [-87.122364, 45.218023],
            [-87.122708, 45.221786],
            [-87.122819, 45.222997],
            [-87.121667, 45.226337],
            [-87.121288, 45.227438],
            [-87.121266, 45.2275],
            [-87.117391, 45.23874],
            [-87.116456, 45.24145],
            [-87.116432, 45.24152],
            [-87.109541, 45.255397],
            [-87.109014, 45.256458],
            [-87.108743, 45.257003],
            [-87.10843, 45.257093],
            [-87.101681, 45.259027],
            [-87.078316, 45.265723],
            [-87.071035, 45.280355],
            [-87.070845, 45.280532],
            [-87.057627, 45.292838],
            [-87.053417, 45.287901],
            [-87.052927, 45.287326],
            [-87.052872, 45.287262],
            [-87.051979, 45.286215],
            [-87.0517, 45.285888],
            [-87.050796, 45.285758],
            [-87.048745, 45.285464],
            [-87.043895, 45.284767],
            [-87.043724, 45.28482],
            [-87.043497, 45.28489],
            [-87.04313, 45.285003],
            [-87.041463, 45.285518],
            [-87.034206, 45.287758],
            [-87.034077, 45.287844],
            [-87.021088, 45.296541],
            [-87.017036, 45.299254],
            [-87.015797, 45.29919],
            [-86.994112, 45.298061],
            [-86.993528, 45.297915],
            [-86.993201, 45.297833],
            [-86.984223, 45.295585],
            [-86.98388, 45.295499],
            [-86.983355, 45.295368],
            [-86.983227, 45.29526],
            [-86.980058, 45.292598],
            [-86.97778, 45.290684],
            [-86.977714, 45.290575],
            [-86.970355, 45.278455],
            [-86.972786, 45.274591],
            [-86.973227, 45.27389],
            [-86.974346, 45.272113],
            [-86.974528, 45.271823],
            [-86.974622, 45.271752],
            [-86.977355, 45.269687],
            [-86.983597, 45.264971],
            [-86.984398, 45.264366],
            [-86.984495, 45.263409],
            [-86.984938, 45.259036],
            [-86.984975, 45.258674],
            [-86.984172, 45.255346],
            [-86.983066, 45.250764],
            [-86.982669, 45.249117],
            [-86.982393, 45.24898],
            [-86.976711, 45.246146],
            [-86.973287, 45.246381],
            [-86.973022, 45.246399],
            [-86.973453, 45.244966],
            [-86.97346, 45.244944],
            [-86.973483, 45.244867],
            [-86.973651, 45.244308],
            [-86.974086, 45.242864],
            [-86.978697, 45.227538],
            [-86.978759, 45.227333],
            [-86.98108, 45.223646],
            [-86.981956, 45.222254],
            [-86.983001, 45.220593],
            [-86.983708, 45.21947],
            [-86.985973, 45.215872],
            [-86.988075, 45.21536],
            [-86.995804, 45.213478],
            [-86.997316, 45.21311],
            [-87.000112, 45.212429],
            [-87.002806, 45.211773],
            [-87.005243, 45.213607],
            [-87.005359, 45.213694],
            [-87.005468, 45.214114],
            [-87.00754, 45.222127],
            [-87.007583, 45.222127],
            [-87.032521, 45.222274],
            [-87.032546, 45.222274],
            [-87.032576, 45.222235],
            [-87.034524, 45.219734],
            [-87.040909, 45.211535],
            [-87.040924, 45.211421],
            [-87.041063, 45.210375],
            [-87.041075, 45.210283],
            [-87.041106, 45.210052],
            [-87.045899, 45.173902],
            [-87.045883, 45.173543],
            [-87.045701, 45.169351],
            [-87.045687, 45.169016],
            [-87.045681, 45.168889],
            [-87.04566, 45.168403],
            [-87.04548, 45.164271],
            [-87.045242, 45.158798],
            [-87.045225, 45.158401],
            [-87.044176, 45.15763],
            [-87.041145, 45.155404],
            [-87.038479, 45.153445],
            [-87.036716, 45.15215],
            [-87.034083, 45.150216],
            [-87.030225, 45.147382],
            [-87.029164, 45.146603],
            [-87.02898, 45.146467],
            [-87.028847, 45.14637],
            [-87.028957, 45.14625],
            [-87.030105, 45.145009],
            [-87.030126, 45.144986],
            [-87.031167, 45.14386],
            [-87.031307, 45.143709],
            [-87.032194, 45.142748],
            [-87.032344, 45.142586],
            [-87.032461, 45.14246],
            [-87.03292, 45.141963],
            [-87.033114, 45.141753],
            [-87.033117, 45.141752],
            [-87.033546, 45.141522],
            [-87.03355, 45.141519],
            [-87.034621, 45.140946],
            [-87.034667, 45.140921],
            [-87.035465, 45.140494],
            [-87.035838, 45.140294],
            [-87.040175, 45.137972],
            [-87.041564, 45.137228],
            [-87.042114, 45.136933],
            [-87.042286, 45.136841],
            [-87.045748, 45.134987],
            [-87.054282, 45.120074],
            [-87.054342, 45.119968],
            [-87.051049, 45.116172],
            [-87.05075, 45.115111],
            [-87.049346, 45.110122],
            [-87.049268, 45.109845],
            [-87.049231, 45.109712],
            [-87.048951, 45.108718],
            [-87.048911, 45.107665],
            [-87.048406, 45.094259],
            [-87.048346, 45.092647],
            [-87.048213, 45.089124],
            [-87.048359, 45.089098],
            [-87.05078, 45.088663],
            [-87.057415, 45.087472],
            [-87.057444, 45.087467],
            [-87.058259, 45.086304],
            [-87.061966, 45.081015],
            [-87.063129, 45.079356],
            [-87.063157, 45.079316],
            [-87.063376, 45.079202],
            [-87.064864, 45.078427],
            [-87.079552, 45.070783],
            [-87.079555, 45.07077],
            [-87.080271, 45.067154],
            [-87.080755, 45.06471],
            [-87.081866, 45.059103],
            [-87.082419, 45.058879],
            [-87.086351, 45.057287],
            [-87.090849, 45.055465],
            [-87.091639, 45.055145],
            [-87.091761, 45.055158],
            [-87.092177, 45.055203],
            [-87.096387, 45.055654],
            [-87.097479, 45.055771],
            [-87.121156, 45.058311],
            [-87.124701, 45.052936],
            [-87.124722, 45.050833],
            [-87.124727, 45.050344],
            [-87.124737, 45.049268],
            [-87.124744, 45.048608],
            [-87.124788, 45.044141],
            [-87.124797, 45.043262],
            [-87.124808, 45.042167],
            [-87.132344, 45.026862],
            [-87.134354, 45.02278],
            [-87.138024, 45.015327],
            [-87.139384, 45.012565],
            [-87.141048, 45.012175],
            [-87.154084, 45.009117],
            [-87.163477, 45.004913],
            [-87.163529, 45.00489],
            [-87.163561, 45.004848],
            [-87.167474, 44.999736],
            [-87.16797, 44.999088],
            [-87.169494, 44.997096],
            [-87.169676, 44.99686],
            [-87.169694, 44.996836],
            [-87.181428, 44.981505],
            [-87.181901, 44.980887],
            [-87.182922, 44.979221],
            [-87.187585, 44.971606],
            [-87.189134, 44.969078],
            [-87.189407, 44.968632],
            [-87.189402, 44.968524],
            [-87.189383, 44.968164],
            [-87.188582, 44.952193],
            [-87.188399, 44.94856],
            [-87.188375, 44.948077],
            [-87.188074, 44.947886],
            [-87.179974, 44.942753],
            [-87.17524, 44.939753],
            [-87.1717, 44.931476],
            [-87.17492, 44.927749],
            [-87.182421, 44.926297],
            [-87.185858, 44.925632],
            [-87.186732, 44.925463],
            [-87.187984, 44.924845],
            [-87.204238, 44.916819],
            [-87.215808, 44.906744],
            [-87.215977, 44.906597],
            [-87.216059, 44.906009],
            [-87.216074, 44.9059],
            [-87.216146, 44.905379],
            [-87.217171, 44.898013],
            [-87.217195, 44.897839],
            [-87.217149, 44.897789],
            [-87.21671, 44.897309],
            [-87.212587, 44.892809],
            [-87.209265, 44.889182],
            [-87.206285, 44.885928],
            [-87.206184, 44.885329],
            [-87.204815, 44.877199],
            [-87.204809, 44.877159],
            [-87.204545, 44.875593],
            [-87.204904, 44.875429],
            [-87.211398, 44.872461],
            [-87.224891, 44.866295],
            [-87.246171, 44.856571],
            [-87.267061, 44.847025],
            [-87.267441, 44.846851],
            [-87.267564, 44.846655],
            [-87.274769, 44.835187],
            [-87.27603, 44.83318],
            [-87.282561, 44.814729],
            [-87.294869, 44.809131],
            [-87.304824, 44.804603],
            [-87.307482, 44.801377],
            [-87.313363, 44.794237],
            [-87.313692, 44.793838],
            [-87.313751, 44.793766],
            [-87.31388, 44.793595],
            [-87.314247, 44.793109],
            [-87.315813, 44.791034],
            [-87.320397, 44.784963],
            [-87.319796, 44.77917],
            [-87.319169, 44.773135],
            [-87.31903, 44.771794],
            [-87.319013, 44.771632],
            [-87.318982, 44.771335],
            [-87.319903, 44.769672],
            [-87.320855, 44.767953],
            [-87.337584, 44.737751],
            [-87.339155, 44.732839],
            [-87.339904, 44.730498],
            [-87.341226, 44.726364],
            [-87.341491, 44.725535],
            [-87.343508, 44.719228],
            [-87.348082, 44.711526],
            [-87.349559, 44.709039],
            [-87.353789, 44.701915],
            [-87.354172, 44.701445],
            [-87.354249, 44.70135],
            [-87.36115, 44.692876],
            [-87.367992, 44.684474],
            [-87.368263, 44.684141],
            [-87.373228, 44.675593],
            [-87.375091, 44.675601],
            [-87.736058, 44.677025]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55033",
      state: "55",
      county: "033",
      name: "Dunn County",
      censusArea: 850.105,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.136348, 44.857848],
          [-92.136113, 45.121432],
          [-92.156465, 45.209554],
          [-91.665647, 45.207991],
          [-91.650455, 44.855951],
          [-91.650361, 44.683633],
          [-92.135202, 44.684363],
          [-92.136348, 44.857848]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55039",
      state: "55",
      county: "039",
      name: "Fond Du Lac County",
      censusArea: 719.553,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.46757, 43.893415],
          [-88.403195, 43.892977],
          [-88.404187, 43.9382],
          [-88.314948, 43.937665],
          [-88.161654, 43.937683],
          [-88.162274, 43.891511],
          [-88.160873, 43.542943],
          [-88.400426, 43.543532],
          [-88.401041, 43.630933],
          [-88.886052, 43.63354],
          [-88.885697, 43.895184],
          [-88.46757, 43.893415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55045",
      state: "55",
      county: "045",
      name: "Green County",
      censusArea: 583.957,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.838167, 42.857397],
          [-89.369069, 42.856471],
          [-89.369127, 42.845046],
          [-89.366031, 42.500274],
          [-89.401416, 42.500433],
          [-89.401432, 42.500433],
          [-89.420991, 42.500589],
          [-89.422567, 42.50068],
          [-89.423926, 42.500818],
          [-89.425162, 42.500726],
          [-89.4843, 42.501426],
          [-89.492612, 42.501514],
          [-89.493216, 42.501514],
          [-89.522542, 42.501889],
          [-89.564407, 42.502628],
          [-89.594779, 42.503468],
          [-89.600001, 42.503672],
          [-89.603523, 42.503557],
          [-89.61341, 42.503942],
          [-89.644176, 42.50452],
          [-89.650324, 42.504613],
          [-89.667596, 42.50496],
          [-89.690088, 42.505191],
          [-89.693487, 42.505099],
          [-89.742395, 42.505382],
          [-89.769643, 42.505322],
          [-89.780302, 42.505349],
          [-89.793957, 42.505466],
          [-89.799704, 42.505421],
          [-89.801897, 42.505444],
          [-89.837587, 42.505543],
          [-89.83841, 42.813799],
          [-89.838167, 42.857397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55063",
      state: "55",
      county: "063",
      name: "La Crosse County",
      censusArea: 451.686,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.151843, 44.079656],
          [-90.972764, 44.070775],
          [-90.912918, 44.071522],
          [-90.910467, 43.725342],
          [-91.268198, 43.726571],
          [-91.262436, 43.792166],
          [-91.284138, 43.847065],
          [-91.298815, 43.856555],
          [-91.310991, 43.867381],
          [-91.363242, 43.926563],
          [-91.420114, 43.984243],
          [-91.322143, 43.984462],
          [-91.337329, 44.037499],
          [-91.151843, 44.079656]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55069",
      state: "55",
      county: "069",
      name: "Lincoln County",
      censusArea: 878.973,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.043414, 45.555082],
          [-89.428258, 45.555353],
          [-89.424824, 45.467257],
          [-89.425971, 45.119098],
          [-90.043685, 45.120214],
          [-90.046143, 45.340309],
          [-90.042273, 45.381866],
          [-90.043414, 45.555082]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55073",
      state: "55",
      county: "073",
      name: "Marathon County",
      censusArea: 1544.983,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.316253, 44.685154],
          [-90.31618, 44.840775],
          [-90.315919, 44.844187],
          [-90.315038, 45.033829],
          [-90.197514, 45.03364],
          [-90.197765, 45.120604],
          [-90.043685, 45.120214],
          [-89.425971, 45.119098],
          [-89.224214, 45.118567],
          [-89.223814, 45.029246],
          [-89.223745, 44.681365],
          [-89.844931, 44.684945],
          [-90.316253, 44.685154]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55081",
      state: "55",
      county: "081",
      name: "Monroe County",
      censusArea: 900.776,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.972764, 44.070775],
          [-90.906152, 44.158316],
          [-90.312575, 44.155284],
          [-90.312195, 43.731312],
          [-90.910467, 43.725342],
          [-90.912918, 44.071522],
          [-90.972764, 44.070775]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55095",
      state: "55",
      county: "095",
      name: "Polk County",
      censusArea: 913.962,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.732594, 45.304224],
          [-92.704794, 45.326526],
          [-92.664102, 45.393309],
          [-92.680234, 45.464344],
          [-92.72623, 45.531085],
          [-92.803432, 45.5625],
          [-92.823309, 45.560934],
          [-92.871082, 45.567581],
          [-92.883749, 45.575483],
          [-92.886421, 45.594881],
          [-92.888114, 45.628377],
          [-92.885661, 45.644126],
          [-92.529111, 45.642171],
          [-92.528198, 45.72868],
          [-92.154443, 45.725616],
          [-92.154888, 45.639742],
          [-92.156465, 45.209554],
          [-92.765898, 45.210173],
          [-92.760615, 45.278827],
          [-92.751735, 45.292667],
          [-92.746139, 45.296046],
          [-92.732594, 45.304224]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55103",
      state: "55",
      county: "103",
      name: "Richland County",
      censusArea: 586.151,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.429825, 43.20094],
          [-90.545408, 43.211455],
          [-90.666786, 43.171778],
          [-90.668561, 43.422994],
          [-90.671654, 43.552969],
          [-90.311069, 43.553991],
          [-90.191964, 43.554996],
          [-90.193814, 43.164464],
          [-90.292534, 43.206859],
          [-90.429825, 43.20094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55115",
      state: "55",
      county: "115",
      name: "Shawano County",
      censusArea: 893.057,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.223814, 45.029246],
          [-89.102069, 45.029499],
          [-88.98168, 45.028917],
          [-88.981432, 44.942841],
          [-88.735548, 44.943178],
          [-88.736194, 44.856376],
          [-88.489149, 44.855445],
          [-88.248213, 44.852739],
          [-88.242687, 44.679631],
          [-88.245204, 44.584746],
          [-88.577163, 44.59063],
          [-88.606083, 44.590515],
          [-88.605159, 44.678331],
          [-89.223745, 44.681365],
          [-89.223814, 45.029246]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55129",
      state: "55",
      county: "129",
      name: "Washburn County",
      censusArea: 797.113,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.540294, 45.637605],
          [-92.031417, 45.639928],
          [-92.033404, 45.98387],
          [-92.049636, 46.157597],
          [-91.551282, 46.157045],
          [-91.540294, 45.637605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55141",
      state: "55",
      county: "141",
      name: "Wood County",
      censusArea: 793.116,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.902565, 44.24947],
          [-90.312035, 44.248758],
          [-90.31605, 44.424673],
          [-90.316253, 44.685154],
          [-89.844931, 44.684945],
          [-89.84441, 44.511542],
          [-89.726622, 44.511191],
          [-89.724746, 44.247678],
          [-89.726235, 44.247943],
          [-89.902565, 44.24947]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56025",
      state: "56",
      county: "025",
      name: "Natrona County",
      censusArea: 5340.352,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.075852, 42.433218],
          [-106.654781, 42.431094],
          [-107.522923, 42.434356],
          [-107.542025, 42.434356],
          [-107.543526, 42.781558],
          [-107.501425, 42.781458],
          [-107.502327, 43.12806],
          [-107.516728, 43.12796],
          [-107.517031, 43.472659],
          [-107.535032, 43.472759],
          [-107.534897, 43.501362],
          [-107.110734, 43.500286],
          [-107.11092, 43.492362],
          [-106.078068, 43.494471],
          [-106.072669, 42.778165],
          [-106.075852, 42.433218]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56031",
      state: "56",
      county: "031",
      name: "Platte County",
      censusArea: 2084.208,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.653338, 41.653007],
          [-105.278236, 41.656655],
          [-105.27375, 42.088824],
          [-105.28391, 42.431402],
          [-105.286511, 42.605826],
          [-104.892437, 42.608939],
          [-104.655639, 42.609468],
          [-104.655027, 42.216785],
          [-104.653338, 41.653007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56033",
      state: "56",
      county: "033",
      name: "Sheridan County",
      censusArea: 2523.993,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.009676, 44.563981],
          [-106.428897, 44.556777],
          [-107.369621, 44.559837],
          [-107.615891, 44.701297],
          [-107.741543, 44.714352],
          [-107.832319, 44.790161],
          [-107.911495, 45.000948],
          [-107.608854, 45.00086],
          [-107.607824, 45.000929],
          [-107.13418, 45.000109],
          [-107.080624, 44.996791],
          [-106.892875, 44.995947],
          [-106.888773, 44.995885],
          [-106.263586, 44.993788],
          [-106.024814, 44.993688],
          [-106.009676, 44.563981]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56045",
      state: "56",
      county: "045",
      name: "Weston County",
      censusArea: 2398.089,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.055077, 43.936535],
          [-104.055104, 43.853478],
          [-104.055138, 43.750421],
          [-104.055133, 43.747105],
          [-104.054902, 43.583852],
          [-104.054885, 43.583512],
          [-104.05484, 43.579368],
          [-104.055032, 43.558603],
          [-104.054787, 43.503328],
          [-104.899941, 43.49964],
          [-105.079797, 43.498445],
          [-105.079281, 44.176182],
          [-104.054487, 44.180381],
          [-104.054562, 44.141081],
          [-104.05495, 43.93809],
          [-104.055077, 43.936535]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53041",
      state: "53",
      county: "041",
      name: "Lewis County",
      censusArea: 2402.802,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.523556, 46.388221],
          [-122.241011, 46.386371],
          [-122.241696, 46.385292],
          [-123.091305, 46.387929],
          [-123.217841, 46.385128],
          [-123.358247, 46.384227],
          [-123.370775, 46.792449],
          [-123.202826, 46.794392],
          [-123.160588, 46.793383],
          [-123.160909, 46.76433],
          [-122.202721, 46.762628],
          [-121.83259, 46.729721],
          [-121.758941, 46.783734],
          [-121.456356, 46.783744],
          [-121.35232, 46.712366],
          [-121.451194, 46.533537],
          [-121.386557, 46.410601],
          [-121.523556, 46.388221]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53045",
      state: "53",
      county: "045",
      name: "Mason County",
      censusArea: 959.417,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.905659, 47.155614],
          [-122.936716, 47.190246],
          [-123.075101, 47.084503],
          [-123.202066, 47.084895],
          [-123.489031, 47.082716],
          [-123.50438, 47.517609],
          [-123.504385, 47.60701],
          [-122.950473, 47.606266],
          [-123.037766, 47.520214],
          [-122.872192, 47.521495],
          [-122.80015, 47.520165],
          [-122.801451, 47.404016],
          [-122.85005, 47.272917],
          [-122.821426, 47.194211],
          [-122.881717, 47.156285],
          [-122.905659, 47.155614]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53065",
      state: "53",
      county: "065",
      name: "Stevens County",
      censusArea: 2477.765,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.196891, 49.000094],
          [-117.429539, 48.999957],
          [-117.430203, 48.923068],
          [-117.498868, 48.922786],
          [-117.498577, 48.836813],
          [-117.563619, 48.836699],
          [-117.563527, 48.742464],
          [-117.629512, 48.742683],
          [-117.629241, 48.655848],
          [-117.499695, 48.656227],
          [-117.502314, 48.134864],
          [-117.437521, 48.134631],
          [-117.436903, 48.047206],
          [-117.535039, 48.047865],
          [-117.535661, 47.795111],
          [-117.699097, 47.856089],
          [-117.820784, 47.825188],
          [-117.882083, 47.847152],
          [-118.078212, 47.800161],
          [-118.213025, 47.945298],
          [-118.340925, 47.894395],
          [-118.402407, 48.026382],
          [-118.238198, 48.058184],
          [-118.248606, 48.134255],
          [-118.191298, 48.159888],
          [-118.1945, 48.212889],
          [-118.141475, 48.259951],
          [-118.175602, 48.319492],
          [-118.206507, 48.470296],
          [-118.149709, 48.520568],
          [-118.10496, 48.653503],
          [-118.131002, 48.777653],
          [-118.195508, 48.840725],
          [-118.221669, 48.937098],
          [-118.196891, 49.000094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54001",
      state: "54",
      county: "001",
      name: "Barbour County",
      censusArea: 341.059,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.166387, 39.242283],
          [-80.023411, 39.247892],
          [-79.895536, 39.299584],
          [-79.809729, 39.230526],
          [-79.825092, 39.115803],
          [-79.896094, 38.973404],
          [-80.085062, 38.947236],
          [-80.05582, 39.03716],
          [-80.128198, 39.037795],
          [-80.227173, 39.112792],
          [-80.224687, 39.170977],
          [-80.166387, 39.242283]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54007",
      state: "54",
      county: "007",
      name: "Braxton County",
      censusArea: 510.812,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.881232, 38.507045],
          [-81.031677, 38.667839],
          [-80.98495, 38.717898],
          [-80.701836, 38.87886],
          [-80.605799, 38.904167],
          [-80.446148, 38.777514],
          [-80.457332, 38.739169],
          [-80.508655, 38.645553],
          [-80.650786, 38.52659],
          [-80.785177, 38.557649],
          [-80.881232, 38.507045]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54013",
      state: "54",
      county: "013",
      name: "Calhoun County",
      censusArea: 279.249,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.98495, 38.717898],
          [-81.031677, 38.667839],
          [-81.08371, 38.611982],
          [-81.158837, 38.644494],
          [-81.171819, 38.739805],
          [-81.278412, 38.914867],
          [-81.162455, 39.030608],
          [-81.033632, 39.009584],
          [-81.002777, 38.846025],
          [-81.056714, 38.793322],
          [-80.98495, 38.717898]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54021",
      state: "54",
      county: "021",
      name: "Gilmer County",
      censusArea: 338.503,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.605799, 38.904167],
          [-80.701836, 38.87886],
          [-80.98495, 38.717898],
          [-81.056714, 38.793322],
          [-81.002777, 38.846025],
          [-81.033632, 39.009584],
          [-80.81297, 39.109401],
          [-80.749214, 39.099843],
          [-80.728326, 39.095679],
          [-80.716815, 39.00843],
          [-80.605799, 38.904167]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54039",
      state: "54",
      county: "039",
      name: "Kanawha County",
      censusArea: 901.587,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.522166, 38.612746],
          [-81.465372, 38.549898],
          [-81.194113, 38.527634],
          [-81.283872, 38.472029],
          [-81.231434, 38.263716],
          [-81.231636, 38.263514],
          [-81.331503, 38.167578],
          [-81.380829, 37.969109],
          [-81.414331, 37.973508],
          [-81.438531, 37.968008],
          [-81.456632, 37.987307],
          [-81.515836, 38.11236],
          [-81.643685, 38.220401],
          [-81.83347, 38.20957],
          [-81.818234, 38.316751],
          [-81.915197, 38.325597],
          [-81.900222, 38.398572],
          [-81.725801, 38.46662],
          [-81.750547, 38.591014],
          [-81.696348, 38.626427],
          [-81.632836, 38.55437],
          [-81.522166, 38.612746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54049",
      state: "54",
      county: "049",
      name: "Marion County",
      censusArea: 308.735,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.494085, 39.469599],
          [-80.498163, 39.563327],
          [-80.39581, 39.637347],
          [-80.153532, 39.610426],
          [-79.938878, 39.452465],
          [-80.086785, 39.386463],
          [-80.197286, 39.393186],
          [-80.293693, 39.434312],
          [-80.494085, 39.469599]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54061",
      state: "54",
      county: "061",
      name: "Monongalia County",
      censusArea: 360.062,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.39581, 39.637347],
          [-80.421388, 39.721189],
          [-80.309457, 39.721264],
          [-80.308651, 39.721283],
          [-79.916266, 39.720792],
          [-79.853131, 39.720713],
          [-79.852904, 39.720713],
          [-79.763774, 39.720776],
          [-79.789277, 39.623781],
          [-79.899744, 39.48194],
          [-79.894688, 39.435588],
          [-79.938878, 39.452465],
          [-80.153532, 39.610426],
          [-80.39581, 39.637347]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54079",
      state: "54",
      county: "079",
      name: "Putnam County",
      censusArea: 345.669,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.696348, 38.626427],
          [-81.750547, 38.591014],
          [-81.725801, 38.46662],
          [-81.900222, 38.398572],
          [-81.915197, 38.325597],
          [-81.991774, 38.269085],
          [-82.048589, 38.290138],
          [-82.047128, 38.374432],
          [-82.055127, 38.474547],
          [-82.064562, 38.607985],
          [-81.955791, 38.657355],
          [-81.772843, 38.680843],
          [-81.747683, 38.684134],
          [-81.696348, 38.626427]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54089",
      state: "54",
      county: "089",
      name: "Summers County",
      censusArea: 360.46,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.94509, 37.819121],
          [-80.806316, 37.868915],
          [-80.663348, 37.731696],
          [-80.660446, 37.714563],
          [-80.858473, 37.428301],
          [-80.859556, 37.429568],
          [-80.859558, 37.429555],
          [-81.09463, 37.588658],
          [-80.947017, 37.715449],
          [-80.882316, 37.680766],
          [-80.94509, 37.819121]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54101",
      state: "54",
      county: "101",
      name: "Webster County",
      censusArea: 553.468,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.436514, 38.267334],
          [-80.609378, 38.358606],
          [-80.678291, 38.503492],
          [-80.650786, 38.52659],
          [-80.508655, 38.645553],
          [-80.457332, 38.739169],
          [-80.444673, 38.726729],
          [-80.426641, 38.731004],
          [-80.404748, 38.723439],
          [-80.393063, 38.727571],
          [-80.280059, 38.694867],
          [-80.183931, 38.525386],
          [-80.245518, 38.388457],
          [-80.352171, 38.345337],
          [-80.360048, 38.225845],
          [-80.436514, 38.267334]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55003",
      state: "55",
      county: "003",
      name: "Ashland County",
      censusArea: 1045.035,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-90.456677, 47.016737],
            [-90.4553, 47.02375],
            [-90.455251, 47.024],
            [-90.456008, 47.032085],
            [-90.45638, 47.036062],
            [-90.457076, 47.043506],
            [-90.457128, 47.044058],
            [-90.455502, 47.051331],
            [-90.455113, 47.053071],
            [-90.455024, 47.05347],
            [-90.449572, 47.064965],
            [-90.449083, 47.065995],
            [-90.449075, 47.066013],
            [-90.449024, 47.066045],
            [-90.448439, 47.066415],
            [-90.441968, 47.070511],
            [-90.438734, 47.072557],
            [-90.437271, 47.073483],
            [-90.435387, 47.073868],
            [-90.434762, 47.073996],
            [-90.434543, 47.07404],
            [-90.42837, 47.075302],
            [-90.427087, 47.075564],
            [-90.425277, 47.075934],
            [-90.417272, 47.07757],
            [-90.400234, 47.077263],
            [-90.398913, 47.077239],
            [-90.395367, 47.077175],
            [-90.39517, 47.077017],
            [-90.39495, 47.076841],
            [-90.393848, 47.075956],
            [-90.39383, 47.075941],
            [-90.393723, 47.074859],
            [-90.393115, 47.068687],
            [-90.393111, 47.068644],
            [-90.393077, 47.068304],
            [-90.393035, 47.067877],
            [-90.393241, 47.066753],
            [-90.393277, 47.066557],
            [-90.393342, 47.066204],
            [-90.393351, 47.066153],
            [-90.393507, 47.065302],
            [-90.394212, 47.061452],
            [-90.394312, 47.060909],
            [-90.395988, 47.056972],
            [-90.396177, 47.056528],
            [-90.396275, 47.056299],
            [-90.400067, 47.047391],
            [-90.400219, 47.047034],
            [-90.400365, 47.046691],
            [-90.400409, 47.046588],
            [-90.400427, 47.046463],
            [-90.403306, 47.026693],
            [-90.403321, 47.02659],
            [-90.403499, 47.025366],
            [-90.403908, 47.024864],
            [-90.405033, 47.023482],
            [-90.409437, 47.018072],
            [-90.411972, 47.014958],
            [-90.412886, 47.013836],
            [-90.413133, 47.013533],
            [-90.413428, 47.01317],
            [-90.425351, 47.007526],
            [-90.42732, 47.006593],
            [-90.428273, 47.006142],
            [-90.428438, 47.006064],
            [-90.429397, 47.00561],
            [-90.429539, 47.005565],
            [-90.448857, 46.999452],
            [-90.453619, 46.997946],
            [-90.457445, 46.996735],
            [-90.464079, 46.994636],
            [-90.464087, 46.994681],
            [-90.465465, 47.002593],
            [-90.465321, 47.002777],
            [-90.457688, 47.012484],
            [-90.457453, 47.012783],
            [-90.457315, 47.013488],
            [-90.457272, 47.013707],
            [-90.456677, 47.016737]
          ]
        ],
        [
          [
            [-90.636718, 46.861323],
            [-90.636386, 46.861705],
            [-90.634048, 46.864396],
            [-90.622048, 46.872872],
            [-90.616452, 46.874466],
            [-90.602619, 46.872715],
            [-90.602552, 46.872706],
            [-90.601924, 46.872099],
            [-90.593899, 46.864344],
            [-90.58819, 46.858827],
            [-90.587392, 46.858056],
            [-90.587306, 46.858015],
            [-90.570006, 46.849696],
            [-90.569868, 46.849398],
            [-90.568938, 46.847391],
            [-90.568874, 46.847252],
            [-90.569108, 46.847112],
            [-90.570652, 46.846192],
            [-90.578263, 46.841653],
            [-90.57839, 46.841616],
            [-90.582347, 46.840454],
            [-90.584489, 46.839825],
            [-90.58505, 46.839789],
            [-90.589921, 46.839476],
            [-90.601288, 46.838747],
            [-90.60329, 46.838618],
            [-90.609501, 46.838219],
            [-90.610306, 46.838168],
            [-90.613569, 46.837958],
            [-90.613822, 46.837942],
            [-90.61424, 46.837815],
            [-90.62548, 46.834395],
            [-90.658031, 46.824493],
            [-90.673838, 46.819684],
            [-90.676133, 46.818986],
            [-90.683356, 46.813275],
            [-90.685753, 46.805003],
            [-90.670438, 46.799633],
            [-90.670049, 46.799496],
            [-90.665389, 46.799394],
            [-90.65531, 46.799173],
            [-90.654861, 46.798907],
            [-90.65455, 46.798723],
            [-90.652916, 46.797755],
            [-90.652906, 46.797707],
            [-90.652343, 46.795088],
            [-90.652236, 46.794589],
            [-90.652219, 46.794511],
            [-90.652591, 46.794083],
            [-90.653417, 46.793131],
            [-90.654103, 46.792342],
            [-90.655339, 46.790919],
            [-90.655375, 46.790877],
            [-90.655483, 46.790753],
            [-90.655611, 46.790606],
            [-90.655682, 46.790524],
            [-90.656358, 46.789745],
            [-90.657179, 46.7888],
            [-90.65892, 46.7885],
            [-90.660541, 46.788221],
            [-90.661154, 46.788116],
            [-90.661517, 46.788054],
            [-90.66326, 46.787754],
            [-90.679469, 46.784964],
            [-90.68763, 46.78356],
            [-90.688602, 46.783393],
            [-90.689259, 46.78328],
            [-90.691473, 46.782899],
            [-90.692974, 46.782641],
            [-90.696465, 46.78204],
            [-90.696934, 46.782119],
            [-90.706594, 46.783752],
            [-90.716456, 46.785418],
            [-90.723938, 46.781737],
            [-90.734915, 46.772015],
            [-90.73884, 46.768539],
            [-90.739425, 46.768021],
            [-90.758019, 46.757969],
            [-90.7625, 46.755547],
            [-90.763647, 46.754927],
            [-90.765221, 46.754534],
            [-90.766611, 46.754187],
            [-90.771228, 46.753034],
            [-90.774436, 46.753086],
            [-90.787751, 46.753301],
            [-90.78781, 46.753302],
            [-90.788304, 46.75331],
            [-90.788515, 46.753313],
            [-90.78843, 46.753619],
            [-90.788285, 46.754145],
            [-90.787554, 46.756786],
            [-90.785415, 46.76452],
            [-90.783086, 46.772939],
            [-90.788926, 46.779191],
            [-90.789243, 46.779529],
            [-90.790965, 46.781373],
            [-90.790992, 46.781534],
            [-90.791085, 46.782097],
            [-90.791358, 46.783752],
            [-90.791392, 46.783955],
            [-90.791517, 46.784713],
            [-90.791562, 46.784983],
            [-90.790231, 46.786103],
            [-90.781682, 46.788835],
            [-90.759119, 46.796044],
            [-90.758586, 46.796214],
            [-90.74572, 46.798228],
            [-90.740603, 46.799029],
            [-90.736609, 46.799654],
            [-90.733231, 46.800183],
            [-90.733001, 46.800219],
            [-90.732823, 46.800385],
            [-90.729581, 46.803395],
            [-90.728546, 46.804356],
            [-90.728384, 46.805707],
            [-90.727952, 46.809319],
            [-90.727818, 46.810434],
            [-90.720932, 46.815897],
            [-90.712381, 46.820743],
            [-90.704619, 46.823508],
            [-90.694496, 46.827114],
            [-90.682954, 46.831225],
            [-90.675364, 46.833929],
            [-90.672908, 46.835202],
            [-90.656946, 46.843476],
            [-90.656109, 46.844108],
            [-90.655873, 46.844286],
            [-90.655691, 46.844424],
            [-90.654497, 46.845325],
            [-90.654472, 46.845344],
            [-90.651804, 46.847359],
            [-90.651231, 46.847792],
            [-90.650914, 46.848031],
            [-90.647486, 46.85062],
            [-90.646982, 46.851001],
            [-90.643218, 46.853844],
            [-90.637885, 46.859981],
            [-90.637091, 46.860895],
            [-90.636718, 46.861323]
          ]
        ],
        [
          [
            [-90.579568, 46.91823],
            [-90.592933, 46.915558],
            [-90.623547, 46.909438],
            [-90.624635, 46.909221],
            [-90.625685, 46.909011],
            [-90.637124, 46.906724],
            [-90.64412, 46.908373],
            [-90.644144, 46.908379],
            [-90.644218, 46.908455],
            [-90.654796, 46.919249],
            [-90.654805, 46.919259],
            [-90.654797, 46.919276],
            [-90.651834, 46.925267],
            [-90.651043, 46.926074],
            [-90.646351, 46.93086],
            [-90.64534, 46.931893],
            [-90.64479, 46.932453],
            [-90.643966, 46.933294],
            [-90.643328, 46.933944],
            [-90.634507, 46.942944],
            [-90.633915, 46.943113],
            [-90.601876, 46.952251],
            [-90.601153, 46.952457],
            [-90.572383, 46.958835],
            [-90.530597, 46.968099],
            [-90.528804, 46.968497],
            [-90.528182, 46.968396],
            [-90.524874, 46.96786],
            [-90.523298, 46.967604],
            [-90.521791, 46.966804],
            [-90.514782, 46.963084],
            [-90.514616, 46.962996],
            [-90.513328, 46.962312],
            [-90.512884, 46.962076],
            [-90.512211, 46.961719],
            [-90.511623, 46.961407],
            [-90.50988, 46.959108],
            [-90.509372, 46.958438],
            [-90.508157, 46.956836],
            [-90.511124, 46.952885],
            [-90.524018, 46.935714],
            [-90.524056, 46.935664],
            [-90.532652, 46.932489],
            [-90.535368, 46.931486],
            [-90.539947, 46.92785],
            [-90.543583, 46.923002],
            [-90.543852, 46.918289],
            [-90.545105, 46.917287],
            [-90.545872, 46.916673],
            [-90.549104, 46.915461],
            [-90.550371, 46.915829],
            [-90.55328, 46.916674],
            [-90.557319, 46.918693],
            [-90.569169, 46.920309],
            [-90.579568, 46.91823]
          ]
        ],
        [
          [
            [-90.927387, 46.585132],
            [-90.92356, 46.584267],
            [-90.923438, 46.584239],
            [-90.921483, 46.583797],
            [-90.920411, 46.583555],
            [-90.920132, 46.583492],
            [-90.91992, 46.583444],
            [-90.918414, 46.583103],
            [-90.918352, 46.583089],
            [-90.918266, 46.58307],
            [-90.916982, 46.583014],
            [-90.909815, 46.582703],
            [-90.906058, 46.58343],
            [-90.905572, 46.583524],
            [-90.901918, 46.585519],
            [-90.901725, 46.585624],
            [-90.891944, 46.590964],
            [-90.886197, 46.594102],
            [-90.885103, 46.5947],
            [-90.885007, 46.594752],
            [-90.873154, 46.601223],
            [-90.86712, 46.601911],
            [-90.864284, 46.602965],
            [-90.858725, 46.605031],
            [-90.856811, 46.605742],
            [-90.856244, 46.605953],
            [-90.85609, 46.60601],
            [-90.854575, 46.606573],
            [-90.85338, 46.607017],
            [-90.852153, 46.607473],
            [-90.851889, 46.607571],
            [-90.849684, 46.608391],
            [-90.843225, 46.610791],
            [-90.843189, 46.610805],
            [-90.842058, 46.611225],
            [-90.841708, 46.611355],
            [-90.837306, 46.612991],
            [-90.834259, 46.614123],
            [-90.831868, 46.615012],
            [-90.831226, 46.61525],
            [-90.830011, 46.615702],
            [-90.82907, 46.616051],
            [-90.829031, 46.616066],
            [-90.794775, 46.624941],
            [-90.792583, 46.625938],
            [-90.772455, 46.635097],
            [-90.770192, 46.636127],
            [-90.768381, 46.637362],
            [-90.755381, 46.646225],
            [-90.755287, 46.646289],
            [-90.755289, 46.646323],
            [-90.756312, 46.66182],
            [-90.756495, 46.664591],
            [-90.74809, 46.669817],
            [-90.748026, 46.669968],
            [-90.739565, 46.689943],
            [-90.739549, 46.689981],
            [-90.73726, 46.692267],
            [-90.724924, 46.684186],
            [-90.705375, 46.671381],
            [-90.694721, 46.664402],
            [-90.654497, 46.63999],
            [-90.650949, 46.637837],
            [-90.627885, 46.623839],
            [-90.607649, 46.612186],
            [-90.60672, 46.611651],
            [-90.606177, 46.611339],
            [-90.599602, 46.607552],
            [-90.599375, 46.607422],
            [-90.59585, 46.605392],
            [-90.595583, 46.605238],
            [-90.591894, 46.603114],
            [-90.590811, 46.60249],
            [-90.590712, 46.602433],
            [-90.586249, 46.599863],
            [-90.581408, 46.597541],
            [-90.580191, 46.596958],
            [-90.579422, 46.596589],
            [-90.563668, 46.589034],
            [-90.561966, 46.588218],
            [-90.561126, 46.587816],
            [-90.558141, 46.586384],
            [-90.556224, 46.58588],
            [-90.549596, 46.584138],
            [-90.55218, 46.240851],
            [-90.425978, 46.24045],
            [-90.426274, 46.15405],
            [-90.303072, 46.154849],
            [-90.302063, 45.981349],
            [-90.676968, 45.981554],
            [-90.925174, 45.981055],
            [-90.924583, 46.154616],
            [-90.927387, 46.585132]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55005",
      state: "55",
      county: "005",
      name: "Barron County",
      censusArea: 862.711,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.665647, 45.207991],
          [-92.156465, 45.209554],
          [-92.154888, 45.639742],
          [-92.031417, 45.639928],
          [-91.540294, 45.637605],
          [-91.541322, 45.292015],
          [-91.542234, 45.206538],
          [-91.665647, 45.207991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55007",
      state: "55",
      county: "007",
      name: "Bayfield County",
      censusArea: 1477.863,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.833091, 46.954686],
          [-90.805028, 46.937987],
          [-90.786595, 46.927019],
          [-90.786502, 46.926964],
          [-90.78601, 46.926672],
          [-90.785606, 46.926431],
          [-90.760095, 46.903296],
          [-90.759364, 46.902634],
          [-90.75563, 46.899247],
          [-90.754552, 46.89827],
          [-90.754388, 46.898037],
          [-90.752725, 46.89568],
          [-90.751329, 46.893702],
          [-90.751048, 46.893305],
          [-90.750858, 46.893035],
          [-90.750871, 46.892643],
          [-90.750889, 46.892136],
          [-90.750952, 46.890293],
          [-90.751031, 46.887963],
          [-90.751151, 46.887863],
          [-90.754692, 46.884915],
          [-90.754734, 46.88488],
          [-90.754753, 46.884875],
          [-90.761295, 46.883238],
          [-90.761567, 46.88317],
          [-90.77017, 46.876296],
          [-90.77024, 46.876184],
          [-90.780972, 46.858989],
          [-90.781011, 46.858911],
          [-90.78804, 46.844886],
          [-90.792034, 46.836916],
          [-90.793713, 46.833566],
          [-90.795693, 46.829614],
          [-90.795957, 46.829087],
          [-90.796437, 46.828129],
          [-90.797404, 46.8262],
          [-90.798545, 46.823922],
          [-90.798936, 46.823143],
          [-90.801041, 46.821626],
          [-90.80137, 46.821389],
          [-90.80927, 46.815695],
          [-90.821967, 46.806545],
          [-90.825696, 46.803858],
          [-90.826337, 46.80211],
          [-90.82735, 46.799345],
          [-90.828057, 46.797415],
          [-90.829048, 46.79641],
          [-90.829933, 46.795513],
          [-90.830094, 46.795349],
          [-90.831645, 46.793777],
          [-90.835008, 46.790366],
          [-90.835028, 46.790346],
          [-90.835319, 46.790051],
          [-90.835607, 46.789759],
          [-90.847214, 46.789274],
          [-90.854916, 46.788952],
          [-90.855472, 46.788929],
          [-90.855746, 46.788918],
          [-90.856317, 46.788894],
          [-90.856531, 46.788885],
          [-90.856677, 46.788712],
          [-90.859999, 46.784769],
          [-90.863542, 46.780565],
          [-90.859724, 46.774433],
          [-90.859445, 46.773985],
          [-90.86088, 46.771079],
          [-90.862333, 46.768135],
          [-90.866337, 46.764626],
          [-90.866586, 46.764408],
          [-90.866694, 46.764361],
          [-90.874598, 46.760902],
          [-90.875925, 46.760321],
          [-90.885021, 46.756341],
          [-90.883443, 46.747255],
          [-90.883396, 46.746987],
          [-90.882099, 46.744622],
          [-90.881622, 46.743753],
          [-90.87881, 46.738629],
          [-90.878343, 46.737777],
          [-90.876538, 46.734487],
          [-90.876426, 46.734284],
          [-90.876334, 46.734115],
          [-90.876039, 46.733577],
          [-90.875829, 46.733195],
          [-90.871612, 46.725509],
          [-90.870577, 46.723623],
          [-90.870396, 46.723293],
          [-90.869461, 46.722039],
          [-90.868607, 46.720896],
          [-90.868531, 46.720794],
          [-90.868273, 46.720448],
          [-90.868024, 46.720115],
          [-90.863864, 46.714539],
          [-90.861353, 46.711173],
          [-90.860897, 46.710562],
          [-90.859797, 46.709088],
          [-90.859601, 46.708826],
          [-90.853225, 46.70028],
          [-90.852916, 46.699866],
          [-90.852704, 46.699582],
          [-90.852731, 46.699437],
          [-90.853644, 46.694464],
          [-90.853807, 46.693579],
          [-90.853829, 46.693457],
          [-90.853927, 46.693369],
          [-90.854539, 46.692822],
          [-90.854692, 46.692686],
          [-90.865494, 46.683033],
          [-90.865745, 46.682808],
          [-90.867423, 46.681309],
          [-90.867823, 46.680952],
          [-90.867981, 46.68081],
          [-90.868376, 46.680457],
          [-90.868468, 46.680375],
          [-90.870079, 46.679449],
          [-90.870532, 46.679189],
          [-90.870739, 46.67907],
          [-90.870956, 46.678945],
          [-90.885869, 46.670374],
          [-90.885943, 46.670353],
          [-90.896529, 46.667315],
          [-90.905273, 46.664807],
          [-90.905567, 46.664722],
          [-90.909176, 46.663687],
          [-90.909688, 46.66354],
          [-90.911281, 46.663083],
          [-90.911353, 46.662996],
          [-90.914619, 46.659054],
          [-90.915152, 46.65841],
          [-90.915367, 46.657615],
          [-90.920813, 46.637432],
          [-90.920835, 46.637351],
          [-90.92085, 46.636492],
          [-90.920867, 46.635515],
          [-90.920936, 46.631606],
          [-90.920936, 46.631584],
          [-90.924487, 46.625417],
          [-90.924489, 46.625415],
          [-90.926745, 46.622698],
          [-90.931623, 46.616822],
          [-90.93212, 46.616223],
          [-90.933208, 46.614913],
          [-90.93831, 46.608768],
          [-90.938617, 46.608398],
          [-90.93868, 46.608322],
          [-90.949532, 46.603019],
          [-90.949621, 46.602975],
          [-90.950215, 46.602248],
          [-90.950544, 46.601845],
          [-90.951418, 46.600774],
          [-90.951543, 46.600621],
          [-90.951528, 46.599827],
          [-90.951476, 46.597033],
          [-90.947572, 46.59351],
          [-90.947287, 46.593253],
          [-90.947159, 46.593138],
          [-90.942101, 46.588573],
          [-90.94193, 46.588419],
          [-90.941374, 46.588293],
          [-90.941102, 46.588232],
          [-90.927387, 46.585132],
          [-90.924583, 46.154616],
          [-91.551282, 46.157045],
          [-91.551408, 46.755666],
          [-91.543057, 46.755153],
          [-91.537583, 46.754817],
          [-91.537115, 46.754788],
          [-91.535764, 46.754926],
          [-91.524766, 46.756052],
          [-91.517712, 46.756774],
          [-91.511077, 46.757453],
          [-91.510595, 46.757614],
          [-91.507819, 46.758538],
          [-91.507586, 46.758615],
          [-91.50716, 46.758757],
          [-91.500042, 46.761128],
          [-91.499696, 46.761243],
          [-91.493643, 46.765757],
          [-91.493037, 46.76621],
          [-91.492493, 46.766615],
          [-91.492429, 46.766663],
          [-91.491744, 46.766732],
          [-91.489125, 46.766997],
          [-91.488247, 46.767086],
          [-91.472097, 46.768717],
          [-91.470181, 46.768911],
          [-91.470124, 46.768923],
          [-91.467168, 46.769546],
          [-91.462774, 46.770471],
          [-91.44957, 46.773252],
          [-91.449327, 46.773303],
          [-91.436955, 46.777586],
          [-91.428526, 46.780504],
          [-91.426491, 46.781208],
          [-91.423713, 46.78217],
          [-91.411799, 46.78964],
          [-91.398256, 46.791213],
          [-91.39714, 46.791047],
          [-91.396959, 46.79102],
          [-91.396261, 46.790917],
          [-91.394764, 46.790694],
          [-91.391469, 46.790205],
          [-91.390774, 46.790316],
          [-91.386155, 46.791057],
          [-91.380577, 46.791951],
          [-91.369387, 46.793745],
          [-91.368819, 46.793836],
          [-91.367498, 46.794545],
          [-91.366888, 46.794872],
          [-91.365974, 46.795362],
          [-91.365536, 46.795597],
          [-91.365277, 46.795736],
          [-91.363644, 46.796612],
          [-91.360804, 46.798136],
          [-91.359434, 46.799612],
          [-91.35533, 46.804035],
          [-91.352191, 46.807417],
          [-91.33825, 46.817704],
          [-91.33757, 46.817969],
          [-91.330463, 46.820735],
          [-91.330433, 46.820746],
          [-91.328848, 46.821363],
          [-91.326045, 46.822454],
          [-91.322202, 46.82395],
          [-91.32101, 46.824414],
          [-91.315061, 46.826729],
          [-91.314815, 46.826825],
          [-91.30705, 46.829007],
          [-91.305487, 46.829446],
          [-91.304512, 46.82972],
          [-91.303594, 46.829978],
          [-91.302295, 46.830343],
          [-91.301879, 46.830384],
          [-91.289541, 46.831604],
          [-91.278558, 46.832689],
          [-91.266404, 46.833891],
          [-91.265866, 46.833944],
          [-91.265816, 46.83396],
          [-91.263725, 46.834632],
          [-91.263445, 46.834722],
          [-91.256873, 46.836833],
          [-91.256705, 46.836887],
          [-91.256563, 46.836989],
          [-91.250806, 46.841135],
          [-91.249471, 46.842531],
          [-91.23773, 46.854809],
          [-91.235283, 46.857368],
          [-91.232733, 46.860035],
          [-91.226796, 46.86361],
          [-91.214886, 46.866137],
          [-91.211647, 46.866824],
          [-91.211113, 46.866696],
          [-91.207524, 46.865835],
          [-91.204839, 46.859727],
          [-91.204439, 46.858816],
          [-91.200107, 46.854017],
          [-91.178292, 46.844259],
          [-91.168297, 46.844727],
          [-91.167601, 46.84476],
          [-91.156108, 46.855414],
          [-91.148026, 46.862906],
          [-91.147837, 46.863082],
          [-91.144266, 46.870301],
          [-91.143877, 46.870576],
          [-91.14385, 46.870595],
          [-91.140301, 46.873105],
          [-91.140165, 46.873201],
          [-91.139758, 46.873148],
          [-91.134668, 46.87249],
          [-91.133772, 46.871043],
          [-91.133337, 46.870341],
          [-91.133674, 46.869348],
          [-91.134184, 46.867843],
          [-91.134724, 46.86625],
          [-91.134882, 46.865784],
          [-91.136512, 46.860975],
          [-91.134977, 46.859023],
          [-91.134948, 46.858986],
          [-91.134732, 46.858935],
          [-91.12353, 46.856273],
          [-91.123109, 46.856173],
          [-91.11854, 46.856548],
          [-91.107323, 46.857469],
          [-91.10549, 46.85762],
          [-91.09878, 46.86056],
          [-91.098346, 46.86075],
          [-91.098125, 46.860847],
          [-91.09755, 46.861098],
          [-91.096565, 46.86153],
          [-91.096342, 46.862965],
          [-91.096028, 46.864987],
          [-91.095058, 46.871234],
          [-91.094828, 46.872714],
          [-91.094724, 46.873383],
          [-91.09462, 46.87405],
          [-91.094498, 46.874837],
          [-91.094096, 46.877423],
          [-91.093714, 46.879882],
          [-91.090916, 46.88267],
          [-91.088721, 46.882877],
          [-91.085077, 46.88322],
          [-91.080951, 46.883609],
          [-91.072584, 46.880126],
          [-91.069331, 46.878772],
          [-91.06822, 46.878309],
          [-91.066193, 46.87871],
          [-91.056258, 46.880678],
          [-91.052991, 46.881325],
          [-91.050153, 46.883037],
          [-91.049232, 46.883593],
          [-91.04922, 46.8836],
          [-91.042992, 46.887358],
          [-91.03989, 46.88923],
          [-91.036622, 46.893594],
          [-91.036193, 46.895523],
          [-91.035936, 46.896679],
          [-91.034518, 46.903053],
          [-91.033447, 46.903642],
          [-91.032508, 46.904158],
          [-91.030583, 46.905215],
          [-91.024009, 46.908827],
          [-91.023976, 46.908845],
          [-91.021538, 46.910185],
          [-91.01967, 46.911211],
          [-91.019141, 46.911502],
          [-91.018045, 46.911872],
          [-91.018024, 46.911879],
          [-91.016237, 46.912481],
          [-91.016184, 46.912499],
          [-91.010689, 46.914352],
          [-91.007997, 46.915259],
          [-91.005872, 46.915976],
          [-91.005199, 46.916203],
          [-91.004892, 46.916192],
          [-91.004752, 46.916187],
          [-90.998848, 46.915975],
          [-90.997943, 46.916367],
          [-90.996825, 46.916851],
          [-90.995149, 46.917577],
          [-90.987013, 46.923776],
          [-90.985815, 46.924689],
          [-90.984617, 46.925602],
          [-90.983192, 46.927662],
          [-90.980235, 46.931937],
          [-90.973755, 46.941304],
          [-90.968419, 46.94391],
          [-90.964865, 46.94378],
          [-90.964072, 46.943369],
          [-90.954612, 46.938468],
          [-90.954537, 46.938429],
          [-90.953865, 46.938283],
          [-90.953685, 46.938244],
          [-90.94056, 46.935394],
          [-90.92204, 46.931372],
          [-90.921811, 46.931322],
          [-90.921382, 46.931434],
          [-90.914044, 46.933346],
          [-90.913838, 46.9334],
          [-90.908654, 46.941221],
          [-90.908598, 46.941305],
          [-90.880358, 46.957661],
          [-90.879621, 46.958088],
          [-90.877324, 46.95891],
          [-90.877165, 46.958967],
          [-90.877092, 46.958993],
          [-90.876778, 46.959106],
          [-90.876544, 46.959189],
          [-90.876213, 46.959308],
          [-90.876211, 46.959309],
          [-90.876006, 46.959382],
          [-90.875995, 46.959386],
          [-90.875635, 46.959515],
          [-90.871126, 46.961129],
          [-90.855874, 46.962232],
          [-90.855165, 46.962045],
          [-90.838814, 46.957728],
          [-90.837973, 46.957506],
          [-90.837716, 46.957438],
          [-90.837617, 46.957379],
          [-90.833091, 46.954686]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55017",
      state: "55",
      county: "017",
      name: "Chippewa County",
      censusArea: 1008.373,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.923362, 45.031143],
          [-90.922838, 44.999966],
          [-90.923393, 44.99552],
          [-90.922395, 44.954816],
          [-90.922669, 44.944459],
          [-90.922247, 44.857309],
          [-91.650455, 44.855951],
          [-91.665647, 45.207991],
          [-91.542234, 45.206538],
          [-91.541322, 45.292015],
          [-90.925223, 45.291965],
          [-90.923362, 45.031143]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55025",
      state: "55",
      county: "025",
      name: "Dane County",
      censusArea: 1197.239,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.369127, 42.845046],
          [-89.369069, 42.856471],
          [-89.838167, 42.857397],
          [-89.838135, 43.206057],
          [-89.720295, 43.292928],
          [-89.009139, 43.28483],
          [-89.00892, 43.197721],
          [-89.013489, 42.847347],
          [-89.369127, 42.845046]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49029",
      state: "49",
      county: "029",
      name: "Morgan County",
      censusArea: 609.196,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.26482, 41.144253],
          [-111.508646, 41.062763],
          [-111.593025, 40.993044],
          [-111.479647, 40.858366],
          [-111.519624, 40.791758],
          [-111.641022, 40.799084],
          [-111.738575, 40.861055],
          [-111.815186, 40.957533],
          [-111.856305, 41.139118],
          [-111.858392, 41.197383],
          [-111.67819, 41.181028],
          [-111.595856, 41.239139],
          [-111.49663, 41.226877],
          [-111.42056, 41.36137],
          [-111.331662, 41.357364],
          [-111.221378, 41.212852],
          [-111.26482, 41.144253]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49031",
      state: "49",
      county: "031",
      name: "Piute County",
      censusArea: 757.791,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.518504, 38.509852],
          [-111.766741, 38.501977],
          [-111.848871, 38.425001],
          [-111.8428, 38.15128],
          [-112.443821, 38.149942],
          [-112.356853, 38.22475],
          [-112.335369, 38.332955],
          [-112.518504, 38.509852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49035",
      state: "49",
      county: "035",
      name: "Salt Lake County",
      censusArea: 742.282,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.913848, 40.452454],
          [-111.91912, 40.443191],
          [-111.931555, 40.433548],
          [-111.978236, 40.41727],
          [-112.173208, 40.466923],
          [-112.175465, 40.604476],
          [-112.222654, 40.72214],
          [-112.22736, 40.72518],
          [-112.26351, 40.77345],
          [-112.006597, 40.921845],
          [-111.915865, 40.822045],
          [-111.738575, 40.861055],
          [-111.641022, 40.799084],
          [-111.553105, 40.609362],
          [-111.564507, 40.584767],
          [-111.59393, 40.577071],
          [-111.913848, 40.452454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49051",
      state: "49",
      county: "051",
      name: "Wasatch County",
      censusArea: 1175.501,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.611734, 40.431466],
          [-111.59393, 40.577071],
          [-111.564507, 40.584767],
          [-111.553105, 40.609362],
          [-111.489582, 40.599157],
          [-111.393697, 40.690384],
          [-111.374868, 40.632703],
          [-111.15136, 40.548352],
          [-111.024396, 40.577504],
          [-110.901931, 40.67817],
          [-110.891872, 39.899146],
          [-111.082455, 39.900155],
          [-111.083061, 39.943198],
          [-111.205772, 40.047368],
          [-111.246207, 40.047466],
          [-111.225507, 40.167324],
          [-111.278846, 40.258671],
          [-111.31483, 40.287566],
          [-111.426482, 40.286906],
          [-111.480337, 40.362169],
          [-111.555235, 40.362696],
          [-111.611734, 40.431466]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50005",
      state: "50",
      county: "005",
      name: "Caledonia County",
      censusArea: 648.864,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.042087, 44.157633],
          [-72.17026, 44.192053],
          [-72.30498, 44.183149],
          [-72.368353, 44.204057],
          [-72.223689, 44.424573],
          [-72.434315, 44.506098],
          [-72.374786, 44.584125],
          [-72.262965, 44.542336],
          [-72.114503, 44.749624],
          [-72.012504, 44.698821],
          [-71.939936, 44.769032],
          [-71.837294, 44.716637],
          [-71.910282, 44.647038],
          [-71.842732, 44.611165],
          [-71.908657, 44.54708],
          [-71.856637, 44.496178],
          [-71.933288, 44.441174],
          [-71.849465, 44.359295],
          [-72.002314, 44.324871],
          [-72.012173, 44.321408],
          [-72.05888, 44.28624],
          [-72.065434, 44.277235],
          [-72.067774, 44.270976],
          [-72.064285, 44.187888],
          [-72.053021, 44.167903],
          [-72.042087, 44.157633]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50019",
      state: "50",
      county: "019",
      name: "Orleans County",
      censusArea: 693.268,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.578161, 44.781578],
          [-72.532638, 44.833346],
          [-72.554253, 45.011794],
          [-72.342453, 45.005354],
          [-72.103058, 45.005598],
          [-71.897622, 45.009172],
          [-71.92851, 44.907298],
          [-71.874433, 44.876382],
          [-71.974318, 44.786519],
          [-71.939936, 44.769032],
          [-72.012504, 44.698821],
          [-72.114503, 44.749624],
          [-72.262965, 44.542336],
          [-72.374786, 44.584125],
          [-72.482302, 44.633928],
          [-72.417315, 44.722893],
          [-72.578161, 44.781578]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51007",
      state: "51",
      county: "007",
      name: "Amelia County",
      censusArea: 355.269,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.855148, 37.418363],
          [-77.851804, 37.354867],
          [-77.75862, 37.268717],
          [-77.65061, 37.265112],
          [-77.795926, 37.192592],
          [-77.856209, 37.209958],
          [-78.231234, 37.29622],
          [-78.235039, 37.368102],
          [-78.132048, 37.454686],
          [-78.014437, 37.492081],
          [-77.899761, 37.475422],
          [-77.855148, 37.418363]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51017",
      state: "51",
      county: "017",
      name: "Bath County",
      censusArea: 529.158,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.055278, 37.951702],
          [-80.002507, 37.992767],
          [-79.978427, 38.029082],
          [-79.959844, 38.063697],
          [-79.933911, 38.099168],
          [-79.931034, 38.101402],
          [-79.938394, 38.130563],
          [-79.933751, 38.135508],
          [-79.928747, 38.144436],
          [-79.928683, 38.144928],
          [-79.925512, 38.150237],
          [-79.925251, 38.150465],
          [-79.921026, 38.179954],
          [-79.921196, 38.180378],
          [-79.917061, 38.183741],
          [-79.916174, 38.184386],
          [-79.892345, 38.202397],
          [-79.891999, 38.203378],
          [-79.794568, 38.264856],
          [-79.706196, 38.221192],
          [-79.512158, 38.180419],
          [-79.436942, 38.163243],
          [-79.482405, 38.086105],
          [-79.650757, 37.871566],
          [-79.881433, 37.892026],
          [-79.935356, 37.954365],
          [-80.055278, 37.951702]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51029",
      state: "51",
      county: "029",
      name: "Buckingham County",
      censusArea: 579.657,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.491567, 37.796712],
          [-78.405596, 37.735263],
          [-78.239748, 37.690495],
          [-78.249021, 37.635546],
          [-78.465089, 37.339677],
          [-78.594436, 37.397974],
          [-78.825238, 37.552542],
          [-78.82124, 37.631842],
          [-78.739437, 37.631542],
          [-78.644036, 37.733143],
          [-78.510971, 37.757594],
          [-78.491567, 37.796712]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51043",
      state: "51",
      county: "043",
      name: "Clarke County",
      censusArea: 176.177,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.033183, 39.264624],
          [-78.032841, 39.264403],
          [-77.828157, 39.132329],
          [-77.961956, 39.013841],
          [-77.965138, 39.008927],
          [-77.990379, 38.996331],
          [-77.98978, 38.993834],
          [-78.004167, 38.979479],
          [-78.151981, 39.036277],
          [-78.033183, 39.264624]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51047",
      state: "51",
      county: "047",
      name: "Culpeper County",
      censusArea: 379.234,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.935454, 38.695853],
          [-77.877169, 38.584891],
          [-77.735536, 38.413116],
          [-77.634835, 38.409713],
          [-77.632438, 38.40737],
          [-77.634216, 38.398366],
          [-77.63271, 38.392627],
          [-77.62141, 38.388727],
          [-77.618727, 38.367835],
          [-77.703189, 38.360598],
          [-77.891822, 38.388953],
          [-78.094571, 38.311361],
          [-78.096413, 38.404632],
          [-78.231978, 38.53255],
          [-78.168792, 38.523809],
          [-77.935454, 38.695853]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51063",
      state: "51",
      county: "063",
      name: "Floyd County",
      censusArea: 380.42,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.235455, 36.87213],
          [-80.386909, 36.797676],
          [-80.46418, 36.708718],
          [-80.633987, 36.93178],
          [-80.543933, 36.984639],
          [-80.347447, 37.021842],
          [-80.178032, 37.113435],
          [-80.172264, 37.108156],
          [-80.128119, 37.122912],
          [-80.121479, 36.991428],
          [-80.235455, 36.87213]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51075",
      state: "51",
      county: "075",
      name: "Goochland County",
      censusArea: 281.415,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.159278, 37.748528],
          [-78.151822, 37.770443],
          [-78.06331, 37.905108],
          [-77.948552, 37.846338],
          [-77.888908, 37.760808],
          [-77.79692, 37.729069],
          [-77.630145, 37.706985],
          [-77.653987, 37.562631],
          [-77.814415, 37.606375],
          [-77.923063, 37.691369],
          [-77.957822, 37.624882],
          [-78.072256, 37.657357],
          [-78.159278, 37.748528]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51101",
      state: "51",
      county: "101",
      name: "King William County",
      censusArea: 273.941,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.345229, 37.789388],
          [-77.243162, 37.900241],
          [-77.181418, 37.891654],
          [-77.033273, 37.720809],
          [-76.915586, 37.693325],
          [-76.777729, 37.554446],
          [-76.792184, 37.517594],
          [-76.853577, 37.572627],
          [-76.981158, 37.567113],
          [-77.123675, 37.626881],
          [-77.29247, 37.714682],
          [-77.345229, 37.789388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51111",
      state: "51",
      county: "111",
      name: "Lunenburg County",
      censusArea: 431.683,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.443319, 37.0794],
          [-78.239354, 37.120089],
          [-78.082652, 37.016406],
          [-78.003639, 37.022756],
          [-78.027411, 36.777858],
          [-78.241509, 36.807958],
          [-78.493028, 36.89122],
          [-78.443319, 37.0794]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51113",
      state: "51",
      county: "113",
      name: "Madison County",
      censusArea: 320.683,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.452918, 38.475508],
          [-78.33832, 38.627282],
          [-78.231978, 38.53255],
          [-78.096413, 38.404632],
          [-78.094571, 38.311361],
          [-78.220645, 38.230308],
          [-78.286968, 38.271533],
          [-78.430031, 38.36715],
          [-78.452918, 38.475508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01023",
      state: "01",
      county: "023",
      name: "Choctaw County",
      censusArea: 913.5,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.43865, 32.172806],
          [-88.431707, 32.227662],
          [-88.421453, 32.30868],
          [-87.930661, 32.310574],
          [-88.019145, 32.28524],
          [-88.007077, 32.185313],
          [-88.070615, 32.0617],
          [-88.07349, 31.990182],
          [-88.121414, 31.950256],
          [-88.180384, 31.814546],
          [-88.088288, 31.699303],
          [-88.464428, 31.697952],
          [-88.471106, 31.850949],
          [-88.471214, 31.851385],
          [-88.46996, 31.893759],
          [-88.468879, 31.930262],
          [-88.46866, 31.933173],
          [-88.455039, 32.039719],
          [-88.454959, 32.040576],
          [-88.43871, 32.172078],
          [-88.43865, 32.172806]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01025",
      state: "01",
      county: "025",
      name: "Clarke County",
      censusArea: 1238.465,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.944233, 31.989872],
          [-87.667769, 31.991355],
          [-87.667488, 31.958459],
          [-87.620112, 31.827123],
          [-87.50093, 31.829251],
          [-87.516131, 31.697824],
          [-87.566841, 31.697115],
          [-87.565413, 31.553573],
          [-87.603137, 31.409556],
          [-87.665572, 31.423166],
          [-87.784796, 31.324672],
          [-87.765152, 31.297346],
          [-87.913637, 31.187356],
          [-87.946588, 31.19293],
          [-87.959619, 31.316206],
          [-87.908068, 31.323041],
          [-87.906143, 31.491752],
          [-88.072234, 31.5945],
          [-88.078711, 31.669618],
          [-88.080629, 31.67785],
          [-88.086946, 31.686181],
          [-88.088288, 31.699303],
          [-88.180384, 31.814546],
          [-88.121414, 31.950256],
          [-88.07349, 31.990182],
          [-87.944233, 31.989872]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01029",
      state: "01",
      county: "029",
      name: "Cleburne County",
      censusArea: 560.1,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.337974, 33.653111],
          [-85.322497, 33.574167],
          [-85.314994, 33.535898],
          [-85.314091, 33.530218],
          [-85.313999, 33.529807],
          [-85.304439, 33.482884],
          [-85.643482, 33.495885],
          [-85.765427, 33.498593],
          [-85.782735, 33.469349],
          [-85.887782, 33.469427],
          [-85.85189, 33.498742],
          [-85.796852, 33.541849],
          [-85.796054, 33.55622],
          [-85.638579, 33.648413],
          [-85.638049, 33.773339],
          [-85.532482, 33.889152],
          [-85.530094, 33.941423],
          [-85.398837, 33.964129],
          [-85.386693, 33.901697],
          [-85.361844, 33.773951],
          [-85.360491, 33.767958],
          [-85.337974, 33.653111]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01031",
      state: "01",
      county: "031",
      name: "Coffee County",
      censusArea: 678.972,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.791402, 31.196349],
          [-86.193476, 31.192213],
          [-86.196365, 31.425431],
          [-86.193951, 31.440072],
          [-86.194784, 31.529949],
          [-86.14395, 31.537675],
          [-86.145895, 31.617741],
          [-85.789142, 31.617964],
          [-85.791402, 31.196349]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01037",
      state: "01",
      county: "037",
      name: "Coosa County",
      censusArea: 650.926,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.336776, 32.767469],
          [-86.336768, 32.753783],
          [-86.374974, 32.75358],
          [-86.457015, 32.813899],
          [-86.515959, 32.929361],
          [-86.517344, 33.020566],
          [-86.491029, 33.102944],
          [-86.226271, 33.104115],
          [-86.17437, 33.104394],
          [-86.00917, 33.09026],
          [-86.007187, 32.754984],
          [-86.31948, 32.753698],
          [-86.336776, 32.767469]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01047",
      state: "01",
      county: "047",
      name: "Dallas County",
      censusArea: 978.695,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.906956, 32.04797],
          [-87.178071, 32.047514],
          [-87.262853, 32.147007],
          [-87.472206, 32.264919],
          [-87.47308, 32.307614],
          [-87.421744, 32.308101],
          [-87.423153, 32.482965],
          [-87.116515, 32.487659],
          [-87.017762, 32.729532],
          [-87.01766, 32.663269],
          [-86.917595, 32.664169],
          [-86.890581, 32.502974],
          [-86.814912, 32.340803],
          [-86.864367, 32.274918],
          [-86.810313, 32.224747],
          [-86.908302, 32.225028],
          [-86.906956, 32.04797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01055",
      state: "01",
      county: "055",
      name: "Etowah County",
      censusArea: 534.991,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.843617, 34.200006],
          [-85.738975, 33.96846],
          [-85.96917, 33.914598],
          [-85.96917, 33.892898],
          [-85.98647, 33.893698],
          [-85.98637, 33.879198],
          [-85.99497, 33.879598],
          [-85.995169, 33.864897],
          [-86.021471, 33.866098],
          [-86.021271, 33.851498],
          [-86.048772, 33.852698],
          [-86.049072, 33.841598],
          [-86.065272, 33.842198],
          [-86.199164, 33.988918],
          [-86.325622, 33.940147],
          [-86.370152, 33.93977],
          [-86.332723, 33.986109],
          [-86.303516, 34.099073],
          [-86.206107, 34.17265],
          [-86.106086, 34.200756],
          [-85.843617, 34.200006]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01059",
      state: "01",
      county: "059",
      name: "Franklin County",
      censusArea: 633.821,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.155305, 34.463087],
          [-88.139988, 34.581703],
          [-87.529667, 34.567081],
          [-87.529722, 34.304598],
          [-87.634725, 34.306997],
          [-88.173632, 34.321054],
          [-88.16591, 34.380926],
          [-88.165634, 34.383102],
          [-88.155305, 34.463087]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01061",
      state: "01",
      county: "061",
      name: "Geneva County",
      censusArea: 574.408,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.497992, 30.996931],
          [-85.498272, 30.996928],
          [-86.035039, 30.99332],
          [-86.052462, 30.993247],
          [-86.056213, 30.993133],
          [-86.162886, 30.993682],
          [-86.168979, 30.993706],
          [-86.175204, 30.993798],
          [-86.180232, 30.994005],
          [-86.187246, 30.993992],
          [-86.193476, 31.192213],
          [-85.791402, 31.196349],
          [-85.710866, 31.195179],
          [-85.48576, 31.199886],
          [-85.488298, 30.997041],
          [-85.497992, 30.996931]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01067",
      state: "01",
      county: "067",
      name: "Henry County",
      censusArea: 561.75,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.046859, 31.517451],
          [-85.068065, 31.427586],
          [-85.087651, 31.308677],
          [-85.179131, 31.307675],
          [-85.417434, 31.314973],
          [-85.416437, 31.619466],
          [-85.416038, 31.706664],
          [-85.216076, 31.702409],
          [-85.124501, 31.763081],
          [-85.11893, 31.732664],
          [-85.12223, 31.722764],
          [-85.12653, 31.716764],
          [-85.12553, 31.694965],
          [-85.087029, 31.640966],
          [-85.065236, 31.624351],
          [-85.059534, 31.621717],
          [-85.057473, 31.618624],
          [-85.041305, 31.540987],
          [-85.046859, 31.517451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01071",
      state: "01",
      county: "071",
      name: "Jackson County",
      censusArea: 1077.868,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.863935, 34.988379],
          [-85.828724, 34.988165],
          [-85.824411, 34.988142],
          [-85.605165, 34.984678],
          [-85.595191, 34.924331],
          [-85.595163, 34.924171],
          [-85.583145, 34.860371],
          [-85.785471, 34.624584],
          [-86.00384, 34.480031],
          [-86.057712, 34.475994],
          [-86.139846, 34.533296],
          [-86.148464, 34.599069],
          [-86.16753, 34.599264],
          [-86.172856, 34.599318],
          [-86.174103, 34.599336],
          [-86.175324, 34.599354],
          [-86.178649, 34.599392],
          [-86.179053, 34.599396],
          [-86.182976, 34.599438],
          [-86.18299, 34.599438],
          [-86.199778, 34.59962],
          [-86.218086, 34.599817],
          [-86.218962, 34.599827],
          [-86.218975, 34.599827],
          [-86.221192, 34.599851],
          [-86.224237, 34.599884],
          [-86.233777, 34.599987],
          [-86.235077, 34.600001],
          [-86.237028, 34.600023],
          [-86.245611, 34.600116],
          [-86.24568, 34.600116],
          [-86.249983, 34.6001],
          [-86.252434, 34.600125],
          [-86.254082, 34.600143],
          [-86.257783, 34.600122],
          [-86.266148, 34.600075],
          [-86.269123, 34.600059],
          [-86.275775, 34.600023],
          [-86.27814, 34.600009],
          [-86.279503, 34.600004],
          [-86.284617, 34.599974],
          [-86.300533, 34.599888],
          [-86.305591, 34.59986],
          [-86.311677, 34.599827],
          [-86.316026, 34.599802],
          [-86.316036, 34.599802],
          [-86.317146, 34.599796],
          [-86.318251, 34.59979],
          [-86.318265, 34.59979],
          [-86.326958, 34.599744],
          [-86.326906, 34.599573],
          [-86.326853, 34.599403],
          [-86.359034, 34.63261],
          [-86.311274, 34.991098],
          [-85.863935, 34.988379]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01081",
      state: "01",
      county: "081",
      name: "Lee County",
      censusArea: 607.536,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.080288, 32.603577],
          [-85.079935, 32.602889],
          [-85.069583, 32.58393],
          [-85.044606, 32.559499],
          [-85.022509, 32.542923],
          [-85.0071, 32.523868],
          [-85.001324, 32.512973],
          [-85.001052, 32.510477],
          [-85.00103, 32.510157],
          [-85.059294, 32.472909],
          [-85.333843, 32.468639],
          [-85.330143, 32.410842],
          [-85.434045, 32.40984],
          [-85.438575, 32.49709],
          [-85.489348, 32.496937],
          [-85.695854, 32.595933],
          [-85.696755, 32.697429],
          [-85.593151, 32.72853],
          [-85.285043, 32.730731],
          [-85.136776, 32.746512],
          [-85.137136, 32.745168],
          [-85.114737, 32.685634],
          [-85.10479, 32.642542],
          [-85.082454, 32.607022],
          [-85.080288, 32.603577]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01085",
      state: "01",
      county: "085",
      name: "Lowndes County",
      censusArea: 715.911,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.857583, 31.962167],
          [-86.856576, 32.048352],
          [-86.906956, 32.04797],
          [-86.908302, 32.225028],
          [-86.810313, 32.224747],
          [-86.864367, 32.274918],
          [-86.814912, 32.340803],
          [-86.717897, 32.402814],
          [-86.496774, 32.344437],
          [-86.408816, 32.240851],
          [-86.406276, 32.050731],
          [-86.448198, 31.964629],
          [-86.857583, 31.962167]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01087",
      state: "01",
      county: "087",
      name: "Macon County",
      censusArea: 608.885,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.886148, 32.493053],
          [-85.797156, 32.494236],
          [-85.798585, 32.581089],
          [-85.695854, 32.595933],
          [-85.489348, 32.496937],
          [-85.438575, 32.49709],
          [-85.434045, 32.40984],
          [-85.433543, 32.234648],
          [-85.856218, 32.231975],
          [-85.919293, 32.274382],
          [-86.023012, 32.419978],
          [-85.89442, 32.447578],
          [-85.886148, 32.493053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01089",
      state: "01",
      county: "089",
      name: "Madison County",
      censusArea: 801.593,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.783628, 34.991925],
          [-86.677616, 34.99207],
          [-86.676726, 34.99207],
          [-86.67436, 34.992001],
          [-86.670853, 34.992],
          [-86.65961, 34.991792],
          [-86.641212, 34.99174],
          [-86.600039, 34.99124],
          [-86.588962, 34.991197],
          [-86.571217, 34.991011],
          [-86.555864, 34.990971],
          [-86.528485, 34.990677],
          [-86.467798, 34.990692],
          [-86.433927, 34.991085],
          [-86.397203, 34.99166],
          [-86.318761, 34.991147],
          [-86.311274, 34.991098],
          [-86.359034, 34.63261],
          [-86.326853, 34.599403],
          [-86.338181, 34.510103],
          [-86.423914, 34.479581],
          [-86.533445, 34.502795],
          [-86.550166, 34.545963],
          [-86.689353, 34.586425],
          [-86.790056, 34.55079],
          [-86.783628, 34.991925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05143",
      state: "05",
      county: "143",
      name: "Washington County",
      censusArea: 941.966,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.962892, 35.76093],
          [-94.124871, 35.750426],
          [-94.492932, 35.759166],
          [-94.493362, 35.761892],
          [-94.494549, 35.768303],
          [-94.499045, 35.79346],
          [-94.499647, 35.79691],
          [-94.500526, 35.802642],
          [-94.500764, 35.80382],
          [-94.501162, 35.80643],
          [-94.503011, 35.81721],
          [-94.504438, 35.826369],
          [-94.505642, 35.833628],
          [-94.507631, 35.845901],
          [-94.522658, 35.93425],
          [-94.522658, 35.934799],
          [-94.522634, 35.934892],
          [-94.52291, 35.936127],
          [-94.524344, 35.94405],
          [-94.52464, 35.945727],
          [-94.528162, 35.965665],
          [-94.528305, 35.966054],
          [-94.532071, 35.987852],
          [-94.533646, 35.996804],
          [-94.534852, 36.002678],
          [-94.535724, 36.007807],
          [-94.547715, 36.077271],
          [-94.547871, 36.078281],
          [-94.552184, 36.102235],
          [-94.391125, 36.099396],
          [-94.333565, 36.214727],
          [-93.973298, 36.203852],
          [-93.887775, 36.235046],
          [-93.944177, 36.091103],
          [-93.962892, 35.76093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05147",
      state: "05",
      county: "147",
      name: "Woodruff County",
      censusArea: 586.791,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.36856, 34.912735],
          [-91.363778, 35.051891],
          [-91.465114, 35.089454],
          [-91.371035, 35.262981],
          [-91.390375, 35.251624],
          [-91.438202, 35.369378],
          [-91.347257, 35.439106],
          [-91.252927, 35.445845],
          [-91.251555, 35.357561],
          [-91.039783, 35.354256],
          [-91.04267, 35.148189],
          [-91.04543, 35.00104],
          [-91.149842, 35.00247],
          [-91.293111, 34.985091],
          [-91.29479, 34.912452],
          [-91.36856, 34.912735]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06003",
      state: "06",
      county: "003",
      name: "Alpine County",
      censusArea: 738.332,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.072392, 38.702767],
          [-119.880303, 38.864776],
          [-119.904315, 38.933324],
          [-119.587679, 38.714734],
          [-119.587066, 38.714345],
          [-119.585437, 38.713212],
          [-119.619066, 38.603529],
          [-119.542862, 38.499694],
          [-119.639205, 38.32688],
          [-119.753481, 38.416759],
          [-119.884749, 38.356185],
          [-120.019951, 38.433521],
          [-120.072484, 38.509869],
          [-120.072392, 38.702767]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06007",
      state: "06",
      county: "007",
      name: "Butte County",
      censusArea: 1636.464,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.076695, 39.597264],
          [-121.149907, 39.526446],
          [-121.305122, 39.519585],
          [-121.333427, 39.468729],
          [-121.407527, 39.339754],
          [-121.62376, 39.295621],
          [-121.908269, 39.303878],
          [-121.890013, 39.383864],
          [-121.856532, 39.536904],
          [-121.994021, 39.533926],
          [-121.950951, 39.715004],
          [-122.046471, 39.797648],
          [-122.044865, 39.883771],
          [-121.804041, 39.884344],
          [-121.703241, 39.98423],
          [-121.646032, 39.982527],
          [-121.586407, 40.100522],
          [-121.436886, 40.151905],
          [-121.366818, 40.086054],
          [-121.430012, 39.914833],
          [-121.360516, 39.84074],
          [-121.076695, 39.597264]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01093",
      state: "01",
      county: "093",
      name: "Marion County",
      censusArea: 742.292,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.203597, 34.08653],
          [-88.176889, 34.293858],
          [-88.175867, 34.302171],
          [-88.173632, 34.321054],
          [-87.634725, 34.306997],
          [-87.636118, 34.002203],
          [-87.635932, 33.915251],
          [-87.951785, 33.91993],
          [-87.98693, 34.052102],
          [-88.207229, 34.058333],
          [-88.203597, 34.08653]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01095",
      state: "01",
      county: "095",
      name: "Marshall County",
      censusArea: 565.842,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.550166, 34.545963],
          [-86.533445, 34.502795],
          [-86.423914, 34.479581],
          [-86.338181, 34.510103],
          [-86.326853, 34.599403],
          [-86.326906, 34.599573],
          [-86.326958, 34.599744],
          [-86.318265, 34.59979],
          [-86.318251, 34.59979],
          [-86.317146, 34.599796],
          [-86.316036, 34.599802],
          [-86.316026, 34.599802],
          [-86.311677, 34.599827],
          [-86.305591, 34.59986],
          [-86.300533, 34.599888],
          [-86.284617, 34.599974],
          [-86.279503, 34.600004],
          [-86.27814, 34.600009],
          [-86.275775, 34.600023],
          [-86.269123, 34.600059],
          [-86.266148, 34.600075],
          [-86.257783, 34.600122],
          [-86.254082, 34.600143],
          [-86.252434, 34.600125],
          [-86.249983, 34.6001],
          [-86.24568, 34.600116],
          [-86.245611, 34.600116],
          [-86.237028, 34.600023],
          [-86.235077, 34.600001],
          [-86.233777, 34.599987],
          [-86.224237, 34.599884],
          [-86.221192, 34.599851],
          [-86.218975, 34.599827],
          [-86.218962, 34.599827],
          [-86.218086, 34.599817],
          [-86.199778, 34.59962],
          [-86.18299, 34.599438],
          [-86.182976, 34.599438],
          [-86.179053, 34.599396],
          [-86.178649, 34.599392],
          [-86.175324, 34.599354],
          [-86.174103, 34.599336],
          [-86.172856, 34.599318],
          [-86.16753, 34.599264],
          [-86.148464, 34.599069],
          [-86.139846, 34.533296],
          [-86.057712, 34.475994],
          [-86.110362, 34.427797],
          [-86.106086, 34.200756],
          [-86.206107, 34.17265],
          [-86.303516, 34.099073],
          [-86.45302, 34.259317],
          [-86.581936, 34.304694],
          [-86.550166, 34.545963]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01097",
      state: "01",
      county: "097",
      name: "Mobile County",
      censusArea: 1229.435,
      stateName: "Alabama"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-88.124658, 30.28364],
            [-88.086812, 30.259864],
            [-88.074854, 30.249119],
            [-88.075856, 30.246139],
            [-88.078786, 30.245039],
            [-88.109432, 30.242097],
            [-88.120151, 30.246149],
            [-88.137083, 30.249179],
            [-88.166569, 30.249255],
            [-88.20854, 30.244807],
            [-88.280571, 30.230274],
            [-88.304773, 30.228031],
            [-88.313323, 30.230024],
            [-88.310025, 30.233233],
            [-88.299705, 30.231812],
            [-88.280781, 30.233781],
            [-88.25837, 30.239595],
            [-88.224615, 30.245559],
            [-88.17335, 30.252418],
            [-88.158303, 30.252393],
            [-88.141143, 30.255024],
            [-88.130631, 30.262125],
            [-88.124722, 30.273541],
            [-88.124658, 30.28364]
          ]
        ],
        [
          [
            [-88.008396, 30.684956],
            [-88.012444, 30.68319],
            [-88.022076, 30.673873],
            [-88.026706, 30.66149],
            [-88.034588, 30.653715],
            [-88.044339, 30.652568],
            [-88.061998, 30.644891],
            [-88.059598, 30.619091],
            [-88.053998, 30.612491],
            [-88.064898, 30.588292],
            [-88.074898, 30.578892],
            [-88.085493, 30.563258],
            [-88.081617, 30.546317],
            [-88.082792, 30.528713],
            [-88.090734, 30.52357],
            [-88.100874, 30.50975],
            [-88.103768, 30.500903],
            [-88.102988, 30.493029],
            [-88.096867, 30.471053],
            [-88.100646, 30.46122],
            [-88.106437, 30.452738],
            [-88.10407, 30.4273],
            [-88.107274, 30.377246],
            [-88.115432, 30.35657],
            [-88.124611, 30.341623],
            [-88.128052, 30.338509],
            [-88.136173, 30.320729],
            [-88.155775, 30.327184],
            [-88.171967, 30.324679],
            [-88.191542, 30.317002],
            [-88.195664, 30.321242],
            [-88.198361, 30.338819],
            [-88.196353, 30.343586],
            [-88.188532, 30.345053],
            [-88.188527, 30.348124],
            [-88.200065, 30.362378],
            [-88.204495, 30.362102],
            [-88.260695, 30.382381],
            [-88.282635, 30.382876],
            [-88.290649, 30.370741],
            [-88.311608, 30.368908],
            [-88.316525, 30.369985],
            [-88.319599, 30.380334],
            [-88.332277, 30.38844],
            [-88.341345, 30.38947],
            [-88.364022, 30.388006],
            [-88.374671, 30.385608],
            [-88.395023, 30.369425],
            [-88.402283, 30.510852],
            [-88.403547, 30.5331],
            [-88.403931, 30.543359],
            [-88.404013, 30.54506],
            [-88.407484, 30.622736],
            [-88.407462, 30.631653],
            [-88.40807, 30.63697],
            [-88.409571, 30.668731],
            [-88.411339, 30.706334],
            [-88.41155, 30.712956],
            [-88.412209, 30.730395],
            [-88.41227, 30.731771],
            [-88.412462, 30.735598],
            [-88.425635, 30.998301],
            [-88.425729, 31.000183],
            [-88.425807, 31.001123],
            [-88.432007, 31.114298],
            [-88.329782, 31.143821],
            [-88.022649, 31.144267],
            [-87.972869, 31.162694],
            [-87.941152, 31.048161],
            [-87.981261, 30.886895],
            [-87.944546, 30.827046],
            [-88.026319, 30.753358],
            [-88.008396, 30.684956]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01101",
      state: "01",
      county: "101",
      name: "Montgomery County",
      censusArea: 784.247,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.411172, 32.409937],
          [-86.237068, 32.49498],
          [-86.192284, 32.43613],
          [-86.023012, 32.419978],
          [-85.919293, 32.274382],
          [-85.999157, 32.250543],
          [-85.996853, 32.051049],
          [-85.995563, 31.967554],
          [-86.191379, 31.966453],
          [-86.302217, 31.965065],
          [-86.303616, 32.051664],
          [-86.406276, 32.050731],
          [-86.408816, 32.240851],
          [-86.496774, 32.344437],
          [-86.411172, 32.409937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01117",
      state: "01",
      county: "117",
      name: "Shelby County",
      censusArea: 784.93,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.023586, 33.246422],
          [-86.830597, 33.332015],
          [-86.674418, 33.466521],
          [-86.60451, 33.467053],
          [-86.516783, 33.545896],
          [-86.481939, 33.502544],
          [-86.378222, 33.502411],
          [-86.378665, 33.390983],
          [-86.35734, 33.296917],
          [-86.503127, 33.179144],
          [-86.491029, 33.102944],
          [-86.517344, 33.020566],
          [-86.610003, 33.070003],
          [-86.881638, 33.071861],
          [-86.881182, 33.049901],
          [-87.025596, 33.165795],
          [-87.026846, 33.246459],
          [-87.023586, 33.246422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01119",
      state: "01",
      county: "119",
      name: "Sumter County",
      censusArea: 903.887,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.403789, 32.44977],
          [-88.403789, 32.449885],
          [-88.388739, 32.578116],
          [-88.383039, 32.626679],
          [-88.382985, 32.626954],
          [-88.347692, 32.929059],
          [-88.340432, 32.991199],
          [-88.171852, 32.99586],
          [-88.207316, 32.924782],
          [-88.079338, 32.772948],
          [-88.109633, 32.770989],
          [-88.057333, 32.593441],
          [-87.928689, 32.632284],
          [-87.840858, 32.605036],
          [-87.897913, 32.600046],
          [-87.85342, 32.532086],
          [-88.031623, 32.43361],
          [-88.046335, 32.377087],
          [-87.930661, 32.310574],
          [-88.421453, 32.30868],
          [-88.403789, 32.44977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01125",
      state: "01",
      county: "125",
      name: "Tuscaloosa County",
      censusArea: 1321.755,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.266923, 33.512929],
          [-87.330816, 33.491501],
          [-87.194841, 33.343069],
          [-87.180006, 33.342979],
          [-87.179754, 33.32801],
          [-87.179638, 33.321112],
          [-87.162175, 33.321012],
          [-87.162007, 33.306587],
          [-87.127142, 33.306013],
          [-87.126756, 33.290844],
          [-87.087497, 33.290675],
          [-87.087546, 33.279666],
          [-87.074602, 33.279682],
          [-87.074634, 33.276018],
          [-87.065754, 33.275937],
          [-87.065738, 33.246907],
          [-87.199153, 33.196554],
          [-87.199317, 33.130657],
          [-87.281945, 33.13306],
          [-87.318539, 33.006179],
          [-87.421936, 33.003379],
          [-87.715709, 33.006824],
          [-87.832233, 33.017258],
          [-87.838047, 33.136864],
          [-87.837521, 33.153637],
          [-87.840683, 33.524839],
          [-87.666661, 33.521667],
          [-87.631718, 33.609833],
          [-87.423701, 33.602096],
          [-87.318532, 33.587393],
          [-87.266923, 33.512929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01131",
      state: "01",
      county: "131",
      name: "Wilcox County",
      censusArea: 888.498,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.50093, 31.829251],
          [-87.620112, 31.827123],
          [-87.667488, 31.958459],
          [-87.667769, 31.991355],
          [-87.622756, 32.005482],
          [-87.624005, 32.132345],
          [-87.521946, 32.132816],
          [-87.523405, 32.25483],
          [-87.472206, 32.264919],
          [-87.262853, 32.147007],
          [-87.178071, 32.047514],
          [-86.906956, 32.04797],
          [-86.856576, 32.048352],
          [-86.857583, 31.962167],
          [-86.908939, 31.961673],
          [-86.906899, 31.830628],
          [-87.50093, 31.829251]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01007",
      state: "01",
      county: "007",
      name: "Bibb County",
      censusArea: 622.582,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.4212, 32.874508],
          [-87.420132, 32.902605],
          [-87.421936, 33.003379],
          [-87.318539, 33.006179],
          [-87.281945, 33.13306],
          [-87.199317, 33.130657],
          [-87.199153, 33.196554],
          [-87.065738, 33.246907],
          [-87.026846, 33.246459],
          [-87.025596, 33.165795],
          [-86.881182, 33.049901],
          [-86.876118, 32.836264],
          [-87.019157, 32.837034],
          [-87.319184, 32.831522],
          [-87.319473, 32.875124],
          [-87.4212, 32.874508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01011",
      state: "01",
      county: "011",
      name: "Bullock County",
      censusArea: 622.805,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.410241, 32.146651],
          [-85.428476, 32.014951],
          [-85.587344, 31.997355],
          [-85.657668, 31.880275],
          [-85.791047, 31.880357],
          [-85.790048, 31.967254],
          [-85.88435, 31.967253],
          [-85.893652, 32.047351],
          [-85.996853, 32.051049],
          [-85.999157, 32.250543],
          [-85.919293, 32.274382],
          [-85.856218, 32.231975],
          [-85.433543, 32.234648],
          [-85.427442, 32.146551],
          [-85.410241, 32.146651]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01015",
      state: "01",
      county: "015",
      name: "Calhoun County",
      censusArea: 605.868,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.145562, 33.679098],
          [-86.043993, 33.763595],
          [-86.066972, 33.839198],
          [-86.066439, 33.841094],
          [-86.065272, 33.842198],
          [-86.049072, 33.841598],
          [-86.048772, 33.852698],
          [-86.021271, 33.851498],
          [-86.021471, 33.866098],
          [-85.995169, 33.864897],
          [-85.99497, 33.879598],
          [-85.98637, 33.879198],
          [-85.98647, 33.893698],
          [-85.96917, 33.892898],
          [-85.96917, 33.914598],
          [-85.738975, 33.96846],
          [-85.529432, 33.95598],
          [-85.530094, 33.941423],
          [-85.532482, 33.889152],
          [-85.638049, 33.773339],
          [-85.638579, 33.648413],
          [-85.796054, 33.55622],
          [-85.794559, 33.585565],
          [-85.994935, 33.586475],
          [-86.050669, 33.67459],
          [-86.145562, 33.679098]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06045",
      state: "06",
      county: "045",
      name: "Mendocino County",
      censusArea: 3506.343,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.821592, 38.85014],
          [-123.080997, 38.852438],
          [-123.136253, 38.809136],
          [-123.26871, 38.808369],
          [-123.533535, 38.768408],
          [-123.541837, 38.776764],
          [-123.571987, 38.798189],
          [-123.579856, 38.802835],
          [-123.58638, 38.802857],
          [-123.600221, 38.814115],
          [-123.601569, 38.81899],
          [-123.605317, 38.822765],
          [-123.638637, 38.843865],
          [-123.642676, 38.844005],
          [-123.647387, 38.845472],
          [-123.652212, 38.854582],
          [-123.654696, 38.865638],
          [-123.659846, 38.872529],
          [-123.688099, 38.893594],
          [-123.71054, 38.91323],
          [-123.725367, 38.917438],
          [-123.72763, 38.9295],
          [-123.726315, 38.936367],
          [-123.738886, 38.95412],
          [-123.732892, 38.954994],
          [-123.729053, 38.956667],
          [-123.721347, 38.963879],
          [-123.711149, 38.977316],
          [-123.6969, 39.004401],
          [-123.69074, 39.021293],
          [-123.690095, 39.031157],
          [-123.693969, 39.057363],
          [-123.713392, 39.108422],
          [-123.721505, 39.125327],
          [-123.735936, 39.139644],
          [-123.737913, 39.143442],
          [-123.742221, 39.164885],
          [-123.76101, 39.191595],
          [-123.765891, 39.193657],
          [-123.774998, 39.212083],
          [-123.777368, 39.237214],
          [-123.787893, 39.264327],
          [-123.798991, 39.271355],
          [-123.803848, 39.278771],
          [-123.801757, 39.28353],
          [-123.803081, 39.291747],
          [-123.811387, 39.312825],
          [-123.808772, 39.324368],
          [-123.817369, 39.3388],
          [-123.822085, 39.343857],
          [-123.825331, 39.360814],
          [-123.826306, 39.36871],
          [-123.822325, 39.379987],
          [-123.821887, 39.406809],
          [-123.81469, 39.446538],
          [-123.795639, 39.492215],
          [-123.78417, 39.509419],
          [-123.778521, 39.521478],
          [-123.766475, 39.552803],
          [-123.76721, 39.559852],
          [-123.787417, 39.604552],
          [-123.78354, 39.609517],
          [-123.782322, 39.621486],
          [-123.78636, 39.659932],
          [-123.792659, 39.684122],
          [-123.808208, 39.710715],
          [-123.824744, 39.718128],
          [-123.829545, 39.723071],
          [-123.831599, 39.730629],
          [-123.835092, 39.738768],
          [-123.838089, 39.752409],
          [-123.83715, 39.776232],
          [-123.839797, 39.795637],
          [-123.851714, 39.832041],
          [-123.853764, 39.8341],
          [-123.881458, 39.845422],
          [-123.907664, 39.863028],
          [-123.915142, 39.875313],
          [-123.915853, 39.881114],
          [-123.930047, 39.909697],
          [-123.954952, 39.922373],
          [-123.962655, 39.937635],
          [-123.980031, 39.962458],
          [-123.99586, 39.973045],
          [-124.023938, 40.001284],
          [-123.544458, 40.001923],
          [-123.544563, 39.977015],
          [-122.934013, 39.978131],
          [-122.937654, 39.798156],
          [-122.892676, 39.708898],
          [-122.885352, 39.580108],
          [-122.89031, 39.529014],
          [-123.063181, 39.503539],
          [-123.075114, 39.407683],
          [-122.994839, 39.235924],
          [-123.077812, 39.173791],
          [-123.093013, 39.071994],
          [-123.028035, 38.996593],
          [-122.98664, 38.997283],
          [-122.948865, 38.90022],
          [-122.821592, 38.85014]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06049",
      state: "06",
      county: "049",
      name: "Modoc County",
      censusArea: 3917.77,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.44754, 41.997169],
          [-121.43961, 41.99708],
          [-121.434977, 41.997022],
          [-121.376101, 41.997026],
          [-121.360253, 41.99668],
          [-121.340517, 41.99622],
          [-121.335734, 41.996518],
          [-121.334385, 41.996655],
          [-121.154347, 41.996352],
          [-121.035195, 41.993323],
          [-120.879481, 41.993781],
          [-120.693941, 41.993676],
          [-120.692219, 41.993677],
          [-120.326005, 41.993122],
          [-120.286424, 41.993058],
          [-119.999168, 41.99454],
          [-119.999866, 41.183974],
          [-121.331786, 41.183886],
          [-121.446495, 41.183484],
          [-121.44754, 41.997169]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06051",
      state: "06",
      county: "051",
      name: "Mono County",
      censusArea: 3048.982,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.156863, 38.414743],
          [-119.125982, 38.39317],
          [-118.746598, 38.124926],
          [-118.428581, 37.895613],
          [-118.039798, 37.615273],
          [-118.039849, 37.615245],
          [-117.832726, 37.464929],
          [-118.775014, 37.463052],
          [-119.022363, 37.585737],
          [-119.124312, 37.733944],
          [-119.268979, 37.73923],
          [-119.20128, 37.804329],
          [-119.200283, 37.885827],
          [-119.308389, 37.946824],
          [-119.30459, 38.023891],
          [-119.345922, 38.083113],
          [-119.632671, 38.198858],
          [-119.639205, 38.32688],
          [-119.542862, 38.499694],
          [-119.619066, 38.603529],
          [-119.585437, 38.713212],
          [-119.494183, 38.649852],
          [-119.494022, 38.649734],
          [-119.450612, 38.619964],
          [-119.450623, 38.619965],
          [-119.328498, 38.534648],
          [-119.156863, 38.414743]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06057",
      state: "06",
      county: "057",
      name: "Nevada County",
      censusArea: 957.772,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.279533, 39.034618],
          [-121.266911, 39.271609],
          [-121.127481, 39.380237],
          [-121.022085, 39.391558],
          [-120.755729, 39.452663],
          [-120.654332, 39.526889],
          [-120.575059, 39.52228],
          [-120.505445, 39.446117],
          [-120.003117, 39.445045],
          [-120.005318, 39.316479],
          [-120.645795, 39.315155],
          [-120.691606, 39.303981],
          [-120.857909, 39.206235],
          [-120.986348, 39.102234],
          [-121.068788, 39.005177],
          [-121.137397, 39.037858],
          [-121.279533, 39.034618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06059",
      state: "06",
      county: "059",
      name: "Orange County",
      censusArea: 790.568,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.093008, 33.786289],
          [-118.084597, 33.803216],
          [-118.072597, 33.815115],
          [-118.063162, 33.81961],
          [-118.063268, 33.82422],
          [-117.976586, 33.906225],
          [-117.976588, 33.909757],
          [-117.976692, 33.910614],
          [-117.976571, 33.911205],
          [-117.976498, 33.919171],
          [-117.97653, 33.928034],
          [-117.783287, 33.946411],
          [-117.680289, 33.877412],
          [-117.673749, 33.870831],
          [-117.533999, 33.710355],
          [-117.474573, 33.703811],
          [-117.459817, 33.691714],
          [-117.412987, 33.659045],
          [-117.509722, 33.505019],
          [-117.59588, 33.386629],
          [-117.607905, 33.406317],
          [-117.631682, 33.430528],
          [-117.645582, 33.440728],
          [-117.645592, 33.440733],
          [-117.684584, 33.461927],
          [-117.689284, 33.460155],
          [-117.691984, 33.456627],
          [-117.691384, 33.454028],
          [-117.715349, 33.460556],
          [-117.726486, 33.483427],
          [-117.761387, 33.516326],
          [-117.784888, 33.541525],
          [-117.801288, 33.546324],
          [-117.814188, 33.552224],
          [-117.840289, 33.573523],
          [-117.87679, 33.592322],
          [-117.89979, 33.599622],
          [-117.927091, 33.605521],
          [-117.940591, 33.620021],
          [-117.957114, 33.629466],
          [-118.000593, 33.654319],
          [-118.029694, 33.676418],
          [-118.064895, 33.711018],
          [-118.088896, 33.729817],
          [-118.101097, 33.734117],
          [-118.116703, 33.743549],
          [-118.09197, 33.758472],
          [-118.099107, 33.776518],
          [-118.093008, 33.786289]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06065",
      state: "06",
      county: "065",
      name: "Riverside County",
      censusArea: 7206.48,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.284475, 34.019015],
          [-117.225372, 34.004314],
          [-116.929357, 34.004914],
          [-116.929558, 34.034113],
          [-116.457775, 34.032881],
          [-115.316212, 34.03411],
          [-115.316065, 34.077843],
          [-114.435429, 34.079727],
          [-114.434949, 34.037784],
          [-114.438266, 34.022609],
          [-114.46361, 33.993431],
          [-114.499883, 33.961789],
          [-114.518434, 33.917518],
          [-114.525539, 33.838614],
          [-114.523365, 33.80612],
          [-114.50434, 33.756381],
          [-114.494197, 33.707922],
          [-114.496489, 33.696901],
          [-114.519113, 33.688473],
          [-114.523959, 33.685879],
          [-114.530348, 33.679245],
          [-114.540617, 33.591412],
          [-114.5403, 33.580615],
          [-114.535965, 33.569154],
          [-114.535664, 33.568788],
          [-114.558898, 33.531819],
          [-114.594534, 33.495059],
          [-114.622918, 33.456561],
          [-114.627125, 33.433554],
          [-115.467506, 33.426992],
          [-116.085165, 33.425932],
          [-116.197591, 33.428893],
          [-116.617687, 33.427354],
          [-117.079721, 33.43033],
          [-117.241271, 33.431993],
          [-117.370925, 33.490549],
          [-117.509722, 33.505019],
          [-117.412987, 33.659045],
          [-117.459817, 33.691714],
          [-117.474573, 33.703811],
          [-117.533999, 33.710355],
          [-117.673749, 33.870831],
          [-117.600066, 33.975316],
          [-117.558312, 34.033451],
          [-117.336161, 34.019378],
          [-117.284475, 34.019015]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06069",
      state: "06",
      county: "069",
      name: "San Benito County",
      censusArea: 1388.71,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.581354, 36.899152],
          [-121.501488, 36.971895],
          [-121.215406, 36.961248],
          [-121.141523, 36.836656],
          [-120.918731, 36.740381],
          [-120.603706, 36.488242],
          [-120.596562, 36.328488],
          [-120.678582, 36.267319],
          [-120.75834, 36.308604],
          [-120.718169, 36.196848],
          [-120.761483, 36.20358],
          [-120.86508, 36.292578],
          [-120.920551, 36.311012],
          [-121.02724, 36.259893],
          [-121.040797, 36.323969],
          [-121.193465, 36.445751],
          [-121.244091, 36.506911],
          [-121.311788, 36.502736],
          [-121.318373, 36.610026],
          [-121.350092, 36.648357],
          [-121.468086, 36.685518],
          [-121.45134, 36.724793],
          [-121.597698, 36.83735],
          [-121.644001, 36.893996],
          [-121.629489, 36.911681],
          [-121.607354, 36.899452],
          [-121.581354, 36.899152]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06073",
      state: "06",
      county: "073",
      name: "San Diego County",
      censusArea: 4206.63,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.106159, 32.618328],
          [-116.390091, 32.59602],
          [-117.118865, 32.534661],
          [-117.124862, 32.534156],
          [-117.133363, 32.575625],
          [-117.132963, 32.597054],
          [-117.136664, 32.618754],
          [-117.139464, 32.627054],
          [-117.159865, 32.660652],
          [-117.168866, 32.671952],
          [-117.180366, 32.681652],
          [-117.192967, 32.687751],
          [-117.196767, 32.688851],
          [-117.213068, 32.687751],
          [-117.223868, 32.683051],
          [-117.236239, 32.671353],
          [-117.246069, 32.669352],
          [-117.255169, 32.700051],
          [-117.25757, 32.72605],
          [-117.25537, 32.745449],
          [-117.25257, 32.752949],
          [-117.25497, 32.786948],
          [-117.26107, 32.803148],
          [-117.280971, 32.822247],
          [-117.28217, 32.839547],
          [-117.28117, 32.843047],
          [-117.27387, 32.851447],
          [-117.26497, 32.848947],
          [-117.26067, 32.852647],
          [-117.25617, 32.859447],
          [-117.25167, 32.874346],
          [-117.25447, 32.900146],
          [-117.26047, 32.931245],
          [-117.262547, 32.939542],
          [-117.28077, 33.012343],
          [-117.29337, 33.034642],
          [-117.309771, 33.07454],
          [-117.315278, 33.093504],
          [-117.328359, 33.121842],
          [-117.359484, 33.164231],
          [-117.362572, 33.168437],
          [-117.39148, 33.202762],
          [-117.401926, 33.213598],
          [-117.445583, 33.268517],
          [-117.469794, 33.296417],
          [-117.50565, 33.334063],
          [-117.547693, 33.365491],
          [-117.571722, 33.378988],
          [-117.59588, 33.386629],
          [-117.509722, 33.505019],
          [-117.370925, 33.490549],
          [-117.241271, 33.431993],
          [-117.079721, 33.43033],
          [-116.617687, 33.427354],
          [-116.197591, 33.428893],
          [-116.085165, 33.425932],
          [-116.08109, 33.074833],
          [-116.103137, 32.904115],
          [-116.106159, 32.618328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06075",
      state: "06",
      county: "075",
      name: "San Francisco County",
      censusArea: 46.873,
      stateName: "California"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-122.418698, 37.852717],
            [-122.434403, 37.852434],
            [-122.443302, 37.855448],
            [-122.446316, 37.861046],
            [-122.438565, 37.868367],
            [-122.430958, 37.872242],
            [-122.421439, 37.869969],
            [-122.421341, 37.869946],
            [-122.41847, 37.861764],
            [-122.41847, 37.852721],
            [-122.418698, 37.852717]
          ]
        ],
        [
          [
            [-123.013916, 37.700355],
            [-123.013897, 37.704478],
            [-123.012194, 37.706749],
            [-123.004489, 37.706262],
            [-123.00019, 37.702937],
            [-122.997189, 37.697909],
            [-123.000677, 37.690203],
            [-123.005543, 37.689392],
            [-123.011464, 37.691907],
            [-123.014303, 37.696205],
            [-123.013916, 37.700355]
          ]
        ],
        [
          [
            [-122.3785, 37.826505],
            [-122.377879, 37.830648],
            [-122.369941, 37.832137],
            [-122.363244, 37.823951],
            [-122.358779, 37.814278],
            [-122.362661, 37.807577],
            [-122.372422, 37.811301],
            [-122.37267, 37.81651],
            [-122.3785, 37.826505]
          ]
        ],
        [
          [
            [-122.502427, 37.708133],
            [-122.506483, 37.723731],
            [-122.509397, 37.748841],
            [-122.511983, 37.77113],
            [-122.514483, 37.780829],
            [-122.50531, 37.788312],
            [-122.492883, 37.787929],
            [-122.485783, 37.790629],
            [-122.478083, 37.810828],
            [-122.470336, 37.808671],
            [-122.463793, 37.804653],
            [-122.425942, 37.810979],
            [-122.407452, 37.811441],
            [-122.398139, 37.80563],
            [-122.385323, 37.790724],
            [-122.375854, 37.734979],
            [-122.370094, 37.732331],
            [-122.367697, 37.734943],
            [-122.365478, 37.734621],
            [-122.356784, 37.729505],
            [-122.361749, 37.71501],
            [-122.370411, 37.717572],
            [-122.391374, 37.708331],
            [-122.393782, 37.708231],
            [-122.471319, 37.708305],
            [-122.471837, 37.708332],
            [-122.481083, 37.708232],
            [-122.484883, 37.708332],
            [-122.485382, 37.708231],
            [-122.502427, 37.708133]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06113",
      state: "06",
      county: "113",
      name: "Yolo County",
      censusArea: 1014.689,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.504139, 38.466563],
          [-121.521641, 38.360104],
          [-121.593273, 38.313089],
          [-121.693697, 38.313732],
          [-121.694679, 38.52705],
          [-121.940285, 38.533384],
          [-122.011673, 38.488991],
          [-122.103281, 38.513348],
          [-122.111563, 38.524051],
          [-122.128123, 38.583697],
          [-122.151772, 38.625297],
          [-122.168349, 38.619297],
          [-122.164944, 38.642462],
          [-122.287999, 38.839932],
          [-122.395056, 38.864245],
          [-122.340172, 38.924246],
          [-122.337967, 38.923945],
          [-121.835488, 38.924481],
          [-121.723149, 38.851306],
          [-121.685998, 38.763371],
          [-121.602894, 38.735838],
          [-121.605774, 38.718319],
          [-121.630446, 38.697571],
          [-121.62576, 38.673133],
          [-121.594069, 38.644106],
          [-121.576068, 38.648604],
          [-121.549869, 38.599405],
          [-121.527667, 38.604604],
          [-121.506269, 38.586305],
          [-121.520965, 38.566205],
          [-121.51129, 38.54629],
          [-121.559167, 38.498007],
          [-121.538766, 38.474707],
          [-121.504139, 38.466563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08001",
      state: "08",
      county: "001",
      name: "Adams County",
      censusArea: 1167.653,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.706547, 39.739894],
          [-104.734969, 39.740302],
          [-104.884646, 39.740156],
          [-104.856377, 39.768673],
          [-104.734601, 39.769181],
          [-104.76247, 39.82298],
          [-104.619906, 39.826633],
          [-104.600435, 39.899459],
          [-104.692934, 39.914183],
          [-104.79091, 39.79825],
          [-105.053246, 39.791063],
          [-105.053162, 39.799239],
          [-105.052829, 39.803874],
          [-105.053256, 39.806497],
          [-105.05327, 39.820028],
          [-105.05319, 39.849251],
          [-105.0529, 39.852042],
          [-105.053245, 39.860018],
          [-105.052884, 39.913795],
          [-104.961408, 40.000337],
          [-104.923689, 40.000327],
          [-104.922153, 40.00033],
          [-104.150332, 40.000856],
          [-103.705701, 40.001366],
          [-103.705826, 39.999984],
          [-103.706547, 39.739894]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08007",
      state: "08",
      county: "007",
      name: "Archuleta County",
      censusArea: 1350.175,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.476228, 36.993472],
          [-106.617159, 36.992967],
          [-106.617125, 36.993004],
          [-106.628652, 36.993175],
          [-106.628733, 36.993161],
          [-106.661344, 36.993243],
          [-106.675626, 36.993123],
          [-106.869796, 36.992426],
          [-107.000592, 37.000009],
          [-107.420913, 37.000005],
          [-107.420915, 37.000005],
          [-107.481737, 36.999973],
          [-107.482131, 37.422673],
          [-107.12868, 37.422942],
          [-107.128737, 37.392409],
          [-106.710775, 37.404228],
          [-106.678373, 37.403596],
          [-106.678354, 37.228566],
          [-106.476228, 36.993472]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08009",
      state: "08",
      county: "009",
      name: "Baca County",
      censusArea: 2554.967,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.04224, 36.993083],
          [-102.054503, 36.993109],
          [-102.184271, 36.993593],
          [-102.208316, 36.99373],
          [-102.355288, 36.994506],
          [-102.355367, 36.994575],
          [-102.570896, 36.995136],
          [-102.875481, 36.999628],
          [-102.979613, 36.998549],
          [-102.985807, 36.998571],
          [-102.986976, 36.998524],
          [-103.002199, 37.000104],
          [-103.086105, 36.999864],
          [-103.075938, 37.64342],
          [-102.747615, 37.643642],
          [-102.041585, 37.644282],
          [-102.041618, 37.607868],
          [-102.041894, 37.557977],
          [-102.041899, 37.541186],
          [-102.042016, 37.535261],
          [-102.041786, 37.506066],
          [-102.041801, 37.469488],
          [-102.041755, 37.434855],
          [-102.041669, 37.43474],
          [-102.041676, 37.409898],
          [-102.041826, 37.389191],
          [-102.042089, 37.352819],
          [-102.041974, 37.352613],
          [-102.041817, 37.30949],
          [-102.041664, 37.29765],
          [-102.041963, 37.258164],
          [-102.042002, 37.141744],
          [-102.042135, 37.125021],
          [-102.042092, 37.125021],
          [-102.041809, 37.111973],
          [-102.041983, 37.106551],
          [-102.04192, 37.035083],
          [-102.041749, 37.034397],
          [-102.041921, 37.032178],
          [-102.04195, 37.030805],
          [-102.041952, 37.024742],
          [-102.04224, 36.993083]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08015",
      state: "08",
      county: "015",
      name: "Chaffee County",
      censusArea: 1013.403,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.577966, 39.057908],
          [-106.191006, 39.056073],
          [-106.110995, 38.940099],
          [-106.028138, 38.942849],
          [-105.925123, 38.872178],
          [-105.906587, 38.806962],
          [-105.96975, 38.693551],
          [-105.879176, 38.607312],
          [-105.908717, 38.505631],
          [-106.010751, 38.446566],
          [-106.24694, 38.422768],
          [-106.452126, 38.698777],
          [-106.326099, 38.911004],
          [-106.465032, 38.910354],
          [-106.599214, 38.997994],
          [-106.591692, 39.024923],
          [-106.597233, 39.044821],
          [-106.58073, 39.048717],
          [-106.577966, 39.057908]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08017",
      state: "08",
      county: "017",
      name: "Cheyenne County",
      censusArea: 1778.276,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.046571, 39.047038],
          [-102.045388, 38.813392],
          [-102.045334, 38.799463],
          [-102.045448, 38.783453],
          [-102.045371, 38.770064],
          [-102.045287, 38.755528],
          [-102.045375, 38.754339],
          [-102.045212, 38.697567],
          [-102.045156, 38.688555],
          [-102.045127, 38.686725],
          [-102.04516, 38.675221],
          [-102.045102, 38.674946],
          [-102.045074, 38.669617],
          [-102.045288, 38.615249],
          [-102.045288, 38.615168],
          [-103.172943, 38.61245],
          [-103.163025, 39.03761],
          [-102.046571, 39.047038]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08021",
      state: "08",
      county: "021",
      name: "Conejos County",
      censusArea: 1287.391,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.71847, 36.995846],
          [-105.996159, 36.995418],
          [-105.997472, 36.995417],
          [-106.006634, 36.995343],
          [-106.247705, 36.994266],
          [-106.248675, 36.994288],
          [-106.476228, 36.993472],
          [-106.678354, 37.228566],
          [-106.678373, 37.403596],
          [-106.039331, 37.400852],
          [-106.038912, 37.356953],
          [-105.743339, 37.356798],
          [-105.729755, 37.180506],
          [-105.770171, 37.061166],
          [-105.71847, 36.995846]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08023",
      state: "08",
      county: "023",
      name: "Costilla County",
      censusArea: 1226.952,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.155042, 36.995339],
          [-105.220613, 36.995169],
          [-105.41931, 36.995856],
          [-105.442459, 36.995994],
          [-105.447255, 36.996017],
          [-105.465182, 36.995991],
          [-105.508836, 36.995895],
          [-105.512485, 36.995777],
          [-105.533922, 36.995875],
          [-105.62747, 36.995679],
          [-105.66472, 36.995874],
          [-105.716471, 36.995849],
          [-105.71847, 36.995846],
          [-105.770171, 37.061166],
          [-105.729755, 37.180506],
          [-105.743339, 37.356798],
          [-105.485516, 37.577899],
          [-105.295587, 37.654416],
          [-105.187549, 37.619669],
          [-105.154176, 37.293129],
          [-105.155042, 36.995339]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08029",
      state: "08",
      county: "029",
      name: "Delta County",
      censusArea: 1142.05,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.500655, 38.668553],
          [-108.378698, 38.668097],
          [-108.378953, 38.829108],
          [-107.85824, 39.079804],
          [-107.76549, 39.043294],
          [-107.500606, 39.217916],
          [-107.500655, 38.668553]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08037",
      state: "08",
      county: "037",
      name: "Eagle County",
      censusArea: 1684.53,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.206732, 39.379636],
          [-106.426487, 39.361871],
          [-107.113446, 39.366066],
          [-107.113672, 39.919112],
          [-107.033968, 39.918913],
          [-106.626555, 39.918671],
          [-106.626569, 39.924785],
          [-106.434508, 39.924914],
          [-106.385478, 39.767164],
          [-106.177604, 39.609056],
          [-106.251638, 39.465039],
          [-106.206732, 39.379636]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08043",
      state: "08",
      county: "043",
      name: "Fremont County",
      censusArea: 1533.068,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.941533, 38.519569],
          [-104.940366, 38.258275],
          [-105.049215, 38.257973],
          [-105.796897, 38.265047],
          [-106.010751, 38.446566],
          [-105.908717, 38.505631],
          [-105.879176, 38.607312],
          [-105.96975, 38.693551],
          [-105.329134, 38.697205],
          [-105.237792, 38.696901],
          [-105.240362, 38.647595],
          [-104.942426, 38.649882],
          [-104.941533, 38.519569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06081",
      state: "06",
      county: "081",
      name: "San Mateo County",
      censusArea: 448.408,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.292974, 37.107318],
          [-122.306139, 37.116383],
          [-122.313907, 37.118161],
          [-122.322971, 37.11546],
          [-122.330463, 37.115338],
          [-122.337071, 37.117382],
          [-122.338856, 37.120854],
          [-122.337085, 37.130795],
          [-122.337833, 37.135936],
          [-122.344029, 37.144099],
          [-122.359791, 37.155574],
          [-122.36179, 37.163593],
          [-122.367085, 37.172817],
          [-122.37927, 37.181128],
          [-122.390599, 37.182988],
          [-122.397065, 37.187249],
          [-122.405073, 37.195791],
          [-122.407181, 37.219465],
          [-122.408982, 37.225258],
          [-122.415822, 37.232839],
          [-122.419113, 37.24147],
          [-122.418452, 37.248521],
          [-122.411686, 37.265844],
          [-122.401323, 37.337009],
          [-122.40085, 37.359225],
          [-122.409258, 37.374805],
          [-122.423286, 37.392542],
          [-122.443687, 37.435941],
          [-122.445987, 37.461541],
          [-122.452087, 37.48054],
          [-122.467888, 37.49814],
          [-122.472388, 37.50054],
          [-122.476443, 37.498768],
          [-122.482351, 37.496187],
          [-122.485888, 37.494641],
          [-122.486749, 37.49439],
          [-122.487139, 37.494277],
          [-122.493789, 37.492341],
          [-122.494429, 37.49269],
          [-122.499289, 37.495341],
          [-122.516689, 37.52134],
          [-122.519533, 37.537302],
          [-122.516589, 37.544939],
          [-122.514789, 37.546139],
          [-122.513688, 37.552239],
          [-122.518088, 37.576138],
          [-122.517187, 37.590637],
          [-122.501386, 37.599637],
          [-122.496786, 37.612136],
          [-122.494085, 37.644035],
          [-122.496784, 37.686433],
          [-122.502427, 37.708133],
          [-122.485382, 37.708231],
          [-122.484883, 37.708332],
          [-122.481083, 37.708232],
          [-122.471837, 37.708332],
          [-122.471319, 37.708305],
          [-122.393782, 37.708231],
          [-122.391374, 37.708331],
          [-122.39319, 37.707531],
          [-122.387626, 37.67906],
          [-122.374291, 37.662206],
          [-122.3756, 37.652389],
          [-122.37789, 37.650425],
          [-122.387381, 37.648462],
          [-122.386072, 37.637662],
          [-122.365455, 37.626208],
          [-122.35531, 37.615736],
          [-122.358583, 37.611155],
          [-122.370364, 37.614427],
          [-122.373309, 37.613773],
          [-122.378545, 37.605592],
          [-122.360219, 37.592501],
          [-122.317676, 37.590865],
          [-122.315385, 37.587265],
          [-122.315713, 37.583666],
          [-122.305895, 37.575484],
          [-122.262698, 37.572866],
          [-122.251898, 37.566321],
          [-122.244372, 37.55814],
          [-122.242832, 37.557136],
          [-122.236323, 37.552891],
          [-122.225135, 37.545594],
          [-122.214264, 37.538505],
          [-122.196593, 37.537196],
          [-122.194957, 37.522469],
          [-122.168449, 37.504143],
          [-122.155686, 37.501198],
          [-122.149632, 37.502671],
          [-122.140142, 37.507907],
          [-122.130979, 37.503652],
          [-122.127706, 37.500053],
          [-122.116112, 37.505386],
          [-122.109574, 37.497637],
          [-122.081473, 37.477838],
          [-122.190402, 37.431472],
          [-122.152278, 37.286055],
          [-122.152774, 37.215444],
          [-122.317682, 37.186945],
          [-122.292974, 37.107318]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06083",
      state: "06",
      county: "083",
      name: "Santa Barbara County",
      censusArea: 2735.085,
      stateName: "California"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-120.248484, 33.999329],
            [-120.247393, 34.001911],
            [-120.238657, 34.007592],
            [-120.230001, 34.010136],
            [-120.221287, 34.010367],
            [-120.208478, 34.005655],
            [-120.19578, 34.004284],
            [-120.167306, 34.008219],
            [-120.151663, 34.018126],
            [-120.147647, 34.024831],
            [-120.140362, 34.025974],
            [-120.135853, 34.026087],
            [-120.115058, 34.019866],
            [-120.090182, 34.019806],
            [-120.073609, 34.024477],
            [-120.062778, 34.031161],
            [-120.061953, 34.033976],
            [-120.057637, 34.03734],
            [-120.055107, 34.037729],
            [-120.043259, 34.035806],
            [-120.044004, 34.02482],
            [-120.047798, 34.021227],
            [-120.050382, 34.013331],
            [-120.048926, 34.009898],
            [-120.046575, 34.000002],
            [-120.041311, 33.994507],
            [-120.025653, 33.985553],
            [-120.011123, 33.979894],
            [-120.003815, 33.979547],
            [-119.984316, 33.983948],
            [-119.978876, 33.983081],
            [-119.979913, 33.969623],
            [-119.976857, 33.956693],
            [-119.971141, 33.950401],
            [-119.97026, 33.944359],
            [-119.973691, 33.942481],
            [-120.00096, 33.941554],
            [-120.017715, 33.936366],
            [-120.046881, 33.919597],
            [-120.048315, 33.917625],
            [-120.048611, 33.915775],
            [-120.049682, 33.914563],
            [-120.077793, 33.908886],
            [-120.098601, 33.907853],
            [-120.105489, 33.90428],
            [-120.109137, 33.899129],
            [-120.121817, 33.895712],
            [-120.168974, 33.91909],
            [-120.179049, 33.927994],
            [-120.18984, 33.947703],
            [-120.192339, 33.950266],
            [-120.198602, 33.952211],
            [-120.200085, 33.956904],
            [-120.209372, 33.972376],
            [-120.224461, 33.989059],
            [-120.248484, 33.999329]
          ]
        ],
        [
          [
            [-119.789798, 34.05726],
            [-119.770729, 34.055051],
            [-119.766081, 34.05537],
            [-119.763688, 34.057155],
            [-119.755521, 34.056716],
            [-119.739472, 34.049299],
            [-119.726437, 34.047908],
            [-119.712576, 34.043265],
            [-119.704628, 34.037681],
            [-119.686507, 34.019805],
            [-119.637742, 34.013178],
            [-119.619343, 34.016468],
            [-119.612226, 34.021256],
            [-119.604287, 34.031561],
            [-119.608798, 34.035245],
            [-119.609239, 34.03735],
            [-119.59324, 34.049625],
            [-119.57341, 34.05011],
            [-119.5667, 34.053452],
            [-119.529603, 34.041155],
            [-119.52064, 34.034262],
            [-119.52177, 34.032247],
            [-119.532413, 34.024949],
            [-119.538847, 34.023988],
            [-119.542449, 34.021082],
            [-119.54828, 34.009819],
            [-119.547072, 34.005469],
            [-119.554472, 33.99782],
            [-119.560464, 33.99553],
            [-119.575636, 33.996009],
            [-119.5902, 33.989712],
            [-119.596877, 33.988611],
            [-119.619082, 33.987228],
            [-119.621117, 33.98899],
            [-119.64771, 33.987786],
            [-119.662825, 33.985889],
            [-119.69011, 33.972225],
            [-119.706952, 33.969178],
            [-119.712363, 33.965422],
            [-119.714696, 33.961439],
            [-119.721206, 33.959583],
            [-119.742966, 33.963877],
            [-119.750438, 33.963759],
            [-119.758141, 33.959212],
            [-119.795938, 33.962929],
            [-119.842748, 33.97034],
            [-119.873358, 33.980375],
            [-119.877057, 33.985757],
            [-119.883033, 34.000802],
            [-119.884896, 34.008814],
            [-119.882531, 34.011674],
            [-119.876916, 34.023527],
            [-119.876329, 34.032087],
            [-119.892821, 34.045529],
            [-119.916216, 34.058351],
            [-119.923337, 34.069361],
            [-119.919155, 34.07728],
            [-119.912857, 34.077508],
            [-119.89113, 34.072856],
            [-119.857304, 34.071298],
            [-119.825865, 34.059794],
            [-119.818742, 34.052997],
            [-119.807825, 34.052127],
            [-119.789798, 34.05726]
          ]
        ],
        [
          [
            [-120.46258, 34.042627],
            [-120.440248, 34.036918],
            [-120.418768, 34.052093],
            [-120.415287, 34.05496],
            [-120.411314, 34.052869],
            [-120.403613, 34.050442],
            [-120.396188, 34.050187],
            [-120.390906, 34.051994],
            [-120.374211, 34.062658],
            [-120.368813, 34.06778],
            [-120.368584, 34.071214],
            [-120.370176, 34.074907],
            [-120.368278, 34.076465],
            [-120.362251, 34.073056],
            [-120.354982, 34.059256],
            [-120.36029, 34.05582],
            [-120.358608, 34.050235],
            [-120.346946, 34.046576],
            [-120.331161, 34.049097],
            [-120.319032, 34.041979],
            [-120.313175, 34.036576],
            [-120.302122, 34.023574],
            [-120.304543, 34.021171],
            [-120.317052, 34.018837],
            [-120.347706, 34.020114],
            [-120.35532, 34.017914],
            [-120.35793, 34.015029],
            [-120.375143, 34.018775],
            [-120.409368, 34.032198],
            [-120.415225, 34.032245],
            [-120.419021, 34.028949],
            [-120.427408, 34.025425],
            [-120.454134, 34.028081],
            [-120.459635, 34.031537],
            [-120.465329, 34.038448],
            [-120.46258, 34.042627]
          ]
        ],
        [
          [
            [-120.355343, 34.941265],
            [-120.311498, 34.903764],
            [-120.334548, 35.006315],
            [-120.188383, 35.030378],
            [-120.093376, 35.114102],
            [-119.980122, 35.057573],
            [-119.928419, 35.059794],
            [-119.745566, 34.973676],
            [-119.672963, 34.973375],
            [-119.535757, 34.897576],
            [-119.472754, 34.901174],
            [-119.442352, 34.901274],
            [-119.442278, 34.465509],
            [-119.440414, 34.441842],
            [-119.444717, 34.42951],
            [-119.453423, 34.42861],
            [-119.454277, 34.418166],
            [-119.445411, 34.408881],
            [-119.454101, 34.395568],
            [-119.472481, 34.38383],
            [-119.473793, 34.379115],
            [-119.478265, 34.377197],
            [-119.510655, 34.386295],
            [-119.536957, 34.395495],
            [-119.559459, 34.413395],
            [-119.616862, 34.420995],
            [-119.638864, 34.415696],
            [-119.648664, 34.417396],
            [-119.671866, 34.416096],
            [-119.688167, 34.412497],
            [-119.684666, 34.408297],
            [-119.691749, 34.403154],
            [-119.709067, 34.395397],
            [-119.729369, 34.395897],
            [-119.74547, 34.402898],
            [-119.785871, 34.415997],
            [-119.794771, 34.417597],
            [-119.835771, 34.415796],
            [-119.853771, 34.407996],
            [-119.873971, 34.408795],
            [-119.925227, 34.433931],
            [-119.956433, 34.435288],
            [-119.971951, 34.444641],
            [-120.008077, 34.460447],
            [-120.038828, 34.463434],
            [-120.050682, 34.461651],
            [-120.088591, 34.460208],
            [-120.097212, 34.461809],
            [-120.118411, 34.469927],
            [-120.141165, 34.473405],
            [-120.183505, 34.470372],
            [-120.225498, 34.470587],
            [-120.238002, 34.468098],
            [-120.25777, 34.467451],
            [-120.283001, 34.468354],
            [-120.295051, 34.470623],
            [-120.299169, 34.469731],
            [-120.301822, 34.467077],
            [-120.341369, 34.458789],
            [-120.441975, 34.451512],
            [-120.451425, 34.447094],
            [-120.471376, 34.447846],
            [-120.47661, 34.475131],
            [-120.480372, 34.481059],
            [-120.490523, 34.490075],
            [-120.511421, 34.522953],
            [-120.524776, 34.531291],
            [-120.581293, 34.556959],
            [-120.608355, 34.556656],
            [-120.612005, 34.553564],
            [-120.622575, 34.554017],
            [-120.637805, 34.56622],
            [-120.645739, 34.581035],
            [-120.640244, 34.604406],
            [-120.625127, 34.634489],
            [-120.60197, 34.692095],
            [-120.60045, 34.70464],
            [-120.601672, 34.709721],
            [-120.614852, 34.730709],
            [-120.62632, 34.738072],
            [-120.637415, 34.755895],
            [-120.62297, 34.7933],
            [-120.616296, 34.816308],
            [-120.609898, 34.842751],
            [-120.610266, 34.85818],
            [-120.616325, 34.866739],
            [-120.639283, 34.880413],
            [-120.642212, 34.894145],
            [-120.647328, 34.901133],
            [-120.662889, 34.901183],
            [-120.670835, 34.904115],
            [-120.648905, 34.974393],
            [-120.513924, 34.989276],
            [-120.355343, 34.941265]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06087",
      state: "06",
      county: "087",
      name: "Santa Cruz County",
      censusArea: 445.17,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.015966, 37.165658],
          [-121.7576, 37.04963],
          [-121.654038, 36.950584],
          [-121.581354, 36.899152],
          [-121.607354, 36.899452],
          [-121.629489, 36.911681],
          [-121.644001, 36.893996],
          [-121.745288, 36.909341],
          [-121.810552, 36.850648],
          [-121.827664, 36.879353],
          [-121.862266, 36.931552],
          [-121.880167, 36.950151],
          [-121.894667, 36.961851],
          [-121.906468, 36.96895],
          [-121.930069, 36.97815],
          [-121.93947, 36.97805],
          [-121.95167, 36.97145],
          [-121.972771, 36.954151],
          [-121.975871, 36.954051],
          [-121.983896, 36.958727],
          [-122.012373, 36.96455],
          [-122.023373, 36.96215],
          [-122.027174, 36.95115],
          [-122.050122, 36.948523],
          [-122.066421, 36.948271],
          [-122.079356, 36.950783],
          [-122.105976, 36.955951],
          [-122.140578, 36.97495],
          [-122.155078, 36.98085],
          [-122.186879, 37.00345],
          [-122.20618, 37.013949],
          [-122.252181, 37.059448],
          [-122.260481, 37.072548],
          [-122.284882, 37.101747],
          [-122.292974, 37.107318],
          [-122.317682, 37.186945],
          [-122.152774, 37.215444],
          [-122.152278, 37.286055],
          [-122.015966, 37.165658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06095",
      state: "06",
      county: "095",
      name: "Solano County",
      censusArea: 821.765,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.088835, 38.384151],
          [-122.103281, 38.513348],
          [-122.011673, 38.488991],
          [-121.940285, 38.533384],
          [-121.694679, 38.52705],
          [-121.693697, 38.313732],
          [-121.593273, 38.313089],
          [-121.605258, 38.295352],
          [-121.60073, 38.278375],
          [-121.60401, 38.270103],
          [-121.600249, 38.257817],
          [-121.601993, 38.252426],
          [-121.602121, 38.241674],
          [-121.603208, 38.237283],
          [-121.602763, 38.230219],
          [-121.688233, 38.145421],
          [-121.713926, 38.084542],
          [-121.862462, 38.06603],
          [-121.865189, 38.059909],
          [-121.869469, 38.054519],
          [-122.050386, 38.060198],
          [-122.123973, 38.035717],
          [-122.266669, 38.06007],
          [-122.273006, 38.07438],
          [-122.282824, 38.082889],
          [-122.301804, 38.105142],
          [-122.314567, 38.115287],
          [-122.366273, 38.141467],
          [-122.39638, 38.149976],
          [-122.403514, 38.150624],
          [-122.406978, 38.155663],
          [-122.406786, 38.155632],
          [-122.19538, 38.155017],
          [-122.205982, 38.315713],
          [-122.064779, 38.315912],
          [-122.088835, 38.384151]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06101",
      state: "06",
      county: "101",
      name: "Sutter County",
      censusArea: 602.41,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.484396, 38.734598],
          [-121.521767, 38.736401],
          [-121.602894, 38.735838],
          [-121.685998, 38.763371],
          [-121.723149, 38.851306],
          [-121.835488, 38.924481],
          [-121.847964, 39.070817],
          [-121.945509, 39.180959],
          [-121.908269, 39.303878],
          [-121.62376, 39.295621],
          [-121.571297, 38.938918],
          [-121.414885, 38.996429],
          [-121.469356, 38.925992],
          [-121.484396, 38.734598]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06103",
      state: "06",
      county: "103",
      name: "Tehama County",
      censusArea: 2949.709,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.934013, 39.978131],
          [-122.989623, 40.145496],
          [-122.989015, 40.250145],
          [-123.065426, 40.286971],
          [-122.918472, 40.306758],
          [-122.872769, 40.348848],
          [-122.651341, 40.328288],
          [-122.523997, 40.3945],
          [-122.445021, 40.373398],
          [-122.310412, 40.371064],
          [-122.010316, 40.426558],
          [-121.940989, 40.415331],
          [-121.806539, 40.44495],
          [-121.64633, 40.434726],
          [-121.497635, 40.445591],
          [-121.470395, 40.350213],
          [-121.345571, 40.312667],
          [-121.368395, 40.21223],
          [-121.436886, 40.151905],
          [-121.586407, 40.100522],
          [-121.646032, 39.982527],
          [-121.703241, 39.98423],
          [-121.804041, 39.884344],
          [-122.044865, 39.883771],
          [-122.046471, 39.797648],
          [-122.937654, 39.798156],
          [-122.934013, 39.978131]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06109",
      state: "06",
      county: "109",
      name: "Tuolumne County",
      censusArea: 2220.884,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.652673, 37.831422],
          [-120.514952, 37.95339],
          [-120.534814, 37.991174],
          [-120.504516, 37.991144],
          [-120.176177, 38.374014],
          [-120.019951, 38.433521],
          [-119.884749, 38.356185],
          [-119.753481, 38.416759],
          [-119.639205, 38.32688],
          [-119.632671, 38.198858],
          [-119.345922, 38.083113],
          [-119.30459, 38.023891],
          [-119.308389, 37.946824],
          [-119.200283, 37.885827],
          [-119.20128, 37.804329],
          [-119.268979, 37.73923],
          [-119.288381, 37.74503],
          [-119.293382, 37.767929],
          [-119.308995, 37.777986],
          [-119.535699, 37.904122],
          [-119.646203, 37.846823],
          [-119.667203, 37.801224],
          [-119.809409, 37.755025],
          [-119.907013, 37.757926],
          [-120.079508, 37.828808],
          [-120.127226, 37.781566],
          [-120.345437, 37.724787],
          [-120.325943, 37.648966],
          [-120.391931, 37.683559],
          [-120.387613, 37.633704],
          [-120.652673, 37.831422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06009",
      state: "06",
      county: "009",
      name: "Calaveras County",
      censusArea: 1020.012,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.652673, 37.831422],
          [-120.926449, 38.077421],
          [-120.93885, 38.088321],
          [-120.995497, 38.225405],
          [-120.893792, 38.221277],
          [-120.423215, 38.473322],
          [-120.380707, 38.461042],
          [-120.072484, 38.509869],
          [-120.019951, 38.433521],
          [-120.176177, 38.374014],
          [-120.504516, 37.991144],
          [-120.534814, 37.991174],
          [-120.514952, 37.95339],
          [-120.652673, 37.831422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06013",
      state: "06",
      county: "013",
      name: "Contra Costa County",
      censusArea: 715.937,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.910138, 37.73046],
          [-121.96077, 37.718629],
          [-121.977999, 37.729579],
          [-122.001071, 37.739028],
          [-122.010771, 37.755127],
          [-122.004228, 37.770058],
          [-122.185977, 37.820726],
          [-122.30618, 37.898225],
          [-122.310881, 37.897825],
          [-122.313496, 37.897211],
          [-122.32373, 37.905845],
          [-122.33453, 37.908791],
          [-122.35711, 37.908791],
          [-122.362346, 37.904209],
          [-122.367582, 37.903882],
          [-122.378709, 37.905191],
          [-122.385908, 37.908136],
          [-122.389181, 37.9101],
          [-122.39049, 37.922535],
          [-122.395071, 37.927117],
          [-122.401289, 37.928426],
          [-122.413725, 37.937262],
          [-122.417371, 37.943513],
          [-122.430087, 37.963115],
          [-122.42976, 37.965405],
          [-122.415361, 37.963115],
          [-122.411761, 37.960497],
          [-122.408383, 37.957544],
          [-122.399832, 37.956009],
          [-122.367582, 37.978168],
          [-122.361905, 37.989991],
          [-122.363001, 37.994375],
          [-122.366928, 37.998458],
          [-122.368891, 38.007948],
          [-122.367909, 38.01253],
          [-122.363655, 38.014166],
          [-122.359493, 38.009941],
          [-122.340093, 38.003694],
          [-122.331912, 38.00533],
          [-122.321112, 38.012857],
          [-122.315549, 38.013511],
          [-122.300823, 38.010893],
          [-122.283478, 38.022674],
          [-122.262861, 38.0446],
          [-122.262861, 38.051473],
          [-122.266669, 38.06007],
          [-122.123973, 38.035717],
          [-122.050386, 38.060198],
          [-121.869469, 38.054519],
          [-121.865189, 38.059909],
          [-121.862462, 38.06603],
          [-121.737824, 38.02663],
          [-121.679265, 38.089319],
          [-121.580022, 38.094414],
          [-121.579943, 37.976113],
          [-121.574823, 37.973025],
          [-121.569889, 37.957495],
          [-121.56364, 37.94611],
          [-121.561937, 37.937612],
          [-121.55987, 37.920862],
          [-121.569113, 37.901521],
          [-121.574849, 37.885021],
          [-121.575463, 37.863118],
          [-121.56937, 37.858881],
          [-121.561813, 37.857135],
          [-121.549517, 37.856986],
          [-121.538026, 37.849622],
          [-121.536595, 37.844723],
          [-121.538931, 37.839563],
          [-121.550971, 37.831155],
          [-121.556936, 37.817218],
          [-121.55916, 37.818927],
          [-121.623724, 37.799291],
          [-121.881567, 37.737328],
          [-121.910138, 37.73046]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06015",
      state: "06",
      county: "015",
      name: "Del Norte County",
      censusArea: 1006.373,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-124.065521, 41.464739],
          [-124.066057, 41.470258],
          [-124.075917, 41.501757],
          [-124.081427, 41.511228],
          [-124.081987, 41.547761],
          [-124.092404, 41.553615],
          [-124.101123, 41.569192],
          [-124.101403, 41.578524],
          [-124.097385, 41.585251],
          [-124.100961, 41.602499],
          [-124.114413, 41.616768],
          [-124.116037, 41.628849],
          [-124.120225, 41.640354],
          [-124.135552, 41.657307],
          [-124.139354, 41.671652],
          [-124.138373, 41.678881],
          [-124.143479, 41.709284],
          [-124.147412, 41.717955],
          [-124.154246, 41.728801],
          [-124.164716, 41.740126],
          [-124.17739, 41.745756],
          [-124.185363, 41.739351],
          [-124.19104, 41.736079],
          [-124.194953, 41.736778],
          [-124.203843, 41.747035],
          [-124.23972, 41.7708],
          [-124.242288, 41.772034],
          [-124.248704, 41.771459],
          [-124.255994, 41.783014],
          [-124.245027, 41.7923],
          [-124.230678, 41.818681],
          [-124.219592, 41.846432],
          [-124.208439, 41.888192],
          [-124.203402, 41.940964],
          [-124.204948, 41.983441],
          [-124.211605, 41.99846],
          [-124.100921, 41.996956],
          [-124.100216, 41.996842],
          [-124.087827, 41.996891],
          [-124.086661, 41.996869],
          [-123.821472, 41.995473],
          [-123.624554, 41.999837],
          [-123.518075, 42.000436],
          [-123.565442, 41.903141],
          [-123.642812, 41.889353],
          [-123.703768, 41.829117],
          [-123.660205, 41.714055],
          [-123.719089, 41.595258],
          [-123.612395, 41.448954],
          [-123.661363, 41.38209],
          [-123.770239, 41.380776],
          [-123.770551, 41.464193],
          [-124.065521, 41.464739]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13149",
      state: "13",
      county: "149",
      name: "Heard County",
      censusArea: 296.03,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.293902, 33.428079],
          [-85.015358, 33.425506],
          [-84.939015, 33.224693],
          [-85.236509, 33.129562],
          [-85.293902, 33.428079]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20059",
      state: "20",
      county: "059",
      name: "Franklin County",
      censusArea: 571.756,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.508267, 38.433933],
          [-95.500738, 38.738808],
          [-95.056412, 38.738587],
          [-95.065831, 38.389945],
          [-95.508328, 38.390278],
          [-95.508267, 38.433933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06025",
      state: "06",
      county: "025",
      name: "Imperial County",
      censusArea: 4176.603,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.103137, 32.904115],
          [-116.08109, 33.074833],
          [-116.085165, 33.425932],
          [-115.467506, 33.426992],
          [-114.627125, 33.433554],
          [-114.665278, 33.415358],
          [-114.721233, 33.396912],
          [-114.700103, 33.341045],
          [-114.672088, 33.258499],
          [-114.67536, 33.185489],
          [-114.679359, 33.159519],
          [-114.687074, 33.142196],
          [-114.696829, 33.131209],
          [-114.706175, 33.105335],
          [-114.707819, 33.091102],
          [-114.675104, 33.047532],
          [-114.62387, 33.02872],
          [-114.606282, 33.025703],
          [-114.589778, 33.026228],
          [-114.584765, 33.028231],
          [-114.578287, 33.035375],
          [-114.571653, 33.036624],
          [-114.523578, 33.030961],
          [-114.516912, 33.026871],
          [-114.511343, 33.023455],
          [-114.468605, 32.971649],
          [-114.46289, 32.905797],
          [-114.465715, 32.87942],
          [-114.465715, 32.879191],
          [-114.496284, 32.822326],
          [-114.496827, 32.822119],
          [-114.615733, 32.729427],
          [-114.615585, 32.728446],
          [-114.65826, 32.733799],
          [-114.65884, 32.73383],
          [-114.677091, 32.736218],
          [-114.678632, 32.736614],
          [-114.719633, 32.718763],
          [-115.465164, 32.6671],
          [-115.875842, 32.636424],
          [-116.106159, 32.618328],
          [-116.103137, 32.904115]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06029",
      state: "06",
      county: "029",
      name: "Kern County",
      censusArea: 8131.916,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.667292, 34.822526],
          [-118.140074, 34.82078],
          [-118.854253, 34.817772],
          [-118.881364, 34.790629],
          [-118.976721, 34.812199],
          [-119.243645, 34.814178],
          [-119.276946, 34.879675],
          [-119.442352, 34.901274],
          [-119.472754, 34.901174],
          [-119.472719, 35.076885],
          [-119.560975, 35.087673],
          [-119.553641, 35.179975],
          [-119.667056, 35.174809],
          [-119.666663, 35.262527],
          [-119.809449, 35.263584],
          [-119.809346, 35.350865],
          [-119.880172, 35.351211],
          [-119.880045, 35.439133],
          [-119.997392, 35.439495],
          [-119.997382, 35.468702],
          [-120.086674, 35.526554],
          [-120.085922, 35.614524],
          [-120.193918, 35.614359],
          [-120.193892, 35.726513],
          [-120.193934, 35.749393],
          [-120.194053, 35.749964],
          [-120.194146, 35.789204],
          [-119.538116, 35.789567],
          [-119.534559, 35.789758],
          [-119.214033, 35.790489],
          [-118.270456, 35.790009],
          [-118.008043, 35.789161],
          [-117.632996, 35.797251],
          [-117.651986, 35.709934],
          [-117.616195, 35.680856],
          [-117.630126, 35.564071],
          [-117.632011, 34.82227],
          [-117.667292, 34.822526]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06031",
      state: "06",
      county: "031",
      name: "Kings County",
      censusArea: 1389.42,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.905119, 36.401741],
          [-119.66629, 36.41896],
          [-119.573194, 36.488835],
          [-119.528792, 36.401465],
          [-119.474892, 36.400953],
          [-119.474607, 36.269025],
          [-119.529358, 36.26985],
          [-119.538116, 35.789567],
          [-120.194146, 35.789204],
          [-120.213979, 35.789276],
          [-120.243275, 35.877669],
          [-120.315068, 35.907186],
          [-119.95921, 36.181403],
          [-119.959227, 36.400975],
          [-119.905119, 36.401741]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06039",
      state: "06",
      county: "039",
      name: "Madera County",
      censusArea: 2137.069,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.733852, 36.948558],
          [-119.823421, 36.848333],
          [-119.932551, 36.84299],
          [-120.013334, 36.828228],
          [-120.027775, 36.814505],
          [-120.079458, 36.825345],
          [-120.132898, 36.809841],
          [-120.229179, 36.769687],
          [-120.422187, 36.840139],
          [-120.541696, 37.044505],
          [-120.400758, 37.117764],
          [-120.052055, 37.183108],
          [-119.761809, 37.417114],
          [-119.651191, 37.417832],
          [-119.651172, 37.461351],
          [-119.58422, 37.494696],
          [-119.583585, 37.560335],
          [-119.308995, 37.777986],
          [-119.293382, 37.767929],
          [-119.288381, 37.74503],
          [-119.268979, 37.73923],
          [-119.124312, 37.733944],
          [-119.022363, 37.585737],
          [-119.286658, 37.374943],
          [-119.335189, 37.310774],
          [-119.330573, 37.205791],
          [-119.361916, 37.167849],
          [-119.43306, 37.161516],
          [-119.470844, 37.110548],
          [-119.559412, 37.143619],
          [-119.537677, 37.104019],
          [-119.733852, 36.948558]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13067",
      state: "13",
      county: "067",
      name: "Cobb County",
      censusArea: 339.549,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.724435, 33.881859],
          [-84.737836, 34.079399],
          [-84.729235, 34.079199],
          [-84.722636, 34.079199],
          [-84.721936, 34.079099],
          [-84.687222, 34.0785],
          [-84.684745, 34.0785],
          [-84.682835, 34.078399],
          [-84.676415, 34.078298],
          [-84.674935, 34.078199],
          [-84.673935, 34.078299],
          [-84.672635, 34.078199],
          [-84.659234, 34.077999],
          [-84.521992, 34.075399],
          [-84.418927, 34.073298],
          [-84.383027, 33.9638],
          [-84.442708, 33.901543],
          [-84.480134, 33.817319],
          [-84.578132, 33.743507],
          [-84.619892, 33.805024],
          [-84.724139, 33.80617],
          [-84.723969, 33.815306],
          [-84.725181, 33.816995],
          [-84.725035, 33.819905],
          [-84.724435, 33.881859]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13069",
      state: "13",
      county: "069",
      name: "Coffee County",
      censusArea: 575.097,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.627342, 31.672672],
          [-82.597509, 31.469293],
          [-82.628181, 31.469351],
          [-82.628975, 31.36393],
          [-82.817544, 31.364486],
          [-82.844093, 31.403328],
          [-83.140483, 31.420395],
          [-83.138998, 31.427784],
          [-83.135927, 31.43051],
          [-83.139448, 31.436466],
          [-83.138618, 31.441528],
          [-83.144891, 31.454633],
          [-83.150474, 31.456787],
          [-83.150846, 31.462262],
          [-83.145874, 31.468926],
          [-83.145587, 31.472276],
          [-82.99836, 31.673164],
          [-82.995698, 31.780983],
          [-82.86309, 31.783005],
          [-82.836366, 31.816154],
          [-82.836633, 31.671398],
          [-82.627342, 31.672672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13073",
      state: "13",
      county: "073",
      name: "Columbia County",
      censusArea: 290.09,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.11663, 33.589723],
          [-82.098816, 33.586358],
          [-82.069039, 33.575382],
          [-82.046335, 33.56383],
          [-82.028238, 33.544934],
          [-82.294179, 33.354635],
          [-82.436814, 33.549658],
          [-82.425328, 33.650629],
          [-82.306414, 33.700644],
          [-82.218649, 33.686299],
          [-82.196583, 33.630582],
          [-82.179854, 33.615945],
          [-82.135046, 33.591044],
          [-82.12908, 33.589925],
          [-82.124841, 33.591274],
          [-82.11663, 33.589723]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13077",
      state: "13",
      county: "077",
      name: "Coweta County",
      censusArea: 440.892,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.939015, 33.224693],
          [-85.015358, 33.425506],
          [-84.850713, 33.511457],
          [-84.60954, 33.502511],
          [-84.62722, 33.440078],
          [-84.497527, 33.257422],
          [-84.508926, 33.245222],
          [-84.50029, 33.233444],
          [-84.502352, 33.221055],
          [-84.85236, 33.22359],
          [-84.862359, 33.191173],
          [-84.939015, 33.224693]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13081",
      state: "13",
      county: "081",
      name: "Crisp County",
      censusArea: 272.583,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.961278, 32.030595],
          [-83.609663, 32.027938],
          [-83.612256, 31.854089],
          [-83.612642, 31.804128],
          [-83.802223, 31.803495],
          [-83.939437, 31.847929],
          [-83.938192, 31.849489],
          [-83.9378, 31.854805],
          [-83.936736, 31.858043],
          [-83.932321, 31.859235],
          [-83.926524, 31.863292],
          [-83.928694, 31.871849],
          [-83.931152, 31.876984],
          [-83.934061, 31.88139],
          [-83.932091, 31.884632],
          [-83.928689, 31.886864],
          [-83.932301, 31.893801],
          [-83.932021, 31.898759],
          [-83.927383, 31.904142],
          [-83.925103, 31.905668],
          [-83.922494, 31.909653],
          [-83.961278, 32.030595]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13087",
      state: "13",
      county: "087",
      name: "Decatur County",
      censusArea: 597.14,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.508078, 31.0784],
          [-84.376612, 31.078883],
          [-84.380719, 30.689673],
          [-84.535042, 30.696523],
          [-84.53937, 30.696775],
          [-84.606249, 30.699872],
          [-84.606386, 30.699865],
          [-84.863465, 30.711487],
          [-84.755917, 30.88527],
          [-84.730855, 31.06919],
          [-84.542653, 31.079029],
          [-84.508078, 31.0784]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13093",
      state: "13",
      county: "093",
      name: "Dooly County",
      censusArea: 391.94,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.027853, 32.171083],
          [-83.965121, 32.248005],
          [-83.848379, 32.29097],
          [-83.615579, 32.288556],
          [-83.608547, 32.118405],
          [-83.608407, 32.116466],
          [-83.609663, 32.027938],
          [-83.961278, 32.030595],
          [-84.027853, 32.171083]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13095",
      state: "13",
      county: "095",
      name: "Dougherty County",
      censusArea: 328.689,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.450398, 31.621868],
          [-84.297801, 31.621951],
          [-84.043213, 31.623573],
          [-84.018404, 31.650274],
          [-83.982569, 31.46348],
          [-83.997679, 31.462773],
          [-83.997796, 31.443753],
          [-84.141124, 31.440179],
          [-84.429876, 31.436661],
          [-84.450398, 31.621868]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13101",
      state: "13",
      county: "101",
      name: "Echols County",
      censusArea: 414.894,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.136616, 30.624346],
          [-83.136236, 30.775229],
          [-83.088082, 30.848658],
          [-83.019419, 30.849453],
          [-83.013962, 30.844709],
          [-83.006577, 30.85947],
          [-82.993866, 30.860786],
          [-82.982155, 30.872249],
          [-82.971336, 30.869392],
          [-82.846353, 30.834988],
          [-82.766159, 30.732404],
          [-82.604005, 30.713676],
          [-82.584002, 30.591796],
          [-82.689271, 30.597719],
          [-82.689539, 30.597734],
          [-82.698618, 30.598232],
          [-82.698902, 30.598271],
          [-82.877259, 30.609024],
          [-82.878779, 30.609082],
          [-83.136616, 30.624346]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13103",
      state: "13",
      county: "103",
      name: "Effingham County",
      censusArea: 477.7,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.348155, 32.569294],
          [-81.328753, 32.561228],
          [-81.318255, 32.559722],
          [-81.286776, 32.544831],
          [-81.275415, 32.539457],
          [-81.199029, 32.467286],
          [-81.168032, 32.368391],
          [-81.140932, 32.349393],
          [-81.134332, 32.341693],
          [-81.122333, 32.305395],
          [-81.120333, 32.285796],
          [-81.128034, 32.276297],
          [-81.125457, 32.22713],
          [-81.195634, 32.237499],
          [-81.391698, 32.095886],
          [-81.43583, 32.241289],
          [-81.542716, 32.417446],
          [-81.548006, 32.489286],
          [-81.538558, 32.509059],
          [-81.389258, 32.595385],
          [-81.348155, 32.569294]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13109",
      state: "13",
      county: "109",
      name: "Evans County",
      censusArea: 182.853,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.969069, 32.268783],
          [-81.81628, 32.23747],
          [-81.780858, 32.15289],
          [-81.718658, 32.089351],
          [-81.761735, 32.0479],
          [-81.980041, 32.081502],
          [-82.025339, 32.278883],
          [-81.999843, 32.276189],
          [-81.969069, 32.268783]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13115",
      state: "13",
      county: "115",
      name: "Floyd County",
      censusArea: 509.911,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.069159, 34.587184],
          [-85.086761, 34.401264],
          [-85.005775, 34.392446],
          [-85.046871, 34.096412],
          [-85.257624, 34.100668],
          [-85.421731, 34.080821],
          [-85.455057, 34.250689],
          [-85.455371, 34.252854],
          [-85.458071, 34.265736],
          [-85.458693, 34.269437],
          [-85.462304, 34.286365],
          [-85.387351, 34.286097],
          [-85.334881, 34.369748],
          [-85.180548, 34.435996],
          [-85.107748, 34.587483],
          [-85.069159, 34.587184]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13119",
      state: "13",
      county: "119",
      name: "Franklin County",
      censusArea: 261.497,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.355269, 34.22373],
          [-83.393915, 34.324801],
          [-83.398396, 34.460883],
          [-83.177838, 34.477017],
          [-83.106874, 34.534053],
          [-83.087189, 34.515939],
          [-83.072746, 34.504349],
          [-83.052161, 34.493062],
          [-83.094238, 34.415901],
          [-83.11336, 34.27353],
          [-83.172745, 34.238522],
          [-83.297317, 34.264587],
          [-83.355269, 34.22373]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08061",
      state: "08",
      county: "061",
      name: "Kiowa County",
      censusArea: 1767.767,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.045211, 38.581609],
          [-102.045189, 38.558732],
          [-102.045223, 38.543797],
          [-102.045112, 38.523784],
          [-102.045262, 38.505532],
          [-102.045263, 38.505395],
          [-102.044944, 38.384419],
          [-102.044613, 38.312324],
          [-102.044568, 38.268819],
          [-102.044567, 38.268749],
          [-102.74233, 38.266971],
          [-103.399938, 38.265435],
          [-103.501764, 38.265016],
          [-103.504666, 38.516409],
          [-103.172878, 38.525315],
          [-103.172943, 38.61245],
          [-102.045288, 38.615168],
          [-102.045211, 38.581609]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08063",
      state: "08",
      county: "063",
      name: "Kit Carson County",
      censusArea: 2160.822,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.049806, 39.574058],
          [-102.049764, 39.56818],
          [-102.049554, 39.538932],
          [-102.049673, 39.536691],
          [-102.049679, 39.506183],
          [-102.049369, 39.423333],
          [-102.04937, 39.41821],
          [-102.049167, 39.403597],
          [-102.04896, 39.373712],
          [-102.048449, 39.303138],
          [-102.04725, 39.13702],
          [-102.047189, 39.133147],
          [-102.047134, 39.129701],
          [-102.046571, 39.047038],
          [-103.163025, 39.03761],
          [-103.154376, 39.565654],
          [-102.802932, 39.567841],
          [-102.049806, 39.574058]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08069",
      state: "08",
      county: "069",
      name: "Larimer County",
      censusArea: 2596.002,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.27686, 40.998173],
          [-105.256527, 40.998191],
          [-105.254779, 40.99821],
          [-104.943371, 40.99819],
          [-104.945551, 40.349099],
          [-105.05672, 40.349281],
          [-105.055089, 40.261793],
          [-105.196476, 40.261085],
          [-105.653321, 40.260457],
          [-105.854926, 40.486252],
          [-106.057715, 40.81712],
          [-106.185307, 40.933973],
          [-106.19055, 40.998056],
          [-106.061181, 40.996999],
          [-105.730421, 40.996886],
          [-105.724804, 40.99691],
          [-105.277138, 40.998173],
          [-105.27686, 40.998173]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12089",
      state: "12",
      county: "089",
      name: "Nassau County",
      censusArea: 648.639,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.906014, 30.822176],
          [-81.868608, 30.792754],
          [-81.840375, 30.786384],
          [-81.827014, 30.788933],
          [-81.806652, 30.789683],
          [-81.759338, 30.771377],
          [-81.741278, 30.762681],
          [-81.732627, 30.749934],
          [-81.719927, 30.744634],
          [-81.637222, 30.733835],
          [-81.624298, 30.736194],
          [-81.606221, 30.718135],
          [-81.544618, 30.712636],
          [-81.444124, 30.709714],
          [-81.432725, 30.703017],
          [-81.42742, 30.69802],
          [-81.430843, 30.669393],
          [-81.443099, 30.600938],
          [-81.442564, 30.555189],
          [-81.434064, 30.522569],
          [-81.442784, 30.50992],
          [-81.499575, 30.563793],
          [-81.543018, 30.523889],
          [-81.624513, 30.586232],
          [-81.72163, 30.571968],
          [-81.791665, 30.50561],
          [-82.049236, 30.273432],
          [-82.050031, 30.36249],
          [-82.016103, 30.497355],
          [-82.01699, 30.519358],
          [-82.005477, 30.563495],
          [-82.01329, 30.595665],
          [-82.037609, 30.633271],
          [-82.049401, 30.655296],
          [-82.050432, 30.676266],
          [-82.023213, 30.781987],
          [-81.999836, 30.788348],
          [-81.906014, 30.822176]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12091",
      state: "12",
      county: "091",
      name: "Okaloosa County",
      censusArea: 930.247,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.393551, 30.493444],
          [-86.394174, 30.46674],
          [-86.395383, 30.433336],
          [-86.397325, 30.378553],
          [-86.400403, 30.378927],
          [-86.412076, 30.380346],
          [-86.429322, 30.381389],
          [-86.454731, 30.382925],
          [-86.456197, 30.383014],
          [-86.457045, 30.383065],
          [-86.470849, 30.3839],
          [-86.47399, 30.383758],
          [-86.50615, 30.3823],
          [-86.529067, 30.386896],
          [-86.632953, 30.396299],
          [-86.750906, 30.391881],
          [-86.800283, 30.386477],
          [-86.785692, 30.996977],
          [-86.728392, 30.996739],
          [-86.727293, 30.996882],
          [-86.725379, 30.996872],
          [-86.688294, 30.995029],
          [-86.678383, 30.994537],
          [-86.664681, 30.994534],
          [-86.567586, 30.995109],
          [-86.563436, 30.995223],
          [-86.519938, 30.993245],
          [-86.512834, 30.9937],
          [-86.458319, 30.993998],
          [-86.454704, 30.993791],
          [-86.404912, 30.994049],
          [-86.391937, 30.994172],
          [-86.388647, 30.994181],
          [-86.388646, 30.994181],
          [-86.393551, 30.493444]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12097",
      state: "12",
      county: "097",
      name: "Osceola County",
      censusArea: 1327.453,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.862908, 28.347487],
          [-80.862881, 28.347439],
          [-80.862815, 28.347221],
          [-80.868881, 27.822522],
          [-80.87315, 27.642288],
          [-81.142164, 27.643238],
          [-81.131495, 27.6494],
          [-81.208124, 27.821125],
          [-81.301862, 27.862081],
          [-81.308696, 27.92188],
          [-81.378718, 28.011743],
          [-81.375467, 28.0851],
          [-81.455315, 28.085367],
          [-81.455647, 28.142894],
          [-81.524227, 28.142805],
          [-81.524083, 28.201012],
          [-81.551345, 28.249045],
          [-81.657189, 28.259217],
          [-81.657268, 28.347098],
          [-81.531837, 28.347384],
          [-81.513859, 28.347374],
          [-81.309571, 28.348051],
          [-80.862908, 28.347487]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12099",
      state: "12",
      county: "099",
      name: "Palm Beach County",
      censusArea: 1969.763,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.116782, 26.327845],
          [-80.121329, 26.32774],
          [-80.130164, 26.327858],
          [-80.13125, 26.327821],
          [-80.134037, 26.327905],
          [-80.15377, 26.327766],
          [-80.296935, 26.334356],
          [-80.297471, 26.334356],
          [-80.881233, 26.333806],
          [-80.886099, 26.72548],
          [-80.885639, 26.958919],
          [-80.38469, 26.958382],
          [-80.374523, 26.958386],
          [-80.246728, 26.957189],
          [-80.079531, 26.9705],
          [-80.066697, 26.927579],
          [-80.046263, 26.859238],
          [-80.031362, 26.796339],
          [-80.03212, 26.77153],
          [-80.036362, 26.77124],
          [-80.037462, 26.76634],
          [-80.032862, 26.715242],
          [-80.032862, 26.700842],
          [-80.035763, 26.676043],
          [-80.035363, 26.612346],
          [-80.038863, 26.569347],
          [-80.050363, 26.509549],
          [-80.060564, 26.444652],
          [-80.070564, 26.336455],
          [-80.072264, 26.335356],
          [-80.074837, 26.321032],
          [-80.087905, 26.320756],
          [-80.088999, 26.324357],
          [-80.091929, 26.326352],
          [-80.098268, 26.322599],
          [-80.099747, 26.327668],
          [-80.116782, 26.327845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12101",
      state: "12",
      county: "101",
      name: "Pasco County",
      censusArea: 746.886,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.678743, 28.433456],
          [-82.600636, 28.43391],
          [-82.253439, 28.434824],
          [-82.253293, 28.478491],
          [-82.054694, 28.478397],
          [-82.055819, 28.312794],
          [-82.106206, 28.25924],
          [-82.105853, 28.17165],
          [-82.643968, 28.173301],
          [-82.649725, 28.173314],
          [-82.651165, 28.173266],
          [-82.805097, 28.172181],
          [-82.797762, 28.187789],
          [-82.762643, 28.219013],
          [-82.76446, 28.220069],
          [-82.764103, 28.244345],
          [-82.759072, 28.25402],
          [-82.746188, 28.261192],
          [-82.732792, 28.291933],
          [-82.735463, 28.30039],
          [-82.73146, 28.325075],
          [-82.715822, 28.345501],
          [-82.706112, 28.368057],
          [-82.706322, 28.401325],
          [-82.697433, 28.420166],
          [-82.684137, 28.428019],
          [-82.678743, 28.433456]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12107",
      state: "12",
      county: "107",
      name: "Putnam County",
      censusArea: 727.622,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.523866, 29.631538],
          [-81.523691, 29.624995],
          [-81.52366, 29.622432],
          [-81.520596, 29.500249],
          [-81.478793, 29.399052],
          [-81.433992, 29.398552],
          [-81.450892, 29.378464],
          [-81.507989, 29.364511],
          [-81.518045, 29.361868],
          [-81.521764, 29.362194],
          [-81.540873, 29.356556],
          [-81.561194, 29.351686],
          [-81.680903, 29.32443],
          [-81.741422, 29.371049],
          [-81.776205, 29.487448],
          [-81.843009, 29.521004],
          [-82.055899, 29.471232],
          [-82.055027, 29.669608],
          [-82.050291, 29.709735],
          [-82.050829, 29.716098],
          [-82.055625, 29.718232],
          [-82.049244, 29.71867],
          [-81.939427, 29.747497],
          [-81.797218, 29.836649],
          [-81.581207, 29.840176],
          [-81.52523, 29.759497],
          [-81.523866, 29.631538]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12109",
      state: "12",
      county: "109",
      name: "St. Johns County",
      censusArea: 600.655,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.601212, 30.130541],
          [-81.433737, 30.106028],
          [-81.436929, 30.252335],
          [-81.379879, 30.252914],
          [-81.355591, 30.162563],
          [-81.308978, 29.96944],
          [-81.295268, 29.928614],
          [-81.288955, 29.91518],
          [-81.27654, 29.90046],
          [-81.270442, 29.883106],
          [-81.264693, 29.858212],
          [-81.263396, 29.820663],
          [-81.256711, 29.784693],
          [-81.240924, 29.739218],
          [-81.229015, 29.714693],
          [-81.212878, 29.670667],
          [-81.324059, 29.62561],
          [-81.52366, 29.622432],
          [-81.523691, 29.624995],
          [-81.523866, 29.631538],
          [-81.52523, 29.759497],
          [-81.581207, 29.840176],
          [-81.60099, 29.956017],
          [-81.688876, 30.028566],
          [-81.680215, 30.12124],
          [-81.650514, 30.121541],
          [-81.623813, 30.133641],
          [-81.601212, 30.130541]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12113",
      state: "12",
      county: "113",
      name: "Santa Rosa County",
      censusArea: 1011.605,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.919292, 30.370675],
          [-87.23297, 30.348999],
          [-87.1344, 30.420294],
          [-87.124968, 30.500196],
          [-87.258884, 30.611281],
          [-87.269407, 30.711687],
          [-87.308502, 30.72691],
          [-87.313611, 30.847266],
          [-87.286882, 30.925441],
          [-87.163084, 30.999051],
          [-87.162614, 30.999055],
          [-87.068633, 30.999143],
          [-87.064063, 30.999191],
          [-87.053737, 30.999131],
          [-87.039989, 30.999594],
          [-87.036366, 30.999348],
          [-87.027107, 30.999255],
          [-86.888135, 30.997577],
          [-86.872989, 30.997631],
          [-86.831934, 30.997378],
          [-86.830497, 30.997401],
          [-86.785918, 30.996978],
          [-86.785692, 30.996977],
          [-86.800283, 30.386477],
          [-86.850625, 30.380967],
          [-86.909679, 30.372423],
          [-86.919292, 30.370675]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12115",
      state: "12",
      county: "115",
      name: "Sarasota County",
      censusArea: 555.868,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.171655, 27.032769],
          [-82.255237, 27.032975],
          [-82.255521, 26.945264],
          [-82.299896, 26.945564],
          [-82.322536, 26.945627],
          [-82.326294, 26.945846],
          [-82.339027, 26.945912],
          [-82.340238, 26.94556],
          [-82.375737, 26.946041],
          [-82.400618, 26.984937],
          [-82.419218, 27.020736],
          [-82.445718, 27.060634],
          [-82.460319, 27.099933],
          [-82.465319, 27.110732],
          [-82.46889, 27.113612],
          [-82.477019, 27.141231],
          [-82.512319, 27.207528],
          [-82.539719, 27.254326],
          [-82.54512, 27.261026],
          [-82.55902, 27.268826],
          [-82.569754, 27.279452],
          [-82.569248, 27.298588],
          [-82.57602, 27.309324],
          [-82.597629, 27.335754],
          [-82.623863, 27.362206],
          [-82.642821, 27.38972],
          [-82.642837, 27.389737],
          [-82.252264, 27.386243],
          [-82.253596, 27.208916],
          [-82.056497, 27.207769],
          [-82.057469, 27.032119],
          [-82.171655, 27.032769]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12117",
      state: "12",
      county: "117",
      name: "Seminole County",
      censusArea: 309.221,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.210452, 28.802453],
          [-81.195021, 28.792302],
          [-81.183198, 28.797468],
          [-81.178936, 28.781886],
          [-81.10575, 28.828541],
          [-80.98725, 28.612997],
          [-81.326991, 28.610298],
          [-81.327911, 28.611268],
          [-81.32865, 28.639334],
          [-81.331099, 28.639581],
          [-81.357965, 28.639187],
          [-81.365289, 28.640468],
          [-81.457975, 28.640336],
          [-81.459501, 28.640285],
          [-81.459695, 28.644475],
          [-81.414446, 28.784955],
          [-81.375088, 28.863366],
          [-81.376006, 28.865901],
          [-81.373491, 28.87025],
          [-81.372009, 28.869039],
          [-81.369692, 28.870536],
          [-81.368608, 28.872921],
          [-81.366705, 28.87325],
          [-81.366158, 28.874744],
          [-81.366764, 28.876619],
          [-81.367052, 28.878315],
          [-81.36694, 28.879227],
          [-81.24997, 28.833106],
          [-81.229881, 28.832621],
          [-81.226003, 28.83263],
          [-81.222874, 28.826862],
          [-81.224596, 28.822039],
          [-81.215898, 28.814423],
          [-81.216345, 28.808302],
          [-81.210452, 28.802453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12127",
      state: "12",
      county: "127",
      name: "Volusia County",
      censusArea: 1101.032,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.178936, 28.781886],
          [-81.183198, 28.797468],
          [-81.195021, 28.792302],
          [-81.210452, 28.802453],
          [-81.216345, 28.808302],
          [-81.215898, 28.814423],
          [-81.224596, 28.822039],
          [-81.222874, 28.826862],
          [-81.226003, 28.83263],
          [-81.229881, 28.832621],
          [-81.24997, 28.833106],
          [-81.36694, 28.879227],
          [-81.354554, 28.984375],
          [-81.502055, 29.097802],
          [-81.531262, 29.176621],
          [-81.61234, 29.202943],
          [-81.641916, 29.276766],
          [-81.648438, 29.290173],
          [-81.655677, 29.29994],
          [-81.667885, 29.301488],
          [-81.675523, 29.310628],
          [-81.680903, 29.32443],
          [-81.561194, 29.351686],
          [-81.540873, 29.356556],
          [-81.521764, 29.362194],
          [-81.518045, 29.361868],
          [-81.507989, 29.364511],
          [-81.450892, 29.378464],
          [-81.433992, 29.398552],
          [-81.41729, 29.261156],
          [-81.150081, 29.265957],
          [-81.155881, 29.410954],
          [-81.101923, 29.427055],
          [-81.046678, 29.307856],
          [-80.995423, 29.206052],
          [-80.966176, 29.14796],
          [-80.944376, 29.110861],
          [-80.907275, 29.064262],
          [-80.893675, 29.036163],
          [-80.878275, 29.010563],
          [-80.787021, 28.875266],
          [-80.732244, 28.791237],
          [-80.967895, 28.790197],
          [-80.964466, 28.612992],
          [-80.98725, 28.612997],
          [-81.10575, 28.828541],
          [-81.178936, 28.781886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12131",
      state: "12",
      county: "131",
      name: "Walton County",
      censusArea: 1037.625,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.992736, 30.38932],
          [-85.994712, 30.311702],
          [-85.99459, 30.290853],
          [-85.996083, 30.269148],
          [-85.999937, 30.27078],
          [-86.089963, 30.303569],
          [-86.222561, 30.343585],
          [-86.2987, 30.363049],
          [-86.364175, 30.374524],
          [-86.397325, 30.378553],
          [-86.395383, 30.433336],
          [-86.394174, 30.46674],
          [-86.393551, 30.493444],
          [-86.388646, 30.994181],
          [-86.374545, 30.994474],
          [-86.36927, 30.994477],
          [-86.364907, 30.994455],
          [-86.304596, 30.994029],
          [-86.289247, 30.993798],
          [-86.187246, 30.993992],
          [-86.180232, 30.994005],
          [-86.175204, 30.993798],
          [-86.168979, 30.993706],
          [-86.162886, 30.993682],
          [-86.056213, 30.993133],
          [-86.052462, 30.993247],
          [-86.035039, 30.99332],
          [-86.037591, 30.704572],
          [-85.844421, 30.70313],
          [-85.915297, 30.636427],
          [-85.858001, 30.496905],
          [-85.992736, 30.38932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13003",
      state: "13",
      county: "003",
      name: "Atkinson County",
      censusArea: 339.382,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.971247, 31.183988],
          [-83.046885, 31.18368],
          [-83.033531, 31.27467],
          [-83.140483, 31.420395],
          [-82.844093, 31.403328],
          [-82.817544, 31.364486],
          [-82.628975, 31.36393],
          [-82.629622, 31.274726],
          [-82.699206, 31.278104],
          [-82.671669, 31.183739],
          [-82.971247, 31.183988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13005",
      state: "13",
      county: "005",
      name: "Bacon County",
      censusArea: 258.576,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.52142, 31.710796],
          [-82.226585, 31.530699],
          [-82.344843, 31.43013],
          [-82.417246, 31.417119],
          [-82.494718, 31.46869],
          [-82.597509, 31.469293],
          [-82.627342, 31.672672],
          [-82.521393, 31.672512],
          [-82.52142, 31.710796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13009",
      state: "13",
      county: "009",
      name: "Baldwin County",
      censusArea: 257.844,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.159585, 33.002803],
          [-83.357685, 32.926141],
          [-83.380383, 32.998077],
          [-83.414997, 33.112833],
          [-83.42909, 33.185352],
          [-83.274108, 33.187238],
          [-83.097507, 33.15141],
          [-83.100834, 33.049849],
          [-83.052197, 33.080682],
          [-83.073648, 32.946562],
          [-83.159585, 33.002803]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13015",
      state: "13",
      county: "015",
      name: "Bartow County",
      censusArea: 459.544,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.005775, 34.392446],
          [-84.862863, 34.396601],
          [-84.653232, 34.41259],
          [-84.659234, 34.077999],
          [-84.672635, 34.078199],
          [-84.673935, 34.078299],
          [-84.674935, 34.078199],
          [-84.676415, 34.078298],
          [-84.682835, 34.078399],
          [-84.684745, 34.0785],
          [-84.687222, 34.0785],
          [-84.721936, 34.079099],
          [-84.722636, 34.079199],
          [-84.729235, 34.079199],
          [-84.737836, 34.079399],
          [-84.922742, 34.082497],
          [-85.046871, 34.096412],
          [-85.005775, 34.392446]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13023",
      state: "13",
      county: "023",
      name: "Bleckley County",
      censusArea: 215.87,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.226536, 32.584201],
          [-83.138991, 32.423069],
          [-83.173252, 32.452533],
          [-83.30475, 32.341958],
          [-83.346528, 32.272489],
          [-83.498039, 32.401715],
          [-83.482648, 32.424536],
          [-83.497923, 32.452198],
          [-83.226536, 32.584201]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13025",
      state: "13",
      county: "025",
      name: "Brantley County",
      censusArea: 442.362,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.936006, 31.047993],
          [-82.06087, 31.075809],
          [-82.131698, 31.010714],
          [-82.208262, 31.084768],
          [-82.208732, 31.170938],
          [-82.284561, 31.224449],
          [-82.194824, 31.20759],
          [-82.100067, 31.27671],
          [-82.006775, 31.277063],
          [-82.041129, 31.373721],
          [-81.923238, 31.345875],
          [-81.801052, 31.363737],
          [-81.731694, 31.330048],
          [-81.766322, 31.169595],
          [-81.936006, 31.047993]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13031",
      state: "13",
      county: "031",
      name: "Bulloch County",
      censusArea: 672.809,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.780858, 32.15289],
          [-81.81628, 32.23747],
          [-81.969069, 32.268783],
          [-81.918671, 32.41541],
          [-82.030233, 32.53877],
          [-82.026842, 32.555163],
          [-82.001236, 32.60691],
          [-81.841005, 32.649093],
          [-81.689643, 32.54629],
          [-81.548006, 32.489286],
          [-81.542716, 32.417446],
          [-81.43583, 32.241289],
          [-81.780858, 32.15289]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13037",
      state: "13",
      county: "037",
      name: "Calhoun County",
      censusArea: 280.367,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.546854, 31.621199],
          [-84.450416, 31.621255],
          [-84.450398, 31.621868],
          [-84.429876, 31.436661],
          [-84.637579, 31.433926],
          [-84.789032, 31.436356],
          [-84.819807, 31.501192],
          [-84.817843, 31.619771],
          [-84.546854, 31.621199]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13049",
      state: "13",
      county: "049",
      name: "Charlton County",
      censusArea: 773.576,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.131698, 31.010714],
          [-82.06087, 31.075809],
          [-81.936006, 31.047993],
          [-81.892028, 30.960474],
          [-81.906014, 30.822176],
          [-81.999836, 30.788348],
          [-82.023213, 30.781987],
          [-82.050432, 30.676266],
          [-82.049401, 30.655296],
          [-82.037609, 30.633271],
          [-82.01329, 30.595665],
          [-82.005477, 30.563495],
          [-82.01699, 30.519358],
          [-82.016103, 30.497355],
          [-82.050031, 30.36249],
          [-82.050069, 30.362338],
          [-82.124835, 30.366564],
          [-82.165192, 30.358035],
          [-82.171508, 30.359869],
          [-82.189583, 30.376213],
          [-82.204151, 30.40133],
          [-82.210291, 30.42459],
          [-82.20604, 30.455507],
          [-82.21233, 30.499558],
          [-82.214385, 30.566958],
          [-82.214847, 30.567009],
          [-82.149872, 30.784336],
          [-82.420442, 30.795229],
          [-82.415603, 31.01359],
          [-82.131698, 31.010714]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13053",
      state: "13",
      county: "053",
      name: "Chattahoochee County",
      censusArea: 248.736,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.923638, 32.231539],
          [-84.92165, 32.233306],
          [-84.909052, 32.26354],
          [-84.916611, 32.281177],
          [-84.93868, 32.300708],
          [-85.001874, 32.322015],
          [-85.007103, 32.328362],
          [-85.008096, 32.336677],
          [-84.987386, 32.381201],
          [-84.987278, 32.381623],
          [-84.848664, 32.41464],
          [-84.844216, 32.413248],
          [-84.83436, 32.413216],
          [-84.823992, 32.413967],
          [-84.814632, 32.414448],
          [-84.811384, 32.418608],
          [-84.809432, 32.420864],
          [-84.805175, 32.418912],
          [-84.798647, 32.415968],
          [-84.791463, 32.414783],
          [-84.788471, 32.418031],
          [-84.782983, 32.421711],
          [-84.781559, 32.422863],
          [-84.778359, 32.423054],
          [-84.774567, 32.421839],
          [-84.769431, 32.422319],
          [-84.69451, 32.518732],
          [-84.686265, 32.519624],
          [-84.678157, 32.518915],
          [-84.661721, 32.529148],
          [-84.658017, 32.528759],
          [-84.648977, 32.534956],
          [-84.637323, 32.534855],
          [-84.657325, 32.49494],
          [-84.65892, 32.232852],
          [-84.923638, 32.231539]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13057",
      state: "13",
      county: "057",
      name: "Cherokee County",
      censusArea: 421.674,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.258075, 34.335156],
          [-84.258743, 34.185909],
          [-84.328263, 34.186144],
          [-84.418927, 34.073298],
          [-84.521992, 34.075399],
          [-84.659234, 34.077999],
          [-84.653232, 34.41259],
          [-84.58263, 34.381492],
          [-84.257586, 34.380992],
          [-84.257652, 34.375111],
          [-84.257812, 34.372631],
          [-84.258075, 34.335156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13063",
      state: "13",
      county: "063",
      name: "Clayton County",
      censusArea: 141.57,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.388118, 33.352465],
          [-84.381759, 33.463414],
          [-84.458665, 33.550933],
          [-84.458579, 33.556242],
          [-84.458924, 33.559759],
          [-84.458927, 33.565911],
          [-84.45863, 33.572107],
          [-84.458399, 33.572743],
          [-84.458627, 33.586456],
          [-84.458514, 33.608625],
          [-84.457726, 33.64887],
          [-84.365325, 33.647809],
          [-84.360224, 33.647909],
          [-84.350224, 33.647908],
          [-84.282619, 33.647009],
          [-84.281273, 33.647411],
          [-84.283518, 33.502514],
          [-84.353584, 33.436165],
          [-84.3544, 33.352514],
          [-84.388118, 33.352465]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09013",
      state: "09",
      county: "013",
      name: "Tolland County",
      censusArea: 410.214,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.10216, 42.028962],
          [-72.099229, 41.958948],
          [-72.123632, 41.95898],
          [-72.12452, 41.95905],
          [-72.151654, 41.959302],
          [-72.21698, 41.958161],
          [-72.217719, 41.835184],
          [-72.164558, 41.834913],
          [-72.156893, 41.753818],
          [-72.156321, 41.753491],
          [-72.239559, 41.714191],
          [-72.410629, 41.601798],
          [-72.463899, 41.745572],
          [-72.51733, 41.869897],
          [-72.51363, 41.942997],
          [-72.51333, 41.945401],
          [-72.503065, 41.946608],
          [-72.492729, 41.947497],
          [-72.498928, 41.977893],
          [-72.49973, 41.984295],
          [-72.509081, 42.033539],
          [-72.509187, 42.034607],
          [-72.397428, 42.033302],
          [-72.198828, 42.030982],
          [-72.135715, 42.030245],
          [-72.135687, 42.030245],
          [-72.10216, 42.028962]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US10005",
      state: "10",
      county: "005",
      name: "Sussex County",
      censusArea: 936.079,
      stateName: "Delaware"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.311923, 38.945917],
          [-75.311882, 38.945698],
          [-75.311542, 38.944633],
          [-75.302552, 38.939002],
          [-75.312282, 38.924594],
          [-75.304078, 38.91316],
          [-75.263115, 38.877351],
          [-75.232029, 38.844254],
          [-75.205329, 38.823386],
          [-75.190552, 38.806861],
          [-75.160748, 38.791224],
          [-75.159022, 38.790193],
          [-75.134022, 38.782242],
          [-75.113331, 38.782998],
          [-75.097103, 38.788703],
          [-75.093654, 38.793992],
          [-75.097197, 38.803101],
          [-75.093805, 38.803812],
          [-75.089473, 38.797198],
          [-75.082153, 38.772157],
          [-75.080217, 38.750112],
          [-75.079221, 38.738238],
          [-75.06551, 38.66103],
          [-75.065217, 38.632394],
          [-75.06192, 38.608869],
          [-75.061259, 38.608602],
          [-75.060478, 38.608012],
          [-75.060032, 38.607709],
          [-75.049748, 38.486387],
          [-75.048939, 38.451263],
          [-75.049268, 38.451264],
          [-75.05251, 38.451273],
          [-75.053483, 38.451274],
          [-75.066327, 38.451291],
          [-75.069909, 38.451276],
          [-75.070356, 38.451276],
          [-75.085814, 38.451258],
          [-75.088281, 38.451256],
          [-75.089649, 38.451254],
          [-75.141894, 38.451196],
          [-75.185413, 38.451013],
          [-75.252723, 38.451397],
          [-75.26035, 38.451492],
          [-75.341247, 38.45197],
          [-75.34125, 38.45197],
          [-75.355797, 38.452008],
          [-75.371054, 38.452107],
          [-75.393563, 38.452114],
          [-75.394786, 38.45216],
          [-75.410884, 38.4524],
          [-75.424831, 38.45261],
          [-75.428728, 38.452671],
          [-75.47915, 38.453699],
          [-75.500142, 38.454144],
          [-75.502961, 38.45422],
          [-75.521304, 38.454657],
          [-75.52273, 38.454657],
          [-75.533763, 38.454958],
          [-75.559212, 38.455563],
          [-75.559934, 38.455579],
          [-75.57411, 38.455991],
          [-75.583601, 38.456424],
          [-75.589307, 38.456286],
          [-75.593082, 38.456404],
          [-75.598069, 38.456855],
          [-75.630457, 38.457904],
          [-75.662843, 38.458759],
          [-75.665585, 38.4589],
          [-75.693521, 38.460128],
          [-75.696369, 38.492373],
          [-75.696688, 38.496467],
          [-75.698777, 38.522001],
          [-75.700179, 38.542717],
          [-75.701465, 38.559433],
          [-75.701565, 38.560736],
          [-75.703445, 38.58512],
          [-75.703981, 38.592066],
          [-75.705774, 38.61474],
          [-75.70586, 38.616268],
          [-75.706235, 38.621296],
          [-75.706585, 38.626125],
          [-75.707346, 38.63528],
          [-75.707352, 38.635359],
          [-75.722028, 38.822078],
          [-75.722599, 38.829859],
          [-75.555013, 38.835649],
          [-75.381339, 38.961285],
          [-75.311923, 38.945917]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US11001",
      state: "11",
      county: "001",
      name: "Washington County",
      lsad: "",
      censusArea: 61.048,
      stateName: "District Of Columbia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.032986, 38.8395],
          [-77.031698, 38.850512],
          [-77.040599, 38.871212],
          [-77.068199, 38.899811],
          [-77.0902, 38.904211],
          [-77.1012, 38.911111],
          [-77.119863, 38.934265],
          [-77.1199, 38.934311],
          [-77.1007, 38.94891],
          [-77.054299, 38.98511],
          [-77.040999, 38.99511],
          [-77.036299, 38.99171],
          [-77.015598, 38.97591],
          [-77.013798, 38.97441],
          [-77.008298, 38.97011],
          [-77.002636, 38.965521],
          [-77.002498, 38.96541],
          [-76.941722, 38.918019],
          [-76.909395, 38.892812],
          [-76.910795, 38.891712],
          [-76.919295, 38.885112],
          [-76.920195, 38.884412],
          [-76.949696, 38.861312],
          [-76.953696, 38.858512],
          [-77.001397, 38.821513],
          [-77.024392, 38.80297],
          [-77.038598, 38.791513],
          [-77.032986, 38.8395]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12003",
      state: "12",
      county: "003",
      name: "Baker County",
      censusArea: 585.231,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.050031, 30.36249],
          [-82.049236, 30.273432],
          [-82.049411, 30.186933],
          [-82.049425, 30.143136],
          [-82.142578, 30.143117],
          [-82.458364, 30.136449],
          [-82.459792, 30.584287],
          [-82.459544, 30.584272],
          [-82.418915, 30.581745],
          [-82.374844, 30.579004],
          [-82.287343, 30.573458],
          [-82.2581, 30.571559],
          [-82.249841, 30.570863],
          [-82.214847, 30.567009],
          [-82.214385, 30.566958],
          [-82.21233, 30.499558],
          [-82.20604, 30.455507],
          [-82.210291, 30.42459],
          [-82.204151, 30.40133],
          [-82.189583, 30.376213],
          [-82.171508, 30.359869],
          [-82.165192, 30.358035],
          [-82.124835, 30.366564],
          [-82.050069, 30.362338],
          [-82.050031, 30.36249]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12005",
      state: "12",
      county: "005",
      name: "Bay County",
      censusArea: 758.459,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.996083, 30.269148],
          [-85.99459, 30.290853],
          [-85.994712, 30.311702],
          [-85.992736, 30.38932],
          [-85.8535, 30.440623],
          [-85.487968, 30.436655],
          [-85.486358, 30.567574],
          [-85.434782, 30.56756],
          [-85.383948, 30.566856],
          [-85.389679, 30.200973],
          [-85.388374, 29.926919],
          [-85.388677, 29.924355],
          [-85.405052, 29.938487],
          [-85.425956, 29.949888],
          [-85.460488, 29.959579],
          [-85.469425, 29.957788],
          [-85.487764, 29.961227],
          [-85.509148, 29.971466],
          [-85.541176, 29.995791],
          [-85.571907, 30.02644],
          [-85.58139, 30.037783],
          [-85.588242, 30.055543],
          [-85.601178, 30.056342],
          [-85.618254, 30.065481],
          [-85.637285, 30.073319],
          [-85.653251, 30.077839],
          [-85.69681, 30.09689],
          [-85.730054, 30.118153],
          [-85.74993, 30.136537],
          [-85.775405, 30.15629],
          [-85.811219, 30.17832],
          [-85.878138, 30.215619],
          [-85.9226, 30.238024],
          [-85.996083, 30.269148]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12011",
      state: "12",
      county: "011",
      name: "Broward County",
      censusArea: 1209.785,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.278764, 25.970968],
          [-80.295187, 25.97057],
          [-80.294972, 25.95677],
          [-80.326712, 25.957118],
          [-80.44049, 25.956861],
          [-80.680016, 25.956857],
          [-80.680038, 25.978749],
          [-80.872932, 25.979434],
          [-80.872748, 26.00037],
          [-80.879809, 26.259455],
          [-80.881233, 26.333806],
          [-80.297471, 26.334356],
          [-80.296935, 26.334356],
          [-80.15377, 26.327766],
          [-80.134037, 26.327905],
          [-80.13125, 26.327821],
          [-80.130164, 26.327858],
          [-80.121329, 26.32774],
          [-80.116782, 26.327845],
          [-80.099747, 26.327668],
          [-80.098268, 26.322599],
          [-80.091929, 26.326352],
          [-80.088999, 26.324357],
          [-80.087905, 26.320756],
          [-80.074837, 26.321032],
          [-80.075264, 26.318656],
          [-80.079865, 26.264358],
          [-80.085565, 26.249259],
          [-80.089365, 26.231859],
          [-80.101366, 26.147762],
          [-80.105266, 26.096264],
          [-80.106813, 26.092991],
          [-80.108995, 26.088372],
          [-80.109566, 26.087165],
          [-80.112334, 26.053193],
          [-80.117778, 25.986369],
          [-80.117798, 25.975152],
          [-80.118078, 25.975158],
          [-80.123874, 25.974484],
          [-80.133862, 25.97411],
          [-80.133867, 25.974868],
          [-80.142227, 25.974674],
          [-80.143501, 25.974511],
          [-80.246763, 25.971874],
          [-80.278764, 25.970968]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12015",
      state: "12",
      county: "015",
      name: "Charlotte County",
      censusArea: 680.279,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.375737, 26.946041],
          [-82.340238, 26.94556],
          [-82.339027, 26.945912],
          [-82.326294, 26.945846],
          [-82.322536, 26.945627],
          [-82.299896, 26.945564],
          [-82.255521, 26.945264],
          [-82.255237, 27.032975],
          [-82.171655, 27.032769],
          [-82.057469, 27.032119],
          [-81.562202, 27.033836],
          [-81.565931, 26.769552],
          [-82.062029, 26.770439],
          [-82.061401, 26.774279],
          [-82.061401, 26.789228],
          [-82.055076, 26.802452],
          [-82.057951, 26.822],
          [-82.058526, 26.838674],
          [-82.056801, 26.858797],
          [-82.059101, 26.876621],
          [-82.066575, 26.88237],
          [-82.090723, 26.888694],
          [-82.093023, 26.906518],
          [-82.090148, 26.923191],
          [-82.083249, 26.927791],
          [-82.067725, 26.927791],
          [-82.061976, 26.931241],
          [-82.061401, 26.938715],
          [-82.063126, 26.950214],
          [-82.076349, 26.958263],
          [-82.107972, 26.957688],
          [-82.113039, 26.955788],
          [-82.117171, 26.954239],
          [-82.124645, 26.945615],
          [-82.137294, 26.926066],
          [-82.162017, 26.925491],
          [-82.169491, 26.923191],
          [-82.175241, 26.916867],
          [-82.172941, 26.897319],
          [-82.156267, 26.851898],
          [-82.147068, 26.789803],
          [-82.151093, 26.783479],
          [-82.172941, 26.778879],
          [-82.17869, 26.772555],
          [-82.209329, 26.772146],
          [-82.221812, 26.77198],
          [-82.232193, 26.78288],
          [-82.233311, 26.784054],
          [-82.234019, 26.783251],
          [-82.241935, 26.774279],
          [-82.251134, 26.755881],
          [-82.259867, 26.717398],
          [-82.263804, 26.725644],
          [-82.264682, 26.756836],
          [-82.269499, 26.784674],
          [-82.271699, 26.789516],
          [-82.281552, 26.811203],
          [-82.289086, 26.827784],
          [-82.301736, 26.841588],
          [-82.351649, 26.908384],
          [-82.375737, 26.946041]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12071",
      state: "12",
      county: "071",
      name: "Lee County",
      censusArea: 784.513,
      stateName: "Florida"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-82.255777, 26.703437],
            [-82.255159, 26.70816],
            [-82.246535, 26.706435],
            [-82.24251, 26.694361],
            [-82.24596, 26.688612],
            [-82.246535, 26.683437],
            [-82.23744, 26.661976],
            [-82.218342, 26.626407],
            [-82.214337, 26.602944],
            [-82.196514, 26.559823],
            [-82.187315, 26.527626],
            [-82.177541, 26.502328],
            [-82.166042, 26.489679],
            [-82.149368, 26.477605],
            [-82.131545, 26.47703],
            [-82.120046, 26.473581],
            [-82.088423, 26.455182],
            [-82.076924, 26.466106],
            [-82.062551, 26.470131],
            [-82.038403, 26.456907],
            [-82.015607, 26.454858],
            [-82.013713, 26.454258],
            [-82.013913, 26.452058],
            [-82.063114, 26.425459],
            [-82.075015, 26.422059],
            [-82.082915, 26.422059],
            [-82.098115, 26.424959],
            [-82.126671, 26.436279],
            [-82.148716, 26.455458],
            [-82.172917, 26.467658],
            [-82.177017, 26.471558],
            [-82.180717, 26.476257],
            [-82.186441, 26.489221],
            [-82.201402, 26.55631],
            [-82.205523, 26.566536],
            [-82.222131, 26.590402],
            [-82.238872, 26.636433],
            [-82.248659, 26.654337],
            [-82.263008, 26.673388],
            [-82.268007, 26.682791],
            [-82.264351, 26.698496],
            [-82.255777, 26.703437]
          ]
        ],
        [
          [
            [-81.690494, 26.317334],
            [-81.845834, 26.330378],
            [-81.868983, 26.378648],
            [-81.90191, 26.410859],
            [-81.90271, 26.416159],
            [-81.91171, 26.427158],
            [-81.923611, 26.436658],
            [-81.938411, 26.445058],
            [-81.956611, 26.452358],
            [-81.964212, 26.457957],
            [-81.967112, 26.462857],
            [-81.966212, 26.465057],
            [-81.969509, 26.476505],
            [-81.980712, 26.480957],
            [-81.997012, 26.484856],
            [-82.008961, 26.484052],
            [-82.01368, 26.490829],
            [-82.00908, 26.505203],
            [-82.024604, 26.512677],
            [-82.043577, 26.519577],
            [-82.06715, 26.513252],
            [-82.07175, 26.492554],
            [-82.094748, 26.48393],
            [-82.105672, 26.48393],
            [-82.111996, 26.54085],
            [-82.118896, 26.560973],
            [-82.122345, 26.579371],
            [-82.137869, 26.637441],
            [-82.149943, 26.654115],
            [-82.181565, 26.681712],
            [-82.17984, 26.696661],
            [-82.173516, 26.701836],
            [-82.151668, 26.704136],
            [-82.139019, 26.702986],
            [-82.125795, 26.699536],
            [-82.118896, 26.690912],
            [-82.106247, 26.667339],
            [-82.099922, 26.662739],
            [-82.093023, 26.665614],
            [-82.086698, 26.685162],
            [-82.084974, 26.702411],
            [-82.079799, 26.716784],
            [-82.066575, 26.742657],
            [-82.062029, 26.770439],
            [-81.565931, 26.769552],
            [-81.563763, 26.513324],
            [-81.56218, 26.422625],
            [-81.659506, 26.421077],
            [-81.690494, 26.317334]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12079",
      state: "12",
      county: "079",
      name: "Madison County",
      censusArea: 695.948,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.309455, 30.634417],
          [-83.30925, 30.634405],
          [-83.272612, 30.627687],
          [-83.243766, 30.473132],
          [-83.170969, 30.385225],
          [-83.247253, 30.26067],
          [-83.249862, 30.260353],
          [-83.36726, 30.26044],
          [-83.469267, 30.260351],
          [-83.468768, 30.304212],
          [-83.820235, 30.30354],
          [-83.806667, 30.371689],
          [-83.72172, 30.434354],
          [-83.731107, 30.492315],
          [-83.604186, 30.581916],
          [-83.61172, 30.651258],
          [-83.611667, 30.651255],
          [-83.448895, 30.64241],
          [-83.440021, 30.642023],
          [-83.429584, 30.641496],
          [-83.429477, 30.641519],
          [-83.390062, 30.639333],
          [-83.37946, 30.63868],
          [-83.357703, 30.637359],
          [-83.341011, 30.636346],
          [-83.340852, 30.636336],
          [-83.311647, 30.634577],
          [-83.309455, 30.634417]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12081",
      state: "12",
      county: "081",
      name: "Manatee County",
      censusArea: 742.931,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.642837, 27.389737],
          [-82.675121, 27.424318],
          [-82.691821, 27.437218],
          [-82.691004, 27.444331],
          [-82.707821, 27.487615],
          [-82.714521, 27.500415],
          [-82.724522, 27.513614],
          [-82.743017, 27.531086],
          [-82.745748, 27.538834],
          [-82.742437, 27.53936],
          [-82.708121, 27.523514],
          [-82.710621, 27.501715],
          [-82.706821, 27.498415],
          [-82.690421, 27.496415],
          [-82.686421, 27.497215],
          [-82.686921, 27.508015],
          [-82.683621, 27.513115],
          [-82.674621, 27.519614],
          [-82.66202, 27.522814],
          [-82.65072, 27.523115],
          [-82.646014, 27.53354],
          [-82.632053, 27.551908],
          [-82.612019, 27.571231],
          [-82.613003, 27.582837],
          [-82.612749, 27.583319],
          [-82.611717, 27.585283],
          [-82.596488, 27.594045],
          [-82.584629, 27.596021],
          [-82.570607, 27.608882],
          [-82.565667, 27.615713],
          [-82.558538, 27.638678],
          [-82.554499, 27.645145],
          [-82.549548, 27.645557],
          [-82.524947, 27.645422],
          [-82.52475, 27.645422],
          [-82.054349, 27.646382],
          [-82.055753, 27.338264],
          [-82.056497, 27.207769],
          [-82.253596, 27.208916],
          [-82.252264, 27.386243],
          [-82.642837, 27.389737]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08085",
      state: "08",
      county: "085",
      name: "Montrose County",
      censusArea: 2240.695,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.042062, 38.15549],
          [-109.060062, 38.275489],
          [-109.059962, 38.499987],
          [-108.379136, 38.499987],
          [-108.378698, 38.668097],
          [-107.500655, 38.668553],
          [-107.50002, 38.301926],
          [-107.63504, 38.301896],
          [-107.635697, 38.332148],
          [-108.13238, 38.331573],
          [-108.086604, 38.255202],
          [-107.937588, 38.218992],
          [-107.965789, 38.152328],
          [-109.042074, 38.153023],
          [-109.042062, 38.15549]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12017",
      state: "12",
      county: "017",
      name: "Citrus County",
      censusArea: 581.696,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.535591, 29.044855],
          [-82.426997, 29.021385],
          [-82.311697, 28.960391],
          [-82.170848, 28.79019],
          [-82.263052, 28.667634],
          [-82.417684, 28.665897],
          [-82.418353, 28.694859],
          [-82.646411, 28.694434],
          [-82.645482, 28.697553],
          [-82.652926, 28.705618],
          [-82.656028, 28.712443],
          [-82.652926, 28.719267],
          [-82.656028, 28.727952],
          [-82.662232, 28.737258],
          [-82.651065, 28.747184],
          [-82.645482, 28.767037],
          [-82.643, 28.782546],
          [-82.643, 28.789991],
          [-82.648584, 28.796195],
          [-82.650445, 28.80488],
          [-82.652926, 28.830936],
          [-82.638657, 28.843344],
          [-82.643, 28.860094],
          [-82.639898, 28.876224],
          [-82.644861, 28.889252],
          [-82.656028, 28.899179],
          [-82.673399, 28.900419],
          [-82.688864, 28.905609],
          [-82.702618, 28.932955],
          [-82.708793, 28.935979],
          [-82.723861, 28.953506],
          [-82.735754, 28.973709],
          [-82.737872, 28.995703],
          [-82.758906, 28.993277],
          [-82.689814, 29.033962],
          [-82.612653, 29.009271],
          [-82.535591, 29.044855]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12029",
      state: "12",
      county: "029",
      name: "Dixie County",
      censusArea: 705.053,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.412278, 29.666922],
          [-83.373351, 29.665951],
          [-83.318859, 29.822623],
          [-82.920608, 29.824143],
          [-82.949707, 29.754409],
          [-82.937374, 29.591312],
          [-82.984371, 29.471004],
          [-83.068522, 29.343953],
          [-83.166091, 29.28888],
          [-83.169576, 29.290355],
          [-83.176736, 29.31422],
          [-83.17826, 29.327916],
          [-83.176852, 29.329269],
          [-83.175518, 29.34469],
          [-83.189581, 29.363417],
          [-83.200702, 29.373855],
          [-83.202446, 29.394422],
          [-83.218075, 29.420492],
          [-83.240509, 29.433178],
          [-83.263965, 29.435806],
          [-83.272019, 29.432256],
          [-83.294747, 29.437923],
          [-83.307094, 29.459651],
          [-83.307828, 29.468861],
          [-83.311546, 29.475666],
          [-83.323214, 29.476789],
          [-83.33113, 29.475594],
          [-83.350067, 29.489358],
          [-83.356722, 29.499901],
          [-83.370288, 29.499901],
          [-83.379254, 29.503558],
          [-83.383973, 29.512995],
          [-83.400252, 29.517242],
          [-83.401552, 29.523291],
          [-83.39983, 29.533042],
          [-83.405256, 29.578319],
          [-83.405068, 29.59557],
          [-83.39948, 29.612956],
          [-83.404081, 29.640798],
          [-83.412278, 29.666922]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12031",
      state: "12",
      county: "031",
      name: "Duval County",
      censusArea: 762.192,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.049236, 30.273432],
          [-81.791665, 30.50561],
          [-81.72163, 30.571968],
          [-81.624513, 30.586232],
          [-81.543018, 30.523889],
          [-81.499575, 30.563793],
          [-81.442784, 30.50992],
          [-81.447087, 30.503679],
          [-81.440108, 30.497678],
          [-81.42601, 30.496739],
          [-81.410809, 30.482039],
          [-81.407008, 30.42204],
          [-81.397422, 30.400626],
          [-81.396407, 30.34004],
          [-81.391606, 30.303441],
          [-81.385505, 30.273841],
          [-81.379879, 30.252914],
          [-81.436929, 30.252335],
          [-81.433737, 30.106028],
          [-81.601212, 30.130541],
          [-81.623813, 30.133641],
          [-81.650514, 30.121541],
          [-81.680215, 30.12124],
          [-81.711348, 30.191193],
          [-82.049411, 30.186933],
          [-82.049236, 30.273432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12041",
      state: "12",
      county: "041",
      name: "Gilchrist County",
      censusArea: 349.677,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.658554, 29.830144],
          [-82.658128, 29.828068],
          [-82.656301, 29.564811],
          [-82.937374, 29.591312],
          [-82.949707, 29.754409],
          [-82.920608, 29.824143],
          [-82.892872, 29.82746],
          [-82.875616, 29.866527],
          [-82.879802, 29.886847],
          [-82.864246, 29.909836],
          [-82.800477, 29.932126],
          [-82.658554, 29.830144]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12043",
      state: "12",
      county: "043",
      name: "Glades County",
      censusArea: 806.009,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.943186, 27.210599],
          [-80.871169, 27.154951],
          [-80.885639, 26.958919],
          [-80.945354, 26.769049],
          [-81.565931, 26.769552],
          [-81.562202, 27.033836],
          [-81.267152, 27.032461],
          [-81.267677, 27.121097],
          [-81.168281, 27.121734],
          [-81.169138, 27.209585],
          [-80.943186, 27.210599]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12045",
      state: "12",
      county: "045",
      name: "Gulf County",
      censusArea: 564.012,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.11566, 30.199976],
          [-85.153773, 30.091294],
          [-85.113691, 30.023173],
          [-85.025439, 29.971399],
          [-85.012191, 29.873167],
          [-85.040078, 29.779346],
          [-85.109401, 29.776793],
          [-85.217355, 29.694953],
          [-85.22745, 29.693633],
          [-85.259719, 29.681296],
          [-85.29074, 29.684081],
          [-85.319215, 29.681494],
          [-85.343619, 29.672004],
          [-85.347711, 29.66719],
          [-85.344768, 29.654793],
          [-85.352615, 29.659787],
          [-85.369419, 29.681048],
          [-85.380303, 29.698485],
          [-85.397871, 29.740498],
          [-85.413983, 29.799865],
          [-85.417971, 29.828855],
          [-85.416548, 29.842628],
          [-85.413575, 29.85294],
          [-85.405815, 29.865817],
          [-85.392469, 29.870914],
          [-85.39874, 29.859267],
          [-85.405011, 29.830151],
          [-85.405907, 29.80193],
          [-85.395528, 29.762368],
          [-85.37796, 29.709621],
          [-85.3638, 29.693526],
          [-85.353885, 29.684765],
          [-85.344986, 29.685015],
          [-85.317661, 29.691286],
          [-85.31139, 29.697557],
          [-85.301331, 29.797117],
          [-85.302591, 29.808094],
          [-85.304877, 29.811096],
          [-85.31142, 29.814373],
          [-85.314547, 29.822279],
          [-85.314783, 29.830575],
          [-85.312911, 29.832273],
          [-85.317464, 29.838894],
          [-85.325008, 29.844966],
          [-85.332289, 29.845905],
          [-85.336654, 29.849295],
          [-85.347044, 29.871981],
          [-85.363731, 29.898915],
          [-85.38473, 29.920949],
          [-85.388677, 29.924355],
          [-85.388374, 29.926919],
          [-85.389679, 30.200973],
          [-85.386263, 30.201385],
          [-85.11566, 30.199976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12049",
      state: "12",
      county: "049",
      name: "Hardee County",
      censusArea: 637.783,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.054349, 27.646382],
          [-81.563318, 27.646647],
          [-81.563696, 27.34068],
          [-82.055753, 27.338264],
          [-82.054349, 27.646382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12051",
      state: "12",
      county: "051",
      name: "Hendry County",
      censusArea: 1152.75,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.879809, 26.259455],
          [-81.26855, 26.253045],
          [-81.271721, 26.422651],
          [-81.271768, 26.517069],
          [-81.563763, 26.513324],
          [-81.565931, 26.769552],
          [-80.945354, 26.769049],
          [-80.885639, 26.958919],
          [-80.886099, 26.72548],
          [-80.881233, 26.333806],
          [-80.879809, 26.259455]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12053",
      state: "12",
      county: "053",
      name: "Hernando County",
      censusArea: 472.539,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.054694, 28.478397],
          [-82.253293, 28.478491],
          [-82.253439, 28.434824],
          [-82.600636, 28.43391],
          [-82.678743, 28.433456],
          [-82.677839, 28.434367],
          [-82.674787, 28.441956],
          [-82.680396, 28.457194],
          [-82.67241, 28.464746],
          [-82.665055, 28.484434],
          [-82.66447, 28.488788],
          [-82.66639, 28.49733],
          [-82.670146, 28.500769],
          [-82.669416, 28.519879],
          [-82.66804, 28.528199],
          [-82.663705, 28.530193],
          [-82.656694, 28.544814],
          [-82.661729, 28.549743],
          [-82.66165, 28.554143],
          [-82.65705, 28.568028],
          [-82.654138, 28.590837],
          [-82.664055, 28.606584],
          [-82.656649, 28.623727],
          [-82.65851, 28.636756],
          [-82.654167, 28.668395],
          [-82.646411, 28.694434],
          [-82.418353, 28.694859],
          [-82.417684, 28.665897],
          [-82.263052, 28.667634],
          [-82.208184, 28.572054],
          [-82.054469, 28.521367],
          [-82.054694, 28.478397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12061",
      state: "12",
      county: "061",
      name: "Indian River County",
      censusArea: 502.872,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.868881, 27.822522],
          [-80.509075, 27.822058],
          [-80.447084, 27.860755],
          [-80.447179, 27.859731],
          [-80.383695, 27.740045],
          [-80.351717, 27.642623],
          [-80.350553, 27.628361],
          [-80.34437, 27.616226],
          [-80.330956, 27.597541],
          [-80.324699, 27.569178],
          [-80.321271, 27.557378],
          [-80.679821, 27.55847],
          [-80.777166, 27.558733],
          [-80.778047, 27.643195],
          [-80.87315, 27.642288],
          [-80.868881, 27.822522]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12063",
      state: "12",
      county: "063",
      name: "Jackson County",
      censusArea: 917.76,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.154452, 31.000835],
          [-85.152218, 31.000834],
          [-85.152085, 31.000888],
          [-85.145835, 31.000695],
          [-85.057534, 31.000585],
          [-85.054802, 31.000585],
          [-85.052088, 31.000585],
          [-85.031155, 31.000647],
          [-85.030107, 31.000653],
          [-85.027512, 31.00067],
          [-85.024108, 31.000681],
          [-85.002368, 31.000682],
          [-84.998628, 30.971386],
          [-84.983527, 30.935486],
          [-84.959626, 30.910587],
          [-84.941925, 30.887988],
          [-84.896122, 30.750591],
          [-84.864693, 30.711542],
          [-84.932519, 30.606393],
          [-85.167713, 30.608026],
          [-85.171891, 30.564336],
          [-85.383948, 30.566856],
          [-85.434782, 30.56756],
          [-85.432274, 30.785457],
          [-85.482276, 30.829509],
          [-85.599934, 30.830552],
          [-85.497992, 30.996931],
          [-85.488298, 30.997041],
          [-85.154452, 31.000835]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12069",
      state: "12",
      county: "069",
      name: "Lake County",
      censusArea: 938.381,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.657268, 28.347098],
          [-81.818903, 28.361712],
          [-81.957641, 28.34518],
          [-81.95358, 28.82094],
          [-81.95419, 28.960053],
          [-81.843025, 28.959884],
          [-81.842194, 28.961024],
          [-81.658698, 28.960345],
          [-81.639151, 29.06288],
          [-81.640517, 29.1316],
          [-81.641916, 29.276766],
          [-81.61234, 29.202943],
          [-81.531262, 29.176621],
          [-81.502055, 29.097802],
          [-81.354554, 28.984375],
          [-81.36694, 28.879227],
          [-81.367052, 28.878315],
          [-81.366764, 28.876619],
          [-81.366158, 28.874744],
          [-81.366705, 28.87325],
          [-81.368608, 28.872921],
          [-81.369692, 28.870536],
          [-81.372009, 28.869039],
          [-81.373491, 28.87025],
          [-81.376006, 28.865901],
          [-81.375088, 28.863366],
          [-81.414446, 28.784955],
          [-81.592971, 28.78597],
          [-81.624731, 28.785927],
          [-81.658114, 28.68271],
          [-81.65739, 28.553122],
          [-81.657268, 28.347098]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08075",
      state: "08",
      county: "075",
      name: "Logan County",
      censusArea: 1838.547,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.574522, 41.001721],
          [-103.497447, 41.001635],
          [-103.486697, 41.001914],
          [-103.421975, 41.002007],
          [-103.421925, 41.001969],
          [-103.382493, 41.001883],
          [-103.365314, 41.001846],
          [-103.362979, 41.001844],
          [-103.077804, 41.002298],
          [-103.076536, 41.002253],
          [-103.059538, 41.002368],
          [-103.057998, 41.002368],
          [-103.043444, 41.002344],
          [-103.038704, 41.002251],
          [-103.002026, 41.002486],
          [-103.000102, 41.0024],
          [-102.98269, 41.002157],
          [-102.981483, 41.002112],
          [-102.963669, 41.002186],
          [-102.962522, 41.002072],
          [-102.960706, 41.002059],
          [-102.959624, 41.002095],
          [-102.94483, 41.002303],
          [-102.943109, 41.002051],
          [-102.925568, 41.00228],
          [-102.924029, 41.002142],
          [-102.906547, 41.002276],
          [-102.904796, 41.002207],
          [-102.887407, 41.002178],
          [-102.885746, 41.002131],
          [-102.867822, 41.002183],
          [-102.865784, 41.001988],
          [-102.849263, 41.002301],
          [-102.846455, 41.002256],
          [-102.830303, 41.002351],
          [-102.82728, 41.002143],
          [-102.773546, 41.002414],
          [-102.766723, 41.002275],
          [-102.754617, 41.002361],
          [-102.739624, 41.00223],
          [-102.653463, 41.002332],
          [-102.65131, 40.749402],
          [-102.664804, 40.438473],
          [-102.77996, 40.438446],
          [-103.465377, 40.436346],
          [-103.4672, 40.523253],
          [-103.581795, 40.52335],
          [-103.574522, 41.001721]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08077",
      state: "08",
      county: "077",
      name: "Mesa County",
      censusArea: 3328.974,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.059962, 38.499987],
          [-109.059541, 38.719888],
          [-109.053943, 38.904414],
          [-109.053797, 38.905284],
          [-109.053233, 38.942467],
          [-109.053292, 38.942878],
          [-109.051516, 39.124982],
          [-109.051417, 39.366677],
          [-108.875659, 39.366531],
          [-108.874002, 39.366269],
          [-108.865465, 39.366251],
          [-107.430949, 39.366178],
          [-107.465957, 39.32212],
          [-107.394486, 39.256299],
          [-107.500606, 39.217916],
          [-107.76549, 39.043294],
          [-107.85824, 39.079804],
          [-108.378953, 38.829108],
          [-108.378698, 38.668097],
          [-108.379136, 38.499987],
          [-109.059962, 38.499987]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08081",
      state: "08",
      county: "081",
      name: "Moffat County",
      censusArea: 4743.29,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.050076, 41.000659],
          [-108.250649, 41.000114],
          [-107.918421, 41.002036],
          [-107.317794, 41.002957],
          [-107.314153, 40.600281],
          [-107.428813, 40.542207],
          [-107.439386, 40.223379],
          [-107.439372, 40.220454],
          [-107.644175, 40.224238],
          [-107.653987, 40.223452],
          [-109.050854, 40.222662],
          [-109.049955, 40.539901],
          [-109.050074, 40.540358],
          [-109.048249, 40.653601],
          [-109.048296, 40.662602],
          [-109.050076, 41.000659]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08087",
      state: "08",
      county: "087",
      name: "Morgan County",
      censusArea: 1280.433,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.150332, 40.000856],
          [-104.150494, 40.350245],
          [-104.147536, 40.350245],
          [-104.147901, 40.524348],
          [-103.581795, 40.52335],
          [-103.4672, 40.523253],
          [-103.465377, 40.436346],
          [-103.47199, 40.001502],
          [-103.705701, 40.001366],
          [-104.150332, 40.000856]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08091",
      state: "08",
      county: "091",
      name: "Ouray County",
      censusArea: 541.593,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.738283, 37.905432],
          [-107.795469, 37.98852],
          [-107.900193, 38.026249],
          [-107.891469, 38.115957],
          [-107.965789, 38.152328],
          [-107.937588, 38.218992],
          [-108.086604, 38.255202],
          [-108.13238, 38.331573],
          [-107.635697, 38.332148],
          [-107.63504, 38.301896],
          [-107.56862, 38.147267],
          [-107.51088, 38.060877],
          [-107.568875, 37.965015],
          [-107.738283, 37.905432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08097",
      state: "08",
      county: "097",
      name: "Pitkin County",
      censusArea: 970.697,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.394486, 39.256299],
          [-107.465957, 39.32212],
          [-107.430949, 39.366178],
          [-107.113446, 39.366066],
          [-106.426487, 39.361871],
          [-106.485121, 39.312566],
          [-106.508614, 39.166681],
          [-106.577966, 39.057908],
          [-106.58073, 39.048717],
          [-106.597233, 39.044821],
          [-106.591692, 39.024923],
          [-106.599214, 38.997994],
          [-106.694969, 39.055667],
          [-106.801874, 38.978303],
          [-106.906422, 38.992091],
          [-107.001622, 39.042273],
          [-107.06632, 39.118805],
          [-107.28249, 39.118718],
          [-107.375608, 39.197931],
          [-107.394486, 39.256299]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08103",
      state: "08",
      county: "103",
      name: "Rio Blanco County",
      censusArea: 3220.934,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.050873, 40.058915],
          [-109.050813, 40.059579],
          [-109.050944, 40.180712],
          [-109.050973, 40.180849],
          [-109.050854, 40.222662],
          [-107.653987, 40.223452],
          [-107.644175, 40.224238],
          [-107.439372, 40.220454],
          [-107.439386, 40.223379],
          [-107.037928, 40.225372],
          [-107.037363, 40.091538],
          [-107.316993, 40.090765],
          [-107.318751, 39.914968],
          [-107.431665, 39.917725],
          [-107.43177, 39.828377],
          [-107.938502, 39.824996],
          [-107.938546, 39.694464],
          [-109.051221, 39.660472],
          [-109.050873, 40.058915]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08109",
      state: "08",
      county: "109",
      name: "Saguache County",
      censusArea: 3168.525,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.24694, 38.422768],
          [-106.010751, 38.446566],
          [-105.796897, 38.265047],
          [-105.67627, 38.146173],
          [-105.604355, 38.008745],
          [-105.473204, 37.89597],
          [-105.428893, 37.851733],
          [-105.457255, 37.751464],
          [-106.038413, 37.748353],
          [-106.586374, 37.747809],
          [-106.587139, 37.834426],
          [-106.693845, 37.835244],
          [-106.693523, 37.873519],
          [-106.879167, 37.96111],
          [-107.000602, 37.956041],
          [-107.001119, 38.147072],
          [-107.000601, 38.425619],
          [-106.24694, 38.422768]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08119",
      state: "08",
      county: "119",
      name: "Teller County",
      censusArea: 557.055,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.330444, 39.129685],
          [-105.32922, 39.129689],
          [-105.033544, 39.129819],
          [-105.028903, 38.868907],
          [-105.072184, 38.799382],
          [-104.938994, 38.796884],
          [-104.942426, 38.649882],
          [-105.240362, 38.647595],
          [-105.237792, 38.696901],
          [-105.329134, 38.697205],
          [-105.330444, 39.129685]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08121",
      state: "08",
      county: "121",
      name: "Washington County",
      censusArea: 2518.031,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.705701, 40.001366],
          [-103.47199, 40.001502],
          [-103.465377, 40.436346],
          [-102.77996, 40.438446],
          [-102.803767, 40.002554],
          [-102.802932, 39.567841],
          [-103.154376, 39.565654],
          [-103.154955, 39.565949],
          [-103.707072, 39.566295],
          [-103.706547, 39.739894],
          [-103.705826, 39.999984],
          [-103.705701, 40.001366]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08125",
      state: "08",
      county: "125",
      name: "Yuma County",
      censusArea: 2364.405,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.051465, 40.440008],
          [-102.05184, 40.396396],
          [-102.051572, 40.39308],
          [-102.051669, 40.349213],
          [-102.051922, 40.235344],
          [-102.051894, 40.229193],
          [-102.051909, 40.162674],
          [-102.052001, 40.148359],
          [-102.051744, 40.003078],
          [-102.051569, 39.849805],
          [-102.051363, 39.843471],
          [-102.051318, 39.833311],
          [-102.051254, 39.818992],
          [-102.050594, 39.675594],
          [-102.049954, 39.592331],
          [-102.049806, 39.574058],
          [-102.802932, 39.567841],
          [-102.803767, 40.002554],
          [-102.77996, 40.438446],
          [-102.664804, 40.438473],
          [-102.051465, 40.440008]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09003",
      state: "09",
      county: "003",
      name: "Hartford County",
      censusArea: 735.097,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.752181, 41.578894],
          [-72.94635, 41.556797],
          [-72.98325, 41.639598],
          [-72.99855, 41.712498],
          [-73.012256, 41.775132],
          [-73.012534, 41.77541],
          [-73.012633, 41.777457],
          [-73.016534, 41.795655],
          [-73.016936, 41.798183],
          [-72.949024, 41.806431],
          [-72.905945, 41.921696],
          [-72.88706, 41.973121],
          [-73.029537, 41.966606],
          [-73.008745, 42.038854],
          [-73.008739, 42.039356],
          [-72.863733, 42.03771],
          [-72.863619, 42.037709],
          [-72.813541, 42.036494],
          [-72.816741, 41.997595],
          [-72.774757, 42.002129],
          [-72.766739, 42.002995],
          [-72.766139, 42.007695],
          [-72.758151, 42.020865],
          [-72.757467, 42.020947],
          [-72.755838, 42.036195],
          [-72.714134, 42.036608],
          [-72.695927, 42.036788],
          [-72.509187, 42.034607],
          [-72.509081, 42.033539],
          [-72.49973, 41.984295],
          [-72.498928, 41.977893],
          [-72.492729, 41.947497],
          [-72.503065, 41.946608],
          [-72.51333, 41.945401],
          [-72.51363, 41.942997],
          [-72.51733, 41.869897],
          [-72.463899, 41.745572],
          [-72.410629, 41.601798],
          [-72.417229, 41.590598],
          [-72.46673, 41.583899],
          [-72.54423, 41.642696],
          [-72.630524, 41.633282],
          [-72.627836, 41.640199],
          [-72.714538, 41.60431],
          [-72.731671, 41.595779],
          [-72.752941, 41.592497],
          [-72.752181, 41.578894]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09007",
      state: "09",
      county: "007",
      name: "Middlesex County",
      censusArea: 369.301,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.322931, 41.518119],
          [-72.305531, 41.436282],
          [-72.43371, 41.423995],
          [-72.34146, 41.28011],
          [-72.348643, 41.277446],
          [-72.348068, 41.269698],
          [-72.386629, 41.261798],
          [-72.398688, 41.278172],
          [-72.40593, 41.278398],
          [-72.451925, 41.278885],
          [-72.472539, 41.270103],
          [-72.485693, 41.270881],
          [-72.499534, 41.265866],
          [-72.506634, 41.260099],
          [-72.51866, 41.261253],
          [-72.521312, 41.2656],
          [-72.529416, 41.264421],
          [-72.533247, 41.26269],
          [-72.536746, 41.256207],
          [-72.537776, 41.255646],
          [-72.678739, 41.433797],
          [-72.682539, 41.433797],
          [-72.708343, 41.429443],
          [-72.746141, 41.423297],
          [-72.733886, 41.485368],
          [-72.74333, 41.483114],
          [-72.744742, 41.497698],
          [-72.752181, 41.578894],
          [-72.752941, 41.592497],
          [-72.731671, 41.595779],
          [-72.714538, 41.60431],
          [-72.627836, 41.640199],
          [-72.630524, 41.633282],
          [-72.54423, 41.642696],
          [-72.46673, 41.583899],
          [-72.430767, 41.524567],
          [-72.322931, 41.518119]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05015",
      state: "05",
      county: "015",
      name: "Carroll County",
      censusArea: 630.087,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.866758, 36.498789],
          [-93.728022, 36.499037],
          [-93.727552, 36.499055],
          [-93.718893, 36.499178],
          [-93.709956, 36.499179],
          [-93.700171, 36.499135],
          [-93.584282, 36.498896],
          [-93.584281, 36.498896],
          [-93.514512, 36.498881],
          [-93.507408, 36.498911],
          [-93.426989, 36.498585],
          [-93.396079, 36.498669],
          [-93.394718, 36.498519],
          [-93.315337, 36.498408],
          [-93.315324, 36.498408],
          [-93.296117, 36.498389],
          [-93.300828, 36.125038],
          [-93.300072, 36.121264],
          [-93.479528, 36.125072],
          [-93.551135, 36.126271],
          [-93.619974, 36.201208],
          [-93.657689, 36.303082],
          [-93.815605, 36.306563],
          [-93.861698, 36.339266],
          [-93.866758, 36.498789]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05017",
      state: "05",
      county: "017",
      name: "Chicot County",
      censusArea: 644.304,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.215671, 33.529423],
          [-91.125109, 33.472842],
          [-91.118495, 33.449116],
          [-91.131885, 33.430063],
          [-91.184427, 33.419576],
          [-91.141216, 33.298397],
          [-91.110561, 33.24593],
          [-91.085984, 33.221644],
          [-91.087589, 33.145177],
          [-91.125656, 33.038276],
          [-91.157662, 33.011217],
          [-91.166073, 33.004106],
          [-91.265018, 33.005084],
          [-91.284398, 33.005007],
          [-91.312016, 33.005262],
          [-91.322506, 33.005341],
          [-91.325037, 33.005364],
          [-91.326396, 33.005376],
          [-91.329767, 33.005421],
          [-91.333011, 33.005529],
          [-91.376016, 33.005794],
          [-91.425466, 33.006016],
          [-91.435782, 33.006099],
          [-91.46037, 33.006246],
          [-91.45737, 33.388974],
          [-91.453438, 33.563703],
          [-91.34954, 33.563646],
          [-91.35081, 33.519476],
          [-91.24576, 33.517148],
          [-91.244582, 33.561425],
          [-91.228287, 33.559937],
          [-91.232295, 33.552788],
          [-91.215671, 33.529423]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05025",
      state: "05",
      county: "025",
      name: "Cleveland County",
      censusArea: 597.783,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.953799, 34.064141],
          [-91.976439, 33.977297],
          [-91.975974, 33.791787],
          [-91.975844, 33.704414],
          [-92.330839, 33.707806],
          [-92.334249, 33.795065],
          [-92.400798, 33.91356],
          [-92.475602, 33.972696],
          [-92.471667, 34.057054],
          [-92.336765, 34.059633],
          [-92.233376, 34.062312],
          [-91.953799, 34.064141]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05027",
      state: "05",
      county: "027",
      name: "Columbia County",
      censusArea: 766.053,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.238607, 33.017992],
          [-93.308181, 33.018156],
          [-93.308398, 33.018179],
          [-93.340353, 33.018337],
          [-93.377134, 33.018234],
          [-93.467042, 33.018611],
          [-93.489506, 33.018443],
          [-93.49052, 33.018442],
          [-93.48176, 33.259991],
          [-93.442019, 33.258165],
          [-93.365585, 33.444459],
          [-93.116361, 33.452909],
          [-93.119014, 33.380293],
          [-92.978469, 33.37728],
          [-92.988708, 33.017298],
          [-93.070686, 33.017792],
          [-93.073167, 33.017898],
          [-93.081428, 33.017928],
          [-93.100981, 33.017786],
          [-93.101443, 33.01774],
          [-93.154351, 33.017856],
          [-93.197402, 33.017951],
          [-93.238607, 33.017992]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05033",
      state: "05",
      county: "033",
      name: "Crawford County",
      censusArea: 593.094,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.431789, 35.397659],
          [-94.463318, 35.58266],
          [-94.464097, 35.587265],
          [-94.464457, 35.588909],
          [-94.465272, 35.594037],
          [-94.472647, 35.638556],
          [-94.487585, 35.726147],
          [-94.48821, 35.72924],
          [-94.492932, 35.759166],
          [-94.124871, 35.750426],
          [-93.962892, 35.76093],
          [-93.910836, 35.759752],
          [-93.984204, 35.660783],
          [-94.073703, 35.662463],
          [-94.076209, 35.575489],
          [-94.023014, 35.574204],
          [-94.074895, 35.445403],
          [-94.223751, 35.353694],
          [-94.321186, 35.357476],
          [-94.403468, 35.449994],
          [-94.431789, 35.397659]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05045",
      state: "05",
      county: "045",
      name: "Faulkner County",
      censusArea: 647.884,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.119719, 35.066493],
          [-92.121336, 35.01221],
          [-92.227541, 35.014484],
          [-92.229893, 34.927558],
          [-92.544341, 34.953319],
          [-92.593585, 35.011761],
          [-92.554685, 35.113277],
          [-92.484259, 35.280706],
          [-92.481469, 35.368392],
          [-92.252759, 35.363718],
          [-92.111463, 35.362725],
          [-92.119719, 35.066493]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05047",
      state: "05",
      county: "047",
      name: "Franklin County",
      censusArea: 608.857,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.910836, 35.759752],
          [-93.910588, 35.774246],
          [-93.695981, 35.768649],
          [-93.710076, 35.375242],
          [-93.806849, 35.430011],
          [-93.810087, 35.313095],
          [-93.917127, 35.319046],
          [-93.920167, 35.229427],
          [-94.028547, 35.213826],
          [-94.082798, 35.215913],
          [-94.074895, 35.445403],
          [-94.023014, 35.574204],
          [-94.076209, 35.575489],
          [-94.073703, 35.662463],
          [-93.984204, 35.660783],
          [-93.910836, 35.759752]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05053",
      state: "05",
      county: "053",
      name: "Grant County",
      censusArea: 631.809,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.245818, 34.493506],
          [-92.208041, 34.492765],
          [-92.212906, 34.2304],
          [-92.233376, 34.062312],
          [-92.336765, 34.059633],
          [-92.493672, 34.144234],
          [-92.678383, 34.149622],
          [-92.668711, 34.414237],
          [-92.589418, 34.455641],
          [-92.40468, 34.451916],
          [-92.403557, 34.495795],
          [-92.245818, 34.493506]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05059",
      state: "05",
      county: "059",
      name: "Hot Spring County",
      censusArea: 615.202,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.793197, 34.503606],
          [-92.685284, 34.473105],
          [-92.668711, 34.414237],
          [-92.678383, 34.149622],
          [-92.813986, 34.15166],
          [-92.886285, 34.155877],
          [-93.042025, 34.159852],
          [-93.035039, 34.236664],
          [-93.199428, 34.248057],
          [-93.198278, 34.277337],
          [-93.35684, 34.280306],
          [-93.408124, 34.339527],
          [-93.407227, 34.396853],
          [-92.948071, 34.389255],
          [-92.915079, 34.506114],
          [-92.793197, 34.503606]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05067",
      state: "05",
      county: "067",
      name: "Jackson County",
      censusArea: 633.938,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.58188, 35.530447],
          [-91.36957, 35.526362],
          [-91.348281, 35.719127],
          [-91.232392, 35.812664],
          [-91.198556, 35.890024],
          [-91.169061, 35.889794],
          [-91.032499, 35.88507],
          [-91.036967, 35.708418],
          [-91.03923, 35.444717],
          [-91.039783, 35.354256],
          [-91.251555, 35.357561],
          [-91.252927, 35.445845],
          [-91.347257, 35.439106],
          [-91.584687, 35.442311],
          [-91.58188, 35.530447]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05073",
      state: "05",
      county: "073",
      name: "Lafayette County",
      censusArea: 528.268,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.49052, 33.018442],
          [-93.490893, 33.018442],
          [-93.520971, 33.018616],
          [-93.520994, 33.018616],
          [-93.524916, 33.018637],
          [-93.531499, 33.018643],
          [-93.80493, 33.019347],
          [-93.860409, 33.092602],
          [-93.850076, 33.145343],
          [-93.734079, 33.155551],
          [-93.750177, 33.183949],
          [-93.665518, 33.327021],
          [-93.733317, 33.42088],
          [-93.722867, 33.481963],
          [-93.483097, 33.476075],
          [-93.483974, 33.447223],
          [-93.365585, 33.444459],
          [-93.442019, 33.258165],
          [-93.48176, 33.259991],
          [-93.49052, 33.018442]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05081",
      state: "05",
      county: "081",
      name: "Little River County",
      censusArea: 532.253,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.04345, 33.552253],
          [-94.066846, 33.568909],
          [-94.085243, 33.575546],
          [-94.181805, 33.593217],
          [-94.252656, 33.586144],
          [-94.257801, 33.582508],
          [-94.355945, 33.54318],
          [-94.409329, 33.568265],
          [-94.464336, 33.598819],
          [-94.470963, 33.60594],
          [-94.478366, 33.620847],
          [-94.485875, 33.637867],
          [-94.485577, 33.65331],
          [-94.485528, 33.663388],
          [-94.48452, 33.687909],
          [-94.484616, 33.691592],
          [-94.48384, 33.711332],
          [-94.483874, 33.716733],
          [-94.48287, 33.750564],
          [-94.482862, 33.75078],
          [-94.482777, 33.753638],
          [-94.482682, 33.756286],
          [-94.481842, 33.789008],
          [-94.481543, 33.795719],
          [-94.481361, 33.802649],
          [-94.481355, 33.802887],
          [-94.480574, 33.830166],
          [-94.479954, 33.85133],
          [-94.478994, 33.881197],
          [-94.478842, 33.881485],
          [-94.477387, 33.937759],
          [-94.477318, 33.940932],
          [-94.237445, 33.802074],
          [-94.040849, 33.782046],
          [-93.958828, 33.750994],
          [-93.957315, 33.75094],
          [-93.965637, 33.669736],
          [-93.825884, 33.609853],
          [-93.951474, 33.549282],
          [-94.04345, 33.552253]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05083",
      state: "05",
      county: "083",
      name: "Logan County",
      censusArea: 708.125,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.704885, 35.019715],
          [-94.074347, 35.026585],
          [-94.140571, 35.099678],
          [-94.139307, 35.143361],
          [-94.031236, 35.140857],
          [-94.028547, 35.213826],
          [-93.920167, 35.229427],
          [-93.917127, 35.319046],
          [-93.810087, 35.313095],
          [-93.806849, 35.430011],
          [-93.710076, 35.375242],
          [-93.43041, 35.423737],
          [-93.417895, 35.326779],
          [-93.299014, 35.327425],
          [-93.294201, 35.325319],
          [-93.282758, 35.318273],
          [-93.278293, 35.317402],
          [-93.279959, 35.212552],
          [-93.457022, 35.215782],
          [-93.529838, 35.145441],
          [-93.706692, 35.134943],
          [-93.704885, 35.019715]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05087",
      state: "05",
      county: "087",
      name: "Madison County",
      censusArea: 834.255,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.962892, 35.76093],
          [-93.944177, 36.091103],
          [-93.887775, 36.235046],
          [-93.815605, 36.306563],
          [-93.657689, 36.303082],
          [-93.619974, 36.201208],
          [-93.551135, 36.126271],
          [-93.479528, 36.125072],
          [-93.450715, 35.967493],
          [-93.523639, 35.881592],
          [-93.519204, 35.763521],
          [-93.695981, 35.768649],
          [-93.910588, 35.774246],
          [-93.910836, 35.759752],
          [-93.962892, 35.76093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16001",
      state: "16",
      county: "001",
      name: "Ada County",
      censusArea: 1052.576,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.974946, 43.365081],
          [-115.978695, 43.113421],
          [-116.266168, 43.112553],
          [-116.385964, 43.193445],
          [-116.433111, 43.296436],
          [-116.511722, 43.290253],
          [-116.513364, 43.45932],
          [-116.473713, 43.459216],
          [-116.473492, 43.634103],
          [-116.513123, 43.634183],
          [-116.512559, 43.807216],
          [-116.281687, 43.806937],
          [-116.122476, 43.691256],
          [-115.975767, 43.591338],
          [-115.974946, 43.365081]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16003",
      state: "16",
      county: "003",
      name: "Adams County",
      censusArea: 1363.056,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.891463, 44.840531],
          [-116.852427, 44.887577],
          [-116.83199, 44.933007],
          [-116.835702, 44.940633],
          [-116.844653, 44.949338],
          [-116.850737, 44.958113],
          [-116.858277, 44.978008],
          [-116.845369, 45.017059],
          [-116.783902, 45.079022],
          [-116.729607, 45.142091],
          [-116.709536, 45.203015],
          [-116.696047, 45.254679],
          [-116.691197, 45.269245],
          [-116.343081, 45.268043],
          [-116.28263, 45.134682],
          [-116.144531, 45.107619],
          [-116.089397, 45.063748],
          [-116.186161, 44.887793],
          [-116.205988, 44.767155],
          [-116.157064, 44.703291],
          [-116.157096, 44.498966],
          [-116.299757, 44.444107],
          [-116.407496, 44.443849],
          [-116.527157, 44.490695],
          [-116.528382, 44.614886],
          [-116.624297, 44.662852],
          [-116.624783, 44.837841],
          [-116.891463, 44.840531]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16009",
      state: "16",
      county: "009",
      name: "Benewah County",
      censusArea: 776.617,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.040019, 47.259272],
          [-117.039843, 47.347201],
          [-117.039857, 47.366093],
          [-116.629147, 47.371701],
          [-116.586576, 47.414662],
          [-116.329496, 47.413781],
          [-116.329586, 47.022442],
          [-116.835438, 47.032576],
          [-117.039821, 47.127265],
          [-117.039836, 47.154734],
          [-117.039871, 47.181858],
          [-117.039888, 47.203282],
          [-117.039899, 47.225515],
          [-117.040019, 47.259272]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16017",
      state: "16",
      county: "017",
      name: "Bonner County",
      censusArea: 1734.573,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.041874, 47.977387],
          [-117.041676, 48.04556],
          [-117.041401, 48.0855],
          [-117.039552, 48.17396],
          [-117.039413, 48.17725],
          [-117.039618, 48.178142],
          [-117.039583, 48.180313],
          [-117.039582, 48.180853],
          [-117.039582, 48.181124],
          [-117.039615, 48.184015],
          [-117.039599, 48.184387],
          [-117.035178, 48.370878],
          [-117.035178, 48.371221],
          [-117.035289, 48.422732],
          [-117.035254, 48.423144],
          [-117.035285, 48.429816],
          [-117.035285, 48.430113],
          [-117.034499, 48.620769],
          [-117.034358, 48.628523],
          [-117.033177, 48.846563],
          [-116.785141, 48.847428],
          [-116.784843, 48.500617],
          [-116.049226, 48.502058],
          [-116.049353, 48.21546],
          [-116.049415, 48.07722],
          [-116.049398, 48.075395],
          [-116.049368, 48.072134],
          [-116.04932, 48.066644],
          [-116.048424, 47.977126],
          [-116.229606, 48.067682],
          [-116.329066, 48.017791],
          [-116.322825, 47.890342],
          [-116.505028, 47.890188],
          [-116.504214, 47.991479],
          [-116.633095, 47.977147],
          [-117.041874, 47.977387]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16019",
      state: "16",
      county: "019",
      name: "Bonneville County",
      censusArea: 1866.077,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.045205, 43.501136],
          [-111.044617, 43.31572],
          [-111.044229, 43.195579],
          [-111.044168, 43.189244],
          [-111.044232, 43.18444],
          [-111.044266, 43.177236],
          [-111.044235, 43.177121],
          [-111.044143, 43.072364],
          [-111.044162, 43.068222],
          [-111.04415, 43.066172],
          [-111.044117, 43.060309],
          [-111.044086, 43.054819],
          [-111.044063, 43.046302],
          [-111.044058, 43.04464],
          [-111.043997, 43.041415],
          [-111.044094, 43.02927],
          [-111.044033, 43.026411],
          [-111.044034, 43.024844],
          [-111.044034, 43.024581],
          [-111.044206, 43.022614],
          [-111.044156, 43.020052],
          [-111.589615, 43.020052],
          [-111.587795, 43.28163],
          [-111.822742, 43.2822],
          [-111.822589, 43.366135],
          [-112.043821, 43.366708],
          [-112.103298, 43.425057],
          [-112.520024, 43.425099],
          [-112.519945, 43.62656],
          [-111.626438, 43.626748],
          [-111.399915, 43.622015],
          [-111.243885, 43.621694],
          [-111.199111, 43.539686],
          [-111.045205, 43.501136]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05089",
      state: "05",
      county: "089",
      name: "Marion County",
      censusArea: 597.009,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.854049, 36.497983],
          [-92.838876, 36.498033],
          [-92.838621, 36.498079],
          [-92.772341, 36.497772],
          [-92.772333, 36.497772],
          [-92.529145, 36.497739],
          [-92.533175, 36.386563],
          [-92.593128, 36.358337],
          [-92.543034, 36.269342],
          [-92.469262, 36.250904],
          [-92.411852, 36.162123],
          [-92.411502, 36.062756],
          [-92.837776, 36.070124],
          [-92.890695, 36.114132],
          [-92.884954, 36.490891],
          [-92.854049, 36.497983]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05091",
      state: "05",
      county: "091",
      name: "Miller County",
      censusArea: 625.578,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.043036, 33.079485],
          [-94.04287, 33.092727],
          [-94.043007, 33.13389],
          [-94.043077, 33.138162],
          [-94.043185, 33.143476],
          [-94.042875, 33.199785],
          [-94.042892, 33.202666],
          [-94.042876, 33.215219],
          [-94.04273, 33.241823],
          [-94.043004, 33.250128],
          [-94.04305, 33.260904],
          [-94.04299, 33.271227],
          [-94.04299, 33.271243],
          [-94.043067, 33.330498],
          [-94.043067, 33.347351],
          [-94.043067, 33.352097],
          [-94.043128, 33.358757],
          [-94.042869, 33.37117],
          [-94.043053, 33.377716],
          [-94.042887, 33.420225],
          [-94.042988, 33.431024],
          [-94.042988, 33.435824],
          [-94.043188, 33.470324],
          [-94.043279, 33.49103],
          [-94.043009, 33.493039],
          [-94.043375, 33.542315],
          [-94.043428, 33.551425],
          [-94.04345, 33.552253],
          [-93.951474, 33.549282],
          [-93.825884, 33.609853],
          [-93.822178, 33.613548],
          [-93.816087, 33.610843],
          [-93.812503, 33.603351],
          [-93.817579, 33.586153],
          [-93.823259, 33.580544],
          [-93.827726, 33.57162],
          [-93.814202, 33.56475],
          [-93.798272, 33.552493],
          [-93.803789, 33.549384],
          [-93.807741, 33.545243],
          [-93.79833, 33.542149],
          [-93.792725, 33.547109],
          [-93.792725, 33.541963],
          [-93.792415, 33.535869],
          [-93.778832, 33.524717],
          [-93.780289, 33.519624],
          [-93.773641, 33.504671],
          [-93.772686, 33.498513],
          [-93.722867, 33.481963],
          [-93.733317, 33.42088],
          [-93.665518, 33.327021],
          [-93.750177, 33.183949],
          [-93.734079, 33.155551],
          [-93.850076, 33.145343],
          [-93.860409, 33.092602],
          [-93.80493, 33.019347],
          [-93.814553, 33.019372],
          [-94.024475, 33.019207],
          [-94.027983, 33.019139],
          [-94.035839, 33.019145],
          [-94.041444, 33.019188],
          [-94.042964, 33.019219],
          [-94.043036, 33.079485]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05093",
      state: "05",
      county: "093",
      name: "Mississippi County",
      censusArea: 900.574,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.933572, 35.533299],
          [-89.957347, 35.528683],
          [-90.017312, 35.555996],
          [-90.02862, 35.555249],
          [-90.032938, 35.55344],
          [-90.039744, 35.548041],
          [-90.034976, 35.480705],
          [-90.034014, 35.468821],
          [-90.04768, 35.459255],
          [-90.059068, 35.457889],
          [-90.067798, 35.466224],
          [-90.072154, 35.470752],
          [-90.07442, 35.472518],
          [-90.107723, 35.476935],
          [-90.152386, 35.436789],
          [-90.286899, 35.438573],
          [-90.287039, 35.439933],
          [-90.286736, 35.699914],
          [-90.288947, 35.996418],
          [-90.2888, 35.996419],
          [-90.158812, 35.997375],
          [-90.127331, 35.997635],
          [-90.12635, 35.997596],
          [-89.972563, 35.998994],
          [-89.965327, 35.998813],
          [-89.961075, 35.999135],
          [-89.959893, 35.99902],
          [-89.959377, 35.99902],
          [-89.959375, 35.99902],
          [-89.901183, 35.999365],
          [-89.896508, 35.999432],
          [-89.875586, 35.999562],
          [-89.875085, 35.999578],
          [-89.87459, 35.999575],
          [-89.86901, 35.99964],
          [-89.770255, 36.000524],
          [-89.769973, 36.000536],
          [-89.737648, 36.000567],
          [-89.737564, 36.000522],
          [-89.733095, 36.000608],
          [-89.71997, 35.97462],
          [-89.686924, 35.947716],
          [-89.687939, 35.905384],
          [-89.688141, 35.896946],
          [-89.714934, 35.906247],
          [-89.73361, 35.904699],
          [-89.756036, 35.896817],
          [-89.765689, 35.891299],
          [-89.771726, 35.879724],
          [-89.773564, 35.871697],
          [-89.772467, 35.865098],
          [-89.769413, 35.861558],
          [-89.749424, 35.852955],
          [-89.729517, 35.847632],
          [-89.709261, 35.838911],
          [-89.702883, 35.834153],
          [-89.701045, 35.828227],
          [-89.703875, 35.820281],
          [-89.821216, 35.756716],
          [-89.865631, 35.746577],
          [-89.889023, 35.750558],
          [-89.905538, 35.759063],
          [-89.909996, 35.759396],
          [-89.950278, 35.738493],
          [-89.956254, 35.733386],
          [-89.958882, 35.723834],
          [-89.955753, 35.690621],
          [-89.937383, 35.665711],
          [-89.922749, 35.655293],
          [-89.906147, 35.651145],
          [-89.89051, 35.652408],
          [-89.884932, 35.655107],
          [-89.909797, 35.537914],
          [-89.933572, 35.533299]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05099",
      state: "05",
      county: "099",
      name: "Nevada County",
      censusArea: 617.837,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.365585, 33.444459],
          [-93.483974, 33.447223],
          [-93.483097, 33.476075],
          [-93.456709, 33.956837],
          [-93.373406, 33.95707],
          [-93.290571, 33.845133],
          [-93.10429, 33.777011],
          [-93.116361, 33.452909],
          [-93.365585, 33.444459]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05103",
      state: "05",
      county: "103",
      name: "Ouachita County",
      censusArea: 732.783,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.567156, 33.3672],
          [-92.660238, 33.355613],
          [-92.978469, 33.37728],
          [-93.119014, 33.380293],
          [-93.116361, 33.452909],
          [-93.10429, 33.777011],
          [-92.893534, 33.809943],
          [-92.583054, 33.802198],
          [-92.58503, 33.736514],
          [-92.647931, 33.663785],
          [-92.735657, 33.624295],
          [-92.717454, 33.552444],
          [-92.776729, 33.537648],
          [-92.705093, 33.431215],
          [-92.653211, 33.433968],
          [-92.567156, 33.3672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05113",
      state: "05",
      county: "113",
      name: "Polk County",
      censusArea: 857.679,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.465847, 34.352073],
          [-94.465425, 34.359548],
          [-94.464176, 34.402713],
          [-94.463816, 34.414465],
          [-94.463671, 34.419585],
          [-94.461149, 34.507457],
          [-94.460058, 34.545264],
          [-94.460052, 34.547869],
          [-94.4575, 34.634945],
          [-94.45753, 34.642961],
          [-94.454576, 34.728962],
          [-94.317659, 34.697408],
          [-94.018168, 34.692469],
          [-93.930509, 34.665608],
          [-93.935371, 34.350097],
          [-94.254668, 34.355481],
          [-94.243739, 34.192501],
          [-94.470292, 34.189864],
          [-94.465847, 34.352073]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05115",
      state: "05",
      county: "115",
      name: "Pope County",
      censusArea: 812.548,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.896109, 35.170678],
          [-93.084678, 35.12739],
          [-93.156078, 35.235307],
          [-93.278293, 35.317402],
          [-93.282758, 35.318273],
          [-93.294201, 35.325319],
          [-93.299014, 35.327425],
          [-93.29591, 35.469915],
          [-93.223633, 35.468593],
          [-93.22117, 35.598938],
          [-93.1678, 35.597695],
          [-93.164452, 35.728831],
          [-92.951315, 35.724663],
          [-92.809763, 35.722401],
          [-92.813417, 35.549],
          [-92.850624, 35.46295],
          [-92.855, 35.172777],
          [-92.896109, 35.170678]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05121",
      state: "05",
      county: "121",
      name: "Randolph County",
      censusArea: 652.191,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.407137, 36.497112],
          [-91.405141, 36.497165],
          [-91.404915, 36.49712],
          [-91.227398, 36.497617],
          [-91.218645, 36.497564],
          [-91.21736, 36.497511],
          [-91.126529, 36.497712],
          [-91.096277, 36.497893],
          [-91.09588, 36.49787],
          [-91.017974, 36.498062],
          [-91.008558, 36.49827],
          [-90.963063, 36.498418],
          [-90.960648, 36.498426],
          [-90.87922, 36.498378],
          [-90.876867, 36.498423],
          [-90.876567, 36.498313],
          [-90.873775, 36.498074],
          [-90.850434, 36.498548],
          [-90.784398, 36.498524],
          [-90.748637, 36.412764],
          [-90.751803, 36.27996],
          [-90.806615, 36.266865],
          [-90.809816, 36.149567],
          [-91.082681, 36.150871],
          [-91.25892, 36.25788],
          [-91.308733, 36.420363],
          [-91.407137, 36.497112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05133",
      state: "05",
      county: "133",
      name: "Sevier County",
      censusArea: 565.126,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.477318, 33.940932],
          [-94.477038, 33.953838],
          [-94.476957, 33.957365],
          [-94.474895, 34.019655],
          [-94.474896, 34.021838],
          [-94.474896, 34.021877],
          [-94.470292, 34.189864],
          [-94.243739, 34.192501],
          [-94.090096, 34.187141],
          [-94.070085, 33.926994],
          [-93.964699, 33.796211],
          [-93.958828, 33.750994],
          [-94.040849, 33.782046],
          [-94.237445, 33.802074],
          [-94.477318, 33.940932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05137",
      state: "05",
      county: "137",
      name: "Stone County",
      censusArea: 606.405,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.19543, 36.132798],
          [-92.063576, 36.024372],
          [-92.108622, 35.931328],
          [-91.95302, 35.931644],
          [-91.849448, 35.867411],
          [-91.870983, 35.810562],
          [-91.837179, 35.704932],
          [-92.24052, 35.71219],
          [-92.418408, 35.716046],
          [-92.416719, 35.78859],
          [-92.414324, 35.975865],
          [-92.30722, 35.974022],
          [-92.304951, 36.06108],
          [-92.197649, 36.059287],
          [-92.19543, 36.132798]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13317",
      state: "13",
      county: "317",
      name: "Wilkes County",
      censusArea: 469.49,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.645451, 33.984195],
          [-82.593527, 33.827322],
          [-82.479518, 33.639535],
          [-82.649705, 33.608768],
          [-82.652439, 33.611202],
          [-82.655937, 33.60661],
          [-82.652208, 33.596718],
          [-82.656606, 33.596812],
          [-82.65912, 33.603048],
          [-82.670284, 33.604138],
          [-82.679975, 33.599792],
          [-82.812958, 33.655836],
          [-82.890599, 33.632877],
          [-82.949046, 33.733333],
          [-82.987286, 33.78071],
          [-82.874587, 33.921767],
          [-82.779506, 33.971124],
          [-82.645451, 33.984195]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13321",
      state: "13",
      county: "321",
      name: "Worth County",
      censusArea: 570.702,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.939437, 31.847929],
          [-83.802223, 31.803495],
          [-83.78688, 31.62281],
          [-83.649384, 31.567977],
          [-83.65409, 31.330661],
          [-83.99943, 31.334966],
          [-83.997796, 31.443753],
          [-83.997679, 31.462773],
          [-83.982569, 31.46348],
          [-84.018404, 31.650274],
          [-84.032344, 31.716772],
          [-83.939437, 31.847929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04005",
      state: "04",
      county: "005",
      name: "Coconino County",
      censusArea: 18618.885,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.538593, 37.000674],
          [-112.534545, 37.000684],
          [-112.368946, 37.001125],
          [-112.35769, 37.001025],
          [-111.412784, 37.001478],
          [-111.405869, 37.001481],
          [-111.405517, 37.001497],
          [-111.189888, 37.000959],
          [-110.75069, 37.003197],
          [-110.750698, 36.409949],
          [-110.750692, 35.814879],
          [-110.750693, 35.500008],
          [-110.750678, 34.263314],
          [-110.788078, 34.30906],
          [-110.848059, 34.267205],
          [-111.096661, 34.396431],
          [-111.147094, 34.375185],
          [-111.250878, 34.453301],
          [-111.397229, 34.426062],
          [-111.427266, 34.386149],
          [-111.423066, 34.437225],
          [-111.550328, 34.419057],
          [-111.472046, 34.462265],
          [-111.502415, 34.498057],
          [-111.555124, 34.472196],
          [-111.552106, 34.80252],
          [-111.77791, 34.803318],
          [-111.777815, 34.981315],
          [-111.809215, 34.976215],
          [-112.334536, 34.973024],
          [-112.335716, 35.148279],
          [-112.4423, 35.149408],
          [-112.440751, 35.260468],
          [-112.4727, 35.238312],
          [-112.577399, 35.243394],
          [-112.694846, 35.311586],
          [-112.754838, 35.306301],
          [-112.95341, 35.35611],
          [-112.975855, 35.392904],
          [-113.148055, 35.478595],
          [-113.174458, 35.506445],
          [-113.301303, 35.531118],
          [-113.320367, 35.53057],
          [-113.329569, 35.528108],
          [-113.334161, 35.528037],
          [-113.333922, 35.800736],
          [-113.310523, 35.86435],
          [-113.317122, 35.961385],
          [-113.354176, 36.040368],
          [-113.319971, 36.096769],
          [-113.231146, 36.089524],
          [-113.19923, 36.151389],
          [-113.13708, 36.166424],
          [-113.009038, 36.23904],
          [-112.792436, 36.285036],
          [-112.723991, 36.345992],
          [-112.681571, 36.341662],
          [-112.629727, 36.392322],
          [-112.645121, 36.457311],
          [-112.621855, 36.485017],
          [-112.660326, 36.539631],
          [-112.538593, 37.000674]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04009",
      state: "04",
      county: "009",
      name: "Graham County",
      censusArea: 4622.597,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.449021, 33.194526],
          [-110.401536, 33.196612],
          [-110.457194, 33.295268],
          [-110.441544, 33.38167],
          [-110.340949, 33.427066],
          [-110.000646, 33.465916],
          [-110.000649, 33.57693],
          [-109.891759, 33.567829],
          [-109.799782, 33.488314],
          [-109.49544, 33.652697],
          [-109.49574, 33.078409],
          [-109.213829, 32.680661],
          [-109.22607, 32.529096],
          [-109.113987, 32.426177],
          [-109.227815, 32.424475],
          [-110.451503, 32.427354],
          [-110.451228, 32.504408],
          [-110.451221, 32.513807],
          [-110.449164, 32.513792],
          [-110.449021, 33.194526]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04011",
      state: "04",
      county: "011",
      name: "Greenlee County",
      censusArea: 1843.126,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.047117, 32.777569],
          [-109.047638, 32.693439],
          [-109.047645, 32.689988],
          [-109.047653, 32.686327],
          [-109.047653, 32.681379],
          [-109.047926, 32.426376],
          [-109.113987, 32.426177],
          [-109.22607, 32.529096],
          [-109.213829, 32.680661],
          [-109.49574, 33.078409],
          [-109.49544, 33.652697],
          [-109.348062, 33.77752],
          [-109.046627, 33.778233],
          [-109.04687, 33.372654],
          [-109.047045, 33.36928],
          [-109.046909, 33.36557],
          [-109.046827, 33.365272],
          [-109.047237, 33.208965],
          [-109.047116, 33.137995],
          [-109.047117, 33.137559],
          [-109.047013, 33.092917],
          [-109.046905, 33.091931],
          [-109.047453, 33.069427],
          [-109.04748, 33.06842],
          [-109.047117, 32.77757],
          [-109.047117, 32.777569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04012",
      state: "04",
      county: "012",
      name: "La Paz County",
      censusArea: 4499.629,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.627125, 33.433554],
          [-114.622918, 33.456561],
          [-114.594534, 33.495059],
          [-114.558898, 33.531819],
          [-114.535664, 33.568788],
          [-114.535965, 33.569154],
          [-114.5403, 33.580615],
          [-114.540617, 33.591412],
          [-114.530348, 33.679245],
          [-114.523959, 33.685879],
          [-114.519113, 33.688473],
          [-114.496489, 33.696901],
          [-114.494197, 33.707922],
          [-114.50434, 33.756381],
          [-114.523365, 33.80612],
          [-114.525539, 33.838614],
          [-114.518434, 33.917518],
          [-114.499883, 33.961789],
          [-114.46361, 33.993431],
          [-114.438266, 34.022609],
          [-114.434949, 34.037784],
          [-114.435429, 34.079727],
          [-114.43338, 34.088413],
          [-114.420499, 34.103466],
          [-114.411681, 34.110031],
          [-114.401352, 34.111652],
          [-114.390565, 34.110084],
          [-114.366521, 34.118575],
          [-114.321618, 34.138093],
          [-114.312592, 34.144453],
          [-114.244191, 34.179625],
          [-114.225861, 34.201774],
          [-114.164476, 34.251667],
          [-114.133264, 34.258462],
          [-114.131489, 34.260387],
          [-114.138282, 34.30323],
          [-114.031132, 34.259819],
          [-113.603079, 34.231049],
          [-113.539524, 34.306377],
          [-113.370507, 34.291968],
          [-113.333508, 34.317877],
          [-113.333316, 34.00103],
          [-113.333753, 33.999227],
          [-113.333823, 33.911661],
          [-113.333313, 33.56571],
          [-113.33392, 33.377425],
          [-113.958046, 33.377578],
          [-113.958042, 33.464655],
          [-114.267815, 33.463574],
          [-114.268748, 33.029815],
          [-114.516912, 33.026871],
          [-114.523578, 33.030961],
          [-114.571653, 33.036624],
          [-114.578287, 33.035375],
          [-114.584765, 33.028231],
          [-114.589778, 33.026228],
          [-114.606282, 33.025703],
          [-114.62387, 33.02872],
          [-114.675104, 33.047532],
          [-114.707819, 33.091102],
          [-114.706175, 33.105335],
          [-114.696829, 33.131209],
          [-114.687074, 33.142196],
          [-114.679359, 33.159519],
          [-114.67536, 33.185489],
          [-114.672088, 33.258499],
          [-114.700103, 33.341045],
          [-114.721233, 33.396912],
          [-114.665278, 33.415358],
          [-114.627125, 33.433554]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04017",
      state: "04",
      county: "017",
      name: "Navajo County",
      censusArea: 9950.422,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.000674, 35.662657],
          [-109.826936, 35.662484],
          [-109.843764, 35.517875],
          [-109.844624, 34.822562],
          [-109.865231, 34.559689],
          [-109.848246, 34.473435],
          [-109.849387, 34.125945],
          [-109.859274, 33.777683],
          [-109.891766, 33.777777],
          [-109.891759, 33.567829],
          [-110.000649, 33.57693],
          [-110.00066, 34.000046],
          [-110.750672, 34.000048],
          [-110.750678, 34.263314],
          [-110.750693, 35.500008],
          [-110.750692, 35.814879],
          [-110.750698, 36.409949],
          [-110.75069, 37.003197],
          [-110.495259, 37.003875],
          [-110.33105, 36.998216],
          [-110.000677, 36.997968],
          [-109.999188, 36.695911],
          [-110.000673, 35.844269],
          [-110.000674, 35.662657]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04021",
      state: "04",
      county: "021",
      name: "Pinal County",
      censusArea: 5365.612,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.27061, 32.501366],
          [-111.792534, 32.506779],
          [-112.203719, 32.50676],
          [-112.203288, 33.310533],
          [-112.191784, 33.265039],
          [-112.083488, 33.20456],
          [-111.893085, 33.204811],
          [-111.686035, 33.204637],
          [-111.583047, 33.20545],
          [-111.580626, 33.465902],
          [-111.26976, 33.465538],
          [-111.250681, 33.464902],
          [-111.039905, 33.466104],
          [-110.974706, 33.342095],
          [-110.946684, 33.250053],
          [-110.780811, 32.983722],
          [-110.678006, 33.111182],
          [-110.525952, 33.159198],
          [-110.449021, 33.194526],
          [-110.449164, 32.513792],
          [-110.451221, 32.513807],
          [-110.785444, 32.513539],
          [-110.844923, 32.513767],
          [-110.844943, 32.511211],
          [-110.925567, 32.511391],
          [-111.27061, 32.501366]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04023",
      state: "04",
      county: "023",
      name: "Santa Cruz County",
      censusArea: 1236.916,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.163995, 31.618687],
          [-111.164183, 31.725983],
          [-111.158299, 31.726007],
          [-110.451736, 31.731267],
          [-110.460172, 31.333051],
          [-110.760997, 31.332765],
          [-110.976828, 31.33256],
          [-111.098097, 31.339836],
          [-111.366635, 31.42588],
          [-111.366613, 31.521291],
          [-111.163725, 31.521826],
          [-111.163995, 31.618687]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04027",
      state: "04",
      county: "027",
      name: "Yuma County",
      censusArea: 5513.992,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.719633, 32.718763],
          [-114.678632, 32.736614],
          [-114.677091, 32.736218],
          [-114.65884, 32.73383],
          [-114.65826, 32.733799],
          [-114.615585, 32.728446],
          [-114.615733, 32.729427],
          [-114.496827, 32.822119],
          [-114.496284, 32.822326],
          [-114.465715, 32.879191],
          [-114.465715, 32.87942],
          [-114.46289, 32.905797],
          [-114.468605, 32.971649],
          [-114.511343, 33.023455],
          [-114.516912, 33.026871],
          [-114.268748, 33.029815],
          [-114.267815, 33.463574],
          [-113.958042, 33.464655],
          [-113.958046, 33.377578],
          [-113.33392, 33.377425],
          [-113.333212, 33.22721],
          [-113.333897, 32.504938],
          [-113.333794, 32.038521],
          [-113.493196, 32.088943],
          [-114.250775, 32.32391],
          [-114.813613, 32.494277],
          [-114.809393, 32.617119],
          [-114.80739, 32.621332],
          [-114.799302, 32.625115],
          [-114.781872, 32.62505],
          [-114.753111, 32.658304],
          [-114.748, 32.664184],
          [-114.719633, 32.718763]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05003",
      state: "05",
      county: "003",
      name: "Ashley County",
      censusArea: 925.349,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.46037, 33.006246],
          [-91.559494, 33.00684],
          [-91.572326, 33.006908],
          [-91.579639, 33.006472],
          [-91.579802, 33.006518],
          [-91.609001, 33.006556],
          [-91.617615, 33.006717],
          [-91.62667, 33.006639],
          [-91.950001, 33.00752],
          [-91.951958, 33.007428],
          [-92.069105, 33.008163],
          [-92.109611, 33.034054],
          [-92.137527, 33.162956],
          [-92.128464, 33.208775],
          [-91.977284, 33.302026],
          [-91.990167, 33.398214],
          [-91.45737, 33.388974],
          [-91.46037, 33.006246]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05013",
      state: "05",
      county: "013",
      name: "Calhoun County",
      censusArea: 628.581,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.350432, 33.298941],
          [-92.445399, 33.274859],
          [-92.567156, 33.3672],
          [-92.653211, 33.433968],
          [-92.705093, 33.431215],
          [-92.776729, 33.537648],
          [-92.717454, 33.552444],
          [-92.735657, 33.624295],
          [-92.647931, 33.663785],
          [-92.58503, 33.736514],
          [-92.583054, 33.802198],
          [-92.334249, 33.795065],
          [-92.330839, 33.707806],
          [-92.311079, 33.499782],
          [-92.374467, 33.37865],
          [-92.350432, 33.298941]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16071",
      state: "16",
      county: "071",
      name: "Oneida County",
      censusArea: 1200.061,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.109528, 41.997105],
          [-112.163956, 41.996708],
          [-112.239107, 42.001217],
          [-112.264936, 42.000991],
          [-112.38617, 42.001126],
          [-112.450567, 42.001092],
          [-112.450814, 42.000953],
          [-112.648019, 42.000307],
          [-112.709375, 42.000309],
          [-112.788542, 41.999681],
          [-112.833084, 41.999305],
          [-112.833125, 41.999345],
          [-112.880619, 41.998921],
          [-112.882367, 41.998922],
          [-112.909587, 41.998791],
          [-112.979218, 41.998263],
          [-113.00082, 41.998223],
          [-113.000823, 42.327483],
          [-112.65399, 42.328869],
          [-112.653989, 42.415978],
          [-112.49779, 42.41598],
          [-112.498169, 42.503046],
          [-112.419995, 42.502857],
          [-112.297202, 42.454097],
          [-112.327029, 42.323854],
          [-112.190692, 42.3512],
          [-112.126286, 42.28533],
          [-112.071014, 42.238475],
          [-112.10921, 42.169781],
          [-112.109528, 41.997105]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16077",
      state: "16",
      county: "077",
      name: "Power County",
      censusArea: 1404.244,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.236952, 42.625281],
          [-113.179038, 42.675543],
          [-113.178978, 42.762283],
          [-113.236927, 42.762359],
          [-113.24351, 43.111109],
          [-113.007657, 43.111027],
          [-113.00776, 42.863051],
          [-112.816427, 42.863253],
          [-112.750503, 42.951443],
          [-112.737583, 42.905549],
          [-112.558535, 42.961101],
          [-112.515974, 42.910003],
          [-112.496937, 42.733053],
          [-112.37961, 42.646225],
          [-112.419995, 42.502857],
          [-112.498169, 42.503046],
          [-112.49779, 42.41598],
          [-112.653989, 42.415978],
          [-112.65399, 42.328869],
          [-113.000823, 42.327483],
          [-113.002807, 42.588022],
          [-113.175702, 42.58935],
          [-113.236952, 42.625281]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16079",
      state: "16",
      county: "079",
      name: "Shoshone County",
      censusArea: 2629.665,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.048424, 47.977126],
          [-116.048421, 47.97682],
          [-116.007254, 47.944968],
          [-115.919291, 47.857406],
          [-115.729123, 47.703102],
          [-115.72377, 47.696671],
          [-115.706284, 47.637864],
          [-115.718072, 47.592675],
          [-115.721207, 47.576323],
          [-115.735755, 47.555346],
          [-115.739684, 47.537663],
          [-115.729861, 47.518016],
          [-115.712178, 47.488546],
          [-115.725931, 47.466934],
          [-115.729861, 47.447287],
          [-115.721084, 47.42235],
          [-115.69057, 47.415059],
          [-115.661341, 47.402663],
          [-115.576836, 47.366825],
          [-115.561439, 47.351887],
          [-115.551309, 47.333856],
          [-115.52369, 47.298919],
          [-115.479255, 47.282089],
          [-115.421645, 47.271736],
          [-115.339201, 47.261623],
          [-115.320184, 47.255717],
          [-115.294785, 47.220914],
          [-115.29211, 47.209861],
          [-115.266723, 47.181101],
          [-115.200547, 47.139154],
          [-115.193221, 47.133026],
          [-115.099178, 47.048129],
          [-115.072985, 47.01376],
          [-114.975789, 46.932865],
          [-116.329587, 46.934502],
          [-116.329586, 47.022442],
          [-116.329496, 47.413781],
          [-116.322825, 47.890342],
          [-116.329066, 48.017791],
          [-116.229606, 48.067682],
          [-116.048424, 47.977126]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16083",
      state: "16",
      county: "083",
      name: "Twin Falls County",
      censusArea: 1921.213,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.498243, 41.994636],
          [-114.498259, 41.994599],
          [-114.598267, 41.994511],
          [-114.763825, 41.999909],
          [-114.914187, 41.999909],
          [-115.031783, 41.996008],
          [-115.038256, 41.996025],
          [-115.037694, 42.749936],
          [-115.037716, 42.76848],
          [-115.037262, 42.911931],
          [-114.901359, 42.858678],
          [-114.928714, 42.764961],
          [-114.850557, 42.749904],
          [-114.821582, 42.671582],
          [-114.616535, 42.649367],
          [-114.375289, 42.598227],
          [-114.130292, 42.494975],
          [-114.045718, 42.528766],
          [-114.000691, 42.526434],
          [-114.06708, 42.51452],
          [-114.069053, 42.417124],
          [-114.285363, 42.417332],
          [-114.281854, 41.994264],
          [-114.498243, 41.994636]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17003",
      state: "17",
      county: "003",
      name: "Alexander County",
      censusArea: 235.509,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.132685, 36.9822],
          [-89.170008, 36.970298],
          [-89.185491, 36.973518],
          [-89.207013, 36.982437],
          [-89.226247, 36.985287],
          [-89.245481, 36.98315],
          [-89.261154, 36.982437],
          [-89.271127, 36.985287],
          [-89.278628, 36.98867],
          [-89.29213, 36.992189],
          [-89.322733, 37.009033],
          [-89.378277, 37.039605],
          [-89.383937, 37.046441],
          [-89.385434, 37.05513],
          [-89.414471, 37.12505],
          [-89.467631, 37.2182],
          [-89.491222, 37.248629],
          [-89.517032, 37.28192],
          [-89.518393, 37.289354],
          [-89.511842, 37.310825],
          [-89.489005, 37.333368],
          [-89.484598, 37.334832],
          [-89.248439, 37.335109],
          [-89.273631, 37.126968],
          [-89.171881, 37.068184],
          [-89.175725, 37.062069],
          [-89.181369, 37.046305],
          [-89.182509, 37.037275],
          [-89.178975, 37.020928],
          [-89.173595, 37.011409],
          [-89.166447, 37.003337],
          [-89.132685, 36.9822]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17007",
      state: "17",
      county: "007",
      name: "Boone County",
      censusArea: 280.719,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.70738, 42.493587],
          [-88.707378, 42.493587],
          [-88.705633, 42.153561],
          [-88.939732, 42.15232],
          [-88.940388, 42.495046],
          [-88.776496, 42.49402],
          [-88.70738, 42.493587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17011",
      state: "17",
      county: "011",
      name: "Bureau County",
      censusArea: 869.031,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.857798, 41.234483],
          [-89.857571, 41.234768],
          [-89.856619, 41.320956],
          [-89.856902, 41.408836],
          [-89.862351, 41.584005],
          [-89.631494, 41.584949],
          [-89.628371, 41.585014],
          [-89.166561, 41.585289],
          [-89.163705, 41.310187],
          [-89.334901, 41.300877],
          [-89.356671, 41.233235],
          [-89.466534, 41.233873],
          [-89.466421, 41.148558],
          [-89.638429, 41.148591],
          [-89.638597, 41.14976],
          [-89.638864, 41.233862],
          [-89.857798, 41.234483]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17015",
      state: "17",
      county: "015",
      name: "Carroll County",
      censusArea: 444.807,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.152104, 41.928947],
          [-90.1516, 41.931002],
          [-90.140613, 41.995999],
          [-90.141167, 42.008931],
          [-90.150916, 42.02944],
          [-90.154221, 42.033073],
          [-90.164537, 42.045007],
          [-90.166495, 42.054543],
          [-90.168358, 42.075779],
          [-90.163405, 42.087613],
          [-90.161119, 42.104404],
          [-90.162225, 42.11488],
          [-90.167533, 42.122475],
          [-90.211328, 42.15401],
          [-90.234919, 42.165431],
          [-90.306531, 42.190439],
          [-90.316269, 42.1936],
          [-90.304913, 42.194073],
          [-89.919657, 42.196305],
          [-89.688486, 42.199112],
          [-89.686549, 41.937493],
          [-89.685366, 41.93034],
          [-90.152104, 41.928947]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17023",
      state: "17",
      county: "023",
      name: "Clark County",
      censusArea: 501.418,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.531663, 39.47711],
          [-87.531624, 39.469378],
          [-87.531608, 39.466225],
          [-87.531489, 39.449474],
          [-87.531355, 39.437732],
          [-87.531355, 39.436656],
          [-87.531646, 39.347888],
          [-87.537271, 39.352089],
          [-87.544013, 39.352907],
          [-87.584852, 39.337329],
          [-87.589084, 39.333831],
          [-87.604043, 39.313526],
          [-87.61005, 39.282232],
          [-87.605543, 39.261122],
          [-87.604076, 39.259459],
          [-87.593486, 39.247452],
          [-87.587507, 39.249282],
          [-87.605173, 39.185897],
          [-87.628727, 39.157427],
          [-87.746174, 39.17922],
          [-87.950385, 39.174882],
          [-88.007766, 39.173925],
          [-88.012121, 39.378968],
          [-88.014205, 39.480762],
          [-87.960179, 39.481309],
          [-87.531663, 39.47711]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17033",
      state: "17",
      county: "033",
      name: "Crawford County",
      censusArea: 443.629,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.528108, 38.908027],
          [-87.540804, 38.896385],
          [-87.553384, 38.863344],
          [-87.550515, 38.85956],
          [-87.537248, 38.853592],
          [-87.532064, 38.852503],
          [-87.759592, 38.849514],
          [-87.908113, 38.850107],
          [-87.945923, 38.850108],
          [-87.950385, 39.174882],
          [-87.746174, 39.17922],
          [-87.628727, 39.157427],
          [-87.642174, 39.157097],
          [-87.651719, 39.150602],
          [-87.658746, 39.135997],
          [-87.614881, 39.102655],
          [-87.572588, 39.057286],
          [-87.512187, 38.954417],
          [-87.518826, 38.923205],
          [-87.52791, 38.908209],
          [-87.528108, 38.908027]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17037",
      state: "17",
      county: "037",
      name: "De Kalb County",
      censusArea: 631.307,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.588657, 42.15359],
          [-88.601291, 41.981336],
          [-88.601933, 41.719563],
          [-88.60362, 41.719546],
          [-88.60224, 41.631389],
          [-88.818462, 41.631352],
          [-88.938618, 41.628319],
          [-88.941279, 41.891752],
          [-88.939732, 42.15232],
          [-88.705633, 42.153561],
          [-88.588657, 42.15359]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17041",
      state: "17",
      county: "041",
      name: "Douglas County",
      censusArea: 416.665,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.937645, 39.879803],
          [-87.9666, 39.685928],
          [-88.063437, 39.652555],
          [-88.472073, 39.651588],
          [-88.473182, 39.791837],
          [-88.462328, 39.79182],
          [-88.462207, 39.879092],
          [-87.937645, 39.879803]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17043",
      state: "17",
      county: "043",
      name: "Du Page County",
      censusArea: 327.499,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.080223, 41.990752],
          [-87.92067, 41.994031],
          [-87.92, 41.958151],
          [-87.920441, 41.906869],
          [-87.920082, 41.862199],
          [-87.919872, 41.859781],
          [-87.91964, 41.852621],
          [-87.919532, 41.851854],
          [-87.919333, 41.847679],
          [-87.914265, 41.716746],
          [-88.02916, 41.68553],
          [-88.030561, 41.72893],
          [-88.261468, 41.724529],
          [-88.261952, 41.724652],
          [-88.26247, 41.777429],
          [-88.26267, 41.779629],
          [-88.263071, 41.799328],
          [-88.263068, 41.81229],
          [-88.262876, 41.986227],
          [-88.233475, 41.986727],
          [-88.232275, 41.986627],
          [-88.217875, 41.987027],
          [-88.216575, 41.987127],
          [-88.210764, 41.987023],
          [-88.207356, 41.987127],
          [-88.080223, 41.990752]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18117",
      state: "18",
      county: "117",
      name: "Orange County",
      censusArea: 398.387,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.308674, 38.422875],
          [-86.308868, 38.394069],
          [-86.681424, 38.394767],
          [-86.682359, 38.526378],
          [-86.683102, 38.686561],
          [-86.308701, 38.688101],
          [-86.307894, 38.672977],
          [-86.308674, 38.422875]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18121",
      state: "18",
      county: "121",
      name: "Parke County",
      censusArea: 444.663,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.420221, 39.952452],
          [-87.204129, 39.953279],
          [-87.190288, 39.953379],
          [-87.190124, 39.953069],
          [-87.179415, 39.953033],
          [-87.178717, 39.953301],
          [-87.126051, 39.952985],
          [-87.125902, 39.953149],
          [-87.091006, 39.953009],
          [-87.091542, 39.867265],
          [-87.009477, 39.866705],
          [-87.013062, 39.604787],
          [-87.199012, 39.607137],
          [-87.382418, 39.607937],
          [-87.395625, 39.65295],
          [-87.353863, 39.862077],
          [-87.420221, 39.952452]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18127",
      state: "18",
      county: "127",
      name: "Porter County",
      censusArea: 418.153,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.932747, 41.71104],
          [-86.929364, 41.463209],
          [-86.930056, 41.449692],
          [-86.930079, 41.236798],
          [-86.968125, 41.219658],
          [-87.131653, 41.286155],
          [-87.218862, 41.242027],
          [-87.222644, 41.624161],
          [-87.22066, 41.624356],
          [-87.187651, 41.629653],
          [-87.160625, 41.637266],
          [-87.160784, 41.645385],
          [-87.125835, 41.650302],
          [-87.120322, 41.645701],
          [-87.066033, 41.661845],
          [-87.027888, 41.674661],
          [-87.00964, 41.68152],
          [-86.93483, 41.709638],
          [-86.932747, 41.71104]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18135",
      state: "18",
      county: "135",
      name: "Randolph County",
      censusArea: 452.379,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.803918, 40.310094],
          [-84.804098, 40.302498],
          [-84.805627, 40.223659],
          [-84.806175, 40.197995],
          [-84.80634, 40.192327],
          [-84.806347, 40.192252],
          [-84.806766, 40.180128],
          [-84.808291, 40.129027],
          [-84.808305, 40.127018],
          [-84.808706, 40.107216],
          [-84.809737, 40.048929],
          [-84.810099, 40.034214],
          [-84.81067, 40.005074],
          [-85.201473, 40.004521],
          [-85.213496, 40.00438],
          [-85.214386, 40.076889],
          [-85.218758, 40.306706],
          [-84.803918, 40.310094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18137",
      state: "18",
      county: "137",
      name: "Ripley County",
      censusArea: 446.426,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.29654, 39.268291],
          [-85.217883, 39.308474],
          [-85.065574, 39.307232],
          [-85.132508, 38.948055],
          [-85.135832, 38.929578],
          [-85.202849, 38.928523],
          [-85.203166, 38.913803],
          [-85.444897, 38.912998],
          [-85.440054, 39.195617],
          [-85.43791, 39.197132],
          [-85.29654, 39.268291]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18141",
      state: "18",
      county: "141",
      name: "St. Joseph County",
      censusArea: 457.85,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.524223, 41.759456],
          [-86.519318, 41.759447],
          [-86.501773, 41.759553],
          [-86.265496, 41.760207],
          [-86.226097, 41.760016],
          [-86.22607, 41.760016],
          [-86.21759, 41.760016],
          [-86.127844, 41.760592],
          [-86.12546, 41.76056],
          [-86.12506, 41.760576],
          [-86.062572, 41.760283],
          [-86.060516, 41.608959],
          [-86.060652, 41.6084],
          [-86.059451, 41.479154],
          [-86.467108, 41.476459],
          [-86.466603, 41.432968],
          [-86.488895, 41.43302],
          [-86.490753, 41.43291],
          [-86.519007, 41.432872],
          [-86.519751, 41.432792],
          [-86.524711, 41.432886],
          [-86.486433, 41.570545],
          [-86.526682, 41.65044],
          [-86.524223, 41.759456]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18149",
      state: "18",
      county: "149",
      name: "Starke County",
      censusArea: 309.134,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.93017, 41.172318],
          [-86.929646, 41.229711],
          [-86.930079, 41.236798],
          [-86.783473, 41.285465],
          [-86.70063, 41.401982],
          [-86.524711, 41.432886],
          [-86.519751, 41.432792],
          [-86.519007, 41.432872],
          [-86.490753, 41.43291],
          [-86.488895, 41.43302],
          [-86.466603, 41.432968],
          [-86.467046, 41.171442],
          [-86.93017, 41.172318]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18151",
      state: "18",
      county: "151",
      name: "Steuben County",
      censusArea: 308.939,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.82513, 41.759991],
          [-84.818873, 41.760059],
          [-84.806134, 41.743115],
          [-84.806074, 41.737603],
          [-84.806065, 41.732909],
          [-84.806042, 41.720544],
          [-84.806018, 41.707485],
          [-84.806082, 41.696089],
          [-84.80621, 41.67455],
          [-84.805673, 41.632342],
          [-84.805696, 41.631398],
          [-84.805812, 41.61304],
          [-84.804729, 41.530231],
          [-84.804729, 41.530135],
          [-85.194084, 41.526437],
          [-85.196774, 41.759735],
          [-85.196637, 41.759735],
          [-85.17223, 41.759618],
          [-85.123102, 41.759743],
          [-85.117267, 41.7597],
          [-85.039436, 41.759985],
          [-85.037817, 41.759801],
          [-84.972803, 41.759366],
          [-84.971551, 41.759527],
          [-84.961562, 41.759552],
          [-84.96086, 41.759438],
          [-84.932484, 41.759691],
          [-84.825196, 41.75999],
          [-84.82513, 41.759991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18159",
      state: "18",
      county: "159",
      name: "Tipton County",
      censusArea: 260.541,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.862117, 40.406889],
          [-85.862296, 40.378367],
          [-85.86116, 40.22531],
          [-85.861903, 40.218936],
          [-86.242743, 40.215834],
          [-86.242992, 40.373661],
          [-86.242812, 40.402914],
          [-85.862117, 40.406889]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18161",
      state: "18",
      county: "161",
      name: "Union County",
      censusArea: 161.224,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.814129, 39.72662],
          [-84.814129, 39.726556],
          [-84.81453, 39.680429],
          [-84.814619, 39.669174],
          [-84.814705, 39.628854],
          [-84.815156, 39.568351],
          [-84.815036, 39.567695],
          [-84.814955, 39.567251],
          [-84.814955, 39.566251],
          [-84.815155, 39.548051],
          [-84.815355, 39.521951],
          [-84.815355, 39.52195],
          [-85.036087, 39.526213],
          [-85.034575, 39.714764],
          [-84.814129, 39.72662]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18167",
      state: "18",
      county: "167",
      name: "Vigo County",
      censusArea: 403.313,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.240379, 39.259064],
          [-87.604076, 39.259459],
          [-87.605543, 39.261122],
          [-87.61005, 39.282232],
          [-87.604043, 39.313526],
          [-87.589084, 39.333831],
          [-87.584852, 39.337329],
          [-87.544013, 39.352907],
          [-87.537271, 39.352089],
          [-87.531646, 39.347888],
          [-87.531355, 39.436656],
          [-87.531355, 39.437732],
          [-87.531489, 39.449474],
          [-87.531608, 39.466225],
          [-87.531624, 39.469378],
          [-87.531663, 39.47711],
          [-87.531663, 39.47712],
          [-87.531627, 39.491698],
          [-87.531692, 39.495516],
          [-87.531965, 39.526937],
          [-87.531939, 39.545853],
          [-87.532008, 39.564013],
          [-87.532196, 39.607306],
          [-87.382418, 39.607937],
          [-87.199012, 39.607137],
          [-87.238964, 39.52062],
          [-87.240379, 39.259064]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18169",
      state: "18",
      county: "169",
      name: "Wabash County",
      censusArea: 412.434,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.945561, 41.042876],
          [-85.927265, 41.042574],
          [-85.684181, 41.046716],
          [-85.683198, 41.001909],
          [-85.643841, 41.002305],
          [-85.638587, 40.653129],
          [-85.857147, 40.651364],
          [-85.864791, 40.651693],
          [-85.939525, 40.650991],
          [-85.946436, 40.999195],
          [-85.9454, 41.000149],
          [-85.945561, 41.042876]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18175",
      state: "18",
      county: "175",
      name: "Washington County",
      censusArea: 513.725,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.99462, 38.418345],
          [-86.014139, 38.419079],
          [-86.014446, 38.418683],
          [-86.032817, 38.417898],
          [-86.255045, 38.422685],
          [-86.308674, 38.422875],
          [-86.307894, 38.672977],
          [-86.308701, 38.688101],
          [-86.275281, 38.763795],
          [-86.091705, 38.783399],
          [-85.888334, 38.734443],
          [-85.885643, 38.575412],
          [-85.847893, 38.561269],
          [-85.884776, 38.503469],
          [-85.993317, 38.48854],
          [-85.99462, 38.418345]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18181",
      state: "18",
      county: "181",
      name: "White County",
      censusArea: 505.125,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.697099, 40.737102],
          [-86.747764, 40.73754],
          [-86.771293, 40.562082],
          [-87.095357, 40.562895],
          [-87.09682, 40.736491],
          [-87.09839, 40.736541],
          [-87.099793, 40.837607],
          [-86.986438, 40.837921],
          [-86.930759, 40.912418],
          [-86.77585, 40.91246],
          [-86.581532, 40.910925],
          [-86.581739, 40.735765],
          [-86.677986, 40.736752],
          [-86.678148, 40.736897],
          [-86.697099, 40.737102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19001",
      state: "19",
      county: "001",
      name: "Adair County",
      censusArea: 569.271,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.241637, 41.157134],
          [-94.470603, 41.157566],
          [-94.700589, 41.158085],
          [-94.700629, 41.504148],
          [-94.241593, 41.503679],
          [-94.241637, 41.157134]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17047",
      state: "17",
      county: "047",
      name: "Edwards County",
      censusArea: 222.416,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.058472, 38.255747],
          [-88.1509, 38.256097],
          [-88.147814, 38.569086],
          [-87.953898, 38.570094],
          [-87.977034, 38.379374],
          [-87.972855, 38.364565],
          [-87.969656, 38.361933],
          [-87.967298, 38.353556],
          [-87.957878, 38.347675],
          [-87.954445, 38.339733],
          [-87.954798, 38.335308],
          [-87.9906, 38.259715],
          [-88.02768, 38.259771],
          [-88.027749, 38.255661],
          [-88.058472, 38.255747]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17051",
      state: "17",
      county: "051",
      name: "Fayette County",
      censusArea: 716.482,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.250658, 39.086442],
          [-89.250513, 39.217512],
          [-89.140076, 39.217907],
          [-89.139305, 39.217192],
          [-89.122992, 39.217303],
          [-89.025698, 39.215536],
          [-88.917477, 39.216545],
          [-88.838197, 39.216273],
          [-88.836967, 39.216548],
          [-88.83481, 39.21637],
          [-88.80672, 39.216402],
          [-88.805325, 39.216263],
          [-88.806794, 38.911656],
          [-88.693531, 38.914617],
          [-88.695165, 38.826299],
          [-89.138138, 38.824244],
          [-89.138393, 38.736331],
          [-89.254237, 38.742019],
          [-89.250447, 39.028145],
          [-89.250335, 39.029942],
          [-89.250658, 39.086442]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17053",
      state: "17",
      county: "053",
      name: "Ford County",
      censusArea: 485.616,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.935309, 40.485923],
          [-87.931758, 40.400187],
          [-87.932858, 40.399401],
          [-88.459957, 40.39885],
          [-88.459475, 40.617345],
          [-88.234949, 40.618166],
          [-88.2473, 40.99456],
          [-88.245975, 40.994607],
          [-88.236498, 40.995218],
          [-88.133741, 40.997605],
          [-88.131938, 40.997839],
          [-88.117905, 40.488086],
          [-87.935309, 40.485923]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17057",
      state: "17",
      county: "057",
      name: "Fulton County",
      censusArea: 865.595,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.215267, 40.713598],
          [-89.986068, 40.712257],
          [-89.989018, 40.625835],
          [-89.873506, 40.624588],
          [-89.872463, 40.513127],
          [-89.92468, 40.435921],
          [-90.033026, 40.377806],
          [-90.118966, 40.235263],
          [-90.199556, 40.183945],
          [-90.451502, 40.188892],
          [-90.450227, 40.276335],
          [-90.445506, 40.627843],
          [-90.445217, 40.65687],
          [-90.445084, 40.657202],
          [-90.445009, 40.662406],
          [-90.444877, 40.663944],
          [-90.44475, 40.677479],
          [-90.444875, 40.677784],
          [-90.444606, 40.695505],
          [-90.444429, 40.697371],
          [-90.444343, 40.714667],
          [-90.215267, 40.713598]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17063",
      state: "17",
      county: "063",
      name: "Grundy County",
      censusArea: 418.043,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.36314, 41.11158],
          [-88.58624, 41.108293],
          [-88.59596, 41.457034],
          [-88.254972, 41.462768],
          [-88.252098, 41.463096],
          [-88.244155, 41.201546],
          [-88.240889, 41.114383],
          [-88.251995, 41.114229],
          [-88.36314, 41.11158]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18055",
      state: "18",
      county: "055",
      name: "Greene County",
      censusArea: 542.495,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.241027, 38.907874],
          [-87.240737, 39.171773],
          [-87.068341, 39.167965],
          [-87.054578, 39.168086],
          [-86.683002, 39.165746],
          [-86.682277, 38.992404],
          [-86.682853, 38.904697],
          [-86.904252, 38.904204],
          [-87.031263, 38.902516],
          [-87.098771, 38.903974],
          [-87.241027, 38.907874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18057",
      state: "18",
      county: "057",
      name: "Hamilton County",
      censusArea: 394.267,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.172237, 39.926895],
          [-86.239739, 39.926055],
          [-86.242365, 40.180778],
          [-86.242743, 40.215834],
          [-85.861903, 40.218936],
          [-85.862489, 39.943618],
          [-85.862395, 39.928943],
          [-85.937587, 39.927137],
          [-86.172237, 39.926895]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18065",
      state: "18",
      county: "065",
      name: "Henry County",
      censusArea: 391.876,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.576192, 39.945756],
          [-85.576197, 40.077143],
          [-85.216033, 40.076828],
          [-85.214386, 40.076889],
          [-85.213496, 40.00438],
          [-85.201473, 40.004521],
          [-85.221118, 39.788449],
          [-85.301584, 39.787579],
          [-85.596916, 39.786519],
          [-85.576192, 39.945756]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18067",
      state: "18",
      county: "067",
      name: "Howard County",
      censusArea: 293.057,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.184044, 40.562459],
          [-86.165024, 40.562649],
          [-86.127424, 40.563098],
          [-85.863641, 40.565525],
          [-85.862117, 40.406889],
          [-86.242812, 40.402914],
          [-86.242992, 40.373661],
          [-86.375762, 40.431851],
          [-86.374417, 40.561361],
          [-86.184044, 40.562459]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18071",
      state: "18",
      county: "071",
      name: "Jackson County",
      censusArea: 509.312,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.888334, 38.734443],
          [-86.091705, 38.783399],
          [-86.275281, 38.763795],
          [-86.280389, 38.991028],
          [-86.317474, 38.99087],
          [-86.31738, 39.00005],
          [-86.317799, 39.002733],
          [-86.318133, 39.04881],
          [-86.080272, 39.05027],
          [-85.860573, 39.04091],
          [-85.798804, 39.068536],
          [-85.795357, 38.807508],
          [-85.888334, 38.734443]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18077",
      state: "18",
      county: "077",
      name: "Jefferson County",
      censusArea: 360.631,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.439351, 38.610388],
          [-85.427467, 38.586735],
          [-85.56998, 38.606155],
          [-85.57093, 38.678706],
          [-85.683338, 38.736731],
          [-85.683839, 38.815406],
          [-85.444897, 38.912998],
          [-85.203166, 38.913803],
          [-85.201498, 38.691575],
          [-85.213257, 38.695446],
          [-85.226062, 38.705456],
          [-85.246505, 38.731821],
          [-85.258846, 38.737754],
          [-85.275454, 38.741172],
          [-85.289226, 38.74241],
          [-85.333285, 38.74048],
          [-85.410925, 38.73708],
          [-85.422021, 38.734834],
          [-85.434065, 38.729455],
          [-85.452114, 38.709348],
          [-85.456978, 38.689135],
          [-85.439351, 38.610388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18087",
      state: "18",
      county: "087",
      name: "Lagrange County",
      censusArea: 379.624,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.65975, 41.759101],
          [-85.650738, 41.759103],
          [-85.647683, 41.759125],
          [-85.632714, 41.759164],
          [-85.624987, 41.759093],
          [-85.622608, 41.759049],
          [-85.608312, 41.759193],
          [-85.607548, 41.759079],
          [-85.518251, 41.759513],
          [-85.515959, 41.759352],
          [-85.432471, 41.759684],
          [-85.427553, 41.759706],
          [-85.379133, 41.759875],
          [-85.350174, 41.759908],
          [-85.330623, 41.759982],
          [-85.318129, 41.759983],
          [-85.30814, 41.760097],
          [-85.298365, 41.760028],
          [-85.292178, 41.759963],
          [-85.292099, 41.759962],
          [-85.273713, 41.75977],
          [-85.272951, 41.759911],
          [-85.272216, 41.759999],
          [-85.232835, 41.759839],
          [-85.196774, 41.759735],
          [-85.194084, 41.526437],
          [-85.654747, 41.523347],
          [-85.65975, 41.759101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18091",
      state: "18",
      county: "091",
      name: "La Porte County",
      censusArea: 598.299,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.824828, 41.76024],
          [-86.823628, 41.76024],
          [-86.804427, 41.76024],
          [-86.801578, 41.76024],
          [-86.800707, 41.76024],
          [-86.800611, 41.760251],
          [-86.748096, 41.759967],
          [-86.746521, 41.759982],
          [-86.641186, 41.759633],
          [-86.640044, 41.759671],
          [-86.524223, 41.759456],
          [-86.526682, 41.65044],
          [-86.486433, 41.570545],
          [-86.524711, 41.432886],
          [-86.70063, 41.401982],
          [-86.783473, 41.285465],
          [-86.930079, 41.236798],
          [-86.930056, 41.449692],
          [-86.929364, 41.463209],
          [-86.932747, 41.71104],
          [-86.90913, 41.726938],
          [-86.875429, 41.737939],
          [-86.824828, 41.76024]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18097",
      state: "18",
      county: "097",
      name: "Marion County",
      censusArea: 396.298,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.937587, 39.927137],
          [-85.938084, 39.923196],
          [-85.938077, 39.887183],
          [-85.951699, 39.726419],
          [-85.951721, 39.697136],
          [-85.95208, 39.638345],
          [-86.044705, 39.637219],
          [-86.242924, 39.633744],
          [-86.249885, 39.633645],
          [-86.268501, 39.633047],
          [-86.269239, 39.633267],
          [-86.326341, 39.632177],
          [-86.326576, 39.682896],
          [-86.326293, 39.924029],
          [-86.263305, 39.924889],
          [-86.24109, 39.926092],
          [-86.239739, 39.926055],
          [-86.172237, 39.926895],
          [-85.937587, 39.927137]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18101",
      state: "18",
      county: "101",
      name: "Martin County",
      censusArea: 335.737,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.682359, 38.526378],
          [-86.924186, 38.505358],
          [-86.922943, 38.817238],
          [-86.904252, 38.904204],
          [-86.682853, 38.904697],
          [-86.683102, 38.686561],
          [-86.682359, 38.526378]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18103",
      state: "18",
      county: "103",
      name: "Miami County",
      censusArea: 373.842,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.864791, 40.651693],
          [-85.863641, 40.565525],
          [-86.127424, 40.563098],
          [-86.165024, 40.562649],
          [-86.169012, 40.909836],
          [-86.168459, 40.996097],
          [-85.946436, 40.999195],
          [-85.939525, 40.650991],
          [-85.864791, 40.651693]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18109",
      state: "18",
      county: "109",
      name: "Morgan County",
      censusArea: 403.969,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.249885, 39.633645],
          [-86.25131, 39.430595],
          [-86.251684, 39.341672],
          [-86.381395, 39.339752],
          [-86.57428, 39.339681],
          [-86.630844, 39.340755],
          [-86.630818, 39.346948],
          [-86.632206, 39.470214],
          [-86.68571, 39.470063],
          [-86.65665, 39.601395],
          [-86.465605, 39.600769],
          [-86.326341, 39.632177],
          [-86.269239, 39.633267],
          [-86.268501, 39.633047],
          [-86.249885, 39.633645]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16023",
      state: "16",
      county: "023",
      name: "Butte County",
      censusArea: 2231.667,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.798031, 43.568818],
          [-113.412487, 43.81684],
          [-113.361529, 43.797376],
          [-113.453713, 43.873739],
          [-113.457929, 44.059209],
          [-113.416344, 44.059209],
          [-113.416796, 44.233311],
          [-113.320672, 44.232998],
          [-112.995379, 44.233956],
          [-112.994195, 43.972403],
          [-112.695027, 43.97194],
          [-112.697408, 43.623011],
          [-112.69908, 43.540244],
          [-112.816193, 43.535869],
          [-112.816659, 43.449048],
          [-112.936835, 43.449386],
          [-112.947187, 43.284725],
          [-113.00729, 43.284801],
          [-113.361905, 43.284655],
          [-113.36191, 43.367232],
          [-113.635446, 43.367556],
          [-113.63526, 43.452266],
          [-113.593503, 43.467251],
          [-113.70057, 43.584762],
          [-113.798031, 43.568818]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16025",
      state: "16",
      county: "025",
      name: "Camas County",
      censusArea: 1074.493,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.374877, 43.199603],
          [-114.594685, 43.198302],
          [-115.08648, 43.198436],
          [-115.083089, 43.604292],
          [-115.008925, 43.697398],
          [-115.042177, 43.745451],
          [-114.989384, 43.858611],
          [-114.828915, 43.788027],
          [-114.714849, 43.814844],
          [-114.666283, 43.73801],
          [-114.682225, 43.590431],
          [-114.586574, 43.558902],
          [-114.507786, 43.480804],
          [-114.546987, 43.436603],
          [-114.53456, 43.333337],
          [-114.39458, 43.325804],
          [-114.374877, 43.199603]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16029",
      state: "16",
      county: "029",
      name: "Caribou County",
      censusArea: 1764.146,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.044156, 43.020052],
          [-111.044129, 43.018702],
          [-111.043924, 42.975063],
          [-111.043957, 42.969482],
          [-111.043959, 42.96445],
          [-111.046249, 42.513116],
          [-111.170956, 42.473299],
          [-111.234734, 42.478378],
          [-111.241365, 42.602078],
          [-111.280054, 42.553042],
          [-111.364809, 42.590462],
          [-111.603999, 42.590757],
          [-111.634939, 42.570117],
          [-111.579321, 42.43051],
          [-111.598597, 42.415821],
          [-111.875573, 42.416335],
          [-111.969449, 42.718039],
          [-112.105476, 42.699195],
          [-112.14114, 42.763051],
          [-112.120692, 42.831618],
          [-112.149792, 42.895603],
          [-112.082064, 42.950516],
          [-112.063055, 43.022364],
          [-111.589615, 43.020052],
          [-111.044156, 43.020052]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16033",
      state: "16",
      county: "033",
      name: "Clark County",
      censusArea: 1764.185,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.829247, 44.385427],
          [-112.824701, 44.393611],
          [-112.823373, 44.406225],
          [-112.822046, 44.420831],
          [-112.822046, 44.434109],
          [-112.812087, 44.45137],
          [-112.781294, 44.484888],
          [-112.71911, 44.504344],
          [-112.68816, 44.49885],
          [-112.544224, 44.483763],
          [-112.473207, 44.480027],
          [-112.410345, 44.462968],
          [-112.38708, 44.460476],
          [-112.371294, 44.472108],
          [-112.358926, 44.48628],
          [-112.3566, 44.493127],
          [-112.347199, 44.520298],
          [-112.339721, 44.538577],
          [-112.299009, 44.559348],
          [-112.274083, 44.564334],
          [-112.258297, 44.564334],
          [-112.246665, 44.56101],
          [-112.235864, 44.555194],
          [-112.229477, 44.549494],
          [-112.221698, 44.543519],
          [-112.187115, 44.534452],
          [-112.078471, 44.533551],
          [-111.947941, 44.556776],
          [-111.870504, 44.564033],
          [-111.849293, 44.539837],
          [-111.843295, 44.528225],
          [-111.806528, 44.515459],
          [-111.737191, 44.54306],
          [-111.704218, 44.560205],
          [-111.631505, 44.552022],
          [-111.617502, 44.553035],
          [-111.617773, 44.401585],
          [-111.791624, 44.401488],
          [-111.791456, 44.316956],
          [-111.852351, 44.316949],
          [-111.857973, 44.1878],
          [-111.913513, 44.144092],
          [-112.033664, 44.144049],
          [-112.034268, 44.05735],
          [-112.15595, 44.057677],
          [-112.694674, 44.058454],
          [-112.695027, 43.97194],
          [-112.994195, 43.972403],
          [-112.995379, 44.233956],
          [-112.813907, 44.230439],
          [-112.829247, 44.385427]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16041",
      state: "16",
      county: "041",
      name: "Franklin County",
      censusArea: 663.645,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.507264, 41.999518],
          [-111.876491, 41.998528],
          [-111.915622, 41.998496],
          [-111.915837, 41.998519],
          [-112.109528, 41.997105],
          [-112.10921, 42.169781],
          [-112.071014, 42.238475],
          [-112.126286, 42.28533],
          [-111.897244, 42.256646],
          [-111.875573, 42.416335],
          [-111.598597, 42.415821],
          [-111.552231, 42.351514],
          [-111.626162, 42.208613],
          [-111.594446, 42.093548],
          [-111.507264, 41.999518]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16043",
      state: "16",
      county: "043",
      name: "Fremont County",
      censusArea: 1863.525,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.048974, 44.474072],
          [-111.049194, 44.438058],
          [-111.049216, 44.435811],
          [-111.048633, 44.062903],
          [-111.048751, 44.060838],
          [-111.048751, 44.060403],
          [-111.048107, 43.983096],
          [-111.18711, 43.932202],
          [-111.398883, 43.923086],
          [-111.501009, 43.929272],
          [-111.558833, 43.883305],
          [-111.837795, 43.898332],
          [-111.976833, 43.927431],
          [-111.97709, 43.970929],
          [-112.15637, 43.971382],
          [-112.15595, 44.057677],
          [-112.034268, 44.05735],
          [-112.033664, 44.144049],
          [-111.913513, 44.144092],
          [-111.857973, 44.1878],
          [-111.852351, 44.316949],
          [-111.791456, 44.316956],
          [-111.791624, 44.401488],
          [-111.617773, 44.401585],
          [-111.617502, 44.553035],
          [-111.601249, 44.55421],
          [-111.525764, 44.604883],
          [-111.516588, 44.643707],
          [-111.489008, 44.705553],
          [-111.481536, 44.709186],
          [-111.394815, 44.751351],
          [-111.377682, 44.751744],
          [-111.323669, 44.724474],
          [-111.219236, 44.622616],
          [-111.139455, 44.517112],
          [-111.131379, 44.499925],
          [-111.122654, 44.493659],
          [-111.106574, 44.48633],
          [-111.048974, 44.474072]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16047",
      state: "16",
      county: "047",
      name: "Gooding County",
      censusArea: 728.973,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.594685, 43.198302],
          [-114.594375, 42.850826],
          [-114.6174, 42.850811],
          [-114.616535, 42.649367],
          [-114.821582, 42.671582],
          [-114.850557, 42.749904],
          [-114.928714, 42.764961],
          [-114.901359, 42.858678],
          [-115.037262, 42.911931],
          [-115.086808, 42.914691],
          [-115.08648, 43.198436],
          [-114.594685, 43.198302]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16053",
      state: "16",
      county: "053",
      name: "Jerome County",
      censusArea: 597.185,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.931799, 42.535275],
          [-113.956046, 42.530243],
          [-113.959446, 42.53502],
          [-113.985215, 42.534136],
          [-114.000691, 42.526434],
          [-114.045718, 42.528766],
          [-114.130292, 42.494975],
          [-114.375289, 42.598227],
          [-114.616535, 42.649367],
          [-114.6174, 42.850811],
          [-114.594375, 42.850826],
          [-114.365366, 42.85079],
          [-114.345659, 42.809055],
          [-114.04987, 42.808521],
          [-114.049868, 42.765121],
          [-113.932904, 42.765032],
          [-113.931799, 42.535275]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16055",
      state: "16",
      county: "055",
      name: "Kootenai County",
      censusArea: 1244.125,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.039857, 47.366093],
          [-117.039882, 47.399085],
          [-117.03995, 47.412412],
          [-117.039948, 47.434885],
          [-117.039971, 47.463309],
          [-117.039945, 47.477823],
          [-117.040514, 47.522351],
          [-117.040545, 47.527562],
          [-117.040745, 47.532909],
          [-117.041276, 47.55821],
          [-117.041174, 47.55853],
          [-117.041431, 47.67814],
          [-117.041431, 47.678185],
          [-117.041431, 47.68],
          [-117.041532, 47.683194],
          [-117.041633, 47.7064],
          [-117.041678, 47.72271],
          [-117.042135, 47.7441],
          [-117.042059, 47.7451],
          [-117.042657, 47.760857],
          [-117.042623, 47.761223],
          [-117.042521, 47.764896],
          [-117.042485, 47.766525],
          [-117.041874, 47.977387],
          [-116.633095, 47.977147],
          [-116.504214, 47.991479],
          [-116.505028, 47.890188],
          [-116.322825, 47.890342],
          [-116.329496, 47.413781],
          [-116.586576, 47.414662],
          [-116.629147, 47.371701],
          [-117.039857, 47.366093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16061",
      state: "16",
      county: "061",
      name: "Lewis County",
      censusArea: 478.795,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.369247, 46.466921],
          [-116.308539, 46.412998],
          [-116.170422, 46.371589],
          [-116.012665, 46.225747],
          [-116.133369, 46.215189],
          [-116.189977, 46.154379],
          [-116.427985, 46.202578],
          [-116.514484, 46.131828],
          [-116.529919, 46.000809],
          [-116.638651, 46.036076],
          [-116.701977, 45.996177],
          [-116.713153, 46.325521],
          [-116.489852, 46.325334],
          [-116.362266, 46.352336],
          [-116.369247, 46.466921]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16065",
      state: "16",
      county: "065",
      name: "Madison County",
      censusArea: 469.206,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.398883, 43.923086],
          [-111.399915, 43.622015],
          [-111.626438, 43.626748],
          [-111.932811, 43.766845],
          [-111.983381, 43.754138],
          [-111.976833, 43.927431],
          [-111.837795, 43.898332],
          [-111.558833, 43.883305],
          [-111.501009, 43.929272],
          [-111.398883, 43.923086]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16069",
      state: "16",
      county: "069",
      name: "Nez Perce County",
      censusArea: 848.092,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.915989, 45.995413],
          [-116.94353, 46.062173],
          [-116.925621, 46.158888],
          [-116.958801, 46.24232],
          [-116.985333, 46.294617],
          [-116.987939, 46.298031],
          [-117.047469, 46.342884],
          [-117.061045, 46.367747],
          [-117.046915, 46.379577],
          [-117.039813, 46.425425],
          [-117.039741, 46.462704],
          [-117.039763, 46.46957],
          [-117.039771, 46.471779],
          [-117.039783, 46.541785],
          [-116.719183, 46.542789],
          [-116.625985, 46.628041],
          [-116.455881, 46.629097],
          [-116.448618, 46.497056],
          [-116.369352, 46.49975],
          [-116.369247, 46.466921],
          [-116.362266, 46.352336],
          [-116.489852, 46.325334],
          [-116.713153, 46.325521],
          [-116.701977, 45.996177],
          [-116.796418, 45.853807],
          [-116.857254, 45.904159],
          [-116.866544, 45.916958],
          [-116.875125, 45.9422],
          [-116.915989, 45.995413]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18007",
      state: "18",
      county: "007",
      name: "Benton County",
      censusArea: 406.418,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.09839, 40.736541],
          [-87.09682, 40.736491],
          [-87.095357, 40.562895],
          [-87.093676, 40.475745],
          [-87.526511, 40.476879],
          [-87.526502, 40.477158],
          [-87.526379, 40.491237],
          [-87.526376, 40.491574],
          [-87.526352, 40.535111],
          [-87.526292, 40.535409],
          [-87.526129, 40.736885],
          [-87.2673, 40.736582],
          [-87.09839, 40.736541]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18013",
      state: "18",
      county: "013",
      name: "Brown County",
      censusArea: 311.981,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.085601, 39.344189],
          [-86.080294, 39.152543],
          [-86.080272, 39.05027],
          [-86.318133, 39.04881],
          [-86.370066, 39.048375],
          [-86.381395, 39.339752],
          [-86.251684, 39.341672],
          [-86.085601, 39.344189]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18015",
      state: "18",
      county: "015",
      name: "Carroll County",
      censusArea: 372.224,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.771293, 40.562082],
          [-86.747764, 40.73754],
          [-86.697099, 40.737102],
          [-86.678148, 40.736897],
          [-86.677986, 40.736752],
          [-86.581739, 40.735765],
          [-86.525164, 40.692419],
          [-86.373521, 40.69246],
          [-86.374417, 40.561361],
          [-86.375762, 40.431851],
          [-86.694665, 40.432156],
          [-86.695353, 40.561874],
          [-86.771293, 40.562082]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18019",
      state: "18",
      county: "019",
      name: "Clark County",
      censusArea: 372.855,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.638044, 38.38027],
          [-85.638777, 38.361443],
          [-85.653641, 38.327108],
          [-85.675017, 38.301317],
          [-85.683561, 38.295469],
          [-85.744862, 38.26717],
          [-85.750962, 38.26787],
          [-85.761062, 38.27257],
          [-85.766563, 38.27767],
          [-85.765963, 38.280469],
          [-85.773363, 38.286169],
          [-85.780963, 38.288469],
          [-85.791563, 38.288569],
          [-85.791575, 38.288566],
          [-85.760844, 38.350218],
          [-85.807704, 38.404443],
          [-85.99462, 38.418345],
          [-85.993317, 38.48854],
          [-85.884776, 38.503469],
          [-85.847893, 38.561269],
          [-85.793584, 38.604814],
          [-85.56998, 38.606155],
          [-85.427467, 38.586735],
          [-85.415821, 38.563558],
          [-85.4156, 38.546341],
          [-85.417322, 38.540763],
          [-85.423077, 38.531581],
          [-85.432928, 38.524072],
          [-85.433136, 38.523914],
          [-85.462518, 38.512602],
          [-85.607629, 38.439295],
          [-85.620521, 38.423105],
          [-85.632937, 38.395666],
          [-85.638041, 38.380338],
          [-85.638044, 38.38027]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18025",
      state: "18",
      county: "025",
      name: "Crawford County",
      censusArea: 305.643,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.323453, 38.139032],
          [-86.326788, 38.134604],
          [-86.332567, 38.130131],
          [-86.398661, 38.106238],
          [-86.426508, 38.081784],
          [-86.460425, 38.206825],
          [-86.571677, 38.207531],
          [-86.570136, 38.266328],
          [-86.679511, 38.263086],
          [-86.681424, 38.394767],
          [-86.308868, 38.394069],
          [-86.308674, 38.422875],
          [-86.255045, 38.422685],
          [-86.314292, 38.14261],
          [-86.323453, 38.139032]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18031",
      state: "18",
      county: "031",
      name: "Decatur County",
      censusArea: 372.568,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.686783, 39.130859],
          [-85.687294, 39.185605],
          [-85.687161, 39.189026],
          [-85.687295, 39.190076],
          [-85.684515, 39.350049],
          [-85.63063, 39.350205],
          [-85.629327, 39.45275],
          [-85.297575, 39.453275],
          [-85.29654, 39.268291],
          [-85.43791, 39.197132],
          [-85.440054, 39.195617],
          [-85.56632, 39.132761],
          [-85.686783, 39.130859]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18035",
      state: "18",
      county: "035",
      name: "Delaware County",
      censusArea: 392.124,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.44433, 40.37914],
          [-85.219901, 40.379034],
          [-85.220028, 40.375043],
          [-85.218758, 40.306706],
          [-85.214386, 40.076889],
          [-85.216033, 40.076828],
          [-85.576197, 40.077143],
          [-85.578589, 40.379524],
          [-85.552303, 40.378666],
          [-85.44433, 40.37914]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18045",
      state: "18",
      county: "045",
      name: "Fountain County",
      censusArea: 395.656,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.091957, 40.171253],
          [-87.090022, 39.953571],
          [-87.091006, 39.953009],
          [-87.125902, 39.953149],
          [-87.126051, 39.952985],
          [-87.178717, 39.953301],
          [-87.179415, 39.953033],
          [-87.190124, 39.953069],
          [-87.190288, 39.953379],
          [-87.204129, 39.953279],
          [-87.420221, 39.952452],
          [-87.406667, 40.127508],
          [-87.438755, 40.165694],
          [-87.339273, 40.223173],
          [-87.311889, 40.250036],
          [-87.294191, 40.257966],
          [-87.244898, 40.309252],
          [-87.092148, 40.366612],
          [-87.092563, 40.214806],
          [-87.091957, 40.171253]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13137",
      state: "13",
      county: "137",
      name: "Habersham County",
      censusArea: 276.74,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.352485, 34.715993],
          [-83.351392, 34.714456],
          [-83.350976, 34.713243],
          [-83.351293, 34.701703],
          [-83.338194, 34.687924],
          [-83.337867, 34.687681],
          [-83.45977, 34.481142],
          [-83.537538, 34.491725],
          [-83.615251, 34.431748],
          [-83.666413, 34.503598],
          [-83.61554, 34.552864],
          [-83.662819, 34.659801],
          [-83.623296, 34.732194],
          [-83.681596, 34.800593],
          [-83.666855, 34.806528],
          [-83.657921, 34.813682],
          [-83.652194, 34.822993],
          [-83.586897, 34.819991],
          [-83.548988, 34.749451],
          [-83.352422, 34.716111],
          [-83.352485, 34.715993]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13141",
      state: "13",
      county: "141",
      name: "Hancock County",
      censusArea: 471.841,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.052197, 33.080682],
          [-83.100834, 33.049849],
          [-83.097507, 33.15141],
          [-83.274108, 33.187238],
          [-83.253461, 33.25929],
          [-83.145774, 33.312084],
          [-83.164207, 33.35503],
          [-83.012853, 33.469178],
          [-82.851954, 33.443543],
          [-82.755846, 33.25344],
          [-82.747238, 33.247663],
          [-82.748311, 33.238348],
          [-82.855046, 33.196427],
          [-82.888866, 33.138603],
          [-83.052197, 33.080682]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13147",
      state: "13",
      county: "147",
      name: "Hart County",
      censusArea: 232.394,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.992215, 34.479198],
          [-82.979568, 34.482702],
          [-82.939067, 34.486202],
          [-82.902665, 34.485902],
          [-82.882864, 34.479003],
          [-82.873831, 34.471508],
          [-82.874864, 34.468891],
          [-82.848651, 34.423844],
          [-82.766078, 34.293509],
          [-82.980905, 34.211505],
          [-83.078004, 34.223606],
          [-83.115646, 34.262111],
          [-83.11336, 34.27353],
          [-83.094238, 34.415901],
          [-83.052161, 34.493062],
          [-83.034712, 34.483495],
          [-83.006848, 34.474376],
          [-82.995284, 34.474549],
          [-82.992671, 34.479072],
          [-82.992215, 34.479198]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13155",
      state: "13",
      county: "155",
      name: "Irwin County",
      censusArea: 354.343,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.500702, 31.59399],
          [-83.453634, 31.757861],
          [-83.325296, 31.756584],
          [-83.325617, 31.680603],
          [-83.177218, 31.651691],
          [-83.176376, 31.678382],
          [-82.99836, 31.673164],
          [-83.145587, 31.472276],
          [-83.146085, 31.472088],
          [-83.338728, 31.475991],
          [-83.500702, 31.59399]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13159",
      state: "13",
      county: "159",
      name: "Jasper County",
      censusArea: 368.165,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.816048, 33.131816],
          [-83.817268, 33.14154],
          [-83.814056, 33.14846],
          [-83.822929, 33.157856],
          [-83.824585, 33.17564],
          [-83.822261, 33.180238],
          [-83.814103, 33.242134],
          [-83.863058, 33.368278],
          [-83.824922, 33.437263],
          [-83.674644, 33.487225],
          [-83.682196, 33.52622],
          [-83.586817, 33.465577],
          [-83.533736, 33.434472],
          [-83.537811, 33.393803],
          [-83.545876, 33.171944],
          [-83.816048, 33.131816]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13165",
      state: "13",
      county: "165",
      name: "Jenkins County",
      censusArea: 347.279,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.767553, 32.909411],
          [-81.867938, 32.68115],
          [-81.841005, 32.649093],
          [-82.001236, 32.60691],
          [-82.08244, 32.663061],
          [-82.144977, 32.812741],
          [-82.081331, 32.916782],
          [-81.857976, 32.953882],
          [-81.767553, 32.909411]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13171",
      state: "13",
      county: "171",
      name: "Lamar County",
      censusArea: 183.5,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.27014, 32.991011],
          [-84.248185, 33.189019],
          [-84.123767, 33.202824],
          [-84.041498, 33.202629],
          [-84.044113, 32.947874],
          [-84.12334, 32.932184],
          [-84.122361, 32.989576],
          [-84.27014, 32.991011]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13173",
      state: "13",
      county: "173",
      name: "Lanier County",
      censusArea: 185.261,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.019419, 30.849453],
          [-83.042924, 30.947296],
          [-83.179465, 30.950128],
          [-83.197971, 31.025405],
          [-83.165072, 31.147198],
          [-83.038246, 31.146705],
          [-83.046885, 31.18368],
          [-82.971247, 31.183988],
          [-82.971336, 30.869392],
          [-82.982155, 30.872249],
          [-82.993866, 30.860786],
          [-83.006577, 30.85947],
          [-83.013962, 30.844709],
          [-83.019419, 30.849453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20091",
      state: "20",
      county: "091",
      name: "Johnson County",
      censusArea: 473.375,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.608033, 38.847207],
          [-94.608041, 38.811064],
          [-94.609039, 38.760611],
          [-94.609399, 38.74244],
          [-94.609456, 38.7407],
          [-94.609509, 38.738102],
          [-95.056412, 38.738587],
          [-95.056207, 38.811432],
          [-95.056258, 38.98212],
          [-94.908765, 38.991401],
          [-94.872442, 39.059088],
          [-94.607391, 39.044086],
          [-94.608134, 38.942006],
          [-94.608134, 38.940006],
          [-94.607866, 38.937398],
          [-94.607978, 38.93687],
          [-94.608033, 38.883807],
          [-94.608033, 38.869207],
          [-94.608033, 38.868107],
          [-94.607993, 38.867271],
          [-94.608033, 38.861207],
          [-94.608033, 38.855007],
          [-94.608033, 38.847207]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20097",
      state: "20",
      county: "097",
      name: "Kiowa County",
      censusArea: 722.639,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.543189, 37.381124],
          [-99.555774, 37.381112],
          [-99.556119, 37.467722],
          [-99.55958, 37.734888],
          [-99.557681, 37.734925],
          [-99.01355, 37.73297],
          [-99.012598, 37.470421],
          [-99.012186, 37.436186],
          [-99.011755, 37.431042],
          [-99.011499, 37.384263],
          [-99.543189, 37.381124]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20099",
      state: "20",
      county: "099",
      name: "Labette County",
      censusArea: 645.295,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.407572, 36.999241],
          [-95.407683, 36.999241],
          [-95.511578, 36.999235],
          [-95.522415, 36.999281],
          [-95.517976, 37.266376],
          [-95.52113, 37.38399],
          [-95.088189, 37.383837],
          [-95.074972, 37.383823],
          [-95.075166, 37.339937],
          [-95.073504, 36.999509],
          [-95.073509, 36.999509],
          [-95.155187, 36.999539],
          [-95.155372, 36.99954],
          [-95.177301, 36.99952],
          [-95.195307, 36.999565],
          [-95.322565, 36.999358],
          [-95.328058, 36.999365],
          [-95.328327, 36.999366],
          [-95.33121, 36.99938],
          [-95.407572, 36.999241]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20103",
      state: "20",
      county: "103",
      name: "Leavenworth County",
      censusArea: 462.831,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.969061, 39.418876],
          [-94.946662, 39.399717],
          [-94.909954, 39.367459],
          [-94.908337, 39.355647],
          [-94.910641, 39.348335],
          [-94.907569, 39.320623],
          [-94.901009, 39.30168],
          [-94.832399, 39.227218],
          [-94.793948, 39.201276],
          [-94.900191, 39.202911],
          [-94.908765, 38.991401],
          [-95.056258, 38.98212],
          [-95.164123, 38.98163],
          [-95.187103, 39.044109],
          [-95.180891, 39.419218],
          [-94.969061, 39.418876]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20109",
      state: "20",
      county: "109",
      name: "Logan County",
      censusArea: 1072.994,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.811858, 39.13336],
          [-100.818698, 38.699861],
          [-101.128379, 38.700603],
          [-101.484383, 38.700166],
          [-101.478195, 39.134761],
          [-101.391717, 39.135117],
          [-100.811858, 39.13336]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20113",
      state: "20",
      county: "113",
      name: "Mcpherson County",
      censusArea: 898.274,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.922136, 38.173713],
          [-97.924269, 38.522755],
          [-97.924795, 38.60988],
          [-97.371911, 38.609353],
          [-97.37175, 38.173673],
          [-97.701841, 38.173814],
          [-97.922136, 38.173713]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20119",
      state: "20",
      county: "119",
      name: "Meade County",
      censusArea: 978.088,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.089484, 37.002092],
          [-100.115722, 37.002206],
          [-100.187547, 37.002082],
          [-100.192371, 37.002036],
          [-100.193754, 37.002133],
          [-100.201676, 37.002081],
          [-100.551598, 37.00062],
          [-100.552683, 37.000735],
          [-100.591328, 37.000376],
          [-100.591413, 37.000399],
          [-100.62977, 37.000025],
          [-100.633323, 36.999936],
          [-100.633831, 37.387577],
          [-100.652728, 37.387627],
          [-100.652642, 37.474848],
          [-100.216229, 37.47519],
          [-100.161075, 37.475021],
          [-100.15822, 37.474854],
          [-100.155812, 37.475016],
          [-100.107248, 37.474836],
          [-100.091529, 37.386892],
          [-100.089484, 37.002092]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20125",
      state: "20",
      county: "125",
      name: "Montgomery County",
      censusArea: 643.527,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.522415, 36.999281],
          [-95.534401, 36.999332],
          [-95.573598, 36.99931],
          [-95.61214, 36.999321],
          [-95.615934, 36.999365],
          [-95.62435, 36.99936],
          [-95.630079, 36.99932],
          [-95.664301, 36.999322],
          [-95.686452, 36.999349],
          [-95.696659, 36.999215],
          [-95.71038, 36.999371],
          [-95.714887, 36.999279],
          [-95.718054, 36.999255],
          [-95.741908, 36.999244],
          [-95.759905, 36.999271],
          [-95.768719, 36.999205],
          [-95.786762, 36.99931],
          [-95.80798, 36.999124],
          [-95.866899, 36.999261],
          [-95.873944, 36.9993],
          [-95.875257, 36.999302],
          [-95.877151, 36.999304],
          [-95.91018, 36.999336],
          [-95.928122, 36.999245],
          [-95.936992, 36.999268],
          [-95.96427, 36.999094],
          [-95.964399, 37.299232],
          [-95.964666, 37.386656],
          [-95.961605, 37.386636],
          [-95.52556, 37.383979],
          [-95.52113, 37.38399],
          [-95.517976, 37.266376],
          [-95.522415, 36.999281]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20131",
      state: "20",
      county: "131",
      name: "Nemaha County",
      censusArea: 717.427,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.239208, 40.000691],
          [-96.239172, 40.000691],
          [-96.223839, 40.000729],
          [-96.220171, 40.00072],
          [-96.154365, 40.000495],
          [-96.154246, 40.00045],
          [-96.147167, 40.000479],
          [-96.125937, 40.000432],
          [-96.125788, 40.000467],
          [-96.089781, 40.000519],
          [-96.081395, 40.000603],
          [-96.051691, 40.000727],
          [-96.02409, 40.000719],
          [-96.01068, 40.000638],
          [-96.010678, 40.000638],
          [-95.958139, 40.000521],
          [-95.882524, 40.00047],
          [-95.788111, 40.000452],
          [-95.788315, 39.883502],
          [-95.788941, 39.653001],
          [-95.789101, 39.565916],
          [-96.035952, 39.5661],
          [-96.037537, 39.566159],
          [-96.238969, 39.56622],
          [-96.239583, 39.827119],
          [-96.239208, 40.000691]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20141",
      state: "20",
      county: "141",
      name: "Osborne County",
      censusArea: 892.504,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.0375, 39.133121],
          [-99.047687, 39.133014],
          [-99.044398, 39.568035],
          [-98.505266, 39.567603],
          [-98.503592, 39.567515],
          [-98.487384, 39.567492],
          [-98.490149, 39.21978],
          [-98.490161, 39.218033],
          [-98.490063, 39.215379],
          [-98.489864, 39.21465],
          [-98.489893, 39.213515],
          [-98.490011, 39.213048],
          [-98.489997, 39.132697],
          [-99.0375, 39.133121]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20143",
      state: "20",
      county: "143",
      name: "Ottawa County",
      censusArea: 720.733,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.929097, 39.306397],
          [-97.926096, 39.306517],
          [-97.369839, 39.306011],
          [-97.371154, 39.131961],
          [-97.371614, 38.957852],
          [-97.92856, 38.958395],
          [-97.929746, 39.219273],
          [-97.931614, 39.277094],
          [-97.929097, 39.306397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20157",
      state: "20",
      county: "157",
      name: "Republic County",
      censusArea: 717.371,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.821496, 40.002002],
          [-97.819426, 40.001958],
          [-97.777155, 40.002167],
          [-97.770776, 40.001977],
          [-97.769204, 40.001995],
          [-97.767746, 40.001994],
          [-97.515308, 40.001901],
          [-97.511381, 40.001899],
          [-97.510264, 40.001835],
          [-97.463285, 40.002047],
          [-97.444662, 40.001958],
          [-97.425443, 40.002048],
          [-97.417826, 40.002024],
          [-97.415833, 40.002001],
          [-97.369199, 40.00206],
          [-97.36867, 39.654043],
          [-97.931482, 39.653767],
          [-97.931826, 40.00205],
          [-97.931811, 40.00205],
          [-97.876261, 40.002102],
          [-97.85745, 40.002065],
          [-97.838379, 40.00191],
          [-97.821598, 40.002004],
          [-97.821496, 40.002002]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20165",
      state: "20",
      county: "165",
      name: "Rush County",
      censusArea: 717.763,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.585087, 38.696537],
          [-99.042626, 38.696807],
          [-99.032971, 38.696759],
          [-99.032408, 38.348334],
          [-99.584794, 38.349386],
          [-99.585087, 38.696537]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20167",
      state: "20",
      county: "167",
      name: "Russell County",
      censusArea: 886.26,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.032971, 38.696759],
          [-99.042626, 38.696807],
          [-99.0375, 39.133121],
          [-98.489997, 39.132697],
          [-98.483748, 39.132671],
          [-98.484861, 38.870791],
          [-98.486108, 38.696878],
          [-99.032971, 38.696759]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20171",
      state: "20",
      county: "171",
      name: "Scott County",
      censusArea: 717.544,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.125438, 38.264509],
          [-101.128379, 38.700603],
          [-100.818698, 38.699861],
          [-100.688006, 38.700021],
          [-100.684637, 38.271122],
          [-100.687836, 38.26415],
          [-101.103268, 38.264557],
          [-101.125438, 38.264509]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20177",
      state: "20",
      county: "177",
      name: "Shawnee County",
      censusArea: 544.016,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.500254, 39.056656],
          [-95.500724, 38.869815],
          [-95.946587, 38.869973],
          [-95.946754, 39.098685],
          [-96.03906, 39.126527],
          [-96.035573, 39.21652],
          [-95.589472, 39.216081],
          [-95.596759, 39.064672],
          [-95.500254, 39.056656]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22057",
      state: "22",
      county: "057",
      name: "Lafourche County",
      lsad: "Parish",
      censusArea: 1068.214,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.029466, 29.432015],
          [-90.032298, 29.427005],
          [-90.031536, 29.412545],
          [-90.033295, 29.393274],
          [-90.029468, 29.388136],
          [-90.029614, 29.386658],
          [-90.029967, 29.383087],
          [-90.030761, 29.375043],
          [-90.030764, 29.375008],
          [-90.030855, 29.374876],
          [-90.033604, 29.370851],
          [-90.035415, 29.368201],
          [-90.036374, 29.363661],
          [-90.032842, 29.348624],
          [-90.031815, 29.344251],
          [-90.034275, 29.322661],
          [-90.028536, 29.307083],
          [-90.013778, 29.30271],
          [-90.009678, 29.294785],
          [-90.016288, 29.284257],
          [-90.019517, 29.282213],
          [-90.032088, 29.280027],
          [-90.043293, 29.282487],
          [-90.057094, 29.281331],
          [-90.061057, 29.276748],
          [-90.059691, 29.272648],
          [-90.060511, 29.267729],
          [-90.070622, 29.262537],
          [-90.086747, 29.259257],
          [-90.091119, 29.261443],
          [-90.097678, 29.26199],
          [-90.101231, 29.259804],
          [-90.096038, 29.240673],
          [-90.073355, 29.227282],
          [-90.073355, 29.210611],
          [-90.070622, 29.208698],
          [-90.06361, 29.209474],
          [-90.04291, 29.211765],
          [-90.019772, 29.231903],
          [-90.005718, 29.240627],
          [-89.969981, 29.255753],
          [-89.965667, 29.259126],
          [-89.959509, 29.267677],
          [-89.951175, 29.266124],
          [-89.949925, 29.263154],
          [-89.950756, 29.260801],
          [-89.95646, 29.253744],
          [-90.022029, 29.216065],
          [-90.058512, 29.183687],
          [-90.079276, 29.16997],
          [-90.088684, 29.162574],
          [-90.104162, 29.150407],
          [-90.174273, 29.105301],
          [-90.223587, 29.085075],
          [-90.231984, 29.08773],
          [-90.245283, 29.085824],
          [-90.343293, 29.057062],
          [-90.348768, 29.057817],
          [-90.349891, 29.063681],
          [-90.325514, 29.075138],
          [-90.304129, 29.077332],
          [-90.29293, 29.078761],
          [-90.282983, 29.082326],
          [-90.26629, 29.089421],
          [-90.258145, 29.091627],
          [-90.253141, 29.093772],
          [-90.249806, 29.100919],
          [-90.250044, 29.108067],
          [-90.243849, 29.11045],
          [-90.234235, 29.110268],
          [-90.234405, 29.128824],
          [-90.243435, 29.136311],
          [-90.248629, 29.13837],
          [-90.26901, 29.139242],
          [-90.280516, 29.142521],
          [-90.27832, 29.150691],
          [-90.297, 29.171317],
          [-90.302846, 29.175098],
          [-90.302948, 29.187948],
          [-90.300885, 29.196171],
          [-90.293183, 29.199789],
          [-90.2828, 29.192545],
          [-90.275851, 29.193668],
          [-90.271251, 29.204639],
          [-90.286621, 29.225694],
          [-90.300304, 29.231241],
          [-90.311663, 29.237954],
          [-90.311523, 29.256374],
          [-90.316093, 29.264777],
          [-90.332796, 29.276956],
          [-90.367166, 29.274128],
          [-90.368154, 29.270736],
          [-90.367012, 29.264956],
          [-90.372565, 29.258923],
          [-90.387924, 29.252786],
          [-90.383857, 29.235606],
          [-90.399465, 29.201046],
          [-90.408578, 29.196421],
          [-90.409416, 29.196135],
          [-90.376666, 29.302883],
          [-90.384285, 29.365755],
          [-90.574611, 29.559141],
          [-90.766849, 29.695477],
          [-90.807692, 29.775908],
          [-90.880194, 29.716809],
          [-91.006743, 29.714771],
          [-90.885589, 29.905353],
          [-90.785659, 29.921592],
          [-90.656312, 29.889246],
          [-90.541943, 29.891522],
          [-90.475418, 29.81221],
          [-90.371978, 29.759239],
          [-90.346191, 29.693302],
          [-90.228157, 29.692028],
          [-90.155807, 29.67753],
          [-90.186172, 29.563341],
          [-90.137504, 29.477163],
          [-90.029466, 29.432015]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22061",
      state: "22",
      county: "061",
      name: "Lincoln County",
      lsad: "Parish",
      censusArea: 471.742,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.415071, 32.582845],
          [-92.41535, 32.495486],
          [-92.622335, 32.496406],
          [-92.621678, 32.452999],
          [-92.777197, 32.453455],
          [-92.879268, 32.454118],
          [-92.880805, 32.585277],
          [-92.828535, 32.758814],
          [-92.725439, 32.75954],
          [-92.571295, 32.748579],
          [-92.518356, 32.67072],
          [-92.41505, 32.670056],
          [-92.415071, 32.582845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22063",
      state: "22",
      county: "063",
      name: "Livingston County",
      lsad: "Parish",
      censusArea: 648.169,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.774577, 30.249808],
          [-90.891728, 30.345244],
          [-90.991529, 30.461818],
          [-90.980939, 30.589988],
          [-90.910701, 30.649385],
          [-90.567165, 30.650023],
          [-90.546118, 30.430283],
          [-90.401254, 30.285534],
          [-90.497899, 30.255946],
          [-90.558638, 30.193737],
          [-90.632811, 30.221409],
          [-90.689821, 30.176589],
          [-90.774577, 30.249808]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22069",
      state: "22",
      county: "069",
      name: "Natchitoches County",
      lsad: "Parish",
      censusArea: 1252.25,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.441171, 31.845233],
          [-93.44057, 31.91373],
          [-93.355452, 31.932222],
          [-93.239501, 31.90352],
          [-93.23847, 31.972781],
          [-93.122686, 31.972728],
          [-93.187162, 32.148412],
          [-92.93908, 32.147942],
          [-92.872174, 31.945856],
          [-92.9668, 31.781299],
          [-92.973529, 31.708922],
          [-92.908313, 31.625169],
          [-92.806206, 31.598548],
          [-92.718983, 31.517594],
          [-92.913019, 31.368236],
          [-92.981464, 31.346644],
          [-93.236104, 31.364507],
          [-93.236662, 31.538339],
          [-93.337456, 31.53811],
          [-93.338972, 31.714259],
          [-93.441743, 31.714485],
          [-93.441171, 31.845233]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22075",
      state: "22",
      county: "075",
      name: "Plaquemines County",
      lsad: "Parish",
      censusArea: 779.911,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.911037, 29.868543],
          [-89.86972, 29.795451],
          [-89.721045, 29.757652],
          [-89.628181, 29.680682],
          [-89.5186, 29.649117],
          [-89.486709, 29.621003],
          [-89.486931, 29.620447],
          [-89.504738, 29.631508],
          [-89.523018, 29.639427],
          [-89.535202, 29.648567],
          [-89.583336, 29.652834],
          [-89.608925, 29.657707],
          [-89.621109, 29.657101],
          [-89.62355, 29.662584],
          [-89.632698, 29.671724],
          [-89.644272, 29.675381],
          [-89.64975, 29.672941],
          [-89.641228, 29.647961],
          [-89.641228, 29.635773],
          [-89.647324, 29.625414],
          [-89.657677, 29.624195],
          [-89.674736, 29.626633],
          [-89.684486, 29.624804],
          [-89.688141, 29.615055],
          [-89.684486, 29.602867],
          [-89.671082, 29.588243],
          [-89.668648, 29.580322],
          [-89.684486, 29.563263],
          [-89.684486, 29.551073],
          [-89.681092, 29.534487],
          [-89.69623, 29.525004],
          [-89.699698, 29.523423],
          [-89.700845, 29.520785],
          [-89.700501, 29.515967],
          [-89.693877, 29.508559],
          [-89.665813, 29.49002],
          [-89.644039, 29.492343],
          [-89.63533, 29.489294],
          [-89.617558, 29.468298],
          [-89.596533, 29.456303],
          [-89.592474, 29.449822],
          [-89.589536, 29.437662],
          [-89.577096, 29.433692],
          [-89.574635, 29.435734],
          [-89.574653, 29.4411],
          [-89.548686, 29.465723],
          [-89.528429, 29.454702],
          [-89.53215, 29.434567],
          [-89.531943, 29.425679],
          [-89.518368, 29.40023],
          [-89.508551, 29.386168],
          [-89.505038, 29.38604],
          [-89.487308, 29.393346],
          [-89.484354, 29.397471],
          [-89.482318, 29.406222],
          [-89.47714, 29.411241],
          [-89.470142, 29.401471],
          [-89.457303, 29.393148],
          [-89.42238, 29.390628],
          [-89.380001, 29.391785],
          [-89.373109, 29.387175],
          [-89.355528, 29.381569],
          [-89.340304, 29.381412],
          [-89.336589, 29.378228],
          [-89.347615, 29.365],
          [-89.350694, 29.349544],
          [-89.32317, 29.343982],
          [-89.303766, 29.357455],
          [-89.283028, 29.356467],
          [-89.272543, 29.351195],
          [-89.2653, 29.345352],
          [-89.257852, 29.336872],
          [-89.253545, 29.322802],
          [-89.24087, 29.310081],
          [-89.224192, 29.313792],
          [-89.223444, 29.318066],
          [-89.219734, 29.324412],
          [-89.204703, 29.338674],
          [-89.200389, 29.344418],
          [-89.200599, 29.347672],
          [-89.189354, 29.345061],
          [-89.179547, 29.339608],
          [-89.177351, 29.33521],
          [-89.178221, 29.32697],
          [-89.165015, 29.303039],
          [-89.157593, 29.296691],
          [-89.140275, 29.291085],
          [-89.134337, 29.27934],
          [-89.136979, 29.275239],
          [-89.129688, 29.265632],
          [-89.100106, 29.25022],
          [-89.096173, 29.24293],
          [-89.095544, 29.238028],
          [-89.098389, 29.232963],
          [-89.105833, 29.231608],
          [-89.106244, 29.215912],
          [-89.10065, 29.206314],
          [-89.090724, 29.199992],
          [-89.068265, 29.204166],
          [-89.067371, 29.208636],
          [-89.029103, 29.220956],
          [-89.02185, 29.218162],
          [-89.015192, 29.211561],
          [-89.000674, 29.180091],
          [-89.00529, 29.164949],
          [-89.013254, 29.16328],
          [-89.018344, 29.165046],
          [-89.024269, 29.170043],
          [-89.043919, 29.162528],
          [-89.047233, 29.157833],
          [-89.03873, 29.14238],
          [-89.032004, 29.144747],
          [-89.024149, 29.137298],
          [-89.023942, 29.1337],
          [-89.026031, 29.130126],
          [-89.051953, 29.106554],
          [-89.055475, 29.084167],
          [-89.062335, 29.070234],
          [-89.09126, 29.066931],
          [-89.098068, 29.067984],
          [-89.105009, 29.073641],
          [-89.121542, 29.069074],
          [-89.143453, 29.047591],
          [-89.156339, 29.028782],
          [-89.162, 29.01586],
          [-89.162326, 29.011713],
          [-89.164788, 29.008703],
          [-89.16985, 29.008703],
          [-89.175732, 29.012123],
          [-89.186061, 29.017993],
          [-89.18215, 29.025486],
          [-89.189893, 29.032635],
          [-89.197871, 29.029701],
          [-89.202563, 29.031603],
          [-89.211144, 29.040813],
          [-89.216101, 29.056371],
          [-89.215531, 29.06141],
          [-89.217201, 29.067275],
          [-89.225865, 29.07866],
          [-89.23631, 29.084605],
          [-89.254726, 29.083261],
          [-89.257283, 29.081086],
          [-89.256869, 29.0738],
          [-89.25364, 29.064954],
          [-89.259354, 29.058358],
          [-89.283215, 29.053325],
          [-89.29109, 29.053097],
          [-89.304888, 29.046379],
          [-89.315389, 29.039081],
          [-89.318102, 29.035342],
          [-89.315182, 29.032662],
          [-89.32485, 29.013805],
          [-89.335228, 29.015003],
          [-89.338249, 29.012935],
          [-89.383814, 28.947434],
          [-89.41148, 28.925011],
          [-89.419865, 28.929709],
          [-89.412388, 28.957504],
          [-89.408157, 28.965341],
          [-89.398104, 28.977016],
          [-89.382106, 28.981525],
          [-89.375049, 28.985368],
          [-89.334735, 29.040335],
          [-89.339828, 29.052221],
          [-89.354798, 29.072543],
          [-89.374522, 29.084174],
          [-89.405654, 29.086936],
          [-89.411154, 29.105838],
          [-89.409371, 29.127855],
          [-89.417718, 29.13869],
          [-89.428965, 29.14451],
          [-89.432932, 29.149023],
          [-89.447472, 29.178576],
          [-89.455829, 29.190991],
          [-89.47231, 29.20755],
          [-89.482844, 29.215053],
          [-89.5366, 29.236212],
          [-89.606651, 29.252023],
          [-89.671781, 29.289028],
          [-89.697258, 29.296679],
          [-89.726162, 29.304026],
          [-89.782149, 29.311132],
          [-89.819859, 29.310241],
          [-89.850305, 29.311768],
          [-89.855109, 29.334997],
          [-89.853699, 29.34064],
          [-89.847124, 29.349186],
          [-89.835, 29.359043],
          [-89.820824, 29.377486],
          [-89.816916, 29.384385],
          [-89.816155, 29.393518],
          [-89.816916, 29.398845],
          [-89.819199, 29.404173],
          [-89.822243, 29.4095],
          [-89.826049, 29.415589],
          [-89.835392, 29.418538],
          [-89.843553, 29.421677],
          [-89.845075, 29.434615],
          [-89.836773, 29.45404],
          [-89.833659, 29.456686],
          [-89.833659, 29.459731],
          [-89.832898, 29.463536],
          [-89.833659, 29.467341],
          [-89.83442, 29.470386],
          [-89.840509, 29.47343],
          [-89.849642, 29.477996],
          [-89.86258, 29.476474],
          [-89.876224, 29.472168],
          [-89.902179, 29.460011],
          [-89.918999, 29.444254],
          [-89.932598, 29.429288],
          [-89.95543, 29.428527],
          [-89.96195, 29.432874],
          [-89.980018, 29.607582],
          [-90.075683, 29.77185],
          [-90.008056, 29.895528],
          [-89.911037, 29.868543]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22079",
      state: "22",
      county: "079",
      name: "Rapides County",
      lsad: "Parish",
      censusArea: 1317.962,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.597741, 30.896101],
          [-92.823932, 30.89],
          [-92.829651, 31.252281],
          [-92.879756, 31.317373],
          [-92.981464, 31.346644],
          [-92.913019, 31.368236],
          [-92.718983, 31.517594],
          [-92.694666, 31.40724],
          [-92.631944, 31.390478],
          [-92.196131, 31.477877],
          [-92.086718, 31.335374],
          [-92.23323, 31.321545],
          [-92.234258, 31.131466],
          [-92.208299, 30.96243],
          [-92.280738, 30.965071],
          [-92.382742, 31.001431],
          [-92.48033, 30.952271],
          [-92.508854, 30.896003],
          [-92.597741, 30.896101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22081",
      state: "22",
      county: "081",
      name: "Red River County",
      lsad: "Parish",
      censusArea: 389.09,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.61469, 32.237526],
          [-93.471249, 32.237186],
          [-93.463338, 32.237159],
          [-93.457933, 32.23597],
          [-93.42819, 32.235897],
          [-93.213988, 32.236065],
          [-93.187162, 32.148412],
          [-93.122686, 31.972728],
          [-93.23847, 31.972781],
          [-93.239501, 31.90352],
          [-93.355452, 31.932222],
          [-93.441506, 31.98918],
          [-93.541353, 32.10588],
          [-93.535963, 32.193126],
          [-93.61469, 32.237526]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22085",
      state: "22",
      county: "085",
      name: "Sabine County",
      lsad: "Parish",
      censusArea: 866.659,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.818582, 31.554826],
          [-93.834923, 31.58621],
          [-93.834924, 31.586211],
          [-93.82629, 31.614903],
          [-93.817059, 31.671694],
          [-93.812477, 31.715246],
          [-93.840029, 31.800596],
          [-93.878225, 31.844276],
          [-93.441171, 31.845233],
          [-93.441743, 31.714485],
          [-93.338972, 31.714259],
          [-93.337456, 31.53811],
          [-93.236662, 31.538339],
          [-93.236104, 31.364507],
          [-93.390495, 31.364147],
          [-93.442554, 31.276716],
          [-93.534919, 31.276579],
          [-93.55254, 31.185605],
          [-93.552649, 31.185575],
          [-93.579215, 31.167422],
          [-93.588503, 31.165581],
          [-93.598828, 31.174679],
          [-93.599705, 31.176456],
          [-93.602315, 31.181742],
          [-93.607243, 31.204806],
          [-93.608158, 31.227835],
          [-93.614402, 31.260869],
          [-93.620829, 31.271299],
          [-93.644407, 31.27711],
          [-93.671676, 31.299586],
          [-93.640805, 31.372546],
          [-93.670182, 31.387184],
          [-93.695866, 31.409392],
          [-93.729613, 31.487922],
          [-93.798087, 31.534044],
          [-93.818582, 31.554826]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21111",
      state: "21",
      county: "111",
      name: "Jefferson County",
      censusArea: 380.416,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.623735, 38.079648],
          [-85.703163, 38.086499],
          [-85.712578, 38.087311],
          [-85.842259, 38.051485],
          [-85.938321, 37.998756],
          [-85.945044, 38.002426],
          [-85.947952, 38.007083],
          [-85.941403, 38.009831],
          [-85.930235, 38.018311],
          [-85.922395, 38.028679],
          [-85.911607, 38.06689],
          [-85.906163, 38.08617],
          [-85.904564, 38.10027],
          [-85.905164, 38.11107],
          [-85.909464, 38.14007],
          [-85.908764, 38.161169],
          [-85.89955, 38.180343],
          [-85.897664, 38.184269],
          [-85.829364, 38.276769],
          [-85.823764, 38.280569],
          [-85.816164, 38.282969],
          [-85.791575, 38.288566],
          [-85.791563, 38.288569],
          [-85.780963, 38.288469],
          [-85.773363, 38.286169],
          [-85.765963, 38.280469],
          [-85.766563, 38.27767],
          [-85.761062, 38.27257],
          [-85.750962, 38.26787],
          [-85.744862, 38.26717],
          [-85.683561, 38.295469],
          [-85.675017, 38.301317],
          [-85.653641, 38.327108],
          [-85.638777, 38.361443],
          [-85.638044, 38.38027],
          [-85.468206, 38.285309],
          [-85.404899, 38.26373],
          [-85.424054, 38.147438],
          [-85.42883, 38.118383],
          [-85.55727, 38.073841],
          [-85.623735, 38.079648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21113",
      state: "21",
      county: "113",
      name: "Jessamine County",
      censusArea: 172.116,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.716925, 37.815389],
          [-84.723114, 37.820503],
          [-84.723423, 37.832947],
          [-84.716905, 37.840923],
          [-84.695498, 37.845415],
          [-84.689095, 37.854796],
          [-84.709918, 37.861952],
          [-84.660135, 38.003745],
          [-84.479277, 37.940076],
          [-84.43572, 37.847202],
          [-84.525643, 37.769497],
          [-84.57031, 37.728713],
          [-84.675889, 37.826242],
          [-84.716925, 37.815389]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21123",
      state: "21",
      county: "123",
      name: "Larue County",
      censusArea: 261.519,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.52129, 37.554343],
          [-85.46625, 37.465595],
          [-85.583497, 37.469917],
          [-85.657332, 37.421996],
          [-85.892804, 37.440308],
          [-85.876006, 37.543259],
          [-85.680488, 37.732284],
          [-85.592088, 37.693479],
          [-85.623323, 37.551604],
          [-85.52129, 37.554343]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21129",
      state: "21",
      county: "129",
      name: "Lee County",
      censusArea: 208.857,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.579186, 37.506249],
          [-83.630412, 37.53746],
          [-83.82934, 37.486821],
          [-83.885262, 37.517688],
          [-83.888697, 37.522189],
          [-83.8931, 37.526583],
          [-83.893514, 37.531119],
          [-83.900265, 37.535795],
          [-83.90557, 37.542856],
          [-83.824102, 37.662746],
          [-83.7221, 37.71642],
          [-83.71221, 37.720135],
          [-83.707974, 37.716463],
          [-83.522308, 37.638516],
          [-83.579186, 37.506249]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21135",
      state: "21",
      county: "135",
      name: "Lewis County",
      censusArea: 482.836,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.642994, 38.643273],
          [-83.636208, 38.670584],
          [-83.626922, 38.679387],
          [-83.615736, 38.684145],
          [-83.574754, 38.692671],
          [-83.533339, 38.702105],
          [-83.520953, 38.703045],
          [-83.512571, 38.701716],
          [-83.369077, 38.659334],
          [-83.333347, 38.641562],
          [-83.324157, 38.633326],
          [-83.320531, 38.622713],
          [-83.2821, 38.602987],
          [-83.265769, 38.605278],
          [-83.156926, 38.620547],
          [-83.142836, 38.625076],
          [-83.135046, 38.631719],
          [-83.127652, 38.642451],
          [-83.030889, 38.72556],
          [-83.071005, 38.5952],
          [-83.166419, 38.503965],
          [-83.237181, 38.426246],
          [-83.23068, 38.339286],
          [-83.341347, 38.319521],
          [-83.452848, 38.381799],
          [-83.579966, 38.429935],
          [-83.641852, 38.525381],
          [-83.646701, 38.637489],
          [-83.642994, 38.643273]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21139",
      state: "21",
      county: "139",
      name: "Livingston County",
      censusArea: 313.127,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.482957, 37.022566],
          [-88.564152, 37.07844],
          [-88.560032, 37.07601],
          [-88.545403, 37.070003],
          [-88.531576, 37.067192],
          [-88.514356, 37.065231],
          [-88.490297, 37.066669],
          [-88.482856, 37.067114],
          [-88.458948, 37.073796],
          [-88.444605, 37.098601],
          [-88.424403, 37.152428],
          [-88.447764, 37.203527],
          [-88.458763, 37.213536],
          [-88.471753, 37.220155],
          [-88.509328, 37.26213],
          [-88.515939, 37.284043],
          [-88.514661, 37.290948],
          [-88.476592, 37.386875],
          [-88.470224, 37.396255],
          [-88.456, 37.408482],
          [-88.439333, 37.416416],
          [-88.414895, 37.423461],
          [-88.408808, 37.425216],
          [-88.358436, 37.40486],
          [-88.21898, 37.273145],
          [-88.193519, 37.147256],
          [-88.231145, 37.081575],
          [-88.209221, 36.999976],
          [-88.240371, 36.981911],
          [-88.365595, 37.064183],
          [-88.482957, 37.022566]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21145",
      state: "21",
      county: "145",
      name: "Mc Cracken County",
      censusArea: 248.744,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.812897, 36.946864],
          [-88.815952, 36.9541],
          [-88.933399, 37.225007],
          [-88.933385, 37.225005],
          [-88.942111, 37.228811],
          [-88.928021, 37.226281],
          [-88.916934, 37.224291],
          [-88.809206, 37.189235],
          [-88.625889, 37.119458],
          [-88.589207, 37.099655],
          [-88.576718, 37.085852],
          [-88.564152, 37.07844],
          [-88.482957, 37.022566],
          [-88.485254, 37.001109],
          [-88.484103, 36.942096],
          [-88.812897, 36.946864]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21153",
      state: "21",
      county: "153",
      name: "Magoffin County",
      censusArea: 308.444,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.92546, 37.486525],
          [-82.934353, 37.493991],
          [-82.941298, 37.489795],
          [-82.941667, 37.496323],
          [-82.945444, 37.49739],
          [-82.944205, 37.501434],
          [-82.948544, 37.503167],
          [-83.089796, 37.632167],
          [-83.134949, 37.606812],
          [-83.248692, 37.669616],
          [-83.258467, 37.679926],
          [-83.243603, 37.699155],
          [-83.262172, 37.712657],
          [-83.269662, 37.766012],
          [-83.113277, 37.864121],
          [-83.004649, 37.859247],
          [-82.940763, 37.716338],
          [-82.878723, 37.540134],
          [-82.92546, 37.486525]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21159",
      state: "21",
      county: "159",
      name: "Martin County",
      censusArea: 229.605,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.497358, 37.945338],
          [-82.500386, 37.936518],
          [-82.474666, 37.910388],
          [-82.417679, 37.870658],
          [-82.400234, 37.851833],
          [-82.329867, 37.775897],
          [-82.319023, 37.758892],
          [-82.326404, 37.740815],
          [-82.456134, 37.668795],
          [-82.563561, 37.684844],
          [-82.64075, 37.720182],
          [-82.670887, 37.851823],
          [-82.612393, 37.879272],
          [-82.582085, 37.952314],
          [-82.497358, 37.945338]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21167",
      state: "21",
      county: "167",
      name: "Mercer County",
      censusArea: 248.797,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.709918, 37.861952],
          [-84.689095, 37.854796],
          [-84.695498, 37.845415],
          [-84.716905, 37.840923],
          [-84.723423, 37.832947],
          [-84.723114, 37.820503],
          [-84.716925, 37.815389],
          [-84.696741, 37.72283],
          [-84.744888, 37.713075],
          [-85.025021, 37.678854],
          [-85.000529, 37.854642],
          [-85.030528, 37.891538],
          [-84.93778, 37.952826],
          [-84.796327, 37.970088],
          [-84.818833, 37.945838],
          [-84.709918, 37.861952]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21171",
      state: "21",
      county: "171",
      name: "Monroe County",
      censusArea: 329.373,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.788613, 36.621845],
          [-85.788645, 36.621846],
          [-85.976421, 36.6284],
          [-85.97691, 36.722722],
          [-85.739255, 36.841476],
          [-85.596214, 36.818039],
          [-85.436418, 36.618194],
          [-85.471338, 36.61638],
          [-85.508605, 36.61502],
          [-85.551483, 36.615727],
          [-85.552017, 36.615782],
          [-85.788613, 36.621845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21173",
      state: "21",
      county: "173",
      name: "Montgomery County",
      censusArea: 197.366,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.977924, 38.192118],
          [-83.881353, 38.136517],
          [-83.75943, 37.998731],
          [-83.768783, 37.918371],
          [-83.96631, 37.930768],
          [-84.051053, 38.070499],
          [-84.080527, 38.115118],
          [-83.977924, 38.192118]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21179",
      state: "21",
      county: "179",
      name: "Nelson County",
      censusArea: 417.512,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.152797, 37.897661],
          [-85.299042, 37.831214],
          [-85.401784, 37.730658],
          [-85.450493, 37.685948],
          [-85.52129, 37.554343],
          [-85.623323, 37.551604],
          [-85.592088, 37.693479],
          [-85.680488, 37.732284],
          [-85.737777, 37.81179],
          [-85.65131, 37.828989],
          [-85.489202, 37.990654],
          [-85.399871, 37.939956],
          [-85.167834, 37.971809],
          [-85.152797, 37.897661]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19053",
      state: "19",
      county: "053",
      name: "Decatur County",
      censusArea: 531.881,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.556899, 40.580235],
          [-93.558938, 40.580189],
          [-93.560798, 40.580304],
          [-93.56524, 40.580143],
          [-93.56581, 40.580075],
          [-93.566189, 40.580117],
          [-93.597352, 40.579496],
          [-93.656211, 40.578352],
          [-93.659272, 40.57833],
          [-93.661913, 40.578354],
          [-93.668845, 40.578241],
          [-93.677099, 40.578127],
          [-93.690333, 40.577875],
          [-93.722443, 40.577641],
          [-93.728355, 40.577547],
          [-93.737259, 40.577542],
          [-93.742759, 40.577518],
          [-93.750223, 40.57772],
          [-93.770231, 40.577615],
          [-93.774344, 40.577584],
          [-93.815485, 40.577278],
          [-93.818725, 40.577086],
          [-93.84093, 40.576791],
          [-93.853656, 40.576606],
          [-93.898327, 40.576011],
          [-93.899317, 40.575942],
          [-93.900877, 40.575874],
          [-93.913961, 40.575672],
          [-93.935687, 40.57533],
          [-93.936317, 40.575284],
          [-93.937097, 40.575421],
          [-93.938627, 40.575284],
          [-93.939857, 40.575192],
          [-93.963863, 40.574754],
          [-93.976766, 40.574635],
          [-94.015492, 40.573914],
          [-94.014803, 40.897031],
          [-93.55654, 40.898295],
          [-93.556899, 40.580235]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18003",
      state: "18",
      county: "003",
      name: "Allen County",
      censusArea: 657.308,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.803582, 41.271273],
          [-84.803581, 41.271079],
          [-84.80358, 41.270942],
          [-84.803492, 41.252562],
          [-84.803492, 41.252531],
          [-84.803472, 41.173889],
          [-84.803594, 41.173203],
          [-84.803413, 41.164649],
          [-84.803378, 41.096867],
          [-84.803374, 41.089302],
          [-84.803313, 40.989394],
          [-84.803313, 40.989209],
          [-84.802936, 40.922568],
          [-85.073861, 40.917823],
          [-85.167063, 40.916585],
          [-85.33603, 40.917082],
          [-85.335374, 41.002047],
          [-85.335643, 41.00525],
          [-85.338552, 41.17912],
          [-85.307781, 41.264158],
          [-85.192094, 41.264209],
          [-84.806471, 41.270904],
          [-84.803582, 41.271273]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13181",
      state: "13",
      county: "181",
      name: "Lincoln County",
      censusArea: 210.38,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.425328, 33.650629],
          [-82.430915, 33.655076],
          [-82.435952, 33.652046],
          [-82.451692, 33.649591],
          [-82.458237, 33.637471],
          [-82.46988, 33.638617],
          [-82.476969, 33.644281],
          [-82.479518, 33.639535],
          [-82.593527, 33.827322],
          [-82.645451, 33.984195],
          [-82.564483, 33.955744],
          [-82.429164, 33.865844],
          [-82.422803, 33.863754],
          [-82.408354, 33.86632],
          [-82.403881, 33.865477],
          [-82.32448, 33.820033],
          [-82.301457, 33.801864],
          [-82.247472, 33.752591],
          [-82.218649, 33.686299],
          [-82.306414, 33.700644],
          [-82.425328, 33.650629]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19061",
      state: "19",
      county: "061",
      name: "Dubuque County",
      censusArea: 608.305,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.896961, 42.674407],
          [-90.84391, 42.663071],
          [-90.769495, 42.651443],
          [-90.720209, 42.640758],
          [-90.709204, 42.636078],
          [-90.702671, 42.630756],
          [-90.645627, 42.5441],
          [-90.636727, 42.518702],
          [-90.636927, 42.513202],
          [-90.640927, 42.508302],
          [-90.655927, 42.491703],
          [-90.656527, 42.489203],
          [-90.656327, 42.483603],
          [-90.654027, 42.478503],
          [-90.646727, 42.471904],
          [-90.624328, 42.458904],
          [-90.559451, 42.430695],
          [-90.477279, 42.383794],
          [-90.474834, 42.381473],
          [-90.665029, 42.382155],
          [-90.665551, 42.294699],
          [-90.89802, 42.295236],
          [-91.130079, 42.295761],
          [-91.132766, 42.645844],
          [-90.897415, 42.645714],
          [-90.896961, 42.674407]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19069",
      state: "19",
      county: "069",
      name: "Franklin County",
      censusArea: 581.972,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.498617, 42.908512],
          [-93.025144, 42.907549],
          [-93.027, 42.55681],
          [-93.499485, 42.5577],
          [-93.498617, 42.908512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19075",
      state: "19",
      county: "075",
      name: "Grundy County",
      censusArea: 501.858,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.53351, 42.297001],
          [-92.76809, 42.29668],
          [-92.767463, 42.21014],
          [-93.001674, 42.209267],
          [-93.002902, 42.470638],
          [-93.027, 42.55681],
          [-92.5543, 42.555854],
          [-92.53351, 42.297001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19077",
      state: "19",
      county: "077",
      name: "Guthrie County",
      censusArea: 590.62,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.700629, 41.504148],
          [-94.744204, 41.602185],
          [-94.744876, 41.862394],
          [-94.676942, 41.862994],
          [-94.67542, 41.862895],
          [-94.674259, 41.862657],
          [-94.673119, 41.86265],
          [-94.672368, 41.86289],
          [-94.671695, 41.862828],
          [-94.667977, 41.862682],
          [-94.628724, 41.862763],
          [-94.28034, 41.862999],
          [-94.280057, 41.601312],
          [-94.241593, 41.503679],
          [-94.700629, 41.504148]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19089",
      state: "19",
      county: "089",
      name: "Howard County",
      censusArea: 473.248,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.553128, 43.5003],
          [-92.464505, 43.500345],
          [-92.448948, 43.50042],
          [-92.408832, 43.500614],
          [-92.40613, 43.500476],
          [-92.388298, 43.500483],
          [-92.368908, 43.500454],
          [-92.279084, 43.500436],
          [-92.277425, 43.500466],
          [-92.198788, 43.500527],
          [-92.178863, 43.500713],
          [-92.103886, 43.500735],
          [-92.08997, 43.500684],
          [-92.079954, 43.500647],
          [-92.079802, 43.500647],
          [-92.080976, 43.212947],
          [-92.554381, 43.212813],
          [-92.553128, 43.5003]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19093",
      state: "19",
      county: "093",
      name: "Ida County",
      censusArea: 431.508,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.323497, 42.210932],
          [-95.670822, 42.211408],
          [-95.669265, 42.474645],
          [-95.741821, 42.474865],
          [-95.741611, 42.561285],
          [-95.38801, 42.561742],
          [-95.387445, 42.474796],
          [-95.322357, 42.474737],
          [-95.323497, 42.210932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19101",
      state: "19",
      county: "101",
      name: "Jefferson County",
      censusArea: 435.51,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.945571, 41.163578],
          [-91.83906, 41.161515],
          [-91.715893, 41.162822],
          [-91.718268, 40.900805],
          [-92.179072, 40.89972],
          [-92.179974, 41.162662],
          [-91.945571, 41.163578]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19103",
      state: "19",
      county: "103",
      name: "Johnson County",
      censusArea: 614.04,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.829231, 41.511457],
          [-91.831379, 41.861851],
          [-91.366079, 41.860068],
          [-91.366448, 41.598373],
          [-91.368521, 41.423178],
          [-91.483995, 41.423848],
          [-91.52248, 41.511035],
          [-91.829231, 41.511457]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19107",
      state: "19",
      county: "107",
      name: "Keokuk County",
      censusArea: 579.179,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.411995, 41.509548],
          [-92.297494, 41.50979],
          [-91.946043, 41.510749],
          [-91.945571, 41.163578],
          [-92.179974, 41.162662],
          [-92.410233, 41.161942],
          [-92.411995, 41.509548]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19111",
      state: "19",
      county: "111",
      name: "Lee County",
      censusArea: 517.518,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.409324, 40.812682],
          [-91.182551, 40.7081],
          [-91.112258, 40.696218],
          [-91.122421, 40.670675],
          [-91.138055, 40.660893],
          [-91.185415, 40.638052],
          [-91.18698, 40.637297],
          [-91.197906, 40.636107],
          [-91.218437, 40.638437],
          [-91.253074, 40.637962],
          [-91.306524, 40.626231],
          [-91.339719, 40.613488],
          [-91.348733, 40.609695],
          [-91.359873, 40.601805],
          [-91.405241, 40.554641],
          [-91.406851, 40.547557],
          [-91.404125, 40.539127],
          [-91.384531, 40.530948],
          [-91.369059, 40.512532],
          [-91.364211, 40.500043],
          [-91.36391, 40.490122],
          [-91.372554, 40.4012],
          [-91.375746, 40.391879],
          [-91.38836, 40.384929],
          [-91.419422, 40.378264],
          [-91.484507, 40.3839],
          [-91.490977, 40.393484],
          [-91.524612, 40.410765],
          [-91.619486, 40.507134],
          [-91.622362, 40.514362],
          [-91.618028, 40.53403],
          [-91.620071, 40.540817],
          [-91.696359, 40.588148],
          [-91.716769, 40.59853],
          [-91.718787, 40.813544],
          [-91.409324, 40.812682]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19127",
      state: "19",
      county: "127",
      name: "Marshall County",
      censusArea: 572.501,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.765999, 41.862373],
          [-93.231858, 41.862711],
          [-93.231722, 42.208886],
          [-93.001674, 42.209267],
          [-92.767463, 42.21014],
          [-92.765999, 41.862373]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19129",
      state: "19",
      county: "129",
      name: "Mills County",
      censusArea: 437.437,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.823123, 40.900924],
          [-95.829074, 40.975688],
          [-95.835434, 40.984184],
          [-95.867286, 41.001599],
          [-95.867246, 41.043671],
          [-95.866289, 41.051731],
          [-95.853396, 41.16028],
          [-95.384349, 41.160011],
          [-95.384964, 40.90155],
          [-95.813465, 40.901693],
          [-95.823123, 40.900924]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19135",
      state: "19",
      county: "135",
      name: "Monroe County",
      censusArea: 433.715,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.869771, 41.161066],
          [-92.640361, 41.161298],
          [-92.639091, 40.898886],
          [-93.097595, 40.898451],
          [-93.097724, 40.899738],
          [-93.099217, 41.160867],
          [-92.869771, 41.161066]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19139",
      state: "19",
      county: "139",
      name: "Muscatine County",
      censusArea: 437.469,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.366448, 41.598373],
          [-90.899853, 41.597133],
          [-90.783812, 41.596531],
          [-90.786282, 41.452888],
          [-90.847458, 41.455019],
          [-90.989976, 41.431962],
          [-91.027787, 41.423603],
          [-91.039872, 41.418523],
          [-91.047819, 41.4109],
          [-91.078682, 41.336089],
          [-91.079657, 41.333727],
          [-91.369332, 41.336063],
          [-91.368521, 41.423178],
          [-91.366448, 41.598373]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19145",
      state: "19",
      county: "145",
      name: "Page County",
      censusArea: 534.942,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.914896, 40.575068],
          [-94.955134, 40.575669],
          [-94.966491, 40.575839],
          [-95.068921, 40.57688],
          [-95.079742, 40.577007],
          [-95.097607, 40.577168],
          [-95.107213, 40.577116],
          [-95.110303, 40.57716],
          [-95.110663, 40.577206],
          [-95.112222, 40.577228],
          [-95.120829, 40.577413],
          [-95.154499, 40.57786],
          [-95.164058, 40.578017],
          [-95.202264, 40.578528],
          [-95.211408, 40.57865],
          [-95.21159, 40.578654],
          [-95.212715, 40.578679],
          [-95.213327, 40.578689],
          [-95.217455, 40.578759],
          [-95.218783, 40.578781],
          [-95.221525, 40.578827],
          [-95.335588, 40.579871],
          [-95.357802, 40.5801],
          [-95.373893, 40.580501],
          [-95.373923, 40.580501],
          [-95.384964, 40.90155],
          [-94.928459, 40.900653],
          [-94.914896, 40.575068]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20181",
      state: "20",
      county: "181",
      name: "Sherman County",
      censusArea: 1056.066,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.389068, 39.568369],
          [-101.391717, 39.135117],
          [-101.478195, 39.134761],
          [-102.047189, 39.133147],
          [-102.04725, 39.13702],
          [-102.048449, 39.303138],
          [-102.04896, 39.373712],
          [-102.049167, 39.403597],
          [-102.04937, 39.41821],
          [-102.049369, 39.423333],
          [-102.049679, 39.506183],
          [-102.049673, 39.536691],
          [-102.049554, 39.538932],
          [-102.049764, 39.56818],
          [-101.413913, 39.568361],
          [-101.389068, 39.568369]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19153",
      state: "19",
      county: "153",
      name: "Polk County",
      censusArea: 573.795,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.328614, 41.507824],
          [-93.328486, 41.49134],
          [-93.328407, 41.490921],
          [-93.41226, 41.505549],
          [-93.790612, 41.511916],
          [-93.814282, 41.600448],
          [-93.815527, 41.863419],
          [-93.698032, 41.86337],
          [-93.347933, 41.863104],
          [-93.348681, 41.600999],
          [-93.328614, 41.507824]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19155",
      state: "19",
      county: "155",
      name: "Pottawattamie County",
      censusArea: 950.28,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.853396, 41.16028],
          [-95.852788, 41.165398],
          [-95.91459, 41.185098],
          [-95.92319, 41.190998],
          [-95.923219, 41.191046],
          [-95.92599, 41.195698],
          [-95.927491, 41.202198],
          [-95.924891, 41.211198],
          [-95.90249, 41.273398],
          [-95.91379, 41.320197],
          [-95.92569, 41.322197],
          [-95.939291, 41.328897],
          [-95.953091, 41.339896],
          [-95.956691, 41.345496],
          [-95.956791, 41.349196],
          [-95.93831, 41.392162],
          [-95.937346, 41.394403],
          [-95.930705, 41.433894],
          [-95.981319, 41.506837],
          [-95.497997, 41.506074],
          [-95.154722, 41.505211],
          [-95.155851, 41.159236],
          [-95.384349, 41.160011],
          [-95.853396, 41.16028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19159",
      state: "19",
      county: "159",
      name: "Ringgold County",
      censusArea: 535.498,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.015492, 40.573914],
          [-94.034134, 40.573585],
          [-94.080223, 40.572899],
          [-94.080463, 40.572899],
          [-94.089194, 40.572806],
          [-94.091085, 40.572897],
          [-94.23224, 40.571907],
          [-94.28735, 40.571521],
          [-94.294813, 40.571341],
          [-94.310724, 40.571524],
          [-94.324765, 40.571477],
          [-94.336556, 40.571475],
          [-94.336706, 40.571452],
          [-94.358307, 40.571363],
          [-94.429725, 40.571041],
          [-94.460088, 40.570947],
          [-94.470648, 40.57083],
          [-94.471213, 40.570825],
          [-94.470779, 40.899502],
          [-94.014803, 40.897031],
          [-94.015492, 40.573914]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19163",
      state: "19",
      county: "163",
      name: "Scott County",
      censusArea: 458.089,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.898373, 41.771392],
          [-90.473617, 41.773465],
          [-90.317041, 41.729104],
          [-90.334525, 41.679559],
          [-90.343162, 41.648141],
          [-90.34165, 41.621484],
          [-90.39793, 41.572233],
          [-90.461432, 41.523533],
          [-90.474332, 41.519733],
          [-90.499475, 41.518055],
          [-90.605937, 41.494232],
          [-90.655839, 41.462132],
          [-90.737537, 41.450127],
          [-90.771672, 41.450761],
          [-90.786282, 41.452888],
          [-90.783812, 41.596531],
          [-90.899853, 41.597133],
          [-90.898373, 41.771392]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19171",
      state: "19",
      county: "171",
      name: "Tama County",
      censusArea: 721.011,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.298164, 41.862779],
          [-92.765999, 41.862373],
          [-92.767463, 42.21014],
          [-92.76809, 42.29668],
          [-92.53351, 42.297001],
          [-92.298792, 42.297527],
          [-92.298164, 41.862779]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19177",
      state: "19",
      county: "177",
      name: "Van Buren County",
      censusArea: 484.789,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.716769, 40.59853],
          [-91.729115, 40.61364],
          [-91.785916, 40.611488],
          [-91.795374, 40.611101],
          [-91.800133, 40.610953],
          [-91.813968, 40.610526],
          [-91.824826, 40.610191],
          [-91.832481, 40.609797],
          [-91.868401, 40.608059],
          [-91.943114, 40.605913],
          [-91.970988, 40.605112],
          [-91.998683, 40.604433],
          [-92.029649, 40.603713],
          [-92.067904, 40.602648],
          [-92.069521, 40.602772],
          [-92.082339, 40.602176],
          [-92.0832, 40.602244],
          [-92.092875, 40.602082],
          [-92.096387, 40.60183],
          [-92.17978, 40.600529],
          [-92.17899, 40.898343],
          [-92.179072, 40.89972],
          [-91.718268, 40.900805],
          [-91.718787, 40.813544],
          [-91.716769, 40.59853]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19183",
      state: "19",
      county: "183",
      name: "Washington County",
      censusArea: 568.835,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.485717, 41.162405],
          [-91.715893, 41.162822],
          [-91.83906, 41.161515],
          [-91.945571, 41.163578],
          [-91.946043, 41.510749],
          [-91.829231, 41.511457],
          [-91.52248, 41.511035],
          [-91.483995, 41.423848],
          [-91.485717, 41.162405]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19189",
      state: "19",
      county: "189",
      name: "Winnebago County",
      censusArea: 400.489,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.970762, 43.499605],
          [-93.97076, 43.499605],
          [-93.795793, 43.49952],
          [-93.794285, 43.499542],
          [-93.716217, 43.499563],
          [-93.708771, 43.499564],
          [-93.704916, 43.499568],
          [-93.699345, 43.499576],
          [-93.648533, 43.499559],
          [-93.617131, 43.499548],
          [-93.576728, 43.49952],
          [-93.558631, 43.499521],
          [-93.532178, 43.499472],
          [-93.528482, 43.499471],
          [-93.497405, 43.499456],
          [-93.49735, 43.499456],
          [-93.497635, 43.255468],
          [-93.970415, 43.255358],
          [-93.970762, 43.499605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19193",
      state: "19",
      county: "193",
      name: "Woodbury County",
      censusArea: 872.834,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.356666, 42.215077],
          [-96.356591, 42.215182],
          [-96.336323, 42.218922],
          [-96.323723, 42.229887],
          [-96.322868, 42.233637],
          [-96.328905, 42.254734],
          [-96.348814, 42.282024],
          [-96.375307, 42.318339],
          [-96.384169, 42.325874],
          [-96.407998, 42.337408],
          [-96.413895, 42.343393],
          [-96.417786, 42.351449],
          [-96.415509, 42.400294],
          [-96.413609, 42.407894],
          [-96.387608, 42.432494],
          [-96.380707, 42.446394],
          [-96.381307, 42.461694],
          [-96.385407, 42.473094],
          [-96.396107, 42.484095],
          [-96.409408, 42.487595],
          [-96.443408, 42.489495],
          [-96.466253, 42.497702],
          [-96.476947, 42.508677],
          [-96.481308, 42.516556],
          [-96.479909, 42.524195],
          [-96.477709, 42.535595],
          [-96.476952, 42.556079],
          [-96.479685, 42.561238],
          [-95.859949, 42.56062],
          [-95.750287, 42.561106],
          [-95.741611, 42.561285],
          [-95.741821, 42.474865],
          [-95.669265, 42.474645],
          [-95.670822, 42.211408],
          [-96.356666, 42.215077]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20003",
      state: "20",
      county: "003",
      name: "Anderson County",
      censusArea: 579.646,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.51897, 38.03823],
          [-95.508328, 38.390278],
          [-95.065831, 38.389945],
          [-95.077876, 38.037706],
          [-95.51897, 38.03823]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20009",
      state: "20",
      county: "009",
      name: "Barton County",
      censusArea: 895.4,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.486108, 38.696878],
          [-98.479958, 38.696916],
          [-98.480377, 38.521841],
          [-98.479841, 38.26079],
          [-98.912583, 38.261088],
          [-99.032309, 38.261227],
          [-99.032408, 38.348334],
          [-99.032971, 38.696759],
          [-98.486108, 38.696878]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20017",
      state: "20",
      county: "017",
      name: "Chase County",
      censusArea: 773.06,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.35378, 38.521657],
          [-96.357277, 38.17266],
          [-96.358099, 38.085817],
          [-96.522782, 38.08637],
          [-96.840772, 38.085622],
          [-96.819723, 38.522464],
          [-96.81783, 38.522396],
          [-96.35378, 38.521657]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20019",
      state: "20",
      county: "019",
      name: "Chautauqua County",
      censusArea: 638.881,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.96427, 36.999094],
          [-96.00081, 36.99886],
          [-96.14121, 36.998973],
          [-96.143207, 36.999134],
          [-96.147143, 36.999022],
          [-96.149709, 36.99904],
          [-96.152384, 36.999051],
          [-96.154017, 36.999161],
          [-96.184768, 36.999211],
          [-96.200028, 36.999028],
          [-96.217571, 36.99907],
          [-96.276368, 36.999271],
          [-96.279079, 36.999272],
          [-96.394272, 36.999221],
          [-96.415412, 36.999113],
          [-96.525493, 36.999147],
          [-96.524873, 37.30273],
          [-95.964399, 37.299232],
          [-95.96427, 36.999094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13211",
      state: "13",
      county: "211",
      name: "Morgan County",
      censusArea: 347.349,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.279931, 33.483438],
          [-83.533736, 33.434472],
          [-83.586817, 33.465577],
          [-83.682196, 33.52622],
          [-83.677669, 33.571176],
          [-83.681443, 33.577913],
          [-83.687085, 33.582375],
          [-83.687386, 33.588559],
          [-83.680896, 33.596919],
          [-83.505928, 33.81776],
          [-83.406189, 33.698307],
          [-83.269213, 33.533226],
          [-83.279931, 33.483438]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27081",
      state: "27",
      county: "081",
      name: "Lincoln County",
      censusArea: 536.757,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.07858, 44.19662],
          [-96.450765, 44.196958],
          [-96.452774, 44.19678],
          [-96.452774, 44.196895],
          [-96.452673, 44.254588],
          [-96.452419, 44.255274],
          [-96.452369, 44.268967],
          [-96.452365, 44.271972],
          [-96.452617, 44.282702],
          [-96.4525, 44.285687],
          [-96.452334, 44.297009],
          [-96.452239, 44.298655],
          [-96.452369, 44.312071],
          [-96.452248, 44.313362],
          [-96.452372, 44.325991],
          [-96.452309, 44.328094],
          [-96.452248, 44.340642],
          [-96.452152, 44.342219],
          [-96.452305, 44.345332],
          [-96.452282, 44.354857],
          [-96.452213, 44.360149],
          [-96.452134, 44.383679],
          [-96.452073, 44.38969],
          [-96.451924, 44.441549],
          [-96.451816, 44.460402],
          [-96.452218, 44.470873],
          [-96.452122, 44.473043],
          [-96.451974, 44.506849],
          [-96.45201, 44.516929],
          [-96.452236, 44.526871],
          [-96.452016, 44.543533],
          [-96.45199, 44.543639],
          [-96.451888, 44.544058],
          [-96.45172, 44.630708],
          [-96.451761, 44.631194],
          [-96.451761, 44.63135],
          [-96.449714, 44.63121],
          [-96.09295, 44.630486],
          [-96.07858, 44.19662]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27089",
      state: "27",
      county: "089",
      name: "Marshall County",
      censusArea: 1775.068,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.16111, 48.543155],
          [-97.163105, 48.543855],
          [-97.16305, 48.543963],
          [-96.387835, 48.544308],
          [-95.602315, 48.538887],
          [-95.593788, 48.172916],
          [-96.500843, 48.174108],
          [-97.147498, 48.173227],
          [-97.147401, 48.193779],
          [-97.14717, 48.242966],
          [-97.143148, 48.246708],
          [-97.134534, 48.249737],
          [-97.12993, 48.249731],
          [-97.11657, 48.279661],
          [-97.115581, 48.323928],
          [-97.126745, 48.34214],
          [-97.136373, 48.352393],
          [-97.145693, 48.397431],
          [-97.131479, 48.406586],
          [-97.123399, 48.421201],
          [-97.12677, 48.520191],
          [-97.150541, 48.539449],
          [-97.16111, 48.543155]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27091",
      state: "27",
      county: "091",
      name: "Martin County",
      censusArea: 712.347,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.854555, 43.500614],
          [-94.854444, 43.848099],
          [-94.368974, 43.848047],
          [-94.247123, 43.847946],
          [-94.247965, 43.500333],
          [-94.377466, 43.500379],
          [-94.390597, 43.500469],
          [-94.442835, 43.500583],
          [-94.442848, 43.500583],
          [-94.447048, 43.500639],
          [-94.47042, 43.50034],
          [-94.560838, 43.500377],
          [-94.565665, 43.50033],
          [-94.615916, 43.500544],
          [-94.854555, 43.500614]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27095",
      state: "27",
      county: "095",
      name: "Mille Lacs County",
      censusArea: 572.31,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.510704, 45.558772],
          [-93.759435, 45.559646],
          [-93.763316, 45.815872],
          [-93.763948, 45.822143],
          [-93.768891, 45.982525],
          [-93.81032, 45.983086],
          [-93.810362, 46.156585],
          [-93.810912, 46.243128],
          [-93.796281, 46.243199],
          [-93.430648, 46.246412],
          [-93.431831, 46.153991],
          [-93.432492, 45.981398],
          [-93.51808, 45.981789],
          [-93.513683, 45.734318],
          [-93.512402, 45.718817],
          [-93.510704, 45.558772]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27097",
      state: "27",
      county: "097",
      name: "Morrison County",
      censusArea: 1125.062,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.341679, 46.277705],
          [-94.374592, 46.157093],
          [-93.810362, 46.156585],
          [-93.81032, 45.983086],
          [-93.768891, 45.982525],
          [-93.763948, 45.822143],
          [-94.353165, 45.822131],
          [-94.271776, 45.775208],
          [-94.64399, 45.77395],
          [-94.653462, 46.348678],
          [-94.534724, 46.302797],
          [-94.42478, 46.331765],
          [-94.341679, 46.277705]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27099",
      state: "27",
      county: "099",
      name: "Mower County",
      censusArea: 711.329,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.007871, 43.499604],
          [-93.024348, 43.499572],
          [-93.024429, 43.499572],
          [-93.049192, 43.499571],
          [-93.049524, 43.848443],
          [-93.049413, 43.848454],
          [-93.045966, 43.848467],
          [-92.689391, 43.84857],
          [-92.448965, 43.834105],
          [-92.449087, 43.503302],
          [-92.448948, 43.50042],
          [-92.464505, 43.500345],
          [-92.553128, 43.5003],
          [-92.553161, 43.5003],
          [-92.649194, 43.500049],
          [-92.653318, 43.50005],
          [-92.67258, 43.500055],
          [-92.689033, 43.500062],
          [-92.692786, 43.500063],
          [-92.707312, 43.500069],
          [-92.752088, 43.500084],
          [-92.790317, 43.499567],
          [-92.870277, 43.499548],
          [-93.007871, 43.499604]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27105",
      state: "27",
      county: "105",
      name: "Nobles County",
      censusArea: 715.106,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.05232, 43.84907],
          [-95.462427, 43.847911],
          [-95.452502, 43.847953],
          [-95.454433, 43.500644],
          [-95.454706, 43.500648],
          [-95.454706, 43.500563],
          [-95.475065, 43.500335],
          [-95.486737, 43.500274],
          [-95.486803, 43.500246],
          [-95.514774, 43.499865],
          [-95.740813, 43.499894],
          [-95.741569, 43.499891],
          [-95.821277, 43.499965],
          [-95.834421, 43.499966],
          [-95.860946, 43.499966],
          [-95.861152, 43.499966],
          [-96.053163, 43.500176],
          [-96.05232, 43.84907]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27109",
      state: "27",
      county: "109",
      name: "Olmsted County",
      censusArea: 653.349,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.448965, 43.834105],
          [-92.689391, 43.84857],
          [-92.678412, 43.848487],
          [-92.678717, 44.195516],
          [-92.550871, 44.194916],
          [-92.318986, 44.194057],
          [-92.318971, 44.107734],
          [-92.079491, 44.106988],
          [-92.079292, 43.84708],
          [-92.44883, 43.848274],
          [-92.448965, 43.834105]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27111",
      state: "27",
      county: "111",
      name: "Otter Tail County",
      censusArea: 1972.068,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.15557, 46.36888],
          [-95.14588, 46.106761],
          [-95.76975, 46.10745],
          [-96.265366, 46.10861],
          [-96.28, 46.28222],
          [-96.280977, 46.630765],
          [-96.17227, 46.630709],
          [-96.174587, 46.717854],
          [-95.247999, 46.716255],
          [-95.16371, 46.71764],
          [-95.15557, 46.36888]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27115",
      state: "27",
      county: "115",
      name: "Pine County",
      censusArea: 1411.289,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.29286, 46.41722],
          [-92.292999, 46.321894],
          [-92.292782, 46.319312],
          [-92.292803, 46.314628],
          [-92.29288, 46.313752],
          [-92.292839, 46.307107],
          [-92.29284, 46.304319],
          [-92.293007, 46.297987],
          [-92.293074, 46.295129],
          [-92.293619, 46.244043],
          [-92.293558, 46.224578],
          [-92.293857, 46.180073],
          [-92.293744, 46.166838],
          [-92.293706, 46.157321],
          [-92.29353, 46.113824],
          [-92.294069, 46.078346],
          [-92.294033, 46.074377],
          [-92.306756, 46.07241],
          [-92.326877, 46.066618],
          [-92.443285, 46.014648],
          [-92.656125, 45.924442],
          [-92.707702, 45.894901],
          [-92.721128, 45.883805],
          [-92.734039, 45.868108],
          [-92.76249, 45.819239],
          [-92.76843, 45.79801],
          [-92.784621, 45.764196],
          [-92.803971, 45.749805],
          [-92.841051, 45.730024],
          [-93.141793, 45.730657],
          [-93.142869, 45.981005],
          [-93.055081, 45.981233],
          [-93.053871, 46.15812],
          [-93.054847, 46.419269],
          [-92.29286, 46.41722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27117",
      state: "27",
      county: "117",
      name: "Pipestone County",
      censusArea: 465.055,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.453264, 43.849506],
          [-96.453264, 43.849604],
          [-96.453335, 43.877029],
          [-96.453304, 43.878583],
          [-96.453183, 43.87865],
          [-96.453352, 43.949122],
          [-96.453289, 43.950814],
          [-96.453165, 43.96654],
          [-96.453292, 43.96718],
          [-96.453389, 43.97806],
          [-96.453263, 43.980277],
          [-96.453328, 43.992871],
          [-96.453297, 43.994723],
          [-96.453116, 44.006876],
          [-96.453053, 44.008887],
          [-96.453373, 44.023744],
          [-96.453405, 44.025413],
          [-96.453313, 44.03643],
          [-96.453187, 44.03835],
          [-96.452774, 44.19678],
          [-96.450765, 44.196958],
          [-96.07858, 44.19662],
          [-96.063778, 44.196425],
          [-96.06462, 43.849039],
          [-96.453264, 43.849506]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27119",
      state: "27",
      county: "119",
      name: "Polk County",
      censusArea: 1971.128,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.890671, 47.672153],
          [-96.980391, 47.815662],
          [-97.000356, 47.860915],
          [-97.023156, 47.873978],
          [-97.088158, 48.059515],
          [-97.098275, 48.07131],
          [-97.123135, 48.109497],
          [-97.14751, 48.170572],
          [-97.147498, 48.173227],
          [-96.500843, 48.174108],
          [-96.48255, 47.963595],
          [-96.482092, 47.847428],
          [-96.352708, 47.847328],
          [-96.351727, 47.7603],
          [-95.837177, 47.760894],
          [-95.836827, 47.848219],
          [-95.709148, 47.848594],
          [-95.709619, 47.935805],
          [-95.582155, 47.933966],
          [-95.578839, 47.673677],
          [-95.554248, 47.499893],
          [-95.563189, 47.500268],
          [-96.067621, 47.499136],
          [-96.848923, 47.498991],
          [-96.853768, 47.499527],
          [-96.853286, 47.503881],
          [-96.84243, 47.508225],
          [-96.850955, 47.598287],
          [-96.88574, 47.664266],
          [-96.890671, 47.672153]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27129",
      state: "27",
      county: "129",
      name: "Renville County",
      censusArea: 982.906,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.78063, 44.45665],
          [-94.865824, 44.498218],
          [-95.132652, 44.596443],
          [-95.360177, 44.69854],
          [-95.482843, 44.752505],
          [-95.482889, 44.890712],
          [-95.24852, 44.89131],
          [-94.758189, 44.892097],
          [-94.502395, 44.892295],
          [-94.49773, 44.892334],
          [-94.498074, 44.717171],
          [-94.629489, 44.716663],
          [-94.6242, 44.45603],
          [-94.78063, 44.45665]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27133",
      state: "27",
      county: "133",
      name: "Rock County",
      censusArea: 482.455,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.453264, 43.849501],
          [-96.453264, 43.849506],
          [-96.06462, 43.849039],
          [-96.05232, 43.84907],
          [-96.053163, 43.500176],
          [-96.198484, 43.500335],
          [-96.198766, 43.500312],
          [-96.208814, 43.500391],
          [-96.332062, 43.500415],
          [-96.351059, 43.500333],
          [-96.453049, 43.500415],
          [-96.453352, 43.58704],
          [-96.453383, 43.588183],
          [-96.453356, 43.607544],
          [-96.453387, 43.609944],
          [-96.453408, 43.675008],
          [-96.45338, 43.689637],
          [-96.453281, 43.791435],
          [-96.453088, 43.805123],
          [-96.453264, 43.849501]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19059",
      state: "19",
      county: "059",
      name: "Dickinson County",
      censusArea: 380.606,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.387787, 43.50024],
          [-95.375269, 43.500322],
          [-95.374737, 43.500314],
          [-95.250969, 43.500464],
          [-95.250762, 43.500406],
          [-95.214938, 43.500885],
          [-95.180423, 43.500774],
          [-95.167891, 43.500885],
          [-95.167294, 43.500771],
          [-95.122633, 43.500755],
          [-95.114874, 43.500667],
          [-95.054289, 43.50086],
          [-95.053504, 43.500769],
          [-95.034, 43.500811],
          [-95.014245, 43.500872],
          [-94.99446, 43.500523],
          [-94.974359, 43.500508],
          [-94.954477, 43.500467],
          [-94.934625, 43.50049],
          [-94.914955, 43.50045],
          [-94.914905, 43.50045],
          [-94.914634, 43.50045],
          [-94.914843, 43.499103],
          [-94.913723, 43.255054],
          [-95.388078, 43.255221],
          [-95.387787, 43.50024]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27141",
      state: "27",
      county: "141",
      name: "Sherburne County",
      censusArea: 432.917,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.149467, 45.559066],
          [-93.759435, 45.559646],
          [-93.510704, 45.558772],
          [-93.51007, 45.414798],
          [-93.512554, 45.25042],
          [-93.51219, 45.245683],
          [-93.515342, 45.24632],
          [-93.521653, 45.246618],
          [-93.57939, 45.292122],
          [-93.75661, 45.294721],
          [-94.047327, 45.422861],
          [-94.141633, 45.49297],
          [-94.149467, 45.559066]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27145",
      state: "27",
      county: "145",
      name: "Stearns County",
      censusArea: 1343.133,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.766899, 45.773911],
          [-94.64399, 45.77395],
          [-94.271776, 45.775208],
          [-94.215712, 45.72656],
          [-94.203666, 45.619556],
          [-94.149467, 45.559066],
          [-94.141633, 45.49297],
          [-94.047327, 45.422861],
          [-94.089509, 45.34662],
          [-94.260821, 45.283941],
          [-94.383449, 45.282797],
          [-94.383556, 45.326705],
          [-94.76308, 45.3261],
          [-94.762934, 45.412861],
          [-95.13173, 45.412402],
          [-95.13967, 45.758891],
          [-95.139731, 45.773416],
          [-94.766899, 45.773911]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27149",
      state: "27",
      county: "149",
      name: "Stevens County",
      censusArea: 563.604,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.747402, 45.412313],
          [-96.117036, 45.411993],
          [-96.241328, 45.412667],
          [-96.253026, 45.585526],
          [-96.254022, 45.75982],
          [-95.758508, 45.759932],
          [-95.747402, 45.412313]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27157",
      state: "27",
      county: "157",
      name: "Wabasha County",
      censusArea: 522.982,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.083925, 44.406211],
          [-92.046285, 44.394398],
          [-91.972493, 44.36676],
          [-91.9636, 44.362112],
          [-91.92559, 44.333548],
          [-91.918625, 44.322671],
          [-91.887189, 44.252513],
          [-91.887905, 44.246398],
          [-91.892963, 44.235149],
          [-91.892698, 44.231105],
          [-91.875158, 44.200575],
          [-91.862858, 44.193001],
          [-92.079397, 44.192722],
          [-92.079491, 44.106988],
          [-92.318971, 44.107734],
          [-92.318986, 44.194057],
          [-92.550871, 44.194916],
          [-92.550733, 44.368521],
          [-92.430629, 44.368291],
          [-92.430271, 44.455157],
          [-92.24201, 44.454254],
          [-92.233897, 44.446334],
          [-92.221083, 44.440386],
          [-92.115296, 44.416056],
          [-92.083925, 44.406211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27163",
      state: "27",
      county: "163",
      name: "Washington County",
      censusArea: 384.282,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.746139, 45.296046],
          [-92.751735, 45.292667],
          [-92.760615, 45.278827],
          [-92.765898, 45.210173],
          [-92.76713, 45.194165],
          [-92.792875, 45.078489],
          [-92.791206, 45.045698],
          [-92.768606, 45.008016],
          [-92.750645, 44.937299],
          [-92.761677, 44.862022],
          [-92.761845, 44.860879],
          [-92.765461, 44.836202],
          [-92.784443, 44.793958],
          [-92.802201, 44.745714],
          [-93.005978, 44.771685],
          [-93.010076, 44.863787],
          [-93.012639, 44.869102],
          [-93.016466, 44.884611],
          [-93.020044, 44.89075],
          [-92.984192, 44.890814],
          [-92.984207, 44.893998],
          [-92.984458, 44.909393],
          [-92.984396, 44.909644],
          [-92.984463, 44.919869],
          [-92.98441, 44.920646],
          [-92.984584, 44.945077],
          [-92.984522, 44.945685],
          [-92.98462, 44.946114],
          [-92.984602, 44.947636],
          [-92.98464, 44.948522],
          [-92.984712, 45.075372],
          [-92.984621, 45.123757],
          [-93.020518, 45.123866],
          [-93.020219, 45.126754],
          [-93.019439, 45.296837],
          [-92.746139, 45.296046]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27171",
      state: "27",
      county: "171",
      name: "Wright County",
      censusArea: 661.457,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.521653, 45.246618],
          [-93.638745, 45.174363],
          [-93.638622, 45.16562],
          [-93.645168, 45.161561],
          [-93.647747, 45.158609],
          [-93.661545, 45.153274],
          [-93.667203, 45.154443],
          [-93.675914, 45.151134],
          [-93.761874, 45.081244],
          [-93.767367, 44.977964],
          [-94.012236, 44.978712],
          [-94.256074, 44.979465],
          [-94.260821, 45.283941],
          [-94.089509, 45.34662],
          [-94.047327, 45.422861],
          [-93.75661, 45.294721],
          [-93.57939, 45.292122],
          [-93.521653, 45.246618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28005",
      state: "28",
      county: "005",
      name: "Amite County",
      censusArea: 730.1,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.825829, 30.99936],
          [-90.826027, 30.99936],
          [-91.060217, 30.998935],
          [-91.059431, 31.281858],
          [-91.095398, 31.320975],
          [-91.018012, 31.347502],
          [-90.633302, 31.349306],
          [-90.548199, 31.349574],
          [-90.547615, 30.999723],
          [-90.567195, 30.999733],
          [-90.583518, 30.999698],
          [-90.584448, 30.999698],
          [-90.587373, 30.999604],
          [-90.588676, 30.99965],
          [-90.648721, 30.999486],
          [-90.651193, 30.99951],
          [-90.734473, 30.999214],
          [-90.734552, 30.999222],
          [-90.758775, 30.999583],
          [-90.769333, 30.999374],
          [-90.775981, 30.999491],
          [-90.779858, 30.999457],
          [-90.783745, 30.999447],
          [-90.825829, 30.99936]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28009",
      state: "28",
      county: "009",
      name: "Benton County",
      censusArea: 406.616,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.138997, 34.99433],
          [-89.017128, 34.994649],
          [-89.017551, 34.858723],
          [-89.088065, 34.815307],
          [-89.088488, 34.598247],
          [-89.245611, 34.597601],
          [-89.245645, 34.583234],
          [-89.298334, 34.582926],
          [-89.299227, 34.844415],
          [-89.352705, 34.844432],
          [-89.352679, 34.99442],
          [-89.198288, 34.994484],
          [-89.139136, 34.994307],
          [-89.138997, 34.99433]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28011",
      state: "28",
      county: "011",
      name: "Bolivar County",
      censusArea: 876.573,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.232295, 33.552788],
          [-91.228287, 33.559937],
          [-91.224121, 33.567369],
          [-91.152148, 33.582721],
          [-91.134043, 33.594489],
          [-91.130445, 33.606034],
          [-91.157484, 33.626187],
          [-91.186519, 33.645909],
          [-91.193093, 33.656866],
          [-91.18871, 33.669466],
          [-91.185971, 33.679327],
          [-91.17611, 33.684257],
          [-91.14817, 33.679327],
          [-91.139953, 33.683709],
          [-91.132831, 33.688092],
          [-91.1279, 33.695762],
          [-91.126805, 33.707814],
          [-91.136118, 33.728632],
          [-91.144682, 33.750108],
          [-91.139869, 33.777117],
          [-91.067511, 33.840443],
          [-91.036821, 33.914656],
          [-91.088164, 33.960078],
          [-91.089119, 33.972653],
          [-91.01889, 34.003151],
          [-90.964708, 34.007952],
          [-90.887413, 34.032505],
          [-90.879163, 34.065503],
          [-90.888396, 34.087433],
          [-90.912057, 34.095512],
          [-90.928793, 34.107631],
          [-90.931969, 34.120017],
          [-90.659457, 34.118867],
          [-90.657002, 33.987586],
          [-90.6586, 33.616272],
          [-90.763469, 33.616164],
          [-90.763509, 33.529389],
          [-91.215671, 33.529423],
          [-91.232295, 33.552788]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28015",
      state: "28",
      county: "015",
      name: "Carroll County",
      censusArea: 628.243,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.174614, 33.333378],
          [-90.174756, 33.417003],
          [-90.101401, 33.45894],
          [-90.127775, 33.676956],
          [-89.78689, 33.676727],
          [-89.785227, 33.416275],
          [-89.646314, 33.402544],
          [-89.64528, 33.285515],
          [-89.749441, 33.214478],
          [-90.174614, 33.333378]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28021",
      state: "28",
      county: "021",
      name: "Claiborne County",
      censusArea: 487.406,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.736282, 31.786134],
          [-90.852657, 31.785422],
          [-90.842, 31.826498],
          [-91.013676, 31.861852],
          [-91.248144, 31.869848],
          [-91.128009, 31.985857],
          [-91.034707, 32.101053],
          [-91.027946, 32.112722],
          [-90.945094, 32.079921],
          [-90.90774, 32.131888],
          [-90.77482, 32.153993],
          [-90.728622, 32.225119],
          [-90.718297, 32.048352],
          [-90.716785, 31.844583],
          [-90.736282, 31.786134]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28031",
      state: "28",
      county: "031",
      name: "Covington County",
      censusArea: 413.792,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.399178, 31.434029],
          [-89.420857, 31.434043],
          [-89.451616, 31.434029],
          [-89.586819, 31.433688],
          [-89.586919, 31.513444],
          [-89.755614, 31.609751],
          [-89.755103, 31.774654],
          [-89.653025, 31.780653],
          [-89.401227, 31.796863],
          [-89.400539, 31.686169],
          [-89.399178, 31.434029]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28039",
      state: "28",
      county: "039",
      name: "George County",
      censusArea: 478.708,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.884534, 30.735591],
          [-88.885038, 30.910788],
          [-88.834475, 30.910323],
          [-88.834339, 30.997983],
          [-88.809328, 30.997379],
          [-88.425635, 30.998301],
          [-88.412462, 30.735598],
          [-88.884534, 30.735591]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19149",
      state: "19",
      county: "149",
      name: "Plymouth County",
      censusArea: 862.893,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.523264, 42.909059],
          [-95.859377, 42.909097],
          [-95.859949, 42.56062],
          [-96.479685, 42.561238],
          [-96.516338, 42.630435],
          [-96.542366, 42.660736],
          [-96.575299, 42.682665],
          [-96.601989, 42.697429],
          [-96.60614, 42.694661],
          [-96.610975, 42.694751],
          [-96.630617, 42.70588],
          [-96.639704, 42.737071],
          [-96.633168, 42.768325],
          [-96.632142, 42.770863],
          [-96.577813, 42.828102],
          [-96.563058, 42.831051],
          [-96.552092, 42.836057],
          [-96.549513, 42.839143],
          [-96.54146, 42.857682],
          [-96.523264, 42.909059]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28045",
      state: "28",
      county: "045",
      name: "Hancock County",
      censusArea: 473.748,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.341446, 30.64772],
          [-89.340947, 30.372896],
          [-89.349447, 30.370995],
          [-89.353248, 30.368795],
          [-89.363848, 30.357395],
          [-89.366116, 30.352169],
          [-89.345934, 30.343573],
          [-89.338847, 30.342995],
          [-89.332546, 30.337895],
          [-89.322345, 30.319296],
          [-89.322545, 30.314896],
          [-89.329946, 30.302896],
          [-89.344746, 30.293196],
          [-89.358546, 30.288896],
          [-89.365747, 30.284896],
          [-89.379547, 30.270396],
          [-89.383747, 30.267796],
          [-89.419348, 30.25432],
          [-89.424624, 30.245391],
          [-89.424493, 30.239092],
          [-89.430428, 30.223218],
          [-89.447465, 30.205098],
          [-89.44791, 30.185352],
          [-89.461275, 30.174745],
          [-89.463595, 30.173943],
          [-89.469792, 30.176278],
          [-89.475824, 30.191561],
          [-89.480214, 30.193751],
          [-89.490099, 30.187677],
          [-89.503231, 30.183051],
          [-89.522814, 30.183076],
          [-89.524504, 30.180753],
          [-89.572654, 30.180897],
          [-89.580754, 30.186197],
          [-89.607655, 30.217096],
          [-89.64344, 30.287682],
          [-89.643575, 30.293099],
          [-89.640694, 30.301313],
          [-89.629877, 30.321017],
          [-89.629941, 30.339449],
          [-89.68341, 30.451793],
          [-89.69058, 30.458773],
          [-89.54451, 30.516862],
          [-89.544232, 30.648368],
          [-89.341446, 30.64772]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28051",
      state: "28",
      county: "051",
      name: "Holmes County",
      censusArea: 756.699,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.331589, 33.302825],
          [-90.321513, 33.375121],
          [-90.174614, 33.333378],
          [-89.749441, 33.214478],
          [-89.895518, 32.936014],
          [-89.965882, 32.879573],
          [-90.364031, 33.010596],
          [-90.431684, 33.096871],
          [-90.330312, 33.137818],
          [-90.331589, 33.302825]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28053",
      state: "28",
      county: "053",
      name: "Humphreys County",
      censusArea: 418.491,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.453233, 33.329484],
          [-90.453801, 33.267302],
          [-90.3735, 33.242209],
          [-90.331589, 33.302825],
          [-90.330312, 33.137818],
          [-90.431684, 33.096871],
          [-90.364031, 33.010596],
          [-90.449323, 33.00901],
          [-90.449893, 32.92329],
          [-90.656632, 32.92251],
          [-90.658049, 33.096514],
          [-90.701845, 33.096294],
          [-90.680319, 33.201971],
          [-90.716277, 33.270032],
          [-90.555211, 33.270533],
          [-90.555549, 33.329181],
          [-90.453233, 33.329484]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28059",
      state: "28",
      county: "059",
      name: "Jackson County",
      censusArea: 722.754,
      stateName: "Mississippi"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-88.845609, 30.405266],
            [-88.88313, 30.418798],
            [-88.884533, 30.677292],
            [-88.884504, 30.713175],
            [-88.884478, 30.71377],
            [-88.884536, 30.714622],
            [-88.884534, 30.735591],
            [-88.412462, 30.735598],
            [-88.41227, 30.731771],
            [-88.412209, 30.730395],
            [-88.41155, 30.712956],
            [-88.411339, 30.706334],
            [-88.409571, 30.668731],
            [-88.40807, 30.63697],
            [-88.407462, 30.631653],
            [-88.407484, 30.622736],
            [-88.404013, 30.54506],
            [-88.403931, 30.543359],
            [-88.403547, 30.5331],
            [-88.402283, 30.510852],
            [-88.395023, 30.369425],
            [-88.397236, 30.367689],
            [-88.399062, 30.360744],
            [-88.397082, 30.354785],
            [-88.39415, 30.352493],
            [-88.39398, 30.349307],
            [-88.401181, 30.344382],
            [-88.409927, 30.342115],
            [-88.418811, 30.353911],
            [-88.433891, 30.354652],
            [-88.446495, 30.347753],
            [-88.446625, 30.337671],
            [-88.45381, 30.329626],
            [-88.471875, 30.32002],
            [-88.480117, 30.318345],
            [-88.504802, 30.321472],
            [-88.506226, 30.322393],
            [-88.506334, 30.327398],
            [-88.522494, 30.340092],
            [-88.536214, 30.343299],
            [-88.579483, 30.34419],
            [-88.596349, 30.358365],
            [-88.599249, 30.358933],
            [-88.601762, 30.355876],
            [-88.613745, 30.353108],
            [-88.624523, 30.358713],
            [-88.66382, 30.362099],
            [-88.679575, 30.35571],
            [-88.692164, 30.347302],
            [-88.700587, 30.343689],
            [-88.714077, 30.342186],
            [-88.728893, 30.342671],
            [-88.746945, 30.347622],
            [-88.771742, 30.365403],
            [-88.811615, 30.384907],
            [-88.812576, 30.38768],
            [-88.810127, 30.391298],
            [-88.810227, 30.394698],
            [-88.823724, 30.402376],
            [-88.841328, 30.409598],
            [-88.845609, 30.405266]
          ]
        ],
        [
          [
            [-88.506999, 30.214348],
            [-88.500011, 30.214044],
            [-88.465713, 30.20254],
            [-88.453444, 30.201236],
            [-88.442654, 30.202314],
            [-88.430332, 30.208548],
            [-88.401466, 30.210172],
            [-88.404581, 30.206162],
            [-88.428301, 30.198511],
            [-88.453654, 30.196584],
            [-88.493523, 30.205945],
            [-88.502752, 30.210506],
            [-88.506999, 30.214348]
          ]
        ],
        [
          [
            [-88.710719, 30.250799],
            [-88.656804, 30.233956],
            [-88.573044, 30.22264],
            [-88.568056, 30.222732],
            [-88.562067, 30.227476],
            [-88.565576, 30.222847],
            [-88.569138, 30.221357],
            [-88.587424, 30.219154],
            [-88.633743, 30.226342],
            [-88.665857, 30.228847],
            [-88.71183, 30.242662],
            [-88.740647, 30.238665],
            [-88.752782, 30.238803],
            [-88.764022, 30.241488],
            [-88.771991, 30.245523],
            [-88.73255, 30.246322],
            [-88.718104, 30.252931],
            [-88.710719, 30.250799]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28067",
      state: "28",
      county: "067",
      name: "Jones County",
      censusArea: 694.795,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.359928, 31.799388],
          [-89.31656, 31.80209],
          [-88.943355, 31.824565],
          [-88.943468, 31.433458],
          [-89.146092, 31.434052],
          [-89.399178, 31.434029],
          [-89.400539, 31.686169],
          [-89.401227, 31.796863],
          [-89.359928, 31.799388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28073",
      state: "28",
      county: "073",
      name: "Lamar County",
      censusArea: 497.055,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.654136, 31.375517],
          [-89.6544, 31.404663],
          [-89.654212, 31.433805],
          [-89.586819, 31.433688],
          [-89.451616, 31.434029],
          [-89.451073, 31.346383],
          [-89.347863, 31.3467],
          [-89.34895, 31.01037],
          [-89.476022, 30.982901],
          [-89.501411, 31.013907],
          [-89.654038, 31.002502],
          [-89.654136, 31.375517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23007",
      state: "23",
      county: "007",
      name: "Franklin County",
      censusArea: 1696.609,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-69.930451, 44.610852],
          [-69.944082, 44.537828],
          [-70.030536, 44.508786],
          [-70.04235, 44.485235],
          [-70.12939, 44.487217],
          [-70.201276, 44.477976],
          [-70.238904, 44.458942],
          [-70.280908, 44.505095],
          [-70.280748, 44.569619],
          [-70.549985, 44.610727],
          [-70.510598, 44.627216],
          [-70.61503, 44.795634],
          [-70.769228, 44.732228],
          [-70.831612, 45.260055],
          [-70.808322, 45.325824],
          [-70.807664, 45.378118],
          [-70.824053, 45.391094],
          [-70.825612, 45.400305],
          [-70.798483, 45.42474],
          [-70.781471, 45.431159],
          [-70.753977, 45.427789],
          [-70.744077, 45.421091],
          [-70.743775, 45.411925],
          [-70.729972, 45.399359],
          [-70.712286, 45.390611],
          [-70.660029, 45.377901],
          [-70.651986, 45.377146],
          [-70.634661, 45.383608],
          [-70.626527, 45.407011],
          [-70.635498, 45.427817],
          [-70.650161, 45.443685],
          [-70.663701, 45.447049],
          [-70.67591, 45.452582],
          [-70.716332, 45.486909],
          [-70.723167, 45.507606],
          [-70.721611, 45.515058],
          [-70.700776, 45.532777],
          [-70.686531, 45.54999],
          [-70.688214, 45.563981],
          [-70.659286, 45.58688],
          [-70.564789, 45.65515],
          [-70.551038, 45.661885],
          [-70.418519, 45.144005],
          [-70.308491, 45.163183],
          [-70.293826, 45.109903],
          [-70.159375, 45.128396],
          [-70.110365, 44.947311],
          [-70.149085, 44.896102],
          [-70.12993, 44.850765],
          [-70.03327, 44.86454],
          [-69.996191, 44.677497],
          [-69.930451, 44.610852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13215",
      state: "13",
      county: "215",
      name: "Muscogee County",
      censusArea: 216.385,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.001052, 32.510477],
          [-85.001324, 32.512973],
          [-85.0071, 32.523868],
          [-85.022509, 32.542923],
          [-85.044606, 32.559499],
          [-85.069583, 32.58393],
          [-85.079935, 32.602889],
          [-85.080288, 32.603577],
          [-85.082454, 32.607022],
          [-84.907703, 32.607608],
          [-84.907883, 32.583433],
          [-84.694603, 32.583945],
          [-84.69451, 32.518732],
          [-84.769431, 32.422319],
          [-84.774567, 32.421839],
          [-84.778359, 32.423054],
          [-84.781559, 32.422863],
          [-84.782983, 32.421711],
          [-84.788471, 32.418031],
          [-84.791463, 32.414783],
          [-84.798647, 32.415968],
          [-84.805175, 32.418912],
          [-84.809432, 32.420864],
          [-84.811384, 32.418608],
          [-84.814632, 32.414448],
          [-84.823992, 32.413967],
          [-84.83436, 32.413216],
          [-84.844216, 32.413248],
          [-84.848664, 32.41464],
          [-84.987278, 32.381623],
          [-84.979431, 32.412244],
          [-84.983831, 32.445643],
          [-84.998231, 32.469842],
          [-85.000779, 32.506548],
          [-85.00103, 32.510157],
          [-85.001052, 32.510477]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13239",
      state: "13",
      county: "239",
      name: "Quitman County",
      censusArea: 151.237,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.062246, 31.991857],
          [-84.907006, 31.924465],
          [-84.910338, 31.776841],
          [-84.95864, 31.777854],
          [-85.138668, 31.780425],
          [-85.141331, 31.783163],
          [-85.141831, 31.839861],
          [-85.132931, 31.88826],
          [-85.10663, 31.915159],
          [-85.08213, 31.944658],
          [-85.07233, 31.964758],
          [-85.062246, 31.991857]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27077",
      state: "27",
      county: "077",
      name: "Lake Of  Woods County",
      censusArea: 1297.869,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.431854, 48.706588],
          [-94.430273, 48.700909],
          [-94.428702, 48.367333],
          [-95.210953, 48.365971],
          [-95.211966, 48.539961],
          [-95.34254, 48.540211],
          [-95.34273, 48.713028],
          [-95.089863, 48.713062],
          [-95.091464, 48.920618],
          [-95.235606, 48.88145],
          [-95.319895, 48.998769],
          [-95.153711, 48.998903],
          [-95.153309, 49.18488],
          [-95.153424, 49.249995],
          [-95.153333, 49.305655],
          [-95.153319, 49.30772],
          [-95.153331, 49.308442],
          [-95.15333, 49.309287],
          [-95.153284, 49.343409],
          [-95.153344, 49.343662],
          [-95.153407, 49.354397],
          [-95.15333, 49.365886],
          [-95.153259, 49.367691],
          [-95.153293, 49.369107],
          [-95.15335, 49.383079],
          [-95.153314, 49.384358],
          [-95.126467, 49.369439],
          [-95.058404, 49.35317],
          [-95.014415, 49.356405],
          [-94.988908, 49.368897],
          [-94.957465, 49.370186],
          [-94.909273, 49.350176],
          [-94.824291, 49.308834],
          [-94.797527, 49.197791],
          [-94.773223, 49.120733],
          [-94.750221, 49.099763],
          [-94.750218, 48.999992],
          [-94.718932, 48.999991],
          [-94.683069, 48.883929],
          [-94.683127, 48.883376],
          [-94.690302, 48.863711],
          [-94.690246, 48.863363],
          [-94.690889, 48.778066],
          [-94.690863, 48.778047],
          [-94.646256, 48.749975],
          [-94.64515, 48.748991],
          [-94.628854, 48.738789],
          [-94.533057, 48.701262],
          [-94.468728, 48.696324],
          [-94.431854, 48.706588]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45031",
      state: "45",
      county: "031",
      name: "Darlington County",
      censusArea: 561.153,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.072808, 34.086013],
          [-80.131441, 34.128666],
          [-80.112727, 34.197018],
          [-80.177225, 34.366203],
          [-80.288692, 34.364139],
          [-80.286936, 34.364572],
          [-80.28688, 34.365305],
          [-80.288596, 34.366207],
          [-80.045439, 34.492631],
          [-79.884542, 34.491218],
          [-79.829981, 34.531925],
          [-79.721794, 34.485416],
          [-79.750566, 34.436797],
          [-79.684559, 34.40049],
          [-79.657764, 34.30519],
          [-79.883065, 34.204112],
          [-79.999789, 34.129007],
          [-80.072808, 34.086013]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45035",
      state: "45",
      county: "035",
      name: "Dorchester County",
      censusArea: 573.233,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.361851, 33.257443],
          [-80.299248, 33.130274],
          [-80.279567, 33.119213],
          [-80.149246, 33.021601],
          [-80.079028, 32.927811],
          [-80.17446, 32.845651],
          [-80.16313, 32.84127],
          [-80.158808, 32.830114],
          [-80.148108, 32.818904],
          [-80.40134, 32.858466],
          [-80.390533, 33.043909],
          [-80.6221, 33.06608],
          [-80.709736, 33.15647],
          [-80.790296, 33.18084],
          [-80.50279, 33.334496],
          [-80.484578, 33.280034],
          [-80.361851, 33.257443]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45037",
      state: "45",
      county: "037",
      name: "Edgefield County",
      censusArea: 500.406,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.651759, 33.81451],
          [-82.012534, 33.532156],
          [-82.014293, 33.530313],
          [-82.028238, 33.544934],
          [-82.046335, 33.56383],
          [-82.069039, 33.575382],
          [-82.098816, 33.586358],
          [-82.11663, 33.589723],
          [-82.095527, 33.633869],
          [-82.166027, 33.690316],
          [-82.172379, 33.765711],
          [-82.113728, 33.82988],
          [-82.1557, 33.92929],
          [-82.046974, 33.95321],
          [-82.008298, 33.961646],
          [-81.894595, 33.975415],
          [-81.836803, 33.866501],
          [-81.651759, 33.81451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47011",
      state: "47",
      county: "011",
      name: "Bradley County",
      censusArea: 328.762,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.703607, 35.241043],
          [-84.699937, 35.170974],
          [-84.775852, 34.9878],
          [-84.808127, 34.987592],
          [-84.809184, 34.987569],
          [-84.810477, 34.987607],
          [-84.810742, 34.987615],
          [-84.817279, 34.987753],
          [-84.820478, 34.987913],
          [-84.82401, 34.987707],
          [-84.831799, 34.988004],
          [-84.858032, 34.987746],
          [-84.861314, 34.987791],
          [-84.939306, 34.987916],
          [-84.94442, 34.987864],
          [-84.955623, 34.98783],
          [-84.976973, 34.987669],
          [-85.024043, 35.148829],
          [-84.94635, 35.287788],
          [-84.860164, 35.350074],
          [-84.703607, 35.241043]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47017",
      state: "47",
      county: "017",
      name: "Carroll County",
      censusArea: 599.252,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.179177, 35.816785],
          [-88.569047, 35.824829],
          [-88.606862, 35.789354],
          [-88.706811, 35.791075],
          [-88.692709, 36.062746],
          [-88.655488, 36.117338],
          [-88.532233, 36.115416],
          [-88.530346, 36.151573],
          [-88.211698, 36.145974],
          [-88.212559, 36.12029],
          [-88.217436, 35.846582],
          [-88.177859, 35.845841],
          [-88.17898, 35.818911],
          [-88.179177, 35.816785]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47021",
      state: "47",
      county: "021",
      name: "Cheatham County",
      censusArea: 302.437,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.286501, 36.321933],
          [-87.177254, 36.424583],
          [-87.171307, 36.427616],
          [-87.163242, 36.42136],
          [-87.162729, 36.413298],
          [-87.150974, 36.416644],
          [-87.148602, 36.422773],
          [-87.150276, 36.445037],
          [-87.120443, 36.45546],
          [-86.988801, 36.37065],
          [-86.913233, 36.382621],
          [-86.997705, 36.128512],
          [-87.054114, 36.045535],
          [-87.085304, 36.043761],
          [-87.106124, 36.045167],
          [-87.142341, 36.048028],
          [-87.182573, 36.049726],
          [-87.149876, 36.176878],
          [-87.177303, 36.314145],
          [-87.286501, 36.321933]
        ],
        [
          [-87.151995, 36.289037],
          [-87.142372, 36.290684],
          [-87.139332, 36.292418],
          [-87.142315, 36.294607],
          [-87.144172, 36.292142],
          [-87.146906, 36.293344],
          [-87.151995, 36.289037]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47025",
      state: "47",
      county: "025",
      name: "Claiborne County",
      censusArea: 434.58,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.472108, 36.597284],
          [-83.398303, 36.534885],
          [-83.3868, 36.413186],
          [-83.532504, 36.353385],
          [-83.667408, 36.344381],
          [-83.73301, 36.34498],
          [-83.805515, 36.435879],
          [-83.905638, 36.419979],
          [-83.987612, 36.589595],
          [-83.930669, 36.588249],
          [-83.690714, 36.582581],
          [-83.677114, 36.596582],
          [-83.675413, 36.600814],
          [-83.670141, 36.600797],
          [-83.670128, 36.600764],
          [-83.472108, 36.597284]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47033",
      state: "47",
      county: "033",
      name: "Crockett County",
      censusArea: 265.535,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.069544, 35.692679],
          [-89.271224, 35.754922],
          [-89.356033, 35.817635],
          [-89.342829, 35.880934],
          [-89.280188, 35.886416],
          [-89.188844, 35.99976],
          [-89.109602, 35.862095],
          [-88.916181, 35.797307],
          [-88.892938, 35.783842],
          [-89.02096, 35.671046],
          [-89.069544, 35.692679]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47035",
      state: "47",
      county: "035",
      name: "Cumberland County",
      censusArea: 681.025,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.680633, 35.908454],
          [-84.781901, 35.825],
          [-84.916062, 35.761939],
          [-85.254062, 35.765611],
          [-85.247784, 35.78666],
          [-85.26038, 35.78258],
          [-85.273288, 35.787948],
          [-85.269359, 35.793388],
          [-85.213115, 35.910379],
          [-85.264206, 35.979154],
          [-85.246362, 36.100698],
          [-85.100213, 36.138561],
          [-84.907753, 36.156293],
          [-84.720727, 35.994914],
          [-84.680633, 35.908454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47041",
      state: "47",
      county: "041",
      name: "De Kalb County",
      censusArea: 304.347,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.885156, 35.839658],
          [-86.014849, 35.96106],
          [-86.059706, 36.086024],
          [-85.807415, 36.131582],
          [-85.644604, 36.015053],
          [-85.637327, 35.881824],
          [-85.682095, 35.831254],
          [-85.885156, 35.839658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47047",
      state: "47",
      county: "047",
      name: "Fayette County",
      censusArea: 704.786,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.642786, 35.04486],
          [-89.632776, 35.375824],
          [-89.474171, 35.403039],
          [-89.183944, 35.397126],
          [-89.198288, 34.994484],
          [-89.352679, 34.99442],
          [-89.493739, 34.994361],
          [-89.511153, 34.994755],
          [-89.644282, 34.995293],
          [-89.643739, 35.011693],
          [-89.643782, 35.012092],
          [-89.642786, 35.04486]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47049",
      state: "47",
      county: "049",
      name: "Fentress County",
      censusArea: 498.612,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.120445, 36.407164],
          [-85.043924, 36.558791],
          [-84.943144, 36.58127],
          [-84.731839, 36.524674],
          [-84.659082, 36.395227],
          [-84.700785, 36.370824],
          [-84.797861, 36.296894],
          [-84.878553, 36.280204],
          [-84.907753, 36.156293],
          [-85.100213, 36.138561],
          [-85.119583, 36.144719],
          [-85.082497, 36.196171],
          [-85.122245, 36.304599],
          [-85.120445, 36.407164]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47059",
      state: "47",
      county: "059",
      name: "Greene County",
      censusArea: 622.165,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.595065, 36.096205],
          [-82.5772, 36.078387],
          [-82.602877, 36.039833],
          [-82.619205, 36.056556],
          [-82.632265, 36.065705],
          [-82.637165, 36.065805],
          [-82.683565, 36.046104],
          [-82.715365, 36.024253],
          [-82.754465, 36.004304],
          [-82.778625, 35.974792],
          [-82.830112, 35.932972],
          [-82.852554, 35.949089],
          [-82.874159, 35.952698],
          [-82.898505, 35.945101],
          [-82.898506, 35.9451],
          [-83.166085, 36.180894],
          [-83.082312, 36.242296],
          [-82.936583, 36.334897],
          [-82.702566, 36.410412],
          [-82.629641, 36.41154],
          [-82.647769, 36.146404],
          [-82.595065, 36.096205]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47061",
      state: "47",
      county: "061",
      name: "Grundy County",
      censusArea: 360.534,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.607326, 35.531651],
          [-85.485635, 35.406433],
          [-85.558551, 35.319292],
          [-85.709418, 35.289251],
          [-85.740113, 35.215295],
          [-85.873025, 35.223362],
          [-85.874977, 35.227287],
          [-85.88675, 35.247379],
          [-85.893394, 35.257333],
          [-85.90812, 35.276522],
          [-85.913593, 35.289189],
          [-85.876962, 35.524095],
          [-85.607326, 35.531651]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27137",
      state: "27",
      county: "137",
      name: "St. Louis County",
      censusArea: 6247.401,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.191501, 46.672586],
          [-92.197145, 46.663408],
          [-92.202292, 46.655041],
          [-92.207092, 46.651941],
          [-92.212392, 46.649941],
          [-92.221731, 46.649724],
          [-92.222338, 46.64971],
          [-92.228155, 46.649574],
          [-92.231944, 46.649486],
          [-92.232647, 46.64947],
          [-92.234815, 46.64942],
          [-92.242493, 46.649241],
          [-92.250861, 46.650061],
          [-92.254379, 46.650405],
          [-92.256654, 46.650628],
          [-92.262503, 46.651201],
          [-92.264462, 46.651393],
          [-92.269948, 46.65193],
          [-92.274465, 46.654227],
          [-92.276373, 46.655198],
          [-92.277155, 46.655596],
          [-92.292192, 46.663242],
          [-92.301676, 46.764134],
          [-93.061529, 46.76655],
          [-93.055943, 47.026415],
          [-93.0695, 47.393915],
          [-93.061253, 47.720187],
          [-93.081158, 47.89187],
          [-93.096488, 48.067826],
          [-93.088438, 48.627597],
          [-92.980484, 48.624915],
          [-92.728046, 48.53929],
          [-92.657881, 48.546263],
          [-92.634931, 48.542873],
          [-92.625739, 48.518189],
          [-92.625151, 48.513048],
          [-92.625374, 48.512916],
          [-92.631117, 48.508252],
          [-92.631137, 48.508077],
          [-92.627237, 48.503383],
          [-92.636696, 48.499428],
          [-92.661418, 48.496557],
          [-92.684866, 48.497611],
          [-92.698824, 48.494892],
          [-92.712562, 48.463013],
          [-92.687998, 48.443889],
          [-92.656027, 48.436709],
          [-92.575636, 48.440827],
          [-92.537202, 48.447703],
          [-92.507285, 48.447875],
          [-92.456325, 48.414204],
          [-92.415121, 48.293841],
          [-92.384387, 48.242914],
          [-92.325304, 48.23703],
          [-92.295053, 48.276587],
          [-92.295668, 48.278118],
          [-92.301451, 48.288608],
          [-92.306309, 48.316442],
          [-92.304561, 48.322977],
          [-92.288994, 48.342991],
          [-92.26228, 48.354933],
          [-92.055228, 48.359213],
          [-92.030872, 48.325824],
          [-91.989545, 48.260214],
          [-91.977555, 48.24714],
          [-91.977486, 48.24634],
          [-91.954432, 48.251678],
          [-91.954397, 48.251199],
          [-91.907597, 48.238183],
          [-91.906967, 48.23777],
          [-91.867641, 48.218341],
          [-91.798268, 48.200086],
          [-91.799114, 47.732178],
          [-91.787928, 47.546817],
          [-91.794039, 46.939676],
          [-91.806851, 46.933727],
          [-91.826068, 46.927199],
          [-91.834852, 46.927135],
          [-91.841349, 46.925215],
          [-91.871286, 46.908352],
          [-91.883238, 46.905728],
          [-91.906483, 46.891236],
          [-91.914984, 46.883836],
          [-91.952985, 46.867037],
          [-91.985086, 46.849637],
          [-91.997987, 46.838737],
          [-92.013405, 46.833727],
          [-92.058888, 46.809938],
          [-92.062088, 46.804038],
          [-92.086089, 46.794339],
          [-92.094089, 46.787839],
          [-92.088289, 46.773639],
          [-92.06449, 46.745439],
          [-92.025789, 46.710839],
          [-92.01529, 46.706469],
          [-92.020289, 46.704039],
          [-92.02472, 46.705624],
          [-92.03399, 46.708939],
          [-92.08949, 46.74924],
          [-92.11659, 46.74864],
          [-92.170413, 46.725553],
          [-92.172997, 46.724444],
          [-92.189091, 46.717541],
          [-92.195321, 46.709626],
          [-92.196264, 46.703966],
          [-92.196435, 46.702938],
          [-92.196575, 46.7021],
          [-92.194562, 46.697672],
          [-92.194087, 46.696626],
          [-92.193439, 46.695202],
          [-92.187795, 46.690185],
          [-92.187592, 46.678941],
          [-92.191075, 46.673278],
          [-92.191501, 46.672586]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23015",
      state: "23",
      county: "015",
      name: "Lincoln County",
      censusArea: 455.818,
      stateName: "Maine"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-69.307908, 43.773767],
            [-69.306751, 43.775095],
            [-69.302995, 43.774591],
            [-69.300376, 43.772144],
            [-69.300818, 43.768599],
            [-69.314325, 43.756707],
            [-69.322559, 43.755883],
            [-69.323569, 43.758994],
            [-69.321141, 43.765763],
            [-69.313631, 43.772762],
            [-69.307908, 43.773767]
          ]
        ],
        [
          [
            [-69.42792, 43.928798],
            [-69.423323, 43.922871],
            [-69.422153, 43.917702],
            [-69.423324, 43.915507],
            [-69.438066, 43.909539],
            [-69.440913, 43.909767],
            [-69.441894, 43.916331],
            [-69.433762, 43.949353],
            [-69.429806, 43.948438],
            [-69.421072, 43.938261],
            [-69.42792, 43.928798]
          ]
        ],
        [
          [
            [-69.528223, 44.275447],
            [-69.321529, 44.1729],
            [-69.281839, 44.022532],
            [-69.321808, 43.969975],
            [-69.331411, 43.974311],
            [-69.351961, 43.974748],
            [-69.366702, 43.964755],
            [-69.375478, 43.964584],
            [-69.388059, 43.96434],
            [-69.398455, 43.971804],
            [-69.416165, 43.977267],
            [-69.42876, 43.957929],
            [-69.431686, 43.964546],
            [-69.436495, 43.966878],
            [-69.441596, 43.964254],
            [-69.45107, 43.941955],
            [-69.459637, 43.903316],
            [-69.483498, 43.88028],
            [-69.486243, 43.869118],
            [-69.50329, 43.837673],
            [-69.514889, 43.831298],
            [-69.516212, 43.837222],
            [-69.513267, 43.84479],
            [-69.520301, 43.868498],
            [-69.524673, 43.875639],
            [-69.543912, 43.881615],
            [-69.54945, 43.880012],
            [-69.550908, 43.877971],
            [-69.550616, 43.872579],
            [-69.545028, 43.861241],
            [-69.552606, 43.841347],
            [-69.558122, 43.84066],
            [-69.568325, 43.844449],
            [-69.572697, 43.844012],
            [-69.575466, 43.841972],
            [-69.578527, 43.823316],
            [-69.588551, 43.81836],
            [-69.604179, 43.813551],
            [-69.605928, 43.814862],
            [-69.604616, 43.825793],
            [-69.598495, 43.825502],
            [-69.592373, 43.830895],
            [-69.589167, 43.851299],
            [-69.594705, 43.858878],
            [-69.604616, 43.858004],
            [-69.613215, 43.845032],
            [-69.613069, 43.837453],
            [-69.61511, 43.831623],
            [-69.621086, 43.826814],
            [-69.630268, 43.837016],
            [-69.629685, 43.843429],
            [-69.634932, 43.845907],
            [-69.649798, 43.836287],
            [-69.65315, 43.817194],
            [-69.650818, 43.803785],
            [-69.653337, 43.79103],
            [-69.664922, 43.791033],
            [-69.685473, 43.816328],
            [-69.685579, 43.820546],
            [-69.692429, 43.824336],
            [-69.697239, 43.825065],
            [-69.697858, 43.824918],
            [-69.697236, 43.832721],
            [-69.689652, 43.843814],
            [-69.692076, 43.857175],
            [-69.743634, 43.893256],
            [-69.700097, 44.006685],
            [-69.813549, 44.026011],
            [-69.758208, 44.139334],
            [-69.666298, 44.124003],
            [-69.656742, 44.122392],
            [-69.656942, 44.125461],
            [-69.665441, 44.227576],
            [-69.650171, 44.279625],
            [-69.547398, 44.260988],
            [-69.542176, 44.262737],
            [-69.534837, 44.267363],
            [-69.534405, 44.272213],
            [-69.528223, 44.275447]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23021",
      state: "23",
      county: "021",
      name: "Piscataquis County",
      censusArea: 3960.856,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-69.232021, 45.090166],
          [-69.355667, 45.073469],
          [-69.499861, 45.053778],
          [-69.620967, 45.010956],
          [-69.779928, 45.542766],
          [-69.691806, 45.645031],
          [-69.832818, 45.738734],
          [-69.784853, 45.785212],
          [-69.732579, 45.755978],
          [-69.705743, 45.853975],
          [-69.646755, 45.863163],
          [-69.683564, 45.983632],
          [-69.728577, 45.976882],
          [-69.73232, 46.394008],
          [-69.721109, 46.574053],
          [-68.821414, 46.57251],
          [-68.819401, 46.395782],
          [-68.827039, 45.684826],
          [-68.95891, 45.662195],
          [-68.964653, 45.512367],
          [-68.857297, 45.527301],
          [-68.800906, 45.329148],
          [-68.776061, 45.240317],
          [-68.880923, 45.224947],
          [-68.856587, 45.142783],
          [-69.232021, 45.090166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23023",
      state: "23",
      county: "023",
      name: "Sagadahoc County",
      censusArea: 253.695,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.051824, 43.990982],
          [-70.005471, 44.124519],
          [-69.897783, 44.107662],
          [-69.854983, 44.167862],
          [-69.763566, 44.153217],
          [-69.758208, 44.139334],
          [-69.813549, 44.026011],
          [-69.700097, 44.006685],
          [-69.743634, 43.893256],
          [-69.692076, 43.857175],
          [-69.689652, 43.843814],
          [-69.697236, 43.832721],
          [-69.697858, 43.824918],
          [-69.705838, 43.823024],
          [-69.714873, 43.810264],
          [-69.717804, 43.801047],
          [-69.717074, 43.792403],
          [-69.719723, 43.786685],
          [-69.752801, 43.75594],
          [-69.761587, 43.757],
          [-69.780097, 43.755397],
          [-69.782429, 43.753794],
          [-69.782283, 43.75117],
          [-69.778494, 43.747089],
          [-69.778348, 43.744612],
          [-69.835323, 43.721125],
          [-69.838689, 43.70514],
          [-69.851297, 43.703581],
          [-69.855081, 43.704746],
          [-69.857927, 43.723915],
          [-69.855595, 43.73266],
          [-69.858947, 43.740531],
          [-69.868673, 43.742701],
          [-69.862155, 43.758962],
          [-69.869732, 43.775656],
          [-69.884066, 43.778035],
          [-69.88739, 43.777052],
          [-69.857032, 43.944742],
          [-69.976226, 43.915276],
          [-70.034238, 43.975661],
          [-70.036399, 43.978721],
          [-70.046586, 43.985642],
          [-70.047002, 43.988078],
          [-70.051824, 43.990982]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23031",
      state: "23",
      county: "031",
      name: "York County",
      censusArea: 990.713,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.536702, 43.335605],
          [-70.553854, 43.321886],
          [-70.562779, 43.310614],
          [-70.585184, 43.270113],
          [-70.593907, 43.249295],
          [-70.591022, 43.237851],
          [-70.575787, 43.221859],
          [-70.576692, 43.217651],
          [-70.587814, 43.199858],
          [-70.618973, 43.163625],
          [-70.634455, 43.127603],
          [-70.634311, 43.122162],
          [-70.638355, 43.114182],
          [-70.654213, 43.099065],
          [-70.655322, 43.098008],
          [-70.656223, 43.093164],
          [-70.665958, 43.076234],
          [-70.673114, 43.070314],
          [-70.703936, 43.059621],
          [-70.756397, 43.079988],
          [-70.784108, 43.098963],
          [-70.819549, 43.123231],
          [-70.8281, 43.129086],
          [-70.8338, 43.146886],
          [-70.829174, 43.180365],
          [-70.828301, 43.186685],
          [-70.816232, 43.234997],
          [-70.817773, 43.237408],
          [-70.817865, 43.237911],
          [-70.822959, 43.240187],
          [-70.823309, 43.240343],
          [-70.825071, 43.24093],
          [-70.826711, 43.241301],
          [-70.828022, 43.241597],
          [-70.83365, 43.242868],
          [-70.839717, 43.250393],
          [-70.839213, 43.251224],
          [-70.863231, 43.265098],
          [-70.86323, 43.265109],
          [-70.909805, 43.306682],
          [-70.91246, 43.308289],
          [-70.916751, 43.317731],
          [-70.930783, 43.329569],
          [-70.952356, 43.333983],
          [-70.967229, 43.343777],
          [-70.984335, 43.376128],
          [-70.984305, 43.376814],
          [-70.987733, 43.391513],
          [-70.986812, 43.414264],
          [-70.964433, 43.473276],
          [-70.964542, 43.473262],
          [-70.963742, 43.476248],
          [-70.954755, 43.509802],
          [-70.962556, 43.53431],
          [-70.958958, 43.537634],
          [-70.955337, 43.54098],
          [-70.955346, 43.540697],
          [-70.953322, 43.552718],
          [-70.972716, 43.570255],
          [-70.979985, 43.673195],
          [-70.980758, 43.684141],
          [-70.981946, 43.70096],
          [-70.981978, 43.701965],
          [-70.982238, 43.711865],
          [-70.982083, 43.715043],
          [-70.989041, 43.792152],
          [-70.782984, 43.814147],
          [-70.652986, 43.787544],
          [-70.659622, 43.710902],
          [-70.549965, 43.715796],
          [-70.457743, 43.642372],
          [-70.492009, 43.606769],
          [-70.353392, 43.535405],
          [-70.361214, 43.52919],
          [-70.379123, 43.507202],
          [-70.384885, 43.49604],
          [-70.385615, 43.487031],
          [-70.382928, 43.469674],
          [-70.380233, 43.46423],
          [-70.37223, 43.45508],
          [-70.349684, 43.442032],
          [-70.362015, 43.439077],
          [-70.370514, 43.434133],
          [-70.384949, 43.418839],
          [-70.383981, 43.41294],
          [-70.39089, 43.402607],
          [-70.400035, 43.399927],
          [-70.401666, 43.401262],
          [-70.406416, 43.400942],
          [-70.421282, 43.395777],
          [-70.427672, 43.389254],
          [-70.424421, 43.379656],
          [-70.424986, 43.375928],
          [-70.442438, 43.356487],
          [-70.460717, 43.34325],
          [-70.465975, 43.340246],
          [-70.472933, 43.343972],
          [-70.485312, 43.346391],
          [-70.517695, 43.344037],
          [-70.535244, 43.336771],
          [-70.536702, 43.335605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28041",
      state: "28",
      county: "041",
      name: "Greene County",
      censusArea: 712.755,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.834339, 30.997983],
          [-88.840929, 30.99841],
          [-88.841707, 31.433703],
          [-88.449446, 31.435837],
          [-88.44866, 31.421277],
          [-88.448686, 31.420888],
          [-88.445209, 31.355969],
          [-88.445182, 31.355855],
          [-88.438211, 31.231252],
          [-88.438104, 31.23006],
          [-88.432007, 31.114298],
          [-88.425807, 31.001123],
          [-88.425729, 31.000183],
          [-88.425635, 30.998301],
          [-88.809328, 30.997379],
          [-88.834339, 30.997983]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31149",
      state: "31",
      county: "149",
      name: "Rock County",
      censusArea: 1008.318,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.257039, 42.804296],
          [-99.245803, 42.782499],
          [-99.234629, 42.087995],
          [-99.662379, 42.086009],
          [-99.677684, 42.730903],
          [-99.537491, 42.717626],
          [-99.257039, 42.804296]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31151",
      state: "31",
      county: "151",
      name: "Saline County",
      censusArea: 574.018,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.913493, 40.697948],
          [-96.912637, 40.523625],
          [-96.913346, 40.351192],
          [-96.915083, 40.349734],
          [-97.368688, 40.350392],
          [-97.368401, 40.698625],
          [-96.913493, 40.697948]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31155",
      state: "31",
      county: "155",
      name: "Saunders County",
      censusArea: 750.234,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.905862, 41.453389],
          [-96.570351, 41.433426],
          [-96.47072, 41.393239],
          [-96.350598, 41.265192],
          [-96.327399, 41.189999],
          [-96.319191, 41.044998],
          [-96.463861, 41.016069],
          [-96.463869, 41.045083],
          [-96.908507, 41.046091],
          [-96.905862, 41.453389]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31161",
      state: "31",
      county: "161",
      name: "Sheridan County",
      censusArea: 2440.862,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.792111, 42.99998],
          [-102.487329, 42.999559],
          [-102.440547, 42.999609],
          [-102.082546, 42.999356],
          [-102.066689, 42.443463],
          [-102.038169, 42.443307],
          [-102.04028, 42.096744],
          [-102.006204, 42.096338],
          [-102.008562, 42.008901],
          [-102.06665, 42.009195],
          [-102.677582, 42.005282],
          [-102.697835, 42.004843],
          [-102.742269, 42.092388],
          [-102.749431, 42.439934],
          [-102.773366, 42.439922],
          [-102.773369, 42.785265],
          [-102.792111, 42.99998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31169",
      state: "31",
      county: "169",
      name: "Thayer County",
      censusArea: 573.807,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.415833, 40.002001],
          [-97.417826, 40.002024],
          [-97.425443, 40.002048],
          [-97.444662, 40.001958],
          [-97.463285, 40.002047],
          [-97.510264, 40.001835],
          [-97.511381, 40.001899],
          [-97.515308, 40.001901],
          [-97.767746, 40.001994],
          [-97.769204, 40.001995],
          [-97.770776, 40.001977],
          [-97.777155, 40.002167],
          [-97.819426, 40.001958],
          [-97.821496, 40.002002],
          [-97.820816, 40.350545],
          [-97.370694, 40.350312],
          [-97.368688, 40.350392],
          [-97.369199, 40.00206],
          [-97.369199, 40.00206],
          [-97.415833, 40.002001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31173",
      state: "31",
      county: "173",
      name: "Thurston County",
      censusArea: 393.584,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.822497, 42.264292],
          [-96.726188, 42.264104],
          [-96.726661, 42.278005],
          [-96.348814, 42.282024],
          [-96.328905, 42.254734],
          [-96.322868, 42.233637],
          [-96.323723, 42.229887],
          [-96.336323, 42.218922],
          [-96.356591, 42.215182],
          [-96.356666, 42.215077],
          [-96.35987, 42.210545],
          [-96.349688, 42.172043],
          [-96.344121, 42.162091],
          [-96.307421, 42.130707],
          [-96.279079, 42.074026],
          [-96.272877, 42.047238],
          [-96.309645, 42.015187],
          [-96.554866, 42.015875],
          [-96.555511, 42.089957],
          [-96.82367, 42.090411],
          [-96.822497, 42.264292]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31181",
      state: "31",
      county: "181",
      name: "Webster County",
      censusArea: 574.913,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.523053, 40.002336],
          [-98.543186, 40.002285],
          [-98.560578, 40.002274],
          [-98.575219, 40.00248],
          [-98.593342, 40.002476],
          [-98.613755, 40.0024],
          [-98.64071, 40.002493],
          [-98.652494, 40.002245],
          [-98.653833, 40.002269],
          [-98.669724, 40.00241],
          [-98.672819, 40.002364],
          [-98.690287, 40.002548],
          [-98.691443, 40.002505],
          [-98.693096, 40.002373],
          [-98.710404, 40.00218],
          [-98.726295, 40.002222],
          [-98.726373, 40.002222],
          [-98.726827, 40.350398],
          [-98.723948, 40.350391],
          [-98.27809, 40.350347],
          [-98.273571, 40.350359],
          [-98.273776, 40.005385],
          [-98.274017, 40.002516],
          [-98.490533, 40.002323],
          [-98.504455, 40.002329],
          [-98.523053, 40.002336]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32005",
      state: "32",
      county: "005",
      name: "Douglas County",
      censusArea: 709.719,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.55102, 39.085791],
          [-119.311714, 39.083615],
          [-119.310702, 38.990719],
          [-119.401648, 38.983323],
          [-119.439534, 38.882001],
          [-119.403908, 38.809111],
          [-119.406279, 38.733362],
          [-119.349884, 38.729193],
          [-119.328498, 38.534648],
          [-119.450623, 38.619965],
          [-119.450612, 38.619964],
          [-119.494022, 38.649734],
          [-119.494183, 38.649852],
          [-119.585437, 38.713212],
          [-119.587066, 38.714345],
          [-119.587679, 38.714734],
          [-119.904315, 38.933324],
          [-120.001014, 38.999574],
          [-120.002461, 39.067489],
          [-120.003402, 39.112687],
          [-119.761213, 39.114009],
          [-119.752116, 39.084904],
          [-119.55102, 39.085791]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32007",
      state: "32",
      county: "007",
      name: "Elko County",
      censusArea: 17169.834,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.041723, 41.99372],
          [-114.041107, 41.850573],
          [-114.041152, 41.850595],
          [-114.039882, 41.741991],
          [-114.042145, 40.999926],
          [-114.043803, 40.759205],
          [-114.043831, 40.758666],
          [-114.043505, 40.726292],
          [-114.045281, 40.506586],
          [-114.045577, 40.495801],
          [-114.045518, 40.494474],
          [-114.046555, 40.116931],
          [-115.263729, 40.122543],
          [-115.834969, 40.127842],
          [-116.000897, 40.127376],
          [-116.157834, 40.666389],
          [-116.158155, 40.999912],
          [-116.586786, 41.000398],
          [-117.018419, 41.000254],
          [-117.018294, 41.999358],
          [-116.62677, 41.99775],
          [-116.586937, 41.99737],
          [-116.510452, 41.997096],
          [-116.485823, 41.996861],
          [-116.483094, 41.996885],
          [-116.463528, 41.996547],
          [-116.163931, 41.997555],
          [-116.160833, 41.997508],
          [-116.038602, 41.99746],
          [-116.03857, 41.997413],
          [-116.030754, 41.997399],
          [-116.030758, 41.997383],
          [-116.01896, 41.997762],
          [-116.018945, 41.997722],
          [-116.012219, 41.998048],
          [-116.012212, 41.998035],
          [-115.98688, 41.998534],
          [-115.038256, 41.996025],
          [-115.031783, 41.996008],
          [-114.914187, 41.999909],
          [-114.763825, 41.999909],
          [-114.598267, 41.994511],
          [-114.498259, 41.994599],
          [-114.498243, 41.994636],
          [-114.281854, 41.994264],
          [-114.107428, 41.993965],
          [-114.107259, 41.993831],
          [-114.061763, 41.993939],
          [-114.061774, 41.993797],
          [-114.048257, 41.993814],
          [-114.048246, 41.993721],
          [-114.041723, 41.99372]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32013",
      state: "32",
      county: "013",
      name: "Humboldt County",
      censusArea: 9640.757,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.777228, 41.992671],
          [-118.775869, 41.992692],
          [-118.197189, 41.996995],
          [-117.026222, 42.000252],
          [-117.018294, 41.999358],
          [-117.018419, 41.000254],
          [-117.018664, 40.643035],
          [-117.246788, 40.642445],
          [-117.300597, 40.526136],
          [-117.301168, 40.682348],
          [-117.644399, 40.684343],
          [-117.64422, 40.857824],
          [-118.786738, 40.855929],
          [-118.786705, 40.96085],
          [-119.309598, 40.960378],
          [-119.329791, 40.960394],
          [-119.331068, 41.235118],
          [-119.305713, 41.234542],
          [-119.304635, 41.414695],
          [-119.323955, 41.414519],
          [-119.324182, 41.994278],
          [-118.777228, 41.992671]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32017",
      state: "32",
      county: "017",
      name: "Lincoln County",
      censusArea: 10633.202,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.049893, 38.677365],
          [-114.050154, 38.57292],
          [-114.049862, 38.547764],
          [-114.049834, 38.543784],
          [-114.050091, 38.404673],
          [-114.05012, 38.404536],
          [-114.049903, 38.148601],
          [-114.050423, 37.999961],
          [-114.048473, 37.809861],
          [-114.051109, 37.756276],
          [-114.05167, 37.746958],
          [-114.051785, 37.746249],
          [-114.051728, 37.745997],
          [-114.052472, 37.604776],
          [-114.052689, 37.517859],
          [-114.052718, 37.517264],
          [-114.052685, 37.502513],
          [-114.052701, 37.492014],
          [-114.051927, 37.370734],
          [-114.051927, 37.370459],
          [-114.0518, 37.293548],
          [-114.0518, 37.293044],
          [-114.051974, 37.284511],
          [-114.051974, 37.283848],
          [-114.051822, 37.090976],
          [-114.051749, 37.088434],
          [-114.0506, 37.000396],
          [-114.050583, 36.843141],
          [-114.762301, 36.843856],
          [-114.762507, 36.853473],
          [-115.740846, 36.853084],
          [-115.896925, 36.842085],
          [-115.89446, 36.861717],
          [-115.893857, 38.050531],
          [-115.000846, 38.050757],
          [-115.000846, 38.677322],
          [-114.049893, 38.677365]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32023",
      state: "32",
      county: "023",
      name: "Nye County",
      censusArea: 18181.924,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.845805, 36.120242],
          [-115.84608, 35.963596],
          [-115.892975, 35.999967],
          [-115.912858, 36.015359],
          [-116.093601, 36.155805],
          [-116.097216, 36.158346],
          [-116.488233, 36.459097],
          [-117.000895, 36.847694],
          [-117.166187, 36.970862],
          [-117.165612, 38.001692],
          [-117.219264, 38.050348],
          [-117.241063, 38.050116],
          [-117.24091, 38.069902],
          [-117.691021, 38.473797],
          [-118.196235, 38.919058],
          [-118.196274, 38.999926],
          [-117.865116, 39.073654],
          [-117.775468, 39.093425],
          [-117.331857, 39.16343],
          [-116.600946, 39.161463],
          [-115.907071, 39.161848],
          [-115.601585, 38.999933],
          [-115.000846, 38.677322],
          [-115.000846, 38.050757],
          [-115.893857, 38.050531],
          [-115.89446, 36.861717],
          [-115.896925, 36.842085],
          [-115.895368, 36.580168],
          [-115.895753, 36.172227],
          [-115.845762, 36.170764],
          [-115.845795, 36.123963],
          [-115.844505, 36.122179],
          [-115.845809, 36.121389],
          [-115.845805, 36.120242]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23011",
      state: "23",
      county: "011",
      name: "Kennebec County",
      censusArea: 867.524,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-69.792813, 44.57733],
          [-69.776294, 44.608236],
          [-69.744282, 44.602607],
          [-69.742091, 44.598246],
          [-69.605746, 44.577264],
          [-69.579094, 44.626971],
          [-69.62848, 44.671969],
          [-69.632971, 44.698763],
          [-69.471712, 44.692899],
          [-69.475599, 44.676678],
          [-69.442936, 44.672381],
          [-69.460558, 44.655611],
          [-69.460606, 44.647054],
          [-69.393123, 44.640375],
          [-69.395987, 44.457078],
          [-69.4354, 44.462422],
          [-69.445498, 44.463839],
          [-69.506382, 44.34287],
          [-69.521258, 44.280683],
          [-69.528223, 44.275447],
          [-69.534405, 44.272213],
          [-69.534837, 44.267363],
          [-69.542176, 44.262737],
          [-69.547398, 44.260988],
          [-69.650171, 44.279625],
          [-69.665441, 44.227576],
          [-69.656942, 44.125461],
          [-69.656742, 44.122392],
          [-69.666298, 44.124003],
          [-69.758208, 44.139334],
          [-69.763566, 44.153217],
          [-69.854983, 44.167862],
          [-69.897783, 44.107662],
          [-70.005471, 44.124519],
          [-70.027386, 44.130577],
          [-70.010329, 44.168651],
          [-69.993792, 44.180594],
          [-70.075747, 44.206168],
          [-70.07426, 44.290154],
          [-70.100491, 44.383854],
          [-70.12939, 44.487217],
          [-70.04235, 44.485235],
          [-70.030536, 44.508786],
          [-69.944082, 44.537828],
          [-69.930451, 44.610852],
          [-69.792813, 44.57733]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39105",
      state: "39",
      county: "105",
      name: "Meigs County",
      censusArea: 430.098,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.749689, 39.180742],
          [-81.747253, 39.095379],
          [-81.747253, 39.095378],
          [-81.812355, 39.082078],
          [-81.814155, 39.073478],
          [-81.811655, 39.059578],
          [-81.803355, 39.047678],
          [-81.793304, 39.040353],
          [-81.772854, 39.026179],
          [-81.764253, 39.015279],
          [-81.756131, 38.933545],
          [-81.759995, 38.925828],
          [-81.874857, 38.881174],
          [-81.908645, 38.87846],
          [-81.926967, 38.891602],
          [-81.928352, 38.895371],
          [-81.926671, 38.901311],
          [-81.90091, 38.924338],
          [-81.89847, 38.929603],
          [-81.900595, 38.937671],
          [-81.919783, 38.968494],
          [-81.935619, 38.9895],
          [-82.002261, 39.027878],
          [-82.017562, 39.030078],
          [-82.035963, 39.025478],
          [-82.041563, 39.017878],
          [-82.045663, 39.003778],
          [-82.051563, 38.994378],
          [-82.093165, 38.97098],
          [-82.098762, 38.958288],
          [-82.095465, 39.002778],
          [-82.322874, 39.027674],
          [-82.306669, 39.205494],
          [-82.268583, 39.20376],
          [-81.749689, 39.180742]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39107",
      state: "39",
      county: "107",
      name: "Mercer County",
      censusArea: 462.449,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.455713, 40.567888],
          [-84.455418, 40.358589],
          [-84.434387, 40.354534],
          [-84.431632, 40.354198],
          [-84.434631, 40.354259],
          [-84.804119, 40.352757],
          [-84.804119, 40.352844],
          [-84.803068, 40.465388],
          [-84.802483, 40.528046],
          [-84.802265, 40.572212],
          [-84.802265, 40.572215],
          [-84.802135, 40.644859],
          [-84.802193, 40.660298],
          [-84.80222, 40.674776],
          [-84.802157, 40.689324],
          [-84.802127, 40.691405],
          [-84.802094, 40.702476],
          [-84.802181, 40.718602],
          [-84.802119, 40.728146],
          [-84.456172, 40.728306],
          [-84.45618, 40.684862],
          [-84.455713, 40.567888]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39117",
      state: "39",
      county: "117",
      name: "Morrow County",
      censusArea: 406.079,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.727164, 40.711203],
          [-82.627191, 40.709416],
          [-82.623609, 40.549879],
          [-82.646503, 40.345076],
          [-82.744931, 40.349603],
          [-82.929588, 40.358123],
          [-82.924781, 40.415004],
          [-83.020798, 40.433795],
          [-82.958401, 40.490664],
          [-82.957817, 40.645148],
          [-82.859753, 40.646395],
          [-82.858302, 40.705019],
          [-82.727164, 40.711203]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39119",
      state: "39",
      county: "119",
      name: "Muskingum County",
      censusArea: 664.579,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.076639, 39.77096],
          [-82.072934, 39.816227],
          [-82.170047, 39.82075],
          [-82.162372, 39.909374],
          [-82.233974, 39.91326],
          [-82.198772, 39.95014],
          [-82.187105, 40.16688],
          [-81.716868, 40.152863],
          [-81.716276, 40.152166],
          [-81.728611, 39.931728],
          [-81.694146, 39.842636],
          [-81.697442, 39.755572],
          [-82.076639, 39.77096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39125",
      state: "39",
      county: "125",
      name: "Paulding County",
      censusArea: 416.438,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.341602, 41.165524],
          [-84.341911, 40.990692],
          [-84.399486, 40.990315],
          [-84.803313, 40.989394],
          [-84.803374, 41.089302],
          [-84.803378, 41.096867],
          [-84.803413, 41.164649],
          [-84.803594, 41.173203],
          [-84.803472, 41.173889],
          [-84.803492, 41.252531],
          [-84.803492, 41.252562],
          [-84.457217, 41.253485],
          [-84.457059, 41.209434],
          [-84.341878, 41.209197],
          [-84.341602, 41.165524]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39129",
      state: "39",
      county: "129",
      name: "Pickaway County",
      censusArea: 501.32,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.740729, 39.46835],
          [-82.998204, 39.480061],
          [-82.98456, 39.506754],
          [-83.266737, 39.516249],
          [-83.252435, 39.695438],
          [-83.243702, 39.812503],
          [-83.013071, 39.804394],
          [-82.824254, 39.794996],
          [-82.842953, 39.56148],
          [-82.731518, 39.554445],
          [-82.740729, 39.46835]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39133",
      state: "39",
      county: "133",
      name: "Portage County",
      censusArea: 487.381,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.393429, 40.988528],
          [-81.392114, 41.135722],
          [-81.392357, 41.136952],
          [-81.391831, 41.276654],
          [-81.391593, 41.33155],
          [-81.391933, 41.336635],
          [-81.391686, 41.3413],
          [-81.391694, 41.348272],
          [-81.003319, 41.34786],
          [-81.00288, 41.271842],
          [-81.00229, 41.134189],
          [-81.001695, 40.987783],
          [-81.086817, 40.988158],
          [-81.393429, 40.988528]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39135",
      state: "39",
      county: "135",
      name: "Preble County",
      censusArea: 424.12,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.814189, 39.785569],
          [-84.814179, 39.786853],
          [-84.814209, 39.799755],
          [-84.81412, 39.811398],
          [-84.814179, 39.814212],
          [-84.813852, 39.824621],
          [-84.813793, 39.826771],
          [-84.813703, 39.843059],
          [-84.813674, 39.843173],
          [-84.813549, 39.850773],
          [-84.813464, 39.853261],
          [-84.81305, 39.872958],
          [-84.812787, 39.89083],
          [-84.812698, 39.891585],
          [-84.812411, 39.916915],
          [-84.485367, 39.918491],
          [-84.479213, 39.591024],
          [-84.478927, 39.56879],
          [-84.815036, 39.567695],
          [-84.815156, 39.568351],
          [-84.814705, 39.628854],
          [-84.814619, 39.669174],
          [-84.81453, 39.680429],
          [-84.814129, 39.726556],
          [-84.814129, 39.72662],
          [-84.814189, 39.785569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39139",
      state: "39",
      county: "139",
      name: "Richland County",
      censusArea: 495.269,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.41648, 40.902252],
          [-82.378397, 40.727145],
          [-82.339211, 40.726683],
          [-82.336962, 40.555001],
          [-82.374898, 40.550871],
          [-82.623609, 40.549879],
          [-82.627191, 40.709416],
          [-82.727164, 40.711203],
          [-82.724792, 40.995638],
          [-82.432852, 40.992944],
          [-82.4179, 40.992938],
          [-82.41648, 40.902252]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39141",
      state: "39",
      county: "141",
      name: "Ross County",
      censusArea: 689.188,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.998204, 39.480061],
          [-82.740729, 39.46835],
          [-82.740771, 39.466029],
          [-82.748591, 39.368165],
          [-82.762892, 39.207967],
          [-82.766692, 39.167768],
          [-82.785891, 39.168769],
          [-83.353531, 39.197585],
          [-83.393511, 39.267272],
          [-83.372714, 39.377416],
          [-83.317496, 39.449993],
          [-83.266737, 39.516249],
          [-82.98456, 39.506754],
          [-82.998204, 39.480061]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39149",
      state: "39",
      county: "149",
      name: "Shelby County",
      censusArea: 407.675,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.022919, 40.183945],
          [-84.432575, 40.197037],
          [-84.434631, 40.354259],
          [-84.431632, 40.354198],
          [-84.434387, 40.354534],
          [-84.338882, 40.378734],
          [-84.339088, 40.481281],
          [-84.002372, 40.483115],
          [-84.014763, 40.273459],
          [-84.022919, 40.183945]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39151",
      state: "39",
      county: "151",
      name: "Stark County",
      censusArea: 575.271,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.467584, 40.907965],
          [-81.420418, 40.906504],
          [-81.393429, 40.988528],
          [-81.086817, 40.988158],
          [-81.086919, 40.981164],
          [-81.086719, 40.944945],
          [-81.086601, 40.944758],
          [-81.086614, 40.940774],
          [-81.086918, 40.939658],
          [-81.086601, 40.937622],
          [-81.086642, 40.932189],
          [-81.086688, 40.931737],
          [-81.086679, 40.901609],
          [-81.087289, 40.727816],
          [-81.237227, 40.723542],
          [-81.24125, 40.650534],
          [-81.317739, 40.651579],
          [-81.649199, 40.635106],
          [-81.650048, 40.65526],
          [-81.650045, 40.668117],
          [-81.647736, 40.914386],
          [-81.64595, 40.914257],
          [-81.467584, 40.907965]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39157",
      state: "39",
      county: "157",
      name: "Tuscarawas County",
      censusArea: 567.636,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.649199, 40.635106],
          [-81.317739, 40.651579],
          [-81.321659, 40.566648],
          [-81.264744, 40.565042],
          [-81.269951, 40.433486],
          [-81.275319, 40.303434],
          [-81.334601, 40.304316],
          [-81.338057, 40.214253],
          [-81.622683, 40.221308],
          [-81.61603, 40.368118],
          [-81.712288, 40.370804],
          [-81.709132, 40.444775],
          [-81.669654, 40.443866],
          [-81.649199, 40.635106]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39161",
      state: "39",
      county: "161",
      name: "Van Wert County",
      censusArea: 409.158,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.340531, 40.859099],
          [-84.397374, 40.815941],
          [-84.396778, 40.684926],
          [-84.45618, 40.684862],
          [-84.456172, 40.728306],
          [-84.802119, 40.728146],
          [-84.802119, 40.728163],
          [-84.802266, 40.742298],
          [-84.802538, 40.765515],
          [-84.802935, 40.922377],
          [-84.802936, 40.922568],
          [-84.803313, 40.989209],
          [-84.803313, 40.989394],
          [-84.399486, 40.990315],
          [-84.398407, 40.903265],
          [-84.340531, 40.859099]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47065",
      state: "47",
      county: "065",
      name: "Hamilton County",
      censusArea: 542.431,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.135191, 35.458649],
          [-85.016343, 35.409288],
          [-84.94635, 35.287788],
          [-85.024043, 35.148829],
          [-84.976973, 34.987669],
          [-84.97986, 34.987647],
          [-85.045052, 34.986859],
          [-85.045183, 34.986883],
          [-85.180553, 34.986075],
          [-85.185905, 34.985995],
          [-85.216554, 34.985675],
          [-85.217854, 34.985675],
          [-85.220554, 34.985575],
          [-85.221854, 34.985475],
          [-85.230354, 34.985475],
          [-85.235555, 34.985475],
          [-85.254955, 34.985175],
          [-85.265055, 34.985075],
          [-85.275856, 34.984975],
          [-85.277556, 34.984975],
          [-85.2945, 34.984651],
          [-85.301488, 34.984475],
          [-85.305457, 34.984475],
          [-85.308257, 34.984375],
          [-85.363919, 34.983375],
          [-85.474472, 34.983972],
          [-85.361959, 35.087773],
          [-85.387089, 35.147169],
          [-85.246005, 35.319077],
          [-85.225877, 35.354276],
          [-85.135191, 35.458649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47067",
      state: "47",
      county: "067",
      name: "Hancock County",
      censusArea: 222.34,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.985087, 36.593829],
          [-82.830433, 36.593761],
          [-82.950519, 36.543005],
          [-83.010789, 36.563691],
          [-83.280595, 36.394689],
          [-83.3868, 36.413186],
          [-83.398303, 36.534885],
          [-83.472108, 36.597284],
          [-83.2763, 36.598187],
          [-83.250304, 36.593935],
          [-83.249899, 36.593898],
          [-83.248933, 36.593827],
          [-83.028357, 36.593893],
          [-83.02725, 36.593847],
          [-82.985087, 36.593829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47071",
      state: "47",
      county: "071",
      name: "Hardin County",
      censusArea: 577.318,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.258111, 34.995463],
          [-88.363531, 34.99559],
          [-88.380508, 34.99561],
          [-88.362572, 35.380994],
          [-88.361929, 35.397044],
          [-88.3555, 35.396994],
          [-88.351765, 35.406735],
          [-88.361521, 35.408877],
          [-88.360829, 35.418972],
          [-88.241677, 35.423263],
          [-88.199643, 35.38257],
          [-88.024741, 35.392307],
          [-87.981585, 35.295678],
          [-87.984916, 35.006256],
          [-88.18245, 35.007712],
          [-88.200064, 34.995634],
          [-88.253825, 34.995553],
          [-88.258111, 34.995463]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47077",
      state: "47",
      county: "077",
      name: "Henderson County",
      censusArea: 520.073,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.241677, 35.423263],
          [-88.360829, 35.418972],
          [-88.364004, 35.492801],
          [-88.505354, 35.51203],
          [-88.613611, 35.588089],
          [-88.606862, 35.789354],
          [-88.569047, 35.824829],
          [-88.179177, 35.816785],
          [-88.190465, 35.609088],
          [-88.243057, 35.528858],
          [-88.241677, 35.423263]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47083",
      state: "47",
      county: "083",
      name: "Houston County",
      censusArea: 200.286,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.949603, 36.24316],
          [-87.98074, 36.353307],
          [-87.821816, 36.328724],
          [-87.715494, 36.36824],
          [-87.592358, 36.367663],
          [-87.586957, 36.34178],
          [-87.513533, 36.334713],
          [-87.566798, 36.177857],
          [-87.798508, 36.244754],
          [-87.949603, 36.24316]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47087",
      state: "47",
      county: "087",
      name: "Jackson County",
      censusArea: 308.32,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.828026, 36.411836],
          [-85.827782, 36.413016],
          [-85.821032, 36.4554],
          [-85.820864, 36.455925],
          [-85.813885, 36.499473],
          [-85.704075, 36.522395],
          [-85.495422, 36.403074],
          [-85.497587, 36.375051],
          [-85.495124, 36.302278],
          [-85.533697, 36.242577],
          [-85.780148, 36.238011],
          [-85.846483, 36.288167],
          [-85.828026, 36.411836]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47095",
      state: "47",
      county: "095",
      name: "Lake County",
      censusArea: 165.784,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.346056, 36.50321],
          [-89.36012, 36.409277],
          [-89.443726, 36.236637],
          [-89.482971, 36.212659],
          [-89.629452, 36.185382],
          [-89.699817, 36.248384],
          [-89.691308, 36.252079],
          [-89.678046, 36.248284],
          [-89.602374, 36.238106],
          [-89.589561, 36.239116],
          [-89.541621, 36.247891],
          [-89.534745, 36.252576],
          [-89.535529, 36.270541],
          [-89.539487, 36.277368],
          [-89.544797, 36.280458],
          [-89.554289, 36.277751],
          [-89.578492, 36.288317],
          [-89.611819, 36.309088],
          [-89.620255, 36.323006],
          [-89.6198, 36.329546],
          [-89.615841, 36.336085],
          [-89.610689, 36.340442],
          [-89.605668, 36.342234],
          [-89.581636, 36.342357],
          [-89.560439, 36.337746],
          [-89.545006, 36.336809],
          [-89.531822, 36.339246],
          [-89.519, 36.3486],
          [-89.513178, 36.359897],
          [-89.509558, 36.375065],
          [-89.519501, 36.475419],
          [-89.522674, 36.481305],
          [-89.5391, 36.498201],
          [-89.498036, 36.497887],
          [-89.492537, 36.497775],
          [-89.485106, 36.497692],
          [-89.493495, 36.4787],
          [-89.493198, 36.470124],
          [-89.486215, 36.46162],
          [-89.471718, 36.457001],
          [-89.460436, 36.45814],
          [-89.448468, 36.46442],
          [-89.429311, 36.481875],
          [-89.417293, 36.499033],
          [-89.403913, 36.499141],
          [-89.381792, 36.500062],
          [-89.380085, 36.500416],
          [-89.356593, 36.502195],
          [-89.346056, 36.50321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47101",
      state: "47",
      county: "101",
      name: "Lewis County",
      censusArea: 282.089,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.339232, 35.65911],
          [-87.289872, 35.548025],
          [-87.294534, 35.444676],
          [-87.448485, 35.457844],
          [-87.44882, 35.420622],
          [-87.575352, 35.398454],
          [-87.717559, 35.483347],
          [-87.658189, 35.609349],
          [-87.586867, 35.653304],
          [-87.459587, 35.615124],
          [-87.339232, 35.65911]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47107",
      state: "47",
      county: "107",
      name: "Mc Minn County",
      censusArea: 430.125,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.52585, 35.624186],
          [-84.409226, 35.398378],
          [-84.401025, 35.330374],
          [-84.495529, 35.285472],
          [-84.703607, 35.241043],
          [-84.860164, 35.350074],
          [-84.814543, 35.43697],
          [-84.619865, 35.644651],
          [-84.584472, 35.644424],
          [-84.584296, 35.633086],
          [-84.566915, 35.63324],
          [-84.566934, 35.629773],
          [-84.557872, 35.629651],
          [-84.557926, 35.636973],
          [-84.54923, 35.636896],
          [-84.547603, 35.641327],
          [-84.535386, 35.633696],
          [-84.534006, 35.629555],
          [-84.525984, 35.628113],
          [-84.52585, 35.624186]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47109",
      state: "47",
      county: "109",
      name: "Mc Nairy County",
      censusArea: 562.86,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.781768, 35.247587],
          [-88.719761, 35.24755],
          [-88.640934, 35.361017],
          [-88.362572, 35.380994],
          [-88.380508, 34.99561],
          [-88.469801, 34.996052],
          [-88.469877, 34.996033],
          [-88.786612, 34.995252],
          [-88.786924, 35.115809],
          [-88.781768, 35.247587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47111",
      state: "47",
      county: "111",
      name: "Macon County",
      censusArea: 307.144,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.788645, 36.621846],
          [-85.788613, 36.621845],
          [-85.813885, 36.499473],
          [-85.820864, 36.455925],
          [-85.821032, 36.4554],
          [-85.827782, 36.413016],
          [-85.828026, 36.411836],
          [-85.978893, 36.426073],
          [-86.168261, 36.444293],
          [-86.228434, 36.487472],
          [-86.205468, 36.639783],
          [-86.204859, 36.639741],
          [-86.197573, 36.639363],
          [-86.081944, 36.633848],
          [-86.080666, 36.63394],
          [-86.038366, 36.630804],
          [-86.033139, 36.630413],
          [-86.03277, 36.630367],
          [-85.976421, 36.6284],
          [-85.788645, 36.621846]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47119",
      state: "47",
      county: "119",
      name: "Maury County",
      censusArea: 613.138,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.215099, 35.850651],
          [-86.953593, 35.781904],
          [-86.782016, 35.706595],
          [-86.849573, 35.525515],
          [-86.960804, 35.417021],
          [-87.120883, 35.45566],
          [-87.206578, 35.433207],
          [-87.226311, 35.440509],
          [-87.227932, 35.429332],
          [-87.295242, 35.442128],
          [-87.294534, 35.444676],
          [-87.289872, 35.548025],
          [-87.339232, 35.65911],
          [-87.234143, 35.724691],
          [-87.215099, 35.850651]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47125",
      state: "47",
      county: "125",
      name: "Montgomery County",
      censusArea: 539.177,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.602597, 36.418979],
          [-87.60342, 36.4259],
          [-87.641146, 36.638036],
          [-87.564928, 36.639113],
          [-87.563052, 36.639113],
          [-87.436509, 36.640747],
          [-87.425009, 36.641047],
          [-87.414309, 36.641047],
          [-87.347796, 36.64144],
          [-87.344131, 36.64151],
          [-87.33598, 36.641543],
          [-87.281506, 36.641761],
          [-87.278398, 36.641718],
          [-87.247655, 36.641841],
          [-87.231037, 36.641888],
          [-87.23053, 36.641895],
          [-87.114983, 36.642414],
          [-87.120443, 36.45546],
          [-87.150276, 36.445037],
          [-87.148602, 36.422773],
          [-87.150974, 36.416644],
          [-87.162729, 36.413298],
          [-87.163242, 36.42136],
          [-87.171307, 36.427616],
          [-87.177254, 36.424583],
          [-87.286501, 36.321933],
          [-87.513533, 36.334713],
          [-87.586957, 36.34178],
          [-87.592358, 36.367663],
          [-87.602597, 36.418979]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47135",
      state: "47",
      county: "135",
      name: "Perry County",
      censusArea: 414.731,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.710185, 35.70861],
          [-87.74375, 35.663952],
          [-87.658189, 35.609349],
          [-87.717559, 35.483347],
          [-87.97318, 35.459968],
          [-88.007709, 35.423381],
          [-88.042332, 35.444617],
          [-87.97463, 35.521025],
          [-88.035837, 35.621839],
          [-88.034788, 35.717261],
          [-87.970738, 35.815705],
          [-87.968987, 35.828827],
          [-87.96274, 35.840921],
          [-87.848144, 35.804079],
          [-87.71676, 35.83875],
          [-87.710185, 35.70861]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47137",
      state: "47",
      county: "137",
      name: "Pickett County",
      censusArea: 162.979,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.276284, 36.626511],
          [-85.096128, 36.622483],
          [-85.086415, 36.621913],
          [-84.974888, 36.614327],
          [-84.859738, 36.606495],
          [-84.859759, 36.606428],
          [-84.785399, 36.603374],
          [-84.74994, 36.553523],
          [-84.731839, 36.524674],
          [-84.943144, 36.58127],
          [-85.043924, 36.558791],
          [-85.120445, 36.407164],
          [-85.185359, 36.490359],
          [-85.284878, 36.531293],
          [-85.276284, 36.626511]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24011",
      state: "24",
      county: "011",
      name: "Caroline County",
      censusArea: 319.419,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.948726, 38.912037],
          [-75.948243, 38.914515],
          [-75.943976, 38.914294],
          [-75.944256, 38.916517],
          [-75.949335, 38.918333],
          [-75.929729, 38.990654],
          [-75.834948, 39.108437],
          [-75.747671, 39.143345],
          [-75.747668, 39.143306],
          [-75.746121, 39.120318],
          [-75.745793, 39.114935],
          [-75.725829, 38.869296],
          [-75.725565, 38.868152],
          [-75.724061, 38.847781],
          [-75.724002, 38.846682],
          [-75.722882, 38.833156],
          [-75.72261, 38.830008],
          [-75.722599, 38.829859],
          [-75.722028, 38.822078],
          [-75.707352, 38.635359],
          [-75.76268, 38.686222],
          [-75.945243, 38.674114],
          [-76.015045, 38.728687],
          [-76.013206, 38.733291],
          [-76.009979, 38.736034],
          [-76.007354, 38.736751],
          [-75.999671, 38.734836],
          [-75.995376, 38.736679],
          [-75.99459, 38.741886],
          [-75.999336, 38.752075],
          [-75.998947, 38.755596],
          [-75.996902, 38.757507],
          [-75.993759, 38.759284],
          [-75.979965, 38.76371],
          [-75.976409, 38.765506],
          [-75.96775, 38.774398],
          [-75.968238, 38.777729],
          [-75.966369, 38.779762],
          [-75.954551, 38.777496],
          [-75.949455, 38.778419],
          [-75.937347, 38.787242],
          [-75.932518, 38.796138],
          [-75.927963, 38.799201],
          [-75.920799, 38.802635],
          [-75.911369, 38.806285],
          [-75.903778, 38.806934],
          [-75.896774, 38.815109],
          [-75.903251, 38.816764],
          [-75.906455, 38.819705],
          [-75.905494, 38.821567],
          [-75.901747, 38.822883],
          [-75.901267, 38.824737],
          [-75.904341, 38.826431],
          [-75.912918, 38.829532],
          [-75.913711, 38.832965],
          [-75.915183, 38.834835],
          [-75.919432, 38.835205],
          [-75.927771, 38.838893],
          [-75.928099, 38.841068],
          [-75.919348, 38.845016],
          [-75.918975, 38.846084],
          [-75.919455, 38.846897],
          [-75.926772, 38.847038],
          [-75.929312, 38.849021],
          [-75.930373, 38.850872],
          [-75.928336, 38.852428],
          [-75.922797, 38.854785],
          [-75.922431, 38.856369],
          [-75.922187, 38.857257],
          [-75.924331, 38.860389],
          [-75.930229, 38.861286],
          [-75.932914, 38.866035],
          [-75.934707, 38.867511],
          [-75.939292, 38.866558],
          [-75.941551, 38.868022],
          [-75.943077, 38.870494],
          [-75.944396, 38.875087],
          [-75.943572, 38.87638],
          [-75.938919, 38.876392],
          [-75.937583, 38.877994],
          [-75.937644, 38.879802],
          [-75.939224, 38.881332],
          [-75.941482, 38.881179],
          [-75.943847, 38.879344],
          [-75.948753, 38.878444],
          [-75.951469, 38.880237],
          [-75.950904, 38.884143],
          [-75.949638, 38.884807],
          [-75.94548, 38.884029],
          [-75.941535, 38.884075],
          [-75.940399, 38.88507],
          [-75.944587, 38.890068],
          [-75.94831, 38.891323],
          [-75.948616, 38.89225],
          [-75.945945, 38.893913],
          [-75.944358, 38.897548],
          [-75.944908, 38.900348],
          [-75.948677, 38.902389],
          [-75.94812, 38.90456],
          [-75.943862, 38.906204],
          [-75.943145, 38.907257],
          [-75.948726, 38.912037]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24013",
      state: "24",
      county: "013",
      name: "Carroll County",
      censusArea: 447.595,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.217024, 39.719998],
          [-77.216806, 39.719998],
          [-77.058904, 39.7201],
          [-77.058204, 39.7202],
          [-77.047104, 39.72],
          [-76.999465, 39.720128],
          [-76.897566, 39.720401],
          [-76.8901, 39.720401],
          [-76.809197, 39.720702],
          [-76.806397, 39.720602],
          [-76.787097, 39.720802],
          [-76.787096, 39.720802],
          [-76.8667, 39.500902],
          [-76.868387, 39.494074],
          [-76.879788, 39.365858],
          [-76.880701, 39.360469],
          [-76.873378, 39.35715],
          [-76.879738, 39.356846],
          [-76.882274, 39.350185],
          [-77.16808, 39.353957],
          [-77.106839, 39.491661],
          [-77.200125, 39.578588],
          [-77.287747, 39.604516],
          [-77.217024, 39.719998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24021",
      state: "24",
      county: "021",
      name: "Frederick County",
      censusArea: 660.221,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.458779, 39.22028],
          [-77.45768, 39.22502],
          [-77.46021, 39.228359],
          [-77.484605, 39.245941],
          [-77.511222, 39.2535],
          [-77.534461, 39.262361],
          [-77.543228, 39.266937],
          [-77.545846, 39.271535],
          [-77.560854, 39.286152],
          [-77.592739, 39.30129],
          [-77.667749, 39.318129],
          [-77.677505, 39.318699],
          [-77.570182, 39.619998],
          [-77.469145, 39.720018],
          [-77.459427, 39.720017],
          [-77.243307, 39.719998],
          [-77.239807, 39.719998],
          [-77.217024, 39.719998],
          [-77.287747, 39.604516],
          [-77.200125, 39.578588],
          [-77.106839, 39.491661],
          [-77.16808, 39.353957],
          [-77.168558, 39.353719],
          [-77.168801, 39.353502],
          [-77.171459, 39.352487],
          [-77.313839, 39.286462],
          [-77.337016, 39.275696],
          [-77.454934, 39.221584],
          [-77.458779, 39.22028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24023",
      state: "24",
      county: "023",
      name: "Garrett County",
      censusArea: 647.103,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.067072, 39.474658],
          [-79.08445, 39.471356],
          [-79.201676, 39.379726],
          [-79.213192, 39.367897],
          [-79.25688, 39.356077],
          [-79.280039, 39.340076],
          [-79.378687, 39.27192],
          [-79.402911, 39.250106],
          [-79.486873, 39.205961],
          [-79.485874, 39.264905],
          [-79.486179, 39.26497],
          [-79.486737, 39.278149],
          [-79.487651, 39.279933],
          [-79.482648, 39.521364],
          [-79.482354, 39.524682],
          [-79.478866, 39.531689],
          [-79.476662, 39.721078],
          [-79.392458, 39.72134],
          [-78.931176, 39.722775],
          [-78.931175, 39.722775],
          [-79.067933, 39.479189],
          [-79.067072, 39.474658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24031",
      state: "24",
      county: "031",
      name: "Montgomery County",
      censusArea: 491.254,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.1199, 38.934311],
          [-77.137701, 38.95531],
          [-77.148179, 38.965002],
          [-77.166901, 38.96811],
          [-77.197502, 38.96681],
          [-77.221502, 38.97131],
          [-77.235403, 38.97661],
          [-77.249803, 38.985909],
          [-77.255703, 39.002409],
          [-77.251803, 39.011409],
          [-77.261403, 39.031009],
          [-77.291605, 39.045408],
          [-77.328002, 39.058554],
          [-77.340287, 39.062991],
          [-77.375079, 39.061297],
          [-77.42318, 39.066878],
          [-77.458202, 39.073723],
          [-77.519929, 39.120925],
          [-77.524559, 39.127821],
          [-77.527282, 39.146236],
          [-77.516426, 39.170891],
          [-77.510631, 39.178484],
          [-77.505162, 39.18205],
          [-77.485971, 39.185665],
          [-77.478596, 39.189168],
          [-77.458884, 39.219826],
          [-77.458779, 39.22028],
          [-77.454934, 39.221584],
          [-77.337016, 39.275696],
          [-77.313839, 39.286462],
          [-77.171459, 39.352487],
          [-77.168801, 39.353502],
          [-77.186804, 39.338203],
          [-77.144669, 39.294707],
          [-76.888505, 39.130967],
          [-76.969748, 39.021343],
          [-76.9711, 39.019305],
          [-76.972097, 39.018109],
          [-76.973771, 39.015718],
          [-76.974565, 39.014714],
          [-76.977278, 39.010947],
          [-76.979606, 39.008161],
          [-76.98415, 39.001438],
          [-76.985693, 38.99998],
          [-76.988265, 38.996288],
          [-76.989059, 38.995577],
          [-76.991071, 38.992349],
          [-76.985747, 38.977349],
          [-77.002636, 38.965521],
          [-77.008298, 38.97011],
          [-77.013798, 38.97441],
          [-77.015598, 38.97591],
          [-77.036299, 38.99171],
          [-77.040999, 38.99511],
          [-77.054299, 38.98511],
          [-77.1007, 38.94891],
          [-77.1199, 38.934311]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24033",
      state: "24",
      county: "033",
      name: "Prince Georges County",
      censusArea: 482.691,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.05991, 38.734419],
          [-77.041398, 38.763914],
          [-77.039239, 38.7852],
          [-77.038598, 38.791513],
          [-77.024392, 38.80297],
          [-77.001397, 38.821513],
          [-76.953696, 38.858512],
          [-76.949696, 38.861312],
          [-76.920195, 38.884412],
          [-76.919295, 38.885112],
          [-76.910795, 38.891712],
          [-76.909395, 38.892812],
          [-76.941722, 38.918019],
          [-77.002498, 38.96541],
          [-77.002636, 38.965521],
          [-76.985747, 38.977349],
          [-76.991071, 38.992349],
          [-76.989059, 38.995577],
          [-76.988265, 38.996288],
          [-76.985693, 38.99998],
          [-76.98415, 39.001438],
          [-76.979606, 39.008161],
          [-76.977278, 39.010947],
          [-76.974565, 39.014714],
          [-76.973771, 39.015718],
          [-76.972097, 39.018109],
          [-76.9711, 39.019305],
          [-76.969748, 39.021343],
          [-76.888505, 39.130967],
          [-76.869003, 39.112214],
          [-76.849084, 39.109097],
          [-76.840362, 39.103142],
          [-76.693286, 38.951416],
          [-76.710639, 38.815694],
          [-76.686358, 38.748475],
          [-76.684018, 38.738348],
          [-76.687009, 38.735897],
          [-76.694996, 38.735075],
          [-76.695555, 38.731544],
          [-76.693589, 38.724761],
          [-76.70196, 38.710864],
          [-76.697743, 38.70703],
          [-76.694946, 38.702468],
          [-76.694983, 38.700307],
          [-76.697932, 38.694782],
          [-76.698429, 38.690443],
          [-76.69549, 38.685781],
          [-76.687147, 38.681371],
          [-76.687424, 38.678746],
          [-76.690461, 38.673683],
          [-76.700667, 38.670131],
          [-76.700381, 38.667022],
          [-76.694236, 38.663841],
          [-76.68737, 38.663793],
          [-76.683329, 38.661178],
          [-76.683186, 38.658813],
          [-76.685127, 38.654771],
          [-76.69163, 38.648506],
          [-76.692853, 38.636437],
          [-76.692021, 38.634932],
          [-76.686138, 38.630182],
          [-76.679355, 38.627949],
          [-76.677562, 38.626234],
          [-76.672334, 38.617533],
          [-76.673479, 38.607249],
          [-76.672557, 38.599271],
          [-76.675166, 38.592106],
          [-76.67948, 38.585211],
          [-76.680702, 38.580499],
          [-76.677799, 38.572374],
          [-76.678891, 38.567954],
          [-76.675468, 38.558824],
          [-76.672274, 38.552846],
          [-76.672642, 38.546818],
          [-76.675457, 38.535876],
          [-76.747335, 38.616677],
          [-76.862955, 38.658649],
          [-77.011028, 38.651975],
          [-77.077027, 38.690168],
          [-77.078599, 38.694114],
          [-77.086113, 38.705792],
          [-77.074599, 38.711015],
          [-77.05991, 38.734419]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24043",
      state: "24",
      county: "043",
      name: "Washington County",
      censusArea: 457.78,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.825411, 39.494036],
          [-77.845105, 39.498285],
          [-77.865423, 39.516472],
          [-77.888945, 39.55595],
          [-77.886135, 39.560432],
          [-77.884536, 39.568174],
          [-77.902649, 39.587796],
          [-77.946442, 39.58491],
          [-78.009985, 39.602893],
          [-78.023427, 39.61986],
          [-78.035992, 39.63572],
          [-78.074595, 39.666686],
          [-78.107834, 39.682137],
          [-78.143478, 39.690412],
          [-78.176625, 39.695967],
          [-78.182759, 39.69511],
          [-78.191107, 39.690262],
          [-78.224337, 39.66327],
          [-78.271122, 39.619642],
          [-78.283039, 39.62047],
          [-78.313033, 39.631001],
          [-78.334044, 39.63577],
          [-78.342834, 39.722539],
          [-78.34252, 39.722539],
          [-78.340498, 39.722514],
          [-78.339539, 39.722552],
          [-78.337111, 39.722461],
          [-78.330715, 39.722689],
          [-78.26902, 39.722613],
          [-78.268948, 39.72259],
          [-78.243103, 39.722481],
          [-78.240334, 39.722498],
          [-78.20445, 39.72252],
          [-78.202895, 39.722416],
          [-78.09914, 39.722322],
          [-78.075771, 39.722301],
          [-78.073736, 39.722314],
          [-77.768534, 39.721358],
          [-77.743204, 39.721205],
          [-77.732615, 39.721094],
          [-77.724115, 39.720894],
          [-77.674522, 39.720847],
          [-77.672249, 39.720778],
          [-77.534758, 39.720134],
          [-77.533371, 39.720165],
          [-77.469145, 39.720018],
          [-77.570182, 39.619998],
          [-77.677505, 39.318699],
          [-77.719029, 39.321125],
          [-77.727379, 39.321666],
          [-77.755789, 39.333899],
          [-77.760586, 39.338864],
          [-77.760435, 39.344171],
          [-77.739204, 39.38573],
          [-77.765442, 39.428574],
          [-77.807821, 39.490241],
          [-77.825411, 39.494036]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32031",
      state: "32",
      county: "031",
      name: "Washoe County",
      censusArea: 6302.366,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.360177, 41.994384],
          [-119.324182, 41.994278],
          [-119.323955, 41.414519],
          [-119.304635, 41.414695],
          [-119.305713, 41.234542],
          [-119.331068, 41.235118],
          [-119.329791, 40.960394],
          [-119.309598, 40.960378],
          [-119.313037, 40.518314],
          [-119.338504, 40.517805],
          [-119.339296, 39.998609],
          [-119.225176, 39.999385],
          [-119.208543, 39.809967],
          [-119.170899, 39.788288],
          [-119.189342, 39.65087],
          [-119.25876, 39.649213],
          [-119.283637, 39.621561],
          [-119.279801, 39.627112],
          [-119.370604, 39.586882],
          [-119.467141, 39.584502],
          [-119.582952, 39.547302],
          [-119.621697, 39.511602],
          [-119.695643, 39.520023],
          [-119.685464, 39.389575],
          [-119.658501, 39.331544],
          [-119.712645, 39.250871],
          [-119.880787, 39.165509],
          [-120.004504, 39.165599],
          [-120.005746, 39.22521],
          [-120.005743, 39.228664],
          [-120.005414, 39.313345],
          [-120.005413, 39.313848],
          [-120.00532, 39.31635],
          [-120.005318, 39.316479],
          [-120.003117, 39.445045],
          [-120.003116, 39.445113],
          [-120.001319, 39.722416],
          [-120.001319, 39.72242],
          [-120.000502, 39.779956],
          [-120.000607, 39.780779],
          [-119.997291, 40.071803],
          [-119.997175, 40.077245],
          [-119.997234, 40.091591],
          [-119.997124, 40.126363],
          [-119.996183, 40.262461],
          [-119.996182, 40.263532],
          [-119.996155, 40.32125],
          [-119.996155, 40.321838],
          [-119.995926, 40.499901],
          [-119.999231, 40.865899],
          [-119.999232, 40.867454],
          [-119.999358, 40.873101],
          [-119.999866, 41.183974],
          [-119.999168, 41.99454],
          [-119.986678, 41.995842],
          [-119.790087, 41.997544],
          [-119.360177, 41.994384]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32510",
      state: "32",
      county: "510",
      name: "Carson City County",
      lsad: "",
      censusArea: 144.662,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.004504, 39.165599],
          [-119.880787, 39.165509],
          [-119.712645, 39.250871],
          [-119.648977, 39.195287],
          [-119.56368, 39.195415],
          [-119.55102, 39.085791],
          [-119.752116, 39.084904],
          [-119.761213, 39.114009],
          [-120.003402, 39.112687],
          [-120.004504, 39.165599]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33003",
      state: "33",
      county: "003",
      name: "Carroll County",
      censusArea: 931.061,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.037296, 43.529689],
          [-71.163626, 43.53937],
          [-71.344184, 43.629327],
          [-71.439621, 43.696002],
          [-71.460347, 43.709747],
          [-71.532369, 43.765028],
          [-71.564615, 43.89311],
          [-71.361889, 43.915549],
          [-71.415801, 44.212082],
          [-71.407246, 44.213258],
          [-71.406901, 44.21332],
          [-71.395246, 44.21491],
          [-71.384993, 44.179144],
          [-71.357508, 44.166003],
          [-71.343214, 44.122282],
          [-71.343617, 44.092234],
          [-71.252401, 44.244235],
          [-71.042493, 44.237803],
          [-71.010239, 44.284774],
          [-71.008736, 44.258825],
          [-71.008764, 44.258443],
          [-71.001335, 44.093205],
          [-71.001367, 44.092931],
          [-70.992986, 43.914232],
          [-70.992086, 43.886261],
          [-70.989067, 43.79244],
          [-70.989041, 43.792152],
          [-70.982083, 43.715043],
          [-70.982238, 43.711865],
          [-70.981978, 43.701965],
          [-70.981946, 43.70096],
          [-70.980758, 43.684141],
          [-70.979985, 43.673195],
          [-70.972716, 43.570255],
          [-70.953322, 43.552718],
          [-70.955346, 43.540697],
          [-70.955337, 43.54098],
          [-70.958958, 43.537634],
          [-71.037296, 43.529689]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33009",
      state: "33",
      county: "009",
      name: "Grafton County",
      censusArea: 1708.749,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.864053, 43.599442],
          [-71.892145, 43.57466],
          [-71.93522, 43.528449],
          [-72.32962, 43.600201],
          [-72.334745, 43.614519],
          [-72.334401, 43.61925],
          [-72.32966, 43.634648],
          [-72.302867, 43.702718],
          [-72.27118, 43.734138],
          [-72.264245, 43.734158],
          [-72.232713, 43.748286],
          [-72.205717, 43.771143],
          [-72.204792, 43.771926],
          [-72.183743, 43.806644],
          [-72.182203, 43.834032],
          [-72.167224, 43.886113],
          [-72.121002, 43.918956],
          [-72.104421, 43.950536],
          [-72.105594, 43.959029],
          [-72.107042, 43.969513],
          [-72.113078, 43.97279],
          [-72.112813, 43.98802],
          [-72.09071, 44.035211],
          [-72.054675, 44.112147],
          [-72.040082, 44.155748],
          [-72.040082, 44.155749],
          [-72.042087, 44.157633],
          [-72.053021, 44.167903],
          [-72.064285, 44.187888],
          [-72.067774, 44.270976],
          [-72.065434, 44.277235],
          [-72.05888, 44.28624],
          [-72.012173, 44.321408],
          [-72.002314, 44.324871],
          [-71.849465, 44.359295],
          [-71.816065, 44.366817],
          [-71.814388, 44.381932],
          [-71.793924, 44.399271],
          [-71.764537, 44.406403],
          [-71.696665, 44.332326],
          [-71.587113, 44.304786],
          [-71.575752, 44.254699],
          [-71.443312, 44.249291],
          [-71.415801, 44.212082],
          [-71.361889, 43.915549],
          [-71.564615, 43.89311],
          [-71.532369, 43.765028],
          [-71.522193, 43.697182],
          [-71.648057, 43.686154],
          [-71.65091, 43.621228],
          [-71.72775, 43.563524],
          [-71.81805, 43.544603],
          [-71.864053, 43.599442]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33015",
      state: "33",
      county: "015",
      name: "Rockingham County",
      censusArea: 694.72,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.819549, 43.123231],
          [-70.784108, 43.098963],
          [-70.756397, 43.079988],
          [-70.703936, 43.059621],
          [-70.718936, 43.03235],
          [-70.74457, 43.00009],
          [-70.749024, 42.995467],
          [-70.759975, 42.988592],
          [-70.795596, 42.93629],
          [-70.810069, 42.909549],
          [-70.81461, 42.891346],
          [-70.816321, 42.884487],
          [-70.817296, 42.87229],
          [-70.848625, 42.860939],
          [-70.886136, 42.88261],
          [-70.902768, 42.88653],
          [-70.914886, 42.886564],
          [-70.914899, 42.886589],
          [-70.930799, 42.884589],
          [-70.931699, 42.884189],
          [-70.996901, 42.864589],
          [-71.037901, 42.854089],
          [-71.058407, 42.847009],
          [-71.113858, 42.827865],
          [-71.149703, 42.815489],
          [-71.165603, 42.808689],
          [-71.186104, 42.790689],
          [-71.181803, 42.73759],
          [-71.208137, 42.743273],
          [-71.208227, 42.743294],
          [-71.208302, 42.743314],
          [-71.245504, 42.742589],
          [-71.360808, 42.798988],
          [-71.44181, 42.889586],
          [-71.377409, 42.947185],
          [-71.391868, 43.007857],
          [-71.39221, 43.008984],
          [-71.356182, 43.101971],
          [-71.247215, 43.274201],
          [-71.026688, 43.138086],
          [-71.072984, 43.081748],
          [-70.875486, 43.080457],
          [-70.86103, 43.095602],
          [-70.861535, 43.125021],
          [-70.819549, 43.123231]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33019",
      state: "33",
      county: "019",
      name: "Sullivan County",
      censusArea: 537.313,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.053802, 43.441078],
          [-72.090028, 43.322567],
          [-72.008995, 43.187874],
          [-72.036791, 43.182553],
          [-72.023403, 43.160151],
          [-72.038149, 43.128807],
          [-72.178457, 43.131955],
          [-72.156548, 43.181962],
          [-72.4521, 43.161414],
          [-72.439407, 43.211322],
          [-72.431187, 43.231786],
          [-72.39715, 43.316515],
          [-72.400511, 43.337073],
          [-72.403949, 43.358098],
          [-72.405253, 43.389992],
          [-72.396948, 43.428925],
          [-72.396934, 43.42899],
          [-72.387852, 43.471567],
          [-72.387642, 43.502481],
          [-72.395825, 43.52056],
          [-72.32962, 43.600201],
          [-71.93522, 43.528449],
          [-72.053802, 43.441078]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34003",
      state: "34",
      county: "003",
      name: "Bergen County",
      censusArea: 233.009,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.213373, 41.133828],
          [-74.096786, 41.083796],
          [-74.092486, 41.081896],
          [-74.041054, 41.059088],
          [-74.041049, 41.059086],
          [-73.91188, 41.001297],
          [-73.90268, 40.997297],
          [-73.893979, 40.997197],
          [-73.917905, 40.917577],
          [-73.919705, 40.913478],
          [-73.92747, 40.895682],
          [-73.933406, 40.882078],
          [-73.933408, 40.882075],
          [-73.938081, 40.874699],
          [-73.948281, 40.858399],
          [-73.986864, 40.798344],
          [-74.017204, 40.823541],
          [-74.044977, 40.807702],
          [-74.147529, 40.786491],
          [-74.138487, 40.7987],
          [-74.139825, 40.803147],
          [-74.136487, 40.8182],
          [-74.130031, 40.819962],
          [-74.148059, 40.94159],
          [-74.148158, 40.942339],
          [-74.146429, 40.948441],
          [-74.141048, 40.966348],
          [-74.17021, 40.983865],
          [-74.170988, 40.976997],
          [-74.270521, 41.022131],
          [-74.213373, 41.133828]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34009",
      state: "34",
      county: "009",
      name: "Cape May County",
      censusArea: 251.425,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.914936, 39.177553],
          [-74.861073, 39.323768],
          [-74.699745, 39.289593],
          [-74.541443, 39.300245],
          [-74.551151, 39.293539],
          [-74.553439, 39.286915],
          [-74.560957, 39.278677],
          [-74.581008, 39.270819],
          [-74.597921, 39.258851],
          [-74.614481, 39.244659],
          [-74.636306, 39.220834],
          [-74.646595, 39.212002],
          [-74.651443, 39.198578],
          [-74.67143, 39.179802],
          [-74.714341, 39.119804],
          [-74.71532, 39.116893],
          [-74.714135, 39.114631],
          [-74.704409, 39.107858],
          [-74.705876, 39.102937],
          [-74.738316, 39.074727],
          [-74.778777, 39.023073],
          [-74.786356, 39.000113],
          [-74.792723, 38.991991],
          [-74.807917, 38.985948],
          [-74.819354, 38.979402],
          [-74.850748, 38.954538],
          [-74.864458, 38.94041],
          [-74.865198, 38.941439],
          [-74.870497, 38.943543],
          [-74.882309, 38.941759],
          [-74.90705, 38.931994],
          [-74.920414, 38.929136],
          [-74.933571, 38.928519],
          [-74.963463, 38.931194],
          [-74.967274, 38.933413],
          [-74.971995, 38.94037],
          [-74.955363, 39.001262],
          [-74.94947, 39.015637],
          [-74.93832, 39.035185],
          [-74.903664, 39.087437],
          [-74.897784, 39.098811],
          [-74.892547, 39.113183],
          [-74.885914, 39.143627],
          [-74.887167, 39.158825],
          [-74.905181, 39.174945],
          [-74.914936, 39.177553]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34013",
      state: "34",
      county: "013",
      name: "Essex County",
      censusArea: 126.212,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.164799, 40.754718],
          [-74.165987, 40.745199],
          [-74.161958, 40.736007],
          [-74.116993, 40.737125],
          [-74.117057, 40.710381],
          [-74.112787, 40.703102],
          [-74.122911, 40.691172],
          [-74.136703, 40.674444],
          [-74.201589, 40.686602],
          [-74.222452, 40.707249],
          [-74.28587, 40.722225],
          [-74.30123, 40.713472],
          [-74.302092, 40.713301],
          [-74.302695, 40.713601],
          [-74.303691, 40.7133],
          [-74.305393, 40.713701],
          [-74.306991, 40.714002],
          [-74.307589, 40.714201],
          [-74.308793, 40.714508],
          [-74.309388, 40.714901],
          [-74.309791, 40.715501],
          [-74.311691, 40.715601],
          [-74.312794, 40.717101],
          [-74.313793, 40.718001],
          [-74.315293, 40.718601],
          [-74.315991, 40.719199],
          [-74.318992, 40.719201],
          [-74.321408, 40.719072],
          [-74.325596, 40.717075],
          [-74.342682, 40.725181],
          [-74.354942, 40.7345],
          [-74.371923, 40.739229],
          [-74.334316, 40.905302],
          [-74.270702, 40.89692],
          [-74.259813, 40.883769],
          [-74.246691, 40.880799],
          [-74.130031, 40.819962],
          [-74.136487, 40.8182],
          [-74.139825, 40.803147],
          [-74.138487, 40.7987],
          [-74.147529, 40.786491],
          [-74.147859, 40.784683],
          [-74.148266, 40.779876],
          [-74.155798, 40.770815],
          [-74.157688, 40.7661],
          [-74.163388, 40.759401],
          [-74.164799, 40.754718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39167",
      state: "39",
      county: "167",
      name: "Washington County",
      censusArea: 631.972,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.371271, 39.342062],
          [-81.375961, 39.341697],
          [-81.384556, 39.343449],
          [-81.393794, 39.351706],
          [-81.406689, 39.38809],
          [-81.412706, 39.394618],
          [-81.435642, 39.408474],
          [-81.446543, 39.410374],
          [-81.456143, 39.409274],
          [-81.467744, 39.403774],
          [-81.542346, 39.352874],
          [-81.605816, 39.275851],
          [-81.695724, 39.242859],
          [-81.721551, 39.212448],
          [-81.721808, 39.269597],
          [-81.853668, 39.318165],
          [-81.844863, 39.450216],
          [-81.823158, 39.494072],
          [-81.708527, 39.480775],
          [-81.714447, 39.584236],
          [-81.588185, 39.586973],
          [-81.473471, 39.583318],
          [-81.47316, 39.645976],
          [-81.394934, 39.601547],
          [-81.283075, 39.605796],
          [-81.246808, 39.579037],
          [-81.036791, 39.572118],
          [-81.038691, 39.540464],
          [-81.063587, 39.520497],
          [-81.127696, 39.464879],
          [-81.215617, 39.388602],
          [-81.223581, 39.386062],
          [-81.249088, 39.389992],
          [-81.270716, 39.385914],
          [-81.295373, 39.37529],
          [-81.347567, 39.34577],
          [-81.356911, 39.343178],
          [-81.371271, 39.342062]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39171",
      state: "39",
      county: "171",
      name: "Williams County",
      censusArea: 420.965,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.341664, 41.427568],
          [-84.803956, 41.426044],
          [-84.803956, 41.426128],
          [-84.803919, 41.435531],
          [-84.804457, 41.488224],
          [-84.804551, 41.500364],
          [-84.804729, 41.530092],
          [-84.804729, 41.530135],
          [-84.804729, 41.530231],
          [-84.805812, 41.61304],
          [-84.805696, 41.631398],
          [-84.805673, 41.632342],
          [-84.80621, 41.67455],
          [-84.806082, 41.696089],
          [-84.438067, 41.704903],
          [-84.399546, 41.705758],
          [-84.380735, 41.513942],
          [-84.341902, 41.485519],
          [-84.341664, 41.427568]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39175",
      state: "39",
      county: "175",
      name: "Wyandot County",
      censusArea: 406.865,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.11136, 40.702915],
          [-83.304687, 40.702006],
          [-83.419836, 40.686823],
          [-83.495799, 40.701617],
          [-83.515883, 40.818134],
          [-83.420319, 40.991889],
          [-83.305004, 40.992787],
          [-83.112737, 40.993447],
          [-83.11136, 40.702915]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40003",
      state: "40",
      county: "003",
      name: "Alfalfa County",
      censusArea: 866.455,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.104286, 36.593578],
          [-98.104427, 36.463105],
          [-98.533617, 36.46323],
          [-98.54466, 36.998996],
          [-98.420209, 36.998516],
          [-98.418268, 36.998538],
          [-98.408991, 36.998513],
          [-98.354073, 36.997961],
          [-98.347149, 36.997962],
          [-98.346188, 36.997962],
          [-98.237712, 36.997972],
          [-98.219499, 36.997824],
          [-98.208218, 36.997997],
          [-98.177596, 36.998009],
          [-98.147452, 36.998162],
          [-98.111985, 36.998133],
          [-98.109311, 36.593588],
          [-98.104286, 36.593578]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40011",
      state: "40",
      county: "011",
      name: "Blaine County",
      censusArea: 928.424,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.636899, 36.164894],
          [-98.601244, 36.165771],
          [-98.210544, 36.16489],
          [-98.207104, 35.72514],
          [-98.31337, 35.725204],
          [-98.313251, 35.551523],
          [-98.623149, 35.551597],
          [-98.63207, 35.551617],
          [-98.631985, 35.812402],
          [-98.636899, 36.164894]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40013",
      state: "40",
      county: "013",
      name: "Bryan County",
      censusArea: 904.471,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.846334, 33.841136],
          [-95.949935, 33.857452],
          [-96.097526, 33.847544],
          [-96.14807, 33.837799],
          [-96.156717, 33.813324],
          [-96.1641, 33.784261],
          [-96.170055, 33.76917],
          [-96.181704, 33.758504],
          [-96.1999, 33.752117],
          [-96.220521, 33.74739],
          [-96.229859, 33.74832],
          [-96.304675, 33.745901],
          [-96.36959, 33.716809],
          [-96.378473, 33.726648],
          [-96.423362, 33.776365],
          [-96.436455, 33.78005],
          [-96.448045, 33.781031],
          [-96.500268, 33.772583],
          [-96.612925, 33.833939],
          [-96.61487, 33.841234],
          [-96.615843, 33.853393],
          [-96.612963, 33.867651],
          [-96.611466, 33.875278],
          [-96.613494, 33.878321],
          [-96.57701, 33.964406],
          [-96.624748, 34.005417],
          [-96.548028, 34.063302],
          [-96.585355, 34.114459],
          [-96.453839, 34.114406],
          [-96.40764, 34.157317],
          [-95.991578, 34.156815],
          [-95.93815, 34.1251],
          [-95.937947, 33.948163],
          [-95.836905, 33.873374],
          [-95.757156, 33.867293],
          [-95.763622, 33.847954],
          [-95.82468, 33.837726],
          [-95.846334, 33.841136]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40017",
      state: "40",
      county: "017",
      name: "Canadian County",
      censusArea: 896.626,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.671319, 35.464338],
          [-97.671369, 35.377151],
          [-97.671529, 35.337508],
          [-97.671595, 35.335934],
          [-97.685054, 35.334301],
          [-97.69587, 35.327709],
          [-97.706159, 35.324989],
          [-97.720035, 35.328515],
          [-97.724708, 35.325832],
          [-97.737305, 35.323762],
          [-97.771765, 35.345735],
          [-97.777567, 35.337087],
          [-97.788319, 35.336643],
          [-97.792022, 35.332702],
          [-97.796719, 35.339105],
          [-97.807186, 35.340846],
          [-97.812129, 35.333788],
          [-97.833675, 35.335362],
          [-98.096014, 35.377753],
          [-98.306326, 35.377239],
          [-98.313251, 35.551523],
          [-98.31337, 35.725204],
          [-98.207104, 35.72514],
          [-97.674026, 35.72597],
          [-97.671319, 35.464338]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40025",
      state: "40",
      county: "025",
      name: "Cimarron County",
      censusArea: 1834.738,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.162463, 36.500326],
          [-103.002434, 36.500397],
          [-103.002565, 36.526588],
          [-103.002188, 36.602716],
          [-103.002252, 36.61718],
          [-103.002518, 36.675186],
          [-103.002198, 36.719427],
          [-103.001964, 36.909573],
          [-103.002247, 36.911587],
          [-103.002199, 37.000104],
          [-102.986976, 36.998524],
          [-102.985807, 36.998571],
          [-102.979613, 36.998549],
          [-102.875481, 36.999628],
          [-102.570896, 36.995136],
          [-102.355367, 36.994575],
          [-102.355288, 36.994506],
          [-102.208316, 36.99373],
          [-102.184271, 36.993593],
          [-102.054503, 36.993109],
          [-102.04224, 36.993083],
          [-102.028207, 36.993125],
          [-102.028204, 36.993125],
          [-102.032339, 36.500066],
          [-102.162463, 36.500326]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40029",
      state: "40",
      county: "029",
      name: "Coal County",
      censusArea: 516.682,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.092043, 34.680556],
          [-96.091873, 34.506155],
          [-96.145342, 34.506102],
          [-96.145096, 34.418677],
          [-96.40733, 34.418391],
          [-96.513786, 34.419569],
          [-96.513865, 34.505389],
          [-96.512171, 34.680065],
          [-96.406357, 34.680023],
          [-96.406483, 34.76759],
          [-96.092001, 34.767479],
          [-96.092043, 34.680556]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40035",
      state: "40",
      county: "035",
      name: "Craig County",
      censusArea: 761.354,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.00762, 36.999514],
          [-94.999403, 36.670631],
          [-95.00573, 36.50989],
          [-95.32817, 36.51024],
          [-95.32837, 36.59763],
          [-95.43157, 36.59754],
          [-95.431731, 36.942776],
          [-95.407572, 36.999241],
          [-95.33121, 36.99938],
          [-95.328327, 36.999366],
          [-95.328058, 36.999365],
          [-95.322565, 36.999358],
          [-95.195307, 36.999565],
          [-95.177301, 36.99952],
          [-95.155372, 36.99954],
          [-95.155187, 36.999539],
          [-95.073509, 36.999509],
          [-95.073504, 36.999509],
          [-95.049499, 36.99958],
          [-95.037857, 36.999497],
          [-95.030324, 36.999517],
          [-95.011433, 36.999535],
          [-95.00762, 36.999514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40039",
      state: "40",
      county: "039",
      name: "Custer County",
      censusArea: 988.817,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.375817, 35.812188],
          [-98.631985, 35.812402],
          [-98.63207, 35.551617],
          [-98.623149, 35.551597],
          [-98.623336, 35.464225],
          [-99.364217, 35.465328],
          [-99.364141, 35.508793],
          [-99.375817, 35.812188]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40045",
      state: "40",
      county: "045",
      name: "Ellis County",
      censusArea: 1231.515,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.000399, 36.055677],
          [-100.000406, 36.499702],
          [-100.003762, 36.499699],
          [-100.00316, 36.59325],
          [-99.605565, 36.592753],
          [-99.596079, 36.164061],
          [-99.382074, 36.164301],
          [-99.381022, 36.012925],
          [-99.495812, 35.978732],
          [-99.558486, 35.865988],
          [-99.717101, 35.859769],
          [-99.801389, 35.910645],
          [-99.809444, 36.000044],
          [-99.888309, 35.987847],
          [-99.925147, 35.913664],
          [-100.000396, 35.880948],
          [-100.000399, 36.055677]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40049",
      state: "40",
      county: "049",
      name: "Garvin County",
      censusArea: 802.121,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.562323, 34.507036],
          [-97.562757, 34.681147],
          [-97.668425, 34.681344],
          [-97.668099, 34.855164],
          [-96.932471, 34.854455],
          [-96.932328, 34.636811],
          [-97.200073, 34.622762],
          [-97.143958, 34.506532],
          [-97.35211, 34.506788],
          [-97.562323, 34.507036]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40055",
      state: "40",
      county: "055",
      name: "Greer County",
      censusArea: 639.324,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.409291, 35.114509],
          [-99.327788, 35.041697],
          [-99.30622, 34.857632],
          [-99.246462, 34.822114],
          [-99.405821, 34.813038],
          [-99.423201, 34.72502],
          [-99.666849, 34.724555],
          [-99.720066, 34.76845],
          [-99.731092, 34.944058],
          [-99.888425, 34.943946],
          [-99.888571, 35.030262],
          [-99.782949, 35.030111],
          [-99.782727, 35.11678],
          [-99.409291, 35.114509]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37193",
      state: "37",
      county: "193",
      name: "Wilkes County",
      censusArea: 754.278,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.477298, 36.240272],
          [-81.378809, 36.277983],
          [-81.354467, 36.355678],
          [-81.253482, 36.365351],
          [-81.094829, 36.440433],
          [-81.013615, 36.371271],
          [-80.967066, 36.4016],
          [-80.868871, 36.326516],
          [-80.873259, 36.236383],
          [-80.88218, 36.055731],
          [-81.028884, 36.045762],
          [-81.190911, 36.036949],
          [-81.328385, 35.99655],
          [-81.545455, 36.117398],
          [-81.455579, 36.199749],
          [-81.477298, 36.240272]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47141",
      state: "47",
      county: "141",
      name: "Putnam County",
      censusArea: 401.103,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.780148, 36.238011],
          [-85.533697, 36.242577],
          [-85.495124, 36.302278],
          [-85.419432, 36.236145],
          [-85.204156, 36.152489],
          [-85.119583, 36.144719],
          [-85.100213, 36.138561],
          [-85.246362, 36.100698],
          [-85.264206, 35.979154],
          [-85.309363, 36.031592],
          [-85.509323, 36.081937],
          [-85.644604, 36.015053],
          [-85.807415, 36.131582],
          [-85.780148, 36.238011]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48045",
      state: "48",
      county: "045",
      name: "Briscoe County",
      censusArea: 900.001,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.041484, 34.312444],
          [-101.47158, 34.31229],
          [-101.471562, 34.747462],
          [-101.090749, 34.748246],
          [-100.944939, 34.748281],
          [-100.946132, 34.312759],
          [-100.966896, 34.312711],
          [-100.966969, 34.31264],
          [-101.041484, 34.312444]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48049",
      state: "48",
      county: "049",
      name: "Brown County",
      censusArea: 944.433,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.118665, 32.079585],
          [-98.924401, 32.078018],
          [-98.668441, 31.700514],
          [-98.760057, 31.687896],
          [-98.779951, 31.610735],
          [-98.991608, 31.484071],
          [-99.090622, 31.460927],
          [-99.202768, 31.467234],
          [-99.195871, 32.079235],
          [-99.147918, 32.079242],
          [-99.12534, 32.079187],
          [-99.118665, 32.079585]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48055",
      state: "48",
      county: "055",
      name: "Caldwell County",
      censusArea: 545.257,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.315823, 29.786541],
          [-97.317893, 29.784658],
          [-97.597743, 29.630718],
          [-97.633176, 29.649863],
          [-97.721852, 29.682088],
          [-97.875259, 29.858208],
          [-97.893896, 29.883734],
          [-97.710215, 30.024499],
          [-97.655257, 30.072907],
          [-97.649449, 30.067866],
          [-97.648043, 30.067262],
          [-97.315823, 29.786541]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48061",
      state: "48",
      county: "061",
      name: "Cameron County",
      censusArea: 890.922,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.790358, 26.337438],
          [-97.527582, 26.299304],
          [-97.446987, 26.324947],
          [-97.382485, 26.411326],
          [-97.377769, 26.409107],
          [-97.369627, 26.394603],
          [-97.374461, 26.380862],
          [-97.388965, 26.36585],
          [-97.392019, 26.339386],
          [-97.391001, 26.332262],
          [-97.387947, 26.330481],
          [-97.376242, 26.336333],
          [-97.372171, 26.339895],
          [-97.369372, 26.348547],
          [-97.358176, 26.356435],
          [-97.343418, 26.359234],
          [-97.335275, 26.355672],
          [-97.330441, 26.350582],
          [-97.336802, 26.331753],
          [-97.352833, 26.318521],
          [-97.354359, 26.313941],
          [-97.34698, 26.311396],
          [-97.347489, 26.292821],
          [-97.343927, 26.267376],
          [-97.341128, 26.265595],
          [-97.331967, 26.265595],
          [-97.322807, 26.271956],
          [-97.311866, 26.273737],
          [-97.307031, 26.253126],
          [-97.308049, 26.249055],
          [-97.32128, 26.236078],
          [-97.32128, 26.228699],
          [-97.304486, 26.20249],
          [-97.296598, 26.200709],
          [-97.294817, 26.192312],
          [-97.296089, 26.182388],
          [-97.306776, 26.159487],
          [-97.296598, 26.142439],
          [-97.28536, 26.128378],
          [-97.282094, 26.120301],
          [-97.283112, 26.117757],
          [-97.294054, 26.11394],
          [-97.295072, 26.108342],
          [-97.279804, 26.092057],
          [-97.270898, 26.086459],
          [-97.24698, 26.080352],
          [-97.208048, 26.079589],
          [-97.199651, 26.077044],
          [-97.195071, 26.04193],
          [-97.204995, 26.030225],
          [-97.214918, 26.030734],
          [-97.224842, 26.027426],
          [-97.226114, 26.024372],
          [-97.219244, 25.996128],
          [-97.216954, 25.993838],
          [-97.208557, 25.991802],
          [-97.195834, 25.993074],
          [-97.17446, 26.000072],
          [-97.167208, 26.007069],
          [-97.162755, 26.014576],
          [-97.162628, 26.023482],
          [-97.172043, 26.044729],
          [-97.178659, 26.045492],
          [-97.18273, 26.053126],
          [-97.164982, 26.063876],
          [-97.152009, 26.062108],
          [-97.15321, 26.038906],
          [-97.151922, 26.017653],
          [-97.145567, 25.971132],
          [-97.172633, 25.962883],
          [-97.228025, 25.958936],
          [-97.271382, 25.951832],
          [-97.348275, 25.931143],
          [-97.362345, 25.915724],
          [-97.367045, 25.903021],
          [-97.366723, 25.885066],
          [-97.408357, 25.856459],
          [-97.496861, 25.880058],
          [-97.511161, 25.887458],
          [-97.582565, 25.937857],
          [-97.668099, 26.022522],
          [-97.671568, 26.033955],
          [-97.863056, 26.061273],
          [-97.861822, 26.069921],
          [-97.861837, 26.15948],
          [-97.861875, 26.348156],
          [-97.800874, 26.338949],
          [-97.799073, 26.338749],
          [-97.798373, 26.338549],
          [-97.791666, 26.337715],
          [-97.790358, 26.337438]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48065",
      state: "48",
      county: "065",
      name: "Carson County",
      censusArea: 920.22,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.085935, 35.619102],
          [-101.086281, 35.18214],
          [-101.622941, 35.183117],
          [-101.622993, 35.184339],
          [-101.6228, 35.620196],
          [-101.622831, 35.624057],
          [-101.086068, 35.625267],
          [-101.085935, 35.619102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48067",
      state: "48",
      county: "067",
      name: "Cass County",
      censusArea: 936.963,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.04299, 33.271243],
          [-94.04299, 33.271227],
          [-94.04305, 33.260904],
          [-94.043004, 33.250128],
          [-94.04273, 33.241823],
          [-94.042876, 33.215219],
          [-94.042892, 33.202666],
          [-94.042875, 33.199785],
          [-94.043185, 33.143476],
          [-94.043077, 33.138162],
          [-94.043007, 33.13389],
          [-94.04287, 33.092727],
          [-94.043036, 33.079485],
          [-94.042964, 33.019219],
          [-94.043088, 32.955592],
          [-94.043067, 32.937903],
          [-94.043092, 32.910021],
          [-94.042885, 32.898911],
          [-94.042859, 32.892771],
          [-94.042886, 32.881089],
          [-94.046061, 32.880949],
          [-94.277594, 32.880532],
          [-94.653994, 32.879506],
          [-94.65226, 33.268861],
          [-94.572463, 33.249826],
          [-94.517868, 33.282096],
          [-94.415739, 33.284017],
          [-94.379409, 33.255651],
          [-94.231333, 33.241073],
          [-94.218517, 33.296936],
          [-94.056144, 33.298036],
          [-94.04299, 33.271243]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48073",
      state: "48",
      county: "073",
      name: "Cherokee County",
      censusArea: 1052.913,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.865857, 31.526916],
          [-95.000212, 31.428196],
          [-95.003345, 31.42571],
          [-95.101382, 31.469887],
          [-95.176359, 31.584826],
          [-95.273203, 31.592886],
          [-95.258859, 31.609959],
          [-95.412908, 31.835157],
          [-95.446747, 31.843116],
          [-95.428512, 32.084475],
          [-95.447088, 32.107291],
          [-95.456843, 32.112307],
          [-95.459908, 32.135559],
          [-94.985272, 32.137989],
          [-94.985037, 31.846193],
          [-94.937532, 31.845555],
          [-94.967167, 31.753767],
          [-94.951969, 31.641351],
          [-94.865857, 31.526916]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48077",
      state: "48",
      county: "077",
      name: "Clay County",
      censusArea: 1088.721,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.421419, 33.466797],
          [-98.423145, 33.610163],
          [-98.423577, 33.836047],
          [-98.433769, 34.096116],
          [-98.400494, 34.121778],
          [-98.398441, 34.128456],
          [-98.383222, 34.147806],
          [-98.364023, 34.157109],
          [-98.325445, 34.151025],
          [-98.300209, 34.134579],
          [-98.293901, 34.13302],
          [-98.200075, 34.116783],
          [-98.16912, 34.114171],
          [-98.106261, 34.033696],
          [-98.088203, 34.005481],
          [-98.08526, 34.003259],
          [-98.041117, 33.993456],
          [-98.019485, 33.993804],
          [-97.983521, 34.001559],
          [-97.94573, 33.989839],
          [-97.953395, 33.936445],
          [-97.977859, 33.889929],
          [-97.97781, 33.889894],
          [-97.978919, 33.467129],
          [-98.421419, 33.466797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48081",
      state: "48",
      county: "081",
      name: "Coke County",
      censusArea: 911.47,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.235761, 31.69297],
          [-100.82537, 31.696155],
          [-100.821594, 32.08661],
          [-100.665353, 32.085407],
          [-100.235137, 32.082372],
          [-100.235761, 31.69297]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48085",
      state: "48",
      county: "085",
      name: "Collin County",
      censusArea: 841.225,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.682467, 32.985325],
          [-96.687954, 32.985259],
          [-96.838963, 32.987521],
          [-96.839552, 32.987508],
          [-96.843979, 32.987554],
          [-96.841854, 33.086264],
          [-96.840426, 33.152585],
          [-96.834004, 33.405504],
          [-96.386474, 33.398075],
          [-96.384602, 33.397826],
          [-96.384495, 33.341119],
          [-96.295413, 33.351936],
          [-96.297227, 32.981752],
          [-96.516866, 32.982308],
          [-96.613377, 32.984021],
          [-96.660185, 32.985127],
          [-96.682467, 32.985325]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48087",
      state: "48",
      county: "087",
      name: "Collingsworth County",
      censusArea: 918.441,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.000384, 35.030385],
          [-100.000381, 34.746461],
          [-100.000381, 34.746358],
          [-100.415895, 34.747525],
          [-100.540703, 34.747723],
          [-100.540997, 34.750052],
          [-100.538978, 35.183144],
          [-100.000385, 35.182702],
          [-100.000384, 35.030385]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48091",
      state: "48",
      county: "091",
      name: "Comal County",
      censusArea: 559.476,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.443852, 29.71965],
          [-98.550489, 29.760713],
          [-98.646124, 29.745181],
          [-98.644703, 29.746487],
          [-98.414018, 29.937557],
          [-98.2976, 30.037994],
          [-98.030523, 29.848539],
          [-97.999271, 29.752444],
          [-98.089941, 29.683479],
          [-98.310928, 29.594473],
          [-98.378068, 29.662613],
          [-98.338618, 29.721786],
          [-98.443852, 29.71965]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24047",
      state: "24",
      county: "047",
      name: "Worcester County",
      censusArea: 468.281,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.242266, 38.027209],
          [-75.242296, 38.027206],
          [-75.250358, 38.026489],
          [-75.256076, 38.02598],
          [-75.260635, 38.025574],
          [-75.262088, 38.025445],
          [-75.263779, 38.025295],
          [-75.377851, 38.015145],
          [-75.398839, 38.013277],
          [-75.42881, 38.010854],
          [-75.435956, 38.010282],
          [-75.624341, 37.994211],
          [-75.661827, 38.041853],
          [-75.54087, 38.08948],
          [-75.545898, 38.17874],
          [-75.612764, 38.278626],
          [-75.364641, 38.290359],
          [-75.307011, 38.35476],
          [-75.341247, 38.45197],
          [-75.26035, 38.451492],
          [-75.252723, 38.451397],
          [-75.185413, 38.451013],
          [-75.141894, 38.451196],
          [-75.089649, 38.451254],
          [-75.088281, 38.451256],
          [-75.085814, 38.451258],
          [-75.070356, 38.451276],
          [-75.069909, 38.451276],
          [-75.066327, 38.451291],
          [-75.053483, 38.451274],
          [-75.05251, 38.451273],
          [-75.049268, 38.451264],
          [-75.048939, 38.451263],
          [-75.049365, 38.448518],
          [-75.049442, 38.448023],
          [-75.049582, 38.447117],
          [-75.051997, 38.431549],
          [-75.052008, 38.431479],
          [-75.052167, 38.430457],
          [-75.052206, 38.430206],
          [-75.052226, 38.430077],
          [-75.052368, 38.429159],
          [-75.052426, 38.428784],
          [-75.052467, 38.428521],
          [-75.052471, 38.428493],
          [-75.052505, 38.428275],
          [-75.052746, 38.42672],
          [-75.052804, 38.426349],
          [-75.05282, 38.426243],
          [-75.0531, 38.424441],
          [-75.053137, 38.424201],
          [-75.053277, 38.423297],
          [-75.053309, 38.423095],
          [-75.054591, 38.41483],
          [-75.055838, 38.410164],
          [-75.056, 38.40956],
          [-75.056182, 38.408876],
          [-75.057288, 38.404738],
          [-75.06137, 38.389466],
          [-75.068111, 38.368716],
          [-75.068162, 38.368559],
          [-75.068404, 38.367812],
          [-75.068548, 38.36737],
          [-75.068559, 38.367335],
          [-75.069817, 38.363463],
          [-75.069845, 38.363376],
          [-75.071329, 38.358809],
          [-75.071632, 38.357876],
          [-75.071687, 38.357707],
          [-75.071694, 38.357686],
          [-75.072111, 38.356402],
          [-75.072476, 38.355278],
          [-75.073852, 38.352006],
          [-75.074532, 38.35039],
          [-75.084149, 38.327526],
          [-75.085171, 38.325096],
          [-75.085327, 38.324724],
          [-75.085468, 38.324389],
          [-75.085518, 38.32427],
          [-75.087466, 38.322769],
          [-75.092142, 38.323252],
          [-75.093888, 38.323432],
          [-75.102947, 38.311525],
          [-75.103757, 38.309349],
          [-75.116837, 38.274229],
          [-75.143229, 38.220475],
          [-75.155351, 38.192572],
          [-75.15897, 38.184242],
          [-75.16164, 38.176383],
          [-75.177394, 38.130014],
          [-75.178945, 38.126798],
          [-75.192925, 38.097819],
          [-75.193796, 38.096013],
          [-75.195382, 38.093582],
          [-75.204684, 38.079317],
          [-75.204911, 38.07897],
          [-75.206515, 38.07651],
          [-75.216117, 38.061786],
          [-75.22434, 38.050912],
          [-75.227592, 38.046612],
          [-75.23586, 38.035679],
          [-75.236065, 38.035409],
          [-75.237538, 38.033461],
          [-75.241817, 38.027802],
          [-75.242266, 38.027209]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25007",
      state: "25",
      county: "007",
      name: "Dukes County",
      censusArea: 103.245,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-70.6948, 41.52564],
            [-70.691364, 41.517842],
            [-70.688353, 41.516789],
            [-70.681397, 41.51755],
            [-70.674646, 41.519385],
            [-70.672253, 41.515774],
            [-70.669518, 41.513339],
            [-70.675379, 41.512623],
            [-70.705181, 41.496677],
            [-70.734306, 41.486335],
            [-70.757171, 41.469917],
            [-70.756481, 41.465977],
            [-70.760863, 41.460947],
            [-70.79027, 41.446339],
            [-70.817478, 41.445562],
            [-70.835867, 41.441877],
            [-70.857528, 41.425767],
            [-70.866946, 41.422378],
            [-70.902763, 41.421061],
            [-70.928197, 41.415781],
            [-70.937282, 41.411618],
            [-70.948431, 41.409193],
            [-70.951045, 41.411777],
            [-70.949861, 41.415323],
            [-70.928165, 41.431265],
            [-70.923698, 41.430716],
            [-70.918983, 41.4253],
            [-70.91164, 41.424484],
            [-70.906011, 41.425708],
            [-70.883247, 41.432239],
            [-70.855265, 41.448892],
            [-70.828546, 41.456448],
            [-70.802186, 41.460864],
            [-70.787769, 41.474609],
            [-70.775268, 41.477465],
            [-70.753905, 41.492256],
            [-70.745053, 41.500966],
            [-70.6948, 41.52564]
          ]
        ],
        [
          [
            [-70.59628, 41.471905],
            [-70.57485, 41.468259],
            [-70.567356, 41.471208],
            [-70.56328, 41.469127],
            [-70.553277, 41.452955],
            [-70.552943, 41.443394],
            [-70.555588, 41.430882],
            [-70.553096, 41.423952],
            [-70.547567, 41.415831],
            [-70.538301, 41.409241],
            [-70.528581, 41.4051],
            [-70.517584, 41.403769],
            [-70.506984, 41.400242],
            [-70.502372, 41.392005],
            [-70.501306, 41.385391],
            [-70.498959, 41.384339],
            [-70.490758, 41.383634],
            [-70.484503, 41.38629],
            [-70.472604, 41.399128],
            [-70.473035, 41.408757],
            [-70.470788, 41.412875],
            [-70.463833, 41.419145],
            [-70.450431, 41.420703],
            [-70.446233, 41.39648],
            [-70.449268, 41.380422],
            [-70.448262, 41.353651],
            [-70.451084, 41.348161],
            [-70.496162, 41.346452],
            [-70.538294, 41.348958],
            [-70.599157, 41.349272],
            [-70.709826, 41.341723],
            [-70.733253, 41.336226],
            [-70.747541, 41.329952],
            [-70.764188, 41.318706],
            [-70.768015, 41.311959],
            [-70.766166, 41.308962],
            [-70.768687, 41.303702],
            [-70.775665, 41.300982],
            [-70.802083, 41.314207],
            [-70.819415, 41.327212],
            [-70.838777, 41.347209],
            [-70.833802, 41.353386],
            [-70.812309, 41.355745],
            [-70.800289, 41.3538],
            [-70.783291, 41.347829],
            [-70.774974, 41.349176],
            [-70.768901, 41.353246],
            [-70.729225, 41.397728],
            [-70.724366, 41.398942],
            [-70.712432, 41.40885],
            [-70.711493, 41.41546],
            [-70.701378, 41.430925],
            [-70.686881, 41.441334],
            [-70.64933, 41.461068],
            [-70.603555, 41.482384],
            [-70.598444, 41.481151],
            [-70.59628, 41.471905]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25013",
      state: "25",
      county: "013",
      name: "Hampden County",
      censusArea: 617.14,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.141843, 42.160898],
          [-72.135023, 42.161708],
          [-72.134954, 42.090665],
          [-72.135715, 42.030245],
          [-72.198828, 42.030982],
          [-72.397428, 42.033302],
          [-72.509187, 42.034607],
          [-72.695927, 42.036788],
          [-72.714134, 42.036608],
          [-72.755838, 42.036195],
          [-72.757467, 42.020947],
          [-72.758151, 42.020865],
          [-72.766139, 42.007695],
          [-72.766739, 42.002995],
          [-72.774757, 42.002129],
          [-72.816741, 41.997595],
          [-72.813541, 42.036494],
          [-72.863619, 42.037709],
          [-72.863733, 42.03771],
          [-73.008739, 42.039356],
          [-73.053254, 42.039861],
          [-73.071199, 42.148214],
          [-73.007993, 42.238409],
          [-73.001846, 42.25109],
          [-73.000067, 42.312693],
          [-72.885184, 42.332615],
          [-72.912302, 42.239133],
          [-72.793414, 42.236854],
          [-72.686861, 42.18339],
          [-72.613138, 42.286265],
          [-72.593433, 42.211691],
          [-72.592418, 42.211691],
          [-72.546356, 42.216489],
          [-72.403947, 42.231847],
          [-72.395492, 42.185812],
          [-72.221218, 42.245252],
          [-72.263876, 42.183885],
          [-72.141843, 42.160898]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25015",
      state: "25",
      county: "015",
      name: "Hampshire County",
      censusArea: 527.255,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.213946, 42.29429],
          [-72.210815, 42.29446],
          [-72.203363, 42.291162],
          [-72.207245, 42.28521],
          [-72.212128, 42.282672],
          [-72.212941, 42.269925],
          [-72.217879, 42.270089],
          [-72.216138, 42.256144],
          [-72.211275, 42.255635],
          [-72.211079, 42.251262],
          [-72.21046, 42.247205],
          [-72.214584, 42.247571],
          [-72.214821, 42.249041],
          [-72.218068, 42.248605],
          [-72.221218, 42.245252],
          [-72.395492, 42.185812],
          [-72.403947, 42.231847],
          [-72.546356, 42.216489],
          [-72.592418, 42.211691],
          [-72.593433, 42.211691],
          [-72.613138, 42.286265],
          [-72.686861, 42.18339],
          [-72.793414, 42.236854],
          [-72.912302, 42.239133],
          [-72.885184, 42.332615],
          [-73.000067, 42.312693],
          [-72.975379, 42.555933],
          [-72.876948, 42.541213],
          [-72.871157, 42.484173],
          [-72.763746, 42.46361],
          [-72.704345, 42.405536],
          [-72.581106, 42.422504],
          [-72.54421, 42.427081],
          [-72.375022, 42.420819],
          [-72.355396, 42.340678],
          [-72.356583, 42.303275],
          [-72.314599, 42.343526],
          [-72.213946, 42.29429]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25017",
      state: "25",
      county: "017",
      name: "Middlesex County",
      censusArea: 817.817,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.585168, 42.310977],
          [-71.592502, 42.317901],
          [-71.59735, 42.321149],
          [-71.601885, 42.326971],
          [-71.605158, 42.330259],
          [-71.603591, 42.331676],
          [-71.604099, 42.336768],
          [-71.611573, 42.339008],
          [-71.619131, 42.343349],
          [-71.625825, 42.349724],
          [-71.624702, 42.350465],
          [-71.543297, 42.466451],
          [-71.538949, 42.543069],
          [-71.635812, 42.524091],
          [-71.650613, 42.526191],
          [-71.651713, 42.526391],
          [-71.655413, 42.526891],
          [-71.664613, 42.528291],
          [-71.665513, 42.528391],
          [-71.675714, 42.52999],
          [-71.67898, 42.530351],
          [-71.67773, 42.539397],
          [-71.664614, 42.611589],
          [-71.775742, 42.644216],
          [-71.85841, 42.63384],
          [-71.898714, 42.711478],
          [-71.80545, 42.709137],
          [-71.77251, 42.70831],
          [-71.636214, 42.704888],
          [-71.631814, 42.704788],
          [-71.54252, 42.702726],
          [-71.294205, 42.69699],
          [-71.254532, 42.734136],
          [-71.256103, 42.656691],
          [-71.165015, 42.597959],
          [-71.158617, 42.612948],
          [-71.148613, 42.613249],
          [-71.13529, 42.599061],
          [-71.076133, 42.604077],
          [-71.053496, 42.475895],
          [-71.051571, 42.473891],
          [-71.044796, 42.466195],
          [-71.044729, 42.465724],
          [-71.040044, 42.460706],
          [-71.039114, 42.459513],
          [-71.03727, 42.457674],
          [-71.025864, 42.444743],
          [-71.025777, 42.444753],
          [-71.020377, 42.43825],
          [-71.033203, 42.414226],
          [-71.055295, 42.387097],
          [-71.073496, 42.391796],
          [-71.080595, 42.380997],
          [-71.064095, 42.368997],
          [-71.069795, 42.369097],
          [-71.077095, 42.358697],
          [-71.099196, 42.352797],
          [-71.117099, 42.355594],
          [-71.117197, 42.367197],
          [-71.130997, 42.373796],
          [-71.143501, 42.364969],
          [-71.148565, 42.361174],
          [-71.167625, 42.360073],
          [-71.174798, 42.350265],
          [-71.167565, 42.333441],
          [-71.156833, 42.330189],
          [-71.178935, 42.314316],
          [-71.164804, 42.303764],
          [-71.178636, 42.294595],
          [-71.191155, 42.283059],
          [-71.269919, 42.328096],
          [-71.332434, 42.247729],
          [-71.329217, 42.245023],
          [-71.329561, 42.23367],
          [-71.34083, 42.224605],
          [-71.343065, 42.220242],
          [-71.339837, 42.213886],
          [-71.34698, 42.215555],
          [-71.352024, 42.207311],
          [-71.34417, 42.200703],
          [-71.404381, 42.188215],
          [-71.40222, 42.178798],
          [-71.430174, 42.179386],
          [-71.444067, 42.174889],
          [-71.463957, 42.158239],
          [-71.478119, 42.156782],
          [-71.599318, 42.225959],
          [-71.586759, 42.259545],
          [-71.549466, 42.266124],
          [-71.513048, 42.267148],
          [-71.497046, 42.289829],
          [-71.486125, 42.310885],
          [-71.486768, 42.330075],
          [-71.551126, 42.326395],
          [-71.585168, 42.310977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34017",
      state: "34",
      county: "017",
      name: "Hudson County",
      censusArea: 46.191,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.986864, 40.798344],
          [-73.993029, 40.788746],
          [-74.000905, 40.776488],
          [-74.009184, 40.763601],
          [-74.013784, 40.756601],
          [-74.024543, 40.709436],
          [-74.038538, 40.710741],
          [-74.051185, 40.695802],
          [-74.069885, 40.684502],
          [-74.082786, 40.673702],
          [-74.089986, 40.659903],
          [-74.087397, 40.653607],
          [-74.094086, 40.649703],
          [-74.143387, 40.641903],
          [-74.161397, 40.644092],
          [-74.160288, 40.645903],
          [-74.153041, 40.6548],
          [-74.136703, 40.674444],
          [-74.122911, 40.691172],
          [-74.112787, 40.703102],
          [-74.117057, 40.710381],
          [-74.116993, 40.737125],
          [-74.161958, 40.736007],
          [-74.165987, 40.745199],
          [-74.164799, 40.754718],
          [-74.163388, 40.759401],
          [-74.157688, 40.7661],
          [-74.155798, 40.770815],
          [-74.148266, 40.779876],
          [-74.147859, 40.784683],
          [-74.147529, 40.786491],
          [-74.044977, 40.807702],
          [-74.017204, 40.823541],
          [-73.986864, 40.798344]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34019",
      state: "34",
      county: "019",
      name: "Hunterdon County",
      censusArea: 427.819,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.724418, 40.719455],
          [-74.71315, 40.583903],
          [-74.782513, 40.487775],
          [-74.748147, 40.424156],
          [-74.856262, 40.346695],
          [-74.937954, 40.340634],
          [-75.024775, 40.403455],
          [-75.035548, 40.406309],
          [-75.058848, 40.418065],
          [-75.061489, 40.422848],
          [-75.070568, 40.455165],
          [-75.065853, 40.519495],
          [-75.06509, 40.526148],
          [-75.067344, 40.536428],
          [-75.068615, 40.542223],
          [-75.100325, 40.567811],
          [-75.117292, 40.573211],
          [-75.135389, 40.575624],
          [-75.147368, 40.573152],
          [-75.190161, 40.589321],
          [-75.190858, 40.591342],
          [-74.968454, 40.710791],
          [-74.889753, 40.787832],
          [-74.724418, 40.719455]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34021",
      state: "34",
      county: "021",
      name: "Mercer County",
      censusArea: 224.557,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.724179, 40.147324],
          [-74.721604, 40.15381],
          [-74.722304, 40.160609],
          [-74.758613, 40.201342],
          [-74.770706, 40.214908],
          [-74.860492, 40.284584],
          [-74.937954, 40.340634],
          [-74.856262, 40.346695],
          [-74.748147, 40.424156],
          [-74.722156, 40.375146],
          [-74.622744, 40.384421],
          [-74.617525, 40.377862],
          [-74.619591, 40.374397],
          [-74.572559, 40.301054],
          [-74.484287, 40.253299],
          [-74.587847, 40.138237],
          [-74.589318, 40.138051],
          [-74.589614, 40.13819],
          [-74.589638, 40.138741],
          [-74.589954, 40.138879],
          [-74.591221, 40.137913],
          [-74.591754, 40.137981],
          [-74.592468, 40.138494],
          [-74.59265, 40.137818],
          [-74.593354, 40.137855],
          [-74.593987, 40.137541],
          [-74.59477, 40.137875],
          [-74.724179, 40.147324]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34029",
      state: "34",
      county: "029",
      name: "Ocean County",
      censusArea: 628.781,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.417393, 39.557255],
          [-74.390399, 39.596037],
          [-74.389905, 39.773428],
          [-74.434653, 39.854803],
          [-74.528808, 40.036964],
          [-74.553105, 40.07913],
          [-74.406777, 40.172355],
          [-74.263763, 40.168264],
          [-74.224411, 40.108982],
          [-74.10803, 40.136873],
          [-74.031861, 40.101047],
          [-74.031318, 40.100541],
          [-74.033546, 40.099518],
          [-74.039421, 40.081437],
          [-74.058798, 40.001244],
          [-74.064135, 39.979157],
          [-74.077247, 39.910991],
          [-74.090945, 39.799978],
          [-74.097071, 39.767847],
          [-74.096906, 39.76303],
          [-74.09892, 39.759538],
          [-74.101443, 39.756173],
          [-74.113655, 39.740719],
          [-74.141733, 39.689435],
          [-74.190974, 39.625118],
          [-74.240506, 39.554911],
          [-74.249043, 39.547994],
          [-74.27737, 39.514064],
          [-74.291585, 39.507705],
          [-74.311037, 39.506715],
          [-74.312451, 39.499869],
          [-74.417393, 39.557255]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34033",
      state: "34",
      county: "033",
      name: "Salem County",
      censusArea: 331.899,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.440623, 39.780926],
          [-75.378232, 39.745763],
          [-75.326452, 39.689992],
          [-75.199706, 39.666912],
          [-75.061973, 39.568643],
          [-75.084187, 39.45796],
          [-75.239529, 39.559188],
          [-75.368213, 39.456479],
          [-75.407294, 39.381954],
          [-75.422099, 39.386521],
          [-75.431803, 39.391625],
          [-75.442393, 39.402291],
          [-75.465212, 39.43893],
          [-75.476279, 39.438126],
          [-75.483572, 39.440824],
          [-75.505672, 39.452927],
          [-75.508383, 39.459131],
          [-75.536431, 39.460559],
          [-75.542894, 39.470447],
          [-75.544368, 39.479602],
          [-75.542693, 39.496568],
          [-75.528088, 39.498114],
          [-75.527141, 39.500112],
          [-75.514756, 39.562612],
          [-75.511932, 39.567616],
          [-75.512732, 39.578],
          [-75.525677, 39.584048],
          [-75.531133, 39.587984],
          [-75.534477, 39.590384],
          [-75.537213, 39.592944],
          [-75.53954, 39.594251],
          [-75.539949, 39.594384],
          [-75.543965, 39.596],
          [-75.545405, 39.596784],
          [-75.55587, 39.605824],
          [-75.559446, 39.629812],
          [-75.526844, 39.655713],
          [-75.526344, 39.656413],
          [-75.509342, 39.685313],
          [-75.509742, 39.686113],
          [-75.504042, 39.698313],
          [-75.475384, 39.731057],
          [-75.474168, 39.735473],
          [-75.469239, 39.743613],
          [-75.466263, 39.750737],
          [-75.466249, 39.750769],
          [-75.448135, 39.773969],
          [-75.440623, 39.780926]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34035",
      state: "34",
      county: "035",
      name: "Somerset County",
      censusArea: 301.813,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.782513, 40.487775],
          [-74.71315, 40.583903],
          [-74.724418, 40.719455],
          [-74.555771, 40.758005],
          [-74.548999, 40.740994],
          [-74.528827, 40.728133],
          [-74.526846, 40.710872],
          [-74.513608, 40.697339],
          [-74.534387, 40.667256],
          [-74.523627, 40.649702],
          [-74.510502, 40.650295],
          [-74.503863, 40.659627],
          [-74.484118, 40.663387],
          [-74.484289, 40.666065],
          [-74.47002, 40.667934],
          [-74.461231, 40.673397],
          [-74.408264, 40.644185],
          [-74.417038, 40.628077],
          [-74.43804, 40.60991],
          [-74.463351, 40.599245],
          [-74.515782, 40.544955],
          [-74.514622, 40.542711],
          [-74.512729, 40.540541],
          [-74.503309, 40.534119],
          [-74.499362, 40.531951],
          [-74.495398, 40.529403],
          [-74.490547, 40.523048],
          [-74.459922, 40.491015],
          [-74.489524, 40.474415],
          [-74.493226, 40.47077],
          [-74.494275, 40.469807],
          [-74.619591, 40.374397],
          [-74.617525, 40.377862],
          [-74.622744, 40.384421],
          [-74.722156, 40.375146],
          [-74.748147, 40.424156],
          [-74.782513, 40.487775]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34041",
      state: "34",
      county: "041",
      name: "Warren County",
      censusArea: 356.918,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.980674, 41.078178],
          [-74.968389, 41.087797],
          [-74.966759, 41.093425],
          [-74.967389, 41.094049],
          [-74.966349, 41.094003],
          [-74.78921, 40.931298],
          [-74.767874, 40.911823],
          [-74.889753, 40.787832],
          [-74.968454, 40.710791],
          [-75.190858, 40.591342],
          [-75.196803, 40.60858],
          [-75.200708, 40.618356],
          [-75.20392, 40.691498],
          [-75.196533, 40.751631],
          [-75.171587, 40.777745],
          [-75.16365, 40.778386],
          [-75.149378, 40.774786],
          [-75.1344, 40.773765],
          [-75.108505, 40.791094],
          [-75.053294, 40.8599],
          [-75.051029, 40.865662],
          [-75.052538, 40.872051],
          [-75.117764, 40.953023],
          [-75.119893, 40.961646],
          [-75.120316, 40.96263],
          [-75.120435, 40.968302],
          [-75.120564, 40.968313],
          [-75.131364, 40.969277],
          [-75.135526, 40.973807],
          [-75.130575, 40.991093],
          [-75.109114, 41.004102],
          [-75.091377, 41.012283],
          [-75.069277, 41.019348],
          [-75.051794, 41.027142],
          [-75.036982, 41.034702],
          [-75.026003, 41.042687],
          [-74.980674, 41.078178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35007",
      state: "35",
      county: "007",
      name: "Colfax County",
      censusArea: 3758.06,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.1208, 36.995428],
          [-105.000554, 36.993264],
          [-104.73212, 36.993484],
          [-104.732031, 36.993447],
          [-104.007855, 36.996239],
          [-104.008927, 36.218018],
          [-104.436121, 36.218761],
          [-104.865545, 36.217997],
          [-104.865185, 36.261144],
          [-105.340907, 36.262245],
          [-105.327301, 36.40543],
          [-105.356826, 36.449504],
          [-105.318718, 36.491693],
          [-105.377745, 36.552096],
          [-105.370638, 36.681032],
          [-105.311842, 36.724471],
          [-105.230632, 36.723784],
          [-105.220613, 36.995169],
          [-105.155042, 36.995339],
          [-105.1208, 36.995428]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30055",
      state: "30",
      county: "055",
      name: "Mccone County",
      censusArea: 2643.172,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.418567, 47.957414],
          [-106.394163, 48.064726],
          [-106.229302, 48.026416],
          [-106.006176, 48.033689],
          [-105.843797, 48.010314],
          [-105.647941, 48.077304],
          [-105.498036, 48.097903],
          [-105.2282, 48.09239],
          [-105.194742, 48.065669],
          [-105.194933, 47.86818],
          [-105.234619, 47.79076],
          [-105.362992, 47.79042],
          [-105.363449, 47.5294],
          [-105.406511, 47.529214],
          [-105.406237, 47.181563],
          [-105.833042, 47.180967],
          [-105.832846, 47.093144],
          [-105.960127, 47.092969],
          [-105.960094, 47.180981],
          [-106.086777, 47.180938],
          [-106.170434, 47.18093],
          [-106.170675, 47.354708],
          [-106.298189, 47.354391],
          [-106.298087, 47.5289],
          [-106.260585, 47.529],
          [-106.261082, 47.868312],
          [-106.358288, 47.868412],
          [-106.358288, 47.957216],
          [-106.418567, 47.957414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30057",
      state: "30",
      county: "057",
      name: "Madison County",
      censusArea: 3587.484,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.805134, 45.798018],
          [-111.536816, 45.656736],
          [-111.536614, 45.642131],
          [-111.351346, 45.641938],
          [-111.377682, 44.751744],
          [-111.394815, 44.751351],
          [-111.481536, 44.709186],
          [-111.781713, 44.708848],
          [-111.781812, 44.781129],
          [-112.06587, 44.783752],
          [-112.065678, 44.825985],
          [-112.167103, 44.826722],
          [-112.191197, 45.000439],
          [-112.334258, 45.000616],
          [-112.333813, 45.088463],
          [-112.456847, 45.088574],
          [-112.457744, 45.349963],
          [-112.505346, 45.443048],
          [-112.628609, 45.440586],
          [-112.691417, 45.483518],
          [-112.687054, 45.624854],
          [-112.549534, 45.684317],
          [-112.515453, 45.746702],
          [-112.190269, 45.748503],
          [-112.106378, 45.841188],
          [-111.930144, 45.857148],
          [-111.805134, 45.798018]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37195",
      state: "37",
      county: "195",
      name: "Wilson County",
      censusArea: 368.174,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.19207, 35.730443],
          [-77.931355, 35.830165],
          [-77.927859, 35.831508],
          [-77.915581, 35.83659],
          [-77.828444, 35.867208],
          [-77.751567, 35.82786],
          [-77.661312, 35.677321],
          [-77.688822, 35.660166],
          [-77.700502, 35.652256],
          [-77.753646, 35.615818],
          [-77.801698, 35.586132],
          [-77.806097, 35.583457],
          [-77.809014, 35.584179],
          [-77.811456, 35.586056],
          [-77.816125, 35.588443],
          [-77.818142, 35.587825],
          [-77.822372, 35.585467],
          [-77.823471, 35.585523],
          [-78.064603, 35.585419],
          [-78.125587, 35.603111],
          [-78.19207, 35.730443]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37199",
      state: "37",
      county: "199",
      name: "Yancey County",
      censusArea: 312.597,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.462958, 36.007309],
          [-82.416857, 36.072885],
          [-82.338245, 36.030294],
          [-82.199347, 36.015792],
          [-82.133182, 35.824446],
          [-82.197226, 35.736369],
          [-82.275394, 35.704423],
          [-82.34816, 35.810581],
          [-82.408728, 35.818061],
          [-82.50586, 35.978342],
          [-82.487451, 35.991557],
          [-82.487411, 35.991634],
          [-82.462958, 36.007309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38005",
      state: "38",
      county: "005",
      name: "Benson County",
      censusArea: 1388.708,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.200306, 48.370658],
          [-99.199911, 48.155309],
          [-99.070443, 48.107857],
          [-99.070351, 48.021955],
          [-99.001038, 48.053294],
          [-98.991253, 47.99026],
          [-98.803893, 48.059901],
          [-98.75061, 47.982456],
          [-98.672086, 48.0004],
          [-98.625384, 47.916399],
          [-98.525555, 47.915056],
          [-98.525516, 47.846276],
          [-99.297204, 47.846811],
          [-99.812167, 47.847257],
          [-99.812133, 48.021451],
          [-99.848222, 48.021424],
          [-99.846613, 48.371298],
          [-99.492919, 48.370946],
          [-99.200306, 48.370658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38009",
      state: "38",
      county: "009",
      name: "Bottineau County",
      censusArea: 1668.421,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.05966, 48.545337],
          [-101.451275, 48.546079],
          [-101.451152, 48.720018],
          [-101.496684, 48.720078],
          [-101.496737, 48.999355],
          [-101.225915, 48.999531],
          [-101.225187, 48.999566],
          [-101.220754, 48.999455],
          [-101.216182, 48.999469],
          [-100.920577, 48.99956],
          [-100.917939, 48.999571],
          [-100.913634, 48.999662],
          [-100.907107, 48.999593],
          [-100.433981, 48.99941],
          [-100.434351, 48.99957],
          [-100.431642, 48.999604],
          [-100.431676, 48.999398],
          [-100.182707, 48.99923],
          [-100.183034, 48.718909],
          [-100.145494, 48.718875],
          [-100.145858, 48.545211],
          [-100.27666, 48.544813],
          [-100.406388, 48.544657],
          [-100.40678, 48.631878],
          [-101.059624, 48.632424],
          [-101.05966, 48.545337]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38017",
      state: "38",
      county: "017",
      name: "Cass County",
      censusArea: 1764.937,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.832946, 47.237588],
          [-96.826655, 47.150539],
          [-96.824807, 47.124968],
          [-96.82318, 46.999965],
          [-96.824598, 46.993309],
          [-96.823222, 46.970861],
          [-96.791558, 46.934264],
          [-96.780258, 46.928263],
          [-96.767458, 46.905163],
          [-96.802013, 46.812464],
          [-96.784205, 46.686768],
          [-96.783014, 46.630647],
          [-97.279948, 46.629348],
          [-97.682006, 46.629928],
          [-97.681508, 46.978481],
          [-97.706027, 46.978558],
          [-97.706034, 47.239978],
          [-97.451512, 47.239061],
          [-96.833019, 47.238095],
          [-96.832946, 47.237588]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38027",
      state: "38",
      county: "027",
      name: "Eddy County",
      censusArea: 630.171,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.265846, 47.587618],
          [-99.297318, 47.673004],
          [-99.297204, 47.846811],
          [-98.525516, 47.846276],
          [-98.500181, 47.672413],
          [-98.500085, 47.587228],
          [-99.265846, 47.587618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38029",
      state: "38",
      county: "029",
      name: "Emmons County",
      censusArea: 1510.435,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.880062, 45.94167],
          [-99.880292, 45.941672],
          [-99.965775, 45.941822],
          [-100.005486, 45.94195],
          [-100.06902, 45.94217],
          [-100.084163, 45.942301],
          [-100.108471, 45.942391],
          [-100.110339, 45.942367],
          [-100.14173, 45.942506],
          [-100.152084, 45.942486],
          [-100.170826, 45.942514],
          [-100.274762, 45.942945],
          [-100.275614, 45.942922],
          [-100.284134, 45.942951],
          [-100.285345, 45.94313],
          [-100.294126, 45.943269],
          [-100.410386, 45.943453],
          [-100.420162, 45.943533],
          [-100.424438, 45.943569],
          [-100.430597, 45.943638],
          [-100.462838, 45.943566],
          [-100.499354, 45.943632],
          [-100.511793, 45.943654],
          [-100.511949, 45.943654],
          [-100.614504, 46.071796],
          [-100.629145, 46.145608],
          [-100.558549, 46.249993],
          [-100.551199, 46.363494],
          [-100.590199, 46.427894],
          [-100.544555, 46.533766],
          [-100.66189, 46.63445],
          [-100.081198, 46.633396],
          [-99.915508, 46.632925],
          [-99.916758, 46.283171],
          [-99.878431, 46.282811],
          [-99.880062, 45.94167]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38037",
      state: "38",
      county: "037",
      name: "Grant County",
      censusArea: 1659.139,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.025662, 46.284219],
          [-101.198073, 46.18377],
          [-101.190372, 46.147847],
          [-101.554185, 46.006836],
          [-101.842108, 46.029136],
          [-101.849355, 46.073275],
          [-101.998575, 46.053461],
          [-101.997888, 46.20548],
          [-102.046929, 46.283606],
          [-102.050986, 46.630871],
          [-102.096585, 46.631024],
          [-102.096395, 46.68895],
          [-102.096239, 46.717706],
          [-101.718155, 46.718019],
          [-101.718106, 46.630999],
          [-101.299052, 46.63031],
          [-101.29899, 46.371451],
          [-101.048532, 46.371393],
          [-101.025662, 46.284219]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38039",
      state: "38",
      county: "039",
      name: "Griggs County",
      censusArea: 708.815,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.500085, 47.587228],
          [-98.500181, 47.672413],
          [-97.98481, 47.6723],
          [-97.986585, 47.3261],
          [-97.961208, 47.240512],
          [-98.467476, 47.240453],
          [-98.467263, 47.32653],
          [-98.499246, 47.326538],
          [-98.500085, 47.587228]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38043",
      state: "38",
      county: "043",
      name: "Kidder County",
      censusArea: 1351.189,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.915508, 46.632925],
          [-100.081198, 46.633396],
          [-100.075229, 46.981282],
          [-100.114637, 46.981666],
          [-100.113261, 47.327262],
          [-100.094739, 47.327326],
          [-100.093491, 47.327087],
          [-100.036063, 47.327304],
          [-100.033292, 47.327192],
          [-99.480726, 47.327076],
          [-99.481085, 46.980694],
          [-99.446423, 46.980889],
          [-99.44972, 46.63163],
          [-99.915508, 46.632925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38047",
      state: "38",
      county: "047",
      name: "Logan County",
      censusArea: 992.818,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.44972, 46.63163],
          [-99.036639, 46.630575],
          [-99.037367, 46.283151],
          [-99.878431, 46.282811],
          [-99.916758, 46.283171],
          [-99.915508, 46.632925],
          [-99.44972, 46.63163]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38053",
      state: "38",
      county: "053",
      name: "Mckenzie County",
      censusArea: 2760.323,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.099197, 47.328714],
          [-103.666723, 47.329354],
          [-104.045307, 47.330128],
          [-104.045313, 47.331955],
          [-104.045333, 47.343452],
          [-104.045069, 47.397461],
          [-104.044797, 47.438445],
          [-104.044621, 47.45938],
          [-104.043242, 47.747106],
          [-104.043199, 47.747292],
          [-104.042384, 47.803256],
          [-104.042432, 47.805358],
          [-104.042567, 47.808237],
          [-104.041869, 47.841699],
          [-104.041662, 47.862282],
          [-104.04223, 47.891031],
          [-104.043329, 47.949554],
          [-104.043497, 47.95449],
          [-104.043933, 47.971515],
          [-104.044162, 47.992836],
          [-104.04412, 47.996107],
          [-103.919789, 47.95595],
          [-103.856306, 48.015072],
          [-103.800817, 47.994269],
          [-103.742216, 48.093403],
          [-103.597044, 48.13441],
          [-103.548572, 48.087724],
          [-103.584774, 48.054124],
          [-103.501172, 48.005925],
          [-103.234865, 48.061923],
          [-103.20189, 48.135208],
          [-103.107083, 48.146494],
          [-102.828477, 48.123664],
          [-102.663487, 48.076651],
          [-102.552981, 47.998646],
          [-102.644764, 47.906715],
          [-102.642266, 47.823713],
          [-102.642766, 47.673911],
          [-103.099867, 47.674416],
          [-103.099197, 47.328714]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38059",
      state: "38",
      county: "059",
      name: "Morton County",
      censusArea: 1926.27,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.025662, 46.284219],
          [-101.048532, 46.371393],
          [-101.29899, 46.371451],
          [-101.299052, 46.63031],
          [-101.718106, 46.630999],
          [-101.718155, 46.718019],
          [-102.096239, 46.717706],
          [-102.097087, 46.980813],
          [-102.096675, 46.981159],
          [-101.764392, 46.981306],
          [-100.935983, 46.98284],
          [-100.819089, 46.792424],
          [-100.839414, 46.763776],
          [-100.66189, 46.63445],
          [-100.544555, 46.533766],
          [-100.590199, 46.427894],
          [-100.721261, 46.390084],
          [-100.91895, 46.394983],
          [-101.025662, 46.284219]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38063",
      state: "38",
      county: "063",
      name: "Nelson County",
      censusArea: 981.775,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.525516, 47.846276],
          [-98.525555, 47.915056],
          [-98.525609, 48.02115],
          [-98.422659, 48.021115],
          [-98.4221, 48.194824],
          [-98.292184, 48.195177],
          [-97.903434, 48.194897],
          [-97.882179, 48.020803],
          [-97.882405, 47.672179],
          [-97.98481, 47.6723],
          [-98.500181, 47.672413],
          [-98.525516, 47.846276]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47001",
      state: "47",
      county: "001",
      name: "Anderson County",
      censusArea: 337.162,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.271587, 35.910165],
          [-84.32006, 35.991607],
          [-84.324987, 36.004535],
          [-84.340197, 36.044973],
          [-84.340405, 36.045727],
          [-84.341812, 36.048977],
          [-84.441034, 36.163478],
          [-84.372731, 36.216778],
          [-84.241825, 36.177578],
          [-84.227527, 36.244879],
          [-84.069918, 36.230378],
          [-84.004317, 36.270378],
          [-83.941711, 36.186378],
          [-84.094011, 36.068911],
          [-84.058585, 36.036304],
          [-84.195151, 35.993008],
          [-84.271587, 35.910165]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47003",
      state: "47",
      county: "003",
      name: "Bedford County",
      censusArea: 473.635,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.25759, 35.412476],
          [-86.444169, 35.3207],
          [-86.525306, 35.354718],
          [-86.543671, 35.363211],
          [-86.552694, 35.359983],
          [-86.582875, 35.370983],
          [-86.599478, 35.36495],
          [-86.658247, 35.435281],
          [-86.639436, 35.6859],
          [-86.543116, 35.697617],
          [-86.403312, 35.618874],
          [-86.34194, 35.664259],
          [-86.245165, 35.631913],
          [-86.25759, 35.412476]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40065",
      state: "40",
      county: "065",
      name: "Jackson County",
      censusArea: 802.651,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.211423, 34.337897],
          [-99.261321, 34.403499],
          [-99.356713, 34.442144],
          [-99.398635, 34.375832],
          [-99.407168, 34.372605],
          [-99.44076, 34.374123],
          [-99.453477, 34.388756],
          [-99.474161, 34.398052],
          [-99.499875, 34.409608],
          [-99.515385, 34.414333],
          [-99.574367, 34.418281],
          [-99.58006, 34.416653],
          [-99.616793, 34.375391],
          [-99.663948, 34.37368],
          [-99.69497, 34.378333],
          [-99.767234, 34.430502],
          [-99.793684, 34.453894],
          [-99.846399, 34.505045],
          [-99.666512, 34.507121],
          [-99.666849, 34.724555],
          [-99.423201, 34.72502],
          [-99.405821, 34.813038],
          [-99.246462, 34.822114],
          [-99.147265, 34.850389],
          [-99.149114, 34.702352],
          [-99.052511, 34.701557],
          [-99.10315, 34.63795],
          [-99.220707, 34.494484],
          [-99.211396, 34.337688],
          [-99.211423, 34.337897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40071",
      state: "40",
      county: "071",
      name: "Kay County",
      censusArea: 919.729,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.749838, 36.998988],
          [-96.752382, 36.782092],
          [-96.889475, 36.75123],
          [-96.931943, 36.686097],
          [-97.064107, 36.684207],
          [-97.057414, 36.593647],
          [-97.461603, 36.593637],
          [-97.462459, 36.593627],
          [-97.462346, 36.998685],
          [-97.46228, 36.998685],
          [-97.384925, 36.998843],
          [-97.372421, 36.998861],
          [-97.147721, 36.999111],
          [-97.122597, 36.999036],
          [-97.120285, 36.999014],
          [-97.104276, 36.99902],
          [-97.100652, 36.998998],
          [-97.039784, 36.999],
          [-97.030082, 36.998929],
          [-96.975562, 36.999019],
          [-96.967371, 36.999067],
          [-96.934642, 36.99907],
          [-96.921915, 36.999151],
          [-96.917093, 36.999182],
          [-96.90351, 36.999132],
          [-96.902083, 36.999155],
          [-96.87629, 36.999233],
          [-96.867517, 36.999217],
          [-96.822791, 36.999182],
          [-96.795199, 36.99886],
          [-96.79206, 36.99918],
          [-96.749838, 36.998988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40083",
      state: "40",
      county: "083",
      name: "Logan County",
      censusArea: 743.834,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.140583, 35.941991],
          [-97.141072, 35.724441],
          [-97.674026, 35.72597],
          [-97.675617, 36.164663],
          [-97.460792, 36.164462],
          [-97.353946, 36.164304],
          [-97.354132, 36.159072],
          [-97.35387, 35.984934],
          [-97.32362, 36.010877],
          [-97.140583, 35.941991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40085",
      state: "40",
      county: "085",
      name: "Love County",
      censusArea: 513.995,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.97287, 33.935698],
          [-96.985567, 33.886522],
          [-97.00613, 33.861884],
          [-97.047972, 33.817931],
          [-97.092414, 33.733214],
          [-97.097154, 33.727809],
          [-97.107183, 33.721119],
          [-97.121102, 33.717174],
          [-97.13753, 33.718664],
          [-97.151257, 33.722608],
          [-97.162803, 33.729127],
          [-97.163149, 33.729322],
          [-97.172192, 33.737545],
          [-97.204827, 33.799908],
          [-97.204995, 33.81887],
          [-97.1997, 33.827322],
          [-97.194678, 33.831192],
          [-97.18137, 33.831375],
          [-97.171627, 33.835335],
          [-97.166824, 33.840395],
          [-97.166629, 33.847311],
          [-97.180845, 33.895204],
          [-97.185458, 33.9007],
          [-97.210921, 33.916064],
          [-97.226522, 33.914642],
          [-97.310843, 33.872461],
          [-97.372941, 33.819454],
          [-97.426493, 33.819398],
          [-97.444193, 33.823773],
          [-97.453057, 33.828536],
          [-97.459068, 33.834581],
          [-97.462857, 33.841772],
          [-97.461486, 33.84956],
          [-97.457617, 33.855126],
          [-97.451469, 33.87093],
          [-97.450954, 33.891398],
          [-97.458069, 33.901635],
          [-97.484071, 33.91568],
          [-97.486505, 33.916994],
          [-97.50096, 33.919643],
          [-97.519171, 33.913638],
          [-97.545526, 33.900396],
          [-97.555002, 33.897282],
          [-97.561054, 33.898006],
          [-97.560764, 34.071089],
          [-96.969627, 34.07102],
          [-96.934508, 33.953748],
          [-96.944611, 33.948994],
          [-96.97287, 33.935698]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40091",
      state: "40",
      county: "091",
      name: "Mcintosh County",
      censusArea: 618.496,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.981376, 35.289899],
          [-95.981334, 35.355297],
          [-95.981423, 35.375123],
          [-95.981307, 35.376828],
          [-95.875626, 35.37669],
          [-95.875572, 35.463625],
          [-95.823396, 35.463969],
          [-95.822361, 35.551693],
          [-95.712957, 35.551802],
          [-95.34503, 35.552241],
          [-95.34465, 35.293097],
          [-95.45125, 35.296019],
          [-95.59938, 35.221651],
          [-95.628064, 35.246798],
          [-95.75597, 35.177198],
          [-95.870663, 35.185986],
          [-95.98371, 35.151557],
          [-95.981376, 35.289899]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40099",
      state: "40",
      county: "099",
      name: "Murray County",
      censusArea: 416.458,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.933457, 34.332661],
          [-97.036488, 34.375415],
          [-97.352158, 34.376227],
          [-97.35211, 34.506788],
          [-97.143958, 34.506532],
          [-97.200073, 34.622762],
          [-96.932328, 34.636811],
          [-96.932423, 34.593261],
          [-96.82729, 34.59322],
          [-96.827288, 34.505989],
          [-96.880814, 34.506053],
          [-96.881005, 34.332977],
          [-96.933457, 34.332661]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40101",
      state: "40",
      county: "101",
      name: "Muskogee County",
      censusArea: 810.45,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.766166, 35.856403],
          [-95.65042, 35.856756],
          [-95.638313, 35.787596],
          [-95.265679, 35.813266],
          [-95.127163, 35.812755],
          [-95.127464, 35.638894],
          [-95.132437, 35.526196],
          [-95.049933, 35.458894],
          [-95.170462, 35.310106],
          [-95.243809, 35.261636],
          [-95.34465, 35.293097],
          [-95.34503, 35.552241],
          [-95.712957, 35.551802],
          [-95.713081, 35.725807],
          [-95.766265, 35.72575],
          [-95.766166, 35.856403]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40107",
      state: "40",
      county: "107",
      name: "Okfuskee County",
      censusArea: 618.567,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.623699, 35.400722],
          [-96.624865, 35.462706],
          [-96.620648, 35.639005],
          [-96.192563, 35.639087],
          [-96.192844, 35.551902],
          [-96.087795, 35.552119],
          [-96.08753, 35.376877],
          [-95.981307, 35.376828],
          [-95.981423, 35.375123],
          [-95.981334, 35.355297],
          [-95.981376, 35.289899],
          [-96.441368, 35.290122],
          [-96.44129, 35.467712],
          [-96.536718, 35.390772],
          [-96.623699, 35.400722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40111",
      state: "40",
      county: "111",
      name: "Okmulgee County",
      censusArea: 697.349,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.192563, 35.639087],
          [-96.192482, 35.857021],
          [-96.033118, 35.856824],
          [-95.819964, 35.8559],
          [-95.806224, 35.856028],
          [-95.805637, 35.856577],
          [-95.766166, 35.856403],
          [-95.766265, 35.72575],
          [-95.713081, 35.725807],
          [-95.712957, 35.551802],
          [-95.822361, 35.551693],
          [-95.823396, 35.463969],
          [-95.875572, 35.463625],
          [-95.875626, 35.37669],
          [-95.981307, 35.376828],
          [-96.08753, 35.376877],
          [-96.087795, 35.552119],
          [-96.192844, 35.551902],
          [-96.192563, 35.639087]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40117",
      state: "40",
      county: "117",
      name: "Pawnee County",
      censusArea: 567.955,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.268287, 36.16198],
          [-96.297888, 36.162279],
          [-96.621333, 36.163553],
          [-96.621441, 36.159929],
          [-96.819005, 36.158886],
          [-96.818968, 36.246478],
          [-96.925069, 36.246474],
          [-96.924936, 36.333344],
          [-97.032106, 36.333354],
          [-97.009785, 36.506935],
          [-96.940235, 36.455525],
          [-96.753554, 36.562601],
          [-96.710769, 36.437656],
          [-96.635727, 36.425908],
          [-96.575697, 36.374347],
          [-96.518709, 36.377576],
          [-96.573481, 36.315709],
          [-96.515229, 36.298332],
          [-96.457684, 36.332664],
          [-96.433812, 36.285889],
          [-96.326732, 36.226551],
          [-96.243295, 36.206677],
          [-96.268287, 36.16198]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40119",
      state: "40",
      county: "119",
      name: "Payne County",
      censusArea: 684.703,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.140583, 35.941991],
          [-97.32362, 36.010877],
          [-97.35387, 35.984934],
          [-97.354132, 36.159072],
          [-97.140668, 36.159231],
          [-97.140968, 36.246435],
          [-96.925069, 36.246474],
          [-96.818968, 36.246478],
          [-96.819005, 36.158886],
          [-96.621441, 36.159929],
          [-96.621097, 35.941519],
          [-97.140583, 35.941991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40125",
      state: "40",
      county: "125",
      name: "Pottawatomie County",
      censusArea: 787.672,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.623699, 35.400722],
          [-96.776707, 35.411372],
          [-96.775681, 34.899958],
          [-96.821488, 34.946112],
          [-96.930586, 34.964364],
          [-97.01593, 34.907348],
          [-97.142349, 34.928176],
          [-97.142114, 35.37686],
          [-97.14149, 35.463977],
          [-96.624865, 35.462706],
          [-96.623699, 35.400722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25021",
      state: "25",
      county: "021",
      name: "Norfolk County",
      censusArea: 396.105,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.498258, 42.01722],
          [-71.498259, 42.027813],
          [-71.497901, 42.031513],
          [-71.498262, 42.031333],
          [-71.498284, 42.064802],
          [-71.50091, 42.117041],
          [-71.478521, 42.131421],
          [-71.478119, 42.156782],
          [-71.463957, 42.158239],
          [-71.444067, 42.174889],
          [-71.430174, 42.179386],
          [-71.40222, 42.178798],
          [-71.404381, 42.188215],
          [-71.34417, 42.200703],
          [-71.352024, 42.207311],
          [-71.34698, 42.215555],
          [-71.339837, 42.213886],
          [-71.343065, 42.220242],
          [-71.34083, 42.224605],
          [-71.329561, 42.23367],
          [-71.329217, 42.245023],
          [-71.332434, 42.247729],
          [-71.269919, 42.328096],
          [-71.191155, 42.283059],
          [-71.130808, 42.22788],
          [-71.112938, 42.258641],
          [-71.102691, 42.259884],
          [-71.088672, 42.269733],
          [-71.082968, 42.268496],
          [-71.065651, 42.271052],
          [-71.061593, 42.267299],
          [-71.053395, 42.272297],
          [-71.055494, 42.275698],
          [-71.042794, 42.276998],
          [-71.041694, 42.305298],
          [-71.005399, 42.307196],
          [-71.000948, 42.302483],
          [-71.006158, 42.28811],
          [-71.0049, 42.28272],
          [-70.996097, 42.271222],
          [-70.98909, 42.267449],
          [-70.967351, 42.268168],
          [-70.948971, 42.272505],
          [-70.945547, 42.269081],
          [-70.935886, 42.264189],
          [-70.923169, 42.263211],
          [-70.910941, 42.265412],
          [-70.906302, 42.271636],
          [-70.924877, 42.15758],
          [-70.981293, 42.135502],
          [-70.993114, 42.130855],
          [-71.002029, 42.126506],
          [-71.080192, 42.095699],
          [-71.138619, 42.072813],
          [-71.138641, 42.072761],
          [-71.168681, 42.061161],
          [-71.288265, 42.014462],
          [-71.288265, 42.014451],
          [-71.288263, 42.014379],
          [-71.364887, 41.985173],
          [-71.381466, 41.984998],
          [-71.381401, 42.018798],
          [-71.458104, 42.017762],
          [-71.498258, 42.01722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25023",
      state: "25",
      county: "023",
      name: "Plymouth County",
      censusArea: 659.075,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.921782, 41.791244],
          [-71.03657, 41.816525],
          [-70.999705, 41.929592],
          [-71.049498, 41.96315],
          [-71.054718, 41.985057],
          [-71.080192, 42.095699],
          [-71.002029, 42.126506],
          [-70.993114, 42.130855],
          [-70.981293, 42.135502],
          [-70.924877, 42.15758],
          [-70.906302, 42.271636],
          [-70.896267, 42.2851],
          [-70.895778, 42.292436],
          [-70.897123, 42.29586],
          [-70.915588, 42.302463],
          [-70.91749, 42.305686],
          [-70.907556, 42.307889],
          [-70.882764, 42.30886],
          [-70.881242, 42.300663],
          [-70.870873, 42.285668],
          [-70.861807, 42.275965],
          [-70.851093, 42.26827],
          [-70.831075, 42.267424],
          [-70.824661, 42.265935],
          [-70.811742, 42.262935],
          [-70.788724, 42.25392],
          [-70.780722, 42.251792],
          [-70.770964, 42.249197],
          [-70.764757, 42.244062],
          [-70.754488, 42.228673],
          [-70.74723, 42.221816],
          [-70.73056, 42.21094],
          [-70.722269, 42.207959],
          [-70.718707, 42.184853],
          [-70.714301, 42.168783],
          [-70.706264, 42.163137],
          [-70.685315, 42.133025],
          [-70.663931, 42.108336],
          [-70.640169, 42.088633],
          [-70.63848, 42.081579],
          [-70.647349, 42.076331],
          [-70.64819, 42.068441],
          [-70.643208, 42.050821],
          [-70.644337, 42.045895],
          [-70.650874, 42.046247],
          [-70.66936, 42.037116],
          [-70.671666, 42.02139],
          [-70.667512, 42.01232],
          [-70.670934, 42.007786],
          [-70.678798, 42.00551],
          [-70.686798, 42.012764],
          [-70.695809, 42.013346],
          [-70.712204, 42.007586],
          [-70.710034, 41.999544],
          [-70.698981, 41.987103],
          [-70.662476, 41.960592],
          [-70.651673, 41.958701],
          [-70.648365, 41.961672],
          [-70.631251, 41.950475],
          [-70.623513, 41.943273],
          [-70.616491, 41.940204],
          [-70.608166, 41.940701],
          [-70.598078, 41.947772],
          [-70.583572, 41.950007],
          [-70.552941, 41.929641],
          [-70.546386, 41.916751],
          [-70.54741, 41.911934],
          [-70.545949, 41.907158],
          [-70.532084, 41.889568],
          [-70.525567, 41.85873],
          [-70.535487, 41.839381],
          [-70.542065, 41.831263],
          [-70.543168, 41.824446],
          [-70.54103, 41.815754],
          [-70.537289, 41.810859],
          [-70.565281, 41.786705],
          [-70.632578, 41.762821],
          [-70.621622, 41.748972],
          [-70.642914, 41.71841],
          [-70.644641, 41.71898],
          [-70.651093, 41.715715],
          [-70.656596, 41.715401],
          [-70.670453, 41.721912],
          [-70.708193, 41.730959],
          [-70.718739, 41.73574],
          [-70.726331, 41.732731],
          [-70.728933, 41.723433],
          [-70.721302, 41.712968],
          [-70.717451, 41.69398],
          [-70.719575, 41.685002],
          [-70.729395, 41.68814],
          [-70.744396, 41.696967],
          [-70.755347, 41.694326],
          [-70.761481, 41.676808],
          [-70.761497, 41.676641],
          [-70.76236, 41.667735],
          [-70.758198, 41.661225],
          [-70.757622, 41.654265],
          [-70.765463, 41.641575],
          [-70.769318, 41.641145],
          [-70.773654, 41.645033],
          [-70.775798, 41.649145],
          [-70.776709, 41.650756],
          [-70.809118, 41.656437],
          [-70.813286, 41.65567],
          [-70.815729, 41.652796],
          [-70.816351, 41.645995],
          [-70.804664, 41.641157],
          [-70.800215, 41.631753],
          [-70.801063, 41.629513],
          [-70.810279, 41.624873],
          [-70.835296, 41.624532],
          [-70.843177, 41.628487],
          [-70.921782, 41.791244]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26003",
      state: "26",
      county: "003",
      name: "Alger County",
      censusArea: 915.07,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.11608, 46.158945],
          [-87.116618, 46.495163],
          [-87.107559, 46.496124],
          [-87.09876, 46.503609],
          [-87.077279, 46.515339],
          [-87.046022, 46.519956],
          [-87.029892, 46.525599],
          [-87.017136, 46.53355],
          [-87.008724, 46.532723],
          [-86.976958, 46.526581],
          [-86.964534, 46.516549],
          [-86.962842, 46.509646],
          [-86.94698, 46.484567],
          [-86.946218, 46.479059],
          [-86.949526, 46.476315],
          [-86.947077, 46.472064],
          [-86.927725, 46.464566],
          [-86.903742, 46.466138],
          [-86.889094, 46.458499],
          [-86.883976, 46.450976],
          [-86.883919, 46.441514],
          [-86.875151, 46.43728],
          [-86.850111, 46.434114],
          [-86.837448, 46.434186],
          [-86.816026, 46.437892],
          [-86.810967, 46.449663],
          [-86.808817, 46.460611],
          [-86.803557, 46.466669],
          [-86.787905, 46.477729],
          [-86.768516, 46.479072],
          [-86.750157, 46.479109],
          [-86.735929, 46.475231],
          [-86.731096, 46.47176],
          [-86.730829, 46.468057],
          [-86.710573, 46.444908],
          [-86.70323, 46.439378],
          [-86.698139, 46.438624],
          [-86.686412, 46.454965],
          [-86.688816, 46.463152],
          [-86.686468, 46.471655],
          [-86.683819, 46.498079],
          [-86.696001, 46.50316],
          [-86.701929, 46.511571],
          [-86.709325, 46.543914],
          [-86.695645, 46.555026],
          [-86.678182, 46.561039],
          [-86.675764, 46.557061],
          [-86.670927, 46.556489],
          [-86.656479, 46.558453],
          [-86.652865, 46.560555],
          [-86.62738, 46.53371],
          [-86.629086, 46.518144],
          [-86.632109, 46.508865],
          [-86.63453, 46.504523],
          [-86.641088, 46.500438],
          [-86.645528, 46.492039],
          [-86.646393, 46.485776],
          [-86.636671, 46.478298],
          [-86.627441, 46.47754],
          [-86.620603, 46.483873],
          [-86.618061, 46.489452],
          [-86.612173, 46.493295],
          [-86.609393, 46.492976],
          [-86.606932, 46.478531],
          [-86.609039, 46.470239],
          [-86.586168, 46.463324],
          [-86.557731, 46.487434],
          [-86.524959, 46.505381],
          [-86.495054, 46.524874],
          [-86.484003, 46.535965],
          [-86.481956, 46.542709],
          [-86.469306, 46.551422],
          [-86.45993, 46.551928],
          [-86.44439, 46.548137],
          [-86.437167, 46.54896],
          [-86.390409, 46.563194],
          [-86.34989, 46.578035],
          [-86.188024, 46.654008],
          [-86.161681, 46.669475],
          [-86.138295, 46.672935],
          [-86.119862, 46.657256],
          [-86.112126, 46.655044],
          [-86.099843, 46.654615],
          [-86.074219, 46.657799],
          [-86.036969, 46.667627],
          [-85.995044, 46.673676],
          [-85.95367, 46.676869],
          [-85.924047, 46.684733],
          [-85.877908, 46.690914],
          [-85.864549, 46.690182],
          [-85.864932, 46.504758],
          [-86.364953, 46.505852],
          [-86.365261, 46.331929],
          [-86.615397, 46.333263],
          [-86.615601, 46.158691],
          [-87.11608, 46.158945]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26005",
      state: "26",
      county: "005",
      name: "Allegan County",
      censusArea: 825.231,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.782498, 42.7682],
          [-85.712659, 42.768237],
          [-85.545564, 42.768139],
          [-85.543191, 42.421433],
          [-85.764352, 42.420669],
          [-86.273893, 42.41928],
          [-86.261573, 42.443894],
          [-86.24971, 42.480212],
          [-86.241446, 42.534697],
          [-86.240642, 42.54],
          [-86.23528, 42.564958],
          [-86.235254, 42.565023],
          [-86.234755, 42.566302],
          [-86.234594, 42.566715],
          [-86.23119, 42.575435],
          [-86.228082, 42.583397],
          [-86.226037, 42.592811],
          [-86.225978, 42.593084],
          [-86.225613, 42.594765],
          [-86.22905, 42.637693],
          [-86.226638, 42.644922],
          [-86.21602, 42.664413],
          [-86.208654, 42.69209],
          [-86.206834, 42.719424],
          [-86.208309, 42.762789],
          [-86.208886, 42.76754],
          [-85.782498, 42.7682]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26007",
      state: "26",
      county: "007",
      name: "Alpena County",
      censusArea: 571.86,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.387587, 45.207107],
          [-83.384265, 45.203472],
          [-83.381647, 45.203357],
          [-83.368896, 45.182168],
          [-83.368046, 45.172478],
          [-83.363678, 45.166469],
          [-83.359895, 45.16302],
          [-83.348684, 45.161516],
          [-83.337822, 45.14712],
          [-83.316118, 45.141958],
          [-83.315924, 45.139992],
          [-83.319315, 45.137684],
          [-83.318442, 45.12893],
          [-83.30788, 45.099093],
          [-83.298275, 45.090483],
          [-83.290827, 45.069157],
          [-83.291346, 45.062597],
          [-83.280272, 45.045962],
          [-83.277037, 45.044767],
          [-83.271464, 45.038114],
          [-83.265896, 45.026844],
          [-83.271506, 45.023417],
          [-83.287974, 45.026462],
          [-83.302153, 45.032315],
          [-83.340257, 45.041545],
          [-83.357609, 45.050613],
          [-83.36747, 45.062268],
          [-83.399255, 45.070364],
          [-83.433798, 45.057616],
          [-83.442052, 45.051056],
          [-83.453363, 45.035331],
          [-83.454168, 45.03188],
          [-83.446342, 45.016655],
          [-83.435249, 45.011883],
          [-83.431254, 45.007998],
          [-83.435822, 45.000012],
          [-83.438948, 45.000011],
          [-83.450013, 44.990219],
          [-83.443718, 44.952247],
          [-83.438856, 44.940843],
          [-83.433032, 44.93289],
          [-83.425311, 44.926741],
          [-83.404596, 44.918761],
          [-83.398879, 44.906417],
          [-83.39396, 44.903056],
          [-83.352815, 44.886164],
          [-83.320503, 44.880571],
          [-83.321093, 44.858494],
          [-83.888484, 44.856458],
          [-83.881218, 45.202062],
          [-83.387587, 45.207107]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30063",
      state: "30",
      county: "063",
      name: "Missoula County",
      censusArea: 2593.424,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.797235, 47.268901],
          [-114.550033, 47.251614],
          [-114.352769, 47.190407],
          [-114.254476, 47.120639],
          [-114.187383, 47.138192],
          [-113.821994, 47.181622],
          [-113.821126, 47.268599],
          [-113.886455, 47.318622],
          [-113.870895, 47.392118],
          [-113.951187, 47.476007],
          [-113.914926, 47.500299],
          [-113.929999, 47.598483],
          [-113.948763, 47.600517],
          [-113.633957, 47.599861],
          [-113.466486, 47.599986],
          [-113.466122, 47.178941],
          [-113.302873, 47.179433],
          [-113.303005, 46.832132],
          [-113.479917, 46.80273],
          [-113.479817, 46.744929],
          [-113.667726, 46.716026],
          [-113.667225, 46.658426],
          [-113.827831, 46.661023],
          [-114.332887, 46.660756],
          [-114.341336, 46.642803],
          [-114.349584, 46.646684],
          [-114.370129, 46.654217],
          [-114.422176, 46.652163],
          [-114.44683, 46.645314],
          [-114.461211, 46.639151],
          [-114.481943, 46.631993],
          [-114.592264, 46.632861],
          [-114.594707, 46.633558],
          [-114.614423, 46.639188],
          [-114.635868, 46.65951],
          [-114.642713, 46.673145],
          [-114.641745, 46.679286],
          [-114.641322, 46.686404],
          [-114.642692, 46.694622],
          [-114.644747, 46.702155],
          [-114.655704, 46.711743],
          [-114.667346, 46.719276],
          [-114.675505, 46.719516],
          [-114.548929, 46.747366],
          [-114.565991, 46.963758],
          [-114.525403, 47.009321],
          [-114.419662, 47.023267],
          [-114.483637, 47.067326],
          [-114.565985, 47.067339],
          [-114.586753, 47.12369],
          [-114.712026, 47.182124],
          [-114.71217, 47.22559],
          [-114.775739, 47.225548],
          [-114.797235, 47.268901]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30065",
      state: "30",
      county: "065",
      name: "Musselshell County",
      censusArea: 1868.155,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.827755, 46.755986],
          [-107.792939, 46.67983],
          [-107.781214, 46.496014],
          [-107.782089, 46.395286],
          [-107.928505, 46.39529],
          [-108.011552, 46.336639],
          [-108.029531, 46.263812],
          [-108.32073, 46.264061],
          [-108.40464, 46.236126],
          [-108.404936, 46.132675],
          [-108.78014, 46.132402],
          [-108.779115, 46.27672],
          [-108.861315, 46.364825],
          [-108.904779, 46.577394],
          [-108.988585, 46.578572],
          [-109.009802, 46.750432],
          [-108.63095, 46.749414],
          [-107.827755, 46.755986]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30071",
      state: "30",
      county: "071",
      name: "Phillips County",
      censusArea: 5140.035,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.179865, 48.999559],
          [-107.206146, 48.912592],
          [-107.206426, 48.567989],
          [-107.193567, 48.509267],
          [-107.25819, 48.514825],
          [-107.301721, 48.480943],
          [-107.301073, 48.394],
          [-107.260454, 48.393864],
          [-107.260593, 48.30475],
          [-107.369306, 48.305134],
          [-107.369899, 48.218294],
          [-107.404555, 48.218303],
          [-107.414886, 47.691677],
          [-107.450151, 47.622077],
          [-107.600981, 47.650011],
          [-107.695788, 47.628756],
          [-107.75931, 47.552006],
          [-107.908907, 47.450638],
          [-107.931105, 47.569073],
          [-108.065418, 47.598294],
          [-108.312615, 47.583452],
          [-108.771619, 47.645523],
          [-108.891022, 47.734407],
          [-108.888163, 47.922746],
          [-108.625708, 47.92082],
          [-108.608027, 47.991075],
          [-108.433966, 47.976552],
          [-108.433848, 48.219443],
          [-108.411366, 48.219443],
          [-108.410374, 48.448484],
          [-108.34593, 48.422296],
          [-108.346193, 48.56845],
          [-108.318834, 48.568432],
          [-108.319144, 48.74229],
          [-108.259347, 48.742191],
          [-108.259372, 48.913592],
          [-108.236495, 48.999405],
          [-107.179865, 48.999559]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30077",
      state: "30",
      county: "077",
      name: "Powell County",
      censusArea: 2326.392,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.303005, 46.832132],
          [-113.302873, 47.179433],
          [-113.466122, 47.178941],
          [-113.466486, 47.599986],
          [-113.144926, 47.596363],
          [-113.114863, 47.490564],
          [-113.059444, 47.488096],
          [-113.058866, 47.179369],
          [-112.796594, 47.177222],
          [-112.79532, 46.831772],
          [-112.535608, 46.834382],
          [-112.535313, 46.698527],
          [-112.41079, 46.699696],
          [-112.410855, 46.656711],
          [-112.298607, 46.625918],
          [-112.31061, 46.421006],
          [-112.558457, 46.265925],
          [-113.036834, 46.266822],
          [-113.038691, 46.736132],
          [-113.115791, 46.71359],
          [-113.303005, 46.832132]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30083",
      state: "30",
      county: "083",
      name: "Richland County",
      censusArea: 2084.143,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.043933, 47.971515],
          [-104.043497, 47.95449],
          [-104.043329, 47.949554],
          [-104.04223, 47.891031],
          [-104.041662, 47.862282],
          [-104.041869, 47.841699],
          [-104.042567, 47.808237],
          [-104.042432, 47.805358],
          [-104.042384, 47.803256],
          [-104.043199, 47.747292],
          [-104.043242, 47.747106],
          [-104.044621, 47.45938],
          [-104.044797, 47.438445],
          [-104.045069, 47.397461],
          [-104.131525, 47.354148],
          [-104.420263, 47.354596],
          [-104.513279, 47.354718],
          [-104.512883, 47.529252],
          [-104.593811, 47.528966],
          [-104.59363, 47.615951],
          [-104.978516, 47.616288],
          [-104.978, 47.703374],
          [-105.23488, 47.703634],
          [-105.234619, 47.79076],
          [-105.194933, 47.86818],
          [-105.194742, 48.065669],
          [-105.177722, 48.1031],
          [-105.119117, 48.0596],
          [-104.990346, 48.066449],
          [-104.891321, 48.15069],
          [-104.822581, 48.118945],
          [-104.681633, 48.103323],
          [-104.544517, 48.126553],
          [-104.245936, 48.031005],
          [-104.14247, 48.055005],
          [-104.098921, 48.000019],
          [-104.04412, 47.996107],
          [-104.044162, 47.992836],
          [-104.043933, 47.971515]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30087",
      state: "30",
      county: "087",
      name: "Rosebud County",
      censusArea: 5010.402,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.781214, 46.496014],
          [-107.792939, 46.67983],
          [-107.827755, 46.755986],
          [-107.892117, 46.851537],
          [-107.466813, 46.860486],
          [-106.720027, 46.859603],
          [-106.720258, 46.845251],
          [-106.086504, 46.845968],
          [-106.086335, 46.831269],
          [-106.120909, 46.83131],
          [-106.120415, 46.483591],
          [-106.155265, 46.48347],
          [-106.153268, 46.137194],
          [-106.191438, 46.137019],
          [-106.191861, 45.7886],
          [-106.234263, 45.788401],
          [-106.235575, 45.351796],
          [-106.279478, 45.351796],
          [-106.279682, 45.179892],
          [-106.768014, 45.179677],
          [-106.727488, 45.676491],
          [-106.912997, 45.684087],
          [-106.912494, 45.788189],
          [-106.877217, 45.831714],
          [-106.939095, 45.870089],
          [-106.93898, 46.136697],
          [-107.007451, 46.134084],
          [-107.007261, 46.307641],
          [-107.028378, 46.395409],
          [-107.153227, 46.395545],
          [-107.174607, 46.48234],
          [-107.738547, 46.481715],
          [-107.755369, 46.482008],
          [-107.75518, 46.496126],
          [-107.781214, 46.496014]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30091",
      state: "30",
      county: "091",
      name: "Sheridan County",
      censusArea: 1677.08,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.047582, 48.633976],
          [-104.04762, 48.627015],
          [-104.047586, 48.625644],
          [-104.04793, 48.62019],
          [-104.048212, 48.599055],
          [-104.047974, 48.591606],
          [-104.047811, 48.56277],
          [-104.047783, 48.539737],
          [-104.047648, 48.531489],
          [-104.047876, 48.530798],
          [-104.047513, 48.525913],
          [-104.047675, 48.517852],
          [-104.047555, 48.49414],
          [-104.047392, 48.467086],
          [-104.047259, 48.452941],
          [-104.047294, 48.452529],
          [-104.047192, 48.447251],
          [-104.04709, 48.445903],
          [-104.04696, 48.421065],
          [-104.047134, 48.411057],
          [-104.046969, 48.390675],
          [-104.046913, 48.389433],
          [-104.046913, 48.389429],
          [-104.628048, 48.389239],
          [-104.62771, 48.476349],
          [-104.757861, 48.476556],
          [-104.757595, 48.563122],
          [-104.973354, 48.56319],
          [-104.973283, 48.650085],
          [-105.038567, 48.650232],
          [-105.057514, 48.999448],
          [-104.647389, 48.999344],
          [-104.543636, 48.999541],
          [-104.048736, 48.999877],
          [-104.048478, 48.987007],
          [-104.048616, 48.966736],
          [-104.048555, 48.963772],
          [-104.0488, 48.958997],
          [-104.048627, 48.957124],
          [-104.048698, 48.951823],
          [-104.048872, 48.94963],
          [-104.04877, 48.943301],
          [-104.048701, 48.940331],
          [-104.048807, 48.933636],
          [-104.048744, 48.912113],
          [-104.048746, 48.906858],
          [-104.048643, 48.902609],
          [-104.048719, 48.879921],
          [-104.048893, 48.875739],
          [-104.048883, 48.874008],
          [-104.048824, 48.867539],
          [-104.048652, 48.865734],
          [-104.0489, 48.847387],
          [-104.048569, 48.797052],
          [-104.048537, 48.788552],
          [-104.048548, 48.751356],
          [-104.04834, 48.747133],
          [-104.047883, 48.664191],
          [-104.047849, 48.663163],
          [-104.047861, 48.658856],
          [-104.047865, 48.65745],
          [-104.047887, 48.649911],
          [-104.047819, 48.648631],
          [-104.047582, 48.633984],
          [-104.047582, 48.633976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30097",
      state: "30",
      county: "097",
      name: "Sweet Grass County",
      censusArea: 1855.205,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.063819, 45.172102],
          [-110.228549, 45.17282],
          [-110.220902, 45.784787],
          [-110.292662, 45.785022],
          [-110.28186, 46.18421],
          [-110.281858, 46.220519],
          [-109.654304, 46.219402],
          [-109.604968, 46.045377],
          [-109.417297, 46.044485],
          [-109.423259, 45.960214],
          [-109.506715, 45.959139],
          [-109.505939, 45.870272],
          [-109.548918, 45.87094],
          [-109.564149, 45.782856],
          [-109.561958, 45.609119],
          [-109.685289, 45.608906],
          [-109.685551, 45.56563],
          [-109.808793, 45.565925],
          [-109.808841, 45.522424],
          [-109.932028, 45.522609],
          [-109.932223, 45.348561],
          [-110.063347, 45.348759],
          [-110.063819, 45.172102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30101",
      state: "30",
      county: "101",
      name: "Toole County",
      censusArea: 1915.647,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.193588, 48.998229],
          [-111.854088, 48.998067],
          [-111.85409, 48.998039],
          [-111.761679, 48.997614],
          [-111.761613, 48.99765],
          [-111.270702, 48.997895],
          [-111.274733, 48.219444],
          [-111.408922, 48.219476],
          [-111.6665, 48.219412],
          [-111.665829, 48.349497],
          [-111.796117, 48.349814],
          [-111.79591, 48.393011],
          [-111.990819, 48.394285],
          [-111.990944, 48.438546],
          [-112.184375, 48.477593],
          [-112.193588, 48.998229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38065",
      state: "38",
      county: "065",
      name: "Oliver County",
      censusArea: 722.511,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.935983, 46.98284],
          [-101.764392, 46.981306],
          [-101.764419, 47.241423],
          [-101.257944, 47.241347],
          [-101.257031, 47.264895],
          [-101.060424, 47.293293],
          [-100.977629, 47.249992],
          [-100.965156, 47.156631],
          [-100.882172, 47.015115],
          [-100.935983, 46.98284]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38067",
      state: "38",
      county: "067",
      name: "Pembina County",
      censusArea: 1118.694,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.950205, 49.000459],
          [-97.77575, 49.000574],
          [-97.411216, 49.00051],
          [-97.229039, 49.000687],
          [-97.234005, 48.99747],
          [-97.238387, 48.982631],
          [-97.239155, 48.967596],
          [-97.23261, 48.946561],
          [-97.199549, 48.881183],
          [-97.157797, 48.78768],
          [-97.137571, 48.749191],
          [-97.092606, 48.682033],
          [-97.16305, 48.543963],
          [-97.163105, 48.543855],
          [-97.16111, 48.543155],
          [-97.162466, 48.54225],
          [-97.927154, 48.543124],
          [-97.949577, 48.717693],
          [-97.950205, 49.000459]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38071",
      state: "38",
      county: "071",
      name: "Ramsey County",
      censusArea: 1186.854,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.970392, 48.544232],
          [-98.318098, 48.543773],
          [-98.292184, 48.195177],
          [-98.4221, 48.194824],
          [-98.422659, 48.021115],
          [-98.525609, 48.02115],
          [-98.525555, 47.915056],
          [-98.625384, 47.916399],
          [-98.672086, 48.0004],
          [-98.75061, 47.982456],
          [-98.803893, 48.059901],
          [-98.991253, 47.99026],
          [-99.001038, 48.053294],
          [-99.070351, 48.021955],
          [-99.070443, 48.107857],
          [-99.199911, 48.155309],
          [-99.200306, 48.370658],
          [-98.970975, 48.370472],
          [-98.970392, 48.544232]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38075",
      state: "38",
      county: "075",
      name: "Renville County",
      censusArea: 877.053,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.021144, 48.999015],
          [-101.496737, 48.999355],
          [-101.496684, 48.720078],
          [-101.451152, 48.720018],
          [-101.451275, 48.546079],
          [-101.05966, 48.545337],
          [-101.060047, 48.458615],
          [-101.059802, 48.458448],
          [-101.841969, 48.459545],
          [-101.842135, 48.720147],
          [-102.021428, 48.720283],
          [-102.021655, 48.807108],
          [-102.021144, 48.999015]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38081",
      state: "38",
      county: "081",
      name: "Sargent County",
      censusArea: 858.513,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.228304, 45.935141],
          [-97.228323, 45.935141],
          [-97.312184, 45.935077],
          [-97.318899, 45.935054],
          [-97.481967, 45.935138],
          [-97.491892, 45.935111],
          [-97.518944, 45.935304],
          [-97.519035, 45.935304],
          [-97.542598, 45.935258],
          [-97.696691, 45.935352],
          [-97.77704, 45.935393],
          [-97.784575, 45.935327],
          [-97.958718, 45.935878],
          [-97.978778, 45.935937],
          [-97.986893, 45.935961],
          [-98.008102, 45.936095],
          [-98.006715, 46.282626],
          [-97.280568, 46.282321],
          [-97.259339, 46.282197],
          [-97.259632, 45.997185],
          [-97.228291, 45.935141],
          [-97.228304, 45.935141]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38089",
      state: "38",
      county: "089",
      name: "Stark County",
      censusArea: 1334.735,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.096675, 46.981159],
          [-102.097087, 46.980813],
          [-102.096239, 46.717706],
          [-102.096395, 46.68895],
          [-102.096585, 46.631024],
          [-102.928003, 46.630065],
          [-103.231566, 46.629918],
          [-103.230991, 46.980296],
          [-103.032681, 46.98058],
          [-102.525462, 46.981307],
          [-102.525463, 47.01024],
          [-102.144771, 47.010307],
          [-102.144878, 46.981425],
          [-102.096675, 46.981159]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38093",
      state: "38",
      county: "093",
      name: "Stutsman County",
      censusArea: 2221.724,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.467476, 47.240453],
          [-98.46753, 46.979594],
          [-98.439466, 46.979658],
          [-98.439056, 46.63112],
          [-99.036639, 46.630575],
          [-99.44972, 46.63163],
          [-99.446423, 46.980889],
          [-99.481085, 46.980694],
          [-99.480726, 47.327076],
          [-99.26628, 47.327194],
          [-99.250392, 47.326894],
          [-98.499246, 47.326538],
          [-98.467263, 47.32653],
          [-98.467476, 47.240453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38097",
      state: "38",
      county: "097",
      name: "Traill County",
      censusArea: 861.949,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.853286, 47.503881],
          [-96.853768, 47.499527],
          [-96.862312, 47.422333],
          [-96.852676, 47.374973],
          [-96.833019, 47.238095],
          [-97.451512, 47.239061],
          [-97.474337, 47.324776],
          [-97.472745, 47.672051],
          [-96.890671, 47.672153],
          [-96.88574, 47.664266],
          [-96.850955, 47.598287],
          [-96.84243, 47.508225],
          [-96.853286, 47.503881]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38101",
      state: "38",
      county: "101",
      name: "Ward County",
      censusArea: 2013.279,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.021655, 48.807108],
          [-102.021428, 48.720283],
          [-101.842135, 48.720147],
          [-101.841969, 48.459545],
          [-101.059802, 48.458448],
          [-101.014138, 48.371432],
          [-101.015024, 48.022739],
          [-100.970497, 48.022645],
          [-100.971325, 47.848703],
          [-101.871862, 47.847589],
          [-101.871537, 48.023176],
          [-101.922123, 48.023173],
          [-101.922237, 48.37233],
          [-101.973399, 48.372095],
          [-101.973322, 48.547133],
          [-102.233892, 48.546339],
          [-102.2336, 48.72029],
          [-102.152545, 48.720294],
          [-102.152164, 48.8072],
          [-102.021655, 48.807108]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39001",
      state: "39",
      county: "001",
      name: "Adams County",
      censusArea: 583.867,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.642994, 38.643273],
          [-83.646701, 38.637489],
          [-83.649737, 38.632753],
          [-83.655425, 38.629735],
          [-83.663911, 38.62793],
          [-83.679484, 38.630036],
          [-83.706043, 38.637101],
          [-83.705311, 38.639853],
          [-83.673018, 39.02043],
          [-83.611592, 39.01889],
          [-83.385637, 39.055197],
          [-83.270817, 39.015791],
          [-83.265769, 38.605278],
          [-83.2821, 38.602987],
          [-83.320531, 38.622713],
          [-83.324157, 38.633326],
          [-83.333347, 38.641562],
          [-83.369077, 38.659334],
          [-83.512571, 38.701716],
          [-83.520953, 38.703045],
          [-83.533339, 38.702105],
          [-83.574754, 38.692671],
          [-83.615736, 38.684145],
          [-83.626922, 38.679387],
          [-83.636208, 38.670584],
          [-83.642994, 38.643273]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39005",
      state: "39",
      county: "005",
      name: "Ashland County",
      censusArea: 422.95,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.41648, 40.902252],
          [-82.4179, 40.992938],
          [-82.432852, 40.992944],
          [-82.436914, 41.065371],
          [-82.336496, 41.065761],
          [-82.171492, 41.063537],
          [-82.173359, 40.992046],
          [-82.129334, 40.991807],
          [-82.127904, 40.937321],
          [-82.128544, 40.929977],
          [-82.1279, 40.900902],
          [-82.12501, 40.886263],
          [-82.126199, 40.668229],
          [-82.220665, 40.66758],
          [-82.22011, 40.568206],
          [-82.336962, 40.555001],
          [-82.339211, 40.726683],
          [-82.378397, 40.727145],
          [-82.41648, 40.902252]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39011",
      state: "39",
      county: "011",
      name: "Auglaize County",
      censusArea: 401.386,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.455713, 40.567888],
          [-84.45618, 40.684862],
          [-84.396778, 40.684926],
          [-84.222799, 40.685957],
          [-84.107787, 40.643069],
          [-83.880194, 40.64469],
          [-83.879932, 40.538708],
          [-83.993867, 40.535174],
          [-84.002372, 40.483115],
          [-84.339088, 40.481281],
          [-84.338882, 40.378734],
          [-84.434387, 40.354534],
          [-84.455418, 40.358589],
          [-84.455713, 40.567888]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39013",
      state: "39",
      county: "013",
      name: "Belmont County",
      censusArea: 532.129,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.705482, 40.153047],
          [-80.726504, 40.089072],
          [-80.733104, 40.058772],
          [-80.730904, 40.049172],
          [-80.731504, 40.037472],
          [-80.732051, 40.03324],
          [-80.740126, 39.970793],
          [-80.743166, 39.969113],
          [-80.759455, 39.958233],
          [-80.806018, 39.91713],
          [-80.808722, 39.91257],
          [-80.809619, 39.905626],
          [-80.806179, 39.897306],
          [-80.812342, 39.848845],
          [-81.237316, 39.867828],
          [-81.234053, 39.95127],
          [-81.225924, 40.170075],
          [-81.035504, 40.164874],
          [-80.882892, 40.159495],
          [-80.703052, 40.157231],
          [-80.705482, 40.153047]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39017",
      state: "39",
      county: "017",
      name: "Butler County",
      censusArea: 467.056,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.348199, 39.348512],
          [-84.353209, 39.292287],
          [-84.391125, 39.297585],
          [-84.428363, 39.298659],
          [-84.44696, 39.300808],
          [-84.630992, 39.311942],
          [-84.819451, 39.305153],
          [-84.819352, 39.309454],
          [-84.817453, 39.391753],
          [-84.815754, 39.477352],
          [-84.815754, 39.477358],
          [-84.815555, 39.510952],
          [-84.815555, 39.511052],
          [-84.815355, 39.52195],
          [-84.815355, 39.521951],
          [-84.815155, 39.548051],
          [-84.814955, 39.566251],
          [-84.814955, 39.567251],
          [-84.815036, 39.567695],
          [-84.478927, 39.56879],
          [-84.479213, 39.591024],
          [-84.474246, 39.590155],
          [-84.365232, 39.589493],
          [-84.332089, 39.548803],
          [-84.348199, 39.348512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39019",
      state: "39",
      county: "019",
      name: "Carroll County",
      censusArea: 394.609,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.861994, 40.599404],
          [-80.92092, 40.556315],
          [-80.941911, 40.4247],
          [-81.269951, 40.433486],
          [-81.264744, 40.565042],
          [-81.321659, 40.566648],
          [-81.317739, 40.651579],
          [-81.24125, 40.650534],
          [-81.237227, 40.723542],
          [-81.087289, 40.727816],
          [-80.917172, 40.726934],
          [-80.918125, 40.643464],
          [-80.861994, 40.599404]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42003",
      state: "42",
      county: "003",
      name: "Allegheny County",
      censusArea: 730.075,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.701624, 40.525449],
          [-79.701985, 40.523787],
          [-79.702131, 40.489635],
          [-79.703834, 40.443526],
          [-79.704739, 40.427231],
          [-79.722151, 40.409098],
          [-79.783684, 40.315913],
          [-79.787853, 40.297693],
          [-79.775139, 40.287888],
          [-79.781761, 40.227711],
          [-79.870585, 40.197415],
          [-79.881913, 40.196519],
          [-79.888986, 40.194823],
          [-79.893281, 40.194352],
          [-79.912586, 40.197767],
          [-79.927883, 40.206822],
          [-79.956073, 40.213582],
          [-79.968335, 40.22382],
          [-79.970652, 40.231814],
          [-79.964764, 40.23767],
          [-79.952098, 40.240877],
          [-80.033712, 40.288034],
          [-80.183129, 40.332515],
          [-80.360873, 40.477539],
          [-80.360782, 40.477604],
          [-80.228579, 40.573072],
          [-80.14485, 40.613474],
          [-80.148451, 40.67429],
          [-79.69293, 40.669744],
          [-79.692587, 40.669732],
          [-79.765415, 40.549854],
          [-79.701624, 40.525449]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42007",
      state: "42",
      county: "007",
      name: "Beaver County",
      censusArea: 434.712,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.519054, 40.517922],
          [-80.519057, 40.517922],
          [-80.519055, 40.590173],
          [-80.519086, 40.590161],
          [-80.519086, 40.616385],
          [-80.519039, 40.616391],
          [-80.518991, 40.638801],
          [-80.519058, 40.792298],
          [-80.518992, 40.801221],
          [-80.519081, 40.849157],
          [-80.51902, 40.850073],
          [-80.519039, 40.851339],
          [-80.158534, 40.855157],
          [-80.148787, 40.676432],
          [-80.148451, 40.67429],
          [-80.14485, 40.613474],
          [-80.228579, 40.573072],
          [-80.360782, 40.477604],
          [-80.360873, 40.477539],
          [-80.448114, 40.477717],
          [-80.518692, 40.477365],
          [-80.519054, 40.517922]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48361",
      state: "48",
      county: "361",
      name: "Orange County",
      censusArea: 333.669,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.901452, 30.242672],
          [-93.716223, 30.244318],
          [-93.712008, 30.194304],
          [-93.695252, 30.1476],
          [-93.695684, 30.135729],
          [-93.712101, 30.067346],
          [-93.721589, 30.051939],
          [-93.722481, 30.050898],
          [-93.752038, 30.016403],
          [-93.75663, 30.014163],
          [-93.789431, 29.987812],
          [-93.840799, 29.914423],
          [-93.860169, 29.977397],
          [-94.031976, 30.028013],
          [-94.115917, 30.160347],
          [-94.117604, 30.241665],
          [-93.901452, 30.242672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48369",
      state: "48",
      county: "369",
      name: "Parmer County",
      censusArea: 880.778,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.525184, 34.746931],
          [-102.525631, 34.313034],
          [-102.61515, 34.312891],
          [-103.043979, 34.312749],
          [-103.043979, 34.312764],
          [-103.043946, 34.379555],
          [-103.043944, 34.37966],
          [-103.043919, 34.380916],
          [-103.043693, 34.383578],
          [-103.04363, 34.38469],
          [-103.043614, 34.384969],
          [-103.043613, 34.388679],
          [-103.043613, 34.390442],
          [-103.043585, 34.393716],
          [-103.043611, 34.397105],
          [-103.043583, 34.400678],
          [-103.043538, 34.405463],
          [-103.043582, 34.455657],
          [-103.043588, 34.459662],
          [-103.043589, 34.459774],
          [-103.043594, 34.46266],
          [-103.043072, 34.619782],
          [-103.042827, 34.671188],
          [-103.042769, 34.747361],
          [-102.525184, 34.746931]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48383",
      state: "48",
      county: "383",
      name: "Reagan County",
      censusArea: 1175.301,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.26763, 31.556462],
          [-101.267947, 31.528688],
          [-101.274799, 31.079379],
          [-101.776191, 31.079784],
          [-101.775802, 31.651319],
          [-101.267122, 31.650854],
          [-101.26763, 31.556462]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48385",
      state: "48",
      county: "385",
      name: "Real County",
      censusArea: 699.195,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.757621, 30.074132],
          [-99.689879, 30.073948],
          [-99.691215, 29.908582],
          [-99.600862, 29.907662],
          [-99.602803, 29.899244],
          [-99.60313, 29.627181],
          [-100.014188, 29.623495],
          [-100.03088, 29.848273],
          [-99.967626, 30.082356],
          [-99.757621, 30.074132]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48387",
      state: "48",
      county: "387",
      name: "Red River County",
      censusArea: 1036.578,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.746096, 33.703016],
          [-94.746925, 33.328938],
          [-94.808783, 33.363636],
          [-95.125451, 33.389454],
          [-95.30664, 33.378027],
          [-95.307545, 33.380676],
          [-95.308637, 33.380662],
          [-95.310475, 33.871481],
          [-95.287565, 33.873617],
          [-95.260815, 33.887653],
          [-95.255541, 33.892047],
          [-95.249922, 33.902024],
          [-95.2482, 33.912327],
          [-95.249807, 33.922241],
          [-95.253623, 33.92971],
          [-95.252906, 33.933648],
          [-95.231113, 33.960361],
          [-95.226393, 33.961954],
          [-95.219358, 33.961567],
          [-95.155981, 33.944027],
          [-95.129572, 33.936718],
          [-95.067253, 33.917351],
          [-95.063459, 33.91404],
          [-95.062284, 33.903618],
          [-95.039452, 33.860621],
          [-94.8693, 33.745871],
          [-94.822473, 33.732716],
          [-94.760604, 33.727054],
          [-94.746096, 33.703016]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48395",
      state: "48",
      county: "395",
      name: "Robertson County",
      censusArea: 855.683,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.349364, 30.911423],
          [-96.45763, 30.744474],
          [-96.566983, 30.69567],
          [-96.568795, 30.701387],
          [-96.595776, 30.715072],
          [-96.599467, 30.728634],
          [-96.621281, 30.730819],
          [-96.661054, 30.829686],
          [-96.68497, 30.906191],
          [-96.75633, 30.927206],
          [-96.829121, 31.105871],
          [-96.596862, 31.2209],
          [-96.319165, 31.357129],
          [-96.33109, 31.25223],
          [-96.24102, 30.973737],
          [-96.263223, 30.964123],
          [-96.336193, 30.9217],
          [-96.349364, 30.911423]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48399",
      state: "48",
      county: "399",
      name: "Runnels County",
      censusArea: 1050.945,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.235761, 31.69297],
          [-100.235137, 32.082372],
          [-100.151911, 32.082638],
          [-99.713971, 32.082089],
          [-99.721698, 31.576759],
          [-100.111234, 31.580267],
          [-100.234636, 31.587147],
          [-100.235761, 31.69297]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48403",
      state: "48",
      county: "403",
      name: "Sabine County",
      censusArea: 491.39,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.039034, 31.134271],
          [-94.044154, 31.409979],
          [-93.983239, 31.466114],
          [-93.984461, 31.569419],
          [-93.834923, 31.58621],
          [-93.818582, 31.554826],
          [-93.798087, 31.534044],
          [-93.729613, 31.487922],
          [-93.695866, 31.409392],
          [-93.670182, 31.387184],
          [-93.640805, 31.372546],
          [-93.671676, 31.299586],
          [-93.644407, 31.27711],
          [-93.620829, 31.271299],
          [-93.614402, 31.260869],
          [-93.608158, 31.227835],
          [-93.607243, 31.204806],
          [-93.602315, 31.181742],
          [-93.599705, 31.176456],
          [-93.600308, 31.176158],
          [-93.911126, 31.158071],
          [-94.039034, 31.134271]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48415",
      state: "48",
      county: "415",
      name: "Scurry County",
      censusArea: 905.444,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.03866, 32.970225],
          [-100.65587, 32.963469],
          [-100.660626, 32.525312],
          [-101.174562, 32.527703],
          [-101.173378, 32.963597],
          [-101.03866, 32.970225]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48417",
      state: "48",
      county: "417",
      name: "Shackelford County",
      censusArea: 914.286,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.09605, 32.514768],
          [-99.114293, 32.514811],
          [-99.612026, 32.514654],
          [-99.612001, 32.956962],
          [-99.471258, 32.957025],
          [-99.096016, 32.957037],
          [-99.09605, 32.514768]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48419",
      state: "48",
      county: "419",
      name: "Shelby County",
      censusArea: 795.583,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.511431, 31.973984],
          [-94.01563, 31.979856],
          [-93.975377, 31.92366],
          [-93.932135, 31.893672],
          [-93.92029, 31.888651],
          [-93.889193, 31.856819],
          [-93.878225, 31.844276],
          [-93.840029, 31.800596],
          [-93.812477, 31.715246],
          [-93.817059, 31.671694],
          [-93.82629, 31.614903],
          [-93.834924, 31.586211],
          [-93.834923, 31.58621],
          [-93.984461, 31.569419],
          [-94.090717, 31.620609],
          [-94.399091, 31.653596],
          [-94.452416, 31.844281],
          [-94.511431, 31.973984]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48423",
      state: "48",
      county: "423",
      name: "Smith County",
      censusArea: 921.454,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.985272, 32.137989],
          [-95.459908, 32.135559],
          [-95.488733, 32.232486],
          [-95.449165, 32.355233],
          [-95.5943, 32.479858],
          [-95.594541, 32.687026],
          [-95.47297, 32.606426],
          [-95.332164, 32.604252],
          [-95.167523, 32.543957],
          [-95.15341, 32.570115],
          [-94.986935, 32.537246],
          [-94.986406, 32.371322],
          [-94.985411, 32.37082],
          [-94.985272, 32.137989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48427",
      state: "48",
      county: "427",
      name: "Starr County",
      censusArea: 1223.179,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.32067, 26.783081],
          [-98.478268, 26.469813],
          [-98.491943, 26.445362],
          [-98.493812, 26.441167],
          [-98.58672, 26.257456],
          [-98.623335, 26.259592],
          [-98.6329, 26.250348],
          [-98.705676, 26.27748],
          [-98.751164, 26.331307],
          [-98.807348, 26.369421],
          [-98.822373, 26.370902],
          [-98.861354, 26.36599],
          [-99.047575, 26.406922],
          [-99.105719, 26.43872],
          [-99.167996, 26.557799],
          [-99.172058, 26.57086],
          [-99.011112, 26.675023],
          [-98.95423, 26.785694],
          [-98.422616, 26.783535],
          [-98.32067, 26.783081]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48431",
      state: "48",
      county: "431",
      name: "Sterling County",
      censusArea: 923.451,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.821594, 32.08661],
          [-100.82537, 31.696155],
          [-100.861099, 31.705418],
          [-100.865256, 31.562294],
          [-101.26763, 31.556462],
          [-101.267122, 31.650854],
          [-101.264216, 32.087136],
          [-101.183997, 32.087208],
          [-100.821594, 32.08661]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26017",
      state: "26",
      county: "017",
      name: "Bay County",
      censusArea: 442.302,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.698816, 43.478957],
          [-83.817678, 43.479052],
          [-83.916091, 43.522629],
          [-84.050987, 43.524064],
          [-84.05, 43.567324],
          [-84.168127, 43.568899],
          [-84.167318, 43.825902],
          [-84.166377, 43.996901],
          [-84.045409, 43.996974],
          [-84.044843, 43.911146],
          [-83.911128, 43.91052],
          [-83.916815, 43.89905],
          [-83.917875, 43.856509],
          [-83.926345, 43.787398],
          [-83.929375, 43.777091],
          [-83.945426, 43.759946],
          [-83.954792, 43.760932],
          [-83.956021, 43.759286],
          [-83.954347, 43.750647],
          [-83.939297, 43.715369],
          [-83.929462, 43.701269],
          [-83.909479, 43.672622],
          [-83.897078, 43.664022],
          [-83.852076, 43.644922],
          [-83.814674, 43.643022],
          [-83.806774, 43.641221],
          [-83.778919, 43.630056],
          [-83.770693, 43.628691],
          [-83.769886, 43.634924],
          [-83.725793, 43.618691],
          [-83.703446, 43.597646],
          [-83.699253, 43.596792],
          [-83.699368, 43.595314],
          [-83.698816, 43.478957]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26019",
      state: "26",
      county: "019",
      name: "Benzie County",
      censusArea: 319.704,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.233503, 44.518278],
          [-86.223788, 44.549043],
          [-86.220697, 44.566742],
          [-86.22545, 44.59459],
          [-86.231828, 44.609107],
          [-86.25395, 44.64808],
          [-86.259029, 44.663654],
          [-86.256796, 44.686769],
          [-86.254996, 44.691935],
          [-86.248474, 44.699046],
          [-86.232482, 44.70605],
          [-86.172201, 44.720623],
          [-86.169323, 44.722448],
          [-86.160585, 44.727988],
          [-86.160268, 44.728189],
          [-86.122946, 44.727982],
          [-86.122466, 44.727979],
          [-86.121125, 44.727972],
          [-86.120759, 44.728048],
          [-86.108303, 44.730646],
          [-86.106182, 44.731088],
          [-86.105473, 44.731522],
          [-86.09074, 44.740544],
          [-86.089186, 44.741496],
          [-86.077933, 44.758234],
          [-86.077068, 44.760494],
          [-86.076811, 44.761167],
          [-86.076719, 44.761407],
          [-86.074658, 44.766792],
          [-86.073506, 44.769803],
          [-86.073073, 44.778393],
          [-85.81512, 44.774525],
          [-85.817802, 44.512969],
          [-86.233503, 44.518278]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26021",
      state: "26",
      county: "021",
      name: "Berrien County",
      censusArea: 567.747,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.581518, 41.943765],
          [-86.564722, 41.981427],
          [-86.564276, 41.982427],
          [-86.564077, 41.982874],
          [-86.563904, 41.983262],
          [-86.563598, 41.983948],
          [-86.561542, 41.988559],
          [-86.556537, 41.999782],
          [-86.556421, 42.000042],
          [-86.549328, 42.01092],
          [-86.549237, 42.011059],
          [-86.545288, 42.017115],
          [-86.544867, 42.017761],
          [-86.541718, 42.02259],
          [-86.541089, 42.023555],
          [-86.536063, 42.031262],
          [-86.533855, 42.034648],
          [-86.532934, 42.036061],
          [-86.53264, 42.036512],
          [-86.532161, 42.037246],
          [-86.508856, 42.072986],
          [-86.501322, 42.08454],
          [-86.490122, 42.105139],
          [-86.485223, 42.118239],
          [-86.479031, 42.123519],
          [-86.466576, 42.134138],
          [-86.466262, 42.134406],
          [-86.464913, 42.135752],
          [-86.464897, 42.135767],
          [-86.464356, 42.136308],
          [-86.463967, 42.136696],
          [-86.463685, 42.136977],
          [-86.440762, 42.159847],
          [-86.439416, 42.16119],
          [-86.437909, 42.162694],
          [-86.437148, 42.163453],
          [-86.404146, 42.196379],
          [-86.402183, 42.198542],
          [-86.398915, 42.202143],
          [-86.398536, 42.202561],
          [-86.397692, 42.203491],
          [-86.394997, 42.20646],
          [-86.394183, 42.207357],
          [-86.393565, 42.208039],
          [-86.393518, 42.20809],
          [-86.386591, 42.215723],
          [-86.385179, 42.217279],
          [-86.383665, 42.219207],
          [-86.383585, 42.21931],
          [-86.381434, 42.222048],
          [-86.380407, 42.223357],
          [-86.380035, 42.22383],
          [-86.36488, 42.243133],
          [-86.223563, 42.243379],
          [-86.22294, 42.071484],
          [-86.226097, 41.760016],
          [-86.265496, 41.760207],
          [-86.501773, 41.759553],
          [-86.519318, 41.759447],
          [-86.524223, 41.759456],
          [-86.640044, 41.759671],
          [-86.641186, 41.759633],
          [-86.746521, 41.759982],
          [-86.748096, 41.759967],
          [-86.800611, 41.760251],
          [-86.800707, 41.76024],
          [-86.801578, 41.76024],
          [-86.804427, 41.76024],
          [-86.823628, 41.76024],
          [-86.824828, 41.76024],
          [-86.82355, 41.760898],
          [-86.802065, 41.771956],
          [-86.801772, 41.772107],
          [-86.794712, 41.77574],
          [-86.794632, 41.775782],
          [-86.79351, 41.776359],
          [-86.777943, 41.784371],
          [-86.777227, 41.78474],
          [-86.77608, 41.785399],
          [-86.774798, 41.786137],
          [-86.751292, 41.799652],
          [-86.735207, 41.808901],
          [-86.717037, 41.819349],
          [-86.715578, 41.820334],
          [-86.697541, 41.832513],
          [-86.679672, 41.844579],
          [-86.679355, 41.844793],
          [-86.648971, 41.869659],
          [-86.6476, 41.870782],
          [-86.645296, 41.872668],
          [-86.644048, 41.873689],
          [-86.643965, 41.873757],
          [-86.629867, 41.885295],
          [-86.629344, 41.885723],
          [-86.623837, 41.89023],
          [-86.623774, 41.890282],
          [-86.621259, 41.89234],
          [-86.619442, 41.893827],
          [-86.616978, 41.896625],
          [-86.616886, 41.89673],
          [-86.616029, 41.897703],
          [-86.612251, 41.901993],
          [-86.61208, 41.902188],
          [-86.611511, 41.902833],
          [-86.610896, 41.903532],
          [-86.600524, 41.91531],
          [-86.597899, 41.918291],
          [-86.596802, 41.919964],
          [-86.588662, 41.93238],
          [-86.587827, 41.933653],
          [-86.587763, 41.933752],
          [-86.586721, 41.935341],
          [-86.585696, 41.936904],
          [-86.585264, 41.937562],
          [-86.582894, 41.941179],
          [-86.582197, 41.942241],
          [-86.581518, 41.943765]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26027",
      state: "26",
      county: "027",
      name: "Cass County",
      censusArea: 490.062,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.22294, 42.071484],
          [-85.762943, 42.069327],
          [-85.760186, 41.798814],
          [-85.791335, 41.759051],
          [-85.791363, 41.759051],
          [-85.872041, 41.759365],
          [-85.874997, 41.759341],
          [-85.888825, 41.759422],
          [-85.974901, 41.759849],
          [-85.97498, 41.759849],
          [-85.991302, 41.759949],
          [-86.003683, 41.760007],
          [-86.062572, 41.760283],
          [-86.12506, 41.760576],
          [-86.12546, 41.76056],
          [-86.127844, 41.760592],
          [-86.21759, 41.760016],
          [-86.22607, 41.760016],
          [-86.226097, 41.760016],
          [-86.22294, 42.071484]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26031",
      state: "26",
      county: "031",
      name: "Cheboygan County",
      censusArea: 715.264,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.732388, 45.787928],
          [-84.726192, 45.786905],
          [-84.718904, 45.777599],
          [-84.715996, 45.766174],
          [-84.681967, 45.756197],
          [-84.679546, 45.749095],
          [-84.644822, 45.73999],
          [-84.604712, 45.721668],
          [-84.573631, 45.710381],
          [-84.555496, 45.702268],
          [-84.553311, 45.698566],
          [-84.539167, 45.69048],
          [-84.539165, 45.690478],
          [-84.538998, 45.690383],
          [-84.46168, 45.652404],
          [-84.442348, 45.654771],
          [-84.435415, 45.664106],
          [-84.427495, 45.669201],
          [-84.413642, 45.669427],
          [-84.400283, 45.663345],
          [-84.376403, 45.655565],
          [-84.329537, 45.66438],
          [-84.289685, 45.653296],
          [-84.270238, 45.64479],
          [-84.215268, 45.634767],
          [-84.204218, 45.627116],
          [-84.25202, 45.625998],
          [-84.246634, 45.198718],
          [-84.366827, 45.198762],
          [-84.734303, 45.201936],
          [-84.734412, 45.202966],
          [-84.732687, 45.289382],
          [-84.732388, 45.787928]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26033",
      state: "26",
      county: "033",
      name: "Chippewa County",
      censusArea: 1558.42,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.108089, 46.241238],
          [-84.118175, 46.233968],
          [-84.125024, 46.232885],
          [-84.134652, 46.23214],
          [-84.14595, 46.224995],
          [-84.14715, 46.224184],
          [-84.14922, 46.223808],
          [-84.150725, 46.223808],
          [-84.151666, 46.224184],
          [-84.152042, 46.224937],
          [-84.15223, 46.226254],
          [-84.152499, 46.227875],
          [-84.159485, 46.233233],
          [-84.182732, 46.23545],
          [-84.219494, 46.231992],
          [-84.233117, 46.224037],
          [-84.249164, 46.206461],
          [-84.245233, 46.192571],
          [-84.247687, 46.17989],
          [-84.251424, 46.175888],
          [-84.221001, 46.163062],
          [-84.196669, 46.16615],
          [-84.177298, 46.183993],
          [-84.17164, 46.181731],
          [-84.125022, 46.180209],
          [-84.114941, 46.174114],
          [-84.113259, 46.16886],
          [-84.100126, 46.15077],
          [-84.095818, 46.147733],
          [-84.089309, 46.146432],
          [-84.060383, 46.146138],
          [-84.026536, 46.131648],
          [-84.031036, 46.123186],
          [-84.038696, 46.12562],
          [-84.0519, 46.11981],
          [-84.061329, 46.113482],
          [-84.069147, 46.103978],
          [-84.072398, 46.09669],
          [-84.071741, 46.092441],
          [-84.066257, 46.087438],
          [-84.051712, 46.079189],
          [-84.027861, 46.054784],
          [-84.006082, 46.044586],
          [-83.989526, 46.032823],
          [-83.963808, 46.027833],
          [-83.95141, 46.029042],
          [-83.943933, 46.031465],
          [-83.939012, 46.029226],
          [-83.93547, 46.020385],
          [-83.931175, 46.017871],
          [-83.908583, 46.011471],
          [-83.900535, 45.998918],
          [-83.873147, 45.993426],
          [-83.868233, 45.995075],
          [-83.845399, 46.025679],
          [-83.830146, 46.022324],
          [-83.818202, 46.002425],
          [-83.794055, 45.995801],
          [-83.776436, 46.004202],
          [-83.765277, 46.018363],
          [-83.765233, 46.031935],
          [-83.773785, 46.051471],
          [-83.796555, 46.056688],
          [-83.81252, 46.073469],
          [-83.824036, 46.103638],
          [-83.815826, 46.108529],
          [-83.81241, 46.108598],
          [-83.792867, 46.101971],
          [-83.779996, 46.093515],
          [-83.771821, 46.090999],
          [-83.755991, 46.092159],
          [-83.728165, 46.090957],
          [-83.7233, 46.09381],
          [-83.719791, 46.101031],
          [-83.703861, 46.103366],
          [-83.661161, 46.100258],
          [-83.63498, 46.103953],
          [-83.625554, 46.102212],
          [-83.615343, 46.095976],
          [-83.598612, 46.090085],
          [-83.581315, 46.089613],
          [-83.57609, 46.083513],
          [-83.57264, 46.074922],
          [-83.572574, 46.069895],
          [-83.565351, 46.061898],
          [-83.554059, 46.058882],
          [-83.547202, 46.047868],
          [-83.543366, 46.037196],
          [-83.540845, 46.021247],
          [-83.532913, 46.011328],
          [-83.49484, 45.999541],
          [-83.488348, 45.999543],
          [-83.480639, 45.996164],
          [-83.473946, 45.988558],
          [-83.473221, 45.984422],
          [-83.481765, 45.971873],
          [-83.488809, 45.96874],
          [-83.510623, 45.929324],
          [-83.517242, 45.923614],
          [-83.526347, 45.918636],
          [-83.561838, 45.912562],
          [-83.583052, 45.915919],
          [-83.632214, 45.932287],
          [-83.65766, 45.945463],
          [-83.687695, 45.935389],
          [-83.719433, 45.934078],
          [-83.732986, 45.937641],
          [-83.742778, 45.938002],
          [-83.766233, 45.935221],
          [-83.768854, 45.932069],
          [-83.78611, 45.933375],
          [-83.80104, 45.937582],
          [-83.803332, 45.943362],
          [-83.808147, 45.945693],
          [-83.82281, 45.943985],
          [-83.827566, 45.941236],
          [-83.835505, 45.941843],
          [-83.840869, 45.952726],
          [-83.846437, 45.953181],
          [-83.864859, 45.959465],
          [-83.879616, 45.966196],
          [-83.881055, 45.968185],
          [-83.910838, 45.965613],
          [-83.921257, 45.958075],
          [-83.952183, 45.965498],
          [-83.985141, 45.967133],
          [-83.996471, 45.961461],
          [-84.017565, 45.959046],
          [-84.080071, 45.970822],
          [-84.090391, 45.967256],
          [-84.10537, 45.972948],
          [-84.107204, 45.977161],
          [-84.111174, 45.978675],
          [-84.114284, 45.978322],
          [-84.239313, 45.98488],
          [-84.238836, 46.071457],
          [-84.36309, 46.071581],
          [-84.363503, 46.157984],
          [-85.112438, 46.158191],
          [-85.113126, 46.245667],
          [-85.237839, 46.245421],
          [-85.237873, 46.755703],
          [-85.173042, 46.763634],
          [-85.063556, 46.757856],
          [-85.036286, 46.760435],
          [-85.00924, 46.769224],
          [-84.989497, 46.772403],
          [-84.964652, 46.772845],
          [-84.954009, 46.771362],
          [-84.95158, 46.769488],
          [-84.987539, 46.745483],
          [-85.007616, 46.728339],
          [-85.020159, 46.712463],
          [-85.027513, 46.697451],
          [-85.030078, 46.684769],
          [-85.028291, 46.675125],
          [-85.035504, 46.625021],
          [-85.037056, 46.600995],
          [-85.035476, 46.581547],
          [-85.031507, 46.568703],
          [-85.029594, 46.554419],
          [-85.027374, 46.553756],
          [-85.025491, 46.546397],
          [-85.027083, 46.543038],
          [-85.045534, 46.537694],
          [-85.052954, 46.532827],
          [-85.056133, 46.52652],
          [-85.054943, 46.51475],
          [-85.049847, 46.503963],
          [-85.033766, 46.48767],
          [-85.025598, 46.483028],
          [-85.015211, 46.479712],
          [-84.969464, 46.47629],
          [-84.955307, 46.480269],
          [-84.947269, 46.487399],
          [-84.937145, 46.489252],
          [-84.934432, 46.480315],
          [-84.921931, 46.469962],
          [-84.915184, 46.467515],
          [-84.893423, 46.465406],
          [-84.87507, 46.466781],
          [-84.861448, 46.46993],
          [-84.849767, 46.460245],
          [-84.843907, 46.448661],
          [-84.829491, 46.444071],
          [-84.800101, 46.446219],
          [-84.769151, 46.453523],
          [-84.723338, 46.468266],
          [-84.689672, 46.483923],
          [-84.678423, 46.487694],
          [-84.65388, 46.48225],
          [-84.63102, 46.484868],
          [-84.616489, 46.47187],
          [-84.607945, 46.456747],
          [-84.584167, 46.43941],
          [-84.573522, 46.427895],
          [-84.551496, 46.418522],
          [-84.503719, 46.43919],
          [-84.493401, 46.440313],
          [-84.479513, 46.432573],
          [-84.471848, 46.434289],
          [-84.462597, 46.44094],
          [-84.455527, 46.453897],
          [-84.455256, 46.462785],
          [-84.463322, 46.467435],
          [-84.445149, 46.489016],
          [-84.420274, 46.501077],
          [-84.394725, 46.499242],
          [-84.37504, 46.508669],
          [-84.325371, 46.500021],
          [-84.264266, 46.495055],
          [-84.226131, 46.53392],
          [-84.193729, 46.53992],
          [-84.139426, 46.532219],
          [-84.128925, 46.530119],
          [-84.117925, 46.517619],
          [-84.111225, 46.504119],
          [-84.125026, 46.470143],
          [-84.146172, 46.41852],
          [-84.138906, 46.372221],
          [-84.115563, 46.268225],
          [-84.108089, 46.241238]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30109",
      state: "30",
      county: "109",
      name: "Wibaux County",
      censusArea: 889.265,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.045271, 46.641449],
          [-104.045271, 46.641443],
          [-104.353874, 46.641446],
          [-104.417782, 46.684892],
          [-104.607017, 46.684926],
          [-104.603766, 46.86088],
          [-104.496529, 46.86091],
          [-104.369228, 46.947439],
          [-104.305564, 47.042311],
          [-104.341603, 47.245598],
          [-104.451518, 47.318417],
          [-104.420263, 47.354596],
          [-104.131525, 47.354148],
          [-104.045069, 47.397461],
          [-104.045333, 47.343452],
          [-104.045313, 47.331955],
          [-104.045307, 47.330128],
          [-104.045121, 47.276969],
          [-104.045155, 47.27393],
          [-104.045088, 47.271406],
          [-104.045057, 47.266868],
          [-104.045091, 47.265953],
          [-104.045159, 47.263874],
          [-104.045081, 47.092813],
          [-104.045018, 47.081202],
          [-104.045354, 47.078574],
          [-104.045259, 47.063901],
          [-104.045227, 47.057502],
          [-104.045195, 47.053639],
          [-104.045052, 47.040863],
          [-104.045076, 47.037589],
          [-104.045566, 46.941231],
          [-104.045535, 46.934009],
          [-104.045542, 46.933887],
          [-104.045402, 46.725423],
          [-104.045403, 46.722177],
          [-104.04537, 46.721332],
          [-104.045572, 46.713881],
          [-104.045474, 46.708738],
          [-104.045271, 46.641449]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31007",
      state: "31",
      county: "007",
      name: "Banner County",
      censusArea: 746.114,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.05254, 41.564274],
          [-104.052859, 41.592254],
          [-104.052735, 41.613676],
          [-104.052975, 41.622931],
          [-104.052945, 41.638167],
          [-104.052913, 41.64519],
          [-104.05283, 41.697954],
          [-103.370391, 41.69921],
          [-103.369024, 41.437655],
          [-103.370437, 41.394514],
          [-103.37762, 41.394633],
          [-104.052287, 41.393214],
          [-104.052287, 41.393307],
          [-104.05216, 41.407662],
          [-104.05234, 41.417865],
          [-104.052478, 41.515754],
          [-104.052476, 41.522343],
          [-104.052686, 41.539111],
          [-104.052692, 41.541154],
          [-104.052584, 41.55265],
          [-104.052531, 41.552723],
          [-104.05254, 41.564274]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31011",
      state: "31",
      county: "011",
      name: "Boone County",
      censusArea: 686.554,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.833288, 41.916286],
          [-97.83239, 41.742788],
          [-97.830493, 41.742238],
          [-97.829846, 41.526174],
          [-98.041328, 41.510534],
          [-98.099523, 41.480777],
          [-98.291344, 41.480727],
          [-98.290943, 41.74121],
          [-98.29576, 41.741178],
          [-98.295602, 41.914954],
          [-97.833288, 41.916286]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31013",
      state: "31",
      county: "013",
      name: "Box Butte County",
      censusArea: 1075.293,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.444532, 42.437354],
          [-102.778666, 42.439745],
          [-102.773366, 42.439922],
          [-102.749431, 42.439934],
          [-102.742269, 42.092388],
          [-102.697835, 42.004843],
          [-103.363337, 42.00293],
          [-103.390382, 42.003365],
          [-103.401639, 42.00354],
          [-103.444108, 42.090708],
          [-103.444532, 42.437354]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31019",
      state: "31",
      county: "019",
      name: "Buffalo County",
      censusArea: 968.112,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.721975, 41.046674],
          [-98.72143, 40.698903],
          [-98.724412, 40.698891],
          [-98.724387, 40.689772],
          [-98.906096, 40.652722],
          [-99.179248, 40.660541],
          [-99.417316, 40.670825],
          [-99.425986, 41.046471],
          [-99.207564, 41.047003],
          [-98.748526, 41.04664],
          [-98.740957, 41.046651],
          [-98.74076, 41.046702],
          [-98.721975, 41.046674]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31025",
      state: "31",
      county: "025",
      name: "Cass County",
      censusArea: 557.446,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.92793, 41.0588],
          [-95.889215, 41.058443],
          [-95.866289, 41.051731],
          [-95.867246, 41.043671],
          [-95.867286, 41.001599],
          [-95.835434, 40.984184],
          [-95.829074, 40.975688],
          [-95.823123, 40.900924],
          [-95.821193, 40.876682],
          [-95.854172, 40.784012],
          [-96.463764, 40.78396],
          [-96.463861, 41.016069],
          [-96.319191, 41.044998],
          [-96.207648, 40.995628],
          [-96.056256, 41.065356],
          [-95.92793, 41.0588]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31029",
      state: "31",
      county: "029",
      name: "Chase County",
      censusArea: 894.419,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.051669, 40.349213],
          [-102.051572, 40.39308],
          [-102.05184, 40.396396],
          [-102.051465, 40.440008],
          [-102.051519, 40.520094],
          [-102.051725, 40.537839],
          [-102.051398, 40.697542],
          [-101.345365, 40.698093],
          [-101.342788, 40.350444],
          [-102.051669, 40.349213]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31033",
      state: "31",
      county: "033",
      name: "Cheyenne County",
      censusArea: 1196.287,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.382493, 41.001883],
          [-103.37762, 41.394633],
          [-103.370437, 41.394514],
          [-103.369024, 41.437655],
          [-102.636695, 41.439053],
          [-102.609386, 41.395115],
          [-102.610413, 41.221614],
          [-102.621033, 41.002597],
          [-102.653463, 41.002332],
          [-102.739624, 41.00223],
          [-102.754617, 41.002361],
          [-102.766723, 41.002275],
          [-102.773546, 41.002414],
          [-102.82728, 41.002143],
          [-102.830303, 41.002351],
          [-102.846455, 41.002256],
          [-102.849263, 41.002301],
          [-102.865784, 41.001988],
          [-102.867822, 41.002183],
          [-102.885746, 41.002131],
          [-102.887407, 41.002178],
          [-102.904796, 41.002207],
          [-102.906547, 41.002276],
          [-102.924029, 41.002142],
          [-102.925568, 41.00228],
          [-102.943109, 41.002051],
          [-102.94483, 41.002303],
          [-102.959624, 41.002095],
          [-102.960706, 41.002059],
          [-102.962522, 41.002072],
          [-102.963669, 41.002186],
          [-102.981483, 41.002112],
          [-102.98269, 41.002157],
          [-103.000102, 41.0024],
          [-103.002026, 41.002486],
          [-103.038704, 41.002251],
          [-103.043444, 41.002344],
          [-103.057998, 41.002368],
          [-103.059538, 41.002368],
          [-103.076536, 41.002253],
          [-103.077804, 41.002298],
          [-103.362979, 41.001844],
          [-103.365314, 41.001846],
          [-103.382493, 41.001883]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31039",
      state: "31",
      county: "039",
      name: "Cuming County",
      censusArea: 570.619,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.555172, 41.742018],
          [-96.905922, 41.742763],
          [-97.019911, 41.74298],
          [-97.019359, 42.090577],
          [-96.82367, 42.090411],
          [-96.555511, 42.089957],
          [-96.554866, 42.015875],
          [-96.555172, 41.742018]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31045",
      state: "31",
      county: "045",
      name: "Dawes County",
      censusArea: 1396.464,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.000897, 43.000474],
          [-102.792111, 42.99998],
          [-102.773369, 42.785265],
          [-102.773366, 42.439922],
          [-102.778666, 42.439745],
          [-103.444532, 42.437354],
          [-103.480082, 42.437267],
          [-103.480403, 42.784966],
          [-103.503232, 42.784787],
          [-103.5051, 43.00077],
          [-103.404579, 43.000737],
          [-103.340829, 43.000879],
          [-103.132955, 43.000784],
          [-103.13174, 43.000783],
          [-103.000897, 43.000474]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31051",
      state: "31",
      county: "051",
      name: "Dixon County",
      censusArea: 476.232,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.015691, 42.758697],
          [-96.961021, 42.738075],
          [-96.920494, 42.731432],
          [-96.906797, 42.7338],
          [-96.813148, 42.706397],
          [-96.806553, 42.703405],
          [-96.724033, 42.665971],
          [-96.712871, 42.648136],
          [-96.707245, 42.631258],
          [-96.710604, 42.619625],
          [-96.711332, 42.610399],
          [-96.708049, 42.601594],
          [-96.635982, 42.524324],
          [-96.722231, 42.524652],
          [-96.726661, 42.278005],
          [-96.726188, 42.264104],
          [-96.822497, 42.264292],
          [-97.01773, 42.264453],
          [-97.017789, 42.351347],
          [-97.015116, 42.698797],
          [-97.015691, 42.758697]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31057",
      state: "31",
      county: "057",
      name: "Dundy County",
      censusArea: 919.678,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.051669, 40.349213],
          [-101.342788, 40.350444],
          [-101.323353, 40.350553],
          [-101.324036, 40.002704],
          [-101.325514, 40.002687],
          [-101.342859, 40.00258],
          [-101.374326, 40.002521],
          [-101.409953, 40.002354],
          [-101.411043, 40.002365],
          [-101.417209, 40.002424],
          [-101.542273, 40.002609],
          [-101.625809, 40.002711],
          [-101.627071, 40.00262],
          [-101.804862, 40.002752],
          [-101.807687, 40.002798],
          [-101.832161, 40.002933],
          [-101.841025, 40.002784],
          [-101.904176, 40.003162],
          [-101.916696, 40.003142],
          [-102.051744, 40.003078],
          [-102.052001, 40.148359],
          [-102.051909, 40.162674],
          [-102.051894, 40.229193],
          [-102.051922, 40.235344],
          [-102.051669, 40.349213]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31071",
      state: "31",
      county: "071",
      name: "Garfield County",
      censusArea: 569.79,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.22183, 41.740792],
          [-99.222733, 42.087916],
          [-98.761155, 42.088518],
          [-98.759133, 41.740382],
          [-99.212016, 41.740613],
          [-99.212773, 41.740644],
          [-99.22183, 41.740792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31075",
      state: "31",
      county: "075",
      name: "Grant County",
      censusArea: 776.224,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.985523, 41.74229],
          [-102.067918, 41.742099],
          [-102.06665, 42.009195],
          [-102.008562, 42.008901],
          [-102.006204, 42.096338],
          [-101.426557, 42.092194],
          [-101.42592, 41.743059],
          [-101.985523, 41.74229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39023",
      state: "39",
      county: "023",
      name: "Clark County",
      censusArea: 397.473,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.051039, 39.879807],
          [-84.036069, 40.040182],
          [-83.516155, 40.010188],
          [-83.588041, 39.768778],
          [-83.647169, 39.773029],
          [-83.829495, 39.797851],
          [-83.827425, 39.822651],
          [-84.053736, 39.850458],
          [-84.053341, 39.855117],
          [-84.053136, 39.856158],
          [-84.052136, 39.866259],
          [-84.051636, 39.877665],
          [-84.051039, 39.879807]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39025",
      state: "39",
      county: "025",
      name: "Clermont County",
      censusArea: 452.1,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.259431, 39.270796],
          [-84.006782, 39.255068],
          [-83.99312, 39.254235],
          [-83.995284, 39.228189],
          [-84.052707, 38.770542],
          [-84.071491, 38.770475],
          [-84.205592, 38.802588],
          [-84.212904, 38.805707],
          [-84.2253, 38.817665],
          [-84.229954, 38.82765],
          [-84.231306, 38.830552],
          [-84.233727, 38.853576],
          [-84.232478, 38.874638],
          [-84.232132, 38.880483],
          [-84.234453, 38.893226],
          [-84.304698, 39.006455],
          [-84.31368, 39.016981],
          [-84.319936, 39.022081],
          [-84.316698, 39.226944],
          [-84.259431, 39.270796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39029",
      state: "39",
      county: "029",
      name: "Columbiana County",
      censusArea: 531.893,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.519764, 40.899858],
          [-80.519039, 40.851339],
          [-80.51902, 40.850073],
          [-80.519081, 40.849157],
          [-80.518992, 40.801221],
          [-80.519058, 40.792298],
          [-80.518991, 40.638801],
          [-80.56072, 40.62368],
          [-80.589041, 40.620128],
          [-80.594065, 40.623664],
          [-80.601494, 40.625387],
          [-80.627171, 40.619936],
          [-80.634355, 40.616095],
          [-80.665892, 40.587728],
          [-80.667957, 40.582496],
          [-80.861994, 40.599404],
          [-80.918125, 40.643464],
          [-80.917172, 40.726934],
          [-81.087289, 40.727816],
          [-81.086679, 40.901609],
          [-80.519764, 40.899858]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39033",
      state: "39",
      county: "033",
      name: "Crawford County",
      censusArea: 401.786,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.11136, 40.702915],
          [-83.112737, 40.993447],
          [-82.829512, 40.996624],
          [-82.724792, 40.995638],
          [-82.727164, 40.711203],
          [-82.858302, 40.705019],
          [-83.11136, 40.702915]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39037",
      state: "39",
      county: "037",
      name: "Darke County",
      censusArea: 598.1,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.803918, 40.310094],
          [-84.803917, 40.310115],
          [-84.804119, 40.352757],
          [-84.434631, 40.354259],
          [-84.432575, 40.197037],
          [-84.425902, 39.919622],
          [-84.446101, 39.919266],
          [-84.446511, 39.918874],
          [-84.448023, 39.91923],
          [-84.485367, 39.918491],
          [-84.812411, 39.916915],
          [-84.812411, 39.916916],
          [-84.812357, 39.921764],
          [-84.812193, 39.92734],
          [-84.81067, 40.005074],
          [-84.810099, 40.034214],
          [-84.809737, 40.048929],
          [-84.808706, 40.107216],
          [-84.808305, 40.127018],
          [-84.808291, 40.129027],
          [-84.806766, 40.180128],
          [-84.806347, 40.192252],
          [-84.80634, 40.192327],
          [-84.806175, 40.197995],
          [-84.805627, 40.223659],
          [-84.804098, 40.302498],
          [-84.803918, 40.310094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39041",
      state: "39",
      county: "041",
      name: "Delaware County",
      censusArea: 443.098,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.749896, 40.288636],
          [-82.750235, 40.284087],
          [-82.750747, 40.276996],
          [-82.761827, 40.125855],
          [-83.063981, 40.138289],
          [-83.149055, 40.141563],
          [-83.169981, 40.143087],
          [-83.17233, 40.244257],
          [-83.248428, 40.244466],
          [-83.248595, 40.443989],
          [-83.020798, 40.433795],
          [-82.924781, 40.415004],
          [-82.929588, 40.358123],
          [-82.744931, 40.349603],
          [-82.749896, 40.288636]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39043",
      state: "39",
      county: "043",
      name: "Erie County",
      censusArea: 251.558,
      stateName: "Ohio"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-82.700208, 41.61219],
            [-82.691123, 41.611331],
            [-82.68015, 41.61897],
            [-82.677772, 41.617986],
            [-82.680669, 41.594611],
            [-82.686033, 41.587246],
            [-82.688744, 41.585896],
            [-82.702027, 41.585437],
            [-82.725827, 41.595199],
            [-82.735766, 41.600982],
            [-82.735707, 41.603361],
            [-82.718802, 41.619629],
            [-82.70731, 41.619609],
            [-82.703438, 41.617734],
            [-82.700208, 41.61219]
          ]
        ],
        [
          [
            [-82.952224, 41.458239],
            [-82.920239, 41.458443],
            [-82.90665, 41.460215],
            [-82.856104, 41.471923],
            [-82.828317, 41.481802],
            [-82.694722, 41.493945],
            [-82.687921, 41.492324],
            [-82.658302, 41.461878],
            [-82.617745, 41.431833],
            [-82.616952, 41.428425],
            [-82.55808, 41.400005],
            [-82.513827, 41.384257],
            [-82.499099, 41.381541],
            [-82.481214, 41.381342],
            [-82.460599, 41.386316],
            [-82.431315, 41.396866],
            [-82.398086, 41.413945],
            [-82.361784, 41.426644],
            [-82.348076, 41.428431],
            [-82.342314, 41.283554],
            [-82.841475, 41.290023],
            [-82.847713, 41.430326],
            [-82.952224, 41.458239]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39049",
      state: "39",
      county: "049",
      name: "Franklin County",
      censusArea: 532.188,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.811458, 39.932374],
          [-82.824254, 39.794996],
          [-83.013071, 39.804394],
          [-83.243702, 39.812503],
          [-83.25383, 40.049218],
          [-83.206273, 40.107733],
          [-83.170208, 40.106927],
          [-83.169981, 40.143087],
          [-83.149055, 40.141563],
          [-83.063981, 40.138289],
          [-82.761827, 40.125855],
          [-82.782495, 39.939662],
          [-82.810797, 39.941162],
          [-82.811458, 39.932374]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35019",
      state: "35",
      county: "019",
      name: "Guadalupe County",
      censusArea: 3030.482,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.312968, 34.34672],
          [-105.312626, 34.607052],
          [-105.290299, 34.606744],
          [-105.290884, 35.042093],
          [-105.291355, 35.216345],
          [-104.124816, 35.2156],
          [-104.124841, 35.142183],
          [-104.129424, 34.779324],
          [-104.340132, 34.779062],
          [-104.444913, 34.604821],
          [-104.892023, 34.604426],
          [-104.89185, 34.346989],
          [-105.312968, 34.34672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35021",
      state: "35",
      county: "021",
      name: "Harding County",
      censusArea: 2125.442,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.374988, 35.740011],
          [-103.379941, 35.397987],
          [-103.637147, 35.389722],
          [-103.724585, 35.422223],
          [-103.976867, 35.801694],
          [-104.021315, 35.78148],
          [-104.367447, 35.781428],
          [-104.389736, 35.857604],
          [-104.327381, 35.96055],
          [-104.436121, 36.218761],
          [-104.008927, 36.218018],
          [-104.008991, 36.174503],
          [-103.793986, 36.173995],
          [-103.794342, 36.085787],
          [-103.364975, 36.086373],
          [-103.374988, 35.740011]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35025",
      state: "35",
      county: "025",
      name: "Lea County",
      censusArea: 4390.932,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.056655, 33.388416],
          [-103.057487, 33.329477],
          [-103.057856, 33.315234],
          [-103.060103, 33.219225],
          [-103.063905, 33.042055],
          [-103.06398, 33.038693],
          [-103.064452, 33.01029],
          [-103.064625, 32.999899],
          [-103.064679, 32.964373],
          [-103.064657, 32.959097],
          [-103.064569, 32.900014],
          [-103.064701, 32.879355],
          [-103.064862, 32.868346],
          [-103.064807, 32.857696],
          [-103.064916, 32.85726],
          [-103.064889, 32.849359],
          [-103.064672, 32.82847],
          [-103.064699, 32.827531],
          [-103.064711, 32.784593],
          [-103.064698, 32.783602],
          [-103.064807, 32.777303],
          [-103.064827, 32.726628],
          [-103.064799, 32.708694],
          [-103.064798, 32.690761],
          [-103.064864, 32.682647],
          [-103.064633, 32.64642],
          [-103.064815, 32.624537],
          [-103.064761, 32.601863],
          [-103.064788, 32.600397],
          [-103.064761, 32.587983],
          [-103.064696, 32.522193],
          [-103.064422, 32.145006],
          [-103.064348, 32.123041],
          [-103.064344, 32.087051],
          [-103.064423, 32.000518],
          [-103.085876, 32.000465],
          [-103.088698, 32.000453],
          [-103.215641, 32.000513],
          [-103.267633, 32.000475],
          [-103.267708, 32.000324],
          [-103.270383, 32.000326],
          [-103.278521, 32.000419],
          [-103.326501, 32.00037],
          [-103.722853, 32.000221],
          [-103.722938, 32.522587],
          [-103.813959, 32.522464],
          [-103.8146, 32.965203],
          [-103.766341, 32.965232],
          [-103.766433, 33.395491],
          [-103.72138, 33.395905],
          [-103.720742, 33.569678],
          [-103.510267, 33.570256],
          [-103.05261, 33.570599],
          [-103.056655, 33.388438],
          [-103.056655, 33.388416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35033",
      state: "35",
      county: "033",
      name: "Mora County",
      censusArea: 1931.274,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.53038, 36.013014],
          [-105.419992, 36.131704],
          [-105.409677, 36.236814],
          [-105.340907, 36.262245],
          [-104.865185, 36.261144],
          [-104.865545, 36.217997],
          [-104.436121, 36.218761],
          [-104.327381, 35.96055],
          [-104.389736, 35.857604],
          [-104.367447, 35.781428],
          [-105.018811, 35.769439],
          [-105.025454, 35.754197],
          [-105.110049, 35.807904],
          [-105.34783, 35.870695],
          [-105.72028, 35.871015],
          [-105.72054, 35.875028],
          [-105.717374, 35.97876],
          [-105.572388, 35.972456],
          [-105.53038, 36.013014]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35039",
      state: "35",
      county: "039",
      name: "Rio Arriba County",
      censusArea: 5860.837,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.62425, 36.219799],
          [-107.618348, 36.804739],
          [-107.605517, 36.798039],
          [-107.451445, 36.904036],
          [-107.420915, 37.000005],
          [-107.420913, 37.000005],
          [-107.000592, 37.000009],
          [-106.869796, 36.992426],
          [-106.675626, 36.993123],
          [-106.661344, 36.993243],
          [-106.628733, 36.993161],
          [-106.628652, 36.993175],
          [-106.617125, 36.993004],
          [-106.617159, 36.992967],
          [-106.476228, 36.993472],
          [-106.248675, 36.994288],
          [-106.247705, 36.994266],
          [-106.006634, 36.995343],
          [-105.971889, 36.886247],
          [-105.984436, 36.812065],
          [-105.972491, 36.648088],
          [-105.938754, 36.462402],
          [-105.959812, 36.35442],
          [-106.058364, 36.296978],
          [-105.856947, 36.230434],
          [-105.735883, 36.100133],
          [-105.53038, 36.013014],
          [-105.572388, 35.972456],
          [-105.717374, 35.97876],
          [-105.71675, 36.002356],
          [-106.071394, 36.002028],
          [-106.054128, 35.930783],
          [-106.245439, 35.93093],
          [-106.245466, 35.959346],
          [-106.248096, 35.959386],
          [-106.247949, 35.966335],
          [-106.247241, 36.001517],
          [-106.885269, 35.998967],
          [-106.884989, 36.21657],
          [-107.62425, 36.219799]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47143",
      state: "47",
      county: "143",
      name: "Rhea County",
      censusArea: 315.377,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.723853, 35.752896],
          [-84.781804, 35.673908],
          [-84.794904, 35.55255],
          [-84.873969, 35.550217],
          [-84.907048, 35.447871],
          [-84.968953, 35.466674],
          [-85.016343, 35.409288],
          [-85.135191, 35.458649],
          [-85.107762, 35.571359],
          [-84.916062, 35.761939],
          [-84.781901, 35.825],
          [-84.723853, 35.752896]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47147",
      state: "47",
      county: "147",
      name: "Robertson County",
      censusArea: 476.287,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.913233, 36.382621],
          [-86.988801, 36.37065],
          [-87.120443, 36.45546],
          [-87.114983, 36.642414],
          [-87.114976, 36.642414],
          [-87.060843, 36.643412],
          [-86.906583, 36.646255],
          [-86.906023, 36.646302],
          [-86.854268, 36.646884],
          [-86.833155, 36.64721],
          [-86.818405, 36.647639],
          [-86.816186, 36.647722],
          [-86.813037, 36.647647],
          [-86.763295, 36.648907],
          [-86.75892, 36.649018],
          [-86.606394, 36.652107],
          [-86.605042, 36.652125],
          [-86.564252, 36.633522],
          [-86.69875, 36.4074],
          [-86.754795, 36.405496],
          [-86.824784, 36.349872],
          [-86.913233, 36.382621]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47149",
      state: "47",
      county: "149",
      name: "Rutherford County",
      censusArea: 619.364,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.417189, 36.041517],
          [-86.310363, 35.978563],
          [-86.153214, 35.954399],
          [-86.207147, 35.70379],
          [-86.245165, 35.631913],
          [-86.34194, 35.664259],
          [-86.403312, 35.618874],
          [-86.543116, 35.697617],
          [-86.639436, 35.6859],
          [-86.641978, 35.689037],
          [-86.674255, 35.696962],
          [-86.673683, 35.703306],
          [-86.682628, 35.704097],
          [-86.686193, 35.710051],
          [-86.613328, 35.790331],
          [-86.618892, 35.968224],
          [-86.515589, 36.10061],
          [-86.417189, 36.041517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47155",
      state: "47",
      county: "155",
      name: "Sevier County",
      censusArea: 592.5,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.662957, 35.569138],
          [-83.662814, 35.690618],
          [-83.77988, 35.860184],
          [-83.784436, 35.868744],
          [-83.784186, 35.879205],
          [-83.794275, 35.887125],
          [-83.654605, 35.971937],
          [-83.673297, 36.038486],
          [-83.523113, 35.963659],
          [-83.310782, 35.895846],
          [-83.255489, 35.714974],
          [-83.250695, 35.709349],
          [-83.254231, 35.695807],
          [-83.297154, 35.65775],
          [-83.366941, 35.638728],
          [-83.445802, 35.611803],
          [-83.479082, 35.583316],
          [-83.478523, 35.579202],
          [-83.480617, 35.576633],
          [-83.485527, 35.568204],
          [-83.498335, 35.562981],
          [-83.640498, 35.566075],
          [-83.662957, 35.569138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47161",
      state: "47",
      county: "161",
      name: "Stewart County",
      censusArea: 459.33,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.602597, 36.418979],
          [-87.592358, 36.367663],
          [-87.715494, 36.36824],
          [-87.821816, 36.328724],
          [-87.98074, 36.353307],
          [-87.982743, 36.356083],
          [-87.988562, 36.359746],
          [-87.99092, 36.360133],
          [-88.05848, 36.439224],
          [-88.053292, 36.49713],
          [-88.053205, 36.497129],
          [-88.039481, 36.510408],
          [-88.037822, 36.51385],
          [-88.032489, 36.540662],
          [-88.035625, 36.561736],
          [-88.045127, 36.602939],
          [-88.055604, 36.63571],
          [-88.068208, 36.659747],
          [-88.070532, 36.678118],
          [-88.011792, 36.677025],
          [-87.849567, 36.663701],
          [-87.853204, 36.633247],
          [-87.694186, 36.636838],
          [-87.64115, 36.638036],
          [-87.641146, 36.638036],
          [-87.60342, 36.4259],
          [-87.602597, 36.418979]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47167",
      state: "47",
      county: "167",
      name: "Tipton County",
      censusArea: 458.366,
      stateName: "Tennessee"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-89.957347, 35.528683],
            [-90.054322, 35.389277],
            [-90.061788, 35.386184],
            [-90.074992, 35.384152],
            [-90.089612, 35.379842],
            [-90.093589, 35.393333],
            [-90.13551, 35.376668],
            [-90.143633, 35.374745],
            [-90.166246, 35.374745],
            [-90.178341, 35.382092],
            [-90.179265, 35.385194],
            [-90.169002, 35.421853],
            [-90.152386, 35.436789],
            [-90.107723, 35.476935],
            [-90.07442, 35.472518],
            [-90.072154, 35.470752],
            [-90.067798, 35.466224],
            [-90.059068, 35.457889],
            [-90.04768, 35.459255],
            [-90.034014, 35.468821],
            [-90.034976, 35.480705],
            [-90.039744, 35.548041],
            [-90.032938, 35.55344],
            [-90.02862, 35.555249],
            [-90.017312, 35.555996],
            [-89.957347, 35.528683]
          ]
        ],
        [
          [
            [-89.632776, 35.375824],
            [-89.70248, 35.408584],
            [-89.889317, 35.390906],
            [-90.041563, 35.39662],
            [-89.933572, 35.533299],
            [-89.909797, 35.537914],
            [-89.758116, 35.650361],
            [-89.651056, 35.648009],
            [-89.502118, 35.580621],
            [-89.469862, 35.546566],
            [-89.474171, 35.403039],
            [-89.632776, 35.375824]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47175",
      state: "47",
      county: "175",
      name: "Van Buren County",
      censusArea: 273.415,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.423804, 35.567422],
          [-85.557535, 35.532977],
          [-85.594632, 35.617898],
          [-85.602876, 35.794996],
          [-85.474213, 35.822656],
          [-85.269359, 35.793388],
          [-85.273288, 35.787948],
          [-85.26038, 35.78258],
          [-85.247784, 35.78666],
          [-85.254062, 35.765611],
          [-85.265387, 35.721237],
          [-85.423804, 35.567422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47177",
      state: "47",
      county: "177",
      name: "Warren County",
      censusArea: 432.68,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.557535, 35.532977],
          [-85.607326, 35.531651],
          [-85.876962, 35.524095],
          [-85.985062, 35.660108],
          [-85.984891, 35.734553],
          [-85.885156, 35.839658],
          [-85.682095, 35.831254],
          [-85.682602, 35.827246],
          [-85.671427, 35.820708],
          [-85.653072, 35.816431],
          [-85.641655, 35.816717],
          [-85.634845, 35.808616],
          [-85.622195, 35.80181],
          [-85.607594, 35.803246],
          [-85.602876, 35.794996],
          [-85.594632, 35.617898],
          [-85.557535, 35.532977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45055",
      state: "45",
      county: "055",
      name: "Kershaw County",
      censusArea: 726.562,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.479857, 34.168696],
          [-80.578081, 34.101549],
          [-80.604403, 34.101671],
          [-80.618542, 34.109176],
          [-80.616186, 34.099605],
          [-80.635594, 34.097666],
          [-80.639534, 34.095633],
          [-80.646696, 34.09399],
          [-80.658325, 34.088774],
          [-80.658978, 34.085743],
          [-80.67297, 34.083732],
          [-80.676421, 34.08461],
          [-80.683662, 34.081603],
          [-80.692965, 34.08128],
          [-80.719318, 34.068972],
          [-80.869733, 34.182389],
          [-80.825921, 34.26876],
          [-80.769926, 34.377111],
          [-80.879227, 34.457776],
          [-80.743345, 34.539916],
          [-80.739966, 34.479957],
          [-80.523945, 34.509752],
          [-80.550552, 34.560116],
          [-80.408373, 34.614765],
          [-80.328553, 34.501349],
          [-80.288596, 34.366207],
          [-80.28688, 34.365305],
          [-80.286936, 34.364572],
          [-80.288692, 34.364139],
          [-80.421654, 34.294301],
          [-80.479857, 34.168696]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45061",
      state: "45",
      county: "061",
      name: "Lee County",
      censusArea: 410.184,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.26906, 34.0351],
          [-80.278461, 34.036722],
          [-80.278827, 34.036706],
          [-80.279339, 34.036829],
          [-80.321721, 34.080356],
          [-80.481318, 34.112622],
          [-80.479857, 34.168696],
          [-80.421654, 34.294301],
          [-80.288692, 34.364139],
          [-80.177225, 34.366203],
          [-80.112727, 34.197018],
          [-80.131441, 34.128666],
          [-80.072808, 34.086013],
          [-80.000594, 34.047877],
          [-80.180371, 33.952002],
          [-80.26906, 34.0351]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45065",
      state: "45",
      county: "065",
      name: "Mccormick County",
      censusArea: 359.13,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.046974, 33.95321],
          [-82.1557, 33.92929],
          [-82.113728, 33.82988],
          [-82.172379, 33.765711],
          [-82.166027, 33.690316],
          [-82.095527, 33.633869],
          [-82.11663, 33.589723],
          [-82.124841, 33.591274],
          [-82.12908, 33.589925],
          [-82.135046, 33.591044],
          [-82.179854, 33.615945],
          [-82.196583, 33.630582],
          [-82.218649, 33.686299],
          [-82.247472, 33.752591],
          [-82.301457, 33.801864],
          [-82.32448, 33.820033],
          [-82.403881, 33.865477],
          [-82.408354, 33.86632],
          [-82.422803, 33.863754],
          [-82.429164, 33.865844],
          [-82.564483, 33.955744],
          [-82.564582, 33.95581],
          [-82.597079, 34.01239],
          [-82.530567, 34.071925],
          [-82.326947, 34.06412],
          [-82.306243, 33.971584],
          [-82.245055, 34.018781],
          [-82.200286, 33.978597],
          [-82.04513, 33.983396],
          [-82.046974, 33.95321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45071",
      state: "45",
      county: "071",
      name: "Newberry County",
      censusArea: 630.037,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.338961, 34.197587],
          [-81.437172, 34.129963],
          [-81.388564, 34.080227],
          [-81.471972, 34.076552],
          [-81.538899, 34.081744],
          [-81.731614, 34.187613],
          [-81.86064, 34.17933],
          [-81.870084, 34.135357],
          [-81.945019, 34.203148],
          [-81.895713, 34.29988],
          [-81.782627, 34.365089],
          [-81.643552, 34.533652],
          [-81.54043, 34.445027],
          [-81.423241, 34.494379],
          [-81.316911, 34.239458],
          [-81.345328, 34.20503],
          [-81.338961, 34.197587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45073",
      state: "45",
      county: "073",
      name: "Oconee County",
      censusArea: 626.334,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.897499, 35.056021],
          [-82.915785, 34.971836],
          [-82.900144, 34.74244],
          [-82.840253, 34.623212],
          [-82.992215, 34.479198],
          [-82.992671, 34.479072],
          [-82.995284, 34.474549],
          [-83.006848, 34.474376],
          [-83.034712, 34.483495],
          [-83.052161, 34.493062],
          [-83.072746, 34.504349],
          [-83.087189, 34.515939],
          [-83.106874, 34.534053],
          [-83.170278, 34.592398],
          [-83.170978, 34.598798],
          [-83.167523, 34.600424],
          [-83.240676, 34.624307],
          [-83.240669, 34.624507],
          [-83.291683, 34.653396],
          [-83.337867, 34.687681],
          [-83.338194, 34.687924],
          [-83.351293, 34.701703],
          [-83.350976, 34.713243],
          [-83.351392, 34.714456],
          [-83.352485, 34.715993],
          [-83.352422, 34.716111],
          [-83.339129, 34.741266],
          [-83.32406, 34.788691],
          [-83.283151, 34.821328],
          [-83.267293, 34.832748],
          [-83.242962, 34.877749],
          [-83.143261, 34.924756],
          [-83.108535, 35.000771],
          [-83.008447, 35.026974],
          [-82.897499, 35.056021]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26041",
      state: "26",
      county: "041",
      name: "Delta County",
      censusArea: 1171.096,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.459866, 45.758042],
          [-86.466039, 45.759741],
          [-86.47905, 45.757416],
          [-86.486028, 45.746608],
          [-86.496251, 45.749255],
          [-86.504216, 45.75423],
          [-86.51457, 45.752337],
          [-86.518281, 45.747688],
          [-86.523197, 45.736498],
          [-86.525166, 45.720797],
          [-86.53328, 45.710849],
          [-86.537258, 45.708361],
          [-86.54143, 45.70811],
          [-86.570627, 45.716412],
          [-86.580936, 45.71192],
          [-86.585847, 45.704922],
          [-86.584771, 45.682007],
          [-86.587528, 45.666456],
          [-86.593613, 45.665625],
          [-86.611306, 45.669733],
          [-86.62043, 45.667098],
          [-86.625132, 45.663819],
          [-86.627938, 45.659293],
          [-86.616972, 45.620581],
          [-86.616893, 45.606796],
          [-86.62387, 45.613262],
          [-86.633224, 45.618249],
          [-86.648439, 45.615992],
          [-86.666127, 45.621689],
          [-86.687208, 45.634253],
          [-86.688772, 45.639969],
          [-86.695275, 45.648175],
          [-86.708038, 45.649202],
          [-86.717828, 45.668106],
          [-86.718191, 45.67732],
          [-86.715781, 45.683949],
          [-86.705184, 45.690901],
          [-86.689102, 45.687862],
          [-86.676184, 45.691862],
          [-86.665677, 45.702217],
          [-86.665511, 45.70903],
          [-86.669263, 45.71086],
          [-86.67148, 45.72053],
          [-86.662762, 45.728964],
          [-86.647319, 45.732618],
          [-86.633138, 45.747654],
          [-86.634902, 45.763536],
          [-86.631018, 45.782019],
          [-86.617336, 45.783538],
          [-86.612137, 45.779356],
          [-86.597661, 45.775385],
          [-86.583391, 45.778242],
          [-86.576869, 45.788502],
          [-86.581071, 45.791802],
          [-86.581759, 45.794797],
          [-86.576858, 45.801473],
          [-86.571172, 45.805452],
          [-86.563392, 45.804469],
          [-86.557215, 45.808172],
          [-86.555547, 45.813499],
          [-86.559044, 45.822323],
          [-86.555186, 45.831696],
          [-86.549723, 45.836039],
          [-86.545602, 45.836495],
          [-86.538831, 45.840083],
          [-86.529208, 45.853043],
          [-86.528224, 45.856974],
          [-86.529573, 45.874974],
          [-86.532989, 45.882665],
          [-86.541464, 45.890234],
          [-86.553608, 45.896476],
          [-86.567719, 45.9005],
          [-86.583304, 45.898784],
          [-86.593184, 45.88511],
          [-86.603293, 45.876626],
          [-86.613536, 45.875982],
          [-86.625736, 45.868295],
          [-86.633168, 45.860068],
          [-86.632478, 45.843309],
          [-86.645998, 45.833888],
          [-86.721113, 45.845431],
          [-86.72852, 45.848759],
          [-86.742466, 45.864719],
          [-86.749638, 45.867796],
          [-86.758449, 45.867274],
          [-86.78208, 45.860195],
          [-86.784177, 45.854641],
          [-86.782259, 45.82995],
          [-86.777225, 45.827183],
          [-86.774612, 45.821696],
          [-86.773279, 45.811385],
          [-86.785722, 45.794517],
          [-86.801476, 45.780027],
          [-86.821523, 45.770356],
          [-86.823743, 45.765486],
          [-86.820868, 45.760776],
          [-86.821814, 45.757164],
          [-86.838658, 45.741831],
          [-86.841818, 45.729051],
          [-86.838746, 45.722307],
          [-86.870392, 45.710087],
          [-86.876904, 45.711891],
          [-86.895342, 45.711464],
          [-86.904089, 45.709546],
          [-86.92106, 45.697868],
          [-86.944158, 45.695833],
          [-86.964275, 45.672761],
          [-86.966885, 45.675001],
          [-86.967315, 45.684923],
          [-86.969765, 45.691895],
          [-86.981349, 45.696463],
          [-86.984588, 45.705812],
          [-86.982413, 45.719873],
          [-86.977655, 45.728768],
          [-86.975224, 45.75313],
          [-86.981341, 45.76616],
          [-86.981624, 45.792221],
          [-86.988438, 45.810621],
          [-87.00508, 45.831718],
          [-87.006095, 45.832244],
          [-87.006225, 45.832312],
          [-87.018902, 45.838886],
          [-87.031435, 45.837238],
          [-87.039842, 45.834245],
          [-87.050104, 45.823844],
          [-87.052043, 45.821879],
          [-87.057439, 45.812483],
          [-87.057863, 45.80917],
          [-87.057882, 45.809021],
          [-87.058207, 45.806483],
          [-87.058277, 45.80594],
          [-87.058489, 45.804285],
          [-87.058844, 45.80151],
          [-87.058682, 45.796447],
          [-87.058628, 45.794772],
          [-87.058413, 45.788071],
          [-87.058127, 45.779152],
          [-87.058601, 45.778126],
          [-87.063975, 45.76651],
          [-87.064302, 45.758828],
          [-87.062406, 45.753296],
          [-87.05555, 45.751535],
          [-87.052908, 45.747983],
          [-87.057444, 45.736822],
          [-87.061721, 45.732821],
          [-87.070442, 45.718779],
          [-87.059953, 45.708893],
          [-87.059533, 45.708497],
          [-87.061339, 45.708122],
          [-87.073932, 45.705508],
          [-87.074813, 45.705325],
          [-87.074873, 45.705312],
          [-87.093365, 45.701473],
          [-87.095455, 45.701039],
          [-87.099401, 45.698614],
          [-87.099411, 45.698514],
          [-87.099725, 45.695231],
          [-87.100265, 45.694808],
          [-87.111638, 45.685905],
          [-87.112524, 45.685696],
          [-87.12857, 45.681909],
          [-87.129412, 45.68171],
          [-87.146713, 45.673662],
          [-87.15033, 45.67198],
          [-87.161593, 45.666741],
          [-87.172241, 45.661788],
          [-87.173493, 45.66049],
          [-87.191496, 45.641827],
          [-87.196852, 45.636275],
          [-87.21122, 45.616468],
          [-87.215616, 45.610408],
          [-87.218989, 45.60576],
          [-87.223647, 45.599338],
          [-87.232472, 45.59087],
          [-87.234612, 45.588817],
          [-87.250122, 45.569059],
          [-87.260709, 45.555573],
          [-87.261979, 45.553954],
          [-87.263488, 45.552032],
          [-87.263763, 45.551482],
          [-87.264446, 45.550118],
          [-87.327472, 45.550507],
          [-87.325725, 45.898623],
          [-87.367843, 45.985313],
          [-87.242885, 45.985087],
          [-87.241555, 46.158901],
          [-87.11608, 46.158945],
          [-86.615601, 46.158691],
          [-86.490136, 46.158085],
          [-86.49025, 45.896329],
          [-86.459866, 45.758042]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26043",
      state: "26",
      county: "043",
      name: "Dickinson County",
      censusArea: 761.4,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.828827, 45.72276],
          [-87.868111, 45.749477],
          [-87.991447, 45.795393],
          [-88.039729, 45.789626],
          [-88.050634, 45.780972],
          [-88.058256, 45.780719],
          [-88.072091, 45.780261],
          [-88.094047, 45.785658],
          [-88.130434, 45.81002],
          [-88.13611, 45.819029],
          [-88.13364, 45.823128],
          [-88.114267, 45.837891],
          [-88.106622, 45.841072],
          [-88.088825, 45.85586],
          [-88.073134, 45.871952],
          [-88.073944, 45.875593],
          [-88.102603, 45.909888],
          [-88.118516, 45.918562],
          [-88.116845, 45.921627],
          [-88.117407, 46.246618],
          [-87.615914, 46.246794],
          [-87.617039, 45.986006],
          [-87.617613, 45.898842],
          [-87.696946, 45.898842],
          [-87.697159, 45.722987],
          [-87.828827, 45.72276]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26047",
      state: "26",
      county: "047",
      name: "Emmet County",
      censusArea: 467.494,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.095985, 45.367001],
          [-85.073617, 45.36542],
          [-85.066969, 45.364951],
          [-85.06425, 45.364758],
          [-85.063459, 45.364703],
          [-85.060905, 45.364522],
          [-85.054805, 45.364091],
          [-85.046635, 45.362287],
          [-85.045526, 45.362041],
          [-85.043721, 45.361643],
          [-85.043446, 45.361582],
          [-85.043101, 45.361506],
          [-85.032813, 45.361251],
          [-85.022234, 45.366701],
          [-84.998603, 45.370173],
          [-84.975357, 45.373587],
          [-84.959119, 45.375973],
          [-84.91585, 45.393115],
          [-84.912537, 45.402828],
          [-84.912956, 45.409776],
          [-84.916165, 45.417639],
          [-84.922006, 45.421914],
          [-84.97637, 45.428801],
          [-84.976623, 45.428833],
          [-84.978969, 45.429131],
          [-84.980467, 45.42932],
          [-84.980953, 45.429382],
          [-84.981192, 45.429336],
          [-84.990041, 45.427618],
          [-84.990785, 45.425264],
          [-84.989224, 45.424253],
          [-84.987416, 45.423082],
          [-84.987143, 45.422906],
          [-84.984928, 45.421471],
          [-84.983836, 45.420764],
          [-84.978373, 45.420171],
          [-84.977116, 45.420035],
          [-84.978608, 45.418663],
          [-84.994191, 45.423173],
          [-85.034856, 45.434941],
          [-85.037741, 45.435776],
          [-85.040272, 45.436509],
          [-85.040936, 45.436701],
          [-85.046943, 45.441429],
          [-85.050234, 45.444019],
          [-85.050747, 45.444423],
          [-85.052994, 45.446191],
          [-85.069573, 45.459239],
          [-85.070485, 45.460096],
          [-85.087756, 45.476335],
          [-85.088386, 45.476928],
          [-85.097142, 45.495684],
          [-85.103943, 45.510252],
          [-85.109252, 45.521626],
          [-85.109353, 45.521915],
          [-85.110884, 45.526285],
          [-85.110955, 45.526488],
          [-85.115479, 45.539406],
          [-85.115785, 45.541533],
          [-85.11587, 45.542125],
          [-85.117406, 45.552811],
          [-85.117656, 45.55455],
          [-85.119494, 45.567338],
          [-85.119675, 45.568597],
          [-85.119721, 45.568915],
          [-85.119737, 45.569026],
          [-85.119026, 45.573002],
          [-85.118825, 45.574123],
          [-85.118637, 45.575175],
          [-85.118353, 45.575625],
          [-85.117812, 45.576482],
          [-85.11243, 45.585004],
          [-85.112406, 45.585042],
          [-85.111909, 45.585829],
          [-85.111737, 45.585962],
          [-85.107744, 45.589067],
          [-85.106909, 45.589716],
          [-85.106246, 45.590231],
          [-85.105458, 45.590844],
          [-85.103521, 45.59235],
          [-85.103077, 45.592695],
          [-85.102917, 45.59282],
          [-85.102221, 45.59336],
          [-85.101977, 45.59355],
          [-85.096798, 45.597576],
          [-85.095531, 45.598562],
          [-85.093525, 45.600121],
          [-85.079528, 45.617083],
          [-85.07937, 45.617355],
          [-85.076284, 45.62266],
          [-85.075686, 45.623688],
          [-85.075678, 45.623742],
          [-85.075461, 45.625295],
          [-85.07491, 45.629242],
          [-85.074243, 45.629752],
          [-85.071381, 45.63194],
          [-85.065716, 45.636272],
          [-85.063974, 45.637604],
          [-85.061488, 45.639505],
          [-85.061006, 45.639631],
          [-85.044872, 45.643847],
          [-85.041037, 45.644849],
          [-85.03559, 45.646273],
          [-85.020107, 45.650319],
          [-85.019374, 45.65051],
          [-85.015341, 45.651564],
          [-85.014352, 45.652134],
          [-85.013218, 45.652789],
          [-85.007026, 45.65636],
          [-85.005645, 45.657504],
          [-85.002344, 45.660239],
          [-85.001154, 45.661225],
          [-84.997431, 45.667762],
          [-84.996336, 45.669685],
          [-84.996067, 45.669861],
          [-84.974237, 45.684178],
          [-84.97095, 45.686334],
          [-84.970213, 45.686984],
          [-84.94565, 45.708621],
          [-84.943756, 45.71029],
          [-84.942636, 45.714292],
          [-84.941073, 45.719879],
          [-84.940526, 45.721832],
          [-84.942098, 45.728349],
          [-84.942125, 45.72846],
          [-84.942543, 45.728865],
          [-84.943982, 45.730257],
          [-84.945958, 45.732169],
          [-84.947266, 45.733435],
          [-84.950226, 45.736299],
          [-84.95084, 45.736893],
          [-84.951745, 45.737326],
          [-84.954275, 45.738537],
          [-84.955394, 45.739072],
          [-84.95813, 45.740381],
          [-84.968309, 45.745252],
          [-84.982328, 45.75196],
          [-84.982973, 45.752022],
          [-84.983316, 45.752055],
          [-84.983829, 45.752104],
          [-84.983933, 45.752114],
          [-84.984568, 45.752175],
          [-84.985318, 45.752247],
          [-84.996755, 45.753347],
          [-85.001656, 45.753819],
          [-85.002914, 45.75394],
          [-85.011433, 45.757962],
          [-85.014509, 45.760329],
          [-85.009173, 45.762463],
          [-85.00741, 45.763168],
          [-84.995105, 45.759855],
          [-84.938312, 45.759892],
          [-84.93779, 45.759777],
          [-84.931463, 45.758389],
          [-84.926802, 45.757366],
          [-84.92523, 45.757021],
          [-84.924664, 45.756897],
          [-84.922478, 45.755842],
          [-84.920691, 45.754979],
          [-84.919996, 45.754643],
          [-84.910398, 45.75001],
          [-84.866976, 45.752066],
          [-84.840981, 45.744751],
          [-84.810953, 45.745993],
          [-84.810411, 45.746015],
          [-84.808285, 45.746103],
          [-84.806642, 45.746171],
          [-84.805114, 45.746378],
          [-84.800664, 45.74698],
          [-84.799558, 45.74713],
          [-84.792474, 45.75053],
          [-84.78958, 45.751919],
          [-84.788893, 45.752248],
          [-84.788821, 45.752283],
          [-84.788808, 45.752298],
          [-84.781995, 45.760345],
          [-84.781373, 45.76108],
          [-84.7798, 45.76965],
          [-84.787228, 45.774892],
          [-84.79229, 45.778464],
          [-84.792337, 45.778497],
          [-84.793153, 45.780463],
          [-84.780313, 45.787224],
          [-84.774156, 45.788918],
          [-84.774014, 45.788957],
          [-84.772765, 45.789301],
          [-84.751571, 45.782733],
          [-84.742, 45.784134],
          [-84.734065, 45.788205],
          [-84.732388, 45.787928],
          [-84.732687, 45.289382],
          [-85.011769, 45.278711],
          [-85.095985, 45.367001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26051",
      state: "26",
      county: "051",
      name: "Gladwin County",
      censusArea: 501.779,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.608104, 44.160482],
          [-84.368039, 44.160538],
          [-84.166107, 44.161788],
          [-84.166377, 43.996901],
          [-84.167318, 43.825902],
          [-84.606035, 43.815218],
          [-84.608001, 44.15859],
          [-84.608104, 44.160482]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26065",
      state: "26",
      county: "065",
      name: "Ingham County",
      censusArea: 556.12,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.158189, 42.776639],
          [-84.151966, 42.710731],
          [-84.140713, 42.424611],
          [-84.600403, 42.421984],
          [-84.602761, 42.76988],
          [-84.36366, 42.769971],
          [-84.363659, 42.775778],
          [-84.158189, 42.776639]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26069",
      state: "26",
      county: "069",
      name: "Iosco County",
      censusArea: 549.096,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.318216, 44.511734],
          [-83.31761, 44.486058],
          [-83.326824, 44.444411],
          [-83.327171, 44.429234],
          [-83.324616, 44.415039],
          [-83.321553, 44.409119],
          [-83.321648, 44.404502],
          [-83.333757, 44.372486],
          [-83.335248, 44.357995],
          [-83.332533, 44.340464],
          [-83.336988, 44.332919],
          [-83.343738, 44.329763],
          [-83.352115, 44.332366],
          [-83.364312, 44.33259],
          [-83.373607, 44.327784],
          [-83.401822, 44.301831],
          [-83.414301, 44.294543],
          [-83.419236, 44.2878],
          [-83.425762, 44.272487],
          [-83.429689, 44.269708],
          [-83.442731, 44.265361],
          [-83.445805, 44.273378],
          [-83.447742, 44.273991],
          [-83.460958, 44.278176],
          [-83.463049, 44.278838],
          [-83.479531, 44.28009],
          [-83.500392, 44.27661],
          [-83.508839, 44.273711],
          [-83.524817, 44.261558],
          [-83.53771, 44.248171],
          [-83.549096, 44.227282],
          [-83.552872, 44.210718],
          [-83.553834, 44.197956],
          [-83.565225, 44.163517],
          [-83.883977, 44.161786],
          [-83.886634, 44.508973],
          [-83.318216, 44.511734]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17131",
      state: "17",
      county: "131",
      name: "Mercer County",
      censusArea: 561.202,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.948523, 41.070248],
          [-90.949383, 41.07271],
          [-90.949383, 41.072711],
          [-90.946627, 41.096632],
          [-90.989663, 41.155716],
          [-90.997906, 41.162564],
          [-91.005503, 41.165622],
          [-91.019036, 41.16491],
          [-91.049808, 41.178033],
          [-91.113648, 41.241401],
          [-91.114186, 41.250029],
          [-91.079657, 41.333727],
          [-91.078682, 41.336089],
          [-90.433771, 41.326983],
          [-90.437657, 41.151252],
          [-90.437847, 41.137221],
          [-90.438122, 41.136808],
          [-90.437957, 41.133884],
          [-90.439423, 41.063995],
          [-90.785194, 41.068749],
          [-90.948523, 41.070248]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13191",
      state: "13",
      county: "191",
      name: "Mc Intosh County",
      censusArea: 424.299,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.663206, 31.538667],
          [-81.566009, 31.576467],
          [-81.491798, 31.699575],
          [-81.43576, 31.642055],
          [-81.261868, 31.650142],
          [-81.19438, 31.568101],
          [-81.204315, 31.568183],
          [-81.214536, 31.557601],
          [-81.240699, 31.552313],
          [-81.254218, 31.55594],
          [-81.260076, 31.54828],
          [-81.263905, 31.532579],
          [-81.263437, 31.530932],
          [-81.258809, 31.52906],
          [-81.217948, 31.527284],
          [-81.213519, 31.528152],
          [-81.199518, 31.537596],
          [-81.193016, 31.535833],
          [-81.181592, 31.527697],
          [-81.17831, 31.52241],
          [-81.177254, 31.517074],
          [-81.189643, 31.503588],
          [-81.204883, 31.473124],
          [-81.246911, 31.422784],
          [-81.258616, 31.404425],
          [-81.278798, 31.367214],
          [-81.279338, 31.351127],
          [-81.282923, 31.326491],
          [-81.274513, 31.326237],
          [-81.268027, 31.324218],
          [-81.25482, 31.315452],
          [-81.260958, 31.30391],
          [-81.269906, 31.294489],
          [-81.435874, 31.313263],
          [-81.62429, 31.452982],
          [-81.663206, 31.538667]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31079",
      state: "31",
      county: "079",
      name: "Hall County",
      censusArea: 546.289,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.282851, 40.698284],
          [-98.72143, 40.698903],
          [-98.721975, 41.046674],
          [-98.287168, 41.046363],
          [-98.282359, 41.046355],
          [-98.282569, 40.872296],
          [-98.282851, 40.698284]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31081",
      state: "31",
      county: "081",
      name: "Hamilton County",
      censusArea: 542.878,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.826287, 41.046581],
          [-97.825723, 40.698555],
          [-98.278103, 40.698291],
          [-98.282851, 40.698284],
          [-98.282569, 40.872296],
          [-98.019385, 41.066392],
          [-97.892531, 41.111496],
          [-97.828243, 41.17375],
          [-97.827964, 41.047315],
          [-97.826287, 41.046581]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31083",
      state: "31",
      county: "083",
      name: "Harlan County",
      censusArea: 553.466,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.630459, 40.35112],
          [-99.179403, 40.350681],
          [-99.179133, 40.001977],
          [-99.186962, 40.001977],
          [-99.188905, 40.002023],
          [-99.197592, 40.002033],
          [-99.216376, 40.002016],
          [-99.25037, 40.001957],
          [-99.254012, 40.002074],
          [-99.282967, 40.001879],
          [-99.286656, 40.002017],
          [-99.290703, 40.001949],
          [-99.403389, 40.001969],
          [-99.412645, 40.001868],
          [-99.480728, 40.001942],
          [-99.493465, 40.001937],
          [-99.49766, 40.001912],
          [-99.498999, 40.001957],
          [-99.501792, 40.002026],
          [-99.625324, 40.001866],
          [-99.62598, 40.001865],
          [-99.628255, 40.001866],
          [-99.630459, 40.35112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31087",
      state: "31",
      county: "087",
      name: "Hitchcock County",
      censusArea: 709.943,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.758435, 40.349507],
          [-100.75883, 40.002302],
          [-100.937427, 40.002145],
          [-101.027686, 40.002256],
          [-101.060317, 40.002307],
          [-101.130907, 40.002427],
          [-101.168704, 40.002547],
          [-101.178805, 40.002468],
          [-101.192219, 40.002491],
          [-101.215033, 40.002555],
          [-101.248673, 40.002543],
          [-101.286555, 40.002559],
          [-101.293991, 40.002559],
          [-101.324036, 40.002696],
          [-101.325514, 40.002687],
          [-101.324036, 40.002704],
          [-101.323353, 40.350553],
          [-100.777376, 40.349466],
          [-100.777372, 40.349447],
          [-100.758435, 40.349507]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31099",
      state: "31",
      county: "099",
      name: "Kearney County",
      censusArea: 516.244,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.723948, 40.350391],
          [-98.726827, 40.350398],
          [-99.179403, 40.350681],
          [-99.179248, 40.660541],
          [-98.906096, 40.652722],
          [-98.724387, 40.689772],
          [-98.723948, 40.350391]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31101",
      state: "31",
      county: "101",
      name: "Keith County",
      censusArea: 1061.6,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.98463, 41.394712],
          [-101.40726, 41.395416],
          [-101.269695, 41.394934],
          [-101.270381, 41.047392],
          [-101.249975, 41.00395],
          [-102.051761, 41.00389],
          [-102.055535, 41.221547],
          [-102.055256, 41.395162],
          [-101.98463, 41.394712]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31105",
      state: "31",
      county: "105",
      name: "Kimball County",
      censusArea: 951.846,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.052324, 41.321144],
          [-104.052287, 41.393214],
          [-103.37762, 41.394633],
          [-103.382493, 41.001883],
          [-103.421925, 41.001969],
          [-103.421975, 41.002007],
          [-103.486697, 41.001914],
          [-103.497447, 41.001635],
          [-103.574522, 41.001721],
          [-103.858449, 41.001681],
          [-103.877967, 41.001673],
          [-103.896207, 41.00175],
          [-103.953525, 41.001596],
          [-103.971373, 41.001524],
          [-103.972642, 41.001615],
          [-104.018223, 41.001617],
          [-104.023383, 41.001887],
          [-104.039238, 41.001502],
          [-104.053249, 41.001406],
          [-104.053158, 41.016809],
          [-104.053097, 41.018045],
          [-104.053177, 41.089725],
          [-104.053025, 41.090274],
          [-104.053083, 41.104985],
          [-104.053142, 41.114457],
          [-104.052666, 41.275251],
          [-104.052574, 41.278019],
          [-104.052453, 41.278202],
          [-104.052568, 41.316202],
          [-104.052476, 41.320961],
          [-104.052324, 41.321144]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31109",
      state: "31",
      county: "109",
      name: "Lancaster County",
      censusArea: 837.548,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.91094, 41.046116],
          [-96.908507, 41.046091],
          [-96.463869, 41.045083],
          [-96.463861, 41.016069],
          [-96.463764, 40.78396],
          [-96.463632, 40.523013],
          [-96.912637, 40.523625],
          [-96.913493, 40.697948],
          [-96.911116, 40.697975],
          [-96.91094, 41.046116]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31115",
      state: "31",
      county: "115",
      name: "Loup County",
      censusArea: 568.29,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.222733, 42.087916],
          [-99.22183, 41.740792],
          [-99.686834, 41.740326],
          [-99.686959, 42.086075],
          [-99.662379, 42.086009],
          [-99.234629, 42.087995],
          [-99.224264, 42.088028],
          [-99.222733, 42.087916]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31119",
      state: "31",
      county: "119",
      name: "Madison County",
      censusArea: 572.743,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.36807, 41.743207],
          [-97.830493, 41.742238],
          [-97.83239, 41.742788],
          [-97.833288, 41.916286],
          [-97.834336, 42.088987],
          [-97.834536, 42.08975],
          [-97.368404, 42.090922],
          [-97.36807, 41.743207]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28091",
      state: "28",
      county: "091",
      name: "Marion County",
      censusArea: 542.384,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.654212, 31.433805],
          [-89.6544, 31.404663],
          [-89.654136, 31.375517],
          [-89.654038, 31.002502],
          [-89.728148, 31.0023],
          [-89.728147, 31.002431],
          [-89.732504, 31.004831],
          [-89.752642, 31.001853],
          [-89.816429, 31.002084],
          [-89.824617, 31.00206],
          [-89.835542, 31.002059],
          [-89.835908, 31.002059],
          [-89.835873, 31.074926],
          [-89.938108, 31.118956],
          [-90.031896, 31.175727],
          [-90.040466, 31.336014],
          [-90.040485, 31.394673],
          [-89.95894, 31.390489],
          [-89.823332, 31.390021],
          [-89.824236, 31.434093],
          [-89.654212, 31.433805]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28097",
      state: "28",
      county: "097",
      name: "Montgomery County",
      censusArea: 406.981,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.453808, 33.285942],
          [-89.64528, 33.285515],
          [-89.646314, 33.402544],
          [-89.785227, 33.416275],
          [-89.78689, 33.676727],
          [-89.506872, 33.677605],
          [-89.506496, 33.459806],
          [-89.381092, 33.460307],
          [-89.45405, 33.4332],
          [-89.453808, 33.285942]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28101",
      state: "28",
      county: "101",
      name: "Newton County",
      censusArea: 578.104,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.117675, 32.223972],
          [-89.322692, 32.223472],
          [-89.322598, 32.576433],
          [-89.317588, 32.576458],
          [-88.914516, 32.576955],
          [-88.913847, 32.224186],
          [-89.117675, 32.223972]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28109",
      state: "28",
      county: "109",
      name: "Pearl River County",
      censusArea: 810.863,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.34895, 31.01037],
          [-89.340806, 30.909912],
          [-89.341446, 30.64772],
          [-89.544232, 30.648368],
          [-89.54451, 30.516862],
          [-89.69058, 30.458773],
          [-89.724614, 30.491902],
          [-89.732577, 30.492502],
          [-89.76057, 30.515761],
          [-89.806843, 30.572039],
          [-89.823384, 30.608535],
          [-89.842585, 30.666005],
          [-89.842596, 30.666038],
          [-89.826175, 30.736594],
          [-89.789863, 30.851527],
          [-89.77341, 30.897693],
          [-89.770931, 30.899432],
          [-89.756543, 30.900431],
          [-89.744789, 30.918933],
          [-89.728382, 30.971141],
          [-89.728148, 31.0023],
          [-89.654038, 31.002502],
          [-89.501411, 31.013907],
          [-89.476022, 30.982901],
          [-89.34895, 31.01037]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28119",
      state: "28",
      county: "119",
      name: "Quitman County",
      censusArea: 405.012,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.450981, 34.072932],
          [-90.450572, 34.262402],
          [-90.399014, 34.261524],
          [-90.399498, 34.423807],
          [-90.301851, 34.424546],
          [-90.304317, 34.509621],
          [-90.198536, 34.511088],
          [-90.194086, 34.301407],
          [-90.134441, 34.265195],
          [-90.135456, 34.160074],
          [-90.135695, 34.0729],
          [-90.450981, 34.072932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28121",
      state: "28",
      county: "121",
      name: "Rankin County",
      censusArea: 775.485,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.230331, 32.049728],
          [-90.24538, 32.171396],
          [-90.160477, 32.30834],
          [-90.065697, 32.399529],
          [-90.06882, 32.438078],
          [-89.962077, 32.491286],
          [-89.858878, 32.584836],
          [-89.784123, 32.586891],
          [-89.730063, 32.352733],
          [-89.730424, 32.222039],
          [-89.731059, 32.222045],
          [-89.730066, 32.047072],
          [-90.230331, 32.049728]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28129",
      state: "28",
      county: "129",
      name: "Smith County",
      censusArea: 636.252,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.359928, 31.799388],
          [-89.401227, 31.796863],
          [-89.653025, 31.780653],
          [-89.662086, 32.047239],
          [-89.730066, 32.047072],
          [-89.731059, 32.222045],
          [-89.730424, 32.222039],
          [-89.322692, 32.223472],
          [-89.321127, 31.885149],
          [-89.317086, 31.874305],
          [-89.31656, 31.80209],
          [-89.359928, 31.799388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35041",
      state: "35",
      county: "041",
      name: "Roosevelt County",
      censusArea: 2447.431,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.043936, 34.302585],
          [-103.043719, 34.289441],
          [-103.043644, 34.256903],
          [-103.043569, 34.087947],
          [-103.043516, 34.079382],
          [-103.043686, 34.063078],
          [-103.043744, 34.049986],
          [-103.043767, 34.043545],
          [-103.043721, 34.04232],
          [-103.043771, 34.041538],
          [-103.043746, 34.037294],
          [-103.043555, 34.032714],
          [-103.043531, 34.018014],
          [-103.043617, 34.003633],
          [-103.04395, 33.974629],
          [-103.044893, 33.945617],
          [-103.045698, 33.906299],
          [-103.045644, 33.901537],
          [-103.046907, 33.8503],
          [-103.047346, 33.824675],
          [-103.049096, 33.74627],
          [-103.050148, 33.701971],
          [-103.050532, 33.672408],
          [-103.051087, 33.658186],
          [-103.051535, 33.650487],
          [-103.051363, 33.64195],
          [-103.051664, 33.629489],
          [-103.05261, 33.570599],
          [-103.510267, 33.570256],
          [-103.509726, 33.657218],
          [-103.71826, 33.656136],
          [-103.716794, 33.8193],
          [-103.841906, 33.819252],
          [-103.841619, 34.081681],
          [-103.945949, 34.082784],
          [-103.948839, 34.605058],
          [-103.738232, 34.604671],
          [-103.740017, 34.303076],
          [-103.043936, 34.302585]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35043",
      state: "35",
      county: "043",
      name: "Sandoval County",
      censusArea: 3710.65,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.244827, 35.215204],
          [-106.598652, 35.217939],
          [-106.729852, 35.218073],
          [-106.763162, 35.218141],
          [-106.962038, 35.218392],
          [-107.19617, 35.219323],
          [-107.23916, 35.305368],
          [-107.310041, 35.305192],
          [-107.309634, 35.419011],
          [-107.306591, 35.41853],
          [-107.308212, 35.99869],
          [-107.62651, 36.00032],
          [-107.62425, 36.219799],
          [-106.884989, 36.21657],
          [-106.885269, 35.998967],
          [-106.247241, 36.001517],
          [-106.247949, 35.966335],
          [-106.399644, 35.960055],
          [-106.4007, 35.807567],
          [-106.24825, 35.755739],
          [-106.248491, 35.443199],
          [-106.245676, 35.238458],
          [-106.24516, 35.23182],
          [-106.245514, 35.229722],
          [-106.245061, 35.228292],
          [-106.244827, 35.215204]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35049",
      state: "35",
      county: "049",
      name: "Santa Fe County",
      censusArea: 1909.414,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.194229, 35.040274],
          [-106.243866, 35.040088],
          [-106.243683, 35.041219],
          [-106.244827, 35.215204],
          [-106.245061, 35.228292],
          [-106.245514, 35.229722],
          [-106.24516, 35.23182],
          [-106.245676, 35.238458],
          [-106.248491, 35.443199],
          [-106.24825, 35.755739],
          [-106.171656, 35.828176],
          [-106.248257, 35.839178],
          [-106.245439, 35.93093],
          [-106.054128, 35.930783],
          [-106.071394, 36.002028],
          [-105.71675, 36.002356],
          [-105.717374, 35.97876],
          [-105.72054, 35.875028],
          [-105.72028, 35.871015],
          [-105.714475, 35.041562],
          [-106.194229, 35.040274]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35057",
      state: "35",
      county: "057",
      name: "Torrance County",
      censusArea: 3344.845,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.41699, 34.439797],
          [-106.417473, 34.521663],
          [-106.469975, 34.521163],
          [-106.464532, 34.809198],
          [-106.464177, 34.870153],
          [-106.410977, 34.870254],
          [-106.149575, 34.870142],
          [-106.150155, 34.953449],
          [-106.243866, 34.953293],
          [-106.243866, 35.040088],
          [-106.194229, 35.040274],
          [-105.714475, 35.041562],
          [-105.290884, 35.042093],
          [-105.290299, 34.606744],
          [-105.312626, 34.607052],
          [-105.312968, 34.34672],
          [-105.313218, 34.260015],
          [-105.923959, 34.259991],
          [-106.415861, 34.259715],
          [-106.41699, 34.439797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35061",
      state: "35",
      county: "061",
      name: "Valencia County",
      censusArea: 1066.175,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.066694, 34.957189],
          [-107.024784, 34.870254],
          [-106.720666, 34.870053],
          [-106.685889, 34.90615],
          [-106.414475, 34.892225],
          [-106.410974, 34.889494],
          [-106.410977, 34.870254],
          [-106.464177, 34.870153],
          [-106.464532, 34.809198],
          [-106.469975, 34.521163],
          [-106.417473, 34.521663],
          [-106.41699, 34.439797],
          [-106.77306, 34.53329],
          [-106.882561, 34.579457],
          [-107.20441, 34.578813],
          [-107.203039, 34.958064],
          [-107.066694, 34.957189]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36007",
      state: "36",
      county: "007",
      name: "Broome County",
      censusArea: 705.766,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.359579, 41.999445],
          [-75.431961, 41.999363],
          [-75.436216, 41.999353],
          [-75.477144, 41.999407],
          [-75.48315, 41.999259],
          [-75.483738, 41.999244],
          [-75.98025, 41.999035],
          [-75.983082, 41.999035],
          [-76.10584, 41.998858],
          [-76.111106, 42.112436],
          [-76.114033, 42.153418],
          [-76.081134, 42.230495],
          [-76.130181, 42.410337],
          [-75.86402, 42.415702],
          [-75.843792, 42.259707],
          [-75.638299, 42.248686],
          [-75.63711, 42.195628],
          [-75.532776, 42.195241],
          [-75.419907, 42.194918],
          [-75.418421, 42.195032],
          [-75.418544, 42.189504],
          [-75.418807, 42.188104],
          [-75.418689, 42.188022],
          [-75.418438, 42.186797],
          [-75.418827, 42.180702],
          [-75.419664, 42.150436],
          [-75.421776, 42.04203],
          [-75.359579, 41.999445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36011",
      state: "36",
      county: "011",
      name: "Cayuga County",
      censusArea: 691.582,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.479224, 43.227519],
          [-76.499312, 43.097949],
          [-76.462999, 43.006316],
          [-76.450738, 42.84576],
          [-76.356974, 42.84945],
          [-76.274673, 42.771257],
          [-76.265584, 42.623588],
          [-76.666543, 42.623457],
          [-76.733454, 42.727895],
          [-76.73674, 42.970286],
          [-76.713806, 43.024035],
          [-76.705345, 43.125463],
          [-76.722501, 43.343686],
          [-76.69836, 43.344436],
          [-76.684856, 43.352691],
          [-76.669624, 43.366526],
          [-76.642672, 43.401241],
          [-76.630774, 43.413356],
          [-76.617109, 43.419137],
          [-76.605012, 43.25357],
          [-76.479224, 43.227519]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36013",
      state: "36",
      county: "013",
      name: "Chautauqua County",
      censusArea: 1060.226,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.610839, 41.998989],
          [-79.625301, 41.999068],
          [-79.625287, 41.999003],
          [-79.761374, 41.999067],
          [-79.762122, 42.131246],
          [-79.761861, 42.150712],
          [-79.761759, 42.162675],
          [-79.761921, 42.173319],
          [-79.761929, 42.179693],
          [-79.761833, 42.183627],
          [-79.762152, 42.243054],
          [-79.761964, 42.251354],
          [-79.761951, 42.26986],
          [-79.717825, 42.284711],
          [-79.645358, 42.315631],
          [-79.593992, 42.341641],
          [-79.546262, 42.363417],
          [-79.510999, 42.382373],
          [-79.474794, 42.404291],
          [-79.453533, 42.411157],
          [-79.429119, 42.42838],
          [-79.405458, 42.453281],
          [-79.381943, 42.466491],
          [-79.36213, 42.480195],
          [-79.351989, 42.48892],
          [-79.342316, 42.489664],
          [-79.335129, 42.488321],
          [-79.331483, 42.489076],
          [-79.323079, 42.494795],
          [-79.31774, 42.499884],
          [-79.283364, 42.511228],
          [-79.264624, 42.523159],
          [-79.242889, 42.531757],
          [-79.223195, 42.536087],
          [-79.193232, 42.545881],
          [-79.148723, 42.553672],
          [-79.138569, 42.564462],
          [-79.136725, 42.569693],
          [-79.060777, 42.537853],
          [-79.061265, 41.999259],
          [-79.538445, 41.998527],
          [-79.551385, 41.998666],
          [-79.610839, 41.998989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36019",
      state: "36",
      county: "019",
      name: "Clinton County",
      censusArea: 1037.852,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.909687, 44.429699],
          [-73.966148, 44.709118],
          [-73.986382, 44.707773],
          [-74.027392, 44.995765],
          [-73.874597, 45.001223],
          [-73.624588, 45.003954],
          [-73.343124, 45.01084],
          [-73.350188, 44.994304],
          [-73.35945, 44.915684],
          [-73.369647, 44.829136],
          [-73.339958, 44.778893],
          [-73.365326, 44.703294],
          [-73.390231, 44.618353],
          [-73.374389, 44.575455],
          [-73.361952, 44.563064],
          [-73.356788, 44.557918],
          [-73.338634, 44.546847],
          [-73.463838, 44.537681],
          [-73.496604, 44.486081],
          [-73.700717, 44.445571],
          [-73.909687, 44.429699]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36027",
      state: "36",
      county: "027",
      name: "Dutchess County",
      censusArea: 795.63,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.953307, 41.589977],
          [-73.947382, 41.667493],
          [-73.942482, 41.684093],
          [-73.943482, 41.690293],
          [-73.945782, 41.695593],
          [-73.946682, 41.699396],
          [-73.944435, 41.714634],
          [-73.941081, 41.732693],
          [-73.941081, 41.735993],
          [-73.962221, 41.90102],
          [-73.929626, 42.078778],
          [-73.71093, 42.005488],
          [-73.527072, 41.97798],
          [-73.496879, 42.049675],
          [-73.487314, 42.049638],
          [-73.504944, 41.824285],
          [-73.505008, 41.823773],
          [-73.510171, 41.758686],
          [-73.517473, 41.666646],
          [-73.521457, 41.616429],
          [-73.530067, 41.527194],
          [-73.933775, 41.488279],
          [-73.981486, 41.438905],
          [-74.000108, 41.456549],
          [-73.997609, 41.487212],
          [-73.953307, 41.589977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36033",
      state: "36",
      county: "033",
      name: "Franklin County",
      censusArea: 1629.119,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.09349, 44.137615],
          [-74.28187, 44.120552],
          [-74.535156, 44.09925],
          [-74.525683, 44.170636],
          [-74.641872, 44.952621],
          [-74.720307, 44.953011],
          [-74.72498, 45.005915],
          [-74.702018, 45.003322],
          [-74.291307, 44.992058],
          [-74.146814, 44.9915],
          [-74.027392, 44.995765],
          [-73.986382, 44.707773],
          [-73.966148, 44.709118],
          [-73.909687, 44.429699],
          [-74.141424, 44.407268],
          [-74.12756, 44.330211],
          [-74.09349, 44.137615]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36035",
      state: "36",
      county: "035",
      name: "Fulton County",
      censusArea: 495.469,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.097467, 42.982934],
          [-74.289304, 42.984415],
          [-74.488844, 42.985118],
          [-74.542367, 42.98553],
          [-74.759895, 43.047423],
          [-74.696333, 43.173515],
          [-74.712615, 43.286143],
          [-74.534657, 43.228115],
          [-74.326378, 43.241635],
          [-74.220902, 43.221403],
          [-74.140147, 43.253979],
          [-74.097467, 42.982934]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36039",
      state: "36",
      county: "039",
      name: "Greene County",
      censusArea: 647.161,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.451713, 42.169225],
          [-74.53731, 42.201424],
          [-74.443506, 42.355017],
          [-74.244692, 42.377159],
          [-74.254303, 42.408207],
          [-73.783721, 42.464231],
          [-73.789502, 42.267738],
          [-73.910675, 42.127293],
          [-74.042393, 42.170386],
          [-74.074797, 42.096589],
          [-74.307571, 42.114346],
          [-74.451713, 42.169225]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40131",
      state: "40",
      county: "131",
      name: "Rogers County",
      censusArea: 675.632,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.726408, 36.162496],
          [-95.730293, 36.162288],
          [-95.73201, 36.162498],
          [-95.743981, 36.162498],
          [-95.745119, 36.162823],
          [-95.747764, 36.162498],
          [-95.751545, 36.162723],
          [-95.76165, 36.16275],
          [-95.8154, 36.16263],
          [-95.81268, 36.42356],
          [-95.812449, 36.597516],
          [-95.809617, 36.597476],
          [-95.66077, 36.59752],
          [-95.43157, 36.59754],
          [-95.32837, 36.59763],
          [-95.32817, 36.51024],
          [-95.43561, 36.51016],
          [-95.43997, 36.0752],
          [-95.571525, 36.075282],
          [-95.61852, 36.162512],
          [-95.726408, 36.162496]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40135",
      state: "40",
      county: "135",
      name: "Sequoyah County",
      censusArea: 673.271,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.463318, 35.58266],
          [-94.431789, 35.397659],
          [-94.431215, 35.39429],
          [-94.433915, 35.387391],
          [-94.433742, 35.386467],
          [-94.493889, 35.301223],
          [-94.594801, 35.327974],
          [-94.668796, 35.295029],
          [-94.750224, 35.356969],
          [-94.813629, 35.323344],
          [-94.911917, 35.346651],
          [-94.901832, 35.39794],
          [-95.049933, 35.458894],
          [-95.132437, 35.526196],
          [-95.127464, 35.638894],
          [-94.807297, 35.638604],
          [-94.472647, 35.638556],
          [-94.465272, 35.594037],
          [-94.464457, 35.588909],
          [-94.464097, 35.587265],
          [-94.463318, 35.58266]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40137",
      state: "40",
      county: "137",
      name: "Stephens County",
      censusArea: 870.244,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.142148, 34.506994],
          [-98.141799, 34.681158],
          [-98.089098, 34.681119],
          [-98.087756, 34.681255],
          [-97.668425, 34.681344],
          [-97.562757, 34.681147],
          [-97.562323, 34.507036],
          [-97.562575, 34.289182],
          [-98.139226, 34.289775],
          [-98.139237, 34.507003],
          [-98.142148, 34.506994]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40143",
      state: "40",
      county: "143",
      name: "Tulsa County",
      censusArea: 570.246,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.029544, 35.988387],
          [-96.029583, 36.075366],
          [-96.297786, 36.075779],
          [-96.297888, 36.162279],
          [-96.268287, 36.16198],
          [-96.001055, 36.161294],
          [-96.001171, 36.423686],
          [-95.81268, 36.42356],
          [-95.8154, 36.16263],
          [-95.76165, 36.16275],
          [-95.761687, 35.900811],
          [-95.819964, 35.8559],
          [-96.033118, 35.856824],
          [-96.029544, 35.988387]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40145",
      state: "40",
      county: "145",
      name: "Wagoner County",
      censusArea: 561.555,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.726408, 36.162496],
          [-95.61852, 36.162512],
          [-95.571525, 36.075282],
          [-95.43997, 36.0752],
          [-95.207946, 36.074708],
          [-95.278701, 35.964102],
          [-95.231459, 35.851195],
          [-95.265679, 35.813266],
          [-95.638313, 35.787596],
          [-95.65042, 35.856756],
          [-95.766166, 35.856403],
          [-95.805637, 35.856577],
          [-95.806224, 35.856028],
          [-95.819964, 35.8559],
          [-95.761687, 35.900811],
          [-95.76165, 36.16275],
          [-95.751545, 36.162723],
          [-95.747764, 36.162498],
          [-95.745119, 36.162823],
          [-95.743981, 36.162498],
          [-95.73201, 36.162498],
          [-95.730293, 36.162288],
          [-95.726408, 36.162496]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40149",
      state: "40",
      county: "149",
      name: "Washita County",
      censusArea: 1003.167,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.621054, 35.097352],
          [-98.750344, 35.116343],
          [-99.360226, 35.116738],
          [-99.364217, 35.465328],
          [-98.623336, 35.464225],
          [-98.621054, 35.097352]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41003",
      state: "41",
      county: "003",
      name: "Benton County",
      censusArea: 675.941,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.602566, 44.721191],
          [-123.580757, 44.719441],
          [-123.347514, 44.720105],
          [-123.149058, 44.720277],
          [-123.073643, 44.657331],
          [-123.164428, 44.637507],
          [-123.260338, 44.555391],
          [-123.214683, 44.507681],
          [-123.205287, 44.443038],
          [-123.2436, 44.378545],
          [-123.18398, 44.283691],
          [-123.77542, 44.283561],
          [-123.816931, 44.344804],
          [-123.715212, 44.360569],
          [-123.72042, 44.433053],
          [-123.597217, 44.43289],
          [-123.602566, 44.721191]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41005",
      state: "41",
      county: "005",
      name: "Clackamas County",
      censusArea: 1870.322,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.696808, 45.258017],
          [-121.681033, 45.250788],
          [-121.682607, 45.228988],
          [-121.701557, 45.230198],
          [-121.745966, 45.186272],
          [-121.659355, 45.066511],
          [-121.805051, 45.01131],
          [-121.734167, 44.885782],
          [-122.395327, 44.885734],
          [-122.508396, 44.919805],
          [-122.788386, 45.130129],
          [-122.738988, 45.259532],
          [-122.849723, 45.259654],
          [-122.867816, 45.259585],
          [-122.868009, 45.317376],
          [-122.743741, 45.33201],
          [-122.744065, 45.433283],
          [-122.648725, 45.432718],
          [-122.651305, 45.436541],
          [-122.645205, 45.441641],
          [-122.652605, 45.445741],
          [-122.660979, 45.457819],
          [-121.820394, 45.461666],
          [-121.696889, 45.373758],
          [-121.696808, 45.258017]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41011",
      state: "41",
      county: "011",
      name: "Coos County",
      censusArea: 1596.173,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.812093, 42.789433],
          [-124.132985, 42.666379],
          [-124.141977, 42.849334],
          [-124.257111, 42.954757],
          [-124.479344, 42.95497],
          [-124.462619, 42.99143],
          [-124.456918, 43.000315],
          [-124.436198, 43.071312],
          [-124.432236, 43.097383],
          [-124.434451, 43.115986],
          [-124.424113, 43.126859],
          [-124.401726, 43.184896],
          [-124.395302, 43.211101],
          [-124.395607, 43.223908],
          [-124.38246, 43.270167],
          [-124.388891, 43.290523],
          [-124.393988, 43.29926],
          [-124.400404, 43.302121],
          [-124.402814, 43.305872],
          [-124.387642, 43.325968],
          [-124.373037, 43.338953],
          [-124.353332, 43.342667],
          [-124.341587, 43.351337],
          [-124.315012, 43.388389],
          [-124.286896, 43.436296],
          [-124.255609, 43.502172],
          [-124.233534, 43.55713],
          [-124.218876, 43.610319],
          [-123.875424, 43.608254],
          [-123.875529, 43.515949],
          [-123.816834, 43.51603],
          [-123.817707, 43.431237],
          [-123.764005, 43.432237],
          [-123.764, 43.257935],
          [-123.703798, 43.257835],
          [-123.701989, 43.087288],
          [-123.76063, 43.083127],
          [-123.762095, 42.996036],
          [-123.820798, 42.995935],
          [-123.812093, 42.789433]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41013",
      state: "41",
      county: "013",
      name: "Crook County",
      censusArea: 2979.086,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.107344, 44.390542],
          [-120.98854, 44.390045],
          [-120.988721, 44.476444],
          [-120.827177, 44.476633],
          [-120.827342, 44.562849],
          [-120.385526, 44.563954],
          [-120.385823, 44.437556],
          [-120.021841, 44.438558],
          [-120.020663, 44.389651],
          [-119.89901, 44.389537],
          [-119.898796, 44.306662],
          [-119.655586, 44.306964],
          [-119.657577, 43.959051],
          [-119.775653, 43.959068],
          [-119.777526, 43.698081],
          [-119.898172, 43.698323],
          [-120.258018, 43.698762],
          [-120.257893, 43.785259],
          [-120.378508, 43.785033],
          [-120.3789, 43.871955],
          [-120.747946, 43.871266],
          [-120.748317, 43.957068],
          [-120.986527, 43.960943],
          [-120.987333, 44.133843],
          [-121.102637, 44.138042],
          [-121.107344, 44.390542]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41021",
      state: "41",
      county: "021",
      name: "Gilliam County",
      censusArea: 1204.809,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.653559, 45.737237],
          [-120.634968, 45.745847],
          [-120.591166, 45.746547],
          [-120.559465, 45.738348],
          [-120.521964, 45.709848],
          [-120.505863, 45.700048],
          [-120.482362, 45.694449],
          [-120.40396, 45.699249],
          [-120.210754, 45.725951],
          [-120.170453, 45.761951],
          [-120.141352, 45.773152],
          [-120.001148, 45.811902],
          [-119.999506, 45.812199],
          [-120.00674, 45.257454],
          [-119.882037, 45.255956],
          [-119.883034, 45.169657],
          [-119.760125, 45.16758],
          [-119.790426, 45.067761],
          [-120.495247, 45.068549],
          [-120.494548, 45.07465],
          [-120.503549, 45.08295],
          [-120.462548, 45.10115],
          [-120.481253, 45.19275],
          [-120.540256, 45.217049],
          [-120.525059, 45.405249],
          [-120.495859, 45.465349],
          [-120.357839, 45.533854],
          [-120.653559, 45.737237]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41027",
      state: "41",
      county: "027",
      name: "Hood River County",
      censusArea: 521.949,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.908267, 45.654399],
          [-121.900858, 45.662009],
          [-121.901855, 45.670716],
          [-121.867167, 45.693277],
          [-121.820055, 45.704649],
          [-121.811304, 45.706761],
          [-121.735104, 45.694039],
          [-121.707358, 45.694809],
          [-121.626185, 45.705915],
          [-121.533106, 45.726541],
          [-121.522392, 45.724677],
          [-121.462849, 45.701367],
          [-121.441045, 45.69727],
          [-121.440612, 45.519301],
          [-121.482525, 45.519619],
          [-121.481527, 45.258279],
          [-121.696808, 45.258017],
          [-121.696889, 45.373758],
          [-121.820394, 45.461666],
          [-121.906742, 45.520497],
          [-121.922242, 45.648495],
          [-121.908267, 45.654399]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26075",
      state: "26",
      county: "075",
      name: "Jackson County",
      censusArea: 701.666,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.131963, 42.071577],
          [-84.363297, 42.073456],
          [-84.709556, 42.070366],
          [-84.718493, 42.42152],
          [-84.600403, 42.421984],
          [-84.140713, 42.424611],
          [-84.131136, 42.424567],
          [-84.131963, 42.071577]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26079",
      state: "26",
      county: "079",
      name: "Kalkaska County",
      censusArea: 559.865,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.850929, 44.511069],
          [-85.334, 44.512303],
          [-85.332836, 44.812372],
          [-85.291716, 44.859608],
          [-84.847309, 44.858037],
          [-84.850929, 44.511069]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26081",
      state: "26",
      county: "081",
      name: "Kent County",
      censusArea: 846.948,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.790448, 43.293003],
          [-85.571636, 43.294252],
          [-85.562538, 43.294271],
          [-85.312376, 43.294205],
          [-85.312513, 43.118793],
          [-85.309626, 42.769878],
          [-85.545564, 42.768139],
          [-85.712659, 42.768237],
          [-85.782498, 42.7682],
          [-85.785276, 42.986911],
          [-85.788503, 43.030877],
          [-85.790662, 43.205167],
          [-85.790448, 43.293003]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26087",
      state: "26",
      county: "087",
      name: "Lapeer County",
      censusArea: 643.014,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.460733, 43.223131],
          [-83.351709, 43.235193],
          [-83.354418, 43.322257],
          [-83.120396, 43.327049],
          [-83.119027, 43.28368],
          [-83.00043, 43.284913],
          [-82.996257, 43.154099],
          [-82.983647, 42.893741],
          [-83.003, 42.893181],
          [-83.023573, 42.890543],
          [-83.102891, 42.888647],
          [-83.453364, 42.880432],
          [-83.460733, 43.223131]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26093",
      state: "26",
      county: "093",
      name: "Livingston County",
      censusArea: 565.254,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.686493, 42.783263],
          [-83.682727, 42.695582],
          [-83.664808, 42.431179],
          [-83.94465, 42.424149],
          [-84.131136, 42.424567],
          [-84.140713, 42.424611],
          [-84.151966, 42.710731],
          [-84.158189, 42.776639],
          [-83.922516, 42.780821],
          [-83.686493, 42.783263]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26095",
      state: "26",
      county: "095",
      name: "Luce County",
      censusArea: 899.078,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.864549, 46.690182],
          [-85.857755, 46.68981],
          [-85.85662, 46.689748],
          [-85.841057, 46.688896],
          [-85.840211, 46.688753],
          [-85.815747, 46.68461],
          [-85.796641, 46.681374],
          [-85.794923, 46.681083],
          [-85.792506, 46.68088],
          [-85.751345, 46.67743],
          [-85.750606, 46.677368],
          [-85.742834, 46.677322],
          [-85.739929, 46.677305],
          [-85.738624, 46.677298],
          [-85.714415, 46.677156],
          [-85.700945, 46.678114],
          [-85.674011, 46.68003],
          [-85.668753, 46.680404],
          [-85.663673, 46.680227],
          [-85.647387, 46.679658],
          [-85.624573, 46.678862],
          [-85.61747, 46.678054],
          [-85.595806, 46.67559],
          [-85.594158, 46.675402],
          [-85.593558, 46.675334],
          [-85.587741, 46.674672],
          [-85.587345, 46.674627],
          [-85.584989, 46.674608],
          [-85.558762, 46.674395],
          [-85.547593, 46.674304],
          [-85.542517, 46.674263],
          [-85.50951, 46.675786],
          [-85.482978, 46.680283],
          [-85.482096, 46.680432],
          [-85.369805, 46.713754],
          [-85.362572, 46.716548],
          [-85.347047, 46.722546],
          [-85.296395, 46.742114],
          [-85.289846, 46.744644],
          [-85.257999, 46.753078],
          [-85.25686, 46.75338],
          [-85.256031, 46.753481],
          [-85.237873, 46.755703],
          [-85.237839, 46.245421],
          [-85.865025, 46.244631],
          [-85.864932, 46.504758],
          [-85.864549, 46.690182]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26099",
      state: "26",
      county: "099",
      name: "Macomb County",
      censusArea: 479.223,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.083393, 42.447153],
          [-83.086882, 42.534792],
          [-83.088157, 42.549379],
          [-83.088776, 42.563843],
          [-83.099432, 42.80055],
          [-83.102891, 42.888647],
          [-83.023573, 42.890543],
          [-83.003, 42.893181],
          [-82.983647, 42.893741],
          [-82.73901, 42.897541],
          [-82.707841, 42.68351],
          [-82.726366, 42.682768],
          [-82.753317, 42.669732],
          [-82.765583, 42.655725],
          [-82.780817, 42.652232],
          [-82.792418, 42.655132],
          [-82.797318, 42.654032],
          [-82.813518, 42.640833],
          [-82.820118, 42.626333],
          [-82.819017, 42.616333],
          [-82.811017, 42.610933],
          [-82.789017, 42.603434],
          [-82.787573, 42.5983],
          [-82.788977, 42.592661],
          [-82.788116, 42.582835],
          [-82.781514, 42.571634],
          [-82.782414, 42.564834],
          [-82.784514, 42.563634],
          [-82.789114, 42.568434],
          [-82.796715, 42.571034],
          [-82.821016, 42.570734],
          [-82.834216, 42.567849],
          [-82.845916, 42.560634],
          [-82.849316, 42.555734],
          [-82.851016, 42.548935],
          [-82.859316, 42.541935],
          [-82.860213, 42.540842],
          [-82.874416, 42.523535],
          [-82.882316, 42.501035],
          [-82.883915, 42.471836],
          [-82.870572, 42.451235],
          [-83.083393, 42.447153]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26101",
      state: "26",
      county: "101",
      name: "Manistee County",
      censusArea: 542.149,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.386867, 44.178685],
          [-86.380188, 44.189272],
          [-86.380062, 44.189472],
          [-86.362847, 44.208113],
          [-86.354592, 44.223811],
          [-86.354313, 44.224342],
          [-86.351638, 44.229429],
          [-86.349294, 44.235459],
          [-86.343793, 44.249608],
          [-86.327287, 44.263057],
          [-86.326902, 44.263781],
          [-86.319063, 44.278503],
          [-86.318328, 44.279884],
          [-86.316651, 44.283034],
          [-86.316513, 44.283293],
          [-86.316025, 44.28421],
          [-86.313612, 44.287882],
          [-86.312774, 44.289158],
          [-86.311943, 44.290422],
          [-86.304691, 44.301459],
          [-86.304433, 44.301852],
          [-86.300264, 44.308197],
          [-86.26871, 44.345324],
          [-86.251926, 44.400984],
          [-86.248083, 44.420946],
          [-86.24832, 44.434758],
          [-86.251843, 44.451632],
          [-86.251605, 44.465443],
          [-86.248914, 44.483004],
          [-86.248897, 44.483024],
          [-86.248816, 44.483116],
          [-86.243745, 44.488929],
          [-86.238743, 44.501682],
          [-86.233503, 44.518278],
          [-85.817802, 44.512969],
          [-85.821186, 44.164197],
          [-86.041988, 44.167091],
          [-86.386867, 44.178685]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26115",
      state: "26",
      county: "115",
      name: "Monroe County",
      censusArea: 549.394,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.372198, 41.874122],
          [-83.372445, 41.874477],
          [-83.379705, 41.871729],
          [-83.381955, 41.870877],
          [-83.389289, 41.861668],
          [-83.393822, 41.855976],
          [-83.39622, 41.852965],
          [-83.40822, 41.832654],
          [-83.409596, 41.830325],
          [-83.422316, 41.822278],
          [-83.422391, 41.822255],
          [-83.425393, 41.821316],
          [-83.426321, 41.821026],
          [-83.431183, 41.819506],
          [-83.434204, 41.818562],
          [-83.435946, 41.816823],
          [-83.436298, 41.816471],
          [-83.439612, 41.813162],
          [-83.441668, 41.808646],
          [-83.442316, 41.80119],
          [-83.442521, 41.79883],
          [-83.442843, 41.795121],
          [-83.443364, 41.789118],
          [-83.437935, 41.771086],
          [-83.437516, 41.769694],
          [-83.437231, 41.76915],
          [-83.437197, 41.769085],
          [-83.435571, 41.765983],
          [-83.434238, 41.763439],
          [-83.432973, 41.761025],
          [-83.432832, 41.760756],
          [-83.432078, 41.759316],
          [-83.431951, 41.759074],
          [-83.431103, 41.757457],
          [-83.427377, 41.750346],
          [-83.427336, 41.750267],
          [-83.427308, 41.750214],
          [-83.42643, 41.747639],
          [-83.42418, 41.741042],
          [-83.424076, 41.740738],
          [-83.424155, 41.74071],
          [-83.43436, 41.737058],
          [-83.451897, 41.734486],
          [-83.453832, 41.732647],
          [-83.497733, 41.731847],
          [-83.499733, 41.731647],
          [-83.503433, 41.731547],
          [-83.504334, 41.731547],
          [-83.585235, 41.729348],
          [-83.593835, 41.729148],
          [-83.595235, 41.729148],
          [-83.636636, 41.727849],
          [-83.639636, 41.727749],
          [-83.665937, 41.726949],
          [-83.685337, 41.726449],
          [-83.763155, 41.72391],
          [-83.773922, 42.08243],
          [-83.539396, 42.085598],
          [-83.295275, 42.094234],
          [-83.185822, 42.029367],
          [-83.181475, 42.019301],
          [-83.187246, 42.007573],
          [-83.190535, 42.006172],
          [-83.208647, 42.00504],
          [-83.209379, 41.995736],
          [-83.216835, 41.98862],
          [-83.216897, 41.988561],
          [-83.223354, 41.989191],
          [-83.223369, 41.989185],
          [-83.224947, 41.988603],
          [-83.227744, 41.987571],
          [-83.22841, 41.987325],
          [-83.228502, 41.987291],
          [-83.228607, 41.987216],
          [-83.22929, 41.986725],
          [-83.248741, 41.972735],
          [-83.249204, 41.972402],
          [-83.249828, 41.971386],
          [-83.255123, 41.962759],
          [-83.256803, 41.960021],
          [-83.257009, 41.959686],
          [-83.257043, 41.958615],
          [-83.257143, 41.955438],
          [-83.257199, 41.95367],
          [-83.257292, 41.950745],
          [-83.269521, 41.939042],
          [-83.270491, 41.939337],
          [-83.28713, 41.944397],
          [-83.292761, 41.944616],
          [-83.293015, 41.944626],
          [-83.295982, 41.944742],
          [-83.29823, 41.9442],
          [-83.299467, 41.943902],
          [-83.302904, 41.943073],
          [-83.303465, 41.942762],
          [-83.305639, 41.941557],
          [-83.315859, 41.935893],
          [-83.326007, 41.924979],
          [-83.326024, 41.924961],
          [-83.326029, 41.924948],
          [-83.32706, 41.922554],
          [-83.330498, 41.914565],
          [-83.332998, 41.908757],
          [-83.333642, 41.907261],
          [-83.334173, 41.903247],
          [-83.334346, 41.901939],
          [-83.335103, 41.896209],
          [-83.335132, 41.895992],
          [-83.335658, 41.892009],
          [-83.335961, 41.889721],
          [-83.341557, 41.879956],
          [-83.359467, 41.867849],
          [-83.366187, 41.865505],
          [-83.372198, 41.874122]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28137",
      state: "28",
      county: "137",
      name: "Tate County",
      censusArea: 404.759,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.200199, 34.724418],
          [-90.128906, 34.760537],
          [-90.030051, 34.70795],
          [-89.912202, 34.772506],
          [-89.723635, 34.772121],
          [-89.722333, 34.685504],
          [-89.66916, 34.685786],
          [-89.668448, 34.554367],
          [-89.721341, 34.554274],
          [-90.198631, 34.554425],
          [-90.239971, 34.626237],
          [-90.200199, 34.724418]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28141",
      state: "28",
      county: "141",
      name: "Tishomingo County",
      censusArea: 424.254,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.258111, 34.995463],
          [-88.253825, 34.995553],
          [-88.200064, 34.995634],
          [-88.125038, 34.902227],
          [-88.097888, 34.892202],
          [-88.118407, 34.724292],
          [-88.138719, 34.589215],
          [-88.139246, 34.587795],
          [-88.139988, 34.581703],
          [-88.155305, 34.463087],
          [-88.329193, 34.463571],
          [-88.330699, 34.740721],
          [-88.365472, 34.755604],
          [-88.363531, 34.99559],
          [-88.258111, 34.995463]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28143",
      state: "28",
      county: "143",
      name: "Tunica County",
      censusArea: 454.668,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.293918, 34.860563],
          [-90.202126, 34.860374],
          [-90.200199, 34.724418],
          [-90.239971, 34.626237],
          [-90.198631, 34.554425],
          [-90.198536, 34.511088],
          [-90.304317, 34.509621],
          [-90.301851, 34.424546],
          [-90.399498, 34.423807],
          [-90.568867, 34.524584],
          [-90.570133, 34.524829],
          [-90.575786, 34.641749],
          [-90.576543, 34.657402],
          [-90.564033, 34.665742],
          [-90.549855, 34.662406],
          [-90.539013, 34.65907],
          [-90.521499, 34.66324],
          [-90.508989, 34.679086],
          [-90.509823, 34.689928],
          [-90.533175, 34.707442],
          [-90.554859, 34.727458],
          [-90.549855, 34.76332],
          [-90.537345, 34.78417],
          [-90.519831, 34.782502],
          [-90.501325, 34.769931],
          [-90.494811, 34.76749],
          [-90.479799, 34.769158],
          [-90.467289, 34.782502],
          [-90.478131, 34.832542],
          [-90.473961, 34.852558],
          [-90.461451, 34.856728],
          [-90.436431, 34.85506],
          [-90.423879, 34.834606],
          [-90.414864, 34.831846],
          [-90.407964, 34.832767],
          [-90.307384, 34.846195],
          [-90.293918, 34.860563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28153",
      state: "28",
      county: "153",
      name: "Wayne County",
      censusArea: 810.75,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.943355, 31.824565],
          [-88.932017, 31.825472],
          [-88.926058, 31.825658],
          [-88.910459, 31.826649],
          [-88.622394, 31.857193],
          [-88.46996, 31.893759],
          [-88.471214, 31.851385],
          [-88.471106, 31.850949],
          [-88.464428, 31.697952],
          [-88.464425, 31.697881],
          [-88.459722, 31.624002],
          [-88.459478, 31.621652],
          [-88.449446, 31.435837],
          [-88.841707, 31.433703],
          [-88.943468, 31.433458],
          [-88.943355, 31.824565]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28155",
      state: "28",
      county: "155",
      name: "Webster County",
      censusArea: 420.94,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.088446, 33.533202],
          [-89.105747, 33.532941],
          [-89.29614, 33.506734],
          [-89.381092, 33.460307],
          [-89.506496, 33.459806],
          [-89.506872, 33.677605],
          [-89.506927, 33.71398],
          [-89.507135, 33.721819],
          [-89.191924, 33.722158],
          [-89.191781, 33.738602],
          [-89.034498, 33.73943],
          [-89.018625, 33.593037],
          [-89.018613, 33.562114],
          [-89.088263, 33.562039],
          [-89.088446, 33.533202]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28157",
      state: "28",
      county: "157",
      name: "Wilkinson County",
      censusArea: 678.106,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.15815, 31.346695],
          [-91.095398, 31.320975],
          [-91.059431, 31.281858],
          [-91.060217, 30.998935],
          [-91.06827, 30.99892],
          [-91.080814, 30.998909],
          [-91.108114, 30.998857],
          [-91.108291, 30.99888],
          [-91.17614, 30.999144],
          [-91.176209, 30.999144],
          [-91.224068, 30.999183],
          [-91.224839, 30.999183],
          [-91.423621, 30.998984],
          [-91.425749, 30.999007],
          [-91.538727, 30.999388],
          [-91.625118, 30.999167],
          [-91.636942, 30.999416],
          [-91.58467, 31.020185],
          [-91.571695, 31.029782],
          [-91.562468, 31.043213],
          [-91.559907, 31.054119],
          [-91.561283, 31.060906],
          [-91.56415, 31.06683],
          [-91.577373, 31.078179],
          [-91.604197, 31.154545],
          [-91.599965, 31.192318],
          [-91.488954, 31.22592],
          [-91.468435, 31.31957],
          [-91.306294, 31.360862],
          [-91.15815, 31.346695]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28163",
      state: "28",
      county: "163",
      name: "Yazoo County",
      censusArea: 922.947,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.965882, 32.879573],
          [-90.049129, 32.735674],
          [-90.197729, 32.66385],
          [-90.278055, 32.660346],
          [-90.363868, 32.603268],
          [-90.450037, 32.573783],
          [-90.553821, 32.507574],
          [-90.654589, 32.546044],
          [-90.720102, 32.618858],
          [-90.697563, 32.625135],
          [-90.693011, 32.640113],
          [-90.712819, 32.642346],
          [-90.723159, 32.662128],
          [-90.664408, 32.689129],
          [-90.742634, 32.729225],
          [-90.694631, 32.815642],
          [-90.759753, 32.835763],
          [-90.656632, 32.92251],
          [-90.449893, 32.92329],
          [-90.449323, 33.00901],
          [-90.364031, 33.010596],
          [-89.965882, 32.879573]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29007",
      state: "29",
      county: "007",
      name: "Audrain County",
      censusArea: 692.235,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.314471, 39.246454],
          [-92.313836, 39.249986],
          [-92.310028, 39.347795],
          [-91.717968, 39.339122],
          [-91.718467, 39.324505],
          [-91.438235, 39.318716],
          [-91.409033, 39.142542],
          [-91.629598, 39.147853],
          [-91.633998, 39.059057],
          [-92.110387, 39.064204],
          [-92.104374, 39.239809],
          [-92.314471, 39.246454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29017",
      state: "29",
      county: "017",
      name: "Bollinger County",
      censusArea: 617.906,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.866627, 37.126227],
          [-89.959576, 37.127353],
          [-89.959196, 37.05438],
          [-90.111247, 37.041205],
          [-90.110354, 37.085871],
          [-90.22323, 37.086533],
          [-90.217657, 37.314967],
          [-90.145699, 37.312091],
          [-90.146763, 37.597434],
          [-89.861123, 37.599286],
          [-89.866627, 37.126227]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29023",
      state: "29",
      county: "023",
      name: "Butler County",
      censusArea: 694.669,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.678562, 36.92649],
          [-90.258563, 36.922043],
          [-90.204205, 36.772262],
          [-90.14002, 36.69676],
          [-90.148398, 36.630656],
          [-90.220732, 36.497858],
          [-90.228943, 36.497771],
          [-90.339892, 36.498213],
          [-90.494575, 36.498368],
          [-90.495027, 36.498371],
          [-90.50016, 36.498399],
          [-90.576112, 36.498446],
          [-90.57618, 36.498446],
          [-90.592166, 36.679368],
          [-90.628387, 36.679544],
          [-90.627517, 36.810954],
          [-90.662617, 36.811453],
          [-90.678562, 36.92649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29029",
      state: "29",
      county: "029",
      name: "Camden County",
      censusArea: 655.923,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.406872, 37.977802],
          [-92.409231, 37.85918],
          [-92.548934, 37.80047],
          [-92.620181, 37.892521],
          [-92.855384, 37.895736],
          [-93.072447, 37.902627],
          [-93.065199, 38.062479],
          [-93.077692, 38.263092],
          [-92.979558, 38.197734],
          [-92.764963, 38.189534],
          [-92.695886, 38.222031],
          [-92.604976, 38.156815],
          [-92.515979, 38.022336],
          [-92.406275, 38.021179],
          [-92.406872, 37.977802]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29035",
      state: "29",
      county: "035",
      name: "Carter County",
      censusArea: 507.361,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.678562, 36.92649],
          [-90.662617, 36.811453],
          [-91.115926, 36.823672],
          [-91.224825, 36.825679],
          [-91.22283, 36.883857],
          [-91.21715, 37.086197],
          [-91.017766, 37.095677],
          [-90.964466, 37.053584],
          [-90.779553, 37.050324],
          [-90.678562, 36.92649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29041",
      state: "29",
      county: "041",
      name: "Chariton County",
      censusArea: 751.181,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.266968, 39.703562],
          [-93.082799, 39.706709],
          [-92.85792, 39.699985],
          [-92.689331, 39.698094],
          [-92.692149, 39.610265],
          [-92.696592, 39.610371],
          [-92.707367, 39.321614],
          [-92.790369, 39.343586],
          [-92.849224, 39.226218],
          [-92.906142, 39.222339],
          [-92.959801, 39.312526],
          [-93.072915, 39.33552],
          [-93.104485, 39.383656],
          [-93.275932, 39.484371],
          [-93.278098, 39.615632],
          [-93.266968, 39.703562]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29045",
      state: "29",
      county: "045",
      name: "Clark County",
      censusArea: 504.692,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.950812, 40.25721],
          [-91.949747, 40.300832],
          [-91.943114, 40.605913],
          [-91.868401, 40.608059],
          [-91.832481, 40.609797],
          [-91.824826, 40.610191],
          [-91.813968, 40.610526],
          [-91.800133, 40.610953],
          [-91.795374, 40.611101],
          [-91.785916, 40.611488],
          [-91.729115, 40.61364],
          [-91.716769, 40.59853],
          [-91.696359, 40.588148],
          [-91.620071, 40.540817],
          [-91.618028, 40.53403],
          [-91.622362, 40.514362],
          [-91.619486, 40.507134],
          [-91.524612, 40.410765],
          [-91.490977, 40.393484],
          [-91.484507, 40.3839],
          [-91.419422, 40.378264],
          [-91.444833, 40.36317],
          [-91.46214, 40.342414],
          [-91.489868, 40.286048],
          [-91.501435, 40.248874],
          [-91.950812, 40.25721]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36041",
      state: "36",
      county: "041",
      name: "Hamilton County",
      censusArea: 1717.373,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.535156, 44.09925],
          [-74.28187, 44.120552],
          [-74.255998, 43.969797],
          [-74.336826, 43.925223],
          [-74.213734, 43.810875],
          [-74.047062, 43.796343],
          [-74.057005, 43.744513],
          [-74.214625, 43.728703],
          [-74.1601, 43.371532],
          [-74.140147, 43.253979],
          [-74.220902, 43.221403],
          [-74.326378, 43.241635],
          [-74.534657, 43.228115],
          [-74.712615, 43.286143],
          [-74.867712, 43.339826],
          [-74.775617, 43.486677],
          [-74.854171, 44.070089],
          [-74.535156, 44.09925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36045",
      state: "36",
      county: "045",
      name: "Jefferson County",
      censusArea: 1268.59,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.355679, 44.133258],
          [-76.312647, 44.199044],
          [-76.286547, 44.203773],
          [-76.245487, 44.203669],
          [-76.206777, 44.214543],
          [-76.191328, 44.221244],
          [-76.164265, 44.239603],
          [-76.118136, 44.29485],
          [-76.045228, 44.331724],
          [-76.000998, 44.347534],
          [-75.978281, 44.34688],
          [-75.970185, 44.342835],
          [-75.94954, 44.349129],
          [-75.912985, 44.368084],
          [-75.871496, 44.394839],
          [-75.86006, 44.403282],
          [-75.446124, 44.217655],
          [-75.545886, 44.102978],
          [-75.484528, 44.074172],
          [-75.542898, 43.967795],
          [-75.60367, 43.971363],
          [-75.758157, 43.878785],
          [-75.84056, 43.883976],
          [-75.850534, 43.791886],
          [-75.786759, 43.78832],
          [-75.774553, 43.688884],
          [-76.025087, 43.707018],
          [-76.022003, 43.668143],
          [-76.2005, 43.680231],
          [-76.205436, 43.718751],
          [-76.213205, 43.753513],
          [-76.229268, 43.804135],
          [-76.250135, 43.825713],
          [-76.266977, 43.838046],
          [-76.277812, 43.841205],
          [-76.283307, 43.843923],
          [-76.284481, 43.850968],
          [-76.28272, 43.858601],
          [-76.276262, 43.863297],
          [-76.269217, 43.868581],
          [-76.261584, 43.873278],
          [-76.249842, 43.875626],
          [-76.243384, 43.877975],
          [-76.234578, 43.877388],
          [-76.227485, 43.875061],
          [-76.219313, 43.86682],
          [-76.202257, 43.864898],
          [-76.192777, 43.869175],
          [-76.180604, 43.877529],
          [-76.158249, 43.887542],
          [-76.145506, 43.888681],
          [-76.133267, 43.892975],
          [-76.127285, 43.897889],
          [-76.125023, 43.912773],
          [-76.130446, 43.933082],
          [-76.133697, 43.940356],
          [-76.134359, 43.945614],
          [-76.134296, 43.954726],
          [-76.139086, 43.962111],
          [-76.146072, 43.964705],
          [-76.169802, 43.962202],
          [-76.184874, 43.971128],
          [-76.22805, 43.982737],
          [-76.236864, 43.9779],
          [-76.244439, 43.975803],
          [-76.252318, 43.975803],
          [-76.258306, 43.976118],
          [-76.264294, 43.978009],
          [-76.268706, 43.980846],
          [-76.268702, 43.987278],
          [-76.266733, 43.995578],
          [-76.269672, 44.001148],
          [-76.281928, 44.009177],
          [-76.287821, 44.01142],
          [-76.296755, 44.013307],
          [-76.298962, 44.017719],
          [-76.300222, 44.022762],
          [-76.299592, 44.030956],
          [-76.296986, 44.045455],
          [-76.300532, 44.057188],
          [-76.304207, 44.059445],
          [-76.360306, 44.070907],
          [-76.360798, 44.087644],
          [-76.366972, 44.100409],
          [-76.363835, 44.111696],
          [-76.358163, 44.123337],
          [-76.355679, 44.133258]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36051",
      state: "36",
      county: "051",
      name: "Livingston County",
      censusArea: 631.763,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.909832, 42.987762],
          [-77.730957, 42.988372],
          [-77.732378, 42.945285],
          [-77.580377, 42.943963],
          [-77.61167, 42.763169],
          [-77.598815, 42.671965],
          [-77.486875, 42.670279],
          [-77.490889, 42.577288],
          [-77.659917, 42.580409],
          [-77.722964, 42.471216],
          [-77.840231, 42.474576],
          [-77.840901, 42.517767],
          [-78.038261, 42.521522],
          [-78.048247, 42.579306],
          [-77.956334, 42.667322],
          [-77.954964, 42.862754],
          [-77.909832, 42.987762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36055",
      state: "36",
      county: "055",
      name: "Monroe County",
      censusArea: 657.205,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.995591, 43.365293],
          [-77.994838, 43.365259],
          [-77.976438, 43.369159],
          [-77.965238, 43.368059],
          [-77.952937, 43.36346],
          [-77.922736, 43.35696],
          [-77.904836, 43.35696],
          [-77.875335, 43.34966],
          [-77.816533, 43.34356],
          [-77.797381, 43.339857],
          [-77.785132, 43.339261],
          [-77.760231, 43.341161],
          [-77.756931, 43.337361],
          [-77.73063, 43.330161],
          [-77.714129, 43.323561],
          [-77.701429, 43.308261],
          [-77.660359, 43.282998],
          [-77.653759, 43.279484],
          [-77.628315, 43.271303],
          [-77.602161, 43.256949],
          [-77.577223, 43.243263],
          [-77.551022, 43.235763],
          [-77.534184, 43.234569],
          [-77.50092, 43.250363],
          [-77.476642, 43.254522],
          [-77.436831, 43.265701],
          [-77.414516, 43.269263],
          [-77.391015, 43.276363],
          [-77.385388, 43.276847],
          [-77.376038, 43.277652],
          [-77.374351, 43.152584],
          [-77.371478, 43.034696],
          [-77.485418, 43.034564],
          [-77.482517, 42.943164],
          [-77.580377, 42.943963],
          [-77.732378, 42.945285],
          [-77.730957, 42.988372],
          [-77.909832, 42.987762],
          [-77.951044, 43.039544],
          [-77.905934, 43.133561],
          [-77.99729, 43.132981],
          [-77.995723, 43.284963],
          [-77.995665, 43.287448],
          [-77.995591, 43.365293]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36059",
      state: "36",
      county: "059",
      name: "Nassau County",
      censusArea: 284.716,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.754323, 40.586357],
          [-73.700582, 40.743184],
          [-73.700872, 40.746866],
          [-73.701744, 40.75253],
          [-73.74676, 40.780382],
          [-73.768431, 40.800704],
          [-73.768301, 40.800797],
          [-73.754032, 40.820941],
          [-73.7544, 40.826837],
          [-73.728275, 40.8529],
          [-73.726675, 40.8568],
          [-73.730675, 40.8654],
          [-73.729575, 40.8665],
          [-73.713674, 40.870099],
          [-73.675573, 40.856999],
          [-73.670692, 40.858708],
          [-73.655872, 40.863899],
          [-73.654372, 40.878199],
          [-73.641072, 40.892599],
          [-73.633771, 40.898198],
          [-73.626972, 40.899397],
          [-73.617571, 40.897898],
          [-73.60187, 40.902798],
          [-73.59517, 40.907298],
          [-73.569969, 40.915398],
          [-73.566169, 40.915798],
          [-73.548068, 40.908698],
          [-73.519267, 40.914298],
          [-73.514999, 40.912821],
          [-73.499941, 40.918166],
          [-73.497061, 40.922801],
          [-73.454172, 40.834097],
          [-73.440967, 40.764399],
          [-73.424758, 40.679052],
          [-73.423269, 40.670893],
          [-73.424618, 40.666272],
          [-73.425586, 40.656291],
          [-73.423806, 40.609869],
          [-73.450369, 40.603501],
          [-73.562372, 40.583703],
          [-73.583773, 40.586703],
          [-73.610873, 40.587703],
          [-73.646674, 40.582804],
          [-73.701138, 40.58361],
          [-73.754776, 40.584404],
          [-73.754323, 40.586357]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36063",
      state: "36",
      county: "063",
      name: "Niagara County",
      censusArea: 522.359,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.828805, 43.030139],
          [-78.945262, 43.066956],
          [-79.009664, 43.069558],
          [-79.028353, 43.066897],
          [-79.028653, 43.069474],
          [-79.058399, 43.075231],
          [-79.066269, 43.09097],
          [-79.056767, 43.126855],
          [-79.044066, 43.138055],
          [-79.042366, 43.143655],
          [-79.058399, 43.238924],
          [-79.070469, 43.262454],
          [-79.019848, 43.273686],
          [-78.971866, 43.281254],
          [-78.930764, 43.293254],
          [-78.859362, 43.310955],
          [-78.836261, 43.318455],
          [-78.834061, 43.317555],
          [-78.777759, 43.327055],
          [-78.747158, 43.334555],
          [-78.696856, 43.341255],
          [-78.634346, 43.357624],
          [-78.547395, 43.369541],
          [-78.488857, 43.374763],
          [-78.482526, 43.374425],
          [-78.473099, 43.370812],
          [-78.465502, 43.371232],
          [-78.466429, 43.302775],
          [-78.460416, 43.216222],
          [-78.465505, 43.128619],
          [-78.464306, 43.091514],
          [-78.464449, 43.088703],
          [-78.733606, 43.084219],
          [-78.828805, 43.030139]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36071",
      state: "36",
      county: "071",
      name: "Orange County",
      censusArea: 811.686,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.953307, 41.589977],
          [-73.997609, 41.487212],
          [-74.000108, 41.456549],
          [-73.981486, 41.438905],
          [-73.981384, 41.324693],
          [-74.161789, 41.195794],
          [-74.234473, 41.142883],
          [-74.365664, 41.2034],
          [-74.392098, 41.215594],
          [-74.457584, 41.248225],
          [-74.694914, 41.357423],
          [-74.689767, 41.361558],
          [-74.691129, 41.367324],
          [-74.752562, 41.426518],
          [-74.75595, 41.426804],
          [-74.752399, 41.493743],
          [-74.475591, 41.504334],
          [-74.367055, 41.590977],
          [-74.264093, 41.632738],
          [-74.126393, 41.582544],
          [-73.953307, 41.589977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36073",
      state: "36",
      county: "073",
      name: "Orleans County",
      censusArea: 391.259,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.995665, 43.287448],
          [-77.995723, 43.284963],
          [-77.99729, 43.132981],
          [-78.410876, 43.130643],
          [-78.465505, 43.128619],
          [-78.460416, 43.216222],
          [-78.466429, 43.302775],
          [-78.465502, 43.371232],
          [-78.370221, 43.376505],
          [-78.358711, 43.373988],
          [-78.250641, 43.370866],
          [-78.233609, 43.36907],
          [-78.145195, 43.37551],
          [-78.104509, 43.375628],
          [-78.023609, 43.366575],
          [-77.995591, 43.365293],
          [-77.995665, 43.287448]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36075",
      state: "36",
      county: "075",
      name: "Oswego County",
      censusArea: 951.65,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.479224, 43.227519],
          [-76.605012, 43.25357],
          [-76.617109, 43.419137],
          [-76.607093, 43.423374],
          [-76.562826, 43.448537],
          [-76.53181, 43.460299],
          [-76.521999, 43.468617],
          [-76.515882, 43.471136],
          [-76.506858, 43.469127],
          [-76.486962, 43.47535],
          [-76.472498, 43.492781],
          [-76.437473, 43.509213],
          [-76.417581, 43.521285],
          [-76.410636, 43.523159],
          [-76.368849, 43.525822],
          [-76.345492, 43.513437],
          [-76.330911, 43.511978],
          [-76.297103, 43.51287],
          [-76.259858, 43.524728],
          [-76.235834, 43.529256],
          [-76.228701, 43.532987],
          [-76.217958, 43.545156],
          [-76.209853, 43.560136],
          [-76.203473, 43.574978],
          [-76.199138, 43.600454],
          [-76.196596, 43.649761],
          [-76.2005, 43.680231],
          [-76.022003, 43.668143],
          [-76.025087, 43.707018],
          [-75.774553, 43.688884],
          [-75.756213, 43.470388],
          [-75.814627, 43.483578],
          [-75.886756, 43.325021],
          [-75.884275, 43.155562],
          [-75.927453, 43.162738],
          [-75.932778, 43.176583],
          [-75.955599, 43.172182],
          [-75.959359, 43.180389],
          [-75.973438, 43.179898],
          [-75.979489, 43.177401],
          [-75.986894, 43.183938],
          [-75.992428, 43.185164],
          [-75.993394, 43.183376],
          [-76.202944, 43.271605],
          [-76.258951, 43.237468],
          [-76.479224, 43.227519]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48435",
      state: "48",
      county: "435",
      name: "Sutton County",
      censusArea: 1453.926,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.960587, 30.706071],
          [-100.849229, 30.706222],
          [-100.116234, 30.710366],
          [-100.116461, 30.290296],
          [-100.381329, 30.288984],
          [-100.700393, 30.288276],
          [-100.960643, 30.287776],
          [-100.960587, 30.706071]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48437",
      state: "48",
      county: "437",
      name: "Swisher County",
      censusArea: 890.159,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.47158, 34.31229],
          [-101.564856, 34.312493],
          [-101.99802, 34.313039],
          [-101.998493, 34.74819],
          [-101.629257, 34.747649],
          [-101.471562, 34.747462],
          [-101.47158, 34.31229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48441",
      state: "48",
      county: "441",
      name: "Taylor County",
      censusArea: 915.552,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.631413, 32.081274],
          [-99.713971, 32.082089],
          [-100.151911, 32.082638],
          [-100.146543, 32.522793],
          [-99.629639, 32.520713],
          [-99.629579, 32.514657],
          [-99.631413, 32.081274]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48447",
      state: "48",
      county: "447",
      name: "Throckmorton County",
      censusArea: 912.552,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.472444, 33.399023],
          [-98.953938, 33.397534],
          [-98.950875, 32.956918],
          [-99.096016, 32.957037],
          [-99.471258, 32.957025],
          [-99.472444, 33.399023]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48451",
      state: "48",
      county: "451",
      name: "Tom Green County",
      censusArea: 1521.972,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.688764, 31.086576],
          [-100.693065, 31.523954],
          [-101.223243, 31.528661],
          [-101.223944, 31.530035],
          [-101.267947, 31.528688],
          [-101.26763, 31.556462],
          [-100.865256, 31.562294],
          [-100.861099, 31.705418],
          [-100.82537, 31.696155],
          [-100.235761, 31.69297],
          [-100.234636, 31.587147],
          [-100.111234, 31.580267],
          [-100.115216, 31.087994],
          [-100.688764, 31.086576]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48453",
      state: "48",
      county: "453",
      name: "Travis County",
      censusArea: 990.202,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.409875, 30.351329],
          [-97.486329, 30.220528],
          [-97.492679, 30.209946],
          [-97.649449, 30.067866],
          [-97.655257, 30.072907],
          [-97.710215, 30.024499],
          [-98.011253, 30.242288],
          [-98.172977, 30.356312],
          [-98.125556, 30.426186],
          [-98.123111, 30.484723],
          [-98.049886, 30.624155],
          [-97.956734, 30.628249],
          [-97.775211, 30.429993],
          [-97.576167, 30.501799],
          [-97.369539, 30.419563],
          [-97.377227, 30.407086],
          [-97.409875, 30.351329]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48457",
      state: "48",
      county: "457",
      name: "Tyler County",
      censusArea: 924.501,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.545717, 30.526977],
          [-94.657992, 31.012007],
          [-94.561943, 31.058952],
          [-94.45782, 31.033326],
          [-94.451127, 31.003445],
          [-94.353782, 31.039371],
          [-94.238424, 31.013705],
          [-94.243766, 30.983554],
          [-94.179343, 30.794974],
          [-94.150119, 30.790203],
          [-94.125824, 30.684052],
          [-94.05096, 30.638568],
          [-94.094812, 30.575543],
          [-94.071689, 30.526033],
          [-94.545717, 30.526977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48467",
      state: "48",
      county: "467",
      name: "Van Zandt County",
      censusArea: 842.557,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.075899, 32.357505],
          [-96.076685, 32.836799],
          [-96.076801, 32.838489],
          [-95.934433, 32.837217],
          [-95.829581, 32.766967],
          [-95.635017, 32.72038],
          [-95.626279, 32.708703],
          [-95.615764, 32.708095],
          [-95.60546, 32.702416],
          [-95.604027, 32.696294],
          [-95.59323, 32.693011],
          [-95.594541, 32.687026],
          [-95.5943, 32.479858],
          [-95.449165, 32.355233],
          [-96.075899, 32.357505]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48469",
      state: "48",
      county: "469",
      name: "Victoria County",
      censusArea: 882.143,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.645867, 28.710457],
          [-96.648758, 28.709627],
          [-96.655528, 28.704167],
          [-96.853757, 28.635199],
          [-96.930367, 28.586728],
          [-96.890951, 28.507178],
          [-96.982147, 28.499639],
          [-97.048529, 28.549976],
          [-97.160751, 28.553475],
          [-97.182359, 28.610602],
          [-97.158933, 28.776156],
          [-97.231971, 28.865303],
          [-97.305916, 28.864037],
          [-96.976378, 29.104046],
          [-96.947959, 29.073335],
          [-96.947333, 29.072716],
          [-96.946942, 29.072235],
          [-96.938587, 29.063244],
          [-96.832103, 29.021299],
          [-96.792133, 28.918803],
          [-96.711514, 28.851411],
          [-96.645867, 28.710457]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48477",
      state: "48",
      county: "477",
      name: "Washington County",
      censusArea: 603.954,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.640883, 30.296786],
          [-96.36919, 30.365072],
          [-96.358976, 30.372637],
          [-96.340485, 30.367925],
          [-96.312795, 30.3843],
          [-96.299173, 30.379152],
          [-96.199544, 30.38291],
          [-96.15463, 30.330288],
          [-96.085893, 30.288983],
          [-96.093165, 30.225187],
          [-96.15901, 30.206613],
          [-96.191418, 30.134669],
          [-96.146052, 30.070224],
          [-96.292849, 30.09615],
          [-96.62198, 30.044283],
          [-96.647223, 30.145174],
          [-96.794552, 30.160545],
          [-96.731645, 30.243886],
          [-96.640883, 30.296786]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48483",
      state: "48",
      county: "483",
      name: "Wheeler County",
      censusArea: 914.523,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.000385, 35.182702],
          [-100.538978, 35.183144],
          [-100.540158, 35.619296],
          [-100.000392, 35.619115],
          [-100.000389, 35.422364],
          [-100.000385, 35.182702]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48485",
      state: "48",
      county: "485",
      name: "Wichita County",
      censusArea: 627.776,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.423577, 33.836047],
          [-98.742358, 33.834552],
          [-98.953088, 33.834],
          [-98.952527, 34.195621],
          [-98.920042, 34.182908],
          [-98.858419, 34.152732],
          [-98.76557, 34.136376],
          [-98.700182, 34.135995],
          [-98.665824, 34.151617],
          [-98.652347, 34.161029],
          [-98.637575, 34.162284],
          [-98.610112, 34.161039],
          [-98.599789, 34.160571],
          [-98.577356, 34.1491],
          [-98.553717, 34.13366],
          [-98.504182, 34.072371],
          [-98.475066, 34.064269],
          [-98.433769, 34.096116],
          [-98.423577, 33.836047]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48489",
      state: "48",
      county: "489",
      name: "Willacy County",
      censusArea: 590.554,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.790358, 26.337438],
          [-97.791666, 26.337715],
          [-97.798373, 26.338549],
          [-97.799073, 26.338749],
          [-97.800874, 26.338949],
          [-97.861875, 26.348156],
          [-97.86192, 26.433579],
          [-98.004189, 26.448784],
          [-97.957405, 26.611769],
          [-97.861688, 26.597995],
          [-97.441206, 26.599901],
          [-97.428151, 26.572466],
          [-97.416955, 26.553637],
          [-97.422299, 26.520303],
          [-97.425861, 26.516741],
          [-97.430696, 26.506563],
          [-97.430696, 26.494603],
          [-97.42637, 26.484425],
          [-97.429169, 26.478064],
          [-97.43553, 26.470176],
          [-97.441383, 26.466614],
          [-97.441383, 26.455418],
          [-97.437566, 26.44982],
          [-97.425861, 26.446003],
          [-97.421026, 26.446766],
          [-97.41721, 26.44982],
          [-97.411612, 26.447275],
          [-97.412884, 26.433026],
          [-97.42179, 26.417249],
          [-97.4195, 26.413178],
          [-97.406014, 26.409107],
          [-97.398126, 26.410888],
          [-97.394309, 26.41445],
          [-97.395072, 26.417249],
          [-97.382485, 26.411326],
          [-97.446987, 26.324947],
          [-97.527582, 26.299304],
          [-97.790358, 26.337438]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48499",
      state: "48",
      county: "499",
      name: "Wood County",
      censusArea: 645.234,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.15341, 32.570115],
          [-95.167523, 32.543957],
          [-95.332164, 32.604252],
          [-95.47297, 32.606426],
          [-95.594541, 32.687026],
          [-95.59323, 32.693011],
          [-95.604027, 32.696294],
          [-95.60546, 32.702416],
          [-95.615764, 32.708095],
          [-95.626279, 32.708703],
          [-95.635017, 32.72038],
          [-95.665389, 32.960434],
          [-95.308957, 32.962572],
          [-95.172947, 32.961829],
          [-95.152206, 33.01345],
          [-95.15211, 32.902641],
          [-95.15341, 32.570115]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49001",
      state: "49",
      county: "001",
      name: "Beaver County",
      censusArea: 2589.88,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.518504, 38.509852],
          [-112.335369, 38.332955],
          [-112.356853, 38.22475],
          [-112.443821, 38.149942],
          [-112.462139, 38.149912],
          [-112.478682, 38.147334],
          [-113.4716, 38.148665],
          [-114.049903, 38.148601],
          [-114.05012, 38.404536],
          [-114.050091, 38.404673],
          [-114.049834, 38.543784],
          [-114.049862, 38.547764],
          [-114.050154, 38.57292],
          [-112.919362, 38.576529],
          [-112.919308, 38.574709],
          [-112.515188, 38.573703],
          [-112.515196, 38.572325],
          [-112.518896, 38.567408],
          [-112.515673, 38.556623],
          [-112.517822, 38.553982],
          [-112.515965, 38.529047],
          [-112.522426, 38.519026],
          [-112.518504, 38.509852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49007",
      state: "49",
      county: "007",
      name: "Carbon County",
      censusArea: 1478.493,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.967601, 39.805979],
          [-109.884708, 39.805742],
          [-109.993741, 39.70278],
          [-110.037952, 39.566667],
          [-110.024118, 39.468951],
          [-111.0812, 39.467533],
          [-111.131953, 39.613844],
          [-111.159113, 39.595947],
          [-111.247388, 39.704611],
          [-111.247502, 39.813269],
          [-110.857902, 39.812428],
          [-109.967601, 39.805979]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26071",
      state: "26",
      county: "071",
      name: "Iron County",
      censusArea: 1166.149,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.932558, 46.073601],
          [-88.943279, 46.077943],
          [-88.948698, 46.080205],
          [-88.990807, 46.097298],
          [-88.990885, 46.09733],
          [-88.990588, 46.333251],
          [-88.991236, 46.419704],
          [-88.679392, 46.420139],
          [-88.116571, 46.419951],
          [-88.117407, 46.246618],
          [-88.116845, 45.921627],
          [-88.118516, 45.918562],
          [-88.139372, 45.929932],
          [-88.175532, 45.944897],
          [-88.202247, 45.949584],
          [-88.211158, 45.944531],
          [-88.239672, 45.948982],
          [-88.244452, 45.952142],
          [-88.250133, 45.963147],
          [-88.295559, 45.951459],
          [-88.376447, 45.989458],
          [-88.423437, 45.98193],
          [-88.489608, 45.991381],
          [-88.497417, 45.995149],
          [-88.500133, 46.000457],
          [-88.507188, 46.0183],
          [-88.532414, 46.021212],
          [-88.613063, 45.990627],
          [-88.616405, 45.9877],
          [-88.6375, 45.98496],
          [-88.666056, 45.988191],
          [-88.670939, 45.999957],
          [-88.670115, 45.999957],
          [-88.674606, 46.010567],
          [-88.679132, 46.013538],
          [-88.683198, 46.014563],
          [-88.730675, 46.026535],
          [-88.739994, 46.027308],
          [-88.769712, 46.018968],
          [-88.815629, 46.02232],
          [-88.840584, 46.031112],
          [-88.848464, 46.038858],
          [-88.85027, 46.040274],
          [-88.932558, 46.073601]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26125",
      state: "26",
      county: "125",
      name: "Oakland County",
      censusArea: 867.663,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.316825, 42.442033],
          [-83.433672, 42.438942],
          [-83.551907, 42.435166],
          [-83.664808, 42.431179],
          [-83.682727, 42.695582],
          [-83.686493, 42.783263],
          [-83.689384, 42.871263],
          [-83.453364, 42.880432],
          [-83.102891, 42.888647],
          [-83.099432, 42.80055],
          [-83.088776, 42.563843],
          [-83.088157, 42.549379],
          [-83.086882, 42.534792],
          [-83.083393, 42.447153],
          [-83.287959, 42.44268],
          [-83.316825, 42.442033]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26131",
      state: "26",
      county: "131",
      name: "Ontonagon County",
      censusArea: 1311.225,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.168493, 46.965279],
          [-89.168244, 46.965536],
          [-89.142595, 46.984859],
          [-89.142425, 46.984954],
          [-89.142282, 46.985033],
          [-89.128698, 46.992599],
          [-89.128101, 46.992692],
          [-89.124799, 46.993209],
          [-89.118339, 46.99422],
          [-89.118106, 46.994002],
          [-89.115903, 46.991933],
          [-89.113158, 46.989356],
          [-89.106277, 46.98648],
          [-89.086742, 46.985298],
          [-89.063103, 46.988522],
          [-89.058591, 46.990604],
          [-89.048164, 46.995416],
          [-89.03949, 46.999419],
          [-89.038116, 46.999643],
          [-89.02893, 47.00114],
          [-89.027648, 46.999839],
          [-89.022994, 46.99512],
          [-88.998907, 46.99531],
          [-88.998417, 46.995314],
          [-88.992399, 46.996347],
          [-88.99172, 46.996463],
          [-88.987197, 46.997239],
          [-88.982483, 46.99883],
          [-88.978813, 47.000068],
          [-88.978433, 47.000196],
          [-88.972802, 47.002096],
          [-88.960337, 47.008053],
          [-88.959409, 47.008496],
          [-88.957276, 47.010111],
          [-88.944045, 47.020129],
          [-88.933329, 47.0322],
          [-88.93248, 46.764787],
          [-88.863906, 46.765013],
          [-88.864474, 46.679245],
          [-88.989529, 46.680119],
          [-88.991236, 46.419704],
          [-88.990588, 46.333251],
          [-89.365408, 46.332963],
          [-89.365347, 46.505645],
          [-89.74146, 46.505247],
          [-89.74026, 46.592147],
          [-89.864481, 46.592765],
          [-89.887721, 46.765825],
          [-89.888687, 46.765826],
          [-89.875069, 46.775991],
          [-89.862544, 46.785341],
          [-89.851966, 46.793237],
          [-89.850979, 46.793974],
          [-89.848652, 46.795711],
          [-89.846962, 46.796556],
          [-89.844691, 46.79769],
          [-89.841453, 46.799308],
          [-89.831956, 46.804053],
          [-89.830863, 46.804434],
          [-89.830093, 46.804703],
          [-89.828129, 46.805389],
          [-89.790663, 46.818469],
          [-89.788353, 46.818861],
          [-89.78708, 46.819077],
          [-89.757585, 46.824082],
          [-89.724471, 46.829701],
          [-89.720277, 46.830413],
          [-89.717848, 46.830559],
          [-89.713354, 46.830829],
          [-89.710062, 46.831026],
          [-89.708873, 46.831098],
          [-89.708303, 46.831132],
          [-89.708084, 46.831145],
          [-89.678469, 46.832923],
          [-89.677771, 46.832965],
          [-89.676878, 46.833019],
          [-89.673375, 46.833229],
          [-89.660625, 46.831056],
          [-89.659681, 46.830762],
          [-89.651795, 46.828309],
          [-89.646854, 46.826771],
          [-89.64344, 46.825709],
          [-89.642255, 46.82534],
          [-89.637309, 46.821384],
          [-89.634938, 46.819488],
          [-89.619329, 46.81889],
          [-89.598527, 46.824338],
          [-89.578217, 46.829657],
          [-89.572423, 46.831174],
          [-89.570326, 46.831723],
          [-89.569808, 46.831859],
          [-89.564509, 46.832483],
          [-89.542408, 46.835086],
          [-89.540709, 46.835286],
          [-89.538927, 46.835496],
          [-89.535683, 46.835878],
          [-89.534331, 46.836248],
          [-89.525656, 46.838625],
          [-89.516895, 46.841025],
          [-89.515665, 46.841362],
          [-89.513938, 46.841835],
          [-89.500227, 46.841638],
          [-89.499248, 46.841623],
          [-89.49908, 46.841621],
          [-89.49776, 46.841086],
          [-89.494019, 46.83957],
          [-89.491307, 46.83847],
          [-89.491252, 46.838448],
          [-89.491079, 46.838438],
          [-89.485166, 46.838112],
          [-89.475333, 46.837569],
          [-89.471849, 46.837376],
          [-89.47154, 46.837359],
          [-89.470915, 46.837398],
          [-89.470619, 46.837416],
          [-89.470547, 46.837421],
          [-89.469651, 46.837477],
          [-89.469446, 46.83749],
          [-89.453185, 46.838505],
          [-89.448454, 46.8388],
          [-89.448445, 46.838801],
          [-89.446583, 46.838917],
          [-89.445969, 46.838955],
          [-89.445313, 46.838996],
          [-89.440145, 46.839319],
          [-89.437047, 46.839512],
          [-89.434016, 46.840131],
          [-89.415154, 46.843983],
          [-89.413413, 46.844524],
          [-89.401123, 46.848344],
          [-89.395386, 46.850127],
          [-89.372032, 46.857386],
          [-89.370788, 46.857851],
          [-89.33613, 46.870807],
          [-89.317396, 46.877811],
          [-89.304623, 46.882586],
          [-89.28578, 46.88963],
          [-89.278612, 46.89231],
          [-89.249938, 46.903029],
          [-89.249143, 46.903326],
          [-89.228362, 46.912751],
          [-89.227914, 46.912954],
          [-89.226244, 46.914105],
          [-89.202811, 46.930253],
          [-89.201511, 46.931149],
          [-89.199648, 46.933075],
          [-89.18373, 46.949529],
          [-89.168493, 46.965279]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26135",
      state: "26",
      county: "135",
      name: "Oscoda County",
      censusArea: 565.731,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.371757, 44.855029],
          [-83.888484, 44.856458],
          [-83.886634, 44.508973],
          [-84.370643, 44.507223],
          [-84.371757, 44.855029]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26145",
      state: "26",
      county: "145",
      name: "Saginaw County",
      censusArea: 800.114,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.05, 43.567324],
          [-84.050987, 43.524064],
          [-83.916091, 43.522629],
          [-83.817678, 43.479052],
          [-83.698816, 43.478957],
          [-83.695621, 43.221422],
          [-83.696516, 43.221396],
          [-83.814494, 43.22121],
          [-83.932071, 43.220377],
          [-83.929079, 43.132782],
          [-84.367891, 43.128452],
          [-84.369876, 43.466044],
          [-84.170576, 43.481969],
          [-84.168127, 43.568899],
          [-84.05, 43.567324]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26147",
      state: "26",
      county: "147",
      name: "St. Clair County",
      censusArea: 721.17,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.503157, 43.168946],
          [-82.50299, 43.168137],
          [-82.501656, 43.161656],
          [-82.501529, 43.161351],
          [-82.500061, 43.157827],
          [-82.495685, 43.147316],
          [-82.494194, 43.143736],
          [-82.494072, 43.142864],
          [-82.494052, 43.142722],
          [-82.494014, 43.142453],
          [-82.493977, 43.142186],
          [-82.493347, 43.137685],
          [-82.493021, 43.135363],
          [-82.490979, 43.120782],
          [-82.490634, 43.118314],
          [-82.490614, 43.118172],
          [-82.490494, 43.117759],
          [-82.486684, 43.104688],
          [-82.486042, 43.102486],
          [-82.471053, 43.087581],
          [-82.457319, 43.06147],
          [-82.457221, 43.061285],
          [-82.450724, 43.051229],
          [-82.443433, 43.039942],
          [-82.422768, 43.007956],
          [-82.415937, 43.005555],
          [-82.447778, 42.937066],
          [-82.455027, 42.926866],
          [-82.469912, 42.887459],
          [-82.483697, 42.733212],
          [-82.509935, 42.637294],
          [-82.518782, 42.613888],
          [-82.523337, 42.607486],
          [-82.583996, 42.554041],
          [-82.589779, 42.550678],
          [-82.607068, 42.548843],
          [-82.624907, 42.557229],
          [-82.679059, 42.52221],
          [-82.686417, 42.518597],
          [-82.685397, 42.528659],
          [-82.679522, 42.53552],
          [-82.670956, 42.537989],
          [-82.664335, 42.546244],
          [-82.680758, 42.557909],
          [-82.681036, 42.574695],
          [-82.688061, 42.588417],
          [-82.701152, 42.585991],
          [-82.711151, 42.590884],
          [-82.713042, 42.597904],
          [-82.700818, 42.606687],
          [-82.683482, 42.609433],
          [-82.681593, 42.618672],
          [-82.690124, 42.625033],
          [-82.689836, 42.627148],
          [-82.669103, 42.637225],
          [-82.645715, 42.631145],
          [-82.630922, 42.64211],
          [-82.626396, 42.647385],
          [-82.623043, 42.655951],
          [-82.623797, 42.665395],
          [-82.630851, 42.673341],
          [-82.635262, 42.675552],
          [-82.659781, 42.678618],
          [-82.674287, 42.687049],
          [-82.6855, 42.690036],
          [-82.700964, 42.689548],
          [-82.706135, 42.683578],
          [-82.707841, 42.68351],
          [-82.73901, 42.897541],
          [-82.983647, 42.893741],
          [-82.996257, 43.154099],
          [-82.503157, 43.168946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26157",
      state: "26",
      county: "157",
      name: "Tuscola County",
      censusArea: 803.127,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.695621, 43.221422],
          [-83.698816, 43.478957],
          [-83.699368, 43.595314],
          [-83.699253, 43.596792],
          [-83.669795, 43.59079],
          [-83.666052, 43.591292],
          [-83.654192, 43.59929],
          [-83.618602, 43.628891],
          [-83.595579, 43.650249],
          [-83.563157, 43.684564],
          [-83.553707, 43.685432],
          [-83.549044, 43.693798],
          [-83.55147, 43.699901],
          [-83.540187, 43.708746],
          [-83.524837, 43.716948],
          [-83.515853, 43.718157],
          [-83.513461, 43.714607],
          [-83.506657, 43.710907],
          [-83.48007, 43.714636],
          [-83.470053, 43.723418],
          [-83.467429, 43.72892],
          [-83.466592, 43.667621],
          [-83.117844, 43.675493],
          [-83.103826, 43.327365],
          [-83.120396, 43.327049],
          [-83.354418, 43.322257],
          [-83.351709, 43.235193],
          [-83.460733, 43.223131],
          [-83.695621, 43.221422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29047",
      state: "29",
      county: "047",
      name: "Clay County",
      censusArea: 397.298,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.335213, 39.183337],
          [-94.389728, 39.193503],
          [-94.46159, 39.120372],
          [-94.523463, 39.148771],
          [-94.607354, 39.113444],
          [-94.589933, 39.140403],
          [-94.591933, 39.155003],
          [-94.596033, 39.157703],
          [-94.601733, 39.159603],
          [-94.600819, 39.456155],
          [-94.597879, 39.455838],
          [-94.210642, 39.454682],
          [-94.212515, 39.207086],
          [-94.289873, 39.237107],
          [-94.335213, 39.183337]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29051",
      state: "29",
      county: "051",
      name: "Cole County",
      censusArea: 393.75,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.194476, 38.335536],
          [-92.408407, 38.341344],
          [-92.403484, 38.425331],
          [-92.495185, 38.427829],
          [-92.394866, 38.73913],
          [-92.350605, 38.676398],
          [-92.220661, 38.643167],
          [-92.161663, 38.578057],
          [-92.065233, 38.557079],
          [-91.944238, 38.596925],
          [-92.025857, 38.552017],
          [-92.043489, 38.466479],
          [-92.168883, 38.466812],
          [-92.114977, 38.401937],
          [-92.194476, 38.335536]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29055",
      state: "29",
      county: "055",
      name: "Crawford County",
      censusArea: 742.517,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.100017, 37.740012],
          [-91.146521, 37.740811],
          [-91.153345, 37.69734],
          [-91.310655, 37.700048],
          [-91.308311, 37.787186],
          [-91.528797, 37.788989],
          [-91.525025, 38.152433],
          [-91.534384, 38.152593],
          [-91.535333, 38.210975],
          [-91.367482, 38.209741],
          [-91.095765, 38.204083],
          [-91.100017, 37.740012]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29057",
      state: "29",
      county: "057",
      name: "Dade County",
      censusArea: 490.012,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.616033, 37.572689],
          [-93.620379, 37.456289],
          [-93.621153, 37.427423],
          [-93.625844, 37.282011],
          [-94.052313, 37.290078],
          [-94.083833, 37.290851],
          [-94.082083, 37.349291],
          [-94.075385, 37.58157],
          [-93.616033, 37.572689]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29061",
      state: "29",
      county: "061",
      name: "Daviess County",
      censusArea: 563.24,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.216696, 40.136626],
          [-93.764021, 40.132909],
          [-93.761973, 39.959552],
          [-93.758992, 39.784674],
          [-94.205844, 39.788954],
          [-94.203457, 40.034539],
          [-94.2188, 40.034885],
          [-94.216494, 40.12503],
          [-94.216696, 40.136626]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29067",
      state: "29",
      county: "067",
      name: "Douglas County",
      censusArea: 813.632,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.251359, 37.059653],
          [-92.25016, 37.059753],
          [-92.088708, 37.056248],
          [-92.095982, 36.794011],
          [-92.112346, 36.794231],
          [-92.764869, 36.806097],
          [-92.909336, 36.809178],
          [-92.903273, 37.070651],
          [-92.685867, 37.067051],
          [-92.251359, 37.059653]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29073",
      state: "29",
      county: "073",
      name: "Gasconade County",
      censusArea: 517.803,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.367482, 38.209741],
          [-91.535333, 38.210975],
          [-91.534384, 38.152593],
          [-91.538016, 38.15249],
          [-91.580252, 38.152793],
          [-91.589527, 38.153249],
          [-91.630554, 38.153519],
          [-91.644893, 38.153679],
          [-91.644729, 38.28851],
          [-91.640372, 38.703792],
          [-91.545313, 38.676882],
          [-91.418637, 38.709778],
          [-91.395134, 38.70799],
          [-91.369192, 38.699324],
          [-91.367482, 38.209741]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29077",
      state: "29",
      county: "077",
      name: "Greene County",
      censusArea: 675.304,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.07338, 37.414989],
          [-93.065274, 37.088694],
          [-93.608899, 37.098153],
          [-93.605095, 37.281482],
          [-93.625844, 37.282011],
          [-93.621153, 37.427423],
          [-93.182489, 37.417204],
          [-93.158287, 37.416799],
          [-93.07338, 37.414989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29079",
      state: "29",
      county: "079",
      name: "Grundy County",
      censusArea: 435.276,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.763324, 40.263988],
          [-93.367214, 40.266314],
          [-93.366556, 40.033099],
          [-93.361951, 40.033098],
          [-93.361957, 39.967606],
          [-93.761973, 39.959552],
          [-93.764021, 40.132909],
          [-93.763324, 40.263988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29087",
      state: "29",
      county: "087",
      name: "Holt County",
      censusArea: 462.693,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.552473, 40.261904],
          [-95.552314, 40.264443],
          [-95.180614, 40.261706],
          [-95.071703, 40.260859],
          [-95.04308, 40.130096],
          [-94.993348, 39.900747],
          [-95.00844, 39.900596],
          [-95.01632, 39.898766],
          [-95.02524, 39.8897],
          [-95.02525, 39.877688],
          [-95.027931, 39.871522],
          [-95.035047, 39.866807],
          [-95.042142, 39.864805],
          [-95.085003, 39.861883],
          [-95.107745, 39.868579],
          [-95.132695, 39.875925],
          [-95.181222, 39.899996],
          [-95.301549, 39.983603],
          [-95.30778, 39.990618],
          [-95.30829, 39.999998],
          [-95.312211, 40.009395],
          [-95.521925, 40.24947],
          [-95.552473, 40.261904]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37011",
      state: "37",
      county: "011",
      name: "Avery County",
      censusArea: 247.087,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.981723, 35.91152],
          [-82.034192, 35.942107],
          [-82.080518, 36.10571],
          [-82.080143, 36.10572],
          [-82.02664, 36.130222],
          [-81.960101, 36.228131],
          [-81.932994, 36.264881],
          [-81.918113, 36.28711],
          [-81.909121, 36.216374],
          [-81.81152, 36.111544],
          [-81.735974, 36.066826],
          [-81.80712, 35.962057],
          [-81.899583, 36.000127],
          [-81.943674, 35.959448],
          [-81.962957, 35.941669],
          [-81.954501, 35.926742],
          [-81.981723, 35.91152]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37013",
      state: "37",
      county: "013",
      name: "Beaufort County",
      censusArea: 827.192,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.190423, 35.418955],
          [-77.189107, 35.426036],
          [-77.19123, 35.434744],
          [-77.181276, 35.440975],
          [-77.179935, 35.455811],
          [-77.17394, 35.456715],
          [-77.175418, 35.474071],
          [-77.087521, 35.564047],
          [-77.173788, 35.635336],
          [-77.174034, 35.733026],
          [-76.985749, 35.658485],
          [-76.845119, 35.705409],
          [-76.638031, 35.705233],
          [-76.564128, 35.601054],
          [-76.467761, 35.556504],
          [-76.599085, 35.51996],
          [-76.546901, 35.389673],
          [-76.580187, 35.387113],
          [-76.606041, 35.387113],
          [-76.620654, 35.37812],
          [-76.628125, 35.368515],
          [-76.628523, 35.368003],
          [-76.61953, 35.345521],
          [-76.604962, 35.337751],
          [-76.632535, 35.235233],
          [-76.845457, 35.216045],
          [-76.895546, 35.253532],
          [-77.190423, 35.418955]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37019",
      state: "37",
      county: "019",
      name: "Brunswick County",
      censusArea: 846.973,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.937313, 33.928645],
          [-77.946568, 33.912261],
          [-77.956881, 33.87779],
          [-77.960172, 33.853315],
          [-77.970606, 33.844517],
          [-78.006765, 33.858704],
          [-78.009973, 33.861406],
          [-78.009426, 33.867823],
          [-78.018689, 33.888289],
          [-78.095429, 33.906031],
          [-78.136952, 33.912178],
          [-78.17772, 33.914272],
          [-78.276147, 33.912364],
          [-78.383964, 33.901946],
          [-78.509042, 33.865515],
          [-78.541087, 33.851112],
          [-78.6505, 33.944035],
          [-78.55685, 34.062377],
          [-78.545218, 34.145747],
          [-78.494928, 34.166772],
          [-78.422567, 34.139217],
          [-78.374233, 34.205023],
          [-78.25447, 34.216318],
          [-78.16235, 34.357007],
          [-78.029922, 34.331768],
          [-77.951643, 34.232368],
          [-77.937313, 33.928645]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37025",
      state: "37",
      county: "025",
      name: "Cabarrus County",
      censusArea: 361.749,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.719625, 35.362842],
          [-80.744252, 35.380638],
          [-80.755284, 35.394927],
          [-80.764255, 35.400226],
          [-80.784339, 35.50584],
          [-80.737289, 35.505633],
          [-80.737069, 35.507163],
          [-80.688362, 35.506561],
          [-80.657612, 35.506059],
          [-80.650431, 35.506094],
          [-80.649002, 35.505915],
          [-80.645902, 35.505913],
          [-80.562402, 35.50311],
          [-80.295236, 35.502947],
          [-80.505334, 35.184048],
          [-80.506418, 35.185226],
          [-80.550297, 35.208381],
          [-80.665835, 35.268181],
          [-80.667274, 35.269014],
          [-80.69362, 35.346806],
          [-80.708556, 35.355213],
          [-80.719625, 35.362842]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13223",
      state: "13",
      county: "223",
      name: "Paulding County",
      censusArea: 312.219,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.05031, 33.904488],
          [-84.978683, 33.951393],
          [-84.922742, 34.082497],
          [-84.737836, 34.079399],
          [-84.724435, 33.881859],
          [-84.725035, 33.819905],
          [-84.725181, 33.816995],
          [-84.723969, 33.815306],
          [-84.724139, 33.80617],
          [-84.724122, 33.791439],
          [-84.725477, 33.788579],
          [-84.741348, 33.788568],
          [-84.769935, 33.784704],
          [-84.791993, 33.781162],
          [-84.795109, 33.779809],
          [-84.799853, 33.779909],
          [-84.832705, 33.778522],
          [-84.832708, 33.776019],
          [-84.879151, 33.774758],
          [-84.901688, 33.780703],
          [-84.918629, 33.786328],
          [-85.037926, 33.811942],
          [-85.036684, 33.904327],
          [-85.05031, 33.904488]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36081",
      state: "36",
      county: "081",
      name: "Queens County",
      censusArea: 108.532,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.868917, 40.69515],
          [-73.874021, 40.694191],
          [-73.889575, 40.68418],
          [-73.896497, 40.682447],
          [-73.904425, 40.695671],
          [-73.912058, 40.703446],
          [-73.921473, 40.709004],
          [-73.923865, 40.720056],
          [-73.929223, 40.727849],
          [-73.937339, 40.72992],
          [-73.947064, 40.737516],
          [-73.954732, 40.739292],
          [-73.961188, 40.737091],
          [-73.962795, 40.737674],
          [-73.961544, 40.743081],
          [-73.9583, 40.745731],
          [-73.95492, 40.751509],
          [-73.943951, 40.764434],
          [-73.940844, 40.767399],
          [-73.936536, 40.769835],
          [-73.93519, 40.771745],
          [-73.938076, 40.772551],
          [-73.938399, 40.774606],
          [-73.93508, 40.779218],
          [-73.92797, 40.776762],
          [-73.916316, 40.786601],
          [-73.910551, 40.790988],
          [-73.912506, 40.79618],
          [-73.899809, 40.792524],
          [-73.892205, 40.79038],
          [-73.890586, 40.790145],
          [-73.889918, 40.787398],
          [-73.884867, 40.786621],
          [-73.870992, 40.786979],
          [-73.866707, 40.793653],
          [-73.862704, 40.797276],
          [-73.830548, 40.803162],
          [-73.811001, 40.805077],
          [-73.793403, 40.799881],
          [-73.785964, 40.800862],
          [-73.781369, 40.794907],
          [-73.776032, 40.795275],
          [-73.768431, 40.800704],
          [-73.74676, 40.780382],
          [-73.701744, 40.75253],
          [-73.700872, 40.746866],
          [-73.700582, 40.743184],
          [-73.754323, 40.586357],
          [-73.753349, 40.59056],
          [-73.774928, 40.590759],
          [-73.80143, 40.585659],
          [-73.806834, 40.584619],
          [-73.834408, 40.577201],
          [-73.878906, 40.560888],
          [-73.934512, 40.545175],
          [-73.934466, 40.555281],
          [-73.932729, 40.560266],
          [-73.935686, 40.564914],
          [-73.938598, 40.566161],
          [-73.849852, 40.588669],
          [-73.86049, 40.654655],
          [-73.85566, 40.663802],
          [-73.866027, 40.681918],
          [-73.868917, 40.69515]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36087",
      state: "36",
      county: "087",
      name: "Rockland County",
      censusArea: 173.55,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.947965, 41.223101],
          [-73.931482, 41.204994],
          [-73.909281, 41.164395],
          [-73.895381, 41.153995],
          [-73.88718, 41.133095],
          [-73.89488, 41.082396],
          [-73.8947, 41.069937],
          [-73.88938, 41.037597],
          [-73.893979, 40.997197],
          [-73.90268, 40.997297],
          [-73.91188, 41.001297],
          [-74.041049, 41.059086],
          [-74.041054, 41.059088],
          [-74.092486, 41.081896],
          [-74.096786, 41.083796],
          [-74.213373, 41.133828],
          [-74.234473, 41.142883],
          [-74.161789, 41.195794],
          [-73.981384, 41.324693],
          [-73.982584, 41.321693],
          [-73.947965, 41.223101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36089",
      state: "36",
      county: "089",
      name: "St. Lawrence County",
      censusArea: 2680.377,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.72498, 45.005915],
          [-74.720307, 44.953011],
          [-74.641872, 44.952621],
          [-74.525683, 44.170636],
          [-74.535156, 44.09925],
          [-74.854171, 44.070089],
          [-75.062779, 44.0504],
          [-75.170159, 44.096959],
          [-75.446124, 44.217655],
          [-75.86006, 44.403282],
          [-75.82083, 44.432244],
          [-75.807778, 44.471644],
          [-75.765495, 44.516285],
          [-75.696586, 44.567583],
          [-75.491201, 44.712947],
          [-75.433124, 44.750062],
          [-75.333744, 44.806378],
          [-75.301975, 44.826637],
          [-75.30763, 44.836813],
          [-75.283136, 44.849156],
          [-75.216486, 44.877478],
          [-75.189313, 44.883037],
          [-75.139868, 44.896676],
          [-75.064826, 44.929449],
          [-75.027125, 44.946568],
          [-75.005155, 44.958402],
          [-74.999655, 44.965921],
          [-74.99927, 44.971638],
          [-74.992756, 44.977449],
          [-74.972463, 44.983402],
          [-74.826578, 45.01585],
          [-74.801625, 45.014571],
          [-74.72498, 45.005915]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36093",
      state: "36",
      county: "093",
      name: "Schenectady County",
      censusArea: 204.516,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.180274, 42.729979],
          [-74.272295, 42.71427],
          [-74.263314, 42.796534],
          [-74.083883, 42.897354],
          [-74.09298, 42.955868],
          [-73.895938, 42.851076],
          [-73.809369, 42.778869],
          [-73.809603, 42.775016],
          [-74.180274, 42.729979]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36099",
      state: "36",
      county: "099",
      name: "Seneca County",
      censusArea: 323.705,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.96335, 42.90302],
          [-76.963926, 43.013157],
          [-76.713806, 43.024035],
          [-76.73674, 42.970286],
          [-76.733454, 42.727895],
          [-76.666543, 42.623457],
          [-76.585989, 42.54991],
          [-76.696655, 42.54679],
          [-76.895596, 42.541537],
          [-76.895349, 42.656255],
          [-76.971392, 42.764223],
          [-76.96335, 42.90302]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36103",
      state: "36",
      county: "103",
      name: "Suffolk County",
      censusArea: 912.051,
      stateName: "New York"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-72.132225, 41.104387],
            [-72.128352, 41.108131],
            [-72.126704, 41.115139],
            [-72.084207, 41.101524],
            [-72.081167, 41.09394],
            [-72.086975, 41.058292],
            [-72.095711, 41.05402],
            [-72.0972, 41.054884],
            [-72.097136, 41.075844],
            [-72.103152, 41.086484],
            [-72.1064, 41.088883],
            [-72.12056, 41.093171],
            [-72.139233, 41.092451],
            [-72.141921, 41.094371],
            [-72.142929, 41.097811],
            [-72.140737, 41.100835],
            [-72.132225, 41.104387]
          ]
        ],
        [
          [
            [-71.943563, 41.286675],
            [-71.926802, 41.290122],
            [-71.935259, 41.280579],
            [-71.94627, 41.276306],
            [-71.962598, 41.270968],
            [-71.978926, 41.265002],
            [-71.994717, 41.256451],
            [-72.002461, 41.252867],
            [-72.036846, 41.249794],
            [-72.034958, 41.255458],
            [-72.029438, 41.26309],
            [-72.023422, 41.270994],
            [-72.018926, 41.274114],
            [-72.006872, 41.27348],
            [-71.991117, 41.281331],
            [-71.980061, 41.280291],
            [-71.952864, 41.285098],
            [-71.943563, 41.286675]
          ]
        ],
        [
          [
            [-73.454172, 40.834097],
            [-73.497061, 40.922801],
            [-73.496642, 40.923476],
            [-73.491765, 40.942097],
            [-73.485365, 40.946397],
            [-73.484798, 40.946065],
            [-73.48011, 40.943319],
            [-73.478365, 40.942297],
            [-73.47441, 40.941056],
            [-73.463708, 40.937697],
            [-73.460603, 40.937375],
            [-73.456523, 40.936953],
            [-73.445026, 40.935763],
            [-73.437509, 40.934985],
            [-73.436664, 40.934897],
            [-73.429863, 40.929797],
            [-73.429665, 40.928203],
            [-73.428836, 40.921506],
            [-73.406074, 40.920235],
            [-73.402963, 40.925097],
            [-73.403462, 40.942197],
            [-73.400862, 40.953997],
            [-73.399762, 40.955197],
            [-73.392862, 40.955297],
            [-73.374462, 40.937597],
            [-73.365961, 40.931697],
            [-73.352761, 40.926697],
            [-73.345561, 40.925297],
            [-73.344161, 40.927297],
            [-73.33136, 40.929597],
            [-73.295061, 40.924497],
            [-73.295059, 40.924497],
            [-73.229285, 40.905121],
            [-73.148994, 40.928898],
            [-73.146242, 40.935074],
            [-73.144673, 40.955842],
            [-73.140785, 40.966178],
            [-73.110368, 40.971938],
            [-73.081582, 40.973058],
            [-73.043701, 40.962185],
            [-73.040445, 40.964498],
            [-72.995931, 40.966498],
            [-72.955163, 40.966146],
            [-72.913834, 40.962466],
            [-72.88825, 40.962962],
            [-72.826057, 40.969794],
            [-72.774104, 40.965314],
            [-72.760031, 40.975334],
            [-72.714425, 40.985596],
            [-72.689341, 40.989776],
            [-72.665018, 40.987496],
            [-72.635374, 40.990536],
            [-72.585327, 40.997587],
            [-72.565406, 41.009508],
            [-72.560974, 41.015444],
            [-72.549853, 41.019844],
            [-72.521548, 41.037652],
            [-72.477306, 41.052212],
            [-72.460778, 41.067012],
            [-72.445242, 41.086116],
            [-72.417945, 41.087955],
            [-72.397, 41.096307],
            [-72.356087, 41.133635],
            [-72.333351, 41.138018],
            [-72.322381, 41.140664],
            [-72.291109, 41.155874],
            [-72.278789, 41.158722],
            [-72.272997, 41.15501],
            [-72.2681, 41.154146],
            [-72.245348, 41.161217],
            [-72.238211, 41.15949],
            [-72.237731, 41.156434],
            [-72.253572, 41.137138],
            [-72.265124, 41.128482],
            [-72.300374, 41.112274],
            [-72.300044, 41.132059],
            [-72.306381, 41.13784],
            [-72.312734, 41.138546],
            [-72.318146, 41.137134],
            [-72.32663, 41.132162],
            [-72.335271, 41.120274],
            [-72.335177, 41.106917],
            [-72.317238, 41.088659],
            [-72.297718, 41.081042],
            [-72.280373, 41.080402],
            [-72.276709, 41.076722],
            [-72.283093, 41.067874],
            [-72.273657, 41.051533],
            [-72.260515, 41.042065],
            [-72.241252, 41.04477],
            [-72.229364, 41.044355],
            [-72.217476, 41.040611],
            [-72.201859, 41.032275],
            [-72.190563, 41.032579],
            [-72.183266, 41.035619],
            [-72.17949, 41.038435],
            [-72.174882, 41.046147],
            [-72.162898, 41.053187],
            [-72.16037, 41.053827],
            [-72.153857, 41.051859],
            [-72.137297, 41.039684],
            [-72.135137, 41.031284],
            [-72.137409, 41.023908],
            [-72.116368, 40.999796],
            [-72.109008, 40.994084],
            [-72.10216, 40.991509],
            [-72.095456, 40.991349],
            [-72.083039, 40.996453],
            [-72.079951, 41.003429],
            [-72.079208, 41.006437],
            [-72.076175, 41.009093],
            [-72.061448, 41.009442],
            [-72.057934, 41.004789],
            [-72.057075, 41.004893],
            [-72.055188, 41.005236],
            [-72.051585, 41.006437],
            [-72.049526, 41.009697],
            [-72.051549, 41.015741],
            [-72.051928, 41.020506],
            [-72.047468, 41.022565],
            [-72.035792, 41.020759],
            [-72.015013, 41.028348],
            [-71.99926, 41.039669],
            [-71.96704, 41.047772],
            [-71.961078, 41.054277],
            [-71.960355, 41.059878],
            [-71.961563, 41.064021],
            [-71.959595, 41.071237],
            [-71.93825, 41.077413],
            [-71.919385, 41.080517],
            [-71.899256, 41.080837],
            [-71.895496, 41.077381],
            [-71.889543, 41.075701],
            [-71.869558, 41.075046],
            [-71.86447, 41.076918],
            [-71.857494, 41.073558],
            [-71.856214, 41.070598],
            [-71.87391, 41.052278],
            [-71.903736, 41.040166],
            [-71.935689, 41.034182],
            [-72.029357, 40.999909],
            [-72.114448, 40.972085],
            [-72.39585, 40.86666],
            [-72.469996, 40.84274],
            [-72.573441, 40.813251],
            [-72.745208, 40.767091],
            [-72.753112, 40.763571],
            [-72.757176, 40.764371],
            [-72.768152, 40.761587],
            [-72.863164, 40.732962],
            [-72.923214, 40.713282],
            [-73.012545, 40.679651],
            [-73.054963, 40.666371],
            [-73.145266, 40.645491],
            [-73.20844, 40.630884],
            [-73.23914, 40.6251],
            [-73.262106, 40.621476],
            [-73.264493, 40.621437],
            [-73.306396, 40.620756],
            [-73.30974, 40.622532],
            [-73.319257, 40.635795],
            [-73.351465, 40.6305],
            [-73.391967, 40.617501],
            [-73.423806, 40.609869],
            [-73.425586, 40.656291],
            [-73.424618, 40.666272],
            [-73.423269, 40.670893],
            [-73.424758, 40.679052],
            [-73.440967, 40.764399],
            [-73.454172, 40.834097]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36107",
      state: "36",
      county: "107",
      name: "Tioga County",
      censusArea: 518.602,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.145519, 41.998913],
          [-76.343722, 41.998346],
          [-76.349898, 41.99841],
          [-76.462155, 41.998934],
          [-76.46654, 41.999025],
          [-76.557624, 42.000149],
          [-76.538349, 42.281755],
          [-76.416199, 42.262976],
          [-76.415305, 42.318368],
          [-76.39465, 42.318509],
          [-76.250149, 42.296676],
          [-76.253359, 42.407568],
          [-76.130181, 42.410337],
          [-76.081134, 42.230495],
          [-76.114033, 42.153418],
          [-76.111106, 42.112436],
          [-76.10584, 41.998858],
          [-76.123696, 41.998954],
          [-76.131201, 41.998954],
          [-76.145519, 41.998913]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36117",
      state: "36",
      county: "117",
      name: "Wayne County",
      censusArea: 603.826,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.374351, 43.152584],
          [-77.376038, 43.277652],
          [-77.341092, 43.280661],
          [-77.314619, 43.28103],
          [-77.303979, 43.27815],
          [-77.264177, 43.277363],
          [-77.214058, 43.284114],
          [-77.173088, 43.281509],
          [-77.143416, 43.287561],
          [-77.130429, 43.285635],
          [-77.111866, 43.287945],
          [-77.067295, 43.280937],
          [-77.033875, 43.271218],
          [-76.999691, 43.271456],
          [-76.988445, 43.2745],
          [-76.958402, 43.270005],
          [-76.952174, 43.270692],
          [-76.922351, 43.285006],
          [-76.904288, 43.291816],
          [-76.886913, 43.293891],
          [-76.877397, 43.292926],
          [-76.854976, 43.298443],
          [-76.841675, 43.305399],
          [-76.794708, 43.309632],
          [-76.769025, 43.318452],
          [-76.747067, 43.331477],
          [-76.731039, 43.343421],
          [-76.722501, 43.343686],
          [-76.705345, 43.125463],
          [-76.713806, 43.024035],
          [-76.963926, 43.013157],
          [-77.133397, 43.012463],
          [-77.134335, 43.039926],
          [-77.371478, 43.034696],
          [-77.374351, 43.152584]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42017",
      state: "42",
      county: "017",
      name: "Bucks County",
      censusArea: 604.307,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.106878, 40.192279],
          [-75.299774, 40.307835],
          [-75.484057, 40.418451],
          [-75.409733, 40.487984],
          [-75.367911, 40.514601],
          [-75.36661, 40.515424],
          [-75.338633, 40.534068],
          [-75.333514, 40.537057],
          [-75.196803, 40.60858],
          [-75.190858, 40.591342],
          [-75.190161, 40.589321],
          [-75.147368, 40.573152],
          [-75.135389, 40.575624],
          [-75.117292, 40.573211],
          [-75.100325, 40.567811],
          [-75.068615, 40.542223],
          [-75.067344, 40.536428],
          [-75.06509, 40.526148],
          [-75.065853, 40.519495],
          [-75.070568, 40.455165],
          [-75.061489, 40.422848],
          [-75.058848, 40.418065],
          [-75.035548, 40.406309],
          [-75.024775, 40.403455],
          [-74.937954, 40.340634],
          [-74.860492, 40.284584],
          [-74.770706, 40.214908],
          [-74.758613, 40.201342],
          [-74.722304, 40.160609],
          [-74.721604, 40.15381],
          [-74.724179, 40.147324],
          [-74.724304, 40.14701],
          [-74.740605, 40.13521],
          [-74.838008, 40.10091],
          [-74.900236, 40.077149],
          [-74.97429, 40.048872],
          [-74.984893, 40.057287],
          [-74.971268, 40.073744],
          [-74.958841, 40.082503],
          [-74.956164, 40.095745],
          [-74.961342, 40.097496],
          [-74.964991, 40.118635],
          [-74.981947, 40.120245],
          [-75.005946, 40.132524],
          [-75.00853, 40.133992],
          [-75.015066, 40.137992],
          [-75.104874, 40.191109],
          [-75.106878, 40.192279]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42023",
      state: "42",
      county: "023",
      name: "Cameron County",
      censusArea: 396.231,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.093381, 41.216931],
          [-78.236817, 41.230475],
          [-78.253959, 41.404721],
          [-78.420028, 41.405046],
          [-78.419118, 41.602188],
          [-78.419299, 41.616853],
          [-78.203422, 41.618157],
          [-78.050442, 41.475462],
          [-77.989194, 41.474822],
          [-77.988786, 41.367453],
          [-78.093381, 41.216931]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45079",
      state: "45",
      county: "079",
      name: "Richland County",
      censusArea: 757.068,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.747337, 33.761742],
          [-80.91731, 33.813735],
          [-81.01233, 33.88008],
          [-81.059715, 34.005253],
          [-81.153617, 34.07795],
          [-81.275845, 34.097381],
          [-81.338961, 34.197587],
          [-81.345328, 34.20503],
          [-81.316911, 34.239458],
          [-81.178293, 34.174552],
          [-81.095863, 34.213872],
          [-80.825921, 34.26876],
          [-80.869733, 34.182389],
          [-80.719318, 34.068972],
          [-80.692965, 34.08128],
          [-80.683662, 34.081603],
          [-80.676421, 34.08461],
          [-80.67297, 34.083732],
          [-80.658978, 34.085743],
          [-80.658325, 34.088774],
          [-80.646696, 34.09399],
          [-80.639534, 34.095633],
          [-80.635594, 34.097666],
          [-80.616186, 34.099605],
          [-80.633545, 33.85268],
          [-80.620107, 33.743237],
          [-80.747337, 33.761742]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45083",
      state: "45",
      county: "083",
      name: "Spartanburg County",
      censusArea: 807.926,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.012297, 34.668587],
          [-82.146792, 34.785554],
          [-82.225698, 34.850171],
          [-82.216217, 35.196044],
          [-82.195483, 35.194951],
          [-82.185513, 35.194355],
          [-82.176874, 35.19379],
          [-82.167676, 35.193699],
          [-81.969325, 35.187215],
          [-81.874433, 35.184113],
          [-81.754911, 34.932672],
          [-81.711694, 34.912577],
          [-81.78339, 34.837125],
          [-81.854002, 34.594549],
          [-82.012297, 34.668587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45085",
      state: "45",
      county: "085",
      name: "Sumter County",
      censusArea: 665.066,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.26906, 34.0351],
          [-80.180371, 33.952002],
          [-80.000594, 34.047877],
          [-79.893517, 33.988017],
          [-79.974601, 33.946527],
          [-80.255904, 33.803708],
          [-80.397131, 33.767821],
          [-80.392003, 33.71555],
          [-80.478709, 33.714265],
          [-80.534085, 33.643911],
          [-80.620107, 33.743237],
          [-80.633545, 33.85268],
          [-80.616186, 34.099605],
          [-80.618542, 34.109176],
          [-80.604403, 34.101671],
          [-80.578081, 34.101549],
          [-80.479857, 34.168696],
          [-80.481318, 34.112622],
          [-80.321721, 34.080356],
          [-80.279339, 34.036829],
          [-80.278827, 34.036706],
          [-80.278461, 34.036722],
          [-80.26906, 34.0351]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45089",
      state: "45",
      county: "089",
      name: "Williamsburg County",
      censusArea: 934.159,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.324317, 33.799123],
          [-79.323988, 33.794965],
          [-79.320443, 33.790511],
          [-79.323081, 33.789247],
          [-79.319664, 33.785434],
          [-79.317041, 33.779878],
          [-79.409332, 33.68889],
          [-79.435733, 33.572702],
          [-79.53808, 33.496896],
          [-79.677014, 33.304944],
          [-79.756172, 33.329699],
          [-79.971963, 33.500937],
          [-80.101697, 33.496891],
          [-79.974382, 33.72159],
          [-79.995638, 33.767906],
          [-79.87562, 33.885245],
          [-79.79837, 33.835562],
          [-79.496486, 33.775357],
          [-79.324317, 33.799123]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46005",
      state: "46",
      county: "005",
      name: "Beadle County",
      censusArea: 1258.71,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.700453, 44.196727],
          [-98.705762, 44.634159],
          [-97.977791, 44.631602],
          [-97.856226, 44.631178],
          [-97.853028, 44.544398],
          [-97.853206, 44.543254],
          [-97.85366, 44.195233],
          [-98.332042, 44.19662],
          [-98.700453, 44.196727]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46011",
      state: "46",
      county: "011",
      name: "Brookings County",
      censusArea: 792.206,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.129671, 44.195933],
          [-97.128024, 44.543126],
          [-96.885504, 44.543773],
          [-96.45199, 44.543639],
          [-96.452016, 44.543533],
          [-96.452236, 44.526871],
          [-96.45201, 44.516929],
          [-96.451974, 44.506849],
          [-96.452122, 44.473043],
          [-96.452218, 44.470873],
          [-96.451816, 44.460402],
          [-96.451924, 44.441549],
          [-96.452073, 44.38969],
          [-96.452134, 44.383679],
          [-96.452213, 44.360149],
          [-96.452282, 44.354857],
          [-96.452305, 44.345332],
          [-96.452152, 44.342219],
          [-96.452248, 44.340642],
          [-96.452309, 44.328094],
          [-96.452372, 44.325991],
          [-96.452248, 44.313362],
          [-96.452369, 44.312071],
          [-96.452239, 44.298655],
          [-96.452334, 44.297009],
          [-96.4525, 44.285687],
          [-96.452617, 44.282702],
          [-96.452365, 44.271972],
          [-96.452369, 44.268967],
          [-96.452419, 44.255274],
          [-96.452673, 44.254588],
          [-96.452774, 44.196895],
          [-96.452774, 44.19678],
          [-96.889216, 44.195705],
          [-97.129671, 44.195933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46019",
      state: "46",
      county: "019",
      name: "Butte County",
      censusArea: 2249.899,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.055877, 44.571016],
          [-104.05581, 44.691343],
          [-104.055938, 44.693881],
          [-104.055777, 44.700466],
          [-104.05587, 44.723422],
          [-104.055934, 44.72372],
          [-104.055963, 44.767962],
          [-104.055963, 44.768236],
          [-104.057698, 44.997431],
          [-104.040128, 44.999987],
          [-104.040274, 45.212891],
          [-102.957281, 45.212851],
          [-102.957371, 45.039569],
          [-102.964125, 44.604287],
          [-103.567653, 44.604098],
          [-103.820639, 44.604777],
          [-104.055877, 44.571016]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46023",
      state: "46",
      county: "023",
      name: "Charles Mix County",
      censusArea: 1097.487,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.49855, 42.99856],
          [-98.524871, 43.039347],
          [-98.69046, 43.076556],
          [-98.773406, 43.145919],
          [-98.868762, 43.162009],
          [-98.896783, 43.246909],
          [-99.069285, 43.320688],
          [-99.152889, 43.426908],
          [-99.304332, 43.460386],
          [-99.297998, 43.499669],
          [-99.297882, 43.499856],
          [-98.794588, 43.499187],
          [-98.714946, 43.499121],
          [-98.705782, 43.499319],
          [-98.680688, 43.373157],
          [-98.109492, 43.196838],
          [-98.069044, 43.184887],
          [-98.077108, 43.16833],
          [-98.092782, 42.973921],
          [-98.157405, 42.831899],
          [-98.165835, 42.837011],
          [-98.204506, 42.846845],
          [-98.309741, 42.881232],
          [-98.325864, 42.8865],
          [-98.447047, 42.935117],
          [-98.467356, 42.947556],
          [-98.490483, 42.977948],
          [-98.495747, 42.988032],
          [-98.49855, 42.99856]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46033",
      state: "46",
      county: "033",
      name: "Custer County",
      censusArea: 1557.002,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.054787, 43.503328],
          [-104.055032, 43.558603],
          [-104.05484, 43.579368],
          [-104.054885, 43.583512],
          [-104.054902, 43.583852],
          [-104.055133, 43.747105],
          [-104.055138, 43.750421],
          [-104.055104, 43.853478],
          [-102.687192, 43.855415],
          [-102.695526, 43.79815],
          [-102.817114, 43.689658],
          [-102.810419, 43.688132],
          [-102.9025, 43.674888],
          [-103.001068, 43.605799],
          [-103.000785, 43.476847],
          [-103.935373, 43.478824],
          [-104.054779, 43.477815],
          [-104.054786, 43.503072],
          [-104.054787, 43.503328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46039",
      state: "46",
      county: "039",
      name: "Deuel County",
      censusArea: 622.686,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.882345, 44.97687],
          [-96.452092, 44.977494],
          [-96.452092, 44.977475],
          [-96.452347, 44.962734],
          [-96.452047, 44.910695],
          [-96.451853, 44.906672],
          [-96.452009, 44.89008],
          [-96.45156, 44.805569],
          [-96.451559, 44.805468],
          [-96.451829, 44.797691],
          [-96.451888, 44.792299],
          [-96.451823, 44.790471],
          [-96.45162, 44.776191],
          [-96.45138, 44.761788],
          [-96.451573, 44.76051],
          [-96.451543, 44.703135],
          [-96.451761, 44.63135],
          [-96.451761, 44.631194],
          [-96.45172, 44.630708],
          [-96.451888, 44.544058],
          [-96.45199, 44.543639],
          [-96.885504, 44.543773],
          [-96.88457, 44.804436],
          [-96.882345, 44.97687]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46041",
      state: "46",
      county: "041",
      name: "Dewey County",
      censusArea: 2302.494,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.341924, 45.472904],
          [-100.274176, 45.381845],
          [-100.316659, 45.317904],
          [-100.260223, 45.24681],
          [-100.325451, 45.099337],
          [-100.280344, 45.018409],
          [-100.391818, 45.026006],
          [-100.404753, 44.897695],
          [-100.418682, 44.843366],
          [-100.543484, 44.761087],
          [-100.676387, 44.823162],
          [-100.717644, 44.77093],
          [-100.829795, 44.783248],
          [-101.022611, 44.724153],
          [-101.136838, 44.747126],
          [-101.136162, 44.994074],
          [-101.500988, 44.993649],
          [-101.486348, 45.38559],
          [-101.47019, 45.472416],
          [-101.261796, 45.472755],
          [-100.341924, 45.472904]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46045",
      state: "46",
      county: "045",
      name: "Edmunds County",
      censusArea: 1125.956,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.708987, 45.245866],
          [-99.707705, 45.593391],
          [-98.725002, 45.591252],
          [-98.722174, 45.591242],
          [-98.722481, 45.243764],
          [-99.573445, 45.245364],
          [-99.708987, 45.245866]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46051",
      state: "46",
      county: "051",
      name: "Grant County",
      censusArea: 681.458,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.452949, 45.269059],
          [-96.452948, 45.268925],
          [-96.452315, 45.208986],
          [-96.452152, 45.204849],
          [-96.452162, 45.203109],
          [-96.452304, 45.178563],
          [-96.452353, 45.124071],
          [-96.452418, 45.122677],
          [-96.452026, 45.095138],
          [-96.452219, 45.093836],
          [-96.45221, 45.051602],
          [-96.452177, 45.050185],
          [-96.45224, 45.042347],
          [-96.452092, 44.977494],
          [-96.882345, 44.97687],
          [-96.883948, 45.150224],
          [-97.226281, 45.151826],
          [-97.226244, 45.297647],
          [-97.007543, 45.296866],
          [-96.992946, 45.32688],
          [-96.47002, 45.326832],
          [-96.454094, 45.301546],
          [-96.453067, 45.298115],
          [-96.452791, 45.28428],
          [-96.452949, 45.269059]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46059",
      state: "46",
      county: "059",
      name: "Hand County",
      censusArea: 1436.613,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.705762, 44.634159],
          [-98.700453, 44.196727],
          [-98.925953, 44.196575],
          [-99.30018, 44.19483],
          [-99.311754, 44.897227],
          [-98.716498, 44.896925],
          [-98.705357, 44.89687],
          [-98.705762, 44.634159]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26159",
      state: "26",
      county: "159",
      name: "Van Buren County",
      censusArea: 607.474,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.22294, 42.071484],
          [-86.223563, 42.243379],
          [-86.36488, 42.243133],
          [-86.356218, 42.254166],
          [-86.321803, 42.310743],
          [-86.297168, 42.358207],
          [-86.284448, 42.394563],
          [-86.284969, 42.401814],
          [-86.276878, 42.413317],
          [-86.273893, 42.41928],
          [-85.764352, 42.420669],
          [-85.762943, 42.069327],
          [-86.22294, 42.071484]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26163",
      state: "26",
      county: "163",
      name: "Wayne County",
      censusArea: 612.08,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.551907, 42.435166],
          [-83.433672, 42.438942],
          [-83.316825, 42.442033],
          [-83.287959, 42.44268],
          [-83.083393, 42.447153],
          [-82.870572, 42.451235],
          [-82.870347, 42.450888],
          [-82.886113, 42.408137],
          [-82.888413, 42.398237],
          [-82.894013, 42.389437],
          [-82.898413, 42.385437],
          [-82.915114, 42.378137],
          [-82.919114, 42.374437],
          [-82.928815, 42.359437],
          [-82.92397, 42.352068],
          [-82.959416, 42.339638],
          [-82.988619, 42.332439],
          [-83.01832, 42.329739],
          [-83.064121, 42.317738],
          [-83.079721, 42.308638],
          [-83.096521, 42.290138],
          [-83.128022, 42.238839],
          [-83.131343, 42.20276],
          [-83.133923, 42.17474],
          [-83.124522, 42.137641],
          [-83.133511, 42.088143],
          [-83.157624, 42.085542],
          [-83.168759, 42.073601],
          [-83.170589, 42.07294],
          [-83.188598, 42.066431],
          [-83.189115, 42.061853],
          [-83.186877, 42.061206],
          [-83.185526, 42.052243],
          [-83.18824, 42.031329],
          [-83.185858, 42.029451],
          [-83.185822, 42.029367],
          [-83.295275, 42.094234],
          [-83.539396, 42.085598],
          [-83.542102, 42.174344],
          [-83.547548, 42.349217],
          [-83.551907, 42.435166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27001",
      state: "27",
      county: "001",
      name: "Aitkin County",
      censusArea: 1821.663,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.775257, 47.030418],
          [-93.055943, 47.026415],
          [-93.061529, 46.76655],
          [-93.054847, 46.419269],
          [-93.053871, 46.15812],
          [-93.431831, 46.153991],
          [-93.430648, 46.246412],
          [-93.796281, 46.243199],
          [-93.811464, 46.584238],
          [-93.777905, 46.589671],
          [-93.776024, 46.802891],
          [-93.775257, 47.030418]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27003",
      state: "27",
      county: "003",
      name: "Anoka County",
      censusArea: 423.01,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.198985, 45.124394],
          [-93.227696, 45.124533],
          [-93.227059, 45.049937],
          [-93.226929, 45.035671],
          [-93.283426, 45.035365],
          [-93.282226, 45.051165],
          [-93.278327, 45.103365],
          [-93.296095, 45.121897],
          [-93.311544, 45.143778],
          [-93.373136, 45.178133],
          [-93.51219, 45.245683],
          [-93.512554, 45.25042],
          [-93.51007, 45.414798],
          [-93.019563, 45.41177],
          [-93.019439, 45.296837],
          [-93.020219, 45.126754],
          [-93.020518, 45.123866],
          [-93.188706, 45.124374],
          [-93.198985, 45.124394]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27011",
      state: "27",
      county: "011",
      name: "Big Stone County",
      censusArea: 499.022,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.452948, 45.268925],
          [-96.452949, 45.269059],
          [-96.452791, 45.28428],
          [-96.453067, 45.298115],
          [-96.454094, 45.301546],
          [-96.47002, 45.326832],
          [-96.489065, 45.357071],
          [-96.521787, 45.375645],
          [-96.617726, 45.408092],
          [-96.680454, 45.410499],
          [-96.692541, 45.417338],
          [-96.732739, 45.458737],
          [-96.742509, 45.478723],
          [-96.745487, 45.488712],
          [-96.76528, 45.521414],
          [-96.784863, 45.5413],
          [-96.835451, 45.586129],
          [-96.253026, 45.585526],
          [-96.241328, 45.412667],
          [-96.117036, 45.411993],
          [-96.103614, 45.176675],
          [-96.28347, 45.246972],
          [-96.452948, 45.268925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27017",
      state: "27",
      county: "017",
      name: "Carlton County",
      censusArea: 861.381,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.061529, 46.76655],
          [-92.301676, 46.764134],
          [-92.292192, 46.663242],
          [-92.291597, 46.624941],
          [-92.291647, 46.604649],
          [-92.292371, 46.495585],
          [-92.29251, 46.478761],
          [-92.292727, 46.431993],
          [-92.292847, 46.420876],
          [-92.29286, 46.41722],
          [-93.054847, 46.419269],
          [-93.061529, 46.76655]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27025",
      state: "27",
      county: "025",
      name: "Chisago County",
      censusArea: 414.862,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.732594, 45.304224],
          [-92.746139, 45.296046],
          [-93.019439, 45.296837],
          [-93.019563, 45.41177],
          [-93.022274, 45.557154],
          [-93.141974, 45.558109],
          [-93.141793, 45.730657],
          [-92.841051, 45.730024],
          [-92.863703, 45.72182],
          [-92.869193, 45.717568],
          [-92.883987, 45.65487],
          [-92.885661, 45.644126],
          [-92.888114, 45.628377],
          [-92.886421, 45.594881],
          [-92.883749, 45.575483],
          [-92.871082, 45.567581],
          [-92.823309, 45.560934],
          [-92.803432, 45.5625],
          [-92.72623, 45.531085],
          [-92.680234, 45.464344],
          [-92.664102, 45.393309],
          [-92.704794, 45.326526],
          [-92.732594, 45.304224]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27035",
      state: "27",
      county: "035",
      name: "Crow Wing County",
      censusArea: 999.095,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.810362, 46.156585],
          [-94.374592, 46.157093],
          [-94.341679, 46.277705],
          [-94.339351, 46.284649],
          [-94.332523, 46.279566],
          [-94.342895, 46.80553],
          [-93.776024, 46.802891],
          [-93.777905, 46.589671],
          [-93.811464, 46.584238],
          [-93.796281, 46.243199],
          [-93.810912, 46.243128],
          [-93.810362, 46.156585]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27037",
      state: "27",
      county: "037",
      name: "Dakota County",
      censusArea: 562.168,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.802402, 44.745167],
          [-92.737259, 44.717155],
          [-92.732042, 44.713775],
          [-92.732043, 44.629484],
          [-92.792584, 44.629722],
          [-92.7926, 44.542971],
          [-93.039344, 44.51535],
          [-93.039485, 44.471871],
          [-93.281686, 44.471998],
          [-93.281521, 44.543957],
          [-93.318731, 44.630578],
          [-93.329615, 44.791086],
          [-93.197918, 44.861977],
          [-93.200744, 44.864884],
          [-93.189886, 44.871537],
          [-93.188627, 44.874096],
          [-93.186212, 44.876364],
          [-93.181772, 44.877829],
          [-93.178618, 44.880337],
          [-93.179613, 44.882748],
          [-93.182381, 44.884728],
          [-93.183148, 44.886739],
          [-93.178539, 44.888338],
          [-93.128629, 44.919648],
          [-93.020044, 44.89075],
          [-93.016466, 44.884611],
          [-93.012639, 44.869102],
          [-93.010076, 44.863787],
          [-93.005978, 44.771685],
          [-92.802201, 44.745714],
          [-92.802402, 44.745167]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27041",
      state: "27",
      county: "041",
      name: "Douglas County",
      censusArea: 637.3,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.758508, 45.759932],
          [-95.76975, 46.10745],
          [-95.14588, 46.106761],
          [-95.139731, 45.773416],
          [-95.13967, 45.758891],
          [-95.758508, 45.759932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27043",
      state: "27",
      county: "043",
      name: "Faribault County",
      censusArea: 712.476,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.247123, 43.847946],
          [-93.767761, 43.847956],
          [-93.64829, 43.848139],
          [-93.648533, 43.499559],
          [-93.699345, 43.499576],
          [-93.704916, 43.499568],
          [-93.708771, 43.499564],
          [-93.716217, 43.499563],
          [-93.794285, 43.499542],
          [-93.795793, 43.49952],
          [-93.97076, 43.499605],
          [-93.970762, 43.499605],
          [-94.092894, 43.500302],
          [-94.094339, 43.500302],
          [-94.108068, 43.5003],
          [-94.10988, 43.500283],
          [-94.247965, 43.500333],
          [-94.247123, 43.847946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27045",
      state: "27",
      county: "045",
      name: "Fillmore County",
      censusArea: 861.3,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.448965, 43.834105],
          [-92.44883, 43.848274],
          [-92.079292, 43.84708],
          [-91.730424, 43.846935],
          [-91.73047, 43.50233],
          [-91.730217, 43.50068],
          [-91.730359, 43.50068],
          [-91.73333, 43.500623],
          [-91.736558, 43.500561],
          [-91.738446, 43.500525],
          [-91.761414, 43.500637],
          [-91.777688, 43.500711],
          [-91.77929, 43.500803],
          [-91.804925, 43.500716],
          [-91.807156, 43.500648],
          [-91.824848, 43.500684],
          [-91.941837, 43.500554],
          [-91.949879, 43.500485],
          [-92.079802, 43.500647],
          [-92.079954, 43.500647],
          [-92.08997, 43.500684],
          [-92.103886, 43.500735],
          [-92.178863, 43.500713],
          [-92.198788, 43.500527],
          [-92.277425, 43.500466],
          [-92.279084, 43.500436],
          [-92.368908, 43.500454],
          [-92.388298, 43.500483],
          [-92.40613, 43.500476],
          [-92.408832, 43.500614],
          [-92.448948, 43.50042],
          [-92.449087, 43.503302],
          [-92.448965, 43.834105]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27053",
      state: "27",
      county: "053",
      name: "Hennepin County",
      censusArea: 553.591,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.208092, 45.035744],
          [-93.178539, 44.888338],
          [-93.183148, 44.886739],
          [-93.182381, 44.884728],
          [-93.179613, 44.882748],
          [-93.178618, 44.880337],
          [-93.181772, 44.877829],
          [-93.186212, 44.876364],
          [-93.188627, 44.874096],
          [-93.189886, 44.871537],
          [-93.200744, 44.864884],
          [-93.197918, 44.861977],
          [-93.329615, 44.791086],
          [-93.520431, 44.804263],
          [-93.520632, 44.858473],
          [-93.520725, 44.861999],
          [-93.520632, 44.864108],
          [-93.520706, 44.891502],
          [-93.767041, 44.890862],
          [-93.767367, 44.977964],
          [-93.761874, 45.081244],
          [-93.675914, 45.151134],
          [-93.667203, 45.154443],
          [-93.661545, 45.153274],
          [-93.647747, 45.158609],
          [-93.645168, 45.161561],
          [-93.638622, 45.16562],
          [-93.638745, 45.174363],
          [-93.521653, 45.246618],
          [-93.515342, 45.24632],
          [-93.51219, 45.245683],
          [-93.373136, 45.178133],
          [-93.311544, 45.143778],
          [-93.296095, 45.121897],
          [-93.278327, 45.103365],
          [-93.282226, 45.051165],
          [-93.283426, 45.035365],
          [-93.226929, 45.035671],
          [-93.208092, 45.035744]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27059",
      state: "27",
      county: "059",
      name: "Isanti County",
      censusArea: 435.794,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.510704, 45.558772],
          [-93.512402, 45.718817],
          [-93.513683, 45.734318],
          [-93.404626, 45.731324],
          [-93.141793, 45.730657],
          [-93.141974, 45.558109],
          [-93.022274, 45.557154],
          [-93.019563, 45.41177],
          [-93.51007, 45.414798],
          [-93.510704, 45.558772]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37035",
      state: "37",
      county: "035",
      name: "Catawba County",
      censusArea: 398.721,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.364055, 35.767865],
          [-81.353862, 35.768895],
          [-81.355709, 35.780385],
          [-81.34634, 35.794072],
          [-81.334255, 35.79646],
          [-81.142338, 35.827434],
          [-81.109416, 35.776638],
          [-80.924548, 35.621735],
          [-80.960029, 35.54781],
          [-80.961841, 35.548191],
          [-81.534359, 35.568811],
          [-81.364055, 35.767865]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37037",
      state: "37",
      county: "037",
      name: "Chatham County",
      censusArea: 682.185,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.90617, 35.867912],
          [-78.909427, 35.842941],
          [-78.971335, 35.675614],
          [-78.995054, 35.610233],
          [-78.913296, 35.583064],
          [-78.969761, 35.521433],
          [-79.118958, 35.628215],
          [-79.350009, 35.517991],
          [-79.351354, 35.517761],
          [-79.555896, 35.515085],
          [-79.54249, 35.843399],
          [-79.318849, 35.844078],
          [-79.249475, 35.876788],
          [-79.016699, 35.863201],
          [-78.90617, 35.867912]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37039",
      state: "37",
      county: "039",
      name: "Cherokee County",
      censusArea: 455.426,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.321869, 34.988408],
          [-84.308576, 35.092761],
          [-84.308437, 35.093173],
          [-84.292321, 35.206677],
          [-84.29024, 35.225572],
          [-84.227818, 35.267878],
          [-84.223718, 35.269078],
          [-84.211818, 35.266078],
          [-84.202879, 35.255772],
          [-84.124915, 35.24983],
          [-84.12115, 35.250644],
          [-84.115279, 35.250438],
          [-84.115048, 35.249765],
          [-84.097508, 35.247382],
          [-84.055712, 35.268182],
          [-84.029033, 35.291049],
          [-83.958805, 35.216929],
          [-83.844709, 35.255408],
          [-83.702521, 35.248398],
          [-83.738106, 35.155911],
          [-83.857142, 35.137071],
          [-84.005457, 34.98744],
          [-84.021357, 34.98743],
          [-84.029954, 34.987321],
          [-84.129455, 34.987504],
          [-84.129555, 34.987504],
          [-84.321869, 34.988408]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37041",
      state: "37",
      county: "041",
      name: "Chowan County",
      censusArea: 172.473,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.411844, 36.075376],
          [-76.412984, 36.072243],
          [-76.420881, 36.06066],
          [-76.442994, 36.042758],
          [-76.451418, 36.039073],
          [-76.458789, 36.028016],
          [-76.459316, 36.024331],
          [-76.491959, 36.018013],
          [-76.514335, 36.00564],
          [-76.547505, 36.009852],
          [-76.5633, 36.009852],
          [-76.575936, 36.006167],
          [-76.580674, 36.00722],
          [-76.589625, 36.015644],
          [-76.60384, 36.033018],
          [-76.615423, 36.037757],
          [-76.631745, 36.038283],
          [-76.653332, 36.035124],
          [-76.668814, 36.020968],
          [-76.736326, 36.147042],
          [-76.706556, 36.243869],
          [-76.690392, 36.267269],
          [-76.696601, 36.296211],
          [-76.559674, 36.351892],
          [-76.589897, 36.239009],
          [-76.573374, 36.112266],
          [-76.411844, 36.075376]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37047",
      state: "37",
      county: "047",
      name: "Columbus County",
      censusArea: 937.293,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.710141, 33.994688],
          [-78.712206, 33.996732],
          [-79.071169, 34.29924],
          [-78.951088, 34.449026],
          [-78.87153, 34.482668],
          [-78.677447, 34.469435],
          [-78.44623, 34.376839],
          [-78.337248, 34.367089],
          [-78.256316, 34.399763],
          [-78.16235, 34.357007],
          [-78.25447, 34.216318],
          [-78.374233, 34.205023],
          [-78.422567, 34.139217],
          [-78.494928, 34.166772],
          [-78.545218, 34.145747],
          [-78.55685, 34.062377],
          [-78.6505, 33.944035],
          [-78.710141, 33.994688]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37059",
      state: "37",
      county: "059",
      name: "Davie County",
      censusArea: 264.105,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.496403, 36.046536],
          [-80.395389, 35.97321],
          [-80.365681, 35.895787],
          [-80.464086, 35.827538],
          [-80.458904, 35.742811],
          [-80.631796, 35.845784],
          [-80.70767, 35.852913],
          [-80.693198, 36.051114],
          [-80.496403, 36.046536]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37061",
      state: "37",
      county: "061",
      name: "Duplin County",
      censusArea: 816.219,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.113208, 34.721969],
          [-78.200473, 34.737301],
          [-78.147081, 34.902448],
          [-78.16481, 35.179145],
          [-78.163539, 35.18937],
          [-78.043617, 35.192769],
          [-77.893043, 35.14656],
          [-77.83425, 35.177814],
          [-77.769064, 35.145471],
          [-77.747639, 35.071354],
          [-77.745885, 35.022587],
          [-77.73966, 35.016641],
          [-77.730784, 35.008259],
          [-77.73103, 35.00814],
          [-77.68338, 34.979123],
          [-77.675373, 34.973417],
          [-77.67149, 34.811643],
          [-77.676395, 34.787182],
          [-77.68056, 34.72064],
          [-78.113208, 34.721969]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37065",
      state: "37",
      county: "065",
      name: "Edgecombe County",
      censusArea: 505.336,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.8091, 35.909272],
          [-77.777751, 35.984635],
          [-77.774125, 35.998742],
          [-77.697879, 36.152981],
          [-77.538311, 36.094037],
          [-77.526809, 36.037699],
          [-77.402701, 36.004933],
          [-77.342462, 35.905346],
          [-77.350602, 35.819214],
          [-77.390227, 35.83292],
          [-77.516479, 35.760866],
          [-77.538364, 35.750162],
          [-77.661312, 35.677321],
          [-77.751567, 35.82786],
          [-77.828444, 35.867208],
          [-77.822385, 35.878216],
          [-77.8091, 35.909272]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37069",
      state: "37",
      county: "069",
      name: "Franklin County",
      censusArea: 491.682,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.496474, 36.175217],
          [-78.422426, 36.16416],
          [-78.307012, 36.266159],
          [-78.132887, 36.246445],
          [-78.006554, 36.202663],
          [-78.172179, 35.956301],
          [-78.254903, 35.81773],
          [-78.307314, 35.896587],
          [-78.546497, 36.021804],
          [-78.544801, 36.080678],
          [-78.496474, 36.175217]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37071",
      state: "37",
      county: "071",
      name: "Gaston County",
      censusArea: 356.027,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.043407, 35.14839],
          [-81.090215, 35.152627],
          [-81.239358, 35.159974],
          [-81.241686, 35.160081],
          [-81.269199, 35.16114],
          [-81.32802, 35.163404],
          [-81.321314, 35.260221],
          [-81.364925, 35.311225],
          [-81.456505, 35.419616],
          [-81.300355, 35.414611],
          [-80.955388, 35.400833],
          [-81.008979, 35.24574],
          [-81.013956, 35.220588],
          [-81.006196, 35.210301],
          [-81.005061, 35.189821],
          [-81.005851, 35.156562],
          [-81.042758, 35.146042],
          [-81.043407, 35.14839]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37075",
      state: "37",
      county: "075",
      name: "Graham County",
      censusArea: 292.079,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.029033, 35.291049],
          [-84.02351, 35.295783],
          [-84.02141, 35.301383],
          [-84.032479, 35.325318],
          [-84.03245, 35.32653],
          [-84.038081, 35.348363],
          [-84.037494, 35.34985],
          [-84.035343, 35.350833],
          [-84.024756, 35.353896],
          [-84.00225, 35.422548],
          [-83.999906, 35.425201],
          [-83.9614, 35.459496],
          [-83.961054, 35.462838],
          [-83.961056, 35.463738],
          [-83.961053, 35.464143],
          [-83.952882, 35.460635],
          [-83.952676, 35.460763],
          [-83.585544, 35.434867],
          [-83.680043, 35.279414],
          [-83.683552, 35.276808],
          [-83.683945, 35.274216],
          [-83.68629, 35.271745],
          [-83.68939, 35.266963],
          [-83.690005, 35.26504],
          [-83.691764, 35.26369],
          [-83.692965, 35.260761],
          [-83.694278, 35.259366],
          [-83.694698, 35.257329],
          [-83.69693, 35.255682],
          [-83.698745, 35.253279],
          [-83.700556, 35.252172],
          [-83.702521, 35.248398],
          [-83.844709, 35.255408],
          [-83.958805, 35.216929],
          [-84.029033, 35.291049]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37077",
      state: "37",
      county: "077",
      name: "Granville County",
      censusArea: 531.57,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.758392, 36.541852],
          [-78.734122, 36.541902],
          [-78.670051, 36.542035],
          [-78.663317, 36.542011],
          [-78.533013, 36.541004],
          [-78.529722, 36.540981],
          [-78.471022, 36.542307],
          [-78.470792, 36.542316],
          [-78.45697, 36.542474],
          [-78.511276, 36.430753],
          [-78.510658, 36.309712],
          [-78.5113, 36.267521],
          [-78.510319, 36.240763],
          [-78.508635, 36.183152],
          [-78.496474, 36.175217],
          [-78.544801, 36.080678],
          [-78.546497, 36.021804],
          [-78.749, 36.071246],
          [-78.804983, 36.091883],
          [-78.802005, 36.235937],
          [-78.794823, 36.526689],
          [-78.7963, 36.541713],
          [-78.76543, 36.541727],
          [-78.758392, 36.541852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37083",
      state: "37",
      county: "083",
      name: "Halifax County",
      censusArea: 724.086,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.89795, 36.506849],
          [-77.645496, 36.480956],
          [-77.557072, 36.418606],
          [-77.586041, 36.337015],
          [-77.441747, 36.32409],
          [-77.386724, 36.213662],
          [-77.350899, 36.19128],
          [-77.330993, 36.192774],
          [-77.300445, 36.164944],
          [-77.29181, 36.16849],
          [-77.264744, 36.159745],
          [-77.230694, 36.103618],
          [-77.327686, 36.074512],
          [-77.339263, 36.064681],
          [-77.402701, 36.004933],
          [-77.526809, 36.037699],
          [-77.538311, 36.094037],
          [-77.697879, 36.152981],
          [-77.887083, 36.14384],
          [-78.006554, 36.202663],
          [-77.912023, 36.38227],
          [-77.89795, 36.506849]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37087",
      state: "37",
      county: "087",
      name: "Haywood County",
      censusArea: 553.692,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.254231, 35.695807],
          [-83.250695, 35.709349],
          [-83.255489, 35.714974],
          [-83.251247, 35.719916],
          [-83.240669, 35.72676],
          [-83.164909, 35.759965],
          [-83.100233, 35.774745],
          [-83.100329, 35.774804],
          [-83.100225, 35.774765],
          [-82.995803, 35.773128],
          [-82.98397, 35.77801],
          [-82.964108, 35.7941],
          [-82.883665, 35.67819],
          [-82.766726, 35.566764],
          [-82.800276, 35.470536],
          [-82.744383, 35.423342],
          [-82.745209, 35.422924],
          [-82.832001, 35.318434],
          [-82.920819, 35.291868],
          [-82.996829, 35.372796],
          [-83.186052, 35.514573],
          [-83.181894, 35.671127],
          [-83.254231, 35.695807]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42027",
      state: "42",
      county: "027",
      name: "Centre County",
      censusArea: 1109.921,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.667819, 40.735335],
          [-77.681334, 40.729758],
          [-77.824506, 40.743681],
          [-77.94425, 40.691557],
          [-78.116521, 40.739127],
          [-78.131834, 40.743017],
          [-78.359912, 40.732592],
          [-78.376518, 40.754417],
          [-78.24922, 40.855383],
          [-78.168128, 40.960865],
          [-78.07774, 40.958824],
          [-78.056276, 41.00417],
          [-78.112239, 41.114217],
          [-78.038203, 41.153633],
          [-77.898195, 41.252752],
          [-77.892724, 41.178259],
          [-77.79856, 41.18195],
          [-77.741911, 41.107614],
          [-77.621759, 41.091185],
          [-77.508596, 40.962938],
          [-77.14416, 41.044338],
          [-77.36418, 40.846937],
          [-77.667819, 40.735335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42029",
      state: "42",
      county: "029",
      name: "Chester County",
      censusArea: 750.508,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.361066, 40.065543],
          [-75.414991, 40.041787],
          [-75.435816, 39.985059],
          [-75.562539, 39.901638],
          [-75.594846, 39.837286],
          [-75.595756, 39.837156],
          [-75.634706, 39.830164],
          [-75.641518, 39.828363],
          [-75.662822, 39.82115],
          [-75.685991, 39.811054],
          [-75.716969, 39.791998],
          [-75.739705, 39.772623],
          [-75.788359, 39.721811],
          [-75.799563, 39.721882],
          [-75.810068, 39.721906],
          [-76.013067, 39.72192],
          [-76.027618, 39.721833],
          [-76.135584, 39.721556],
          [-76.066685, 39.759969],
          [-75.991644, 39.868713],
          [-75.985056, 39.937871],
          [-75.942997, 40.109266],
          [-75.873375, 40.137107],
          [-75.729863, 40.224489],
          [-75.696782, 40.241863],
          [-75.592657, 40.225491],
          [-75.573338, 40.195303],
          [-75.562878, 40.197361],
          [-75.557148, 40.207213],
          [-75.549174, 40.189542],
          [-75.525945, 40.148611],
          [-75.361066, 40.065543]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42033",
      state: "42",
      county: "033",
      name: "Clearfield County",
      censusArea: 1144.722,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.513712, 40.725968],
          [-78.712747, 40.72435],
          [-78.801697, 40.724539],
          [-78.806363, 40.845035],
          [-78.805167, 40.90598],
          [-78.804532, 41.132186],
          [-78.710248, 41.201859],
          [-78.646666, 41.253778],
          [-78.236817, 41.230475],
          [-78.093381, 41.216931],
          [-78.061007, 41.174539],
          [-78.038203, 41.153633],
          [-78.112239, 41.114217],
          [-78.056276, 41.00417],
          [-78.07774, 40.958824],
          [-78.168128, 40.960865],
          [-78.24922, 40.855383],
          [-78.376518, 40.754417],
          [-78.359912, 40.732592],
          [-78.35043, 40.724827],
          [-78.513712, 40.725968]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42039",
      state: "42",
      county: "039",
      name: "Crawford County",
      censusArea: 1012.298,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.612598, 41.77503],
          [-79.612873, 41.623858],
          [-79.612866, 41.617058],
          [-79.748047, 41.617747],
          [-79.828876, 41.622057],
          [-79.999779, 41.490049],
          [-80.125316, 41.480395],
          [-80.519209, 41.489013],
          [-80.519225, 41.499924],
          [-80.519157, 41.528769],
          [-80.519339, 41.539297],
          [-80.519357, 41.669767],
          [-80.519424, 41.671228],
          [-80.519373, 41.701473],
          [-80.519408, 41.739359],
          [-80.519369, 41.752487],
          [-80.519239, 41.765138],
          [-80.519294, 41.849563],
          [-79.612072, 41.850056],
          [-79.612598, 41.77503]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42045",
      state: "42",
      county: "045",
      name: "Delaware County",
      censusArea: 183.843,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.562539, 39.901638],
          [-75.435816, 39.985059],
          [-75.414991, 40.041787],
          [-75.361066, 40.065543],
          [-75.356534, 40.062787],
          [-75.349457, 40.052905],
          [-75.276482, 39.976959],
          [-75.262539, 39.876578],
          [-75.24047, 39.872381],
          [-75.215722, 39.876087],
          [-75.210972, 39.865706],
          [-75.235026, 39.856613],
          [-75.271159, 39.84944],
          [-75.3065, 39.849812],
          [-75.374635, 39.82577],
          [-75.390169, 39.816549],
          [-75.415041, 39.801786],
          [-75.428038, 39.809212],
          [-75.45374, 39.820312],
          [-75.498843, 39.833312],
          [-75.539346, 39.838211],
          [-75.579849, 39.838526],
          [-75.5799, 39.838522],
          [-75.593082, 39.8375],
          [-75.593666, 39.837455],
          [-75.594846, 39.837286],
          [-75.562539, 39.901638]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42049",
      state: "42",
      county: "049",
      name: "Erie County",
      censusArea: 799.154,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.610839, 41.998989],
          [-79.612072, 41.850056],
          [-80.519294, 41.849563],
          [-80.519345, 41.929168],
          [-80.519304, 41.943992],
          [-80.519405, 41.976158],
          [-80.519425, 41.977522],
          [-80.519425, 41.977523],
          [-80.435451, 42.005611],
          [-80.409776, 42.011578],
          [-80.373066, 42.024102],
          [-80.371869, 42.023966],
          [-80.363251, 42.027973],
          [-80.349169, 42.030243],
          [-80.329976, 42.036168],
          [-80.296758, 42.049076],
          [-80.230486, 42.077957],
          [-80.188085, 42.094257],
          [-80.165884, 42.105857],
          [-80.154084, 42.114757],
          [-80.136213, 42.149937],
          [-80.13043, 42.156331],
          [-80.117368, 42.166341],
          [-80.088512, 42.173184],
          [-80.077388, 42.171262],
          [-80.073381, 42.168658],
          [-80.080028, 42.163625],
          [-80.071981, 42.155357],
          [-80.078781, 42.151457],
          [-80.076281, 42.147857],
          [-80.07198, 42.146057],
          [-80.06108, 42.144857],
          [-79.989186, 42.177051],
          [-79.931324, 42.206737],
          [-79.923924, 42.207546],
          [-79.90105, 42.216701],
          [-79.886187, 42.224933],
          [-79.867979, 42.230999],
          [-79.844661, 42.235486],
          [-79.798447, 42.255939],
          [-79.761951, 42.26986],
          [-79.761964, 42.251354],
          [-79.762152, 42.243054],
          [-79.761833, 42.183627],
          [-79.761929, 42.179693],
          [-79.761921, 42.173319],
          [-79.761759, 42.162675],
          [-79.761861, 42.150712],
          [-79.762122, 42.131246],
          [-79.761374, 41.999067],
          [-79.625287, 41.999003],
          [-79.625301, 41.999068],
          [-79.610839, 41.998989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42051",
      state: "42",
      county: "051",
      name: "Fayette County",
      censusArea: 790.339,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.476662, 39.721078],
          [-79.608223, 39.721154],
          [-79.610623, 39.721245],
          [-79.763774, 39.720776],
          [-79.852904, 39.720713],
          [-79.853131, 39.720713],
          [-79.916266, 39.720792],
          [-79.915278, 39.8803],
          [-79.998014, 39.983322],
          [-79.877048, 40.036826],
          [-79.877385, 40.126792],
          [-79.72224, 40.121141],
          [-79.641098, 40.088317],
          [-79.592602, 40.092005],
          [-79.578966, 40.105496],
          [-79.540347, 40.114035],
          [-79.505188, 40.140568],
          [-79.479402, 40.142801],
          [-79.457008, 40.135108],
          [-79.293682, 40.040413],
          [-79.294283, 40.039429],
          [-79.296131, 40.03675],
          [-79.301533, 40.02986],
          [-79.302801, 40.0285],
          [-79.32525, 39.999872],
          [-79.330397, 39.992796],
          [-79.347363, 39.969492],
          [-79.417558, 39.853776],
          [-79.365695, 39.820546],
          [-79.392458, 39.72134],
          [-79.476662, 39.721078]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42055",
      state: "42",
      county: "055",
      name: "Franklin County",
      censusArea: 772.224,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.70303, 40.263576],
          [-77.685163, 40.276304],
          [-77.671761, 40.289825],
          [-77.614665, 40.198549],
          [-77.471085, 39.944077],
          [-77.459427, 39.720017],
          [-77.469145, 39.720018],
          [-77.533371, 39.720165],
          [-77.534758, 39.720134],
          [-77.672249, 39.720778],
          [-77.674522, 39.720847],
          [-77.724115, 39.720894],
          [-77.732615, 39.721094],
          [-77.743204, 39.721205],
          [-77.768534, 39.721358],
          [-78.073736, 39.722314],
          [-78.075771, 39.722301],
          [-78.09914, 39.722322],
          [-78.0027, 39.826599],
          [-77.920952, 40.00045],
          [-77.864489, 40.061568],
          [-77.70303, 40.263576]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42061",
      state: "42",
      county: "061",
      name: "Huntingdon County",
      censusArea: 874.639,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.116521, 40.739127],
          [-77.94425, 40.691557],
          [-77.824506, 40.743681],
          [-77.681334, 40.729758],
          [-77.68708, 40.67675],
          [-77.841827, 40.550035],
          [-77.816513, 40.500074],
          [-77.913844, 40.398679],
          [-77.75212, 40.378545],
          [-77.70303, 40.263576],
          [-77.864489, 40.061568],
          [-77.925905, 40.101911],
          [-78.134863, 40.165131],
          [-78.236302, 40.224559],
          [-78.257305, 40.297883],
          [-78.130081, 40.48199],
          [-78.197083, 40.558444],
          [-78.22197, 40.674539],
          [-78.116521, 40.739127]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42067",
      state: "42",
      county: "067",
      name: "Juniata County",
      censusArea: 391.35,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.75212, 40.378545],
          [-77.611315, 40.515017],
          [-77.49776, 40.609484],
          [-77.36069, 40.669991],
          [-77.323053, 40.683082],
          [-77.287941, 40.693595],
          [-77.198923, 40.682511],
          [-77.035549, 40.676918],
          [-76.939749, 40.638378],
          [-76.947365, 40.629517],
          [-76.949414, 40.628163],
          [-77.18293, 40.58214],
          [-77.16996, 40.562726],
          [-77.33811, 40.495336],
          [-77.540141, 40.399221],
          [-77.671761, 40.289825],
          [-77.685163, 40.276304],
          [-77.70303, 40.263576],
          [-77.75212, 40.378545]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42073",
      state: "42",
      county: "073",
      name: "Lawrence County",
      censusArea: 358.175,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.096861, 41.069804],
          [-80.16529, 41.00047],
          [-80.160006, 40.886909],
          [-80.158534, 40.855157],
          [-80.519039, 40.851339],
          [-80.519764, 40.899858],
          [-80.51979, 40.900761],
          [-80.519091, 40.921061],
          [-80.519, 40.98738],
          [-80.518989, 40.995445],
          [-80.518927, 41.015387],
          [-80.51896, 41.061546],
          [-80.518928, 41.070954],
          [-80.51896, 41.071866],
          [-80.518999, 41.075014],
          [-80.519088, 41.082074],
          [-80.519092, 41.090658],
          [-80.519125, 41.100819],
          [-80.519192, 41.105358],
          [-80.518992, 41.115958],
          [-80.519056, 41.125057],
          [-80.519012, 41.125057],
          [-80.519012, 41.125093],
          [-80.385094, 41.126492],
          [-80.147164, 41.113795],
          [-80.096861, 41.069804]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42079",
      state: "42",
      county: "079",
      name: "Luzerne County",
      censusArea: 890.333,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.207827, 40.94974],
          [-76.228975, 41.138466],
          [-76.317916, 41.205384],
          [-76.310133, 41.310199],
          [-76.284865, 41.372724],
          [-76.2831, 41.376517],
          [-76.00507, 41.384551],
          [-75.834695, 41.427246],
          [-75.832631, 41.399934],
          [-75.795286, 41.378127],
          [-75.76786, 41.365841],
          [-75.760518, 41.360942],
          [-75.741539, 41.348179],
          [-75.687244, 41.339216],
          [-75.649257, 41.190951],
          [-75.600736, 41.161497],
          [-75.603002, 41.151914],
          [-75.615557, 41.150001],
          [-75.634978, 41.123192],
          [-75.649637, 41.122344],
          [-75.77196, 41.076751],
          [-75.73193, 41.00822],
          [-75.997348, 40.912985],
          [-76.02815, 40.901986],
          [-76.207827, 40.94974]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41029",
      state: "41",
      county: "029",
      name: "Jackson County",
      censusArea: 2783.548,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.230024, 42.287494],
          [-123.229857, 42.551783],
          [-123.2299, 42.702486],
          [-123.151421, 42.756069],
          [-122.794793, 42.779422],
          [-122.677211, 42.881341],
          [-122.460053, 42.939852],
          [-122.400467, 42.996426],
          [-122.281856, 42.996556],
          [-122.287446, 42.127168],
          [-122.290351, 42.127092],
          [-122.28974, 42.122561],
          [-122.289533, 42.007764],
          [-122.378193, 42.009518],
          [-122.501135, 42.00846],
          [-122.634739, 42.004858],
          [-123.001152, 42.003],
          [-123.045254, 42.003049],
          [-123.154908, 42.008036],
          [-123.230762, 42.003845],
          [-123.230764, 42.003845],
          [-123.230024, 42.287494]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41033",
      state: "41",
      county: "033",
      name: "Josephine County",
      censusArea: 1639.668,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.230024, 42.287494],
          [-123.230764, 42.003845],
          [-123.347562, 41.999108],
          [-123.49883, 42.000525],
          [-123.498896, 42.000474],
          [-123.501997, 42.000527],
          [-123.518075, 42.000436],
          [-123.624554, 41.999837],
          [-123.821472, 41.995473],
          [-123.856786, 42.141132],
          [-123.791241, 42.236398],
          [-123.839687, 42.311532],
          [-123.892189, 42.352831],
          [-124.025894, 42.35913],
          [-124.041983, 42.387656],
          [-124.008397, 42.496352],
          [-123.885746, 42.504233],
          [-123.831762, 42.632561],
          [-123.71619, 42.784234],
          [-123.715088, 42.739735],
          [-123.444177, 42.703138],
          [-123.365975, 42.73204],
          [-123.2299, 42.702486],
          [-123.229857, 42.551783],
          [-123.230024, 42.287494]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41039",
      state: "41",
      county: "039",
      name: "Lane County",
      censusArea: 4553.119,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.132044, 43.440445],
          [-122.740765, 43.437142],
          [-122.741771, 43.544553],
          [-123.107475, 43.540004],
          [-123.137064, 43.60597],
          [-123.137677, 43.779666],
          [-123.347659, 43.780169],
          [-123.348016, 43.809165],
          [-123.470506, 43.810196],
          [-123.579681, 43.868138],
          [-123.6193, 43.92013],
          [-123.703461, 43.945131],
          [-123.827622, 43.945045],
          [-123.925513, 43.899167],
          [-123.92551, 43.865633],
          [-124.158684, 43.863504],
          [-124.150267, 43.91085],
          [-124.142704, 43.958182],
          [-124.133547, 44.035845],
          [-124.122406, 44.104442],
          [-124.125824, 44.12613],
          [-124.117006, 44.171913],
          [-124.114424, 44.198164],
          [-124.115671, 44.206554],
          [-124.111054, 44.235071],
          [-124.108945, 44.265475],
          [-124.109744, 44.270597],
          [-124.114869, 44.272721],
          [-124.115953, 44.274641],
          [-124.115849, 44.276277],
          [-123.77542, 44.283561],
          [-123.18398, 44.283691],
          [-123.165394, 44.200021],
          [-122.905594, 44.200247],
          [-122.864595, 44.287947],
          [-122.760494, 44.290548],
          [-122.577089, 44.228149],
          [-122.386174, 44.217149],
          [-122.326584, 44.253349],
          [-121.799359, 44.258138],
          [-121.76855, 44.101437],
          [-121.869269, 43.911893],
          [-121.92018, 43.913816],
          [-121.975479, 43.856875],
          [-121.960872, 43.763805],
          [-121.986187, 43.661633],
          [-121.964854, 43.626826],
          [-122.002675, 43.615228],
          [-122.130944, 43.557149],
          [-122.132044, 43.440445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41043",
      state: "41",
      county: "043",
      name: "Linn County",
      censusArea: 2290.129,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.260338, 44.555391],
          [-123.164428, 44.637507],
          [-123.073643, 44.657331],
          [-123.149058, 44.720277],
          [-123.150429, 44.727611],
          [-123.133529, 44.738662],
          [-123.143556, 44.74967],
          [-123.061509, 44.747952],
          [-123.006847, 44.686898],
          [-122.798369, 44.791564],
          [-122.619769, 44.78895],
          [-122.478373, 44.755551],
          [-122.288446, 44.753065],
          [-122.230559, 44.697867],
          [-122.154047, 44.719762],
          [-122.033233, 44.685678],
          [-121.794077, 44.68394],
          [-121.842667, 44.39244],
          [-121.799359, 44.258138],
          [-122.326584, 44.253349],
          [-122.386174, 44.217149],
          [-122.577089, 44.228149],
          [-122.760494, 44.290548],
          [-122.864595, 44.287947],
          [-122.905594, 44.200247],
          [-123.165394, 44.200021],
          [-123.18398, 44.283691],
          [-123.2436, 44.378545],
          [-123.205287, 44.443038],
          [-123.214683, 44.507681],
          [-123.260338, 44.555391]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41049",
      state: "41",
      county: "049",
      name: "Morrow County",
      censusArea: 2031.609,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.868135, 45.835962],
          [-119.802655, 45.84753],
          [-119.772927, 45.845578],
          [-119.669877, 45.856867],
          [-119.623393, 45.905639],
          [-119.600549, 45.919581],
          [-119.571584, 45.925456],
          [-119.524632, 45.908605],
          [-119.487829, 45.906307],
          [-119.432067, 45.913629],
          [-119.434639, 45.602771],
          [-119.248241, 45.601873],
          [-119.248276, 45.516102],
          [-119.14537, 45.516055],
          [-119.146402, 45.082963],
          [-119.163882, 44.995887],
          [-119.666708, 44.993525],
          [-119.671987, 44.994424],
          [-119.791055, 44.994636],
          [-119.790426, 45.067761],
          [-119.760125, 45.16758],
          [-119.883034, 45.169657],
          [-119.882037, 45.255956],
          [-120.00674, 45.257454],
          [-119.999506, 45.812199],
          [-119.868135, 45.835962]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41051",
      state: "41",
      county: "051",
      name: "Multnomah County",
      censusArea: 431.297,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.248993, 45.547745],
          [-122.2017, 45.564141],
          [-122.183695, 45.577696],
          [-122.14075, 45.584508],
          [-122.129548, 45.582945],
          [-122.12949, 45.582967],
          [-122.126197, 45.582573],
          [-122.126197, 45.582617],
          [-122.112356, 45.581409],
          [-122.101675, 45.583516],
          [-121.983038, 45.622812],
          [-121.922242, 45.648495],
          [-121.906742, 45.520497],
          [-121.820394, 45.461666],
          [-122.660979, 45.457819],
          [-122.652605, 45.445741],
          [-122.645205, 45.441641],
          [-122.651305, 45.436541],
          [-122.648725, 45.432718],
          [-122.744065, 45.433283],
          [-122.743809, 45.440429],
          [-122.743904, 45.442774],
          [-122.743633, 45.442959],
          [-122.743608, 45.447667],
          [-122.743807, 45.447902],
          [-122.74381, 45.452021],
          [-122.743613, 45.454868],
          [-122.743862, 45.519515],
          [-122.764458, 45.529727],
          [-122.929214, 45.633864],
          [-122.928961, 45.721502],
          [-122.762182, 45.728598],
          [-122.772511, 45.699637],
          [-122.774511, 45.680437],
          [-122.76381, 45.657138],
          [-122.738109, 45.644138],
          [-122.675008, 45.618039],
          [-122.643907, 45.609739],
          [-122.492259, 45.583281],
          [-122.380302, 45.575941],
          [-122.352802, 45.569441],
          [-122.331502, 45.548241],
          [-122.294901, 45.543541],
          [-122.266701, 45.543841],
          [-122.248993, 45.547745]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41059",
      state: "41",
      county: "059",
      name: "Umatilla County",
      censusArea: 3215.509,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.996911, 46.000787],
          [-117.977766, 46.000695],
          [-117.977688, 45.860558],
          [-117.972922, 45.860586],
          [-117.973666, 45.816978],
          [-118.045003, 45.817098],
          [-118.066823, 45.688365],
          [-118.117153, 45.688498],
          [-118.117053, 45.470754],
          [-118.198158, 45.470453],
          [-118.197957, 45.427153],
          [-118.428469, 45.428951],
          [-118.428268, 45.355151],
          [-118.697684, 45.345153],
          [-118.696782, 45.257653],
          [-118.655777, 45.196855],
          [-118.546678, 45.196951],
          [-118.548568, 45.080756],
          [-118.519063, 44.995956],
          [-119.163882, 44.995887],
          [-119.146402, 45.082963],
          [-119.14537, 45.516055],
          [-119.248276, 45.516102],
          [-119.248241, 45.601873],
          [-119.434639, 45.602771],
          [-119.432067, 45.913629],
          [-119.37613, 45.920974],
          [-119.322509, 45.933183],
          [-119.25715, 45.939926],
          [-119.225745, 45.932725],
          [-119.19553, 45.92787],
          [-119.169496, 45.927603],
          [-119.12612, 45.932859],
          [-119.093221, 45.942745],
          [-119.027056, 45.969134],
          [-119.008558, 45.97927],
          [-118.987129, 45.999855],
          [-118.67787, 46.000935],
          [-118.658717, 46.000955],
          [-118.639332, 46.000994],
          [-118.637725, 46.00097],
          [-118.579906, 46.000818],
          [-118.57571, 46.000718],
          [-118.569392, 46.000773],
          [-118.537119, 46.00084],
          [-118.497027, 46.00062],
          [-118.470756, 46.000632],
          [-118.37836, 46.000574],
          [-118.36779, 46.000622],
          [-118.314982, 46.000453],
          [-118.256368, 46.000439],
          [-118.25253, 46.000459],
          [-118.236584, 46.000418],
          [-118.228941, 46.000421],
          [-118.131019, 46.00028],
          [-118.126197, 46.000282],
          [-117.996911, 46.000787]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41065",
      state: "41",
      county: "065",
      name: "Wasco County",
      censusArea: 2381.519,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.734167, 44.885782],
          [-121.805051, 45.01131],
          [-121.659355, 45.066511],
          [-121.745966, 45.186272],
          [-121.701557, 45.230198],
          [-121.682607, 45.228988],
          [-121.681033, 45.250788],
          [-121.696808, 45.258017],
          [-121.481527, 45.258279],
          [-121.482525, 45.519619],
          [-121.440612, 45.519301],
          [-121.441045, 45.69727],
          [-121.423592, 45.69399],
          [-121.401739, 45.692887],
          [-121.372574, 45.703111],
          [-121.33777, 45.704949],
          [-121.312198, 45.699925],
          [-121.215779, 45.671238],
          [-121.200367, 45.649829],
          [-121.195233, 45.629513],
          [-121.086675, 45.646667],
          [-121.06437, 45.652549],
          [-120.943977, 45.656445],
          [-120.915876, 45.641345],
          [-120.882022, 45.527597],
          [-120.825275, 45.513788],
          [-120.868569, 45.401146],
          [-120.956471, 45.310345],
          [-121.031202, 45.285857],
          [-121.02408, 45.218726],
          [-120.889357, 45.202305],
          [-120.723569, 45.130911],
          [-120.729757, 45.083847],
          [-120.503549, 45.08295],
          [-120.494548, 45.07465],
          [-120.495247, 45.068549],
          [-120.455389, 44.866046],
          [-120.371422, 44.821568],
          [-121.75948, 44.82564],
          [-121.749381, 44.83354],
          [-121.742081, 44.84534],
          [-121.742481, 44.85314],
          [-121.751131, 44.85699],
          [-121.753182, 44.86124],
          [-121.745582, 44.87544],
          [-121.733782, 44.88174],
          [-121.734167, 44.885782]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41071",
      state: "41",
      county: "071",
      name: "Yamhill County",
      censusArea: 715.86,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.849723, 45.259654],
          [-122.900435, 45.257063],
          [-122.962688, 45.284254],
          [-123.040165, 45.221961],
          [-122.996959, 45.11979],
          [-123.069955, 45.075108],
          [-123.724213, 45.076098],
          [-123.784794, 45.076315],
          [-123.78441, 45.216034],
          [-123.463278, 45.216269],
          [-123.464932, 45.43384],
          [-123.135329, 45.433302],
          [-122.868009, 45.317376],
          [-122.867816, 45.259585],
          [-122.849723, 45.259654]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27063",
      state: "27",
      county: "063",
      name: "Jackson County",
      censusArea: 702.982,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.375269, 43.500322],
          [-95.387787, 43.50024],
          [-95.387812, 43.50024],
          [-95.387851, 43.50024],
          [-95.434199, 43.500314],
          [-95.434293, 43.50036],
          [-95.454433, 43.500644],
          [-95.452502, 43.847953],
          [-94.859388, 43.848093],
          [-94.854444, 43.848099],
          [-94.854555, 43.500614],
          [-94.857867, 43.500615],
          [-94.860192, 43.500546],
          [-94.872725, 43.500564],
          [-94.874235, 43.500557],
          [-94.887291, 43.500502],
          [-94.914523, 43.50045],
          [-94.914634, 43.50045],
          [-94.914905, 43.50045],
          [-94.914955, 43.50045],
          [-94.934625, 43.50049],
          [-94.954477, 43.500467],
          [-94.974359, 43.500508],
          [-94.99446, 43.500523],
          [-95.014245, 43.500872],
          [-95.034, 43.500811],
          [-95.053504, 43.500769],
          [-95.054289, 43.50086],
          [-95.114874, 43.500667],
          [-95.122633, 43.500755],
          [-95.167294, 43.500771],
          [-95.167891, 43.500885],
          [-95.180423, 43.500774],
          [-95.214938, 43.500885],
          [-95.250762, 43.500406],
          [-95.250969, 43.500464],
          [-95.374737, 43.500314],
          [-95.375269, 43.500322]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27065",
      state: "27",
      county: "065",
      name: "Kanabec County",
      censusArea: 521.586,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.141793, 45.730657],
          [-93.404626, 45.731324],
          [-93.513683, 45.734318],
          [-93.51808, 45.981789],
          [-93.432492, 45.981398],
          [-93.431831, 46.153991],
          [-93.053871, 46.15812],
          [-93.055081, 45.981233],
          [-93.142869, 45.981005],
          [-93.141793, 45.730657]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27069",
      state: "27",
      county: "069",
      name: "Kittson County",
      censusArea: 1098.804,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.16305, 48.543963],
          [-97.092606, 48.682033],
          [-97.137571, 48.749191],
          [-97.157797, 48.78768],
          [-97.199549, 48.881183],
          [-97.23261, 48.946561],
          [-97.239155, 48.967596],
          [-97.238387, 48.982631],
          [-97.234005, 48.99747],
          [-97.229039, 49.000687],
          [-96.405412, 48.99982],
          [-96.387835, 48.544308],
          [-97.16305, 48.543963]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29105",
      state: "29",
      county: "105",
      name: "Laclede County",
      censusArea: 764.717,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.249463, 37.604543],
          [-92.252261, 37.472944],
          [-92.686671, 37.481545],
          [-92.852107, 37.484083],
          [-92.853481, 37.48397],
          [-92.855384, 37.895736],
          [-92.620181, 37.892521],
          [-92.548934, 37.80047],
          [-92.409231, 37.85918],
          [-92.413669, 37.713941],
          [-92.248865, 37.648442],
          [-92.249463, 37.604543]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29111",
      state: "29",
      county: "111",
      name: "Lewis County",
      censusArea: 505.042,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.455887, 39.945538],
          [-91.840353, 39.948235],
          [-91.952726, 39.949426],
          [-91.950812, 40.25721],
          [-91.501435, 40.248874],
          [-91.505968, 40.234305],
          [-91.510332, 40.201142],
          [-91.512974, 40.181062],
          [-91.510322, 40.127994],
          [-91.494865, 40.037421],
          [-91.460287, 39.980333],
          [-91.455887, 39.945538]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29119",
      state: "29",
      county: "119",
      name: "Mc Donald County",
      censusArea: 539.48,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.618019, 36.667921],
          [-94.618025, 36.66943],
          [-94.61813, 36.701423],
          [-94.618307, 36.76656],
          [-94.068898, 36.747818],
          [-94.077089, 36.49873],
          [-94.098588, 36.498676],
          [-94.100252, 36.49867],
          [-94.110673, 36.498587],
          [-94.111473, 36.498597],
          [-94.519478, 36.499214],
          [-94.55929, 36.499496],
          [-94.617919, 36.499414],
          [-94.617877, 36.514999],
          [-94.617883, 36.517799],
          [-94.617997, 36.53428],
          [-94.617868, 36.53609],
          [-94.617897, 36.536983],
          [-94.617814, 36.577732],
          [-94.617853, 36.59912],
          [-94.617865, 36.606851],
          [-94.617815, 36.612604],
          [-94.618019, 36.667921]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29125",
      state: "29",
      county: "125",
      name: "Maries County",
      censusArea: 526.979,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.18537, 38.016338],
          [-92.195675, 38.292237],
          [-91.644729, 38.28851],
          [-91.644893, 38.153679],
          [-91.630554, 38.153519],
          [-91.632431, 38.050951],
          [-91.895347, 38.051199],
          [-92.021165, 38.010639],
          [-92.18537, 38.015138],
          [-92.18537, 38.016338]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29129",
      state: "29",
      county: "129",
      name: "Mercer County",
      censusArea: 453.835,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.774344, 40.577584],
          [-93.770231, 40.577615],
          [-93.750223, 40.57772],
          [-93.742759, 40.577518],
          [-93.737259, 40.577542],
          [-93.728355, 40.577547],
          [-93.722443, 40.577641],
          [-93.690333, 40.577875],
          [-93.677099, 40.578127],
          [-93.668845, 40.578241],
          [-93.661913, 40.578354],
          [-93.659272, 40.57833],
          [-93.656211, 40.578352],
          [-93.597352, 40.579496],
          [-93.566189, 40.580117],
          [-93.56581, 40.580075],
          [-93.56524, 40.580143],
          [-93.560798, 40.580304],
          [-93.558938, 40.580189],
          [-93.556899, 40.580235],
          [-93.553986, 40.580303],
          [-93.548284, 40.580417],
          [-93.528177, 40.580367],
          [-93.527607, 40.580436],
          [-93.524124, 40.580481],
          [-93.466887, 40.580072],
          [-93.465297, 40.580164],
          [-93.441767, 40.579916],
          [-93.374386, 40.580334],
          [-93.366935, 40.382999],
          [-93.367214, 40.266314],
          [-93.763324, 40.263988],
          [-93.774344, 40.577584]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29139",
      state: "29",
      county: "139",
      name: "Montgomery County",
      censusArea: 536.247,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.260232, 39.139845],
          [-91.264287, 38.992762],
          [-91.264927, 38.843833],
          [-91.415832, 38.8453],
          [-91.418637, 38.709778],
          [-91.545313, 38.676882],
          [-91.640372, 38.703792],
          [-91.641376, 38.703939],
          [-91.645546, 38.703678],
          [-91.647171, 38.703396],
          [-91.633998, 39.059057],
          [-91.629598, 39.147853],
          [-91.409033, 39.142542],
          [-91.260232, 39.139845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29141",
      state: "29",
      county: "141",
      name: "Morgan County",
      censusArea: 597.631,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.695886, 38.222031],
          [-92.764963, 38.189534],
          [-92.979558, 38.197734],
          [-93.077692, 38.263092],
          [-93.067294, 38.529995],
          [-93.059974, 38.693077],
          [-92.950502, 38.686028],
          [-92.839837, 38.683974],
          [-92.841871, 38.596792],
          [-92.625189, 38.430778],
          [-92.633756, 38.348082],
          [-92.689834, 38.348472],
          [-92.695886, 38.222031]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29147",
      state: "29",
      county: "147",
      name: "Nodaway County",
      censusArea: 876.964,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.04308, 40.130096],
          [-95.071703, 40.260859],
          [-95.180614, 40.261706],
          [-95.177708, 40.478903],
          [-95.202264, 40.578528],
          [-95.164058, 40.578017],
          [-95.154499, 40.57786],
          [-95.120829, 40.577413],
          [-95.112222, 40.577228],
          [-95.110663, 40.577206],
          [-95.110303, 40.57716],
          [-95.107213, 40.577116],
          [-95.097607, 40.577168],
          [-95.079742, 40.577007],
          [-95.068921, 40.57688],
          [-94.966491, 40.575839],
          [-94.955134, 40.575669],
          [-94.914896, 40.575068],
          [-94.901451, 40.574877],
          [-94.896801, 40.574738],
          [-94.823758, 40.573942],
          [-94.819978, 40.573714],
          [-94.811188, 40.573532],
          [-94.773988, 40.572977],
          [-94.716665, 40.572201],
          [-94.714925, 40.572201],
          [-94.682601, 40.571787],
          [-94.632035, 40.571186],
          [-94.598487, 40.386714],
          [-94.605593, 40.127767],
          [-94.605942, 40.126127],
          [-95.04308, 40.130096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29153",
      state: "29",
      county: "153",
      name: "Ozark County",
      censusArea: 744.972,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.112346, 36.794231],
          [-92.120415, 36.498863],
          [-92.137741, 36.498706],
          [-92.150295, 36.498634],
          [-92.199396, 36.498351],
          [-92.211449, 36.498395],
          [-92.214143, 36.498372],
          [-92.216412, 36.498417],
          [-92.309424, 36.497894],
          [-92.318415, 36.497711],
          [-92.529145, 36.497739],
          [-92.772333, 36.497772],
          [-92.772341, 36.497772],
          [-92.764869, 36.806097],
          [-92.112346, 36.794231]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29157",
      state: "29",
      county: "157",
      name: "Perry County",
      censusArea: 474.354,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.521523, 37.566208],
          [-89.603574, 37.592056],
          [-89.861123, 37.599286],
          [-90.146763, 37.597434],
          [-90.147306, 37.613546],
          [-90.146778, 37.641618],
          [-90.110537, 37.67134],
          [-89.999768, 37.821744],
          [-89.938191, 37.875111],
          [-89.937383, 37.874693],
          [-89.923185, 37.870672],
          [-89.901832, 37.869822],
          [-89.881475, 37.879591],
          [-89.862949, 37.896906],
          [-89.851048, 37.90398],
          [-89.844786, 37.905572],
          [-89.739873, 37.84693],
          [-89.674304, 37.803179],
          [-89.671255, 37.801144],
          [-89.515436, 37.67137],
          [-89.506563, 37.62505],
          [-89.511936, 37.584564],
          [-89.518007, 37.583962],
          [-89.521274, 37.578971],
          [-89.521407, 37.572143],
          [-89.521523, 37.566208]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29161",
      state: "29",
      county: "161",
      name: "Phelps County",
      censusArea: 671.784,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.809105, 37.598863],
          [-92.029258, 37.602542],
          [-92.021165, 38.010639],
          [-91.895347, 38.051199],
          [-91.632431, 38.050951],
          [-91.630554, 38.153519],
          [-91.589527, 38.153249],
          [-91.580252, 38.152793],
          [-91.538016, 38.15249],
          [-91.534384, 38.152593],
          [-91.525025, 38.152433],
          [-91.528797, 37.788989],
          [-91.806219, 37.791286],
          [-91.809105, 37.598863]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29167",
      state: "29",
      county: "167",
      name: "Polk County",
      censusArea: 635.523,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.621153, 37.427423],
          [-93.620379, 37.456289],
          [-93.616033, 37.572689],
          [-93.628404, 37.829435],
          [-93.573202, 37.828035],
          [-93.574103, 37.813336],
          [-93.187915, 37.802737],
          [-93.175222, 37.67489],
          [-93.182489, 37.417204],
          [-93.621153, 37.427423]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37095",
      state: "37",
      county: "095",
      name: "Hyde County",
      censusArea: 612.7,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.869869, 35.582743],
          [-75.895045, 35.573152],
          [-75.906848, 35.559101],
          [-75.908534, 35.555166],
          [-75.908534, 35.546174],
          [-75.916403, 35.538305],
          [-75.94563, 35.53437],
          [-75.950126, 35.530998],
          [-75.954623, 35.526502],
          [-75.964178, 35.511326],
          [-75.96474, 35.504582],
          [-75.961929, 35.496713],
          [-75.963053, 35.493903],
          [-75.987222, 35.484348],
          [-75.995652, 35.475355],
          [-75.994528, 35.463552],
          [-75.997901, 35.453435],
          [-76.009704, 35.442194],
          [-76.012514, 35.432639],
          [-76.01139, 35.423084],
          [-76.014762, 35.416902],
          [-76.020945, 35.410719],
          [-76.025441, 35.408471],
          [-76.037244, 35.414091],
          [-76.050171, 35.415778],
          [-76.059726, 35.410157],
          [-76.063661, 35.405099],
          [-76.063661, 35.398354],
          [-76.06085, 35.392733],
          [-76.059726, 35.383741],
          [-76.069281, 35.370813],
          [-76.092887, 35.361259],
          [-76.123238, 35.351142],
          [-76.132793, 35.349455],
          [-76.14291, 35.338776],
          [-76.14291, 35.32866],
          [-76.149655, 35.326411],
          [-76.165392, 35.328659],
          [-76.168764, 35.332032],
          [-76.182254, 35.336528],
          [-76.20586, 35.336528],
          [-76.235087, 35.350017],
          [-76.253072, 35.350017],
          [-76.257569, 35.344397],
          [-76.265437, 35.343273],
          [-76.282299, 35.345521],
          [-76.304781, 35.355638],
          [-76.327263, 35.356762],
          [-76.335132, 35.355638],
          [-76.340752, 35.346645],
          [-76.349745, 35.345521],
          [-76.365483, 35.348893],
          [-76.374475, 35.355638],
          [-76.382344, 35.356762],
          [-76.387965, 35.356762],
          [-76.399206, 35.348893],
          [-76.408199, 35.350017],
          [-76.409323, 35.35339],
          [-76.420564, 35.35901],
          [-76.431805, 35.362383],
          [-76.436301, 35.37812],
          [-76.448666, 35.383741],
          [-76.455411, 35.383741],
          [-76.462156, 35.380368],
          [-76.472273, 35.371375],
          [-76.485762, 35.371375],
          [-76.499251, 35.381492],
          [-76.521733, 35.391609],
          [-76.546901, 35.389673],
          [-76.599085, 35.51996],
          [-76.467761, 35.556504],
          [-76.564128, 35.601054],
          [-76.638031, 35.705233],
          [-76.546091, 35.705033],
          [-76.395283, 35.697469],
          [-76.269325, 35.690722],
          [-76.297845, 35.609052],
          [-76.214298, 35.597993],
          [-76.143577, 35.702741],
          [-76.045987, 35.666117],
          [-75.901164, 35.668998],
          [-75.869869, 35.582743]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37099",
      state: "37",
      county: "099",
      name: "Jackson County",
      censusArea: 490.755,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.920819, 35.291868],
          [-82.983329, 35.131904],
          [-83.055352, 35.053865],
          [-83.008447, 35.026974],
          [-83.108535, 35.000771],
          [-83.108714, 35.000768],
          [-83.339707, 35.331004],
          [-83.351373, 35.468354],
          [-83.186052, 35.514573],
          [-82.996829, 35.372796],
          [-82.920819, 35.291868]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37105",
      state: "37",
      county: "105",
      name: "Lee County",
      censusArea: 254.959,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.183301, 35.306976],
          [-79.273565, 35.353369],
          [-79.350009, 35.517991],
          [-79.118958, 35.628215],
          [-78.969761, 35.521433],
          [-79.183301, 35.306976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37111",
      state: "37",
      county: "111",
      name: "Mc Dowell County",
      censusArea: 440.608,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.824184, 35.574955],
          [-81.973556, 35.524565],
          [-82.002851, 35.551802],
          [-82.169018, 35.527841],
          [-82.290797, 35.591288],
          [-82.275394, 35.704423],
          [-82.197226, 35.736369],
          [-82.133182, 35.824446],
          [-81.981723, 35.91152],
          [-81.954501, 35.926742],
          [-81.962957, 35.941669],
          [-81.943674, 35.959448],
          [-81.906607, 35.883437],
          [-81.987035, 35.806936],
          [-81.869347, 35.719574],
          [-81.824184, 35.574955]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37115",
      state: "37",
      county: "115",
      name: "Madison County",
      censusArea: 449.57,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.898506, 35.9451],
          [-82.898505, 35.945101],
          [-82.874159, 35.952698],
          [-82.852554, 35.949089],
          [-82.830112, 35.932972],
          [-82.778625, 35.974792],
          [-82.754465, 36.004304],
          [-82.715365, 36.024253],
          [-82.683565, 36.046104],
          [-82.637165, 36.065805],
          [-82.632265, 36.065705],
          [-82.619205, 36.056556],
          [-82.602877, 36.039833],
          [-82.615062, 36.000306],
          [-82.611262, 35.973155],
          [-82.596774, 35.964987],
          [-82.557529, 35.954671],
          [-82.512598, 35.975664],
          [-82.507068, 35.977475],
          [-82.50586, 35.978342],
          [-82.408728, 35.818061],
          [-82.514519, 35.803194],
          [-82.806917, 35.685686],
          [-82.883665, 35.67819],
          [-82.964108, 35.7941],
          [-82.962842, 35.795126],
          [-82.918356, 35.845467],
          [-82.89798, 35.881278],
          [-82.913338, 35.924113],
          [-82.898506, 35.9451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37121",
      state: "37",
      county: "121",
      name: "Mitchell County",
      censusArea: 221.425,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.416857, 36.072885],
          [-82.409458, 36.083409],
          [-82.375558, 36.105609],
          [-82.371383, 36.106388],
          [-82.366566, 36.10765],
          [-82.360919, 36.110614],
          [-82.348422, 36.115929],
          [-82.329177, 36.117427],
          [-82.325169, 36.119363],
          [-82.237737, 36.139189],
          [-82.236415, 36.139926],
          [-82.235479, 36.140748],
          [-82.234807, 36.14172],
          [-82.222052, 36.156911],
          [-82.211251, 36.159012],
          [-82.147948, 36.149516],
          [-82.136547, 36.128817],
          [-82.137974, 36.119576],
          [-82.130646, 36.106417],
          [-82.127146, 36.104417],
          [-82.080518, 36.10571],
          [-82.034192, 35.942107],
          [-81.981723, 35.91152],
          [-82.133182, 35.824446],
          [-82.199347, 36.015792],
          [-82.338245, 36.030294],
          [-82.416857, 36.072885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37129",
      state: "37",
      county: "129",
      name: "New Hanover County",
      censusArea: 191.534,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.937313, 33.928645],
          [-77.951643, 34.232368],
          [-78.029922, 34.331768],
          [-77.822995, 34.389359],
          [-77.715916, 34.292719],
          [-77.740136, 34.272546],
          [-77.764022, 34.245641],
          [-77.829209, 34.162618],
          [-77.841785, 34.140747],
          [-77.870327, 34.079221],
          [-77.874384, 34.075671],
          [-77.878161, 34.067963],
          [-77.915536, 33.971723],
          [-77.927926, 33.945265],
          [-77.937313, 33.928645]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37135",
      state: "37",
      county: "135",
      name: "Orange County",
      censusArea: 397.958,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.259481, 36.137273],
          [-79.256964, 36.243866],
          [-79.153733, 36.242117],
          [-78.95079, 36.23916],
          [-79.016699, 35.863201],
          [-79.249475, 35.876788],
          [-79.259481, 36.137273]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37139",
      state: "37",
      county: "139",
      name: "Pasquotank County",
      censusArea: 226.88,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.491477, 36.510536],
          [-76.231218, 36.371712],
          [-76.216558, 36.298174],
          [-76.0471, 36.183106],
          [-76.051626, 36.181325],
          [-76.063498, 36.173518],
          [-76.065287, 36.170591],
          [-76.06545, 36.165224],
          [-76.05992, 36.15514],
          [-76.05927, 36.149285],
          [-76.064224, 36.143775],
          [-76.071672, 36.140183],
          [-76.092555, 36.135794],
          [-76.178946, 36.123424],
          [-76.184133, 36.121562],
          [-76.19051, 36.116333],
          [-76.289807, 36.222955],
          [-76.375078, 36.258853],
          [-76.45369, 36.378042],
          [-76.491405, 36.468648],
          [-76.491477, 36.510536]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37143",
      state: "37",
      county: "143",
      name: "Perquimans County",
      censusArea: 247.089,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.45369, 36.378042],
          [-76.375078, 36.258853],
          [-76.289807, 36.222955],
          [-76.19051, 36.116333],
          [-76.190784, 36.116108],
          [-76.19238, 36.113448],
          [-76.191715, 36.107197],
          [-76.216599, 36.095409],
          [-76.238712, 36.098568],
          [-76.265037, 36.104886],
          [-76.28715, 36.104886],
          [-76.298733, 36.1012],
          [-76.303998, 36.092776],
          [-76.323478, 36.084879],
          [-76.331902, 36.083826],
          [-76.337168, 36.086458],
          [-76.355069, 36.086458],
          [-76.410878, 36.078034],
          [-76.411844, 36.075376],
          [-76.573374, 36.112266],
          [-76.589897, 36.239009],
          [-76.559674, 36.351892],
          [-76.556837, 36.355186],
          [-76.539147, 36.357238],
          [-76.45369, 36.378042]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37147",
      state: "37",
      county: "147",
      name: "Pitt County",
      censusArea: 651.975,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.470067, 35.420157],
          [-77.472532, 35.425518],
          [-77.476, 35.426684],
          [-77.509307, 35.519569],
          [-77.700502, 35.652256],
          [-77.688822, 35.660166],
          [-77.661312, 35.677321],
          [-77.538364, 35.750162],
          [-77.516479, 35.760866],
          [-77.390227, 35.83292],
          [-77.350602, 35.819214],
          [-77.174034, 35.733026],
          [-77.173788, 35.635336],
          [-77.087521, 35.564047],
          [-77.175418, 35.474071],
          [-77.17394, 35.456715],
          [-77.179935, 35.455811],
          [-77.181276, 35.440975],
          [-77.19123, 35.434744],
          [-77.189107, 35.426036],
          [-77.190423, 35.418955],
          [-77.295879, 35.379163],
          [-77.301155, 35.374239],
          [-77.353981, 35.327988],
          [-77.357402, 35.328827],
          [-77.361188, 35.331852],
          [-77.365484, 35.333651],
          [-77.374683, 35.333825],
          [-77.383581, 35.33536],
          [-77.390965, 35.340047],
          [-77.427046, 35.350424],
          [-77.470067, 35.420157]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37153",
      state: "37",
      county: "153",
      name: "Richmond County",
      censusArea: 473.821,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.744116, 34.805651],
          [-79.744925, 34.805686],
          [-79.772829, 34.805954],
          [-79.773607, 34.805931],
          [-79.927618, 34.806555],
          [-79.84999, 34.899317],
          [-79.875716, 35.037936],
          [-79.927887, 35.117713],
          [-79.999268, 35.085391],
          [-80.07539, 35.143081],
          [-79.851762, 35.183418],
          [-79.614306, 35.163679],
          [-79.575384, 35.069815],
          [-79.459176, 35.043096],
          [-79.572684, 34.991968],
          [-79.561826, 34.910363],
          [-79.692948, 34.804973],
          [-79.744116, 34.805651]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42085",
      state: "42",
      county: "085",
      name: "Mercer County",
      censusArea: 672.575,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.99978, 41.274178],
          [-79.999779, 41.171861],
          [-80.096861, 41.069804],
          [-80.147164, 41.113795],
          [-80.385094, 41.126492],
          [-80.519012, 41.125093],
          [-80.519012, 41.125116],
          [-80.519167, 41.133343],
          [-80.519167, 41.133388],
          [-80.519115, 41.14552],
          [-80.519144, 41.171203],
          [-80.51883, 41.209213],
          [-80.518893, 41.219356],
          [-80.518893, 41.219357],
          [-80.518893, 41.232556],
          [-80.518693, 41.248855],
          [-80.518893, 41.265155],
          [-80.518993, 41.268155],
          [-80.518996, 41.2683],
          [-80.518794, 41.305509],
          [-80.519129, 41.312408],
          [-80.519265, 41.333495],
          [-80.519281, 41.335958],
          [-80.519281, 41.337145],
          [-80.519281, 41.337174],
          [-80.519311, 41.339052],
          [-80.519293, 41.339054],
          [-80.519293, 41.339654],
          [-80.519345, 41.340145],
          [-80.519345, 41.34074],
          [-80.519297, 41.350833],
          [-80.519249, 41.36103],
          [-80.519217, 41.372006],
          [-80.519249, 41.378918],
          [-80.519025, 41.416438],
          [-80.518993, 41.416437],
          [-80.518993, 41.435454],
          [-80.519169, 41.462581],
          [-80.519209, 41.489013],
          [-80.125316, 41.480395],
          [-79.999779, 41.490049],
          [-79.99978, 41.274178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42089",
      state: "42",
      county: "089",
      name: "Monroe County",
      censusArea: 608.286,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.600736, 41.161497],
          [-75.505607, 41.232539],
          [-75.359184, 41.239206],
          [-75.129721, 41.252101],
          [-75.156507, 41.150341],
          [-74.980674, 41.078178],
          [-75.026003, 41.042687],
          [-75.036982, 41.034702],
          [-75.051794, 41.027142],
          [-75.069277, 41.019348],
          [-75.091377, 41.012283],
          [-75.109114, 41.004102],
          [-75.130575, 40.991093],
          [-75.135526, 40.973807],
          [-75.131364, 40.969277],
          [-75.120564, 40.968313],
          [-75.300594, 40.859563],
          [-75.474193, 40.814746],
          [-75.57459, 40.951323],
          [-75.49599, 40.987181],
          [-75.649637, 41.122344],
          [-75.634978, 41.123192],
          [-75.615557, 41.150001],
          [-75.603002, 41.151914],
          [-75.600736, 41.161497]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42095",
      state: "42",
      county: "095",
      name: "Northampton County",
      censusArea: 369.671,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.474193, 40.814746],
          [-75.300594, 40.859563],
          [-75.120564, 40.968313],
          [-75.120435, 40.968302],
          [-75.120316, 40.96263],
          [-75.119893, 40.961646],
          [-75.117764, 40.953023],
          [-75.052538, 40.872051],
          [-75.051029, 40.865662],
          [-75.053294, 40.8599],
          [-75.108505, 40.791094],
          [-75.1344, 40.773765],
          [-75.149378, 40.774786],
          [-75.16365, 40.778386],
          [-75.171587, 40.777745],
          [-75.196533, 40.751631],
          [-75.20392, 40.691498],
          [-75.200708, 40.618356],
          [-75.196803, 40.60858],
          [-75.333514, 40.537057],
          [-75.404412, 40.581154],
          [-75.430404, 40.674152],
          [-75.481469, 40.655744],
          [-75.608985, 40.787387],
          [-75.474193, 40.814746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42101",
      state: "42",
      county: "101",
      name: "Philadelphia County",
      censusArea: 134.101,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.264431, 40.054094],
          [-75.176438, 40.084649],
          [-75.151596, 40.070328],
          [-75.135193, 40.061031],
          [-75.129021, 40.057091],
          [-75.124916, 40.054684],
          [-75.109406, 40.04584],
          [-75.087425, 40.063599],
          [-75.096733, 40.068989],
          [-75.015066, 40.137992],
          [-75.00853, 40.133992],
          [-75.005946, 40.132524],
          [-74.981947, 40.120245],
          [-74.964991, 40.118635],
          [-74.961342, 40.097496],
          [-74.956164, 40.095745],
          [-74.958841, 40.082503],
          [-74.971268, 40.073744],
          [-74.984893, 40.057287],
          [-74.97429, 40.048872],
          [-74.974713, 40.048711],
          [-75.014343, 40.020976],
          [-75.056039, 39.991795],
          [-75.072017, 39.980612],
          [-75.085754, 39.967572],
          [-75.13322, 39.922512],
          [-75.153925, 39.906144],
          [-75.184952, 39.881615],
          [-75.210425, 39.865913],
          [-75.210972, 39.865706],
          [-75.215722, 39.876087],
          [-75.24047, 39.872381],
          [-75.262539, 39.876578],
          [-75.276482, 39.976959],
          [-75.263687, 39.982658],
          [-75.256119, 39.987301],
          [-75.249214, 39.990547],
          [-75.264431, 40.054094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42109",
      state: "42",
      county: "109",
      name: "Snyder County",
      censusArea: 328.705,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.800242, 40.881994],
          [-76.798714, 40.878366],
          [-76.798921, 40.876901],
          [-76.799523, 40.874749],
          [-76.801561, 40.870634],
          [-76.806246, 40.862535],
          [-76.808366, 40.857369],
          [-76.811228, 40.851712],
          [-76.812242, 40.850305],
          [-76.812728, 40.847457],
          [-76.817522, 40.843782],
          [-76.820895, 40.839898],
          [-76.825648, 40.835503],
          [-76.829685, 40.831029],
          [-76.836338, 40.825761],
          [-76.840626, 40.821324],
          [-76.845395, 40.815175],
          [-76.84928, 40.809755],
          [-76.84966, 40.808205],
          [-76.848912, 40.805989],
          [-76.852163, 40.800839],
          [-76.856094, 40.791211],
          [-76.856292, 40.789697],
          [-76.858703, 40.781521],
          [-76.858513, 40.779416],
          [-76.859252, 40.777043],
          [-76.858352, 40.769536],
          [-76.857522, 40.766],
          [-76.857719, 40.764374],
          [-76.857209, 40.759446],
          [-76.856445, 40.75581],
          [-76.857521, 40.749612],
          [-76.856681, 40.747311],
          [-76.856979, 40.746001],
          [-76.855995, 40.737931],
          [-76.855523, 40.737119],
          [-76.855362, 40.728821],
          [-76.939749, 40.638378],
          [-77.035549, 40.676918],
          [-77.198923, 40.682511],
          [-77.287941, 40.693595],
          [-77.354097, 40.701667],
          [-77.356628, 40.807334],
          [-76.940706, 40.888023],
          [-76.800242, 40.881994]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42111",
      state: "42",
      county: "111",
      name: "Somerset County",
      censusArea: 1074.374,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.768593, 40.243441],
          [-78.760311, 40.243171],
          [-78.658409, 40.242933],
          [-78.763206, 40.057005],
          [-78.757241, 39.823484],
          [-78.808387, 39.722726],
          [-78.931175, 39.722775],
          [-78.931176, 39.722775],
          [-79.392458, 39.72134],
          [-79.365695, 39.820546],
          [-79.417558, 39.853776],
          [-79.347363, 39.969492],
          [-79.330397, 39.992796],
          [-79.32525, 39.999872],
          [-79.302801, 40.0285],
          [-79.301533, 40.02986],
          [-79.296131, 40.03675],
          [-79.294283, 40.039429],
          [-79.293682, 40.040413],
          [-79.185222, 40.109848],
          [-79.088656, 40.241259],
          [-79.086174, 40.242665],
          [-79.086734, 40.245342],
          [-79.082472, 40.250074],
          [-79.07925, 40.251172],
          [-79.078084, 40.25484],
          [-79.07957, 40.257928],
          [-79.076638, 40.260307],
          [-79.073523, 40.266727],
          [-79.067963, 40.272471],
          [-79.059378, 40.275767],
          [-79.056646, 40.278604],
          [-79.055983, 40.285089],
          [-78.927601, 40.285041],
          [-78.884862, 40.244631],
          [-78.768593, 40.243441]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42115",
      state: "42",
      county: "115",
      name: "Susquehanna County",
      censusArea: 823.435,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.719887, 41.642263],
          [-76.115172, 41.651825],
          [-76.145519, 41.998913],
          [-76.131201, 41.998954],
          [-76.123696, 41.998954],
          [-76.10584, 41.998858],
          [-75.983082, 41.999035],
          [-75.98025, 41.999035],
          [-75.483738, 41.999244],
          [-75.48315, 41.999259],
          [-75.462411, 41.641587],
          [-75.469259, 41.64141],
          [-75.719887, 41.642263]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42119",
      state: "42",
      county: "119",
      name: "Union County",
      censusArea: 315.982,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.356628, 40.807334],
          [-77.357113, 40.844484],
          [-77.36418, 40.846937],
          [-77.14416, 41.044338],
          [-77.143723, 41.04578],
          [-77.144111, 41.06884],
          [-76.977939, 41.087883],
          [-76.896114, 41.13907],
          [-76.88228, 40.971334],
          [-76.800242, 40.881994],
          [-76.940706, 40.888023],
          [-77.356628, 40.807334]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42121",
      state: "42",
      county: "121",
      name: "Venango County",
      censusArea: 674.284,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.477822, 41.386479],
          [-79.47814, 41.335708],
          [-79.604161, 41.321478],
          [-79.694984, 41.172865],
          [-79.999779, 41.171861],
          [-79.99978, 41.274178],
          [-79.999779, 41.490049],
          [-79.828876, 41.622057],
          [-79.748047, 41.617747],
          [-79.612866, 41.617058],
          [-79.612873, 41.623858],
          [-79.51207, 41.624559],
          [-79.512276, 41.46524],
          [-79.477822, 41.386479]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42125",
      state: "42",
      county: "125",
      name: "Washington County",
      censusArea: 856.989,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.519217, 39.962199],
          [-80.519218, 39.962424],
          [-80.519207, 39.963381],
          [-80.51912, 40.01641],
          [-80.519008, 40.077001],
          [-80.51896, 40.078089],
          [-80.519104, 40.159672],
          [-80.519056, 40.172744],
          [-80.519056, 40.172771],
          [-80.517991, 40.398868],
          [-80.517998, 40.399644],
          [-80.518692, 40.477365],
          [-80.448114, 40.477717],
          [-80.360873, 40.477539],
          [-80.183129, 40.332515],
          [-80.033712, 40.288034],
          [-79.952098, 40.240877],
          [-79.964764, 40.23767],
          [-79.970652, 40.231814],
          [-79.968335, 40.22382],
          [-79.956073, 40.213582],
          [-79.927883, 40.206822],
          [-79.912586, 40.197767],
          [-79.893281, 40.194352],
          [-79.888986, 40.194823],
          [-79.881913, 40.196519],
          [-79.870585, 40.197415],
          [-79.877385, 40.126792],
          [-79.877048, 40.036826],
          [-79.998014, 39.983322],
          [-80.157339, 39.999581],
          [-80.379505, 39.994125],
          [-80.519217, 39.962199]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42133",
      state: "42",
      county: "133",
      name: "York County",
      censusArea: 904.181,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.72162, 40.12007],
          [-76.663825, 40.063227],
          [-76.534524, 40.051348],
          [-76.47358, 39.947352],
          [-76.239805, 39.721305],
          [-76.380083, 39.721304],
          [-76.380583, 39.721304],
          [-76.395583, 39.721204],
          [-76.418684, 39.721304],
          [-76.418784, 39.721204],
          [-76.491887, 39.721304],
          [-76.517087, 39.721304],
          [-76.569389, 39.721203],
          [-76.569475, 39.721203],
          [-76.711894, 39.721103],
          [-76.715594, 39.721103],
          [-76.787096, 39.720802],
          [-76.787097, 39.720802],
          [-76.806397, 39.720602],
          [-76.809197, 39.720702],
          [-76.8901, 39.720401],
          [-76.897566, 39.720401],
          [-76.999465, 39.720128],
          [-76.998377, 39.832115],
          [-76.954932, 39.85685],
          [-77.018648, 40.013947],
          [-77.137425, 40.069945],
          [-77.028853, 40.147838],
          [-76.905875, 40.165723],
          [-76.859023, 40.226328],
          [-76.72162, 40.12007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49009",
      state: "49",
      county: "009",
      name: "Daggett County",
      censusArea: 696.979,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.000708, 40.997352],
          [-109.999838, 40.99733],
          [-109.855299, 40.997614],
          [-109.854302, 40.997661],
          [-109.715409, 40.998191],
          [-109.713877, 40.998266],
          [-109.676421, 40.998395],
          [-109.207383, 41.001459],
          [-109.050076, 41.000659],
          [-109.048296, 40.662602],
          [-109.161063, 40.683817],
          [-109.163584, 40.78589],
          [-109.202356, 40.785814],
          [-109.203253, 40.858641],
          [-109.394481, 40.858073],
          [-109.394368, 40.790881],
          [-109.480252, 40.739929],
          [-109.525495, 40.82637],
          [-109.870207, 40.763829],
          [-109.977505, 40.809828],
          [-109.987631, 40.807616],
          [-109.993667, 40.8152],
          [-110.000709, 40.81375],
          [-110.000708, 40.997352]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49013",
      state: "49",
      county: "013",
      name: "Duchesne County",
      censusArea: 3240.945,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.857902, 39.812428],
          [-110.857321, 39.898941],
          [-110.891872, 39.899146],
          [-110.901931, 40.67817],
          [-110.89309, 40.72733],
          [-110.375002, 40.788211],
          [-110.294317, 40.834228],
          [-110.000709, 40.81375],
          [-109.993667, 40.8152],
          [-109.987631, 40.807616],
          [-109.977505, 40.809828],
          [-109.973684, 40.039869],
          [-109.967601, 39.805979],
          [-110.857902, 39.812428]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49019",
      state: "49",
      county: "019",
      name: "Grand County",
      censusArea: 3671.543,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.025402, 38.499981],
          [-109.994283, 38.527118],
          [-110.057897, 38.58374],
          [-109.996929, 38.608847],
          [-110.05595, 38.604953],
          [-110.036344, 38.652311],
          [-110.113526, 38.706507],
          [-110.083861, 38.751952],
          [-110.179252, 38.907248],
          [-110.150018, 38.980696],
          [-110.005576, 38.949914],
          [-110.097736, 39.001619],
          [-110.150496, 38.998655],
          [-110.098763, 39.189509],
          [-110.05457, 39.228283],
          [-110.07727, 39.275869],
          [-110.010617, 39.416322],
          [-110.02414, 39.461695],
          [-109.106972, 39.461979],
          [-109.051363, 39.497674],
          [-109.051417, 39.366677],
          [-109.051516, 39.124982],
          [-109.053292, 38.942878],
          [-109.053233, 38.942467],
          [-109.053797, 38.905284],
          [-109.053943, 38.904414],
          [-109.059541, 38.719888],
          [-109.059962, 38.499987],
          [-110.025402, 38.499981]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49023",
      state: "49",
      county: "023",
      name: "Juab County",
      censusArea: 3392.278,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.179729, 40.011569],
          [-112.053359, 39.893194],
          [-112.07135, 39.777942],
          [-111.972, 39.806657],
          [-111.909411, 39.894519],
          [-111.794837, 39.931159],
          [-111.734178, 39.853895],
          [-111.766193, 39.810601],
          [-111.640578, 39.813017],
          [-111.585068, 39.740135],
          [-111.71862, 39.694623],
          [-111.713224, 39.460107],
          [-111.747075, 39.38283],
          [-111.922462, 39.381452],
          [-112.015677, 39.314455],
          [-112.195578, 39.3296],
          [-112.220362, 39.554054],
          [-112.354345, 39.553871],
          [-112.462119, 39.552449],
          [-113.81557, 39.552624],
          [-113.815748, 39.544097],
          [-114.047728, 39.542742],
          [-114.047134, 39.906037],
          [-112.341883, 39.904247],
          [-112.331783, 39.952747],
          [-112.269882, 39.938947],
          [-112.179729, 40.011569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48001",
      state: "48",
      county: "001",
      name: "Anderson County",
      censusArea: 1062.602,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.428512, 32.084475],
          [-95.446747, 31.843116],
          [-95.412908, 31.835157],
          [-95.258859, 31.609959],
          [-95.273203, 31.592886],
          [-95.651764, 31.541791],
          [-95.739279, 31.504056],
          [-95.710112, 31.615587],
          [-95.7873, 31.618385],
          [-95.794081, 31.66031],
          [-95.861262, 31.687451],
          [-95.875937, 31.755503],
          [-95.980568, 31.784561],
          [-95.994127, 31.866258],
          [-96.027788, 31.878242],
          [-96.022955, 31.957581],
          [-96.062172, 31.95634],
          [-96.052786, 32.005895],
          [-95.428512, 32.084475]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48013",
      state: "48",
      county: "013",
      name: "Atascosa County",
      censusArea: 1219.544,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.800841, 28.647487],
          [-98.8049, 29.090434],
          [-98.804763, 29.250693],
          [-98.407336, 29.114435],
          [-98.190991, 28.882333],
          [-98.098315, 28.786949],
          [-98.335031, 28.612658],
          [-98.335047, 28.648275],
          [-98.800848, 28.647306],
          [-98.800841, 28.647487]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48015",
      state: "48",
      county: "015",
      name: "Austin County",
      censusArea: 646.508,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.62198, 30.044283],
          [-96.292849, 30.09615],
          [-96.146052, 30.070224],
          [-96.084541, 30.005137],
          [-96.122754, 29.968545],
          [-96.121313, 29.841963],
          [-96.049234, 29.803187],
          [-96.032711, 29.727944],
          [-96.02485, 29.602877],
          [-96.088912, 29.601658],
          [-96.175422, 29.633806],
          [-96.259226, 29.668912],
          [-96.344476, 29.830147],
          [-96.413283, 29.824985],
          [-96.569844, 29.961516],
          [-96.62198, 30.044283]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48017",
      state: "48",
      county: "017",
      name: "Bailey County",
      censusArea: 826.797,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.043979, 34.312749],
          [-102.61515, 34.312891],
          [-102.615447, 33.825121],
          [-103.047346, 33.824675],
          [-103.046907, 33.8503],
          [-103.045644, 33.901537],
          [-103.045698, 33.906299],
          [-103.044893, 33.945617],
          [-103.04395, 33.974629],
          [-103.043617, 34.003633],
          [-103.043531, 34.018014],
          [-103.043555, 34.032714],
          [-103.043746, 34.037294],
          [-103.043771, 34.041538],
          [-103.043721, 34.04232],
          [-103.043767, 34.043545],
          [-103.043744, 34.049986],
          [-103.043686, 34.063078],
          [-103.043516, 34.079382],
          [-103.043569, 34.087947],
          [-103.043644, 34.256903],
          [-103.043719, 34.289441],
          [-103.043936, 34.302585],
          [-103.043979, 34.312749]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48023",
      state: "48",
      county: "023",
      name: "Baylor County",
      censusArea: 867.482,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.47565, 33.834005],
          [-98.953088, 33.834],
          [-98.953938, 33.397534],
          [-99.472444, 33.399023],
          [-99.474498, 33.733849],
          [-99.47565, 33.834005]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48025",
      state: "48",
      county: "025",
      name: "Bee County",
      censusArea: 880.239,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.375579, 28.388684],
          [-97.541071, 28.164669],
          [-97.567622, 28.129683],
          [-97.817716, 28.176848],
          [-97.808774, 28.183415],
          [-98.089764, 28.662979],
          [-98.005252, 28.690239],
          [-97.915106, 28.719648],
          [-97.77853, 28.668027],
          [-97.696945, 28.541856],
          [-97.558496, 28.514054],
          [-97.551369, 28.450441],
          [-97.375579, 28.388684]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48029",
      state: "48",
      county: "029",
      name: "Bexar County",
      censusArea: 1239.82,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.28702, 29.257626],
          [-98.407336, 29.114435],
          [-98.804763, 29.250693],
          [-98.806552, 29.690709],
          [-98.778782, 29.720167],
          [-98.646124, 29.745181],
          [-98.550489, 29.760713],
          [-98.443852, 29.71965],
          [-98.338618, 29.721786],
          [-98.378068, 29.662613],
          [-98.310928, 29.594473],
          [-98.298524, 29.561141],
          [-98.272924, 29.550878],
          [-98.126767, 29.482248],
          [-98.134171, 29.441751],
          [-98.137187, 29.440428],
          [-98.28702, 29.257626]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48033",
      state: "48",
      county: "033",
      name: "Borden County",
      censusArea: 897.443,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.173378, 32.963597],
          [-101.174562, 32.527703],
          [-101.174571, 32.524112],
          [-101.68874, 32.525222],
          [-101.691284, 32.961838],
          [-101.557434, 32.961025],
          [-101.173378, 32.963597]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48035",
      state: "48",
      county: "035",
      name: "Bosque County",
      censusArea: 982.978,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.864864, 32.087328],
          [-97.615055, 32.203581],
          [-97.516121, 32.133612],
          [-97.476086, 32.17346],
          [-97.485968, 32.017611],
          [-97.379992, 31.97274],
          [-97.438765, 31.933506],
          [-97.382848, 31.870788],
          [-97.28206, 31.843152],
          [-97.32653, 31.78836],
          [-97.277265, 31.745492],
          [-97.580232, 31.598796],
          [-97.60523, 31.587762],
          [-97.689834, 31.711019],
          [-97.766431, 31.672461],
          [-98.005462, 32.017897],
          [-97.864864, 32.087328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48039",
      state: "48",
      county: "039",
      name: "Brazoria County",
      censusArea: 1357.701,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.424118, 29.580233],
          [-95.297409, 29.596514],
          [-95.216422, 29.55608],
          [-95.216788, 29.554643],
          [-95.217702, 29.549503],
          [-95.217794, 29.548449],
          [-95.219169, 29.541283],
          [-95.219395, 29.539122],
          [-95.219813, 29.536757],
          [-95.220748, 29.532688],
          [-95.221212, 29.529137],
          [-95.221862, 29.526521],
          [-95.223994, 29.514422],
          [-95.225168, 29.503974],
          [-95.229732, 29.480714],
          [-95.231803, 29.472607],
          [-95.233081, 29.465566],
          [-95.056528, 29.199934],
          [-95.119271, 29.077844],
          [-95.122525, 29.074],
          [-95.125134, 29.067321],
          [-95.191391, 29.02309],
          [-95.238924, 28.988644],
          [-95.272266, 28.961546],
          [-95.297147, 28.934073],
          [-95.309704, 28.928262],
          [-95.353451, 28.898145],
          [-95.376979, 28.87616],
          [-95.38239, 28.866348],
          [-95.416174, 28.859482],
          [-95.439594, 28.859022],
          [-95.486769, 28.836287],
          [-95.507041, 28.824755],
          [-95.567708, 28.829758],
          [-95.67955, 28.965734],
          [-95.764937, 28.967359],
          [-95.770446, 29.06621],
          [-95.84631, 29.107799],
          [-95.874028, 29.229702],
          [-95.841948, 29.258707],
          [-95.847656, 29.26259],
          [-95.752866, 29.324503],
          [-95.654355, 29.290945],
          [-95.579598, 29.340129],
          [-95.549994, 29.438439],
          [-95.462503, 29.43924],
          [-95.443224, 29.510214],
          [-95.424118, 29.580233]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29171",
      state: "29",
      county: "171",
      name: "Putnam County",
      censusArea: 517.317,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.366935, 40.382999],
          [-93.374386, 40.580334],
          [-93.345442, 40.580514],
          [-93.317605, 40.580671],
          [-93.260612, 40.580797],
          [-93.098507, 40.583973],
          [-93.097296, 40.584014],
          [-93.085517, 40.584403],
          [-92.957747, 40.58743],
          [-92.941595, 40.587743],
          [-92.903544, 40.58786],
          [-92.889796, 40.588039],
          [-92.879178, 40.588341],
          [-92.863034, 40.588175],
          [-92.857391, 40.58836],
          [-92.835074, 40.588484],
          [-92.827992, 40.588515],
          [-92.828061, 40.588593],
          [-92.757407, 40.588908],
          [-92.742232, 40.589207],
          [-92.714598, 40.589564],
          [-92.667478, 40.408258],
          [-92.684167, 40.343466],
          [-92.855629, 40.342736],
          [-92.855313, 40.385894],
          [-93.366935, 40.382999]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29175",
      state: "29",
      county: "175",
      name: "Randolph County",
      censusArea: 482.684,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.692149, 39.610265],
          [-92.300871, 39.605348],
          [-92.310028, 39.347795],
          [-92.313836, 39.249986],
          [-92.314471, 39.246454],
          [-92.430229, 39.248795],
          [-92.707367, 39.321614],
          [-92.696592, 39.610371],
          [-92.692149, 39.610265]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29177",
      state: "29",
      county: "177",
      name: "Ray County",
      censusArea: 568.805,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.758463, 39.207021],
          [-93.840147, 39.215467],
          [-93.988892, 39.152786],
          [-94.104823, 39.143507],
          [-94.212515, 39.207086],
          [-94.210642, 39.454682],
          [-94.21083, 39.456337],
          [-94.208423, 39.5272],
          [-93.759183, 39.524558],
          [-93.758651, 39.209623],
          [-93.758463, 39.207021]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29181",
      state: "29",
      county: "181",
      name: "Ripley County",
      censusArea: 629.537,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.662617, 36.811453],
          [-90.627517, 36.810954],
          [-90.628387, 36.679544],
          [-90.592166, 36.679368],
          [-90.57618, 36.498446],
          [-90.585342, 36.498497],
          [-90.5943, 36.498459],
          [-90.60545, 36.498459],
          [-90.612554, 36.498559],
          [-90.648494, 36.498447],
          [-90.653246, 36.498488],
          [-90.693005, 36.49851],
          [-90.711226, 36.498318],
          [-90.765672, 36.498494],
          [-90.782454, 36.498523],
          [-90.784398, 36.498524],
          [-90.850434, 36.498548],
          [-90.873775, 36.498074],
          [-90.876567, 36.498313],
          [-90.876867, 36.498423],
          [-90.87922, 36.498378],
          [-90.960648, 36.498426],
          [-90.963063, 36.498418],
          [-91.008558, 36.49827],
          [-91.017974, 36.498062],
          [-91.09588, 36.49787],
          [-91.096277, 36.497893],
          [-91.126529, 36.497712],
          [-91.115926, 36.823672],
          [-90.662617, 36.811453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29186",
      state: "29",
      county: "186",
      name: "Ste. Genevieve County",
      censusArea: 499.153,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.110537, 37.67134],
          [-90.200208, 37.669628],
          [-90.459133, 37.878924],
          [-90.32287, 37.981731],
          [-90.416022, 38.042315],
          [-90.254112, 38.12199],
          [-90.243116, 38.112669],
          [-90.203847, 38.088753],
          [-90.126532, 38.041666],
          [-90.072283, 38.017001],
          [-89.997103, 37.963225],
          [-89.974918, 37.926719],
          [-89.973642, 37.917661],
          [-89.950594, 37.881526],
          [-89.938191, 37.875111],
          [-89.999768, 37.821744],
          [-90.110537, 37.67134]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29187",
      state: "29",
      county: "187",
      name: "St. Francois County",
      censusArea: 451.887,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.535016, 37.643641],
          [-90.647874, 37.642809],
          [-90.645135, 37.734813],
          [-90.63998, 38.076548],
          [-90.583324, 38.003499],
          [-90.416022, 38.042315],
          [-90.32287, 37.981731],
          [-90.459133, 37.878924],
          [-90.200208, 37.669628],
          [-90.110537, 37.67134],
          [-90.146778, 37.641618],
          [-90.516756, 37.642665],
          [-90.535016, 37.643641]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29197",
      state: "29",
      county: "197",
      name: "Schuyler County",
      censusArea: 307.303,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.349975, 40.346505],
          [-92.684167, 40.343466],
          [-92.667478, 40.408258],
          [-92.714598, 40.589564],
          [-92.689854, 40.589884],
          [-92.686693, 40.589809],
          [-92.639223, 40.590825],
          [-92.637898, 40.590853],
          [-92.580278, 40.592151],
          [-92.484588, 40.594924],
          [-92.482394, 40.594894],
          [-92.481692, 40.594941],
          [-92.461609, 40.595355],
          [-92.453745, 40.595288],
          [-92.379691, 40.596509],
          [-92.350807, 40.597273],
          [-92.349975, 40.346505]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29203",
      state: "29",
      county: "203",
      name: "Shannon County",
      censusArea: 1003.82,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.22283, 36.883857],
          [-91.250129, 36.88515],
          [-91.658111, 36.888724],
          [-91.657814, 36.889648],
          [-91.655607, 37.048925],
          [-91.646626, 37.422731],
          [-91.211863, 37.415277],
          [-91.175405, 37.414805],
          [-91.159323, 37.254936],
          [-91.034346, 37.169343],
          [-91.017766, 37.095677],
          [-91.21715, 37.086197],
          [-91.22283, 36.883857]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29207",
      state: "29",
      county: "207",
      name: "Stoddard County",
      censusArea: 823.222,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.958786, 36.629331],
          [-90.148398, 36.630656],
          [-90.14002, 36.69676],
          [-90.204205, 36.772262],
          [-90.258563, 36.922043],
          [-90.111247, 37.041205],
          [-89.959196, 37.05438],
          [-89.959576, 37.127353],
          [-89.866627, 37.126227],
          [-89.765211, 37.126066],
          [-89.786819, 37.087824],
          [-89.68662, 36.928293],
          [-89.691603, 36.859517],
          [-89.700952, 36.627453],
          [-89.958786, 36.629331]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29213",
      state: "29",
      county: "213",
      name: "Taney County",
      censusArea: 632.436,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.146854, 36.81382],
          [-92.909336, 36.809178],
          [-92.764869, 36.806097],
          [-92.772341, 36.497772],
          [-92.838621, 36.498079],
          [-92.838876, 36.498033],
          [-92.854049, 36.497983],
          [-92.894001, 36.49785],
          [-92.894336, 36.497867],
          [-93.013742, 36.49813],
          [-93.068455, 36.49825],
          [-93.069512, 36.498242],
          [-93.087635, 36.498239],
          [-93.088988, 36.498184],
          [-93.296117, 36.498389],
          [-93.315324, 36.498408],
          [-93.304359, 36.816866],
          [-93.146854, 36.81382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29219",
      state: "29",
      county: "219",
      name: "Warren County",
      censusArea: 428.601,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.9622, 38.7742],
          [-90.964461, 38.547545],
          [-91.077324, 38.609343],
          [-91.226547, 38.621567],
          [-91.313093, 38.706715],
          [-91.369192, 38.699324],
          [-91.395134, 38.70799],
          [-91.418637, 38.709778],
          [-91.415832, 38.8453],
          [-91.264927, 38.843833],
          [-91.264287, 38.992762],
          [-91.18839, 38.992141],
          [-91.189348, 38.932151],
          [-91.110187, 38.931901],
          [-91.111251, 38.872709],
          [-90.958536, 38.870865],
          [-90.9622, 38.7742]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29225",
      state: "29",
      county: "225",
      name: "Webster County",
      censusArea: 592.562,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.065274, 37.088694],
          [-93.07338, 37.414989],
          [-93.071584, 37.487444],
          [-92.853481, 37.48397],
          [-92.852107, 37.484083],
          [-92.686671, 37.481545],
          [-92.685867, 37.067051],
          [-92.903273, 37.070651],
          [-93.065274, 37.088694]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29510",
      state: "29",
      county: "510",
      name: "St. Louis City County",
      lsad: "city",
      censusArea: 61.909,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.301911, 38.655652],
          [-90.251709, 38.718948],
          [-90.165082, 38.770618],
          [-90.171309, 38.766549],
          [-90.20991, 38.72605],
          [-90.21201, 38.71175],
          [-90.20921, 38.70275],
          [-90.20221, 38.69345],
          [-90.19521, 38.68755],
          [-90.18641, 38.67475],
          [-90.181399, 38.660378],
          [-90.18111, 38.65955],
          [-90.17771, 38.64275],
          [-90.17801, 38.63375],
          [-90.182625, 38.617989],
          [-90.18451, 38.611551],
          [-90.196011, 38.594451],
          [-90.202511, 38.588651],
          [-90.224512, 38.574651],
          [-90.248913, 38.544752],
          [-90.257773, 38.532008],
          [-90.306433, 38.63359],
          [-90.304812, 38.642444],
          [-90.3036, 38.645003],
          [-90.301911, 38.655652]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30003",
      state: "30",
      county: "003",
      name: "Big Horn County",
      censusArea: 4995.462,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.888773, 44.995885],
          [-106.892875, 44.995947],
          [-107.080624, 44.996791],
          [-107.13418, 45.000109],
          [-107.607824, 45.000929],
          [-107.608854, 45.00086],
          [-107.911495, 45.000948],
          [-108.125666, 45.001011],
          [-108.249345, 44.999458],
          [-108.25863, 45.007576],
          [-108.127979, 45.217911],
          [-108.699552, 45.217952],
          [-108.682105, 45.433093],
          [-108.644971, 45.464091],
          [-108.316374, 45.460509],
          [-108.316249, 45.489459],
          [-108.193191, 45.489396],
          [-108.193125, 45.518413],
          [-108.069703, 45.518246],
          [-108.069044, 45.783599],
          [-108.04741, 45.899316],
          [-107.881553, 45.89897],
          [-107.798047, 45.985681],
          [-107.674438, 45.985685],
          [-107.674261, 46.043627],
          [-107.511497, 46.043257],
          [-107.424889, 46.043396],
          [-107.424923, 45.956786],
          [-107.176311, 45.956788],
          [-107.176433, 45.913323],
          [-107.051803, 45.913788],
          [-107.052103, 45.870088],
          [-106.939095, 45.870089],
          [-106.877217, 45.831714],
          [-106.912494, 45.788189],
          [-106.912997, 45.684087],
          [-106.727488, 45.676491],
          [-106.768014, 45.179677],
          [-106.279682, 45.179892],
          [-106.263682, 45.179792],
          [-106.263586, 44.993788],
          [-106.888773, 44.995885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37159",
      state: "37",
      county: "159",
      name: "Rowan County",
      censusArea: 511.374,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.757239, 35.616265],
          [-80.771518, 35.674926],
          [-80.70767, 35.852913],
          [-80.631796, 35.845784],
          [-80.458904, 35.742811],
          [-80.413844, 35.722471],
          [-80.391729, 35.722258],
          [-80.369852, 35.716236],
          [-80.339465, 35.721385],
          [-80.332228, 35.719047],
          [-80.326578, 35.675475],
          [-80.208176, 35.580401],
          [-80.182682, 35.504335],
          [-80.295236, 35.502947],
          [-80.562402, 35.50311],
          [-80.645902, 35.505913],
          [-80.649002, 35.505915],
          [-80.650431, 35.506094],
          [-80.657612, 35.506059],
          [-80.688362, 35.506561],
          [-80.737069, 35.507163],
          [-80.757239, 35.616265]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37163",
      state: "37",
      county: "163",
      name: "Sampson County",
      censusArea: 944.74,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.391146, 35.262632],
          [-78.332587, 35.279446],
          [-78.304662, 35.286097],
          [-78.163539, 35.18937],
          [-78.16481, 35.179145],
          [-78.147081, 34.902448],
          [-78.200473, 34.737301],
          [-78.113208, 34.721969],
          [-78.254556, 34.553963],
          [-78.49461, 34.856464],
          [-78.49541, 34.857678],
          [-78.494838, 34.858841],
          [-78.496564, 34.859471],
          [-78.498938, 34.859302],
          [-78.502189, 34.861549],
          [-78.502835, 34.864663],
          [-78.506006, 34.865179],
          [-78.50694, 34.866669],
          [-78.511755, 34.867961],
          [-78.5133, 34.869287],
          [-78.513379, 34.873385],
          [-78.514758, 34.873734],
          [-78.51501, 34.875567],
          [-78.514079, 34.875956],
          [-78.512906, 34.880906],
          [-78.514457, 34.880927],
          [-78.51387, 34.881916],
          [-78.515626, 34.881958],
          [-78.515661, 34.883],
          [-78.518023, 34.883846],
          [-78.519742, 34.8837],
          [-78.519347, 34.882653],
          [-78.521229, 34.882736],
          [-78.522724, 34.88355],
          [-78.647522, 34.99482],
          [-78.6701, 35.092964],
          [-78.617079, 35.245706],
          [-78.541262, 35.315116],
          [-78.491383, 35.263362],
          [-78.391146, 35.262632]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37169",
      state: "37",
      county: "169",
      name: "Stokes County",
      censusArea: 448.857,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.053455, 36.542623],
          [-80.027269, 36.542495],
          [-80.033218, 36.344867],
          [-80.03554, 36.257351],
          [-80.451954, 36.261635],
          [-80.4401, 36.55063],
          [-80.432628, 36.550302],
          [-80.431605, 36.550219],
          [-80.228263, 36.543867],
          [-80.225408, 36.543748],
          [-80.171636, 36.543219],
          [-80.169535, 36.54319],
          [-80.053455, 36.542623]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37175",
      state: "37",
      county: "175",
      name: "Transylvania County",
      censusArea: 378.528,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.764464, 35.068177],
          [-82.781973, 35.066817],
          [-82.780546, 35.069043],
          [-82.779116, 35.073674],
          [-82.777407, 35.076885],
          [-82.781062, 35.084492],
          [-82.78113, 35.084585],
          [-82.897499, 35.056021],
          [-83.008447, 35.026974],
          [-83.055352, 35.053865],
          [-82.983329, 35.131904],
          [-82.920819, 35.291868],
          [-82.832001, 35.318434],
          [-82.745209, 35.422924],
          [-82.619513, 35.303758],
          [-82.574406, 35.144216],
          [-82.580127, 35.141963],
          [-82.582264, 35.142268],
          [-82.686496, 35.121822],
          [-82.686738, 35.11979],
          [-82.764464, 35.068177]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39061",
      state: "39",
      county: "061",
      name: "Hamilton County",
      censusArea: 405.91,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.510057, 39.093593],
          [-84.603444, 39.073699],
          [-84.620112, 39.073457],
          [-84.623732, 39.074427],
          [-84.632446, 39.07676],
          [-84.684847, 39.100459],
          [-84.689747, 39.104159],
          [-84.718548, 39.137059],
          [-84.732048, 39.144458],
          [-84.744149, 39.147458],
          [-84.754449, 39.146658],
          [-84.766749, 39.138558],
          [-84.78768, 39.115297],
          [-84.820157, 39.10548],
          [-84.819985, 39.149081],
          [-84.819826, 39.156504],
          [-84.819802, 39.157613],
          [-84.819813, 39.244334],
          [-84.819801, 39.247806],
          [-84.819859, 39.251018],
          [-84.819633, 39.261855],
          [-84.819622, 39.27159],
          [-84.819451, 39.305152],
          [-84.819451, 39.305153],
          [-84.630992, 39.311942],
          [-84.44696, 39.300808],
          [-84.428363, 39.298659],
          [-84.391125, 39.297585],
          [-84.353209, 39.292287],
          [-84.259431, 39.270796],
          [-84.316698, 39.226944],
          [-84.319936, 39.022081],
          [-84.326539, 39.027463],
          [-84.432841, 39.094261],
          [-84.493743, 39.10246],
          [-84.502062, 39.096641],
          [-84.506082, 39.095081],
          [-84.509743, 39.09366],
          [-84.510057, 39.093593]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39071",
      state: "39",
      county: "071",
      name: "Highland County",
      censusArea: 553.084,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.372714, 39.377416],
          [-83.393511, 39.267272],
          [-83.353531, 39.197585],
          [-83.385637, 39.055197],
          [-83.611592, 39.01889],
          [-83.673018, 39.02043],
          [-83.872214, 39.021304],
          [-83.865679, 39.247333],
          [-83.784793, 39.262888],
          [-83.590878, 39.378736],
          [-83.375389, 39.377521],
          [-83.372714, 39.377416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39077",
      state: "39",
      county: "077",
      name: "Huron County",
      censusArea: 491.495,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.829512, 40.996624],
          [-82.840087, 41.255337],
          [-82.841475, 41.290023],
          [-82.342314, 41.283554],
          [-82.336496, 41.065761],
          [-82.436914, 41.065371],
          [-82.432852, 40.992944],
          [-82.724792, 40.995638],
          [-82.829512, 40.996624]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39081",
      state: "39",
      county: "081",
      name: "Jefferson County",
      censusArea: 408.329,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.617346, 40.396004],
          [-80.619196, 40.381768],
          [-80.609313, 40.360468],
          [-80.599895, 40.317669],
          [-80.602895, 40.307069],
          [-80.616696, 40.28027],
          [-80.637198, 40.25507],
          [-80.644598, 40.25127],
          [-80.652098, 40.24497],
          [-80.685391, 40.187642],
          [-80.703052, 40.157231],
          [-80.882892, 40.159495],
          [-80.865504, 40.422929],
          [-80.941911, 40.4247],
          [-80.92092, 40.556315],
          [-80.861994, 40.599404],
          [-80.667957, 40.582496],
          [-80.666917, 40.573664],
          [-80.610259, 40.490962],
          [-80.599194, 40.482566],
          [-80.612295, 40.434867],
          [-80.617346, 40.396004]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39087",
      state: "39",
      county: "087",
      name: "Lawrence County",
      censusArea: 453.371,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.50667, 38.410092],
          [-82.520178, 38.407582],
          [-82.560664, 38.404338],
          [-82.593673, 38.421809],
          [-82.665485, 38.505734],
          [-82.665548, 38.505808],
          [-82.690509, 38.536576],
          [-82.696621, 38.542112],
          [-82.724846, 38.5576],
          [-82.730958, 38.559264],
          [-82.789776, 38.559951],
          [-82.800112, 38.563183],
          [-82.816012, 38.570733],
          [-82.740383, 38.597167],
          [-82.763895, 38.679974],
          [-82.706823, 38.677495],
          [-82.650039, 38.849073],
          [-82.626045, 38.847647],
          [-82.625022, 38.84754],
          [-82.575583, 38.844478],
          [-82.582683, 38.779077],
          [-82.483579, 38.772278],
          [-82.472478, 38.682279],
          [-82.354468, 38.67607],
          [-82.361674, 38.585182],
          [-82.287102, 38.582588],
          [-82.291271, 38.578983],
          [-82.293871, 38.572683],
          [-82.293271, 38.560283],
          [-82.295571, 38.539783],
          [-82.313935, 38.468084],
          [-82.320223, 38.454516],
          [-82.323999, 38.449268],
          [-82.330335, 38.4445],
          [-82.34064, 38.440948],
          [-82.50667, 38.410092]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39093",
      state: "39",
      county: "093",
      name: "Lorain County",
      censusArea: 491.101,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.96813, 41.506422],
          [-81.971262, 41.351268],
          [-81.877066, 41.343557],
          [-81.878053, 41.275044],
          [-81.897853, 41.274916],
          [-81.906227, 41.275392],
          [-81.914792, 41.275469],
          [-81.920513, 41.275379],
          [-81.927005, 41.274818],
          [-81.972485, 41.274829],
          [-81.973895, 41.199831],
          [-82.072465, 41.19985],
          [-82.074266, 41.136456],
          [-82.169875, 41.137102],
          [-82.171492, 41.063537],
          [-82.336496, 41.065761],
          [-82.342314, 41.283554],
          [-82.348076, 41.428431],
          [-82.334182, 41.430243],
          [-82.29158, 41.428442],
          [-82.283488, 41.429283],
          [-82.268479, 41.430842],
          [-82.254678, 41.434441],
          [-82.193375, 41.46454],
          [-82.18885, 41.468097],
          [-82.186174, 41.47344],
          [-82.184774, 41.47404],
          [-82.181598, 41.471634],
          [-82.165373, 41.47444],
          [-82.094169, 41.495039],
          [-82.011966, 41.515639],
          [-81.994565, 41.51444],
          [-81.96813, 41.506422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39099",
      state: "39",
      county: "099",
      name: "Mahoning County",
      censusArea: 411.623,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.519167, 41.133388],
          [-80.519167, 41.133343],
          [-80.519012, 41.125116],
          [-80.519012, 41.125093],
          [-80.519012, 41.125057],
          [-80.519056, 41.125057],
          [-80.518992, 41.115958],
          [-80.519192, 41.105358],
          [-80.519125, 41.100819],
          [-80.519092, 41.090658],
          [-80.519088, 41.082074],
          [-80.518999, 41.075014],
          [-80.51896, 41.071866],
          [-80.518928, 41.070954],
          [-80.51896, 41.061546],
          [-80.518927, 41.015387],
          [-80.518989, 40.995445],
          [-80.519, 40.98738],
          [-80.519091, 40.921061],
          [-80.51979, 40.900761],
          [-80.519764, 40.899858],
          [-81.086679, 40.901609],
          [-81.086688, 40.931737],
          [-81.086642, 40.932189],
          [-81.086601, 40.937622],
          [-81.086918, 40.939658],
          [-81.086614, 40.940774],
          [-81.086601, 40.944758],
          [-81.086719, 40.944945],
          [-81.086919, 40.981164],
          [-81.086817, 40.988158],
          [-81.001695, 40.987783],
          [-81.00229, 41.134189],
          [-80.789472, 41.134159],
          [-80.519167, 41.133388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39101",
      state: "39",
      county: "101",
      name: "Marion County",
      censusArea: 403.757,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.419836, 40.686823],
          [-83.304687, 40.702006],
          [-83.11136, 40.702915],
          [-82.858302, 40.705019],
          [-82.859753, 40.646395],
          [-82.957817, 40.645148],
          [-82.958401, 40.490664],
          [-83.020798, 40.433795],
          [-83.248595, 40.443989],
          [-83.248183, 40.507034],
          [-83.418378, 40.505232],
          [-83.415303, 40.515495],
          [-83.419836, 40.686823]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US44001",
      state: "44",
      county: "001",
      name: "Bristol County",
      censusArea: 24.164,
      stateName: "Rhode Island"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.224798, 41.710498],
          [-71.227875, 41.705498],
          [-71.240991, 41.697744],
          [-71.237635, 41.681635],
          [-71.24155, 41.667205],
          [-71.25956, 41.642595],
          [-71.267055, 41.644945],
          [-71.270075, 41.652439],
          [-71.26918, 41.6549],
          [-71.280366, 41.672575],
          [-71.287637, 41.672463],
          [-71.290546, 41.662395],
          [-71.299159, 41.649531],
          [-71.301396, 41.649978],
          [-71.303746, 41.654788],
          [-71.306095, 41.672575],
          [-71.302627, 41.681747],
          [-71.298935, 41.681524],
          [-71.293119, 41.688347],
          [-71.291217, 41.702666],
          [-71.305759, 41.718662],
          [-71.31482, 41.723808],
          [-71.342786, 41.728506],
          [-71.350057, 41.727835],
          [-71.353172, 41.725191],
          [-71.35508, 41.726962],
          [-71.364561, 41.734928],
          [-71.365207, 41.735649],
          [-71.356997, 41.741601],
          [-71.353897, 41.751301],
          [-71.31779, 41.776099],
          [-71.261392, 41.752301],
          [-71.224798, 41.710498]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US44003",
      state: "44",
      county: "003",
      name: "Kent County",
      censusArea: 168.528,
      stateName: "Rhode Island"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.789678, 41.724568],
          [-71.455342, 41.732365],
          [-71.378273, 41.755297],
          [-71.372508, 41.744727],
          [-71.365207, 41.735649],
          [-71.364561, 41.734928],
          [-71.35508, 41.726962],
          [-71.353172, 41.725191],
          [-71.353748, 41.724702],
          [-71.365717, 41.711615],
          [-71.365717, 41.694947],
          [-71.372988, 41.672575],
          [-71.37791, 41.666646],
          [-71.382049, 41.667317],
          [-71.38988, 41.671903],
          [-71.390775, 41.680629],
          [-71.389432, 41.683425],
          [-71.390551, 41.684096],
          [-71.418069, 41.684208],
          [-71.441336, 41.686446],
          [-71.443082, 41.688303],
          [-71.441896, 41.690025],
          [-71.445923, 41.691144],
          [-71.449318, 41.687401],
          [-71.444468, 41.664409],
          [-71.430038, 41.667541],
          [-71.425452, 41.670785],
          [-71.409302, 41.662643],
          [-71.408636, 41.653819],
          [-71.484406, 41.602769],
          [-71.789359, 41.596852],
          [-71.789356, 41.59691],
          [-71.789465, 41.640017],
          [-71.789678, 41.724568]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US44005",
      state: "44",
      county: "005",
      name: "Newport County",
      censusArea: 102.386,
      stateName: "Rhode Island"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-71.383586, 41.464782],
            [-71.389284, 41.460605],
            [-71.390275, 41.455043],
            [-71.399568, 41.448596],
            [-71.40056, 41.46094],
            [-71.395927, 41.492215],
            [-71.386511, 41.493071],
            [-71.378914, 41.504948],
            [-71.391005, 41.514578],
            [-71.392137, 41.524468],
            [-71.384478, 41.556736],
            [-71.379021, 41.567772],
            [-71.373618, 41.573214],
            [-71.370194, 41.573963],
            [-71.36356, 41.57086],
            [-71.359868, 41.556308],
            [-71.363292, 41.501952],
            [-71.360403, 41.483121],
            [-71.380947, 41.474561],
            [-71.383586, 41.464782]
          ]
        ],
        [
          [
            [-71.326769, 41.491286],
            [-71.325365, 41.487601],
            [-71.327822, 41.482985],
            [-71.343013, 41.495615],
            [-71.341122, 41.498598],
            [-71.326769, 41.491286]
          ]
        ],
        [
          [
            [-71.3312, 41.580318],
            [-71.335949, 41.585898],
            [-71.337048, 41.594688],
            [-71.333751, 41.605859],
            [-71.329559, 41.609097],
            [-71.326609, 41.616114],
            [-71.325877, 41.623988],
            [-71.333305, 41.629536],
            [-71.34657, 41.632229],
            [-71.362869, 41.651457],
            [-71.366165, 41.66098],
            [-71.348402, 41.663727],
            [-71.338696, 41.658782],
            [-71.336182, 41.647961],
            [-71.337048, 41.646146],
            [-71.342514, 41.644791],
            [-71.343666, 41.6399],
            [-71.330711, 41.632992],
            [-71.314889, 41.630398],
            [-71.30555, 41.622523],
            [-71.303352, 41.606591],
            [-71.307381, 41.597984],
            [-71.317474, 41.583187],
            [-71.326103, 41.578583],
            [-71.3312, 41.580318]
          ]
        ],
        [
          [
            [-71.281571, 41.648207],
            [-71.278171, 41.647309],
            [-71.274315, 41.638125],
            [-71.283791, 41.637797],
            [-71.286755, 41.642725],
            [-71.283005, 41.644434],
            [-71.281571, 41.648207]
          ]
        ],
        [
          [
            [-71.12057, 41.497448],
            [-71.136867, 41.493942],
            [-71.141093, 41.489937],
            [-71.140224, 41.485855],
            [-71.167345, 41.471405],
            [-71.170131, 41.463974],
            [-71.19302, 41.457931],
            [-71.194967, 41.459037],
            [-71.196857, 41.461116],
            [-71.196607, 41.464756],
            [-71.190016, 41.478275],
            [-71.190167, 41.484285],
            [-71.19939, 41.491769],
            [-71.199692, 41.495511],
            [-71.206382, 41.499215],
            [-71.200788, 41.514371],
            [-71.213563, 41.545818],
            [-71.20865, 41.571028],
            [-71.20778, 41.60066],
            [-71.212656, 41.610072],
            [-71.212417, 41.61829],
            [-71.212004, 41.62299],
            [-71.21616, 41.62549],
            [-71.240709, 41.619225],
            [-71.2436, 41.587508],
            [-71.23613, 41.574767],
            [-71.236642, 41.535852],
            [-71.234775, 41.532538],
            [-71.227989, 41.528297],
            [-71.229444, 41.521544],
            [-71.233056, 41.514757],
            [-71.233141, 41.514598],
            [-71.233435, 41.514045],
            [-71.240614, 41.500557],
            [-71.238586, 41.486845],
            [-71.237175, 41.486546],
            [-71.236751, 41.483369],
            [-71.237783, 41.481155],
            [-71.237902, 41.480899],
            [-71.239804, 41.476817],
            [-71.240047, 41.476296],
            [-71.24071, 41.474872],
            [-71.246703, 41.47196],
            [-71.246676, 41.472323],
            [-71.246174, 41.478917],
            [-71.245992, 41.481302],
            [-71.252692, 41.485902],
            [-71.264793, 41.488902],
            [-71.267579, 41.488755],
            [-71.282262, 41.487982],
            [-71.285639, 41.487805],
            [-71.295111, 41.48435],
            [-71.296517, 41.479829],
            [-71.300438, 41.46722],
            [-71.302583, 41.460325],
            [-71.302741, 41.459817],
            [-71.302895, 41.459323],
            [-71.302929, 41.459213],
            [-71.304247, 41.454976],
            [-71.304394, 41.454502],
            [-71.304604, 41.454391],
            [-71.305896, 41.453708],
            [-71.310357, 41.45135],
            [-71.310473, 41.451289],
            [-71.311344, 41.450829],
            [-71.311394, 41.450802],
            [-71.311404, 41.450807],
            [-71.312694, 41.451402],
            [-71.312718, 41.454597],
            [-71.32141, 41.4556],
            [-71.337377, 41.449033],
            [-71.337695, 41.448902],
            [-71.351096, 41.450802],
            [-71.351457, 41.451099],
            [-71.358166, 41.456616],
            [-71.358262, 41.456695],
            [-71.358356, 41.456772],
            [-71.358657, 41.457019],
            [-71.359015, 41.457314],
            [-71.359136, 41.457414],
            [-71.359292, 41.457542],
            [-71.361852, 41.459647],
            [-71.362201, 41.459934],
            [-71.362288, 41.460006],
            [-71.362699, 41.460343],
            [-71.362743, 41.460379],
            [-71.362736, 41.460405],
            [-71.362012, 41.463041],
            [-71.36176, 41.463955],
            [-71.36152, 41.464831],
            [-71.34707, 41.47123],
            [-71.335992, 41.469647],
            [-71.316519, 41.47756],
            [-71.317414, 41.488776],
            [-71.323125, 41.503088],
            [-71.327804, 41.504258],
            [-71.330694, 41.507699],
            [-71.330831, 41.518364],
            [-71.313079, 41.534672],
            [-71.310533, 41.54692],
            [-71.303652, 41.559925],
            [-71.294363, 41.571416],
            [-71.288376, 41.573274],
            [-71.285142, 41.577127],
            [-71.273445, 41.60699],
            [-71.272412, 41.615041],
            [-71.275234, 41.619444],
            [-71.271862, 41.623986],
            [-71.251082, 41.63878],
            [-71.212136, 41.641945],
            [-71.19564, 41.67509],
            [-71.19439, 41.674802],
            [-71.191178, 41.674216],
            [-71.191175, 41.674292],
            [-71.18129, 41.672502],
            [-71.17599, 41.671402],
            [-71.17609, 41.668502],
            [-71.17609, 41.668102],
            [-71.153989, 41.664102],
            [-71.14587, 41.662795],
            [-71.135188, 41.660502],
            [-71.134688, 41.660502],
            [-71.134478, 41.641262],
            [-71.134484, 41.641198],
            [-71.133796, 41.63409],
            [-71.133608, 41.632152],
            [-71.133353, 41.629509],
            [-71.12057, 41.497448]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45001",
      state: "45",
      county: "001",
      name: "Abbeville County",
      censusArea: 490.484,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.326947, 34.06412],
          [-82.530567, 34.071925],
          [-82.597079, 34.01239],
          [-82.640701, 34.088341],
          [-82.641252, 34.088914],
          [-82.652175, 34.099704],
          [-82.654019, 34.100346],
          [-82.658561, 34.103118],
          [-82.659077, 34.103544],
          [-82.668113, 34.12016],
          [-82.67732, 34.131657],
          [-82.70414, 34.141007],
          [-82.717507, 34.150504],
          [-82.731881, 34.178363],
          [-82.741491, 34.208709],
          [-82.313996, 34.484002],
          [-82.246337, 34.409676],
          [-82.334234, 34.34263],
          [-82.247747, 34.219619],
          [-82.267184, 34.10836],
          [-82.326947, 34.06412]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45003",
      state: "45",
      county: "003",
      name: "Aiken County",
      censusArea: 1071.034,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.522124, 33.825522],
          [-81.415308, 33.732161],
          [-81.187271, 33.652937],
          [-81.373494, 33.49051],
          [-81.372931, 33.490417],
          [-81.374827, 33.489652],
          [-81.516419, 33.382239],
          [-81.757035, 33.198148],
          [-81.772251, 33.18012],
          [-81.851975, 33.247408],
          [-81.852136, 33.247544],
          [-81.902737, 33.331242],
          [-81.912572, 33.408811],
          [-81.957934, 33.468632],
          [-81.967037, 33.480636],
          [-82.007138, 33.522835],
          [-82.007638, 33.523335],
          [-82.014293, 33.530313],
          [-82.012534, 33.532156],
          [-81.651759, 33.81451],
          [-81.571485, 33.87723],
          [-81.569149, 33.871845],
          [-81.563671, 33.865324],
          [-81.560733, 33.862645],
          [-81.554531, 33.860547],
          [-81.551552, 33.860657],
          [-81.546946, 33.858683],
          [-81.540865, 33.851915],
          [-81.542263, 33.848407],
          [-81.540866, 33.845608],
          [-81.538679, 33.844531],
          [-81.538291, 33.841496],
          [-81.534438, 33.837454],
          [-81.53359, 33.832154],
          [-81.527148, 33.829799],
          [-81.52286, 33.826967],
          [-81.522124, 33.825522]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45007",
      state: "45",
      county: "007",
      name: "Anderson County",
      censusArea: 715.426,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.486746, 34.819475],
          [-82.453789, 34.625114],
          [-82.419665, 34.557089],
          [-82.313996, 34.484002],
          [-82.741491, 34.208709],
          [-82.74192, 34.210063],
          [-82.744415, 34.224913],
          [-82.743461, 34.227343],
          [-82.74198, 34.230196],
          [-82.755028, 34.276067],
          [-82.766078, 34.293509],
          [-82.848651, 34.423844],
          [-82.874864, 34.468891],
          [-82.873831, 34.471508],
          [-82.882864, 34.479003],
          [-82.902665, 34.485902],
          [-82.939067, 34.486202],
          [-82.979568, 34.482702],
          [-82.992215, 34.479198],
          [-82.840253, 34.623212],
          [-82.788664, 34.672017],
          [-82.486746, 34.819475]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45017",
      state: "45",
      county: "017",
      name: "Calhoun County",
      censusArea: 381.151,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.01233, 33.88008],
          [-80.91731, 33.813735],
          [-80.747337, 33.761742],
          [-80.620107, 33.743237],
          [-80.534085, 33.643911],
          [-80.496273, 33.558481],
          [-80.552272, 33.565414],
          [-80.65902, 33.470982],
          [-80.807631, 33.604081],
          [-80.879528, 33.619285],
          [-80.93979, 33.608798],
          [-81.042854, 33.707131],
          [-80.926321, 33.755583],
          [-81.058521, 33.747053],
          [-81.01233, 33.88008]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45023",
      state: "45",
      county: "023",
      name: "Chester County",
      censusArea: 580.657,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.478454, 34.821509],
          [-80.898215, 34.820746],
          [-80.860274, 34.695847],
          [-80.896406, 34.631781],
          [-80.877491, 34.543146],
          [-80.949774, 34.545341],
          [-81.422706, 34.572029],
          [-81.478454, 34.821509]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45025",
      state: "45",
      county: "025",
      name: "Chesterfield County",
      censusArea: 799.075,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.288596, 34.366207],
          [-80.328553, 34.501349],
          [-80.408373, 34.614765],
          [-80.561674, 34.817481],
          [-80.561657, 34.817481],
          [-80.485683, 34.816732],
          [-80.45166, 34.816396],
          [-80.448766, 34.816332],
          [-80.434843, 34.815968],
          [-80.425902, 34.81581],
          [-80.419586, 34.815581],
          [-80.418433, 34.815622],
          [-80.417014, 34.815508],
          [-80.399871, 34.815128],
          [-80.320413, 34.813813],
          [-80.098994, 34.810147],
          [-80.098022, 34.810147],
          [-80.077223, 34.809716],
          [-80.072912, 34.809736],
          [-80.042764, 34.809097],
          [-80.027464, 34.808726],
          [-79.927618, 34.806555],
          [-79.871959, 34.693841],
          [-79.778249, 34.614868],
          [-79.829981, 34.531925],
          [-79.884542, 34.491218],
          [-80.045439, 34.492631],
          [-80.288596, 34.366207]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46063",
      state: "46",
      county: "063",
      name: "Harding County",
      censusArea: 2671.375,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.957281, 45.212851],
          [-104.040274, 45.212891],
          [-104.040358, 45.335946],
          [-104.040265, 45.345356],
          [-104.040114, 45.374214],
          [-104.04041, 45.393474],
          [-104.041764, 45.490789],
          [-104.041274, 45.499994],
          [-104.041145, 45.503367],
          [-104.041717, 45.539122],
          [-104.041647, 45.550691],
          [-104.041937, 45.557915],
          [-104.043814, 45.868385],
          [-104.044009, 45.871974],
          [-104.04403, 45.881971],
          [-104.04403, 45.881975],
          [-104.045443, 45.94531],
          [-103.668479, 45.945242],
          [-103.660779, 45.945241],
          [-103.660779, 45.945231],
          [-103.577083, 45.945283],
          [-103.55871, 45.945131],
          [-103.434851, 45.945291],
          [-103.432393, 45.945313],
          [-103.41804, 45.945186],
          [-103.411325, 45.945264],
          [-103.284109, 45.945152],
          [-103.284092, 45.945149],
          [-103.218396, 45.945208],
          [-103.210634, 45.945222],
          [-103.161251, 45.945309],
          [-103.140939, 45.945257],
          [-103.097872, 45.945262],
          [-103.078477, 45.945289],
          [-103.047779, 45.945335],
          [-103.026058, 45.945307],
          [-102.995668, 45.945167],
          [-102.995345, 45.945166],
          [-102.989902, 45.945211],
          [-102.94207, 45.945092],
          [-102.957281, 45.212851]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46065",
      state: "46",
      county: "065",
      name: "Hughes County",
      censusArea: 741.56,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.676398, 44.548111],
          [-99.66367, 44.548089],
          [-99.663508, 44.217517],
          [-99.695752, 44.172375],
          [-99.612249, 44.103694],
          [-99.884485, 44.129582],
          [-99.938217, 44.195195],
          [-99.892101, 44.241881],
          [-100.08533, 44.308451],
          [-100.36539, 44.355524],
          [-100.386376, 44.440008],
          [-100.573957, 44.455475],
          [-100.526498, 44.547422],
          [-99.676398, 44.548111]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46069",
      state: "46",
      county: "069",
      name: "Hyde County",
      censusArea: 860.524,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.663508, 44.217517],
          [-99.66367, 44.548089],
          [-99.676398, 44.548111],
          [-99.67305, 44.897492],
          [-99.572699, 44.897263],
          [-99.311754, 44.897227],
          [-99.30018, 44.19483],
          [-99.576571, 44.192421],
          [-99.593941, 44.204045],
          [-99.63235, 44.215661],
          [-99.663508, 44.217517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46075",
      state: "46",
      county: "075",
      name: "Jones County",
      censusArea: 969.678,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.367207, 44.168694],
          [-100.366174, 43.846978],
          [-100.33897, 43.716942],
          [-100.473105, 43.720891],
          [-100.58338, 43.768012],
          [-100.735003, 43.707621],
          [-100.789635, 43.725438],
          [-100.795732, 43.754055],
          [-101.031377, 43.857896],
          [-101.064326, 43.84109],
          [-101.064068, 43.994929],
          [-101.046786, 43.99497],
          [-101.046916, 44.168484],
          [-100.367207, 44.168694]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46079",
      state: "46",
      county: "079",
      name: "Lake County",
      censusArea: 563.277,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.370115, 44.194971],
          [-97.129671, 44.195933],
          [-96.889216, 44.195705],
          [-96.888664, 43.848385],
          [-97.129089, 43.847973],
          [-97.369463, 43.848526],
          [-97.370115, 44.194971]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46085",
      state: "46",
      county: "085",
      name: "Lyman County",
      censusArea: 1641.936,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.663508, 44.217517],
          [-99.63235, 44.215661],
          [-99.593941, 44.204045],
          [-99.576571, 44.192421],
          [-99.562536, 44.101519],
          [-99.353717, 43.995321],
          [-99.355864, 43.934371],
          [-99.310826, 43.855216],
          [-99.41015, 43.774619],
          [-99.458809, 43.6558],
          [-99.375378, 43.656869],
          [-99.297882, 43.499856],
          [-99.297998, 43.499669],
          [-99.529121, 43.499783],
          [-99.534482, 43.499648],
          [-99.623578, 43.499859],
          [-99.623587, 43.742414],
          [-99.656181, 43.765925],
          [-99.667613, 43.730241],
          [-99.688111, 43.759074],
          [-99.729978, 43.718827],
          [-99.837825, 43.689071],
          [-99.981465, 43.687119],
          [-100.016612, 43.706746],
          [-100.115169, 43.685434],
          [-100.230845, 43.713856],
          [-100.33897, 43.716942],
          [-100.366174, 43.846978],
          [-100.367207, 44.168694],
          [-100.367247, 44.194621],
          [-99.938217, 44.195195],
          [-99.884485, 44.129582],
          [-99.612249, 44.103694],
          [-99.695752, 44.172375],
          [-99.663508, 44.217517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46093",
      state: "46",
      county: "093",
      name: "Meade County",
      censusArea: 3470.984,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.957371, 45.039569],
          [-101.999993, 45.037999],
          [-102.000432, 44.511663],
          [-102.000841, 44.511259],
          [-102.001068, 44.510926],
          [-102.164179, 44.427466],
          [-102.306223, 44.434085],
          [-102.388045, 44.140183],
          [-103.452453, 44.140772],
          [-103.453016, 44.256609],
          [-103.574396, 44.256077],
          [-103.567653, 44.604098],
          [-102.964125, 44.604287],
          [-102.957371, 45.039569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46097",
      state: "46",
      county: "097",
      name: "Miner County",
      censusArea: 570.311,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.370115, 44.194971],
          [-97.369463, 43.848526],
          [-97.608517, 43.849059],
          [-97.85111, 43.849857],
          [-97.849492, 44.195235],
          [-97.370115, 44.194971]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46101",
      state: "46",
      county: "101",
      name: "Moody County",
      censusArea: 519.386,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.888664, 43.848385],
          [-96.889216, 44.195705],
          [-96.452774, 44.19678],
          [-96.453187, 44.03835],
          [-96.453313, 44.03643],
          [-96.453405, 44.025413],
          [-96.453373, 44.023744],
          [-96.453053, 44.008887],
          [-96.453116, 44.006876],
          [-96.453297, 43.994723],
          [-96.453328, 43.992871],
          [-96.453263, 43.980277],
          [-96.453389, 43.97806],
          [-96.453292, 43.96718],
          [-96.453165, 43.96654],
          [-96.453289, 43.950814],
          [-96.453352, 43.949122],
          [-96.453183, 43.87865],
          [-96.453304, 43.878583],
          [-96.453335, 43.877029],
          [-96.453264, 43.849604],
          [-96.453264, 43.849506],
          [-96.453264, 43.849501],
          [-96.508636, 43.847591],
          [-96.888664, 43.848385]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46113",
      state: "46",
      county: "113",
      name: "Shannon County",
      censusArea: 2093.898,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.000785, 43.476847],
          [-103.001068, 43.605799],
          [-102.9025, 43.674888],
          [-102.810419, 43.688132],
          [-102.175231, 43.687756],
          [-102.139158, 43.700948],
          [-102.139051, 43.476676],
          [-102.110819, 43.389902],
          [-102.108892, 43.129258],
          [-102.082486, 42.999356],
          [-102.082546, 42.999356],
          [-102.440547, 42.999609],
          [-102.487329, 42.999559],
          [-102.792111, 42.99998],
          [-103.000897, 43.000474],
          [-103.000785, 43.476847]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46115",
      state: "46",
      county: "115",
      name: "Spink County",
      censusArea: 1503.925,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.981457, 45.240415],
          [-97.981511, 45.172156],
          [-97.981641, 45.170965],
          [-97.981792, 45.153295],
          [-97.977791, 44.631602],
          [-98.705762, 44.634159],
          [-98.705357, 44.89687],
          [-98.716498, 44.896925],
          [-98.717759, 45.243732],
          [-97.981457, 45.240415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46117",
      state: "46",
      county: "117",
      name: "Stanley County",
      censusArea: 1444.432,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.136838, 44.747126],
          [-101.022611, 44.724153],
          [-100.829795, 44.783248],
          [-100.717644, 44.77093],
          [-100.613805, 44.707135],
          [-100.640061, 44.583723],
          [-100.526498, 44.547422],
          [-100.573957, 44.455475],
          [-100.386376, 44.440008],
          [-100.36539, 44.355524],
          [-100.08533, 44.308451],
          [-99.892101, 44.241881],
          [-99.938217, 44.195195],
          [-100.367247, 44.194621],
          [-100.367207, 44.168694],
          [-101.046916, 44.168484],
          [-101.166753, 44.168085],
          [-101.154516, 44.689947],
          [-101.136838, 44.747126]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46121",
      state: "46",
      county: "121",
      name: "Todd County",
      censusArea: 1388.556,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.228104, 42.997874],
          [-101.227749, 43.002087],
          [-101.228179, 43.389185],
          [-100.214221, 43.390321],
          [-100.198413, 42.998542],
          [-100.198434, 42.998542],
          [-100.277793, 42.998674],
          [-100.283713, 42.998767],
          [-100.349548, 42.99874],
          [-100.355406, 42.99876],
          [-100.472742, 42.999288],
          [-100.534335, 42.999017],
          [-100.544018, 42.998795],
          [-100.553131, 42.998721],
          [-100.887898, 42.997881],
          [-100.906714, 42.99791],
          [-100.958365, 42.997796],
          [-100.96419, 42.997886],
          [-101.226494, 42.997901],
          [-101.226853, 42.997896],
          [-101.228104, 42.997874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46127",
      state: "46",
      county: "127",
      name: "Union County",
      censusArea: 460.542,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.805682, 43.08367],
          [-96.455107, 43.083366],
          [-96.466017, 43.062235],
          [-96.513111, 43.02788],
          [-96.509479, 42.971122],
          [-96.510749, 42.944397],
          [-96.523264, 42.909059],
          [-96.54146, 42.857682],
          [-96.549513, 42.839143],
          [-96.552092, 42.836057],
          [-96.563058, 42.831051],
          [-96.577813, 42.828102],
          [-96.632142, 42.770863],
          [-96.633168, 42.768325],
          [-96.639704, 42.737071],
          [-96.630617, 42.70588],
          [-96.610975, 42.694751],
          [-96.60614, 42.694661],
          [-96.601989, 42.697429],
          [-96.575299, 42.682665],
          [-96.542366, 42.660736],
          [-96.516338, 42.630435],
          [-96.479685, 42.561238],
          [-96.476952, 42.556079],
          [-96.477709, 42.535595],
          [-96.479909, 42.524195],
          [-96.481308, 42.516556],
          [-96.476947, 42.508677],
          [-96.466253, 42.497702],
          [-96.443408, 42.489495],
          [-96.475565, 42.480036],
          [-96.48749, 42.479846],
          [-96.611489, 42.506088],
          [-96.625958, 42.513576],
          [-96.635982, 42.524324],
          [-96.708049, 42.601594],
          [-96.711332, 42.610399],
          [-96.710604, 42.619625],
          [-96.707245, 42.631258],
          [-96.712871, 42.648136],
          [-96.724033, 42.665971],
          [-96.806553, 42.703405],
          [-96.806213, 42.704154],
          [-96.805682, 43.08367]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30005",
      state: "30",
      county: "005",
      name: "Blaine County",
      censusArea: 4227.546,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.489686, 49.000267],
          [-109.384762, 49.000397],
          [-109.384068, 49.000374],
          [-109.06057, 48.999666],
          [-109.060292, 48.999621],
          [-109.000708, 48.999234],
          [-108.994722, 48.999237],
          [-108.543194, 48.999377],
          [-108.488063, 48.999368],
          [-108.236495, 48.999405],
          [-108.259372, 48.913592],
          [-108.259347, 48.742191],
          [-108.319144, 48.74229],
          [-108.318834, 48.568432],
          [-108.346193, 48.56845],
          [-108.34593, 48.422296],
          [-108.410374, 48.448484],
          [-108.411366, 48.219443],
          [-108.433848, 48.219443],
          [-108.433966, 47.976552],
          [-108.608027, 47.991075],
          [-108.625708, 47.92082],
          [-108.888163, 47.922746],
          [-108.891022, 47.734407],
          [-108.942745, 47.791824],
          [-109.274171, 47.793239],
          [-109.425315, 47.720153],
          [-109.538454, 47.738894],
          [-109.534295, 48.135086],
          [-109.551644, 48.293479],
          [-109.497259, 48.451748],
          [-109.464257, 48.452395],
          [-109.464442, 48.52526],
          [-109.505322, 48.567612],
          [-109.505778, 48.913913],
          [-109.489686, 49.000267]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30009",
      state: "30",
      county: "009",
      name: "Carbon County",
      censusArea: 2048.788,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.249345, 44.999458],
          [-108.565921, 45.000578],
          [-108.578484, 45.000484],
          [-108.621313, 45.000408],
          [-109.062262, 44.999623],
          [-109.08301, 44.99961],
          [-109.103445, 45.005904],
          [-109.263431, 45.005345],
          [-109.269294, 45.005283],
          [-109.798687, 45.002188],
          [-109.798221, 45.167405],
          [-109.691105, 45.167198],
          [-109.690497, 45.261265],
          [-109.60866, 45.261214],
          [-109.562014, 45.36294],
          [-109.356241, 45.464445],
          [-109.191508, 45.464272],
          [-109.191449, 45.523141],
          [-109.12846, 45.523053],
          [-109.12781, 45.587454],
          [-108.842697, 45.611006],
          [-108.801661, 45.552067],
          [-108.644971, 45.464091],
          [-108.682105, 45.433093],
          [-108.699552, 45.217952],
          [-108.127979, 45.217911],
          [-108.25863, 45.007576],
          [-108.249345, 44.999458]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30015",
      state: "30",
      county: "015",
      name: "Chouteau County",
      censusArea: 3972.488,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.408495, 47.987045],
          [-111.409097, 48.132218],
          [-110.827168, 48.133173],
          [-110.827034, 48.219669],
          [-110.755649, 48.219671],
          [-110.625528, 48.219625],
          [-110.625313, 48.306479],
          [-109.845734, 48.305971],
          [-109.845811, 48.219109],
          [-109.72694, 48.221419],
          [-109.727044, 48.132619],
          [-109.534295, 48.135086],
          [-109.538454, 47.738894],
          [-109.836811, 47.712253],
          [-109.850627, 47.539301],
          [-110.159419, 47.415338],
          [-110.213293, 47.416659],
          [-110.639564, 47.416259],
          [-110.639934, 47.445332],
          [-110.769218, 47.445996],
          [-110.768771, 47.524837],
          [-110.863942, 47.52506],
          [-111.049832, 47.598325],
          [-110.976382, 47.698605],
          [-111.409117, 47.698277],
          [-111.408495, 47.987045]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30019",
      state: "30",
      county: "019",
      name: "Daniels County",
      censusArea: 1426.105,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.804432, 48.563318],
          [-106.021212, 48.56322],
          [-106.021358, 48.823975],
          [-106.15194, 48.823898],
          [-106.112108, 48.999279],
          [-106.050543, 48.999207],
          [-105.966197, 48.999445],
          [-105.834181, 48.999707],
          [-105.775808, 48.999637],
          [-105.65027, 48.999444],
          [-105.612577, 48.999703],
          [-105.607542, 48.999624],
          [-105.578616, 48.999673],
          [-105.522636, 48.999591],
          [-105.411972, 48.999582],
          [-105.407909, 48.99948],
          [-105.391379, 48.999475],
          [-105.38749, 48.999382],
          [-105.355888, 48.999357],
          [-105.277521, 48.999457],
          [-105.265192, 48.9995],
          [-105.057514, 48.999448],
          [-105.038567, 48.650232],
          [-104.973283, 48.650085],
          [-104.973354, 48.56319],
          [-105.804432, 48.563318]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30023",
      state: "30",
      county: "023",
      name: "Deer Lodge County",
      censusArea: 736.53,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.085474, 45.860586],
          [-113.179791, 45.885009],
          [-113.395063, 45.76649],
          [-113.501295, 45.940807],
          [-113.516338, 45.938638],
          [-113.516912, 45.93942],
          [-113.517481, 45.939898],
          [-113.274361, 46.059484],
          [-113.284652, 46.222761],
          [-113.03716, 46.223078],
          [-113.036834, 46.266822],
          [-112.558457, 46.265925],
          [-112.57685, 46.178859],
          [-112.777685, 46.135807],
          [-112.777105, 46.048363],
          [-112.938373, 45.993224],
          [-112.923572, 45.935524],
          [-113.085474, 45.860586]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30029",
      state: "30",
      county: "029",
      name: "Flathead County",
      censusArea: 5087.66,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.34947, 48.310155],
          [-113.236785, 48.247345],
          [-113.231495, 48.170652],
          [-113.016894, 48.13246],
          [-112.878615, 47.975766],
          [-112.984705, 47.953982],
          [-113.08667, 47.918109],
          [-113.080938, 47.749885],
          [-113.165906, 47.720729],
          [-113.144926, 47.596363],
          [-113.466486, 47.599986],
          [-113.633957, 47.599861],
          [-113.604004, 47.72153],
          [-113.681245, 47.794662],
          [-113.667442, 47.890291],
          [-113.747738, 47.953079],
          [-113.841867, 47.978185],
          [-113.885757, 48.051129],
          [-114.028314, 48.051911],
          [-114.078146, 48.023454],
          [-114.038545, 47.96255],
          [-114.491992, 47.961906],
          [-114.477186, 47.789841],
          [-114.604326, 47.789744],
          [-114.604407, 47.875605],
          [-114.98963, 47.872819],
          [-115.010806, 48.017256],
          [-115.020079, 48.225954],
          [-114.847946, 48.226159],
          [-114.849699, 48.573549],
          [-114.888446, 48.573612],
          [-114.888676, 48.65849],
          [-114.639014, 48.658959],
          [-114.692445, 48.680372],
          [-114.611925, 48.751888],
          [-114.73248, 48.809234],
          [-114.691742, 48.842091],
          [-114.728125, 49.000623],
          [-114.678217, 49.000725],
          [-114.674398, 49.000679],
          [-114.43883, 49.001076],
          [-114.068151, 48.999364],
          [-114.014249, 48.907927],
          [-114.05809, 48.888935],
          [-113.957138, 48.817636],
          [-113.911428, 48.850196],
          [-113.790355, 48.845427],
          [-113.71491, 48.742789],
          [-113.750295, 48.613931],
          [-113.467378, 48.5476],
          [-113.480934, 48.459711],
          [-113.333137, 48.38782],
          [-113.34947, 48.310155]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30035",
      state: "30",
      county: "035",
      name: "Glacier County",
      censusArea: 2995.937,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.193588, 48.998229],
          [-112.184375, 48.477593],
          [-112.229874, 48.483081],
          [-112.576892, 48.483478],
          [-112.575885, 48.309372],
          [-113.34947, 48.310155],
          [-113.333137, 48.38782],
          [-113.480934, 48.459711],
          [-113.467378, 48.5476],
          [-113.750295, 48.613931],
          [-113.71491, 48.742789],
          [-113.790355, 48.845427],
          [-113.911428, 48.850196],
          [-113.957138, 48.817636],
          [-114.05809, 48.888935],
          [-114.014249, 48.907927],
          [-114.068151, 48.999364],
          [-113.692982, 48.997632],
          [-113.116356, 48.998462],
          [-113.110155, 48.99855],
          [-113.106891, 48.998501],
          [-113.103212, 48.99853],
          [-113.098147, 48.998494],
          [-113.095436, 48.998533],
          [-113.092055, 48.998543],
          [-113.087863, 48.998557],
          [-113.085576, 48.998581],
          [-113.011041, 48.998643],
          [-113.009895, 48.998619],
          [-112.193588, 48.998229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30041",
      state: "30",
      county: "041",
      name: "Hill County",
      censusArea: 2898.955,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.489686, 49.000267],
          [-109.505778, 48.913913],
          [-109.505322, 48.567612],
          [-109.464442, 48.52526],
          [-109.464257, 48.452395],
          [-109.497259, 48.451748],
          [-109.551644, 48.293479],
          [-109.534295, 48.135086],
          [-109.727044, 48.132619],
          [-109.72694, 48.221419],
          [-109.845811, 48.219109],
          [-109.845734, 48.305971],
          [-110.625313, 48.306479],
          [-110.625528, 48.219625],
          [-110.755649, 48.219671],
          [-110.743209, 48.998363],
          [-110.216135, 48.999239],
          [-110.215516, 48.999197],
          [-110.171595, 48.999262],
          [-109.995618, 48.999642],
          [-109.489686, 49.000267]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31129",
      state: "31",
      county: "129",
      name: "Nuckolls County",
      censusArea: 575.155,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.931826, 40.00205],
          [-97.972186, 40.002114],
          [-98.010157, 40.002153],
          [-98.014412, 40.002223],
          [-98.047469, 40.002186],
          [-98.050057, 40.002278],
          [-98.068701, 40.002355],
          [-98.076034, 40.002301],
          [-98.099659, 40.002227],
          [-98.142031, 40.002452],
          [-98.172269, 40.002438],
          [-98.179315, 40.002483],
          [-98.193483, 40.002614],
          [-98.250008, 40.002307],
          [-98.268218, 40.00249],
          [-98.274015, 40.002516],
          [-98.274017, 40.002516],
          [-98.273776, 40.005385],
          [-98.273571, 40.350359],
          [-97.824114, 40.350541],
          [-97.821458, 40.350561],
          [-97.820816, 40.350545],
          [-97.821496, 40.002002],
          [-97.821598, 40.002004],
          [-97.838379, 40.00191],
          [-97.85745, 40.002065],
          [-97.876261, 40.002102],
          [-97.931811, 40.00205],
          [-97.931826, 40.00205]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31133",
      state: "31",
      county: "133",
      name: "Pawnee County",
      censusArea: 431.075,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.301066, 40.000632],
          [-96.304555, 40.000629],
          [-96.46364, 40.000967],
          [-96.463713, 40.000968],
          [-96.463667, 40.262173],
          [-96.066736, 40.262038],
          [-96.012628, 40.261449],
          [-96.010176, 40.261482],
          [-96.01068, 40.000638],
          [-96.02409, 40.000719],
          [-96.051691, 40.000727],
          [-96.081395, 40.000603],
          [-96.089781, 40.000519],
          [-96.125788, 40.000467],
          [-96.125937, 40.000432],
          [-96.147167, 40.000479],
          [-96.154246, 40.00045],
          [-96.154365, 40.000495],
          [-96.220171, 40.00072],
          [-96.223839, 40.000729],
          [-96.239172, 40.000691],
          [-96.239208, 40.000691],
          [-96.301066, 40.000632]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31141",
      state: "31",
      county: "141",
      name: "Platte County",
      censusArea: 674.059,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.253519, 41.384212],
          [-97.368186, 41.396416],
          [-97.598253, 41.333119],
          [-97.598461, 41.395068],
          [-97.703765, 41.394876],
          [-97.703627, 41.526796],
          [-97.829846, 41.526174],
          [-97.830493, 41.742238],
          [-97.36807, 41.743207],
          [-97.252425, 41.743085],
          [-97.253519, 41.384212]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19007",
      state: "19",
      county: "007",
      name: "Appanoose County",
      censusArea: 497.292,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.637898, 40.590853],
          [-92.639223, 40.590825],
          [-92.686693, 40.589809],
          [-92.689854, 40.589884],
          [-92.714598, 40.589564],
          [-92.742232, 40.589207],
          [-92.757407, 40.588908],
          [-92.828061, 40.588593],
          [-92.827992, 40.588515],
          [-92.835074, 40.588484],
          [-92.857391, 40.58836],
          [-92.863034, 40.588175],
          [-92.879178, 40.588341],
          [-92.889796, 40.588039],
          [-92.903544, 40.58786],
          [-92.941595, 40.587743],
          [-92.957747, 40.58743],
          [-93.085517, 40.584403],
          [-93.097296, 40.584014],
          [-93.097595, 40.898451],
          [-92.639091, 40.898886],
          [-92.637898, 40.590853]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19011",
      state: "19",
      county: "011",
      name: "Benton County",
      censusArea: 716.265,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.831089, 42.299063],
          [-91.831379, 41.861851],
          [-92.298164, 41.862779],
          [-92.298792, 42.297527],
          [-92.064766, 42.297259],
          [-91.831089, 42.299063]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19015",
      state: "19",
      county: "015",
      name: "Boone County",
      censusArea: 571.569,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.69839, 42.209339],
          [-93.698032, 41.86337],
          [-93.815527, 41.863419],
          [-94.164138, 41.863244],
          [-94.164704, 42.20992],
          [-93.931581, 42.210095],
          [-93.69839, 42.209339]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19019",
      state: "19",
      county: "019",
      name: "Buchanan County",
      censusArea: 571.02,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.596981, 42.296408],
          [-91.831089, 42.299063],
          [-92.064766, 42.297259],
          [-92.081661, 42.642056],
          [-91.607059, 42.643973],
          [-91.596981, 42.296408]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19023",
      state: "19",
      county: "023",
      name: "Butler County",
      censusArea: 580.131,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.025144, 42.907549],
          [-92.554211, 42.907112],
          [-92.554492, 42.642314],
          [-92.554561, 42.55935],
          [-92.5543, 42.555854],
          [-93.027, 42.55681],
          [-93.025144, 42.907549]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19027",
      state: "19",
      county: "027",
      name: "Carroll County",
      censusArea: 569.437,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.858412, 42.209692],
          [-94.628806, 42.209396],
          [-94.628724, 41.862763],
          [-94.667977, 41.862682],
          [-94.671695, 41.862828],
          [-94.672368, 41.86289],
          [-94.673119, 41.86265],
          [-94.674259, 41.862657],
          [-94.67542, 41.862895],
          [-94.676942, 41.862994],
          [-94.744876, 41.862394],
          [-95.092861, 41.863374],
          [-95.090851, 42.210405],
          [-94.858412, 42.209692]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19033",
      state: "19",
      county: "033",
      name: "Cerro Gordo County",
      censusArea: 568.313,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.024143, 43.255538],
          [-93.024092, 43.212812],
          [-93.025144, 42.907549],
          [-93.498617, 42.908512],
          [-93.497635, 43.255468],
          [-93.024143, 43.255538]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17081",
      state: "17",
      county: "081",
      name: "Jefferson County",
      censusArea: 571.169,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.698961, 38.474914],
          [-88.702391, 38.256661],
          [-88.704606, 38.125195],
          [-89.129637, 38.124747],
          [-89.149739, 38.124945],
          [-89.147416, 38.212896],
          [-89.144388, 38.473878],
          [-88.698961, 38.474914]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17091",
      state: "17",
      county: "091",
      name: "Kankakee County",
      censusArea: 676.556,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.526768, 41.298052],
          [-87.52657, 41.166097],
          [-87.526567, 41.163865],
          [-87.52666, 41.16009],
          [-87.526719, 41.159448],
          [-87.526693, 41.153958],
          [-87.526696, 41.149222],
          [-87.5267, 41.139658],
          [-87.526711, 41.121485],
          [-87.52652, 41.024837],
          [-87.526346, 41.010583],
          [-87.526307, 41.010355],
          [-88.131938, 40.997839],
          [-88.133741, 40.997605],
          [-88.236498, 40.995218],
          [-88.245975, 40.994607],
          [-88.2473, 40.99456],
          [-88.248258, 41.023547],
          [-88.25113, 41.10353],
          [-88.251995, 41.114229],
          [-88.240889, 41.114383],
          [-88.244155, 41.201546],
          [-88.011812, 41.205604],
          [-88.013919, 41.292447],
          [-87.526768, 41.298052]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17093",
      state: "17",
      county: "093",
      name: "Kendall County",
      censusArea: 320.335,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.261468, 41.724529],
          [-88.257274, 41.593469],
          [-88.252098, 41.463096],
          [-88.254972, 41.462768],
          [-88.59596, 41.457034],
          [-88.60224, 41.631389],
          [-88.60362, 41.719546],
          [-88.601933, 41.719563],
          [-88.261952, 41.724652],
          [-88.261468, 41.724529]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17099",
      state: "17",
      county: "099",
      name: "La Salle County",
      censusArea: 1135.124,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.166561, 41.585289],
          [-89.1672, 41.628707],
          [-88.938618, 41.628319],
          [-88.818462, 41.631352],
          [-88.60224, 41.631389],
          [-88.59596, 41.457034],
          [-88.58624, 41.108293],
          [-88.930881, 41.1059],
          [-88.93139, 40.92774],
          [-89.047718, 40.925749],
          [-89.047856, 41.104781],
          [-89.162238, 41.10408],
          [-89.163705, 41.310187],
          [-89.166561, 41.585289]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17101",
      state: "17",
      county: "101",
      name: "Lawrence County",
      censusArea: 372.178,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.532064, 38.852503],
          [-87.528194, 38.851691],
          [-87.525893, 38.848795],
          [-87.495211, 38.78306],
          [-87.498515, 38.758983],
          [-87.519609, 38.697198],
          [-87.531231, 38.684036],
          [-87.540052, 38.679143],
          [-87.579397, 38.672475],
          [-87.593678, 38.667402],
          [-87.60018, 38.662462],
          [-87.62012, 38.639489],
          [-87.627345, 38.607237],
          [-87.651529, 38.568166],
          [-87.653977, 38.568289],
          [-87.760094, 38.568783],
          [-87.769353, 38.568937],
          [-87.912286, 38.570104],
          [-87.908113, 38.850107],
          [-87.759592, 38.849514],
          [-87.532064, 38.852503]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17105",
      state: "17",
      county: "105",
      name: "Livingston County",
      censusArea: 1044.285,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.459475, 40.617345],
          [-88.574502, 40.61655],
          [-88.584272, 40.757608],
          [-88.814487, 40.75506],
          [-88.929331, 40.753337],
          [-88.93139, 40.92774],
          [-88.930881, 41.1059],
          [-88.58624, 41.108293],
          [-88.36314, 41.11158],
          [-88.251995, 41.114229],
          [-88.25113, 41.10353],
          [-88.248258, 41.023547],
          [-88.2473, 40.99456],
          [-88.234949, 40.618166],
          [-88.459475, 40.617345]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17109",
      state: "17",
      county: "109",
      name: "Mc Donough County",
      censusArea: 589.407,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.789652, 40.63575],
          [-90.445506, 40.627843],
          [-90.450227, 40.276335],
          [-90.909756, 40.284394],
          [-90.904216, 40.639201],
          [-90.894339, 40.638683],
          [-90.789652, 40.63575]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17111",
      state: "17",
      county: "111",
      name: "Mc Henry County",
      censusArea: 603.171,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.304692, 42.494773],
          [-88.271691, 42.494818],
          [-88.2169, 42.495923],
          [-88.200172, 42.496016],
          [-88.199521, 42.496013],
          [-88.19875, 42.25422],
          [-88.199584, 42.15426],
          [-88.237402, 42.154281],
          [-88.238369, 42.154253],
          [-88.588657, 42.15359],
          [-88.705633, 42.153561],
          [-88.707378, 42.493587],
          [-88.506912, 42.494883],
          [-88.470597, 42.494672],
          [-88.461397, 42.494618],
          [-88.417396, 42.494618],
          [-88.304692, 42.494773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17117",
      state: "17",
      county: "117",
      name: "Macoupin County",
      censusArea: 862.906,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.813147, 39.522838],
          [-89.804458, 39.522876],
          [-89.800366, 39.523025],
          [-89.795316, 39.522939],
          [-89.701645, 39.523369],
          [-89.700338, 39.17544],
          [-89.698555, 38.998979],
          [-89.70402, 38.99894],
          [-89.705349, 38.998963],
          [-89.705675, 38.999161],
          [-89.707266, 38.999012],
          [-89.707911, 38.999009],
          [-89.811673, 38.998462],
          [-89.81462, 38.998349],
          [-90.145991, 39.000046],
          [-90.148069, 39.261947],
          [-90.149173, 39.334597],
          [-90.150641, 39.346315],
          [-90.15375, 39.520315],
          [-89.926037, 39.522104],
          [-89.813147, 39.522838]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17125",
      state: "17",
      county: "125",
      name: "Mason County",
      censusArea: 539.238,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.199556, 40.183945],
          [-90.118966, 40.235263],
          [-90.033026, 40.377806],
          [-89.92468, 40.435921],
          [-89.717104, 40.435655],
          [-89.714927, 40.319218],
          [-89.602979, 40.320129],
          [-89.601604, 40.122432],
          [-89.638262, 40.15487],
          [-89.749196, 40.126852],
          [-89.97049, 40.142082],
          [-89.994724, 40.108373],
          [-90.196609, 40.052499],
          [-90.286493, 40.055023],
          [-90.355198, 40.124245],
          [-90.199556, 40.183945]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17127",
      state: "17",
      county: "127",
      name: "Massac County",
      censusArea: 237.218,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.564152, 37.07844],
          [-88.576718, 37.085852],
          [-88.589207, 37.099655],
          [-88.625889, 37.119458],
          [-88.809206, 37.189235],
          [-88.916934, 37.224291],
          [-88.928021, 37.226281],
          [-88.929129, 37.302647],
          [-88.904003, 37.335629],
          [-88.71065, 37.33709],
          [-88.490336, 37.159358],
          [-88.490297, 37.066669],
          [-88.514356, 37.065231],
          [-88.531576, 37.067192],
          [-88.545403, 37.070003],
          [-88.560032, 37.07601],
          [-88.564152, 37.07844]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13185",
      state: "13",
      county: "185",
      name: "Lowndes County",
      censusArea: 496.066,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.475617, 31.031964],
          [-83.295052, 31.027278],
          [-83.280476, 31.02658],
          [-83.273731, 31.026658],
          [-83.197971, 31.025405],
          [-83.179465, 30.950128],
          [-83.042924, 30.947296],
          [-83.019419, 30.849453],
          [-83.088082, 30.848658],
          [-83.136236, 30.775229],
          [-83.136616, 30.624346],
          [-83.15617, 30.625504],
          [-83.163309, 30.625895],
          [-83.174411, 30.626444],
          [-83.187391, 30.627223],
          [-83.256218, 30.631279],
          [-83.309249, 30.634405],
          [-83.30925, 30.634405],
          [-83.309455, 30.634417],
          [-83.311647, 30.634577],
          [-83.340852, 30.636336],
          [-83.341011, 30.636346],
          [-83.357703, 30.637359],
          [-83.482461, 30.756931],
          [-83.449293, 30.817957],
          [-83.358989, 30.83671],
          [-83.458129, 31.000859],
          [-83.460412, 31.002812],
          [-83.461306, 31.005709],
          [-83.463689, 31.005935],
          [-83.464269, 31.011093],
          [-83.466784, 31.013886],
          [-83.465898, 31.018502],
          [-83.467778, 31.021635],
          [-83.471324, 31.02386],
          [-83.471596, 31.026583],
          [-83.468295, 31.028481],
          [-83.470776, 31.028531],
          [-83.472275, 31.029742],
          [-83.47326, 31.028452],
          [-83.473792, 31.031276],
          [-83.476889, 31.030987],
          [-83.475617, 31.031964]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13189",
      state: "13",
      county: "189",
      name: "Mc Duffie County",
      censusArea: 257.461,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.425328, 33.650629],
          [-82.436814, 33.549658],
          [-82.294179, 33.354635],
          [-82.350299, 33.314801],
          [-82.353498, 33.312318],
          [-82.357647, 33.312717],
          [-82.371972, 33.310879],
          [-82.383829, 33.312106],
          [-82.549373, 33.357748],
          [-82.649705, 33.608768],
          [-82.479518, 33.639535],
          [-82.476969, 33.644281],
          [-82.46988, 33.638617],
          [-82.458237, 33.637471],
          [-82.451692, 33.649591],
          [-82.435952, 33.652046],
          [-82.430915, 33.655076],
          [-82.425328, 33.650629]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13195",
      state: "13",
      county: "195",
      name: "Madison County",
      censusArea: 282.311,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.258413, 33.999098],
          [-83.360028, 34.040572],
          [-83.402428, 34.197499],
          [-83.400125, 34.200525],
          [-83.372429, 34.205933],
          [-83.355269, 34.22373],
          [-83.297317, 34.264587],
          [-83.172745, 34.238522],
          [-83.11336, 34.27353],
          [-83.115646, 34.262111],
          [-83.078004, 34.223606],
          [-83.102074, 34.173794],
          [-82.976294, 34.043219],
          [-83.049347, 34.016071],
          [-83.123878, 34.047558],
          [-83.258413, 33.999098]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13199",
      state: "13",
      county: "199",
      name: "Meriwether County",
      censusArea: 501.223,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.862359, 33.191173],
          [-84.85236, 33.22359],
          [-84.502352, 33.221055],
          [-84.492889, 33.200006],
          [-84.496783, 33.183866],
          [-84.52702, 32.970548],
          [-84.506888, 32.881788],
          [-84.570669, 32.845179],
          [-84.700538, 32.84464],
          [-84.861768, 32.872495],
          [-84.862359, 33.191173]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13205",
      state: "13",
      county: "205",
      name: "Mitchell County",
      censusArea: 512.085,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.376612, 31.078883],
          [-84.508078, 31.0784],
          [-84.48331, 31.155536],
          [-84.40435, 31.199143],
          [-84.340552, 31.305341],
          [-84.20066, 31.355525],
          [-84.141124, 31.440179],
          [-83.997796, 31.443753],
          [-83.99943, 31.334966],
          [-84.003627, 31.07729],
          [-84.004502, 31.077192],
          [-84.116644, 31.077971],
          [-84.376612, 31.078883]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13209",
      state: "13",
      county: "209",
      name: "Montgomery County",
      censusArea: 239.523,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.40913, 32.353738],
          [-82.483131, 31.968961],
          [-82.543655, 31.958914],
          [-82.598068, 32.014429],
          [-82.612876, 32.161549],
          [-82.655486, 32.297561],
          [-82.40913, 32.353738]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13213",
      state: "13",
      county: "213",
      name: "Murray County",
      censusArea: 344.466,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.775852, 34.9878],
          [-84.731022, 34.988088],
          [-84.727434, 34.98802],
          [-84.621483, 34.988329],
          [-84.618637, 34.855398],
          [-84.582306, 34.825141],
          [-84.657098, 34.728905],
          [-84.654523, 34.583187],
          [-84.716498, 34.6229],
          [-84.863793, 34.608597],
          [-84.913456, 34.634128],
          [-84.933722, 34.706799],
          [-84.864945, 34.707356],
          [-84.878499, 34.77961],
          [-84.818715, 34.87695],
          [-84.810477, 34.987607],
          [-84.809184, 34.987569],
          [-84.808127, 34.987592],
          [-84.775852, 34.9878]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13221",
      state: "13",
      county: "221",
      name: "Oglethorpe County",
      censusArea: 439.012,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.779506, 33.971124],
          [-82.874587, 33.921767],
          [-82.987286, 33.78071],
          [-82.949046, 33.733333],
          [-82.98888, 33.706315],
          [-82.995602, 33.693583],
          [-83.118729, 33.698818],
          [-83.28034, 33.761774],
          [-83.275933, 33.847977],
          [-83.24086, 33.90443],
          [-83.258413, 33.999098],
          [-83.123878, 34.047558],
          [-83.049347, 34.016071],
          [-82.976294, 34.043219],
          [-82.779506, 33.971124]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13229",
      state: "13",
      county: "229",
      name: "Pierce County",
      censusArea: 316.494,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.417246, 31.417119],
          [-82.344843, 31.43013],
          [-82.226585, 31.530699],
          [-82.132794, 31.471262],
          [-82.052754, 31.454567],
          [-82.041129, 31.373721],
          [-82.006775, 31.277063],
          [-82.100067, 31.27671],
          [-82.194824, 31.20759],
          [-82.284561, 31.224449],
          [-82.383705, 31.291143],
          [-82.417246, 31.417119]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13231",
      state: "13",
      county: "231",
      name: "Pike County",
      censusArea: 216.086,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.248185, 33.189019],
          [-84.27014, 32.991011],
          [-84.489708, 32.993729],
          [-84.52702, 32.970548],
          [-84.496783, 33.183866],
          [-84.451336, 33.208889],
          [-84.248185, 33.189019]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13235",
      state: "13",
      county: "235",
      name: "Pulaski County",
      censusArea: 249.031,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.498039, 32.401715],
          [-83.346528, 32.272489],
          [-83.291106, 32.178825],
          [-83.337313, 32.105885],
          [-83.608547, 32.118405],
          [-83.615579, 32.288556],
          [-83.498039, 32.401715]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13241",
      state: "13",
      county: "241",
      name: "Rabun County",
      censusArea: 369.993,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.108535, 35.000771],
          [-83.143261, 34.924756],
          [-83.242962, 34.877749],
          [-83.267293, 34.832748],
          [-83.283151, 34.821328],
          [-83.32406, 34.788691],
          [-83.339129, 34.741266],
          [-83.352422, 34.716111],
          [-83.548988, 34.749451],
          [-83.586897, 34.819991],
          [-83.652194, 34.822993],
          [-83.656792, 34.878492],
          [-83.549381, 34.992492],
          [-83.483064, 34.993737],
          [-83.108714, 35.000768],
          [-83.108535, 35.000771]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13247",
      state: "13",
      county: "247",
      name: "Rockdale County",
      censusArea: 129.793,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.023713, 33.752808],
          [-83.984555, 33.784332],
          [-83.982033, 33.786054],
          [-83.954499, 33.76892],
          [-83.953332, 33.768034],
          [-83.914823, 33.744203],
          [-83.972655, 33.605482],
          [-84.024279, 33.548226],
          [-84.024854, 33.547507],
          [-84.044493, 33.525776],
          [-84.136289, 33.57233],
          [-84.17213, 33.621919],
          [-84.181584, 33.629174],
          [-84.184143, 33.646157],
          [-84.104334, 33.636025],
          [-84.056614, 33.726608],
          [-84.023713, 33.752808]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13255",
      state: "13",
      county: "255",
      name: "Spalding County",
      censusArea: 196.469,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.502352, 33.221055],
          [-84.50029, 33.233444],
          [-84.508926, 33.245222],
          [-84.497527, 33.257422],
          [-84.432907, 33.2565],
          [-84.388118, 33.352465],
          [-84.3544, 33.352514],
          [-84.150581, 33.335639],
          [-84.102582, 33.298191],
          [-84.123767, 33.202824],
          [-84.248185, 33.189019],
          [-84.451336, 33.208889],
          [-84.496783, 33.183866],
          [-84.492889, 33.200006],
          [-84.502352, 33.221055]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13257",
      state: "13",
      county: "257",
      name: "Stephens County",
      censusArea: 179.131,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.337867, 34.687681],
          [-83.291683, 34.653396],
          [-83.240669, 34.624507],
          [-83.240676, 34.624307],
          [-83.167523, 34.600424],
          [-83.170978, 34.598798],
          [-83.170278, 34.592398],
          [-83.106874, 34.534053],
          [-83.177838, 34.477017],
          [-83.398396, 34.460883],
          [-83.413586, 34.468014],
          [-83.45977, 34.481142],
          [-83.337867, 34.687681]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13267",
      state: "13",
      county: "267",
      name: "Tattnall County",
      censusArea: 479.403,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.761735, 32.0479],
          [-81.809931, 32.019657],
          [-81.824405, 32.014882],
          [-81.969052, 31.789324],
          [-81.981941, 31.784405],
          [-82.006777, 31.800047],
          [-82.014639, 31.8218],
          [-82.028346, 31.815513],
          [-82.048582, 31.827075],
          [-82.142843, 31.8993],
          [-82.225042, 31.913074],
          [-82.182017, 32.165593],
          [-82.232788, 32.318838],
          [-82.025339, 32.278883],
          [-81.980041, 32.081502],
          [-81.761735, 32.0479]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13269",
      state: "13",
      county: "269",
      name: "Taylor County",
      censusArea: 376.686,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.254613, 32.372053],
          [-84.392316, 32.414046],
          [-84.444353, 32.562083],
          [-84.408866, 32.561923],
          [-84.33794, 32.720647],
          [-84.286246, 32.747626],
          [-84.202628, 32.690018],
          [-84.104966, 32.673385],
          [-84.003363, 32.529927],
          [-84.001069, 32.520586],
          [-84.00849, 32.521769],
          [-84.122347, 32.505921],
          [-84.254613, 32.372053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20183",
      state: "20",
      county: "183",
      name: "Smith County",
      censusArea: 895.465,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.726373, 40.002222],
          [-98.726295, 40.002222],
          [-98.710404, 40.00218],
          [-98.693096, 40.002373],
          [-98.691443, 40.002505],
          [-98.690287, 40.002548],
          [-98.672819, 40.002364],
          [-98.669724, 40.00241],
          [-98.653833, 40.002269],
          [-98.652494, 40.002245],
          [-98.64071, 40.002493],
          [-98.613755, 40.0024],
          [-98.593342, 40.002476],
          [-98.575219, 40.00248],
          [-98.560578, 40.002274],
          [-98.543186, 40.002285],
          [-98.523053, 40.002336],
          [-98.504455, 40.002329],
          [-98.505152, 39.569754],
          [-98.505266, 39.567603],
          [-99.044398, 39.568035],
          [-99.066203, 39.568093],
          [-99.067022, 40.00217],
          [-99.020338, 40.002264],
          [-99.018701, 40.002333],
          [-98.992135, 40.002192],
          [-98.972287, 40.002245],
          [-98.971721, 40.002268],
          [-98.961009, 40.002317],
          [-98.960919, 40.002271],
          [-98.934792, 40.002205],
          [-98.834456, 40.002363],
          [-98.82059, 40.002319],
          [-98.777203, 40.002359],
          [-98.774941, 40.002336],
          [-98.726373, 40.002222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20187",
      state: "20",
      county: "187",
      name: "Stanton County",
      censusArea: 680.345,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.04199, 37.738541],
          [-101.527063, 37.736314],
          [-101.52581, 37.388392],
          [-101.54368, 37.388384],
          [-101.543876, 37.388457],
          [-101.556606, 37.388485],
          [-102.041826, 37.389191],
          [-102.041676, 37.409898],
          [-102.041669, 37.43474],
          [-102.041755, 37.434855],
          [-102.041801, 37.469488],
          [-102.041786, 37.506066],
          [-102.042016, 37.535261],
          [-102.041899, 37.541186],
          [-102.041894, 37.557977],
          [-102.041618, 37.607868],
          [-102.041585, 37.644282],
          [-102.041582, 37.654495],
          [-102.041694, 37.665681],
          [-102.041574, 37.680436],
          [-102.041876, 37.723875],
          [-102.04199, 37.738541]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20189",
      state: "20",
      county: "189",
      name: "Stevens County",
      censusArea: 727.293,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.556606, 37.388485],
          [-101.543876, 37.388457],
          [-101.54368, 37.388384],
          [-101.52581, 37.388392],
          [-101.089653, 37.387721],
          [-101.067904, 37.387779],
          [-101.066457, 36.998493],
          [-101.066742, 36.997921],
          [-101.211486, 36.997124],
          [-101.212909, 36.997044],
          [-101.357797, 36.996271],
          [-101.359674, 36.996232],
          [-101.37818, 36.996164],
          [-101.413868, 36.996008],
          [-101.415005, 36.995966],
          [-101.485326, 36.995611],
          [-101.519066, 36.995546],
          [-101.555239, 36.995414],
          [-101.55526, 36.995414],
          [-101.556606, 37.388485]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20197",
      state: "20",
      county: "197",
      name: "Wabaunsee County",
      censusArea: 794.302,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.352613, 38.739021],
          [-96.390398, 38.825858],
          [-96.501397, 38.826188],
          [-96.501556, 38.869704],
          [-96.501174, 39.043666],
          [-96.390796, 39.043257],
          [-96.388884, 39.172724],
          [-96.331405, 39.158884],
          [-96.233671, 39.212279],
          [-96.083557, 39.193504],
          [-96.03906, 39.126527],
          [-95.946754, 39.098685],
          [-95.946587, 38.869973],
          [-95.945924, 38.739112],
          [-96.352613, 38.739021]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20207",
      state: "20",
      county: "207",
      name: "Woodson County",
      censusArea: 497.82,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.960876, 37.734304],
          [-95.958554, 37.909245],
          [-95.959046, 38.040195],
          [-95.51897, 38.03823],
          [-95.525499, 37.732759],
          [-95.960876, 37.734304]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21191",
      state: "21",
      county: "191",
      name: "Pendleton County",
      censusArea: 277.157,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.232478, 38.874638],
          [-84.233727, 38.853576],
          [-84.231306, 38.830552],
          [-84.229954, 38.82765],
          [-84.205346, 38.582039],
          [-84.476299, 38.542723],
          [-84.532288, 38.791937],
          [-84.420199, 38.805679],
          [-84.418107, 38.806596],
          [-84.232478, 38.874638]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21193",
      state: "21",
      county: "193",
      name: "Perry County",
      censusArea: 339.67,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.12209, 37.000997],
          [-83.124334, 37.005722],
          [-83.136701, 37.00415],
          [-83.144352, 37.013705],
          [-83.160106, 37.013859],
          [-83.160825, 37.020739],
          [-83.171706, 37.017192],
          [-83.182104, 37.021115],
          [-83.201887, 37.17413],
          [-83.32462, 37.225561],
          [-83.351774, 37.295682],
          [-83.460992, 37.293848],
          [-83.507886, 37.235877],
          [-83.511263, 37.255073],
          [-83.526907, 37.258529],
          [-83.547628, 37.334418],
          [-83.449454, 37.378853],
          [-83.38165, 37.338266],
          [-83.214735, 37.443538],
          [-83.124348, 37.404973],
          [-83.127249, 37.268401],
          [-83.000142, 37.19571],
          [-83.058818, 37.124079],
          [-83.054626, 37.02],
          [-83.12209, 37.000997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21201",
      state: "21",
      county: "201",
      name: "Robertson County",
      censusArea: 99.911,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.930348, 38.492278],
          [-83.97001, 38.463997],
          [-83.980031, 38.439159],
          [-84.1021, 38.459379],
          [-84.183903, 38.498019],
          [-84.162031, 38.554125],
          [-83.991605, 38.593701],
          [-83.930348, 38.492278]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21205",
      state: "21",
      county: "205",
      name: "Rowan County",
      censusArea: 279.795,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.433942, 38.033974],
          [-83.498846, 38.05062],
          [-83.635195, 38.187525],
          [-83.50716, 38.269447],
          [-83.452848, 38.381799],
          [-83.341347, 38.319521],
          [-83.306662, 38.225241],
          [-83.244306, 38.19238],
          [-83.263289, 38.115386],
          [-83.433942, 38.033974]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21207",
      state: "21",
      county: "207",
      name: "Russell County",
      censusArea: 253.657,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.043316, 37.186028],
          [-84.954406, 37.102271],
          [-84.901112, 37.116297],
          [-84.905778, 37.047187],
          [-84.835712, 36.997612],
          [-85.064307, 36.858743],
          [-85.118411, 36.827334],
          [-85.215405, 36.854467],
          [-85.27495, 36.897901],
          [-85.232026, 36.925069],
          [-85.205831, 36.996862],
          [-85.043316, 37.186028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21217",
      state: "21",
      county: "217",
      name: "Taylor County",
      censusArea: 266.327,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.46625, 37.465595],
          [-85.340446, 37.469512],
          [-85.246759, 37.420205],
          [-85.17144, 37.465081],
          [-85.073772, 37.413669],
          [-85.165174, 37.310396],
          [-85.314663, 37.24074],
          [-85.352771, 37.192432],
          [-85.478081, 37.363673],
          [-85.584477, 37.432091],
          [-85.583497, 37.469917],
          [-85.46625, 37.465595]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21221",
      state: "21",
      county: "221",
      name: "Trigg County",
      censusArea: 441.433,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.694186, 36.636838],
          [-87.853204, 36.633247],
          [-87.849567, 36.663701],
          [-88.011792, 36.677025],
          [-88.070532, 36.678118],
          [-88.084215, 36.713147],
          [-88.110831, 36.74715],
          [-88.157347, 36.867183],
          [-87.876996, 36.960116],
          [-87.733368, 37.002103],
          [-87.659724, 36.966397],
          [-87.694186, 36.636838]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21231",
      state: "21",
      county: "231",
      name: "Wayne County",
      censusArea: 458.17,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.785341, 36.603372],
          [-84.785399, 36.603374],
          [-84.859759, 36.606428],
          [-84.859738, 36.606495],
          [-84.974888, 36.614327],
          [-85.004099, 36.756237],
          [-85.064307, 36.858743],
          [-84.835712, 36.997612],
          [-84.770172, 36.957735],
          [-84.679165, 36.980914],
          [-84.578158, 36.867133],
          [-84.579339, 36.814397],
          [-84.673964, 36.739542],
          [-84.778455, 36.603222],
          [-84.785341, 36.603372]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21233",
      state: "21",
      county: "233",
      name: "Webster County",
      censusArea: 331.943,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.813413, 37.350645],
          [-87.827977, 37.353711],
          [-87.819963, 37.366034],
          [-87.811461, 37.364087],
          [-87.801046, 37.379445],
          [-87.903034, 37.398197],
          [-87.932952, 37.480052],
          [-87.918474, 37.492937],
          [-87.734545, 37.638376],
          [-87.495331, 37.647547],
          [-87.375145, 37.569988],
          [-87.499968, 37.486741],
          [-87.593575, 37.461963],
          [-87.813413, 37.350645]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21237",
      state: "21",
      county: "237",
      name: "Wolfe County",
      censusArea: 222.171,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.707974, 37.716463],
          [-83.63106, 37.825372],
          [-83.49508, 37.861906],
          [-83.262172, 37.712657],
          [-83.243603, 37.699155],
          [-83.258467, 37.679926],
          [-83.248692, 37.669616],
          [-83.418474, 37.690729],
          [-83.522308, 37.638516],
          [-83.707974, 37.716463]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21239",
      state: "21",
      county: "239",
      name: "Woodford County",
      censusArea: 188.784,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.625079, 38.116397],
          [-84.657498, 38.017835],
          [-84.660135, 38.003745],
          [-84.709918, 37.861952],
          [-84.818833, 37.945838],
          [-84.796327, 37.970088],
          [-84.864908, 38.116926],
          [-84.725168, 38.195426],
          [-84.625079, 38.116397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22005",
      state: "22",
      county: "005",
      name: "Ascension County",
      lsad: "Parish",
      censusArea: 289.981,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.105951, 30.062478],
          [-91.059273, 30.213113],
          [-91.021014, 30.321489],
          [-90.891728, 30.345244],
          [-90.774577, 30.249808],
          [-90.689821, 30.176589],
          [-90.632811, 30.221409],
          [-90.632906, 30.219933],
          [-90.641998, 30.16643],
          [-90.915565, 30.113285],
          [-90.963693, 30.06645],
          [-91.105951, 30.062478]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22091",
      state: "22",
      county: "091",
      name: "St. Helena County",
      lsad: "Parish",
      censusArea: 408.403,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.825829, 30.99936],
          [-90.783745, 30.999447],
          [-90.779858, 30.999457],
          [-90.775981, 30.999491],
          [-90.769333, 30.999374],
          [-90.758775, 30.999583],
          [-90.734552, 30.999222],
          [-90.734473, 30.999214],
          [-90.651193, 30.99951],
          [-90.648721, 30.999486],
          [-90.588676, 30.99965],
          [-90.587373, 30.999604],
          [-90.584448, 30.999698],
          [-90.583518, 30.999698],
          [-90.567195, 30.999733],
          [-90.565857, 30.663902],
          [-90.567165, 30.650023],
          [-90.910701, 30.649385],
          [-90.849041, 30.719311],
          [-90.863041, 30.941981],
          [-90.825829, 30.99936]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22093",
      state: "22",
      county: "093",
      name: "St. James County",
      lsad: "Parish",
      censusArea: 241.537,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.963693, 30.06645],
          [-90.915565, 30.113285],
          [-90.641998, 30.16643],
          [-90.68885, 30.035953],
          [-90.656312, 29.889246],
          [-90.785659, 29.921592],
          [-90.885589, 29.905353],
          [-90.959869, 29.977924],
          [-90.963693, 30.06645]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22097",
      state: "22",
      county: "097",
      name: "St. Landry County",
      lsad: "Parish",
      censusArea: 923.879,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.493259, 30.480499],
          [-92.42078, 30.53882],
          [-92.262719, 30.538738],
          [-92.211243, 30.568125],
          [-92.17255, 30.713365],
          [-92.212711, 30.848607],
          [-91.815258, 30.848107],
          [-91.733673, 30.70173],
          [-91.756193, 30.497738],
          [-91.678896, 30.394731],
          [-91.869273, 30.417235],
          [-91.988718, 30.368598],
          [-92.056066, 30.378237],
          [-92.142184, 30.298817],
          [-92.176444, 30.436852],
          [-92.244795, 30.480193],
          [-92.493259, 30.480499]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22099",
      state: "22",
      county: "099",
      name: "St. Martin County",
      lsad: "Parish",
      censusArea: 737.648,
      stateName: "Louisiana"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-91.471951, 29.955989],
            [-91.255229, 29.971457],
            [-91.182817, 29.846172],
            [-91.092748, 29.801574],
            [-91.10001, 29.699402],
            [-91.215195, 29.751211],
            [-91.386491, 29.783681],
            [-91.373642, 29.834401],
            [-91.471951, 29.955989]
          ]
        ],
        [
          [
            [-91.700933, 30.497433],
            [-91.640099, 30.442728],
            [-91.621055, 30.309938],
            [-91.56276, 30.241949],
            [-91.475277, 30.229231],
            [-91.463833, 30.102493],
            [-91.368835, 30.058824],
            [-91.600354, 30.033169],
            [-91.658886, 30.108226],
            [-91.765168, 30.108495],
            [-91.819216, 30.044013],
            [-91.950998, 30.073213],
            [-91.905544, 30.154616],
            [-91.980328, 30.185248],
            [-91.942818, 30.236599],
            [-91.988718, 30.368598],
            [-91.869273, 30.417235],
            [-91.678896, 30.394731],
            [-91.756193, 30.497738],
            [-91.700933, 30.497433]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22103",
      state: "22",
      county: "103",
      name: "St. Tammany County",
      lsad: "Parish",
      censusArea: 845.551,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.69058, 30.458773],
          [-89.68341, 30.451793],
          [-89.629941, 30.339449],
          [-89.629877, 30.321017],
          [-89.640694, 30.301313],
          [-89.643575, 30.293099],
          [-89.64344, 30.287682],
          [-89.607655, 30.217096],
          [-89.580754, 30.186197],
          [-89.572654, 30.180897],
          [-89.524504, 30.180753],
          [-89.531213, 30.177099],
          [-89.537493, 30.171745],
          [-89.555013, 30.170798],
          [-89.562825, 30.168667],
          [-89.56827, 30.163932],
          [-89.572093, 30.160362],
          [-89.587062, 30.150648],
          [-89.595021, 30.149891],
          [-89.598027, 30.152409],
          [-89.617542, 30.156422],
          [-89.62288, 30.152368],
          [-89.749211, 30.176429],
          [-89.794106, 30.151326],
          [-89.878689, 30.199193],
          [-89.99426, 30.150039],
          [-90.105456, 30.191801],
          [-90.243034, 30.224447],
          [-90.255648, 30.711905],
          [-89.994201, 30.66543],
          [-89.842585, 30.666005],
          [-89.823384, 30.608535],
          [-89.806843, 30.572039],
          [-89.76057, 30.515761],
          [-89.732577, 30.492502],
          [-89.724614, 30.491902],
          [-89.69058, 30.458773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22113",
      state: "22",
      county: "113",
      name: "Vermilion County",
      lsad: "Parish",
      censusArea: 1173.199,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.041168, 29.581648],
          [-92.046316, 29.584362],
          [-92.066533, 29.583842],
          [-92.105923, 29.586335],
          [-92.158624, 29.581616],
          [-92.21259, 29.562479],
          [-92.25186, 29.539354],
          [-92.280392, 29.533434],
          [-92.309357, 29.533026],
          [-92.347236, 29.539394],
          [-92.402165, 29.551269],
          [-92.473585, 29.561081],
          [-92.558602, 29.569935],
          [-92.61627, 29.578729],
          [-92.617725, 29.579092],
          [-92.61561, 30.038168],
          [-92.738604, 30.037323],
          [-92.625484, 30.09233],
          [-92.534638, 30.107852],
          [-92.426039, 30.049369],
          [-92.284675, 30.147443],
          [-92.12812, 30.115904],
          [-91.964458, 30.036918],
          [-91.990396, 29.970326],
          [-91.970443, 29.80431],
          [-91.978381, 29.799217],
          [-91.983871, 29.794516],
          [-92.035666, 29.781662],
          [-92.056398, 29.772313],
          [-92.107486, 29.744429],
          [-92.144431, 29.716418],
          [-92.149349, 29.697052],
          [-92.134347, 29.669516],
          [-92.132804, 29.660462],
          [-92.111787, 29.62177],
          [-92.093419, 29.618694],
          [-92.06564, 29.619967],
          [-92.04767, 29.624527],
          [-92.02532, 29.625647],
          [-92.01627, 29.618741],
          [-92.000371, 29.613143],
          [-92.006448, 29.608158],
          [-92.009765, 29.598395],
          [-92.019499, 29.592973],
          [-92.025388, 29.585386],
          [-92.02915, 29.584432],
          [-92.038618, 29.585048],
          [-92.041168, 29.581648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22117",
      state: "22",
      county: "117",
      name: "Washington County",
      lsad: "Parish",
      censusArea: 669.525,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.347241, 31.000359],
          [-90.34723, 31.000359],
          [-90.346007, 31.000363],
          [-90.259555, 31.000657],
          [-90.164676, 31.00098],
          [-90.164278, 31.001025],
          [-90.131395, 31.000924],
          [-90.128406, 31.001047],
          [-90.050706, 31.001215],
          [-90.029574, 31.00119],
          [-90.022185, 31.001302],
          [-90.005332, 31.001364],
          [-89.97543, 31.001692],
          [-89.972802, 31.001392],
          [-89.927161, 31.001437],
          [-89.923119, 31.001446],
          [-89.897516, 31.001913],
          [-89.892708, 31.001759],
          [-89.856862, 31.002075],
          [-89.835908, 31.002059],
          [-89.835542, 31.002059],
          [-89.824617, 31.00206],
          [-89.816429, 31.002084],
          [-89.752642, 31.001853],
          [-89.732504, 31.004831],
          [-89.728147, 31.002431],
          [-89.728148, 31.0023],
          [-89.728382, 30.971141],
          [-89.744789, 30.918933],
          [-89.756543, 30.900431],
          [-89.770931, 30.899432],
          [-89.77341, 30.897693],
          [-89.789863, 30.851527],
          [-89.826175, 30.736594],
          [-89.842596, 30.666038],
          [-89.842585, 30.666005],
          [-89.994201, 30.66543],
          [-90.255648, 30.711905],
          [-90.347241, 31.000359]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21011",
      state: "21",
      county: "011",
      name: "Bath County",
      censusArea: 278.792,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.709507, 38.009317],
          [-83.712187, 38.009693],
          [-83.715208, 38.004399],
          [-83.718497, 38.003397],
          [-83.722177, 38.006011],
          [-83.727146, 38.006999],
          [-83.727154, 38.008629],
          [-83.730982, 38.010524],
          [-83.733293, 38.007642],
          [-83.735911, 38.007057],
          [-83.737673, 38.009972],
          [-83.740512, 38.007149],
          [-83.744082, 38.006919],
          [-83.747451, 38.003967],
          [-83.744956, 38.001578],
          [-83.749591, 37.996733],
          [-83.751818, 37.997098],
          [-83.756983, 37.992618],
          [-83.75943, 37.998731],
          [-83.881353, 38.136517],
          [-83.977924, 38.192118],
          [-83.850355, 38.296258],
          [-83.848355, 38.296949],
          [-83.725393, 38.292584],
          [-83.646546, 38.170963],
          [-83.638854, 38.17827],
          [-83.635195, 38.187525],
          [-83.498846, 38.05062],
          [-83.58053, 38.011702],
          [-83.709507, 38.009317]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21013",
      state: "21",
      county: "013",
      name: "Bell County",
      censusArea: 359,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.461013, 36.664916],
          [-83.498011, 36.670485],
          [-83.527212, 36.665984],
          [-83.649513, 36.616683],
          [-83.675413, 36.600814],
          [-83.677114, 36.596582],
          [-83.690714, 36.582581],
          [-83.930669, 36.588249],
          [-83.957274, 36.642204],
          [-83.877503, 36.687259],
          [-83.798248, 36.785197],
          [-83.581094, 36.904667],
          [-83.589521, 36.955786],
          [-83.530847, 36.943038],
          [-83.520687, 36.93755],
          [-83.509083, 36.938509],
          [-83.509482, 36.923375],
          [-83.494254, 36.919621],
          [-83.489423, 36.911302],
          [-83.50481, 36.903917],
          [-83.500388, 36.895504],
          [-83.489813, 36.895413],
          [-83.511729, 36.807362],
          [-83.461013, 36.664916]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21015",
      state: "21",
      county: "015",
      name: "Boone County",
      censusArea: 246.359,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.632446, 39.07676],
          [-84.623732, 39.074427],
          [-84.598031, 38.87954],
          [-84.61574, 38.802192],
          [-84.630007, 38.802901],
          [-84.656167, 38.790117],
          [-84.660107, 38.777293],
          [-84.791078, 38.855859],
          [-84.785799, 38.869496],
          [-84.785111, 38.879988],
          [-84.789521, 38.884687],
          [-84.813182, 38.930583],
          [-84.849445, 39.000923],
          [-84.878614, 39.030296],
          [-84.889065, 39.04082],
          [-84.897171, 39.052407],
          [-84.897364, 39.057378],
          [-84.888873, 39.066376],
          [-84.831197, 39.10192],
          [-84.820157, 39.10548],
          [-84.78768, 39.115297],
          [-84.766749, 39.138558],
          [-84.754449, 39.146658],
          [-84.744149, 39.147458],
          [-84.732048, 39.144458],
          [-84.718548, 39.137059],
          [-84.689747, 39.104159],
          [-84.684847, 39.100459],
          [-84.632446, 39.07676]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21027",
      state: "21",
      county: "027",
      name: "Breckinridge County",
      censusArea: 567.172,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.59281, 37.56436],
          [-86.638935, 37.661877],
          [-86.677871, 37.792845],
          [-86.655285, 37.842506],
          [-86.648028, 37.841425],
          [-86.634271, 37.843845],
          [-86.604624, 37.858272],
          [-86.525174, 37.968228],
          [-86.524969, 38.027879],
          [-86.521825, 38.038327],
          [-86.517289, 38.042634],
          [-86.51176, 38.044448],
          [-86.490858, 38.045796],
          [-86.242777, 37.877002],
          [-86.151285, 37.798878],
          [-86.27527, 37.593519],
          [-86.34623, 37.562718],
          [-86.503369, 37.622191],
          [-86.59281, 37.56436]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21029",
      state: "21",
      county: "029",
      name: "Bullitt County",
      censusArea: 297.024,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.623735, 38.079648],
          [-85.55727, 38.073841],
          [-85.42883, 38.118383],
          [-85.489202, 37.990654],
          [-85.65131, 37.828989],
          [-85.737777, 37.81179],
          [-85.838198, 37.879284],
          [-85.854871, 37.945385],
          [-85.938321, 37.998756],
          [-85.842259, 38.051485],
          [-85.712578, 38.087311],
          [-85.703163, 38.086499],
          [-85.623735, 38.079648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17133",
      state: "17",
      county: "133",
      name: "Monroe County",
      censusArea: 385.011,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.203847, 38.088753],
          [-90.243116, 38.112669],
          [-90.254112, 38.12199],
          [-90.334258, 38.189932],
          [-90.353902, 38.213855],
          [-90.363926, 38.236355],
          [-90.373929, 38.281853],
          [-90.372519, 38.323354],
          [-90.370819, 38.333554],
          [-90.350918, 38.375053],
          [-90.346118, 38.381853],
          [-90.340297, 38.386998],
          [-90.295316, 38.426753],
          [-90.285215, 38.443453],
          [-90.279215, 38.472453],
          [-90.263064, 38.52022],
          [-90.036018, 38.30885],
          [-89.913226, 38.307829],
          [-89.899061, 38.220755],
          [-90.036385, 38.223125],
          [-90.035999, 38.13563],
          [-90.203847, 38.088753]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17137",
      state: "17",
      county: "137",
      name: "Morgan County",
      censusArea: 568.791,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.571754, 39.839326],
          [-90.582435, 39.854574],
          [-90.583534, 39.87675],
          [-89.994405, 39.87286],
          [-89.984745, 39.718079],
          [-89.926037, 39.522104],
          [-90.15375, 39.520315],
          [-90.301801, 39.520192],
          [-90.300666, 39.636541],
          [-90.372969, 39.665781],
          [-90.371253, 39.753388],
          [-90.599105, 39.79006],
          [-90.566976, 39.826883],
          [-90.571754, 39.839326]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17141",
      state: "17",
      county: "141",
      name: "Ogle County",
      censusArea: 758.574,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.939732, 42.15232],
          [-88.941279, 41.891752],
          [-89.360682, 41.88807],
          [-89.62933, 41.901617],
          [-89.628907, 41.930044],
          [-89.685366, 41.93034],
          [-89.686549, 41.937493],
          [-89.688486, 42.199112],
          [-89.396192, 42.201916],
          [-89.173, 42.204241],
          [-89.172799, 42.150297],
          [-88.939732, 42.15232]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17143",
      state: "17",
      county: "143",
      name: "Peoria County",
      censusArea: 619.209,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.986068, 40.712257],
          [-89.985333, 40.800032],
          [-89.985226, 40.848699],
          [-89.985376, 40.850576],
          [-89.985429, 40.887645],
          [-89.985479, 40.974495],
          [-89.982549, 40.974707],
          [-89.638727, 40.973707],
          [-89.447693, 40.973246],
          [-89.47233, 40.921206],
          [-89.552766, 40.810469],
          [-89.554994, 40.747637],
          [-89.658003, 40.567438],
          [-89.872463, 40.513127],
          [-89.873506, 40.624588],
          [-89.989018, 40.625835],
          [-89.986068, 40.712257]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17147",
      state: "17",
      county: "147",
      name: "Piatt County",
      censusArea: 439.202,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.462207, 39.879092],
          [-88.462328, 39.79182],
          [-88.473182, 39.791837],
          [-88.745671, 39.792146],
          [-88.745957, 39.858136],
          [-88.745164, 40.055191],
          [-88.574885, 40.281501],
          [-88.4809, 40.281216],
          [-88.460418, 40.281935],
          [-88.462207, 39.879092]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17153",
      state: "17",
      county: "153",
      name: "Pulaski County",
      censusArea: 199.185,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.928021, 37.226281],
          [-88.942111, 37.228811],
          [-88.933385, 37.225005],
          [-88.933399, 37.225007],
          [-88.966831, 37.229891],
          [-88.98326, 37.228685],
          [-89.000968, 37.224401],
          [-89.041263, 37.202881],
          [-89.086526, 37.165602],
          [-89.168087, 37.074218],
          [-89.171881, 37.068184],
          [-89.273631, 37.126968],
          [-89.248439, 37.335109],
          [-89.099448, 37.333559],
          [-89.044787, 37.329846],
          [-88.929129, 37.302647],
          [-88.928021, 37.226281]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17159",
      state: "17",
      county: "159",
      name: "Richland County",
      censusArea: 359.99,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.953898, 38.570094],
          [-88.147814, 38.569086],
          [-88.148044, 38.597988],
          [-88.253851, 38.599502],
          [-88.258608, 38.847521],
          [-87.945923, 38.850108],
          [-87.908113, 38.850107],
          [-87.912286, 38.570104],
          [-87.912351, 38.569909],
          [-87.953014, 38.57014],
          [-87.953898, 38.570094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17165",
      state: "17",
      county: "165",
      name: "Saline County",
      censusArea: 379.819,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.706622, 37.906797],
          [-88.485265, 37.907032],
          [-88.484314, 37.906945],
          [-88.375019, 37.90775],
          [-88.37453, 37.907678],
          [-88.374406, 37.875066],
          [-88.37453, 37.874824],
          [-88.374593, 37.871139],
          [-88.374398, 37.870982],
          [-88.374395, 37.863935],
          [-88.373884, 37.776237],
          [-88.375332, 37.599563],
          [-88.412112, 37.599912],
          [-88.708546, 37.599277],
          [-88.70676, 37.863338],
          [-88.706688, 37.886951],
          [-88.706747, 37.887706],
          [-88.706665, 37.888866],
          [-88.706622, 37.906797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17167",
      state: "17",
      county: "167",
      name: "Sangamon County",
      censusArea: 868.302,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.701645, 39.523369],
          [-89.795316, 39.522939],
          [-89.800366, 39.523025],
          [-89.804458, 39.522876],
          [-89.813147, 39.522838],
          [-89.926037, 39.522104],
          [-89.984745, 39.718079],
          [-89.994405, 39.87286],
          [-89.994506, 39.901925],
          [-89.701864, 39.916787],
          [-89.698259, 39.975309],
          [-89.578289, 39.976127],
          [-89.483419, 39.93314],
          [-89.217846, 39.91699],
          [-89.21781, 39.873818],
          [-89.217546, 39.872392],
          [-89.21747, 39.85296],
          [-89.217523, 39.813224],
          [-89.233523, 39.813159],
          [-89.240092, 39.823137],
          [-89.248609, 39.825723],
          [-89.252325, 39.820416],
          [-89.392858, 39.74385],
          [-89.425051, 39.683696],
          [-89.535028, 39.641138],
          [-89.533655, 39.524592],
          [-89.701645, 39.523369]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17177",
      state: "17",
      county: "177",
      name: "Stephenson County",
      censusArea: 564.523,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.837587, 42.505543],
          [-89.801897, 42.505444],
          [-89.799704, 42.505421],
          [-89.793957, 42.505466],
          [-89.780302, 42.505349],
          [-89.769643, 42.505322],
          [-89.742395, 42.505382],
          [-89.693487, 42.505099],
          [-89.690088, 42.505191],
          [-89.667596, 42.50496],
          [-89.650324, 42.504613],
          [-89.644176, 42.50452],
          [-89.61341, 42.503942],
          [-89.603523, 42.503557],
          [-89.600001, 42.503672],
          [-89.594779, 42.503468],
          [-89.564407, 42.502628],
          [-89.522542, 42.501889],
          [-89.493216, 42.501514],
          [-89.492612, 42.501514],
          [-89.4843, 42.501426],
          [-89.425162, 42.500726],
          [-89.423926, 42.500818],
          [-89.422567, 42.50068],
          [-89.420991, 42.500589],
          [-89.401432, 42.500433],
          [-89.401416, 42.500433],
          [-89.396192, 42.201916],
          [-89.688486, 42.199112],
          [-89.919657, 42.196305],
          [-89.926224, 42.505788],
          [-89.837587, 42.505543]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17183",
      state: "17",
      county: "183",
      name: "Vermilion County",
      censusArea: 898.368,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.526379, 40.491237],
          [-87.526502, 40.477158],
          [-87.526511, 40.476879],
          [-87.526549, 40.475659],
          [-87.526809, 40.46217],
          [-87.530054, 40.250671],
          [-87.529992, 40.250036],
          [-87.530828, 40.191812],
          [-87.531133, 40.17003],
          [-87.531438, 40.148123],
          [-87.531439, 40.148027],
          [-87.531759, 40.144273],
          [-87.531561, 40.133005],
          [-87.532308, 40.011587],
          [-87.532308, 40.011492],
          [-87.532287, 40.000037],
          [-87.532331, 39.997776],
          [-87.532542, 39.987462],
          [-87.532683, 39.977691],
          [-87.53279, 39.97501],
          [-87.532776, 39.971077],
          [-87.533227, 39.883127],
          [-87.533227, 39.883],
          [-87.937645, 39.879803],
          [-87.932858, 40.399401],
          [-87.931758, 40.400187],
          [-87.935309, 40.485923],
          [-87.526379, 40.491237]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17191",
      state: "17",
      county: "191",
      name: "Wayne County",
      censusArea: 713.814,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.702391, 38.256661],
          [-88.698961, 38.474914],
          [-88.698457, 38.606304],
          [-88.564109, 38.607093],
          [-88.253851, 38.599502],
          [-88.148044, 38.597988],
          [-88.147814, 38.569086],
          [-88.1509, 38.256097],
          [-88.370491, 38.255413],
          [-88.702391, 38.256661]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17195",
      state: "17",
      county: "195",
      name: "Whiteside County",
      censusArea: 684.25,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.20844, 41.797176],
          [-90.187969, 41.803163],
          [-90.181973, 41.80707],
          [-90.152104, 41.928947],
          [-89.685366, 41.93034],
          [-89.628907, 41.930044],
          [-89.62933, 41.901617],
          [-89.631368, 41.771297],
          [-89.631494, 41.584949],
          [-89.862351, 41.584005],
          [-90.185609, 41.584653],
          [-90.181412, 41.590993],
          [-90.170427, 41.591761],
          [-90.164842, 41.597322],
          [-90.165057, 41.602933],
          [-90.229749, 41.675375],
          [-90.242747, 41.783767],
          [-90.222263, 41.793133],
          [-90.20844, 41.797176]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17197",
      state: "17",
      county: "197",
      name: "Will County",
      censusArea: 836.908,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.525669, 41.470283],
          [-87.525671, 41.470115],
          [-87.525623, 41.453619],
          [-87.526768, 41.298177],
          [-87.526768, 41.298052],
          [-88.013919, 41.292447],
          [-88.011812, 41.205604],
          [-88.244155, 41.201546],
          [-88.252098, 41.463096],
          [-88.257274, 41.593469],
          [-88.261468, 41.724529],
          [-88.030561, 41.72893],
          [-88.02916, 41.68553],
          [-88.027688, 41.641506],
          [-87.91193, 41.643933],
          [-87.909453, 41.556732],
          [-87.792793, 41.55854],
          [-87.792292, 41.538662],
          [-87.79056, 41.539916],
          [-87.790408, 41.528134],
          [-87.790303, 41.469846],
          [-87.717554, 41.469733],
          [-87.712752, 41.46962],
          [-87.653236, 41.469754],
          [-87.525669, 41.470283]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18001",
      state: "18",
      county: "001",
      name: "Adams County",
      censusArea: 339.028,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.802936, 40.922568],
          [-84.802935, 40.922377],
          [-84.802538, 40.765515],
          [-84.802266, 40.742298],
          [-84.802119, 40.728163],
          [-84.802119, 40.728146],
          [-84.802181, 40.718602],
          [-84.802094, 40.702476],
          [-84.802127, 40.691405],
          [-84.802157, 40.689324],
          [-84.80222, 40.674776],
          [-84.802193, 40.660298],
          [-84.802135, 40.644859],
          [-84.802265, 40.572215],
          [-84.802265, 40.572212],
          [-85.068479, 40.56824],
          [-85.073861, 40.917823],
          [-84.802936, 40.922568]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13275",
      state: "13",
      county: "275",
      name: "Thomas County",
      censusArea: 544.596,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.083753, 30.675954],
          [-84.076964, 30.91557],
          [-84.119058, 30.980956],
          [-84.116644, 31.077971],
          [-84.004502, 31.077192],
          [-84.003627, 31.07729],
          [-84.003876, 31.041582],
          [-83.736158, 31.037679],
          [-83.743729, 30.658396],
          [-83.810536, 30.66188],
          [-83.820886, 30.662612],
          [-83.855216, 30.664412],
          [-83.88022, 30.665832],
          [-83.880317, 30.665807],
          [-84.007391, 30.672097],
          [-84.007454, 30.6721],
          [-84.039707, 30.673819],
          [-84.04181, 30.673878],
          [-84.046605, 30.6742],
          [-84.057228, 30.674705],
          [-84.083753, 30.675954]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13279",
      state: "13",
      county: "279",
      name: "Toombs County",
      censusArea: 364.005,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.431531, 31.966182],
          [-82.439351, 31.96779],
          [-82.444451, 31.96749],
          [-82.452051, 31.96459],
          [-82.458152, 31.96389],
          [-82.479607, 31.971653],
          [-82.483131, 31.968961],
          [-82.40913, 32.353738],
          [-82.409127, 32.353772],
          [-82.409118, 32.35381],
          [-82.409118, 32.35383],
          [-82.354546, 32.292194],
          [-82.232788, 32.318838],
          [-82.182017, 32.165593],
          [-82.225042, 31.913074],
          [-82.431531, 31.966182]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13281",
      state: "13",
      county: "281",
      name: "Towns County",
      censusArea: 166.563,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.780401, 34.792893],
          [-83.819099, 34.91369],
          [-83.915104, 34.926887],
          [-83.936413, 34.987485],
          [-83.673621, 34.987111],
          [-83.620185, 34.992091],
          [-83.549381, 34.992492],
          [-83.656792, 34.878492],
          [-83.652194, 34.822993],
          [-83.657921, 34.813682],
          [-83.666855, 34.806528],
          [-83.681596, 34.800593],
          [-83.685685, 34.800997],
          [-83.693155, 34.797322],
          [-83.69599, 34.79352],
          [-83.701813, 34.792488],
          [-83.704146, 34.790982],
          [-83.707757, 34.79171],
          [-83.711803, 34.79052],
          [-83.714186, 34.793112],
          [-83.721035, 34.79611],
          [-83.72203, 34.797271],
          [-83.726874, 34.797732],
          [-83.735981, 34.800071],
          [-83.743107, 34.801191],
          [-83.780401, 34.792893]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13289",
      state: "13",
      county: "289",
      name: "Twiggs County",
      censusArea: 358.398,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.40699, 32.89822],
          [-83.226536, 32.584201],
          [-83.497923, 32.452198],
          [-83.566198, 32.660786],
          [-83.597656, 32.664338],
          [-83.502603, 32.801119],
          [-83.513511, 32.844868],
          [-83.407668, 32.897822],
          [-83.40699, 32.89822]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13295",
      state: "13",
      county: "295",
      name: "Walker County",
      censusArea: 446.379,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.069159, 34.587184],
          [-85.107748, 34.587483],
          [-85.527127, 34.588684],
          [-85.534423, 34.623789],
          [-85.450409, 34.759241],
          [-85.450561, 34.831677],
          [-85.363919, 34.983375],
          [-85.308257, 34.984375],
          [-85.305457, 34.984475],
          [-85.301488, 34.984475],
          [-85.2945, 34.984651],
          [-85.277556, 34.984975],
          [-85.275856, 34.984975],
          [-85.265055, 34.985075],
          [-85.264762, 34.854277],
          [-85.20218, 34.854451],
          [-85.201856, 34.77508],
          [-85.144789, 34.767639],
          [-85.168851, 34.720279],
          [-85.050847, 34.71998],
          [-85.050446, 34.622482],
          [-85.055095, 34.62239],
          [-85.055206, 34.622383],
          [-85.06038, 34.62245],
          [-85.060499, 34.587184],
          [-85.069159, 34.587184]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13301",
      state: "13",
      county: "301",
      name: "Warren County",
      censusArea: 284.298,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.383829, 33.312106],
          [-82.431957, 33.27483],
          [-82.559753, 33.32727],
          [-82.755846, 33.25344],
          [-82.851954, 33.443543],
          [-82.815315, 33.521137],
          [-82.751275, 33.510143],
          [-82.679975, 33.599792],
          [-82.670284, 33.604138],
          [-82.65912, 33.603048],
          [-82.656606, 33.596812],
          [-82.652208, 33.596718],
          [-82.655937, 33.60661],
          [-82.652439, 33.611202],
          [-82.649705, 33.608768],
          [-82.549373, 33.357748],
          [-82.383829, 33.312106]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20035",
      state: "20",
      county: "035",
      name: "Cowley County",
      censusArea: 1125.75,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.525493, 36.999147],
          [-96.705431, 36.999203],
          [-96.710482, 36.999271],
          [-96.73659, 36.999286],
          [-96.74127, 36.999239],
          [-96.749838, 36.998988],
          [-96.79206, 36.99918],
          [-96.795199, 36.99886],
          [-96.822791, 36.999182],
          [-96.867517, 36.999217],
          [-96.87629, 36.999233],
          [-96.902083, 36.999155],
          [-96.90351, 36.999132],
          [-96.917093, 36.999182],
          [-96.921915, 36.999151],
          [-96.934642, 36.99907],
          [-96.967371, 36.999067],
          [-96.975562, 36.999019],
          [-97.030082, 36.998929],
          [-97.039784, 36.999],
          [-97.100652, 36.998998],
          [-97.104276, 36.99902],
          [-97.120285, 36.999014],
          [-97.122597, 36.999036],
          [-97.147721, 36.999111],
          [-97.153331, 37.475544],
          [-96.52569, 37.476405],
          [-96.524873, 37.30273],
          [-96.525493, 36.999147]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20041",
      state: "20",
      county: "041",
      name: "Dickinson County",
      censusArea: 847.072,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.963177, 39.1323],
          [-96.96347, 38.964901],
          [-96.926209, 38.978941],
          [-96.890245, 38.870067],
          [-96.927234, 38.81216],
          [-96.930286, 38.609362],
          [-97.364088, 38.609146],
          [-97.371911, 38.609353],
          [-97.371614, 38.957852],
          [-97.371154, 39.131961],
          [-96.963177, 39.1323]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20047",
      state: "20",
      county: "047",
      name: "Edwards County",
      censusArea: 621.894,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.022136, 38.000236],
          [-99.022627, 37.825358],
          [-99.013318, 37.825336],
          [-99.01355, 37.73297],
          [-99.557681, 37.734925],
          [-99.55958, 37.734888],
          [-99.569879, 37.913016],
          [-99.569533, 38.087372],
          [-99.350359, 38.087594],
          [-99.350289, 38.000745],
          [-99.022136, 38.000236]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20051",
      state: "20",
      county: "051",
      name: "Ellis County",
      censusArea: 899.912,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.598323, 38.696514],
          [-99.591776, 39.132357],
          [-99.047687, 39.133014],
          [-99.0375, 39.133121],
          [-99.042626, 38.696807],
          [-99.585087, 38.696537],
          [-99.598323, 38.696514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20053",
      state: "20",
      county: "053",
      name: "Ellsworth County",
      censusArea: 715.847,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.480377, 38.521841],
          [-98.479958, 38.696916],
          [-98.486108, 38.696878],
          [-98.484861, 38.870791],
          [-97.928595, 38.871102],
          [-97.924795, 38.60988],
          [-97.924269, 38.522755],
          [-98.480377, 38.521841]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20057",
      state: "20",
      county: "057",
      name: "Ford County",
      censusArea: 1098.271,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.216229, 37.47519],
          [-100.214637, 37.826808],
          [-100.22709, 37.914018],
          [-99.569879, 37.913016],
          [-99.55958, 37.734888],
          [-99.556119, 37.467722],
          [-99.774512, 37.467404],
          [-100.107248, 37.474836],
          [-100.155812, 37.475016],
          [-100.15822, 37.474854],
          [-100.161075, 37.475021],
          [-100.216229, 37.47519]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20063",
      state: "20",
      county: "063",
      name: "Gove County",
      censusArea: 1071.665,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.1642, 39.132071],
          [-100.162393, 39.131926],
          [-100.14794, 39.132179],
          [-100.153823, 38.697341],
          [-100.2472, 38.698165],
          [-100.688006, 38.700021],
          [-100.818698, 38.699861],
          [-100.811858, 39.13336],
          [-100.775311, 39.133375],
          [-100.775177, 39.133265],
          [-100.721296, 39.133381],
          [-100.1642, 39.132071]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20073",
      state: "20",
      county: "073",
      name: "Greenwood County",
      censusArea: 1143.3,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.523755, 37.955899],
          [-96.522782, 38.08637],
          [-96.358099, 38.085817],
          [-96.357277, 38.17266],
          [-95.958862, 38.170939],
          [-95.959046, 38.040195],
          [-95.958554, 37.909245],
          [-95.960876, 37.734304],
          [-95.961002, 37.603761],
          [-96.5253, 37.607015],
          [-96.523755, 37.955899]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20077",
      state: "20",
      county: "077",
      name: "Harper County",
      censusArea: 801.269,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.802313, 36.998713],
          [-98.033955, 36.998366],
          [-98.03989, 36.998349],
          [-98.045342, 36.998327],
          [-98.111985, 36.998133],
          [-98.147452, 36.998162],
          [-98.177596, 36.998009],
          [-98.208218, 36.997997],
          [-98.219499, 36.997824],
          [-98.237712, 36.997972],
          [-98.346188, 36.997962],
          [-98.347149, 36.997962],
          [-98.349804, 37.384056],
          [-97.807057, 37.386293],
          [-97.804566, 37.386298],
          [-97.802313, 36.998713]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20079",
      state: "20",
      county: "079",
      name: "Harvey County",
      censusArea: 539.754,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.701969, 37.911325],
          [-97.701841, 38.173814],
          [-97.37175, 38.173673],
          [-97.153093, 38.174634],
          [-97.152913, 38.087704],
          [-97.152476, 37.912733],
          [-97.701969, 37.911325]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20087",
      state: "20",
      county: "087",
      name: "Jefferson County",
      censusArea: 532.574,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.285425, 39.032162],
          [-95.500254, 39.056656],
          [-95.596759, 39.064672],
          [-95.589472, 39.216081],
          [-95.589295, 39.419086],
          [-95.570351, 39.41905],
          [-95.180891, 39.419218],
          [-95.187103, 39.044109],
          [-95.285425, 39.032162]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22011",
      state: "22",
      county: "011",
      name: "Beauregard County",
      lsad: "Parish",
      censusArea: 1157.342,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.741701, 30.403007],
          [-93.702665, 30.429947],
          [-93.698302, 30.438657],
          [-93.697828, 30.443838],
          [-93.710117, 30.5064],
          [-93.740045, 30.538765],
          [-93.727844, 30.57407],
          [-93.578395, 30.802047],
          [-93.563292, 30.818503],
          [-93.558672, 30.868829],
          [-93.558617, 30.869424],
          [-93.55497, 30.876685],
          [-93.554576, 30.87747],
          [-92.978453, 30.878175],
          [-92.976886, 30.77404],
          [-92.97917, 30.59834],
          [-93.130294, 30.597886],
          [-93.131155, 30.423612],
          [-93.131109, 30.403057],
          [-93.38515, 30.40263],
          [-93.384673, 30.490109],
          [-93.486384, 30.490523],
          [-93.486116, 30.4025],
          [-93.741701, 30.403007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22015",
      state: "22",
      county: "015",
      name: "Bossier County",
      lsad: "Parish",
      censusArea: 840.057,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.42819, 32.235897],
          [-93.457933, 32.23597],
          [-93.463338, 32.237159],
          [-93.471249, 32.237186],
          [-93.659041, 32.406058],
          [-93.685569, 32.395498],
          [-93.661396, 32.427624],
          [-93.699506, 32.49748],
          [-93.767444, 32.538401],
          [-93.739474, 32.590773],
          [-93.782111, 32.712212],
          [-93.819169, 32.736002],
          [-93.783233, 32.78436],
          [-93.824253, 32.792451],
          [-93.785181, 32.857353],
          [-93.842597, 32.946764],
          [-93.814553, 33.019372],
          [-93.80493, 33.019347],
          [-93.531499, 33.018643],
          [-93.524916, 33.018637],
          [-93.520994, 33.018616],
          [-93.530405, 32.975397],
          [-93.448039, 32.866321],
          [-93.441212, 32.410431],
          [-93.372781, 32.410153],
          [-93.42819, 32.235897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22017",
      state: "22",
      county: "017",
      name: "Caddo County",
      lsad: "Parish",
      censusArea: 878.539,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.043147, 32.69303],
          [-94.043147, 32.693031],
          [-94.042947, 32.767991],
          [-94.043027, 32.776863],
          [-94.042938, 32.780558],
          [-94.042829, 32.785277],
          [-94.042747, 32.786973],
          [-94.043026, 32.797476],
          [-94.042785, 32.871486],
          [-94.043025, 32.880446],
          [-94.042886, 32.880965],
          [-94.042886, 32.881089],
          [-94.042859, 32.892771],
          [-94.042885, 32.898911],
          [-94.043092, 32.910021],
          [-94.043067, 32.937903],
          [-94.043088, 32.955592],
          [-94.042964, 33.019219],
          [-94.041444, 33.019188],
          [-94.035839, 33.019145],
          [-94.027983, 33.019139],
          [-94.024475, 33.019207],
          [-93.814553, 33.019372],
          [-93.842597, 32.946764],
          [-93.785181, 32.857353],
          [-93.824253, 32.792451],
          [-93.783233, 32.78436],
          [-93.819169, 32.736002],
          [-93.782111, 32.712212],
          [-93.739474, 32.590773],
          [-93.767444, 32.538401],
          [-93.699506, 32.49748],
          [-93.661396, 32.427624],
          [-93.685569, 32.395498],
          [-93.659041, 32.406058],
          [-93.471249, 32.237186],
          [-93.61469, 32.237526],
          [-93.666472, 32.317444],
          [-93.791282, 32.340224],
          [-93.951085, 32.195545],
          [-94.042621, 32.196005],
          [-94.042662, 32.218146],
          [-94.042732, 32.26962],
          [-94.042733, 32.269696],
          [-94.042739, 32.363559],
          [-94.042763, 32.373332],
          [-94.042901, 32.392283],
          [-94.042923, 32.399918],
          [-94.042899, 32.400659],
          [-94.042986, 32.435507],
          [-94.042908, 32.439891],
          [-94.042903, 32.470386],
          [-94.042875, 32.471348],
          [-94.042902, 32.472906],
          [-94.042995, 32.478004],
          [-94.042955, 32.480261],
          [-94.043072, 32.4843],
          [-94.043089, 32.486561],
          [-94.042911, 32.492852],
          [-94.042885, 32.505145],
          [-94.043081, 32.513613],
          [-94.043142, 32.559502],
          [-94.043083, 32.564261],
          [-94.042919, 32.610142],
          [-94.042929, 32.61826],
          [-94.042926, 32.622015],
          [-94.042824, 32.640305],
          [-94.04278, 32.643466],
          [-94.042913, 32.655127],
          [-94.043147, 32.69303]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22023",
      state: "22",
      county: "023",
      name: "Cameron County",
      lsad: "Parish",
      censusArea: 1284.887,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.840799, 29.914423],
          [-93.789431, 29.987812],
          [-93.75663, 30.014163],
          [-93.752038, 30.016403],
          [-93.722481, 30.050898],
          [-93.128475, 30.052587],
          [-92.996371, 30.038479],
          [-92.756546, 30.039326],
          [-92.738604, 30.037323],
          [-92.61561, 30.038168],
          [-92.617725, 29.579092],
          [-92.653651, 29.588065],
          [-92.744126, 29.617608],
          [-92.871232, 29.670028],
          [-92.940455, 29.701033],
          [-92.974305, 29.71398],
          [-93.065354, 29.740966],
          [-93.088182, 29.749125],
          [-93.17693, 29.770487],
          [-93.226934, 29.777519],
          [-93.267456, 29.778113],
          [-93.295573, 29.775071],
          [-93.342104, 29.763402],
          [-93.344993, 29.759618],
          [-93.347331, 29.759046],
          [-93.372029, 29.763049],
          [-93.438973, 29.768949],
          [-93.475252, 29.769242],
          [-93.538462, 29.763299],
          [-93.590786, 29.755649],
          [-93.635304, 29.752806],
          [-93.68364, 29.747153],
          [-93.741948, 29.736343],
          [-93.766048, 29.7295],
          [-93.79925, 29.71526],
          [-93.818995, 29.704076],
          [-93.837971, 29.690619],
          [-93.892246, 29.765241],
          [-93.89847, 29.771577],
          [-93.890679, 29.843159],
          [-93.840799, 29.914423]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22031",
      state: "22",
      county: "031",
      name: "De Soto County",
      lsad: "Parish",
      censusArea: 875.579,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.01563, 31.979856],
          [-94.04272, 31.999265],
          [-94.0427, 32.056012],
          [-94.042337, 32.119914],
          [-94.042681, 32.137956],
          [-94.042591, 32.158097],
          [-94.042539, 32.166826],
          [-94.042566, 32.166894],
          [-94.042621, 32.196005],
          [-93.951085, 32.195545],
          [-93.791282, 32.340224],
          [-93.666472, 32.317444],
          [-93.61469, 32.237526],
          [-93.535963, 32.193126],
          [-93.541353, 32.10588],
          [-93.441506, 31.98918],
          [-93.355452, 31.932222],
          [-93.44057, 31.91373],
          [-93.441171, 31.845233],
          [-93.878225, 31.844276],
          [-93.889193, 31.856819],
          [-93.92029, 31.888651],
          [-93.932135, 31.893672],
          [-93.975377, 31.92366],
          [-94.01563, 31.979856]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22035",
      state: "22",
      county: "035",
      name: "East Carroll County",
      lsad: "Parish",
      censusArea: 420.704,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.090771, 32.563978],
          [-91.090611, 32.562705],
          [-91.133713, 32.537912],
          [-91.457643, 32.537807],
          [-91.430989, 32.561186],
          [-91.445299, 32.581956],
          [-91.344845, 32.756659],
          [-91.30938, 32.935237],
          [-91.265018, 33.005084],
          [-91.166073, 33.004106],
          [-91.201842, 32.961212],
          [-91.214027, 32.93032],
          [-91.212837, 32.922104],
          [-91.208263, 32.915354],
          [-91.196785, 32.906784],
          [-91.175405, 32.899998],
          [-91.159975, 32.899879],
          [-91.145076, 32.905494],
          [-91.134041, 32.917676],
          [-91.132115, 32.923122],
          [-91.130947, 32.963815],
          [-91.138585, 32.971352],
          [-91.134414, 32.980533],
          [-91.125107, 32.984669],
          [-91.106581, 32.988938],
          [-91.094265, 32.984371],
          [-91.086802, 32.976266],
          [-91.063974, 32.924113],
          [-91.063809, 32.903709],
          [-91.070602, 32.888659],
          [-91.086683, 32.873392],
          [-91.105631, 32.858396],
          [-91.116091, 32.855641],
          [-91.127886, 32.855059],
          [-91.145002, 32.84287],
          [-91.158336, 32.822304],
          [-91.161669, 32.812465],
          [-91.164397, 32.785821],
          [-91.165328, 32.751301],
          [-91.163389, 32.747009],
          [-91.154461, 32.742339],
          [-91.123152, 32.742798],
          [-91.077176, 32.732534],
          [-91.060766, 32.727494],
          [-91.054481, 32.722259],
          [-91.057043, 32.712576],
          [-91.063946, 32.702926],
          [-91.076061, 32.693751],
          [-91.098762, 32.685291],
          [-91.118258, 32.674075],
          [-91.152081, 32.641508],
          [-91.153821, 32.631282],
          [-91.151318, 32.615919],
          [-91.146204, 32.604144],
          [-91.141148, 32.597209],
          [-91.127912, 32.586493],
          [-91.119854, 32.584795],
          [-91.104111, 32.584657],
          [-91.096774, 32.576652],
          [-91.090771, 32.563978]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22041",
      state: "22",
      county: "041",
      name: "Franklin County",
      lsad: "Parish",
      censusArea: 624.585,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.575721, 31.88256],
          [-91.596922, 31.969758],
          [-91.6475, 31.970428],
          [-91.729158, 31.88251],
          [-91.804869, 31.893193],
          [-91.889687, 31.971474],
          [-91.894729, 32.15155],
          [-91.775019, 32.243333],
          [-91.764379, 32.330018],
          [-91.634931, 32.405371],
          [-91.476739, 32.405808],
          [-91.526719, 32.300675],
          [-91.492718, 32.203349],
          [-91.489761, 32.092422],
          [-91.575721, 31.88256]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22043",
      state: "22",
      county: "043",
      name: "Grant County",
      lsad: "Parish",
      censusArea: 643.026,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.973529, 31.708922],
          [-92.619976, 31.709572],
          [-92.619224, 31.797149],
          [-92.362608, 31.796633],
          [-92.347739, 31.696584],
          [-92.384441, 31.620437],
          [-92.306855, 31.58952],
          [-92.266202, 31.492791],
          [-92.196131, 31.477877],
          [-92.631944, 31.390478],
          [-92.694666, 31.40724],
          [-92.718983, 31.517594],
          [-92.806206, 31.598548],
          [-92.908313, 31.625169],
          [-92.973529, 31.708922]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22049",
      state: "22",
      county: "049",
      name: "Jackson County",
      lsad: "Parish",
      censusArea: 569.183,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.311851, 32.277441],
          [-92.312218, 32.146123],
          [-92.814737, 32.146907],
          [-92.774936, 32.237055],
          [-92.777197, 32.453455],
          [-92.621678, 32.452999],
          [-92.622335, 32.496406],
          [-92.41535, 32.495486],
          [-92.415418, 32.407812],
          [-92.311829, 32.320755],
          [-92.311851, 32.277441]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22051",
      state: "22",
      county: "051",
      name: "Jefferson County",
      lsad: "Parish",
      censusArea: 295.632,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.228157, 29.692028],
          [-90.173202, 29.827524],
          [-90.240173, 29.862117],
          [-90.279533, 29.967971],
          [-90.280296, 29.972618],
          [-90.280143, 29.992744],
          [-90.27951, 30.006448],
          [-90.279739, 30.013338],
          [-90.27922, 30.049118],
          [-90.277855, 30.230888],
          [-90.243034, 30.224447],
          [-90.105456, 30.191801],
          [-90.119373, 30.036637],
          [-90.118541, 30.029845],
          [-90.120959, 30.021975],
          [-90.124775, 29.975791],
          [-90.126559, 29.970265],
          [-90.140074, 29.947904],
          [-90.058547, 29.945334],
          [-90.008056, 29.895528],
          [-90.075683, 29.77185],
          [-89.980018, 29.607582],
          [-89.96195, 29.432874],
          [-89.964563, 29.434615],
          [-89.972934, 29.443748],
          [-89.991961, 29.463536],
          [-90.01251, 29.462775],
          [-90.018598, 29.45212],
          [-90.022404, 29.444509],
          [-90.029466, 29.432015],
          [-90.137504, 29.477163],
          [-90.186172, 29.563341],
          [-90.155807, 29.67753],
          [-90.228157, 29.692028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22055",
      state: "22",
      county: "055",
      name: "Lafayette County",
      lsad: "Parish",
      censusArea: 268.72,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.284675, 30.147443],
          [-92.142184, 30.298817],
          [-92.056066, 30.378237],
          [-91.988718, 30.368598],
          [-91.942818, 30.236599],
          [-91.980328, 30.185248],
          [-91.905544, 30.154616],
          [-91.950998, 30.073213],
          [-91.96279, 30.04137],
          [-91.963827, 30.038175],
          [-91.964458, 30.036918],
          [-92.12812, 30.115904],
          [-92.284675, 30.147443]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21035",
      state: "21",
      county: "035",
      name: "Calloway County",
      censusArea: 385.021,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.070532, 36.678118],
          [-88.068208, 36.659747],
          [-88.055604, 36.63571],
          [-88.045127, 36.602939],
          [-88.035625, 36.561736],
          [-88.032489, 36.540662],
          [-88.037822, 36.51385],
          [-88.039481, 36.510408],
          [-88.053205, 36.497129],
          [-88.053292, 36.49713],
          [-88.320794, 36.500432],
          [-88.325895, 36.500483],
          [-88.330799, 36.500531],
          [-88.41636, 36.500756],
          [-88.450161, 36.501101],
          [-88.452543, 36.500872],
          [-88.472564, 36.501028],
          [-88.489075, 36.501068],
          [-88.485697, 36.750328],
          [-88.110831, 36.74715],
          [-88.084215, 36.713147],
          [-88.070532, 36.678118]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21039",
      state: "21",
      county: "039",
      name: "Carlisle County",
      censusArea: 189.434,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.100766, 36.943973],
          [-89.100794, 36.944006],
          [-88.988811, 36.919491],
          [-88.815952, 36.9541],
          [-88.812897, 36.946864],
          [-88.813229, 36.773115],
          [-89.123548, 36.78522],
          [-89.12353, 36.785309],
          [-89.179229, 36.812915],
          [-89.178574, 36.816554],
          [-89.177177, 36.835779],
          [-89.147674, 36.847148],
          [-89.100766, 36.943973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21041",
      state: "21",
      county: "041",
      name: "Carroll County",
      censusArea: 128.569,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.074583, 38.596838],
          [-85.16827, 38.585448],
          [-85.333285, 38.74048],
          [-85.289226, 38.74241],
          [-85.275454, 38.741172],
          [-85.258846, 38.737754],
          [-85.246505, 38.731821],
          [-85.226062, 38.705456],
          [-85.213257, 38.695446],
          [-85.201498, 38.691575],
          [-85.189362, 38.687579],
          [-85.172528, 38.688082],
          [-85.156158, 38.692251],
          [-85.13868, 38.699168],
          [-85.106902, 38.720789],
          [-85.100963, 38.7268],
          [-85.024205, 38.763311],
          [-84.934536, 38.66213],
          [-85.074583, 38.596838]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21045",
      state: "21",
      county: "045",
      name: "Casey County",
      censusArea: 444.23,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.043316, 37.186028],
          [-85.056898, 37.257041],
          [-85.165174, 37.310396],
          [-85.073772, 37.413669],
          [-85.039674, 37.545232],
          [-84.847326, 37.547894],
          [-84.850816, 37.427143],
          [-84.722826, 37.360043],
          [-84.720712, 37.238328],
          [-84.809338, 37.175034],
          [-84.901112, 37.116297],
          [-84.954406, 37.102271],
          [-85.043316, 37.186028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21057",
      state: "21",
      county: "057",
      name: "Cumberland County",
      censusArea: 305.182,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.45183, 36.938138],
          [-85.232026, 36.925069],
          [-85.27495, 36.897901],
          [-85.215405, 36.854467],
          [-85.246357, 36.744294],
          [-85.29599, 36.625488],
          [-85.436418, 36.618194],
          [-85.596214, 36.818039],
          [-85.45183, 36.938138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21065",
      state: "21",
      county: "065",
      name: "Estill County",
      censusArea: 253.078,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.90557, 37.542856],
          [-83.965244, 37.581421],
          [-84.090364, 37.566227],
          [-84.080337, 37.853066],
          [-84.001967, 37.837592],
          [-83.906251, 37.759363],
          [-83.7221, 37.71642],
          [-83.824102, 37.662746],
          [-83.90557, 37.542856]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21067",
      state: "21",
      county: "067",
      name: "Fayette County",
      censusArea: 283.649,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.401847, 38.207889],
          [-84.380928, 38.113563],
          [-84.286461, 38.067028],
          [-84.337393, 37.892007],
          [-84.43572, 37.847202],
          [-84.479277, 37.940076],
          [-84.660135, 38.003745],
          [-84.657498, 38.017835],
          [-84.625079, 38.116397],
          [-84.62065, 38.129527],
          [-84.401847, 38.207889]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21071",
      state: "21",
      county: "071",
      name: "Floyd County",
      censusArea: 393.345,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.92546, 37.486525],
          [-82.878723, 37.540134],
          [-82.940763, 37.716338],
          [-82.763313, 37.75612],
          [-82.64075, 37.720182],
          [-82.563561, 37.684844],
          [-82.623112, 37.503541],
          [-82.584711, 37.466574],
          [-82.672375, 37.306841],
          [-82.710805, 37.285323],
          [-82.752346, 37.308039],
          [-82.767605, 37.427777],
          [-82.92546, 37.486525]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21079",
      state: "21",
      county: "079",
      name: "Garrard County",
      censusArea: 230.077,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.716925, 37.815389],
          [-84.675889, 37.826242],
          [-84.57031, 37.728713],
          [-84.525643, 37.769497],
          [-84.347805, 37.539064],
          [-84.37444, 37.472663],
          [-84.446719, 37.485893],
          [-84.658296, 37.634501],
          [-84.744888, 37.713075],
          [-84.696741, 37.72283],
          [-84.716925, 37.815389]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21083",
      state: "21",
      county: "083",
      name: "Graves County",
      censusArea: 551.743,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.48921, 36.501068],
          [-88.51192, 36.501457],
          [-88.51227, 36.501506],
          [-88.516346, 36.501431],
          [-88.516427, 36.50143],
          [-88.545192, 36.501814],
          [-88.577283, 36.50194],
          [-88.661133, 36.502243],
          [-88.715255, 36.502662],
          [-88.747523, 36.502834],
          [-88.799594, 36.502757],
          [-88.816765, 36.502815],
          [-88.813229, 36.773115],
          [-88.812897, 36.946864],
          [-88.484103, 36.942096],
          [-88.485697, 36.750328],
          [-88.489075, 36.501068],
          [-88.48921, 36.501068]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21085",
      state: "21",
      county: "085",
      name: "Grayson County",
      censusArea: 496.698,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.612095, 37.395123],
          [-86.664055, 37.546105],
          [-86.59281, 37.56436],
          [-86.503369, 37.622191],
          [-86.34623, 37.562718],
          [-86.27527, 37.593519],
          [-86.114095, 37.567131],
          [-86.047598, 37.449933],
          [-86.019089, 37.394537],
          [-86.072351, 37.381071],
          [-86.064554, 37.348613],
          [-86.157589, 37.335649],
          [-86.469126, 37.321099],
          [-86.487152, 37.362501],
          [-86.612095, 37.395123]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21091",
      state: "21",
      county: "091",
      name: "Hancock County",
      censusArea: 187.652,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.823429, 37.737754],
          [-86.854035, 37.840811],
          [-86.97883, 37.930214],
          [-86.919329, 37.936664],
          [-86.902413, 37.946161],
          [-86.884961, 37.964373],
          [-86.85595, 37.987292],
          [-86.823491, 37.998939],
          [-86.815267, 37.998877],
          [-86.814776, 37.998638],
          [-86.794985, 37.988982],
          [-86.787695, 37.971449],
          [-86.770179, 37.940264],
          [-86.75099, 37.912893],
          [-86.740877, 37.902568],
          [-86.73146, 37.89434],
          [-86.72351, 37.892831],
          [-86.718462, 37.893123],
          [-86.709164, 37.89764],
          [-86.660293, 37.865057],
          [-86.655296, 37.842508],
          [-86.655285, 37.842506],
          [-86.677871, 37.792845],
          [-86.638935, 37.661877],
          [-86.823429, 37.737754]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21097",
      state: "21",
      county: "097",
      name: "Harrison County",
      censusArea: 306.364,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.442661, 38.283236],
          [-84.440725, 38.296385],
          [-84.432831, 38.299442],
          [-84.443756, 38.32431],
          [-84.557375, 38.492917],
          [-84.476299, 38.542723],
          [-84.205346, 38.582039],
          [-84.211319, 38.550864],
          [-84.162031, 38.554125],
          [-84.183903, 38.498019],
          [-84.1021, 38.459379],
          [-84.19405, 38.37175],
          [-84.345476, 38.285099],
          [-84.442661, 38.283236]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21101",
      state: "21",
      county: "101",
      name: "Henderson County",
      censusArea: 436.669,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.684018, 37.903498],
          [-87.67573, 37.90193],
          [-87.666481, 37.895786],
          [-87.662865, 37.885578],
          [-87.681633, 37.855917],
          [-87.6819, 37.84641],
          [-87.679188, 37.836321],
          [-87.670889, 37.828429],
          [-87.645858, 37.825899],
          [-87.615399, 37.831974],
          [-87.606599, 37.838669],
          [-87.591504, 37.856642],
          [-87.588729, 37.860984],
          [-87.588426, 37.868791],
          [-87.591582, 37.887194],
          [-87.597118, 37.892394],
          [-87.620272, 37.906922],
          [-87.626256, 37.916138],
          [-87.62896, 37.926714],
          [-87.601416, 37.972542],
          [-87.592916, 37.975842],
          [-87.585916, 37.975442],
          [-87.581115, 37.973442],
          [-87.574715, 37.967742],
          [-87.573415, 37.962642],
          [-87.574287, 37.954842],
          [-87.57203, 37.947466],
          [-87.559342, 37.931146],
          [-87.550333, 37.92489],
          [-87.511499, 37.906426],
          [-87.451185, 37.941034],
          [-87.450458, 37.941451],
          [-87.436859, 37.944192],
          [-87.418585, 37.944763],
          [-87.402632, 37.942267],
          [-87.380247, 37.935596],
          [-87.302324, 37.898445],
          [-87.323402, 37.819239],
          [-87.271608, 37.78012],
          [-87.408547, 37.683563],
          [-87.420533, 37.670153],
          [-87.438436, 37.670931],
          [-87.472811, 37.664869],
          [-87.495331, 37.647547],
          [-87.734545, 37.638376],
          [-87.75565, 37.727763],
          [-87.815093, 37.762423],
          [-87.927424, 37.902038],
          [-87.904789, 37.924892],
          [-87.898062, 37.927514],
          [-87.883321, 37.926238],
          [-87.87254, 37.920999],
          [-87.865558, 37.915056],
          [-87.857243, 37.900649],
          [-87.841193, 37.882325],
          [-87.830578, 37.876516],
          [-87.7909, 37.875714],
          [-87.783643, 37.877759],
          [-87.76226, 37.890906],
          [-87.700421, 37.900858],
          [-87.684018, 37.903498]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21107",
      state: "21",
      county: "107",
      name: "Hopkins County",
      censusArea: 541.995,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.680617, 37.149239],
          [-87.805029, 37.241083],
          [-87.85076, 37.322136],
          [-87.813413, 37.350645],
          [-87.593575, 37.461963],
          [-87.499968, 37.486741],
          [-87.375145, 37.569988],
          [-87.351893, 37.425655],
          [-87.295965, 37.391819],
          [-87.388826, 37.261395],
          [-87.333741, 37.157186],
          [-87.521609, 37.105344],
          [-87.680617, 37.149239]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48267",
      state: "48",
      county: "267",
      name: "Kimble County",
      censusArea: 1250.987,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.754142, 30.290698],
          [-100.116461, 30.290296],
          [-100.116234, 30.710366],
          [-99.483869, 30.710771],
          [-99.484493, 30.499641],
          [-99.303996, 30.499832],
          [-99.301718, 30.286653],
          [-99.754142, 30.290698]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48271",
      state: "48",
      county: "271",
      name: "Kinney County",
      censusArea: 1360.055,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.668356, 29.08397],
          [-100.674656, 29.099777],
          [-100.793662, 29.242146],
          [-100.745564, 29.254809],
          [-100.699141, 29.419747],
          [-100.699932, 29.623897],
          [-100.112098, 29.623263],
          [-100.111406, 29.086318],
          [-100.667168, 29.08431],
          [-100.668356, 29.08397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48277",
      state: "48",
      county: "277",
      name: "Lamar County",
      censusArea: 907.19,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.857738, 33.461722],
          [-95.846334, 33.841136],
          [-95.82468, 33.837726],
          [-95.763622, 33.847954],
          [-95.757156, 33.867293],
          [-95.599678, 33.934247],
          [-95.567022, 33.932697],
          [-95.559414, 33.930179],
          [-95.549145, 33.90795],
          [-95.549475, 33.901311],
          [-95.552331, 33.89442],
          [-95.552085, 33.888422],
          [-95.548325, 33.882744],
          [-95.544346, 33.880111],
          [-95.445751, 33.86885],
          [-95.407795, 33.866308],
          [-95.339758, 33.868752],
          [-95.310475, 33.871481],
          [-95.308637, 33.380662],
          [-95.621027, 33.48706],
          [-95.758439, 33.495098],
          [-95.82673, 33.459038],
          [-95.857738, 33.461722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48281",
      state: "48",
      county: "281",
      name: "Lampasas County",
      censusArea: 712.84,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.562739, 31.230582],
          [-98.266794, 31.410521],
          [-98.27107, 31.416398],
          [-98.180006, 31.463717],
          [-97.9071, 31.069374],
          [-97.913847, 31.065882],
          [-97.911684, 31.034919],
          [-98.439687, 31.029537],
          [-98.523459, 31.088886],
          [-98.510341, 31.155666],
          [-98.562739, 31.230582]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48285",
      state: "48",
      county: "285",
      name: "Lavaca County",
      censusArea: 969.708,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.938587, 29.063244],
          [-96.946942, 29.072235],
          [-96.947333, 29.072716],
          [-96.947959, 29.073335],
          [-96.976378, 29.104046],
          [-97.240108, 29.384488],
          [-97.204087, 29.580613],
          [-97.142643, 29.628101],
          [-96.874222, 29.632706],
          [-96.793132, 29.577378],
          [-96.560555, 29.335439],
          [-96.658466, 29.263876],
          [-96.938587, 29.063244]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48287",
      state: "48",
      county: "287",
      name: "Lee County",
      censusArea: 629.016,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.024461, 30.051435],
          [-97.04821, 30.137947],
          [-97.081834, 30.259357],
          [-97.334463, 30.402843],
          [-97.155219, 30.457344],
          [-96.963629, 30.557169],
          [-96.740542, 30.318655],
          [-96.640883, 30.296786],
          [-96.731645, 30.243886],
          [-96.794552, 30.160545],
          [-97.019163, 30.032],
          [-97.024461, 30.051435]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48291",
      state: "48",
      county: "291",
      name: "Liberty County",
      censusArea: 1158.417,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.165897, 30.344976],
          [-95.03246, 30.404091],
          [-95.024589, 30.406903],
          [-94.849414, 30.493553],
          [-94.777341, 30.489002],
          [-94.732732, 30.490066],
          [-94.603159, 30.130904],
          [-94.44491, 30.113015],
          [-94.445096, 30.110966],
          [-94.442235, 29.889538],
          [-94.98146, 29.884276],
          [-94.985364, 29.972279],
          [-95.031179, 29.993324],
          [-95.096708, 30.167214],
          [-95.165897, 30.344976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48293",
      state: "48",
      county: "293",
      name: "Limestone County",
      censusArea: 905.288,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.236629, 31.413392],
          [-96.304907, 31.382132],
          [-96.322853, 31.374067],
          [-96.319165, 31.357129],
          [-96.596862, 31.2209],
          [-96.801123, 31.522269],
          [-96.932215, 31.708887],
          [-96.735543, 31.806667],
          [-96.719114, 31.814887],
          [-96.496713, 31.796191],
          [-96.236629, 31.413392]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48295",
      state: "48",
      county: "295",
      name: "Lipscomb County",
      censusArea: 932.179,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.546724, 36.056536],
          [-100.546145, 36.499343],
          [-100.531215, 36.499341],
          [-100.531215, 36.49929],
          [-100.530478, 36.49924],
          [-100.530314, 36.499357],
          [-100.522227, 36.499291],
          [-100.441065, 36.49949],
          [-100.441064, 36.499462],
          [-100.433959, 36.499456],
          [-100.421328, 36.499447],
          [-100.421301, 36.499488],
          [-100.413634, 36.499444],
          [-100.41355, 36.499469],
          [-100.378634, 36.499517],
          [-100.378592, 36.499445],
          [-100.351852, 36.499487],
          [-100.351842, 36.499473],
          [-100.334464, 36.49942],
          [-100.334441, 36.49944],
          [-100.32415, 36.499679],
          [-100.311245, 36.499631],
          [-100.311018, 36.499688],
          [-100.310643, 36.499642],
          [-100.181221, 36.499633],
          [-100.090021, 36.499634],
          [-100.003762, 36.499699],
          [-100.000406, 36.499702],
          [-100.000399, 36.055677],
          [-100.540221, 36.056491],
          [-100.546724, 36.056536]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48301",
      state: "48",
      county: "301",
      name: "Loving County",
      censusArea: 668.925,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.326501, 32.00037],
          [-103.327538, 31.65142],
          [-103.610887, 31.651802],
          [-103.625726, 31.705032],
          [-103.797232, 31.770187],
          [-103.829385, 31.887992],
          [-103.885584, 31.870373],
          [-103.980179, 32.000125],
          [-103.722853, 32.000221],
          [-103.326501, 32.00037]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48305",
      state: "48",
      county: "305",
      name: "Lynn County",
      censusArea: 891.867,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.557434, 32.961025],
          [-101.691284, 32.961838],
          [-102.076214, 32.959702],
          [-102.075929, 33.389586],
          [-101.556884, 33.39476],
          [-101.557434, 32.961025]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48307",
      state: "48",
      county: "307",
      name: "Mc Culloch County",
      censusArea: 1065.598,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.60185, 31.49195],
          [-99.513678, 31.425495],
          [-99.402284, 31.446198],
          [-99.314875, 31.409875],
          [-99.202768, 31.467234],
          [-99.090622, 31.460927],
          [-99.092317, 30.941002],
          [-99.484755, 30.940605],
          [-99.603627, 30.940726],
          [-99.603223, 31.087296],
          [-99.60185, 31.49195]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48317",
      state: "48",
      county: "317",
      name: "Martin County",
      censusArea: 914.944,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.211037, 32.523244],
          [-102.202703, 32.523271],
          [-101.68874, 32.525222],
          [-101.695011, 32.087532],
          [-101.776085, 32.086925],
          [-102.211249, 32.0868],
          [-102.211037, 32.523244]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48323",
      state: "48",
      county: "323",
      name: "Maverick County",
      censusArea: 1279.258,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.113719, 28.197819],
          [-100.209464, 28.196866],
          [-100.257788, 28.240341],
          [-100.291391, 28.275398],
          [-100.346986, 28.407232],
          [-100.333814, 28.499252],
          [-100.338518, 28.501833],
          [-100.386963, 28.514023],
          [-100.405058, 28.53578],
          [-100.429856, 28.596441],
          [-100.446659, 28.608659],
          [-100.500354, 28.66196],
          [-100.510055, 28.690723],
          [-100.511998, 28.705352],
          [-100.506701, 28.716745],
          [-100.507613, 28.740599],
          [-100.535438, 28.805195],
          [-100.546804, 28.825374],
          [-100.589854, 28.861618],
          [-100.640092, 28.915833],
          [-100.651512, 28.943432],
          [-100.648104, 28.98386],
          [-100.645894, 28.986421],
          [-100.664065, 29.073206],
          [-100.668356, 29.08397],
          [-100.667168, 29.08431],
          [-100.111406, 29.086318],
          [-100.114336, 28.648122],
          [-100.113719, 28.197819]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48327",
      state: "48",
      county: "327",
      name: "Menard County",
      censusArea: 902.03,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.115216, 31.087994],
          [-99.603223, 31.087296],
          [-99.603627, 30.940726],
          [-99.484755, 30.940605],
          [-99.483869, 30.710771],
          [-100.116234, 30.710366],
          [-100.115216, 31.087994]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48331",
      state: "48",
      county: "331",
      name: "Milam County",
      censusArea: 1016.93,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.829121, 31.105871],
          [-96.75633, 30.927206],
          [-96.68497, 30.906191],
          [-96.661054, 30.829686],
          [-96.621281, 30.730819],
          [-96.963629, 30.557169],
          [-97.155219, 30.457344],
          [-97.271418, 30.735692],
          [-97.315507, 30.752371],
          [-97.259082, 30.889596],
          [-97.070188, 30.98622],
          [-96.829121, 31.105871]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48333",
      state: "48",
      county: "333",
      name: "Mills County",
      censusArea: 748.261,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.27107, 31.416398],
          [-98.266794, 31.410521],
          [-98.562739, 31.230582],
          [-98.726094, 31.428278],
          [-98.762589, 31.375282],
          [-98.773, 31.410926],
          [-98.854292, 31.413425],
          [-98.8725, 31.444299],
          [-98.967159, 31.435903],
          [-98.991608, 31.484071],
          [-98.779951, 31.610735],
          [-98.760057, 31.687896],
          [-98.668441, 31.700514],
          [-98.492802, 31.723608],
          [-98.463736, 31.683989],
          [-98.27107, 31.416398]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51800",
      state: "51",
      county: "800",
      name: "Suffolk City County",
      lsad: "city",
      censusArea: 400.168,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.897592, 36.64405],
          [-76.53352, 36.915563],
          [-76.484107, 36.928916],
          [-76.482407, 36.917364],
          [-76.482135, 36.901108],
          [-76.483369, 36.896239],
          [-76.469914, 36.882898],
          [-76.454692, 36.884077],
          [-76.45329, 36.887031],
          [-76.453941, 36.89274],
          [-76.447413, 36.90322],
          [-76.441605, 36.906116],
          [-76.43122, 36.904532],
          [-76.407507, 36.897444],
          [-76.406908, 36.897507],
          [-76.406667, 36.878448],
          [-76.420368, 36.866348],
          [-76.457469, 36.814841],
          [-76.491497, 36.550365],
          [-76.541391, 36.550312],
          [-76.541687, 36.550312],
          [-76.781296, 36.550659],
          [-76.807078, 36.550606],
          [-76.915384, 36.543856],
          [-76.897592, 36.64405]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51810",
      state: "51",
      county: "810",
      name: "Virginia Beach City County",
      lsad: "city",
      censusArea: 249.016,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.22606, 36.839947],
          [-76.213959, 36.832347],
          [-76.210412, 36.832907],
          [-76.177019, 36.92929],
          [-76.139557, 36.923047],
          [-76.095508, 36.908817],
          [-76.087955, 36.908647],
          [-76.058154, 36.916947],
          [-76.043054, 36.927547],
          [-76.033454, 36.931946],
          [-76.013753, 36.930746],
          [-76.007553, 36.929047],
          [-75.996252, 36.922047],
          [-75.991552, 36.910847],
          [-75.972151, 36.842268],
          [-75.965451, 36.812449],
          [-75.94955, 36.76115],
          [-75.921748, 36.692051],
          [-75.890946, 36.630753],
          [-75.874145, 36.583853],
          [-75.867044, 36.550754],
          [-75.879744, 36.550754],
          [-75.880644, 36.550754],
          [-75.885945, 36.550754],
          [-75.886545, 36.550754],
          [-75.891945, 36.550754],
          [-75.893245, 36.550654],
          [-75.894145, 36.550754],
          [-75.903445, 36.550654],
          [-75.904745, 36.550654],
          [-75.909046, 36.550654],
          [-75.911446, 36.550654],
          [-75.922046, 36.550654],
          [-75.952847, 36.550553],
          [-75.953447, 36.550553],
          [-75.955748, 36.550553],
          [-75.957648, 36.550553],
          [-76.02675, 36.550553],
          [-76.034751, 36.550653],
          [-76.12236, 36.550621],
          [-76.121955, 36.66575],
          [-76.068154, 36.69235],
          [-76.203667, 36.769655],
          [-76.22606, 36.839947]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51840",
      state: "51",
      county: "840",
      name: "Winchester City County",
      lsad: "city",
      censusArea: 9.233,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.196945, 39.191161],
          [-78.154919, 39.200895],
          [-78.136953, 39.18453],
          [-78.18289, 39.13097],
          [-78.196945, 39.191161]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53001",
      state: "53",
      county: "001",
      name: "Adams County",
      censusArea: 1924.978,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.13607, 46.91222],
          [-118.982545, 46.911346],
          [-118.979519, 47.261712],
          [-118.838281, 47.260957],
          [-117.960143, 47.260449],
          [-117.958856, 46.914878],
          [-118.012613, 46.790485],
          [-118.209807, 46.738938],
          [-119.081621, 46.738019],
          [-119.369387, 46.737696],
          [-119.368662, 46.912562],
          [-119.13607, 46.91222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53003",
      state: "53",
      county: "003",
      name: "Asotin County",
      censusArea: 636.213,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.039813, 46.425425],
          [-117.046915, 46.379577],
          [-117.061045, 46.367747],
          [-117.047469, 46.342884],
          [-116.987939, 46.298031],
          [-116.985333, 46.294617],
          [-116.958801, 46.24232],
          [-116.925621, 46.158888],
          [-116.94353, 46.062173],
          [-116.915989, 45.995413],
          [-117.212616, 45.998321],
          [-117.214534, 45.99832],
          [-117.216731, 45.998356],
          [-117.475148, 45.997893],
          [-117.47536, 45.997855],
          [-117.480103, 45.99787],
          [-117.48013, 45.99787],
          [-117.480045, 46.121993],
          [-117.419591, 46.121921],
          [-117.41968, 46.383294],
          [-117.228758, 46.411293],
          [-117.228896, 46.462314],
          [-117.199044, 46.419786],
          [-117.039813, 46.425425]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53005",
      state: "53",
      county: "005",
      name: "Benton County",
      censusArea: 1700.379,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.868135, 45.835962],
          [-119.866265, 46.040662],
          [-119.874057, 46.628585],
          [-119.626139, 46.644983],
          [-119.515653, 46.727396],
          [-119.45322, 46.679243],
          [-119.258292, 46.489457],
          [-119.268928, 46.277957],
          [-119.041418, 46.192856],
          [-118.980933, 46.146296],
          [-118.941075, 46.029339],
          [-118.987129, 45.999855],
          [-119.008558, 45.97927],
          [-119.027056, 45.969134],
          [-119.093221, 45.942745],
          [-119.12612, 45.932859],
          [-119.169496, 45.927603],
          [-119.19553, 45.92787],
          [-119.225745, 45.932725],
          [-119.25715, 45.939926],
          [-119.322509, 45.933183],
          [-119.37613, 45.920974],
          [-119.432067, 45.913629],
          [-119.487829, 45.906307],
          [-119.524632, 45.908605],
          [-119.571584, 45.925456],
          [-119.600549, 45.919581],
          [-119.623393, 45.905639],
          [-119.669877, 45.856867],
          [-119.772927, 45.845578],
          [-119.802655, 45.84753],
          [-119.868135, 45.835962]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53015",
      state: "53",
      county: "015",
      name: "Cowlitz County",
      censusArea: 1140.125,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.217841, 46.385128],
          [-123.091305, 46.387929],
          [-122.241696, 46.385292],
          [-122.239952, 46.384204],
          [-122.245633, 46.053932],
          [-122.357765, 45.956915],
          [-122.477104, 45.988089],
          [-122.738996, 45.915287],
          [-122.785515, 45.850536],
          [-122.785026, 45.867699],
          [-122.813998, 45.960984],
          [-122.884478, 46.06028],
          [-122.904119, 46.083734],
          [-123.115904, 46.185268],
          [-123.166414, 46.188973],
          [-123.213054, 46.172541],
          [-123.217841, 46.385128]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53017",
      state: "53",
      county: "017",
      name: "Douglas County",
      censusArea: 1819.264,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.007065, 47.220053],
          [-120.074669, 47.225152],
          [-120.09447, 47.262151],
          [-120.083274, 47.33369],
          [-120.129573, 47.366251],
          [-120.28653, 47.393111],
          [-120.318084, 47.457748],
          [-120.292814, 47.538172],
          [-120.233584, 47.59763],
          [-120.199048, 47.682276],
          [-120.209395, 47.748356],
          [-120.130914, 47.776601],
          [-119.994952, 47.781353],
          [-119.870307, 47.960328],
          [-119.891656, 48.055053],
          [-119.771307, 48.104713],
          [-119.670241, 48.069775],
          [-119.693627, 48.032554],
          [-119.629835, 47.996239],
          [-119.502391, 48.082116],
          [-119.340989, 48.049512],
          [-119.325444, 48.099645],
          [-119.126141, 48.15051],
          [-119.062237, 48.138378],
          [-119.006111, 48.046143],
          [-118.955484, 48.023185],
          [-118.982394, 47.962019],
          [-119.104041, 47.961763],
          [-119.125659, 47.917253],
          [-119.212149, 47.887617],
          [-119.212499, 47.800652],
          [-119.298744, 47.728277],
          [-119.341708, 47.612348],
          [-119.405196, 47.612471],
          [-119.532889, 47.527563],
          [-119.533624, 47.441317],
          [-119.875835, 47.437352],
          [-120.004767, 47.320052],
          [-120.007065, 47.220053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53019",
      state: "53",
      county: "019",
      name: "Ferry County",
      censusArea: 2203.159,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.836794, 49.000209],
          [-118.196891, 49.000094],
          [-118.221669, 48.937098],
          [-118.195508, 48.840725],
          [-118.131002, 48.777653],
          [-118.10496, 48.653503],
          [-118.149709, 48.520568],
          [-118.206507, 48.470296],
          [-118.175602, 48.319492],
          [-118.141475, 48.259951],
          [-118.1945, 48.212889],
          [-118.191298, 48.159888],
          [-118.248606, 48.134255],
          [-118.238198, 48.058184],
          [-118.402407, 48.026382],
          [-118.340925, 47.894395],
          [-118.357065, 47.841599],
          [-118.54409, 47.883973],
          [-118.570396, 47.932318],
          [-118.711682, 47.903347],
          [-118.851972, 47.957686],
          [-118.845785, 47.963478],
          [-118.843439, 48.481682],
          [-118.86954, 48.481881],
          [-118.869672, 48.653598],
          [-118.835399, 48.654153],
          [-118.836794, 49.000209]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53023",
      state: "53",
      county: "023",
      name: "Garfield County",
      censusArea: 710.686,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.603163, 45.998887],
          [-117.611815, 46.338496],
          [-117.675113, 46.337628],
          [-117.737301, 46.471366],
          [-117.863261, 46.470935],
          [-117.851844, 46.624772],
          [-117.751037, 46.690027],
          [-117.601227, 46.672679],
          [-117.475331, 46.70033],
          [-117.253902, 46.545082],
          [-117.228896, 46.462314],
          [-117.228758, 46.411293],
          [-117.41968, 46.383294],
          [-117.419591, 46.121921],
          [-117.480045, 46.121993],
          [-117.48013, 45.99787],
          [-117.504833, 45.998317],
          [-117.603163, 45.998887]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53025",
      state: "53",
      county: "025",
      name: "Grant County",
      censusArea: 2679.513,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.979519, 47.261712],
          [-118.982545, 46.911346],
          [-119.13607, 46.91222],
          [-119.368662, 46.912562],
          [-119.369387, 46.737696],
          [-119.368939, 46.678204],
          [-119.45322, 46.679243],
          [-119.515653, 46.727396],
          [-119.626139, 46.644983],
          [-119.874057, 46.628585],
          [-119.973017, 46.737171],
          [-119.927537, 46.817092],
          [-120.042866, 47.073453],
          [-120.007065, 47.220053],
          [-120.004767, 47.320052],
          [-119.875835, 47.437352],
          [-119.533624, 47.441317],
          [-119.532889, 47.527563],
          [-119.405196, 47.612471],
          [-119.341708, 47.612348],
          [-119.298744, 47.728277],
          [-119.212499, 47.800652],
          [-119.212149, 47.887617],
          [-119.125659, 47.917253],
          [-119.104041, 47.961763],
          [-118.982394, 47.962019],
          [-118.982754, 47.949782],
          [-118.973502, 47.94357],
          [-118.97648, 47.659033],
          [-118.979519, 47.261712]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51600",
      state: "51",
      county: "600",
      name: "Fairfax City County",
      lsad: "city",
      censusArea: 6.239,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.334805, 38.851404],
          [-77.328788, 38.864848],
          [-77.270597, 38.866075],
          [-77.2705, 38.840992],
          [-77.318689, 38.834651],
          [-77.334805, 38.851404]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55101",
      state: "55",
      county: "101",
      name: "Racine County",
      censusArea: 332.501,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.827021, 42.842222],
          [-87.825352, 42.839144],
          [-87.824, 42.836649],
          [-87.823278, 42.835318],
          [-87.822324, 42.834371],
          [-87.804033, 42.816205],
          [-87.803773, 42.815948],
          [-87.796201, 42.808428],
          [-87.793976, 42.806218],
          [-87.789953, 42.803691],
          [-87.786291, 42.801391],
          [-87.785936, 42.801168],
          [-87.773699, 42.793481],
          [-87.773623, 42.793388],
          [-87.766675, 42.784896],
          [-87.769774, 42.773719],
          [-87.77134, 42.771687],
          [-87.777432, 42.763781],
          [-87.778174, 42.762819],
          [-87.778774, 42.760556],
          [-87.780782, 42.752973],
          [-87.781016, 42.75209],
          [-87.781949, 42.74857],
          [-87.782174, 42.747719],
          [-87.781849, 42.745847],
          [-87.781655, 42.744733],
          [-87.781642, 42.744656],
          [-87.781333, 42.742879],
          [-87.779527, 42.732482],
          [-87.778824, 42.728432],
          [-87.778627, 42.727299],
          [-87.78032, 42.718679],
          [-87.782023, 42.710008],
          [-87.782374, 42.708219],
          [-87.783489, 42.705164],
          [-87.784015, 42.70372],
          [-87.785074, 42.700819],
          [-87.786774, 42.700719],
          [-87.794874, 42.689919],
          [-87.802377, 42.676651],
          [-87.802876, 42.675768],
          [-87.802989, 42.675569],
          [-87.803028, 42.675501],
          [-87.803074, 42.675419],
          [-87.803127, 42.675276],
          [-87.805421, 42.669067],
          [-87.805768, 42.668128],
          [-88.188306, 42.669709],
          [-88.188173, 42.611456],
          [-88.305891, 42.610817],
          [-88.306384, 42.842095],
          [-88.245499, 42.841356],
          [-88.069924, 42.843323],
          [-87.827021, 42.842222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55107",
      state: "55",
      county: "107",
      name: "Rusk County",
      censusArea: 913.585,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.678749, 45.638264],
          [-90.678739, 45.37777],
          [-90.92517, 45.37935],
          [-90.925223, 45.291965],
          [-91.541322, 45.292015],
          [-91.540294, 45.637605],
          [-90.678749, 45.638264]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55111",
      state: "55",
      county: "111",
      name: "Sauk County",
      censusArea: 830.902,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.716768, 43.321806],
          [-89.727171, 43.301305],
          [-89.720295, 43.292928],
          [-89.838135, 43.206057],
          [-90.193814, 43.164464],
          [-90.191964, 43.554996],
          [-90.311069, 43.553991],
          [-90.311142, 43.565134],
          [-90.310979, 43.566408],
          [-90.312404, 43.640988],
          [-89.838112, 43.64093],
          [-89.835589, 43.64092],
          [-89.831781, 43.641095],
          [-89.825916, 43.641011],
          [-89.824362, 43.641146],
          [-89.823444, 43.641035],
          [-89.785809, 43.641049],
          [-89.732238, 43.571826],
          [-89.599357, 43.558041],
          [-89.600719, 43.380006],
          [-89.716768, 43.321806]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55113",
      state: "55",
      county: "113",
      name: "Sawyer County",
      censusArea: 1257.305,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.924583, 46.154616],
          [-90.925174, 45.981055],
          [-90.676968, 45.981554],
          [-90.678749, 45.638264],
          [-91.540294, 45.637605],
          [-91.551282, 46.157045],
          [-90.924583, 46.154616]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55119",
      state: "55",
      county: "119",
      name: "Taylor County",
      censusArea: 974.876,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.678739, 45.37777],
          [-90.042273, 45.381866],
          [-90.046143, 45.340309],
          [-90.043685, 45.120214],
          [-90.197765, 45.120604],
          [-90.197514, 45.03364],
          [-90.315038, 45.033829],
          [-90.923362, 45.031143],
          [-90.925223, 45.291965],
          [-90.92517, 45.37935],
          [-90.678739, 45.37777]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55123",
      state: "55",
      county: "123",
      name: "Vernon County",
      censusArea: 791.579,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.312404, 43.640988],
          [-90.310979, 43.566408],
          [-90.311142, 43.565134],
          [-90.311069, 43.553991],
          [-90.671654, 43.552969],
          [-90.668561, 43.422994],
          [-91.20555, 43.422949],
          [-91.217706, 43.50055],
          [-91.265091, 43.609977],
          [-91.268748, 43.615348],
          [-91.273316, 43.668307],
          [-91.268198, 43.726571],
          [-90.910467, 43.725342],
          [-90.312195, 43.731312],
          [-90.312404, 43.640988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55125",
      state: "55",
      county: "125",
      name: "Vilas County",
      censusArea: 856.604,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.990807, 46.097298],
          [-88.948698, 46.080205],
          [-88.943279, 46.077943],
          [-88.932558, 46.073601],
          [-88.933567, 45.982098],
          [-89.047562, 45.982167],
          [-89.047481, 45.895359],
          [-89.178269, 45.899395],
          [-89.175245, 45.856579],
          [-89.300849, 45.860903],
          [-89.300631, 45.901464],
          [-90.042854, 45.897151],
          [-90.043958, 45.981948],
          [-89.928837, 45.981967],
          [-89.929093, 46.300087],
          [-89.90991, 46.296402],
          [-89.908196, 46.296037],
          [-89.276883, 46.174116],
          [-89.276489, 46.174047],
          [-89.219964, 46.163319],
          [-89.218156, 46.162988],
          [-89.205657, 46.160408],
          [-89.203289, 46.16002],
          [-89.201283, 46.159426],
          [-89.194508, 46.157942],
          [-89.166887, 46.152868],
          [-89.161757, 46.151816],
          [-89.125136, 46.144531],
          [-89.058745, 46.124952],
          [-88.990885, 46.09733],
          [-88.990807, 46.097298]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55133",
      state: "55",
      county: "133",
      name: "Waukesha County",
      censusArea: 549.574,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.542026, 42.93094],
          [-88.539619, 43.107605],
          [-88.535843, 43.196048],
          [-88.417987, 43.194697],
          [-88.063353, 43.192117],
          [-88.06862, 42.98096],
          [-88.068588, 42.979814],
          [-88.068887, 42.973666],
          [-88.068817, 42.972832],
          [-88.068978, 42.967369],
          [-88.069098, 42.966397],
          [-88.069249, 42.959104],
          [-88.069924, 42.843323],
          [-88.245499, 42.841356],
          [-88.306384, 42.842095],
          [-88.531228, 42.842719],
          [-88.541535, 42.842996],
          [-88.542026, 42.93094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55139",
      state: "55",
      county: "139",
      name: "Winnebago County",
      censusArea: 434.487,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.404043, 44.226903],
          [-88.404425, 44.209254],
          [-88.404187, 43.9382],
          [-88.403195, 43.892977],
          [-88.46757, 43.893415],
          [-88.885697, 43.895184],
          [-88.886193, 43.983233],
          [-88.886673, 44.242622],
          [-88.73977, 44.243303],
          [-88.40407, 44.244102],
          [-88.404043, 44.226903]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56003",
      state: "56",
      county: "003",
      name: "Big Horn County",
      censusArea: 3137.097,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.146808, 44.165666],
          [-108.550562, 44.168455],
          [-108.573797, 44.168468],
          [-108.57342, 44.520811],
          [-108.592494, 44.52093],
          [-108.592296, 44.871444],
          [-108.621313, 45.000408],
          [-108.578484, 45.000484],
          [-108.565921, 45.000578],
          [-108.249345, 44.999458],
          [-108.125666, 45.001011],
          [-107.911495, 45.000948],
          [-107.832319, 44.790161],
          [-107.741543, 44.714352],
          [-107.615891, 44.701297],
          [-107.369621, 44.559837],
          [-107.304293, 44.463779],
          [-107.200149, 44.441962],
          [-107.145001, 44.324004],
          [-107.146808, 44.165666]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56009",
      state: "56",
      county: "009",
      name: "Converse County",
      censusArea: 4254.883,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.079797, 43.498445],
          [-104.899941, 43.49964],
          [-104.892437, 42.608939],
          [-105.286511, 42.605826],
          [-105.28391, 42.431402],
          [-105.332638, 42.431106],
          [-105.375541, 42.290139],
          [-105.613865, 42.304455],
          [-105.556042, 42.430583],
          [-106.073403, 42.433236],
          [-106.075852, 42.433218],
          [-106.072669, 42.778165],
          [-106.078068, 43.494471],
          [-106.050599, 43.494751],
          [-106.017366, 43.494969],
          [-105.079797, 43.498445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56017",
      state: "56",
      county: "017",
      name: "Hot Springs County",
      censusArea: 2004.092,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.550758, 44.079403],
          [-108.490254, 44.07976],
          [-108.489431, 43.991866],
          [-108.427064, 43.992054],
          [-108.429563, 43.903355],
          [-108.30936, 43.903255],
          [-108.309695, 43.846619],
          [-108.185255, 43.848255],
          [-108.174938, 43.818968],
          [-107.956448, 43.818666],
          [-107.956577, 43.733446],
          [-107.715876, 43.733343],
          [-107.716218, 43.645766],
          [-107.595157, 43.646427],
          [-107.595105, 43.501397],
          [-107.595137, 43.472279],
          [-108.337861, 43.459765],
          [-108.456973, 43.474279],
          [-108.456962, 43.503156],
          [-108.576167, 43.503256],
          [-108.576167, 43.532556],
          [-108.695072, 43.532255],
          [-108.694672, 43.575755],
          [-108.814276, 43.575555],
          [-108.814276, 43.605255],
          [-108.934141, 43.619163],
          [-109.171638, 43.619764],
          [-109.171448, 43.699198],
          [-109.312949, 43.813293],
          [-109.093603, 43.817619],
          [-109.092878, 43.904474],
          [-108.912783, 43.904754],
          [-108.915985, 43.990854],
          [-108.791779, 43.991053],
          [-108.792277, 44.079616],
          [-108.550758, 44.079403]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56019",
      state: "56",
      county: "019",
      name: "Johnson County",
      censusArea: 4154.153,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.078068, 43.494471],
          [-107.11092, 43.492362],
          [-107.110734, 43.500286],
          [-107.128584, 44.165692],
          [-107.146808, 44.165666],
          [-107.145001, 44.324004],
          [-107.200149, 44.441962],
          [-107.304293, 44.463779],
          [-107.369621, 44.559837],
          [-106.428897, 44.556777],
          [-106.009676, 44.563981],
          [-106.016504, 44.169729],
          [-106.009201, 43.821579],
          [-106.017366, 43.494969],
          [-106.050599, 43.494751],
          [-106.078068, 43.494471]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54023",
      state: "54",
      county: "023",
      name: "Grant County",
      censusArea: 477.373,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.979898, 39.237624],
          [-79.089655, 39.038208],
          [-79.045953, 38.92871],
          [-79.134296, 38.81334],
          [-79.349867, 38.957509],
          [-79.349566, 38.958709],
          [-79.357666, 38.964509],
          [-79.298164, 39.07321],
          [-79.355468, 39.086508],
          [-79.289264, 39.189706],
          [-79.487175, 39.194906],
          [-79.486873, 39.205961],
          [-79.402911, 39.250106],
          [-79.378687, 39.27192],
          [-79.280039, 39.340076],
          [-79.134456, 39.312501],
          [-78.979898, 39.237624]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54025",
      state: "54",
      county: "025",
      name: "Greenbrier County",
      censusArea: 1019.569,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.882187, 38.103457],
          [-80.711961, 38.079129],
          [-80.436514, 38.267334],
          [-80.360048, 38.225845],
          [-80.363295, 38.114331],
          [-80.264653, 38.046616],
          [-80.169169, 38.036111],
          [-79.959844, 38.063697],
          [-79.978427, 38.029082],
          [-80.002507, 37.992767],
          [-80.055278, 37.951702],
          [-80.130464, 37.893194],
          [-80.14613, 37.884453],
          [-80.147316, 37.885936],
          [-80.148964, 37.886057],
          [-80.162202, 37.875122],
          [-80.227092, 37.798886],
          [-80.231551, 37.792591],
          [-80.257411, 37.756084],
          [-80.262765, 37.738336],
          [-80.260313, 37.733517],
          [-80.259342, 37.731205],
          [-80.263936, 37.719137],
          [-80.287107, 37.696403],
          [-80.296138, 37.691783],
          [-80.450651, 37.683858],
          [-80.663348, 37.731696],
          [-80.806316, 37.868915],
          [-80.769866, 37.98527],
          [-80.882187, 38.103457]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54027",
      state: "54",
      county: "027",
      name: "Hampshire County",
      censusArea: 640.249,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.508132, 39.08863],
          [-78.899695, 39.199137],
          [-78.979898, 39.237624],
          [-78.850723, 39.331093],
          [-78.767447, 39.447238],
          [-78.657324, 39.535087],
          [-78.655984, 39.534695],
          [-78.565929, 39.519444],
          [-78.468639, 39.516789],
          [-78.347087, 39.466012],
          [-78.362267, 39.357784],
          [-78.34048, 39.353492],
          [-78.35894, 39.319484],
          [-78.360035, 39.317771],
          [-78.399785, 39.244129],
          [-78.399669, 39.243874],
          [-78.423968, 39.212049],
          [-78.427911, 39.208611],
          [-78.429803, 39.207014],
          [-78.431167, 39.205744],
          [-78.43213, 39.204717],
          [-78.437053, 39.199766],
          [-78.438651, 39.198049],
          [-78.426722, 39.188903],
          [-78.41074, 39.171983],
          [-78.418377, 39.156656],
          [-78.439429, 39.132146],
          [-78.459869, 39.113351],
          [-78.508132, 39.08863]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54033",
      state: "54",
      county: "033",
      name: "Harrison County",
      censusArea: 416.008,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.544521, 39.42912],
          [-80.515598, 39.43774],
          [-80.512663, 39.458612],
          [-80.493991, 39.461148],
          [-80.494085, 39.469599],
          [-80.293693, 39.434312],
          [-80.197286, 39.393186],
          [-80.166387, 39.242283],
          [-80.224687, 39.170977],
          [-80.227173, 39.112792],
          [-80.240394, 39.113145],
          [-80.248771, 39.100809],
          [-80.300209, 39.103857],
          [-80.596126, 39.167069],
          [-80.525709, 39.209429],
          [-80.603684, 39.291041],
          [-80.531808, 39.360121],
          [-80.544521, 39.42912]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54037",
      state: "54",
      county: "037",
      name: "Jefferson County",
      censusArea: 209.635,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.807821, 39.490241],
          [-77.765442, 39.428574],
          [-77.739204, 39.38573],
          [-77.760435, 39.344171],
          [-77.760586, 39.338864],
          [-77.755789, 39.333899],
          [-77.727379, 39.321666],
          [-77.719029, 39.321125],
          [-77.750267, 39.289284],
          [-77.753357, 39.280331],
          [-77.75306, 39.277971],
          [-77.753105, 39.27734],
          [-77.755193, 39.275191],
          [-77.755698, 39.274575],
          [-77.758412, 39.269197],
          [-77.758733, 39.268114],
          [-77.761217, 39.263721],
          [-77.761768, 39.263031],
          [-77.768, 39.257657],
          [-77.768992, 39.256417],
          [-77.767277, 39.24938],
          [-77.771415, 39.236776],
          [-77.822182, 39.139985],
          [-77.828157, 39.132329],
          [-78.032841, 39.264403],
          [-78.033183, 39.264624],
          [-78.033185, 39.264626],
          [-77.966953, 39.367164],
          [-77.825411, 39.494036],
          [-77.807821, 39.490241]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54045",
      state: "54",
      county: "045",
      name: "Logan County",
      censusArea: 453.737,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.802937, 37.660508],
          [-81.930238, 37.682011],
          [-81.991744, 37.642707],
          [-82.082648, 37.701565],
          [-82.112411, 37.854039],
          [-82.190658, 37.974778],
          [-81.932507, 38.025356],
          [-81.979706, 37.99136],
          [-81.785641, 37.936404],
          [-81.722136, 37.809507],
          [-81.607532, 37.788709],
          [-81.764737, 37.752108],
          [-81.701734, 37.701409],
          [-81.802937, 37.660508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54051",
      state: "54",
      county: "051",
      name: "Marshall County",
      censusArea: 305.431,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.828618, 39.720848],
          [-80.812342, 39.848845],
          [-80.806179, 39.897306],
          [-80.809619, 39.905626],
          [-80.808722, 39.91257],
          [-80.806018, 39.91713],
          [-80.759455, 39.958233],
          [-80.743166, 39.969113],
          [-80.740126, 39.970793],
          [-80.732051, 40.03324],
          [-80.51912, 40.01641],
          [-80.519207, 39.963381],
          [-80.519218, 39.962424],
          [-80.519217, 39.962199],
          [-80.519203, 39.959394],
          [-80.519175, 39.956648],
          [-80.519115, 39.939188],
          [-80.519248, 39.936967],
          [-80.519342, 39.721403],
          [-80.828618, 39.720848]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54053",
      state: "54",
      county: "053",
      name: "Mason County",
      censusArea: 430.748,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.772843, 38.680843],
          [-81.955791, 38.657355],
          [-82.064562, 38.607985],
          [-82.055127, 38.474547],
          [-82.218967, 38.591683],
          [-82.193824, 38.593096],
          [-82.181967, 38.599384],
          [-82.177267, 38.603784],
          [-82.175167, 38.608484],
          [-82.172066, 38.619284],
          [-82.172066, 38.625984],
          [-82.182467, 38.708782],
          [-82.188268, 38.734082],
          [-82.198882, 38.757725],
          [-82.220449, 38.773739],
          [-82.221566, 38.787187],
          [-82.215759, 38.797462],
          [-82.191172, 38.815137],
          [-82.184428, 38.816529],
          [-82.139279, 38.866407],
          [-82.098762, 38.958288],
          [-82.093165, 38.97098],
          [-82.051563, 38.994378],
          [-82.045663, 39.003778],
          [-82.041563, 39.017878],
          [-82.035963, 39.025478],
          [-82.017562, 39.030078],
          [-82.002261, 39.027878],
          [-81.935619, 38.9895],
          [-81.919783, 38.968494],
          [-81.900595, 38.937671],
          [-81.89847, 38.929603],
          [-81.90091, 38.924338],
          [-81.926671, 38.901311],
          [-81.928352, 38.895371],
          [-81.926967, 38.891602],
          [-81.908645, 38.87846],
          [-81.772843, 38.680843]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54059",
      state: "54",
      county: "059",
      name: "Mingo County",
      censusArea: 423.109,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.802937, 37.660508],
          [-81.855939, 37.54891],
          [-81.865239, 37.54361],
          [-81.869439, 37.53781],
          [-81.882936, 37.53821],
          [-81.88605, 37.536744],
          [-81.889236, 37.528387],
          [-81.897951, 37.524395],
          [-81.909374, 37.527041],
          [-81.912826, 37.52041],
          [-81.922628, 37.511914],
          [-81.92787, 37.512118],
          [-81.968297, 37.537798],
          [-81.998177, 37.543082],
          [-81.999844, 37.542579],
          [-82.008274, 37.533357],
          [-82.048521, 37.531078],
          [-82.116584, 37.559588],
          [-82.125892, 37.574031],
          [-82.125601, 37.579021],
          [-82.141814, 37.595052],
          [-82.21967, 37.643046],
          [-82.25028, 37.657776],
          [-82.297011, 37.687273],
          [-82.307235, 37.707669],
          [-82.325739, 37.735799],
          [-82.326404, 37.740815],
          [-82.319023, 37.758892],
          [-82.329867, 37.775897],
          [-82.400234, 37.851833],
          [-82.306391, 37.94364],
          [-82.190658, 37.974778],
          [-82.112411, 37.854039],
          [-82.082648, 37.701565],
          [-81.991744, 37.642707],
          [-81.930238, 37.682011],
          [-81.802937, 37.660508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54065",
      state: "54",
      county: "065",
      name: "Morgan County",
      censusArea: 229.073,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.347087, 39.466012],
          [-78.468639, 39.516789],
          [-78.436939, 39.538959],
          [-78.432033, 39.561053],
          [-78.450207, 39.570889],
          [-78.454376, 39.574319],
          [-78.458456, 39.581158],
          [-78.457187, 39.587379],
          [-78.43025, 39.62329],
          [-78.355218, 39.640576],
          [-78.334044, 39.63577],
          [-78.313033, 39.631001],
          [-78.283039, 39.62047],
          [-78.271122, 39.619642],
          [-78.224337, 39.66327],
          [-78.191107, 39.690262],
          [-78.182759, 39.69511],
          [-78.176625, 39.695967],
          [-78.143478, 39.690412],
          [-78.107834, 39.682137],
          [-78.074595, 39.666686],
          [-78.035992, 39.63572],
          [-78.023427, 39.61986],
          [-78.138937, 39.593796],
          [-78.228766, 39.391233],
          [-78.347087, 39.466012]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54069",
      state: "54",
      county: "069",
      name: "Ohio County",
      censusArea: 105.82,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.705482, 40.153047],
          [-80.703052, 40.157231],
          [-80.685391, 40.187642],
          [-80.519104, 40.159672],
          [-80.51896, 40.078089],
          [-80.519008, 40.077001],
          [-80.51912, 40.01641],
          [-80.732051, 40.03324],
          [-80.731504, 40.037472],
          [-80.730904, 40.049172],
          [-80.733104, 40.058772],
          [-80.726504, 40.089072],
          [-80.705482, 40.153047]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54075",
      state: "54",
      county: "075",
      name: "Pocahontas County",
      censusArea: 940.284,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.245518, 38.388457],
          [-80.11692, 38.473953],
          [-80.029208, 38.459184],
          [-79.86325, 38.55082],
          [-79.781783, 38.736911],
          [-79.73918, 38.679613],
          [-79.626774, 38.664214],
          [-79.649075, 38.591515],
          [-79.669128, 38.510975],
          [-79.669128, 38.510883],
          [-79.695565, 38.477842],
          [-79.699006, 38.475148],
          [-79.688205, 38.450476],
          [-79.688962, 38.449538],
          [-79.689544, 38.442511],
          [-79.691377, 38.439558],
          [-79.731661, 38.374143],
          [-79.808711, 38.309429],
          [-79.810115, 38.305037],
          [-79.788945, 38.268703],
          [-79.790134, 38.267654],
          [-79.794568, 38.264856],
          [-79.891999, 38.203378],
          [-79.892345, 38.202397],
          [-79.916174, 38.184386],
          [-79.917061, 38.183741],
          [-79.921196, 38.180378],
          [-79.921026, 38.179954],
          [-79.925251, 38.150465],
          [-79.925512, 38.150237],
          [-79.928683, 38.144928],
          [-79.928747, 38.144436],
          [-79.933751, 38.135508],
          [-79.938394, 38.130563],
          [-79.931034, 38.101402],
          [-79.933911, 38.099168],
          [-79.959844, 38.063697],
          [-80.169169, 38.036111],
          [-80.264653, 38.046616],
          [-80.363295, 38.114331],
          [-80.360048, 38.225845],
          [-80.352171, 38.345337],
          [-80.245518, 38.388457]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48341",
      state: "48",
      county: "341",
      name: "Moore County",
      censusArea: 899.691,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.622831, 35.624057],
          [-101.6228, 35.620196],
          [-102.162752, 35.620035],
          [-102.162808, 35.625044],
          [-102.162809, 35.627515],
          [-102.163015, 36.055249],
          [-101.623466, 36.055405],
          [-101.622831, 35.624057]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54083",
      state: "54",
      county: "083",
      name: "Randolph County",
      censusArea: 1039.681,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.825092, 39.115803],
          [-79.785685, 39.037404],
          [-79.508765, 38.975173],
          [-79.357666, 38.964509],
          [-79.349566, 38.958709],
          [-79.349867, 38.957509],
          [-79.455472, 38.92991],
          [-79.539075, 38.841411],
          [-79.510073, 38.780712],
          [-79.626774, 38.664214],
          [-79.73918, 38.679613],
          [-79.781783, 38.736911],
          [-79.86325, 38.55082],
          [-80.029208, 38.459184],
          [-80.11692, 38.473953],
          [-80.245518, 38.388457],
          [-80.183931, 38.525386],
          [-80.280059, 38.694867],
          [-80.236831, 38.743765],
          [-80.126105, 38.745836],
          [-80.085062, 38.947236],
          [-79.896094, 38.973404],
          [-79.825092, 39.115803]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54087",
      state: "54",
      county: "087",
      name: "Roane County",
      censusArea: 483.563,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.522166, 38.612746],
          [-81.546631, 38.672387],
          [-81.502628, 38.917922],
          [-81.448148, 38.941645],
          [-81.343911, 38.895633],
          [-81.278412, 38.914867],
          [-81.171819, 38.739805],
          [-81.158837, 38.644494],
          [-81.08371, 38.611982],
          [-81.130775, 38.565948],
          [-81.194113, 38.527634],
          [-81.465372, 38.549898],
          [-81.522166, 38.612746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54091",
      state: "54",
      county: "091",
      name: "Taylor County",
      censusArea: 172.771,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.938878, 39.452465],
          [-79.894688, 39.435588],
          [-79.895536, 39.299584],
          [-80.023411, 39.247892],
          [-80.166387, 39.242283],
          [-80.197286, 39.393186],
          [-80.086785, 39.386463],
          [-79.938878, 39.452465]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54093",
      state: "54",
      county: "093",
      name: "Tucker County",
      censusArea: 418.922,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.809729, 39.230526],
          [-79.687283, 39.271398],
          [-79.487175, 39.194906],
          [-79.289264, 39.189706],
          [-79.355468, 39.086508],
          [-79.298164, 39.07321],
          [-79.357666, 38.964509],
          [-79.508765, 38.975173],
          [-79.785685, 39.037404],
          [-79.825092, 39.115803],
          [-79.809729, 39.230526]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54097",
      state: "54",
      county: "097",
      name: "Upshur County",
      censusArea: 354.638,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.300209, 39.103857],
          [-80.248771, 39.100809],
          [-80.240394, 39.113145],
          [-80.227173, 39.112792],
          [-80.128198, 39.037795],
          [-80.05582, 39.03716],
          [-80.085062, 38.947236],
          [-80.126105, 38.745836],
          [-80.236831, 38.743765],
          [-80.280059, 38.694867],
          [-80.393063, 38.727571],
          [-80.386989, 38.874808],
          [-80.317486, 38.958647],
          [-80.300209, 39.103857]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54099",
      state: "54",
      county: "099",
      name: "Wayne County",
      censusArea: 505.981,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.264849, 38.229199],
          [-82.283316, 38.141936],
          [-82.203865, 38.023606],
          [-82.306391, 37.94364],
          [-82.400234, 37.851833],
          [-82.417679, 37.870658],
          [-82.474666, 37.910388],
          [-82.500386, 37.936518],
          [-82.497358, 37.945338],
          [-82.4973, 37.945507],
          [-82.464987, 37.976859],
          [-82.464096, 37.982437],
          [-82.465473, 37.98478],
          [-82.487732, 37.99833],
          [-82.509812, 38.001249],
          [-82.519665, 38.008538],
          [-82.539139, 38.039093],
          [-82.547284, 38.061094],
          [-82.600127, 38.117389],
          [-82.602618, 38.11835],
          [-82.621164, 38.123239],
          [-82.636466, 38.13786],
          [-82.644739, 38.165487],
          [-82.60423, 38.247303],
          [-82.59497, 38.245453],
          [-82.584001, 38.246371],
          [-82.578254, 38.254809],
          [-82.574656, 38.263873],
          [-82.571877, 38.315781],
          [-82.593008, 38.375082],
          [-82.595382, 38.382712],
          [-82.595369, 38.382722],
          [-82.597113, 38.412881],
          [-82.596281, 38.417681],
          [-82.593673, 38.421809],
          [-82.560664, 38.404338],
          [-82.520178, 38.407582],
          [-82.50667, 38.410092],
          [-82.343911, 38.305209],
          [-82.297858, 38.327443],
          [-82.264849, 38.229199]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54103",
      state: "54",
      county: "103",
      name: "Wetzel County",
      censusArea: 358.063,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.494085, 39.469599],
          [-80.493991, 39.461148],
          [-80.512663, 39.458612],
          [-80.515598, 39.43774],
          [-80.544521, 39.42912],
          [-80.618629, 39.450375],
          [-80.720451, 39.481237],
          [-80.852182, 39.602048],
          [-80.943782, 39.606926],
          [-80.892208, 39.616756],
          [-80.88036, 39.620706],
          [-80.831871, 39.705655],
          [-80.829764, 39.711839],
          [-80.828618, 39.720848],
          [-80.519342, 39.721403],
          [-80.421388, 39.721189],
          [-80.39581, 39.637347],
          [-80.498163, 39.563327],
          [-80.494085, 39.469599]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54105",
      state: "54",
      county: "105",
      name: "Wirt County",
      censusArea: 232.509,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.502628, 38.917922],
          [-81.581447, 39.026179],
          [-81.424906, 39.135679],
          [-81.298017, 39.185572],
          [-81.328786, 39.152584],
          [-81.250252, 39.035074],
          [-81.162455, 39.030608],
          [-81.278412, 38.914867],
          [-81.343911, 38.895633],
          [-81.448148, 38.941645],
          [-81.502628, 38.917922]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54107",
      state: "54",
      county: "107",
      name: "Wood County",
      censusArea: 366.26,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.747253, 39.095379],
          [-81.749689, 39.180742],
          [-81.749853, 39.186489],
          [-81.740061, 39.190652],
          [-81.721551, 39.212448],
          [-81.695724, 39.242859],
          [-81.605816, 39.275851],
          [-81.542346, 39.352874],
          [-81.467744, 39.403774],
          [-81.456143, 39.409274],
          [-81.446543, 39.410374],
          [-81.435642, 39.408474],
          [-81.412706, 39.394618],
          [-81.406689, 39.38809],
          [-81.393794, 39.351706],
          [-81.384556, 39.343449],
          [-81.375961, 39.341697],
          [-81.371271, 39.342062],
          [-81.239477, 39.268328],
          [-81.298017, 39.185572],
          [-81.424906, 39.135679],
          [-81.581447, 39.026179],
          [-81.747253, 39.095379]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55011",
      state: "55",
      county: "011",
      name: "Buffalo County",
      censusArea: 671.635,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.529101, 44.596197],
          [-91.529741, 44.246777],
          [-91.612994, 44.172606],
          [-91.534229, 44.096046],
          [-91.559197, 44.023959],
          [-91.580019, 44.026925],
          [-91.59207, 44.031372],
          [-91.67252, 44.091286],
          [-91.721552, 44.130342],
          [-91.740871, 44.133918],
          [-91.808064, 44.159262],
          [-91.862858, 44.193001],
          [-91.875158, 44.200575],
          [-91.892698, 44.231105],
          [-91.892963, 44.235149],
          [-91.887905, 44.246398],
          [-91.887189, 44.252513],
          [-91.918625, 44.322671],
          [-91.92559, 44.333548],
          [-91.9636, 44.362112],
          [-91.972493, 44.36676],
          [-92.046285, 44.394398],
          [-92.083925, 44.406211],
          [-92.02918, 44.586292],
          [-91.650248, 44.596649],
          [-91.529101, 44.596197]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55013",
      state: "55",
      county: "013",
      name: "Burnett County",
      censusArea: 821.848,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.885661, 45.644126],
          [-92.883987, 45.65487],
          [-92.869193, 45.717568],
          [-92.863703, 45.72182],
          [-92.841051, 45.730024],
          [-92.803971, 45.749805],
          [-92.784621, 45.764196],
          [-92.76843, 45.79801],
          [-92.76249, 45.819239],
          [-92.734039, 45.868108],
          [-92.721128, 45.883805],
          [-92.707702, 45.894901],
          [-92.656125, 45.924442],
          [-92.443285, 46.014648],
          [-92.326877, 46.066618],
          [-92.306756, 46.07241],
          [-92.294033, 46.074377],
          [-92.294069, 46.078346],
          [-92.29353, 46.113824],
          [-92.293706, 46.157321],
          [-92.049636, 46.157597],
          [-92.033404, 45.98387],
          [-92.031417, 45.639928],
          [-92.154888, 45.639742],
          [-92.154443, 45.725616],
          [-92.528198, 45.72868],
          [-92.529111, 45.642171],
          [-92.885661, 45.644126]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55019",
      state: "55",
      county: "019",
      name: "Clark County",
      censusArea: 1209.815,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.315919, 44.844187],
          [-90.31618, 44.840775],
          [-90.316253, 44.685154],
          [-90.31605, 44.424673],
          [-90.801904, 44.422347],
          [-90.801852, 44.509573],
          [-90.923104, 44.509838],
          [-90.92235, 44.596293],
          [-90.922247, 44.857309],
          [-90.922669, 44.944459],
          [-90.922395, 44.954816],
          [-90.923393, 44.99552],
          [-90.922838, 44.999966],
          [-90.923362, 45.031143],
          [-90.315038, 45.033829],
          [-90.315919, 44.844187]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55021",
      state: "55",
      county: "021",
      name: "Columbia County",
      censusArea: 765.529,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.720295, 43.292928],
          [-89.727171, 43.301305],
          [-89.716768, 43.321806],
          [-89.600719, 43.380006],
          [-89.599357, 43.558041],
          [-89.732238, 43.571826],
          [-89.785809, 43.641049],
          [-89.599985, 43.642596],
          [-89.245437, 43.643083],
          [-89.006848, 43.633045],
          [-89.009139, 43.28483],
          [-89.720295, 43.292928]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55023",
      state: "55",
      county: "023",
      name: "Crawford County",
      censusArea: 570.66,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.666786, 43.171778],
          [-90.845746, 43.077625],
          [-91.06527, 42.992074],
          [-91.163064, 42.986781],
          [-91.175167, 43.041267],
          [-91.178087, 43.062044],
          [-91.177222, 43.080247],
          [-91.178251, 43.124982],
          [-91.175253, 43.134665],
          [-91.1562, 43.142945],
          [-91.1462, 43.152405],
          [-91.062562, 43.243165],
          [-91.057918, 43.255366],
          [-91.085652, 43.29187],
          [-91.107237, 43.313645],
          [-91.210233, 43.372064],
          [-91.20555, 43.422949],
          [-90.668561, 43.422994],
          [-90.666786, 43.171778]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53029",
      state: "53",
      county: "029",
      name: "Island County",
      censusArea: 208.449,
      stateName: "Washington"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-122.397349, 47.912401],
            [-122.419274, 47.912125],
            [-122.431035, 47.914732],
            [-122.445519, 47.930226],
            [-122.445759, 47.93619],
            [-122.44079, 47.946594],
            [-122.44076, 47.951845],
            [-122.446682, 47.963155],
            [-122.47266, 47.988449],
            [-122.487505, 47.990729],
            [-122.501257, 47.987089],
            [-122.514813, 47.981152],
            [-122.51778, 47.974916],
            [-122.521219, 47.972997],
            [-122.546824, 47.967215],
            [-122.552053, 47.973644],
            [-122.551032, 47.977346],
            [-122.543063, 47.985983],
            [-122.541564, 47.992998],
            [-122.542924, 47.996404],
            [-122.560018, 48.006502],
            [-122.58178, 48.010386],
            [-122.607342, 48.030992],
            [-122.596786, 48.038834],
            [-122.593621, 48.0472],
            [-122.594922, 48.056318],
            [-122.614028, 48.072788],
            [-122.613217, 48.079485],
            [-122.607291, 48.088034],
            [-122.598301, 48.110616],
            [-122.602109, 48.135249],
            [-122.609568, 48.15186],
            [-122.617464, 48.159055],
            [-122.633167, 48.163281],
            [-122.65602, 48.162513],
            [-122.671235, 48.157312],
            [-122.677337, 48.154587],
            [-122.679556, 48.155113],
            [-122.686626, 48.174653],
            [-122.693084, 48.181509],
            [-122.711508, 48.193573],
            [-122.73503, 48.199964],
            [-122.744612, 48.20965],
            [-122.763042, 48.215342],
            [-122.770045, 48.224395],
            [-122.769939, 48.227548],
            [-122.752563, 48.260061],
            [-122.732022, 48.279425],
            [-122.72259, 48.304268],
            [-122.707077, 48.315286],
            [-122.673731, 48.354683],
            [-122.664928, 48.374823],
            [-122.664659, 48.401508],
            [-122.644798, 48.405488],
            [-122.634991, 48.404244],
            [-122.632643, 48.401068],
            [-122.634024, 48.398858],
            [-122.637339, 48.398029],
            [-122.637892, 48.395681],
            [-122.63582, 48.395128],
            [-122.627809, 48.3972],
            [-122.617174, 48.407145],
            [-122.609715, 48.411565],
            [-122.60198, 48.409907],
            [-122.596732, 48.405626],
            [-122.595351, 48.3972],
            [-122.585038, 48.395166],
            [-122.588891, 48.363005],
            [-122.585162, 48.353304],
            [-122.565525, 48.348217],
            [-122.551334, 48.342138],
            [-122.515979, 48.320419],
            [-122.506568, 48.310041],
            [-122.504729, 48.300373],
            [-122.505828, 48.297677],
            [-122.519535, 48.288314],
            [-122.522756, 48.285504],
            [-122.530976, 48.282445],
            [-122.551793, 48.281512],
            [-122.558332, 48.282061],
            [-122.574872, 48.294903],
            [-122.584086, 48.297987],
            [-122.599532, 48.298303],
            [-122.618466, 48.294159],
            [-122.626757, 48.288991],
            [-122.620748, 48.282961],
            [-122.623779, 48.269431],
            [-122.652639, 48.265081],
            [-122.65343, 48.25934],
            [-122.66921, 48.240614],
            [-122.668385, 48.223967],
            [-122.63126, 48.220686],
            [-122.628352, 48.222467],
            [-122.606406, 48.208262],
            [-122.588138, 48.18594],
            [-122.585778, 48.182352],
            [-122.582595, 48.170424],
            [-122.574905, 48.155593],
            [-122.567936, 48.148624],
            [-122.558205, 48.119579],
            [-122.559911, 48.114186],
            [-122.571615, 48.105113],
            [-122.571853, 48.102143],
            [-122.554559, 48.077392],
            [-122.54512, 48.05255],
            [-122.538953, 48.050232],
            [-122.516314, 48.057181],
            [-122.513994, 48.059077],
            [-122.511081, 48.075301],
            [-122.516906, 48.081085],
            [-122.525422, 48.096537],
            [-122.513276, 48.097538],
            [-122.491104, 48.094242],
            [-122.461606, 48.068501],
            [-122.448419, 48.054323],
            [-122.431266, 48.045001],
            [-122.400628, 48.036563],
            [-122.387382, 48.03403],
            [-122.376259, 48.034457],
            [-122.373263, 48.000791],
            [-122.369161, 47.995295],
            [-122.353611, 47.981433],
            [-122.350254, 47.969355],
            [-122.349597, 47.958796],
            [-122.350741, 47.953235],
            [-122.358812, 47.93742],
            [-122.367876, 47.932415],
            [-122.376837, 47.923703],
            [-122.37578, 47.910252],
            [-122.3773, 47.905941],
            [-122.380497, 47.904023],
            [-122.39042, 47.905696],
            [-122.397349, 47.912401]
          ]
        ],
        [
          [
            [-122.405757, 48.252193],
            [-122.398712, 48.241684],
            [-122.392851, 48.238555],
            [-122.395499, 48.228551],
            [-122.396121, 48.229233],
            [-122.425572, 48.232887],
            [-122.430578, 48.236237],
            [-122.433767, 48.23655],
            [-122.449605, 48.232598],
            [-122.45371, 48.228859],
            [-122.453618, 48.22683],
            [-122.449513, 48.214736],
            [-122.444508, 48.214522],
            [-122.441731, 48.211776],
            [-122.442051, 48.20935],
            [-122.45493, 48.196639],
            [-122.461888, 48.193137],
            [-122.464801, 48.194767],
            [-122.47025, 48.194007],
            [-122.478535, 48.188087],
            [-122.479008, 48.175703],
            [-122.475803, 48.166792],
            [-122.442383, 48.130841],
            [-122.411649, 48.11321],
            [-122.379481, 48.087384],
            [-122.360345, 48.061527],
            [-122.358375, 48.056133],
            [-122.363107, 48.054546],
            [-122.377114, 48.057568],
            [-122.38769, 48.065189],
            [-122.390787, 48.069477],
            [-122.393413, 48.078472],
            [-122.400692, 48.085255],
            [-122.423703, 48.102941],
            [-122.44966, 48.114041],
            [-122.4675, 48.130353],
            [-122.477983, 48.129048],
            [-122.486736, 48.12095],
            [-122.489986, 48.120617],
            [-122.512031, 48.133931],
            [-122.522576, 48.161712],
            [-122.53722, 48.183745],
            [-122.538916, 48.209683],
            [-122.534431, 48.223005],
            [-122.535209, 48.240213],
            [-122.530996, 48.249821],
            [-122.503786, 48.257045],
            [-122.499648, 48.256611],
            [-122.497727, 48.253389],
            [-122.493448, 48.252043],
            [-122.480925, 48.251706],
            [-122.474494, 48.255227],
            [-122.466803, 48.269604],
            [-122.463962, 48.270541],
            [-122.406516, 48.25183],
            [-122.405757, 48.252193]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49039",
      state: "49",
      county: "039",
      name: "Sanpete County",
      censusArea: 1590.152,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.014198, 39.045355],
          [-112.015677, 39.314455],
          [-111.922462, 39.381452],
          [-111.747075, 39.38283],
          [-111.713224, 39.460107],
          [-111.71862, 39.694623],
          [-111.585068, 39.740135],
          [-111.640578, 39.813017],
          [-111.247502, 39.813269],
          [-111.247388, 39.704611],
          [-111.247275, 39.467152],
          [-111.301222, 39.467302],
          [-111.29936, 39.032264],
          [-111.853462, 39.032981],
          [-112.014198, 39.045355]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49043",
      state: "49",
      county: "043",
      name: "Summit County",
      censusArea: 1871.712,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.046551, 41.251716],
          [-111.046723, 40.997959],
          [-110.500718, 40.994746],
          [-110.048474, 40.997103],
          [-110.000708, 40.997352],
          [-110.000709, 40.81375],
          [-110.294317, 40.834228],
          [-110.375002, 40.788211],
          [-110.89309, 40.72733],
          [-110.901931, 40.67817],
          [-111.024396, 40.577504],
          [-111.15136, 40.548352],
          [-111.374868, 40.632703],
          [-111.393697, 40.690384],
          [-111.489582, 40.599157],
          [-111.553105, 40.609362],
          [-111.641022, 40.799084],
          [-111.519624, 40.791758],
          [-111.479647, 40.858366],
          [-111.593025, 40.993044],
          [-111.508646, 41.062763],
          [-111.26482, 41.144253],
          [-111.046551, 41.251716]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49045",
      state: "49",
      county: "045",
      name: "Tooele County",
      censusArea: 6941.352,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.222654, 40.72214],
          [-112.175465, 40.604476],
          [-112.173208, 40.466923],
          [-112.212216, 40.459233],
          [-112.179558, 40.232069],
          [-112.145163, 40.171239],
          [-112.179729, 40.011569],
          [-112.269882, 39.938947],
          [-112.331783, 39.952747],
          [-112.341883, 39.904247],
          [-114.047134, 39.906037],
          [-114.046555, 40.116931],
          [-114.045518, 40.494474],
          [-114.045577, 40.495801],
          [-114.045281, 40.506586],
          [-114.043505, 40.726292],
          [-114.043831, 40.758666],
          [-114.043803, 40.759205],
          [-114.042145, 40.999926],
          [-112.799307, 41.000236],
          [-112.493393, 41.076735],
          [-112.26351, 40.77345],
          [-112.22736, 40.72518],
          [-112.222654, 40.72214]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49049",
      state: "49",
      county: "049",
      name: "Utah County",
      censusArea: 2003.454,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.913848, 40.452454],
          [-111.59393, 40.577071],
          [-111.611734, 40.431466],
          [-111.555235, 40.362696],
          [-111.480337, 40.362169],
          [-111.426482, 40.286906],
          [-111.31483, 40.287566],
          [-111.278846, 40.258671],
          [-111.225507, 40.167324],
          [-111.246207, 40.047466],
          [-111.205772, 40.047368],
          [-111.083061, 39.943198],
          [-111.082455, 39.900155],
          [-110.891872, 39.899146],
          [-110.857321, 39.898941],
          [-110.857902, 39.812428],
          [-111.247502, 39.813269],
          [-111.640578, 39.813017],
          [-111.766193, 39.810601],
          [-111.734178, 39.853895],
          [-111.794837, 39.931159],
          [-111.909411, 39.894519],
          [-111.972, 39.806657],
          [-112.07135, 39.777942],
          [-112.053359, 39.893194],
          [-112.179729, 40.011569],
          [-112.145163, 40.171239],
          [-112.179558, 40.232069],
          [-112.212216, 40.459233],
          [-112.173208, 40.466923],
          [-111.978236, 40.41727],
          [-111.931555, 40.433548],
          [-111.91912, 40.443191],
          [-111.913848, 40.452454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49057",
      state: "49",
      county: "057",
      name: "Weber County",
      censusArea: 576.084,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.885476, 41.426435],
          [-111.72651, 41.375814],
          [-111.666576, 41.42883],
          [-111.510839, 41.4231],
          [-111.42056, 41.36137],
          [-111.49663, 41.226877],
          [-111.595856, 41.239139],
          [-111.67819, 41.181028],
          [-111.858392, 41.197383],
          [-111.856305, 41.139118],
          [-112.189578, 41.152741],
          [-112.493393, 41.076735],
          [-112.237877, 41.336739],
          [-112.028376, 41.336439],
          [-111.9591, 41.433706],
          [-111.885476, 41.426435]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50003",
      state: "50",
      county: "003",
      name: "Bennington County",
      censusArea: 674.98,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.274294, 42.943652],
          [-73.269472, 43.030686],
          [-73.259159, 43.216848],
          [-73.254848, 43.314684],
          [-72.867902, 43.300742],
          [-72.8199, 43.29921],
          [-72.819368, 43.255498],
          [-72.868737, 43.11317],
          [-72.9969, 43.119095],
          [-73.002789, 43.023007],
          [-73.008063, 42.930087],
          [-72.921376, 42.926792],
          [-72.92482, 42.856164],
          [-73.015111, 42.832074],
          [-73.018646, 42.741025],
          [-73.022903, 42.741133],
          [-73.142487, 42.743508],
          [-73.264957, 42.74594],
          [-73.276421, 42.746019],
          [-73.290944, 42.80192],
          [-73.285388, 42.834093],
          [-73.278673, 42.83341],
          [-73.274466, 42.940361],
          [-73.274393, 42.942482],
          [-73.274294, 42.943652]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50007",
      state: "50",
      county: "007",
      name: "Chittenden County",
      censusArea: 536.578,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.924112, 44.633051],
          [-72.809349, 44.547921],
          [-72.804995, 44.451676],
          [-72.826112, 44.35919],
          [-72.855592, 44.365551],
          [-72.911398, 44.281769],
          [-72.89598, 44.276284],
          [-72.952167, 44.161271],
          [-72.973318, 44.29342],
          [-73.323268, 44.264796],
          [-73.315016, 44.388513],
          [-73.296031, 44.428339],
          [-73.293613, 44.438903],
          [-73.299885, 44.476652],
          [-73.306707, 44.500334],
          [-73.33863, 44.546844],
          [-73.338634, 44.546847],
          [-73.356788, 44.557918],
          [-73.361952, 44.563064],
          [-73.233209, 44.649791],
          [-73.228854, 44.722866],
          [-73.038028, 44.651157],
          [-72.924112, 44.633051]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50011",
      state: "50",
      county: "011",
      name: "Franklin County",
      censusArea: 633.706,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.578161, 44.781578],
          [-72.74736, 44.78617],
          [-72.786134, 44.727723],
          [-72.793958, 44.699397],
          [-72.795505, 44.6922],
          [-72.851068, 44.71107],
          [-72.924112, 44.633051],
          [-73.038028, 44.651157],
          [-73.228854, 44.722866],
          [-73.256321, 44.759444],
          [-73.19402, 44.886844],
          [-73.19131, 44.904762],
          [-73.226274, 44.919505],
          [-73.230777, 44.927437],
          [-73.191948, 45.013576],
          [-73.085972, 45.015494],
          [-73.015539, 45.015072],
          [-73.014766, 45.01498],
          [-72.845633, 45.016659],
          [-72.67477, 45.015459],
          [-72.554253, 45.011794],
          [-72.532638, 44.833346],
          [-72.578161, 44.781578]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50015",
      state: "50",
      county: "015",
      name: "Lamoille County",
      censusArea: 458.797,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.786134, 44.727723],
          [-72.74736, 44.78617],
          [-72.578161, 44.781578],
          [-72.417315, 44.722893],
          [-72.482302, 44.633928],
          [-72.374786, 44.584125],
          [-72.434315, 44.506098],
          [-72.490548, 44.425693],
          [-72.596807, 44.479292],
          [-72.654398, 44.398224],
          [-72.765856, 44.442595],
          [-72.804995, 44.451676],
          [-72.809349, 44.547921],
          [-72.924112, 44.633051],
          [-72.851068, 44.71107],
          [-72.795505, 44.6922],
          [-72.793958, 44.699397],
          [-72.786134, 44.727723]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50021",
      state: "50",
      county: "021",
      name: "Rutland County",
      censusArea: 929.821,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.771543, 43.477862],
          [-72.727184, 43.468717],
          [-72.759799, 43.354144],
          [-72.853439, 43.371076],
          [-72.867902, 43.300742],
          [-73.254848, 43.314684],
          [-73.253084, 43.354714],
          [-73.252832, 43.363493],
          [-73.252674, 43.370285],
          [-73.252582, 43.370997],
          [-73.247061, 43.514919],
          [-73.242042, 43.534925],
          [-73.245594, 43.540253],
          [-73.302552, 43.625708],
          [-73.306234, 43.628018],
          [-73.366537, 43.623462],
          [-73.375594, 43.61335],
          [-73.400295, 43.568889],
          [-73.428636, 43.583994],
          [-73.431229, 43.588285],
          [-73.421606, 43.646577],
          [-73.408697, 43.67402],
          [-73.403517, 43.685032],
          [-73.370724, 43.735571],
          [-73.360711, 43.753268],
          [-73.231377, 43.764469],
          [-73.155743, 43.83966],
          [-73.156173, 43.842314],
          [-73.029938, 43.846716],
          [-73.032082, 43.844718],
          [-73.026833, 43.842113],
          [-73.016831, 43.804953],
          [-72.989434, 43.814611],
          [-72.958853, 43.826542],
          [-72.874552, 43.858131],
          [-72.837188, 43.719878],
          [-72.700945, 43.682831],
          [-72.79107, 43.495],
          [-72.771543, 43.477862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56023",
      state: "56",
      county: "023",
      name: "Lincoln County",
      censusArea: 4076.129,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.578656, 43.235224],
          [-110.587993, 43.126147],
          [-110.587662, 42.779188],
          [-110.623477, 42.710181],
          [-110.590074, 42.710087],
          [-110.541634, 42.277491],
          [-110.053708, 42.270744],
          [-110.048, 41.578015],
          [-110.062787, 41.577519],
          [-111.046402, 41.579845],
          [-111.046689, 42.001567],
          [-111.047109, 42.142497],
          [-111.047107, 42.148971],
          [-111.047058, 42.182672],
          [-111.047097, 42.194773],
          [-111.047074, 42.280787],
          [-111.04708, 42.34942],
          [-111.046249, 42.513116],
          [-111.043959, 42.96445],
          [-111.043957, 42.969482],
          [-111.043924, 42.975063],
          [-111.044129, 43.018702],
          [-111.044156, 43.020052],
          [-111.044206, 43.022614],
          [-111.044034, 43.024581],
          [-111.044034, 43.024844],
          [-111.044033, 43.026411],
          [-111.044094, 43.02927],
          [-111.043997, 43.041415],
          [-111.044058, 43.04464],
          [-111.044063, 43.046302],
          [-111.044086, 43.054819],
          [-111.044117, 43.060309],
          [-111.04415, 43.066172],
          [-111.044162, 43.068222],
          [-111.044143, 43.072364],
          [-111.044235, 43.177121],
          [-111.044266, 43.177236],
          [-111.044232, 43.18444],
          [-111.044168, 43.189244],
          [-111.044229, 43.195579],
          [-111.044617, 43.31572],
          [-110.813988, 43.315906],
          [-110.813993, 43.235178],
          [-110.578656, 43.235224]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51005",
      state: "51",
      county: "005",
      name: "Alleghany County",
      censusArea: 445.457,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.220984, 37.627767],
          [-80.239288, 37.637672],
          [-80.254431, 37.642352],
          [-80.254469, 37.642333],
          [-80.263281, 37.645082],
          [-80.263291, 37.645101],
          [-80.26483, 37.645526],
          [-80.264874, 37.645511],
          [-80.267228, 37.646011],
          [-80.267455, 37.646108],
          [-80.270352, 37.648929],
          [-80.270323, 37.648982],
          [-80.292258, 37.683732],
          [-80.292337, 37.683976],
          [-80.296138, 37.691783],
          [-80.287107, 37.696403],
          [-80.263936, 37.719137],
          [-80.259342, 37.731205],
          [-80.260313, 37.733517],
          [-80.262765, 37.738336],
          [-80.257411, 37.756084],
          [-80.231551, 37.792591],
          [-80.227092, 37.798886],
          [-80.162202, 37.875122],
          [-80.148964, 37.886057],
          [-80.147316, 37.885936],
          [-80.14613, 37.884453],
          [-80.130464, 37.893194],
          [-80.055278, 37.951702],
          [-79.935356, 37.954365],
          [-79.881433, 37.892026],
          [-79.650757, 37.871566],
          [-79.674006, 37.763052],
          [-79.816702, 37.800989],
          [-79.91756, 37.701848],
          [-80.020519, 37.647459],
          [-80.144394, 37.596627],
          [-80.220984, 37.627767]
        ],
        [
          [-79.983631, 37.802278],
          [-79.984451, 37.812016],
          [-79.987402, 37.813585],
          [-79.98921, 37.810961],
          [-79.999078, 37.80624],
          [-79.992055, 37.79981],
          [-79.992955, 37.797063],
          [-80.00136, 37.789826],
          [-79.995941, 37.782229],
          [-80.001196, 37.768848],
          [-80.008893, 37.762983],
          [-79.992566, 37.748244],
          [-79.966509, 37.757109],
          [-79.977265, 37.767344],
          [-79.980001, 37.781063],
          [-79.966452, 37.792484],
          [-79.967071, 37.803405],
          [-79.983631, 37.802278]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51011",
      state: "51",
      county: "011",
      name: "Appomattox County",
      censusArea: 333.494,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.869245, 37.542095],
          [-78.830938, 37.535642],
          [-78.825238, 37.552542],
          [-78.594436, 37.397974],
          [-78.681573, 37.248759],
          [-78.824209, 37.205361],
          [-78.917373, 37.242687],
          [-79.018526, 37.429917],
          [-78.869245, 37.542095]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51013",
      state: "51",
      county: "013",
      name: "Arlington County",
      censusArea: 25.974,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.032986, 38.8395],
          [-77.05455, 38.840995],
          [-77.063871, 38.845011],
          [-77.085609, 38.84419],
          [-77.087931, 38.82745],
          [-77.088047, 38.824973],
          [-77.110731, 38.843457],
          [-77.150287, 38.876192],
          [-77.172169, 38.893329],
          [-77.16811, 38.896521],
          [-77.157513, 38.904855],
          [-77.154368, 38.907044],
          [-77.149735, 38.910523],
          [-77.148492, 38.911993],
          [-77.119863, 38.934265],
          [-77.1012, 38.911111],
          [-77.0902, 38.904211],
          [-77.068199, 38.899811],
          [-77.040599, 38.871212],
          [-77.031698, 38.850512],
          [-77.032986, 38.8395]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51015",
      state: "51",
      county: "015",
      name: "Augusta County",
      censusArea: 966.999,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.512158, 38.180419],
          [-79.438103, 38.216235],
          [-79.312276, 38.411876],
          [-79.297758, 38.416438],
          [-79.295712, 38.418129],
          [-79.291813, 38.419627],
          [-79.290529, 38.420757],
          [-79.280263, 38.425475],
          [-79.282663, 38.431021],
          [-79.267414, 38.438322],
          [-79.265327, 38.441772],
          [-79.263376, 38.443762],
          [-79.26291, 38.444586],
          [-79.242024, 38.464332],
          [-79.240059, 38.469841],
          [-79.234408, 38.473011],
          [-79.23162, 38.474041],
          [-79.225839, 38.479446],
          [-78.749354, 38.206621],
          [-78.77923, 38.083451],
          [-78.839211, 38.047565],
          [-78.907278, 37.945959],
          [-79.005129, 37.88169],
          [-79.062454, 37.9176],
          [-79.157423, 37.890995],
          [-79.183978, 37.914194],
          [-79.482405, 38.086105],
          [-79.436942, 38.163243],
          [-79.512158, 38.180419]
        ],
        [
          [-78.949568, 38.057183],
          [-78.872163, 38.039587],
          [-78.855796, 38.074375],
          [-78.903534, 38.099978],
          [-78.949568, 38.057183]
        ],
        [
          [-79.091666, 38.132142],
          [-79.049779, 38.121112],
          [-79.02301, 38.195777],
          [-79.09711, 38.184771],
          [-79.091666, 38.132142]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51019",
      state: "51",
      county: "019",
      name: "Bedford County",
      censusArea: 753.021,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.626126, 37.455284],
          [-79.498553, 37.532887],
          [-79.436926, 37.617004],
          [-79.349319, 37.52484],
          [-79.183302, 37.465908],
          [-79.268727, 37.431028],
          [-79.257188, 37.355608],
          [-79.330491, 37.238435],
          [-79.442884, 37.055863],
          [-79.480727, 37.005876],
          [-79.594059, 37.042238],
          [-79.718272, 37.189757],
          [-79.847082, 37.225468],
          [-79.847599, 37.309177],
          [-79.844191, 37.317785],
          [-79.81075, 37.335305],
          [-79.788151, 37.351116],
          [-79.778608, 37.381816],
          [-79.814193, 37.403687],
          [-79.790467, 37.40883],
          [-79.719063, 37.448989],
          [-79.690053, 37.476403],
          [-79.659053, 37.485206],
          [-79.634201, 37.45705],
          [-79.628157, 37.453633],
          [-79.626126, 37.455284]
        ],
        [
          [-79.543597, 37.324178],
          [-79.503624, 37.316653],
          [-79.487211, 37.345777],
          [-79.535648, 37.355314],
          [-79.543597, 37.324178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51025",
      state: "51",
      county: "025",
      name: "Brunswick County",
      censusArea: 566.173,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.889813, 36.988818],
          [-77.73363, 36.945676],
          [-77.658036, 36.894514],
          [-77.655841, 36.713539],
          [-77.767117, 36.544752],
          [-77.87528, 36.544754],
          [-77.882357, 36.544737],
          [-77.899771, 36.544663],
          [-78.038938, 36.544173],
          [-78.03942, 36.544196],
          [-78.046202, 36.544168],
          [-78.033437, 36.708451],
          [-78.027419, 36.775951],
          [-78.027411, 36.777858],
          [-78.003639, 37.022756],
          [-77.889813, 36.988818]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51031",
      state: "51",
      county: "031",
      name: "Campbell County",
      censusArea: 503.867,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.330491, 37.238435],
          [-79.257188, 37.355608],
          [-79.214982, 37.333],
          [-79.085244, 37.394743],
          [-79.018526, 37.429917],
          [-78.917373, 37.242687],
          [-78.824209, 37.205361],
          [-78.903393, 37.024371],
          [-78.904587, 37.022288],
          [-79.092264, 37.061498],
          [-79.194525, 37.055816],
          [-79.217104, 37.118874],
          [-79.332161, 37.125568],
          [-79.442884, 37.055863],
          [-79.330491, 37.238435]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51033",
      state: "51",
      county: "033",
      name: "Caroline County",
      censusArea: 527.513,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.181418, 37.891654],
          [-77.243162, 37.900241],
          [-77.345229, 37.789388],
          [-77.403642, 37.779569],
          [-77.445155, 37.894819],
          [-77.484098, 37.883505],
          [-77.6422, 37.990928],
          [-77.626219, 38.003429],
          [-77.551611, 38.073609],
          [-77.518179, 38.10489],
          [-77.37467, 38.243099],
          [-77.370301, 38.246576],
          [-77.3604, 38.242423],
          [-77.345931, 38.252227],
          [-77.33886, 38.252093],
          [-77.332738, 38.24584],
          [-77.326692, 38.245136],
          [-77.224689, 38.242207],
          [-77.115997, 38.149931],
          [-77.1701, 38.079543],
          [-77.070115, 37.96477],
          [-77.148634, 37.972],
          [-77.181418, 37.891654]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51036",
      state: "51",
      county: "036",
      name: "Charles City County",
      censusArea: 182.817,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.883156, 37.223057],
          [-76.917081, 37.224365],
          [-76.972439, 37.248501],
          [-77.010096, 37.305251],
          [-77.069269, 37.269742],
          [-77.270675, 37.320756],
          [-77.264595, 37.344052],
          [-77.244167, 37.369274],
          [-77.249665, 37.382],
          [-77.177324, 37.4906],
          [-76.904471, 37.377665],
          [-76.873507, 37.363415],
          [-76.883156, 37.223057]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51045",
      state: "51",
      county: "045",
      name: "Craig County",
      censusArea: 329.534,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.220984, 37.627767],
          [-80.144394, 37.596627],
          [-80.020519, 37.647459],
          [-79.969037, 37.544419],
          [-80.073985, 37.422305],
          [-80.261984, 37.340694],
          [-80.327898, 37.366792],
          [-80.433361, 37.314901],
          [-80.475601, 37.422949],
          [-80.46482, 37.426144],
          [-80.443025, 37.438126],
          [-80.425656, 37.449876],
          [-80.320627, 37.49888],
          [-80.314806, 37.500943],
          [-80.330306, 37.536244],
          [-80.312393, 37.546239],
          [-80.288766, 37.58196],
          [-80.240272, 37.606961],
          [-80.223386, 37.623185],
          [-80.220984, 37.627767]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51049",
      state: "51",
      county: "049",
      name: "Cumberland County",
      censusArea: 297.461,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.159278, 37.748528],
          [-78.072256, 37.657357],
          [-78.132048, 37.454686],
          [-78.235039, 37.368102],
          [-78.32184, 37.309292],
          [-78.465089, 37.339677],
          [-78.249021, 37.635546],
          [-78.239748, 37.690495],
          [-78.159278, 37.748528]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51051",
      state: "51",
      county: "051",
      name: "Dickenson County",
      censusArea: 330.532,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.148718, 37.041691],
          [-82.328009, 36.970905],
          [-82.467419, 37.022144],
          [-82.54622, 37.124982],
          [-82.553549, 37.200867],
          [-82.510826, 37.218091],
          [-82.350948, 37.267077],
          [-82.310793, 37.297387],
          [-82.305874, 37.3011],
          [-82.135935, 37.061169],
          [-82.148718, 37.041691]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54081",
      state: "54",
      county: "081",
      name: "Raleigh County",
      censusArea: 605.351,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.456632, 37.987307],
          [-81.438531, 37.968008],
          [-81.414331, 37.973508],
          [-81.380829, 37.969109],
          [-81.296628, 37.94401],
          [-81.295127, 37.889311],
          [-81.073249, 37.875865],
          [-81.091041, 37.849674],
          [-80.94509, 37.819121],
          [-80.882316, 37.680766],
          [-80.947017, 37.715449],
          [-81.09463, 37.588658],
          [-81.19932, 37.574515],
          [-81.222121, 37.510416],
          [-81.30413, 37.546564],
          [-81.427781, 37.749352],
          [-81.514228, 37.791211],
          [-81.571334, 37.928107],
          [-81.456632, 37.987307]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51061",
      state: "51",
      county: "061",
      name: "Fauquier County",
      censusArea: 647.449,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.663346, 38.752917],
          [-77.580256, 38.627685],
          [-77.535808, 38.561621],
          [-77.530914, 38.555929],
          [-77.634157, 38.464508],
          [-77.634835, 38.409713],
          [-77.735536, 38.413116],
          [-77.877169, 38.584891],
          [-77.935454, 38.695853],
          [-78.028075, 38.759293],
          [-78.122166, 38.856607],
          [-78.12361, 38.864902],
          [-78.130574, 38.864865],
          [-78.004167, 38.979479],
          [-77.98978, 38.993834],
          [-77.990379, 38.996331],
          [-77.965138, 39.008927],
          [-77.961956, 39.013841],
          [-77.960269, 39.013384],
          [-77.655081, 38.942635],
          [-77.665797, 38.915867],
          [-77.680547, 38.8932],
          [-77.689176, 38.889231],
          [-77.715415, 38.832214],
          [-77.663346, 38.752917]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51067",
      state: "51",
      county: "067",
      name: "Franklin County",
      censusArea: 690.426,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.640652, 36.856139],
          [-79.761756, 36.788337],
          [-79.999771, 36.830738],
          [-80.012032, 36.789644],
          [-80.027194, 36.791262],
          [-80.013194, 36.783748],
          [-80.025178, 36.780752],
          [-80.041493, 36.794365],
          [-80.066449, 36.841057],
          [-80.235455, 36.87213],
          [-80.121479, 36.991428],
          [-80.128119, 37.122912],
          [-80.030562, 37.17481],
          [-79.962301, 37.137099],
          [-79.847082, 37.225468],
          [-79.718272, 37.189757],
          [-79.594059, 37.042238],
          [-79.59593, 37.036349],
          [-79.640652, 36.856139]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51069",
      state: "51",
      county: "069",
      name: "Frederick County",
      censusArea: 413.496,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.544111, 39.056676],
          [-78.508132, 39.08863],
          [-78.459869, 39.113351],
          [-78.439429, 39.132146],
          [-78.418377, 39.156656],
          [-78.41074, 39.171983],
          [-78.426722, 39.188903],
          [-78.438651, 39.198049],
          [-78.437053, 39.199766],
          [-78.43213, 39.204717],
          [-78.431167, 39.205744],
          [-78.429803, 39.207014],
          [-78.427911, 39.208611],
          [-78.423968, 39.212049],
          [-78.399669, 39.243874],
          [-78.399785, 39.244129],
          [-78.360035, 39.317771],
          [-78.35894, 39.319484],
          [-78.34048, 39.353492],
          [-78.362267, 39.357784],
          [-78.347087, 39.466012],
          [-78.228766, 39.391233],
          [-78.033185, 39.264626],
          [-78.033183, 39.264624],
          [-78.151981, 39.036277],
          [-78.314196, 39.007977],
          [-78.335803, 39.101509],
          [-78.454023, 39.027599],
          [-78.544111, 39.056676]
        ],
        [
          [-78.196945, 39.191161],
          [-78.18289, 39.13097],
          [-78.136953, 39.18453],
          [-78.154919, 39.200895],
          [-78.196945, 39.191161]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51073",
      state: "51",
      county: "073",
      name: "Gloucester County",
      censusArea: 217.807,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.712305, 37.431309],
          [-76.647086, 37.480685],
          [-76.654076, 37.603174],
          [-76.437561, 37.51436],
          [-76.404756, 37.400213],
          [-76.415167, 37.402133],
          [-76.418719, 37.3978],
          [-76.418176, 37.385064],
          [-76.422503, 37.381355],
          [-76.437525, 37.37975],
          [-76.445333, 37.36646],
          [-76.434965, 37.354524],
          [-76.406388, 37.332924],
          [-76.38777, 37.30767],
          [-76.385603, 37.294108],
          [-76.381075, 37.28534],
          [-76.369029, 37.279311],
          [-76.352556, 37.278334],
          [-76.349489, 37.273963],
          [-76.36229, 37.270226],
          [-76.392788, 37.264973],
          [-76.417173, 37.26395],
          [-76.421765, 37.255198],
          [-76.429141, 37.25331],
          [-76.475927, 37.250543],
          [-76.48284, 37.254831],
          [-76.493302, 37.24947],
          [-76.4989, 37.241015],
          [-76.658651, 37.380139],
          [-76.672296, 37.395808],
          [-76.712305, 37.431309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51081",
      state: "51",
      county: "081",
      name: "Greensville County",
      censusArea: 295.226,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.658036, 36.894514],
          [-77.648037, 36.896884],
          [-77.645705, 36.888531],
          [-77.648045, 36.881507],
          [-77.639169, 36.873752],
          [-77.632266, 36.878709],
          [-77.638073, 36.885989],
          [-77.631207, 36.88761],
          [-77.62671, 36.88127],
          [-77.619029, 36.877844],
          [-77.458678, 36.862963],
          [-77.489999, 36.721009],
          [-77.430127, 36.707658],
          [-77.501917, 36.670506],
          [-77.377963, 36.633048],
          [-77.296875, 36.544746],
          [-77.767117, 36.544752],
          [-77.655841, 36.713539],
          [-77.658036, 36.894514]
        ],
        [
          [-77.556524, 36.674874],
          [-77.509246, 36.682998],
          [-77.511921, 36.719122],
          [-77.557359, 36.713166],
          [-77.556524, 36.674874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51085",
      state: "51",
      county: "085",
      name: "Hanover County",
      censusArea: 468.536,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.228504, 37.536398],
          [-77.333838, 37.576865],
          [-77.445827, 37.683975],
          [-77.630145, 37.706985],
          [-77.79692, 37.729069],
          [-77.796803, 37.730604],
          [-77.687294, 38.008011],
          [-77.684065, 38.008283],
          [-77.680592, 38.007587],
          [-77.676618, 38.006409],
          [-77.670898, 38.003769],
          [-77.667921, 38.00012],
          [-77.667985, 37.998379],
          [-77.66716, 37.997079],
          [-77.664779, 37.996902],
          [-77.662338, 37.997764],
          [-77.660602, 37.997145],
          [-77.660469, 37.993726],
          [-77.659698, 37.992082],
          [-77.657916, 37.991864],
          [-77.65367, 37.993595],
          [-77.649201, 37.994168],
          [-77.647163, 37.993948],
          [-77.642318, 37.992284],
          [-77.6422, 37.990928],
          [-77.484098, 37.883505],
          [-77.445155, 37.894819],
          [-77.403642, 37.779569],
          [-77.345229, 37.789388],
          [-77.29247, 37.714682],
          [-77.123675, 37.626881],
          [-77.228504, 37.536398]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51091",
      state: "51",
      county: "091",
      name: "Highland County",
      censusArea: 415.156,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.794568, 38.264856],
          [-79.790134, 38.267654],
          [-79.788945, 38.268703],
          [-79.810115, 38.305037],
          [-79.808711, 38.309429],
          [-79.731661, 38.374143],
          [-79.691377, 38.439558],
          [-79.689544, 38.442511],
          [-79.688962, 38.449538],
          [-79.688205, 38.450476],
          [-79.699006, 38.475148],
          [-79.695565, 38.477842],
          [-79.669128, 38.510883],
          [-79.669128, 38.510975],
          [-79.649075, 38.591515],
          [-79.597955, 38.572767],
          [-79.555471, 38.560217],
          [-79.53687, 38.550917],
          [-79.521469, 38.533918],
          [-79.476638, 38.457228],
          [-79.312276, 38.411876],
          [-79.438103, 38.216235],
          [-79.512158, 38.180419],
          [-79.706196, 38.221192],
          [-79.794568, 38.264856]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51095",
      state: "51",
      county: "095",
      name: "James City County",
      censusArea: 142.437,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.611018, 37.167097],
          [-76.612517, 37.170486],
          [-76.613599, 37.172931],
          [-76.614221, 37.174335],
          [-76.616268, 37.178962],
          [-76.616804, 37.18126],
          [-76.617537, 37.184409],
          [-76.618008, 37.186429],
          [-76.61934, 37.192146],
          [-76.619962, 37.193184],
          [-76.621113, 37.195103],
          [-76.623292, 37.198738],
          [-76.629868, 37.206738],
          [-76.639608, 37.214783],
          [-76.641085, 37.216002],
          [-76.649869, 37.220914],
          [-76.689166, 37.222866],
          [-76.693373, 37.221228],
          [-76.698943, 37.219059],
          [-76.730951, 37.213813],
          [-76.73432, 37.204211],
          [-76.74, 37.195379],
          [-76.74304, 37.192611],
          [-76.75047, 37.190098],
          [-76.757765, 37.191658],
          [-76.773752, 37.206061],
          [-76.780532, 37.209336],
          [-76.791555, 37.207564],
          [-76.818511, 37.224618],
          [-76.883156, 37.223057],
          [-76.873507, 37.363415],
          [-76.904471, 37.377665],
          [-76.892677, 37.433067],
          [-76.741984, 37.467076],
          [-76.732961, 37.450235],
          [-76.712305, 37.431309],
          [-76.672296, 37.395808],
          [-76.658651, 37.380139],
          [-76.741667, 37.37044],
          [-76.727588, 37.304921],
          [-76.739665, 37.306255],
          [-76.725272, 37.279686],
          [-76.742487, 37.275092],
          [-76.726003, 37.249901],
          [-76.664621, 37.25049],
          [-76.679468, 37.265652],
          [-76.591432, 37.214721],
          [-76.577675, 37.190445],
          [-76.611018, 37.167097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51103",
      state: "51",
      county: "103",
      name: "Lancaster County",
      censusArea: 133.25,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.642276, 37.792317],
          [-76.507828, 37.838814],
          [-76.418735, 37.822934],
          [-76.376721, 37.712908],
          [-76.320216, 37.680666],
          [-76.324808, 37.676983],
          [-76.339892, 37.655966],
          [-76.332562, 37.645817],
          [-76.306464, 37.642005],
          [-76.292534, 37.636098],
          [-76.287959, 37.631771],
          [-76.279447, 37.618225],
          [-76.28037, 37.613715],
          [-76.309174, 37.621892],
          [-76.36232, 37.610368],
          [-76.381106, 37.627003],
          [-76.390054, 37.630326],
          [-76.399236, 37.628636],
          [-76.443254, 37.652347],
          [-76.472392, 37.665772],
          [-76.489576, 37.666201],
          [-76.491799, 37.663614],
          [-76.497564, 37.647056],
          [-76.501522, 37.643762],
          [-76.510187, 37.642324],
          [-76.536548, 37.663574],
          [-76.537698, 37.66893],
          [-76.535302, 37.687516],
          [-76.537228, 37.698892],
          [-76.54005, 37.704432],
          [-76.560476, 37.727827],
          [-76.576387, 37.757493],
          [-76.584289, 37.76889],
          [-76.593835, 37.772848],
          [-76.595939, 37.77168],
          [-76.602024, 37.772731],
          [-76.615351, 37.780759],
          [-76.642276, 37.792317]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49037",
      state: "49",
      county: "037",
      name: "San Juan County",
      censusArea: 7819.988,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.043121, 37.97426],
          [-109.042137, 37.88116],
          [-109.041723, 37.842051],
          [-109.041754, 37.835826],
          [-109.04176, 37.713182],
          [-109.041732, 37.711214],
          [-109.042089, 37.623795],
          [-109.042131, 37.617662],
          [-109.041806, 37.604171],
          [-109.041865, 37.530726],
          [-109.041915, 37.530653],
          [-109.043053, 37.485161],
          [-109.04581, 37.374993],
          [-109.045156, 37.112064],
          [-109.045203, 37.111958],
          [-109.045173, 37.109464],
          [-109.045189, 37.096271],
          [-109.044995, 37.086429],
          [-109.045058, 37.074661],
          [-109.045166, 37.072742],
          [-109.045223, 36.999084],
          [-109.181196, 36.999271],
          [-109.233848, 36.999266],
          [-109.246917, 36.999346],
          [-109.26339, 36.999263],
          [-109.268213, 36.999242],
          [-109.270097, 36.999266],
          [-109.378039, 36.999135],
          [-109.381226, 36.999148],
          [-110.000677, 36.997968],
          [-110.33105, 36.998216],
          [-110.495259, 37.003875],
          [-110.75069, 37.003197],
          [-111.189888, 37.000959],
          [-111.405517, 37.001497],
          [-111.405869, 37.001481],
          [-111.412784, 37.001478],
          [-111.314856, 37.012133],
          [-111.268784, 37.0542],
          [-111.247488, 37.021604],
          [-111.17835, 37.102613],
          [-111.05767, 37.099395],
          [-110.958333, 37.124438],
          [-110.899517, 37.176153],
          [-110.869804, 37.348691],
          [-110.777799, 37.322992],
          [-110.736098, 37.345192],
          [-110.699197, 37.430691],
          [-110.719198, 37.481391],
          [-110.639097, 37.487091],
          [-110.646394, 37.541063],
          [-110.497711, 37.652205],
          [-110.428107, 37.778987],
          [-110.404612, 37.879923],
          [-110.30498, 37.890518],
          [-110.283035, 37.866227],
          [-109.927882, 38.151885],
          [-109.887266, 38.190214],
          [-110.015126, 38.364604],
          [-110.025402, 38.499981],
          [-109.059962, 38.499987],
          [-109.060062, 38.275489],
          [-109.042062, 38.15549],
          [-109.042074, 38.153023],
          [-109.04282, 37.999301],
          [-109.042819, 37.997068],
          [-109.043121, 37.97426]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51119",
      state: "51",
      county: "119",
      name: "Middlesex County",
      censusArea: 130.306,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.355084, 37.527364],
          [-76.404118, 37.535542],
          [-76.418786, 37.510385],
          [-76.437561, 37.51436],
          [-76.654076, 37.603174],
          [-76.751072, 37.728605],
          [-76.723086, 37.756697],
          [-76.703069, 37.755714],
          [-76.683359, 37.770258],
          [-76.683372, 37.765507],
          [-76.680922, 37.759647],
          [-76.677002, 37.7561],
          [-76.663887, 37.751887],
          [-76.639962, 37.750941],
          [-76.61971, 37.744795],
          [-76.617373, 37.742347],
          [-76.621433, 37.737973],
          [-76.61997, 37.731271],
          [-76.606466, 37.724819],
          [-76.597213, 37.717269],
          [-76.595943, 37.712989],
          [-76.598073, 37.70912],
          [-76.597868, 37.702918],
          [-76.579591, 37.671508],
          [-76.583143, 37.661986],
          [-76.574049, 37.646781],
          [-76.542666, 37.616857],
          [-76.533777, 37.61253],
          [-76.527188, 37.611315],
          [-76.435474, 37.612807],
          [-76.420252, 37.598686],
          [-76.410781, 37.581815],
          [-76.383188, 37.573056],
          [-76.357835, 37.573699],
          [-76.332641, 37.570042],
          [-76.300144, 37.561734],
          [-76.29796, 37.557636],
          [-76.302762, 37.551295],
          [-76.330598, 37.536391],
          [-76.339989, 37.53833],
          [-76.348992, 37.536548],
          [-76.355084, 37.527364]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50025",
      state: "50",
      county: "025",
      name: "Windham County",
      censusArea: 785.305,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.86418, 42.737117],
          [-72.927291, 42.738714],
          [-72.930271, 42.738789],
          [-73.018646, 42.741025],
          [-73.015111, 42.832074],
          [-72.92482, 42.856164],
          [-72.921376, 42.926792],
          [-73.008063, 42.930087],
          [-73.002789, 43.023007],
          [-72.9969, 43.119095],
          [-72.868737, 43.11317],
          [-72.819368, 43.255498],
          [-72.685637, 43.222495],
          [-72.431187, 43.231786],
          [-72.439407, 43.211322],
          [-72.4521, 43.161414],
          [-72.441904, 43.136615],
          [-72.433097, 43.120021],
          [-72.432661, 43.114077],
          [-72.443762, 43.006245],
          [-72.556214, 42.86695],
          [-72.556112, 42.866252],
          [-72.555768, 42.858153],
          [-72.555693, 42.85638],
          [-72.542784, 42.808482],
          [-72.509506, 42.781183],
          [-72.491122, 42.772465],
          [-72.478453, 42.762296],
          [-72.460195, 42.732066],
          [-72.458519, 42.726853],
          [-72.86418, 42.737117]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53037",
      state: "53",
      county: "037",
      name: "Kittitas County",
      censusArea: 2297.271,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.243207, 47.540334],
          [-121.113606, 47.597135],
          [-120.916098, 47.42854],
          [-120.525137, 47.333239],
          [-120.388978, 47.260889],
          [-120.09447, 47.262151],
          [-120.074669, 47.225152],
          [-120.007065, 47.220053],
          [-120.042866, 47.073453],
          [-119.927537, 46.817092],
          [-119.973017, 46.737171],
          [-120.509618, 46.738114],
          [-120.509227, 46.824534],
          [-120.634431, 46.825831],
          [-120.634883, 46.911995],
          [-121.02649, 46.911341],
          [-121.089794, 46.991039],
          [-121.282039, 47.088954],
          [-121.379671, 47.086765],
          [-121.298041, 47.14823],
          [-121.432652, 47.293591],
          [-121.425421, 47.422354],
          [-121.310953, 47.479271],
          [-121.243207, 47.540334]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53039",
      state: "53",
      county: "039",
      name: "Klickitat County",
      censusArea: 1871.313,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.608576, 45.85822],
          [-121.612502, 46.04384],
          [-121.523938, 46.044048],
          [-121.070459, 46.042575],
          [-119.866265, 46.040662],
          [-119.868135, 45.835962],
          [-119.999506, 45.812199],
          [-120.001148, 45.811902],
          [-120.141352, 45.773152],
          [-120.170453, 45.761951],
          [-120.210754, 45.725951],
          [-120.40396, 45.699249],
          [-120.482362, 45.694449],
          [-120.505863, 45.700048],
          [-120.521964, 45.709848],
          [-120.559465, 45.738348],
          [-120.591166, 45.746547],
          [-120.634968, 45.745847],
          [-120.653559, 45.737237],
          [-120.668869, 45.730147],
          [-120.68937, 45.715847],
          [-120.788872, 45.686246],
          [-120.855674, 45.671545],
          [-120.870042, 45.661242],
          [-120.915876, 45.641345],
          [-120.943977, 45.656445],
          [-121.06437, 45.652549],
          [-121.086675, 45.646667],
          [-121.195233, 45.629513],
          [-121.200367, 45.649829],
          [-121.215779, 45.671238],
          [-121.312198, 45.699925],
          [-121.33777, 45.704949],
          [-121.372574, 45.703111],
          [-121.401739, 45.692887],
          [-121.423592, 45.69399],
          [-121.441045, 45.69727],
          [-121.462849, 45.701367],
          [-121.522392, 45.724677],
          [-121.608928, 45.78258],
          [-121.608576, 45.85822]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53059",
      state: "53",
      county: "059",
      name: "Skamania County",
      censusArea: 1655.677,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.820055, 45.704649],
          [-121.867167, 45.693277],
          [-121.901855, 45.670716],
          [-121.900858, 45.662009],
          [-121.908267, 45.654399],
          [-121.922242, 45.648495],
          [-121.983038, 45.622812],
          [-122.101675, 45.583516],
          [-122.112356, 45.581409],
          [-122.126197, 45.582617],
          [-122.126197, 45.582573],
          [-122.12949, 45.582967],
          [-122.129548, 45.582945],
          [-122.14075, 45.584508],
          [-122.183695, 45.577696],
          [-122.2017, 45.564141],
          [-122.248993, 45.547745],
          [-122.245633, 46.053932],
          [-122.239952, 46.384204],
          [-122.241696, 46.385292],
          [-122.241011, 46.386371],
          [-121.523556, 46.388221],
          [-121.523938, 46.044048],
          [-121.612502, 46.04384],
          [-121.608576, 45.85822],
          [-121.608928, 45.78258],
          [-121.522392, 45.724677],
          [-121.533106, 45.726541],
          [-121.626185, 45.705915],
          [-121.707358, 45.694809],
          [-121.735104, 45.694039],
          [-121.811304, 45.706761],
          [-121.820055, 45.704649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53075",
      state: "53",
      county: "075",
      name: "Whitman County",
      censusArea: 2159.09,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.209807, 46.738938],
          [-118.012613, 46.790485],
          [-117.958856, 46.914878],
          [-117.960143, 47.260449],
          [-117.823695, 47.260378],
          [-117.040019, 47.259272],
          [-117.039899, 47.225515],
          [-117.039888, 47.203282],
          [-117.039871, 47.181858],
          [-117.039836, 47.154734],
          [-117.039821, 47.127265],
          [-117.039657, 46.825798],
          [-117.039828, 46.815443],
          [-117.039783, 46.541785],
          [-117.039771, 46.471779],
          [-117.039763, 46.46957],
          [-117.039741, 46.462704],
          [-117.039813, 46.425425],
          [-117.199044, 46.419786],
          [-117.228896, 46.462314],
          [-117.253902, 46.545082],
          [-117.475331, 46.70033],
          [-117.601227, 46.672679],
          [-117.751037, 46.690027],
          [-117.851844, 46.624772],
          [-118.172552, 46.556947],
          [-118.21566, 46.588924],
          [-118.249122, 46.734138],
          [-118.209807, 46.738938]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54017",
      state: "54",
      county: "017",
      name: "Doddridge County",
      censusArea: 319.72,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.544521, 39.42912],
          [-80.531808, 39.360121],
          [-80.603684, 39.291041],
          [-80.525709, 39.209429],
          [-80.596126, 39.167069],
          [-80.728326, 39.095679],
          [-80.749214, 39.099843],
          [-80.81297, 39.109401],
          [-80.91384, 39.272947],
          [-80.888744, 39.294298],
          [-80.618629, 39.450375],
          [-80.544521, 39.42912]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54029",
      state: "54",
      county: "029",
      name: "Hancock County",
      censusArea: 82.609,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.519054, 40.517922],
          [-80.518692, 40.477365],
          [-80.517998, 40.399644],
          [-80.573784, 40.399755],
          [-80.617346, 40.396004],
          [-80.612295, 40.434867],
          [-80.599194, 40.482566],
          [-80.610259, 40.490962],
          [-80.666917, 40.573664],
          [-80.667957, 40.582496],
          [-80.665892, 40.587728],
          [-80.634355, 40.616095],
          [-80.627171, 40.619936],
          [-80.601494, 40.625387],
          [-80.594065, 40.623664],
          [-80.589041, 40.620128],
          [-80.56072, 40.62368],
          [-80.518991, 40.638801],
          [-80.519039, 40.616391],
          [-80.519086, 40.616385],
          [-80.519086, 40.590161],
          [-80.519055, 40.590173],
          [-80.519057, 40.517922],
          [-80.519054, 40.517922]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54043",
      state: "54",
      county: "043",
      name: "Lincoln County",
      censusArea: 437.039,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.915197, 38.325597],
          [-81.818234, 38.316751],
          [-81.83347, 38.20957],
          [-81.927565, 38.152492],
          [-81.932507, 38.025356],
          [-82.190658, 37.974778],
          [-82.306391, 37.94364],
          [-82.203865, 38.023606],
          [-82.283316, 38.141936],
          [-82.264849, 38.229199],
          [-82.17679, 38.3396],
          [-82.047128, 38.374432],
          [-82.048589, 38.290138],
          [-81.991774, 38.269085],
          [-81.915197, 38.325597]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54055",
      state: "54",
      county: "055",
      name: "Mercer County",
      censusArea: 418.993,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.222121, 37.510416],
          [-81.19932, 37.574515],
          [-81.09463, 37.588658],
          [-80.859558, 37.429555],
          [-80.862761, 37.411829],
          [-80.872589, 37.372347],
          [-80.849451, 37.346909],
          [-80.900535, 37.315],
          [-80.919259, 37.306163],
          [-80.947896, 37.295872],
          [-80.966556, 37.292158],
          [-80.973889, 37.291444],
          [-80.980044, 37.293118],
          [-80.981322, 37.293465],
          [-81.034652, 37.290751],
          [-81.084012, 37.284401],
          [-81.112596, 37.278497],
          [-81.167029, 37.262881],
          [-81.204774, 37.243013],
          [-81.225104, 37.234874],
          [-81.320105, 37.299323],
          [-81.362156, 37.337687],
          [-81.311201, 37.424509],
          [-81.222121, 37.510416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54073",
      state: "54",
      county: "073",
      name: "Pleasants County",
      censusArea: 130.103,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.239477, 39.268328],
          [-81.371271, 39.342062],
          [-81.356911, 39.343178],
          [-81.347567, 39.34577],
          [-81.295373, 39.37529],
          [-81.270716, 39.385914],
          [-81.249088, 39.389992],
          [-81.223581, 39.386062],
          [-81.215617, 39.388602],
          [-81.127696, 39.464879],
          [-81.029337, 39.467383],
          [-81.007836, 39.350923],
          [-81.239477, 39.268328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54085",
      state: "54",
      county: "085",
      name: "Ritchie County",
      censusArea: 451.991,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.298017, 39.185572],
          [-81.239477, 39.268328],
          [-81.007836, 39.350923],
          [-80.93214, 39.389119],
          [-80.888744, 39.294298],
          [-80.91384, 39.272947],
          [-80.81297, 39.109401],
          [-81.033632, 39.009584],
          [-81.162455, 39.030608],
          [-81.250252, 39.035074],
          [-81.328786, 39.152584],
          [-81.298017, 39.185572]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54095",
      state: "54",
      county: "095",
      name: "Tyler County",
      censusArea: 256.294,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.888744, 39.294298],
          [-80.93214, 39.389119],
          [-81.007836, 39.350923],
          [-81.029337, 39.467383],
          [-81.127696, 39.464879],
          [-81.063587, 39.520497],
          [-81.038691, 39.540464],
          [-81.020055, 39.55541],
          [-80.943782, 39.606926],
          [-80.852182, 39.602048],
          [-80.720451, 39.481237],
          [-80.618629, 39.450375],
          [-80.888744, 39.294298]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51057",
      state: "51",
      county: "057",
      name: "Essex County",
      censusArea: 257.123,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.751072, 37.728605],
          [-76.804423, 37.794892],
          [-76.943137, 37.78291],
          [-77.018849, 37.844848],
          [-77.070115, 37.96477],
          [-77.1701, 38.079543],
          [-77.115997, 38.149931],
          [-77.094796, 38.161631],
          [-77.084596, 38.160231],
          [-77.082596, 38.146831],
          [-77.075496, 38.141131],
          [-77.066895, 38.147631],
          [-77.069296, 38.157731],
          [-77.061695, 38.161631],
          [-77.049695, 38.109832],
          [-76.936959, 38.077074],
          [-76.905403, 38.003469],
          [-76.77539, 37.874306],
          [-76.784618, 37.869569],
          [-76.782826, 37.863184],
          [-76.766328, 37.840437],
          [-76.7512, 37.824141],
          [-76.734309, 37.79866],
          [-76.723863, 37.788503],
          [-76.715498, 37.785873],
          [-76.689773, 37.78519],
          [-76.683775, 37.781391],
          [-76.681901, 37.778118],
          [-76.683343, 37.775783],
          [-76.683359, 37.770258],
          [-76.703069, 37.755714],
          [-76.723086, 37.756697],
          [-76.751072, 37.728605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51115",
      state: "51",
      county: "115",
      name: "Mathews County",
      censusArea: 85.929,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.437561, 37.51436],
          [-76.418786, 37.510385],
          [-76.404118, 37.535542],
          [-76.355084, 37.527364],
          [-76.360474, 37.51924],
          [-76.359378, 37.513426],
          [-76.352678, 37.504913],
          [-76.32947, 37.49492],
          [-76.306952, 37.497488],
          [-76.297739, 37.506863],
          [-76.296445, 37.511235],
          [-76.298456, 37.512677],
          [-76.297651, 37.515424],
          [-76.293599, 37.516499],
          [-76.288167, 37.514118],
          [-76.281043, 37.507821],
          [-76.265056, 37.481365],
          [-76.252415, 37.447274],
          [-76.250454, 37.421886],
          [-76.246617, 37.404122],
          [-76.245283, 37.386839],
          [-76.24846, 37.375135],
          [-76.258277, 37.36202],
          [-76.262407, 37.360786],
          [-76.264847, 37.357399],
          [-76.272888, 37.335174],
          [-76.272005, 37.322194],
          [-76.275552, 37.309964],
          [-76.282555, 37.319107],
          [-76.291324, 37.324145],
          [-76.308581, 37.329366],
          [-76.31205, 37.338088],
          [-76.337476, 37.364014],
          [-76.366751, 37.374495],
          [-76.387112, 37.385061],
          [-76.391437, 37.390284],
          [-76.393958, 37.39594],
          [-76.393125, 37.398068],
          [-76.404756, 37.400213],
          [-76.437561, 37.51436]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22095",
      state: "22",
      county: "095",
      name: "St. John Baptist County",
      lsad: "Parish",
      censusArea: 213.071,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.641998, 30.16643],
          [-90.632906, 30.219933],
          [-90.632811, 30.221409],
          [-90.558638, 30.193737],
          [-90.497899, 30.255946],
          [-90.401254, 30.285534],
          [-90.297375, 30.293658],
          [-90.277855, 30.230888],
          [-90.449133, 30.032863],
          [-90.538132, 30.016884],
          [-90.541943, 29.891522],
          [-90.656312, 29.889246],
          [-90.68885, 30.035953],
          [-90.641998, 30.16643]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22111",
      state: "22",
      county: "111",
      name: "Union County",
      lsad: "Parish",
      censusArea: 876.988,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.065179, 32.723003],
          [-92.272313, 32.580975],
          [-92.415071, 32.582845],
          [-92.41505, 32.670056],
          [-92.518356, 32.67072],
          [-92.571295, 32.748579],
          [-92.725439, 32.75954],
          [-92.724743, 33.014347],
          [-92.723553, 33.014328],
          [-92.715884, 33.014398],
          [-92.711289, 33.014307],
          [-92.503776, 33.012161],
          [-92.501383, 33.01216],
          [-92.469762, 33.01201],
          [-92.37029, 33.010717],
          [-92.362865, 33.010628],
          [-92.335893, 33.010349],
          [-92.292664, 33.010103],
          [-92.222825, 33.00908],
          [-92.069105, 33.008163],
          [-92.065179, 32.723003]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21001",
      state: "21",
      county: "001",
      name: "Adair County",
      censusArea: 405.283,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.165174, 37.310396],
          [-85.056898, 37.257041],
          [-85.043316, 37.186028],
          [-85.205831, 36.996862],
          [-85.232026, 36.925069],
          [-85.45183, 36.938138],
          [-85.526881, 37.10945],
          [-85.380258, 37.168314],
          [-85.352771, 37.192432],
          [-85.314663, 37.24074],
          [-85.165174, 37.310396]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21005",
      state: "21",
      county: "005",
      name: "Anderson County",
      censusArea: 201.832,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.152797, 37.897661],
          [-85.167834, 37.971809],
          [-85.101646, 38.03727],
          [-85.023711, 38.129052],
          [-84.864908, 38.116926],
          [-84.796327, 37.970088],
          [-84.93778, 37.952826],
          [-85.030528, 37.891538],
          [-85.152797, 37.897661]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21007",
      state: "21",
      county: "007",
      name: "Ballard County",
      censusArea: 246.659,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.100794, 36.944006],
          [-89.132685, 36.9822],
          [-89.166447, 37.003337],
          [-89.173595, 37.011409],
          [-89.178975, 37.020928],
          [-89.182509, 37.037275],
          [-89.181369, 37.046305],
          [-89.175725, 37.062069],
          [-89.171881, 37.068184],
          [-89.168087, 37.074218],
          [-89.086526, 37.165602],
          [-89.041263, 37.202881],
          [-89.000968, 37.224401],
          [-88.98326, 37.228685],
          [-88.966831, 37.229891],
          [-88.933399, 37.225007],
          [-88.815952, 36.9541],
          [-88.988811, 36.919491],
          [-89.100794, 36.944006]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21019",
      state: "21",
      county: "019",
      name: "Boyd County",
      censusArea: 159.864,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.665485, 38.505734],
          [-82.593673, 38.421809],
          [-82.596281, 38.417681],
          [-82.597113, 38.412881],
          [-82.595369, 38.382722],
          [-82.595382, 38.382712],
          [-82.593008, 38.375082],
          [-82.571877, 38.315781],
          [-82.574656, 38.263873],
          [-82.578254, 38.254809],
          [-82.584001, 38.246371],
          [-82.59497, 38.245453],
          [-82.60423, 38.247303],
          [-82.794206, 38.243772],
          [-82.771737, 38.362588],
          [-82.817454, 38.373935],
          [-82.665485, 38.505734]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21037",
      state: "21",
      county: "037",
      name: "Campbell County",
      censusArea: 151.308,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.506082, 39.095081],
          [-84.502062, 39.096641],
          [-84.493743, 39.10246],
          [-84.432841, 39.094261],
          [-84.326539, 39.027463],
          [-84.319936, 39.022081],
          [-84.31368, 39.016981],
          [-84.304698, 39.006455],
          [-84.234453, 38.893226],
          [-84.232132, 38.880483],
          [-84.232478, 38.874638],
          [-84.418107, 38.806596],
          [-84.4634, 38.852059],
          [-84.4508, 38.993609],
          [-84.506082, 39.095081]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21049",
      state: "21",
      county: "049",
      name: "Clark County",
      censusArea: 252.463,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.337393, 37.892007],
          [-84.286461, 38.067028],
          [-84.080527, 38.115118],
          [-84.051053, 38.070499],
          [-83.96631, 37.930768],
          [-84.001967, 37.837592],
          [-84.080337, 37.853066],
          [-84.337393, 37.892007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21061",
      state: "21",
      county: "061",
      name: "Edmonson County",
      censusArea: 302.883,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.469126, 37.321099],
          [-86.157589, 37.335649],
          [-86.049133, 37.215285],
          [-86.056443, 37.167208],
          [-86.074267, 37.053285],
          [-86.113512, 37.061072],
          [-86.218607, 37.104029],
          [-86.28175, 37.080597],
          [-86.399165, 37.169896],
          [-86.469126, 37.321099]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21069",
      state: "21",
      county: "069",
      name: "Fleming County",
      censusArea: 348.542,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.646546, 38.170963],
          [-83.725393, 38.292584],
          [-83.848355, 38.296949],
          [-83.980031, 38.439159],
          [-83.97001, 38.463997],
          [-83.930348, 38.492278],
          [-83.85928, 38.456348],
          [-83.641852, 38.525381],
          [-83.579966, 38.429935],
          [-83.452848, 38.381799],
          [-83.50716, 38.269447],
          [-83.635195, 38.187525],
          [-83.638854, 38.17827],
          [-83.646546, 38.170963]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21081",
      state: "21",
      county: "081",
      name: "Grant County",
      censusArea: 257.965,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.61574, 38.802192],
          [-84.597775, 38.79117],
          [-84.532288, 38.791937],
          [-84.476299, 38.542723],
          [-84.557375, 38.492917],
          [-84.560674, 38.492378],
          [-84.580539, 38.473039],
          [-84.774322, 38.618594],
          [-84.785788, 38.720459],
          [-84.780766, 38.765208],
          [-84.660107, 38.777293],
          [-84.656167, 38.790117],
          [-84.630007, 38.802901],
          [-84.61574, 38.802192]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21093",
      state: "21",
      county: "093",
      name: "Hardin County",
      censusArea: 623.278,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.047598, 37.449933],
          [-86.114095, 37.567131],
          [-86.27527, 37.593519],
          [-86.151285, 37.798878],
          [-86.007432, 37.818439],
          [-85.975948, 37.875793],
          [-85.998609, 37.997775],
          [-85.951467, 38.005608],
          [-85.947952, 38.007083],
          [-85.945044, 38.002426],
          [-85.938321, 37.998756],
          [-85.854871, 37.945385],
          [-85.838198, 37.879284],
          [-85.737777, 37.81179],
          [-85.680488, 37.732284],
          [-85.876006, 37.543259],
          [-85.892804, 37.440308],
          [-86.047598, 37.449933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21109",
      state: "21",
      county: "109",
      name: "Jackson County",
      censusArea: 345.201,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.784975, 37.348717],
          [-83.943718, 37.250551],
          [-84.137898, 37.319759],
          [-84.199489, 37.522858],
          [-84.090364, 37.566227],
          [-83.965244, 37.581421],
          [-83.90557, 37.542856],
          [-83.900265, 37.535795],
          [-83.893514, 37.531119],
          [-83.8931, 37.526583],
          [-83.888697, 37.522189],
          [-83.885262, 37.517688],
          [-83.784975, 37.348717]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21125",
      state: "21",
      county: "125",
      name: "Laurel County",
      censusArea: 433.953,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.871315, 37.054919],
          [-83.959385, 36.977128],
          [-84.091853, 36.955992],
          [-84.297416, 36.945931],
          [-84.345872, 36.939496],
          [-84.368212, 36.945588],
          [-84.358024, 36.9594],
          [-84.289076, 37.15174],
          [-84.253696, 37.239516],
          [-84.137898, 37.319759],
          [-83.943718, 37.250551],
          [-83.96899, 37.174794],
          [-83.871315, 37.054919]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21131",
      state: "21",
      county: "131",
      name: "Leslie County",
      censusArea: 400.844,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.507886, 37.235877],
          [-83.460992, 37.293848],
          [-83.351774, 37.295682],
          [-83.32462, 37.225561],
          [-83.201887, 37.17413],
          [-83.182104, 37.021115],
          [-83.26002, 36.968951],
          [-83.489813, 36.895413],
          [-83.500388, 36.895504],
          [-83.50481, 36.903917],
          [-83.489423, 36.911302],
          [-83.494254, 36.919621],
          [-83.509482, 36.923375],
          [-83.509083, 36.938509],
          [-83.551303, 37.16751],
          [-83.507886, 37.235877]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40037",
      state: "40",
      county: "037",
      name: "Creek County",
      censusArea: 950.141,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.033118, 35.856824],
          [-96.192482, 35.857021],
          [-96.192563, 35.639087],
          [-96.620648, 35.639005],
          [-96.621097, 35.941519],
          [-96.621441, 36.159929],
          [-96.621333, 36.163553],
          [-96.297888, 36.162279],
          [-96.297786, 36.075779],
          [-96.029583, 36.075366],
          [-96.029544, 35.988387],
          [-96.033118, 35.856824]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40047",
      state: "40",
      county: "047",
      name: "Garfield County",
      censusArea: 1058.466,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.462459, 36.593627],
          [-97.461603, 36.593637],
          [-97.460792, 36.164462],
          [-97.675617, 36.164663],
          [-98.103904, 36.164877],
          [-98.104427, 36.463105],
          [-98.104286, 36.593578],
          [-97.462459, 36.593627]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37183",
      state: "37",
      county: "183",
      name: "Wake County",
      censusArea: 835.219,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.254903, 35.81773],
          [-78.473733, 35.701551],
          [-78.566633, 35.62895],
          [-78.708629, 35.519081],
          [-78.913296, 35.583064],
          [-78.995054, 35.610233],
          [-78.971335, 35.675614],
          [-78.909427, 35.842941],
          [-78.90617, 35.867912],
          [-78.831781, 35.867314],
          [-78.69793, 36.012306],
          [-78.749, 36.071246],
          [-78.546497, 36.021804],
          [-78.307314, 35.896587],
          [-78.254903, 35.81773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37187",
      state: "37",
      county: "187",
      name: "Washington County",
      censusArea: 348.135,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.692376, 35.945342],
          [-76.691766, 35.944566],
          [-76.673865, 35.935089],
          [-76.667547, 35.933509],
          [-76.657017, 35.935089],
          [-76.608052, 35.936668],
          [-76.60384, 35.939827],
          [-76.586992, 35.941933],
          [-76.528551, 35.944039],
          [-76.507491, 35.949831],
          [-76.496961, 35.955096],
          [-76.473795, 35.960888],
          [-76.460632, 35.970365],
          [-76.398242, 35.984317],
          [-76.389818, 35.980105],
          [-76.38192, 35.971681],
          [-76.381394, 35.96273],
          [-76.36521, 35.944697],
          [-76.353444, 35.861353],
          [-76.405993, 35.785045],
          [-76.395283, 35.697469],
          [-76.546091, 35.705033],
          [-76.638031, 35.705233],
          [-76.845119, 35.705409],
          [-76.760842, 35.864822],
          [-76.692376, 35.945342]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38003",
      state: "38",
      county: "003",
      name: "Barnes County",
      censusArea: 1491.554,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.033862, 46.630727],
          [-98.439056, 46.63112],
          [-98.439466, 46.979658],
          [-98.46753, 46.979594],
          [-98.467476, 47.240453],
          [-97.961208, 47.240512],
          [-97.706034, 47.239978],
          [-97.706027, 46.978558],
          [-97.681508, 46.978481],
          [-97.682006, 46.629928],
          [-98.033862, 46.630727]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38013",
      state: "38",
      county: "013",
      name: "Burke County",
      censusArea: 1103.568,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.938793, 48.998998],
          [-102.216993, 48.998553],
          [-102.211301, 48.998554],
          [-102.151847, 48.998798],
          [-102.131614, 48.998842],
          [-102.021144, 48.999015],
          [-102.021655, 48.807108],
          [-102.152164, 48.8072],
          [-102.152545, 48.720294],
          [-102.2336, 48.72029],
          [-102.233892, 48.546339],
          [-102.885763, 48.54639],
          [-102.885775, 48.560628],
          [-102.886015, 48.561179],
          [-102.885775, 48.633298],
          [-102.939932, 48.720469],
          [-102.938793, 48.998998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38025",
      state: "38",
      county: "025",
      name: "Dunn County",
      censusArea: 2008.46,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.099197, 47.328714],
          [-103.099867, 47.674416],
          [-102.642766, 47.673911],
          [-102.642266, 47.823713],
          [-102.561965, 47.780111],
          [-102.385758, 47.758008],
          [-102.260951, 47.781409],
          [-102.265452, 47.624307],
          [-102.20535, 47.574506],
          [-102.205601, 47.328071],
          [-102.144453, 47.328079],
          [-102.144771, 47.010307],
          [-102.525463, 47.01024],
          [-102.525462, 46.981307],
          [-103.032681, 46.98058],
          [-103.032569, 47.328692],
          [-103.099197, 47.328714]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38041",
      state: "38",
      county: "041",
      name: "Hettinger County",
      censusArea: 1132.221,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.928003, 46.630065],
          [-102.096585, 46.631024],
          [-102.050986, 46.630871],
          [-102.046929, 46.283606],
          [-101.997888, 46.20548],
          [-102.497475, 46.206077],
          [-102.497449, 46.283196],
          [-102.924547, 46.281519],
          [-102.924509, 46.282589],
          [-102.928003, 46.630065]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38055",
      state: "38",
      county: "055",
      name: "Mclean County",
      censusArea: 2110.876,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.965156, 47.156631],
          [-100.977629, 47.249992],
          [-101.060424, 47.293293],
          [-101.257031, 47.264895],
          [-101.345735, 47.291897],
          [-101.362536, 47.367998],
          [-101.440139, 47.477299],
          [-101.370866, 47.518696],
          [-101.428037, 47.561986],
          [-101.641696, 47.528379],
          [-101.707537, 47.538278],
          [-101.826148, 47.494402],
          [-102.036648, 47.572705],
          [-102.20535, 47.574506],
          [-102.265452, 47.624307],
          [-102.260951, 47.781409],
          [-102.385758, 47.758008],
          [-102.385565, 47.84775],
          [-101.871862, 47.847589],
          [-100.971325, 47.848703],
          [-100.585161, 47.84748],
          [-100.585106, 47.673912],
          [-100.672262, 47.674128],
          [-100.673445, 47.32772],
          [-100.749334, 47.327685],
          [-100.74937, 47.157245],
          [-100.965156, 47.156631]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38069",
      state: "38",
      county: "069",
      name: "Pierce County",
      censusArea: 1018.6,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.492919, 48.370946],
          [-99.846613, 48.371298],
          [-99.848222, 48.021424],
          [-99.812133, 48.021451],
          [-99.812167, 47.847257],
          [-100.069206, 47.847301],
          [-100.164591, 47.847465],
          [-100.166099, 47.847581],
          [-100.197996, 47.8476],
          [-100.197558, 48.021857],
          [-100.237092, 48.021866],
          [-100.237332, 48.371246],
          [-100.276401, 48.371334],
          [-100.27666, 48.544813],
          [-100.145858, 48.545211],
          [-99.493189, 48.544834],
          [-99.493356, 48.373675],
          [-99.492919, 48.370946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38087",
      state: "38",
      county: "087",
      name: "Slope County",
      censusArea: 1214.922,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.045335, 46.540929],
          [-103.800881, 46.540747],
          [-103.800825, 46.629563],
          [-103.60921, 46.629797],
          [-103.231566, 46.629918],
          [-102.928003, 46.630065],
          [-102.924509, 46.282589],
          [-102.924547, 46.281519],
          [-102.969946, 46.281144],
          [-102.995454, 46.280713],
          [-104.045633, 46.280188],
          [-104.045469, 46.324545],
          [-104.045462, 46.341895],
          [-104.045335, 46.540929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38095",
      state: "38",
      county: "095",
      name: "Towner County",
      censusArea: 1024.561,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.999819, 48.999769],
          [-98.99989, 48.718129],
          [-98.970368, 48.718089],
          [-98.970392, 48.544232],
          [-98.970975, 48.370472],
          [-99.200306, 48.370658],
          [-99.492919, 48.370946],
          [-99.493356, 48.373675],
          [-99.493189, 48.544834],
          [-99.493002, 48.718359],
          [-99.525895, 48.718384],
          [-99.5257, 48.999423],
          [-98.999819, 48.999769]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39009",
      state: "39",
      county: "009",
      name: "Athens County",
      censusArea: 503.598,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.055779, 39.460053],
          [-81.844863, 39.450216],
          [-81.853668, 39.318165],
          [-81.721808, 39.269597],
          [-81.721551, 39.212448],
          [-81.740061, 39.190652],
          [-81.749853, 39.186489],
          [-81.749689, 39.180742],
          [-82.268583, 39.20376],
          [-82.263952, 39.253289],
          [-82.263767, 39.253786],
          [-82.263645, 39.255898],
          [-82.263682, 39.256194],
          [-82.260243, 39.292915],
          [-82.298476, 39.294996],
          [-82.289663, 39.384195],
          [-82.28041, 39.472906],
          [-82.167379, 39.466307],
          [-82.159161, 39.556574],
          [-82.047528, 39.550825],
          [-82.055779, 39.460053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39021",
      state: "39",
      county: "021",
      name: "Champaign County",
      censusArea: 428.669,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.551338, 40.22937],
          [-83.494498, 40.225467],
          [-83.503714, 40.111468],
          [-83.516155, 40.010188],
          [-84.036069, 40.040182],
          [-84.022919, 40.183945],
          [-84.014763, 40.273459],
          [-83.551338, 40.22937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54109",
      state: "54",
      county: "109",
      name: "Wyoming County",
      censusArea: 499.453,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.514228, 37.791211],
          [-81.427781, 37.749352],
          [-81.30413, 37.546564],
          [-81.222121, 37.510416],
          [-81.311201, 37.424509],
          [-81.465845, 37.484703],
          [-81.654431, 37.523312],
          [-81.729433, 37.494585],
          [-81.855939, 37.54891],
          [-81.802937, 37.660508],
          [-81.701734, 37.701409],
          [-81.764737, 37.752108],
          [-81.607532, 37.788709],
          [-81.514228, 37.791211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55015",
      state: "55",
      county: "015",
      name: "Calumet County",
      censusArea: 318.236,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.40407, 44.244102],
          [-88.354532, 44.243863],
          [-88.351301, 44.243768],
          [-88.346334, 44.243718],
          [-88.303974, 44.243335],
          [-88.192611, 44.241947],
          [-88.04324, 44.241018],
          [-88.041794, 43.891692],
          [-88.162274, 43.891511],
          [-88.161654, 43.937683],
          [-88.314948, 43.937665],
          [-88.404187, 43.9382],
          [-88.404425, 44.209254],
          [-88.404043, 44.226903],
          [-88.40407, 44.244102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55027",
      state: "55",
      county: "027",
      name: "Dodge County",
      censusArea: 875.625,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.832098, 43.197897],
          [-89.00892, 43.197721],
          [-89.009139, 43.28483],
          [-89.006848, 43.633045],
          [-88.886052, 43.63354],
          [-88.401041, 43.630933],
          [-88.400426, 43.543532],
          [-88.417987, 43.194697],
          [-88.535843, 43.196048],
          [-88.832098, 43.197897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49027",
      state: "49",
      county: "027",
      name: "Millard County",
      censusArea: 6572.425,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.354345, 39.553871],
          [-112.220362, 39.554054],
          [-112.195578, 39.3296],
          [-112.015677, 39.314455],
          [-112.014198, 39.045355],
          [-112.065343, 38.957677],
          [-112.132228, 38.956716],
          [-112.224831, 38.865105],
          [-112.218662, 38.727674],
          [-112.309841, 38.676233],
          [-112.447764, 38.681162],
          [-112.515196, 38.572325],
          [-112.515188, 38.573703],
          [-112.919308, 38.574709],
          [-112.919362, 38.576529],
          [-114.050154, 38.57292],
          [-114.049893, 38.677365],
          [-114.047728, 39.542742],
          [-113.815748, 39.544097],
          [-113.81557, 39.552624],
          [-112.462119, 39.552449],
          [-112.354345, 39.553871]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49033",
      state: "49",
      county: "033",
      name: "Rich County",
      censusArea: 1028.775,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.046689, 42.001567],
          [-111.046402, 41.579845],
          [-111.046264, 41.377731],
          [-111.0466, 41.360692],
          [-111.046551, 41.251716],
          [-111.26482, 41.144253],
          [-111.221378, 41.212852],
          [-111.331662, 41.357364],
          [-111.42056, 41.36137],
          [-111.510839, 41.4231],
          [-111.442949, 41.531365],
          [-111.426855, 41.677864],
          [-111.471321, 41.928157],
          [-111.507264, 41.999518],
          [-111.425535, 42.00084],
          [-111.420898, 42.000793],
          [-111.415873, 42.000748],
          [-111.046689, 42.001567]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49041",
      state: "49",
      county: "041",
      name: "Sevier County",
      censusArea: 1910.577,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.305825, 38.510178],
          [-111.756242, 38.509977],
          [-111.766741, 38.501977],
          [-112.518504, 38.509852],
          [-112.522426, 38.519026],
          [-112.515965, 38.529047],
          [-112.517822, 38.553982],
          [-112.515673, 38.556623],
          [-112.518896, 38.567408],
          [-112.515196, 38.572325],
          [-112.447764, 38.681162],
          [-112.309841, 38.676233],
          [-112.218662, 38.727674],
          [-112.224831, 38.865105],
          [-112.132228, 38.956716],
          [-112.065343, 38.957677],
          [-112.014198, 39.045355],
          [-111.853462, 39.032981],
          [-111.29936, 39.032264],
          [-111.305825, 38.510178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49055",
      state: "49",
      county: "055",
      name: "Wayne County",
      censusArea: 2460.676,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.927882, 38.151885],
          [-110.687406, 38.151385],
          [-111.41443, 38.150082],
          [-111.8428, 38.15128],
          [-111.848871, 38.425001],
          [-111.766741, 38.501977],
          [-111.756242, 38.509977],
          [-111.305825, 38.510178],
          [-111.305858, 38.499978],
          [-110.025402, 38.499981],
          [-110.015126, 38.364604],
          [-109.887266, 38.190214],
          [-109.927882, 38.151885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50013",
      state: "50",
      county: "013",
      name: "Grand Isle County",
      censusArea: 81.811,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.230777, 44.927437],
          [-73.226274, 44.919505],
          [-73.19131, 44.904762],
          [-73.19402, 44.886844],
          [-73.256321, 44.759444],
          [-73.228854, 44.722866],
          [-73.233209, 44.649791],
          [-73.361952, 44.563064],
          [-73.374389, 44.575455],
          [-73.390231, 44.618353],
          [-73.365326, 44.703294],
          [-73.339958, 44.778893],
          [-73.369647, 44.829136],
          [-73.35945, 44.915684],
          [-73.350188, 44.994304],
          [-73.343124, 45.01084],
          [-73.191948, 45.013576],
          [-73.230777, 44.927437]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51003",
      state: "51",
      county: "003",
      name: "Albemarle County",
      censusArea: 720.698,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.208092, 38.13315],
          [-78.254287, 38.063831],
          [-78.306607, 38.006399],
          [-78.31025, 38.002728],
          [-78.491567, 37.796712],
          [-78.510971, 37.757594],
          [-78.644036, 37.733143],
          [-78.839211, 38.047565],
          [-78.77923, 38.083451],
          [-78.749354, 38.206621],
          [-78.663174, 38.277923],
          [-78.369797, 38.184757],
          [-78.3445, 38.177931],
          [-78.208092, 38.13315]
        ],
        [
          [-78.520375, 38.018355],
          [-78.469733, 38.011439],
          [-78.44905, 38.058728],
          [-78.495034, 38.065337],
          [-78.520375, 38.018355]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51009",
      state: "51",
      county: "009",
      name: "Amherst County",
      censusArea: 473.934,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.183302, 37.465908],
          [-79.349319, 37.52484],
          [-79.436926, 37.617004],
          [-79.34501, 37.661129],
          [-79.264384, 37.79472],
          [-79.171926, 37.803135],
          [-79.071417, 37.778303],
          [-79.067355, 37.720255],
          [-78.986017, 37.695546],
          [-78.869245, 37.542095],
          [-79.018526, 37.429917],
          [-79.085244, 37.394743],
          [-79.183302, 37.465908]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51023",
      state: "51",
      county: "023",
      name: "Botetourt County",
      censusArea: 541.202,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.847599, 37.309177],
          [-79.865099, 37.328417],
          [-79.945616, 37.362997],
          [-80.062622, 37.408325],
          [-80.073985, 37.422305],
          [-79.969037, 37.544419],
          [-80.020519, 37.647459],
          [-79.91756, 37.701848],
          [-79.816702, 37.800989],
          [-79.674006, 37.763052],
          [-79.683594, 37.661668],
          [-79.498553, 37.532887],
          [-79.626126, 37.455284],
          [-79.628157, 37.453633],
          [-79.634201, 37.45705],
          [-79.659053, 37.485206],
          [-79.690053, 37.476403],
          [-79.719063, 37.448989],
          [-79.790467, 37.40883],
          [-79.814193, 37.403687],
          [-79.778608, 37.381816],
          [-79.788151, 37.351116],
          [-79.81075, 37.335305],
          [-79.844191, 37.317785],
          [-79.847599, 37.309177]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51035",
      state: "51",
      county: "035",
      name: "Carroll County",
      censusArea: 474.69,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.042406, 36.806422],
          [-80.740483, 36.879001],
          [-80.633987, 36.93178],
          [-80.46418, 36.708718],
          [-80.525522, 36.644114],
          [-80.578148, 36.662118],
          [-80.612158, 36.558127],
          [-80.687539, 36.561411],
          [-80.744101, 36.561686],
          [-80.837089, 36.559154],
          [-80.837641, 36.559118],
          [-80.837954, 36.559131],
          [-80.914173, 36.650246],
          [-80.877274, 36.692215],
          [-80.917573, 36.694133],
          [-80.920497, 36.678139],
          [-80.932271, 36.671593],
          [-81.042406, 36.806422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51053",
      state: "51",
      county: "053",
      name: "Dinwiddie County",
      censusArea: 503.716,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.73363, 36.945676],
          [-77.889813, 36.988818],
          [-77.900338, 37.143882],
          [-77.795926, 37.192592],
          [-77.65061, 37.265112],
          [-77.447521, 37.22271],
          [-77.399203, 37.17085],
          [-77.396791, 37.152758],
          [-77.398498, 36.992985],
          [-77.616204, 36.879202],
          [-77.619029, 36.877844],
          [-77.62671, 36.88127],
          [-77.631207, 36.88761],
          [-77.638073, 36.885989],
          [-77.632266, 36.878709],
          [-77.639169, 36.873752],
          [-77.648045, 36.881507],
          [-77.645705, 36.888531],
          [-77.648037, 36.896884],
          [-77.658036, 36.894514],
          [-77.73363, 36.945676]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51065",
      state: "51",
      county: "065",
      name: "Fluvanna County",
      censusArea: 286.005,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.306607, 38.006399],
          [-78.06331, 37.905108],
          [-78.151822, 37.770443],
          [-78.159278, 37.748528],
          [-78.239748, 37.690495],
          [-78.405596, 37.735263],
          [-78.491567, 37.796712],
          [-78.31025, 38.002728],
          [-78.306607, 38.006399]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01003",
      state: "01",
      county: "003",
      name: "Baldwin County",
      censusArea: 1589.784,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.598927, 30.997454],
          [-87.594111, 30.976335],
          [-87.601355, 30.936294],
          [-87.622203, 30.897508],
          [-87.634938, 30.865886],
          [-87.624137, 30.845713],
          [-87.532607, 30.743489],
          [-87.502926, 30.722369],
          [-87.497515, 30.720123],
          [-87.481225, 30.716508],
          [-87.449362, 30.698913],
          [-87.407118, 30.671796],
          [-87.396997, 30.65364],
          [-87.394219, 30.641699],
          [-87.394479, 30.625192],
          [-87.449921, 30.51469],
          [-87.449841, 30.514369],
          [-87.448332, 30.513063],
          [-87.430578, 30.491096],
          [-87.419177, 30.410198],
          [-87.427478, 30.408398],
          [-87.440678, 30.391498],
          [-87.451378, 30.367199],
          [-87.450962, 30.346262],
          [-87.450078, 30.3111],
          [-87.452378, 30.300201],
          [-87.518324, 30.280435],
          [-87.544533, 30.275659],
          [-87.558097, 30.274437],
          [-87.581362, 30.269257],
          [-87.656888, 30.249709],
          [-87.73553, 30.240679],
          [-87.80056, 30.229365],
          [-87.838462, 30.227185],
          [-87.926119, 30.230373],
          [-87.962253, 30.229522],
          [-87.999996, 30.225753],
          [-88.014572, 30.222366],
          [-88.028401, 30.221132],
          [-88.029272, 30.222714],
          [-88.023991, 30.23039],
          [-87.966847, 30.235618],
          [-87.948979, 30.256564],
          [-87.936041, 30.261469],
          [-87.918247, 30.253308],
          [-87.913762, 30.247837],
          [-87.90046, 30.241531],
          [-87.893201, 30.239237],
          [-87.879343, 30.23859],
          [-87.860085, 30.240289],
          [-87.817743, 30.254292],
          [-87.802087, 30.253054],
          [-87.78775, 30.254244],
          [-87.766626, 30.262353],
          [-87.755263, 30.277292],
          [-87.755516, 30.291217],
          [-87.772758, 30.311701],
          [-87.796717, 30.324198],
          [-87.809266, 30.332702],
          [-87.82988, 30.353809],
          [-87.837239, 30.369324],
          [-87.845132, 30.377446],
          [-87.853806, 30.378481],
          [-87.865017, 30.38345],
          [-87.906343, 30.40938],
          [-87.908908, 30.41424],
          [-87.914136, 30.446144],
          [-87.920031, 30.470645],
          [-87.924211, 30.4761],
          [-87.931902, 30.4811],
          [-87.933355, 30.487357],
          [-87.911141, 30.525848],
          [-87.905343, 30.537566],
          [-87.901711, 30.550879],
          [-87.904168, 30.565985],
          [-87.907891, 30.573114],
          [-87.911431, 30.576261],
          [-87.914956, 30.585893],
          [-87.91253, 30.615795],
          [-87.919346, 30.63606],
          [-87.93107, 30.652694],
          [-87.936717, 30.657432],
          [-87.955989, 30.658862],
          [-87.981196, 30.67509],
          [-88.008396, 30.684956],
          [-88.026319, 30.753358],
          [-87.944546, 30.827046],
          [-87.981261, 30.886895],
          [-87.941152, 31.048161],
          [-87.972869, 31.162694],
          [-87.943748, 31.161341],
          [-87.946588, 31.19293],
          [-87.913637, 31.187356],
          [-87.765152, 31.297346],
          [-87.61589, 31.244458],
          [-87.615431, 30.997361],
          [-87.598927, 30.997454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01027",
      state: "01",
      county: "027",
      name: "Clay County",
      censusArea: 603.961,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.85189, 33.498742],
          [-85.887782, 33.469427],
          [-85.782735, 33.469349],
          [-85.765427, 33.498593],
          [-85.643482, 33.495885],
          [-85.653654, 33.106634],
          [-85.974715, 33.105286],
          [-86.00917, 33.09026],
          [-86.17437, 33.104394],
          [-86.17283, 33.195681],
          [-86.120567, 33.194511],
          [-86.118198, 33.29632],
          [-85.980293, 33.29419],
          [-85.976525, 33.38187],
          [-85.923762, 33.396206],
          [-85.904909, 33.498655],
          [-85.85189, 33.498742]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01041",
      state: "01",
      county: "041",
      name: "Crenshaw County",
      censusArea: 608.84,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.193951, 31.440072],
          [-86.277031, 31.455602],
          [-86.280155, 31.52851],
          [-86.398536, 31.451325],
          [-86.39994, 31.527128],
          [-86.499213, 31.525331],
          [-86.499533, 31.655247],
          [-86.448635, 31.655617],
          [-86.448198, 31.964629],
          [-86.406276, 32.050731],
          [-86.303616, 32.051664],
          [-86.302217, 31.965065],
          [-86.191379, 31.966453],
          [-86.199378, 31.79045],
          [-86.148339, 31.790951],
          [-86.145895, 31.617741],
          [-86.14395, 31.537675],
          [-86.194784, 31.529949],
          [-86.193951, 31.440072]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01057",
      state: "01",
      county: "057",
      name: "Fayette County",
      censusArea: 627.66,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.951785, 33.91993],
          [-87.635932, 33.915251],
          [-87.63604, 33.871999],
          [-87.531602, 33.867618],
          [-87.528338, 33.692049],
          [-87.423843, 33.689112],
          [-87.423701, 33.602096],
          [-87.631718, 33.609833],
          [-87.666661, 33.521667],
          [-87.840683, 33.524839],
          [-87.85856, 33.525326],
          [-87.946519, 33.524065],
          [-87.951785, 33.91993]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01073",
      state: "01",
      county: "073",
      name: "Jefferson County",
      censusArea: 1111.276,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.023586, 33.246422],
          [-87.026846, 33.246459],
          [-87.065738, 33.246907],
          [-87.065754, 33.275937],
          [-87.074634, 33.276018],
          [-87.074602, 33.279682],
          [-87.087546, 33.279666],
          [-87.087497, 33.290675],
          [-87.126756, 33.290844],
          [-87.127142, 33.306013],
          [-87.162007, 33.306587],
          [-87.162175, 33.321012],
          [-87.179638, 33.321112],
          [-87.179754, 33.32801],
          [-87.180006, 33.342979],
          [-87.194841, 33.343069],
          [-87.330816, 33.491501],
          [-87.266923, 33.512929],
          [-87.185794, 33.555882],
          [-87.17943, 33.613794],
          [-87.057757, 33.656363],
          [-87.005965, 33.787237],
          [-86.953664, 33.815297],
          [-86.759144, 33.840617],
          [-86.577799, 33.765316],
          [-86.542758, 33.765173],
          [-86.516783, 33.545896],
          [-86.60451, 33.467053],
          [-86.674418, 33.466521],
          [-86.830597, 33.332015],
          [-87.023586, 33.246422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01091",
      state: "01",
      county: "091",
      name: "Marengo County",
      censusArea: 976.882,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.667769, 31.991355],
          [-87.944233, 31.989872],
          [-88.07349, 31.990182],
          [-88.070615, 32.0617],
          [-88.007077, 32.185313],
          [-88.019145, 32.28524],
          [-87.930661, 32.310574],
          [-88.046335, 32.377087],
          [-88.031623, 32.43361],
          [-87.85342, 32.532086],
          [-87.845144, 32.546687],
          [-87.822317, 32.543748],
          [-87.823383, 32.520443],
          [-87.812559, 32.52456],
          [-87.728744, 32.480918],
          [-87.524485, 32.482028],
          [-87.52429, 32.307293],
          [-87.47308, 32.307614],
          [-87.472206, 32.264919],
          [-87.523405, 32.25483],
          [-87.521946, 32.132816],
          [-87.624005, 32.132345],
          [-87.622756, 32.005482],
          [-87.667769, 31.991355]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01103",
      state: "01",
      county: "103",
      name: "Morgan County",
      censusArea: 579.337,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.110111, 34.313799],
          [-87.105913, 34.587643],
          [-87.105073, 34.686037],
          [-86.942899, 34.598053],
          [-86.790056, 34.55079],
          [-86.689353, 34.586425],
          [-86.550166, 34.545963],
          [-86.581936, 34.304694],
          [-87.092311, 34.312297],
          [-87.110111, 34.313799]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01115",
      state: "01",
      county: "115",
      name: "St. Clair County",
      censusArea: 631.902,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.065272, 33.842198],
          [-86.066439, 33.841094],
          [-86.066972, 33.839198],
          [-86.043993, 33.763595],
          [-86.145562, 33.679098],
          [-86.235328, 33.494532],
          [-86.281999, 33.509895],
          [-86.378665, 33.390983],
          [-86.378222, 33.502411],
          [-86.481939, 33.502544],
          [-86.516783, 33.545896],
          [-86.542758, 33.765173],
          [-86.577799, 33.765316],
          [-86.577528, 33.801977],
          [-86.377532, 33.861706],
          [-86.325622, 33.940147],
          [-86.199164, 33.988918],
          [-86.065272, 33.842198]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01129",
      state: "01",
      county: "129",
      name: "Washington County",
      censusArea: 1080.207,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.432007, 31.114298],
          [-88.438104, 31.23006],
          [-88.438211, 31.231252],
          [-88.445182, 31.355855],
          [-88.445209, 31.355969],
          [-88.448686, 31.420888],
          [-88.44866, 31.421277],
          [-88.449446, 31.435837],
          [-88.459478, 31.621652],
          [-88.459722, 31.624002],
          [-88.464425, 31.697881],
          [-88.464428, 31.697952],
          [-88.088288, 31.699303],
          [-88.086946, 31.686181],
          [-88.080629, 31.67785],
          [-88.078711, 31.669618],
          [-88.072234, 31.5945],
          [-87.906143, 31.491752],
          [-87.908068, 31.323041],
          [-87.959619, 31.316206],
          [-87.946588, 31.19293],
          [-87.943748, 31.161341],
          [-87.972869, 31.162694],
          [-88.022649, 31.144267],
          [-88.329782, 31.143821],
          [-88.432007, 31.114298]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21147",
      state: "21",
      county: "147",
      name: "Mc Creary County",
      censusArea: 426.803,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.227295, 36.591685],
          [-84.261333, 36.591981],
          [-84.778455, 36.603222],
          [-84.673964, 36.739542],
          [-84.579339, 36.814397],
          [-84.578158, 36.867133],
          [-84.358024, 36.9594],
          [-84.368212, 36.945588],
          [-84.345872, 36.939496],
          [-84.297416, 36.945931],
          [-84.350006, 36.780681],
          [-84.307598, 36.676809],
          [-84.227295, 36.591685]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21165",
      state: "21",
      county: "165",
      name: "Menifee County",
      censusArea: 203.585,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.709507, 38.009317],
          [-83.58053, 38.011702],
          [-83.498846, 38.05062],
          [-83.433942, 38.033974],
          [-83.49508, 37.861906],
          [-83.63106, 37.825372],
          [-83.768783, 37.918371],
          [-83.75943, 37.998731],
          [-83.756983, 37.992618],
          [-83.751818, 37.997098],
          [-83.749591, 37.996733],
          [-83.744956, 38.001578],
          [-83.747451, 38.003967],
          [-83.744082, 38.006919],
          [-83.740512, 38.007149],
          [-83.737673, 38.009972],
          [-83.735911, 38.007057],
          [-83.733293, 38.007642],
          [-83.730982, 38.010524],
          [-83.727154, 38.008629],
          [-83.727146, 38.006999],
          [-83.722177, 38.006011],
          [-83.718497, 38.003397],
          [-83.715208, 38.004399],
          [-83.712187, 38.009693],
          [-83.709507, 38.009317]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21181",
      state: "21",
      county: "181",
      name: "Nicholas County",
      censusArea: 195.171,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.980031, 38.439159],
          [-83.848355, 38.296949],
          [-83.850355, 38.296258],
          [-83.977924, 38.192118],
          [-84.120185, 38.275516],
          [-84.19405, 38.37175],
          [-84.1021, 38.459379],
          [-83.980031, 38.439159]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19041",
      state: "19",
      county: "041",
      name: "Clay County",
      censusArea: 567.238,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.388078, 43.255221],
          [-94.913723, 43.255054],
          [-94.91389, 42.9097],
          [-95.388182, 42.909897],
          [-95.388078, 43.255221]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19043",
      state: "19",
      county: "043",
      name: "Clayton County",
      censusArea: 778.539,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.163064, 42.986781],
          [-91.14556, 42.90798],
          [-91.143375, 42.90467],
          [-91.117411, 42.895837],
          [-91.100565, 42.883078],
          [-91.077643, 42.803798],
          [-91.069549, 42.769628],
          [-91.064896, 42.757272],
          [-91.053733, 42.738238],
          [-90.976314, 42.695996],
          [-90.949213, 42.685573],
          [-90.896961, 42.674407],
          [-90.897415, 42.645714],
          [-91.132766, 42.645844],
          [-91.607059, 42.643973],
          [-91.605307, 43.081653],
          [-91.177222, 43.080247],
          [-91.178087, 43.062044],
          [-91.175167, 43.041267],
          [-91.163064, 42.986781]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19049",
      state: "19",
      county: "049",
      name: "Dallas County",
      censusArea: 588.447,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.28034, 41.862999],
          [-94.164138, 41.863244],
          [-93.815527, 41.863419],
          [-93.814282, 41.600448],
          [-93.790612, 41.511916],
          [-93.896807, 41.503098],
          [-94.241593, 41.503679],
          [-94.280057, 41.601312],
          [-94.28034, 41.862999]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19057",
      state: "19",
      county: "057",
      name: "Des Moines County",
      censusArea: 416.123,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.37071, 41.074112],
          [-90.949383, 41.07271],
          [-90.948523, 41.070248],
          [-90.945549, 41.06173],
          [-90.942253, 41.034702],
          [-90.952233, 40.954047],
          [-90.962916, 40.924957],
          [-90.968995, 40.919127],
          [-90.9985, 40.90812],
          [-91.009536, 40.900565],
          [-91.092993, 40.821079],
          [-91.097553, 40.808433],
          [-91.097031, 40.802471],
          [-91.094728, 40.797833],
          [-91.11194, 40.697018],
          [-91.112258, 40.696218],
          [-91.182551, 40.7081],
          [-91.409324, 40.812682],
          [-91.372095, 40.812631],
          [-91.37071, 41.074112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19073",
      state: "19",
      county: "073",
      name: "Greene County",
      censusArea: 569.574,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.397526, 42.209161],
          [-94.164704, 42.20992],
          [-94.164138, 41.863244],
          [-94.28034, 41.862999],
          [-94.628724, 41.862763],
          [-94.628806, 42.209396],
          [-94.397526, 42.209161]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19081",
      state: "19",
      county: "081",
      name: "Hancock County",
      censusArea: 571.005,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.971238, 42.907762],
          [-93.970415, 43.255358],
          [-93.497635, 43.255468],
          [-93.498617, 42.908512],
          [-93.971238, 42.907762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19091",
      state: "19",
      county: "091",
      name: "Humboldt County",
      censusArea: 434.352,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.443083, 42.645164],
          [-94.442954, 42.908073],
          [-93.971238, 42.907762],
          [-93.971714, 42.644707],
          [-94.443083, 42.645164]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19099",
      state: "19",
      county: "099",
      name: "Jasper County",
      censusArea: 730.42,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.231858, 41.862711],
          [-92.765999, 41.862373],
          [-92.756508, 41.508794],
          [-92.871421, 41.508522],
          [-93.328614, 41.507824],
          [-93.348681, 41.600999],
          [-93.347933, 41.863104],
          [-93.231858, 41.862711]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19115",
      state: "19",
      county: "115",
      name: "Louisa County",
      censusArea: 401.766,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.37071, 41.074112],
          [-91.370298, 41.161247],
          [-91.485717, 41.162405],
          [-91.483995, 41.423848],
          [-91.368521, 41.423178],
          [-91.369332, 41.336063],
          [-91.079657, 41.333727],
          [-91.114186, 41.250029],
          [-91.113648, 41.241401],
          [-91.049808, 41.178033],
          [-91.019036, 41.16491],
          [-91.005503, 41.165622],
          [-90.997906, 41.162564],
          [-90.989663, 41.155716],
          [-90.946627, 41.096632],
          [-90.949383, 41.072711],
          [-90.949383, 41.07271],
          [-91.37071, 41.074112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19131",
      state: "19",
      county: "131",
      name: "Mitchell County",
      censusArea: 469.133,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.024348, 43.499572],
          [-93.007871, 43.499604],
          [-92.870277, 43.499548],
          [-92.790317, 43.499567],
          [-92.752088, 43.500084],
          [-92.707312, 43.500069],
          [-92.692786, 43.500063],
          [-92.689033, 43.500062],
          [-92.67258, 43.500055],
          [-92.653318, 43.50005],
          [-92.649194, 43.500049],
          [-92.553161, 43.5003],
          [-92.553128, 43.5003],
          [-92.554381, 43.212813],
          [-93.024092, 43.212812],
          [-93.024143, 43.255538],
          [-93.024348, 43.499572]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19147",
      state: "19",
      county: "147",
      name: "Palo Alto County",
      censusArea: 563.844,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.443137, 43.255014],
          [-94.443136, 42.909498],
          [-94.442954, 42.908073],
          [-94.91389, 42.9097],
          [-94.913723, 43.255054],
          [-94.443137, 43.255014]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19157",
      state: "19",
      county: "157",
      name: "Poweshiek County",
      censusArea: 584.932,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.297494, 41.50979],
          [-92.411995, 41.509548],
          [-92.756508, 41.508794],
          [-92.765999, 41.862373],
          [-92.298164, 41.862779],
          [-92.297494, 41.50979]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19165",
      state: "19",
      county: "165",
      name: "Shelby County",
      censusArea: 590.779,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.04077, 41.50469],
          [-95.154722, 41.505211],
          [-95.497997, 41.506074],
          [-95.497793, 41.602333],
          [-95.558143, 41.602383],
          [-95.557277, 41.863449],
          [-95.092861, 41.863374],
          [-95.09252, 41.602055],
          [-95.040748, 41.602152],
          [-95.04077, 41.50469]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19179",
      state: "19",
      county: "179",
      name: "Wapello County",
      censusArea: 431.833,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.410233, 41.161942],
          [-92.179974, 41.162662],
          [-92.179072, 40.89972],
          [-92.639091, 40.898886],
          [-92.640361, 41.161298],
          [-92.410233, 41.161942]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19187",
      state: "19",
      county: "187",
      name: "Webster County",
      censusArea: 715.619,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.443024, 42.558843],
          [-94.443083, 42.645164],
          [-93.971714, 42.644707],
          [-93.971583, 42.558139],
          [-93.931684, 42.472011],
          [-93.931581, 42.210095],
          [-94.164704, 42.20992],
          [-94.397526, 42.209161],
          [-94.397671, 42.4733],
          [-94.443024, 42.558843]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19197",
      state: "19",
      county: "197",
      name: "Wright County",
      censusArea: 580.422,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.971238, 42.907762],
          [-93.498617, 42.908512],
          [-93.499485, 42.5577],
          [-93.500212, 42.557836],
          [-93.971583, 42.558139],
          [-93.971714, 42.644707],
          [-93.971238, 42.907762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39031",
      state: "39",
      county: "031",
      name: "Coshocton County",
      censusArea: 563.913,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.187105, 40.16688],
          [-82.182823, 40.238624],
          [-82.195911, 40.239071],
          [-82.184467, 40.454262],
          [-82.184607, 40.45628],
          [-81.709132, 40.444775],
          [-81.712288, 40.370804],
          [-81.61603, 40.368118],
          [-81.622683, 40.221308],
          [-81.716276, 40.152166],
          [-81.716868, 40.152863],
          [-82.187105, 40.16688]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39045",
      state: "39",
      county: "045",
      name: "Fairfield County",
      censusArea: 504.411,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.824254, 39.794996],
          [-82.811458, 39.932374],
          [-82.810797, 39.941162],
          [-82.782495, 39.939662],
          [-82.581813, 39.930156],
          [-82.473423, 39.925065],
          [-82.462813, 39.93038],
          [-82.472402, 39.837223],
          [-82.396594, 39.833057],
          [-82.374531, 39.654959],
          [-82.490329, 39.661704],
          [-82.496123, 39.602849],
          [-82.617546, 39.608605],
          [-82.620088, 39.563987],
          [-82.731518, 39.554445],
          [-82.842953, 39.56148],
          [-82.824254, 39.794996]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35013",
      state: "35",
      county: "013",
      name: "Dona Ana County",
      censusArea: 3807.511,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.377174, 32.00149],
          [-106.394298, 32.001484],
          [-106.411075, 32.001334],
          [-106.565142, 32.000736],
          [-106.566056, 32.000759],
          [-106.587972, 32.000749],
          [-106.595333, 32.000778],
          [-106.598639, 32.000754],
          [-106.599096, 32.000731],
          [-106.614702, 31.956],
          [-106.616136, 31.948439],
          [-106.627808, 31.860593],
          [-106.625763, 31.856276],
          [-106.621857, 31.852854],
          [-106.614637, 31.84649],
          [-106.605845, 31.846305],
          [-106.605245, 31.845905],
          [-106.566844, 31.813306],
          [-106.563444, 31.812606],
          [-106.545344, 31.805007],
          [-106.544714, 31.804287],
          [-106.542144, 31.802107],
          [-106.542097, 31.802146],
          [-106.535843, 31.798607],
          [-106.535343, 31.797507],
          [-106.535154, 31.797089],
          [-106.534743, 31.796107],
          [-106.533043, 31.791907],
          [-106.533, 31.791829],
          [-106.53248, 31.791914],
          [-106.530515, 31.792103],
          [-106.527943, 31.790507],
          [-106.527738, 31.789761],
          [-106.527623, 31.789119],
          [-106.527997, 31.786945],
          [-106.528543, 31.784407],
          [-106.528543, 31.783907],
          [-106.750547, 31.783706],
          [-106.750547, 31.783898],
          [-106.993544, 31.783689],
          [-106.998235, 31.783671],
          [-107.00056, 31.783679],
          [-107.00056, 31.783513],
          [-107.296824, 31.783762],
          [-107.299463, 32.605534],
          [-107.299371, 32.780603],
          [-106.887733, 32.779311],
          [-106.887859, 32.822844],
          [-106.340473, 33.052795],
          [-106.340731, 32.95825],
          [-106.376614, 32.957941],
          [-106.377174, 32.00149]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35017",
      state: "35",
      county: "017",
      name: "Grant County",
      censusArea: 3961.625,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.608283, 32.605382],
          [-107.92349, 32.60438],
          [-107.923489, 32.517283],
          [-108.229259, 32.516996],
          [-108.217194, 31.864096],
          [-108.524702, 31.863694],
          [-108.524586, 32.079732],
          [-108.538135, 32.079836],
          [-108.537035, 32.516751],
          [-108.647155, 32.516897],
          [-108.646373, 32.603582],
          [-108.853075, 32.603627],
          [-108.854381, 32.777276],
          [-109.047117, 32.777569],
          [-109.047117, 32.77757],
          [-109.04748, 33.06842],
          [-109.047453, 33.069427],
          [-109.046905, 33.091931],
          [-109.047013, 33.092917],
          [-109.047117, 33.137559],
          [-109.047116, 33.137995],
          [-109.047237, 33.208965],
          [-108.449988, 33.200997],
          [-108.000597, 33.201563],
          [-107.858292, 33.200879],
          [-107.836192, 33.067763],
          [-107.856672, 33.002539],
          [-107.780548, 32.952776],
          [-107.773088, 32.853775],
          [-107.716186, 32.762676],
          [-107.736949, 32.629494],
          [-107.608283, 32.605382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35037",
      state: "35",
      county: "037",
      name: "Quay County",
      censusArea: 2874.349,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.04252, 35.135596],
          [-103.042521, 34.954101],
          [-103.282998, 34.953957],
          [-103.284157, 34.86499],
          [-103.389359, 34.86532],
          [-103.390274, 34.778122],
          [-103.495379, 34.778699],
          [-103.496024, 34.691473],
          [-103.705788, 34.692463],
          [-103.706528, 34.604935],
          [-103.738232, 34.604671],
          [-103.948839, 34.605058],
          [-104.12888, 34.60522],
          [-104.129424, 34.779324],
          [-104.124841, 35.142183],
          [-103.857698, 35.264082],
          [-103.857529, 35.242199],
          [-103.637057, 35.240881],
          [-103.637147, 35.389722],
          [-103.379941, 35.397987],
          [-103.374988, 35.740011],
          [-103.041357, 35.739274],
          [-103.041554, 35.622487],
          [-103.042497, 35.211862],
          [-103.042377, 35.183156],
          [-103.042377, 35.183149],
          [-103.042366, 35.182786],
          [-103.042339, 35.181922],
          [-103.042395, 35.178573],
          [-103.042568, 35.159318],
          [-103.042711, 35.144735],
          [-103.0426, 35.142766],
          [-103.04252, 35.135596]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35059",
      state: "35",
      county: "059",
      name: "Union County",
      censusArea: 3823.744,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.040824, 36.055231],
          [-103.041357, 35.739274],
          [-103.374988, 35.740011],
          [-103.364975, 36.086373],
          [-103.794342, 36.085787],
          [-103.793986, 36.173995],
          [-104.008991, 36.174503],
          [-104.008927, 36.218018],
          [-104.007855, 36.996239],
          [-103.734364, 36.998041],
          [-103.733247, 36.998016],
          [-103.086105, 36.999864],
          [-103.002199, 37.000104],
          [-103.002247, 36.911587],
          [-103.001964, 36.909573],
          [-103.002198, 36.719427],
          [-103.002518, 36.675186],
          [-103.002252, 36.61718],
          [-103.002188, 36.602716],
          [-103.002565, 36.526588],
          [-103.002434, 36.500397],
          [-103.041669, 36.478514],
          [-103.041745, 36.318267],
          [-103.041674, 36.317534],
          [-103.040824, 36.055231]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36017",
      state: "36",
      county: "017",
      name: "Chenango County",
      censusArea: 893.548,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.532776, 42.195241],
          [-75.63711, 42.195628],
          [-75.638299, 42.248686],
          [-75.843792, 42.259707],
          [-75.86402, 42.415702],
          [-75.889832, 42.723844],
          [-75.295877, 42.744106],
          [-75.330143, 42.568082],
          [-75.404464, 42.479117],
          [-75.374905, 42.410784],
          [-75.415319, 42.314151],
          [-75.418421, 42.195032],
          [-75.419907, 42.194918],
          [-75.532776, 42.195241]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36031",
      state: "36",
      county: "031",
      name: "Essex County",
      censusArea: 1794.228,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.324681, 44.243614],
          [-73.350806, 44.225943],
          [-73.390583, 44.190886],
          [-73.403686, 44.153102],
          [-73.429239, 44.079414],
          [-73.437429, 44.046861],
          [-73.43688, 44.042578],
          [-73.410776, 44.026944],
          [-73.405999, 44.016229],
          [-73.405525, 43.948813],
          [-73.407742, 43.929887],
          [-73.397256, 43.905668],
          [-73.388389, 43.832404],
          [-73.379312, 43.808478],
          [-73.433237, 43.804083],
          [-73.435909, 43.803836],
          [-73.43812, 43.803687],
          [-73.495503, 43.799319],
          [-73.86869, 43.762803],
          [-74.057005, 43.744513],
          [-74.047062, 43.796343],
          [-74.213734, 43.810875],
          [-74.336826, 43.925223],
          [-74.255998, 43.969797],
          [-74.28187, 44.120552],
          [-74.09349, 44.137615],
          [-74.12756, 44.330211],
          [-74.141424, 44.407268],
          [-73.909687, 44.429699],
          [-73.700717, 44.445571],
          [-73.496604, 44.486081],
          [-73.463838, 44.537681],
          [-73.338634, 44.546847],
          [-73.33863, 44.546844],
          [-73.306707, 44.500334],
          [-73.299885, 44.476652],
          [-73.293613, 44.438903],
          [-73.296031, 44.428339],
          [-73.315016, 44.388513],
          [-73.323268, 44.264796],
          [-73.324681, 44.243614]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36049",
      state: "36",
      county: "049",
      name: "Lewis County",
      censusArea: 1274.679,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.170159, 44.096959],
          [-75.11016, 43.615229],
          [-75.5335, 43.419756],
          [-75.756213, 43.470388],
          [-75.774553, 43.688884],
          [-75.786759, 43.78832],
          [-75.850534, 43.791886],
          [-75.84056, 43.883976],
          [-75.758157, 43.878785],
          [-75.60367, 43.971363],
          [-75.542898, 43.967795],
          [-75.484528, 44.074172],
          [-75.545886, 44.102978],
          [-75.446124, 44.217655],
          [-75.170159, 44.096959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36065",
      state: "36",
      county: "065",
      name: "Oneida County",
      censusArea: 1212.429,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.247963, 42.871604],
          [-75.437167, 42.863319],
          [-75.444173, 42.933089],
          [-75.544211, 42.93177],
          [-75.552774, 43.037554],
          [-75.737774, 43.164673],
          [-75.884275, 43.155562],
          [-75.886756, 43.325021],
          [-75.814627, 43.483578],
          [-75.756213, 43.470388],
          [-75.5335, 43.419756],
          [-75.11016, 43.615229],
          [-75.086851, 43.41701],
          [-75.076581, 43.330705],
          [-75.16035, 43.255805],
          [-75.069165, 43.227333],
          [-75.219106, 43.052469],
          [-75.212158, 42.879973],
          [-75.242745, 42.877869],
          [-75.247963, 42.871604]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36077",
      state: "36",
      county: "077",
      name: "Otsego County",
      censusArea: 1001.7,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.71158, 42.517799],
          [-74.97494, 42.467488],
          [-75.197237, 42.358329],
          [-75.415319, 42.314151],
          [-75.374905, 42.410784],
          [-75.404464, 42.479117],
          [-75.330143, 42.568082],
          [-75.295877, 42.744106],
          [-75.247963, 42.871604],
          [-75.242745, 42.877869],
          [-75.212158, 42.879973],
          [-75.13987, 42.85976],
          [-75.100999, 42.908363],
          [-74.906738, 42.824943],
          [-74.878822, 42.898274],
          [-74.763303, 42.863237],
          [-74.702054, 42.845305],
          [-74.650213, 42.829941],
          [-74.648298, 42.829558],
          [-74.667512, 42.75071],
          [-74.630631, 42.626674],
          [-74.71158, 42.517799]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51079",
      state: "51",
      county: "079",
      name: "Greene County",
      censusArea: 156.246,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.452918, 38.475508],
          [-78.430031, 38.36715],
          [-78.286968, 38.271533],
          [-78.297716, 38.261585],
          [-78.369797, 38.184757],
          [-78.663174, 38.277923],
          [-78.551491, 38.336315],
          [-78.486003, 38.421486],
          [-78.484246, 38.423648],
          [-78.481647, 38.430603],
          [-78.476566, 38.432146],
          [-78.477718, 38.438574],
          [-78.472509, 38.447627],
          [-78.465073, 38.447],
          [-78.464699, 38.452542],
          [-78.462113, 38.45472],
          [-78.462658, 38.459528],
          [-78.457711, 38.464804],
          [-78.455495, 38.472532],
          [-78.452918, 38.475508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12129",
      state: "12",
      county: "129",
      name: "Wakulla County",
      censusArea: 606.419,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.075539, 30.273617],
          [-84.076043, 30.095464],
          [-84.083057, 30.092286],
          [-84.087034, 30.092103],
          [-84.094725, 30.094964],
          [-84.10273, 30.093611],
          [-84.11384, 30.085478],
          [-84.124889, 30.090601],
          [-84.135683, 30.083018],
          [-84.157278, 30.072714],
          [-84.167881, 30.071422],
          [-84.179149, 30.073187],
          [-84.19853, 30.087937],
          [-84.201585, 30.087982],
          [-84.203349, 30.085875],
          [-84.20801, 30.084776],
          [-84.237014, 30.08556],
          [-84.245668, 30.093021],
          [-84.247491, 30.10114],
          [-84.256439, 30.103791],
          [-84.269363, 30.09766],
          [-84.272511, 30.092358],
          [-84.274003, 30.083079],
          [-84.270368, 30.075469],
          [-84.270792, 30.068094],
          [-84.277168, 30.060263],
          [-84.289727, 30.057197],
          [-84.297836, 30.057451],
          [-84.315344, 30.069492],
          [-84.342022, 30.063858],
          [-84.358923, 30.058224],
          [-84.365882, 30.024588],
          [-84.361962, 29.987739],
          [-84.359986, 29.984739],
          [-84.3477, 29.984123],
          [-84.343041, 29.9751],
          [-84.341261, 29.960775],
          [-84.54445, 30.01103],
          [-84.669258, 30.1252],
          [-84.713351, 30.30017],
          [-84.247673, 30.303493],
          [-84.241222, 30.274244],
          [-84.075539, 30.273617]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13011",
      state: "13",
      county: "011",
      name: "Banks County",
      censusArea: 232.091,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.355269, 34.22373],
          [-83.372429, 34.205933],
          [-83.400125, 34.200525],
          [-83.402428, 34.197499],
          [-83.480361, 34.262038],
          [-83.620115, 34.295276],
          [-83.669473, 34.366689],
          [-83.615251, 34.431748],
          [-83.537538, 34.491725],
          [-83.45977, 34.481142],
          [-83.413586, 34.468014],
          [-83.398396, 34.460883],
          [-83.393915, 34.324801],
          [-83.355269, 34.22373]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13019",
      state: "13",
      county: "019",
      name: "Berrien County",
      censusArea: 451.897,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.197971, 31.025405],
          [-83.273731, 31.026658],
          [-83.280476, 31.02658],
          [-83.295052, 31.027278],
          [-83.305611, 31.167233],
          [-83.434492, 31.350361],
          [-83.338728, 31.475991],
          [-83.146085, 31.472088],
          [-83.145587, 31.472276],
          [-83.145874, 31.468926],
          [-83.150846, 31.462262],
          [-83.150474, 31.456787],
          [-83.144891, 31.454633],
          [-83.138618, 31.441528],
          [-83.139448, 31.436466],
          [-83.135927, 31.43051],
          [-83.138998, 31.427784],
          [-83.140483, 31.420395],
          [-83.033531, 31.27467],
          [-83.046885, 31.18368],
          [-83.038246, 31.146705],
          [-83.165072, 31.147198],
          [-83.197971, 31.025405]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13027",
      state: "13",
      county: "027",
      name: "Brooks County",
      censusArea: 493.046,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.574857, 31.078135],
          [-83.475617, 31.031964],
          [-83.476889, 31.030987],
          [-83.473792, 31.031276],
          [-83.47326, 31.028452],
          [-83.472275, 31.029742],
          [-83.470776, 31.028531],
          [-83.468295, 31.028481],
          [-83.471596, 31.026583],
          [-83.471324, 31.02386],
          [-83.467778, 31.021635],
          [-83.465898, 31.018502],
          [-83.466784, 31.013886],
          [-83.464269, 31.011093],
          [-83.463689, 31.005935],
          [-83.461306, 31.005709],
          [-83.460412, 31.002812],
          [-83.458129, 31.000859],
          [-83.358989, 30.83671],
          [-83.449293, 30.817957],
          [-83.482461, 30.756931],
          [-83.357703, 30.637359],
          [-83.37946, 30.63868],
          [-83.390062, 30.639333],
          [-83.429477, 30.641519],
          [-83.429584, 30.641496],
          [-83.440021, 30.642023],
          [-83.448895, 30.64241],
          [-83.611667, 30.651255],
          [-83.61172, 30.651258],
          [-83.674058, 30.654747],
          [-83.676773, 30.654905],
          [-83.743729, 30.658396],
          [-83.736158, 31.037679],
          [-83.573959, 31.033875],
          [-83.574857, 31.078135]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13033",
      state: "13",
      county: "033",
      name: "Burke County",
      censusArea: 826.968,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.851975, 33.247408],
          [-81.772251, 33.18012],
          [-81.696934, 33.116551],
          [-81.620287, 33.095408],
          [-81.615756, 33.092534],
          [-81.541831, 33.045654],
          [-81.767553, 32.909411],
          [-81.857976, 32.953882],
          [-82.081331, 32.916782],
          [-82.144977, 32.812741],
          [-82.316512, 32.835772],
          [-82.272353, 32.937796],
          [-82.29118, 33.062827],
          [-82.232554, 33.231211],
          [-82.26758, 33.267397],
          [-82.17424, 33.296771],
          [-82.088162, 33.230245],
          [-81.851975, 33.247408]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13039",
      state: "13",
      county: "039",
      name: "Camden County",
      censusArea: 613.025,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.906014, 30.822176],
          [-81.892028, 30.960474],
          [-81.936006, 31.047993],
          [-81.766322, 31.169595],
          [-81.572692, 31.097269],
          [-81.44317, 31.016661],
          [-81.451444, 31.015515],
          [-81.457795, 31.010259],
          [-81.45924, 31.005692],
          [-81.469298, 30.996028],
          [-81.490586, 30.984952],
          [-81.493651, 30.977528],
          [-81.486966, 30.969602],
          [-81.475789, 30.965976],
          [-81.472321, 30.969899],
          [-81.466814, 30.97091],
          [-81.453568, 30.965573],
          [-81.447388, 30.956732],
          [-81.426929, 30.956615],
          [-81.420108, 30.974076],
          [-81.415825, 30.977192],
          [-81.408484, 30.977718],
          [-81.403409, 30.957914],
          [-81.405153, 30.908203],
          [-81.428577, 30.836336],
          [-81.430835, 30.831156],
          [-81.44013, 30.821369],
          [-81.446927, 30.81039],
          [-81.455287, 30.79093],
          [-81.460061, 30.769912],
          [-81.461065, 30.753684],
          [-81.45947, 30.741979],
          [-81.449375, 30.715601],
          [-81.444124, 30.709714],
          [-81.544618, 30.712636],
          [-81.606221, 30.718135],
          [-81.624298, 30.736194],
          [-81.637222, 30.733835],
          [-81.719927, 30.744634],
          [-81.732627, 30.749934],
          [-81.741278, 30.762681],
          [-81.759338, 30.771377],
          [-81.806652, 30.789683],
          [-81.827014, 30.788933],
          [-81.840375, 30.786384],
          [-81.868608, 30.792754],
          [-81.906014, 30.822176]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13051",
      state: "13",
      county: "051",
      name: "Chatham County",
      censusArea: 426.437,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.004115, 32.072225],
          [-80.885517, 32.0346],
          [-80.859111, 32.023693],
          [-80.852276, 32.026676],
          [-80.84313, 32.024226],
          [-80.840549, 32.011306],
          [-80.841913, 32.002643],
          [-80.848441, 31.988279],
          [-80.862814, 31.969346],
          [-80.882814, 31.959075],
          [-80.897687, 31.949065],
          [-80.911207, 31.943769],
          [-80.929101, 31.944964],
          [-80.930279, 31.956705],
          [-80.948491, 31.95723],
          [-80.972392, 31.94127],
          [-80.975714, 31.923602],
          [-80.968494, 31.915822],
          [-80.954469, 31.911768],
          [-80.941359, 31.912984],
          [-80.934508, 31.90918],
          [-80.947294, 31.89621],
          [-80.971434, 31.877941],
          [-80.99269, 31.857641],
          [-81.000317, 31.856744],
          [-81.014478, 31.867474],
          [-81.041548, 31.876198],
          [-81.065255, 31.877095],
          [-81.058596, 31.857811],
          [-81.05907, 31.850106],
          [-81.06279, 31.84474],
          [-81.076178, 31.836132],
          [-81.075812, 31.829031],
          [-81.057181, 31.822687],
          [-81.050946, 31.822383],
          [-81.04794, 31.824881],
          [-81.039808, 31.823],
          [-81.036958, 31.819558],
          [-81.036873, 31.812721],
          [-81.047345, 31.802865],
          [-81.068116, 31.768735],
          [-81.077057, 31.761256],
          [-81.097402, 31.753126],
          [-81.130634, 31.722692],
          [-81.138448, 31.720934],
          [-81.154686, 31.726203],
          [-81.177027, 31.816113],
          [-81.138533, 31.855921],
          [-81.285467, 31.942994],
          [-81.323529, 32.019854],
          [-81.391698, 32.095886],
          [-81.195634, 32.237499],
          [-81.125457, 32.22713],
          [-81.122034, 32.161803],
          [-81.117234, 32.117605],
          [-81.113334, 32.113205],
          [-81.06185, 32.087935],
          [-81.042833, 32.084508],
          [-81.004115, 32.072225]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13061",
      state: "13",
      county: "061",
      name: "Clay County",
      censusArea: 195.381,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.817843, 31.619771],
          [-84.819807, 31.501192],
          [-85.029227, 31.486071],
          [-85.046859, 31.517451],
          [-85.041305, 31.540987],
          [-85.057473, 31.618624],
          [-85.059534, 31.621717],
          [-85.065236, 31.624351],
          [-85.087029, 31.640966],
          [-85.12553, 31.694965],
          [-85.12653, 31.716764],
          [-85.12223, 31.722764],
          [-85.11893, 31.732664],
          [-85.124501, 31.763081],
          [-85.12523, 31.767063],
          [-85.130731, 31.772263],
          [-85.138668, 31.780425],
          [-84.95864, 31.777854],
          [-84.942353, 31.618669],
          [-84.817843, 31.619771]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05149",
      state: "05",
      county: "149",
      name: "Yell County",
      censusArea: 929.984,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.896109, 35.170678],
          [-93.040525, 35.076923],
          [-93.181478, 34.97639],
          [-93.288284, 34.860992],
          [-93.287785, 34.773193],
          [-93.394456, 34.772355],
          [-93.394289, 34.743393],
          [-93.710297, 34.745296],
          [-93.704885, 35.019715],
          [-93.706692, 35.134943],
          [-93.529838, 35.145441],
          [-93.457022, 35.215782],
          [-93.279959, 35.212552],
          [-93.278293, 35.317402],
          [-93.156078, 35.235307],
          [-93.084678, 35.12739],
          [-92.896109, 35.170678]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06011",
      state: "06",
      county: "011",
      name: "Colusa County",
      censusArea: 1150.731,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.908269, 39.303878],
          [-121.945509, 39.180959],
          [-121.847964, 39.070817],
          [-121.835488, 38.924481],
          [-122.337967, 38.923945],
          [-122.340172, 38.924246],
          [-122.492215, 39.054092],
          [-122.476083, 39.172609],
          [-122.680927, 39.238726],
          [-122.775043, 39.311778],
          [-122.78509, 39.382975],
          [-122.739062, 39.383266],
          [-122.136006, 39.385494],
          [-122.136161, 39.414499],
          [-121.890013, 39.383864],
          [-121.908269, 39.303878]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06027",
      state: "06",
      county: "027",
      name: "Inyo County",
      censusArea: 10180.879,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.832726, 37.464929],
          [-117.500909, 37.220282],
          [-117.166187, 36.970862],
          [-117.000895, 36.847694],
          [-116.488233, 36.459097],
          [-116.097216, 36.158346],
          [-116.093601, 36.155805],
          [-115.912858, 36.015359],
          [-115.892975, 35.999967],
          [-115.84608, 35.963596],
          [-115.689302, 35.842003],
          [-115.648029, 35.809629],
          [-115.648925, 35.809128],
          [-115.735745, 35.809097],
          [-115.735891, 35.79362],
          [-117.632996, 35.797251],
          [-118.008043, 35.789161],
          [-117.998258, 35.868782],
          [-117.980761, 35.867515],
          [-118.003577, 35.983718],
          [-118.033611, 36.008944],
          [-118.127147, 36.279656],
          [-118.100317, 36.346137],
          [-118.21482, 36.435036],
          [-118.274624, 36.597334],
          [-118.366328, 36.691635],
          [-118.360586, 36.744773],
          [-118.437137, 37.059818],
          [-118.654593, 37.141826],
          [-118.665167, 37.261009],
          [-118.716016, 37.328208],
          [-118.786736, 37.34338],
          [-118.775014, 37.463052],
          [-117.832726, 37.464929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06047",
      state: "06",
      county: "047",
      name: "Merced County",
      censusArea: 1934.972,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.931374, 37.421753],
          [-120.38767, 37.633364],
          [-120.26601, 37.400607],
          [-120.143826, 37.239192],
          [-120.052055, 37.183108],
          [-120.400758, 37.117764],
          [-120.541696, 37.044505],
          [-120.590566, 36.952641],
          [-120.65595, 36.952832],
          [-120.918731, 36.740381],
          [-121.141523, 36.836656],
          [-121.215406, 36.961248],
          [-121.226804, 37.134774],
          [-120.964223, 37.345782],
          [-120.931374, 37.421753]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06063",
      state: "06",
      county: "063",
      name: "Plumas County",
      censusArea: 2553.043,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.076695, 39.597264],
          [-121.360516, 39.84074],
          [-121.430012, 39.914833],
          [-121.366818, 40.086054],
          [-121.436886, 40.151905],
          [-121.368395, 40.21223],
          [-121.345571, 40.312667],
          [-121.470395, 40.350213],
          [-121.497635, 40.445591],
          [-121.327826, 40.445367],
          [-121.061417, 40.446536],
          [-121.061493, 40.256417],
          [-120.928582, 40.191931],
          [-120.764403, 40.31601],
          [-120.652158, 40.307656],
          [-120.510817, 40.248945],
          [-120.445892, 40.176854],
          [-120.341385, 40.115243],
          [-120.209641, 40.086008],
          [-120.201264, 40.013474],
          [-120.108761, 39.93951],
          [-120.110627, 39.765779],
          [-120.147149, 39.707658],
          [-120.792484, 39.709794],
          [-120.873618, 39.775875],
          [-121.009477, 39.639459],
          [-121.050922, 39.613801],
          [-121.05267, 39.612334],
          [-121.076695, 39.597264]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06077",
      state: "06",
      county: "077",
      name: "San Joaquin County",
      censusArea: 1391.322,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.926449, 38.077421],
          [-120.920665, 37.738392],
          [-120.995754, 37.760152],
          [-121.155849, 37.719844],
          [-121.258171, 37.653495],
          [-121.273751, 37.63833],
          [-121.333388, 37.59105],
          [-121.471925, 37.481783],
          [-121.556655, 37.542732],
          [-121.556936, 37.817218],
          [-121.550971, 37.831155],
          [-121.538931, 37.839563],
          [-121.536595, 37.844723],
          [-121.538026, 37.849622],
          [-121.549517, 37.856986],
          [-121.561813, 37.857135],
          [-121.56937, 37.858881],
          [-121.575463, 37.863118],
          [-121.574849, 37.885021],
          [-121.569113, 37.901521],
          [-121.55987, 37.920862],
          [-121.561937, 37.937612],
          [-121.56364, 37.94611],
          [-121.569889, 37.957495],
          [-121.574823, 37.973025],
          [-121.579943, 37.976113],
          [-121.580022, 38.094414],
          [-121.484317, 38.241509],
          [-121.344024, 38.22826],
          [-121.027084, 38.300252],
          [-120.995497, 38.225405],
          [-120.93885, 38.088321],
          [-120.926449, 38.077421]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06091",
      state: "06",
      county: "091",
      name: "Sierra County",
      censusArea: 953.214,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.001319, 39.722416],
          [-120.003116, 39.445113],
          [-120.003117, 39.445045],
          [-120.505445, 39.446117],
          [-120.575059, 39.52228],
          [-120.654332, 39.526889],
          [-120.755729, 39.452663],
          [-121.022085, 39.391558],
          [-121.034406, 39.514509],
          [-121.009477, 39.639459],
          [-120.873618, 39.775875],
          [-120.792484, 39.709794],
          [-120.147149, 39.707658],
          [-120.001319, 39.722416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06107",
      state: "06",
      county: "107",
      name: "Tulare County",
      censusArea: 4824.215,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.214033, 35.790489],
          [-119.534559, 35.789758],
          [-119.538116, 35.789567],
          [-119.529358, 36.26985],
          [-119.474607, 36.269025],
          [-119.474892, 36.400953],
          [-119.528792, 36.401465],
          [-119.573194, 36.488835],
          [-119.466322, 36.575238],
          [-119.305102, 36.573725],
          [-119.304625, 36.660606],
          [-118.984779, 36.657147],
          [-118.982441, 36.741646],
          [-118.360586, 36.744773],
          [-118.366328, 36.691635],
          [-118.274624, 36.597334],
          [-118.21482, 36.435036],
          [-118.100317, 36.346137],
          [-118.127147, 36.279656],
          [-118.033611, 36.008944],
          [-118.003577, 35.983718],
          [-117.980761, 35.867515],
          [-117.998258, 35.868782],
          [-118.008043, 35.789161],
          [-118.270456, 35.790009],
          [-119.214033, 35.790489]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08011",
      state: "08",
      county: "011",
      name: "Bent County",
      censusArea: 1512.861,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.403592, 37.792397],
          [-103.399938, 38.265435],
          [-102.74233, 38.266971],
          [-102.747615, 37.643642],
          [-103.075938, 37.64342],
          [-103.404339, 37.643576],
          [-103.403592, 37.792397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08019",
      state: "08",
      county: "019",
      name: "Clear Creek County",
      censusArea: 395.227,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.398949, 39.566056],
          [-105.587986, 39.568197],
          [-105.642798, 39.566584],
          [-105.829662, 39.564865],
          [-105.793058, 39.636328],
          [-105.924618, 39.698972],
          [-105.887395, 39.796996],
          [-105.736426, 39.804801],
          [-105.690348, 39.851996],
          [-105.545076, 39.773635],
          [-105.397949, 39.747087],
          [-105.398949, 39.566056]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08027",
      state: "08",
      county: "027",
      name: "Custer County",
      censusArea: 738.625,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.049215, 38.257973],
          [-105.049917, 37.915479],
          [-105.194586, 38.00756],
          [-105.285076, 37.899421],
          [-105.317081, 37.938949],
          [-105.473204, 37.89597],
          [-105.604355, 38.008745],
          [-105.67627, 38.146173],
          [-105.796897, 38.265047],
          [-105.049215, 38.257973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20013",
      state: "20",
      county: "013",
      name: "Brown County",
      censusArea: 570.872,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.788941, 39.653001],
          [-95.788315, 39.883502],
          [-95.788111, 40.000452],
          [-95.788024, 40.000452],
          [-95.784575, 40.000463],
          [-95.375257, 40],
          [-95.339896, 39.999999],
          [-95.33974, 39.65298],
          [-95.564126, 39.652872],
          [-95.788941, 39.653001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20023",
      state: "20",
      county: "023",
      name: "Cheyenne County",
      censusArea: 1019.887,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.411043, 40.002365],
          [-101.41124, 39.999997],
          [-101.413913, 39.568361],
          [-102.049764, 39.56818],
          [-102.049806, 39.574058],
          [-102.049954, 39.592331],
          [-102.050594, 39.675594],
          [-102.051254, 39.818992],
          [-102.051318, 39.833311],
          [-102.051363, 39.843471],
          [-102.051569, 39.849805],
          [-102.051744, 40.003078],
          [-101.916696, 40.003142],
          [-101.904176, 40.003162],
          [-101.841025, 40.002784],
          [-101.832161, 40.002933],
          [-101.807687, 40.002798],
          [-101.804862, 40.002752],
          [-101.627071, 40.00262],
          [-101.625809, 40.002711],
          [-101.542273, 40.002609],
          [-101.417209, 40.002424],
          [-101.411043, 40.002365]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27071",
      state: "27",
      county: "071",
      name: "Koochiching County",
      censusArea: 3104.07,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.088438, 48.627597],
          [-93.096488, 48.067826],
          [-93.081158, 47.89187],
          [-93.775772, 47.89895],
          [-93.776173, 47.846404],
          [-94.418543, 47.845816],
          [-94.425771, 48.367339],
          [-94.428702, 48.367333],
          [-94.430273, 48.700909],
          [-94.431854, 48.706588],
          [-94.416191, 48.710948],
          [-94.388848, 48.711945],
          [-94.308446, 48.710239],
          [-94.290737, 48.707747],
          [-94.260541, 48.696381],
          [-94.251169, 48.683514],
          [-94.250497, 48.656654],
          [-94.250191, 48.656323],
          [-94.244394, 48.653442],
          [-94.224276, 48.649527],
          [-94.126336, 48.644447],
          [-94.052452, 48.64402],
          [-93.840754, 48.628548],
          [-93.834323, 48.624954],
          [-93.822644, 48.609067],
          [-93.806763, 48.577616],
          [-93.805369, 48.568393],
          [-93.811201, 48.542385],
          [-93.818375, 48.534442],
          [-93.818253, 48.530046],
          [-93.815178, 48.526508],
          [-93.794454, 48.516021],
          [-93.771741, 48.515825],
          [-93.756483, 48.515366],
          [-93.662337, 48.515708],
          [-93.544361, 48.529109],
          [-93.467504, 48.545664],
          [-93.460798, 48.550552],
          [-93.456675, 48.561834],
          [-93.457046, 48.567199],
          [-93.461731, 48.57403],
          [-93.466007, 48.587291],
          [-93.464308, 48.591792],
          [-93.40366, 48.607593],
          [-93.367025, 48.608283],
          [-93.347528, 48.62662],
          [-93.254854, 48.642784],
          [-93.207398, 48.642474],
          [-93.17999, 48.624926],
          [-93.14242, 48.624924],
          [-93.088438, 48.627597]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27079",
      state: "27",
      county: "079",
      name: "Le Sueur County",
      censusArea: 448.76,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.646696, 44.195993],
          [-93.727389, 44.196104],
          [-93.767971, 44.195836],
          [-93.768031, 44.239384],
          [-94.011834, 44.239192],
          [-93.932685, 44.344885],
          [-93.92955, 44.45664],
          [-93.910405, 44.543163],
          [-93.524327, 44.543618],
          [-93.52523, 44.195975],
          [-93.646696, 44.195993]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27087",
      state: "27",
      county: "087",
      name: "Mahnomen County",
      censusArea: 557.878,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.067621, 47.499136],
          [-95.563189, 47.500268],
          [-95.554248, 47.499893],
          [-95.551458, 47.50002],
          [-95.551186, 47.151453],
          [-96.067069, 47.151813],
          [-96.067621, 47.499136]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27101",
      state: "27",
      county: "101",
      name: "Murray County",
      censusArea: 704.702,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.462427, 43.847911],
          [-96.05232, 43.84907],
          [-96.06462, 43.849039],
          [-96.063778, 44.196425],
          [-95.59357, 44.19553],
          [-95.462547, 44.195942],
          [-95.462427, 43.847911]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27107",
      state: "27",
      county: "107",
      name: "Norman County",
      censusArea: 872.789,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.826655, 47.150539],
          [-96.832946, 47.237588],
          [-96.833019, 47.238095],
          [-96.852676, 47.374973],
          [-96.862312, 47.422333],
          [-96.853768, 47.499527],
          [-96.848923, 47.498991],
          [-96.067621, 47.499136],
          [-96.067069, 47.151813],
          [-96.194671, 47.151149],
          [-96.826655, 47.150539]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27125",
      state: "27",
      county: "125",
      name: "Red Lake County",
      censusArea: 432.407,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.709619, 47.935805],
          [-95.709148, 47.848594],
          [-95.836827, 47.848219],
          [-95.837177, 47.760894],
          [-96.351727, 47.7603],
          [-96.352708, 47.847328],
          [-96.482092, 47.847428],
          [-96.48255, 47.963595],
          [-95.70984, 47.96433],
          [-95.709619, 47.935805]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27131",
      state: "27",
      county: "131",
      name: "Rice County",
      censusArea: 495.684,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.281521, 44.543957],
          [-93.281686, 44.471998],
          [-93.039485, 44.471871],
          [-93.041159, 44.196726],
          [-93.04591, 44.196716],
          [-93.406565, 44.196377],
          [-93.52523, 44.195975],
          [-93.524327, 44.543618],
          [-93.281521, 44.543957]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27143",
      state: "27",
      county: "143",
      name: "Sibley County",
      censusArea: 588.779,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.92955, 44.45664],
          [-94.6242, 44.45603],
          [-94.629489, 44.716663],
          [-94.498074, 44.717171],
          [-94.497835, 44.629921],
          [-94.254719, 44.630311],
          [-94.254076, 44.717855],
          [-94.010494, 44.717464],
          [-93.888636, 44.717044],
          [-93.768039, 44.640303],
          [-93.857678, 44.621095],
          [-93.910405, 44.543163],
          [-93.92955, 44.45664]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27159",
      state: "27",
      county: "159",
      name: "Wadena County",
      censusArea: 536.268,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.787126, 46.803943],
          [-94.779121, 46.394215],
          [-94.731442, 46.368605],
          [-95.15557, 46.36888],
          [-95.16371, 46.71764],
          [-95.16301, 46.80472],
          [-94.787126, 46.803943]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27169",
      state: "27",
      county: "169",
      name: "Winona County",
      censusArea: 626.206,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.559197, 44.023959],
          [-91.547028, 44.022226],
          [-91.505918, 44.018651],
          [-91.463515, 44.009041],
          [-91.43738, 43.999962],
          [-91.432522, 43.996827],
          [-91.420114, 43.984243],
          [-91.363242, 43.926563],
          [-91.310991, 43.867381],
          [-91.298815, 43.856555],
          [-91.284138, 43.847065],
          [-91.730424, 43.846935],
          [-92.079292, 43.84708],
          [-92.079491, 44.106988],
          [-92.079397, 44.192722],
          [-91.862858, 44.193001],
          [-91.808064, 44.159262],
          [-91.740871, 44.133918],
          [-91.721552, 44.130342],
          [-91.67252, 44.091286],
          [-91.59207, 44.031372],
          [-91.580019, 44.026925],
          [-91.559197, 44.023959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28007",
      state: "28",
      county: "007",
      name: "Attala County",
      censusArea: 734.982,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.64528, 33.285515],
          [-89.453808, 33.285942],
          [-89.316408, 33.285556],
          [-89.317855, 33.108871],
          [-89.319163, 32.931654],
          [-89.473311, 32.931115],
          [-89.730154, 32.929924],
          [-89.730016, 32.884773],
          [-89.965882, 32.879573],
          [-89.895518, 32.936014],
          [-89.749441, 33.214478],
          [-89.64528, 33.285515]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28023",
      state: "28",
      county: "023",
      name: "Clarke County",
      censusArea: 691.554,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.910459, 31.826649],
          [-88.913847, 32.224186],
          [-88.431707, 32.227662],
          [-88.43865, 32.172806],
          [-88.43871, 32.172078],
          [-88.454959, 32.040576],
          [-88.455039, 32.039719],
          [-88.46866, 31.933173],
          [-88.468879, 31.930262],
          [-88.46996, 31.893759],
          [-88.622394, 31.857193],
          [-88.910459, 31.826649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28035",
      state: "28",
      county: "035",
      name: "Forrest County",
      censusArea: 466.314,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.34895, 31.01037],
          [-89.347863, 31.3467],
          [-89.451073, 31.346383],
          [-89.451616, 31.434029],
          [-89.420857, 31.434043],
          [-89.399178, 31.434029],
          [-89.146092, 31.434052],
          [-89.137967, 30.909877],
          [-89.340806, 30.909912],
          [-89.34895, 31.01037]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28043",
      state: "28",
      county: "043",
      name: "Grenada County",
      censusArea: 422.107,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.136787, 33.721648],
          [-90.136491, 33.809319],
          [-89.926294, 33.823854],
          [-89.926099, 33.897957],
          [-89.769296, 33.897137],
          [-89.76905, 33.867334],
          [-89.507353, 33.867469],
          [-89.507135, 33.721819],
          [-89.506927, 33.71398],
          [-89.506872, 33.677605],
          [-89.78689, 33.676727],
          [-90.127775, 33.676956],
          [-90.137011, 33.676944],
          [-90.136787, 33.721648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36091",
      state: "36",
      county: "091",
      name: "Saratoga County",
      censusArea: 809.984,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.676762, 42.783277],
          [-73.688362, 42.775477],
          [-73.719863, 42.801277],
          [-73.722663, 42.820677],
          [-73.809369, 42.778869],
          [-73.895938, 42.851076],
          [-74.09298, 42.955868],
          [-74.093814, 42.959378],
          [-74.097467, 42.982934],
          [-74.140147, 43.253979],
          [-74.1601, 43.371532],
          [-73.884139, 43.398041],
          [-73.835811, 43.253756],
          [-73.59496, 43.306118],
          [-73.573342, 43.100545],
          [-73.635463, 42.94129],
          [-73.68461, 42.892399],
          [-73.659663, 42.818978],
          [-73.661362, 42.802977],
          [-73.672355, 42.795791],
          [-73.673463, 42.790276],
          [-73.676762, 42.783277]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36101",
      state: "36",
      county: "101",
      name: "Steuben County",
      censusArea: 1390.559,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.965728, 42.001274],
          [-77.007536, 42.000819],
          [-77.007635, 42.000848],
          [-77.124693, 41.999395],
          [-77.505308, 42.00007],
          [-77.610028, 41.999519],
          [-77.749931, 41.998782],
          [-77.722964, 42.471216],
          [-77.659917, 42.580409],
          [-77.490889, 42.577288],
          [-77.366505, 42.576368],
          [-77.143795, 42.576869],
          [-77.107203, 42.483771],
          [-77.099657, 42.272356],
          [-76.965028, 42.278495],
          [-76.965728, 42.001274]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36113",
      state: "36",
      county: "113",
      name: "Warren County",
      censusArea: 866.952,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.86869, 43.762803],
          [-73.495503, 43.799319],
          [-73.43812, 43.803687],
          [-73.492893, 43.657303],
          [-73.62894, 43.486391],
          [-73.59496, 43.306118],
          [-73.835811, 43.253756],
          [-73.884139, 43.398041],
          [-74.1601, 43.371532],
          [-74.214625, 43.728703],
          [-74.057005, 43.744513],
          [-73.86869, 43.762803]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42011",
      state: "42",
      county: "011",
      name: "Berks County",
      censusArea: 856.506,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.739442, 40.57994],
          [-75.738038, 40.579105],
          [-75.694308, 40.551149],
          [-75.682238, 40.543638],
          [-75.529694, 40.446995],
          [-75.695955, 40.242359],
          [-75.696782, 40.241863],
          [-75.729863, 40.224489],
          [-75.873375, 40.137107],
          [-75.875162, 40.138071],
          [-76.091764, 40.277692],
          [-76.149509, 40.314454],
          [-76.151215, 40.315971],
          [-76.151994, 40.31654],
          [-76.440181, 40.495952],
          [-76.171624, 40.534947],
          [-76.012226, 40.577713],
          [-75.996233, 40.629971],
          [-75.993212, 40.639712],
          [-75.891473, 40.67727],
          [-75.76674, 40.597033],
          [-75.739442, 40.57994]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42019",
      state: "42",
      county: "019",
      name: "Butler County",
      censusArea: 788.604,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.160006, 40.886909],
          [-80.16529, 41.00047],
          [-80.096861, 41.069804],
          [-79.999779, 41.171861],
          [-79.694984, 41.172865],
          [-79.69235, 41.172028],
          [-79.690711, 41.170691],
          [-79.689994, 40.741362],
          [-79.692331, 40.716656],
          [-79.692615, 40.688784],
          [-79.692794, 40.674604],
          [-79.692357, 40.672857],
          [-79.69293, 40.669744],
          [-80.148451, 40.67429],
          [-80.148787, 40.676432],
          [-80.158534, 40.855157],
          [-80.160006, 40.886909]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42025",
      state: "42",
      county: "025",
      name: "Carbon County",
      censusArea: 381.46,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.997348, 40.912985],
          [-75.73193, 41.00822],
          [-75.77196, 41.076751],
          [-75.649637, 41.122344],
          [-75.49599, 40.987181],
          [-75.57459, 40.951323],
          [-75.474193, 40.814746],
          [-75.608985, 40.787387],
          [-75.757807, 40.735414],
          [-75.997348, 40.912985]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42037",
      state: "42",
      county: "037",
      name: "Columbia County",
      censusArea: 483.108,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.463481, 41.262612],
          [-76.460762, 41.264848],
          [-76.447597, 41.275629],
          [-76.407934, 41.308418],
          [-76.310133, 41.310199],
          [-76.317916, 41.205384],
          [-76.228975, 41.138466],
          [-76.207827, 40.94974],
          [-76.284611, 40.883588],
          [-76.30717, 40.801809],
          [-76.380152, 40.775511],
          [-76.409667, 40.833624],
          [-76.501312, 40.824817],
          [-76.528034, 40.882515],
          [-76.514951, 40.958959],
          [-76.618971, 41.063759],
          [-76.640767, 41.155718],
          [-76.592607, 41.157765],
          [-76.463481, 41.262612]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42053",
      state: "42",
      county: "053",
      name: "Forest County",
      censusArea: 427.185,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.402653, 41.625968],
          [-78.956042, 41.623402],
          [-78.958957, 41.437061],
          [-79.06352, 41.453207],
          [-79.095096, 41.340658],
          [-79.208878, 41.331861],
          [-79.207196, 41.430922],
          [-79.400269, 41.436145],
          [-79.477822, 41.386479],
          [-79.512276, 41.46524],
          [-79.51207, 41.624559],
          [-79.402653, 41.625968]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42057",
      state: "42",
      county: "057",
      name: "Fulton County",
      censusArea: 437.551,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.34252, 39.722539],
          [-78.342834, 39.722539],
          [-78.380599, 39.722554],
          [-78.299003, 39.8255],
          [-78.134863, 40.165131],
          [-77.925905, 40.101911],
          [-77.864489, 40.061568],
          [-77.920952, 40.00045],
          [-78.0027, 39.826599],
          [-78.09914, 39.722322],
          [-78.202895, 39.722416],
          [-78.20445, 39.72252],
          [-78.240334, 39.722498],
          [-78.243103, 39.722481],
          [-78.268948, 39.72259],
          [-78.26902, 39.722613],
          [-78.330715, 39.722689],
          [-78.337111, 39.722461],
          [-78.339539, 39.722552],
          [-78.340498, 39.722514],
          [-78.34252, 39.722539]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42075",
      state: "42",
      county: "075",
      name: "Lebanon County",
      censusArea: 361.833,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.151994, 40.31654],
          [-76.151215, 40.315971],
          [-76.307578, 40.255066],
          [-76.566339, 40.196644],
          [-76.67804, 40.474717],
          [-76.535414, 40.555157],
          [-76.440181, 40.495952],
          [-76.151994, 40.31654]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42081",
      state: "42",
      county: "081",
      name: "Lycoming County",
      censusArea: 1228.594,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.463481, 41.262612],
          [-76.592607, 41.157765],
          [-76.640767, 41.155718],
          [-76.678776, 41.154172],
          [-76.732672, 41.17204],
          [-76.788659, 41.175762],
          [-76.880963, 41.158044],
          [-76.888145, 41.153807],
          [-76.896114, 41.13907],
          [-76.977939, 41.087883],
          [-77.144111, 41.06884],
          [-77.320891, 41.218932],
          [-77.526537, 41.358528],
          [-77.598129, 41.478576],
          [-77.599278, 41.542271],
          [-77.00871, 41.549883],
          [-76.874714, 41.596919],
          [-76.813731, 41.590034],
          [-76.749497, 41.405572],
          [-76.592532, 41.304332],
          [-76.447597, 41.275629],
          [-76.460762, 41.264848],
          [-76.463481, 41.262612]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42099",
      state: "42",
      county: "099",
      name: "Perry County",
      censusArea: 551.445,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.083942, 40.30122],
          [-77.229166, 40.283744],
          [-77.241691, 40.282847],
          [-77.270057, 40.278652],
          [-77.280548, 40.275923],
          [-77.419795, 40.26407],
          [-77.366018, 40.313569],
          [-77.614665, 40.198549],
          [-77.671761, 40.289825],
          [-77.540141, 40.399221],
          [-77.33811, 40.495336],
          [-77.16996, 40.562726],
          [-77.18293, 40.58214],
          [-76.949414, 40.628163],
          [-76.957444, 40.594677],
          [-76.983673, 40.578259],
          [-76.991939, 40.561475],
          [-76.949397, 40.469037],
          [-77.028645, 40.391197],
          [-76.933589, 40.356045],
          [-76.928521, 40.347985],
          [-76.92618, 40.34098],
          [-76.91727, 40.332715],
          [-76.91497, 40.328484],
          [-77.083942, 40.30122]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42117",
      state: "42",
      county: "117",
      name: "Tioga County",
      censusArea: 1133.788,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.00871, 41.549883],
          [-77.599278, 41.542271],
          [-77.610028, 41.999519],
          [-77.505308, 42.00007],
          [-77.124693, 41.999395],
          [-77.007635, 42.000848],
          [-77.007536, 42.000819],
          [-76.965728, 42.001274],
          [-76.965686, 42.001274],
          [-76.942585, 42.001574],
          [-76.937084, 42.001674],
          [-76.927084, 42.001674],
          [-76.893201, 41.748463],
          [-76.883511, 41.666314],
          [-76.878699, 41.620344],
          [-76.874714, 41.596919],
          [-77.00871, 41.549883]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42131",
      state: "42",
      county: "131",
      name: "Wyoming County",
      censusArea: 397.324,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.2831, 41.376517],
          [-76.223866, 41.524544],
          [-76.220139, 41.541285],
          [-76.197998, 41.647823],
          [-76.115172, 41.651825],
          [-75.719887, 41.642263],
          [-75.834695, 41.427246],
          [-76.00507, 41.384551],
          [-76.2831, 41.376517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01005",
      state: "01",
      county: "005",
      name: "Barbour County",
      censusArea: 884.876,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.056029, 32.063055],
          [-85.050212, 32.024196],
          [-85.062246, 31.991857],
          [-85.07233, 31.964758],
          [-85.08213, 31.944658],
          [-85.10663, 31.915159],
          [-85.132931, 31.88826],
          [-85.141831, 31.839861],
          [-85.141331, 31.783163],
          [-85.138668, 31.780425],
          [-85.130731, 31.772263],
          [-85.12523, 31.767063],
          [-85.124501, 31.763081],
          [-85.216076, 31.702409],
          [-85.416038, 31.706664],
          [-85.416437, 31.619466],
          [-85.748251, 31.618048],
          [-85.66623, 31.772877],
          [-85.657668, 31.880275],
          [-85.587344, 31.997355],
          [-85.428476, 32.014951],
          [-85.410241, 32.146651],
          [-85.257747, 32.148251],
          [-85.185067, 32.061708],
          [-85.056029, 32.063055]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01013",
      state: "01",
      county: "013",
      name: "Butler County",
      censusArea: 776.829,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.701554, 31.523946],
          [-86.839386, 31.525204],
          [-86.906769, 31.632671],
          [-86.905899, 31.753035],
          [-86.906899, 31.830628],
          [-86.908939, 31.961673],
          [-86.857583, 31.962167],
          [-86.448198, 31.964629],
          [-86.448635, 31.655617],
          [-86.499533, 31.655247],
          [-86.499213, 31.525331],
          [-86.690217, 31.523693],
          [-86.701554, 31.523946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01019",
      state: "01",
      county: "019",
      name: "Cherokee County",
      censusArea: 553.7,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.512108, 34.518252],
          [-85.508384, 34.501212],
          [-85.502454, 34.474527],
          [-85.502316, 34.473954],
          [-85.462304, 34.286365],
          [-85.458693, 34.269437],
          [-85.458071, 34.265736],
          [-85.455371, 34.252854],
          [-85.455057, 34.250689],
          [-85.421731, 34.080821],
          [-85.398837, 33.964129],
          [-85.530094, 33.941423],
          [-85.529432, 33.95598],
          [-85.738975, 33.96846],
          [-85.843617, 34.200006],
          [-85.636645, 34.366622],
          [-85.576981, 34.483543],
          [-85.513699, 34.524133],
          [-85.512108, 34.518252]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06041",
      state: "06",
      county: "041",
      name: "Marin County",
      censusArea: 520.306,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.490727, 38.109755],
          [-122.491283, 38.108087],
          [-122.489974, 38.096961],
          [-122.486702, 38.090088],
          [-122.483757, 38.071762],
          [-122.492265, 38.056381],
          [-122.499465, 38.032165],
          [-122.497828, 38.019402],
          [-122.494556, 38.015148],
          [-122.481466, 38.007621],
          [-122.462812, 38.003367],
          [-122.452995, 37.996167],
          [-122.448413, 37.988313],
          [-122.448413, 37.984713],
          [-122.456595, 37.978823],
          [-122.462485, 37.981441],
          [-122.471975, 37.981768],
          [-122.488665, 37.966714],
          [-122.490302, 37.964751],
          [-122.490302, 37.959188],
          [-122.487684, 37.948716],
          [-122.480484, 37.945443],
          [-122.479175, 37.941516],
          [-122.48572, 37.937589],
          [-122.499465, 37.939225],
          [-122.503064, 37.936607],
          [-122.503064, 37.928753],
          [-122.493574, 37.921881],
          [-122.486375, 37.921881],
          [-122.478193, 37.918608],
          [-122.471975, 37.910427],
          [-122.472303, 37.902573],
          [-122.458558, 37.894064],
          [-122.448413, 37.89341],
          [-122.43925, 37.88392],
          [-122.438268, 37.880974],
          [-122.45005, 37.871157],
          [-122.462158, 37.868866],
          [-122.474266, 37.874429],
          [-122.480811, 37.873448],
          [-122.483429, 37.868866],
          [-122.483102, 37.863957],
          [-122.476536, 37.832812],
          [-122.476473, 37.832513],
          [-122.479151, 37.825428],
          [-122.47986, 37.825641],
          [-122.483483, 37.826728],
          [-122.492474, 37.82484],
          [-122.505383, 37.822128],
          [-122.522836, 37.824717],
          [-122.523585, 37.824828],
          [-122.537285, 37.830328],
          [-122.548986, 37.836227],
          [-122.561487, 37.851827],
          [-122.584289, 37.859227],
          [-122.60129, 37.875126],
          [-122.627113, 37.88608],
          [-122.639977, 37.897349],
          [-122.656519, 37.904519],
          [-122.678474, 37.906604],
          [-122.682171, 37.90645],
          [-122.693569, 37.901171],
          [-122.70264, 37.89382],
          [-122.727297, 37.904626],
          [-122.732898, 37.920225],
          [-122.736898, 37.925825],
          [-122.754606, 37.935527],
          [-122.766138, 37.938004],
          [-122.783244, 37.951334],
          [-122.791739, 37.969422],
          [-122.797405, 37.976657],
          [-122.821383, 37.996735],
          [-122.856573, 38.016717],
          [-122.882114, 38.025273],
          [-122.939711, 38.031908],
          [-122.956811, 38.02872],
          [-122.972378, 38.020247],
          [-122.981776, 38.009119],
          [-122.982386, 38.004274],
          [-122.980147, 38.000831],
          [-122.976764, 37.99568],
          [-122.97439, 37.992429],
          [-123.024066, 37.994878],
          [-123.020562, 37.999544],
          [-123.016303, 38.001691],
          [-123.011533, 38.003438],
          [-122.99242, 38.041758],
          [-122.960889, 38.112962],
          [-122.952086, 38.138562],
          [-122.949074, 38.15406],
          [-122.949626, 38.164041],
          [-122.953629, 38.17567],
          [-122.965408, 38.187113],
          [-122.96637, 38.198514],
          [-122.968112, 38.202428],
          [-122.991953, 38.233185],
          [-122.993959, 38.237602],
          [-122.993235, 38.239686],
          [-122.987149, 38.237538],
          [-122.968569, 38.242879],
          [-122.967203, 38.250691],
          [-122.977082, 38.267902],
          [-122.986319, 38.273164],
          [-122.994603, 38.283096],
          [-122.997106, 38.289458],
          [-123.002911, 38.295708],
          [-122.901726, 38.316943],
          [-122.7399, 38.207018],
          [-122.648986, 38.181077],
          [-122.565093, 38.182217],
          [-122.490727, 38.109755]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06053",
      state: "06",
      county: "053",
      name: "Monterey County",
      censusArea: 3280.595,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.213979, 35.789276],
          [-121.175695, 35.794102],
          [-121.346363, 35.795183],
          [-121.356737, 35.804187],
          [-121.388053, 35.823483],
          [-121.406823, 35.844623],
          [-121.413146, 35.855316],
          [-121.426955, 35.860103],
          [-121.439584, 35.86695],
          [-121.462264, 35.885618],
          [-121.461227, 35.896906],
          [-121.463452, 35.904416],
          [-121.472435, 35.91989],
          [-121.4862, 35.970348],
          [-121.503112, 36.000299],
          [-121.51159, 36.006598],
          [-121.531876, 36.014368],
          [-121.553716, 36.019798],
          [-121.569612, 36.021539],
          [-121.574602, 36.025156],
          [-121.590395, 36.050363],
          [-121.589183, 36.053775],
          [-121.592853, 36.065062],
          [-121.606845, 36.072065],
          [-121.618672, 36.087767],
          [-121.622009, 36.099695],
          [-121.629634, 36.114452],
          [-121.680145, 36.165818],
          [-121.717176, 36.195146],
          [-121.779851, 36.227407],
          [-121.797059, 36.234211],
          [-121.806979, 36.232907],
          [-121.813734, 36.234235],
          [-121.826425, 36.24186],
          [-121.835785, 36.250748],
          [-121.83935, 36.260478],
          [-121.851967, 36.277831],
          [-121.874797, 36.289064],
          [-121.888491, 36.30281],
          [-121.894714, 36.317806],
          [-121.892917, 36.340428],
          [-121.905446, 36.358269],
          [-121.902669, 36.363901],
          [-121.901813, 36.381879],
          [-121.903195, 36.393603],
          [-121.905657, 36.398206],
          [-121.914378, 36.404344],
          [-121.917463, 36.414809],
          [-121.91474, 36.42589],
          [-121.9255, 36.453918],
          [-121.937205, 36.472488],
          [-121.9416, 36.485602],
          [-121.939216, 36.496896],
          [-121.938763, 36.506423],
          [-121.943678, 36.511802],
          [-121.944666, 36.521861],
          [-121.928769, 36.523147],
          [-121.925937, 36.525173],
          [-121.932508, 36.559935],
          [-121.942533, 36.566435],
          [-121.949659, 36.567602],
          [-121.95146, 36.564009],
          [-121.957335, 36.564482],
          [-121.972594, 36.57337],
          [-121.978592, 36.580488],
          [-121.970427, 36.582754],
          [-121.941666, 36.618059],
          [-121.938551, 36.633908],
          [-121.93643, 36.636746],
          [-121.929666, 36.636959],
          [-121.923866, 36.634559],
          [-121.890164, 36.609259],
          [-121.889064, 36.601759],
          [-121.886764, 36.601459],
          [-121.871364, 36.604559],
          [-121.860604, 36.611136],
          [-121.842263, 36.630059],
          [-121.831995, 36.644856],
          [-121.825052, 36.657207],
          [-121.814462, 36.682858],
          [-121.807062, 36.714157],
          [-121.805643, 36.750239],
          [-121.788278, 36.803994],
          [-121.791544, 36.815186],
          [-121.809363, 36.848654],
          [-121.810552, 36.850648],
          [-121.745288, 36.909341],
          [-121.644001, 36.893996],
          [-121.597698, 36.83735],
          [-121.45134, 36.724793],
          [-121.468086, 36.685518],
          [-121.350092, 36.648357],
          [-121.318373, 36.610026],
          [-121.311788, 36.502736],
          [-121.244091, 36.506911],
          [-121.193465, 36.445751],
          [-121.040797, 36.323969],
          [-121.02724, 36.259893],
          [-120.920551, 36.311012],
          [-120.86508, 36.292578],
          [-120.761483, 36.20358],
          [-120.718169, 36.196848],
          [-120.75834, 36.308604],
          [-120.678582, 36.267319],
          [-120.62691, 36.203227],
          [-120.648784, 36.10799],
          [-120.315068, 35.907186],
          [-120.243275, 35.877669],
          [-120.213979, 35.789276]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06061",
      state: "06",
      county: "061",
      name: "Placer County",
      censusArea: 1407.009,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.005318, 39.316479],
          [-120.00532, 39.31635],
          [-120.005413, 39.313848],
          [-120.005414, 39.313345],
          [-120.005743, 39.228664],
          [-120.005746, 39.22521],
          [-120.004504, 39.165599],
          [-120.003402, 39.112687],
          [-120.002461, 39.067489],
          [-120.143663, 39.067285],
          [-120.184098, 39.031014],
          [-120.43524, 39.028397],
          [-120.470985, 38.965507],
          [-120.556321, 38.915164],
          [-120.642458, 38.944404],
          [-120.746395, 39.010352],
          [-121.040406, 38.915651],
          [-121.141009, 38.71198],
          [-121.151857, 38.711502],
          [-121.188571, 38.714308],
          [-121.242795, 38.718241],
          [-121.484396, 38.734598],
          [-121.469356, 38.925992],
          [-121.414885, 38.996429],
          [-121.279533, 39.034618],
          [-121.137397, 39.037858],
          [-121.068788, 39.005177],
          [-120.986348, 39.102234],
          [-120.857909, 39.206235],
          [-120.691606, 39.303981],
          [-120.645795, 39.315155],
          [-120.005318, 39.316479]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08041",
      state: "08",
      county: "041",
      name: "El Paso County",
      censusArea: 2126.801,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.053921, 38.522393],
          [-104.941533, 38.519569],
          [-104.942426, 38.649882],
          [-104.938994, 38.796884],
          [-105.072184, 38.799382],
          [-105.028903, 38.868907],
          [-105.033544, 39.129819],
          [-104.662896, 39.129527],
          [-104.051663, 39.128366],
          [-104.055528, 38.868868],
          [-104.053921, 38.522393]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08049",
      state: "08",
      county: "049",
      name: "Grand County",
      censusArea: 1846.33,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.652112, 40.445231],
          [-106.612321, 40.379383],
          [-106.489842, 40.411578],
          [-106.44342, 40.346533],
          [-106.353758, 40.357536],
          [-106.230074, 40.323382],
          [-106.018622, 40.373295],
          [-105.959659, 40.348149],
          [-105.891754, 40.476661],
          [-105.854926, 40.486252],
          [-105.653321, 40.260457],
          [-105.638797, 40.038787],
          [-105.675798, 39.932445],
          [-105.690348, 39.851996],
          [-105.736426, 39.804801],
          [-105.887395, 39.796996],
          [-105.924618, 39.698972],
          [-106.023783, 39.68842],
          [-106.083711, 39.805964],
          [-106.252027, 39.914712],
          [-106.434508, 39.924914],
          [-106.626569, 39.924785],
          [-106.632257, 40.341559],
          [-106.652112, 40.445231]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12085",
      state: "12",
      county: "085",
      name: "Martin County",
      censusArea: 543.464,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.38469, 26.958382],
          [-80.885639, 26.958919],
          [-80.677427, 27.121619],
          [-80.677862, 27.205988],
          [-80.481534, 27.20637],
          [-80.481413, 27.20637],
          [-80.28551, 27.206025],
          [-80.284771, 27.263723],
          [-80.199288, 27.263022],
          [-80.19309, 27.249546],
          [-80.16147, 27.192814],
          [-80.153375, 27.169308],
          [-80.159554, 27.163325],
          [-80.14982, 27.143557],
          [-80.138605, 27.111517],
          [-80.116772, 27.072397],
          [-80.093909, 27.018587],
          [-80.079531, 26.9705],
          [-80.246728, 26.957189],
          [-80.374523, 26.958386],
          [-80.38469, 26.958382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12093",
      state: "12",
      county: "093",
      name: "Okeechobee County",
      censusArea: 768.908,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.679821, 27.55847],
          [-80.679288, 27.455697],
          [-80.678609, 27.448598],
          [-80.677862, 27.205988],
          [-80.677427, 27.121619],
          [-80.885639, 26.958919],
          [-80.871169, 27.154951],
          [-80.943186, 27.210599],
          [-80.951656, 27.224825],
          [-80.970916, 27.217698],
          [-81.213717, 27.528654],
          [-81.142164, 27.643238],
          [-80.87315, 27.642288],
          [-80.778047, 27.643195],
          [-80.777166, 27.558733],
          [-80.679821, 27.55847]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12105",
      state: "12",
      county: "105",
      name: "Polk County",
      censusArea: 1797.837,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.818903, 28.361712],
          [-81.657268, 28.347098],
          [-81.657189, 28.259217],
          [-81.551345, 28.249045],
          [-81.524083, 28.201012],
          [-81.524227, 28.142805],
          [-81.455647, 28.142894],
          [-81.455315, 28.085367],
          [-81.375467, 28.0851],
          [-81.378718, 28.011743],
          [-81.308696, 27.92188],
          [-81.301862, 27.862081],
          [-81.208124, 27.821125],
          [-81.131495, 27.6494],
          [-81.142164, 27.643238],
          [-81.563318, 27.646647],
          [-82.054349, 27.646382],
          [-82.054035, 27.850683],
          [-82.056404, 28.080385],
          [-82.056261, 28.171592],
          [-82.105853, 28.17165],
          [-82.106206, 28.25924],
          [-82.055819, 28.312794],
          [-81.957641, 28.34518],
          [-81.818903, 28.361712]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12121",
      state: "12",
      county: "121",
      name: "Suwannee County",
      censusArea: 688.552,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.247253, 30.26067],
          [-83.170969, 30.385225],
          [-83.078706, 30.438116],
          [-83.002058, 30.429371],
          [-82.794594, 30.337024],
          [-82.800477, 29.932126],
          [-82.864246, 29.909836],
          [-82.879802, 29.886847],
          [-82.963112, 29.99305],
          [-83.160557, 30.109823],
          [-83.22636, 30.114621],
          [-83.247253, 30.26067]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12133",
      state: "12",
      county: "133",
      name: "Washington County",
      censusArea: 582.803,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.434782, 30.56756],
          [-85.486358, 30.567574],
          [-85.487968, 30.436655],
          [-85.8535, 30.440623],
          [-85.992736, 30.38932],
          [-85.858001, 30.496905],
          [-85.915297, 30.636427],
          [-85.844421, 30.70313],
          [-85.821616, 30.790775],
          [-85.736051, 30.74637],
          [-85.619011, 30.743868],
          [-85.599934, 30.830552],
          [-85.482276, 30.829509],
          [-85.432274, 30.785457],
          [-85.434782, 30.56756]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13007",
      state: "13",
      county: "007",
      name: "Baker County",
      censusArea: 341.944,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.637579, 31.433926],
          [-84.429876, 31.436661],
          [-84.141124, 31.440179],
          [-84.20066, 31.355525],
          [-84.340552, 31.305341],
          [-84.40435, 31.199143],
          [-84.48331, 31.155536],
          [-84.508078, 31.0784],
          [-84.542653, 31.079029],
          [-84.537101, 31.255932],
          [-84.641673, 31.258967],
          [-84.637579, 31.433926]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13021",
      state: "13",
      county: "021",
      name: "Bibb County",
      censusArea: 249.762,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.710685, 32.952792],
          [-83.658211, 32.887913],
          [-83.513511, 32.844868],
          [-83.502603, 32.801119],
          [-83.597656, 32.664338],
          [-83.701152, 32.692169],
          [-83.824398, 32.741861],
          [-83.891922, 32.848351],
          [-83.710685, 32.952792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13035",
      state: "13",
      county: "035",
      name: "Butts County",
      censusArea: 184.393,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.863058, 33.368278],
          [-83.814103, 33.242134],
          [-83.822261, 33.180238],
          [-84.041498, 33.202629],
          [-84.123767, 33.202824],
          [-84.102582, 33.298191],
          [-84.042663, 33.333501],
          [-84.044597, 33.333495],
          [-84.044594, 33.333656],
          [-83.923913, 33.444194],
          [-83.863058, 33.368278]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13047",
      state: "13",
      county: "047",
      name: "Catoosa County",
      censusArea: 162.159,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.144789, 34.767639],
          [-85.201856, 34.77508],
          [-85.20218, 34.854451],
          [-85.264762, 34.854277],
          [-85.265055, 34.985075],
          [-85.254955, 34.985175],
          [-85.235555, 34.985475],
          [-85.230354, 34.985475],
          [-85.221854, 34.985475],
          [-85.220554, 34.985575],
          [-85.217854, 34.985675],
          [-85.216554, 34.985675],
          [-85.185905, 34.985995],
          [-85.180553, 34.986075],
          [-85.045183, 34.986883],
          [-85.045052, 34.986859],
          [-84.97986, 34.987647],
          [-85.061648, 34.819478],
          [-85.144789, 34.767639]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13065",
      state: "13",
      county: "065",
      name: "Clinch County",
      censusArea: 800.222,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.459792, 30.584287],
          [-82.524899, 30.588189],
          [-82.536233, 30.588885],
          [-82.545055, 30.589361],
          [-82.553159, 30.589934],
          [-82.565476, 30.590622],
          [-82.569237, 30.590965],
          [-82.584002, 30.591796],
          [-82.604005, 30.713676],
          [-82.766159, 30.732404],
          [-82.846353, 30.834988],
          [-82.971336, 30.869392],
          [-82.971247, 31.183988],
          [-82.671669, 31.183739],
          [-82.592071, 31.018487],
          [-82.490558, 30.963166],
          [-82.495476, 30.819553],
          [-82.435852, 30.820068],
          [-82.418915, 30.581745],
          [-82.459544, 30.584272],
          [-82.459792, 30.584287]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13075",
      state: "13",
      county: "075",
      name: "Cook County",
      censusArea: 227.162,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.295052, 31.027278],
          [-83.475617, 31.031964],
          [-83.574857, 31.078135],
          [-83.50981, 31.206181],
          [-83.512607, 31.327405],
          [-83.460644, 31.326263],
          [-83.434492, 31.350361],
          [-83.305611, 31.167233],
          [-83.295052, 31.027278]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28057",
      state: "28",
      county: "057",
      name: "Itawamba County",
      censusArea: 532.787,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.542079, 34.465276],
          [-88.329193, 34.463571],
          [-88.155305, 34.463087],
          [-88.165634, 34.383102],
          [-88.16591, 34.380926],
          [-88.173632, 34.321054],
          [-88.175867, 34.302171],
          [-88.176889, 34.293858],
          [-88.203597, 34.08653],
          [-88.543352, 34.089044],
          [-88.542079, 34.465276]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28069",
      state: "28",
      county: "069",
      name: "Kemper County",
      censusArea: 766.182,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.812782, 32.925973],
          [-88.347692, 32.929059],
          [-88.382985, 32.626954],
          [-88.383039, 32.626679],
          [-88.388739, 32.578116],
          [-88.914516, 32.576955],
          [-88.914413, 32.9266],
          [-88.812782, 32.925973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22119",
      state: "22",
      county: "119",
      name: "Webster County",
      lsad: "Parish",
      censusArea: 593.03,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.372781, 32.410153],
          [-93.441212, 32.410431],
          [-93.448039, 32.866321],
          [-93.530405, 32.975397],
          [-93.520994, 33.018616],
          [-93.520971, 33.018616],
          [-93.490893, 33.018442],
          [-93.49052, 33.018442],
          [-93.489506, 33.018443],
          [-93.467042, 33.018611],
          [-93.377134, 33.018234],
          [-93.340353, 33.018337],
          [-93.308398, 33.018179],
          [-93.308181, 33.018156],
          [-93.238607, 33.017992],
          [-93.237381, 32.71625],
          [-93.186571, 32.716755],
          [-93.178767, 32.584916],
          [-93.147193, 32.455173],
          [-93.236656, 32.410071],
          [-93.372781, 32.410153]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22125",
      state: "22",
      county: "125",
      name: "West Feliciana County",
      lsad: "Parish",
      censusArea: 403.214,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.33087, 30.658677],
          [-91.340652, 30.670238],
          [-91.353092, 30.72177],
          [-91.38062, 30.758804],
          [-91.553019, 30.730798],
          [-91.515626, 30.861378],
          [-91.661372, 30.857099],
          [-91.662506, 30.970362],
          [-91.646409, 30.986146],
          [-91.640613, 30.996125],
          [-91.636942, 30.999416],
          [-91.625118, 30.999167],
          [-91.538727, 30.999388],
          [-91.425749, 30.999007],
          [-91.423621, 30.998984],
          [-91.224839, 30.999183],
          [-91.224068, 30.999183],
          [-91.176209, 30.999144],
          [-91.17614, 30.999144],
          [-91.236164, 30.795093],
          [-91.320706, 30.699417],
          [-91.310061, 30.653961],
          [-91.322256, 30.656025],
          [-91.33087, 30.658677]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23017",
      state: "23",
      county: "017",
      name: "Oxford County",
      censusArea: 2076.84,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.595631, 44.032057],
          [-70.587331, 44.122516],
          [-70.624329, 44.171036],
          [-70.821803, 44.077089],
          [-70.715418, 43.961815],
          [-70.747507, 43.945901],
          [-70.72978, 43.92833],
          [-70.782984, 43.814147],
          [-70.989041, 43.792152],
          [-70.989067, 43.79244],
          [-70.992086, 43.886261],
          [-70.992986, 43.914232],
          [-71.001367, 44.092931],
          [-71.001335, 44.093205],
          [-71.008764, 44.258443],
          [-71.008736, 44.258825],
          [-71.010239, 44.284774],
          [-71.01349, 44.340882],
          [-71.029101, 44.610331],
          [-71.037518, 44.755607],
          [-71.060101, 45.019661],
          [-71.07564, 45.224093],
          [-71.084334, 45.305293],
          [-71.030565, 45.312652],
          [-71.014268, 45.316761],
          [-71.00905, 45.319022],
          [-71.002401, 45.327956],
          [-71.005087, 45.331545],
          [-70.990233, 45.336277],
          [-70.950824, 45.33453],
          [-70.917904, 45.311924],
          [-70.912176, 45.296768],
          [-70.913732, 45.292746],
          [-70.907978, 45.269316],
          [-70.896898, 45.242031],
          [-70.885029, 45.234873],
          [-70.857042, 45.22916],
          [-70.842845, 45.234422],
          [-70.839482, 45.237829],
          [-70.831612, 45.260055],
          [-70.769228, 44.732228],
          [-70.61503, 44.795634],
          [-70.510598, 44.627216],
          [-70.549985, 44.610727],
          [-70.280748, 44.569619],
          [-70.280908, 44.505095],
          [-70.238904, 44.458942],
          [-70.274412, 44.440632],
          [-70.26835, 44.416398],
          [-70.276752, 44.407534],
          [-70.265223, 44.362913],
          [-70.325218, 44.208399],
          [-70.334094, 44.210753],
          [-70.391735, 44.153562],
          [-70.480078, 44.032078],
          [-70.595631, 44.032057]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24015",
      state: "24",
      county: "015",
      name: "Cecil County",
      censusArea: 346.273,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.233259, 39.721305],
          [-76.224191, 39.721328],
          [-76.135584, 39.721556],
          [-76.027618, 39.721833],
          [-76.013067, 39.72192],
          [-75.810068, 39.721906],
          [-75.799563, 39.721882],
          [-75.788359, 39.721811],
          [-75.788395, 39.700287],
          [-75.788395, 39.700031],
          [-75.788658, 39.681911],
          [-75.788616, 39.680742],
          [-75.78745, 39.637455],
          [-75.78689, 39.630575],
          [-75.779383, 39.536522],
          [-75.77924, 39.534737],
          [-75.766693, 39.377537],
          [-75.766714, 39.377537],
          [-75.766731, 39.377537],
          [-75.7668, 39.377586],
          [-75.767044, 39.377739],
          [-75.767105, 39.377796],
          [-75.767253, 39.377833],
          [-75.767358, 39.377871],
          [-75.767413, 39.3779],
          [-75.767542, 39.377937],
          [-75.767665, 39.377913],
          [-75.767769, 39.377875],
          [-75.767831, 39.377937],
          [-75.767985, 39.378079],
          [-75.768059, 39.378126],
          [-75.768102, 39.378117],
          [-75.768249, 39.378007],
          [-75.768304, 39.377978],
          [-75.768365, 39.377968],
          [-75.768439, 39.37804],
          [-75.768489, 39.378101],
          [-75.768526, 39.378111],
          [-75.768569, 39.378106],
          [-75.768642, 39.378053],
          [-75.768691, 39.378034],
          [-75.768783, 39.378043],
          [-75.7689, 39.378086],
          [-75.768999, 39.378114],
          [-75.769116, 39.378166],
          [-75.769165, 39.378209],
          [-75.769233, 39.378228],
          [-75.769294, 39.378213],
          [-75.769331, 39.378194],
          [-75.769386, 39.378237],
          [-75.769423, 39.378294],
          [-75.769455, 39.378394],
          [-75.769461, 39.378461],
          [-75.769443, 39.378489],
          [-75.769437, 39.378532],
          [-75.769474, 39.37857],
          [-75.769535, 39.37857],
          [-75.769621, 39.378546],
          [-75.769695, 39.378603],
          [-75.769792, 39.378622],
          [-75.769865, 39.378622],
          [-75.770047, 39.37864],
          [-75.770388, 39.378743],
          [-75.770485, 39.378724],
          [-75.770545, 39.378657],
          [-75.770836, 39.378647],
          [-75.770994, 39.378684],
          [-75.771116, 39.378693],
          [-75.771334, 39.378664],
          [-75.77148, 39.378739],
          [-75.771578, 39.378833],
          [-75.771699, 39.378899],
          [-75.771845, 39.378927],
          [-75.77187, 39.379002],
          [-75.77187, 39.379115],
          [-75.771968, 39.379143],
          [-75.77199, 39.37915],
          [-75.772462, 39.379431],
          [-75.772688, 39.379525],
          [-75.773112, 39.379544],
          [-75.773171, 39.379497],
          [-75.773305, 39.37945],
          [-75.773451, 39.379515],
          [-75.773508, 39.379549],
          [-75.773651, 39.379486],
          [-75.77387, 39.379371],
          [-75.773962, 39.379294],
          [-75.77414, 39.379003],
          [-75.774253, 39.378916],
          [-75.774387, 39.378921],
          [-75.774635, 39.379042],
          [-75.775045, 39.37907],
          [-75.775088, 39.37907],
          [-75.775449, 39.37896],
          [-75.775668, 39.37896],
          [-75.775909, 39.379015],
          [-75.776093, 39.378971],
          [-75.776312, 39.378993],
          [-75.776369, 39.379059],
          [-75.776213, 39.380026],
          [-75.776248, 39.38019],
          [-75.776304, 39.380256],
          [-75.776355, 39.380268],
          [-75.776503, 39.380306],
          [-75.776757, 39.3803],
          [-75.776998, 39.380405],
          [-75.777671, 39.38024],
          [-75.777918, 39.380246],
          [-75.77806, 39.38029],
          [-75.77922, 39.380916],
          [-75.779503, 39.381169],
          [-75.780126, 39.381471],
          [-75.780331, 39.381653],
          [-75.780409, 39.381889],
          [-75.780473, 39.381944],
          [-75.781095, 39.382125],
          [-75.781131, 39.382125],
          [-75.781548, 39.381966],
          [-75.781753, 39.381954],
          [-75.781846, 39.38195],
          [-75.782023, 39.381867],
          [-75.782235, 39.381856],
          [-75.782957, 39.382164],
          [-75.78365, 39.382439],
          [-75.783891, 39.382505],
          [-75.784224, 39.382549],
          [-75.784415, 39.382527],
          [-75.784443, 39.3825],
          [-75.784465, 39.38231],
          [-75.784486, 39.382143],
          [-75.78433, 39.381385],
          [-75.784309, 39.38094],
          [-75.78433, 39.380687],
          [-75.784415, 39.380363],
          [-75.784656, 39.380165],
          [-75.784968, 39.380088],
          [-75.78538, 39.380091],
          [-75.785732, 39.380094],
          [-75.786036, 39.380061],
          [-75.786383, 39.380105],
          [-75.786567, 39.380155],
          [-75.786585, 39.380158],
          [-75.787202, 39.380267],
          [-75.78731, 39.380287],
          [-75.78741, 39.380296],
          [-75.788032, 39.380353],
          [-75.78823, 39.380424],
          [-75.788351, 39.380512],
          [-75.788381, 39.38061],
          [-75.788478, 39.380919],
          [-75.788584, 39.381061],
          [-75.788768, 39.381111],
          [-75.789398, 39.381128],
          [-75.78949, 39.381095],
          [-75.789667, 39.381012],
          [-75.789865, 39.380891],
          [-75.789971, 39.380815],
          [-75.790531, 39.380342],
          [-75.790996, 39.380043],
          [-75.791182, 39.379925],
          [-75.791833, 39.379843],
          [-75.792043, 39.379822],
          [-75.792279, 39.379799],
          [-75.792357, 39.379804],
          [-75.792484, 39.379826],
          [-75.792534, 39.379843],
          [-75.79288, 39.380096],
          [-75.79295, 39.380116],
          [-75.792994, 39.380129],
          [-75.7938, 39.37998],
          [-75.793907, 39.379936],
          [-75.794239, 39.379744],
          [-75.79431, 39.379641],
          [-75.794381, 39.379541],
          [-75.794423, 39.379102],
          [-75.794494, 39.378959],
          [-75.794664, 39.378756],
          [-75.79482, 39.378009],
          [-75.795018, 39.377789],
          [-75.795216, 39.377701],
          [-75.795443, 39.377586],
          [-75.795606, 39.377317],
          [-75.795726, 39.37724],
          [-75.795825, 39.377245],
          [-75.796038, 39.377322],
          [-75.79625, 39.377229],
          [-75.796356, 39.377229],
          [-75.796441, 39.377542],
          [-75.796483, 39.377575],
          [-75.796547, 39.377586],
          [-75.79666, 39.377564],
          [-75.796965, 39.377295],
          [-75.79729, 39.377273],
          [-75.797566, 39.377268],
          [-75.797779, 39.377141],
          [-75.797998, 39.377251],
          [-75.798225, 39.377279],
          [-75.798607, 39.37724],
          [-75.798748, 39.37713],
          [-75.798847, 39.377119],
          [-75.799222, 39.377224],
          [-75.799605, 39.377372],
          [-75.799678, 39.377368],
          [-75.799852, 39.377361],
          [-75.800263, 39.377224],
          [-75.800461, 39.377202],
          [-75.800567, 39.377229],
          [-75.800617, 39.377317],
          [-75.800588, 39.377608],
          [-75.800701, 39.377691],
          [-75.800907, 39.377746],
          [-75.801098, 39.377867],
          [-75.801695, 39.377912],
          [-75.80182, 39.377922],
          [-75.802237, 39.377653],
          [-75.802605, 39.377592],
          [-75.802752, 39.377493],
          [-75.802761, 39.377488],
          [-75.802874, 39.377334],
          [-75.802917, 39.377318],
          [-75.80337, 39.377219],
          [-75.803794, 39.376972],
          [-75.804162, 39.376576],
          [-75.804304, 39.376291],
          [-75.804311, 39.375939],
          [-75.804354, 39.375818],
          [-75.804495, 39.375609],
          [-75.804686, 39.375434],
          [-75.804757, 39.375417],
          [-75.804955, 39.375456],
          [-75.805141, 39.375464],
          [-75.805514, 39.375483],
          [-75.805684, 39.375505],
          [-75.805741, 39.3755],
          [-75.805861, 39.375467],
          [-75.805925, 39.375439],
          [-75.806073, 39.375258],
          [-75.806201, 39.375198],
          [-75.806378, 39.375274],
          [-75.806484, 39.375384],
          [-75.806498, 39.375467],
          [-75.806472, 39.375483],
          [-75.806318, 39.375583],
          [-75.806229, 39.375897],
          [-75.806333, 39.376206],
          [-75.806184, 39.37673],
          [-75.806165, 39.376799],
          [-75.806417, 39.377096],
          [-75.806552, 39.377135],
          [-75.806958, 39.377254],
          [-75.806992, 39.377264],
          [-75.807076, 39.377328],
          [-75.807139, 39.377376],
          [-75.807328, 39.377522],
          [-75.807392, 39.377571],
          [-75.807435, 39.377604],
          [-75.807564, 39.377703],
          [-75.807608, 39.377737],
          [-75.807696, 39.377804],
          [-75.807898, 39.37796],
          [-75.807976, 39.377972],
          [-75.808087, 39.377991],
          [-75.808631, 39.378081],
          [-75.808741, 39.378066],
          [-75.808879, 39.378048],
          [-75.8092, 39.378101],
          [-75.809396, 39.378425],
          [-75.809425, 39.378832],
          [-75.809359, 39.378894],
          [-75.809237, 39.379495],
          [-75.809259, 39.379521],
          [-75.809452, 39.379749],
          [-75.809634, 39.379758],
          [-75.809817, 39.379768],
          [-75.809989, 39.379806],
          [-75.810108, 39.37983],
          [-75.811537, 39.379984],
          [-75.812984, 39.380026],
          [-75.814614, 39.38027],
          [-75.816313, 39.380654],
          [-75.816356, 39.380681],
          [-75.816968, 39.381064],
          [-75.817365, 39.3814],
          [-75.817646, 39.381638],
          [-75.817925, 39.381874],
          [-75.817975, 39.381916],
          [-75.818345, 39.382081],
          [-75.818696, 39.382239],
          [-75.818829, 39.382299],
          [-75.819576, 39.382141],
          [-75.819956, 39.382061],
          [-75.821266, 39.381755],
          [-75.822289, 39.381609],
          [-75.823658, 39.381413],
          [-75.823761, 39.381354],
          [-75.824392, 39.380998],
          [-75.824458, 39.380961],
          [-75.825426, 39.379846],
          [-75.82742, 39.378236],
          [-75.827991, 39.377921],
          [-75.828161, 39.377762],
          [-75.829369, 39.376632],
          [-75.829513, 39.376499],
          [-75.829736, 39.376173],
          [-75.830031, 39.375742],
          [-75.830062, 39.375659],
          [-75.830183, 39.375337],
          [-75.830023, 39.375086],
          [-75.830523, 39.374689],
          [-75.830661, 39.374579],
          [-75.8308, 39.374469],
          [-75.831173, 39.374173],
          [-75.831533, 39.374109],
          [-75.832316, 39.37397],
          [-75.8334, 39.373834],
          [-75.834014, 39.373757],
          [-75.835534, 39.373174],
          [-75.835719, 39.373103],
          [-75.836593, 39.37277],
          [-75.836611, 39.372763],
          [-75.839605, 39.371978],
          [-75.840283, 39.371734],
          [-75.841682, 39.37123],
          [-75.841727, 39.371213],
          [-75.842385, 39.370978],
          [-75.843785, 39.369951],
          [-75.844304, 39.369571],
          [-75.84432, 39.369544],
          [-75.845476, 39.367698],
          [-75.846048, 39.367628],
          [-75.846611, 39.367561],
          [-75.846694, 39.367551],
          [-75.846776, 39.367541],
          [-75.846901, 39.367526],
          [-75.847037, 39.367584],
          [-75.848501, 39.368208],
          [-75.84872, 39.368065],
          [-75.848845, 39.367984],
          [-75.84897, 39.367902],
          [-75.849392, 39.367627],
          [-75.851468, 39.366866],
          [-75.852394, 39.366527],
          [-75.853064, 39.36589],
          [-75.853652, 39.365333],
          [-75.85543, 39.364562],
          [-75.856646, 39.364892],
          [-75.857246, 39.365055],
          [-75.858592, 39.36593],
          [-75.859169, 39.366306],
          [-75.860832, 39.367466],
          [-75.861038, 39.36761],
          [-75.861415, 39.367549],
          [-75.862518, 39.36737],
          [-75.863754, 39.366436],
          [-75.864978, 39.366503],
          [-75.865669, 39.366542],
          [-75.866206, 39.366893],
          [-75.86678, 39.367268],
          [-75.867355, 39.367644],
          [-75.867614, 39.367813],
          [-75.868066, 39.367759],
          [-75.868377, 39.367721],
          [-75.872062, 39.366344],
          [-75.872575, 39.366223],
          [-75.873341, 39.366043],
          [-75.874412, 39.365791],
          [-75.875024, 39.365789],
          [-75.875801, 39.365787],
          [-75.876636, 39.365707],
          [-75.877848, 39.36559],
          [-75.877952, 39.365581],
          [-75.877965, 39.36558],
          [-75.879463, 39.365467],
          [-75.879839, 39.365329],
          [-75.880935, 39.364929],
          [-75.882131, 39.364327],
          [-75.882141, 39.364322],
          [-75.882827, 39.363452],
          [-75.883628, 39.362251],
          [-75.884391, 39.361354],
          [-75.885101, 39.360843],
          [-75.885864, 39.360797],
          [-75.888499, 39.361889],
          [-75.888542, 39.361907],
          [-75.888874, 39.362131],
          [-75.890174, 39.36301],
          [-75.891222, 39.363808],
          [-75.892288, 39.36462],
          [-75.894523, 39.365638],
          [-75.894855, 39.365691],
          [-75.894911, 39.3657],
          [-75.89598, 39.365871],
          [-75.897376, 39.365567],
          [-75.897782, 39.365478],
          [-75.898187, 39.36539],
          [-75.898292, 39.365367],
          [-75.899827, 39.364914],
          [-75.90036, 39.364757],
          [-75.902687, 39.364478],
          [-75.903793, 39.364345],
          [-75.908035, 39.364501],
          [-75.90847, 39.364601],
          [-75.909337, 39.3648],
          [-75.910202, 39.364999],
          [-75.911805, 39.365367],
          [-75.912002, 39.365413],
          [-75.913581, 39.366096],
          [-75.915008, 39.366584],
          [-75.915029, 39.366587],
          [-75.916557, 39.366817],
          [-75.916699, 39.366821],
          [-75.917606, 39.366844],
          [-75.918515, 39.366868],
          [-75.918914, 39.366878],
          [-75.919789, 39.366948],
          [-75.919914, 39.366958],
          [-75.92115, 39.367057],
          [-75.922775, 39.367248],
          [-75.923884, 39.367968],
          [-75.92395, 39.368011],
          [-75.925285, 39.36975],
          [-75.926589, 39.370994],
          [-75.927154, 39.371187],
          [-75.92794, 39.371456],
          [-75.929962, 39.371681],
          [-75.930638, 39.371623],
          [-75.932022, 39.371505],
          [-75.934112, 39.370857],
          [-75.936424, 39.369544],
          [-75.937728, 39.368743],
          [-75.93779, 39.368715],
          [-75.939849, 39.367786],
          [-75.941032, 39.367675],
          [-75.942337, 39.367752],
          [-75.94375, 39.368064],
          [-75.943893, 39.368095],
          [-75.944429, 39.368272],
          [-75.945365, 39.368583],
          [-75.94616, 39.369266],
          [-75.946425, 39.369494],
          [-75.94683, 39.369842],
          [-75.947769, 39.371032],
          [-75.949246, 39.372421],
          [-75.94934, 39.372509],
          [-75.950401, 39.373153],
          [-75.951219, 39.373432],
          [-75.95182, 39.373638],
          [-75.954299, 39.374145],
          [-75.95697, 39.374603],
          [-75.958488, 39.374553],
          [-75.960083, 39.374698],
          [-75.960143, 39.374697],
          [-75.961448, 39.374694],
          [-75.964065, 39.374725],
          [-75.964388, 39.374688],
          [-75.966011, 39.374504],
          [-75.968269, 39.373973],
          [-75.96941, 39.373424],
          [-75.970603, 39.372852],
          [-75.971952, 39.371644],
          [-75.972793, 39.370891],
          [-75.97364, 39.369632],
          [-75.974418, 39.368621],
          [-75.976249, 39.367458],
          [-75.977714, 39.366874],
          [-75.979034, 39.366577],
          [-75.980539, 39.366571],
          [-75.981109, 39.366569],
          [-75.981506, 39.36684],
          [-75.982696, 39.367492],
          [-75.983985, 39.369182],
          [-75.984054, 39.36927],
          [-75.985267, 39.370807],
          [-75.987136, 39.372577],
          [-75.988998, 39.373771],
          [-75.990951, 39.37471],
          [-75.991996, 39.375129],
          [-75.992193, 39.375263],
          [-75.992317, 39.375347],
          [-75.993125, 39.375667],
          [-75.993932, 39.375848],
          [-75.994178, 39.375904],
          [-75.995094, 39.376106],
          [-75.996147, 39.376235],
          [-75.99855, 39.376453],
          [-75.999397, 39.376636],
          [-76.000221, 39.376651],
          [-76.001113, 39.376579],
          [-76.001541, 39.376522],
          [-76.001704, 39.376507],
          [-76.002197, 39.376462],
          [-76.002361, 39.376448],
          [-76.002463, 39.37644],
          [-76.002514, 39.384805],
          [-76.002515, 39.385024],
          [-76.006805, 39.385174],
          [-76.022304, 39.385716],
          [-76.035464, 39.386176],
          [-76.035568, 39.38618],
          [-76.035644, 39.386213],
          [-76.039279, 39.387796],
          [-76.039932, 39.38808],
          [-76.039973, 39.388327],
          [-76.040854, 39.393594],
          [-76.040962, 39.394237],
          [-76.040612, 39.394692],
          [-76.035298, 39.401609],
          [-76.035002, 39.401994],
          [-76.03472, 39.402093],
          [-76.018726, 39.407696],
          [-76.018061, 39.407929],
          [-76.016531, 39.408465],
          [-76.015763, 39.408947],
          [-76.012567, 39.410955],
          [-76.009693, 39.41276],
          [-76.00688, 39.414527],
          [-76.002856, 39.421225],
          [-75.998586, 39.428333],
          [-75.997396, 39.430314],
          [-75.996697, 39.430549],
          [-75.992742, 39.431877],
          [-75.992425, 39.431983],
          [-75.98884, 39.433187],
          [-75.982585, 39.435287],
          [-75.977751, 39.44302],
          [-75.976747, 39.444627],
          [-75.976698, 39.44569],
          [-75.976601, 39.447808],
          [-75.990005, 39.458646],
          [-75.990128, 39.458624],
          [-75.991779, 39.458332],
          [-75.994432, 39.457862],
          [-75.998276, 39.457182],
          [-76.002497, 39.450231],
          [-76.002513, 39.450204],
          [-76.002579, 39.450194],
          [-76.009071, 39.449256],
          [-76.009452, 39.449201],
          [-76.009536, 39.449316],
          [-76.01188, 39.452524],
          [-76.012312, 39.453115],
          [-76.011716, 39.454165],
          [-76.010009, 39.45717],
          [-76.002926, 39.469642],
          [-75.99657, 39.476658],
          [-75.995289, 39.483014],
          [-75.994135, 39.488743],
          [-75.993469, 39.490583],
          [-75.986298, 39.510398],
          [-75.985403, 39.512108],
          [-75.985238, 39.512424],
          [-75.980279, 39.5219],
          [-75.976105, 39.529876],
          [-75.976057, 39.529968],
          [-75.975313, 39.530678],
          [-75.973678, 39.532237],
          [-75.966955, 39.53865],
          [-75.966959, 39.538839],
          [-75.967093, 39.544623],
          [-75.967173, 39.548047],
          [-75.967186, 39.548627],
          [-75.967221, 39.55014],
          [-75.970337, 39.557637],
          [-75.992633, 39.563098],
          [-75.999669, 39.560488],
          [-76.00019, 39.559696],
          [-76.001244, 39.558096],
          [-76.002793, 39.555741],
          [-76.003434, 39.554768],
          [-76.004207, 39.553594],
          [-76.006213, 39.550546],
          [-76.006272, 39.550457],
          [-76.006341, 39.550352],
          [-76.03042, 39.548784],
          [-76.046876, 39.547713],
          [-76.063379, 39.546638],
          [-76.063811, 39.54661],
          [-76.075714, 39.543032],
          [-76.085972, 39.559011],
          [-76.106161, 39.58061],
          [-76.135674, 39.613108],
          [-76.144081, 39.617709],
          [-76.233259, 39.721305]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24027",
      state: "24",
      county: "027",
      name: "Howard County",
      censusArea: 250.741,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.144669, 39.294707],
          [-77.186804, 39.338203],
          [-77.168801, 39.353502],
          [-77.168558, 39.353719],
          [-77.16808, 39.353957],
          [-76.882274, 39.350185],
          [-76.76383, 39.251312],
          [-76.739528, 39.232163],
          [-76.723811, 39.228181],
          [-76.705184, 39.215548],
          [-76.697085, 39.214092],
          [-76.719217, 39.19148],
          [-76.722606, 39.182857],
          [-76.740631, 39.179221],
          [-76.763738, 39.166229],
          [-76.776962, 39.150883],
          [-76.786966, 39.128782],
          [-76.824608, 39.117217],
          [-76.840362, 39.103142],
          [-76.849084, 39.109097],
          [-76.869003, 39.112214],
          [-76.888505, 39.130967],
          [-77.144669, 39.294707]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25001",
      state: "25",
      county: "001",
      name: "Barnstable County",
      censusArea: 393.723,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.147411, 42.06159],
          [-70.148294, 42.06195],
          [-70.155415, 42.062409],
          [-70.169781, 42.059736],
          [-70.178468, 42.05642],
          [-70.186816, 42.05045],
          [-70.194456, 42.03947],
          [-70.195345, 42.034163],
          [-70.193074, 42.027576],
          [-70.186295, 42.021308],
          [-70.186708, 42.019904],
          [-70.190834, 42.020028],
          [-70.196693, 42.022429],
          [-70.208016, 42.03073],
          [-70.218701, 42.045848],
          [-70.233256, 42.057714],
          [-70.238875, 42.060479],
          [-70.24354, 42.060569],
          [-70.245385, 42.063733],
          [-70.238087, 42.072878],
          [-70.225626, 42.078601],
          [-70.206899, 42.0819],
          [-70.189305, 42.082337],
          [-70.160166, 42.078628],
          [-70.154177, 42.077139],
          [-70.115968, 42.067638],
          [-70.082624, 42.054657],
          [-70.058531, 42.040363],
          [-70.034806, 42.018916],
          [-70.033501, 42.017736],
          [-70.031054, 42.014562],
          [-70.029953, 42.013135],
          [-70.029449, 42.012482],
          [-70.01628, 41.995403],
          [-70.014109, 41.992587],
          [-70.013876, 41.992285],
          [-70.011957, 41.989796],
          [-70.011898, 41.98972],
          [-70.01051, 41.987563],
          [-70.005717, 41.980113],
          [-70.005458, 41.97971],
          [-70.005332, 41.979515],
          [-70.004608, 41.978389],
          [-70.004271, 41.977865],
          [-69.986196, 41.949769],
          [-69.986085, 41.949597],
          [-69.986024, 41.949465],
          [-69.968598, 41.9117],
          [-69.945314, 41.845222],
          [-69.935952, 41.809422],
          [-69.928652, 41.74125],
          [-69.928311, 41.697981],
          [-69.928308, 41.697681],
          [-69.928275, 41.693504],
          [-69.928265, 41.692247],
          [-69.928265, 41.692242],
          [-69.928261, 41.6917],
          [-69.928723, 41.689634],
          [-69.929377, 41.686713],
          [-69.929457, 41.686357],
          [-69.9309, 41.67991],
          [-69.933114, 41.670014],
          [-69.933149, 41.669955],
          [-69.93412, 41.668304],
          [-69.942742, 41.653649],
          [-69.943474, 41.652406],
          [-69.947599, 41.645394],
          [-69.951169, 41.640799],
          [-69.951911, 41.640656],
          [-69.952844, 41.640476],
          [-69.953924, 41.640268],
          [-69.956862, 41.639701],
          [-69.958272, 41.639429],
          [-69.963234, 41.633794],
          [-69.967869, 41.627503],
          [-69.976478, 41.603664],
          [-69.982768, 41.581812],
          [-69.988215, 41.554704],
          [-69.988495, 41.55439],
          [-69.996546, 41.54536],
          [-69.998071, 41.54365],
          [-69.999063, 41.5434],
          [-70.003768, 41.542213],
          [-70.004136, 41.54212],
          [-70.004554, 41.542166],
          [-70.011211, 41.542892],
          [-70.011504, 41.542924],
          [-70.011725, 41.54312],
          [-70.014456, 41.545534],
          [-70.016584, 41.550772],
          [-70.015059, 41.553037],
          [-70.011596, 41.552766],
          [-70.010644, 41.552692],
          [-70.008854, 41.55451],
          [-70.00153, 41.561953],
          [-70.001281, 41.56247],
          [-70.000937, 41.563184],
          [-70.000627, 41.563827],
          [-69.994357, 41.576846],
          [-69.991723, 41.58851],
          [-69.987192, 41.608579],
          [-69.987111, 41.608765],
          [-69.982946, 41.618316],
          [-69.980418, 41.624113],
          [-69.977489, 41.630832],
          [-69.976886, 41.632213],
          [-69.976641, 41.632777],
          [-69.975579, 41.635212],
          [-69.975245, 41.635978],
          [-69.973035, 41.641046],
          [-69.973037, 41.641126],
          [-69.973153, 41.646963],
          [-69.975719, 41.653738],
          [-69.975811, 41.653798],
          [-69.990659, 41.663471],
          [-69.990668, 41.663476],
          [-69.990734, 41.663519],
          [-69.990748, 41.663528],
          [-69.99079, 41.663556],
          [-69.996194, 41.667076],
          [-69.996359, 41.667184],
          [-69.996509, 41.667246],
          [-70.002697, 41.669799],
          [-70.003016, 41.669931],
          [-70.006833, 41.671506],
          [-70.007011, 41.671579],
          [-70.007153, 41.671587],
          [-70.014211, 41.671971],
          [-70.015789, 41.67153],
          [-70.020964, 41.670085],
          [-70.029119, 41.667807],
          [-70.029319, 41.667751],
          [-70.029346, 41.667744],
          [-70.037145, 41.66688],
          [-70.037255, 41.666868],
          [-70.037757, 41.666812],
          [-70.055523, 41.664843],
          [-70.089238, 41.662813],
          [-70.119904, 41.655455],
          [-70.120935, 41.655208],
          [-70.121351, 41.655108],
          [-70.122597, 41.654809],
          [-70.122689, 41.654787],
          [-70.134702, 41.651905],
          [-70.140163, 41.650594],
          [-70.140207, 41.650584],
          [-70.140437, 41.650529],
          [-70.140877, 41.650423],
          [-70.141049, 41.650423],
          [-70.141141, 41.650423],
          [-70.142592, 41.650424],
          [-70.145882, 41.650427],
          [-70.147011, 41.650428],
          [-70.148435, 41.650429],
          [-70.158621, 41.650438],
          [-70.191061, 41.645259],
          [-70.245867, 41.628479],
          [-70.25621, 41.620698],
          [-70.25542, 41.617541],
          [-70.259601, 41.610863],
          [-70.265424, 41.609333],
          [-70.267587, 41.610912],
          [-70.269687, 41.617775],
          [-70.26913, 41.625742],
          [-70.274522, 41.632927],
          [-70.28132, 41.635125],
          [-70.29062, 41.635196],
          [-70.321588, 41.630508],
          [-70.329924, 41.634578],
          [-70.338067, 41.636338],
          [-70.351634, 41.634687],
          [-70.360352, 41.631069],
          [-70.364892, 41.626721],
          [-70.364744, 41.623671],
          [-70.369854, 41.615888],
          [-70.379151, 41.611361],
          [-70.400581, 41.606382],
          [-70.401148, 41.606451],
          [-70.408535, 41.607345],
          [-70.413944, 41.606965],
          [-70.437246, 41.605329],
          [-70.437889, 41.604249],
          [-70.440556, 41.599767],
          [-70.442225, 41.596963],
          [-70.44435, 41.593393],
          [-70.445289, 41.591815],
          [-70.446543, 41.590247],
          [-70.450382, 41.585445],
          [-70.456052, 41.578355],
          [-70.457356, 41.576724],
          [-70.461093, 41.572051],
          [-70.461278, 41.57182],
          [-70.46196, 41.571213],
          [-70.462206, 41.570995],
          [-70.462239, 41.570965],
          [-70.472778, 41.561595],
          [-70.473328, 41.561106],
          [-70.476231, 41.558524],
          [-70.476256, 41.558502],
          [-70.476275, 41.558493],
          [-70.48335, 41.555259],
          [-70.485155, 41.554434],
          [-70.485571, 41.554244],
          [-70.486815, 41.553887],
          [-70.493244, 41.552044],
          [-70.522009, 41.548999],
          [-70.522327, 41.548965],
          [-70.53164, 41.548807],
          [-70.559482, 41.548334],
          [-70.559689, 41.54833],
          [-70.559728, 41.548326],
          [-70.560909, 41.548203],
          [-70.561261, 41.548167],
          [-70.574023, 41.54684],
          [-70.588412, 41.545345],
          [-70.588494, 41.545336],
          [-70.58881, 41.545304],
          [-70.588894, 41.545295],
          [-70.58957, 41.545225],
          [-70.59657, 41.544497],
          [-70.611081, 41.542989],
          [-70.611147, 41.542975],
          [-70.613006, 41.542584],
          [-70.614398, 41.542292],
          [-70.616715, 41.541805],
          [-70.617223, 41.541698],
          [-70.617638, 41.541611],
          [-70.617911, 41.541553],
          [-70.633607, 41.538254],
          [-70.633713, 41.538192],
          [-70.6338, 41.538141],
          [-70.643627, 41.532357],
          [-70.650059, 41.524172],
          [-70.650128, 41.524084],
          [-70.651566, 41.522254],
          [-70.65205, 41.521639],
          [-70.654104, 41.519025],
          [-70.654168, 41.518992],
          [-70.663856, 41.514031],
          [-70.669518, 41.513339],
          [-70.672253, 41.515774],
          [-70.674646, 41.519385],
          [-70.681397, 41.51755],
          [-70.688353, 41.516789],
          [-70.691364, 41.517842],
          [-70.6948, 41.52564],
          [-70.658659, 41.543385],
          [-70.654302, 41.549926],
          [-70.655365, 41.557498],
          [-70.653899, 41.56516],
          [-70.64878, 41.56987],
          [-70.642748, 41.572385],
          [-70.640948, 41.577325],
          [-70.64204, 41.583066],
          [-70.652449, 41.60521],
          [-70.651986, 41.610184],
          [-70.640003, 41.624616],
          [-70.645251, 41.633547],
          [-70.652614, 41.637829],
          [-70.650419, 41.644202],
          [-70.638695, 41.649427],
          [-70.637632, 41.654573],
          [-70.646308, 41.678433],
          [-70.649285, 41.680943],
          [-70.661475, 41.681756],
          [-70.645962, 41.693794],
          [-70.62544, 41.698691],
          [-70.623652, 41.707398],
          [-70.626529, 41.712995],
          [-70.642914, 41.71841],
          [-70.621622, 41.748972],
          [-70.632578, 41.762821],
          [-70.565281, 41.786705],
          [-70.537289, 41.810859],
          [-70.533823, 41.806323],
          [-70.533303, 41.805643],
          [-70.532656, 41.804796],
          [-70.52507, 41.797907],
          [-70.518468, 41.791912],
          [-70.517615, 41.791139],
          [-70.517411, 41.790953],
          [-70.516921, 41.790595],
          [-70.516651, 41.790398],
          [-70.516101, 41.789996],
          [-70.514603, 41.788901],
          [-70.514483, 41.788814],
          [-70.514424, 41.788771],
          [-70.494048, 41.773883],
          [-70.493016, 41.773318],
          [-70.492884, 41.773245],
          [-70.483477, 41.768094],
          [-70.482152, 41.767368],
          [-70.474051, 41.762931],
          [-70.473707, 41.762743],
          [-70.473335, 41.762539],
          [-70.471999, 41.761808],
          [-70.471969, 41.761791],
          [-70.471807, 41.761702],
          [-70.471552, 41.761563],
          [-70.458968, 41.757906],
          [-70.413713, 41.744756],
          [-70.412476, 41.744397],
          [-70.412071, 41.744336],
          [-70.376992, 41.739029],
          [-70.375341, 41.738779],
          [-70.375253, 41.738774],
          [-70.318091, 41.735748],
          [-70.31529, 41.7356],
          [-70.314667, 41.735567],
          [-70.291301, 41.73433],
          [-70.290957, 41.734312],
          [-70.290788, 41.734224],
          [-70.288323, 41.732946],
          [-70.277064, 41.727108],
          [-70.275393, 41.726242],
          [-70.275203, 41.726143],
          [-70.275164, 41.726079],
          [-70.274188, 41.724471],
          [-70.272289, 41.721346],
          [-70.263831, 41.714263],
          [-70.263654, 41.714115],
          [-70.263097, 41.714095],
          [-70.26144, 41.714035],
          [-70.259205, 41.713954],
          [-70.256395, 41.716236],
          [-70.252686, 41.719248],
          [-70.251794, 41.719973],
          [-70.250704, 41.720858],
          [-70.247935, 41.723106],
          [-70.247379, 41.723558],
          [-70.24705, 41.723825],
          [-70.237418, 41.731648],
          [-70.235048, 41.733572],
          [-70.23485, 41.733733],
          [-70.225881, 41.73815],
          [-70.225312, 41.738431],
          [-70.224462, 41.738849],
          [-70.224006, 41.739074],
          [-70.220842, 41.740632],
          [-70.216697, 41.742674],
          [-70.216073, 41.742981],
          [-70.198037, 41.749034],
          [-70.197525, 41.749206],
          [-70.193202, 41.750657],
          [-70.189254, 41.751982],
          [-70.189117, 41.751961],
          [-70.182076, 41.750885],
          [-70.176566, 41.752134],
          [-70.159127, 41.756085],
          [-70.143848, 41.759547],
          [-70.143444, 41.759639],
          [-70.141533, 41.760072],
          [-70.141519, 41.760071],
          [-70.14038, 41.759999],
          [-70.136007, 41.759724],
          [-70.129065, 41.759287],
          [-70.124364, 41.758991],
          [-70.12407, 41.758973],
          [-70.122132, 41.758851],
          [-70.121978, 41.758841],
          [-70.115895, 41.76065],
          [-70.115067, 41.760896],
          [-70.109319, 41.762606],
          [-70.097666, 41.766072],
          [-70.097658, 41.766074],
          [-70.096768, 41.766339],
          [-70.096061, 41.766549],
          [-70.086376, 41.76847],
          [-70.081833, 41.769371],
          [-70.067603, 41.772193],
          [-70.065142, 41.772681],
          [-70.064314, 41.772845],
          [-70.060674, 41.77418],
          [-70.03439, 41.783822],
          [-70.033874, 41.784011],
          [-70.033223, 41.78425],
          [-70.024734, 41.787364],
          [-70.024658, 41.787427],
          [-70.023082, 41.788726],
          [-70.022809, 41.788952],
          [-70.022381, 41.789305],
          [-70.009936, 41.79957],
          [-70.008882, 41.80044],
          [-70.008462, 41.800786],
          [-70.003842, 41.80852],
          [-70.004486, 41.838826],
          [-70.009013, 41.876625],
          [-70.000188, 41.886938],
          [-70.002922, 41.890315],
          [-70.012154, 41.891656],
          [-70.024335, 41.89882],
          [-70.025553, 41.911699],
          [-70.030537, 41.929154],
          [-70.044995, 41.930049],
          [-70.054464, 41.927366],
          [-70.065671, 41.911658],
          [-70.065723, 41.899641],
          [-70.065372, 41.887702],
          [-70.064084, 41.878924],
          [-70.066002, 41.877011],
          [-70.067566, 41.877793],
          [-70.070889, 41.882973],
          [-70.073039, 41.899783],
          [-70.074006, 41.93865],
          [-70.077421, 41.985497],
          [-70.083775, 42.012041],
          [-70.089578, 42.024896],
          [-70.095595, 42.032832],
          [-70.10806, 42.043601],
          [-70.123043, 42.051668],
          [-70.147411, 42.06159]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25003",
      state: "25",
      county: "003",
      name: "Berkshire County",
      censusArea: 926.825,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.000067, 42.312693],
          [-73.001846, 42.25109],
          [-73.007993, 42.238409],
          [-73.071199, 42.148214],
          [-73.053254, 42.039861],
          [-73.127276, 42.041964],
          [-73.229798, 42.044877],
          [-73.231056, 42.044945],
          [-73.293097, 42.04694],
          [-73.29442, 42.046984],
          [-73.487314, 42.049638],
          [-73.496879, 42.049675],
          [-73.508142, 42.086257],
          [-73.410647, 42.351738],
          [-73.352527, 42.510002],
          [-73.307004, 42.632653],
          [-73.264957, 42.74594],
          [-73.142487, 42.743508],
          [-73.022903, 42.741133],
          [-72.949742, 42.705103],
          [-72.951039, 42.641006],
          [-72.975379, 42.555933],
          [-73.000067, 42.312693]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25027",
      state: "25",
      county: "027",
      name: "Worcester County",
      censusArea: 1510.77,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.211079, 42.251262],
          [-72.211275, 42.255635],
          [-72.216138, 42.256144],
          [-72.217879, 42.270089],
          [-72.212941, 42.269925],
          [-72.212128, 42.282672],
          [-72.207245, 42.28521],
          [-72.203363, 42.291162],
          [-72.210815, 42.29446],
          [-72.213946, 42.29429],
          [-72.314599, 42.343526],
          [-72.244952, 42.513552],
          [-72.224839, 42.638981],
          [-72.282968, 42.722008],
          [-72.111068, 42.717264],
          [-72.078463, 42.716364],
          [-71.928811, 42.712234],
          [-71.898714, 42.711478],
          [-71.85841, 42.63384],
          [-71.775742, 42.644216],
          [-71.664614, 42.611589],
          [-71.67773, 42.539397],
          [-71.67898, 42.530351],
          [-71.675714, 42.52999],
          [-71.665513, 42.528391],
          [-71.664613, 42.528291],
          [-71.655413, 42.526891],
          [-71.651713, 42.526391],
          [-71.650613, 42.526191],
          [-71.635812, 42.524091],
          [-71.538949, 42.543069],
          [-71.543297, 42.466451],
          [-71.624702, 42.350465],
          [-71.625825, 42.349724],
          [-71.619131, 42.343349],
          [-71.611573, 42.339008],
          [-71.604099, 42.336768],
          [-71.603591, 42.331676],
          [-71.605158, 42.330259],
          [-71.601885, 42.326971],
          [-71.59735, 42.321149],
          [-71.592502, 42.317901],
          [-71.585168, 42.310977],
          [-71.551126, 42.326395],
          [-71.486768, 42.330075],
          [-71.486125, 42.310885],
          [-71.497046, 42.289829],
          [-71.513048, 42.267148],
          [-71.549466, 42.266124],
          [-71.586759, 42.259545],
          [-71.599318, 42.225959],
          [-71.478119, 42.156782],
          [-71.478521, 42.131421],
          [-71.50091, 42.117041],
          [-71.498284, 42.064802],
          [-71.498262, 42.031333],
          [-71.497901, 42.031513],
          [-71.498259, 42.027813],
          [-71.498258, 42.01722],
          [-71.499905, 42.017198],
          [-71.500905, 42.017098],
          [-71.527306, 42.015098],
          [-71.527606, 42.014998],
          [-71.559439, 42.014342],
          [-71.576908, 42.014098],
          [-71.591104, 42.013713],
          [-71.799242, 42.008065],
          [-71.80065, 42.023569],
          [-72.059752, 42.027339],
          [-72.10216, 42.028962],
          [-72.135687, 42.030245],
          [-72.135715, 42.030245],
          [-72.134954, 42.090665],
          [-72.135023, 42.161708],
          [-72.141843, 42.160898],
          [-72.263876, 42.183885],
          [-72.221218, 42.245252],
          [-72.218068, 42.248605],
          [-72.214821, 42.249041],
          [-72.214584, 42.247571],
          [-72.21046, 42.247205],
          [-72.211079, 42.251262]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26025",
      state: "26",
      county: "025",
      name: "Calhoun County",
      censusArea: 706.233,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.259847, 42.071851],
          [-85.293626, 42.071553],
          [-85.296996, 42.246071],
          [-85.29948, 42.416794],
          [-85.298879, 42.419849],
          [-85.071609, 42.421428],
          [-84.718493, 42.42152],
          [-84.709556, 42.070366],
          [-84.826491, 42.072468],
          [-85.259847, 42.071851]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26039",
      state: "26",
      county: "039",
      name: "Crawford County",
      censusArea: 556.28,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.371757, 44.855029],
          [-84.370643, 44.507223],
          [-84.850929, 44.511069],
          [-84.847309, 44.858037],
          [-84.371757, 44.855029]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26049",
      state: "26",
      county: "049",
      name: "Genesee County",
      censusArea: 636.977,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.927555, 42.88075],
          [-83.929079, 43.132782],
          [-83.932071, 43.220377],
          [-83.814494, 43.22121],
          [-83.696516, 43.221396],
          [-83.695621, 43.221422],
          [-83.460733, 43.223131],
          [-83.453364, 42.880432],
          [-83.689384, 42.871263],
          [-83.686493, 42.783263],
          [-83.922516, 42.780821],
          [-83.927555, 42.88075]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US44007",
      state: "44",
      county: "007",
      name: "Providence County",
      censusArea: 409.502,
      stateName: "Rhode Island"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.796822, 41.928552],
          [-71.799242, 42.008065],
          [-71.591104, 42.013713],
          [-71.576908, 42.014098],
          [-71.559439, 42.014342],
          [-71.527606, 42.014998],
          [-71.527306, 42.015098],
          [-71.500905, 42.017098],
          [-71.499905, 42.017198],
          [-71.498258, 42.01722],
          [-71.458104, 42.017762],
          [-71.381401, 42.018798],
          [-71.381466, 41.984998],
          [-71.381501, 41.966699],
          [-71.381401, 41.964799],
          [-71.3816, 41.922899],
          [-71.3817, 41.922699],
          [-71.3766, 41.893999],
          [-71.373799, 41.894399],
          [-71.370999, 41.894599],
          [-71.365399, 41.895299],
          [-71.364699, 41.895399],
          [-71.362499, 41.895599],
          [-71.354699, 41.896499],
          [-71.352699, 41.896699],
          [-71.339298, 41.893599],
          [-71.339298, 41.893399],
          [-71.337597, 41.8337],
          [-71.339597, 41.832],
          [-71.339297, 41.8065],
          [-71.327896, 41.780501],
          [-71.317795, 41.776101],
          [-71.31779, 41.776099],
          [-71.353897, 41.751301],
          [-71.356997, 41.741601],
          [-71.365207, 41.735649],
          [-71.372508, 41.744727],
          [-71.378273, 41.755297],
          [-71.455342, 41.732365],
          [-71.789678, 41.724568],
          [-71.789678, 41.724734],
          [-71.792767, 41.807001],
          [-71.792786, 41.80867],
          [-71.794161, 41.840141],
          [-71.794161, 41.841101],
          [-71.796089, 41.904468],
          [-71.796822, 41.928552]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45021",
      state: "45",
      county: "021",
      name: "Cherokee County",
      censusArea: 392.659,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.457257, 34.83928],
          [-81.481613, 34.866221],
          [-81.711694, 34.912577],
          [-81.754911, 34.932672],
          [-81.874433, 35.184113],
          [-81.857832, 35.183492],
          [-81.768073, 35.180132],
          [-81.494265, 35.169882],
          [-81.493401, 35.169951],
          [-81.461408, 35.168657],
          [-81.452398, 35.168293],
          [-81.366691, 35.164893],
          [-81.366599, 35.164889],
          [-81.409586, 35.046396],
          [-81.488434, 35.029516],
          [-81.457257, 34.83928]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45033",
      state: "45",
      county: "033",
      name: "Dillon County",
      censusArea: 404.872,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.54793, 34.230913],
          [-79.633413, 34.297994],
          [-79.450034, 34.621036],
          [-79.358317, 34.545358],
          [-79.331328, 34.521869],
          [-79.324854, 34.516282],
          [-79.323249, 34.514634],
          [-79.198982, 34.406699],
          [-79.192041, 34.40104],
          [-79.190739, 34.399751],
          [-79.151485, 34.366753],
          [-79.143242, 34.359817],
          [-79.071169, 34.29924],
          [-79.128165, 34.253652],
          [-79.380264, 34.300572],
          [-79.480712, 34.288081],
          [-79.54793, 34.230913]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45045",
      state: "45",
      county: "045",
      name: "Greenville County",
      censusArea: 785.12,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.193006, 34.686303],
          [-82.236, 34.535962],
          [-82.313996, 34.484002],
          [-82.419665, 34.557089],
          [-82.453789, 34.625114],
          [-82.486746, 34.819475],
          [-82.537866, 35.015696],
          [-82.588598, 35.073973],
          [-82.764464, 35.068177],
          [-82.686738, 35.11979],
          [-82.686496, 35.121822],
          [-82.582264, 35.142268],
          [-82.580127, 35.141963],
          [-82.574406, 35.144216],
          [-82.536527, 35.159137],
          [-82.535804, 35.158699],
          [-82.517284, 35.162643],
          [-82.51691, 35.163029],
          [-82.476136, 35.175486],
          [-82.384029, 35.210542],
          [-82.35243, 35.206595],
          [-82.288453, 35.198605],
          [-82.257515, 35.198636],
          [-82.230915, 35.196784],
          [-82.230517, 35.196764],
          [-82.216217, 35.196044],
          [-82.225698, 34.850171],
          [-82.146792, 34.785554],
          [-82.193006, 34.686303]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47009",
      state: "47",
      county: "009",
      name: "Blount County",
      censusArea: 558.706,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.794275, 35.887125],
          [-83.784186, 35.879205],
          [-83.784436, 35.868744],
          [-83.77988, 35.860184],
          [-83.662814, 35.690618],
          [-83.662957, 35.569138],
          [-83.676268, 35.570289],
          [-83.756917, 35.563604],
          [-83.880074, 35.518745],
          [-83.933876, 35.472438],
          [-83.952676, 35.460763],
          [-83.952882, 35.460635],
          [-83.961053, 35.464143],
          [-83.961056, 35.463738],
          [-83.993815, 35.550001],
          [-84.081561, 35.541231],
          [-84.188319, 35.610549],
          [-84.133072, 35.660733],
          [-84.166267, 35.805106],
          [-83.994677, 35.883749],
          [-83.853457, 35.841138],
          [-83.794275, 35.887125]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47019",
      state: "47",
      county: "019",
      name: "Carter County",
      censusArea: 341.203,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.983843, 36.508314],
          [-81.94984, 36.472385],
          [-82.058946, 36.367515],
          [-81.932994, 36.264881],
          [-81.960101, 36.228131],
          [-82.02664, 36.130222],
          [-82.080143, 36.10572],
          [-82.080518, 36.10571],
          [-82.127146, 36.104417],
          [-82.130646, 36.106417],
          [-82.137974, 36.119576],
          [-82.136547, 36.128817],
          [-82.147948, 36.149516],
          [-82.211251, 36.159012],
          [-82.222052, 36.156911],
          [-82.256356, 36.233208],
          [-82.34046, 36.252807],
          [-82.299859, 36.396505],
          [-82.188053, 36.400608],
          [-81.983843, 36.508314]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47037",
      state: "47",
      county: "037",
      name: "Davidson County",
      censusArea: 504.033,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.864957, 36.044672],
          [-86.9292, 36.052722],
          [-87.027705, 35.989423],
          [-87.054114, 36.045535],
          [-86.997705, 36.128512],
          [-86.913233, 36.382621],
          [-86.824784, 36.349872],
          [-86.754795, 36.405496],
          [-86.723495, 36.34768],
          [-86.594286, 36.245105],
          [-86.515589, 36.10061],
          [-86.618892, 35.968224],
          [-86.627397, 35.974027],
          [-86.648288, 35.976422],
          [-86.681889, 35.989399],
          [-86.686164, 35.994092],
          [-86.687714, 35.995343],
          [-86.691904, 35.994581],
          [-86.787205, 36.035999],
          [-86.864957, 36.044672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47051",
      state: "47",
      county: "051",
      name: "Franklin County",
      censusArea: 554.542,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.318761, 34.991147],
          [-86.320277, 35.032503],
          [-86.318168, 35.126986],
          [-86.261489, 35.333579],
          [-86.158018, 35.362607],
          [-85.913593, 35.289189],
          [-85.90812, 35.276522],
          [-85.893394, 35.257333],
          [-85.88675, 35.247379],
          [-85.874977, 35.227287],
          [-85.873025, 35.223362],
          [-85.863935, 34.988379],
          [-86.311274, 34.991098],
          [-86.318761, 34.991147]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47063",
      state: "47",
      county: "063",
      name: "Hamblen County",
      censusArea: 161.179,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.457728, 36.155375],
          [-83.458695, 36.155289],
          [-83.467097, 36.174688],
          [-83.321695, 36.29459],
          [-83.255292, 36.289092],
          [-83.16259, 36.345292],
          [-83.082312, 36.242296],
          [-83.166085, 36.180894],
          [-83.234585, 36.085094],
          [-83.293689, 36.138193],
          [-83.457728, 36.155375]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47075",
      state: "47",
      county: "075",
      name: "Haywood County",
      censusArea: 533.112,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.502118, 35.580621],
          [-89.401805, 35.822657],
          [-89.356033, 35.817635],
          [-89.271224, 35.754922],
          [-89.069544, 35.692679],
          [-89.078876, 35.431428],
          [-89.182551, 35.432816],
          [-89.183944, 35.397126],
          [-89.474171, 35.403039],
          [-89.469862, 35.546566],
          [-89.502118, 35.580621]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47085",
      state: "47",
      county: "085",
      name: "Humphreys County",
      censusArea: 530.98,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.53453, 35.993074],
          [-87.70696, 35.905884],
          [-87.71676, 35.83875],
          [-87.848144, 35.804079],
          [-87.96274, 35.840921],
          [-87.925835, 35.930432],
          [-88.012932, 35.946648],
          [-88.000809, 36.028089],
          [-87.919043, 36.130049],
          [-87.949603, 36.24316],
          [-87.798508, 36.244754],
          [-87.566798, 36.177857],
          [-87.53453, 35.993074]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47099",
      state: "47",
      county: "099",
      name: "Lawrence County",
      censusArea: 617.128,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.575352, 35.398454],
          [-87.44882, 35.420622],
          [-87.448485, 35.457844],
          [-87.294534, 35.444676],
          [-87.295242, 35.442128],
          [-87.227932, 35.429332],
          [-87.226311, 35.440509],
          [-87.206578, 35.433207],
          [-87.224053, 34.999327],
          [-87.230544, 34.999484],
          [-87.270014, 35.00039],
          [-87.299185, 35.000915],
          [-87.349251, 35.001662],
          [-87.359281, 35.001823],
          [-87.381071, 35.002118],
          [-87.391314, 35.002374],
          [-87.4174, 35.002669],
          [-87.421543, 35.002679],
          [-87.428613, 35.002795],
          [-87.606031, 35.003343],
          [-87.588833, 35.058431],
          [-87.575352, 35.398454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47113",
      state: "47",
      county: "113",
      name: "Madison County",
      censusArea: 557.117,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.606862, 35.789354],
          [-88.613611, 35.588089],
          [-88.675867, 35.489163],
          [-88.841607, 35.427826],
          [-89.078876, 35.431428],
          [-89.069544, 35.692679],
          [-89.02096, 35.671046],
          [-88.892938, 35.783842],
          [-88.916181, 35.797307],
          [-88.910964, 35.797336],
          [-88.706811, 35.791075],
          [-88.606862, 35.789354]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47127",
      state: "47",
      county: "127",
      name: "Moore County",
      censusArea: 129.223,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.525306, 35.354718],
          [-86.444169, 35.3207],
          [-86.25759, 35.412476],
          [-86.261489, 35.333579],
          [-86.318168, 35.126986],
          [-86.366236, 35.124405],
          [-86.392192, 35.235381],
          [-86.52728, 35.311536],
          [-86.525306, 35.354718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06019",
      state: "06",
      county: "019",
      name: "Fresno County",
      censusArea: 5957.991,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.573194, 36.488835],
          [-119.66629, 36.41896],
          [-119.905119, 36.401741],
          [-119.959227, 36.400975],
          [-119.95921, 36.181403],
          [-120.315068, 35.907186],
          [-120.648784, 36.10799],
          [-120.62691, 36.203227],
          [-120.678582, 36.267319],
          [-120.596562, 36.328488],
          [-120.603706, 36.488242],
          [-120.918731, 36.740381],
          [-120.65595, 36.952832],
          [-120.590566, 36.952641],
          [-120.541696, 37.044505],
          [-120.422187, 36.840139],
          [-120.229179, 36.769687],
          [-120.132898, 36.809841],
          [-120.079458, 36.825345],
          [-120.027775, 36.814505],
          [-120.013334, 36.828228],
          [-119.932551, 36.84299],
          [-119.823421, 36.848333],
          [-119.733852, 36.948558],
          [-119.537677, 37.104019],
          [-119.559412, 37.143619],
          [-119.470844, 37.110548],
          [-119.43306, 37.161516],
          [-119.361916, 37.167849],
          [-119.330573, 37.205791],
          [-119.335189, 37.310774],
          [-119.286658, 37.374943],
          [-119.022363, 37.585737],
          [-118.775014, 37.463052],
          [-118.786736, 37.34338],
          [-118.716016, 37.328208],
          [-118.665167, 37.261009],
          [-118.654593, 37.141826],
          [-118.437137, 37.059818],
          [-118.360586, 36.744773],
          [-118.982441, 36.741646],
          [-118.984779, 36.657147],
          [-119.304625, 36.660606],
          [-119.305102, 36.573725],
          [-119.466322, 36.575238],
          [-119.573194, 36.488835]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06023",
      state: "06",
      county: "023",
      name: "Humboldt County",
      censusArea: 3567.987,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.544458, 40.001923],
          [-124.023938, 40.001284],
          [-124.035904, 40.013319],
          [-124.056408, 40.024305],
          [-124.065069, 40.024785],
          [-124.068908, 40.021307],
          [-124.072509, 40.022657],
          [-124.079983, 40.029773],
          [-124.080709, 40.06611],
          [-124.087086, 40.078442],
          [-124.110549, 40.103765],
          [-124.139952, 40.11635],
          [-124.170767, 40.124207],
          [-124.187874, 40.130542],
          [-124.214895, 40.160902],
          [-124.231095, 40.171581],
          [-124.258405, 40.184277],
          [-124.296497, 40.208816],
          [-124.320912, 40.226617],
          [-124.327691, 40.23737],
          [-124.34307, 40.243979],
          [-124.352715, 40.250453],
          [-124.363414, 40.260974],
          [-124.363634, 40.276212],
          [-124.347853, 40.314634],
          [-124.353124, 40.331425],
          [-124.356595, 40.335016],
          [-124.362796, 40.350046],
          [-124.365357, 40.374855],
          [-124.373599, 40.392923],
          [-124.379082, 40.398828],
          [-124.391496, 40.407047],
          [-124.402623, 40.422105],
          [-124.409591, 40.438076],
          [-124.408601, 40.443201],
          [-124.396642, 40.462119],
          [-124.38494, 40.48982],
          [-124.383224, 40.499852],
          [-124.387023, 40.504954],
          [-124.382816, 40.519],
          [-124.379096, 40.522865],
          [-124.363545, 40.548698],
          [-124.329404, 40.61643],
          [-124.315141, 40.639526],
          [-124.312558, 40.641333],
          [-124.289119, 40.67963],
          [-124.248406, 40.735166],
          [-124.228244, 40.76939],
          [-124.201921, 40.805111],
          [-124.176715, 40.843618],
          [-124.158322, 40.876069],
          [-124.137066, 40.925732],
          [-124.118147, 40.989263],
          [-124.112165, 41.028173],
          [-124.125448, 41.048504],
          [-124.132946, 41.052482],
          [-124.138217, 41.054342],
          [-124.142867, 41.054032],
          [-124.147216, 41.052884],
          [-124.148939, 41.051467],
          [-124.151266, 41.051101],
          [-124.153622, 41.05355],
          [-124.154028, 41.059923],
          [-124.154513, 41.087159],
          [-124.160556, 41.099011],
          [-124.159065, 41.121957],
          [-124.165414, 41.129822],
          [-124.163988, 41.138675],
          [-124.158539, 41.143021],
          [-124.149674, 41.140845],
          [-124.1438, 41.144686],
          [-124.122677, 41.189726],
          [-124.106986, 41.229678],
          [-124.106389, 41.240682],
          [-124.092284, 41.287695],
          [-124.079015, 41.347135],
          [-124.072294, 41.374844],
          [-124.063076, 41.439579],
          [-124.065521, 41.464739],
          [-123.770551, 41.464193],
          [-123.770239, 41.380776],
          [-123.661363, 41.38209],
          [-123.474085, 41.366193],
          [-123.408291, 41.179944],
          [-123.481457, 40.914957],
          [-123.560163, 40.950257],
          [-123.623826, 40.929421],
          [-123.55985, 40.829584],
          [-123.544059, 40.739386],
          [-123.544458, 40.001923]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06035",
      state: "06",
      county: "035",
      name: "Lassen County",
      censusArea: 4541.184,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.001319, 39.72242],
          [-120.001319, 39.722416],
          [-120.147149, 39.707658],
          [-120.110627, 39.765779],
          [-120.108761, 39.93951],
          [-120.201264, 40.013474],
          [-120.209641, 40.086008],
          [-120.341385, 40.115243],
          [-120.445892, 40.176854],
          [-120.510817, 40.248945],
          [-120.652158, 40.307656],
          [-120.764403, 40.31601],
          [-120.928582, 40.191931],
          [-121.061493, 40.256417],
          [-121.061417, 40.446536],
          [-121.327826, 40.445367],
          [-121.319972, 40.905886],
          [-121.331786, 41.183886],
          [-119.999866, 41.183974],
          [-119.999358, 40.873101],
          [-119.999232, 40.867454],
          [-119.999231, 40.865899],
          [-119.995926, 40.499901],
          [-119.996155, 40.321838],
          [-119.996155, 40.32125],
          [-119.996182, 40.263532],
          [-119.996183, 40.262461],
          [-119.997124, 40.126363],
          [-119.997234, 40.091591],
          [-119.997175, 40.077245],
          [-119.997291, 40.071803],
          [-120.000607, 39.780779],
          [-120.000502, 39.779956],
          [-120.001319, 39.72242]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06037",
      state: "06",
      county: "037",
      name: "Los Angeles County",
      censusArea: 4057.884,
      stateName: "California"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-118.524531, 32.895488],
            [-118.535823, 32.90628],
            [-118.551134, 32.945155],
            [-118.560887, 32.957891],
            [-118.573522, 32.969183],
            [-118.586928, 33.008281],
            [-118.596037, 33.015357],
            [-118.606559, 33.01469],
            [-118.605534, 33.030999],
            [-118.594033, 33.035951],
            [-118.57516, 33.033961],
            [-118.569013, 33.029151],
            [-118.564445, 33.024914],
            [-118.564527, 33.018637],
            [-118.559171, 33.006291],
            [-118.540069, 32.980933],
            [-118.529228, 32.970921],
            [-118.496811, 32.933847],
            [-118.485288, 32.923545],
            [-118.479039, 32.920363],
            [-118.460623, 32.90951],
            [-118.446771, 32.895424],
            [-118.369984, 32.839273],
            [-118.353504, 32.821962],
            [-118.356541, 32.817311],
            [-118.36053, 32.819921],
            [-118.379968, 32.824545],
            [-118.387375, 32.825327],
            [-118.394565, 32.823978],
            [-118.401268, 32.820338],
            [-118.425634, 32.800595],
            [-118.42943, 32.805429],
            [-118.428372, 32.806872],
            [-118.44492, 32.820593],
            [-118.476074, 32.841754],
            [-118.487908, 32.84459],
            [-118.496298, 32.851572],
            [-118.506902, 32.868503],
            [-118.508095, 32.871321],
            [-118.507193, 32.876264],
            [-118.524641, 32.893175],
            [-118.524531, 32.895488]
          ]
        ],
        [
          [
            [-118.500212, 33.449592],
            [-118.499669, 33.447879],
            [-118.48557, 33.446213],
            [-118.477646, 33.448392],
            [-118.445812, 33.428907],
            [-118.423576, 33.427258],
            [-118.382037, 33.409883],
            [-118.370323, 33.409285],
            [-118.368301, 33.40711],
            [-118.365094, 33.388374],
            [-118.32446, 33.348782],
            [-118.316083, 33.342928],
            [-118.310213, 33.335795],
            [-118.303174, 33.320264],
            [-118.305084, 33.310323],
            [-118.316787, 33.301137],
            [-118.325244, 33.299075],
            [-118.343249, 33.305234],
            [-118.360332, 33.31533],
            [-118.374768, 33.320065],
            [-118.402941, 33.320901],
            [-118.440047, 33.318638],
            [-118.456309, 33.32182],
            [-118.465368, 33.326056],
            [-118.481886, 33.344123],
            [-118.48877, 33.356649],
            [-118.482609, 33.369914],
            [-118.478465, 33.38632],
            [-118.484949, 33.412131],
            [-118.48875, 33.419826],
            [-118.503952, 33.424234],
            [-118.515914, 33.422417],
            [-118.516267, 33.425075],
            [-118.52323, 33.430733],
            [-118.53738, 33.434608],
            [-118.558715, 33.433419],
            [-118.563442, 33.434381],
            [-118.570927, 33.439351],
            [-118.575901, 33.448261],
            [-118.593969, 33.467198],
            [-118.601185, 33.469853],
            [-118.60403, 33.47654],
            [-118.603375, 33.478098],
            [-118.598783, 33.477939],
            [-118.585936, 33.473819],
            [-118.54453, 33.474119],
            [-118.530702, 33.468071],
            [-118.500212, 33.449592]
          ]
        ],
        [
          [
            [-118.796692, 34.616559],
            [-118.881364, 34.790629],
            [-118.854253, 34.817772],
            [-118.140074, 34.82078],
            [-117.667292, 34.822526],
            [-117.658798, 34.44058],
            [-117.650459, 34.339025],
            [-117.711067, 34.079536],
            [-117.719481, 34.059731],
            [-117.72601, 34.041128],
            [-117.735846, 34.018554],
            [-117.767752, 34.019429],
            [-117.783287, 33.946411],
            [-117.97653, 33.928034],
            [-117.976498, 33.919171],
            [-117.976571, 33.911205],
            [-117.976692, 33.910614],
            [-117.976588, 33.909757],
            [-117.976586, 33.906225],
            [-118.063268, 33.82422],
            [-118.063162, 33.81961],
            [-118.072597, 33.815115],
            [-118.084597, 33.803216],
            [-118.093008, 33.786289],
            [-118.099107, 33.776518],
            [-118.09197, 33.758472],
            [-118.116703, 33.743549],
            [-118.132698, 33.753217],
            [-118.1569, 33.760317],
            [-118.1755, 33.763617],
            [-118.180831, 33.763072],
            [-118.187701, 33.749218],
            [-118.1837, 33.736118],
            [-118.181367, 33.717367],
            [-118.207476, 33.716905],
            [-118.258687, 33.703741],
            [-118.277208, 33.707091],
            [-118.297104, 33.708319],
            [-118.317205, 33.712818],
            [-118.354705, 33.732317],
            [-118.360505, 33.736817],
            [-118.385006, 33.741417],
            [-118.396606, 33.735917],
            [-118.411211, 33.741985],
            [-118.428407, 33.774715],
            [-118.423407, 33.782015],
            [-118.405007, 33.800215],
            [-118.394376, 33.804289],
            [-118.394307, 33.804315],
            [-118.391507, 33.815415],
            [-118.392107, 33.840915],
            [-118.412708, 33.883913],
            [-118.44241, 33.940312],
            [-118.460611, 33.969111],
            [-118.482729, 33.995912],
            [-118.484212, 33.99771],
            [-118.502813, 34.015509],
            [-118.519514, 34.027509],
            [-118.543115, 34.038508],
            [-118.569235, 34.04164],
            [-118.603572, 34.039048],
            [-118.609652, 34.036424],
            [-118.668358, 34.038887],
            [-118.67543, 34.037479],
            [-118.679366, 34.033255],
            [-118.706215, 34.029383],
            [-118.732391, 34.032743],
            [-118.744952, 34.032103],
            [-118.783433, 34.021543],
            [-118.787094, 34.019545],
            [-118.805114, 34.001239],
            [-118.821579, 34.013959],
            [-118.84038, 34.027527],
            [-118.854653, 34.034215],
            [-118.896159, 34.039207],
            [-118.928048, 34.045847],
            [-118.938081, 34.043383],
            [-118.944887, 34.04534],
            [-118.940965, 34.07483],
            [-118.788889, 34.168214],
            [-118.668152, 34.168195],
            [-118.633473, 34.269626],
            [-118.636789, 34.291804],
            [-118.650859, 34.320484],
            [-118.796692, 34.616559]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13085",
      state: "13",
      county: "085",
      name: "Dawson County",
      censusArea: 210.83,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.258075, 34.335156],
          [-84.257812, 34.372631],
          [-84.257652, 34.375111],
          [-84.257586, 34.380992],
          [-84.256867, 34.467327],
          [-84.319679, 34.467857],
          [-84.345553, 34.562735],
          [-84.255112, 34.568333],
          [-84.196754, 34.617924],
          [-84.189026, 34.608109],
          [-84.188557, 34.602692],
          [-84.19104, 34.539181],
          [-84.102037, 34.464544],
          [-83.980649, 34.418389],
          [-83.957077, 34.334011],
          [-84.258075, 34.335156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13099",
      state: "13",
      county: "099",
      name: "Early County",
      censusArea: 512.594,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.046859, 31.517451],
          [-85.029227, 31.486071],
          [-84.819807, 31.501192],
          [-84.789032, 31.436356],
          [-84.637579, 31.433926],
          [-84.641673, 31.258967],
          [-84.91743, 31.25599],
          [-84.922423, 31.07259],
          [-84.999869, 31.074736],
          [-85.001928, 31.074983],
          [-85.022121, 31.07548],
          [-85.029736, 31.096163],
          [-85.035615, 31.108192],
          [-85.076628, 31.156927],
          [-85.083582, 31.15963],
          [-85.092106, 31.160293],
          [-85.100207, 31.16549],
          [-85.107516, 31.186451],
          [-85.111871, 31.258388],
          [-85.101561, 31.283362],
          [-85.087651, 31.308677],
          [-85.068065, 31.427586],
          [-85.046859, 31.517451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13117",
      state: "13",
      county: "117",
      name: "Forsyth County",
      censusArea: 224.021,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.258075, 34.335156],
          [-83.957077, 34.334011],
          [-83.927284, 34.279399],
          [-83.989059, 34.195732],
          [-84.062841, 34.167873],
          [-84.074624, 34.163687],
          [-84.094763, 34.131708],
          [-84.101343, 34.106305],
          [-84.10261, 34.103788],
          [-84.105403, 34.102223],
          [-84.107143, 34.10003],
          [-84.109894, 34.098423],
          [-84.117801, 34.065315],
          [-84.097692, 34.050654],
          [-84.200373, 34.090118],
          [-84.258934, 34.109539],
          [-84.258743, 34.185909],
          [-84.258075, 34.335156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08051",
      state: "08",
      county: "051",
      name: "Gunnison County",
      censusArea: 3239.1,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.500606, 39.217916],
          [-107.394486, 39.256299],
          [-107.375608, 39.197931],
          [-107.28249, 39.118718],
          [-107.06632, 39.118805],
          [-107.001622, 39.042273],
          [-106.906422, 38.992091],
          [-106.801874, 38.978303],
          [-106.694969, 39.055667],
          [-106.599214, 38.997994],
          [-106.465032, 38.910354],
          [-106.326099, 38.911004],
          [-106.452126, 38.698777],
          [-106.24694, 38.422768],
          [-107.000601, 38.425619],
          [-107.001119, 38.147072],
          [-107.56862, 38.147267],
          [-107.63504, 38.301896],
          [-107.50002, 38.301926],
          [-107.500655, 38.668553],
          [-107.500606, 39.217916]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08055",
      state: "08",
      county: "055",
      name: "Huerfano County",
      censusArea: 1591.001,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.473204, 37.89597],
          [-105.317081, 37.938949],
          [-105.285076, 37.899421],
          [-105.194586, 38.00756],
          [-105.049917, 37.915479],
          [-105.013729, 37.881271],
          [-104.646383, 37.900527],
          [-104.351109, 37.817488],
          [-104.45493, 37.740839],
          [-104.548417, 37.609424],
          [-104.548652, 37.5747],
          [-104.649229, 37.49532],
          [-104.696111, 37.485425],
          [-104.695075, 37.439915],
          [-104.748938, 37.407029],
          [-104.995505, 37.375551],
          [-105.154176, 37.293129],
          [-105.187549, 37.619669],
          [-105.295587, 37.654416],
          [-105.485516, 37.577899],
          [-105.457255, 37.751464],
          [-105.428893, 37.851733],
          [-105.473204, 37.89597]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08065",
      state: "08",
      county: "065",
      name: "Lake County",
      censusArea: 376.911,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.426487, 39.361871],
          [-106.206732, 39.379636],
          [-106.136412, 39.379694],
          [-106.135529, 39.379546],
          [-106.183972, 39.309354],
          [-106.191006, 39.056073],
          [-106.577966, 39.057908],
          [-106.508614, 39.166681],
          [-106.485121, 39.312566],
          [-106.426487, 39.361871]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08079",
      state: "08",
      county: "079",
      name: "Mineral County",
      censusArea: 875.666,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.693845, 37.835244],
          [-106.711624, 37.664273],
          [-106.710775, 37.404228],
          [-107.128737, 37.392409],
          [-107.12868, 37.422942],
          [-107.123868, 37.675029],
          [-107.145138, 37.675497],
          [-107.138191, 37.938848],
          [-107.000602, 37.956041],
          [-106.879167, 37.96111],
          [-106.693523, 37.873519],
          [-106.693845, 37.835244]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08089",
      state: "08",
      county: "089",
      name: "Otero County",
      censusArea: 1261.961,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.619172, 38.113355],
          [-103.619283, 38.171455],
          [-103.509364, 38.172514],
          [-103.501764, 38.265016],
          [-103.399938, 38.265435],
          [-103.403592, 37.792397],
          [-103.404339, 37.643576],
          [-104.058196, 37.644049],
          [-104.061132, 37.734704],
          [-104.058242, 38.146492],
          [-103.835616, 38.11334],
          [-103.619172, 38.113355]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08101",
      state: "08",
      county: "101",
      name: "Pueblo County",
      censusArea: 2386.104,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.053921, 38.522393],
          [-104.058242, 38.146492],
          [-104.061132, 37.734704],
          [-104.351109, 37.817488],
          [-104.646383, 37.900527],
          [-105.013729, 37.881271],
          [-105.049917, 37.915479],
          [-105.049215, 38.257973],
          [-104.940366, 38.258275],
          [-104.941533, 38.519569],
          [-104.053921, 38.522393]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08117",
      state: "08",
      county: "117",
      name: "Summit County",
      censusArea: 608.358,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.434508, 39.924914],
          [-106.252027, 39.914712],
          [-106.083711, 39.805964],
          [-106.023783, 39.68842],
          [-105.924618, 39.698972],
          [-105.793058, 39.636328],
          [-105.829662, 39.564865],
          [-105.817631, 39.53831],
          [-105.966786, 39.438035],
          [-106.017619, 39.365503],
          [-106.135529, 39.379546],
          [-106.136412, 39.379694],
          [-106.206732, 39.379636],
          [-106.251638, 39.465039],
          [-106.177604, 39.609056],
          [-106.385478, 39.767164],
          [-106.434508, 39.924914]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09005",
      state: "09",
      county: "005",
      name: "Litchfield County",
      censusArea: 920.56,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.145036, 41.557822],
          [-73.164676, 41.557088],
          [-73.15573, 41.515279],
          [-73.318889, 41.505189],
          [-73.327114, 41.484527],
          [-73.310473, 41.468815],
          [-73.312873, 41.467415],
          [-73.326774, 41.469515],
          [-73.331569, 41.472677],
          [-73.441466, 41.491294],
          [-73.506923, 41.665802],
          [-73.510379, 41.666498],
          [-73.511149, 41.666488],
          [-73.514784, 41.665886],
          [-73.516019, 41.665964],
          [-73.517473, 41.666646],
          [-73.510171, 41.758686],
          [-73.505008, 41.823773],
          [-73.504944, 41.824285],
          [-73.487314, 42.049638],
          [-73.29442, 42.046984],
          [-73.293097, 42.04694],
          [-73.231056, 42.044945],
          [-73.229798, 42.044877],
          [-73.127276, 42.041964],
          [-73.053254, 42.039861],
          [-73.008739, 42.039356],
          [-73.008745, 42.038854],
          [-73.029537, 41.966606],
          [-72.88706, 41.973121],
          [-72.905945, 41.921696],
          [-72.949024, 41.806431],
          [-73.016936, 41.798183],
          [-73.016534, 41.795655],
          [-73.012633, 41.777457],
          [-73.012534, 41.77541],
          [-73.012256, 41.775132],
          [-72.99855, 41.712498],
          [-72.98325, 41.639598],
          [-73.004046, 41.628577],
          [-73.021471, 41.627104],
          [-73.01865, 41.614097],
          [-73.036156, 41.615197],
          [-73.058024, 41.60674],
          [-73.145036, 41.557822]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12007",
      state: "12",
      county: "007",
      name: "Bradford County",
      censusArea: 293.962,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.049244, 29.71867],
          [-82.055625, 29.718232],
          [-82.133126, 29.835949],
          [-82.272563, 29.843011],
          [-82.418728, 29.923093],
          [-82.307409, 29.956156],
          [-82.142578, 30.143117],
          [-82.049425, 30.143136],
          [-82.046112, 29.747129],
          [-82.049244, 29.71867]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12019",
      state: "12",
      county: "019",
      name: "Clay County",
      censusArea: 604.36,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.049411, 30.186933],
          [-81.711348, 30.191193],
          [-81.680215, 30.12124],
          [-81.688876, 30.028566],
          [-81.60099, 29.956017],
          [-81.581207, 29.840176],
          [-81.797218, 29.836649],
          [-81.939427, 29.747497],
          [-82.049244, 29.71867],
          [-82.046112, 29.747129],
          [-82.049425, 30.143136],
          [-82.049411, 30.186933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12027",
      state: "12",
      county: "027",
      name: "De Soto County",
      censusArea: 637.059,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.057469, 27.032119],
          [-82.056497, 27.207769],
          [-82.055753, 27.338264],
          [-81.563696, 27.34068],
          [-81.562202, 27.033836],
          [-82.057469, 27.032119]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12035",
      state: "12",
      county: "035",
      name: "Flagler County",
      censusArea: 485.461,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.212878, 29.670667],
          [-81.211565, 29.667085],
          [-81.163581, 29.55529],
          [-81.123896, 29.474465],
          [-81.101923, 29.427055],
          [-81.155881, 29.410954],
          [-81.150081, 29.265957],
          [-81.41729, 29.261156],
          [-81.433992, 29.398552],
          [-81.478793, 29.399052],
          [-81.520596, 29.500249],
          [-81.52366, 29.622432],
          [-81.324059, 29.62561],
          [-81.212878, 29.670667]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26059",
      state: "26",
      county: "059",
      name: "Hillsdale County",
      censusArea: 598.133,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.396547, 41.705935],
          [-84.39943, 41.705761],
          [-84.399546, 41.705758],
          [-84.438067, 41.704903],
          [-84.806082, 41.696089],
          [-84.806018, 41.707485],
          [-84.806042, 41.720544],
          [-84.806065, 41.732909],
          [-84.806074, 41.737603],
          [-84.806134, 41.743115],
          [-84.818873, 41.760059],
          [-84.82513, 41.759991],
          [-84.826491, 42.072468],
          [-84.709556, 42.070366],
          [-84.363297, 42.073456],
          [-84.360419, 41.706925],
          [-84.396547, 41.705935]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26063",
      state: "26",
      county: "063",
      name: "Huron County",
      censusArea: 835.706,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.606233, 43.690437],
          [-83.117844, 43.675493],
          [-83.466592, 43.667621],
          [-83.467429, 43.72892],
          [-83.46508, 43.733843],
          [-83.459628, 43.740931],
          [-83.440171, 43.761694],
          [-83.438878, 43.767135],
          [-83.441591, 43.770175],
          [-83.446752, 43.77186],
          [-83.438311, 43.786846],
          [-83.426068, 43.799915],
          [-83.416378, 43.801034],
          [-83.411453, 43.805033],
          [-83.410663, 43.80773],
          [-83.412456, 43.817569],
          [-83.410853, 43.825585],
          [-83.407647, 43.831998],
          [-83.390344, 43.839132],
          [-83.389017, 43.840457],
          [-83.389424, 43.844229],
          [-83.358869, 43.857395],
          [-83.33227, 43.880522],
          [-83.331788, 43.893901],
          [-83.333532, 43.89852],
          [-83.340976, 43.904541],
          [-83.348007, 43.906388],
          [-83.348648, 43.909915],
          [-83.347365, 43.91216],
          [-83.338067, 43.915687],
          [-83.318656, 43.91762],
          [-83.30569, 43.922489],
          [-83.28231, 43.938031],
          [-83.26898, 43.956132],
          [-83.26185, 43.969021],
          [-83.26153, 43.973525],
          [-83.227093, 43.981003],
          [-83.195688, 43.983137],
          [-83.180618, 43.982109],
          [-83.145407, 43.989441],
          [-83.134881, 43.993147],
          [-83.120659, 44.00095],
          [-83.10782, 44.003245],
          [-83.079297, 44.001079],
          [-83.066026, 44.003366],
          [-83.058741, 44.006224],
          [-83.046577, 44.01571],
          [-83.029868, 44.041175],
          [-83.024604, 44.045174],
          [-82.999283, 44.04651],
          [-82.990728, 44.048846],
          [-82.967439, 44.066138],
          [-82.958688, 44.065774],
          [-82.956658, 44.063306],
          [-82.947368, 44.062187],
          [-82.928884, 44.069389],
          [-82.915976, 44.070503],
          [-82.889831, 44.050952],
          [-82.875889, 44.045046],
          [-82.833103, 44.036851],
          [-82.793205, 44.023247],
          [-82.788298, 44.013712],
          [-82.783198, 44.009366],
          [-82.765018, 44.006845],
          [-82.746255, 43.996037],
          [-82.738992, 43.989506],
          [-82.728528, 43.972615],
          [-82.712235, 43.94961],
          [-82.709839, 43.948226],
          [-82.693505, 43.91798],
          [-82.678642, 43.88373],
          [-82.65545, 43.867883],
          [-82.643166, 43.852468],
          [-82.642899, 43.846419],
          [-82.647467, 43.84449],
          [-82.647784, 43.842684],
          [-82.644345, 43.837539],
          [-82.633641, 43.831224],
          [-82.617955, 43.768596],
          [-82.619079, 43.756088],
          [-82.617213, 43.746788],
          [-82.612224, 43.739771],
          [-82.606233, 43.690437]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26113",
      state: "26",
      county: "113",
      name: "Missaukee County",
      censusArea: 564.726,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.850929, 44.511069],
          [-84.855795, 44.421984],
          [-84.851705, 44.161375],
          [-85.08704, 44.164239],
          [-85.334777, 44.165118],
          [-85.334, 44.512303],
          [-84.850929, 44.511069]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26119",
      state: "26",
      county: "119",
      name: "Montmorency County",
      censusArea: 546.663,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.371757, 44.855029],
          [-84.366827, 45.198762],
          [-84.246634, 45.198718],
          [-83.881218, 45.202062],
          [-83.888484, 44.856458],
          [-84.371757, 44.855029]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26129",
      state: "26",
      county: "129",
      name: "Ogemaw County",
      censusArea: 563.488,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.370643, 44.507223],
          [-83.886634, 44.508973],
          [-83.883977, 44.161786],
          [-84.166107, 44.161788],
          [-84.368039, 44.160538],
          [-84.366324, 44.175508],
          [-84.370643, 44.507223]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26137",
      state: "26",
      county: "137",
      name: "Otsego County",
      censusArea: 514.971,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.371757, 44.855029],
          [-84.847309, 44.858037],
          [-84.857452, 45.116707],
          [-84.734799, 45.114903],
          [-84.734303, 45.201936],
          [-84.366827, 45.198762],
          [-84.371757, 44.855029]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26149",
      state: "26",
      county: "149",
      name: "St. Joseph County",
      censusArea: 500.591,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.724534, 41.759085],
          [-85.749992, 41.759091],
          [-85.750469, 41.75909],
          [-85.775039, 41.759147],
          [-85.791335, 41.759051],
          [-85.760186, 41.798814],
          [-85.762943, 42.069327],
          [-85.293626, 42.071553],
          [-85.292178, 41.759963],
          [-85.298365, 41.760028],
          [-85.30814, 41.760097],
          [-85.318129, 41.759983],
          [-85.330623, 41.759982],
          [-85.350174, 41.759908],
          [-85.379133, 41.759875],
          [-85.427553, 41.759706],
          [-85.432471, 41.759684],
          [-85.515959, 41.759352],
          [-85.518251, 41.759513],
          [-85.607548, 41.759079],
          [-85.608312, 41.759193],
          [-85.622608, 41.759049],
          [-85.624987, 41.759093],
          [-85.632714, 41.759164],
          [-85.647683, 41.759125],
          [-85.650738, 41.759103],
          [-85.65975, 41.759101],
          [-85.724534, 41.759085]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27005",
      state: "27",
      county: "005",
      name: "Becker County",
      censusArea: 1315.203,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.16301, 46.80472],
          [-95.16371, 46.71764],
          [-95.247999, 46.716255],
          [-96.174587, 46.717854],
          [-96.174108, 46.977375],
          [-96.194671, 47.151149],
          [-96.067069, 47.151813],
          [-95.551186, 47.151453],
          [-95.169172, 47.152515],
          [-95.16301, 46.80472]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27013",
      state: "27",
      county: "013",
      name: "Blue Earth County",
      censusArea: 747.844,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.767761, 43.847956],
          [-94.247123, 43.847946],
          [-94.368974, 43.848047],
          [-94.36885, 44.108698],
          [-94.371731, 44.264448],
          [-94.055349, 44.154018],
          [-94.011834, 44.239192],
          [-93.768031, 44.239384],
          [-93.767971, 44.195836],
          [-93.767761, 43.847956]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27023",
      state: "27",
      county: "023",
      name: "Chippewa County",
      censusArea: 581.122,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.737015, 44.93618],
          [-96.036698, 45.152206],
          [-95.24649, 45.152384],
          [-95.24852, 44.89131],
          [-95.482889, 44.890712],
          [-95.482843, 44.752505],
          [-95.498456, 44.786867],
          [-95.737015, 44.93618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27033",
      state: "27",
      county: "033",
      name: "Cottonwood County",
      censusArea: 638.61,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.859388, 43.848093],
          [-95.452502, 43.847953],
          [-95.462427, 43.847911],
          [-95.462547, 44.195942],
          [-95.108777, 44.195044],
          [-95.100221, 44.108034],
          [-94.859803, 44.108016],
          [-94.859388, 43.848093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27047",
      state: "27",
      county: "047",
      name: "Freeborn County",
      censusArea: 707.087,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.64829, 43.848139],
          [-93.406642, 43.84812],
          [-93.049524, 43.848443],
          [-93.049192, 43.499571],
          [-93.228861, 43.499567],
          [-93.2718, 43.499356],
          [-93.399035, 43.499485],
          [-93.428509, 43.499478],
          [-93.468563, 43.499473],
          [-93.472804, 43.4994],
          [-93.482009, 43.499482],
          [-93.488261, 43.499417],
          [-93.49735, 43.499456],
          [-93.497405, 43.499456],
          [-93.528482, 43.499471],
          [-93.532178, 43.499472],
          [-93.558631, 43.499521],
          [-93.576728, 43.49952],
          [-93.617131, 43.499548],
          [-93.648533, 43.499559],
          [-93.64829, 43.848139]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27061",
      state: "27",
      county: "061",
      name: "Itasca County",
      censusArea: 2667.722,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.416279, 47.444828],
          [-94.41904, 47.758929],
          [-94.418543, 47.845816],
          [-93.776173, 47.846404],
          [-93.775772, 47.89895],
          [-93.081158, 47.89187],
          [-93.061253, 47.720187],
          [-93.0695, 47.393915],
          [-93.055943, 47.026415],
          [-93.775257, 47.030418],
          [-93.77393, 47.211335],
          [-93.80882, 47.232622],
          [-93.77401, 47.30506],
          [-93.93327, 47.304639],
          [-94.027108, 47.352277],
          [-94.027858, 47.408633],
          [-94.095115, 47.477972],
          [-94.327531, 47.415844],
          [-94.416279, 47.444828]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29095",
      state: "29",
      county: "095",
      name: "Jackson County",
      censusArea: 604.462,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.607354, 39.113444],
          [-94.523463, 39.148771],
          [-94.46159, 39.120372],
          [-94.389728, 39.193503],
          [-94.335213, 39.183337],
          [-94.289873, 39.237107],
          [-94.212515, 39.207086],
          [-94.104823, 39.143507],
          [-94.109584, 39.018745],
          [-94.109953, 39.015815],
          [-94.110863, 38.986311],
          [-94.111478, 38.982862],
          [-94.112928, 38.960904],
          [-94.11427, 38.92857],
          [-94.114175, 38.917304],
          [-94.113712, 38.915366],
          [-94.119096, 38.833167],
          [-94.120778, 38.833006],
          [-94.399447, 38.838809],
          [-94.608033, 38.847207],
          [-94.608033, 38.855007],
          [-94.608033, 38.861207],
          [-94.607993, 38.867271],
          [-94.608033, 38.868107],
          [-94.608033, 38.869207],
          [-94.608033, 38.883807],
          [-94.607978, 38.93687],
          [-94.607866, 38.937398],
          [-94.608134, 38.940006],
          [-94.608134, 38.942006],
          [-94.607391, 39.044086],
          [-94.607234, 39.065704],
          [-94.607334, 39.081704],
          [-94.607234, 39.089604],
          [-94.607354, 39.113444]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47133",
      state: "47",
      county: "133",
      name: "Overton County",
      censusArea: 433.483,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.495124, 36.302278],
          [-85.497587, 36.375051],
          [-85.495422, 36.403074],
          [-85.438808, 36.501102],
          [-85.284878, 36.531293],
          [-85.185359, 36.490359],
          [-85.120445, 36.407164],
          [-85.122245, 36.304599],
          [-85.082497, 36.196171],
          [-85.119583, 36.144719],
          [-85.204156, 36.152489],
          [-85.419432, 36.236145],
          [-85.495124, 36.302278]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47145",
      state: "47",
      county: "145",
      name: "Roane County",
      censusArea: 360.708,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.324987, 36.004535],
          [-84.32006, 35.991607],
          [-84.271587, 35.910165],
          [-84.268393, 35.902563],
          [-84.263383, 35.89694],
          [-84.584472, 35.644424],
          [-84.619865, 35.644651],
          [-84.61988, 35.658865],
          [-84.625868, 35.658869],
          [-84.723853, 35.752896],
          [-84.781901, 35.825],
          [-84.680633, 35.908454],
          [-84.606659, 35.951145],
          [-84.446054, 35.985984],
          [-84.341812, 36.048977],
          [-84.340405, 36.045727],
          [-84.340197, 36.044973],
          [-84.324987, 36.004535]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47159",
      state: "47",
      county: "159",
      name: "Smith County",
      censusArea: 314.289,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.978893, 36.426073],
          [-85.828026, 36.411836],
          [-85.846483, 36.288167],
          [-85.780148, 36.238011],
          [-85.807415, 36.131582],
          [-86.059706, 36.086024],
          [-86.136803, 36.295859],
          [-85.978893, 36.426073]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47165",
      state: "47",
      county: "165",
      name: "Sumner County",
      censusArea: 529.449,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.228434, 36.487472],
          [-86.283256, 36.3475],
          [-86.298452, 36.313634],
          [-86.451721, 36.335629],
          [-86.543434, 36.320602],
          [-86.594286, 36.245105],
          [-86.723495, 36.34768],
          [-86.754795, 36.405496],
          [-86.69875, 36.4074],
          [-86.564252, 36.633522],
          [-86.564143, 36.633472],
          [-86.473497, 36.651671],
          [-86.473413, 36.651676],
          [-86.47219, 36.651763],
          [-86.468497, 36.651841],
          [-86.411387, 36.65055],
          [-86.333051, 36.648778],
          [-86.222151, 36.640891],
          [-86.219081, 36.640824],
          [-86.21641, 36.640595],
          [-86.216183, 36.640527],
          [-86.205468, 36.639783],
          [-86.228434, 36.487472]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45047",
      state: "45",
      county: "047",
      name: "Greenwood County",
      censusArea: 454.726,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.870084, 34.135357],
          [-82.008298, 33.961646],
          [-82.046974, 33.95321],
          [-82.04513, 33.983396],
          [-82.200286, 33.978597],
          [-82.245055, 34.018781],
          [-82.306243, 33.971584],
          [-82.326947, 34.06412],
          [-82.267184, 34.10836],
          [-82.247747, 34.219619],
          [-82.334234, 34.34263],
          [-82.246337, 34.409676],
          [-82.169297, 34.330746],
          [-81.945019, 34.203148],
          [-81.870084, 34.135357]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45051",
      state: "45",
      county: "051",
      name: "Horry County",
      censusArea: 1133.896,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.002109, 33.572114],
          [-79.100653, 33.571807],
          [-79.190264, 33.705787],
          [-79.339076, 33.969722],
          [-79.126681, 34.235362],
          [-79.135118, 34.241458],
          [-79.128165, 34.253652],
          [-79.071169, 34.29924],
          [-78.712206, 33.996732],
          [-78.710141, 33.994688],
          [-78.6505, 33.944035],
          [-78.541087, 33.851112],
          [-78.553944, 33.847831],
          [-78.584841, 33.844282],
          [-78.67226, 33.817587],
          [-78.714116, 33.800138],
          [-78.772737, 33.768511],
          [-78.812931, 33.743472],
          [-78.862931, 33.705654],
          [-78.938076, 33.639826],
          [-79.002109, 33.572114]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45059",
      state: "45",
      county: "059",
      name: "Laurens County",
      censusArea: 713.804,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.313996, 34.484002],
          [-82.236, 34.535962],
          [-82.193006, 34.686303],
          [-82.146792, 34.785554],
          [-82.012297, 34.668587],
          [-81.854002, 34.594549],
          [-81.643552, 34.533652],
          [-81.782627, 34.365089],
          [-81.895713, 34.29988],
          [-81.945019, 34.203148],
          [-82.169297, 34.330746],
          [-82.246337, 34.409676],
          [-82.313996, 34.484002]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45069",
      state: "45",
      county: "069",
      name: "Marlboro County",
      censusArea: 479.674,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.744116, 34.805651],
          [-79.692948, 34.804973],
          [-79.690201, 34.804937],
          [-79.688088, 34.804897],
          [-79.634216, 34.771012],
          [-79.631577, 34.768835],
          [-79.561691, 34.711996],
          [-79.554454, 34.706363],
          [-79.520269, 34.678327],
          [-79.519043, 34.677321],
          [-79.490201, 34.653819],
          [-79.479305, 34.64464],
          [-79.471599, 34.6372],
          [-79.468717, 34.635323],
          [-79.461754, 34.630432],
          [-79.461318, 34.630126],
          [-79.459766, 34.629027],
          [-79.450034, 34.621036],
          [-79.633413, 34.297994],
          [-79.635373, 34.301299],
          [-79.641751, 34.299762],
          [-79.644463, 34.30119],
          [-79.657764, 34.30519],
          [-79.684559, 34.40049],
          [-79.750566, 34.436797],
          [-79.721794, 34.485416],
          [-79.829981, 34.531925],
          [-79.778249, 34.614868],
          [-79.871959, 34.693841],
          [-79.927618, 34.806555],
          [-79.773607, 34.805931],
          [-79.772829, 34.805954],
          [-79.744925, 34.805686],
          [-79.744116, 34.805651]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45087",
      state: "45",
      county: "087",
      name: "Union County",
      censusArea: 514.171,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.854002, 34.594549],
          [-81.78339, 34.837125],
          [-81.711694, 34.912577],
          [-81.481613, 34.866221],
          [-81.457257, 34.83928],
          [-81.461755, 34.831018],
          [-81.478454, 34.821509],
          [-81.422706, 34.572029],
          [-81.419829, 34.566588],
          [-81.424304, 34.55599],
          [-81.427545, 34.529482],
          [-81.424907, 34.524073],
          [-81.421139, 34.503224],
          [-81.423241, 34.494379],
          [-81.54043, 34.445027],
          [-81.643552, 34.533652],
          [-81.854002, 34.594549]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46015",
      state: "46",
      county: "015",
      name: "Brule County",
      censusArea: 817.24,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.926997, 43.935143],
          [-98.807771, 43.935223],
          [-98.794588, 43.499187],
          [-99.297882, 43.499856],
          [-99.375378, 43.656869],
          [-99.458809, 43.6558],
          [-99.41015, 43.774619],
          [-99.310826, 43.855216],
          [-99.355864, 43.934371],
          [-99.350695, 43.933752],
          [-98.926997, 43.935143]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46025",
      state: "46",
      county: "025",
      name: "Clark County",
      censusArea: 957.604,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.491346, 44.804035],
          [-97.492266, 44.543884],
          [-97.853028, 44.544398],
          [-97.856226, 44.631178],
          [-97.977791, 44.631602],
          [-97.981792, 45.153295],
          [-97.494254, 45.151631],
          [-97.491346, 44.804035]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46037",
      state: "46",
      county: "037",
      name: "Day County",
      censusArea: 1027.868,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.981457, 45.240415],
          [-97.979592, 45.588483],
          [-97.373366, 45.587634],
          [-97.227089, 45.558158],
          [-97.226244, 45.297647],
          [-97.226281, 45.151826],
          [-97.494254, 45.151631],
          [-97.981792, 45.153295],
          [-97.981641, 45.170965],
          [-97.981511, 45.172156],
          [-97.981457, 45.240415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46049",
      state: "46",
      county: "049",
      name: "Faulk County",
      censusArea: 981.75,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.311754, 44.897227],
          [-99.572699, 44.897263],
          [-99.573445, 45.245364],
          [-98.722481, 45.243764],
          [-98.717759, 45.243732],
          [-98.716498, 44.896925],
          [-99.311754, 44.897227]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46057",
      state: "46",
      county: "057",
      name: "Hamlin County",
      censusArea: 507.231,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.492266, 44.543884],
          [-97.491346, 44.804035],
          [-96.88457, 44.804436],
          [-96.885504, 44.543773],
          [-97.128024, 44.543126],
          [-97.492266, 44.543884]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46067",
      state: "46",
      county: "067",
      name: "Hutchinson County",
      censusArea: 812.904,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.077108, 43.16833],
          [-98.069044, 43.184887],
          [-98.109492, 43.196838],
          [-98.113196, 43.484439],
          [-98.114758, 43.498297],
          [-97.965012, 43.49904],
          [-97.607012, 43.499826],
          [-97.40145, 43.499783],
          [-97.399191, 43.169419],
          [-97.637496, 43.16877],
          [-98.077108, 43.16833]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46077",
      state: "46",
      county: "077",
      name: "Kingsbury County",
      censusArea: 832.236,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.370115, 44.194971],
          [-97.849492, 44.195235],
          [-97.85315, 44.19522],
          [-97.85366, 44.195233],
          [-97.853206, 44.543254],
          [-97.853028, 44.544398],
          [-97.492266, 44.543884],
          [-97.128024, 44.543126],
          [-97.129671, 44.195933],
          [-97.370115, 44.194971]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51097",
      state: "51",
      county: "097",
      name: "King And Queen County",
      censusArea: 315.138,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.654076, 37.603174],
          [-76.647086, 37.480685],
          [-76.712305, 37.431309],
          [-76.732961, 37.450235],
          [-76.741984, 37.467076],
          [-76.747411, 37.478153],
          [-76.751693, 37.483941],
          [-76.76454, 37.493563],
          [-76.781293, 37.501339],
          [-76.789524, 37.503862],
          [-76.791457, 37.508339],
          [-76.792184, 37.517594],
          [-76.777729, 37.554446],
          [-76.915586, 37.693325],
          [-77.033273, 37.720809],
          [-77.181418, 37.891654],
          [-77.148634, 37.972],
          [-77.070115, 37.96477],
          [-77.018849, 37.844848],
          [-76.943137, 37.78291],
          [-76.804423, 37.794892],
          [-76.751072, 37.728605],
          [-76.654076, 37.603174]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51109",
      state: "51",
      county: "109",
      name: "Louisa County",
      censusArea: 496.3,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.687294, 38.008011],
          [-77.796803, 37.730604],
          [-77.79692, 37.729069],
          [-77.888908, 37.760808],
          [-77.948552, 37.846338],
          [-78.06331, 37.905108],
          [-78.306607, 38.006399],
          [-78.254287, 38.063831],
          [-78.208092, 38.13315],
          [-78.093063, 38.151611],
          [-77.954819, 38.117437],
          [-77.786614, 38.076337],
          [-77.687294, 38.008011]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01035",
      state: "01",
      county: "035",
      name: "Conecuh County",
      censusArea: 850.156,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.701554, 31.523946],
          [-86.667245, 31.369404],
          [-86.700282, 31.192217],
          [-86.772519, 31.202243],
          [-86.763961, 31.261293],
          [-87.427455, 31.260386],
          [-87.166581, 31.519561],
          [-87.135051, 31.642417],
          [-87.052284, 31.716761],
          [-86.905899, 31.753035],
          [-86.906769, 31.632671],
          [-86.839386, 31.525204],
          [-86.701554, 31.523946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01039",
      state: "01",
      county: "039",
      name: "Covington County",
      censusArea: 1030.456,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.388646, 30.994181],
          [-86.388647, 30.994181],
          [-86.391937, 30.994172],
          [-86.404912, 30.994049],
          [-86.454704, 30.993791],
          [-86.458319, 30.993998],
          [-86.512834, 30.9937],
          [-86.519938, 30.993245],
          [-86.563436, 30.995223],
          [-86.567586, 30.995109],
          [-86.664681, 30.994534],
          [-86.678383, 30.994537],
          [-86.688294, 30.995029],
          [-86.700282, 31.192217],
          [-86.667245, 31.369404],
          [-86.701554, 31.523946],
          [-86.690217, 31.523693],
          [-86.499213, 31.525331],
          [-86.39994, 31.527128],
          [-86.398536, 31.451325],
          [-86.280155, 31.52851],
          [-86.277031, 31.455602],
          [-86.193951, 31.440072],
          [-86.196365, 31.425431],
          [-86.193476, 31.192213],
          [-86.187246, 30.993992],
          [-86.289247, 30.993798],
          [-86.304596, 30.994029],
          [-86.364907, 30.994455],
          [-86.36927, 30.994477],
          [-86.374545, 30.994474],
          [-86.388646, 30.994181]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01049",
      state: "01",
      county: "049",
      name: "De Kalb County",
      censusArea: 777.093,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.513709, 34.52417],
          [-85.513699, 34.524133],
          [-85.576981, 34.483543],
          [-85.636645, 34.366622],
          [-85.843617, 34.200006],
          [-86.106086, 34.200756],
          [-86.110362, 34.427797],
          [-86.057712, 34.475994],
          [-86.00384, 34.480031],
          [-85.785471, 34.624584],
          [-85.583145, 34.860371],
          [-85.552482, 34.708321],
          [-85.552454, 34.708138],
          [-85.541267, 34.656783],
          [-85.541264, 34.656701],
          [-85.534423, 34.623789],
          [-85.527127, 34.588684],
          [-85.51393, 34.525192],
          [-85.513709, 34.52417]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01053",
      state: "01",
      county: "053",
      name: "Escambia County",
      censusArea: 945.08,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.163084, 30.999051],
          [-87.25498, 30.998285],
          [-87.255592, 30.998216],
          [-87.257002, 30.998194],
          [-87.25796, 30.998263],
          [-87.259689, 30.998172],
          [-87.26054, 30.998195],
          [-87.265564, 30.998267],
          [-87.288905, 30.998345],
          [-87.290995, 30.998352],
          [-87.301567, 30.998434],
          [-87.30403, 30.998191],
          [-87.312183, 30.998435],
          [-87.333973, 30.998272],
          [-87.355656, 30.998244],
          [-87.364011, 30.998218],
          [-87.367842, 30.998292],
          [-87.425774, 30.99809],
          [-87.432292, 30.998205],
          [-87.449811, 30.998272],
          [-87.455705, 30.998318],
          [-87.458658, 30.998386],
          [-87.461638, 30.998202],
          [-87.461783, 30.998201],
          [-87.466827, 30.998178],
          [-87.466879, 30.998178],
          [-87.478706, 30.998213],
          [-87.479703, 30.998197],
          [-87.480243, 30.998202],
          [-87.548543, 30.997927],
          [-87.571281, 30.99787],
          [-87.598928, 30.997457],
          [-87.598927, 30.997454],
          [-87.615431, 30.997361],
          [-87.61589, 31.244458],
          [-87.427455, 31.260386],
          [-86.763961, 31.261293],
          [-86.772519, 31.202243],
          [-86.700282, 31.192217],
          [-86.688294, 30.995029],
          [-86.725379, 30.996872],
          [-86.727293, 30.996882],
          [-86.728392, 30.996739],
          [-86.785692, 30.996977],
          [-86.785918, 30.996978],
          [-86.830497, 30.997401],
          [-86.831934, 30.997378],
          [-86.872989, 30.997631],
          [-86.888135, 30.997577],
          [-87.027107, 30.999255],
          [-87.036366, 30.999348],
          [-87.039989, 30.999594],
          [-87.053737, 30.999131],
          [-87.064063, 30.999191],
          [-87.068633, 30.999143],
          [-87.162614, 30.999055],
          [-87.163084, 30.999051]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01063",
      state: "01",
      county: "063",
      name: "Greene County",
      censusArea: 647.11,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.026971, 33.063602],
          [-87.837521, 33.153637],
          [-87.838047, 33.136864],
          [-87.832233, 33.017258],
          [-87.715709, 33.006824],
          [-87.870464, 32.762442],
          [-87.828542, 32.768903],
          [-87.796043, 32.715632],
          [-87.813401, 32.633875],
          [-87.736786, 32.58919],
          [-87.812559, 32.52456],
          [-87.823383, 32.520443],
          [-87.822317, 32.543748],
          [-87.845144, 32.546687],
          [-87.85342, 32.532086],
          [-87.897913, 32.600046],
          [-87.840858, 32.605036],
          [-87.928689, 32.632284],
          [-88.057333, 32.593441],
          [-88.109633, 32.770989],
          [-88.079338, 32.772948],
          [-88.207316, 32.924782],
          [-88.171852, 32.99586],
          [-88.026971, 33.063602]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01069",
      state: "01",
      county: "069",
      name: "Houston County",
      censusArea: 579.824,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.002368, 31.000682],
          [-85.024108, 31.000681],
          [-85.027512, 31.00067],
          [-85.030107, 31.000653],
          [-85.031155, 31.000647],
          [-85.052088, 31.000585],
          [-85.054802, 31.000585],
          [-85.057534, 31.000585],
          [-85.145835, 31.000695],
          [-85.152085, 31.000888],
          [-85.152218, 31.000834],
          [-85.154452, 31.000835],
          [-85.488298, 30.997041],
          [-85.48576, 31.199886],
          [-85.710866, 31.195179],
          [-85.659985, 31.270889],
          [-85.485854, 31.246096],
          [-85.417434, 31.314973],
          [-85.179131, 31.307675],
          [-85.087651, 31.308677],
          [-85.101561, 31.283362],
          [-85.111871, 31.258388],
          [-85.107516, 31.186451],
          [-85.100207, 31.16549],
          [-85.092106, 31.160293],
          [-85.083582, 31.15963],
          [-85.076628, 31.156927],
          [-85.035615, 31.108192],
          [-85.029736, 31.096163],
          [-85.022121, 31.07548],
          [-84.999428, 31.013843],
          [-85.002368, 31.000682]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01075",
      state: "01",
      county: "075",
      name: "Lamar County",
      censusArea: 604.846,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.24839, 33.744908],
          [-88.226517, 33.911551],
          [-88.226517, 33.911665],
          [-88.226428, 33.912875],
          [-88.207229, 34.058333],
          [-87.98693, 34.052102],
          [-87.951785, 33.91993],
          [-87.946519, 33.524065],
          [-88.274619, 33.534008],
          [-88.27005, 33.570819],
          [-88.269532, 33.572894],
          [-88.269076, 33.576929],
          [-88.26816, 33.58504],
          [-88.267148, 33.591989],
          [-88.267005, 33.594229],
          [-88.256343, 33.682053],
          [-88.256131, 33.68286],
          [-88.254622, 33.69578],
          [-88.254445, 33.698779],
          [-88.24839, 33.744908]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01077",
      state: "01",
      county: "077",
      name: "Lauderdale County",
      censusArea: 667.697,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.18245, 35.007712],
          [-87.984916, 35.006256],
          [-87.877969, 35.005468],
          [-87.877742, 35.005512],
          [-87.872626, 35.005571],
          [-87.853528, 35.005541],
          [-87.853411, 35.005576],
          [-87.851886, 35.005656],
          [-87.773586, 35.004946],
          [-87.767602, 35.004783],
          [-87.75889, 35.004711],
          [-87.709491, 35.004089],
          [-87.702321, 35.003945],
          [-87.700543, 35.003988],
          [-87.696834, 35.003852],
          [-87.671405, 35.003537],
          [-87.664123, 35.003523],
          [-87.606031, 35.003343],
          [-87.428613, 35.002795],
          [-87.421543, 35.002679],
          [-87.4174, 35.002669],
          [-87.391314, 35.002374],
          [-87.381071, 35.002118],
          [-87.359281, 35.001823],
          [-87.349251, 35.001662],
          [-87.299185, 35.000915],
          [-87.270014, 35.00039],
          [-87.230544, 34.999484],
          [-87.224053, 34.999327],
          [-87.216683, 34.999148],
          [-87.210759, 34.999024],
          [-87.214914, 34.816011],
          [-87.260676, 34.758626],
          [-87.42651, 34.800022],
          [-87.524363, 34.832384],
          [-87.835833, 34.741239],
          [-87.973222, 34.88255],
          [-88.097888, 34.892202],
          [-88.125038, 34.902227],
          [-88.200064, 34.995634],
          [-88.18245, 35.007712]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12047",
      state: "12",
      county: "047",
      name: "Hamilton County",
      censusArea: 513.79,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.187391, 30.627223],
          [-83.174411, 30.626444],
          [-83.163309, 30.625895],
          [-83.15617, 30.625504],
          [-83.136616, 30.624346],
          [-82.878779, 30.609082],
          [-82.877259, 30.609024],
          [-82.698902, 30.598271],
          [-82.698618, 30.598232],
          [-82.689539, 30.597734],
          [-82.726583, 30.559486],
          [-82.645768, 30.407992],
          [-82.681473, 30.342805],
          [-82.794594, 30.337024],
          [-83.002058, 30.429371],
          [-83.078706, 30.438116],
          [-83.170969, 30.385225],
          [-83.243766, 30.473132],
          [-83.272612, 30.627687],
          [-83.30925, 30.634405],
          [-83.309249, 30.634405],
          [-83.256218, 30.631279],
          [-83.187391, 30.627223]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12067",
      state: "12",
      county: "067",
      name: "Lafayette County",
      censusArea: 543.411,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.36726, 30.26044],
          [-83.249862, 30.260353],
          [-83.247253, 30.26067],
          [-83.22636, 30.114621],
          [-83.160557, 30.109823],
          [-82.963112, 29.99305],
          [-82.879802, 29.886847],
          [-82.875616, 29.866527],
          [-82.892872, 29.82746],
          [-82.920608, 29.824143],
          [-83.318859, 29.822623],
          [-83.37089, 29.887934],
          [-83.36726, 30.26044]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12077",
      state: "12",
      county: "077",
      name: "Liberty County",
      censusArea: 835.562,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.54445, 30.01103],
          [-85.007626, 30.013883],
          [-85.025439, 29.971399],
          [-85.113691, 30.023173],
          [-85.153773, 30.091294],
          [-85.11566, 30.199976],
          [-85.059092, 30.263229],
          [-84.983203, 30.441837],
          [-84.990017, 30.524795],
          [-84.932519, 30.606393],
          [-84.882616, 30.533094],
          [-84.781813, 30.518693],
          [-84.781111, 30.459794],
          [-84.646408, 30.388281],
          [-84.713351, 30.30017],
          [-84.669258, 30.1252],
          [-84.54445, 30.01103]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04001",
      state: "04",
      county: "001",
      name: "Apache County",
      censusArea: 11197.521,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.045973, 36.002338],
          [-109.046011, 35.925896],
          [-109.046054, 35.92586],
          [-109.046055, 35.888721],
          [-109.046024, 35.8798],
          [-109.046295, 35.616517],
          [-109.046296, 35.614251],
          [-109.046509, 35.54644],
          [-109.046481, 35.546326],
          [-109.046082, 35.174665],
          [-109.045851, 34.959718],
          [-109.046156, 34.579291],
          [-109.046182, 34.522553],
          [-109.046182, 34.522393],
          [-109.046627, 33.778233],
          [-109.348062, 33.77752],
          [-109.49544, 33.652697],
          [-109.799782, 33.488314],
          [-109.891759, 33.567829],
          [-109.891766, 33.777777],
          [-109.859274, 33.777683],
          [-109.849387, 34.125945],
          [-109.848246, 34.473435],
          [-109.865231, 34.559689],
          [-109.844624, 34.822562],
          [-109.843764, 35.517875],
          [-109.826936, 35.662484],
          [-110.000674, 35.662657],
          [-110.000673, 35.844269],
          [-109.999188, 36.695911],
          [-110.000677, 36.997968],
          [-109.381226, 36.999148],
          [-109.378039, 36.999135],
          [-109.270097, 36.999266],
          [-109.268213, 36.999242],
          [-109.26339, 36.999263],
          [-109.246917, 36.999346],
          [-109.233848, 36.999266],
          [-109.181196, 36.999271],
          [-109.045223, 36.999084],
          [-109.045244, 36.969489],
          [-109.045272, 36.968871],
          [-109.045407, 36.874998],
          [-109.045433, 36.874589],
          [-109.045973, 36.002338]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04007",
      state: "04",
      county: "007",
      name: "Gila County",
      censusArea: 4757.933,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.039905, 33.466104],
          [-111.153354, 33.677606],
          [-111.222055, 33.602446],
          [-111.26283, 33.623157],
          [-111.389444, 33.776663],
          [-111.372317, 33.823924],
          [-111.450904, 33.919877],
          [-111.433271, 33.983321],
          [-111.494778, 33.999817],
          [-111.477853, 34.151108],
          [-111.720728, 34.161704],
          [-111.659514, 34.386424],
          [-111.555124, 34.472196],
          [-111.502415, 34.498057],
          [-111.472046, 34.462265],
          [-111.550328, 34.419057],
          [-111.423066, 34.437225],
          [-111.427266, 34.386149],
          [-111.397229, 34.426062],
          [-111.250878, 34.453301],
          [-111.147094, 34.375185],
          [-111.096661, 34.396431],
          [-110.848059, 34.267205],
          [-110.788078, 34.30906],
          [-110.750678, 34.263314],
          [-110.750672, 34.000048],
          [-110.00066, 34.000046],
          [-110.000649, 33.57693],
          [-110.000646, 33.465916],
          [-110.340949, 33.427066],
          [-110.441544, 33.38167],
          [-110.457194, 33.295268],
          [-110.401536, 33.196612],
          [-110.449021, 33.194526],
          [-110.525952, 33.159198],
          [-110.678006, 33.111182],
          [-110.780811, 32.983722],
          [-110.946684, 33.250053],
          [-110.974706, 33.342095],
          [-111.039905, 33.466104]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04019",
      state: "04",
      county: "019",
      name: "Pima County",
      censusArea: 9187.036,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.792534, 32.506779],
          [-111.27061, 32.501366],
          [-110.925567, 32.511391],
          [-110.844943, 32.511211],
          [-110.844923, 32.513767],
          [-110.785444, 32.513539],
          [-110.451221, 32.513807],
          [-110.451228, 32.504408],
          [-110.451503, 32.427354],
          [-110.451736, 31.731267],
          [-111.158299, 31.726007],
          [-111.164183, 31.725983],
          [-111.163995, 31.618687],
          [-111.163725, 31.521826],
          [-111.366613, 31.521291],
          [-111.366635, 31.42588],
          [-111.57953, 31.494095],
          [-112.246102, 31.704195],
          [-112.867074, 31.895488],
          [-113.125961, 31.97278],
          [-113.333794, 32.038521],
          [-113.333897, 32.504938],
          [-112.203719, 32.50676],
          [-111.792534, 32.506779]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05011",
      state: "05",
      county: "011",
      name: "Bradley County",
      censusArea: 649.231,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.975844, 33.704414],
          [-91.97608, 33.529687],
          [-92.00785, 33.529396],
          [-91.990167, 33.398214],
          [-91.977284, 33.302026],
          [-92.128464, 33.208775],
          [-92.137527, 33.162956],
          [-92.22406, 33.251782],
          [-92.350432, 33.298941],
          [-92.374467, 33.37865],
          [-92.311079, 33.499782],
          [-92.330839, 33.707806],
          [-91.975844, 33.704414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05023",
      state: "05",
      county: "023",
      name: "Cleburne County",
      censusArea: 553.692,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.837179, 35.704932],
          [-91.789823, 35.711734],
          [-91.793958, 35.534114],
          [-91.795855, 35.455574],
          [-91.850144, 35.436442],
          [-91.851958, 35.358287],
          [-92.111463, 35.362725],
          [-92.252759, 35.363718],
          [-92.24052, 35.71219],
          [-91.837179, 35.704932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05037",
      state: "05",
      county: "037",
      name: "Cross County",
      censusArea: 616.381,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.039783, 35.354256],
          [-91.03923, 35.444717],
          [-90.502429, 35.441726],
          [-90.502993, 35.144214],
          [-91.04267, 35.148189],
          [-91.039783, 35.354256]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05039",
      state: "05",
      county: "039",
      name: "Dallas County",
      censusArea: 667.386,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.336765, 34.059633],
          [-92.471667, 34.057054],
          [-92.475602, 33.972696],
          [-92.400798, 33.91356],
          [-92.334249, 33.795065],
          [-92.583054, 33.802198],
          [-92.893534, 33.809943],
          [-92.89314, 33.902751],
          [-92.961185, 33.97482],
          [-92.893116, 33.983284],
          [-92.886285, 34.155877],
          [-92.813986, 34.15166],
          [-92.678383, 34.149622],
          [-92.493672, 34.144234],
          [-92.336765, 34.059633]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05055",
      state: "05",
      county: "055",
      name: "Greene County",
      censusArea: 577.699,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.198735, 36.201382],
          [-90.220425, 36.184764],
          [-90.319168, 36.089976],
          [-90.36443, 36.013625],
          [-90.368718, 35.995812],
          [-90.387378, 35.964708],
          [-90.852064, 35.968092],
          [-90.820763, 36.07977],
          [-90.747425, 36.149116],
          [-90.809816, 36.149567],
          [-90.806615, 36.266865],
          [-90.31982, 36.259144],
          [-90.32096, 36.200575],
          [-90.198735, 36.201382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05065",
      state: "05",
      county: "065",
      name: "Izard County",
      censusArea: 580.577,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.849448, 35.867411],
          [-91.95302, 35.931644],
          [-92.108622, 35.931328],
          [-92.063576, 36.024372],
          [-92.19543, 36.132798],
          [-92.15699, 36.261248],
          [-91.691435, 36.253301],
          [-91.682277, 36.000535],
          [-91.710552, 35.941759],
          [-91.753345, 35.942883],
          [-91.849448, 35.867411]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05079",
      state: "05",
      county: "079",
      name: "Lincoln County",
      censusArea: 561.517,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.445614, 34.080147],
          [-91.435887, 34.06844],
          [-91.434621, 34.052702],
          [-91.437131, 34.034663],
          [-91.431239, 34.020363],
          [-91.421715, 34.014472],
          [-91.548668, 33.976799],
          [-91.557648, 33.783384],
          [-91.663738, 33.792246],
          [-91.975974, 33.791787],
          [-91.976439, 33.977297],
          [-91.953799, 34.064141],
          [-91.953629, 34.09338],
          [-91.74499, 34.094021],
          [-91.727276, 34.174093],
          [-91.445614, 34.080147]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29099",
      state: "29",
      county: "099",
      name: "Jefferson County",
      censusArea: 656.629,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.736319, 38.466645],
          [-90.591123, 38.501652],
          [-90.405262, 38.501153],
          [-90.376841, 38.452861],
          [-90.340297, 38.386998],
          [-90.346118, 38.381853],
          [-90.350918, 38.375053],
          [-90.370819, 38.333554],
          [-90.372519, 38.323354],
          [-90.373929, 38.281853],
          [-90.363926, 38.236355],
          [-90.353902, 38.213855],
          [-90.334258, 38.189932],
          [-90.254112, 38.12199],
          [-90.416022, 38.042315],
          [-90.583324, 38.003499],
          [-90.63998, 38.076548],
          [-90.780185, 38.204112],
          [-90.737599, 38.415897],
          [-90.736319, 38.466645]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29115",
      state: "29",
      county: "115",
      name: "Linn County",
      censusArea: 615.564,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.266968, 39.703562],
          [-93.364808, 39.703803],
          [-93.361957, 39.967606],
          [-93.361951, 40.033098],
          [-92.856191, 40.037253],
          [-92.847477, 40.037301],
          [-92.85792, 39.699985],
          [-93.082799, 39.706709],
          [-93.266968, 39.703562]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29121",
      state: "29",
      county: "121",
      name: "Macon County",
      censusArea: 801.227,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.345064, 40.03721],
          [-92.344708, 39.953297],
          [-92.287882, 39.95249],
          [-92.299316, 39.605344],
          [-92.300871, 39.605348],
          [-92.692149, 39.610265],
          [-92.689331, 39.698094],
          [-92.85792, 39.699985],
          [-92.847477, 40.037301],
          [-92.345064, 40.03721]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29135",
      state: "29",
      county: "135",
      name: "Moniteau County",
      censusArea: 415.025,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.495185, 38.427829],
          [-92.623858, 38.430639],
          [-92.625189, 38.430778],
          [-92.841871, 38.596792],
          [-92.839837, 38.683974],
          [-92.617147, 38.680013],
          [-92.498377, 38.922084],
          [-92.379614, 38.800076],
          [-92.394866, 38.73913],
          [-92.495185, 38.427829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29145",
      state: "29",
      county: "145",
      name: "Newton County",
      censusArea: 624.765,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.068898, 36.747818],
          [-94.618307, 36.76656],
          [-94.61838, 36.84732],
          [-94.618658, 36.880064],
          [-94.618243, 36.897027],
          [-94.618282, 36.911472],
          [-94.618207, 36.926236],
          [-94.618295, 36.929647],
          [-94.618166, 36.937584],
          [-94.618109, 36.946564],
          [-94.618026, 36.950158],
          [-94.618031, 36.994704],
          [-94.618049, 36.996208],
          [-94.61808, 36.998135],
          [-94.617995, 37.009016],
          [-94.617965, 37.040537],
          [-94.617875, 37.056797],
          [-94.059211, 37.048127],
          [-94.06274, 36.931775],
          [-94.068898, 36.747818]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29163",
      state: "29",
      county: "163",
      name: "Pike County",
      censusArea: 670.438,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.260232, 39.139845],
          [-91.409033, 39.142542],
          [-91.438235, 39.318716],
          [-91.460442, 39.450722],
          [-91.178012, 39.598196],
          [-91.168419, 39.564928],
          [-91.158606, 39.553048],
          [-91.042351, 39.452062],
          [-90.993789, 39.422959],
          [-90.939983, 39.393555],
          [-90.882706, 39.362254],
          [-90.842175, 39.341932],
          [-90.799346, 39.313087],
          [-90.72996, 39.255894],
          [-90.726981, 39.251173],
          [-90.718996, 39.224973],
          [-91.182942, 39.227233],
          [-91.18546, 39.13905],
          [-91.260232, 39.139845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29173",
      state: "29",
      county: "173",
      name: "Ralls County",
      censusArea: 469.781,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.715247, 39.657644],
          [-91.715497, 39.686492],
          [-91.313162, 39.684907],
          [-91.229317, 39.620853],
          [-91.178012, 39.598196],
          [-91.460442, 39.450722],
          [-91.438235, 39.318716],
          [-91.718467, 39.324505],
          [-91.717968, 39.339122],
          [-91.715247, 39.657644]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29185",
      state: "29",
      county: "185",
      name: "St. Clair County",
      censusArea: 669.983,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.049895, 38.213985],
          [-93.523484, 38.207216],
          [-93.504025, 38.071757],
          [-93.512103, 37.913535],
          [-93.568709, 37.914918],
          [-93.573202, 37.828035],
          [-93.628404, 37.829435],
          [-93.811402, 37.834571],
          [-93.808648, 37.892775],
          [-94.065675, 37.900987],
          [-94.058285, 38.036627],
          [-94.049895, 38.213985]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29201",
      state: "29",
      county: "201",
      name: "Scott County",
      censusArea: 419.99,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.691603, 36.859517],
          [-89.68662, 36.928293],
          [-89.786819, 37.087824],
          [-89.765211, 37.126066],
          [-89.620256, 37.233008],
          [-89.491222, 37.248629],
          [-89.467631, 37.2182],
          [-89.414471, 37.12505],
          [-89.385434, 37.05513],
          [-89.383937, 37.046441],
          [-89.378277, 37.039605],
          [-89.322733, 37.009033],
          [-89.465393, 36.935729],
          [-89.519809, 36.869617],
          [-89.691603, 36.859517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29215",
      state: "29",
      county: "215",
      name: "Texas County",
      censusArea: 1177.266,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.252261, 37.472944],
          [-92.249463, 37.604543],
          [-92.029258, 37.602542],
          [-91.809105, 37.598863],
          [-91.754795, 37.598768],
          [-91.75504, 37.42411],
          [-91.646626, 37.422731],
          [-91.655607, 37.048925],
          [-92.088708, 37.056248],
          [-92.25016, 37.059753],
          [-92.251359, 37.059653],
          [-92.252261, 37.472944]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29223",
      state: "29",
      county: "223",
      name: "Wayne County",
      censusArea: 759.176,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.258563, 36.922043],
          [-90.678562, 36.92649],
          [-90.779553, 37.050324],
          [-90.737146, 37.166413],
          [-90.735453, 37.27104],
          [-90.550603, 37.272608],
          [-90.549729, 37.317594],
          [-90.217657, 37.314967],
          [-90.22323, 37.086533],
          [-90.110354, 37.085871],
          [-90.111247, 37.041205],
          [-90.258563, 36.922043]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30007",
      state: "30",
      county: "007",
      name: "Broadwater County",
      censusArea: 1192.541,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.659995, 45.833512],
          [-111.660441, 46.049449],
          [-111.784333, 46.050004],
          [-111.787875, 46.568998],
          [-111.631643, 46.569108],
          [-111.639261, 46.73818],
          [-111.497961, 46.762469],
          [-111.412842, 46.646878],
          [-111.330541, 46.633458],
          [-111.34497, 46.545832],
          [-111.276801, 46.516821],
          [-111.246288, 46.437997],
          [-111.126362, 46.441322],
          [-111.057211, 46.40345],
          [-111.12707, 46.273754],
          [-111.06256, 46.192897],
          [-111.34646, 46.188102],
          [-111.415802, 46.001785],
          [-111.659995, 45.833512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30021",
      state: "30",
      county: "021",
      name: "Dawson County",
      censusArea: 2371.865,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.603766, 46.86088],
          [-105.027522, 46.86149],
          [-105.069484, 46.919128],
          [-105.196945, 46.919179],
          [-105.196735, 46.977172],
          [-105.324898, 46.977161],
          [-105.324817, 47.181586],
          [-105.406237, 47.181563],
          [-105.406511, 47.529214],
          [-105.363449, 47.5294],
          [-105.362992, 47.79042],
          [-105.234619, 47.79076],
          [-105.23488, 47.703634],
          [-104.978, 47.703374],
          [-104.978516, 47.616288],
          [-104.59363, 47.615951],
          [-104.593811, 47.528966],
          [-104.512883, 47.529252],
          [-104.513279, 47.354718],
          [-104.420263, 47.354596],
          [-104.451518, 47.318417],
          [-104.341603, 47.245598],
          [-104.305564, 47.042311],
          [-104.369228, 46.947439],
          [-104.496529, 46.86091],
          [-104.603766, 46.86088]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30031",
      state: "30",
      county: "031",
      name: "Gallatin County",
      censusArea: 2602.689,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.044275, 45.001345],
          [-111.056207, 44.935901],
          [-111.055629, 44.933578],
          [-111.056888, 44.866658],
          [-111.055495, 44.666262],
          [-111.055208, 44.624927],
          [-111.048974, 44.474072],
          [-111.106574, 44.48633],
          [-111.122654, 44.493659],
          [-111.131379, 44.499925],
          [-111.139455, 44.517112],
          [-111.219236, 44.622616],
          [-111.323669, 44.724474],
          [-111.377682, 44.751744],
          [-111.351346, 45.641938],
          [-111.536614, 45.642131],
          [-111.536816, 45.656736],
          [-111.805134, 45.798018],
          [-111.659995, 45.833512],
          [-111.415802, 46.001785],
          [-111.34646, 46.188102],
          [-111.06256, 46.192897],
          [-110.790022, 46.192567],
          [-110.783982, 46.192408],
          [-110.790447, 46.142024],
          [-110.794977, 45.590289],
          [-110.857054, 45.590872],
          [-110.858228, 45.525089],
          [-110.919212, 45.525338],
          [-110.917661, 45.351328],
          [-111.038168, 45.351174],
          [-111.044275, 45.001345]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46091",
      state: "46",
      county: "091",
      name: "Marshall County",
      censusArea: 838.066,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.978778, 45.935937],
          [-97.958718, 45.935878],
          [-97.784575, 45.935327],
          [-97.77704, 45.935393],
          [-97.696691, 45.935352],
          [-97.542598, 45.935258],
          [-97.519035, 45.935304],
          [-97.518944, 45.935304],
          [-97.491892, 45.935111],
          [-97.481967, 45.935138],
          [-97.318899, 45.935054],
          [-97.312184, 45.935077],
          [-97.228323, 45.935141],
          [-97.228304, 45.935141],
          [-97.227089, 45.558158],
          [-97.373366, 45.587634],
          [-97.979592, 45.588483],
          [-97.978778, 45.935937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46107",
      state: "46",
      county: "107",
      name: "Potter County",
      censusArea: 861.14,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.67305, 44.897492],
          [-100.404753, 44.897695],
          [-100.391818, 45.026006],
          [-100.280344, 45.018409],
          [-100.325451, 45.099337],
          [-100.260223, 45.24681],
          [-99.708987, 45.245866],
          [-99.573445, 45.245364],
          [-99.572699, 44.897263],
          [-99.67305, 44.897492]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46119",
      state: "46",
      county: "119",
      name: "Sully County",
      censusArea: 1006.817,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.676398, 44.548111],
          [-100.526498, 44.547422],
          [-100.640061, 44.583723],
          [-100.613805, 44.707135],
          [-100.717644, 44.77093],
          [-100.676387, 44.823162],
          [-100.543484, 44.761087],
          [-100.418682, 44.843366],
          [-100.404753, 44.897695],
          [-99.67305, 44.897492],
          [-99.676398, 44.548111]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46129",
      state: "46",
      county: "129",
      name: "Walworth County",
      censusArea: 708.63,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.260223, 45.24681],
          [-100.316659, 45.317904],
          [-100.274176, 45.381845],
          [-100.341924, 45.472904],
          [-100.486561, 45.536521],
          [-100.430079, 45.594823],
          [-99.717466, 45.593428],
          [-99.707705, 45.593391],
          [-99.708987, 45.245866],
          [-100.260223, 45.24681]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40057",
      state: "40",
      county: "057",
      name: "Harmon County",
      censusArea: 537.194,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.000381, 34.746358],
          [-100.000381, 34.746461],
          [-100.000384, 35.030385],
          [-99.888571, 35.030262],
          [-99.888425, 34.943946],
          [-99.731092, 34.944058],
          [-99.720066, 34.76845],
          [-99.666849, 34.724555],
          [-99.666512, 34.507121],
          [-99.846399, 34.505045],
          [-99.864922, 34.523018],
          [-99.872357, 34.532096],
          [-99.874403, 34.537095],
          [-99.884842, 34.546953],
          [-99.923211, 34.574552],
          [-99.94572, 34.579273],
          [-99.954567, 34.578195],
          [-99.997853, 34.561485],
          [-100.000381, 34.560509],
          [-100.000381, 34.746358]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40061",
      state: "40",
      county: "061",
      name: "Haskell County",
      censusArea: 576.521,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.349233, 35.058514],
          [-95.349339, 35.160296],
          [-95.454954, 35.160577],
          [-95.45125, 35.296019],
          [-95.34465, 35.293097],
          [-95.243809, 35.261636],
          [-95.170462, 35.310106],
          [-95.049933, 35.458894],
          [-94.901832, 35.39794],
          [-94.911917, 35.346651],
          [-94.813629, 35.323344],
          [-94.814284, 35.202331],
          [-94.927936, 35.202611],
          [-94.927863, 35.058279],
          [-95.349233, 35.058514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40069",
      state: "40",
      county: "069",
      name: "Johnston County",
      censusArea: 642.94,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.40733, 34.418391],
          [-96.40764, 34.157317],
          [-96.453839, 34.114406],
          [-96.585355, 34.114459],
          [-96.669965, 34.172254],
          [-96.933671, 34.172553],
          [-96.933457, 34.332661],
          [-96.881005, 34.332977],
          [-96.880814, 34.506053],
          [-96.827288, 34.505989],
          [-96.513865, 34.505389],
          [-96.513786, 34.419569],
          [-96.40733, 34.418391]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40075",
      state: "40",
      county: "075",
      name: "Kiowa County",
      censusArea: 1015.226,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.621054, 35.097352],
          [-98.619816, 34.854953],
          [-98.826124, 34.855466],
          [-98.826008, 34.594413],
          [-99.001107, 34.594366],
          [-99.001025, 34.637872],
          [-99.10315, 34.63795],
          [-99.052511, 34.701557],
          [-99.149114, 34.702352],
          [-99.147265, 34.850389],
          [-99.246462, 34.822114],
          [-99.30622, 34.857632],
          [-99.327788, 35.041697],
          [-99.409291, 35.114509],
          [-99.409268, 35.116493],
          [-99.360226, 35.116738],
          [-98.750344, 35.116343],
          [-98.621054, 35.097352]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40093",
      state: "40",
      county: "093",
      name: "Major County",
      censusArea: 954.988,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.636899, 36.164894],
          [-98.955849, 36.161577],
          [-98.960334, 36.506914],
          [-98.843646, 36.506248],
          [-98.80782, 36.460744],
          [-98.625361, 36.392595],
          [-98.53355, 36.397592],
          [-98.533617, 36.46323],
          [-98.104427, 36.463105],
          [-98.103904, 36.164877],
          [-98.210544, 36.16489],
          [-98.601244, 36.165771],
          [-98.636899, 36.164894]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40103",
      state: "40",
      county: "103",
      name: "Noble County",
      censusArea: 731.895,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.925069, 36.246474],
          [-97.140968, 36.246435],
          [-97.140668, 36.159231],
          [-97.354132, 36.159072],
          [-97.353946, 36.164304],
          [-97.460792, 36.164462],
          [-97.461603, 36.593637],
          [-97.057414, 36.593647],
          [-96.912756, 36.598949],
          [-96.902643, 36.551388],
          [-97.009785, 36.506935],
          [-97.032106, 36.333354],
          [-96.924936, 36.333344],
          [-96.925069, 36.246474]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40115",
      state: "40",
      county: "115",
      name: "Ottawa County",
      censusArea: 470.821,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.00762, 36.999514],
          [-94.995293, 36.999529],
          [-94.853197, 36.998874],
          [-94.849801, 36.998876],
          [-94.840926, 36.998833],
          [-94.83128, 36.998812],
          [-94.777257, 36.998764],
          [-94.739324, 36.998687],
          [-94.737183, 36.998665],
          [-94.71277, 36.998794],
          [-94.701797, 36.998814],
          [-94.699735, 36.998805],
          [-94.61808, 36.998135],
          [-94.618049, 36.996208],
          [-94.618031, 36.994704],
          [-94.618026, 36.950158],
          [-94.618109, 36.946564],
          [-94.618166, 36.937584],
          [-94.618295, 36.929647],
          [-94.618207, 36.926236],
          [-94.618282, 36.911472],
          [-94.618243, 36.897027],
          [-94.618658, 36.880064],
          [-94.61838, 36.84732],
          [-94.618307, 36.76656],
          [-94.61813, 36.701423],
          [-94.618025, 36.66943],
          [-94.618019, 36.667921],
          [-94.999403, 36.670631],
          [-95.00762, 36.999514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40133",
      state: "40",
      county: "133",
      name: "Seminole County",
      censusArea: 632.837,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.623699, 35.400722],
          [-96.536718, 35.390772],
          [-96.44129, 35.467712],
          [-96.441368, 35.290122],
          [-96.441401, 35.11577],
          [-96.490434, 35.115858],
          [-96.490786, 34.910585],
          [-96.556889, 34.914184],
          [-96.722891, 34.856782],
          [-96.775681, 34.899958],
          [-96.776707, 35.411372],
          [-96.623699, 35.400722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40141",
      state: "40",
      county: "141",
      name: "Tillman County",
      censusArea: 871.134,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.952527, 34.195621],
          [-98.966743, 34.201185],
          [-99.060344, 34.204761],
          [-99.119204, 34.201747],
          [-99.157114, 34.207414],
          [-99.19089, 34.215251],
          [-99.211648, 34.292232],
          [-99.2116, 34.31397],
          [-99.209724, 34.324935],
          [-99.211396, 34.337688],
          [-99.220707, 34.494484],
          [-99.10315, 34.63795],
          [-99.001025, 34.637872],
          [-99.001107, 34.594366],
          [-98.826008, 34.594413],
          [-98.82607, 34.50737],
          [-98.661982, 34.50746],
          [-98.661956, 34.405948],
          [-98.60966, 34.33372],
          [-98.610112, 34.161039],
          [-98.637575, 34.162284],
          [-98.652347, 34.161029],
          [-98.665824, 34.151617],
          [-98.700182, 34.135995],
          [-98.76557, 34.136376],
          [-98.858419, 34.152732],
          [-98.920042, 34.182908],
          [-98.952527, 34.195621]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41009",
      state: "41",
      county: "009",
      name: "Columbia County",
      censusArea: 657.357,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.213054, 46.172541],
          [-123.166414, 46.188973],
          [-123.115904, 46.185268],
          [-122.904119, 46.083734],
          [-122.884478, 46.06028],
          [-122.813998, 45.960984],
          [-122.785026, 45.867699],
          [-122.785515, 45.850536],
          [-122.795963, 45.825024],
          [-122.795605, 45.81],
          [-122.769532, 45.780583],
          [-122.761451, 45.759163],
          [-122.760108, 45.734413],
          [-122.762182, 45.728598],
          [-122.928961, 45.721502],
          [-123.030996, 45.779199],
          [-123.360962, 45.779669],
          [-123.363638, 46.146241],
          [-123.280166, 46.144843],
          [-123.213054, 46.172541]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06067",
      state: "06",
      county: "067",
      name: "Sacramento County",
      censusArea: 964.644,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.242795, 38.718241],
          [-121.188571, 38.714308],
          [-121.151857, 38.711502],
          [-121.141009, 38.71198],
          [-121.133264, 38.705427],
          [-121.118617, 38.717118],
          [-121.027507, 38.508292],
          [-121.027084, 38.300252],
          [-121.344024, 38.22826],
          [-121.484317, 38.241509],
          [-121.580022, 38.094414],
          [-121.679265, 38.089319],
          [-121.737824, 38.02663],
          [-121.862462, 38.06603],
          [-121.713926, 38.084542],
          [-121.688233, 38.145421],
          [-121.602763, 38.230219],
          [-121.603208, 38.237283],
          [-121.602121, 38.241674],
          [-121.601993, 38.252426],
          [-121.600249, 38.257817],
          [-121.60401, 38.270103],
          [-121.60073, 38.278375],
          [-121.605258, 38.295352],
          [-121.593273, 38.313089],
          [-121.521641, 38.360104],
          [-121.504139, 38.466563],
          [-121.538766, 38.474707],
          [-121.559167, 38.498007],
          [-121.51129, 38.54629],
          [-121.520965, 38.566205],
          [-121.506269, 38.586305],
          [-121.527667, 38.604604],
          [-121.549869, 38.599405],
          [-121.576068, 38.648604],
          [-121.594069, 38.644106],
          [-121.62576, 38.673133],
          [-121.630446, 38.697571],
          [-121.605774, 38.718319],
          [-121.602894, 38.735838],
          [-121.521767, 38.736401],
          [-121.484396, 38.734598],
          [-121.242795, 38.718241]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06071",
      state: "06",
      county: "071",
      name: "San Bernardino County",
      censusArea: 20056.938,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.648029, 35.809629],
          [-115.647683, 35.809358],
          [-115.647202, 35.808995],
          [-115.406079, 35.618613],
          [-115.404537, 35.617605],
          [-115.393996, 35.609344],
          [-115.391535, 35.607271],
          [-115.388866, 35.605171],
          [-115.303743, 35.538207],
          [-115.271342, 35.51266],
          [-115.225273, 35.475907],
          [-115.160599, 35.424313],
          [-115.160068, 35.424129],
          [-115.146788, 35.413662],
          [-115.145813, 35.413182],
          [-114.92548, 35.237054],
          [-114.925381, 35.237039],
          [-114.80503, 35.140284],
          [-114.804249, 35.139689],
          [-114.633013, 35.002085],
          [-114.62919, 34.991887],
          [-114.629015, 34.986148],
          [-114.630877, 34.907263],
          [-114.636725, 34.889107],
          [-114.635176, 34.875003],
          [-114.633051, 34.869971],
          [-114.552682, 34.766871],
          [-114.465607, 34.69226],
          [-114.452547, 34.653494],
          [-114.339627, 34.451435],
          [-114.264317, 34.401329],
          [-114.199482, 34.361373],
          [-114.176909, 34.349306],
          [-114.138282, 34.30323],
          [-114.131489, 34.260387],
          [-114.133264, 34.258462],
          [-114.164476, 34.251667],
          [-114.225861, 34.201774],
          [-114.244191, 34.179625],
          [-114.312592, 34.144453],
          [-114.321618, 34.138093],
          [-114.366521, 34.118575],
          [-114.390565, 34.110084],
          [-114.401352, 34.111652],
          [-114.411681, 34.110031],
          [-114.420499, 34.103466],
          [-114.43338, 34.088413],
          [-114.435429, 34.079727],
          [-115.316065, 34.077843],
          [-115.316212, 34.03411],
          [-116.457775, 34.032881],
          [-116.929558, 34.034113],
          [-116.929357, 34.004914],
          [-117.225372, 34.004314],
          [-117.284475, 34.019015],
          [-117.336161, 34.019378],
          [-117.558312, 34.033451],
          [-117.600066, 33.975316],
          [-117.673749, 33.870831],
          [-117.680289, 33.877412],
          [-117.783287, 33.946411],
          [-117.767752, 34.019429],
          [-117.735846, 34.018554],
          [-117.72601, 34.041128],
          [-117.719481, 34.059731],
          [-117.711067, 34.079536],
          [-117.650459, 34.339025],
          [-117.658798, 34.44058],
          [-117.667292, 34.822526],
          [-117.632011, 34.82227],
          [-117.630126, 35.564071],
          [-117.616195, 35.680856],
          [-117.651986, 35.709934],
          [-117.632996, 35.797251],
          [-115.735891, 35.79362],
          [-115.735745, 35.809097],
          [-115.648925, 35.809128],
          [-115.648029, 35.809629]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06111",
      state: "06",
      county: "111",
      name: "Ventura County",
      censusArea: 1843.133,
      stateName: "California"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-119.543842, 33.280329],
            [-119.532941, 33.284728],
            [-119.528141, 33.284929],
            [-119.50504, 33.272829],
            [-119.48278, 33.263973],
            [-119.465717, 33.259239],
            [-119.458466, 33.254661],
            [-119.429559, 33.228167],
            [-119.444269, 33.21919],
            [-119.464725, 33.215432],
            [-119.476029, 33.21552],
            [-119.500684, 33.220569],
            [-119.511659, 33.223027],
            [-119.517514, 33.226737],
            [-119.545872, 33.233406],
            [-119.564971, 33.24744],
            [-119.565641, 33.250029],
            [-119.566014, 33.252639],
            [-119.570642, 33.257729],
            [-119.578942, 33.278628],
            [-119.562042, 33.271129],
            [-119.555242, 33.273429],
            [-119.547642, 33.280328],
            [-119.543842, 33.280329]
          ]
        ],
        [
          [
            [-119.422972, 34.004368],
            [-119.427589, 34.006445],
            [-119.437734, 34.01],
            [-119.441116, 34.012426],
            [-119.441226, 34.014075],
            [-119.433912, 34.015975],
            [-119.421376, 34.015012],
            [-119.411317, 34.008005],
            [-119.396251, 34.005918],
            [-119.389983, 34.006099],
            [-119.366591, 34.016785],
            [-119.357462, 34.015919],
            [-119.376396, 34.010551],
            [-119.391551, 34.002505],
            [-119.414528, 34.004994],
            [-119.422972, 34.004368]
          ]
        ],
        [
          [
            [-118.881364, 34.790629],
            [-118.796692, 34.616559],
            [-118.650859, 34.320484],
            [-118.636789, 34.291804],
            [-118.633473, 34.269626],
            [-118.668152, 34.168195],
            [-118.788889, 34.168214],
            [-118.940965, 34.07483],
            [-118.944887, 34.04534],
            [-118.954722, 34.048167],
            [-118.977751, 34.059822],
            [-118.99698, 34.065943],
            [-119.004644, 34.066231],
            [-119.037494, 34.083111],
            [-119.069959, 34.09047],
            [-119.088536, 34.09831],
            [-119.098216, 34.099334],
            [-119.109784, 34.094566],
            [-119.130169, 34.100102],
            [-119.159554, 34.119653],
            [-119.18864, 34.139005],
            [-119.20314, 34.144505],
            [-119.211241, 34.144905],
            [-119.216441, 34.146105],
            [-119.227743, 34.161728],
            [-119.237142, 34.175804],
            [-119.257043, 34.213304],
            [-119.265927, 34.234609],
            [-119.270144, 34.252903],
            [-119.278644, 34.266902],
            [-119.290945, 34.274902],
            [-119.302131, 34.272761],
            [-119.313034, 34.275689],
            [-119.337475, 34.290576],
            [-119.349187, 34.304383],
            [-119.370356, 34.319486],
            [-119.37578, 34.321118],
            [-119.388249, 34.317398],
            [-119.390449, 34.318198],
            [-119.42777, 34.353016],
            [-119.431066, 34.355297],
            [-119.435888, 34.355839],
            [-119.461036, 34.374064],
            [-119.472678, 34.375628],
            [-119.478265, 34.377197],
            [-119.473793, 34.379115],
            [-119.472481, 34.38383],
            [-119.454101, 34.395568],
            [-119.445411, 34.408881],
            [-119.454277, 34.418166],
            [-119.453423, 34.42861],
            [-119.444717, 34.42951],
            [-119.440414, 34.441842],
            [-119.442278, 34.465509],
            [-119.442352, 34.901274],
            [-119.276946, 34.879675],
            [-119.243645, 34.814178],
            [-118.976721, 34.812199],
            [-118.881364, 34.790629]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08005",
      state: "08",
      county: "005",
      name: "Arapahoe County",
      censusArea: 798.1,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.884646, 39.740156],
          [-104.734969, 39.740302],
          [-103.706547, 39.739894],
          [-103.707072, 39.566295],
          [-103.714355, 39.566331],
          [-104.660626, 39.565909],
          [-105.04874, 39.566088],
          [-105.05414, 39.566088],
          [-105.053439, 39.621423],
          [-105.053411, 39.631586],
          [-105.034822, 39.627809],
          [-105.032038, 39.657087],
          [-105.015696, 39.678684],
          [-104.931703, 39.653044],
          [-104.913341, 39.624213],
          [-104.911935, 39.624346],
          [-104.911686, 39.624342],
          [-104.908862, 39.624301],
          [-104.908587, 39.624297],
          [-104.906942, 39.624268],
          [-104.906394, 39.624259],
          [-104.90549, 39.624158],
          [-104.90403, 39.623995],
          [-104.902451, 39.623939],
          [-104.901763, 39.623907],
          [-104.901719, 39.623905],
          [-104.901561, 39.623905],
          [-104.900933, 39.623906],
          [-104.899371, 39.624097],
          [-104.899339, 39.624097],
          [-104.899263, 39.624097],
          [-104.898639, 39.624099],
          [-104.897084, 39.624127],
          [-104.894642, 39.624173],
          [-104.894522, 39.624175],
          [-104.892667, 39.624135],
          [-104.891733, 39.624115],
          [-104.889813, 39.624191],
          [-104.889661, 39.6242],
          [-104.889037, 39.624176],
          [-104.888758, 39.624165],
          [-104.888331, 39.624143],
          [-104.887773, 39.624116],
          [-104.885282, 39.624129],
          [-104.88528, 39.627128],
          [-104.885213, 39.629087],
          [-104.885218, 39.629513],
          [-104.885231, 39.631345],
          [-104.885156, 39.633467],
          [-104.885173, 39.636268],
          [-104.885167, 39.636493],
          [-104.885132, 39.638093],
          [-104.885121, 39.638283],
          [-104.885124, 39.638444],
          [-104.885128, 39.638637],
          [-104.885129, 39.638788],
          [-104.885119, 39.638961],
          [-104.885112, 39.639262],
          [-104.884936, 39.639344],
          [-104.884765, 39.639425],
          [-104.88368, 39.639939],
          [-104.882917, 39.640301],
          [-104.882827, 39.640345],
          [-104.880929, 39.641216],
          [-104.880329, 39.641491],
          [-104.880322, 39.641651],
          [-104.880326, 39.64173],
          [-104.880326, 39.641819],
          [-104.880338, 39.641955],
          [-104.88034, 39.641993],
          [-104.880368, 39.642193],
          [-104.880381, 39.642475],
          [-104.880372, 39.642605],
          [-104.880313, 39.643949],
          [-104.880313, 39.645902],
          [-104.880239, 39.645993],
          [-104.880239, 39.646068],
          [-104.880246, 39.64653],
          [-104.880246, 39.647751],
          [-104.880228, 39.647994],
          [-104.880086, 39.650773],
          [-104.88014, 39.653057],
          [-104.880078, 39.653055],
          [-104.878034, 39.653051],
          [-104.877498, 39.653051],
          [-104.877497, 39.653052],
          [-104.877218, 39.652875],
          [-104.876306, 39.652295],
          [-104.876297, 39.652021],
          [-104.876281, 39.651459],
          [-104.87624, 39.65011],
          [-104.876195, 39.6486],
          [-104.876195, 39.648583],
          [-104.876151, 39.647061],
          [-104.876149, 39.647004],
          [-104.87613, 39.646499],
          [-104.876224, 39.644301],
          [-104.875552, 39.644617],
          [-104.857203, 39.653246],
          [-104.85683, 39.653419],
          [-104.856753, 39.653456],
          [-104.856038, 39.654178],
          [-104.854747, 39.654888],
          [-104.854474, 39.655011],
          [-104.852944, 39.655706],
          [-104.850762, 39.656305],
          [-104.849397, 39.656482],
          [-104.848967, 39.656538],
          [-104.84812, 39.656648],
          [-104.847697, 39.656704],
          [-104.847639, 39.656712],
          [-104.847119, 39.65678],
          [-104.847449, 39.656783],
          [-104.847536, 39.656785],
          [-104.847604, 39.656786],
          [-104.847733, 39.656788],
          [-104.847731, 39.656927],
          [-104.84773, 39.657239],
          [-104.84773, 39.657789],
          [-104.84794, 39.657824],
          [-104.848048, 39.657842],
          [-104.848336, 39.657891],
          [-104.848332, 39.659161],
          [-104.884646, 39.740156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05109",
      state: "05",
      county: "109",
      name: "Pike County",
      censusArea: 600.618,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.935371, 34.350097],
          [-93.478646, 34.340851],
          [-93.434165, 34.049739],
          [-93.373406, 33.95707],
          [-93.456709, 33.956837],
          [-93.528752, 33.943171],
          [-93.823076, 34.008893],
          [-93.820722, 34.183802],
          [-93.92583, 34.186496],
          [-93.935371, 34.350097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05117",
      state: "05",
      county: "117",
      name: "Prairie County",
      censusArea: 647.958,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.465114, 35.089454],
          [-91.363778, 35.051891],
          [-91.36856, 34.912735],
          [-91.374765, 34.665964],
          [-91.446983, 34.667531],
          [-91.447795, 34.609185],
          [-91.37782, 34.56348],
          [-91.594429, 34.56805],
          [-91.59552, 34.48051],
          [-91.678037, 34.48192],
          [-91.638531, 34.626093],
          [-91.700828, 34.672029],
          [-91.693705, 34.919605],
          [-91.803703, 34.921524],
          [-91.802505, 35.030425],
          [-91.695463, 35.062344],
          [-91.5851, 35.025234],
          [-91.584005, 35.091584],
          [-91.465114, 35.089454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05135",
      state: "05",
      county: "135",
      name: "Sharp County",
      censusArea: 604.439,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.407137, 36.497112],
          [-91.308733, 36.420363],
          [-91.25892, 36.25788],
          [-91.349834, 36.230967],
          [-91.35723, 35.890621],
          [-91.464678, 35.892497],
          [-91.463198, 35.93681],
          [-91.710552, 35.941759],
          [-91.682277, 36.000535],
          [-91.691435, 36.253301],
          [-91.566078, 36.249945],
          [-91.565866, 36.3374],
          [-91.454437, 36.335465],
          [-91.450005, 36.497538],
          [-91.446284, 36.497469],
          [-91.436502, 36.497377],
          [-91.433298, 36.497262],
          [-91.407261, 36.497123],
          [-91.407137, 36.497112]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13307",
      state: "13",
      county: "307",
      name: "Webster County",
      censusArea: 209.118,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.65582, 31.920308],
          [-84.649319, 32.232953],
          [-84.527117, 32.134556],
          [-84.431214, 32.134058],
          [-84.443807, 31.967456],
          [-84.45331, 31.919224],
          [-84.599776, 31.920171],
          [-84.65582, 31.920308]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13311",
      state: "13",
      county: "311",
      name: "White County",
      censusArea: 240.686,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.856506, 34.722191],
          [-83.780401, 34.792893],
          [-83.743107, 34.801191],
          [-83.735981, 34.800071],
          [-83.726874, 34.797732],
          [-83.72203, 34.797271],
          [-83.721035, 34.79611],
          [-83.714186, 34.793112],
          [-83.711803, 34.79052],
          [-83.707757, 34.79171],
          [-83.704146, 34.790982],
          [-83.701813, 34.792488],
          [-83.69599, 34.79352],
          [-83.693155, 34.797322],
          [-83.685685, 34.800997],
          [-83.681596, 34.800593],
          [-83.623296, 34.732194],
          [-83.662819, 34.659801],
          [-83.61554, 34.552864],
          [-83.666413, 34.503598],
          [-83.843405, 34.505494],
          [-83.877276, 34.629597],
          [-83.856506, 34.722191]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13315",
      state: "13",
      county: "315",
      name: "Wilcox County",
      censusArea: 377.705,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.177469, 31.847857],
          [-83.480176, 31.847312],
          [-83.612256, 31.854089],
          [-83.609663, 32.027938],
          [-83.608407, 32.116466],
          [-83.608547, 32.118405],
          [-83.337313, 32.105885],
          [-83.292061, 31.984506],
          [-83.205739, 31.900325],
          [-83.177469, 31.847857]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16005",
      state: "16",
      county: "005",
      name: "Bannock County",
      censusArea: 1111.988,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.875573, 42.416335],
          [-111.897244, 42.256646],
          [-112.126286, 42.28533],
          [-112.190692, 42.3512],
          [-112.327029, 42.323854],
          [-112.297202, 42.454097],
          [-112.419995, 42.502857],
          [-112.37961, 42.646225],
          [-112.496937, 42.733053],
          [-112.515974, 42.910003],
          [-112.558535, 42.961101],
          [-112.737583, 42.905549],
          [-112.750503, 42.951443],
          [-112.676152, 43.022012],
          [-112.063055, 43.022364],
          [-112.082064, 42.950516],
          [-112.149792, 42.895603],
          [-112.120692, 42.831618],
          [-112.14114, 42.763051],
          [-112.105476, 42.699195],
          [-111.969449, 42.718039],
          [-111.875573, 42.416335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16027",
      state: "16",
      county: "027",
      name: "Canyon County",
      censusArea: 587.37,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.026623, 43.680865],
          [-117.026586, 43.683001],
          [-117.026825, 43.706193],
          [-117.026725, 43.714815],
          [-117.026841, 43.732905],
          [-117.026651, 43.733935],
          [-117.026634, 43.808104],
          [-116.996504, 43.864714],
          [-116.982482, 43.872799],
          [-116.979186, 43.879973],
          [-116.85322, 43.7926],
          [-116.712672, 43.807161],
          [-116.707332, 43.807279],
          [-116.512559, 43.807216],
          [-116.513123, 43.634183],
          [-116.473492, 43.634103],
          [-116.473713, 43.459216],
          [-116.513364, 43.45932],
          [-116.511722, 43.290253],
          [-116.780671, 43.477217],
          [-116.841528, 43.593168],
          [-117.026623, 43.680865]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16039",
      state: "16",
      county: "039",
      name: "Elmore County",
      censusArea: 3074.738,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.266168, 43.112553],
          [-115.978695, 43.113421],
          [-115.974946, 43.365081],
          [-115.975767, 43.591338],
          [-115.698219, 43.665088],
          [-115.645864, 43.700272],
          [-115.59297, 43.798087],
          [-115.54754, 43.792655],
          [-115.513842, 43.880723],
          [-115.287258, 43.935501],
          [-115.197061, 44.068328],
          [-115.230685, 44.098685],
          [-115.182416, 44.092262],
          [-115.147722, 44.002364],
          [-115.067174, 43.94035],
          [-114.99075, 43.949725],
          [-114.983747, 43.945988],
          [-114.978209, 43.946841],
          [-114.971258, 43.938134],
          [-114.989384, 43.858611],
          [-115.042177, 43.745451],
          [-115.008925, 43.697398],
          [-115.083089, 43.604292],
          [-115.08648, 43.198436],
          [-115.086808, 42.914691],
          [-115.037262, 42.911931],
          [-115.037716, 42.76848],
          [-115.454314, 42.767732],
          [-115.44157, 42.930904],
          [-115.60373, 42.937249],
          [-115.641066, 42.95536],
          [-115.76991, 42.938948],
          [-115.926546, 43.001526],
          [-115.965669, 42.943725],
          [-116.012092, 42.949917],
          [-116.266168, 43.112553]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16063",
      state: "16",
      county: "063",
      name: "Lincoln County",
      censusArea: 1201.405,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.714701, 43.20003],
          [-113.713928, 42.849733],
          [-113.763862, 42.764508],
          [-113.932904, 42.765032],
          [-114.049868, 42.765121],
          [-114.04987, 42.808521],
          [-114.345659, 42.809055],
          [-114.365366, 42.85079],
          [-114.594375, 42.850826],
          [-114.594685, 43.198302],
          [-114.374877, 43.199603],
          [-113.714701, 43.20003]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16075",
      state: "16",
      county: "075",
      name: "Payette County",
      censusArea: 406.867,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.961535, 43.918388],
          [-116.935301, 43.988616],
          [-116.931719, 44.102474],
          [-116.897145, 44.152537],
          [-116.452012, 44.151417],
          [-116.452526, 44.065345],
          [-116.532176, 44.06606],
          [-116.572245, 43.980334],
          [-116.712591, 43.981259],
          [-116.712672, 43.807161],
          [-116.85322, 43.7926],
          [-116.979186, 43.879973],
          [-116.961535, 43.918388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17001",
      state: "17",
      county: "001",
      name: "Adams County",
      censusArea: 855.202,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.365396, 39.777266],
          [-91.432919, 39.840554],
          [-91.446385, 39.870394],
          [-91.455887, 39.945538],
          [-91.460287, 39.980333],
          [-91.494865, 40.037421],
          [-91.510322, 40.127994],
          [-91.512974, 40.181062],
          [-91.510332, 40.201142],
          [-91.258828, 40.197299],
          [-90.911969, 40.193088],
          [-90.913616, 40.104452],
          [-90.916609, 39.845075],
          [-90.916071, 39.757168],
          [-91.147678, 39.75707],
          [-91.265848, 39.757258],
          [-91.365125, 39.758723],
          [-91.367962, 39.759124],
          [-91.365396, 39.777266]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17017",
      state: "17",
      county: "017",
      name: "Cass County",
      censusArea: 375.818,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.355198, 40.124245],
          [-90.286493, 40.055023],
          [-90.196609, 40.052499],
          [-89.994724, 40.108373],
          [-89.994506, 39.901925],
          [-89.994405, 39.87286],
          [-90.583534, 39.87675],
          [-90.536721, 39.923027],
          [-90.513747, 39.987891],
          [-90.433523, 40.024001],
          [-90.355198, 40.124245]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30047",
      state: "30",
      county: "047",
      name: "Lake County",
      censusArea: 1490.15,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.187383, 47.138192],
          [-114.203293, 47.355899],
          [-114.288557, 47.355875],
          [-114.35308, 47.38985],
          [-114.301457, 47.49705],
          [-114.383453, 47.483254],
          [-114.321479, 47.595551],
          [-114.58329, 47.60168],
          [-114.604326, 47.789744],
          [-114.477186, 47.789841],
          [-114.491992, 47.961906],
          [-114.038545, 47.96255],
          [-114.078146, 48.023454],
          [-114.028314, 48.051911],
          [-113.885757, 48.051129],
          [-113.841867, 47.978185],
          [-113.747738, 47.953079],
          [-113.667442, 47.890291],
          [-113.681245, 47.794662],
          [-113.604004, 47.72153],
          [-113.633957, 47.599861],
          [-113.948763, 47.600517],
          [-113.929999, 47.598483],
          [-113.914926, 47.500299],
          [-113.951187, 47.476007],
          [-113.870895, 47.392118],
          [-113.886455, 47.318622],
          [-113.821126, 47.268599],
          [-113.821994, 47.181622],
          [-114.187383, 47.138192]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31123",
      state: "31",
      county: "123",
      name: "Morrill County",
      censusArea: 1423.838,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.369024, 41.437655],
          [-103.370391, 41.69921],
          [-103.363337, 42.00293],
          [-102.697835, 42.004843],
          [-102.677582, 42.005282],
          [-102.66487, 41.743457],
          [-102.636476, 41.743139],
          [-102.636695, 41.439053],
          [-103.369024, 41.437655]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31127",
      state: "31",
      county: "127",
      name: "Nemaha County",
      censusArea: 407.382,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.066736, 40.262038],
          [-96.067012, 40.523148],
          [-95.709974, 40.523798],
          [-95.697281, 40.536985],
          [-95.696673, 40.545137],
          [-95.659281, 40.519808],
          [-95.681409, 40.49082],
          [-95.692665, 40.483022],
          [-95.68728, 40.465059],
          [-95.622684, 40.342323],
          [-95.553292, 40.291158],
          [-95.550966, 40.285947],
          [-95.552314, 40.264443],
          [-95.552473, 40.261904],
          [-96.010176, 40.261482],
          [-96.012628, 40.261449],
          [-96.066736, 40.262038]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31143",
      state: "31",
      county: "143",
      name: "Polk County",
      censusArea: 438.345,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.368186, 41.396416],
          [-97.368118, 41.046947],
          [-97.826287, 41.046581],
          [-97.827964, 41.047315],
          [-97.828243, 41.17375],
          [-97.598253, 41.333119],
          [-97.368186, 41.396416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31153",
      state: "31",
      county: "153",
      name: "Sarpy County",
      censusArea: 238.99,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.92793, 41.0588],
          [-96.056256, 41.065356],
          [-96.207648, 40.995628],
          [-96.319191, 41.044998],
          [-96.327399, 41.189999],
          [-96.158333, 41.190762],
          [-95.923219, 41.191046],
          [-95.92319, 41.190998],
          [-95.91459, 41.185098],
          [-95.852788, 41.165398],
          [-95.853396, 41.16028],
          [-95.866289, 41.051731],
          [-95.889215, 41.058443],
          [-95.92793, 41.0588]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31167",
      state: "31",
      county: "167",
      name: "Stanton County",
      censusArea: 427.949,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.019911, 41.74298],
          [-97.252425, 41.743085],
          [-97.36807, 41.743207],
          [-97.368404, 42.090922],
          [-97.019359, 42.090577],
          [-97.019911, 41.74298]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31179",
      state: "31",
      county: "179",
      name: "Wayne County",
      censusArea: 442.915,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.251888, 42.351626],
          [-97.017789, 42.351347],
          [-97.01773, 42.264453],
          [-96.822497, 42.264292],
          [-96.82367, 42.090411],
          [-97.019359, 42.090577],
          [-97.368404, 42.090922],
          [-97.367724, 42.351861],
          [-97.251888, 42.351626]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31185",
      state: "31",
      county: "185",
      name: "York County",
      censusArea: 572.51,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.82478, 40.698537],
          [-97.825723, 40.698555],
          [-97.826287, 41.046581],
          [-97.368118, 41.046947],
          [-97.368401, 40.698625],
          [-97.82478, 40.698537]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32011",
      state: "32",
      county: "011",
      name: "Eureka County",
      censusArea: 4175.684,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.834969, 40.127842],
          [-115.813476, 39.593521],
          [-115.898339, 39.45389],
          [-115.907659, 39.464432],
          [-115.907071, 39.161848],
          [-116.600946, 39.161463],
          [-116.597109, 39.176241],
          [-116.590238, 39.70644],
          [-116.596815, 40.94646],
          [-116.587104, 40.947021],
          [-116.586786, 41.000398],
          [-116.158155, 40.999912],
          [-116.157834, 40.666389],
          [-116.000897, 40.127376],
          [-115.834969, 40.127842]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32027",
      state: "32",
      county: "027",
      name: "Pershing County",
      censusArea: 6036.563,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.225176, 39.999385],
          [-119.339296, 39.998609],
          [-119.338504, 40.517805],
          [-119.313037, 40.518314],
          [-119.309598, 40.960378],
          [-118.786705, 40.96085],
          [-118.786738, 40.855929],
          [-117.64422, 40.857824],
          [-117.644399, 40.684343],
          [-117.301168, 40.682348],
          [-117.300597, 40.526136],
          [-117.541749, 40.001034],
          [-117.7327, 40.002466],
          [-117.734782, 40.002168],
          [-117.736241, 40.002464],
          [-119.225176, 39.999385]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33005",
      state: "33",
      county: "005",
      name: "Cheshire County",
      censusArea: 706.66,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.001034, 42.91951],
          [-71.928811, 42.712234],
          [-72.078463, 42.716364],
          [-72.111068, 42.717264],
          [-72.282968, 42.722008],
          [-72.412011, 42.725569],
          [-72.451257, 42.726653],
          [-72.458519, 42.726853],
          [-72.460195, 42.732066],
          [-72.478453, 42.762296],
          [-72.491122, 42.772465],
          [-72.509506, 42.781183],
          [-72.542784, 42.808482],
          [-72.555693, 42.85638],
          [-72.555768, 42.858153],
          [-72.556112, 42.866252],
          [-72.556214, 42.86695],
          [-72.443762, 43.006245],
          [-72.432661, 43.114077],
          [-72.433097, 43.120021],
          [-72.441904, 43.136615],
          [-72.4521, 43.161414],
          [-72.156548, 43.181962],
          [-72.178457, 43.131955],
          [-72.038149, 43.128807],
          [-72.062374, 42.944837],
          [-72.001034, 42.91951]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33017",
      state: "33",
      county: "017",
      name: "Strafford County",
      censusArea: 368.975,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.037296, 43.529689],
          [-70.958958, 43.537634],
          [-70.962556, 43.53431],
          [-70.954755, 43.509802],
          [-70.963742, 43.476248],
          [-70.964542, 43.473262],
          [-70.964433, 43.473276],
          [-70.986812, 43.414264],
          [-70.987733, 43.391513],
          [-70.984305, 43.376814],
          [-70.984335, 43.376128],
          [-70.967229, 43.343777],
          [-70.952356, 43.333983],
          [-70.930783, 43.329569],
          [-70.916751, 43.317731],
          [-70.91246, 43.308289],
          [-70.909805, 43.306682],
          [-70.86323, 43.265109],
          [-70.863231, 43.265098],
          [-70.839213, 43.251224],
          [-70.839717, 43.250393],
          [-70.83365, 43.242868],
          [-70.828022, 43.241597],
          [-70.826711, 43.241301],
          [-70.825071, 43.24093],
          [-70.823309, 43.240343],
          [-70.822959, 43.240187],
          [-70.817865, 43.237911],
          [-70.817773, 43.237408],
          [-70.816232, 43.234997],
          [-70.828301, 43.186685],
          [-70.829174, 43.180365],
          [-70.8338, 43.146886],
          [-70.8281, 43.129086],
          [-70.819549, 43.123231],
          [-70.861535, 43.125021],
          [-70.86103, 43.095602],
          [-70.875486, 43.080457],
          [-71.072984, 43.081748],
          [-71.026688, 43.138086],
          [-71.247215, 43.274201],
          [-71.235828, 43.284717],
          [-71.160134, 43.35586],
          [-71.192207, 43.512589],
          [-71.163626, 43.53937],
          [-71.037296, 43.529689]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34027",
      state: "34",
      county: "027",
      name: "Morris County",
      censusArea: 460.177,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.767874, 40.911823],
          [-74.65303, 40.921623],
          [-74.536002, 41.058497],
          [-74.504928, 41.084805],
          [-74.502892, 41.085904],
          [-74.334605, 41.003719],
          [-74.315226, 41.001991],
          [-74.309838, 40.998484],
          [-74.300327, 41.002743],
          [-74.270702, 40.89692],
          [-74.334316, 40.905302],
          [-74.371923, 40.739229],
          [-74.461231, 40.673397],
          [-74.47002, 40.667934],
          [-74.484289, 40.666065],
          [-74.484118, 40.663387],
          [-74.503863, 40.659627],
          [-74.510502, 40.650295],
          [-74.523627, 40.649702],
          [-74.534387, 40.667256],
          [-74.513608, 40.697339],
          [-74.526846, 40.710872],
          [-74.528827, 40.728133],
          [-74.548999, 40.740994],
          [-74.555771, 40.758005],
          [-74.724418, 40.719455],
          [-74.889753, 40.787832],
          [-74.767874, 40.911823]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34037",
      state: "34",
      county: "037",
      name: "Sussex County",
      censusArea: 519.014,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.969434, 41.096074],
          [-74.974338, 41.103912],
          [-74.923169, 41.138146],
          [-74.882139, 41.180836],
          [-74.867287, 41.208754],
          [-74.864356, 41.224854],
          [-74.867267, 41.228861],
          [-74.861678, 41.241575],
          [-74.838366, 41.277286],
          [-74.830057, 41.2872],
          [-74.795847, 41.318951],
          [-74.76173, 41.336398],
          [-74.694914, 41.357423],
          [-74.457584, 41.248225],
          [-74.392098, 41.215594],
          [-74.365664, 41.2034],
          [-74.365897, 41.202894],
          [-74.502892, 41.085904],
          [-74.504928, 41.084805],
          [-74.536002, 41.058497],
          [-74.65303, 40.921623],
          [-74.767874, 40.911823],
          [-74.78921, 40.931298],
          [-74.966349, 41.094003],
          [-74.967389, 41.094049],
          [-74.969434, 41.096074]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41023",
      state: "41",
      county: "023",
      name: "Grant County",
      censusArea: 4528.541,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.655586, 44.306964],
          [-119.652128, 44.823171],
          [-119.671987, 44.994424],
          [-119.666708, 44.993525],
          [-119.163882, 44.995887],
          [-118.519063, 44.995956],
          [-118.244751, 44.958256],
          [-118.236652, 44.861565],
          [-118.296153, 44.862664],
          [-118.285153, 44.751172],
          [-118.519257, 44.706374],
          [-118.504257, 44.665575],
          [-118.372953, 44.645376],
          [-118.30615, 44.588979],
          [-118.40845, 44.452782],
          [-118.409749, 44.378683],
          [-118.46905, 44.334583],
          [-118.497249, 44.255084],
          [-118.23214, 44.256083],
          [-118.227435, 44.039981],
          [-118.586746, 44.040078],
          [-118.81625, 44.047678],
          [-118.816649, 43.959975],
          [-119.657577, 43.959051],
          [-119.655586, 44.306964]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41047",
      state: "41",
      county: "047",
      name: "Marion County",
      censusArea: 1182.326,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.734167, 44.885782],
          [-121.733782, 44.88174],
          [-121.745582, 44.87544],
          [-121.753182, 44.86124],
          [-121.751131, 44.85699],
          [-121.742481, 44.85314],
          [-121.742081, 44.84534],
          [-121.749381, 44.83354],
          [-121.75948, 44.82564],
          [-121.794077, 44.68394],
          [-122.033233, 44.685678],
          [-122.154047, 44.719762],
          [-122.230559, 44.697867],
          [-122.288446, 44.753065],
          [-122.478373, 44.755551],
          [-122.619769, 44.78895],
          [-122.798369, 44.791564],
          [-123.006847, 44.686898],
          [-123.061509, 44.747952],
          [-123.143556, 44.74967],
          [-123.092503, 44.813538],
          [-123.178687, 44.833551],
          [-123.113081, 44.928577],
          [-123.039507, 44.951741],
          [-123.069955, 45.075108],
          [-122.996959, 45.11979],
          [-123.040165, 45.221961],
          [-122.962688, 45.284254],
          [-122.900435, 45.257063],
          [-122.849723, 45.259654],
          [-122.738988, 45.259532],
          [-122.788386, 45.130129],
          [-122.508396, 44.919805],
          [-122.395327, 44.885734],
          [-121.734167, 44.885782]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41061",
      state: "41",
      county: "061",
      name: "Union County",
      censusArea: 2036.606,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.519063, 44.995956],
          [-118.548568, 45.080756],
          [-118.546678, 45.196951],
          [-118.655777, 45.196855],
          [-118.696782, 45.257653],
          [-118.697684, 45.345153],
          [-118.428268, 45.355151],
          [-118.428469, 45.428951],
          [-118.197957, 45.427153],
          [-118.198158, 45.470453],
          [-118.117053, 45.470754],
          [-118.117153, 45.688498],
          [-118.066823, 45.688365],
          [-118.045003, 45.817098],
          [-117.973666, 45.816978],
          [-117.972922, 45.860586],
          [-117.747313, 45.861012],
          [-117.747101, 45.773346],
          [-117.788236, 45.773726],
          [-117.787637, 45.689597],
          [-117.727149, 45.617272],
          [-117.727271, 45.514705],
          [-117.683957, 45.515143],
          [-117.663557, 45.365757],
          [-117.583018, 45.338291],
          [-117.521904, 45.267762],
          [-117.476987, 45.165315],
          [-117.267162, 45.165858],
          [-117.266572, 45.080805],
          [-117.563138, 45.079588],
          [-117.563714, 44.992788],
          [-117.777504, 44.992161],
          [-117.84343, 45.058477],
          [-117.969185, 44.995803],
          [-118.130309, 45.045124],
          [-118.244751, 44.958256],
          [-118.519063, 44.995956]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42009",
      state: "42",
      county: "009",
      name: "Bedford County",
      censusArea: 1012.296,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.620383, 40.327025],
          [-78.447895, 40.244395],
          [-78.434288, 40.302301],
          [-78.288764, 40.242107],
          [-78.257305, 40.297883],
          [-78.236302, 40.224559],
          [-78.134863, 40.165131],
          [-78.299003, 39.8255],
          [-78.380599, 39.722554],
          [-78.808387, 39.722726],
          [-78.757241, 39.823484],
          [-78.763206, 40.057005],
          [-78.658409, 40.242933],
          [-78.620383, 40.327025]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48355",
      state: "48",
      county: "355",
      name: "Nueces County",
      censusArea: 838.482,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.377166, 27.875181],
          [-97.361246, 27.849891],
          [-97.379042, 27.837867],
          [-97.391764, 27.813948],
          [-97.393291, 27.782905],
          [-97.386166, 27.76662],
          [-97.368355, 27.741683],
          [-97.34698, 27.725398],
          [-97.316446, 27.712676],
          [-97.253955, 27.696696],
          [-97.259957, 27.679597],
          [-97.266064, 27.678579],
          [-97.296598, 27.613947],
          [-97.298634, 27.604787],
          [-97.297616, 27.59868],
          [-97.294054, 27.5941],
          [-97.302196, 27.585957],
          [-97.321535, 27.571199],
          [-97.325118, 27.560927],
          [-97.840678, 27.558358],
          [-97.942146, 27.635932],
          [-97.934274, 27.885202],
          [-97.798517, 27.995659],
          [-97.805765, 27.934287],
          [-97.691074, 27.917353],
          [-97.589473, 27.856101],
          [-97.377166, 27.875181]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48363",
      state: "48",
      county: "363",
      name: "Palo Pinto County",
      censusArea: 951.791,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.576256, 32.515163],
          [-98.575616, 32.954349],
          [-98.427211, 32.9523],
          [-98.426553, 33.007913],
          [-98.056094, 33.003332],
          [-98.066836, 32.558822],
          [-98.068545, 32.511626],
          [-98.475177, 32.513039],
          [-98.495775, 32.513026],
          [-98.573016, 32.515261],
          [-98.576256, 32.515163]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48373",
      state: "48",
      county: "373",
      name: "Polk County",
      censusArea: 1057.093,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.061113, 30.941302],
          [-94.939245, 31.041981],
          [-94.842947, 31.146578],
          [-94.561943, 31.058952],
          [-94.657992, 31.012007],
          [-94.545717, 30.526977],
          [-94.537926, 30.491059],
          [-94.732732, 30.490066],
          [-94.777341, 30.489002],
          [-94.849414, 30.493553],
          [-94.830429, 30.541613],
          [-95.028628, 30.578485],
          [-95.054602, 30.680173],
          [-95.135384, 30.693816],
          [-95.131205, 30.781703],
          [-95.20018, 30.824566],
          [-95.061113, 30.941302]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48389",
      state: "48",
      county: "389",
      name: "Reeves County",
      censusArea: 2635.375,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.980179, 32.000125],
          [-103.885584, 31.870373],
          [-103.829385, 31.887992],
          [-103.797232, 31.770187],
          [-103.625726, 31.705032],
          [-103.610887, 31.651802],
          [-103.57489, 31.632662],
          [-103.530927, 31.642284],
          [-103.459426, 31.428494],
          [-103.01104, 31.371306],
          [-103.585084, 30.76647],
          [-104.102351, 31.105203],
          [-104.024521, 32.00001],
          [-103.980179, 32.000125]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48405",
      state: "48",
      county: "405",
      name: "San Augustine County",
      censusArea: 530.657,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.326616, 31.224754],
          [-94.339773, 31.24082],
          [-94.311042, 31.589252],
          [-94.395361, 31.638752],
          [-94.399091, 31.653596],
          [-94.090717, 31.620609],
          [-93.984461, 31.569419],
          [-93.983239, 31.466114],
          [-94.044154, 31.409979],
          [-94.039034, 31.134271],
          [-94.129632, 31.09928],
          [-94.326616, 31.224754]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48413",
      state: "48",
      county: "413",
      name: "Schleicher County",
      censusArea: 1310.63,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.116234, 30.710366],
          [-100.849229, 30.706222],
          [-100.960587, 30.706071],
          [-100.962176, 31.08249],
          [-100.688764, 31.086576],
          [-100.115216, 31.087994],
          [-100.116234, 30.710366]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48421",
      state: "48",
      county: "421",
      name: "Sherman County",
      censusArea: 923.035,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.162463, 36.500326],
          [-102.032339, 36.500066],
          [-101.826565, 36.499654],
          [-101.826498, 36.499535],
          [-101.78811, 36.499678],
          [-101.783359, 36.499709],
          [-101.781987, 36.499718],
          [-101.78061, 36.499727],
          [-101.779435, 36.499734],
          [-101.709314, 36.499722],
          [-101.698685, 36.499508],
          [-101.653708, 36.499573],
          [-101.649966, 36.499573],
          [-101.623915, 36.499528],
          [-101.623466, 36.055405],
          [-102.163015, 36.055249],
          [-102.162771, 36.497902],
          [-102.162463, 36.500326]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48439",
      state: "48",
      county: "439",
      name: "Tarrant County",
      censusArea: 863.608,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.550582, 32.555391],
          [-97.550368, 32.580323],
          [-97.544181, 32.994177],
          [-97.414843, 32.992029],
          [-97.39848, 32.990839],
          [-97.397205, 32.990747],
          [-97.37994, 32.990144],
          [-97.29423, 32.99026],
          [-97.167261, 32.989802],
          [-97.032287, 32.989324],
          [-97.032687, 32.920614],
          [-97.038385, 32.548662],
          [-97.05262, 32.548907],
          [-97.086834, 32.549428],
          [-97.550582, 32.555391]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48455",
      state: "48",
      county: "455",
      name: "Trinity County",
      censusArea: 693.608,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.842947, 31.146578],
          [-94.939245, 31.041981],
          [-95.061113, 30.941302],
          [-95.20018, 30.824566],
          [-95.252385, 30.906719],
          [-95.327482, 30.859537],
          [-95.434335, 30.937405],
          [-95.434711, 31.04663],
          [-95.434781, 31.058098],
          [-94.95811, 31.38693],
          [-94.909502, 31.337059],
          [-94.842947, 31.146578]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48471",
      state: "48",
      county: "471",
      name: "Walker County",
      censusArea: 784.169,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.434711, 31.04663],
          [-95.434335, 30.937405],
          [-95.327482, 30.859537],
          [-95.359156, 30.504369],
          [-95.598971, 30.509002],
          [-95.83024, 30.630284],
          [-95.863059, 30.864125],
          [-95.617872, 30.930418],
          [-95.434781, 31.058098],
          [-95.434711, 31.04663]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13083",
      state: "13",
      county: "083",
      name: "Dade County",
      censusArea: 173.981,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.605165, 34.984678],
          [-85.474472, 34.983972],
          [-85.363919, 34.983375],
          [-85.450561, 34.831677],
          [-85.450409, 34.759241],
          [-85.534423, 34.623789],
          [-85.541264, 34.656701],
          [-85.541267, 34.656783],
          [-85.552454, 34.708138],
          [-85.552482, 34.708321],
          [-85.583145, 34.860371],
          [-85.595163, 34.924171],
          [-85.595191, 34.924331],
          [-85.605165, 34.984678]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13089",
      state: "13",
      county: "089",
      name: "De Kalb County",
      censusArea: 267.582,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.254149, 33.647045],
          [-84.281273, 33.647411],
          [-84.282619, 33.647009],
          [-84.350224, 33.647908],
          [-84.349799, 33.664035],
          [-84.348325, 33.852503],
          [-84.348138, 33.857692],
          [-84.348225, 33.859502],
          [-84.348525, 33.861903],
          [-84.348125, 33.864903],
          [-84.348325, 33.867903],
          [-84.348125, 33.879203],
          [-84.348425, 33.881902],
          [-84.348224, 33.8929],
          [-84.348225, 33.904802],
          [-84.348025, 33.918302],
          [-84.347825, 33.918902],
          [-84.347925, 33.927001],
          [-84.347823, 33.938017],
          [-84.276822, 33.9577],
          [-84.275722, 33.954201],
          [-84.271922, 33.9559],
          [-84.266306, 33.947577],
          [-84.272216, 33.944853],
          [-84.265337, 33.932576],
          [-84.259011, 33.91882],
          [-84.256022, 33.914401],
          [-84.250413, 33.910812],
          [-84.23222, 33.902002],
          [-84.21663, 33.884976],
          [-84.20472, 33.877003],
          [-84.203519, 33.873003],
          [-84.187005, 33.865515],
          [-84.179418, 33.864403],
          [-84.172139, 33.857516],
          [-84.023713, 33.752808],
          [-84.056614, 33.726608],
          [-84.104334, 33.636025],
          [-84.184143, 33.646157],
          [-84.223952, 33.646572],
          [-84.224235, 33.630657],
          [-84.245453, 33.63073],
          [-84.254149, 33.647045]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13097",
      state: "13",
      county: "097",
      name: "Douglas County",
      censusArea: 200.067,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.769935, 33.784704],
          [-84.741348, 33.788568],
          [-84.725477, 33.788579],
          [-84.724122, 33.791439],
          [-84.724139, 33.80617],
          [-84.619892, 33.805024],
          [-84.578132, 33.743507],
          [-84.586826, 33.729114],
          [-84.594332, 33.729007],
          [-84.601732, 33.724408],
          [-84.608032, 33.712908],
          [-84.621232, 33.704508],
          [-84.632131, 33.700312],
          [-84.630117, 33.693116],
          [-84.752735, 33.63021],
          [-84.763097, 33.614211],
          [-84.775591, 33.609662],
          [-84.805655, 33.58642],
          [-84.808934, 33.574085],
          [-84.905788, 33.573378],
          [-84.902546, 33.661066],
          [-84.901688, 33.780703],
          [-84.879151, 33.774758],
          [-84.832708, 33.776019],
          [-84.832705, 33.778522],
          [-84.799853, 33.779909],
          [-84.795109, 33.779809],
          [-84.791993, 33.781162],
          [-84.769935, 33.784704]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13105",
      state: "13",
      county: "105",
      name: "Elbert County",
      censusArea: 351.055,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.741491, 34.208709],
          [-82.731881, 34.178363],
          [-82.717507, 34.150504],
          [-82.70414, 34.141007],
          [-82.67732, 34.131657],
          [-82.668113, 34.12016],
          [-82.659077, 34.103544],
          [-82.658561, 34.103118],
          [-82.654019, 34.100346],
          [-82.652175, 34.099704],
          [-82.641252, 34.088914],
          [-82.640701, 34.088341],
          [-82.597079, 34.01239],
          [-82.564582, 33.95581],
          [-82.564483, 33.955744],
          [-82.645451, 33.984195],
          [-82.779506, 33.971124],
          [-82.976294, 34.043219],
          [-83.102074, 34.173794],
          [-83.078004, 34.223606],
          [-82.980905, 34.211505],
          [-82.766078, 34.293509],
          [-82.755028, 34.276067],
          [-82.74198, 34.230196],
          [-82.743461, 34.227343],
          [-82.744415, 34.224913],
          [-82.74192, 34.210063],
          [-82.741491, 34.208709]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13111",
      state: "13",
      county: "111",
      name: "Fannin County",
      censusArea: 386.723,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.621483, 34.988329],
          [-84.509886, 34.98801],
          [-84.509052, 34.988033],
          [-84.394903, 34.98803],
          [-84.393935, 34.988068],
          [-84.321869, 34.988408],
          [-84.129555, 34.987504],
          [-84.129455, 34.987504],
          [-84.178672, 34.946497],
          [-84.107422, 34.886896],
          [-84.093193, 34.801411],
          [-84.158035, 34.648243],
          [-84.188557, 34.602692],
          [-84.189026, 34.608109],
          [-84.196754, 34.617924],
          [-84.314653, 34.807328],
          [-84.429093, 34.855036],
          [-84.618637, 34.855398],
          [-84.621483, 34.988329]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13121",
      state: "13",
      county: "121",
      name: "Fulton County",
      censusArea: 526.635,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.752735, 33.63021],
          [-84.630117, 33.693116],
          [-84.632131, 33.700312],
          [-84.621232, 33.704508],
          [-84.608032, 33.712908],
          [-84.601732, 33.724408],
          [-84.594332, 33.729007],
          [-84.586826, 33.729114],
          [-84.578132, 33.743507],
          [-84.480134, 33.817319],
          [-84.442708, 33.901543],
          [-84.383027, 33.9638],
          [-84.418927, 34.073298],
          [-84.328263, 34.186144],
          [-84.258743, 34.185909],
          [-84.258934, 34.109539],
          [-84.200373, 34.090118],
          [-84.097692, 34.050654],
          [-84.276822, 33.9577],
          [-84.347823, 33.938017],
          [-84.347925, 33.927001],
          [-84.347825, 33.918902],
          [-84.348025, 33.918302],
          [-84.348225, 33.904802],
          [-84.348224, 33.8929],
          [-84.348425, 33.881902],
          [-84.348125, 33.879203],
          [-84.348325, 33.867903],
          [-84.348125, 33.864903],
          [-84.348525, 33.861903],
          [-84.348225, 33.859502],
          [-84.348138, 33.857692],
          [-84.348325, 33.852503],
          [-84.349799, 33.664035],
          [-84.350224, 33.647908],
          [-84.360224, 33.647909],
          [-84.365325, 33.647809],
          [-84.457726, 33.64887],
          [-84.458514, 33.608625],
          [-84.458627, 33.586456],
          [-84.458399, 33.572743],
          [-84.45863, 33.572107],
          [-84.458927, 33.565911],
          [-84.458924, 33.559759],
          [-84.458579, 33.556242],
          [-84.458665, 33.550933],
          [-84.60954, 33.502511],
          [-84.850713, 33.511457],
          [-84.808934, 33.574085],
          [-84.805655, 33.58642],
          [-84.775591, 33.609662],
          [-84.763097, 33.614211],
          [-84.752735, 33.63021]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08067",
      state: "08",
      county: "067",
      name: "La Plata County",
      censusArea: 1692.078,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.481737, 36.999973],
          [-108.288086, 36.999555],
          [-108.2884, 36.99952],
          [-108.320464, 36.999499],
          [-108.320721, 36.99951],
          [-108.379203, 36.999459],
          [-108.197197, 37.355475],
          [-108.038409, 37.451931],
          [-108.023006, 37.590999],
          [-107.970086, 37.639587],
          [-107.482179, 37.639501],
          [-107.482131, 37.422673],
          [-107.481737, 36.999973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08071",
      state: "08",
      county: "071",
      name: "Las Animas County",
      censusArea: 4772.672,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.007855, 36.996239],
          [-104.732031, 36.993447],
          [-104.73212, 36.993484],
          [-105.000554, 36.993264],
          [-105.1208, 36.995428],
          [-105.155042, 36.995339],
          [-105.154176, 37.293129],
          [-104.995505, 37.375551],
          [-104.748938, 37.407029],
          [-104.695075, 37.439915],
          [-104.696111, 37.485425],
          [-104.649229, 37.49532],
          [-104.548652, 37.5747],
          [-104.548417, 37.609424],
          [-104.45493, 37.740839],
          [-104.351109, 37.817488],
          [-104.061132, 37.734704],
          [-104.058196, 37.644049],
          [-103.404339, 37.643576],
          [-103.075938, 37.64342],
          [-103.086105, 36.999864],
          [-103.733247, 36.998016],
          [-103.734364, 36.998041],
          [-104.007855, 36.996239]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05095",
      state: "05",
      county: "095",
      name: "Monroe County",
      censusArea: 607.122,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.056276, 34.336605],
          [-91.107762, 34.353404],
          [-91.209161, 34.483221],
          [-91.37782, 34.56348],
          [-91.447795, 34.609185],
          [-91.446983, 34.667531],
          [-91.374765, 34.665964],
          [-91.36856, 34.912735],
          [-91.29479, 34.912452],
          [-91.293111, 34.985091],
          [-91.149842, 35.00247],
          [-91.152457, 34.911847],
          [-91.099961, 34.86712],
          [-91.102542, 34.734864],
          [-91.049474, 34.734184],
          [-91.051918, 34.645721],
          [-91.054774, 34.47088],
          [-91.000459, 34.469372],
          [-91.002693, 34.336353],
          [-91.056276, 34.336605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17025",
      state: "17",
      county: "025",
      name: "Clay County",
      censusArea: 468.316,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.253851, 38.599502],
          [-88.564109, 38.607093],
          [-88.698457, 38.606304],
          [-88.695165, 38.826299],
          [-88.693531, 38.914617],
          [-88.620486, 38.915303],
          [-88.361745, 38.910847],
          [-88.36176, 38.851949],
          [-88.258608, 38.847521],
          [-88.253851, 38.599502]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17039",
      state: "17",
      county: "039",
      name: "De Witt County",
      censusArea: 397.513,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.689271, 40.282148],
          [-88.574885, 40.281501],
          [-88.745164, 40.055191],
          [-89.144764, 40.048853],
          [-89.148764, 40.282038],
          [-88.689271, 40.282148]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17045",
      state: "17",
      county: "045",
      name: "Edgar County",
      censusArea: 623.374,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.533227, 39.883],
          [-87.533142, 39.810947],
          [-87.533056, 39.803922],
          [-87.533058, 39.796243],
          [-87.533066, 39.781743],
          [-87.532703, 39.664868],
          [-87.532444, 39.646102],
          [-87.532365, 39.646126],
          [-87.532196, 39.607306],
          [-87.532008, 39.564013],
          [-87.531939, 39.545853],
          [-87.531965, 39.526937],
          [-87.531692, 39.495516],
          [-87.531627, 39.491698],
          [-87.531663, 39.47712],
          [-87.531663, 39.47711],
          [-87.960179, 39.481309],
          [-87.9666, 39.685928],
          [-87.937645, 39.879803],
          [-87.533227, 39.883]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17061",
      state: "17",
      county: "061",
      name: "Greene County",
      censusArea: 543.019,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.48749, 39.175133],
          [-90.574615, 39.178623],
          [-90.603569, 39.117592],
          [-90.613694, 39.395775],
          [-90.593256, 39.484586],
          [-90.590605, 39.493675],
          [-90.582202, 39.510332],
          [-90.581055, 39.521728],
          [-90.301801, 39.520192],
          [-90.15375, 39.520315],
          [-90.150641, 39.346315],
          [-90.149173, 39.334597],
          [-90.148069, 39.261947],
          [-90.314071, 39.225053],
          [-90.370711, 39.174444],
          [-90.48749, 39.175133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17069",
      state: "17",
      county: "069",
      name: "Hardin County",
      censusArea: 177.528,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.133416, 37.574277],
          [-88.133393, 37.574235],
          [-88.133306, 37.574191],
          [-88.133235, 37.574154],
          [-88.121517, 37.568166],
          [-88.072242, 37.528826],
          [-88.063311, 37.515755],
          [-88.061311, 37.505332],
          [-88.061292, 37.505232],
          [-88.062562, 37.489385],
          [-88.083395, 37.473683],
          [-88.255193, 37.456748],
          [-88.281667, 37.452596],
          [-88.312585, 37.440591],
          [-88.330622, 37.429316],
          [-88.358436, 37.40486],
          [-88.408808, 37.425216],
          [-88.414895, 37.423461],
          [-88.415149, 37.424863],
          [-88.412112, 37.599912],
          [-88.375332, 37.599563],
          [-88.133416, 37.574277]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18049",
      state: "18",
      county: "049",
      name: "Fulton County",
      censusArea: 368.388,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.945561, 41.042876],
          [-85.9454, 41.000149],
          [-85.946436, 40.999195],
          [-86.168459, 40.996097],
          [-86.169012, 40.909836],
          [-86.468509, 40.909889],
          [-86.467046, 41.171442],
          [-86.077551, 41.172868],
          [-86.075939, 41.084878],
          [-85.945561, 41.042876]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18053",
      state: "18",
      county: "053",
      name: "Grant County",
      censusArea: 414.074,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.863641, 40.565525],
          [-85.864791, 40.651693],
          [-85.857147, 40.651364],
          [-85.638587, 40.653129],
          [-85.448825, 40.653607],
          [-85.448993, 40.651271],
          [-85.447014, 40.566929],
          [-85.44433, 40.37914],
          [-85.552303, 40.378666],
          [-85.578589, 40.379524],
          [-85.587859, 40.379757],
          [-85.862296, 40.378367],
          [-85.862117, 40.406889],
          [-85.863641, 40.565525]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18063",
      state: "18",
      county: "063",
      name: "Hendricks County",
      censusArea: 406.911,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.326576, 39.682896],
          [-86.326341, 39.632177],
          [-86.465605, 39.600769],
          [-86.65665, 39.601395],
          [-86.687406, 39.630437],
          [-86.695127, 39.864845],
          [-86.695071, 39.92277],
          [-86.326293, 39.924029],
          [-86.326576, 39.682896]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18075",
      state: "18",
      county: "075",
      name: "Jay County",
      censusArea: 383.905,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.804119, 40.352757],
          [-84.803917, 40.310115],
          [-84.803918, 40.310094],
          [-85.218758, 40.306706],
          [-85.220028, 40.375043],
          [-85.219901, 40.379034],
          [-85.206831, 40.379182],
          [-85.201146, 40.567242],
          [-85.068479, 40.56824],
          [-84.802265, 40.572212],
          [-84.802483, 40.528046],
          [-84.803068, 40.465388],
          [-84.804119, 40.352844],
          [-84.804119, 40.352757]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18093",
      state: "18",
      county: "093",
      name: "Lawrence County",
      censusArea: 449.168,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.573548, 38.992147],
          [-86.317474, 38.99087],
          [-86.280389, 38.991028],
          [-86.275281, 38.763795],
          [-86.308701, 38.688101],
          [-86.683102, 38.686561],
          [-86.682853, 38.904697],
          [-86.682277, 38.992404],
          [-86.573548, 38.992147]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18099",
      state: "18",
      county: "099",
      name: "Marshall County",
      censusArea: 443.629,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.05923, 41.435812],
          [-86.054047, 41.173147],
          [-86.077551, 41.172868],
          [-86.467046, 41.171442],
          [-86.466603, 41.432968],
          [-86.467108, 41.476459],
          [-86.059451, 41.479154],
          [-86.059052, 41.445236],
          [-86.059237, 41.442753],
          [-86.05923, 41.435812]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18113",
      state: "18",
      county: "113",
      name: "Noble County",
      censusArea: 410.843,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.653507, 41.436856],
          [-85.654274, 41.515695],
          [-85.654747, 41.523347],
          [-85.194084, 41.526437],
          [-85.192094, 41.264209],
          [-85.307781, 41.264158],
          [-85.53718, 41.266157],
          [-85.651928, 41.294776],
          [-85.653507, 41.436856]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18119",
      state: "18",
      county: "119",
      name: "Owen County",
      censusArea: 385.288,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.683002, 39.165746],
          [-87.054578, 39.168086],
          [-87.053646, 39.342621],
          [-86.942473, 39.342043],
          [-86.939981, 39.473345],
          [-86.68571, 39.470063],
          [-86.632206, 39.470214],
          [-86.630818, 39.346948],
          [-86.68561, 39.336043],
          [-86.683002, 39.165746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18133",
      state: "18",
      county: "133",
      name: "Putnam County",
      censusArea: 480.528,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.013062, 39.604787],
          [-87.009477, 39.866705],
          [-86.695127, 39.864845],
          [-86.687406, 39.630437],
          [-86.65665, 39.601395],
          [-86.68571, 39.470063],
          [-86.939981, 39.473345],
          [-87.014533, 39.473572],
          [-87.013062, 39.604787]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18145",
      state: "18",
      county: "145",
      name: "Shelby County",
      censusArea: 411.147,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.684515, 39.350049],
          [-85.854064, 39.349498],
          [-85.85746, 39.349312],
          [-85.952005, 39.347373],
          [-85.95322, 39.38352],
          [-85.952545, 39.435921],
          [-85.95122, 39.436053],
          [-85.9508, 39.49522],
          [-85.952523, 39.60915],
          [-85.95208, 39.638345],
          [-85.951721, 39.697136],
          [-85.633228, 39.698462],
          [-85.632525, 39.697504],
          [-85.629327, 39.45275],
          [-85.63063, 39.350205],
          [-85.684515, 39.350049]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18157",
      state: "18",
      county: "157",
      name: "Tippecanoe County",
      censusArea: 499.806,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.093676, 40.475745],
          [-87.095357, 40.562895],
          [-86.771293, 40.562082],
          [-86.695353, 40.561874],
          [-86.694665, 40.432156],
          [-86.695637, 40.214367],
          [-87.092563, 40.214806],
          [-87.092148, 40.366612],
          [-87.09278, 40.475075],
          [-87.093676, 40.475745]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35011",
      state: "35",
      county: "011",
      name: "De Baca County",
      censusArea: 2322.623,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.948839, 34.605058],
          [-103.945949, 34.082784],
          [-104.155841, 34.08332],
          [-104.156269, 33.995413],
          [-104.785964, 34.000057],
          [-104.786238, 34.087763],
          [-104.893141, 34.088304],
          [-104.89185, 34.346989],
          [-104.892023, 34.604426],
          [-104.444913, 34.604821],
          [-104.340132, 34.779062],
          [-104.129424, 34.779324],
          [-104.12888, 34.60522],
          [-103.948839, 34.605058]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30049",
      state: "30",
      county: "049",
      name: "Lewis And Clark County",
      censusArea: 3458.828,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.31061, 46.421006],
          [-112.298607, 46.625918],
          [-112.410855, 46.656711],
          [-112.41079, 46.699696],
          [-112.535313, 46.698527],
          [-112.535608, 46.834382],
          [-112.79532, 46.831772],
          [-112.796594, 47.177222],
          [-113.058866, 47.179369],
          [-113.059444, 47.488096],
          [-113.114863, 47.490564],
          [-113.144926, 47.596363],
          [-113.165906, 47.720729],
          [-113.080938, 47.749885],
          [-113.08667, 47.918109],
          [-112.984705, 47.953982],
          [-112.913256, 47.826216],
          [-112.91422, 47.694958],
          [-112.818574, 47.607849],
          [-112.55231, 47.652231],
          [-112.278406, 47.505955],
          [-112.047308, 47.516028],
          [-112.044923, 47.19271],
          [-111.789326, 47.128993],
          [-111.790092, 46.91393],
          [-111.658131, 46.913364],
          [-111.645393, 46.841937],
          [-111.497961, 46.762469],
          [-111.639261, 46.73818],
          [-111.631643, 46.569108],
          [-111.787875, 46.568998],
          [-112.029312, 46.569096],
          [-112.188399, 46.499211],
          [-112.17579, 46.456606],
          [-112.31061, 46.421006]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30053",
      state: "30",
      county: "053",
      name: "Lincoln County",
      censusArea: 3612.916,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.049353, 48.21546],
          [-116.049226, 48.502058],
          [-116.049023, 48.957947],
          [-116.049025, 48.958351],
          [-116.049193, 49.000912],
          [-115.251126, 48.999557],
          [-114.728125, 49.000623],
          [-114.691742, 48.842091],
          [-114.73248, 48.809234],
          [-114.611925, 48.751888],
          [-114.692445, 48.680372],
          [-114.639014, 48.658959],
          [-114.888676, 48.65849],
          [-114.888446, 48.573612],
          [-114.849699, 48.573549],
          [-114.847946, 48.226159],
          [-115.020079, 48.225954],
          [-115.010806, 48.017256],
          [-115.154475, 48.017703],
          [-115.185098, 47.919079],
          [-115.278707, 47.890261],
          [-115.525428, 47.908957],
          [-115.537646, 47.981705],
          [-115.63693, 48.074611],
          [-115.671869, 48.206171],
          [-115.737709, 48.263009],
          [-115.799976, 48.222967],
          [-116.007724, 48.158901],
          [-116.049353, 48.21546]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30075",
      state: "30",
      county: "075",
      name: "Powder River County",
      censusArea: 3297.298,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.038405, 45.000345],
          [-105.076607, 45.000347],
          [-105.848065, 45.000396],
          [-105.918995, 44.997771],
          [-105.928184, 44.993647],
          [-106.024814, 44.993688],
          [-106.263586, 44.993788],
          [-106.263682, 45.179792],
          [-106.279682, 45.179892],
          [-106.279478, 45.351796],
          [-106.235575, 45.351796],
          [-106.234263, 45.788401],
          [-106.191861, 45.7886],
          [-104.982036, 45.787009],
          [-104.987143, 45.352023],
          [-105.038235, 45.352052],
          [-105.038405, 45.000345]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30085",
      state: "30",
      county: "085",
      name: "Roosevelt County",
      censusArea: 2354.793,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.046913, 48.389429],
          [-104.046654, 48.374773],
          [-104.046371, 48.374154],
          [-104.046332, 48.34229],
          [-104.046039, 48.256761],
          [-104.045861, 48.255097],
          [-104.045645, 48.246179],
          [-104.045729, 48.244586],
          [-104.045692, 48.241415],
          [-104.04556, 48.193913],
          [-104.045424, 48.192473],
          [-104.045498, 48.176249],
          [-104.045399, 48.16439],
          [-104.04412, 47.996107],
          [-104.098921, 48.000019],
          [-104.14247, 48.055005],
          [-104.245936, 48.031005],
          [-104.544517, 48.126553],
          [-104.681633, 48.103323],
          [-104.822581, 48.118945],
          [-104.891321, 48.15069],
          [-104.990346, 48.066449],
          [-105.119117, 48.0596],
          [-105.177722, 48.1031],
          [-105.194742, 48.065669],
          [-105.2282, 48.09239],
          [-105.498036, 48.097903],
          [-105.647941, 48.077304],
          [-105.843797, 48.010314],
          [-105.843462, 48.21903],
          [-105.804345, 48.219179],
          [-105.804432, 48.563318],
          [-104.973354, 48.56319],
          [-104.757595, 48.563122],
          [-104.757861, 48.476556],
          [-104.62771, 48.476349],
          [-104.628048, 48.389239],
          [-104.046913, 48.389429]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31001",
      state: "31",
      county: "001",
      name: "Adams County",
      censusArea: 563.272,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.278103, 40.698291],
          [-98.27809, 40.350347],
          [-98.723948, 40.350391],
          [-98.724387, 40.689772],
          [-98.724412, 40.698891],
          [-98.72143, 40.698903],
          [-98.282851, 40.698284],
          [-98.278103, 40.698291]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31005",
      state: "31",
      county: "005",
      name: "Arthur County",
      censusArea: 715.355,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.985523, 41.74229],
          [-101.42592, 41.743059],
          [-101.406466, 41.7432],
          [-101.40726, 41.395416],
          [-101.98463, 41.394712],
          [-101.985523, 41.74229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31017",
      state: "31",
      county: "017",
      name: "Brown County",
      censusArea: 1221.333,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.198478, 42.846102],
          [-100.133978, 42.807819],
          [-99.677684, 42.730903],
          [-99.662379, 42.086009],
          [-99.686959, 42.086075],
          [-100.167605, 42.085848],
          [-100.183167, 42.43343],
          [-100.18327, 42.781005],
          [-100.198478, 42.846102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31027",
      state: "31",
      county: "027",
      name: "Cedar County",
      censusArea: 740.312,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.163082, 42.793385],
          [-97.13746, 42.782313],
          [-97.05218, 42.770187],
          [-97.026103, 42.762625],
          [-97.015691, 42.758697],
          [-97.015116, 42.698797],
          [-97.017789, 42.351347],
          [-97.251888, 42.351626],
          [-97.367724, 42.351861],
          [-97.36759, 42.438775],
          [-97.485298, 42.438578],
          [-97.485376, 42.854838],
          [-97.42319, 42.861168],
          [-97.328511, 42.859501],
          [-97.257089, 42.853854],
          [-97.218269, 42.829561],
          [-97.190135, 42.805076],
          [-97.163082, 42.793385]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31041",
      state: "31",
      county: "041",
      name: "Custer County",
      censusArea: 2575.522,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.22183, 41.740792],
          [-99.212773, 41.740644],
          [-99.212016, 41.740613],
          [-99.203261, 41.39414],
          [-99.207564, 41.047003],
          [-99.425986, 41.046471],
          [-100.224235, 41.046597],
          [-100.238155, 41.046536],
          [-100.236106, 41.393324],
          [-100.251098, 41.393298],
          [-100.251224, 41.739817],
          [-99.686834, 41.740326],
          [-99.22183, 41.740792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31059",
      state: "31",
      county: "059",
      name: "Fillmore County",
      censusArea: 575.371,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.368688, 40.350392],
          [-97.370694, 40.350312],
          [-97.820816, 40.350545],
          [-97.821458, 40.350561],
          [-97.824114, 40.350541],
          [-97.82478, 40.698537],
          [-97.368401, 40.698625],
          [-97.368688, 40.350392]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31069",
      state: "31",
      county: "069",
      name: "Garden County",
      censusArea: 1704.283,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.06665, 42.009195],
          [-102.067918, 41.742099],
          [-101.985523, 41.74229],
          [-101.98463, 41.394712],
          [-102.055256, 41.395162],
          [-102.055535, 41.221547],
          [-102.610413, 41.221614],
          [-102.609386, 41.395115],
          [-102.636695, 41.439053],
          [-102.636476, 41.743139],
          [-102.66487, 41.743457],
          [-102.677582, 42.005282],
          [-102.06665, 42.009195]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31077",
      state: "31",
      county: "077",
      name: "Greeley County",
      censusArea: 569.811,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.291344, 41.480727],
          [-98.291419, 41.393894],
          [-98.74433, 41.394178],
          [-98.751558, 41.394084],
          [-98.752225, 41.740372],
          [-98.29576, 41.741178],
          [-98.290943, 41.74121],
          [-98.291344, 41.480727]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31089",
      state: "31",
      county: "089",
      name: "Holt County",
      censusArea: 2412.399,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.305149, 42.761207],
          [-98.305456, 42.43693],
          [-98.300235, 42.436913],
          [-98.300592, 42.088853],
          [-98.761155, 42.088518],
          [-99.222733, 42.087916],
          [-99.224264, 42.088028],
          [-99.234629, 42.087995],
          [-99.245803, 42.782499],
          [-99.257039, 42.804296],
          [-98.954634, 42.894077],
          [-98.892846, 42.845792],
          [-98.58376, 42.779736],
          [-98.305149, 42.761207]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48481",
      state: "48",
      county: "481",
      name: "Wharton County",
      censusArea: 1086.15,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.088912, 29.601658],
          [-96.044596, 29.562238],
          [-96.060676, 29.476473],
          [-95.97343, 29.411445],
          [-95.937434, 29.358086],
          [-95.94681, 29.333433],
          [-95.894468, 29.31498],
          [-95.8796, 29.266761],
          [-95.847656, 29.26259],
          [-95.841948, 29.258707],
          [-95.874028, 29.229702],
          [-95.965196, 29.146865],
          [-96.309178, 28.963291],
          [-96.640315, 29.247804],
          [-96.349217, 29.406052],
          [-96.307672, 29.514523],
          [-96.175422, 29.633806],
          [-96.088912, 29.601658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48493",
      state: "48",
      county: "493",
      name: "Wilson County",
      censusArea: 803.733,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.28702, 29.257626],
          [-98.137187, 29.440428],
          [-98.134171, 29.441751],
          [-98.084684, 29.379594],
          [-97.840383, 29.376791],
          [-97.858782, 29.35289],
          [-97.728443, 29.221748],
          [-98.190991, 28.882333],
          [-98.407336, 29.114435],
          [-98.28702, 29.257626]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48503",
      state: "48",
      county: "503",
      name: "Young County",
      censusArea: 914.468,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.950875, 32.956918],
          [-98.953938, 33.397534],
          [-98.420666, 33.396195],
          [-98.426553, 33.007913],
          [-98.427211, 32.9523],
          [-98.575616, 32.954349],
          [-98.950875, 32.956918]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49005",
      state: "49",
      county: "005",
      name: "Cache County",
      censusArea: 1164.813,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.885476, 41.426435],
          [-111.916568, 41.538573],
          [-111.982078, 41.534032],
          [-112.051815, 41.699789],
          [-112.052735, 41.866015],
          [-112.163956, 41.996708],
          [-112.109528, 41.997105],
          [-111.915837, 41.998519],
          [-111.915622, 41.998496],
          [-111.876491, 41.998528],
          [-111.507264, 41.999518],
          [-111.471321, 41.928157],
          [-111.426855, 41.677864],
          [-111.442949, 41.531365],
          [-111.510839, 41.4231],
          [-111.666576, 41.42883],
          [-111.72651, 41.375814],
          [-111.885476, 41.426435]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49017",
      state: "49",
      county: "017",
      name: "Garfield County",
      censusArea: 5175.121,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.8428, 38.15128],
          [-111.41443, 38.150082],
          [-110.687406, 38.151385],
          [-109.927882, 38.151885],
          [-110.283035, 37.866227],
          [-110.30498, 37.890518],
          [-110.404612, 37.879923],
          [-110.428107, 37.778987],
          [-110.497711, 37.652205],
          [-110.646394, 37.541063],
          [-111.853181, 37.535578],
          [-112.361333, 37.536425],
          [-112.361187, 37.543118],
          [-112.683197, 37.543193],
          [-112.689222, 37.805814],
          [-112.57855, 37.80454],
          [-112.588402, 37.890423],
          [-112.468177, 37.890464],
          [-112.478682, 38.147334],
          [-112.462139, 38.149912],
          [-112.443821, 38.149942],
          [-111.8428, 38.15128]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47183",
      state: "47",
      county: "183",
      name: "Weakley County",
      censusArea: 580.364,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.692709, 36.062746],
          [-88.840884, 36.123495],
          [-88.959839, 36.222834],
          [-88.950205, 36.409248],
          [-88.81642, 36.410732],
          [-88.827301, 36.502852],
          [-88.827012, 36.50285],
          [-88.816765, 36.502815],
          [-88.799594, 36.502757],
          [-88.747523, 36.502834],
          [-88.715255, 36.502662],
          [-88.661133, 36.502243],
          [-88.577283, 36.50194],
          [-88.545192, 36.501814],
          [-88.516427, 36.50143],
          [-88.516346, 36.501431],
          [-88.530346, 36.151573],
          [-88.532233, 36.115416],
          [-88.655488, 36.117338],
          [-88.692709, 36.062746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47187",
      state: "47",
      county: "187",
      name: "Williamson County",
      censusArea: 582.599,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.20507, 35.914639],
          [-87.202092, 35.936075],
          [-87.201426, 35.940579],
          [-87.205665, 35.940967],
          [-87.204242, 35.959186],
          [-87.182573, 36.049726],
          [-87.142341, 36.048028],
          [-87.106124, 36.045167],
          [-87.085304, 36.043761],
          [-87.054114, 36.045535],
          [-87.027705, 35.989423],
          [-86.9292, 36.052722],
          [-86.864957, 36.044672],
          [-86.787205, 36.035999],
          [-86.691904, 35.994581],
          [-86.687714, 35.995343],
          [-86.686164, 35.994092],
          [-86.681889, 35.989399],
          [-86.648288, 35.976422],
          [-86.627397, 35.974027],
          [-86.618892, 35.968224],
          [-86.613328, 35.790331],
          [-86.686193, 35.710051],
          [-86.782016, 35.706595],
          [-86.953593, 35.781904],
          [-87.215099, 35.850651],
          [-87.20507, 35.914639]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48003",
      state: "48",
      county: "003",
      name: "Andrews County",
      censusArea: 1500.713,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.287048, 32.086991],
          [-102.799086, 32.085795],
          [-103.064344, 32.087051],
          [-103.064348, 32.123041],
          [-103.064422, 32.145006],
          [-103.064696, 32.522193],
          [-102.211037, 32.523244],
          [-102.211249, 32.0868],
          [-102.287048, 32.086991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48019",
      state: "48",
      county: "019",
      name: "Bandera County",
      censusArea: 790.961,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.60313, 29.627181],
          [-99.602803, 29.899244],
          [-99.600862, 29.907662],
          [-99.176988, 29.895063],
          [-98.917725, 29.781398],
          [-98.781017, 29.72089],
          [-98.778782, 29.720167],
          [-98.806552, 29.690709],
          [-98.927161, 29.56225],
          [-98.983787, 29.62345],
          [-99.411817, 29.627514],
          [-99.60313, 29.627181]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48027",
      state: "48",
      county: "027",
      name: "Bell County",
      censusArea: 1051.016,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.070188, 30.98622],
          [-97.259082, 30.889596],
          [-97.315507, 30.752371],
          [-97.625288, 30.87043],
          [-97.828512, 30.906188],
          [-97.840365, 30.929318],
          [-97.911684, 31.034919],
          [-97.913847, 31.065882],
          [-97.9071, 31.069374],
          [-97.418606, 31.320202],
          [-97.343426, 31.244215],
          [-97.278113, 31.279799],
          [-97.070188, 30.98622]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48041",
      state: "48",
      county: "041",
      name: "Brazos County",
      censusArea: 585.445,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.566983, 30.69567],
          [-96.45763, 30.744474],
          [-96.349364, 30.911423],
          [-96.336193, 30.9217],
          [-96.263223, 30.964123],
          [-96.24102, 30.973737],
          [-96.16837, 30.822997],
          [-96.187765, 30.598737],
          [-96.121806, 30.443426],
          [-96.079969, 30.43003],
          [-96.147255, 30.38284],
          [-96.15463, 30.330288],
          [-96.199544, 30.38291],
          [-96.299173, 30.379152],
          [-96.294068, 30.380235],
          [-96.294939, 30.385623],
          [-96.306356, 30.391715],
          [-96.302498, 30.399403],
          [-96.297599, 30.400421],
          [-96.298866, 30.410502],
          [-96.28659, 30.411661],
          [-96.278998, 30.407422],
          [-96.272837, 30.409042],
          [-96.272049, 30.416145],
          [-96.281109, 30.414831],
          [-96.287993, 30.421281],
          [-96.282564, 30.426614],
          [-96.286449, 30.433756],
          [-96.285516, 30.448393],
          [-96.290895, 30.443999],
          [-96.358817, 30.541753],
          [-96.510494, 30.627828],
          [-96.601708, 30.640535],
          [-96.594308, 30.652589],
          [-96.584439, 30.658957],
          [-96.582804, 30.663792],
          [-96.584171, 30.683191],
          [-96.581519, 30.68889],
          [-96.572064, 30.688849],
          [-96.566983, 30.69567]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48059",
      state: "48",
      county: "059",
      name: "Callahan County",
      censusArea: 899.372,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.195871, 32.079235],
          [-99.631413, 32.081274],
          [-99.629579, 32.514657],
          [-99.612026, 32.514654],
          [-99.114293, 32.514811],
          [-99.118665, 32.079585],
          [-99.12534, 32.079187],
          [-99.147918, 32.079242],
          [-99.195871, 32.079235]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48075",
      state: "48",
      county: "075",
      name: "Childress County",
      censusArea: 696.405,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.99772, 34.311829],
          [-100.417783, 34.313524],
          [-100.415895, 34.747525],
          [-100.000381, 34.746358],
          [-100.000381, 34.560509],
          [-99.997853, 34.561485],
          [-99.997508, 34.558017],
          [-99.99772, 34.311829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48083",
      state: "48",
      county: "083",
      name: "Coleman County",
      censusArea: 1261.946,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.721698, 31.576759],
          [-99.713971, 32.082089],
          [-99.631413, 32.081274],
          [-99.195871, 32.079235],
          [-99.202768, 31.467234],
          [-99.314875, 31.409875],
          [-99.402284, 31.446198],
          [-99.513678, 31.425495],
          [-99.60185, 31.49195],
          [-99.721698, 31.576759]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48089",
      state: "48",
      county: "089",
      name: "Colorado County",
      censusArea: 960.274,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.640315, 29.247804],
          [-96.642303, 29.249576],
          [-96.643316, 29.25025],
          [-96.658466, 29.263876],
          [-96.560555, 29.335439],
          [-96.793132, 29.577378],
          [-96.874222, 29.632706],
          [-96.754307, 29.755502],
          [-96.569844, 29.961516],
          [-96.413283, 29.824985],
          [-96.344476, 29.830147],
          [-96.259226, 29.668912],
          [-96.175422, 29.633806],
          [-96.307672, 29.514523],
          [-96.349217, 29.406052],
          [-96.640315, 29.247804]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06001",
      state: "06",
      county: "001",
      name: "Alameda County",
      censusArea: 739.017,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.855762, 37.484537],
          [-122.045271, 37.460276],
          [-122.052648, 37.459656],
          [-122.081473, 37.477838],
          [-122.109574, 37.497637],
          [-122.116112, 37.505386],
          [-122.111344, 37.50758],
          [-122.111998, 37.528851],
          [-122.128688, 37.560594],
          [-122.133924, 37.562885],
          [-122.137524, 37.567467],
          [-122.144396, 37.581866],
          [-122.147014, 37.588411],
          [-122.145378, 37.600846],
          [-122.14636, 37.607391],
          [-122.152905, 37.640771],
          [-122.163049, 37.667933],
          [-122.170904, 37.676114],
          [-122.179085, 37.680041],
          [-122.197411, 37.692804],
          [-122.203971, 37.697769],
          [-122.213774, 37.698695],
          [-122.221628, 37.705567],
          [-122.246826, 37.72193],
          [-122.255989, 37.735674],
          [-122.257953, 37.739601],
          [-122.257134, 37.745001],
          [-122.252226, 37.747619],
          [-122.244938, 37.750294],
          [-122.242638, 37.753744],
          [-122.253753, 37.761218],
          [-122.264101, 37.764667],
          [-122.275408, 37.76735],
          [-122.286139, 37.769458],
          [-122.293996, 37.770416],
          [-122.304345, 37.774632],
          [-122.318909, 37.77904],
          [-122.329159, 37.783173],
          [-122.33079, 37.78383],
          [-122.330963, 37.786035],
          [-122.331748, 37.796052],
          [-122.33555, 37.799538],
          [-122.335675, 37.799652],
          [-122.333711, 37.809797],
          [-122.323567, 37.823214],
          [-122.317676, 37.826814],
          [-122.306222, 37.827469],
          [-122.303931, 37.830087],
          [-122.301313, 37.847758],
          [-122.310477, 37.873938],
          [-122.309986, 37.892755],
          [-122.313258, 37.89701],
          [-122.313496, 37.897211],
          [-122.310881, 37.897825],
          [-122.30618, 37.898225],
          [-122.185977, 37.820726],
          [-122.004228, 37.770058],
          [-122.010771, 37.755127],
          [-122.001071, 37.739028],
          [-121.977999, 37.729579],
          [-121.96077, 37.718629],
          [-121.910138, 37.73046],
          [-121.881567, 37.737328],
          [-121.623724, 37.799291],
          [-121.55916, 37.818927],
          [-121.556936, 37.817218],
          [-121.556655, 37.542732],
          [-121.471925, 37.481783],
          [-121.472648, 37.48217],
          [-121.855762, 37.484537]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01105",
      state: "01",
      county: "105",
      name: "Perry County",
      censusArea: 719.664,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.524485, 32.482028],
          [-87.525198, 32.655713],
          [-87.473915, 32.655867],
          [-87.472174, 32.83062],
          [-87.4212, 32.874508],
          [-87.319473, 32.875124],
          [-87.319184, 32.831522],
          [-87.019157, 32.837034],
          [-87.017762, 32.729532],
          [-87.116515, 32.487659],
          [-87.423153, 32.482965],
          [-87.421744, 32.308101],
          [-87.47308, 32.307614],
          [-87.52429, 32.307293],
          [-87.524485, 32.482028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01111",
      state: "01",
      county: "111",
      name: "Randolph County",
      censusArea: 580.55,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.643482, 33.495885],
          [-85.304439, 33.482884],
          [-85.293902, 33.428079],
          [-85.236509, 33.129562],
          [-85.232378, 33.108077],
          [-85.593177, 33.10734],
          [-85.653654, 33.106634],
          [-85.643482, 33.495885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01113",
      state: "01",
      county: "113",
      name: "Russell County",
      censusArea: 641.14,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.987278, 32.381623],
          [-84.987386, 32.381201],
          [-85.008096, 32.336677],
          [-85.007103, 32.328362],
          [-85.001874, 32.322015],
          [-84.93868, 32.300708],
          [-84.916611, 32.281177],
          [-84.909052, 32.26354],
          [-84.92165, 32.233306],
          [-84.923638, 32.231539],
          [-84.964828, 32.194952],
          [-84.995929, 32.184852],
          [-85.047865, 32.142033],
          [-85.056029, 32.063055],
          [-85.185067, 32.061708],
          [-85.257747, 32.148251],
          [-85.410241, 32.146651],
          [-85.427442, 32.146551],
          [-85.433543, 32.234648],
          [-85.434045, 32.40984],
          [-85.330143, 32.410842],
          [-85.333843, 32.468639],
          [-85.059294, 32.472909],
          [-85.00103, 32.510157],
          [-85.000779, 32.506548],
          [-84.998231, 32.469842],
          [-84.983831, 32.445643],
          [-84.979431, 32.412244],
          [-84.987278, 32.381623]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01123",
      state: "01",
      county: "123",
      name: "Tallapoosa County",
      censusArea: 716.521,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.886148, 32.493053],
          [-85.87986, 32.754528],
          [-86.007187, 32.754984],
          [-86.00917, 33.09026],
          [-85.974715, 33.105286],
          [-85.653654, 33.106634],
          [-85.593177, 33.10734],
          [-85.593151, 32.72853],
          [-85.696755, 32.697429],
          [-85.695854, 32.595933],
          [-85.798585, 32.581089],
          [-85.797156, 32.494236],
          [-85.886148, 32.493053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18165",
      state: "18",
      county: "165",
      name: "Vermillion County",
      censusArea: 256.878,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.382418, 39.607937],
          [-87.532196, 39.607306],
          [-87.532365, 39.646126],
          [-87.532444, 39.646102],
          [-87.532703, 39.664868],
          [-87.533066, 39.781743],
          [-87.533058, 39.796243],
          [-87.533056, 39.803922],
          [-87.533142, 39.810947],
          [-87.533227, 39.883],
          [-87.533227, 39.883127],
          [-87.532776, 39.971077],
          [-87.53279, 39.97501],
          [-87.532683, 39.977691],
          [-87.532542, 39.987462],
          [-87.532331, 39.997776],
          [-87.532287, 40.000037],
          [-87.532308, 40.011492],
          [-87.532308, 40.011587],
          [-87.531561, 40.133005],
          [-87.531759, 40.144273],
          [-87.531439, 40.148027],
          [-87.531438, 40.148123],
          [-87.406667, 40.127508],
          [-87.420221, 39.952452],
          [-87.353863, 39.862077],
          [-87.395625, 39.65295],
          [-87.382418, 39.607937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19003",
      state: "19",
      county: "003",
      name: "Adams County",
      censusArea: 423.439,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.927587, 41.158508],
          [-94.704975, 41.157903],
          [-94.700589, 41.158085],
          [-94.470603, 41.157566],
          [-94.470779, 40.899502],
          [-94.928459, 40.900653],
          [-94.927587, 41.158508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19013",
      state: "19",
      county: "013",
      name: "Black Hawk County",
      censusArea: 565.769,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.064766, 42.297259],
          [-92.298792, 42.297527],
          [-92.53351, 42.297001],
          [-92.5543, 42.555854],
          [-92.554561, 42.55935],
          [-92.554492, 42.642314],
          [-92.081661, 42.642056],
          [-92.064766, 42.297259]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19025",
      state: "19",
      county: "025",
      name: "Calhoun County",
      censusArea: 569.968,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.914485, 42.560309],
          [-94.443024, 42.558843],
          [-94.397671, 42.4733],
          [-94.397526, 42.209161],
          [-94.628806, 42.209396],
          [-94.858412, 42.209692],
          [-94.856702, 42.473967],
          [-94.914487, 42.47419],
          [-94.914485, 42.560309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19035",
      state: "19",
      county: "035",
      name: "Cherokee County",
      censusArea: 576.906,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.859377, 42.909097],
          [-95.388182, 42.909897],
          [-95.38801, 42.561742],
          [-95.741611, 42.561285],
          [-95.750287, 42.561106],
          [-95.859949, 42.56062],
          [-95.859377, 42.909097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17071",
      state: "17",
      county: "071",
      name: "Henderson County",
      censusArea: 378.872,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.112258, 40.696218],
          [-91.11194, 40.697018],
          [-91.094728, 40.797833],
          [-91.097031, 40.802471],
          [-91.097553, 40.808433],
          [-91.092993, 40.821079],
          [-91.009536, 40.900565],
          [-90.9985, 40.90812],
          [-90.968995, 40.919127],
          [-90.962916, 40.924957],
          [-90.952233, 40.954047],
          [-90.942253, 41.034702],
          [-90.945549, 41.06173],
          [-90.948523, 41.070248],
          [-90.785194, 41.068749],
          [-90.789652, 40.63575],
          [-90.894339, 40.638683],
          [-90.904216, 40.639201],
          [-90.913894, 40.637748],
          [-91.185415, 40.638052],
          [-91.138055, 40.660893],
          [-91.122421, 40.670675],
          [-91.112258, 40.696218]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17075",
      state: "17",
      county: "075",
      name: "Iroquois County",
      censusArea: 1117.316,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.526129, 40.736885],
          [-87.526292, 40.535409],
          [-87.526352, 40.535111],
          [-87.526376, 40.491574],
          [-87.526379, 40.491237],
          [-87.935309, 40.485923],
          [-88.117905, 40.488086],
          [-88.131938, 40.997839],
          [-87.526307, 41.010355],
          [-87.526305, 41.010346],
          [-87.526084, 40.911914],
          [-87.526437, 40.894209],
          [-87.525962, 40.880618],
          [-87.526113, 40.879703],
          [-87.525783, 40.854357],
          [-87.526129, 40.73695],
          [-87.526129, 40.736885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17087",
      state: "17",
      county: "087",
      name: "Johnson County",
      censusArea: 343.915,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.041401, 37.596576],
          [-88.851487, 37.597236],
          [-88.708546, 37.599277],
          [-88.71065, 37.33709],
          [-88.904003, 37.335629],
          [-88.929129, 37.302647],
          [-89.044787, 37.329846],
          [-89.041401, 37.596576]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17095",
      state: "17",
      county: "095",
      name: "Knox County",
      censusArea: 716.395,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.215267, 40.713598],
          [-90.444343, 40.714667],
          [-90.439423, 41.063995],
          [-90.437957, 41.133884],
          [-90.438122, 41.136808],
          [-90.437847, 41.137221],
          [-90.437657, 41.151252],
          [-90.323178, 41.151288],
          [-89.984559, 41.149366],
          [-89.985479, 40.974495],
          [-89.985429, 40.887645],
          [-89.985376, 40.850576],
          [-89.985226, 40.848699],
          [-89.985333, 40.800032],
          [-89.986068, 40.712257],
          [-90.215267, 40.713598]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17113",
      state: "17",
      county: "113",
      name: "Mc Lean County",
      censusArea: 1183.378,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.814487, 40.75506],
          [-88.584272, 40.757608],
          [-88.574502, 40.61655],
          [-88.459475, 40.617345],
          [-88.459957, 40.39885],
          [-88.460418, 40.281935],
          [-88.4809, 40.281216],
          [-88.574885, 40.281501],
          [-88.689271, 40.282148],
          [-89.148764, 40.282038],
          [-89.26265, 40.280919],
          [-89.26374, 40.325344],
          [-89.26939, 40.594329],
          [-89.155175, 40.596417],
          [-89.145649, 40.662057],
          [-88.9847, 40.664954],
          [-88.986896, 40.752297],
          [-88.929331, 40.753337],
          [-88.814487, 40.75506]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17123",
      state: "17",
      county: "123",
      name: "Marshall County",
      censusArea: 386.789,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.638429, 41.148591],
          [-89.466421, 41.148558],
          [-89.32927, 41.147931],
          [-89.358597, 41.103665],
          [-89.162238, 41.10408],
          [-89.047856, 41.104781],
          [-89.047718, 40.925749],
          [-89.47233, 40.921206],
          [-89.447693, 40.973246],
          [-89.638727, 40.973707],
          [-89.638429, 41.148591]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17135",
      state: "17",
      county: "135",
      name: "Montgomery County",
      censusArea: 703.687,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.700338, 39.17544],
          [-89.701645, 39.523369],
          [-89.533655, 39.524592],
          [-89.530836, 39.348864],
          [-89.139807, 39.348888],
          [-89.140076, 39.217907],
          [-89.250513, 39.217512],
          [-89.250658, 39.086442],
          [-89.250335, 39.029942],
          [-89.250447, 39.028145],
          [-89.586088, 39.028246],
          [-89.639265, 38.999129],
          [-89.648846, 38.999167],
          [-89.652405, 38.999581],
          [-89.653446, 38.999191],
          [-89.698555, 38.998979],
          [-89.700338, 39.17544]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17151",
      state: "17",
      county: "151",
      name: "Pope County",
      censusArea: 368.77,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.490297, 37.066669],
          [-88.490336, 37.159358],
          [-88.71065, 37.33709],
          [-88.708546, 37.599277],
          [-88.412112, 37.599912],
          [-88.415149, 37.424863],
          [-88.414895, 37.423461],
          [-88.439333, 37.416416],
          [-88.456, 37.408482],
          [-88.470224, 37.396255],
          [-88.476592, 37.386875],
          [-88.514661, 37.290948],
          [-88.515939, 37.284043],
          [-88.509328, 37.26213],
          [-88.471753, 37.220155],
          [-88.458763, 37.213536],
          [-88.447764, 37.203527],
          [-88.424403, 37.152428],
          [-88.444605, 37.098601],
          [-88.458948, 37.073796],
          [-88.482856, 37.067114],
          [-88.490297, 37.066669]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17163",
      state: "17",
      county: "163",
      name: "St. Clair County",
      censusArea: 657.759,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.18451, 38.611551],
          [-90.182625, 38.617989],
          [-90.17801, 38.63375],
          [-90.17771, 38.64275],
          [-90.18111, 38.65955],
          [-90.181399, 38.660378],
          [-90.17772, 38.660536],
          [-90.037105, 38.658449],
          [-89.707024, 38.655152],
          [-89.704417, 38.415667],
          [-89.703256, 38.219404],
          [-89.899061, 38.220755],
          [-89.913226, 38.307829],
          [-90.036018, 38.30885],
          [-90.263064, 38.52022],
          [-90.260314, 38.528352],
          [-90.257773, 38.532008],
          [-90.248913, 38.544752],
          [-90.224512, 38.574651],
          [-90.202511, 38.588651],
          [-90.196011, 38.594451],
          [-90.18451, 38.611551]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17187",
      state: "17",
      county: "187",
      name: "Warren County",
      censusArea: 542.405,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.445506, 40.627843],
          [-90.789652, 40.63575],
          [-90.785194, 41.068749],
          [-90.439423, 41.063995],
          [-90.444343, 40.714667],
          [-90.444429, 40.697371],
          [-90.444606, 40.695505],
          [-90.444875, 40.677784],
          [-90.44475, 40.677479],
          [-90.444877, 40.663944],
          [-90.445009, 40.662406],
          [-90.445084, 40.657202],
          [-90.445217, 40.65687],
          [-90.445506, 40.627843]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17199",
      state: "17",
      county: "199",
      name: "Williamson County",
      censusArea: 420.149,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.708546, 37.599277],
          [-88.851487, 37.597236],
          [-89.041401, 37.596576],
          [-89.153668, 37.600469],
          [-89.151176, 37.861999],
          [-88.70676, 37.863338],
          [-88.708546, 37.599277]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31107",
      state: "31",
      county: "107",
      name: "Knox County",
      censusArea: 1108.35,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.157405, 42.831899],
          [-98.056625, 42.770781],
          [-98.035034, 42.764205],
          [-98.017228, 42.762411],
          [-98.000348, 42.763256],
          [-97.950147, 42.769619],
          [-97.936716, 42.775754],
          [-97.908983, 42.794909],
          [-97.888562, 42.817251],
          [-97.879878, 42.835395],
          [-97.875345, 42.858724],
          [-97.865695, 42.86286],
          [-97.84527, 42.867734],
          [-97.828496, 42.868797],
          [-97.814382, 42.861119],
          [-97.774456, 42.849774],
          [-97.686506, 42.842435],
          [-97.635115, 42.844984],
          [-97.531867, 42.850105],
          [-97.485376, 42.854838],
          [-97.485298, 42.438578],
          [-97.834431, 42.437684],
          [-98.300235, 42.436913],
          [-98.305456, 42.43693],
          [-98.305149, 42.761207],
          [-98.309741, 42.881232],
          [-98.204506, 42.846845],
          [-98.165835, 42.837011],
          [-98.157405, 42.831899]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28077",
      state: "28",
      county: "077",
      name: "Lawrence County",
      censusArea: 430.668,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.974586, 31.761655],
          [-89.976052, 31.522477],
          [-89.95894, 31.390489],
          [-90.040485, 31.394673],
          [-90.040466, 31.336014],
          [-90.24389, 31.350274],
          [-90.245191, 31.717524],
          [-90.122565, 31.75268],
          [-89.974586, 31.761655]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28081",
      state: "28",
      county: "081",
      name: "Lee County",
      censusArea: 449.951,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.824523, 34.074948],
          [-88.824966, 34.364759],
          [-88.789442, 34.49493],
          [-88.736465, 34.50891],
          [-88.541749, 34.509192],
          [-88.542079, 34.465276],
          [-88.543352, 34.089044],
          [-88.718693, 34.08934],
          [-88.718964, 34.074521],
          [-88.725125, 34.074815],
          [-88.824523, 34.074948]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28085",
      state: "28",
      county: "085",
      name: "Lincoln County",
      censusArea: 586.115,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.736824, 31.698491],
          [-90.279953, 31.699865],
          [-90.245191, 31.717524],
          [-90.24389, 31.350274],
          [-90.260391, 31.350274],
          [-90.548199, 31.349574],
          [-90.633302, 31.349306],
          [-90.633231, 31.611409],
          [-90.73733, 31.611124],
          [-90.736824, 31.698491]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28087",
      state: "28",
      county: "087",
      name: "Lowndes County",
      censusArea: 505.509,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.277421, 33.512436],
          [-88.291127, 33.399061],
          [-88.304514, 33.28832],
          [-88.669076, 33.286922],
          [-88.67125, 33.506168],
          [-88.480299, 33.522741],
          [-88.513917, 33.650209],
          [-88.37033, 33.685918],
          [-88.325119, 33.745591],
          [-88.24839, 33.744908],
          [-88.254445, 33.698779],
          [-88.254622, 33.69578],
          [-88.256131, 33.68286],
          [-88.256343, 33.682053],
          [-88.267005, 33.594229],
          [-88.267148, 33.591989],
          [-88.26816, 33.58504],
          [-88.269076, 33.576929],
          [-88.269532, 33.572894],
          [-88.27005, 33.570819],
          [-88.274619, 33.534008],
          [-88.276805, 33.516463],
          [-88.277421, 33.512436]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28107",
      state: "28",
      county: "107",
      name: "Panola County",
      censusArea: 685.144,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.721341, 34.554274],
          [-89.72105, 34.192545],
          [-89.721431, 34.163513],
          [-89.931485, 34.164303],
          [-90.135456, 34.160074],
          [-90.134441, 34.265195],
          [-90.194086, 34.301407],
          [-90.198536, 34.511088],
          [-90.198631, 34.554425],
          [-89.721341, 34.554274]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28123",
      state: "28",
      county: "123",
      name: "Scott County",
      censusArea: 609.185,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.322692, 32.223472],
          [-89.730424, 32.222039],
          [-89.730063, 32.352733],
          [-89.784123, 32.586891],
          [-89.781805, 32.597364],
          [-89.770781, 32.599184],
          [-89.759155, 32.613543],
          [-89.730304, 32.634228],
          [-89.730152, 32.577248],
          [-89.322598, 32.576433],
          [-89.322692, 32.223472]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28133",
      state: "28",
      county: "133",
      name: "Sunflower County",
      censusArea: 697.754,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.451643, 33.81139],
          [-90.453233, 33.329484],
          [-90.555549, 33.329181],
          [-90.555211, 33.270533],
          [-90.716277, 33.270032],
          [-90.763773, 33.270359],
          [-90.763509, 33.529389],
          [-90.763469, 33.616164],
          [-90.6586, 33.616272],
          [-90.657002, 33.987586],
          [-90.451012, 33.985689],
          [-90.450289, 33.970944],
          [-90.451643, 33.81139]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28145",
      state: "28",
      county: "145",
      name: "Union County",
      censusArea: 415.598,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.73544, 34.596483],
          [-88.736465, 34.50891],
          [-88.789442, 34.49493],
          [-88.824966, 34.364759],
          [-88.928901, 34.379361],
          [-89.246339, 34.379344],
          [-89.246133, 34.495633],
          [-89.24592, 34.54135],
          [-89.244591, 34.542329],
          [-89.245922, 34.542942],
          [-89.245645, 34.583234],
          [-89.245611, 34.597601],
          [-89.088488, 34.598247],
          [-88.73544, 34.596483]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28159",
      state: "28",
      county: "159",
      name: "Winston County",
      censusArea: 607.245,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.108824, 33.215886],
          [-89.108886, 33.285937],
          [-89.088255, 33.286003],
          [-88.988471, 33.286087],
          [-88.813053, 33.286424],
          [-88.812782, 32.925973],
          [-88.914413, 32.9266],
          [-89.319163, 32.931654],
          [-89.317855, 33.108871],
          [-89.213921, 33.109402],
          [-89.213422, 33.197543],
          [-89.108824, 33.215886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29003",
      state: "29",
      county: "003",
      name: "Andrew County",
      censusArea: 432.703,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.04308, 40.130096],
          [-94.605942, 40.126127],
          [-94.606575, 40.042049],
          [-94.605377, 40.039217],
          [-94.602819, 39.819608],
          [-94.876544, 39.820594],
          [-94.908267, 39.812172],
          [-94.927897, 39.886112],
          [-94.935963, 39.894355],
          [-94.954155, 39.901138],
          [-94.993348, 39.900747],
          [-95.04308, 40.130096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29015",
      state: "29",
      county: "015",
      name: "Benton County",
      censusArea: 704.06,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.504025, 38.071757],
          [-93.523484, 38.207216],
          [-93.512743, 38.512476],
          [-93.291851, 38.506321],
          [-93.290454, 38.535388],
          [-93.067294, 38.529995],
          [-93.077692, 38.263092],
          [-93.065199, 38.062479],
          [-93.497132, 38.072573],
          [-93.504025, 38.071757]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29027",
      state: "29",
      county: "027",
      name: "Callaway County",
      censusArea: 834.575,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.944238, 38.596925],
          [-92.065233, 38.557079],
          [-92.161663, 38.578057],
          [-92.220661, 38.643167],
          [-92.17095, 38.7653],
          [-92.131512, 39.064484],
          [-92.110387, 39.064204],
          [-91.633998, 39.059057],
          [-91.647171, 38.703396],
          [-91.869489, 38.66394],
          [-91.944238, 38.596925]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29039",
      state: "29",
      county: "039",
      name: "Cedar County",
      censusArea: 474.483,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.616033, 37.572689],
          [-94.075385, 37.58157],
          [-94.073524, 37.639722],
          [-94.065675, 37.900987],
          [-93.808648, 37.892775],
          [-93.811402, 37.834571],
          [-93.628404, 37.829435],
          [-93.616033, 37.572689]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29049",
      state: "29",
      county: "049",
      name: "Clinton County",
      censusArea: 418.956,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.602257, 39.747195],
          [-94.205961, 39.745841],
          [-94.208423, 39.5272],
          [-94.21083, 39.456337],
          [-94.210642, 39.454682],
          [-94.597879, 39.455838],
          [-94.600819, 39.456155],
          [-94.601246, 39.530372],
          [-94.602257, 39.747195]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29059",
      state: "29",
      county: "059",
      name: "Dallas County",
      censusArea: 540.77,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.182489, 37.417204],
          [-93.175222, 37.67489],
          [-93.187915, 37.802737],
          [-93.182706, 37.904144],
          [-93.072447, 37.902627],
          [-92.855384, 37.895736],
          [-92.853481, 37.48397],
          [-93.071584, 37.487444],
          [-93.07338, 37.414989],
          [-93.158287, 37.416799],
          [-93.182489, 37.417204]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29071",
      state: "29",
      county: "071",
      name: "Franklin County",
      censusArea: 922.681,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.964461, 38.547545],
          [-90.916554, 38.534725],
          [-90.732328, 38.639302],
          [-90.736319, 38.466645],
          [-90.737599, 38.415897],
          [-90.780185, 38.204112],
          [-91.095765, 38.204083],
          [-91.367482, 38.209741],
          [-91.369192, 38.699324],
          [-91.313093, 38.706715],
          [-91.226547, 38.621567],
          [-91.077324, 38.609343],
          [-90.964461, 38.547545]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48107",
      state: "48",
      county: "107",
      name: "Crosby County",
      censusArea: 900.198,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.56358, 33.830451],
          [-101.041158, 33.833625],
          [-101.038788, 33.397214],
          [-101.556884, 33.39476],
          [-101.56358, 33.830451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48119",
      state: "48",
      county: "119",
      name: "Delta County",
      censusArea: 256.825,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.861778, 33.21933],
          [-95.858723, 33.40953],
          [-95.857738, 33.461722],
          [-95.82673, 33.459038],
          [-95.758439, 33.495098],
          [-95.621027, 33.48706],
          [-95.308637, 33.380662],
          [-95.307545, 33.380676],
          [-95.30664, 33.378027],
          [-95.30777, 33.377769],
          [-95.308593, 33.37719],
          [-95.314165, 33.367232],
          [-95.584353, 33.359405],
          [-95.730784, 33.305405],
          [-95.861778, 33.21933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48131",
      state: "48",
      county: "131",
      name: "Duval County",
      censusArea: 1793.481,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.798323, 27.354029],
          [-98.803325, 28.05748],
          [-98.334323, 28.0578],
          [-98.235497, 28.057966],
          [-98.232464, 27.262487],
          [-98.523723, 27.265115],
          [-98.522293, 27.343284],
          [-98.798323, 27.354029]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48143",
      state: "48",
      county: "143",
      name: "Erath County",
      censusArea: 1083.071,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.945625, 32.233468],
          [-97.940474, 32.221738],
          [-97.864864, 32.087328],
          [-98.005462, 32.017897],
          [-98.208386, 31.917511],
          [-98.251211, 31.980127],
          [-98.327499, 31.941261],
          [-98.551158, 32.261436],
          [-98.475133, 32.298916],
          [-98.475177, 32.513039],
          [-98.068545, 32.511626],
          [-97.945625, 32.233468]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48153",
      state: "48",
      county: "153",
      name: "Floyd County",
      censusArea: 992.143,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.47158, 34.31229],
          [-101.041484, 34.312444],
          [-101.041158, 33.833625],
          [-101.56358, 33.830451],
          [-101.564856, 34.312493],
          [-101.47158, 34.31229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48163",
      state: "48",
      county: "163",
      name: "Frio County",
      censusArea: 1133.5,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.8049, 29.090434],
          [-98.800841, 28.647487],
          [-99.395736, 28.640784],
          [-99.409021, 28.640566],
          [-99.41388, 29.091335],
          [-98.8049, 29.090434]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48171",
      state: "48",
      county: "171",
      name: "Gillespie County",
      censusArea: 1058.214,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.920147, 30.13829],
          [-99.300988, 30.134298],
          [-99.301718, 30.286653],
          [-99.303996, 30.499832],
          [-98.964232, 30.498482],
          [-98.59167, 30.499875],
          [-98.587897, 30.138954],
          [-98.920147, 30.13829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48173",
      state: "48",
      county: "173",
      name: "Glasscock County",
      censusArea: 900.218,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.267122, 31.650854],
          [-101.775802, 31.651319],
          [-101.776085, 32.086925],
          [-101.695011, 32.087532],
          [-101.264216, 32.087136],
          [-101.267122, 31.650854]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48175",
      state: "48",
      county: "175",
      name: "Goliad County",
      censusArea: 852.014,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.574639, 28.8133],
          [-97.41734, 28.925228],
          [-97.305916, 28.864037],
          [-97.231971, 28.865303],
          [-97.158933, 28.776156],
          [-97.182359, 28.610602],
          [-97.160751, 28.553475],
          [-97.375268, 28.389116],
          [-97.375579, 28.388684],
          [-97.551369, 28.450441],
          [-97.558496, 28.514054],
          [-97.696945, 28.541856],
          [-97.77853, 28.668027],
          [-97.574639, 28.8133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48183",
      state: "48",
      county: "183",
      name: "Gregg County",
      censusArea: 273.303,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.986935, 32.537246],
          [-94.70179, 32.652209],
          [-94.701986, 32.421782],
          [-94.579219, 32.394916],
          [-94.600776, 32.360044],
          [-94.638347, 32.370539],
          [-94.985411, 32.37082],
          [-94.986406, 32.371322],
          [-94.986935, 32.537246]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48187",
      state: "48",
      county: "187",
      name: "Guadalupe County",
      censusArea: 711.3,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.134171, 29.441751],
          [-98.126767, 29.482248],
          [-98.272924, 29.550878],
          [-98.298524, 29.561141],
          [-98.310928, 29.594473],
          [-98.089941, 29.683479],
          [-97.999271, 29.752444],
          [-97.875259, 29.858208],
          [-97.721852, 29.682088],
          [-97.633176, 29.649863],
          [-97.840383, 29.376791],
          [-98.084684, 29.379594],
          [-98.134171, 29.441751]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48205",
      state: "48",
      county: "205",
      name: "Hartley County",
      censusArea: 1462.033,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.163015, 36.055249],
          [-102.162809, 35.627515],
          [-103.041554, 35.622487],
          [-103.041357, 35.739274],
          [-103.040824, 36.055231],
          [-102.163015, 36.055249]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48209",
      state: "48",
      county: "209",
      name: "Hays County",
      censusArea: 677.978,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.172977, 30.356312],
          [-98.011253, 30.242288],
          [-97.710215, 30.024499],
          [-97.893896, 29.883734],
          [-97.875259, 29.858208],
          [-97.999271, 29.752444],
          [-98.030523, 29.848539],
          [-98.2976, 30.037994],
          [-98.172977, 30.356312]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48219",
      state: "48",
      county: "219",
      name: "Hockley County",
      censusArea: 908.392,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.075929, 33.389586],
          [-102.594836, 33.388489],
          [-102.615447, 33.825121],
          [-102.085733, 33.824675],
          [-102.075929, 33.389586]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48231",
      state: "48",
      county: "231",
      name: "Hunt County",
      censusArea: 840.318,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.297322, 32.841723],
          [-96.297227, 32.981752],
          [-96.295413, 33.351936],
          [-95.858723, 33.40953],
          [-95.861778, 33.21933],
          [-95.863296, 33.000117],
          [-95.862521, 32.979571],
          [-95.9453, 32.979877],
          [-95.987259, 32.876415],
          [-95.934433, 32.837217],
          [-96.076801, 32.838489],
          [-96.297322, 32.841723]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48241",
      state: "48",
      county: "241",
      name: "Jasper County",
      censusArea: 938.845,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.129632, 31.09928],
          [-94.039034, 31.134271],
          [-93.911126, 31.158071],
          [-93.865711, 30.947218],
          [-93.901452, 30.242672],
          [-94.117604, 30.241665],
          [-94.083386, 30.342181],
          [-94.116498, 30.433966],
          [-94.071689, 30.526033],
          [-94.094812, 30.575543],
          [-94.05096, 30.638568],
          [-94.125824, 30.684052],
          [-94.150119, 30.790203],
          [-94.179343, 30.794974],
          [-94.243766, 30.983554],
          [-94.238424, 31.013705],
          [-94.353782, 31.039371],
          [-94.451127, 31.003445],
          [-94.45782, 31.033326],
          [-94.129632, 31.09928]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48255",
      state: "48",
      county: "255",
      name: "Karnes County",
      censusArea: 747.556,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.098315, 28.786949],
          [-98.190991, 28.882333],
          [-97.728443, 29.221748],
          [-97.613132, 29.109765],
          [-97.75511, 29.007113],
          [-97.574639, 28.8133],
          [-97.77853, 28.668027],
          [-97.915106, 28.719648],
          [-98.005252, 28.690239],
          [-98.098315, 28.786949]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48269",
      state: "48",
      county: "269",
      name: "King County",
      censusArea: 910.872,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.518691, 33.83565],
          [-100.04848, 33.835974],
          [-99.996434, 33.835967],
          [-99.99098, 33.397404],
          [-100.517449, 33.397866],
          [-100.518691, 33.83565]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48279",
      state: "48",
      county: "279",
      name: "Lamb County",
      censusArea: 1016.181,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.525631, 34.313034],
          [-102.090415, 34.313132],
          [-102.085733, 33.824675],
          [-102.615447, 33.825121],
          [-102.61515, 34.312891],
          [-102.525631, 34.313034]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48289",
      state: "48",
      county: "289",
      name: "Leon County",
      censusArea: 1073.151,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.319165, 31.357129],
          [-96.322853, 31.374067],
          [-96.304907, 31.382132],
          [-96.236629, 31.413392],
          [-95.7873, 31.618385],
          [-95.710112, 31.615587],
          [-95.739279, 31.504056],
          [-95.657202, 31.317999],
          [-95.725226, 31.271084],
          [-95.76441, 31.094211],
          [-95.973841, 31.09215],
          [-96.24102, 30.973737],
          [-96.33109, 31.25223],
          [-96.319165, 31.357129]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08033",
      state: "08",
      county: "033",
      name: "Dolores County",
      censusArea: 1067.051,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.041915, 37.530653],
          [-109.041865, 37.530726],
          [-109.041806, 37.604171],
          [-109.042131, 37.617662],
          [-109.042089, 37.623795],
          [-109.041732, 37.711214],
          [-109.04176, 37.713182],
          [-109.041754, 37.835826],
          [-109.041723, 37.842051],
          [-109.042137, 37.88116],
          [-108.256427, 37.894446],
          [-108.209093, 37.820936],
          [-107.989208, 37.85664],
          [-107.860845, 37.775517],
          [-107.968875, 37.696485],
          [-107.970086, 37.639587],
          [-108.917081, 37.631902],
          [-109.043053, 37.485161],
          [-109.041915, 37.530653]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08039",
      state: "08",
      county: "039",
      name: "Elbert County",
      censusArea: 1850.847,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.662896, 39.129527],
          [-104.660626, 39.565909],
          [-103.714355, 39.566331],
          [-103.715254, 39.128246],
          [-103.722007, 39.084085],
          [-103.719664, 38.866827],
          [-104.055528, 38.868868],
          [-104.051663, 39.128366],
          [-104.662896, 39.129527]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08045",
      state: "08",
      county: "045",
      name: "Garfield County",
      censusArea: 2947.563,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.051417, 39.366677],
          [-109.051363, 39.497674],
          [-109.051221, 39.660472],
          [-107.938546, 39.694464],
          [-107.938502, 39.824996],
          [-107.43177, 39.828377],
          [-107.431665, 39.917725],
          [-107.318751, 39.914968],
          [-107.316993, 40.090765],
          [-107.037363, 40.091538],
          [-107.033968, 39.918913],
          [-107.113672, 39.919112],
          [-107.113446, 39.366066],
          [-107.430949, 39.366178],
          [-108.865465, 39.366251],
          [-108.874002, 39.366269],
          [-108.875659, 39.366531],
          [-109.051417, 39.366677]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06079",
      state: "06",
      county: "079",
      name: "San Luis Obispo County",
      censusArea: 3298.567,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.194146, 35.789204],
          [-120.194053, 35.749964],
          [-120.193934, 35.749393],
          [-120.193892, 35.726513],
          [-120.193918, 35.614359],
          [-120.085922, 35.614524],
          [-120.086674, 35.526554],
          [-119.997382, 35.468702],
          [-119.997392, 35.439495],
          [-119.880045, 35.439133],
          [-119.880172, 35.351211],
          [-119.809346, 35.350865],
          [-119.809449, 35.263584],
          [-119.666663, 35.262527],
          [-119.667056, 35.174809],
          [-119.553641, 35.179975],
          [-119.560975, 35.087673],
          [-119.472719, 35.076885],
          [-119.472754, 34.901174],
          [-119.535757, 34.897576],
          [-119.672963, 34.973375],
          [-119.745566, 34.973676],
          [-119.928419, 35.059794],
          [-119.980122, 35.057573],
          [-120.093376, 35.114102],
          [-120.188383, 35.030378],
          [-120.334548, 35.006315],
          [-120.311498, 34.903764],
          [-120.355343, 34.941265],
          [-120.513924, 34.989276],
          [-120.648905, 34.974393],
          [-120.63999, 35.002963],
          [-120.63357, 35.033085],
          [-120.629931, 35.061515],
          [-120.629583, 35.078362],
          [-120.630957, 35.101941],
          [-120.635787, 35.123805],
          [-120.644311, 35.139616],
          [-120.651134, 35.147768],
          [-120.662475, 35.153357],
          [-120.667994, 35.15203],
          [-120.675074, 35.153061],
          [-120.686974, 35.160708],
          [-120.698906, 35.171192],
          [-120.704203, 35.173206],
          [-120.714185, 35.175998],
          [-120.734231, 35.178472],
          [-120.74887, 35.177795],
          [-120.754823, 35.174701],
          [-120.756862, 35.169208],
          [-120.756086, 35.160459],
          [-120.760492, 35.15971],
          [-120.778998, 35.168897],
          [-120.786076, 35.177666],
          [-120.805258, 35.184973],
          [-120.846674, 35.204429],
          [-120.856047, 35.206487],
          [-120.873046, 35.225688],
          [-120.89679, 35.247877],
          [-120.896876, 35.25399],
          [-120.889354, 35.277819],
          [-120.87957, 35.294184],
          [-120.8672, 35.327154],
          [-120.862684, 35.346776],
          [-120.862133, 35.360763],
          [-120.866099, 35.393045],
          [-120.869209, 35.403276],
          [-120.884757, 35.430196],
          [-120.896862, 35.442243],
          [-120.907937, 35.449069],
          [-120.946546, 35.446715],
          [-120.950742, 35.44802],
          [-120.955863, 35.453743],
          [-120.969436, 35.460197],
          [-120.976122, 35.459028],
          [-121.003359, 35.46071],
          [-121.025621, 35.484598],
          [-121.05308, 35.50753],
          [-121.059913, 35.509671],
          [-121.101595, 35.548814],
          [-121.126027, 35.593058],
          [-121.133556, 35.600455],
          [-121.143561, 35.606046],
          [-121.166712, 35.635399],
          [-121.188897, 35.643138],
          [-121.195291, 35.640734],
          [-121.251034, 35.656641],
          [-121.272322, 35.666711],
          [-121.284973, 35.674109],
          [-121.289794, 35.689428],
          [-121.296473, 35.696824],
          [-121.304583, 35.701794],
          [-121.314632, 35.71331],
          [-121.315786, 35.75252],
          [-121.324918, 35.769347],
          [-121.332449, 35.783106],
          [-121.346363, 35.795183],
          [-121.175695, 35.794102],
          [-120.213979, 35.789276],
          [-120.194146, 35.789204]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06093",
      state: "06",
      county: "093",
      name: "Siskiyou County",
      censusArea: 6277.887,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.289533, 42.007764],
          [-122.289527, 42.007764],
          [-122.161328, 42.007637],
          [-122.160438, 42.007637],
          [-122.156666, 42.007384],
          [-122.155408, 42.007429],
          [-122.001119, 42.004017],
          [-121.708199, 42.000815],
          [-121.705045, 42.000766],
          [-121.689159, 42.000584],
          [-121.675348, 42.000351],
          [-121.580865, 41.998668],
          [-121.52025, 41.997983],
          [-121.44754, 41.997169],
          [-121.446495, 41.183484],
          [-122.376123, 41.182904],
          [-122.498376, 41.182675],
          [-122.522636, 41.213525],
          [-122.50389, 41.342985],
          [-122.585241, 41.359008],
          [-122.653686, 41.289237],
          [-122.811342, 41.202451],
          [-122.961347, 41.181525],
          [-122.966949, 41.08205],
          [-122.897411, 41.028475],
          [-122.917529, 40.99399],
          [-123.036808, 41.004058],
          [-123.141338, 41.079178],
          [-123.23948, 41.075763],
          [-123.273402, 41.122891],
          [-123.408291, 41.179944],
          [-123.474085, 41.366193],
          [-123.661363, 41.38209],
          [-123.612395, 41.448954],
          [-123.719089, 41.595258],
          [-123.660205, 41.714055],
          [-123.703768, 41.829117],
          [-123.642812, 41.889353],
          [-123.565442, 41.903141],
          [-123.518075, 42.000436],
          [-123.501997, 42.000527],
          [-123.498896, 42.000474],
          [-123.49883, 42.000525],
          [-123.347562, 41.999108],
          [-123.230764, 42.003845],
          [-123.230762, 42.003845],
          [-123.154908, 42.008036],
          [-123.045254, 42.003049],
          [-123.001152, 42.003],
          [-122.634739, 42.004858],
          [-122.501135, 42.00846],
          [-122.378193, 42.009518],
          [-122.289533, 42.007764]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06097",
      state: "06",
      county: "097",
      name: "Sonoma County",
      censusArea: 1575.849,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.26871, 38.808369],
          [-123.136253, 38.809136],
          [-123.080997, 38.852438],
          [-122.821592, 38.85014],
          [-122.627396, 38.667506],
          [-122.646268, 38.59912],
          [-122.543893, 38.519966],
          [-122.378987, 38.250714],
          [-122.406786, 38.155632],
          [-122.406978, 38.155663],
          [-122.403514, 38.150624],
          [-122.40358, 38.15063],
          [-122.409798, 38.136231],
          [-122.439577, 38.116923],
          [-122.450377, 38.116269],
          [-122.454958, 38.118887],
          [-122.484411, 38.11496],
          [-122.489974, 38.112014],
          [-122.490727, 38.109755],
          [-122.565093, 38.182217],
          [-122.648986, 38.181077],
          [-122.7399, 38.207018],
          [-122.901726, 38.316943],
          [-123.002911, 38.295708],
          [-123.004122, 38.297012],
          [-123.024333, 38.310573],
          [-123.038742, 38.313576],
          [-123.051061, 38.310693],
          [-123.053476, 38.305722],
          [-123.052021, 38.302246],
          [-123.053504, 38.299385],
          [-123.058239, 38.298355],
          [-123.063671, 38.302178],
          [-123.074684, 38.322574],
          [-123.068437, 38.33521],
          [-123.068265, 38.359865],
          [-123.085572, 38.390525],
          [-123.103706, 38.415541],
          [-123.122379, 38.437314],
          [-123.128825, 38.450418],
          [-123.145325, 38.459422],
          [-123.166428, 38.474947],
          [-123.202277, 38.494314],
          [-123.249797, 38.511045],
          [-123.287156, 38.540223],
          [-123.297151, 38.543452],
          [-123.331899, 38.565542],
          [-123.343338, 38.590008],
          [-123.349612, 38.596805],
          [-123.371876, 38.607235],
          [-123.379303, 38.621953],
          [-123.398166, 38.647044],
          [-123.40301, 38.649449],
          [-123.405663, 38.656729],
          [-123.43272, 38.687131],
          [-123.441774, 38.699744],
          [-123.461291, 38.717001],
          [-123.490021, 38.732213],
          [-123.514784, 38.741966],
          [-123.525152, 38.753801],
          [-123.533535, 38.768408],
          [-123.26871, 38.808369]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06105",
      state: "06",
      county: "105",
      name: "Trinity County",
      censusArea: 3179.254,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.065426, 40.286971],
          [-122.989015, 40.250145],
          [-122.989623, 40.145496],
          [-122.934013, 39.978131],
          [-123.544563, 39.977015],
          [-123.544458, 40.001923],
          [-123.544059, 40.739386],
          [-123.55985, 40.829584],
          [-123.623826, 40.929421],
          [-123.560163, 40.950257],
          [-123.481457, 40.914957],
          [-123.408291, 41.179944],
          [-123.273402, 41.122891],
          [-123.23948, 41.075763],
          [-123.141338, 41.079178],
          [-123.036808, 41.004058],
          [-122.917529, 40.99399],
          [-122.897411, 41.028475],
          [-122.966949, 41.08205],
          [-122.961347, 41.181525],
          [-122.811342, 41.202451],
          [-122.653686, 41.289237],
          [-122.585241, 41.359008],
          [-122.50389, 41.342985],
          [-122.522636, 41.213525],
          [-122.498376, 41.182675],
          [-122.445453, 41.15793],
          [-122.752266, 40.689642],
          [-122.695298, 40.572514],
          [-122.845954, 40.505181],
          [-122.903738, 40.445071],
          [-122.998682, 40.418142],
          [-123.068789, 40.332233],
          [-123.065426, 40.286971]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06017",
      state: "06",
      county: "017",
      name: "El Dorado County",
      censusArea: 1707.883,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.141009, 38.71198],
          [-121.040406, 38.915651],
          [-120.746395, 39.010352],
          [-120.642458, 38.944404],
          [-120.556321, 38.915164],
          [-120.470985, 38.965507],
          [-120.43524, 39.028397],
          [-120.184098, 39.031014],
          [-120.143663, 39.067285],
          [-120.002461, 39.067489],
          [-120.001014, 38.999574],
          [-119.904315, 38.933324],
          [-119.880303, 38.864776],
          [-120.072392, 38.702767],
          [-120.098322, 38.709029],
          [-120.301721, 38.549109],
          [-120.510596, 38.511467],
          [-120.627604, 38.503143],
          [-120.813554, 38.562193],
          [-121.027507, 38.508292],
          [-121.118617, 38.717118],
          [-121.133264, 38.705427],
          [-121.141009, 38.71198]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18005",
      state: "18",
      county: "005",
      name: "Bartholomew County",
      censusArea: 406.908,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.952005, 39.347373],
          [-85.85746, 39.349312],
          [-85.854064, 39.349498],
          [-85.684515, 39.350049],
          [-85.687295, 39.190076],
          [-85.687161, 39.189026],
          [-85.687294, 39.185605],
          [-85.686783, 39.130859],
          [-85.799834, 39.128897],
          [-85.798804, 39.068536],
          [-85.860573, 39.04091],
          [-86.080272, 39.05027],
          [-86.080294, 39.152543],
          [-86.085601, 39.344189],
          [-86.081531, 39.344446],
          [-85.952005, 39.347373]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18017",
      state: "18",
      county: "017",
      name: "Cass County",
      censusArea: 412.155,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.165024, 40.562649],
          [-86.184044, 40.562459],
          [-86.374417, 40.561361],
          [-86.373521, 40.69246],
          [-86.525164, 40.692419],
          [-86.581739, 40.735765],
          [-86.581532, 40.910925],
          [-86.468509, 40.909889],
          [-86.169012, 40.909836],
          [-86.165024, 40.562649]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18027",
      state: "18",
      county: "027",
      name: "Daviess County",
      censusArea: 429.487,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.242097, 38.54493],
          [-87.279333, 38.693314],
          [-87.241463, 38.817476],
          [-87.192243, 38.814688],
          [-87.098771, 38.903974],
          [-87.031263, 38.902516],
          [-86.904252, 38.904204],
          [-86.922943, 38.817238],
          [-86.924186, 38.505358],
          [-87.072369, 38.5147],
          [-87.242097, 38.54493]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18041",
      state: "18",
      county: "041",
      name: "Fayette County",
      censusArea: 215.014,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.036087, 39.526213],
          [-85.29811, 39.525476],
          [-85.301584, 39.787579],
          [-85.221118, 39.788449],
          [-85.185089, 39.715515],
          [-85.034575, 39.714764],
          [-85.036087, 39.526213]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18043",
      state: "18",
      county: "043",
      name: "Floyd County",
      censusArea: 147.935,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.816164, 38.282969],
          [-85.823764, 38.280569],
          [-85.829364, 38.276769],
          [-85.897664, 38.184269],
          [-85.89955, 38.180343],
          [-86.032559, 38.33066],
          [-86.032817, 38.417898],
          [-86.014446, 38.418683],
          [-86.014139, 38.419079],
          [-85.99462, 38.418345],
          [-85.807704, 38.404443],
          [-85.760844, 38.350218],
          [-85.791575, 38.288566],
          [-85.816164, 38.282969]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13127",
      state: "13",
      county: "127",
      name: "Glynn County",
      censusArea: 419.753,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.44317, 31.016661],
          [-81.572692, 31.097269],
          [-81.766322, 31.169595],
          [-81.731694, 31.330048],
          [-81.69199, 31.400519],
          [-81.62429, 31.452982],
          [-81.435874, 31.313263],
          [-81.269906, 31.294489],
          [-81.274688, 31.289454],
          [-81.276862, 31.254734],
          [-81.282842, 31.24433],
          [-81.289136, 31.225487],
          [-81.288403, 31.211065],
          [-81.293359, 31.206332],
          [-81.304957, 31.206173],
          [-81.314183, 31.207938],
          [-81.339028, 31.186918],
          [-81.35488, 31.167204],
          [-81.360791, 31.155903],
          [-81.359349, 31.149166],
          [-81.368241, 31.136534],
          [-81.38683, 31.133214],
          [-81.399677, 31.134113],
          [-81.402096, 31.125383],
          [-81.403732, 31.107115],
          [-81.401209, 31.086143],
          [-81.401267, 31.072781],
          [-81.415123, 31.026718],
          [-81.420474, 31.016703],
          [-81.424732, 31.013678],
          [-81.432475, 31.012991],
          [-81.43471, 31.014641],
          [-81.434923, 31.017804],
          [-81.44317, 31.016661]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13133",
      state: "13",
      county: "133",
      name: "Greene County",
      censusArea: 387.435,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.164207, 33.35503],
          [-83.279931, 33.483438],
          [-83.269213, 33.533226],
          [-83.406189, 33.698307],
          [-83.28034, 33.761774],
          [-83.118729, 33.698818],
          [-82.995602, 33.693583],
          [-82.951142, 33.658032],
          [-83.014038, 33.580383],
          [-83.012853, 33.469178],
          [-83.164207, 33.35503]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13143",
      state: "13",
      county: "143",
      name: "Haralson County",
      censusArea: 282.165,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.386693, 33.901697],
          [-85.05031, 33.904488],
          [-85.036684, 33.904327],
          [-85.037926, 33.811942],
          [-85.050889, 33.714632],
          [-85.337974, 33.653111],
          [-85.360491, 33.767958],
          [-85.361844, 33.773951],
          [-85.386693, 33.901697]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13153",
      state: "13",
      county: "153",
      name: "Houston County",
      censusArea: 375.541,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.848379, 32.29097],
          [-83.846676, 32.468514],
          [-83.719859, 32.53176],
          [-83.70109, 32.691598],
          [-83.701152, 32.692169],
          [-83.597656, 32.664338],
          [-83.566198, 32.660786],
          [-83.497923, 32.452198],
          [-83.482648, 32.424536],
          [-83.498039, 32.401715],
          [-83.615579, 32.288556],
          [-83.848379, 32.29097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13163",
      state: "13",
      county: "163",
      name: "Jefferson County",
      censusArea: 526.485,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.434149, 32.762258],
          [-82.521052, 32.822356],
          [-82.510851, 32.917754],
          [-82.550197, 33.019247],
          [-82.661917, 33.126331],
          [-82.431957, 33.27483],
          [-82.383829, 33.312106],
          [-82.371972, 33.310879],
          [-82.357647, 33.312717],
          [-82.353498, 33.312318],
          [-82.311539, 33.297146],
          [-82.296855, 33.277349],
          [-82.286507, 33.274135],
          [-82.278539, 33.269174],
          [-82.26758, 33.267397],
          [-82.232554, 33.231211],
          [-82.29118, 33.062827],
          [-82.272353, 32.937796],
          [-82.316512, 32.835772],
          [-82.434149, 32.762258]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13183",
      state: "13",
      county: "183",
      name: "Long County",
      censusArea: 400.293,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.491798, 31.699575],
          [-81.566009, 31.576467],
          [-81.663206, 31.538667],
          [-81.850624, 31.667671],
          [-81.969052, 31.789324],
          [-81.824405, 32.014882],
          [-81.750257, 31.973207],
          [-81.765357, 31.87454],
          [-81.641761, 31.765451],
          [-81.491798, 31.699575]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13193",
      state: "13",
      county: "193",
      name: "Macon County",
      censusArea: 400.641,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.181657, 32.229703],
          [-84.255912, 32.296156],
          [-84.254613, 32.372053],
          [-84.122347, 32.505921],
          [-84.00849, 32.521769],
          [-83.846676, 32.468514],
          [-83.848379, 32.29097],
          [-83.965121, 32.248005],
          [-84.027853, 32.171083],
          [-84.181657, 32.229703]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13207",
      state: "13",
      county: "207",
      name: "Monroe County",
      censusArea: 395.658,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.822261, 33.180238],
          [-83.824585, 33.17564],
          [-83.822929, 33.157856],
          [-83.814056, 33.14846],
          [-83.817268, 33.14154],
          [-83.816048, 33.131816],
          [-83.747004, 33.063373],
          [-83.710685, 32.952792],
          [-83.891922, 32.848351],
          [-84.124275, 32.849562],
          [-84.12334, 32.932184],
          [-84.044113, 32.947874],
          [-84.041498, 33.202629],
          [-83.822261, 33.180238]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13219",
      state: "13",
      county: "219",
      name: "Oconee County",
      censusArea: 184.29,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.505928, 33.81776],
          [-83.647031, 33.906198],
          [-83.537385, 33.965912],
          [-83.275933, 33.847977],
          [-83.28034, 33.761774],
          [-83.406189, 33.698307],
          [-83.505928, 33.81776]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13227",
      state: "13",
      county: "227",
      name: "Pickens County",
      censusArea: 232.057,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.654366, 34.548946],
          [-84.345553, 34.562735],
          [-84.319679, 34.467857],
          [-84.256867, 34.467327],
          [-84.257586, 34.380992],
          [-84.58263, 34.381492],
          [-84.653232, 34.41259],
          [-84.654366, 34.548946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13237",
      state: "13",
      county: "237",
      name: "Putnam County",
      censusArea: 344.639,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.545876, 33.171944],
          [-83.537811, 33.393803],
          [-83.533736, 33.434472],
          [-83.279931, 33.483438],
          [-83.164207, 33.35503],
          [-83.145774, 33.312084],
          [-83.253461, 33.25929],
          [-83.274108, 33.187238],
          [-83.42909, 33.185352],
          [-83.545876, 33.171944]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13249",
      state: "13",
      county: "249",
      name: "Schley County",
      censusArea: 166.906,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.254613, 32.372053],
          [-84.255912, 32.296156],
          [-84.181657, 32.229703],
          [-84.183569, 32.158493],
          [-84.430215, 32.166257],
          [-84.392316, 32.414046],
          [-84.254613, 32.372053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29083",
      state: "29",
      county: "083",
      name: "Henry County",
      censusArea: 696.947,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.064317, 38.56738],
          [-93.51103, 38.55621],
          [-93.511364, 38.548889],
          [-93.511303, 38.548565],
          [-93.512743, 38.512476],
          [-93.523484, 38.207216],
          [-94.049895, 38.213985],
          [-94.075214, 38.214502],
          [-94.065713, 38.447087],
          [-94.064317, 38.56738]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29093",
      state: "29",
      county: "093",
      name: "Iron County",
      censusArea: 550.259,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.535016, 37.643641],
          [-90.549729, 37.317594],
          [-90.550603, 37.272608],
          [-90.735453, 37.27104],
          [-90.772949, 37.373659],
          [-90.768695, 37.603011],
          [-91.155073, 37.588092],
          [-91.153345, 37.69734],
          [-91.146521, 37.740811],
          [-91.100017, 37.740012],
          [-90.645135, 37.734813],
          [-90.647874, 37.642809],
          [-90.535016, 37.643641]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37003",
      state: "37",
      county: "003",
      name: "Alexander County",
      censusArea: 259.994,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.028884, 36.045762],
          [-81.005381, 35.958205],
          [-81.05213, 35.844848],
          [-81.064742, 35.824393],
          [-81.075682, 35.814955],
          [-81.078943, 35.802775],
          [-81.093699, 35.794033],
          [-81.109416, 35.776638],
          [-81.142338, 35.827434],
          [-81.334255, 35.79646],
          [-81.328385, 35.99655],
          [-81.190911, 36.036949],
          [-81.028884, 36.045762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37007",
      state: "37",
      county: "007",
      name: "Anson County",
      censusArea: 531.452,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.098994, 34.810147],
          [-80.320413, 34.813813],
          [-80.277052, 35.196136],
          [-80.174176, 35.147992],
          [-80.07539, 35.143081],
          [-79.999268, 35.085391],
          [-79.927887, 35.117713],
          [-79.875716, 35.037936],
          [-79.84999, 34.899317],
          [-79.927618, 34.806555],
          [-80.027464, 34.808726],
          [-80.042764, 34.809097],
          [-80.072912, 34.809736],
          [-80.077223, 34.809716],
          [-80.098022, 34.810147],
          [-80.098994, 34.810147]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37009",
      state: "37",
      county: "009",
      name: "Ashe County",
      censusArea: 426.135,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.677535, 36.588117],
          [-81.60697, 36.587094],
          [-81.521032, 36.58052],
          [-81.353322, 36.574723],
          [-81.353169, 36.574724],
          [-81.253482, 36.365351],
          [-81.354467, 36.355678],
          [-81.378809, 36.277983],
          [-81.477298, 36.240272],
          [-81.56617, 36.272064],
          [-81.720969, 36.3875],
          [-81.714277, 36.450978],
          [-81.715082, 36.453365],
          [-81.697287, 36.484738],
          [-81.695907, 36.49158],
          [-81.696835, 36.493393],
          [-81.697261, 36.496141],
          [-81.698265, 36.497221],
          [-81.699928, 36.498018],
          [-81.700238, 36.500475],
          [-81.699923, 36.500865],
          [-81.69797, 36.504063],
          [-81.69729, 36.504887],
          [-81.697829, 36.507544],
          [-81.697744, 36.508448],
          [-81.699446, 36.511504],
          [-81.699601, 36.512883],
          [-81.700093, 36.514158],
          [-81.700553, 36.51519],
          [-81.707963, 36.536209],
          [-81.699962, 36.539714],
          [-81.677535, 36.588117]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37033",
      state: "37",
      county: "033",
      name: "Caswell County",
      censusArea: 424.922,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.510961, 36.54074],
          [-79.510647, 36.540738],
          [-79.470047, 36.541025],
          [-79.445961, 36.541195],
          [-79.445687, 36.541218],
          [-79.342696, 36.541382],
          [-79.218638, 36.541579],
          [-79.20948, 36.541594],
          [-79.208686, 36.541571],
          [-79.137936, 36.541739],
          [-79.153733, 36.242117],
          [-79.256964, 36.243866],
          [-79.315828, 36.245331],
          [-79.365366, 36.246411],
          [-79.523399, 36.249851],
          [-79.532096, 36.248584],
          [-79.510961, 36.54074]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37045",
      state: "37",
      county: "045",
      name: "Cleveland County",
      censusArea: 464.252,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.32802, 35.163404],
          [-81.366599, 35.164889],
          [-81.366691, 35.164893],
          [-81.452398, 35.168293],
          [-81.461408, 35.168657],
          [-81.493401, 35.169951],
          [-81.494265, 35.169882],
          [-81.768073, 35.180132],
          [-81.70285, 35.357194],
          [-81.692329, 35.579789],
          [-81.53776, 35.564472],
          [-81.456505, 35.419616],
          [-81.364925, 35.311225],
          [-81.321314, 35.260221],
          [-81.32802, 35.163404]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37063",
      state: "37",
      county: "063",
      name: "Durham County",
      censusArea: 285.975,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.016699, 35.863201],
          [-78.95079, 36.23916],
          [-78.949311, 36.239435],
          [-78.802005, 36.235937],
          [-78.804983, 36.091883],
          [-78.749, 36.071246],
          [-78.69793, 36.012306],
          [-78.831781, 35.867314],
          [-78.90617, 35.867912],
          [-79.016699, 35.863201]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37073",
      state: "37",
      county: "073",
      name: "Gates County",
      censusArea: 340.445,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.541687, 36.550312],
          [-76.541391, 36.550312],
          [-76.491477, 36.510536],
          [-76.491405, 36.468648],
          [-76.45369, 36.378042],
          [-76.539147, 36.357238],
          [-76.556837, 36.355186],
          [-76.559674, 36.351892],
          [-76.696601, 36.296211],
          [-76.762156, 36.352687],
          [-76.931821, 36.40107],
          [-76.916001, 36.543818],
          [-76.915384, 36.543856],
          [-76.807078, 36.550606],
          [-76.781296, 36.550659],
          [-76.541687, 36.550312]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37085",
      state: "37",
      county: "085",
      name: "Harnett County",
      censusArea: 594.987,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.617079, 35.245706],
          [-78.825828, 35.259287],
          [-78.941859, 35.219211],
          [-79.09613, 35.192181],
          [-79.223065, 35.268134],
          [-79.183301, 35.306976],
          [-78.969761, 35.521433],
          [-78.913296, 35.583064],
          [-78.708629, 35.519081],
          [-78.660555, 35.496245],
          [-78.530282, 35.333775],
          [-78.541262, 35.315116],
          [-78.617079, 35.245706]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37097",
      state: "37",
      county: "097",
      name: "Iredell County",
      censusArea: 573.833,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.109416, 35.776638],
          [-81.093699, 35.794033],
          [-81.078943, 35.802775],
          [-81.075682, 35.814955],
          [-81.064742, 35.824393],
          [-81.05213, 35.844848],
          [-81.005381, 35.958205],
          [-81.028884, 36.045762],
          [-80.88218, 36.055731],
          [-80.693198, 36.051114],
          [-80.70767, 35.852913],
          [-80.771518, 35.674926],
          [-80.757239, 35.616265],
          [-80.737069, 35.507163],
          [-80.737289, 35.505633],
          [-80.784339, 35.50584],
          [-80.94703, 35.487699],
          [-80.960029, 35.54781],
          [-80.924548, 35.621735],
          [-81.109416, 35.776638]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37109",
      state: "37",
      county: "109",
      name: "Lincoln County",
      censusArea: 297.938,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.955388, 35.400833],
          [-81.300355, 35.414611],
          [-81.456505, 35.419616],
          [-81.53776, 35.564472],
          [-81.536076, 35.566555],
          [-81.535733, 35.567064],
          [-81.535632, 35.567324],
          [-81.534359, 35.568811],
          [-80.961841, 35.548191],
          [-80.960029, 35.54781],
          [-80.94703, 35.487699],
          [-80.955388, 35.400833]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37123",
      state: "37",
      county: "123",
      name: "Montgomery County",
      censusArea: 491.76,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.769285, 35.511457],
          [-79.698036, 35.251451],
          [-79.614306, 35.163679],
          [-79.851762, 35.183418],
          [-80.07539, 35.143081],
          [-80.098185, 35.255173],
          [-80.051739, 35.372585],
          [-80.182682, 35.504335],
          [-80.066842, 35.505661],
          [-79.769285, 35.511457]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37133",
      state: "37",
      county: "133",
      name: "Onslow County",
      censusArea: 762.744,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.67149, 34.811643],
          [-77.675373, 34.973417],
          [-77.452144, 34.946963],
          [-77.367414, 34.906631],
          [-77.328398, 34.938317],
          [-77.221606, 34.887088],
          [-77.165803, 34.784945],
          [-77.1261, 34.756927],
          [-77.1128, 34.639352],
          [-77.136843, 34.632926],
          [-77.169701, 34.622023],
          [-77.209161, 34.605032],
          [-77.240991, 34.587507],
          [-77.322524, 34.535574],
          [-77.462922, 34.471354],
          [-77.491796, 34.456098],
          [-77.51796, 34.440483],
          [-77.576411, 34.478737],
          [-77.68056, 34.72064],
          [-77.676395, 34.787182],
          [-77.67149, 34.811643]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37145",
      state: "37",
      county: "145",
      name: "Person County",
      censusArea: 392.322,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.7963, 36.541713],
          [-78.794823, 36.526689],
          [-78.802005, 36.235937],
          [-78.949311, 36.239435],
          [-78.95079, 36.23916],
          [-79.153733, 36.242117],
          [-79.137936, 36.541739],
          [-79.126078, 36.541533],
          [-79.124736, 36.541568],
          [-78.971814, 36.542123],
          [-78.970577, 36.542154],
          [-78.942254, 36.542079],
          [-78.942009, 36.542113],
          [-78.91542, 36.541974],
          [-78.914543, 36.541972],
          [-78.7963, 36.541713]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48299",
      state: "48",
      county: "299",
      name: "Llano County",
      censusArea: 934.034,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.964232, 30.498482],
          [-98.964112, 30.500742],
          [-98.964612, 30.921368],
          [-98.445782, 30.921439],
          [-98.376877, 30.83489],
          [-98.432098, 30.78631],
          [-98.371905, 30.740485],
          [-98.438995, 30.673285],
          [-98.351041, 30.486096],
          [-98.411462, 30.502095],
          [-98.59167, 30.499875],
          [-98.964232, 30.498482]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48313",
      state: "48",
      county: "313",
      name: "Madison County",
      censusArea: 466.065,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.617872, 30.930418],
          [-95.863059, 30.864125],
          [-96.16837, 30.822997],
          [-96.24102, 30.973737],
          [-95.973841, 31.09215],
          [-95.76441, 31.094211],
          [-95.663546, 31.083884],
          [-95.681158, 30.986958],
          [-95.617872, 30.930418]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48325",
      state: "48",
      county: "325",
      name: "Medina County",
      censusArea: 1325.356,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.8049, 29.090434],
          [-99.41388, 29.091335],
          [-99.411817, 29.627514],
          [-98.983787, 29.62345],
          [-98.927161, 29.56225],
          [-98.806552, 29.690709],
          [-98.804763, 29.250693],
          [-98.8049, 29.090434]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48335",
      state: "48",
      county: "335",
      name: "Mitchell County",
      censusArea: 911.09,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.660626, 32.525312],
          [-100.662215, 32.380297],
          [-100.665353, 32.085407],
          [-100.821594, 32.08661],
          [-101.183997, 32.087208],
          [-101.174571, 32.524112],
          [-101.174562, 32.527703],
          [-100.660626, 32.525312]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48343",
      state: "48",
      county: "343",
      name: "Morris County",
      censusArea: 251.985,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.746925, 33.328938],
          [-94.65226, 33.268861],
          [-94.653994, 32.879506],
          [-94.705878, 32.879177],
          [-94.705177, 32.892062],
          [-94.720065, 32.90045],
          [-94.719942, 32.904502],
          [-94.819828, 32.982886],
          [-94.808783, 33.363636],
          [-94.746925, 33.328938]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51127",
      state: "51",
      county: "127",
      name: "New Kent County",
      censusArea: 209.729,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.228504, 37.536398],
          [-77.123675, 37.626881],
          [-76.981158, 37.567113],
          [-76.853577, 37.572627],
          [-76.792184, 37.517594],
          [-76.791457, 37.508339],
          [-76.789524, 37.503862],
          [-76.781293, 37.501339],
          [-76.76454, 37.493563],
          [-76.751693, 37.483941],
          [-76.747411, 37.478153],
          [-76.741984, 37.467076],
          [-76.892677, 37.433067],
          [-76.904471, 37.377665],
          [-77.177324, 37.4906],
          [-77.184836, 37.49572],
          [-77.188164, 37.500224],
          [-77.191937, 37.501237],
          [-77.192962, 37.506598],
          [-77.198184, 37.508965],
          [-77.202667, 37.512866],
          [-77.207007, 37.513446],
          [-77.206239, 37.515977],
          [-77.214847, 37.525372],
          [-77.220254, 37.527221],
          [-77.221244, 37.533229],
          [-77.226512, 37.53387],
          [-77.228504, 37.536398]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51133",
      state: "51",
      county: "133",
      name: "Northumberlnd County",
      censusArea: 191.295,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.519536, 38.034814],
          [-76.516547, 38.026566],
          [-76.491998, 38.017222],
          [-76.469343, 38.013544],
          [-76.465291, 38.010226],
          [-76.462542, 37.998572],
          [-76.427487, 37.977038],
          [-76.416299, 37.966828],
          [-76.391439, 37.958742],
          [-76.360211, 37.952329],
          [-76.343848, 37.947345],
          [-76.265998, 37.91138],
          [-76.236725, 37.889174],
          [-76.245072, 37.861918],
          [-76.251358, 37.833072],
          [-76.266057, 37.8174],
          [-76.275178, 37.812664],
          [-76.280544, 37.812597],
          [-76.282592, 37.814109],
          [-76.281985, 37.818068],
          [-76.284904, 37.822308],
          [-76.293525, 37.822717],
          [-76.307482, 37.81235],
          [-76.310307, 37.794849],
          [-76.306489, 37.788646],
          [-76.312108, 37.750522],
          [-76.304917, 37.729913],
          [-76.312858, 37.720338],
          [-76.302803, 37.704474],
          [-76.300067, 37.695364],
          [-76.302545, 37.689],
          [-76.312079, 37.684651],
          [-76.315161, 37.68472],
          [-76.320216, 37.680666],
          [-76.376721, 37.712908],
          [-76.418735, 37.822934],
          [-76.507828, 37.838814],
          [-76.556042, 37.931524],
          [-76.63423, 37.96663],
          [-76.519536, 38.034814]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51167",
      state: "51",
      county: "167",
      name: "Russell County",
      censusArea: 473.822,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.148718, 37.041691],
          [-82.017673, 37.068787],
          [-81.901087, 37.142546],
          [-81.780539, 36.958614],
          [-81.819041, 36.947513],
          [-81.836842, 36.929013],
          [-81.949151, 36.864409],
          [-82.077936, 36.855005],
          [-82.107614, 36.796385],
          [-82.332089, 36.709355],
          [-82.356728, 36.780309],
          [-82.406633, 36.876356],
          [-82.298594, 36.905419],
          [-82.328009, 36.970905],
          [-82.148718, 37.041691]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51179",
      state: "51",
      county: "179",
      name: "Stafford County",
      censusArea: 268.956,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.634835, 38.409713],
          [-77.634157, 38.464508],
          [-77.530914, 38.555929],
          [-77.491569, 38.590899],
          [-77.302457, 38.504683],
          [-77.310334, 38.493926],
          [-77.322622, 38.467131],
          [-77.32544, 38.44885],
          [-77.319036, 38.417803],
          [-77.310719, 38.397669],
          [-77.312201, 38.390958],
          [-77.314848, 38.389579],
          [-77.317288, 38.383576],
          [-77.296077, 38.369797],
          [-77.288145, 38.359477],
          [-77.28835, 38.351286],
          [-77.286202, 38.347025],
          [-77.286202, 38.347024],
          [-77.326692, 38.245136],
          [-77.332738, 38.24584],
          [-77.33886, 38.252093],
          [-77.345931, 38.252227],
          [-77.3604, 38.242423],
          [-77.370301, 38.246576],
          [-77.39085, 38.245589],
          [-77.420148, 38.257986],
          [-77.447126, 38.284614],
          [-77.455692, 38.301341],
          [-77.467053, 38.31866],
          [-77.475137, 38.32096],
          [-77.478996, 38.316693],
          [-77.498754, 38.32543],
          [-77.506782, 38.325925],
          [-77.527185, 38.320655],
          [-77.526243, 38.309531],
          [-77.530289, 38.309172],
          [-77.618727, 38.367835],
          [-77.62141, 38.388727],
          [-77.63271, 38.392627],
          [-77.634216, 38.398366],
          [-77.632438, 38.40737],
          [-77.634835, 38.409713]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51515",
      state: "51",
      county: "515",
      name: "Bedford County",
      lsad: "city",
      censusArea: 6.878,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.543597, 37.324178],
          [-79.535648, 37.355314],
          [-79.487211, 37.345777],
          [-79.503624, 37.316653],
          [-79.543597, 37.324178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51590",
      state: "51",
      county: "590",
      name: "Danville City County",
      lsad: "city",
      censusArea: 42.934,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.470047, 36.541025],
          [-79.456646, 36.576738],
          [-79.501101, 36.61825],
          [-79.422097, 36.600681],
          [-79.398845, 36.644537],
          [-79.304486, 36.582202],
          [-79.342696, 36.541382],
          [-79.445687, 36.541218],
          [-79.445961, 36.541195],
          [-79.470047, 36.541025]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51620",
      state: "51",
      county: "620",
      name: "Franklin City County",
      lsad: "city",
      censusArea: 8.206,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.924629, 36.707729],
          [-76.926142, 36.698587],
          [-76.912221, 36.698408],
          [-76.925167, 36.683583],
          [-76.912015, 36.672717],
          [-76.917742, 36.662368],
          [-76.909672, 36.648175],
          [-76.962339, 36.667515],
          [-76.949327, 36.67873],
          [-76.968027, 36.682971],
          [-76.967212, 36.702588],
          [-76.949535, 36.708617],
          [-76.924629, 36.707729]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51680",
      state: "51",
      county: "680",
      name: "Lynchburg City County",
      lsad: "city",
      censusArea: 49.128,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.183302, 37.465908],
          [-79.085244, 37.394743],
          [-79.214982, 37.333],
          [-79.257188, 37.355608],
          [-79.268727, 37.431028],
          [-79.183302, 37.465908]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51720",
      state: "51",
      county: "720",
      name: "Norton City County",
      lsad: "city",
      censusArea: 7.481,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.660291, 36.918804],
          [-82.616375, 36.956694],
          [-82.58889, 36.951182],
          [-82.615137, 36.905251],
          [-82.660291, 36.918804]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51740",
      state: "51",
      county: "740",
      name: "Portsmouth City County",
      lsad: "city",
      censusArea: 33.653,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.344663, 36.919313],
          [-76.340769, 36.901603],
          [-76.32848, 36.893219],
          [-76.336784, 36.875027],
          [-76.313533, 36.858533],
          [-76.294262, 36.841647],
          [-76.289962, 36.822048],
          [-76.375679, 36.786062],
          [-76.420368, 36.866348],
          [-76.406667, 36.878448],
          [-76.406908, 36.897507],
          [-76.387567, 36.899547],
          [-76.385867, 36.923247],
          [-76.353765, 36.922747],
          [-76.345569, 36.924531],
          [-76.344663, 36.919313]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51830",
      state: "51",
      county: "830",
      name: "Williamsburg City County",
      lsad: "city",
      censusArea: 9.022,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.727588, 37.304921],
          [-76.727264, 37.298783],
          [-76.715314, 37.280562],
          [-76.710367, 37.27765],
          [-76.709775, 37.28229],
          [-76.691736, 37.287459],
          [-76.690217, 37.292656],
          [-76.681182, 37.292721],
          [-76.677054, 37.27717],
          [-76.684705, 37.270068],
          [-76.679468, 37.265652],
          [-76.664621, 37.25049],
          [-76.726003, 37.249901],
          [-76.742487, 37.275092],
          [-76.725272, 37.279686],
          [-76.739665, 37.306255],
          [-76.727588, 37.304921]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12086",
      state: "12",
      county: "086",
      name: "Miami-Dade County",
      censusArea: 1897.716,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.44049, 25.956861],
          [-80.326712, 25.957118],
          [-80.294972, 25.95677],
          [-80.295187, 25.97057],
          [-80.278764, 25.970968],
          [-80.246763, 25.971874],
          [-80.143501, 25.974511],
          [-80.142227, 25.974674],
          [-80.133867, 25.974868],
          [-80.133862, 25.97411],
          [-80.123874, 25.974484],
          [-80.118078, 25.975158],
          [-80.117798, 25.975152],
          [-80.117904, 25.915772],
          [-80.12087, 25.883152],
          [-80.119684, 25.841043],
          [-80.122056, 25.817913],
          [-80.127394, 25.791224],
          [-80.127987, 25.772245],
          [-80.137476, 25.750301],
          [-80.144, 25.740812],
          [-80.152896, 25.702855],
          [-80.154082, 25.683283],
          [-80.152303, 25.676759],
          [-80.154972, 25.66549],
          [-80.160903, 25.664897],
          [-80.176916, 25.685062],
          [-80.170392, 25.710565],
          [-80.164461, 25.721833],
          [-80.166241, 25.72895],
          [-80.172765, 25.737847],
          [-80.184626, 25.745557],
          [-80.197674, 25.74437],
          [-80.229107, 25.732509],
          [-80.240376, 25.724206],
          [-80.244528, 25.717089],
          [-80.250459, 25.688028],
          [-80.265879, 25.658373],
          [-80.267065, 25.651849],
          [-80.277147, 25.637022],
          [-80.288416, 25.630498],
          [-80.296719, 25.622195],
          [-80.301464, 25.613299],
          [-80.305615, 25.593134],
          [-80.305615, 25.575342],
          [-80.302057, 25.567632],
          [-80.313918, 25.539164],
          [-80.324594, 25.535605],
          [-80.328746, 25.53264],
          [-80.339421, 25.499427],
          [-80.339421, 25.478669],
          [-80.337049, 25.465621],
          [-80.328152, 25.443084],
          [-80.320442, 25.437153],
          [-80.326373, 25.422919],
          [-80.32578, 25.39801],
          [-80.320442, 25.391486],
          [-80.31036, 25.389707],
          [-80.306801, 25.384369],
          [-80.31036, 25.3731],
          [-80.335269, 25.338701],
          [-80.352469, 25.329805],
          [-80.361662, 25.327433],
          [-80.374116, 25.31735],
          [-80.383013, 25.301337],
          [-80.385978, 25.288289],
          [-80.419191, 25.26338],
          [-80.437815, 25.253077],
          [-80.447066, 25.24796],
          [-80.462832, 25.236248],
          [-80.467824, 25.23254],
          [-80.468366, 25.231977],
          [-80.469843, 25.230442],
          [-80.483244, 25.216526],
          [-80.487591, 25.207829],
          [-80.488035, 25.206942],
          [-80.488216, 25.206757],
          [-80.495341, 25.199463],
          [-80.496179, 25.199637],
          [-80.498361, 25.200091],
          [-80.498644, 25.20015],
          [-80.502983, 25.20316],
          [-80.503125, 25.203259],
          [-80.50778, 25.206488],
          [-80.508113, 25.206719],
          [-80.508116, 25.206725],
          [-80.512928, 25.216719],
          [-80.514724, 25.217702],
          [-80.515297, 25.218016],
          [-80.520359, 25.220788],
          [-80.52319, 25.22008],
          [-80.525784, 25.218649],
          [-80.530207, 25.216207],
          [-80.540947, 25.21821],
          [-80.54204, 25.22185],
          [-80.540947, 25.224945],
          [-80.540765, 25.229678],
          [-80.548491, 25.236535],
          [-80.558223, 25.239012],
          [-80.570785, 25.239366],
          [-80.589186, 25.223796],
          [-80.587593, 25.207695],
          [-80.584783, 25.200695],
          [-80.584771, 25.200665],
          [-80.594338, 25.193437],
          [-80.601627, 25.187931],
          [-80.609607, 25.181902],
          [-80.609609, 25.181901],
          [-80.618989, 25.177345],
          [-80.619024, 25.177328],
          [-80.633992, 25.176829],
          [-80.639873, 25.176633],
          [-80.640275, 25.17662],
          [-80.640823, 25.176364],
          [-80.642456, 25.175601],
          [-80.645737, 25.174069],
          [-80.645822, 25.174029],
          [-80.646099, 25.173599],
          [-80.648565, 25.169773],
          [-80.649251, 25.168708],
          [-80.656944, 25.168216],
          [-80.660692, 25.167976],
          [-80.66995, 25.167384],
          [-80.674324, 25.167104],
          [-80.679852, 25.166751],
          [-80.683909, 25.166491],
          [-80.688361, 25.164711],
          [-80.695038, 25.157588],
          [-80.70127, 25.146683],
          [-80.703718, 25.139115],
          [-80.715099, 25.140872],
          [-80.721886, 25.145101],
          [-80.728602, 25.144316],
          [-80.734741, 25.143598],
          [-80.73833, 25.143178],
          [-80.742877, 25.142646],
          [-80.746379, 25.148559],
          [-80.746264, 25.155303],
          [-80.751866, 25.163762],
          [-80.757581, 25.166734],
          [-80.795456, 25.172337],
          [-80.796219, 25.17245],
          [-80.815193, 25.164956],
          [-80.817116, 25.164196],
          [-80.823441, 25.161698],
          [-80.826465, 25.160504],
          [-80.82653, 25.160478],
          [-80.826544, 25.160509],
          [-80.827179, 25.161888],
          [-80.827489, 25.162562],
          [-80.828435, 25.164619],
          [-80.830034, 25.168094],
          [-80.838227, 25.174791],
          [-80.843703, 25.176312],
          [-80.846395, 25.17706],
          [-80.8464, 25.17706],
          [-80.858167, 25.176576],
          [-80.87319, 25.363993],
          [-80.873096, 25.805377],
          [-80.872932, 25.979434],
          [-80.680038, 25.978749],
          [-80.680016, 25.956857],
          [-80.44049, 25.956861]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12103",
      state: "12",
      county: "103",
      name: "Pinellas County",
      censusArea: 273.799,
      stateName: "Florida"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-82.821585, 27.964443],
            [-82.829801, 27.968469],
            [-82.828625, 28.019795],
            [-82.823025, 28.030695],
            [-82.823063, 28.044758],
            [-82.826282, 28.05345],
            [-82.831825, 28.062893],
            [-82.836326, 28.073193],
            [-82.833225, 28.082193],
            [-82.830525, 28.085293],
            [-82.826125, 28.083793],
            [-82.823063, 28.068258],
            [-82.818288, 28.057613],
            [-82.813435, 28.03716],
            [-82.815168, 28.012547],
            [-82.821408, 28.008387],
            [-82.821755, 28.002494],
            [-82.817248, 27.992094],
            [-82.815168, 27.973721],
            [-82.821585, 27.964443]
          ]
        ],
        [
          [
            [-82.679019, 27.696054],
            [-82.679251, 27.694665],
            [-82.693748, 27.700217],
            [-82.713629, 27.698661],
            [-82.718822, 27.692007],
            [-82.723022, 27.671208],
            [-82.721622, 27.663908],
            [-82.716322, 27.651409],
            [-82.712555, 27.646647],
            [-82.698091, 27.638858],
            [-82.705017, 27.62531],
            [-82.733076, 27.612972],
            [-82.736552, 27.617326],
            [-82.737312, 27.623115],
            [-82.73779, 27.626758],
            [-82.739122, 27.636909],
            [-82.738022, 27.706807],
            [-82.740323, 27.718206],
            [-82.746223, 27.731306],
            [-82.753723, 27.736306],
            [-82.760923, 27.745205],
            [-82.770023, 27.767904],
            [-82.783124, 27.783804],
            [-82.790224, 27.791603],
            [-82.820433, 27.813742],
            [-82.828561, 27.822254],
            [-82.846526, 27.854301],
            [-82.849126, 27.8632],
            [-82.851126, 27.8863],
            [-82.847826, 27.910199],
            [-82.840882, 27.937162],
            [-82.831388, 27.962117],
            [-82.824875, 27.960201],
            [-82.821975, 27.956868],
            [-82.830819, 27.930926],
            [-82.838484, 27.909111],
            [-82.832155, 27.909242],
            [-82.820715, 27.927268],
            [-82.808745, 27.953112],
            [-82.805462, 27.960201],
            [-82.79782, 27.990563],
            [-82.792635, 28.01116],
            [-82.792635, 28.032307],
            [-82.782724, 28.055894],
            [-82.783824, 28.106292],
            [-82.782181, 28.120287],
            [-82.781324, 28.127591],
            [-82.786624, 28.144991],
            [-82.790724, 28.15249],
            [-82.799024, 28.15179],
            [-82.808474, 28.154803],
            [-82.805097, 28.172181],
            [-82.651165, 28.173266],
            [-82.648557, 28.03863],
            [-82.648616, 27.966309],
            [-82.716522, 27.958398],
            [-82.720522, 27.955798],
            [-82.724122, 27.948098],
            [-82.721975, 27.941819],
            [-82.721429, 27.940222],
            [-82.720395, 27.937199],
            [-82.720122, 27.936399],
            [-82.710022, 27.928299],
            [-82.691621, 27.924899],
            [-82.685121, 27.916299],
            [-82.671221, 27.913],
            [-82.628063, 27.910397],
            [-82.63422, 27.9037],
            [-82.63212, 27.8911],
            [-82.61002, 27.873501],
            [-82.567919, 27.883701],
            [-82.567826, 27.881537],
            [-82.566819, 27.858002],
            [-82.598443, 27.857582],
            [-82.594819, 27.843402],
            [-82.589319, 27.835702],
            [-82.586519, 27.816703],
            [-82.60742, 27.798904],
            [-82.622723, 27.779868],
            [-82.63052, 27.753905],
            [-82.62502, 27.732706],
            [-82.62572, 27.727006],
            [-82.63362, 27.710607],
            [-82.63982, 27.703907],
            [-82.652521, 27.700307],
            [-82.662921, 27.702307],
            [-82.663246, 27.702442],
            [-82.667445, 27.704179],
            [-82.668772, 27.704728],
            [-82.670449, 27.705422],
            [-82.670942, 27.705626],
            [-82.671549, 27.705877],
            [-82.671621, 27.705907],
            [-82.672093, 27.705932],
            [-82.673061, 27.705983],
            [-82.674123, 27.706039],
            [-82.677321, 27.706207],
            [-82.679019, 27.696054]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12123",
      state: "12",
      county: "123",
      name: "Taylor County",
      censusArea: 1043.305,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.856987, 30.273318],
          [-83.829465, 30.287863],
          [-83.820235, 30.30354],
          [-83.468768, 30.304212],
          [-83.469267, 30.260351],
          [-83.36726, 30.26044],
          [-83.37089, 29.887934],
          [-83.318859, 29.822623],
          [-83.373351, 29.665951],
          [-83.412278, 29.666922],
          [-83.412768, 29.668485],
          [-83.414701, 29.670536],
          [-83.436259, 29.677389],
          [-83.444635, 29.677155],
          [-83.448194, 29.675254],
          [-83.455356, 29.676444],
          [-83.483143, 29.690478],
          [-83.483567, 29.698542],
          [-83.493728, 29.708388],
          [-83.512716, 29.71648],
          [-83.537645, 29.72306],
          [-83.547172, 29.732223],
          [-83.554993, 29.7426],
          [-83.566018, 29.761434],
          [-83.578955, 29.768378],
          [-83.584716, 29.77608],
          [-83.586089, 29.784644],
          [-83.583045, 29.787307],
          [-83.581903, 29.792063],
          [-83.585899, 29.811754],
          [-83.595493, 29.827984],
          [-83.605244, 29.836387],
          [-83.618568, 29.842336],
          [-83.63798, 29.886073],
          [-83.659951, 29.899524],
          [-83.679219, 29.918513],
          [-83.686423, 29.923735],
          [-83.757249, 29.957943],
          [-83.788729, 29.976982],
          [-83.82869, 29.983187],
          [-83.845427, 29.998068],
          [-83.93151, 30.039068],
          [-83.933668, 30.041152],
          [-83.931879, 30.044175],
          [-83.933432, 30.046305],
          [-83.95968, 30.064943],
          [-83.991607, 30.08392],
          [-83.915533, 30.245386],
          [-83.856987, 30.273318]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13259",
      state: "13",
      county: "259",
      name: "Stewart County",
      censusArea: 458.733,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.923638, 32.231539],
          [-84.65892, 32.232852],
          [-84.649319, 32.232953],
          [-84.65582, 31.920308],
          [-84.907006, 31.924465],
          [-85.062246, 31.991857],
          [-85.050212, 32.024196],
          [-85.056029, 32.063055],
          [-85.047865, 32.142033],
          [-84.995929, 32.184852],
          [-84.964828, 32.194952],
          [-84.923638, 32.231539]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13271",
      state: "13",
      county: "271",
      name: "Telfair County",
      censusArea: 437.303,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.995698, 31.780983],
          [-83.177469, 31.847857],
          [-83.205739, 31.900325],
          [-82.942066, 32.124074],
          [-82.955466, 32.135274],
          [-82.934566, 32.153274],
          [-82.927865, 32.135274],
          [-82.887657, 32.079891],
          [-82.729112, 32.006878],
          [-82.645659, 31.918883],
          [-82.836366, 31.816154],
          [-82.86309, 31.783005],
          [-82.995698, 31.780983]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13283",
      state: "13",
      county: "283",
      name: "Treutlen County",
      censusArea: 199.436,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.409118, 32.35383],
          [-82.409118, 32.35381],
          [-82.409127, 32.353772],
          [-82.40913, 32.353738],
          [-82.655486, 32.297561],
          [-82.721964, 32.309283],
          [-82.737137, 32.33803],
          [-82.647733, 32.512507],
          [-82.488941, 32.49183],
          [-82.38194, 32.440596],
          [-82.409118, 32.35383]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13297",
      state: "13",
      county: "297",
      name: "Walton County",
      censusArea: 325.68,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.914823, 33.744203],
          [-83.953332, 33.768034],
          [-83.954499, 33.76892],
          [-83.982033, 33.786054],
          [-83.799104, 33.929844],
          [-83.763835, 33.896682],
          [-83.647031, 33.906198],
          [-83.505928, 33.81776],
          [-83.680896, 33.596919],
          [-83.77735, 33.658301],
          [-83.914823, 33.744203]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20025",
      state: "20",
      county: "025",
      name: "Clark County",
      censusArea: 974.632,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.558068, 36.999528],
          [-99.625399, 36.999671],
          [-99.657658, 37.000197],
          [-99.774255, 37.000837],
          [-99.774816, 37.000841],
          [-99.786016, 37.000931],
          [-99.995201, 37.001631],
          [-100.001286, 37.001699],
          [-100.002563, 37.001706],
          [-100.005706, 37.001726],
          [-100.089484, 37.002092],
          [-100.091529, 37.386892],
          [-100.107248, 37.474836],
          [-99.774512, 37.467404],
          [-99.556119, 37.467722],
          [-99.555774, 37.381112],
          [-99.543189, 37.381124],
          [-99.541116, 36.999573],
          [-99.558068, 36.999528]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20031",
      state: "20",
      county: "031",
      name: "Coffey County",
      censusArea: 626.947,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.508267, 38.433933],
          [-95.508328, 38.390278],
          [-95.51897, 38.03823],
          [-95.959046, 38.040195],
          [-95.958862, 38.170939],
          [-95.950282, 38.434105],
          [-95.508267, 38.433933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20033",
      state: "20",
      county: "033",
      name: "Comanche County",
      censusArea: 788.298,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.000303, 36.99951],
          [-99.029337, 36.999595],
          [-99.124883, 36.99942],
          [-99.129449, 36.999422],
          [-99.24812, 36.999565],
          [-99.277506, 36.999579],
          [-99.456203, 36.999471],
          [-99.484333, 36.999626],
          [-99.500395, 36.999576],
          [-99.500395, 36.999637],
          [-99.502665, 36.999645],
          [-99.504093, 36.999648],
          [-99.508574, 36.999658],
          [-99.541116, 36.999573],
          [-99.543189, 37.381124],
          [-99.011499, 37.384263],
          [-99.001294, 37.384296],
          [-99.000303, 36.99951]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20045",
      state: "20",
      county: "045",
      name: "Douglas County",
      censusArea: 455.871,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.056207, 38.811432],
          [-95.056412, 38.738587],
          [-95.500738, 38.738808],
          [-95.501038, 38.739721],
          [-95.500724, 38.869815],
          [-95.500254, 39.056656],
          [-95.285425, 39.032162],
          [-95.187103, 39.044109],
          [-95.164123, 38.98163],
          [-95.056258, 38.98212],
          [-95.056207, 38.811432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20055",
      state: "20",
      county: "055",
      name: "Finney County",
      censusArea: 1301.967,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.227137, 38.262237],
          [-100.22661, 38.001015],
          [-100.664166, 38.00253],
          [-100.652251, 37.736275],
          [-101.089667, 37.736337],
          [-101.103778, 37.829035],
          [-101.103268, 38.264557],
          [-100.687836, 38.26415],
          [-100.244393, 38.26229],
          [-100.227137, 38.262237]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20069",
      state: "20",
      county: "069",
      name: "Gray County",
      censusArea: 868.867,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.22661, 38.001015],
          [-100.22709, 37.914018],
          [-100.214637, 37.826808],
          [-100.216229, 37.47519],
          [-100.652642, 37.474848],
          [-100.652251, 37.736275],
          [-100.664166, 38.00253],
          [-100.22661, 38.001015]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20081",
      state: "20",
      county: "081",
      name: "Haskell County",
      censusArea: 577.521,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.652251, 37.736275],
          [-100.652642, 37.474848],
          [-100.652728, 37.387627],
          [-101.067904, 37.387779],
          [-101.089653, 37.387721],
          [-101.089667, 37.736337],
          [-100.652251, 37.736275]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20093",
      state: "20",
      county: "093",
      name: "Kearny County",
      censusArea: 870.543,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.542312, 38.263207],
          [-101.125438, 38.264509],
          [-101.103268, 38.264557],
          [-101.103778, 37.829035],
          [-101.089667, 37.736337],
          [-101.527063, 37.736314],
          [-101.542757, 37.827594],
          [-101.542312, 38.263207]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20101",
      state: "20",
      county: "101",
      name: "Lane County",
      censusArea: 717.455,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.244393, 38.26229],
          [-100.687836, 38.26415],
          [-100.684637, 38.271122],
          [-100.688006, 38.700021],
          [-100.2472, 38.698165],
          [-100.244393, 38.26229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20111",
      state: "20",
      county: "111",
      name: "Lyon County",
      censusArea: 847.474,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.357277, 38.17266],
          [-96.35378, 38.521657],
          [-96.352613, 38.739021],
          [-95.945924, 38.739112],
          [-95.950282, 38.434105],
          [-95.958862, 38.170939],
          [-96.357277, 38.17266]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20123",
      state: "20",
      county: "123",
      name: "Mitchell County",
      censusArea: 701.792,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.929097, 39.306397],
          [-97.931614, 39.277094],
          [-97.929746, 39.219273],
          [-98.490149, 39.21978],
          [-98.487384, 39.567492],
          [-97.931844, 39.566921],
          [-97.928462, 39.566915],
          [-97.929097, 39.306397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20133",
      state: "20",
      county: "133",
      name: "Neosho County",
      censusArea: 571.472,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.088012, 37.674519],
          [-95.088189, 37.383837],
          [-95.52113, 37.38399],
          [-95.52556, 37.383979],
          [-95.525499, 37.732759],
          [-95.088082, 37.732476],
          [-95.088012, 37.674519]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20139",
      state: "20",
      county: "139",
      name: "Osage County",
      censusArea: 705.518,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.508267, 38.433933],
          [-95.950282, 38.434105],
          [-95.945924, 38.739112],
          [-95.946587, 38.869973],
          [-95.500724, 38.869815],
          [-95.501038, 38.739721],
          [-95.500738, 38.738808],
          [-95.508267, 38.433933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20151",
      state: "20",
      county: "151",
      name: "Pratt County",
      censusArea: 735.045,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.472257, 37.824497],
          [-98.464899, 37.824512],
          [-98.464804, 37.732675],
          [-98.464663, 37.471013],
          [-99.012598, 37.470421],
          [-99.01355, 37.73297],
          [-99.013318, 37.825336],
          [-98.472257, 37.824497]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20159",
      state: "20",
      county: "159",
      name: "Rice County",
      censusArea: 726.237,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.480377, 38.521841],
          [-97.924269, 38.522755],
          [-97.922136, 38.173713],
          [-98.472794, 38.172757],
          [-98.479841, 38.26079],
          [-98.480377, 38.521841]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20169",
      state: "20",
      county: "169",
      name: "Saline County",
      censusArea: 720.225,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.928595, 38.871102],
          [-97.928967, 38.882176],
          [-97.928702, 38.886208],
          [-97.92856, 38.958395],
          [-97.371614, 38.957852],
          [-97.371911, 38.609353],
          [-97.924795, 38.60988],
          [-97.928595, 38.871102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37157",
      state: "37",
      county: "157",
      name: "Rockingham County",
      censusArea: 565.551,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.027269, 36.542495],
          [-79.967511, 36.542502],
          [-79.966979, 36.542475],
          [-79.920239, 36.542365],
          [-79.714855, 36.541884],
          [-79.667309, 36.541772],
          [-79.666827, 36.541772],
          [-79.510961, 36.54074],
          [-79.532096, 36.248584],
          [-79.532329, 36.241427],
          [-79.532365, 36.241342],
          [-79.700569, 36.246043],
          [-79.797538, 36.248806],
          [-79.808899, 36.249619],
          [-80.027352, 36.257507],
          [-80.03554, 36.257351],
          [-80.033218, 36.344867],
          [-80.027269, 36.542495]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37173",
      state: "37",
      county: "173",
      name: "Swain County",
      censusArea: 527.996,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.662957, 35.569138],
          [-83.640498, 35.566075],
          [-83.498335, 35.562981],
          [-83.485527, 35.568204],
          [-83.480617, 35.576633],
          [-83.478523, 35.579202],
          [-83.479082, 35.583316],
          [-83.445802, 35.611803],
          [-83.366941, 35.638728],
          [-83.297154, 35.65775],
          [-83.254231, 35.695807],
          [-83.181894, 35.671127],
          [-83.186052, 35.514573],
          [-83.351373, 35.468354],
          [-83.339707, 35.331004],
          [-83.680043, 35.279414],
          [-83.585544, 35.434867],
          [-83.952676, 35.460763],
          [-83.933876, 35.472438],
          [-83.880074, 35.518745],
          [-83.756917, 35.563604],
          [-83.676268, 35.570289],
          [-83.662957, 35.569138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37179",
      state: "37",
      county: "179",
      name: "Union County",
      censusArea: 631.52,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.561657, 34.817481],
          [-80.561674, 34.817481],
          [-80.621222, 34.818174],
          [-80.625993, 34.818239],
          [-80.626077, 34.818217],
          [-80.644656, 34.818568],
          [-80.646601, 34.818592],
          [-80.771792, 34.819646],
          [-80.777712, 34.819697],
          [-80.79675, 34.825281],
          [-80.782042, 34.935782],
          [-80.806461, 34.962894],
          [-80.806784, 34.963249],
          [-80.840461, 35.001566],
          [-80.770385, 35.034234],
          [-80.550297, 35.208381],
          [-80.506418, 35.185226],
          [-80.505334, 35.184048],
          [-80.48259, 35.180278],
          [-80.468247, 35.166815],
          [-80.438682, 35.161758],
          [-80.428251, 35.167347],
          [-80.277052, 35.196136],
          [-80.320413, 34.813813],
          [-80.399871, 34.815128],
          [-80.417014, 34.815508],
          [-80.418433, 34.815622],
          [-80.419586, 34.815581],
          [-80.425902, 34.81581],
          [-80.434843, 34.815968],
          [-80.448766, 34.816332],
          [-80.45166, 34.816396],
          [-80.485683, 34.816732],
          [-80.561657, 34.817481]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39059",
      state: "39",
      county: "059",
      name: "Guernsey County",
      censusArea: 522.254,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.694146, 39.842636],
          [-81.728611, 39.931728],
          [-81.716276, 40.152166],
          [-81.622683, 40.221308],
          [-81.338057, 40.214253],
          [-81.339565, 40.171999],
          [-81.225924, 40.170075],
          [-81.234053, 39.95127],
          [-81.386006, 39.950694],
          [-81.464108, 39.89455],
          [-81.57859, 39.897683],
          [-81.580313, 39.839104],
          [-81.694146, 39.842636]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39069",
      state: "39",
      county: "069",
      name: "Henry County",
      censusArea: 416.01,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.228222, 41.165862],
          [-84.228454, 41.42781],
          [-84.341664, 41.427568],
          [-84.341902, 41.485519],
          [-83.882943, 41.487543],
          [-83.883234, 41.414503],
          [-83.881149, 41.167824],
          [-84.228222, 41.165862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39083",
      state: "39",
      county: "083",
      name: "Knox County",
      censusArea: 525.494,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.364344, 40.240957],
          [-82.476114, 40.264703],
          [-82.750747, 40.276996],
          [-82.750235, 40.284087],
          [-82.749896, 40.288636],
          [-82.744931, 40.349603],
          [-82.646503, 40.345076],
          [-82.623609, 40.549879],
          [-82.374898, 40.550871],
          [-82.336962, 40.555001],
          [-82.22011, 40.568206],
          [-82.179069, 40.573112],
          [-82.184607, 40.45628],
          [-82.184467, 40.454262],
          [-82.195911, 40.239071],
          [-82.326025, 40.239725],
          [-82.364344, 40.240957]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39091",
      state: "39",
      county: "091",
      name: "Logan County",
      censusArea: 458.429,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.551338, 40.22937],
          [-84.014763, 40.273459],
          [-84.002372, 40.483115],
          [-83.993867, 40.535174],
          [-83.879932, 40.538708],
          [-83.520227, 40.504077],
          [-83.551338, 40.22937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39103",
      state: "39",
      county: "103",
      name: "Medina County",
      censusArea: 421.358,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.973895, 41.199831],
          [-81.972485, 41.274829],
          [-81.927005, 41.274818],
          [-81.920513, 41.275379],
          [-81.914792, 41.275469],
          [-81.906227, 41.275392],
          [-81.897853, 41.274916],
          [-81.878053, 41.275044],
          [-81.68495, 41.277146],
          [-81.685646, 41.202132],
          [-81.688491, 40.98859],
          [-82.129334, 40.991807],
          [-82.173359, 40.992046],
          [-82.171492, 41.063537],
          [-82.169875, 41.137102],
          [-82.074266, 41.136456],
          [-82.072465, 41.19985],
          [-81.973895, 41.199831]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39115",
      state: "39",
      county: "115",
      name: "Morgan County",
      censusArea: 416.423,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.844863, 39.450216],
          [-82.055779, 39.460053],
          [-82.047528, 39.550825],
          [-82.024219, 39.724686],
          [-82.076639, 39.77096],
          [-81.697442, 39.755572],
          [-81.639886, 39.75342],
          [-81.6432, 39.665681],
          [-81.586132, 39.663993],
          [-81.588185, 39.586973],
          [-81.714447, 39.584236],
          [-81.708527, 39.480775],
          [-81.823158, 39.494072],
          [-81.844863, 39.450216]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39127",
      state: "39",
      county: "127",
      name: "Perry County",
      censusArea: 407.971,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.076639, 39.77096],
          [-82.024219, 39.724686],
          [-82.047528, 39.550825],
          [-82.159161, 39.556574],
          [-82.37989, 39.596738],
          [-82.374531, 39.654959],
          [-82.396594, 39.833057],
          [-82.472402, 39.837223],
          [-82.462813, 39.93038],
          [-82.233974, 39.91326],
          [-82.162372, 39.909374],
          [-82.170047, 39.82075],
          [-82.072934, 39.816227],
          [-82.076639, 39.77096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39137",
      state: "39",
      county: "137",
      name: "Putnam County",
      censusArea: 482.522,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.341602, 41.165524],
          [-84.228222, 41.165862],
          [-83.881149, 41.167824],
          [-83.880423, 40.920429],
          [-84.109516, 40.90473],
          [-84.109586, 40.860994],
          [-84.340531, 40.859099],
          [-84.398407, 40.903265],
          [-84.399486, 40.990315],
          [-84.341911, 40.990692],
          [-84.341602, 41.165524]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39153",
      state: "39",
      county: "153",
      name: "Summit County",
      censusArea: 412.748,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.590542, 41.277554],
          [-81.598061, 41.350913],
          [-81.592653, 41.351119],
          [-81.450331, 41.349148],
          [-81.399585, 41.348573],
          [-81.391694, 41.348272],
          [-81.391686, 41.3413],
          [-81.391933, 41.336635],
          [-81.391593, 41.33155],
          [-81.391831, 41.276654],
          [-81.392357, 41.136952],
          [-81.392114, 41.135722],
          [-81.393429, 40.988528],
          [-81.420418, 40.906504],
          [-81.467584, 40.907965],
          [-81.64595, 40.914257],
          [-81.647736, 40.914386],
          [-81.688491, 40.98859],
          [-81.685646, 41.202132],
          [-81.68495, 41.277146],
          [-81.590542, 41.277554]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39165",
      state: "39",
      county: "165",
      name: "Warren County",
      censusArea: 401.314,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.988613, 39.444397],
          [-84.006782, 39.255068],
          [-84.259431, 39.270796],
          [-84.353209, 39.292287],
          [-84.348199, 39.348512],
          [-84.332089, 39.548803],
          [-84.365232, 39.589493],
          [-84.187334, 39.583081],
          [-84.114195, 39.577983],
          [-83.977005, 39.569169],
          [-83.988613, 39.444397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40005",
      state: "40",
      county: "005",
      name: "Atoka County",
      censusArea: 975.517,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.671976, 34.593852],
          [-95.6717, 34.506799],
          [-95.779425, 34.506556],
          [-95.779376, 34.156699],
          [-95.991578, 34.156815],
          [-96.40764, 34.157317],
          [-96.40733, 34.418391],
          [-96.145096, 34.418677],
          [-96.145342, 34.506102],
          [-96.091873, 34.506155],
          [-96.092043, 34.680556],
          [-95.882103, 34.680475],
          [-95.881987, 34.593528],
          [-95.671976, 34.593852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40015",
      state: "40",
      county: "015",
      name: "Caddo County",
      censusArea: 1278.289,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.619816, 34.854953],
          [-98.621054, 35.097352],
          [-98.623336, 35.464225],
          [-98.623149, 35.551597],
          [-98.313251, 35.551523],
          [-98.306326, 35.377239],
          [-98.096014, 35.377753],
          [-98.092645, 34.854919],
          [-98.619816, 34.854953]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40027",
      state: "40",
      county: "027",
      name: "Cleveland County",
      censusArea: 538.766,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.671369, 35.377151],
          [-97.423566, 35.377316],
          [-97.264992, 35.377368],
          [-97.142114, 35.37686],
          [-97.142349, 34.928176],
          [-97.341509, 34.964001],
          [-97.354192, 35.076656],
          [-97.54146, 35.214165],
          [-97.556987, 35.28844],
          [-97.671529, 35.337508],
          [-97.671369, 35.377151]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53011",
      state: "53",
      county: "011",
      name: "Clark County",
      censusArea: 629.004,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.245633, 46.053932],
          [-122.248993, 45.547745],
          [-122.266701, 45.543841],
          [-122.294901, 45.543541],
          [-122.331502, 45.548241],
          [-122.352802, 45.569441],
          [-122.380302, 45.575941],
          [-122.492259, 45.583281],
          [-122.643907, 45.609739],
          [-122.675008, 45.618039],
          [-122.738109, 45.644138],
          [-122.76381, 45.657138],
          [-122.774511, 45.680437],
          [-122.772511, 45.699637],
          [-122.762182, 45.728598],
          [-122.760108, 45.734413],
          [-122.761451, 45.759163],
          [-122.769532, 45.780583],
          [-122.795605, 45.81],
          [-122.795963, 45.825024],
          [-122.785515, 45.850536],
          [-122.738996, 45.915287],
          [-122.477104, 45.988089],
          [-122.357765, 45.956915],
          [-122.245633, 46.053932]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53035",
      state: "53",
      county: "035",
      name: "Kitsap County",
      censusArea: 394.941,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.749199, 47.741063],
          [-122.740159, 47.736228],
          [-122.733012, 47.737625],
          [-122.722686, 47.748827],
          [-122.719712, 47.760976],
          [-122.714801, 47.768176],
          [-122.690562, 47.778372],
          [-122.684085, 47.798574],
          [-122.682015, 47.800882],
          [-122.648108, 47.825123],
          [-122.623192, 47.836199],
          [-122.614585, 47.850806],
          [-122.608105, 47.856728],
          [-122.573672, 47.857582],
          [-122.573098, 47.874081],
          [-122.586339, 47.902023],
          [-122.588235, 47.912923],
          [-122.596228, 47.92021],
          [-122.616701, 47.925139],
          [-122.620316, 47.931553],
          [-122.617022, 47.938987],
          [-122.611956, 47.940772],
          [-122.603861, 47.940478],
          [-122.601507, 47.931726],
          [-122.592184, 47.922519],
          [-122.581846, 47.920282],
          [-122.549072, 47.919072],
          [-122.527593, 47.905882],
          [-122.513986, 47.880807],
          [-122.512778, 47.863879],
          [-122.506122, 47.831745],
          [-122.502224, 47.826395],
          [-122.482529, 47.815511],
          [-122.482437, 47.809255],
          [-122.485214, 47.804128],
          [-122.495346, 47.79704],
          [-122.495458, 47.786692],
          [-122.471402, 47.765965],
          [-122.470333, 47.757109],
          [-122.471844, 47.749819],
          [-122.477344, 47.746019],
          [-122.488491, 47.743605],
          [-122.507638, 47.74304],
          [-122.515193, 47.743911],
          [-122.519325, 47.74622],
          [-122.537318, 47.74714],
          [-122.554454, 47.745704],
          [-122.543161, 47.710941],
          [-122.53094, 47.704814],
          [-122.525851, 47.705095],
          [-122.523962, 47.708034],
          [-122.511196, 47.708715],
          [-122.504604, 47.699136],
          [-122.504452, 47.685888],
          [-122.508709, 47.670843],
          [-122.518277, 47.65132],
          [-122.502116, 47.639074],
          [-122.493205, 47.635122],
          [-122.492809, 47.629591],
          [-122.494518, 47.623625],
          [-122.500357, 47.617816],
          [-122.49824, 47.598242],
          [-122.493933, 47.588963],
          [-122.483805, 47.586721],
          [-122.479089, 47.583654],
          [-122.503672, 47.575178],
          [-122.518367, 47.57408],
          [-122.529915, 47.568441],
          [-122.534664, 47.566122],
          [-122.543118, 47.556326],
          [-122.542355, 47.53784],
          [-122.547207, 47.528257],
          [-122.546611, 47.52355],
          [-122.532909, 47.522184],
          [-122.52305, 47.524],
          [-122.500543, 47.51528],
          [-122.494882, 47.510265],
          [-122.484993, 47.512731],
          [-122.53672, 47.40299],
          [-122.801451, 47.404016],
          [-122.80015, 47.520165],
          [-122.872192, 47.521495],
          [-123.037766, 47.520214],
          [-122.950473, 47.606266],
          [-122.783237, 47.672754],
          [-122.749199, 47.741063]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55053",
      state: "55",
      county: "053",
      name: "Jackson County",
      censusArea: 987.72,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.151843, 44.079656],
          [-91.165786, 44.247429],
          [-91.165619, 44.596987],
          [-90.92235, 44.596293],
          [-90.923104, 44.509838],
          [-90.801852, 44.509573],
          [-90.801904, 44.422347],
          [-90.31605, 44.424673],
          [-90.312035, 44.248758],
          [-90.31257, 44.240933],
          [-90.312575, 44.155284],
          [-90.906152, 44.158316],
          [-90.972764, 44.070775],
          [-91.151843, 44.079656]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55067",
      state: "55",
      county: "067",
      name: "Langlade County",
      censusArea: 870.64,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.98168, 45.028917],
          [-89.102069, 45.029499],
          [-89.223814, 45.029246],
          [-89.224214, 45.118567],
          [-89.425971, 45.119098],
          [-89.424824, 45.467257],
          [-89.364049, 45.469886],
          [-89.046315, 45.464762],
          [-88.923062, 45.465137],
          [-88.926025, 45.378637],
          [-88.677826, 45.378682],
          [-88.680066, 45.204994],
          [-88.641159, 45.117346],
          [-88.982176, 45.117733],
          [-88.98168, 45.028917]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55078",
      state: "55",
      county: "078",
      name: "Menomonee County",
      censusArea: 357.608,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.641159, 45.117346],
          [-88.483732, 45.116887],
          [-88.489149, 44.855445],
          [-88.736194, 44.856376],
          [-88.735548, 44.943178],
          [-88.981432, 44.942841],
          [-88.98168, 45.028917],
          [-88.982176, 45.117733],
          [-88.641159, 45.117346]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55087",
      state: "55",
      county: "087",
      name: "Outagamie County",
      censusArea: 637.523,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.346334, 44.243718],
          [-88.351301, 44.243768],
          [-88.354532, 44.243863],
          [-88.40407, 44.244102],
          [-88.73977, 44.243303],
          [-88.736802, 44.590605],
          [-88.606083, 44.590515],
          [-88.577163, 44.59063],
          [-88.245204, 44.584746],
          [-88.190603, 44.586791],
          [-88.192611, 44.241947],
          [-88.303974, 44.243335],
          [-88.346334, 44.243718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55099",
      state: "55",
      county: "099",
      name: "Price County",
      censusArea: 1254.376,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.043309, 45.730053],
          [-90.043414, 45.555082],
          [-90.042273, 45.381866],
          [-90.678739, 45.37777],
          [-90.678749, 45.638264],
          [-90.676968, 45.981554],
          [-90.302063, 45.981349],
          [-90.043958, 45.981948],
          [-90.042854, 45.897151],
          [-90.044358, 45.749992],
          [-90.043309, 45.730053]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55109",
      state: "55",
      county: "109",
      name: "St. Croix County",
      censusArea: 722.329,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.765898, 45.210173],
          [-92.156465, 45.209554],
          [-92.136113, 45.121432],
          [-92.136348, 44.857848],
          [-92.761677, 44.862022],
          [-92.750645, 44.937299],
          [-92.768606, 45.008016],
          [-92.791206, 45.045698],
          [-92.792875, 45.078489],
          [-92.76713, 45.194165],
          [-92.765898, 45.210173]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55121",
      state: "55",
      county: "121",
      name: "Trempealeau County",
      censusArea: 732.965,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.165619, 44.596987],
          [-91.165786, 44.247429],
          [-91.151843, 44.079656],
          [-91.337329, 44.037499],
          [-91.322143, 43.984462],
          [-91.420114, 43.984243],
          [-91.432522, 43.996827],
          [-91.43738, 43.999962],
          [-91.463515, 44.009041],
          [-91.505918, 44.018651],
          [-91.547028, 44.022226],
          [-91.559197, 44.023959],
          [-91.534229, 44.096046],
          [-91.612994, 44.172606],
          [-91.529741, 44.246777],
          [-91.529101, 44.596197],
          [-91.165619, 44.596987]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55135",
      state: "55",
      county: "135",
      name: "Waupaca County",
      censusArea: 747.713,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.606083, 44.590515],
          [-88.736802, 44.590605],
          [-88.73977, 44.243303],
          [-88.886673, 44.242622],
          [-89.224892, 44.243546],
          [-89.223745, 44.681365],
          [-88.605159, 44.678331],
          [-88.606083, 44.590515]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56005",
      state: "56",
      county: "005",
      name: "Campbell County",
      censusArea: 4802.709,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.076607, 45.000347],
          [-105.082181, 44.176175],
          [-105.079281, 44.176182],
          [-105.079797, 43.498445],
          [-106.017366, 43.494969],
          [-106.009201, 43.821579],
          [-106.016504, 44.169729],
          [-106.009676, 44.563981],
          [-106.024814, 44.993688],
          [-105.928184, 44.993647],
          [-105.918995, 44.997771],
          [-105.848065, 45.000396],
          [-105.076607, 45.000347]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56015",
      state: "56",
      county: "015",
      name: "Goshen County",
      censusArea: 2225.391,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.052774, 41.733401],
          [-104.05283, 41.697954],
          [-104.052913, 41.64519],
          [-104.052945, 41.638167],
          [-104.052975, 41.622931],
          [-104.052735, 41.613676],
          [-104.052859, 41.592254],
          [-104.05254, 41.564274],
          [-104.652992, 41.565014],
          [-104.653338, 41.653007],
          [-104.655027, 42.216785],
          [-104.655639, 42.609468],
          [-104.61235, 42.608805],
          [-104.052773, 42.611766],
          [-104.052775, 42.61159],
          [-104.052775, 42.610813],
          [-104.052776, 42.25822],
          [-104.052793, 42.249962],
          [-104.052761, 42.170278],
          [-104.052547, 42.166801],
          [-104.053001, 42.137254],
          [-104.052738, 42.133769],
          [-104.0526, 42.124963],
          [-104.052954, 42.089077],
          [-104.052967, 42.075004],
          [-104.05288, 42.021761],
          [-104.052729, 42.016318],
          [-104.052704, 42.001718],
          [-104.052699, 41.998673],
          [-104.052761, 41.994967],
          [-104.05283, 41.9946],
          [-104.052856, 41.975958],
          [-104.052734, 41.973007],
          [-104.052991, 41.914973],
          [-104.052931, 41.906143],
          [-104.053026, 41.885464],
          [-104.052774, 41.733401]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56041",
      state: "56",
      county: "041",
      name: "Uinta County",
      censusArea: 2081.264,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.046402, 41.579845],
          [-110.062787, 41.577519],
          [-110.048, 41.578015],
          [-110.048474, 40.997103],
          [-110.500718, 40.994746],
          [-111.046723, 40.997959],
          [-111.046551, 41.251716],
          [-111.0466, 41.360692],
          [-111.046264, 41.377731],
          [-111.046402, 41.579845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01127",
      state: "01",
      county: "127",
      name: "Walker County",
      censusArea: 791.192,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.423701, 33.602096],
          [-87.423843, 33.689112],
          [-87.528338, 33.692049],
          [-87.531602, 33.867618],
          [-87.63604, 33.871999],
          [-87.635932, 33.915251],
          [-87.636118, 34.002203],
          [-87.151036, 33.993225],
          [-87.091836, 33.890093],
          [-86.963358, 33.858221],
          [-86.96296, 33.844865],
          [-86.954305, 33.844862],
          [-86.953664, 33.815297],
          [-87.005965, 33.787237],
          [-87.057757, 33.656363],
          [-87.17943, 33.613794],
          [-87.185794, 33.555882],
          [-87.266923, 33.512929],
          [-87.318532, 33.587393],
          [-87.423701, 33.602096]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01133",
      state: "01",
      county: "133",
      name: "Winston County",
      censusArea: 612.979,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.634725, 34.306997],
          [-87.529722, 34.304598],
          [-87.109911, 34.299299],
          [-87.111992, 33.992385],
          [-87.151036, 33.993225],
          [-87.636118, 34.002203],
          [-87.634725, 34.306997]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20179",
      state: "20",
      county: "179",
      name: "Sheridan County",
      censusArea: 895.957,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.720213, 39.568034],
          [-100.180351, 39.56689],
          [-100.161667, 39.567277],
          [-100.1642, 39.132071],
          [-100.721296, 39.133381],
          [-100.720213, 39.568034]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20191",
      state: "20",
      county: "191",
      name: "Sumner County",
      censusArea: 1181.941,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.147721, 36.999111],
          [-97.372421, 36.998861],
          [-97.384925, 36.998843],
          [-97.46228, 36.998685],
          [-97.462346, 36.998685],
          [-97.472861, 36.998721],
          [-97.527292, 36.99875],
          [-97.5459, 36.998709],
          [-97.546498, 36.998747],
          [-97.564536, 36.998711],
          [-97.606549, 36.998682],
          [-97.637137, 36.99909],
          [-97.650466, 36.999004],
          [-97.697104, 36.998826],
          [-97.768704, 36.99875],
          [-97.783432, 36.998961],
          [-97.783489, 36.998847],
          [-97.802298, 36.998713],
          [-97.802313, 36.998713],
          [-97.804566, 37.386298],
          [-97.807057, 37.386293],
          [-97.807576, 37.473888],
          [-97.8076, 37.474184],
          [-97.153331, 37.475544],
          [-97.147721, 36.999111]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20209",
      state: "20",
      county: "209",
      name: "Wyandotte County",
      censusArea: 151.6,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.607354, 39.113444],
          [-94.607234, 39.089604],
          [-94.607334, 39.081704],
          [-94.607234, 39.065704],
          [-94.607391, 39.044086],
          [-94.872442, 39.059088],
          [-94.908765, 38.991401],
          [-94.900191, 39.202911],
          [-94.793948, 39.201276],
          [-94.752338, 39.173203],
          [-94.736537, 39.169203],
          [-94.723637, 39.169003],
          [-94.714137, 39.170403],
          [-94.706723, 39.173801],
          [-94.601733, 39.159603],
          [-94.596033, 39.157703],
          [-94.591933, 39.155003],
          [-94.589933, 39.140403],
          [-94.607354, 39.113444]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21185",
      state: "21",
      county: "185",
      name: "Oldham County",
      censusArea: 187.218,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.468206, 38.285309],
          [-85.638044, 38.38027],
          [-85.638041, 38.380338],
          [-85.632937, 38.395666],
          [-85.620521, 38.423105],
          [-85.607629, 38.439295],
          [-85.462518, 38.512602],
          [-85.433136, 38.523914],
          [-85.432928, 38.524072],
          [-85.314006, 38.492592],
          [-85.34608, 38.45954],
          [-85.283082, 38.358054],
          [-85.324453, 38.310112],
          [-85.468206, 38.285309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21197",
      state: "21",
      county: "197",
      name: "Powell County",
      censusArea: 178.979,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.7221, 37.71642],
          [-83.906251, 37.759363],
          [-84.001967, 37.837592],
          [-83.96631, 37.930768],
          [-83.768783, 37.918371],
          [-83.63106, 37.825372],
          [-83.707974, 37.716463],
          [-83.71221, 37.720135],
          [-83.7221, 37.71642]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21203",
      state: "21",
      county: "203",
      name: "Rockcastle County",
      censusArea: 316.545,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.502932, 37.328801],
          [-84.446719, 37.485893],
          [-84.37444, 37.472663],
          [-84.347805, 37.539064],
          [-84.199489, 37.522858],
          [-84.137898, 37.319759],
          [-84.253696, 37.239516],
          [-84.289076, 37.15174],
          [-84.502932, 37.328801]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21215",
      state: "21",
      county: "215",
      name: "Spencer County",
      censusArea: 186.677,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.489202, 37.990654],
          [-85.42883, 38.118383],
          [-85.424054, 38.147438],
          [-85.232406, 38.089431],
          [-85.101646, 38.03727],
          [-85.167834, 37.971809],
          [-85.399871, 37.939956],
          [-85.489202, 37.990654]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21227",
      state: "21",
      county: "227",
      name: "Warren County",
      censusArea: 541.6,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.399165, 37.169896],
          [-86.28175, 37.080597],
          [-86.218607, 37.104029],
          [-86.113512, 37.061072],
          [-86.16674, 36.934015],
          [-86.405769, 36.776187],
          [-86.400518, 36.807226],
          [-86.583289, 36.830282],
          [-86.611586, 36.883057],
          [-86.674462, 36.999766],
          [-86.618158, 37.183906],
          [-86.399165, 37.169896]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21235",
      state: "21",
      county: "235",
      name: "Whitley County",
      censusArea: 437.83,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.930669, 36.588249],
          [-83.987612, 36.589595],
          [-83.987842, 36.5896],
          [-84.227295, 36.591685],
          [-84.307598, 36.676809],
          [-84.350006, 36.780681],
          [-84.297416, 36.945931],
          [-84.091853, 36.955992],
          [-84.065755, 36.853961],
          [-83.958534, 36.727245],
          [-83.877503, 36.687259],
          [-83.957274, 36.642204],
          [-83.930669, 36.588249]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22007",
      state: "22",
      county: "007",
      name: "Assumption County",
      lsad: "Parish",
      censusArea: 338.658,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.224356, 30.025336],
          [-91.105951, 30.062478],
          [-90.963693, 30.06645],
          [-90.959869, 29.977924],
          [-90.885589, 29.905353],
          [-91.006743, 29.714771],
          [-91.082602, 29.626368],
          [-91.104912, 29.642496],
          [-91.10001, 29.699402],
          [-91.092748, 29.801574],
          [-91.182817, 29.846172],
          [-91.255229, 29.971457],
          [-91.259388, 30.0002],
          [-91.235889, 30.001128],
          [-91.224356, 30.025336]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22021",
      state: "22",
      county: "021",
      name: "Caldwell County",
      lsad: "Parish",
      censusArea: 529.425,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.312623, 31.927382],
          [-92.312218, 32.146123],
          [-92.311851, 32.277441],
          [-92.034397, 32.277068],
          [-91.979868, 32.166148],
          [-91.894729, 32.15155],
          [-91.889687, 31.971474],
          [-91.904381, 31.927561],
          [-92.005434, 31.927296],
          [-92.312623, 31.927382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22025",
      state: "22",
      county: "025",
      name: "Catahoula County",
      lsad: "Parish",
      censusArea: 708.03,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.008121, 31.325797],
          [-92.005434, 31.927296],
          [-91.904381, 31.927561],
          [-91.889687, 31.971474],
          [-91.804869, 31.893193],
          [-91.729158, 31.88251],
          [-91.6475, 31.970428],
          [-91.596922, 31.969758],
          [-91.575721, 31.88256],
          [-91.544791, 31.751731],
          [-91.601622, 31.761864],
          [-91.694469, 31.735286],
          [-91.705948, 31.642863],
          [-91.775836, 31.648626],
          [-91.824426, 31.599671],
          [-91.738331, 31.431572],
          [-91.807098, 31.438794],
          [-91.857083, 31.494527],
          [-91.855889, 31.423831],
          [-91.776779, 31.385215],
          [-91.845019, 31.375916],
          [-91.862168, 31.302314],
          [-91.83428, 31.266344],
          [-91.926854, 31.29483],
          [-91.994828, 31.229561],
          [-92.008121, 31.325797]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22047",
      state: "22",
      county: "047",
      name: "Iberville County",
      lsad: "Parish",
      censusArea: 618.627,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.475277, 30.229231],
          [-91.56276, 30.241949],
          [-91.621055, 30.309938],
          [-91.640099, 30.442728],
          [-91.700933, 30.497433],
          [-91.485089, 30.497138],
          [-91.31166, 30.322471],
          [-91.142105, 30.323293],
          [-91.142042, 30.322718],
          [-91.142088, 30.322028],
          [-91.142332, 30.320437],
          [-91.142305, 30.319893],
          [-91.021014, 30.321489],
          [-91.059273, 30.213113],
          [-91.105951, 30.062478],
          [-91.224356, 30.025336],
          [-91.368835, 30.058824],
          [-91.463833, 30.102493],
          [-91.475277, 30.229231]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22059",
      state: "22",
      county: "059",
      name: "Lasalle County",
      lsad: "Parish",
      censusArea: 624.684,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.196131, 31.477877],
          [-92.266202, 31.492791],
          [-92.306855, 31.58952],
          [-92.384441, 31.620437],
          [-92.347739, 31.696584],
          [-92.362608, 31.796633],
          [-92.312623, 31.927382],
          [-92.005434, 31.927296],
          [-92.008121, 31.325797],
          [-92.086718, 31.335374],
          [-92.196131, 31.477877]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22067",
      state: "22",
      county: "067",
      name: "Morehouse County",
      lsad: "Parish",
      censusArea: 794.934,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.913057, 32.505902],
          [-91.910952, 32.661119],
          [-92.065179, 32.723003],
          [-92.069105, 33.008163],
          [-91.951958, 33.007428],
          [-91.950001, 33.00752],
          [-91.62667, 33.006639],
          [-91.617615, 33.006717],
          [-91.609001, 33.006556],
          [-91.579802, 33.006518],
          [-91.579639, 33.006472],
          [-91.572326, 33.006908],
          [-91.559494, 33.00684],
          [-91.46037, 33.006246],
          [-91.435782, 33.006099],
          [-91.511515, 32.879007],
          [-91.566363, 32.855561],
          [-91.637587, 32.667341],
          [-91.742286, 32.558906],
          [-91.80432, 32.571316],
          [-91.913057, 32.505902]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22083",
      state: "22",
      county: "083",
      name: "Richland County",
      lsad: "Parish",
      censusArea: 559.043,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.476739, 32.405808],
          [-91.634931, 32.405371],
          [-91.764379, 32.330018],
          [-91.775019, 32.243333],
          [-91.894729, 32.15155],
          [-91.979868, 32.166148],
          [-92.034397, 32.277068],
          [-92.00434, 32.380499],
          [-91.913057, 32.505902],
          [-91.80432, 32.571316],
          [-91.742286, 32.558906],
          [-91.637587, 32.667341],
          [-91.596027, 32.582192],
          [-91.445299, 32.581956],
          [-91.430989, 32.561186],
          [-91.457643, 32.537807],
          [-91.476739, 32.405808]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48101",
      state: "48",
      county: "101",
      name: "Cottle County",
      censusArea: 900.563,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.99772, 34.311829],
          [-99.997671, 34.284652],
          [-99.997867, 34.282934],
          [-99.997621, 34.224355],
          [-100.047028, 34.22978],
          [-100.04848, 33.835974],
          [-100.518691, 33.83565],
          [-100.51734, 34.314102],
          [-100.500398, 34.314074],
          [-100.498193, 34.313527],
          [-100.417783, 34.313524],
          [-99.99772, 34.311829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48105",
      state: "48",
      county: "105",
      name: "Crockett County",
      censusArea: 2807.327,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.7584, 30.288044],
          [-101.65853, 30.357861],
          [-101.740429, 30.420897],
          [-101.646245, 30.560763],
          [-101.65263, 30.619374],
          [-101.76842, 30.653077],
          [-101.883436, 30.925647],
          [-101.98364, 30.98717],
          [-102.191731, 31.003788],
          [-102.388804, 31.087156],
          [-102.301212, 31.086212],
          [-101.776191, 31.079784],
          [-101.274799, 31.079379],
          [-100.962176, 31.08249],
          [-100.960587, 30.706071],
          [-100.960643, 30.287776],
          [-101.7584, 30.288044]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48109",
      state: "48",
      county: "109",
      name: "Culberson County",
      censusArea: 3812.798,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.024521, 32.00001],
          [-104.102351, 31.105203],
          [-104.917163, 30.663626],
          [-104.916396, 30.835979],
          [-104.907592, 30.835919],
          [-104.918637, 31.991805],
          [-104.918272, 32.000496],
          [-104.847757, 32.000482],
          [-104.643526, 32.000443],
          [-104.640918, 32.000396],
          [-104.531937, 32.000311],
          [-104.531756, 32.000117],
          [-104.024521, 32.00001]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48111",
      state: "48",
      county: "111",
      name: "Dallam County",
      censusArea: 1503.259,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.162463, 36.500326],
          [-102.162771, 36.497902],
          [-102.163015, 36.055249],
          [-103.040824, 36.055231],
          [-103.041674, 36.317534],
          [-103.041745, 36.318267],
          [-103.041669, 36.478514],
          [-103.002434, 36.500397],
          [-102.162463, 36.500326]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48113",
      state: "48",
      county: "113",
      name: "Dallas County",
      censusArea: 871.279,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.838963, 32.987521],
          [-96.687954, 32.985259],
          [-96.682467, 32.985325],
          [-96.660185, 32.985127],
          [-96.613377, 32.984021],
          [-96.516866, 32.982308],
          [-96.518273, 32.891534],
          [-96.518314, 32.877153],
          [-96.518586, 32.873389],
          [-96.51897, 32.813616],
          [-96.523118, 32.545214],
          [-96.529987, 32.545282],
          [-96.908135, 32.547838],
          [-97.038385, 32.548662],
          [-97.032687, 32.920614],
          [-97.032287, 32.989324],
          [-97.016602, 32.98932],
          [-97.016332, 32.989324],
          [-96.843979, 32.987554],
          [-96.839552, 32.987508],
          [-96.838963, 32.987521]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48121",
      state: "48",
      county: "121",
      name: "Denton County",
      censusArea: 878.432,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.834004, 33.405504],
          [-96.840426, 33.152585],
          [-96.841854, 33.086264],
          [-96.843979, 32.987554],
          [-97.016332, 32.989324],
          [-97.016602, 32.98932],
          [-97.032287, 32.989324],
          [-97.167261, 32.989802],
          [-97.29423, 32.99026],
          [-97.37994, 32.990144],
          [-97.397205, 32.990747],
          [-97.39848, 32.990839],
          [-97.383095, 33.43045],
          [-97.051253, 33.41995],
          [-96.94386, 33.41641],
          [-96.834004, 33.405504]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48125",
      state: "48",
      county: "125",
      name: "Dickens County",
      censusArea: 901.724,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.041158, 33.833625],
          [-100.518691, 33.83565],
          [-100.517449, 33.397866],
          [-101.038788, 33.397214],
          [-101.041158, 33.833625]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48133",
      state: "48",
      county: "133",
      name: "Eastland County",
      censusArea: 926.491,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.924401, 32.078018],
          [-99.118665, 32.079585],
          [-99.114293, 32.514811],
          [-99.09605, 32.514768],
          [-98.576256, 32.515163],
          [-98.573016, 32.515261],
          [-98.495775, 32.513026],
          [-98.475177, 32.513039],
          [-98.475133, 32.298916],
          [-98.551158, 32.261436],
          [-98.660316, 32.204408],
          [-98.924401, 32.078018]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48139",
      state: "48",
      county: "139",
      name: "Ellis County",
      censusArea: 935.487,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.086834, 32.549428],
          [-97.05262, 32.548907],
          [-97.038385, 32.548662],
          [-96.908135, 32.547838],
          [-96.529987, 32.545282],
          [-96.452138, 32.358631],
          [-96.383082, 32.328851],
          [-96.896209, 32.073977],
          [-96.940656, 32.052087],
          [-97.086191, 32.265451],
          [-97.086834, 32.549428]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48145",
      state: "48",
      county: "145",
      name: "Falls County",
      censusArea: 765.483,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.070188, 30.98622],
          [-97.278113, 31.279799],
          [-96.801123, 31.522269],
          [-96.596862, 31.2209],
          [-96.829121, 31.105871],
          [-97.070188, 30.98622]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48147",
      state: "48",
      county: "147",
      name: "Fannin County",
      censusArea: 890.837,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.378473, 33.726648],
          [-96.36959, 33.716809],
          [-96.304675, 33.745901],
          [-96.229859, 33.74832],
          [-96.220521, 33.74739],
          [-96.1999, 33.752117],
          [-96.181704, 33.758504],
          [-96.170055, 33.76917],
          [-96.1641, 33.784261],
          [-96.156717, 33.813324],
          [-96.14807, 33.837799],
          [-96.097526, 33.847544],
          [-95.949935, 33.857452],
          [-95.846334, 33.841136],
          [-95.857738, 33.461722],
          [-95.858723, 33.40953],
          [-96.295413, 33.351936],
          [-96.384495, 33.341119],
          [-96.384602, 33.397826],
          [-96.37947, 33.724876],
          [-96.378473, 33.726648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48151",
      state: "48",
      county: "151",
      name: "Fisher County",
      censusArea: 898.941,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.146543, 32.522793],
          [-100.379054, 32.523798],
          [-100.660626, 32.525312],
          [-100.65587, 32.963469],
          [-100.519208, 32.962926],
          [-100.144224, 32.959978],
          [-100.146543, 32.522793]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48157",
      state: "48",
      county: "157",
      name: "Fort Bend County",
      censusArea: 861.481,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.688349, 29.710862],
          [-95.687994, 29.710667],
          [-95.687082, 29.710078],
          [-95.610025, 29.66699],
          [-95.537948, 29.633282],
          [-95.44697, 29.590978],
          [-95.440415, 29.588065],
          [-95.435618, 29.585633],
          [-95.424118, 29.580233],
          [-95.443224, 29.510214],
          [-95.462503, 29.43924],
          [-95.549994, 29.438439],
          [-95.579598, 29.340129],
          [-95.654355, 29.290945],
          [-95.752866, 29.324503],
          [-95.847656, 29.26259],
          [-95.8796, 29.266761],
          [-95.894468, 29.31498],
          [-95.94681, 29.333433],
          [-95.937434, 29.358086],
          [-95.97343, 29.411445],
          [-96.060676, 29.476473],
          [-96.044596, 29.562238],
          [-96.088912, 29.601658],
          [-96.02485, 29.602877],
          [-96.032711, 29.727944],
          [-95.82622, 29.78828],
          [-95.688349, 29.710862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48181",
      state: "48",
      county: "181",
      name: "Grayson County",
      censusArea: 932.804,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.922114, 33.959579],
          [-96.9163, 33.957798],
          [-96.907387, 33.950025],
          [-96.899442, 33.933728],
          [-96.895728, 33.896414],
          [-96.88301, 33.868019],
          [-96.866438, 33.853149],
          [-96.85609, 33.84749],
          [-96.761588, 33.824406],
          [-96.712422, 33.831633],
          [-96.707274, 33.835398],
          [-96.700952, 33.840748],
          [-96.694629, 33.849988],
          [-96.68928, 33.86166],
          [-96.685389, 33.872846],
          [-96.682471, 33.883059],
          [-96.67858, 33.892786],
          [-96.672258, 33.899595],
          [-96.65864, 33.900081],
          [-96.646968, 33.895218],
          [-96.633351, 33.889382],
          [-96.622165, 33.885977],
          [-96.615356, 33.881114],
          [-96.613494, 33.878321],
          [-96.611466, 33.875278],
          [-96.612963, 33.867651],
          [-96.615843, 33.853393],
          [-96.61487, 33.841234],
          [-96.612925, 33.833939],
          [-96.500268, 33.772583],
          [-96.448045, 33.781031],
          [-96.436455, 33.78005],
          [-96.423362, 33.776365],
          [-96.378473, 33.726648],
          [-96.37947, 33.724876],
          [-96.384602, 33.397826],
          [-96.386474, 33.398075],
          [-96.834004, 33.405504],
          [-96.94386, 33.41641],
          [-96.944611, 33.948994],
          [-96.934508, 33.953748],
          [-96.922114, 33.959579]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48189",
      state: "48",
      county: "189",
      name: "Hale County",
      censusArea: 1004.683,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.99802, 34.313039],
          [-101.564856, 34.312493],
          [-101.56358, 33.830451],
          [-102.085733, 33.824675],
          [-102.090415, 34.313132],
          [-101.99802, 34.313039]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48193",
      state: "48",
      county: "193",
      name: "Hamilton County",
      censusArea: 835.914,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.180006, 31.463717],
          [-98.27107, 31.416398],
          [-98.463736, 31.683989],
          [-98.156568, 31.841713],
          [-98.208386, 31.917511],
          [-98.005462, 32.017897],
          [-97.766431, 31.672461],
          [-98.180006, 31.463717]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48345",
      state: "48",
      county: "345",
      name: "Motley County",
      censusArea: 989.562,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.041158, 33.833625],
          [-101.041484, 34.312444],
          [-100.966969, 34.31264],
          [-100.966896, 34.312711],
          [-100.946132, 34.312759],
          [-100.51734, 34.314102],
          [-100.518691, 33.83565],
          [-101.041158, 33.833625]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48347",
      state: "48",
      county: "347",
      name: "Nacogdoches County",
      censusArea: 946.537,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.326616, 31.224754],
          [-94.495874, 31.405728],
          [-94.530634, 31.398654],
          [-94.544888, 31.431715],
          [-94.728679, 31.457226],
          [-94.865857, 31.526916],
          [-94.951969, 31.641351],
          [-94.967167, 31.753767],
          [-94.937532, 31.845555],
          [-94.452416, 31.844281],
          [-94.399091, 31.653596],
          [-94.395361, 31.638752],
          [-94.311042, 31.589252],
          [-94.339773, 31.24082],
          [-94.326616, 31.224754]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48351",
      state: "48",
      county: "351",
      name: "Newton County",
      censusArea: 933.677,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.901452, 30.242672],
          [-93.865711, 30.947218],
          [-93.911126, 31.158071],
          [-93.600308, 31.176158],
          [-93.599705, 31.176456],
          [-93.598828, 31.174679],
          [-93.588503, 31.165581],
          [-93.579215, 31.167422],
          [-93.552649, 31.185575],
          [-93.55254, 31.185605],
          [-93.548931, 31.186601],
          [-93.535097, 31.185614],
          [-93.531744, 31.180817],
          [-93.527644, 31.074509],
          [-93.540354, 31.008135],
          [-93.530936, 30.924534],
          [-93.554576, 30.87747],
          [-93.55497, 30.876685],
          [-93.558617, 30.869424],
          [-93.558672, 30.868829],
          [-93.563292, 30.818503],
          [-93.578395, 30.802047],
          [-93.727844, 30.57407],
          [-93.740045, 30.538765],
          [-93.710117, 30.5064],
          [-93.697828, 30.443838],
          [-93.698302, 30.438657],
          [-93.702665, 30.429947],
          [-93.741701, 30.403007],
          [-93.751437, 30.396288],
          [-93.757654, 30.390423],
          [-93.765822, 30.333318],
          [-93.735896, 30.29944],
          [-93.723586, 30.294951],
          [-93.720575, 30.295961],
          [-93.716223, 30.244318],
          [-93.901452, 30.242672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51135",
      state: "51",
      county: "135",
      name: "Nottoway County",
      censusArea: 314.392,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.889813, 36.988818],
          [-78.003639, 37.022756],
          [-78.082652, 37.016406],
          [-78.239354, 37.120089],
          [-78.231234, 37.29622],
          [-77.856209, 37.209958],
          [-77.795926, 37.192592],
          [-77.900338, 37.143882],
          [-77.889813, 36.988818]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51137",
      state: "51",
      county: "137",
      name: "Orange County",
      censusArea: 340.783,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.703189, 38.360598],
          [-77.707798, 38.358565],
          [-77.705491, 38.354837],
          [-77.713032, 38.351431],
          [-77.71245, 38.345447],
          [-77.718646, 38.341602],
          [-77.714698, 38.33669],
          [-77.719737, 38.335508],
          [-77.726689, 38.323905],
          [-77.954819, 38.117437],
          [-78.093063, 38.151611],
          [-78.208092, 38.13315],
          [-78.3445, 38.177931],
          [-78.369797, 38.184757],
          [-78.297716, 38.261585],
          [-78.286968, 38.271533],
          [-78.220645, 38.230308],
          [-78.094571, 38.311361],
          [-77.891822, 38.388953],
          [-77.703189, 38.360598]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51139",
      state: "51",
      county: "139",
      name: "Page County",
      censusArea: 310.856,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.284877, 38.759229],
          [-78.33832, 38.627282],
          [-78.452918, 38.475508],
          [-78.455495, 38.472532],
          [-78.457711, 38.464804],
          [-78.462658, 38.459528],
          [-78.462113, 38.45472],
          [-78.464699, 38.452542],
          [-78.465073, 38.447],
          [-78.472509, 38.447627],
          [-78.477718, 38.438574],
          [-78.476566, 38.432146],
          [-78.481647, 38.430603],
          [-78.484246, 38.423648],
          [-78.486003, 38.421486],
          [-78.691312, 38.510008],
          [-78.641769, 38.604719],
          [-78.548828, 38.738655],
          [-78.50926, 38.725626],
          [-78.386682, 38.830152],
          [-78.284877, 38.759229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51143",
      state: "51",
      county: "143",
      name: "Pittsylvania County",
      censusArea: 968.941,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.442884, 37.055863],
          [-79.332161, 37.125568],
          [-79.217104, 37.118874],
          [-79.194525, 37.055816],
          [-79.092264, 37.061498],
          [-79.218638, 36.541579],
          [-79.342696, 36.541382],
          [-79.304486, 36.582202],
          [-79.398845, 36.644537],
          [-79.422097, 36.600681],
          [-79.501101, 36.61825],
          [-79.456646, 36.576738],
          [-79.470047, 36.541025],
          [-79.510647, 36.540738],
          [-79.510961, 36.54074],
          [-79.666827, 36.541772],
          [-79.667309, 36.541772],
          [-79.714855, 36.541884],
          [-79.640652, 36.856139],
          [-79.59593, 37.036349],
          [-79.594059, 37.042238],
          [-79.480727, 37.005876],
          [-79.442884, 37.055863]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51153",
      state: "51",
      county: "153",
      name: "Prince William County",
      censusArea: 336.402,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.530914, 38.555929],
          [-77.535808, 38.561621],
          [-77.580256, 38.627685],
          [-77.663346, 38.752917],
          [-77.715415, 38.832214],
          [-77.689176, 38.889231],
          [-77.680547, 38.8932],
          [-77.665797, 38.915867],
          [-77.655081, 38.942635],
          [-77.53576, 38.847474],
          [-77.449084, 38.803133],
          [-77.387917, 38.748217],
          [-77.380451, 38.722516],
          [-77.22235, 38.638091],
          [-77.240604, 38.638917],
          [-77.246704, 38.635217],
          [-77.248904, 38.628617],
          [-77.245104, 38.620717],
          [-77.246441, 38.599532],
          [-77.247003, 38.590618],
          [-77.26443, 38.582845],
          [-77.265304, 38.580319],
          [-77.26083, 38.56533],
          [-77.276603, 38.54712],
          [-77.276303, 38.53962],
          [-77.283503, 38.525221],
          [-77.291103, 38.515721],
          [-77.29582, 38.511457],
          [-77.298844, 38.508724],
          [-77.300776, 38.506978],
          [-77.302457, 38.504683],
          [-77.491569, 38.590899],
          [-77.530914, 38.555929]
        ],
        [
          [-77.471425, 38.728152],
          [-77.451642, 38.738337],
          [-77.44751, 38.761115],
          [-77.45931, 38.772614],
          [-77.427503, 38.7534],
          [-77.420682, 38.762242],
          [-77.422236, 38.768917],
          [-77.44061, 38.782615],
          [-77.445282, 38.770722],
          [-77.459499, 38.778395],
          [-77.458507, 38.787394],
          [-77.473195, 38.788248],
          [-77.475711, 38.781214],
          [-77.495927, 38.770607],
          [-77.526457, 38.732466],
          [-77.524721, 38.723966],
          [-77.471425, 38.728152]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51163",
      state: "51",
      county: "163",
      name: "Rockbridge County",
      censusArea: 597.558,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.482405, 38.086105],
          [-79.183978, 37.914194],
          [-79.157423, 37.890995],
          [-79.171926, 37.803135],
          [-79.264384, 37.79472],
          [-79.34501, 37.661129],
          [-79.436926, 37.617004],
          [-79.498553, 37.532887],
          [-79.683594, 37.661668],
          [-79.674006, 37.763052],
          [-79.650757, 37.871566],
          [-79.482405, 38.086105]
        ],
        [
          [-79.366811, 37.72722],
          [-79.382642, 37.706345],
          [-79.359875, 37.702359],
          [-79.328768, 37.760576],
          [-79.380389, 37.743771],
          [-79.366811, 37.72722]
        ],
        [
          [-79.457611, 37.766222],
          [-79.415304, 37.789111],
          [-79.456515, 37.791261],
          [-79.461917, 37.78616],
          [-79.457611, 37.766222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51169",
      state: "51",
      county: "169",
      name: "Scott County",
      censusArea: 535.53,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.771308, 36.79988],
          [-82.634608, 36.878484],
          [-82.406633, 36.876356],
          [-82.356728, 36.780309],
          [-82.332089, 36.709355],
          [-82.308, 36.619525],
          [-82.293814, 36.595565],
          [-82.554294, 36.594876],
          [-82.559774, 36.5948],
          [-82.561074, 36.5948],
          [-82.609176, 36.594099],
          [-82.679879, 36.593698],
          [-82.69578, 36.593698],
          [-82.830433, 36.593761],
          [-82.985087, 36.593829],
          [-82.947535, 36.67584],
          [-82.823163, 36.733295],
          [-82.771308, 36.79988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51171",
      state: "51",
      county: "171",
      name: "Shenandoah County",
      censusArea: 508.776,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.869276, 38.762991],
          [-78.835191, 38.811499],
          [-78.808181, 38.856175],
          [-78.788031, 38.885123],
          [-78.779198, 38.892298],
          [-78.757278, 38.903203],
          [-78.719806, 38.922638],
          [-78.719755, 38.922135],
          [-78.716956, 38.916273],
          [-78.714135, 38.911176],
          [-78.710949, 38.910175],
          [-78.601655, 38.964603],
          [-78.557647, 39.013189],
          [-78.554222, 39.019672],
          [-78.565837, 39.026303],
          [-78.571901, 39.031995],
          [-78.544111, 39.056676],
          [-78.454023, 39.027599],
          [-78.335803, 39.101509],
          [-78.314196, 39.007977],
          [-78.300544, 38.94353],
          [-78.386682, 38.830152],
          [-78.50926, 38.725626],
          [-78.548828, 38.738655],
          [-78.641769, 38.604719],
          [-78.869276, 38.762991]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51181",
      state: "51",
      county: "181",
      name: "Surry County",
      censusArea: 278.949,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.671588, 37.14206],
          [-76.711775, 37.05538],
          [-76.849684, 36.996211],
          [-76.953546, 36.94445],
          [-76.965681, 37.054835],
          [-77.155415, 37.112034],
          [-76.972439, 37.248501],
          [-76.917081, 37.224365],
          [-76.883156, 37.223057],
          [-76.818511, 37.224618],
          [-76.791555, 37.207564],
          [-76.801023, 37.206043],
          [-76.803198, 37.201513],
          [-76.802511, 37.198308],
          [-76.796905, 37.189404],
          [-76.756899, 37.161582],
          [-76.747632, 37.150548],
          [-76.73728, 37.146164],
          [-76.73032, 37.145395],
          [-76.715295, 37.148035],
          [-76.696735, 37.174403],
          [-76.692926, 37.186147],
          [-76.691918, 37.195731],
          [-76.685614, 37.198851],
          [-76.669886, 37.183571],
          [-76.663774, 37.173875],
          [-76.66427, 37.171027],
          [-76.66867, 37.166771],
          [-76.67147, 37.158739],
          [-76.671588, 37.14206]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51183",
      state: "51",
      county: "183",
      name: "Sussex County",
      censusArea: 490.22,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.398498, 36.992985],
          [-77.382384, 37.00015],
          [-77.17344, 37.105238],
          [-77.173095, 37.105391],
          [-77.155415, 37.112034],
          [-76.965681, 37.054835],
          [-76.953546, 36.94445],
          [-77.430127, 36.707658],
          [-77.489999, 36.721009],
          [-77.458678, 36.862963],
          [-77.619029, 36.877844],
          [-77.616204, 36.879202],
          [-77.398498, 36.992985]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53033",
      state: "53",
      county: "033",
      name: "King County",
      censusArea: 2115.566,
      stateName: "Washington"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-122.484993, 47.512731],
            [-122.467796, 47.509956],
            [-122.452399, 47.503471],
            [-122.460503, 47.494773],
            [-122.460029, 47.486861],
            [-122.433386, 47.46643],
            [-122.43984, 47.458083],
            [-122.440083, 47.417647],
            [-122.437657, 47.407424],
            [-122.427327, 47.402129],
            [-122.395054, 47.399277],
            [-122.373629, 47.388718],
            [-122.378482, 47.38533],
            [-122.401768, 47.381325],
            [-122.437809, 47.365607],
            [-122.448399, 47.354987],
            [-122.453998, 47.343337],
            [-122.457494, 47.342567],
            [-122.469703, 47.344623],
            [-122.491237, 47.335172],
            [-122.491063, 47.332427],
            [-122.493123, 47.330253],
            [-122.504918, 47.33068],
            [-122.51885, 47.33332],
            [-122.52416, 47.33837],
            [-122.528129, 47.345542],
            [-122.52603, 47.358908],
            [-122.537935, 47.359203],
            [-122.528434, 47.378211],
            [-122.538429, 47.390829],
            [-122.53672, 47.40299],
            [-122.484993, 47.512731]
          ]
        ],
        [
          [
            [-122.418268, 47.320614],
            [-122.364168, 47.335953],
            [-122.336934, 47.341421],
            [-122.324833, 47.348521],
            [-122.325734, 47.391521],
            [-122.328434, 47.400621],
            [-122.335234, 47.408421],
            [-122.348035, 47.415921],
            [-122.355135, 47.441921],
            [-122.367036, 47.447621],
            [-122.383136, 47.450521],
            [-122.368036, 47.459221],
            [-122.363062, 47.475702],
            [-122.361336, 47.481421],
            [-122.365236, 47.48842],
            [-122.386637, 47.50222],
            [-122.396538, 47.51522],
            [-122.393938, 47.52482],
            [-122.398338, 47.55012],
            [-122.409839, 47.56892],
            [-122.421139, 47.57602],
            [-122.401839, 47.58392],
            [-122.387139, 47.59572],
            [-122.375421, 47.585181],
            [-122.370167, 47.583087],
            [-122.358238, 47.58482],
            [-122.342937, 47.59122],
            [-122.339513, 47.599113],
            [-122.344937, 47.60912],
            [-122.367819, 47.624213],
            [-122.386039, 47.63172],
            [-122.393739, 47.63102],
            [-122.40424, 47.63392],
            [-122.414645, 47.639766],
            [-122.429841, 47.658919],
            [-122.407841, 47.680119],
            [-122.403841, 47.689419],
            [-122.393248, 47.701602],
            [-122.38044, 47.709119],
            [-122.37644, 47.716519],
            [-122.375607, 47.719724],
            [-122.37314, 47.729219],
            [-122.382641, 47.749119],
            [-122.380241, 47.758519],
            [-122.394442, 47.772219],
            [-122.396422, 47.777927],
            [-122.000425, 47.775284],
            [-122.000396, 47.775284],
            [-121.928146, 47.776971],
            [-121.584537, 47.776726],
            [-121.580986, 47.777534],
            [-121.11901, 47.779933],
            [-121.068409, 47.706234],
            [-121.113606, 47.597135],
            [-121.243207, 47.540334],
            [-121.310953, 47.479271],
            [-121.425421, 47.422354],
            [-121.432652, 47.293591],
            [-121.298041, 47.14823],
            [-121.379671, 47.086765],
            [-121.796678, 47.173059],
            [-121.839148, 47.143356],
            [-122.047909, 47.171645],
            [-122.143967, 47.257347],
            [-122.229208, 47.257538],
            [-122.229494, 47.257538],
            [-122.33486, 47.257594],
            [-122.418268, 47.320614]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55035",
      state: "55",
      county: "035",
      name: "Eau Claire County",
      censusArea: 637.983,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.165619, 44.596987],
          [-91.529101, 44.596197],
          [-91.650248, 44.596649],
          [-91.650324, 44.623005],
          [-91.650474, 44.624963],
          [-91.650361, 44.683633],
          [-91.650455, 44.855951],
          [-90.922247, 44.857309],
          [-90.92235, 44.596293],
          [-91.165619, 44.596987]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55037",
      state: "55",
      county: "037",
      name: "Florence County",
      censusArea: 488.195,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.425302, 45.722426],
          [-88.67598, 45.722835],
          [-88.683198, 46.014563],
          [-88.679132, 46.013538],
          [-88.674606, 46.010567],
          [-88.670115, 45.999957],
          [-88.670939, 45.999957],
          [-88.666056, 45.988191],
          [-88.6375, 45.98496],
          [-88.616405, 45.9877],
          [-88.613063, 45.990627],
          [-88.532414, 46.021212],
          [-88.507188, 46.0183],
          [-88.500133, 46.000457],
          [-88.497417, 45.995149],
          [-88.489608, 45.991381],
          [-88.423437, 45.98193],
          [-88.376447, 45.989458],
          [-88.295559, 45.951459],
          [-88.250133, 45.963147],
          [-88.244452, 45.952142],
          [-88.239672, 45.948982],
          [-88.211158, 45.944531],
          [-88.202247, 45.949584],
          [-88.175532, 45.944897],
          [-88.139372, 45.929932],
          [-88.118516, 45.918562],
          [-88.102603, 45.909888],
          [-88.073944, 45.875593],
          [-88.073134, 45.871952],
          [-88.088825, 45.85586],
          [-88.106622, 45.841072],
          [-88.114267, 45.837891],
          [-88.13364, 45.823128],
          [-88.13611, 45.819029],
          [-88.130434, 45.81002],
          [-88.094047, 45.785658],
          [-88.072091, 45.780261],
          [-88.058256, 45.780719],
          [-88.059246, 45.713063],
          [-88.425302, 45.722426]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55041",
      state: "55",
      county: "041",
      name: "Forest County",
      censusArea: 1014.067,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.046315, 45.464762],
          [-89.046781, 45.637517],
          [-89.047159, 45.640801],
          [-89.047481, 45.895359],
          [-89.047562, 45.982167],
          [-88.933567, 45.982098],
          [-88.932558, 46.073601],
          [-88.85027, 46.040274],
          [-88.848464, 46.038858],
          [-88.840584, 46.031112],
          [-88.815629, 46.02232],
          [-88.769712, 46.018968],
          [-88.739994, 46.027308],
          [-88.730675, 46.026535],
          [-88.683198, 46.014563],
          [-88.67598, 45.722835],
          [-88.425302, 45.722426],
          [-88.428102, 45.37701],
          [-88.677826, 45.378682],
          [-88.926025, 45.378637],
          [-88.923062, 45.465137],
          [-89.046315, 45.464762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55043",
      state: "55",
      county: "043",
      name: "Grant County",
      censusArea: 1146.849,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.949213, 42.685573],
          [-90.976314, 42.695996],
          [-91.053733, 42.738238],
          [-91.064896, 42.757272],
          [-91.069549, 42.769628],
          [-91.077643, 42.803798],
          [-91.100565, 42.883078],
          [-91.117411, 42.895837],
          [-91.143375, 42.90467],
          [-91.14556, 42.90798],
          [-91.163064, 42.986781],
          [-91.06527, 42.992074],
          [-90.845746, 43.077625],
          [-90.666786, 43.171778],
          [-90.545408, 43.211455],
          [-90.429825, 43.20094],
          [-90.426902, 42.812862],
          [-90.426378, 42.507059],
          [-90.437011, 42.507147],
          [-90.474955, 42.507484],
          [-90.479446, 42.507416],
          [-90.491716, 42.507624],
          [-90.532254, 42.507573],
          [-90.544347, 42.507707],
          [-90.544799, 42.507713],
          [-90.551165, 42.507691],
          [-90.555862, 42.507509],
          [-90.565441, 42.5076],
          [-90.614589, 42.508053],
          [-90.617731, 42.508077],
          [-90.640927, 42.508302],
          [-90.636927, 42.513202],
          [-90.636727, 42.518702],
          [-90.645627, 42.5441],
          [-90.702671, 42.630756],
          [-90.709204, 42.636078],
          [-90.720209, 42.640758],
          [-90.769495, 42.651443],
          [-90.84391, 42.663071],
          [-90.896961, 42.674407],
          [-90.949213, 42.685573]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55047",
      state: "55",
      county: "047",
      name: "Green Lake County",
      censusArea: 349.437,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.006848, 43.633045],
          [-89.245437, 43.643083],
          [-89.245471, 43.766416],
          [-89.198299, 43.812677],
          [-89.168612, 43.982834],
          [-88.886193, 43.983233],
          [-88.885697, 43.895184],
          [-88.886052, 43.63354],
          [-89.006848, 43.633045]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55049",
      state: "55",
      county: "049",
      name: "Iowa County",
      censusArea: 762.58,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.83841, 42.813799],
          [-90.426902, 42.812862],
          [-90.429825, 43.20094],
          [-90.292534, 43.206859],
          [-90.193814, 43.164464],
          [-89.838135, 43.206057],
          [-89.838167, 42.857397],
          [-89.83841, 42.813799]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55055",
      state: "55",
      county: "055",
      name: "Jefferson County",
      censusArea: 556.474,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.539619, 43.107605],
          [-88.542026, 42.93094],
          [-88.541535, 42.842996],
          [-88.659279, 42.842541],
          [-88.707961, 42.842657],
          [-88.771719, 42.842825],
          [-88.777076, 42.842694],
          [-89.013489, 42.847347],
          [-89.00892, 43.197721],
          [-88.832098, 43.197897],
          [-88.535843, 43.196048],
          [-88.539619, 43.107605]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55057",
      state: "55",
      county: "057",
      name: "Juneau County",
      censusArea: 766.925,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.785809, 43.641049],
          [-89.823444, 43.641035],
          [-89.824362, 43.641146],
          [-89.825916, 43.641011],
          [-89.831781, 43.641095],
          [-89.835589, 43.64092],
          [-89.838112, 43.64093],
          [-90.312404, 43.640988],
          [-90.312195, 43.731312],
          [-90.312575, 44.155284],
          [-90.31257, 44.240933],
          [-90.312035, 44.248758],
          [-89.902565, 44.24947],
          [-89.925822, 44.192715],
          [-89.903344, 44.178696],
          [-90.028807, 44.090232],
          [-89.968949, 43.994074],
          [-89.958101, 43.859902],
          [-89.865478, 43.783177],
          [-89.785809, 43.641049]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55059",
      state: "55",
      county: "059",
      name: "Kenosha County",
      censusArea: 271.99,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.812632, 42.547562],
          [-87.812273, 42.52982],
          [-87.809672, 42.51482],
          [-87.800477, 42.49192],
          [-87.800561, 42.49192],
          [-87.815872, 42.49192],
          [-87.843594, 42.492307],
          [-87.900242, 42.49302],
          [-87.971279, 42.494019],
          [-87.99018, 42.494519],
          [-88.049782, 42.495319],
          [-88.199521, 42.496013],
          [-88.200172, 42.496016],
          [-88.2169, 42.495923],
          [-88.271691, 42.494818],
          [-88.304692, 42.494773],
          [-88.305891, 42.610817],
          [-88.188173, 42.611456],
          [-88.188306, 42.669709],
          [-87.805768, 42.668128],
          [-87.807082, 42.664571],
          [-87.80988, 42.656997],
          [-87.809885, 42.656982],
          [-87.811371, 42.652959],
          [-87.813445, 42.647345],
          [-87.814674, 42.64402],
          [-87.819407, 42.617327],
          [-87.819674, 42.61582],
          [-87.819374, 42.60662],
          [-87.815736, 42.596045],
          [-87.815074, 42.59412],
          [-87.810873, 42.58732],
          [-87.811617, 42.584809],
          [-87.812871, 42.580576],
          [-87.813273, 42.57922],
          [-87.813223, 42.576759],
          [-87.813142, 42.572768],
          [-87.813135, 42.572424],
          [-87.812851, 42.558352],
          [-87.812724, 42.552104],
          [-87.812632, 42.547562]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56027",
      state: "56",
      county: "027",
      name: "Niobrara County",
      censusArea: 2626.037,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.655639, 42.609468],
          [-104.892437, 42.608939],
          [-104.899941, 43.49964],
          [-104.054787, 43.503328],
          [-104.054786, 43.503072],
          [-104.054779, 43.477815],
          [-104.054766, 43.428914],
          [-104.054614, 43.390949],
          [-104.054403, 43.325914],
          [-104.054218, 43.30437],
          [-104.053884, 43.297047],
          [-104.053876, 43.289801],
          [-104.053127, 43.000585],
          [-104.052863, 42.754569],
          [-104.052809, 42.749966],
          [-104.052583, 42.650062],
          [-104.052741, 42.633982],
          [-104.052586, 42.630917],
          [-104.052773, 42.611766],
          [-104.61235, 42.608805],
          [-104.655639, 42.609468]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56029",
      state: "56",
      county: "029",
      name: "Park County",
      censusArea: 6942.078,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.053179, 44.008022],
          [-110.053357, 44.132628],
          [-110.119193, 44.132584],
          [-110.111284, 44.199375],
          [-110.1511, 44.292464],
          [-110.296648, 44.431341],
          [-110.29485, 44.546923],
          [-110.375334, 44.583016],
          [-110.667899, 44.582792],
          [-110.667867, 44.666291],
          [-111.055495, 44.666262],
          [-111.056888, 44.866658],
          [-111.055629, 44.933578],
          [-111.056207, 44.935901],
          [-111.044275, 45.001345],
          [-110.875772, 45.002101],
          [-110.750767, 44.997948],
          [-110.705272, 44.992324],
          [-110.552433, 44.992237],
          [-110.547165, 44.992459],
          [-110.221343, 44.996176],
          [-110.110103, 45.003905],
          [-110.026347, 45.003665],
          [-110.025544, 45.003602],
          [-109.798687, 45.002188],
          [-109.269294, 45.005283],
          [-109.263431, 45.005345],
          [-109.103445, 45.005904],
          [-109.08301, 44.99961],
          [-109.062262, 44.999623],
          [-108.621313, 45.000408],
          [-108.592296, 44.871444],
          [-108.592494, 44.52093],
          [-108.57342, 44.520811],
          [-108.573797, 44.168468],
          [-108.550562, 44.168455],
          [-108.550758, 44.079403],
          [-108.792277, 44.079616],
          [-108.791779, 43.991053],
          [-108.915985, 43.990854],
          [-108.912783, 43.904754],
          [-109.092878, 43.904474],
          [-109.093603, 43.817619],
          [-109.312949, 43.813293],
          [-109.542332, 43.961352],
          [-109.641808, 43.922785],
          [-109.665893, 43.845726],
          [-109.7899, 43.803252],
          [-109.843455, 43.819864],
          [-109.816179, 43.881974],
          [-109.858754, 43.957988],
          [-109.990073, 43.949066],
          [-110.053179, 44.008022]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56035",
      state: "56",
      county: "035",
      name: "Sublette County",
      censusArea: 4886.537,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.05157, 43.464783],
          [-109.754717, 43.4632],
          [-109.754462, 43.365905],
          [-109.694942, 43.366152],
          [-109.692716, 43.26503],
          [-109.651451, 43.167036],
          [-109.560451, 43.083538],
          [-109.552579, 43.012538],
          [-109.370658, 42.945728],
          [-109.31536, 42.856131],
          [-109.072851, 42.693514],
          [-109.069477, 42.433477],
          [-109.043801, 42.433644],
          [-109.043864, 42.263684],
          [-109.496675, 42.263318],
          [-110.053708, 42.270744],
          [-110.541634, 42.277491],
          [-110.590074, 42.710087],
          [-110.623477, 42.710181],
          [-110.587662, 42.779188],
          [-110.587993, 43.126147],
          [-110.578656, 43.235224],
          [-110.57838, 43.29164],
          [-110.343959, 43.291165],
          [-110.344218, 43.377918],
          [-110.05731, 43.377768],
          [-110.05157, 43.464783]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56037",
      state: "56",
      county: "037",
      name: "Sweetwater County",
      censusArea: 10426.649,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.048, 41.578015],
          [-110.053708, 42.270744],
          [-109.496675, 42.263318],
          [-109.043864, 42.263684],
          [-108.375655, 42.263672],
          [-107.522722, 42.261756],
          [-107.503821, 42.087957],
          [-107.50972, 41.657452],
          [-107.929736, 41.659596],
          [-107.918421, 41.002036],
          [-108.250649, 41.000114],
          [-109.050076, 41.000659],
          [-109.207383, 41.001459],
          [-109.676421, 40.998395],
          [-109.713877, 40.998266],
          [-109.715409, 40.998191],
          [-109.854302, 40.997661],
          [-109.855299, 40.997614],
          [-109.999838, 40.99733],
          [-110.000708, 40.997352],
          [-110.048474, 40.997103],
          [-110.048, 41.578015]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56043",
      state: "56",
      county: "043",
      name: "Washakie County",
      censusArea: 2238.549,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.550758, 44.079403],
          [-108.550562, 44.168455],
          [-107.146808, 44.165666],
          [-107.128584, 44.165692],
          [-107.110734, 43.500286],
          [-107.534897, 43.501362],
          [-107.595105, 43.501397],
          [-107.595157, 43.646427],
          [-107.716218, 43.645766],
          [-107.715876, 43.733343],
          [-107.956577, 43.733446],
          [-107.956448, 43.818666],
          [-108.174938, 43.818968],
          [-108.185255, 43.848255],
          [-108.309695, 43.846619],
          [-108.30936, 43.903255],
          [-108.429563, 43.903355],
          [-108.427064, 43.992054],
          [-108.489431, 43.991866],
          [-108.490254, 44.07976],
          [-108.550758, 44.079403]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53043",
      state: "53",
      county: "043",
      name: "Lincoln County",
      censusArea: 2310.494,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.820784, 47.825188],
          [-117.823695, 47.260378],
          [-117.960143, 47.260449],
          [-118.838281, 47.260957],
          [-118.979519, 47.261712],
          [-118.97648, 47.659033],
          [-118.973502, 47.94357],
          [-118.954307, 47.940809],
          [-118.851972, 47.957686],
          [-118.711682, 47.903347],
          [-118.570396, 47.932318],
          [-118.54409, 47.883973],
          [-118.357065, 47.841599],
          [-118.340925, 47.894395],
          [-118.213025, 47.945298],
          [-118.078212, 47.800161],
          [-117.882083, 47.847152],
          [-117.820784, 47.825188]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53051",
      state: "53",
      county: "051",
      name: "Pend Oreille County",
      censusArea: 1399.989,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.429539, 48.999957],
          [-117.268192, 48.999928],
          [-117.268247, 48.999818],
          [-117.032351, 48.999188],
          [-117.033177, 48.846563],
          [-117.034358, 48.628523],
          [-117.034499, 48.620769],
          [-117.035285, 48.430113],
          [-117.035285, 48.429816],
          [-117.035254, 48.423144],
          [-117.035289, 48.422732],
          [-117.035178, 48.371221],
          [-117.035178, 48.370878],
          [-117.039599, 48.184387],
          [-117.039615, 48.184015],
          [-117.039582, 48.181124],
          [-117.039582, 48.180853],
          [-117.039583, 48.180313],
          [-117.039618, 48.178142],
          [-117.039413, 48.17725],
          [-117.039552, 48.17396],
          [-117.041401, 48.0855],
          [-117.041676, 48.04556],
          [-117.436903, 48.047206],
          [-117.437521, 48.134631],
          [-117.502314, 48.134864],
          [-117.499695, 48.656227],
          [-117.629241, 48.655848],
          [-117.629512, 48.742683],
          [-117.563527, 48.742464],
          [-117.563619, 48.836699],
          [-117.498577, 48.836813],
          [-117.498868, 48.922786],
          [-117.430203, 48.923068],
          [-117.429539, 48.999957]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53053",
      state: "53",
      county: "053",
      name: "Pierce County",
      censusArea: 1669.508,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.4442, 47.266723],
          [-122.437727, 47.268046],
          [-122.429605, 47.269707],
          [-122.418074, 47.281765],
          [-122.409199, 47.288556],
          [-122.413735, 47.293921],
          [-122.424235, 47.297521],
          [-122.432335, 47.296021],
          [-122.444635, 47.300421],
          [-122.443008, 47.306333],
          [-122.423535, 47.319121],
          [-122.418268, 47.320614],
          [-122.33486, 47.257594],
          [-122.229494, 47.257538],
          [-122.229208, 47.257538],
          [-122.143967, 47.257347],
          [-122.047909, 47.171645],
          [-121.839148, 47.143356],
          [-121.796678, 47.173059],
          [-121.379671, 47.086765],
          [-121.4565, 46.922733],
          [-121.52327, 46.87328],
          [-121.456356, 46.783744],
          [-121.758941, 46.783734],
          [-121.83259, 46.729721],
          [-122.202721, 46.762628],
          [-122.372977, 46.845038],
          [-122.491497, 46.867863],
          [-122.637396, 46.977097],
          [-122.7595, 47.141216],
          [-122.775997, 47.155187],
          [-122.792371, 47.174169],
          [-122.821426, 47.194211],
          [-122.85005, 47.272917],
          [-122.801451, 47.404016],
          [-122.53672, 47.40299],
          [-122.538429, 47.390829],
          [-122.528434, 47.378211],
          [-122.537935, 47.359203],
          [-122.551536, 47.35954],
          [-122.55584, 47.347519],
          [-122.57134, 47.327219],
          [-122.575985, 47.32642],
          [-122.573739, 47.318419],
          [-122.571239, 47.315619],
          [-122.547747, 47.316403],
          [-122.547408, 47.317734],
          [-122.540238, 47.31852],
          [-122.533338, 47.31662],
          [-122.471652, 47.277321],
          [-122.4442, 47.266723]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48197",
      state: "48",
      county: "197",
      name: "Hardeman County",
      censusArea: 695.114,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.99772, 34.311829],
          [-99.997508, 34.558017],
          [-99.997853, 34.561485],
          [-99.954567, 34.578195],
          [-99.94572, 34.579273],
          [-99.923211, 34.574552],
          [-99.884842, 34.546953],
          [-99.874403, 34.537095],
          [-99.872357, 34.532096],
          [-99.864922, 34.523018],
          [-99.846399, 34.505045],
          [-99.793684, 34.453894],
          [-99.767234, 34.430502],
          [-99.69497, 34.378333],
          [-99.663948, 34.37368],
          [-99.616793, 34.375391],
          [-99.58006, 34.416653],
          [-99.574367, 34.418281],
          [-99.515385, 34.414333],
          [-99.499875, 34.409608],
          [-99.474161, 34.398052],
          [-99.475115, 34.39593],
          [-99.475294, 34.085618],
          [-99.547509, 34.05598],
          [-99.65301, 34.098971],
          [-99.772043, 34.093286],
          [-99.997621, 34.224355],
          [-99.997867, 34.282934],
          [-99.997671, 34.284652],
          [-99.99772, 34.311829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48199",
      state: "48",
      county: "199",
      name: "Hardin County",
      censusArea: 890.567,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.545717, 30.526977],
          [-94.071689, 30.526033],
          [-94.116498, 30.433966],
          [-94.083386, 30.342181],
          [-94.117604, 30.241665],
          [-94.115917, 30.160347],
          [-94.209577, 30.187724],
          [-94.292629, 30.117671],
          [-94.44491, 30.113015],
          [-94.603159, 30.130904],
          [-94.732732, 30.490066],
          [-94.537926, 30.491059],
          [-94.545717, 30.526977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48201",
      state: "48",
      county: "201",
      name: "Harris County",
      censusArea: 1703.478,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.688349, 29.710862],
          [-95.82622, 29.78828],
          [-95.960733, 30.163415],
          [-95.80333, 30.090096],
          [-95.65543, 30.108994],
          [-95.551421, 30.170606],
          [-95.505125, 30.145153],
          [-95.264013, 30.032217],
          [-95.096708, 30.167214],
          [-95.031179, 29.993324],
          [-94.985364, 29.972279],
          [-94.98146, 29.884276],
          [-94.908654, 29.825181],
          [-94.931154, 29.673874],
          [-94.934167, 29.678682],
          [-94.936089, 29.692704],
          [-94.942681, 29.697778],
          [-94.965963, 29.70033],
          [-94.972666, 29.68487],
          [-94.988924, 29.672202],
          [-95.005398, 29.659366],
          [-95.005648, 29.658985],
          [-95.006381, 29.657871],
          [-95.007623, 29.65598],
          [-95.011025, 29.650803],
          [-95.011683, 29.649802],
          [-95.01272, 29.647088],
          [-95.013777, 29.644322],
          [-95.015636, 29.639457],
          [-95.0156, 29.639285],
          [-95.013623, 29.62979],
          [-95.013499, 29.629194],
          [-95.01166, 29.627768],
          [-95.000781, 29.619331],
          [-95.00046, 29.619082],
          [-94.999481, 29.618323],
          [-94.999082, 29.618014],
          [-94.988871, 29.610095],
          [-94.984831, 29.604361],
          [-94.982936, 29.60167],
          [-94.982706, 29.601344],
          [-94.982855, 29.601103],
          [-94.982923, 29.600992],
          [-94.988993, 29.591155],
          [-95.00767, 29.574257],
          [-95.016145, 29.559336],
          [-95.016353, 29.55897],
          [-95.016627, 29.558487],
          [-95.016672, 29.558388],
          [-95.018253, 29.554885],
          [-95.018198, 29.554618],
          [-95.018191, 29.554584],
          [-95.163927, 29.498051],
          [-95.216422, 29.55608],
          [-95.297409, 29.596514],
          [-95.424118, 29.580233],
          [-95.435618, 29.585633],
          [-95.440415, 29.588065],
          [-95.44697, 29.590978],
          [-95.537948, 29.633282],
          [-95.610025, 29.66699],
          [-95.687082, 29.710078],
          [-95.687994, 29.710667],
          [-95.688349, 29.710862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48203",
      state: "48",
      county: "203",
      name: "Harrison County",
      censusArea: 899.951,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.491818, 32.394137],
          [-94.579219, 32.394916],
          [-94.701986, 32.421782],
          [-94.70179, 32.652209],
          [-94.70214, 32.793088],
          [-94.507323, 32.693738],
          [-94.390783, 32.694377],
          [-94.242809, 32.75073],
          [-94.043147, 32.69303],
          [-94.042913, 32.655127],
          [-94.04278, 32.643466],
          [-94.042824, 32.640305],
          [-94.042926, 32.622015],
          [-94.042929, 32.61826],
          [-94.042919, 32.610142],
          [-94.043083, 32.564261],
          [-94.043142, 32.559502],
          [-94.043081, 32.513613],
          [-94.042885, 32.505145],
          [-94.042911, 32.492852],
          [-94.043089, 32.486561],
          [-94.043072, 32.4843],
          [-94.042955, 32.480261],
          [-94.042995, 32.478004],
          [-94.042902, 32.472906],
          [-94.042875, 32.471348],
          [-94.042903, 32.470386],
          [-94.042908, 32.439891],
          [-94.042986, 32.435507],
          [-94.042899, 32.400659],
          [-94.042923, 32.399918],
          [-94.042901, 32.392283],
          [-94.347563, 32.330682],
          [-94.491818, 32.394137]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48211",
      state: "48",
      county: "211",
      name: "Hemphill County",
      censusArea: 906.286,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.540221, 36.056491],
          [-100.000399, 36.055677],
          [-100.000396, 35.880948],
          [-100.000392, 35.619115],
          [-100.540158, 35.619296],
          [-100.540221, 36.056491]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48213",
      state: "48",
      county: "213",
      name: "Henderson County",
      censusArea: 873.748,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.449165, 32.355233],
          [-95.488733, 32.232486],
          [-95.459908, 32.135559],
          [-95.456843, 32.112307],
          [-95.447088, 32.107291],
          [-95.428512, 32.084475],
          [-96.052786, 32.005895],
          [-96.05288, 32.008198],
          [-96.05478, 32.012526],
          [-96.115693, 32.160915],
          [-96.383082, 32.328851],
          [-96.452138, 32.358631],
          [-96.075899, 32.357505],
          [-95.449165, 32.355233]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48215",
      state: "48",
      county: "215",
      name: "Hidalgo County",
      censusArea: 1570.865,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.861875, 26.348156],
          [-97.861837, 26.15948],
          [-97.861822, 26.069921],
          [-97.863056, 26.061273],
          [-97.888699, 26.064931],
          [-98.008307, 26.061147],
          [-98.011883, 26.056608],
          [-98.044517, 26.043746],
          [-98.132605, 26.057507],
          [-98.241664, 26.074544],
          [-98.264514, 26.085507],
          [-98.386694, 26.157872],
          [-98.438822, 26.212022],
          [-98.466167, 26.223245],
          [-98.58581, 26.257403],
          [-98.58672, 26.257456],
          [-98.493812, 26.441167],
          [-98.491943, 26.445362],
          [-98.478268, 26.469813],
          [-98.32067, 26.783081],
          [-97.985494, 26.780917],
          [-97.985303, 26.615809],
          [-97.957405, 26.611769],
          [-98.004189, 26.448784],
          [-97.86192, 26.433579],
          [-97.861875, 26.348156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48223",
      state: "48",
      county: "223",
      name: "Hopkins County",
      censusArea: 767.174,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.862521, 32.979571],
          [-95.863296, 33.000117],
          [-95.861778, 33.21933],
          [-95.730784, 33.305405],
          [-95.584353, 33.359405],
          [-95.314165, 33.367232],
          [-95.308593, 33.37719],
          [-95.308957, 32.962572],
          [-95.665389, 32.960434],
          [-95.862521, 32.979571]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48235",
      state: "48",
      county: "235",
      name: "Irion County",
      censusArea: 1051.563,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.688764, 31.086576],
          [-100.962176, 31.08249],
          [-101.274799, 31.079379],
          [-101.267947, 31.528688],
          [-101.223944, 31.530035],
          [-101.223243, 31.528661],
          [-100.693065, 31.523954],
          [-100.688764, 31.086576]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48237",
      state: "48",
      county: "237",
      name: "Jack County",
      censusArea: 910.662,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.978919, 33.467129],
          [-97.978967, 33.433748],
          [-97.918189, 33.433873],
          [-97.921642, 33.001284],
          [-98.056094, 33.003332],
          [-98.426553, 33.007913],
          [-98.420666, 33.396195],
          [-98.421419, 33.466797],
          [-97.978919, 33.467129]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48239",
      state: "48",
      county: "239",
      name: "Jackson County",
      censusArea: 829.435,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.309178, 28.963291],
          [-96.323522, 28.675597],
          [-96.428394, 28.703523],
          [-96.575141, 28.702837],
          [-96.577727, 28.704365],
          [-96.57802, 28.704538],
          [-96.579639, 28.712312],
          [-96.579669, 28.712455],
          [-96.580564, 28.716752],
          [-96.584091, 28.722798],
          [-96.584127, 28.722859],
          [-96.584196, 28.722877],
          [-96.591647, 28.724838],
          [-96.593796, 28.725403],
          [-96.604921, 28.72221],
          [-96.634419, 28.713743],
          [-96.634912, 28.713601],
          [-96.645867, 28.710457],
          [-96.711514, 28.851411],
          [-96.792133, 28.918803],
          [-96.832103, 29.021299],
          [-96.938587, 29.063244],
          [-96.658466, 29.263876],
          [-96.643316, 29.25025],
          [-96.642303, 29.249576],
          [-96.640315, 29.247804],
          [-96.309178, 28.963291]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48243",
      state: "48",
      county: "243",
      name: "Jeff Davis County",
      censusArea: 2264.555,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.439976, 30.665939],
          [-103.80068, 30.412527],
          [-103.90101, 30.41219],
          [-104.976636, 30.628946],
          [-104.979527, 30.629247],
          [-104.985365, 30.623792],
          [-104.97961, 30.629363],
          [-104.917163, 30.663626],
          [-104.102351, 31.105203],
          [-103.585084, 30.76647],
          [-103.439976, 30.665939]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48249",
      state: "48",
      county: "249",
      name: "Jim Wells County",
      censusArea: 864.966,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.232464, 27.262487],
          [-98.235497, 28.057966],
          [-97.883148, 28.0569],
          [-97.798517, 27.995659],
          [-97.934274, 27.885202],
          [-97.942146, 27.635932],
          [-98.0598, 27.635869],
          [-98.058078, 27.260981],
          [-98.232464, 27.262487]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48253",
      state: "48",
      county: "253",
      name: "Jones County",
      censusArea: 928.554,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.988827, 32.960121],
          [-99.612001, 32.956962],
          [-99.612026, 32.514654],
          [-99.629579, 32.514657],
          [-99.629639, 32.520713],
          [-100.146543, 32.522793],
          [-100.144224, 32.959978],
          [-99.988827, 32.960121]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48259",
      state: "48",
      county: "259",
      name: "Kendall County",
      censusArea: 662.453,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.917725, 29.781398],
          [-98.920147, 30.13829],
          [-98.587897, 30.138954],
          [-98.414018, 29.937557],
          [-98.644703, 29.746487],
          [-98.646124, 29.745181],
          [-98.778782, 29.720167],
          [-98.781017, 29.72089],
          [-98.917725, 29.781398]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51185",
      state: "51",
      county: "185",
      name: "Tazewell County",
      censusArea: 518.846,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.362156, 37.337687],
          [-81.320105, 37.299323],
          [-81.225104, 37.234874],
          [-81.328117, 37.139007],
          [-81.270259, 37.093004],
          [-81.462213, 37.035541],
          [-81.437182, 37.010091],
          [-81.576936, 36.989515],
          [-81.683038, 36.933014],
          [-81.780539, 36.958614],
          [-81.901087, 37.142546],
          [-81.738378, 37.240917],
          [-81.73332, 37.238127],
          [-81.728194, 37.239823],
          [-81.683544, 37.211452],
          [-81.678603, 37.202467],
          [-81.560625, 37.206663],
          [-81.558353, 37.208145],
          [-81.557315, 37.207697],
          [-81.507325, 37.2338],
          [-81.50488, 37.247697],
          [-81.504168, 37.250115],
          [-81.409729, 37.284837],
          [-81.409196, 37.286071],
          [-81.388132, 37.319903],
          [-81.386727, 37.320474],
          [-81.38581, 37.320085],
          [-81.384914, 37.318832],
          [-81.384127, 37.318596],
          [-81.380159, 37.317838],
          [-81.377349, 37.318447],
          [-81.371315, 37.324115],
          [-81.367599, 37.327569],
          [-81.369379, 37.331827],
          [-81.36809, 37.332423],
          [-81.367052, 37.334504],
          [-81.366315, 37.335927],
          [-81.362156, 37.337687]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51191",
      state: "51",
      county: "191",
      name: "Washington County",
      censusArea: 560.975,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.332089, 36.709355],
          [-82.107614, 36.796385],
          [-82.077936, 36.855005],
          [-81.949151, 36.864409],
          [-81.836842, 36.929013],
          [-81.699441, 36.789614],
          [-81.605835, 36.638717],
          [-81.610663, 36.616913],
          [-81.6469, 36.611918],
          [-81.826742, 36.614215],
          [-81.922644, 36.616213],
          [-81.934144, 36.594213],
          [-82.14607, 36.594712],
          [-82.114497, 36.638542],
          [-82.243274, 36.595699],
          [-82.293814, 36.595565],
          [-82.308, 36.619525],
          [-82.332089, 36.709355]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51195",
      state: "51",
      county: "195",
      name: "Wise County",
      censusArea: 403.189,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.406633, 36.876356],
          [-82.634608, 36.878484],
          [-82.771308, 36.79988],
          [-82.885618, 36.900415],
          [-82.873777, 36.912299],
          [-82.872136, 36.913456],
          [-82.863468, 36.922308],
          [-82.861943, 36.924236],
          [-82.858461, 36.932717],
          [-82.858784, 36.933065],
          [-82.860537, 36.937439],
          [-82.861684, 36.939316],
          [-82.861282, 36.944848],
          [-82.860633, 36.94584],
          [-82.856099, 36.952471],
          [-82.855705, 36.953808],
          [-82.858443, 36.954036],
          [-82.860534, 36.956015],
          [-82.862866, 36.957765],
          [-82.864211, 36.957983],
          [-82.865404, 36.958084],
          [-82.867358, 36.963182],
          [-82.87023, 36.965498],
          [-82.870274, 36.965993],
          [-82.869183, 36.974182],
          [-82.869183, 36.974183],
          [-82.868455, 36.976481],
          [-82.867535, 36.977518],
          [-82.866689, 36.978052],
          [-82.866019, 36.978272],
          [-82.864909, 36.97901],
          [-82.862926, 36.979975],
          [-82.852614, 36.984963],
          [-82.851397, 36.984497],
          [-82.840051, 36.987113],
          [-82.838549, 36.987027],
          [-82.836008, 36.988837],
          [-82.833843, 36.991973],
          [-82.830802, 36.993445],
          [-82.829125, 36.997541],
          [-82.830588, 37.000945],
          [-82.818006, 37.006161],
          [-82.815748, 37.007196],
          [-82.800531, 37.007944],
          [-82.79089, 37.00676],
          [-82.790462, 37.007263],
          [-82.789092, 37.007995],
          [-82.788897, 37.00816],
          [-82.777368, 37.015279],
          [-82.771795, 37.015716],
          [-82.722472, 37.045101],
          [-82.720597, 37.081833],
          [-82.721941, 37.105689],
          [-82.624878, 37.162932],
          [-82.565375, 37.196092],
          [-82.565329, 37.196118],
          [-82.553549, 37.200867],
          [-82.54622, 37.124982],
          [-82.467419, 37.022144],
          [-82.328009, 36.970905],
          [-82.298594, 36.905419],
          [-82.406633, 36.876356]
        ],
        [
          [-82.660291, 36.918804],
          [-82.615137, 36.905251],
          [-82.58889, 36.951182],
          [-82.616375, 36.956694],
          [-82.660291, 36.918804]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51520",
      state: "51",
      county: "520",
      name: "Bristol City County",
      lsad: "city",
      censusArea: 13.013,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.243274, 36.595699],
          [-82.114497, 36.638542],
          [-82.14607, 36.594712],
          [-82.148569, 36.594718],
          [-82.150727, 36.594673],
          [-82.173982, 36.594607],
          [-82.177247, 36.594768],
          [-82.18074, 36.594928],
          [-82.188491, 36.595179],
          [-82.210497, 36.595772],
          [-82.211005, 36.59586],
          [-82.221713, 36.595814],
          [-82.223445, 36.595721],
          [-82.225716, 36.595744],
          [-82.226653, 36.595743],
          [-82.243274, 36.595699]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51550",
      state: "51",
      county: "550",
      name: "Chesapeake City County",
      lsad: "city",
      censusArea: 340.8,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.313196, 36.550551],
          [-76.313215, 36.550551],
          [-76.491497, 36.550365],
          [-76.457469, 36.814841],
          [-76.420368, 36.866348],
          [-76.375679, 36.786062],
          [-76.289962, 36.822048],
          [-76.27471, 36.828039],
          [-76.257475, 36.820757],
          [-76.243765, 36.836862],
          [-76.22606, 36.839947],
          [-76.203667, 36.769655],
          [-76.068154, 36.69235],
          [-76.121955, 36.66575],
          [-76.12236, 36.550621],
          [-76.313196, 36.550551]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51580",
      state: "51",
      county: "580",
      name: "Covington County",
      lsad: "city",
      censusArea: 5.468,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.995941, 37.782229],
          [-80.00136, 37.789826],
          [-79.992955, 37.797063],
          [-79.992055, 37.79981],
          [-79.999078, 37.80624],
          [-79.98921, 37.810961],
          [-79.987402, 37.813585],
          [-79.984451, 37.812016],
          [-79.983631, 37.802278],
          [-79.967071, 37.803405],
          [-79.966452, 37.792484],
          [-79.980001, 37.781063],
          [-79.977265, 37.767344],
          [-79.966509, 37.757109],
          [-79.992566, 37.748244],
          [-80.008893, 37.762983],
          [-80.001196, 37.768848],
          [-79.995941, 37.782229]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51610",
      state: "51",
      county: "610",
      name: "Falls Church City County",
      lsad: "city",
      censusArea: 1.999,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.150287, 38.876192],
          [-77.154974, 38.872213],
          [-77.173243, 38.878604],
          [-77.189766, 38.878117],
          [-77.19434, 38.886065],
          [-77.188854, 38.896272],
          [-77.172169, 38.893329],
          [-77.150287, 38.876192]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51640",
      state: "51",
      county: "640",
      name: "Galax City County",
      lsad: "city",
      censusArea: 8.24,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.932271, 36.671593],
          [-80.920497, 36.678139],
          [-80.917573, 36.694133],
          [-80.877274, 36.692215],
          [-80.914173, 36.650246],
          [-80.916043, 36.629343],
          [-80.931655, 36.628494],
          [-80.921711, 36.643533],
          [-80.947754, 36.648361],
          [-80.944109, 36.676303],
          [-80.932271, 36.671593]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51678",
      state: "51",
      county: "678",
      name: "Lexington City County",
      lsad: "city",
      censusArea: 2.497,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.457611, 37.766222],
          [-79.461917, 37.78616],
          [-79.456515, 37.791261],
          [-79.415304, 37.789111],
          [-79.457611, 37.766222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51683",
      state: "51",
      county: "683",
      name: "Manassas City County",
      lsad: "city",
      censusArea: 9.881,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.45931, 38.772614],
          [-77.44751, 38.761115],
          [-77.451642, 38.738337],
          [-77.471425, 38.728152],
          [-77.524721, 38.723966],
          [-77.526457, 38.732466],
          [-77.495927, 38.770607],
          [-77.475711, 38.781214],
          [-77.474711, 38.781414],
          [-77.462209, 38.774514],
          [-77.45931, 38.772614]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51685",
      state: "51",
      county: "685",
      name: "Manassas Park City County",
      lsad: "city",
      censusArea: 2.534,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.475711, 38.781214],
          [-77.473195, 38.788248],
          [-77.458507, 38.787394],
          [-77.459499, 38.778395],
          [-77.445282, 38.770722],
          [-77.44061, 38.782615],
          [-77.422236, 38.768917],
          [-77.420682, 38.762242],
          [-77.427503, 38.7534],
          [-77.45931, 38.772614],
          [-77.462209, 38.774514],
          [-77.474711, 38.781414],
          [-77.475711, 38.781214]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51710",
      state: "51",
      county: "710",
      name: "Norfolk City County",
      lsad: "city",
      censusArea: 54.12,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.177019, 36.92929],
          [-76.210412, 36.832907],
          [-76.213959, 36.832347],
          [-76.22606, 36.839947],
          [-76.243765, 36.836862],
          [-76.257475, 36.820757],
          [-76.27471, 36.828039],
          [-76.289962, 36.822048],
          [-76.294262, 36.841647],
          [-76.313533, 36.858533],
          [-76.336784, 36.875027],
          [-76.32848, 36.893219],
          [-76.340769, 36.901603],
          [-76.344663, 36.919313],
          [-76.333158, 36.917293],
          [-76.328864, 36.918447],
          [-76.330765, 36.938647],
          [-76.327365, 36.959447],
          [-76.322764, 36.959147],
          [-76.315867, 36.955351],
          [-76.299364, 36.965547],
          [-76.297663, 36.968147],
          [-76.285063, 36.968747],
          [-76.267962, 36.964547],
          [-76.234961, 36.945147],
          [-76.22166, 36.939547],
          [-76.189959, 36.931447],
          [-76.177019, 36.92929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51730",
      state: "51",
      county: "730",
      name: "Petersburg City County",
      lsad: "city",
      censusArea: 22.932,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.399203, 37.17085],
          [-77.447521, 37.22271],
          [-77.4339, 37.224648],
          [-77.42889, 37.229678],
          [-77.41651, 37.233157],
          [-77.41542, 37.233796],
          [-77.413119, 37.234257],
          [-77.409877, 37.233723],
          [-77.406415, 37.235645],
          [-77.405034, 37.2361],
          [-77.403671, 37.237346],
          [-77.390393, 37.23998],
          [-77.383856, 37.238906],
          [-77.376728, 37.244993],
          [-77.399203, 37.17085]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51750",
      state: "51",
      county: "750",
      name: "Radford City County",
      lsad: "city",
      censusArea: 9.872,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.580067, 37.089603],
          [-80.590406, 37.102],
          [-80.596389, 37.118897],
          [-80.592875, 37.127866],
          [-80.573087, 37.139876],
          [-80.548837, 37.146557],
          [-80.529678, 37.134066],
          [-80.527625, 37.134427],
          [-80.524885, 37.14836],
          [-80.507317, 37.135752],
          [-80.532631, 37.118064],
          [-80.555171, 37.11742],
          [-80.55626, 37.099551],
          [-80.580067, 37.089603]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51790",
      state: "51",
      county: "790",
      name: "Staunton City County",
      lsad: "city",
      censusArea: 19.975,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.091666, 38.132142],
          [-79.09711, 38.184771],
          [-79.02301, 38.195777],
          [-79.049779, 38.121112],
          [-79.091666, 38.132142]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55065",
      state: "55",
      county: "065",
      name: "Lafayette County",
      censusArea: 633.588,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.426902, 42.812862],
          [-89.83841, 42.813799],
          [-89.837587, 42.505543],
          [-89.926224, 42.505788],
          [-89.926374, 42.505788],
          [-89.926484, 42.505787],
          [-89.985072, 42.506464],
          [-89.985645, 42.506431],
          [-89.997213, 42.506755],
          [-89.999314, 42.506914],
          [-90.017028, 42.507127],
          [-90.018665, 42.507288],
          [-90.093026, 42.50816],
          [-90.095004, 42.507885],
          [-90.142922, 42.508151],
          [-90.164363, 42.508272],
          [-90.181572, 42.508068],
          [-90.206073, 42.507747],
          [-90.22319, 42.507765],
          [-90.250622, 42.507521],
          [-90.253121, 42.50734],
          [-90.267143, 42.507642],
          [-90.269335, 42.507726],
          [-90.272864, 42.507531],
          [-90.303823, 42.507469],
          [-90.362652, 42.507048],
          [-90.367874, 42.507114],
          [-90.370673, 42.507111],
          [-90.405927, 42.506891],
          [-90.426378, 42.507059],
          [-90.426902, 42.812862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55075",
      state: "55",
      county: "075",
      name: "Marinette County",
      censusArea: 1399.347,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-87.500105, 45.061117],
            [-87.498964, 45.061231],
            [-87.497254, 45.061278],
            [-87.496415, 45.061072],
            [-87.495479, 45.060661],
            [-87.494543, 45.060044],
            [-87.493381, 45.059107],
            [-87.492864, 45.058376],
            [-87.49285, 45.058325],
            [-87.492702, 45.057805],
            [-87.492691, 45.057799],
            [-87.492573, 45.057736],
            [-87.492563, 45.057653],
            [-87.49254, 45.057462],
            [-87.492516, 45.057419],
            [-87.492411, 45.057233],
            [-87.492314, 45.056936],
            [-87.492023, 45.056502],
            [-87.491765, 45.056273],
            [-87.491636, 45.056227],
            [-87.491507, 45.05609],
            [-87.491345, 45.056022],
            [-87.490216, 45.055222],
            [-87.490151, 45.055039],
            [-87.490189, 45.055017],
            [-87.490312, 45.054948],
            [-87.490925, 45.055039],
            [-87.492668, 45.055792],
            [-87.494475, 45.056523],
            [-87.498379, 45.057733],
            [-87.49867, 45.057779],
            [-87.498863, 45.057847],
            [-87.500105, 45.058167],
            [-87.501572, 45.058602],
            [-87.504281, 45.059198],
            [-87.504967, 45.059287],
            [-87.506055, 45.059428],
            [-87.506086, 45.059461],
            [-87.506184, 45.059565],
            [-87.506109, 45.059644],
            [-87.506054, 45.059702],
            [-87.504488, 45.060024],
            [-87.50428, 45.060067],
            [-87.503602, 45.060272],
            [-87.50244, 45.060706],
            [-87.501802, 45.060841],
            [-87.500603, 45.061094],
            [-87.500105, 45.061117]
          ]
        ],
        [
          [
            [-87.590208, 45.095264],
            [-87.587147, 45.089495],
            [-87.587992, 45.085271],
            [-87.591583, 45.083792],
            [-87.594718, 45.085134],
            [-87.601849, 45.082297],
            [-87.610395, 45.075617],
            [-87.625748, 45.045157],
            [-87.624693, 45.014176],
            [-87.630298, 44.976865],
            [-87.661964, 44.973035],
            [-87.696492, 44.974233],
            [-87.76262, 44.965796],
            [-87.944436, 44.993057],
            [-87.942995, 45.020418],
            [-88.121152, 45.023581],
            [-88.118879, 45.110197],
            [-88.189247, 45.111844],
            [-88.186356, 45.197897],
            [-88.309063, 45.20158],
            [-88.305811, 45.37461],
            [-88.428102, 45.37701],
            [-88.425302, 45.722426],
            [-88.059246, 45.713063],
            [-88.058256, 45.780719],
            [-88.050634, 45.780972],
            [-88.039729, 45.789626],
            [-87.991447, 45.795393],
            [-87.868111, 45.749477],
            [-87.828827, 45.72276],
            [-87.807144, 45.708014],
            [-87.780808, 45.680349],
            [-87.781623, 45.67328],
            [-87.823672, 45.659817],
            [-87.824102, 45.647138],
            [-87.79588, 45.618846],
            [-87.792016, 45.616756],
            [-87.788798, 45.565947],
            [-87.792769, 45.499967],
            [-87.79896, 45.485147],
            [-87.806347, 45.472262],
            [-87.812976, 45.464159],
            [-87.820348, 45.460284],
            [-87.860432, 45.423504],
            [-87.888052, 45.354697],
            [-87.88517, 45.351736],
            [-87.850133, 45.340435],
            [-87.838141, 45.345101],
            [-87.835303, 45.35098],
            [-87.832612, 45.352249],
            [-87.790324, 45.353444],
            [-87.771384, 45.35121],
            [-87.751452, 45.351755],
            [-87.718891, 45.377462],
            [-87.693956, 45.389893],
            [-87.685934, 45.388711],
            [-87.675017, 45.382454],
            [-87.657349, 45.368752],
            [-87.647729, 45.350721],
            [-87.648126, 45.339396],
            [-87.709137, 45.260341],
            [-87.707779, 45.258343],
            [-87.711722, 45.243481],
            [-87.73171, 45.206792],
            [-87.739492, 45.202126],
            [-87.741732, 45.198201],
            [-87.735282, 45.176565],
            [-87.731389, 45.170989],
            [-87.659952, 45.107512],
            [-87.590208, 45.095264]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55077",
      state: "55",
      county: "077",
      name: "Marquette County",
      censusArea: 455.602,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.599985, 43.642596],
          [-89.599545, 43.644514],
          [-89.59795, 43.9821],
          [-89.168612, 43.982834],
          [-89.198299, 43.812677],
          [-89.245471, 43.766416],
          [-89.245437, 43.643083],
          [-89.599985, 43.642596]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55083",
      state: "55",
      county: "083",
      name: "Oconto County",
      censusArea: 997.988,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.76262, 44.965796],
          [-87.7627, 44.965786],
          [-87.762846, 44.965768],
          [-87.766115, 44.965351],
          [-87.766872, 44.965254],
          [-87.781298, 44.961738],
          [-87.781464, 44.961697],
          [-87.788987, 44.959863],
          [-87.789934, 44.959633],
          [-87.791372, 44.959282],
          [-87.791987, 44.959132],
          [-87.792325, 44.95905],
          [-87.812594, 44.954109],
          [-87.812989, 44.954013],
          [-87.813276, 44.953885],
          [-87.813866, 44.953623],
          [-87.815134, 44.95306],
          [-87.816151, 44.952608],
          [-87.817285, 44.952104],
          [-87.817551, 44.951986],
          [-87.817713, 44.951914],
          [-87.817912, 44.951826],
          [-87.818113, 44.951736],
          [-87.819313, 44.951203],
          [-87.819369, 44.951178],
          [-87.819525, 44.951109],
          [-87.820281, 44.950358],
          [-87.820848, 44.949793],
          [-87.82105, 44.949593],
          [-87.821485, 44.949161],
          [-87.821983, 44.948665],
          [-87.822192, 44.948457],
          [-87.837647, 44.933091],
          [-87.839028, 44.931718],
          [-87.843433, 44.924355],
          [-87.844299, 44.918524],
          [-87.842844, 44.912586],
          [-87.842719, 44.912077],
          [-87.84264, 44.911916],
          [-87.842375, 44.911371],
          [-87.840382, 44.907289],
          [-87.839561, 44.905607],
          [-87.837017, 44.902509],
          [-87.836743, 44.902177],
          [-87.836082, 44.901371],
          [-87.834275, 44.899172],
          [-87.833731, 44.89851],
          [-87.833521, 44.898254],
          [-87.832137, 44.896569],
          [-87.827751, 44.891229],
          [-87.832764, 44.880939],
          [-87.835497, 44.877544],
          [-87.836916, 44.87578],
          [-87.838359, 44.873987],
          [-87.847616, 44.870692],
          [-87.848324, 44.87044],
          [-87.84914, 44.86942],
          [-87.852789, 44.86486],
          [-87.854184, 44.859633],
          [-87.854681, 44.857771],
          [-87.854938, 44.857386],
          [-87.855962, 44.855854],
          [-87.857121, 44.85412],
          [-87.857469, 44.853599],
          [-87.865898, 44.840988],
          [-87.866237, 44.840481],
          [-87.871679, 44.839816],
          [-87.878218, 44.839016],
          [-87.885709, 44.835208],
          [-87.89257, 44.83172],
          [-87.895812, 44.830072],
          [-87.897931, 44.828995],
          [-87.899787, 44.828051],
          [-87.901137, 44.827365],
          [-87.901814, 44.825616],
          [-87.902166, 44.824708],
          [-87.903395, 44.821534],
          [-87.903844, 44.820376],
          [-87.904281, 44.819246],
          [-87.904484, 44.818723],
          [-87.905444, 44.816997],
          [-87.906582, 44.814949],
          [-87.911366, 44.806345],
          [-87.918874, 44.79284],
          [-87.923588, 44.784361],
          [-87.925845, 44.780301],
          [-87.926816, 44.778555],
          [-87.941453, 44.75608],
          [-87.95156, 44.753107],
          [-87.956849, 44.749272],
          [-87.964714, 44.74357],
          [-87.972654, 44.733687],
          [-87.983065, 44.72073],
          [-87.983494, 44.720196],
          [-87.983941, 44.717781],
          [-87.98395, 44.717735],
          [-87.984094, 44.716955],
          [-87.984247, 44.716129],
          [-87.984401, 44.715298],
          [-87.986055, 44.70636],
          [-87.986134, 44.705936],
          [-87.987296, 44.699657],
          [-87.987417, 44.699005],
          [-87.988667, 44.692255],
          [-87.98925, 44.689107],
          [-87.989344, 44.688598],
          [-87.989376, 44.688424],
          [-87.98947, 44.687918],
          [-87.989717, 44.686582],
          [-87.989912, 44.677587],
          [-88.242687, 44.679631],
          [-88.248213, 44.852739],
          [-88.489149, 44.855445],
          [-88.483732, 45.116887],
          [-88.641159, 45.117346],
          [-88.680066, 45.204994],
          [-88.677826, 45.378682],
          [-88.428102, 45.37701],
          [-88.305811, 45.37461],
          [-88.309063, 45.20158],
          [-88.186356, 45.197897],
          [-88.189247, 45.111844],
          [-88.118879, 45.110197],
          [-88.121152, 45.023581],
          [-87.942995, 45.020418],
          [-87.944436, 44.993057],
          [-87.76262, 44.965796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55085",
      state: "55",
      county: "085",
      name: "Oneida County",
      censusArea: 1112.97,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.043414, 45.555082],
          [-90.043309, 45.730053],
          [-90.044358, 45.749992],
          [-90.042854, 45.897151],
          [-89.300631, 45.901464],
          [-89.300849, 45.860903],
          [-89.175245, 45.856579],
          [-89.178269, 45.899395],
          [-89.047481, 45.895359],
          [-89.047159, 45.640801],
          [-89.046781, 45.637517],
          [-89.046315, 45.464762],
          [-89.364049, 45.469886],
          [-89.424824, 45.467257],
          [-89.428258, 45.555353],
          [-90.043414, 45.555082]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55091",
      state: "55",
      county: "091",
      name: "Pepin County",
      censusArea: 231.983,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.650361, 44.683633],
          [-91.650474, 44.624963],
          [-91.650324, 44.623005],
          [-91.650248, 44.596649],
          [-92.02918, 44.586292],
          [-92.083925, 44.406211],
          [-92.115296, 44.416056],
          [-92.221083, 44.440386],
          [-92.233897, 44.446334],
          [-92.24201, 44.454254],
          [-92.276784, 44.473649],
          [-92.291005, 44.485464],
          [-92.302215, 44.500298],
          [-92.303046, 44.518646],
          [-92.314071, 44.538014],
          [-92.316425, 44.540792],
          [-92.1353, 44.53958],
          [-92.135202, 44.684363],
          [-91.650361, 44.683633]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55093",
      state: "55",
      county: "093",
      name: "Pierce County",
      censusArea: 573.748,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.765461, 44.836202],
          [-92.761845, 44.860879],
          [-92.761677, 44.862022],
          [-92.136348, 44.857848],
          [-92.135202, 44.684363],
          [-92.1353, 44.53958],
          [-92.316425, 44.540792],
          [-92.319938, 44.54494],
          [-92.329013, 44.550895],
          [-92.340058, 44.555273],
          [-92.357313, 44.55848],
          [-92.491773, 44.566003],
          [-92.534186, 44.570375],
          [-92.549685, 44.576],
          [-92.619774, 44.629214],
          [-92.621733, 44.638983],
          [-92.632105, 44.649027],
          [-92.732042, 44.713775],
          [-92.737259, 44.717155],
          [-92.802402, 44.745167],
          [-92.802201, 44.745714],
          [-92.784443, 44.793958],
          [-92.765461, 44.836202]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55097",
      state: "55",
      county: "097",
      name: "Portage County",
      censusArea: 800.679,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.59798, 44.245725],
          [-89.724746, 44.247678],
          [-89.726622, 44.511191],
          [-89.84441, 44.511542],
          [-89.844931, 44.684945],
          [-89.223745, 44.681365],
          [-89.224892, 44.243546],
          [-89.59798, 44.245725]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53063",
      state: "53",
      county: "063",
      name: "Spokane County",
      censusArea: 1763.791,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.042485, 47.766525],
          [-117.042521, 47.764896],
          [-117.042623, 47.761223],
          [-117.042657, 47.760857],
          [-117.042059, 47.7451],
          [-117.042135, 47.7441],
          [-117.041678, 47.72271],
          [-117.041633, 47.7064],
          [-117.041532, 47.683194],
          [-117.041431, 47.68],
          [-117.041431, 47.678185],
          [-117.041431, 47.67814],
          [-117.041174, 47.55853],
          [-117.041276, 47.55821],
          [-117.040745, 47.532909],
          [-117.040545, 47.527562],
          [-117.040514, 47.522351],
          [-117.039945, 47.477823],
          [-117.039971, 47.463309],
          [-117.039948, 47.434885],
          [-117.03995, 47.412412],
          [-117.039882, 47.399085],
          [-117.039857, 47.366093],
          [-117.039843, 47.347201],
          [-117.040019, 47.259272],
          [-117.823695, 47.260378],
          [-117.820784, 47.825188],
          [-117.699097, 47.856089],
          [-117.535661, 47.795111],
          [-117.535039, 48.047865],
          [-117.436903, 48.047206],
          [-117.041676, 48.04556],
          [-117.041874, 47.977387],
          [-117.042485, 47.766525]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53067",
      state: "53",
      county: "067",
      name: "Thurston County",
      censusArea: 721.961,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.821426, 47.194211],
          [-122.792371, 47.174169],
          [-122.775997, 47.155187],
          [-122.7595, 47.141216],
          [-122.637396, 46.977097],
          [-122.491497, 46.867863],
          [-122.372977, 46.845038],
          [-122.202721, 46.762628],
          [-123.160909, 46.76433],
          [-123.160588, 46.793383],
          [-123.158439, 46.996237],
          [-123.202066, 47.084895],
          [-123.075101, 47.084503],
          [-122.936716, 47.190246],
          [-122.905659, 47.155614],
          [-122.881717, 47.156285],
          [-122.821426, 47.194211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53069",
      state: "53",
      county: "069",
      name: "Wahkiakum County",
      censusArea: 263.375,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.363638, 46.146241],
          [-123.371433, 46.146372],
          [-123.430847, 46.181827],
          [-123.427629, 46.229348],
          [-123.447592, 46.249832],
          [-123.468743, 46.264531],
          [-123.474844, 46.267831],
          [-123.484244, 46.269531],
          [-123.547659, 46.259109],
          [-123.547636, 46.265595],
          [-123.559923, 46.265098],
          [-123.564405, 46.262172],
          [-123.581642, 46.260502],
          [-123.613544, 46.259988],
          [-123.669501, 46.266832],
          [-123.679125, 46.272502],
          [-123.68008, 46.277943],
          [-123.678069, 46.286469],
          [-123.67876, 46.290721],
          [-123.680574, 46.296025],
          [-123.687763, 46.299235],
          [-123.700764, 46.305278],
          [-123.724273, 46.301161],
          [-123.724038, 46.295058],
          [-123.727913, 46.289661],
          [-123.72606, 46.385626],
          [-123.358247, 46.384227],
          [-123.217841, 46.385128],
          [-123.213054, 46.172541],
          [-123.280166, 46.144843],
          [-123.363638, 46.146241]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53077",
      state: "53",
      county: "077",
      name: "Yakima County",
      censusArea: 4295.396,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.523938, 46.044048],
          [-121.523556, 46.388221],
          [-121.386557, 46.410601],
          [-121.451194, 46.533537],
          [-121.35232, 46.712366],
          [-121.456356, 46.783744],
          [-121.52327, 46.87328],
          [-121.4565, 46.922733],
          [-121.379671, 47.086765],
          [-121.282039, 47.088954],
          [-121.089794, 46.991039],
          [-121.02649, 46.911341],
          [-120.634883, 46.911995],
          [-120.634431, 46.825831],
          [-120.509227, 46.824534],
          [-120.509618, 46.738114],
          [-119.973017, 46.737171],
          [-119.874057, 46.628585],
          [-119.866265, 46.040662],
          [-121.070459, 46.042575],
          [-121.523938, 46.044048]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54003",
      state: "54",
      county: "003",
      name: "Berkeley County",
      censusArea: 321.142,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.228766, 39.391233],
          [-78.138937, 39.593796],
          [-78.023427, 39.61986],
          [-78.009985, 39.602893],
          [-77.946442, 39.58491],
          [-77.902649, 39.587796],
          [-77.884536, 39.568174],
          [-77.886135, 39.560432],
          [-77.888945, 39.55595],
          [-77.865423, 39.516472],
          [-77.845105, 39.498285],
          [-77.825411, 39.494036],
          [-77.966953, 39.367164],
          [-78.033185, 39.264626],
          [-78.228766, 39.391233]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54005",
      state: "54",
      county: "005",
      name: "Boone County",
      censusArea: 501.543,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.456632, 37.987307],
          [-81.571334, 37.928107],
          [-81.514228, 37.791211],
          [-81.607532, 37.788709],
          [-81.722136, 37.809507],
          [-81.785641, 37.936404],
          [-81.979706, 37.99136],
          [-81.932507, 38.025356],
          [-81.927565, 38.152492],
          [-81.83347, 38.20957],
          [-81.643685, 38.220401],
          [-81.515836, 38.11236],
          [-81.456632, 37.987307]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54009",
      state: "54",
      county: "009",
      name: "Brooke County",
      censusArea: 89.205,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.517998, 40.399644],
          [-80.517991, 40.398868],
          [-80.519056, 40.172771],
          [-80.519056, 40.172744],
          [-80.519104, 40.159672],
          [-80.685391, 40.187642],
          [-80.652098, 40.24497],
          [-80.644598, 40.25127],
          [-80.637198, 40.25507],
          [-80.616696, 40.28027],
          [-80.602895, 40.307069],
          [-80.599895, 40.317669],
          [-80.609313, 40.360468],
          [-80.619196, 40.381768],
          [-80.617346, 40.396004],
          [-80.573784, 40.399755],
          [-80.517998, 40.399644]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54011",
      state: "54",
      county: "011",
      name: "Cabell County",
      censusArea: 281.018,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.047128, 38.374432],
          [-82.17679, 38.3396],
          [-82.264849, 38.229199],
          [-82.297858, 38.327443],
          [-82.343911, 38.305209],
          [-82.50667, 38.410092],
          [-82.34064, 38.440948],
          [-82.330335, 38.4445],
          [-82.323999, 38.449268],
          [-82.320223, 38.454516],
          [-82.313935, 38.468084],
          [-82.295571, 38.539783],
          [-82.293271, 38.560283],
          [-82.293871, 38.572683],
          [-82.291271, 38.578983],
          [-82.287102, 38.582588],
          [-82.27427, 38.593683],
          [-82.26207, 38.598183],
          [-82.245969, 38.598483],
          [-82.218967, 38.591683],
          [-82.055127, 38.474547],
          [-82.047128, 38.374432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54015",
      state: "54",
      county: "015",
      name: "Clay County",
      censusArea: 341.902,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.231434, 38.263716],
          [-81.283872, 38.472029],
          [-81.194113, 38.527634],
          [-81.130775, 38.565948],
          [-81.08371, 38.611982],
          [-81.031677, 38.667839],
          [-80.881232, 38.507045],
          [-80.817424, 38.478553],
          [-80.911361, 38.414785],
          [-81.231434, 38.263716]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54019",
      state: "54",
      county: "019",
      name: "Fayette County",
      censusArea: 661.55,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.231636, 38.263514],
          [-81.070465, 38.210103],
          [-80.983473, 38.222537],
          [-80.882187, 38.103457],
          [-80.769866, 37.98527],
          [-80.806316, 37.868915],
          [-80.94509, 37.819121],
          [-81.091041, 37.849674],
          [-81.073249, 37.875865],
          [-81.295127, 37.889311],
          [-81.296628, 37.94401],
          [-81.380829, 37.969109],
          [-81.331503, 38.167578],
          [-81.231636, 38.263514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12037",
      state: "12",
      county: "037",
      name: "Franklin County",
      censusArea: 534.725,
      stateName: "Florida"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-84.777208, 29.707398],
            [-84.729836, 29.738881],
            [-84.716994, 29.749066],
            [-84.696726, 29.76993],
            [-84.694125, 29.764593],
            [-84.694939, 29.761844],
            [-84.713747, 29.74139],
            [-84.765117, 29.699724],
            [-84.776954, 29.692191],
            [-84.799129, 29.681565],
            [-84.853829, 29.66472],
            [-84.884632, 29.652248],
            [-84.957779, 29.612635],
            [-85.036219, 29.588919],
            [-85.051033, 29.586928],
            [-85.054624, 29.592084],
            [-85.069453, 29.605282],
            [-85.09519, 29.62249],
            [-85.097082, 29.625215],
            [-85.094882, 29.627757],
            [-85.06653, 29.609952],
            [-85.038497, 29.599552],
            [-85.023501, 29.597073],
            [-85.017205, 29.604379],
            [-84.987775, 29.610307],
            [-84.968314, 29.617238],
            [-84.932592, 29.637232],
            [-84.925842, 29.644949],
            [-84.920333, 29.648638],
            [-84.895885, 29.657444],
            [-84.862099, 29.672572],
            [-84.813352, 29.687028],
            [-84.79816, 29.699321],
            [-84.777208, 29.707398]
          ]
        ],
        [
          [
            [-85.156415, 29.679628],
            [-85.137397, 29.684348],
            [-85.134639, 29.686569],
            [-85.114268, 29.688658],
            [-85.093902, 29.684838],
            [-85.083719, 29.679019],
            [-85.077237, 29.670862],
            [-85.091399, 29.648634],
            [-85.097218, 29.633004],
            [-85.124913, 29.628433],
            [-85.142746, 29.635404],
            [-85.16252, 29.650282],
            [-85.18453, 29.663987],
            [-85.204314, 29.672695],
            [-85.222546, 29.678039],
            [-85.220324, 29.680138],
            [-85.208981, 29.681775],
            [-85.184776, 29.68271],
            [-85.168625, 29.682409],
            [-85.156415, 29.679628]
          ]
        ],
        [
          [
            [-84.54445, 30.01103],
            [-84.341261, 29.960775],
            [-84.339426, 29.946007],
            [-84.336511, 29.942508],
            [-84.333746, 29.923721],
            [-84.335953, 29.912962],
            [-84.343389, 29.899539],
            [-84.349066, 29.896812],
            [-84.378937, 29.893112],
            [-84.404958, 29.901229],
            [-84.423834, 29.902996],
            [-84.434287, 29.906328],
            [-84.443652, 29.913785],
            [-84.451705, 29.929085],
            [-84.470323, 29.924524],
            [-84.494562, 29.913957],
            [-84.511996, 29.916574],
            [-84.535873, 29.910092],
            [-84.57744, 29.887828],
            [-84.603303, 29.876117],
            [-84.613154, 29.867984],
            [-84.647958, 29.847104],
            [-84.656318, 29.837943],
            [-84.65645, 29.834277],
            [-84.669728, 29.82891],
            [-84.683934, 29.831327],
            [-84.692053, 29.829059],
            [-84.730327, 29.8069],
            [-84.755595, 29.78854],
            [-84.762998, 29.788846],
            [-84.824197, 29.758288],
            [-84.837168, 29.755926],
            [-84.868271, 29.742454],
            [-84.881777, 29.733882],
            [-84.888031, 29.722406],
            [-84.892493, 29.72266],
            [-84.901781, 29.735723],
            [-84.890066, 29.755802],
            [-84.877111, 29.772888],
            [-84.893992, 29.785176],
            [-84.90413, 29.786279],
            [-84.91511, 29.783303],
            [-84.920917, 29.772901],
            [-84.93837, 29.750211],
            [-84.946595, 29.745032],
            [-84.964007, 29.742422],
            [-84.968841, 29.72708],
            [-84.977004, 29.721209],
            [-84.993264, 29.714961],
            [-85.037212, 29.711074],
            [-85.072123, 29.719027],
            [-85.101682, 29.718748],
            [-85.121473, 29.715854],
            [-85.153238, 29.708231],
            [-85.177284, 29.700193],
            [-85.217355, 29.694953],
            [-85.109401, 29.776793],
            [-85.040078, 29.779346],
            [-85.012191, 29.873167],
            [-85.025439, 29.971399],
            [-85.007626, 30.013883],
            [-84.54445, 30.01103]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12057",
      state: "12",
      county: "057",
      name: "Hillsborough County",
      censusArea: 1020.214,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.643968, 28.173301],
          [-82.105853, 28.17165],
          [-82.056261, 28.171592],
          [-82.056404, 28.080385],
          [-82.054035, 27.850683],
          [-82.054349, 27.646382],
          [-82.52475, 27.645422],
          [-82.524947, 27.645422],
          [-82.549548, 27.645557],
          [-82.554499, 27.645145],
          [-82.537146, 27.672933],
          [-82.514265, 27.705588],
          [-82.494891, 27.718963],
          [-82.482449, 27.719886],
          [-82.477638, 27.723004],
          [-82.476297, 27.729929],
          [-82.477129, 27.735216],
          [-82.482305, 27.742649],
          [-82.478339, 27.74625],
          [-82.457543, 27.752571],
          [-82.434635, 27.764355],
          [-82.43198, 27.768092],
          [-82.433981, 27.774087],
          [-82.419066, 27.793767],
          [-82.418401, 27.803187],
          [-82.410837, 27.810868],
          [-82.402857, 27.812671],
          [-82.393383, 27.837519],
          [-82.397463, 27.851631],
          [-82.402615, 27.882602],
          [-82.413915, 27.901401],
          [-82.432316, 27.901301],
          [-82.451591, 27.907506],
          [-82.460016, 27.9116],
          [-82.459616, 27.9169],
          [-82.462078, 27.920066],
          [-82.478063, 27.92768],
          [-82.489817, 27.9196],
          [-82.491117, 27.9145],
          [-82.487417, 27.895001],
          [-82.488057, 27.863566],
          [-82.480137, 27.853246],
          [-82.471624, 27.847342],
          [-82.46884, 27.843295],
          [-82.47244, 27.822559],
          [-82.475273, 27.820991],
          [-82.489849, 27.822607],
          [-82.511193, 27.828015],
          [-82.553946, 27.848462],
          [-82.552918, 27.862702],
          [-82.538618, 27.864901],
          [-82.533218, 27.870701],
          [-82.529918, 27.877501],
          [-82.539318, 27.885001],
          [-82.542818, 27.890601],
          [-82.541747, 27.893706],
          [-82.535818, 27.898],
          [-82.531318, 27.9039],
          [-82.533718, 27.932999],
          [-82.541218, 27.948998],
          [-82.553918, 27.966998],
          [-82.589419, 27.970898],
          [-82.61952, 27.969698],
          [-82.648616, 27.966309],
          [-82.648557, 28.03863],
          [-82.651165, 28.173266],
          [-82.649725, 28.173314],
          [-82.643968, 28.173301]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12065",
      state: "12",
      county: "065",
      name: "Jefferson County",
      censusArea: 598.095,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.007391, 30.672097],
          [-83.880317, 30.665807],
          [-83.88022, 30.665832],
          [-83.855216, 30.664412],
          [-83.820886, 30.662612],
          [-83.810536, 30.66188],
          [-83.743729, 30.658396],
          [-83.676773, 30.654905],
          [-83.674058, 30.654747],
          [-83.61172, 30.651258],
          [-83.604186, 30.581916],
          [-83.731107, 30.492315],
          [-83.72172, 30.434354],
          [-83.806667, 30.371689],
          [-83.820235, 30.30354],
          [-83.829465, 30.287863],
          [-83.856987, 30.273318],
          [-83.915533, 30.245386],
          [-83.991607, 30.08392],
          [-84.000716, 30.096209],
          [-84.024274, 30.103271],
          [-84.048715, 30.103208],
          [-84.06299, 30.101378],
          [-84.076043, 30.095464],
          [-84.075539, 30.273617],
          [-84.075819, 30.369819],
          [-84.075191, 30.381902],
          [-84.074892, 30.413106],
          [-84.074691, 30.434302],
          [-84.041488, 30.463308],
          [-84.041197, 30.483827],
          [-84.041192, 30.483967],
          [-83.977165, 30.521987],
          [-84.007454, 30.6721],
          [-84.007391, 30.672097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08093",
      state: "08",
      county: "093",
      name: "Park County",
      censusArea: 2193.846,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.398949, 39.566056],
          [-105.398453, 39.363516],
          [-105.397875, 39.129564],
          [-105.330444, 39.129685],
          [-105.329134, 38.697205],
          [-105.96975, 38.693551],
          [-105.906587, 38.806962],
          [-105.925123, 38.872178],
          [-106.028138, 38.942849],
          [-106.110995, 38.940099],
          [-106.191006, 39.056073],
          [-106.183972, 39.309354],
          [-106.135529, 39.379546],
          [-106.017619, 39.365503],
          [-105.966786, 39.438035],
          [-105.817631, 39.53831],
          [-105.829662, 39.564865],
          [-105.642798, 39.566584],
          [-105.587986, 39.568197],
          [-105.398949, 39.566056]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08099",
      state: "08",
      county: "099",
      name: "Prowers County",
      censusArea: 1638.395,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.04451, 38.262412],
          [-102.044398, 38.250015],
          [-102.043844, 37.928102],
          [-102.043845, 37.926135],
          [-102.043219, 37.867929],
          [-102.043033, 37.824146],
          [-102.042953, 37.803535],
          [-102.042668, 37.788758],
          [-102.042158, 37.760164],
          [-102.04199, 37.738541],
          [-102.041876, 37.723875],
          [-102.041574, 37.680436],
          [-102.041694, 37.665681],
          [-102.041582, 37.654495],
          [-102.041585, 37.644282],
          [-102.747615, 37.643642],
          [-102.74233, 38.266971],
          [-102.044567, 38.268749],
          [-102.04451, 38.262412]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08107",
      state: "08",
      county: "107",
      name: "Routt County",
      censusArea: 2362.026,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.000606, 41.003444],
          [-106.857772, 41.003082],
          [-106.852349, 40.925183],
          [-106.82503, 40.932119],
          [-106.654541, 40.849117],
          [-106.636922, 40.789489],
          [-106.706149, 40.616597],
          [-106.652112, 40.445231],
          [-106.632257, 40.341559],
          [-106.626569, 39.924785],
          [-106.626555, 39.918671],
          [-107.033968, 39.918913],
          [-107.037363, 40.091538],
          [-107.037928, 40.225372],
          [-107.439386, 40.223379],
          [-107.428813, 40.542207],
          [-107.314153, 40.600281],
          [-107.317794, 41.002957],
          [-107.000606, 41.003444]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08113",
      state: "08",
      county: "113",
      name: "San Miguel County",
      censusArea: 1286.611,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.042137, 37.88116],
          [-109.043121, 37.97426],
          [-109.042819, 37.997068],
          [-109.04282, 37.999301],
          [-109.042074, 38.153023],
          [-107.965789, 38.152328],
          [-107.891469, 38.115957],
          [-107.900193, 38.026249],
          [-107.795469, 37.98852],
          [-107.738283, 37.905432],
          [-107.860845, 37.775517],
          [-107.989208, 37.85664],
          [-108.209093, 37.820936],
          [-108.256427, 37.894446],
          [-109.042137, 37.88116]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US08115",
      state: "08",
      county: "115",
      name: "Sedgwick County",
      censusArea: 548.041,
      stateName: "Colorado"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.653463, 41.002332],
          [-102.621033, 41.002597],
          [-102.578696, 41.002291],
          [-102.575738, 41.002268],
          [-102.575496, 41.0022],
          [-102.566048, 41.0022],
          [-102.556789, 41.002219],
          [-102.487955, 41.002445],
          [-102.470537, 41.002382],
          [-102.469223, 41.002424],
          [-102.379593, 41.002301],
          [-102.364066, 41.002174],
          [-102.292833, 41.002207],
          [-102.292622, 41.00223],
          [-102.292553, 41.002207],
          [-102.291354, 41.002207],
          [-102.2721, 41.002245],
          [-102.267812, 41.002383],
          [-102.231931, 41.002327],
          [-102.2122, 41.002462],
          [-102.209361, 41.002442],
          [-102.19121, 41.002326],
          [-102.124972, 41.002338],
          [-102.070598, 41.002423],
          [-102.051718, 41.002377],
          [-102.051614, 41.002377],
          [-102.051292, 40.749591],
          [-102.051292, 40.749586],
          [-102.65131, 40.749402],
          [-102.653463, 41.002332]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05125",
      state: "05",
      county: "125",
      name: "Saline County",
      censusArea: 723.604,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.245818, 34.493506],
          [-92.403557, 34.495795],
          [-92.40468, 34.451916],
          [-92.589418, 34.455641],
          [-92.668711, 34.414237],
          [-92.685284, 34.473105],
          [-92.793197, 34.503606],
          [-92.808028, 34.590406],
          [-92.875456, 34.635124],
          [-92.979902, 34.637222],
          [-92.96925, 34.772266],
          [-93.075178, 34.771892],
          [-93.073877, 34.858091],
          [-92.739779, 34.853459],
          [-92.651248, 34.851682],
          [-92.654324, 34.764541],
          [-92.549856, 34.761699],
          [-92.551963, 34.689432],
          [-92.443265, 34.672041],
          [-92.445148, 34.62694],
          [-92.346814, 34.625611],
          [-92.348749, 34.58229],
          [-92.244209, 34.58089],
          [-92.245818, 34.493506]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05131",
      state: "05",
      county: "131",
      name: "Sebastian County",
      censusArea: 531.911,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.433742, 35.386467],
          [-94.433915, 35.387391],
          [-94.431215, 35.39429],
          [-94.431789, 35.397659],
          [-94.403468, 35.449994],
          [-94.321186, 35.357476],
          [-94.223751, 35.353694],
          [-94.074895, 35.445403],
          [-94.082798, 35.215913],
          [-94.028547, 35.213826],
          [-94.031236, 35.140857],
          [-94.139307, 35.143361],
          [-94.140571, 35.099678],
          [-94.222869, 35.079115],
          [-94.230524, 34.968527],
          [-94.447889, 34.933941],
          [-94.441232, 35.119724],
          [-94.440754, 35.128806],
          [-94.43955, 35.169037],
          [-94.439509, 35.171807],
          [-94.439056, 35.193588],
          [-94.439084, 35.197298],
          [-94.43847, 35.208587],
          [-94.438247, 35.210992],
          [-94.437774, 35.239271],
          [-94.437578, 35.242202],
          [-94.435812, 35.2713],
          [-94.435706, 35.274267],
          [-94.435316, 35.275893],
          [-94.43528, 35.287485],
          [-94.43517, 35.291494],
          [-94.434115, 35.306493],
          [-94.431815, 35.362891],
          [-94.432685, 35.380806],
          [-94.433742, 35.386467]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05139",
      state: "05",
      county: "139",
      name: "Union County",
      censusArea: 1039.214,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.724743, 33.014347],
          [-92.724994, 33.014351],
          [-92.733197, 33.014347],
          [-92.830798, 33.015661],
          [-92.844073, 33.016034],
          [-92.844286, 33.01607],
          [-92.854167, 33.016132],
          [-92.86751, 33.016062],
          [-92.946553, 33.016807],
          [-92.971137, 33.017192],
          [-92.988708, 33.017298],
          [-92.978469, 33.37728],
          [-92.660238, 33.355613],
          [-92.567156, 33.3672],
          [-92.445399, 33.274859],
          [-92.350432, 33.298941],
          [-92.22406, 33.251782],
          [-92.137527, 33.162956],
          [-92.109611, 33.034054],
          [-92.069105, 33.008163],
          [-92.222825, 33.00908],
          [-92.292664, 33.010103],
          [-92.335893, 33.010349],
          [-92.362865, 33.010628],
          [-92.37029, 33.010717],
          [-92.469762, 33.01201],
          [-92.501383, 33.01216],
          [-92.503776, 33.012161],
          [-92.711289, 33.014307],
          [-92.715884, 33.014398],
          [-92.723553, 33.014328],
          [-92.724743, 33.014347]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18171",
      state: "18",
      county: "171",
      name: "Warren County",
      censusArea: 364.681,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.294191, 40.257966],
          [-87.311889, 40.250036],
          [-87.339273, 40.223173],
          [-87.438755, 40.165694],
          [-87.406667, 40.127508],
          [-87.531438, 40.148123],
          [-87.531133, 40.17003],
          [-87.530828, 40.191812],
          [-87.529992, 40.250036],
          [-87.530054, 40.250671],
          [-87.526809, 40.46217],
          [-87.526549, 40.475659],
          [-87.526511, 40.476879],
          [-87.093676, 40.475745],
          [-87.09278, 40.475075],
          [-87.092148, 40.366612],
          [-87.244898, 40.309252],
          [-87.294191, 40.257966]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18177",
      state: "18",
      county: "177",
      name: "Wayne County",
      censusArea: 401.74,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.814189, 39.785569],
          [-84.814129, 39.72662],
          [-85.034575, 39.714764],
          [-85.185089, 39.715515],
          [-85.221118, 39.788449],
          [-85.201473, 40.004521],
          [-84.81067, 40.005074],
          [-84.812193, 39.92734],
          [-84.812357, 39.921764],
          [-84.812411, 39.916916],
          [-84.812411, 39.916915],
          [-84.812698, 39.891585],
          [-84.812787, 39.89083],
          [-84.81305, 39.872958],
          [-84.813464, 39.853261],
          [-84.813549, 39.850773],
          [-84.813674, 39.843173],
          [-84.813703, 39.843059],
          [-84.813793, 39.826771],
          [-84.813852, 39.824621],
          [-84.814179, 39.814212],
          [-84.81412, 39.811398],
          [-84.814209, 39.799755],
          [-84.814179, 39.786853],
          [-84.814189, 39.785569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19005",
      state: "19",
      county: "005",
      name: "Allamakee County",
      censusArea: 639.082,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.217706, 43.50055],
          [-91.20555, 43.422949],
          [-91.210233, 43.372064],
          [-91.107237, 43.313645],
          [-91.085652, 43.29187],
          [-91.057918, 43.255366],
          [-91.062562, 43.243165],
          [-91.1462, 43.152405],
          [-91.1562, 43.142945],
          [-91.175253, 43.134665],
          [-91.178251, 43.124982],
          [-91.177222, 43.080247],
          [-91.605307, 43.081653],
          [-91.610832, 43.50053],
          [-91.591073, 43.500536],
          [-91.551021, 43.500539],
          [-91.54122, 43.500515],
          [-91.533806, 43.50056],
          [-91.491042, 43.50069],
          [-91.465063, 43.500608],
          [-91.461403, 43.500642],
          [-91.445932, 43.500588],
          [-91.441786, 43.500438],
          [-91.37695, 43.500482],
          [-91.371608, 43.500945],
          [-91.369325, 43.500827],
          [-91.217706, 43.50055]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17059",
      state: "17",
      county: "059",
      name: "Gallatin County",
      censusArea: 323.071,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.039, 37.775454],
          [-88.045008, 37.762436],
          [-88.050969, 37.752599],
          [-88.059588, 37.742608],
          [-88.072538, 37.733286],
          [-88.122412, 37.709685],
          [-88.151646, 37.675098],
          [-88.158207, 37.664542],
          [-88.160187, 37.657592],
          [-88.156827, 37.632801],
          [-88.139973, 37.586451],
          [-88.133416, 37.574277],
          [-88.375332, 37.599563],
          [-88.373884, 37.776237],
          [-88.374395, 37.863935],
          [-88.374398, 37.870982],
          [-88.374593, 37.871139],
          [-88.37453, 37.874824],
          [-88.374406, 37.875066],
          [-88.37453, 37.907678],
          [-88.37452, 37.908432],
          [-88.357462, 37.909081],
          [-88.26331, 37.908632],
          [-88.071449, 37.895813],
          [-88.049453, 37.845187],
          [-88.027789, 37.828298],
          [-88.02803, 37.799224],
          [-88.039, 37.775454]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17067",
      state: "17",
      county: "067",
      name: "Hancock County",
      censusArea: 793.726,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.501435, 40.248874],
          [-91.489868, 40.286048],
          [-91.46214, 40.342414],
          [-91.444833, 40.36317],
          [-91.419422, 40.378264],
          [-91.38836, 40.384929],
          [-91.375746, 40.391879],
          [-91.372554, 40.4012],
          [-91.36391, 40.490122],
          [-91.364211, 40.500043],
          [-91.369059, 40.512532],
          [-91.384531, 40.530948],
          [-91.404125, 40.539127],
          [-91.406851, 40.547557],
          [-91.405241, 40.554641],
          [-91.359873, 40.601805],
          [-91.348733, 40.609695],
          [-91.339719, 40.613488],
          [-91.306524, 40.626231],
          [-91.253074, 40.637962],
          [-91.218437, 40.638437],
          [-91.197906, 40.636107],
          [-91.18698, 40.637297],
          [-91.185415, 40.638052],
          [-90.913894, 40.637748],
          [-90.904216, 40.639201],
          [-90.909756, 40.284394],
          [-90.911969, 40.193088],
          [-91.258828, 40.197299],
          [-91.510332, 40.201142],
          [-91.505968, 40.234305],
          [-91.501435, 40.248874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18061",
      state: "18",
      county: "061",
      name: "Harrison County",
      censusArea: 484.516,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.89955, 38.180343],
          [-85.908764, 38.161169],
          [-85.909464, 38.14007],
          [-85.905164, 38.11107],
          [-85.904564, 38.10027],
          [-85.906163, 38.08617],
          [-85.911607, 38.06689],
          [-85.922395, 38.028679],
          [-85.930235, 38.018311],
          [-85.941403, 38.009831],
          [-85.947952, 38.007083],
          [-85.951467, 38.005608],
          [-85.998609, 37.997775],
          [-86.029509, 37.99264],
          [-86.172186, 38.00992],
          [-86.178983, 38.011308],
          [-86.220371, 38.027922],
          [-86.266891, 38.057125],
          [-86.273584, 38.067443],
          [-86.27872, 38.089303],
          [-86.278034, 38.102426],
          [-86.271223, 38.130112],
          [-86.283964, 38.143185],
          [-86.309727, 38.144393],
          [-86.314292, 38.14261],
          [-86.255045, 38.422685],
          [-86.032817, 38.417898],
          [-86.032559, 38.33066],
          [-85.89955, 38.180343]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18073",
      state: "18",
      county: "073",
      name: "Jasper County",
      censusArea: 559.625,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.218862, 41.242027],
          [-87.131653, 41.286155],
          [-86.968125, 41.219658],
          [-86.930079, 41.236798],
          [-86.929646, 41.229711],
          [-86.93017, 41.172318],
          [-86.930314, 41.170899],
          [-86.930759, 40.912418],
          [-86.986438, 40.837921],
          [-87.099793, 40.837607],
          [-87.09839, 40.736541],
          [-87.2673, 40.736582],
          [-87.275882, 41.218595],
          [-87.226372, 41.235507],
          [-87.218862, 41.242027]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18081",
      state: "18",
      county: "081",
      name: "Johnson County",
      censusArea: 320.427,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.952523, 39.60915],
          [-85.9508, 39.49522],
          [-85.95122, 39.436053],
          [-85.952545, 39.435921],
          [-85.95322, 39.38352],
          [-85.952005, 39.347373],
          [-86.081531, 39.344446],
          [-86.085601, 39.344189],
          [-86.251684, 39.341672],
          [-86.25131, 39.430595],
          [-86.249885, 39.633645],
          [-86.242924, 39.633744],
          [-86.044705, 39.637219],
          [-85.95208, 39.638345],
          [-85.952523, 39.60915]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18083",
      state: "18",
      county: "083",
      name: "Knox County",
      censusArea: 516.031,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.528194, 38.851691],
          [-87.532064, 38.852503],
          [-87.537248, 38.853592],
          [-87.550515, 38.85956],
          [-87.553384, 38.863344],
          [-87.540804, 38.896385],
          [-87.528108, 38.908027],
          [-87.241027, 38.907874],
          [-87.098771, 38.903974],
          [-87.192243, 38.814688],
          [-87.241463, 38.817476],
          [-87.279333, 38.693314],
          [-87.242097, 38.54493],
          [-87.462892, 38.533519],
          [-87.599317, 38.450721],
          [-87.740001, 38.415698],
          [-87.656144, 38.521668],
          [-87.669232, 38.541742],
          [-87.670071, 38.545237],
          [-87.651529, 38.568166],
          [-87.627345, 38.607237],
          [-87.62012, 38.639489],
          [-87.60018, 38.662462],
          [-87.593678, 38.667402],
          [-87.579397, 38.672475],
          [-87.540052, 38.679143],
          [-87.531231, 38.684036],
          [-87.519609, 38.697198],
          [-87.498515, 38.758983],
          [-87.495211, 38.78306],
          [-87.525893, 38.848795],
          [-87.528194, 38.851691]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18089",
      state: "18",
      county: "089",
      name: "Lake County",
      censusArea: 498.961,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.526768, 41.298177],
          [-87.525623, 41.453619],
          [-87.525671, 41.470115],
          [-87.525669, 41.470283],
          [-87.524641, 41.563335],
          [-87.524642, 41.622535],
          [-87.524742, 41.632435],
          [-87.524642, 41.634935],
          [-87.524844, 41.691635],
          [-87.524944, 41.702635],
          [-87.524044, 41.708335],
          [-87.520544, 41.709935],
          [-87.515243, 41.704235],
          [-87.511043, 41.696535],
          [-87.505343, 41.691535],
          [-87.470742, 41.672835],
          [-87.463142, 41.675535],
          [-87.453041, 41.673035],
          [-87.446113, 41.66934],
          [-87.441987, 41.671905],
          [-87.43853, 41.670679],
          [-87.437191, 41.669006],
          [-87.434849, 41.666887],
          [-87.432953, 41.665102],
          [-87.432396, 41.66053],
          [-87.438941, 41.654335],
          [-87.42984, 41.646035],
          [-87.42344, 41.642835],
          [-87.394539, 41.637235],
          [-87.365439, 41.629536],
          [-87.324338, 41.623036],
          [-87.287637, 41.622236],
          [-87.278437, 41.619736],
          [-87.261536, 41.620336],
          [-87.222644, 41.624161],
          [-87.218862, 41.242027],
          [-87.226372, 41.235507],
          [-87.275882, 41.218595],
          [-87.414421, 41.162248],
          [-87.52657, 41.166097],
          [-87.526768, 41.298052],
          [-87.526768, 41.298177]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18107",
      state: "18",
      county: "107",
      name: "Montgomery County",
      censusArea: 504.613,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.091006, 39.953009],
          [-87.090022, 39.953571],
          [-87.091957, 40.171253],
          [-87.092563, 40.214806],
          [-86.695637, 40.214367],
          [-86.695804, 40.178657],
          [-86.695071, 39.92277],
          [-86.695127, 39.864845],
          [-87.009477, 39.866705],
          [-87.091542, 39.867265],
          [-87.091006, 39.953009]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20121",
      state: "20",
      county: "121",
      name: "Miami County",
      censusArea: 575.661,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.609509, 38.738102],
          [-94.611602, 38.635384],
          [-94.611858, 38.620485],
          [-94.611908, 38.609272],
          [-94.611887, 38.580139],
          [-94.611902, 38.58011],
          [-94.612176, 38.576546],
          [-94.612157, 38.549817],
          [-94.612272, 38.547917],
          [-94.612644, 38.491644],
          [-94.612726, 38.484367],
          [-94.612696, 38.483154],
          [-94.612865, 38.477602],
          [-94.612866, 38.477571],
          [-94.613365, 38.403422],
          [-94.613265, 38.392426],
          [-94.613275, 38.388718],
          [-95.065831, 38.389945],
          [-95.056412, 38.738587],
          [-94.609509, 38.738102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20129",
      state: "20",
      county: "129",
      name: "Morton County",
      censusArea: 729.731,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.55526, 36.995414],
          [-101.600396, 36.995153],
          [-101.601593, 36.995095],
          [-101.90244, 36.993702],
          [-102.000447, 36.993249],
          [-102.000447, 36.993272],
          [-102.028204, 36.993125],
          [-102.028207, 36.993125],
          [-102.04224, 36.993083],
          [-102.041952, 37.024742],
          [-102.04195, 37.030805],
          [-102.041921, 37.032178],
          [-102.041749, 37.034397],
          [-102.04192, 37.035083],
          [-102.041983, 37.106551],
          [-102.041809, 37.111973],
          [-102.042092, 37.125021],
          [-102.042135, 37.125021],
          [-102.042002, 37.141744],
          [-102.041963, 37.258164],
          [-102.041664, 37.29765],
          [-102.041817, 37.30949],
          [-102.041974, 37.352613],
          [-102.042089, 37.352819],
          [-102.041826, 37.389191],
          [-101.556606, 37.388485],
          [-101.55526, 36.995414]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20137",
      state: "20",
      county: "137",
      name: "Norton County",
      censusArea: 878.134,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.177795, 40.001593],
          [-99.990926, 40.001503],
          [-99.986611, 40.00155],
          [-99.948167, 40.001813],
          [-99.944417, 40.001584],
          [-99.930433, 40.001516],
          [-99.906658, 40.001512],
          [-99.813401, 40.0014],
          [-99.77564, 40.001647],
          [-99.772121, 40.001804],
          [-99.764214, 40.001551],
          [-99.756835, 40.001342],
          [-99.746628, 40.00182],
          [-99.731959, 40.001827],
          [-99.719639, 40.001808],
          [-99.628346, 40.001866],
          [-99.628255, 40.001866],
          [-99.62598, 40.001865],
          [-99.625324, 40.001866],
          [-99.627953, 39.567437],
          [-100.161667, 39.567277],
          [-100.180351, 39.56689],
          [-100.178276, 39.998801],
          [-100.177795, 40.001593]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20147",
      state: "20",
      county: "147",
      name: "Phillips County",
      censusArea: 885.877,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.625324, 40.001866],
          [-99.501792, 40.002026],
          [-99.498999, 40.001957],
          [-99.49766, 40.001912],
          [-99.493465, 40.001937],
          [-99.480728, 40.001942],
          [-99.412645, 40.001868],
          [-99.403389, 40.001969],
          [-99.290703, 40.001949],
          [-99.286656, 40.002017],
          [-99.282967, 40.001879],
          [-99.254012, 40.002074],
          [-99.25037, 40.001957],
          [-99.216376, 40.002016],
          [-99.197592, 40.002033],
          [-99.188905, 40.002023],
          [-99.186962, 40.001977],
          [-99.179133, 40.001977],
          [-99.178965, 40.001977],
          [-99.169816, 40.001925],
          [-99.123033, 40.002165],
          [-99.11351, 40.002193],
          [-99.085597, 40.002133],
          [-99.067022, 40.00217],
          [-99.066203, 39.568093],
          [-99.602255, 39.567328],
          [-99.627953, 39.567437],
          [-99.625324, 40.001866]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20153",
      state: "20",
      county: "153",
      name: "Rawlins County",
      censusArea: 1069.416,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.75883, 40.002302],
          [-100.752183, 40.002128],
          [-100.738826, 40.002228],
          [-100.740873, 39.568043],
          [-101.389068, 39.568369],
          [-101.413913, 39.568361],
          [-101.41124, 39.999997],
          [-101.411043, 40.002365],
          [-101.409953, 40.002354],
          [-101.374326, 40.002521],
          [-101.342859, 40.00258],
          [-101.325514, 40.002687],
          [-101.324036, 40.002696],
          [-101.293991, 40.002559],
          [-101.286555, 40.002559],
          [-101.248673, 40.002543],
          [-101.215033, 40.002555],
          [-101.192219, 40.002491],
          [-101.178805, 40.002468],
          [-101.168704, 40.002547],
          [-101.130907, 40.002427],
          [-101.060317, 40.002307],
          [-101.027686, 40.002256],
          [-100.937427, 40.002145],
          [-100.75883, 40.002302]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20161",
      state: "20",
      county: "161",
      name: "Riley County",
      censusArea: 609.771,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.755786, 39.088006],
          [-96.851111, 39.088386],
          [-96.850991, 39.101307],
          [-96.849879, 39.219012],
          [-96.961387, 39.220073],
          [-96.958859, 39.5664],
          [-96.806544, 39.566423],
          [-96.581764, 39.566333],
          [-96.726865, 39.404012],
          [-96.638605, 39.292827],
          [-96.518329, 39.203877],
          [-96.554012, 39.178532],
          [-96.388884, 39.172724],
          [-96.390796, 39.043257],
          [-96.501174, 39.043666],
          [-96.501181, 39.072782],
          [-96.755786, 39.088006]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20175",
      state: "20",
      county: "175",
      name: "Seward County",
      censusArea: 639.497,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.633327, 36.999936],
          [-100.675552, 36.999688],
          [-100.756894, 36.999357],
          [-100.765484, 36.999177],
          [-100.806116, 36.999091],
          [-100.814277, 36.999085],
          [-100.855634, 36.998626],
          [-100.89166, 36.998604],
          [-100.904274, 36.998745],
          [-100.904588, 36.998561],
          [-100.945469, 36.998153],
          [-100.945566, 36.998152],
          [-100.996502, 36.998044],
          [-101.012641, 36.998176],
          [-101.053589, 36.997967],
          [-101.066742, 36.997921],
          [-101.066457, 36.998493],
          [-101.067904, 37.387779],
          [-100.652728, 37.387627],
          [-100.633831, 37.387577],
          [-100.633323, 36.999936],
          [-100.633327, 36.999936]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22065",
      state: "22",
      county: "065",
      name: "Madison County",
      lsad: "Parish",
      censusArea: 624.437,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.090611, 32.562705],
          [-91.089436, 32.553306],
          [-91.093741, 32.549128],
          [-91.097878, 32.544752],
          [-91.116708, 32.500139],
          [-91.116008, 32.48314],
          [-91.108808, 32.47204],
          [-91.095308, 32.458741],
          [-91.070207, 32.445141],
          [-91.052907, 32.438442],
          [-91.029606, 32.433542],
          [-90.99408, 32.403862],
          [-91.004506, 32.368144],
          [-91.003506, 32.362145],
          [-91.000106, 32.357695],
          [-90.986672, 32.35176],
          [-90.912363, 32.339454],
          [-90.901511, 32.337864],
          [-90.898588, 32.329094],
          [-90.905173, 32.315497],
          [-91.004557, 32.215381],
          [-91.006306, 32.22415],
          [-91.021507, 32.236149],
          [-91.039007, 32.242349],
          [-91.046507, 32.241149],
          [-91.122602, 32.216929],
          [-91.13378, 32.249339],
          [-91.320742, 32.202455],
          [-91.492718, 32.203349],
          [-91.526719, 32.300675],
          [-91.476739, 32.405808],
          [-91.457643, 32.537807],
          [-91.133713, 32.537912],
          [-91.090611, 32.562705]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09001",
      state: "09",
      county: "001",
      name: "Fairfield County",
      censusArea: 624.89,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.517473, 41.666646],
          [-73.516019, 41.665964],
          [-73.514784, 41.665886],
          [-73.511149, 41.666488],
          [-73.510379, 41.666498],
          [-73.506923, 41.665802],
          [-73.441466, 41.491294],
          [-73.331569, 41.472677],
          [-73.326774, 41.469515],
          [-73.312873, 41.467415],
          [-73.310473, 41.468815],
          [-73.207688, 41.420544],
          [-73.184857, 41.406296],
          [-73.187957, 41.397296],
          [-73.183269, 41.391711],
          [-73.173356, 41.383596],
          [-73.161253, 41.383853],
          [-73.147555, 41.367696],
          [-73.136054, 41.351196],
          [-73.115754, 41.332196],
          [-73.090938, 41.319256],
          [-73.08665, 41.314495],
          [-73.078952, 41.311596],
          [-73.077652, 41.307696],
          [-73.075352, 41.305596],
          [-73.067851, 41.300996],
          [-72.985095, 41.234358],
          [-72.986247, 41.233497],
          [-72.997948, 41.222697],
          [-73.003639, 41.215287],
          [-73.007548, 41.210197],
          [-73.013465, 41.205479],
          [-73.013988, 41.205062],
          [-73.014948, 41.204297],
          [-73.020149, 41.204097],
          [-73.020167, 41.204237],
          [-73.020195, 41.204446],
          [-73.02021, 41.204568],
          [-73.020254, 41.204906],
          [-73.020449, 41.206397],
          [-73.022549, 41.207197],
          [-73.024783, 41.207435],
          [-73.045602, 41.209658],
          [-73.05065, 41.210197],
          [-73.054947, 41.208468],
          [-73.05935, 41.206697],
          [-73.07761, 41.195176],
          [-73.07945, 41.194015],
          [-73.09122, 41.184153],
          [-73.092, 41.1835],
          [-73.092147, 41.183377],
          [-73.104328, 41.17317],
          [-73.105483, 41.172203],
          [-73.105493, 41.172194],
          [-73.107987, 41.168738],
          [-73.110352, 41.159697],
          [-73.109952, 41.156997],
          [-73.108352, 41.153718],
          [-73.111052, 41.150797],
          [-73.130253, 41.146797],
          [-73.16437, 41.158565],
          [-73.170074, 41.160532],
          [-73.170701, 41.164945],
          [-73.177774, 41.166697],
          [-73.202656, 41.158096],
          [-73.228295, 41.142602],
          [-73.235058, 41.143996],
          [-73.247958, 41.126396],
          [-73.262358, 41.117496],
          [-73.286759, 41.127896],
          [-73.296359, 41.125696],
          [-73.31186, 41.116296],
          [-73.33066, 41.109996],
          [-73.372296, 41.10402],
          [-73.392162, 41.087696],
          [-73.400154, 41.086299],
          [-73.41367, 41.073258],
          [-73.435063, 41.056696],
          [-73.450364, 41.057096],
          [-73.468239, 41.051347],
          [-73.477364, 41.035997],
          [-73.493327, 41.048173],
          [-73.516903, 41.038738],
          [-73.516766, 41.029497],
          [-73.522666, 41.019297],
          [-73.528866, 41.016397],
          [-73.531169, 41.021919],
          [-73.530189, 41.028776],
          [-73.532786, 41.03167],
          [-73.535338, 41.03192],
          [-73.551494, 41.024336],
          [-73.561968, 41.016797],
          [-73.567668, 41.010897],
          [-73.570068, 41.001597],
          [-73.583968, 41.000897],
          [-73.584988, 41.010537],
          [-73.595699, 41.015995],
          [-73.603952, 41.015054],
          [-73.643478, 41.002171],
          [-73.651175, 40.995229],
          [-73.657336, 40.985171],
          [-73.654671, 41.011697],
          [-73.655371, 41.012797],
          [-73.670472, 41.030097],
          [-73.679973, 41.041797],
          [-73.687173, 41.050697],
          [-73.694273, 41.059296],
          [-73.727775, 41.100696],
          [-73.639672, 41.141495],
          [-73.632153, 41.144921],
          [-73.614407, 41.153001],
          [-73.514617, 41.198434],
          [-73.509487, 41.200814],
          [-73.482709, 41.21276],
          [-73.550961, 41.295422],
          [-73.544728, 41.366375],
          [-73.543641, 41.376778],
          [-73.537673, 41.433905],
          [-73.537469, 41.43589],
          [-73.536969, 41.441094],
          [-73.536067, 41.451331],
          [-73.535986, 41.45306],
          [-73.535885, 41.455236],
          [-73.535857, 41.455709],
          [-73.535769, 41.457159],
          [-73.534369, 41.475894],
          [-73.534269, 41.476394],
          [-73.534269, 41.476911],
          [-73.53415, 41.47806],
          [-73.534055, 41.478968],
          [-73.533969, 41.479693],
          [-73.530067, 41.527194],
          [-73.521457, 41.616429],
          [-73.517473, 41.666646]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05021",
      state: "05",
      county: "021",
      name: "Clay County",
      censusArea: 639.465,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.57618, 36.498446],
          [-90.576112, 36.498446],
          [-90.50016, 36.498399],
          [-90.495027, 36.498371],
          [-90.494575, 36.498368],
          [-90.339892, 36.498213],
          [-90.228943, 36.497771],
          [-90.220732, 36.497858],
          [-90.220702, 36.497858],
          [-90.217323, 36.497797],
          [-90.193943, 36.497823],
          [-90.152481, 36.497952],
          [-90.154409, 36.496832],
          [-90.156369, 36.487748],
          [-90.133993, 36.437906],
          [-90.072897, 36.393007],
          [-90.064514, 36.382085],
          [-90.06398, 36.303038],
          [-90.076301, 36.280708],
          [-90.083731, 36.272332],
          [-90.114922, 36.265595],
          [-90.198735, 36.201382],
          [-90.32096, 36.200575],
          [-90.31982, 36.259144],
          [-90.806615, 36.266865],
          [-90.751803, 36.27996],
          [-90.748637, 36.412764],
          [-90.784398, 36.498524],
          [-90.782454, 36.498523],
          [-90.765672, 36.498494],
          [-90.711226, 36.498318],
          [-90.693005, 36.49851],
          [-90.653246, 36.498488],
          [-90.648494, 36.498447],
          [-90.612554, 36.498559],
          [-90.60545, 36.498459],
          [-90.5943, 36.498459],
          [-90.585342, 36.498497],
          [-90.57618, 36.498446]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21119",
      state: "21",
      county: "119",
      name: "Knott County",
      censusArea: 351.518,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.732693, 37.271502],
          [-82.819373, 37.268671],
          [-82.979183, 37.17242],
          [-83.000142, 37.19571],
          [-83.127249, 37.268401],
          [-83.124348, 37.404973],
          [-83.134711, 37.457181],
          [-83.062266, 37.526231],
          [-82.948544, 37.503167],
          [-82.944205, 37.501434],
          [-82.945444, 37.49739],
          [-82.941667, 37.496323],
          [-82.941298, 37.489795],
          [-82.934353, 37.493991],
          [-82.92546, 37.486525],
          [-82.767605, 37.427777],
          [-82.752346, 37.308039],
          [-82.710805, 37.285323],
          [-82.718852, 37.280276],
          [-82.724557, 37.281035],
          [-82.732693, 37.271502]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21127",
      state: "21",
      county: "127",
      name: "Lawrence County",
      censusArea: 415.595,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.4973, 37.945507],
          [-82.497358, 37.945338],
          [-82.582085, 37.952314],
          [-82.612393, 37.879272],
          [-82.947936, 38.000431],
          [-82.989014, 37.963968],
          [-83.016058, 37.974221],
          [-83.022115, 38.007047],
          [-82.889059, 38.111587],
          [-82.924805, 38.175114],
          [-82.794206, 38.243772],
          [-82.60423, 38.247303],
          [-82.644739, 38.165487],
          [-82.636466, 38.13786],
          [-82.621164, 38.123239],
          [-82.602618, 38.11835],
          [-82.600127, 38.117389],
          [-82.547284, 38.061094],
          [-82.539139, 38.039093],
          [-82.519665, 38.008538],
          [-82.509812, 38.001249],
          [-82.487732, 37.99833],
          [-82.465473, 37.98478],
          [-82.464096, 37.982437],
          [-82.464987, 37.976859],
          [-82.4973, 37.945507]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21133",
      state: "21",
      county: "133",
      name: "Letcher County",
      censusArea: 337.914,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.565375, 37.196092],
          [-82.624878, 37.162932],
          [-82.721941, 37.105689],
          [-82.720597, 37.081833],
          [-82.722472, 37.045101],
          [-82.771795, 37.015716],
          [-82.777368, 37.015279],
          [-82.788897, 37.00816],
          [-82.789092, 37.007995],
          [-82.790462, 37.007263],
          [-82.79089, 37.00676],
          [-82.800531, 37.007944],
          [-82.815748, 37.007196],
          [-82.818006, 37.006161],
          [-82.830588, 37.000945],
          [-82.829125, 36.997541],
          [-82.830802, 36.993445],
          [-82.833843, 36.991973],
          [-82.836008, 36.988837],
          [-82.838549, 36.987027],
          [-82.840051, 36.987113],
          [-82.851397, 36.984497],
          [-82.852614, 36.984963],
          [-82.862926, 36.979975],
          [-82.864909, 36.97901],
          [-82.866019, 36.978272],
          [-82.866689, 36.978052],
          [-82.867535, 36.977518],
          [-82.868455, 36.976481],
          [-82.869183, 36.974183],
          [-82.939466, 37.01552],
          [-83.150402, 36.954163],
          [-83.12209, 37.000997],
          [-83.054626, 37.02],
          [-83.058818, 37.124079],
          [-83.000142, 37.19571],
          [-82.979183, 37.17242],
          [-82.819373, 37.268671],
          [-82.732693, 37.271502],
          [-82.565375, 37.196092]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21141",
      state: "21",
      county: "141",
      name: "Logan County",
      censusArea: 552.131,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.763295, 36.648907],
          [-86.813037, 36.647647],
          [-86.816186, 36.647722],
          [-86.818405, 36.647639],
          [-86.833155, 36.64721],
          [-86.854268, 36.646884],
          [-86.906023, 36.646302],
          [-86.906583, 36.646255],
          [-87.060843, 36.643412],
          [-87.053164, 37.061019],
          [-86.976399, 37.073588],
          [-86.941391, 37.068981],
          [-86.84535, 37.056513],
          [-86.674462, 36.999766],
          [-86.611586, 36.883057],
          [-86.690719, 36.8443],
          [-86.763295, 36.648907]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21149",
      state: "21",
      county: "149",
      name: "Mc Lean County",
      censusArea: 252.474,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.03826, 37.560803],
          [-87.049376, 37.55186],
          [-87.103111, 37.4809],
          [-87.099803, 37.416042],
          [-87.295965, 37.391819],
          [-87.351893, 37.425655],
          [-87.375145, 37.569988],
          [-87.495331, 37.647547],
          [-87.472811, 37.664869],
          [-87.438436, 37.670931],
          [-87.420533, 37.670153],
          [-87.408547, 37.683563],
          [-87.26161, 37.622009],
          [-87.03826, 37.560803]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21155",
      state: "21",
      county: "155",
      name: "Marion County",
      censusArea: 343.008,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.073772, 37.413669],
          [-85.17144, 37.465081],
          [-85.246759, 37.420205],
          [-85.340446, 37.469512],
          [-85.46625, 37.465595],
          [-85.52129, 37.554343],
          [-85.450493, 37.685948],
          [-85.401784, 37.730658],
          [-85.373639, 37.673343],
          [-85.257298, 37.630903],
          [-85.030172, 37.63127],
          [-85.039674, 37.545232],
          [-85.073772, 37.413669]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04015",
      state: "04",
      county: "015",
      name: "Mohave County",
      censusArea: 13311.084,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.0506, 37.000396],
          [-113.965907, 37.000025],
          [-113.965907, 36.999976],
          [-112.966471, 37.000219],
          [-112.899366, 37.000319],
          [-112.609787, 37.000753],
          [-112.558974, 37.000692],
          [-112.545094, 37.000734],
          [-112.540368, 37.000669],
          [-112.538593, 37.000674],
          [-112.660326, 36.539631],
          [-112.621855, 36.485017],
          [-112.645121, 36.457311],
          [-112.629727, 36.392322],
          [-112.681571, 36.341662],
          [-112.723991, 36.345992],
          [-112.792436, 36.285036],
          [-113.009038, 36.23904],
          [-113.13708, 36.166424],
          [-113.19923, 36.151389],
          [-113.231146, 36.089524],
          [-113.319971, 36.096769],
          [-113.354176, 36.040368],
          [-113.317122, 35.961385],
          [-113.310523, 35.86435],
          [-113.333922, 35.800736],
          [-113.334161, 35.528037],
          [-113.332713, 35.00011],
          [-113.333508, 34.317877],
          [-113.370507, 34.291968],
          [-113.539524, 34.306377],
          [-113.603079, 34.231049],
          [-114.031132, 34.259819],
          [-114.138282, 34.30323],
          [-114.176909, 34.349306],
          [-114.199482, 34.361373],
          [-114.264317, 34.401329],
          [-114.339627, 34.451435],
          [-114.452547, 34.653494],
          [-114.465607, 34.69226],
          [-114.552682, 34.766871],
          [-114.633051, 34.869971],
          [-114.635176, 34.875003],
          [-114.636725, 34.889107],
          [-114.630877, 34.907263],
          [-114.629015, 34.986148],
          [-114.62919, 34.991887],
          [-114.633013, 35.002085],
          [-114.633544, 35.015644],
          [-114.635469, 35.028266],
          [-114.621956, 35.094678],
          [-114.59912, 35.12105],
          [-114.5843, 35.124999],
          [-114.578524, 35.12875],
          [-114.572747, 35.138725],
          [-114.569569, 35.163053],
          [-114.569238, 35.18348],
          [-114.595931, 35.325234],
          [-114.604314, 35.353584],
          [-114.627137, 35.409504],
          [-114.652005, 35.429165],
          [-114.662125, 35.444241],
          [-114.677643, 35.489742],
          [-114.676733, 35.499063],
          [-114.674981, 35.510564],
          [-114.671147, 35.520641],
          [-114.653806, 35.59949],
          [-114.653406, 35.610789],
          [-114.657506, 35.618289],
          [-114.676707, 35.640989],
          [-114.689407, 35.651412],
          [-114.700308, 35.700387],
          [-114.70771, 35.811885],
          [-114.705611, 35.848884],
          [-114.704211, 35.851984],
          [-114.70613, 35.878712],
          [-114.731296, 35.945157],
          [-114.743576, 35.983721],
          [-114.755618, 36.087166],
          [-114.744857, 36.098693],
          [-114.736165, 36.104367],
          [-114.631716, 36.142306],
          [-114.572031, 36.15161],
          [-114.513256, 36.151217],
          [-114.381446, 36.141665],
          [-114.365835, 36.133722],
          [-114.328777, 36.105501],
          [-114.310857, 36.083787],
          [-114.307235, 36.076544],
          [-114.303614, 36.066456],
          [-114.292663, 36.051118],
          [-114.263146, 36.025937],
          [-114.252651, 36.020193],
          [-114.238799, 36.014561],
          [-114.233289, 36.014289],
          [-114.21369, 36.015613],
          [-114.15413, 36.023862],
          [-114.148774, 36.02731],
          [-114.138202, 36.041284],
          [-114.125796, 36.077531],
          [-114.120193, 36.102228],
          [-114.063021, 36.186942],
          [-114.060302, 36.189363],
          [-114.046838, 36.194069],
          [-114.047106, 36.250591],
          [-114.048515, 36.289598],
          [-114.046403, 36.371873],
          [-114.046488, 36.473449],
          [-114.050562, 36.656259],
          [-114.050578, 36.800173],
          [-114.050583, 36.843141],
          [-114.0506, 37.000396]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21163",
      state: "21",
      county: "163",
      name: "Meade County",
      censusArea: 305.423,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.151285, 37.798878],
          [-86.242777, 37.877002],
          [-86.490858, 38.045796],
          [-86.490853, 38.045796],
          [-86.466876, 38.046597],
          [-86.452192, 38.05049],
          [-86.438236, 38.060426],
          [-86.432789, 38.067171],
          [-86.430091, 38.078638],
          [-86.426508, 38.081784],
          [-86.398661, 38.106238],
          [-86.332567, 38.130131],
          [-86.326788, 38.134604],
          [-86.323453, 38.139032],
          [-86.314292, 38.14261],
          [-86.309727, 38.144393],
          [-86.283964, 38.143185],
          [-86.271223, 38.130112],
          [-86.278034, 38.102426],
          [-86.27872, 38.089303],
          [-86.273584, 38.067443],
          [-86.266891, 38.057125],
          [-86.220371, 38.027922],
          [-86.178983, 38.011308],
          [-86.172186, 38.00992],
          [-86.029509, 37.99264],
          [-85.998609, 37.997775],
          [-85.975948, 37.875793],
          [-86.007432, 37.818439],
          [-86.151285, 37.798878]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21177",
      state: "21",
      county: "177",
      name: "Muhlenberg County",
      censusArea: 467.079,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.099803, 37.416042],
          [-86.899268, 37.212313],
          [-86.893911, 37.088478],
          [-86.941391, 37.068981],
          [-86.976399, 37.073588],
          [-87.053164, 37.061019],
          [-87.250256, 37.040557],
          [-87.259371, 37.072401],
          [-87.333741, 37.157186],
          [-87.388826, 37.261395],
          [-87.295965, 37.391819],
          [-87.099803, 37.416042]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19051",
      state: "19",
      county: "051",
      name: "Davis County",
      censusArea: 502.188,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.17978, 40.600529],
          [-92.196162, 40.600069],
          [-92.201669, 40.59998],
          [-92.217603, 40.599832],
          [-92.236484, 40.599531],
          [-92.298754, 40.598469],
          [-92.331205, 40.597805],
          [-92.331445, 40.597714],
          [-92.350776, 40.597274],
          [-92.350807, 40.597273],
          [-92.379691, 40.596509],
          [-92.453745, 40.595288],
          [-92.461609, 40.595355],
          [-92.481692, 40.594941],
          [-92.482394, 40.594894],
          [-92.484588, 40.594924],
          [-92.580278, 40.592151],
          [-92.637898, 40.590853],
          [-92.639091, 40.898886],
          [-92.179072, 40.89972],
          [-92.17899, 40.898343],
          [-92.17978, 40.600529]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19065",
      state: "19",
      county: "065",
      name: "Fayette County",
      censusArea: 730.811,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.607059, 42.643973],
          [-92.081661, 42.642056],
          [-92.081568, 42.907013],
          [-92.081146, 43.082794],
          [-91.605307, 43.081653],
          [-91.607059, 42.643973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19071",
      state: "19",
      county: "071",
      name: "Fremont County",
      censusArea: 511.15,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.765645, 40.585208],
          [-95.753148, 40.59284],
          [-95.750053, 40.597052],
          [-95.748626, 40.603355],
          [-95.776251, 40.647463],
          [-95.786568, 40.657253],
          [-95.795489, 40.662384],
          [-95.822913, 40.66724],
          [-95.842801, 40.677496],
          [-95.883178, 40.717579],
          [-95.888907, 40.731855],
          [-95.88669, 40.742101],
          [-95.881529, 40.750611],
          [-95.872281, 40.758349],
          [-95.861695, 40.762871],
          [-95.854172, 40.784012],
          [-95.821193, 40.876682],
          [-95.823123, 40.900924],
          [-95.813465, 40.901693],
          [-95.384964, 40.90155],
          [-95.373923, 40.580501],
          [-95.415406, 40.581014],
          [-95.469319, 40.58154],
          [-95.525392, 40.58209],
          [-95.526682, 40.582136],
          [-95.533182, 40.582249],
          [-95.554959, 40.582629],
          [-95.574046, 40.582963],
          [-95.611069, 40.583495],
          [-95.64184, 40.584234],
          [-95.687442, 40.58438],
          [-95.6875, 40.584381],
          [-95.746443, 40.584935],
          [-95.765645, 40.585208]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19083",
      state: "19",
      county: "083",
      name: "Hardin County",
      censusArea: 569.307,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.463043, 42.209302],
          [-93.462635, 42.470802],
          [-93.499485, 42.5577],
          [-93.027, 42.55681],
          [-93.002902, 42.470638],
          [-93.001674, 42.209267],
          [-93.231722, 42.208886],
          [-93.463043, 42.209302]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19085",
      state: "19",
      county: "085",
      name: "Harrison County",
      censusArea: 696.848,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.981319, 41.506837],
          [-95.994784, 41.526242],
          [-96.030593, 41.527292],
          [-96.036603, 41.509047],
          [-96.040701, 41.507076],
          [-96.046707, 41.507085],
          [-96.055096, 41.509577],
          [-96.089714, 41.531778],
          [-96.09409, 41.539265],
          [-96.118105, 41.613495],
          [-96.116233, 41.621574],
          [-96.097728, 41.639633],
          [-96.095046, 41.647365],
          [-96.095415, 41.652736],
          [-96.099837, 41.66103],
          [-96.121726, 41.68274],
          [-96.096795, 41.698681],
          [-96.077088, 41.715403],
          [-96.064537, 41.793002],
          [-96.06577, 41.798174],
          [-96.071007, 41.804639],
          [-96.077646, 41.808804],
          [-96.086407, 41.81138],
          [-96.110907, 41.830818],
          [-96.139554, 41.86583],
          [-96.137698, 41.866307],
          [-95.672771, 41.863147],
          [-95.557277, 41.863449],
          [-95.558143, 41.602383],
          [-95.497793, 41.602333],
          [-95.497997, 41.506074],
          [-95.981319, 41.506837]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19097",
      state: "19",
      county: "097",
      name: "Jackson County",
      censusArea: 636.039,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.898373, 42.033548],
          [-90.89802, 42.295236],
          [-90.665551, 42.294699],
          [-90.665029, 42.382155],
          [-90.474834, 42.381473],
          [-90.419027, 42.328505],
          [-90.416535, 42.325109],
          [-90.4162, 42.321314],
          [-90.424326, 42.293326],
          [-90.430735, 42.284211],
          [-90.430884, 42.27823],
          [-90.419326, 42.254467],
          [-90.391108, 42.225473],
          [-90.375129, 42.214811],
          [-90.356964, 42.205445],
          [-90.349162, 42.204277],
          [-90.316269, 42.1936],
          [-90.306531, 42.190439],
          [-90.234919, 42.165431],
          [-90.211328, 42.15401],
          [-90.167533, 42.122475],
          [-90.162225, 42.11488],
          [-90.161119, 42.104404],
          [-90.163405, 42.087613],
          [-90.168358, 42.075779],
          [-90.166495, 42.054543],
          [-90.164537, 42.045007],
          [-90.154221, 42.033073],
          [-90.898373, 42.033548]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19109",
      state: "19",
      county: "109",
      name: "Kossuth County",
      censusArea: 972.72,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.442848, 43.500583],
          [-94.442835, 43.500583],
          [-94.390597, 43.500469],
          [-94.377466, 43.500379],
          [-94.247965, 43.500333],
          [-94.10988, 43.500283],
          [-94.108068, 43.5003],
          [-94.094339, 43.500302],
          [-94.092894, 43.500302],
          [-93.970762, 43.499605],
          [-93.970415, 43.255358],
          [-93.971238, 42.907762],
          [-94.442954, 42.908073],
          [-94.443136, 42.909498],
          [-94.443137, 43.255014],
          [-94.442848, 43.500583]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18111",
      state: "18",
      county: "111",
      name: "Newton County",
      censusArea: 401.759,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.275882, 41.218595],
          [-87.2673, 40.736582],
          [-87.526129, 40.736885],
          [-87.526129, 40.73695],
          [-87.525783, 40.854357],
          [-87.526113, 40.879703],
          [-87.525962, 40.880618],
          [-87.526437, 40.894209],
          [-87.526084, 40.911914],
          [-87.526305, 41.010346],
          [-87.526307, 41.010355],
          [-87.526346, 41.010583],
          [-87.52652, 41.024837],
          [-87.526711, 41.121485],
          [-87.5267, 41.139658],
          [-87.526696, 41.149222],
          [-87.526693, 41.153958],
          [-87.526719, 41.159448],
          [-87.52666, 41.16009],
          [-87.526567, 41.163865],
          [-87.52657, 41.166097],
          [-87.414421, 41.162248],
          [-87.275882, 41.218595]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19119",
      state: "19",
      county: "119",
      name: "Lyon County",
      censusArea: 587.65,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.572489, 43.249178],
          [-96.584124, 43.268101],
          [-96.586317, 43.274319],
          [-96.56911, 43.295535],
          [-96.551929, 43.292974],
          [-96.530392, 43.300034],
          [-96.525564, 43.312467],
          [-96.521264, 43.374978],
          [-96.521697, 43.386897],
          [-96.524044, 43.394762],
          [-96.529152, 43.397735],
          [-96.531159, 43.39561],
          [-96.53746, 43.395246],
          [-96.557586, 43.406792],
          [-96.594254, 43.434153],
          [-96.60286, 43.450907],
          [-96.600039, 43.45708],
          [-96.58407, 43.468856],
          [-96.587151, 43.484697],
          [-96.598928, 43.500457],
          [-96.591213, 43.500514],
          [-96.453049, 43.500415],
          [-96.351059, 43.500333],
          [-96.332062, 43.500415],
          [-96.208814, 43.500391],
          [-96.198766, 43.500312],
          [-96.198484, 43.500335],
          [-96.053163, 43.500176],
          [-95.861152, 43.499966],
          [-95.860946, 43.499966],
          [-95.861914, 43.257566],
          [-96.554969, 43.259951],
          [-96.572489, 43.249178]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19133",
      state: "19",
      county: "133",
      name: "Monona County",
      censusArea: 694.072,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.139554, 41.86583],
          [-96.144483, 41.871854],
          [-96.161756, 41.90182],
          [-96.161988, 41.905553],
          [-96.159098, 41.910057],
          [-96.142265, 41.915379],
          [-96.136743, 41.920826],
          [-96.129186, 41.965136],
          [-96.129505, 41.971673],
          [-96.22173, 42.026205],
          [-96.251714, 42.040472],
          [-96.272877, 42.047238],
          [-96.279079, 42.074026],
          [-96.307421, 42.130707],
          [-96.344121, 42.162091],
          [-96.349688, 42.172043],
          [-96.35987, 42.210545],
          [-96.356666, 42.215077],
          [-95.670822, 42.211408],
          [-95.672771, 41.863147],
          [-96.137698, 41.866307],
          [-96.139554, 41.86583]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19143",
      state: "19",
      county: "143",
      name: "Osceola County",
      censusArea: 398.684,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.454433, 43.500644],
          [-95.434293, 43.50036],
          [-95.434199, 43.500314],
          [-95.387851, 43.50024],
          [-95.387812, 43.50024],
          [-95.387787, 43.50024],
          [-95.388078, 43.255221],
          [-95.861914, 43.257566],
          [-95.860946, 43.499966],
          [-95.834421, 43.499966],
          [-95.821277, 43.499965],
          [-95.741569, 43.499891],
          [-95.740813, 43.499894],
          [-95.514774, 43.499865],
          [-95.486803, 43.500246],
          [-95.486737, 43.500274],
          [-95.475065, 43.500335],
          [-95.454706, 43.500563],
          [-95.454706, 43.500648],
          [-95.454433, 43.500644]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19167",
      state: "19",
      county: "167",
      name: "Sioux County",
      censusArea: 768.332,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.455107, 43.083366],
          [-96.439335, 43.113916],
          [-96.436589, 43.120842],
          [-96.475571, 43.221054],
          [-96.485264, 43.224183],
          [-96.557126, 43.224192],
          [-96.572489, 43.249178],
          [-96.554969, 43.259951],
          [-95.861914, 43.257566],
          [-95.859377, 42.909097],
          [-96.523264, 42.909059],
          [-96.510749, 42.944397],
          [-96.509479, 42.971122],
          [-96.513111, 43.02788],
          [-96.466017, 43.062235],
          [-96.455107, 43.083366]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19173",
      state: "19",
      county: "173",
      name: "Taylor County",
      censusArea: 531.902,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.632032, 40.571186],
          [-94.632035, 40.571186],
          [-94.682601, 40.571787],
          [-94.714925, 40.572201],
          [-94.716665, 40.572201],
          [-94.773988, 40.572977],
          [-94.811188, 40.573532],
          [-94.819978, 40.573714],
          [-94.823758, 40.573942],
          [-94.896801, 40.574738],
          [-94.901451, 40.574877],
          [-94.914896, 40.575068],
          [-94.928459, 40.900653],
          [-94.470779, 40.899502],
          [-94.471213, 40.570825],
          [-94.48928, 40.570707],
          [-94.533878, 40.570739],
          [-94.537058, 40.570763],
          [-94.538318, 40.570763],
          [-94.541828, 40.570809],
          [-94.542154, 40.570809],
          [-94.594001, 40.570966],
          [-94.632032, 40.571186]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19185",
      state: "19",
      county: "185",
      name: "Wayne County",
      censusArea: 525.437,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.345442, 40.580514],
          [-93.374386, 40.580334],
          [-93.441767, 40.579916],
          [-93.465297, 40.580164],
          [-93.466887, 40.580072],
          [-93.524124, 40.580481],
          [-93.527607, 40.580436],
          [-93.528177, 40.580367],
          [-93.548284, 40.580417],
          [-93.553986, 40.580303],
          [-93.556899, 40.580235],
          [-93.55654, 40.898295],
          [-93.097595, 40.898451],
          [-93.097296, 40.584014],
          [-93.098507, 40.583973],
          [-93.260612, 40.580797],
          [-93.317605, 40.580671],
          [-93.345442, 40.580514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19191",
      state: "19",
      county: "191",
      name: "Winneshiek County",
      censusArea: 689.868,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.730217, 43.50068],
          [-91.700749, 43.500581],
          [-91.670872, 43.500513],
          [-91.658401, 43.500533],
          [-91.651396, 43.500454],
          [-91.644924, 43.500529],
          [-91.639772, 43.500573],
          [-91.635626, 43.500463],
          [-91.634495, 43.500439],
          [-91.634244, 43.500479],
          [-91.625611, 43.500727],
          [-91.620785, 43.500677],
          [-91.617407, 43.500687],
          [-91.616895, 43.500663],
          [-91.615293, 43.50055],
          [-91.610895, 43.50053],
          [-91.610832, 43.50053],
          [-91.605307, 43.081653],
          [-92.081146, 43.082794],
          [-92.080976, 43.212947],
          [-92.079802, 43.500647],
          [-91.949879, 43.500485],
          [-91.941837, 43.500554],
          [-91.824848, 43.500684],
          [-91.807156, 43.500648],
          [-91.804925, 43.500716],
          [-91.77929, 43.500803],
          [-91.777688, 43.500711],
          [-91.761414, 43.500637],
          [-91.738446, 43.500525],
          [-91.736558, 43.500561],
          [-91.73333, 43.500623],
          [-91.730359, 43.50068],
          [-91.730217, 43.50068]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20001",
      state: "20",
      county: "001",
      name: "Allen County",
      censusArea: 500.303,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.088082, 37.732476],
          [-95.525499, 37.732759],
          [-95.51897, 38.03823],
          [-95.077876, 38.037706],
          [-95.088082, 37.732476]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20007",
      state: "20",
      county: "007",
      name: "Barber County",
      censusArea: 1134.073,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.347149, 36.997962],
          [-98.354073, 36.997961],
          [-98.408991, 36.998513],
          [-98.418268, 36.998538],
          [-98.420209, 36.998516],
          [-98.54466, 36.998996],
          [-98.544872, 36.998997],
          [-98.714512, 36.99906],
          [-98.718465, 36.99918],
          [-98.761597, 36.999425],
          [-98.791936, 36.999255],
          [-98.793711, 36.999227],
          [-98.797452, 36.999229],
          [-98.869449, 36.999286],
          [-98.880009, 36.999263],
          [-98.88058, 36.999309],
          [-98.994371, 36.999493],
          [-99.000303, 36.99951],
          [-99.001294, 37.384296],
          [-99.011499, 37.384263],
          [-99.011755, 37.431042],
          [-99.012186, 37.436186],
          [-99.012598, 37.470421],
          [-98.464663, 37.471013],
          [-98.464951, 37.384086],
          [-98.349804, 37.384056],
          [-98.347149, 36.997962]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20011",
      state: "20",
      county: "011",
      name: "Bourbon County",
      censusArea: 635.471,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.613981, 38.037057],
          [-94.613981, 38.036949],
          [-94.614212, 37.992462],
          [-94.614465, 37.987799],
          [-94.614557, 37.971037],
          [-94.614562, 37.951517],
          [-94.614594, 37.949978],
          [-94.614612, 37.944362],
          [-94.614754, 37.940769],
          [-94.614835, 37.9367],
          [-94.614778, 37.9342],
          [-94.615181, 37.915944],
          [-94.615393, 37.906392],
          [-94.615469, 37.901775],
          [-94.615706, 37.886843],
          [-94.615921, 37.878331],
          [-94.615834, 37.87251],
          [-94.616, 37.863126],
          [-94.616426, 37.845282],
          [-94.61645, 37.83756],
          [-94.616862, 37.819456],
          [-94.617808, 37.729707],
          [-94.617975, 37.722176],
          [-94.617805, 37.690178],
          [-94.617651, 37.687671],
          [-94.617687, 37.686653],
          [-94.617885, 37.682214],
          [-94.617734, 37.673127],
          [-94.617734, 37.673105],
          [-95.088012, 37.674519],
          [-95.088082, 37.732476],
          [-95.077876, 38.037706],
          [-94.613981, 38.037057]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22071",
      state: "22",
      county: "071",
      name: "Orleans County",
      lsad: "Parish",
      censusArea: 169.423,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.124775, 29.975791],
          [-90.120959, 30.021975],
          [-90.118541, 30.029845],
          [-90.119373, 30.036637],
          [-90.105456, 30.191801],
          [-89.99426, 30.150039],
          [-89.878689, 30.199193],
          [-89.794106, 30.151326],
          [-89.749211, 30.176429],
          [-89.62288, 30.152368],
          [-89.625053, 30.150717],
          [-89.640989, 30.138612],
          [-89.64455, 30.134108],
          [-89.650467, 30.126625],
          [-89.656986, 30.118381],
          [-89.658594, 30.117364],
          [-89.668164, 30.111311],
          [-89.668638, 30.111011],
          [-89.669157, 30.110683],
          [-89.669182, 30.110667],
          [-89.672881, 30.11049],
          [-89.674124, 30.11043],
          [-89.674633, 30.110406],
          [-89.674956, 30.110212],
          [-89.675762, 30.109728],
          [-89.676117, 30.109515],
          [-89.678156, 30.10829],
          [-89.678163, 30.108286],
          [-89.678293, 30.107746],
          [-89.678457, 30.107059],
          [-89.678499, 30.106886],
          [-89.679655, 30.102067],
          [-89.679823, 30.101367],
          [-89.679838, 30.101304],
          [-89.680042, 30.100452],
          [-89.68035, 30.099171],
          [-89.680485, 30.098605],
          [-89.681265, 30.095355],
          [-89.681906, 30.092682],
          [-89.682057, 30.092052],
          [-89.682181, 30.091536],
          [-89.682181, 30.091531],
          [-89.682277, 30.090565],
          [-89.68228, 30.090531],
          [-89.682331, 30.090015],
          [-89.682371, 30.089607],
          [-89.682393, 30.089391],
          [-89.682409, 30.089225],
          [-89.682691, 30.086365],
          [-89.683051, 30.082718],
          [-89.683168, 30.08153],
          [-89.683712, 30.076018],
          [-89.697556, 30.070842],
          [-89.698461, 30.070504],
          [-89.698496, 30.070491],
          [-89.698909, 30.070451],
          [-89.699144, 30.070428],
          [-89.699629, 30.070381],
          [-89.711848, 30.069194],
          [-89.712942, 30.069088],
          [-89.713003, 30.069061],
          [-89.714085, 30.068582],
          [-89.714827, 30.068253],
          [-89.715494, 30.067958],
          [-89.715884, 30.067785],
          [-89.716237, 30.067628],
          [-89.716425, 30.067545],
          [-89.71675, 30.067402],
          [-89.716887, 30.067341],
          [-89.721992, 30.06508],
          [-89.724433, 30.063999],
          [-89.72633, 30.063158],
          [-89.727453, 30.062661],
          [-89.728026, 30.061841],
          [-89.72957, 30.059628],
          [-89.72968, 30.05947],
          [-89.72979, 30.059314],
          [-89.729911, 30.059139],
          [-89.73099, 30.057594],
          [-89.730999, 30.057581],
          [-89.731208, 30.054558],
          [-89.731428, 30.051377],
          [-89.731452, 30.05104],
          [-89.731545, 30.049694],
          [-89.731545, 30.049691],
          [-89.731544, 30.04969],
          [-89.7163, 30.02811],
          [-89.7163, 30.028106],
          [-89.716301, 30.028088],
          [-89.716328, 30.027415],
          [-89.716336, 30.02723],
          [-89.716358, 30.026686],
          [-89.716377, 30.026222],
          [-89.724649, 30.022454],
          [-89.724669, 30.022453],
          [-89.72576, 30.022403],
          [-89.733323, 30.022054],
          [-89.734361, 30.022884],
          [-89.739028, 30.026618],
          [-89.739574, 30.027055],
          [-89.740886, 30.028104],
          [-89.745827, 30.032056],
          [-89.746313, 30.032445],
          [-89.746505, 30.032599],
          [-89.746506, 30.0326],
          [-89.757138, 30.03865],
          [-89.763216, 30.042108],
          [-89.782534, 30.045372],
          [-89.78463, 30.045253],
          [-89.813684, 30.043605],
          [-89.818561, 30.043328],
          [-89.829762, 30.033275],
          [-89.830442, 30.032664],
          [-89.832213, 30.031075],
          [-89.832965, 30.0304],
          [-89.833794, 30.029656],
          [-89.834644, 30.028893],
          [-89.835202, 30.028393],
          [-89.839926, 30.024153],
          [-89.839933, 30.024146],
          [-89.840963, 30.022995],
          [-89.841739, 30.022127],
          [-89.854533, 30.007821],
          [-89.989882, 29.98949],
          [-90.012102, 29.94598],
          [-89.921019, 29.915729],
          [-89.911037, 29.868543],
          [-90.008056, 29.895528],
          [-90.058547, 29.945334],
          [-90.140074, 29.947904],
          [-90.126559, 29.970265],
          [-90.124775, 29.975791]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27075",
      state: "27",
      county: "075",
      name: "Lake County",
      censusArea: 2109.292,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.023125, 47.464964],
          [-91.032945, 47.458236],
          [-91.045646, 47.456525],
          [-91.077712, 47.428767],
          [-91.097569, 47.413888],
          [-91.106218, 47.411806],
          [-91.128131, 47.399619],
          [-91.131268, 47.393567],
          [-91.146958, 47.381464],
          [-91.156513, 47.378816],
          [-91.170037, 47.366266],
          [-91.188772, 47.340082],
          [-91.206248, 47.329182],
          [-91.238658, 47.304976],
          [-91.250163, 47.29049],
          [-91.262512, 47.27929],
          [-91.26595, 47.279479],
          [-91.270697, 47.277134],
          [-91.288478, 47.26596],
          [-91.326019, 47.238993],
          [-91.35385, 47.212686],
          [-91.357803, 47.206743],
          [-91.374191, 47.1978],
          [-91.387021, 47.187293],
          [-91.398455, 47.183916],
          [-91.418805, 47.172152],
          [-91.452031, 47.145158],
          [-91.456965, 47.139156],
          [-91.477351, 47.125667],
          [-91.497902, 47.122579],
          [-91.506998, 47.118489],
          [-91.518793, 47.108121],
          [-91.573817, 47.089917],
          [-91.591508, 47.068684],
          [-91.600969, 47.063425],
          [-91.604949, 47.063309],
          [-91.613173, 47.059192],
          [-91.626824, 47.049953],
          [-91.637164, 47.040429],
          [-91.644564, 47.026491],
          [-91.660248, 47.019288],
          [-91.666477, 47.014297],
          [-91.704649, 47.005246],
          [-91.737098, 46.982853],
          [-91.7773, 46.951799],
          [-91.780675, 46.945881],
          [-91.794039, 46.939676],
          [-91.787928, 47.546817],
          [-91.799114, 47.732178],
          [-91.798268, 48.200086],
          [-91.789693, 48.197829],
          [-91.742313, 48.204491],
          [-91.714931, 48.19913],
          [-91.710519, 48.193898],
          [-91.699336, 48.144728],
          [-91.708099, 48.122985],
          [-91.712226, 48.116883],
          [-91.71185, 48.114598],
          [-91.641062, 48.097703],
          [-91.569746, 48.093348],
          [-91.488067, 48.068111],
          [-91.340159, 48.073236],
          [-91.328738, 48.070588],
          [-91.290215, 48.073945],
          [-91.26638, 48.078713],
          [-91.250112, 48.084087],
          [-91.176181, 48.125811],
          [-91.140773, 48.147689],
          [-91.138311, 48.151024],
          [-91.138482, 48.151458],
          [-91.035858, 48.189436],
          [-91.03555, 48.189459],
          [-91.032942, 48.190794],
          [-91.020737, 47.887995],
          [-91.023125, 47.464964]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21161",
      state: "21",
      county: "161",
      name: "Mason County",
      censusArea: 240.128,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.706043, 38.637101],
          [-83.679484, 38.630036],
          [-83.663911, 38.62793],
          [-83.655425, 38.629735],
          [-83.649737, 38.632753],
          [-83.646701, 38.637489],
          [-83.641852, 38.525381],
          [-83.85928, 38.456348],
          [-83.930348, 38.492278],
          [-83.991605, 38.593701],
          [-83.903918, 38.76815],
          [-83.870443, 38.761608],
          [-83.859028, 38.756793],
          [-83.85333, 38.752572],
          [-83.777823, 38.674453],
          [-83.773682, 38.661309],
          [-83.769347, 38.65522],
          [-83.762445, 38.652103],
          [-83.706043, 38.637101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27123",
      state: "27",
      county: "123",
      name: "Ramsey County",
      censusArea: 152.212,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.227696, 45.124533],
          [-93.198985, 45.124394],
          [-93.188706, 45.124374],
          [-93.020518, 45.123866],
          [-92.984621, 45.123757],
          [-92.984712, 45.075372],
          [-92.98464, 44.948522],
          [-92.984602, 44.947636],
          [-92.98462, 44.946114],
          [-92.984522, 44.945685],
          [-92.984584, 44.945077],
          [-92.98441, 44.920646],
          [-92.984463, 44.919869],
          [-92.984396, 44.909644],
          [-92.984458, 44.909393],
          [-92.984207, 44.893998],
          [-92.984192, 44.890814],
          [-93.020044, 44.89075],
          [-93.128629, 44.919648],
          [-93.178539, 44.888338],
          [-93.208092, 45.035744],
          [-93.226929, 45.035671],
          [-93.227059, 45.049937],
          [-93.227696, 45.124533]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27135",
      state: "27",
      county: "135",
      name: "Roseau County",
      censusArea: 1671.602,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.319895, 48.998769],
          [-95.235606, 48.88145],
          [-95.091464, 48.920618],
          [-95.089863, 48.713062],
          [-95.34273, 48.713028],
          [-95.34254, 48.540211],
          [-95.586733, 48.538533],
          [-95.602315, 48.538887],
          [-96.387835, 48.544308],
          [-96.405412, 48.99982],
          [-95.368698, 48.998729],
          [-95.355819, 48.998735],
          [-95.340962, 48.99874],
          [-95.322946, 48.998767],
          [-95.319895, 48.998769]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27151",
      state: "27",
      county: "151",
      name: "Swift County",
      censusArea: 742.083,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.747402, 45.412313],
          [-95.255206, 45.412525],
          [-95.24649, 45.152384],
          [-96.036698, 45.152206],
          [-96.048217, 45.15843],
          [-96.053511, 45.156453],
          [-96.075686, 45.165935],
          [-96.084646, 45.165762],
          [-96.103614, 45.176675],
          [-96.117036, 45.411993],
          [-95.747402, 45.412313]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27155",
      state: "27",
      county: "155",
      name: "Traverse County",
      censusArea: 573.902,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.835451, 45.586129],
          [-96.843957, 45.594003],
          [-96.844334, 45.594375],
          [-96.849444, 45.598944],
          [-96.853646, 45.602307],
          [-96.857751, 45.605962],
          [-96.844211, 45.639583],
          [-96.840746, 45.645294],
          [-96.835769, 45.649648],
          [-96.745086, 45.701576],
          [-96.711157, 45.717561],
          [-96.672665, 45.732336],
          [-96.652226, 45.746809],
          [-96.583085, 45.820024],
          [-96.57974, 45.82582],
          [-96.576544, 45.839945],
          [-96.572984, 45.861602],
          [-96.568772, 45.888072],
          [-96.568281, 45.891203],
          [-96.568053, 45.898697],
          [-96.568315, 45.902902],
          [-96.568918, 45.911426],
          [-96.569265, 45.911471],
          [-96.569401, 45.913946],
          [-96.569069, 45.914785],
          [-96.564317, 45.921074],
          [-96.564518, 45.926256],
          [-96.56328, 45.935238],
          [-96.57035, 45.963595],
          [-96.577326, 46.021757],
          [-96.576347, 46.02185],
          [-96.26614, 46.02161],
          [-96.254022, 45.75982],
          [-96.253026, 45.585526],
          [-96.835451, 45.586129]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27165",
      state: "27",
      county: "165",
      name: "Watonwan County",
      censusArea: 434.947,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.368974, 43.848047],
          [-94.854444, 43.848099],
          [-94.859388, 43.848093],
          [-94.859803, 44.108016],
          [-94.36885, 44.108698],
          [-94.368974, 43.848047]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27167",
      state: "27",
      county: "167",
      name: "Wilkin County",
      censusArea: 750.957,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.577326, 46.021757],
          [-96.57794, 46.026874],
          [-96.566295, 46.051416],
          [-96.559271, 46.058272],
          [-96.55694, 46.064601],
          [-96.554835, 46.084824],
          [-96.557952, 46.102442],
          [-96.578362, 46.170112],
          [-96.585647, 46.177309],
          [-96.599087, 46.263701],
          [-96.598233, 46.312563],
          [-96.631586, 46.353752],
          [-96.669794, 46.384644],
          [-96.721999, 46.439986],
          [-96.716438, 46.444567],
          [-96.739054, 46.543708],
          [-96.783003, 46.630405],
          [-96.280977, 46.630765],
          [-96.28, 46.28222],
          [-96.265366, 46.10861],
          [-96.26608, 46.05796],
          [-96.266048, 46.057771],
          [-96.26603, 46.05742],
          [-96.26614, 46.02161],
          [-96.576347, 46.02185],
          [-96.577326, 46.021757]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27173",
      state: "27",
      county: "173",
      name: "Yellow Medcine County",
      censusArea: 759.101,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.451761, 44.63135],
          [-96.451543, 44.703135],
          [-96.451573, 44.76051],
          [-96.45138, 44.761788],
          [-96.45162, 44.776191],
          [-96.451823, 44.790471],
          [-96.451888, 44.792299],
          [-96.451829, 44.797691],
          [-96.451559, 44.805468],
          [-96.45156, 44.805569],
          [-95.849009, 44.805347],
          [-95.847452, 44.891799],
          [-95.737309, 44.891479],
          [-95.737015, 44.93618],
          [-95.498456, 44.786867],
          [-95.482843, 44.752505],
          [-95.360177, 44.69854],
          [-95.3604, 44.541762],
          [-95.594817, 44.542305],
          [-95.604001, 44.629945],
          [-96.09295, 44.630486],
          [-96.449714, 44.63121],
          [-96.451761, 44.63135]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28001",
      state: "28",
      county: "001",
      name: "Adams County",
      censusArea: 462.407,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.153864, 31.610068],
          [-91.15815, 31.346695],
          [-91.306294, 31.360862],
          [-91.468435, 31.31957],
          [-91.488954, 31.22592],
          [-91.599965, 31.192318],
          [-91.59849, 31.205485],
          [-91.574493, 31.261289],
          [-91.547588, 31.264444],
          [-91.522397, 31.273423],
          [-91.515614, 31.27821],
          [-91.508858, 31.291644],
          [-91.51982, 31.311228],
          [-91.531201, 31.326625],
          [-91.539904, 31.337336],
          [-91.541242, 31.35675],
          [-91.525386, 31.378904],
          [-91.513795, 31.386875],
          [-91.505093, 31.400933],
          [-91.500406, 31.419008],
          [-91.505301, 31.432945],
          [-91.510356, 31.438928],
          [-91.51513, 31.449206],
          [-91.52292, 31.519841],
          [-91.51581, 31.530894],
          [-91.511217, 31.532612],
          [-91.479718, 31.530366],
          [-91.450017, 31.539666],
          [-91.437616, 31.546166],
          [-91.422691, 31.55439],
          [-91.416683, 31.561599],
          [-91.415482, 31.567006],
          [-91.425094, 31.57782],
          [-91.437711, 31.580824],
          [-91.44862, 31.583032],
          [-91.458679, 31.583226],
          [-91.470754, 31.58563],
          [-91.477362, 31.592239],
          [-91.477963, 31.598247],
          [-91.477963, 31.604855],
          [-91.477963, 31.610863],
          [-91.474959, 31.621677],
          [-91.464746, 31.628886],
          [-91.45273, 31.63129],
          [-91.441315, 31.63129],
          [-91.42209, 31.63129],
          [-91.404668, 31.637898],
          [-91.398059, 31.655922],
          [-91.400115, 31.688164],
          [-91.397915, 31.709364],
          [-91.379935, 31.733013],
          [-91.369375, 31.746903],
          [-91.365084, 31.752743],
          [-91.317864, 31.749766],
          [-91.269313, 31.647254],
          [-91.153864, 31.610068]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28019",
      state: "28",
      county: "019",
      name: "Choctaw County",
      censusArea: 418.177,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.108824, 33.215886],
          [-89.213422, 33.197543],
          [-89.213921, 33.109402],
          [-89.317855, 33.108871],
          [-89.316408, 33.285556],
          [-89.453808, 33.285942],
          [-89.45405, 33.4332],
          [-89.381092, 33.460307],
          [-89.29614, 33.506734],
          [-89.105747, 33.532941],
          [-89.088446, 33.533202],
          [-89.088255, 33.286003],
          [-89.108886, 33.285937],
          [-89.108824, 33.215886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28025",
      state: "28",
      county: "025",
      name: "Clay County",
      censusArea: 410.077,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.018613, 33.562114],
          [-89.018625, 33.593037],
          [-89.034498, 33.73943],
          [-88.931604, 33.754312],
          [-88.931176, 33.812374],
          [-88.719979, 33.81133],
          [-88.719626, 33.680253],
          [-88.496414, 33.680075],
          [-88.513917, 33.650209],
          [-88.480299, 33.522741],
          [-88.67125, 33.506168],
          [-88.671526, 33.565135],
          [-89.018613, 33.562114]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28027",
      state: "28",
      county: "027",
      name: "Coahoma County",
      censusArea: 552.442,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.570133, 34.524829],
          [-90.568867, 34.524584],
          [-90.399498, 34.423807],
          [-90.399014, 34.261524],
          [-90.450572, 34.262402],
          [-90.450981, 34.072932],
          [-90.45053, 34.000112],
          [-90.451012, 33.985689],
          [-90.657002, 33.987586],
          [-90.659457, 34.118867],
          [-90.931969, 34.120017],
          [-90.933116, 34.124488],
          [-90.934564, 34.130138],
          [-90.918405, 34.154376],
          [-90.893013, 34.155531],
          [-90.880317, 34.169381],
          [-90.880894, 34.180346],
          [-90.895898, 34.191888],
          [-90.913212, 34.210355],
          [-90.91148, 34.223051],
          [-90.894744, 34.229977],
          [-90.868775, 34.228245],
          [-90.856367, 34.238928],
          [-90.764143, 34.363396],
          [-90.658542, 34.375705],
          [-90.613944, 34.390723],
          [-90.575336, 34.415152],
          [-90.565809, 34.4354],
          [-90.570133, 34.524829]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19117",
      state: "19",
      county: "117",
      name: "Lucas County",
      censusArea: 430.593,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.557556, 41.161271],
          [-93.327886, 41.160659],
          [-93.099217, 41.160867],
          [-93.097724, 40.899738],
          [-93.097595, 40.898451],
          [-93.55654, 40.898295],
          [-93.557556, 41.161271]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37053",
      state: "37",
      county: "053",
      name: "Currituck County",
      censusArea: 261.852,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.12236, 36.550621],
          [-76.034751, 36.550653],
          [-76.02675, 36.550553],
          [-75.957648, 36.550553],
          [-75.955748, 36.550553],
          [-75.953447, 36.550553],
          [-75.952847, 36.550553],
          [-75.922046, 36.550654],
          [-75.911446, 36.550654],
          [-75.909046, 36.550654],
          [-75.904745, 36.550654],
          [-75.903445, 36.550654],
          [-75.894145, 36.550754],
          [-75.893245, 36.550654],
          [-75.891945, 36.550754],
          [-75.886545, 36.550754],
          [-75.885945, 36.550754],
          [-75.880644, 36.550754],
          [-75.879744, 36.550754],
          [-75.867044, 36.550754],
          [-75.856901, 36.500155],
          [-75.834975, 36.42265],
          [-75.818735, 36.357579],
          [-75.79641, 36.290351],
          [-75.773329, 36.231529],
          [-75.794371, 36.227336],
          [-75.798528, 36.2308],
          [-75.80369, 36.235853],
          [-75.811588, 36.244014],
          [-75.811851, 36.247699],
          [-75.808165, 36.259545],
          [-75.814483, 36.285344],
          [-75.822907, 36.291662],
          [-75.833964, 36.292188],
          [-75.837913, 36.294558],
          [-75.845284, 36.305614],
          [-75.841335, 36.328517],
          [-75.831858, 36.339047],
          [-75.831595, 36.346418],
          [-75.836201, 36.363135],
          [-75.843046, 36.371032],
          [-75.847258, 36.372085],
          [-75.85147, 36.379456],
          [-75.852523, 36.384721],
          [-75.85147, 36.415785],
          [-75.864106, 36.430527],
          [-75.880428, 36.435792],
          [-75.888325, 36.441583],
          [-75.891484, 36.460537],
          [-75.899908, 36.482124],
          [-75.907279, 36.485809],
          [-75.913071, 36.486336],
          [-75.917283, 36.485809],
          [-75.924127, 36.482124],
          [-75.927333, 36.482815],
          [-75.935473, 36.490601],
          [-75.960069, 36.495025],
          [-75.972545, 36.494671],
          [-76.003708, 36.506235],
          [-76.019261, 36.503506],
          [-76.023627, 36.500778],
          [-76.029221, 36.494365],
          [-76.031949, 36.482496],
          [-76.020216, 36.45862],
          [-76.012337, 36.447462],
          [-76.003912, 36.441864],
          [-75.989869, 36.436808],
          [-75.98005, 36.435464],
          [-75.962285, 36.41724],
          [-75.940676, 36.41885],
          [-75.936446, 36.423079],
          [-75.932694, 36.427627],
          [-75.928369, 36.428588],
          [-75.923601, 36.425788],
          [-75.916409, 36.38901],
          [-75.916949, 36.383167],
          [-75.923511, 36.367796],
          [-75.923331, 36.361863],
          [-75.917758, 36.353593],
          [-75.915331, 36.352335],
          [-75.895285, 36.319615],
          [-75.888211, 36.293414],
          [-75.882154, 36.284674],
          [-75.872721, 36.28277],
          [-75.864933, 36.284674],
          [-75.86052, 36.280607],
          [-75.861818, 36.266415],
          [-75.867356, 36.252483],
          [-75.864154, 36.235522],
          [-75.858703, 36.222628],
          [-75.848838, 36.21657],
          [-75.838367, 36.200129],
          [-75.841222, 36.193812],
          [-75.839924, 36.17711],
          [-75.823915, 36.158332],
          [-75.822531, 36.145957],
          [-75.813444, 36.136871],
          [-75.800378, 36.112728],
          [-75.791637, 36.082267],
          [-75.793974, 36.07171],
          [-75.799779, 36.07264],
          [-75.836084, 36.092616],
          [-75.847785, 36.10199],
          [-75.867792, 36.127262],
          [-75.866323, 36.14141],
          [-75.867385, 36.151182],
          [-75.869828, 36.15458],
          [-75.875033, 36.157448],
          [-75.887415, 36.160233],
          [-75.948426, 36.285268],
          [-76.050755, 36.359819],
          [-76.114069, 36.357498],
          [-76.158583, 36.426347],
          [-76.313196, 36.550551],
          [-76.12236, 36.550621]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37079",
      state: "37",
      county: "079",
      name: "Greene County",
      censusArea: 265.928,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.826207, 35.476813],
          [-77.824291, 35.498663],
          [-77.823471, 35.585523],
          [-77.822372, 35.585467],
          [-77.818142, 35.587825],
          [-77.816125, 35.588443],
          [-77.811456, 35.586056],
          [-77.809014, 35.584179],
          [-77.806097, 35.583457],
          [-77.801698, 35.586132],
          [-77.753646, 35.615818],
          [-77.700502, 35.652256],
          [-77.509307, 35.519569],
          [-77.476, 35.426684],
          [-77.660811, 35.352757],
          [-77.680542, 35.34776],
          [-77.701681, 35.380597],
          [-77.738695, 35.374869],
          [-77.749753, 35.373241],
          [-77.77768, 35.367682],
          [-77.805564, 35.369768],
          [-77.805258, 35.370218],
          [-77.802742, 35.372945],
          [-77.799961, 35.382462],
          [-77.80559, 35.407469],
          [-77.818414, 35.415911],
          [-77.827, 35.431151],
          [-77.826207, 35.476813]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37089",
      state: "37",
      county: "089",
      name: "Henderson County",
      censusArea: 373.068,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.35243, 35.206595],
          [-82.384029, 35.210542],
          [-82.476136, 35.175486],
          [-82.51691, 35.163029],
          [-82.517284, 35.162643],
          [-82.535804, 35.158699],
          [-82.536527, 35.159137],
          [-82.574406, 35.144216],
          [-82.619513, 35.303758],
          [-82.745209, 35.422924],
          [-82.744383, 35.423342],
          [-82.672392, 35.459534],
          [-82.570269, 35.431255],
          [-82.266847, 35.465854],
          [-82.279615, 35.445973],
          [-82.270255, 35.400351],
          [-82.261456, 35.392984],
          [-82.346089, 35.286038],
          [-82.35243, 35.206595]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42035",
      state: "42",
      county: "035",
      name: "Clinton County",
      censusArea: 887.984,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.989194, 41.474822],
          [-77.598129, 41.478576],
          [-77.526537, 41.358528],
          [-77.320891, 41.218932],
          [-77.144111, 41.06884],
          [-77.143723, 41.04578],
          [-77.14416, 41.044338],
          [-77.508596, 40.962938],
          [-77.621759, 41.091185],
          [-77.741911, 41.107614],
          [-77.79856, 41.18195],
          [-77.892724, 41.178259],
          [-77.898195, 41.252752],
          [-78.038203, 41.153633],
          [-78.061007, 41.174539],
          [-78.093381, 41.216931],
          [-77.988786, 41.367453],
          [-77.989194, 41.474822]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42041",
      state: "42",
      county: "041",
      name: "Cumberland County",
      censusArea: 545.459,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.137425, 40.069945],
          [-77.255209, 40.02076],
          [-77.403788, 39.994447],
          [-77.471085, 39.944077],
          [-77.614665, 40.198549],
          [-77.366018, 40.313569],
          [-77.419795, 40.26407],
          [-77.280548, 40.275923],
          [-77.270057, 40.278652],
          [-77.241691, 40.282847],
          [-77.229166, 40.283744],
          [-77.083942, 40.30122],
          [-76.91497, 40.328484],
          [-76.859023, 40.226328],
          [-76.905875, 40.165723],
          [-77.028853, 40.147838],
          [-77.137425, 40.069945]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20021",
      state: "20",
      county: "021",
      name: "Cherokee County",
      censusArea: 587.568,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.61808, 36.998135],
          [-94.699735, 36.998805],
          [-94.701797, 36.998814],
          [-94.71277, 36.998794],
          [-94.737183, 36.998665],
          [-94.739324, 36.998687],
          [-94.777257, 36.998764],
          [-94.83128, 36.998812],
          [-94.840926, 36.998833],
          [-94.849801, 36.998876],
          [-94.853197, 36.998874],
          [-94.995293, 36.999529],
          [-95.00762, 36.999514],
          [-95.011433, 36.999535],
          [-95.030324, 36.999517],
          [-95.037857, 36.999497],
          [-95.049499, 36.99958],
          [-95.073504, 36.999509],
          [-95.075166, 37.339937],
          [-94.619202, 37.33836],
          [-94.617636, 37.338415],
          [-94.617695, 37.336842],
          [-94.617648, 37.323589],
          [-94.618075, 37.240436],
          [-94.618158, 37.237597],
          [-94.618123, 37.229334],
          [-94.61815, 37.228121],
          [-94.618219, 37.207772],
          [-94.618305, 37.207337],
          [-94.618319, 37.188774],
          [-94.618505, 37.181184],
          [-94.618473, 37.174782],
          [-94.618351, 37.160211],
          [-94.618072, 37.132345],
          [-94.618075, 37.129755],
          [-94.618212, 37.113169],
          [-94.618151, 37.103968],
          [-94.618059, 37.096676],
          [-94.618088, 37.093671],
          [-94.61809, 37.093494],
          [-94.618082, 37.086432],
          [-94.61812, 37.085934],
          [-94.617982, 37.075077],
          [-94.617875, 37.056798],
          [-94.617875, 37.056797],
          [-94.617965, 37.040537],
          [-94.617995, 37.009016],
          [-94.61808, 36.998135]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28055",
      state: "28",
      county: "055",
      name: "Issaquena County",
      censusArea: 413.045,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.720102, 32.618858],
          [-90.728344, 32.566265],
          [-90.860566, 32.575173],
          [-90.861364, 32.442115],
          [-90.95124, 32.444389],
          [-90.964043, 32.575605],
          [-91.090771, 32.563978],
          [-91.096774, 32.576652],
          [-91.104111, 32.584657],
          [-91.119854, 32.584795],
          [-91.127912, 32.586493],
          [-91.141148, 32.597209],
          [-91.146204, 32.604144],
          [-91.151318, 32.615919],
          [-91.153821, 32.631282],
          [-91.152081, 32.641508],
          [-91.118258, 32.674075],
          [-91.098762, 32.685291],
          [-91.076061, 32.693751],
          [-91.063946, 32.702926],
          [-91.057043, 32.712576],
          [-91.054481, 32.722259],
          [-91.060766, 32.727494],
          [-91.077176, 32.732534],
          [-91.123152, 32.742798],
          [-91.154461, 32.742339],
          [-91.163389, 32.747009],
          [-91.165328, 32.751301],
          [-91.164397, 32.785821],
          [-91.161669, 32.812465],
          [-91.158336, 32.822304],
          [-91.145002, 32.84287],
          [-91.127886, 32.855059],
          [-91.116091, 32.855641],
          [-91.105631, 32.858396],
          [-91.086683, 32.873392],
          [-91.070602, 32.888659],
          [-91.063809, 32.903709],
          [-91.063974, 32.924113],
          [-91.086802, 32.976266],
          [-91.094265, 32.984371],
          [-91.106581, 32.988938],
          [-91.125107, 32.984669],
          [-91.134414, 32.980533],
          [-91.138585, 32.971352],
          [-91.130947, 32.963815],
          [-91.132115, 32.923122],
          [-91.134041, 32.917676],
          [-91.145076, 32.905494],
          [-91.159975, 32.899879],
          [-91.175405, 32.899998],
          [-91.196785, 32.906784],
          [-91.208263, 32.915354],
          [-91.212837, 32.922104],
          [-91.214027, 32.93032],
          [-91.201842, 32.961212],
          [-91.166073, 33.004106],
          [-91.157662, 33.011217],
          [-90.914337, 33.007697],
          [-90.963753, 32.838133],
          [-90.964864, 32.662798],
          [-90.723159, 32.662128],
          [-90.712819, 32.642346],
          [-90.693011, 32.640113],
          [-90.697563, 32.625135],
          [-90.720102, 32.618858]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28063",
      state: "28",
      county: "063",
      name: "Jefferson County",
      censusArea: 519.933,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.736282, 31.786134],
          [-90.736824, 31.698491],
          [-90.73733, 31.611124],
          [-91.153864, 31.610068],
          [-91.269313, 31.647254],
          [-91.317864, 31.749766],
          [-91.365084, 31.752743],
          [-91.365614, 31.760763],
          [-91.363714, 31.780363],
          [-91.359514, 31.799362],
          [-91.345714, 31.842861],
          [-91.338414, 31.851261],
          [-91.326914, 31.854961],
          [-91.293413, 31.86016],
          [-91.248144, 31.869848],
          [-91.013676, 31.861852],
          [-90.842, 31.826498],
          [-90.852657, 31.785422],
          [-90.736282, 31.786134]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28071",
      state: "28",
      county: "071",
      name: "Lafayette County",
      censusArea: 631.708,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.509748, 34.162082],
          [-89.50944, 34.19096],
          [-89.72105, 34.192545],
          [-89.721341, 34.554274],
          [-89.668448, 34.554367],
          [-89.476642, 34.554168],
          [-89.424459, 34.519129],
          [-89.246133, 34.495633],
          [-89.246339, 34.379344],
          [-89.245485, 34.161186],
          [-89.250072, 34.161022],
          [-89.509748, 34.162082]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23001",
      state: "23",
      county: "001",
      name: "Androscoggin County",
      censusArea: 467.927,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.080839, 43.926458],
          [-70.229244, 43.974064],
          [-70.315374, 44.037807],
          [-70.363874, 43.986445],
          [-70.480078, 44.032078],
          [-70.391735, 44.153562],
          [-70.334094, 44.210753],
          [-70.325218, 44.208399],
          [-70.265223, 44.362913],
          [-70.276752, 44.407534],
          [-70.26835, 44.416398],
          [-70.274412, 44.440632],
          [-70.238904, 44.458942],
          [-70.201276, 44.477976],
          [-70.12939, 44.487217],
          [-70.100491, 44.383854],
          [-70.07426, 44.290154],
          [-70.075747, 44.206168],
          [-69.993792, 44.180594],
          [-70.010329, 44.168651],
          [-70.027386, 44.130577],
          [-70.005471, 44.124519],
          [-70.051824, 43.990982],
          [-70.047002, 43.988078],
          [-70.046586, 43.985642],
          [-70.036399, 43.978721],
          [-70.034238, 43.975661],
          [-70.080839, 43.926458]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23003",
      state: "23",
      county: "003",
      name: "Aroostook County",
      censusArea: 6671.33,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-68.433001, 45.581204],
          [-68.436443, 46.382121],
          [-68.694877, 46.382612],
          [-68.69492, 46.395505],
          [-68.819401, 46.395782],
          [-68.821414, 46.57251],
          [-69.721109, 46.574053],
          [-70.023016, 46.573486],
          [-69.997086, 46.69523],
          [-69.818552, 46.87503],
          [-69.566383, 47.125032],
          [-69.22442, 47.459686],
          [-69.146439, 47.44886],
          [-69.055465, 47.432274],
          [-69.042702, 47.426651],
          [-69.032909, 47.407907],
          [-69.031127, 47.403106],
          [-69.04079, 47.39808],
          [-69.041733, 47.397326],
          [-69.03928, 47.385052],
          [-69.043958, 47.383682],
          [-69.046448, 47.383934],
          [-69.053819, 47.375036],
          [-69.050334, 47.256621],
          [-69.0402, 47.2451],
          [-69.033456, 47.240984],
          [-68.982002, 47.220295],
          [-68.941948, 47.206248],
          [-68.926421, 47.19786],
          [-68.900985, 47.178519],
          [-68.85343, 47.193443],
          [-68.723168, 47.239569],
          [-68.580398, 47.286957],
          [-68.474851, 47.297534],
          [-68.361559, 47.355605],
          [-68.33368, 47.36007],
          [-68.284101, 47.360389],
          [-68.240168, 47.354217],
          [-68.224005, 47.348456],
          [-68.153509, 47.314038],
          [-67.955669, 47.199542],
          [-67.952269, 47.196142],
          [-67.949369, 47.182542],
          [-67.935868, 47.164843],
          [-67.889155, 47.118772],
          [-67.846865, 47.092404],
          [-67.816345, 47.081188],
          [-67.790515, 47.067921],
          [-67.789461, 47.062544],
          [-67.78938, 46.765528],
          [-67.789284, 46.758219],
          [-67.782114, 46.279381],
          [-67.781095, 45.943032],
          [-67.768709, 45.901997],
          [-67.763955, 45.829983],
          [-67.763767, 45.820315],
          [-67.780443, 45.816206],
          [-67.801989, 45.803546],
          [-67.806598, 45.794723],
          [-67.817892, 45.693705],
          [-67.805483, 45.680241],
          [-68.048012, 45.639897],
          [-68.410884, 45.573337],
          [-68.414389, 45.572848],
          [-68.433028, 45.578482],
          [-68.433001, 45.581204]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22087",
      state: "22",
      county: "087",
      name: "St. Bernard County",
      lsad: "Parish",
      censusArea: 377.517,
      stateName: "Louisiana"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-89.911037, 29.868543],
            [-89.921019, 29.915729],
            [-90.012102, 29.94598],
            [-89.989882, 29.98949],
            [-89.854533, 30.007821],
            [-89.857558, 30.004439],
            [-89.852312, 29.97765],
            [-89.844202, 29.955645],
            [-89.8385, 29.945816],
            [-89.829023, 29.939228],
            [-89.81803, 29.934145],
            [-89.804463, 29.932588],
            [-89.775459, 29.937416],
            [-89.748492, 29.945831],
            [-89.727933, 29.95878],
            [-89.719067, 29.953699],
            [-89.71291, 29.946349],
            [-89.736311, 29.936263],
            [-89.742727, 29.935894],
            [-89.746273, 29.928221],
            [-89.742479, 29.90817],
            [-89.711158, 29.879287],
            [-89.692004, 29.868722],
            [-89.671555, 29.867535],
            [-89.660568, 29.862909],
            [-89.638016, 29.864065],
            [-89.613159, 29.87216],
            [-89.598129, 29.881409],
            [-89.591194, 29.897018],
            [-89.592346, 29.917253],
            [-89.583099, 29.931705],
            [-89.583099, 29.945581],
            [-89.574997, 29.959455],
            [-89.574425, 29.983738],
            [-89.58136, 29.994722],
            [-89.571533, 29.999926],
            [-89.551292, 30.005709],
            [-89.501587, 30.034037],
            [-89.494064, 30.040972],
            [-89.494637, 30.0508],
            [-89.499275, 30.058893],
            [-89.493484, 30.072191],
            [-89.481926, 30.079128],
            [-89.458946, 30.06345],
            [-89.444618, 30.060959],
            [-89.429047, 30.05224],
            [-89.418465, 30.049747],
            [-89.372375, 30.054729],
            [-89.368637, 30.047256],
            [-89.372375, 30.036671],
            [-89.381096, 30.030441],
            [-89.393555, 30.029818],
            [-89.41597, 30.020477],
            [-89.422813, 30.015495],
            [-89.432785, 30.008022],
            [-89.433411, 29.991205],
            [-89.432785, 29.978752],
            [-89.40538, 29.965672],
            [-89.393555, 29.966295],
            [-89.379227, 29.963804],
            [-89.378601, 29.919588],
            [-89.368019, 29.911491],
            [-89.331894, 29.91585],
            [-89.315453, 29.923208],
            [-89.283562, 29.97332],
            [-89.273315, 29.99382],
            [-89.250534, 30.002361],
            [-89.243706, 29.997236],
            [-89.249969, 29.975597],
            [-89.218071, 29.97275],
            [-89.22377, 29.961929],
            [-89.231178, 29.925484],
            [-89.244843, 29.93004],
            [-89.263062, 29.929472],
            [-89.280144, 29.924915],
            [-89.318306, 29.898149],
            [-89.322289, 29.887333],
            [-89.311462, 29.881636],
            [-89.289253, 29.880499],
            [-89.272179, 29.886763],
            [-89.241425, 29.88961],
            [-89.236298, 29.886763],
            [-89.236298, 29.877081],
            [-89.254517, 29.864552],
            [-89.269897, 29.859997],
            [-89.294952, 29.857149],
            [-89.317726, 29.850885],
            [-89.363289, 29.84576],
            [-89.383789, 29.838928],
            [-89.383217, 29.830385],
            [-89.372971, 29.82526],
            [-89.345634, 29.820135],
            [-89.342781, 29.798496],
            [-89.33197, 29.790524],
            [-89.318306, 29.788815],
            [-89.293251, 29.803053],
            [-89.277298, 29.807608],
            [-89.277298, 29.799635],
            [-89.284134, 29.795649],
            [-89.284706, 29.770021],
            [-89.269325, 29.760912],
            [-89.271034, 29.756355],
            [-89.305199, 29.756926],
            [-89.316025, 29.760912],
            [-89.325134, 29.772301],
            [-89.337662, 29.779135],
            [-89.354179, 29.781412],
            [-89.367271, 29.775148],
            [-89.386063, 29.788815],
            [-89.394608, 29.784828],
            [-89.399162, 29.770592],
            [-89.414536, 29.752371],
            [-89.428207, 29.74155],
            [-89.42421, 29.697638],
            [-89.44812, 29.703316],
            [-89.471992, 29.718597],
            [-89.486961, 29.72592],
            [-89.506065, 29.731651],
            [-89.530258, 29.74375],
            [-89.540131, 29.74375],
            [-89.560181, 29.735472],
            [-89.572922, 29.746616],
            [-89.598068, 29.74757],
            [-89.634048, 29.752981],
            [-89.651237, 29.749479],
            [-89.649651, 29.719872],
            [-89.644562, 29.710957],
            [-89.618446, 29.700768],
            [-89.59903, 29.704908],
            [-89.592979, 29.702042],
            [-89.599663, 29.690262],
            [-89.596802, 29.684212],
            [-89.573883, 29.674025],
            [-89.55732, 29.670204],
            [-89.53376, 29.670204],
            [-89.487915, 29.630405],
            [-89.485367, 29.624357],
            [-89.486709, 29.621003],
            [-89.5186, 29.649117],
            [-89.628181, 29.680682],
            [-89.721045, 29.757652],
            [-89.86972, 29.795451],
            [-89.911037, 29.868543]
          ]
        ],
        [
          [
            [-88.865067, 29.752714],
            [-88.889755, 29.718204],
            [-88.940346, 29.657234],
            [-88.944435, 29.658806],
            [-88.94632, 29.662579],
            [-88.941605, 29.674833],
            [-88.920235, 29.694319],
            [-88.911751, 29.699348],
            [-88.89006, 29.729202],
            [-88.873611, 29.758043],
            [-88.867973, 29.772272],
            [-88.867973, 29.79133],
            [-88.861267, 29.805826],
            [-88.846497, 29.817101],
            [-88.84301, 29.82596],
            [-88.836296, 29.855221],
            [-88.83683, 29.858978],
            [-88.843277, 29.86381],
            [-88.8312, 29.878839],
            [-88.828247, 29.920717],
            [-88.837379, 29.944878],
            [-88.838715, 29.962326],
            [-88.835495, 29.967695],
            [-88.835495, 29.974138],
            [-88.83979, 29.983803],
            [-88.840866, 29.995613],
            [-88.848373, 30.01333],
            [-88.857368, 30.027826],
            [-88.86972, 30.043798],
            [-88.881454, 30.053202],
            [-88.870476, 30.049212],
            [-88.855583, 30.034414],
            [-88.841225, 30.012789],
            [-88.833725, 29.998821],
            [-88.824158, 29.970461],
            [-88.817017, 29.93425],
            [-88.818146, 29.889109],
            [-88.826538, 29.847092],
            [-88.83271, 29.824062],
            [-88.844078, 29.795713],
            [-88.865067, 29.752714]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38015",
      state: "38",
      county: "015",
      name: "Burleigh County",
      censusArea: 1632.653,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.935983, 46.98284],
          [-100.882172, 47.015115],
          [-100.965156, 47.156631],
          [-100.74937, 47.157245],
          [-100.749334, 47.327685],
          [-100.673445, 47.32772],
          [-100.669601, 47.327435],
          [-100.113261, 47.327262],
          [-100.114637, 46.981666],
          [-100.075229, 46.981282],
          [-100.081198, 46.633396],
          [-100.66189, 46.63445],
          [-100.839414, 46.763776],
          [-100.819089, 46.792424],
          [-100.935983, 46.98284]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38021",
      state: "38",
      county: "021",
      name: "Dickey County",
      censusArea: 1131.47,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.008102, 45.936095],
          [-98.008202, 45.936096],
          [-98.070515, 45.93618],
          [-98.184637, 45.936183],
          [-98.18563, 45.936185],
          [-98.724375, 45.938685],
          [-98.904429, 45.93952],
          [-98.905477, 45.93952],
          [-99.005642, 45.939944],
          [-99.005754, 45.939944],
          [-99.003118, 46.282898],
          [-98.034573, 46.282796],
          [-98.029816, 46.282636],
          [-98.006715, 46.282626],
          [-98.008102, 45.936095]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38023",
      state: "38",
      county: "023",
      name: "Divide County",
      censusArea: 1260.786,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.885775, 48.633298],
          [-104.047582, 48.633976],
          [-104.047582, 48.633984],
          [-104.047819, 48.648631],
          [-104.047887, 48.649911],
          [-104.047865, 48.65745],
          [-104.047861, 48.658856],
          [-104.047849, 48.663163],
          [-104.047883, 48.664191],
          [-104.04834, 48.747133],
          [-104.048548, 48.751356],
          [-104.048537, 48.788552],
          [-104.048569, 48.797052],
          [-104.0489, 48.847387],
          [-104.048652, 48.865734],
          [-104.048824, 48.867539],
          [-104.048883, 48.874008],
          [-104.048893, 48.875739],
          [-104.048719, 48.879921],
          [-104.048643, 48.902609],
          [-104.048746, 48.906858],
          [-104.048744, 48.912113],
          [-104.048807, 48.933636],
          [-104.048701, 48.940331],
          [-104.04877, 48.943301],
          [-104.048872, 48.94963],
          [-104.048698, 48.951823],
          [-104.048627, 48.957124],
          [-104.0488, 48.958997],
          [-104.048555, 48.963772],
          [-104.048616, 48.966736],
          [-104.048478, 48.987007],
          [-104.048736, 48.999877],
          [-103.992467, 48.999567],
          [-103.988925, 48.99958],
          [-103.983786, 48.999604],
          [-103.982361, 48.999615],
          [-103.980868, 48.999581],
          [-103.976459, 48.999605],
          [-103.969479, 48.999525],
          [-103.968611, 48.999525],
          [-103.923261, 48.999562],
          [-103.921976, 48.999551],
          [-103.918921, 48.999551],
          [-103.917428, 48.999585],
          [-103.876905, 48.999544],
          [-103.865336, 48.999591],
          [-103.862738, 48.999639],
          [-103.858363, 48.999606],
          [-103.856072, 48.999572],
          [-103.852287, 48.999561],
          [-102.938793, 48.998998],
          [-102.939932, 48.720469],
          [-102.885775, 48.633298]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38033",
      state: "38",
      county: "033",
      name: "Golden Valley County",
      censusArea: 1000.786,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.045335, 46.540929],
          [-104.045271, 46.641443],
          [-104.045271, 46.641449],
          [-104.045474, 46.708738],
          [-104.045572, 46.713881],
          [-104.04537, 46.721332],
          [-104.045403, 46.722177],
          [-104.045402, 46.725423],
          [-104.045542, 46.933887],
          [-104.045535, 46.934009],
          [-104.045566, 46.941231],
          [-104.045076, 47.037589],
          [-104.045052, 47.040863],
          [-104.045195, 47.053639],
          [-104.045227, 47.057502],
          [-104.045259, 47.063901],
          [-104.045354, 47.078574],
          [-104.045018, 47.081202],
          [-104.045081, 47.092813],
          [-104.045159, 47.263874],
          [-104.045091, 47.265953],
          [-104.045057, 47.266868],
          [-104.045088, 47.271406],
          [-104.045155, 47.27393],
          [-104.045121, 47.276969],
          [-104.045307, 47.330128],
          [-103.666723, 47.329354],
          [-103.666986, 46.979789],
          [-103.609545, 46.979902],
          [-103.60921, 46.629797],
          [-103.800825, 46.629563],
          [-103.800881, 46.540747],
          [-104.045335, 46.540929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38049",
      state: "38",
      county: "049",
      name: "Mchenry County",
      censusArea: 1873.954,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.971325, 47.848703],
          [-100.970497, 48.022645],
          [-101.015024, 48.022739],
          [-101.014138, 48.371432],
          [-101.059802, 48.458448],
          [-101.060047, 48.458615],
          [-101.05966, 48.545337],
          [-101.059624, 48.632424],
          [-100.40678, 48.631878],
          [-100.406388, 48.544657],
          [-100.27666, 48.544813],
          [-100.276401, 48.371334],
          [-100.237332, 48.371246],
          [-100.237092, 48.021866],
          [-100.197558, 48.021857],
          [-100.197996, 47.8476],
          [-100.585161, 47.84748],
          [-100.971325, 47.848703]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28047",
      state: "28",
      county: "047",
      name: "Harrison County",
      censusArea: 573.994,
      stateName: "Mississippi"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-89.095623, 30.231767],
            [-89.077259, 30.23168],
            [-89.067128, 30.250199],
            [-89.063989, 30.246299],
            [-89.065097, 30.239929],
            [-89.073538, 30.223318],
            [-89.091469, 30.202305],
            [-89.118222, 30.223343],
            [-89.156738, 30.230699],
            [-89.095623, 30.231767]
          ]
        ],
        [
          [
            [-88.90037, 30.224576],
            [-88.909752, 30.220405],
            [-88.945498, 30.209646],
            [-88.974672, 30.207391],
            [-88.980239, 30.207962],
            [-88.984249, 30.21032],
            [-88.982219, 30.211627],
            [-88.976811, 30.210987],
            [-88.947872, 30.214179],
            [-88.920511, 30.220578],
            [-88.908885, 30.225437],
            [-88.889797, 30.239665],
            [-88.877824, 30.242442],
            [-88.87366, 30.241748],
            [-88.90037, 30.224576]
          ]
        ],
        [
          [
            [-88.884533, 30.677292],
            [-88.88313, 30.418798],
            [-88.845609, 30.405266],
            [-88.857828, 30.392898],
            [-88.851442, 30.375355],
            [-88.880526, 30.3866],
            [-88.883789, 30.389172],
            [-88.884829, 30.391998],
            [-88.89393, 30.393398],
            [-88.922031, 30.393798],
            [-88.971233, 30.390798],
            [-89.016334, 30.383898],
            [-89.083237, 30.368097],
            [-89.18684, 30.331197],
            [-89.220442, 30.322497],
            [-89.271833, 30.305491],
            [-89.285744, 30.303097],
            [-89.291444, 30.303296],
            [-89.294444, 30.307596],
            [-89.291844, 30.328096],
            [-89.287844, 30.333196],
            [-89.281564, 30.33198],
            [-89.279818, 30.34979],
            [-89.292499, 30.365635],
            [-89.315067, 30.375408],
            [-89.335942, 30.374016],
            [-89.340947, 30.372896],
            [-89.341446, 30.64772],
            [-89.240073, 30.67716],
            [-88.884533, 30.677292]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28105",
      state: "28",
      county: "105",
      name: "Oktibbeha County",
      censusArea: 458.201,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.813053, 33.286424],
          [-88.988471, 33.286087],
          [-89.088255, 33.286003],
          [-89.088446, 33.533202],
          [-89.088263, 33.562039],
          [-89.018613, 33.562114],
          [-88.671526, 33.565135],
          [-88.67125, 33.506168],
          [-88.669076, 33.286922],
          [-88.813053, 33.286424]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28111",
      state: "28",
      county: "111",
      name: "Perry County",
      censusArea: 647.252,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.841707, 31.433703],
          [-88.840929, 30.99841],
          [-88.834339, 30.997983],
          [-88.834475, 30.910323],
          [-88.885038, 30.910788],
          [-89.137967, 30.909877],
          [-89.146092, 31.434052],
          [-88.943468, 31.433458],
          [-88.841707, 31.433703]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28113",
      state: "28",
      county: "113",
      name: "Pike County",
      censusArea: 409.015,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.369371, 31.000335],
          [-90.422117, 30.99981],
          [-90.426849, 30.999776],
          [-90.437351, 30.99973],
          [-90.441725, 30.999729],
          [-90.442479, 30.999722],
          [-90.474094, 30.999798],
          [-90.475928, 30.99974],
          [-90.477284, 30.999717],
          [-90.485876, 30.99974],
          [-90.486749, 30.999693],
          [-90.547615, 30.999723],
          [-90.548199, 31.349574],
          [-90.260391, 31.350274],
          [-90.259555, 31.000657],
          [-90.346007, 31.000363],
          [-90.34723, 31.000359],
          [-90.347241, 31.000359],
          [-90.369371, 31.000335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28125",
      state: "28",
      county: "125",
      name: "Sharkey County",
      censusArea: 431.72,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.701845, 33.096294],
          [-90.658049, 33.096514],
          [-90.656632, 32.92251],
          [-90.759753, 32.835763],
          [-90.694631, 32.815642],
          [-90.742634, 32.729225],
          [-90.664408, 32.689129],
          [-90.723159, 32.662128],
          [-90.964864, 32.662798],
          [-90.963753, 32.838133],
          [-90.914337, 33.007697],
          [-90.914465, 33.097417],
          [-90.701845, 33.096294]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28131",
      state: "28",
      county: "131",
      name: "Stone County",
      censusArea: 445.485,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.884534, 30.735591],
          [-88.884536, 30.714622],
          [-88.884478, 30.71377],
          [-88.884504, 30.713175],
          [-88.884533, 30.677292],
          [-89.240073, 30.67716],
          [-89.341446, 30.64772],
          [-89.340806, 30.909912],
          [-89.137967, 30.909877],
          [-88.885038, 30.910788],
          [-88.884534, 30.735591]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35047",
      state: "35",
      county: "047",
      name: "San Miguel County",
      censusArea: 4715.821,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.290884, 35.042093],
          [-105.714475, 35.041562],
          [-105.72028, 35.871015],
          [-105.34783, 35.870695],
          [-105.110049, 35.807904],
          [-105.025454, 35.754197],
          [-105.018811, 35.769439],
          [-104.367447, 35.781428],
          [-104.021315, 35.78148],
          [-103.976867, 35.801694],
          [-103.724585, 35.422223],
          [-103.637147, 35.389722],
          [-103.637057, 35.240881],
          [-103.857529, 35.242199],
          [-103.857698, 35.264082],
          [-104.124841, 35.142183],
          [-104.124816, 35.2156],
          [-105.291355, 35.216345],
          [-105.290884, 35.042093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35051",
      state: "35",
      county: "051",
      name: "Sierra County",
      censusArea: 4178.956,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.299463, 32.605534],
          [-107.608283, 32.605382],
          [-107.736949, 32.629494],
          [-107.716186, 32.762676],
          [-107.773088, 32.853775],
          [-107.780548, 32.952776],
          [-107.856672, 33.002539],
          [-107.836192, 33.067763],
          [-107.858292, 33.200879],
          [-108.000597, 33.201563],
          [-108.000602, 33.478014],
          [-107.712713, 33.476892],
          [-106.474295, 33.475786],
          [-106.373082, 33.480116],
          [-106.372235, 33.390883],
          [-106.345851, 33.390815],
          [-106.340473, 33.052795],
          [-106.887859, 32.822844],
          [-106.887733, 32.779311],
          [-107.299371, 32.780603],
          [-107.299463, 32.605534]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35055",
      state: "35",
      county: "055",
      name: "Taos County",
      censusArea: 2203.11,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.220613, 36.995169],
          [-105.230632, 36.723784],
          [-105.311842, 36.724471],
          [-105.370638, 36.681032],
          [-105.377745, 36.552096],
          [-105.318718, 36.491693],
          [-105.356826, 36.449504],
          [-105.327301, 36.40543],
          [-105.340907, 36.262245],
          [-105.409677, 36.236814],
          [-105.419992, 36.131704],
          [-105.53038, 36.013014],
          [-105.735883, 36.100133],
          [-105.856947, 36.230434],
          [-106.058364, 36.296978],
          [-105.959812, 36.35442],
          [-105.938754, 36.462402],
          [-105.972491, 36.648088],
          [-105.984436, 36.812065],
          [-105.971889, 36.886247],
          [-106.006634, 36.995343],
          [-105.997472, 36.995417],
          [-105.996159, 36.995418],
          [-105.71847, 36.995846],
          [-105.716471, 36.995849],
          [-105.66472, 36.995874],
          [-105.62747, 36.995679],
          [-105.533922, 36.995875],
          [-105.512485, 36.995777],
          [-105.508836, 36.995895],
          [-105.465182, 36.995991],
          [-105.447255, 36.996017],
          [-105.442459, 36.995994],
          [-105.41931, 36.995856],
          [-105.220613, 36.995169]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36001",
      state: "36",
      county: "001",
      name: "Albany County",
      censusArea: 522.804,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.809369, 42.778869],
          [-73.722663, 42.820677],
          [-73.719863, 42.801277],
          [-73.688362, 42.775477],
          [-73.676762, 42.783277],
          [-73.761265, 42.610379],
          [-73.773161, 42.509377],
          [-73.784594, 42.489947],
          [-73.783721, 42.464231],
          [-74.254303, 42.408207],
          [-74.241572, 42.550802],
          [-74.169725, 42.667426],
          [-74.180274, 42.729979],
          [-73.809603, 42.775016],
          [-73.809369, 42.778869]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36005",
      state: "36",
      county: "005",
      name: "Bronx County",
      censusArea: 42.096,
      stateName: "New York"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-73.773361, 40.859449],
            [-73.770552, 40.86033],
            [-73.766333, 40.857317],
            [-73.765128, 40.854228],
            [-73.766032, 40.844961],
            [-73.769648, 40.84466],
            [-73.773038, 40.848125],
            [-73.773717, 40.854831],
            [-73.773361, 40.859449]
          ]
        ],
        [
          [
            [-73.785964, 40.800862],
            [-73.793403, 40.799881],
            [-73.811001, 40.805077],
            [-73.830548, 40.803162],
            [-73.862704, 40.797276],
            [-73.866707, 40.793653],
            [-73.870992, 40.786979],
            [-73.884867, 40.786621],
            [-73.889918, 40.787398],
            [-73.890586, 40.790145],
            [-73.892205, 40.79038],
            [-73.899809, 40.792524],
            [-73.912506, 40.79618],
            [-73.907, 40.873455],
            [-73.907105, 40.876277],
            [-73.911405, 40.879278],
            [-73.9152, 40.875581],
            [-73.933408, 40.882075],
            [-73.933406, 40.882078],
            [-73.92747, 40.895682],
            [-73.919705, 40.913478],
            [-73.917905, 40.917577],
            [-73.824047, 40.889866],
            [-73.823557, 40.889865],
            [-73.823617, 40.890413],
            [-73.82312, 40.890648],
            [-73.823244, 40.891199],
            [-73.806395, 40.886801],
            [-73.804789, 40.886505],
            [-73.783545, 40.88104],
            [-73.784803, 40.878528],
            [-73.785502, 40.869079],
            [-73.788786, 40.858485],
            [-73.78806, 40.854131],
            [-73.784754, 40.851793],
            [-73.782174, 40.847358],
            [-73.782093, 40.844616],
            [-73.782254, 40.842359],
            [-73.781206, 40.838891],
            [-73.782577, 40.837601],
            [-73.783867, 40.836795],
            [-73.785399, 40.838004],
            [-73.788221, 40.842036],
            [-73.791044, 40.846552],
            [-73.789512, 40.85139],
            [-73.792253, 40.855825],
            [-73.793785, 40.855583],
            [-73.797252, 40.852196],
            [-73.799543, 40.848027],
            [-73.806914, 40.849501],
            [-73.81281, 40.846737],
            [-73.815574, 40.835129],
            [-73.815205, 40.831075],
            [-73.811889, 40.825363],
            [-73.804518, 40.818546],
            [-73.797332, 40.815597],
            [-73.785964, 40.800862]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42043",
      state: "42",
      county: "043",
      name: "Dauphin County",
      censusArea: 525.047,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.91497, 40.328484],
          [-76.91727, 40.332715],
          [-76.92618, 40.34098],
          [-76.928521, 40.347985],
          [-76.933589, 40.356045],
          [-77.028645, 40.391197],
          [-76.949397, 40.469037],
          [-76.991939, 40.561475],
          [-76.983673, 40.578259],
          [-76.957444, 40.594677],
          [-76.949414, 40.628163],
          [-76.890875, 40.610096],
          [-76.701624, 40.658082],
          [-76.701566, 40.658048],
          [-76.701507, 40.658014],
          [-76.700933, 40.65768],
          [-76.698863, 40.656477],
          [-76.698173, 40.656076],
          [-76.696916, 40.655321],
          [-76.693145, 40.653057],
          [-76.691889, 40.652303],
          [-76.691603, 40.652118],
          [-76.690746, 40.651565],
          [-76.69056, 40.651445],
          [-76.690461, 40.651381],
          [-76.690418, 40.651353],
          [-76.690398, 40.65134],
          [-76.690289, 40.651271],
          [-76.690247, 40.651244],
          [-76.690172, 40.651195],
          [-76.68995, 40.651051],
          [-76.689876, 40.651003],
          [-76.687847, 40.649693],
          [-76.685028, 40.647873],
          [-76.681741, 40.645797],
          [-76.6797, 40.644509],
          [-76.678351, 40.643656],
          [-76.674304, 40.6411],
          [-76.673096, 40.640337],
          [-76.673026, 40.640292],
          [-76.672956, 40.640248],
          [-76.672894, 40.640208],
          [-76.672833, 40.640169],
          [-76.671471, 40.639296],
          [-76.668823, 40.637599],
          [-76.666982, 40.636502],
          [-76.665469, 40.635601],
          [-76.665355, 40.635533],
          [-76.665083, 40.635359],
          [-76.663935, 40.634624],
          [-76.663554, 40.63438],
          [-76.663552, 40.634379],
          [-76.662765, 40.633898],
          [-76.660405, 40.632455],
          [-76.659618, 40.631975],
          [-76.658169, 40.631089],
          [-76.655755, 40.629613],
          [-76.653822, 40.628431],
          [-76.652374, 40.627546],
          [-76.650217, 40.626228],
          [-76.648348, 40.625086],
          [-76.64373, 40.622304],
          [-76.641566, 40.621001],
          [-76.641488, 40.620955],
          [-76.641433, 40.620922],
          [-76.641259, 40.620812],
          [-76.641183, 40.620765],
          [-76.641162, 40.620752],
          [-76.641102, 40.620714],
          [-76.641082, 40.620702],
          [-76.64078, 40.620513],
          [-76.639877, 40.619948],
          [-76.639697, 40.619836],
          [-76.639576, 40.61976],
          [-76.637041, 40.618175],
          [-76.629437, 40.61342],
          [-76.626903, 40.611835],
          [-76.626499, 40.611582],
          [-76.625287, 40.610824],
          [-76.624883, 40.610572],
          [-76.624686, 40.610449],
          [-76.623653, 40.609799],
          [-76.622898, 40.609324],
          [-76.619966, 40.607481],
          [-76.618738, 40.606709],
          [-76.618353, 40.606466],
          [-76.617203, 40.605739],
          [-76.616824, 40.6055],
          [-76.616819, 40.605497],
          [-76.616703, 40.605423],
          [-76.616465, 40.605273],
          [-76.615405, 40.604603],
          [-76.615053, 40.60438],
          [-76.61461, 40.6041],
          [-76.614473, 40.604007],
          [-76.612764, 40.602846],
          [-76.612414, 40.602609],
          [-76.612186, 40.602473],
          [-76.612146, 40.602449],
          [-76.612026, 40.602377],
          [-76.611987, 40.602354],
          [-76.609607, 40.600932],
          [-76.609284, 40.600739],
          [-76.607903, 40.599915],
          [-76.601283, 40.595723],
          [-76.600084, 40.594964],
          [-76.598611, 40.594061],
          [-76.598325, 40.593885],
          [-76.597468, 40.59336],
          [-76.597183, 40.593185],
          [-76.596992, 40.593064],
          [-76.596422, 40.592701],
          [-76.596232, 40.59258],
          [-76.59427, 40.591333],
          [-76.588384, 40.587592],
          [-76.586423, 40.586345],
          [-76.585822, 40.585963],
          [-76.58448, 40.58511],
          [-76.584014, 40.584831],
          [-76.583404, 40.584466],
          [-76.583115, 40.584293],
          [-76.58225, 40.583775],
          [-76.581962, 40.583603],
          [-76.581662, 40.583423],
          [-76.580763, 40.582886],
          [-76.580464, 40.582707],
          [-76.579789, 40.582303],
          [-76.577765, 40.581091],
          [-76.577091, 40.580688],
          [-76.577073, 40.580677],
          [-76.575558, 40.57977],
          [-76.575216, 40.579566],
          [-76.571818, 40.577533],
          [-76.570965, 40.577007],
          [-76.56948, 40.576092],
          [-76.569445, 40.57607],
          [-76.568682, 40.575599],
          [-76.566393, 40.574189],
          [-76.565631, 40.573719],
          [-76.565486, 40.573629],
          [-76.565051, 40.573361],
          [-76.564906, 40.573272],
          [-76.564446, 40.572989],
          [-76.563068, 40.572141],
          [-76.562649, 40.571883],
          [-76.562609, 40.571859],
          [-76.560911, 40.570815],
          [-76.560743, 40.570712],
          [-76.556354, 40.568014],
          [-76.555148, 40.567273],
          [-76.553283, 40.566127],
          [-76.552318, 40.565533],
          [-76.549423, 40.563753],
          [-76.54846, 40.56316],
          [-76.548127, 40.562955],
          [-76.547134, 40.562344],
          [-76.546803, 40.562141],
          [-76.54514, 40.561118],
          [-76.545071, 40.561076],
          [-76.543218, 40.559937],
          [-76.539858, 40.557915],
          [-76.538117, 40.556868],
          [-76.537576, 40.556525],
          [-76.535954, 40.555499],
          [-76.535414, 40.555157],
          [-76.67804, 40.474717],
          [-76.566339, 40.196644],
          [-76.72162, 40.12007],
          [-76.859023, 40.226328],
          [-76.91497, 40.328484]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42059",
      state: "42",
      county: "059",
      name: "Greene County",
      censusArea: 575.949,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.916266, 39.720792],
          [-80.308651, 39.721283],
          [-80.309457, 39.721264],
          [-80.421388, 39.721189],
          [-80.519342, 39.721403],
          [-80.519248, 39.936967],
          [-80.519115, 39.939188],
          [-80.519175, 39.956648],
          [-80.519203, 39.959394],
          [-80.519217, 39.962199],
          [-80.379505, 39.994125],
          [-80.157339, 39.999581],
          [-79.998014, 39.983322],
          [-79.915278, 39.8803],
          [-79.916266, 39.720792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42065",
      state: "42",
      county: "065",
      name: "Jefferson County",
      censusArea: 652.429,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.208878, 41.331861],
          [-79.095096, 41.340658],
          [-78.974478, 41.375476],
          [-78.73528, 41.262915],
          [-78.710248, 41.201859],
          [-78.804532, 41.132186],
          [-78.805167, 40.90598],
          [-79.21531, 40.911346],
          [-79.215214, 41.050515],
          [-79.208878, 41.331861]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42071",
      state: "42",
      county: "071",
      name: "Lancaster County",
      censusArea: 943.81,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.091764, 40.277692],
          [-75.875162, 40.138071],
          [-75.873375, 40.137107],
          [-75.942997, 40.109266],
          [-75.985056, 39.937871],
          [-75.991644, 39.868713],
          [-76.066685, 39.759969],
          [-76.135584, 39.721556],
          [-76.224191, 39.721328],
          [-76.233259, 39.721305],
          [-76.233277, 39.721305],
          [-76.239805, 39.721305],
          [-76.47358, 39.947352],
          [-76.534524, 40.051348],
          [-76.663825, 40.063227],
          [-76.72162, 40.12007],
          [-76.566339, 40.196644],
          [-76.307578, 40.255066],
          [-76.151215, 40.315971],
          [-76.149509, 40.314454],
          [-76.091764, 40.277692]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42077",
      state: "42",
      county: "077",
      name: "Lehigh County",
      censusArea: 345.166,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.694308, 40.551149],
          [-75.738038, 40.579105],
          [-75.739442, 40.57994],
          [-75.76674, 40.597033],
          [-75.891473, 40.67727],
          [-75.757807, 40.735414],
          [-75.608985, 40.787387],
          [-75.481469, 40.655744],
          [-75.430404, 40.674152],
          [-75.404412, 40.581154],
          [-75.333514, 40.537057],
          [-75.338633, 40.534068],
          [-75.36661, 40.515424],
          [-75.367911, 40.514601],
          [-75.409733, 40.487984],
          [-75.484057, 40.418451],
          [-75.529694, 40.446995],
          [-75.682238, 40.543638],
          [-75.694308, 40.551149]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42083",
      state: "42",
      county: "083",
      name: "Mc Kean County",
      censusArea: 979.197,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.419118, 41.602188],
          [-78.483409, 41.629898],
          [-78.956056, 41.623863],
          [-78.948689, 41.83048],
          [-78.918854, 41.997961],
          [-78.874759, 41.997559],
          [-78.59665, 41.999877],
          [-78.308128, 41.999415],
          [-78.206606, 41.999989],
          [-78.203422, 41.618157],
          [-78.419299, 41.616853],
          [-78.419118, 41.602188]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41035",
      state: "41",
      county: "035",
      name: "Klamath County",
      censusArea: 5941.052,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.132044, 43.440445],
          [-122.130944, 43.557149],
          [-122.002675, 43.615228],
          [-121.332982, 43.616629],
          [-121.332708, 43.43857],
          [-121.348576, 43.356691],
          [-121.34988, 42.746671],
          [-120.88334, 42.743883],
          [-120.879481, 41.993781],
          [-121.035195, 41.993323],
          [-121.154347, 41.996352],
          [-121.334385, 41.996655],
          [-121.335734, 41.996518],
          [-121.340517, 41.99622],
          [-121.360253, 41.99668],
          [-121.376101, 41.997026],
          [-121.434977, 41.997022],
          [-121.43961, 41.99708],
          [-121.44754, 41.997169],
          [-121.52025, 41.997983],
          [-121.580865, 41.998668],
          [-121.675348, 42.000351],
          [-121.689159, 42.000584],
          [-121.705045, 42.000766],
          [-121.708199, 42.000815],
          [-122.001119, 42.004017],
          [-122.155408, 42.007429],
          [-122.156666, 42.007384],
          [-122.160438, 42.007637],
          [-122.161328, 42.007637],
          [-122.289527, 42.007764],
          [-122.289533, 42.007764],
          [-122.28974, 42.122561],
          [-122.290351, 42.127092],
          [-122.287446, 42.127168],
          [-122.281856, 42.996556],
          [-122.282245, 43.067547],
          [-122.091539, 43.07605],
          [-122.037839, 43.21725],
          [-121.973841, 43.261728],
          [-122.010239, 43.343249],
          [-122.132044, 43.440445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23005",
      state: "23",
      county: "005",
      name: "Cumberland County",
      censusArea: 835.241,
      stateName: "Maine"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-70.119671, 43.748621],
            [-70.113059, 43.74913],
            [-70.097318, 43.757292],
            [-70.094986, 43.753211],
            [-70.100233, 43.742134],
            [-70.107812, 43.734555],
            [-70.109561, 43.730474],
            [-70.108978, 43.722312],
            [-70.124136, 43.70832],
            [-70.129383, 43.70832],
            [-70.138128, 43.718231],
            [-70.138711, 43.727559],
            [-70.127051, 43.742717],
            [-70.119671, 43.748621]
          ]
        ],
        [
          [
            [-70.171245, 43.663498],
            [-70.171136, 43.6633],
            [-70.190323, 43.642986],
            [-70.205934, 43.633633],
            [-70.207165, 43.63369],
            [-70.211062, 43.641842],
            [-70.200116, 43.662978],
            [-70.188047, 43.673762],
            [-70.184659, 43.674699],
            [-70.171245, 43.663498]
          ]
        ],
        [
          [
            [-70.186213, 43.682655],
            [-70.187536, 43.678669],
            [-70.192574, 43.673139],
            [-70.207341, 43.662562],
            [-70.210825, 43.661695],
            [-70.21313, 43.662973],
            [-70.213948, 43.666161],
            [-70.209627, 43.676308],
            [-70.201893, 43.685483],
            [-70.196535, 43.688784],
            [-70.191041, 43.689071],
            [-70.186213, 43.682655]
          ]
        ],
        [
          [
            [-70.163884, 43.692404],
            [-70.156787, 43.694706],
            [-70.146115, 43.701635],
            [-70.135563, 43.700658],
            [-70.154503, 43.680933],
            [-70.168227, 43.675136],
            [-70.170284, 43.675441],
            [-70.173571, 43.683734],
            [-70.171339, 43.687546],
            [-70.163884, 43.692404]
          ]
        ],
        [
          [
            [-70.087621, 43.699913],
            [-70.093704, 43.6918],
            [-70.099594, 43.695366],
            [-70.115908, 43.682978],
            [-70.118291, 43.683343],
            [-70.118174, 43.686375],
            [-70.115961, 43.689202],
            [-70.100683, 43.705962],
            [-70.095727, 43.709278],
            [-70.093113, 43.710524],
            [-70.092137, 43.709468],
            [-70.097184, 43.700929],
            [-70.091929, 43.698111],
            [-70.087621, 43.699913]
          ]
        ],
        [
          [
            [-70.595631, 44.032057],
            [-70.480078, 44.032078],
            [-70.363874, 43.986445],
            [-70.315374, 44.037807],
            [-70.229244, 43.974064],
            [-70.080839, 43.926458],
            [-70.034238, 43.975661],
            [-69.976226, 43.915276],
            [-69.857032, 43.944742],
            [-69.88739, 43.777052],
            [-69.903164, 43.77239],
            [-69.915593, 43.775112],
            [-69.927011, 43.780174],
            [-69.948539, 43.765948],
            [-69.953246, 43.768806],
            [-69.958056, 43.767786],
            [-69.982574, 43.750801],
            [-69.989131, 43.743227],
            [-69.994479, 43.728451],
            [-69.992396, 43.726852],
            [-69.992615, 43.724793],
            [-70.001645, 43.717666],
            [-70.006954, 43.717065],
            [-70.005205, 43.727559],
            [-70.001125, 43.733389],
            [-69.998793, 43.740385],
            [-70.001708, 43.744466],
            [-70.041351, 43.738053],
            [-70.040768, 43.745049],
            [-70.034355, 43.759041],
            [-70.02561, 43.769534],
            [-70.005205, 43.787607],
            [-69.99821, 43.798684],
            [-69.999376, 43.805097],
            [-70.002874, 43.812093],
            [-70.011035, 43.810927],
            [-70.026193, 43.822587],
            [-70.026193, 43.829],
            [-70.023278, 43.834247],
            [-70.006954, 43.844158],
            [-70.002874, 43.848239],
            [-70.002874, 43.852903],
            [-70.009869, 43.859315],
            [-70.019197, 43.858733],
            [-70.022845, 43.856245],
            [-70.032023, 43.849988],
            [-70.053594, 43.828417],
            [-70.053011, 43.821421],
            [-70.064671, 43.813259],
            [-70.06642, 43.819672],
            [-70.080995, 43.819672],
            [-70.107229, 43.809178],
            [-70.142792, 43.791688],
            [-70.153869, 43.781194],
            [-70.153869, 43.774781],
            [-70.176023, 43.76079],
            [-70.177772, 43.764871],
            [-70.172525, 43.773615],
            [-70.17544, 43.777113],
            [-70.190014, 43.771866],
            [-70.194678, 43.766037],
            [-70.197593, 43.753211],
            [-70.194095, 43.745632],
            [-70.194678, 43.742134],
            [-70.217998, 43.71998],
            [-70.219164, 43.715899],
            [-70.215666, 43.707737],
            [-70.216832, 43.704822],
            [-70.227909, 43.701907],
            [-70.23199, 43.704822],
            [-70.251812, 43.683251],
            [-70.254144, 43.676839],
            [-70.252961, 43.67501],
            [-70.247321, 43.671973],
            [-70.242289, 43.669544],
            [-70.239512, 43.665986],
            [-70.240119, 43.664685],
            [-70.241942, 43.663296],
            [-70.240987, 43.659132],
            [-70.22299, 43.639023],
            [-70.211204, 43.625765],
            [-70.217087, 43.596717],
            [-70.214369, 43.590445],
            [-70.20112, 43.586515],
            [-70.196911, 43.565146],
            [-70.206123, 43.557627],
            [-70.216782, 43.556874],
            [-70.219784, 43.562149],
            [-70.231963, 43.561118],
            [-70.244331, 43.551849],
            [-70.261917, 43.553687],
            [-70.272497, 43.562616],
            [-70.299184, 43.550589],
            [-70.307764, 43.544315],
            [-70.341793, 43.540484],
            [-70.352826, 43.535855],
            [-70.353392, 43.535405],
            [-70.492009, 43.606769],
            [-70.457743, 43.642372],
            [-70.549965, 43.715796],
            [-70.659622, 43.710902],
            [-70.652986, 43.787544],
            [-70.782984, 43.814147],
            [-70.72978, 43.92833],
            [-70.747507, 43.945901],
            [-70.715418, 43.961815],
            [-70.821803, 44.077089],
            [-70.624329, 44.171036],
            [-70.587331, 44.122516],
            [-70.595631, 44.032057]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45029",
      state: "45",
      county: "029",
      name: "Colleton County",
      censusArea: 1056.491,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.47691, 32.48539],
          [-80.551504, 32.565769],
          [-80.659751, 32.612683],
          [-80.66774, 32.665535],
          [-80.826309, 32.704321],
          [-80.900196, 32.832813],
          [-81.082291, 33.02663],
          [-80.797912, 33.176944],
          [-80.795864, 33.17693],
          [-80.793711, 33.178648],
          [-80.793002, 33.17984],
          [-80.791667, 33.181077],
          [-80.790296, 33.18084],
          [-80.709736, 33.15647],
          [-80.6221, 33.06608],
          [-80.390533, 33.043909],
          [-80.40134, 32.858466],
          [-80.446077, 32.727477],
          [-80.389955, 32.647906],
          [-80.416586, 32.546318],
          [-80.352823, 32.500187],
          [-80.249566, 32.529584],
          [-80.277681, 32.516161],
          [-80.332438, 32.478104],
          [-80.338354, 32.47873],
          [-80.343883, 32.490795],
          [-80.363956, 32.496098],
          [-80.380716, 32.486359],
          [-80.386827, 32.47881],
          [-80.392561, 32.475332],
          [-80.413487, 32.470672],
          [-80.415943, 32.472074],
          [-80.417896, 32.476076],
          [-80.418502, 32.490894],
          [-80.423454, 32.497989],
          [-80.439407, 32.503472],
          [-80.452078, 32.497286],
          [-80.46571, 32.4953],
          [-80.472068, 32.496964],
          [-80.47691, 32.48539]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45041",
      state: "45",
      county: "041",
      name: "Florence County",
      censusArea: 799.964,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.544036, 34.219676],
          [-79.568984, 34.175087],
          [-79.515308, 34.097207],
          [-79.482549, 33.937537],
          [-79.324317, 33.799123],
          [-79.496486, 33.775357],
          [-79.79837, 33.835562],
          [-79.87562, 33.885245],
          [-79.945584, 33.885717],
          [-79.974601, 33.946527],
          [-79.893517, 33.988017],
          [-80.000594, 34.047877],
          [-80.072808, 34.086013],
          [-79.999789, 34.129007],
          [-79.883065, 34.204112],
          [-79.657764, 34.30519],
          [-79.644463, 34.30119],
          [-79.641751, 34.299762],
          [-79.635373, 34.301299],
          [-79.633413, 34.297994],
          [-79.54793, 34.230913],
          [-79.54404, 34.225186],
          [-79.544036, 34.219676]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45043",
      state: "45",
      county: "043",
      name: "Georgetown County",
      censusArea: 813.547,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.235068, 33.69425],
          [-79.208955, 33.703126],
          [-79.195161, 33.703438],
          [-79.199432, 33.710101],
          [-79.190264, 33.705787],
          [-79.100653, 33.571807],
          [-79.002109, 33.572114],
          [-79.007356, 33.566565],
          [-79.028516, 33.533365],
          [-79.041125, 33.523773],
          [-79.084588, 33.483669],
          [-79.10136, 33.461016],
          [-79.135441, 33.403867],
          [-79.147496, 33.378243],
          [-79.152035, 33.350925],
          [-79.158429, 33.332811],
          [-79.162332, 33.327246],
          [-79.180318, 33.254141],
          [-79.180563, 33.237955],
          [-79.172394, 33.206577],
          [-79.18787, 33.173712],
          [-79.195631, 33.166016],
          [-79.215453, 33.155569],
          [-79.238262, 33.137055],
          [-79.24609, 33.124865],
          [-79.290754, 33.110051],
          [-79.268762, 33.136043],
          [-79.404858, 33.184929],
          [-79.413588, 33.198159],
          [-79.438387, 33.215136],
          [-79.446699, 33.213458],
          [-79.677014, 33.304944],
          [-79.53808, 33.496896],
          [-79.435733, 33.572702],
          [-79.409332, 33.68889],
          [-79.317041, 33.779878],
          [-79.316294, 33.778554],
          [-79.313573, 33.775846],
          [-79.30883, 33.771973],
          [-79.306679, 33.76623],
          [-79.30468, 33.763449],
          [-79.300613, 33.75883],
          [-79.298706, 33.757025],
          [-79.294779, 33.754324],
          [-79.293312, 33.75228],
          [-79.292578, 33.750167],
          [-79.29291, 33.747135],
          [-79.294366, 33.743757],
          [-79.293978, 33.741144],
          [-79.290436, 33.739116],
          [-79.286248, 33.737386],
          [-79.286157, 33.735701],
          [-79.289351, 33.734349],
          [-79.29139, 33.732392],
          [-79.291574, 33.728981],
          [-79.288312, 33.723526],
          [-79.28717, 33.718807],
          [-79.286441, 33.717597],
          [-79.282995, 33.715972],
          [-79.280518, 33.716324],
          [-79.278604, 33.718226],
          [-79.271664, 33.721855],
          [-79.269597, 33.719893],
          [-79.269411, 33.717788],
          [-79.271326, 33.71571],
          [-79.274935, 33.714319],
          [-79.274694, 33.711077],
          [-79.275057, 33.708852],
          [-79.273306, 33.708116],
          [-79.271676, 33.708557],
          [-79.26925, 33.710893],
          [-79.266927, 33.710979],
          [-79.265011, 33.710275],
          [-79.264683, 33.707364],
          [-79.265102, 33.706001],
          [-79.267863, 33.704375],
          [-79.267586, 33.703399],
          [-79.261467, 33.703617],
          [-79.258107, 33.702742],
          [-79.24977, 33.703424],
          [-79.244897, 33.705717],
          [-79.243348, 33.706114],
          [-79.241463, 33.705397],
          [-79.240279, 33.703728],
          [-79.240644, 33.702598],
          [-79.242086, 33.701515],
          [-79.247339, 33.700817],
          [-79.249581, 33.699302],
          [-79.249309, 33.696975],
          [-79.246384, 33.695347],
          [-79.23888, 33.694417],
          [-79.235068, 33.69425]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47023",
      state: "47",
      county: "023",
      name: "Chester County",
      censusArea: 285.736,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.613611, 35.588089],
          [-88.505354, 35.51203],
          [-88.364004, 35.492801],
          [-88.360829, 35.418972],
          [-88.361521, 35.408877],
          [-88.351765, 35.406735],
          [-88.3555, 35.396994],
          [-88.361929, 35.397044],
          [-88.362572, 35.380994],
          [-88.640934, 35.361017],
          [-88.719761, 35.24755],
          [-88.781768, 35.247587],
          [-88.841607, 35.427826],
          [-88.675867, 35.489163],
          [-88.613611, 35.588089]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38051",
      state: "38",
      county: "051",
      name: "Mcintosh County",
      censusArea: 974.733,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.718071, 45.940907],
          [-99.718073, 45.940907],
          [-99.74787, 45.940933],
          [-99.749325, 45.940935],
          [-99.749494, 45.940956],
          [-99.750396, 45.940935],
          [-99.83868, 45.941293],
          [-99.880062, 45.94167],
          [-99.878431, 46.282811],
          [-99.037367, 46.283151],
          [-99.003118, 46.282898],
          [-99.005754, 45.939944],
          [-99.092868, 45.940184],
          [-99.102372, 45.940158],
          [-99.212571, 45.940108],
          [-99.213644, 45.940116],
          [-99.221672, 45.940069],
          [-99.222269, 45.940071],
          [-99.257745, 45.94006],
          [-99.276266, 45.940188],
          [-99.283968, 45.940195],
          [-99.297272, 45.940165],
          [-99.317875, 45.940263],
          [-99.344774, 45.940299],
          [-99.34496, 45.940299],
          [-99.378486, 45.940403],
          [-99.385565, 45.940407],
          [-99.40126, 45.940367],
          [-99.490254, 45.940362],
          [-99.49314, 45.940383],
          [-99.58878, 45.941104],
          [-99.61116, 45.941098],
          [-99.671938, 45.941062],
          [-99.692975, 45.940949],
          [-99.718071, 45.940907]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38057",
      state: "38",
      county: "057",
      name: "Mercer County",
      censusArea: 1042.957,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.764392, 46.981306],
          [-102.096675, 46.981159],
          [-102.144878, 46.981425],
          [-102.144771, 47.010307],
          [-102.144453, 47.328079],
          [-102.205601, 47.328071],
          [-102.20535, 47.574506],
          [-102.036648, 47.572705],
          [-101.826148, 47.494402],
          [-101.707537, 47.538278],
          [-101.641696, 47.528379],
          [-101.428037, 47.561986],
          [-101.370866, 47.518696],
          [-101.440139, 47.477299],
          [-101.362536, 47.367998],
          [-101.345735, 47.291897],
          [-101.257031, 47.264895],
          [-101.257944, 47.241347],
          [-101.764419, 47.241423],
          [-101.764392, 46.981306]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40067",
      state: "40",
      county: "067",
      name: "Jefferson County",
      censusArea: 758.832,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.97781, 33.889894],
          [-97.977859, 33.889929],
          [-97.953395, 33.936445],
          [-97.94573, 33.989839],
          [-97.983521, 34.001559],
          [-98.019485, 33.993804],
          [-98.041117, 33.993456],
          [-98.08526, 34.003259],
          [-98.088203, 34.005481],
          [-98.106261, 34.033696],
          [-98.16912, 34.114171],
          [-98.139072, 34.14187],
          [-98.139226, 34.289775],
          [-97.562575, 34.289182],
          [-97.562616, 34.082921],
          [-97.560764, 34.071089],
          [-97.561054, 33.898006],
          [-97.581041, 33.900396],
          [-97.587441, 33.902479],
          [-97.596289, 33.913769],
          [-97.597115, 33.917868],
          [-97.588828, 33.951882],
          [-97.589598, 33.953554],
          [-97.609091, 33.968093],
          [-97.65621, 33.989488],
          [-97.671772, 33.99137],
          [-97.688023, 33.986607],
          [-97.69311, 33.983699],
          [-97.700477, 33.972246],
          [-97.733723, 33.936392],
          [-97.785317, 33.890701],
          [-97.834333, 33.857671],
          [-97.865765, 33.849393],
          [-97.877387, 33.850236],
          [-97.966706, 33.881949],
          [-97.97781, 33.889894]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40077",
      state: "40",
      county: "077",
      name: "Latimer County",
      censusArea: 722.08,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.927863, 35.058279],
          [-94.927668, 34.942212],
          [-94.998121, 34.942154],
          [-94.99781, 34.855078],
          [-95.059512, 34.855019],
          [-95.059616, 34.680736],
          [-95.514514, 34.681142],
          [-95.507658, 35.029197],
          [-95.349437, 35.029299],
          [-95.349233, 35.058514],
          [-94.927863, 35.058279]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40079",
      state: "40",
      county: "079",
      name: "Le Flore County",
      censusArea: 1589.214,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.441232, 35.119724],
          [-94.447889, 34.933941],
          [-94.449253, 34.895869],
          [-94.449086, 34.894152],
          [-94.449058, 34.890556],
          [-94.44963, 34.875253],
          [-94.450065, 34.861335],
          [-94.45014, 34.858694],
          [-94.450233, 34.855413],
          [-94.454576, 34.728962],
          [-94.45753, 34.642961],
          [-94.4575, 34.634945],
          [-94.460052, 34.547869],
          [-94.460058, 34.545264],
          [-94.461149, 34.507457],
          [-94.937605, 34.506831],
          [-94.937752, 34.680877],
          [-95.059616, 34.680736],
          [-95.059512, 34.855019],
          [-94.99781, 34.855078],
          [-94.998121, 34.942154],
          [-94.927668, 34.942212],
          [-94.927863, 35.058279],
          [-94.927936, 35.202611],
          [-94.814284, 35.202331],
          [-94.813629, 35.323344],
          [-94.750224, 35.356969],
          [-94.668796, 35.295029],
          [-94.594801, 35.327974],
          [-94.493889, 35.301223],
          [-94.433742, 35.386467],
          [-94.432685, 35.380806],
          [-94.431815, 35.362891],
          [-94.434115, 35.306493],
          [-94.43517, 35.291494],
          [-94.43528, 35.287485],
          [-94.435316, 35.275893],
          [-94.435706, 35.274267],
          [-94.435812, 35.2713],
          [-94.437578, 35.242202],
          [-94.437774, 35.239271],
          [-94.438247, 35.210992],
          [-94.43847, 35.208587],
          [-94.439084, 35.197298],
          [-94.439056, 35.193588],
          [-94.439509, 35.171807],
          [-94.43955, 35.169037],
          [-94.440754, 35.128806],
          [-94.441232, 35.119724]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40089",
      state: "40",
      county: "089",
      name: "Mccurtain County",
      censusArea: 1850.005,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.746096, 33.703016],
          [-94.760604, 33.727054],
          [-94.822473, 33.732716],
          [-94.8693, 33.745871],
          [-95.039452, 33.860621],
          [-95.062284, 33.903618],
          [-95.063459, 33.91404],
          [-95.067253, 33.917351],
          [-95.129572, 33.936718],
          [-95.155981, 33.944027],
          [-95.156521, 34.156965],
          [-95.151233, 34.243906],
          [-95.046585, 34.243671],
          [-95.046746, 34.507229],
          [-94.937605, 34.506831],
          [-94.461149, 34.507457],
          [-94.463671, 34.419585],
          [-94.463816, 34.414465],
          [-94.464176, 34.402713],
          [-94.465425, 34.359548],
          [-94.465847, 34.352073],
          [-94.470292, 34.189864],
          [-94.474896, 34.021877],
          [-94.474896, 34.021838],
          [-94.474895, 34.019655],
          [-94.476957, 33.957365],
          [-94.477038, 33.953838],
          [-94.477318, 33.940932],
          [-94.477387, 33.937759],
          [-94.478842, 33.881485],
          [-94.478994, 33.881197],
          [-94.479954, 33.85133],
          [-94.480574, 33.830166],
          [-94.481355, 33.802887],
          [-94.481361, 33.802649],
          [-94.481543, 33.795719],
          [-94.481842, 33.789008],
          [-94.482682, 33.756286],
          [-94.482777, 33.753638],
          [-94.482862, 33.75078],
          [-94.48287, 33.750564],
          [-94.483874, 33.716733],
          [-94.48384, 33.711332],
          [-94.484616, 33.691592],
          [-94.48452, 33.687909],
          [-94.485528, 33.663388],
          [-94.485577, 33.65331],
          [-94.485875, 33.637867],
          [-94.487611, 33.628544],
          [-94.490684, 33.625586],
          [-94.524055, 33.615765],
          [-94.52838, 33.615995],
          [-94.660964, 33.660322],
          [-94.735128, 33.691267],
          [-94.746096, 33.703016]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40095",
      state: "40",
      county: "095",
      name: "Marshall County",
      censusArea: 371.08,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.922114, 33.959579],
          [-96.934508, 33.953748],
          [-96.969627, 34.07102],
          [-96.933671, 34.172553],
          [-96.669965, 34.172254],
          [-96.585355, 34.114459],
          [-96.548028, 34.063302],
          [-96.624748, 34.005417],
          [-96.57701, 33.964406],
          [-96.613494, 33.878321],
          [-96.615356, 33.881114],
          [-96.622165, 33.885977],
          [-96.633351, 33.889382],
          [-96.646968, 33.895218],
          [-96.65864, 33.900081],
          [-96.672258, 33.899595],
          [-96.67858, 33.892786],
          [-96.682471, 33.883059],
          [-96.685389, 33.872846],
          [-96.68928, 33.86166],
          [-96.694629, 33.849988],
          [-96.700952, 33.840748],
          [-96.707274, 33.835398],
          [-96.712422, 33.831633],
          [-96.761588, 33.824406],
          [-96.85609, 33.84749],
          [-96.866438, 33.853149],
          [-96.88301, 33.868019],
          [-96.895728, 33.896414],
          [-96.899442, 33.933728],
          [-96.907387, 33.950025],
          [-96.9163, 33.957798],
          [-96.922114, 33.959579]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40105",
      state: "40",
      county: "105",
      name: "Nowata County",
      censusArea: 565.777,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.66077, 36.59752],
          [-95.809617, 36.597476],
          [-95.80982, 36.94193],
          [-95.786762, 36.99931],
          [-95.768719, 36.999205],
          [-95.759905, 36.999271],
          [-95.741908, 36.999244],
          [-95.718054, 36.999255],
          [-95.714887, 36.999279],
          [-95.71038, 36.999371],
          [-95.696659, 36.999215],
          [-95.686452, 36.999349],
          [-95.664301, 36.999322],
          [-95.630079, 36.99932],
          [-95.62435, 36.99936],
          [-95.615934, 36.999365],
          [-95.61214, 36.999321],
          [-95.573598, 36.99931],
          [-95.534401, 36.999332],
          [-95.522415, 36.999281],
          [-95.511578, 36.999235],
          [-95.407683, 36.999241],
          [-95.407572, 36.999241],
          [-95.431731, 36.942776],
          [-95.43157, 36.59754],
          [-95.66077, 36.59752]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40113",
      state: "40",
      county: "113",
      name: "Osage County",
      censusArea: 2246.358,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.268287, 36.16198],
          [-96.243295, 36.206677],
          [-96.326732, 36.226551],
          [-96.433812, 36.285889],
          [-96.457684, 36.332664],
          [-96.515229, 36.298332],
          [-96.573481, 36.315709],
          [-96.518709, 36.377576],
          [-96.575697, 36.374347],
          [-96.635727, 36.425908],
          [-96.710769, 36.437656],
          [-96.753554, 36.562601],
          [-96.940235, 36.455525],
          [-97.009785, 36.506935],
          [-96.902643, 36.551388],
          [-96.912756, 36.598949],
          [-97.057414, 36.593647],
          [-97.064107, 36.684207],
          [-96.931943, 36.686097],
          [-96.889475, 36.75123],
          [-96.752382, 36.782092],
          [-96.749838, 36.998988],
          [-96.74127, 36.999239],
          [-96.73659, 36.999286],
          [-96.710482, 36.999271],
          [-96.705431, 36.999203],
          [-96.525493, 36.999147],
          [-96.415412, 36.999113],
          [-96.394272, 36.999221],
          [-96.279079, 36.999272],
          [-96.276368, 36.999271],
          [-96.217571, 36.99907],
          [-96.200028, 36.999028],
          [-96.184768, 36.999211],
          [-96.154017, 36.999161],
          [-96.152384, 36.999051],
          [-96.149709, 36.99904],
          [-96.147143, 36.999022],
          [-96.143207, 36.999134],
          [-96.14121, 36.998973],
          [-96.00081, 36.99886],
          [-96.001171, 36.423686],
          [-96.001055, 36.161294],
          [-96.268287, 36.16198]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36015",
      state: "36",
      county: "015",
      name: "Chemung County",
      censusArea: 407.352,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.927084, 42.001674],
          [-76.937084, 42.001674],
          [-76.942585, 42.001574],
          [-76.965686, 42.001274],
          [-76.965728, 42.001274],
          [-76.965028, 42.278495],
          [-76.733912, 42.29372],
          [-76.642256, 42.233721],
          [-76.619303, 42.282853],
          [-76.561601, 42.281986],
          [-76.538349, 42.281755],
          [-76.557624, 42.000149],
          [-76.558118, 42.000155],
          [-76.815878, 42.001673],
          [-76.835079, 42.001773],
          [-76.920784, 42.001774],
          [-76.921884, 42.001674],
          [-76.927084, 42.001674]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36023",
      state: "36",
      county: "023",
      name: "Cortland County",
      censusArea: 498.76,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.130181, 42.410337],
          [-76.253359, 42.407568],
          [-76.265584, 42.623588],
          [-76.274673, 42.771257],
          [-75.896079, 42.790964],
          [-75.889832, 42.723844],
          [-75.86402, 42.415702],
          [-76.130181, 42.410337]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36025",
      state: "36",
      county: "025",
      name: "Delaware County",
      censusArea: 1442.44,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.359579, 41.999445],
          [-75.421776, 42.04203],
          [-75.419664, 42.150436],
          [-75.418827, 42.180702],
          [-75.418438, 42.186797],
          [-75.418689, 42.188022],
          [-75.418807, 42.188104],
          [-75.418544, 42.189504],
          [-75.418421, 42.195032],
          [-75.415319, 42.314151],
          [-75.197237, 42.358329],
          [-74.97494, 42.467488],
          [-74.71158, 42.517799],
          [-74.618895, 42.424389],
          [-74.443506, 42.355017],
          [-74.53731, 42.201424],
          [-74.451713, 42.169225],
          [-74.780693, 42.016375],
          [-74.997252, 41.918485],
          [-75.146446, 41.850899],
          [-75.263005, 41.885109],
          [-75.279094, 41.938917],
          [-75.292589, 41.953897],
          [-75.341868, 41.993262],
          [-75.359579, 41.999445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36029",
      state: "36",
      county: "029",
      name: "Erie County",
      censusArea: 1042.693,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.136725, 42.569693],
          [-79.12963, 42.589824],
          [-79.126261, 42.590937],
          [-79.121921, 42.594234],
          [-79.113713, 42.605994],
          [-79.111361, 42.613358],
          [-79.078761, 42.640058],
          [-79.073261, 42.639958],
          [-79.06376, 42.644758],
          [-79.062261, 42.668358],
          [-79.04886, 42.689158],
          [-79.01886, 42.701558],
          [-79.00616, 42.704558],
          [-78.991159, 42.705358],
          [-78.944158, 42.731958],
          [-78.918157, 42.737258],
          [-78.868556, 42.770258],
          [-78.853455, 42.783958],
          [-78.851355, 42.791758],
          [-78.856456, 42.800258],
          [-78.859356, 42.800658],
          [-78.863656, 42.813058],
          [-78.865656, 42.826758],
          [-78.860445, 42.83511],
          [-78.859456, 42.841358],
          [-78.865592, 42.852358],
          [-78.872227, 42.853306],
          [-78.882557, 42.867258],
          [-78.891655, 42.884845],
          [-78.912458, 42.886557],
          [-78.905758, 42.899957],
          [-78.905659, 42.923357],
          [-78.909159, 42.933257],
          [-78.918859, 42.946857],
          [-78.921206, 42.948422],
          [-78.93236, 42.955857],
          [-78.972524, 42.966804],
          [-79.011563, 42.985256],
          [-79.019964, 42.994756],
          [-79.028353, 43.066897],
          [-79.009664, 43.069558],
          [-78.945262, 43.066956],
          [-78.828805, 43.030139],
          [-78.733606, 43.084219],
          [-78.464449, 43.088703],
          [-78.463887, 42.924325],
          [-78.464381, 42.867461],
          [-78.46394, 42.536332],
          [-78.695937, 42.47194],
          [-78.920446, 42.442556],
          [-78.991702, 42.529249],
          [-79.060777, 42.537853],
          [-79.136725, 42.569693]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40139",
      state: "40",
      county: "139",
      name: "Texas County",
      censusArea: 2041.261,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.977088, 36.499595],
          [-101.045331, 36.49954],
          [-101.052418, 36.499563],
          [-101.085156, 36.499244],
          [-101.623915, 36.499528],
          [-101.649966, 36.499573],
          [-101.653708, 36.499573],
          [-101.698685, 36.499508],
          [-101.709314, 36.499722],
          [-101.779435, 36.499734],
          [-101.78061, 36.499727],
          [-101.781987, 36.499718],
          [-101.783359, 36.499709],
          [-101.78811, 36.499678],
          [-101.826498, 36.499535],
          [-101.826565, 36.499654],
          [-102.032339, 36.500066],
          [-102.028204, 36.993125],
          [-102.000447, 36.993272],
          [-102.000447, 36.993249],
          [-101.90244, 36.993702],
          [-101.601593, 36.995095],
          [-101.600396, 36.995153],
          [-101.55526, 36.995414],
          [-101.555239, 36.995414],
          [-101.519066, 36.995546],
          [-101.485326, 36.995611],
          [-101.415005, 36.995966],
          [-101.413868, 36.996008],
          [-101.37818, 36.996164],
          [-101.359674, 36.996232],
          [-101.357797, 36.996271],
          [-101.212909, 36.997044],
          [-101.211486, 36.997124],
          [-101.066742, 36.997921],
          [-101.053589, 36.997967],
          [-101.012641, 36.998176],
          [-100.996502, 36.998044],
          [-100.945566, 36.998152],
          [-100.945469, 36.998153],
          [-100.954153, 36.499599],
          [-100.977088, 36.499595]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40151",
      state: "40",
      county: "151",
      name: "Woods County",
      censusArea: 1286.454,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.295186, 36.819566],
          [-99.372996, 36.87861],
          [-99.456203, 36.999471],
          [-99.277506, 36.999579],
          [-99.24812, 36.999565],
          [-99.129449, 36.999422],
          [-99.124883, 36.99942],
          [-99.029337, 36.999595],
          [-99.000303, 36.99951],
          [-98.994371, 36.999493],
          [-98.88058, 36.999309],
          [-98.880009, 36.999263],
          [-98.869449, 36.999286],
          [-98.797452, 36.999229],
          [-98.793711, 36.999227],
          [-98.791936, 36.999255],
          [-98.761597, 36.999425],
          [-98.718465, 36.99918],
          [-98.714512, 36.99906],
          [-98.544872, 36.998997],
          [-98.54466, 36.998996],
          [-98.533617, 36.46323],
          [-98.53355, 36.397592],
          [-98.625361, 36.392595],
          [-98.80782, 36.460744],
          [-98.843646, 36.506248],
          [-98.960334, 36.506914],
          [-98.959973, 36.598991],
          [-99.139855, 36.78304],
          [-99.295186, 36.819566]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41001",
      state: "41",
      county: "001",
      name: "Baker County",
      censusArea: 3068.357,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.931499, 44.792281],
          [-117.016216, 44.755572],
          [-117.03827, 44.748179],
          [-117.044217, 44.74514],
          [-117.062273, 44.727143],
          [-117.095868, 44.664737],
          [-117.1159, 44.623339],
          [-117.144161, 44.545647],
          [-117.149242, 44.536151],
          [-117.156489, 44.528312],
          [-117.208454, 44.485928],
          [-117.214889, 44.466901],
          [-117.189769, 44.336585],
          [-117.191546, 44.329621],
          [-117.194827, 44.31054],
          [-117.196597, 44.30228],
          [-117.197506, 44.29728],
          [-117.486153, 44.30142],
          [-117.485899, 44.387696],
          [-117.590992, 44.444461],
          [-117.971596, 44.443328],
          [-118.15234, 44.299583],
          [-118.23214, 44.256083],
          [-118.497249, 44.255084],
          [-118.46905, 44.334583],
          [-118.409749, 44.378683],
          [-118.40845, 44.452782],
          [-118.30615, 44.588979],
          [-118.372953, 44.645376],
          [-118.504257, 44.665575],
          [-118.519257, 44.706374],
          [-118.285153, 44.751172],
          [-118.296153, 44.862664],
          [-118.236652, 44.861565],
          [-118.244751, 44.958256],
          [-118.130309, 45.045124],
          [-117.969185, 44.995803],
          [-117.84343, 45.058477],
          [-117.777504, 44.992161],
          [-117.563714, 44.992788],
          [-117.563138, 45.079588],
          [-117.266572, 45.080805],
          [-116.783902, 45.079022],
          [-116.845369, 45.017059],
          [-116.858277, 44.978008],
          [-116.850737, 44.958113],
          [-116.844653, 44.949338],
          [-116.835702, 44.940633],
          [-116.83199, 44.933007],
          [-116.852427, 44.887577],
          [-116.891463, 44.840531],
          [-116.931499, 44.792281]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41007",
      state: "41",
      county: "007",
      name: "Clatsop County",
      censusArea: 829.049,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.96934, 45.783197],
          [-123.961544, 45.837101],
          [-123.962736, 45.869974],
          [-123.96763, 45.907807],
          [-123.979501, 45.930389],
          [-123.99304, 45.938842],
          [-123.993703, 45.946431],
          [-123.969991, 45.969139],
          [-123.957438, 45.974469],
          [-123.941831, 45.97566],
          [-123.937471, 45.977306],
          [-123.927891, 46.009564],
          [-123.92933, 46.041978],
          [-123.933366, 46.071672],
          [-123.947531, 46.116131],
          [-123.95919, 46.141675],
          [-123.974124, 46.168798],
          [-123.996766, 46.20399],
          [-124.010344, 46.223514],
          [-124.024305, 46.229256],
          [-124.011355, 46.236223],
          [-124.001998, 46.237316],
          [-123.998052, 46.235327],
          [-123.988429, 46.224132],
          [-123.990117, 46.21763],
          [-123.987196, 46.211521],
          [-123.982149, 46.209662],
          [-123.961739, 46.207916],
          [-123.950148, 46.204097],
          [-123.927038, 46.191617],
          [-123.912405, 46.17945],
          [-123.9042, 46.169293],
          [-123.891186, 46.164778],
          [-123.854801, 46.157342],
          [-123.842849, 46.160529],
          [-123.841521, 46.169824],
          [-123.863347, 46.18235],
          [-123.866643, 46.187674],
          [-123.864209, 46.189527],
          [-123.838801, 46.192211],
          [-123.821834, 46.190293],
          [-123.793936, 46.196283],
          [-123.759976, 46.2073],
          [-123.736747, 46.200687],
          [-123.71278, 46.198751],
          [-123.706667, 46.199665],
          [-123.67538, 46.212401],
          [-123.673831, 46.215418],
          [-123.666751, 46.218228],
          [-123.65539, 46.217974],
          [-123.636474, 46.214359],
          [-123.6325, 46.216681],
          [-123.626247, 46.226434],
          [-123.625219, 46.233868],
          [-123.622812, 46.23664],
          [-123.613459, 46.239228],
          [-123.605487, 46.2393],
          [-123.60019, 46.234814],
          [-123.586205, 46.228654],
          [-123.548194, 46.248245],
          [-123.547659, 46.259109],
          [-123.484244, 46.269531],
          [-123.474844, 46.267831],
          [-123.468743, 46.264531],
          [-123.447592, 46.249832],
          [-123.427629, 46.229348],
          [-123.430847, 46.181827],
          [-123.371433, 46.146372],
          [-123.363638, 46.146241],
          [-123.360962, 45.779669],
          [-123.719288, 45.77315],
          [-123.96934, 45.783197]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41041",
      state: "41",
      county: "041",
      name: "Lincoln County",
      censusArea: 979.765,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-124.004598, 45.044959],
          [-123.725389, 45.043987],
          [-123.724916, 44.73908],
          [-123.602566, 44.721191],
          [-123.597217, 44.43289],
          [-123.72042, 44.433053],
          [-123.715212, 44.360569],
          [-123.816931, 44.344804],
          [-123.77542, 44.283561],
          [-124.115849, 44.276277],
          [-124.1152, 44.286486],
          [-124.10907, 44.303707],
          [-124.108088, 44.309926],
          [-124.109556, 44.314545],
          [-124.100587, 44.331926],
          [-124.092101, 44.370388],
          [-124.084401, 44.415611],
          [-124.080989, 44.419728],
          [-124.071706, 44.423662],
          [-124.067569, 44.428582],
          [-124.073941, 44.434481],
          [-124.079301, 44.430863],
          [-124.082113, 44.441518],
          [-124.082061, 44.478171],
          [-124.084429, 44.486927],
          [-124.083601, 44.501123],
          [-124.076387, 44.531214],
          [-124.067251, 44.60804],
          [-124.06914, 44.612979],
          [-124.082326, 44.608861],
          [-124.084476, 44.611056],
          [-124.065202, 44.622445],
          [-124.065008, 44.632504],
          [-124.058281, 44.658866],
          [-124.060043, 44.669361],
          [-124.070394, 44.683514],
          [-124.063406, 44.703177],
          [-124.059077, 44.737656],
          [-124.066325, 44.762671],
          [-124.075473, 44.771403],
          [-124.074066, 44.798107],
          [-124.066746, 44.831191],
          [-124.063155, 44.835333],
          [-124.054151, 44.838233],
          [-124.048814, 44.850007],
          [-124.032296, 44.900809],
          [-124.025136, 44.928175],
          [-124.025678, 44.936542],
          [-124.023834, 44.949825],
          [-124.015243, 44.982904],
          [-124.004598, 45.044959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31139",
      state: "31",
      county: "139",
      name: "Pierce County",
      censusArea: 573.249,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.834536, 42.08975],
          [-97.834699, 42.091108],
          [-97.834431, 42.437684],
          [-97.485298, 42.438578],
          [-97.36759, 42.438775],
          [-97.367724, 42.351861],
          [-97.368404, 42.090922],
          [-97.834536, 42.08975]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31145",
      state: "31",
      county: "145",
      name: "Red Willow County",
      censusArea: 716.985,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.733296, 40.00227],
          [-100.738826, 40.002228],
          [-100.752183, 40.002128],
          [-100.75883, 40.002302],
          [-100.758435, 40.349507],
          [-100.197807, 40.350027],
          [-100.193597, 40.001573],
          [-100.196959, 40.001494],
          [-100.215406, 40.001629],
          [-100.229479, 40.001693],
          [-100.231652, 40.001623],
          [-100.39008, 40.001809],
          [-100.439081, 40.001774],
          [-100.447072, 40.001795],
          [-100.468773, 40.001724],
          [-100.475854, 40.001768],
          [-100.477018, 40.001752],
          [-100.487159, 40.001767],
          [-100.511065, 40.00184],
          [-100.551886, 40.001889],
          [-100.567238, 40.001889],
          [-100.594757, 40.001977],
          [-100.600945, 40.001906],
          [-100.645445, 40.001883],
          [-100.66023, 40.002162],
          [-100.683435, 40.002234],
          [-100.721128, 40.002069],
          [-100.729904, 40.002111],
          [-100.733296, 40.00227]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19029",
      state: "19",
      county: "029",
      name: "Cass County",
      censusArea: 564.269,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.154722, 41.505211],
          [-95.04077, 41.50469],
          [-94.700629, 41.504148],
          [-94.700589, 41.158085],
          [-94.704975, 41.157903],
          [-94.927587, 41.158508],
          [-95.154115, 41.159145],
          [-95.155851, 41.159236],
          [-95.154722, 41.505211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19037",
      state: "19",
      county: "037",
      name: "Chickasaw County",
      censusArea: 504.38,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.081568, 42.907013],
          [-92.554211, 42.907112],
          [-92.554381, 43.212813],
          [-92.080976, 43.212947],
          [-92.081146, 43.082794],
          [-92.081568, 42.907013]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17083",
      state: "17",
      county: "083",
      name: "Jersey County",
      censusArea: 369.271,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.385751, 38.956848],
          [-90.406367, 38.962554],
          [-90.450792, 38.967764],
          [-90.450809, 38.967758],
          [-90.570169, 38.99329],
          [-90.603569, 39.117592],
          [-90.574615, 39.178623],
          [-90.48749, 39.175133],
          [-90.370711, 39.174444],
          [-90.314071, 39.225053],
          [-90.148069, 39.261947],
          [-90.145991, 39.000046],
          [-90.273686, 38.999347],
          [-90.275932, 38.926453],
          [-90.385751, 38.956848]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17085",
      state: "17",
      county: "085",
      name: "Jo Daviess County",
      censusArea: 601.087,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.640927, 42.508302],
          [-90.617731, 42.508077],
          [-90.614589, 42.508053],
          [-90.565441, 42.5076],
          [-90.555862, 42.507509],
          [-90.551165, 42.507691],
          [-90.544799, 42.507713],
          [-90.544347, 42.507707],
          [-90.532254, 42.507573],
          [-90.491716, 42.507624],
          [-90.479446, 42.507416],
          [-90.474955, 42.507484],
          [-90.437011, 42.507147],
          [-90.426378, 42.507059],
          [-90.405927, 42.506891],
          [-90.370673, 42.507111],
          [-90.367874, 42.507114],
          [-90.362652, 42.507048],
          [-90.303823, 42.507469],
          [-90.272864, 42.507531],
          [-90.269335, 42.507726],
          [-90.267143, 42.507642],
          [-90.253121, 42.50734],
          [-90.250622, 42.507521],
          [-90.22319, 42.507765],
          [-90.206073, 42.507747],
          [-90.181572, 42.508068],
          [-90.164363, 42.508272],
          [-90.142922, 42.508151],
          [-90.095004, 42.507885],
          [-90.093026, 42.50816],
          [-90.018665, 42.507288],
          [-90.017028, 42.507127],
          [-89.999314, 42.506914],
          [-89.997213, 42.506755],
          [-89.985645, 42.506431],
          [-89.985072, 42.506464],
          [-89.926484, 42.505787],
          [-89.926374, 42.505788],
          [-89.926224, 42.505788],
          [-89.919657, 42.196305],
          [-90.304913, 42.194073],
          [-90.316269, 42.1936],
          [-90.349162, 42.204277],
          [-90.356964, 42.205445],
          [-90.375129, 42.214811],
          [-90.391108, 42.225473],
          [-90.419326, 42.254467],
          [-90.430884, 42.27823],
          [-90.430735, 42.284211],
          [-90.424326, 42.293326],
          [-90.4162, 42.321314],
          [-90.416535, 42.325109],
          [-90.419027, 42.328505],
          [-90.474834, 42.381473],
          [-90.477279, 42.383794],
          [-90.559451, 42.430695],
          [-90.624328, 42.458904],
          [-90.646727, 42.471904],
          [-90.654027, 42.478503],
          [-90.656327, 42.483603],
          [-90.656527, 42.489203],
          [-90.655927, 42.491703],
          [-90.640927, 42.508302]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17097",
      state: "17",
      county: "097",
      name: "Lake County",
      censusArea: 443.67,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.049782, 42.495319],
          [-87.99018, 42.494519],
          [-87.971279, 42.494019],
          [-87.900242, 42.49302],
          [-87.843594, 42.492307],
          [-87.815872, 42.49192],
          [-87.800561, 42.49192],
          [-87.800477, 42.49192],
          [-87.800317, 42.490578],
          [-87.798971, 42.479278],
          [-87.79823, 42.473054],
          [-87.798071, 42.471721],
          [-87.800752, 42.445867],
          [-87.80337, 42.420621],
          [-87.803529, 42.417759],
          [-87.80375, 42.413793],
          [-87.80537, 42.384721],
          [-87.80613, 42.383357],
          [-87.81557, 42.366416],
          [-87.81657, 42.364621],
          [-87.820858, 42.361584],
          [-87.820871, 42.361544],
          [-87.826717, 42.343497],
          [-87.830646, 42.331368],
          [-87.830986, 42.330317],
          [-87.831221, 42.32853],
          [-87.834769, 42.301522],
          [-87.833468, 42.294892],
          [-87.832738, 42.291173],
          [-87.83253, 42.290109],
          [-87.831286, 42.283772],
          [-87.828569, 42.269922],
          [-87.828034, 42.268673],
          [-87.82772, 42.267937],
          [-87.826944, 42.266125],
          [-87.825313, 42.262313],
          [-87.812461, 42.232278],
          [-87.812422, 42.232185],
          [-87.812315, 42.231935],
          [-87.812267, 42.231823],
          [-87.808395, 42.224271],
          [-87.803873, 42.215449],
          [-87.800066, 42.208024],
          [-87.798589, 42.206007],
          [-87.7983, 42.205611],
          [-87.797497, 42.204514],
          [-87.797444, 42.204442],
          [-87.796937, 42.203748],
          [-87.796779, 42.203533],
          [-87.788313, 42.191966],
          [-87.787241, 42.190501],
          [-87.775295, 42.174179],
          [-87.759327, 42.152362],
          [-88.004466, 42.153645],
          [-88.121545, 42.154193],
          [-88.199584, 42.15426],
          [-88.19875, 42.25422],
          [-88.199521, 42.496013],
          [-88.049782, 42.495319]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17115",
      state: "17",
      county: "115",
      name: "Macon County",
      censusArea: 580.689,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.025691, 39.654637],
          [-89.139125, 39.655131],
          [-89.141817, 39.80091],
          [-89.217523, 39.813224],
          [-89.21747, 39.85296],
          [-89.217546, 39.872392],
          [-89.21781, 39.873818],
          [-89.217846, 39.91699],
          [-89.143457, 39.91792],
          [-89.144764, 40.048853],
          [-88.745164, 40.055191],
          [-88.745957, 39.858136],
          [-88.745671, 39.792146],
          [-88.812709, 39.740486],
          [-88.810575, 39.653222],
          [-89.02568, 39.654183],
          [-89.025691, 39.654637]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17119",
      state: "17",
      county: "119",
      name: "Madison County",
      censusArea: 715.582,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.117707, 38.805748],
          [-90.114707, 38.815048],
          [-90.109107, 38.837448],
          [-90.109407, 38.843548],
          [-90.113327, 38.849306],
          [-90.207035, 38.898954],
          [-90.22585, 38.908923],
          [-90.250248, 38.919344],
          [-90.275932, 38.926453],
          [-90.273686, 38.999347],
          [-90.145991, 39.000046],
          [-89.81462, 38.998349],
          [-89.811673, 38.998462],
          [-89.707911, 38.999009],
          [-89.707266, 38.999012],
          [-89.705675, 38.999161],
          [-89.705349, 38.998963],
          [-89.70402, 38.99894],
          [-89.698555, 38.998979],
          [-89.653446, 38.999191],
          [-89.652405, 38.999581],
          [-89.648846, 38.999167],
          [-89.639265, 38.999129],
          [-89.636874, 38.874278],
          [-89.599593, 38.87453],
          [-89.597321, 38.743236],
          [-89.595103, 38.655948],
          [-89.707024, 38.655152],
          [-90.037105, 38.658449],
          [-90.17772, 38.660536],
          [-90.181399, 38.660378],
          [-90.18641, 38.67475],
          [-90.19521, 38.68755],
          [-90.20221, 38.69345],
          [-90.20921, 38.70275],
          [-90.21201, 38.71175],
          [-90.20991, 38.72605],
          [-90.171309, 38.766549],
          [-90.165082, 38.770618],
          [-90.123107, 38.798048],
          [-90.117707, 38.805748]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47039",
      state: "47",
      county: "039",
      name: "Decatur County",
      censusArea: 333.845,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.007709, 35.423381],
          [-87.975184, 35.414174],
          [-87.984553, 35.392115],
          [-88.024741, 35.392307],
          [-88.199643, 35.38257],
          [-88.241677, 35.423263],
          [-88.243057, 35.528858],
          [-88.190465, 35.609088],
          [-88.179177, 35.816785],
          [-88.17898, 35.818911],
          [-88.177859, 35.845841],
          [-87.970738, 35.815705],
          [-88.034788, 35.717261],
          [-88.035837, 35.621839],
          [-87.97463, 35.521025],
          [-88.042332, 35.444617],
          [-88.007709, 35.423381]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47043",
      state: "47",
      county: "043",
      name: "Dickson County",
      censusArea: 489.896,
      stateName: "Tennessee"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-87.151995, 36.289037],
            [-87.146906, 36.293344],
            [-87.144172, 36.292142],
            [-87.142315, 36.294607],
            [-87.139332, 36.292418],
            [-87.142372, 36.290684],
            [-87.151995, 36.289037]
          ]
        ],
        [
          [
            [-87.204242, 35.959186],
            [-87.53453, 35.993074],
            [-87.566798, 36.177857],
            [-87.513533, 36.334713],
            [-87.286501, 36.321933],
            [-87.177303, 36.314145],
            [-87.149876, 36.176878],
            [-87.182573, 36.049726],
            [-87.204242, 35.959186]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47053",
      state: "47",
      county: "053",
      name: "Gibson County",
      censusArea: 602.742,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.692709, 36.062746],
          [-88.706811, 35.791075],
          [-88.910964, 35.797336],
          [-88.916181, 35.797307],
          [-89.109602, 35.862095],
          [-89.188844, 35.99976],
          [-89.163406, 36.026044],
          [-89.154464, 36.204678],
          [-88.961232, 36.201635],
          [-88.959839, 36.222834],
          [-88.840884, 36.123495],
          [-88.692709, 36.062746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47055",
      state: "47",
      county: "055",
      name: "Giles County",
      censusArea: 610.927,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.828301, 35.263677],
          [-86.836306, 34.991764],
          [-86.83637, 34.991764],
          [-86.846466, 34.99186],
          [-86.849794, 34.991924],
          [-86.862147, 34.991956],
          [-86.96712, 34.9944],
          [-86.970236, 34.994546],
          [-86.972613, 34.99461],
          [-86.974412, 34.994513],
          [-87.000007, 34.995121],
          [-87.011174, 34.995162],
          [-87.210759, 34.999024],
          [-87.216683, 34.999148],
          [-87.224053, 34.999327],
          [-87.206578, 35.433207],
          [-87.120883, 35.45566],
          [-86.960804, 35.417021],
          [-86.906451, 35.314075],
          [-86.828301, 35.263677]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23013",
      state: "23",
      county: "013",
      name: "Knox County",
      censusArea: 365.134,
      stateName: "Maine"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-68.880242, 43.863398],
            [-68.882554, 43.848525],
            [-68.876224, 43.836597],
            [-68.886725, 43.822195],
            [-68.898319, 43.820987],
            [-68.899108, 43.821925],
            [-68.893537, 43.831498],
            [-68.8947, 43.84397],
            [-68.898045, 43.849545],
            [-68.903957, 43.848684],
            [-68.908222, 43.849852],
            [-68.902618, 43.868855],
            [-68.889367, 43.87553],
            [-68.880242, 43.863398]
          ]
        ],
        [
          [
            [-69.043981, 44.005684],
            [-69.0782, 43.974132],
            [-69.092994, 43.979063],
            [-69.061966, 44.015023],
            [-69.047583, 44.013995],
            [-69.043981, 44.005684]
          ]
        ],
        [
          [
            [-68.618212, 44.012367],
            [-68.635315, 44.018886],
            [-68.64736, 44.0145],
            [-68.651863, 44.009859],
            [-68.652881, 44.003845],
            [-68.657031, 44.003823],
            [-68.659972, 44.016013],
            [-68.659874, 44.022758],
            [-68.657369, 44.024404],
            [-68.650767, 44.039908],
            [-68.654783, 44.059599],
            [-68.661594, 44.075837],
            [-68.627893, 44.088128],
            [-68.62535, 44.092906],
            [-68.6181, 44.096706],
            [-68.609722, 44.094674],
            [-68.602863, 44.08665],
            [-68.589563, 44.075585],
            [-68.585916, 44.075335],
            [-68.584074, 44.070578],
            [-68.588098, 44.06127],
            [-68.590792, 44.058662],
            [-68.601099, 44.058362],
            [-68.611473, 44.025176],
            [-68.610703, 44.013422],
            [-68.615896, 44.009761],
            [-68.618212, 44.012367]
          ]
        ],
        [
          [
            [-68.785601, 44.053503],
            [-68.790595, 44.053832],
            [-68.807315, 44.035796],
            [-68.818441, 44.032046],
            [-68.828465, 44.032118],
            [-68.862845, 44.025037],
            [-68.874139, 44.025359],
            [-68.889717, 44.032516],
            [-68.899997, 44.06696],
            [-68.905098, 44.077344],
            [-68.913406, 44.08519],
            [-68.907812, 44.105518],
            [-68.908984, 44.110001],
            [-68.943105, 44.10973],
            [-68.944597, 44.11284],
            [-68.935327, 44.13038],
            [-68.917286, 44.148239],
            [-68.888597, 44.15955],
            [-68.87868, 44.166612],
            [-68.847249, 44.183017],
            [-68.825067, 44.186338],
            [-68.819156, 44.180462],
            [-68.822206, 44.178815],
            [-68.82284, 44.173693],
            [-68.818423, 44.160978],
            [-68.792221, 44.145998],
            [-68.786886, 44.143961],
            [-68.782375, 44.14531],
            [-68.780693, 44.143274],
            [-68.792065, 44.136759],
            [-68.802162, 44.137857],
            [-68.818039, 44.136852],
            [-68.819659, 44.135434],
            [-68.820515, 44.130198],
            [-68.81953, 44.122056],
            [-68.815562, 44.115836],
            [-68.806832, 44.116339],
            [-68.790525, 44.09292],
            [-68.781772, 44.084274],
            [-68.772639, 44.078439],
            [-68.77029, 44.069566],
            [-68.77965, 44.057754],
            [-68.785601, 44.053503]
          ]
        ],
        [
          [
            [-69.321529, 44.1729],
            [-69.528223, 44.275447],
            [-69.521258, 44.280683],
            [-69.506382, 44.34287],
            [-69.394361, 44.342934],
            [-69.346616, 44.308337],
            [-69.268882, 44.364653],
            [-69.121622, 44.255644],
            [-69.02859, 44.249139],
            [-69.029434, 44.248558],
            [-69.040193, 44.233673],
            [-69.043599, 44.225029],
            [-69.042807, 44.215173],
            [-69.05181, 44.19592],
            [-69.05269, 44.188082],
            [-69.054546, 44.171542],
            [-69.06124, 44.165498],
            [-69.077776, 44.165043],
            [-69.079835, 44.160953],
            [-69.080978, 44.156768],
            [-69.079608, 44.143962],
            [-69.075667, 44.129991],
            [-69.080331, 44.117824],
            [-69.100863, 44.104529],
            [-69.101107, 44.093601],
            [-69.092, 44.085734],
            [-69.089078, 44.085326],
            [-69.076452, 44.090634],
            [-69.056303, 44.095162],
            [-69.050814, 44.094888],
            [-69.043403, 44.092164],
            [-69.031878, 44.079036],
            [-69.048917, 44.062506],
            [-69.050566, 44.063152],
            [-69.050622, 44.068017],
            [-69.056093, 44.06949],
            [-69.064299, 44.069911],
            [-69.067876, 44.067596],
            [-69.079805, 44.055256],
            [-69.073767, 44.046135],
            [-69.081131, 44.041295],
            [-69.094177, 44.038981],
            [-69.113113, 44.028881],
            [-69.125738, 44.019623],
            [-69.128052, 44.017309],
            [-69.124475, 44.007419],
            [-69.128611, 44.005922],
            [-69.148883, 43.998582],
            [-69.162559, 43.999003],
            [-69.170345, 43.995637],
            [-69.193805, 43.975543],
            [-69.197803, 43.967547],
            [-69.193805, 43.959762],
            [-69.19633, 43.950504],
            [-69.203668, 43.941806],
            [-69.214205, 43.935583],
            [-69.237368, 43.931596],
            [-69.24271, 43.925465],
            [-69.259838, 43.921427],
            [-69.265257, 43.933393],
            [-69.267515, 43.943667],
            [-69.280498, 43.95744],
            [-69.282559, 43.958105],
            [-69.283998, 43.958569],
            [-69.288513, 43.957665],
            [-69.307776, 43.943451],
            [-69.31427, 43.942951],
            [-69.319751, 43.94487],
            [-69.305176, 43.956676],
            [-69.304301, 43.962068],
            [-69.321808, 43.969975],
            [-69.281839, 44.022532],
            [-69.321529, 44.1729]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23019",
      state: "23",
      county: "019",
      name: "Penobscot County",
      censusArea: 3397.362,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-68.815793, 44.683754],
          [-69.014167, 44.671794],
          [-69.183732, 44.644023],
          [-69.2052, 44.729586],
          [-69.266785, 44.721928],
          [-69.305521, 44.895988],
          [-69.355667, 45.073469],
          [-69.232021, 45.090166],
          [-68.856587, 45.142783],
          [-68.880923, 45.224947],
          [-68.776061, 45.240317],
          [-68.800906, 45.329148],
          [-68.857297, 45.527301],
          [-68.964653, 45.512367],
          [-68.95891, 45.662195],
          [-68.827039, 45.684826],
          [-68.819401, 46.395782],
          [-68.69492, 46.395505],
          [-68.694877, 46.382612],
          [-68.436443, 46.382121],
          [-68.433001, 45.581204],
          [-68.433028, 45.578482],
          [-68.414389, 45.572848],
          [-68.410884, 45.573337],
          [-68.048012, 45.639897],
          [-67.93976, 45.267845],
          [-68.054985, 45.252577],
          [-68.303714, 45.221531],
          [-68.295438, 45.190443],
          [-68.270497, 45.100121],
          [-68.396903, 45.083296],
          [-68.375892, 44.997373],
          [-68.501095, 44.980718],
          [-68.477952, 44.889091],
          [-68.454889, 44.80155],
          [-68.445219, 44.763014],
          [-68.528542, 44.752167],
          [-68.616673, 44.740992],
          [-68.647134, 44.703636],
          [-68.69093, 44.721668],
          [-68.815793, 44.683754]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23025",
      state: "23",
      county: "025",
      name: "Somerset County",
      censusArea: 3924.403,
      stateName: "Maine"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-69.632971, 44.698763],
          [-69.62848, 44.671969],
          [-69.579094, 44.626971],
          [-69.605746, 44.577264],
          [-69.742091, 44.598246],
          [-69.744282, 44.602607],
          [-69.776294, 44.608236],
          [-69.792813, 44.57733],
          [-69.930451, 44.610852],
          [-69.996191, 44.677497],
          [-70.03327, 44.86454],
          [-70.12993, 44.850765],
          [-70.149085, 44.896102],
          [-70.110365, 44.947311],
          [-70.159375, 45.128396],
          [-70.293826, 45.109903],
          [-70.308491, 45.163183],
          [-70.418519, 45.144005],
          [-70.551038, 45.661885],
          [-70.469869, 45.701639],
          [-70.451503, 45.704432],
          [-70.39721, 45.756872],
          [-70.40775, 45.773654],
          [-70.417674, 45.79457],
          [-70.396362, 45.802703],
          [-70.398159, 45.80412],
          [-70.397208, 45.807572],
          [-70.387916, 45.819043],
          [-70.36702, 45.834993],
          [-70.34244, 45.852192],
          [-70.307798, 45.85894],
          [-70.284204, 45.872034],
          [-70.262655, 45.887853],
          [-70.253704, 45.902981],
          [-70.240177, 45.943729],
          [-70.252242, 45.954777],
          [-70.26541, 45.962692],
          [-70.312055, 45.971544],
          [-70.313364, 46.016383],
          [-70.310609, 46.064544],
          [-70.28978, 46.094325],
          [-70.284554, 46.098713],
          [-70.252411, 46.106221],
          [-70.237947, 46.147378],
          [-70.278034, 46.175001],
          [-70.290896, 46.185838],
          [-70.292736, 46.191599],
          [-70.207415, 46.331316],
          [-70.188046, 46.350015],
          [-70.174709, 46.358472],
          [-70.133367, 46.368906],
          [-70.056433, 46.41659],
          [-70.023016, 46.573486],
          [-69.721109, 46.574053],
          [-69.73232, 46.394008],
          [-69.728577, 45.976882],
          [-69.683564, 45.983632],
          [-69.646755, 45.863163],
          [-69.705743, 45.853975],
          [-69.732579, 45.755978],
          [-69.784853, 45.785212],
          [-69.832818, 45.738734],
          [-69.691806, 45.645031],
          [-69.779928, 45.542766],
          [-69.620967, 45.010956],
          [-69.499861, 45.053778],
          [-69.355667, 45.073469],
          [-69.305521, 44.895988],
          [-69.266785, 44.721928],
          [-69.398412, 44.744559],
          [-69.471712, 44.692899],
          [-69.632971, 44.698763]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40121",
      state: "40",
      county: "121",
      name: "Pittsburg County",
      censusArea: 1305.462,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.671976, 34.593852],
          [-95.881987, 34.593528],
          [-95.882103, 34.680475],
          [-96.092043, 34.680556],
          [-96.092001, 34.767479],
          [-96.088843, 35.049863],
          [-95.98371, 35.151557],
          [-95.870663, 35.185986],
          [-95.75597, 35.177198],
          [-95.628064, 35.246798],
          [-95.59938, 35.221651],
          [-95.45125, 35.296019],
          [-95.454954, 35.160577],
          [-95.349339, 35.160296],
          [-95.349233, 35.058514],
          [-95.349437, 35.029299],
          [-95.507658, 35.029197],
          [-95.514514, 34.681142],
          [-95.514315, 34.594153],
          [-95.671976, 34.593852]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40129",
      state: "40",
      county: "129",
      name: "Roger Mills County",
      censusArea: 1141.136,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.000392, 35.619115],
          [-100.000396, 35.880948],
          [-99.925147, 35.913664],
          [-99.888309, 35.987847],
          [-99.809444, 36.000044],
          [-99.801389, 35.910645],
          [-99.717101, 35.859769],
          [-99.558486, 35.865988],
          [-99.495812, 35.978732],
          [-99.381022, 36.012925],
          [-99.375817, 35.812188],
          [-99.364141, 35.508793],
          [-99.575598, 35.508861],
          [-99.57582, 35.421655],
          [-100.000389, 35.422364],
          [-100.000392, 35.619115]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24045",
      state: "24",
      county: "045",
      name: "Wicomico County",
      censusArea: 374.444,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.920279, 38.264415],
          [-75.925492, 38.26667],
          [-75.928628, 38.297272],
          [-75.864213, 38.350782],
          [-75.860258, 38.353868],
          [-75.857673, 38.357011],
          [-75.857207, 38.359942],
          [-75.857656, 38.363498],
          [-75.859499, 38.36675],
          [-75.866042, 38.373053],
          [-75.867311, 38.375118],
          [-75.865397, 38.379064],
          [-75.860851, 38.383577],
          [-75.85529, 38.385345],
          [-75.846202, 38.384405],
          [-75.840348, 38.384784],
          [-75.838282, 38.385878],
          [-75.838369, 38.387476],
          [-75.842399, 38.393082],
          [-75.846741, 38.397058],
          [-75.851258, 38.400348],
          [-75.850952, 38.404536],
          [-75.847281, 38.417287],
          [-75.845573, 38.421868],
          [-75.843343, 38.422745],
          [-75.828869, 38.423417],
          [-75.82496, 38.425372],
          [-75.821287, 38.430135],
          [-75.819829, 38.435886],
          [-75.821342, 38.439261],
          [-75.826639, 38.442231],
          [-75.833127, 38.443443],
          [-75.835994, 38.445207],
          [-75.836743, 38.448088],
          [-75.835728, 38.45329],
          [-75.83316, 38.45762],
          [-75.828492, 38.459614],
          [-75.821994, 38.460959],
          [-75.81669, 38.463307],
          [-75.814901, 38.466459],
          [-75.815772, 38.469373],
          [-75.818709, 38.472332],
          [-75.822902, 38.47601],
          [-75.824765, 38.479095],
          [-75.823706, 38.481022],
          [-75.815193, 38.488794],
          [-75.800496, 38.49102],
          [-75.79466, 38.492373],
          [-75.784972, 38.500115],
          [-75.782064, 38.501596],
          [-75.764994, 38.506479],
          [-75.759754, 38.510122],
          [-75.756022, 38.515316],
          [-75.753636, 38.5281],
          [-75.749655, 38.531688],
          [-75.746515, 38.536785],
          [-75.740586, 38.538364],
          [-75.737763, 38.537649],
          [-75.72976, 38.538704],
          [-75.72449, 38.54123],
          [-75.718793, 38.546184],
          [-75.711893, 38.550876],
          [-75.70734, 38.559524],
          [-75.701565, 38.560736],
          [-75.701465, 38.559433],
          [-75.700179, 38.542717],
          [-75.698777, 38.522001],
          [-75.696688, 38.496467],
          [-75.696369, 38.492373],
          [-75.693521, 38.460128],
          [-75.665585, 38.4589],
          [-75.662843, 38.458759],
          [-75.630457, 38.457904],
          [-75.598069, 38.456855],
          [-75.593082, 38.456404],
          [-75.589307, 38.456286],
          [-75.583601, 38.456424],
          [-75.57411, 38.455991],
          [-75.559934, 38.455579],
          [-75.559212, 38.455563],
          [-75.533763, 38.454958],
          [-75.52273, 38.454657],
          [-75.521304, 38.454657],
          [-75.502961, 38.45422],
          [-75.500142, 38.454144],
          [-75.47915, 38.453699],
          [-75.428728, 38.452671],
          [-75.424831, 38.45261],
          [-75.410884, 38.4524],
          [-75.394786, 38.45216],
          [-75.393563, 38.452114],
          [-75.371054, 38.452107],
          [-75.355797, 38.452008],
          [-75.34125, 38.45197],
          [-75.341247, 38.45197],
          [-75.307011, 38.35476],
          [-75.364641, 38.290359],
          [-75.612764, 38.278626],
          [-75.768671, 38.285183],
          [-75.864628, 38.23832],
          [-75.87031, 38.243425],
          [-75.870318, 38.243432],
          [-75.872464, 38.243968],
          [-75.874653, 38.244514],
          [-75.882873, 38.244449],
          [-75.883435, 38.244445],
          [-75.8852, 38.243395],
          [-75.886841, 38.242418],
          [-75.887409, 38.24208],
          [-75.888513, 38.241423],
          [-75.889356, 38.2395],
          [-75.885676, 38.231006],
          [-75.885909, 38.230866],
          [-75.890669, 38.228009],
          [-75.894583, 38.228439],
          [-75.895689, 38.228561],
          [-75.895879, 38.228717],
          [-75.899781, 38.231921],
          [-75.90004, 38.232133],
          [-75.900661, 38.233206],
          [-75.901381, 38.234448],
          [-75.905852, 38.242165],
          [-75.906016, 38.242447],
          [-75.90845, 38.246648],
          [-75.908272, 38.252045],
          [-75.911143, 38.257951],
          [-75.917297, 38.263126],
          [-75.919446, 38.264056],
          [-75.920279, 38.264415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25025",
      state: "25",
      county: "025",
      name: "Suffolk County",
      censusArea: 58.154,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.042794, 42.276998],
          [-71.055494, 42.275698],
          [-71.053395, 42.272297],
          [-71.061593, 42.267299],
          [-71.065651, 42.271052],
          [-71.082968, 42.268496],
          [-71.088672, 42.269733],
          [-71.102691, 42.259884],
          [-71.112938, 42.258641],
          [-71.130808, 42.22788],
          [-71.191155, 42.283059],
          [-71.178636, 42.294595],
          [-71.164804, 42.303764],
          [-71.178935, 42.314316],
          [-71.156833, 42.330189],
          [-71.167565, 42.333441],
          [-71.174798, 42.350265],
          [-71.167625, 42.360073],
          [-71.148565, 42.361174],
          [-71.143501, 42.364969],
          [-71.130997, 42.373796],
          [-71.117197, 42.367197],
          [-71.117099, 42.355594],
          [-71.099196, 42.352797],
          [-71.077095, 42.358697],
          [-71.069795, 42.369097],
          [-71.064095, 42.368997],
          [-71.080595, 42.380997],
          [-71.073496, 42.391796],
          [-71.055295, 42.387097],
          [-71.033203, 42.414226],
          [-71.020377, 42.43825],
          [-71.025777, 42.444753],
          [-70.982294, 42.431296],
          [-70.960647, 42.443787],
          [-70.960835, 42.441272],
          [-70.982994, 42.423996],
          [-70.987694, 42.416696],
          [-70.990595, 42.407098],
          [-70.989195, 42.402598],
          [-70.985068, 42.402041],
          [-70.983426, 42.396246],
          [-70.980336, 42.391513],
          [-70.972706, 42.389968],
          [-70.970195, 42.388036],
          [-70.97174, 42.387071],
          [-70.972513, 42.385042],
          [-70.972706, 42.381759],
          [-70.972223, 42.377316],
          [-70.960798, 42.360648],
          [-70.953292, 42.349698],
          [-70.953022, 42.343973],
          [-70.963578, 42.34686],
          [-70.972418, 42.353875],
          [-70.974897, 42.355843],
          [-70.979927, 42.356382],
          [-70.982282, 42.35592],
          [-70.998253, 42.352788],
          [-71.006877, 42.347039],
          [-71.010146, 42.339234],
          [-71.011804, 42.335274],
          [-71.01568, 42.326019],
          [-71.013165, 42.315419],
          [-71.005399, 42.307196],
          [-71.041694, 42.305298],
          [-71.042794, 42.276998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26001",
      state: "26",
      county: "001",
      name: "Alcona County",
      censusArea: 674.585,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.321093, 44.858494],
          [-83.321237, 44.853102],
          [-83.321241, 44.852962],
          [-83.321089, 44.852723],
          [-83.318373, 44.84844],
          [-83.314429, 44.84222],
          [-83.31433, 44.842131],
          [-83.31294, 44.840882],
          [-83.312831, 44.840783],
          [-83.312478, 44.840466],
          [-83.308578, 44.83696],
          [-83.306854, 44.83541],
          [-83.300648, 44.829831],
          [-83.299737, 44.823362],
          [-83.298618, 44.815416],
          [-83.29553, 44.793486],
          [-83.295518, 44.793397],
          [-83.29565, 44.790237],
          [-83.295696, 44.789112],
          [-83.296125, 44.778828],
          [-83.296969, 44.758543],
          [-83.296971, 44.758495],
          [-83.298021, 44.755632],
          [-83.298287, 44.754907],
          [-83.298237, 44.754465],
          [-83.297797, 44.750547],
          [-83.297526, 44.74814],
          [-83.29742, 44.747203],
          [-83.2973, 44.746134],
          [-83.296265, 44.743502],
          [-83.290944, 44.729974],
          [-83.290665, 44.729265],
          [-83.288214, 44.726453],
          [-83.284724, 44.72245],
          [-83.284128, 44.721766],
          [-83.283098, 44.721011],
          [-83.277213, 44.7167],
          [-83.274747, 44.714893],
          [-83.274103, 44.714421],
          [-83.273393, 44.713901],
          [-83.274674, 44.70477],
          [-83.275078, 44.701891],
          [-83.275281, 44.700441],
          [-83.275318, 44.700177],
          [-83.2755, 44.698882],
          [-83.275543, 44.698574],
          [-83.275601, 44.698161],
          [-83.276137, 44.69434],
          [-83.276836, 44.689354],
          [-83.277476, 44.687507],
          [-83.279129, 44.682735],
          [-83.279288, 44.682278],
          [-83.285992, 44.662927],
          [-83.286874, 44.66038],
          [-83.287211, 44.659409],
          [-83.287585, 44.658328],
          [-83.287802, 44.657703],
          [-83.289442, 44.652968],
          [-83.295377, 44.64536],
          [-83.307116, 44.630313],
          [-83.307504, 44.629816],
          [-83.309222, 44.62465],
          [-83.309802, 44.622906],
          [-83.309869, 44.622703],
          [-83.31445, 44.608926],
          [-83.314517, 44.608725],
          [-83.314534, 44.608512],
          [-83.315037, 44.60219],
          [-83.31504, 44.602152],
          [-83.315277, 44.599178],
          [-83.315435, 44.597193],
          [-83.315603, 44.595079],
          [-83.315503, 44.593514],
          [-83.314127, 44.572043],
          [-83.314098, 44.571592],
          [-83.314013, 44.570266],
          [-83.313925, 44.568888],
          [-83.313893, 44.568395],
          [-83.313813, 44.567148],
          [-83.313792, 44.566822],
          [-83.313744, 44.566076],
          [-83.313649, 44.564588],
          [-83.313623, 44.564498],
          [-83.309234, 44.549444],
          [-83.309117, 44.549043],
          [-83.308918, 44.54836],
          [-83.308906, 44.54814],
          [-83.308471, 44.539902],
          [-83.309385, 44.537528],
          [-83.310185, 44.535448],
          [-83.310376, 44.534952],
          [-83.311245, 44.532693],
          [-83.311529, 44.531956],
          [-83.318104, 44.514871],
          [-83.318279, 44.514416],
          [-83.318276, 44.514294],
          [-83.318216, 44.511734],
          [-83.886634, 44.508973],
          [-83.888484, 44.856458],
          [-83.321093, 44.858494]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41015",
      state: "41",
      county: "015",
      name: "Curry County",
      censusArea: 1627.46,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-124.479344, 42.95497],
          [-124.257111, 42.954757],
          [-124.141977, 42.849334],
          [-124.132985, 42.666379],
          [-123.812093, 42.789433],
          [-123.777322, 42.798988],
          [-123.727589, 42.780135],
          [-123.71619, 42.784234],
          [-123.831762, 42.632561],
          [-123.885746, 42.504233],
          [-124.008397, 42.496352],
          [-124.041983, 42.387656],
          [-124.025894, 42.35913],
          [-123.892189, 42.352831],
          [-123.839687, 42.311532],
          [-123.791241, 42.236398],
          [-123.856786, 42.141132],
          [-123.821472, 41.995473],
          [-124.086661, 41.996869],
          [-124.087827, 41.996891],
          [-124.100216, 41.996842],
          [-124.100921, 41.996956],
          [-124.211605, 41.99846],
          [-124.214213, 42.005939],
          [-124.270464, 42.045553],
          [-124.287374, 42.046016],
          [-124.299649, 42.051736],
          [-124.314289, 42.067864],
          [-124.34101, 42.092929],
          [-124.356229, 42.114952],
          [-124.357122, 42.118016],
          [-124.351535, 42.129796],
          [-124.351784, 42.134965],
          [-124.355696, 42.141964],
          [-124.361563, 42.143767],
          [-124.366028, 42.152343],
          [-124.366832, 42.15845],
          [-124.363389, 42.158588],
          [-124.360318, 42.162272],
          [-124.361009, 42.180752],
          [-124.367751, 42.188321],
          [-124.373175, 42.190218],
          [-124.374949, 42.193129],
          [-124.376215, 42.196381],
          [-124.375553, 42.20882],
          [-124.377762, 42.218809],
          [-124.383633, 42.22716],
          [-124.410982, 42.250547],
          [-124.411534, 42.254115],
          [-124.408514, 42.260588],
          [-124.405148, 42.278107],
          [-124.410556, 42.307431],
          [-124.429288, 42.331746],
          [-124.427222, 42.33488],
          [-124.425554, 42.351874],
          [-124.424066, 42.377242],
          [-124.424863, 42.395426],
          [-124.428068, 42.420333],
          [-124.434882, 42.434916],
          [-124.435105, 42.440163],
          [-124.422038, 42.461226],
          [-124.423084, 42.478952],
          [-124.421381, 42.491737],
          [-124.399065, 42.539928],
          [-124.390664, 42.566593],
          [-124.389977, 42.574758],
          [-124.400918, 42.597518],
          [-124.399421, 42.618079],
          [-124.401177, 42.627192],
          [-124.413119, 42.657934],
          [-124.416774, 42.661594],
          [-124.45074, 42.675798],
          [-124.451484, 42.677787],
          [-124.447487, 42.68474],
          [-124.448418, 42.689909],
          [-124.473864, 42.732671],
          [-124.491679, 42.741789],
          [-124.498473, 42.741077],
          [-124.499122, 42.738606],
          [-124.510017, 42.734746],
          [-124.513368, 42.735068],
          [-124.514669, 42.736806],
          [-124.516236, 42.753632],
          [-124.524439, 42.789793],
          [-124.536073, 42.814175],
          [-124.544179, 42.822958],
          [-124.552441, 42.840568],
          [-124.500141, 42.917502],
          [-124.480938, 42.951495],
          [-124.479344, 42.95497]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41025",
      state: "41",
      county: "025",
      name: "Harney County",
      censusArea: 10133.17,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.197189, 41.996995],
          [-118.775869, 41.992692],
          [-118.777228, 41.992671],
          [-119.324182, 41.994278],
          [-119.360177, 41.994384],
          [-119.365284, 42.749038],
          [-119.943888, 42.74632],
          [-119.932439, 43.178982],
          [-119.896776, 43.179006],
          [-119.896365, 43.610259],
          [-119.898172, 43.698323],
          [-119.777526, 43.698081],
          [-119.775653, 43.959068],
          [-119.657577, 43.959051],
          [-118.816649, 43.959975],
          [-118.81625, 44.047678],
          [-118.586746, 44.040078],
          [-118.227435, 44.039981],
          [-118.231926, 43.77898],
          [-118.214725, 42.276029],
          [-118.195361, 42.275869],
          [-118.197189, 41.996995]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26089",
      state: "26",
      county: "089",
      name: "Leelanau County",
      censusArea: 347.171,
      stateName: "Michigan"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-85.640216, 44.775051],
            [-85.81512, 44.774525],
            [-86.073073, 44.778393],
            [-86.071746, 44.804717],
            [-86.065966, 44.821522],
            [-86.066031, 44.834852],
            [-86.071112, 44.86542],
            [-86.072468, 44.884788],
            [-86.07099, 44.895876],
            [-86.066745, 44.905685],
            [-86.058862, 44.911012],
            [-86.038332, 44.915696],
            [-86.031194, 44.907349],
            [-86.021513, 44.902774],
            [-86.009355, 44.899454],
            [-85.992535, 44.900026],
            [-85.980219, 44.906136],
            [-85.972824, 44.914781],
            [-85.967169, 44.929484],
            [-85.961603, 44.935567],
            [-85.952721, 44.940758],
            [-85.942099, 44.954317],
            [-85.938589, 44.964559],
            [-85.9316, 44.968788],
            [-85.915851, 44.968307],
            [-85.897626, 44.962014],
            [-85.891543, 44.957783],
            [-85.879934, 44.943305],
            [-85.869852, 44.939031],
            [-85.854304, 44.938147],
            [-85.83615, 44.940256],
            [-85.815451, 44.945631],
            [-85.807403, 44.949814],
            [-85.780439, 44.977932],
            [-85.778278, 44.983075],
            [-85.776207, 45.000574],
            [-85.771395, 45.015181],
            [-85.761943, 45.023454],
            [-85.746444, 45.051229],
            [-85.740836, 45.055575],
            [-85.712262, 45.065622],
            [-85.695715, 45.076461],
            [-85.681096, 45.092693],
            [-85.675671, 45.10554],
            [-85.674861, 45.116216],
            [-85.656024, 45.145788],
            [-85.618639, 45.186771],
            [-85.613174, 45.184624],
            [-85.611684, 45.181104],
            [-85.606963, 45.178477],
            [-85.593064, 45.178527],
            [-85.585986, 45.180381],
            [-85.564654, 45.192546],
            [-85.561809, 45.200524],
            [-85.551072, 45.210742],
            [-85.540497, 45.210169],
            [-85.526734, 45.189316],
            [-85.531461, 45.177247],
            [-85.536892, 45.173385],
            [-85.552179, 45.167352],
            [-85.56168, 45.15894],
            [-85.562104, 45.156954],
            [-85.563102, 45.155358],
            [-85.5639, 45.154361],
            [-85.564897, 45.153962],
            [-85.566493, 45.153762],
            [-85.568489, 45.153762],
            [-85.570178, 45.155145],
            [-85.573893, 45.155488],
            [-85.590434, 45.153175],
            [-85.599801, 45.149286],
            [-85.614319, 45.127562],
            [-85.609266, 45.11351],
            [-85.583198, 45.071304],
            [-85.573353, 45.068382],
            [-85.566066, 45.059201],
            [-85.56613, 45.043633],
            [-85.57016, 45.041278],
            [-85.573976, 45.043361],
            [-85.597181, 45.040547],
            [-85.599652, 45.021749],
            [-85.609123, 45.013103],
            [-85.621878, 45.004529],
            [-85.606588, 44.990662],
            [-85.604301, 44.990983],
            [-85.602356, 44.974272],
            [-85.602034, 44.926743],
            [-85.621403, 44.923123],
            [-85.625497, 44.921107],
            [-85.639842, 44.890255],
            [-85.645456, 44.883645],
            [-85.648932, 44.87401],
            [-85.652355, 44.849092],
            [-85.651435, 44.831624],
            [-85.641652, 44.810816],
            [-85.637, 44.790078],
            [-85.640781, 44.775561],
            [-85.640216, 44.775051]
          ]
        ],
        [
          [
            [-86.093536, 45.007838],
            [-86.115699, 44.999093],
            [-86.133655, 44.996874],
            [-86.154824, 45.002394],
            [-86.156689, 45.010535],
            [-86.154557, 45.018102],
            [-86.141644, 45.040251],
            [-86.138095, 45.043038],
            [-86.117908, 45.048478],
            [-86.093166, 45.041492],
            [-86.079103, 45.030795],
            [-86.093451, 45.03166],
            [-86.097094, 45.030128],
            [-86.100315, 45.02624],
            [-86.101894, 45.022811],
            [-86.101214, 45.018101],
            [-86.093536, 45.007838]
          ]
        ],
        [
          [
            [-86.033174, 45.15842],
            [-86.005946, 45.155751],
            [-85.993194, 45.152805],
            [-85.989412, 45.151069],
            [-85.976803, 45.138363],
            [-85.976434, 45.120706],
            [-85.980433, 45.113046],
            [-85.984095, 45.087073],
            [-85.982799, 45.080787],
            [-85.977082, 45.072993],
            [-85.976883, 45.06266],
            [-85.99736, 45.055929],
            [-86.013073, 45.063774],
            [-86.019874, 45.071665],
            [-86.037129, 45.086576],
            [-86.052424, 45.095311],
            [-86.058653, 45.100776],
            [-86.060396, 45.104617],
            [-86.065016, 45.140266],
            [-86.059393, 45.152291],
            [-86.050473, 45.158418],
            [-86.04443, 45.159582],
            [-86.033174, 45.15842]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26105",
      state: "26",
      county: "105",
      name: "Mason County",
      censusArea: 495.073,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.431064, 43.819406],
          [-86.431172, 43.836638],
          [-86.431176, 43.837177],
          [-86.431182, 43.838119],
          [-86.431198, 43.84072],
          [-86.431238, 43.840941],
          [-86.432894, 43.850013],
          [-86.433471, 43.853176],
          [-86.433915, 43.855608],
          [-86.434078, 43.85609],
          [-86.434258, 43.856623],
          [-86.434985, 43.858771],
          [-86.445455, 43.889726],
          [-86.44573, 43.892897],
          [-86.445938, 43.89529],
          [-86.446463, 43.901349],
          [-86.447915, 43.918089],
          [-86.448009, 43.918416],
          [-86.448147, 43.918897],
          [-86.44955, 43.92377],
          [-86.451275, 43.929763],
          [-86.454401, 43.940624],
          [-86.462756, 43.969655],
          [-86.463087, 43.970807],
          [-86.463136, 43.970976],
          [-86.46322, 43.971101],
          [-86.481606, 43.998599],
          [-86.483331, 44.001179],
          [-86.484399, 44.002382],
          [-86.501738, 44.021912],
          [-86.502998, 44.023839],
          [-86.504133, 44.025575],
          [-86.50444, 44.026044],
          [-86.508827, 44.032755],
          [-86.514742, 44.04792],
          [-86.514704, 44.057672],
          [-86.514702, 44.058119],
          [-86.514573, 44.05833],
          [-86.509052, 44.067408],
          [-86.508764, 44.067881],
          [-86.508415, 44.068206],
          [-86.501243, 44.074873],
          [-86.500453, 44.075607],
          [-86.497937, 44.077033],
          [-86.469295, 44.093267],
          [-86.454895, 44.101429],
          [-86.447678, 44.105519],
          [-86.447126, 44.105832],
          [-86.446883, 44.10597],
          [-86.446609, 44.106193],
          [-86.429871, 44.119782],
          [-86.426873, 44.123099],
          [-86.425876, 44.124203],
          [-86.425162, 44.124993],
          [-86.421576, 44.128962],
          [-86.421108, 44.12948],
          [-86.400645, 44.156848],
          [-86.397874, 44.161239],
          [-86.397023, 44.162589],
          [-86.3965, 44.163418],
          [-86.386867, 44.178685],
          [-86.041988, 44.167091],
          [-86.038948, 43.818977],
          [-86.037884, 43.815611],
          [-86.416471, 43.819572],
          [-86.431064, 43.819406]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28147",
      state: "28",
      county: "147",
      name: "Walthall County",
      censusArea: 403.944,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.040466, 31.336014],
          [-90.031896, 31.175727],
          [-89.938108, 31.118956],
          [-89.835873, 31.074926],
          [-89.835908, 31.002059],
          [-89.856862, 31.002075],
          [-89.892708, 31.001759],
          [-89.897516, 31.001913],
          [-89.923119, 31.001446],
          [-89.927161, 31.001437],
          [-89.972802, 31.001392],
          [-89.97543, 31.001692],
          [-90.005332, 31.001364],
          [-90.022185, 31.001302],
          [-90.029574, 31.00119],
          [-90.050706, 31.001215],
          [-90.128406, 31.001047],
          [-90.131395, 31.000924],
          [-90.164278, 31.001025],
          [-90.164676, 31.00098],
          [-90.259555, 31.000657],
          [-90.260391, 31.350274],
          [-90.24389, 31.350274],
          [-90.040466, 31.336014]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42107",
      state: "42",
      county: "107",
      name: "Schuylkill County",
      censusArea: 778.634,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.996233, 40.629971],
          [-76.012226, 40.577713],
          [-76.171624, 40.534947],
          [-76.440181, 40.495952],
          [-76.535414, 40.555157],
          [-76.535954, 40.555499],
          [-76.537576, 40.556525],
          [-76.538117, 40.556868],
          [-76.539858, 40.557915],
          [-76.543218, 40.559937],
          [-76.545071, 40.561076],
          [-76.54514, 40.561118],
          [-76.546803, 40.562141],
          [-76.547134, 40.562344],
          [-76.548127, 40.562955],
          [-76.54846, 40.56316],
          [-76.549423, 40.563753],
          [-76.552318, 40.565533],
          [-76.553283, 40.566127],
          [-76.555148, 40.567273],
          [-76.556354, 40.568014],
          [-76.560743, 40.570712],
          [-76.560911, 40.570815],
          [-76.562609, 40.571859],
          [-76.562649, 40.571883],
          [-76.563068, 40.572141],
          [-76.564446, 40.572989],
          [-76.564906, 40.573272],
          [-76.565051, 40.573361],
          [-76.565486, 40.573629],
          [-76.565631, 40.573719],
          [-76.566393, 40.574189],
          [-76.568682, 40.575599],
          [-76.569445, 40.57607],
          [-76.56948, 40.576092],
          [-76.570965, 40.577007],
          [-76.571818, 40.577533],
          [-76.575216, 40.579566],
          [-76.575558, 40.57977],
          [-76.577073, 40.580677],
          [-76.577091, 40.580688],
          [-76.577765, 40.581091],
          [-76.579789, 40.582303],
          [-76.580464, 40.582707],
          [-76.580763, 40.582886],
          [-76.581662, 40.583423],
          [-76.581962, 40.583603],
          [-76.58225, 40.583775],
          [-76.583115, 40.584293],
          [-76.583404, 40.584466],
          [-76.584014, 40.584831],
          [-76.58448, 40.58511],
          [-76.585822, 40.585963],
          [-76.586423, 40.586345],
          [-76.588384, 40.587592],
          [-76.59427, 40.591333],
          [-76.596232, 40.59258],
          [-76.596422, 40.592701],
          [-76.596992, 40.593064],
          [-76.597183, 40.593185],
          [-76.597468, 40.59336],
          [-76.598325, 40.593885],
          [-76.598611, 40.594061],
          [-76.600084, 40.594964],
          [-76.601283, 40.595723],
          [-76.607903, 40.599915],
          [-76.609284, 40.600739],
          [-76.609607, 40.600932],
          [-76.611987, 40.602354],
          [-76.612026, 40.602377],
          [-76.612146, 40.602449],
          [-76.612186, 40.602473],
          [-76.612414, 40.602609],
          [-76.612764, 40.602846],
          [-76.614473, 40.604007],
          [-76.61461, 40.6041],
          [-76.615053, 40.60438],
          [-76.615405, 40.604603],
          [-76.616465, 40.605273],
          [-76.616703, 40.605423],
          [-76.616819, 40.605497],
          [-76.616824, 40.6055],
          [-76.617203, 40.605739],
          [-76.618353, 40.606466],
          [-76.618738, 40.606709],
          [-76.619966, 40.607481],
          [-76.622898, 40.609324],
          [-76.623653, 40.609799],
          [-76.624686, 40.610449],
          [-76.624883, 40.610572],
          [-76.625287, 40.610824],
          [-76.626499, 40.611582],
          [-76.626903, 40.611835],
          [-76.629437, 40.61342],
          [-76.637041, 40.618175],
          [-76.639576, 40.61976],
          [-76.639697, 40.619836],
          [-76.639877, 40.619948],
          [-76.64078, 40.620513],
          [-76.641082, 40.620702],
          [-76.641102, 40.620714],
          [-76.641162, 40.620752],
          [-76.641183, 40.620765],
          [-76.641259, 40.620812],
          [-76.641433, 40.620922],
          [-76.641488, 40.620955],
          [-76.641566, 40.621001],
          [-76.64373, 40.622304],
          [-76.648348, 40.625086],
          [-76.650217, 40.626228],
          [-76.652374, 40.627546],
          [-76.653822, 40.628431],
          [-76.655755, 40.629613],
          [-76.658169, 40.631089],
          [-76.659618, 40.631975],
          [-76.660405, 40.632455],
          [-76.662765, 40.633898],
          [-76.663552, 40.634379],
          [-76.663554, 40.63438],
          [-76.663935, 40.634624],
          [-76.665083, 40.635359],
          [-76.665355, 40.635533],
          [-76.665469, 40.635601],
          [-76.666982, 40.636502],
          [-76.668823, 40.637599],
          [-76.671471, 40.639296],
          [-76.672833, 40.640169],
          [-76.672894, 40.640208],
          [-76.672956, 40.640248],
          [-76.673026, 40.640292],
          [-76.673096, 40.640337],
          [-76.674304, 40.6411],
          [-76.678351, 40.643656],
          [-76.6797, 40.644509],
          [-76.681741, 40.645797],
          [-76.685028, 40.647873],
          [-76.687847, 40.649693],
          [-76.689876, 40.651003],
          [-76.68995, 40.651051],
          [-76.690172, 40.651195],
          [-76.690247, 40.651244],
          [-76.690289, 40.651271],
          [-76.690398, 40.65134],
          [-76.690418, 40.651353],
          [-76.690461, 40.651381],
          [-76.69056, 40.651445],
          [-76.690746, 40.651565],
          [-76.691603, 40.652118],
          [-76.691889, 40.652303],
          [-76.693145, 40.653057],
          [-76.696916, 40.655321],
          [-76.698173, 40.656076],
          [-76.698863, 40.656477],
          [-76.700933, 40.65768],
          [-76.701507, 40.658014],
          [-76.701566, 40.658048],
          [-76.701624, 40.658082],
          [-76.380152, 40.775511],
          [-76.30717, 40.801809],
          [-76.284611, 40.883588],
          [-76.207827, 40.94974],
          [-76.02815, 40.901986],
          [-75.997348, 40.912985],
          [-75.757807, 40.735414],
          [-75.891473, 40.67727],
          [-75.993212, 40.639712],
          [-75.996233, 40.629971]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42127",
      state: "42",
      county: "127",
      name: "Wayne County",
      censusArea: 725.604,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.505607, 41.232539],
          [-75.441421, 41.260053],
          [-75.462411, 41.641587],
          [-75.48315, 41.999259],
          [-75.477144, 41.999407],
          [-75.436216, 41.999353],
          [-75.431961, 41.999363],
          [-75.359579, 41.999445],
          [-75.341868, 41.993262],
          [-75.292589, 41.953897],
          [-75.279094, 41.938917],
          [-75.263005, 41.885109],
          [-75.146446, 41.850899],
          [-75.140241, 41.852078],
          [-75.114399, 41.843583],
          [-75.090799, 41.811991],
          [-75.053431, 41.752538],
          [-75.048199, 41.632011],
          [-75.053077, 41.618552],
          [-75.050074, 41.606893],
          [-75.311143, 41.331439],
          [-75.359184, 41.239206],
          [-75.505607, 41.232539]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42129",
      state: "42",
      county: "129",
      name: "Westmoreland County",
      censusArea: 1027.554,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.703834, 40.443526],
          [-79.702131, 40.489635],
          [-79.701985, 40.523787],
          [-79.701624, 40.525449],
          [-79.765415, 40.549854],
          [-79.692587, 40.669732],
          [-79.638137, 40.665601],
          [-79.56111, 40.564113],
          [-79.450176, 40.530149],
          [-79.454011, 40.485507],
          [-79.250605, 40.428726],
          [-79.247568, 40.43208],
          [-79.240175, 40.429991],
          [-79.233203, 40.430741],
          [-79.214783, 40.424458],
          [-79.206513, 40.415032],
          [-79.199233, 40.41078],
          [-79.192722, 40.414862],
          [-79.058481, 40.380868],
          [-78.974649, 40.395972],
          [-79.055983, 40.285089],
          [-79.056646, 40.278604],
          [-79.059378, 40.275767],
          [-79.067963, 40.272471],
          [-79.073523, 40.266727],
          [-79.076638, 40.260307],
          [-79.07957, 40.257928],
          [-79.078084, 40.25484],
          [-79.07925, 40.251172],
          [-79.082472, 40.250074],
          [-79.086734, 40.245342],
          [-79.086174, 40.242665],
          [-79.088656, 40.241259],
          [-79.185222, 40.109848],
          [-79.293682, 40.040413],
          [-79.457008, 40.135108],
          [-79.479402, 40.142801],
          [-79.505188, 40.140568],
          [-79.540347, 40.114035],
          [-79.578966, 40.105496],
          [-79.592602, 40.092005],
          [-79.641098, 40.088317],
          [-79.72224, 40.121141],
          [-79.877385, 40.126792],
          [-79.870585, 40.197415],
          [-79.781761, 40.227711],
          [-79.775139, 40.287888],
          [-79.787853, 40.297693],
          [-79.783684, 40.315913],
          [-79.722151, 40.409098],
          [-79.704739, 40.427231],
          [-79.703834, 40.443526]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49015",
      state: "49",
      county: "015",
      name: "Emery County",
      censusArea: 4462.314,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.025402, 38.499981],
          [-111.305858, 38.499978],
          [-111.305825, 38.510178],
          [-111.29936, 39.032264],
          [-111.301222, 39.467302],
          [-111.247275, 39.467152],
          [-111.247388, 39.704611],
          [-111.159113, 39.595947],
          [-111.131953, 39.613844],
          [-111.0812, 39.467533],
          [-110.024118, 39.468951],
          [-110.024828, 39.46756],
          [-110.02414, 39.461695],
          [-110.010617, 39.416322],
          [-110.07727, 39.275869],
          [-110.05457, 39.228283],
          [-110.098763, 39.189509],
          [-110.150496, 38.998655],
          [-110.097736, 39.001619],
          [-110.005576, 38.949914],
          [-110.150018, 38.980696],
          [-110.179252, 38.907248],
          [-110.083861, 38.751952],
          [-110.113526, 38.706507],
          [-110.036344, 38.652311],
          [-110.05595, 38.604953],
          [-109.996929, 38.608847],
          [-110.057897, 38.58374],
          [-109.994283, 38.527118],
          [-110.025402, 38.499981]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49021",
      state: "49",
      county: "021",
      name: "Iron County",
      censusArea: 3296.681,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.683197, 37.543193],
          [-112.901174, 37.543398],
          [-112.900994, 37.499738],
          [-113.03698, 37.498577],
          [-113.03688, 37.474578],
          [-113.146281, 37.474079],
          [-113.253677, 37.482241],
          [-113.253184, 37.528879],
          [-113.474389, 37.528978],
          [-113.474991, 37.618276],
          [-114.052472, 37.604776],
          [-114.051728, 37.745997],
          [-114.051785, 37.746249],
          [-114.05167, 37.746958],
          [-114.051109, 37.756276],
          [-114.048473, 37.809861],
          [-114.050423, 37.999961],
          [-114.049903, 38.148601],
          [-113.4716, 38.148665],
          [-112.478682, 38.147334],
          [-112.468177, 37.890464],
          [-112.588402, 37.890423],
          [-112.57855, 37.80454],
          [-112.689222, 37.805814],
          [-112.683197, 37.543193]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49025",
      state: "49",
      county: "025",
      name: "Kane County",
      censusArea: 3990.229,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.646394, 37.541063],
          [-110.639097, 37.487091],
          [-110.719198, 37.481391],
          [-110.699197, 37.430691],
          [-110.736098, 37.345192],
          [-110.777799, 37.322992],
          [-110.869804, 37.348691],
          [-110.899517, 37.176153],
          [-110.958333, 37.124438],
          [-111.05767, 37.099395],
          [-111.17835, 37.102613],
          [-111.247488, 37.021604],
          [-111.268784, 37.0542],
          [-111.314856, 37.012133],
          [-111.412784, 37.001478],
          [-112.35769, 37.001025],
          [-112.368946, 37.001125],
          [-112.534545, 37.000684],
          [-112.538593, 37.000674],
          [-112.540368, 37.000669],
          [-112.545094, 37.000734],
          [-112.558974, 37.000692],
          [-112.609787, 37.000753],
          [-112.899366, 37.000319],
          [-112.900994, 37.499738],
          [-112.901174, 37.543398],
          [-112.683197, 37.543193],
          [-112.361187, 37.543118],
          [-112.361333, 37.536425],
          [-111.853181, 37.535578],
          [-110.646394, 37.541063]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47029",
      state: "47",
      county: "029",
      name: "Cocke County",
      censusArea: 434.565,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.276677, 36.007652],
          [-83.234585, 36.085094],
          [-83.166085, 36.180894],
          [-82.898506, 35.9451],
          [-82.913338, 35.924113],
          [-82.89798, 35.881278],
          [-82.918356, 35.845467],
          [-82.962842, 35.795126],
          [-82.964108, 35.7941],
          [-82.98397, 35.77801],
          [-82.995803, 35.773128],
          [-83.100225, 35.774765],
          [-83.100329, 35.774804],
          [-83.100233, 35.774745],
          [-83.164909, 35.759965],
          [-83.240669, 35.72676],
          [-83.251247, 35.719916],
          [-83.255489, 35.714974],
          [-83.310782, 35.895846],
          [-83.276677, 36.007652]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US23027",
      state: "23",
      county: "027",
      name: "Waldo County",
      censusArea: 729.918,
      stateName: "Maine"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-68.942826, 44.281073],
            [-68.919301, 44.309872],
            [-68.919325, 44.335392],
            [-68.911634, 44.365027],
            [-68.90353, 44.378613],
            [-68.87894, 44.386584],
            [-68.868444, 44.38144],
            [-68.860649, 44.364425],
            [-68.864338, 44.355002],
            [-68.87169, 44.344662],
            [-68.883065, 44.338193],
            [-68.888706, 44.338196],
            [-68.89285, 44.334653],
            [-68.896587, 44.321986],
            [-68.88746, 44.303094],
            [-68.899445, 44.283775],
            [-68.904255, 44.279889],
            [-68.916872, 44.242866],
            [-68.92648, 44.233035],
            [-68.945976, 44.220824],
            [-68.95189, 44.218719],
            [-68.94709, 44.226792],
            [-68.955332, 44.243873],
            [-68.959468, 44.247439],
            [-68.965896, 44.249754],
            [-68.967074, 44.251968],
            [-68.965264, 44.259332],
            [-68.953686, 44.272346],
            [-68.942826, 44.281073]
          ]
        ],
        [
          [
            [-69.014167, 44.671794],
            [-68.815793, 44.683754],
            [-68.811815, 44.670465],
            [-68.822416, 44.664005],
            [-68.846945, 44.625307],
            [-68.841821, 44.611062],
            [-68.796206, 44.471437],
            [-68.810059, 44.468737],
            [-68.829153, 44.462242],
            [-68.858993, 44.444923],
            [-68.880271, 44.428112],
            [-68.886491, 44.430676],
            [-68.890826, 44.437995],
            [-68.8928, 44.443415],
            [-68.892073, 44.445075],
            [-68.897104, 44.450643],
            [-68.900934, 44.452062],
            [-68.927452, 44.448039],
            [-68.931934, 44.43869],
            [-68.946582, 44.429108],
            [-68.982449, 44.426195],
            [-68.990767, 44.415033],
            [-68.984404, 44.396879],
            [-68.978815, 44.38634],
            [-68.9673, 44.381106],
            [-68.961111, 44.375076],
            [-68.948164, 44.355882],
            [-68.954465, 44.32405],
            [-68.958889, 44.314353],
            [-68.979005, 44.296327],
            [-69.003682, 44.294582],
            [-69.005071, 44.274071],
            [-69.017051, 44.257086],
            [-69.02859, 44.249139],
            [-69.121622, 44.255644],
            [-69.268882, 44.364653],
            [-69.346616, 44.308337],
            [-69.394361, 44.342934],
            [-69.506382, 44.34287],
            [-69.445498, 44.463839],
            [-69.4354, 44.462422],
            [-69.395987, 44.457078],
            [-69.393123, 44.640375],
            [-69.460606, 44.647054],
            [-69.460558, 44.655611],
            [-69.442936, 44.672381],
            [-69.475599, 44.676678],
            [-69.471712, 44.692899],
            [-69.398412, 44.744559],
            [-69.266785, 44.721928],
            [-69.2052, 44.729586],
            [-69.183732, 44.644023],
            [-69.014167, 44.671794]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31157",
      state: "31",
      county: "157",
      name: "Scott Bluff County",
      censusArea: 739.401,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.05283, 41.697954],
          [-104.052774, 41.733401],
          [-104.053026, 41.885464],
          [-104.052931, 41.906143],
          [-104.052991, 41.914973],
          [-104.052734, 41.973007],
          [-104.052856, 41.975958],
          [-104.05283, 41.9946],
          [-104.052761, 41.994967],
          [-104.052699, 41.998673],
          [-104.052704, 42.001718],
          [-103.401639, 42.00354],
          [-103.390382, 42.003365],
          [-103.363337, 42.00293],
          [-103.370391, 41.69921],
          [-104.05283, 41.697954]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31165",
      state: "31",
      county: "165",
      name: "Sioux County",
      censusArea: 2066.74,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.052704, 42.001718],
          [-104.052729, 42.016318],
          [-104.05288, 42.021761],
          [-104.052967, 42.075004],
          [-104.052954, 42.089077],
          [-104.0526, 42.124963],
          [-104.052738, 42.133769],
          [-104.053001, 42.137254],
          [-104.052547, 42.166801],
          [-104.052761, 42.170278],
          [-104.052793, 42.249962],
          [-104.052776, 42.25822],
          [-104.052775, 42.610813],
          [-104.052775, 42.61159],
          [-104.052773, 42.611766],
          [-104.052586, 42.630917],
          [-104.052741, 42.633982],
          [-104.052583, 42.650062],
          [-104.052809, 42.749966],
          [-104.052863, 42.754569],
          [-104.053127, 43.000585],
          [-103.815573, 43.001279],
          [-103.813939, 43.001378],
          [-103.576966, 43.000746],
          [-103.576329, 43.000807],
          [-103.506556, 43.000771],
          [-103.506151, 43.000771],
          [-103.505219, 43.00077],
          [-103.5051, 43.00077],
          [-103.503232, 42.784787],
          [-103.480403, 42.784966],
          [-103.480082, 42.437267],
          [-103.444532, 42.437354],
          [-103.444108, 42.090708],
          [-103.401639, 42.00354],
          [-104.052704, 42.001718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31175",
      state: "31",
      county: "175",
      name: "Valley County",
      censusArea: 568.048,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.212016, 41.740613],
          [-98.759133, 41.740382],
          [-98.752225, 41.740372],
          [-98.751558, 41.394084],
          [-99.203261, 41.39414],
          [-99.212016, 41.740613]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31177",
      state: "31",
      county: "177",
      name: "Washington County",
      censusArea: 389.958,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.329012, 41.393136],
          [-96.443898, 41.523933],
          [-96.444785, 41.683633],
          [-96.125293, 41.682951],
          [-96.121726, 41.68274],
          [-96.099837, 41.66103],
          [-96.095415, 41.652736],
          [-96.095046, 41.647365],
          [-96.097728, 41.639633],
          [-96.116233, 41.621574],
          [-96.118105, 41.613495],
          [-96.09409, 41.539265],
          [-96.089714, 41.531778],
          [-96.055096, 41.509577],
          [-96.046707, 41.507085],
          [-96.040701, 41.507076],
          [-96.036603, 41.509047],
          [-96.030593, 41.527292],
          [-95.994784, 41.526242],
          [-95.981319, 41.506837],
          [-95.930705, 41.433894],
          [-95.937346, 41.394403],
          [-95.93831, 41.392162],
          [-95.9393, 41.393184],
          [-96.329012, 41.393136]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32001",
      state: "32",
      county: "001",
      name: "Churchill County",
      censusArea: 4930.458,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.775468, 39.093425],
          [-117.865116, 39.073654],
          [-118.753835, 39.07463],
          [-118.754014, 39.112023],
          [-119.118193, 39.388516],
          [-119.059922, 39.740385],
          [-119.190676, 39.631787],
          [-119.189342, 39.65087],
          [-119.170899, 39.788288],
          [-119.208543, 39.809967],
          [-119.225176, 39.999385],
          [-117.736241, 40.002464],
          [-117.734782, 40.002168],
          [-117.7327, 40.002466],
          [-117.541749, 40.001034],
          [-117.46164, 39.886817],
          [-117.495344, 39.748364],
          [-117.461786, 39.622444],
          [-117.485336, 39.545322],
          [-117.59024, 39.517521],
          [-117.656397, 39.468043],
          [-117.646011, 39.426602],
          [-117.759795, 39.369225],
          [-117.714056, 39.245558],
          [-117.806183, 39.145845],
          [-117.775468, 39.093425]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32003",
      state: "32",
      county: "003",
      name: "Clark County",
      censusArea: 7891.431,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-115.845805, 36.120242],
          [-115.845809, 36.121389],
          [-115.844505, 36.122179],
          [-115.845795, 36.123963],
          [-115.845762, 36.170764],
          [-115.895753, 36.172227],
          [-115.895368, 36.580168],
          [-115.896925, 36.842085],
          [-115.740846, 36.853084],
          [-114.762507, 36.853473],
          [-114.762301, 36.843856],
          [-114.050583, 36.843141],
          [-114.050578, 36.800173],
          [-114.050562, 36.656259],
          [-114.046488, 36.473449],
          [-114.046403, 36.371873],
          [-114.048515, 36.289598],
          [-114.047106, 36.250591],
          [-114.046838, 36.194069],
          [-114.060302, 36.189363],
          [-114.063021, 36.186942],
          [-114.120193, 36.102228],
          [-114.125796, 36.077531],
          [-114.138202, 36.041284],
          [-114.148774, 36.02731],
          [-114.15413, 36.023862],
          [-114.21369, 36.015613],
          [-114.233289, 36.014289],
          [-114.238799, 36.014561],
          [-114.252651, 36.020193],
          [-114.263146, 36.025937],
          [-114.292663, 36.051118],
          [-114.303614, 36.066456],
          [-114.307235, 36.076544],
          [-114.310857, 36.083787],
          [-114.328777, 36.105501],
          [-114.365835, 36.133722],
          [-114.381446, 36.141665],
          [-114.513256, 36.151217],
          [-114.572031, 36.15161],
          [-114.631716, 36.142306],
          [-114.736165, 36.104367],
          [-114.744857, 36.098693],
          [-114.755618, 36.087166],
          [-114.743576, 35.983721],
          [-114.731296, 35.945157],
          [-114.70613, 35.878712],
          [-114.704211, 35.851984],
          [-114.705611, 35.848884],
          [-114.70771, 35.811885],
          [-114.700308, 35.700387],
          [-114.689407, 35.651412],
          [-114.676707, 35.640989],
          [-114.657506, 35.618289],
          [-114.653406, 35.610789],
          [-114.653806, 35.59949],
          [-114.671147, 35.520641],
          [-114.674981, 35.510564],
          [-114.676733, 35.499063],
          [-114.677643, 35.489742],
          [-114.662125, 35.444241],
          [-114.652005, 35.429165],
          [-114.627137, 35.409504],
          [-114.604314, 35.353584],
          [-114.595931, 35.325234],
          [-114.569238, 35.18348],
          [-114.569569, 35.163053],
          [-114.572747, 35.138725],
          [-114.578524, 35.12875],
          [-114.5843, 35.124999],
          [-114.59912, 35.12105],
          [-114.621956, 35.094678],
          [-114.635469, 35.028266],
          [-114.633544, 35.015644],
          [-114.633013, 35.002085],
          [-114.804249, 35.139689],
          [-114.80503, 35.140284],
          [-114.925381, 35.237039],
          [-114.92548, 35.237054],
          [-115.145813, 35.413182],
          [-115.146788, 35.413662],
          [-115.160068, 35.424129],
          [-115.160599, 35.424313],
          [-115.225273, 35.475907],
          [-115.271342, 35.51266],
          [-115.303743, 35.538207],
          [-115.388866, 35.605171],
          [-115.391535, 35.607271],
          [-115.393996, 35.609344],
          [-115.404537, 35.617605],
          [-115.406079, 35.618613],
          [-115.647202, 35.808995],
          [-115.647683, 35.809358],
          [-115.648029, 35.809629],
          [-115.689302, 35.842003],
          [-115.84608, 35.963596],
          [-115.845805, 36.120242]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26011",
      state: "26",
      county: "011",
      name: "Arenac County",
      censusArea: 363.191,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.565225, 44.163517],
          [-83.565257, 44.163418],
          [-83.565984, 44.161219],
          [-83.566366, 44.160066],
          [-83.567744, 44.155899],
          [-83.567941, 44.150983],
          [-83.5682, 44.144542],
          [-83.568238, 44.143587],
          [-83.568243, 44.143475],
          [-83.568803, 44.129513],
          [-83.568831, 44.128819],
          [-83.568915, 44.126734],
          [-83.568509, 44.124343],
          [-83.567978, 44.121207],
          [-83.567714, 44.119652],
          [-83.573071, 44.101298],
          [-83.588004, 44.086758],
          [-83.591361, 44.079237],
          [-83.590437, 44.069569],
          [-83.58409, 44.056748],
          [-83.601173, 44.054686],
          [-83.621078, 44.056186],
          [-83.650116, 44.052404],
          [-83.679654, 44.036365],
          [-83.687892, 44.020709],
          [-83.680108, 43.994196],
          [-83.708741, 43.992997],
          [-83.743806, 43.991529],
          [-83.746779, 43.988807],
          [-83.757063, 43.986599],
          [-83.76283, 43.985361],
          [-83.763015, 43.985321],
          [-83.763345, 43.98525],
          [-83.763774, 43.985158],
          [-83.779086, 43.985235],
          [-83.787863, 43.985279],
          [-83.82808, 43.989003],
          [-83.828398, 43.989032],
          [-83.829077, 43.989095],
          [-83.829102, 43.989085],
          [-83.829123, 43.989077],
          [-83.848276, 43.981594],
          [-83.851496, 43.979403],
          [-83.85175, 43.979231],
          [-83.851884, 43.97914],
          [-83.853582, 43.977984],
          [-83.85493, 43.977067],
          [-83.855219, 43.975997],
          [-83.855516, 43.974899],
          [-83.855572, 43.974691],
          [-83.855602, 43.974581],
          [-83.855653, 43.974391],
          [-83.856077, 43.972822],
          [-83.856128, 43.972632],
          [-83.856371, 43.972414],
          [-83.858373, 43.970618],
          [-83.858528, 43.970479],
          [-83.859114, 43.969953],
          [-83.859305, 43.969782],
          [-83.859459, 43.969643],
          [-83.859615, 43.969504],
          [-83.859743, 43.969389],
          [-83.869406, 43.960719],
          [-83.869614, 43.960682],
          [-83.877047, 43.959351],
          [-83.877694, 43.959235],
          [-83.880011, 43.955428],
          [-83.880113, 43.955261],
          [-83.885328, 43.946691],
          [-83.885526, 43.946197],
          [-83.885543, 43.946154],
          [-83.890145, 43.934672],
          [-83.890912, 43.923314],
          [-83.907388, 43.918062],
          [-83.911128, 43.91052],
          [-84.044843, 43.911146],
          [-84.045409, 43.996974],
          [-84.166377, 43.996901],
          [-84.166107, 44.161788],
          [-83.883977, 44.161786],
          [-83.565225, 44.163517]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30067",
      state: "30",
      county: "067",
      name: "Park County",
      censusArea: 2803.063,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.798687, 45.002188],
          [-110.025544, 45.003602],
          [-110.026347, 45.003665],
          [-110.110103, 45.003905],
          [-110.221343, 44.996176],
          [-110.547165, 44.992459],
          [-110.552433, 44.992237],
          [-110.705272, 44.992324],
          [-110.750767, 44.997948],
          [-110.875772, 45.002101],
          [-111.044275, 45.001345],
          [-111.038168, 45.351174],
          [-110.917661, 45.351328],
          [-110.919212, 45.525338],
          [-110.858228, 45.525089],
          [-110.857054, 45.590872],
          [-110.794977, 45.590289],
          [-110.790447, 46.142024],
          [-110.783982, 46.192408],
          [-110.28186, 46.18421],
          [-110.292662, 45.785022],
          [-110.220902, 45.784787],
          [-110.228549, 45.17282],
          [-110.063819, 45.172102],
          [-109.798173, 45.17192],
          [-109.798221, 45.167405],
          [-109.798687, 45.002188]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30073",
      state: "30",
      county: "073",
      name: "Pondera County",
      censusArea: 1622.863,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.408922, 48.219476],
          [-111.409005, 48.21838],
          [-111.409097, 48.132218],
          [-111.408495, 47.987045],
          [-111.983753, 47.984771],
          [-111.9835, 48.043204],
          [-112.047253, 48.086892],
          [-112.177751, 48.087466],
          [-112.177757, 48.130932],
          [-113.016894, 48.13246],
          [-113.231495, 48.170652],
          [-113.236785, 48.247345],
          [-113.34947, 48.310155],
          [-112.575885, 48.309372],
          [-112.576892, 48.483478],
          [-112.229874, 48.483081],
          [-112.184375, 48.477593],
          [-111.990944, 48.438546],
          [-111.990819, 48.394285],
          [-111.79591, 48.393011],
          [-111.796117, 48.349814],
          [-111.665829, 48.349497],
          [-111.6665, 48.219412],
          [-111.408922, 48.219476]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30079",
      state: "30",
      county: "079",
      name: "Prairie County",
      censusArea: 1736.737,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.406237, 47.181563],
          [-105.324817, 47.181586],
          [-105.324898, 46.977161],
          [-105.196735, 46.977172],
          [-105.196945, 46.919179],
          [-105.069484, 46.919128],
          [-105.027522, 46.86149],
          [-104.603766, 46.86088],
          [-104.607017, 46.684926],
          [-104.732843, 46.656264],
          [-104.732853, 46.612672],
          [-104.858698, 46.612479],
          [-104.858857, 46.569114],
          [-104.984754, 46.56945],
          [-104.984714, 46.540606],
          [-105.239439, 46.541005],
          [-105.239175, 46.570089],
          [-105.449006, 46.570916],
          [-105.491952, 46.657985],
          [-105.575683, 46.657889],
          [-105.576256, 46.744935],
          [-105.618076, 46.745025],
          [-105.617595, 46.83209],
          [-106.08667, 46.860264],
          [-106.086777, 47.180938],
          [-105.960094, 47.180981],
          [-105.960127, 47.092969],
          [-105.832846, 47.093144],
          [-105.833042, 47.180967],
          [-105.406237, 47.181563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30081",
      state: "30",
      county: "081",
      name: "Ravalli County",
      censusArea: 2390.821,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.551286, 45.559007],
          [-114.560855, 45.564575],
          [-114.561773, 45.565109],
          [-114.563652, 45.637412],
          [-114.561046, 45.639906],
          [-114.550834, 45.6426],
          [-114.545574, 45.642928],
          [-114.54182, 45.641305],
          [-114.515411, 45.652842],
          [-114.500925, 45.666715],
          [-114.495421, 45.703321],
          [-114.497553, 45.710677],
          [-114.504869, 45.722176],
          [-114.528667, 45.731636],
          [-114.547763, 45.743309],
          [-114.566172, 45.773864],
          [-114.514596, 45.840785],
          [-114.509303, 45.845531],
          [-114.498809, 45.850676],
          [-114.44868, 45.858891],
          [-114.39376, 45.89402],
          [-114.401552, 45.963195],
          [-114.412007, 45.978057],
          [-114.429638, 45.986638],
          [-114.465376, 45.996077],
          [-114.483379, 46.008047],
          [-114.490578, 46.022872],
          [-114.4912, 46.034059],
          [-114.4912, 46.044003],
          [-114.476284, 46.062026],
          [-114.467583, 46.081914],
          [-114.468205, 46.099937],
          [-114.476284, 46.112988],
          [-114.48312, 46.129147],
          [-114.472643, 46.162202],
          [-114.426885, 46.289671],
          [-114.378, 46.435371],
          [-114.385297, 46.467162],
          [-114.346528, 46.535789],
          [-114.331093, 46.607649],
          [-114.331093, 46.630248],
          [-114.337942, 46.641205],
          [-114.341336, 46.642803],
          [-114.332887, 46.660756],
          [-113.827831, 46.661023],
          [-113.813152, 46.588119],
          [-113.875931, 46.508576],
          [-113.750143, 46.27259],
          [-113.821129, 46.212549],
          [-113.753246, 46.1128],
          [-113.803099, 46.0373],
          [-113.744524, 46.048807],
          [-113.648219, 45.965062],
          [-113.517481, 45.939898],
          [-113.516912, 45.93942],
          [-113.516338, 45.938638],
          [-113.532405, 45.878353],
          [-113.697639, 45.843545],
          [-113.9426, 45.686362],
          [-113.971565, 45.700636],
          [-113.987759, 45.705247],
          [-114.015633, 45.696127],
          [-114.019315, 45.692937],
          [-114.022534, 45.67962],
          [-114.03493, 45.647967],
          [-114.135249, 45.557465],
          [-114.187852, 45.542151],
          [-114.247828, 45.545489],
          [-114.309613, 45.469432],
          [-114.350819, 45.468542],
          [-114.36662, 45.491616],
          [-114.460542, 45.561283],
          [-114.473759, 45.563278],
          [-114.514196, 45.564734],
          [-114.551286, 45.559007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30089",
      state: "30",
      county: "089",
      name: "Sanders County",
      censusArea: 2760.524,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.048421, 47.97682],
          [-116.048424, 47.977126],
          [-116.04932, 48.066644],
          [-116.049368, 48.072134],
          [-116.049398, 48.075395],
          [-116.049415, 48.07722],
          [-116.049353, 48.21546],
          [-116.007724, 48.158901],
          [-115.799976, 48.222967],
          [-115.737709, 48.263009],
          [-115.671869, 48.206171],
          [-115.63693, 48.074611],
          [-115.537646, 47.981705],
          [-115.525428, 47.908957],
          [-115.278707, 47.890261],
          [-115.185098, 47.919079],
          [-115.154475, 48.017703],
          [-115.010806, 48.017256],
          [-114.98963, 47.872819],
          [-114.604407, 47.875605],
          [-114.604326, 47.789744],
          [-114.58329, 47.60168],
          [-114.321479, 47.595551],
          [-114.383453, 47.483254],
          [-114.301457, 47.49705],
          [-114.35308, 47.38985],
          [-114.288557, 47.355875],
          [-114.203293, 47.355899],
          [-114.187383, 47.138192],
          [-114.254476, 47.120639],
          [-114.352769, 47.190407],
          [-114.550033, 47.251614],
          [-114.797235, 47.268901],
          [-114.968943, 47.312617],
          [-114.979291, 47.395519],
          [-115.282381, 47.486897],
          [-115.628695, 47.479641],
          [-115.661341, 47.402663],
          [-115.69057, 47.415059],
          [-115.721084, 47.42235],
          [-115.729861, 47.447287],
          [-115.725931, 47.466934],
          [-115.712178, 47.488546],
          [-115.729861, 47.518016],
          [-115.739684, 47.537663],
          [-115.735755, 47.555346],
          [-115.721207, 47.576323],
          [-115.718072, 47.592675],
          [-115.706284, 47.637864],
          [-115.72377, 47.696671],
          [-115.729123, 47.703102],
          [-115.919291, 47.857406],
          [-116.007254, 47.944968],
          [-116.048421, 47.97682]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30095",
      state: "30",
      county: "095",
      name: "Stillwater County",
      censusArea: 1795.35,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.924129, 46.132211],
          [-108.901279, 45.959536],
          [-108.905232, 45.682437],
          [-108.842697, 45.611006],
          [-109.12781, 45.587454],
          [-109.12846, 45.523053],
          [-109.191449, 45.523141],
          [-109.191508, 45.464272],
          [-109.356241, 45.464445],
          [-109.562014, 45.36294],
          [-109.60866, 45.261214],
          [-109.690497, 45.261265],
          [-109.691105, 45.167198],
          [-109.798221, 45.167405],
          [-109.798173, 45.17192],
          [-110.063819, 45.172102],
          [-110.063347, 45.348759],
          [-109.932223, 45.348561],
          [-109.932028, 45.522609],
          [-109.808841, 45.522424],
          [-109.808793, 45.565925],
          [-109.685551, 45.56563],
          [-109.685289, 45.608906],
          [-109.561958, 45.609119],
          [-109.564149, 45.782856],
          [-109.548918, 45.87094],
          [-109.505939, 45.870272],
          [-109.506715, 45.959139],
          [-109.423259, 45.960214],
          [-109.417297, 46.044485],
          [-109.415506, 46.132261],
          [-108.924129, 46.132211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26109",
      state: "26",
      county: "109",
      name: "Menominee County",
      censusArea: 1044.079,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.306122, 45.475513],
          [-87.317416, 45.466712],
          [-87.319703, 45.464929],
          [-87.330262, 45.451011],
          [-87.333147, 45.447208],
          [-87.333407, 45.446056],
          [-87.334033, 45.443273],
          [-87.334249, 45.442315],
          [-87.333309, 45.437269],
          [-87.33324, 45.436897],
          [-87.331107, 45.433673],
          [-87.329958, 45.431937],
          [-87.32864, 45.431331],
          [-87.327367, 45.430745],
          [-87.326855, 45.43051],
          [-87.325834, 45.43004],
          [-87.326069, 45.429459],
          [-87.327749, 45.425307],
          [-87.328248, 45.424716],
          [-87.329505, 45.423229],
          [-87.330882, 45.421598],
          [-87.336152, 45.41536],
          [-87.338083, 45.41436],
          [-87.350852, 45.407743],
          [-87.351306, 45.407328],
          [-87.359512, 45.399829],
          [-87.360078, 45.398512],
          [-87.360529, 45.397464],
          [-87.364368, 45.388532],
          [-87.371282, 45.383738],
          [-87.387851, 45.372251],
          [-87.389181, 45.371329],
          [-87.389478, 45.371123],
          [-87.389849, 45.370866],
          [-87.391446, 45.369759],
          [-87.3925, 45.369028],
          [-87.39452, 45.363702],
          [-87.399973, 45.349322],
          [-87.409987, 45.33892],
          [-87.427379, 45.320854],
          [-87.431684, 45.316383],
          [-87.436128, 45.307705],
          [-87.437257, 45.3055],
          [-87.437507, 45.30367],
          [-87.438908, 45.293405],
          [-87.455519, 45.280736],
          [-87.456414, 45.280053],
          [-87.464794, 45.273662],
          [-87.465201, 45.273351],
          [-87.496869, 45.240364],
          [-87.503302, 45.233662],
          [-87.504186, 45.232742],
          [-87.512336, 45.224252],
          [-87.530499, 45.208056],
          [-87.535346, 45.203734],
          [-87.548964, 45.191591],
          [-87.552468, 45.189767],
          [-87.556327, 45.187759],
          [-87.563417, 45.18407],
          [-87.582324, 45.169039],
          [-87.585651, 45.166394],
          [-87.587822, 45.163591],
          [-87.596556, 45.152316],
          [-87.600796, 45.146842],
          [-87.607812, 45.134833],
          [-87.607958, 45.134583],
          [-87.60928, 45.13232],
          [-87.612019, 45.123377],
          [-87.611814, 45.122406],
          [-87.6115, 45.120915],
          [-87.611497, 45.120901],
          [-87.611216, 45.119568],
          [-87.610073, 45.114141],
          [-87.608951, 45.112886],
          [-87.606757, 45.110433],
          [-87.605193, 45.108684],
          [-87.604464, 45.107868],
          [-87.602542, 45.105719],
          [-87.602217, 45.105356],
          [-87.602179, 45.105314],
          [-87.601981, 45.105092],
          [-87.601649, 45.104721],
          [-87.601086, 45.104092],
          [-87.60012, 45.103011],
          [-87.598689, 45.102052],
          [-87.597665, 45.101365],
          [-87.592318, 45.097779],
          [-87.59027, 45.096406],
          [-87.589396, 45.09649],
          [-87.581969, 45.097206],
          [-87.590208, 45.095264],
          [-87.659952, 45.107512],
          [-87.731389, 45.170989],
          [-87.735282, 45.176565],
          [-87.741732, 45.198201],
          [-87.739492, 45.202126],
          [-87.73171, 45.206792],
          [-87.711722, 45.243481],
          [-87.707779, 45.258343],
          [-87.709137, 45.260341],
          [-87.648126, 45.339396],
          [-87.647729, 45.350721],
          [-87.657349, 45.368752],
          [-87.675017, 45.382454],
          [-87.685934, 45.388711],
          [-87.693956, 45.389893],
          [-87.718891, 45.377462],
          [-87.751452, 45.351755],
          [-87.771384, 45.35121],
          [-87.790324, 45.353444],
          [-87.832612, 45.352249],
          [-87.835303, 45.35098],
          [-87.838141, 45.345101],
          [-87.850133, 45.340435],
          [-87.88517, 45.351736],
          [-87.888052, 45.354697],
          [-87.860432, 45.423504],
          [-87.820348, 45.460284],
          [-87.812976, 45.464159],
          [-87.806347, 45.472262],
          [-87.79896, 45.485147],
          [-87.792769, 45.499967],
          [-87.788798, 45.565947],
          [-87.792016, 45.616756],
          [-87.79588, 45.618846],
          [-87.824102, 45.647138],
          [-87.823672, 45.659817],
          [-87.781623, 45.67328],
          [-87.780808, 45.680349],
          [-87.807144, 45.708014],
          [-87.828827, 45.72276],
          [-87.697159, 45.722987],
          [-87.696946, 45.898842],
          [-87.617613, 45.898842],
          [-87.617039, 45.986006],
          [-87.367843, 45.985313],
          [-87.325725, 45.898623],
          [-87.327472, 45.550507],
          [-87.264446, 45.550118],
          [-87.280709, 45.517624],
          [-87.288726, 45.501606],
          [-87.289443, 45.50053],
          [-87.306122, 45.475513]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28149",
      state: "28",
      county: "149",
      name: "Warren County",
      censusArea: 588.501,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.720102, 32.618858],
          [-90.654589, 32.546044],
          [-90.553821, 32.507574],
          [-90.636162, 32.374498],
          [-90.694517, 32.352996],
          [-90.728622, 32.225119],
          [-90.77482, 32.153993],
          [-90.90774, 32.131888],
          [-90.945094, 32.079921],
          [-91.027946, 32.112722],
          [-91.027535, 32.113431],
          [-91.027125, 32.120434],
          [-91.026931, 32.123737],
          [-91.030152, 32.129821],
          [-91.034447, 32.134832],
          [-91.040531, 32.137337],
          [-91.046615, 32.138768],
          [-91.052342, 32.137337],
          [-91.057487, 32.135515],
          [-91.067004, 32.132144],
          [-91.131403, 32.126213],
          [-91.162822, 32.132694],
          [-91.165452, 32.13429],
          [-91.171702, 32.14425],
          [-91.174552, 32.154978],
          [-91.171046, 32.176526],
          [-91.164171, 32.196888],
          [-91.158026, 32.201956],
          [-91.133587, 32.213432],
          [-91.122602, 32.216929],
          [-91.046507, 32.241149],
          [-91.039007, 32.242349],
          [-91.021507, 32.236149],
          [-91.006306, 32.22415],
          [-91.004557, 32.215381],
          [-90.905173, 32.315497],
          [-90.898588, 32.329094],
          [-90.901511, 32.337864],
          [-90.912363, 32.339454],
          [-90.986672, 32.35176],
          [-91.000106, 32.357695],
          [-91.003506, 32.362145],
          [-91.004506, 32.368144],
          [-90.99408, 32.403862],
          [-91.029606, 32.433542],
          [-91.052907, 32.438442],
          [-91.070207, 32.445141],
          [-91.095308, 32.458741],
          [-91.108808, 32.47204],
          [-91.116008, 32.48314],
          [-91.116708, 32.500139],
          [-91.097878, 32.544752],
          [-91.093741, 32.549128],
          [-91.089436, 32.553306],
          [-91.090611, 32.562705],
          [-91.090771, 32.563978],
          [-90.964043, 32.575605],
          [-90.95124, 32.444389],
          [-90.861364, 32.442115],
          [-90.860566, 32.575173],
          [-90.728344, 32.566265],
          [-90.720102, 32.618858]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28161",
      state: "28",
      county: "161",
      name: "Yalobusha County",
      censusArea: 467.129,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.72105, 34.192545],
          [-89.50944, 34.19096],
          [-89.509748, 34.162082],
          [-89.507353, 33.867469],
          [-89.76905, 33.867334],
          [-89.769296, 33.897137],
          [-89.926099, 33.897957],
          [-89.931485, 34.164303],
          [-89.721431, 34.163513],
          [-89.72105, 34.192545]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29005",
      state: "29",
      county: "005",
      name: "Atchison County",
      censusArea: 547.302,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.709974, 40.523798],
          [-95.74868, 40.524275],
          [-95.75711, 40.52599],
          [-95.76692, 40.531563],
          [-95.769281, 40.536656],
          [-95.774704, 40.573574],
          [-95.773549, 40.578205],
          [-95.765645, 40.585208],
          [-95.746443, 40.584935],
          [-95.6875, 40.584381],
          [-95.687442, 40.58438],
          [-95.64184, 40.584234],
          [-95.611069, 40.583495],
          [-95.574046, 40.582963],
          [-95.554959, 40.582629],
          [-95.533182, 40.582249],
          [-95.526682, 40.582136],
          [-95.525392, 40.58209],
          [-95.469319, 40.58154],
          [-95.415406, 40.581014],
          [-95.373923, 40.580501],
          [-95.373893, 40.580501],
          [-95.357802, 40.5801],
          [-95.335588, 40.579871],
          [-95.221525, 40.578827],
          [-95.218783, 40.578781],
          [-95.217455, 40.578759],
          [-95.213327, 40.578689],
          [-95.212715, 40.578679],
          [-95.21159, 40.578654],
          [-95.211408, 40.57865],
          [-95.202264, 40.578528],
          [-95.177708, 40.478903],
          [-95.180614, 40.261706],
          [-95.552314, 40.264443],
          [-95.550966, 40.285947],
          [-95.553292, 40.291158],
          [-95.622684, 40.342323],
          [-95.68728, 40.465059],
          [-95.692665, 40.483022],
          [-95.681409, 40.49082],
          [-95.659281, 40.519808],
          [-95.696673, 40.545137],
          [-95.697281, 40.536985],
          [-95.709974, 40.523798]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29011",
      state: "29",
      county: "011",
      name: "Barton County",
      censusArea: 591.921,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.082083, 37.349291],
          [-94.617538, 37.364167],
          [-94.617537, 37.364355],
          [-94.617626, 37.367445],
          [-94.617625, 37.367576],
          [-94.617557, 37.396375],
          [-94.617511, 37.410909],
          [-94.617265, 37.425536],
          [-94.617132, 37.439818],
          [-94.617201, 37.454788],
          [-94.617205, 37.460373],
          [-94.617222, 37.460476],
          [-94.61718, 37.465203],
          [-94.617183, 37.469665],
          [-94.617023, 37.483765],
          [-94.616789, 37.52151],
          [-94.616908, 37.527804],
          [-94.617186, 37.553485],
          [-94.61716, 37.557308],
          [-94.617081, 37.567013],
          [-94.617315, 37.571499],
          [-94.617283, 37.571896],
          [-94.617428, 37.609522],
          [-94.6173, 37.610495],
          [-94.617477, 37.63717],
          [-94.617575, 37.653577],
          [-94.073524, 37.639722],
          [-94.075385, 37.58157],
          [-94.082083, 37.349291]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29019",
      state: "29",
      county: "019",
      name: "Boone County",
      censusArea: 685.414,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.430229, 39.248795],
          [-92.314471, 39.246454],
          [-92.104374, 39.239809],
          [-92.110387, 39.064204],
          [-92.131512, 39.064484],
          [-92.17095, 38.7653],
          [-92.220661, 38.643167],
          [-92.350605, 38.676398],
          [-92.394866, 38.73913],
          [-92.379614, 38.800076],
          [-92.498377, 38.922084],
          [-92.508657, 38.93271],
          [-92.514987, 38.938114],
          [-92.526437, 38.944776],
          [-92.540118, 38.953718],
          [-92.544946, 38.959691],
          [-92.558086, 38.970692],
          [-92.565124, 38.998274],
          [-92.430229, 39.248795]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29025",
      state: "29",
      county: "025",
      name: "Caldwell County",
      censusArea: 426.393,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.758992, 39.784674],
          [-93.758357, 39.611407],
          [-93.759183, 39.524558],
          [-94.208423, 39.5272],
          [-94.205961, 39.745841],
          [-94.205728, 39.756815],
          [-94.205432, 39.760821],
          [-94.205884, 39.767152],
          [-94.205844, 39.788954],
          [-93.758992, 39.784674]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39085",
      state: "39",
      county: "085",
      name: "Lake County",
      censusArea: 227.493,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.391005, 41.569716],
          [-81.408992, 41.56985],
          [-81.41763, 41.56967],
          [-81.419936, 41.56995],
          [-81.487842, 41.570051],
          [-81.488139, 41.577328],
          [-81.488062, 41.598556],
          [-81.488934, 41.627948],
          [-81.48864, 41.631348],
          [-81.486919, 41.632704],
          [-81.477544, 41.640086],
          [-81.466038, 41.649148],
          [-81.452461, 41.663139],
          [-81.447734, 41.668011],
          [-81.443647, 41.672222],
          [-81.442843, 41.673051],
          [-81.44272, 41.673178],
          [-81.442645, 41.673255],
          [-81.442552, 41.673314],
          [-81.441803, 41.673783],
          [-81.441339, 41.674074],
          [-81.438971, 41.67556],
          [-81.437809, 41.676289],
          [-81.437014, 41.676788],
          [-81.435582, 41.677686],
          [-81.431441, 41.680285],
          [-81.413062, 41.691816],
          [-81.407984, 41.695002],
          [-81.406098, 41.696185],
          [-81.404476, 41.697203],
          [-81.402417, 41.698495],
          [-81.400686, 41.699581],
          [-81.400617, 41.699624],
          [-81.388632, 41.707144],
          [-81.388269, 41.707355],
          [-81.380935, 41.711622],
          [-81.374509, 41.715361],
          [-81.374454, 41.715393],
          [-81.373666, 41.715852],
          [-81.372587, 41.71648],
          [-81.368913, 41.718618],
          [-81.36867, 41.718759],
          [-81.367498, 41.719441],
          [-81.353229, 41.727743],
          [-81.330154, 41.737938],
          [-81.309499, 41.747064],
          [-81.30701, 41.748164],
          [-81.306495, 41.748392],
          [-81.301626, 41.750543],
          [-81.288892, 41.758945],
          [-81.286925, 41.760243],
          [-81.279925, 41.759944],
          [-81.279187, 41.759859],
          [-81.265576, 41.758298],
          [-81.264224, 41.758143],
          [-81.263751, 41.758239],
          [-81.26311, 41.758369],
          [-81.259349, 41.75913],
          [-81.25552, 41.759905],
          [-81.252977, 41.76042],
          [-81.251918, 41.760634],
          [-81.248672, 41.761291],
          [-81.248609, 41.761316],
          [-81.247832, 41.761623],
          [-81.247607, 41.761712],
          [-81.202359, 41.77957],
          [-81.201721, 41.779822],
          [-81.192684, 41.783389],
          [-81.184368, 41.786671],
          [-81.183328, 41.78712],
          [-81.167638, 41.793903],
          [-81.122295, 41.813503],
          [-81.112885, 41.817571],
          [-81.098931, 41.821319],
          [-81.095592, 41.822216],
          [-81.092716, 41.822988],
          [-81.05192, 41.839557],
          [-81.024525, 41.846469],
          [-81.01049, 41.853962],
          [-81.002663, 41.854846],
          [-81.002117, 41.854908],
          [-81.004763, 41.726436],
          [-81.003631, 41.715137],
          [-81.102258, 41.714307],
          [-81.101422, 41.641457],
          [-81.294669, 41.640889],
          [-81.29588, 41.56995],
          [-81.391005, 41.569716]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39095",
      state: "39",
      county: "095",
      name: "Lucas County",
      censusArea: 340.855,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.453832, 41.732647],
          [-83.455626, 41.727445],
          [-83.449001, 41.710719],
          [-83.446032, 41.706847],
          [-83.409531, 41.691247],
          [-83.39263, 41.691947],
          [-83.37573, 41.686647],
          [-83.357073, 41.687763],
          [-83.341817, 41.693518],
          [-83.337985, 41.698682],
          [-83.337977, 41.70341],
          [-83.326825, 41.701562],
          [-83.298731, 41.683871],
          [-83.29561, 41.681905],
          [-83.293928, 41.680846],
          [-83.29068, 41.676794],
          [-83.289682, 41.676409],
          [-83.288469, 41.675941],
          [-83.287125, 41.675423],
          [-83.285181, 41.674673],
          [-83.278455, 41.672078],
          [-83.238191, 41.651167],
          [-83.23166, 41.644218],
          [-83.194524, 41.631008],
          [-83.165713, 41.623246],
          [-83.415919, 41.618938],
          [-83.569934, 41.61715],
          [-83.703336, 41.528952],
          [-83.712432, 41.492856],
          [-83.883234, 41.414503],
          [-83.882943, 41.487543],
          [-83.882455, 41.603225],
          [-83.880387, 41.720086],
          [-83.763155, 41.72391],
          [-83.685337, 41.726449],
          [-83.665937, 41.726949],
          [-83.639636, 41.727749],
          [-83.636636, 41.727849],
          [-83.595235, 41.729148],
          [-83.593835, 41.729148],
          [-83.585235, 41.729348],
          [-83.504334, 41.731547],
          [-83.503433, 41.731547],
          [-83.499733, 41.731647],
          [-83.497733, 41.731847],
          [-83.453832, 41.732647]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US44009",
      state: "44",
      county: "009",
      name: "Washington County",
      censusArea: 329.235,
      stateName: "Rhode Island"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-71.58955, 41.196557],
            [-71.580228, 41.204837],
            [-71.577301, 41.21471],
            [-71.576661, 41.224434],
            [-71.573785, 41.228436],
            [-71.561093, 41.224207],
            [-71.555006, 41.216822],
            [-71.554067, 41.212957],
            [-71.557459, 41.204542],
            [-71.564119, 41.195372],
            [-71.565752, 41.184373],
            [-71.560969, 41.176186],
            [-71.550226, 41.166787],
            [-71.544446, 41.164912],
            [-71.543872, 41.161321],
            [-71.547051, 41.153684],
            [-71.551953, 41.151718],
            [-71.5937, 41.146339],
            [-71.599993, 41.146932],
            [-71.611706, 41.153239],
            [-71.613133, 41.160281],
            [-71.605565, 41.182139],
            [-71.594994, 41.188392],
            [-71.58955, 41.196557]
          ]
        ],
        [
          [
            [-71.408636, 41.653819],
            [-71.40377, 41.589321],
            [-71.447712, 41.5804],
            [-71.442567, 41.565075],
            [-71.421649, 41.537892],
            [-71.417398, 41.534536],
            [-71.414825, 41.523126],
            [-71.414937, 41.516303],
            [-71.421425, 41.498629],
            [-71.419971, 41.484758],
            [-71.417957, 41.482073],
            [-71.417621, 41.477934],
            [-71.418404, 41.472652],
            [-71.421157, 41.469888],
            [-71.422991, 41.472682],
            [-71.430744, 41.470636],
            [-71.430926, 41.465655],
            [-71.427935, 41.459529],
            [-71.428652, 41.454158],
            [-71.433612, 41.444995],
            [-71.43767, 41.441302],
            [-71.441199, 41.441602],
            [-71.448948, 41.438479],
            [-71.455845, 41.432986],
            [-71.455371, 41.407962],
            [-71.474918, 41.386104],
            [-71.483295, 41.371722],
            [-71.513401, 41.374702],
            [-71.526724, 41.376636],
            [-71.555381, 41.373316],
            [-71.624505, 41.36087],
            [-71.68807, 41.342823],
            [-71.701631, 41.336968],
            [-71.72074, 41.331567],
            [-71.729247, 41.33099],
            [-71.773702, 41.327977],
            [-71.785957, 41.325739],
            [-71.833755, 41.315631],
            [-71.857432, 41.306318],
            [-71.862772, 41.309791],
            [-71.862109, 41.316612],
            [-71.85957, 41.322399],
            [-71.839782, 41.33469],
            [-71.83253, 41.341204],
            [-71.83124, 41.344645],
            [-71.831613, 41.370899],
            [-71.833443, 41.384524],
            [-71.842131, 41.395359],
            [-71.843472, 41.40583],
            [-71.842563, 41.409855],
            [-71.839649, 41.412119],
            [-71.81839, 41.419599],
            [-71.797683, 41.416709],
            [-71.797674, 41.41691],
            [-71.789359, 41.596852],
            [-71.484406, 41.602769],
            [-71.408636, 41.653819]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45011",
      state: "45",
      county: "011",
      name: "Barnwell County",
      censusArea: 548.393,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.772251, 33.18012],
          [-81.757035, 33.198148],
          [-81.516419, 33.382239],
          [-81.374827, 33.489652],
          [-81.372931, 33.490417],
          [-81.222673, 33.44],
          [-81.227804, 33.160697],
          [-81.193099, 33.118668],
          [-81.364857, 33.108061],
          [-81.541881, 33.158279],
          [-81.615756, 33.092534],
          [-81.620287, 33.095408],
          [-81.696934, 33.116551],
          [-81.772251, 33.18012]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45013",
      state: "45",
      county: "013",
      name: "Beaufort County",
      censusArea: 576.28,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.826309, 32.704321],
          [-80.66774, 32.665535],
          [-80.659751, 32.612683],
          [-80.551504, 32.565769],
          [-80.47691, 32.48539],
          [-80.48025, 32.477407],
          [-80.484617, 32.460976],
          [-80.480156, 32.447048],
          [-80.467588, 32.425259],
          [-80.446075, 32.423721],
          [-80.43296, 32.410659],
          [-80.429941, 32.401782],
          [-80.429291, 32.389667],
          [-80.434303, 32.375193],
          [-80.445451, 32.350335],
          [-80.456814, 32.336884],
          [-80.455192, 32.326458],
          [-80.466342, 32.31917],
          [-80.517871, 32.298796],
          [-80.539429, 32.287024],
          [-80.545688, 32.282076],
          [-80.571096, 32.273278],
          [-80.596394, 32.273549],
          [-80.618286, 32.260183],
          [-80.638857, 32.255618],
          [-80.658634, 32.248638],
          [-80.669166, 32.216783],
          [-80.688857, 32.200971],
          [-80.721463, 32.160427],
          [-80.749091, 32.140137],
          [-80.789996, 32.122494],
          [-80.812503, 32.109746],
          [-80.82153, 32.108589],
          [-80.828394, 32.113222],
          [-80.831531, 32.112709],
          [-80.844431, 32.109709],
          [-80.858735, 32.099581],
          [-80.878111, 32.079792],
          [-81.016341, 32.241923],
          [-81.006432, 32.306196],
          [-80.934935, 32.300369],
          [-80.829489, 32.411836],
          [-80.869705, 32.660935],
          [-80.837033, 32.700038],
          [-80.826309, 32.704321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32015",
      state: "32",
      county: "015",
      name: "Lander County",
      censusArea: 5490.105,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.300597, 40.526136],
          [-117.246788, 40.642445],
          [-117.018664, 40.643035],
          [-117.018419, 41.000254],
          [-116.586786, 41.000398],
          [-116.587104, 40.947021],
          [-116.596815, 40.94646],
          [-116.590238, 39.70644],
          [-116.597109, 39.176241],
          [-116.600946, 39.161463],
          [-117.331857, 39.16343],
          [-117.775468, 39.093425],
          [-117.806183, 39.145845],
          [-117.714056, 39.245558],
          [-117.759795, 39.369225],
          [-117.646011, 39.426602],
          [-117.656397, 39.468043],
          [-117.59024, 39.517521],
          [-117.485336, 39.545322],
          [-117.461786, 39.622444],
          [-117.495344, 39.748364],
          [-117.46164, 39.886817],
          [-117.541749, 40.001034],
          [-117.300597, 40.526136]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32019",
      state: "32",
      county: "019",
      name: "Lyon County",
      censusArea: 2001.187,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.712645, 39.250871],
          [-119.64735, 39.266568],
          [-119.478806, 39.337416],
          [-119.283637, 39.621561],
          [-119.25876, 39.649213],
          [-119.189342, 39.65087],
          [-119.190676, 39.631787],
          [-119.059922, 39.740385],
          [-119.118193, 39.388516],
          [-118.754014, 39.112023],
          [-118.753835, 39.07463],
          [-118.929737, 39.073698],
          [-119.014837, 38.943323],
          [-119.014528, 38.8513],
          [-118.902124, 38.852158],
          [-118.906861, 38.414677],
          [-119.156863, 38.414743],
          [-119.328498, 38.534648],
          [-119.349884, 38.729193],
          [-119.406279, 38.733362],
          [-119.403908, 38.809111],
          [-119.439534, 38.882001],
          [-119.401648, 38.983323],
          [-119.310702, 38.990719],
          [-119.311714, 39.083615],
          [-119.55102, 39.085791],
          [-119.56368, 39.195415],
          [-119.648977, 39.195287],
          [-119.712645, 39.250871]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US32029",
      state: "32",
      county: "029",
      name: "Storey County",
      censusArea: 262.92,
      stateName: "Nevada"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-119.283637, 39.621561],
          [-119.478806, 39.337416],
          [-119.64735, 39.266568],
          [-119.712645, 39.250871],
          [-119.658501, 39.331544],
          [-119.685464, 39.389575],
          [-119.695643, 39.520023],
          [-119.621697, 39.511602],
          [-119.582952, 39.547302],
          [-119.467141, 39.584502],
          [-119.370604, 39.586882],
          [-119.279801, 39.627112],
          [-119.283637, 39.621561]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39111",
      state: "39",
      county: "111",
      name: "Monroe County",
      censusArea: 455.721,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.812342, 39.848845],
          [-80.828618, 39.720848],
          [-80.829764, 39.711839],
          [-80.831871, 39.705655],
          [-80.88036, 39.620706],
          [-80.892208, 39.616756],
          [-80.943782, 39.606926],
          [-81.020055, 39.55541],
          [-81.038691, 39.540464],
          [-81.036791, 39.572118],
          [-81.246808, 39.579037],
          [-81.283075, 39.605796],
          [-81.319125, 39.70736],
          [-81.313591, 39.869471],
          [-81.237316, 39.867828],
          [-80.812342, 39.848845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39113",
      state: "39",
      county: "113",
      name: "Montgomery County",
      censusArea: 461.553,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.157223, 39.88563],
          [-84.154452, 39.885587],
          [-84.10483, 39.882652],
          [-84.098237, 39.882529],
          [-84.056413, 39.879875],
          [-84.051039, 39.879807],
          [-84.051636, 39.877665],
          [-84.052136, 39.866259],
          [-84.053136, 39.856158],
          [-84.053341, 39.855117],
          [-84.053736, 39.850458],
          [-84.055238, 39.835958],
          [-84.092938, 39.838345],
          [-84.096937, 39.793033],
          [-84.114195, 39.577983],
          [-84.187334, 39.583081],
          [-84.365232, 39.589493],
          [-84.474246, 39.590155],
          [-84.479213, 39.591024],
          [-84.485367, 39.918491],
          [-84.448023, 39.91923],
          [-84.446511, 39.918874],
          [-84.446101, 39.919266],
          [-84.425902, 39.919622],
          [-84.157671, 39.92297],
          [-84.157223, 39.88563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39123",
      state: "39",
      county: "123",
      name: "Ottawa County",
      censusArea: 254.917,
      stateName: "Ohio"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-82.824731, 41.659428],
            [-82.808587, 41.661682],
            [-82.805179, 41.664281],
            [-82.796333, 41.665973],
            [-82.793069, 41.664692],
            [-82.794198, 41.662496],
            [-82.797493, 41.658197],
            [-82.827011, 41.633701],
            [-82.834662, 41.629331],
            [-82.842099, 41.628323],
            [-82.843602, 41.647009],
            [-82.834092, 41.657121],
            [-82.824731, 41.659428]
          ]
        ],
        [
          [
            [-82.803341, 41.693837],
            [-82.786304, 41.69714],
            [-82.782719, 41.694003],
            [-82.790561, 41.68997],
            [-82.801539, 41.682577],
            [-82.805137, 41.674931],
            [-82.805423, 41.67192],
            [-82.813781, 41.670594],
            [-82.826443, 41.684774],
            [-82.812805, 41.692002],
            [-82.803341, 41.693837]
          ]
        ],
        [
          [
            [-82.813489, 41.723468],
            [-82.810487, 41.720524],
            [-82.808869, 41.708333],
            [-82.816133, 41.706732],
            [-82.835118, 41.708971],
            [-82.835577, 41.710823],
            [-82.832709, 41.71565],
            [-82.82572, 41.72281],
            [-82.820409, 41.724549],
            [-82.813489, 41.723468]
          ]
        ],
        [
          [
            [-82.828317, 41.481802],
            [-82.856104, 41.471923],
            [-82.90665, 41.460215],
            [-82.920239, 41.458443],
            [-82.952224, 41.458239],
            [-83.338196, 41.457508],
            [-83.414626, 41.500228],
            [-83.415919, 41.618938],
            [-83.165713, 41.623246],
            [-83.145887, 41.617904],
            [-83.103928, 41.613558],
            [-83.086036, 41.60668],
            [-83.066593, 41.59534],
            [-83.048222, 41.573951],
            [-83.043287, 41.568205],
            [-83.043079, 41.567963],
            [-83.042909, 41.567823],
            [-83.031653, 41.558592],
            [-83.030764, 41.557864],
            [-83.028072, 41.555656],
            [-83.019057, 41.550174],
            [-83.017473, 41.549211],
            [-83.016753, 41.548773],
            [-82.999916, 41.538534],
            [-82.999897, 41.538525],
            [-82.96985, 41.524327],
            [-82.969642, 41.524229],
            [-82.969574, 41.52421],
            [-82.958481, 41.521104],
            [-82.934369, 41.514353],
            [-82.897728, 41.519241],
            [-82.896854, 41.519541],
            [-82.8882, 41.522508],
            [-82.882305, 41.52577],
            [-82.875229, 41.529684],
            [-82.869422, 41.533962],
            [-82.866262, 41.537534],
            [-82.861323, 41.543116],
            [-82.857841, 41.547051],
            [-82.85677, 41.548262],
            [-82.856533, 41.55065],
            [-82.855197, 41.564114],
            [-82.856088, 41.566633],
            [-82.856647, 41.568216],
            [-82.858702, 41.574025],
            [-82.859531, 41.576371],
            [-82.857945, 41.578049],
            [-82.857316, 41.578714],
            [-82.856919, 41.579135],
            [-82.854263, 41.581945],
            [-82.852957, 41.583327],
            [-82.847657, 41.585639],
            [-82.834101, 41.587587],
            [-82.820207, 41.570664],
            [-82.794324, 41.546486],
            [-82.785496, 41.540675],
            [-82.77201, 41.54058],
            [-82.749907, 41.54647],
            [-82.739868, 41.545047],
            [-82.732886, 41.544057],
            [-82.7319, 41.543918],
            [-82.731628, 41.543879],
            [-82.730516, 41.543721],
            [-82.728489, 41.543434],
            [-82.727656, 41.543316],
            [-82.722069, 41.542524],
            [-82.720539, 41.542307],
            [-82.717878, 41.54193],
            [-82.717595, 41.541715],
            [-82.715227, 41.539913],
            [-82.711332, 41.53695],
            [-82.710935, 41.536648],
            [-82.710967, 41.536221],
            [-82.711632, 41.527201],
            [-82.716492, 41.522227],
            [-82.721914, 41.516677],
            [-82.719956, 41.510735],
            [-82.719811, 41.510296],
            [-82.713904, 41.501697],
            [-82.710013, 41.49759],
            [-82.694722, 41.493945],
            [-82.828317, 41.481802]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39145",
      state: "39",
      county: "145",
      name: "Scioto County",
      censusArea: 610.213,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.04362, 38.956939],
          [-82.80709, 38.948073],
          [-82.753391, 38.945373],
          [-82.76069, 38.854875],
          [-82.650039, 38.849073],
          [-82.706823, 38.677495],
          [-82.763895, 38.679974],
          [-82.740383, 38.597167],
          [-82.816012, 38.570733],
          [-82.820161, 38.572703],
          [-82.839538, 38.586159],
          [-82.847186, 38.595166],
          [-82.853554, 38.610926],
          [-82.869592, 38.678177],
          [-82.873492, 38.710077],
          [-82.870392, 38.722077],
          [-82.869992, 38.730477],
          [-82.871292, 38.739376],
          [-82.874466, 38.745346],
          [-82.875492, 38.747276],
          [-82.879492, 38.751476],
          [-82.889193, 38.756076],
          [-82.894193, 38.756576],
          [-82.923694, 38.750076],
          [-82.943147, 38.74328],
          [-82.968695, 38.728776],
          [-82.979395, 38.725976],
          [-83.011816, 38.730057],
          [-83.030702, 38.72572],
          [-83.030889, 38.72556],
          [-83.127652, 38.642451],
          [-83.135046, 38.631719],
          [-83.142836, 38.625076],
          [-83.156926, 38.620547],
          [-83.265769, 38.605278],
          [-83.270817, 39.015791],
          [-83.212107, 38.960148],
          [-83.04362, 38.956939]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39155",
      state: "39",
      county: "155",
      name: "Trumbull County",
      censusArea: 618.297,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.519345, 41.34074],
          [-80.519345, 41.340145],
          [-80.519293, 41.339654],
          [-80.519293, 41.339054],
          [-80.519311, 41.339052],
          [-80.519281, 41.337174],
          [-80.519281, 41.337145],
          [-80.519281, 41.335958],
          [-80.519265, 41.333495],
          [-80.519129, 41.312408],
          [-80.518794, 41.305509],
          [-80.518996, 41.2683],
          [-80.518993, 41.268155],
          [-80.518893, 41.265155],
          [-80.518693, 41.248855],
          [-80.518893, 41.232556],
          [-80.518893, 41.219357],
          [-80.518893, 41.219356],
          [-80.51883, 41.209213],
          [-80.519144, 41.171203],
          [-80.519115, 41.14552],
          [-80.519167, 41.133388],
          [-80.789472, 41.134159],
          [-81.00229, 41.134189],
          [-81.00288, 41.271842],
          [-81.003319, 41.34786],
          [-81.003194, 41.424482],
          [-81.003066, 41.425928],
          [-81.003125, 41.501685],
          [-80.519225, 41.499924],
          [-80.519209, 41.489013],
          [-80.519169, 41.462581],
          [-80.518993, 41.435454],
          [-80.518993, 41.416437],
          [-80.519025, 41.416438],
          [-80.519249, 41.378918],
          [-80.519217, 41.372006],
          [-80.519249, 41.36103],
          [-80.519297, 41.350833],
          [-80.519345, 41.34074]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30103",
      state: "30",
      county: "103",
      name: "Treasure County",
      censusArea: 977.398,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.738547, 46.481715],
          [-107.174607, 46.48234],
          [-107.153227, 46.395545],
          [-107.028378, 46.395409],
          [-107.007261, 46.307641],
          [-107.007451, 46.134084],
          [-106.93898, 46.136697],
          [-106.939095, 45.870089],
          [-107.052103, 45.870088],
          [-107.051803, 45.913788],
          [-107.176433, 45.913323],
          [-107.176311, 45.956788],
          [-107.424923, 45.956786],
          [-107.424889, 46.043396],
          [-107.511497, 46.043257],
          [-107.466381, 46.177535],
          [-107.61278, 46.30811],
          [-107.738547, 46.481715]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30105",
      state: "30",
      county: "105",
      name: "Valley County",
      censusArea: 4925.819,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.112108, 48.999279],
          [-106.15194, 48.823898],
          [-106.021358, 48.823975],
          [-106.021212, 48.56322],
          [-105.804432, 48.563318],
          [-105.804345, 48.219179],
          [-105.843462, 48.21903],
          [-105.843797, 48.010314],
          [-106.006176, 48.033689],
          [-106.229302, 48.026416],
          [-106.394163, 48.064726],
          [-106.418567, 47.957414],
          [-106.445793, 47.983016],
          [-106.609705, 47.77751],
          [-106.711813, 47.771809],
          [-106.751016, 47.729708],
          [-106.863822, 47.736508],
          [-106.891324, 47.676607],
          [-107.010862, 47.66314],
          [-107.247694, 47.6614],
          [-107.414886, 47.691677],
          [-107.404555, 48.218303],
          [-107.369899, 48.218294],
          [-107.369306, 48.305134],
          [-107.260593, 48.30475],
          [-107.260454, 48.393864],
          [-107.301073, 48.394],
          [-107.301721, 48.480943],
          [-107.25819, 48.514825],
          [-107.193567, 48.509267],
          [-107.206426, 48.567989],
          [-107.206146, 48.912592],
          [-107.179865, 48.999559],
          [-106.625597, 48.99964],
          [-106.617539, 48.999583],
          [-106.518201, 48.999564],
          [-106.500592, 48.999756],
          [-106.374616, 48.999617],
          [-106.368151, 48.999503],
          [-106.274267, 48.999312],
          [-106.24621, 48.999258],
          [-106.243154, 48.999373],
          [-106.233987, 48.999423],
          [-106.112108, 48.999279]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38077",
      state: "38",
      county: "077",
      name: "Richland County",
      censusArea: 1435.779,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.739054, 46.543708],
          [-96.716438, 46.444567],
          [-96.721999, 46.439986],
          [-96.669794, 46.384644],
          [-96.631586, 46.353752],
          [-96.598233, 46.312563],
          [-96.599087, 46.263701],
          [-96.585647, 46.177309],
          [-96.578362, 46.170112],
          [-96.557952, 46.102442],
          [-96.554835, 46.084824],
          [-96.55694, 46.064601],
          [-96.559271, 46.058272],
          [-96.566295, 46.051416],
          [-96.57794, 46.026874],
          [-96.577326, 46.021757],
          [-96.57035, 45.963595],
          [-96.56328, 45.935238],
          [-96.576897, 45.935259],
          [-96.597432, 45.935209],
          [-96.607142, 45.935301],
          [-96.618295, 45.935407],
          [-96.639066, 45.935318],
          [-96.659895, 45.93556],
          [-96.680646, 45.935716],
          [-96.701313, 45.935807],
          [-96.791505, 45.935857],
          [-96.998652, 45.9357],
          [-97.019596, 45.935382],
          [-97.118053, 45.935485],
          [-97.144987, 45.935278],
          [-97.228291, 45.935141],
          [-97.259632, 45.997185],
          [-97.259339, 46.282197],
          [-97.280568, 46.282321],
          [-97.279948, 46.629348],
          [-96.783014, 46.630647],
          [-96.783009, 46.630418],
          [-96.783003, 46.630405],
          [-96.739054, 46.543708]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38085",
      state: "38",
      county: "085",
      name: "Sioux County",
      censusArea: 1094.094,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.511949, 45.943654],
          [-100.627681, 45.943642],
          [-100.65082, 45.94368],
          [-100.720865, 45.944024],
          [-100.750407, 45.943649],
          [-100.762072, 45.943803],
          [-100.76211, 45.943767],
          [-100.769751, 45.943766],
          [-100.890176, 45.943861],
          [-100.935582, 45.943757],
          [-100.938989, 45.943848],
          [-100.964411, 45.943822],
          [-100.976565, 45.943864],
          [-100.980693, 45.944068],
          [-101.101334, 45.943841],
          [-101.106826, 45.943984],
          [-101.142571, 45.943841],
          [-101.146076, 45.943842],
          [-101.163241, 45.943915],
          [-101.171074, 45.943959],
          [-101.175693, 45.943983],
          [-101.179103, 45.943896],
          [-101.203787, 45.943895],
          [-101.224006, 45.944025],
          [-101.271524, 45.944209],
          [-101.287223, 45.944107],
          [-101.313272, 45.944164],
          [-101.333871, 45.944166],
          [-101.365283, 45.944092],
          [-101.37069, 45.944198],
          [-101.373769, 45.944265],
          [-101.557276, 45.9441],
          [-101.562156, 45.944237],
          [-101.628597, 45.944293],
          [-101.657631, 45.944387],
          [-101.680574, 45.944329],
          [-101.681819, 45.944444],
          [-101.708785, 45.944348],
          [-101.72338, 45.944187],
          [-101.730069, 45.944356],
          [-101.758611, 45.944478],
          [-101.764277, 45.944412],
          [-101.765293, 45.944367],
          [-101.766177, 45.944322],
          [-101.790054, 45.944442],
          [-101.794606, 45.944397],
          [-101.832991, 45.944464],
          [-101.852642, 45.944457],
          [-101.886838, 45.944559],
          [-101.957439, 45.944484],
          [-101.973749, 45.944456],
          [-101.989501, 45.944472],
          [-101.992187, 45.944471],
          [-101.998618, 45.944556],
          [-101.998575, 46.053461],
          [-101.849355, 46.073275],
          [-101.842108, 46.029136],
          [-101.554185, 46.006836],
          [-101.190372, 46.147847],
          [-101.198073, 46.18377],
          [-101.025662, 46.284219],
          [-100.91895, 46.394983],
          [-100.721261, 46.390084],
          [-100.590199, 46.427894],
          [-100.551199, 46.363494],
          [-100.558549, 46.249993],
          [-100.629145, 46.145608],
          [-100.614504, 46.071796],
          [-100.511949, 45.943654]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38099",
      state: "38",
      county: "099",
      name: "Walsh County",
      censusArea: 1281.931,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.318098, 48.543773],
          [-97.927154, 48.543124],
          [-97.162466, 48.54225],
          [-97.16111, 48.543155],
          [-97.150541, 48.539449],
          [-97.12677, 48.520191],
          [-97.123399, 48.421201],
          [-97.131479, 48.406586],
          [-97.145693, 48.397431],
          [-97.136373, 48.352393],
          [-97.126745, 48.34214],
          [-97.115581, 48.323928],
          [-97.11657, 48.279661],
          [-97.12993, 48.249731],
          [-97.134534, 48.249737],
          [-97.143148, 48.246708],
          [-97.14717, 48.242966],
          [-97.147401, 48.193779],
          [-97.903434, 48.194897],
          [-98.292184, 48.195177],
          [-98.318098, 48.543773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38105",
      state: "38",
      county: "105",
      name: "Williams County",
      censusArea: 2077.401,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.046913, 48.389433],
          [-104.046969, 48.390675],
          [-104.047134, 48.411057],
          [-104.04696, 48.421065],
          [-104.04709, 48.445903],
          [-104.047192, 48.447251],
          [-104.047294, 48.452529],
          [-104.047259, 48.452941],
          [-104.047392, 48.467086],
          [-104.047555, 48.49414],
          [-104.047675, 48.517852],
          [-104.047513, 48.525913],
          [-104.047876, 48.530798],
          [-104.047648, 48.531489],
          [-104.047783, 48.539737],
          [-104.047811, 48.56277],
          [-104.047974, 48.591606],
          [-104.048212, 48.599055],
          [-104.04793, 48.62019],
          [-104.047586, 48.625644],
          [-104.04762, 48.627015],
          [-104.047582, 48.633976],
          [-102.885775, 48.633298],
          [-102.886015, 48.561179],
          [-102.885775, 48.560628],
          [-102.885763, 48.54639],
          [-102.885737, 48.372578],
          [-102.828061, 48.37292],
          [-102.828477, 48.123664],
          [-103.107083, 48.146494],
          [-103.20189, 48.135208],
          [-103.234865, 48.061923],
          [-103.501172, 48.005925],
          [-103.584774, 48.054124],
          [-103.548572, 48.087724],
          [-103.597044, 48.13441],
          [-103.742216, 48.093403],
          [-103.800817, 47.994269],
          [-103.856306, 48.015072],
          [-103.919789, 47.95595],
          [-104.04412, 47.996107],
          [-104.045399, 48.16439],
          [-104.045498, 48.176249],
          [-104.045424, 48.192473],
          [-104.04556, 48.193913],
          [-104.045692, 48.241415],
          [-104.045729, 48.244586],
          [-104.045645, 48.246179],
          [-104.045861, 48.255097],
          [-104.046039, 48.256761],
          [-104.046332, 48.34229],
          [-104.046371, 48.374154],
          [-104.046654, 48.374773],
          [-104.046913, 48.389429],
          [-104.046913, 48.389433]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29031",
      state: "29",
      county: "031",
      name: "Cape Girardeau County",
      censusArea: 578.535,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.491222, 37.248629],
          [-89.620256, 37.233008],
          [-89.765211, 37.126066],
          [-89.866627, 37.126227],
          [-89.861123, 37.599286],
          [-89.603574, 37.592056],
          [-89.521523, 37.566208],
          [-89.521697, 37.557325],
          [-89.517051, 37.537278],
          [-89.492051, 37.494008],
          [-89.475525, 37.471388],
          [-89.450969, 37.450069],
          [-89.439769, 37.4372],
          [-89.42594, 37.407471],
          [-89.421054, 37.387668],
          [-89.428185, 37.356158],
          [-89.432836, 37.347056],
          [-89.43604, 37.344441],
          [-89.447556, 37.340475],
          [-89.474569, 37.338165],
          [-89.484598, 37.334832],
          [-89.489005, 37.333368],
          [-89.511842, 37.310825],
          [-89.518393, 37.289354],
          [-89.517032, 37.28192],
          [-89.491222, 37.248629]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29037",
      state: "29",
      county: "037",
      name: "Cass County",
      censusArea: 696.836,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.399447, 38.838809],
          [-94.120778, 38.833006],
          [-94.119096, 38.833167],
          [-94.119664, 38.568712],
          [-94.064317, 38.56738],
          [-94.065713, 38.447087],
          [-94.31217, 38.471496],
          [-94.612865, 38.477602],
          [-94.612696, 38.483154],
          [-94.612726, 38.484367],
          [-94.612644, 38.491644],
          [-94.612272, 38.547917],
          [-94.612157, 38.549817],
          [-94.612176, 38.576546],
          [-94.611902, 38.58011],
          [-94.611887, 38.580139],
          [-94.611908, 38.609272],
          [-94.611858, 38.620485],
          [-94.611602, 38.635384],
          [-94.609509, 38.738102],
          [-94.609456, 38.7407],
          [-94.609399, 38.74244],
          [-94.609039, 38.760611],
          [-94.608041, 38.811064],
          [-94.608033, 38.847207],
          [-94.399447, 38.838809]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36047",
      state: "36",
      county: "047",
      name: "Kings County",
      censusArea: 70.816,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.868917, 40.69515],
          [-73.866027, 40.681918],
          [-73.85566, 40.663802],
          [-73.86049, 40.654655],
          [-73.849852, 40.588669],
          [-73.938598, 40.566161],
          [-73.944558, 40.568716],
          [-73.95005, 40.573363],
          [-73.95938, 40.572682],
          [-73.991346, 40.57035],
          [-74.002056, 40.570623],
          [-74.00903, 40.572846],
          [-74.012022, 40.574528],
          [-74.012996, 40.578169],
          [-74.007276, 40.583616],
          [-74.00635, 40.584767],
          [-74.001591, 40.590684],
          [-74.003281, 40.595754],
          [-74.010926, 40.600789],
          [-74.032856, 40.604421],
          [-74.03959, 40.612934],
          [-74.042412, 40.624847],
          [-74.038336, 40.637074],
          [-74.035868, 40.640776],
          [-74.032066, 40.646479],
          [-74.018272, 40.659019],
          [-74.020467, 40.67877],
          [-74.022911, 40.681267],
          [-74.019347, 40.679548],
          [-74.008117, 40.686615],
          [-74.003946, 40.689047],
          [-73.994648, 40.704135],
          [-73.969845, 40.709047],
          [-73.962645, 40.722747],
          [-73.961543, 40.723876],
          [-73.962795, 40.737674],
          [-73.961188, 40.737091],
          [-73.954732, 40.739292],
          [-73.947064, 40.737516],
          [-73.937339, 40.72992],
          [-73.929223, 40.727849],
          [-73.923865, 40.720056],
          [-73.921473, 40.709004],
          [-73.912058, 40.703446],
          [-73.904425, 40.695671],
          [-73.896497, 40.682447],
          [-73.889575, 40.68418],
          [-73.874021, 40.694191],
          [-73.868917, 40.69515]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36053",
      state: "36",
      county: "053",
      name: "Madison County",
      censusArea: 654.842,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.884275, 43.155562],
          [-75.737774, 43.164673],
          [-75.552774, 43.037554],
          [-75.544211, 42.93177],
          [-75.444173, 42.933089],
          [-75.437167, 42.863319],
          [-75.247963, 42.871604],
          [-75.295877, 42.744106],
          [-75.889832, 42.723844],
          [-75.896079, 42.790964],
          [-75.917189, 43.085779],
          [-75.975588, 43.091278],
          [-75.975849, 43.094606],
          [-75.974724, 43.096919],
          [-75.976286, 43.098489],
          [-75.975641, 43.100336],
          [-75.976396, 43.103038],
          [-75.974149, 43.103563],
          [-75.971524, 43.104914],
          [-75.972988, 43.106379],
          [-75.97203, 43.107522],
          [-75.970349, 43.107567],
          [-75.971725, 43.109186],
          [-75.969201, 43.110504],
          [-75.96543, 43.116721],
          [-75.965594, 43.119552],
          [-75.964695, 43.119811],
          [-75.963383, 43.119043],
          [-75.963517, 43.117563],
          [-75.959703, 43.115459],
          [-75.956023, 43.115148],
          [-75.955995, 43.11623],
          [-75.9544, 43.116857],
          [-75.955843, 43.118314],
          [-75.957416, 43.118194],
          [-75.95595, 43.119515],
          [-75.95713, 43.120241],
          [-75.959414, 43.119808],
          [-75.95701, 43.121973],
          [-75.958339, 43.123728],
          [-75.960494, 43.122969],
          [-75.958817, 43.125112],
          [-75.958833, 43.128093],
          [-75.956763, 43.126804],
          [-75.954592, 43.127986],
          [-75.952421, 43.130705],
          [-75.950287, 43.132551],
          [-75.953277, 43.133363],
          [-75.950066, 43.134376],
          [-75.950296, 43.135195],
          [-75.953405, 43.135459],
          [-75.954803, 43.134008],
          [-75.957129, 43.136297],
          [-75.958196, 43.135082],
          [-75.957184, 43.133947],
          [-75.960459, 43.13284],
          [-75.961672, 43.133941],
          [-75.963961, 43.133799],
          [-75.964403, 43.135128],
          [-75.967318, 43.134819],
          [-75.969221, 43.136765],
          [-75.96992, 43.13863],
          [-75.972347, 43.140476],
          [-75.975318, 43.138939],
          [-75.973837, 43.137342],
          [-75.975602, 43.136169],
          [-75.977349, 43.136343],
          [-75.97678, 43.137905],
          [-75.977515, 43.139791],
          [-75.976964, 43.140754],
          [-75.972648, 43.142995],
          [-75.971783, 43.143847],
          [-75.972702, 43.145473],
          [-75.973342, 43.149225],
          [-75.971235, 43.154639],
          [-75.972903, 43.155894],
          [-75.977068, 43.157646],
          [-75.976752, 43.160269],
          [-75.974629, 43.161917],
          [-75.97438, 43.163175],
          [-75.97788, 43.164591],
          [-75.979131, 43.166262],
          [-75.978319, 43.167706],
          [-75.981008, 43.167704],
          [-75.979478, 43.170698],
          [-75.982668, 43.173631],
          [-75.986514, 43.171572],
          [-75.988447, 43.172616],
          [-75.990028, 43.171527],
          [-75.991933, 43.172953],
          [-75.992635, 43.173849],
          [-75.989672, 43.179591],
          [-75.990766, 43.180917],
          [-75.991437, 43.18325],
          [-75.993394, 43.183376],
          [-75.992428, 43.185164],
          [-75.986894, 43.183938],
          [-75.979489, 43.177401],
          [-75.973438, 43.179898],
          [-75.959359, 43.180389],
          [-75.955599, 43.172182],
          [-75.932778, 43.176583],
          [-75.927453, 43.162738],
          [-75.884275, 43.155562]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36061",
      state: "36",
      county: "061",
      name: "New York County",
      censusArea: 22.829,
      stateName: "New York"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-74.04086, 40.700117],
            [-74.040018, 40.700678],
            [-74.039401, 40.700454],
            [-74.037998, 40.698995],
            [-74.043441, 40.68968],
            [-74.044451, 40.688445],
            [-74.046359, 40.689175],
            [-74.047313, 40.690466],
            [-74.04692, 40.691139],
            [-74.04086, 40.700117]
          ]
        ],
        [
          [
            [-73.962795, 40.737674],
            [-73.961543, 40.723876],
            [-73.962645, 40.722747],
            [-73.969845, 40.709047],
            [-73.994648, 40.704135],
            [-74.003946, 40.689047],
            [-74.008117, 40.686615],
            [-74.019347, 40.679548],
            [-74.022911, 40.681267],
            [-74.023982, 40.68236],
            [-74.024827, 40.687007],
            [-74.021721, 40.693504],
            [-74.01849, 40.695457],
            [-74.0168, 40.701794],
            [-74.019526, 40.706985],
            [-74.024543, 40.709436],
            [-74.013784, 40.756601],
            [-74.009184, 40.763601],
            [-74.000905, 40.776488],
            [-73.993029, 40.788746],
            [-73.986864, 40.798344],
            [-73.948281, 40.858399],
            [-73.938081, 40.874699],
            [-73.933408, 40.882075],
            [-73.9152, 40.875581],
            [-73.911405, 40.879278],
            [-73.907105, 40.876277],
            [-73.907, 40.873455],
            [-73.912506, 40.79618],
            [-73.910551, 40.790988],
            [-73.916316, 40.786601],
            [-73.92797, 40.776762],
            [-73.93508, 40.779218],
            [-73.938399, 40.774606],
            [-73.938076, 40.772551],
            [-73.93519, 40.771745],
            [-73.936536, 40.769835],
            [-73.940844, 40.767399],
            [-73.943951, 40.764434],
            [-73.95492, 40.751509],
            [-73.9583, 40.745731],
            [-73.961544, 40.743081],
            [-73.962795, 40.737674]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36067",
      state: "36",
      county: "067",
      name: "Onondaga County",
      censusArea: 778.39,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.499312, 43.097949],
          [-76.479224, 43.227519],
          [-76.258951, 43.237468],
          [-76.202944, 43.271605],
          [-75.993394, 43.183376],
          [-75.991437, 43.18325],
          [-75.990766, 43.180917],
          [-75.989672, 43.179591],
          [-75.992635, 43.173849],
          [-75.991933, 43.172953],
          [-75.990028, 43.171527],
          [-75.988447, 43.172616],
          [-75.986514, 43.171572],
          [-75.982668, 43.173631],
          [-75.979478, 43.170698],
          [-75.981008, 43.167704],
          [-75.978319, 43.167706],
          [-75.979131, 43.166262],
          [-75.97788, 43.164591],
          [-75.97438, 43.163175],
          [-75.974629, 43.161917],
          [-75.976752, 43.160269],
          [-75.977068, 43.157646],
          [-75.972903, 43.155894],
          [-75.971235, 43.154639],
          [-75.973342, 43.149225],
          [-75.972702, 43.145473],
          [-75.971783, 43.143847],
          [-75.972648, 43.142995],
          [-75.976964, 43.140754],
          [-75.977515, 43.139791],
          [-75.97678, 43.137905],
          [-75.977349, 43.136343],
          [-75.975602, 43.136169],
          [-75.973837, 43.137342],
          [-75.975318, 43.138939],
          [-75.972347, 43.140476],
          [-75.96992, 43.13863],
          [-75.969221, 43.136765],
          [-75.967318, 43.134819],
          [-75.964403, 43.135128],
          [-75.963961, 43.133799],
          [-75.961672, 43.133941],
          [-75.960459, 43.13284],
          [-75.957184, 43.133947],
          [-75.958196, 43.135082],
          [-75.957129, 43.136297],
          [-75.954803, 43.134008],
          [-75.953405, 43.135459],
          [-75.950296, 43.135195],
          [-75.950066, 43.134376],
          [-75.953277, 43.133363],
          [-75.950287, 43.132551],
          [-75.952421, 43.130705],
          [-75.954592, 43.127986],
          [-75.956763, 43.126804],
          [-75.958833, 43.128093],
          [-75.958817, 43.125112],
          [-75.960494, 43.122969],
          [-75.958339, 43.123728],
          [-75.95701, 43.121973],
          [-75.959414, 43.119808],
          [-75.95713, 43.120241],
          [-75.95595, 43.119515],
          [-75.957416, 43.118194],
          [-75.955843, 43.118314],
          [-75.9544, 43.116857],
          [-75.955995, 43.11623],
          [-75.956023, 43.115148],
          [-75.959703, 43.115459],
          [-75.963517, 43.117563],
          [-75.963383, 43.119043],
          [-75.964695, 43.119811],
          [-75.965594, 43.119552],
          [-75.96543, 43.116721],
          [-75.969201, 43.110504],
          [-75.971725, 43.109186],
          [-75.970349, 43.107567],
          [-75.97203, 43.107522],
          [-75.972988, 43.106379],
          [-75.971524, 43.104914],
          [-75.974149, 43.103563],
          [-75.976396, 43.103038],
          [-75.975641, 43.100336],
          [-75.976286, 43.098489],
          [-75.974724, 43.096919],
          [-75.975849, 43.094606],
          [-75.975588, 43.091278],
          [-75.917189, 43.085779],
          [-75.896079, 42.790964],
          [-76.274673, 42.771257],
          [-76.356974, 42.84945],
          [-76.450738, 42.84576],
          [-76.462999, 43.006316],
          [-76.499312, 43.097949]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41045",
      state: "41",
      county: "045",
      name: "Malheur County",
      censusArea: 9887.532,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.961535, 43.918388],
          [-116.979186, 43.879973],
          [-116.982482, 43.872799],
          [-116.996504, 43.864714],
          [-117.026634, 43.808104],
          [-117.026651, 43.733935],
          [-117.026841, 43.732905],
          [-117.026725, 43.714815],
          [-117.026825, 43.706193],
          [-117.026586, 43.683001],
          [-117.026623, 43.680865],
          [-117.026717, 43.675523],
          [-117.026661, 43.664385],
          [-117.026705, 43.631659],
          [-117.026905, 43.62488],
          [-117.027001, 43.621032],
          [-117.026937, 43.617614],
          [-117.026789, 43.610669],
          [-117.02676, 43.601912],
          [-117.026824, 43.600357],
          [-117.026889, 43.596033],
          [-117.026922, 43.593632],
          [-117.026774, 43.578674],
          [-117.026746, 43.577526],
          [-117.026652, 43.025128],
          [-117.026683, 43.024876],
          [-117.026253, 42.807447],
          [-117.026303, 42.80717],
          [-117.026331, 42.807015],
          [-117.026222, 42.000252],
          [-118.197189, 41.996995],
          [-118.195361, 42.275869],
          [-118.214725, 42.276029],
          [-118.231926, 43.77898],
          [-118.227435, 44.039981],
          [-118.23214, 44.256083],
          [-118.15234, 44.299583],
          [-117.971596, 44.443328],
          [-117.590992, 44.444461],
          [-117.485899, 44.387696],
          [-117.486153, 44.30142],
          [-117.197506, 44.29728],
          [-117.197777, 44.295789],
          [-117.196597, 44.287529],
          [-117.190107, 44.273958],
          [-117.170342, 44.25889],
          [-117.041144, 44.243653],
          [-117.03317, 44.248192],
          [-117.027558, 44.248881],
          [-116.98687, 44.245477],
          [-116.975905, 44.242844],
          [-116.902752, 44.179467],
          [-116.895757, 44.171267],
          [-116.894083, 44.160191],
          [-116.895931, 44.154295],
          [-116.897145, 44.152537],
          [-116.931719, 44.102474],
          [-116.935301, 43.988616],
          [-116.961535, 43.918388]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41055",
      state: "41",
      county: "055",
      name: "Sherman County",
      censusArea: 823.694,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.503549, 45.08295],
          [-120.729757, 45.083847],
          [-120.723569, 45.130911],
          [-120.889357, 45.202305],
          [-121.02408, 45.218726],
          [-121.031202, 45.285857],
          [-120.956471, 45.310345],
          [-120.868569, 45.401146],
          [-120.825275, 45.513788],
          [-120.882022, 45.527597],
          [-120.915876, 45.641345],
          [-120.870042, 45.661242],
          [-120.855674, 45.671545],
          [-120.788872, 45.686246],
          [-120.68937, 45.715847],
          [-120.668869, 45.730147],
          [-120.653559, 45.737237],
          [-120.357839, 45.533854],
          [-120.495859, 45.465349],
          [-120.525059, 45.405249],
          [-120.540256, 45.217049],
          [-120.481253, 45.19275],
          [-120.462548, 45.10115],
          [-120.503549, 45.08295]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41057",
      state: "41",
      county: "057",
      name: "Tillamook County",
      censusArea: 1102.58,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.96934, 45.783197],
          [-123.719288, 45.77315],
          [-123.360962, 45.779669],
          [-123.360892, 45.708695],
          [-123.48472, 45.679313],
          [-123.299441, 45.607306],
          [-123.442733, 45.521198],
          [-123.464932, 45.43384],
          [-123.463278, 45.216269],
          [-123.78441, 45.216034],
          [-123.784794, 45.076315],
          [-123.724213, 45.076098],
          [-123.725389, 45.043987],
          [-124.004598, 45.044959],
          [-124.004386, 45.046197],
          [-124.004668, 45.048167],
          [-124.00977, 45.047266],
          [-124.017991, 45.049808],
          [-124.015851, 45.064759],
          [-124.012163, 45.076921],
          [-124.006057, 45.084736],
          [-124.004863, 45.084232],
          [-123.989529, 45.094045],
          [-123.975425, 45.145476],
          [-123.968187, 45.201217],
          [-123.972919, 45.216784],
          [-123.962887, 45.280218],
          [-123.964169, 45.317026],
          [-123.972899, 45.33689],
          [-123.978671, 45.338854],
          [-124.007756, 45.336813],
          [-124.007494, 45.33974],
          [-123.979715, 45.347724],
          [-123.973398, 45.354791],
          [-123.965728, 45.386242],
          [-123.960557, 45.430778],
          [-123.964074, 45.449112],
          [-123.972953, 45.467513],
          [-123.976544, 45.489733],
          [-123.970794, 45.493507],
          [-123.96634, 45.493417],
          [-123.957568, 45.510399],
          [-123.947556, 45.564878],
          [-123.956711, 45.571303],
          [-123.951246, 45.585775],
          [-123.939005, 45.661923],
          [-123.939448, 45.708795],
          [-123.943121, 45.727031],
          [-123.946027, 45.733249],
          [-123.968563, 45.757019],
          [-123.982578, 45.761815],
          [-123.981864, 45.768285],
          [-123.969459, 45.782371],
          [-123.96934, 45.783197]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41063",
      state: "41",
      county: "063",
      name: "Wallowa County",
      censusArea: 3146.189,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.603163, 45.998887],
          [-117.504833, 45.998317],
          [-117.48013, 45.99787],
          [-117.480103, 45.99787],
          [-117.47536, 45.997855],
          [-117.475148, 45.997893],
          [-117.216731, 45.998356],
          [-117.214534, 45.99832],
          [-117.212616, 45.998321],
          [-116.915989, 45.995413],
          [-116.875125, 45.9422],
          [-116.866544, 45.916958],
          [-116.857254, 45.904159],
          [-116.796418, 45.853807],
          [-116.789099, 45.847749],
          [-116.633377, 45.784681],
          [-116.59421, 45.77908],
          [-116.54809, 45.752364],
          [-116.535698, 45.734231],
          [-116.538014, 45.714929],
          [-116.535396, 45.691734],
          [-116.528272, 45.681473],
          [-116.510938, 45.668114],
          [-116.48912, 45.65114],
          [-116.471504, 45.628467],
          [-116.463504, 45.615785],
          [-116.463635, 45.602785],
          [-116.481208, 45.580597],
          [-116.553473, 45.499107],
          [-116.673793, 45.321511],
          [-116.691197, 45.269245],
          [-116.696047, 45.254679],
          [-116.709536, 45.203015],
          [-116.729607, 45.142091],
          [-116.783902, 45.079022],
          [-117.266572, 45.080805],
          [-117.267162, 45.165858],
          [-117.476987, 45.165315],
          [-117.521904, 45.267762],
          [-117.583018, 45.338291],
          [-117.663557, 45.365757],
          [-117.683957, 45.515143],
          [-117.727271, 45.514705],
          [-117.727149, 45.617272],
          [-117.787637, 45.689597],
          [-117.788236, 45.773726],
          [-117.747101, 45.773346],
          [-117.747313, 45.861012],
          [-117.972922, 45.860586],
          [-117.977688, 45.860558],
          [-117.977766, 46.000695],
          [-117.603163, 45.998887]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41067",
      state: "41",
      county: "067",
      name: "Washington County",
      censusArea: 724.23,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.764458, 45.529727],
          [-122.743862, 45.519515],
          [-122.743613, 45.454868],
          [-122.74381, 45.452021],
          [-122.743807, 45.447902],
          [-122.743608, 45.447667],
          [-122.743633, 45.442959],
          [-122.743904, 45.442774],
          [-122.743809, 45.440429],
          [-122.744065, 45.433283],
          [-122.743741, 45.33201],
          [-122.868009, 45.317376],
          [-123.135329, 45.433302],
          [-123.464932, 45.43384],
          [-123.442733, 45.521198],
          [-123.299441, 45.607306],
          [-123.48472, 45.679313],
          [-123.360892, 45.708695],
          [-123.360962, 45.779669],
          [-123.030996, 45.779199],
          [-122.928961, 45.721502],
          [-122.929214, 45.633864],
          [-122.764458, 45.529727]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29101",
      state: "29",
      county: "101",
      name: "Johnson County",
      censusArea: 829.282,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.114175, 38.917304],
          [-93.836319, 38.908709],
          [-93.834719, 38.937909],
          [-93.497278, 38.92843],
          [-93.51103, 38.55621],
          [-94.064317, 38.56738],
          [-94.119664, 38.568712],
          [-94.119096, 38.833167],
          [-94.113712, 38.915366],
          [-94.114175, 38.917304]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29107",
      state: "29",
      county: "107",
      name: "Lafayette County",
      censusArea: 628.433,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.109584, 39.018745],
          [-94.104823, 39.143507],
          [-93.988892, 39.152786],
          [-93.840147, 39.215467],
          [-93.758463, 39.207021],
          [-93.650526, 39.248214],
          [-93.487885, 39.228875],
          [-93.477233, 39.292796],
          [-93.496377, 38.942837],
          [-93.497278, 38.92843],
          [-93.834719, 38.937909],
          [-93.836319, 38.908709],
          [-94.114175, 38.917304],
          [-94.11427, 38.92857],
          [-94.112928, 38.960904],
          [-94.111478, 38.982862],
          [-94.110863, 38.986311],
          [-94.109953, 39.015815],
          [-94.109584, 39.018745]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29113",
      state: "29",
      county: "113",
      name: "Lincoln County",
      censusArea: 626.556,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.71158, 39.046798],
          [-90.66587, 38.934195],
          [-90.77821, 38.922488],
          [-90.817827, 38.875966],
          [-90.958536, 38.870865],
          [-91.111251, 38.872709],
          [-91.110187, 38.931901],
          [-91.189348, 38.932151],
          [-91.18839, 38.992141],
          [-91.264287, 38.992762],
          [-91.260232, 39.139845],
          [-91.18546, 39.13905],
          [-91.182942, 39.227233],
          [-90.718996, 39.224973],
          [-90.681086, 39.10059],
          [-90.682744, 39.088348],
          [-90.712541, 39.057064],
          [-90.713629, 39.053977],
          [-90.71158, 39.046798]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29123",
      state: "29",
      county: "123",
      name: "Madison County",
      censusArea: 494.39,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.535016, 37.643641],
          [-90.516756, 37.642665],
          [-90.146778, 37.641618],
          [-90.147306, 37.613546],
          [-90.146763, 37.597434],
          [-90.145699, 37.312091],
          [-90.217657, 37.314967],
          [-90.549729, 37.317594],
          [-90.535016, 37.643641]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29127",
      state: "29",
      county: "127",
      name: "Marion County",
      censusArea: 436.924,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.369953, 39.745042],
          [-91.370009, 39.732524],
          [-91.367753, 39.729029],
          [-91.349039, 39.712316],
          [-91.313162, 39.684907],
          [-91.715497, 39.686492],
          [-91.715247, 39.657644],
          [-91.846743, 39.658513],
          [-91.840353, 39.948235],
          [-91.455887, 39.945538],
          [-91.446385, 39.870394],
          [-91.432919, 39.840554],
          [-91.365396, 39.777266],
          [-91.367962, 39.759124],
          [-91.369953, 39.745042]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39163",
      state: "39",
      county: "163",
      name: "Vinton County",
      censusArea: 412.36,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.289663, 39.384195],
          [-82.298476, 39.294996],
          [-82.260243, 39.292915],
          [-82.263682, 39.256194],
          [-82.263645, 39.255898],
          [-82.263767, 39.253786],
          [-82.263952, 39.253289],
          [-82.268583, 39.20376],
          [-82.306669, 39.205494],
          [-82.322874, 39.027674],
          [-82.435309, 39.035079],
          [-82.424677, 39.137972],
          [-82.536306, 39.180757],
          [-82.762892, 39.207967],
          [-82.748591, 39.368165],
          [-82.51758, 39.369468],
          [-82.514479, 39.395767],
          [-82.289663, 39.384195]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47073",
      state: "47",
      county: "073",
      name: "Hawkins County",
      censusArea: 486.975,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.830433, 36.593761],
          [-82.69578, 36.593698],
          [-82.679879, 36.593698],
          [-82.609176, 36.594099],
          [-82.680677, 36.4318],
          [-82.676177, 36.4234],
          [-82.702566, 36.410412],
          [-82.936583, 36.334897],
          [-83.082312, 36.242296],
          [-83.16259, 36.345292],
          [-83.255292, 36.289092],
          [-83.280595, 36.394689],
          [-83.010789, 36.563691],
          [-82.950519, 36.543005],
          [-82.830433, 36.593761]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47079",
      state: "47",
      county: "079",
      name: "Henry County",
      censusArea: 562.096,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.99092, 36.360133],
          [-88.087674, 36.359166],
          [-88.083157, 36.256724],
          [-88.212559, 36.12029],
          [-88.211698, 36.145974],
          [-88.530346, 36.151573],
          [-88.516346, 36.501431],
          [-88.51227, 36.501506],
          [-88.51192, 36.501457],
          [-88.48921, 36.501068],
          [-88.489075, 36.501068],
          [-88.472564, 36.501028],
          [-88.452543, 36.500872],
          [-88.450161, 36.501101],
          [-88.41636, 36.500756],
          [-88.330799, 36.500531],
          [-88.325895, 36.500483],
          [-88.320794, 36.500432],
          [-88.053292, 36.49713],
          [-88.05848, 36.439224],
          [-87.99092, 36.360133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47089",
      state: "47",
      county: "089",
      name: "Jefferson County",
      censusArea: 274.078,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.457728, 36.155375],
          [-83.293689, 36.138193],
          [-83.234585, 36.085094],
          [-83.276677, 36.007652],
          [-83.310782, 35.895846],
          [-83.523113, 35.963659],
          [-83.673297, 36.038486],
          [-83.66746, 36.080587],
          [-83.659387, 36.085533],
          [-83.674454, 36.116399],
          [-83.674688, 36.124463],
          [-83.541099, 36.181987],
          [-83.467097, 36.174688],
          [-83.458695, 36.155289],
          [-83.457728, 36.155375]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47091",
      state: "47",
      county: "091",
      name: "Johnson County",
      censusArea: 298.475,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.6469, 36.611918],
          [-81.677535, 36.588117],
          [-81.699962, 36.539714],
          [-81.707963, 36.536209],
          [-81.700553, 36.51519],
          [-81.700093, 36.514158],
          [-81.699601, 36.512883],
          [-81.699446, 36.511504],
          [-81.697744, 36.508448],
          [-81.697829, 36.507544],
          [-81.69729, 36.504887],
          [-81.69797, 36.504063],
          [-81.699923, 36.500865],
          [-81.700238, 36.500475],
          [-81.699928, 36.498018],
          [-81.698265, 36.497221],
          [-81.697261, 36.496141],
          [-81.696835, 36.493393],
          [-81.695907, 36.49158],
          [-81.697287, 36.484738],
          [-81.715082, 36.453365],
          [-81.714277, 36.450978],
          [-81.720969, 36.3875],
          [-81.725938, 36.340364],
          [-81.730976, 36.341187],
          [-81.747842, 36.337356],
          [-81.764927, 36.338672],
          [-81.789712, 36.348324],
          [-81.793688, 36.360428],
          [-81.800812, 36.358073],
          [-81.855005, 36.337259],
          [-81.908137, 36.302013],
          [-81.918113, 36.28711],
          [-81.932994, 36.264881],
          [-82.058946, 36.367515],
          [-81.94984, 36.472385],
          [-81.983843, 36.508314],
          [-81.826742, 36.614215],
          [-81.6469, 36.611918]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47097",
      state: "47",
      county: "097",
      name: "Lauderdale County",
      censusArea: 471.992,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.502118, 35.580621],
          [-89.651056, 35.648009],
          [-89.758116, 35.650361],
          [-89.909797, 35.537914],
          [-89.884932, 35.655107],
          [-89.89051, 35.652408],
          [-89.906147, 35.651145],
          [-89.922749, 35.655293],
          [-89.937383, 35.665711],
          [-89.955753, 35.690621],
          [-89.958882, 35.723834],
          [-89.956254, 35.733386],
          [-89.950278, 35.738493],
          [-89.909996, 35.759396],
          [-89.905538, 35.759063],
          [-89.889023, 35.750558],
          [-89.865631, 35.746577],
          [-89.821216, 35.756716],
          [-89.703875, 35.820281],
          [-89.701045, 35.828227],
          [-89.702883, 35.834153],
          [-89.709261, 35.838911],
          [-89.729517, 35.847632],
          [-89.749424, 35.852955],
          [-89.769413, 35.861558],
          [-89.772467, 35.865098],
          [-89.773564, 35.871697],
          [-89.771726, 35.879724],
          [-89.765689, 35.891299],
          [-89.756036, 35.896817],
          [-89.73361, 35.904699],
          [-89.714934, 35.906247],
          [-89.688141, 35.896946],
          [-89.687939, 35.905384],
          [-89.406965, 35.946599],
          [-89.342829, 35.880934],
          [-89.356033, 35.817635],
          [-89.401805, 35.822657],
          [-89.502118, 35.580621]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47103",
      state: "47",
      county: "103",
      name: "Lincoln County",
      censusArea: 570.338,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.677616, 34.99207],
          [-86.783628, 34.991925],
          [-86.783648, 34.991925],
          [-86.820657, 34.991764],
          [-86.836306, 34.991764],
          [-86.828301, 35.263677],
          [-86.746899, 35.252878],
          [-86.602613, 35.327013],
          [-86.599478, 35.36495],
          [-86.582875, 35.370983],
          [-86.552694, 35.359983],
          [-86.543671, 35.363211],
          [-86.525306, 35.354718],
          [-86.52728, 35.311536],
          [-86.392192, 35.235381],
          [-86.366236, 35.124405],
          [-86.318168, 35.126986],
          [-86.320277, 35.032503],
          [-86.318761, 34.991147],
          [-86.397203, 34.99166],
          [-86.433927, 34.991085],
          [-86.467798, 34.990692],
          [-86.528485, 34.990677],
          [-86.555864, 34.990971],
          [-86.571217, 34.991011],
          [-86.588962, 34.991197],
          [-86.600039, 34.99124],
          [-86.641212, 34.99174],
          [-86.65961, 34.991792],
          [-86.670853, 34.992],
          [-86.67436, 34.992001],
          [-86.676726, 34.99207],
          [-86.677616, 34.99207]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47115",
      state: "47",
      county: "115",
      name: "Marion County",
      censusArea: 498.16,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.558551, 35.319292],
          [-85.444144, 35.262646],
          [-85.387089, 35.147169],
          [-85.361959, 35.087773],
          [-85.474472, 34.983972],
          [-85.605165, 34.984678],
          [-85.824411, 34.988142],
          [-85.828724, 34.988165],
          [-85.863935, 34.988379],
          [-85.873025, 35.223362],
          [-85.740113, 35.215295],
          [-85.709418, 35.289251],
          [-85.558551, 35.319292]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47121",
      state: "47",
      county: "121",
      name: "Meigs County",
      censusArea: 195.122,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.625868, 35.658869],
          [-84.61988, 35.658865],
          [-84.619865, 35.644651],
          [-84.814543, 35.43697],
          [-84.860164, 35.350074],
          [-84.94635, 35.287788],
          [-85.016343, 35.409288],
          [-84.968953, 35.466674],
          [-84.907048, 35.447871],
          [-84.873969, 35.550217],
          [-84.794904, 35.55255],
          [-84.781804, 35.673908],
          [-84.723853, 35.752896],
          [-84.625868, 35.658869]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47123",
      state: "47",
      county: "123",
      name: "Monroe County",
      censusArea: 635.565,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.495529, 35.285472],
          [-84.401025, 35.330374],
          [-84.409226, 35.398378],
          [-84.52585, 35.624186],
          [-84.423549, 35.671735],
          [-84.188319, 35.610549],
          [-84.081561, 35.541231],
          [-83.993815, 35.550001],
          [-83.961056, 35.463738],
          [-83.961054, 35.462838],
          [-83.9614, 35.459496],
          [-83.999906, 35.425201],
          [-84.00225, 35.422548],
          [-84.024756, 35.353896],
          [-84.035343, 35.350833],
          [-84.037494, 35.34985],
          [-84.038081, 35.348363],
          [-84.03245, 35.32653],
          [-84.032479, 35.325318],
          [-84.02141, 35.301383],
          [-84.02351, 35.295783],
          [-84.029033, 35.291049],
          [-84.055712, 35.268182],
          [-84.097508, 35.247382],
          [-84.115048, 35.249765],
          [-84.115279, 35.250438],
          [-84.12115, 35.250644],
          [-84.124915, 35.24983],
          [-84.202879, 35.255772],
          [-84.211818, 35.266078],
          [-84.223718, 35.269078],
          [-84.227818, 35.267878],
          [-84.29024, 35.225572],
          [-84.292321, 35.206677],
          [-84.495529, 35.285472]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47131",
      state: "47",
      county: "131",
      name: "Obion County",
      censusArea: 544.728,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.346056, 36.50321],
          [-89.346053, 36.50321],
          [-89.282298, 36.506782],
          [-89.279091, 36.506511],
          [-89.211409, 36.50563],
          [-89.163429, 36.504526],
          [-89.163224, 36.504522],
          [-89.119805, 36.503647],
          [-89.117537, 36.503603],
          [-89.090146, 36.503392],
          [-89.072118, 36.503249],
          [-89.058871, 36.503157],
          [-89.034649, 36.502964],
          [-89.010439, 36.50271],
          [-89.006825, 36.502684],
          [-89.000063, 36.502633],
          [-88.964471, 36.502191],
          [-88.874725, 36.502446],
          [-88.834866, 36.502911],
          [-88.834626, 36.502914],
          [-88.827301, 36.502852],
          [-88.81642, 36.410732],
          [-88.950205, 36.409248],
          [-88.959839, 36.222834],
          [-88.961232, 36.201635],
          [-89.154464, 36.204678],
          [-89.482971, 36.212659],
          [-89.443726, 36.236637],
          [-89.36012, 36.409277],
          [-89.346056, 36.50321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39007",
      state: "39",
      county: "007",
      name: "Ashtabula County",
      censusArea: 701.931,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.973674, 41.858122],
          [-80.971141, 41.858409],
          [-80.958494, 41.859838],
          [-80.936244, 41.862352],
          [-80.916528, 41.865954],
          [-80.900342, 41.868912],
          [-80.853542, 41.884685],
          [-80.852064, 41.885183],
          [-80.826889, 41.893668],
          [-80.819165, 41.896271],
          [-80.816444, 41.897188],
          [-80.814943, 41.897694],
          [-80.81324, 41.89876],
          [-80.812659, 41.899125],
          [-80.808697, 41.901606],
          [-80.80801, 41.902304],
          [-80.806133, 41.904211],
          [-80.801436, 41.908982],
          [-80.800794, 41.909635],
          [-80.799822, 41.909749],
          [-80.798311, 41.909926],
          [-80.794388, 41.910386],
          [-80.78641, 41.911322],
          [-80.785497, 41.911429],
          [-80.784682, 41.911525],
          [-80.782944, 41.908294],
          [-80.782052, 41.906635],
          [-80.781112, 41.906897],
          [-80.77709, 41.908018],
          [-80.771784, 41.909496],
          [-80.770756, 41.909782],
          [-80.76802, 41.910545],
          [-80.766046, 41.911095],
          [-80.762176, 41.912173],
          [-80.757945, 41.913352],
          [-80.727504, 41.918593],
          [-80.720816, 41.919744],
          [-80.717469, 41.920656],
          [-80.712218, 41.922087],
          [-80.708293, 41.923157],
          [-80.699363, 41.925591],
          [-80.693429, 41.927208],
          [-80.635576, 41.942976],
          [-80.619443, 41.947373],
          [-80.619242, 41.947428],
          [-80.60861, 41.950325],
          [-80.581882, 41.95761],
          [-80.579439, 41.958499],
          [-80.576735, 41.959483],
          [-80.576591, 41.959535],
          [-80.5755, 41.959932],
          [-80.572028, 41.961196],
          [-80.563327, 41.964362],
          [-80.560671, 41.965328],
          [-80.555898, 41.967065],
          [-80.553836, 41.967815],
          [-80.533774, 41.973475],
          [-80.53139, 41.974147],
          [-80.530164, 41.974493],
          [-80.529274, 41.974744],
          [-80.525521, 41.975803],
          [-80.522057, 41.97678],
          [-80.519461, 41.977513],
          [-80.519425, 41.977523],
          [-80.519425, 41.977522],
          [-80.519405, 41.976158],
          [-80.519304, 41.943992],
          [-80.519345, 41.929168],
          [-80.519294, 41.849563],
          [-80.519239, 41.765138],
          [-80.519369, 41.752487],
          [-80.519408, 41.739359],
          [-80.519373, 41.701473],
          [-80.519424, 41.671228],
          [-80.519357, 41.669767],
          [-80.519339, 41.539297],
          [-80.519157, 41.528769],
          [-80.519225, 41.499924],
          [-81.003125, 41.501685],
          [-81.00299, 41.700009],
          [-81.003631, 41.715137],
          [-81.004763, 41.726436],
          [-81.002117, 41.854908],
          [-80.991799, 41.856074],
          [-80.973674, 41.858122]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48371",
      state: "48",
      county: "371",
      name: "Pecos County",
      censusArea: 4763.847,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.01104, 31.371306],
          [-102.827087, 31.267909],
          [-102.767365, 31.293803],
          [-102.676497, 31.326823],
          [-102.429545, 31.193557],
          [-102.388804, 31.087156],
          [-102.191731, 31.003788],
          [-101.98364, 30.98717],
          [-101.883436, 30.925647],
          [-101.76842, 30.653077],
          [-101.768377, 30.652938],
          [-101.768806, 30.653133],
          [-101.769105, 30.653327],
          [-101.769353, 30.653369],
          [-101.997614, 30.654086],
          [-102.000408, 30.654933],
          [-102.02388, 30.655141],
          [-102.031813, 30.655503],
          [-102.032467, 30.655385],
          [-102.032927, 30.655489],
          [-102.033231, 30.655496],
          [-102.034002, 30.655433],
          [-102.034385, 30.655268],
          [-102.137778, 30.655982],
          [-102.138406, 30.597521],
          [-102.342986, 30.598759],
          [-102.343085, 30.284116],
          [-102.566937, 30.283269],
          [-102.567049, 30.052808],
          [-103.195667, 30.495574],
          [-103.439976, 30.665939],
          [-103.585084, 30.76647],
          [-103.01104, 31.371306]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48377",
      state: "48",
      county: "377",
      name: "Presidio County",
      censusArea: 3855.237,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.80068, 30.412527],
          [-103.798159, 29.999606],
          [-103.792705, 29.262931],
          [-103.838303, 29.278304],
          [-103.924976, 29.293913],
          [-103.963076, 29.298404],
          [-104.025488, 29.314404],
          [-104.143692, 29.383278],
          [-104.167506, 29.402928],
          [-104.21341, 29.462069],
          [-104.235847, 29.496744],
          [-104.264155, 29.514001],
          [-104.290728, 29.519326],
          [-104.321603, 29.527923],
          [-104.394351, 29.560535],
          [-104.515512, 29.641493],
          [-104.538569, 29.677396],
          [-104.549773, 29.716091],
          [-104.549117, 29.728814],
          [-104.566055, 29.771379],
          [-104.633275, 29.870485],
          [-104.667301, 29.910401],
          [-104.674932, 29.911112],
          [-104.679877, 29.920946],
          [-104.693592, 30.019077],
          [-104.685793, 30.092656],
          [-104.687296, 30.179464],
          [-104.708916, 30.235826],
          [-104.761634, 30.301148],
          [-104.78102, 30.314564],
          [-104.859521, 30.390413],
          [-104.872808, 30.500145],
          [-104.929778, 30.59924],
          [-104.972071, 30.61026],
          [-104.985365, 30.623792],
          [-104.979527, 30.629247],
          [-104.976636, 30.628946],
          [-103.90101, 30.41219],
          [-103.80068, 30.412527]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48391",
      state: "48",
      county: "391",
      name: "Refugio County",
      censusArea: 770.444,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.260818, 28.075759],
          [-97.351825, 28.123502],
          [-97.427171, 28.117668],
          [-97.541071, 28.164669],
          [-97.375579, 28.388684],
          [-97.375268, 28.389116],
          [-97.160751, 28.553475],
          [-97.048529, 28.549976],
          [-96.982147, 28.499639],
          [-96.890951, 28.507178],
          [-96.76696, 28.410737],
          [-96.768352, 28.410389],
          [-96.772209, 28.408074],
          [-96.775985, 28.405809],
          [-96.780796, 28.398421],
          [-96.790235, 28.383926],
          [-96.794554, 28.365688],
          [-96.794796, 28.364668],
          [-96.794815, 28.364587],
          [-96.794812, 28.364515],
          [-96.794812, 28.364503],
          [-96.794357, 28.350865],
          [-96.794306, 28.34932],
          [-96.794016, 28.347249],
          [-96.793926, 28.346606],
          [-96.793835, 28.345959],
          [-96.793765, 28.345453],
          [-96.79254, 28.336705],
          [-96.790744, 28.323874],
          [-96.791159, 28.319095],
          [-97.123072, 28.272817],
          [-97.023586, 28.187657],
          [-97.144916, 28.102622],
          [-97.260818, 28.075759]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48401",
      state: "48",
      county: "401",
      name: "Rusk County",
      censusArea: 924.027,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.452416, 31.844281],
          [-94.937532, 31.845555],
          [-94.985037, 31.846193],
          [-94.985272, 32.137989],
          [-94.985411, 32.37082],
          [-94.638347, 32.370539],
          [-94.600776, 32.360044],
          [-94.579219, 32.394916],
          [-94.491818, 32.394137],
          [-94.5805, 32.201363],
          [-94.599978, 31.973193],
          [-94.511431, 31.973984],
          [-94.452416, 31.844281]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48407",
      state: "48",
      county: "407",
      name: "San Jacinto County",
      censusArea: 569.243,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.849414, 30.493553],
          [-95.024589, 30.406903],
          [-95.03246, 30.404091],
          [-95.165897, 30.344976],
          [-95.308395, 30.40575],
          [-95.359156, 30.504369],
          [-95.327482, 30.859537],
          [-95.252385, 30.906719],
          [-95.20018, 30.824566],
          [-95.131205, 30.781703],
          [-95.135384, 30.693816],
          [-95.054602, 30.680173],
          [-95.028628, 30.578485],
          [-94.830429, 30.541613],
          [-94.849414, 30.493553]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48409",
      state: "48",
      county: "409",
      name: "San Patricio County",
      censusArea: 693.451,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.541071, 28.164669],
          [-97.427171, 28.117668],
          [-97.351825, 28.123502],
          [-97.260818, 28.075759],
          [-97.259401, 28.073196],
          [-97.134801, 27.902467],
          [-97.135783, 27.899445],
          [-97.136615, 27.898956],
          [-97.144435, 27.894356],
          [-97.155122, 27.880615],
          [-97.183455, 27.833231],
          [-97.184639, 27.831251],
          [-97.186709, 27.825453],
          [-97.186871, 27.825001],
          [-97.187183, 27.824126],
          [-97.188866, 27.823772],
          [-97.192874, 27.822928],
          [-97.193701, 27.822754],
          [-97.196852, 27.822091],
          [-97.209127, 27.822091],
          [-97.209575, 27.822091],
          [-97.211226, 27.822391],
          [-97.219738, 27.823939],
          [-97.220771, 27.824126],
          [-97.222212, 27.824649],
          [-97.222957, 27.824919],
          [-97.225176, 27.825723],
          [-97.225435, 27.826145],
          [-97.225811, 27.826755],
          [-97.226012, 27.827083],
          [-97.227317, 27.829204],
          [-97.227317, 27.830939],
          [-97.227317, 27.831258],
          [-97.227317, 27.832895],
          [-97.227317, 27.832952],
          [-97.226514, 27.838307],
          [-97.228388, 27.843661],
          [-97.234512, 27.849063],
          [-97.241127, 27.857714],
          [-97.241393, 27.858953],
          [-97.242654, 27.864839],
          [-97.242922, 27.865208],
          [-97.250797, 27.876035],
          [-97.256394, 27.877901],
          [-97.258305, 27.878538],
          [-97.26301, 27.880106],
          [-97.272253, 27.881427],
          [-97.273698, 27.881633],
          [-97.276632, 27.881144],
          [-97.285466, 27.879672],
          [-97.295072, 27.878071],
          [-97.301455, 27.875907],
          [-97.317892, 27.870335],
          [-97.325097, 27.867893],
          [-97.327494, 27.866405],
          [-97.354614, 27.849572],
          [-97.360212, 27.85059],
          [-97.360547, 27.850363],
          [-97.361246, 27.849891],
          [-97.377166, 27.875181],
          [-97.589473, 27.856101],
          [-97.691074, 27.917353],
          [-97.805765, 27.934287],
          [-97.798517, 27.995659],
          [-97.883148, 28.0569],
          [-97.904081, 28.114228],
          [-97.817716, 28.176848],
          [-97.567622, 28.129683],
          [-97.541071, 28.164669]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36079",
      state: "36",
      county: "079",
      name: "Putnam County",
      censusArea: 230.312,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.982584, 41.321693],
          [-73.981384, 41.324693],
          [-73.981486, 41.438905],
          [-73.933775, 41.488279],
          [-73.530067, 41.527194],
          [-73.533969, 41.479693],
          [-73.534055, 41.478968],
          [-73.53415, 41.47806],
          [-73.534269, 41.476911],
          [-73.534269, 41.476394],
          [-73.534369, 41.475894],
          [-73.535769, 41.457159],
          [-73.535857, 41.455709],
          [-73.535885, 41.455236],
          [-73.535986, 41.45306],
          [-73.536067, 41.451331],
          [-73.536969, 41.441094],
          [-73.537469, 41.43589],
          [-73.537673, 41.433905],
          [-73.543641, 41.376778],
          [-73.544728, 41.366375],
          [-73.982584, 41.321693]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48443",
      state: "48",
      county: "443",
      name: "Terrell County",
      censusArea: 2358.026,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.761839, 29.777187],
          [-101.786353, 29.782047],
          [-101.883655, 29.796193],
          [-101.944913, 29.801468],
          [-102.040539, 29.790061],
          [-102.050044, 29.78507],
          [-102.116076, 29.792527],
          [-102.262466, 29.853765],
          [-102.282247, 29.863598],
          [-102.297331, 29.875194],
          [-102.320819, 29.875193],
          [-102.320819, 29.878933],
          [-102.567049, 30.052808],
          [-102.566937, 30.283269],
          [-102.343085, 30.284116],
          [-102.342986, 30.598759],
          [-102.138406, 30.597521],
          [-102.137778, 30.655982],
          [-102.034385, 30.655268],
          [-102.034002, 30.655433],
          [-102.033231, 30.655496],
          [-102.032927, 30.655489],
          [-102.032467, 30.655385],
          [-102.031813, 30.655503],
          [-102.02388, 30.655141],
          [-102.000408, 30.654933],
          [-101.997614, 30.654086],
          [-101.769353, 30.653369],
          [-101.769105, 30.653327],
          [-101.768806, 30.653133],
          [-101.768377, 30.652938],
          [-101.76842, 30.653077],
          [-101.65263, 30.619374],
          [-101.646245, 30.560763],
          [-101.740429, 30.420897],
          [-101.65853, 30.357861],
          [-101.7584, 30.288044],
          [-101.760924, 29.781802],
          [-101.761839, 29.777187]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48461",
      state: "48",
      county: "461",
      name: "Upton County",
      censusArea: 1241.324,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.31805, 31.651327],
          [-102.288782, 31.65137],
          [-102.287345, 31.651276],
          [-101.775802, 31.651319],
          [-101.776191, 31.079784],
          [-102.301212, 31.086212],
          [-102.31805, 31.651327]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48465",
      state: "48",
      county: "465",
      name: "Val Verde County",
      censusArea: 3144.75,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.699932, 29.623897],
          [-100.699141, 29.419747],
          [-100.745564, 29.254809],
          [-100.793662, 29.242146],
          [-100.797671, 29.246943],
          [-100.954042, 29.347598],
          [-100.995607, 29.363403],
          [-101.037642, 29.414681],
          [-101.043364, 29.42988],
          [-101.060151, 29.458661],
          [-101.087149, 29.469414],
          [-101.137503, 29.473542],
          [-101.235275, 29.524854],
          [-101.247319, 29.618482],
          [-101.250383, 29.624171],
          [-101.314135, 29.659054],
          [-101.343743, 29.662047],
          [-101.403926, 29.757587],
          [-101.455007, 29.770188],
          [-101.625958, 29.771063],
          [-101.635128, 29.758675],
          [-101.646418, 29.754304],
          [-101.761839, 29.777187],
          [-101.760924, 29.781802],
          [-101.7584, 30.288044],
          [-100.960643, 30.287776],
          [-100.700393, 30.288276],
          [-100.699932, 29.623897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48473",
      state: "48",
      county: "473",
      name: "Waller County",
      censusArea: 513.43,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.871864, 30.241108],
          [-95.842138, 30.243445],
          [-95.804872, 30.245645],
          [-95.804306, 30.24557],
          [-95.80333, 30.090096],
          [-95.960733, 30.163415],
          [-95.82622, 29.78828],
          [-96.032711, 29.727944],
          [-96.049234, 29.803187],
          [-96.121313, 29.841963],
          [-96.122754, 29.968545],
          [-96.084541, 30.005137],
          [-96.146052, 30.070224],
          [-96.191418, 30.134669],
          [-96.15901, 30.206613],
          [-96.093165, 30.225187],
          [-95.872022, 30.241097],
          [-95.871864, 30.241108]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48479",
      state: "48",
      county: "479",
      name: "Webb County",
      censusArea: 3361.482,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.954669, 27.269397],
          [-99.33361, 27.273223],
          [-99.370621, 27.319174],
          [-99.453949, 27.264673],
          [-99.463309, 27.268437],
          [-99.47088, 27.265444],
          [-99.494678, 27.277595],
          [-99.529654, 27.306051],
          [-99.536443, 27.312538],
          [-99.537771, 27.316073],
          [-99.50696, 27.35642],
          [-99.492831, 27.390491],
          [-99.487704, 27.409924],
          [-99.480219, 27.485796],
          [-99.519381, 27.573569],
          [-99.541644, 27.605772],
          [-99.600619, 27.641314],
          [-99.623478, 27.644136],
          [-99.626294, 27.64245],
          [-99.627123, 27.638412],
          [-99.666175, 27.638324],
          [-99.843463, 27.77456],
          [-99.876174, 27.806276],
          [-99.901486, 27.864162],
          [-99.904385, 27.875284],
          [-99.895828, 27.904178],
          [-99.922046, 27.922119],
          [-99.991447, 27.99456],
          [-100.057049, 28.095656],
          [-100.080868, 28.137192],
          [-100.083393, 28.144035],
          [-100.088143, 28.147503],
          [-100.14414, 28.168238],
          [-100.171323, 28.17623],
          [-100.202448, 28.190554],
          [-100.209464, 28.196866],
          [-100.113719, 28.197819],
          [-99.394177, 28.204627],
          [-99.388946, 28.030419],
          [-98.803325, 28.05748],
          [-98.798323, 27.354029],
          [-98.798087, 27.268012],
          [-98.954669, 27.269397]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48487",
      state: "48",
      county: "487",
      name: "Wilbarger County",
      censusArea: 970.844,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.211423, 34.337897],
          [-99.211396, 34.337688],
          [-99.209724, 34.324935],
          [-99.2116, 34.31397],
          [-99.211648, 34.292232],
          [-99.19089, 34.215251],
          [-99.157114, 34.207414],
          [-99.119204, 34.201747],
          [-99.060344, 34.204761],
          [-98.966743, 34.201185],
          [-98.952527, 34.195621],
          [-98.953088, 33.834],
          [-99.47565, 33.834005],
          [-99.475294, 34.085618],
          [-99.475115, 34.39593],
          [-99.474161, 34.398052],
          [-99.453477, 34.388756],
          [-99.44076, 34.374123],
          [-99.407168, 34.372605],
          [-99.398635, 34.375832],
          [-99.356713, 34.442144],
          [-99.261321, 34.403499],
          [-99.211423, 34.337897]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48495",
      state: "48",
      county: "495",
      name: "Winkler County",
      censusArea: 841.107,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.799086, 32.085795],
          [-102.798939, 31.651783],
          [-102.801793, 31.651306],
          [-103.327538, 31.65142],
          [-103.326501, 32.00037],
          [-103.278521, 32.000419],
          [-103.270383, 32.000326],
          [-103.267708, 32.000324],
          [-103.267633, 32.000475],
          [-103.215641, 32.000513],
          [-103.088698, 32.000453],
          [-103.085876, 32.000465],
          [-103.064423, 32.000518],
          [-103.064344, 32.087051],
          [-102.799086, 32.085795]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48501",
      state: "48",
      county: "501",
      name: "Yoakum County",
      censusArea: 799.708,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.595023, 32.958831],
          [-103.064657, 32.959097],
          [-103.064679, 32.964373],
          [-103.064625, 32.999899],
          [-103.064452, 33.01029],
          [-103.06398, 33.038693],
          [-103.063905, 33.042055],
          [-103.060103, 33.219225],
          [-103.057856, 33.315234],
          [-103.057487, 33.329477],
          [-103.056655, 33.388416],
          [-102.594836, 33.388489],
          [-102.595023, 32.958831]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48505",
      state: "48",
      county: "505",
      name: "Zapata County",
      censusArea: 998.412,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.95423, 26.785694],
          [-99.011112, 26.675023],
          [-99.172058, 26.57086],
          [-99.208951, 26.689489],
          [-99.242444, 26.788262],
          [-99.268613, 26.843213],
          [-99.321819, 26.906846],
          [-99.379149, 26.93449],
          [-99.446524, 27.023008],
          [-99.452316, 27.062669],
          [-99.451035, 27.066765],
          [-99.446593, 27.070593],
          [-99.429879, 27.094323],
          [-99.426348, 27.176262],
          [-99.441549, 27.24992],
          [-99.452391, 27.264046],
          [-99.453949, 27.264673],
          [-99.370621, 27.319174],
          [-99.33361, 27.273223],
          [-98.954669, 27.269397],
          [-98.95423, 26.785694]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49003",
      state: "49",
      county: "003",
      name: "Box Elder County",
      censusArea: 5745.551,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.885476, 41.426435],
          [-111.9591, 41.433706],
          [-112.028376, 41.336439],
          [-112.237877, 41.336739],
          [-112.493393, 41.076735],
          [-112.799307, 41.000236],
          [-114.042145, 40.999926],
          [-114.039882, 41.741991],
          [-114.041152, 41.850595],
          [-114.041107, 41.850573],
          [-114.041723, 41.99372],
          [-113.893261, 41.988057],
          [-113.796082, 41.989104],
          [-113.76453, 41.989459],
          [-113.431563, 41.993799],
          [-113.40223, 41.994161],
          [-113.396497, 41.99425],
          [-113.000821, 41.998223],
          [-113.00082, 41.998223],
          [-112.979218, 41.998263],
          [-112.909587, 41.998791],
          [-112.882367, 41.998922],
          [-112.880619, 41.998921],
          [-112.833125, 41.999345],
          [-112.833084, 41.999305],
          [-112.788542, 41.999681],
          [-112.709375, 42.000309],
          [-112.648019, 42.000307],
          [-112.450814, 42.000953],
          [-112.450567, 42.001092],
          [-112.38617, 42.001126],
          [-112.264936, 42.000991],
          [-112.239107, 42.001217],
          [-112.163956, 41.996708],
          [-112.052735, 41.866015],
          [-112.051815, 41.699789],
          [-111.982078, 41.534032],
          [-111.916568, 41.538573],
          [-111.885476, 41.426435]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48005",
      state: "48",
      county: "005",
      name: "Angelina County",
      censusArea: 797.778,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.326616, 31.224754],
          [-94.129632, 31.09928],
          [-94.45782, 31.033326],
          [-94.561943, 31.058952],
          [-94.842947, 31.146578],
          [-94.909502, 31.337059],
          [-94.95811, 31.38693],
          [-94.959415, 31.388884],
          [-94.966254, 31.391205],
          [-94.964521, 31.395558],
          [-94.967634, 31.397412],
          [-94.969369, 31.396948],
          [-94.973581, 31.399759],
          [-94.97778, 31.399381],
          [-94.976068, 31.402],
          [-94.976291, 31.40525],
          [-94.979364, 31.405975],
          [-94.976033, 31.407744],
          [-94.983053, 31.411593],
          [-94.984753, 31.41385],
          [-94.988061, 31.414417],
          [-94.990043, 31.413356],
          [-94.993832, 31.41422],
          [-94.994108, 31.417835],
          [-94.997132, 31.41678],
          [-94.998247, 31.42004],
          [-95.001258, 31.417949],
          [-95.005566, 31.421349],
          [-95.003345, 31.42571],
          [-95.000212, 31.428196],
          [-94.865857, 31.526916],
          [-94.728679, 31.457226],
          [-94.544888, 31.431715],
          [-94.530634, 31.398654],
          [-94.495874, 31.405728],
          [-94.326616, 31.224754]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48007",
      state: "48",
      county: "007",
      name: "Aransas County",
      censusArea: 252.074,
      stateName: "Texas"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-96.853455, 28.061345],
            [-96.929053, 27.99044],
            [-96.966996, 27.950531],
            [-97.001441, 27.911442],
            [-97.03166, 27.869975],
            [-97.041799, 27.852926],
            [-97.045409, 27.837452],
            [-97.054724, 27.842419],
            [-97.055823, 27.843404],
            [-97.043226, 27.859119],
            [-97.013634, 27.90678],
            [-97.017955, 27.911494],
            [-97.016384, 27.917255],
            [-96.985745, 27.954048],
            [-96.977889, 27.976439],
            [-96.978805, 27.978272],
            [-96.9809, 27.978272],
            [-96.986007, 27.976177],
            [-96.986661, 27.980759],
            [-96.978282, 28.001709],
            [-96.967807, 28.020041],
            [-96.966759, 28.020368],
            [-96.965188, 28.013297],
            [-96.962569, 28.012381],
            [-96.952618, 28.01644],
            [-96.946988, 28.026522],
            [-96.932454, 28.035426],
            [-96.92643, 28.043413],
            [-96.929573, 28.0514],
            [-96.927085, 28.057292],
            [-96.906004, 28.076147],
            [-96.890947, 28.076802],
            [-96.886233, 28.084396],
            [-96.888328, 28.086622],
            [-96.889113, 28.099454],
            [-96.886887, 28.11713],
            [-96.879424, 28.131402],
            [-96.874972, 28.133236],
            [-96.870782, 28.131271],
            [-96.864628, 28.126296],
            [-96.857165, 28.115559],
            [-96.84538, 28.108881],
            [-96.83003, 28.111842],
            [-96.830861, 28.108],
            [-96.837736, 28.099491],
            [-96.837646, 28.094673],
            [-96.844083, 28.084351],
            [-96.847129, 28.070604],
            [-96.853912, 28.063699],
            [-96.853455, 28.061345]
          ]
        ],
        [
          [
            [-96.791159, 28.319095],
            [-96.791761, 28.31217],
            [-96.806011, 28.296902],
            [-96.809573, 28.290287],
            [-96.806011, 28.282144],
            [-96.799302, 28.272716],
            [-96.787181, 28.255681],
            [-96.787181, 28.250083],
            [-96.800413, 28.224128],
            [-96.810037, 28.217086],
            [-96.842143, 28.193594],
            [-96.872678, 28.176291],
            [-96.898123, 28.152881],
            [-96.910337, 28.147283],
            [-96.934765, 28.123873],
            [-96.962755, 28.123365],
            [-97.000414, 28.137614],
            [-97.007539, 28.136087],
            [-97.027386, 28.124382],
            [-97.028913, 28.117258],
            [-97.022806, 28.107588],
            [-97.023824, 28.103517],
            [-97.031966, 28.093848],
            [-97.035528, 28.084688],
            [-97.035528, 28.074],
            [-97.031457, 28.053644],
            [-97.025859, 28.041939],
            [-97.030948, 28.033288],
            [-97.040618, 28.028708],
            [-97.04876, 28.022092],
            [-97.061992, 27.996138],
            [-97.075732, 27.986977],
            [-97.121534, 27.923364],
            [-97.129168, 27.919801],
            [-97.134801, 27.902467],
            [-97.259401, 28.073196],
            [-97.260818, 28.075759],
            [-97.144916, 28.102622],
            [-97.023586, 28.187657],
            [-97.123072, 28.272817],
            [-96.791159, 28.319095]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48037",
      state: "48",
      county: "037",
      name: "Bowie County",
      censusArea: 885.006,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.746096, 33.703016],
          [-94.735128, 33.691267],
          [-94.660964, 33.660322],
          [-94.52838, 33.615995],
          [-94.524055, 33.615765],
          [-94.490684, 33.625586],
          [-94.487611, 33.628544],
          [-94.485875, 33.637867],
          [-94.478366, 33.620847],
          [-94.470963, 33.60594],
          [-94.464336, 33.598819],
          [-94.409329, 33.568265],
          [-94.355945, 33.54318],
          [-94.257801, 33.582508],
          [-94.252656, 33.586144],
          [-94.181805, 33.593217],
          [-94.085243, 33.575546],
          [-94.066846, 33.568909],
          [-94.04345, 33.552253],
          [-94.043428, 33.551425],
          [-94.043375, 33.542315],
          [-94.043009, 33.493039],
          [-94.043279, 33.49103],
          [-94.043188, 33.470324],
          [-94.042988, 33.435824],
          [-94.042988, 33.431024],
          [-94.042887, 33.420225],
          [-94.043053, 33.377716],
          [-94.042869, 33.37117],
          [-94.043128, 33.358757],
          [-94.043067, 33.352097],
          [-94.043067, 33.347351],
          [-94.043067, 33.330498],
          [-94.04299, 33.271243],
          [-94.056144, 33.298036],
          [-94.218517, 33.296936],
          [-94.231333, 33.241073],
          [-94.379409, 33.255651],
          [-94.415739, 33.284017],
          [-94.517868, 33.282096],
          [-94.572463, 33.249826],
          [-94.65226, 33.268861],
          [-94.746925, 33.328938],
          [-94.746096, 33.703016]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48043",
      state: "48",
      county: "043",
      name: "Brewster County",
      censusArea: 6183.733,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.792705, 29.262931],
          [-103.798159, 29.999606],
          [-103.80068, 30.412527],
          [-103.439976, 30.665939],
          [-103.195667, 30.495574],
          [-102.567049, 30.052808],
          [-102.320819, 29.878933],
          [-102.320819, 29.875193],
          [-102.323389, 29.875193],
          [-102.364647, 29.843624],
          [-102.386677, 29.76148],
          [-102.404422, 29.765105],
          [-102.434063, 29.7767],
          [-102.486819, 29.786725],
          [-102.500741, 29.781446],
          [-102.554915, 29.748966],
          [-102.612879, 29.748182],
          [-102.665194, 29.737421],
          [-102.689738, 29.722349],
          [-102.698347, 29.695591],
          [-102.745494, 29.592967],
          [-102.781807, 29.542454],
          [-102.808692, 29.522319],
          [-102.831938, 29.437943],
          [-102.83332, 29.408981],
          [-102.875813, 29.353715],
          [-102.895886, 29.284778],
          [-102.886308, 29.24568],
          [-102.881135, 29.246022],
          [-102.871347, 29.241625],
          [-102.866846, 29.225015],
          [-102.87802, 29.214698],
          [-102.95089, 29.176835],
          [-102.995688, 29.161219],
          [-103.10443, 29.03359],
          [-103.115328, 28.98527],
          [-103.156646, 28.972831],
          [-103.163865, 28.972099],
          [-103.281903, 28.990089],
          [-103.383508, 29.024112],
          [-103.471037, 29.075368],
          [-103.470518, 29.085108],
          [-103.503236, 29.11911],
          [-103.525471, 29.137744],
          [-103.558679, 29.154962],
          [-103.61007, 29.165727],
          [-103.627067, 29.163336],
          [-103.714867, 29.184137],
          [-103.767814, 29.224954],
          [-103.792005, 29.262695],
          [-103.792705, 29.262931]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29183",
      state: "29",
      county: "183",
      name: "St. Charles County",
      censusArea: 560.435,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.571822, 38.681052],
          [-90.660509, 38.688551],
          [-90.732328, 38.639302],
          [-90.916554, 38.534725],
          [-90.964461, 38.547545],
          [-90.9622, 38.7742],
          [-90.958536, 38.870865],
          [-90.817827, 38.875966],
          [-90.77821, 38.922488],
          [-90.66587, 38.934195],
          [-90.663372, 38.928042],
          [-90.657254, 38.92027],
          [-90.625122, 38.888654],
          [-90.583388, 38.86903],
          [-90.566557, 38.868847],
          [-90.555693, 38.870785],
          [-90.54403, 38.87505],
          [-90.507451, 38.902767],
          [-90.486974, 38.925982],
          [-90.482725, 38.934712],
          [-90.482419, 38.94446],
          [-90.472122, 38.958838],
          [-90.467784, 38.961809],
          [-90.450809, 38.967758],
          [-90.450792, 38.967764],
          [-90.406367, 38.962554],
          [-90.385751, 38.956848],
          [-90.275932, 38.926453],
          [-90.250248, 38.919344],
          [-90.22585, 38.908923],
          [-90.207035, 38.898954],
          [-90.113327, 38.849306],
          [-90.109407, 38.843548],
          [-90.109107, 38.837448],
          [-90.114707, 38.815048],
          [-90.117707, 38.805748],
          [-90.298743, 38.887759],
          [-90.482299, 38.764348],
          [-90.49579, 38.748256],
          [-90.528024, 38.733702],
          [-90.53134, 38.731178],
          [-90.533638, 38.726871],
          [-90.530378, 38.708195],
          [-90.530363, 38.69863],
          [-90.533785, 38.691915],
          [-90.541612, 38.685267],
          [-90.554722, 38.681552],
          [-90.571822, 38.681052]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29189",
      state: "29",
      county: "189",
      name: "St. Louis County",
      censusArea: 507.8,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.340297, 38.386998],
          [-90.376841, 38.452861],
          [-90.405262, 38.501153],
          [-90.591123, 38.501652],
          [-90.736319, 38.466645],
          [-90.732328, 38.639302],
          [-90.660509, 38.688551],
          [-90.571822, 38.681052],
          [-90.554722, 38.681552],
          [-90.541612, 38.685267],
          [-90.533785, 38.691915],
          [-90.530363, 38.69863],
          [-90.530378, 38.708195],
          [-90.533638, 38.726871],
          [-90.53134, 38.731178],
          [-90.528024, 38.733702],
          [-90.49579, 38.748256],
          [-90.482299, 38.764348],
          [-90.298743, 38.887759],
          [-90.117707, 38.805748],
          [-90.123107, 38.798048],
          [-90.165082, 38.770618],
          [-90.251709, 38.718948],
          [-90.301911, 38.655652],
          [-90.3036, 38.645003],
          [-90.304812, 38.642444],
          [-90.306433, 38.63359],
          [-90.257773, 38.532008],
          [-90.260314, 38.528352],
          [-90.263064, 38.52022],
          [-90.279215, 38.472453],
          [-90.285215, 38.443453],
          [-90.295316, 38.426753],
          [-90.340297, 38.386998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29199",
      state: "29",
      county: "199",
      name: "Scotland County",
      censusArea: 436.671,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.949747, 40.300832],
          [-92.347801, 40.302756],
          [-92.349897, 40.302733],
          [-92.349975, 40.346505],
          [-92.350807, 40.597273],
          [-92.350776, 40.597274],
          [-92.331445, 40.597714],
          [-92.331205, 40.597805],
          [-92.298754, 40.598469],
          [-92.236484, 40.599531],
          [-92.217603, 40.599832],
          [-92.201669, 40.59998],
          [-92.196162, 40.600069],
          [-92.17978, 40.600529],
          [-92.096387, 40.60183],
          [-92.092875, 40.602082],
          [-92.0832, 40.602244],
          [-92.082339, 40.602176],
          [-92.069521, 40.602772],
          [-92.067904, 40.602648],
          [-92.029649, 40.603713],
          [-91.998683, 40.604433],
          [-91.970988, 40.605112],
          [-91.943114, 40.605913],
          [-91.949747, 40.300832]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24005",
      state: "24",
      county: "005",
      name: "Baltimore County",
      censusArea: 598.302,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.711894, 39.721103],
          [-76.569475, 39.721203],
          [-76.540573, 39.572236],
          [-76.536098, 39.561822],
          [-76.5324, 39.561556],
          [-76.531058, 39.555399],
          [-76.534063, 39.554618],
          [-76.531012, 39.54378],
          [-76.43698, 39.501964],
          [-76.377613, 39.424328],
          [-76.375991, 39.423786],
          [-76.374011, 39.422307],
          [-76.373519, 39.421432],
          [-76.370198, 39.413411],
          [-76.37223, 39.405576],
          [-76.361055, 39.400255],
          [-76.359269, 39.394046],
          [-76.358781, 39.390305],
          [-76.357753, 39.38979],
          [-76.32368, 39.374407],
          [-76.330027, 39.356139],
          [-76.341443, 39.354217],
          [-76.339942, 39.350169],
          [-76.335412, 39.337949],
          [-76.334401, 39.335222],
          [-76.334021, 39.334197],
          [-76.333924, 39.333935],
          [-76.338898, 39.325783],
          [-76.33957, 39.324681],
          [-76.33382, 39.319611],
          [-76.327579, 39.314108],
          [-76.337858, 39.305799],
          [-76.339817, 39.304216],
          [-76.341432, 39.30291],
          [-76.353205, 39.31065],
          [-76.354817, 39.311709],
          [-76.355495, 39.312155],
          [-76.36439, 39.31184],
          [-76.365532, 39.31095],
          [-76.380662, 39.299161],
          [-76.380734, 39.298765],
          [-76.383021, 39.286231],
          [-76.383345, 39.284455],
          [-76.383891, 39.281465],
          [-76.384901, 39.275928],
          [-76.385151, 39.275766],
          [-76.395136, 39.269293],
          [-76.395301, 39.269042],
          [-76.400094, 39.261753],
          [-76.400187, 39.261612],
          [-76.401103, 39.260219],
          [-76.402047, 39.258783],
          [-76.402355, 39.258315],
          [-76.401911, 39.258053],
          [-76.386937, 39.249216],
          [-76.38138, 39.249508],
          [-76.382358, 39.247292],
          [-76.38438, 39.242708],
          [-76.384699, 39.242242],
          [-76.38938, 39.235408],
          [-76.389563, 39.235261],
          [-76.393626, 39.232012],
          [-76.394381, 39.231408],
          [-76.398314, 39.22945],
          [-76.399122, 39.229048],
          [-76.41762, 39.219838],
          [-76.417681, 39.219808],
          [-76.41829, 39.218677],
          [-76.419201, 39.216988],
          [-76.425281, 39.205708],
          [-76.425413, 39.205629],
          [-76.441411, 39.196049],
          [-76.442482, 39.195408],
          [-76.447621, 39.197977],
          [-76.46156, 39.204947],
          [-76.462679, 39.205506],
          [-76.463483, 39.205908],
          [-76.471126, 39.20554],
          [-76.480083, 39.205108],
          [-76.482153, 39.204426],
          [-76.485527, 39.203314],
          [-76.486371, 39.203036],
          [-76.488883, 39.202208],
          [-76.489505, 39.202378],
          [-76.489777, 39.202453],
          [-76.49077, 39.202724],
          [-76.497977, 39.204697],
          [-76.498384, 39.204808],
          [-76.500086, 39.207798],
          [-76.500834, 39.209113],
          [-76.500984, 39.209376],
          [-76.519804, 39.222946],
          [-76.520584, 39.223508],
          [-76.520941, 39.22324],
          [-76.528587, 39.217492],
          [-76.529486, 39.218408],
          [-76.529623, 39.272307],
          [-76.529781, 39.273101],
          [-76.529787, 39.281872],
          [-76.529934, 39.283082],
          [-76.529663, 39.284203],
          [-76.529592, 39.284852],
          [-76.529681, 39.288152],
          [-76.529552, 39.289337],
          [-76.529499, 39.290168],
          [-76.529689, 39.290993],
          [-76.529708, 39.291895],
          [-76.529793, 39.292783],
          [-76.52951, 39.29718],
          [-76.529624, 39.298179],
          [-76.529775, 39.298731],
          [-76.529611, 39.302911],
          [-76.529685, 39.304661],
          [-76.529686, 39.305506],
          [-76.529743, 39.305964],
          [-76.529693, 39.306474],
          [-76.529697, 39.307717],
          [-76.529786, 39.308534],
          [-76.529786, 39.310391],
          [-76.529785, 39.356552],
          [-76.529793, 39.372058],
          [-76.609778, 39.372202],
          [-76.711313, 39.371933],
          [-76.711191, 39.362771],
          [-76.711342, 39.361225],
          [-76.711186, 39.354383],
          [-76.711054, 39.277889],
          [-76.688667, 39.268195],
          [-76.686048, 39.266885],
          [-76.685153, 39.266697],
          [-76.681644, 39.265032],
          [-76.678801, 39.263997],
          [-76.673432, 39.261281],
          [-76.667349, 39.258858],
          [-76.665567, 39.257982],
          [-76.653393, 39.252703],
          [-76.651491, 39.251983],
          [-76.647256, 39.25006],
          [-76.644212, 39.248924],
          [-76.639428, 39.246568],
          [-76.637267, 39.245648],
          [-76.632589, 39.243406],
          [-76.631132, 39.242899],
          [-76.618612, 39.237398],
          [-76.630674, 39.224187],
          [-76.639404, 39.224029],
          [-76.647219, 39.229036],
          [-76.66173, 39.231135],
          [-76.668744, 39.228989],
          [-76.680177, 39.220163],
          [-76.687745, 39.221292],
          [-76.69675, 39.217399],
          [-76.697085, 39.214092],
          [-76.705184, 39.215548],
          [-76.723811, 39.228181],
          [-76.739528, 39.232163],
          [-76.76383, 39.251312],
          [-76.882274, 39.350185],
          [-76.879738, 39.356846],
          [-76.873378, 39.35715],
          [-76.880701, 39.360469],
          [-76.879788, 39.365858],
          [-76.868387, 39.494074],
          [-76.8667, 39.500902],
          [-76.787096, 39.720802],
          [-76.715594, 39.721103],
          [-76.711894, 39.721103]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24025",
      state: "24",
      county: "025",
      name: "Harford County",
      censusArea: 437.089,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.569475, 39.721203],
          [-76.569389, 39.721203],
          [-76.517087, 39.721304],
          [-76.491887, 39.721304],
          [-76.418784, 39.721204],
          [-76.418684, 39.721304],
          [-76.395583, 39.721204],
          [-76.380583, 39.721304],
          [-76.380083, 39.721304],
          [-76.239805, 39.721305],
          [-76.233277, 39.721305],
          [-76.233259, 39.721305],
          [-76.144081, 39.617709],
          [-76.135674, 39.613108],
          [-76.106161, 39.58061],
          [-76.085972, 39.559011],
          [-76.075714, 39.543032],
          [-76.096072, 39.536912],
          [-76.102955, 39.523639],
          [-76.105571, 39.518595],
          [-76.105678, 39.518388],
          [-76.106215, 39.517353],
          [-76.107126, 39.515597],
          [-76.111583, 39.507001],
          [-76.116831, 39.496882],
          [-76.117253, 39.496068],
          [-76.11461, 39.488619],
          [-76.113929, 39.486701],
          [-76.108813, 39.482333],
          [-76.104665, 39.478792],
          [-76.100218, 39.476918],
          [-76.099506, 39.476618],
          [-76.098315, 39.476116],
          [-76.083286, 39.47786],
          [-76.083082, 39.477809],
          [-76.08159, 39.477438],
          [-76.073119, 39.475331],
          [-76.072309, 39.47513],
          [-76.071975, 39.475047],
          [-76.071836, 39.47476],
          [-76.069814, 39.470577],
          [-76.063874, 39.458295],
          [-76.062259, 39.454955],
          [-76.060931, 39.452208],
          [-76.060944, 39.451174],
          [-76.060988, 39.447775],
          [-76.060989, 39.447722],
          [-76.081176, 39.436712],
          [-76.081409, 39.436891],
          [-76.083269, 39.438321],
          [-76.083405, 39.438302],
          [-76.093595, 39.436871],
          [-76.102232, 39.435659],
          [-76.112761, 39.429852],
          [-76.115096, 39.428565],
          [-76.11682, 39.427614],
          [-76.117172, 39.42717],
          [-76.120991, 39.422358],
          [-76.121754, 39.421396],
          [-76.121889, 39.421226],
          [-76.132114, 39.414579],
          [-76.142224, 39.408007],
          [-76.146373, 39.40531],
          [-76.147048, 39.405364],
          [-76.150249, 39.405623],
          [-76.150516, 39.405644],
          [-76.150934, 39.405678],
          [-76.151948, 39.40576],
          [-76.152059, 39.405769],
          [-76.152357, 39.405793],
          [-76.157108, 39.406176],
          [-76.158592, 39.406295],
          [-76.158774, 39.40631],
          [-76.159358, 39.405661],
          [-76.171134, 39.392588],
          [-76.171474, 39.39221],
          [-76.171747, 39.391747],
          [-76.175121, 39.386019],
          [-76.175597, 39.38521],
          [-76.175928, 39.384648],
          [-76.180057, 39.377638],
          [-76.180074, 39.377609],
          [-76.199356, 39.366221],
          [-76.202346, 39.364455],
          [-76.226976, 39.349908],
          [-76.227625, 39.350109],
          [-76.229614, 39.350723],
          [-76.233776, 39.352008],
          [-76.234743, 39.353498],
          [-76.234765, 39.353532],
          [-76.235795, 39.355119],
          [-76.23652, 39.356236],
          [-76.237943, 39.358429],
          [-76.239877, 39.361408],
          [-76.241268, 39.361567],
          [-76.243377, 39.361808],
          [-76.244443, 39.361731],
          [-76.244988, 39.361691],
          [-76.245273, 39.361671],
          [-76.250107, 39.36132],
          [-76.250483, 39.361136],
          [-76.266365, 39.353352],
          [-76.265277, 39.350008],
          [-76.263019, 39.348634],
          [-76.258377, 39.345808],
          [-76.258342, 39.345737],
          [-76.253928, 39.336768],
          [-76.262008, 39.334708],
          [-76.262471, 39.33459],
          [-76.263073, 39.334436],
          [-76.263577, 39.334308],
          [-76.263624, 39.334265],
          [-76.265615, 39.33245],
          [-76.265777, 39.332302],
          [-76.266094, 39.332013],
          [-76.266257, 39.331864],
          [-76.266407, 39.331727],
          [-76.272671, 39.326015],
          [-76.276078, 39.322908],
          [-76.277813, 39.318738],
          [-76.277866, 39.31861],
          [-76.278239, 39.317712],
          [-76.278351, 39.317444],
          [-76.278499, 39.317088],
          [-76.280599, 39.312037],
          [-76.280778, 39.311608],
          [-76.281088, 39.30793],
          [-76.281374, 39.304531],
          [-76.281429, 39.303872],
          [-76.281556, 39.302369],
          [-76.281578, 39.302108],
          [-76.2817, 39.30211],
          [-76.296546, 39.302383],
          [-76.297878, 39.302408],
          [-76.291078, 39.318108],
          [-76.298778, 39.329208],
          [-76.298778, 39.333958],
          [-76.298778, 39.339278],
          [-76.298778, 39.340208],
          [-76.297569, 39.342244],
          [-76.295558, 39.345631],
          [-76.294978, 39.346608],
          [-76.295678, 39.350008],
          [-76.30177, 39.352216],
          [-76.310618, 39.355423],
          [-76.310829, 39.3555],
          [-76.31136, 39.355693],
          [-76.311679, 39.355808],
          [-76.31261, 39.355917],
          [-76.312864, 39.355946],
          [-76.314356, 39.35612],
          [-76.322687, 39.357092],
          [-76.323679, 39.357208],
          [-76.330027, 39.356139],
          [-76.32368, 39.374407],
          [-76.357753, 39.38979],
          [-76.358781, 39.390305],
          [-76.359269, 39.394046],
          [-76.361055, 39.400255],
          [-76.37223, 39.405576],
          [-76.370198, 39.413411],
          [-76.373519, 39.421432],
          [-76.374011, 39.422307],
          [-76.375991, 39.423786],
          [-76.377613, 39.424328],
          [-76.43698, 39.501964],
          [-76.531012, 39.54378],
          [-76.534063, 39.554618],
          [-76.531058, 39.555399],
          [-76.5324, 39.561556],
          [-76.536098, 39.561822],
          [-76.540573, 39.572236],
          [-76.569475, 39.721203]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26013",
      state: "26",
      county: "013",
      name: "Baraga County",
      censusArea: 898.257,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.232969, 46.933955],
          [-88.194361, 46.948578],
          [-88.176758, 46.955245],
          [-88.167373, 46.9588],
          [-88.167227, 46.958855],
          [-88.164704, 46.960178],
          [-88.164059, 46.960516],
          [-88.162856, 46.961146],
          [-88.162833, 46.961158],
          [-88.162437, 46.961366],
          [-88.160571, 46.962345],
          [-88.160086, 46.962599],
          [-88.156273, 46.964598],
          [-88.15543, 46.96504],
          [-88.155374, 46.965069],
          [-88.145561, 46.966409],
          [-88.143688, 46.966665],
          [-88.143614, 46.966635],
          [-88.142893, 46.966337],
          [-88.142807, 46.966302],
          [-88.142528, 46.966186],
          [-88.142339, 46.966108],
          [-88.142057, 46.965992],
          [-88.14175, 46.965865],
          [-88.132957, 46.962237],
          [-88.132876, 46.962204],
          [-88.13521, 46.959689],
          [-88.150114, 46.94363],
          [-88.156359, 46.939518],
          [-88.167989, 46.931861],
          [-88.170905, 46.92994],
          [-88.171317, 46.929669],
          [-88.175568, 46.92687],
          [-88.176156, 46.926483],
          [-88.177921, 46.92532],
          [-88.185964, 46.920025],
          [-88.187522, 46.918999],
          [-88.18295, 46.91365],
          [-88.181908, 46.912431],
          [-88.175197, 46.90458],
          [-88.161913, 46.904941],
          [-88.160704, 46.90511],
          [-88.130288, 46.909369],
          [-88.126927, 46.90984],
          [-88.126378, 46.909998],
          [-88.124935, 46.910413],
          [-88.124573, 46.910517],
          [-88.122862, 46.911009],
          [-88.106377, 46.915751],
          [-88.105195, 46.916091],
          [-88.102816, 46.916775],
          [-88.102362, 46.916906],
          [-88.101457, 46.917166],
          [-88.101315, 46.917207],
          [-88.100133, 46.917405],
          [-88.099227, 46.917556],
          [-88.0989, 46.917611],
          [-88.098858, 46.917618],
          [-88.097609, 46.917827],
          [-88.083937, 46.920112],
          [-88.08377, 46.92014],
          [-88.082263, 46.920392],
          [-88.08187, 46.920458],
          [-88.080087, 46.920255],
          [-88.074736, 46.919647],
          [-88.065192, 46.918563],
          [-88.063614, 46.918097],
          [-88.044551, 46.912473],
          [-88.044709, 46.765353],
          [-87.991546, 46.679113],
          [-88.115843, 46.678381],
          [-88.116571, 46.419951],
          [-88.679392, 46.420139],
          [-88.678983, 46.851822],
          [-88.551943, 46.851795],
          [-88.552185, 46.938533],
          [-88.4512, 46.938976],
          [-88.450823, 46.939038],
          [-88.452129, 46.934557],
          [-88.452354, 46.933787],
          [-88.454333, 46.926996],
          [-88.454353, 46.926928],
          [-88.454781, 46.925458],
          [-88.455117, 46.924305],
          [-88.455404, 46.923321],
          [-88.457776, 46.918999],
          [-88.474217, 46.889034],
          [-88.475859, 46.886042],
          [-88.476098, 46.881957],
          [-88.476918, 46.867946],
          [-88.47762, 46.855937],
          [-88.477681, 46.854902],
          [-88.477845, 46.852091],
          [-88.477935, 46.85056],
          [-88.483748, 46.831727],
          [-88.482579, 46.826197],
          [-88.473342, 46.806226],
          [-88.462349, 46.786711],
          [-88.438427, 46.786714],
          [-88.433835, 46.793502],
          [-88.415225, 46.811715],
          [-88.38141, 46.838466],
          [-88.382204, 46.844477],
          [-88.381947, 46.84611],
          [-88.381727, 46.847868],
          [-88.380849, 46.850064],
          [-88.38019, 46.852481],
          [-88.378432, 46.854897],
          [-88.375577, 46.857313],
          [-88.372501, 46.858192],
          [-88.368767, 46.857313],
          [-88.362502, 46.856432],
          [-88.361936, 46.856352],
          [-88.360868, 46.856202],
          [-88.36026, 46.856258],
          [-88.352145, 46.857009],
          [-88.35194, 46.857028],
          [-88.35173, 46.857193],
          [-88.34743, 46.860571],
          [-88.334647, 46.870613],
          [-88.333168, 46.871775],
          [-88.333092, 46.871835],
          [-88.310714, 46.889415],
          [-88.31029, 46.889748],
          [-88.308786, 46.890622],
          [-88.307978, 46.891092],
          [-88.307261, 46.891509],
          [-88.305045, 46.892797],
          [-88.299515, 46.896012],
          [-88.296476, 46.897778],
          [-88.289298, 46.90195],
          [-88.288767, 46.902259],
          [-88.286473, 46.903592],
          [-88.282087, 46.906142],
          [-88.282066, 46.906154],
          [-88.281244, 46.906632],
          [-88.276915, 46.908589],
          [-88.27473, 46.909577],
          [-88.273929, 46.909939],
          [-88.272478, 46.910595],
          [-88.261593, 46.915516],
          [-88.258072, 46.918409],
          [-88.250141, 46.924926],
          [-88.246953, 46.927545],
          [-88.246872, 46.927611],
          [-88.244437, 46.929612],
          [-88.243175, 46.93009],
          [-88.241858, 46.930589],
          [-88.24134, 46.930785],
          [-88.240926, 46.930942],
          [-88.240846, 46.930972],
          [-88.238324, 46.931927],
          [-88.2358, 46.932883],
          [-88.235702, 46.93292],
          [-88.233334, 46.933817],
          [-88.232969, 46.933955]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26029",
      state: "26",
      county: "029",
      name: "Charlevoix County",
      censusArea: 416.339,
      stateName: "Michigan"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-85.566441, 45.760222],
            [-85.54956, 45.757266],
            [-85.54375, 45.751413],
            [-85.53562, 45.750394],
            [-85.525237, 45.750462],
            [-85.506133, 45.754715],
            [-85.501267, 45.754415],
            [-85.497656, 45.746246],
            [-85.503758, 45.742771],
            [-85.508818, 45.742358],
            [-85.510091, 45.742888],
            [-85.508522, 45.744991],
            [-85.50904, 45.748488],
            [-85.515145, 45.749451],
            [-85.520569, 45.744745],
            [-85.521911, 45.739419],
            [-85.520803, 45.737247],
            [-85.510895, 45.734414],
            [-85.498777, 45.726291],
            [-85.494154, 45.705378],
            [-85.494016, 45.698476],
            [-85.5028, 45.690998],
            [-85.506104, 45.681148],
            [-85.503767, 45.670472],
            [-85.500451, 45.664298],
            [-85.490252, 45.652122],
            [-85.487026, 45.621211],
            [-85.491347, 45.609665],
            [-85.509276, 45.596475],
            [-85.518038, 45.592912],
            [-85.526895, 45.59159],
            [-85.530273, 45.589253],
            [-85.534064, 45.578198],
            [-85.541129, 45.575045],
            [-85.561634, 45.572213],
            [-85.618049, 45.582647],
            [-85.622741, 45.586028],
            [-85.630016, 45.598166],
            [-85.61985, 45.624547],
            [-85.608653, 45.632008],
            [-85.604521, 45.639256],
            [-85.604951, 45.647599],
            [-85.609295, 45.658067],
            [-85.604881, 45.681932],
            [-85.600842, 45.68886],
            [-85.590769, 45.698051],
            [-85.583724, 45.700796],
            [-85.572309, 45.711449],
            [-85.565132, 45.730719],
            [-85.564774, 45.745462],
            [-85.567128, 45.750419],
            [-85.567781, 45.757655],
            [-85.566441, 45.760222]
          ]
        ],
        [
          [
            [-84.734303, 45.201936],
            [-84.734799, 45.114903],
            [-84.857452, 45.116707],
            [-85.223679, 45.117751],
            [-85.222705, 45.205551],
            [-85.387463, 45.207565],
            [-85.388593, 45.23524],
            [-85.371593, 45.270834],
            [-85.355478, 45.282774],
            [-85.335016, 45.294027],
            [-85.323941, 45.303355],
            [-85.307646, 45.31314],
            [-85.294848, 45.316408],
            [-85.289568, 45.314052],
            [-85.273789, 45.315443],
            [-85.262996, 45.319507],
            [-85.25505, 45.325675],
            [-85.252193, 45.330863],
            [-85.235629, 45.339374],
            [-85.209673, 45.356937],
            [-85.196704, 45.360641],
            [-85.182471, 45.360824],
            [-85.143651, 45.370369],
            [-85.095985, 45.367001],
            [-85.011769, 45.278711],
            [-84.732687, 45.289382],
            [-84.734412, 45.202966],
            [-84.734303, 45.201936]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30111",
      state: "30",
      county: "111",
      name: "Yellowstone County",
      censusArea: 2633.294,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.924129, 46.132211],
          [-108.78014, 46.132402],
          [-108.404936, 46.132675],
          [-108.40464, 46.236126],
          [-108.32073, 46.264061],
          [-108.029531, 46.263812],
          [-108.011552, 46.336639],
          [-107.928505, 46.39529],
          [-107.782089, 46.395286],
          [-107.781214, 46.496014],
          [-107.75518, 46.496126],
          [-107.755369, 46.482008],
          [-107.738547, 46.481715],
          [-107.61278, 46.30811],
          [-107.466381, 46.177535],
          [-107.511497, 46.043257],
          [-107.674261, 46.043627],
          [-107.674438, 45.985685],
          [-107.798047, 45.985681],
          [-107.881553, 45.89897],
          [-108.04741, 45.899316],
          [-108.069044, 45.783599],
          [-108.069703, 45.518246],
          [-108.193125, 45.518413],
          [-108.193191, 45.489396],
          [-108.316249, 45.489459],
          [-108.316374, 45.460509],
          [-108.644971, 45.464091],
          [-108.801661, 45.552067],
          [-108.842697, 45.611006],
          [-108.905232, 45.682437],
          [-108.901279, 45.959536],
          [-108.924129, 46.132211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31015",
      state: "31",
      county: "015",
      name: "Boyd County",
      censusArea: 539.94,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.254454, 42.99814],
          [-99.254297, 42.998138],
          [-99.234462, 42.998281],
          [-99.195199, 42.998107],
          [-99.161388, 42.998465],
          [-99.151143, 42.998344],
          [-99.139045, 42.998508],
          [-99.135961, 42.998301],
          [-99.08188, 42.998288],
          [-99.080011, 42.998357],
          [-99.0223, 42.998237],
          [-99.021909, 42.998365],
          [-99.00037, 42.998273],
          [-98.962081, 42.998286],
          [-98.919234, 42.998241],
          [-98.919136, 42.998242],
          [-98.903154, 42.998306],
          [-98.899944, 42.998122],
          [-98.823989, 42.99831],
          [-98.801304, 42.998241],
          [-98.764378, 42.998323],
          [-98.742394, 42.998343],
          [-98.665613, 42.998536],
          [-98.663712, 42.998444],
          [-98.568936, 42.998537],
          [-98.565072, 42.9984],
          [-98.49855, 42.99856],
          [-98.495747, 42.988032],
          [-98.490483, 42.977948],
          [-98.467356, 42.947556],
          [-98.447047, 42.935117],
          [-98.325864, 42.8865],
          [-98.309741, 42.881232],
          [-98.305149, 42.761207],
          [-98.58376, 42.779736],
          [-98.892846, 42.845792],
          [-98.954634, 42.894077],
          [-99.257039, 42.804296],
          [-99.254235, 42.996812],
          [-99.254454, 42.99814]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31021",
      state: "31",
      county: "021",
      name: "Burt County",
      censusArea: 491.581,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.444785, 41.683633],
          [-96.444217, 41.741775],
          [-96.555172, 41.742018],
          [-96.554866, 42.015875],
          [-96.309645, 42.015187],
          [-96.272877, 42.047238],
          [-96.251714, 42.040472],
          [-96.22173, 42.026205],
          [-96.129505, 41.971673],
          [-96.129186, 41.965136],
          [-96.136743, 41.920826],
          [-96.142265, 41.915379],
          [-96.159098, 41.910057],
          [-96.161988, 41.905553],
          [-96.161756, 41.90182],
          [-96.144483, 41.871854],
          [-96.139554, 41.86583],
          [-96.110907, 41.830818],
          [-96.086407, 41.81138],
          [-96.077646, 41.808804],
          [-96.071007, 41.804639],
          [-96.06577, 41.798174],
          [-96.064537, 41.793002],
          [-96.077088, 41.715403],
          [-96.096795, 41.698681],
          [-96.121726, 41.68274],
          [-96.125293, 41.682951],
          [-96.444785, 41.683633]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26121",
      state: "26",
      county: "121",
      name: "Muskegon County",
      censusArea: 499.246,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.462195, 43.472328],
          [-86.461278, 43.472326],
          [-86.039491, 43.467447],
          [-86.038953, 43.293519],
          [-85.790448, 43.293003],
          [-85.790662, 43.205167],
          [-85.908388, 43.20592],
          [-85.907435, 43.118911],
          [-86.271996, 43.118365],
          [-86.280756, 43.136015],
          [-86.299048, 43.166465],
          [-86.311336, 43.18692],
          [-86.311887, 43.187837],
          [-86.316259, 43.195114],
          [-86.329836, 43.2158],
          [-86.344643, 43.23836],
          [-86.348647, 43.244459],
          [-86.34891, 43.24486],
          [-86.349801, 43.246217],
          [-86.350027, 43.246562],
          [-86.352192, 43.24986],
          [-86.352314, 43.250047],
          [-86.354505, 43.253385],
          [-86.357996, 43.258703],
          [-86.382882, 43.29662],
          [-86.383821, 43.298051],
          [-86.386096, 43.301516],
          [-86.386374, 43.301941],
          [-86.388493, 43.305168],
          [-86.38941, 43.306565],
          [-86.392124, 43.310701],
          [-86.393385, 43.312622],
          [-86.39575, 43.316225],
          [-86.401684, 43.327135],
          [-86.40301, 43.329572],
          [-86.403541, 43.330548],
          [-86.404831, 43.33292],
          [-86.407832, 43.338436],
          [-86.411941, 43.347209],
          [-86.427585, 43.380607],
          [-86.434757, 43.395919],
          [-86.434842, 43.3961],
          [-86.435124, 43.396702],
          [-86.435271, 43.397083],
          [-86.437896, 43.40389],
          [-86.438028, 43.404231],
          [-86.438267, 43.404851],
          [-86.438875, 43.406427],
          [-86.448479, 43.431329],
          [-86.448743, 43.432013],
          [-86.45457, 43.449475],
          [-86.456083, 43.45401],
          [-86.456845, 43.456294],
          [-86.457561, 43.458441],
          [-86.462195, 43.472328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26127",
      state: "26",
      county: "127",
      name: "Oceana County",
      censusArea: 512.071,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.462195, 43.472328],
          [-86.468747, 43.491963],
          [-86.468754, 43.491979],
          [-86.468919, 43.492344],
          [-86.476956, 43.510184],
          [-86.477424, 43.511223],
          [-86.478716, 43.514091],
          [-86.479276, 43.515335],
          [-86.479552, 43.51575],
          [-86.481158, 43.518158],
          [-86.48165, 43.518896],
          [-86.48187, 43.519225],
          [-86.482255, 43.519803],
          [-86.483043, 43.520984],
          [-86.483083, 43.521045],
          [-86.505799, 43.555112],
          [-86.509205, 43.560221],
          [-86.509339, 43.560421],
          [-86.512317, 43.564887],
          [-86.514168, 43.567664],
          [-86.515108, 43.569074],
          [-86.515838, 43.570169],
          [-86.516295, 43.570854],
          [-86.516725, 43.571499],
          [-86.517493, 43.572651],
          [-86.518698, 43.574458],
          [-86.519912, 43.576278],
          [-86.520205, 43.576718],
          [-86.520248, 43.576796],
          [-86.529507, 43.593462],
          [-86.537923, 43.615965],
          [-86.538497, 43.617501],
          [-86.538876, 43.619957],
          [-86.538921, 43.620244],
          [-86.539103, 43.621424],
          [-86.539291, 43.622642],
          [-86.539303, 43.62272],
          [-86.540916, 43.633158],
          [-86.540906, 43.634047],
          [-86.540896, 43.634969],
          [-86.54081, 43.642516],
          [-86.540787, 43.644593],
          [-86.54064, 43.645499],
          [-86.538763, 43.657061],
          [-86.538482, 43.658795],
          [-86.538186, 43.659403],
          [-86.529686, 43.676849],
          [-86.529179, 43.677889],
          [-86.527667, 43.679552],
          [-86.526863, 43.680435],
          [-86.517867, 43.690326],
          [-86.516152, 43.692212],
          [-86.514371, 43.69417],
          [-86.511008, 43.697867],
          [-86.510319, 43.698625],
          [-86.50762, 43.701138],
          [-86.50669, 43.702004],
          [-86.504679, 43.703878],
          [-86.503192, 43.705263],
          [-86.502989, 43.705452],
          [-86.501899, 43.706467],
          [-86.496187, 43.711787],
          [-86.488381, 43.719056],
          [-86.486983, 43.720358],
          [-86.481854, 43.725135],
          [-86.480655, 43.726407],
          [-86.480628, 43.726436],
          [-86.480267, 43.726819],
          [-86.463436, 43.744687],
          [-86.461554, 43.746685],
          [-86.460444, 43.748366],
          [-86.445532, 43.770945],
          [-86.445123, 43.771564],
          [-86.444903, 43.772069],
          [-86.442786, 43.776934],
          [-86.437722, 43.788573],
          [-86.437391, 43.789334],
          [-86.43114, 43.815569],
          [-86.431043, 43.815975],
          [-86.431063, 43.819178],
          [-86.431064, 43.819406],
          [-86.416471, 43.819572],
          [-86.037884, 43.815611],
          [-86.039491, 43.467447],
          [-86.461278, 43.472326],
          [-86.462195, 43.472328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26139",
      state: "26",
      county: "139",
      name: "Ottawa County",
      censusArea: 563.467,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.790662, 43.205167],
          [-85.788503, 43.030877],
          [-85.785276, 42.986911],
          [-85.782498, 42.7682],
          [-86.208886, 42.76754],
          [-86.210863, 42.783832],
          [-86.211815, 42.833236],
          [-86.210737, 42.859128],
          [-86.214138, 42.883555],
          [-86.216209, 42.919007],
          [-86.226305, 42.988284],
          [-86.232707, 43.015762],
          [-86.244277, 43.049681],
          [-86.250069, 43.057489],
          [-86.250517, 43.066993],
          [-86.254646, 43.083409],
          [-86.271996, 43.118365],
          [-85.907435, 43.118911],
          [-85.908388, 43.20592],
          [-85.790662, 43.205167]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26141",
      state: "26",
      county: "141",
      name: "Presque Isle County",
      censusArea: 658.719,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.204218, 45.627116],
          [-84.196043, 45.621456],
          [-84.180514, 45.604639],
          [-84.157121, 45.585305],
          [-84.139462, 45.573714],
          [-84.128867, 45.562284],
          [-84.126532, 45.556616],
          [-84.126971, 45.542428],
          [-84.122309, 45.523788],
          [-84.116687, 45.51305],
          [-84.109238, 45.505171],
          [-84.095905, 45.497298],
          [-84.075792, 45.490537],
          [-84.056138, 45.489349],
          [-84.039958, 45.493733],
          [-84.036286, 45.496245],
          [-84.028813, 45.497225],
          [-84.009582, 45.495069],
          [-83.99835, 45.491158],
          [-83.978017, 45.494138],
          [-83.939261, 45.493189],
          [-83.909472, 45.485784],
          [-83.881813, 45.467907],
          [-83.85856, 45.446865],
          [-83.841543, 45.435287],
          [-83.806622, 45.419159],
          [-83.788777, 45.416415],
          [-83.773171, 45.417302],
          [-83.755569, 45.411034],
          [-83.737321, 45.410943],
          [-83.721815, 45.413304],
          [-83.697316, 45.396239],
          [-83.667934, 45.384675],
          [-83.64379, 45.37171],
          [-83.599273, 45.352561],
          [-83.570361, 45.347198],
          [-83.550268, 45.350832],
          [-83.546799, 45.352637],
          [-83.545729, 45.358397],
          [-83.538306, 45.358167],
          [-83.520258, 45.347239],
          [-83.514717, 45.34646],
          [-83.496704, 45.357536],
          [-83.488826, 45.355872],
          [-83.477794, 45.341891],
          [-83.477728, 45.341827],
          [-83.468099, 45.33245],
          [-83.445672, 45.310612],
          [-83.43304, 45.303688],
          [-83.42514, 45.296808],
          [-83.423178, 45.292506],
          [-83.422486, 45.290989],
          [-83.422389, 45.290775],
          [-83.422272, 45.290713],
          [-83.42051, 45.289787],
          [-83.401091, 45.279572],
          [-83.40088, 45.279528],
          [-83.400753, 45.279502],
          [-83.400261, 45.2794],
          [-83.398845, 45.279107],
          [-83.388274, 45.276916],
          [-83.385104, 45.274195],
          [-83.381743, 45.268983],
          [-83.388034, 45.254976],
          [-83.412569, 45.245807],
          [-83.41241, 45.238905],
          [-83.405914, 45.227157],
          [-83.387587, 45.207107],
          [-83.881218, 45.202062],
          [-84.246634, 45.198718],
          [-84.25202, 45.625998],
          [-84.204218, 45.627116]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26151",
      state: "26",
      county: "151",
      name: "Sanilac County",
      censusArea: 962.566,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.996257, 43.154099],
          [-83.00043, 43.284913],
          [-83.119027, 43.28368],
          [-83.120396, 43.327049],
          [-83.103826, 43.327365],
          [-83.117844, 43.675493],
          [-82.606233, 43.690437],
          [-82.60483, 43.678884],
          [-82.605783, 43.669489],
          [-82.6005, 43.602935],
          [-82.597911, 43.590016],
          [-82.593785, 43.581467],
          [-82.585654, 43.543969],
          [-82.565691, 43.502904],
          [-82.565505, 43.497063],
          [-82.55354, 43.464111],
          [-82.539517, 43.437539],
          [-82.538578, 43.431594],
          [-82.53993, 43.422378],
          [-82.535627, 43.368062],
          [-82.536794, 43.34851],
          [-82.530128, 43.333805],
          [-82.529416, 43.316243],
          [-82.532396, 43.30577],
          [-82.523086, 43.225361],
          [-82.519123, 43.212737],
          [-82.508881, 43.196748],
          [-82.503157, 43.168946],
          [-82.996257, 43.154099]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26153",
      state: "26",
      county: "153",
      name: "Schoolcraft County",
      censusArea: 1171.358,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.865402, 45.968257],
          [-85.882442, 45.96862],
          [-85.893196, 45.967253],
          [-85.9091, 45.959074],
          [-85.91237, 45.956487],
          [-85.912604, 45.956302],
          [-85.922737, 45.948287],
          [-85.926213, 45.938093],
          [-85.926017, 45.932104],
          [-85.917238, 45.927782],
          [-85.910264, 45.922112],
          [-85.913769, 45.919439],
          [-85.920581, 45.920994],
          [-85.954063, 45.936629],
          [-85.998868, 45.950968],
          [-86.050956, 45.962205],
          [-86.072067, 45.965313],
          [-86.094753, 45.966704],
          [-86.123567, 45.964748],
          [-86.145714, 45.957372],
          [-86.150173, 45.954494],
          [-86.159415, 45.953765],
          [-86.196618, 45.963185],
          [-86.208255, 45.962978],
          [-86.220546, 45.958883],
          [-86.22906, 45.94857],
          [-86.233613, 45.945802],
          [-86.248008, 45.944849],
          [-86.254768, 45.94864],
          [-86.278007, 45.942057],
          [-86.315981, 45.915247],
          [-86.324232, 45.90608],
          [-86.332625, 45.851813],
          [-86.349134, 45.83416],
          [-86.355062, 45.805355],
          [-86.351658, 45.798132],
          [-86.363808, 45.790057],
          [-86.369918, 45.789254],
          [-86.395809, 45.78974],
          [-86.401656, 45.795412],
          [-86.415971, 45.793793],
          [-86.424828, 45.789747],
          [-86.428423, 45.785587],
          [-86.428946, 45.782524],
          [-86.427183, 45.77905],
          [-86.428294, 45.77562],
          [-86.431921, 45.767756],
          [-86.435493, 45.764485],
          [-86.43553, 45.764452],
          [-86.439661, 45.760669],
          [-86.440329, 45.760508],
          [-86.442335, 45.760026],
          [-86.455534, 45.75685],
          [-86.459866, 45.758042],
          [-86.49025, 45.896329],
          [-86.490136, 46.158085],
          [-86.615601, 46.158691],
          [-86.615397, 46.333263],
          [-86.365261, 46.331929],
          [-86.364953, 46.505852],
          [-85.864932, 46.504758],
          [-85.865025, 46.244631],
          [-85.865402, 45.968257]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45019",
      state: "45",
      county: "019",
      name: "Charleston County",
      censusArea: 916.089,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.446699, 33.213458],
          [-79.438387, 33.215136],
          [-79.413588, 33.198159],
          [-79.404858, 33.184929],
          [-79.268762, 33.136043],
          [-79.290754, 33.110051],
          [-79.291591, 33.109773],
          [-79.329909, 33.089986],
          [-79.337169, 33.072302],
          [-79.335346, 33.065362],
          [-79.339313, 33.050336],
          [-79.359961, 33.006672],
          [-79.403712, 33.003903],
          [-79.416515, 33.006815],
          [-79.423447, 33.015085],
          [-79.461047, 33.007639],
          [-79.483499, 33.001265],
          [-79.488727, 33.015832],
          [-79.506923, 33.032813],
          [-79.522449, 33.03535],
          [-79.55756, 33.021269],
          [-79.580725, 33.006447],
          [-79.58659, 32.991334],
          [-79.60102, 32.979282],
          [-79.606615, 32.972248],
          [-79.617611, 32.952726],
          [-79.617715, 32.94487],
          [-79.612928, 32.934815],
          [-79.606194, 32.925953],
          [-79.585897, 32.926461],
          [-79.581687, 32.931341],
          [-79.574951, 32.934526],
          [-79.572614, 32.933885],
          [-79.569762, 32.926692],
          [-79.576006, 32.906235],
          [-79.631149, 32.888606],
          [-79.655426, 32.872705],
          [-79.695141, 32.850398],
          [-79.699482, 32.839997],
          [-79.702956, 32.835781],
          [-79.719879, 32.825796],
          [-79.716761, 32.813627],
          [-79.726389, 32.805996],
          [-79.811021, 32.77696],
          [-79.818237, 32.766352],
          [-79.84035, 32.756816],
          [-79.848527, 32.755248],
          [-79.866742, 32.757422],
          [-79.872232, 32.752128],
          [-79.873605, 32.745657],
          [-79.868352, 32.734849],
          [-79.870336, 32.727777],
          [-79.888028, 32.695177],
          [-79.884961, 32.684402],
          [-79.915682, 32.664915],
          [-79.968468, 32.639732],
          [-79.975248, 32.639537],
          [-79.986917, 32.626388],
          [-79.99175, 32.616389],
          [-79.999374, 32.611851],
          [-80.010505, 32.608852],
          [-80.037276, 32.610236],
          [-80.077039, 32.603319],
          [-80.121368, 32.590523],
          [-80.148406, 32.578479],
          [-80.167286, 32.559885],
          [-80.171764, 32.546118],
          [-80.188401, 32.553604],
          [-80.193676, 32.554213],
          [-80.20523, 32.555547],
          [-80.246361, 32.531114],
          [-80.249566, 32.529584],
          [-80.352823, 32.500187],
          [-80.416586, 32.546318],
          [-80.389955, 32.647906],
          [-80.446077, 32.727477],
          [-80.40134, 32.858466],
          [-80.148108, 32.818904],
          [-80.158808, 32.830114],
          [-80.16313, 32.84127],
          [-80.17446, 32.845651],
          [-80.079028, 32.927811],
          [-80.149246, 33.021601],
          [-80.055463, 32.999514],
          [-80.011774, 32.899881],
          [-79.948848, 32.90811],
          [-79.928305, 32.823983],
          [-79.855069, 32.87387],
          [-79.643301, 33.123453],
          [-79.518844, 33.148297],
          [-79.446699, 33.213458]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46071",
      state: "46",
      county: "071",
      name: "Jackson County",
      censusArea: 1863.914,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.228179, 43.389185],
          [-102.110819, 43.389902],
          [-102.139051, 43.476676],
          [-102.139158, 43.700948],
          [-102.018887, 43.708618],
          [-102.007336, 43.99453],
          [-101.064068, 43.994929],
          [-101.064326, 43.84109],
          [-101.228264, 43.788844],
          [-101.228179, 43.389185]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46081",
      state: "46",
      county: "081",
      name: "Lawrence County",
      censusArea: 800.035,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.054562, 44.141081],
          [-104.054487, 44.180381],
          [-104.055927, 44.51773],
          [-104.055892, 44.543341],
          [-104.055877, 44.571016],
          [-103.820639, 44.604777],
          [-103.567653, 44.604098],
          [-103.574396, 44.256077],
          [-103.453016, 44.256609],
          [-103.452453, 44.140772],
          [-104.054562, 44.141081]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46083",
      state: "46",
      county: "083",
      name: "Lincoln County",
      censusArea: 577.279,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.584124, 43.268101],
          [-96.572489, 43.249178],
          [-96.557126, 43.224192],
          [-96.485264, 43.224183],
          [-96.475571, 43.221054],
          [-96.436589, 43.120842],
          [-96.439335, 43.113916],
          [-96.455107, 43.083366],
          [-96.805682, 43.08367],
          [-96.924142, 43.083733],
          [-96.924841, 43.500007],
          [-96.598928, 43.500457],
          [-96.587151, 43.484697],
          [-96.58407, 43.468856],
          [-96.600039, 43.45708],
          [-96.60286, 43.450907],
          [-96.594254, 43.434153],
          [-96.557586, 43.406792],
          [-96.53746, 43.395246],
          [-96.531159, 43.39561],
          [-96.529152, 43.397735],
          [-96.524044, 43.394762],
          [-96.521697, 43.386897],
          [-96.521264, 43.374978],
          [-96.525564, 43.312467],
          [-96.530392, 43.300034],
          [-96.551929, 43.292974],
          [-96.56911, 43.295535],
          [-96.586317, 43.274319],
          [-96.584124, 43.268101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46089",
      state: "46",
      county: "089",
      name: "Mc Pherson County",
      censusArea: 1136.644,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.717466, 45.593428],
          [-99.718071, 45.940907],
          [-99.692975, 45.940949],
          [-99.671938, 45.941062],
          [-99.61116, 45.941098],
          [-99.58878, 45.941104],
          [-99.49314, 45.940383],
          [-99.490254, 45.940362],
          [-99.40126, 45.940367],
          [-99.385565, 45.940407],
          [-99.378486, 45.940403],
          [-99.34496, 45.940299],
          [-99.344774, 45.940299],
          [-99.317875, 45.940263],
          [-99.297272, 45.940165],
          [-99.283968, 45.940195],
          [-99.276266, 45.940188],
          [-99.257745, 45.94006],
          [-99.222269, 45.940071],
          [-99.221672, 45.940069],
          [-99.213644, 45.940116],
          [-99.212571, 45.940108],
          [-99.102372, 45.940158],
          [-99.092868, 45.940184],
          [-99.005754, 45.939944],
          [-99.005642, 45.939944],
          [-98.905477, 45.93952],
          [-98.904429, 45.93952],
          [-98.724375, 45.938685],
          [-98.725002, 45.591252],
          [-99.707705, 45.593391],
          [-99.717466, 45.593428]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46099",
      state: "46",
      county: "099",
      name: "Minnehaha County",
      censusArea: 807.147,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.129089, 43.847973],
          [-96.888664, 43.848385],
          [-96.508636, 43.847591],
          [-96.453264, 43.849501],
          [-96.453088, 43.805123],
          [-96.453281, 43.791435],
          [-96.45338, 43.689637],
          [-96.453408, 43.675008],
          [-96.453387, 43.609944],
          [-96.453356, 43.607544],
          [-96.453383, 43.588183],
          [-96.453352, 43.58704],
          [-96.453049, 43.500415],
          [-96.591213, 43.500514],
          [-96.598928, 43.500457],
          [-96.924841, 43.500007],
          [-97.129478, 43.499683],
          [-97.129089, 43.847973]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46105",
      state: "46",
      county: "105",
      name: "Perkins County",
      censusArea: 2870.478,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.957371, 45.039569],
          [-102.957281, 45.212851],
          [-102.94207, 45.945092],
          [-102.920482, 45.945038],
          [-102.880252, 45.945069],
          [-102.704871, 45.945072],
          [-102.674077, 45.945274],
          [-102.672474, 45.945244],
          [-102.666684, 45.945307],
          [-102.65162, 45.94545],
          [-102.642555, 45.945404],
          [-102.558579, 45.945129],
          [-102.550947, 45.945015],
          [-102.476024, 45.945183],
          [-102.467563, 45.945159],
          [-102.459586, 45.945081],
          [-102.446419, 45.945083],
          [-102.425397, 45.945041],
          [-102.425358, 45.94499],
          [-102.420173, 45.94507],
          [-102.410346, 45.945079],
          [-102.406176, 45.944997],
          [-102.398575, 45.944868],
          [-102.396359, 45.944916],
          [-102.392767, 45.944979],
          [-102.392696, 45.944951],
          [-102.354283, 45.944901],
          [-102.353384, 45.944984],
          [-102.32823, 45.944806],
          [-102.217867, 45.944711],
          [-102.176993, 45.944622],
          [-102.176698, 45.944622],
          [-102.159439, 45.944641],
          [-102.157965, 45.944641],
          [-102.156393, 45.944663],
          [-102.145356, 45.944659],
          [-102.135269, 45.944586],
          [-102.125429, 45.944652],
          [-102.124628, 45.944813],
          [-102.087555, 45.944598],
          [-102.085122, 45.944642],
          [-102.06093, 45.944622],
          [-102.000679, 45.944515],
          [-101.99979, 45.472414],
          [-101.999993, 45.037999],
          [-102.957371, 45.039569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46109",
      state: "46",
      county: "109",
      name: "Roberts County",
      censusArea: 1101.041,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.226244, 45.297647],
          [-97.227089, 45.558158],
          [-97.228304, 45.935141],
          [-97.228291, 45.935141],
          [-97.144987, 45.935278],
          [-97.118053, 45.935485],
          [-97.019596, 45.935382],
          [-96.998652, 45.9357],
          [-96.791505, 45.935857],
          [-96.701313, 45.935807],
          [-96.680646, 45.935716],
          [-96.659895, 45.93556],
          [-96.639066, 45.935318],
          [-96.618295, 45.935407],
          [-96.607142, 45.935301],
          [-96.597432, 45.935209],
          [-96.576897, 45.935259],
          [-96.56328, 45.935238],
          [-96.564518, 45.926256],
          [-96.564317, 45.921074],
          [-96.569069, 45.914785],
          [-96.569401, 45.913946],
          [-96.569265, 45.911471],
          [-96.568918, 45.911426],
          [-96.568315, 45.902902],
          [-96.568053, 45.898697],
          [-96.568281, 45.891203],
          [-96.568772, 45.888072],
          [-96.572984, 45.861602],
          [-96.576544, 45.839945],
          [-96.57974, 45.82582],
          [-96.583085, 45.820024],
          [-96.652226, 45.746809],
          [-96.672665, 45.732336],
          [-96.711157, 45.717561],
          [-96.745086, 45.701576],
          [-96.835769, 45.649648],
          [-96.840746, 45.645294],
          [-96.844211, 45.639583],
          [-96.857751, 45.605962],
          [-96.853646, 45.602307],
          [-96.849444, 45.598944],
          [-96.844334, 45.594375],
          [-96.843957, 45.594003],
          [-96.835451, 45.586129],
          [-96.784863, 45.5413],
          [-96.76528, 45.521414],
          [-96.745487, 45.488712],
          [-96.742509, 45.478723],
          [-96.732739, 45.458737],
          [-96.692541, 45.417338],
          [-96.680454, 45.410499],
          [-96.617726, 45.408092],
          [-96.521787, 45.375645],
          [-96.489065, 45.357071],
          [-96.47002, 45.326832],
          [-96.992946, 45.32688],
          [-97.007543, 45.296866],
          [-97.226244, 45.297647]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33001",
      state: "33",
      county: "001",
      name: "Belknap County",
      censusArea: 400.232,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.532369, 43.765028],
          [-71.460347, 43.709747],
          [-71.439621, 43.696002],
          [-71.344184, 43.629327],
          [-71.163626, 43.53937],
          [-71.192207, 43.512589],
          [-71.160134, 43.35586],
          [-71.235828, 43.284717],
          [-71.346983, 43.346573],
          [-71.467375, 43.409391],
          [-71.544678, 43.450709],
          [-71.546975, 43.452586],
          [-71.625673, 43.471047],
          [-71.629057, 43.478762],
          [-71.63187, 43.477911],
          [-71.638686, 43.492715],
          [-71.653388, 43.488791],
          [-71.653772, 43.491557],
          [-71.661875, 43.495793],
          [-71.66935, 43.497003],
          [-71.672712, 43.499613],
          [-71.675939, 43.500227],
          [-71.678605, 43.503013],
          [-71.680027, 43.506371],
          [-71.684002, 43.510545],
          [-71.688361, 43.513142],
          [-71.69072, 43.515708],
          [-71.690664, 43.518337],
          [-71.689314, 43.530295],
          [-71.706127, 43.538182],
          [-71.709851, 43.542018],
          [-71.710214, 43.54495],
          [-71.714098, 43.550991],
          [-71.720682, 43.552632],
          [-71.727979, 43.559507],
          [-71.72775, 43.563524],
          [-71.65091, 43.621228],
          [-71.648057, 43.686154],
          [-71.522193, 43.697182],
          [-71.532369, 43.765028]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33007",
      state: "33",
      county: "007",
      name: "Coos County",
      censusArea: 1794.685,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.029101, 44.610331],
          [-71.01349, 44.340882],
          [-71.010239, 44.284774],
          [-71.042493, 44.237803],
          [-71.252401, 44.244235],
          [-71.343617, 44.092234],
          [-71.343214, 44.122282],
          [-71.357508, 44.166003],
          [-71.384993, 44.179144],
          [-71.395246, 44.21491],
          [-71.406901, 44.21332],
          [-71.407246, 44.213258],
          [-71.415801, 44.212082],
          [-71.443312, 44.249291],
          [-71.575752, 44.254699],
          [-71.587113, 44.304786],
          [-71.696665, 44.332326],
          [-71.764537, 44.406403],
          [-71.761966, 44.407027],
          [-71.756091, 44.406401],
          [-71.745011, 44.401359],
          [-71.708934, 44.411611],
          [-71.67995, 44.427908],
          [-71.59948, 44.486455],
          [-71.580263, 44.506509],
          [-71.536251, 44.587939],
          [-71.553156, 44.626645],
          [-71.598656, 44.698005],
          [-71.625323, 44.729718],
          [-71.627358, 44.747544],
          [-71.623924, 44.755135],
          [-71.57252, 44.810074],
          [-71.495844, 44.90498],
          [-71.493985, 44.911312],
          [-71.516814, 44.947588],
          [-71.537784, 44.984298],
          [-71.538592, 44.988182],
          [-71.53698, 44.994177],
          [-71.530091, 44.999656],
          [-71.505, 45.008151],
          [-71.486457, 45.007011],
          [-71.468567, 45.010222],
          [-71.464555, 45.013637],
          [-71.502487, 45.013367],
          [-71.496098, 45.069235],
          [-71.489145, 45.072308],
          [-71.428828, 45.123881],
          [-71.39781, 45.203553],
          [-71.403267, 45.215348],
          [-71.44288, 45.234799],
          [-71.401182, 45.242544],
          [-71.342961, 45.271281],
          [-71.297092, 45.298985],
          [-71.284396, 45.302434],
          [-71.266754, 45.29123],
          [-71.263042, 45.277401],
          [-71.259614, 45.27324],
          [-71.232998, 45.256625],
          [-71.183785, 45.244932],
          [-71.13943, 45.242958],
          [-71.131953, 45.245423],
          [-71.124517, 45.25527],
          [-71.119914, 45.262287],
          [-71.120112, 45.265738],
          [-71.105151, 45.294635],
          [-71.097772, 45.301906],
          [-71.084334, 45.305293],
          [-71.07564, 45.224093],
          [-71.060101, 45.019661],
          [-71.037518, 44.755607],
          [-71.029101, 44.610331]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33011",
      state: "33",
      county: "011",
      name: "Hillsborough County",
      censusArea: 876.139,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.809658, 43.124485],
          [-71.733116, 43.139372],
          [-71.707407, 43.144267],
          [-71.707482, 43.144505],
          [-71.704749, 43.145008],
          [-71.700628, 43.146174],
          [-71.667826, 43.152588],
          [-71.641252, 43.054173],
          [-71.518521, 43.068914],
          [-71.39221, 43.008984],
          [-71.391868, 43.007857],
          [-71.377409, 42.947185],
          [-71.44181, 42.889586],
          [-71.360808, 42.798988],
          [-71.245504, 42.742589],
          [-71.254532, 42.734136],
          [-71.294205, 42.69699],
          [-71.54252, 42.702726],
          [-71.631814, 42.704788],
          [-71.636214, 42.704888],
          [-71.77251, 42.70831],
          [-71.80545, 42.709137],
          [-71.898714, 42.711478],
          [-71.928811, 42.712234],
          [-72.001034, 42.91951],
          [-72.062374, 42.944837],
          [-72.038149, 43.128807],
          [-72.023403, 43.160151],
          [-72.036791, 43.182553],
          [-72.008995, 43.187874],
          [-71.913255, 43.207263],
          [-71.856029, 43.115383],
          [-71.809658, 43.124485]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US33013",
      state: "33",
      county: "013",
      name: "Merrimack County",
      censusArea: 934.116,
      stateName: "New Hampshire"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.809658, 43.124485],
          [-71.856029, 43.115383],
          [-71.913255, 43.207263],
          [-72.008995, 43.187874],
          [-72.090028, 43.322567],
          [-72.053802, 43.441078],
          [-71.93522, 43.528449],
          [-71.892145, 43.57466],
          [-71.864053, 43.599442],
          [-71.81805, 43.544603],
          [-71.72775, 43.563524],
          [-71.727979, 43.559507],
          [-71.720682, 43.552632],
          [-71.714098, 43.550991],
          [-71.710214, 43.54495],
          [-71.709851, 43.542018],
          [-71.706127, 43.538182],
          [-71.689314, 43.530295],
          [-71.690664, 43.518337],
          [-71.69072, 43.515708],
          [-71.688361, 43.513142],
          [-71.684002, 43.510545],
          [-71.680027, 43.506371],
          [-71.678605, 43.503013],
          [-71.675939, 43.500227],
          [-71.672712, 43.499613],
          [-71.66935, 43.497003],
          [-71.661875, 43.495793],
          [-71.653772, 43.491557],
          [-71.653388, 43.488791],
          [-71.638686, 43.492715],
          [-71.63187, 43.477911],
          [-71.629057, 43.478762],
          [-71.625673, 43.471047],
          [-71.546975, 43.452586],
          [-71.544678, 43.450709],
          [-71.467375, 43.409391],
          [-71.346983, 43.346573],
          [-71.235828, 43.284717],
          [-71.247215, 43.274201],
          [-71.356182, 43.101971],
          [-71.39221, 43.008984],
          [-71.518521, 43.068914],
          [-71.641252, 43.054173],
          [-71.667826, 43.152588],
          [-71.700628, 43.146174],
          [-71.704749, 43.145008],
          [-71.707482, 43.144505],
          [-71.707407, 43.144267],
          [-71.733116, 43.139372],
          [-71.809658, 43.124485]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34001",
      state: "34",
      county: "001",
      name: "Atlantic County",
      censusArea: 555.704,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.877189, 39.608288],
          [-74.801051, 39.674102],
          [-74.736309, 39.729909],
          [-74.621959, 39.626633],
          [-74.479558, 39.55597],
          [-74.417393, 39.557255],
          [-74.312451, 39.499869],
          [-74.313689, 39.493874],
          [-74.308344, 39.483945],
          [-74.304778, 39.482945],
          [-74.302184, 39.478935],
          [-74.304343, 39.471445],
          [-74.334804, 39.432001],
          [-74.36699, 39.402017],
          [-74.406692, 39.377516],
          [-74.406792, 39.373916],
          [-74.408237, 39.365071],
          [-74.412692, 39.360816],
          [-74.459894, 39.345016],
          [-74.521797, 39.313816],
          [-74.541443, 39.300245],
          [-74.699745, 39.289593],
          [-74.861073, 39.323768],
          [-74.855894, 39.424562],
          [-74.985223, 39.514802],
          [-74.911566, 39.57862],
          [-74.877189, 39.608288]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34005",
      state: "34",
      county: "005",
      name: "Burlington County",
      censusArea: 798.576,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.900236, 40.077149],
          [-74.838008, 40.10091],
          [-74.740605, 40.13521],
          [-74.724304, 40.14701],
          [-74.724179, 40.147324],
          [-74.59477, 40.137875],
          [-74.593987, 40.137541],
          [-74.593354, 40.137855],
          [-74.59265, 40.137818],
          [-74.592468, 40.138494],
          [-74.591754, 40.137981],
          [-74.591221, 40.137913],
          [-74.589954, 40.138879],
          [-74.589638, 40.138741],
          [-74.589614, 40.13819],
          [-74.589318, 40.138051],
          [-74.587847, 40.138237],
          [-74.553105, 40.07913],
          [-74.528808, 40.036964],
          [-74.434653, 39.854803],
          [-74.389905, 39.773428],
          [-74.390399, 39.596037],
          [-74.417393, 39.557255],
          [-74.479558, 39.55597],
          [-74.621959, 39.626633],
          [-74.736309, 39.729909],
          [-74.758064, 39.743695],
          [-74.772894, 39.750929],
          [-74.791478, 39.772119],
          [-74.812471, 39.785269],
          [-74.845183, 39.79],
          [-74.876499, 39.781647],
          [-74.889062, 39.781804],
          [-74.902546, 39.790797],
          [-74.903267, 39.791247],
          [-74.926935, 39.873839],
          [-74.932174, 39.887532],
          [-74.951823, 39.90147],
          [-74.964146, 39.920683],
          [-74.983717, 39.92694],
          [-74.984549, 39.931238],
          [-74.974879, 39.938382],
          [-74.988925, 39.94105],
          [-74.994442, 39.939266],
          [-75.014966, 39.946546],
          [-75.01345, 39.954188],
          [-75.013114, 39.956433],
          [-75.017537, 39.959087],
          [-75.016653, 39.960623],
          [-75.017468, 39.963509],
          [-75.018723, 39.963664],
          [-75.019212, 39.967462],
          [-75.010146, 39.979768],
          [-75.035461, 39.995951],
          [-75.056039, 39.991795],
          [-75.014343, 40.020976],
          [-74.974713, 40.048711],
          [-74.97429, 40.048872],
          [-74.900236, 40.077149]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34011",
      state: "34",
      county: "011",
      name: "Cumberland County",
      censusArea: 483.703,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.407294, 39.381954],
          [-75.368213, 39.456479],
          [-75.239529, 39.559188],
          [-75.084187, 39.45796],
          [-75.061973, 39.568643],
          [-74.985223, 39.514802],
          [-74.855894, 39.424562],
          [-74.861073, 39.323768],
          [-74.914936, 39.177553],
          [-74.962382, 39.190238],
          [-74.976266, 39.192271],
          [-74.998002, 39.191253],
          [-75.026179, 39.193621],
          [-75.028885, 39.19456],
          [-75.027824, 39.199482],
          [-75.023586, 39.202594],
          [-75.023437, 39.204791],
          [-75.026376, 39.20985],
          [-75.035672, 39.215415],
          [-75.041663, 39.215511],
          [-75.047797, 39.211702],
          [-75.052326, 39.213609],
          [-75.062506, 39.213564],
          [-75.086395, 39.208159],
          [-75.101019, 39.211657],
          [-75.107286, 39.211403],
          [-75.114748, 39.207554],
          [-75.12707, 39.189766],
          [-75.136548, 39.179425],
          [-75.139136, 39.180021],
          [-75.165979, 39.201842],
          [-75.164798, 39.216606],
          [-75.170444, 39.234643],
          [-75.177506, 39.242746],
          [-75.205857, 39.262619],
          [-75.21251, 39.262755],
          [-75.241639, 39.274097],
          [-75.244056, 39.27769],
          [-75.242881, 39.280574],
          [-75.244357, 39.2857],
          [-75.251806, 39.299913],
          [-75.271629, 39.304041],
          [-75.28262, 39.299055],
          [-75.285333, 39.292212],
          [-75.288898, 39.289557],
          [-75.30601, 39.301712],
          [-75.315201, 39.310593],
          [-75.326754, 39.332473],
          [-75.327463, 39.33927],
          [-75.333743, 39.345335],
          [-75.341969, 39.348697],
          [-75.355558, 39.347823],
          [-75.365016, 39.341388],
          [-75.39003, 39.358259],
          [-75.394331, 39.363753],
          [-75.395181, 39.371398],
          [-75.399304, 39.37949],
          [-75.407294, 39.381954]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31031",
      state: "31",
      county: "031",
      name: "Cherry County",
      censusArea: 5960.422,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.082486, 42.999356],
          [-101.875424, 42.999298],
          [-101.625424, 42.996238],
          [-101.230325, 42.997899],
          [-101.229203, 42.997854],
          [-101.228104, 42.997874],
          [-101.226853, 42.997896],
          [-101.226494, 42.997901],
          [-100.96419, 42.997886],
          [-100.958365, 42.997796],
          [-100.906714, 42.99791],
          [-100.887898, 42.997881],
          [-100.553131, 42.998721],
          [-100.544018, 42.998795],
          [-100.534335, 42.999017],
          [-100.472742, 42.999288],
          [-100.355406, 42.99876],
          [-100.349548, 42.99874],
          [-100.283713, 42.998767],
          [-100.277793, 42.998674],
          [-100.198434, 42.998542],
          [-100.198413, 42.998542],
          [-100.198412, 42.998542],
          [-100.198478, 42.846102],
          [-100.18327, 42.781005],
          [-100.183167, 42.43343],
          [-100.167605, 42.085848],
          [-100.267622, 42.086147],
          [-100.846129, 42.088166],
          [-101.426557, 42.092194],
          [-102.006204, 42.096338],
          [-102.04028, 42.096744],
          [-102.038169, 42.443307],
          [-102.066689, 42.443463],
          [-102.082546, 42.999356],
          [-102.082486, 42.999356]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31043",
      state: "31",
      county: "043",
      name: "Dakota County",
      censusArea: 264.255,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.635982, 42.524324],
          [-96.625958, 42.513576],
          [-96.611489, 42.506088],
          [-96.48749, 42.479846],
          [-96.475565, 42.480036],
          [-96.443408, 42.489495],
          [-96.409408, 42.487595],
          [-96.396107, 42.484095],
          [-96.385407, 42.473094],
          [-96.381307, 42.461694],
          [-96.380707, 42.446394],
          [-96.387608, 42.432494],
          [-96.413609, 42.407894],
          [-96.415509, 42.400294],
          [-96.417786, 42.351449],
          [-96.413895, 42.343393],
          [-96.407998, 42.337408],
          [-96.384169, 42.325874],
          [-96.375307, 42.318339],
          [-96.348814, 42.282024],
          [-96.726661, 42.278005],
          [-96.722231, 42.524652],
          [-96.635982, 42.524324]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31049",
      state: "31",
      county: "049",
      name: "Deuel County",
      censusArea: 439.851,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.055535, 41.221547],
          [-102.051761, 41.00389],
          [-102.051718, 41.002377],
          [-102.070598, 41.002423],
          [-102.124972, 41.002338],
          [-102.19121, 41.002326],
          [-102.209361, 41.002442],
          [-102.2122, 41.002462],
          [-102.231931, 41.002327],
          [-102.267812, 41.002383],
          [-102.2721, 41.002245],
          [-102.291354, 41.002207],
          [-102.292553, 41.002207],
          [-102.292622, 41.00223],
          [-102.292833, 41.002207],
          [-102.364066, 41.002174],
          [-102.379593, 41.002301],
          [-102.469223, 41.002424],
          [-102.470537, 41.002382],
          [-102.487955, 41.002445],
          [-102.556789, 41.002219],
          [-102.566048, 41.0022],
          [-102.575496, 41.0022],
          [-102.575738, 41.002268],
          [-102.578696, 41.002291],
          [-102.621033, 41.002597],
          [-102.610413, 41.221614],
          [-102.055535, 41.221547]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31055",
      state: "31",
      county: "055",
      name: "Douglas County",
      censusArea: 328.455,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.923219, 41.191046],
          [-96.158333, 41.190762],
          [-96.327399, 41.189999],
          [-96.350598, 41.265192],
          [-96.47072, 41.393239],
          [-96.329012, 41.393136],
          [-95.9393, 41.393184],
          [-95.93831, 41.392162],
          [-95.956791, 41.349196],
          [-95.956691, 41.345496],
          [-95.953091, 41.339896],
          [-95.939291, 41.328897],
          [-95.92569, 41.322197],
          [-95.91379, 41.320197],
          [-95.90249, 41.273398],
          [-95.924891, 41.211198],
          [-95.927491, 41.202198],
          [-95.92599, 41.195698],
          [-95.923219, 41.191046]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31061",
      state: "31",
      county: "061",
      name: "Franklin County",
      censusArea: 575.816,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.726373, 40.002222],
          [-98.774941, 40.002336],
          [-98.777203, 40.002359],
          [-98.82059, 40.002319],
          [-98.834456, 40.002363],
          [-98.934792, 40.002205],
          [-98.960919, 40.002271],
          [-98.961009, 40.002317],
          [-98.971721, 40.002268],
          [-98.972287, 40.002245],
          [-98.992135, 40.002192],
          [-99.018701, 40.002333],
          [-99.020338, 40.002264],
          [-99.067022, 40.00217],
          [-99.085597, 40.002133],
          [-99.11351, 40.002193],
          [-99.123033, 40.002165],
          [-99.169816, 40.001925],
          [-99.178965, 40.001977],
          [-99.179133, 40.001977],
          [-99.179403, 40.350681],
          [-98.726827, 40.350398],
          [-98.726373, 40.002222]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31067",
      state: "31",
      county: "067",
      name: "Gage County",
      censusArea: 851.491,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.912637, 40.523625],
          [-96.463632, 40.523013],
          [-96.463667, 40.262173],
          [-96.463713, 40.000968],
          [-96.467536, 40.001035],
          [-96.469945, 40.000966],
          [-96.527111, 40.001031],
          [-96.538977, 40.000851],
          [-96.557863, 40.000968],
          [-96.570854, 40.001091],
          [-96.580852, 40.000966],
          [-96.604884, 40.000891],
          [-96.610349, 40.000881],
          [-96.622401, 40.001158],
          [-96.805768, 40.001371],
          [-96.873812, 40.00145],
          [-96.875057, 40.001448],
          [-96.878253, 40.001466],
          [-96.880459, 40.001448],
          [-96.916093, 40.001506],
          [-96.916407, 40.001506],
          [-96.915902, 40.348356],
          [-96.915083, 40.349734],
          [-96.913346, 40.351192],
          [-96.912637, 40.523625]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39035",
      state: "39",
      county: "035",
      name: "Cuyahoga County",
      censusArea: 457.191,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.878053, 41.275044],
          [-81.877066, 41.343557],
          [-81.971262, 41.351268],
          [-81.96813, 41.506422],
          [-81.964912, 41.505446],
          [-81.962664, 41.501341],
          [-81.958463, 41.498642],
          [-81.937862, 41.491443],
          [-81.930404, 41.490457],
          [-81.928652, 41.490226],
          [-81.87736, 41.483445],
          [-81.860262, 41.483841],
          [-81.850141, 41.486255],
          [-81.837092, 41.489367],
          [-81.836059, 41.489614],
          [-81.810992, 41.495592],
          [-81.810758, 41.495648],
          [-81.801207, 41.496223],
          [-81.800253, 41.496281],
          [-81.799746, 41.496311],
          [-81.794449, 41.49663],
          [-81.794157, 41.496648],
          [-81.782258, 41.49605],
          [-81.768898, 41.491663],
          [-81.768856, 41.491649],
          [-81.762489, 41.49046],
          [-81.75861, 41.489736],
          [-81.746095, 41.4874],
          [-81.745818, 41.487348],
          [-81.744755, 41.48715],
          [-81.744272, 41.487263],
          [-81.741852, 41.487827],
          [-81.738755, 41.48855],
          [-81.732674, 41.491437],
          [-81.727806, 41.493749],
          [-81.726429, 41.494402],
          [-81.725763, 41.494718],
          [-81.71931, 41.497782],
          [-81.716836, 41.498957],
          [-81.71559, 41.499549],
          [-81.710986, 41.501734],
          [-81.710953, 41.50175],
          [-81.707622, 41.505108],
          [-81.706864, 41.505872],
          [-81.69325, 41.514161],
          [-81.691248, 41.515379],
          [-81.688058, 41.517321],
          [-81.664884, 41.53143],
          [-81.664851, 41.53145],
          [-81.649872, 41.535775],
          [-81.648453, 41.536185],
          [-81.641055, 41.538321],
          [-81.633652, 41.540458],
          [-81.627431, 41.544163],
          [-81.61691, 41.550428],
          [-81.616567, 41.550633],
          [-81.615816, 41.551079],
          [-81.610163, 41.554446],
          [-81.60967, 41.55474],
          [-81.599747, 41.560649],
          [-81.593144, 41.565462],
          [-81.593092, 41.5655],
          [-81.591981, 41.56631],
          [-81.591471, 41.566682],
          [-81.579815, 41.575178],
          [-81.579746, 41.575228],
          [-81.579727, 41.575243],
          [-81.579285, 41.575565],
          [-81.578666, 41.576016],
          [-81.577459, 41.576896],
          [-81.575715, 41.578167],
          [-81.575577, 41.578267],
          [-81.562844, 41.587549],
          [-81.531674, 41.612972],
          [-81.531612, 41.613023],
          [-81.529955, 41.614374],
          [-81.529742, 41.614548],
          [-81.527026, 41.615373],
          [-81.520707, 41.617292],
          [-81.509359, 41.620739],
          [-81.50044, 41.623448],
          [-81.48864, 41.631348],
          [-81.488934, 41.627948],
          [-81.488062, 41.598556],
          [-81.488139, 41.577328],
          [-81.487842, 41.570051],
          [-81.419936, 41.56995],
          [-81.41763, 41.56967],
          [-81.408992, 41.56985],
          [-81.391005, 41.569716],
          [-81.391694, 41.348272],
          [-81.399585, 41.348573],
          [-81.450331, 41.349148],
          [-81.592653, 41.351119],
          [-81.598061, 41.350913],
          [-81.590542, 41.277554],
          [-81.68495, 41.277146],
          [-81.878053, 41.275044]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39051",
      state: "39",
      county: "051",
      name: "Fulton County",
      censusArea: 405.443,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.880387, 41.720086],
          [-83.882455, 41.603225],
          [-83.882943, 41.487543],
          [-84.341902, 41.485519],
          [-84.380735, 41.513942],
          [-84.399546, 41.705758],
          [-84.39943, 41.705761],
          [-84.396547, 41.705935],
          [-84.360419, 41.706925],
          [-83.880539, 41.720081],
          [-83.880387, 41.720086]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39053",
      state: "39",
      county: "053",
      name: "Gallia County",
      censusArea: 466.53,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.139279, 38.866407],
          [-82.184428, 38.816529],
          [-82.191172, 38.815137],
          [-82.215759, 38.797462],
          [-82.221566, 38.787187],
          [-82.220449, 38.773739],
          [-82.198882, 38.757725],
          [-82.188268, 38.734082],
          [-82.182467, 38.708782],
          [-82.172066, 38.625984],
          [-82.172066, 38.619284],
          [-82.175167, 38.608484],
          [-82.177267, 38.603784],
          [-82.181967, 38.599384],
          [-82.193824, 38.593096],
          [-82.218967, 38.591683],
          [-82.245969, 38.598483],
          [-82.26207, 38.598183],
          [-82.27427, 38.593683],
          [-82.287102, 38.582588],
          [-82.361674, 38.585182],
          [-82.354468, 38.67607],
          [-82.472478, 38.682279],
          [-82.483579, 38.772278],
          [-82.582683, 38.779077],
          [-82.575583, 38.844478],
          [-82.455179, 38.844577],
          [-82.435309, 39.035079],
          [-82.322874, 39.027674],
          [-82.095465, 39.002778],
          [-82.098762, 38.958288],
          [-82.139279, 38.866407]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29063",
      state: "29",
      county: "063",
      name: "De Kalb County",
      censusArea: 421.356,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.2188, 40.034885],
          [-94.203457, 40.034539],
          [-94.205844, 39.788954],
          [-94.205884, 39.767152],
          [-94.205432, 39.760821],
          [-94.205728, 39.756815],
          [-94.205961, 39.745841],
          [-94.602257, 39.747195],
          [-94.602819, 39.819608],
          [-94.605377, 40.039217],
          [-94.2188, 40.034885]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29069",
      state: "29",
      county: "069",
      name: "Dunklin County",
      censusArea: 541.069,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.148398, 36.630656],
          [-89.958786, 36.629331],
          [-89.96131, 36.388803],
          [-89.959375, 35.99902],
          [-89.959377, 35.99902],
          [-89.959893, 35.99902],
          [-89.961075, 35.999135],
          [-89.965327, 35.998813],
          [-89.972563, 35.998994],
          [-90.12635, 35.997596],
          [-90.127331, 35.997635],
          [-90.158812, 35.997375],
          [-90.2888, 35.996419],
          [-90.288947, 35.996418],
          [-90.292376, 35.996397],
          [-90.339434, 35.996033],
          [-90.342616, 35.995895],
          [-90.368718, 35.995812],
          [-90.36443, 36.013625],
          [-90.319168, 36.089976],
          [-90.220425, 36.184764],
          [-90.198735, 36.201382],
          [-90.114922, 36.265595],
          [-90.083731, 36.272332],
          [-90.076301, 36.280708],
          [-90.06398, 36.303038],
          [-90.064514, 36.382085],
          [-90.072897, 36.393007],
          [-90.133993, 36.437906],
          [-90.156369, 36.487748],
          [-90.154409, 36.496832],
          [-90.152481, 36.497952],
          [-90.193943, 36.497823],
          [-90.217323, 36.497797],
          [-90.220702, 36.497858],
          [-90.220732, 36.497858],
          [-90.148398, 36.630656]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29081",
      state: "29",
      county: "081",
      name: "Harrison County",
      censusArea: 722.504,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.774344, 40.577584],
          [-93.763324, 40.263988],
          [-93.764021, 40.132909],
          [-94.216696, 40.136626],
          [-94.214278, 40.384254],
          [-94.23224, 40.571907],
          [-94.091085, 40.572897],
          [-94.089194, 40.572806],
          [-94.080463, 40.572899],
          [-94.080223, 40.572899],
          [-94.034134, 40.573585],
          [-94.015492, 40.573914],
          [-93.976766, 40.574635],
          [-93.963863, 40.574754],
          [-93.939857, 40.575192],
          [-93.938627, 40.575284],
          [-93.937097, 40.575421],
          [-93.936317, 40.575284],
          [-93.935687, 40.57533],
          [-93.913961, 40.575672],
          [-93.900877, 40.575874],
          [-93.899317, 40.575942],
          [-93.898327, 40.576011],
          [-93.853656, 40.576606],
          [-93.84093, 40.576791],
          [-93.818725, 40.577086],
          [-93.815485, 40.577278],
          [-93.774344, 40.577584]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29089",
      state: "29",
      county: "089",
      name: "Howard County",
      censusArea: 463.849,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.849224, 39.226218],
          [-92.790369, 39.343586],
          [-92.707367, 39.321614],
          [-92.430229, 39.248795],
          [-92.565124, 38.998274],
          [-92.558086, 38.970692],
          [-92.841617, 38.979821],
          [-92.934569, 39.064547],
          [-92.94165, 39.125892],
          [-92.849224, 39.226218]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29091",
      state: "29",
      county: "091",
      name: "Howell County",
      censusArea: 927.248,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.658111, 36.888724],
          [-91.674237, 36.787832],
          [-91.672343, 36.499463],
          [-91.686026, 36.499374],
          [-91.687615, 36.499397],
          [-91.726663, 36.499209],
          [-91.766111, 36.499114],
          [-91.784713, 36.499074],
          [-91.7995, 36.498952],
          [-91.80204, 36.498963],
          [-91.805981, 36.498987],
          [-91.864385, 36.498789],
          [-91.865995, 36.498783],
          [-91.985802, 36.498431],
          [-91.988751, 36.498498],
          [-92.019375, 36.498524],
          [-92.028847, 36.498642],
          [-92.055789, 36.49867],
          [-92.057178, 36.49867],
          [-92.074934, 36.498761],
          [-92.098356, 36.498803],
          [-92.120306, 36.498864],
          [-92.120415, 36.498863],
          [-92.112346, 36.794231],
          [-92.095982, 36.794011],
          [-92.088708, 37.056248],
          [-91.655607, 37.048925],
          [-91.657814, 36.889648],
          [-91.658111, 36.888724]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37015",
      state: "37",
      county: "015",
      name: "Bertie County",
      censusArea: 699.27,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.692376, 35.945342],
          [-76.760842, 35.864822],
          [-76.830195, 35.885922],
          [-76.888624, 35.812287],
          [-76.918531, 35.895647],
          [-77.004306, 35.858714],
          [-77.043684, 35.943146],
          [-77.196689, 35.937433],
          [-77.206169, 36.004737],
          [-77.327686, 36.074512],
          [-77.230694, 36.103618],
          [-77.264744, 36.159745],
          [-77.29181, 36.16849],
          [-77.20879, 36.246875],
          [-77.183194, 36.242692],
          [-76.706556, 36.243869],
          [-76.736326, 36.147042],
          [-76.668814, 36.020968],
          [-76.671759, 36.018276],
          [-76.684922, 36.009852],
          [-76.683869, 36.000375],
          [-76.679657, 35.991951],
          [-76.684922, 35.983001],
          [-76.695452, 35.973524],
          [-76.70019, 35.964573],
          [-76.697558, 35.951937],
          [-76.692376, 35.945342]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37023",
      state: "37",
      county: "023",
      name: "Burke County",
      censusArea: 507.099,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.824184, 35.574955],
          [-81.869347, 35.719574],
          [-81.987035, 35.806936],
          [-81.906607, 35.883437],
          [-81.943674, 35.959448],
          [-81.899583, 36.000127],
          [-81.80712, 35.962057],
          [-81.55596, 35.777332],
          [-81.364055, 35.767865],
          [-81.534359, 35.568811],
          [-81.535632, 35.567324],
          [-81.535733, 35.567064],
          [-81.536076, 35.566555],
          [-81.53776, 35.564472],
          [-81.692329, 35.579789],
          [-81.824184, 35.574955]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37029",
      state: "37",
      county: "029",
      name: "Camden County",
      censusArea: 240.556,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.491497, 36.550365],
          [-76.313215, 36.550551],
          [-76.313196, 36.550551],
          [-76.158583, 36.426347],
          [-76.114069, 36.357498],
          [-76.050755, 36.359819],
          [-75.948426, 36.285268],
          [-75.887415, 36.160233],
          [-75.904999, 36.164188],
          [-75.920028, 36.164853],
          [-75.924654, 36.163591],
          [-75.939047, 36.165518],
          [-75.995191, 36.178072],
          [-76.016984, 36.186367],
          [-76.018936, 36.188318],
          [-76.031784, 36.189131],
          [-76.0471, 36.183106],
          [-76.216558, 36.298174],
          [-76.231218, 36.371712],
          [-76.491477, 36.510536],
          [-76.541391, 36.550312],
          [-76.491497, 36.550365]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37031",
      state: "37",
      county: "031",
      name: "Carteret County",
      censusArea: 506.251,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.090323, 34.802755],
          [-76.939252, 34.828636],
          [-76.693085, 34.869219],
          [-76.65421, 34.937088],
          [-76.711278, 34.981971],
          [-76.644562, 35.019799],
          [-76.642012, 35.012786],
          [-76.628523, 34.994801],
          [-76.588055, 34.991428],
          [-76.566697, 34.998173],
          [-76.539719, 35.000421],
          [-76.502623, 35.007166],
          [-76.495879, 35.011662],
          [-76.491382, 35.017283],
          [-76.490258, 35.034144],
          [-76.480141, 35.05213],
          [-76.474521, 35.070116],
          [-76.468796, 35.075345],
          [-76.463468, 35.076411],
          [-76.435762, 35.057941],
          [-76.432565, 35.049061],
          [-76.431855, 35.030945],
          [-76.425461, 35.001464],
          [-76.406281, 34.987256],
          [-76.398466, 34.9766],
          [-76.395625, 34.975179],
          [-76.332044, 34.970917],
          [-76.326361, 34.976245],
          [-76.329557, 34.986901],
          [-76.350159, 35.016737],
          [-76.360815, 35.025973],
          [-76.364367, 35.031301],
          [-76.364367, 35.034853],
          [-76.35229, 35.033077],
          [-76.318546, 35.020645],
          [-76.293682, 35.009633],
          [-76.288354, 35.005726],
          [-76.288709, 34.997201],
          [-76.29093, 34.994285],
          [-76.293851, 34.991948],
          [-76.296188, 34.98669],
          [-76.296524, 34.976245],
          [-76.290691, 34.969059],
          [-76.275567, 34.960971],
          [-76.274856, 34.953867],
          [-76.277698, 34.940014],
          [-76.284092, 34.936817],
          [-76.311442, 34.910177],
          [-76.319967, 34.897745],
          [-76.33382, 34.882116],
          [-76.347673, 34.872171],
          [-76.368274, 34.872881],
          [-76.377154, 34.867553],
          [-76.379641, 34.86258],
          [-76.395269, 34.855476],
          [-76.400242, 34.855476],
          [-76.411609, 34.841268],
          [-76.415871, 34.82564],
          [-76.415871, 34.813563],
          [-76.403108, 34.806153],
          [-76.390254, 34.805568],
          [-76.383827, 34.807906],
          [-76.373247, 34.817115],
          [-76.362591, 34.824219],
          [-76.341279, 34.842689],
          [-76.322808, 34.86116],
          [-76.273986, 34.897298],
          [-76.233672, 34.925926],
          [-76.194936, 34.962747],
          [-76.160127, 34.991163],
          [-76.11182, 35.034497],
          [-76.093349, 35.048705],
          [-76.069906, 35.075701],
          [-76.064933, 35.077121],
          [-76.043621, 35.070017],
          [-76.038648, 35.065045],
          [-76.035933, 35.058987],
          [-76.073, 35.030509],
          [-76.137269, 34.987858],
          [-76.233088, 34.905477],
          [-76.31021, 34.852309],
          [-76.386804, 34.784579],
          [-76.450454, 34.71445],
          [-76.494068, 34.66197],
          [-76.524199, 34.615416],
          [-76.535946, 34.588577],
          [-76.555196, 34.615993],
          [-76.553806, 34.628252],
          [-76.550423, 34.630789],
          [-76.549343, 34.645585],
          [-76.579467, 34.660174],
          [-76.618719, 34.67255],
          [-76.642939, 34.677618],
          [-76.662645, 34.685524],
          [-76.676312, 34.693151],
          [-76.693751, 34.692509],
          [-76.726969, 34.69669],
          [-76.770044, 34.696899],
          [-76.817453, 34.693722],
          [-76.906257, 34.68282],
          [-76.990262, 34.669623],
          [-77.031105, 34.661184],
          [-77.1128, 34.639352],
          [-77.1261, 34.756927],
          [-77.165803, 34.784945],
          [-77.090323, 34.802755]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29133",
      state: "29",
      county: "133",
      name: "Mississippi County",
      censusArea: 411.583,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.519809, 36.869617],
          [-89.465393, 36.935729],
          [-89.322733, 37.009033],
          [-89.29213, 36.992189],
          [-89.278628, 36.98867],
          [-89.271127, 36.985287],
          [-89.261154, 36.982437],
          [-89.245481, 36.98315],
          [-89.226247, 36.985287],
          [-89.207013, 36.982437],
          [-89.185491, 36.973518],
          [-89.170008, 36.970298],
          [-89.132685, 36.9822],
          [-89.100794, 36.944006],
          [-89.100766, 36.943973],
          [-89.147674, 36.847148],
          [-89.177177, 36.835779],
          [-89.178574, 36.816554],
          [-89.179229, 36.812915],
          [-89.12353, 36.785309],
          [-89.123548, 36.78522],
          [-89.130399, 36.751702],
          [-89.142313, 36.755369],
          [-89.169106, 36.759473],
          [-89.184523, 36.753638],
          [-89.197808, 36.739412],
          [-89.201047, 36.725772],
          [-89.19948, 36.716045],
          [-89.171882, 36.672526],
          [-89.174733, 36.650476],
          [-89.174741, 36.650416],
          [-89.187749, 36.641115],
          [-89.197654, 36.628936],
          [-89.200902, 36.618177],
          [-89.202607, 36.601576],
          [-89.213563, 36.580119],
          [-89.227319, 36.569375],
          [-89.236542, 36.566824],
          [-89.259994, 36.565149],
          [-89.27171, 36.571387],
          [-89.294637, 36.593729],
          [-89.313405, 36.62012],
          [-89.326731, 36.632186],
          [-89.327578, 36.632127],
          [-89.519809, 36.869617]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29143",
      state: "29",
      county: "143",
      name: "New Madrid County",
      censusArea: 674.84,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.485106, 36.497692],
          [-89.47246, 36.513741],
          [-89.465888, 36.529946],
          [-89.465445, 36.536163],
          [-89.467761, 36.546847],
          [-89.473341, 36.559918],
          [-89.479093, 36.568206],
          [-89.484836, 36.571821],
          [-89.500076, 36.576305],
          [-89.527583, 36.581147],
          [-89.546113, 36.579989],
          [-89.55264, 36.577178],
          [-89.566817, 36.564216],
          [-89.571509, 36.552569],
          [-89.570071, 36.544387],
          [-89.560344, 36.525436],
          [-89.5391, 36.498201],
          [-89.522674, 36.481305],
          [-89.519501, 36.475419],
          [-89.509558, 36.375065],
          [-89.513178, 36.359897],
          [-89.519, 36.3486],
          [-89.531822, 36.339246],
          [-89.545006, 36.336809],
          [-89.611308, 36.411273],
          [-89.752147, 36.386396],
          [-89.96131, 36.388803],
          [-89.958786, 36.629331],
          [-89.700952, 36.627453],
          [-89.691603, 36.859517],
          [-89.519809, 36.869617],
          [-89.327578, 36.632127],
          [-89.343753, 36.630991],
          [-89.365548, 36.625059],
          [-89.376367, 36.613868],
          [-89.38814, 36.573416],
          [-89.400581, 36.538337],
          [-89.417293, 36.499033],
          [-89.429311, 36.481875],
          [-89.448468, 36.46442],
          [-89.460436, 36.45814],
          [-89.471718, 36.457001],
          [-89.486215, 36.46162],
          [-89.493198, 36.470124],
          [-89.493495, 36.4787],
          [-89.485106, 36.497692]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29149",
      state: "29",
      county: "149",
      name: "Oregon County",
      censusArea: 789.796,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.115926, 36.823672],
          [-91.126529, 36.497712],
          [-91.21736, 36.497511],
          [-91.218645, 36.497564],
          [-91.227398, 36.497617],
          [-91.404915, 36.49712],
          [-91.405141, 36.497165],
          [-91.407137, 36.497112],
          [-91.407261, 36.497123],
          [-91.433298, 36.497262],
          [-91.436502, 36.497377],
          [-91.446284, 36.497469],
          [-91.450005, 36.497538],
          [-91.529774, 36.499022],
          [-91.53687, 36.499156],
          [-91.539359, 36.499116],
          [-91.549163, 36.499161],
          [-91.596213, 36.499162],
          [-91.601317, 36.499343],
          [-91.631439, 36.499198],
          [-91.64259, 36.499335],
          [-91.672343, 36.499463],
          [-91.674237, 36.787832],
          [-91.658111, 36.888724],
          [-91.250129, 36.88515],
          [-91.22283, 36.883857],
          [-91.224825, 36.825679],
          [-91.115926, 36.823672]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29155",
      state: "29",
      county: "155",
      name: "Pemiscot County",
      censusArea: 492.544,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.545006, 36.336809],
          [-89.560439, 36.337746],
          [-89.581636, 36.342357],
          [-89.605668, 36.342234],
          [-89.610689, 36.340442],
          [-89.615841, 36.336085],
          [-89.6198, 36.329546],
          [-89.620255, 36.323006],
          [-89.611819, 36.309088],
          [-89.578492, 36.288317],
          [-89.554289, 36.277751],
          [-89.544797, 36.280458],
          [-89.539487, 36.277368],
          [-89.535529, 36.270541],
          [-89.534745, 36.252576],
          [-89.541621, 36.247891],
          [-89.589561, 36.239116],
          [-89.602374, 36.238106],
          [-89.678046, 36.248284],
          [-89.691308, 36.252079],
          [-89.699817, 36.248384],
          [-89.629452, 36.185382],
          [-89.618228, 36.179966],
          [-89.607004, 36.171179],
          [-89.594397, 36.155457],
          [-89.591605, 36.144096],
          [-89.594, 36.12719],
          [-89.601936, 36.11947],
          [-89.624235, 36.108626],
          [-89.684439, 36.051719],
          [-89.687254, 36.034048],
          [-89.690306, 36.024835],
          [-89.706932, 36.000981],
          [-89.733095, 36.000608],
          [-89.737564, 36.000522],
          [-89.737648, 36.000567],
          [-89.769973, 36.000536],
          [-89.770255, 36.000524],
          [-89.86901, 35.99964],
          [-89.87459, 35.999575],
          [-89.875085, 35.999578],
          [-89.875586, 35.999562],
          [-89.896508, 35.999432],
          [-89.901183, 35.999365],
          [-89.959375, 35.99902],
          [-89.96131, 36.388803],
          [-89.752147, 36.386396],
          [-89.611308, 36.411273],
          [-89.545006, 36.336809]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29165",
      state: "29",
      county: "165",
      name: "Platte County",
      censusArea: 420.19,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.600819, 39.456155],
          [-94.601733, 39.159603],
          [-94.706723, 39.173801],
          [-94.714137, 39.170403],
          [-94.723637, 39.169003],
          [-94.736537, 39.169203],
          [-94.752338, 39.173203],
          [-94.793948, 39.201276],
          [-94.832399, 39.227218],
          [-94.901009, 39.30168],
          [-94.907569, 39.320623],
          [-94.910641, 39.348335],
          [-94.908337, 39.355647],
          [-94.909954, 39.367459],
          [-94.946662, 39.399717],
          [-94.969061, 39.418876],
          [-95.102888, 39.533347],
          [-94.601326, 39.532138],
          [-94.601246, 39.530372],
          [-94.600819, 39.456155]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37101",
      state: "37",
      county: "101",
      name: "Johnston County",
      censusArea: 791.299,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.391146, 35.262632],
          [-78.491383, 35.263362],
          [-78.541262, 35.315116],
          [-78.530282, 35.333775],
          [-78.660555, 35.496245],
          [-78.708629, 35.519081],
          [-78.566633, 35.62895],
          [-78.473733, 35.701551],
          [-78.254903, 35.81773],
          [-78.19207, 35.730443],
          [-78.125587, 35.603111],
          [-78.064603, 35.585419],
          [-78.154889, 35.417627],
          [-78.153233, 35.351421],
          [-78.304662, 35.286097],
          [-78.332587, 35.279446],
          [-78.391146, 35.262632]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37107",
      state: "37",
      county: "107",
      name: "Lenoir County",
      censusArea: 400.591,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.747639, 35.071354],
          [-77.769064, 35.145471],
          [-77.83425, 35.177814],
          [-77.831055, 35.23344],
          [-77.805564, 35.369768],
          [-77.77768, 35.367682],
          [-77.749753, 35.373241],
          [-77.738695, 35.374869],
          [-77.701681, 35.380597],
          [-77.680542, 35.34776],
          [-77.660811, 35.352757],
          [-77.476, 35.426684],
          [-77.472532, 35.425518],
          [-77.470067, 35.420157],
          [-77.427046, 35.350424],
          [-77.390965, 35.340047],
          [-77.473716, 35.228983],
          [-77.478741, 35.230011],
          [-77.52321, 35.241725],
          [-77.527206, 35.243433],
          [-77.506638, 35.172177],
          [-77.601831, 35.071193],
          [-77.730784, 35.008259],
          [-77.73966, 35.016641],
          [-77.745885, 35.022587],
          [-77.747639, 35.071354]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37113",
      state: "37",
      county: "113",
      name: "Macon County",
      censusArea: 515.558,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.339707, 35.331004],
          [-83.108714, 35.000768],
          [-83.483064, 34.993737],
          [-83.659765, 35.160243],
          [-83.738106, 35.155911],
          [-83.702521, 35.248398],
          [-83.700556, 35.252172],
          [-83.698745, 35.253279],
          [-83.69693, 35.255682],
          [-83.694698, 35.257329],
          [-83.694278, 35.259366],
          [-83.692965, 35.260761],
          [-83.691764, 35.26369],
          [-83.690005, 35.26504],
          [-83.68939, 35.266963],
          [-83.68629, 35.271745],
          [-83.683945, 35.274216],
          [-83.683552, 35.276808],
          [-83.680043, 35.279414],
          [-83.339707, 35.331004]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37119",
      state: "37",
      county: "119",
      name: "Mecklenburg County",
      censusArea: 523.842,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.906417, 35.076608],
          [-80.906553, 35.076763],
          [-80.93495, 35.107409],
          [-80.95787, 35.092639],
          [-81.041489, 35.044703],
          [-81.057465, 35.061983],
          [-81.058029, 35.07319],
          [-81.057236, 35.086129],
          [-81.052078, 35.096276],
          [-81.046524, 35.100617],
          [-81.036163, 35.103205],
          [-81.032547, 35.109102],
          [-81.042758, 35.146042],
          [-81.005851, 35.156562],
          [-81.005061, 35.189821],
          [-81.006196, 35.210301],
          [-81.013956, 35.220588],
          [-81.008979, 35.24574],
          [-80.955388, 35.400833],
          [-80.94703, 35.487699],
          [-80.784339, 35.50584],
          [-80.764255, 35.400226],
          [-80.755284, 35.394927],
          [-80.744252, 35.380638],
          [-80.719625, 35.362842],
          [-80.708556, 35.355213],
          [-80.69362, 35.346806],
          [-80.667274, 35.269014],
          [-80.665835, 35.268181],
          [-80.550297, 35.208381],
          [-80.770385, 35.034234],
          [-80.840461, 35.001566],
          [-80.906417, 35.076608]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37125",
      state: "37",
      county: "125",
      name: "Moore County",
      censusArea: 697.843,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.614306, 35.163679],
          [-79.698036, 35.251451],
          [-79.769285, 35.511457],
          [-79.555896, 35.515085],
          [-79.351354, 35.517761],
          [-79.350009, 35.517991],
          [-79.273565, 35.353369],
          [-79.183301, 35.306976],
          [-79.223065, 35.268134],
          [-79.09613, 35.192181],
          [-79.098031, 35.188359],
          [-79.098726, 35.183439],
          [-79.096389, 35.177682],
          [-79.097671, 35.174172],
          [-79.229885, 35.210678],
          [-79.335189, 35.161827],
          [-79.459176, 35.043096],
          [-79.575384, 35.069815],
          [-79.614306, 35.163679]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40001",
      state: "40",
      county: "001",
      name: "Adair County",
      censusArea: 573.477,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.472647, 35.638556],
          [-94.807297, 35.638604],
          [-94.797279, 36.16139],
          [-94.562828, 36.161895],
          [-94.562803, 36.161749],
          [-94.561165, 36.15211],
          [-94.552184, 36.102235],
          [-94.547871, 36.078281],
          [-94.547715, 36.077271],
          [-94.535724, 36.007807],
          [-94.534852, 36.002678],
          [-94.533646, 35.996804],
          [-94.532071, 35.987852],
          [-94.528305, 35.966054],
          [-94.528162, 35.965665],
          [-94.52464, 35.945727],
          [-94.524344, 35.94405],
          [-94.52291, 35.936127],
          [-94.522634, 35.934892],
          [-94.522658, 35.934799],
          [-94.522658, 35.93425],
          [-94.507631, 35.845901],
          [-94.505642, 35.833628],
          [-94.504438, 35.826369],
          [-94.503011, 35.81721],
          [-94.501162, 35.80643],
          [-94.500764, 35.80382],
          [-94.500526, 35.802642],
          [-94.499647, 35.79691],
          [-94.499045, 35.79346],
          [-94.494549, 35.768303],
          [-94.493362, 35.761892],
          [-94.492932, 35.759166],
          [-94.48821, 35.72924],
          [-94.487585, 35.726147],
          [-94.472647, 35.638556]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40007",
      state: "40",
      county: "007",
      name: "Beaver County",
      censusArea: 1814.672,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.546145, 36.499343],
          [-100.578114, 36.499439],
          [-100.578114, 36.499463],
          [-100.583379, 36.499443],
          [-100.583539, 36.499483],
          [-100.592551, 36.499429],
          [-100.592556, 36.499469],
          [-100.592614, 36.499469],
          [-100.648344, 36.499463],
          [-100.648343, 36.499495],
          [-100.657763, 36.4995],
          [-100.657763, 36.499483],
          [-100.708628, 36.499521],
          [-100.708626, 36.499553],
          [-100.724361, 36.499558],
          [-100.724362, 36.49958],
          [-100.761811, 36.49958],
          [-100.761811, 36.499618],
          [-100.802886, 36.499621],
          [-100.802909, 36.499621],
          [-100.806172, 36.499634],
          [-100.80619, 36.499674],
          [-100.824218, 36.499618],
          [-100.824236, 36.499618],
          [-100.85084, 36.4997],
          [-100.859657, 36.499687],
          [-100.88408, 36.499682],
          [-100.884174, 36.499682],
          [-100.918513, 36.499621],
          [-100.936058, 36.499602],
          [-100.954153, 36.499599],
          [-100.945469, 36.998153],
          [-100.904588, 36.998561],
          [-100.904274, 36.998745],
          [-100.89166, 36.998604],
          [-100.855634, 36.998626],
          [-100.814277, 36.999085],
          [-100.806116, 36.999091],
          [-100.765484, 36.999177],
          [-100.756894, 36.999357],
          [-100.675552, 36.999688],
          [-100.633327, 36.999936],
          [-100.633323, 36.999936],
          [-100.62977, 37.000025],
          [-100.591413, 37.000399],
          [-100.591328, 37.000376],
          [-100.552683, 37.000735],
          [-100.551598, 37.00062],
          [-100.201676, 37.002081],
          [-100.193754, 37.002133],
          [-100.192371, 37.002036],
          [-100.187547, 37.002082],
          [-100.115722, 37.002206],
          [-100.089484, 37.002092],
          [-100.005706, 37.001726],
          [-100.002563, 37.001706],
          [-100.002866, 37.000029],
          [-100.00316, 36.59325],
          [-100.003762, 36.499699],
          [-100.090021, 36.499634],
          [-100.181221, 36.499633],
          [-100.310643, 36.499642],
          [-100.311018, 36.499688],
          [-100.311245, 36.499631],
          [-100.32415, 36.499679],
          [-100.334441, 36.49944],
          [-100.334464, 36.49942],
          [-100.351842, 36.499473],
          [-100.351852, 36.499487],
          [-100.378592, 36.499445],
          [-100.378634, 36.499517],
          [-100.41355, 36.499469],
          [-100.413634, 36.499444],
          [-100.421301, 36.499488],
          [-100.421328, 36.499447],
          [-100.433959, 36.499456],
          [-100.441064, 36.499462],
          [-100.441065, 36.49949],
          [-100.522227, 36.499291],
          [-100.530314, 36.499357],
          [-100.530478, 36.49924],
          [-100.531215, 36.49929],
          [-100.531215, 36.499341],
          [-100.546145, 36.499343]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40021",
      state: "40",
      county: "021",
      name: "Cherokee County",
      censusArea: 749.408,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.807297, 35.638604],
          [-95.127464, 35.638894],
          [-95.127163, 35.812755],
          [-95.265679, 35.813266],
          [-95.231459, 35.851195],
          [-95.278701, 35.964102],
          [-95.207946, 36.074708],
          [-95.118377, 36.074544],
          [-95.1185, 36.16163],
          [-95.011303, 36.161815],
          [-94.797279, 36.16139],
          [-94.807297, 35.638604]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40023",
      state: "40",
      county: "023",
      name: "Choctaw County",
      censusArea: 770.357,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.155981, 33.944027],
          [-95.219358, 33.961567],
          [-95.226393, 33.961954],
          [-95.231113, 33.960361],
          [-95.252906, 33.933648],
          [-95.253623, 33.92971],
          [-95.249807, 33.922241],
          [-95.2482, 33.912327],
          [-95.249922, 33.902024],
          [-95.255541, 33.892047],
          [-95.260815, 33.887653],
          [-95.287565, 33.873617],
          [-95.310475, 33.871481],
          [-95.339758, 33.868752],
          [-95.407795, 33.866308],
          [-95.445751, 33.86885],
          [-95.544346, 33.880111],
          [-95.548325, 33.882744],
          [-95.552085, 33.888422],
          [-95.552331, 33.89442],
          [-95.549475, 33.901311],
          [-95.549145, 33.90795],
          [-95.559414, 33.930179],
          [-95.567022, 33.932697],
          [-95.599678, 33.934247],
          [-95.757156, 33.867293],
          [-95.836905, 33.873374],
          [-95.937947, 33.948163],
          [-95.93815, 34.1251],
          [-95.991578, 34.156815],
          [-95.779376, 34.156699],
          [-95.156521, 34.156965],
          [-95.155981, 33.944027]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40033",
      state: "40",
      county: "033",
      name: "Cotton County",
      censusArea: 632.654,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.16912, 34.114171],
          [-98.200075, 34.116783],
          [-98.293901, 34.13302],
          [-98.300209, 34.134579],
          [-98.325445, 34.151025],
          [-98.364023, 34.157109],
          [-98.383222, 34.147806],
          [-98.398441, 34.128456],
          [-98.400494, 34.121778],
          [-98.433769, 34.096116],
          [-98.475066, 34.064269],
          [-98.504182, 34.072371],
          [-98.553717, 34.13366],
          [-98.577356, 34.1491],
          [-98.599789, 34.160571],
          [-98.610112, 34.161039],
          [-98.60966, 34.33372],
          [-98.661956, 34.405948],
          [-98.504988, 34.420452],
          [-98.504958, 34.449414],
          [-98.295928, 34.449043],
          [-98.24412, 34.507121],
          [-98.142148, 34.506994],
          [-98.139237, 34.507003],
          [-98.139226, 34.289775],
          [-98.139072, 34.14187],
          [-98.16912, 34.114171]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40041",
      state: "40",
      county: "041",
      name: "Delaware County",
      censusArea: 738.177,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.00573, 36.50989],
          [-94.999403, 36.670631],
          [-94.618019, 36.667921],
          [-94.617815, 36.612604],
          [-94.617865, 36.606851],
          [-94.617853, 36.59912],
          [-94.617814, 36.577732],
          [-94.617897, 36.536983],
          [-94.617868, 36.53609],
          [-94.617997, 36.53428],
          [-94.617883, 36.517799],
          [-94.617877, 36.514999],
          [-94.617919, 36.499414],
          [-94.615311, 36.484992],
          [-94.61383, 36.476248],
          [-94.611609, 36.461528],
          [-94.605408, 36.421949],
          [-94.602623, 36.405283],
          [-94.601984, 36.40212],
          [-94.599723, 36.387587],
          [-94.593397, 36.345742],
          [-94.5862, 36.299969],
          [-94.577883, 36.25008],
          [-94.577899, 36.249548],
          [-94.576003, 36.24007],
          [-94.575071, 36.233682],
          [-94.57488, 36.232741],
          [-94.574395, 36.229996],
          [-94.571806, 36.213748],
          [-94.571253, 36.210901],
          [-94.566588, 36.183774],
          [-94.565655, 36.178439],
          [-94.562828, 36.161895],
          [-94.797279, 36.16139],
          [-95.011303, 36.161815],
          [-95.00573, 36.50989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US40051",
      state: "40",
      county: "051",
      name: "Grady County",
      censusArea: 1100.497,
      stateName: "Oklahoma"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.096014, 35.377753],
          [-97.833675, 35.335362],
          [-97.812129, 35.333788],
          [-97.807186, 35.340846],
          [-97.796719, 35.339105],
          [-97.792022, 35.332702],
          [-97.788319, 35.336643],
          [-97.777567, 35.337087],
          [-97.771765, 35.345735],
          [-97.737305, 35.323762],
          [-97.724708, 35.325832],
          [-97.720035, 35.328515],
          [-97.706159, 35.324989],
          [-97.69587, 35.327709],
          [-97.685054, 35.334301],
          [-97.671595, 35.335934],
          [-97.668099, 34.855164],
          [-97.668425, 34.681344],
          [-98.087756, 34.681255],
          [-98.089098, 34.681119],
          [-98.089558, 34.854929],
          [-98.092645, 34.854919],
          [-98.096014, 35.377753]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37189",
      state: "37",
      county: "189",
      name: "Watauga County",
      censusArea: 312.556,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.725938, 36.340364],
          [-81.720969, 36.3875],
          [-81.56617, 36.272064],
          [-81.477298, 36.240272],
          [-81.455579, 36.199749],
          [-81.545455, 36.117398],
          [-81.81152, 36.111544],
          [-81.909121, 36.216374],
          [-81.918113, 36.28711],
          [-81.908137, 36.302013],
          [-81.855005, 36.337259],
          [-81.800812, 36.358073],
          [-81.793688, 36.360428],
          [-81.789712, 36.348324],
          [-81.764927, 36.338672],
          [-81.747842, 36.337356],
          [-81.730976, 36.341187],
          [-81.725938, 36.340364]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48051",
      state: "48",
      county: "051",
      name: "Burleson County",
      censusArea: 659.026,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.36919, 30.365072],
          [-96.640883, 30.296786],
          [-96.740542, 30.318655],
          [-96.963629, 30.557169],
          [-96.621281, 30.730819],
          [-96.599467, 30.728634],
          [-96.595776, 30.715072],
          [-96.568795, 30.701387],
          [-96.566983, 30.69567],
          [-96.572064, 30.688849],
          [-96.581519, 30.68889],
          [-96.584171, 30.683191],
          [-96.582804, 30.663792],
          [-96.584439, 30.658957],
          [-96.594308, 30.652589],
          [-96.601708, 30.640535],
          [-96.510494, 30.627828],
          [-96.358817, 30.541753],
          [-96.290895, 30.443999],
          [-96.285516, 30.448393],
          [-96.286449, 30.433756],
          [-96.282564, 30.426614],
          [-96.287993, 30.421281],
          [-96.281109, 30.414831],
          [-96.272049, 30.416145],
          [-96.272837, 30.409042],
          [-96.278998, 30.407422],
          [-96.28659, 30.411661],
          [-96.298866, 30.410502],
          [-96.297599, 30.400421],
          [-96.302498, 30.399403],
          [-96.306356, 30.391715],
          [-96.294939, 30.385623],
          [-96.294068, 30.380235],
          [-96.299173, 30.379152],
          [-96.312795, 30.3843],
          [-96.340485, 30.367925],
          [-96.358976, 30.372637],
          [-96.36919, 30.365072]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35023",
      state: "35",
      county: "023",
      name: "Hidalgo County",
      censusArea: 3436.861,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.048286, 32.089114],
          [-109.047926, 32.426376],
          [-109.047653, 32.681379],
          [-109.047653, 32.686327],
          [-109.047645, 32.689988],
          [-109.047638, 32.693439],
          [-109.047117, 32.777569],
          [-108.854381, 32.777276],
          [-108.853075, 32.603627],
          [-108.646373, 32.603582],
          [-108.647155, 32.516897],
          [-108.537035, 32.516751],
          [-108.538135, 32.079836],
          [-108.524586, 32.079732],
          [-108.524702, 31.863694],
          [-108.217194, 31.864096],
          [-108.217295, 31.820297],
          [-108.209995, 31.820398],
          [-108.209995, 31.783699],
          [-108.208394, 31.783599],
          [-108.208521, 31.499798],
          [-108.208572, 31.499742],
          [-108.208573, 31.333395],
          [-108.851105, 31.332301],
          [-108.861028, 31.332315],
          [-109.050044, 31.332502],
          [-109.049843, 31.499515],
          [-109.049813, 31.499528],
          [-109.049195, 31.796551],
          [-109.048763, 31.810776],
          [-109.048769, 31.861383],
          [-109.04859, 31.870791],
          [-109.048599, 32.013651],
          [-109.048731, 32.028174],
          [-109.048296, 32.084093],
          [-109.048286, 32.089114]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35029",
      state: "35",
      county: "029",
      name: "Luna County",
      censusArea: 2965.188,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.296824, 31.783762],
          [-107.422246, 31.783599],
          [-107.422495, 31.783599],
          [-108.208394, 31.783599],
          [-108.209995, 31.783699],
          [-108.209995, 31.820398],
          [-108.217295, 31.820297],
          [-108.217194, 31.864096],
          [-108.229259, 32.516996],
          [-107.923489, 32.517283],
          [-107.92349, 32.60438],
          [-107.608283, 32.605382],
          [-107.299463, 32.605534],
          [-107.296824, 31.783762]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35031",
      state: "35",
      county: "031",
      name: "Mckinley County",
      censusArea: 5449.809,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.468585, 35.299239],
          [-108.469264, 34.958972],
          [-109.045851, 34.959718],
          [-109.046082, 35.174665],
          [-109.046481, 35.546326],
          [-109.046509, 35.54644],
          [-109.046296, 35.614251],
          [-109.046295, 35.616517],
          [-109.046024, 35.8798],
          [-109.046055, 35.888721],
          [-109.046054, 35.92586],
          [-109.046011, 35.925896],
          [-109.045973, 36.002338],
          [-107.62651, 36.00032],
          [-107.308212, 35.99869],
          [-107.306591, 35.41853],
          [-107.309634, 35.419011],
          [-107.310041, 35.305192],
          [-107.628344, 35.304766],
          [-107.62821, 35.348333],
          [-107.734079, 35.348332],
          [-107.734212, 35.304934],
          [-108.468586, 35.306755],
          [-108.468585, 35.299239]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35035",
      state: "35",
      county: "035",
      name: "Otero County",
      censusArea: 6613.209,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.998003, 32.002328],
          [-106.09976, 32.002174],
          [-106.18184, 32.00205],
          [-106.200699, 32.001785],
          [-106.205915, 32.001762],
          [-106.313307, 32.001512],
          [-106.377174, 32.00149],
          [-106.376614, 32.957941],
          [-106.340731, 32.95825],
          [-106.340473, 33.052795],
          [-106.345851, 33.390815],
          [-105.727343, 33.390524],
          [-105.725957, 33.304834],
          [-105.319045, 33.30509],
          [-105.316882, 33.132218],
          [-105.316151, 32.962014],
          [-105.35255, 32.961976],
          [-105.353907, 32.518769],
          [-104.851525, 32.520505],
          [-104.847757, 32.000482],
          [-104.918272, 32.000496],
          [-105.077046, 32.000579],
          [-105.078605, 32.000533],
          [-105.11804, 32.000485],
          [-105.131377, 32.000524],
          [-105.132916, 32.000518],
          [-105.14824, 32.000485],
          [-105.15031, 32.000497],
          [-105.153994, 32.000497],
          [-105.390396, 32.000607],
          [-105.427049, 32.000638],
          [-105.428582, 32.0006],
          [-105.429281, 32.000577],
          [-105.886159, 32.00197],
          [-105.9006, 32.0021],
          [-105.998003, 32.002328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47153",
      state: "47",
      county: "153",
      name: "Sequatchie County",
      censusArea: 265.858,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.557535, 35.532977],
          [-85.423804, 35.567422],
          [-85.403298, 35.49228],
          [-85.225877, 35.354276],
          [-85.246005, 35.319077],
          [-85.387089, 35.147169],
          [-85.444144, 35.262646],
          [-85.558551, 35.319292],
          [-85.485635, 35.406433],
          [-85.607326, 35.531651],
          [-85.557535, 35.532977]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47157",
      state: "47",
      county: "157",
      name: "Shelby County",
      censusArea: 763.174,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.642786, 35.04486],
          [-89.643782, 35.012092],
          [-89.643739, 35.011693],
          [-89.644282, 34.995293],
          [-89.724324, 34.994763],
          [-89.795187, 34.994293],
          [-89.848488, 34.994193],
          [-89.883365, 34.994261],
          [-89.893402, 34.994356],
          [-90.309297, 34.995694],
          [-90.309877, 35.00975],
          [-90.295596, 35.040093],
          [-90.193859, 35.061646],
          [-90.174594, 35.116682],
          [-90.160058, 35.12883],
          [-90.142794, 35.135091],
          [-90.109393, 35.118891],
          [-90.100593, 35.116691],
          [-90.09061, 35.118287],
          [-90.08342, 35.12167],
          [-90.065392, 35.137691],
          [-90.064612, 35.140621],
          [-90.073354, 35.211004],
          [-90.074262, 35.218316],
          [-90.07741, 35.225479],
          [-90.097947, 35.249983],
          [-90.105093, 35.254288],
          [-90.116493, 35.255788],
          [-90.140394, 35.252289],
          [-90.152094, 35.255989],
          [-90.158865, 35.262577],
          [-90.166594, 35.274588],
          [-90.168871, 35.281997],
          [-90.163812, 35.296115],
          [-90.158913, 35.300637],
          [-90.153394, 35.302588],
          [-90.114893, 35.303887],
          [-90.086691, 35.369935],
          [-90.089612, 35.379842],
          [-90.074992, 35.384152],
          [-90.061788, 35.386184],
          [-90.054322, 35.389277],
          [-89.957347, 35.528683],
          [-89.933572, 35.533299],
          [-90.041563, 35.39662],
          [-89.889317, 35.390906],
          [-89.70248, 35.408584],
          [-89.632776, 35.375824],
          [-89.642786, 35.04486]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47163",
      state: "47",
      county: "163",
      name: "Sullivan County",
      censusArea: 413.363,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.609176, 36.594099],
          [-82.561074, 36.5948],
          [-82.559774, 36.5948],
          [-82.554294, 36.594876],
          [-82.293814, 36.595565],
          [-82.243274, 36.595699],
          [-82.226653, 36.595743],
          [-82.225716, 36.595744],
          [-82.223445, 36.595721],
          [-82.221713, 36.595814],
          [-82.211005, 36.59586],
          [-82.210497, 36.595772],
          [-82.188491, 36.595179],
          [-82.18074, 36.594928],
          [-82.177247, 36.594768],
          [-82.173982, 36.594607],
          [-82.150727, 36.594673],
          [-82.148569, 36.594718],
          [-82.14607, 36.594712],
          [-81.934144, 36.594213],
          [-81.922644, 36.616213],
          [-81.826742, 36.614215],
          [-81.983843, 36.508314],
          [-82.188053, 36.400608],
          [-82.299859, 36.396505],
          [-82.438266, 36.445703],
          [-82.680677, 36.4318],
          [-82.609176, 36.594099]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47171",
      state: "47",
      county: "171",
      name: "Unicoi County",
      censusArea: 186.165,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.462958, 36.007309],
          [-82.487411, 35.991634],
          [-82.487451, 35.991557],
          [-82.50586, 35.978342],
          [-82.507068, 35.977475],
          [-82.512598, 35.975664],
          [-82.557529, 35.954671],
          [-82.596774, 35.964987],
          [-82.611262, 35.973155],
          [-82.615062, 36.000306],
          [-82.602877, 36.039833],
          [-82.5772, 36.078387],
          [-82.595065, 36.096205],
          [-82.527605, 36.148951],
          [-82.437251, 36.154709],
          [-82.34046, 36.252807],
          [-82.256356, 36.233208],
          [-82.222052, 36.156911],
          [-82.234807, 36.14172],
          [-82.235479, 36.140748],
          [-82.236415, 36.139926],
          [-82.237737, 36.139189],
          [-82.325169, 36.119363],
          [-82.329177, 36.117427],
          [-82.348422, 36.115929],
          [-82.360919, 36.110614],
          [-82.366566, 36.10765],
          [-82.371383, 36.106388],
          [-82.375558, 36.105609],
          [-82.409458, 36.083409],
          [-82.416857, 36.072885],
          [-82.462958, 36.007309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47181",
      state: "47",
      county: "181",
      name: "Wayne County",
      censusArea: 734.1,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.984916, 35.006256],
          [-87.981585, 35.295678],
          [-88.024741, 35.392307],
          [-87.984553, 35.392115],
          [-87.975184, 35.414174],
          [-88.007709, 35.423381],
          [-87.97318, 35.459968],
          [-87.717559, 35.483347],
          [-87.575352, 35.398454],
          [-87.588833, 35.058431],
          [-87.606031, 35.003343],
          [-87.664123, 35.003523],
          [-87.671405, 35.003537],
          [-87.696834, 35.003852],
          [-87.700543, 35.003988],
          [-87.702321, 35.003945],
          [-87.709491, 35.004089],
          [-87.75889, 35.004711],
          [-87.767602, 35.004783],
          [-87.773586, 35.004946],
          [-87.851886, 35.005656],
          [-87.853411, 35.005576],
          [-87.853528, 35.005541],
          [-87.872626, 35.005571],
          [-87.877742, 35.005512],
          [-87.877969, 35.005468],
          [-87.984916, 35.006256]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45057",
      state: "45",
      county: "057",
      name: "Lancaster County",
      censusArea: 549.16,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.906417, 35.076608],
          [-80.840461, 35.001566],
          [-80.806784, 34.963249],
          [-80.806461, 34.962894],
          [-80.782042, 34.935782],
          [-80.79675, 34.825281],
          [-80.777712, 34.819697],
          [-80.771792, 34.819646],
          [-80.646601, 34.818592],
          [-80.644656, 34.818568],
          [-80.626077, 34.818217],
          [-80.625993, 34.818239],
          [-80.621222, 34.818174],
          [-80.561674, 34.817481],
          [-80.408373, 34.614765],
          [-80.550552, 34.560116],
          [-80.523945, 34.509752],
          [-80.739966, 34.479957],
          [-80.743345, 34.539916],
          [-80.879227, 34.457776],
          [-80.877491, 34.543146],
          [-80.896406, 34.631781],
          [-80.860274, 34.695847],
          [-80.898215, 34.820746],
          [-80.868652, 34.951143],
          [-80.906417, 35.076608]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36085",
      state: "36",
      county: "085",
      name: "Richmond County",
      censusArea: 58.37,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.144428, 40.53516],
          [-74.148697, 40.534489],
          [-74.160859, 40.52679],
          [-74.177986, 40.519603],
          [-74.182157, 40.520634],
          [-74.199923, 40.511729],
          [-74.210474, 40.509448],
          [-74.219787, 40.502603],
          [-74.23324, 40.501299],
          [-74.246688, 40.496103],
          [-74.250188, 40.496703],
          [-74.254588, 40.502303],
          [-74.256088, 40.507903],
          [-74.252702, 40.513895],
          [-74.242888, 40.520903],
          [-74.241732, 40.531273],
          [-74.247808, 40.543396],
          [-74.229002, 40.555041],
          [-74.216997, 40.554991],
          [-74.210887, 40.560902],
          [-74.204054, 40.589336],
          [-74.19682, 40.597037],
          [-74.195407, 40.601806],
          [-74.196096, 40.616169],
          [-74.200994, 40.616906],
          [-74.201812, 40.619507],
          [-74.20058, 40.631448],
          [-74.1894, 40.642121],
          [-74.180191, 40.645521],
          [-74.174085, 40.645109],
          [-74.170187, 40.642201],
          [-74.152973, 40.638886],
          [-74.120186, 40.642201],
          [-74.086485, 40.648601],
          [-74.075884, 40.648101],
          [-74.0697, 40.641216],
          [-74.067598, 40.623865],
          [-74.060345, 40.611999],
          [-74.053125, 40.603678],
          [-74.059184, 40.593502],
          [-74.068184, 40.584102],
          [-74.090797, 40.566463],
          [-74.111471, 40.546908],
          [-74.112585, 40.547603],
          [-74.121672, 40.542691],
          [-74.137241, 40.530076],
          [-74.14023, 40.533738],
          [-74.144428, 40.53516]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36097",
      state: "36",
      county: "097",
      name: "Schuyler County",
      censusArea: 328.333,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.895596, 42.541537],
          [-76.696655, 42.54679],
          [-76.691406, 42.284307],
          [-76.619303, 42.282853],
          [-76.642256, 42.233721],
          [-76.733912, 42.29372],
          [-76.965028, 42.278495],
          [-77.099657, 42.272356],
          [-77.107203, 42.483771],
          [-76.889805, 42.463054],
          [-76.895596, 42.541537]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36105",
      state: "36",
      county: "105",
      name: "Sullivan County",
      censusArea: 968.132,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.050074, 41.606893],
          [-75.053077, 41.618552],
          [-75.048199, 41.632011],
          [-75.053431, 41.752538],
          [-75.090799, 41.811991],
          [-75.114399, 41.843583],
          [-75.140241, 41.852078],
          [-75.146446, 41.850899],
          [-74.997252, 41.918485],
          [-74.780693, 42.016375],
          [-74.453685, 41.875595],
          [-74.575086, 41.745258],
          [-74.395071, 41.644876],
          [-74.367055, 41.590977],
          [-74.475591, 41.504334],
          [-74.752399, 41.493743],
          [-74.75595, 41.426804],
          [-74.799165, 41.430451],
          [-74.876721, 41.440338],
          [-74.891948, 41.448853],
          [-74.912517, 41.475605],
          [-74.93976, 41.483371],
          [-74.984226, 41.506299],
          [-75.04049, 41.569688],
          [-75.050074, 41.606893]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36111",
      state: "36",
      county: "111",
      name: "Ulster County",
      censusArea: 1124.235,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.942482, 41.684093],
          [-73.947382, 41.667493],
          [-73.953307, 41.589977],
          [-74.126393, 41.582544],
          [-74.264093, 41.632738],
          [-74.367055, 41.590977],
          [-74.395071, 41.644876],
          [-74.575086, 41.745258],
          [-74.453685, 41.875595],
          [-74.780693, 42.016375],
          [-74.451713, 42.169225],
          [-74.307571, 42.114346],
          [-74.074797, 42.096589],
          [-74.042393, 42.170386],
          [-73.910675, 42.127293],
          [-73.921465, 42.110025],
          [-73.929626, 42.078778],
          [-73.962221, 41.90102],
          [-73.941081, 41.735993],
          [-73.941081, 41.732693],
          [-73.944435, 41.714634],
          [-73.946682, 41.699396],
          [-73.945782, 41.695593],
          [-73.943482, 41.690293],
          [-73.942482, 41.684093]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36115",
      state: "36",
      county: "115",
      name: "Washington County",
      censusArea: 831.184,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.269472, 43.030686],
          [-73.274294, 42.943652],
          [-73.635463, 42.94129],
          [-73.573342, 43.100545],
          [-73.59496, 43.306118],
          [-73.62894, 43.486391],
          [-73.492893, 43.657303],
          [-73.43812, 43.803687],
          [-73.435909, 43.803836],
          [-73.433237, 43.804083],
          [-73.379312, 43.808478],
          [-73.379279, 43.808391],
          [-73.357547, 43.785933],
          [-73.350431, 43.771438],
          [-73.360711, 43.753268],
          [-73.370724, 43.735571],
          [-73.403517, 43.685032],
          [-73.408697, 43.67402],
          [-73.421606, 43.646577],
          [-73.431229, 43.588285],
          [-73.428636, 43.583994],
          [-73.400295, 43.568889],
          [-73.375594, 43.61335],
          [-73.366537, 43.623462],
          [-73.306234, 43.628018],
          [-73.302552, 43.625708],
          [-73.245594, 43.540253],
          [-73.242042, 43.534925],
          [-73.247061, 43.514919],
          [-73.252582, 43.370997],
          [-73.252674, 43.370285],
          [-73.252832, 43.363493],
          [-73.253084, 43.354714],
          [-73.254848, 43.314684],
          [-73.259159, 43.216848],
          [-73.269472, 43.030686]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36119",
      state: "36",
      county: "119",
      name: "Westchester County",
      censusArea: 430.497,
      stateName: "New York"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-73.767176, 40.886299],
            [-73.767076, 40.885399],
            [-73.767076, 40.884799],
            [-73.767076, 40.883499],
            [-73.766276, 40.881099],
            [-73.766976, 40.880099],
            [-73.770876, 40.879299],
            [-73.775276, 40.882199],
            [-73.775176, 40.884199],
            [-73.772776, 40.884599],
            [-73.772276, 40.887499],
            [-73.770576, 40.888399],
            [-73.768276, 40.887599],
            [-73.767276, 40.886899],
            [-73.767176, 40.886299]
          ]
        ],
        [
          [
            [-73.514617, 41.198434],
            [-73.614407, 41.153001],
            [-73.632153, 41.144921],
            [-73.639672, 41.141495],
            [-73.727775, 41.100696],
            [-73.694273, 41.059296],
            [-73.687173, 41.050697],
            [-73.679973, 41.041797],
            [-73.670472, 41.030097],
            [-73.655371, 41.012797],
            [-73.654671, 41.011697],
            [-73.657336, 40.985171],
            [-73.655972, 40.979597],
            [-73.659972, 40.968398],
            [-73.662072, 40.966198],
            [-73.664472, 40.967198],
            [-73.678073, 40.962798],
            [-73.683273, 40.948998],
            [-73.686473, 40.945198],
            [-73.697974, 40.939598],
            [-73.721739, 40.932037],
            [-73.731775, 40.924999],
            [-73.756776, 40.912599],
            [-73.781338, 40.885447],
            [-73.783545, 40.88104],
            [-73.804789, 40.886505],
            [-73.806395, 40.886801],
            [-73.823244, 40.891199],
            [-73.82312, 40.890648],
            [-73.823617, 40.890413],
            [-73.823557, 40.889865],
            [-73.824047, 40.889866],
            [-73.917905, 40.917577],
            [-73.893979, 40.997197],
            [-73.88938, 41.037597],
            [-73.8947, 41.069937],
            [-73.89488, 41.082396],
            [-73.88718, 41.133095],
            [-73.895381, 41.153995],
            [-73.909281, 41.164395],
            [-73.931482, 41.204994],
            [-73.947965, 41.223101],
            [-73.982584, 41.321693],
            [-73.544728, 41.366375],
            [-73.550961, 41.295422],
            [-73.482709, 41.21276],
            [-73.509487, 41.200814],
            [-73.514617, 41.198434]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45091",
      state: "45",
      county: "091",
      name: "York County",
      censusArea: 680.595,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.032547, 35.109102],
          [-81.036163, 35.103205],
          [-81.046524, 35.100617],
          [-81.052078, 35.096276],
          [-81.057236, 35.086129],
          [-81.058029, 35.07319],
          [-81.057465, 35.061983],
          [-81.041489, 35.044703],
          [-80.95787, 35.092639],
          [-80.93495, 35.107409],
          [-80.906553, 35.076763],
          [-80.906417, 35.076608],
          [-80.868652, 34.951143],
          [-80.898215, 34.820746],
          [-81.478454, 34.821509],
          [-81.461755, 34.831018],
          [-81.457257, 34.83928],
          [-81.488434, 35.029516],
          [-81.409586, 35.046396],
          [-81.366599, 35.164889],
          [-81.32802, 35.163404],
          [-81.269199, 35.16114],
          [-81.241686, 35.160081],
          [-81.239358, 35.159974],
          [-81.090215, 35.152627],
          [-81.043407, 35.14839],
          [-81.042758, 35.146042],
          [-81.032547, 35.109102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46009",
      state: "46",
      county: "009",
      name: "Bon Homme County",
      censusArea: 563.701,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.077108, 43.16833],
          [-97.637496, 43.16877],
          [-97.635115, 42.844984],
          [-97.686506, 42.842435],
          [-97.774456, 42.849774],
          [-97.814382, 42.861119],
          [-97.828496, 42.868797],
          [-97.84527, 42.867734],
          [-97.865695, 42.86286],
          [-97.875345, 42.858724],
          [-97.879878, 42.835395],
          [-97.888562, 42.817251],
          [-97.908983, 42.794909],
          [-97.936716, 42.775754],
          [-97.950147, 42.769619],
          [-98.000348, 42.763256],
          [-98.017228, 42.762411],
          [-98.035034, 42.764205],
          [-98.056625, 42.770781],
          [-98.157405, 42.831899],
          [-98.092782, 42.973921],
          [-98.077108, 43.16833]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46013",
      state: "46",
      county: "013",
      name: "Brown County",
      censusArea: 1712.981,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.978778, 45.935937],
          [-97.979592, 45.588483],
          [-97.981457, 45.240415],
          [-98.717759, 45.243732],
          [-98.722481, 45.243764],
          [-98.722174, 45.591242],
          [-98.725002, 45.591252],
          [-98.724375, 45.938685],
          [-98.18563, 45.936185],
          [-98.184637, 45.936183],
          [-98.070515, 45.93618],
          [-98.008202, 45.936096],
          [-98.008102, 45.936095],
          [-97.986893, 45.935961],
          [-97.978778, 45.935937]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46021",
      state: "46",
      county: "021",
      name: "Campbell County",
      censusArea: 733.677,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.430079, 45.594823],
          [-100.309013, 45.707842],
          [-100.387036, 45.869142],
          [-100.499354, 45.943632],
          [-100.462838, 45.943566],
          [-100.430597, 45.943638],
          [-100.424438, 45.943569],
          [-100.420162, 45.943533],
          [-100.410386, 45.943453],
          [-100.294126, 45.943269],
          [-100.285345, 45.94313],
          [-100.284134, 45.942951],
          [-100.275614, 45.942922],
          [-100.274762, 45.942945],
          [-100.170826, 45.942514],
          [-100.152084, 45.942486],
          [-100.14173, 45.942506],
          [-100.110339, 45.942367],
          [-100.108471, 45.942391],
          [-100.084163, 45.942301],
          [-100.06902, 45.94217],
          [-100.005486, 45.94195],
          [-99.965775, 45.941822],
          [-99.880292, 45.941672],
          [-99.880062, 45.94167],
          [-99.83868, 45.941293],
          [-99.750396, 45.940935],
          [-99.749494, 45.940956],
          [-99.749325, 45.940935],
          [-99.74787, 45.940933],
          [-99.718073, 45.940907],
          [-99.718071, 45.940907],
          [-99.717466, 45.593428],
          [-100.430079, 45.594823]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29209",
      state: "29",
      county: "209",
      name: "Stone County",
      censusArea: 464.033,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.610126, 36.99581],
          [-93.337451, 36.992494],
          [-93.342041, 36.817301],
          [-93.304359, 36.816866],
          [-93.315324, 36.498408],
          [-93.315337, 36.498408],
          [-93.394718, 36.498519],
          [-93.396079, 36.498669],
          [-93.426989, 36.498585],
          [-93.507408, 36.498911],
          [-93.514512, 36.498881],
          [-93.584281, 36.498896],
          [-93.584282, 36.498896],
          [-93.5738, 36.820032],
          [-93.611003, 36.922831],
          [-93.610126, 36.99581]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29217",
      state: "29",
      county: "217",
      name: "Vernon County",
      censusArea: 826.398,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.614055, 38.060056],
          [-94.058285, 38.036627],
          [-94.065675, 37.900987],
          [-94.073524, 37.639722],
          [-94.617575, 37.653577],
          [-94.617576, 37.653671],
          [-94.617734, 37.673105],
          [-94.617734, 37.673127],
          [-94.617885, 37.682214],
          [-94.617687, 37.686653],
          [-94.617651, 37.687671],
          [-94.617805, 37.690178],
          [-94.617975, 37.722176],
          [-94.617808, 37.729707],
          [-94.616862, 37.819456],
          [-94.61645, 37.83756],
          [-94.616426, 37.845282],
          [-94.616, 37.863126],
          [-94.615834, 37.87251],
          [-94.615921, 37.878331],
          [-94.615706, 37.886843],
          [-94.615469, 37.901775],
          [-94.615393, 37.906392],
          [-94.615181, 37.915944],
          [-94.614778, 37.9342],
          [-94.614835, 37.9367],
          [-94.614754, 37.940769],
          [-94.614612, 37.944362],
          [-94.614594, 37.949978],
          [-94.614562, 37.951517],
          [-94.614557, 37.971037],
          [-94.614465, 37.987799],
          [-94.614212, 37.992462],
          [-94.613981, 38.036949],
          [-94.613981, 38.037057],
          [-94.614055, 38.060056]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29227",
      state: "29",
      county: "227",
      name: "Worth County",
      censusArea: 266.613,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.214278, 40.384254],
          [-94.598487, 40.386714],
          [-94.632035, 40.571186],
          [-94.632032, 40.571186],
          [-94.594001, 40.570966],
          [-94.542154, 40.570809],
          [-94.541828, 40.570809],
          [-94.538318, 40.570763],
          [-94.537058, 40.570763],
          [-94.533878, 40.570739],
          [-94.48928, 40.570707],
          [-94.471213, 40.570825],
          [-94.470648, 40.57083],
          [-94.460088, 40.570947],
          [-94.429725, 40.571041],
          [-94.358307, 40.571363],
          [-94.336706, 40.571452],
          [-94.336556, 40.571475],
          [-94.324765, 40.571477],
          [-94.310724, 40.571524],
          [-94.294813, 40.571341],
          [-94.28735, 40.571521],
          [-94.23224, 40.571907],
          [-94.214278, 40.384254]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30001",
      state: "30",
      county: "001",
      name: "Beaverhead County",
      censusArea: 5541.624,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.83134, 44.38166],
          [-112.849266, 44.371038],
          [-112.875822, 44.374357],
          [-112.9703, 44.426933],
          [-113.003544, 44.450814],
          [-113.027147, 44.495915],
          [-113.05677, 44.618657],
          [-113.053529, 44.621187],
          [-113.049349, 44.62938],
          [-113.051504, 44.63695],
          [-113.101082, 44.718832],
          [-113.134617, 44.763149],
          [-113.158206, 44.780847],
          [-113.207878, 44.80804],
          [-113.247166, 44.82295],
          [-113.278382, 44.812706],
          [-113.329491, 44.788645],
          [-113.341704, 44.784853],
          [-113.353631, 44.791395],
          [-113.455071, 44.865424],
          [-113.475838, 44.894648],
          [-113.498745, 44.942314],
          [-113.494446, 44.948597],
          [-113.480836, 44.95031],
          [-113.472715, 44.948123],
          [-113.447199, 44.971924],
          [-113.445915, 44.979918],
          [-113.445751, 45.018351],
          [-113.449349, 45.045745],
          [-113.463961, 45.063194],
          [-113.47377, 45.0617],
          [-113.485278, 45.063519],
          [-113.576453, 45.130311],
          [-113.57636, 45.133771],
          [-113.566179, 45.14312],
          [-113.565979, 45.149335],
          [-113.599506, 45.191114],
          [-113.650064, 45.23471],
          [-113.674409, 45.249411],
          [-113.738729, 45.329741],
          [-113.760924, 45.406501],
          [-113.774156, 45.465372],
          [-113.76576, 45.483228],
          [-113.772915, 45.512322],
          [-113.786269, 45.521385],
          [-113.810117, 45.530924],
          [-113.813456, 45.550002],
          [-113.802009, 45.579096],
          [-113.804871, 45.589112],
          [-113.813933, 45.600559],
          [-113.835396, 45.61296],
          [-113.896681, 45.641451],
          [-113.93422, 45.682232],
          [-113.9426, 45.686362],
          [-113.697639, 45.843545],
          [-113.532405, 45.878353],
          [-113.516338, 45.938638],
          [-113.501295, 45.940807],
          [-113.395063, 45.76649],
          [-113.179791, 45.885009],
          [-113.085474, 45.860586],
          [-112.759339, 45.748209],
          [-112.687054, 45.624854],
          [-112.691417, 45.483518],
          [-112.628609, 45.440586],
          [-112.505346, 45.443048],
          [-112.457744, 45.349963],
          [-112.456847, 45.088574],
          [-112.333813, 45.088463],
          [-112.334258, 45.000616],
          [-112.191197, 45.000439],
          [-112.167103, 44.826722],
          [-112.065678, 44.825985],
          [-112.06587, 44.783752],
          [-111.781812, 44.781129],
          [-111.781713, 44.708848],
          [-111.481536, 44.709186],
          [-111.489008, 44.705553],
          [-111.516588, 44.643707],
          [-111.525764, 44.604883],
          [-111.601249, 44.55421],
          [-111.617502, 44.553035],
          [-111.631505, 44.552022],
          [-111.704218, 44.560205],
          [-111.737191, 44.54306],
          [-111.806528, 44.515459],
          [-111.843295, 44.528225],
          [-111.849293, 44.539837],
          [-111.870504, 44.564033],
          [-111.947941, 44.556776],
          [-112.078471, 44.533551],
          [-112.187115, 44.534452],
          [-112.221698, 44.543519],
          [-112.229477, 44.549494],
          [-112.235864, 44.555194],
          [-112.246665, 44.56101],
          [-112.258297, 44.564334],
          [-112.274083, 44.564334],
          [-112.299009, 44.559348],
          [-112.339721, 44.538577],
          [-112.347199, 44.520298],
          [-112.3566, 44.493127],
          [-112.358926, 44.48628],
          [-112.371294, 44.472108],
          [-112.38708, 44.460476],
          [-112.410345, 44.462968],
          [-112.473207, 44.480027],
          [-112.544224, 44.483763],
          [-112.68816, 44.49885],
          [-112.71911, 44.504344],
          [-112.781294, 44.484888],
          [-112.812087, 44.45137],
          [-112.822046, 44.434109],
          [-112.822046, 44.420831],
          [-112.823373, 44.406225],
          [-112.824701, 44.393611],
          [-112.829247, 44.385427],
          [-112.83134, 44.38166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37171",
      state: "37",
      county: "171",
      name: "Surry County",
      censusArea: 532.166,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.837954, 36.559131],
          [-80.837641, 36.559118],
          [-80.837089, 36.559154],
          [-80.744101, 36.561686],
          [-80.687539, 36.561411],
          [-80.612158, 36.558127],
          [-80.4401, 36.55063],
          [-80.451954, 36.261635],
          [-80.452379, 36.241957],
          [-80.452452, 36.241356],
          [-80.452319, 36.241222],
          [-80.547478, 36.285246],
          [-80.652865, 36.252571],
          [-80.873259, 36.236383],
          [-80.868871, 36.326516],
          [-80.967066, 36.4016],
          [-80.974619, 36.452846],
          [-80.901836, 36.561754],
          [-80.901726, 36.561751],
          [-80.837954, 36.559131]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37177",
      state: "37",
      county: "177",
      name: "Tyrrell County",
      censusArea: 389.035,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.045987, 35.666117],
          [-76.143577, 35.702741],
          [-76.214298, 35.597993],
          [-76.297845, 35.609052],
          [-76.269325, 35.690722],
          [-76.395283, 35.697469],
          [-76.405993, 35.785045],
          [-76.353444, 35.861353],
          [-76.36521, 35.944697],
          [-76.362966, 35.942197],
          [-76.340327, 35.94325],
          [-76.317687, 35.946935],
          [-76.272408, 35.972734],
          [-76.213966, 35.988002],
          [-76.176585, 35.993267],
          [-76.093697, 35.993001],
          [-76.083131, 35.989845],
          [-76.062071, 35.993004],
          [-76.024162, 35.970891],
          [-76.014685, 35.960361],
          [-76.014159, 35.957202],
          [-76.01995, 35.934036],
          [-76.011526, 35.914029],
          [-76.004682, 35.905078],
          [-75.999386, 35.903521],
          [-76.045987, 35.666117]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39067",
      state: "39",
      county: "067",
      name: "Harrison County",
      censusArea: 402.339,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.035504, 40.164874],
          [-81.225924, 40.170075],
          [-81.339565, 40.171999],
          [-81.338057, 40.214253],
          [-81.334601, 40.304316],
          [-81.275319, 40.303434],
          [-81.269951, 40.433486],
          [-80.941911, 40.4247],
          [-80.865504, 40.422929],
          [-80.882892, 40.159495],
          [-81.035504, 40.164874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39073",
      state: "39",
      county: "073",
      name: "Hocking County",
      censusArea: 421.323,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.374531, 39.654959],
          [-82.37989, 39.596738],
          [-82.159161, 39.556574],
          [-82.167379, 39.466307],
          [-82.28041, 39.472906],
          [-82.289663, 39.384195],
          [-82.514479, 39.395767],
          [-82.51758, 39.369468],
          [-82.748591, 39.368165],
          [-82.740771, 39.466029],
          [-82.740729, 39.46835],
          [-82.731518, 39.554445],
          [-82.620088, 39.563987],
          [-82.617546, 39.608605],
          [-82.496123, 39.602849],
          [-82.490329, 39.661704],
          [-82.374531, 39.654959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39079",
      state: "39",
      county: "079",
      name: "Jackson County",
      censusArea: 420.304,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.785891, 39.168769],
          [-82.766692, 39.167768],
          [-82.762892, 39.207967],
          [-82.536306, 39.180757],
          [-82.424677, 39.137972],
          [-82.435309, 39.035079],
          [-82.455179, 38.844577],
          [-82.575583, 38.844478],
          [-82.625022, 38.84754],
          [-82.626045, 38.847647],
          [-82.650039, 38.849073],
          [-82.76069, 38.854875],
          [-82.753391, 38.945373],
          [-82.80709, 38.948073],
          [-82.785891, 39.168769]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48057",
      state: "48",
      county: "057",
      name: "Calhoun County",
      censusArea: 506.84,
      stateName: "Texas"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-96.655528, 28.704167],
            [-96.664534, 28.696904],
            [-96.657918, 28.687744],
            [-96.635018, 28.668914],
            [-96.634, 28.654665],
            [-96.627893, 28.650085],
            [-96.623313, 28.649576],
            [-96.615679, 28.644487],
            [-96.61059, 28.638889],
            [-96.61059, 28.636344],
            [-96.61975, 28.627693],
            [-96.622804, 28.622095],
            [-96.611099, 28.585962],
            [-96.608045, 28.583418],
            [-96.573949, 28.584436],
            [-96.565297, 28.5824],
            [-96.564279, 28.576293],
            [-96.561226, 28.570695],
            [-96.536289, 28.559499],
            [-96.526111, 28.557972],
            [-96.52204, 28.55441],
            [-96.514406, 28.535071],
            [-96.505755, 28.525911],
            [-96.450284, 28.490796],
            [-96.419749, 28.467387],
            [-96.410589, 28.459244],
            [-96.402446, 28.449066],
            [-96.403973, 28.44245],
            [-96.46148, 28.421585],
            [-96.481836, 28.407844],
            [-96.504737, 28.397666],
            [-96.520513, 28.394104],
            [-96.542905, 28.385452],
            [-96.559699, 28.377819],
            [-96.570386, 28.368658],
            [-96.59176, 28.357462],
            [-96.600412, 28.354409],
            [-96.650794, 28.346775],
            [-96.672677, 28.335579],
            [-96.688453, 28.347284],
            [-96.69456, 28.347284],
            [-96.698122, 28.342704],
            [-96.705247, 28.348811],
            [-96.700158, 28.369676],
            [-96.705756, 28.400211],
            [-96.710336, 28.406827],
            [-96.72255, 28.408862],
            [-96.749013, 28.408862],
            [-96.762245, 28.411916],
            [-96.76696, 28.410737],
            [-96.890951, 28.507178],
            [-96.930367, 28.586728],
            [-96.853757, 28.635199],
            [-96.655528, 28.704167]
          ]
        ],
        [
          [
            [-96.323522, 28.675597],
            [-96.322921, 28.64186],
            [-96.328655, 28.640924],
            [-96.373439, 28.626675],
            [-96.376492, 28.620059],
            [-96.384635, 28.615988],
            [-96.473694, 28.57324],
            [-96.487943, 28.569677],
            [-96.482854, 28.580364],
            [-96.480309, 28.596649],
            [-96.485907, 28.607845],
            [-96.490488, 28.610899],
            [-96.510844, 28.61497],
            [-96.510335, 28.617515],
            [-96.497612, 28.625148],
            [-96.496595, 28.630746],
            [-96.499648, 28.635835],
            [-96.506264, 28.63838],
            [-96.54545, 28.645505],
            [-96.555119, 28.646013],
            [-96.563262, 28.644487],
            [-96.572931, 28.667897],
            [-96.570386, 28.674003],
            [-96.55919, 28.687235],
            [-96.559699, 28.691306],
            [-96.561226, 28.696395],
            [-96.566824, 28.697922],
            [-96.575141, 28.702837],
            [-96.428394, 28.703523],
            [-96.323522, 28.675597]
          ]
        ],
        [
          [
            [-96.83003, 28.111842],
            [-96.827049, 28.112417],
            [-96.816574, 28.119618],
            [-96.81042, 28.126034],
            [-96.810944, 28.136378],
            [-96.816836, 28.158048],
            [-96.820315, 28.163494],
            [-96.822859, 28.167476],
            [-96.818656, 28.17228],
            [-96.816443, 28.174808],
            [-96.791958, 28.188687],
            [-96.733037, 28.190913],
            [-96.703838, 28.198246],
            [-96.697422, 28.202959],
            [-96.702659, 28.211208],
            [-96.703314, 28.216446],
            [-96.686816, 28.21841],
            [-96.662462, 28.227314],
            [-96.660628, 28.228885],
            [-96.663116, 28.233206],
            [-96.651856, 28.251275],
            [-96.607992, 28.27707],
            [-96.608123, 28.280081],
            [-96.611527, 28.281391],
            [-96.61048, 28.283093],
            [-96.592934, 28.296972],
            [-96.581019, 28.30221],
            [-96.55326, 28.302341],
            [-96.546975, 28.305614],
            [-96.542131, 28.315958],
            [-96.528906, 28.322505],
            [-96.476269, 28.330754],
            [-96.450998, 28.337039],
            [-96.434108, 28.344764],
            [-96.418919, 28.354846],
            [-96.415253, 28.362833],
            [-96.417217, 28.367154],
            [-96.412896, 28.369511],
            [-96.403206, 28.371475],
            [-96.401242, 28.366892],
            [-96.397846, 28.343513],
            [-96.4137, 28.327343],
            [-96.439099, 28.319052],
            [-96.547774, 28.270798],
            [-96.621534, 28.2297],
            [-96.694666, 28.18212],
            [-96.758141, 28.136873],
            [-96.849624, 28.064939],
            [-96.853455, 28.061345],
            [-96.853912, 28.063699],
            [-96.847129, 28.070604],
            [-96.844083, 28.084351],
            [-96.837646, 28.094673],
            [-96.837736, 28.099491],
            [-96.830861, 28.108],
            [-96.83003, 28.111842]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48071",
      state: "48",
      county: "071",
      name: "Chambers County",
      censusArea: 597.137,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.370866, 29.55507],
          [-94.371828, 29.597178],
          [-94.408992, 29.597467],
          [-94.412771, 29.572612],
          [-94.511086, 29.542971],
          [-94.522421, 29.545672],
          [-94.523743, 29.545987],
          [-94.526336, 29.552634],
          [-94.532021, 29.558379],
          [-94.541108, 29.567561],
          [-94.542532, 29.569],
          [-94.546385, 29.572048],
          [-94.553988, 29.573882],
          [-94.570006, 29.572232],
          [-94.570593, 29.571878],
          [-94.570867, 29.571713],
          [-94.578211, 29.567281],
          [-94.593518, 29.561319],
          [-94.610147, 29.556947],
          [-94.618028, 29.554875],
          [-94.621819, 29.553878],
          [-94.621953, 29.553843],
          [-94.62589, 29.552808],
          [-94.682706, 29.539825],
          [-94.689353, 29.538306],
          [-94.691625, 29.537787],
          [-94.718276, 29.533547],
          [-94.740699, 29.525858],
          [-94.755514, 29.524776],
          [-94.757689, 29.524617],
          [-94.757788, 29.524626],
          [-94.757883, 29.524635],
          [-94.767246, 29.525523],
          [-94.768676, 29.525659],
          [-94.769418, 29.525988],
          [-94.769573, 29.526057],
          [-94.77084, 29.526618],
          [-94.774571, 29.528271],
          [-94.774798, 29.528372],
          [-94.779674, 29.530533],
          [-94.779708, 29.530548],
          [-94.779831, 29.530602],
          [-94.780938, 29.531093],
          [-94.783296, 29.535314],
          [-94.784034, 29.536635],
          [-94.786512, 29.541073],
          [-94.786649, 29.541319],
          [-94.78954, 29.546494],
          [-94.790605, 29.548401],
          [-94.779439, 29.549472],
          [-94.771053, 29.548439],
          [-94.755237, 29.562782],
          [-94.734626, 29.584046],
          [-94.708741, 29.625226],
          [-94.693154, 29.694453],
          [-94.692434, 29.70361],
          [-94.695317, 29.723052],
          [-94.724616, 29.774766],
          [-94.735271, 29.785433],
          [-94.740919, 29.787081],
          [-94.771512, 29.773889],
          [-94.792238, 29.767433],
          [-94.816085, 29.75671],
          [-94.851108, 29.721373],
          [-94.865007, 29.695337],
          [-94.867438, 29.678768],
          [-94.872551, 29.67125],
          [-94.893107, 29.661336],
          [-94.915413, 29.656614],
          [-94.921318, 29.658178],
          [-94.931154, 29.673874],
          [-94.908654, 29.825181],
          [-94.98146, 29.884276],
          [-94.442235, 29.889538],
          [-94.357976, 29.887464],
          [-94.354163, 29.561459],
          [-94.370866, 29.55507]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48093",
      state: "48",
      county: "093",
      name: "Comanche County",
      censusArea: 937.754,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.463736, 31.683989],
          [-98.492802, 31.723608],
          [-98.668441, 31.700514],
          [-98.924401, 32.078018],
          [-98.660316, 32.204408],
          [-98.551158, 32.261436],
          [-98.327499, 31.941261],
          [-98.251211, 31.980127],
          [-98.208386, 31.917511],
          [-98.156568, 31.841713],
          [-98.463736, 31.683989]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48097",
      state: "48",
      county: "097",
      name: "Cooke County",
      censusArea: 874.759,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.051253, 33.41995],
          [-97.383095, 33.43045],
          [-97.383015, 33.43289],
          [-97.487065, 33.433681],
          [-97.484071, 33.91568],
          [-97.458069, 33.901635],
          [-97.450954, 33.891398],
          [-97.451469, 33.87093],
          [-97.457617, 33.855126],
          [-97.461486, 33.84956],
          [-97.462857, 33.841772],
          [-97.459068, 33.834581],
          [-97.453057, 33.828536],
          [-97.444193, 33.823773],
          [-97.426493, 33.819398],
          [-97.372941, 33.819454],
          [-97.310843, 33.872461],
          [-97.226522, 33.914642],
          [-97.210921, 33.916064],
          [-97.185458, 33.9007],
          [-97.180845, 33.895204],
          [-97.166629, 33.847311],
          [-97.166824, 33.840395],
          [-97.171627, 33.835335],
          [-97.18137, 33.831375],
          [-97.194678, 33.831192],
          [-97.1997, 33.827322],
          [-97.204995, 33.81887],
          [-97.204827, 33.799908],
          [-97.172192, 33.737545],
          [-97.163149, 33.729322],
          [-97.162803, 33.729127],
          [-97.151257, 33.722608],
          [-97.13753, 33.718664],
          [-97.121102, 33.717174],
          [-97.107183, 33.721119],
          [-97.097154, 33.727809],
          [-97.092414, 33.733214],
          [-97.047972, 33.817931],
          [-97.00613, 33.861884],
          [-96.985567, 33.886522],
          [-96.97287, 33.935698],
          [-96.944611, 33.948994],
          [-96.94386, 33.41641],
          [-97.051253, 33.41995]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US24510",
      state: "24",
      county: "510",
      name: "Baltimore City County",
      lsad: "city",
      censusArea: 80.944,
      stateName: "Maryland"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.618612, 39.237398],
          [-76.631132, 39.242899],
          [-76.632589, 39.243406],
          [-76.637267, 39.245648],
          [-76.639428, 39.246568],
          [-76.644212, 39.248924],
          [-76.647256, 39.25006],
          [-76.651491, 39.251983],
          [-76.653393, 39.252703],
          [-76.665567, 39.257982],
          [-76.667349, 39.258858],
          [-76.673432, 39.261281],
          [-76.678801, 39.263997],
          [-76.681644, 39.265032],
          [-76.685153, 39.266697],
          [-76.686048, 39.266885],
          [-76.688667, 39.268195],
          [-76.711054, 39.277889],
          [-76.711186, 39.354383],
          [-76.711342, 39.361225],
          [-76.711191, 39.362771],
          [-76.711313, 39.371933],
          [-76.609778, 39.372202],
          [-76.529793, 39.372058],
          [-76.529785, 39.356552],
          [-76.529786, 39.310391],
          [-76.529786, 39.308534],
          [-76.529697, 39.307717],
          [-76.529693, 39.306474],
          [-76.529743, 39.305964],
          [-76.529686, 39.305506],
          [-76.529685, 39.304661],
          [-76.529611, 39.302911],
          [-76.529775, 39.298731],
          [-76.529624, 39.298179],
          [-76.52951, 39.29718],
          [-76.529793, 39.292783],
          [-76.529708, 39.291895],
          [-76.529689, 39.290993],
          [-76.529499, 39.290168],
          [-76.529552, 39.289337],
          [-76.529681, 39.288152],
          [-76.529592, 39.284852],
          [-76.529663, 39.284203],
          [-76.529934, 39.283082],
          [-76.529787, 39.281872],
          [-76.529781, 39.273101],
          [-76.529623, 39.272307],
          [-76.529486, 39.218408],
          [-76.528587, 39.217492],
          [-76.529649, 39.216693],
          [-76.534285, 39.213208],
          [-76.53496, 39.21228],
          [-76.535885, 39.211008],
          [-76.533103, 39.20763],
          [-76.550086, 39.197207],
          [-76.582386, 39.207151],
          [-76.611695, 39.2344],
          [-76.618612, 39.237398]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45063",
      state: "45",
      county: "063",
      name: "Lexington County",
      censusArea: 698.913,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.338961, 34.197587],
          [-81.275845, 34.097381],
          [-81.153617, 34.07795],
          [-81.059715, 34.005253],
          [-81.01233, 33.88008],
          [-81.058521, 33.747053],
          [-80.926321, 33.755583],
          [-81.042854, 33.707131],
          [-81.086803, 33.695315],
          [-81.187271, 33.652937],
          [-81.415308, 33.732161],
          [-81.522124, 33.825522],
          [-81.52286, 33.826967],
          [-81.527148, 33.829799],
          [-81.53359, 33.832154],
          [-81.534438, 33.837454],
          [-81.538291, 33.841496],
          [-81.538679, 33.844531],
          [-81.540866, 33.845608],
          [-81.542263, 33.848407],
          [-81.540865, 33.851915],
          [-81.546946, 33.858683],
          [-81.551552, 33.860657],
          [-81.554531, 33.860547],
          [-81.560733, 33.862645],
          [-81.563671, 33.865324],
          [-81.569149, 33.871845],
          [-81.571485, 33.87723],
          [-81.471972, 34.076552],
          [-81.388564, 34.080227],
          [-81.437172, 34.129963],
          [-81.338961, 34.197587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45067",
      state: "45",
      county: "067",
      name: "Marion County",
      censusArea: 489.227,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.544036, 34.219676],
          [-79.54404, 34.225186],
          [-79.54793, 34.230913],
          [-79.480712, 34.288081],
          [-79.380264, 34.300572],
          [-79.128165, 34.253652],
          [-79.135118, 34.241458],
          [-79.126681, 34.235362],
          [-79.339076, 33.969722],
          [-79.190264, 33.705787],
          [-79.199432, 33.710101],
          [-79.195161, 33.703438],
          [-79.208955, 33.703126],
          [-79.235068, 33.69425],
          [-79.23888, 33.694417],
          [-79.246384, 33.695347],
          [-79.249309, 33.696975],
          [-79.249581, 33.699302],
          [-79.247339, 33.700817],
          [-79.242086, 33.701515],
          [-79.240644, 33.702598],
          [-79.240279, 33.703728],
          [-79.241463, 33.705397],
          [-79.243348, 33.706114],
          [-79.244897, 33.705717],
          [-79.24977, 33.703424],
          [-79.258107, 33.702742],
          [-79.261467, 33.703617],
          [-79.267586, 33.703399],
          [-79.267863, 33.704375],
          [-79.265102, 33.706001],
          [-79.264683, 33.707364],
          [-79.265011, 33.710275],
          [-79.266927, 33.710979],
          [-79.26925, 33.710893],
          [-79.271676, 33.708557],
          [-79.273306, 33.708116],
          [-79.275057, 33.708852],
          [-79.274694, 33.711077],
          [-79.274935, 33.714319],
          [-79.271326, 33.71571],
          [-79.269411, 33.717788],
          [-79.269597, 33.719893],
          [-79.271664, 33.721855],
          [-79.278604, 33.718226],
          [-79.280518, 33.716324],
          [-79.282995, 33.715972],
          [-79.286441, 33.717597],
          [-79.28717, 33.718807],
          [-79.288312, 33.723526],
          [-79.291574, 33.728981],
          [-79.29139, 33.732392],
          [-79.289351, 33.734349],
          [-79.286157, 33.735701],
          [-79.286248, 33.737386],
          [-79.290436, 33.739116],
          [-79.293978, 33.741144],
          [-79.294366, 33.743757],
          [-79.29291, 33.747135],
          [-79.292578, 33.750167],
          [-79.293312, 33.75228],
          [-79.294779, 33.754324],
          [-79.298706, 33.757025],
          [-79.300613, 33.75883],
          [-79.30468, 33.763449],
          [-79.306679, 33.76623],
          [-79.30883, 33.771973],
          [-79.313573, 33.775846],
          [-79.316294, 33.778554],
          [-79.317041, 33.779878],
          [-79.319664, 33.785434],
          [-79.323081, 33.789247],
          [-79.320443, 33.790511],
          [-79.323988, 33.794965],
          [-79.324317, 33.799123],
          [-79.482549, 33.937537],
          [-79.515308, 34.097207],
          [-79.568984, 34.175087],
          [-79.544036, 34.219676]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45077",
      state: "45",
      county: "077",
      name: "Pickens County",
      censusArea: 496.407,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.764464, 35.068177],
          [-82.588598, 35.073973],
          [-82.537866, 35.015696],
          [-82.486746, 34.819475],
          [-82.788664, 34.672017],
          [-82.840253, 34.623212],
          [-82.900144, 34.74244],
          [-82.915785, 34.971836],
          [-82.897499, 35.056021],
          [-82.78113, 35.084585],
          [-82.781062, 35.084492],
          [-82.777407, 35.076885],
          [-82.779116, 35.073674],
          [-82.780546, 35.069043],
          [-82.781973, 35.066817],
          [-82.764464, 35.068177]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26053",
      state: "26",
      county: "053",
      name: "Gogebic County",
      censusArea: 1101.849,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.990885, 46.09733],
          [-89.058745, 46.124952],
          [-89.125136, 46.144531],
          [-89.161757, 46.151816],
          [-89.166887, 46.152868],
          [-89.194508, 46.157942],
          [-89.201283, 46.159426],
          [-89.203289, 46.16002],
          [-89.205657, 46.160408],
          [-89.218156, 46.162988],
          [-89.219964, 46.163319],
          [-89.276489, 46.174047],
          [-89.276883, 46.174116],
          [-89.908196, 46.296037],
          [-89.90991, 46.296402],
          [-89.929093, 46.300087],
          [-90.120489, 46.336852],
          [-90.119691, 46.359755],
          [-90.119757, 46.359748],
          [-90.134663, 46.374947],
          [-90.134656, 46.374979],
          [-90.166909, 46.439311],
          [-90.166919, 46.439851],
          [-90.17786, 46.440548],
          [-90.204572, 46.478998],
          [-90.284545, 46.518714],
          [-90.307716, 46.518392],
          [-90.313839, 46.516199],
          [-90.313894, 46.516199],
          [-90.357676, 46.540271],
          [-90.374461, 46.539212],
          [-90.400041, 46.544384],
          [-90.400429, 46.544384],
          [-90.414464, 46.55732],
          [-90.414596, 46.55732],
          [-90.418136, 46.566094],
          [-90.417943, 46.56619],
          [-90.398478, 46.575832],
          [-90.39735, 46.576391],
          [-90.39731, 46.57641],
          [-90.396772, 46.576677],
          [-90.394936, 46.577586],
          [-90.371717, 46.589088],
          [-90.355341, 46.5972],
          [-90.353284, 46.598219],
          [-90.348407, 46.600635],
          [-90.347801, 46.600842],
          [-90.327626, 46.607744],
          [-90.327604, 46.607739],
          [-90.306609, 46.602741],
          [-90.29546, 46.606998],
          [-90.28455, 46.611164],
          [-90.284189, 46.611302],
          [-90.28315, 46.611698],
          [-90.279723, 46.613007],
          [-90.278287, 46.613555],
          [-90.265294, 46.618516],
          [-90.265291, 46.618517],
          [-90.251669, 46.621454],
          [-90.250176, 46.621776],
          [-90.237609, 46.624485],
          [-90.164026, 46.645515],
          [-90.100695, 46.655132],
          [-90.04542, 46.668272],
          [-90.028392, 46.67439],
          [-89.996034, 46.693225],
          [-89.985817, 46.70319],
          [-89.973803, 46.710322],
          [-89.957101, 46.716929],
          [-89.918466, 46.740324],
          [-89.907516, 46.74987],
          [-89.892355, 46.763088],
          [-89.888687, 46.765826],
          [-89.887721, 46.765825],
          [-89.864481, 46.592765],
          [-89.74026, 46.592147],
          [-89.74146, 46.505247],
          [-89.365347, 46.505645],
          [-89.365408, 46.332963],
          [-88.990588, 46.333251],
          [-88.990885, 46.09733]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46027",
      state: "46",
      county: "027",
      name: "Clay County",
      censusArea: 412.185,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.805682, 43.08367],
          [-96.806213, 42.704154],
          [-96.806553, 42.703405],
          [-96.813148, 42.706397],
          [-96.906797, 42.7338],
          [-96.920494, 42.731432],
          [-96.961021, 42.738075],
          [-97.015691, 42.758697],
          [-97.026103, 42.762625],
          [-97.05218, 42.770187],
          [-97.13746, 42.782313],
          [-97.163082, 42.793385],
          [-97.160353, 42.799701],
          [-97.160543, 43.083145],
          [-96.924142, 43.083733],
          [-96.805682, 43.08367]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46031",
      state: "46",
      county: "031",
      name: "Corson County",
      censusArea: 2469.692,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.341924, 45.472904],
          [-101.261796, 45.472755],
          [-101.47019, 45.472416],
          [-101.99979, 45.472414],
          [-102.000679, 45.944515],
          [-102.000656, 45.944515],
          [-102.000425, 45.944581],
          [-101.998703, 45.944557],
          [-101.998618, 45.944556],
          [-101.992187, 45.944471],
          [-101.989501, 45.944472],
          [-101.973749, 45.944456],
          [-101.957439, 45.944484],
          [-101.886838, 45.944559],
          [-101.852642, 45.944457],
          [-101.832991, 45.944464],
          [-101.794606, 45.944397],
          [-101.790054, 45.944442],
          [-101.766177, 45.944322],
          [-101.765293, 45.944367],
          [-101.764277, 45.944412],
          [-101.758611, 45.944478],
          [-101.730069, 45.944356],
          [-101.72338, 45.944187],
          [-101.708785, 45.944348],
          [-101.681819, 45.944444],
          [-101.680574, 45.944329],
          [-101.657631, 45.944387],
          [-101.628597, 45.944293],
          [-101.562156, 45.944237],
          [-101.557276, 45.9441],
          [-101.373769, 45.944265],
          [-101.37069, 45.944198],
          [-101.365283, 45.944092],
          [-101.333871, 45.944166],
          [-101.313272, 45.944164],
          [-101.287223, 45.944107],
          [-101.271524, 45.944209],
          [-101.224006, 45.944025],
          [-101.203787, 45.943895],
          [-101.179103, 45.943896],
          [-101.175693, 45.943983],
          [-101.171074, 45.943959],
          [-101.163241, 45.943915],
          [-101.146076, 45.943842],
          [-101.142571, 45.943841],
          [-101.106826, 45.943984],
          [-101.101334, 45.943841],
          [-100.980693, 45.944068],
          [-100.976565, 45.943864],
          [-100.964411, 45.943822],
          [-100.938989, 45.943848],
          [-100.935582, 45.943757],
          [-100.890176, 45.943861],
          [-100.769751, 45.943766],
          [-100.76211, 45.943767],
          [-100.762072, 45.943803],
          [-100.750407, 45.943649],
          [-100.720865, 45.944024],
          [-100.65082, 45.94368],
          [-100.627681, 45.943642],
          [-100.511949, 45.943654],
          [-100.511793, 45.943654],
          [-100.499354, 45.943632],
          [-100.387036, 45.869142],
          [-100.309013, 45.707842],
          [-100.430079, 45.594823],
          [-100.486561, 45.536521],
          [-100.341924, 45.472904]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46047",
      state: "46",
      county: "047",
      name: "Fall River County",
      censusArea: 1739.915,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.054779, 43.477815],
          [-103.935373, 43.478824],
          [-103.000785, 43.476847],
          [-103.000897, 43.000474],
          [-103.13174, 43.000783],
          [-103.132955, 43.000784],
          [-103.340829, 43.000879],
          [-103.404579, 43.000737],
          [-103.5051, 43.00077],
          [-103.505219, 43.00077],
          [-103.506151, 43.000771],
          [-103.506556, 43.000771],
          [-103.576329, 43.000807],
          [-103.576966, 43.000746],
          [-103.813939, 43.001378],
          [-103.815573, 43.001279],
          [-104.053127, 43.000585],
          [-104.053876, 43.289801],
          [-104.053884, 43.297047],
          [-104.054218, 43.30437],
          [-104.054403, 43.325914],
          [-104.054614, 43.390949],
          [-104.054766, 43.428914],
          [-104.054779, 43.477815]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46053",
      state: "46",
      county: "053",
      name: "Gregory County",
      censusArea: 1014.957,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.534482, 43.499648],
          [-99.529121, 43.499783],
          [-99.297998, 43.499669],
          [-99.304332, 43.460386],
          [-99.152889, 43.426908],
          [-99.069285, 43.320688],
          [-98.896783, 43.246909],
          [-98.868762, 43.162009],
          [-98.773406, 43.145919],
          [-98.69046, 43.076556],
          [-98.524871, 43.039347],
          [-98.49855, 42.99856],
          [-98.565072, 42.9984],
          [-98.568936, 42.998537],
          [-98.663712, 42.998444],
          [-98.665613, 42.998536],
          [-98.742394, 42.998343],
          [-98.764378, 42.998323],
          [-98.801304, 42.998241],
          [-98.823989, 42.99831],
          [-98.899944, 42.998122],
          [-98.903154, 42.998306],
          [-98.919136, 42.998242],
          [-98.919234, 42.998241],
          [-98.962081, 42.998286],
          [-99.00037, 42.998273],
          [-99.021909, 42.998365],
          [-99.0223, 42.998237],
          [-99.080011, 42.998357],
          [-99.08188, 42.998288],
          [-99.135961, 42.998301],
          [-99.139045, 42.998508],
          [-99.151143, 42.998344],
          [-99.161388, 42.998465],
          [-99.195199, 42.998107],
          [-99.234462, 42.998281],
          [-99.254297, 42.998138],
          [-99.254454, 42.99814],
          [-99.26271, 42.998234],
          [-99.288045, 42.998152],
          [-99.347283, 42.998217],
          [-99.368628, 42.99814],
          [-99.371121, 42.998093],
          [-99.374268, 42.998047],
          [-99.395568, 42.99817],
          [-99.471353, 42.997967],
          [-99.474531, 42.998081],
          [-99.490798, 42.998143],
          [-99.494287, 42.998118],
          [-99.534049, 42.998041],
          [-99.534482, 43.499648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27007",
      state: "27",
      county: "007",
      name: "Beltrami County",
      censusArea: 2504.94,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.41904, 47.758929],
          [-94.416279, 47.444828],
          [-94.416387, 47.41183],
          [-94.670214, 47.410151],
          [-94.718396, 47.409542],
          [-94.760529, 47.410452],
          [-94.78893, 47.40979],
          [-94.799934, 47.409848],
          [-95.183216, 47.412773],
          [-95.193705, 47.872544],
          [-95.277834, 47.925346],
          [-95.229133, 48.019958],
          [-95.582887, 48.020557],
          [-95.592487, 48.020558],
          [-95.593788, 48.172916],
          [-95.602315, 48.538887],
          [-95.586733, 48.538533],
          [-95.34254, 48.540211],
          [-95.211966, 48.539961],
          [-95.210953, 48.365971],
          [-94.428702, 48.367333],
          [-94.425771, 48.367339],
          [-94.418543, 47.845816],
          [-94.41904, 47.758929]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27015",
      state: "27",
      county: "015",
      name: "Brown County",
      censusArea: 611.09,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.78063, 44.45665],
          [-94.666062, 44.394048],
          [-94.522958, 44.365391],
          [-94.371731, 44.264448],
          [-94.36885, 44.108698],
          [-94.859803, 44.108016],
          [-95.100221, 44.108034],
          [-95.108777, 44.195044],
          [-95.10748, 44.281768],
          [-94.866259, 44.282372],
          [-94.865824, 44.498218],
          [-94.78063, 44.45665]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27021",
      state: "27",
      county: "021",
      name: "Cass County",
      censusArea: 2021.542,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.341679, 46.277705],
          [-94.42478, 46.331765],
          [-94.534724, 46.302797],
          [-94.653462, 46.348678],
          [-94.722769, 46.352321],
          [-94.731442, 46.368605],
          [-94.779121, 46.394215],
          [-94.787126, 46.803943],
          [-94.659942, 46.803716],
          [-94.670214, 47.410151],
          [-94.416387, 47.41183],
          [-94.416279, 47.444828],
          [-94.327531, 47.415844],
          [-94.095115, 47.477972],
          [-94.027858, 47.408633],
          [-94.027108, 47.352277],
          [-93.93327, 47.304639],
          [-93.77401, 47.30506],
          [-93.80882, 47.232622],
          [-93.77393, 47.211335],
          [-93.775257, 47.030418],
          [-93.776024, 46.802891],
          [-94.342895, 46.80553],
          [-94.332523, 46.279566],
          [-94.339351, 46.284649],
          [-94.341679, 46.277705]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27027",
      state: "27",
      county: "027",
      name: "Clay County",
      censusArea: 1045.366,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.783003, 46.630405],
          [-96.783009, 46.630418],
          [-96.783014, 46.630647],
          [-96.784205, 46.686768],
          [-96.802013, 46.812464],
          [-96.767458, 46.905163],
          [-96.780258, 46.928263],
          [-96.791558, 46.934264],
          [-96.823222, 46.970861],
          [-96.824598, 46.993309],
          [-96.82318, 46.999965],
          [-96.824807, 47.124968],
          [-96.826655, 47.150539],
          [-96.194671, 47.151149],
          [-96.174108, 46.977375],
          [-96.174587, 46.717854],
          [-96.17227, 46.630709],
          [-96.280977, 46.630765],
          [-96.783003, 46.630405]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46123",
      state: "46",
      county: "123",
      name: "Tripp County",
      censusArea: 1612.446,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.534482, 43.499648],
          [-99.534049, 42.998041],
          [-99.535375, 42.998038],
          [-99.569277, 42.997995],
          [-99.699234, 42.99788],
          [-99.701446, 42.997994],
          [-99.719177, 42.997899],
          [-99.726788, 42.997892],
          [-99.743138, 42.997912],
          [-99.768524, 42.998125],
          [-99.788247, 42.998016],
          [-99.800306, 42.997972],
          [-99.803328, 42.998064],
          [-99.809373, 42.998178],
          [-99.821868, 42.997995],
          [-99.850037, 42.998171],
          [-99.859945, 42.997962],
          [-99.869885, 42.998094],
          [-99.877697, 42.998094],
          [-99.918401, 42.998057],
          [-99.927645, 42.998113],
          [-99.950411, 42.998286],
          [-99.950921, 42.998291],
          [-99.961204, 42.998335],
          [-100.004757, 42.998392],
          [-100.027815, 42.998424],
          [-100.034389, 42.998425],
          [-100.119297, 42.998689],
          [-100.126427, 42.99871],
          [-100.126896, 42.998711],
          [-100.198412, 42.998542],
          [-100.198413, 42.998542],
          [-100.214221, 43.390321],
          [-100.230845, 43.713856],
          [-100.115169, 43.685434],
          [-100.016612, 43.706746],
          [-99.981465, 43.687119],
          [-99.837825, 43.689071],
          [-99.729978, 43.718827],
          [-99.688111, 43.759074],
          [-99.667613, 43.730241],
          [-99.656181, 43.765925],
          [-99.623587, 43.742414],
          [-99.623578, 43.499859],
          [-99.534482, 43.499648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46135",
      state: "46",
      county: "135",
      name: "Yankton County",
      censusArea: 521.164,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.637496, 43.16877],
          [-97.399191, 43.169419],
          [-97.160544, 43.169978],
          [-97.160543, 43.083145],
          [-97.160353, 42.799701],
          [-97.163082, 42.793385],
          [-97.190135, 42.805076],
          [-97.218269, 42.829561],
          [-97.257089, 42.853854],
          [-97.328511, 42.859501],
          [-97.42319, 42.861168],
          [-97.485376, 42.854838],
          [-97.531867, 42.850105],
          [-97.635115, 42.844984],
          [-97.637496, 43.16877]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30011",
      state: "30",
      county: "011",
      name: "Carter County",
      censusArea: 3340.752,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.040274, 45.212891],
          [-104.040128, 44.999987],
          [-104.057698, 44.997431],
          [-104.470117, 44.998453],
          [-104.470422, 44.998453],
          [-104.663882, 44.998869],
          [-104.665171, 44.998618],
          [-104.759855, 44.999066],
          [-104.765063, 44.999183],
          [-105.01824, 45.000437],
          [-105.019284, 45.000329],
          [-105.025266, 45.00029],
          [-105.038405, 45.000345],
          [-105.038235, 45.352052],
          [-104.987143, 45.352023],
          [-104.982036, 45.787009],
          [-104.939228, 45.786959],
          [-104.939396, 46.136589],
          [-104.88584, 46.136693],
          [-104.51584, 46.136344],
          [-104.51626, 46.054611],
          [-104.39029, 46.054964],
          [-104.390877, 45.970305],
          [-104.134716, 45.968946],
          [-104.134663, 45.882036],
          [-104.04403, 45.881971],
          [-104.044009, 45.871974],
          [-104.043814, 45.868385],
          [-104.041937, 45.557915],
          [-104.041647, 45.550691],
          [-104.041717, 45.539122],
          [-104.041145, 45.503367],
          [-104.041274, 45.499994],
          [-104.041764, 45.490789],
          [-104.04041, 45.393474],
          [-104.040114, 45.374214],
          [-104.040265, 45.345356],
          [-104.040358, 45.335946],
          [-104.040274, 45.212891]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30017",
      state: "30",
      county: "017",
      name: "Custer County",
      censusArea: 3783.362,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.982036, 45.787009],
          [-106.191861, 45.7886],
          [-106.191438, 46.137019],
          [-106.153268, 46.137194],
          [-106.155265, 46.48347],
          [-106.120415, 46.483591],
          [-106.120909, 46.83131],
          [-106.086335, 46.831269],
          [-106.086504, 46.845968],
          [-106.08667, 46.860264],
          [-105.617595, 46.83209],
          [-105.618076, 46.745025],
          [-105.576256, 46.744935],
          [-105.575683, 46.657889],
          [-105.491952, 46.657985],
          [-105.449006, 46.570916],
          [-105.239175, 46.570089],
          [-105.239439, 46.541005],
          [-104.984714, 46.540606],
          [-104.984754, 46.56945],
          [-104.858857, 46.569114],
          [-104.858698, 46.612479],
          [-104.732853, 46.612672],
          [-104.733077, 46.482239],
          [-104.905878, 46.48218],
          [-104.88584, 46.136693],
          [-104.939396, 46.136589],
          [-104.939228, 45.786959],
          [-104.982036, 45.787009]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30025",
      state: "30",
      county: "025",
      name: "Fallon County",
      censusArea: 1620.772,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.045443, 45.94531],
          [-104.04403, 45.881975],
          [-104.04403, 45.881971],
          [-104.134663, 45.882036],
          [-104.134716, 45.968946],
          [-104.390877, 45.970305],
          [-104.39029, 46.054964],
          [-104.51626, 46.054611],
          [-104.51584, 46.136344],
          [-104.88584, 46.136693],
          [-104.905878, 46.48218],
          [-104.733077, 46.482239],
          [-104.732853, 46.612672],
          [-104.732843, 46.656264],
          [-104.607017, 46.684926],
          [-104.417782, 46.684892],
          [-104.353874, 46.641446],
          [-104.045271, 46.641443],
          [-104.045335, 46.540929],
          [-104.045462, 46.341895],
          [-104.045469, 46.324545],
          [-104.045633, 46.280188],
          [-104.04667, 46.000001],
          [-104.045443, 45.94531]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30027",
      state: "30",
      county: "027",
      name: "Fergus County",
      censusArea: 4339.803,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.891022, 47.734407],
          [-108.771619, 47.645523],
          [-108.312615, 47.583452],
          [-108.314955, 47.314908],
          [-108.590901, 47.306262],
          [-108.590492, 47.270463],
          [-108.719223, 47.270267],
          [-108.736711, 47.098196],
          [-108.608546, 47.097826],
          [-108.63095, 46.749414],
          [-109.009802, 46.750432],
          [-109.388963, 46.751009],
          [-109.389163, 46.693661],
          [-109.746006, 46.693744],
          [-109.756279, 46.780416],
          [-109.835424, 46.809133],
          [-109.755404, 46.896214],
          [-109.756905, 47.185574],
          [-109.872449, 47.185462],
          [-109.872395, 47.228976],
          [-110.000131, 47.22886],
          [-110.000337, 47.272244],
          [-110.127913, 47.272017],
          [-110.127952, 47.358973],
          [-110.213418, 47.359107],
          [-110.213293, 47.416659],
          [-110.159419, 47.415338],
          [-109.850627, 47.539301],
          [-109.836811, 47.712253],
          [-109.538454, 47.738894],
          [-109.425315, 47.720153],
          [-109.274171, 47.793239],
          [-108.942745, 47.791824],
          [-108.891022, 47.734407]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30033",
      state: "30",
      county: "033",
      name: "Garfield County",
      censusArea: 4675.356,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.908907, 47.450638],
          [-107.75931, 47.552006],
          [-107.695788, 47.628756],
          [-107.600981, 47.650011],
          [-107.450151, 47.622077],
          [-107.414886, 47.691677],
          [-107.247694, 47.6614],
          [-107.010862, 47.66314],
          [-106.891324, 47.676607],
          [-106.863822, 47.736508],
          [-106.751016, 47.729708],
          [-106.711813, 47.771809],
          [-106.609705, 47.77751],
          [-106.445793, 47.983016],
          [-106.418567, 47.957414],
          [-106.358288, 47.957216],
          [-106.358288, 47.868412],
          [-106.261082, 47.868312],
          [-106.260585, 47.529],
          [-106.298087, 47.5289],
          [-106.298189, 47.354391],
          [-106.170675, 47.354708],
          [-106.170434, 47.18093],
          [-106.086777, 47.180938],
          [-106.08667, 46.860264],
          [-106.086504, 46.845968],
          [-106.720258, 46.845251],
          [-106.720027, 46.859603],
          [-107.466813, 46.860486],
          [-107.892117, 46.851537],
          [-107.939113, 46.876459],
          [-107.882588, 47.000151],
          [-107.919544, 47.017133],
          [-107.968483, 47.250033],
          [-107.962875, 47.354368],
          [-107.908907, 47.450638]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30039",
      state: "30",
      county: "039",
      name: "Granite County",
      censusArea: 1727.415,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.827831, 46.661023],
          [-113.667225, 46.658426],
          [-113.667726, 46.716026],
          [-113.479817, 46.744929],
          [-113.479917, 46.80273],
          [-113.303005, 46.832132],
          [-113.115791, 46.71359],
          [-113.038691, 46.736132],
          [-113.036834, 46.266822],
          [-113.03716, 46.223078],
          [-113.284652, 46.222761],
          [-113.274361, 46.059484],
          [-113.517481, 45.939898],
          [-113.648219, 45.965062],
          [-113.744524, 46.048807],
          [-113.803099, 46.0373],
          [-113.753246, 46.1128],
          [-113.821129, 46.212549],
          [-113.750143, 46.27259],
          [-113.875931, 46.508576],
          [-113.813152, 46.588119],
          [-113.827831, 46.661023]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30045",
      state: "30",
      county: "045",
      name: "Judith Basin County",
      censusArea: 1869.821,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.639564, 47.416259],
          [-110.213293, 47.416659],
          [-110.213418, 47.359107],
          [-110.127952, 47.358973],
          [-110.127913, 47.272017],
          [-110.000337, 47.272244],
          [-110.000131, 47.22886],
          [-109.872395, 47.228976],
          [-109.872449, 47.185462],
          [-109.756905, 47.185574],
          [-109.755404, 46.896214],
          [-109.835424, 46.809133],
          [-109.756279, 46.780416],
          [-109.746006, 46.693744],
          [-109.969223, 46.752655],
          [-110.273374, 46.71064],
          [-110.457163, 46.67872],
          [-110.620062, 46.771547],
          [-110.652877, 46.823626],
          [-110.645353, 47.095735],
          [-110.772254, 47.095871],
          [-110.767125, 47.359128],
          [-110.63928, 47.358998],
          [-110.639564, 47.416259]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31131",
      state: "31",
      county: "131",
      name: "Otoe County",
      censusArea: 615.631,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.709974, 40.523798],
          [-96.067012, 40.523148],
          [-96.455766, 40.522803],
          [-96.463632, 40.523013],
          [-96.463764, 40.78396],
          [-95.854172, 40.784012],
          [-95.861695, 40.762871],
          [-95.872281, 40.758349],
          [-95.881529, 40.750611],
          [-95.88669, 40.742101],
          [-95.888907, 40.731855],
          [-95.883178, 40.717579],
          [-95.842801, 40.677496],
          [-95.822913, 40.66724],
          [-95.795489, 40.662384],
          [-95.786568, 40.657253],
          [-95.776251, 40.647463],
          [-95.748626, 40.603355],
          [-95.750053, 40.597052],
          [-95.753148, 40.59284],
          [-95.765645, 40.585208],
          [-95.773549, 40.578205],
          [-95.774704, 40.573574],
          [-95.769281, 40.536656],
          [-95.76692, 40.531563],
          [-95.75711, 40.52599],
          [-95.74868, 40.524275],
          [-95.709974, 40.523798]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25005",
      state: "25",
      county: "005",
      name: "Bristol County",
      censusArea: 553.096,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-70.821001, 41.587268],
            [-70.821743, 41.583656],
            [-70.82191, 41.582841],
            [-70.830087, 41.585385],
            [-70.837632, 41.595374],
            [-70.838147, 41.596056],
            [-70.838452, 41.59646],
            [-70.834529, 41.60261],
            [-70.832044, 41.606504],
            [-70.831802, 41.606272],
            [-70.828025, 41.602666],
            [-70.827398, 41.602067],
            [-70.823735, 41.598569],
            [-70.820918, 41.587673],
            [-70.821001, 41.587268]
          ]
        ],
        [
          [
            [-70.854232, 41.618429],
            [-70.854211, 41.618302],
            [-70.853445, 41.613592],
            [-70.850181, 41.593529],
            [-70.85222, 41.589223],
            [-70.852488, 41.588658],
            [-70.852551, 41.588526],
            [-70.853121, 41.587321],
            [-70.85324, 41.587332],
            [-70.857239, 41.587705],
            [-70.862852, 41.600678],
            [-70.862998, 41.601014],
            [-70.863486, 41.602143],
            [-70.868501, 41.613733],
            [-70.868904, 41.614664],
            [-70.86836, 41.622664],
            [-70.869624, 41.625608],
            [-70.872665, 41.627816],
            [-70.87904, 41.629777],
            [-70.887643, 41.632422],
            [-70.889209, 41.632904],
            [-70.88926, 41.632875],
            [-70.889594, 41.632685],
            [-70.904513, 41.624205],
            [-70.905765, 41.623494],
            [-70.913202, 41.619266],
            [-70.904522, 41.610361],
            [-70.899981, 41.593504],
            [-70.901381, 41.592504],
            [-70.910814, 41.595506],
            [-70.916581, 41.607483],
            [-70.920074, 41.61081],
            [-70.927172, 41.611253],
            [-70.929722, 41.609479],
            [-70.93, 41.600441],
            [-70.927393, 41.594064],
            [-70.931338, 41.5842],
            [-70.937978, 41.577416],
            [-70.941588, 41.581034],
            [-70.946911, 41.581089],
            [-70.948797, 41.579038],
            [-70.9473, 41.573659],
            [-70.93783, 41.565239],
            [-70.931545, 41.540169],
            [-70.941785, 41.540121],
            [-70.979225, 41.530427],
            [-70.983354, 41.520616],
            [-71.003275, 41.511912],
            [-71.019354, 41.508857],
            [-71.023523, 41.506326],
            [-71.035514, 41.499047],
            [-71.058418, 41.505967],
            [-71.085663, 41.509292],
            [-71.12057, 41.497448],
            [-71.133353, 41.629509],
            [-71.133608, 41.632152],
            [-71.133796, 41.63409],
            [-71.134484, 41.641198],
            [-71.134478, 41.641262],
            [-71.134688, 41.660502],
            [-71.135188, 41.660502],
            [-71.14587, 41.662795],
            [-71.153989, 41.664102],
            [-71.17609, 41.668102],
            [-71.17609, 41.668502],
            [-71.17599, 41.671402],
            [-71.18129, 41.672502],
            [-71.191175, 41.674292],
            [-71.191178, 41.674216],
            [-71.19439, 41.674802],
            [-71.19564, 41.67509],
            [-71.224798, 41.710498],
            [-71.261392, 41.752301],
            [-71.31779, 41.776099],
            [-71.317795, 41.776101],
            [-71.327896, 41.780501],
            [-71.339297, 41.8065],
            [-71.339597, 41.832],
            [-71.337597, 41.8337],
            [-71.339298, 41.893399],
            [-71.339298, 41.893599],
            [-71.352699, 41.896699],
            [-71.354699, 41.896499],
            [-71.362499, 41.895599],
            [-71.364699, 41.895399],
            [-71.365399, 41.895299],
            [-71.370999, 41.894599],
            [-71.373799, 41.894399],
            [-71.3766, 41.893999],
            [-71.3817, 41.922699],
            [-71.3816, 41.922899],
            [-71.381401, 41.964799],
            [-71.381501, 41.966699],
            [-71.381466, 41.984998],
            [-71.364887, 41.985173],
            [-71.288263, 42.014379],
            [-71.288265, 42.014451],
            [-71.288265, 42.014462],
            [-71.168681, 42.061161],
            [-71.138641, 42.072761],
            [-71.138619, 42.072813],
            [-71.080192, 42.095699],
            [-71.054718, 41.985057],
            [-71.049498, 41.96315],
            [-70.999705, 41.929592],
            [-71.03657, 41.816525],
            [-70.921782, 41.791244],
            [-70.843177, 41.628487],
            [-70.843522, 41.62866],
            [-70.843528, 41.628663],
            [-70.844165, 41.628983],
            [-70.852518, 41.626919],
            [-70.855031, 41.624283],
            [-70.855162, 41.624145],
            [-70.854232, 41.618429]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25009",
      state: "25",
      county: "009",
      name: "Essex County",
      censusArea: 492.563,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.208137, 42.743273],
          [-71.181803, 42.73759],
          [-71.186104, 42.790689],
          [-71.165603, 42.808689],
          [-71.149703, 42.815489],
          [-71.113858, 42.827865],
          [-71.058407, 42.847009],
          [-71.037901, 42.854089],
          [-70.996901, 42.864589],
          [-70.931699, 42.884189],
          [-70.930799, 42.884589],
          [-70.914899, 42.886589],
          [-70.914886, 42.886564],
          [-70.902768, 42.88653],
          [-70.886136, 42.88261],
          [-70.848625, 42.860939],
          [-70.817296, 42.87229],
          [-70.817731, 42.850613],
          [-70.80522, 42.781798],
          [-70.792867, 42.747118],
          [-70.772267, 42.711064],
          [-70.770453, 42.704824],
          [-70.778552, 42.69852],
          [-70.778671, 42.693622],
          [-70.764421, 42.68565],
          [-70.748752, 42.683878],
          [-70.744427, 42.682092],
          [-70.72982, 42.669602],
          [-70.728845, 42.663877],
          [-70.689402, 42.653319],
          [-70.682594, 42.654525],
          [-70.681594, 42.662342],
          [-70.663548, 42.677603],
          [-70.645101, 42.689423],
          [-70.630077, 42.692699],
          [-70.620031, 42.688006],
          [-70.622864, 42.67599],
          [-70.623815, 42.665481],
          [-70.622791, 42.660873],
          [-70.61482, 42.65765],
          [-70.595474, 42.660336],
          [-70.591742, 42.648508],
          [-70.591469, 42.639821],
          [-70.594014, 42.63503],
          [-70.605611, 42.634898],
          [-70.61842, 42.62864],
          [-70.635635, 42.600243],
          [-70.654727, 42.582234],
          [-70.664887, 42.580436],
          [-70.668022, 42.581732],
          [-70.668115, 42.585361],
          [-70.668488, 42.589643],
          [-70.670442, 42.592249],
          [-70.672583, 42.594296],
          [-70.675747, 42.594669],
          [-70.678819, 42.594389],
          [-70.681428, 42.593173],
          [-70.684502, 42.588858],
          [-70.698574, 42.577393],
          [-70.729688, 42.57151],
          [-70.737044, 42.576863],
          [-70.757283, 42.570455],
          [-70.804091, 42.561595],
          [-70.815391, 42.554195],
          [-70.823291, 42.551495],
          [-70.848492, 42.550195],
          [-70.871382, 42.546404],
          [-70.872357, 42.542952],
          [-70.866279, 42.522617],
          [-70.859751, 42.520441],
          [-70.857125, 42.521492],
          [-70.842091, 42.519495],
          [-70.831091, 42.503596],
          [-70.835991, 42.490496],
          [-70.841591, 42.487596],
          [-70.847391, 42.491496],
          [-70.857791, 42.490296],
          [-70.879692, 42.478796],
          [-70.886493, 42.470197],
          [-70.887992, 42.467096],
          [-70.887292, 42.464896],
          [-70.894292, 42.460896],
          [-70.908092, 42.466896],
          [-70.917693, 42.467996],
          [-70.921993, 42.466696],
          [-70.934993, 42.457896],
          [-70.934264, 42.444646],
          [-70.933155, 42.437833],
          [-70.928226, 42.430986],
          [-70.913192, 42.427697],
          [-70.908392, 42.425197],
          [-70.901992, 42.420297],
          [-70.905692, 42.416197],
          [-70.936393, 42.418097],
          [-70.943295, 42.436248],
          [-70.943612, 42.452092],
          [-70.94702, 42.456236],
          [-70.96047, 42.446166],
          [-70.960647, 42.443787],
          [-70.982294, 42.431296],
          [-71.025777, 42.444753],
          [-71.025864, 42.444743],
          [-71.03727, 42.457674],
          [-71.039114, 42.459513],
          [-71.040044, 42.460706],
          [-71.044729, 42.465724],
          [-71.044796, 42.466195],
          [-71.051571, 42.473891],
          [-71.053496, 42.475895],
          [-71.076133, 42.604077],
          [-71.13529, 42.599061],
          [-71.148613, 42.613249],
          [-71.158617, 42.612948],
          [-71.165015, 42.597959],
          [-71.256103, 42.656691],
          [-71.254532, 42.734136],
          [-71.245504, 42.742589],
          [-71.208302, 42.743314],
          [-71.208227, 42.743294],
          [-71.208137, 42.743273]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US25019",
      state: "25",
      county: "019",
      name: "Nantucket County",
      censusArea: 44.97,
      stateName: "Massachusetts"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-70.092142, 41.297741],
          [-70.082072, 41.299093],
          [-70.062565, 41.308726],
          [-70.046088, 41.321651],
          [-70.031332, 41.339332],
          [-70.028805, 41.359919],
          [-70.030924, 41.367453],
          [-70.035162, 41.372161],
          [-70.038458, 41.376399],
          [-70.045586, 41.383598],
          [-70.049564, 41.3879],
          [-70.049053, 41.391702],
          [-70.033514, 41.385816],
          [-70.018446, 41.36863],
          [-69.960277, 41.278731],
          [-69.960181, 41.264546],
          [-69.964422, 41.25457],
          [-69.965725, 41.252466],
          [-69.975, 41.247392],
          [-70.001586, 41.239353],
          [-70.015225, 41.237964],
          [-70.052807, 41.242685],
          [-70.083239, 41.2444],
          [-70.096967, 41.24085],
          [-70.118669, 41.242351],
          [-70.170681, 41.255881],
          [-70.237175, 41.282724],
          [-70.256164, 41.288123],
          [-70.266776, 41.294453],
          [-70.273478, 41.301528],
          [-70.275526, 41.310464],
          [-70.260632, 41.310092],
          [-70.249276, 41.305623],
          [-70.244435, 41.303203],
          [-70.240153, 41.295384],
          [-70.229541, 41.290171],
          [-70.20869, 41.290171],
          [-70.196304, 41.294612],
          [-70.12446, 41.293851],
          [-70.092142, 41.297741]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34023",
      state: "34",
      county: "023",
      name: "Middlesex County",
      censusArea: 308.914,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.490547, 40.523048],
          [-74.495398, 40.529403],
          [-74.499362, 40.531951],
          [-74.503309, 40.534119],
          [-74.512729, 40.540541],
          [-74.514622, 40.542711],
          [-74.515782, 40.544955],
          [-74.463351, 40.599245],
          [-74.4568, 40.5955],
          [-74.437334, 40.597188],
          [-74.424699, 40.598513],
          [-74.416106, 40.59891],
          [-74.399895, 40.600603],
          [-74.394634, 40.600901],
          [-74.372794, 40.602303],
          [-74.303392, 40.608903],
          [-74.291391, 40.591903],
          [-74.258114, 40.60225],
          [-74.255822, 40.602194],
          [-74.25381, 40.600989],
          [-74.24779, 40.600103],
          [-74.231748, 40.598881],
          [-74.215101, 40.604074],
          [-74.206731, 40.594569],
          [-74.208988, 40.576304],
          [-74.214788, 40.560604],
          [-74.218189, 40.557204],
          [-74.231589, 40.559204],
          [-74.248641, 40.549601],
          [-74.251441, 40.542301],
          [-74.246237, 40.520963],
          [-74.26829, 40.499205],
          [-74.269998, 40.495014],
          [-74.27269, 40.488405],
          [-74.26759, 40.471806],
          [-74.261889, 40.464706],
          [-74.236689, 40.457806],
          [-74.225035, 40.453301],
          [-74.360699, 40.318386],
          [-74.37859, 40.304116],
          [-74.381135, 40.301546],
          [-74.394112, 40.288463],
          [-74.393517, 40.279574],
          [-74.408085, 40.273791],
          [-74.460776, 40.251358],
          [-74.484287, 40.253299],
          [-74.572559, 40.301054],
          [-74.619591, 40.374397],
          [-74.494275, 40.469807],
          [-74.493226, 40.47077],
          [-74.489524, 40.474415],
          [-74.459922, 40.491015],
          [-74.490547, 40.523048]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26055",
      state: "26",
      county: "055",
      name: "Grand Traverse County",
      censusArea: 464.331,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.640216, 44.775051],
          [-85.636097, 44.771329],
          [-85.627982, 44.767508],
          [-85.624541, 44.767038],
          [-85.623607, 44.766911],
          [-85.620551, 44.766494],
          [-85.619648, 44.766371],
          [-85.610776, 44.76516],
          [-85.607701, 44.765363],
          [-85.605586, 44.765502],
          [-85.599874, 44.765878],
          [-85.599256, 44.765919],
          [-85.599103, 44.765996],
          [-85.593833, 44.768651],
          [-85.593571, 44.768783],
          [-85.593474, 44.769348],
          [-85.593449, 44.769499],
          [-85.591852, 44.778839],
          [-85.591145, 44.782981],
          [-85.591124, 44.7831],
          [-85.590985, 44.783914],
          [-85.581717, 44.807784],
          [-85.581392, 44.808294],
          [-85.545891, 44.864024],
          [-85.539042, 44.868868],
          [-85.532931, 44.87319],
          [-85.530729, 44.889182],
          [-85.530711, 44.889314],
          [-85.530649, 44.889763],
          [-85.553348, 44.890916],
          [-85.553509, 44.890924],
          [-85.559524, 44.888113],
          [-85.564509, 44.895246],
          [-85.562936, 44.896612],
          [-85.562503, 44.896987],
          [-85.557257, 44.901541],
          [-85.556647, 44.90207],
          [-85.556471, 44.902223],
          [-85.551567, 44.906481],
          [-85.539703, 44.916779],
          [-85.538945, 44.917885],
          [-85.538288, 44.918845],
          [-85.533553, 44.925762],
          [-85.530477, 44.933732],
          [-85.529233, 44.936955],
          [-85.520205, 44.960347],
          [-85.520443, 44.961149],
          [-85.520698, 44.962008],
          [-85.5221, 44.966727],
          [-85.521896, 44.967446],
          [-85.520789, 44.971338],
          [-85.520034, 44.973996],
          [-85.518875, 44.974665],
          [-85.4926, 44.989834],
          [-85.492386, 44.989849],
          [-85.475204, 44.991053],
          [-85.472676, 44.985558],
          [-85.471708, 44.983453],
          [-85.470462, 44.980745],
          [-85.470215, 44.979864],
          [-85.468293, 44.973008],
          [-85.46802, 44.972035],
          [-85.466848, 44.967853],
          [-85.464944, 44.961062],
          [-85.46665, 44.958844],
          [-85.470688, 44.959238],
          [-85.471261, 44.959294],
          [-85.471646, 44.959331],
          [-85.472258, 44.959391],
          [-85.474274, 44.958529],
          [-85.48574, 44.953626],
          [-85.489049, 44.938087],
          [-85.489895, 44.934118],
          [-85.490713, 44.930274],
          [-85.491215, 44.927918],
          [-85.491239, 44.927804],
          [-85.491286, 44.927585],
          [-85.491393, 44.925868],
          [-85.491403, 44.925695],
          [-85.491414, 44.925531],
          [-85.491471, 44.92461],
          [-85.491496, 44.924205],
          [-85.492397, 44.909719],
          [-85.49249, 44.90822],
          [-85.489477, 44.903145],
          [-85.488927, 44.902217],
          [-85.488624, 44.901707],
          [-85.489704, 44.897533],
          [-85.489921, 44.896694],
          [-85.498007, 44.865451],
          [-85.500872, 44.85883],
          [-85.502182, 44.855802],
          [-85.502386, 44.855551],
          [-85.508227, 44.848352],
          [-85.508617, 44.847872],
          [-85.511751, 44.847114],
          [-85.513575, 44.846674],
          [-85.516997, 44.845846],
          [-85.518845, 44.8454],
          [-85.519096, 44.845339],
          [-85.527181, 44.841002],
          [-85.533434, 44.837648],
          [-85.538522, 44.834918],
          [-85.539924, 44.834166],
          [-85.542706, 44.831395],
          [-85.546021, 44.828092],
          [-85.547119, 44.826998],
          [-85.551797, 44.822338],
          [-85.55285, 44.821288],
          [-85.553844, 44.820298],
          [-85.555894, 44.818256],
          [-85.557952, 44.814372],
          [-85.560231, 44.810072],
          [-85.560232, 44.809974],
          [-85.560253, 44.808359],
          [-85.560348, 44.800772],
          [-85.560352, 44.800454],
          [-85.5604, 44.796655],
          [-85.560409, 44.79595],
          [-85.560424, 44.794772],
          [-85.560488, 44.78969],
          [-85.560488, 44.789679],
          [-85.568781, 44.774477],
          [-85.571018, 44.770377],
          [-85.57517, 44.762766],
          [-85.576239, 44.760807],
          [-85.576566, 44.760208],
          [-85.575916, 44.759602],
          [-85.571301, 44.755293],
          [-85.554774, 44.748917],
          [-85.554326, 44.748744],
          [-85.554083, 44.748715],
          [-85.538285, 44.746821],
          [-85.527216, 44.748235],
          [-85.523296, 44.751702],
          [-85.509432, 44.763964],
          [-85.504775, 44.768082],
          [-85.504772, 44.768102],
          [-85.5047, 44.768518],
          [-85.503935, 44.772951],
          [-85.504079, 44.773901],
          [-85.504787, 44.778577],
          [-85.505244, 44.781594],
          [-85.507113, 44.784271],
          [-85.509251, 44.787334],
          [-85.508462, 44.788682],
          [-85.507473, 44.790372],
          [-85.499591, 44.803838],
          [-85.496314, 44.805308],
          [-85.484668, 44.810531],
          [-85.481972, 44.81174],
          [-85.477423, 44.813781],
          [-85.475748, 44.814532],
          [-85.475114, 44.814816],
          [-85.474796, 44.814959],
          [-85.474622, 44.815107],
          [-85.462943, 44.825044],
          [-85.462916, 44.825067],
          [-85.462906, 44.82511],
          [-85.462622, 44.826327],
          [-85.460445, 44.835667],
          [-85.457475, 44.839609],
          [-85.457472, 44.839613],
          [-85.457328, 44.839804],
          [-85.456007, 44.841558],
          [-85.455336, 44.842448],
          [-85.452683, 44.845969],
          [-85.44225, 44.859817],
          [-85.332836, 44.812372],
          [-85.334, 44.512303],
          [-85.817802, 44.512969],
          [-85.81512, 44.774525],
          [-85.640216, 44.775051]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31091",
      state: "31",
      county: "091",
      name: "Hooker County",
      censusArea: 721.121,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.406466, 41.7432],
          [-101.42592, 41.743059],
          [-101.426557, 42.092194],
          [-100.846129, 42.088166],
          [-100.842459, 41.740404],
          [-101.406466, 41.7432]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31095",
      state: "31",
      county: "095",
      name: "Jefferson County",
      censusArea: 570.185,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.369199, 40.00206],
          [-97.368688, 40.350392],
          [-96.915083, 40.349734],
          [-96.915902, 40.348356],
          [-96.916407, 40.001506],
          [-97.009165, 40.001463],
          [-97.030803, 40.001342],
          [-97.049663, 40.001323],
          [-97.137866, 40.001814],
          [-97.142448, 40.001495],
          [-97.181775, 40.00155],
          [-97.20019, 40.001549],
          [-97.20231, 40.001442],
          [-97.24508, 40.001467],
          [-97.245169, 40.001513],
          [-97.350272, 40.001976],
          [-97.350896, 40.00193],
          [-97.369103, 40.00206],
          [-97.369199, 40.00206]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31103",
      state: "31",
      county: "103",
      name: "Keya Paha County",
      censusArea: 773.072,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.534049, 42.998041],
          [-99.494287, 42.998118],
          [-99.490798, 42.998143],
          [-99.474531, 42.998081],
          [-99.471353, 42.997967],
          [-99.395568, 42.99817],
          [-99.374268, 42.998047],
          [-99.371121, 42.998093],
          [-99.368628, 42.99814],
          [-99.347283, 42.998217],
          [-99.288045, 42.998152],
          [-99.26271, 42.998234],
          [-99.254454, 42.99814],
          [-99.254235, 42.996812],
          [-99.257039, 42.804296],
          [-99.537491, 42.717626],
          [-99.677684, 42.730903],
          [-100.133978, 42.807819],
          [-100.198478, 42.846102],
          [-100.198412, 42.998542],
          [-100.126896, 42.998711],
          [-100.126427, 42.99871],
          [-100.119297, 42.998689],
          [-100.034389, 42.998425],
          [-100.027815, 42.998424],
          [-100.004757, 42.998392],
          [-99.961204, 42.998335],
          [-99.950921, 42.998291],
          [-99.950411, 42.998286],
          [-99.927645, 42.998113],
          [-99.918401, 42.998057],
          [-99.877697, 42.998094],
          [-99.869885, 42.998094],
          [-99.859945, 42.997962],
          [-99.850037, 42.998171],
          [-99.821868, 42.997995],
          [-99.809373, 42.998178],
          [-99.803328, 42.998064],
          [-99.800306, 42.997972],
          [-99.788247, 42.998016],
          [-99.768524, 42.998125],
          [-99.743138, 42.997912],
          [-99.726788, 42.997892],
          [-99.719177, 42.997899],
          [-99.701446, 42.997994],
          [-99.699234, 42.99788],
          [-99.569277, 42.997995],
          [-99.535375, 42.998038],
          [-99.534049, 42.998041]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31111",
      state: "31",
      county: "111",
      name: "Lincoln County",
      censusArea: 2564.068,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.269695, 41.394934],
          [-100.713243, 41.393511],
          [-100.251098, 41.393298],
          [-100.236106, 41.393324],
          [-100.238155, 41.046536],
          [-100.224235, 41.046597],
          [-100.223311, 40.700254],
          [-100.777833, 40.700135],
          [-101.247963, 40.697802],
          [-101.249975, 41.00395],
          [-101.270381, 41.047392],
          [-101.269695, 41.394934]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28089",
      state: "28",
      county: "089",
      name: "Madison County",
      censusArea: 714.506,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.730016, 32.884773],
          [-89.73014, 32.685119],
          [-89.730304, 32.634228],
          [-89.759155, 32.613543],
          [-89.770781, 32.599184],
          [-89.781805, 32.597364],
          [-89.784123, 32.586891],
          [-89.858878, 32.584836],
          [-89.962077, 32.491286],
          [-90.06882, 32.438078],
          [-90.065697, 32.399529],
          [-90.141232, 32.399495],
          [-90.146688, 32.399859],
          [-90.243764, 32.400059],
          [-90.243641, 32.487533],
          [-90.44978, 32.487983],
          [-90.450037, 32.573783],
          [-90.363868, 32.603268],
          [-90.278055, 32.660346],
          [-90.197729, 32.66385],
          [-90.049129, 32.735674],
          [-89.965882, 32.879573],
          [-89.730016, 32.884773]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28093",
      state: "28",
      county: "093",
      name: "Marshall County",
      censusArea: 706.193,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.724324, 34.994763],
          [-89.644282, 34.995293],
          [-89.511153, 34.994755],
          [-89.493739, 34.994361],
          [-89.352679, 34.99442],
          [-89.352705, 34.844432],
          [-89.299227, 34.844415],
          [-89.298334, 34.582926],
          [-89.245645, 34.583234],
          [-89.245922, 34.542942],
          [-89.244591, 34.542329],
          [-89.24592, 34.54135],
          [-89.246133, 34.495633],
          [-89.424459, 34.519129],
          [-89.476642, 34.554168],
          [-89.668448, 34.554367],
          [-89.66916, 34.685786],
          [-89.722333, 34.685504],
          [-89.723635, 34.772121],
          [-89.723357, 34.773347],
          [-89.724324, 34.994763]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27031",
      state: "27",
      county: "031",
      name: "Cook County",
      censusArea: 1452.277,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.032942, 48.190794],
          [-90.976955, 48.219452],
          [-90.88548, 48.245784],
          [-90.847352, 48.244443],
          [-90.843624, 48.243576],
          [-90.839176, 48.239511],
          [-90.804207, 48.177833],
          [-90.789776, 48.143472],
          [-90.795308, 48.135523],
          [-90.793841, 48.135569],
          [-90.776133, 48.122481],
          [-90.775962, 48.122229],
          [-90.774225, 48.118894],
          [-90.774191, 48.118575],
          [-90.761625, 48.098283],
          [-90.751608, 48.090968],
          [-90.569763, 48.106951],
          [-90.495637, 48.099444],
          [-90.495398, 48.099787],
          [-90.452022, 48.105006],
          [-90.403219, 48.105114],
          [-90.344234, 48.094447],
          [-90.343484, 48.095064],
          [-90.337177, 48.099771],
          [-90.330052, 48.102399],
          [-90.312386, 48.1053],
          [-90.176605, 48.112445],
          [-90.136191, 48.112136],
          [-90.12509, 48.107702],
          [-90.1239, 48.107131],
          [-90.091639, 48.10463],
          [-90.073873, 48.101138],
          [-90.023595, 48.084708],
          [-89.997852, 48.057567],
          [-89.993822, 48.049027],
          [-89.996702, 48.035391],
          [-89.99305, 48.028404],
          [-89.968255, 48.014482],
          [-89.897414, 47.987599],
          [-89.873286, 47.985419],
          [-89.847571, 47.992442],
          [-89.819802, 48.015099],
          [-89.763967, 48.022969],
          [-89.749314, 48.023325],
          [-89.724184, 48.019818],
          [-89.687891, 48.010826],
          [-89.655793, 48.007532],
          [-89.564288, 48.00293],
          [-89.489226, 48.014528],
          [-89.491739, 48.005212],
          [-89.495344, 48.002356],
          [-89.541521, 47.992841],
          [-89.551555, 47.987305],
          [-89.552939, 47.980731],
          [-89.555015, 47.974849],
          [-89.572315, 47.967238],
          [-89.58823, 47.9662],
          [-89.59589, 47.971046],
          [-89.611412, 47.980731],
          [-89.624559, 47.983153],
          [-89.631825, 47.980039],
          [-89.637015, 47.973465],
          [-89.640129, 47.96793],
          [-89.639844, 47.959826],
          [-89.638285, 47.954275],
          [-89.639545, 47.95359],
          [-89.660616, 47.951216],
          [-89.697619, 47.941288],
          [-89.72973, 47.925245],
          [-89.737539, 47.918183],
          [-89.758714, 47.906993],
          [-89.793539, 47.891358],
          [-89.85396, 47.873997],
          [-89.87158, 47.874194],
          [-89.923649, 47.862062],
          [-89.930844, 47.857723],
          [-89.92752, 47.850825],
          [-89.933899, 47.84676],
          [-89.974296, 47.830514],
          [-90.01373, 47.821373],
          [-90.042761, 47.817568],
          [-90.072025, 47.811105],
          [-90.072241, 47.807727],
          [-90.075559, 47.803303],
          [-90.082354, 47.803619],
          [-90.08816, 47.803041],
          [-90.1168, 47.79538],
          [-90.132078, 47.79572],
          [-90.16079, 47.792807],
          [-90.178755, 47.786414],
          [-90.187636, 47.77813],
          [-90.229145, 47.776198],
          [-90.248794, 47.772763],
          [-90.295952, 47.759054],
          [-90.30634, 47.756627],
          [-90.313958, 47.756681],
          [-90.323446, 47.753771],
          [-90.330254, 47.750892],
          [-90.332686, 47.746387],
          [-90.386234, 47.7411],
          [-90.393823, 47.738271],
          [-90.42139, 47.73515],
          [-90.437712, 47.731612],
          [-90.441912, 47.726404],
          [-90.458365, 47.7214],
          [-90.537105, 47.703055],
          [-90.551291, 47.690266],
          [-90.584954, 47.68074],
          [-90.647837, 47.656176],
          [-90.686382, 47.643594],
          [-90.735927, 47.624343],
          [-90.86827, 47.5569],
          [-90.907494, 47.532873],
          [-90.910127, 47.530178],
          [-90.909801, 47.526215],
          [-90.914247, 47.522639],
          [-90.919375, 47.519784],
          [-90.927975, 47.519008],
          [-90.939072, 47.514532],
          [-91.023124, 47.464964],
          [-91.023125, 47.464964],
          [-91.020737, 47.887995],
          [-91.032942, 48.190794]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27049",
      state: "27",
      county: "049",
      name: "Goodhue County",
      censusArea: 756.838,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.316425, 44.540792],
          [-92.314071, 44.538014],
          [-92.303046, 44.518646],
          [-92.302215, 44.500298],
          [-92.291005, 44.485464],
          [-92.276784, 44.473649],
          [-92.24201, 44.454254],
          [-92.430271, 44.455157],
          [-92.430629, 44.368291],
          [-92.550733, 44.368521],
          [-92.550871, 44.194916],
          [-92.678717, 44.195516],
          [-93.041159, 44.196726],
          [-93.039485, 44.471871],
          [-93.039344, 44.51535],
          [-92.7926, 44.542971],
          [-92.792584, 44.629722],
          [-92.732043, 44.629484],
          [-92.732042, 44.713775],
          [-92.632105, 44.649027],
          [-92.621733, 44.638983],
          [-92.619774, 44.629214],
          [-92.549685, 44.576],
          [-92.534186, 44.570375],
          [-92.491773, 44.566003],
          [-92.357313, 44.55848],
          [-92.340058, 44.555273],
          [-92.329013, 44.550895],
          [-92.319938, 44.54494],
          [-92.316425, 44.540792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27055",
      state: "27",
      county: "055",
      name: "Houston County",
      censusArea: 552.058,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.262436, 43.792166],
          [-91.268198, 43.726571],
          [-91.273316, 43.668307],
          [-91.268748, 43.615348],
          [-91.265091, 43.609977],
          [-91.217706, 43.50055],
          [-91.369325, 43.500827],
          [-91.371608, 43.500945],
          [-91.37695, 43.500482],
          [-91.441786, 43.500438],
          [-91.445932, 43.500588],
          [-91.461403, 43.500642],
          [-91.465063, 43.500608],
          [-91.491042, 43.50069],
          [-91.533806, 43.50056],
          [-91.54122, 43.500515],
          [-91.551021, 43.500539],
          [-91.591073, 43.500536],
          [-91.610832, 43.50053],
          [-91.610895, 43.50053],
          [-91.615293, 43.50055],
          [-91.616895, 43.500663],
          [-91.617407, 43.500687],
          [-91.620785, 43.500677],
          [-91.625611, 43.500727],
          [-91.634244, 43.500479],
          [-91.634495, 43.500439],
          [-91.635626, 43.500463],
          [-91.639772, 43.500573],
          [-91.644924, 43.500529],
          [-91.651396, 43.500454],
          [-91.658401, 43.500533],
          [-91.670872, 43.500513],
          [-91.700749, 43.500581],
          [-91.730217, 43.50068],
          [-91.73047, 43.50233],
          [-91.730424, 43.846935],
          [-91.284138, 43.847065],
          [-91.262436, 43.792166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37049",
      state: "37",
      county: "049",
      name: "Craven County",
      censusArea: 708.958,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.125021, 35.077561],
          [-77.339077, 35.155076],
          [-77.473716, 35.228983],
          [-77.390965, 35.340047],
          [-77.383581, 35.33536],
          [-77.374683, 35.333825],
          [-77.365484, 35.333651],
          [-77.361188, 35.331852],
          [-77.357402, 35.328827],
          [-77.353981, 35.327988],
          [-77.301155, 35.374239],
          [-77.295879, 35.379163],
          [-77.190423, 35.418955],
          [-76.895546, 35.253532],
          [-76.98696, 35.155811],
          [-76.935011, 35.105799],
          [-76.967297, 35.040655],
          [-76.896514, 34.981764],
          [-76.893266, 34.980285],
          [-76.839333, 34.95941],
          [-76.711278, 34.981971],
          [-76.65421, 34.937088],
          [-76.693085, 34.869219],
          [-76.939252, 34.828636],
          [-77.090323, 34.802755],
          [-77.108813, 34.887572],
          [-77.125021, 35.077561]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37051",
      state: "37",
      county: "051",
      name: "Cumberland County",
      censusArea: 652.315,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.617079, 35.245706],
          [-78.6701, 35.092964],
          [-78.647522, 34.99482],
          [-78.522724, 34.88355],
          [-78.521229, 34.882736],
          [-78.519347, 34.882653],
          [-78.519742, 34.8837],
          [-78.518023, 34.883846],
          [-78.515661, 34.883],
          [-78.515626, 34.881958],
          [-78.51387, 34.881916],
          [-78.514457, 34.880927],
          [-78.512906, 34.880906],
          [-78.514079, 34.875956],
          [-78.51501, 34.875567],
          [-78.514758, 34.873734],
          [-78.513379, 34.873385],
          [-78.5133, 34.869287],
          [-78.511755, 34.867961],
          [-78.50694, 34.866669],
          [-78.506006, 34.865179],
          [-78.502835, 34.864663],
          [-78.502189, 34.861549],
          [-78.498938, 34.859302],
          [-78.496564, 34.859471],
          [-78.494838, 34.858841],
          [-78.49541, 34.857678],
          [-78.49461, 34.856464],
          [-78.901096, 34.834789],
          [-79.034715, 34.953584],
          [-79.035766, 34.955329],
          [-79.036447, 34.955582],
          [-79.036926, 34.956926],
          [-79.083163, 35.032662],
          [-79.097671, 35.174172],
          [-79.096389, 35.177682],
          [-79.098726, 35.183439],
          [-79.098031, 35.188359],
          [-79.09613, 35.192181],
          [-78.941859, 35.219211],
          [-78.825828, 35.259287],
          [-78.617079, 35.245706]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37131",
      state: "37",
      county: "131",
      name: "Northampton County",
      censusArea: 536.589,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.767117, 36.544752],
          [-77.296875, 36.544746],
          [-77.24969, 36.544745],
          [-77.1645, 36.54633],
          [-77.119577, 36.474139],
          [-77.20879, 36.246875],
          [-77.29181, 36.16849],
          [-77.300445, 36.164944],
          [-77.330993, 36.192774],
          [-77.350899, 36.19128],
          [-77.386724, 36.213662],
          [-77.441747, 36.32409],
          [-77.586041, 36.337015],
          [-77.557072, 36.418606],
          [-77.645496, 36.480956],
          [-77.89795, 36.506849],
          [-77.898943, 36.518158],
          [-77.899771, 36.544663],
          [-77.882357, 36.544737],
          [-77.87528, 36.544754],
          [-77.767117, 36.544752]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37137",
      state: "37",
      county: "137",
      name: "Pamlico County",
      censusArea: 336.537,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.839333, 34.95941],
          [-76.893266, 34.980285],
          [-76.896514, 34.981764],
          [-76.967297, 35.040655],
          [-76.935011, 35.105799],
          [-76.98696, 35.155811],
          [-76.895546, 35.253532],
          [-76.845457, 35.216045],
          [-76.632535, 35.235233],
          [-76.604962, 35.337751],
          [-76.602669, 35.336528],
          [-76.588055, 35.333156],
          [-76.554332, 35.332032],
          [-76.548712, 35.328659],
          [-76.500375, 35.321915],
          [-76.482389, 35.314046],
          [-76.472273, 35.294936],
          [-76.467776, 35.276951],
          [-76.467776, 35.261213],
          [-76.477893, 35.243228],
          [-76.483514, 35.240979],
          [-76.490258, 35.233111],
          [-76.491382, 35.220745],
          [-76.494755, 35.212877],
          [-76.504872, 35.207256],
          [-76.521733, 35.192643],
          [-76.536346, 35.174657],
          [-76.539719, 35.166788],
          [-76.536346, 35.149927],
          [-76.536346, 35.142058],
          [-76.546463, 35.122948],
          [-76.557704, 35.116204],
          [-76.561077, 35.108335],
          [-76.568945, 35.097094],
          [-76.57569, 35.092598],
          [-76.586931, 35.092598],
          [-76.592552, 35.083605],
          [-76.593676, 35.075736],
          [-76.60042, 35.067867],
          [-76.61391, 35.061123],
          [-76.622902, 35.061123],
          [-76.631895, 35.056626],
          [-76.639764, 35.051006],
          [-76.646509, 35.042013],
          [-76.646509, 35.025151],
          [-76.644562, 35.019799],
          [-76.711278, 34.981971],
          [-76.839333, 34.95941]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37149",
      state: "37",
      county: "149",
      name: "Polk County",
      censusArea: 237.789,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.969325, 35.187215],
          [-82.167676, 35.193699],
          [-82.176874, 35.19379],
          [-82.185513, 35.194355],
          [-82.195483, 35.194951],
          [-82.216217, 35.196044],
          [-82.230517, 35.196764],
          [-82.230915, 35.196784],
          [-82.257515, 35.198636],
          [-82.288453, 35.198605],
          [-82.35243, 35.206595],
          [-82.346089, 35.286038],
          [-82.261456, 35.392984],
          [-82.115159, 35.38739],
          [-81.968788, 35.257764],
          [-81.969325, 35.187215]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37155",
      state: "37",
      county: "155",
      name: "Robeson County",
      censusArea: 949.221,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.461318, 34.630126],
          [-79.461754, 34.630432],
          [-79.364296, 34.737549],
          [-79.359305, 34.743079],
          [-79.349143, 34.838476],
          [-79.191423, 34.833377],
          [-79.034715, 34.953584],
          [-78.901096, 34.834789],
          [-78.805744, 34.689267],
          [-78.87153, 34.482668],
          [-78.951088, 34.449026],
          [-79.071169, 34.29924],
          [-79.143242, 34.359817],
          [-79.151485, 34.366753],
          [-79.190739, 34.399751],
          [-79.192041, 34.40104],
          [-79.198982, 34.406699],
          [-79.323249, 34.514634],
          [-79.324854, 34.516282],
          [-79.331328, 34.521869],
          [-79.358317, 34.545358],
          [-79.450034, 34.621036],
          [-79.459766, 34.629027],
          [-79.461318, 34.630126]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42091",
      state: "42",
      county: "091",
      name: "Montgomery County",
      censusArea: 483.04,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.361066, 40.065543],
          [-75.525945, 40.148611],
          [-75.549174, 40.189542],
          [-75.557148, 40.207213],
          [-75.562878, 40.197361],
          [-75.573338, 40.195303],
          [-75.592657, 40.225491],
          [-75.696782, 40.241863],
          [-75.695955, 40.242359],
          [-75.529694, 40.446995],
          [-75.484057, 40.418451],
          [-75.299774, 40.307835],
          [-75.106878, 40.192279],
          [-75.104874, 40.191109],
          [-75.015066, 40.137992],
          [-75.096733, 40.068989],
          [-75.087425, 40.063599],
          [-75.109406, 40.04584],
          [-75.124916, 40.054684],
          [-75.129021, 40.057091],
          [-75.135193, 40.061031],
          [-75.151596, 40.070328],
          [-75.176438, 40.084649],
          [-75.264431, 40.054094],
          [-75.249214, 39.990547],
          [-75.256119, 39.987301],
          [-75.263687, 39.982658],
          [-75.276482, 39.976959],
          [-75.349457, 40.052905],
          [-75.356534, 40.062787],
          [-75.361066, 40.065543]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42097",
      state: "42",
      county: "097",
      name: "Northumberlnd County",
      censusArea: 458.368,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.896114, 41.13907],
          [-76.888145, 41.153807],
          [-76.880963, 41.158044],
          [-76.788659, 41.175762],
          [-76.732672, 41.17204],
          [-76.792878, 40.946583],
          [-76.661348, 40.967996],
          [-76.557013, 40.938617],
          [-76.528034, 40.882515],
          [-76.501312, 40.824817],
          [-76.409667, 40.833624],
          [-76.380152, 40.775511],
          [-76.701624, 40.658082],
          [-76.890875, 40.610096],
          [-76.949414, 40.628163],
          [-76.947365, 40.629517],
          [-76.939749, 40.638378],
          [-76.855362, 40.728821],
          [-76.855523, 40.737119],
          [-76.855995, 40.737931],
          [-76.856979, 40.746001],
          [-76.856681, 40.747311],
          [-76.857521, 40.749612],
          [-76.856445, 40.75581],
          [-76.857209, 40.759446],
          [-76.857719, 40.764374],
          [-76.857522, 40.766],
          [-76.858352, 40.769536],
          [-76.859252, 40.777043],
          [-76.858513, 40.779416],
          [-76.858703, 40.781521],
          [-76.856292, 40.789697],
          [-76.856094, 40.791211],
          [-76.852163, 40.800839],
          [-76.848912, 40.805989],
          [-76.84966, 40.808205],
          [-76.84928, 40.809755],
          [-76.845395, 40.815175],
          [-76.840626, 40.821324],
          [-76.836338, 40.825761],
          [-76.829685, 40.831029],
          [-76.825648, 40.835503],
          [-76.820895, 40.839898],
          [-76.817522, 40.843782],
          [-76.812728, 40.847457],
          [-76.812242, 40.850305],
          [-76.811228, 40.851712],
          [-76.808366, 40.857369],
          [-76.806246, 40.862535],
          [-76.801561, 40.870634],
          [-76.799523, 40.874749],
          [-76.798921, 40.876901],
          [-76.798714, 40.878366],
          [-76.800242, 40.881994],
          [-76.88228, 40.971334],
          [-76.896114, 41.13907]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42103",
      state: "42",
      county: "103",
      name: "Pike County",
      censusArea: 544.961,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.966759, 41.093425],
          [-74.968389, 41.087797],
          [-74.980674, 41.078178],
          [-75.156507, 41.150341],
          [-75.129721, 41.252101],
          [-75.359184, 41.239206],
          [-75.311143, 41.331439],
          [-75.050074, 41.606893],
          [-75.04049, 41.569688],
          [-74.984226, 41.506299],
          [-74.93976, 41.483371],
          [-74.912517, 41.475605],
          [-74.891948, 41.448853],
          [-74.876721, 41.440338],
          [-74.799165, 41.430451],
          [-74.75595, 41.426804],
          [-74.752562, 41.426518],
          [-74.691129, 41.367324],
          [-74.689767, 41.361558],
          [-74.694914, 41.357423],
          [-74.76173, 41.336398],
          [-74.795847, 41.318951],
          [-74.830057, 41.2872],
          [-74.838366, 41.277286],
          [-74.861678, 41.241575],
          [-74.867267, 41.228861],
          [-74.864356, 41.224854],
          [-74.867287, 41.208754],
          [-74.882139, 41.180836],
          [-74.923169, 41.138146],
          [-74.974338, 41.103912],
          [-74.969434, 41.096074],
          [-74.967389, 41.094049],
          [-74.966759, 41.093425]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34025",
      state: "34",
      county: "025",
      name: "Monmouth County",
      censusArea: 468.793,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.408085, 40.273791],
          [-74.393517, 40.279574],
          [-74.394112, 40.288463],
          [-74.381135, 40.301546],
          [-74.37859, 40.304116],
          [-74.360699, 40.318386],
          [-74.225035, 40.453301],
          [-74.224047, 40.452919],
          [-74.222959, 40.452499],
          [-74.209788, 40.447407],
          [-74.206188, 40.440707],
          [-74.206419, 40.438789],
          [-74.208655, 40.43752],
          [-74.207205, 40.435434],
          [-74.202128, 40.43894],
          [-74.193908, 40.440995],
          [-74.191309, 40.44299],
          [-74.187787, 40.447407],
          [-74.174787, 40.455607],
          [-74.174893, 40.454491],
          [-74.175074, 40.449144],
          [-74.176842, 40.44774],
          [-74.175346, 40.446607],
          [-74.169977, 40.45064],
          [-74.167009, 40.448737],
          [-74.166193, 40.447128],
          [-74.164029, 40.448312],
          [-74.163314, 40.448424],
          [-74.157787, 40.446607],
          [-74.153611, 40.447647],
          [-74.152686, 40.447344],
          [-74.151952, 40.448062],
          [-74.142886, 40.450407],
          [-74.139886, 40.453407],
          [-74.138415, 40.454468],
          [-74.135823, 40.455196],
          [-74.133727, 40.454672],
          [-74.131135, 40.453245],
          [-74.127466, 40.451061],
          [-74.124692, 40.44958],
          [-74.122327, 40.448258],
          [-74.116863, 40.446069],
          [-74.088085, 40.438407],
          [-74.076185, 40.433707],
          [-74.058984, 40.422708],
          [-74.047884, 40.418908],
          [-74.006383, 40.411108],
          [-73.998505, 40.410911],
          [-73.995486, 40.419472],
          [-73.991682, 40.442908],
          [-74.006077, 40.464625],
          [-74.017783, 40.472207],
          [-74.017917, 40.474338],
          [-74.014031, 40.476471],
          [-74.0071, 40.475298],
          [-73.995683, 40.468707],
          [-73.978282, 40.440208],
          [-73.976982, 40.408508],
          [-73.971381, 40.371709],
          [-73.971381, 40.34801],
          [-73.977442, 40.299373],
          [-73.981681, 40.279411],
          [-73.993292, 40.237669],
          [-74.016017, 40.166914],
          [-74.030181, 40.122814],
          [-74.03408, 40.103115],
          [-74.031861, 40.101047],
          [-74.10803, 40.136873],
          [-74.224411, 40.108982],
          [-74.263763, 40.168264],
          [-74.406777, 40.172355],
          [-74.553105, 40.07913],
          [-74.587847, 40.138237],
          [-74.484287, 40.253299],
          [-74.460776, 40.251358],
          [-74.408085, 40.273791]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US34039",
      state: "34",
      county: "039",
      name: "Union County",
      censusArea: 102.855,
      stateName: "New Jersey"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-74.206731, 40.594569],
          [-74.215101, 40.604074],
          [-74.231748, 40.598881],
          [-74.24779, 40.600103],
          [-74.25381, 40.600989],
          [-74.255822, 40.602194],
          [-74.258114, 40.60225],
          [-74.291391, 40.591903],
          [-74.303392, 40.608903],
          [-74.372794, 40.602303],
          [-74.394634, 40.600901],
          [-74.399895, 40.600603],
          [-74.416106, 40.59891],
          [-74.424699, 40.598513],
          [-74.437334, 40.597188],
          [-74.4568, 40.5955],
          [-74.463351, 40.599245],
          [-74.43804, 40.60991],
          [-74.417038, 40.628077],
          [-74.408264, 40.644185],
          [-74.461231, 40.673397],
          [-74.371923, 40.739229],
          [-74.354942, 40.7345],
          [-74.342682, 40.725181],
          [-74.325596, 40.717075],
          [-74.321408, 40.719072],
          [-74.318992, 40.719201],
          [-74.315991, 40.719199],
          [-74.315293, 40.718601],
          [-74.313793, 40.718001],
          [-74.312794, 40.717101],
          [-74.311691, 40.715601],
          [-74.309791, 40.715501],
          [-74.309388, 40.714901],
          [-74.308793, 40.714508],
          [-74.307589, 40.714201],
          [-74.306991, 40.714002],
          [-74.305393, 40.713701],
          [-74.303691, 40.7133],
          [-74.302695, 40.713601],
          [-74.302092, 40.713301],
          [-74.30123, 40.713472],
          [-74.28587, 40.722225],
          [-74.222452, 40.707249],
          [-74.201589, 40.686602],
          [-74.136703, 40.674444],
          [-74.153041, 40.6548],
          [-74.160288, 40.645903],
          [-74.161397, 40.644092],
          [-74.181083, 40.646484],
          [-74.186027, 40.646076],
          [-74.189106, 40.643832],
          [-74.202223, 40.631053],
          [-74.206731, 40.594569]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35003",
      state: "35",
      county: "003",
      name: "Catron County",
      censusArea: 6923.69,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-108.000597, 33.201563],
          [-108.449988, 33.200997],
          [-109.047237, 33.208965],
          [-109.046827, 33.365272],
          [-109.046909, 33.36557],
          [-109.047045, 33.36928],
          [-109.04687, 33.372654],
          [-109.046627, 33.778233],
          [-109.046182, 34.522393],
          [-109.046182, 34.522553],
          [-109.046156, 34.579291],
          [-107.724854, 34.57806],
          [-107.725461, 34.259775],
          [-107.716301, 34.259856],
          [-107.712713, 33.476892],
          [-108.000602, 33.478014],
          [-108.000597, 33.201563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35005",
      state: "35",
      county: "005",
      name: "Chaves County",
      censusArea: 6065.275,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.945949, 34.082784],
          [-103.841619, 34.081681],
          [-103.841906, 33.819252],
          [-103.716794, 33.8193],
          [-103.71826, 33.656136],
          [-103.509726, 33.657218],
          [-103.510267, 33.570256],
          [-103.720742, 33.569678],
          [-103.72138, 33.395905],
          [-103.766433, 33.395491],
          [-103.766341, 32.965232],
          [-103.8146, 32.965203],
          [-103.828855, 32.96546],
          [-104.841229, 32.963303],
          [-104.842026, 32.520705],
          [-104.851525, 32.520505],
          [-105.353907, 32.518769],
          [-105.35255, 32.961976],
          [-105.316151, 32.962014],
          [-105.316882, 33.132218],
          [-104.905762, 33.138626],
          [-104.90382, 33.306184],
          [-104.885186, 33.306267],
          [-104.893141, 34.088304],
          [-104.786238, 34.087763],
          [-104.785964, 34.000057],
          [-104.156269, 33.995413],
          [-104.155841, 34.08332],
          [-103.945949, 34.082784]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US35009",
      state: "35",
      county: "009",
      name: "Curry County",
      censusArea: 1404.797,
      stateName: "New Mexico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.042521, 34.954101],
          [-103.042521, 34.899546],
          [-103.042781, 34.850243],
          [-103.04277, 34.792224],
          [-103.042769, 34.747361],
          [-103.042827, 34.671188],
          [-103.043072, 34.619782],
          [-103.043594, 34.46266],
          [-103.043589, 34.459774],
          [-103.043588, 34.459662],
          [-103.043582, 34.455657],
          [-103.043538, 34.405463],
          [-103.043583, 34.400678],
          [-103.043611, 34.397105],
          [-103.043585, 34.393716],
          [-103.043613, 34.390442],
          [-103.043613, 34.388679],
          [-103.043614, 34.384969],
          [-103.04363, 34.38469],
          [-103.043693, 34.383578],
          [-103.043919, 34.380916],
          [-103.043944, 34.37966],
          [-103.043946, 34.379555],
          [-103.043979, 34.312764],
          [-103.043979, 34.312749],
          [-103.043936, 34.302585],
          [-103.740017, 34.303076],
          [-103.738232, 34.604671],
          [-103.706528, 34.604935],
          [-103.705788, 34.692463],
          [-103.496024, 34.691473],
          [-103.495379, 34.778699],
          [-103.390274, 34.778122],
          [-103.389359, 34.86532],
          [-103.284157, 34.86499],
          [-103.282998, 34.953957],
          [-103.042521, 34.954101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US30061",
      state: "30",
      county: "061",
      name: "Mineral County",
      censusArea: 1219.437,
      stateName: "Montana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.797235, 47.268901],
          [-114.775739, 47.225548],
          [-114.71217, 47.22559],
          [-114.712026, 47.182124],
          [-114.586753, 47.12369],
          [-114.565985, 47.067339],
          [-114.483637, 47.067326],
          [-114.419662, 47.023267],
          [-114.525403, 47.009321],
          [-114.565991, 46.963758],
          [-114.548929, 46.747366],
          [-114.675505, 46.719516],
          [-114.69063, 46.719961],
          [-114.717645, 46.713994],
          [-114.739253, 46.715167],
          [-114.76718, 46.738828],
          [-114.825923, 46.781949],
          [-114.853279, 46.799794],
          [-114.888146, 46.808573],
          [-114.920459, 46.827697],
          [-114.927837, 46.83599],
          [-114.938713, 46.869021],
          [-114.936015, 46.899761],
          [-114.975789, 46.932865],
          [-115.072985, 47.01376],
          [-115.099178, 47.048129],
          [-115.193221, 47.133026],
          [-115.200547, 47.139154],
          [-115.266723, 47.181101],
          [-115.29211, 47.209861],
          [-115.294785, 47.220914],
          [-115.320184, 47.255717],
          [-115.339201, 47.261623],
          [-115.421645, 47.271736],
          [-115.479255, 47.282089],
          [-115.52369, 47.298919],
          [-115.551309, 47.333856],
          [-115.561439, 47.351887],
          [-115.576836, 47.366825],
          [-115.661341, 47.402663],
          [-115.628695, 47.479641],
          [-115.282381, 47.486897],
          [-114.979291, 47.395519],
          [-114.968943, 47.312617],
          [-114.797235, 47.268901]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38001",
      state: "38",
      county: "001",
      name: "Adams County",
      censusArea: 987.619,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.998618, 45.944556],
          [-101.998703, 45.944557],
          [-102.000425, 45.944581],
          [-102.000656, 45.944515],
          [-102.000679, 45.944515],
          [-102.06093, 45.944622],
          [-102.085122, 45.944642],
          [-102.087555, 45.944598],
          [-102.124628, 45.944813],
          [-102.125429, 45.944652],
          [-102.135269, 45.944586],
          [-102.145356, 45.944659],
          [-102.156393, 45.944663],
          [-102.157965, 45.944641],
          [-102.159439, 45.944641],
          [-102.176698, 45.944622],
          [-102.176993, 45.944622],
          [-102.217867, 45.944711],
          [-102.32823, 45.944806],
          [-102.353384, 45.944984],
          [-102.354283, 45.944901],
          [-102.392696, 45.944951],
          [-102.392767, 45.944979],
          [-102.396359, 45.944916],
          [-102.398575, 45.944868],
          [-102.406176, 45.944997],
          [-102.410346, 45.945079],
          [-102.420173, 45.94507],
          [-102.425358, 45.94499],
          [-102.425397, 45.945041],
          [-102.446419, 45.945083],
          [-102.459586, 45.945081],
          [-102.467563, 45.945159],
          [-102.476024, 45.945183],
          [-102.550947, 45.945015],
          [-102.558579, 45.945129],
          [-102.642555, 45.945404],
          [-102.65162, 45.94545],
          [-102.666684, 45.945307],
          [-102.672474, 45.945244],
          [-102.674077, 45.945274],
          [-102.704871, 45.945072],
          [-102.880252, 45.945069],
          [-102.920482, 45.945038],
          [-102.94207, 45.945092],
          [-102.989902, 45.945211],
          [-102.995345, 45.945166],
          [-102.995668, 45.945167],
          [-102.995454, 46.280713],
          [-102.969946, 46.281144],
          [-102.924547, 46.281519],
          [-102.497449, 46.283196],
          [-102.497475, 46.206077],
          [-101.997888, 46.20548],
          [-101.998575, 46.053461],
          [-101.998618, 45.944556]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09011",
      state: "09",
      county: "011",
      name: "New London County",
      censusArea: 664.878,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.157546, 41.657627],
          [-72.153391, 41.665643],
          [-72.120328, 41.661925],
          [-72.105651, 41.659528],
          [-72.103523, 41.659305],
          [-72.062051, 41.652239],
          [-71.954983, 41.634799],
          [-71.789465, 41.640017],
          [-71.789356, 41.59691],
          [-71.789359, 41.596852],
          [-71.797674, 41.41691],
          [-71.797683, 41.416709],
          [-71.81839, 41.419599],
          [-71.839649, 41.412119],
          [-71.842563, 41.409855],
          [-71.843472, 41.40583],
          [-71.842131, 41.395359],
          [-71.833443, 41.384524],
          [-71.831613, 41.370899],
          [-71.83124, 41.344645],
          [-71.83253, 41.341204],
          [-71.839782, 41.33469],
          [-71.85957, 41.322399],
          [-71.868235, 41.330941],
          [-71.886302, 41.33641],
          [-71.91671, 41.332217],
          [-71.922092, 41.334518],
          [-71.923282, 41.335113],
          [-71.936284, 41.337959],
          [-71.945652, 41.337799],
          [-71.956747, 41.329871],
          [-71.970955, 41.324526],
          [-71.979447, 41.329987],
          [-71.982194, 41.329861],
          [-71.988153, 41.320577],
          [-72.021898, 41.316838],
          [-72.084487, 41.319634],
          [-72.094443, 41.314164],
          [-72.09982, 41.306998],
          [-72.11182, 41.299098],
          [-72.134221, 41.299398],
          [-72.16158, 41.310262],
          [-72.173922, 41.317597],
          [-72.177622, 41.322497],
          [-72.184122, 41.323997],
          [-72.191022, 41.323197],
          [-72.201422, 41.315697],
          [-72.203022, 41.313197],
          [-72.204022, 41.299097],
          [-72.212924, 41.291365],
          [-72.225276, 41.299047],
          [-72.235531, 41.300413],
          [-72.248161, 41.299488],
          [-72.251895, 41.29862],
          [-72.250515, 41.294386],
          [-72.251323, 41.289997],
          [-72.261487, 41.282926],
          [-72.31776, 41.277782],
          [-72.327595, 41.27846],
          [-72.333894, 41.282916],
          [-72.34146, 41.28011],
          [-72.43371, 41.423995],
          [-72.305531, 41.436282],
          [-72.322931, 41.518119],
          [-72.430767, 41.524567],
          [-72.46673, 41.583899],
          [-72.417229, 41.590598],
          [-72.410629, 41.601798],
          [-72.239559, 41.714191],
          [-72.237947, 41.713167],
          [-72.186651, 41.676283],
          [-72.157546, 41.657627]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US10001",
      state: "10",
      county: "001",
      name: "Kent County",
      censusArea: 586.179,
      stateName: "Delaware"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.747671, 39.143345],
          [-75.755953, 39.245958],
          [-75.755962, 39.246069],
          [-75.760104, 39.296817],
          [-75.584341, 39.308718],
          [-75.512372, 39.365656],
          [-75.511788, 39.365191],
          [-75.505276, 39.359169],
          [-75.494158, 39.354613],
          [-75.491797, 39.351845],
          [-75.494122, 39.34658],
          [-75.493148, 39.345527],
          [-75.491688, 39.343963],
          [-75.490377, 39.342818],
          [-75.479845, 39.337472],
          [-75.479963, 39.336577],
          [-75.469324, 39.33082],
          [-75.460423, 39.328236],
          [-75.439027, 39.313384],
          [-75.436936, 39.309379],
          [-75.435551, 39.297546],
          [-75.435374, 39.296676],
          [-75.427953, 39.285049],
          [-75.408376, 39.264698],
          [-75.402964, 39.254626],
          [-75.404823, 39.245898],
          [-75.405927, 39.243631],
          [-75.405716, 39.223834],
          [-75.404745, 39.222666],
          [-75.396892, 39.216141],
          [-75.393015, 39.204512],
          [-75.39479, 39.188354],
          [-75.398584, 39.186616],
          [-75.400144, 39.186456],
          [-75.408266, 39.174625],
          [-75.410625, 39.156246],
          [-75.401193, 39.088762],
          [-75.402035, 39.066885],
          [-75.400294, 39.065645],
          [-75.395806, 39.059211],
          [-75.396277, 39.057884],
          [-75.387914, 39.051174],
          [-75.379873, 39.04879],
          [-75.345763, 39.024857],
          [-75.34089, 39.01996],
          [-75.318354, 38.988191],
          [-75.314951, 38.980775],
          [-75.311607, 38.967637],
          [-75.312546, 38.951065],
          [-75.312546, 38.94928],
          [-75.311923, 38.945917],
          [-75.381339, 38.961285],
          [-75.555013, 38.835649],
          [-75.722599, 38.829859],
          [-75.72261, 38.830008],
          [-75.722882, 38.833156],
          [-75.724002, 38.846682],
          [-75.724061, 38.847781],
          [-75.725565, 38.868152],
          [-75.725829, 38.869296],
          [-75.745793, 39.114935],
          [-75.746121, 39.120318],
          [-75.747668, 39.143306],
          [-75.747671, 39.143345]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US10003",
      state: "10",
      county: "003",
      name: "New Castle County",
      censusArea: 426.286,
      stateName: "Delaware"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-75.564927, 39.583248],
            [-75.576271, 39.588144],
            [-75.578719, 39.591504],
            [-75.579615, 39.598656],
            [-75.565823, 39.590608],
            [-75.564927, 39.583248]
          ]
        ],
        [
          [
            [-75.55587, 39.605824],
            [-75.561934, 39.605216],
            [-75.567694, 39.613744],
            [-75.571759, 39.623584],
            [-75.570798, 39.626768],
            [-75.559446, 39.629812],
            [-75.55587, 39.605824]
          ]
        ],
        [
          [
            [-75.594846, 39.837286],
            [-75.593666, 39.837455],
            [-75.593082, 39.8375],
            [-75.5799, 39.838522],
            [-75.579849, 39.838526],
            [-75.539346, 39.838211],
            [-75.498843, 39.833312],
            [-75.45374, 39.820312],
            [-75.428038, 39.809212],
            [-75.415041, 39.801786],
            [-75.416095, 39.79583],
            [-75.437938, 39.783413],
            [-75.440623, 39.780926],
            [-75.448135, 39.773969],
            [-75.466249, 39.750769],
            [-75.466263, 39.750737],
            [-75.469239, 39.743613],
            [-75.474168, 39.735473],
            [-75.475384, 39.731057],
            [-75.504042, 39.698313],
            [-75.509742, 39.686113],
            [-75.529744, 39.692613],
            [-75.562246, 39.656712],
            [-75.587147, 39.651012],
            [-75.611969, 39.621968],
            [-75.613153, 39.62096],
            [-75.613377, 39.620288],
            [-75.614065, 39.61832],
            [-75.614929, 39.615952],
            [-75.614273, 39.61464],
            [-75.613345, 39.613056],
            [-75.613665, 39.61256],
            [-75.613233, 39.607408],
            [-75.613477, 39.606861],
            [-75.613473, 39.606832],
            [-75.613793, 39.606192],
            [-75.611905, 39.597568],
            [-75.611873, 39.597408],
            [-75.60464, 39.58992],
            [-75.603584, 39.58896],
            [-75.592224, 39.583568],
            [-75.591984, 39.583248],
            [-75.587744, 39.580672],
            [-75.5872, 39.580256],
            [-75.586608, 39.57888],
            [-75.586016, 39.578448],
            [-75.571599, 39.567728],
            [-75.570783, 39.56728],
            [-75.563034, 39.56224],
            [-75.564649, 39.559922],
            [-75.565636, 39.558509],
            [-75.569359, 39.540589],
            [-75.569418, 39.539124],
            [-75.570362, 39.527223],
            [-75.560728, 39.520472],
            [-75.566933, 39.508273],
            [-75.576436, 39.509195],
            [-75.587729, 39.496353],
            [-75.587729, 39.495369],
            [-75.593068, 39.479186],
            [-75.593068, 39.477996],
            [-75.589901, 39.462022],
            [-75.589439, 39.460812],
            [-75.580185, 39.450786],
            [-75.578914, 39.44788],
            [-75.570985, 39.442486],
            [-75.57183, 39.438897],
            [-75.55589, 39.430351],
            [-75.538512, 39.416502],
            [-75.535977, 39.409384],
            [-75.523583, 39.391583],
            [-75.521682, 39.387871],
            [-75.512996, 39.366153],
            [-75.512372, 39.365656],
            [-75.584341, 39.308718],
            [-75.760104, 39.296817],
            [-75.766667, 39.377216],
            [-75.766693, 39.377537],
            [-75.77924, 39.534737],
            [-75.779383, 39.536522],
            [-75.78689, 39.630575],
            [-75.78745, 39.637455],
            [-75.788616, 39.680742],
            [-75.788658, 39.681911],
            [-75.788395, 39.700031],
            [-75.788395, 39.700287],
            [-75.788359, 39.721811],
            [-75.739705, 39.772623],
            [-75.716969, 39.791998],
            [-75.685991, 39.811054],
            [-75.662822, 39.82115],
            [-75.641518, 39.828363],
            [-75.634706, 39.830164],
            [-75.595756, 39.837156],
            [-75.594846, 39.837286]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12009",
      state: "12",
      county: "009",
      name: "Brevard County",
      censusArea: 1015.664,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.631314, 28.655188],
          [-80.61679, 28.634561],
          [-80.583884, 28.597705],
          [-80.574868, 28.585166],
          [-80.567361, 28.562353],
          [-80.560973, 28.530736],
          [-80.536115, 28.478647],
          [-80.525094, 28.459454],
          [-80.526732, 28.451705],
          [-80.562877, 28.437779],
          [-80.574136, 28.427764],
          [-80.587813, 28.410856],
          [-80.596174, 28.390682],
          [-80.603374, 28.363983],
          [-80.606874, 28.336484],
          [-80.608074, 28.311285],
          [-80.604214, 28.257733],
          [-80.589975, 28.17799],
          [-80.566432, 28.09563],
          [-80.547675, 28.048795],
          [-80.508871, 27.970477],
          [-80.446973, 27.861954],
          [-80.447084, 27.860755],
          [-80.509075, 27.822058],
          [-80.868881, 27.822522],
          [-80.862815, 28.347221],
          [-80.862881, 28.347439],
          [-80.862908, 28.347487],
          [-80.88089, 28.503633],
          [-80.98725, 28.612997],
          [-80.964466, 28.612992],
          [-80.967895, 28.790197],
          [-80.732244, 28.791237],
          [-80.713183, 28.761997],
          [-80.713108, 28.761882],
          [-80.712714, 28.761277],
          [-80.709725, 28.756692],
          [-80.708545, 28.755202],
          [-80.672232, 28.709363],
          [-80.663183, 28.69794],
          [-80.647924, 28.678677],
          [-80.64776, 28.67847],
          [-80.647288, 28.677875],
          [-80.645839, 28.675817],
          [-80.641436, 28.669564],
          [-80.639019, 28.666131],
          [-80.631314, 28.655188]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12075",
      state: "12",
      county: "075",
      name: "Levy County",
      censusArea: 1118.206,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.405152, 29.33026],
          [-82.404594, 29.27886],
          [-82.403237, 29.215623],
          [-82.53486, 29.21468],
          [-82.535591, 29.044855],
          [-82.612653, 29.009271],
          [-82.689814, 29.033962],
          [-82.758906, 28.993277],
          [-82.760551, 28.993087],
          [-82.764055, 28.999707],
          [-82.759378, 29.006619],
          [-82.753513, 29.026496],
          [-82.759704, 29.054192],
          [-82.783328, 29.064619],
          [-82.780558, 29.07358],
          [-82.816925, 29.076215],
          [-82.823659, 29.098902],
          [-82.809483, 29.10462],
          [-82.801166, 29.105103],
          [-82.799117, 29.110647],
          [-82.798876, 29.114504],
          [-82.805703, 29.129848],
          [-82.804736, 29.146624],
          [-82.827073, 29.158425],
          [-82.858179, 29.162275],
          [-82.887211, 29.161741],
          [-82.922613, 29.169769],
          [-82.932405, 29.167891],
          [-82.945302, 29.167821],
          [-82.974676, 29.17091],
          [-82.979522, 29.171817],
          [-82.987162, 29.180094],
          [-82.991653, 29.180664],
          [-82.996144, 29.178074],
          [-83.018212, 29.151417],
          [-83.019071, 29.141324],
          [-83.030453, 29.134023],
          [-83.053207, 29.130839],
          [-83.056867, 29.146263],
          [-83.068249, 29.153135],
          [-83.060947, 29.170959],
          [-83.061162, 29.176113],
          [-83.065242, 29.184489],
          [-83.078986, 29.196944],
          [-83.087839, 29.21642],
          [-83.074734, 29.247975],
          [-83.077265, 29.255331],
          [-83.089013, 29.266502],
          [-83.107477, 29.268889],
          [-83.125567, 29.278845],
          [-83.128027, 29.282733],
          [-83.146445, 29.289194],
          [-83.149764, 29.289768],
          [-83.16073, 29.286611],
          [-83.166091, 29.28888],
          [-83.068522, 29.343953],
          [-82.984371, 29.471004],
          [-82.937374, 29.591312],
          [-82.656301, 29.564811],
          [-82.556511, 29.511982],
          [-82.556766, 29.480055],
          [-82.40662, 29.485048],
          [-82.405152, 29.33026]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05031",
      state: "05",
      county: "031",
      name: "Craighead County",
      censusArea: 707.206,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.288947, 35.996418],
          [-90.286736, 35.699914],
          [-91.036967, 35.708418],
          [-91.032499, 35.88507],
          [-90.872785, 35.88087],
          [-90.852064, 35.968092],
          [-90.387378, 35.964708],
          [-90.368718, 35.995812],
          [-90.342616, 35.995895],
          [-90.339434, 35.996033],
          [-90.292376, 35.996397],
          [-90.288947, 35.996418]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05035",
      state: "05",
      county: "035",
      name: "Crittenden County",
      censusArea: 609.76,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.074262, 35.218316],
          [-90.073354, 35.211004],
          [-90.064612, 35.140621],
          [-90.065392, 35.137691],
          [-90.08342, 35.12167],
          [-90.09061, 35.118287],
          [-90.100593, 35.116691],
          [-90.109393, 35.118891],
          [-90.142794, 35.135091],
          [-90.160058, 35.12883],
          [-90.174594, 35.116682],
          [-90.193859, 35.061646],
          [-90.295596, 35.040093],
          [-90.309877, 35.00975],
          [-90.309297, 34.995694],
          [-90.253969, 34.954988],
          [-90.244476, 34.937596],
          [-90.250095, 34.90732],
          [-90.293918, 34.860563],
          [-90.307384, 34.846195],
          [-90.407964, 34.832767],
          [-90.40854, 34.904121],
          [-90.400434, 35.148196],
          [-90.502993, 35.144214],
          [-90.502429, 35.441726],
          [-90.500104, 35.441763],
          [-90.286899, 35.438573],
          [-90.152386, 35.436789],
          [-90.169002, 35.421853],
          [-90.179265, 35.385194],
          [-90.178341, 35.382092],
          [-90.166246, 35.374745],
          [-90.143633, 35.374745],
          [-90.13551, 35.376668],
          [-90.093589, 35.393333],
          [-90.089612, 35.379842],
          [-90.086691, 35.369935],
          [-90.114893, 35.303887],
          [-90.153394, 35.302588],
          [-90.158913, 35.300637],
          [-90.163812, 35.296115],
          [-90.168871, 35.281997],
          [-90.166594, 35.274588],
          [-90.158865, 35.262577],
          [-90.152094, 35.255989],
          [-90.140394, 35.252289],
          [-90.116493, 35.255788],
          [-90.105093, 35.254288],
          [-90.097947, 35.249983],
          [-90.07741, 35.225479],
          [-90.074262, 35.218316]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05041",
      state: "05",
      county: "041",
      name: "Desha County",
      censusArea: 768.152,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.224121, 33.567369],
          [-91.228287, 33.559937],
          [-91.244582, 33.561425],
          [-91.24576, 33.517148],
          [-91.35081, 33.519476],
          [-91.34954, 33.563646],
          [-91.453438, 33.563703],
          [-91.450325, 33.780415],
          [-91.557648, 33.783384],
          [-91.548668, 33.976799],
          [-91.421715, 34.014472],
          [-91.347628, 33.954941],
          [-91.132686, 33.955221],
          [-91.194278, 34.070938],
          [-91.117905, 34.119105],
          [-90.95517, 34.118833],
          [-90.933116, 34.124488],
          [-90.931969, 34.120017],
          [-90.928793, 34.107631],
          [-90.912057, 34.095512],
          [-90.888396, 34.087433],
          [-90.879163, 34.065503],
          [-90.887413, 34.032505],
          [-90.964708, 34.007952],
          [-91.01889, 34.003151],
          [-91.089119, 33.972653],
          [-91.088164, 33.960078],
          [-91.036821, 33.914656],
          [-91.067511, 33.840443],
          [-91.139869, 33.777117],
          [-91.144682, 33.750108],
          [-91.136118, 33.728632],
          [-91.126805, 33.707814],
          [-91.1279, 33.695762],
          [-91.132831, 33.688092],
          [-91.139953, 33.683709],
          [-91.14817, 33.679327],
          [-91.17611, 33.684257],
          [-91.185971, 33.679327],
          [-91.18871, 33.669466],
          [-91.193093, 33.656866],
          [-91.186519, 33.645909],
          [-91.157484, 33.626187],
          [-91.130445, 33.606034],
          [-91.134043, 33.594489],
          [-91.152148, 33.582721],
          [-91.224121, 33.567369]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05057",
      state: "05",
      county: "057",
      name: "Hempstead County",
      censusArea: 727.52,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.957315, 33.75094],
          [-93.831452, 33.747709],
          [-93.823076, 34.008893],
          [-93.528752, 33.943171],
          [-93.456709, 33.956837],
          [-93.483097, 33.476075],
          [-93.722867, 33.481963],
          [-93.772686, 33.498513],
          [-93.773641, 33.504671],
          [-93.780289, 33.519624],
          [-93.778832, 33.524717],
          [-93.792415, 33.535869],
          [-93.792725, 33.541963],
          [-93.792725, 33.547109],
          [-93.79833, 33.542149],
          [-93.807741, 33.545243],
          [-93.803789, 33.549384],
          [-93.798272, 33.552493],
          [-93.814202, 33.56475],
          [-93.827726, 33.57162],
          [-93.823259, 33.580544],
          [-93.817579, 33.586153],
          [-93.812503, 33.603351],
          [-93.816087, 33.610843],
          [-93.822178, 33.613548],
          [-93.825884, 33.609853],
          [-93.965637, 33.669736],
          [-93.957315, 33.75094]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05063",
      state: "05",
      county: "063",
      name: "Independence County",
      censusArea: 763.953,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.793958, 35.534114],
          [-91.789823, 35.711734],
          [-91.837179, 35.704932],
          [-91.870983, 35.810562],
          [-91.849448, 35.867411],
          [-91.753345, 35.942883],
          [-91.710552, 35.941759],
          [-91.463198, 35.93681],
          [-91.464678, 35.892497],
          [-91.35723, 35.890621],
          [-91.198556, 35.890024],
          [-91.232392, 35.812664],
          [-91.348281, 35.719127],
          [-91.36957, 35.526362],
          [-91.58188, 35.530447],
          [-91.793958, 35.534114]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05069",
      state: "05",
      county: "069",
      name: "Jefferson County",
      censusArea: 870.746,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.029996, 34.489352],
          [-91.705185, 34.48267],
          [-91.711795, 34.233919],
          [-91.540178, 34.224599],
          [-91.445614, 34.080147],
          [-91.727276, 34.174093],
          [-91.74499, 34.094021],
          [-91.953629, 34.09338],
          [-91.953799, 34.064141],
          [-92.233376, 34.062312],
          [-92.212906, 34.2304],
          [-92.208041, 34.492765],
          [-92.029996, 34.489352]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05075",
      state: "05",
      county: "075",
      name: "Lawrence County",
      censusArea: 587.614,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.35723, 35.890621],
          [-91.349834, 36.230967],
          [-91.25892, 36.25788],
          [-91.082681, 36.150871],
          [-90.809816, 36.149567],
          [-90.747425, 36.149116],
          [-90.820763, 36.07977],
          [-90.852064, 35.968092],
          [-90.872785, 35.88087],
          [-91.032499, 35.88507],
          [-91.169061, 35.889794],
          [-91.198556, 35.890024],
          [-91.35723, 35.890621]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05077",
      state: "05",
      county: "077",
      name: "Lee County",
      censusArea: 602.621,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.407964, 34.832767],
          [-90.414864, 34.831846],
          [-90.423879, 34.834606],
          [-90.436431, 34.85506],
          [-90.461451, 34.856728],
          [-90.473961, 34.852558],
          [-90.478131, 34.832542],
          [-90.467289, 34.782502],
          [-90.479799, 34.769158],
          [-90.494811, 34.76749],
          [-90.501325, 34.769931],
          [-90.519831, 34.782502],
          [-90.537345, 34.78417],
          [-90.549855, 34.76332],
          [-90.554859, 34.727458],
          [-90.533175, 34.707442],
          [-90.509823, 34.689928],
          [-90.508989, 34.679086],
          [-90.521499, 34.66324],
          [-90.539013, 34.65907],
          [-90.549855, 34.662406],
          [-90.564033, 34.665742],
          [-90.576543, 34.657402],
          [-90.575786, 34.641749],
          [-90.583224, 34.641389],
          [-91.051918, 34.645721],
          [-91.049474, 34.734184],
          [-91.102542, 34.734864],
          [-91.099961, 34.86712],
          [-91.099201, 34.911796],
          [-90.40854, 34.904121],
          [-90.407964, 34.832767]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US04025",
      state: "04",
      county: "025",
      name: "Yavapai County",
      censusArea: 8123.499,
      stateName: "Arizona"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.861997, 34.015108],
          [-112.164758, 34.04817],
          [-112.256668, 33.905526],
          [-112.26047, 33.903592],
          [-112.264425, 33.899094],
          [-112.27604, 33.882312],
          [-112.341876, 33.898958],
          [-112.743951, 33.999898],
          [-113.333753, 33.999227],
          [-113.333316, 34.00103],
          [-113.333508, 34.317877],
          [-113.332713, 35.00011],
          [-113.334161, 35.528037],
          [-113.329569, 35.528108],
          [-113.320367, 35.53057],
          [-113.301303, 35.531118],
          [-113.174458, 35.506445],
          [-113.148055, 35.478595],
          [-112.975855, 35.392904],
          [-112.95341, 35.35611],
          [-112.754838, 35.306301],
          [-112.694846, 35.311586],
          [-112.577399, 35.243394],
          [-112.4727, 35.238312],
          [-112.440751, 35.260468],
          [-112.4423, 35.149408],
          [-112.335716, 35.148279],
          [-112.334536, 34.973024],
          [-111.809215, 34.976215],
          [-111.777815, 34.981315],
          [-111.77791, 34.803318],
          [-111.552106, 34.80252],
          [-111.555124, 34.472196],
          [-111.659514, 34.386424],
          [-111.720728, 34.161704],
          [-111.477853, 34.151108],
          [-111.494778, 33.999817],
          [-111.72531, 33.999849],
          [-111.861997, 34.015108]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05005",
      state: "05",
      county: "005",
      name: "Baxter County",
      censusArea: 554.283,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.150295, 36.498634],
          [-92.159215, 36.261311],
          [-92.15699, 36.261248],
          [-92.19543, 36.132798],
          [-92.197649, 36.059287],
          [-92.304951, 36.06108],
          [-92.30722, 35.974022],
          [-92.414324, 35.975865],
          [-92.412542, 36.027228],
          [-92.411502, 36.062756],
          [-92.411852, 36.162123],
          [-92.469262, 36.250904],
          [-92.543034, 36.269342],
          [-92.593128, 36.358337],
          [-92.533175, 36.386563],
          [-92.529145, 36.497739],
          [-92.318415, 36.497711],
          [-92.309424, 36.497894],
          [-92.216412, 36.498417],
          [-92.214143, 36.498372],
          [-92.211449, 36.498395],
          [-92.199396, 36.498351],
          [-92.150295, 36.498634]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05007",
      state: "05",
      county: "007",
      name: "Benton County",
      censusArea: 847.357,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.552184, 36.102235],
          [-94.561165, 36.15211],
          [-94.562803, 36.161749],
          [-94.562828, 36.161895],
          [-94.565655, 36.178439],
          [-94.566588, 36.183774],
          [-94.571253, 36.210901],
          [-94.571806, 36.213748],
          [-94.574395, 36.229996],
          [-94.57488, 36.232741],
          [-94.575071, 36.233682],
          [-94.576003, 36.24007],
          [-94.577899, 36.249548],
          [-94.577883, 36.25008],
          [-94.5862, 36.299969],
          [-94.593397, 36.345742],
          [-94.599723, 36.387587],
          [-94.601984, 36.40212],
          [-94.602623, 36.405283],
          [-94.605408, 36.421949],
          [-94.611609, 36.461528],
          [-94.61383, 36.476248],
          [-94.615311, 36.484992],
          [-94.617919, 36.499414],
          [-94.55929, 36.499496],
          [-94.519478, 36.499214],
          [-94.111473, 36.498597],
          [-94.110673, 36.498587],
          [-94.100252, 36.49867],
          [-94.098588, 36.498676],
          [-94.077089, 36.49873],
          [-93.96392, 36.498717],
          [-93.95919, 36.498717],
          [-93.92184, 36.498718],
          [-93.906128, 36.498718],
          [-93.866758, 36.498789],
          [-93.861698, 36.339266],
          [-93.815605, 36.306563],
          [-93.887775, 36.235046],
          [-93.973298, 36.203852],
          [-94.333565, 36.214727],
          [-94.391125, 36.099396],
          [-94.552184, 36.102235]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16073",
      state: "16",
      county: "073",
      name: "Owyhee County",
      censusArea: 7665.511,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.026717, 43.675523],
          [-117.026623, 43.680865],
          [-116.841528, 43.593168],
          [-116.780671, 43.477217],
          [-116.511722, 43.290253],
          [-116.433111, 43.296436],
          [-116.385964, 43.193445],
          [-116.266168, 43.112553],
          [-116.012092, 42.949917],
          [-115.965669, 42.943725],
          [-115.926546, 43.001526],
          [-115.76991, 42.938948],
          [-115.641066, 42.95536],
          [-115.60373, 42.937249],
          [-115.44157, 42.930904],
          [-115.454314, 42.767732],
          [-115.037716, 42.76848],
          [-115.037694, 42.749936],
          [-115.038256, 41.996025],
          [-115.98688, 41.998534],
          [-116.012212, 41.998035],
          [-116.012219, 41.998048],
          [-116.018945, 41.997722],
          [-116.01896, 41.997762],
          [-116.030758, 41.997383],
          [-116.030754, 41.997399],
          [-116.03857, 41.997413],
          [-116.038602, 41.99746],
          [-116.160833, 41.997508],
          [-116.163931, 41.997555],
          [-116.463528, 41.996547],
          [-116.483094, 41.996885],
          [-116.485823, 41.996861],
          [-116.510452, 41.997096],
          [-116.586937, 41.99737],
          [-116.62677, 41.99775],
          [-117.018294, 41.999358],
          [-117.026222, 42.000252],
          [-117.026331, 42.807015],
          [-117.026303, 42.80717],
          [-117.026253, 42.807447],
          [-117.026683, 43.024876],
          [-117.026652, 43.025128],
          [-117.026746, 43.577526],
          [-117.026774, 43.578674],
          [-117.026922, 43.593632],
          [-117.026889, 43.596033],
          [-117.026824, 43.600357],
          [-117.02676, 43.601912],
          [-117.026789, 43.610669],
          [-117.026937, 43.617614],
          [-117.027001, 43.621032],
          [-117.026905, 43.62488],
          [-117.026705, 43.631659],
          [-117.026661, 43.664385],
          [-117.026717, 43.675523]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16085",
      state: "16",
      county: "085",
      name: "Valley County",
      censusArea: 3664.515,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.812434, 44.808138],
          [-115.009938, 44.713369],
          [-115.088961, 44.770916],
          [-115.151795, 44.728171],
          [-115.168309, 44.647451],
          [-115.279663, 44.612095],
          [-115.304751, 44.580026],
          [-115.246996, 44.519552],
          [-115.228241, 44.422868],
          [-115.294462, 44.339411],
          [-115.401528, 44.259374],
          [-115.52015, 44.235196],
          [-116.103075, 44.236624],
          [-116.112427, 44.147834],
          [-116.212132, 44.151354],
          [-116.212071, 44.324277],
          [-116.152289, 44.324388],
          [-116.157096, 44.498966],
          [-116.157064, 44.703291],
          [-116.205988, 44.767155],
          [-116.186161, 44.887793],
          [-116.089397, 45.063748],
          [-116.144531, 45.107619],
          [-116.121743, 45.15871],
          [-115.974957, 45.219699],
          [-115.975756, 45.195699],
          [-114.694126, 45.197114],
          [-114.722627, 45.178914],
          [-114.732212, 44.880351],
          [-114.812434, 44.808138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16087",
      state: "16",
      county: "087",
      name: "Washington County",
      censusArea: 1452.982,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-117.197506, 44.29728],
          [-117.196597, 44.30228],
          [-117.194827, 44.31054],
          [-117.191546, 44.329621],
          [-117.189769, 44.336585],
          [-117.214889, 44.466901],
          [-117.208454, 44.485928],
          [-117.156489, 44.528312],
          [-117.149242, 44.536151],
          [-117.144161, 44.545647],
          [-117.1159, 44.623339],
          [-117.095868, 44.664737],
          [-117.062273, 44.727143],
          [-117.044217, 44.74514],
          [-117.03827, 44.748179],
          [-117.016216, 44.755572],
          [-116.931499, 44.792281],
          [-116.891463, 44.840531],
          [-116.624783, 44.837841],
          [-116.624297, 44.662852],
          [-116.528382, 44.614886],
          [-116.527157, 44.490695],
          [-116.407496, 44.443849],
          [-116.299757, 44.444107],
          [-116.358532, 44.151066],
          [-116.452012, 44.151417],
          [-116.897145, 44.152537],
          [-116.895931, 44.154295],
          [-116.894083, 44.160191],
          [-116.895757, 44.171267],
          [-116.902752, 44.179467],
          [-116.975905, 44.242844],
          [-116.98687, 44.245477],
          [-117.027558, 44.248881],
          [-117.03317, 44.248192],
          [-117.041144, 44.243653],
          [-117.170342, 44.25889],
          [-117.190107, 44.273958],
          [-117.196597, 44.287529],
          [-117.197777, 44.295789],
          [-117.197506, 44.29728]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17013",
      state: "17",
      county: "013",
      name: "Calhoun County",
      censusArea: 253.825,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.467784, 38.961809],
          [-90.472122, 38.958838],
          [-90.482419, 38.94446],
          [-90.482725, 38.934712],
          [-90.486974, 38.925982],
          [-90.507451, 38.902767],
          [-90.54403, 38.87505],
          [-90.555693, 38.870785],
          [-90.566557, 38.868847],
          [-90.583388, 38.86903],
          [-90.625122, 38.888654],
          [-90.657254, 38.92027],
          [-90.663372, 38.928042],
          [-90.66587, 38.934195],
          [-90.71158, 39.046798],
          [-90.713629, 39.053977],
          [-90.712541, 39.057064],
          [-90.682744, 39.088348],
          [-90.681086, 39.10059],
          [-90.718996, 39.224973],
          [-90.726981, 39.251173],
          [-90.72996, 39.255894],
          [-90.799346, 39.313087],
          [-90.842175, 39.341932],
          [-90.882706, 39.362254],
          [-90.939983, 39.393555],
          [-90.613694, 39.395775],
          [-90.603569, 39.117592],
          [-90.570169, 38.99329],
          [-90.450809, 38.967758],
          [-90.467784, 38.961809]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17021",
      state: "17",
      county: "021",
      name: "Christian County",
      censusArea: 709.377,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.252325, 39.820416],
          [-89.248609, 39.825723],
          [-89.240092, 39.823137],
          [-89.233523, 39.813159],
          [-89.217523, 39.813224],
          [-89.141817, 39.80091],
          [-89.139125, 39.655131],
          [-89.025691, 39.654637],
          [-89.02568, 39.654183],
          [-89.025469, 39.464462],
          [-89.025381, 39.345896],
          [-89.139807, 39.348888],
          [-89.530836, 39.348864],
          [-89.533655, 39.524592],
          [-89.535028, 39.641138],
          [-89.425051, 39.683696],
          [-89.392858, 39.74385],
          [-89.252325, 39.820416]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17027",
      state: "17",
      county: "027",
      name: "Clinton County",
      censusArea: 474.085,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.254237, 38.742019],
          [-89.138393, 38.736331],
          [-89.143898, 38.503088],
          [-89.360248, 38.516522],
          [-89.481556, 38.468465],
          [-89.615212, 38.472072],
          [-89.704417, 38.415667],
          [-89.707024, 38.655152],
          [-89.595103, 38.655948],
          [-89.597321, 38.743236],
          [-89.481642, 38.740532],
          [-89.254237, 38.742019]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16031",
      state: "16",
      county: "031",
      name: "Cassia County",
      censusArea: 2565.081,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.00082, 41.998223],
          [-113.000821, 41.998223],
          [-113.396497, 41.99425],
          [-113.40223, 41.994161],
          [-113.431563, 41.993799],
          [-113.76453, 41.989459],
          [-113.796082, 41.989104],
          [-113.893261, 41.988057],
          [-114.041723, 41.99372],
          [-114.048246, 41.993721],
          [-114.048257, 41.993814],
          [-114.061774, 41.993797],
          [-114.061763, 41.993939],
          [-114.107259, 41.993831],
          [-114.107428, 41.993965],
          [-114.281854, 41.994264],
          [-114.285363, 42.417332],
          [-114.069053, 42.417124],
          [-114.06708, 42.51452],
          [-114.000691, 42.526434],
          [-113.985215, 42.534136],
          [-113.959446, 42.53502],
          [-113.956046, 42.530243],
          [-113.931799, 42.535275],
          [-113.779811, 42.55687],
          [-113.655338, 42.535663],
          [-113.557609, 42.656416],
          [-113.472177, 42.669251],
          [-113.375835, 42.687887],
          [-113.236952, 42.625281],
          [-113.175702, 42.58935],
          [-113.002807, 42.588022],
          [-113.000823, 42.327483],
          [-113.00082, 41.998223]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16035",
      state: "16",
      county: "035",
      name: "Clearwater County",
      censusArea: 2457.274,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.675505, 46.719516],
          [-114.667346, 46.719276],
          [-114.655704, 46.711743],
          [-114.644747, 46.702155],
          [-114.642692, 46.694622],
          [-114.641322, 46.686404],
          [-114.641745, 46.679286],
          [-114.642713, 46.673145],
          [-114.635868, 46.65951],
          [-114.614423, 46.639188],
          [-114.594707, 46.633558],
          [-115.630833, 46.47312],
          [-115.779083, 46.267252],
          [-115.814704, 46.265718],
          [-116.170422, 46.371589],
          [-116.308539, 46.412998],
          [-116.369247, 46.466921],
          [-116.369352, 46.49975],
          [-116.448618, 46.497056],
          [-116.455881, 46.629097],
          [-116.329279, 46.6279],
          [-116.329587, 46.934502],
          [-114.975789, 46.932865],
          [-114.936015, 46.899761],
          [-114.938713, 46.869021],
          [-114.927837, 46.83599],
          [-114.920459, 46.827697],
          [-114.888146, 46.808573],
          [-114.853279, 46.799794],
          [-114.825923, 46.781949],
          [-114.76718, 46.738828],
          [-114.739253, 46.715167],
          [-114.717645, 46.713994],
          [-114.69063, 46.719961],
          [-114.675505, 46.719516]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16037",
      state: "16",
      county: "037",
      name: "Custer County",
      censusArea: 4920.94,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-114.812434, 44.808138],
          [-114.819855, 44.747227],
          [-114.74165, 44.653252],
          [-114.662714, 44.662131],
          [-114.562436, 44.575723],
          [-114.477309, 44.594118],
          [-114.386634, 44.670988],
          [-114.406309, 44.719319],
          [-114.344608, 44.793421],
          [-114.220805, 44.878922],
          [-114.189803, 44.824723],
          [-114.217202, 44.746829],
          [-114.084698, 44.755725],
          [-114.035375, 44.671167],
          [-113.81535, 44.494175],
          [-113.503476, 44.422479],
          [-113.446807, 44.45115],
          [-113.320672, 44.232998],
          [-113.416796, 44.233311],
          [-113.416344, 44.059209],
          [-113.457929, 44.059209],
          [-113.453713, 43.873739],
          [-113.361529, 43.797376],
          [-113.412487, 43.81684],
          [-113.798031, 43.568818],
          [-113.875866, 43.650749],
          [-113.973494, 43.707207],
          [-114.03277, 43.778879],
          [-114.073072, 43.738668],
          [-114.275226, 43.830935],
          [-114.29299, 43.887215],
          [-114.514979, 43.894842],
          [-114.561237, 43.84441],
          [-114.681246, 43.927753],
          [-114.813789, 43.927818],
          [-114.813745, 43.992978],
          [-114.873411, 43.992879],
          [-114.971258, 43.938134],
          [-114.978209, 43.946841],
          [-114.983747, 43.945988],
          [-114.99075, 43.949725],
          [-115.04647, 44.056242],
          [-115.026206, 44.158417],
          [-115.167118, 44.202711],
          [-115.178952, 44.29638],
          [-115.294462, 44.339411],
          [-115.228241, 44.422868],
          [-115.246996, 44.519552],
          [-115.304751, 44.580026],
          [-115.279663, 44.612095],
          [-115.168309, 44.647451],
          [-115.151795, 44.728171],
          [-115.088961, 44.770916],
          [-115.009938, 44.713369],
          [-114.812434, 44.808138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16049",
      state: "16",
      county: "049",
      name: "Idaho County",
      censusArea: 8477.352,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-116.691197, 45.269245],
          [-116.673793, 45.321511],
          [-116.553473, 45.499107],
          [-116.481208, 45.580597],
          [-116.463635, 45.602785],
          [-116.463504, 45.615785],
          [-116.471504, 45.628467],
          [-116.48912, 45.65114],
          [-116.510938, 45.668114],
          [-116.528272, 45.681473],
          [-116.535396, 45.691734],
          [-116.538014, 45.714929],
          [-116.535698, 45.734231],
          [-116.54809, 45.752364],
          [-116.59421, 45.77908],
          [-116.633377, 45.784681],
          [-116.789099, 45.847749],
          [-116.796418, 45.853807],
          [-116.701977, 45.996177],
          [-116.638651, 46.036076],
          [-116.529919, 46.000809],
          [-116.514484, 46.131828],
          [-116.427985, 46.202578],
          [-116.189977, 46.154379],
          [-116.133369, 46.215189],
          [-116.012665, 46.225747],
          [-116.170422, 46.371589],
          [-115.814704, 46.265718],
          [-115.779083, 46.267252],
          [-115.630833, 46.47312],
          [-114.594707, 46.633558],
          [-114.592264, 46.632861],
          [-114.481943, 46.631993],
          [-114.461211, 46.639151],
          [-114.44683, 46.645314],
          [-114.422176, 46.652163],
          [-114.370129, 46.654217],
          [-114.349584, 46.646684],
          [-114.341336, 46.642803],
          [-114.337942, 46.641205],
          [-114.331093, 46.630248],
          [-114.331093, 46.607649],
          [-114.346528, 46.535789],
          [-114.385297, 46.467162],
          [-114.378, 46.435371],
          [-114.426885, 46.289671],
          [-114.472643, 46.162202],
          [-114.48312, 46.129147],
          [-114.476284, 46.112988],
          [-114.468205, 46.099937],
          [-114.467583, 46.081914],
          [-114.476284, 46.062026],
          [-114.4912, 46.044003],
          [-114.4912, 46.034059],
          [-114.490578, 46.022872],
          [-114.483379, 46.008047],
          [-114.465376, 45.996077],
          [-114.429638, 45.986638],
          [-114.412007, 45.978057],
          [-114.401552, 45.963195],
          [-114.39376, 45.89402],
          [-114.44868, 45.858891],
          [-114.498809, 45.850676],
          [-114.509303, 45.845531],
          [-114.514596, 45.840785],
          [-114.566172, 45.773864],
          [-114.547763, 45.743309],
          [-114.528667, 45.731636],
          [-114.504869, 45.722176],
          [-114.497553, 45.710677],
          [-114.495421, 45.703321],
          [-114.500925, 45.666715],
          [-114.515411, 45.652842],
          [-114.54182, 45.641305],
          [-114.545574, 45.642928],
          [-114.550834, 45.6426],
          [-114.561046, 45.639906],
          [-114.563652, 45.637412],
          [-114.561773, 45.565109],
          [-114.560855, 45.564575],
          [-114.663529, 45.471215],
          [-114.793263, 45.508641],
          [-114.793633, 45.436614],
          [-114.604026, 45.295618],
          [-114.694126, 45.197114],
          [-115.975756, 45.195699],
          [-115.974957, 45.219699],
          [-116.121743, 45.15871],
          [-116.144531, 45.107619],
          [-116.28263, 45.134682],
          [-116.343081, 45.268043],
          [-116.691197, 45.269245]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16059",
      state: "16",
      county: "059",
      name: "Lemhi County",
      censusArea: 4563.387,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.9426, 45.686362],
          [-113.93422, 45.682232],
          [-113.896681, 45.641451],
          [-113.835396, 45.61296],
          [-113.813933, 45.600559],
          [-113.804871, 45.589112],
          [-113.802009, 45.579096],
          [-113.813456, 45.550002],
          [-113.810117, 45.530924],
          [-113.786269, 45.521385],
          [-113.772915, 45.512322],
          [-113.76576, 45.483228],
          [-113.774156, 45.465372],
          [-113.760924, 45.406501],
          [-113.738729, 45.329741],
          [-113.674409, 45.249411],
          [-113.650064, 45.23471],
          [-113.599506, 45.191114],
          [-113.565979, 45.149335],
          [-113.566179, 45.14312],
          [-113.57636, 45.133771],
          [-113.576453, 45.130311],
          [-113.485278, 45.063519],
          [-113.47377, 45.0617],
          [-113.463961, 45.063194],
          [-113.449349, 45.045745],
          [-113.445751, 45.018351],
          [-113.445915, 44.979918],
          [-113.447199, 44.971924],
          [-113.472715, 44.948123],
          [-113.480836, 44.95031],
          [-113.494446, 44.948597],
          [-113.498745, 44.942314],
          [-113.475838, 44.894648],
          [-113.455071, 44.865424],
          [-113.353631, 44.791395],
          [-113.341704, 44.784853],
          [-113.329491, 44.788645],
          [-113.278382, 44.812706],
          [-113.247166, 44.82295],
          [-113.207878, 44.80804],
          [-113.158206, 44.780847],
          [-113.134617, 44.763149],
          [-113.101082, 44.718832],
          [-113.051504, 44.63695],
          [-113.049349, 44.62938],
          [-113.053529, 44.621187],
          [-113.05677, 44.618657],
          [-113.027147, 44.495915],
          [-113.003544, 44.450814],
          [-112.9703, 44.426933],
          [-112.875822, 44.374357],
          [-112.849266, 44.371038],
          [-112.83134, 44.38166],
          [-112.829247, 44.385427],
          [-112.813907, 44.230439],
          [-112.995379, 44.233956],
          [-113.320672, 44.232998],
          [-113.446807, 44.45115],
          [-113.503476, 44.422479],
          [-113.81535, 44.494175],
          [-114.035375, 44.671167],
          [-114.084698, 44.755725],
          [-114.217202, 44.746829],
          [-114.189803, 44.824723],
          [-114.220805, 44.878922],
          [-114.344608, 44.793421],
          [-114.406309, 44.719319],
          [-114.386634, 44.670988],
          [-114.477309, 44.594118],
          [-114.562436, 44.575723],
          [-114.662714, 44.662131],
          [-114.74165, 44.653252],
          [-114.819855, 44.747227],
          [-114.812434, 44.808138],
          [-114.732212, 44.880351],
          [-114.722627, 45.178914],
          [-114.694126, 45.197114],
          [-114.604026, 45.295618],
          [-114.793633, 45.436614],
          [-114.793263, 45.508641],
          [-114.663529, 45.471215],
          [-114.560855, 45.564575],
          [-114.551286, 45.559007],
          [-114.514196, 45.564734],
          [-114.473759, 45.563278],
          [-114.460542, 45.561283],
          [-114.36662, 45.491616],
          [-114.350819, 45.468542],
          [-114.309613, 45.469432],
          [-114.247828, 45.545489],
          [-114.187852, 45.542151],
          [-114.135249, 45.557465],
          [-114.03493, 45.647967],
          [-114.022534, 45.67962],
          [-114.019315, 45.692937],
          [-114.015633, 45.696127],
          [-113.987759, 45.705247],
          [-113.971565, 45.700636],
          [-113.9426, 45.686362]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18023",
      state: "18",
      county: "023",
      name: "Clinton County",
      censusArea: 405.07,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.242743, 40.215834],
          [-86.242365, 40.180778],
          [-86.39885, 40.17719],
          [-86.695804, 40.178657],
          [-86.695637, 40.214367],
          [-86.694665, 40.432156],
          [-86.375762, 40.431851],
          [-86.242992, 40.373661],
          [-86.242743, 40.215834]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12021",
      state: "12",
      county: "021",
      name: "Collier County",
      censusArea: 1998.324,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.563763, 26.513324],
          [-81.271768, 26.517069],
          [-81.271721, 26.422651],
          [-81.26855, 26.253045],
          [-80.879809, 26.259455],
          [-80.872748, 26.00037],
          [-80.872932, 25.979434],
          [-80.873096, 25.805377],
          [-81.344564, 25.803322],
          [-81.349152, 25.816847],
          [-81.352731, 25.822015],
          [-81.362272, 25.824401],
          [-81.386127, 25.839906],
          [-81.394476, 25.851834],
          [-81.417536, 25.864954],
          [-81.424295, 25.867737],
          [-81.429066, 25.865351],
          [-81.441391, 25.863761],
          [-81.458487, 25.868929],
          [-81.471607, 25.881652],
          [-81.473992, 25.888411],
          [-81.48751, 25.888411],
          [-81.501027, 25.884037],
          [-81.508979, 25.884037],
          [-81.512955, 25.886423],
          [-81.511762, 25.89676],
          [-81.515738, 25.899941],
          [-81.527665, 25.901531],
          [-81.541183, 25.900338],
          [-81.577363, 25.889206],
          [-81.584519, 25.888808],
          [-81.614735, 25.893977],
          [-81.623482, 25.897158],
          [-81.640084, 25.897784],
          [-81.644553, 25.897953],
          [-81.654493, 25.893579],
          [-81.663821, 25.885605],
          [-81.672633, 25.856654],
          [-81.678287, 25.845301],
          [-81.6848, 25.847205],
          [-81.68954, 25.85271],
          [-81.713172, 25.897568],
          [-81.717687, 25.902039],
          [-81.727086, 25.907207],
          [-81.73195, 25.931506],
          [-81.738118, 25.942009],
          [-81.745579, 25.949643],
          [-81.749724, 25.960463],
          [-81.747834, 25.994273],
          [-81.750668, 25.998425],
          [-81.757463, 26.000374],
          [-81.762439, 26.00607],
          [-81.801663, 26.088227],
          [-81.808833, 26.152246],
          [-81.81461, 26.173167],
          [-81.81681, 26.207166],
          [-81.820675, 26.236735],
          [-81.833142, 26.294518],
          [-81.844555, 26.327712],
          [-81.845834, 26.330378],
          [-81.690494, 26.317334],
          [-81.659506, 26.421077],
          [-81.56218, 26.422625],
          [-81.563763, 26.513324]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US12033",
      state: "12",
      county: "033",
      name: "Escambia County",
      censusArea: 656.461,
      stateName: "Florida"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.919292, 30.370675],
          [-87.155392, 30.327748],
          [-87.206254, 30.320943],
          [-87.267827, 30.31548],
          [-87.282787, 30.318744],
          [-87.295422, 30.323503],
          [-87.319518, 30.317814],
          [-87.350486, 30.313064],
          [-87.419859, 30.297128],
          [-87.518324, 30.280435],
          [-87.452378, 30.300201],
          [-87.450078, 30.3111],
          [-87.450962, 30.346262],
          [-87.451378, 30.367199],
          [-87.440678, 30.391498],
          [-87.427478, 30.408398],
          [-87.419177, 30.410198],
          [-87.430578, 30.491096],
          [-87.448332, 30.513063],
          [-87.449841, 30.514369],
          [-87.449921, 30.51469],
          [-87.394479, 30.625192],
          [-87.394219, 30.641699],
          [-87.396997, 30.65364],
          [-87.407118, 30.671796],
          [-87.449362, 30.698913],
          [-87.481225, 30.716508],
          [-87.497515, 30.720123],
          [-87.502926, 30.722369],
          [-87.532607, 30.743489],
          [-87.624137, 30.845713],
          [-87.634938, 30.865886],
          [-87.622203, 30.897508],
          [-87.601355, 30.936294],
          [-87.594111, 30.976335],
          [-87.598927, 30.997454],
          [-87.598928, 30.997457],
          [-87.571281, 30.99787],
          [-87.548543, 30.997927],
          [-87.480243, 30.998202],
          [-87.479703, 30.998197],
          [-87.478706, 30.998213],
          [-87.466879, 30.998178],
          [-87.466827, 30.998178],
          [-87.461783, 30.998201],
          [-87.461638, 30.998202],
          [-87.458658, 30.998386],
          [-87.455705, 30.998318],
          [-87.449811, 30.998272],
          [-87.432292, 30.998205],
          [-87.425774, 30.99809],
          [-87.367842, 30.998292],
          [-87.364011, 30.998218],
          [-87.355656, 30.998244],
          [-87.333973, 30.998272],
          [-87.312183, 30.998435],
          [-87.30403, 30.998191],
          [-87.301567, 30.998434],
          [-87.290995, 30.998352],
          [-87.288905, 30.998345],
          [-87.265564, 30.998267],
          [-87.26054, 30.998195],
          [-87.259689, 30.998172],
          [-87.25796, 30.998263],
          [-87.257002, 30.998194],
          [-87.255592, 30.998216],
          [-87.25498, 30.998285],
          [-87.163084, 30.999051],
          [-87.286882, 30.925441],
          [-87.313611, 30.847266],
          [-87.308502, 30.72691],
          [-87.269407, 30.711687],
          [-87.258884, 30.611281],
          [-87.124968, 30.500196],
          [-87.1344, 30.420294],
          [-87.23297, 30.348999],
          [-86.919292, 30.370675]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16007",
      state: "16",
      county: "007",
      name: "Bear Lake County",
      censusArea: 974.785,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.046249, 42.513116],
          [-111.04708, 42.34942],
          [-111.047074, 42.280787],
          [-111.047097, 42.194773],
          [-111.047058, 42.182672],
          [-111.047107, 42.148971],
          [-111.047109, 42.142497],
          [-111.046689, 42.001567],
          [-111.415873, 42.000748],
          [-111.420898, 42.000793],
          [-111.425535, 42.00084],
          [-111.507264, 41.999518],
          [-111.594446, 42.093548],
          [-111.626162, 42.208613],
          [-111.552231, 42.351514],
          [-111.598597, 42.415821],
          [-111.579321, 42.43051],
          [-111.634939, 42.570117],
          [-111.603999, 42.590757],
          [-111.364809, 42.590462],
          [-111.280054, 42.553042],
          [-111.241365, 42.602078],
          [-111.234734, 42.478378],
          [-111.170956, 42.473299],
          [-111.046249, 42.513116]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16011",
      state: "16",
      county: "011",
      name: "Bingham County",
      censusArea: 2093.978,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.697408, 43.623011],
          [-112.519945, 43.62656],
          [-112.520024, 43.425099],
          [-112.103298, 43.425057],
          [-112.043821, 43.366708],
          [-111.822589, 43.366135],
          [-111.822742, 43.2822],
          [-111.587795, 43.28163],
          [-111.589615, 43.020052],
          [-112.063055, 43.022364],
          [-112.676152, 43.022012],
          [-112.750503, 42.951443],
          [-112.816427, 42.863253],
          [-113.00776, 42.863051],
          [-113.007657, 43.111027],
          [-113.00729, 43.284801],
          [-112.947187, 43.284725],
          [-112.936835, 43.449386],
          [-112.816659, 43.449048],
          [-112.816193, 43.535869],
          [-112.69908, 43.540244],
          [-112.697408, 43.623011]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US16013",
      state: "16",
      county: "013",
      name: "Blaine County",
      censusArea: 2643.586,
      stateName: "Idaho"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-113.472177, 42.669251],
          [-113.472155, 42.849218],
          [-113.413026, 42.84925],
          [-113.413693, 43.199785],
          [-113.714701, 43.20003],
          [-114.374877, 43.199603],
          [-114.39458, 43.325804],
          [-114.53456, 43.333337],
          [-114.546987, 43.436603],
          [-114.507786, 43.480804],
          [-114.586574, 43.558902],
          [-114.682225, 43.590431],
          [-114.666283, 43.73801],
          [-114.714849, 43.814844],
          [-114.828915, 43.788027],
          [-114.989384, 43.858611],
          [-114.971258, 43.938134],
          [-114.873411, 43.992879],
          [-114.813745, 43.992978],
          [-114.813789, 43.927818],
          [-114.681246, 43.927753],
          [-114.561237, 43.84441],
          [-114.514979, 43.894842],
          [-114.29299, 43.887215],
          [-114.275226, 43.830935],
          [-114.073072, 43.738668],
          [-114.03277, 43.778879],
          [-113.973494, 43.707207],
          [-113.875866, 43.650749],
          [-113.798031, 43.568818],
          [-113.70057, 43.584762],
          [-113.593503, 43.467251],
          [-113.63526, 43.452266],
          [-113.635446, 43.367556],
          [-113.36191, 43.367232],
          [-113.361905, 43.284655],
          [-113.00729, 43.284801],
          [-113.007657, 43.111027],
          [-113.24351, 43.111109],
          [-113.236927, 42.762359],
          [-113.178978, 42.762283],
          [-113.179038, 42.675543],
          [-113.236952, 42.625281],
          [-113.375835, 42.687887],
          [-113.472177, 42.669251]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05105",
      state: "05",
      county: "105",
      name: "Perry County",
      censusArea: 551.402,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.544341, 34.953319],
          [-92.753626, 34.911248],
          [-92.739779, 34.853459],
          [-93.073877, 34.858091],
          [-93.075178, 34.771892],
          [-93.269886, 34.771993],
          [-93.287785, 34.773193],
          [-93.288284, 34.860992],
          [-93.181478, 34.97639],
          [-93.040525, 35.076923],
          [-92.716331, 35.071742],
          [-92.554685, 35.113277],
          [-92.593585, 35.011761],
          [-92.544341, 34.953319]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05107",
      state: "05",
      county: "107",
      name: "Phillips County",
      censusArea: 695.663,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.575786, 34.641749],
          [-90.570133, 34.524829],
          [-90.565809, 34.4354],
          [-90.575336, 34.415152],
          [-90.613944, 34.390723],
          [-90.658542, 34.375705],
          [-90.764143, 34.363396],
          [-90.856367, 34.238928],
          [-90.868775, 34.228245],
          [-90.894744, 34.229977],
          [-90.91148, 34.223051],
          [-90.913212, 34.210355],
          [-90.895898, 34.191888],
          [-90.880894, 34.180346],
          [-90.880317, 34.169381],
          [-90.893013, 34.155531],
          [-90.918405, 34.154376],
          [-90.934564, 34.130138],
          [-90.933116, 34.124488],
          [-90.95517, 34.118833],
          [-91.117905, 34.119105],
          [-91.060626, 34.216766],
          [-91.056276, 34.336605],
          [-91.002693, 34.336353],
          [-91.000459, 34.469372],
          [-91.054774, 34.47088],
          [-91.051918, 34.645721],
          [-90.583224, 34.641389],
          [-90.575786, 34.641749]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05119",
      state: "05",
      county: "119",
      name: "Pulaski County",
      censusArea: 759.763,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.245818, 34.493506],
          [-92.244209, 34.58089],
          [-92.348749, 34.58229],
          [-92.346814, 34.625611],
          [-92.445148, 34.62694],
          [-92.443265, 34.672041],
          [-92.551963, 34.689432],
          [-92.549856, 34.761699],
          [-92.654324, 34.764541],
          [-92.651248, 34.851682],
          [-92.739779, 34.853459],
          [-92.753626, 34.911248],
          [-92.544341, 34.953319],
          [-92.229893, 34.927558],
          [-92.227541, 35.014484],
          [-92.121336, 35.01221],
          [-92.070805, 35.011802],
          [-92.078711, 34.729983],
          [-92.113313, 34.622966],
          [-92.02954, 34.621403],
          [-92.029996, 34.489352],
          [-92.208041, 34.492765],
          [-92.245818, 34.493506]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17031",
      state: "17",
      county: "031",
      name: "Cook County",
      censusArea: 945.326,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.524044, 41.708335],
          [-87.524944, 41.702635],
          [-87.524844, 41.691635],
          [-87.524642, 41.634935],
          [-87.524742, 41.632435],
          [-87.524642, 41.622535],
          [-87.524641, 41.563335],
          [-87.525669, 41.470283],
          [-87.653236, 41.469754],
          [-87.712752, 41.46962],
          [-87.717554, 41.469733],
          [-87.790303, 41.469846],
          [-87.790408, 41.528134],
          [-87.79056, 41.539916],
          [-87.792292, 41.538662],
          [-87.792793, 41.55854],
          [-87.909453, 41.556732],
          [-87.91193, 41.643933],
          [-88.027688, 41.641506],
          [-88.02916, 41.68553],
          [-87.914265, 41.716746],
          [-87.919333, 41.847679],
          [-87.919532, 41.851854],
          [-87.91964, 41.852621],
          [-87.919872, 41.859781],
          [-87.920082, 41.862199],
          [-87.920441, 41.906869],
          [-87.92, 41.958151],
          [-87.92067, 41.994031],
          [-88.080223, 41.990752],
          [-88.207356, 41.987127],
          [-88.210764, 41.987023],
          [-88.216575, 41.987127],
          [-88.217875, 41.987027],
          [-88.232275, 41.986627],
          [-88.233475, 41.986727],
          [-88.262876, 41.986227],
          [-88.238369, 42.154253],
          [-88.237402, 42.154281],
          [-88.199584, 42.15426],
          [-88.121545, 42.154193],
          [-88.004466, 42.153645],
          [-87.759327, 42.152362],
          [-87.75639, 42.14835],
          [-87.755826, 42.147579],
          [-87.754767, 42.146132],
          [-87.754444, 42.145691],
          [-87.754358, 42.145573],
          [-87.750396, 42.14016],
          [-87.748486, 42.13755],
          [-87.746421, 42.134729],
          [-87.741662, 42.128227],
          [-87.741318, 42.127812],
          [-87.740787, 42.127172],
          [-87.740718, 42.127089],
          [-87.739735, 42.125903],
          [-87.739529, 42.125655],
          [-87.733929, 42.118903],
          [-87.730463, 42.114723],
          [-87.729387, 42.113426],
          [-87.729378, 42.113414],
          [-87.728335, 42.112157],
          [-87.728191, 42.111984],
          [-87.727248, 42.110847],
          [-87.727005, 42.110554],
          [-87.725196, 42.108373],
          [-87.724661, 42.107727],
          [-87.724603, 42.107674],
          [-87.723723, 42.106879],
          [-87.722306, 42.105596],
          [-87.72222, 42.105518],
          [-87.7215, 42.104866],
          [-87.720407, 42.103878],
          [-87.720303, 42.103783],
          [-87.718065, 42.101758],
          [-87.717504, 42.10125],
          [-87.717276, 42.101044],
          [-87.717169, 42.100947],
          [-87.712206, 42.096455],
          [-87.71096, 42.095328],
          [-87.706415, 42.092213],
          [-87.704594, 42.090966],
          [-87.703882, 42.090478],
          [-87.70237, 42.089442],
          [-87.682359, 42.075729],
          [-87.682179, 42.075441],
          [-87.68075, 42.07316],
          [-87.679843, 42.071713],
          [-87.671462, 42.058334],
          [-87.670512, 42.05298],
          [-87.670699, 42.052305],
          [-87.671119, 42.050781],
          [-87.671184, 42.050548],
          [-87.671894, 42.047972],
          [-87.671094, 42.042802],
          [-87.668982, 42.029142],
          [-87.630953, 41.933132],
          [-87.63087, 41.932784],
          [-87.627038, 41.916738],
          [-87.624763, 41.907209],
          [-87.624134, 41.904574],
          [-87.624052, 41.904232],
          [-87.622944, 41.90202],
          [-87.619852, 41.901392],
          [-87.617433, 41.898032],
          [-87.614163, 41.893418],
          [-87.612291, 41.893335],
          [-87.611659, 41.892216],
          [-87.611659, 41.890708],
          [-87.61268, 41.889248],
          [-87.614188, 41.888421],
          [-87.613556, 41.88448],
          [-87.613654, 41.884412],
          [-87.615734, 41.882958],
          [-87.616537, 41.882396],
          [-87.616251, 41.868933],
          [-87.60945, 41.845233],
          [-87.600549, 41.826833],
          [-87.587123, 41.811422],
          [-87.587054, 41.811342],
          [-87.580948, 41.804334],
          [-87.58092, 41.804225],
          [-87.58055, 41.80275],
          [-87.576347, 41.786034],
          [-87.560646, 41.766034],
          [-87.542845, 41.752135],
          [-87.530745, 41.748235],
          [-87.524141, 41.72399],
          [-87.524044, 41.708335]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18123",
      state: "18",
      county: "123",
      name: "Perry County",
      censusArea: 381.727,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.655296, 37.842508],
          [-86.660293, 37.865057],
          [-86.709164, 37.89764],
          [-86.718462, 37.893123],
          [-86.72351, 37.892831],
          [-86.73146, 37.89434],
          [-86.740877, 37.902568],
          [-86.75099, 37.912893],
          [-86.770179, 37.940264],
          [-86.787695, 37.971449],
          [-86.794985, 37.988982],
          [-86.814776, 37.998638],
          [-86.772367, 37.998165],
          [-86.791497, 38.20513],
          [-86.792152, 38.2491],
          [-86.679511, 38.263086],
          [-86.570136, 38.266328],
          [-86.571677, 38.207531],
          [-86.460425, 38.206825],
          [-86.426508, 38.081784],
          [-86.430091, 38.078638],
          [-86.432789, 38.067171],
          [-86.438236, 38.060426],
          [-86.452192, 38.05049],
          [-86.466876, 38.046597],
          [-86.490853, 38.045796],
          [-86.490858, 38.045796],
          [-86.51176, 38.044448],
          [-86.517289, 38.042634],
          [-86.521825, 38.038327],
          [-86.524969, 38.027879],
          [-86.525174, 37.968228],
          [-86.604624, 37.858272],
          [-86.634271, 37.843845],
          [-86.648028, 37.841425],
          [-86.655285, 37.842506],
          [-86.655296, 37.842508]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18129",
      state: "18",
      county: "129",
      name: "Posey County",
      censusArea: 409.571,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.700421, 37.900858],
          [-87.76226, 37.890906],
          [-87.783643, 37.877759],
          [-87.7909, 37.875714],
          [-87.830578, 37.876516],
          [-87.841193, 37.882325],
          [-87.857243, 37.900649],
          [-87.865558, 37.915056],
          [-87.87254, 37.920999],
          [-87.883321, 37.926238],
          [-87.898062, 37.927514],
          [-87.904789, 37.924892],
          [-87.927424, 37.902038],
          [-87.936784, 37.892587],
          [-87.940069, 37.88767],
          [-87.940839, 37.883338],
          [-87.940005, 37.875044],
          [-87.936228, 37.867937],
          [-87.927303, 37.858709],
          [-87.914892, 37.849618],
          [-87.910276, 37.843416],
          [-87.907773, 37.837611],
          [-87.903804, 37.817762],
          [-87.90681, 37.807624],
          [-87.943756, 37.776843],
          [-87.970869, 37.783456],
          [-87.976389, 37.788004],
          [-88.004706, 37.800145],
          [-88.017314, 37.801911],
          [-88.02803, 37.799224],
          [-88.027789, 37.828298],
          [-88.049453, 37.845187],
          [-88.071449, 37.895813],
          [-88.055373, 37.891238],
          [-88.021117, 37.888057],
          [-88.017073, 37.889222],
          [-88.013451, 37.893951],
          [-88.012574, 37.977062],
          [-88.041532, 38.037345],
          [-88.042132, 38.046345],
          [-88.013118, 38.103527],
          [-87.974272, 38.121981],
          [-87.942176, 38.131759],
          [-87.92783, 38.141545],
          [-87.911034, 38.162194],
          [-87.910792, 38.167666],
          [-87.915666, 38.173322],
          [-87.928856, 38.176771],
          [-87.940694, 38.181261],
          [-87.951307, 38.193099],
          [-87.959062, 38.203712],
          [-87.961103, 38.214733],
          [-87.955344, 38.230008],
          [-87.799169, 38.22858],
          [-87.688374, 38.168419],
          [-87.700421, 37.900858]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18143",
      state: "18",
      county: "143",
      name: "Scott County",
      censusArea: 190.397,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.56998, 38.606155],
          [-85.793584, 38.604814],
          [-85.847893, 38.561269],
          [-85.885643, 38.575412],
          [-85.888334, 38.734443],
          [-85.795357, 38.807508],
          [-85.683839, 38.815406],
          [-85.683338, 38.736731],
          [-85.57093, 38.678706],
          [-85.56998, 38.606155]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18147",
      state: "18",
      county: "147",
      name: "Spencer County",
      censusArea: 396.744,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.814776, 37.998638],
          [-86.815267, 37.998877],
          [-86.823491, 37.998939],
          [-86.85595, 37.987292],
          [-86.884961, 37.964373],
          [-86.902413, 37.946161],
          [-86.919329, 37.936664],
          [-86.97883, 37.930214],
          [-86.978957, 37.9302],
          [-87.010315, 37.919668],
          [-87.033444, 37.906593],
          [-87.042249, 37.898291],
          [-87.046237, 37.889866],
          [-87.043407, 37.87994],
          [-87.043854, 37.870796],
          [-87.057836, 37.827457],
          [-87.067836, 37.806065],
          [-87.077404, 37.796209],
          [-87.090636, 37.787808],
          [-87.111133, 37.782512],
          [-87.119229, 37.782848],
          [-87.128749, 37.785728],
          [-87.132621, 37.791008],
          [-87.20224, 37.843791],
          [-87.26293, 37.872846],
          [-87.269561, 37.877155],
          [-87.268003, 37.924766],
          [-87.072332, 38.11828],
          [-87.017453, 38.118301],
          [-87.01749, 38.20358],
          [-86.791497, 38.20513],
          [-86.772367, 37.998165],
          [-86.814776, 37.998638]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18155",
      state: "18",
      county: "155",
      name: "Switzerland County",
      censusArea: 220.633,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.813182, 38.930583],
          [-84.789521, 38.884687],
          [-84.785111, 38.879988],
          [-84.785799, 38.869496],
          [-84.791078, 38.855859],
          [-84.81335, 38.79833],
          [-84.844412, 38.787078],
          [-84.944296, 38.775184],
          [-84.973432, 38.778847],
          [-84.984627, 38.779072],
          [-84.995939, 38.776756],
          [-85.024205, 38.763311],
          [-85.100963, 38.7268],
          [-85.106902, 38.720789],
          [-85.13868, 38.699168],
          [-85.156158, 38.692251],
          [-85.172528, 38.688082],
          [-85.189362, 38.687579],
          [-85.201498, 38.691575],
          [-85.203166, 38.913803],
          [-85.202849, 38.928523],
          [-85.135832, 38.929578],
          [-85.140312, 38.903194],
          [-84.870749, 38.900905],
          [-84.813182, 38.930583]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18163",
      state: "18",
      county: "163",
      name: "Vanderburgh County",
      censusArea: 233.475,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.451185, 37.941034],
          [-87.511499, 37.906426],
          [-87.550333, 37.92489],
          [-87.559342, 37.931146],
          [-87.57203, 37.947466],
          [-87.574287, 37.954842],
          [-87.573415, 37.962642],
          [-87.574715, 37.967742],
          [-87.581115, 37.973442],
          [-87.585916, 37.975442],
          [-87.592916, 37.975842],
          [-87.601416, 37.972542],
          [-87.62896, 37.926714],
          [-87.626256, 37.916138],
          [-87.620272, 37.906922],
          [-87.597118, 37.892394],
          [-87.591582, 37.887194],
          [-87.588426, 37.868791],
          [-87.588729, 37.860984],
          [-87.591504, 37.856642],
          [-87.606599, 37.838669],
          [-87.615399, 37.831974],
          [-87.645858, 37.825899],
          [-87.670889, 37.828429],
          [-87.679188, 37.836321],
          [-87.6819, 37.84641],
          [-87.681633, 37.855917],
          [-87.662865, 37.885578],
          [-87.666481, 37.895786],
          [-87.67573, 37.90193],
          [-87.684018, 37.903498],
          [-87.700421, 37.900858],
          [-87.688374, 38.168419],
          [-87.467284, 38.165403],
          [-87.451185, 37.941034]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18029",
      state: "18",
      county: "029",
      name: "Dearborn County",
      censusArea: 305.034,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.819826, 39.156504],
          [-84.819985, 39.149081],
          [-84.820157, 39.10548],
          [-84.831197, 39.10192],
          [-84.888873, 39.066376],
          [-84.897364, 39.057378],
          [-84.897171, 39.052407],
          [-84.889065, 39.04082],
          [-84.878614, 39.030296],
          [-85.132508, 38.948055],
          [-85.065574, 39.307232],
          [-84.928655, 39.305934],
          [-84.819451, 39.305153],
          [-84.819451, 39.305152],
          [-84.819622, 39.27159],
          [-84.819633, 39.261855],
          [-84.819859, 39.251018],
          [-84.819801, 39.247806],
          [-84.819813, 39.244334],
          [-84.819802, 39.157613],
          [-84.819826, 39.156504]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18037",
      state: "18",
      county: "037",
      name: "Dubois County",
      censusArea: 427.269,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.679511, 38.263086],
          [-86.792152, 38.2491],
          [-86.791497, 38.20513],
          [-87.01749, 38.20358],
          [-87.073141, 38.203821],
          [-87.073067, 38.232596],
          [-87.072369, 38.5147],
          [-86.924186, 38.505358],
          [-86.682359, 38.526378],
          [-86.681424, 38.394767],
          [-86.679511, 38.263086]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18039",
      state: "18",
      county: "039",
      name: "Elkhart County",
      censusArea: 463.17,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.791335, 41.759051],
          [-85.775039, 41.759147],
          [-85.750469, 41.75909],
          [-85.749992, 41.759091],
          [-85.724534, 41.759085],
          [-85.65975, 41.759101],
          [-85.654747, 41.523347],
          [-85.654274, 41.515695],
          [-85.653507, 41.436856],
          [-86.05923, 41.435812],
          [-86.059237, 41.442753],
          [-86.059052, 41.445236],
          [-86.059451, 41.479154],
          [-86.060652, 41.6084],
          [-86.060516, 41.608959],
          [-86.062572, 41.760283],
          [-86.003683, 41.760007],
          [-85.991302, 41.759949],
          [-85.97498, 41.759849],
          [-85.974901, 41.759849],
          [-85.888825, 41.759422],
          [-85.874997, 41.759341],
          [-85.872041, 41.759365],
          [-85.791363, 41.759051],
          [-85.791335, 41.759051]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13135",
      state: "13",
      county: "135",
      name: "Gwinnett County",
      censusArea: 430.383,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.109894, 34.098423],
          [-84.107143, 34.10003],
          [-84.105403, 34.102223],
          [-84.10261, 34.103788],
          [-84.101343, 34.106305],
          [-84.094763, 34.131708],
          [-84.074624, 34.163687],
          [-84.062841, 34.167873],
          [-83.86803, 34.098281],
          [-83.817682, 34.127493],
          [-83.869115, 34.004316],
          [-83.799104, 33.929844],
          [-83.982033, 33.786054],
          [-83.984555, 33.784332],
          [-84.023713, 33.752808],
          [-84.172139, 33.857516],
          [-84.179418, 33.864403],
          [-84.187005, 33.865515],
          [-84.203519, 33.873003],
          [-84.20472, 33.877003],
          [-84.21663, 33.884976],
          [-84.23222, 33.902002],
          [-84.250413, 33.910812],
          [-84.256022, 33.914401],
          [-84.259011, 33.91882],
          [-84.265337, 33.932576],
          [-84.272216, 33.944853],
          [-84.266306, 33.947577],
          [-84.271922, 33.9559],
          [-84.275722, 33.954201],
          [-84.276822, 33.9577],
          [-84.097692, 34.050654],
          [-84.117801, 34.065315],
          [-84.109894, 34.098423]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13145",
      state: "13",
      county: "145",
      name: "Harris County",
      censusArea: 463.869,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.13204, 32.764211],
          [-85.123421, 32.772248],
          [-85.184131, 32.870525],
          [-84.861768, 32.872495],
          [-84.700538, 32.84464],
          [-84.694603, 32.583945],
          [-84.907883, 32.583433],
          [-84.907703, 32.607608],
          [-85.082454, 32.607022],
          [-85.10479, 32.642542],
          [-85.114737, 32.685634],
          [-85.137136, 32.745168],
          [-85.136776, 32.746512],
          [-85.13204, 32.764211]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13151",
      state: "13",
      county: "151",
      name: "Henry County",
      censusArea: 322.127,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.283518, 33.502514],
          [-84.281273, 33.647411],
          [-84.254149, 33.647045],
          [-84.245453, 33.63073],
          [-84.224235, 33.630657],
          [-84.223952, 33.646572],
          [-84.184143, 33.646157],
          [-84.181584, 33.629174],
          [-84.17213, 33.621919],
          [-84.136289, 33.57233],
          [-84.044493, 33.525776],
          [-83.923913, 33.444194],
          [-84.044594, 33.333656],
          [-84.044597, 33.333495],
          [-84.042663, 33.333501],
          [-84.102582, 33.298191],
          [-84.150581, 33.335639],
          [-84.3544, 33.352514],
          [-84.353584, 33.436165],
          [-84.283518, 33.502514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13161",
      state: "13",
      county: "161",
      name: "Jeff Davis County",
      censusArea: 330.74,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.52142, 31.710796],
          [-82.521393, 31.672512],
          [-82.627342, 31.672672],
          [-82.836633, 31.671398],
          [-82.836366, 31.816154],
          [-82.645659, 31.918883],
          [-82.543655, 31.958914],
          [-82.483131, 31.968961],
          [-82.479607, 31.971653],
          [-82.458152, 31.96389],
          [-82.452051, 31.96459],
          [-82.444451, 31.96749],
          [-82.439351, 31.96779],
          [-82.431531, 31.966182],
          [-82.431362, 31.837993],
          [-82.520251, 31.838388],
          [-82.52142, 31.710796]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13167",
      state: "13",
      county: "167",
      name: "Johnson County",
      censusArea: 303.01,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.946966, 32.759358],
          [-82.802456, 32.809756],
          [-82.768365, 32.769108],
          [-82.521052, 32.822356],
          [-82.434149, 32.762258],
          [-82.499431, 32.68634],
          [-82.465065, 32.646833],
          [-82.594439, 32.579158],
          [-82.647733, 32.512507],
          [-82.668557, 32.612164],
          [-82.862763, 32.71576],
          [-82.957066, 32.708318],
          [-82.946366, 32.741258],
          [-82.946966, 32.759358]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13177",
      state: "13",
      county: "177",
      name: "Lee County",
      censusArea: 355.784,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.922494, 31.909653],
          [-83.925103, 31.905668],
          [-83.927383, 31.904142],
          [-83.932021, 31.898759],
          [-83.932301, 31.893801],
          [-83.928689, 31.886864],
          [-83.932091, 31.884632],
          [-83.934061, 31.88139],
          [-83.931152, 31.876984],
          [-83.928694, 31.871849],
          [-83.926524, 31.863292],
          [-83.932321, 31.859235],
          [-83.936736, 31.858043],
          [-83.9378, 31.854805],
          [-83.938192, 31.849489],
          [-83.939437, 31.847929],
          [-84.032344, 31.716772],
          [-84.018404, 31.650274],
          [-84.043213, 31.623573],
          [-84.297801, 31.621951],
          [-84.286456, 31.791182],
          [-84.338245, 31.873591],
          [-84.338143, 31.91619],
          [-83.922494, 31.909653]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13179",
      state: "13",
      county: "179",
      name: "Liberty County",
      censusArea: 489.801,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.19438, 31.568101],
          [-81.261868, 31.650142],
          [-81.43576, 31.642055],
          [-81.491798, 31.699575],
          [-81.641761, 31.765451],
          [-81.765357, 31.87454],
          [-81.750257, 31.973207],
          [-81.824405, 32.014882],
          [-81.809931, 32.019657],
          [-81.761735, 32.0479],
          [-81.718658, 32.089351],
          [-81.604431, 32.010948],
          [-81.593429, 31.97135],
          [-81.475761, 31.974255],
          [-81.407737, 31.944302],
          [-81.355837, 31.8149],
          [-81.198394, 31.72607],
          [-81.203572, 31.719448],
          [-81.186303, 31.701509],
          [-81.161084, 31.691401],
          [-81.154624, 31.693874],
          [-81.151888, 31.698411],
          [-81.149369, 31.699304],
          [-81.139394, 31.699917],
          [-81.131137, 31.695774],
          [-81.135608, 31.683491],
          [-81.136408, 31.674832],
          [-81.131728, 31.654484],
          [-81.133493, 31.623348],
          [-81.14997, 31.593476],
          [-81.160364, 31.570436],
          [-81.173079, 31.555908],
          [-81.178822, 31.55553],
          [-81.183252, 31.560058],
          [-81.186114, 31.568032],
          [-81.19438, 31.568101]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20107",
      state: "20",
      county: "107",
      name: "Linn County",
      censusArea: 594.059,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.613275, 38.388718],
          [-94.613329, 38.369618],
          [-94.613312, 38.364407],
          [-94.613, 38.335801],
          [-94.612825, 38.324387],
          [-94.612788, 38.320142],
          [-94.612673, 38.314832],
          [-94.612673, 38.302527],
          [-94.612844, 38.291423],
          [-94.612849, 38.289914],
          [-94.612692, 38.270394],
          [-94.612614, 38.237766],
          [-94.612635, 38.226987],
          [-94.612659, 38.219251],
          [-94.612658, 38.217649],
          [-94.612822, 38.203918],
          [-94.612848, 38.200714],
          [-94.613073, 38.190552],
          [-94.613422, 38.167908],
          [-94.613748, 38.160633],
          [-94.613856, 38.149769],
          [-94.614061, 38.067343],
          [-94.614089, 38.065901],
          [-94.614055, 38.060088],
          [-94.614055, 38.060056],
          [-94.613981, 38.037057],
          [-95.077876, 38.037706],
          [-95.065831, 38.389945],
          [-94.613275, 38.388718]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20117",
      state: "20",
      county: "117",
      name: "Marshall County",
      censusArea: 900.183,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.463713, 40.000968],
          [-96.46364, 40.000967],
          [-96.304555, 40.000629],
          [-96.301066, 40.000632],
          [-96.239208, 40.000691],
          [-96.239583, 39.827119],
          [-96.238969, 39.56622],
          [-96.581764, 39.566333],
          [-96.806544, 39.566423],
          [-96.805768, 40.001371],
          [-96.622401, 40.001158],
          [-96.610349, 40.000881],
          [-96.604884, 40.000891],
          [-96.580852, 40.000966],
          [-96.570854, 40.001091],
          [-96.557863, 40.000968],
          [-96.538977, 40.000851],
          [-96.527111, 40.001031],
          [-96.469945, 40.000966],
          [-96.467536, 40.001035],
          [-96.463713, 40.000968]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17149",
      state: "17",
      county: "149",
      name: "Pike County",
      censusArea: 831.381,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.313162, 39.684907],
          [-91.349039, 39.712316],
          [-91.367753, 39.729029],
          [-91.370009, 39.732524],
          [-91.369953, 39.745042],
          [-91.367962, 39.759124],
          [-91.365125, 39.758723],
          [-91.265848, 39.757258],
          [-91.147678, 39.75707],
          [-90.916071, 39.757168],
          [-90.916609, 39.845075],
          [-90.893651, 39.84092],
          [-90.571754, 39.839326],
          [-90.566976, 39.826883],
          [-90.599105, 39.79006],
          [-90.64599, 39.703368],
          [-90.581055, 39.521728],
          [-90.582202, 39.510332],
          [-90.590605, 39.493675],
          [-90.593256, 39.484586],
          [-90.613694, 39.395775],
          [-90.939983, 39.393555],
          [-90.993789, 39.422959],
          [-91.042351, 39.452062],
          [-91.158606, 39.553048],
          [-91.168419, 39.564928],
          [-91.178012, 39.598196],
          [-91.229317, 39.620853],
          [-91.313162, 39.684907]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17157",
      state: "17",
      county: "157",
      name: "Randolph County",
      censusArea: 575.5,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.674304, 37.803179],
          [-89.739873, 37.84693],
          [-89.844786, 37.905572],
          [-89.851048, 37.90398],
          [-89.862949, 37.896906],
          [-89.881475, 37.879591],
          [-89.901832, 37.869822],
          [-89.923185, 37.870672],
          [-89.937383, 37.874693],
          [-89.938191, 37.875111],
          [-89.950594, 37.881526],
          [-89.973642, 37.917661],
          [-89.974918, 37.926719],
          [-89.997103, 37.963225],
          [-90.072283, 38.017001],
          [-90.126532, 38.041666],
          [-90.203847, 38.088753],
          [-90.035999, 38.13563],
          [-90.036385, 38.223125],
          [-89.899061, 38.220755],
          [-89.703256, 38.219404],
          [-89.592797, 38.219271],
          [-89.595084, 37.95531],
          [-89.674304, 37.803179]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17161",
      state: "17",
      county: "161",
      name: "Rock Island County",
      censusArea: 427.636,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.278633, 41.767358],
          [-90.242747, 41.783767],
          [-90.229749, 41.675375],
          [-90.165057, 41.602933],
          [-90.164842, 41.597322],
          [-90.170427, 41.591761],
          [-90.181412, 41.590993],
          [-90.185609, 41.584653],
          [-90.196535, 41.540126],
          [-90.332657, 41.514001],
          [-90.43213, 41.456234],
          [-90.433771, 41.326983],
          [-91.078682, 41.336089],
          [-91.047819, 41.4109],
          [-91.039872, 41.418523],
          [-91.027787, 41.423603],
          [-90.989976, 41.431962],
          [-90.847458, 41.455019],
          [-90.786282, 41.452888],
          [-90.771672, 41.450761],
          [-90.737537, 41.450127],
          [-90.655839, 41.462132],
          [-90.605937, 41.494232],
          [-90.499475, 41.518055],
          [-90.474332, 41.519733],
          [-90.461432, 41.523533],
          [-90.39793, 41.572233],
          [-90.34165, 41.621484],
          [-90.343162, 41.648141],
          [-90.334525, 41.679559],
          [-90.317041, 41.729104],
          [-90.31522, 41.734264],
          [-90.309826, 41.743321],
          [-90.302782, 41.750031],
          [-90.278633, 41.767358]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17171",
      state: "17",
      county: "171",
      name: "Scott County",
      censusArea: 250.913,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.581055, 39.521728],
          [-90.64599, 39.703368],
          [-90.599105, 39.79006],
          [-90.371253, 39.753388],
          [-90.372969, 39.665781],
          [-90.300666, 39.636541],
          [-90.301801, 39.520192],
          [-90.581055, 39.521728]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17173",
      state: "17",
      county: "173",
      name: "Shelby County",
      censusArea: 758.523,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.139807, 39.348888],
          [-89.025381, 39.345896],
          [-89.025469, 39.464462],
          [-89.02568, 39.654183],
          [-88.810575, 39.653222],
          [-88.809051, 39.580241],
          [-88.717539, 39.579299],
          [-88.716875, 39.521209],
          [-88.641468, 39.520958],
          [-88.584273, 39.447582],
          [-88.470505, 39.447041],
          [-88.47083, 39.374515],
          [-88.471147, 39.220615],
          [-88.470906, 39.215029],
          [-88.804484, 39.216131],
          [-88.805325, 39.216263],
          [-88.80672, 39.216402],
          [-88.83481, 39.21637],
          [-88.836967, 39.216548],
          [-88.838197, 39.216273],
          [-88.917477, 39.216545],
          [-89.025698, 39.215536],
          [-89.122992, 39.217303],
          [-89.139305, 39.217192],
          [-89.140076, 39.217907],
          [-89.139807, 39.348888]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17181",
      state: "17",
      county: "181",
      name: "Union County",
      censusArea: 413.459,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.521523, 37.566208],
          [-89.521407, 37.572143],
          [-89.450594, 37.600022],
          [-89.153668, 37.600469],
          [-89.041401, 37.596576],
          [-89.044787, 37.329846],
          [-89.099448, 37.333559],
          [-89.248439, 37.335109],
          [-89.484598, 37.334832],
          [-89.474569, 37.338165],
          [-89.447556, 37.340475],
          [-89.43604, 37.344441],
          [-89.432836, 37.347056],
          [-89.428185, 37.356158],
          [-89.421054, 37.387668],
          [-89.42594, 37.407471],
          [-89.439769, 37.4372],
          [-89.450969, 37.450069],
          [-89.475525, 37.471388],
          [-89.492051, 37.494008],
          [-89.517051, 37.537278],
          [-89.521697, 37.557325],
          [-89.521523, 37.566208]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17185",
      state: "17",
      county: "185",
      name: "Wabash County",
      censusArea: 223.252,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.651529, 38.568166],
          [-87.670071, 38.545237],
          [-87.669232, 38.541742],
          [-87.656144, 38.521668],
          [-87.740001, 38.415698],
          [-87.750028, 38.403027],
          [-87.863032, 38.280172],
          [-87.923958, 38.258409],
          [-87.951715, 38.239632],
          [-87.987911, 38.256957],
          [-87.9906, 38.259715],
          [-87.954798, 38.335308],
          [-87.954445, 38.339733],
          [-87.957878, 38.347675],
          [-87.967298, 38.353556],
          [-87.969656, 38.361933],
          [-87.972855, 38.364565],
          [-87.977034, 38.379374],
          [-87.953898, 38.570094],
          [-87.953014, 38.57014],
          [-87.912351, 38.569909],
          [-87.912286, 38.570104],
          [-87.769353, 38.568937],
          [-87.760094, 38.568783],
          [-87.653977, 38.568289],
          [-87.651529, 38.568166]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17193",
      state: "17",
      county: "193",
      name: "White County",
      censusArea: 494.766,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.951715, 38.239632],
          [-87.955344, 38.230008],
          [-87.961103, 38.214733],
          [-87.959062, 38.203712],
          [-87.951307, 38.193099],
          [-87.940694, 38.181261],
          [-87.928856, 38.176771],
          [-87.915666, 38.173322],
          [-87.910792, 38.167666],
          [-87.911034, 38.162194],
          [-87.92783, 38.141545],
          [-87.942176, 38.131759],
          [-87.974272, 38.121981],
          [-88.013118, 38.103527],
          [-88.042132, 38.046345],
          [-88.041532, 38.037345],
          [-88.012574, 37.977062],
          [-88.013451, 37.893951],
          [-88.017073, 37.889222],
          [-88.021117, 37.888057],
          [-88.055373, 37.891238],
          [-88.071449, 37.895813],
          [-88.26331, 37.908632],
          [-88.357462, 37.909081],
          [-88.37452, 37.908432],
          [-88.370491, 38.255413],
          [-88.1509, 38.256097],
          [-88.058472, 38.255747],
          [-88.027749, 38.255661],
          [-88.02768, 38.259771],
          [-87.9906, 38.259715],
          [-87.987911, 38.256957],
          [-87.951715, 38.239632]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US17201",
      state: "17",
      county: "201",
      name: "Winnebago County",
      censusArea: 513.362,
      stateName: "Illinois"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.013667, 42.496087],
          [-88.992977, 42.496026],
          [-88.992659, 42.496025],
          [-88.943264, 42.495114],
          [-88.940391, 42.495046],
          [-88.940388, 42.495046],
          [-88.939732, 42.15232],
          [-89.172799, 42.150297],
          [-89.173, 42.204241],
          [-89.396192, 42.201916],
          [-89.401416, 42.500433],
          [-89.366031, 42.500274],
          [-89.361561, 42.500012],
          [-89.290896, 42.498853],
          [-89.250759, 42.497994],
          [-89.246972, 42.49813],
          [-89.228279, 42.498047],
          [-89.22627, 42.497957],
          [-89.166728, 42.497256],
          [-89.164905, 42.497347],
          [-89.125111, 42.496957],
          [-89.120365, 42.496992],
          [-89.116949, 42.49691],
          [-89.099012, 42.496499],
          [-89.071141, 42.496208],
          [-89.042898, 42.496255],
          [-89.013804, 42.496097],
          [-89.013667, 42.496087]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13287",
      state: "13",
      county: "287",
      name: "Turner County",
      censusArea: 285.393,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.480176, 31.847312],
          [-83.453634, 31.757861],
          [-83.500702, 31.59399],
          [-83.648972, 31.596466],
          [-83.649384, 31.567977],
          [-83.78688, 31.62281],
          [-83.802223, 31.803495],
          [-83.612642, 31.804128],
          [-83.612256, 31.854089],
          [-83.480176, 31.847312]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13291",
      state: "13",
      county: "291",
      name: "Union County",
      censusArea: 321.926,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.005457, 34.98744],
          [-83.936646, 34.987485],
          [-83.936413, 34.987485],
          [-83.915104, 34.926887],
          [-83.819099, 34.91369],
          [-83.780401, 34.792893],
          [-83.856506, 34.722191],
          [-83.939007, 34.740859],
          [-84.03651, 34.641934],
          [-84.158035, 34.648243],
          [-84.093193, 34.801411],
          [-84.107422, 34.886896],
          [-84.178672, 34.946497],
          [-84.129455, 34.987504],
          [-84.029954, 34.987321],
          [-84.021357, 34.98743],
          [-84.005457, 34.98744]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13299",
      state: "13",
      county: "299",
      name: "Ware County",
      censusArea: 892.461,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.284561, 31.224449],
          [-82.208732, 31.170938],
          [-82.208262, 31.084768],
          [-82.131698, 31.010714],
          [-82.415603, 31.01359],
          [-82.420442, 30.795229],
          [-82.149872, 30.784336],
          [-82.214847, 30.567009],
          [-82.249841, 30.570863],
          [-82.2581, 30.571559],
          [-82.287343, 30.573458],
          [-82.374844, 30.579004],
          [-82.418915, 30.581745],
          [-82.435852, 30.820068],
          [-82.495476, 30.819553],
          [-82.490558, 30.963166],
          [-82.592071, 31.018487],
          [-82.671669, 31.183739],
          [-82.699206, 31.278104],
          [-82.629622, 31.274726],
          [-82.628975, 31.36393],
          [-82.628181, 31.469351],
          [-82.597509, 31.469293],
          [-82.494718, 31.46869],
          [-82.417246, 31.417119],
          [-82.383705, 31.291143],
          [-82.284561, 31.224449]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21105",
      state: "21",
      county: "105",
      name: "Hickman County",
      censusArea: 242.274,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.816765, 36.502815],
          [-88.827012, 36.50285],
          [-88.827301, 36.502852],
          [-88.834626, 36.502914],
          [-88.834866, 36.502911],
          [-88.90348, 36.576864],
          [-89.026629, 36.592124],
          [-89.174733, 36.650476],
          [-89.171882, 36.672526],
          [-89.19948, 36.716045],
          [-89.201047, 36.725772],
          [-89.197808, 36.739412],
          [-89.184523, 36.753638],
          [-89.169106, 36.759473],
          [-89.142313, 36.755369],
          [-89.130399, 36.751702],
          [-89.123548, 36.78522],
          [-88.813229, 36.773115],
          [-88.816765, 36.502815]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48273",
      state: "48",
      county: "273",
      name: "Kleberg County",
      censusArea: 881.308,
      stateName: "Texas"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-97.985887, 27.209308],
            [-98.058078, 27.260981],
            [-98.0598, 27.635869],
            [-97.942146, 27.635932],
            [-97.840678, 27.558358],
            [-97.325118, 27.560927],
            [-97.336802, 27.527433],
            [-97.343418, 27.517764],
            [-97.347489, 27.503005],
            [-97.350543, 27.478578],
            [-97.359194, 27.458221],
            [-97.36581, 27.450588],
            [-97.371917, 27.425142],
            [-97.369881, 27.41242],
            [-97.372935, 27.401224],
            [-97.37955, 27.390028],
            [-97.399398, 27.344735],
            [-97.401942, 27.335574],
            [-97.404996, 27.329977],
            [-97.413138, 27.321325],
            [-97.420263, 27.317254],
            [-97.430441, 27.313691],
            [-97.450798, 27.313691],
            [-97.482859, 27.297915],
            [-97.508304, 27.275014],
            [-97.532223, 27.278577],
            [-97.544437, 27.284175],
            [-97.546981, 27.290791],
            [-97.536803, 27.289264],
            [-97.526625, 27.291808],
            [-97.524589, 27.297915],
            [-97.517465, 27.30504],
            [-97.504742, 27.30504],
            [-97.498126, 27.308602],
            [-97.502706, 27.322343],
            [-97.499144, 27.327941],
            [-97.483877, 27.338628],
            [-97.483877, 27.351351],
            [-97.48693, 27.358984],
            [-97.501688, 27.366618],
            [-97.514411, 27.361529],
            [-97.520518, 27.352877],
            [-97.53833, 27.335574],
            [-97.5709, 27.315727],
            [-97.584132, 27.30962],
            [-97.609068, 27.285193],
            [-97.621791, 27.287228],
            [-97.63146, 27.28621],
            [-97.636549, 27.282139],
            [-97.63668, 27.281727],
            [-97.951904, 27.233249],
            [-97.985887, 27.209308]
          ]
        ],
        [
          [
            [-97.055823, 27.843404],
            [-97.054724, 27.842419],
            [-97.045409, 27.837452],
            [-97.04598, 27.835004],
            [-97.085395, 27.793245],
            [-97.116277, 27.752599],
            [-97.166682, 27.676583],
            [-97.201866, 27.614858],
            [-97.221912, 27.576315],
            [-97.276091, 27.472145],
            [-97.30447, 27.407734],
            [-97.326523, 27.347612],
            [-97.347438, 27.277963],
            [-97.372861, 27.278069],
            [-97.367033, 27.298709],
            [-97.364676, 27.302637],
            [-97.359963, 27.304732],
            [-97.357606, 27.307875],
            [-97.36232, 27.32673],
            [-97.366771, 27.333276],
            [-97.361796, 27.359988],
            [-97.346607, 27.390365],
            [-97.336132, 27.402411],
            [-97.331157, 27.412362],
            [-97.329585, 27.418124],
            [-97.330895, 27.425456],
            [-97.329847, 27.43436],
            [-97.317277, 27.46369],
            [-97.293709, 27.497209],
            [-97.282972, 27.521564],
            [-97.266474, 27.542514],
            [-97.257832, 27.556393],
            [-97.26176, 27.563464],
            [-97.260451, 27.567392],
            [-97.252071, 27.578652],
            [-97.247877, 27.581366],
            [-97.247619, 27.581533],
            [-97.236882, 27.598293],
            [-97.241072, 27.602483],
            [-97.242643, 27.607458],
            [-97.231383, 27.632336],
            [-97.221955, 27.63286],
            [-97.219075, 27.630241],
            [-97.214099, 27.631551],
            [-97.200743, 27.650144],
            [-97.197339, 27.664547],
            [-97.197601, 27.678426],
            [-97.203474, 27.684533],
            [-97.203089, 27.688001],
            [-97.190007, 27.692829],
            [-97.170628, 27.720326],
            [-97.166176, 27.732372],
            [-97.1612, 27.734074],
            [-97.153606, 27.733289],
            [-97.147321, 27.735384],
            [-97.130823, 27.75162],
            [-97.127942, 27.75581],
            [-97.127681, 27.76],
            [-97.103326, 27.789068],
            [-97.102279, 27.798233],
            [-97.098874, 27.808447],
            [-97.092851, 27.81447],
            [-97.092589, 27.819183],
            [-97.098874, 27.82285],
            [-97.126109, 27.819707],
            [-97.130299, 27.820493],
            [-97.134489, 27.825206],
            [-97.132394, 27.827301],
            [-97.056713, 27.842294],
            [-97.055823, 27.843404]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48311",
      state: "48",
      county: "311",
      name: "Mc Mullen County",
      censusArea: 1139.426,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.334323, 28.0578],
          [-98.803325, 28.05748],
          [-98.800848, 28.647306],
          [-98.335047, 28.648275],
          [-98.335031, 28.612658],
          [-98.334323, 28.0578]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48315",
      state: "48",
      county: "315",
      name: "Marion County",
      censusArea: 380.88,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.277594, 32.880532],
          [-94.046061, 32.880949],
          [-94.042886, 32.881089],
          [-94.042886, 32.880965],
          [-94.043025, 32.880446],
          [-94.042785, 32.871486],
          [-94.043026, 32.797476],
          [-94.042747, 32.786973],
          [-94.042829, 32.785277],
          [-94.042938, 32.780558],
          [-94.043027, 32.776863],
          [-94.042947, 32.767991],
          [-94.043147, 32.693031],
          [-94.043147, 32.69303],
          [-94.242809, 32.75073],
          [-94.390783, 32.694377],
          [-94.507323, 32.693738],
          [-94.70214, 32.793088],
          [-94.705878, 32.879177],
          [-94.653994, 32.879506],
          [-94.277594, 32.880532]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48321",
      state: "48",
      county: "321",
      name: "Matagorda County",
      censusArea: 1100.276,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.322921, 28.64186],
          [-96.323522, 28.675597],
          [-96.309178, 28.963291],
          [-95.965196, 29.146865],
          [-95.874028, 29.229702],
          [-95.84631, 29.107799],
          [-95.770446, 29.06621],
          [-95.764937, 28.967359],
          [-95.67955, 28.965734],
          [-95.567708, 28.829758],
          [-95.507041, 28.824755],
          [-95.568136, 28.789998],
          [-95.812504, 28.664942],
          [-95.884026, 28.633098],
          [-96.000682, 28.588238],
          [-96.077868, 28.556626],
          [-96.194412, 28.502224],
          [-96.220376, 28.491966],
          [-96.244751, 28.475055],
          [-96.270391, 28.46193],
          [-96.303212, 28.441871],
          [-96.32156, 28.425148],
          [-96.328817, 28.423659],
          [-96.341617, 28.417334],
          [-96.371117, 28.397661],
          [-96.372101, 28.393875],
          [-96.370717, 28.387667],
          [-96.378616, 28.383909],
          [-96.379372, 28.386089],
          [-96.381864, 28.393276],
          [-96.37596, 28.401682],
          [-96.374138, 28.404275],
          [-96.335119, 28.437795],
          [-96.268341, 28.477992],
          [-96.223825, 28.495067],
          [-96.218978, 28.500383],
          [-96.21505, 28.509679],
          [-96.145448, 28.544741],
          [-96.104735, 28.559499],
          [-96.046211, 28.58698],
          [-96.032979, 28.589016],
          [-96.007534, 28.599703],
          [-95.98616, 28.606319],
          [-95.982088, 28.614461],
          [-95.985651, 28.621077],
          [-95.983106, 28.641942],
          [-95.978526, 28.650594],
          [-95.986066, 28.655468],
          [-95.996338, 28.658736],
          [-96.002954, 28.656192],
          [-96.006516, 28.648049],
          [-96.033488, 28.652629],
          [-96.047737, 28.649067],
          [-96.072165, 28.635326],
          [-96.099137, 28.624639],
          [-96.148501, 28.611408],
          [-96.187178, 28.593596],
          [-96.198374, 28.58698],
          [-96.221784, 28.580364],
          [-96.228909, 28.580873],
          [-96.233998, 28.596649],
          [-96.233998, 28.601738],
          [-96.222293, 28.607336],
          [-96.21415, 28.613443],
          [-96.212624, 28.622604],
          [-96.230944, 28.641433],
          [-96.208552, 28.662298],
          [-96.214659, 28.665352],
          [-96.192267, 28.687744],
          [-96.19125, 28.69436],
          [-96.19583, 28.69894],
          [-96.202446, 28.700976],
          [-96.222802, 28.698431],
          [-96.231453, 28.696395],
          [-96.256899, 28.68469],
          [-96.263515, 28.683673],
          [-96.268604, 28.688762],
          [-96.287942, 28.683164],
          [-96.304227, 28.671459],
          [-96.305245, 28.660263],
          [-96.303718, 28.644996],
          [-96.322921, 28.64186]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48337",
      state: "48",
      county: "337",
      name: "Montague County",
      censusArea: 930.906,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.484071, 33.91568],
          [-97.487065, 33.433681],
          [-97.500154, 33.434057],
          [-97.918189, 33.433873],
          [-97.978967, 33.433748],
          [-97.978919, 33.467129],
          [-97.97781, 33.889894],
          [-97.966706, 33.881949],
          [-97.877387, 33.850236],
          [-97.865765, 33.849393],
          [-97.834333, 33.857671],
          [-97.785317, 33.890701],
          [-97.733723, 33.936392],
          [-97.700477, 33.972246],
          [-97.69311, 33.983699],
          [-97.688023, 33.986607],
          [-97.671772, 33.99137],
          [-97.65621, 33.989488],
          [-97.609091, 33.968093],
          [-97.589598, 33.953554],
          [-97.588828, 33.951882],
          [-97.597115, 33.917868],
          [-97.596289, 33.913769],
          [-97.587441, 33.902479],
          [-97.581041, 33.900396],
          [-97.561054, 33.898006],
          [-97.555002, 33.897282],
          [-97.545526, 33.900396],
          [-97.519171, 33.913638],
          [-97.50096, 33.919643],
          [-97.486505, 33.916994],
          [-97.484071, 33.91568]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54047",
      state: "54",
      county: "047",
      name: "Mc Dowell County",
      censusArea: 533.456,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.855939, 37.54891],
          [-81.729433, 37.494585],
          [-81.654431, 37.523312],
          [-81.465845, 37.484703],
          [-81.311201, 37.424509],
          [-81.362156, 37.337687],
          [-81.366315, 37.335927],
          [-81.367052, 37.334504],
          [-81.36809, 37.332423],
          [-81.369379, 37.331827],
          [-81.367599, 37.327569],
          [-81.371315, 37.324115],
          [-81.377349, 37.318447],
          [-81.380159, 37.317838],
          [-81.384127, 37.318596],
          [-81.384914, 37.318832],
          [-81.38581, 37.320085],
          [-81.386727, 37.320474],
          [-81.388132, 37.319903],
          [-81.409196, 37.286071],
          [-81.409729, 37.284837],
          [-81.504168, 37.250115],
          [-81.50488, 37.247697],
          [-81.507325, 37.2338],
          [-81.557315, 37.207697],
          [-81.558353, 37.208145],
          [-81.560625, 37.206663],
          [-81.678603, 37.202467],
          [-81.683544, 37.211452],
          [-81.728194, 37.239823],
          [-81.73332, 37.238127],
          [-81.738378, 37.240917],
          [-81.744291, 37.244178],
          [-81.74342, 37.245858],
          [-81.743505, 37.247601],
          [-81.747656, 37.264329],
          [-81.75129, 37.265131],
          [-81.752123, 37.265568],
          [-81.752912, 37.266614],
          [-81.755012, 37.26772],
          [-81.757531, 37.27001],
          [-81.757714, 37.271124],
          [-81.75773, 37.271934],
          [-81.757631, 37.274003],
          [-81.76022, 37.275254],
          [-81.761752, 37.275713],
          [-81.762776, 37.275391],
          [-81.763836, 37.275218],
          [-81.765195, 37.275099],
          [-81.767837, 37.274216],
          [-81.774684, 37.274807],
          [-81.774747, 37.274847],
          [-81.793425, 37.281674],
          [-81.793639, 37.282188],
          [-81.809184, 37.283003],
          [-81.810559, 37.28298],
          [-81.84231, 37.285556],
          [-81.843167, 37.285586],
          [-81.854059, 37.291352],
          [-81.853488, 37.294763],
          [-81.854465, 37.299937],
          [-81.853978, 37.300418],
          [-81.86476, 37.308404],
          [-81.865219, 37.308839],
          [-81.873213, 37.325065],
          [-81.878343, 37.328837],
          [-81.878713, 37.331753],
          [-81.879601, 37.332074],
          [-81.880886, 37.331146],
          [-81.885075, 37.330665],
          [-81.886952, 37.330725],
          [-81.887722, 37.331156],
          [-81.892876, 37.330134],
          [-81.893773, 37.330105],
          [-81.894768, 37.331381],
          [-81.894797, 37.332012],
          [-81.895489, 37.332022],
          [-81.896001, 37.331967],
          [-81.899459, 37.340277],
          [-81.899495, 37.341102],
          [-81.902992, 37.34234],
          [-81.903795, 37.34305],
          [-81.905945, 37.342775],
          [-81.906368, 37.34276],
          [-81.907322, 37.343119],
          [-81.907895, 37.343783],
          [-81.910875, 37.348729],
          [-81.911487, 37.348839],
          [-81.920711, 37.355416],
          [-81.921571, 37.356423],
          [-81.925643, 37.357316],
          [-81.926589, 37.358942],
          [-81.929915, 37.366589],
          [-81.930194, 37.366728],
          [-81.933895, 37.372747],
          [-81.92749, 37.413251],
          [-81.935621, 37.438397],
          [-81.949367, 37.445687],
          [-81.968795, 37.451496],
          [-81.987006, 37.454878],
          [-81.99227, 37.460916],
          [-81.996578, 37.476705],
          [-81.992916, 37.482969],
          [-81.94264, 37.508844],
          [-81.92787, 37.512118],
          [-81.922628, 37.511914],
          [-81.912826, 37.52041],
          [-81.909374, 37.527041],
          [-81.897951, 37.524395],
          [-81.889236, 37.528387],
          [-81.88605, 37.536744],
          [-81.882936, 37.53821],
          [-81.869439, 37.53781],
          [-81.865239, 37.54361],
          [-81.855939, 37.54891]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54057",
      state: "54",
      county: "057",
      name: "Mineral County",
      censusArea: 327.83,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.979898, 39.237624],
          [-79.134456, 39.312501],
          [-79.280039, 39.340076],
          [-79.25688, 39.356077],
          [-79.213192, 39.367897],
          [-79.201676, 39.379726],
          [-79.08445, 39.471356],
          [-79.067072, 39.474658],
          [-79.04244, 39.479339],
          [-79.025681, 39.465539],
          [-78.968996, 39.441543],
          [-78.874744, 39.522611],
          [-78.824788, 39.590233],
          [-78.787461, 39.627492],
          [-78.760497, 39.609984],
          [-78.769029, 39.599871],
          [-78.772048, 39.593833],
          [-78.76749, 39.587487],
          [-78.760196, 39.582154],
          [-78.689455, 39.54577],
          [-78.676219, 39.540622],
          [-78.657324, 39.535087],
          [-78.767447, 39.447238],
          [-78.850723, 39.331093],
          [-78.979898, 39.237624]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54063",
      state: "54",
      county: "063",
      name: "Monroe County",
      censusArea: 472.752,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.663348, 37.731696],
          [-80.450651, 37.683858],
          [-80.296138, 37.691783],
          [-80.292337, 37.683976],
          [-80.292258, 37.683732],
          [-80.270323, 37.648982],
          [-80.270352, 37.648929],
          [-80.267455, 37.646108],
          [-80.267228, 37.646011],
          [-80.264874, 37.645511],
          [-80.26483, 37.645526],
          [-80.263291, 37.645101],
          [-80.263281, 37.645082],
          [-80.254469, 37.642333],
          [-80.254431, 37.642352],
          [-80.239288, 37.637672],
          [-80.220984, 37.627767],
          [-80.223386, 37.623185],
          [-80.240272, 37.606961],
          [-80.288766, 37.58196],
          [-80.312393, 37.546239],
          [-80.330306, 37.536244],
          [-80.314806, 37.500943],
          [-80.320627, 37.49888],
          [-80.425656, 37.449876],
          [-80.443025, 37.438126],
          [-80.46482, 37.426144],
          [-80.475601, 37.422949],
          [-80.494867, 37.43507],
          [-80.511391, 37.481672],
          [-80.622664, 37.433307],
          [-80.770082, 37.372363],
          [-80.776649, 37.383679],
          [-80.776766, 37.384131],
          [-80.798869, 37.395807],
          [-80.811639, 37.407507],
          [-80.85836, 37.428168],
          [-80.858473, 37.428301],
          [-80.660446, 37.714563],
          [-80.663348, 37.731696]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54071",
      state: "54",
      county: "071",
      name: "Pendleton County",
      censusArea: 696.049,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.134296, 38.81334],
          [-79.057253, 38.761413],
          [-79.092555, 38.700149],
          [-79.092271, 38.699208],
          [-79.135472, 38.644057],
          [-79.135546, 38.643715],
          [-79.136374, 38.6424],
          [-79.137012, 38.640655],
          [-79.146741, 38.625819],
          [-79.146974, 38.625641],
          [-79.174512, 38.566531],
          [-79.174881, 38.566314],
          [-79.207884, 38.500428],
          [-79.207873, 38.500122],
          [-79.209703, 38.495574],
          [-79.210008, 38.494283],
          [-79.210026, 38.494231],
          [-79.225839, 38.479446],
          [-79.23162, 38.474041],
          [-79.234408, 38.473011],
          [-79.240059, 38.469841],
          [-79.242024, 38.464332],
          [-79.26291, 38.444586],
          [-79.263376, 38.443762],
          [-79.265327, 38.441772],
          [-79.267414, 38.438322],
          [-79.282663, 38.431021],
          [-79.280263, 38.425475],
          [-79.290529, 38.420757],
          [-79.291813, 38.419627],
          [-79.295712, 38.418129],
          [-79.297758, 38.416438],
          [-79.312276, 38.411876],
          [-79.476638, 38.457228],
          [-79.521469, 38.533918],
          [-79.53687, 38.550917],
          [-79.555471, 38.560217],
          [-79.597955, 38.572767],
          [-79.649075, 38.591515],
          [-79.626774, 38.664214],
          [-79.510073, 38.780712],
          [-79.539075, 38.841411],
          [-79.455472, 38.92991],
          [-79.349867, 38.957509],
          [-79.134296, 38.81334]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54077",
      state: "54",
      county: "077",
      name: "Preston County",
      censusArea: 648.805,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.895536, 39.299584],
          [-79.894688, 39.435588],
          [-79.899744, 39.48194],
          [-79.789277, 39.623781],
          [-79.763774, 39.720776],
          [-79.610623, 39.721245],
          [-79.608223, 39.721154],
          [-79.476662, 39.721078],
          [-79.478866, 39.531689],
          [-79.482354, 39.524682],
          [-79.482648, 39.521364],
          [-79.487651, 39.279933],
          [-79.486737, 39.278149],
          [-79.486179, 39.26497],
          [-79.485874, 39.264905],
          [-79.486873, 39.205961],
          [-79.487175, 39.194906],
          [-79.687283, 39.271398],
          [-79.809729, 39.230526],
          [-79.895536, 39.299584]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51059",
      state: "51",
      county: "059",
      name: "Fairfax County",
      censusArea: 390.969,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.088047, 38.824973],
          [-77.039239, 38.7852],
          [-77.041398, 38.763914],
          [-77.05991, 38.734419],
          [-77.074599, 38.711015],
          [-77.086113, 38.705792],
          [-77.1059, 38.696815],
          [-77.121101, 38.686616],
          [-77.132501, 38.673816],
          [-77.135901, 38.649817],
          [-77.1302, 38.635017],
          [-77.157501, 38.636417],
          [-77.174902, 38.624217],
          [-77.202002, 38.617217],
          [-77.204302, 38.617817],
          [-77.205103, 38.623917],
          [-77.216303, 38.637817],
          [-77.22235, 38.638091],
          [-77.380451, 38.722516],
          [-77.387917, 38.748217],
          [-77.449084, 38.803133],
          [-77.53576, 38.847474],
          [-77.328318, 39.057678],
          [-77.328002, 39.058554],
          [-77.291605, 39.045408],
          [-77.261403, 39.031009],
          [-77.251803, 39.011409],
          [-77.255703, 39.002409],
          [-77.249803, 38.985909],
          [-77.235403, 38.97661],
          [-77.221502, 38.97131],
          [-77.197502, 38.96681],
          [-77.166901, 38.96811],
          [-77.148179, 38.965002],
          [-77.137701, 38.95531],
          [-77.1199, 38.934311],
          [-77.119863, 38.934265],
          [-77.148492, 38.911993],
          [-77.149735, 38.910523],
          [-77.154368, 38.907044],
          [-77.157513, 38.904855],
          [-77.16811, 38.896521],
          [-77.172169, 38.893329],
          [-77.188854, 38.896272],
          [-77.19434, 38.886065],
          [-77.189766, 38.878117],
          [-77.173243, 38.878604],
          [-77.154974, 38.872213],
          [-77.150287, 38.876192],
          [-77.110731, 38.843457],
          [-77.088047, 38.824973]
        ],
        [
          [-77.334805, 38.851404],
          [-77.318689, 38.834651],
          [-77.2705, 38.840992],
          [-77.270597, 38.866075],
          [-77.328788, 38.864848],
          [-77.334805, 38.851404]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51071",
      state: "51",
      county: "071",
      name: "Giles County",
      censusArea: 355.781,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.980044, 37.293118],
          [-80.973889, 37.291444],
          [-80.966556, 37.292158],
          [-80.947896, 37.295872],
          [-80.919259, 37.306163],
          [-80.900535, 37.315],
          [-80.849451, 37.346909],
          [-80.872589, 37.372347],
          [-80.862761, 37.411829],
          [-80.859558, 37.429555],
          [-80.859556, 37.429568],
          [-80.858473, 37.428301],
          [-80.85836, 37.428168],
          [-80.811639, 37.407507],
          [-80.798869, 37.395807],
          [-80.776766, 37.384131],
          [-80.776649, 37.383679],
          [-80.770082, 37.372363],
          [-80.622664, 37.433307],
          [-80.511391, 37.481672],
          [-80.494867, 37.43507],
          [-80.475601, 37.422949],
          [-80.433361, 37.314901],
          [-80.606874, 37.244911],
          [-80.854627, 37.147482],
          [-81.014489, 37.275385],
          [-80.980044, 37.293118]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51077",
      state: "51",
      county: "077",
      name: "Grayson County",
      censusArea: 442.175,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.605835, 36.638717],
          [-81.544536, 36.701617],
          [-81.26162, 36.76312],
          [-81.08851, 36.767422],
          [-81.042406, 36.806422],
          [-80.932271, 36.671593],
          [-80.944109, 36.676303],
          [-80.947754, 36.648361],
          [-80.921711, 36.643533],
          [-80.931655, 36.628494],
          [-80.916043, 36.629343],
          [-80.914173, 36.650246],
          [-80.837954, 36.559131],
          [-80.901726, 36.561751],
          [-80.901836, 36.561754],
          [-80.944338, 36.563058],
          [-80.945988, 36.563196],
          [-81.058844, 36.566976],
          [-81.061866, 36.56702],
          [-81.307511, 36.575024],
          [-81.353169, 36.574724],
          [-81.353322, 36.574723],
          [-81.521032, 36.58052],
          [-81.60697, 36.587094],
          [-81.677535, 36.588117],
          [-81.6469, 36.611918],
          [-81.610663, 36.616913],
          [-81.605835, 36.638717]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51083",
      state: "51",
      county: "083",
      name: "Halifax County",
      censusArea: 817.839,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.092264, 37.061498],
          [-78.904587, 37.022288],
          [-78.671463, 36.857951],
          [-78.648541, 36.697909],
          [-78.711702, 36.641078],
          [-78.734122, 36.541902],
          [-78.758392, 36.541852],
          [-78.76543, 36.541727],
          [-78.7963, 36.541713],
          [-78.914543, 36.541972],
          [-78.91542, 36.541974],
          [-78.942009, 36.542113],
          [-78.942254, 36.542079],
          [-78.970577, 36.542154],
          [-78.971814, 36.542123],
          [-79.124736, 36.541568],
          [-79.126078, 36.541533],
          [-79.137936, 36.541739],
          [-79.208686, 36.541571],
          [-79.20948, 36.541594],
          [-79.218638, 36.541579],
          [-79.092264, 37.061498]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51089",
      state: "51",
      county: "089",
      name: "Henry County",
      censusArea: 382.334,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.012032, 36.789644],
          [-79.999771, 36.830738],
          [-79.761756, 36.788337],
          [-79.640652, 36.856139],
          [-79.714855, 36.541884],
          [-79.920239, 36.542365],
          [-79.966979, 36.542475],
          [-79.967511, 36.542502],
          [-80.027269, 36.542495],
          [-80.053455, 36.542623],
          [-80.094389, 36.779552],
          [-80.041493, 36.794365],
          [-80.025178, 36.780752],
          [-80.013194, 36.783748],
          [-80.027194, 36.791262],
          [-80.012032, 36.789644]
        ],
        [
          [-79.904077, 36.684127],
          [-79.895561, 36.675237],
          [-79.894181, 36.672036],
          [-79.884861, 36.672037],
          [-79.881661, 36.670537],
          [-79.880164, 36.667144],
          [-79.877668, 36.666707],
          [-79.85926, 36.661737],
          [-79.859761, 36.650368],
          [-79.827459, 36.647637],
          [-79.826619, 36.659379],
          [-79.843737, 36.672443],
          [-79.837359, 36.704137],
          [-79.87476, 36.715237],
          [-79.904077, 36.684127]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51093",
      state: "51",
      county: "093",
      name: "Isle Of Wight County",
      censusArea: 315.612,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.576617, 37.021374],
          [-76.565803, 37.007493],
          [-76.562923, 37.003796],
          [-76.551246, 36.998946],
          [-76.524853, 36.983833],
          [-76.522971, 36.981085],
          [-76.524142, 36.978316],
          [-76.521006, 36.973187],
          [-76.513363, 36.968057],
          [-76.500355, 36.965212],
          [-76.487559, 36.952372],
          [-76.484107, 36.928916],
          [-76.53352, 36.915563],
          [-76.897592, 36.64405],
          [-76.9004, 36.645731],
          [-76.907702, 36.64719],
          [-76.909672, 36.648175],
          [-76.917742, 36.662368],
          [-76.912015, 36.672717],
          [-76.925167, 36.683583],
          [-76.912221, 36.698408],
          [-76.926142, 36.698587],
          [-76.924629, 36.707729],
          [-76.816162, 36.914907],
          [-76.849684, 36.996211],
          [-76.711775, 37.05538],
          [-76.671588, 37.14206],
          [-76.669604, 37.140534],
          [-76.666542, 37.138179],
          [-76.665833, 37.136098],
          [-76.665641, 37.135534],
          [-76.66375, 37.129979],
          [-76.656894, 37.109843],
          [-76.657101, 37.107617],
          [-76.657703, 37.101161],
          [-76.65811, 37.096787],
          [-76.659394, 37.094019],
          [-76.66555, 37.080746],
          [-76.666526, 37.078643],
          [-76.667219, 37.077149],
          [-76.667646, 37.076228],
          [-76.668295, 37.072656],
          [-76.669118, 37.068132],
          [-76.669822, 37.06426],
          [-76.66835, 37.05506],
          [-76.662558, 37.045748],
          [-76.653998, 37.039172],
          [-76.646013, 37.036228],
          [-76.612124, 37.035604],
          [-76.586491, 37.02874],
          [-76.584478, 37.027349],
          [-76.579393, 37.023835],
          [-76.579236, 37.023726],
          [-76.57816, 37.022982],
          [-76.577531, 37.022548],
          [-76.576617, 37.021374]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51099",
      state: "51",
      county: "099",
      name: "King George County",
      censusArea: 179.636,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.99767, 38.278047],
          [-77.061695, 38.161631],
          [-77.069296, 38.157731],
          [-77.066895, 38.147631],
          [-77.075496, 38.141131],
          [-77.082596, 38.146831],
          [-77.084596, 38.160231],
          [-77.094796, 38.161631],
          [-77.115997, 38.149931],
          [-77.224689, 38.242207],
          [-77.326692, 38.245136],
          [-77.286202, 38.347024],
          [-77.279633, 38.339444],
          [-77.265295, 38.333165],
          [-77.240072, 38.331598],
          [-77.199433, 38.34089],
          [-77.17934, 38.341915],
          [-77.162692, 38.345994],
          [-77.155191, 38.351047],
          [-77.138224, 38.367917],
          [-77.104717, 38.369655],
          [-77.094665, 38.367715],
          [-77.08481, 38.368297],
          [-77.069956, 38.377895],
          [-77.056032, 38.3962],
          [-77.051437, 38.399083],
          [-77.043526, 38.400548],
          [-77.024866, 38.386791],
          [-77.011827, 38.374554],
          [-77.016932, 38.341697],
          [-77.020947, 38.329273],
          [-77.030683, 38.311623],
          [-77.026304, 38.302685],
          [-76.99767, 38.278047]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51105",
      state: "51",
      county: "105",
      name: "Lee County",
      censusArea: 435.518,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.771308, 36.79988],
          [-82.823163, 36.733295],
          [-82.947535, 36.67584],
          [-82.985087, 36.593829],
          [-83.02725, 36.593847],
          [-83.028357, 36.593893],
          [-83.248933, 36.593827],
          [-83.249899, 36.593898],
          [-83.250304, 36.593935],
          [-83.2763, 36.598187],
          [-83.472108, 36.597284],
          [-83.670128, 36.600764],
          [-83.670141, 36.600797],
          [-83.675413, 36.600814],
          [-83.649513, 36.616683],
          [-83.527212, 36.665984],
          [-83.498011, 36.670485],
          [-83.461013, 36.664916],
          [-83.460808, 36.664885],
          [-83.354606, 36.696153],
          [-83.353613, 36.696699],
          [-83.342804, 36.701286],
          [-83.199698, 36.737487],
          [-83.167396, 36.739187],
          [-83.136395, 36.743088],
          [-83.127833, 36.750828],
          [-83.125728, 36.761276],
          [-83.125655, 36.761407],
          [-83.131245, 36.767105],
          [-83.131694, 36.781488],
          [-83.099792, 36.824889],
          [-83.07259, 36.854589],
          [-82.973395, 36.859097],
          [-82.911824, 36.874243],
          [-82.91169, 36.874248],
          [-82.885618, 36.900415],
          [-82.771308, 36.79988]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21033",
      state: "21",
      county: "033",
      name: "Caldwell County",
      censusArea: 344.791,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.801046, 37.379445],
          [-87.811461, 37.364087],
          [-87.819963, 37.366034],
          [-87.827977, 37.353711],
          [-87.813413, 37.350645],
          [-87.85076, 37.322136],
          [-87.805029, 37.241083],
          [-87.680617, 37.149239],
          [-87.710912, 37.021607],
          [-87.733368, 37.002103],
          [-87.876996, 36.960116],
          [-88.042142, 37.174756],
          [-88.095781, 37.179684],
          [-88.089074, 37.222388],
          [-87.801046, 37.379445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13305",
      state: "13",
      county: "305",
      name: "Wayne County",
      censusArea: 641.777,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.969052, 31.789324],
          [-81.850624, 31.667671],
          [-81.663206, 31.538667],
          [-81.62429, 31.452982],
          [-81.69199, 31.400519],
          [-81.731694, 31.330048],
          [-81.801052, 31.363737],
          [-81.923238, 31.345875],
          [-82.041129, 31.373721],
          [-82.052754, 31.454567],
          [-82.132794, 31.471262],
          [-82.133013, 31.773404],
          [-82.048582, 31.827075],
          [-82.028346, 31.815513],
          [-82.014639, 31.8218],
          [-82.006777, 31.800047],
          [-81.981941, 31.784405],
          [-81.969052, 31.789324]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51117",
      state: "51",
      county: "117",
      name: "Mecklenburg County",
      censusArea: 625.485,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.033437, 36.708451],
          [-78.046202, 36.544168],
          [-78.132911, 36.543811],
          [-78.133323, 36.543847],
          [-78.245462, 36.544411],
          [-78.246681, 36.544341],
          [-78.323912, 36.543809],
          [-78.436333, 36.542666],
          [-78.441199, 36.542687],
          [-78.45697, 36.542474],
          [-78.470792, 36.542316],
          [-78.471022, 36.542307],
          [-78.529722, 36.540981],
          [-78.533013, 36.541004],
          [-78.663317, 36.542011],
          [-78.670051, 36.542035],
          [-78.734122, 36.541902],
          [-78.711702, 36.641078],
          [-78.648541, 36.697909],
          [-78.493028, 36.89122],
          [-78.241509, 36.807958],
          [-78.027411, 36.777858],
          [-78.027419, 36.775951],
          [-78.033437, 36.708451]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53007",
      state: "53",
      county: "007",
      name: "Chelan County",
      censusArea: 2920.525,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.11901, 47.779933],
          [-121.07071, 47.826034],
          [-121.180213, 47.897132],
          [-121.118059, 47.99766],
          [-121.153501, 48.040871],
          [-121.015412, 48.075141],
          [-120.906411, 48.163348],
          [-121.001409, 48.295548],
          [-121.069941, 48.316539],
          [-121.035823, 48.494749],
          [-120.824008, 48.545153],
          [-120.701557, 48.53162],
          [-120.651747, 48.536585],
          [-120.650211, 48.397559],
          [-120.559708, 48.360661],
          [-120.391901, 48.242963],
          [-120.3625, 48.168761],
          [-120.213493, 48.079061],
          [-120.14329, 48.065061],
          [-120.066621, 47.966378],
          [-119.870307, 47.960328],
          [-119.994952, 47.781353],
          [-120.130914, 47.776601],
          [-120.209395, 47.748356],
          [-120.199048, 47.682276],
          [-120.233584, 47.59763],
          [-120.292814, 47.538172],
          [-120.318084, 47.457748],
          [-120.28653, 47.393111],
          [-120.129573, 47.366251],
          [-120.083274, 47.33369],
          [-120.09447, 47.262151],
          [-120.388978, 47.260889],
          [-120.525137, 47.333239],
          [-120.916098, 47.42854],
          [-121.113606, 47.597135],
          [-121.068409, 47.706234],
          [-121.11901, 47.779933]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48117",
      state: "48",
      county: "117",
      name: "Deaf Smith County",
      censusArea: 1496.868,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-103.04252, 35.135596],
          [-103.0426, 35.142766],
          [-103.042711, 35.144735],
          [-103.042568, 35.159318],
          [-103.042395, 35.178573],
          [-103.042339, 35.181922],
          [-103.042366, 35.182786],
          [-103.042377, 35.183149],
          [-103.042377, 35.183156],
          [-102.167471, 35.183233],
          [-102.168839, 34.747417],
          [-102.525184, 34.746931],
          [-103.042769, 34.747361],
          [-103.04277, 34.792224],
          [-103.042781, 34.850243],
          [-103.042521, 34.899546],
          [-103.042521, 34.954101],
          [-103.04252, 35.135596]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48261",
      state: "48",
      county: "261",
      name: "Kenedy County",
      censusArea: 1458.329,
      stateName: "Texas"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-97.372861, 27.278069],
            [-97.347438, 27.277963],
            [-97.350398, 27.268105],
            [-97.363401, 27.210366],
            [-97.370941, 27.161166],
            [-97.377001, 27.101021],
            [-97.37913, 27.047996],
            [-97.378362, 26.992877],
            [-97.370731, 26.909706],
            [-97.364726, 26.871693],
            [-97.351413, 26.808604],
            [-97.333028, 26.736479],
            [-97.30069, 26.635375],
            [-97.287885, 26.600341],
            [-97.275119, 26.565415],
            [-97.269392, 26.554046],
            [-97.229844, 26.433569],
            [-97.223728, 26.411492],
            [-97.194644, 26.306513],
            [-97.185844, 26.267103],
            [-97.173265, 26.192314],
            [-97.161471, 26.088705],
            [-97.154271, 26.066841],
            [-97.161462, 26.06764],
            [-97.169842, 26.077853],
            [-97.171781, 26.102522],
            [-97.179532, 26.146202],
            [-97.178746, 26.177103],
            [-97.183983, 26.214289],
            [-97.194458, 26.27164],
            [-97.214885, 26.353606],
            [-97.226931, 26.385555],
            [-97.240286, 26.405981],
            [-97.240849, 26.411504],
            [-97.243167, 26.434263],
            [-97.247619, 26.456261],
            [-97.254166, 26.471188],
            [-97.262546, 26.482972],
            [-97.276425, 26.521729],
            [-97.292399, 26.528014],
            [-97.31073, 26.556558],
            [-97.308112, 26.571223],
            [-97.308635, 26.576723],
            [-97.317015, 26.597673],
            [-97.318458, 26.60027],
            [-97.324872, 26.611814],
            [-97.338489, 26.647429],
            [-97.336394, 26.666022],
            [-97.345822, 26.700589],
            [-97.363105, 26.71054],
            [-97.370438, 26.723896],
            [-97.370961, 26.736204],
            [-97.367557, 26.740394],
            [-97.364153, 26.758987],
            [-97.364646, 26.767122],
            [-97.368866, 26.774699],
            [-97.370438, 26.781508],
            [-97.368343, 26.795649],
            [-97.373056, 26.808481],
            [-97.387459, 26.820789],
            [-97.383531, 26.875521],
            [-97.385626, 26.888876],
            [-97.391649, 26.90197],
            [-97.389554, 26.945965],
            [-97.39034, 27.052286],
            [-97.389816, 27.067213],
            [-97.386412, 27.083187],
            [-97.387459, 27.090519],
            [-97.390602, 27.094186],
            [-97.390078, 27.156512],
            [-97.377508, 27.199459],
            [-97.379865, 27.202863],
            [-97.386674, 27.204696],
            [-97.382222, 27.229051],
            [-97.37489, 27.250262],
            [-97.373318, 27.27645],
            [-97.372861, 27.278069]
          ]
        ],
        [
          [
            [-97.63668, 27.281727],
            [-97.640111, 27.270943],
            [-97.639094, 27.253131],
            [-97.635022, 27.247024],
            [-97.628916, 27.242953],
            [-97.597363, 27.242444],
            [-97.582605, 27.240409],
            [-97.573953, 27.238882],
            [-97.561231, 27.232775],
            [-97.54291, 27.229213],
            [-97.520009, 27.231248],
            [-97.509831, 27.23532],
            [-97.503215, 27.2399],
            [-97.500162, 27.24448],
            [-97.485149, 27.250841],
            [-97.467083, 27.25364],
            [-97.458431, 27.259493],
            [-97.450289, 27.262546],
            [-97.42408, 27.264073],
            [-97.422299, 27.257712],
            [-97.434767, 27.202241],
            [-97.444945, 27.144734],
            [-97.443673, 27.116235],
            [-97.452324, 27.115217],
            [-97.455887, 27.110383],
            [-97.45665, 27.099695],
            [-97.461739, 27.095624],
            [-97.47548, 27.098423],
            [-97.480569, 27.102494],
            [-97.49151, 27.101222],
            [-97.495836, 27.094098],
            [-97.493291, 27.078067],
            [-97.477515, 27.066108],
            [-97.479042, 27.0628],
            [-97.482257, 27.061942],
            [-97.48693, 27.057711],
            [-97.487693, 27.053639],
            [-97.486676, 27.03481],
            [-97.477515, 27.03252],
            [-97.473953, 27.029212],
            [-97.473444, 27.02285],
            [-97.478533, 26.999186],
            [-97.480569, 26.997659],
            [-97.484131, 27.000458],
            [-97.536803, 26.999695],
            [-97.549271, 26.995878],
            [-97.555378, 26.99028],
            [-97.551053, 26.980865],
            [-97.549526, 26.965344],
            [-97.552325, 26.952112],
            [-97.555378, 26.947277],
            [-97.555378, 26.93888],
            [-97.540874, 26.90631],
            [-97.540111, 26.900967],
            [-97.547999, 26.895114],
            [-97.552325, 26.888499],
            [-97.552325, 26.867633],
            [-97.558432, 26.864325],
            [-97.563266, 26.842188],
            [-97.552579, 26.827938],
            [-97.547745, 26.824631],
            [-97.537566, 26.824885],
            [-97.509831, 26.803511],
            [-97.484385, 26.763562],
            [-97.478024, 26.7572],
            [-97.471663, 26.758727],
            [-97.468609, 26.740915],
            [-97.467337, 26.710126],
            [-97.444945, 26.633535],
            [-97.445708, 26.609362],
            [-97.441206, 26.599901],
            [-97.861688, 26.597995],
            [-97.957405, 26.611769],
            [-97.985303, 26.615809],
            [-97.985494, 26.780917],
            [-97.985887, 27.209308],
            [-97.951904, 27.233249],
            [-97.63668, 27.281727]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48135",
      state: "48",
      county: "135",
      name: "Ector County",
      censusArea: 897.687,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.799086, 32.085795],
          [-102.287048, 32.086991],
          [-102.287345, 31.651276],
          [-102.288782, 31.65137],
          [-102.31805, 31.651327],
          [-102.767246, 31.651714],
          [-102.798939, 31.651783],
          [-102.799086, 32.085795]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48141",
      state: "48",
      county: "141",
      name: "El Paso County",
      censusArea: 1012.693,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-106.206018, 31.466648],
          [-106.207524, 31.467872],
          [-106.2092, 31.469234],
          [-106.212918, 31.473409],
          [-106.218538, 31.47972],
          [-106.254062, 31.547512],
          [-106.356938, 31.70221],
          [-106.381039, 31.73211],
          [-106.41794, 31.752009],
          [-106.436743, 31.756872],
          [-106.472514, 31.751802],
          [-106.528543, 31.783907],
          [-106.528543, 31.784407],
          [-106.527997, 31.786945],
          [-106.527623, 31.789119],
          [-106.527738, 31.789761],
          [-106.527943, 31.790507],
          [-106.530515, 31.792103],
          [-106.53248, 31.791914],
          [-106.533, 31.791829],
          [-106.533043, 31.791907],
          [-106.534743, 31.796107],
          [-106.535154, 31.797089],
          [-106.535343, 31.797507],
          [-106.535843, 31.798607],
          [-106.542097, 31.802146],
          [-106.542144, 31.802107],
          [-106.544714, 31.804287],
          [-106.545344, 31.805007],
          [-106.563444, 31.812606],
          [-106.566844, 31.813306],
          [-106.605245, 31.845905],
          [-106.605845, 31.846305],
          [-106.614637, 31.84649],
          [-106.621857, 31.852854],
          [-106.625763, 31.856276],
          [-106.627808, 31.860593],
          [-106.616136, 31.948439],
          [-106.614702, 31.956],
          [-106.599096, 32.000731],
          [-106.598639, 32.000754],
          [-106.595333, 32.000778],
          [-106.587972, 32.000749],
          [-106.566056, 32.000759],
          [-106.565142, 32.000736],
          [-106.411075, 32.001334],
          [-106.394298, 32.001484],
          [-106.377174, 32.00149],
          [-106.313307, 32.001512],
          [-106.205915, 32.001762],
          [-106.200699, 32.001785],
          [-106.18184, 32.00205],
          [-106.09976, 32.002174],
          [-105.998003, 32.002328],
          [-105.997658, 31.386489],
          [-106.00693, 31.392826],
          [-106.099393, 31.41476],
          [-106.132782, 31.425367],
          [-106.202851, 31.464073],
          [-106.206018, 31.466648]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48155",
      state: "48",
      county: "155",
      name: "Foard County",
      censusArea: 704.397,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.475294, 34.085618],
          [-99.47565, 33.834005],
          [-99.474498, 33.733849],
          [-99.599863, 33.787634],
          [-99.835143, 33.8358],
          [-99.996434, 33.835967],
          [-100.04848, 33.835974],
          [-100.047028, 34.22978],
          [-99.997621, 34.224355],
          [-99.772043, 34.093286],
          [-99.65301, 34.098971],
          [-99.547509, 34.05598],
          [-99.475294, 34.085618]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48161",
      state: "48",
      county: "161",
      name: "Freestone County",
      censusArea: 877.739,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.496713, 31.796191],
          [-96.05478, 32.012526],
          [-96.05288, 32.008198],
          [-96.052786, 32.005895],
          [-96.062172, 31.95634],
          [-96.022955, 31.957581],
          [-96.027788, 31.878242],
          [-95.994127, 31.866258],
          [-95.980568, 31.784561],
          [-95.875937, 31.755503],
          [-95.861262, 31.687451],
          [-95.794081, 31.66031],
          [-95.7873, 31.618385],
          [-96.236629, 31.413392],
          [-96.496713, 31.796191]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48165",
      state: "48",
      county: "165",
      name: "Gaines County",
      censusArea: 1502.375,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.20852, 32.958956],
          [-102.202703, 32.523271],
          [-102.211037, 32.523244],
          [-103.064696, 32.522193],
          [-103.064761, 32.587983],
          [-103.064788, 32.600397],
          [-103.064761, 32.601863],
          [-103.064815, 32.624537],
          [-103.064633, 32.64642],
          [-103.064864, 32.682647],
          [-103.064798, 32.690761],
          [-103.064799, 32.708694],
          [-103.064827, 32.726628],
          [-103.064807, 32.777303],
          [-103.064698, 32.783602],
          [-103.064711, 32.784593],
          [-103.064699, 32.827531],
          [-103.064672, 32.82847],
          [-103.064889, 32.849359],
          [-103.064916, 32.85726],
          [-103.064807, 32.857696],
          [-103.064862, 32.868346],
          [-103.064701, 32.879355],
          [-103.064569, 32.900014],
          [-103.064657, 32.959097],
          [-102.595023, 32.958831],
          [-102.20852, 32.958956]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51107",
      state: "51",
      county: "107",
      name: "Loudoun County",
      censusArea: 515.561,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.655081, 38.942635],
          [-77.960269, 39.013384],
          [-77.961956, 39.013841],
          [-77.828157, 39.132329],
          [-77.822182, 39.139985],
          [-77.771415, 39.236776],
          [-77.767277, 39.24938],
          [-77.768992, 39.256417],
          [-77.768, 39.257657],
          [-77.761768, 39.263031],
          [-77.761217, 39.263721],
          [-77.758733, 39.268114],
          [-77.758412, 39.269197],
          [-77.755698, 39.274575],
          [-77.755193, 39.275191],
          [-77.753105, 39.27734],
          [-77.75306, 39.277971],
          [-77.753357, 39.280331],
          [-77.750267, 39.289284],
          [-77.719029, 39.321125],
          [-77.677505, 39.318699],
          [-77.667749, 39.318129],
          [-77.592739, 39.30129],
          [-77.560854, 39.286152],
          [-77.545846, 39.271535],
          [-77.543228, 39.266937],
          [-77.534461, 39.262361],
          [-77.511222, 39.2535],
          [-77.484605, 39.245941],
          [-77.46021, 39.228359],
          [-77.45768, 39.22502],
          [-77.458779, 39.22028],
          [-77.458884, 39.219826],
          [-77.478596, 39.189168],
          [-77.485971, 39.185665],
          [-77.505162, 39.18205],
          [-77.510631, 39.178484],
          [-77.516426, 39.170891],
          [-77.527282, 39.146236],
          [-77.524559, 39.127821],
          [-77.519929, 39.120925],
          [-77.458202, 39.073723],
          [-77.42318, 39.066878],
          [-77.375079, 39.061297],
          [-77.340287, 39.062991],
          [-77.328002, 39.058554],
          [-77.328318, 39.057678],
          [-77.53576, 38.847474],
          [-77.655081, 38.942635]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51131",
      state: "51",
      county: "131",
      name: "Northampton County",
      censusArea: 211.612,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-75.937665, 37.549652],
          [-75.90041, 37.557265],
          [-75.884455, 37.549542],
          [-75.835214, 37.554245],
          [-75.788012, 37.528816],
          [-75.809357, 37.465923],
          [-75.776564, 37.454589],
          [-75.763912, 37.463308],
          [-75.713275, 37.449876],
          [-75.6655, 37.467319],
          [-75.664311, 37.458901],
          [-75.66179, 37.455028],
          [-75.665957, 37.439209],
          [-75.672648, 37.429915],
          [-75.697914, 37.405301],
          [-75.720739, 37.373129],
          [-75.727335, 37.360346],
          [-75.725634, 37.358416],
          [-75.726691, 37.350127],
          [-75.735829, 37.335426],
          [-75.765401, 37.305596],
          [-75.778817, 37.297176],
          [-75.780766, 37.297222],
          [-75.784634, 37.300976],
          [-75.791913, 37.300589],
          [-75.798448, 37.296285],
          [-75.79083, 37.276207],
          [-75.799343, 37.251779],
          [-75.795881, 37.236922],
          [-75.790386, 37.231225],
          [-75.789929, 37.228134],
          [-75.790903, 37.225066],
          [-75.804446, 37.208011],
          [-75.800468, 37.201029],
          [-75.800755, 37.197297],
          [-75.830341, 37.1706],
          [-75.87767, 37.135604],
          [-75.886369, 37.126085],
          [-75.897298, 37.118037],
          [-75.906734, 37.114193],
          [-75.912308, 37.115154],
          [-75.913222, 37.119849],
          [-75.92552, 37.133601],
          [-75.942539, 37.125142],
          [-75.945872, 37.120514],
          [-75.962596, 37.117535],
          [-75.97043, 37.118608],
          [-75.970004, 37.128861],
          [-75.978083, 37.157338],
          [-75.998647, 37.188739],
          [-76.006094, 37.19481],
          [-76.013071, 37.205366],
          [-76.013778, 37.219263],
          [-76.010535, 37.231579],
          [-76.014026, 37.235381],
          [-76.025753, 37.257407],
          [-76.023664, 37.268971],
          [-76.015507, 37.280874],
          [-76.023475, 37.289067],
          [-76.018645, 37.31782],
          [-76.014251, 37.331943],
          [-75.997778, 37.351739],
          [-75.987122, 37.368548],
          [-75.97997, 37.404608],
          [-75.983105, 37.415802],
          [-75.981624, 37.434116],
          [-75.976491, 37.444878],
          [-75.960877, 37.467562],
          [-75.963496, 37.475352],
          [-75.963326, 37.481785],
          [-75.958966, 37.500133],
          [-75.949974, 37.521876],
          [-75.940318, 37.534582],
          [-75.937665, 37.549652]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51147",
      state: "51",
      county: "147",
      name: "Prince Edward County",
      censusArea: 349.96,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.231234, 37.29622],
          [-78.239354, 37.120089],
          [-78.443319, 37.0794],
          [-78.547128, 37.150431],
          [-78.692472, 37.204786],
          [-78.681573, 37.248759],
          [-78.594436, 37.397974],
          [-78.465089, 37.339677],
          [-78.32184, 37.309292],
          [-78.235039, 37.368102],
          [-78.231234, 37.29622]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51149",
      state: "51",
      county: "149",
      name: "Prince George County",
      censusArea: 265.155,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.310532, 37.261595],
          [-77.284019, 37.313012],
          [-77.282659, 37.318084],
          [-77.270675, 37.320756],
          [-77.069269, 37.269742],
          [-77.010096, 37.305251],
          [-76.972439, 37.248501],
          [-77.155415, 37.112034],
          [-77.173095, 37.105391],
          [-77.17344, 37.105238],
          [-77.382384, 37.00015],
          [-77.398498, 36.992985],
          [-77.396791, 37.152758],
          [-77.399203, 37.17085],
          [-77.376728, 37.244993],
          [-77.377381, 37.255541],
          [-77.373735, 37.266949],
          [-77.368823, 37.272613],
          [-77.360455, 37.277333],
          [-77.356582, 37.283125],
          [-77.35271, 37.297957],
          [-77.352518, 37.310165],
          [-77.334549, 37.312468],
          [-77.310532, 37.261595]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51159",
      state: "51",
      county: "159",
      name: "Richmond County",
      censusArea: 191.492,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.507828, 37.838814],
          [-76.642276, 37.792317],
          [-76.651413, 37.796239],
          [-76.658302, 37.806815],
          [-76.680197, 37.825654],
          [-76.692747, 37.82277],
          [-76.701606, 37.822677],
          [-76.722156, 37.83668],
          [-76.72718, 37.842263],
          [-76.733046, 37.852009],
          [-76.738395, 37.865373],
          [-76.747552, 37.875864],
          [-76.765711, 37.879274],
          [-76.77539, 37.874306],
          [-76.905403, 38.003469],
          [-76.936959, 38.077074],
          [-76.877062, 38.124358],
          [-76.764716, 38.001979],
          [-76.63423, 37.96663],
          [-76.556042, 37.931524],
          [-76.507828, 37.838814]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51161",
      state: "51",
      county: "161",
      name: "Roanoke County",
      censusArea: 250.518,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.945616, 37.362997],
          [-79.865099, 37.328417],
          [-79.847599, 37.309177],
          [-79.847082, 37.225468],
          [-79.962301, 37.137099],
          [-80.030562, 37.17481],
          [-80.128119, 37.122912],
          [-80.172264, 37.108156],
          [-80.178032, 37.113435],
          [-80.205224, 37.248042],
          [-80.25852, 37.308344],
          [-80.261984, 37.340694],
          [-80.073985, 37.422305],
          [-80.062622, 37.408325],
          [-79.945616, 37.362997]
        ],
        [
          [-79.974313, 37.335227],
          [-79.979809, 37.331751],
          [-79.99312, 37.324829],
          [-80.001111, 37.319196],
          [-80.004807, 37.315614],
          [-80.012638, 37.316491],
          [-80.017082, 37.314342],
          [-80.022428, 37.308156],
          [-80.12049, 37.287053],
          [-80.033476, 37.262811],
          [-79.950384, 37.211274],
          [-79.904962, 37.244325],
          [-79.917601, 37.280899],
          [-79.88258, 37.29461],
          [-79.878186, 37.291745],
          [-79.904965, 37.320537],
          [-79.974313, 37.335227]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51165",
      state: "51",
      county: "165",
      name: "Rockingham County",
      censusArea: 849.086,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.225839, 38.479446],
          [-79.210026, 38.494231],
          [-79.210008, 38.494283],
          [-79.209703, 38.495574],
          [-79.207873, 38.500122],
          [-79.207884, 38.500428],
          [-79.174881, 38.566314],
          [-79.174512, 38.566531],
          [-79.146974, 38.625641],
          [-79.146741, 38.625819],
          [-79.137012, 38.640655],
          [-79.136374, 38.6424],
          [-79.135546, 38.643715],
          [-79.135472, 38.644057],
          [-79.092271, 38.699208],
          [-79.092555, 38.700149],
          [-79.057253, 38.761413],
          [-79.055354, 38.782213],
          [-78.999752, 38.846162],
          [-78.993997, 38.850102],
          [-78.869276, 38.762991],
          [-78.641769, 38.604719],
          [-78.691312, 38.510008],
          [-78.486003, 38.421486],
          [-78.551491, 38.336315],
          [-78.663174, 38.277923],
          [-78.749354, 38.206621],
          [-79.225839, 38.479446]
        ],
        [
          [-78.870651, 38.482606],
          [-78.872978, 38.48851],
          [-78.914591, 38.389755],
          [-78.824253, 38.435631],
          [-78.870651, 38.482606]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51175",
      state: "51",
      county: "175",
      name: "Southampton County",
      censusArea: 599.145,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.953546, 36.94445],
          [-76.849684, 36.996211],
          [-76.816162, 36.914907],
          [-76.924629, 36.707729],
          [-76.949535, 36.708617],
          [-76.967212, 36.702588],
          [-76.968027, 36.682971],
          [-76.949327, 36.67873],
          [-76.962339, 36.667515],
          [-76.909672, 36.648175],
          [-76.907702, 36.64719],
          [-76.9004, 36.645731],
          [-76.897592, 36.64405],
          [-76.915384, 36.543856],
          [-76.916001, 36.543818],
          [-76.916048, 36.543815],
          [-77.1645, 36.54633],
          [-77.24969, 36.544745],
          [-77.296875, 36.544746],
          [-77.377963, 36.633048],
          [-77.501917, 36.670506],
          [-77.430127, 36.707658],
          [-76.953546, 36.94445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51177",
      state: "51",
      county: "177",
      name: "Spotsylvania County",
      censusArea: 401.496,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.726689, 38.323905],
          [-77.719737, 38.335508],
          [-77.714698, 38.33669],
          [-77.718646, 38.341602],
          [-77.71245, 38.345447],
          [-77.713032, 38.351431],
          [-77.705491, 38.354837],
          [-77.707798, 38.358565],
          [-77.703189, 38.360598],
          [-77.618727, 38.367835],
          [-77.530289, 38.309172],
          [-77.532313, 38.308498],
          [-77.529618, 38.30838],
          [-77.526039, 38.307112],
          [-77.522272, 38.306589],
          [-77.520165, 38.306699],
          [-77.516344, 38.306402],
          [-77.516421, 38.305614],
          [-77.514883, 38.305499],
          [-77.516805, 38.295128],
          [-77.507563, 38.292143],
          [-77.511826, 38.279953],
          [-77.497909, 38.28291],
          [-77.494708, 38.273834],
          [-77.473388, 38.270245],
          [-77.467151, 38.27978],
          [-77.457837, 38.271108],
          [-77.447126, 38.284614],
          [-77.420148, 38.257986],
          [-77.39085, 38.245589],
          [-77.370301, 38.246576],
          [-77.37467, 38.243099],
          [-77.518179, 38.10489],
          [-77.551611, 38.073609],
          [-77.626219, 38.003429],
          [-77.6422, 37.990928],
          [-77.642318, 37.992284],
          [-77.647163, 37.993948],
          [-77.649201, 37.994168],
          [-77.65367, 37.993595],
          [-77.657916, 37.991864],
          [-77.659698, 37.992082],
          [-77.660469, 37.993726],
          [-77.660602, 37.997145],
          [-77.662338, 37.997764],
          [-77.664779, 37.996902],
          [-77.66716, 37.997079],
          [-77.667985, 37.998379],
          [-77.667921, 38.00012],
          [-77.670898, 38.003769],
          [-77.676618, 38.006409],
          [-77.680592, 38.007587],
          [-77.684065, 38.008283],
          [-77.687294, 38.008011],
          [-77.786614, 38.076337],
          [-77.954819, 38.117437],
          [-77.726689, 38.323905]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55061",
      state: "55",
      county: "061",
      name: "Kewaunee County",
      censusArea: 342.518,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.373228, 44.675593],
          [-87.37849, 44.666534],
          [-87.38684, 44.652157],
          [-87.393521, 44.640655],
          [-87.401629, 44.631191],
          [-87.403724, 44.628974],
          [-87.40541, 44.627191],
          [-87.418028, 44.62087],
          [-87.435766, 44.606472],
          [-87.437493, 44.605071],
          [-87.437751, 44.604559],
          [-87.446963, 44.586274],
          [-87.467089, 44.553557],
          [-87.467214, 44.553355],
          [-87.467398, 44.553055],
          [-87.468093, 44.551925],
          [-87.472934, 44.539338],
          [-87.481884, 44.516065],
          [-87.483696, 44.511354],
          [-87.483873, 44.505742],
          [-87.483914, 44.504425],
          [-87.484042, 44.503856],
          [-87.486822, 44.491477],
          [-87.490024, 44.477224],
          [-87.492333, 44.472804],
          [-87.494678, 44.468314],
          [-87.495176, 44.46736],
          [-87.495252, 44.467215],
          [-87.498662, 44.460686],
          [-87.49886, 44.459737],
          [-87.498872, 44.45968],
          [-87.498934, 44.459384],
          [-87.499349, 44.457395],
          [-87.499674, 44.455837],
          [-87.499719, 44.455625],
          [-87.502309, 44.443219],
          [-87.506362, 44.423804],
          [-87.507651, 44.421418],
          [-87.511387, 44.414502],
          [-87.511635, 44.414043],
          [-87.512311, 44.411941],
          [-87.512886, 44.410153],
          [-87.515482, 44.402077],
          [-87.517965, 44.394356],
          [-87.517881, 44.390082],
          [-87.517597, 44.375696],
          [-87.51774, 44.375357],
          [-87.518094, 44.374519],
          [-87.519106, 44.372122],
          [-87.521047, 44.367526],
          [-87.521663, 44.366719],
          [-87.533583, 44.351111],
          [-87.533606, 44.351052],
          [-87.541517, 44.331123],
          [-87.54295, 44.327513],
          [-87.766194, 44.327185],
          [-87.762376, 44.644501],
          [-87.756048, 44.649117],
          [-87.756031, 44.649129],
          [-87.750899, 44.656192],
          [-87.74909, 44.664131],
          [-87.748409, 44.667122],
          [-87.736058, 44.677025],
          [-87.375091, 44.675601],
          [-87.373228, 44.675593]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56039",
      state: "56",
      county: "039",
      name: "Teton County",
      censusArea: 3995.379,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-111.048751, 44.060403],
          [-111.048751, 44.060838],
          [-111.048633, 44.062903],
          [-111.049216, 44.435811],
          [-111.049194, 44.438058],
          [-111.048974, 44.474072],
          [-111.055208, 44.624927],
          [-111.055495, 44.666262],
          [-110.667867, 44.666291],
          [-110.667899, 44.582792],
          [-110.375334, 44.583016],
          [-110.29485, 44.546923],
          [-110.296648, 44.431341],
          [-110.1511, 44.292464],
          [-110.111284, 44.199375],
          [-110.119193, 44.132584],
          [-110.053357, 44.132628],
          [-110.053179, 44.008022],
          [-110.05157, 43.464783],
          [-110.05731, 43.377768],
          [-110.344218, 43.377918],
          [-110.343959, 43.291165],
          [-110.57838, 43.29164],
          [-110.578656, 43.235224],
          [-110.813993, 43.235178],
          [-110.813988, 43.315906],
          [-111.044617, 43.31572],
          [-111.045205, 43.501136],
          [-111.045706, 43.659112],
          [-111.04588, 43.681033],
          [-111.046118, 43.684902],
          [-111.046051, 43.685812],
          [-111.04611, 43.687848],
          [-111.046421, 43.722059],
          [-111.046435, 43.726545],
          [-111.04634, 43.726957],
          [-111.046715, 43.815832],
          [-111.048107, 43.983096],
          [-111.048751, 44.060403]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53047",
      state: "53",
      county: "047",
      name: "Okanogan County",
      censusArea: 5267.978,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-120.851202, 49.001191],
          [-119.702016, 49.000269],
          [-119.701218, 49.000258],
          [-119.4577, 49.000261],
          [-119.428678, 49.000253],
          [-119.137274, 49.000297],
          [-119.132102, 49.000262],
          [-118.836794, 49.000209],
          [-118.835399, 48.654153],
          [-118.869672, 48.653598],
          [-118.86954, 48.481881],
          [-118.843439, 48.481682],
          [-118.845785, 47.963478],
          [-118.851972, 47.957686],
          [-118.954307, 47.940809],
          [-118.973502, 47.94357],
          [-118.982754, 47.949782],
          [-118.982394, 47.962019],
          [-118.955484, 48.023185],
          [-119.006111, 48.046143],
          [-119.062237, 48.138378],
          [-119.126141, 48.15051],
          [-119.325444, 48.099645],
          [-119.340989, 48.049512],
          [-119.502391, 48.082116],
          [-119.629835, 47.996239],
          [-119.693627, 48.032554],
          [-119.670241, 48.069775],
          [-119.771307, 48.104713],
          [-119.891656, 48.055053],
          [-119.870307, 47.960328],
          [-120.066621, 47.966378],
          [-120.14329, 48.065061],
          [-120.213493, 48.079061],
          [-120.3625, 48.168761],
          [-120.391901, 48.242963],
          [-120.559708, 48.360661],
          [-120.650211, 48.397559],
          [-120.651747, 48.536585],
          [-120.701557, 48.53162],
          [-120.752021, 48.657538],
          [-120.654676, 48.724198],
          [-120.73371, 48.7829],
          [-120.767528, 48.954385],
          [-120.851202, 49.001191]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48129",
      state: "48",
      county: "129",
      name: "Donley County",
      censusArea: 926.886,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.538978, 35.183144],
          [-100.540997, 34.750052],
          [-100.540703, 34.747723],
          [-100.944939, 34.748281],
          [-101.090749, 34.748246],
          [-101.086281, 35.18214],
          [-100.538978, 35.183144]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48167",
      state: "48",
      county: "167",
      name: "Galveston County",
      censusArea: 378.358,
      stateName: "Texas"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-94.370866, 29.55507],
            [-94.500807, 29.505367],
            [-94.594853, 29.467903],
            [-94.631084, 29.451464],
            [-94.670389, 29.43078],
            [-94.694158, 29.415632],
            [-94.708473, 29.403049],
            [-94.723959, 29.383268],
            [-94.731047, 29.369141],
            [-94.744834, 29.369158],
            [-94.761491, 29.361883],
            [-94.778691, 29.361483],
            [-94.782356, 29.364266],
            [-94.783131, 29.375642],
            [-94.766848, 29.393489],
            [-94.7541, 29.401],
            [-94.723818, 29.426536],
            [-94.706365, 29.436805],
            [-94.686386, 29.466509],
            [-94.681541, 29.471389],
            [-94.6724, 29.476843],
            [-94.665853, 29.478401],
            [-94.656737, 29.478033],
            [-94.645948, 29.473769],
            [-94.628217, 29.475986],
            [-94.608557, 29.483345],
            [-94.594211, 29.492127],
            [-94.59544, 29.507669],
            [-94.591407, 29.513858],
            [-94.580274, 29.525295],
            [-94.566674, 29.531988],
            [-94.55399, 29.529559],
            [-94.546994, 29.524379],
            [-94.532348, 29.5178],
            [-94.511045, 29.51965],
            [-94.495025, 29.525031],
            [-94.503429, 29.54325],
            [-94.509487, 29.54259],
            [-94.511086, 29.542971],
            [-94.412771, 29.572612],
            [-94.408992, 29.597467],
            [-94.371828, 29.597178],
            [-94.370866, 29.55507]
          ]
        ],
        [
          [
            [-95.223994, 29.514422],
            [-95.221862, 29.526521],
            [-95.221212, 29.529137],
            [-95.220748, 29.532688],
            [-95.219813, 29.536757],
            [-95.219395, 29.539122],
            [-95.219169, 29.541283],
            [-95.217794, 29.548449],
            [-95.217702, 29.549503],
            [-95.216788, 29.554643],
            [-95.216422, 29.55608],
            [-95.163927, 29.498051],
            [-95.018191, 29.554584],
            [-95.016889, 29.548303],
            [-95.015165, 29.539989],
            [-95.013378, 29.537822],
            [-94.999581, 29.521093],
            [-94.981916, 29.511141],
            [-94.958443, 29.505013],
            [-94.934876, 29.501079],
            [-94.933483, 29.500847],
            [-94.933039, 29.500773],
            [-94.909898, 29.49691],
            [-94.909465, 29.496838],
            [-94.913385, 29.487254],
            [-94.925914, 29.469047],
            [-94.930861, 29.450504],
            [-94.919401, 29.448031],
            [-94.8908, 29.433432],
            [-94.8873, 29.415132],
            [-94.886536, 29.366386],
            [-94.894234, 29.338],
            [-94.893994, 29.30817],
            [-94.886536, 29.297826],
            [-94.875952, 29.293015],
            [-94.865126, 29.293977],
            [-94.84973, 29.297345],
            [-94.824953, 29.306005],
            [-94.822547, 29.321882],
            [-94.822307, 29.344254],
            [-94.810696, 29.353435],
            [-94.784895, 29.335535],
            [-94.779995, 29.334935],
            [-94.777064, 29.336811],
            [-94.745529, 29.334235],
            [-94.744945, 29.33641],
            [-94.73132, 29.338066],
            [-94.72253, 29.331446],
            [-94.731082, 29.331833],
            [-94.769695, 29.304936],
            [-94.786095, 29.290737],
            [-94.803695, 29.279237],
            [-95.026219, 29.148064],
            [-95.081773, 29.111222],
            [-95.110484, 29.088224],
            [-95.119271, 29.077844],
            [-95.056528, 29.199934],
            [-95.233081, 29.465566],
            [-95.231803, 29.472607],
            [-95.229732, 29.480714],
            [-95.225168, 29.503974],
            [-95.223994, 29.514422]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48221",
      state: "48",
      county: "221",
      name: "Hood County",
      censusArea: 420.641,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.945625, 32.233468],
          [-98.068545, 32.511626],
          [-98.066836, 32.558822],
          [-97.617066, 32.555484],
          [-97.615286, 32.318618],
          [-97.78276, 32.316493],
          [-97.945625, 32.233468]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48225",
      state: "48",
      county: "225",
      name: "Houston County",
      censusArea: 1230.909,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.273203, 31.592886],
          [-95.176359, 31.584826],
          [-95.101382, 31.469887],
          [-95.003345, 31.42571],
          [-95.005566, 31.421349],
          [-95.001258, 31.417949],
          [-94.998247, 31.42004],
          [-94.997132, 31.41678],
          [-94.994108, 31.417835],
          [-94.993832, 31.41422],
          [-94.990043, 31.413356],
          [-94.988061, 31.414417],
          [-94.984753, 31.41385],
          [-94.983053, 31.411593],
          [-94.976033, 31.407744],
          [-94.979364, 31.405975],
          [-94.976291, 31.40525],
          [-94.976068, 31.402],
          [-94.97778, 31.399381],
          [-94.973581, 31.399759],
          [-94.969369, 31.396948],
          [-94.967634, 31.397412],
          [-94.964521, 31.395558],
          [-94.966254, 31.391205],
          [-94.959415, 31.388884],
          [-94.95811, 31.38693],
          [-95.434781, 31.058098],
          [-95.617872, 30.930418],
          [-95.681158, 30.986958],
          [-95.663546, 31.083884],
          [-95.76441, 31.094211],
          [-95.725226, 31.271084],
          [-95.657202, 31.317999],
          [-95.739279, 31.504056],
          [-95.651764, 31.541791],
          [-95.273203, 31.592886]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48229",
      state: "48",
      county: "229",
      name: "Hudspeth County",
      censusArea: 4570.983,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.985365, 30.623792],
          [-105.044407, 30.683895],
          [-105.129328, 30.750139],
          [-105.211234, 30.799609],
          [-105.250028, 30.799068],
          [-105.265479, 30.802536],
          [-105.394627, 30.85892],
          [-105.411833, 30.893367],
          [-105.55679, 30.989469],
          [-105.57765, 31.019811],
          [-105.579756, 31.036588],
          [-105.585323, 31.057488],
          [-105.60333, 31.082625],
          [-105.648834, 31.115902],
          [-105.709491, 31.136375],
          [-105.750519, 31.16494],
          [-105.793719, 31.201713],
          [-105.869353, 31.288634],
          [-105.900315, 31.300008],
          [-105.923131, 31.312089],
          [-105.981027, 31.375122],
          [-105.997658, 31.386489],
          [-105.998003, 32.002328],
          [-105.9006, 32.0021],
          [-105.886159, 32.00197],
          [-105.429281, 32.000577],
          [-105.428582, 32.0006],
          [-105.427049, 32.000638],
          [-105.390396, 32.000607],
          [-105.153994, 32.000497],
          [-105.15031, 32.000497],
          [-105.14824, 32.000485],
          [-105.132916, 32.000518],
          [-105.131377, 32.000524],
          [-105.11804, 32.000485],
          [-105.078605, 32.000533],
          [-105.077046, 32.000579],
          [-104.918272, 32.000496],
          [-104.918637, 31.991805],
          [-104.907592, 30.835919],
          [-104.916396, 30.835979],
          [-104.917163, 30.663626],
          [-104.97961, 30.629363],
          [-104.985365, 30.623792]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48245",
      state: "48",
      county: "245",
      name: "Jefferson County",
      censusArea: 876.295,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.354163, 29.561459],
          [-94.357976, 29.887464],
          [-94.442235, 29.889538],
          [-94.445096, 30.110966],
          [-94.44491, 30.113015],
          [-94.292629, 30.117671],
          [-94.209577, 30.187724],
          [-94.115917, 30.160347],
          [-94.031976, 30.028013],
          [-93.860169, 29.977397],
          [-93.840799, 29.914423],
          [-93.890679, 29.843159],
          [-93.89847, 29.771577],
          [-93.892246, 29.765241],
          [-93.837971, 29.690619],
          [-93.852868, 29.675885],
          [-93.866981, 29.673085],
          [-93.88999, 29.674013],
          [-93.931, 29.679612],
          [-94.001406, 29.681486],
          [-94.056506, 29.671163],
          [-94.132577, 29.646217],
          [-94.354163, 29.561459]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48257",
      state: "48",
      county: "257",
      name: "Kaufman County",
      censusArea: 780.699,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.529987, 32.545282],
          [-96.523118, 32.545214],
          [-96.51897, 32.813616],
          [-96.297376, 32.814185],
          [-96.297322, 32.841723],
          [-96.076801, 32.838489],
          [-96.076685, 32.836799],
          [-96.075899, 32.357505],
          [-96.452138, 32.358631],
          [-96.529987, 32.545282]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51193",
      state: "51",
      county: "193",
      name: "Westmoreland County",
      censusArea: 229.375,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.936959, 38.077074],
          [-77.049695, 38.109832],
          [-77.061695, 38.161631],
          [-76.99767, 38.278047],
          [-76.990255, 38.273935],
          [-76.981372, 38.274214],
          [-76.96215, 38.256486],
          [-76.957796, 38.243183],
          [-76.957417, 38.236341],
          [-76.962375, 38.230093],
          [-76.966553, 38.229542],
          [-76.967335, 38.227185],
          [-76.962311, 38.214075],
          [-76.937134, 38.202384],
          [-76.916922, 38.199751],
          [-76.910832, 38.197073],
          [-76.875272, 38.172207],
          [-76.838795, 38.163476],
          [-76.824274, 38.163639],
          [-76.802968, 38.167988],
          [-76.788445, 38.169199],
          [-76.760241, 38.166581],
          [-76.749685, 38.162114],
          [-76.743064, 38.156988],
          [-76.740278, 38.152824],
          [-76.738938, 38.14651],
          [-76.721722, 38.137635],
          [-76.704048, 38.149264],
          [-76.701297, 38.155718],
          [-76.684892, 38.156497],
          [-76.665127, 38.147638],
          [-76.643448, 38.14825],
          [-76.638983, 38.151476],
          [-76.629476, 38.15305],
          [-76.613939, 38.148587],
          [-76.604131, 38.128771],
          [-76.600937, 38.110084],
          [-76.579497, 38.09487],
          [-76.543155, 38.076971],
          [-76.535919, 38.069532],
          [-76.522354, 38.04259],
          [-76.519536, 38.034814],
          [-76.63423, 37.96663],
          [-76.764716, 38.001979],
          [-76.877062, 38.124358],
          [-76.936959, 38.077074]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51199",
      state: "51",
      county: "199",
      name: "York County",
      censusArea: 104.78,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.435519, 37.094882],
          [-76.525624, 37.176628],
          [-76.565092, 37.220645],
          [-76.581575, 37.212845],
          [-76.582487, 37.212614],
          [-76.587528, 37.213086],
          [-76.591483, 37.215083],
          [-76.591432, 37.214721],
          [-76.679468, 37.265652],
          [-76.684705, 37.270068],
          [-76.677054, 37.27717],
          [-76.681182, 37.292721],
          [-76.690217, 37.292656],
          [-76.691736, 37.287459],
          [-76.709775, 37.28229],
          [-76.710367, 37.27765],
          [-76.715314, 37.280562],
          [-76.727264, 37.298783],
          [-76.727588, 37.304921],
          [-76.741667, 37.37044],
          [-76.658651, 37.380139],
          [-76.4989, 37.241015],
          [-76.50364, 37.233856],
          [-76.494008, 37.225408],
          [-76.471799, 37.216016],
          [-76.394132, 37.22515],
          [-76.389793, 37.222981],
          [-76.3936, 37.214049],
          [-76.396052, 37.201087],
          [-76.389284, 37.193503],
          [-76.391252, 37.179887],
          [-76.397883, 37.164415],
          [-76.413638, 37.137968],
          [-76.39447, 37.106248],
          [-76.409614, 37.091905],
          [-76.435519, 37.094882]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53031",
      state: "53",
      county: "031",
      name: "Jefferson County",
      censusArea: 1803.7,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-122.927146, 48.065133],
          [-122.926851, 48.064593],
          [-122.918602, 48.058238],
          [-122.877641, 48.047025],
          [-122.849273, 48.053808],
          [-122.857727, 48.065774],
          [-122.878255, 48.076072],
          [-122.882013, 48.100779],
          [-122.876282, 48.110877],
          [-122.833173, 48.134406],
          [-122.784076, 48.142974],
          [-122.760448, 48.14324],
          [-122.748911, 48.117026],
          [-122.773177, 48.106864],
          [-122.778466, 48.106135],
          [-122.792902, 48.09718],
          [-122.798464, 48.092451],
          [-122.801399, 48.087561],
          [-122.770559, 48.053432],
          [-122.770496, 48.047897],
          [-122.766648, 48.04429],
          [-122.74229, 48.049324],
          [-122.740007, 48.054116],
          [-122.739271, 48.069153],
          [-122.741184, 48.070958],
          [-122.747389, 48.070795],
          [-122.748345, 48.072097],
          [-122.733257, 48.091232],
          [-122.718558, 48.097567],
          [-122.698465, 48.103102],
          [-122.68724, 48.101662],
          [-122.69164, 48.096726],
          [-122.69222, 48.087081],
          [-122.682264, 48.042723],
          [-122.677153, 48.036346],
          [-122.668942, 48.032026],
          [-122.669868, 48.017217],
          [-122.686898, 48.008305],
          [-122.690066, 48.00842],
          [-122.697185, 48.014978],
          [-122.70184, 48.016106],
          [-122.723374, 48.008095],
          [-122.718082, 47.987739],
          [-122.701294, 47.972979],
          [-122.683223, 47.972226],
          [-122.6788, 47.96793],
          [-122.676215, 47.958743],
          [-122.684688, 47.944049],
          [-122.68445, 47.939593],
          [-122.681924, 47.936415],
          [-122.66238, 47.9307],
          [-122.657722, 47.931156],
          [-122.651063, 47.920985],
          [-122.65399, 47.91589],
          [-122.655085, 47.905058],
          [-122.646494, 47.894771],
          [-122.637425, 47.889945],
          [-122.618873, 47.890242],
          [-122.610341, 47.887343],
          [-122.631857, 47.874815],
          [-122.633879, 47.868401],
          [-122.63636, 47.866186],
          [-122.650083, 47.86386],
          [-122.666417, 47.867497],
          [-122.69376, 47.868002],
          [-122.690974, 47.860118],
          [-122.681602, 47.850405],
          [-122.683742, 47.838773],
          [-122.688596, 47.831438],
          [-122.719609, 47.813036],
          [-122.731956, 47.809741],
          [-122.748061, 47.800546],
          [-122.75054, 47.773966],
          [-122.757885, 47.757744],
          [-122.758498, 47.746036],
          [-122.749199, 47.741063],
          [-122.783237, 47.672754],
          [-122.950473, 47.606266],
          [-123.504385, 47.60701],
          [-123.50438, 47.517609],
          [-124.047108, 47.51812],
          [-124.353651, 47.53361],
          [-124.355955, 47.545698],
          [-124.359028, 47.547616],
          [-124.366221, 47.582439],
          [-124.371746, 47.599575],
          [-124.374927, 47.603891],
          [-124.382215, 47.632302],
          [-124.395983, 47.665534],
          [-124.412106, 47.691199],
          [-124.420219, 47.725294],
          [-124.425195, 47.738434],
          [-124.430546, 47.746249],
          [-124.453927, 47.765334],
          [-124.471687, 47.766907],
          [-124.47657, 47.769671],
          [-124.482154, 47.797454],
          [-124.489737, 47.816988],
          [-124.497987, 47.822605],
          [-124.50668, 47.82391],
          [-124.51278, 47.822518],
          [-124.539927, 47.836967],
          [-124.558254, 47.855979],
          [-124.559034, 47.863085],
          [-124.562363, 47.866216],
          [-124.588172, 47.877878],
          [-124.609538, 47.879996],
          [-124.610763, 47.880607],
          [-123.505917, 47.88108],
          [-123.505917, 47.866897],
          [-122.949167, 47.866829],
          [-122.949055, 48.065151],
          [-122.927146, 48.065133]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55031",
      state: "55",
      county: "031",
      name: "Douglas County",
      censusArea: 1304.136,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.212392, 46.649941],
          [-92.207092, 46.651941],
          [-92.202292, 46.655041],
          [-92.197145, 46.663408],
          [-92.191501, 46.672586],
          [-92.191075, 46.673278],
          [-92.187592, 46.678941],
          [-92.187795, 46.690185],
          [-92.193439, 46.695202],
          [-92.194087, 46.696626],
          [-92.194562, 46.697672],
          [-92.196575, 46.7021],
          [-92.196435, 46.702938],
          [-92.196264, 46.703966],
          [-92.195321, 46.709626],
          [-92.189091, 46.717541],
          [-92.172997, 46.724444],
          [-92.170413, 46.725553],
          [-92.11659, 46.74864],
          [-92.08949, 46.74924],
          [-92.03399, 46.708939],
          [-92.02472, 46.705624],
          [-92.020289, 46.704039],
          [-92.01529, 46.706469],
          [-92.007989, 46.705039],
          [-91.987889, 46.692739],
          [-91.973389, 46.686439],
          [-91.961889, 46.682539],
          [-91.942988, 46.679939],
          [-91.930261, 46.682273],
          [-91.886963, 46.690211],
          [-91.87873, 46.690811],
          [-91.877371, 46.69091],
          [-91.876183, 46.690997],
          [-91.87484, 46.691095],
          [-91.866583, 46.691697],
          [-91.864491, 46.69185],
          [-91.857462, 46.692362],
          [-91.840288, 46.689693],
          [-91.831355, 46.689906],
          [-91.820027, 46.690176],
          [-91.817185, 46.690604],
          [-91.817099, 46.690617],
          [-91.814669, 46.690982],
          [-91.799987, 46.693192],
          [-91.798455, 46.693422],
          [-91.790473, 46.694624],
          [-91.790132, 46.694675],
          [-91.781928, 46.697604],
          [-91.758619, 46.705927],
          [-91.74965, 46.709129],
          [-91.748787, 46.709341],
          [-91.735632, 46.712575],
          [-91.677021, 46.726984],
          [-91.675621, 46.727329],
          [-91.667534, 46.729317],
          [-91.662426, 46.730572],
          [-91.66067, 46.731004],
          [-91.658739, 46.731479],
          [-91.652915, 46.732911],
          [-91.646146, 46.734575],
          [-91.645502, 46.734733],
          [-91.636919, 46.737802],
          [-91.63604, 46.738116],
          [-91.636005, 46.738128],
          [-91.635161, 46.73843],
          [-91.635107, 46.738449],
          [-91.625387, 46.741924],
          [-91.593442, 46.753345],
          [-91.592742, 46.753595],
          [-91.592203, 46.753788],
          [-91.590684, 46.754331],
          [-91.576632, 46.757037],
          [-91.574291, 46.757488],
          [-91.574019, 46.757466],
          [-91.571423, 46.75726],
          [-91.569075, 46.757074],
          [-91.557503, 46.756155],
          [-91.551445, 46.755674],
          [-91.551408, 46.755666],
          [-91.551282, 46.157045],
          [-92.049636, 46.157597],
          [-92.293706, 46.157321],
          [-92.293744, 46.166838],
          [-92.293857, 46.180073],
          [-92.293558, 46.224578],
          [-92.293619, 46.244043],
          [-92.293074, 46.295129],
          [-92.293007, 46.297987],
          [-92.29284, 46.304319],
          [-92.292839, 46.307107],
          [-92.29288, 46.313752],
          [-92.292803, 46.314628],
          [-92.292782, 46.319312],
          [-92.292999, 46.321894],
          [-92.29286, 46.41722],
          [-92.292847, 46.420876],
          [-92.292727, 46.431993],
          [-92.29251, 46.478761],
          [-92.292371, 46.495585],
          [-92.291647, 46.604649],
          [-92.291597, 46.624941],
          [-92.292192, 46.663242],
          [-92.277155, 46.655596],
          [-92.276373, 46.655198],
          [-92.274465, 46.654227],
          [-92.269948, 46.65193],
          [-92.264462, 46.651393],
          [-92.262503, 46.651201],
          [-92.256654, 46.650628],
          [-92.254379, 46.650405],
          [-92.250861, 46.650061],
          [-92.242493, 46.649241],
          [-92.234815, 46.64942],
          [-92.232647, 46.64947],
          [-92.231944, 46.649486],
          [-92.228155, 46.649574],
          [-92.222338, 46.64971],
          [-92.221731, 46.649724],
          [-92.212392, 46.649941]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55051",
      state: "55",
      county: "051",
      name: "Iron County",
      censusArea: 758.174,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.549596, 46.584138],
          [-90.538346, 46.581182],
          [-90.537962, 46.581081],
          [-90.537829, 46.581143],
          [-90.527776, 46.585858],
          [-90.525788, 46.58679],
          [-90.525498, 46.586926],
          [-90.525334, 46.586949],
          [-90.522674, 46.587313],
          [-90.519105, 46.587803],
          [-90.519002, 46.587817],
          [-90.51851, 46.587885],
          [-90.505909, 46.589614],
          [-90.497359, 46.585509],
          [-90.478826, 46.57661],
          [-90.478469, 46.576439],
          [-90.47648, 46.575484],
          [-90.476315, 46.575405],
          [-90.476046, 46.575276],
          [-90.47376, 46.574178],
          [-90.472483, 46.57373],
          [-90.440085, 46.562365],
          [-90.438174, 46.561695],
          [-90.437596, 46.561492],
          [-90.437532, 46.561507],
          [-90.436512, 46.561748],
          [-90.436314, 46.561795],
          [-90.434812, 46.56215],
          [-90.433367, 46.562492],
          [-90.418136, 46.566094],
          [-90.414596, 46.55732],
          [-90.414464, 46.55732],
          [-90.400429, 46.544384],
          [-90.400041, 46.544384],
          [-90.374461, 46.539212],
          [-90.357676, 46.540271],
          [-90.313894, 46.516199],
          [-90.313839, 46.516199],
          [-90.307716, 46.518392],
          [-90.284545, 46.518714],
          [-90.204572, 46.478998],
          [-90.17786, 46.440548],
          [-90.166919, 46.439851],
          [-90.166909, 46.439311],
          [-90.134656, 46.374979],
          [-90.134663, 46.374947],
          [-90.119757, 46.359748],
          [-90.119691, 46.359755],
          [-90.120489, 46.336852],
          [-89.929093, 46.300087],
          [-89.928837, 45.981967],
          [-90.043958, 45.981948],
          [-90.302063, 45.981349],
          [-90.303072, 46.154849],
          [-90.426274, 46.15405],
          [-90.425978, 46.24045],
          [-90.55218, 46.240851],
          [-90.549596, 46.584138]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53049",
      state: "53",
      county: "049",
      name: "Pacific County",
      censusArea: 932.658,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.358247, 46.384227],
          [-123.72606, 46.385626],
          [-123.727913, 46.289661],
          [-123.728585, 46.288725],
          [-123.741478, 46.290274],
          [-123.75956, 46.275073],
          [-123.766682, 46.273499],
          [-123.775054, 46.274599],
          [-123.782654, 46.280227],
          [-123.795556, 46.284501],
          [-123.806139, 46.283588],
          [-123.875525, 46.239787],
          [-123.909306, 46.245491],
          [-123.919581, 46.251217],
          [-123.954353, 46.277001],
          [-123.969427, 46.291398],
          [-123.970912, 46.293866],
          [-123.970355, 46.299352],
          [-123.974509, 46.303063],
          [-123.985204, 46.309039],
          [-124.001264, 46.31326],
          [-124.020551, 46.315737],
          [-124.029924, 46.308312],
          [-124.035599, 46.296843],
          [-124.038797, 46.283675],
          [-124.044018, 46.275925],
          [-124.060961, 46.278761],
          [-124.080671, 46.267239],
          [-124.082187, 46.269159],
          [-124.081729, 46.274714],
          [-124.076262, 46.296498],
          [-124.071384, 46.305504],
          [-124.064624, 46.326899],
          [-124.058351, 46.386503],
          [-124.057425, 46.409315],
          [-124.057024, 46.493338],
          [-124.061953, 46.556165],
          [-124.06842, 46.601397],
          [-124.069583, 46.630651],
          [-124.068655, 46.634879],
          [-124.062715, 46.642582],
          [-124.056476, 46.645645],
          [-124.048444, 46.645827],
          [-124.035874, 46.630822],
          [-124.052708, 46.622796],
          [-124.050842, 46.617421],
          [-124.028799, 46.59104],
          [-124.023566, 46.582559],
          [-124.023148, 46.564113],
          [-124.026019, 46.531589],
          [-124.031737, 46.496375],
          [-124.026032, 46.462978],
          [-124.001271, 46.459992],
          [-123.990615, 46.463019],
          [-123.99087, 46.465738],
          [-123.994181, 46.468868],
          [-123.99268, 46.488617],
          [-123.988386, 46.497008],
          [-123.983688, 46.498542],
          [-123.979053, 46.497378],
          [-123.979213, 46.489949],
          [-123.97083, 46.47537],
          [-123.968044, 46.473497],
          [-123.943667, 46.477197],
          [-123.921192, 46.507731],
          [-123.896703, 46.522665],
          [-123.897242, 46.52848],
          [-123.894254, 46.537028],
          [-123.903321, 46.55191],
          [-123.916902, 46.562633],
          [-123.920247, 46.567343],
          [-123.922332, 46.577057],
          [-123.928861, 46.588875],
          [-123.939139, 46.596326],
          [-123.955556, 46.60357],
          [-123.959175, 46.613581],
          [-123.960642, 46.636364],
          [-123.940616, 46.640862],
          [-123.921913, 46.650262],
          [-123.920916, 46.653576],
          [-123.923269, 46.672708],
          [-123.915596, 46.678649],
          [-123.895601, 46.683672],
          [-123.864902, 46.698685],
          [-123.851356, 46.70256],
          [-123.84621, 46.716795],
          [-123.848725, 46.719898],
          [-123.862149, 46.727749],
          [-123.870782, 46.728327],
          [-123.87668, 46.730657],
          [-123.893054, 46.750204],
          [-123.898641, 46.750205],
          [-123.910716, 46.746715],
          [-123.916371, 46.741322],
          [-123.91584, 46.737322],
          [-123.91285, 46.730647],
          [-123.916874, 46.726739],
          [-123.929073, 46.725278],
          [-123.948683, 46.725369],
          [-123.968564, 46.736106],
          [-123.974994, 46.733391],
          [-123.979655, 46.724658],
          [-123.975157, 46.713971],
          [-123.966886, 46.705184],
          [-123.973663, 46.703353],
          [-123.987521, 46.707507],
          [-123.994242, 46.707929],
          [-124.003458, 46.702337],
          [-124.022413, 46.708973],
          [-124.042478, 46.72004],
          [-124.042111, 46.722783],
          [-124.046399, 46.725686],
          [-124.063117, 46.733664],
          [-124.080983, 46.735003],
          [-124.092176, 46.741624],
          [-124.096515, 46.746202],
          [-124.095041, 46.756812],
          [-124.096655, 46.784374],
          [-124.098359, 46.794157],
          [-123.370775, 46.792449],
          [-123.358247, 46.384227]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51570",
      state: "51",
      county: "570",
      name: "Colonial Heights City County",
      lsad: "city",
      censusArea: 7.52,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.41651, 37.233157],
          [-77.414406, 37.260949],
          [-77.431705, 37.276348],
          [-77.362322, 37.282162],
          [-77.379193, 37.26758],
          [-77.382521, 37.242837],
          [-77.385702, 37.241158],
          [-77.392064, 37.240853],
          [-77.394214, 37.239524],
          [-77.4021, 37.239521],
          [-77.403671, 37.237346],
          [-77.405034, 37.2361],
          [-77.406415, 37.235645],
          [-77.409877, 37.233723],
          [-77.413119, 37.234257],
          [-77.41542, 37.233796],
          [-77.41651, 37.233157]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51630",
      state: "51",
      county: "630",
      name: "Fredericksburg City County",
      lsad: "city",
      censusArea: 10.44,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.530289, 38.309172],
          [-77.526243, 38.309531],
          [-77.527185, 38.320655],
          [-77.506782, 38.325925],
          [-77.498754, 38.32543],
          [-77.478996, 38.316693],
          [-77.475137, 38.32096],
          [-77.467053, 38.31866],
          [-77.455692, 38.301341],
          [-77.447126, 38.284614],
          [-77.457837, 38.271108],
          [-77.467151, 38.27978],
          [-77.473388, 38.270245],
          [-77.494708, 38.273834],
          [-77.497909, 38.28291],
          [-77.511826, 38.279953],
          [-77.507563, 38.292143],
          [-77.516805, 38.295128],
          [-77.514883, 38.305499],
          [-77.516421, 38.305614],
          [-77.516344, 38.306402],
          [-77.520165, 38.306699],
          [-77.522272, 38.306589],
          [-77.526039, 38.307112],
          [-77.529618, 38.30838],
          [-77.532313, 38.308498],
          [-77.530289, 38.309172]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51650",
      state: "51",
      county: "650",
      name: "Hampton City County",
      lsad: "city",
      censusArea: 51.413,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.39447, 37.106248],
          [-76.393795, 37.108397],
          [-76.384553, 37.105167],
          [-76.384265, 37.107383],
          [-76.387733, 37.109181],
          [-76.381383, 37.111939],
          [-76.287236, 37.117453],
          [-76.274463, 37.094544],
          [-76.271262, 37.084544],
          [-76.292863, 37.035145],
          [-76.300352, 37.00885],
          [-76.304272, 37.001378],
          [-76.312048, 37.000371],
          [-76.315008, 37.001683],
          [-76.314624, 37.00933],
          [-76.318065, 37.013846],
          [-76.329531, 37.014556],
          [-76.34011, 37.015212],
          [-76.340666, 37.015246],
          [-76.348066, 37.006747],
          [-76.356366, 37.002947],
          [-76.373567, 36.998347],
          [-76.383367, 36.993347],
          [-76.387711, 36.989671],
          [-76.396368, 36.982347],
          [-76.408568, 36.969147],
          [-76.411768, 36.962847],
          [-76.418969, 36.964047],
          [-76.428869, 36.969947],
          [-76.452118, 36.998163],
          [-76.452461, 37.004603],
          [-76.449891, 37.004868],
          [-76.448231, 37.007705],
          [-76.464471, 37.027547],
          [-76.457871, 37.019747],
          [-76.452682, 37.024375],
          [-76.458849, 37.034299],
          [-76.449733, 37.043236],
          [-76.445859, 37.069328],
          [-76.45117, 37.076346],
          [-76.435519, 37.094882],
          [-76.409614, 37.091905],
          [-76.39447, 37.106248]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51700",
      state: "51",
      county: "700",
      name: "Newport News City County",
      lsad: "city",
      censusArea: 68.714,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.581575, 37.212845],
          [-76.565092, 37.220645],
          [-76.525624, 37.176628],
          [-76.435519, 37.094882],
          [-76.45117, 37.076346],
          [-76.445859, 37.069328],
          [-76.449733, 37.043236],
          [-76.458849, 37.034299],
          [-76.452682, 37.024375],
          [-76.457871, 37.019747],
          [-76.464471, 37.027547],
          [-76.46949, 37.030414],
          [-76.507614, 37.052188],
          [-76.509339, 37.053173],
          [-76.512289, 37.054858],
          [-76.518242, 37.055351],
          [-76.526273, 37.062947],
          [-76.527973, 37.068247],
          [-76.526573, 37.070047],
          [-76.526203, 37.077773],
          [-76.528997, 37.079388],
          [-76.536875, 37.083942],
          [-76.555066, 37.075859],
          [-76.564219, 37.077507],
          [-76.567931, 37.080467],
          [-76.579499, 37.096627],
          [-76.618252, 37.119347],
          [-76.62478, 37.127091],
          [-76.622252, 37.142146],
          [-76.617084, 37.144498],
          [-76.604476, 37.160034],
          [-76.606684, 37.166674],
          [-76.610972, 37.166994],
          [-76.611018, 37.167097],
          [-76.577675, 37.190445],
          [-76.591432, 37.214721],
          [-76.591483, 37.215083],
          [-76.587528, 37.213086],
          [-76.582487, 37.212614],
          [-76.581575, 37.212845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51735",
      state: "51",
      county: "735",
      name: "Poquoson County",
      lsad: "city",
      censusArea: 15.317,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-76.287236, 37.117453],
          [-76.381383, 37.111939],
          [-76.387733, 37.109181],
          [-76.384265, 37.107383],
          [-76.384553, 37.105167],
          [-76.393795, 37.108397],
          [-76.39447, 37.106248],
          [-76.413638, 37.137968],
          [-76.397883, 37.164415],
          [-76.399659, 37.160272],
          [-76.394756, 37.157568],
          [-76.381379, 37.155711],
          [-76.375255, 37.16084],
          [-76.35969, 37.16858],
          [-76.348658, 37.170655],
          [-76.343234, 37.166207],
          [-76.344898, 37.164479],
          [-76.34405, 37.160367],
          [-76.340129, 37.151823],
          [-76.334017, 37.144223],
          [-76.330481, 37.141727],
          [-76.324353, 37.142895],
          [-76.311088, 37.138495],
          [-76.292344, 37.126615],
          [-76.287236, 37.117453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51770",
      state: "51",
      county: "770",
      name: "Roanoke City County",
      lsad: "city",
      censusArea: 42.562,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.022428, 37.308156],
          [-80.017082, 37.314342],
          [-80.012638, 37.316491],
          [-80.004807, 37.315614],
          [-80.001111, 37.319196],
          [-79.99312, 37.324829],
          [-79.979809, 37.331751],
          [-79.974313, 37.335227],
          [-79.904965, 37.320537],
          [-79.878186, 37.291745],
          [-79.88258, 37.29461],
          [-79.917601, 37.280899],
          [-79.904962, 37.244325],
          [-79.950384, 37.211274],
          [-80.033476, 37.262811],
          [-80.01876, 37.264336],
          [-80.022428, 37.308156]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55071",
      state: "55",
      county: "071",
      name: "Manitowoc County",
      censusArea: 589.082,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.54295, 44.327513],
          [-87.545382, 44.321385],
          [-87.544725, 44.307068],
          [-87.544716, 44.306864],
          [-87.544675, 44.306707],
          [-87.541382, 44.294018],
          [-87.541265, 44.293567],
          [-87.541156, 44.293147],
          [-87.541155, 44.293143],
          [-87.541053, 44.292968],
          [-87.526466, 44.268016],
          [-87.526391, 44.267888],
          [-87.526226, 44.267605],
          [-87.525824, 44.266918],
          [-87.525797, 44.266871],
          [-87.525703, 44.266711],
          [-87.525643, 44.266609],
          [-87.525485, 44.266338],
          [-87.525455, 44.266285],
          [-87.525294, 44.26601],
          [-87.525211, 44.265869],
          [-87.524691, 44.264979],
          [-87.524459, 44.264583],
          [-87.524193, 44.264128],
          [-87.523179, 44.262393],
          [-87.52284, 44.261813],
          [-87.522557, 44.261329],
          [-87.521862, 44.26014],
          [-87.521755, 44.259957],
          [-87.52133, 44.258993],
          [-87.521318, 44.258965],
          [-87.515406, 44.245537],
          [-87.515399, 44.245522],
          [-87.515314, 44.245328],
          [-87.508457, 44.229755],
          [-87.508419, 44.229669],
          [-87.508412, 44.229546],
          [-87.507419, 44.210803],
          [-87.512903, 44.192808],
          [-87.51966, 44.17987],
          [-87.53994, 44.15969],
          [-87.550076, 44.152933],
          [-87.563181, 44.144195],
          [-87.563551, 44.144072],
          [-87.567045, 44.142914],
          [-87.567817, 44.142658],
          [-87.570533, 44.141757],
          [-87.59668, 44.133088],
          [-87.59791, 44.13268],
          [-87.599055, 44.132301],
          [-87.600882, 44.131695],
          [-87.603572, 44.13039],
          [-87.621082, 44.121895],
          [-87.6458, 44.105222],
          [-87.646583, 44.104694],
          [-87.646719, 44.104335],
          [-87.647551, 44.102128],
          [-87.647603, 44.101991],
          [-87.651507, 44.091639],
          [-87.654935, 44.082552],
          [-87.655183, 44.081894],
          [-87.653483, 44.067194],
          [-87.653691, 44.065961],
          [-87.656062, 44.051919],
          [-87.656083, 44.051794],
          [-87.671316, 44.03735],
          [-87.683361, 44.020139],
          [-87.686389, 44.01252],
          [-87.691479, 43.99971],
          [-87.695053, 43.990715],
          [-87.695503, 43.989582],
          [-87.695511, 43.989524],
          [-87.69892, 43.965936],
          [-87.699029, 43.965734],
          [-87.700125, 43.963712],
          [-87.700321, 43.963351],
          [-87.702707, 43.958946],
          [-87.702719, 43.958926],
          [-87.703951, 43.956651],
          [-87.704679, 43.955872],
          [-87.706656, 43.953753],
          [-87.709463, 43.950747],
          [-87.716037, 43.943705],
          [-87.71817, 43.939498],
          [-87.719041, 43.937781],
          [-87.719041, 43.93778],
          [-87.719194, 43.9371],
          [-87.720121, 43.932958],
          [-87.720382, 43.931794],
          [-87.720734, 43.930224],
          [-87.721268, 43.92784],
          [-87.7221, 43.924128],
          [-87.723716, 43.916915],
          [-87.724244, 43.914557],
          [-87.726766, 43.903297],
          [-87.726803, 43.903133],
          [-87.726841, 43.90304],
          [-87.728542, 43.89892],
          [-87.728885, 43.89809],
          [-87.73063, 43.893862],
          [-87.730645, 43.893825],
          [-87.731322, 43.892184],
          [-88.041794, 43.891692],
          [-88.04324, 44.241018],
          [-87.888087, 44.240461],
          [-87.887526, 44.327589],
          [-87.766194, 44.327185],
          [-87.54295, 44.327513]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55079",
      state: "55",
      county: "079",
      name: "Milwaukee County",
      censusArea: 241.402,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.89209, 43.192044],
          [-87.88857, 43.187795],
          [-87.887586, 43.186608],
          [-87.886266, 43.183359],
          [-87.884878, 43.179944],
          [-87.884872, 43.179929],
          [-87.884769, 43.179674],
          [-87.881085, 43.170609],
          [-87.88638, 43.160255],
          [-87.888849, 43.155429],
          [-87.889327, 43.154493],
          [-87.889375, 43.1544],
          [-87.889634, 43.153894],
          [-87.891059, 43.151108],
          [-87.892285, 43.14871],
          [-87.896008, 43.143405],
          [-87.898105, 43.140417],
          [-87.89902, 43.139112],
          [-87.900285, 43.13731],
          [-87.901345, 43.133357],
          [-87.901385, 43.13321],
          [-87.901359, 43.133002],
          [-87.900496, 43.126],
          [-87.900485, 43.12591],
          [-87.900463, 43.125874],
          [-87.893185, 43.114011],
          [-87.879169, 43.101717],
          [-87.877915, 43.100617],
          [-87.876084, 43.099011],
          [-87.872493, 43.08981],
          [-87.872362, 43.089474],
          [-87.872352, 43.089449],
          [-87.872255, 43.089199],
          [-87.871928, 43.088363],
          [-87.870459, 43.084598],
          [-87.866487, 43.074419],
          [-87.866484, 43.074412],
          [-87.870184, 43.064412],
          [-87.870217, 43.064376],
          [-87.87546, 43.058516],
          [-87.876343, 43.057529],
          [-87.88193, 43.051285],
          [-87.882069, 43.05113],
          [-87.882084, 43.051113],
          [-87.889883, 43.045834],
          [-87.894813, 43.042497],
          [-87.895028, 43.042351],
          [-87.895084, 43.042313],
          [-87.895127, 43.042152],
          [-87.896227, 43.038028],
          [-87.896598, 43.036637],
          [-87.898184, 43.030689],
          [-87.898684, 43.028813],
          [-87.896836, 43.02053],
          [-87.896647, 43.01968],
          [-87.896398, 43.018565],
          [-87.896157, 43.017486],
          [-87.895784, 43.015814],
          [-87.893511, 43.011521],
          [-87.889342, 43.003647],
          [-87.889095, 43.003181],
          [-87.888804, 43.002631],
          [-87.887789, 43.000715],
          [-87.887703, 43.000552],
          [-87.887683, 43.000514],
          [-87.878683, 42.992415],
          [-87.857182, 42.978015],
          [-87.845181, 42.962015],
          [-87.844739, 42.958848],
          [-87.843835, 42.952375],
          [-87.843575, 42.950519],
          [-87.842786, 42.944865],
          [-87.84276, 42.944679],
          [-87.842681, 42.944116],
          [-87.844578, 42.923688],
          [-87.846708, 42.900756],
          [-87.847745, 42.889595],
          [-87.84778, 42.889216],
          [-87.845811, 42.884256],
          [-87.834879, 42.856717],
          [-87.831176, 42.849886],
          [-87.827021, 42.842222],
          [-88.069924, 42.843323],
          [-88.069249, 42.959104],
          [-88.069098, 42.966397],
          [-88.068978, 42.967369],
          [-88.068817, 42.972832],
          [-88.068887, 42.973666],
          [-88.068588, 42.979814],
          [-88.06862, 42.98096],
          [-88.063353, 43.192117],
          [-87.994288, 43.19255],
          [-87.917643, 43.192105],
          [-87.914486, 43.192208],
          [-87.904786, 43.192108],
          [-87.899385, 43.192206],
          [-87.892886, 43.192008],
          [-87.89209, 43.192044]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55089",
      state: "55",
      county: "089",
      name: "Ozaukee County",
      censusArea: 233.077,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.911512, 43.248064],
          [-87.910172, 43.236634],
          [-87.910087, 43.235907],
          [-87.897331, 43.200046],
          [-87.897326, 43.200031],
          [-87.897316, 43.200003],
          [-87.897125, 43.199468],
          [-87.896286, 43.197108],
          [-87.895554, 43.196225],
          [-87.89209, 43.192044],
          [-87.892886, 43.192008],
          [-87.899385, 43.192206],
          [-87.904786, 43.192108],
          [-87.914486, 43.192208],
          [-87.917643, 43.192105],
          [-87.994288, 43.19255],
          [-88.063353, 43.192117],
          [-88.040528, 43.542361],
          [-87.794321, 43.543034],
          [-87.797608, 43.52731],
          [-87.797336, 43.510623],
          [-87.793239, 43.492783],
          [-87.807799, 43.461136],
          [-87.827319, 43.434849],
          [-87.855608, 43.405441],
          [-87.865048, 43.39357],
          [-87.867504, 43.389158],
          [-87.869099, 43.386295],
          [-87.872504, 43.380178],
          [-87.877448, 43.369235],
          [-87.882392, 43.352099],
          [-87.888122, 43.314731],
          [-87.888312, 43.313489],
          [-87.888314, 43.313476],
          [-87.888328, 43.313388],
          [-87.888335, 43.313342],
          [-87.888342, 43.313294],
          [-87.889207, 43.307652],
          [-87.897813, 43.291628],
          [-87.900255, 43.287081],
          [-87.901847, 43.284117],
          [-87.902967, 43.280319],
          [-87.903094, 43.279887],
          [-87.903141, 43.279728],
          [-87.903451, 43.278676],
          [-87.904251, 43.275962],
          [-87.906465, 43.268454],
          [-87.906483, 43.268393],
          [-87.906502, 43.26833],
          [-87.906565, 43.268115],
          [-87.906583, 43.268055],
          [-87.906599, 43.268002],
          [-87.906605, 43.26798],
          [-87.906611, 43.26796],
          [-87.907289, 43.265661],
          [-87.907291, 43.265653],
          [-87.907306, 43.265604],
          [-87.909053, 43.259678],
          [-87.910345, 43.255296],
          [-87.911787, 43.250406],
          [-87.911512, 43.248064]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53055",
      state: "53",
      county: "055",
      name: "San Juan County",
      censusArea: 173.915,
      stateName: "Washington"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-122.800217, 48.60169],
            [-122.804869, 48.595932],
            [-122.801096, 48.585425],
            [-122.786586, 48.576666],
            [-122.771206, 48.562426],
            [-122.770349, 48.558106],
            [-122.772384, 48.552143],
            [-122.782618, 48.545191],
            [-122.788503, 48.530393],
            [-122.787347, 48.523012],
            [-122.777467, 48.517799],
            [-122.779124, 48.508911],
            [-122.800414, 48.494467],
            [-122.816332, 48.487841],
            [-122.817912, 48.483888],
            [-122.81973, 48.458843],
            [-122.8131, 48.452856],
            [-122.807708, 48.444058],
            [-122.802509, 48.433098],
            [-122.803521, 48.428748],
            [-122.812208, 48.422326],
            [-122.825803, 48.424131],
            [-122.874135, 48.418196],
            [-122.883759, 48.418793],
            [-122.893646, 48.422655],
            [-122.889016, 48.435947],
            [-122.903214, 48.436979],
            [-122.913888, 48.443231],
            [-122.917771, 48.439781],
            [-122.928004, 48.439966],
            [-122.91646, 48.453263],
            [-122.920099, 48.458428],
            [-122.926901, 48.460874],
            [-122.937881, 48.456221],
            [-122.962009, 48.451161],
            [-123.039156, 48.460003],
            [-123.058154, 48.471522],
            [-123.067675, 48.479497],
            [-123.119451, 48.492576],
            [-123.141478, 48.505291],
            [-123.151065, 48.513955],
            [-123.163234, 48.529544],
            [-123.164057, 48.535622],
            [-123.161853, 48.539255],
            [-123.16147, 48.547618],
            [-123.172412, 48.556486],
            [-123.176266, 48.562131],
            [-123.175852, 48.568483],
            [-123.171958, 48.572255],
            [-123.173061, 48.579086],
            [-123.184941, 48.58697],
            [-123.197754, 48.586216],
            [-123.20268, 48.590214],
            [-123.203026, 48.596178],
            [-123.195725, 48.607055],
            [-123.178425, 48.622115],
            [-123.151643, 48.623686],
            [-123.139705, 48.622786],
            [-123.135645, 48.620171],
            [-123.107362, 48.622451],
            [-123.098462, 48.612834],
            [-123.098254, 48.610092],
            [-123.102074, 48.604035],
            [-123.101552, 48.59782],
            [-123.074611, 48.591816],
            [-123.06004, 48.582105],
            [-123.048403, 48.569216],
            [-123.033669, 48.563409],
            [-123.015046, 48.560821],
            [-122.987296, 48.561895],
            [-122.98611, 48.569984],
            [-122.989649, 48.574668],
            [-122.995026, 48.578162],
            [-123.0048, 48.580788],
            [-123.016647, 48.580244],
            [-123.034101, 48.591767],
            [-123.024902, 48.594484],
            [-123.023433, 48.599477],
            [-123.041189, 48.611947],
            [-123.04653, 48.61149],
            [-123.048652, 48.621002],
            [-123.023495, 48.634001],
            [-123.015592, 48.642567],
            [-123.014829, 48.647503],
            [-123.009924, 48.655064],
            [-122.988884, 48.667246],
            [-122.984853, 48.672686],
            [-122.949116, 48.693398],
            [-122.941316, 48.702904],
            [-122.942367, 48.706723],
            [-122.918252, 48.713505],
            [-122.894599, 48.71503],
            [-122.875938, 48.71212],
            [-122.833124, 48.698173],
            [-122.802545, 48.682682],
            [-122.800267, 48.67962],
            [-122.743049, 48.661991],
            [-122.742082, 48.660689],
            [-122.755031, 48.649512],
            [-122.783875, 48.635419],
            [-122.792147, 48.633502],
            [-122.809622, 48.619035],
            [-122.808864, 48.61531],
            [-122.79901, 48.604683],
            [-122.79877, 48.602352],
            [-122.800217, 48.60169]
          ]
        ],
        [
          [
            [-123.197953, 48.68466],
            [-123.186076, 48.684917],
            [-123.172066, 48.679866],
            [-123.14799, 48.668001],
            [-123.130962, 48.656789],
            [-123.122016, 48.647065],
            [-123.106165, 48.633473],
            [-123.119677, 48.632972],
            [-123.134956, 48.63724],
            [-123.215917, 48.669352],
            [-123.237148, 48.683466],
            [-123.236567, 48.68895],
            [-123.212892, 48.689713],
            [-123.197953, 48.68466]
          ]
        ],
        [
          [
            [-123.025486, 48.717966],
            [-123.019699, 48.721312],
            [-123.009787, 48.722291],
            [-123.007511, 48.718863],
            [-123.005086, 48.694342],
            [-123.014449, 48.684978],
            [-123.021215, 48.681416],
            [-123.042337, 48.675663],
            [-123.041645, 48.678633],
            [-123.035672, 48.68535],
            [-123.03636, 48.69008],
            [-123.047058, 48.695772],
            [-123.070427, 48.699971],
            [-123.040179, 48.717296],
            [-123.025486, 48.717966]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53061",
      state: "53",
      county: "061",
      name: "Snohomish County",
      censusArea: 2087.273,
      stateName: "Washington"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-122.321721, 48.019977],
            [-122.303455, 48.005603],
            [-122.306629, 48.004397],
            [-122.326115, 48.010295],
            [-122.334524, 48.018916],
            [-122.328343, 48.021335],
            [-122.321721, 48.019977]
          ]
        ],
        [
          [
            [-121.584537, 47.776726],
            [-121.928146, 47.776971],
            [-122.000396, 47.775284],
            [-122.000425, 47.775284],
            [-122.396422, 47.777927],
            [-122.397043, 47.779719],
            [-122.394944, 47.803318],
            [-122.392044, 47.807718],
            [-122.353244, 47.840618],
            [-122.346544, 47.842418],
            [-122.339944, 47.846718],
            [-122.33595, 47.852306],
            [-122.329545, 47.869418],
            [-122.330145, 47.875318],
            [-122.333543, 47.880246],
            [-122.328546, 47.897917],
            [-122.321847, 47.911817],
            [-122.311927, 47.923703],
            [-122.310747, 47.925117],
            [-122.309747, 47.929117],
            [-122.311148, 47.936717],
            [-122.307048, 47.949117],
            [-122.278047, 47.956517],
            [-122.249007, 47.959507],
            [-122.230046, 47.970917],
            [-122.226346, 47.976417],
            [-122.232391, 47.987713],
            [-122.23022, 48.007154],
            [-122.228767, 48.012468],
            [-122.224979, 48.016626],
            [-122.231761, 48.029876],
            [-122.281087, 48.049793],
            [-122.305838, 48.073415],
            [-122.321709, 48.085507],
            [-122.326119, 48.092877],
            [-122.343241, 48.097631],
            [-122.363842, 48.12393],
            [-122.365078, 48.125822],
            [-122.363797, 48.142759],
            [-122.364744, 48.151304],
            [-122.370253, 48.164809],
            [-122.363479, 48.174438],
            [-122.362044, 48.187568],
            [-122.372492, 48.193022],
            [-122.382102, 48.207106],
            [-122.385703, 48.217811],
            [-122.395499, 48.228551],
            [-122.392851, 48.238555],
            [-122.398712, 48.241684],
            [-122.405757, 48.252193],
            [-122.395328, 48.257187],
            [-122.392058, 48.269628],
            [-122.371693, 48.287839],
            [-122.376818, 48.296099],
            [-122.37821, 48.29759],
            [-121.001409, 48.295548],
            [-120.906411, 48.163348],
            [-121.015412, 48.075141],
            [-121.153501, 48.040871],
            [-121.118059, 47.99766],
            [-121.180213, 47.897132],
            [-121.07071, 47.826034],
            [-121.11901, 47.779933],
            [-121.580986, 47.777534],
            [-121.584537, 47.776726]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53071",
      state: "53",
      county: "071",
      name: "Walla Walla County",
      censusArea: 1270.129,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-118.228928, 46.593606],
          [-118.241611, 46.295072],
          [-118.116139, 46.295455],
          [-118.116493, 46.207912],
          [-117.991292, 46.207843],
          [-117.996911, 46.000787],
          [-118.126197, 46.000282],
          [-118.131019, 46.00028],
          [-118.228941, 46.000421],
          [-118.236584, 46.000418],
          [-118.25253, 46.000459],
          [-118.256368, 46.000439],
          [-118.314982, 46.000453],
          [-118.36779, 46.000622],
          [-118.37836, 46.000574],
          [-118.470756, 46.000632],
          [-118.497027, 46.00062],
          [-118.537119, 46.00084],
          [-118.569392, 46.000773],
          [-118.57571, 46.000718],
          [-118.579906, 46.000818],
          [-118.637725, 46.00097],
          [-118.639332, 46.000994],
          [-118.658717, 46.000955],
          [-118.67787, 46.000935],
          [-118.987129, 45.999855],
          [-118.941075, 46.029339],
          [-118.980933, 46.146296],
          [-119.041418, 46.192856],
          [-118.972827, 46.238705],
          [-118.777228, 46.290177],
          [-118.746199, 46.359512],
          [-118.633826, 46.414022],
          [-118.615592, 46.504543],
          [-118.452637, 46.596973],
          [-118.228928, 46.593606]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53073",
      state: "53",
      county: "073",
      name: "Whatcom County",
      censusArea: 2106.857,
      stateName: "Washington"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-120.752021, 48.657538],
            [-120.910534, 48.640937],
            [-121.251233, 48.640997],
            [-122.488754, 48.645358],
            [-122.49399, 48.651596],
            [-122.500308, 48.656163],
            [-122.506718, 48.669692],
            [-122.519172, 48.713095],
            [-122.515511, 48.720992],
            [-122.505684, 48.724524],
            [-122.495301, 48.737328],
            [-122.490401, 48.751128],
            [-122.510902, 48.757728],
            [-122.528203, 48.768428],
            [-122.535803, 48.776128],
            [-122.567498, 48.779185],
            [-122.596844, 48.771492],
            [-122.598033, 48.769489],
            [-122.606787, 48.759143],
            [-122.627808, 48.74466],
            [-122.637146, 48.735708],
            [-122.638082, 48.732486],
            [-122.626287, 48.72093],
            [-122.612562, 48.714932],
            [-122.605733, 48.701066],
            [-122.606105, 48.698556],
            [-122.615169, 48.693839],
            [-122.620338, 48.693651],
            [-122.630422, 48.696625],
            [-122.646323, 48.708001],
            [-122.673472, 48.733082],
            [-122.666953, 48.748445],
            [-122.661111, 48.753962],
            [-122.647443, 48.773998],
            [-122.645743, 48.781538],
            [-122.646777, 48.785011],
            [-122.656528, 48.784969],
            [-122.659708, 48.786523],
            [-122.680246, 48.80275],
            [-122.693683, 48.804475],
            [-122.697219, 48.80281],
            [-122.698675, 48.800522],
            [-122.699507, 48.794906],
            [-122.699303, 48.789063],
            [-122.703106, 48.786321],
            [-122.709815, 48.786205],
            [-122.7112, 48.79146],
            [-122.709169, 48.817829],
            [-122.711805, 48.832408],
            [-122.717073, 48.84719],
            [-122.722685, 48.852855],
            [-122.785659, 48.885066],
            [-122.793175, 48.892927],
            [-122.792584, 48.894732],
            [-122.783747, 48.894639],
            [-122.751289, 48.911239],
            [-122.747514, 48.915582],
            [-122.745371, 48.921227],
            [-122.746596, 48.930731],
            [-122.755624, 48.93866],
            [-122.766096, 48.941955],
            [-122.770432, 48.942528],
            [-122.787539, 48.931702],
            [-122.818232, 48.939062],
            [-122.821631, 48.941369],
            [-122.822464, 48.944911],
            [-122.817226, 48.95597],
            [-122.796887, 48.975026],
            [-122.774276, 48.991038],
            [-122.766307, 48.991672],
            [-122.756318, 48.996881],
            [-122.756037, 48.999512],
            [-122.75802, 49.002357],
            [-122.407829, 49.002193],
            [-122.405989, 49.002239],
            [-122.098357, 49.002146],
            [-121.751252, 48.997399],
            [-121.12624, 49.001412],
            [-120.851202, 49.001191],
            [-120.767528, 48.954385],
            [-120.73371, 48.7829],
            [-120.654676, 48.724198],
            [-120.752021, 48.657538]
          ]
        ],
        [
          [
            [-123.035393, 49.002154],
            [-123.021459, 48.977299],
            [-123.028091, 48.973943],
            [-123.040967, 48.977305],
            [-123.060717, 48.975388],
            [-123.083834, 48.976139],
            [-123.084498, 48.986535],
            [-123.090546, 49.001976],
            [-123.035393, 49.002154]
          ]
        ],
        [
          [
            [-122.695907, 48.737273],
            [-122.668947, 48.706644],
            [-122.663259, 48.697077],
            [-122.644901, 48.691389],
            [-122.618225, 48.670721],
            [-122.609576, 48.645018],
            [-122.616956, 48.645563],
            [-122.635299, 48.651846],
            [-122.673538, 48.680809],
            [-122.691795, 48.711498],
            [-122.702223, 48.717004],
            [-122.718833, 48.716818],
            [-122.721981, 48.723375],
            [-122.722262, 48.731624],
            [-122.715709, 48.748672],
            [-122.70306, 48.743602],
            [-122.695907, 48.737273]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22039",
      state: "22",
      county: "039",
      name: "Evangeline County",
      lsad: "Parish",
      censusArea: 662.378,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.630347, 30.487873],
          [-92.597402, 30.583928],
          [-92.597741, 30.896101],
          [-92.508854, 30.896003],
          [-92.48033, 30.952271],
          [-92.382742, 31.001431],
          [-92.280738, 30.965071],
          [-92.212711, 30.848607],
          [-92.17255, 30.713365],
          [-92.211243, 30.568125],
          [-92.262719, 30.538738],
          [-92.42078, 30.53882],
          [-92.493259, 30.480499],
          [-92.629063, 30.480614],
          [-92.631987, 30.481147],
          [-92.633495, 30.482287],
          [-92.633839, 30.483319],
          [-92.631933, 30.483707],
          [-92.63069, 30.48334],
          [-92.630028, 30.483569],
          [-92.630212, 30.485105],
          [-92.629471, 30.485586],
          [-92.629285, 30.486869],
          [-92.630347, 30.487873]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US27139",
      state: "27",
      county: "139",
      name: "Scott County",
      censusArea: 356.476,
      stateName: "Minnesota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.329615, 44.791086],
          [-93.318731, 44.630578],
          [-93.281521, 44.543957],
          [-93.524327, 44.543618],
          [-93.910405, 44.543163],
          [-93.857678, 44.621095],
          [-93.768039, 44.640303],
          [-93.618141, 44.707096],
          [-93.621967, 44.760281],
          [-93.520431, 44.804263],
          [-93.329615, 44.791086]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31113",
      state: "31",
      county: "113",
      name: "Logan County",
      censusArea: 570.66,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.251098, 41.393298],
          [-100.713243, 41.393511],
          [-100.713132, 41.394575],
          [-100.711711, 41.739761],
          [-100.265474, 41.739856],
          [-100.251224, 41.739817],
          [-100.251098, 41.393298]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37043",
      state: "37",
      county: "043",
      name: "Clay County",
      censusArea: 214.751,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.483064, 34.993737],
          [-83.549381, 34.992492],
          [-83.620185, 34.992091],
          [-83.673621, 34.987111],
          [-83.936413, 34.987485],
          [-83.936646, 34.987485],
          [-84.005457, 34.98744],
          [-83.857142, 35.137071],
          [-83.738106, 35.155911],
          [-83.659765, 35.160243],
          [-83.483064, 34.993737]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US39057",
      state: "39",
      county: "057",
      name: "Greene County",
      censusArea: 413.729,
      stateName: "Ohio"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.096937, 39.793033],
          [-84.092938, 39.838345],
          [-84.055238, 39.835958],
          [-84.053736, 39.850458],
          [-83.827425, 39.822651],
          [-83.829495, 39.797851],
          [-83.647169, 39.773029],
          [-83.648641, 39.768478],
          [-83.648882, 39.761115],
          [-83.65333, 39.716876],
          [-83.670196, 39.550254],
          [-83.977005, 39.569169],
          [-84.114195, 39.577983],
          [-84.096937, 39.793033]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US36009",
      state: "36",
      county: "009",
      name: "Cattaraugus County",
      censusArea: 1308.35,
      stateName: "New York"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.918854, 41.997961],
          [-79.052473, 41.999179],
          [-79.061265, 41.999259],
          [-79.060777, 42.537853],
          [-78.991702, 42.529249],
          [-78.920446, 42.442556],
          [-78.695937, 42.47194],
          [-78.46394, 42.536332],
          [-78.464556, 42.519166],
          [-78.308839, 42.521217],
          [-78.308128, 41.999415],
          [-78.59665, 41.999877],
          [-78.874759, 41.997559],
          [-78.918854, 41.997961]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47169",
      state: "47",
      county: "169",
      name: "Trousdale County",
      censusArea: 114.193,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.174666, 36.328],
          [-86.283256, 36.3475],
          [-86.228434, 36.487472],
          [-86.168261, 36.444293],
          [-85.978893, 36.426073],
          [-86.136803, 36.295859],
          [-86.144659, 36.304502],
          [-86.164045, 36.320899],
          [-86.174666, 36.328]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US46073",
      state: "46",
      county: "073",
      name: "Jerauld County",
      censusArea: 526.233,
      stateName: "South Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.331508, 43.937708],
          [-98.807771, 43.935223],
          [-98.926997, 43.935143],
          [-98.925953, 44.196575],
          [-98.700453, 44.196727],
          [-98.332042, 44.19662],
          [-98.331508, 43.937708]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48365",
      state: "48",
      county: "365",
      name: "Panola County",
      censusArea: 801.749,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.511431, 31.973984],
          [-94.599978, 31.973193],
          [-94.5805, 32.201363],
          [-94.491818, 32.394137],
          [-94.347563, 32.330682],
          [-94.042901, 32.392283],
          [-94.042763, 32.373332],
          [-94.042739, 32.363559],
          [-94.042733, 32.269696],
          [-94.042732, 32.26962],
          [-94.042662, 32.218146],
          [-94.042621, 32.196005],
          [-94.042566, 32.166894],
          [-94.042539, 32.166826],
          [-94.042591, 32.158097],
          [-94.042681, 32.137956],
          [-94.042337, 32.119914],
          [-94.0427, 32.056012],
          [-94.04272, 31.999265],
          [-94.01563, 31.979856],
          [-94.511431, 31.973984]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48207",
      state: "48",
      county: "207",
      name: "Haskell County",
      censusArea: 903.131,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.472444, 33.399023],
          [-99.471258, 32.957025],
          [-99.612001, 32.956962],
          [-99.988827, 32.960121],
          [-99.99098, 33.397404],
          [-99.472444, 33.399023]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51690",
      state: "51",
      county: "690",
      name: "Martinsville County",
      lsad: "city",
      censusArea: 10.956,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.877668, 36.666707],
          [-79.880164, 36.667144],
          [-79.881661, 36.670537],
          [-79.884861, 36.672037],
          [-79.894181, 36.672036],
          [-79.895561, 36.675237],
          [-79.904077, 36.684127],
          [-79.87476, 36.715237],
          [-79.837359, 36.704137],
          [-79.843737, 36.672443],
          [-79.826619, 36.659379],
          [-79.827459, 36.647637],
          [-79.859761, 36.650368],
          [-79.85926, 36.661737],
          [-79.877668, 36.666707]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55131",
      state: "55",
      county: "131",
      name: "Washington County",
      censusArea: 430.703,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.040528, 43.542361],
          [-88.063353, 43.192117],
          [-88.417987, 43.194697],
          [-88.400426, 43.543532],
          [-88.160873, 43.542943],
          [-88.040528, 43.542361]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54067",
      state: "54",
      county: "067",
      name: "Nicholas County",
      censusArea: 646.824,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.231636, 38.263514],
          [-81.231434, 38.263716],
          [-80.911361, 38.414785],
          [-80.817424, 38.478553],
          [-80.881232, 38.507045],
          [-80.785177, 38.557649],
          [-80.650786, 38.52659],
          [-80.678291, 38.503492],
          [-80.609378, 38.358606],
          [-80.436514, 38.267334],
          [-80.711961, 38.079129],
          [-80.882187, 38.103457],
          [-80.983473, 38.222537],
          [-81.070465, 38.210103],
          [-81.231636, 38.263514]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51087",
      state: "51",
      county: "087",
      name: "Henrico County",
      censusArea: 233.697,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.333838, 37.576865],
          [-77.228504, 37.536398],
          [-77.226512, 37.53387],
          [-77.221244, 37.533229],
          [-77.220254, 37.527221],
          [-77.214847, 37.525372],
          [-77.206239, 37.515977],
          [-77.207007, 37.513446],
          [-77.202667, 37.512866],
          [-77.198184, 37.508965],
          [-77.192962, 37.506598],
          [-77.191937, 37.501237],
          [-77.188164, 37.500224],
          [-77.184836, 37.49572],
          [-77.177324, 37.4906],
          [-77.249665, 37.382],
          [-77.361937, 37.376057],
          [-77.420849, 37.447079],
          [-77.413196, 37.579928],
          [-77.483092, 37.573832],
          [-77.59611, 37.55572],
          [-77.60645, 37.555443],
          [-77.649239, 37.559613],
          [-77.653987, 37.562631],
          [-77.630145, 37.706985],
          [-77.445827, 37.683975],
          [-77.333838, 37.576865]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01043",
      state: "01",
      county: "043",
      name: "Cullman County",
      censusArea: 734.841,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.109911, 34.299299],
          [-87.110111, 34.313799],
          [-87.092311, 34.312297],
          [-86.581936, 34.304694],
          [-86.45302, 34.259317],
          [-86.514881, 34.25437],
          [-86.599632, 34.119914],
          [-86.692061, 34.092654],
          [-86.685365, 34.05914],
          [-86.793914, 33.952059],
          [-86.924387, 33.909222],
          [-86.963358, 33.858221],
          [-87.091836, 33.890093],
          [-87.151036, 33.993225],
          [-87.111992, 33.992385],
          [-87.109911, 34.299299]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US01109",
      state: "01",
      county: "109",
      name: "Pike County",
      censusArea: 672.094,
      stateName: "Alabama"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.748251, 31.618048],
          [-85.749941, 31.617914],
          [-85.789142, 31.617964],
          [-86.145895, 31.617741],
          [-86.148339, 31.790951],
          [-86.199378, 31.79045],
          [-86.191379, 31.966453],
          [-85.995563, 31.967554],
          [-85.996853, 32.051049],
          [-85.893652, 32.047351],
          [-85.88435, 31.967253],
          [-85.790048, 31.967254],
          [-85.791047, 31.880357],
          [-85.657668, 31.880275],
          [-85.66623, 31.772877],
          [-85.748251, 31.618048]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US09009",
      state: "09",
      county: "009",
      name: "New Haven County",
      censusArea: 604.506,
      stateName: "Connecticut"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-73.15573, 41.515279],
          [-73.164676, 41.557088],
          [-73.145036, 41.557822],
          [-73.058024, 41.60674],
          [-73.036156, 41.615197],
          [-73.01865, 41.614097],
          [-73.021471, 41.627104],
          [-73.004046, 41.628577],
          [-72.98325, 41.639598],
          [-72.94635, 41.556797],
          [-72.752181, 41.578894],
          [-72.744742, 41.497698],
          [-72.74333, 41.483114],
          [-72.733886, 41.485368],
          [-72.746141, 41.423297],
          [-72.708343, 41.429443],
          [-72.682539, 41.433797],
          [-72.678739, 41.433797],
          [-72.537776, 41.255646],
          [-72.546833, 41.250718],
          [-72.547235, 41.250499],
          [-72.570655, 41.267744],
          [-72.571076, 41.268054],
          [-72.571136, 41.268098],
          [-72.583336, 41.271698],
          [-72.585181, 41.271321],
          [-72.585934, 41.271168],
          [-72.586674, 41.271017],
          [-72.587926, 41.270761],
          [-72.589818, 41.270375],
          [-72.590967, 41.270141],
          [-72.598036, 41.268698],
          [-72.607863, 41.270387],
          [-72.610236, 41.270795],
          [-72.617237, 41.271998],
          [-72.617521, 41.27194],
          [-72.617983, 41.271845],
          [-72.631363, 41.269092],
          [-72.641001, 41.267108],
          [-72.641538, 41.266998],
          [-72.642811, 41.266884],
          [-72.650697, 41.266178],
          [-72.653838, 41.265897],
          [-72.653931, 41.265931],
          [-72.654715, 41.266219],
          [-72.662203, 41.268964],
          [-72.662838, 41.269197],
          [-72.667176, 41.268192],
          [-72.671673, 41.267151],
          [-72.672339, 41.266997],
          [-72.674319, 41.26552],
          [-72.684939, 41.257597],
          [-72.685414, 41.252607],
          [-72.685539, 41.251297],
          [-72.689446, 41.247629],
          [-72.690237, 41.246887],
          [-72.690439, 41.246697],
          [-72.693441, 41.245493],
          [-72.694744, 41.24497],
          [-72.69547, 41.244948],
          [-72.701806, 41.244752],
          [-72.706236, 41.244615],
          [-72.707212, 41.244585],
          [-72.708658, 41.24454],
          [-72.708963, 41.24453],
          [-72.709193, 41.244523],
          [-72.710595, 41.24448],
          [-72.710821, 41.244812],
          [-72.713674, 41.249007],
          [-72.711208, 41.251018],
          [-72.71246, 41.254167],
          [-72.722439, 41.259138],
          [-72.732813, 41.254727],
          [-72.754444, 41.266913],
          [-72.757477, 41.266913],
          [-72.786142, 41.264796],
          [-72.818737, 41.252244],
          [-72.819372, 41.254061],
          [-72.826883, 41.256755],
          [-72.847767, 41.25669],
          [-72.85021, 41.255544],
          [-72.854055, 41.24774],
          [-72.861344, 41.245297],
          [-72.881445, 41.242597],
          [-72.895445, 41.243697],
          [-72.900803, 41.245864],
          [-72.904345, 41.247297],
          [-72.905245, 41.248297],
          [-72.903045, 41.252797],
          [-72.902808, 41.252894],
          [-72.894745, 41.256197],
          [-72.89473, 41.25626],
          [-72.893845, 41.259897],
          [-72.89637, 41.263949],
          [-72.903129, 41.274794],
          [-72.907962, 41.282549],
          [-72.9082, 41.282932],
          [-72.916827, 41.282033],
          [-72.917037, 41.281905],
          [-72.920062, 41.280056],
          [-72.920658, 41.271574],
          [-72.920714, 41.27078],
          [-72.920846, 41.268897],
          [-72.931887, 41.261139],
          [-72.933472, 41.260024],
          [-72.935646, 41.258497],
          [-72.956984, 41.25292],
          [-72.959633, 41.252228],
          [-72.961345, 41.25178],
          [-72.962047, 41.251597],
          [-72.983751, 41.235364],
          [-72.985095, 41.234358],
          [-73.067851, 41.300996],
          [-73.075352, 41.305596],
          [-73.077652, 41.307696],
          [-73.078952, 41.311596],
          [-73.08665, 41.314495],
          [-73.090938, 41.319256],
          [-73.115754, 41.332196],
          [-73.136054, 41.351196],
          [-73.147555, 41.367696],
          [-73.161253, 41.383853],
          [-73.173356, 41.383596],
          [-73.183269, 41.391711],
          [-73.187957, 41.397296],
          [-73.184857, 41.406296],
          [-73.207688, 41.420544],
          [-73.310473, 41.468815],
          [-73.327114, 41.484527],
          [-73.318889, 41.505189],
          [-73.15573, 41.515279]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05049",
      state: "05",
      county: "049",
      name: "Fulton County",
      censusArea: 618.194,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.120415, 36.498863],
          [-92.120306, 36.498864],
          [-92.098356, 36.498803],
          [-92.074934, 36.498761],
          [-92.057178, 36.49867],
          [-92.055789, 36.49867],
          [-92.028847, 36.498642],
          [-92.019375, 36.498524],
          [-91.988751, 36.498498],
          [-91.985802, 36.498431],
          [-91.865995, 36.498783],
          [-91.864385, 36.498789],
          [-91.805981, 36.498987],
          [-91.80204, 36.498963],
          [-91.7995, 36.498952],
          [-91.784713, 36.499074],
          [-91.766111, 36.499114],
          [-91.726663, 36.499209],
          [-91.687615, 36.499397],
          [-91.686026, 36.499374],
          [-91.672343, 36.499463],
          [-91.64259, 36.499335],
          [-91.631439, 36.499198],
          [-91.601317, 36.499343],
          [-91.596213, 36.499162],
          [-91.549163, 36.499161],
          [-91.539359, 36.499116],
          [-91.53687, 36.499156],
          [-91.529774, 36.499022],
          [-91.450005, 36.497538],
          [-91.454437, 36.335465],
          [-91.565866, 36.3374],
          [-91.566078, 36.249945],
          [-91.691435, 36.253301],
          [-92.15699, 36.261248],
          [-92.159215, 36.261311],
          [-92.150295, 36.498634],
          [-92.137741, 36.498706],
          [-92.120415, 36.498863]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US05127",
      state: "05",
      county: "127",
      name: "Scott County",
      censusArea: 892.32,
      stateName: "Arkansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.449253, 34.895869],
          [-94.447889, 34.933941],
          [-94.230524, 34.968527],
          [-94.222869, 35.079115],
          [-94.140571, 35.099678],
          [-94.074347, 35.026585],
          [-93.704885, 35.019715],
          [-93.710297, 34.745296],
          [-93.870197, 34.712699],
          [-93.930509, 34.665608],
          [-94.018168, 34.692469],
          [-94.317659, 34.697408],
          [-94.454576, 34.728962],
          [-94.450233, 34.855413],
          [-94.45014, 34.858694],
          [-94.450065, 34.861335],
          [-94.44963, 34.875253],
          [-94.449058, 34.890556],
          [-94.449086, 34.894152],
          [-94.449253, 34.895869]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21117",
      state: "21",
      county: "117",
      name: "Kenton County",
      censusArea: 160.25,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.510057, 39.093593],
          [-84.509743, 39.09366],
          [-84.506082, 39.095081],
          [-84.4508, 38.993609],
          [-84.4634, 38.852059],
          [-84.418107, 38.806596],
          [-84.420199, 38.805679],
          [-84.532288, 38.791937],
          [-84.597775, 38.79117],
          [-84.61574, 38.802192],
          [-84.598031, 38.87954],
          [-84.623732, 39.074427],
          [-84.620112, 39.073457],
          [-84.603444, 39.073699],
          [-84.510057, 39.093593]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US19123",
      state: "19",
      county: "123",
      name: "Mahaska County",
      censusArea: 570.863,
      stateName: "Iowa"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.871421, 41.508522],
          [-92.756508, 41.508794],
          [-92.411995, 41.509548],
          [-92.410233, 41.161942],
          [-92.640361, 41.161298],
          [-92.869771, 41.161066],
          [-92.871421, 41.508522]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US47015",
      state: "47",
      county: "015",
      name: "Cannon County",
      censusArea: 265.635,
      stateName: "Tennessee"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.885156, 35.839658],
          [-85.984891, 35.734553],
          [-85.985062, 35.660108],
          [-86.143652, 35.661747],
          [-86.207147, 35.70379],
          [-86.153214, 35.954399],
          [-86.014849, 35.96106],
          [-85.885156, 35.839658]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38011",
      state: "38",
      county: "011",
      name: "Bowman County",
      censusArea: 1161.807,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-102.995668, 45.945167],
          [-103.026058, 45.945307],
          [-103.047779, 45.945335],
          [-103.078477, 45.945289],
          [-103.097872, 45.945262],
          [-103.140939, 45.945257],
          [-103.161251, 45.945309],
          [-103.210634, 45.945222],
          [-103.218396, 45.945208],
          [-103.284092, 45.945149],
          [-103.284109, 45.945152],
          [-103.411325, 45.945264],
          [-103.41804, 45.945186],
          [-103.432393, 45.945313],
          [-103.434851, 45.945291],
          [-103.55871, 45.945131],
          [-103.577083, 45.945283],
          [-103.660779, 45.945231],
          [-103.660779, 45.945241],
          [-103.668479, 45.945242],
          [-104.045443, 45.94531],
          [-104.04667, 46.000001],
          [-104.045633, 46.280188],
          [-102.995454, 46.280713],
          [-102.995668, 45.945167]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US38083",
      state: "38",
      county: "083",
      name: "Sheridan County",
      censusArea: 972.379,
      stateName: "North Dakota"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.673445, 47.32772],
          [-100.672262, 47.674128],
          [-100.585106, 47.673912],
          [-100.585161, 47.84748],
          [-100.197996, 47.8476],
          [-100.166099, 47.847581],
          [-100.164591, 47.847465],
          [-100.069206, 47.847301],
          [-100.069514, 47.67365],
          [-100.032849, 47.673621],
          [-100.033292, 47.327192],
          [-100.036063, 47.327304],
          [-100.093491, 47.327087],
          [-100.094739, 47.327326],
          [-100.113261, 47.327262],
          [-100.669601, 47.327435],
          [-100.673445, 47.32772]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US48375",
      state: "48",
      county: "375",
      name: "Potter County",
      censusArea: 908.37,
      stateName: "Texas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.622941, 35.183117],
          [-102.167471, 35.183233],
          [-102.162752, 35.620035],
          [-101.6228, 35.620196],
          [-101.622993, 35.184339],
          [-101.622941, 35.183117]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US31065",
      state: "31",
      county: "065",
      name: "Furnas County",
      censusArea: 719.132,
      stateName: "Nebraska"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-99.642209, 40.3512],
          [-99.630619, 40.351206],
          [-99.630459, 40.35112],
          [-99.628255, 40.001866],
          [-99.628346, 40.001866],
          [-99.719639, 40.001808],
          [-99.731959, 40.001827],
          [-99.746628, 40.00182],
          [-99.756835, 40.001342],
          [-99.764214, 40.001551],
          [-99.772121, 40.001804],
          [-99.77564, 40.001647],
          [-99.813401, 40.0014],
          [-99.906658, 40.001512],
          [-99.930433, 40.001516],
          [-99.944417, 40.001584],
          [-99.948167, 40.001813],
          [-99.986611, 40.00155],
          [-99.990926, 40.001503],
          [-100.177795, 40.001593],
          [-100.177823, 40.001593],
          [-100.188181, 40.001541],
          [-100.190323, 40.001586],
          [-100.19359, 40.001573],
          [-100.193597, 40.001573],
          [-100.197807, 40.350027],
          [-100.094798, 40.351245],
          [-99.642209, 40.3512]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28135",
      state: "28",
      county: "135",
      name: "Tallahatchie County",
      censusArea: 645.288,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.926099, 33.897957],
          [-89.926294, 33.823854],
          [-90.136491, 33.809319],
          [-90.136787, 33.721648],
          [-90.269105, 33.697208],
          [-90.294502, 33.750981],
          [-90.185599, 33.765514],
          [-90.182584, 33.809322],
          [-90.451643, 33.81139],
          [-90.450289, 33.970944],
          [-90.451012, 33.985689],
          [-90.45053, 34.000112],
          [-90.450981, 34.072932],
          [-90.135695, 34.0729],
          [-90.135456, 34.160074],
          [-89.931485, 34.164303],
          [-89.926099, 33.897957]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US41019",
      state: "41",
      county: "019",
      name: "Douglas County",
      censusArea: 5036.075,
      stateName: "Oregon"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-124.218876, 43.610319],
          [-124.203028, 43.667825],
          [-124.204888, 43.673976],
          [-124.198275, 43.689481],
          [-124.193455, 43.706085],
          [-124.168392, 43.808903],
          [-124.158684, 43.863504],
          [-123.92551, 43.865633],
          [-123.925513, 43.899167],
          [-123.827622, 43.945045],
          [-123.703461, 43.945131],
          [-123.6193, 43.92013],
          [-123.579681, 43.868138],
          [-123.470506, 43.810196],
          [-123.348016, 43.809165],
          [-123.347659, 43.780169],
          [-123.137677, 43.779666],
          [-123.137064, 43.60597],
          [-123.107475, 43.540004],
          [-122.741771, 43.544553],
          [-122.740765, 43.437142],
          [-122.132044, 43.440445],
          [-122.010239, 43.343249],
          [-121.973841, 43.261728],
          [-122.037839, 43.21725],
          [-122.091539, 43.07605],
          [-122.282245, 43.067547],
          [-122.281856, 42.996556],
          [-122.400467, 42.996426],
          [-122.460053, 42.939852],
          [-122.677211, 42.881341],
          [-122.794793, 42.779422],
          [-123.151421, 42.756069],
          [-123.2299, 42.702486],
          [-123.365975, 42.73204],
          [-123.444177, 42.703138],
          [-123.715088, 42.739735],
          [-123.71619, 42.784234],
          [-123.727589, 42.780135],
          [-123.777322, 42.798988],
          [-123.812093, 42.789433],
          [-123.820798, 42.995935],
          [-123.762095, 42.996036],
          [-123.76063, 43.083127],
          [-123.701989, 43.087288],
          [-123.703798, 43.257835],
          [-123.764, 43.257935],
          [-123.764005, 43.432237],
          [-123.817707, 43.431237],
          [-123.816834, 43.51603],
          [-123.875529, 43.515949],
          [-123.875424, 43.608254],
          [-124.218876, 43.610319]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US29013",
      state: "29",
      county: "013",
      name: "Bates County",
      censusArea: 836.689,
      stateName: "Missouri"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.065713, 38.447087],
          [-94.075214, 38.214502],
          [-94.049895, 38.213985],
          [-94.058285, 38.036627],
          [-94.614055, 38.060056],
          [-94.614055, 38.060088],
          [-94.614089, 38.065901],
          [-94.614061, 38.067343],
          [-94.613856, 38.149769],
          [-94.613748, 38.160633],
          [-94.613422, 38.167908],
          [-94.613073, 38.190552],
          [-94.612848, 38.200714],
          [-94.612822, 38.203918],
          [-94.612658, 38.217649],
          [-94.612659, 38.219251],
          [-94.612635, 38.226987],
          [-94.612614, 38.237766],
          [-94.612692, 38.270394],
          [-94.612849, 38.289914],
          [-94.612844, 38.291423],
          [-94.612673, 38.302527],
          [-94.612673, 38.314832],
          [-94.612788, 38.320142],
          [-94.612825, 38.324387],
          [-94.613, 38.335801],
          [-94.613312, 38.364407],
          [-94.613329, 38.369618],
          [-94.613275, 38.388718],
          [-94.613265, 38.392426],
          [-94.613365, 38.403422],
          [-94.612866, 38.477571],
          [-94.612865, 38.477602],
          [-94.31217, 38.471496],
          [-94.065713, 38.447087]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55001",
      state: "55",
      county: "001",
      name: "Adams County",
      censusArea: 645.646,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.902565, 44.24947],
          [-89.726235, 44.247943],
          [-89.724746, 44.247678],
          [-89.59798, 44.245725],
          [-89.59795, 43.9821],
          [-89.599545, 43.644514],
          [-89.599985, 43.642596],
          [-89.785809, 43.641049],
          [-89.865478, 43.783177],
          [-89.958101, 43.859902],
          [-89.968949, 43.994074],
          [-90.028807, 44.090232],
          [-89.903344, 44.178696],
          [-89.925822, 44.192715],
          [-89.902565, 44.24947]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53057",
      state: "53",
      county: "057",
      name: "Skagit County",
      censusArea: 1731.203,
      stateName: "Washington"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-122.649405, 48.588457],
            [-122.642597, 48.588339],
            [-122.629321, 48.5722],
            [-122.610841, 48.561146],
            [-122.592901, 48.553635],
            [-122.583985, 48.551534],
            [-122.578856, 48.54813],
            [-122.572967, 48.529028],
            [-122.583565, 48.53234],
            [-122.590194, 48.536259],
            [-122.599948, 48.536904],
            [-122.619858, 48.529246],
            [-122.635738, 48.526021],
            [-122.640414, 48.52586],
            [-122.649256, 48.528769],
            [-122.652041, 48.531329],
            [-122.654342, 48.537956],
            [-122.653612, 48.548975],
            [-122.650786, 48.554019],
            [-122.652385, 48.583432],
            [-122.649405, 48.588457]
          ]
        ],
        [
          [
            [-122.714512, 48.60878],
            [-122.694672, 48.596602],
            [-122.691745, 48.590612],
            [-122.670638, 48.568812],
            [-122.68944, 48.543903],
            [-122.717278, 48.539739],
            [-122.722407, 48.540606],
            [-122.724031, 48.549906],
            [-122.73048, 48.565602],
            [-122.736199, 48.569005],
            [-122.73944, 48.573893],
            [-122.739898, 48.583949],
            [-122.72493, 48.603263],
            [-122.714512, 48.60878]
          ]
        ],
        [
          [
            [-122.699266, 48.621115],
            [-122.69806, 48.62308],
            [-122.674173, 48.629944],
            [-122.657016, 48.609891],
            [-122.666149, 48.608088],
            [-122.676796, 48.610055],
            [-122.686136, 48.613267],
            [-122.699266, 48.621115]
          ]
        ],
        [
          [
            [-122.37821, 48.29759],
            [-122.38431, 48.304123],
            [-122.408718, 48.326413],
            [-122.424102, 48.334346],
            [-122.442678, 48.337934],
            [-122.475529, 48.359912],
            [-122.482423, 48.361737],
            [-122.497686, 48.361837],
            [-122.507437, 48.364666],
            [-122.533452, 48.383409],
            [-122.539449, 48.39719],
            [-122.547492, 48.399889],
            [-122.554536, 48.40604],
            [-122.558403, 48.426758],
            [-122.551221, 48.439465],
            [-122.557298, 48.444438],
            [-122.568348, 48.44499],
            [-122.575254, 48.443333],
            [-122.581607, 48.429244],
            [-122.61448, 48.41488],
            [-122.649839, 48.408526],
            [-122.665338, 48.416453],
            [-122.674158, 48.424726],
            [-122.678928, 48.439466],
            [-122.677072, 48.444059],
            [-122.674188, 48.443327],
            [-122.674085, 48.441979],
            [-122.667249, 48.442503],
            [-122.654844, 48.454087],
            [-122.657753, 48.47294],
            [-122.664623, 48.478128],
            [-122.689121, 48.476849],
            [-122.695725, 48.464785],
            [-122.695587, 48.460558],
            [-122.700603, 48.457632],
            [-122.710362, 48.461584],
            [-122.712322, 48.464143],
            [-122.712981, 48.47879],
            [-122.701644, 48.497622],
            [-122.684521, 48.509123],
            [-122.679122, 48.507797],
            [-122.676922, 48.504484],
            [-122.671386, 48.50398],
            [-122.615183, 48.521427],
            [-122.606961, 48.522152],
            [-122.599951, 48.520946],
            [-122.598469, 48.512169],
            [-122.568071, 48.50821],
            [-122.556834, 48.498812],
            [-122.537355, 48.466749],
            [-122.532845, 48.466057],
            [-122.526943, 48.468004],
            [-122.515056, 48.465554],
            [-122.511348, 48.461825],
            [-122.500721, 48.460887],
            [-122.471832, 48.470724],
            [-122.469634, 48.472187],
            [-122.46967, 48.474975],
            [-122.473763, 48.47975],
            [-122.478851, 48.481736],
            [-122.483501, 48.49243],
            [-122.484996, 48.50962],
            [-122.483872, 48.521891],
            [-122.485288, 48.528106],
            [-122.498463, 48.556206],
            [-122.504428, 48.564775],
            [-122.52537, 48.567344],
            [-122.531978, 48.568644],
            [-122.534719, 48.574246],
            [-122.534787, 48.57596],
            [-122.512372, 48.578067],
            [-122.495904, 48.575927],
            [-122.488421, 48.564665],
            [-122.482406, 48.559653],
            [-122.478431, 48.559303],
            [-122.44456, 48.570115],
            [-122.433059, 48.581609],
            [-122.425271, 48.599522],
            [-122.448702, 48.622624],
            [-122.46425, 48.625717],
            [-122.486878, 48.643122],
            [-122.488754, 48.645358],
            [-121.251233, 48.640997],
            [-120.910534, 48.640937],
            [-120.752021, 48.657538],
            [-120.701557, 48.53162],
            [-120.824008, 48.545153],
            [-121.035823, 48.494749],
            [-121.069941, 48.316539],
            [-121.001409, 48.295548],
            [-122.37821, 48.29759]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US06085",
      state: "06",
      county: "085",
      name: "Santa Clara County",
      censusArea: 1290.1,
      stateName: "California"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-121.654038, 36.950584],
          [-121.7576, 37.04963],
          [-122.015966, 37.165658],
          [-122.152278, 37.286055],
          [-122.190402, 37.431472],
          [-122.081473, 37.477838],
          [-122.052648, 37.459656],
          [-122.045271, 37.460276],
          [-121.855762, 37.484537],
          [-121.472648, 37.48217],
          [-121.405753, 37.31099],
          [-121.459051, 37.282739],
          [-121.404636, 37.155989],
          [-121.282271, 37.183675],
          [-121.226804, 37.134774],
          [-121.215406, 36.961248],
          [-121.501488, 36.971895],
          [-121.581354, 36.899152],
          [-121.654038, 36.950584]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13017",
      state: "13",
      county: "017",
      name: "Ben Hill County",
      censusArea: 250.121,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.177469, 31.847857],
          [-82.995698, 31.780983],
          [-82.99836, 31.673164],
          [-83.176376, 31.678382],
          [-83.177218, 31.651691],
          [-83.325617, 31.680603],
          [-83.325296, 31.756584],
          [-83.453634, 31.757861],
          [-83.480176, 31.847312],
          [-83.177469, 31.847857]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US18047",
      state: "18",
      county: "047",
      name: "Franklin County",
      censusArea: 384.43,
      stateName: "Indiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.815555, 39.511052],
          [-84.815555, 39.510952],
          [-84.815754, 39.477358],
          [-84.815754, 39.477352],
          [-84.817453, 39.391753],
          [-84.819352, 39.309454],
          [-84.819451, 39.305153],
          [-84.928655, 39.305934],
          [-85.065574, 39.307232],
          [-85.217883, 39.308474],
          [-85.29654, 39.268291],
          [-85.297575, 39.453275],
          [-85.298623, 39.520258],
          [-85.29811, 39.525476],
          [-85.036087, 39.526213],
          [-84.815355, 39.52195],
          [-84.815555, 39.511052]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22123",
      state: "22",
      county: "123",
      name: "West Carroll County",
      lsad: "Parish",
      censusArea: 359.646,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.265018, 33.005084],
          [-91.30938, 32.935237],
          [-91.344845, 32.756659],
          [-91.445299, 32.581956],
          [-91.596027, 32.582192],
          [-91.637587, 32.667341],
          [-91.566363, 32.855561],
          [-91.511515, 32.879007],
          [-91.435782, 33.006099],
          [-91.425466, 33.006016],
          [-91.376016, 33.005794],
          [-91.333011, 33.005529],
          [-91.329767, 33.005421],
          [-91.326396, 33.005376],
          [-91.325037, 33.005364],
          [-91.322506, 33.005341],
          [-91.312016, 33.005262],
          [-91.284398, 33.005007],
          [-91.265018, 33.005084]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US26067",
      state: "26",
      county: "067",
      name: "Ionia County",
      censusArea: 571.304,
      stateName: "Michigan"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.312513, 43.118793],
          [-84.836889, 43.118851],
          [-84.837085, 42.770479],
          [-85.074245, 42.770784],
          [-85.309626, 42.769878],
          [-85.312513, 43.118793]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37185",
      state: "37",
      county: "185",
      name: "Warren County",
      censusArea: 428.456,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.046202, 36.544168],
          [-78.03942, 36.544196],
          [-78.038938, 36.544173],
          [-77.899771, 36.544663],
          [-77.898943, 36.518158],
          [-77.89795, 36.506849],
          [-77.912023, 36.38227],
          [-78.006554, 36.202663],
          [-78.132887, 36.246445],
          [-78.307012, 36.266159],
          [-78.28767, 36.358369],
          [-78.323912, 36.543809],
          [-78.246681, 36.544341],
          [-78.245462, 36.544411],
          [-78.133323, 36.543847],
          [-78.132911, 36.543811],
          [-78.046202, 36.544168]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US45015",
      state: "45",
      county: "015",
      name: "Berkeley County",
      censusArea: 1098.857,
      stateName: "South Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.446699, 33.213458],
          [-79.518844, 33.148297],
          [-79.643301, 33.123453],
          [-79.855069, 32.87387],
          [-79.928305, 32.823983],
          [-79.948848, 32.90811],
          [-80.011774, 32.899881],
          [-80.055463, 32.999514],
          [-80.149246, 33.021601],
          [-80.279567, 33.119213],
          [-80.299248, 33.130274],
          [-80.361851, 33.257443],
          [-80.253836, 33.29926],
          [-80.222267, 33.443716],
          [-80.101697, 33.496891],
          [-79.971963, 33.500937],
          [-79.756172, 33.329699],
          [-79.677014, 33.304944],
          [-79.446699, 33.213458]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13045",
      state: "13",
      county: "045",
      name: "Carroll County",
      censusArea: 499.076,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.337974, 33.653111],
          [-85.050889, 33.714632],
          [-85.037926, 33.811942],
          [-84.918629, 33.786328],
          [-84.901688, 33.780703],
          [-84.902546, 33.661066],
          [-84.905788, 33.573378],
          [-84.808934, 33.574085],
          [-84.850713, 33.511457],
          [-85.015358, 33.425506],
          [-85.293902, 33.428079],
          [-85.304439, 33.482884],
          [-85.313999, 33.529807],
          [-85.314091, 33.530218],
          [-85.314994, 33.535898],
          [-85.322497, 33.574167],
          [-85.337974, 33.653111]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37093",
      state: "37",
      county: "093",
      name: "Hoke County",
      censusArea: 390.744,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.083163, 35.032662],
          [-79.036926, 34.956926],
          [-79.036447, 34.955582],
          [-79.035766, 34.955329],
          [-79.034715, 34.953584],
          [-79.191423, 34.833377],
          [-79.349143, 34.838476],
          [-79.354278, 34.945641],
          [-79.459176, 35.043096],
          [-79.335189, 35.161827],
          [-79.229885, 35.210678],
          [-79.097671, 35.174172],
          [-79.083163, 35.032662]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20149",
      state: "20",
      county: "149",
      name: "Pottawatomie County",
      censusArea: 841.022,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.238969, 39.56622],
          [-96.037537, 39.566159],
          [-96.035952, 39.5661],
          [-96.035573, 39.21652],
          [-96.03906, 39.126527],
          [-96.083557, 39.193504],
          [-96.233671, 39.212279],
          [-96.331405, 39.158884],
          [-96.388884, 39.172724],
          [-96.554012, 39.178532],
          [-96.518329, 39.203877],
          [-96.638605, 39.292827],
          [-96.726865, 39.404012],
          [-96.581764, 39.566333],
          [-96.238969, 39.56622]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US28033",
      state: "28",
      county: "033",
      name: "De Soto County",
      censusArea: 476.145,
      stateName: "Mississippi"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.795187, 34.994293],
          [-89.724324, 34.994763],
          [-89.723357, 34.773347],
          [-89.723635, 34.772121],
          [-89.912202, 34.772506],
          [-90.030051, 34.70795],
          [-90.128906, 34.760537],
          [-90.200199, 34.724418],
          [-90.202126, 34.860374],
          [-90.293918, 34.860563],
          [-90.250095, 34.90732],
          [-90.244476, 34.937596],
          [-90.253969, 34.954988],
          [-90.309297, 34.995694],
          [-89.893402, 34.994356],
          [-89.883365, 34.994261],
          [-89.848488, 34.994193],
          [-89.795187, 34.994293]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US42001",
      state: "42",
      county: "001",
      name: "Adams County",
      censusArea: 518.668,
      stateName: "Pennsylvania"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.255209, 40.02076],
          [-77.137425, 40.069945],
          [-77.018648, 40.013947],
          [-76.954932, 39.85685],
          [-76.998377, 39.832115],
          [-76.999465, 39.720128],
          [-77.047104, 39.72],
          [-77.058204, 39.7202],
          [-77.058904, 39.7201],
          [-77.216806, 39.719998],
          [-77.217024, 39.719998],
          [-77.239807, 39.719998],
          [-77.243307, 39.719998],
          [-77.459427, 39.720017],
          [-77.471085, 39.944077],
          [-77.403788, 39.994447],
          [-77.255209, 40.02076]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US37161",
      state: "37",
      county: "161",
      name: "Rutherford County",
      censusArea: 564.151,
      stateName: "North Carolina"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.874433, 35.184113],
          [-81.969325, 35.187215],
          [-81.968788, 35.257764],
          [-82.115159, 35.38739],
          [-82.261456, 35.392984],
          [-82.270255, 35.400351],
          [-82.279615, 35.445973],
          [-82.266847, 35.465854],
          [-82.169018, 35.527841],
          [-82.002851, 35.551802],
          [-81.973556, 35.524565],
          [-81.824184, 35.574955],
          [-81.692329, 35.579789],
          [-81.70285, 35.357194],
          [-81.768073, 35.180132],
          [-81.857832, 35.183492],
          [-81.874433, 35.184113]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21051",
      state: "21",
      county: "051",
      name: "Clay County",
      censusArea: 469.247,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.507886, 37.235877],
          [-83.551303, 37.16751],
          [-83.509083, 36.938509],
          [-83.520687, 36.93755],
          [-83.530847, 36.943038],
          [-83.589521, 36.955786],
          [-83.871315, 37.054919],
          [-83.96899, 37.174794],
          [-83.943718, 37.250551],
          [-83.784975, 37.348717],
          [-83.65394, 37.358061],
          [-83.526907, 37.258529],
          [-83.511263, 37.255073],
          [-83.507886, 37.235877]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13245",
      state: "13",
      county: "245",
      name: "Richmond County",
      censusArea: 324.326,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.028238, 33.544934],
          [-82.014293, 33.530313],
          [-82.007638, 33.523335],
          [-82.007138, 33.522835],
          [-81.967037, 33.480636],
          [-81.957934, 33.468632],
          [-81.912572, 33.408811],
          [-81.902737, 33.331242],
          [-81.852136, 33.247544],
          [-81.851975, 33.247408],
          [-82.088162, 33.230245],
          [-82.17424, 33.296771],
          [-82.26758, 33.267397],
          [-82.278539, 33.269174],
          [-82.286507, 33.274135],
          [-82.296855, 33.277349],
          [-82.311539, 33.297146],
          [-82.353498, 33.312318],
          [-82.350299, 33.314801],
          [-82.294179, 33.354635],
          [-82.028238, 33.544934]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13251",
      state: "13",
      county: "251",
      name: "Screven County",
      censusArea: 645.101,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.405134, 32.744964],
          [-81.414761, 32.63744],
          [-81.418431, 32.634704],
          [-81.41866, 32.629392],
          [-81.411906, 32.61841],
          [-81.389338, 32.595436],
          [-81.389258, 32.595385],
          [-81.538558, 32.509059],
          [-81.548006, 32.489286],
          [-81.689643, 32.54629],
          [-81.841005, 32.649093],
          [-81.867938, 32.68115],
          [-81.767553, 32.909411],
          [-81.541831, 33.045654],
          [-81.51169, 33.024506],
          [-81.496637, 33.010224],
          [-81.491495, 32.998572],
          [-81.494736, 32.978998],
          [-81.49983, 32.963816],
          [-81.423772, 32.810514],
          [-81.404949, 32.747027],
          [-81.405134, 32.744964]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13261",
      state: "13",
      county: "261",
      name: "Sumter County",
      censusArea: 482.697,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.430215, 32.166257],
          [-84.183569, 32.158493],
          [-84.181657, 32.229703],
          [-84.027853, 32.171083],
          [-83.961278, 32.030595],
          [-83.922494, 31.909653],
          [-84.338143, 31.91619],
          [-84.338245, 31.873591],
          [-84.443807, 31.967456],
          [-84.431214, 32.134058],
          [-84.430215, 32.166257]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13263",
      state: "13",
      county: "263",
      name: "Talbot County",
      censusArea: 391.389,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.444353, 32.562083],
          [-84.637323, 32.534855],
          [-84.648977, 32.534956],
          [-84.658017, 32.528759],
          [-84.661721, 32.529148],
          [-84.678157, 32.518915],
          [-84.686265, 32.519624],
          [-84.69451, 32.518732],
          [-84.694603, 32.583945],
          [-84.700538, 32.84464],
          [-84.570669, 32.845179],
          [-84.506888, 32.881788],
          [-84.391017, 32.786956],
          [-84.286246, 32.747626],
          [-84.33794, 32.720647],
          [-84.408866, 32.561923],
          [-84.444353, 32.562083]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US13273",
      state: "13",
      county: "273",
      name: "Terrell County",
      censusArea: 335.439,
      stateName: "Georgia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.297801, 31.621951],
          [-84.450398, 31.621868],
          [-84.450416, 31.621255],
          [-84.546854, 31.621199],
          [-84.535527, 31.681828],
          [-84.603086, 31.772001],
          [-84.599776, 31.920171],
          [-84.45331, 31.919224],
          [-84.443807, 31.967456],
          [-84.338245, 31.873591],
          [-84.286456, 31.791182],
          [-84.297801, 31.621951]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20193",
      state: "20",
      county: "193",
      name: "Thomas County",
      censusArea: 1074.687,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.720213, 39.568034],
          [-100.721296, 39.133381],
          [-100.775177, 39.133265],
          [-100.775311, 39.133375],
          [-100.811858, 39.13336],
          [-101.391717, 39.135117],
          [-101.389068, 39.568369],
          [-100.740873, 39.568043],
          [-100.720213, 39.568034]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20199",
      state: "20",
      county: "199",
      name: "Wallace County",
      censusArea: 913.652,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.567094, 38.699669],
          [-102.045212, 38.697567],
          [-102.045375, 38.754339],
          [-102.045287, 38.755528],
          [-102.045371, 38.770064],
          [-102.045448, 38.783453],
          [-102.045334, 38.799463],
          [-102.045388, 38.813392],
          [-102.046571, 39.047038],
          [-102.047134, 39.129701],
          [-102.047189, 39.133147],
          [-101.478195, 39.134761],
          [-101.484383, 38.700166],
          [-101.567094, 38.699669]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20201",
      state: "20",
      county: "201",
      name: "Washington County",
      censusArea: 894.756,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-97.369199, 40.00206],
          [-97.369199, 40.00206],
          [-97.369103, 40.00206],
          [-97.350896, 40.00193],
          [-97.350272, 40.001976],
          [-97.245169, 40.001513],
          [-97.24508, 40.001467],
          [-97.20231, 40.001442],
          [-97.20019, 40.001549],
          [-97.181775, 40.00155],
          [-97.142448, 40.001495],
          [-97.137866, 40.001814],
          [-97.049663, 40.001323],
          [-97.030803, 40.001342],
          [-97.009165, 40.001463],
          [-96.916407, 40.001506],
          [-96.916093, 40.001506],
          [-96.880459, 40.001448],
          [-96.878253, 40.001466],
          [-96.875057, 40.001448],
          [-96.873812, 40.00145],
          [-96.805768, 40.001371],
          [-96.806544, 39.566423],
          [-96.958859, 39.5664],
          [-97.368574, 39.567018],
          [-97.368635, 39.575926],
          [-97.368374, 39.577145],
          [-97.368653, 39.583833],
          [-97.36867, 39.654043],
          [-97.369199, 40.00206]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21195",
      state: "21",
      county: "195",
      name: "Pike County",
      censusArea: 786.833,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.326404, 37.740815],
          [-82.325739, 37.735799],
          [-82.307235, 37.707669],
          [-82.297011, 37.687273],
          [-82.25028, 37.657776],
          [-82.21967, 37.643046],
          [-82.141814, 37.595052],
          [-82.125601, 37.579021],
          [-82.125892, 37.574031],
          [-82.116584, 37.559588],
          [-82.048521, 37.531078],
          [-82.008274, 37.533357],
          [-81.999844, 37.542579],
          [-81.998177, 37.543082],
          [-81.968297, 37.537798],
          [-82.291908, 37.311642],
          [-82.305874, 37.3011],
          [-82.310793, 37.297387],
          [-82.350948, 37.267077],
          [-82.510826, 37.218091],
          [-82.553549, 37.200867],
          [-82.565329, 37.196118],
          [-82.565375, 37.196092],
          [-82.732693, 37.271502],
          [-82.724557, 37.281035],
          [-82.718852, 37.280276],
          [-82.710805, 37.285323],
          [-82.672375, 37.306841],
          [-82.584711, 37.466574],
          [-82.623112, 37.503541],
          [-82.563561, 37.684844],
          [-82.456134, 37.668795],
          [-82.326404, 37.740815]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21211",
      state: "21",
      county: "211",
      name: "Shelby County",
      censusArea: 379.636,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.424054, 38.147438],
          [-85.404899, 38.26373],
          [-85.468206, 38.285309],
          [-85.324453, 38.310112],
          [-85.283082, 38.358054],
          [-84.997667, 38.335586],
          [-85.023711, 38.129052],
          [-85.101646, 38.03727],
          [-85.232406, 38.089431],
          [-85.424054, 38.147438]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21213",
      state: "21",
      county: "213",
      name: "Simpson County",
      censusArea: 234.204,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-86.468497, 36.651841],
          [-86.47219, 36.651763],
          [-86.473413, 36.651676],
          [-86.473497, 36.651671],
          [-86.564143, 36.633472],
          [-86.564252, 36.633522],
          [-86.605042, 36.652125],
          [-86.606394, 36.652107],
          [-86.75892, 36.649018],
          [-86.763295, 36.648907],
          [-86.690719, 36.8443],
          [-86.611586, 36.883057],
          [-86.583289, 36.830282],
          [-86.400518, 36.807226],
          [-86.405769, 36.776187],
          [-86.411387, 36.65055],
          [-86.468497, 36.651841]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21223",
      state: "21",
      county: "223",
      name: "Trimble County",
      censusArea: 151.648,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-85.16827, 38.585448],
          [-85.314006, 38.492592],
          [-85.432928, 38.524072],
          [-85.423077, 38.531581],
          [-85.417322, 38.540763],
          [-85.4156, 38.546341],
          [-85.415821, 38.563558],
          [-85.427467, 38.586735],
          [-85.439351, 38.610388],
          [-85.456978, 38.689135],
          [-85.452114, 38.709348],
          [-85.434065, 38.729455],
          [-85.422021, 38.734834],
          [-85.410925, 38.73708],
          [-85.333285, 38.74048],
          [-85.16827, 38.585448]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21225",
      state: "21",
      county: "225",
      name: "Union County",
      censusArea: 342.849,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.133393, 37.574235],
          [-88.133416, 37.574277],
          [-88.139973, 37.586451],
          [-88.156827, 37.632801],
          [-88.160187, 37.657592],
          [-88.158207, 37.664542],
          [-88.151646, 37.675098],
          [-88.122412, 37.709685],
          [-88.072538, 37.733286],
          [-88.059588, 37.742608],
          [-88.050969, 37.752599],
          [-88.045008, 37.762436],
          [-88.039, 37.775454],
          [-88.02803, 37.799224],
          [-88.017314, 37.801911],
          [-88.004706, 37.800145],
          [-87.976389, 37.788004],
          [-87.970869, 37.783456],
          [-87.943756, 37.776843],
          [-87.90681, 37.807624],
          [-87.903804, 37.817762],
          [-87.907773, 37.837611],
          [-87.910276, 37.843416],
          [-87.914892, 37.849618],
          [-87.927303, 37.858709],
          [-87.936228, 37.867937],
          [-87.940005, 37.875044],
          [-87.940839, 37.883338],
          [-87.940069, 37.88767],
          [-87.936784, 37.892587],
          [-87.927424, 37.902038],
          [-87.815093, 37.762423],
          [-87.75565, 37.727763],
          [-87.734545, 37.638376],
          [-87.918474, 37.492937],
          [-87.932952, 37.480052],
          [-88.015572, 37.54721],
          [-88.061311, 37.505332],
          [-88.063311, 37.515755],
          [-88.072242, 37.528826],
          [-88.121517, 37.568166],
          [-88.133235, 37.574154],
          [-88.133306, 37.574191],
          [-88.133393, 37.574235]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20037",
      state: "20",
      county: "037",
      name: "Crawford County",
      censusArea: 589.756,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-94.617575, 37.653577],
          [-94.617477, 37.63717],
          [-94.6173, 37.610495],
          [-94.617428, 37.609522],
          [-94.617283, 37.571896],
          [-94.617315, 37.571499],
          [-94.617081, 37.567013],
          [-94.61716, 37.557308],
          [-94.617186, 37.553485],
          [-94.616908, 37.527804],
          [-94.616789, 37.52151],
          [-94.617023, 37.483765],
          [-94.617183, 37.469665],
          [-94.61718, 37.465203],
          [-94.617222, 37.460476],
          [-94.617205, 37.460373],
          [-94.617201, 37.454788],
          [-94.617132, 37.439818],
          [-94.617265, 37.425536],
          [-94.617511, 37.410909],
          [-94.617557, 37.396375],
          [-94.617625, 37.367576],
          [-94.617626, 37.367445],
          [-94.617537, 37.364355],
          [-94.617538, 37.364167],
          [-94.617636, 37.338417],
          [-94.617636, 37.338415],
          [-94.619202, 37.33836],
          [-95.075166, 37.339937],
          [-95.074972, 37.383823],
          [-95.088189, 37.383837],
          [-95.088012, 37.674519],
          [-94.617734, 37.673105],
          [-94.617576, 37.653671],
          [-94.617575, 37.653577]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20043",
      state: "20",
      county: "043",
      name: "Doniphan County",
      censusArea: 393.405,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-95.339896, 39.999999],
          [-95.30829, 39.999998],
          [-95.30778, 39.990618],
          [-95.301549, 39.983603],
          [-95.181222, 39.899996],
          [-95.132695, 39.875925],
          [-95.107745, 39.868579],
          [-95.085003, 39.861883],
          [-95.042142, 39.864805],
          [-95.035047, 39.866807],
          [-95.027931, 39.871522],
          [-95.02525, 39.877688],
          [-95.02524, 39.8897],
          [-95.01632, 39.898766],
          [-95.00844, 39.900596],
          [-94.993348, 39.900747],
          [-94.954155, 39.901138],
          [-94.935963, 39.894355],
          [-94.927897, 39.886112],
          [-94.908267, 39.812172],
          [-94.895268, 39.76321],
          [-94.916932, 39.728186],
          [-94.937924, 39.725471],
          [-95.011103, 39.677572],
          [-95.01531, 39.674262],
          [-95.069592, 39.615106],
          [-95.115189, 39.652549],
          [-95.33974, 39.65298],
          [-95.339896, 39.999999]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20061",
      state: "20",
      county: "061",
      name: "Geary County",
      censusArea: 384.618,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-96.755786, 39.088006],
          [-96.501181, 39.072782],
          [-96.501174, 39.043666],
          [-96.501556, 38.869704],
          [-96.890245, 38.870067],
          [-96.926209, 38.978941],
          [-96.96347, 38.964901],
          [-96.963177, 39.1323],
          [-96.96138, 39.132325],
          [-96.961387, 39.220073],
          [-96.849879, 39.219012],
          [-96.850991, 39.101307],
          [-96.851111, 39.088386],
          [-96.755786, 39.088006]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20067",
      state: "20",
      county: "067",
      name: "Grant County",
      censusArea: 574.8,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.52581, 37.388392],
          [-101.527063, 37.736314],
          [-101.089667, 37.736337],
          [-101.089653, 37.387721],
          [-101.52581, 37.388392]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20071",
      state: "20",
      county: "071",
      name: "Greeley County",
      censusArea: 778.45,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-101.567094, 38.699669],
          [-101.567482, 38.263145],
          [-102.04451, 38.262412],
          [-102.044567, 38.268749],
          [-102.044568, 38.268819],
          [-102.044613, 38.312324],
          [-102.044944, 38.384419],
          [-102.045263, 38.505395],
          [-102.045262, 38.505532],
          [-102.045112, 38.523784],
          [-102.045223, 38.543797],
          [-102.045189, 38.558732],
          [-102.045211, 38.581609],
          [-102.045288, 38.615168],
          [-102.045288, 38.615249],
          [-102.045074, 38.669617],
          [-102.045102, 38.674946],
          [-102.04516, 38.675221],
          [-102.045127, 38.686725],
          [-102.045156, 38.688555],
          [-102.045212, 38.697567],
          [-101.567094, 38.699669]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20083",
      state: "20",
      county: "083",
      name: "Hodgeman County",
      censusArea: 859.992,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-100.227137, 38.262237],
          [-99.584207, 38.262179],
          [-99.570543, 38.261957],
          [-99.569533, 38.087372],
          [-99.569879, 37.913016],
          [-100.22709, 37.914018],
          [-100.22661, 38.001015],
          [-100.227137, 38.262237]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US20089",
      state: "20",
      county: "089",
      name: "Jewell County",
      censusArea: 909.781,
      stateName: "Kansas"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-98.504455, 40.002329],
          [-98.490533, 40.002323],
          [-98.274017, 40.002516],
          [-98.274015, 40.002516],
          [-98.268218, 40.00249],
          [-98.250008, 40.002307],
          [-98.193483, 40.002614],
          [-98.179315, 40.002483],
          [-98.172269, 40.002438],
          [-98.142031, 40.002452],
          [-98.099659, 40.002227],
          [-98.076034, 40.002301],
          [-98.068701, 40.002355],
          [-98.050057, 40.002278],
          [-98.047469, 40.002186],
          [-98.014412, 40.002223],
          [-98.010157, 40.002153],
          [-97.972186, 40.002114],
          [-97.931826, 40.00205],
          [-97.931482, 39.653767],
          [-97.931844, 39.566921],
          [-98.487384, 39.567492],
          [-98.503592, 39.567515],
          [-98.505266, 39.567603],
          [-98.505152, 39.569754],
          [-98.504455, 40.002329]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22019",
      state: "22",
      county: "019",
      name: "Calcasieu County",
      lsad: "Parish",
      censusArea: 1063.659,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-93.722481, 30.050898],
          [-93.721589, 30.051939],
          [-93.712101, 30.067346],
          [-93.695684, 30.135729],
          [-93.695252, 30.1476],
          [-93.712008, 30.194304],
          [-93.716223, 30.244318],
          [-93.720575, 30.295961],
          [-93.723586, 30.294951],
          [-93.735896, 30.29944],
          [-93.765822, 30.333318],
          [-93.757654, 30.390423],
          [-93.751437, 30.396288],
          [-93.741701, 30.403007],
          [-93.486116, 30.4025],
          [-93.486384, 30.490523],
          [-93.384673, 30.490109],
          [-93.38515, 30.40263],
          [-93.131109, 30.403057],
          [-93.030955, 30.37939],
          [-92.9963, 30.156955],
          [-92.893324, 30.156314],
          [-92.887605, 30.082475],
          [-92.996371, 30.038479],
          [-93.128475, 30.052587],
          [-93.722481, 30.050898]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22027",
      state: "22",
      county: "027",
      name: "Claiborne County",
      lsad: "Parish",
      censusArea: 754.878,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.880805, 32.585277],
          [-93.178767, 32.584916],
          [-93.186571, 32.716755],
          [-93.237381, 32.71625],
          [-93.238607, 33.017992],
          [-93.197402, 33.017951],
          [-93.154351, 33.017856],
          [-93.101443, 33.01774],
          [-93.100981, 33.017786],
          [-93.081428, 33.017928],
          [-93.073167, 33.017898],
          [-93.070686, 33.017792],
          [-92.988708, 33.017298],
          [-92.971137, 33.017192],
          [-92.946553, 33.016807],
          [-92.86751, 33.016062],
          [-92.854167, 33.016132],
          [-92.844286, 33.01607],
          [-92.844073, 33.016034],
          [-92.830798, 33.015661],
          [-92.733197, 33.014347],
          [-92.724994, 33.014351],
          [-92.724743, 33.014347],
          [-92.725439, 32.75954],
          [-92.828535, 32.758814],
          [-92.880805, 32.585277]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22029",
      state: "22",
      county: "029",
      name: "Concordia County",
      lsad: "Parish",
      censusArea: 696.92,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.379935, 31.733013],
          [-91.397915, 31.709364],
          [-91.400115, 31.688164],
          [-91.398059, 31.655922],
          [-91.404668, 31.637898],
          [-91.42209, 31.63129],
          [-91.441315, 31.63129],
          [-91.45273, 31.63129],
          [-91.464746, 31.628886],
          [-91.474959, 31.621677],
          [-91.477963, 31.610863],
          [-91.477963, 31.604855],
          [-91.477963, 31.598247],
          [-91.477362, 31.592239],
          [-91.470754, 31.58563],
          [-91.458679, 31.583226],
          [-91.44862, 31.583032],
          [-91.437711, 31.580824],
          [-91.425094, 31.57782],
          [-91.415482, 31.567006],
          [-91.416683, 31.561599],
          [-91.422691, 31.55439],
          [-91.437616, 31.546166],
          [-91.450017, 31.539666],
          [-91.479718, 31.530366],
          [-91.511217, 31.532612],
          [-91.51581, 31.530894],
          [-91.52292, 31.519841],
          [-91.51513, 31.449206],
          [-91.510356, 31.438928],
          [-91.505301, 31.432945],
          [-91.500406, 31.419008],
          [-91.505093, 31.400933],
          [-91.513795, 31.386875],
          [-91.525386, 31.378904],
          [-91.541242, 31.35675],
          [-91.539904, 31.337336],
          [-91.531201, 31.326625],
          [-91.51982, 31.311228],
          [-91.508858, 31.291644],
          [-91.515614, 31.27821],
          [-91.522397, 31.273423],
          [-91.547588, 31.264444],
          [-91.574493, 31.261289],
          [-91.59849, 31.205485],
          [-91.599965, 31.192318],
          [-91.604197, 31.154545],
          [-91.577373, 31.078179],
          [-91.56415, 31.06683],
          [-91.561283, 31.060906],
          [-91.559907, 31.054119],
          [-91.562468, 31.043213],
          [-91.571695, 31.029782],
          [-91.58467, 31.020185],
          [-91.636942, 30.999416],
          [-91.640613, 30.996125],
          [-91.646409, 30.986146],
          [-91.662506, 30.970362],
          [-91.750119, 31.018814],
          [-91.677302, 31.188047],
          [-91.83428, 31.266344],
          [-91.862168, 31.302314],
          [-91.845019, 31.375916],
          [-91.776779, 31.385215],
          [-91.855889, 31.423831],
          [-91.857083, 31.494527],
          [-91.807098, 31.438794],
          [-91.738331, 31.431572],
          [-91.824426, 31.599671],
          [-91.775836, 31.648626],
          [-91.705948, 31.642863],
          [-91.694469, 31.735286],
          [-91.601622, 31.761864],
          [-91.544791, 31.751731],
          [-91.379935, 31.733013]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22037",
      state: "22",
      county: "037",
      name: "East Feliciana County",
      lsad: "Parish",
      censusArea: 453.41,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.826027, 30.99936],
          [-90.825829, 30.99936],
          [-90.863041, 30.941981],
          [-90.849041, 30.719311],
          [-91.254419, 30.705294],
          [-91.297658, 30.649548],
          [-91.298918, 30.650462],
          [-91.301179, 30.651572],
          [-91.303767, 30.652029],
          [-91.309395, 30.651996],
          [-91.310061, 30.653961],
          [-91.320706, 30.699417],
          [-91.236164, 30.795093],
          [-91.17614, 30.999144],
          [-91.108291, 30.99888],
          [-91.108114, 30.998857],
          [-91.080814, 30.998909],
          [-91.06827, 30.99892],
          [-91.060217, 30.998935],
          [-90.826027, 30.99936]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53009",
      state: "53",
      county: "009",
      name: "Clallam County",
      censusArea: 1738.329,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-123.131422, 48.152736],
          [-123.124816, 48.153472],
          [-123.116479, 48.150208],
          [-123.085154, 48.127137],
          [-123.06621, 48.120469],
          [-123.050446, 48.102825],
          [-123.038727, 48.081138],
          [-123.016651, 48.08538],
          [-123.004128, 48.090516],
          [-122.979413, 48.09594],
          [-122.946119, 48.098552],
          [-122.929095, 48.096244],
          [-122.917942, 48.091535],
          [-122.920911, 48.088199],
          [-122.926644, 48.0741],
          [-122.927975, 48.06665],
          [-122.927146, 48.065133],
          [-122.949055, 48.065151],
          [-122.949167, 47.866829],
          [-123.505917, 47.866897],
          [-123.505917, 47.88108],
          [-124.610763, 47.880607],
          [-124.625512, 47.887963],
          [-124.630153, 47.892467],
          [-124.629706, 47.896968],
          [-124.645442, 47.935338],
          [-124.651966, 47.943177],
          [-124.662334, 47.951451],
          [-124.672427, 47.964414],
          [-124.67083, 47.982366],
          [-124.679024, 48.015697],
          [-124.682157, 48.035987],
          [-124.685393, 48.049238],
          [-124.688359, 48.054927],
          [-124.693676, 48.058697],
          [-124.696542, 48.069274],
          [-124.695114, 48.087096],
          [-124.688602, 48.092466],
          [-124.687101, 48.098657],
          [-124.695088, 48.114878],
          [-124.721725, 48.153185],
          [-124.731703, 48.160402],
          [-124.733174, 48.163393],
          [-124.731746, 48.169997],
          [-124.704153, 48.184422],
          [-124.696111, 48.198599],
          [-124.6909, 48.212617],
          [-124.690389, 48.219745],
          [-124.705031, 48.238774],
          [-124.70592, 48.239894],
          [-124.699663, 48.245812],
          [-124.684677, 48.255228],
          [-124.680877, 48.26535],
          [-124.676319, 48.295143],
          [-124.669265, 48.296353],
          [-124.665908, 48.299324],
          [-124.662068, 48.31045],
          [-124.65894, 48.331057],
          [-124.670072, 48.341341],
          [-124.696703, 48.349748],
          [-124.713817, 48.366309],
          [-124.727022, 48.371101],
          [-124.730863, 48.3762],
          [-124.731828, 48.381157],
          [-124.725839, 48.386012],
          [-124.716947, 48.389776],
          [-124.694511, 48.389004],
          [-124.653243, 48.390691],
          [-124.639389, 48.385524],
          [-124.631108, 48.376522],
          [-124.611782, 48.378182],
          [-124.599278, 48.381035],
          [-124.597331, 48.381882],
          [-124.590733, 48.373604],
          [-124.572864, 48.366228],
          [-124.564841, 48.367921],
          [-124.546259, 48.353594],
          [-124.538821, 48.349893],
          [-124.525453, 48.349022],
          [-124.510582, 48.343236],
          [-124.414007, 48.300887],
          [-124.395593, 48.288772],
          [-124.380874, 48.284699],
          [-124.361351, 48.287582],
          [-124.342412, 48.277695],
          [-124.299146, 48.268239],
          [-124.295589, 48.262983],
          [-124.296924, 48.261796],
          [-124.297643, 48.260676],
          [-124.295693, 48.259282],
          [-124.272017, 48.25441],
          [-124.265824, 48.254842],
          [-124.255109, 48.258972],
          [-124.252267, 48.261004],
          [-124.250882, 48.264773],
          [-124.238582, 48.262471],
          [-124.217873, 48.253294],
          [-124.192692, 48.246316],
          [-124.14129, 48.227413],
          [-124.110974, 48.220557],
          [-124.101773, 48.216883],
          [-124.107215, 48.200082],
          [-124.090717, 48.196458],
          [-124.072124, 48.189903],
          [-124.050734, 48.177747],
          [-123.981032, 48.164761],
          [-123.955347, 48.165455],
          [-123.934921, 48.16084],
          [-123.915589, 48.159352],
          [-123.880068, 48.160621],
          [-123.866677, 48.154796],
          [-123.858821, 48.154273],
          [-123.831571, 48.157937],
          [-123.778122, 48.155466],
          [-123.756395, 48.161057],
          [-123.728736, 48.1628],
          [-123.72829, 48.160858],
          [-123.725352, 48.159191],
          [-123.71835, 48.158713],
          [-123.706226, 48.1634],
          [-123.706432, 48.165822],
          [-123.702743, 48.166783],
          [-123.672445, 48.162715],
          [-123.651408, 48.156952],
          [-123.636967, 48.150319],
          [-123.641108, 48.146127],
          [-123.628819, 48.139279],
          [-123.590839, 48.134949],
          [-123.574214, 48.140756],
          [-123.560591, 48.150697],
          [-123.551131, 48.151382],
          [-123.534879, 48.14578],
          [-123.52232, 48.135539],
          [-123.507235, 48.131807],
          [-123.473379, 48.134079],
          [-123.455458, 48.140047],
          [-123.440128, 48.142014],
          [-123.439127, 48.141278],
          [-123.441972, 48.124259],
          [-123.424668, 48.118065],
          [-123.395048, 48.114243],
          [-123.360923, 48.115864],
          [-123.332699, 48.11297],
          [-123.314578, 48.113725],
          [-123.288265, 48.121036],
          [-123.280178, 48.117309],
          [-123.268917, 48.116094],
          [-123.248615, 48.115745],
          [-123.239129, 48.118217],
          [-123.21719, 48.127203],
          [-123.191521, 48.143821],
          [-123.1644, 48.165894],
          [-123.144783, 48.175943],
          [-123.133445, 48.177276],
          [-123.132417, 48.174704],
          [-123.139258, 48.16648],
          [-123.143229, 48.156633],
          [-123.131422, 48.152736]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US53027",
      state: "53",
      county: "027",
      name: "Grays Harbor County",
      censusArea: 1902.027,
      stateName: "Washington"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-124.098359, 46.794157],
          [-124.101232, 46.810656],
          [-124.108078, 46.836388],
          [-124.122979, 46.879809],
          [-124.138225, 46.905534],
          [-124.117712, 46.91238],
          [-124.110641, 46.91252],
          [-124.093392, 46.901168],
          [-124.090422, 46.8955],
          [-124.089286, 46.867716],
          [-124.073113, 46.861493],
          [-124.066349, 46.863504],
          [-124.061051, 46.865127],
          [-124.055085, 46.870429],
          [-124.049279, 46.891253],
          [-124.046344, 46.893972],
          [-124.03624, 46.898473],
          [-124.01366, 46.90363],
          [-124.009519, 46.910325],
          [-123.985082, 46.921916],
          [-123.979378, 46.923038],
          [-123.957493, 46.921261],
          [-123.915256, 46.932964],
          [-123.882884, 46.939946],
          [-123.86018, 46.948556],
          [-123.876136, 46.961054],
          [-123.889402, 46.968904],
          [-123.898245, 46.971927],
          [-123.921617, 46.971864],
          [-123.939214, 46.969739],
          [-123.947996, 46.971818],
          [-123.959185, 46.981759],
          [-123.991612, 46.980215],
          [-124.012218, 46.985176],
          [-124.019727, 46.991189],
          [-124.010068, 46.997882],
          [-124.005248, 47.003915],
          [-124.017035, 47.011717],
          [-124.016999, 47.014848],
          [-124.026345, 47.030187],
          [-124.065856, 47.04114],
          [-124.106378, 47.04264],
          [-124.122057, 47.04165],
          [-124.141517, 47.035142],
          [-124.149043, 47.029294],
          [-124.151288, 47.021112],
          [-124.139733, 46.98837],
          [-124.138035, 46.970959],
          [-124.124386, 46.94387],
          [-124.141267, 46.940266],
          [-124.158624, 46.929439],
          [-124.180111, 46.926357],
          [-124.174503, 46.941623],
          [-124.171161, 46.958443],
          [-124.169113, 46.994508],
          [-124.173501, 47.06637],
          [-124.176745, 47.092999],
          [-124.183833, 47.124807],
          [-124.182802, 47.134041],
          [-124.185806, 47.136017],
          [-124.189725, 47.146827],
          [-124.195893, 47.174],
          [-124.209017, 47.218151],
          [-124.236349, 47.287287],
          [-124.242234, 47.295101],
          [-124.25359, 47.30248],
          [-124.257452, 47.304059],
          [-124.271193, 47.305025],
          [-124.286369, 47.325162],
          [-124.293288, 47.339309],
          [-124.299943, 47.34836],
          [-124.307509, 47.352268],
          [-124.319379, 47.355559],
          [-124.324091, 47.367602],
          [-124.32665, 47.388759],
          [-124.336724, 47.415996],
          [-124.345155, 47.48903],
          [-124.353651, 47.53361],
          [-124.047108, 47.51812],
          [-123.50438, 47.517609],
          [-123.489031, 47.082716],
          [-123.202066, 47.084895],
          [-123.158439, 46.996237],
          [-123.160588, 46.793383],
          [-123.202826, 46.794392],
          [-123.370775, 46.792449],
          [-124.098359, 46.794157]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55105",
      state: "55",
      county: "105",
      name: "Rock County",
      censusArea: 718.142,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.777137, 42.834488],
          [-88.776496, 42.49402],
          [-88.940388, 42.495046],
          [-88.940391, 42.495046],
          [-88.943264, 42.495114],
          [-88.992659, 42.496025],
          [-88.992977, 42.496026],
          [-89.013667, 42.496087],
          [-89.013804, 42.496097],
          [-89.042898, 42.496255],
          [-89.071141, 42.496208],
          [-89.099012, 42.496499],
          [-89.116949, 42.49691],
          [-89.120365, 42.496992],
          [-89.125111, 42.496957],
          [-89.164905, 42.497347],
          [-89.166728, 42.497256],
          [-89.22627, 42.497957],
          [-89.228279, 42.498047],
          [-89.246972, 42.49813],
          [-89.250759, 42.497994],
          [-89.290896, 42.498853],
          [-89.361561, 42.500012],
          [-89.366031, 42.500274],
          [-89.369127, 42.845046],
          [-89.013489, 42.847347],
          [-88.777076, 42.842694],
          [-88.777137, 42.834488]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55117",
      state: "55",
      county: "117",
      name: "Sheboygan County",
      censusArea: 511.265,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.731322, 43.892184],
          [-87.733735, 43.88634],
          [-87.734457, 43.88459],
          [-87.735436, 43.882219],
          [-87.736178, 43.880421],
          [-87.736017, 43.873721],
          [-87.734881, 43.87043],
          [-87.728698, 43.852524],
          [-87.728728, 43.851833],
          [-87.729217, 43.840582],
          [-87.7296, 43.831782],
          [-87.727686, 43.818989],
          [-87.726772, 43.812885],
          [-87.726408, 43.810454],
          [-87.726407, 43.810445],
          [-87.726405, 43.810442],
          [-87.700251, 43.76735],
          [-87.700245, 43.767129],
          [-87.70009, 43.76159],
          [-87.700085, 43.761395],
          [-87.702985, 43.749695],
          [-87.703047, 43.749565],
          [-87.70463, 43.746256],
          [-87.705185, 43.745095],
          [-87.708167, 43.742979],
          [-87.708285, 43.742895],
          [-87.708321, 43.742737],
          [-87.709885, 43.735795],
          [-87.708185, 43.722895],
          [-87.707923, 43.722064],
          [-87.703144, 43.706901],
          [-87.702985, 43.706395],
          [-87.702685, 43.687596],
          [-87.703671, 43.685339],
          [-87.706204, 43.679542],
          [-87.706242, 43.679488],
          [-87.72646, 43.650462],
          [-87.733622, 43.640181],
          [-87.734312, 43.63919],
          [-87.735065, 43.638217],
          [-87.735489, 43.637668],
          [-87.735972, 43.637044],
          [-87.736146, 43.636819],
          [-87.737859, 43.634604],
          [-87.738638, 43.633597],
          [-87.742485, 43.628622],
          [-87.742522, 43.628574],
          [-87.780175, 43.579889],
          [-87.781255, 43.578493],
          [-87.782271, 43.576726],
          [-87.789105, 43.564844],
          [-87.790135, 43.563054],
          [-87.790277, 43.562376],
          [-87.793075, 43.54899],
          [-87.794321, 43.543034],
          [-88.040528, 43.542361],
          [-88.160873, 43.542943],
          [-88.162274, 43.891511],
          [-88.041794, 43.891692],
          [-87.731322, 43.892184]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55127",
      state: "55",
      county: "127",
      name: "Walworth County",
      censusArea: 555.127,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.707961, 42.842657],
          [-88.659279, 42.842541],
          [-88.541535, 42.842996],
          [-88.531228, 42.842719],
          [-88.306384, 42.842095],
          [-88.305891, 42.610817],
          [-88.304692, 42.494773],
          [-88.417396, 42.494618],
          [-88.461397, 42.494618],
          [-88.470597, 42.494672],
          [-88.506912, 42.494883],
          [-88.707378, 42.493587],
          [-88.70738, 42.493587],
          [-88.776496, 42.49402],
          [-88.777137, 42.834488],
          [-88.777076, 42.842694],
          [-88.771719, 42.842825],
          [-88.707961, 42.842657]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55009",
      state: "55",
      county: "009",
      name: "Brown County",
      censusArea: 529.708,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.989912, 44.677587],
          [-87.990081, 44.669791],
          [-87.99011, 44.668455],
          [-87.998964, 44.665187],
          [-88.000321, 44.664686],
          [-88.002085, 44.664035],
          [-88.002254, 44.663442],
          [-88.002677, 44.661956],
          [-88.00334, 44.65963],
          [-88.007287, 44.645779],
          [-88.009766, 44.637081],
          [-88.009523, 44.631711],
          [-88.009486, 44.630897],
          [-88.009472, 44.630594],
          [-88.009463, 44.630398],
          [-88.008717, 44.628932],
          [-88.007489, 44.626521],
          [-88.007158, 44.625871],
          [-88.007013, 44.625586],
          [-88.006871, 44.625306],
          [-88.006409, 44.624399],
          [-88.001172, 44.614112],
          [-87.998836, 44.609523],
          [-87.998716, 44.609288],
          [-88.001943, 44.603909],
          [-88.012395, 44.602438],
          [-88.014735, 44.5964],
          [-88.015092, 44.595478],
          [-88.016373, 44.592172],
          [-88.016404, 44.592092],
          [-88.019465, 44.588344],
          [-88.022853, 44.584196],
          [-88.027103, 44.578992],
          [-88.028148, 44.578736],
          [-88.034535, 44.577175],
          [-88.036103, 44.576792],
          [-88.036104, 44.576791],
          [-88.039092, 44.574324],
          [-88.039489, 44.573995],
          [-88.041162, 44.572614],
          [-88.041202, 44.572581],
          [-88.041358, 44.571809],
          [-88.041485, 44.571183],
          [-88.041561, 44.570804],
          [-88.041659, 44.57032],
          [-88.041775, 44.569746],
          [-88.042261, 44.567344],
          [-88.042414, 44.566589],
          [-88.037178, 44.562705],
          [-88.020707, 44.550484],
          [-88.016709, 44.547518],
          [-88.005518, 44.539216],
          [-88.000099, 44.537827],
          [-87.999002, 44.537546],
          [-87.998536, 44.537426],
          [-87.990985, 44.535491],
          [-87.990468, 44.535358],
          [-87.986059, 44.534228],
          [-87.982821, 44.533398],
          [-87.977901, 44.532137],
          [-87.970702, 44.530292],
          [-87.943801, 44.529693],
          [-87.939972, 44.531323],
          [-87.935707, 44.533138],
          [-87.929001, 44.535993],
          [-87.924044, 44.540991],
          [-87.917467, 44.547622],
          [-87.917, 44.548093],
          [-87.915846, 44.549612],
          [-87.901206, 44.568887],
          [-87.901178, 44.568924],
          [-87.901177, 44.568925],
          [-87.900668, 44.570084],
          [-87.899368, 44.573043],
          [-87.898907, 44.574091],
          [-87.898888, 44.574135],
          [-87.899183, 44.574576],
          [-87.900198, 44.576095],
          [-87.900634, 44.576747],
          [-87.901061, 44.577386],
          [-87.901236, 44.577648],
          [-87.903689, 44.581317],
          [-87.902319, 44.583079],
          [-87.901179, 44.584545],
          [-87.896412, 44.58678],
          [-87.894356, 44.587745],
          [-87.891727, 44.588977],
          [-87.891717, 44.588982],
          [-87.890868, 44.589647],
          [-87.88741, 44.592356],
          [-87.867941, 44.607606],
          [-87.866884, 44.608434],
          [-87.842164, 44.618826],
          [-87.837416, 44.620822],
          [-87.836101, 44.621375],
          [-87.830848, 44.623583],
          [-87.829229, 44.624521],
          [-87.823444, 44.62787],
          [-87.820083, 44.629816],
          [-87.809076, 44.636189],
          [-87.808819, 44.636338],
          [-87.808366, 44.636378],
          [-87.78824, 44.638137],
          [-87.77516, 44.639281],
          [-87.765774, 44.642023],
          [-87.762912, 44.64411],
          [-87.762376, 44.644501],
          [-87.766194, 44.327185],
          [-87.887526, 44.327589],
          [-87.888087, 44.240461],
          [-88.04324, 44.241018],
          [-88.192611, 44.241947],
          [-88.190603, 44.586791],
          [-88.245204, 44.584746],
          [-88.242687, 44.679631],
          [-87.989912, 44.677587]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49047",
      state: "49",
      county: "047",
      name: "Uintah County",
      censusArea: 4479.693,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-110.02414, 39.461695],
          [-110.024828, 39.46756],
          [-110.024118, 39.468951],
          [-110.037952, 39.566667],
          [-109.993741, 39.70278],
          [-109.884708, 39.805742],
          [-109.967601, 39.805979],
          [-109.973684, 40.039869],
          [-109.977505, 40.809828],
          [-109.870207, 40.763829],
          [-109.525495, 40.82637],
          [-109.480252, 40.739929],
          [-109.394368, 40.790881],
          [-109.394481, 40.858073],
          [-109.203253, 40.858641],
          [-109.202356, 40.785814],
          [-109.163584, 40.78589],
          [-109.161063, 40.683817],
          [-109.048296, 40.662602],
          [-109.048249, 40.653601],
          [-109.050074, 40.540358],
          [-109.049955, 40.539901],
          [-109.050854, 40.222662],
          [-109.050973, 40.180849],
          [-109.050944, 40.180712],
          [-109.050813, 40.059579],
          [-109.050873, 40.058915],
          [-109.051221, 39.660472],
          [-109.051363, 39.497674],
          [-109.106972, 39.461979],
          [-110.02414, 39.461695]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US49053",
      state: "49",
      county: "053",
      name: "Washington County",
      censusArea: 2426.358,
      stateName: "Utah"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-112.899366, 37.000319],
          [-112.966471, 37.000219],
          [-113.965907, 36.999976],
          [-113.965907, 37.000025],
          [-114.0506, 37.000396],
          [-114.051749, 37.088434],
          [-114.051822, 37.090976],
          [-114.051974, 37.283848],
          [-114.051974, 37.284511],
          [-114.0518, 37.293044],
          [-114.0518, 37.293548],
          [-114.051927, 37.370459],
          [-114.051927, 37.370734],
          [-114.052701, 37.492014],
          [-114.052685, 37.502513],
          [-114.052718, 37.517264],
          [-114.052689, 37.517859],
          [-114.052472, 37.604776],
          [-113.474991, 37.618276],
          [-113.474389, 37.528978],
          [-113.253184, 37.528879],
          [-113.253677, 37.482241],
          [-113.146281, 37.474079],
          [-113.03688, 37.474578],
          [-113.03698, 37.498577],
          [-112.900994, 37.499738],
          [-112.899366, 37.000319]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50001",
      state: "50",
      county: "001",
      name: "Addison County",
      censusArea: 766.325,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.958853, 43.826542],
          [-72.989434, 43.814611],
          [-73.016831, 43.804953],
          [-73.026833, 43.842113],
          [-73.032082, 43.844718],
          [-73.029938, 43.846716],
          [-73.156173, 43.842314],
          [-73.155743, 43.83966],
          [-73.231377, 43.764469],
          [-73.360711, 43.753268],
          [-73.350431, 43.771438],
          [-73.357547, 43.785933],
          [-73.379279, 43.808391],
          [-73.379312, 43.808478],
          [-73.388389, 43.832404],
          [-73.397256, 43.905668],
          [-73.407742, 43.929887],
          [-73.405525, 43.948813],
          [-73.405999, 44.016229],
          [-73.410776, 44.026944],
          [-73.43688, 44.042578],
          [-73.437429, 44.046861],
          [-73.429239, 44.079414],
          [-73.403686, 44.153102],
          [-73.390583, 44.190886],
          [-73.350806, 44.225943],
          [-73.324681, 44.243614],
          [-73.323268, 44.264796],
          [-72.973318, 44.29342],
          [-72.952167, 44.161271],
          [-72.916778, 44.071886],
          [-72.742303, 44.029655],
          [-72.75396, 44.012219],
          [-72.791336, 43.961855],
          [-72.980158, 43.882587],
          [-72.958853, 43.826542]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50009",
      state: "50",
      county: "009",
      name: "Essex County",
      censusArea: 663.598,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-71.939936, 44.769032],
          [-71.974318, 44.786519],
          [-71.874433, 44.876382],
          [-71.92851, 44.907298],
          [-71.897622, 45.009172],
          [-71.767452, 45.011437],
          [-71.502487, 45.013367],
          [-71.464555, 45.013637],
          [-71.468567, 45.010222],
          [-71.486457, 45.007011],
          [-71.505, 45.008151],
          [-71.530091, 44.999656],
          [-71.53698, 44.994177],
          [-71.538592, 44.988182],
          [-71.537784, 44.984298],
          [-71.516814, 44.947588],
          [-71.493985, 44.911312],
          [-71.495844, 44.90498],
          [-71.57252, 44.810074],
          [-71.623924, 44.755135],
          [-71.627358, 44.747544],
          [-71.625323, 44.729718],
          [-71.598656, 44.698005],
          [-71.553156, 44.626645],
          [-71.536251, 44.587939],
          [-71.580263, 44.506509],
          [-71.59948, 44.486455],
          [-71.67995, 44.427908],
          [-71.708934, 44.411611],
          [-71.745011, 44.401359],
          [-71.756091, 44.406401],
          [-71.761966, 44.407027],
          [-71.764537, 44.406403],
          [-71.793924, 44.399271],
          [-71.814388, 44.381932],
          [-71.816065, 44.366817],
          [-71.849465, 44.359295],
          [-71.933288, 44.441174],
          [-71.856637, 44.496178],
          [-71.908657, 44.54708],
          [-71.842732, 44.611165],
          [-71.910282, 44.647038],
          [-71.837294, 44.716637],
          [-71.939936, 44.769032]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50017",
      state: "50",
      county: "017",
      name: "Orange County",
      censusArea: 687.033,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.460132, 43.838291],
          [-72.49903, 43.850224],
          [-72.499473, 43.850398],
          [-72.574443, 43.873348],
          [-72.783208, 43.928967],
          [-72.791336, 43.961855],
          [-72.75396, 44.012219],
          [-72.742303, 44.029655],
          [-72.683772, 44.012938],
          [-72.634789, 44.08945],
          [-72.62891, 44.088639],
          [-72.589333, 44.160282],
          [-72.443032, 44.128669],
          [-72.419747, 44.220979],
          [-72.368353, 44.204057],
          [-72.30498, 44.183149],
          [-72.17026, 44.192053],
          [-72.042087, 44.157633],
          [-72.040082, 44.155749],
          [-72.040082, 44.155748],
          [-72.054675, 44.112147],
          [-72.09071, 44.035211],
          [-72.112813, 43.98802],
          [-72.113078, 43.97279],
          [-72.107042, 43.969513],
          [-72.105594, 43.959029],
          [-72.104421, 43.950536],
          [-72.121002, 43.918956],
          [-72.167224, 43.886113],
          [-72.182203, 43.834032],
          [-72.183743, 43.806644],
          [-72.204792, 43.771926],
          [-72.205717, 43.771143],
          [-72.207259, 43.771812],
          [-72.460132, 43.838291]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50023",
      state: "50",
      county: "023",
      name: "Washington County",
      censusArea: 687.233,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.826112, 44.35919],
          [-72.804995, 44.451676],
          [-72.765856, 44.442595],
          [-72.654398, 44.398224],
          [-72.596807, 44.479292],
          [-72.490548, 44.425693],
          [-72.434315, 44.506098],
          [-72.223689, 44.424573],
          [-72.368353, 44.204057],
          [-72.419747, 44.220979],
          [-72.443032, 44.128669],
          [-72.589333, 44.160282],
          [-72.62891, 44.088639],
          [-72.634789, 44.08945],
          [-72.683772, 44.012938],
          [-72.742303, 44.029655],
          [-72.916778, 44.071886],
          [-72.952167, 44.161271],
          [-72.89598, 44.276284],
          [-72.911398, 44.281769],
          [-72.855592, 44.365551],
          [-72.826112, 44.35919]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22003",
      state: "22",
      county: "003",
      name: "Allen County",
      lsad: "Parish",
      censusArea: 761.847,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.597402, 30.583928],
          [-92.630347, 30.487873],
          [-92.722339, 30.488208],
          [-92.775554, 30.437656],
          [-93.065543, 30.438569],
          [-93.131155, 30.423612],
          [-93.130294, 30.597886],
          [-92.97917, 30.59834],
          [-92.976886, 30.77404],
          [-92.978453, 30.878175],
          [-92.823932, 30.89],
          [-92.597741, 30.896101],
          [-92.597402, 30.583928]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22009",
      state: "22",
      county: "009",
      name: "Avoyelles County",
      lsad: "Parish",
      censusArea: 832.434,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.815258, 30.848107],
          [-92.212711, 30.848607],
          [-92.280738, 30.965071],
          [-92.208299, 30.96243],
          [-92.234258, 31.131466],
          [-92.23323, 31.321545],
          [-92.086718, 31.335374],
          [-92.008121, 31.325797],
          [-91.994828, 31.229561],
          [-91.926854, 31.29483],
          [-91.83428, 31.266344],
          [-91.677302, 31.188047],
          [-91.750119, 31.018814],
          [-91.805963, 30.972558],
          [-91.815258, 30.848107]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22101",
      state: "22",
      county: "101",
      name: "St. Mary County",
      lsad: "Parish",
      censusArea: 555.379,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.082602, 29.626368],
          [-91.178048, 29.609703],
          [-91.260661, 29.54451],
          [-91.265649, 29.472362],
          [-91.2813, 29.481547],
          [-91.294325, 29.476894],
          [-91.335742, 29.485886],
          [-91.343567, 29.492593],
          [-91.34588, 29.504538],
          [-91.356625, 29.515191],
          [-91.402214, 29.511914],
          [-91.420449, 29.515745],
          [-91.42713, 29.520215],
          [-91.432337, 29.53283],
          [-91.439941, 29.540434],
          [-91.447345, 29.544749],
          [-91.468748, 29.544299],
          [-91.517274, 29.52974],
          [-91.531021, 29.531543],
          [-91.531471, 29.535374],
          [-91.52584, 29.545946],
          [-91.525523, 29.551904],
          [-91.529217, 29.558598],
          [-91.537445, 29.565888],
          [-91.541974, 29.594353],
          [-91.553537, 29.632766],
          [-91.560908, 29.63735],
          [-91.570589, 29.638312],
          [-91.581843, 29.637165],
          [-91.600179, 29.631156],
          [-91.625114, 29.626195],
          [-91.643832, 29.630625],
          [-91.648941, 29.633635],
          [-91.648657, 29.636713],
          [-91.646478, 29.639427],
          [-91.643198, 29.640274],
          [-91.637344, 29.647217],
          [-91.627286, 29.662132],
          [-91.625114, 29.671679],
          [-91.626826, 29.684753],
          [-91.623829, 29.69924],
          [-91.618479, 29.710816],
          [-91.61809, 29.720694],
          [-91.621512, 29.735429],
          [-91.632829, 29.742576],
          [-91.667128, 29.745822],
          [-91.710935, 29.738993],
          [-91.737253, 29.74937],
          [-91.752259, 29.748264],
          [-91.783674, 29.740689],
          [-91.830499, 29.718918],
          [-91.845962, 29.708763],
          [-91.85864, 29.703121],
          [-91.866516, 29.70715],
          [-91.88075, 29.710839],
          [-91.880999, 29.713338],
          [-91.878331, 29.716087],
          [-91.875637, 29.722316],
          [-91.711149, 29.846065],
          [-91.624709, 29.945275],
          [-91.471951, 29.955989],
          [-91.373642, 29.834401],
          [-91.386491, 29.783681],
          [-91.215195, 29.751211],
          [-91.10001, 29.699402],
          [-91.104912, 29.642496],
          [-91.082602, 29.626368]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22107",
      state: "22",
      county: "107",
      name: "Tensas County",
      lsad: "Parish",
      censusArea: 602.784,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.027946, 32.112722],
          [-91.034707, 32.101053],
          [-91.128009, 31.985857],
          [-91.248144, 31.869848],
          [-91.293413, 31.86016],
          [-91.326914, 31.854961],
          [-91.338414, 31.851261],
          [-91.345714, 31.842861],
          [-91.359514, 31.799362],
          [-91.363714, 31.780363],
          [-91.365614, 31.760763],
          [-91.365084, 31.752743],
          [-91.369375, 31.746903],
          [-91.379935, 31.733013],
          [-91.544791, 31.751731],
          [-91.575721, 31.88256],
          [-91.489761, 32.092422],
          [-91.492718, 32.203349],
          [-91.320742, 32.202455],
          [-91.13378, 32.249339],
          [-91.122602, 32.216929],
          [-91.133587, 32.213432],
          [-91.158026, 32.201956],
          [-91.164171, 32.196888],
          [-91.171046, 32.176526],
          [-91.174552, 32.154978],
          [-91.171702, 32.14425],
          [-91.165452, 32.13429],
          [-91.162822, 32.132694],
          [-91.131403, 32.126213],
          [-91.067004, 32.132144],
          [-91.057487, 32.135515],
          [-91.052342, 32.137337],
          [-91.046615, 32.138768],
          [-91.040531, 32.137337],
          [-91.034447, 32.134832],
          [-91.030152, 32.129821],
          [-91.026931, 32.123737],
          [-91.027125, 32.120434],
          [-91.027535, 32.113431],
          [-91.027946, 32.112722]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22109",
      state: "22",
      county: "109",
      name: "Terrebonne County",
      lsad: "Parish",
      censusArea: 1231.815,
      stateName: "Louisiana"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-91.006743, 29.714771],
          [-90.880194, 29.716809],
          [-90.807692, 29.775908],
          [-90.766849, 29.695477],
          [-90.574611, 29.559141],
          [-90.384285, 29.365755],
          [-90.376666, 29.302883],
          [-90.409416, 29.196135],
          [-90.432912, 29.188132],
          [-90.435907, 29.188449],
          [-90.443954, 29.19583],
          [-90.472489, 29.192688],
          [-90.473273, 29.195224],
          [-90.468773, 29.198469],
          [-90.467233, 29.202549],
          [-90.485786, 29.209843],
          [-90.494928, 29.216713],
          [-90.490987, 29.220883],
          [-90.46832, 29.227532],
          [-90.465764, 29.242951],
          [-90.462866, 29.249809],
          [-90.450674, 29.263739],
          [-90.452186, 29.26625],
          [-90.472779, 29.272556],
          [-90.495299, 29.287277],
          [-90.510555, 29.290925],
          [-90.517277, 29.282719],
          [-90.526216, 29.276492],
          [-90.552005, 29.278512],
          [-90.565436, 29.285111],
          [-90.582525, 29.276037],
          [-90.589724, 29.248521],
          [-90.58847, 29.245863],
          [-90.583924, 29.242886],
          [-90.576506, 29.243986],
          [-90.565378, 29.242475],
          [-90.544547, 29.230683],
          [-90.543245, 29.227843],
          [-90.544311, 29.224292],
          [-90.556501, 29.219913],
          [-90.55739, 29.207881],
          [-90.560889, 29.204261],
          [-90.575277, 29.206827],
          [-90.618413, 29.20329],
          [-90.624161, 29.210366],
          [-90.62742, 29.211004],
          [-90.633819, 29.209128],
          [-90.640223, 29.196554],
          [-90.645612, 29.175867],
          [-90.645169, 29.172958],
          [-90.640863, 29.171261],
          [-90.636973, 29.164572],
          [-90.647042, 29.12858],
          [-90.677724, 29.118742],
          [-90.691109, 29.121722],
          [-90.700893, 29.12147],
          [-90.718035, 29.116611],
          [-90.731239, 29.122886],
          [-90.764189, 29.113374],
          [-90.773458, 29.100133],
          [-90.799444, 29.087377],
          [-90.802053, 29.083322],
          [-90.803699, 29.063709],
          [-90.79872, 29.054841],
          [-90.781981, 29.049431],
          [-90.765188, 29.049403],
          [-90.750092, 29.053247],
          [-90.7253, 29.066616],
          [-90.709105, 29.064305],
          [-90.70535, 29.062679],
          [-90.702102, 29.060275],
          [-90.692205, 29.059607],
          [-90.683645, 29.060944],
          [-90.676958, 29.063619],
          [-90.665589, 29.06723],
          [-90.652348, 29.069237],
          [-90.644189, 29.07151],
          [-90.641247, 29.072313],
          [-90.63924, 29.072848],
          [-90.637623, 29.072084],
          [-90.636033, 29.069792],
          [-90.637495, 29.066608],
          [-90.648058, 29.062649],
          [-90.730899, 29.042259],
          [-90.755677, 29.038997],
          [-90.79768, 29.039741],
          [-90.811473, 29.03658],
          [-90.839345, 29.039167],
          [-90.842762, 29.042947],
          [-90.844849, 29.048721],
          [-90.841226, 29.054266],
          [-90.844593, 29.06728],
          [-90.862757, 29.094863],
          [-90.867766, 29.095434],
          [-90.877583, 29.104891],
          [-90.885351, 29.117016],
          [-90.898215, 29.131342],
          [-90.925797, 29.153116],
          [-90.941877, 29.162373],
          [-90.948091, 29.174104],
          [-90.961278, 29.180817],
          [-90.981458, 29.171211],
          [-91.000096, 29.169481],
          [-91.023955, 29.174784],
          [-91.031786, 29.182188],
          [-91.05863, 29.181734],
          [-91.094015, 29.187711],
          [-91.11476, 29.207918],
          [-91.129141, 29.215863],
          [-91.199647, 29.221287],
          [-91.219032, 29.226051],
          [-91.278792, 29.247776],
          [-91.302677, 29.265958],
          [-91.334885, 29.298775],
          [-91.33275, 29.305816],
          [-91.309314, 29.305698],
          [-91.299054, 29.309017],
          [-91.291821, 29.311357],
          [-91.29042, 29.313062],
          [-91.279742, 29.326058],
          [-91.276647, 29.329825],
          [-91.276187, 29.332783],
          [-91.274308, 29.344878],
          [-91.270582, 29.355415],
          [-91.270053, 29.356912],
          [-91.26994, 29.357231],
          [-91.266589, 29.361218],
          [-91.265479, 29.361767],
          [-91.251546, 29.368659],
          [-91.251232, 29.368814],
          [-91.249517, 29.369662],
          [-91.249109, 29.369864],
          [-91.245558, 29.37058],
          [-91.238515, 29.371999],
          [-91.235348, 29.370638],
          [-91.222377, 29.360703],
          [-91.207299, 29.360703],
          [-91.197465, 29.369882],
          [-91.200087, 29.38955],
          [-91.218463, 29.407235],
          [-91.215976, 29.412505],
          [-91.214284, 29.416089],
          [-91.211999, 29.420931],
          [-91.2151, 29.427542],
          [-91.217448, 29.432549],
          [-91.218067, 29.433193],
          [-91.219242, 29.434418],
          [-91.221166, 29.436421],
          [-91.24164, 29.441021],
          [-91.251319, 29.444483],
          [-91.258226, 29.446954],
          [-91.259537, 29.458001],
          [-91.259844, 29.460582],
          [-91.259988, 29.461803],
          [-91.260024, 29.462102],
          [-91.261588, 29.464954],
          [-91.265649, 29.472362],
          [-91.260661, 29.54451],
          [-91.178048, 29.609703],
          [-91.082602, 29.626368],
          [-91.006743, 29.714771]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21021",
      state: "21",
      county: "021",
      name: "Boyle County",
      censusArea: 180.169,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.658296, 37.634501],
          [-84.801112, 37.568776],
          [-84.847326, 37.547894],
          [-85.039674, 37.545232],
          [-85.030172, 37.63127],
          [-85.025021, 37.678854],
          [-84.744888, 37.713075],
          [-84.658296, 37.634501]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21023",
      state: "21",
      county: "023",
      name: "Bracken County",
      censusArea: 205.612,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.044486, 38.770572],
          [-83.978814, 38.787104],
          [-83.962123, 38.787384],
          [-83.943978, 38.783616],
          [-83.903971, 38.76816],
          [-83.903918, 38.76815],
          [-83.991605, 38.593701],
          [-84.162031, 38.554125],
          [-84.211319, 38.550864],
          [-84.205346, 38.582039],
          [-84.229954, 38.82765],
          [-84.2253, 38.817665],
          [-84.212904, 38.805707],
          [-84.205592, 38.802588],
          [-84.071491, 38.770475],
          [-84.052707, 38.770542],
          [-84.044486, 38.770572]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US22045",
      state: "22",
      county: "045",
      name: "Iberia County",
      lsad: "Parish",
      censusArea: 574.111,
      stateName: "Louisiana"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-92.000371, 29.613143],
            [-92.000003, 29.613013],
            [-91.965031, 29.608019],
            [-91.939903, 29.610291],
            [-91.935024, 29.612239],
            [-91.929567, 29.61884],
            [-91.922825, 29.633173],
            [-91.898996, 29.63701],
            [-91.896763, 29.634618],
            [-91.882318, 29.62977],
            [-91.866113, 29.631583],
            [-91.863018, 29.633739],
            [-91.841294, 29.62962],
            [-91.838981, 29.624475],
            [-91.840921, 29.619913],
            [-91.838297, 29.616041],
            [-91.821693, 29.606049],
            [-91.803831, 29.599562],
            [-91.784976, 29.595204],
            [-91.7785, 29.58922],
            [-91.774805, 29.582113],
            [-91.774686, 29.576387],
            [-91.74632, 29.574337],
            [-91.719102, 29.565568],
            [-91.715642, 29.565844],
            [-91.712002, 29.56474],
            [-91.709205, 29.561012],
            [-91.711654, 29.55427],
            [-91.733956, 29.539504],
            [-91.747058, 29.535144],
            [-91.765448, 29.520844],
            [-91.771927, 29.504871],
            [-91.772529, 29.499016],
            [-91.770069, 29.493812],
            [-91.770516, 29.488953],
            [-91.782387, 29.482882],
            [-91.789119, 29.482081],
            [-91.800121, 29.486828],
            [-91.803448, 29.486851],
            [-91.814609, 29.482061],
            [-91.821576, 29.473925],
            [-91.8385, 29.478874],
            [-91.848665, 29.484144],
            [-91.852598, 29.494984],
            [-91.862324, 29.502393],
            [-91.878746, 29.502937],
            [-91.886815, 29.505577],
            [-91.906175, 29.518052],
            [-91.915322, 29.518513],
            [-91.947007, 29.53316],
            [-91.969312, 29.536893],
            [-91.985726, 29.547708],
            [-92.02681, 29.566805],
            [-92.035462, 29.57864],
            [-92.041168, 29.581648],
            [-92.038618, 29.585048],
            [-92.02915, 29.584432],
            [-92.025388, 29.585386],
            [-92.019499, 29.592973],
            [-92.009765, 29.598395],
            [-92.006448, 29.608158],
            [-92.000371, 29.613143]
          ]
        ],
        [
          [
            [-91.368835, 30.058824],
            [-91.224356, 30.025336],
            [-91.235889, 30.001128],
            [-91.259388, 30.0002],
            [-91.255229, 29.971457],
            [-91.471951, 29.955989],
            [-91.624709, 29.945275],
            [-91.711149, 29.846065],
            [-91.875637, 29.722316],
            [-91.87557, 29.722471],
            [-91.87557, 29.728043],
            [-91.878355, 29.735007],
            [-91.879748, 29.742668],
            [-91.878355, 29.751025],
            [-91.874761, 29.760083],
            [-91.859151, 29.783331],
            [-91.854677, 29.807436],
            [-91.869998, 29.828328],
            [-91.889118, 29.836023],
            [-91.90689, 29.83094],
            [-91.915989, 29.815654],
            [-91.919143, 29.815379],
            [-91.940723, 29.817008],
            [-91.96123, 29.810221],
            [-91.970443, 29.80431],
            [-91.990396, 29.970326],
            [-91.964458, 30.036918],
            [-91.963827, 30.038175],
            [-91.96279, 30.04137],
            [-91.950998, 30.073213],
            [-91.819216, 30.044013],
            [-91.765168, 30.108495],
            [-91.658886, 30.108226],
            [-91.600354, 30.033169],
            [-91.368835, 30.058824]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21047",
      state: "21",
      county: "047",
      name: "Christian County",
      censusArea: 717.503,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.344131, 36.64151],
          [-87.347796, 36.64144],
          [-87.414309, 36.641047],
          [-87.425009, 36.641047],
          [-87.436509, 36.640747],
          [-87.563052, 36.639113],
          [-87.564928, 36.639113],
          [-87.641146, 36.638036],
          [-87.64115, 36.638036],
          [-87.694186, 36.636838],
          [-87.659724, 36.966397],
          [-87.733368, 37.002103],
          [-87.710912, 37.021607],
          [-87.680617, 37.149239],
          [-87.521609, 37.105344],
          [-87.333741, 37.157186],
          [-87.259371, 37.072401],
          [-87.33598, 36.641543],
          [-87.344131, 36.64151]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21055",
      state: "21",
      county: "055",
      name: "Crittenden County",
      censusArea: 359.952,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.801046, 37.379445],
          [-88.089074, 37.222388],
          [-88.095781, 37.179684],
          [-88.193519, 37.147256],
          [-88.21898, 37.273145],
          [-88.358436, 37.40486],
          [-88.330622, 37.429316],
          [-88.312585, 37.440591],
          [-88.281667, 37.452596],
          [-88.255193, 37.456748],
          [-88.083395, 37.473683],
          [-88.062562, 37.489385],
          [-88.061292, 37.505232],
          [-88.061311, 37.505332],
          [-88.015572, 37.54721],
          [-87.932952, 37.480052],
          [-87.903034, 37.398197],
          [-87.801046, 37.379445]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21059",
      state: "21",
      county: "059",
      name: "Daviess County",
      censusArea: 458.346,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.408547, 37.683563],
          [-87.271608, 37.78012],
          [-87.323402, 37.819239],
          [-87.302324, 37.898445],
          [-87.269561, 37.877155],
          [-87.26293, 37.872846],
          [-87.20224, 37.843791],
          [-87.132621, 37.791008],
          [-87.128749, 37.785728],
          [-87.119229, 37.782848],
          [-87.111133, 37.782512],
          [-87.090636, 37.787808],
          [-87.077404, 37.796209],
          [-87.067836, 37.806065],
          [-87.057836, 37.827457],
          [-87.043854, 37.870796],
          [-87.043407, 37.87994],
          [-87.046237, 37.889866],
          [-87.042249, 37.898291],
          [-87.033444, 37.906593],
          [-87.010315, 37.919668],
          [-86.978957, 37.9302],
          [-86.97883, 37.930214],
          [-86.854035, 37.840811],
          [-86.823429, 37.737754],
          [-86.817514, 37.673814],
          [-86.94876, 37.630548],
          [-87.03826, 37.560803],
          [-87.26161, 37.622009],
          [-87.408547, 37.683563]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21073",
      state: "21",
      county: "073",
      name: "Franklin County",
      censusArea: 207.747,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-84.740594, 38.352415],
          [-84.739684, 38.338035],
          [-84.730289, 38.208302],
          [-84.725168, 38.195426],
          [-84.864908, 38.116926],
          [-85.023711, 38.129052],
          [-84.997667, 38.335586],
          [-84.870482, 38.356751],
          [-84.740594, 38.352415]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21075",
      state: "21",
      county: "075",
      name: "Fulton County",
      censusArea: 205.504,
      stateName: "Kentucky"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-89.5391, 36.498201],
            [-89.560344, 36.525436],
            [-89.570071, 36.544387],
            [-89.571509, 36.552569],
            [-89.566817, 36.564216],
            [-89.55264, 36.577178],
            [-89.546113, 36.579989],
            [-89.527583, 36.581147],
            [-89.500076, 36.576305],
            [-89.484836, 36.571821],
            [-89.479093, 36.568206],
            [-89.473341, 36.559918],
            [-89.467761, 36.546847],
            [-89.465445, 36.536163],
            [-89.465888, 36.529946],
            [-89.47246, 36.513741],
            [-89.485106, 36.497692],
            [-89.492537, 36.497775],
            [-89.498036, 36.497887],
            [-89.5391, 36.498201]
          ]
        ],
        [
          [
            [-88.834866, 36.502911],
            [-88.874725, 36.502446],
            [-88.964471, 36.502191],
            [-89.000063, 36.502633],
            [-89.006825, 36.502684],
            [-89.010439, 36.50271],
            [-89.034649, 36.502964],
            [-89.058871, 36.503157],
            [-89.072118, 36.503249],
            [-89.090146, 36.503392],
            [-89.117537, 36.503603],
            [-89.119805, 36.503647],
            [-89.163224, 36.504522],
            [-89.163429, 36.504526],
            [-89.211409, 36.50563],
            [-89.279091, 36.506511],
            [-89.282298, 36.506782],
            [-89.346053, 36.50321],
            [-89.346056, 36.50321],
            [-89.356593, 36.502195],
            [-89.380085, 36.500416],
            [-89.381792, 36.500062],
            [-89.403913, 36.499141],
            [-89.417293, 36.499033],
            [-89.400581, 36.538337],
            [-89.38814, 36.573416],
            [-89.376367, 36.613868],
            [-89.365548, 36.625059],
            [-89.343753, 36.630991],
            [-89.327578, 36.632127],
            [-89.326731, 36.632186],
            [-89.313405, 36.62012],
            [-89.294637, 36.593729],
            [-89.27171, 36.571387],
            [-89.259994, 36.565149],
            [-89.236542, 36.566824],
            [-89.227319, 36.569375],
            [-89.213563, 36.580119],
            [-89.202607, 36.601576],
            [-89.200902, 36.618177],
            [-89.197654, 36.628936],
            [-89.187749, 36.641115],
            [-89.174741, 36.650416],
            [-89.174733, 36.650476],
            [-89.026629, 36.592124],
            [-88.90348, 36.576864],
            [-88.834866, 36.502911]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21089",
      state: "21",
      county: "089",
      name: "Greenup County",
      censusArea: 344.397,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-83.030889, 38.72556],
          [-83.030702, 38.72572],
          [-83.011816, 38.730057],
          [-82.979395, 38.725976],
          [-82.968695, 38.728776],
          [-82.943147, 38.74328],
          [-82.923694, 38.750076],
          [-82.894193, 38.756576],
          [-82.889193, 38.756076],
          [-82.879492, 38.751476],
          [-82.875492, 38.747276],
          [-82.874466, 38.745346],
          [-82.871292, 38.739376],
          [-82.869992, 38.730477],
          [-82.870392, 38.722077],
          [-82.873492, 38.710077],
          [-82.869592, 38.678177],
          [-82.853554, 38.610926],
          [-82.847186, 38.595166],
          [-82.839538, 38.586159],
          [-82.820161, 38.572703],
          [-82.816012, 38.570733],
          [-82.800112, 38.563183],
          [-82.789776, 38.559951],
          [-82.730958, 38.559264],
          [-82.724846, 38.5576],
          [-82.696621, 38.542112],
          [-82.690509, 38.536576],
          [-82.665548, 38.505808],
          [-82.665485, 38.505734],
          [-82.817454, 38.373935],
          [-82.97859, 38.398857],
          [-83.051751, 38.465404],
          [-83.166419, 38.503965],
          [-83.071005, 38.5952],
          [-83.030889, 38.72556]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US21095",
      state: "21",
      county: "095",
      name: "Harlan County",
      censusArea: 465.828,
      stateName: "Kentucky"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-82.869183, 36.974183],
          [-82.869183, 36.974182],
          [-82.870274, 36.965993],
          [-82.87023, 36.965498],
          [-82.867358, 36.963182],
          [-82.865404, 36.958084],
          [-82.864211, 36.957983],
          [-82.862866, 36.957765],
          [-82.860534, 36.956015],
          [-82.858443, 36.954036],
          [-82.855705, 36.953808],
          [-82.856099, 36.952471],
          [-82.860633, 36.94584],
          [-82.861282, 36.944848],
          [-82.861684, 36.939316],
          [-82.860537, 36.937439],
          [-82.858784, 36.933065],
          [-82.858461, 36.932717],
          [-82.861943, 36.924236],
          [-82.863468, 36.922308],
          [-82.872136, 36.913456],
          [-82.873777, 36.912299],
          [-82.885618, 36.900415],
          [-82.91169, 36.874248],
          [-82.911824, 36.874243],
          [-82.973395, 36.859097],
          [-83.07259, 36.854589],
          [-83.099792, 36.824889],
          [-83.131694, 36.781488],
          [-83.131245, 36.767105],
          [-83.125655, 36.761407],
          [-83.125728, 36.761276],
          [-83.127833, 36.750828],
          [-83.136395, 36.743088],
          [-83.167396, 36.739187],
          [-83.199698, 36.737487],
          [-83.342804, 36.701286],
          [-83.353613, 36.696699],
          [-83.354606, 36.696153],
          [-83.460808, 36.664885],
          [-83.461013, 36.664916],
          [-83.511729, 36.807362],
          [-83.489813, 36.895413],
          [-83.26002, 36.968951],
          [-83.182104, 37.021115],
          [-83.171706, 37.017192],
          [-83.160825, 37.020739],
          [-83.160106, 37.013859],
          [-83.144352, 37.013705],
          [-83.136701, 37.00415],
          [-83.124334, 37.005722],
          [-83.12209, 37.000997],
          [-83.150402, 36.954163],
          [-82.939466, 37.01552],
          [-82.869183, 36.974183]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US55137",
      state: "55",
      county: "137",
      name: "Waushara County",
      censusArea: 626.153,
      stateName: "Wisconsin"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-88.886673, 44.242622],
          [-88.886193, 43.983233],
          [-89.168612, 43.982834],
          [-89.59795, 43.9821],
          [-89.59798, 44.245725],
          [-89.224892, 44.243546],
          [-88.886673, 44.242622]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56001",
      state: "56",
      county: "001",
      name: "Albany County",
      censusArea: 4273.84,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-105.28391, 42.431402],
          [-105.27375, 42.088824],
          [-105.278236, 41.656655],
          [-105.27686, 40.998173],
          [-105.277138, 40.998173],
          [-105.724804, 40.99691],
          [-105.730421, 40.996886],
          [-106.061181, 40.996999],
          [-106.19055, 40.998056],
          [-106.321165, 40.999123],
          [-106.322885, 41.395141],
          [-106.068406, 41.395028],
          [-106.074922, 42.126407],
          [-106.073403, 42.433236],
          [-105.556042, 42.430583],
          [-105.613865, 42.304455],
          [-105.375541, 42.290139],
          [-105.332638, 42.431106],
          [-105.28391, 42.431402]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56007",
      state: "56",
      county: "007",
      name: "Carbon County",
      censusArea: 7897.578,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-107.000606, 41.003444],
          [-107.317794, 41.002957],
          [-107.918421, 41.002036],
          [-107.929736, 41.659596],
          [-107.50972, 41.657452],
          [-107.503821, 42.087957],
          [-107.522722, 42.261756],
          [-107.522923, 42.434356],
          [-106.654781, 42.431094],
          [-106.075852, 42.433218],
          [-106.073403, 42.433236],
          [-106.074922, 42.126407],
          [-106.068406, 41.395028],
          [-106.322885, 41.395141],
          [-106.321165, 40.999123],
          [-106.386356, 41.001144],
          [-106.391852, 41.001176],
          [-106.43095, 41.001752],
          [-106.437419, 41.001795],
          [-106.439563, 41.001978],
          [-106.453859, 41.002057],
          [-106.857772, 41.003082],
          [-107.000606, 41.003444]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56011",
      state: "56",
      county: "011",
      name: "Crook County",
      censusArea: 2854.408,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.05581, 44.691343],
          [-104.055877, 44.571016],
          [-104.055892, 44.543341],
          [-104.055927, 44.51773],
          [-104.054487, 44.180381],
          [-105.079281, 44.176182],
          [-105.082181, 44.176175],
          [-105.076607, 45.000347],
          [-105.038405, 45.000345],
          [-105.025266, 45.00029],
          [-105.019284, 45.000329],
          [-105.01824, 45.000437],
          [-104.765063, 44.999183],
          [-104.759855, 44.999066],
          [-104.665171, 44.998618],
          [-104.663882, 44.998869],
          [-104.470422, 44.998453],
          [-104.470117, 44.998453],
          [-104.057698, 44.997431],
          [-104.055963, 44.768236],
          [-104.055963, 44.767962],
          [-104.055934, 44.72372],
          [-104.05587, 44.723422],
          [-104.055777, 44.700466],
          [-104.055938, 44.693881],
          [-104.05581, 44.691343]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56013",
      state: "56",
      county: "013",
      name: "Fremont County",
      censusArea: 9183.814,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-109.043864, 42.263684],
          [-109.043801, 42.433644],
          [-109.069477, 42.433477],
          [-109.072851, 42.693514],
          [-109.31536, 42.856131],
          [-109.370658, 42.945728],
          [-109.552579, 43.012538],
          [-109.560451, 43.083538],
          [-109.651451, 43.167036],
          [-109.692716, 43.26503],
          [-109.694942, 43.366152],
          [-109.754462, 43.365905],
          [-109.754717, 43.4632],
          [-110.05157, 43.464783],
          [-110.053179, 44.008022],
          [-109.990073, 43.949066],
          [-109.858754, 43.957988],
          [-109.816179, 43.881974],
          [-109.843455, 43.819864],
          [-109.7899, 43.803252],
          [-109.665893, 43.845726],
          [-109.641808, 43.922785],
          [-109.542332, 43.961352],
          [-109.312949, 43.813293],
          [-109.171448, 43.699198],
          [-109.171638, 43.619764],
          [-108.934141, 43.619163],
          [-108.814276, 43.605255],
          [-108.814276, 43.575555],
          [-108.694672, 43.575755],
          [-108.695072, 43.532255],
          [-108.576167, 43.532556],
          [-108.576167, 43.503256],
          [-108.456962, 43.503156],
          [-108.456973, 43.474279],
          [-108.337861, 43.459765],
          [-107.595137, 43.472279],
          [-107.595105, 43.501397],
          [-107.534897, 43.501362],
          [-107.535032, 43.472759],
          [-107.517031, 43.472659],
          [-107.516728, 43.12796],
          [-107.502327, 43.12806],
          [-107.501425, 42.781458],
          [-107.543526, 42.781558],
          [-107.542025, 42.434356],
          [-107.522923, 42.434356],
          [-107.522722, 42.261756],
          [-108.375655, 42.263672],
          [-109.043864, 42.263684]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US56021",
      state: "56",
      county: "021",
      name: "Laramie County",
      censusArea: 2685.912,
      stateName: "Wyoming"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-104.653338, 41.653007],
          [-104.652992, 41.565014],
          [-104.05254, 41.564274],
          [-104.052531, 41.552723],
          [-104.052584, 41.55265],
          [-104.052692, 41.541154],
          [-104.052686, 41.539111],
          [-104.052476, 41.522343],
          [-104.052478, 41.515754],
          [-104.05234, 41.417865],
          [-104.05216, 41.407662],
          [-104.052287, 41.393307],
          [-104.052287, 41.393214],
          [-104.052324, 41.321144],
          [-104.052476, 41.320961],
          [-104.052568, 41.316202],
          [-104.052453, 41.278202],
          [-104.052574, 41.278019],
          [-104.052666, 41.275251],
          [-104.053142, 41.114457],
          [-104.053083, 41.104985],
          [-104.053025, 41.090274],
          [-104.053177, 41.089725],
          [-104.053097, 41.018045],
          [-104.053158, 41.016809],
          [-104.053249, 41.001406],
          [-104.066961, 41.001504],
          [-104.086068, 41.001563],
          [-104.10459, 41.001543],
          [-104.123586, 41.001626],
          [-104.211473, 41.001591],
          [-104.214191, 41.001568],
          [-104.214692, 41.001657],
          [-104.497058, 41.001805],
          [-104.497149, 41.001828],
          [-104.882452, 40.998186],
          [-104.943371, 40.99819],
          [-105.254779, 40.99821],
          [-105.256527, 40.998191],
          [-105.27686, 40.998173],
          [-105.278236, 41.656655],
          [-104.653338, 41.653007]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54031",
      state: "54",
      county: "031",
      name: "Hardy County",
      censusArea: 582.312,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.571901, 39.031995],
          [-78.565837, 39.026303],
          [-78.554222, 39.019672],
          [-78.557647, 39.013189],
          [-78.601655, 38.964603],
          [-78.710949, 38.910175],
          [-78.714135, 38.911176],
          [-78.716956, 38.916273],
          [-78.719755, 38.922135],
          [-78.719806, 38.922638],
          [-78.757278, 38.903203],
          [-78.779198, 38.892298],
          [-78.788031, 38.885123],
          [-78.808181, 38.856175],
          [-78.835191, 38.811499],
          [-78.869276, 38.762991],
          [-78.993997, 38.850102],
          [-78.999752, 38.846162],
          [-79.055354, 38.782213],
          [-79.057253, 38.761413],
          [-79.134296, 38.81334],
          [-79.045953, 38.92871],
          [-79.089655, 39.038208],
          [-78.979898, 39.237624],
          [-78.899695, 39.199137],
          [-78.508132, 39.08863],
          [-78.544111, 39.056676],
          [-78.571901, 39.031995]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54035",
      state: "54",
      county: "035",
      name: "Jackson County",
      censusArea: 464.348,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.522166, 38.612746],
          [-81.632836, 38.55437],
          [-81.696348, 38.626427],
          [-81.747683, 38.684134],
          [-81.772843, 38.680843],
          [-81.908645, 38.87846],
          [-81.874857, 38.881174],
          [-81.759995, 38.925828],
          [-81.756131, 38.933545],
          [-81.764253, 39.015279],
          [-81.772854, 39.026179],
          [-81.793304, 39.040353],
          [-81.803355, 39.047678],
          [-81.811655, 39.059578],
          [-81.814155, 39.073478],
          [-81.812355, 39.082078],
          [-81.747253, 39.095378],
          [-81.747253, 39.095379],
          [-81.581447, 39.026179],
          [-81.502628, 38.917922],
          [-81.546631, 38.672387],
          [-81.522166, 38.612746]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US54041",
      state: "54",
      county: "041",
      name: "Lewis County",
      censusArea: 384.895,
      stateName: "West Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.457332, 38.739169],
          [-80.446148, 38.777514],
          [-80.605799, 38.904167],
          [-80.716815, 39.00843],
          [-80.728326, 39.095679],
          [-80.596126, 39.167069],
          [-80.300209, 39.103857],
          [-80.317486, 38.958647],
          [-80.386989, 38.874808],
          [-80.393063, 38.727571],
          [-80.404748, 38.723439],
          [-80.426641, 38.731004],
          [-80.444673, 38.726729],
          [-80.457332, 38.739169]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US50027",
      state: "50",
      county: "027",
      name: "Windsor County",
      censusArea: 969.337,
      stateName: "Vermont"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-72.771543, 43.477862],
          [-72.79107, 43.495],
          [-72.700945, 43.682831],
          [-72.837188, 43.719878],
          [-72.874552, 43.858131],
          [-72.958853, 43.826542],
          [-72.980158, 43.882587],
          [-72.791336, 43.961855],
          [-72.783208, 43.928967],
          [-72.574443, 43.873348],
          [-72.499473, 43.850398],
          [-72.49903, 43.850224],
          [-72.460132, 43.838291],
          [-72.207259, 43.771812],
          [-72.205717, 43.771143],
          [-72.232713, 43.748286],
          [-72.264245, 43.734158],
          [-72.27118, 43.734138],
          [-72.302867, 43.702718],
          [-72.32966, 43.634648],
          [-72.334401, 43.61925],
          [-72.334745, 43.614519],
          [-72.32962, 43.600201],
          [-72.395825, 43.52056],
          [-72.387642, 43.502481],
          [-72.387852, 43.471567],
          [-72.396934, 43.42899],
          [-72.396948, 43.428925],
          [-72.405253, 43.389992],
          [-72.403949, 43.358098],
          [-72.400511, 43.337073],
          [-72.39715, 43.316515],
          [-72.431187, 43.231786],
          [-72.685637, 43.222495],
          [-72.819368, 43.255498],
          [-72.8199, 43.29921],
          [-72.867902, 43.300742],
          [-72.853439, 43.371076],
          [-72.759799, 43.354144],
          [-72.727184, 43.468717],
          [-72.771543, 43.477862]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51001",
      state: "51",
      county: "001",
      name: "Accomack County",
      censusArea: 449.496,
      stateName: "Virginia"
    },
    geometry: {
      type: "MultiPolygon",
      coordinates: [
        [
          [
            [-75.242266, 38.027209],
            [-75.296871, 37.959043],
            [-75.319335, 37.922484],
            [-75.334296, 37.893477],
            [-75.349338, 37.873143],
            [-75.359036, 37.864143],
            [-75.36683, 37.859446],
            [-75.374642, 37.859454],
            [-75.392008, 37.867738],
            [-75.40054, 37.874865],
            [-75.428956, 37.875305],
            [-75.437868, 37.872324],
            [-75.452681, 37.86351],
            [-75.467951, 37.851328],
            [-75.487485, 37.832136],
            [-75.514921, 37.799149],
            [-75.548082, 37.742383],
            [-75.556868, 37.72441],
            [-75.572464, 37.701565],
            [-75.581333, 37.683593],
            [-75.586136, 37.660653],
            [-75.60322, 37.620243],
            [-75.610808, 37.605909],
            [-75.612237, 37.585602],
            [-75.608123, 37.578018],
            [-75.595716, 37.576657],
            [-75.594044, 37.569698],
            [-75.60672, 37.55717],
            [-75.63337, 37.52214],
            [-75.666178, 37.472124],
            [-75.6655, 37.467319],
            [-75.713275, 37.449876],
            [-75.763912, 37.463308],
            [-75.776564, 37.454589],
            [-75.809357, 37.465923],
            [-75.788012, 37.528816],
            [-75.835214, 37.554245],
            [-75.884455, 37.549542],
            [-75.90041, 37.557265],
            [-75.937665, 37.549652],
            [-75.937299, 37.551729],
            [-75.941153, 37.558436],
            [-75.941182, 37.563839],
            [-75.924756, 37.600215],
            [-75.909586, 37.622671],
            [-75.877059, 37.660641],
            [-75.868481, 37.668224],
            [-75.869523, 37.674356],
            [-75.868355, 37.687609],
            [-75.859262, 37.703111],
            [-75.845579, 37.707993],
            [-75.837685, 37.712985],
            [-75.830773, 37.725486],
            [-75.831438, 37.73169],
            [-75.827922, 37.737986],
            [-75.82481, 37.741671],
            [-75.812155, 37.749502],
            [-75.803041, 37.762464],
            [-75.812125, 37.776589],
            [-75.818125, 37.791698],
            [-75.793399, 37.804493],
            [-75.784599, 37.806826],
            [-75.770607, 37.804602],
            [-75.743097, 37.806656],
            [-75.73588, 37.816561],
            [-75.723224, 37.820124],
            [-75.71659, 37.826696],
            [-75.714487, 37.837777],
            [-75.709114, 37.8477],
            [-75.702914, 37.849659],
            [-75.689837, 37.861817],
            [-75.685293, 37.873341],
            [-75.687584, 37.88634],
            [-75.709626, 37.900622],
            [-75.72049, 37.901926],
            [-75.724505, 37.900184],
            [-75.726699, 37.897299],
            [-75.753048, 37.896605],
            [-75.758796, 37.897615],
            [-75.759835, 37.899333],
            [-75.757694, 37.903912],
            [-75.712065, 37.936082],
            [-75.704318, 37.92901],
            [-75.693942, 37.930362],
            [-75.669711, 37.950796],
            [-75.660956, 37.959174],
            [-75.646507, 37.973],
            [-75.646289, 37.973209],
            [-75.645251, 37.974202],
            [-75.645096, 37.97435],
            [-75.644665, 37.974763],
            [-75.644591, 37.974833],
            [-75.644545, 37.974877],
            [-75.639786, 37.979432],
            [-75.635502, 37.983531],
            [-75.630222, 37.988584],
            [-75.626129, 37.9925],
            [-75.624341, 37.994211],
            [-75.435956, 38.010282],
            [-75.42881, 38.010854],
            [-75.398839, 38.013277],
            [-75.377851, 38.015145],
            [-75.263779, 38.025295],
            [-75.262088, 38.025445],
            [-75.260635, 38.025574],
            [-75.256076, 38.02598],
            [-75.250358, 38.026489],
            [-75.242296, 38.027206],
            [-75.242266, 38.027209]
          ]
        ],
        [
          [
            [-75.973607, 37.835817],
            [-75.971705, 37.830928],
            [-75.977301, 37.825821],
            [-75.982158, 37.806226],
            [-75.987301, 37.804917],
            [-75.9983, 37.812626],
            [-76.001116, 37.834947],
            [-75.999658, 37.848198],
            [-75.996859, 37.85042],
            [-75.992556, 37.848889],
            [-75.988018, 37.841085],
            [-75.982098, 37.837253],
            [-75.973607, 37.835817]
          ]
        ],
        [
          [
            [-75.994739, 37.953501],
            [-75.993905, 37.953489],
            [-76.00313, 37.947997],
            [-76.017592, 37.935161],
            [-76.032491, 37.915008],
            [-76.035802, 37.929008],
            [-76.04653, 37.953586],
            [-76.045561, 37.953669],
            [-76.041691, 37.954],
            [-76.041402, 37.954006],
            [-76.038026, 37.953901],
            [-76.030122, 37.953655],
            [-76.029463, 37.953775],
            [-76.029405, 37.953776],
            [-76.022325, 37.953878],
            [-76.021714, 37.953887],
            [-76.020932, 37.953879],
            [-76.020796, 37.953877],
            [-76.017686, 37.953832],
            [-76.005888, 37.953662],
            [-75.994739, 37.953501]
          ]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51021",
      state: "51",
      county: "021",
      name: "Bland County",
      censusArea: 357.725,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.225104, 37.234874],
          [-81.204774, 37.243013],
          [-81.167029, 37.262881],
          [-81.112596, 37.278497],
          [-81.084012, 37.284401],
          [-81.034652, 37.290751],
          [-80.981322, 37.293465],
          [-80.980044, 37.293118],
          [-81.014489, 37.275385],
          [-80.854627, 37.147482],
          [-80.912674, 37.073349],
          [-81.114319, 37.022612],
          [-81.224613, 37.04784],
          [-81.379427, 36.952417],
          [-81.437182, 37.010091],
          [-81.462213, 37.035541],
          [-81.270259, 37.093004],
          [-81.328117, 37.139007],
          [-81.225104, 37.234874]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51027",
      state: "51",
      county: "027",
      name: "Buchanan County",
      censusArea: 502.763,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-81.968297, 37.537798],
          [-81.92787, 37.512118],
          [-81.94264, 37.508844],
          [-81.992916, 37.482969],
          [-81.996578, 37.476705],
          [-81.99227, 37.460916],
          [-81.987006, 37.454878],
          [-81.968795, 37.451496],
          [-81.949367, 37.445687],
          [-81.935621, 37.438397],
          [-81.92749, 37.413251],
          [-81.933895, 37.372747],
          [-81.930194, 37.366728],
          [-81.929915, 37.366589],
          [-81.926589, 37.358942],
          [-81.925643, 37.357316],
          [-81.921571, 37.356423],
          [-81.920711, 37.355416],
          [-81.911487, 37.348839],
          [-81.910875, 37.348729],
          [-81.907895, 37.343783],
          [-81.907322, 37.343119],
          [-81.906368, 37.34276],
          [-81.905945, 37.342775],
          [-81.903795, 37.34305],
          [-81.902992, 37.34234],
          [-81.899495, 37.341102],
          [-81.899459, 37.340277],
          [-81.896001, 37.331967],
          [-81.895489, 37.332022],
          [-81.894797, 37.332012],
          [-81.894768, 37.331381],
          [-81.893773, 37.330105],
          [-81.892876, 37.330134],
          [-81.887722, 37.331156],
          [-81.886952, 37.330725],
          [-81.885075, 37.330665],
          [-81.880886, 37.331146],
          [-81.879601, 37.332074],
          [-81.878713, 37.331753],
          [-81.878343, 37.328837],
          [-81.873213, 37.325065],
          [-81.865219, 37.308839],
          [-81.86476, 37.308404],
          [-81.853978, 37.300418],
          [-81.854465, 37.299937],
          [-81.853488, 37.294763],
          [-81.854059, 37.291352],
          [-81.843167, 37.285586],
          [-81.84231, 37.285556],
          [-81.810559, 37.28298],
          [-81.809184, 37.283003],
          [-81.793639, 37.282188],
          [-81.793425, 37.281674],
          [-81.774747, 37.274847],
          [-81.774684, 37.274807],
          [-81.767837, 37.274216],
          [-81.765195, 37.275099],
          [-81.763836, 37.275218],
          [-81.762776, 37.275391],
          [-81.761752, 37.275713],
          [-81.76022, 37.275254],
          [-81.757631, 37.274003],
          [-81.75773, 37.271934],
          [-81.757714, 37.271124],
          [-81.757531, 37.27001],
          [-81.755012, 37.26772],
          [-81.752912, 37.266614],
          [-81.752123, 37.265568],
          [-81.75129, 37.265131],
          [-81.747656, 37.264329],
          [-81.743505, 37.247601],
          [-81.74342, 37.245858],
          [-81.744291, 37.244178],
          [-81.738378, 37.240917],
          [-81.901087, 37.142546],
          [-82.017673, 37.068787],
          [-82.148718, 37.041691],
          [-82.135935, 37.061169],
          [-82.305874, 37.3011],
          [-82.291908, 37.311642],
          [-81.968297, 37.537798]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51037",
      state: "51",
      county: "037",
      name: "Charlotte County",
      censusArea: 475.271,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.443319, 37.0794],
          [-78.493028, 36.89122],
          [-78.648541, 36.697909],
          [-78.671463, 36.857951],
          [-78.904587, 37.022288],
          [-78.903393, 37.024371],
          [-78.824209, 37.205361],
          [-78.681573, 37.248759],
          [-78.692472, 37.204786],
          [-78.547128, 37.150431],
          [-78.443319, 37.0794]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US51041",
      state: "51",
      county: "041",
      name: "Chesterfield County",
      censusArea: 423.297,
      stateName: "Virginia"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-77.851804, 37.354867],
          [-77.855148, 37.418363],
          [-77.653987, 37.562631],
          [-77.649239, 37.559613],
          [-77.60645, 37.555443],
          [-77.59611, 37.55572],
          [-77.514425, 37.478934],
          [-77.500745, 37.463831],
          [-77.486388, 37.459024],
          [-77.459219, 37.46427],
          [-77.420849, 37.447079],
          [-77.361937, 37.376057],
          [-77.249665, 37.382],
          [-77.244167, 37.369274],
          [-77.264595, 37.344052],
          [-77.270675, 37.320756],
          [-77.282659, 37.318084],
          [-77.284019, 37.313012],
          [-77.30988, 37.311556],
          [-77.322813, 37.308792],
          [-77.334549, 37.312468],
          [-77.352518, 37.310165],
          [-77.35271, 37.297957],
          [-77.356582, 37.283125],
          [-77.360455, 37.277333],
          [-77.368823, 37.272613],
          [-77.373735, 37.266949],
          [-77.377381, 37.255541],
          [-77.376728, 37.244993],
          [-77.383856, 37.238906],
          [-77.390393, 37.23998],
          [-77.403671, 37.237346],
          [-77.4021, 37.239521],
          [-77.394214, 37.239524],
          [-77.392064, 37.240853],
          [-77.385702, 37.241158],
          [-77.382521, 37.242837],
          [-77.379193, 37.26758],
          [-77.362322, 37.282162],
          [-77.431705, 37.276348],
          [-77.414406, 37.260949],
          [-77.41651, 37.233157],
          [-77.42889, 37.229678],
          [-77.4339, 37.224648],
          [-77.447521, 37.22271],
          [-77.65061, 37.265112],
          [-77.75862, 37.268717],
          [-77.851804, 37.354867]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US15009",
      state: "15",
      county: "009",
      name: "Maui County",
      censusArea: 1161.521,
      stateName: "Hawaii"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.014553, 21.185503],
          [-88.94213500000001, 21.160173],
          [-88.918072, 21.129479],
          [-88.898876, 21.147604],
          [-88.918248, 21.168279],
          [-88.90346600000001, 21.16421],
          [-88.89817400000001, 21.16594],
          [-88.89613, 21.169561],
          [-88.896537, 21.172208],
          [-88.867944, 21.16452],
          [-88.84159199999999, 21.167926],
          [-88.821944, 21.174693],
          [-88.77149499999999, 21.180053],
          [-88.742231, 21.176214],
          [-88.73834099999999, 21.17202],
          [-88.736648, 21.16188],
          [-88.71938599999999, 21.163911],
          [-88.712696, 21.161547],
          [-88.714158, 21.152238],
          [-88.726033, 21.13236],
          [-88.748932, 21.1086],
          [-88.775995, 21.089751],
          [-88.79081500000001, 21.081686],
          [-88.79413600000001, 21.075796],
          [-88.835351, 21.06336],
          [-88.86579499999999, 21.057801],
          [-88.877137, 21.0493],
          [-88.89194599999999, 21.051831],
          [-88.89517000000001, 21.055771],
          [-88.953719, 21.067761],
          [-89.00295, 21.083282],
          [-89.02617000000001, 21.089015],
          [-89.03204500000001, 21.091094],
          [-89.037667, 21.097864],
          [-89.07969600000001, 21.105835],
          [-89.095373, 21.10636],
          [-89.125, 21.1026],
          [-89.143483, 21.096632],
          [-89.25406100000001, 21.090601],
          [-89.29805400000001, 21.096917],
          [-89.313343, 21.105755],
          [-89.29918699999999, 21.132488],
          [-89.29947100000001, 21.135972],
          [-89.29377400000001, 21.146127],
          [-89.284346, 21.157755],
          [-89.27647400000001, 21.163175],
          [-89.27450400000001, 21.162762],
          [-89.25991099999999, 21.174875],
          [-89.25470899999999, 21.181376],
          [-89.25100699999999, 21.190952],
          [-89.25026, 21.207739],
          [-89.256935, 21.215665],
          [-89.26145700000001, 21.217661],
          [-89.26316299999999, 21.220873],
          [-89.26069000000001, 21.225684],
          [-89.25708499999999, 21.227268],
          [-89.241534, 21.220969],
          [-89.22644500000001, 21.220185],
          [-89.21208200000001, 21.221848],
          [-89.202125, 21.219298],
          [-89.19243900000001, 21.207644],
          [-89.185553, 21.205602],
          [-89.157103, 21.200706],
          [-89.148125, 21.200745],
          [-89.14462700000001, 21.202555],
          [-89.128207, 21.201488],
          [-89.113438, 21.197375],
          [-89.097971, 21.198012],
          [-89.06426400000001, 21.189076],
          [-89.053053, 21.188754],
          [-89.04775699999999, 21.190739],
          [-89.039987, 21.190909],
          [-89.014553, 21.185503]
        ],
        [
          [-88.54416900000001, 20.522802],
          [-88.550016, 20.520273],
          [-88.55999399999999, 20.521892],
          [-88.58623800000001, 20.511711],
          [-88.60384400000001, 20.524372],
          [-88.63114300000001, 20.514943],
          [-88.642347, 20.508285],
          [-88.64746400000001, 20.512017],
          [-88.66880900000001, 20.504738],
          [-88.682939, 20.506775],
          [-88.70367300000001, 20.527237],
          [-88.70226500000001, 20.532451],
          [-88.696662, 20.541646],
          [-88.68010000000001, 20.557021],
          [-88.651567, 20.565574],
          [-88.614598, 20.587109],
          [-88.61073400000001, 20.59377],
          [-88.57687100000001, 20.60657],
          [-88.56714, 20.604895],
          [-88.553604, 20.594729],
          [-88.543034, 20.580115],
          [-88.54280800000001, 20.573674],
          [-88.54890900000001, 20.56859],
          [-88.55602099999999, 20.542657],
          [-88.55301800000001, 20.539382],
          [-88.540189, 20.534741],
          [-88.53964300000001, 20.527644],
          [-88.54416900000001, 20.522802]
        ],
        [
          [-88.612012, 21.02477],
          [-88.612065, 21.027273],
          [-88.60623799999999, 21.034371],
          [-88.59225599999999, 21.03288],
          [-88.58044799999999, 21.020172],
          [-88.56277299999999, 21.016167],
          [-88.549813, 21.004939],
          [-88.546291, 21.005082],
          [-88.528246, 20.967757],
          [-88.518707, 20.954662],
          [-88.512226, 20.95128],
          [-88.510391, 20.940358],
          [-88.507913, 20.937886],
          [-88.49948, 20.934577],
          [-88.49588299999999, 20.928005],
          [-88.49326300000001, 20.916011],
          [-88.481055, 20.898199],
          [-88.474796, 20.894546],
          [-88.42266799999999, 20.911631],
          [-88.386045, 20.919563],
          [-88.37429700000001, 20.927616],
          [-88.37072900000001, 20.932669],
          [-88.35264900000001, 20.941414],
          [-88.345655, 20.941596],
          [-88.342365, 20.938737],
          [-88.332817, 20.94645],
          [-88.324578, 20.950184],
          [-88.307198, 20.942739],
          [-88.28633199999999, 20.947701],
          [-88.275116, 20.937361],
          [-88.26310699999999, 20.940888],
          [-88.24255500000001, 20.937838],
          [-88.23015899999999, 20.931936],
          [-88.23008899999999, 20.917864],
          [-88.22675699999999, 20.916677],
          [-88.222062, 20.918309],
          [-88.217953, 20.916573],
          [-88.216341, 20.907035],
          [-88.173103, 20.876926],
          [-88.170458, 20.874605],
          [-88.16674599999999, 20.865646],
          [-88.13266899999999, 20.861369],
          [-88.129381, 20.847513],
          [-88.115735, 20.827301],
          [-88.100123, 20.828502],
          [-88.09029100000001, 20.831872],
          [-88.059788, 20.81054],
          [-88.033287, 20.808246],
          [-88.003532, 20.795545],
          [-88.002947, 20.789418],
          [-87.987944, 20.776552],
          [-87.984587, 20.767496],
          [-87.986851, 20.758577],
          [-87.985413, 20.744245],
          [-87.98721599999999, 20.722717],
          [-87.991534, 20.713654],
          [-88.00187, 20.698064],
          [-88.01415, 20.685681],
          [-88.02004400000001, 20.686857],
          [-88.03070199999999, 20.682452],
          [-88.04034100000001, 20.672719],
          [-88.04378600000001, 20.664902],
          [-88.05338499999999, 20.65432],
          [-88.059753, 20.652044],
          [-88.08147199999999, 20.654387],
          [-88.08936499999999, 20.648519],
          [-88.12098499999999, 20.633685],
          [-88.129898, 20.627523],
          [-88.142665, 20.623605],
          [-88.144588, 20.624032],
          [-88.14808500000001, 20.629067],
          [-88.15677199999999, 20.629639],
          [-88.16973200000001, 20.627358],
          [-88.173393, 20.6241],
          [-88.18455599999999, 20.629719],
          [-88.192938, 20.631769],
          [-88.21025800000001, 20.628518],
          [-88.225338, 20.62294],
          [-88.236145, 20.61595],
          [-88.26592099999999, 20.601629],
          [-88.284391, 20.596488],
          [-88.288037, 20.59203],
          [-88.293454, 20.588783],
          [-88.30269200000001, 20.586199],
          [-88.322944, 20.588273],
          [-88.35171600000001, 20.58697],
          [-88.359634, 20.581977],
          [-88.370725, 20.57876],
          [-88.377633, 20.578427],
          [-88.415313, 20.586099],
          [-88.41752299999999, 20.589728],
          [-88.41574600000001, 20.594044],
          [-88.417799, 20.598682],
          [-88.42314099999999, 20.602079],
          [-88.427708, 20.598873],
          [-88.431872, 20.598143],
          [-88.43838500000001, 20.601337],
          [-88.444242, 20.607941],
          [-88.44288399999999, 20.613842],
          [-88.450651, 20.642212],
          [-88.44589400000001, 20.64927],
          [-88.44367299999999, 20.656018],
          [-88.448656, 20.704739],
          [-88.45103800000001, 20.725469],
          [-88.45289500000001, 20.731287],
          [-88.458438, 20.736676],
          [-88.462242, 20.753952],
          [-88.46205800000001, 20.772571],
          [-88.464043, 20.781667],
          [-88.47356199999999, 20.790756],
          [-88.489496, 20.798339],
          [-88.501688, 20.799933],
          [-88.50602599999999, 20.799463],
          [-88.515994, 20.794234],
          [-88.525215, 20.780821],
          [-88.53775200000001, 20.778408],
          [-88.63179400000001, 20.82124],
          [-88.67863399999999, 20.870541],
          [-88.68896899999999, 20.888673],
          [-88.687804, 20.89072],
          [-88.688132, 20.906325],
          [-88.69133400000001, 20.91244],
          [-88.697418, 20.916368],
          [-88.69989000000001, 20.920629],
          [-88.69411, 20.952708],
          [-88.680905, 20.980262],
          [-88.665514, 21.007054],
          [-88.65241900000001, 21.008994],
          [-88.64596599999999, 21.014416],
          [-88.64259200000001, 21.019936],
          [-88.64416700000001, 21.022312],
          [-88.642809, 21.027583],
          [-88.61958100000001, 21.027793],
          [-88.612012, 21.02477]
        ],
        [
          [-89.01000099999999, 20.929757],
          [-88.989813, 20.932127],
          [-88.97160400000001, 20.926254],
          [-88.93752900000001, 20.925274],
          [-88.91845, 20.922546],
          [-88.89716899999999, 20.915395],
          [-88.83704700000001, 20.863575],
          [-88.82523699999999, 20.850731],
          [-88.80957599999999, 20.826036],
          [-88.808469, 20.820396],
          [-88.809463, 20.809169],
          [-88.81742700000001, 20.794606],
          [-88.83832100000001, 20.764575],
          [-88.84641300000001, 20.760201],
          [-88.851481, 20.760069],
          [-88.869753, 20.754701],
          [-88.89029500000001, 20.744855],
          [-88.90908099999999, 20.739533],
          [-88.94900899999999, 20.738997],
          [-88.96789000000001, 20.73508],
          [-88.984747, 20.756677],
          [-88.994001, 20.786671],
          [-88.988933, 20.815496],
          [-88.99183400000001, 20.826603],
          [-89.00624300000001, 20.849603],
          [-89.010911, 20.854476],
          [-89.054552, 20.877219],
          [-89.059663, 20.884634],
          [-89.061128, 20.890635],
          [-89.062511, 20.904385],
          [-89.05913000000001, 20.913407],
          [-89.035789, 20.927078],
          [-89.02562599999999, 20.929528],
          [-89.01000099999999, 20.929757]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US15001",
      state: "15",
      county: "001",
      name: "Hawaii County",
      censusArea: 4028.417,
      stateName: "Hawaii"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-87.778234, 20.245743],
          [-87.77273400000001, 20.245409],
          [-87.746893, 20.232325],
          [-87.73700400000001, 20.222773],
          [-87.73582200000001, 20.212417],
          [-87.73270400000001, 20.205392],
          [-87.653966, 20.16736],
          [-87.630382, 20.146916],
          [-87.62456499999999, 20.145911],
          [-87.607797, 20.137987],
          [-87.600909, 20.126573],
          [-87.59803299999999, 20.124539],
          [-87.590923, 20.122497],
          [-87.58168, 20.123617],
          [-87.56836799999999, 20.130545],
          [-87.558933, 20.13157],
          [-87.523661, 20.120028],
          [-87.516795, 20.11523],
          [-87.50256099999999, 20.114155],
          [-87.468211, 20.104296],
          [-87.44395700000001, 20.095318],
          [-87.40545900000001, 20.078772],
          [-87.4024, 20.075541],
          [-87.38757799999999, 20.067119],
          [-87.33021, 20.038517],
          [-87.29548, 20.024438],
          [-87.28262899999999, 20.021969],
          [-87.27031600000001, 20.014525],
          [-87.24093300000001, 19.990173],
          [-87.204486, 19.969438],
          [-87.194593, 19.958368],
          [-87.17993899999999, 19.949372],
          [-87.149215, 19.922872],
          [-87.144394, 19.920523],
          [-87.131235, 19.906801],
          [-87.124618, 19.897288],
          [-87.12174999999999, 19.886099],
          [-87.107541, 19.872467],
          [-87.098716, 19.867811],
          [-87.095032, 19.867882],
          [-87.086341, 19.855399],
          [-87.08435700000001, 19.849736],
          [-87.08567400000001, 19.838584],
          [-87.088979, 19.826656],
          [-87.094414, 19.81491],
          [-87.09207, 19.799409],
          [-87.091216, 19.776368],
          [-87.09351699999999, 19.771832],
          [-87.093387, 19.737751],
          [-87.087118, 19.728013],
          [-87.07942600000001, 19.726193],
          [-87.063972, 19.728917],
          [-87.04538199999999, 19.739824],
          [-87.00642300000001, 19.739286],
          [-86.997278, 19.72858],
          [-86.987168, 19.708524],
          [-86.98110199999999, 19.690687],
          [-86.98471799999999, 19.672161],
          [-86.983778, 19.641647],
          [-86.97434200000001, 19.633201],
          [-86.963933, 19.627605],
          [-86.95035899999999, 19.626461],
          [-86.94787400000001, 19.62425],
          [-86.94771800000001, 19.621947],
          [-86.95101399999999, 19.613614],
          [-86.94710599999999, 19.604856],
          [-86.93394, 19.597505],
          [-86.92820499999999, 19.592702],
          [-86.92442199999999, 19.586553],
          [-86.90354199999999, 19.570622],
          [-86.875, 19.556797],
          [-86.852618, 19.549172],
          [-86.83738399999999, 19.538354],
          [-86.82673199999999, 19.537626],
          [-86.81441699999999, 19.53009],
          [-86.809561, 19.522377],
          [-86.809379, 19.519086],
          [-86.822968, 19.48129],
          [-86.83854500000001, 19.463642],
          [-86.86854, 19.438126],
          [-86.88781700000001, 19.426425],
          [-86.92877200000001, 19.397646],
          [-86.944185, 19.381852],
          [-86.964619, 19.365646],
          [-86.980861, 19.349291],
          [-87.02053699999999, 19.331317],
          [-87.06172900000001, 19.316636],
          [-87.113272, 19.290613],
          [-87.1337, 19.276099],
          [-87.15963500000001, 19.268375],
          [-87.172413, 19.26906],
          [-87.18742700000001, 19.266156],
          [-87.19626, 19.261295],
          [-87.205892, 19.260907],
          [-87.24396100000001, 19.271313],
          [-87.26461900000001, 19.274213],
          [-87.296761, 19.266289],
          [-87.303808, 19.261835],
          [-87.31336999999999, 19.250698],
          [-87.34126800000001, 19.234039],
          [-87.34914800000001, 19.217756],
          [-87.36063100000001, 19.20893],
          [-87.378638, 19.202435],
          [-87.390701, 19.201171],
          [-87.41736900000001, 19.187858],
          [-87.42709300000001, 19.179546],
          [-87.43251900000001, 19.170623],
          [-87.45351600000001, 19.151952],
          [-87.465663, 19.146964],
          [-87.505281, 19.137908],
          [-87.51473999999999, 19.132501],
          [-87.51213999999999, 19.128174],
          [-87.512137, 19.124296],
          [-87.51965200000001, 19.117025],
          [-87.52613600000001, 19.115889],
          [-87.52890199999999, 19.11371],
          [-87.544806, 19.091059],
          [-87.551129, 19.08878],
          [-87.557817, 19.08213],
          [-87.55532600000001, 19.069377],
          [-87.55517699999999, 19.053932],
          [-87.55737099999999, 19.046565],
          [-87.56644600000001, 19.032531],
          [-87.57659899999999, 19.027412],
          [-87.58190300000001, 19.02224],
          [-87.59603200000001, 18.998833],
          [-87.596521, 18.980654],
          [-87.601866, 18.971572],
          [-87.613966, 18.970399],
          [-87.62525600000001, 18.961951],
          [-87.625, 18.959934],
          [-87.63805400000001, 18.941723],
          [-87.65848600000001, 18.924835],
          [-87.67200500000001, 18.917466],
          [-87.681825, 18.918694],
          [-87.687716, 18.923358],
          [-87.69017099999999, 18.932195],
          [-87.693117, 18.940542],
          [-87.726043, 18.969437],
          [-87.763598, 18.981837],
          [-87.80610899999999, 19.013967],
          [-87.85394299999999, 19.023762],
          [-87.88155, 19.036644],
          [-87.88407699999999, 19.039266],
          [-87.886278, 19.05576],
          [-87.903693, 19.080777],
          [-87.908355, 19.081138],
          [-87.921389, 19.121183],
          [-87.917292, 19.155963],
          [-87.90333899999999, 19.217792],
          [-87.90491, 19.230147],
          [-87.90256500000001, 19.258427],
          [-87.89543499999999, 19.274639],
          [-87.89084199999999, 19.298905],
          [-87.88735600000001, 19.337101],
          [-87.888701, 19.348031],
          [-87.89879199999999, 19.377984],
          [-87.913849, 19.401107],
          [-87.909087, 19.415455],
          [-87.921707, 19.43055],
          [-87.92426900000001, 19.438794],
          [-87.92516599999999, 19.468081],
          [-87.922609, 19.478611],
          [-87.924124, 19.481406],
          [-87.930523, 19.484921],
          [-87.935641, 19.485628],
          [-87.93640300000001, 19.481905],
          [-87.939145, 19.481577],
          [-87.95149, 19.486649],
          [-87.95289700000001, 19.488805],
          [-87.953663, 19.510003],
          [-87.96045699999999, 19.546612],
          [-87.962264, 19.551779],
          [-87.96521100000001, 19.554745],
          [-87.96934999999999, 19.555963],
          [-87.970969, 19.586328],
          [-87.978206, 19.608159],
          [-87.997728, 19.642816],
          [-88.02898200000001, 19.650098],
          [-88.032928, 19.653905],
          [-88.03499400000001, 19.65936],
          [-88.03332599999999, 19.66923],
          [-88.02742699999999, 19.672154],
          [-88.029281, 19.678908],
          [-88.036079, 19.690252],
          [-88.04795999999999, 19.698938],
          [-88.05165199999999, 19.703649],
          [-88.05248499999999, 19.718667],
          [-88.06436400000001, 19.730766],
          [-88.05722, 19.742536],
          [-88.052315, 19.756836],
          [-88.04965100000001, 19.780452],
          [-88.02173199999999, 19.8022],
          [-88.00626700000001, 19.81758],
          [-87.982821, 19.845651],
          [-87.976651, 19.85053],
          [-87.96481700000001, 19.855183],
          [-87.949251, 19.857034],
          [-87.94529700000001, 19.853443],
          [-87.94031100000001, 19.852305],
          [-87.92584299999999, 19.858928],
          [-87.926938, 19.870221],
          [-87.92549, 19.875],
          [-87.915662, 19.887126],
          [-87.90198699999999, 19.912081],
          [-87.89409900000001, 19.923135],
          [-87.894474, 19.926927],
          [-87.89253299999999, 19.932162],
          [-87.866919, 19.954172],
          [-87.85658799999999, 19.968885],
          [-87.840708, 19.976952],
          [-87.83869200000001, 19.975527],
          [-87.83531199999999, 19.976078],
          [-87.83194800000001, 19.982775],
          [-87.82896500000001, 19.995542],
          [-87.82547299999999, 20.025944],
          [-87.828182, 20.035424],
          [-87.85038499999999, 20.062506],
          [-87.866931, 20.078652],
          [-87.88418999999999, 20.10675],
          [-87.899149, 20.145728],
          [-87.906035, 20.205157],
          [-87.901452, 20.235787],
          [-87.89066299999999, 20.25524],
          [-87.882631, 20.263026],
          [-87.873921, 20.267744],
          [-87.85329300000001, 20.271548],
          [-87.81145900000001, 20.26032],
          [-87.783242, 20.246395],
          [-87.778234, 20.245743]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US15007",
      state: "15",
      county: "007",
      name: "Kauai County",
      censusArea: 619.956,
      stateName: "Hawaii"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-92.555771, 21.668287],
          [-92.551041, 21.670101],
          [-92.54332, 21.667128],
          [-92.542404, 21.648081],
          [-92.549316, 21.645353],
          [-92.554123, 21.649546],
          [-92.555771, 21.668287]
        ],
        [
          [-92.125, 21.95909],
          [-92.122262, 21.962881],
          [-92.11274599999999, 21.995245],
          [-92.09645, 22.001489],
          [-92.072123, 22.003334],
          [-92.05854299999999, 21.99638],
          [-92.05199200000001, 21.983681],
          [-92.052729, 21.980321],
          [-92.05633599999999, 21.977939],
          [-92.06054900000001, 21.976729],
          [-92.06334899999999, 21.978354],
          [-92.065811, 21.976562],
          [-92.078393, 21.955153],
          [-92.08578700000001, 21.927295],
          [-92.08001200000001, 21.910808],
          [-92.07906500000001, 21.89608],
          [-92.098897, 21.884711],
          [-92.12428299999999, 21.876789],
          [-92.14760899999999, 21.872814],
          [-92.16162, 21.864746],
          [-92.17479599999999, 21.846923],
          [-92.18978200000001, 21.82245],
          [-92.20521099999999, 21.789053],
          [-92.20042699999999, 21.786479],
          [-92.205851, 21.779518],
          [-92.21804399999999, 21.783755],
          [-92.23478, 21.795418],
          [-92.24960999999999, 21.815145],
          [-92.244943, 21.848943],
          [-92.23102800000001, 21.886263],
          [-92.22896499999999, 21.889117],
          [-92.21383, 21.899193],
          [-92.20552799999999, 21.907507],
          [-92.20271600000001, 21.912422],
          [-92.190158, 21.923592],
          [-92.167471, 21.932863],
          [-92.13704999999999, 21.948632],
          [-92.12730199999999, 21.955508],
          [-92.125, 21.95909]
        ],
        [
          [-91.43170699999999, 22.220015],
          [-91.40732, 22.230555],
          [-91.38811899999999, 22.223252],
          [-91.385977, 22.220009],
          [-91.36756299999999, 22.214906],
          [-91.35984199999999, 22.214831],
          [-91.357227, 22.217744],
          [-91.35379499999999, 22.217669],
          [-91.33996400000001, 22.208519],
          [-91.31561300000001, 22.186817],
          [-91.308855, 22.155555],
          [-91.297808, 22.149748],
          [-91.295875, 22.144547],
          [-91.29527100000001, 22.13039],
          [-91.297143, 22.113815],
          [-91.317451, 22.080944],
          [-91.32166699999999, 22.063411],
          [-91.32477499999999, 22.05867],
          [-91.333267, 22.054639],
          [-91.337996, 22.046575],
          [-91.34140099999999, 22.028978],
          [-91.333224, 21.973005],
          [-91.33310900000001, 21.964176],
          [-91.33471399999999, 21.961099],
          [-91.350828, 21.950817],
          [-91.35661300000001, 21.939546],
          [-91.382349, 21.924479],
          [-91.40828400000001, 21.897781],
          [-91.425862, 21.884527],
          [-91.44659899999999, 21.871647],
          [-91.47196199999999, 21.88292],
          [-91.490914, 21.888898],
          [-91.51797300000001, 21.890996],
          [-91.55541500000001, 21.891355],
          [-91.57499100000001, 21.896585],
          [-91.57778400000001, 21.900486],
          [-91.584272, 21.899038],
          [-91.610241, 21.898356],
          [-91.63784899999999, 21.917166],
          [-91.648132, 21.93297],
          [-91.67187200000001, 21.957038],
          [-91.68149299999999, 21.960054],
          [-91.705255, 21.963427],
          [-91.72013999999999, 21.970789],
          [-91.758218, 21.980694],
          [-91.765735, 21.986593],
          [-91.788139, 22.018411],
          [-91.790932, 22.031177],
          [-91.786543, 22.06369],
          [-91.78009599999999, 22.072567],
          [-91.74815899999999, 22.100388],
          [-91.74122299999999, 22.115666],
          [-91.73345699999999, 22.142756],
          [-91.726043, 22.152171],
          [-91.69997799999999, 22.165252],
          [-91.66984, 22.170782],
          [-91.60879399999999, 22.207878],
          [-91.59159600000001, 22.219456],
          [-91.583965, 22.22668],
          [-91.559643, 22.229185],
          [-91.55416600000001, 22.228212],
          [-91.54859400000001, 22.226263],
          [-91.54114999999999, 22.216764],
          [-91.534594, 22.219403],
          [-91.52376899999999, 22.217602],
          [-91.51941, 22.215646],
          [-91.518348, 22.211182],
          [-91.51557399999999, 22.208008],
          [-91.507811, 22.205987],
          [-91.50105500000001, 22.211064],
          [-91.500821, 22.225538],
          [-91.48855800000001, 22.23317],
          [-91.48015799999999, 22.232715],
          [-91.467007, 22.226529],
          [-91.45618999999999, 22.228811],
          [-91.441809, 22.226321],
          [-91.43170699999999, 22.220015]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US15005",
      state: "15",
      county: "005",
      name: "Kalawao County",
      censusArea: 11.991,
      stateName: "Hawaii"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-89.014553, 21.185503],
          [-88.999108, 21.182221],
          [-88.991318, 21.18551],
          [-88.98776799999999, 21.18935],
          [-88.98234299999999, 21.207798],
          [-88.984464, 21.210063],
          [-88.98403200000001, 21.212198],
          [-88.97400200000001, 21.218503],
          [-88.969064, 21.217018],
          [-88.96284700000001, 21.212131],
          [-88.95165399999999, 21.191662],
          [-88.950808, 21.182636],
          [-88.946159, 21.175963],
          [-88.918248, 21.168279],
          [-88.898876, 21.147604],
          [-88.918072, 21.129479],
          [-88.94213500000001, 21.160173],
          [-89.014553, 21.185503]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US15003",
      state: "15",
      county: "003",
      name: "Honolulu County",
      censusArea: 600.743,
      stateName: "Hawaii"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-90.04448500000001, 21.306011],
          [-90.0883, 21.2988],
          [-90.10329999999999, 21.2979],
          [-90.11269999999999, 21.3019],
          [-90.12110000000001, 21.3169],
          [-90.1225, 21.3224],
          [-90.11194900000001, 21.326622],
          [-90.11419599999999, 21.331123],
          [-90.119427, 21.334594],
          [-90.125459, 21.330264],
          [-90.13324, 21.359207],
          [-90.1403, 21.3738],
          [-90.149719, 21.385208],
          [-90.161743, 21.396282],
          [-90.17920000000001, 21.4043],
          [-90.181274, 21.409626],
          [-90.18100000000001, 21.420868],
          [-90.182648, 21.430073],
          [-90.192352, 21.44804],
          [-90.20538300000001, 21.459793],
          [-90.219446, 21.46978],
          [-90.233, 21.4876],
          [-90.23117099999999, 21.523857],
          [-90.23175, 21.533035],
          [-90.23431400000001, 21.540058],
          [-90.25067100000001, 21.557373],
          [-90.27950999999999, 21.575794],
          [-90.277679, 21.578789],
          [-90.254425, 21.582684],
          [-90.19070400000001, 21.585892],
          [-90.16999999999999, 21.5823],
          [-90.12561, 21.586739],
          [-90.10672, 21.596577],
          [-90.10668899999999, 21.603024],
          [-90.1095, 21.6057],
          [-90.10818499999999, 21.607487],
          [-90.079895, 21.628101],
          [-90.0668, 21.6437],
          [-90.066711, 21.65234],
          [-90.06389999999999, 21.6584],
          [-90.03720000000001, 21.6843],
          [-90.01812699999999, 21.699955],
          [-89.9923, 21.708],
          [-89.98703, 21.712494],
          [-89.968628, 21.712704],
          [-89.94717399999999, 21.689568],
          [-89.939, 21.669],
          [-89.93010000000001, 21.6552],
          [-89.92459099999999, 21.651183],
          [-89.9228, 21.6361],
          [-89.9238, 21.6293],
          [-89.910797, 21.611183],
          [-89.900574, 21.605885],
          [-89.87735, 21.575277],
          [-89.878601, 21.560181],
          [-89.87252799999999, 21.557568],
          [-89.86689999999999, 21.5637],
          [-89.85614000000001, 21.560661],
          [-89.85256999999999, 21.557514],
          [-89.83694499999999, 21.529945],
          [-89.83737199999999, 21.512085],
          [-89.849579, 21.509598],
          [-89.85262499999999, 21.499971],
          [-89.84549000000001, 21.466747],
          [-89.84099, 21.459483],
          [-89.82489000000001, 21.455379],
          [-89.81630000000001, 21.4502],
          [-89.81389999999999, 21.4403],
          [-89.80590000000001, 21.4301],
          [-89.78651300000001, 21.415633],
          [-89.77984599999999, 21.417309],
          [-89.77445499999999, 21.421352],
          [-89.772209, 21.431236],
          [-89.77490499999999, 21.453698],
          [-89.772209, 21.457741],
          [-89.76457199999999, 21.461335],
          [-89.75423900000001, 21.461335],
          [-89.737617, 21.459089],
          [-89.731777, 21.455944],
          [-89.73132799999999, 21.444713],
          [-89.73581999999999, 21.438424],
          [-89.74076199999999, 21.424048],
          [-89.74121099999999, 21.414614],
          [-89.73859999999999, 21.4043],
          [-89.73019099999999, 21.401871],
          [-89.72822099999999, 21.402104],
          [-89.72642099999999, 21.402845],
          [-89.724324, 21.403311],
          [-89.723794, 21.40329],
          [-89.723286, 21.403227],
          [-89.722735, 21.403121],
          [-89.722544, 21.403036],
          [-89.721845, 21.401596],
          [-89.721083, 21.399541],
          [-89.71889999999999, 21.3961],
          [-89.7089, 21.3833],
          [-89.7087, 21.3793],
          [-89.71260000000001, 21.3689],
          [-89.7106, 21.3585],
          [-89.7088, 21.3534],
          [-89.6971, 21.3364],
          [-89.69380000000001, 21.3329],
          [-89.6619, 21.3131],
          [-89.65180000000001, 21.3139],
          [-89.65262899999999, 21.308709],
          [-89.65369999999999, 21.302],
          [-89.69460000000001, 21.2739],
          [-89.6944, 21.2665],
          [-89.70009999999999, 21.264],
          [-89.7097, 21.2621],
          [-89.7139, 21.2638],
          [-89.7142, 21.2665],
          [-89.7114, 21.272],
          [-89.7122, 21.2814],
          [-89.71430000000001, 21.2845],
          [-89.72130000000001, 21.2869],
          [-89.75720000000001, 21.278],
          [-89.76499999999999, 21.2789],
          [-89.7782, 21.2735],
          [-89.79310000000001, 21.2604],
          [-89.80959999999999, 21.2577],
          [-89.8211, 21.2606],
          [-89.82409999999999, 21.2646],
          [-89.8253, 21.2714],
          [-89.83189999999999, 21.2795],
          [-89.8457, 21.29],
          [-89.88999999999999, 21.3065],
          [-89.894518, 21.319632],
          [-89.898969, 21.327391],
          [-89.90482, 21.329172],
          [-89.918939, 21.318615],
          [-89.917921, 21.313781],
          [-89.91346899999999, 21.310983],
          [-89.91092499999999, 21.305768],
          [-89.95226299999999, 21.306531],
          [-89.950736, 21.312509],
          [-89.95188099999999, 21.318742],
          [-89.967971, 21.327986],
          [-89.973334, 21.327426],
          [-89.98942400000001, 21.317984],
          [-90.02449999999999, 21.3093],
          [-90.04448500000001, 21.306011]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72013",
      state: "72",
      county: "013",
      name: "Arecibo County",
      censusArea: 125.947,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.586778, 23.484948],
          [-79.588181, 23.389408],
          [-79.588305, 23.37414],
          [-79.582374, 23.374238],
          [-79.583014, 23.363218],
          [-79.590785, 23.33806],
          [-79.600229, 23.339603],
          [-79.606989, 23.328186],
          [-79.770072, 23.325013],
          [-79.768129, 23.477654],
          [-79.764893, 23.484097],
          [-79.749301, 23.476701],
          [-79.742067, 23.474681],
          [-79.733986, 23.473457],
          [-79.710743, 23.472611],
          [-79.683719, 23.481367],
          [-79.679876, 23.484944],
          [-79.664364, 23.487809],
          [-79.645839, 23.488777],
          [-79.624618, 23.494199],
          [-79.586778, 23.484948]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72019",
      state: "72",
      county: "019",
      name: "Barranquitas County",
      censusArea: 34.253,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.314848, 23.152088],
          [-79.322323, 23.165503],
          [-79.351258, 23.165488],
          [-79.373768, 23.173694],
          [-79.350793, 23.241235],
          [-79.340121, 23.238689],
          [-79.327034, 23.244191],
          [-79.326297, 23.247884],
          [-79.313299, 23.249324],
          [-79.306488, 23.240967],
          [-79.293176, 23.245902],
          [-79.266466, 23.24527],
          [-79.24046, 23.184794],
          [-79.242623, 23.183408],
          [-79.242735, 23.180248],
          [-79.314848, 23.152088]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72025",
      state: "72",
      county: "025",
      name: "Caguas County",
      censusArea: 58.597,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.066103, 23.303321],
          [-79.052729, 23.303647],
          [-79.045336, 23.299418],
          [-79.047827, 23.306885],
          [-79.041535, 23.312344],
          [-79.031631, 23.308877],
          [-79.027073, 23.310201],
          [-79.021747, 23.305034],
          [-78.99765, 23.206992],
          [-79.052555, 23.113069],
          [-79.060761, 23.116315],
          [-79.06949, 23.142397],
          [-79.065968, 23.148411],
          [-79.080343, 23.152146],
          [-79.095099, 23.169077],
          [-79.096813, 23.168197],
          [-79.100635, 23.169824],
          [-79.102177, 23.169419],
          [-79.105096, 23.171544],
          [-79.101673, 23.183082],
          [-79.109374, 23.200934],
          [-79.117774, 23.209949],
          [-79.066103, 23.303321]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72031",
      state: "72",
      county: "031",
      name: "Carolina County",
      censusArea: 45.321,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.036559, 23.450216],
          [-79.036491, 23.450117],
          [-79.023221, 23.443875],
          [-79.006523, 23.444347],
          [-78.99718, 23.449895],
          [-78.992935, 23.457489],
          [-78.992793, 23.458102],
          [-78.919278, 23.402993],
          [-78.918535, 23.27013],
          [-78.924175, 23.272622],
          [-78.939296, 23.286771],
          [-78.942429, 23.293469],
          [-78.999556, 23.380065],
          [-79.036559, 23.450216]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72045",
      state: "72",
      county: "045",
      name: "Comerio County",
      censusArea: 28.401,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.169126, 23.22429],
          [-79.24046, 23.184794],
          [-79.266466, 23.24527],
          [-79.242185, 23.254632],
          [-79.227615, 23.254673],
          [-79.228952, 23.262801],
          [-79.218205, 23.27471],
          [-79.206135, 23.27666],
          [-79.20128, 23.27628],
          [-79.200802, 23.271477],
          [-79.193248, 23.268138],
          [-79.190676, 23.25884],
          [-79.187298, 23.25379],
          [-79.179052, 23.246969],
          [-79.178855, 23.23754],
          [-79.174947, 23.233095],
          [-79.176409, 23.230251],
          [-79.169141, 23.226013],
          [-79.169126, 23.22429]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72054",
      state: "72",
      county: "054",
      name: "Florida County",
      censusArea: 15.209,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.543079, 23.405422],
          [-79.531299, 23.382165],
          [-79.533623, 23.351333],
          [-79.552734, 23.340438],
          [-79.573822, 23.342517],
          [-79.590785, 23.33806],
          [-79.583014, 23.363218],
          [-79.582374, 23.374238],
          [-79.588305, 23.37414],
          [-79.588181, 23.389408],
          [-79.564313, 23.413309],
          [-79.543079, 23.405422]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72061",
      state: "72",
      county: "061",
      name: "Guaynabo County",
      censusArea: 27.579,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.108281, 23.438902],
          [-79.080475, 23.297295],
          [-79.086465, 23.285094],
          [-79.107787, 23.277874],
          [-79.143861, 23.27959],
          [-79.13158, 23.424656],
          [-79.115647, 23.439973],
          [-79.108281, 23.438902]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72069",
      state: "72",
      county: "069",
      name: "Humacao County",
      censusArea: 44.755,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.739125, 23.173453],
          [-78.743632, 23.163957],
          [-78.758728, 23.156601],
          [-78.766919, 23.148424],
          [-78.777584, 23.129239],
          [-78.796711, 23.083746],
          [-78.796289, 23.079835],
          [-78.794686, 23.078607],
          [-78.795028, 23.073561],
          [-78.796711, 23.069842],
          [-78.877615, 23.118167],
          [-78.826063, 23.200408],
          [-78.739125, 23.173453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72083",
      state: "72",
      county: "083",
      name: "Las Marias County",
      censusArea: 46.36,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.907236, 23.25309],
          [-79.912859, 23.233009],
          [-79.897964, 23.187744],
          [-80.019836, 23.195471],
          [-80.082002, 23.254946],
          [-80.061828, 23.270082],
          [-80.040228, 23.271985],
          [-80.037935, 23.289705],
          [-79.907236, 23.25309]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72093",
      state: "72",
      county: "093",
      name: "Maricao County",
      censusArea: 36.623,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.977855, 23.143799],
          [-79.994529, 23.154774],
          [-80.0096, 23.158915],
          [-80.013844, 23.165874],
          [-80.044039, 23.169202],
          [-80.051461, 23.174053],
          [-80.034521, 23.185519],
          [-80.024372, 23.184668],
          [-80.019836, 23.195471],
          [-79.897964, 23.187744],
          [-79.863848, 23.18661],
          [-79.862832, 23.174808],
          [-79.850765, 23.170357],
          [-79.83676, 23.170553],
          [-79.926517, 23.151267],
          [-79.939065, 23.147855],
          [-79.943554, 23.148979],
          [-79.952093, 23.146112],
          [-79.960424, 23.145907],
          [-79.963939, 23.148212],
          [-79.971866, 23.14154],
          [-79.97554, 23.143395],
          [-79.977855, 23.143799]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72105",
      state: "72",
      county: "105",
      name: "Naranjito County",
      censusArea: 27.401,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.204734, 23.316035],
          [-79.205908, 23.306921],
          [-79.213072, 23.307003],
          [-79.206135, 23.27666],
          [-79.218205, 23.27471],
          [-79.228952, 23.262801],
          [-79.227615, 23.254673],
          [-79.242185, 23.254632],
          [-79.266466, 23.24527],
          [-79.293176, 23.245902],
          [-79.306488, 23.240967],
          [-79.313299, 23.249324],
          [-79.278076, 23.329975],
          [-79.204734, 23.316035]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72115",
      state: "72",
      county: "115",
      name: "Quebradillas County",
      censusArea: 22.683,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.8989, 23.366108],
          [-79.919497, 23.378578],
          [-79.92202, 23.393204],
          [-79.957517, 23.489171],
          [-79.944636, 23.491693],
          [-79.906872, 23.483556],
          [-79.90143, 23.484552],
          [-79.8989, 23.366108]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72129",
      state: "72",
      county: "129",
      name: "San Lorenzo County",
      censusArea: 53.108,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.010245, 23.075565],
          [-79.019443, 23.083723],
          [-79.019442, 23.102576],
          [-79.051788, 23.109878],
          [-79.051162, 23.111088],
          [-79.052555, 23.113069],
          [-78.99765, 23.206992],
          [-78.988847, 23.220472],
          [-78.971036, 23.229239],
          [-78.951019, 23.229292],
          [-78.928341, 23.142845],
          [-78.924448, 23.135673],
          [-78.927429, 23.121821],
          [-78.926227, 23.119963],
          [-79.010245, 23.075565]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72135",
      state: "72",
      county: "135",
      name: "Toa Alta County",
      censusArea: 27.016,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.318764, 23.367411],
          [-79.314158, 23.380801],
          [-79.303663, 23.384077],
          [-79.271288, 23.383718],
          [-79.252554, 23.394187],
          [-79.234345, 23.401464],
          [-79.19698, 23.389289],
          [-79.199568, 23.369616],
          [-79.197293, 23.360199],
          [-79.203616, 23.336673],
          [-79.201531, 23.328983],
          [-79.204734, 23.316035],
          [-79.278076, 23.329975],
          [-79.289035, 23.353719],
          [-79.306194, 23.369688],
          [-79.318764, 23.367411]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72145",
      state: "72",
      county: "145",
      name: "Vega Baja County",
      censusArea: 45.856,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.438813, 23.485713],
          [-79.420921, 23.488639],
          [-79.410344, 23.489886],
          [-79.394287, 23.489748],
          [-79.377286, 23.488044],
          [-79.37282, 23.487726],
          [-79.349647, 23.486335],
          [-79.377058, 23.344246],
          [-79.4438, 23.371027],
          [-79.438813, 23.485713]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72027",
      state: "72",
      county: "027",
      name: "Camuy County",
      censusArea: 46.355,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.8989, 23.366108],
          [-79.90143, 23.484552],
          [-79.867386, 23.490785],
          [-79.849673, 23.490745],
          [-79.83694, 23.487659],
          [-79.836635, 23.487701],
          [-79.824223, 23.342998],
          [-79.828738, 23.344375],
          [-79.836853, 23.348376],
          [-79.842901, 23.350373],
          [-79.846401, 23.351174],
          [-79.848462, 23.353682],
          [-79.851685, 23.353884],
          [-79.852063, 23.354626],
          [-79.854737, 23.355335],
          [-79.861541, 23.356003],
          [-79.871446, 23.356112],
          [-79.873457, 23.356876],
          [-79.876958, 23.356829],
          [-79.877784, 23.358463],
          [-79.879144, 23.358881],
          [-79.880939, 23.361254],
          [-79.880675, 23.362451],
          [-79.882193, 23.36459],
          [-79.884952, 23.365857],
          [-79.885509, 23.367087],
          [-79.8872, 23.367001],
          [-79.891242, 23.368177],
          [-79.893339, 23.367089],
          [-79.894481, 23.366195],
          [-79.895865, 23.366425],
          [-79.897696, 23.365701],
          [-79.8989, 23.366108]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72035",
      state: "72",
      county: "035",
      name: "Cayey County",
      censusArea: 51.934,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.051788, 23.109878],
          [-79.053208, 23.109526],
          [-79.053146, 23.107293],
          [-79.166145, 23.050063],
          [-79.22449, 23.095339],
          [-79.226805, 23.116389],
          [-79.220358, 23.120797],
          [-79.220693, 23.131684],
          [-79.216899, 23.14322],
          [-79.119213, 23.137907],
          [-79.095099, 23.169077],
          [-79.080343, 23.152146],
          [-79.065968, 23.148411],
          [-79.06949, 23.142397],
          [-79.060761, 23.116315],
          [-79.052555, 23.113069],
          [-79.051162, 23.111088],
          [-79.051788, 23.109878]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72041",
      state: "72",
      county: "041",
      name: "Cidra County",
      censusArea: 36.021,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.216899, 23.14322],
          [-79.228748, 23.159611],
          [-79.223578, 23.165038],
          [-79.242735, 23.180248],
          [-79.242623, 23.183408],
          [-79.24046, 23.184794],
          [-79.169126, 23.22429],
          [-79.117774, 23.209949],
          [-79.109374, 23.200934],
          [-79.101673, 23.183082],
          [-79.105096, 23.171544],
          [-79.102177, 23.169419],
          [-79.100635, 23.169824],
          [-79.096813, 23.168197],
          [-79.095099, 23.169077],
          [-79.119213, 23.137907],
          [-79.216899, 23.14322]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72043",
      state: "72",
      county: "043",
      name: "Coamo County",
      censusArea: 78.012,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.257527, 23.075884],
          [-79.331244, 23.015891],
          [-79.426989, 23.042627],
          [-79.42433, 23.05294],
          [-79.428167, 23.054931],
          [-79.431538, 23.063519],
          [-79.427541, 23.071666],
          [-79.430347, 23.077135],
          [-79.430314, 23.082053],
          [-79.44456, 23.176674],
          [-79.436075, 23.175501],
          [-79.427994, 23.170935],
          [-79.423649, 23.172025],
          [-79.420902, 23.166316],
          [-79.413686, 23.163337],
          [-79.373768, 23.173694],
          [-79.351258, 23.165488],
          [-79.322323, 23.165503],
          [-79.314848, 23.152088],
          [-79.257527, 23.075884]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72053",
      state: "72",
      county: "053",
      name: "Fajardo County",
      censusArea: 29.864,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.571523, 23.351635],
          [-78.569013, 23.352794],
          [-78.565744, 23.349524],
          [-78.565072, 23.347499],
          [-78.567951, 23.343341],
          [-78.571523, 23.351635]
        ],
        [
          [-78.572248, 23.381757],
          [-78.568979, 23.382125],
          [-78.566355, 23.378212],
          [-78.571787, 23.37637],
          [-78.572248, 23.381757]
        ],
        [
          [-78.587335, 23.381994],
          [-78.59165, 23.386287],
          [-78.592585, 23.390849],
          [-78.589332, 23.390516],
          [-78.584537, 23.386939],
          [-78.583571, 23.383751],
          [-78.587335, 23.381994]
        ],
        [
          [-78.753591, 23.296755],
          [-78.669636, 23.362102],
          [-78.668845, 23.361939],
          [-78.634431, 23.369835],
          [-78.627246, 23.376436],
          [-78.626527, 23.381728],
          [-78.624975, 23.386553],
          [-78.622761, 23.387771],
          [-78.618229, 23.386496],
          [-78.614891, 23.382473],
          [-78.619068, 23.367755],
          [-78.628198, 23.353711],
          [-78.63419, 23.338965],
          [-78.628047, 23.328252],
          [-78.626456, 23.298982],
          [-78.634389, 23.292349],
          [-78.635826, 23.288271],
          [-78.634893, 23.283923],
          [-78.71751, 23.267744],
          [-78.753591, 23.296755]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72055",
      state: "72",
      county: "055",
      name: "Guanica County",
      censusArea: 37.05,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.884653, 23.024816],
          [-79.863219, 22.998029],
          [-79.859471, 22.954316],
          [-79.862545, 22.952022],
          [-79.871697, 22.952707],
          [-79.88344, 22.952526],
          [-79.899639, 22.948298],
          [-79.904585, 22.950527],
          [-79.906532, 22.955356],
          [-79.906276, 22.963368],
          [-79.924529, 22.972808],
          [-79.928651, 22.970204],
          [-79.930414, 22.963127],
          [-79.916127, 22.959102],
          [-79.909483, 22.952559],
          [-79.909359, 22.94988],
          [-79.912522, 22.947446],
          [-79.930313, 22.943389],
          [-79.932636, 22.939998],
          [-79.931581, 22.9369],
          [-79.919298, 22.932062],
          [-79.923826, 22.926923],
          [-79.927261, 22.926875],
          [-79.959998, 22.940216],
          [-79.980516, 22.951648],
          [-79.98105, 22.952505],
          [-79.961935, 22.991592],
          [-79.958748, 23.032477],
          [-79.947158, 23.033439],
          [-79.935902, 23.029324],
          [-79.930811, 23.030583],
          [-79.924206, 23.027377],
          [-79.919535, 23.027692],
          [-79.910887, 23.023883],
          [-79.905108, 23.026523],
          [-79.895027, 23.023247],
          [-79.884653, 23.024816]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72059",
      state: "72",
      county: "059",
      name: "Guayanilla County",
      censusArea: 42.27,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.769049, 23.134983],
          [-79.753964, 22.99959],
          [-79.755341, 23.001203],
          [-79.764491, 23.006317],
          [-79.770307, 23.005955],
          [-79.799656, 22.99245],
          [-79.806866, 22.983786],
          [-79.807924, 22.979606],
          [-79.806903, 22.976046],
          [-79.805683, 22.975052],
          [-79.795106, 22.977438],
          [-79.789302, 22.980793],
          [-79.784953, 22.978326],
          [-79.787245, 22.972914],
          [-79.80827, 22.965635],
          [-79.8224, 22.954499],
          [-79.838584, 22.949931],
          [-79.852288, 22.955004],
          [-79.797557, 23.133445],
          [-79.788544, 23.134404],
          [-79.7844, 23.138652],
          [-79.775694, 23.133788],
          [-79.769049, 23.134983]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72067",
      state: "72",
      county: "067",
      name: "Hormigueros County",
      censusArea: 11.344,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.159733, 23.153227],
          [-80.086698, 23.15199],
          [-80.08423, 23.14844],
          [-80.087787, 23.145535],
          [-80.092501, 23.145915],
          [-80.09334, 23.141064],
          [-80.092797, 23.136883],
          [-80.09196, 23.129903],
          [-80.085329, 23.120752],
          [-80.081658, 23.11829],
          [-80.07817, 23.114952],
          [-80.07988, 23.11068],
          [-80.082935, 23.10678],
          [-80.088155, 23.106136],
          [-80.092542, 23.106115],
          [-80.100492, 23.106395],
          [-80.159733, 23.153227]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72073",
      state: "72",
      county: "073",
      name: "Jayuya County",
      censusArea: 44.533,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.552766, 23.152057],
          [-79.649467, 23.158857],
          [-79.56662, 23.298549],
          [-79.543133, 23.163098],
          [-79.543211, 23.161577],
          [-79.544836, 23.160262],
          [-79.544152, 23.158328],
          [-79.546835, 23.155019],
          [-79.54722, 23.15313],
          [-79.54816, 23.152966],
          [-79.549188, 23.152507],
          [-79.549955, 23.152516],
          [-79.550433, 23.153094],
          [-79.551267, 23.152848],
          [-79.552037, 23.152237],
          [-79.552766, 23.152057]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72005",
      state: "72",
      county: "005",
      name: "Aguadilla County",
      censusArea: 36.529,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.103468, 23.514523],
          [-80.077061, 23.488966],
          [-80.05846, 23.479982],
          [-80.056515, 23.460984],
          [-80.13221, 23.389391],
          [-80.150689, 23.392377],
          [-80.160144, 23.415587],
          [-80.159608, 23.415915],
          [-80.156599, 23.418983],
          [-80.155245, 23.424401],
          [-80.156619, 23.439562],
          [-80.161746, 23.453462],
          [-80.169011, 23.466352],
          [-80.169016, 23.478488],
          [-80.164144, 23.487396],
          [-80.14283, 23.505485],
          [-80.138249, 23.507776],
          [-80.125655, 23.511706],
          [-80.103468, 23.514523]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72079",
      state: "72",
      county: "079",
      name: "Lajas County",
      censusArea: 59.953,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.109986, 22.945806],
          [-80.109044, 23.056085],
          [-79.983337, 23.052518],
          [-79.982675, 23.041202],
          [-79.96037, 23.036519],
          [-79.958748, 23.032477],
          [-79.961935, 22.991592],
          [-79.98105, 22.952505],
          [-79.982669, 22.9551],
          [-79.982206, 22.961192],
          [-79.987287, 22.970663],
          [-79.996738, 22.972899],
          [-80.003972, 22.970799],
          [-80.014744, 22.968468],
          [-80.024522, 22.970722],
          [-80.062478, 22.973819],
          [-80.076534, 22.967759],
          [-80.089827, 22.951418],
          [-80.101468, 22.946621],
          [-80.109985, 22.945806],
          [-80.109986, 22.945806]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72081",
      state: "72",
      county: "081",
      name: "Lares County",
      censusArea: 61.45,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.826128, 23.323382],
          [-79.817271, 23.230336],
          [-79.827327, 23.171242],
          [-79.828278, 23.170427],
          [-79.830798, 23.171018],
          [-79.832474, 23.170514],
          [-79.83676, 23.170553],
          [-79.850765, 23.170357],
          [-79.862832, 23.174808],
          [-79.863848, 23.18661],
          [-79.897964, 23.187744],
          [-79.912859, 23.233009],
          [-79.907236, 23.25309],
          [-79.893339, 23.367089],
          [-79.891242, 23.368177],
          [-79.8872, 23.367001],
          [-79.885509, 23.367087],
          [-79.884952, 23.365857],
          [-79.882193, 23.36459],
          [-79.880675, 23.362451],
          [-79.880939, 23.361254],
          [-79.879144, 23.358881],
          [-79.877784, 23.358463],
          [-79.876958, 23.356829],
          [-79.873457, 23.356876],
          [-79.871446, 23.356112],
          [-79.861541, 23.356003],
          [-79.854737, 23.355335],
          [-79.852063, 23.354626],
          [-79.851685, 23.353884],
          [-79.848462, 23.353682],
          [-79.846401, 23.351174],
          [-79.842901, 23.350373],
          [-79.836853, 23.348376],
          [-79.828738, 23.344375],
          [-79.824223, 23.342998],
          [-79.824794, 23.337546],
          [-79.824852, 23.333114],
          [-79.825428, 23.327709],
          [-79.826128, 23.323382]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72089",
      state: "72",
      county: "089",
      name: "Luquillo County",
      censusArea: 25.811,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.750179, 23.38505],
          [-78.742154, 23.380459],
          [-78.733567, 23.382211],
          [-78.699069, 23.368156],
          [-78.669636, 23.362102],
          [-78.753591, 23.296755],
          [-78.754723, 23.29523],
          [-78.756027, 23.294456],
          [-78.757383, 23.293022],
          [-78.757681, 23.291596],
          [-78.758861, 23.290604],
          [-78.750179, 23.38505]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72095",
      state: "72",
      county: "095",
      name: "Maunabo County",
      censusArea: 21.066,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.910537, 22.981855],
          [-78.988523, 23.058941],
          [-78.850913, 23.011954],
          [-78.870335, 23.006597],
          [-78.875122, 23.002826],
          [-78.884937, 22.988521],
          [-78.896102, 22.99026],
          [-78.905319, 22.983974],
          [-78.910537, 22.981855]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72101",
      state: "72",
      county: "101",
      name: "Morovis County",
      censusArea: 38.871,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.377712, 23.293976],
          [-79.455703, 23.257726],
          [-79.463211, 23.371473],
          [-79.459029, 23.371249],
          [-79.451578, 23.372214],
          [-79.4438, 23.371027],
          [-79.377058, 23.344246],
          [-79.37472, 23.344189],
          [-79.374295, 23.342625],
          [-79.371731, 23.340491],
          [-79.371131, 23.337902],
          [-79.36713, 23.335119],
          [-79.365272, 23.333229],
          [-79.372457, 23.309835],
          [-79.373312, 23.301937],
          [-79.37609, 23.299733],
          [-79.377712, 23.293976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72103",
      state: "72",
      county: "103",
      name: "Maguabo County",
      censusArea: 51.661,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.739125, 23.173453],
          [-78.826063, 23.200408],
          [-78.831388, 23.214222],
          [-78.828789, 23.230243],
          [-78.822164, 23.244701],
          [-78.817246, 23.252327],
          [-78.815664, 23.262527],
          [-78.823769, 23.268738],
          [-78.82422, 23.273054],
          [-78.811424, 23.290141],
          [-78.784859, 23.294],
          [-78.766831, 23.280004],
          [-78.662185, 23.207018],
          [-78.664127, 23.207136],
          [-78.690749, 23.19499],
          [-78.694515, 23.187011],
          [-78.691021, 23.178998],
          [-78.695856, 23.179324],
          [-78.710895, 23.186963],
          [-78.712533, 23.189146],
          [-78.717999, 23.190176],
          [-78.728471, 23.185588],
          [-78.734664, 23.180368],
          [-78.738834, 23.174066],
          [-78.739125, 23.173453]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72109",
      state: "72",
      county: "109",
      name: "Patillas County",
      censusArea: 46.699,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.019539, 22.978354],
          [-79.082263, 23.032958],
          [-79.053146, 23.107293],
          [-79.053208, 23.109526],
          [-79.051788, 23.109878],
          [-79.019442, 23.102576],
          [-79.019443, 23.083723],
          [-79.010245, 23.075565],
          [-79.010266, 23.067432],
          [-79.000072, 23.060275],
          [-78.988523, 23.058941],
          [-78.910537, 22.981855],
          [-78.924738, 22.976087],
          [-78.976611, 22.967669],
          [-78.98455, 22.969411],
          [-78.985358, 22.971854],
          [-78.995185, 22.978989],
          [-79.007731, 22.980541],
          [-79.017308, 22.979583],
          [-79.019539, 22.978354]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72117",
      state: "72",
      county: "117",
      name: "Rincon County",
      censusArea: 14.288,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.182852, 23.313026],
          [-80.195908, 23.307539],
          [-80.220709, 23.3057],
          [-80.225403, 23.296648],
          [-80.226081, 23.296722],
          [-80.235137, 23.299935],
          [-80.267484, 23.353149],
          [-80.27135, 23.362329],
          [-80.268259, 23.366989],
          [-80.260671, 23.370197],
          [-80.23909, 23.375318],
          [-80.182852, 23.313026]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72119",
      state: "72",
      county: "119",
      name: "Rio Grande County",
      censusArea: 60.623,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.828457, 23.423543],
          [-78.816691, 23.410663],
          [-78.794556, 23.402845],
          [-78.787666, 23.402544],
          [-78.774937, 23.413951],
          [-78.77053, 23.41294],
          [-78.769749, 23.409473],
          [-78.771695, 23.406277],
          [-78.750455, 23.385208],
          [-78.750179, 23.38505],
          [-78.758861, 23.290604],
          [-78.761818, 23.289268],
          [-78.766831, 23.280004],
          [-78.784859, 23.294],
          [-78.811424, 23.290141],
          [-78.82422, 23.273054],
          [-78.831717, 23.271736],
          [-78.836387, 23.275246],
          [-78.867478, 23.378128],
          [-78.859635, 23.381868],
          [-78.855633, 23.395752],
          [-78.852538, 23.4017],
          [-78.843816, 23.4057],
          [-78.832623, 23.420692],
          [-78.828457, 23.423543]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72125",
      state: "72",
      county: "125",
      name: "San German County",
      censusArea: 54.498,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.086698, 23.15199],
          [-80.08592, 23.1624],
          [-80.072183, 23.173053],
          [-80.051461, 23.174053],
          [-80.044039, 23.169202],
          [-80.013844, 23.165874],
          [-80.0096, 23.158915],
          [-79.994529, 23.154774],
          [-79.977855, 23.143799],
          [-79.983337, 23.052518],
          [-80.109044, 23.056085],
          [-80.10711, 23.06178],
          [-80.09912, 23.067358],
          [-80.101267, 23.074203],
          [-80.105566, 23.073861],
          [-80.106123, 23.079693],
          [-80.104044, 23.098804],
          [-80.100492, 23.106395],
          [-80.092542, 23.106115],
          [-80.088155, 23.106136],
          [-80.082935, 23.10678],
          [-80.07988, 23.11068],
          [-80.07817, 23.114952],
          [-80.081658, 23.11829],
          [-80.085329, 23.120752],
          [-80.09196, 23.129903],
          [-80.092797, 23.136883],
          [-80.09334, 23.141064],
          [-80.092501, 23.145915],
          [-80.087787, 23.145535],
          [-80.08423, 23.14844],
          [-80.086698, 23.15199]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72131",
      state: "72",
      county: "131",
      name: "San Sebastian County",
      censusArea: 70.423,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.92202, 23.393204],
          [-79.919497, 23.378578],
          [-79.8989, 23.366108],
          [-79.897696, 23.365701],
          [-79.895865, 23.366425],
          [-79.894481, 23.366195],
          [-79.893339, 23.367089],
          [-79.907236, 23.25309],
          [-80.037935, 23.289705],
          [-80.040709, 23.300302],
          [-80.052583, 23.306655],
          [-80.027173, 23.393376],
          [-79.92202, 23.393204]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72137",
      state: "72",
      county: "137",
      name: "Toa Baja County",
      censusArea: 23.241,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.199032, 23.466163],
          [-79.192664, 23.466212],
          [-79.183886, 23.460506],
          [-79.179218, 23.455305],
          [-79.172315, 23.451462],
          [-79.159796, 23.451706],
          [-79.153037, 23.454457],
          [-79.14395, 23.459761],
          [-79.148318, 23.451163],
          [-79.15467, 23.445635],
          [-79.163774, 23.444673],
          [-79.165015, 23.437665],
          [-79.170096, 23.433586],
          [-79.169516, 23.430998],
          [-79.172653, 23.42105],
          [-79.198013, 23.414626],
          [-79.19698, 23.389289],
          [-79.234345, 23.401464],
          [-79.252554, 23.394187],
          [-79.199032, 23.466163]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72143",
      state: "72",
      county: "143",
      name: "Vega Alta County",
      censusArea: 27.728,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.31503, 23.47468],
          [-79.303663, 23.384077],
          [-79.314158, 23.380801],
          [-79.318764, 23.367411],
          [-79.365272, 23.333229],
          [-79.36713, 23.335119],
          [-79.371131, 23.337902],
          [-79.371731, 23.340491],
          [-79.374295, 23.342625],
          [-79.37472, 23.344189],
          [-79.377058, 23.344246],
          [-79.349647, 23.486335],
          [-79.337728, 23.48562],
          [-79.315477, 23.474724],
          [-79.31503, 23.47468]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72075",
      state: "72",
      county: "075",
      name: "Juana Diaz County",
      censusArea: 60.277,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.450368, 22.983226],
          [-79.454888, 22.986784],
          [-79.461342, 22.990273],
          [-79.491396, 22.990262],
          [-79.510143, 22.985618],
          [-79.540537, 22.975476],
          [-79.552766, 23.152057],
          [-79.552037, 23.152237],
          [-79.551267, 23.152848],
          [-79.550433, 23.153094],
          [-79.549955, 23.152516],
          [-79.549188, 23.152507],
          [-79.54816, 23.152966],
          [-79.54722, 23.15313],
          [-79.544281, 23.147619],
          [-79.534862, 23.152293],
          [-79.521899, 23.151954],
          [-79.528745, 23.100673],
          [-79.430314, 23.082053],
          [-79.430347, 23.077135],
          [-79.427541, 23.071666],
          [-79.431538, 23.063519],
          [-79.428167, 23.054931],
          [-79.42433, 23.05294],
          [-79.426989, 23.042627],
          [-79.450368, 22.983226]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72147",
      state: "72",
      county: "147",
      name: "Vieques County",
      censusArea: 50.768,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.308717, 23.145172],
          [-78.302295, 23.141089],
          [-78.294896, 23.14283],
          [-78.287962, 23.148097],
          [-78.275165, 23.13443],
          [-78.276214, 23.131936],
          [-78.283248, 23.132999],
          [-78.296036, 23.12799],
          [-78.322794, 23.126589],
          [-78.327184, 23.124106],
          [-78.338506, 23.112439],
          [-78.342037, 23.11138],
          [-78.350493, 23.111914],
          [-78.364733, 23.120377],
          [-78.397837, 23.110873],
          [-78.399791, 23.108832],
          [-78.411767, 23.106211],
          [-78.423765, 23.097764],
          [-78.426311, 23.093749],
          [-78.45138, 23.086096],
          [-78.45681, 23.087778],
          [-78.465849, 23.087715],
          [-78.468768, 23.092643],
          [-78.47979, 23.096352],
          [-78.507265, 23.091646],
          [-78.524209, 23.081977],
          [-78.542087, 23.081177],
          [-78.558646, 23.08566],
          [-78.569305, 23.091616],
          [-78.570628, 23.097325],
          [-78.57686, 23.103224],
          [-78.575579, 23.115669],
          [-78.546199, 23.119329],
          [-78.511712, 23.13284],
          [-78.489829, 23.135912],
          [-78.46791, 23.143767],
          [-78.437058, 23.15766],
          [-78.399517, 23.161935],
          [-78.371373, 23.157517],
          [-78.334289, 23.147761],
          [-78.313476, 23.144296],
          [-78.308717, 23.145172]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72151",
      state: "72",
      county: "151",
      name: "Yabucoa County",
      censusArea: 55.215,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.988523, 23.058941],
          [-79.000072, 23.060275],
          [-79.010266, 23.067432],
          [-79.010245, 23.075565],
          [-78.926227, 23.119963],
          [-78.900893, 23.125856],
          [-78.877615, 23.118167],
          [-78.796711, 23.069842],
          [-78.801831, 23.058527],
          [-78.809174, 23.056818],
          [-78.817107, 23.063378],
          [-78.825848, 23.057482],
          [-78.83109, 23.050664],
          [-78.834274, 23.038988],
          [-78.832429, 23.014916],
          [-78.839591, 23.015077],
          [-78.850913, 23.011954],
          [-78.988523, 23.058941]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72153",
      state: "72",
      county: "153",
      name: "Yauco County",
      censusArea: 68.192,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.859471, 22.954316],
          [-79.863219, 22.998029],
          [-79.884653, 23.024816],
          [-79.926517, 23.151267],
          [-79.83676, 23.170553],
          [-79.832474, 23.170514],
          [-79.830798, 23.171018],
          [-79.828278, 23.170427],
          [-79.827327, 23.171242],
          [-79.826278, 23.160216],
          [-79.81809, 23.150509],
          [-79.804058, 23.148056],
          [-79.803365, 23.140384],
          [-79.797557, 23.133445],
          [-79.852288, 22.955004],
          [-79.856474, 22.956553],
          [-79.859471, 22.954316]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72149",
      state: "72",
      county: "149",
      name: "Villalba County",
      censusArea: 35.637,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.521899, 23.151954],
          [-79.44456, 23.176674],
          [-79.430314, 23.082053],
          [-79.528745, 23.100673],
          [-79.521899, 23.151954]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72017",
      state: "72",
      county: "017",
      name: "Barceloneta County",
      censusArea: 18.693,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.586778, 23.484948],
          [-79.584074, 23.484287],
          [-79.565241, 23.485523],
          [-79.562916, 23.48845],
          [-79.563485, 23.490512],
          [-79.558503, 23.489987],
          [-79.53484, 23.481253],
          [-79.533487, 23.481663],
          [-79.543079, 23.405422],
          [-79.564313, 23.413309],
          [-79.588181, 23.389408],
          [-79.586778, 23.484948]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72021",
      state: "72",
      county: "021",
      name: "Bayamon County",
      censusArea: 44.324,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.143861, 23.27959],
          [-79.177099, 23.276194],
          [-79.190676, 23.25884],
          [-79.193248, 23.268138],
          [-79.200802, 23.271477],
          [-79.20128, 23.27628],
          [-79.206135, 23.27666],
          [-79.213072, 23.307003],
          [-79.205908, 23.306921],
          [-79.204734, 23.316035],
          [-79.201531, 23.328983],
          [-79.203616, 23.336673],
          [-79.197293, 23.360199],
          [-79.199568, 23.369616],
          [-79.19698, 23.389289],
          [-79.198013, 23.414626],
          [-79.172653, 23.42105],
          [-79.169516, 23.430998],
          [-79.157782, 23.420843],
          [-79.13158, 23.424656],
          [-79.143861, 23.27959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72029",
      state: "72",
      county: "029",
      name: "Canovanas County",
      censusArea: 32.865,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.919278, 23.402993],
          [-78.867478, 23.378128],
          [-78.836387, 23.275246],
          [-78.839854, 23.265696],
          [-78.846289, 23.258083],
          [-78.852616, 23.254972],
          [-78.874602, 23.253081],
          [-78.889216, 23.261757],
          [-78.918535, 23.27013],
          [-78.919278, 23.402993]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72039",
      state: "72",
      county: "039",
      name: "Ciales County",
      censusArea: 66.529,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.463211, 23.371473],
          [-79.455703, 23.257726],
          [-79.500762, 23.256011],
          [-79.543133, 23.163098],
          [-79.56662, 23.298549],
          [-79.585269, 23.322689],
          [-79.604552, 23.321306],
          [-79.606989, 23.328186],
          [-79.600229, 23.339603],
          [-79.590785, 23.33806],
          [-79.573822, 23.342517],
          [-79.552734, 23.340438],
          [-79.533623, 23.351333],
          [-79.528304, 23.350564],
          [-79.511776, 23.362003],
          [-79.494806, 23.366445],
          [-79.473346, 23.367604],
          [-79.463211, 23.371473]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72051",
      state: "72",
      county: "051",
      name: "Dorado County",
      censusArea: 23.089,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.303663, 23.384077],
          [-79.31503, 23.47468],
          [-79.291225, 23.472347],
          [-79.283675, 23.472203],
          [-79.276599, 23.478129],
          [-79.269799, 23.480281],
          [-79.258015, 23.476906],
          [-79.251547, 23.472464],
          [-79.241797, 23.46874],
          [-79.220148, 23.466],
          [-79.199032, 23.466163],
          [-79.252554, 23.394187],
          [-79.271288, 23.383718],
          [-79.303663, 23.384077]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72063",
      state: "72",
      county: "063",
      name: "Gurabo County",
      censusArea: 27.887,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.021747, 23.305034],
          [-78.942429, 23.293469],
          [-78.939296, 23.286771],
          [-78.924175, 23.272622],
          [-78.918535, 23.27013],
          [-78.951019, 23.229292],
          [-78.971036, 23.229239],
          [-78.988847, 23.220472],
          [-78.99765, 23.206992],
          [-79.021747, 23.305034]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72077",
      state: "72",
      county: "077",
      name: "Juncos County",
      censusArea: 26.489,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.928341, 23.142845],
          [-78.951019, 23.229292],
          [-78.918535, 23.27013],
          [-78.889216, 23.261757],
          [-78.874602, 23.253081],
          [-78.852616, 23.254972],
          [-78.928341, 23.142845]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72085",
      state: "72",
      county: "085",
      name: "Las Piedras County",
      censusArea: 33.88,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.836387, 23.275246],
          [-78.831717, 23.271736],
          [-78.82422, 23.273054],
          [-78.823769, 23.268738],
          [-78.815664, 23.262527],
          [-78.817246, 23.252327],
          [-78.822164, 23.244701],
          [-78.828789, 23.230243],
          [-78.831388, 23.214222],
          [-78.826063, 23.200408],
          [-78.877615, 23.118167],
          [-78.900893, 23.125856],
          [-78.926227, 23.119963],
          [-78.927429, 23.121821],
          [-78.924448, 23.135673],
          [-78.928341, 23.142845],
          [-78.852616, 23.254972],
          [-78.846289, 23.258083],
          [-78.839854, 23.265696],
          [-78.836387, 23.275246]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72099",
      state: "72",
      county: "099",
      name: "Moca County",
      censusArea: 50.344,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.027173, 23.393376],
          [-80.052583, 23.306655],
          [-80.121432, 23.323938],
          [-80.130167, 23.317927],
          [-80.130389, 23.325143],
          [-80.134138, 23.330531],
          [-80.131025, 23.334689],
          [-80.134515, 23.346514],
          [-80.128124, 23.353859],
          [-80.128629, 23.365545],
          [-80.126434, 23.371203],
          [-80.13221, 23.389391],
          [-80.056515, 23.460984],
          [-80.052813, 23.448354],
          [-80.027173, 23.393376]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72111",
      state: "72",
      county: "111",
      name: "Penuelas County",
      censusArea: 44.617,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.69407, 23.130534],
          [-79.699115, 22.977568],
          [-79.709856, 22.982109],
          [-79.713394, 22.987763],
          [-79.716957, 22.990344],
          [-79.731118, 22.991658],
          [-79.746248, 22.990349],
          [-79.750427, 22.995443],
          [-79.753964, 22.99959],
          [-79.769049, 23.134983],
          [-79.69407, 23.130534]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72123",
      state: "72",
      county: "123",
      name: "Salinas County",
      censusArea: 69.365,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.338152, 22.976492],
          [-79.342515, 22.993786],
          [-79.331244, 23.015891],
          [-79.257527, 23.075884],
          [-79.242238, 23.077148],
          [-79.24289, 23.079405],
          [-79.2409, 23.081894],
          [-79.239785, 23.084691],
          [-79.240053, 23.093186],
          [-79.234899, 23.094213],
          [-79.23092, 23.095925],
          [-79.229539, 23.09537],
          [-79.22449, 23.095339],
          [-79.166145, 23.050063],
          [-79.206207, 22.96305],
          [-79.206807, 22.963307],
          [-79.215355, 22.959376],
          [-79.218081, 22.95729],
          [-79.231519, 22.943912],
          [-79.229181, 22.934651],
          [-79.232013, 22.931154],
          [-79.252737, 22.934574],
          [-79.260684, 22.936083],
          [-79.270905, 22.947098],
          [-79.275651, 22.94826],
          [-79.290782, 22.946491],
          [-79.297679, 22.959148],
          [-79.31695, 22.976683],
          [-79.323659, 22.978536],
          [-79.338152, 22.976492]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72139",
      state: "72",
      county: "139",
      name: "Trujillo Alto County",
      censusArea: 20.764,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.999556, 23.380065],
          [-78.942429, 23.293469],
          [-79.021747, 23.305034],
          [-79.027073, 23.310201],
          [-79.031631, 23.308877],
          [-79.041535, 23.312344],
          [-78.999556, 23.380065]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72001",
      state: "72",
      county: "001",
      name: "Adjuntas County",
      censusArea: 66.69,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.670494, 23.152831],
          [-79.676951, 23.129977],
          [-79.69407, 23.130534],
          [-79.769049, 23.134983],
          [-79.775694, 23.133788],
          [-79.7844, 23.138652],
          [-79.788544, 23.134404],
          [-79.797557, 23.133445],
          [-79.803365, 23.140384],
          [-79.804058, 23.148056],
          [-79.81809, 23.150509],
          [-79.826278, 23.160216],
          [-79.827327, 23.171242],
          [-79.817271, 23.230336],
          [-79.69373, 23.216891],
          [-79.670494, 23.152831]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72007",
      state: "72",
      county: "007",
      name: "Aguas Buenas County",
      censusArea: 30.085,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.143861, 23.27959],
          [-79.107787, 23.277874],
          [-79.086465, 23.285094],
          [-79.080475, 23.297295],
          [-79.072824, 23.297841],
          [-79.066103, 23.303321],
          [-79.117774, 23.209949],
          [-79.169126, 23.22429],
          [-79.169141, 23.226013],
          [-79.176409, 23.230251],
          [-79.174947, 23.233095],
          [-79.178855, 23.23754],
          [-79.179052, 23.246969],
          [-79.187298, 23.25379],
          [-79.190676, 23.25884],
          [-79.177099, 23.276194],
          [-79.143861, 23.27959]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72011",
      state: "72",
      county: "011",
      name: "Anasco County",
      censusArea: 39.285,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.082002, 23.254946],
          [-80.187873, 23.266874],
          [-80.189971, 23.281015],
          [-80.196056, 23.290443],
          [-80.209963, 23.294974],
          [-80.225403, 23.296648],
          [-80.220709, 23.3057],
          [-80.195908, 23.307539],
          [-80.182852, 23.313026],
          [-80.172608, 23.318232],
          [-80.16099, 23.316072],
          [-80.148707, 23.322239],
          [-80.130167, 23.317927],
          [-80.121432, 23.323938],
          [-80.052583, 23.306655],
          [-80.040709, 23.300302],
          [-80.037935, 23.289705],
          [-80.040228, 23.271985],
          [-80.061828, 23.270082],
          [-80.082002, 23.254946]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72023",
      state: "72",
      county: "023",
      name: "Cabo Rojo County",
      censusArea: 70.374,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.100492, 23.106395],
          [-80.104044, 23.098804],
          [-80.106123, 23.079693],
          [-80.105566, 23.073861],
          [-80.101267, 23.074203],
          [-80.09912, 23.067358],
          [-80.10711, 23.06178],
          [-80.109044, 23.056085],
          [-80.109986, 22.945806],
          [-80.128251, 22.948153],
          [-80.133733, 22.951919],
          [-80.167031, 22.963073],
          [-80.178566, 22.964792],
          [-80.183508, 22.962706],
          [-80.188717, 22.950989],
          [-80.187474, 22.946252],
          [-80.183694, 22.937982],
          [-80.183457, 22.931135],
          [-80.194785, 22.932826],
          [-80.196924, 22.935651],
          [-80.197273, 22.937461],
          [-80.197517, 22.941514],
          [-80.197668, 22.943549],
          [-80.198988, 22.94782],
          [-80.200973, 22.949896],
          [-80.210034, 22.953595],
          [-80.212101, 22.956027],
          [-80.21433, 22.962436],
          [-80.215271, 22.983464],
          [-80.211973, 22.992993],
          [-80.207694, 22.998019],
          [-80.177893, 23.008882],
          [-80.174299, 23.011149],
          [-80.172397, 23.014906],
          [-80.172138, 23.021422],
          [-80.173761, 23.024548],
          [-80.193269, 23.03185],
          [-80.209887, 23.035439],
          [-80.196694, 23.066491],
          [-80.190656, 23.064269],
          [-80.184589, 23.06775],
          [-80.183938, 23.069914],
          [-80.186465, 23.074195],
          [-80.192999, 23.076877],
          [-80.198212, 23.076828],
          [-80.199314, 23.091135],
          [-80.19529, 23.096149],
          [-80.183921, 23.103683],
          [-80.182182, 23.108507],
          [-80.176554, 23.151046],
          [-80.178618, 23.159318],
          [-80.180822, 23.168055],
          [-80.180701, 23.168182],
          [-80.173015, 23.162471],
          [-80.171331, 23.159301],
          [-80.166165, 23.158382],
          [-80.166722, 23.1555],
          [-80.1622, 23.152484],
          [-80.159733, 23.153227],
          [-80.100492, 23.106395]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72033",
      state: "72",
      county: "033",
      name: "Catano County",
      censusArea: 4.845,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.169516, 23.430998],
          [-79.170096, 23.433586],
          [-79.165015, 23.437665],
          [-79.163774, 23.444673],
          [-79.15467, 23.445635],
          [-79.148318, 23.451163],
          [-79.14395, 23.459761],
          [-79.139572, 23.462317],
          [-79.139451, 23.462387],
          [-79.139443, 23.462315],
          [-79.138532, 23.453305],
          [-79.133085, 23.445881],
          [-79.127938, 23.444632],
          [-79.125198, 23.451209],
          [-79.124284, 23.456324],
          [-79.123188, 23.45943],
          [-79.123343, 23.460363],
          [-79.109703, 23.447202],
          [-79.108281, 23.438902],
          [-79.115647, 23.439973],
          [-79.13158, 23.424656],
          [-79.157782, 23.420843],
          [-79.169516, 23.430998]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72037",
      state: "72",
      county: "037",
      name: "Ceiba County",
      censusArea: 29.035,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.582967, 23.246749],
          [-78.591954, 23.248653],
          [-78.59494, 23.248653],
          [-78.59586, 23.247792],
          [-78.598153, 23.247595],
          [-78.599627, 23.255699],
          [-78.584892, 23.256376],
          [-78.582967, 23.246749]
        ],
        [
          [-78.634893, 23.283923],
          [-78.630833, 23.264989],
          [-78.623111, 23.248012],
          [-78.597618, 23.234289],
          [-78.589947, 23.228225],
          [-78.593795, 23.224059],
          [-78.615981, 23.227389],
          [-78.626731, 23.235484],
          [-78.638181, 23.229121],
          [-78.637565, 23.224444],
          [-78.628414, 23.205149],
          [-78.635281, 23.199975],
          [-78.639688, 23.205656],
          [-78.662185, 23.207018],
          [-78.766831, 23.280004],
          [-78.761818, 23.289268],
          [-78.758861, 23.290604],
          [-78.757681, 23.291596],
          [-78.757383, 23.293022],
          [-78.756027, 23.294456],
          [-78.754723, 23.29523],
          [-78.753591, 23.296755],
          [-78.71751, 23.267744],
          [-78.634893, 23.283923]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72047",
      state: "72",
      county: "047",
      name: "Corozal County",
      censusArea: 42.573,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.377712, 23.293976],
          [-79.37609, 23.299733],
          [-79.373312, 23.301937],
          [-79.372457, 23.309835],
          [-79.365272, 23.333229],
          [-79.318764, 23.367411],
          [-79.306194, 23.369688],
          [-79.289035, 23.353719],
          [-79.278076, 23.329975],
          [-79.313299, 23.249324],
          [-79.326297, 23.247884],
          [-79.327034, 23.244191],
          [-79.340121, 23.238689],
          [-79.350793, 23.241235],
          [-79.382452, 23.279242],
          [-79.377712, 23.293976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72049",
      state: "72",
      county: "049",
      name: "Culebra County",
      censusArea: 11.624,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.280764, 23.288274],
          [-78.283269, 23.280214],
          [-78.287257, 23.277744],
          [-78.300238, 23.294404],
          [-78.30169, 23.300126],
          [-78.299701, 23.302181],
          [-78.296515, 23.302299],
          [-78.288941, 23.299977],
          [-78.280764, 23.288274]
        ],
        [
          [-78.316371, 23.30966],
          [-78.316885, 23.314047],
          [-78.322785, 23.321157],
          [-78.327318, 23.323666],
          [-78.342068, 23.34529],
          [-78.335701, 23.349535],
          [-78.329334, 23.341955],
          [-78.321754, 23.338316],
          [-78.309833, 23.337973],
          [-78.304409, 23.332054],
          [-78.298328, 23.330529],
          [-78.281657, 23.32937],
          [-78.277319, 23.332582],
          [-78.253174, 23.318524],
          [-78.245095, 23.309668],
          [-78.244424, 23.303493],
          [-78.249857, 23.296691],
          [-78.260282, 23.290823],
          [-78.263937, 23.290685],
          [-78.276037, 23.296067],
          [-78.277099, 23.298978],
          [-78.276007, 23.302613],
          [-78.280502, 23.306202],
          [-78.302536, 23.310488],
          [-78.30216, 23.305379],
          [-78.302426, 23.303145],
          [-78.307027, 23.301636],
          [-78.316371, 23.30966]
        ],
        [
          [-78.3277, 23.295843],
          [-78.331398, 23.29611],
          [-78.337451, 23.308308],
          [-78.330188, 23.315998],
          [-78.327821, 23.30897],
          [-78.3277, 23.295843]
        ],
        [
          [-78.228485, 23.323013],
          [-78.221568, 23.320959],
          [-78.22111, 23.311984],
          [-78.222853, 23.310464],
          [-78.230025, 23.311274],
          [-78.231933, 23.320339],
          [-78.228485, 23.323013]
        ],
        [
          [-78.266169, 23.341751],
          [-78.255933, 23.342117],
          [-78.245756, 23.334707],
          [-78.24721, 23.333345],
          [-78.259612, 23.334147],
          [-78.265558, 23.339392],
          [-78.266169, 23.341751]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72057",
      state: "72",
      county: "057",
      name: "Guayama County",
      censusArea: 64.992,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.206207, 22.96305],
          [-79.166145, 23.050063],
          [-79.053146, 23.107293],
          [-79.082263, 23.032958],
          [-79.095209, 23.022186],
          [-79.082029, 23.010271],
          [-79.07161, 22.993732],
          [-79.069979, 22.966357],
          [-79.08141, 22.966552],
          [-79.116194, 22.949141],
          [-79.127009, 22.946953],
          [-79.140661, 22.94102],
          [-79.147912, 22.933963],
          [-79.155387, 22.929406],
          [-79.159742, 22.928613],
          [-79.161232, 22.931747],
          [-79.175626, 22.933565],
          [-79.186914, 22.935363],
          [-79.189726, 22.933936],
          [-79.200174, 22.929515],
          [-79.206961, 22.932268],
          [-79.213374, 22.944614],
          [-79.202655, 22.944753],
          [-79.185554, 22.940997],
          [-79.179548, 22.943727],
          [-79.174839, 22.948214],
          [-79.176814, 22.950438],
          [-79.206207, 22.96305]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72065",
      state: "72",
      county: "065",
      name: "Hatillo County",
      censusArea: 41.781,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.764893, 23.484097],
          [-79.768129, 23.477654],
          [-79.770072, 23.325013],
          [-79.770458, 23.320614],
          [-79.826128, 23.323382],
          [-79.825428, 23.327709],
          [-79.824852, 23.333114],
          [-79.824794, 23.337546],
          [-79.824223, 23.342998],
          [-79.836635, 23.487701],
          [-79.79932, 23.492775],
          [-79.780311, 23.491411],
          [-79.764893, 23.484097]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72087",
      state: "72",
      county: "087",
      name: "Loiza County",
      censusArea: 19.366,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-78.992793, 23.458102],
          [-78.992349, 23.460024],
          [-78.99079, 23.460419],
          [-78.958492, 23.451354],
          [-78.92567, 23.444881],
          [-78.916843, 23.444619],
          [-78.907756, 23.446893],
          [-78.904988, 23.450926],
          [-78.878683, 23.438322],
          [-78.838825, 23.431865],
          [-78.831476, 23.426849],
          [-78.828457, 23.423543],
          [-78.832623, 23.420692],
          [-78.843816, 23.4057],
          [-78.852538, 23.4017],
          [-78.855633, 23.395752],
          [-78.859635, 23.381868],
          [-78.867478, 23.378128],
          [-78.919278, 23.402993],
          [-78.992793, 23.458102]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72091",
      state: "72",
      county: "091",
      name: "Manati County",
      censusArea: 45.128,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.463211, 23.371473],
          [-79.473346, 23.367604],
          [-79.494806, 23.366445],
          [-79.511776, 23.362003],
          [-79.528304, 23.350564],
          [-79.533623, 23.351333],
          [-79.531299, 23.382165],
          [-79.543079, 23.405422],
          [-79.533487, 23.481663],
          [-79.529476, 23.482877],
          [-79.511609, 23.476848],
          [-79.470292, 23.46907],
          [-79.456486, 23.46892],
          [-79.449184, 23.470991],
          [-79.441852, 23.479751],
          [-79.439961, 23.485525],
          [-79.438813, 23.485713],
          [-79.4438, 23.371027],
          [-79.451578, 23.372214],
          [-79.459029, 23.371249],
          [-79.463211, 23.371473]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72097",
      state: "72",
      county: "097",
      name: "Mayaguez County",
      censusArea: 77.645,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.187873, 23.266874],
          [-80.082002, 23.254946],
          [-80.019836, 23.195471],
          [-80.024372, 23.184668],
          [-80.034521, 23.185519],
          [-80.051461, 23.174053],
          [-80.072183, 23.173053],
          [-80.08592, 23.1624],
          [-80.086698, 23.15199],
          [-80.159733, 23.153227],
          [-80.1622, 23.152484],
          [-80.166722, 23.1555],
          [-80.166165, 23.158382],
          [-80.171331, 23.159301],
          [-80.173015, 23.162471],
          [-80.180701, 23.168182],
          [-80.155185, 23.195001],
          [-80.152665, 23.203493],
          [-80.158001, 23.216719],
          [-80.173, 23.230666],
          [-80.175429, 23.248008],
          [-80.187843, 23.266671],
          [-80.187873, 23.266874]
        ],
        [
          [-80.89174, 23.11397],
          [-80.887099, 23.112574],
          [-80.87643, 23.114157],
          [-80.869804, 23.118851],
          [-80.861548, 23.122144],
          [-80.848245, 23.10832],
          [-80.843202, 23.094858],
          [-80.843615, 23.085099],
          [-80.845293, 23.081938],
          [-80.853098, 23.078195],
          [-80.865598, 23.06544],
          [-80.871462, 23.0578],
          [-80.895921, 23.052342],
          [-80.904431, 23.05913],
          [-80.918778, 23.063116],
          [-80.927841, 23.068572],
          [-80.940799, 23.079716],
          [-80.934479, 23.111306],
          [-80.932185, 23.113221],
          [-80.91088, 23.119668],
          [-80.89174, 23.11397]
        ],
        [
          [-80.950099, 23.154235],
          [-80.954119, 23.154576],
          [-80.952974, 23.157663],
          [-80.949873, 23.157015],
          [-80.950099, 23.154235]
        ],
        [
          [-80.477976, 23.378945],
          [-80.485499, 23.382224],
          [-80.489346, 23.387441],
          [-80.482204, 23.39147],
          [-80.477922, 23.390544],
          [-80.472368, 23.382801],
          [-80.477976, 23.378945]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72107",
      state: "72",
      county: "107",
      name: "Orocovis County",
      censusArea: 63.62,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.377712, 23.293976],
          [-79.382452, 23.279242],
          [-79.350793, 23.241235],
          [-79.373768, 23.173694],
          [-79.413686, 23.163337],
          [-79.420902, 23.166316],
          [-79.423649, 23.172025],
          [-79.427994, 23.170935],
          [-79.436075, 23.175501],
          [-79.44456, 23.176674],
          [-79.521899, 23.151954],
          [-79.534862, 23.152293],
          [-79.544281, 23.147619],
          [-79.54722, 23.15313],
          [-79.546835, 23.155019],
          [-79.544152, 23.158328],
          [-79.544836, 23.160262],
          [-79.543211, 23.161577],
          [-79.543133, 23.163098],
          [-79.500762, 23.256011],
          [-79.455703, 23.257726],
          [-79.377712, 23.293976]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72113",
      state: "72",
      county: "113",
      name: "Ponce County",
      censusArea: 114.762,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.699115, 22.977568],
          [-79.69407, 23.130534],
          [-79.676951, 23.129977],
          [-79.670494, 23.152831],
          [-79.669569, 23.152267],
          [-79.66423, 23.153584],
          [-79.662885, 23.155415],
          [-79.660323, 23.156855],
          [-79.65675, 23.156597],
          [-79.653538, 23.159068],
          [-79.650628, 23.158501],
          [-79.649467, 23.158857],
          [-79.552766, 23.152057],
          [-79.540537, 22.975476],
          [-79.583233, 22.961229],
          [-79.589658, 22.969386],
          [-79.594392, 22.970682],
          [-79.605035, 22.969015],
          [-79.623788, 22.98105],
          [-79.631944, 22.982746],
          [-79.645651, 22.98026],
          [-79.657797, 22.974605],
          [-79.664391, 22.968259],
          [-79.672819, 22.966451],
          [-79.699115, 22.977568]
        ],
        [
          [-79.523478, 22.896176],
          [-79.513454, 22.902857],
          [-79.509783, 22.900466],
          [-79.509525, 22.898552],
          [-79.515349, 22.8927],
          [-79.528572, 22.884813],
          [-79.526072, 22.892937],
          [-79.523478, 22.896176]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72121",
      state: "72",
      county: "121",
      name: "Sabana Grande County",
      censusArea: 35.833,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.958748, 23.032477],
          [-79.96037, 23.036519],
          [-79.982675, 23.041202],
          [-79.983337, 23.052518],
          [-79.977855, 23.143799],
          [-79.97554, 23.143395],
          [-79.971866, 23.14154],
          [-79.963939, 23.148212],
          [-79.960424, 23.145907],
          [-79.952093, 23.146112],
          [-79.943554, 23.148979],
          [-79.939065, 23.147855],
          [-79.926517, 23.151267],
          [-79.884653, 23.024816],
          [-79.895027, 23.023247],
          [-79.905108, 23.026523],
          [-79.910887, 23.023883],
          [-79.919535, 23.027692],
          [-79.924206, 23.027377],
          [-79.930811, 23.030583],
          [-79.935902, 23.029324],
          [-79.947158, 23.033439],
          [-79.958748, 23.032477]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72127",
      state: "72",
      county: "127",
      name: "San Juan County",
      censusArea: 47.847,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.036559, 23.450216],
          [-78.999556, 23.380065],
          [-79.041535, 23.312344],
          [-79.047827, 23.306885],
          [-79.045336, 23.299418],
          [-79.052729, 23.303647],
          [-79.066103, 23.303321],
          [-79.072824, 23.297841],
          [-79.080475, 23.297295],
          [-79.108281, 23.438902],
          [-79.109703, 23.447202],
          [-79.123343, 23.460363],
          [-79.125015, 23.470435],
          [-79.118338, 23.469581],
          [-79.092098, 23.466535],
          [-79.083254, 23.462022],
          [-79.073987, 23.4581],
          [-79.043272, 23.453655],
          [-79.03944, 23.454441],
          [-79.036559, 23.450216]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72133",
      state: "72",
      county: "133",
      name: "Santa Isabel County",
      censusArea: 34.023,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.331244, 23.015891],
          [-79.342515, 22.993786],
          [-79.338152, 22.976492],
          [-79.33839, 22.976458],
          [-79.362511, 22.968231],
          [-79.365098, 22.964832],
          [-79.368777, 22.957717],
          [-79.371591, 22.951469],
          [-79.385059, 22.939004],
          [-79.391227, 22.945819],
          [-79.398945, 22.950925],
          [-79.412131, 22.957286],
          [-79.445481, 22.979379],
          [-79.450368, 22.983226],
          [-79.426989, 23.042627],
          [-79.331244, 23.015891]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72141",
      state: "72",
      county: "141",
      name: "Utuado County",
      censusArea: 113.529,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.670494, 23.152831],
          [-79.69373, 23.216891],
          [-79.817271, 23.230336],
          [-79.826128, 23.323382],
          [-79.770458, 23.320614],
          [-79.770072, 23.325013],
          [-79.606989, 23.328186],
          [-79.604552, 23.321306],
          [-79.585269, 23.322689],
          [-79.56662, 23.298549],
          [-79.649467, 23.158857],
          [-79.650628, 23.158501],
          [-79.653538, 23.159068],
          [-79.65675, 23.156597],
          [-79.660323, 23.156855],
          [-79.662885, 23.155415],
          [-79.66423, 23.153584],
          [-79.669569, 23.152267],
          [-79.670494, 23.152831]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72071",
      state: "72",
      county: "071",
      name: "Isabela County",
      censusArea: 55.304,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.957517, 23.489171],
          [-79.92202, 23.393204],
          [-80.027173, 23.393376],
          [-80.052813, 23.448354],
          [-80.056515, 23.460984],
          [-80.05846, 23.479982],
          [-80.077061, 23.488966],
          [-80.103468, 23.514523],
          [-80.093752, 23.515757],
          [-80.07929, 23.513256],
          [-80.020276, 23.510603],
          [-79.988958, 23.497724],
          [-79.95954, 23.489878],
          [-79.957733, 23.489129],
          [-79.957517, 23.489171]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72003",
      state: "72",
      county: "003",
      name: "Aguada County",
      censusArea: 30.851,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-80.182852, 23.313026],
          [-80.23909, 23.375318],
          [-80.226744, 23.378247],
          [-80.216998, 23.382078],
          [-80.202167, 23.389908],
          [-80.160144, 23.415587],
          [-80.150689, 23.392377],
          [-80.13221, 23.389391],
          [-80.126434, 23.371203],
          [-80.128629, 23.365545],
          [-80.128124, 23.353859],
          [-80.134515, 23.346514],
          [-80.131025, 23.334689],
          [-80.134138, 23.330531],
          [-80.130389, 23.325143],
          [-80.130167, 23.317927],
          [-80.148707, 23.322239],
          [-80.16099, 23.316072],
          [-80.172608, 23.318232],
          [-80.182852, 23.313026]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72009",
      state: "72",
      county: "009",
      name: "Aibonito County",
      censusArea: 31.311,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.22449, 23.095339],
          [-79.229539, 23.09537],
          [-79.23092, 23.095925],
          [-79.234899, 23.094213],
          [-79.240053, 23.093186],
          [-79.239785, 23.084691],
          [-79.2409, 23.081894],
          [-79.24289, 23.079405],
          [-79.242238, 23.077148],
          [-79.257527, 23.075884],
          [-79.314848, 23.152088],
          [-79.242735, 23.180248],
          [-79.223578, 23.165038],
          [-79.228748, 23.159611],
          [-79.216899, 23.14322],
          [-79.220693, 23.131684],
          [-79.220358, 23.120797],
          [-79.226805, 23.116389],
          [-79.22449, 23.095339]
        ]
      ]
    }
  },
  {
    type: "Feature",
    properties: {
      geo_id: "0500000US72015",
      state: "72",
      county: "015",
      name: "Arroyo County",
      censusArea: 15.008,
      stateName: "Puerto Rico"
    },
    geometry: {
      type: "Polygon",
      coordinates: [
        [
          [-79.082263, 23.032958],
          [-79.019539, 22.978354],
          [-79.024, 22.975896],
          [-79.046585, 22.954853],
          [-79.049033, 22.954561],
          [-79.058217, 22.959238],
          [-79.068678, 22.966335],
          [-79.069979, 22.966357],
          [-79.07161, 22.993732],
          [-79.082029, 23.010271],
          [-79.095209, 23.022186],
          [-79.082263, 23.032958]
        ]
      ]
    }
  }
];

// src/components/MapComponent/MapLayers/LayersData/stateMarkers.json
var stateMarkers = [
  {
    id: "01",
    name: "Alabama",
    postal: "AL",
    position: {
      lat: 33,
      lng: -86.902298
    }
  },
  {
    id: "02",
    name: "Alaska",
    postal: "AK",
    position: {
      lat: 21.12474693219995,
      lng: -110.49121093750001
    }
  },
  {
    id: "04",
    name: "Arizona",
    postal: "AZ",
    position: {
      lat: 34.548927,
      lng: -111.793735
    }
  },
  {
    id: "05",
    name: "Arkansas",
    postal: "AR",
    position: {
      lat: 34.799999,
      lng: -92.699997
    }
  },
  {
    id: "06",
    name: "California",
    postal: "CA",
    position: {
      lat: 36.778259,
      lng: -119.417931
    }
  },
  {
    id: "08",
    name: "Colorado",
    postal: "CO",
    position: {
      lat: 39.1,
      lng: -105.658887
    }
  },
  {
    id: "09",
    name: "Connecticut",
    postal: "CT",
    position: {
      lat: 41.65,
      lng: -72.699997
    }
  },
  {
    id: "10",
    name: "Delaware",
    postal: "DE",
    position: {
      lat: 39,
      lng: -75.55
    }
  },
  {
    id: "11",
    name: "District of Columbia",
    postal: "DC",
    position: {
      lat: 38.745753,
      lng: -76.941273
    }
  },
  {
    id: "12",
    name: "Florida",
    postal: "FL",
    position: {
      lat: 28.70171810699327,
      lng: -81.63032273110946
    }
  },
  {
    id: "13",
    name: "Georgia",
    postal: "GA",
    position: {
      lat: 33,
      lng: -83.541162
    }
  },
  {
    id: "15",
    name: "Hawaii",
    postal: "HI",
    position: {
      lat: 20.041755,
      lng: -90
    }
  },
  {
    id: "16",
    name: "Idaho",
    postal: "ID",
    position: {
      lat: 44.068203,
      lng: -114.342043
    }
  },
  {
    id: "17",
    name: "Illinois",
    postal: "IL",
    position: {
      lat: 40.17,
      lng: -89.1
    }
  },
  {
    id: "18",
    name: "Indiana",
    postal: "IN",
    position: {
      lat: 40.173502,
      lng: -86.226976
    }
  },
  {
    id: "19",
    name: "Iowa",
    postal: "IA",
    position: {
      lat: 42.032974,
      lng: -93.581543
    }
  },
  {
    id: "20",
    name: "Kansas",
    postal: "KS",
    position: {
      lat: 38.5,
      lng: -98.5
    }
  },
  {
    id: "21",
    name: "Kentucky",
    postal: "KY",
    position: {
      lat: 37.539333,
      lng: -85.57002
    }
  },
  {
    id: "22",
    name: "Louisiana",
    postal: "LA",
    position: {
      lat: 31.39183,
      lng: -92.629102
    }
  },
  {
    id: "23",
    name: "Maine",
    postal: "ME",
    position: {
      lat: 45.367584,
      lng: -69.572168
    }
  },
  {
    id: "24",
    name: "Maryland",
    postal: "MD",
    position: {
      lat: 39.345753,
      lng: -76.941273
    }
  },
  {
    id: "25",
    name: "Massachusetts",
    postal: "MA",
    position: {
      lat: 42.387211,
      lng: -71.92439
    }
  },
  {
    id: "26",
    name: "Michigan",
    postal: "MI",
    position: {
      lat: 43.582205,
      lng: -85.006836
    }
  },
  {
    id: "27",
    name: "Minnesota",
    postal: "MN",
    position: {
      lat: 46.39241,
      lng: -94.63623
    }
  },
  {
    id: "28",
    name: "Mississippi",
    postal: "MS",
    position: {
      lat: 33,
      lng: -89.75
    }
  },
  {
    id: "29",
    name: "Missouri",
    postal: "MO",
    position: {
      lat: 38.5,
      lng: -92.60376
    }
  },
  {
    id: "30",
    name: "Montana",
    postal: "MT",
    position: {
      lat: 46.96526,
      lng: -109.533691
    }
  },
  {
    id: "31",
    name: "Nebraska",
    postal: "NE",
    position: {
      lat: 41.5,
      lng: -100
    }
  },
  {
    id: "32",
    name: "Nevada",
    postal: "NV",
    position: {
      lat: 39.876019,
      lng: -117.024121
    }
  },
  {
    id: "33",
    name: "New Hampshire",
    postal: "NH",
    position: {
      lat: 43.5,
      lng: -71.65
    }
  },
  {
    id: "34",
    name: "New Jersey",
    postal: "NJ",
    position: {
      lat: 39.833851,
      lng: -74.671826
    }
  },
  {
    id: "35",
    name: "New Mexico",
    postal: "NM",
    position: {
      lat: 34.548927,
      lng: -106.178066
    }
  },
  {
    id: "36",
    name: "New York",
    postal: "NY",
    position: {
      lat: 43,
      lng: -75
    }
  },
  {
    id: "37",
    name: "North Carolina",
    postal: "NC",
    position: {
      lat: 35.782169,
      lng: -79.393457
    }
  },
  {
    id: "38",
    name: "North Dakota",
    postal: "ND",
    position: {
      lat: 47.550589,
      lng: -100.437012
    }
  },
  {
    id: "39",
    name: "Ohio",
    postal: "OH",
    position: {
      lat: 40.367474,
      lng: -82.996216
    }
  },
  {
    id: "40",
    name: "Oklahoma",
    postal: "OK",
    position: {
      lat: 35.584621,
      lng: -97.221387
    }
  },
  {
    id: "41",
    name: "Oregon",
    postal: "OR",
    position: {
      lat: 44,
      lng: -120.5
    }
  },
  {
    id: "42",
    name: "Pennsylvania",
    postal: "PA",
    position: {
      lat: 40.853323,
      lng: -77.654527
    }
  },
  {
    id: "44",
    name: "Rhode Island",
    postal: "RI",
    position: {
      lat: 41.742325,
      lng: -71.582332
    }
  },
  {
    id: "45",
    name: "South Carolina",
    postal: "SC",
    position: {
      lat: 33.836082,
      lng: -80.863727
    }
  },
  {
    id: "46",
    name: "South Dakota",
    postal: "SD",
    position: {
      lat: 44.5,
      lng: -100.5
    }
  },
  {
    id: "47",
    name: "Tennessee",
    postal: "TN",
    position: {
      lat: 35.860119,
      lng: -86.660156
    }
  },
  {
    id: "48",
    name: "Texas",
    postal: "TX",
    position: {
      lat: 31.5,
      lng: -99.3
    }
  },
  {
    id: "49",
    name: "Utah",
    postal: "UT",
    position: {
      lat: 39.1,
      lng: -111.550684
    }
  },
  {
    id: "50",
    name: "Vermont",
    postal: "VT",
    position: {
      lat: 44,
      lng: -72.799997
    }
  },
  {
    id: "51",
    name: "Virginia",
    postal: "VA",
    position: {
      lat: 37.62171403263375,
      lng: -78.706787109375
    }
  },
  {
    id: "53",
    name: "Washington",
    postal: "WA",
    position: {
      lat: 47.550589,
      lng: -120.340135
    }
  },
  {
    id: "54",
    name: "West Virginia",
    postal: "WV",
    position: {
      lat: 39,
      lng: -80.5
    }
  },
  {
    id: "55",
    name: "Wisconsin",
    postal: "WI",
    position: {
      lat: 44.5,
      lng: -90
    }
  },
  {
    id: "56",
    name: "Wyoming",
    postal: "WY",
    position: {
      lat: 43.07597,
      lng: -107.690283
    }
  },
  {
    id: "72",
    name: "Puerto Rico",
    postal: "PR",
    position: {
      lat: 22.5,
      lng: -79.5
    }
  }
];
var stateMarkers_default = {
  stateMarkers
};

// src/components/MapComponent/MapLayers/LayersData/countyMarkers.json
var countyMarkers = [
  {
    id: "050",
    state: "02",
    name: "Bethel County",
    postal: "CA",
    position: {
      lat: 17.481594399829145,
      lng: -119.7635882935443
    },
    stateName: "Alaska"
  },
  {
    id: "105",
    state: "02",
    name: "Hoonah-Angoon Census Area County",
    postal: "CA",
    position: {
      lat: 14.4962924081418,
      lng: -96.28543093151299
    },
    stateName: "Alaska"
  },
  {
    id: "122",
    state: "02",
    name: "Kenai Peninsula County",
    postal: "Borough",
    position: {
      lat: 16.395859418996107,
      lng: -110.5604947007627
    },
    stateName: "Alaska"
  },
  {
    id: "150",
    state: "02",
    name: "Kodiak Island County",
    postal: "Borough",
    position: {
      lat: 13.231057271090759,
      lng: -113.67444853552463
    },
    stateName: "Alaska"
  },
  {
    id: "164",
    state: "02",
    name: "Lake And Peninsula County",
    postal: "Borough",
    position: {
      lat: 14.953389646576516,
      lng: -115.7097558858778
    },
    stateName: "Alaska"
  },
  {
    id: "180",
    state: "02",
    name: "Nome County",
    postal: "CA",
    position: {
      lat: 21.408197238581014,
      lng: -125.1366850641183
    },
    stateName: "Alaska"
  },
  {
    id: "188",
    state: "02",
    name: "Northwest Arctic County",
    postal: "Borough",
    position: {
      lat: 23.0830131979709,
      lng: -119.93566052413694
    },
    stateName: "Alaska"
  },
  {
    id: "198",
    state: "02",
    name: "Prince Of Wales-Hyder County",
    postal: "CA",
    position: {
      lat: 11.488827024293068,
      lng: -92.7533949166839
    },
    stateName: "Alaska"
  },
  {
    id: "261",
    state: "02",
    name: "Valdez-Cordova County",
    postal: "CA",
    position: {
      lat: 17.719725553807148,
      lng: -104.13473811401242
    },
    stateName: "Alaska"
  },
  {
    id: "270",
    state: "02",
    name: "Wade Hampton County",
    postal: "CA",
    position: {
      lat: 18.018494345119723,
      lng: -124.25594517135495
    },
    stateName: "Alaska"
  },
  {
    id: "070",
    state: "02",
    name: "Dillingham County",
    postal: "CA",
    position: {
      lat: 15.497279641776515,
      lng: -118.9642846354236
    },
    stateName: "Alaska"
  },
  {
    id: "110",
    state: "02",
    name: "Juneau County",
    postal: "Cty&Bor",
    position: {
      lat: 14.461696794918174,
      lng: -94.24062088510786
    },
    stateName: "Alaska"
  },
  {
    id: "130",
    state: "02",
    name: "Ketchikan Gateway County",
    postal: "Borough",
    position: {
      lat: 11.77680669536436,
      lng: -90.83770751953126
    },
    stateName: "Alaska"
  },
  {
    id: "185",
    state: "02",
    name: "North Slope County",
    postal: "Borough",
    position: {
      lat: 25.461825917615048,
      lng: -114.10185404852258
    },
    stateName: "Alaska"
  },
  {
    id: "195",
    state: "02",
    name: "Petersburg County",
    postal: "CA",
    position: {
      lat: 12.835851070739452,
      lng: -93.4628634518688
    },
    stateName: "Alaska"
  },
  {
    id: "220",
    state: "02",
    name: "Sitka County",
    postal: "Cty&Bor",
    position: {
      lat: 13.800131562093414,
      lng: -95.62914346802485
    },
    stateName: "Alaska"
  },
  {
    id: "230",
    state: "02",
    name: "Skagway County",
    postal: "Muny",
    position: {
      lat: 15.581175644766498,
      lng: -95.31960567795046
    },
    stateName: "Alaska"
  },
  {
    id: "020",
    state: "02",
    name: "Anchorage County",
    postal: "Muny",
    position: {
      lat: 17.146495578973948,
      lng: -109.0201699429259
    },
    stateName: "Alaska"
  },
  {
    id: "068",
    state: "02",
    name: "Denali County",
    postal: "Borough",
    position: {
      lat: 19.70658871333655,
      lng: -110.28369248702603
    },
    stateName: "Alaska"
  },
  {
    id: "013",
    state: "02",
    name: "Aleutians East County",
    postal: "Borough",
    position: {
      lat: 11.542354819920194,
      lng: -122.0947265904397
    },
    stateName: "Alaska"
  },
  {
    id: "275",
    state: "02",
    name: "Wrangell County",
    postal: "Cty&Bor",
    position: {
      lat: 12.384672806103309,
      lng: -91.82475365541367
    },
    stateName: "Alaska"
  },
  {
    id: "240",
    state: "02",
    name: "Southeast Fairbanks County",
    postal: "CA",
    position: {
      lat: 19.93213007464273,
      lng: -102.63427734375001
    },
    stateName: "Alaska"
  },
  {
    id: "090",
    state: "02",
    name: "Fairbanks North Star County",
    postal: "Borough",
    position: {
      lat: 20.9369569424866,
      lng: -106.7638023246893
    },
    stateName: "Alaska"
  },
  {
    id: "100",
    state: "02",
    name: "Haines County",
    postal: "Borough",
    position: {
      lat: 15.350128625119355,
      lng: -95.83123679696479
    },
    stateName: "Alaska"
  },
  {
    id: "170",
    state: "02",
    name: "Matanuska-Susitna County",
    postal: "Borough",
    position: {
      lat: 18.424258259481803,
      lng: -109.99134305001128
    },
    stateName: "Alaska"
  },
  {
    id: "016",
    state: "02",
    name: "Aleutians West County",
    postal: "CA",
    position: {
      lat: 9.413457997250203,
      lng: -128.12852301987428
    },
    stateName: "Alaska"
  },
  {
    id: "060",
    state: "02",
    name: "Bristol Bay Borough County",
    postal: "Borough",
    position: {
      lat: 14.758871319541779,
      lng: -116.68451947130718
    },
    stateName: "Alaska"
  },
  {
    id: "290",
    state: "02",
    name: "Yukon-Koyukuk County",
    postal: "CA",
    position: {
      lat: 22.072423407685918,
      lng: -111.57072579780697
    },
    stateName: "Alaska"
  },
  {
    id: "282",
    state: "02",
    name: "Yakutat County",
    postal: "Cty&Bor",
    position: {
      lat: 16.059079525042243,
      lng: -100.88536390711955
    },
    stateName: "Alaska"
  },
  {
    id: "001",
    state: "01",
    name: "Autauga County",
    position: {
      lat: 32.536382,
      lng: -86.64449
    },
    stateName: "Alabama"
  },
  {
    id: "009",
    state: "01",
    name: "Blount County",
    position: {
      lat: 33.977448,
      lng: -86.567246
    },
    stateName: "Alabama"
  },
  {
    id: "017",
    state: "01",
    name: "Chambers County",
    position: {
      lat: 32.917943,
      lng: -85.391812
    },
    stateName: "Alabama"
  },
  {
    id: "021",
    state: "01",
    name: "Chilton County",
    position: {
      lat: 32.85406,
      lng: -86.726628
    },
    stateName: "Alabama"
  },
  {
    id: "033",
    state: "01",
    name: "Colbert County",
    position: {
      lat: 34.703112,
      lng: -87.801457
    },
    stateName: "Alabama"
  },
  {
    id: "045",
    state: "01",
    name: "Dale County",
    position: {
      lat: 31.430654,
      lng: -85.609476
    },
    stateName: "Alabama"
  },
  {
    id: "051",
    state: "01",
    name: "Elmore County",
    position: {
      lat: 32.597229,
      lng: -86.142739
    },
    stateName: "Alabama"
  },
  {
    id: "065",
    state: "01",
    name: "Hale County",
    position: {
      lat: 32.752796,
      lng: -87.623061
    },
    stateName: "Alabama"
  },
  {
    id: "079",
    state: "01",
    name: "Lawrence County",
    position: {
      lat: 34.529776,
      lng: -87.321865
    },
    stateName: "Alabama"
  },
  {
    id: "083",
    state: "01",
    name: "Limestone County",
    position: {
      lat: 34.810239,
      lng: -86.9814
    },
    stateName: "Alabama"
  },
  {
    id: "099",
    state: "01",
    name: "Monroe County",
    position: {
      lat: 31.580332,
      lng: -87.383266
    },
    stateName: "Alabama"
  },
  {
    id: "107",
    state: "01",
    name: "Pickens County",
    position: {
      lat: 33.296809,
      lng: -88.096878
    },
    stateName: "Alabama"
  },
  {
    id: "121",
    state: "01",
    name: "Talladega County",
    position: {
      lat: 33.369277,
      lng: -86.175805
    },
    stateName: "Alabama"
  },
  {
    id: "141",
    state: "05",
    name: "Van Buren County",
    position: {
      lat: 35.582959,
      lng: -92.515977
    },
    stateName: "Arkansas"
  },
  {
    id: "145",
    state: "05",
    name: "White County",
    position: {
      lat: 35.254722,
      lng: -91.753158
    },
    stateName: "Arkansas"
  },
  {
    id: "005",
    state: "06",
    name: "Amador County",
    position: {
      lat: 38.44355,
      lng: -120.653856
    },
    stateName: "California"
  },
  {
    id: "021",
    state: "06",
    name: "Glenn County",
    position: {
      lat: 39.602546,
      lng: -122.4017
    },
    stateName: "California"
  },
  {
    id: "033",
    state: "06",
    name: "Lake County",
    position: {
      lat: 39.094802,
      lng: -122.746757
    },
    stateName: "California"
  },
  {
    id: "043",
    state: "06",
    name: "Mariposa County",
    position: {
      lat: 37.570034,
      lng: -119.91286
    },
    stateName: "California"
  },
  {
    id: "055",
    state: "06",
    name: "Napa County",
    position: {
      lat: 38.507351,
      lng: -122.325995
    },
    stateName: "California"
  },
  {
    id: "089",
    state: "06",
    name: "Shasta County",
    position: {
      lat: 40.760522,
      lng: -122.04355
    },
    stateName: "California"
  },
  {
    id: "099",
    state: "06",
    name: "Stanislaus County",
    position: {
      lat: 37.562384,
      lng: -121.002656
    },
    stateName: "California"
  },
  {
    id: "115",
    state: "06",
    name: "Yuba County",
    position: {
      lat: 39.270026,
      lng: -121.34428
    },
    stateName: "California"
  },
  {
    id: "003",
    state: "08",
    name: "Alamosa County",
    position: {
      lat: 37.568442,
      lng: -105.788041
    },
    stateName: "Colorado"
  },
  {
    id: "013",
    state: "08",
    name: "Boulder County",
    position: {
      lat: 40.094826,
      lng: -105.398382
    },
    stateName: "Colorado"
  },
  {
    id: "014",
    state: "08",
    name: "Broomfield County",
    position: {
      lat: 39.953383,
      lng: -105.052125
    },
    stateName: "Colorado"
  },
  {
    id: "025",
    state: "08",
    name: "Crowley County",
    position: {
      lat: 38.321956,
      lng: -103.787562
    },
    stateName: "Colorado"
  },
  {
    id: "031",
    state: "08",
    name: "Denver County",
    position: {
      lat: 39.761849,
      lng: -104.880625
    },
    stateName: "Colorado"
  },
  {
    id: "035",
    state: "08",
    name: "Douglas County",
    position: {
      lat: 39.326435,
      lng: -104.926199
    },
    stateName: "Colorado"
  },
  {
    id: "047",
    state: "08",
    name: "Gilpin County",
    position: {
      lat: 39.861082,
      lng: -105.528947
    },
    stateName: "Colorado"
  },
  {
    id: "083",
    state: "12",
    name: "Marion County",
    position: {
      lat: 29.202805,
      lng: -82.0431
    },
    stateName: "Florida"
  },
  {
    id: "087",
    state: "12",
    name: "Monroe County",
    position: {
      lat: 25.601043,
      lng: -81.206777
    },
    stateName: "Florida"
  },
  {
    id: "095",
    state: "12",
    name: "Orange County",
    position: {
      lat: 28.514435,
      lng: -81.323295
    },
    stateName: "Florida"
  },
  {
    id: "111",
    state: "12",
    name: "St. Lucie County",
    position: {
      lat: 27.380775,
      lng: -80.443364
    },
    stateName: "Florida"
  },
  {
    id: "119",
    state: "12",
    name: "Sumter County",
    position: {
      lat: 28.714294,
      lng: -82.074715
    },
    stateName: "Florida"
  },
  {
    id: "125",
    state: "12",
    name: "Union County",
    position: {
      lat: 30.05428,
      lng: -82.366918
    },
    stateName: "Florida"
  },
  {
    id: "001",
    state: "13",
    name: "Appling County",
    position: {
      lat: 31.739712,
      lng: -82.290103
    },
    stateName: "Georgia"
  },
  {
    id: "013",
    state: "13",
    name: "Barrow County",
    position: {
      lat: 33.992009,
      lng: -83.712303
    },
    stateName: "Georgia"
  },
  {
    id: "029",
    state: "13",
    name: "Bryan County",
    position: {
      lat: 32.017969,
      lng: -81.438543
    },
    stateName: "Georgia"
  },
  {
    id: "043",
    state: "13",
    name: "Candler County",
    position: {
      lat: 32.403986,
      lng: -82.071446
    },
    stateName: "Georgia"
  },
  {
    id: "055",
    state: "13",
    name: "Chattooga County",
    position: {
      lat: 34.474179,
      lng: -85.345289
    },
    stateName: "Georgia"
  },
  {
    id: "059",
    state: "13",
    name: "Clarke County",
    position: {
      lat: 33.952234,
      lng: -83.36713
    },
    stateName: "Georgia"
  },
  {
    id: "071",
    state: "13",
    name: "Colquitt County",
    position: {
      lat: 31.189758,
      lng: -83.769741
    },
    stateName: "Georgia"
  },
  {
    id: "079",
    state: "13",
    name: "Crawford County",
    position: {
      lat: 32.709446,
      lng: -83.979182
    },
    stateName: "Georgia"
  },
  {
    id: "091",
    state: "13",
    name: "Dodge County",
    position: {
      lat: 32.160656,
      lng: -83.156818
    },
    stateName: "Georgia"
  },
  {
    id: "107",
    state: "13",
    name: "Emanuel County",
    position: {
      lat: 32.5911,
      lng: -82.299763
    },
    stateName: "Georgia"
  },
  {
    id: "113",
    state: "13",
    name: "Fayette County",
    position: {
      lat: 33.412717,
      lng: -84.493941
    },
    stateName: "Georgia"
  },
  {
    id: "123",
    state: "13",
    name: "Gilmer County",
    position: {
      lat: 34.690541,
      lng: -84.453984
    },
    stateName: "Georgia"
  },
  {
    id: "053",
    state: "08",
    name: "Hinsdale County",
    position: {
      lat: 37.811625,
      lng: -107.383405
    },
    stateName: "Colorado"
  },
  {
    id: "057",
    state: "08",
    name: "Jackson County",
    position: {
      lat: 40.663432,
      lng: -106.329248
    },
    stateName: "Colorado"
  },
  {
    id: "059",
    state: "08",
    name: "Jefferson County",
    position: {
      lat: 39.58646,
      lng: -105.245601
    },
    stateName: "Colorado"
  },
  {
    id: "073",
    state: "08",
    name: "Lincoln County",
    position: {
      lat: 38.99374,
      lng: -103.507555
    },
    stateName: "Colorado"
  },
  {
    id: "083",
    state: "08",
    name: "Montezuma County",
    position: {
      lat: 37.338025,
      lng: -108.595786
    },
    stateName: "Colorado"
  },
  {
    id: "095",
    state: "08",
    name: "Phillips County",
    position: {
      lat: 40.594712,
      lng: -102.345105
    },
    stateName: "Colorado"
  },
  {
    id: "105",
    state: "08",
    name: "Rio Grande County",
    position: {
      lat: 37.485763,
      lng: -106.453214
    },
    stateName: "Colorado"
  },
  {
    id: "111",
    state: "08",
    name: "San Juan County",
    position: {
      lat: 37.781075,
      lng: -107.670257
    },
    stateName: "Colorado"
  },
  {
    id: "123",
    state: "08",
    name: "Weld County",
    position: {
      lat: 40.555794,
      lng: -104.383649
    },
    stateName: "Colorado"
  },
  {
    id: "015",
    state: "09",
    name: "Windham County",
    position: {
      lat: 41.824999,
      lng: -71.990702
    },
    stateName: "Connecticut"
  },
  {
    id: "001",
    state: "12",
    name: "Alachua County",
    position: {
      lat: 29.67574,
      lng: -82.357221
    },
    stateName: "Florida"
  },
  {
    id: "013",
    state: "12",
    name: "Calhoun County",
    position: {
      lat: 30.388801,
      lng: -85.197916
    },
    stateName: "Florida"
  },
  {
    id: "023",
    state: "12",
    name: "Columbia County",
    position: {
      lat: 30.221305,
      lng: -82.623127
    },
    stateName: "Florida"
  },
  {
    id: "039",
    state: "12",
    name: "Gadsden County",
    position: {
      lat: 30.57917,
      lng: -84.612783
    },
    stateName: "Florida"
  },
  {
    id: "055",
    state: "12",
    name: "Highlands County",
    position: {
      lat: 27.342627,
      lng: -81.340921
    },
    stateName: "Florida"
  },
  {
    id: "059",
    state: "12",
    name: "Holmes County",
    position: {
      lat: 30.866222,
      lng: -85.812959
    },
    stateName: "Florida"
  },
  {
    id: "073",
    state: "12",
    name: "Leon County",
    position: {
      lat: 30.45931,
      lng: -84.2778
    },
    stateName: "Florida"
  },
  {
    id: "003",
    state: "04",
    name: "Cochise County",
    position: {
      lat: 31.881793,
      lng: -109.75412
    },
    stateName: "Arizona"
  },
  {
    id: "013",
    state: "04",
    name: "Maricopa County",
    position: {
      lat: 33.346541,
      lng: -112.495534
    },
    stateName: "Arizona"
  },
  {
    id: "001",
    state: "05",
    name: "Arkansas County",
    position: {
      lat: 34.289574,
      lng: -91.376547
    },
    stateName: "Arkansas"
  },
  {
    id: "009",
    state: "05",
    name: "Boone County",
    position: {
      lat: 36.304308,
      lng: -93.079204
    },
    stateName: "Arkansas"
  },
  {
    id: "019",
    state: "05",
    name: "Clark County",
    position: {
      lat: 34.053312,
      lng: -93.176205
    },
    stateName: "Arkansas"
  },
  {
    id: "029",
    state: "05",
    name: "Conway County",
    position: {
      lat: 35.265702,
      lng: -92.689248
    },
    stateName: "Arkansas"
  },
  {
    id: "043",
    state: "05",
    name: "Drew County",
    position: {
      lat: 33.587242,
      lng: -91.722778
    },
    stateName: "Arkansas"
  },
  {
    id: "051",
    state: "05",
    name: "Garland County",
    position: {
      lat: 34.578861,
      lng: -93.146915
    },
    stateName: "Arkansas"
  },
  {
    id: "061",
    state: "05",
    name: "Howard County",
    position: {
      lat: 34.083179,
      lng: -93.990988
    },
    stateName: "Arkansas"
  },
  {
    id: "071",
    state: "05",
    name: "Johnson County",
    position: {
      lat: 35.573359,
      lng: -93.466322
    },
    stateName: "Arkansas"
  },
  {
    id: "085",
    state: "05",
    name: "Lonoke County",
    position: {
      lat: 34.755114,
      lng: -91.894132
    },
    stateName: "Arkansas"
  },
  {
    id: "097",
    state: "05",
    name: "Montgomery County",
    position: {
      lat: 34.545652,
      lng: -93.664147
    },
    stateName: "Arkansas"
  },
  {
    id: "101",
    state: "05",
    name: "Newton County",
    position: {
      lat: 35.910063,
      lng: -93.215084
    },
    stateName: "Arkansas"
  },
  {
    id: "111",
    state: "05",
    name: "Poinsett County",
    position: {
      lat: 35.568981,
      lng: -90.680595
    },
    stateName: "Arkansas"
  },
  {
    id: "123",
    state: "05",
    name: "St. Francis County",
    position: {
      lat: 35.014438,
      lng: -90.742199
    },
    stateName: "Arkansas"
  },
  {
    id: "129",
    state: "05",
    name: "Searcy County",
    position: {
      lat: 35.90966,
      lng: -92.699351
    },
    stateName: "Arkansas"
  },
  {
    id: "309",
    state: "13",
    name: "Wheeler County",
    position: {
      lat: 32.109276,
      lng: -82.738701
    },
    stateName: "Georgia"
  },
  {
    id: "313",
    state: "13",
    name: "Whitfield County",
    position: {
      lat: 34.801726,
      lng: -84.968541
    },
    stateName: "Georgia"
  },
  {
    id: "319",
    state: "13",
    name: "Wilkinson County",
    position: {
      lat: 32.804321,
      lng: -83.175587
    },
    stateName: "Georgia"
  },
  {
    id: "015",
    state: "16",
    name: "Boise County",
    position: {
      lat: 43.987275,
      lng: -115.715111
    },
    stateName: "Idaho"
  },
  {
    id: "021",
    state: "16",
    name: "Boundary County",
    position: {
      lat: 48.773065,
      lng: -116.524619
    },
    stateName: "Idaho"
  },
  {
    id: "045",
    state: "16",
    name: "Gem County",
    position: {
      lat: 44.061473,
      lng: -116.398784
    },
    stateName: "Idaho"
  },
  {
    id: "051",
    state: "16",
    name: "Jefferson County",
    position: {
      lat: 43.796965,
      lng: -112.318588
    },
    stateName: "Idaho"
  },
  {
    id: "057",
    state: "16",
    name: "Latah County",
    position: {
      lat: 46.81892,
      lng: -116.730974
    },
    stateName: "Idaho"
  },
  {
    id: "067",
    state: "16",
    name: "Minidoka County",
    position: {
      lat: 42.856937,
      lng: -113.64001
    },
    stateName: "Idaho"
  },
  {
    id: "081",
    state: "16",
    name: "Teton County",
    position: {
      lat: 43.760994,
      lng: -111.211765
    },
    stateName: "Idaho"
  },
  {
    id: "005",
    state: "17",
    name: "Bond County",
    position: {
      lat: 38.885924,
      lng: -89.436592
    },
    stateName: "Illinois"
  },
  {
    id: "009",
    state: "17",
    name: "Brown County",
    position: {
      lat: 39.962069,
      lng: -90.75031
    },
    stateName: "Illinois"
  },
  {
    id: "019",
    state: "17",
    name: "Champaign County",
    position: {
      lat: 40.13915,
      lng: -88.197201
    },
    stateName: "Illinois"
  },
  {
    id: "029",
    state: "17",
    name: "Coles County",
    position: {
      lat: 39.51368,
      lng: -88.220782
    },
    stateName: "Illinois"
  },
  {
    id: "035",
    state: "17",
    name: "Cumberland County",
    position: {
      lat: 39.273121,
      lng: -88.240619
    },
    stateName: "Illinois"
  },
  {
    id: "049",
    state: "17",
    name: "Effingham County",
    position: {
      lat: 39.047694,
      lng: -88.592786
    },
    stateName: "Illinois"
  },
  {
    id: "055",
    state: "17",
    name: "Franklin County",
    position: {
      lat: 37.991848,
      lng: -88.926246
    },
    stateName: "Illinois"
  },
  {
    id: "065",
    state: "17",
    name: "Hamilton County",
    position: {
      lat: 38.085226,
      lng: -88.539005
    },
    stateName: "Illinois"
  },
  {
    id: "051",
    state: "18",
    name: "Gibson County",
    position: {
      lat: 38.317413,
      lng: -87.580566
    },
    stateName: "Indiana"
  },
  {
    id: "059",
    state: "18",
    name: "Hancock County",
    position: {
      lat: 39.822604,
      lng: -85.772904
    },
    stateName: "Indiana"
  },
  {
    id: "069",
    state: "18",
    name: "Huntington County",
    position: {
      lat: 40.826394,
      lng: -85.478598
    },
    stateName: "Indiana"
  },
  {
    id: "079",
    state: "18",
    name: "Jennings County",
    position: {
      lat: 38.996234,
      lng: -85.628111
    },
    stateName: "Indiana"
  },
  {
    id: "085",
    state: "18",
    name: "Kosciusko County",
    position: {
      lat: 41.244293,
      lng: -85.861575
    },
    stateName: "Indiana"
  },
  {
    id: "095",
    state: "18",
    name: "Madison County",
    position: {
      lat: 40.166203,
      lng: -85.722454
    },
    stateName: "Indiana"
  },
  {
    id: "105",
    state: "18",
    name: "Monroe County",
    position: {
      lat: 39.160751,
      lng: -86.523325
    },
    stateName: "Indiana"
  },
  {
    id: "115",
    state: "18",
    name: "Ohio County",
    position: {
      lat: 38.940527,
      lng: -84.964299
    },
    stateName: "Indiana"
  },
  {
    id: "125",
    state: "18",
    name: "Pike County",
    position: {
      lat: 38.397898,
      lng: -87.232532
    },
    stateName: "Indiana"
  },
  {
    id: "131",
    state: "18",
    name: "Pulaski County",
    position: {
      lat: 41.045272,
      lng: -86.692538
    },
    stateName: "Indiana"
  },
  {
    id: "139",
    state: "18",
    name: "Rush County",
    position: {
      lat: 39.622312,
      lng: -85.466527
    },
    stateName: "Indiana"
  },
  {
    id: "153",
    state: "18",
    name: "Sullivan County",
    position: {
      lat: 39.089225,
      lng: -87.415843
    },
    stateName: "Indiana"
  },
  {
    id: "173",
    state: "18",
    name: "Warrick County",
    position: {
      lat: 38.097764,
      lng: -87.272023
    },
    stateName: "Indiana"
  },
  {
    id: "179",
    state: "18",
    name: "Wells County",
    position: {
      lat: 40.735273,
      lng: -85.212974
    },
    stateName: "Indiana"
  },
  {
    id: "183",
    state: "18",
    name: "Whitley County",
    position: {
      lat: 41.136426,
      lng: -85.501892
    },
    stateName: "Indiana"
  },
  {
    id: "009",
    state: "19",
    name: "Audubon County",
    position: {
      lat: 41.679178,
      lng: -94.904312
    },
    stateName: "Iowa"
  },
  {
    id: "017",
    state: "19",
    name: "Bremer County",
    position: {
      lat: 42.78089,
      lng: -92.327586
    },
    stateName: "Iowa"
  },
  {
    id: "021",
    state: "19",
    name: "Buena Vista County",
    position: {
      lat: 42.741522,
      lng: -95.141433
    },
    stateName: "Iowa"
  },
  {
    id: "031",
    state: "19",
    name: "Cedar County",
    position: {
      lat: 41.772355,
      lng: -91.13219
    },
    stateName: "Iowa"
  },
  {
    id: "039",
    state: "19",
    name: "Clarke County",
    position: {
      lat: 41.029191,
      lng: -93.784096
    },
    stateName: "Iowa"
  },
  {
    id: "073",
    state: "17",
    name: "Henry County",
    position: {
      lat: 41.350021,
      lng: -90.130838
    },
    stateName: "Illinois"
  },
  {
    id: "077",
    state: "17",
    name: "Jackson County",
    position: {
      lat: 37.786096,
      lng: -89.381212
    },
    stateName: "Illinois"
  },
  {
    id: "079",
    state: "17",
    name: "Jasper County",
    position: {
      lat: 39.004874,
      lng: -88.150763
    },
    stateName: "Illinois"
  },
  {
    id: "089",
    state: "17",
    name: "Kane County",
    position: {
      lat: 41.939594,
      lng: -88.42804
    },
    stateName: "Illinois"
  },
  {
    id: "103",
    state: "17",
    name: "Lee County",
    position: {
      lat: 41.747442,
      lng: -89.299351
    },
    stateName: "Illinois"
  },
  {
    id: "107",
    state: "17",
    name: "Logan County",
    position: {
      lat: 40.12907,
      lng: -89.365308
    },
    stateName: "Illinois"
  },
  {
    id: "121",
    state: "17",
    name: "Marion County",
    position: {
      lat: 38.648396,
      lng: -88.920221
    },
    stateName: "Illinois"
  },
  {
    id: "129",
    state: "17",
    name: "Menard County",
    position: {
      lat: 40.022569,
      lng: -89.794133
    },
    stateName: "Illinois"
  },
  {
    id: "139",
    state: "17",
    name: "Moultrie County",
    position: {
      lat: 39.636896,
      lng: -88.625726
    },
    stateName: "Illinois"
  },
  {
    id: "145",
    state: "17",
    name: "Perry County",
    position: {
      lat: 38.084385,
      lng: -89.368487
    },
    stateName: "Illinois"
  },
  {
    id: "155",
    state: "17",
    name: "Putnam County",
    position: {
      lat: 41.19894,
      lng: -89.298386
    },
    stateName: "Illinois"
  },
  {
    id: "169",
    state: "17",
    name: "Schuyler County",
    position: {
      lat: 40.156905,
      lng: -90.613464
    },
    stateName: "Illinois"
  },
  {
    id: "175",
    state: "17",
    name: "Stark County",
    position: {
      lat: 41.096908,
      lng: -89.797411
    },
    stateName: "Illinois"
  },
  {
    id: "179",
    state: "17",
    name: "Tazewell County",
    position: {
      lat: 40.508074,
      lng: -89.51626
    },
    stateName: "Illinois"
  },
  {
    id: "189",
    state: "17",
    name: "Washington County",
    position: {
      lat: 38.353141,
      lng: -89.417187
    },
    stateName: "Illinois"
  },
  {
    id: "203",
    state: "17",
    name: "Woodford County",
    position: {
      lat: 40.789596,
      lng: -89.210301
    },
    stateName: "Illinois"
  },
  {
    id: "009",
    state: "18",
    name: "Blackford County",
    position: {
      lat: 40.472672,
      lng: -85.32373
    },
    stateName: "Indiana"
  },
  {
    id: "011",
    state: "18",
    name: "Boone County",
    position: {
      lat: 40.050892,
      lng: -86.469014
    },
    stateName: "Indiana"
  },
  {
    id: "021",
    state: "18",
    name: "Clay County",
    position: {
      lat: 39.393951,
      lng: -87.115837
    },
    stateName: "Indiana"
  },
  {
    id: "033",
    state: "18",
    name: "De Kalb County",
    position: {
      lat: 41.401189,
      lng: -85.000185
    },
    stateName: "Indiana"
  },
  {
    id: "125",
    state: "13",
    name: "Glascock County",
    position: {
      lat: 33.227491,
      lng: -82.606913
    },
    stateName: "Georgia"
  },
  {
    id: "129",
    state: "13",
    name: "Gordon County",
    position: {
      lat: 34.509667,
      lng: -84.873862
    },
    stateName: "Georgia"
  },
  {
    id: "131",
    state: "13",
    name: "Grady County",
    position: {
      lat: 30.875863,
      lng: -84.244772
    },
    stateName: "Georgia"
  },
  {
    id: "139",
    state: "13",
    name: "Hall County",
    position: {
      lat: 34.317588,
      lng: -83.818497
    },
    stateName: "Georgia"
  },
  {
    id: "157",
    state: "13",
    name: "Jackson County",
    position: {
      lat: 34.134157,
      lng: -83.565133
    },
    stateName: "Georgia"
  },
  {
    id: "169",
    state: "13",
    name: "Jones County",
    position: {
      lat: 33.020226,
      lng: -83.562339
    },
    stateName: "Georgia"
  },
  {
    id: "175",
    state: "13",
    name: "Laurens County",
    position: {
      lat: 32.39322,
      lng: -82.926317
    },
    stateName: "Georgia"
  },
  {
    id: "187",
    state: "13",
    name: "Lumpkin County",
    position: {
      lat: 34.568149,
      lng: -83.998828
    },
    stateName: "Georgia"
  },
  {
    id: "197",
    state: "13",
    name: "Marion County",
    position: {
      lat: 32.359538,
      lng: -84.529561
    },
    stateName: "Georgia"
  },
  {
    id: "201",
    state: "13",
    name: "Miller County",
    position: {
      lat: 31.162908,
      lng: -84.730386
    },
    stateName: "Georgia"
  },
  {
    id: "217",
    state: "13",
    name: "Newton County",
    position: {
      lat: 33.544046,
      lng: -83.855189
    },
    stateName: "Georgia"
  },
  {
    id: "225",
    state: "13",
    name: "Peach County",
    position: {
      lat: 32.571324,
      lng: -83.831978
    },
    stateName: "Georgia"
  },
  {
    id: "233",
    state: "13",
    name: "Polk County",
    position: {
      lat: 33.995961,
      lng: -85.186826
    },
    stateName: "Georgia"
  },
  {
    id: "243",
    state: "13",
    name: "Randolph County",
    position: {
      lat: 31.762651,
      lng: -84.752311
    },
    stateName: "Georgia"
  },
  {
    id: "253",
    state: "13",
    name: "Seminole County",
    position: {
      lat: 30.933894,
      lng: -84.867592
    },
    stateName: "Georgia"
  },
  {
    id: "265",
    state: "13",
    name: "Taliaferro County",
    position: {
      lat: 33.559314,
      lng: -82.875208
    },
    stateName: "Georgia"
  },
  {
    id: "277",
    state: "13",
    name: "Tift County",
    position: {
      lat: 31.457003,
      lng: -83.525931
    },
    stateName: "Georgia"
  },
  {
    id: "285",
    state: "13",
    name: "Troup County",
    position: {
      lat: 33.034482,
      lng: -85.02836
    },
    stateName: "Georgia"
  },
  {
    id: "293",
    state: "13",
    name: "Upson County",
    position: {
      lat: 32.881837,
      lng: -84.292281
    },
    stateName: "Georgia"
  },
  {
    id: "303",
    state: "13",
    name: "Washington County",
    position: {
      lat: 32.971848,
      lng: -82.798112
    },
    stateName: "Georgia"
  },
  {
    id: "027",
    state: "20",
    name: "Clay County",
    position: {
      lat: 39.344964,
      lng: -97.168853
    },
    stateName: "Kansas"
  },
  {
    id: "029",
    state: "20",
    name: "Cloud County",
    position: {
      lat: 39.487329,
      lng: -97.64139
    },
    stateName: "Kansas"
  },
  {
    id: "039",
    state: "20",
    name: "Decatur County",
    position: {
      lat: 39.783542,
      lng: -100.459708
    },
    stateName: "Kansas"
  },
  {
    id: "049",
    state: "20",
    name: "Elk County",
    position: {
      lat: 37.456026,
      lng: -96.244642
    },
    stateName: "Kansas"
  },
  {
    id: "065",
    state: "20",
    name: "Graham County",
    position: {
      lat: 39.349445,
      lng: -99.880952
    },
    stateName: "Kansas"
  },
  {
    id: "075",
    state: "20",
    name: "Hamilton County",
    position: {
      lat: 37.995244,
      lng: -101.793689
    },
    stateName: "Kansas"
  },
  {
    id: "085",
    state: "20",
    name: "Jackson County",
    position: {
      lat: 39.410989,
      lng: -95.794509
    },
    stateName: "Kansas"
  },
  {
    id: "095",
    state: "20",
    name: "Kingman County",
    position: {
      lat: 37.552951,
      lng: -98.144529
    },
    stateName: "Kansas"
  },
  {
    id: "105",
    state: "20",
    name: "Lincoln County",
    position: {
      lat: 39.047276,
      lng: -98.214265
    },
    stateName: "Kansas"
  },
  {
    id: "115",
    state: "20",
    name: "Marion County",
    position: {
      lat: 38.359647,
      lng: -97.102771
    },
    stateName: "Kansas"
  },
  {
    id: "127",
    state: "20",
    name: "Morris County",
    position: {
      lat: 38.687696,
      lng: -96.644905
    },
    stateName: "Kansas"
  },
  {
    id: "135",
    state: "20",
    name: "Ness County",
    position: {
      lat: 38.480437,
      lng: -99.908745
    },
    stateName: "Kansas"
  },
  {
    id: "145",
    state: "20",
    name: "Pawnee County",
    position: {
      lat: 38.182873,
      lng: -99.232154
    },
    stateName: "Kansas"
  },
  {
    id: "155",
    state: "20",
    name: "Reno County",
    position: {
      lat: 37.948185,
      lng: -98.078346
    },
    stateName: "Kansas"
  },
  {
    id: "163",
    state: "20",
    name: "Rooks County",
    position: {
      lat: 39.346006,
      lng: -99.324492
    },
    stateName: "Kansas"
  },
  {
    id: "173",
    state: "20",
    name: "Sedgwick County",
    position: {
      lat: 37.683807,
      lng: -97.459451
    },
    stateName: "Kansas"
  },
  {
    id: "185",
    state: "20",
    name: "Stafford County",
    position: {
      lat: 38.03563,
      lng: -98.719889
    },
    stateName: "Kansas"
  },
  {
    id: "195",
    state: "20",
    name: "Trego County",
    position: {
      lat: 38.921302,
      lng: -99.865423
    },
    stateName: "Kansas"
  },
  {
    id: "203",
    state: "20",
    name: "Wichita County",
    position: {
      lat: 38.481922,
      lng: -101.347434
    },
    stateName: "Kansas"
  },
  {
    id: "205",
    state: "20",
    name: "Wilson County",
    position: {
      lat: 37.558515,
      lng: -95.745175
    },
    stateName: "Kansas"
  },
  {
    id: "183",
    state: "21",
    name: "Ohio County",
    position: {
      lat: 37.477859,
      lng: -86.844871
    },
    stateName: "Kentucky"
  },
  {
    id: "187",
    state: "21",
    name: "Owen County",
    position: {
      lat: 38.499603,
      lng: -84.841509
    },
    stateName: "Kentucky"
  },
  {
    id: "189",
    state: "21",
    name: "Owsley County",
    position: {
      lat: 37.423452,
      lng: -83.691566
    },
    stateName: "Kentucky"
  },
  {
    id: "199",
    state: "21",
    name: "Pulaski County",
    position: {
      lat: 37.108312,
      lng: -84.579986
    },
    stateName: "Kentucky"
  },
  {
    id: "209",
    state: "21",
    name: "Scott County",
    position: {
      lat: 38.289882,
      lng: -84.579376
    },
    stateName: "Kentucky"
  },
  {
    id: "219",
    state: "21",
    name: "Todd County",
    position: {
      lat: 36.840338,
      lng: -87.183642
    },
    stateName: "Kentucky"
  },
  {
    id: "229",
    state: "21",
    name: "Washington County",
    position: {
      lat: 37.754209,
      lng: -85.175416
    },
    stateName: "Kentucky"
  },
  {
    id: "001",
    state: "22",
    name: "Acadia County",
    postal: "Parish",
    position: {
      lat: 30.291497,
      lng: -92.411037
    },
    stateName: "Louisiana"
  },
  {
    id: "013",
    state: "22",
    name: "Bienville County",
    postal: "Parish",
    position: {
      lat: 32.344268,
      lng: -93.061513
    },
    stateName: "Louisiana"
  },
  {
    id: "033",
    state: "22",
    name: "E. Baton Rouge County",
    postal: "Parish",
    position: {
      lat: 30.544002,
      lng: -91.093174
    },
    stateName: "Louisiana"
  },
  {
    id: "053",
    state: "22",
    name: "Jeffrson Davis County",
    postal: "Parish",
    position: {
      lat: 30.269529,
      lng: -92.816221
    },
    stateName: "Louisiana"
  },
  {
    id: "073",
    state: "22",
    name: "Ouachita County",
    postal: "Parish",
    position: {
      lat: 32.477495,
      lng: -92.154798
    },
    stateName: "Louisiana"
  },
  {
    id: "077",
    state: "22",
    name: "Pointe Coupee County",
    postal: "Parish",
    position: {
      lat: 30.708319,
      lng: -91.604621
    },
    stateName: "Louisiana"
  },
  {
    id: "089",
    state: "22",
    name: "St. Charles County",
    postal: "Parish",
    position: {
      lat: 29.913833,
      lng: -90.359314
    },
    stateName: "Louisiana"
  },
  {
    id: "105",
    state: "22",
    name: "Tangipahoa County",
    postal: "Parish",
    position: {
      lat: 30.621581,
      lng: -90.406633
    },
    stateName: "Louisiana"
  },
  {
    id: "115",
    state: "22",
    name: "Vernon County",
    postal: "Parish",
    position: {
      lat: 31.110563,
      lng: -93.18152
    },
    stateName: "Louisiana"
  },
  {
    id: "003",
    state: "21",
    name: "Allen County",
    position: {
      lat: 36.75077,
      lng: -86.192458
    },
    stateName: "Kentucky"
  },
  {
    id: "009",
    state: "21",
    name: "Barren County",
    position: {
      lat: 36.963614,
      lng: -85.92499
    },
    stateName: "Kentucky"
  },
  {
    id: "017",
    state: "21",
    name: "Bourbon County",
    position: {
      lat: 38.202562,
      lng: -84.20986
    },
    stateName: "Kentucky"
  },
  {
    id: "025",
    state: "21",
    name: "Breathitt County",
    position: {
      lat: 37.521178,
      lng: -83.322401
    },
    stateName: "Kentucky"
  },
  {
    id: "031",
    state: "21",
    name: "Butler County",
    position: {
      lat: 37.207013,
      lng: -86.682471
    },
    stateName: "Kentucky"
  },
  {
    id: "043",
    state: "21",
    name: "Carter County",
    position: {
      lat: 38.309555,
      lng: -83.048821
    },
    stateName: "Kentucky"
  },
  {
    id: "053",
    state: "21",
    name: "Clinton County",
    position: {
      lat: 36.729124,
      lng: -85.153499
    },
    stateName: "Kentucky"
  },
  {
    id: "063",
    state: "21",
    name: "Elliott County",
    position: {
      lat: 38.116425,
      lng: -83.097541
    },
    stateName: "Kentucky"
  },
  {
    id: "077",
    state: "21",
    name: "Gallatin County",
    position: {
      lat: 38.760184,
      lng: -84.862114
    },
    stateName: "Kentucky"
  },
  {
    id: "087",
    state: "21",
    name: "Green County",
    position: {
      lat: 37.269637,
      lng: -85.561403
    },
    stateName: "Kentucky"
  },
  {
    id: "099",
    state: "21",
    name: "Hart County",
    position: {
      lat: 37.313856,
      lng: -85.881995
    },
    stateName: "Kentucky"
  },
  {
    id: "103",
    state: "21",
    name: "Henry County",
    position: {
      lat: 38.451561,
      lng: -85.120079
    },
    stateName: "Kentucky"
  },
  {
    id: "115",
    state: "21",
    name: "Johnson County",
    position: {
      lat: 37.84926,
      lng: -82.830639
    },
    stateName: "Kentucky"
  },
  {
    id: "121",
    state: "21",
    name: "Knox County",
    position: {
      lat: 36.887476,
      lng: -83.85563
    },
    stateName: "Kentucky"
  },
  {
    id: "137",
    state: "21",
    name: "Lincoln County",
    position: {
      lat: 37.457257,
      lng: -84.658074
    },
    stateName: "Kentucky"
  },
  {
    id: "143",
    state: "21",
    name: "Lyon County",
    position: {
      lat: 37.023976,
      lng: -88.083391
    },
    stateName: "Kentucky"
  },
  {
    id: "151",
    state: "21",
    name: "Madison County",
    position: {
      lat: 37.723528,
      lng: -84.277008
    },
    stateName: "Kentucky"
  },
  {
    id: "157",
    state: "21",
    name: "Marshall County",
    position: {
      lat: 36.882017,
      lng: -88.332752
    },
    stateName: "Kentucky"
  },
  {
    id: "169",
    state: "21",
    name: "Metcalfe County",
    position: {
      lat: 36.990394,
      lng: -85.629554
    },
    stateName: "Kentucky"
  },
  {
    id: "175",
    state: "21",
    name: "Morgan County",
    position: {
      lat: 37.92294,
      lng: -83.258944
    },
    stateName: "Kentucky"
  },
  {
    id: "045",
    state: "19",
    name: "Clinton County",
    position: {
      lat: 41.898073,
      lng: -90.534243
    },
    stateName: "Iowa"
  },
  {
    id: "047",
    state: "19",
    name: "Crawford County",
    position: {
      lat: 42.043119,
      lng: -95.38909
    },
    stateName: "Iowa"
  },
  {
    id: "055",
    state: "19",
    name: "Delaware County",
    position: {
      lat: 42.473003,
      lng: -91.359443
    },
    stateName: "Iowa"
  },
  {
    id: "063",
    state: "19",
    name: "Emmet County",
    position: {
      lat: 43.377984,
      lng: -94.66937
    },
    stateName: "Iowa"
  },
  {
    id: "067",
    state: "19",
    name: "Floyd County",
    position: {
      lat: 43.052741,
      lng: -92.787367
    },
    stateName: "Iowa"
  },
  {
    id: "079",
    state: "19",
    name: "Hamilton County",
    position: {
      lat: 42.390768,
      lng: -93.709198
    },
    stateName: "Iowa"
  },
  {
    id: "087",
    state: "19",
    name: "Henry County",
    position: {
      lat: 40.985864,
      lng: -91.54427
    },
    stateName: "Iowa"
  },
  {
    id: "095",
    state: "19",
    name: "Iowa County",
    position: {
      lat: 41.683918,
      lng: -92.059123
    },
    stateName: "Iowa"
  },
  {
    id: "105",
    state: "19",
    name: "Jones County",
    position: {
      lat: 42.125118,
      lng: -91.116914
    },
    stateName: "Iowa"
  },
  {
    id: "113",
    state: "19",
    name: "Linn County",
    position: {
      lat: 42.077951,
      lng: -91.597674
    },
    stateName: "Iowa"
  },
  {
    id: "121",
    state: "19",
    name: "Madison County",
    position: {
      lat: 41.330622,
      lng: -94.015184
    },
    stateName: "Iowa"
  },
  {
    id: "125",
    state: "19",
    name: "Marion County",
    position: {
      lat: 41.331455,
      lng: -93.093849
    },
    stateName: "Iowa"
  },
  {
    id: "137",
    state: "19",
    name: "Montgomery County",
    position: {
      lat: 41.021735,
      lng: -95.15779
    },
    stateName: "Iowa"
  },
  {
    id: "141",
    state: "19",
    name: "Obrien County",
    position: {
      lat: 43.083746,
      lng: -95.625624
    },
    stateName: "Iowa"
  },
  {
    id: "151",
    state: "19",
    name: "Pocahontas County",
    position: {
      lat: 42.734033,
      lng: -94.678279
    },
    stateName: "Iowa"
  },
  {
    id: "161",
    state: "19",
    name: "Sac County",
    position: {
      lat: 42.387526,
      lng: -95.105224
    },
    stateName: "Iowa"
  },
  {
    id: "169",
    state: "19",
    name: "Story County",
    position: {
      lat: 42.037538,
      lng: -93.466093
    },
    stateName: "Iowa"
  },
  {
    id: "175",
    state: "19",
    name: "Union County",
    position: {
      lat: 41.02855,
      lng: -94.245091
    },
    stateName: "Iowa"
  },
  {
    id: "181",
    state: "19",
    name: "Warren County",
    position: {
      lat: 41.336769,
      lng: -93.564366
    },
    stateName: "Iowa"
  },
  {
    id: "195",
    state: "19",
    name: "Worth County",
    position: {
      lat: 43.373491,
      lng: -93.248533
    },
    stateName: "Iowa"
  },
  {
    id: "005",
    state: "20",
    name: "Atchison County",
    position: {
      lat: 39.532544,
      lng: -95.313398
    },
    stateName: "Kansas"
  },
  {
    id: "015",
    state: "20",
    name: "Butler County",
    position: {
      lat: 37.773681,
      lng: -96.838762
    },
    stateName: "Kansas"
  },
  {
    id: "073",
    state: "27",
    name: "Lac Qui Parle County",
    position: {
      lat: 44.999855,
      lng: -96.176836
    },
    stateName: "Minnesota"
  },
  {
    id: "083",
    state: "27",
    name: "Lyon County",
    position: {
      lat: 44.409195,
      lng: -95.847268
    },
    stateName: "Minnesota"
  },
  {
    id: "085",
    state: "27",
    name: "Mc Leod County",
    position: {
      lat: 44.821644,
      lng: -94.27232
    },
    stateName: "Minnesota"
  },
  {
    id: "093",
    state: "27",
    name: "Meeker County",
    position: {
      lat: 45.123156,
      lng: -94.527346
    },
    stateName: "Minnesota"
  },
  {
    id: "103",
    state: "27",
    name: "Nicollet County",
    position: {
      lat: 44.35882,
      lng: -94.245685
    },
    stateName: "Minnesota"
  },
  {
    id: "113",
    state: "27",
    name: "Pennington County",
    position: {
      lat: 48.069247,
      lng: -96.037725
    },
    stateName: "Minnesota"
  },
  {
    id: "121",
    state: "27",
    name: "Pope County",
    position: {
      lat: 45.589623,
      lng: -95.446705
    },
    stateName: "Minnesota"
  },
  {
    id: "127",
    state: "27",
    name: "Redwood County",
    position: {
      lat: 44.403536,
      lng: -95.254242
    },
    stateName: "Minnesota"
  },
  {
    id: "147",
    state: "27",
    name: "Steele County",
    position: {
      lat: 44.015261,
      lng: -93.220453
    },
    stateName: "Minnesota"
  },
  {
    id: "153",
    state: "27",
    name: "Todd County",
    position: {
      lat: 46.066569,
      lng: -94.900576
    },
    stateName: "Minnesota"
  },
  {
    id: "161",
    state: "27",
    name: "Waseca County",
    position: {
      lat: 44.01846,
      lng: -93.589844
    },
    stateName: "Minnesota"
  },
  {
    id: "003",
    state: "28",
    name: "Alcorn County",
    position: {
      lat: 34.886648,
      lng: -88.581014
    },
    stateName: "Mississippi"
  },
  {
    id: "013",
    state: "28",
    name: "Calhoun County",
    position: {
      lat: 33.936634,
      lng: -89.337114
    },
    stateName: "Mississippi"
  },
  {
    id: "017",
    state: "28",
    name: "Chickasaw County",
    position: {
      lat: 33.921654,
      lng: -88.945808
    },
    stateName: "Mississippi"
  },
  {
    id: "029",
    state: "28",
    name: "Copiah County",
    position: {
      lat: 31.866915,
      lng: -90.448758
    },
    stateName: "Mississippi"
  },
  {
    id: "037",
    state: "28",
    name: "Franklin County",
    position: {
      lat: 31.477798,
      lng: -90.895884
    },
    stateName: "Mississippi"
  },
  {
    id: "049",
    state: "28",
    name: "Hinds County",
    position: {
      lat: 32.267924,
      lng: -90.4659
    },
    stateName: "Mississippi"
  },
  {
    id: "061",
    state: "28",
    name: "Jasper County",
    position: {
      lat: 32.016989,
      lng: -89.11943
    },
    stateName: "Mississippi"
  },
  {
    id: "065",
    state: "28",
    name: "Jefferson Davis County",
    position: {
      lat: 31.564734,
      lng: -89.826626
    },
    stateName: "Mississippi"
  },
  {
    id: "075",
    state: "28",
    name: "Lauderdale County",
    position: {
      lat: 32.403998,
      lng: -88.660449
    },
    stateName: "Mississippi"
  },
  {
    id: "121",
    state: "22",
    name: "W. Baton Rouge County",
    postal: "Parish",
    position: {
      lat: 30.464052,
      lng: -91.309808
    },
    stateName: "Louisiana"
  },
  {
    id: "127",
    state: "22",
    name: "Winn County",
    postal: "Parish",
    position: {
      lat: 31.941187,
      lng: -92.641269
    },
    stateName: "Louisiana"
  },
  {
    id: "009",
    state: "23",
    name: "Hancock County",
    position: {
      lat: 44.564906,
      lng: -68.370703
    },
    stateName: "Maine"
  },
  {
    id: "029",
    state: "23",
    name: "Washington County",
    position: {
      lat: 44.967009,
      lng: -67.609354
    },
    stateName: "Maine"
  },
  {
    id: "001",
    state: "24",
    name: "Allegany County",
    position: {
      lat: 39.612309,
      lng: -78.703108
    },
    stateName: "Maryland"
  },
  {
    id: "003",
    state: "24",
    name: "Anne Arundel County",
    position: {
      lat: 38.993374,
      lng: -76.560511
    },
    stateName: "Maryland"
  },
  {
    id: "009",
    state: "24",
    name: "Calvert County",
    position: {
      lat: 38.521358,
      lng: -76.525864
    },
    stateName: "Maryland"
  },
  {
    id: "017",
    state: "24",
    name: "Charles County",
    position: {
      lat: 38.472853,
      lng: -77.015427
    },
    stateName: "Maryland"
  },
  {
    id: "019",
    state: "24",
    name: "Dorchester County",
    position: {
      lat: 38.429196,
      lng: -76.047433
    },
    stateName: "Maryland"
  },
  {
    id: "029",
    state: "24",
    name: "Kent County",
    position: {
      lat: 39.239177,
      lng: -76.1242
    },
    stateName: "Maryland"
  },
  {
    id: "035",
    state: "24",
    name: "Queen Annes County",
    position: {
      lat: 39.040693,
      lng: -76.082405
    },
    stateName: "Maryland"
  },
  {
    id: "037",
    state: "24",
    name: "St. Marys County",
    position: {
      lat: 38.222666,
      lng: -76.534271
    },
    stateName: "Maryland"
  },
  {
    id: "039",
    state: "24",
    name: "Somerset County",
    position: {
      lat: 38.07445,
      lng: -75.853323
    },
    stateName: "Maryland"
  },
  {
    id: "041",
    state: "24",
    name: "Talbot County",
    position: {
      lat: 38.749427,
      lng: -76.17913
    },
    stateName: "Maryland"
  },
  {
    id: "011",
    state: "25",
    name: "Franklin County",
    position: {
      lat: 42.583791,
      lng: -72.591655
    },
    stateName: "Massachusetts"
  },
  {
    id: "009",
    state: "26",
    name: "Antrim County",
    position: {
      lat: 45.005457,
      lng: -85.175625
    },
    stateName: "Michigan"
  },
  {
    id: "015",
    state: "26",
    name: "Barry County",
    position: {
      lat: 42.582811,
      lng: -85.31455
    },
    stateName: "Michigan"
  },
  {
    id: "023",
    state: "26",
    name: "Branch County",
    position: {
      lat: 41.918585,
      lng: -85.066523
    },
    stateName: "Michigan"
  },
  {
    id: "035",
    state: "26",
    name: "Clare County",
    position: {
      lat: 43.991137,
      lng: -84.838325
    },
    stateName: "Michigan"
  },
  {
    id: "037",
    state: "26",
    name: "Clinton County",
    position: {
      lat: 42.950455,
      lng: -84.591695
    },
    stateName: "Michigan"
  },
  {
    id: "045",
    state: "26",
    name: "Eaton County",
    position: {
      lat: 42.589614,
      lng: -84.846524
    },
    stateName: "Michigan"
  },
  {
    id: "057",
    state: "26",
    name: "Gratiot County",
    position: {
      lat: 43.292326,
      lng: -84.60469
    },
    stateName: "Michigan"
  },
  {
    id: "061",
    state: "26",
    name: "Houghton County",
    position: {
      lat: 46.998305,
      lng: -88.652066
    },
    stateName: "Michigan"
  },
  {
    id: "073",
    state: "26",
    name: "Isabella County",
    position: {
      lat: 43.645233,
      lng: -84.839425
    },
    stateName: "Michigan"
  },
  {
    id: "077",
    state: "26",
    name: "Kalamazoo County",
    position: {
      lat: 42.246266,
      lng: -85.532854
    },
    stateName: "Michigan"
  },
  {
    id: "083",
    state: "26",
    name: "Keweenaw County",
    position: {
      lat: 47.681981,
      lng: -88.148802
    },
    stateName: "Michigan"
  },
  {
    id: "085",
    state: "26",
    name: "Lake County",
    position: {
      lat: 43.995187,
      lng: -85.8114
    },
    stateName: "Michigan"
  },
  {
    id: "091",
    state: "26",
    name: "Lenawee County",
    position: {
      lat: 41.895915,
      lng: -84.066853
    },
    stateName: "Michigan"
  },
  {
    id: "097",
    state: "26",
    name: "Mackinac County",
    position: {
      lat: 46.167981,
      lng: -85.303756
    },
    stateName: "Michigan"
  },
  {
    id: "103",
    state: "26",
    name: "Marquette County",
    position: {
      lat: 46.656597,
      lng: -87.584028
    },
    stateName: "Michigan"
  },
  {
    id: "107",
    state: "26",
    name: "Mecosta County",
    position: {
      lat: 43.635295,
      lng: -85.332751
    },
    stateName: "Michigan"
  },
  {
    id: "111",
    state: "26",
    name: "Midland County",
    position: {
      lat: 43.648378,
      lng: -84.37922
    },
    stateName: "Michigan"
  },
  {
    id: "117",
    state: "26",
    name: "Montcalm County",
    position: {
      lat: 43.312782,
      lng: -85.149468
    },
    stateName: "Michigan"
  },
  {
    id: "123",
    state: "26",
    name: "Newaygo County",
    position: {
      lat: 43.562709,
      lng: -85.791423
    },
    stateName: "Michigan"
  },
  {
    id: "133",
    state: "26",
    name: "Osceola County",
    position: {
      lat: 43.997552,
      lng: -85.322283
    },
    stateName: "Michigan"
  },
  {
    id: "143",
    state: "26",
    name: "Roscommon County",
    position: {
      lat: 44.339517,
      lng: -84.611272
    },
    stateName: "Michigan"
  },
  {
    id: "155",
    state: "26",
    name: "Shiawassee County",
    position: {
      lat: 42.951545,
      lng: -84.146352
    },
    stateName: "Michigan"
  },
  {
    id: "161",
    state: "26",
    name: "Washtenaw County",
    position: {
      lat: 42.252327,
      lng: -83.844634
    },
    stateName: "Michigan"
  },
  {
    id: "165",
    state: "26",
    name: "Wexford County",
    position: {
      lat: 44.331375,
      lng: -85.570046
    },
    stateName: "Michigan"
  },
  {
    id: "009",
    state: "27",
    name: "Benton County",
    position: {
      lat: 45.701227,
      lng: -94.00144
    },
    stateName: "Minnesota"
  },
  {
    id: "019",
    state: "27",
    name: "Carver County",
    position: {
      lat: 44.821381,
      lng: -93.800575
    },
    stateName: "Minnesota"
  },
  {
    id: "029",
    state: "27",
    name: "Clearwater County",
    position: {
      lat: 47.575873,
      lng: -95.371117
    },
    stateName: "Minnesota"
  },
  {
    id: "039",
    state: "27",
    name: "Dodge County",
    position: {
      lat: 44.020706,
      lng: -92.869353
    },
    stateName: "Minnesota"
  },
  {
    id: "051",
    state: "27",
    name: "Grant County",
    position: {
      lat: 45.930743,
      lng: -96.010699
    },
    stateName: "Minnesota"
  },
  {
    id: "057",
    state: "27",
    name: "Hubbard County",
    position: {
      lat: 47.095551,
      lng: -94.91329
    },
    stateName: "Minnesota"
  },
  {
    id: "067",
    state: "27",
    name: "Kandiyohi County",
    position: {
      lat: 45.152714,
      lng: -95.004981
    },
    stateName: "Minnesota"
  },
  {
    id: "097",
    state: "29",
    name: "Jasper County",
    position: {
      lat: 37.200865,
      lng: -94.338869
    },
    stateName: "Missouri"
  },
  {
    id: "103",
    state: "29",
    name: "Knox County",
    position: {
      lat: 40.136776,
      lng: -92.146809
    },
    stateName: "Missouri"
  },
  {
    id: "109",
    state: "29",
    name: "Lawrence County",
    position: {
      lat: 37.106135,
      lng: -93.833262
    },
    stateName: "Missouri"
  },
  {
    id: "117",
    state: "29",
    name: "Livingston County",
    position: {
      lat: 39.778587,
      lng: -93.548201
    },
    stateName: "Missouri"
  },
  {
    id: "131",
    state: "29",
    name: "Miller County",
    position: {
      lat: 38.21672,
      lng: -92.429871
    },
    stateName: "Missouri"
  },
  {
    id: "137",
    state: "29",
    name: "Monroe County",
    position: {
      lat: 39.49827,
      lng: -92.006458
    },
    stateName: "Missouri"
  },
  {
    id: "151",
    state: "29",
    name: "Osage County",
    position: {
      lat: 38.464254,
      lng: -91.859504
    },
    stateName: "Missouri"
  },
  {
    id: "159",
    state: "29",
    name: "Pettis County",
    position: {
      lat: 38.727367,
      lng: -93.285207
    },
    stateName: "Missouri"
  },
  {
    id: "169",
    state: "29",
    name: "Pulaski County",
    position: {
      lat: 37.824835,
      lng: -92.207022
    },
    stateName: "Missouri"
  },
  {
    id: "179",
    state: "29",
    name: "Reynolds County",
    position: {
      lat: 37.360857,
      lng: -90.969516
    },
    stateName: "Missouri"
  },
  {
    id: "195",
    state: "29",
    name: "Saline County",
    position: {
      lat: 39.13584,
      lng: -93.204164
    },
    stateName: "Missouri"
  },
  {
    id: "205",
    state: "29",
    name: "Shelby County",
    position: {
      lat: 39.797531,
      lng: -92.088719
    },
    stateName: "Missouri"
  },
  {
    id: "211",
    state: "29",
    name: "Sullivan County",
    position: {
      lat: 40.209587,
      lng: -93.109783
    },
    stateName: "Missouri"
  },
  {
    id: "221",
    state: "29",
    name: "Washington County",
    position: {
      lat: 37.942315,
      lng: -90.897056
    },
    stateName: "Missouri"
  },
  {
    id: "229",
    state: "29",
    name: "Wright County",
    position: {
      lat: 37.267636,
      lng: -92.48001
    },
    stateName: "Missouri"
  },
  {
    id: "013",
    state: "30",
    name: "Cascade County",
    position: {
      lat: 47.316443,
      lng: -111.350571
    },
    stateName: "Montana"
  },
  {
    id: "037",
    state: "30",
    name: "Golden Valley County",
    position: {
      lat: 46.380624,
      lng: -109.174586
    },
    stateName: "Montana"
  },
  {
    id: "043",
    state: "30",
    name: "Jefferson County",
    position: {
      lat: 46.126557,
      lng: -112.056771
    },
    stateName: "Montana"
  },
  {
    id: "121",
    state: "31",
    name: "Merrick County",
    position: {
      lat: 41.169801,
      lng: -98.031051
    },
    stateName: "Nebraska"
  },
  {
    id: "125",
    state: "31",
    name: "Nance County",
    position: {
      lat: 41.402386,
      lng: -97.991408
    },
    stateName: "Nebraska"
  },
  {
    id: "135",
    state: "31",
    name: "Perkins County",
    position: {
      lat: 40.856065,
      lng: -101.636443
    },
    stateName: "Nebraska"
  },
  {
    id: "137",
    state: "31",
    name: "Phelps County",
    position: {
      lat: 40.516365,
      lng: -99.406557
    },
    stateName: "Nebraska"
  },
  {
    id: "147",
    state: "31",
    name: "Richardson County",
    position: {
      lat: 40.123743,
      lng: -95.718603
    },
    stateName: "Nebraska"
  },
  {
    id: "159",
    state: "31",
    name: "Seward County",
    position: {
      lat: 40.871944,
      lng: -97.140383
    },
    stateName: "Nebraska"
  },
  {
    id: "163",
    state: "31",
    name: "Sherman County",
    position: {
      lat: 41.218743,
      lng: -98.972849
    },
    stateName: "Nebraska"
  },
  {
    id: "171",
    state: "31",
    name: "Thomas County",
    position: {
      lat: 41.84862,
      lng: -100.506911
    },
    stateName: "Nebraska"
  },
  {
    id: "183",
    state: "31",
    name: "Wheeler County",
    position: {
      lat: 41.937859,
      lng: -98.522289
    },
    stateName: "Nebraska"
  },
  {
    id: "009",
    state: "32",
    name: "Esmeralda County",
    position: {
      lat: 37.778966,
      lng: -117.632382
    },
    stateName: "Nevada"
  },
  {
    id: "021",
    state: "32",
    name: "Mineral County",
    position: {
      lat: 38.516647,
      lng: -118.416279
    },
    stateName: "Nevada"
  },
  {
    id: "033",
    state: "32",
    name: "White Pine County",
    position: {
      lat: 39.417804,
      lng: -114.900945
    },
    stateName: "Nevada"
  },
  {
    id: "007",
    state: "34",
    name: "Camden County",
    position: {
      lat: 39.802352,
      lng: -74.961251
    },
    stateName: "New Jersey"
  },
  {
    id: "015",
    state: "34",
    name: "Gloucester County",
    position: {
      lat: 39.721019,
      lng: -75.143708
    },
    stateName: "New Jersey"
  },
  {
    id: "031",
    state: "34",
    name: "Passaic County",
    position: {
      lat: 41.033763,
      lng: -74.300308
    },
    stateName: "New Jersey"
  },
  {
    id: "001",
    state: "35",
    name: "Bernalillo County",
    position: {
      lat: 35.054002,
      lng: -106.669065
    },
    stateName: "New Mexico"
  },
  {
    id: "006",
    state: "35",
    name: "Cibola County",
    position: {
      lat: 34.93205,
      lng: -108.000255
    },
    stateName: "New Mexico"
  },
  {
    id: "051",
    state: "30",
    name: "Liberty County",
    position: {
      lat: 48.559654,
      lng: -111.036924
    },
    stateName: "Montana"
  },
  {
    id: "059",
    state: "30",
    name: "Meagher County",
    position: {
      lat: 46.585706,
      lng: -110.921781
    },
    stateName: "Montana"
  },
  {
    id: "069",
    state: "30",
    name: "Petroleum County",
    position: {
      lat: 47.141917,
      lng: -108.226575
    },
    stateName: "Montana"
  },
  {
    id: "093",
    state: "30",
    name: "Silver Bow County",
    position: {
      lat: 45.895959,
      lng: -112.660222
    },
    stateName: "Montana"
  },
  {
    id: "099",
    state: "30",
    name: "Teton County",
    position: {
      lat: 47.819035,
      lng: -112.281725
    },
    stateName: "Montana"
  },
  {
    id: "107",
    state: "30",
    name: "Wheatland County",
    position: {
      lat: 46.497047,
      lng: -109.857147
    },
    stateName: "Montana"
  },
  {
    id: "003",
    state: "31",
    name: "Antelope County",
    position: {
      lat: 42.183225,
      lng: -98.058037
    },
    stateName: "Nebraska"
  },
  {
    id: "009",
    state: "31",
    name: "Blaine County",
    position: {
      lat: 41.92523,
      lng: -99.990771
    },
    stateName: "Nebraska"
  },
  {
    id: "023",
    state: "31",
    name: "Butler County",
    position: {
      lat: 41.226072,
      lng: -97.13204
    },
    stateName: "Nebraska"
  },
  {
    id: "035",
    state: "31",
    name: "Clay County",
    position: {
      lat: 40.523669,
      lng: -98.05105
    },
    stateName: "Nebraska"
  },
  {
    id: "037",
    state: "31",
    name: "Colfax County",
    position: {
      lat: 41.574675,
      lng: -97.089455
    },
    stateName: "Nebraska"
  },
  {
    id: "047",
    state: "31",
    name: "Dawson County",
    position: {
      lat: 40.867378,
      lng: -99.815154
    },
    stateName: "Nebraska"
  },
  {
    id: "053",
    state: "31",
    name: "Dodge County",
    position: {
      lat: 41.577008,
      lng: -96.645853
    },
    stateName: "Nebraska"
  },
  {
    id: "063",
    state: "31",
    name: "Frontier County",
    position: {
      lat: 40.530947,
      lng: -100.406683
    },
    stateName: "Nebraska"
  },
  {
    id: "073",
    state: "31",
    name: "Gosper County",
    position: {
      lat: 40.509838,
      lng: -99.824078
    },
    stateName: "Nebraska"
  },
  {
    id: "085",
    state: "31",
    name: "Hayes County",
    position: {
      lat: 40.535786,
      lng: -101.05558
    },
    stateName: "Nebraska"
  },
  {
    id: "093",
    state: "31",
    name: "Howard County",
    position: {
      lat: 41.219403,
      lng: -98.513303
    },
    stateName: "Nebraska"
  },
  {
    id: "097",
    state: "31",
    name: "Johnson County",
    position: {
      lat: 40.395457,
      lng: -96.268562
    },
    stateName: "Nebraska"
  },
  {
    id: "117",
    state: "31",
    name: "Mc Pherson County",
    position: {
      lat: 41.596473,
      lng: -101.060237
    },
    stateName: "Nebraska"
  },
  {
    id: "079",
    state: "28",
    name: "Leake County",
    position: {
      lat: 32.753268,
      lng: -89.522568
    },
    stateName: "Mississippi"
  },
  {
    id: "083",
    state: "28",
    name: "Leflore County",
    position: {
      lat: 33.54979,
      lng: -90.294934
    },
    stateName: "Mississippi"
  },
  {
    id: "095",
    state: "28",
    name: "Monroe County",
    position: {
      lat: 33.89003,
      lng: -88.485038
    },
    stateName: "Mississippi"
  },
  {
    id: "099",
    state: "28",
    name: "Neshoba County",
    position: {
      lat: 32.752518,
      lng: -89.119274
    },
    stateName: "Mississippi"
  },
  {
    id: "103",
    state: "28",
    name: "Noxubee County",
    position: {
      lat: 33.10627,
      lng: -88.565787
    },
    stateName: "Mississippi"
  },
  {
    id: "115",
    state: "28",
    name: "Pontotoc County",
    position: {
      lat: 34.227081,
      lng: -89.037239
    },
    stateName: "Mississippi"
  },
  {
    id: "117",
    state: "28",
    name: "Prentiss County",
    position: {
      lat: 34.620866,
      lng: -88.52224
    },
    stateName: "Mississippi"
  },
  {
    id: "127",
    state: "28",
    name: "Simpson County",
    position: {
      lat: 31.902503,
      lng: -89.917707
    },
    stateName: "Mississippi"
  },
  {
    id: "139",
    state: "28",
    name: "Tippah County",
    position: {
      lat: 34.763618,
      lng: -88.918819
    },
    stateName: "Mississippi"
  },
  {
    id: "151",
    state: "28",
    name: "Washington County",
    position: {
      lat: 33.273131,
      lng: -90.94443
    },
    stateName: "Mississippi"
  },
  {
    id: "001",
    state: "29",
    name: "Adair County",
    position: {
      lat: 40.190666,
      lng: -92.603592
    },
    stateName: "Missouri"
  },
  {
    id: "009",
    state: "29",
    name: "Barry County",
    position: {
      lat: 36.699378,
      lng: -93.834326
    },
    stateName: "Missouri"
  },
  {
    id: "021",
    state: "29",
    name: "Buchanan County",
    position: {
      lat: 39.66037,
      lng: -94.808173
    },
    stateName: "Missouri"
  },
  {
    id: "033",
    state: "29",
    name: "Carroll County",
    position: {
      lat: 39.427375,
      lng: -93.500227
    },
    stateName: "Missouri"
  },
  {
    id: "043",
    state: "29",
    name: "Christian County",
    position: {
      lat: 36.969739,
      lng: -93.187614
    },
    stateName: "Missouri"
  },
  {
    id: "053",
    state: "29",
    name: "Cooper County",
    position: {
      lat: 38.845386,
      lng: -92.812323
    },
    stateName: "Missouri"
  },
  {
    id: "065",
    state: "29",
    name: "Dent County",
    position: {
      lat: 37.60325,
      lng: -91.495916
    },
    stateName: "Missouri"
  },
  {
    id: "075",
    state: "29",
    name: "Gentry County",
    position: {
      lat: 40.209379,
      lng: -94.40411
    },
    stateName: "Missouri"
  },
  {
    id: "085",
    state: "29",
    name: "Hickory County",
    position: {
      lat: 37.9367,
      lng: -93.322835
    },
    stateName: "Missouri"
  },
  {
    id: "001",
    state: "37",
    name: "Alamance County",
    position: {
      lat: 36.041974,
      lng: -79.399935
    },
    stateName: "North Carolina"
  },
  {
    id: "005",
    state: "37",
    name: "Alleghany County",
    position: {
      lat: 36.489356,
      lng: -81.132299
    },
    stateName: "North Carolina"
  },
  {
    id: "017",
    state: "37",
    name: "Bladen County",
    position: {
      lat: 34.591949,
      lng: -78.539513
    },
    stateName: "North Carolina"
  },
  {
    id: "021",
    state: "37",
    name: "Buncombe County",
    position: {
      lat: 35.609371,
      lng: -82.530426
    },
    stateName: "North Carolina"
  },
  {
    id: "027",
    state: "37",
    name: "Caldwell County",
    position: {
      lat: 35.957857,
      lng: -81.530076
    },
    stateName: "North Carolina"
  },
  {
    id: "055",
    state: "37",
    name: "Dare County",
    position: {
      lat: 35.606269,
      lng: -75.767536
    },
    stateName: "North Carolina"
  },
  {
    id: "057",
    state: "37",
    name: "Davidson County",
    position: {
      lat: 35.795123,
      lng: -80.206525
    },
    stateName: "North Carolina"
  },
  {
    id: "067",
    state: "37",
    name: "Forsyth County",
    position: {
      lat: 36.131667,
      lng: -80.257289
    },
    stateName: "North Carolina"
  },
  {
    id: "081",
    state: "37",
    name: "Guilford County",
    position: {
      lat: 36.079065,
      lng: -79.788665
    },
    stateName: "North Carolina"
  },
  {
    id: "091",
    state: "37",
    name: "Hertford County",
    position: {
      lat: 36.363517,
      lng: -76.981616
    },
    stateName: "North Carolina"
  },
  {
    id: "103",
    state: "37",
    name: "Jones County",
    position: {
      lat: 35.03216,
      lng: -77.356443
    },
    stateName: "North Carolina"
  },
  {
    id: "117",
    state: "37",
    name: "Martin County",
    position: {
      lat: 35.841059,
      lng: -77.112867
    },
    stateName: "North Carolina"
  },
  {
    id: "127",
    state: "37",
    name: "Nash County",
    position: {
      lat: 35.965945,
      lng: -77.987555
    },
    stateName: "North Carolina"
  },
  {
    id: "141",
    state: "37",
    name: "Pender County",
    position: {
      lat: 34.512581,
      lng: -77.888029
    },
    stateName: "North Carolina"
  },
  {
    id: "151",
    state: "37",
    name: "Randolph County",
    position: {
      lat: 35.709915,
      lng: -79.806215
    },
    stateName: "North Carolina"
  },
  {
    id: "165",
    state: "37",
    name: "Scotland County",
    position: {
      lat: 34.840023,
      lng: -79.477337
    },
    stateName: "North Carolina"
  },
  {
    id: "167",
    state: "37",
    name: "Stanly County",
    position: {
      lat: 35.310523,
      lng: -80.254355
    },
    stateName: "North Carolina"
  },
  {
    id: "055",
    state: "39",
    name: "Geauga County",
    position: {
      lat: 41.499322,
      lng: -81.173505
    },
    stateName: "Ohio"
  },
  {
    id: "063",
    state: "39",
    name: "Hancock County",
    position: {
      lat: 41.000471,
      lng: -83.666034
    },
    stateName: "Ohio"
  },
  {
    id: "065",
    state: "39",
    name: "Hardin County",
    position: {
      lat: 40.660415,
      lng: -83.664077
    },
    stateName: "Ohio"
  },
  {
    id: "075",
    state: "39",
    name: "Holmes County",
    position: {
      lat: 40.565309,
      lng: -81.929869
    },
    stateName: "Ohio"
  },
  {
    id: "089",
    state: "39",
    name: "Licking County",
    position: {
      lat: 40.093609,
      lng: -82.481251
    },
    stateName: "Ohio"
  },
  {
    id: "097",
    state: "39",
    name: "Madison County",
    position: {
      lat: 39.896607,
      lng: -83.400885
    },
    stateName: "Ohio"
  },
  {
    id: "109",
    state: "39",
    name: "Miami County",
    position: {
      lat: 40.053326,
      lng: -84.228414
    },
    stateName: "Ohio"
  },
  {
    id: "121",
    state: "39",
    name: "Noble County",
    position: {
      lat: 39.767232,
      lng: -81.452604
    },
    stateName: "Ohio"
  },
  {
    id: "131",
    state: "39",
    name: "Pike County",
    position: {
      lat: 39.071365,
      lng: -83.052921
    },
    stateName: "Ohio"
  },
  {
    id: "143",
    state: "39",
    name: "Sandusky County",
    position: {
      lat: 41.355291,
      lng: -83.142735
    },
    stateName: "Ohio"
  },
  {
    id: "147",
    state: "39",
    name: "Seneca County",
    position: {
      lat: 41.120008,
      lng: -83.127436
    },
    stateName: "Ohio"
  },
  {
    id: "159",
    state: "39",
    name: "Union County",
    position: {
      lat: 40.295901,
      lng: -83.367042
    },
    stateName: "Ohio"
  },
  {
    id: "169",
    state: "39",
    name: "Wayne County",
    position: {
      lat: 40.829661,
      lng: -81.887194
    },
    stateName: "Ohio"
  },
  {
    id: "173",
    state: "39",
    name: "Wood County",
    position: {
      lat: 41.360183,
      lng: -83.622682
    },
    stateName: "Ohio"
  },
  {
    id: "009",
    state: "40",
    name: "Beckham County",
    position: {
      lat: 35.273945,
      lng: -99.671638
    },
    stateName: "Oklahoma"
  },
  {
    id: "019",
    state: "40",
    name: "Carter County",
    position: {
      lat: 34.251848,
      lng: -97.287927
    },
    stateName: "Oklahoma"
  },
  {
    id: "031",
    state: "40",
    name: "Comanche County",
    position: {
      lat: 34.662628,
      lng: -98.476597
    },
    stateName: "Oklahoma"
  },
  {
    id: "043",
    state: "40",
    name: "Dewey County",
    position: {
      lat: 35.978433,
      lng: -99.014094
    },
    stateName: "Oklahoma"
  },
  {
    id: "053",
    state: "40",
    name: "Grant County",
    position: {
      lat: 36.788254,
      lng: -97.788151
    },
    stateName: "Oklahoma"
  },
  {
    id: "181",
    state: "37",
    name: "Vance County",
    position: {
      lat: 36.365481,
      lng: -78.405434
    },
    stateName: "North Carolina"
  },
  {
    id: "191",
    state: "37",
    name: "Wayne County",
    position: {
      lat: 35.362741,
      lng: -78.004826
    },
    stateName: "North Carolina"
  },
  {
    id: "197",
    state: "37",
    name: "Yadkin County",
    position: {
      lat: 36.158765,
      lng: -80.665164
    },
    stateName: "North Carolina"
  },
  {
    id: "007",
    state: "38",
    name: "Billings County",
    position: {
      lat: 47.009283,
      lng: -103.364924
    },
    stateName: "North Dakota"
  },
  {
    id: "019",
    state: "38",
    name: "Cavalier County",
    position: {
      lat: 48.768439,
      lng: -98.46379
    },
    stateName: "North Dakota"
  },
  {
    id: "031",
    state: "38",
    name: "Foster County",
    position: {
      lat: 47.464327,
      lng: -98.872817
    },
    stateName: "North Dakota"
  },
  {
    id: "035",
    state: "38",
    name: "Grand Forks County",
    position: {
      lat: 47.926003,
      lng: -97.450851
    },
    stateName: "North Dakota"
  },
  {
    id: "045",
    state: "38",
    name: "La Moure County",
    position: {
      lat: 46.464195,
      lng: -98.526057
    },
    stateName: "North Dakota"
  },
  {
    id: "061",
    state: "38",
    name: "Mountrail County",
    position: {
      lat: 48.210291,
      lng: -102.364756
    },
    stateName: "North Dakota"
  },
  {
    id: "073",
    state: "38",
    name: "Ransom County",
    position: {
      lat: 46.449276,
      lng: -97.647554
    },
    stateName: "North Dakota"
  },
  {
    id: "079",
    state: "38",
    name: "Rolette County",
    position: {
      lat: 48.768272,
      lng: -99.840463
    },
    stateName: "North Dakota"
  },
  {
    id: "091",
    state: "38",
    name: "Steele County",
    position: {
      lat: 47.458288,
      lng: -97.718895
    },
    stateName: "North Dakota"
  },
  {
    id: "103",
    state: "38",
    name: "Wells County",
    position: {
      lat: 47.580851,
      lng: -99.671502
    },
    stateName: "North Dakota"
  },
  {
    id: "003",
    state: "39",
    name: "Allen County",
    position: {
      lat: 40.771528,
      lng: -84.106546
    },
    stateName: "Ohio"
  },
  {
    id: "015",
    state: "39",
    name: "Brown County",
    position: {
      lat: 38.931377,
      lng: -83.866772
    },
    stateName: "Ohio"
  },
  {
    id: "027",
    state: "39",
    name: "Clinton County",
    position: {
      lat: 39.414041,
      lng: -83.814542
    },
    stateName: "Ohio"
  },
  {
    id: "039",
    state: "39",
    name: "Defiance County",
    position: {
      lat: 41.321679,
      lng: -84.486433
    },
    stateName: "Ohio"
  },
  {
    id: "047",
    state: "39",
    name: "Fayette County",
    position: {
      lat: 39.553845,
      lng: -83.459327
    },
    stateName: "Ohio"
  },
  {
    id: "015",
    state: "35",
    name: "Eddy County",
    position: {
      lat: 32.457858,
      lng: -104.306471
    },
    stateName: "New Mexico"
  },
  {
    id: "027",
    state: "35",
    name: "Lincoln County",
    position: {
      lat: 33.740941,
      lng: -105.449083
    },
    stateName: "New Mexico"
  },
  {
    id: "028",
    state: "35",
    name: "Los Alamos County",
    position: {
      lat: 35.870047,
      lng: -106.307968
    },
    stateName: "New Mexico"
  },
  {
    id: "045",
    state: "35",
    name: "San Juan County",
    position: {
      lat: 36.511625,
      lng: -108.324578
    },
    stateName: "New Mexico"
  },
  {
    id: "053",
    state: "35",
    name: "Socorro County",
    position: {
      lat: 33.991614,
      lng: -106.939003
    },
    stateName: "New Mexico"
  },
  {
    id: "003",
    state: "36",
    name: "Allegany County",
    position: {
      lat: 42.247894,
      lng: -78.026176
    },
    stateName: "New York"
  },
  {
    id: "021",
    state: "36",
    name: "Columbia County",
    position: {
      lat: 42.247729,
      lng: -73.626805
    },
    stateName: "New York"
  },
  {
    id: "037",
    state: "36",
    name: "Genesee County",
    position: {
      lat: 43.00091,
      lng: -78.192778
    },
    stateName: "New York"
  },
  {
    id: "043",
    state: "36",
    name: "Herkimer County",
    position: {
      lat: 43.461635,
      lng: -74.894694
    },
    stateName: "New York"
  },
  {
    id: "057",
    state: "36",
    name: "Montgomery County",
    position: {
      lat: 42.900891,
      lng: -74.435358
    },
    stateName: "New York"
  },
  {
    id: "069",
    state: "36",
    name: "Ontario County",
    position: {
      lat: 42.856695,
      lng: -77.303277
    },
    stateName: "New York"
  },
  {
    id: "083",
    state: "36",
    name: "Rensselaer County",
    position: {
      lat: 42.710421,
      lng: -73.513845
    },
    stateName: "New York"
  },
  {
    id: "095",
    state: "36",
    name: "Schoharie County",
    position: {
      lat: 42.591294,
      lng: -74.438172
    },
    stateName: "New York"
  },
  {
    id: "109",
    state: "36",
    name: "Tompkins County",
    position: {
      lat: 42.453281,
      lng: -76.473712
    },
    stateName: "New York"
  },
  {
    id: "121",
    state: "36",
    name: "Wyoming County",
    position: {
      lat: 42.701363,
      lng: -78.228567
    },
    stateName: "New York"
  },
  {
    id: "123",
    state: "36",
    name: "Yates County",
    position: {
      lat: 42.638237,
      lng: -77.104324
    },
    stateName: "New York"
  },
  {
    id: "013",
    state: "42",
    name: "Blair County",
    position: {
      lat: 40.497926,
      lng: -78.31064
    },
    stateName: "Pennsylvania"
  },
  {
    id: "015",
    state: "42",
    name: "Bradford County",
    position: {
      lat: 41.791495,
      lng: -76.502124
    },
    stateName: "Pennsylvania"
  },
  {
    id: "021",
    state: "42",
    name: "Cambria County",
    position: {
      lat: 40.494127,
      lng: -78.715284
    },
    stateName: "Pennsylvania"
  },
  {
    id: "031",
    state: "42",
    name: "Clarion County",
    position: {
      lat: 41.198239,
      lng: -79.420404
    },
    stateName: "Pennsylvania"
  },
  {
    id: "047",
    state: "42",
    name: "Elk County",
    position: {
      lat: 41.427334,
      lng: -78.653938
    },
    stateName: "Pennsylvania"
  },
  {
    id: "063",
    state: "42",
    name: "Indiana County",
    position: {
      lat: 40.651432,
      lng: -79.087545
    },
    stateName: "Pennsylvania"
  },
  {
    id: "069",
    state: "42",
    name: "Lackawanna County",
    position: {
      lat: 41.44025,
      lng: -75.609587
    },
    stateName: "Pennsylvania"
  },
  {
    id: "087",
    state: "42",
    name: "Mifflin County",
    position: {
      lat: 40.61189,
      lng: -77.620661
    },
    stateName: "Pennsylvania"
  },
  {
    id: "093",
    state: "42",
    name: "Montour County",
    position: {
      lat: 41.029261,
      lng: -76.665259
    },
    stateName: "Pennsylvania"
  },
  {
    id: "105",
    state: "42",
    name: "Potter County",
    position: {
      lat: 41.748222,
      lng: -77.894735
    },
    stateName: "Pennsylvania"
  },
  {
    id: "113",
    state: "42",
    name: "Sullivan County",
    position: {
      lat: 41.43979,
      lng: -76.511525
    },
    stateName: "Pennsylvania"
  },
  {
    id: "123",
    state: "42",
    name: "Warren County",
    position: {
      lat: 41.843669,
      lng: -79.313173
    },
    stateName: "Pennsylvania"
  },
  {
    id: "005",
    state: "45",
    name: "Allendale County",
    position: {
      lat: 32.979784,
      lng: -81.363421
    },
    stateName: "South Carolina"
  },
  {
    id: "009",
    state: "45",
    name: "Bamberg County",
    position: {
      lat: 33.203021,
      lng: -81.053161
    },
    stateName: "South Carolina"
  },
  {
    id: "027",
    state: "45",
    name: "Clarendon County",
    position: {
      lat: 33.664682,
      lng: -80.217889
    },
    stateName: "South Carolina"
  },
  {
    id: "039",
    state: "45",
    name: "Fairfield County",
    position: {
      lat: 34.395669,
      lng: -81.127001
    },
    stateName: "South Carolina"
  },
  {
    id: "005",
    state: "47",
    name: "Benton County",
    position: {
      lat: 36.069253,
      lng: -88.071212
    },
    stateName: "Tennessee"
  },
  {
    id: "007",
    state: "47",
    name: "Bledsoe County",
    position: {
      lat: 35.593668,
      lng: -85.205979
    },
    stateName: "Tennessee"
  },
  {
    id: "013",
    state: "47",
    name: "Campbell County",
    position: {
      lat: 36.401592,
      lng: -84.15925
    },
    stateName: "Tennessee"
  },
  {
    id: "027",
    state: "47",
    name: "Clay County",
    position: {
      lat: 36.545765,
      lng: -85.545718
    },
    stateName: "Tennessee"
  },
  {
    id: "031",
    state: "47",
    name: "Coffee County",
    position: {
      lat: 35.488759,
      lng: -86.078219
    },
    stateName: "Tennessee"
  },
  {
    id: "045",
    state: "47",
    name: "Dyer County",
    position: {
      lat: 36.054196,
      lng: -89.398306
    },
    stateName: "Tennessee"
  },
  {
    id: "057",
    state: "47",
    name: "Grainger County",
    position: {
      lat: 36.277463,
      lng: -83.509493
    },
    stateName: "Tennessee"
  },
  {
    id: "069",
    state: "47",
    name: "Hardeman County",
    position: {
      lat: 35.218131,
      lng: -88.989037
    },
    stateName: "Tennessee"
  },
  {
    id: "081",
    state: "47",
    name: "Hickman County",
    position: {
      lat: 35.802396,
      lng: -87.467114
    },
    stateName: "Tennessee"
  },
  {
    id: "093",
    state: "47",
    name: "Knox County",
    position: {
      lat: 35.992727,
      lng: -83.937721
    },
    stateName: "Tennessee"
  },
  {
    id: "105",
    state: "47",
    name: "Loudon County",
    position: {
      lat: 35.73745,
      lng: -84.316204
    },
    stateName: "Tennessee"
  },
  {
    id: "117",
    state: "47",
    name: "Marshall County",
    position: {
      lat: 35.468387,
      lng: -86.765886
    },
    stateName: "Tennessee"
  },
  {
    id: "129",
    state: "47",
    name: "Morgan County",
    position: {
      lat: 36.138697,
      lng: -84.639262
    },
    stateName: "Tennessee"
  },
  {
    id: "139",
    state: "47",
    name: "Polk County",
    position: {
      lat: 35.109437,
      lng: -84.541112
    },
    stateName: "Tennessee"
  },
  {
    id: "151",
    state: "47",
    name: "Scott County",
    position: {
      lat: 36.437239,
      lng: -84.498386
    },
    stateName: "Tennessee"
  },
  {
    id: "173",
    state: "47",
    name: "Union County",
    position: {
      lat: 36.28414,
      lng: -83.836088
    },
    stateName: "Tennessee"
  },
  {
    id: "179",
    state: "47",
    name: "Washington County",
    position: {
      lat: 36.295665,
      lng: -82.495037
    },
    stateName: "Tennessee"
  },
  {
    id: "049",
    state: "45",
    name: "Hampton County",
    position: {
      lat: 32.778323,
      lng: -81.143362
    },
    stateName: "South Carolina"
  },
  {
    id: "053",
    state: "45",
    name: "Jasper County",
    position: {
      lat: 32.43059,
      lng: -81.021627
    },
    stateName: "South Carolina"
  },
  {
    id: "075",
    state: "45",
    name: "Orangeburg County",
    position: {
      lat: 33.436135,
      lng: -80.802913
    },
    stateName: "South Carolina"
  },
  {
    id: "081",
    state: "45",
    name: "Saluda County",
    position: {
      lat: 34.005278,
      lng: -81.727903
    },
    stateName: "South Carolina"
  },
  {
    id: "003",
    state: "46",
    name: "Aurora County",
    position: {
      lat: 43.724719,
      lng: -98.577587
    },
    stateName: "South Dakota"
  },
  {
    id: "007",
    state: "46",
    name: "Bennett County",
    position: {
      lat: 43.184826,
      lng: -101.676426
    },
    stateName: "South Dakota"
  },
  {
    id: "017",
    state: "46",
    name: "Buffalo County",
    position: {
      lat: 44.044306,
      lng: -99.203998
    },
    stateName: "South Dakota"
  },
  {
    id: "029",
    state: "46",
    name: "Codington County",
    position: {
      lat: 44.966324,
      lng: -97.198843
    },
    stateName: "South Dakota"
  },
  {
    id: "035",
    state: "46",
    name: "Davison County",
    position: {
      lat: 43.680439,
      lng: -98.155868
    },
    stateName: "South Dakota"
  },
  {
    id: "043",
    state: "46",
    name: "Douglas County",
    position: {
      lat: 43.391506,
      lng: -98.358433
    },
    stateName: "South Dakota"
  },
  {
    id: "055",
    state: "46",
    name: "Haakon County",
    position: {
      lat: 44.284312,
      lng: -101.59179
    },
    stateName: "South Dakota"
  },
  {
    id: "061",
    state: "46",
    name: "Hanson County",
    position: {
      lat: 43.680612,
      lng: -97.796845
    },
    stateName: "South Dakota"
  },
  {
    id: "087",
    state: "46",
    name: "Mc Cook County",
    position: {
      lat: 43.678924,
      lng: -97.362036
    },
    stateName: "South Dakota"
  },
  {
    id: "095",
    state: "46",
    name: "Mellette County",
    position: {
      lat: 43.58493,
      lng: -100.760598
    },
    stateName: "South Dakota"
  },
  {
    id: "103",
    state: "46",
    name: "Pennington County",
    position: {
      lat: 44.002349,
      lng: -102.823802
    },
    stateName: "South Dakota"
  },
  {
    id: "111",
    state: "46",
    name: "Sanborn County",
    position: {
      lat: 44.021032,
      lng: -98.092105
    },
    stateName: "South Dakota"
  },
  {
    id: "125",
    state: "46",
    name: "Turner County",
    position: {
      lat: 43.30867,
      lng: -97.150185
    },
    stateName: "South Dakota"
  },
  {
    id: "137",
    state: "46",
    name: "Ziebach County",
    position: {
      lat: 44.981666,
      lng: -101.669225
    },
    stateName: "South Dakota"
  },
  {
    id: "059",
    state: "40",
    name: "Harper County",
    position: {
      lat: 36.800456,
      lng: -99.662842
    },
    stateName: "Oklahoma"
  },
  {
    id: "063",
    state: "40",
    name: "Hughes County",
    position: {
      lat: 35.052934,
      lng: -96.251183
    },
    stateName: "Oklahoma"
  },
  {
    id: "073",
    state: "40",
    name: "Kingfisher County",
    position: {
      lat: 35.949431,
      lng: -97.934568
    },
    stateName: "Oklahoma"
  },
  {
    id: "081",
    state: "40",
    name: "Lincoln County",
    position: {
      lat: 35.703118,
      lng: -96.881392
    },
    stateName: "Oklahoma"
  },
  {
    id: "087",
    state: "40",
    name: "Mcclain County",
    position: {
      lat: 35.016414,
      lng: -97.449811
    },
    stateName: "Oklahoma"
  },
  {
    id: "097",
    state: "40",
    name: "Mayes County",
    position: {
      lat: 36.303804,
      lng: -95.235638
    },
    stateName: "Oklahoma"
  },
  {
    id: "109",
    state: "40",
    name: "Oklahoma County",
    position: {
      lat: 35.554611,
      lng: -97.409401
    },
    stateName: "Oklahoma"
  },
  {
    id: "123",
    state: "40",
    name: "Pontotoc County",
    position: {
      lat: 34.721071,
      lng: -96.692738
    },
    stateName: "Oklahoma"
  },
  {
    id: "127",
    state: "40",
    name: "Pushmataha County",
    position: {
      lat: 34.377896,
      lng: -95.408085
    },
    stateName: "Oklahoma"
  },
  {
    id: "147",
    state: "40",
    name: "Washington County",
    position: {
      lat: 36.70438,
      lng: -95.906155
    },
    stateName: "Oklahoma"
  },
  {
    id: "153",
    state: "40",
    name: "Woodward County",
    position: {
      lat: 36.425619,
      lng: -99.273661
    },
    stateName: "Oklahoma"
  },
  {
    id: "017",
    state: "41",
    name: "Deschutes County",
    position: {
      lat: 43.915118,
      lng: -121.225575
    },
    stateName: "Oregon"
  },
  {
    id: "031",
    state: "41",
    name: "Jefferson County",
    position: {
      lat: 44.64379,
      lng: -121.183021
    },
    stateName: "Oregon"
  },
  {
    id: "037",
    state: "41",
    name: "Lake County",
    position: {
      lat: 42.788401,
      lng: -120.38979
    },
    stateName: "Oregon"
  },
  {
    id: "053",
    state: "41",
    name: "Polk County",
    position: {
      lat: 44.904395,
      lng: -123.397329
    },
    stateName: "Oregon"
  },
  {
    id: "069",
    state: "41",
    name: "Wheeler County",
    position: {
      lat: 44.736411,
      lng: -120.026875
    },
    stateName: "Oregon"
  },
  {
    id: "005",
    state: "42",
    name: "Armstrong County",
    position: {
      lat: 40.812379,
      lng: -79.464128
    },
    stateName: "Pennsylvania"
  },
  {
    id: "357",
    state: "48",
    name: "Ochiltree County",
    position: {
      lat: 36.278744,
      lng: -100.815864
    },
    stateName: "Texas"
  },
  {
    id: "359",
    state: "48",
    name: "Oldham County",
    position: {
      lat: 35.401921,
      lng: -102.59762
    },
    stateName: "Texas"
  },
  {
    id: "367",
    state: "48",
    name: "Parker County",
    position: {
      lat: 32.777096,
      lng: -97.805905
    },
    stateName: "Texas"
  },
  {
    id: "379",
    state: "48",
    name: "Rains County",
    position: {
      lat: 32.87058,
      lng: -95.79544
    },
    stateName: "Texas"
  },
  {
    id: "381",
    state: "48",
    name: "Randall County",
    position: {
      lat: 34.962529,
      lng: -101.895547
    },
    stateName: "Texas"
  },
  {
    id: "393",
    state: "48",
    name: "Roberts County",
    position: {
      lat: 35.836216,
      lng: -100.807555
    },
    stateName: "Texas"
  },
  {
    id: "397",
    state: "48",
    name: "Rockwall County",
    position: {
      lat: 32.889216,
      lng: -96.407501
    },
    stateName: "Texas"
  },
  {
    id: "411",
    state: "48",
    name: "San Saba County",
    position: {
      lat: 31.155138,
      lng: -98.819292
    },
    stateName: "Texas"
  },
  {
    id: "425",
    state: "48",
    name: "Somervell County",
    position: {
      lat: 32.217942,
      lng: -97.769211
    },
    stateName: "Texas"
  },
  {
    id: "429",
    state: "48",
    name: "Stephens County",
    position: {
      lat: 32.731531,
      lng: -98.840081
    },
    stateName: "Texas"
  },
  {
    id: "433",
    state: "48",
    name: "Stonewall County",
    position: {
      lat: 33.17958,
      lng: -100.253807
    },
    stateName: "Texas"
  },
  {
    id: "445",
    state: "48",
    name: "Terry County",
    position: {
      lat: 33.171229,
      lng: -102.339284
    },
    stateName: "Texas"
  },
  {
    id: "449",
    state: "48",
    name: "Titus County",
    position: {
      lat: 33.214599,
      lng: -94.966783
    },
    stateName: "Texas"
  },
  {
    id: "459",
    state: "48",
    name: "Upshur County",
    position: {
      lat: 32.735878,
      lng: -94.941649
    },
    stateName: "Texas"
  },
  {
    id: "463",
    state: "48",
    name: "Uvalde County",
    position: {
      lat: 29.35034,
      lng: -99.761074
    },
    stateName: "Texas"
  },
  {
    id: "475",
    state: "48",
    name: "Ward County",
    position: {
      lat: 31.513069,
      lng: -103.105113
    },
    stateName: "Texas"
  },
  {
    id: "491",
    state: "48",
    name: "Williamson County",
    position: {
      lat: 30.64903,
      lng: -97.605069
    },
    stateName: "Texas"
  },
  {
    id: "497",
    state: "48",
    name: "Wise County",
    position: {
      lat: 33.219095,
      lng: -97.653997
    },
    stateName: "Texas"
  },
  {
    id: "507",
    state: "48",
    name: "Zavala County",
    position: {
      lat: 28.864652,
      lng: -99.75983
    },
    stateName: "Texas"
  },
  {
    id: "011",
    state: "49",
    name: "Davis County",
    position: {
      lat: 41.037045,
      lng: -112.202123
    },
    stateName: "Utah"
  },
  {
    id: "185",
    state: "47",
    name: "White County",
    position: {
      lat: 35.927062,
      lng: -85.455766
    },
    stateName: "Tennessee"
  },
  {
    id: "189",
    state: "47",
    name: "Wilson County",
    position: {
      lat: 36.148476,
      lng: -86.29021
    },
    stateName: "Tennessee"
  },
  {
    id: "009",
    state: "48",
    name: "Archer County",
    position: {
      lat: 33.616305,
      lng: -98.687267
    },
    stateName: "Texas"
  },
  {
    id: "011",
    state: "48",
    name: "Armstrong County",
    position: {
      lat: 34.964179,
      lng: -101.356636
    },
    stateName: "Texas"
  },
  {
    id: "021",
    state: "48",
    name: "Bastrop County",
    position: {
      lat: 30.103128,
      lng: -97.311859
    },
    stateName: "Texas"
  },
  {
    id: "031",
    state: "48",
    name: "Blanco County",
    position: {
      lat: 30.265605,
      lng: -98.399977
    },
    stateName: "Texas"
  },
  {
    id: "047",
    state: "48",
    name: "Brooks County",
    position: {
      lat: 27.034994,
      lng: -98.215276
    },
    stateName: "Texas"
  },
  {
    id: "053",
    state: "48",
    name: "Burnet County",
    position: {
      lat: 30.789616,
      lng: -98.201195
    },
    stateName: "Texas"
  },
  {
    id: "063",
    state: "48",
    name: "Camp County",
    position: {
      lat: 32.974581,
      lng: -94.979085
    },
    stateName: "Texas"
  },
  {
    id: "069",
    state: "48",
    name: "Castro County",
    position: {
      lat: 34.533621,
      lng: -102.258786
    },
    stateName: "Texas"
  },
  {
    id: "079",
    state: "48",
    name: "Cochran County",
    position: {
      lat: 33.60844,
      lng: -102.830449
    },
    stateName: "Texas"
  },
  {
    id: "095",
    state: "48",
    name: "Concho County",
    position: {
      lat: 31.318865,
      lng: -99.863648
    },
    stateName: "Texas"
  },
  {
    id: "099",
    state: "48",
    name: "Coryell County",
    position: {
      lat: 31.391177,
      lng: -97.798022
    },
    stateName: "Texas"
  },
  {
    id: "103",
    state: "48",
    name: "Crane County",
    position: {
      lat: 31.422797,
      lng: -102.487774
    },
    stateName: "Texas"
  },
  {
    id: "115",
    state: "48",
    name: "Dawson County",
    position: {
      lat: 32.741934,
      lng: -101.947322
    },
    stateName: "Texas"
  },
  {
    id: "123",
    state: "48",
    name: "De Witt County",
    position: {
      lat: 29.082342,
      lng: -97.361656
    },
    stateName: "Texas"
  },
  {
    id: "127",
    state: "48",
    name: "Dimmit County",
    position: {
      lat: 28.423587,
      lng: -99.765871
    },
    stateName: "Texas"
  },
  {
    id: "137",
    state: "48",
    name: "Edwards County",
    position: {
      lat: 29.985877,
      lng: -100.307373
    },
    stateName: "Texas"
  },
  {
    id: "149",
    state: "48",
    name: "Fayette County",
    position: {
      lat: 29.877886,
      lng: -96.921231
    },
    stateName: "Texas"
  },
  {
    id: "159",
    state: "48",
    name: "Franklin County",
    position: {
      lat: 33.175846,
      lng: -95.219066
    },
    stateName: "Texas"
  },
  {
    id: "169",
    state: "48",
    name: "Garza County",
    position: {
      lat: 33.183792,
      lng: -101.301134
    },
    stateName: "Texas"
  },
  {
    id: "177",
    state: "48",
    name: "Gonzales County",
    position: {
      lat: 29.468704,
      lng: -97.477738
    },
    stateName: "Texas"
  },
  {
    id: "179",
    state: "48",
    name: "Gray County",
    position: {
      lat: 35.402542,
      lng: -100.812374
    },
    stateName: "Texas"
  },
  {
    id: "185",
    state: "48",
    name: "Grimes County",
    position: {
      lat: 30.543231,
      lng: -95.988082
    },
    stateName: "Texas"
  },
  {
    id: "191",
    state: "48",
    name: "Hall County",
    position: {
      lat: 34.453189,
      lng: -100.576343
    },
    stateName: "Texas"
  },
  {
    id: "195",
    state: "48",
    name: "Hansford County",
    position: {
      lat: 36.272847,
      lng: -101.35693
    },
    stateName: "Texas"
  },
  {
    id: "217",
    state: "48",
    name: "Hill County",
    position: {
      lat: 31.98224,
      lng: -97.129886
    },
    stateName: "Texas"
  },
  {
    id: "227",
    state: "48",
    name: "Howard County",
    position: {
      lat: 32.303583,
      lng: -101.43853
    },
    stateName: "Texas"
  },
  {
    id: "233",
    state: "48",
    name: "Hutchinson County",
    position: {
      lat: 35.837047,
      lng: -101.362746
    },
    stateName: "Texas"
  },
  {
    id: "247",
    state: "48",
    name: "Jim Hogg County",
    position: {
      lat: 27.041212,
      lng: -98.700127
    },
    stateName: "Texas"
  },
  {
    id: "251",
    state: "48",
    name: "Johnson County",
    position: {
      lat: 32.379511,
      lng: -97.364823
    },
    stateName: "Texas"
  },
  {
    id: "263",
    state: "48",
    name: "Kent County",
    position: {
      lat: 33.18478,
      lng: -100.76972
    },
    stateName: "Texas"
  },
  {
    id: "265",
    state: "48",
    name: "Kerr County",
    position: {
      lat: 30.053928,
      lng: -99.351968
    },
    stateName: "Texas"
  },
  {
    id: "275",
    state: "48",
    name: "Knox County",
    position: {
      lat: 33.616657,
      lng: -99.747112
    },
    stateName: "Texas"
  },
  {
    id: "283",
    state: "48",
    name: "La Salle County",
    position: {
      lat: 28.351098,
      lng: -99.096774
    },
    stateName: "Texas"
  },
  {
    id: "297",
    state: "48",
    name: "Live Oak County",
    position: {
      lat: 28.351535,
      lng: -98.126961
    },
    stateName: "Texas"
  },
  {
    id: "303",
    state: "48",
    name: "Lubbock County",
    position: {
      lat: 33.611469,
      lng: -101.819944
    },
    stateName: "Texas"
  },
  {
    id: "309",
    state: "48",
    name: "Mc Lennan County",
    position: {
      lat: 31.549493,
      lng: -97.201472
    },
    stateName: "Texas"
  },
  {
    id: "319",
    state: "48",
    name: "Mason County",
    position: {
      lat: 30.703232,
      lng: -99.237608
    },
    stateName: "Texas"
  },
  {
    id: "329",
    state: "48",
    name: "Midland County",
    position: {
      lat: 31.870896,
      lng: -102.024326
    },
    stateName: "Texas"
  },
  {
    id: "339",
    state: "48",
    name: "Montgomery County",
    position: {
      lat: 30.302364,
      lng: -95.503523
    },
    stateName: "Texas"
  },
  {
    id: "349",
    state: "48",
    name: "Navarro County",
    position: {
      lat: 32.04845,
      lng: -96.476908
    },
    stateName: "Texas"
  },
  {
    id: "353",
    state: "48",
    name: "Nolan County",
    position: {
      lat: 32.312338,
      lng: -100.418108
    },
    stateName: "Texas"
  },
  {
    id: "121",
    state: "51",
    name: "Montgomery County",
    position: {
      lat: 37.174885,
      lng: -80.387314
    },
    stateName: "Virginia"
  },
  {
    id: "125",
    state: "51",
    name: "Nelson County",
    position: {
      lat: 37.790016,
      lng: -78.879394
    },
    stateName: "Virginia"
  },
  {
    id: "141",
    state: "51",
    name: "Patrick County",
    position: {
      lat: 36.667327,
      lng: -80.286141
    },
    stateName: "Virginia"
  },
  {
    id: "145",
    state: "51",
    name: "Powhatan County",
    position: {
      lat: 37.549404,
      lng: -77.912855
    },
    stateName: "Virginia"
  },
  {
    id: "155",
    state: "51",
    name: "Pulaski County",
    position: {
      lat: 37.063385,
      lng: -80.713444
    },
    stateName: "Virginia"
  },
  {
    id: "157",
    state: "51",
    name: "Rappahannock County",
    position: {
      lat: 38.684522,
      lng: -78.168824
    },
    stateName: "Virginia"
  },
  {
    id: "173",
    state: "51",
    name: "Smyth County",
    position: {
      lat: 36.842318,
      lng: -81.539786
    },
    stateName: "Virginia"
  },
  {
    id: "187",
    state: "51",
    name: "Warren County",
    position: {
      lat: 38.908187,
      lng: -78.207131
    },
    stateName: "Virginia"
  },
  {
    id: "197",
    state: "51",
    name: "Wythe County",
    position: {
      lat: 36.901471,
      lng: -81.084209
    },
    stateName: "Virginia"
  },
  {
    id: "510",
    state: "51",
    name: "Alexandria City County",
    postal: "city",
    position: {
      lat: 38.818343,
      lng: -77.082026
    },
    stateName: "Virginia"
  },
  {
    id: "530",
    state: "51",
    name: "Buena Vista City County",
    postal: "city",
    position: {
      lat: 37.731663,
      lng: -79.356375
    },
    stateName: "Virginia"
  },
  {
    id: "540",
    state: "51",
    name: "Charlottesville City County",
    postal: "city",
    position: {
      lat: 38.037658,
      lng: -78.485381
    },
    stateName: "Virginia"
  },
  {
    id: "595",
    state: "51",
    name: "Emporia City County",
    postal: "city",
    position: {
      lat: 36.696182,
      lng: -77.535975
    },
    stateName: "Virginia"
  },
  {
    id: "660",
    state: "51",
    name: "Harrisonburg City County",
    postal: "city",
    position: {
      lat: 38.436013,
      lng: -78.874197
    },
    stateName: "Virginia"
  },
  {
    id: "670",
    state: "51",
    name: "Hopewell City County",
    postal: "city",
    position: {
      lat: 37.29101,
      lng: -77.298944
    },
    stateName: "Virginia"
  },
  {
    id: "760",
    state: "51",
    name: "Richmond City County",
    postal: "city",
    position: {
      lat: 37.942894,
      lng: -76.730561
    },
    stateName: "Virginia"
  },
  {
    id: "775",
    state: "51",
    name: "Salem City County",
    postal: "city",
    position: {
      lat: 37.285333,
      lng: -80.055241
    },
    stateName: "Virginia"
  },
  {
    id: "820",
    state: "51",
    name: "Waynesboro City County",
    postal: "city",
    position: {
      lat: 38.067157,
      lng: -78.90142
    },
    stateName: "Virginia"
  },
  {
    id: "013",
    state: "53",
    name: "Columbia County",
    position: {
      lat: 46.292851,
      lng: -117.911635
    },
    stateName: "Washington"
  },
  {
    id: "021",
    state: "53",
    name: "Franklin County",
    position: {
      lat: 46.53458,
      lng: -118.906944
    },
    stateName: "Washington"
  },
  {
    id: "029",
    state: "55",
    name: "Door County",
    position: {
      lat: 45.067808,
      lng: -87.087936
    },
    stateName: "Wisconsin"
  },
  {
    id: "033",
    state: "55",
    name: "Dunn County",
    position: {
      lat: 44.947741,
      lng: -91.89772
    },
    stateName: "Wisconsin"
  },
  {
    id: "039",
    state: "55",
    name: "Fond Du Lac County",
    position: {
      lat: 43.754722,
      lng: -88.493284
    },
    stateName: "Wisconsin"
  },
  {
    id: "045",
    state: "55",
    name: "Green County",
    position: {
      lat: 42.677728,
      lng: -89.605639
    },
    stateName: "Wisconsin"
  },
  {
    id: "063",
    state: "55",
    name: "La Crosse County",
    position: {
      lat: 43.908222,
      lng: -91.111758
    },
    stateName: "Wisconsin"
  },
  {
    id: "069",
    state: "55",
    name: "Lincoln County",
    position: {
      lat: 45.338319,
      lng: -89.742082
    },
    stateName: "Wisconsin"
  },
  {
    id: "073",
    state: "55",
    name: "Marathon County",
    position: {
      lat: 44.898036,
      lng: -89.757823
    },
    stateName: "Wisconsin"
  },
  {
    id: "081",
    state: "55",
    name: "Monroe County",
    position: {
      lat: 43.945175,
      lng: -90.619969
    },
    stateName: "Wisconsin"
  },
  {
    id: "095",
    state: "55",
    name: "Polk County",
    position: {
      lat: 45.46803,
      lng: -92.453154
    },
    stateName: "Wisconsin"
  },
  {
    id: "103",
    state: "55",
    name: "Richland County",
    position: {
      lat: 43.376199,
      lng: -90.435693
    },
    stateName: "Wisconsin"
  },
  {
    id: "115",
    state: "55",
    name: "Shawano County",
    position: {
      lat: 44.789641,
      lng: -88.755813
    },
    stateName: "Wisconsin"
  },
  {
    id: "129",
    state: "55",
    name: "Washburn County",
    position: {
      lat: 45.892463,
      lng: -91.796423
    },
    stateName: "Wisconsin"
  },
  {
    id: "141",
    state: "55",
    name: "Wood County",
    position: {
      lat: 44.461413,
      lng: -90.038825
    },
    stateName: "Wisconsin"
  },
  {
    id: "025",
    state: "56",
    name: "Natrona County",
    position: {
      lat: 42.973641,
      lng: -106.764877
    },
    stateName: "Wyoming"
  },
  {
    id: "031",
    state: "56",
    name: "Platte County",
    position: {
      lat: 42.130319,
      lng: -104.960809
    },
    stateName: "Wyoming"
  },
  {
    id: "033",
    state: "56",
    name: "Sheridan County",
    position: {
      lat: 44.781369,
      lng: -106.881211
    },
    stateName: "Wyoming"
  },
  {
    id: "045",
    state: "56",
    name: "Weston County",
    position: {
      lat: 43.846213,
      lng: -104.57002
    },
    stateName: "Wyoming"
  },
  {
    id: "041",
    state: "53",
    name: "Lewis County",
    position: {
      lat: 46.580071,
      lng: -122.377444
    },
    stateName: "Washington"
  },
  {
    id: "045",
    state: "53",
    name: "Mason County",
    position: {
      lat: 47.354126,
      lng: -123.17385
    },
    stateName: "Washington"
  },
  {
    id: "065",
    state: "53",
    name: "Stevens County",
    position: {
      lat: 48.390648,
      lng: -117.854897
    },
    stateName: "Washington"
  },
  {
    id: "001",
    state: "54",
    name: "Barbour County",
    position: {
      lat: 39.139754,
      lng: -79.996914
    },
    stateName: "West Virginia"
  },
  {
    id: "007",
    state: "54",
    name: "Braxton County",
    position: {
      lat: 38.699302,
      lng: -80.731649
    },
    stateName: "West Virginia"
  },
  {
    id: "013",
    state: "54",
    name: "Calhoun County",
    position: {
      lat: 38.844159,
      lng: -81.115478
    },
    stateName: "West Virginia"
  },
  {
    id: "021",
    state: "54",
    name: "Gilmer County",
    position: {
      lat: 38.915865,
      lng: -80.849409
    },
    stateName: "West Virginia"
  },
  {
    id: "039",
    state: "54",
    name: "Kanawha County",
    position: {
      lat: 38.328061,
      lng: -81.523522
    },
    stateName: "West Virginia"
  },
  {
    id: "049",
    state: "54",
    name: "Marion County",
    position: {
      lat: 39.505839,
      lng: -80.243402
    },
    stateName: "West Virginia"
  },
  {
    id: "061",
    state: "54",
    name: "Monongalia County",
    position: {
      lat: 39.633645,
      lng: -80.059074
    },
    stateName: "West Virginia"
  },
  {
    id: "079",
    state: "54",
    name: "Putnam County",
    position: {
      lat: 38.510513,
      lng: -81.906109
    },
    stateName: "West Virginia"
  },
  {
    id: "089",
    state: "54",
    name: "Summers County",
    position: {
      lat: 37.656003,
      lng: -80.856315
    },
    stateName: "West Virginia"
  },
  {
    id: "101",
    state: "54",
    name: "Webster County",
    position: {
      lat: 38.492985,
      lng: -80.430262
    },
    stateName: "West Virginia"
  },
  {
    id: "003",
    state: "55",
    name: "Ashland County",
    position: {
      lat: 46.546291,
      lng: -90.665154
    },
    stateName: "Wisconsin"
  },
  {
    id: "005",
    state: "55",
    name: "Barron County",
    position: {
      lat: 45.437192,
      lng: -91.852892
    },
    stateName: "Wisconsin"
  },
  {
    id: "007",
    state: "55",
    name: "Bayfield County",
    position: {
      lat: 46.634199,
      lng: -91.177282
    },
    stateName: "Wisconsin"
  },
  {
    id: "017",
    state: "55",
    name: "Chippewa County",
    position: {
      lat: 45.069092,
      lng: -91.283505
    },
    stateName: "Wisconsin"
  },
  {
    id: "025",
    state: "55",
    name: "Dane County",
    position: {
      lat: 43.067468,
      lng: -89.417852
    },
    stateName: "Wisconsin"
  },
  {
    id: "029",
    state: "49",
    name: "Morgan County",
    position: {
      lat: 41.091027,
      lng: -111.577885
    },
    stateName: "Utah"
  },
  {
    id: "031",
    state: "49",
    name: "Piute County",
    position: {
      lat: 38.335881,
      lng: -112.129376
    },
    stateName: "Utah"
  },
  {
    id: "035",
    state: "49",
    name: "Salt Lake County",
    position: {
      lat: 40.667882,
      lng: -111.924244
    },
    stateName: "Utah"
  },
  {
    id: "051",
    state: "49",
    name: "Wasatch County",
    position: {
      lat: 40.334884,
      lng: -111.161568
    },
    stateName: "Utah"
  },
  {
    id: "005",
    state: "50",
    name: "Caledonia County",
    position: {
      lat: 44.468791,
      lng: -72.112168
    },
    stateName: "Vermont"
  },
  {
    id: "019",
    state: "50",
    name: "Orleans County",
    position: {
      lat: 44.828442,
      lng: -72.25163
    },
    stateName: "Vermont"
  },
  {
    id: "007",
    state: "51",
    name: "Amelia County",
    position: {
      lat: 37.336131,
      lng: -77.973218
    },
    stateName: "Virginia"
  },
  {
    id: "017",
    state: "51",
    name: "Bath County",
    position: {
      lat: 38.068988,
      lng: -79.732898
    },
    stateName: "Virginia"
  },
  {
    id: "029",
    state: "51",
    name: "Buckingham County",
    position: {
      lat: 37.573928,
      lng: -78.529169
    },
    stateName: "Virginia"
  },
  {
    id: "043",
    state: "51",
    name: "Clarke County",
    position: {
      lat: 39.115931,
      lng: -77.992004
    },
    stateName: "Virginia"
  },
  {
    id: "047",
    state: "51",
    name: "Culpeper County",
    position: {
      lat: 38.48593,
      lng: -77.956476
    },
    stateName: "Virginia"
  },
  {
    id: "063",
    state: "51",
    name: "Floyd County",
    position: {
      lat: 36.931438,
      lng: -80.350309
    },
    stateName: "Virginia"
  },
  {
    id: "075",
    state: "51",
    name: "Goochland County",
    position: {
      lat: 37.724166,
      lng: -77.914273
    },
    stateName: "Virginia"
  },
  {
    id: "101",
    state: "51",
    name: "King William County",
    position: {
      lat: 37.70826,
      lng: -77.091054
    },
    stateName: "Virginia"
  },
  {
    id: "111",
    state: "51",
    name: "Lunenburg County",
    position: {
      lat: 36.945757,
      lng: -78.242313
    },
    stateName: "Virginia"
  },
  {
    id: "113",
    state: "51",
    name: "Madison County",
    position: {
      lat: 38.412059,
      lng: -78.276961
    },
    stateName: "Virginia"
  },
  {
    id: "023",
    state: "01",
    name: "Choctaw County",
    position: {
      lat: 31.991008,
      lng: -88.248887
    },
    stateName: "Alabama"
  },
  {
    id: "025",
    state: "01",
    name: "Clarke County",
    position: {
      lat: 31.685521,
      lng: -87.818624
    },
    stateName: "Alabama"
  },
  {
    id: "029",
    state: "01",
    name: "Cleburne County",
    position: {
      lat: 33.671981,
      lng: -85.516109
    },
    stateName: "Alabama"
  },
  {
    id: "031",
    state: "01",
    name: "Coffee County",
    position: {
      lat: 31.402183,
      lng: -85.989201
    },
    stateName: "Alabama"
  },
  {
    id: "037",
    state: "01",
    name: "Coosa County",
    position: {
      lat: 32.931445,
      lng: -86.243482
    },
    stateName: "Alabama"
  },
  {
    id: "047",
    state: "01",
    name: "Dallas County",
    position: {
      lat: 32.33354,
      lng: -87.114356
    },
    stateName: "Alabama"
  },
  {
    id: "055",
    state: "01",
    name: "Etowah County",
    position: {
      lat: 34.047638,
      lng: -86.03442
    },
    stateName: "Alabama"
  },
  {
    id: "059",
    state: "01",
    name: "Franklin County",
    position: {
      lat: 34.441988,
      lng: -87.842815
    },
    stateName: "Alabama"
  },
  {
    id: "061",
    state: "01",
    name: "Geneva County",
    position: {
      lat: 31.090866,
      lng: -85.824346
    },
    stateName: "Alabama"
  },
  {
    id: "067",
    state: "01",
    name: "Henry County",
    position: {
      lat: 31.516978,
      lng: -85.239971
    },
    stateName: "Alabama"
  },
  {
    id: "071",
    state: "01",
    name: "Jackson County",
    position: {
      lat: 34.763522,
      lng: -85.9774
    },
    stateName: "Alabama"
  },
  {
    id: "081",
    state: "01",
    name: "Lee County",
    position: {
      lat: 32.604064,
      lng: -85.353048
    },
    stateName: "Alabama"
  },
  {
    id: "085",
    state: "01",
    name: "Lowndes County",
    position: {
      lat: 32.147888,
      lng: -86.650586
    },
    stateName: "Alabama"
  },
  {
    id: "087",
    state: "01",
    name: "Macon County",
    position: {
      lat: 32.387027,
      lng: -85.692887
    },
    stateName: "Alabama"
  },
  {
    id: "089",
    state: "01",
    name: "Madison County",
    position: {
      lat: 34.764238,
      lng: -86.55108
    },
    stateName: "Alabama"
  },
  {
    id: "143",
    state: "05",
    name: "Washington County",
    position: {
      lat: 35.971209,
      lng: -94.218417
    },
    stateName: "Arkansas"
  },
  {
    id: "147",
    state: "05",
    name: "Woodruff County",
    position: {
      lat: 35.189071,
      lng: -91.244418
    },
    stateName: "Arkansas"
  },
  {
    id: "003",
    state: "06",
    name: "Alpine County",
    position: {
      lat: 38.61761,
      lng: -119.798999
    },
    stateName: "California"
  },
  {
    id: "007",
    state: "06",
    name: "Butte County",
    position: {
      lat: 39.665959,
      lng: -121.601919
    },
    stateName: "California"
  },
  {
    id: "093",
    state: "01",
    name: "Marion County",
    position: {
      lat: 34.138219,
      lng: -87.881551
    },
    stateName: "Alabama"
  },
  {
    id: "095",
    state: "01",
    name: "Marshall County",
    position: {
      lat: 34.309564,
      lng: -86.321668
    },
    stateName: "Alabama"
  },
  {
    id: "097",
    state: "01",
    name: "Mobile County",
    position: {
      lat: 30.684573,
      lng: -88.196568
    },
    stateName: "Alabama"
  },
  {
    id: "101",
    state: "01",
    name: "Montgomery County",
    position: {
      lat: 32.203651,
      lng: -86.203831
    },
    stateName: "Alabama"
  },
  {
    id: "117",
    state: "01",
    name: "Shelby County",
    position: {
      lat: 33.262937,
      lng: -86.678104
    },
    stateName: "Alabama"
  },
  {
    id: "119",
    state: "01",
    name: "Sumter County",
    position: {
      lat: 32.597481,
      lng: -88.200057
    },
    stateName: "Alabama"
  },
  {
    id: "125",
    state: "01",
    name: "Tuscaloosa County",
    position: {
      lat: 33.290202,
      lng: -87.52286
    },
    stateName: "Alabama"
  },
  {
    id: "131",
    state: "01",
    name: "Wilcox County",
    position: {
      lat: 31.99033,
      lng: -87.302205
    },
    stateName: "Alabama"
  },
  {
    id: "007",
    state: "01",
    name: "Bibb County",
    position: {
      lat: 33.015893,
      lng: -87.127148
    },
    stateName: "Alabama"
  },
  {
    id: "011",
    state: "01",
    name: "Bullock County",
    position: {
      lat: 32.101759,
      lng: -85.717261
    },
    stateName: "Alabama"
  },
  {
    id: "015",
    state: "01",
    name: "Calhoun County",
    position: {
      lat: 33.771706,
      lng: -85.822513
    },
    stateName: "Alabama"
  },
  {
    id: "045",
    state: "06",
    name: "Mendocino County",
    position: {
      lat: 39.432388,
      lng: -123.442881
    },
    stateName: "California"
  },
  {
    id: "049",
    state: "06",
    name: "Modoc County",
    position: {
      lat: 41.592919,
      lng: -120.71837
    },
    stateName: "California"
  },
  {
    id: "051",
    state: "06",
    name: "Mono County",
    position: {
      lat: 37.915836,
      lng: -118.875167
    },
    stateName: "California"
  },
  {
    id: "057",
    state: "06",
    name: "Nevada County",
    position: {
      lat: 39.295191,
      lng: -120.773446
    },
    stateName: "California"
  },
  {
    id: "059",
    state: "06",
    name: "Orange County",
    position: {
      lat: 33.675687,
      lng: -117.777207
    },
    stateName: "California"
  },
  {
    id: "065",
    state: "06",
    name: "Riverside County",
    position: {
      lat: 33.729828,
      lng: -116.002239
    },
    stateName: "California"
  },
  {
    id: "069",
    state: "06",
    name: "San Benito County",
    position: {
      lat: 36.610702,
      lng: -121.085296
    },
    stateName: "California"
  },
  {
    id: "073",
    state: "06",
    name: "San Diego County",
    position: {
      lat: 33.023604,
      lng: -116.776117
    },
    stateName: "California"
  },
  {
    id: "075",
    state: "06",
    name: "San Francisco County",
    position: {
      lat: 37.727239,
      lng: -123.032229
    },
    stateName: "California"
  },
  {
    id: "113",
    state: "06",
    name: "Yolo County",
    position: {
      lat: 38.679268,
      lng: -121.903178
    },
    stateName: "California"
  },
  {
    id: "001",
    state: "08",
    name: "Adams County",
    position: {
      lat: 39.874325,
      lng: -104.331872
    },
    stateName: "Colorado"
  },
  {
    id: "007",
    state: "08",
    name: "Archuleta County",
    position: {
      lat: 37.202395,
      lng: -107.050863
    },
    stateName: "Colorado"
  },
  {
    id: "009",
    state: "08",
    name: "Baca County",
    position: {
      lat: 37.303144,
      lng: -102.535457
    },
    stateName: "Colorado"
  },
  {
    id: "015",
    state: "08",
    name: "Chaffee County",
    position: {
      lat: 38.738246,
      lng: -106.316972
    },
    stateName: "Colorado"
  },
  {
    id: "017",
    state: "08",
    name: "Cheyenne County",
    position: {
      lat: 38.835387,
      lng: -102.604585
    },
    stateName: "Colorado"
  },
  {
    id: "021",
    state: "08",
    name: "Conejos County",
    position: {
      lat: 37.213407,
      lng: -106.176447
    },
    stateName: "Colorado"
  },
  {
    id: "023",
    state: "08",
    name: "Costilla County",
    position: {
      lat: 37.277547,
      lng: -105.42894
    },
    stateName: "Colorado"
  },
  {
    id: "029",
    state: "08",
    name: "Delta County",
    position: {
      lat: 38.861756,
      lng: -107.864757
    },
    stateName: "Colorado"
  },
  {
    id: "037",
    state: "08",
    name: "Eagle County",
    position: {
      lat: 39.630638,
      lng: -106.692944
    },
    stateName: "Colorado"
  },
  {
    id: "043",
    state: "08",
    name: "Fremont County",
    position: {
      lat: 38.455658,
      lng: -105.421438
    },
    stateName: "Colorado"
  },
  {
    id: "081",
    state: "06",
    name: "San Mateo County",
    position: {
      lat: 37.414664,
      lng: -122.371542
    },
    stateName: "California"
  },
  {
    id: "083",
    state: "06",
    name: "Santa Barbara County",
    position: {
      lat: 34.537378,
      lng: -120.038485
    },
    stateName: "California"
  },
  {
    id: "087",
    state: "06",
    name: "Santa Cruz County",
    position: {
      lat: 37.012488,
      lng: -122.007205
    },
    stateName: "California"
  },
  {
    id: "095",
    state: "06",
    name: "Solano County",
    position: {
      lat: 38.267226,
      lng: -121.939594
    },
    stateName: "California"
  },
  {
    id: "101",
    state: "06",
    name: "Sutter County",
    position: {
      lat: 39.035257,
      lng: -121.702758
    },
    stateName: "California"
  },
  {
    id: "103",
    state: "06",
    name: "Tehama County",
    position: {
      lat: 40.126156,
      lng: -122.232276
    },
    stateName: "California"
  },
  {
    id: "109",
    state: "06",
    name: "Tuolumne County",
    position: {
      lat: 38.021451,
      lng: -119.964708
    },
    stateName: "California"
  },
  {
    id: "009",
    state: "06",
    name: "Calaveras County",
    position: {
      lat: 38.187844,
      lng: -120.555115
    },
    stateName: "California"
  },
  {
    id: "013",
    state: "06",
    name: "Contra Costa County",
    position: {
      lat: 37.919479,
      lng: -121.951543
    },
    stateName: "California"
  },
  {
    id: "015",
    state: "06",
    name: "Del Norte County",
    position: {
      lat: 41.749903,
      lng: -123.980998
    },
    stateName: "California"
  },
  {
    id: "149",
    state: "13",
    name: "Heard County",
    position: {
      lat: 33.290968,
      lng: -85.139594
    },
    stateName: "Georgia"
  },
  {
    id: "059",
    state: "20",
    name: "Franklin County",
    position: {
      lat: 38.558019,
      lng: -95.278962
    },
    stateName: "Kansas"
  },
  {
    id: "025",
    state: "06",
    name: "Imperial County",
    position: {
      lat: 33.040816,
      lng: -115.355395
    },
    stateName: "California"
  },
  {
    id: "029",
    state: "06",
    name: "Kern County",
    position: {
      lat: 35.346629,
      lng: -118.729506
    },
    stateName: "California"
  },
  {
    id: "031",
    state: "06",
    name: "Kings County",
    position: {
      lat: 36.072478,
      lng: -119.81553
    },
    stateName: "California"
  },
  {
    id: "039",
    state: "06",
    name: "Madera County",
    position: {
      lat: 37.210039,
      lng: -119.749852
    },
    stateName: "California"
  },
  {
    id: "067",
    state: "13",
    name: "Cobb County",
    position: {
      lat: 33.93994,
      lng: -84.574166
    },
    stateName: "Georgia"
  },
  {
    id: "069",
    state: "13",
    name: "Coffee County",
    position: {
      lat: 31.549245,
      lng: -82.844938
    },
    stateName: "Georgia"
  },
  {
    id: "073",
    state: "13",
    name: "Columbia County",
    position: {
      lat: 33.550556,
      lng: -82.251342
    },
    stateName: "Georgia"
  },
  {
    id: "077",
    state: "13",
    name: "Coweta County",
    position: {
      lat: 33.352897,
      lng: -84.762138
    },
    stateName: "Georgia"
  },
  {
    id: "081",
    state: "13",
    name: "Crisp County",
    position: {
      lat: 31.914753,
      lng: -83.753338
    },
    stateName: "Georgia"
  },
  {
    id: "087",
    state: "13",
    name: "Decatur County",
    position: {
      lat: 30.877961,
      lng: -84.577744
    },
    stateName: "Georgia"
  },
  {
    id: "093",
    state: "13",
    name: "Dooly County",
    position: {
      lat: 32.151995,
      lng: -83.807167
    },
    stateName: "Georgia"
  },
  {
    id: "095",
    state: "13",
    name: "Dougherty County",
    position: {
      lat: 31.535068,
      lng: -84.214444
    },
    stateName: "Georgia"
  },
  {
    id: "101",
    state: "13",
    name: "Echols County",
    position: {
      lat: 30.7085,
      lng: -82.836132
    },
    stateName: "Georgia"
  },
  {
    id: "103",
    state: "13",
    name: "Effingham County",
    position: {
      lat: 32.361711,
      lng: -81.343337
    },
    stateName: "Georgia"
  },
  {
    id: "109",
    state: "13",
    name: "Evans County",
    position: {
      lat: 32.151902,
      lng: -81.887618
    },
    stateName: "Georgia"
  },
  {
    id: "115",
    state: "13",
    name: "Floyd County",
    position: {
      lat: 34.263677,
      lng: -85.21373
    },
    stateName: "Georgia"
  },
  {
    id: "119",
    state: "13",
    name: "Franklin County",
    position: {
      lat: 34.375155,
      lng: -83.227291
    },
    stateName: "Georgia"
  },
  {
    id: "061",
    state: "08",
    name: "Kiowa County",
    position: {
      lat: 38.388466,
      lng: -102.75621
    },
    stateName: "Colorado"
  },
  {
    id: "063",
    state: "08",
    name: "Kit Carson County",
    position: {
      lat: 39.30534,
      lng: -102.603023
    },
    stateName: "Colorado"
  },
  {
    id: "069",
    state: "08",
    name: "Larimer County",
    position: {
      lat: 40.663091,
      lng: -105.482131
    },
    stateName: "Colorado"
  },
  {
    id: "089",
    state: "12",
    name: "Nassau County",
    position: {
      lat: 30.605926,
      lng: -81.764929
    },
    stateName: "Florida"
  },
  {
    id: "091",
    state: "12",
    name: "Okaloosa County",
    position: {
      lat: 30.665858,
      lng: -86.594194
    },
    stateName: "Florida"
  },
  {
    id: "097",
    state: "12",
    name: "Osceola County",
    position: {
      lat: 28.059027,
      lng: -81.139312
    },
    stateName: "Florida"
  },
  {
    id: "099",
    state: "12",
    name: "Palm Beach County",
    position: {
      lat: 26.645763,
      lng: -80.448673
    },
    stateName: "Florida"
  },
  {
    id: "101",
    state: "12",
    name: "Pasco County",
    position: {
      lat: 28.302024,
      lng: -82.455707
    },
    stateName: "Florida"
  },
  {
    id: "107",
    state: "12",
    name: "Putnam County",
    position: {
      lat: 29.606006,
      lng: -81.740894
    },
    stateName: "Florida"
  },
  {
    id: "109",
    state: "12",
    name: "St. Johns County",
    position: {
      lat: 29.890593,
      lng: -81.383914
    },
    stateName: "Florida"
  },
  {
    id: "113",
    state: "12",
    name: "Santa Rosa County",
    position: {
      lat: 30.703633,
      lng: -87.014255
    },
    stateName: "Florida"
  },
  {
    id: "115",
    state: "12",
    name: "Sarasota County",
    position: {
      lat: 27.184386,
      lng: -82.365835
    },
    stateName: "Florida"
  },
  {
    id: "117",
    state: "12",
    name: "Seminole County",
    position: {
      lat: 28.690079,
      lng: -81.13198
    },
    stateName: "Florida"
  },
  {
    id: "127",
    state: "12",
    name: "Volusia County",
    position: {
      lat: 29.057617,
      lng: -81.161813
    },
    stateName: "Florida"
  },
  {
    id: "131",
    state: "12",
    name: "Walton County",
    position: {
      lat: 30.631211,
      lng: -86.176614
    },
    stateName: "Florida"
  },
  {
    id: "003",
    state: "13",
    name: "Atkinson County",
    position: {
      lat: 31.300562,
      lng: -82.883614
    },
    stateName: "Georgia"
  },
  {
    id: "005",
    state: "13",
    name: "Bacon County",
    position: {
      lat: 31.563327,
      lng: -82.387859
    },
    stateName: "Georgia"
  },
  {
    id: "009",
    state: "13",
    name: "Baldwin County",
    position: {
      lat: 33.059532,
      lng: -83.255362
    },
    stateName: "Georgia"
  },
  {
    id: "015",
    state: "13",
    name: "Bartow County",
    position: {
      lat: 34.240918,
      lng: -84.838188
    },
    stateName: "Georgia"
  },
  {
    id: "023",
    state: "13",
    name: "Bleckley County",
    position: {
      lat: 32.435403,
      lng: -83.331717
    },
    stateName: "Georgia"
  },
  {
    id: "025",
    state: "13",
    name: "Brantley County",
    position: {
      lat: 31.197334,
      lng: -81.982978
    },
    stateName: "Georgia"
  },
  {
    id: "031",
    state: "13",
    name: "Bulloch County",
    position: {
      lat: 32.393408,
      lng: -81.74381
    },
    stateName: "Georgia"
  },
  {
    id: "037",
    state: "13",
    name: "Calhoun County",
    position: {
      lat: 31.521279,
      lng: -84.62629
    },
    stateName: "Georgia"
  },
  {
    id: "049",
    state: "13",
    name: "Charlton County",
    position: {
      lat: 30.779904,
      lng: -82.139644
    },
    stateName: "Georgia"
  },
  {
    id: "053",
    state: "13",
    name: "Chattahoochee County",
    position: {
      lat: 32.347445,
      lng: -84.788021
    },
    stateName: "Georgia"
  },
  {
    id: "057",
    state: "13",
    name: "Cherokee County",
    position: {
      lat: 34.244317,
      lng: -84.475057
    },
    stateName: "Georgia"
  },
  {
    id: "063",
    state: "13",
    name: "Clayton County",
    position: {
      lat: 33.552242,
      lng: -84.412977
    },
    stateName: "Georgia"
  },
  {
    id: "013",
    state: "09",
    name: "Tolland County",
    position: {
      lat: 41.858076,
      lng: -72.340977
    },
    stateName: "Connecticut"
  },
  {
    id: "005",
    state: "10",
    name: "Sussex County",
    position: {
      lat: 38.677511,
      lng: -75.335495
    },
    stateName: "Delaware"
  },
  {
    id: "001",
    state: "11",
    name: "Washington County",
    postal: "",
    position: {
      lat: 38.904149,
      lng: -77.017094
    },
    stateName: "District Of Columbia"
  },
  {
    id: "003",
    state: "12",
    name: "Baker County",
    position: {
      lat: 30.324442,
      lng: -82.302284
    },
    stateName: "Florida"
  },
  {
    id: "005",
    state: "12",
    name: "Bay County",
    position: {
      lat: 30.237563,
      lng: -85.631348
    },
    stateName: "Florida"
  },
  {
    id: "011",
    state: "12",
    name: "Broward County",
    position: {
      lat: 26.19352,
      lng: -80.476658
    },
    stateName: "Florida"
  },
  {
    id: "015",
    state: "12",
    name: "Charlotte County",
    position: {
      lat: 26.868826,
      lng: -81.940858
    },
    stateName: "Florida"
  },
  {
    id: "071",
    state: "12",
    name: "Lee County",
    position: {
      lat: 26.552134,
      lng: -81.89225
    },
    stateName: "Florida"
  },
  {
    id: "079",
    state: "12",
    name: "Madison County",
    position: {
      lat: 30.447228,
      lng: -83.470437
    },
    stateName: "Florida"
  },
  {
    id: "081",
    state: "12",
    name: "Manatee County",
    position: {
      lat: 27.481386,
      lng: -82.365784
    },
    stateName: "Florida"
  },
  {
    id: "085",
    state: "08",
    name: "Montrose County",
    position: {
      lat: 38.413427,
      lng: -108.263042
    },
    stateName: "Colorado"
  },
  {
    id: "017",
    state: "12",
    name: "Citrus County",
    position: {
      lat: 28.843628,
      lng: -82.524796
    },
    stateName: "Florida"
  },
  {
    id: "029",
    state: "12",
    name: "Dixie County",
    position: {
      lat: 29.580899,
      lng: -83.195666
    },
    stateName: "Florida"
  },
  {
    id: "031",
    state: "12",
    name: "Duval County",
    position: {
      lat: 30.335245,
      lng: -81.648113
    },
    stateName: "Florida"
  },
  {
    id: "041",
    state: "12",
    name: "Gilchrist County",
    position: {
      lat: 29.723456,
      lng: -82.795801
    },
    stateName: "Florida"
  },
  {
    id: "043",
    state: "12",
    name: "Glades County",
    position: {
      lat: 26.95481,
      lng: -81.19082
    },
    stateName: "Florida"
  },
  {
    id: "045",
    state: "12",
    name: "Gulf County",
    position: {
      lat: 29.907257,
      lng: -85.256537
    },
    stateName: "Florida"
  },
  {
    id: "049",
    state: "12",
    name: "Hardee County",
    position: {
      lat: 27.492846,
      lng: -81.82158
    },
    stateName: "Florida"
  },
  {
    id: "051",
    state: "12",
    name: "Hendry County",
    position: {
      lat: 26.53934,
      lng: -81.151584
    },
    stateName: "Florida"
  },
  {
    id: "053",
    state: "12",
    name: "Hernando County",
    position: {
      lat: 28.567911,
      lng: -82.464835
    },
    stateName: "Florida"
  },
  {
    id: "061",
    state: "12",
    name: "Indian River County",
    position: {
      lat: 27.700638,
      lng: -80.574803
    },
    stateName: "Florida"
  },
  {
    id: "063",
    state: "12",
    name: "Jackson County",
    position: {
      lat: 30.787812,
      lng: -85.210374
    },
    stateName: "Florida"
  },
  {
    id: "069",
    state: "12",
    name: "Lake County",
    position: {
      lat: 28.764113,
      lng: -81.712282
    },
    stateName: "Florida"
  },
  {
    id: "075",
    state: "08",
    name: "Logan County",
    position: {
      lat: 40.728091,
      lng: -103.090464
    },
    stateName: "Colorado"
  },
  {
    id: "077",
    state: "08",
    name: "Mesa County",
    position: {
      lat: 39.019492,
      lng: -108.461837
    },
    stateName: "Colorado"
  },
  {
    id: "081",
    state: "08",
    name: "Moffat County",
    position: {
      lat: 40.573984,
      lng: -108.204521
    },
    stateName: "Colorado"
  },
  {
    id: "087",
    state: "08",
    name: "Morgan County",
    position: {
      lat: 40.262354,
      lng: -103.807092
    },
    stateName: "Colorado"
  },
  {
    id: "091",
    state: "08",
    name: "Ouray County",
    position: {
      lat: 38.1506,
      lng: -107.767133
    },
    stateName: "Colorado"
  },
  {
    id: "097",
    state: "08",
    name: "Pitkin County",
    position: {
      lat: 39.217533,
      lng: -106.915943
    },
    stateName: "Colorado"
  },
  {
    id: "103",
    state: "08",
    name: "Rio Blanco County",
    position: {
      lat: 39.972606,
      lng: -108.200685
    },
    stateName: "Colorado"
  },
  {
    id: "109",
    state: "08",
    name: "Saguache County",
    position: {
      lat: 38.033952,
      lng: -106.246675
    },
    stateName: "Colorado"
  },
  {
    id: "119",
    state: "08",
    name: "Teller County",
    position: {
      lat: 38.871994,
      lng: -105.182552
    },
    stateName: "Colorado"
  },
  {
    id: "121",
    state: "08",
    name: "Washington County",
    position: {
      lat: 39.965413,
      lng: -103.209605
    },
    stateName: "Colorado"
  },
  {
    id: "125",
    state: "08",
    name: "Yuma County",
    position: {
      lat: 40.000631,
      lng: -102.422649
    },
    stateName: "Colorado"
  },
  {
    id: "003",
    state: "09",
    name: "Hartford County",
    position: {
      lat: 41.806053,
      lng: -72.732916
    },
    stateName: "Connecticut"
  },
  {
    id: "007",
    state: "09",
    name: "Middlesex County",
    position: {
      lat: 41.434525,
      lng: -72.524227
    },
    stateName: "Connecticut"
  },
  {
    id: "015",
    state: "05",
    name: "Carroll County",
    position: {
      lat: 36.337774,
      lng: -93.541663
    },
    stateName: "Arkansas"
  },
  {
    id: "017",
    state: "05",
    name: "Chicot County",
    position: {
      lat: 33.267139,
      lng: -91.297158
    },
    stateName: "Arkansas"
  },
  {
    id: "025",
    state: "05",
    name: "Cleveland County",
    position: {
      lat: 33.893201,
      lng: -92.188714
    },
    stateName: "Arkansas"
  },
  {
    id: "027",
    state: "05",
    name: "Columbia County",
    position: {
      lat: 33.21507,
      lng: -93.226901
    },
    stateName: "Arkansas"
  },
  {
    id: "033",
    state: "05",
    name: "Crawford County",
    position: {
      lat: 35.583041,
      lng: -94.236224
    },
    stateName: "Arkansas"
  },
  {
    id: "045",
    state: "05",
    name: "Faulkner County",
    position: {
      lat: 35.146356,
      lng: -92.324654
    },
    stateName: "Arkansas"
  },
  {
    id: "047",
    state: "05",
    name: "Franklin County",
    position: {
      lat: 35.508573,
      lng: -93.887672
    },
    stateName: "Arkansas"
  },
  {
    id: "053",
    state: "05",
    name: "Grant County",
    position: {
      lat: 34.288063,
      lng: -92.423984
    },
    stateName: "Arkansas"
  },
  {
    id: "059",
    state: "05",
    name: "Hot Spring County",
    position: {
      lat: 34.315177,
      lng: -92.944147
    },
    stateName: "Arkansas"
  },
  {
    id: "067",
    state: "05",
    name: "Jackson County",
    position: {
      lat: 35.596605,
      lng: -91.223178
    },
    stateName: "Arkansas"
  },
  {
    id: "073",
    state: "05",
    name: "Lafayette County",
    position: {
      lat: 33.24066,
      lng: -93.611643
    },
    stateName: "Arkansas"
  },
  {
    id: "081",
    state: "05",
    name: "Little River County",
    position: {
      lat: 33.699497,
      lng: -94.229774
    },
    stateName: "Arkansas"
  },
  {
    id: "083",
    state: "05",
    name: "Logan County",
    position: {
      lat: 35.21855,
      lng: -93.720563
    },
    stateName: "Arkansas"
  },
  {
    id: "087",
    state: "05",
    name: "Madison County",
    position: {
      lat: 36.012545,
      lng: -93.724053
    },
    stateName: "Arkansas"
  },
  {
    id: "001",
    state: "16",
    name: "Ada County",
    position: {
      lat: 43.447861,
      lng: -116.244456
    },
    stateName: "Idaho"
  },
  {
    id: "003",
    state: "16",
    name: "Adams County",
    position: {
      lat: 44.884583,
      lng: -116.431873
    },
    stateName: "Idaho"
  },
  {
    id: "009",
    state: "16",
    name: "Benewah County",
    position: {
      lat: 47.218451,
      lng: -116.633541
    },
    stateName: "Idaho"
  },
  {
    id: "017",
    state: "16",
    name: "Bonner County",
    position: {
      lat: 48.312512,
      lng: -116.59694
    },
    stateName: "Idaho"
  },
  {
    id: "019",
    state: "16",
    name: "Bonneville County",
    position: {
      lat: 43.395171,
      lng: -111.621878
    },
    stateName: "Idaho"
  },
  {
    id: "089",
    state: "05",
    name: "Marion County",
    position: {
      lat: 36.266656,
      lng: -92.678588
    },
    stateName: "Arkansas"
  },
  {
    id: "091",
    state: "05",
    name: "Miller County",
    position: {
      lat: 33.305505,
      lng: -93.901509
    },
    stateName: "Arkansas"
  },
  {
    id: "093",
    state: "05",
    name: "Mississippi County",
    position: {
      lat: 35.766943,
      lng: -90.052209
    },
    stateName: "Arkansas"
  },
  {
    id: "099",
    state: "05",
    name: "Nevada County",
    position: {
      lat: 33.666699,
      lng: -93.305072
    },
    stateName: "Arkansas"
  },
  {
    id: "103",
    state: "05",
    name: "Ouachita County",
    position: {
      lat: 33.591158,
      lng: -92.878418
    },
    stateName: "Arkansas"
  },
  {
    id: "113",
    state: "05",
    name: "Polk County",
    position: {
      lat: 34.490915,
      lng: -94.230884
    },
    stateName: "Arkansas"
  },
  {
    id: "115",
    state: "05",
    name: "Pope County",
    position: {
      lat: 35.455297,
      lng: -93.031535
    },
    stateName: "Arkansas"
  },
  {
    id: "121",
    state: "05",
    name: "Randolph County",
    position: {
      lat: 36.341299,
      lng: -91.028441
    },
    stateName: "Arkansas"
  },
  {
    id: "133",
    state: "05",
    name: "Sevier County",
    position: {
      lat: 33.994608,
      lng: -94.24329
    },
    stateName: "Arkansas"
  },
  {
    id: "137",
    state: "05",
    name: "Stone County",
    position: {
      lat: 35.856989,
      lng: -92.140494
    },
    stateName: "Arkansas"
  },
  {
    id: "317",
    state: "13",
    name: "Wilkes County",
    position: {
      lat: 33.77904,
      lng: -82.747922
    },
    stateName: "Georgia"
  },
  {
    id: "321",
    state: "13",
    name: "Worth County",
    position: {
      lat: 31.551773,
      lng: -83.84996
    },
    stateName: "Georgia"
  },
  {
    id: "005",
    state: "04",
    name: "Coconino County",
    position: {
      lat: 35.829692,
      lng: -111.773728
    },
    stateName: "Arizona"
  },
  {
    id: "009",
    state: "04",
    name: "Graham County",
    position: {
      lat: 32.931828,
      lng: -109.87831
    },
    stateName: "Arizona"
  },
  {
    id: "011",
    state: "04",
    name: "Greenlee County",
    position: {
      lat: 33.238872,
      lng: -109.242323
    },
    stateName: "Arizona"
  },
  {
    id: "012",
    state: "04",
    name: "La Paz County",
    position: {
      lat: 33.727625,
      lng: -114.038793
    },
    stateName: "Arizona"
  },
  {
    id: "017",
    state: "04",
    name: "Navajo County",
    position: {
      lat: 35.390934,
      lng: -110.320908
    },
    stateName: "Arizona"
  },
  {
    id: "021",
    state: "04",
    name: "Pinal County",
    position: {
      lat: 32.91891,
      lng: -111.367257
    },
    stateName: "Arizona"
  },
  {
    id: "023",
    state: "04",
    name: "Santa Cruz County",
    position: {
      lat: 31.525904,
      lng: -110.84519
    },
    stateName: "Arizona"
  },
  {
    id: "027",
    state: "04",
    name: "Yuma County",
    position: {
      lat: 32.773942,
      lng: -113.910905
    },
    stateName: "Arizona"
  },
  {
    id: "003",
    state: "05",
    name: "Ashley County",
    position: {
      lat: 33.190835,
      lng: -91.772267
    },
    stateName: "Arkansas"
  },
  {
    id: "013",
    state: "05",
    name: "Calhoun County",
    position: {
      lat: 33.56046,
      lng: -92.513879
    },
    stateName: "Arkansas"
  },
  {
    id: "071",
    state: "16",
    name: "Oneida County",
    position: {
      lat: 42.18389,
      lng: -112.520465
    },
    stateName: "Idaho"
  },
  {
    id: "077",
    state: "16",
    name: "Power County",
    position: {
      lat: 42.694126,
      lng: -112.844407
    },
    stateName: "Idaho"
  },
  {
    id: "079",
    state: "16",
    name: "Shoshone County",
    position: {
      lat: 47.347694,
      lng: -115.885092
    },
    stateName: "Idaho"
  },
  {
    id: "083",
    state: "16",
    name: "Twin Falls County",
    position: {
      lat: 42.352309,
      lng: -114.665639
    },
    stateName: "Idaho"
  },
  {
    id: "003",
    state: "17",
    name: "Alexander County",
    position: {
      lat: 37.183683,
      lng: -89.349506
    },
    stateName: "Illinois"
  },
  {
    id: "007",
    state: "17",
    name: "Boone County",
    position: {
      lat: 42.318983,
      lng: -88.824295
    },
    stateName: "Illinois"
  },
  {
    id: "011",
    state: "17",
    name: "Bureau County",
    position: {
      lat: 41.401304,
      lng: -89.528377
    },
    stateName: "Illinois"
  },
  {
    id: "015",
    state: "17",
    name: "Carroll County",
    position: {
      lat: 42.059084,
      lng: -89.926485
    },
    stateName: "Illinois"
  },
  {
    id: "023",
    state: "17",
    name: "Clark County",
    position: {
      lat: 39.332364,
      lng: -87.791687
    },
    stateName: "Illinois"
  },
  {
    id: "033",
    state: "17",
    name: "Crawford County",
    position: {
      lat: 39.00373,
      lng: -87.757172
    },
    stateName: "Illinois"
  },
  {
    id: "037",
    state: "17",
    name: "De Kalb County",
    position: {
      lat: 41.894613,
      lng: -88.768991
    },
    stateName: "Illinois"
  },
  {
    id: "041",
    state: "17",
    name: "Douglas County",
    position: {
      lat: 39.766078,
      lng: -88.222866
    },
    stateName: "Illinois"
  },
  {
    id: "043",
    state: "17",
    name: "Du Page County",
    position: {
      lat: 41.852058,
      lng: -88.086038
    },
    stateName: "Illinois"
  },
  {
    id: "117",
    state: "18",
    name: "Orange County",
    position: {
      lat: 38.547381,
      lng: -86.489257
    },
    stateName: "Indiana"
  },
  {
    id: "121",
    state: "18",
    name: "Parke County",
    position: {
      lat: 39.77425,
      lng: -87.19695
    },
    stateName: "Indiana"
  },
  {
    id: "127",
    state: "18",
    name: "Porter County",
    position: {
      lat: 41.509922,
      lng: -87.071308
    },
    stateName: "Indiana"
  },
  {
    id: "135",
    state: "18",
    name: "Randolph County",
    position: {
      lat: 40.164414,
      lng: -85.005004
    },
    stateName: "Indiana"
  },
  {
    id: "137",
    state: "18",
    name: "Ripley County",
    position: {
      lat: 39.10023,
      lng: -85.260541
    },
    stateName: "Indiana"
  },
  {
    id: "141",
    state: "18",
    name: "St. Joseph County",
    position: {
      lat: 41.617699,
      lng: -86.288159
    },
    stateName: "Indiana"
  },
  {
    id: "149",
    state: "18",
    name: "Starke County",
    position: {
      lat: 41.28322,
      lng: -86.64757
    },
    stateName: "Indiana"
  },
  {
    id: "151",
    state: "18",
    name: "Steuben County",
    position: {
      lat: 41.643437,
      lng: -85.002467
    },
    stateName: "Indiana"
  },
  {
    id: "159",
    state: "18",
    name: "Tipton County",
    position: {
      lat: 40.310229,
      lng: -86.056207
    },
    stateName: "Indiana"
  },
  {
    id: "161",
    state: "18",
    name: "Union County",
    position: {
      lat: 39.623111,
      lng: -84.925152
    },
    stateName: "Indiana"
  },
  {
    id: "167",
    state: "18",
    name: "Vigo County",
    position: {
      lat: 39.429143,
      lng: -87.390375
    },
    stateName: "Indiana"
  },
  {
    id: "169",
    state: "18",
    name: "Wabash County",
    position: {
      lat: 40.843717,
      lng: -85.795175
    },
    stateName: "Indiana"
  },
  {
    id: "175",
    state: "18",
    name: "Washington County",
    position: {
      lat: 38.600613,
      lng: -86.104751
    },
    stateName: "Indiana"
  },
  {
    id: "181",
    state: "18",
    name: "White County",
    position: {
      lat: 40.75095,
      lng: -86.864293
    },
    stateName: "Indiana"
  },
  {
    id: "001",
    state: "19",
    name: "Adair County",
    position: {
      lat: 41.328528,
      lng: -94.478164
    },
    stateName: "Iowa"
  },
  {
    id: "047",
    state: "17",
    name: "Edwards County",
    position: {
      lat: 38.417095,
      lng: -88.047941
    },
    stateName: "Illinois"
  },
  {
    id: "051",
    state: "17",
    name: "Fayette County",
    position: {
      lat: 39.001125,
      lng: -89.017923
    },
    stateName: "Illinois"
  },
  {
    id: "053",
    state: "17",
    name: "Ford County",
    position: {
      lat: 40.594423,
      lng: -88.224746
    },
    stateName: "Illinois"
  },
  {
    id: "057",
    state: "17",
    name: "Fulton County",
    position: {
      lat: 40.465688,
      lng: -90.206793
    },
    stateName: "Illinois"
  },
  {
    id: "063",
    state: "17",
    name: "Grundy County",
    position: {
      lat: 41.29241,
      lng: -88.401055
    },
    stateName: "Illinois"
  },
  {
    id: "055",
    state: "18",
    name: "Greene County",
    position: {
      lat: 39.0486,
      lng: -87.005245
    },
    stateName: "Indiana"
  },
  {
    id: "057",
    state: "18",
    name: "Hamilton County",
    position: {
      lat: 40.04987,
      lng: -86.020586
    },
    stateName: "Indiana"
  },
  {
    id: "065",
    state: "18",
    name: "Henry County",
    position: {
      lat: 39.929576,
      lng: -85.397338
    },
    stateName: "Indiana"
  },
  {
    id: "067",
    state: "18",
    name: "Howard County",
    position: {
      lat: 40.483537,
      lng: -86.114118
    },
    stateName: "Indiana"
  },
  {
    id: "071",
    state: "18",
    name: "Jackson County",
    position: {
      lat: 38.911957,
      lng: -86.042516
    },
    stateName: "Indiana"
  },
  {
    id: "077",
    state: "18",
    name: "Jefferson County",
    position: {
      lat: 38.783604,
      lng: -85.44009
    },
    stateName: "Indiana"
  },
  {
    id: "087",
    state: "18",
    name: "Lagrange County",
    position: {
      lat: 41.642468,
      lng: -85.426302
    },
    stateName: "Indiana"
  },
  {
    id: "091",
    state: "18",
    name: "La Porte County",
    position: {
      lat: 41.549011,
      lng: -86.744729
    },
    stateName: "Indiana"
  },
  {
    id: "097",
    state: "18",
    name: "Marion County",
    position: {
      lat: 39.782976,
      lng: -86.135794
    },
    stateName: "Indiana"
  },
  {
    id: "101",
    state: "18",
    name: "Martin County",
    position: {
      lat: 38.705322,
      lng: -86.801847
    },
    stateName: "Indiana"
  },
  {
    id: "103",
    state: "18",
    name: "Miami County",
    position: {
      lat: 40.772881,
      lng: -86.044259
    },
    stateName: "Indiana"
  },
  {
    id: "109",
    state: "18",
    name: "Morgan County",
    position: {
      lat: 39.482646,
      lng: -86.447457
    },
    stateName: "Indiana"
  },
  {
    id: "023",
    state: "16",
    name: "Butte County",
    position: {
      lat: 43.6851,
      lng: -113.177627
    },
    stateName: "Idaho"
  },
  {
    id: "025",
    state: "16",
    name: "Camas County",
    position: {
      lat: 43.463396,
      lng: -114.804427
    },
    stateName: "Idaho"
  },
  {
    id: "029",
    state: "16",
    name: "Caribou County",
    position: {
      lat: 42.786273,
      lng: -111.544172
    },
    stateName: "Idaho"
  },
  {
    id: "033",
    state: "16",
    name: "Clark County",
    position: {
      lat: 44.286251,
      lng: -112.36112
    },
    stateName: "Idaho"
  },
  {
    id: "041",
    state: "16",
    name: "Franklin County",
    position: {
      lat: 42.173589,
      lng: -111.822955
    },
    stateName: "Idaho"
  },
  {
    id: "043",
    state: "16",
    name: "Fremont County",
    position: {
      lat: 44.218091,
      lng: -111.484429
    },
    stateName: "Idaho"
  },
  {
    id: "047",
    state: "16",
    name: "Gooding County",
    position: {
      lat: 42.973185,
      lng: -114.82142
    },
    stateName: "Idaho"
  },
  {
    id: "053",
    state: "16",
    name: "Jerome County",
    position: {
      lat: 42.691172,
      lng: -114.263009
    },
    stateName: "Idaho"
  },
  {
    id: "055",
    state: "16",
    name: "Kootenai County",
    position: {
      lat: 47.677113,
      lng: -116.694918
    },
    stateName: "Idaho"
  },
  {
    id: "061",
    state: "16",
    name: "Lewis County",
    position: {
      lat: 46.236328,
      lng: -116.42376
    },
    stateName: "Idaho"
  },
  {
    id: "065",
    state: "16",
    name: "Madison County",
    position: {
      lat: 43.789709,
      lng: -111.65655
    },
    stateName: "Idaho"
  },
  {
    id: "069",
    state: "16",
    name: "Nez Perce County",
    position: {
      lat: 46.333766,
      lng: -116.760903
    },
    stateName: "Idaho"
  },
  {
    id: "007",
    state: "18",
    name: "Benton County",
    position: {
      lat: 40.608253,
      lng: -87.315479
    },
    stateName: "Indiana"
  },
  {
    id: "013",
    state: "18",
    name: "Brown County",
    position: {
      lat: 39.192585,
      lng: -86.23941
    },
    stateName: "Indiana"
  },
  {
    id: "015",
    state: "18",
    name: "Carroll County",
    position: {
      lat: 40.58498,
      lng: -86.565141
    },
    stateName: "Indiana"
  },
  {
    id: "019",
    state: "18",
    name: "Clark County",
    position: {
      lat: 38.476217,
      lng: -85.711122
    },
    stateName: "Indiana"
  },
  {
    id: "025",
    state: "18",
    name: "Crawford County",
    position: {
      lat: 38.289433,
      lng: -86.440871
    },
    stateName: "Indiana"
  },
  {
    id: "031",
    state: "18",
    name: "Decatur County",
    position: {
      lat: 39.30598,
      lng: -85.499831
    },
    stateName: "Indiana"
  },
  {
    id: "035",
    state: "18",
    name: "Delaware County",
    position: {
      lat: 40.227165,
      lng: -85.398856
    },
    stateName: "Indiana"
  },
  {
    id: "045",
    state: "18",
    name: "Fountain County",
    position: {
      lat: 40.121282,
      lng: -87.234806
    },
    stateName: "Indiana"
  },
  {
    id: "137",
    state: "13",
    name: "Habersham County",
    position: {
      lat: 34.635108,
      lng: -83.526406
    },
    stateName: "Georgia"
  },
  {
    id: "141",
    state: "13",
    name: "Hancock County",
    position: {
      lat: 33.26922,
      lng: -83.000465
    },
    stateName: "Georgia"
  },
  {
    id: "147",
    state: "13",
    name: "Hart County",
    position: {
      lat: 34.348733,
      lng: -82.96329
    },
    stateName: "Georgia"
  },
  {
    id: "155",
    state: "13",
    name: "Irwin County",
    position: {
      lat: 31.604306,
      lng: -83.277037
    },
    stateName: "Georgia"
  },
  {
    id: "159",
    state: "13",
    name: "Jasper County",
    position: {
      lat: 33.314905,
      lng: -83.687892
    },
    stateName: "Georgia"
  },
  {
    id: "165",
    state: "13",
    name: "Jenkins County",
    position: {
      lat: 32.794563,
      lng: -81.971524
    },
    stateName: "Georgia"
  },
  {
    id: "171",
    state: "13",
    name: "Lamar County",
    position: {
      lat: 33.07446,
      lng: -84.146721
    },
    stateName: "Georgia"
  },
  {
    id: "173",
    state: "13",
    name: "Lanier County",
    position: {
      lat: 31.038197,
      lng: -83.063164
    },
    stateName: "Georgia"
  },
  {
    id: "091",
    state: "20",
    name: "Johnson County",
    position: {
      lat: 38.883907,
      lng: -94.82233
    },
    stateName: "Kansas"
  },
  {
    id: "097",
    state: "20",
    name: "Kiowa County",
    position: {
      lat: 37.561231,
      lng: -99.286539
    },
    stateName: "Kansas"
  },
  {
    id: "099",
    state: "20",
    name: "Labette County",
    position: {
      lat: 37.191468,
      lng: -95.297473
    },
    stateName: "Kansas"
  },
  {
    id: "103",
    state: "20",
    name: "Leavenworth County",
    position: {
      lat: 39.189511,
      lng: -95.038977
    },
    stateName: "Kansas"
  },
  {
    id: "109",
    state: "20",
    name: "Logan County",
    position: {
      lat: 38.91327,
      lng: -101.157407
    },
    stateName: "Kansas"
  },
  {
    id: "113",
    state: "20",
    name: "Mcpherson County",
    position: {
      lat: 38.395812,
      lng: -97.647489
    },
    stateName: "Kansas"
  },
  {
    id: "119",
    state: "20",
    name: "Meade County",
    position: {
      lat: 37.243886,
      lng: -100.360094
    },
    stateName: "Kansas"
  },
  {
    id: "125",
    state: "20",
    name: "Montgomery County",
    position: {
      lat: 37.189537,
      lng: -95.742403
    },
    stateName: "Kansas"
  },
  {
    id: "131",
    state: "20",
    name: "Nemaha County",
    position: {
      lat: 39.791043,
      lng: -96.005381
    },
    stateName: "Kansas"
  },
  {
    id: "141",
    state: "20",
    name: "Osborne County",
    position: {
      lat: 39.348649,
      lng: -98.767876
    },
    stateName: "Kansas"
  },
  {
    id: "143",
    state: "20",
    name: "Ottawa County",
    position: {
      lat: 39.137963,
      lng: -97.654803
    },
    stateName: "Kansas"
  },
  {
    id: "157",
    state: "20",
    name: "Republic County",
    position: {
      lat: 39.82891,
      lng: -97.650921
    },
    stateName: "Kansas"
  },
  {
    id: "165",
    state: "20",
    name: "Rush County",
    position: {
      lat: 38.523592,
      lng: -99.309183
    },
    stateName: "Kansas"
  },
  {
    id: "167",
    state: "20",
    name: "Russell County",
    position: {
      lat: 38.916839,
      lng: -98.765638
    },
    stateName: "Kansas"
  },
  {
    id: "171",
    state: "20",
    name: "Scott County",
    position: {
      lat: 38.481877,
      lng: -100.90636
    },
    stateName: "Kansas"
  },
  {
    id: "177",
    state: "20",
    name: "Shawnee County",
    position: {
      lat: 39.041805,
      lng: -95.755664
    },
    stateName: "Kansas"
  },
  {
    id: "057",
    state: "22",
    name: "Lafourche County",
    postal: "Parish",
    position: {
      lat: 29.491993,
      lng: -90.394849
    },
    stateName: "Louisiana"
  },
  {
    id: "061",
    state: "22",
    name: "Lincoln County",
    postal: "Parish",
    position: {
      lat: 32.601148,
      lng: -92.662082
    },
    stateName: "Louisiana"
  },
  {
    id: "063",
    state: "22",
    name: "Livingston County",
    postal: "Parish",
    position: {
      lat: 30.440419,
      lng: -90.727474
    },
    stateName: "Louisiana"
  },
  {
    id: "069",
    state: "22",
    name: "Natchitoches County",
    postal: "Parish",
    position: {
      lat: 31.734982,
      lng: -93.086106
    },
    stateName: "Louisiana"
  },
  {
    id: "075",
    state: "22",
    name: "Plaquemines County",
    postal: "Parish",
    position: {
      lat: 29.402337,
      lng: -89.298489
    },
    stateName: "Louisiana"
  },
  {
    id: "079",
    state: "22",
    name: "Rapides County",
    postal: "Parish",
    position: {
      lat: 31.193204,
      lng: -92.535953
    },
    stateName: "Louisiana"
  },
  {
    id: "081",
    state: "22",
    name: "Red River County",
    postal: "Parish",
    position: {
      lat: 32.101213,
      lng: -93.34905
    },
    stateName: "Louisiana"
  },
  {
    id: "085",
    state: "22",
    name: "Sabine County",
    postal: "Parish",
    position: {
      lat: 31.560209,
      lng: -93.559228
    },
    stateName: "Louisiana"
  },
  {
    id: "111",
    state: "21",
    name: "Jefferson County",
    position: {
      lat: 38.189533,
      lng: -85.657624
    },
    stateName: "Kentucky"
  },
  {
    id: "113",
    state: "21",
    name: "Jessamine County",
    position: {
      lat: 37.873291,
      lng: -84.58396
    },
    stateName: "Kentucky"
  },
  {
    id: "123",
    state: "21",
    name: "Larue County",
    position: {
      lat: 37.545518,
      lng: -85.697209
    },
    stateName: "Kentucky"
  },
  {
    id: "129",
    state: "21",
    name: "Lee County",
    position: {
      lat: 37.605383,
      lng: -83.718497
    },
    stateName: "Kentucky"
  },
  {
    id: "135",
    state: "21",
    name: "Lewis County",
    position: {
      lat: 38.532051,
      lng: -83.373303
    },
    stateName: "Kentucky"
  },
  {
    id: "139",
    state: "21",
    name: "Livingston County",
    position: {
      lat: 37.209517,
      lng: -88.363426
    },
    stateName: "Kentucky"
  },
  {
    id: "145",
    state: "21",
    name: "Mc Cracken County",
    position: {
      lat: 37.053688,
      lng: -88.712378
    },
    stateName: "Kentucky"
  },
  {
    id: "153",
    state: "21",
    name: "Magoffin County",
    position: {
      lat: 37.698954,
      lng: -83.069716
    },
    stateName: "Kentucky"
  },
  {
    id: "159",
    state: "21",
    name: "Martin County",
    position: {
      lat: 37.796774,
      lng: -82.506623
    },
    stateName: "Kentucky"
  },
  {
    id: "167",
    state: "21",
    name: "Mercer County",
    position: {
      lat: 37.812085,
      lng: -84.879695
    },
    stateName: "Kentucky"
  },
  {
    id: "171",
    state: "21",
    name: "Monroe County",
    position: {
      lat: 36.714077,
      lng: -85.713508
    },
    stateName: "Kentucky"
  },
  {
    id: "173",
    state: "21",
    name: "Montgomery County",
    position: {
      lat: 38.038138,
      lng: -83.912338
    },
    stateName: "Kentucky"
  },
  {
    id: "179",
    state: "21",
    name: "Nelson County",
    position: {
      lat: 37.803188,
      lng: -85.465955
    },
    stateName: "Kentucky"
  },
  {
    id: "053",
    state: "19",
    name: "Decatur County",
    position: {
      lat: 40.736379,
      lng: -93.78458
    },
    stateName: "Iowa"
  },
  {
    id: "003",
    state: "18",
    name: "Allen County",
    position: {
      lat: 41.091855,
      lng: -85.07223
    },
    stateName: "Indiana"
  },
  {
    id: "181",
    state: "13",
    name: "Lincoln County",
    position: {
      lat: 33.792151,
      lng: -82.448299
    },
    stateName: "Georgia"
  },
  {
    id: "061",
    state: "19",
    name: "Dubuque County",
    position: {
      lat: 42.463481,
      lng: -90.878771
    },
    stateName: "Iowa"
  },
  {
    id: "069",
    state: "19",
    name: "Franklin County",
    position: {
      lat: 42.736549,
      lng: -93.271425
    },
    stateName: "Iowa"
  },
  {
    id: "075",
    state: "19",
    name: "Grundy County",
    position: {
      lat: 42.403323,
      lng: -92.790261
    },
    stateName: "Iowa"
  },
  {
    id: "077",
    state: "19",
    name: "Guthrie County",
    position: {
      lat: 41.683573,
      lng: -94.501272
    },
    stateName: "Iowa"
  },
  {
    id: "089",
    state: "19",
    name: "Howard County",
    position: {
      lat: 43.365313,
      lng: -92.321908
    },
    stateName: "Iowa"
  },
  {
    id: "093",
    state: "19",
    name: "Ida County",
    position: {
      lat: 42.39186,
      lng: -95.507421
    },
    stateName: "Iowa"
  },
  {
    id: "101",
    state: "19",
    name: "Jefferson County",
    position: {
      lat: 41.00688,
      lng: -91.967137
    },
    stateName: "Iowa"
  },
  {
    id: "103",
    state: "19",
    name: "Johnson County",
    position: {
      lat: 41.668737,
      lng: -91.588812
    },
    stateName: "Iowa"
  },
  {
    id: "107",
    state: "19",
    name: "Keokuk County",
    position: {
      lat: 41.331182,
      lng: -92.167721
    },
    stateName: "Iowa"
  },
  {
    id: "111",
    state: "19",
    name: "Lee County",
    position: {
      lat: 40.647588,
      lng: -91.477157
    },
    stateName: "Iowa"
  },
  {
    id: "127",
    state: "19",
    name: "Marshall County",
    position: {
      lat: 42.041691,
      lng: -92.981452
    },
    stateName: "Iowa"
  },
  {
    id: "129",
    state: "19",
    name: "Mills County",
    position: {
      lat: 41.033703,
      lng: -95.619101
    },
    stateName: "Iowa"
  },
  {
    id: "135",
    state: "19",
    name: "Monroe County",
    position: {
      lat: 41.028847,
      lng: -92.869642
    },
    stateName: "Iowa"
  },
  {
    id: "139",
    state: "19",
    name: "Muscatine County",
    position: {
      lat: 41.483776,
      lng: -91.118699
    },
    stateName: "Iowa"
  },
  {
    id: "145",
    state: "19",
    name: "Page County",
    position: {
      lat: 40.73909,
      lng: -95.14429
    },
    stateName: "Iowa"
  },
  {
    id: "181",
    state: "20",
    name: "Sherman County",
    position: {
      lat: 39.351352,
      lng: -101.719859
    },
    stateName: "Kansas"
  },
  {
    id: "153",
    state: "19",
    name: "Polk County",
    position: {
      lat: 41.684281,
      lng: -93.56972
    },
    stateName: "Iowa"
  },
  {
    id: "155",
    state: "19",
    name: "Pottawattamie County",
    position: {
      lat: 41.340184,
      lng: -95.544905
    },
    stateName: "Iowa"
  },
  {
    id: "159",
    state: "19",
    name: "Ringgold County",
    position: {
      lat: 40.735334,
      lng: -94.244251
    },
    stateName: "Iowa"
  },
  {
    id: "163",
    state: "19",
    name: "Scott County",
    position: {
      lat: 41.641679,
      lng: -90.62229
    },
    stateName: "Iowa"
  },
  {
    id: "171",
    state: "19",
    name: "Tama County",
    position: {
      lat: 42.074848,
      lng: -92.529412
    },
    stateName: "Iowa"
  },
  {
    id: "177",
    state: "19",
    name: "Van Buren County",
    position: {
      lat: 40.754117,
      lng: -91.952943
    },
    stateName: "Iowa"
  },
  {
    id: "183",
    state: "19",
    name: "Washington County",
    position: {
      lat: 41.329401,
      lng: -91.725052
    },
    stateName: "Iowa"
  },
  {
    id: "189",
    state: "19",
    name: "Winnebago County",
    position: {
      lat: 43.378124,
      lng: -93.743488
    },
    stateName: "Iowa"
  },
  {
    id: "193",
    state: "19",
    name: "Woodbury County",
    position: {
      lat: 42.39322,
      lng: -96.053296
    },
    stateName: "Iowa"
  },
  {
    id: "003",
    state: "20",
    name: "Anderson County",
    position: {
      lat: 38.215114,
      lng: -95.292046
    },
    stateName: "Kansas"
  },
  {
    id: "009",
    state: "20",
    name: "Barton County",
    position: {
      lat: 38.481239,
      lng: -98.767837
    },
    stateName: "Kansas"
  },
  {
    id: "017",
    state: "20",
    name: "Chase County",
    position: {
      lat: 38.298553,
      lng: -96.594064
    },
    stateName: "Kansas"
  },
  {
    id: "019",
    state: "20",
    name: "Chautauqua County",
    position: {
      lat: 37.154259,
      lng: -96.245396
    },
    stateName: "Kansas"
  },
  {
    id: "211",
    state: "13",
    name: "Morgan County",
    position: {
      lat: 33.593732,
      lng: -83.492324
    },
    stateName: "Georgia"
  },
  {
    id: "081",
    state: "27",
    name: "Lincoln County",
    position: {
      lat: 44.408238,
      lng: -96.272032
    },
    stateName: "Minnesota"
  },
  {
    id: "089",
    state: "27",
    name: "Marshall County",
    position: {
      lat: 48.362728,
      lng: -96.357761
    },
    stateName: "Minnesota"
  },
  {
    id: "091",
    state: "27",
    name: "Martin County",
    position: {
      lat: 43.677118,
      lng: -94.537198
    },
    stateName: "Minnesota"
  },
  {
    id: "095",
    state: "27",
    name: "Mille Lacs County",
    position: {
      lat: 45.929043,
      lng: -93.632996
    },
    stateName: "Minnesota"
  },
  {
    id: "097",
    state: "27",
    name: "Morrison County",
    position: {
      lat: 46.020484,
      lng: -94.266619
    },
    stateName: "Minnesota"
  },
  {
    id: "099",
    state: "27",
    name: "Mower County",
    position: {
      lat: 43.666249,
      lng: -92.759514
    },
    stateName: "Minnesota"
  },
  {
    id: "105",
    state: "27",
    name: "Nobles County",
    position: {
      lat: 43.677686,
      lng: -95.763132
    },
    stateName: "Minnesota"
  },
  {
    id: "109",
    state: "27",
    name: "Olmsted County",
    position: {
      lat: 44.00343,
      lng: -92.406722
    },
    stateName: "Minnesota"
  },
  {
    id: "111",
    state: "27",
    name: "Otter Tail County",
    position: {
      lat: 46.405725,
      lng: -95.714578
    },
    stateName: "Minnesota"
  },
  {
    id: "115",
    state: "27",
    name: "Pine County",
    position: {
      lat: 46.10094,
      lng: -92.763094
    },
    stateName: "Minnesota"
  },
  {
    id: "117",
    state: "27",
    name: "Pipestone County",
    position: {
      lat: 44.015361,
      lng: -96.257015
    },
    stateName: "Minnesota"
  },
  {
    id: "119",
    state: "27",
    name: "Polk County",
    position: {
      lat: 47.774254,
      lng: -96.400027
    },
    stateName: "Minnesota"
  },
  {
    id: "129",
    state: "27",
    name: "Renville County",
    position: {
      lat: 44.723697,
      lng: -94.955617
    },
    stateName: "Minnesota"
  },
  {
    id: "133",
    state: "27",
    name: "Rock County",
    position: {
      lat: 43.669587,
      lng: -96.263238
    },
    stateName: "Minnesota"
  },
  {
    id: "059",
    state: "19",
    name: "Dickinson County",
    position: {
      lat: 43.389611,
      lng: -95.196057
    },
    stateName: "Iowa"
  },
  {
    id: "141",
    state: "27",
    name: "Sherburne County",
    position: {
      lat: 45.443171,
      lng: -93.775092
    },
    stateName: "Minnesota"
  },
  {
    id: "145",
    state: "27",
    name: "Stearns County",
    position: {
      lat: 45.555235,
      lng: -94.610482
    },
    stateName: "Minnesota"
  },
  {
    id: "149",
    state: "27",
    name: "Stevens County",
    position: {
      lat: 45.593461,
      lng: -95.992315
    },
    stateName: "Minnesota"
  },
  {
    id: "157",
    state: "27",
    name: "Wabasha County",
    position: {
      lat: 44.289693,
      lng: -92.233341
    },
    stateName: "Minnesota"
  },
  {
    id: "163",
    state: "27",
    name: "Washington County",
    position: {
      lat: 45.037929,
      lng: -92.890117
    },
    stateName: "Minnesota"
  },
  {
    id: "171",
    state: "27",
    name: "Wright County",
    position: {
      lat: 45.175091,
      lng: -93.966397
    },
    stateName: "Minnesota"
  },
  {
    id: "005",
    state: "28",
    name: "Amite County",
    position: {
      lat: 31.203933,
      lng: -90.795542
    },
    stateName: "Mississippi"
  },
  {
    id: "009",
    state: "28",
    name: "Benton County",
    position: {
      lat: 34.810773,
      lng: -89.200029
    },
    stateName: "Mississippi"
  },
  {
    id: "011",
    state: "28",
    name: "Bolivar County",
    position: {
      lat: 33.799278,
      lng: -90.884476
    },
    stateName: "Mississippi"
  },
  {
    id: "015",
    state: "28",
    name: "Carroll County",
    position: {
      lat: 33.440795,
      lng: -89.918887
    },
    stateName: "Mississippi"
  },
  {
    id: "021",
    state: "28",
    name: "Claiborne County",
    position: {
      lat: 31.97281,
      lng: -90.915424
    },
    stateName: "Mississippi"
  },
  {
    id: "031",
    state: "28",
    name: "Covington County",
    position: {
      lat: 31.633331,
      lng: -89.548897
    },
    stateName: "Mississippi"
  },
  {
    id: "039",
    state: "28",
    name: "George County",
    position: {
      lat: 30.855431,
      lng: -88.642268
    },
    stateName: "Mississippi"
  },
  {
    id: "149",
    state: "19",
    name: "Plymouth County",
    position: {
      lat: 42.737585,
      lng: -96.215864
    },
    stateName: "Iowa"
  },
  {
    id: "045",
    state: "28",
    name: "Hancock County",
    position: {
      lat: 30.391437,
      lng: -89.47985
    },
    stateName: "Mississippi"
  },
  {
    id: "051",
    state: "28",
    name: "Holmes County",
    position: {
      lat: 33.125942,
      lng: -90.091197
    },
    stateName: "Mississippi"
  },
  {
    id: "053",
    state: "28",
    name: "Humphreys County",
    position: {
      lat: 33.132978,
      lng: -90.523911
    },
    stateName: "Mississippi"
  },
  {
    id: "059",
    state: "28",
    name: "Jackson County",
    position: {
      lat: 30.458491,
      lng: -88.619991
    },
    stateName: "Mississippi"
  },
  {
    id: "067",
    state: "28",
    name: "Jones County",
    position: {
      lat: 31.621044,
      lng: -89.167262
    },
    stateName: "Mississippi"
  },
  {
    id: "073",
    state: "28",
    name: "Lamar County",
    position: {
      lat: 31.197135,
      lng: -89.514952
    },
    stateName: "Mississippi"
  },
  {
    id: "007",
    state: "23",
    name: "Franklin County",
    position: {
      lat: 44.973012,
      lng: -70.444727
    },
    stateName: "Maine"
  },
  {
    id: "215",
    state: "13",
    name: "Muscogee County",
    position: {
      lat: 32.510197,
      lng: -84.874946
    },
    stateName: "Georgia"
  },
  {
    id: "239",
    state: "13",
    name: "Quitman County",
    position: {
      lat: 31.861487,
      lng: -85.009317
    },
    stateName: "Georgia"
  },
  {
    id: "077",
    state: "27",
    name: "Lake Of  Woods County",
    position: {
      lat: 48.7681,
      lng: -94.904634
    },
    stateName: "Minnesota"
  },
  {
    id: "031",
    state: "45",
    name: "Darlington County",
    position: {
      lat: 34.332185,
      lng: -79.962116
    },
    stateName: "South Carolina"
  },
  {
    id: "035",
    state: "45",
    name: "Dorchester County",
    position: {
      lat: 33.082186,
      lng: -80.404697
    },
    stateName: "South Carolina"
  },
  {
    id: "037",
    state: "45",
    name: "Edgefield County",
    position: {
      lat: 33.776498,
      lng: -81.968245
    },
    stateName: "South Carolina"
  },
  {
    id: "011",
    state: "47",
    name: "Bradley County",
    position: {
      lat: 35.153914,
      lng: -84.859414
    },
    stateName: "Tennessee"
  },
  {
    id: "017",
    state: "47",
    name: "Carroll County",
    position: {
      lat: 35.967896,
      lng: -88.451659
    },
    stateName: "Tennessee"
  },
  {
    id: "021",
    state: "47",
    name: "Cheatham County",
    position: {
      lat: 36.25518,
      lng: -87.100816
    },
    stateName: "Tennessee"
  },
  {
    id: "025",
    state: "47",
    name: "Claiborne County",
    position: {
      lat: 36.501557,
      lng: -83.660724
    },
    stateName: "Tennessee"
  },
  {
    id: "033",
    state: "47",
    name: "Crockett County",
    position: {
      lat: 35.811312,
      lng: -89.135349
    },
    stateName: "Tennessee"
  },
  {
    id: "035",
    state: "47",
    name: "Cumberland County",
    position: {
      lat: 35.952398,
      lng: -84.994761
    },
    stateName: "Tennessee"
  },
  {
    id: "041",
    state: "47",
    name: "De Kalb County",
    position: {
      lat: 35.98222,
      lng: -85.833596
    },
    stateName: "Tennessee"
  },
  {
    id: "047",
    state: "47",
    name: "Fayette County",
    position: {
      lat: 35.196993,
      lng: -89.413803
    },
    stateName: "Tennessee"
  },
  {
    id: "049",
    state: "47",
    name: "Fentress County",
    position: {
      lat: 36.376079,
      lng: -84.932703
    },
    stateName: "Tennessee"
  },
  {
    id: "059",
    state: "47",
    name: "Greene County",
    position: {
      lat: 36.178998,
      lng: -82.847746
    },
    stateName: "Tennessee"
  },
  {
    id: "061",
    state: "47",
    name: "Grundy County",
    position: {
      lat: 35.387273,
      lng: -85.722188
    },
    stateName: "Tennessee"
  },
  {
    id: "137",
    state: "27",
    name: "St. Louis County",
    position: {
      lat: 47.583852,
      lng: -92.463645
    },
    stateName: "Minnesota"
  },
  {
    id: "015",
    state: "23",
    name: "Lincoln County",
    position: {
      lat: 43.994265,
      lng: -69.514029
    },
    stateName: "Maine"
  },
  {
    id: "021",
    state: "23",
    name: "Piscataquis County",
    position: {
      lat: 45.917678,
      lng: -69.104548
    },
    stateName: "Maine"
  },
  {
    id: "023",
    state: "23",
    name: "Sagadahoc County",
    position: {
      lat: 43.916694,
      lng: -69.843994
    },
    stateName: "Maine"
  },
  {
    id: "031",
    state: "23",
    name: "York County",
    position: {
      lat: 43.427239,
      lng: -70.670402
    },
    stateName: "Maine"
  },
  {
    id: "041",
    state: "28",
    name: "Greene County",
    position: {
      lat: 31.212846,
      lng: -88.634811
    },
    stateName: "Mississippi"
  },
  {
    id: "149",
    state: "31",
    name: "Rock County",
    position: {
      lat: 42.413325,
      lng: -99.45853
    },
    stateName: "Nebraska"
  },
  {
    id: "151",
    state: "31",
    name: "Saline County",
    position: {
      lat: 40.516802,
      lng: -97.131755
    },
    stateName: "Nebraska"
  },
  {
    id: "155",
    state: "31",
    name: "Saunders County",
    position: {
      lat: 41.223152,
      lng: -96.640923
    },
    stateName: "Nebraska"
  },
  {
    id: "161",
    state: "31",
    name: "Sheridan County",
    position: {
      lat: 42.507074,
      lng: -102.389698
    },
    stateName: "Nebraska"
  },
  {
    id: "169",
    state: "31",
    name: "Thayer County",
    position: {
      lat: 40.173845,
      lng: -97.596263
    },
    stateName: "Nebraska"
  },
  {
    id: "173",
    state: "31",
    name: "Thurston County",
    position: {
      lat: 42.154061,
      lng: -96.533943
    },
    stateName: "Nebraska"
  },
  {
    id: "181",
    state: "31",
    name: "Webster County",
    position: {
      lat: 40.180646,
      lng: -98.49859
    },
    stateName: "Nebraska"
  },
  {
    id: "005",
    state: "32",
    name: "Douglas County",
    position: {
      lat: 38.905129,
      lng: -119.609019
    },
    stateName: "Nevada"
  },
  {
    id: "007",
    state: "32",
    name: "Elko County",
    position: {
      lat: 41.141133,
      lng: -115.351424
    },
    stateName: "Nevada"
  },
  {
    id: "013",
    state: "32",
    name: "Humboldt County",
    position: {
      lat: 41.407914,
      lng: -118.127592
    },
    stateName: "Nevada"
  },
  {
    id: "017",
    state: "32",
    name: "Lincoln County",
    position: {
      lat: 37.634605,
      lng: -114.863037
    },
    stateName: "Nevada"
  },
  {
    id: "023",
    state: "32",
    name: "Nye County",
    position: {
      lat: 37.966379,
      lng: -116.459047
    },
    stateName: "Nevada"
  },
  {
    id: "011",
    state: "23",
    name: "Kennebec County",
    position: {
      lat: 44.417012,
      lng: -69.765764
    },
    stateName: "Maine"
  },
  {
    id: "105",
    state: "39",
    name: "Meigs County",
    position: {
      lat: 39.089807,
      lng: -82.028397
    },
    stateName: "Ohio"
  },
  {
    id: "107",
    state: "39",
    name: "Mercer County",
    position: {
      lat: 40.535333,
      lng: -84.632059
    },
    stateName: "Ohio"
  },
  {
    id: "117",
    state: "39",
    name: "Morrow County",
    position: {
      lat: 40.525266,
      lng: -82.797729
    },
    stateName: "Ohio"
  },
  {
    id: "119",
    state: "39",
    name: "Muskingum County",
    position: {
      lat: 39.966046,
      lng: -81.943506
    },
    stateName: "Ohio"
  },
  {
    id: "125",
    state: "39",
    name: "Paulding County",
    position: {
      lat: 41.119141,
      lng: -84.576967
    },
    stateName: "Ohio"
  },
  {
    id: "129",
    state: "39",
    name: "Pickaway County",
    position: {
      lat: 39.648947,
      lng: -83.052827
    },
    stateName: "Ohio"
  },
  {
    id: "133",
    state: "39",
    name: "Portage County",
    position: {
      lat: 41.16864,
      lng: -81.196932
    },
    stateName: "Ohio"
  },
  {
    id: "135",
    state: "39",
    name: "Preble County",
    position: {
      lat: 39.737906,
      lng: -84.645358
    },
    stateName: "Ohio"
  },
  {
    id: "139",
    state: "39",
    name: "Richland County",
    position: {
      lat: 40.774167,
      lng: -82.542715
    },
    stateName: "Ohio"
  },
  {
    id: "141",
    state: "39",
    name: "Ross County",
    position: {
      lat: 39.323763,
      lng: -83.059585
    },
    stateName: "Ohio"
  },
  {
    id: "149",
    state: "39",
    name: "Shelby County",
    position: {
      lat: 40.33668,
      lng: -84.204143
    },
    stateName: "Ohio"
  },
  {
    id: "151",
    state: "39",
    name: "Stark County",
    position: {
      lat: 40.814131,
      lng: -81.365667
    },
    stateName: "Ohio"
  },
  {
    id: "157",
    state: "39",
    name: "Tuscarawas County",
    position: {
      lat: 40.447441,
      lng: -81.471157
    },
    stateName: "Ohio"
  },
  {
    id: "161",
    state: "39",
    name: "Van Wert County",
    position: {
      lat: 40.85552,
      lng: -84.585775
    },
    stateName: "Ohio"
  },
  {
    id: "065",
    state: "47",
    name: "Hamilton County",
    position: {
      lat: 35.159186,
      lng: -85.202296
    },
    stateName: "Tennessee"
  },
  {
    id: "067",
    state: "47",
    name: "Hancock County",
    position: {
      lat: 36.52142,
      lng: -83.227453
    },
    stateName: "Tennessee"
  },
  {
    id: "071",
    state: "47",
    name: "Hardin County",
    position: {
      lat: 35.201893,
      lng: -88.185696
    },
    stateName: "Tennessee"
  },
  {
    id: "077",
    state: "47",
    name: "Henderson County",
    position: {
      lat: 35.653995,
      lng: -88.387674
    },
    stateName: "Tennessee"
  },
  {
    id: "083",
    state: "47",
    name: "Houston County",
    position: {
      lat: 36.285777,
      lng: -87.705605
    },
    stateName: "Tennessee"
  },
  {
    id: "087",
    state: "47",
    name: "Jackson County",
    position: {
      lat: 36.354242,
      lng: -85.674182
    },
    stateName: "Tennessee"
  },
  {
    id: "095",
    state: "47",
    name: "Lake County",
    position: {
      lat: 36.333905,
      lng: -89.485537
    },
    stateName: "Tennessee"
  },
  {
    id: "101",
    state: "47",
    name: "Lewis County",
    position: {
      lat: 35.523244,
      lng: -87.496983
    },
    stateName: "Tennessee"
  },
  {
    id: "107",
    state: "47",
    name: "Mc Minn County",
    position: {
      lat: 35.424471,
      lng: -84.619963
    },
    stateName: "Tennessee"
  },
  {
    id: "109",
    state: "47",
    name: "Mc Nairy County",
    position: {
      lat: 35.175626,
      lng: -88.564671
    },
    stateName: "Tennessee"
  },
  {
    id: "111",
    state: "47",
    name: "Macon County",
    position: {
      lat: 36.537838,
      lng: -86.001231
    },
    stateName: "Tennessee"
  },
  {
    id: "119",
    state: "47",
    name: "Maury County",
    position: {
      lat: 35.615696,
      lng: -87.077763
    },
    stateName: "Tennessee"
  },
  {
    id: "125",
    state: "47",
    name: "Montgomery County",
    position: {
      lat: 36.500354,
      lng: -87.380887
    },
    stateName: "Tennessee"
  },
  {
    id: "135",
    state: "47",
    name: "Perry County",
    position: {
      lat: 35.659785,
      lng: -87.877027
    },
    stateName: "Tennessee"
  },
  {
    id: "137",
    state: "47",
    name: "Pickett County",
    position: {
      lat: 36.559364,
      lng: -85.075741
    },
    stateName: "Tennessee"
  },
  {
    id: "011",
    state: "24",
    name: "Caroline County",
    position: {
      lat: 38.871531,
      lng: -75.831631
    },
    stateName: "Maryland"
  },
  {
    id: "013",
    state: "24",
    name: "Carroll County",
    position: {
      lat: 39.563189,
      lng: -77.015512
    },
    stateName: "Maryland"
  },
  {
    id: "021",
    state: "24",
    name: "Frederick County",
    position: {
      lat: 39.470427,
      lng: -77.397627
    },
    stateName: "Maryland"
  },
  {
    id: "023",
    state: "24",
    name: "Garrett County",
    position: {
      lat: 39.529871,
      lng: -79.269416
    },
    stateName: "Maryland"
  },
  {
    id: "031",
    state: "24",
    name: "Montgomery County",
    position: {
      lat: 39.137382,
      lng: -77.203063
    },
    stateName: "Maryland"
  },
  {
    id: "033",
    state: "24",
    name: "Prince Georges County",
    position: {
      lat: 38.82588,
      lng: -76.847272
    },
    stateName: "Maryland"
  },
  {
    id: "043",
    state: "24",
    name: "Washington County",
    position: {
      lat: 39.603621,
      lng: -77.814671
    },
    stateName: "Maryland"
  },
  {
    id: "031",
    state: "32",
    name: "Washoe County",
    position: {
      lat: 40.703311,
      lng: -119.710315
    },
    stateName: "Nevada"
  },
  {
    id: "510",
    state: "32",
    name: "Carson City County",
    postal: "",
    position: {
      lat: 39.153447,
      lng: -119.743442
    },
    stateName: "Nevada"
  },
  {
    id: "003",
    state: "33",
    name: "Carroll County",
    position: {
      lat: 43.867567,
      lng: -71.201665
    },
    stateName: "New Hampshire"
  },
  {
    id: "009",
    state: "33",
    name: "Grafton County",
    position: {
      lat: 43.926488,
      lng: -71.842264
    },
    stateName: "New Hampshire"
  },
  {
    id: "015",
    state: "33",
    name: "Rockingham County",
    position: {
      lat: 42.98936,
      lng: -71.099437
    },
    stateName: "New Hampshire"
  },
  {
    id: "019",
    state: "33",
    name: "Sullivan County",
    position: {
      lat: 43.361188,
      lng: -72.222084
    },
    stateName: "New Hampshire"
  },
  {
    id: "003",
    state: "34",
    name: "Bergen County",
    position: {
      lat: 40.95909,
      lng: -74.074522
    },
    stateName: "New Jersey"
  },
  {
    id: "009",
    state: "34",
    name: "Cape May County",
    position: {
      lat: 39.086143,
      lng: -74.847716
    },
    stateName: "New Jersey"
  },
  {
    id: "013",
    state: "34",
    name: "Essex County",
    position: {
      lat: 40.787217,
      lng: -74.246136
    },
    stateName: "New Jersey"
  },
  {
    id: "167",
    state: "39",
    name: "Washington County",
    position: {
      lat: 39.450684,
      lng: -81.490653
    },
    stateName: "Ohio"
  },
  {
    id: "171",
    state: "39",
    name: "Williams County",
    position: {
      lat: 41.564958,
      lng: -84.584323
    },
    stateName: "Ohio"
  },
  {
    id: "175",
    state: "39",
    name: "Wyandot County",
    position: {
      lat: 40.840122,
      lng: -83.313172
    },
    stateName: "Ohio"
  },
  {
    id: "003",
    state: "40",
    name: "Alfalfa County",
    position: {
      lat: 36.729703,
      lng: -98.323445
    },
    stateName: "Oklahoma"
  },
  {
    id: "011",
    state: "40",
    name: "Blaine County",
    position: {
      lat: 35.877782,
      lng: -98.428934
    },
    stateName: "Oklahoma"
  },
  {
    id: "013",
    state: "40",
    name: "Bryan County",
    position: {
      lat: 33.964004,
      lng: -96.264137
    },
    stateName: "Oklahoma"
  },
  {
    id: "017",
    state: "40",
    name: "Canadian County",
    position: {
      lat: 35.543416,
      lng: -97.979836
    },
    stateName: "Oklahoma"
  },
  {
    id: "025",
    state: "40",
    name: "Cimarron County",
    position: {
      lat: 36.755276,
      lng: -102.508735
    },
    stateName: "Oklahoma"
  },
  {
    id: "029",
    state: "40",
    name: "Coal County",
    position: {
      lat: 34.582861,
      lng: -96.288039
    },
    stateName: "Oklahoma"
  },
  {
    id: "035",
    state: "40",
    name: "Craig County",
    position: {
      lat: 36.76389,
      lng: -95.201553
    },
    stateName: "Oklahoma"
  },
  {
    id: "039",
    state: "40",
    name: "Custer County",
    position: {
      lat: 35.645601,
      lng: -98.997386
    },
    stateName: "Oklahoma"
  },
  {
    id: "045",
    state: "40",
    name: "Ellis County",
    position: {
      lat: 36.224258,
      lng: -99.750139
    },
    stateName: "Oklahoma"
  },
  {
    id: "049",
    state: "40",
    name: "Garvin County",
    position: {
      lat: 34.70935,
      lng: -97.312723
    },
    stateName: "Oklahoma"
  },
  {
    id: "055",
    state: "40",
    name: "Greer County",
    position: {
      lat: 34.935263,
      lng: -99.552968
    },
    stateName: "Oklahoma"
  },
  {
    id: "193",
    state: "37",
    name: "Wilkes County",
    position: {
      lat: 36.209303,
      lng: -81.165354
    },
    stateName: "North Carolina"
  },
  {
    id: "141",
    state: "47",
    name: "Putnam County",
    position: {
      lat: 36.140807,
      lng: -85.496928
    },
    stateName: "Tennessee"
  },
  {
    id: "045",
    state: "48",
    name: "Briscoe County",
    position: {
      lat: 34.525173,
      lng: -101.205893
    },
    stateName: "Texas"
  },
  {
    id: "049",
    state: "48",
    name: "Brown County",
    position: {
      lat: 31.764103,
      lng: -98.99847
    },
    stateName: "Texas"
  },
  {
    id: "055",
    state: "48",
    name: "Caldwell County",
    position: {
      lat: 29.840422,
      lng: -97.631097
    },
    stateName: "Texas"
  },
  {
    id: "061",
    state: "48",
    name: "Cameron County",
    position: {
      lat: 26.102923,
      lng: -97.478958
    },
    stateName: "Texas"
  },
  {
    id: "065",
    state: "48",
    name: "Carson County",
    position: {
      lat: 35.405496,
      lng: -101.355356
    },
    stateName: "Texas"
  },
  {
    id: "067",
    state: "48",
    name: "Cass County",
    position: {
      lat: 33.083698,
      lng: -94.357579
    },
    stateName: "Texas"
  },
  {
    id: "073",
    state: "48",
    name: "Cherokee County",
    position: {
      lat: 31.843859,
      lng: -95.156504
    },
    stateName: "Texas"
  },
  {
    id: "077",
    state: "48",
    name: "Clay County",
    position: {
      lat: 33.785904,
      lng: -98.212918
    },
    stateName: "Texas"
  },
  {
    id: "081",
    state: "48",
    name: "Coke County",
    position: {
      lat: 31.877105,
      lng: -100.635236
    },
    stateName: "Texas"
  },
  {
    id: "085",
    state: "48",
    name: "Collin County",
    position: {
      lat: 33.193885,
      lng: -96.578153
    },
    stateName: "Texas"
  },
  {
    id: "087",
    state: "48",
    name: "Collingsworth County",
    position: {
      lat: 34.963358,
      lng: -100.272135
    },
    stateName: "Texas"
  },
  {
    id: "091",
    state: "48",
    name: "Comal County",
    position: {
      lat: 29.803019,
      lng: -98.255201
    },
    stateName: "Texas"
  },
  {
    id: "047",
    state: "24",
    name: "Worcester County",
    position: {
      lat: 38.222133,
      lng: -75.309932
    },
    stateName: "Maryland"
  },
  {
    id: "007",
    state: "25",
    name: "Dukes County",
    position: {
      lat: 41.380939,
      lng: -70.701536
    },
    stateName: "Massachusetts"
  },
  {
    id: "013",
    state: "25",
    name: "Hampden County",
    position: {
      lat: 42.136198,
      lng: -72.635648
    },
    stateName: "Massachusetts"
  },
  {
    id: "015",
    state: "25",
    name: "Hampshire County",
    position: {
      lat: 42.339459,
      lng: -72.663694
    },
    stateName: "Massachusetts"
  },
  {
    id: "017",
    state: "25",
    name: "Middlesex County",
    position: {
      lat: 42.479477,
      lng: -71.396507
    },
    stateName: "Massachusetts"
  },
  {
    id: "017",
    state: "34",
    name: "Hudson County",
    position: {
      lat: 40.731384,
      lng: -74.078627
    },
    stateName: "New Jersey"
  },
  {
    id: "019",
    state: "34",
    name: "Hunterdon County",
    position: {
      lat: 40.565283,
      lng: -74.91197
    },
    stateName: "New Jersey"
  },
  {
    id: "021",
    state: "34",
    name: "Mercer County",
    position: {
      lat: 40.282503,
      lng: -74.703724
    },
    stateName: "New Jersey"
  },
  {
    id: "029",
    state: "34",
    name: "Ocean County",
    position: {
      lat: 39.86585,
      lng: -74.263027
    },
    stateName: "New Jersey"
  },
  {
    id: "033",
    state: "34",
    name: "Salem County",
    position: {
      lat: 39.573828,
      lng: -75.357356
    },
    stateName: "New Jersey"
  },
  {
    id: "035",
    state: "34",
    name: "Somerset County",
    position: {
      lat: 40.565522,
      lng: -74.61993
    },
    stateName: "New Jersey"
  },
  {
    id: "041",
    state: "34",
    name: "Warren County",
    position: {
      lat: 40.853524,
      lng: -75.009542
    },
    stateName: "New Jersey"
  },
  {
    id: "007",
    state: "35",
    name: "Colfax County",
    position: {
      lat: 36.612963,
      lng: -104.640126
    },
    stateName: "New Mexico"
  },
  {
    id: "055",
    state: "30",
    name: "Mccone County",
    position: {
      lat: 47.629628,
      lng: -105.757222
    },
    stateName: "Montana"
  },
  {
    id: "057",
    state: "30",
    name: "Madison County",
    position: {
      lat: 45.319288,
      lng: -111.917328
    },
    stateName: "Montana"
  },
  {
    id: "195",
    state: "37",
    name: "Wilson County",
    position: {
      lat: 35.704125,
      lng: -77.918982
    },
    stateName: "North Carolina"
  },
  {
    id: "199",
    state: "37",
    name: "Yancey County",
    position: {
      lat: 35.889504,
      lng: -82.30398
    },
    stateName: "North Carolina"
  },
  {
    id: "005",
    state: "38",
    name: "Benson County",
    position: {
      lat: 48.071738,
      lng: -99.361987
    },
    stateName: "North Dakota"
  },
  {
    id: "009",
    state: "38",
    name: "Bottineau County",
    position: {
      lat: 48.794412,
      lng: -100.831257
    },
    stateName: "North Dakota"
  },
  {
    id: "017",
    state: "38",
    name: "Cass County",
    position: {
      lat: 46.927003,
      lng: -97.252375
    },
    stateName: "North Dakota"
  },
  {
    id: "027",
    state: "38",
    name: "Eddy County",
    position: {
      lat: 47.723436,
      lng: -98.900475
    },
    stateName: "North Dakota"
  },
  {
    id: "029",
    state: "38",
    name: "Emmons County",
    position: {
      lat: 46.284255,
      lng: -100.237842
    },
    stateName: "North Dakota"
  },
  {
    id: "037",
    state: "38",
    name: "Grant County",
    position: {
      lat: 46.357827,
      lng: -101.639049
    },
    stateName: "North Dakota"
  },
  {
    id: "039",
    state: "38",
    name: "Griggs County",
    position: {
      lat: 47.463463,
      lng: -98.232444
    },
    stateName: "North Dakota"
  },
  {
    id: "043",
    state: "38",
    name: "Kidder County",
    position: {
      lat: 46.935385,
      lng: -99.742727
    },
    stateName: "North Dakota"
  },
  {
    id: "047",
    state: "38",
    name: "Logan County",
    position: {
      lat: 46.469278,
      lng: -99.504585
    },
    stateName: "North Dakota"
  },
  {
    id: "053",
    state: "38",
    name: "Mckenzie County",
    position: {
      lat: 47.742475,
      lng: -103.403215
    },
    stateName: "North Dakota"
  },
  {
    id: "059",
    state: "38",
    name: "Morton County",
    position: {
      lat: 46.713789,
      lng: -101.279743
    },
    stateName: "North Dakota"
  },
  {
    id: "063",
    state: "38",
    name: "Nelson County",
    position: {
      lat: 47.918667,
      lng: -98.204428
    },
    stateName: "North Dakota"
  },
  {
    id: "001",
    state: "47",
    name: "Anderson County",
    position: {
      lat: 36.116731,
      lng: -84.195418
    },
    stateName: "Tennessee"
  },
  {
    id: "003",
    state: "47",
    name: "Bedford County",
    position: {
      lat: 35.51366,
      lng: -86.458294
    },
    stateName: "Tennessee"
  },
  {
    id: "065",
    state: "40",
    name: "Jackson County",
    position: {
      lat: 34.593949,
      lng: -99.41221
    },
    stateName: "Oklahoma"
  },
  {
    id: "071",
    state: "40",
    name: "Kay County",
    position: {
      lat: 36.814842,
      lng: -97.143755
    },
    stateName: "Oklahoma"
  },
  {
    id: "083",
    state: "40",
    name: "Logan County",
    position: {
      lat: 35.914151,
      lng: -97.450764
    },
    stateName: "Oklahoma"
  },
  {
    id: "085",
    state: "40",
    name: "Love County",
    position: {
      lat: 33.957775,
      lng: -97.245124
    },
    stateName: "Oklahoma"
  },
  {
    id: "091",
    state: "40",
    name: "Mcintosh County",
    position: {
      lat: 35.369092,
      lng: -95.671764
    },
    stateName: "Oklahoma"
  },
  {
    id: "099",
    state: "40",
    name: "Murray County",
    position: {
      lat: 34.485766,
      lng: -97.071557
    },
    stateName: "Oklahoma"
  },
  {
    id: "101",
    state: "40",
    name: "Muskogee County",
    position: {
      lat: 35.617551,
      lng: -95.383911
    },
    stateName: "Oklahoma"
  },
  {
    id: "107",
    state: "40",
    name: "Okfuskee County",
    position: {
      lat: 35.466804,
      lng: -96.327762
    },
    stateName: "Oklahoma"
  },
  {
    id: "111",
    state: "40",
    name: "Okmulgee County",
    position: {
      lat: 35.646879,
      lng: -95.96458
    },
    stateName: "Oklahoma"
  },
  {
    id: "117",
    state: "40",
    name: "Pawnee County",
    position: {
      lat: 36.313704,
      lng: -96.696669
    },
    stateName: "Oklahoma"
  },
  {
    id: "119",
    state: "40",
    name: "Payne County",
    position: {
      lat: 36.079225,
      lng: -96.975255
    },
    stateName: "Oklahoma"
  },
  {
    id: "125",
    state: "40",
    name: "Pottawatomie County",
    position: {
      lat: 35.211393,
      lng: -96.957007
    },
    stateName: "Oklahoma"
  },
  {
    id: "021",
    state: "25",
    name: "Norfolk County",
    position: {
      lat: 42.169703,
      lng: -71.179875
    },
    stateName: "Massachusetts"
  },
  {
    id: "023",
    state: "25",
    name: "Plymouth County",
    position: {
      lat: 41.987196,
      lng: -70.741942
    },
    stateName: "Massachusetts"
  },
  {
    id: "003",
    state: "26",
    name: "Alger County",
    position: {
      lat: 47.080077,
      lng: -86.564108
    },
    stateName: "Michigan"
  },
  {
    id: "005",
    state: "26",
    name: "Allegan County",
    position: {
      lat: 42.595788,
      lng: -86.634745
    },
    stateName: "Michigan"
  },
  {
    id: "007",
    state: "26",
    name: "Alpena County",
    position: {
      lat: 44.894954,
      lng: -83.426574
    },
    stateName: "Michigan"
  },
  {
    id: "063",
    state: "30",
    name: "Missoula County",
    position: {
      lat: 47.027263,
      lng: -113.892681
    },
    stateName: "Montana"
  },
  {
    id: "065",
    state: "30",
    name: "Musselshell County",
    position: {
      lat: 46.505665,
      lng: -108.436982
    },
    stateName: "Montana"
  },
  {
    id: "071",
    state: "30",
    name: "Phillips County",
    position: {
      lat: 48.250155,
      lng: -107.928833
    },
    stateName: "Montana"
  },
  {
    id: "077",
    state: "30",
    name: "Powell County",
    position: {
      lat: 46.844225,
      lng: -112.9311
    },
    stateName: "Montana"
  },
  {
    id: "083",
    state: "30",
    name: "Richland County",
    position: {
      lat: 47.785513,
      lng: -104.563387
    },
    stateName: "Montana"
  },
  {
    id: "087",
    state: "30",
    name: "Rosebud County",
    position: {
      lat: 46.161734,
      lng: -106.701953
    },
    stateName: "Montana"
  },
  {
    id: "091",
    state: "30",
    name: "Sheridan County",
    position: {
      lat: 48.705523,
      lng: -104.533913
    },
    stateName: "Montana"
  },
  {
    id: "097",
    state: "30",
    name: "Sweet Grass County",
    position: {
      lat: 45.81306,
      lng: -109.941312
    },
    stateName: "Montana"
  },
  {
    id: "101",
    state: "30",
    name: "Toole County",
    position: {
      lat: 48.645052,
      lng: -111.733493
    },
    stateName: "Montana"
  },
  {
    id: "065",
    state: "38",
    name: "Oliver County",
    position: {
      lat: 47.118079,
      lng: -101.33142
    },
    stateName: "North Dakota"
  },
  {
    id: "067",
    state: "38",
    name: "Pembina County",
    position: {
      lat: 48.766896,
      lng: -97.545405
    },
    stateName: "North Dakota"
  },
  {
    id: "071",
    state: "38",
    name: "Ramsey County",
    position: {
      lat: 48.265551,
      lng: -98.737329
    },
    stateName: "North Dakota"
  },
  {
    id: "075",
    state: "38",
    name: "Renville County",
    position: {
      lat: 48.712782,
      lng: -101.658152
    },
    stateName: "North Dakota"
  },
  {
    id: "081",
    state: "38",
    name: "Sargent County",
    position: {
      lat: 46.108206,
      lng: -97.63003
    },
    stateName: "North Dakota"
  },
  {
    id: "089",
    state: "38",
    name: "Stark County",
    position: {
      lat: 46.817031,
      lng: -102.662026
    },
    stateName: "North Dakota"
  },
  {
    id: "093",
    state: "38",
    name: "Stutsman County",
    position: {
      lat: 46.971103,
      lng: -98.957592
    },
    stateName: "North Dakota"
  },
  {
    id: "097",
    state: "38",
    name: "Traill County",
    position: {
      lat: 47.446215,
      lng: -97.164754
    },
    stateName: "North Dakota"
  },
  {
    id: "101",
    state: "38",
    name: "Ward County",
    position: {
      lat: 48.216686,
      lng: -101.540537
    },
    stateName: "North Dakota"
  },
  {
    id: "001",
    state: "39",
    name: "Adams County",
    position: {
      lat: 38.841681,
      lng: -83.474173
    },
    stateName: "Ohio"
  },
  {
    id: "005",
    state: "39",
    name: "Ashland County",
    position: {
      lat: 40.843274,
      lng: -82.270121
    },
    stateName: "Ohio"
  },
  {
    id: "011",
    state: "39",
    name: "Auglaize County",
    position: {
      lat: 40.561309,
      lng: -84.224018
    },
    stateName: "Ohio"
  },
  {
    id: "013",
    state: "39",
    name: "Belmont County",
    position: {
      lat: 40.017682,
      lng: -80.967727
    },
    stateName: "Ohio"
  },
  {
    id: "017",
    state: "39",
    name: "Butler County",
    position: {
      lat: 39.439915,
      lng: -84.565397
    },
    stateName: "Ohio"
  },
  {
    id: "019",
    state: "39",
    name: "Carroll County",
    position: {
      lat: 40.579884,
      lng: -81.090787
    },
    stateName: "Ohio"
  },
  {
    id: "003",
    state: "42",
    name: "Allegheny County",
    position: {
      lat: 40.46892,
      lng: -79.98092
    },
    stateName: "Pennsylvania"
  },
  {
    id: "007",
    state: "42",
    name: "Beaver County",
    position: {
      lat: 40.68414,
      lng: -80.350721
    },
    stateName: "Pennsylvania"
  },
  {
    id: "361",
    state: "48",
    name: "Orange County",
    position: {
      lat: 30.120918,
      lng: -93.893358
    },
    stateName: "Texas"
  },
  {
    id: "369",
    state: "48",
    name: "Parmer County",
    position: {
      lat: 34.532163,
      lng: -102.784853
    },
    stateName: "Texas"
  },
  {
    id: "383",
    state: "48",
    name: "Reagan County",
    position: {
      lat: 31.372895,
      lng: -101.513901
    },
    stateName: "Texas"
  },
  {
    id: "385",
    state: "48",
    name: "Real County",
    position: {
      lat: 29.82303,
      lng: -99.805303
    },
    stateName: "Texas"
  },
  {
    id: "387",
    state: "48",
    name: "Red River County",
    position: {
      lat: 33.619626,
      lng: -95.048429
    },
    stateName: "Texas"
  },
  {
    id: "395",
    state: "48",
    name: "Robertson County",
    position: {
      lat: 31.025481,
      lng: -96.514941
    },
    stateName: "Texas"
  },
  {
    id: "399",
    state: "48",
    name: "Runnels County",
    position: {
      lat: 31.833311,
      lng: -99.967856
    },
    stateName: "Texas"
  },
  {
    id: "403",
    state: "48",
    name: "Sabine County",
    position: {
      lat: 31.3433,
      lng: -93.851913
    },
    stateName: "Texas"
  },
  {
    id: "415",
    state: "48",
    name: "Scurry County",
    position: {
      lat: 32.744462,
      lng: -100.913399
    },
    stateName: "Texas"
  },
  {
    id: "417",
    state: "48",
    name: "Shackelford County",
    position: {
      lat: 32.743788,
      lng: -99.347045
    },
    stateName: "Texas"
  },
  {
    id: "419",
    state: "48",
    name: "Shelby County",
    position: {
      lat: 31.790137,
      lng: -94.142565
    },
    stateName: "Texas"
  },
  {
    id: "423",
    state: "48",
    name: "Smith County",
    position: {
      lat: 32.377093,
      lng: -95.26963
    },
    stateName: "Texas"
  },
  {
    id: "427",
    state: "48",
    name: "Starr County",
    position: {
      lat: 26.546335,
      lng: -98.715803
    },
    stateName: "Texas"
  },
  {
    id: "431",
    state: "48",
    name: "Sterling County",
    position: {
      lat: 31.835774,
      lng: -101.054911
    },
    stateName: "Texas"
  },
  {
    id: "017",
    state: "26",
    name: "Bay County",
    position: {
      lat: 43.699711,
      lng: -83.978701
    },
    stateName: "Michigan"
  },
  {
    id: "019",
    state: "26",
    name: "Benzie County",
    position: {
      lat: 44.648621,
      lng: -86.494317
    },
    stateName: "Michigan"
  },
  {
    id: "021",
    state: "26",
    name: "Berrien County",
    position: {
      lat: 41.792639,
      lng: -86.741822
    },
    stateName: "Michigan"
  },
  {
    id: "027",
    state: "26",
    name: "Cass County",
    position: {
      lat: 41.91624,
      lng: -85.999457
    },
    stateName: "Michigan"
  },
  {
    id: "031",
    state: "26",
    name: "Cheboygan County",
    position: {
      lat: 45.47612,
      lng: -84.495271
    },
    stateName: "Michigan"
  },
  {
    id: "033",
    state: "26",
    name: "Chippewa County",
    position: {
      lat: 46.321819,
      lng: -84.52063
    },
    stateName: "Michigan"
  },
  {
    id: "109",
    state: "30",
    name: "Wibaux County",
    position: {
      lat: 46.962866,
      lng: -104.274466
    },
    stateName: "Montana"
  },
  {
    id: "007",
    state: "31",
    name: "Banner County",
    position: {
      lat: 41.53975,
      lng: -103.726263
    },
    stateName: "Nebraska"
  },
  {
    id: "011",
    state: "31",
    name: "Boone County",
    position: {
      lat: 41.7054,
      lng: -98.066794
    },
    stateName: "Nebraska"
  },
  {
    id: "013",
    state: "31",
    name: "Box Butte County",
    position: {
      lat: 42.215665,
      lng: -103.087821
    },
    stateName: "Nebraska"
  },
  {
    id: "019",
    state: "31",
    name: "Buffalo County",
    position: {
      lat: 40.855226,
      lng: -99.074983
    },
    stateName: "Nebraska"
  },
  {
    id: "025",
    state: "31",
    name: "Cass County",
    position: {
      lat: 40.909878,
      lng: -96.140609
    },
    stateName: "Nebraska"
  },
  {
    id: "029",
    state: "31",
    name: "Chase County",
    position: {
      lat: 40.53039,
      lng: -101.694192
    },
    stateName: "Nebraska"
  },
  {
    id: "033",
    state: "31",
    name: "Cheyenne County",
    position: {
      lat: 41.214236,
      lng: -103.011929
    },
    stateName: "Nebraska"
  },
  {
    id: "039",
    state: "31",
    name: "Cuming County",
    position: {
      lat: 41.915865,
      lng: -96.788517
    },
    stateName: "Nebraska"
  },
  {
    id: "045",
    state: "31",
    name: "Dawes County",
    position: {
      lat: 42.711214,
      lng: -103.134872
    },
    stateName: "Nebraska"
  },
  {
    id: "051",
    state: "31",
    name: "Dixon County",
    position: {
      lat: 42.48528,
      lng: -96.855862
    },
    stateName: "Nebraska"
  },
  {
    id: "057",
    state: "31",
    name: "Dundy County",
    position: {
      lat: 40.180165,
      lng: -101.681133
    },
    stateName: "Nebraska"
  },
  {
    id: "071",
    state: "31",
    name: "Garfield County",
    position: {
      lat: 41.905572,
      lng: -98.922998
    },
    stateName: "Nebraska"
  },
  {
    id: "075",
    state: "31",
    name: "Grant County",
    position: {
      lat: 41.915867,
      lng: -101.756875
    },
    stateName: "Nebraska"
  },
  {
    id: "023",
    state: "39",
    name: "Clark County",
    position: {
      lat: 39.917032,
      lng: -83.783676
    },
    stateName: "Ohio"
  },
  {
    id: "025",
    state: "39",
    name: "Clermont County",
    position: {
      lat: 39.052084,
      lng: -84.149614
    },
    stateName: "Ohio"
  },
  {
    id: "029",
    state: "39",
    name: "Columbiana County",
    position: {
      lat: 40.768462,
      lng: -80.777231
    },
    stateName: "Ohio"
  },
  {
    id: "033",
    state: "39",
    name: "Crawford County",
    position: {
      lat: 40.848508,
      lng: -82.924771
    },
    stateName: "Ohio"
  },
  {
    id: "037",
    state: "39",
    name: "Darke County",
    position: {
      lat: 40.132176,
      lng: -84.620438
    },
    stateName: "Ohio"
  },
  {
    id: "041",
    state: "39",
    name: "Delaware County",
    position: {
      lat: 40.278941,
      lng: -83.007462
    },
    stateName: "Ohio"
  },
  {
    id: "043",
    state: "39",
    name: "Erie County",
    position: {
      lat: 41.554006,
      lng: -82.525897
    },
    stateName: "Ohio"
  },
  {
    id: "049",
    state: "39",
    name: "Franklin County",
    position: {
      lat: 39.969447,
      lng: -83.008258
    },
    stateName: "Ohio"
  },
  {
    id: "019",
    state: "35",
    name: "Guadalupe County",
    position: {
      lat: 34.869782,
      lng: -104.784968
    },
    stateName: "New Mexico"
  },
  {
    id: "021",
    state: "35",
    name: "Harding County",
    position: {
      lat: 35.863152,
      lng: -103.829931
    },
    stateName: "New Mexico"
  },
  {
    id: "025",
    state: "35",
    name: "Lea County",
    position: {
      lat: 32.795687,
      lng: -103.413271
    },
    stateName: "New Mexico"
  },
  {
    id: "033",
    state: "35",
    name: "Mora County",
    position: {
      lat: 35.982841,
      lng: -104.921898
    },
    stateName: "New Mexico"
  },
  {
    id: "039",
    state: "35",
    name: "Rio Arriba County",
    position: {
      lat: 36.509669,
      lng: -106.693983
    },
    stateName: "New Mexico"
  },
  {
    id: "143",
    state: "47",
    name: "Rhea County",
    position: {
      lat: 35.600587,
      lng: -84.949552
    },
    stateName: "Tennessee"
  },
  {
    id: "147",
    state: "47",
    name: "Robertson County",
    position: {
      lat: 36.52753,
      lng: -86.869377
    },
    stateName: "Tennessee"
  },
  {
    id: "149",
    state: "47",
    name: "Rutherford County",
    position: {
      lat: 35.843369,
      lng: -86.417213
    },
    stateName: "Tennessee"
  },
  {
    id: "155",
    state: "47",
    name: "Sevier County",
    position: {
      lat: 35.791284,
      lng: -83.521955
    },
    stateName: "Tennessee"
  },
  {
    id: "161",
    state: "47",
    name: "Stewart County",
    position: {
      lat: 36.511756,
      lng: -87.851548
    },
    stateName: "Tennessee"
  },
  {
    id: "167",
    state: "47",
    name: "Tipton County",
    position: {
      lat: 35.500297,
      lng: -89.763708
    },
    stateName: "Tennessee"
  },
  {
    id: "175",
    state: "47",
    name: "Van Buren County",
    position: {
      lat: 35.699245,
      lng: -85.458411
    },
    stateName: "Tennessee"
  },
  {
    id: "177",
    state: "47",
    name: "Warren County",
    position: {
      lat: 35.678282,
      lng: -85.777363
    },
    stateName: "Tennessee"
  },
  {
    id: "055",
    state: "45",
    name: "Kershaw County",
    position: {
      lat: 34.338356,
      lng: -80.590885
    },
    stateName: "South Carolina"
  },
  {
    id: "061",
    state: "45",
    name: "Lee County",
    position: {
      lat: 34.15864,
      lng: -80.251209
    },
    stateName: "South Carolina"
  },
  {
    id: "065",
    state: "45",
    name: "Mccormick County",
    position: {
      lat: 33.897605,
      lng: -82.316192
    },
    stateName: "South Carolina"
  },
  {
    id: "071",
    state: "45",
    name: "Newberry County",
    position: {
      lat: 34.28973,
      lng: -81.600053
    },
    stateName: "South Carolina"
  },
  {
    id: "073",
    state: "45",
    name: "Oconee County",
    position: {
      lat: 34.748759,
      lng: -83.061522
    },
    stateName: "South Carolina"
  },
  {
    id: "041",
    state: "26",
    name: "Delta County",
    position: {
      lat: 45.805101,
      lng: -86.901373
    },
    stateName: "Michigan"
  },
  {
    id: "043",
    state: "26",
    name: "Dickinson County",
    position: {
      lat: 46.012823,
      lng: -87.866119
    },
    stateName: "Michigan"
  },
  {
    id: "047",
    state: "26",
    name: "Emmet County",
    position: {
      lat: 45.590094,
      lng: -84.986822
    },
    stateName: "Michigan"
  },
  {
    id: "051",
    state: "26",
    name: "Gladwin County",
    position: {
      lat: 43.98975,
      lng: -84.389816
    },
    stateName: "Michigan"
  },
  {
    id: "065",
    state: "26",
    name: "Ingham County",
    position: {
      lat: 42.603534,
      lng: -84.373811
    },
    stateName: "Michigan"
  },
  {
    id: "069",
    state: "26",
    name: "Iosco County",
    position: {
      lat: 44.329482,
      lng: -82.849447
    },
    stateName: "Michigan"
  },
  {
    id: "131",
    state: "17",
    name: "Mercer County",
    position: {
      lat: 41.204791,
      lng: -90.741433
    },
    stateName: "Illinois"
  },
  {
    id: "191",
    state: "13",
    name: "Mc Intosh County",
    position: {
      lat: 31.482978,
      lng: -81.370199
    },
    stateName: "Georgia"
  },
  {
    id: "079",
    state: "31",
    name: "Hall County",
    position: {
      lat: 40.866023,
      lng: -98.498004
    },
    stateName: "Nebraska"
  },
  {
    id: "081",
    state: "31",
    name: "Hamilton County",
    position: {
      lat: 40.877145,
      lng: -98.021943
    },
    stateName: "Nebraska"
  },
  {
    id: "083",
    state: "31",
    name: "Harlan County",
    position: {
      lat: 40.178766,
      lng: -99.403418
    },
    stateName: "Nebraska"
  },
  {
    id: "087",
    state: "31",
    name: "Hitchcock County",
    position: {
      lat: 40.176896,
      lng: -101.044217
    },
    stateName: "Nebraska"
  },
  {
    id: "099",
    state: "31",
    name: "Kearney County",
    position: {
      lat: 40.506269,
      lng: -98.948461
    },
    stateName: "Nebraska"
  },
  {
    id: "101",
    state: "31",
    name: "Keith County",
    position: {
      lat: 41.194404,
      lng: -101.644474
    },
    stateName: "Nebraska"
  },
  {
    id: "105",
    state: "31",
    name: "Kimball County",
    position: {
      lat: 41.199155,
      lng: -103.711105
    },
    stateName: "Nebraska"
  },
  {
    id: "109",
    state: "31",
    name: "Lancaster County",
    position: {
      lat: 40.783547,
      lng: -96.688658
    },
    stateName: "Nebraska"
  },
  {
    id: "115",
    state: "31",
    name: "Loup County",
    position: {
      lat: 41.897714,
      lng: -99.509764
    },
    stateName: "Nebraska"
  },
  {
    id: "119",
    state: "31",
    name: "Madison County",
    position: {
      lat: 41.909929,
      lng: -97.606856
    },
    stateName: "Nebraska"
  },
  {
    id: "091",
    state: "28",
    name: "Marion County",
    position: {
      lat: 31.230126,
      lng: -89.821736
    },
    stateName: "Mississippi"
  },
  {
    id: "097",
    state: "28",
    name: "Montgomery County",
    position: {
      lat: 33.500714,
      lng: -89.639625
    },
    stateName: "Mississippi"
  },
  {
    id: "101",
    state: "28",
    name: "Newton County",
    position: {
      lat: 32.40197,
      lng: -89.118412
    },
    stateName: "Mississippi"
  },
  {
    id: "109",
    state: "28",
    name: "Pearl River County",
    position: {
      lat: 30.773791,
      lng: -89.586832
    },
    stateName: "Mississippi"
  },
  {
    id: "119",
    state: "28",
    name: "Quitman County",
    position: {
      lat: 34.25264,
      lng: -90.290525
    },
    stateName: "Mississippi"
  },
  {
    id: "121",
    state: "28",
    name: "Rankin County",
    position: {
      lat: 32.262057,
      lng: -89.946552
    },
    stateName: "Mississippi"
  },
  {
    id: "129",
    state: "28",
    name: "Smith County",
    position: {
      lat: 32.019034,
      lng: -89.494973
    },
    stateName: "Mississippi"
  },
  {
    id: "041",
    state: "35",
    name: "Roosevelt County",
    position: {
      lat: 34.021457,
      lng: -103.482725
    },
    stateName: "New Mexico"
  },
  {
    id: "043",
    state: "35",
    name: "Sandoval County",
    position: {
      lat: 35.685073,
      lng: -106.882618
    },
    stateName: "New Mexico"
  },
  {
    id: "049",
    state: "35",
    name: "Santa Fe County",
    position: {
      lat: 35.513722,
      lng: -105.966441
    },
    stateName: "New Mexico"
  },
  {
    id: "057",
    state: "35",
    name: "Torrance County",
    position: {
      lat: 34.634644,
      lng: -105.846836
    },
    stateName: "New Mexico"
  },
  {
    id: "061",
    state: "35",
    name: "Valencia County",
    position: {
      lat: 34.71684,
      lng: -106.806582
    },
    stateName: "New Mexico"
  },
  {
    id: "007",
    state: "36",
    name: "Broome County",
    position: {
      lat: 42.161977,
      lng: -75.830291
    },
    stateName: "New York"
  },
  {
    id: "011",
    state: "36",
    name: "Cayuga County",
    position: {
      lat: 43.008546,
      lng: -76.574587
    },
    stateName: "New York"
  },
  {
    id: "013",
    state: "36",
    name: "Chautauqua County",
    position: {
      lat: 42.304216,
      lng: -79.407595
    },
    stateName: "New York"
  },
  {
    id: "019",
    state: "36",
    name: "Clinton County",
    position: {
      lat: 44.75271,
      lng: -73.705648
    },
    stateName: "New York"
  },
  {
    id: "027",
    state: "36",
    name: "Dutchess County",
    position: {
      lat: 41.755009,
      lng: -73.739951
    },
    stateName: "New York"
  },
  {
    id: "033",
    state: "36",
    name: "Franklin County",
    position: {
      lat: 44.594374,
      lng: -74.31067
    },
    stateName: "New York"
  },
  {
    id: "035",
    state: "36",
    name: "Fulton County",
    position: {
      lat: 43.115609,
      lng: -74.423678
    },
    stateName: "New York"
  },
  {
    id: "039",
    state: "36",
    name: "Greene County",
    position: {
      lat: 42.286951,
      lng: -74.149495
    },
    stateName: "New York"
  },
  {
    id: "131",
    state: "40",
    name: "Rogers County",
    position: {
      lat: 36.378082,
      lng: -95.601337
    },
    stateName: "Oklahoma"
  },
  {
    id: "135",
    state: "40",
    name: "Sequoyah County",
    position: {
      lat: 35.502435,
      lng: -94.750757
    },
    stateName: "Oklahoma"
  },
  {
    id: "137",
    state: "40",
    name: "Stephens County",
    position: {
      lat: 34.481361,
      lng: -97.855607
    },
    stateName: "Oklahoma"
  },
  {
    id: "143",
    state: "40",
    name: "Tulsa County",
    position: {
      lat: 36.120121,
      lng: -95.941731
    },
    stateName: "Oklahoma"
  },
  {
    id: "145",
    state: "40",
    name: "Wagoner County",
    position: {
      lat: 35.963479,
      lng: -95.5141
    },
    stateName: "Oklahoma"
  },
  {
    id: "149",
    state: "40",
    name: "Washita County",
    position: {
      lat: 35.290177,
      lng: -98.991962
    },
    stateName: "Oklahoma"
  },
  {
    id: "003",
    state: "41",
    name: "Benton County",
    position: {
      lat: 44.490623,
      lng: -123.426317
    },
    stateName: "Oregon"
  },
  {
    id: "005",
    state: "41",
    name: "Clackamas County",
    position: {
      lat: 45.160493,
      lng: -122.195127
    },
    stateName: "Oregon"
  },
  {
    id: "011",
    state: "41",
    name: "Coos County",
    position: {
      lat: 43.184193,
      lng: -124.092557
    },
    stateName: "Oregon"
  },
  {
    id: "013",
    state: "41",
    name: "Crook County",
    position: {
      lat: 44.163054,
      lng: -120.371585
    },
    stateName: "Oregon"
  },
  {
    id: "021",
    state: "41",
    name: "Gilliam County",
    position: {
      lat: 45.381666,
      lng: -120.211851
    },
    stateName: "Oregon"
  },
  {
    id: "027",
    state: "41",
    name: "Hood River County",
    position: {
      lat: 45.511775,
      lng: -121.655976
    },
    stateName: "Oregon"
  },
  {
    id: "075",
    state: "26",
    name: "Jackson County",
    position: {
      lat: 42.248474,
      lng: -84.420868
    },
    stateName: "Michigan"
  },
  {
    id: "079",
    state: "26",
    name: "Kalkaska County",
    position: {
      lat: 44.678881,
      lng: -85.088992
    },
    stateName: "Michigan"
  },
  {
    id: "081",
    state: "26",
    name: "Kent County",
    position: {
      lat: 43.032497,
      lng: -85.547446
    },
    stateName: "Michigan"
  },
  {
    id: "087",
    state: "26",
    name: "Lapeer County",
    position: {
      lat: 43.088633,
      lng: -83.224325
    },
    stateName: "Michigan"
  },
  {
    id: "093",
    state: "26",
    name: "Livingston County",
    position: {
      lat: 42.602532,
      lng: -83.911718
    },
    stateName: "Michigan"
  },
  {
    id: "095",
    state: "26",
    name: "Luce County",
    position: {
      lat: 46.940602,
      lng: -85.582368
    },
    stateName: "Michigan"
  },
  {
    id: "099",
    state: "26",
    name: "Macomb County",
    position: {
      lat: 42.671467,
      lng: -82.910869
    },
    stateName: "Michigan"
  },
  {
    id: "101",
    state: "26",
    name: "Manistee County",
    position: {
      lat: 44.350385,
      lng: -86.602967
    },
    stateName: "Michigan"
  },
  {
    id: "115",
    state: "26",
    name: "Monroe County",
    position: {
      lat: 41.916097,
      lng: -83.487106
    },
    stateName: "Michigan"
  },
  {
    id: "137",
    state: "28",
    name: "Tate County",
    position: {
      lat: 34.649553,
      lng: -89.943105
    },
    stateName: "Mississippi"
  },
  {
    id: "141",
    state: "28",
    name: "Tishomingo County",
    position: {
      lat: 34.737699,
      lng: -88.236067
    },
    stateName: "Mississippi"
  },
  {
    id: "143",
    state: "28",
    name: "Tunica County",
    position: {
      lat: 34.652201,
      lng: -90.37177
    },
    stateName: "Mississippi"
  },
  {
    id: "153",
    state: "28",
    name: "Wayne County",
    position: {
      lat: 31.64476,
      lng: -88.682057
    },
    stateName: "Mississippi"
  },
  {
    id: "155",
    state: "28",
    name: "Webster County",
    position: {
      lat: 33.612048,
      lng: -89.283836
    },
    stateName: "Mississippi"
  },
  {
    id: "157",
    state: "28",
    name: "Wilkinson County",
    position: {
      lat: 31.160249,
      lng: -91.325567
    },
    stateName: "Mississippi"
  },
  {
    id: "163",
    state: "28",
    name: "Yazoo County",
    position: {
      lat: 32.765675,
      lng: -90.387928
    },
    stateName: "Mississippi"
  },
  {
    id: "007",
    state: "29",
    name: "Audrain County",
    position: {
      lat: 39.21448,
      lng: -91.843415
    },
    stateName: "Missouri"
  },
  {
    id: "017",
    state: "29",
    name: "Bollinger County",
    position: {
      lat: 37.318349,
      lng: -90.024601
    },
    stateName: "Missouri"
  },
  {
    id: "023",
    state: "29",
    name: "Butler County",
    position: {
      lat: 36.71518,
      lng: -90.40313
    },
    stateName: "Missouri"
  },
  {
    id: "029",
    state: "29",
    name: "Camden County",
    position: {
      lat: 38.031995,
      lng: -92.765138
    },
    stateName: "Missouri"
  },
  {
    id: "035",
    state: "29",
    name: "Carter County",
    position: {
      lat: 36.944783,
      lng: -90.945626
    },
    stateName: "Missouri"
  },
  {
    id: "041",
    state: "29",
    name: "Chariton County",
    position: {
      lat: 39.517969,
      lng: -92.961621
    },
    stateName: "Missouri"
  },
  {
    id: "045",
    state: "29",
    name: "Clark County",
    position: {
      lat: 40.407275,
      lng: -91.729472
    },
    stateName: "Missouri"
  },
  {
    id: "041",
    state: "36",
    name: "Hamilton County",
    position: {
      lat: 43.657879,
      lng: -74.502456
    },
    stateName: "New York"
  },
  {
    id: "045",
    state: "36",
    name: "Jefferson County",
    position: {
      lat: 43.995371,
      lng: -76.053522
    },
    stateName: "New York"
  },
  {
    id: "051",
    state: "36",
    name: "Livingston County",
    position: {
      lat: 42.727485,
      lng: -77.76978
    },
    stateName: "New York"
  },
  {
    id: "055",
    state: "36",
    name: "Monroe County",
    position: {
      lat: 43.464475,
      lng: -77.664656
    },
    stateName: "New York"
  },
  {
    id: "059",
    state: "36",
    name: "Nassau County",
    position: {
      lat: 40.729687,
      lng: -73.589384
    },
    stateName: "New York"
  },
  {
    id: "063",
    state: "36",
    name: "Niagara County",
    position: {
      lat: 43.456731,
      lng: -78.792143
    },
    stateName: "New York"
  },
  {
    id: "071",
    state: "36",
    name: "Orange County",
    position: {
      lat: 41.40241,
      lng: -74.306252
    },
    stateName: "New York"
  },
  {
    id: "073",
    state: "36",
    name: "Orleans County",
    position: {
      lat: 43.502287,
      lng: -78.229726
    },
    stateName: "New York"
  },
  {
    id: "075",
    state: "36",
    name: "Oswego County",
    position: {
      lat: 43.461443,
      lng: -76.209258
    },
    stateName: "New York"
  },
  {
    id: "435",
    state: "48",
    name: "Sutton County",
    position: {
      lat: 30.517865,
      lng: -100.505395
    },
    stateName: "Texas"
  },
  {
    id: "437",
    state: "48",
    name: "Swisher County",
    position: {
      lat: 34.53046,
      lng: -101.732852
    },
    stateName: "Texas"
  },
  {
    id: "441",
    state: "48",
    name: "Taylor County",
    position: {
      lat: 32.295684,
      lng: -99.89322
    },
    stateName: "Texas"
  },
  {
    id: "447",
    state: "48",
    name: "Throckmorton County",
    position: {
      lat: 33.170712,
      lng: -99.206137
    },
    stateName: "Texas"
  },
  {
    id: "451",
    state: "48",
    name: "Tom Green County",
    position: {
      lat: 31.401583,
      lng: -100.461355
    },
    stateName: "Texas"
  },
  {
    id: "453",
    state: "48",
    name: "Travis County",
    position: {
      lat: 30.239513,
      lng: -97.69127
    },
    stateName: "Texas"
  },
  {
    id: "457",
    state: "48",
    name: "Tyler County",
    position: {
      lat: 30.769579,
      lng: -94.379449
    },
    stateName: "Texas"
  },
  {
    id: "467",
    state: "48",
    name: "Van Zandt County",
    position: {
      lat: 32.558948,
      lng: -95.836391
    },
    stateName: "Texas"
  },
  {
    id: "469",
    state: "48",
    name: "Victoria County",
    position: {
      lat: 28.79637,
      lng: -96.971198
    },
    stateName: "Texas"
  },
  {
    id: "477",
    state: "48",
    name: "Washington County",
    position: {
      lat: 30.215075,
      lng: -96.410272
    },
    stateName: "Texas"
  },
  {
    id: "483",
    state: "48",
    name: "Wheeler County",
    position: {
      lat: 35.392593,
      lng: -100.253107
    },
    stateName: "Texas"
  },
  {
    id: "485",
    state: "48",
    name: "Wichita County",
    position: {
      lat: 33.991103,
      lng: -98.716851
    },
    stateName: "Texas"
  },
  {
    id: "489",
    state: "48",
    name: "Willacy County",
    position: {
      lat: 26.481092,
      lng: -97.584224
    },
    stateName: "Texas"
  },
  {
    id: "499",
    state: "48",
    name: "Wood County",
    position: {
      lat: 32.783588,
      lng: -95.382166
    },
    stateName: "Texas"
  },
  {
    id: "001",
    state: "49",
    name: "Beaver County",
    position: {
      lat: 38.357535,
      lng: -113.238948
    },
    stateName: "Utah"
  },
  {
    id: "007",
    state: "49",
    name: "Carbon County",
    position: {
      lat: 39.67005,
      lng: -110.590357
    },
    stateName: "Utah"
  },
  {
    id: "071",
    state: "26",
    name: "Iron County",
    position: {
      lat: 46.170249,
      lng: -88.540409
    },
    stateName: "Michigan"
  },
  {
    id: "125",
    state: "26",
    name: "Oakland County",
    position: {
      lat: 42.660452,
      lng: -83.38421
    },
    stateName: "Michigan"
  },
  {
    id: "131",
    state: "26",
    name: "Ontonagon County",
    position: {
      lat: 47.216604,
      lng: -89.500461
    },
    stateName: "Michigan"
  },
  {
    id: "135",
    state: "26",
    name: "Oscoda County",
    position: {
      lat: 44.685121,
      lng: -84.124894
    },
    stateName: "Michigan"
  },
  {
    id: "145",
    state: "26",
    name: "Saginaw County",
    position: {
      lat: 43.328267,
      lng: -84.05541
    },
    stateName: "Michigan"
  },
  {
    id: "147",
    state: "26",
    name: "St. Clair County",
    position: {
      lat: 42.928804,
      lng: -82.668914
    },
    stateName: "Michigan"
  },
  {
    id: "157",
    state: "26",
    name: "Tuscola County",
    position: {
      lat: 43.487902,
      lng: -83.436618
    },
    stateName: "Michigan"
  },
  {
    id: "047",
    state: "29",
    name: "Clay County",
    position: {
      lat: 39.315551,
      lng: -94.421502
    },
    stateName: "Missouri"
  },
  {
    id: "051",
    state: "29",
    name: "Cole County",
    position: {
      lat: 38.506847,
      lng: -92.271404
    },
    stateName: "Missouri"
  },
  {
    id: "055",
    state: "29",
    name: "Crawford County",
    position: {
      lat: 37.966561,
      lng: -91.313933
    },
    stateName: "Missouri"
  },
  {
    id: "057",
    state: "29",
    name: "Dade County",
    position: {
      lat: 37.43235,
      lng: -93.854878
    },
    stateName: "Missouri"
  },
  {
    id: "061",
    state: "29",
    name: "Daviess County",
    position: {
      lat: 39.962839,
      lng: -93.970053
    },
    stateName: "Missouri"
  },
  {
    id: "067",
    state: "29",
    name: "Douglas County",
    position: {
      lat: 36.946517,
      lng: -92.515892
    },
    stateName: "Missouri"
  },
  {
    id: "073",
    state: "29",
    name: "Gasconade County",
    position: {
      lat: 38.441183,
      lng: -91.50578
    },
    stateName: "Missouri"
  },
  {
    id: "077",
    state: "29",
    name: "Greene County",
    position: {
      lat: 37.258196,
      lng: -93.340641
    },
    stateName: "Missouri"
  },
  {
    id: "079",
    state: "29",
    name: "Grundy County",
    position: {
      lat: 40.112541,
      lng: -93.565054
    },
    stateName: "Missouri"
  },
  {
    id: "087",
    state: "29",
    name: "Holt County",
    position: {
      lat: 40.095724,
      lng: -95.219072
    },
    stateName: "Missouri"
  },
  {
    id: "011",
    state: "37",
    name: "Avery County",
    position: {
      lat: 36.072894,
      lng: -81.920363
    },
    stateName: "North Carolina"
  },
  {
    id: "013",
    state: "37",
    name: "Beaufort County",
    position: {
      lat: 35.482313,
      lng: -76.842014
    },
    stateName: "North Carolina"
  },
  {
    id: "019",
    state: "37",
    name: "Brunswick County",
    position: {
      lat: 34.038708,
      lng: -78.227688
    },
    stateName: "North Carolina"
  },
  {
    id: "025",
    state: "37",
    name: "Cabarrus County",
    position: {
      lat: 35.387845,
      lng: -80.552868
    },
    stateName: "North Carolina"
  },
  {
    id: "223",
    state: "13",
    name: "Paulding County",
    position: {
      lat: 33.920903,
      lng: -84.866979
    },
    stateName: "Georgia"
  },
  {
    id: "081",
    state: "36",
    name: "Queens County",
    position: {
      lat: 40.658557,
      lng: -73.837929
    },
    stateName: "New York"
  },
  {
    id: "087",
    state: "36",
    name: "Rockland County",
    position: {
      lat: 41.154785,
      lng: -74.024772
    },
    stateName: "New York"
  },
  {
    id: "089",
    state: "36",
    name: "St. Lawrence County",
    position: {
      lat: 44.488113,
      lng: -75.074311
    },
    stateName: "New York"
  },
  {
    id: "093",
    state: "36",
    name: "Schenectady County",
    position: {
      lat: 42.817542,
      lng: -74.043583
    },
    stateName: "New York"
  },
  {
    id: "099",
    state: "36",
    name: "Seneca County",
    position: {
      lat: 42.782294,
      lng: -76.827088
    },
    stateName: "New York"
  },
  {
    id: "103",
    state: "36",
    name: "Suffolk County",
    position: {
      lat: 40.943554,
      lng: -72.692218
    },
    stateName: "New York"
  },
  {
    id: "107",
    state: "36",
    name: "Tioga County",
    position: {
      lat: 42.178057,
      lng: -76.29745
    },
    stateName: "New York"
  },
  {
    id: "117",
    state: "36",
    name: "Wayne County",
    position: {
      lat: 43.458758,
      lng: -77.063164
    },
    stateName: "New York"
  },
  {
    id: "017",
    state: "42",
    name: "Bucks County",
    position: {
      lat: 40.336887,
      lng: -75.10706
    },
    stateName: "Pennsylvania"
  },
  {
    id: "023",
    state: "42",
    name: "Cameron County",
    position: {
      lat: 41.438289,
      lng: -78.198315
    },
    stateName: "Pennsylvania"
  },
  {
    id: "079",
    state: "45",
    name: "Richland County",
    position: {
      lat: 34.029783,
      lng: -80.896566
    },
    stateName: "South Carolina"
  },
  {
    id: "083",
    state: "45",
    name: "Spartanburg County",
    position: {
      lat: 34.933239,
      lng: -81.991053
    },
    stateName: "South Carolina"
  },
  {
    id: "085",
    state: "45",
    name: "Sumter County",
    position: {
      lat: 33.916046,
      lng: -80.382472
    },
    stateName: "South Carolina"
  },
  {
    id: "089",
    state: "45",
    name: "Williamsburg County",
    position: {
      lat: 33.626463,
      lng: -79.716475
    },
    stateName: "South Carolina"
  },
  {
    id: "005",
    state: "46",
    name: "Beadle County",
    position: {
      lat: 44.418265,
      lng: -98.279422
    },
    stateName: "South Dakota"
  },
  {
    id: "011",
    state: "46",
    name: "Brookings County",
    position: {
      lat: 44.376675,
      lng: -96.797797
    },
    stateName: "South Dakota"
  },
  {
    id: "019",
    state: "46",
    name: "Butte County",
    position: {
      lat: 44.896435,
      lng: -103.501762
    },
    stateName: "South Dakota"
  },
  {
    id: "023",
    state: "46",
    name: "Charles Mix County",
    position: {
      lat: 43.206185,
      lng: -98.595143
    },
    stateName: "South Dakota"
  },
  {
    id: "033",
    state: "46",
    name: "Custer County",
    position: {
      lat: 43.684943,
      lng: -103.46225
    },
    stateName: "South Dakota"
  },
  {
    id: "039",
    state: "46",
    name: "Deuel County",
    position: {
      lat: 44.75629,
      lng: -96.690239
    },
    stateName: "South Dakota"
  },
  {
    id: "041",
    state: "46",
    name: "Dewey County",
    position: {
      lat: 45.150005,
      lng: -100.852218
    },
    stateName: "South Dakota"
  },
  {
    id: "045",
    state: "46",
    name: "Edmunds County",
    position: {
      lat: 45.41168,
      lng: -99.205362
    },
    stateName: "South Dakota"
  },
  {
    id: "051",
    state: "46",
    name: "Grant County",
    position: {
      lat: 45.172637,
      lng: -96.772261
    },
    stateName: "South Dakota"
  },
  {
    id: "059",
    state: "46",
    name: "Hand County",
    position: {
      lat: 44.546713,
      lng: -99.004575
    },
    stateName: "South Dakota"
  },
  {
    id: "159",
    state: "26",
    name: "Van Buren County",
    position: {
      lat: 42.283986,
      lng: -86.305697
    },
    stateName: "Michigan"
  },
  {
    id: "163",
    state: "26",
    name: "Wayne County",
    position: {
      lat: 42.284664,
      lng: -83.261953
    },
    stateName: "Michigan"
  },
  {
    id: "001",
    state: "27",
    name: "Aitkin County",
    position: {
      lat: 46.602446,
      lng: -93.41976
    },
    stateName: "Minnesota"
  },
  {
    id: "003",
    state: "27",
    name: "Anoka County",
    position: {
      lat: 45.27411,
      lng: -93.242723
    },
    stateName: "Minnesota"
  },
  {
    id: "011",
    state: "27",
    name: "Big Stone County",
    position: {
      lat: 45.419925,
      lng: -96.402226
    },
    stateName: "Minnesota"
  },
  {
    id: "017",
    state: "27",
    name: "Carlton County",
    position: {
      lat: 46.603818,
      lng: -92.671044
    },
    stateName: "Minnesota"
  },
  {
    id: "025",
    state: "27",
    name: "Chisago County",
    position: {
      lat: 45.505444,
      lng: -92.903849
    },
    stateName: "Minnesota"
  },
  {
    id: "035",
    state: "27",
    name: "Crow Wing County",
    position: {
      lat: 46.491114,
      lng: -94.071213
    },
    stateName: "Minnesota"
  },
  {
    id: "037",
    state: "27",
    name: "Dakota County",
    position: {
      lat: 44.670893,
      lng: -93.062481
    },
    stateName: "Minnesota"
  },
  {
    id: "041",
    state: "27",
    name: "Douglas County",
    position: {
      lat: 45.936429,
      lng: -95.46061
    },
    stateName: "Minnesota"
  },
  {
    id: "043",
    state: "27",
    name: "Faribault County",
    position: {
      lat: 43.676522,
      lng: -93.947234
    },
    stateName: "Minnesota"
  },
  {
    id: "045",
    state: "27",
    name: "Fillmore County",
    position: {
      lat: 43.67727,
      lng: -92.093681
    },
    stateName: "Minnesota"
  },
  {
    id: "053",
    state: "27",
    name: "Hennepin County",
    position: {
      lat: 45.006064,
      lng: -93.475185
    },
    stateName: "Minnesota"
  },
  {
    id: "059",
    state: "27",
    name: "Isanti County",
    position: {
      lat: 45.562431,
      lng: -93.296339
    },
    stateName: "Minnesota"
  },
  {
    id: "035",
    state: "37",
    name: "Catawba County",
    position: {
      lat: 35.663182,
      lng: -81.214151
    },
    stateName: "North Carolina"
  },
  {
    id: "037",
    state: "37",
    name: "Chatham County",
    position: {
      lat: 35.704994,
      lng: -79.251454
    },
    stateName: "North Carolina"
  },
  {
    id: "039",
    state: "37",
    name: "Cherokee County",
    position: {
      lat: 35.136233,
      lng: -84.061308
    },
    stateName: "North Carolina"
  },
  {
    id: "041",
    state: "37",
    name: "Chowan County",
    position: {
      lat: 36.127288,
      lng: -76.60207
    },
    stateName: "North Carolina"
  },
  {
    id: "047",
    state: "37",
    name: "Columbus County",
    position: {
      lat: 34.260471,
      lng: -78.636378
    },
    stateName: "North Carolina"
  },
  {
    id: "059",
    state: "37",
    name: "Davie County",
    position: {
      lat: 35.929356,
      lng: -80.542542
    },
    stateName: "North Carolina"
  },
  {
    id: "061",
    state: "37",
    name: "Duplin County",
    position: {
      lat: 34.934403,
      lng: -77.933543
    },
    stateName: "North Carolina"
  },
  {
    id: "065",
    state: "37",
    name: "Edgecombe County",
    position: {
      lat: 35.917055,
      lng: -77.602655
    },
    stateName: "North Carolina"
  },
  {
    id: "069",
    state: "37",
    name: "Franklin County",
    position: {
      lat: 36.088241,
      lng: -78.28309
    },
    stateName: "North Carolina"
  },
  {
    id: "071",
    state: "37",
    name: "Gaston County",
    position: {
      lat: 35.293344,
      lng: -81.177256
    },
    stateName: "North Carolina"
  },
  {
    id: "075",
    state: "37",
    name: "Graham County",
    position: {
      lat: 35.348111,
      lng: -83.830909
    },
    stateName: "North Carolina"
  },
  {
    id: "077",
    state: "37",
    name: "Granville County",
    position: {
      lat: 36.299884,
      lng: -78.657634
    },
    stateName: "North Carolina"
  },
  {
    id: "083",
    state: "37",
    name: "Halifax County",
    position: {
      lat: 36.251438,
      lng: -77.644842
    },
    stateName: "North Carolina"
  },
  {
    id: "087",
    state: "37",
    name: "Haywood County",
    position: {
      lat: 35.557097,
      lng: -82.972807
    },
    stateName: "North Carolina"
  },
  {
    id: "027",
    state: "42",
    name: "Centre County",
    position: {
      lat: 40.90916,
      lng: -77.84783
    },
    stateName: "Pennsylvania"
  },
  {
    id: "029",
    state: "42",
    name: "Chester County",
    position: {
      lat: 39.973965,
      lng: -75.749732
    },
    stateName: "Pennsylvania"
  },
  {
    id: "033",
    state: "42",
    name: "Clearfield County",
    position: {
      lat: 41.000249,
      lng: -78.473749
    },
    stateName: "Pennsylvania"
  },
  {
    id: "039",
    state: "42",
    name: "Crawford County",
    position: {
      lat: 41.68684,
      lng: -80.107811
    },
    stateName: "Pennsylvania"
  },
  {
    id: "045",
    state: "42",
    name: "Delaware County",
    position: {
      lat: 39.91667,
      lng: -75.398786
    },
    stateName: "Pennsylvania"
  },
  {
    id: "049",
    state: "42",
    name: "Erie County",
    position: {
      lat: 42.117952,
      lng: -80.096386
    },
    stateName: "Pennsylvania"
  },
  {
    id: "051",
    state: "42",
    name: "Fayette County",
    position: {
      lat: 39.914115,
      lng: -79.644586
    },
    stateName: "Pennsylvania"
  },
  {
    id: "055",
    state: "42",
    name: "Franklin County",
    position: {
      lat: 39.926686,
      lng: -77.724485
    },
    stateName: "Pennsylvania"
  },
  {
    id: "061",
    state: "42",
    name: "Huntingdon County",
    position: {
      lat: 40.422321,
      lng: -77.968584
    },
    stateName: "Pennsylvania"
  },
  {
    id: "067",
    state: "42",
    name: "Juniata County",
    position: {
      lat: 40.530673,
      lng: -77.400438
    },
    stateName: "Pennsylvania"
  },
  {
    id: "073",
    state: "42",
    name: "Lawrence County",
    position: {
      lat: 40.992735,
      lng: -80.334446
    },
    stateName: "Pennsylvania"
  },
  {
    id: "079",
    state: "42",
    name: "Luzerne County",
    position: {
      lat: 41.172787,
      lng: -75.976035
    },
    stateName: "Pennsylvania"
  },
  {
    id: "029",
    state: "41",
    name: "Jackson County",
    position: {
      lat: 42.411782,
      lng: -122.675797
    },
    stateName: "Oregon"
  },
  {
    id: "033",
    state: "41",
    name: "Josephine County",
    position: {
      lat: 42.385382,
      lng: -123.597245
    },
    stateName: "Oregon"
  },
  {
    id: "039",
    state: "41",
    name: "Lane County",
    position: {
      lat: 43.928276,
      lng: -122.897678
    },
    stateName: "Oregon"
  },
  {
    id: "043",
    state: "41",
    name: "Linn County",
    position: {
      lat: 44.494824,
      lng: -122.543756
    },
    stateName: "Oregon"
  },
  {
    id: "049",
    state: "41",
    name: "Morrow County",
    position: {
      lat: 45.425496,
      lng: -119.602311
    },
    stateName: "Oregon"
  },
  {
    id: "051",
    state: "41",
    name: "Multnomah County",
    position: {
      lat: 45.547693,
      lng: -122.417173
    },
    stateName: "Oregon"
  },
  {
    id: "059",
    state: "41",
    name: "Umatilla County",
    position: {
      lat: 45.5912,
      lng: -118.73388
    },
    stateName: "Oregon"
  },
  {
    id: "065",
    state: "41",
    name: "Wasco County",
    position: {
      lat: 45.164536,
      lng: -121.165069
    },
    stateName: "Oregon"
  },
  {
    id: "071",
    state: "41",
    name: "Yamhill County",
    position: {
      lat: 45.248138,
      lng: -123.316117
    },
    stateName: "Oregon"
  },
  {
    id: "063",
    state: "27",
    name: "Jackson County",
    position: {
      lat: 43.673025,
      lng: -95.149704
    },
    stateName: "Minnesota"
  },
  {
    id: "065",
    state: "27",
    name: "Kanabec County",
    position: {
      lat: 45.94776,
      lng: -93.297788
    },
    stateName: "Minnesota"
  },
  {
    id: "069",
    state: "27",
    name: "Kittson County",
    position: {
      lat: 48.77604,
      lng: -96.780349
    },
    stateName: "Minnesota"
  },
  {
    id: "105",
    state: "29",
    name: "Laclede County",
    position: {
      lat: 37.65969,
      lng: -92.594832
    },
    stateName: "Missouri"
  },
  {
    id: "111",
    state: "29",
    name: "Lewis County",
    position: {
      lat: 40.084559,
      lng: -91.728803
    },
    stateName: "Missouri"
  },
  {
    id: "119",
    state: "29",
    name: "Mc Donald County",
    position: {
      lat: 36.630218,
      lng: -94.343956
    },
    stateName: "Missouri"
  },
  {
    id: "125",
    state: "29",
    name: "Maries County",
    position: {
      lat: 38.162615,
      lng: -91.923601
    },
    stateName: "Missouri"
  },
  {
    id: "129",
    state: "29",
    name: "Mercer County",
    position: {
      lat: 40.421414,
      lng: -93.567631
    },
    stateName: "Missouri"
  },
  {
    id: "139",
    state: "29",
    name: "Montgomery County",
    position: {
      lat: 38.935162,
      lng: -91.465437
    },
    stateName: "Missouri"
  },
  {
    id: "141",
    state: "29",
    name: "Morgan County",
    position: {
      lat: 38.420807,
      lng: -92.874835
    },
    stateName: "Missouri"
  },
  {
    id: "147",
    state: "29",
    name: "Nodaway County",
    position: {
      lat: 40.360484,
      lng: -94.883281
    },
    stateName: "Missouri"
  },
  {
    id: "153",
    state: "29",
    name: "Ozark County",
    position: {
      lat: 36.649643,
      lng: -92.45858
    },
    stateName: "Missouri"
  },
  {
    id: "157",
    state: "29",
    name: "Perry County",
    position: {
      lat: 37.71113,
      lng: -89.802125
    },
    stateName: "Missouri"
  },
  {
    id: "161",
    state: "29",
    name: "Phelps County",
    position: {
      lat: 37.866326,
      lng: -91.790349
    },
    stateName: "Missouri"
  },
  {
    id: "167",
    state: "29",
    name: "Polk County",
    position: {
      lat: 37.616761,
      lng: -93.400817
    },
    stateName: "Missouri"
  },
  {
    id: "095",
    state: "37",
    name: "Hyde County",
    position: {
      lat: 35.408157,
      lng: -76.153687
    },
    stateName: "North Carolina"
  },
  {
    id: "099",
    state: "37",
    name: "Jackson County",
    position: {
      lat: 35.286463,
      lng: -83.130641
    },
    stateName: "North Carolina"
  },
  {
    id: "105",
    state: "37",
    name: "Lee County",
    position: {
      lat: 35.476075,
      lng: -79.17222
    },
    stateName: "North Carolina"
  },
  {
    id: "111",
    state: "37",
    name: "Mc Dowell County",
    position: {
      lat: 35.682232,
      lng: -82.048029
    },
    stateName: "North Carolina"
  },
  {
    id: "115",
    state: "37",
    name: "Madison County",
    position: {
      lat: 35.86408,
      lng: -82.712731
    },
    stateName: "North Carolina"
  },
  {
    id: "121",
    state: "37",
    name: "Mitchell County",
    position: {
      lat: 36.013102,
      lng: -82.163554
    },
    stateName: "North Carolina"
  },
  {
    id: "129",
    state: "37",
    name: "New Hanover County",
    position: {
      lat: 34.177466,
      lng: -77.871378
    },
    stateName: "North Carolina"
  },
  {
    id: "135",
    state: "37",
    name: "Orange County",
    position: {
      lat: 36.062499,
      lng: -79.119355
    },
    stateName: "North Carolina"
  },
  {
    id: "139",
    state: "37",
    name: "Pasquotank County",
    position: {
      lat: 36.265276,
      lng: -76.260355
    },
    stateName: "North Carolina"
  },
  {
    id: "143",
    state: "37",
    name: "Perquimans County",
    position: {
      lat: 36.178261,
      lng: -76.404269
    },
    stateName: "North Carolina"
  },
  {
    id: "147",
    state: "37",
    name: "Pitt County",
    position: {
      lat: 35.591065,
      lng: -77.372404
    },
    stateName: "North Carolina"
  },
  {
    id: "153",
    state: "37",
    name: "Richmond County",
    position: {
      lat: 35.001957,
      lng: -79.747809
    },
    stateName: "North Carolina"
  },
  {
    id: "085",
    state: "42",
    name: "Mercer County",
    position: {
      lat: 41.300014,
      lng: -80.252786
    },
    stateName: "Pennsylvania"
  },
  {
    id: "089",
    state: "42",
    name: "Monroe County",
    position: {
      lat: 41.056233,
      lng: -75.329037
    },
    stateName: "Pennsylvania"
  },
  {
    id: "095",
    state: "42",
    name: "Northampton County",
    position: {
      lat: 40.752791,
      lng: -75.307447
    },
    stateName: "Pennsylvania"
  },
  {
    id: "101",
    state: "42",
    name: "Philadelphia County",
    position: {
      lat: 40.009376,
      lng: -75.133346
    },
    stateName: "Pennsylvania"
  },
  {
    id: "109",
    state: "42",
    name: "Snyder County",
    position: {
      lat: 40.755348,
      lng: -77.072954
    },
    stateName: "Pennsylvania"
  },
  {
    id: "111",
    state: "42",
    name: "Somerset County",
    position: {
      lat: 39.981297,
      lng: -79.028486
    },
    stateName: "Pennsylvania"
  },
  {
    id: "115",
    state: "42",
    name: "Susquehanna County",
    position: {
      lat: 41.819665,
      lng: -75.800969
    },
    stateName: "Pennsylvania"
  },
  {
    id: "119",
    state: "42",
    name: "Union County",
    position: {
      lat: 40.962179,
      lng: -77.055475
    },
    stateName: "Pennsylvania"
  },
  {
    id: "121",
    state: "42",
    name: "Venango County",
    position: {
      lat: 41.40198,
      lng: -79.754418
    },
    stateName: "Pennsylvania"
  },
  {
    id: "125",
    state: "42",
    name: "Washington County",
    position: {
      lat: 40.200005,
      lng: -80.252132
    },
    stateName: "Pennsylvania"
  },
  {
    id: "133",
    state: "42",
    name: "York County",
    position: {
      lat: 39.921839,
      lng: -76.728446
    },
    stateName: "Pennsylvania"
  },
  {
    id: "009",
    state: "49",
    name: "Daggett County",
    position: {
      lat: 40.890099,
      lng: -109.505786
    },
    stateName: "Utah"
  },
  {
    id: "013",
    state: "49",
    name: "Duchesne County",
    position: {
      lat: 40.289649,
      lng: -110.42983
    },
    stateName: "Utah"
  },
  {
    id: "019",
    state: "49",
    name: "Grand County",
    position: {
      lat: 38.974326,
      lng: -109.57345
    },
    stateName: "Utah"
  },
  {
    id: "023",
    state: "49",
    name: "Juab County",
    position: {
      lat: 39.710827,
      lng: -112.794262
    },
    stateName: "Utah"
  },
  {
    id: "001",
    state: "48",
    name: "Anderson County",
    position: {
      lat: 31.841266,
      lng: -95.661744
    },
    stateName: "Texas"
  },
  {
    id: "013",
    state: "48",
    name: "Atascosa County",
    position: {
      lat: 28.894296,
      lng: -98.528187
    },
    stateName: "Texas"
  },
  {
    id: "015",
    state: "48",
    name: "Austin County",
    position: {
      lat: 29.891901,
      lng: -96.27017
    },
    stateName: "Texas"
  },
  {
    id: "017",
    state: "48",
    name: "Bailey County",
    position: {
      lat: 34.067521,
      lng: -102.830345
    },
    stateName: "Texas"
  },
  {
    id: "023",
    state: "48",
    name: "Baylor County",
    position: {
      lat: 33.618172,
      lng: -99.197228
    },
    stateName: "Texas"
  },
  {
    id: "025",
    state: "48",
    name: "Bee County",
    position: {
      lat: 28.416077,
      lng: -97.742586
    },
    stateName: "Texas"
  },
  {
    id: "029",
    state: "48",
    name: "Bexar County",
    position: {
      lat: 29.448671,
      lng: -98.520147
    },
    stateName: "Texas"
  },
  {
    id: "033",
    state: "48",
    name: "Borden County",
    position: {
      lat: 32.744062,
      lng: -101.433033
    },
    stateName: "Texas"
  },
  {
    id: "035",
    state: "48",
    name: "Bosque County",
    position: {
      lat: 31.900764,
      lng: -97.637632
    },
    stateName: "Texas"
  },
  {
    id: "039",
    state: "48",
    name: "Brazoria County",
    position: {
      lat: 29.167818,
      lng: -95.434647
    },
    stateName: "Texas"
  },
  {
    id: "171",
    state: "29",
    name: "Putnam County",
    position: {
      lat: 40.478606,
      lng: -93.014531
    },
    stateName: "Missouri"
  },
  {
    id: "175",
    state: "29",
    name: "Randolph County",
    position: {
      lat: 39.441601,
      lng: -92.492725
    },
    stateName: "Missouri"
  },
  {
    id: "177",
    state: "29",
    name: "Ray County",
    position: {
      lat: 39.308401,
      lng: -93.995746
    },
    stateName: "Missouri"
  },
  {
    id: "181",
    state: "29",
    name: "Ripley County",
    position: {
      lat: 36.648902,
      lng: -90.86706
    },
    stateName: "Missouri"
  },
  {
    id: "186",
    state: "29",
    name: "Ste. Genevieve County",
    position: {
      lat: 37.89018,
      lng: -90.18117
    },
    stateName: "Missouri"
  },
  {
    id: "187",
    state: "29",
    name: "St. Francois County",
    position: {
      lat: 37.810707,
      lng: -90.473868
    },
    stateName: "Missouri"
  },
  {
    id: "197",
    state: "29",
    name: "Schuyler County",
    position: {
      lat: 40.469361,
      lng: -92.519016
    },
    stateName: "Missouri"
  },
  {
    id: "203",
    state: "29",
    name: "Shannon County",
    position: {
      lat: 37.15198,
      lng: -91.398364
    },
    stateName: "Missouri"
  },
  {
    id: "207",
    state: "29",
    name: "Stoddard County",
    position: {
      lat: 36.855428,
      lng: -89.941735
    },
    stateName: "Missouri"
  },
  {
    id: "213",
    state: "29",
    name: "Taney County",
    position: {
      lat: 36.649827,
      lng: -93.042819
    },
    stateName: "Missouri"
  },
  {
    id: "219",
    state: "29",
    name: "Warren County",
    position: {
      lat: 38.761902,
      lng: -91.159307
    },
    stateName: "Missouri"
  },
  {
    id: "225",
    state: "29",
    name: "Webster County",
    position: {
      lat: 37.280804,
      lng: -92.87608
    },
    stateName: "Missouri"
  },
  {
    id: "510",
    state: "29",
    name: "St. Louis City County",
    postal: "city",
    position: {
      lat: 38.640702,
      lng: -90.445954
    },
    stateName: "Missouri"
  },
  {
    id: "003",
    state: "30",
    name: "Big Horn County",
    position: {
      lat: 45.407869,
      lng: -107.518163
    },
    stateName: "Montana"
  },
  {
    id: "159",
    state: "37",
    name: "Rowan County",
    position: {
      lat: 35.639218,
      lng: -80.525344
    },
    stateName: "North Carolina"
  },
  {
    id: "163",
    state: "37",
    name: "Sampson County",
    position: {
      lat: 34.990575,
      lng: -78.371382
    },
    stateName: "North Carolina"
  },
  {
    id: "169",
    state: "37",
    name: "Stokes County",
    position: {
      lat: 36.404195,
      lng: -80.239271
    },
    stateName: "North Carolina"
  },
  {
    id: "175",
    state: "37",
    name: "Transylvania County",
    position: {
      lat: 35.210095,
      lng: -82.816696
    },
    stateName: "North Carolina"
  },
  {
    id: "061",
    state: "39",
    name: "Hamilton County",
    position: {
      lat: 39.196927,
      lng: -84.544187
    },
    stateName: "Ohio"
  },
  {
    id: "071",
    state: "39",
    name: "Highland County",
    position: {
      lat: 39.184488,
      lng: -83.603668
    },
    stateName: "Ohio"
  },
  {
    id: "077",
    state: "39",
    name: "Huron County",
    position: {
      lat: 41.14508,
      lng: -82.594641
    },
    stateName: "Ohio"
  },
  {
    id: "081",
    state: "39",
    name: "Jefferson County",
    position: {
      lat: 40.399188,
      lng: -80.76141
    },
    stateName: "Ohio"
  },
  {
    id: "087",
    state: "39",
    name: "Lawrence County",
    position: {
      lat: 38.603866,
      lng: -82.517186
    },
    stateName: "Ohio"
  },
  {
    id: "093",
    state: "39",
    name: "Lorain County",
    position: {
      lat: 41.438805,
      lng: -82.179722
    },
    stateName: "Ohio"
  },
  {
    id: "099",
    state: "39",
    name: "Mahoning County",
    position: {
      lat: 41.01088,
      lng: -80.770396
    },
    stateName: "Ohio"
  },
  {
    id: "101",
    state: "39",
    name: "Marion County",
    position: {
      lat: 40.588208,
      lng: -83.172927
    },
    stateName: "Ohio"
  },
  {
    id: "001",
    state: "44",
    name: "Bristol County",
    position: {
      lat: 41.705271,
      lng: -71.285053
    },
    stateName: "Rhode Island"
  },
  {
    id: "003",
    state: "44",
    name: "Kent County",
    position: {
      lat: 41.67775,
      lng: -71.576314
    },
    stateName: "Rhode Island"
  },
  {
    id: "005",
    state: "44",
    name: "Newport County",
    position: {
      lat: 41.502732,
      lng: -71.284063
    },
    stateName: "Rhode Island"
  },
  {
    id: "001",
    state: "45",
    name: "Abbeville County",
    position: {
      lat: 34.213809,
      lng: -82.46046
    },
    stateName: "South Carolina"
  },
  {
    id: "003",
    state: "45",
    name: "Aiken County",
    position: {
      lat: 33.549317,
      lng: -81.63387
    },
    stateName: "South Carolina"
  },
  {
    id: "007",
    state: "45",
    name: "Anderson County",
    position: {
      lat: 34.519549,
      lng: -82.638086
    },
    stateName: "South Carolina"
  },
  {
    id: "017",
    state: "45",
    name: "Calhoun County",
    position: {
      lat: 33.67478,
      lng: -80.780347
    },
    stateName: "South Carolina"
  },
  {
    id: "023",
    state: "45",
    name: "Chester County",
    position: {
      lat: 34.689345,
      lng: -81.161249
    },
    stateName: "South Carolina"
  },
  {
    id: "025",
    state: "45",
    name: "Chesterfield County",
    position: {
      lat: 34.637018,
      lng: -80.159227
    },
    stateName: "South Carolina"
  },
  {
    id: "063",
    state: "46",
    name: "Harding County",
    position: {
      lat: 45.596612,
      lng: -103.473867
    },
    stateName: "South Dakota"
  },
  {
    id: "065",
    state: "46",
    name: "Hughes County",
    position: {
      lat: 44.392258,
      lng: -99.985846
    },
    stateName: "South Dakota"
  },
  {
    id: "069",
    state: "46",
    name: "Hyde County",
    position: {
      lat: 44.537301,
      lng: -99.492148
    },
    stateName: "South Dakota"
  },
  {
    id: "075",
    state: "46",
    name: "Jones County",
    position: {
      lat: 43.95199,
      lng: -100.686139
    },
    stateName: "South Dakota"
  },
  {
    id: "079",
    state: "46",
    name: "Lake County",
    position: {
      lat: 44.02845,
      lng: -97.123223
    },
    stateName: "South Dakota"
  },
  {
    id: "085",
    state: "46",
    name: "Lyman County",
    position: {
      lat: 43.894812,
      lng: -99.841925
    },
    stateName: "South Dakota"
  },
  {
    id: "093",
    state: "46",
    name: "Meade County",
    position: {
      lat: 44.606792,
      lng: -102.715864
    },
    stateName: "South Dakota"
  },
  {
    id: "097",
    state: "46",
    name: "Miner County",
    position: {
      lat: 44.017327,
      lng: -97.60979
    },
    stateName: "South Dakota"
  },
  {
    id: "101",
    state: "46",
    name: "Moody County",
    position: {
      lat: 44.012429,
      lng: -96.676054
    },
    stateName: "South Dakota"
  },
  {
    id: "113",
    state: "46",
    name: "Shannon County",
    position: {
      lat: 37.15198,
      lng: -91.398364
    },
    stateName: "South Dakota"
  },
  {
    id: "115",
    state: "46",
    name: "Spink County",
    position: {
      lat: 44.931034,
      lng: -98.339644
    },
    stateName: "South Dakota"
  },
  {
    id: "117",
    state: "46",
    name: "Stanley County",
    position: {
      lat: 44.415547,
      lng: -100.749163
    },
    stateName: "South Dakota"
  },
  {
    id: "121",
    state: "46",
    name: "Todd County",
    position: {
      lat: 43.208172,
      lng: -100.717204
    },
    stateName: "South Dakota"
  },
  {
    id: "127",
    state: "46",
    name: "Union County",
    position: {
      lat: 42.831106,
      lng: -96.650829
    },
    stateName: "South Dakota"
  },
  {
    id: "005",
    state: "30",
    name: "Blaine County",
    position: {
      lat: 48.428282,
      lng: -108.967648
    },
    stateName: "Montana"
  },
  {
    id: "009",
    state: "30",
    name: "Carbon County",
    position: {
      lat: 45.224475,
      lng: -109.028551
    },
    stateName: "Montana"
  },
  {
    id: "015",
    state: "30",
    name: "Chouteau County",
    position: {
      lat: 47.886837,
      lng: -110.436189
    },
    stateName: "Montana"
  },
  {
    id: "019",
    state: "30",
    name: "Daniels County",
    position: {
      lat: 48.794429,
      lng: -105.541739
    },
    stateName: "Montana"
  },
  {
    id: "023",
    state: "30",
    name: "Deer Lodge County",
    position: {
      lat: 46.099059,
      lng: -113.139108
    },
    stateName: "Montana"
  },
  {
    id: "029",
    state: "30",
    name: "Flathead County",
    position: {
      lat: 48.314696,
      lng: -114.054319
    },
    stateName: "Montana"
  },
  {
    id: "035",
    state: "30",
    name: "Glacier County",
    position: {
      lat: 48.705671,
      lng: -112.990502
    },
    stateName: "Montana"
  },
  {
    id: "041",
    state: "30",
    name: "Hill County",
    position: {
      lat: 48.628331,
      lng: -110.106372
    },
    stateName: "Montana"
  },
  {
    id: "129",
    state: "31",
    name: "Nuckolls County",
    position: {
      lat: 40.176492,
      lng: -98.046842
    },
    stateName: "Nebraska"
  },
  {
    id: "133",
    state: "31",
    name: "Pawnee County",
    position: {
      lat: 40.13785,
      lng: -96.245225
    },
    stateName: "Nebraska"
  },
  {
    id: "141",
    state: "31",
    name: "Platte County",
    position: {
      lat: 41.576866,
      lng: -97.513467
    },
    stateName: "Nebraska"
  },
  {
    id: "007",
    state: "19",
    name: "Appanoose County",
    position: {
      lat: 40.744683,
      lng: -92.870345
    },
    stateName: "Iowa"
  },
  {
    id: "011",
    state: "19",
    name: "Benton County",
    position: {
      lat: 42.092547,
      lng: -92.05763
    },
    stateName: "Iowa"
  },
  {
    id: "015",
    state: "19",
    name: "Boone County",
    position: {
      lat: 42.038601,
      lng: -93.939138
    },
    stateName: "Iowa"
  },
  {
    id: "019",
    state: "19",
    name: "Buchanan County",
    position: {
      lat: 42.470328,
      lng: -91.838666
    },
    stateName: "Iowa"
  },
  {
    id: "023",
    state: "19",
    name: "Butler County",
    position: {
      lat: 42.734708,
      lng: -92.780066
    },
    stateName: "Iowa"
  },
  {
    id: "027",
    state: "19",
    name: "Carroll County",
    position: {
      lat: 42.039492,
      lng: -94.867647
    },
    stateName: "Iowa"
  },
  {
    id: "033",
    state: "19",
    name: "Cerro Gordo County",
    position: {
      lat: 43.075171,
      lng: -93.251266
    },
    stateName: "Iowa"
  },
  {
    id: "081",
    state: "17",
    name: "Jefferson County",
    position: {
      lat: 38.30078,
      lng: -88.92421
    },
    stateName: "Illinois"
  },
  {
    id: "091",
    state: "17",
    name: "Kankakee County",
    position: {
      lat: 41.139494,
      lng: -87.861125
    },
    stateName: "Illinois"
  },
  {
    id: "093",
    state: "17",
    name: "Kendall County",
    position: {
      lat: 41.58814,
      lng: -88.430626
    },
    stateName: "Illinois"
  },
  {
    id: "099",
    state: "17",
    name: "La Salle County",
    position: {
      lat: 41.343341,
      lng: 88.885931
    },
    stateName: "Illinois"
  },
  {
    id: "101",
    state: "17",
    name: "Lawrence County",
    position: {
      lat: 38.718954,
      lng: -87.730221
    },
    stateName: "Illinois"
  },
  {
    id: "105",
    state: "17",
    name: "Livingston County",
    position: {
      lat: 40.894376,
      lng: -88.552852
    },
    stateName: "Illinois"
  },
  {
    id: "109",
    state: "17",
    name: "Mc Donough County",
    position: {
      lat: 40.455789,
      lng: -90.677579
    },
    stateName: "Illinois"
  },
  {
    id: "111",
    state: "17",
    name: "Mc Henry County",
    position: {
      lat: 42.324298,
      lng: -88.452245
    },
    stateName: "Illinois"
  },
  {
    id: "117",
    state: "17",
    name: "Macoupin County",
    position: {
      lat: 39.2659,
      lng: -89.926344
    },
    stateName: "Illinois"
  },
  {
    id: "125",
    state: "17",
    name: "Mason County",
    position: {
      lat: 40.236993,
      lng: -89.913575
    },
    stateName: "Illinois"
  },
  {
    id: "127",
    state: "17",
    name: "Massac County",
    position: {
      lat: 37.216119,
      lng: -88.705658
    },
    stateName: "Illinois"
  },
  {
    id: "185",
    state: "13",
    name: "Lowndes County",
    position: {
      lat: 30.83368,
      lng: -83.268967
    },
    stateName: "Georgia"
  },
  {
    id: "189",
    state: "13",
    name: "Mc Duffie County",
    position: {
      lat: 33.482464,
      lng: -82.473188
    },
    stateName: "Georgia"
  },
  {
    id: "195",
    state: "13",
    name: "Madison County",
    position: {
      lat: 34.128486,
      lng: -83.203637
    },
    stateName: "Georgia"
  },
  {
    id: "199",
    state: "13",
    name: "Meriwether County",
    position: {
      lat: 33.03022,
      lng: -84.663279
    },
    stateName: "Georgia"
  },
  {
    id: "205",
    state: "13",
    name: "Mitchell County",
    position: {
      lat: 31.22894,
      lng: -84.192407
    },
    stateName: "Georgia"
  },
  {
    id: "209",
    state: "13",
    name: "Montgomery County",
    position: {
      lat: 32.172108,
      lng: -82.533349
    },
    stateName: "Georgia"
  },
  {
    id: "213",
    state: "13",
    name: "Murray County",
    position: {
      lat: 34.797097,
      lng: -84.73799
    },
    stateName: "Georgia"
  },
  {
    id: "221",
    state: "13",
    name: "Oglethorpe County",
    position: {
      lat: 33.866806,
      lng: -83.074081
    },
    stateName: "Georgia"
  },
  {
    id: "229",
    state: "13",
    name: "Pierce County",
    position: {
      lat: 31.353988,
      lng: -82.210427
    },
    stateName: "Georgia"
  },
  {
    id: "231",
    state: "13",
    name: "Pike County",
    position: {
      lat: 33.090769,
      lng: -84.386627
    },
    stateName: "Georgia"
  },
  {
    id: "235",
    state: "13",
    name: "Pulaski County",
    position: {
      lat: 32.238794,
      lng: -83.481855
    },
    stateName: "Georgia"
  },
  {
    id: "241",
    state: "13",
    name: "Rabun County",
    position: {
      lat: 34.883026,
      lng: -83.404735
    },
    stateName: "Georgia"
  },
  {
    id: "247",
    state: "13",
    name: "Rockdale County",
    position: {
      lat: 33.652081,
      lng: -84.02637
    },
    stateName: "Georgia"
  },
  {
    id: "255",
    state: "13",
    name: "Spalding County",
    position: {
      lat: 33.262389,
      lng: -84.286067
    },
    stateName: "Georgia"
  },
  {
    id: "257",
    state: "13",
    name: "Stephens County",
    position: {
      lat: 34.552914,
      lng: -83.290216
    },
    stateName: "Georgia"
  },
  {
    id: "267",
    state: "13",
    name: "Tattnall County",
    position: {
      lat: 32.043768,
      lng: -82.059208
    },
    stateName: "Georgia"
  },
  {
    id: "269",
    state: "13",
    name: "Taylor County",
    position: {
      lat: 32.554667,
      lng: -84.251426
    },
    stateName: "Georgia"
  },
  {
    id: "183",
    state: "20",
    name: "Smith County",
    position: {
      lat: 39.78466,
      lng: -98.78543
    },
    stateName: "Kansas"
  },
  {
    id: "187",
    state: "20",
    name: "Stanton County",
    position: {
      lat: 37.565932,
      lng: -101.789383
    },
    stateName: "Kansas"
  },
  {
    id: "189",
    state: "20",
    name: "Stevens County",
    position: {
      lat: 37.202356,
      lng: -101.315796
    },
    stateName: "Kansas"
  },
  {
    id: "197",
    state: "20",
    name: "Wabaunsee County",
    position: {
      lat: 38.955154,
      lng: -96.201262
    },
    stateName: "Kansas"
  },
  {
    id: "207",
    state: "20",
    name: "Woodson County",
    position: {
      lat: 37.888484,
      lng: -95.757553
    },
    stateName: "Kansas"
  },
  {
    id: "191",
    state: "21",
    name: "Pendleton County",
    position: {
      lat: 38.690765,
      lng: -84.354041
    },
    stateName: "Kentucky"
  },
  {
    id: "193",
    state: "21",
    name: "Perry County",
    position: {
      lat: 37.241282,
      lng: -83.217772
    },
    stateName: "Kentucky"
  },
  {
    id: "201",
    state: "21",
    name: "Robertson County",
    position: {
      lat: 38.513826,
      lng: -84.063757
    },
    stateName: "Kentucky"
  },
  {
    id: "205",
    state: "21",
    name: "Rowan County",
    position: {
      lat: 38.205894,
      lng: -83.425969
    },
    stateName: "Kentucky"
  },
  {
    id: "207",
    state: "21",
    name: "Russell County",
    position: {
      lat: 36.989127,
      lng: -85.058976
    },
    stateName: "Kentucky"
  },
  {
    id: "217",
    state: "21",
    name: "Taylor County",
    position: {
      lat: 37.365106,
      lng: -85.326698
    },
    stateName: "Kentucky"
  },
  {
    id: "221",
    state: "21",
    name: "Trigg County",
    position: {
      lat: 36.807681,
      lng: -87.858652
    },
    stateName: "Kentucky"
  },
  {
    id: "231",
    state: "21",
    name: "Wayne County",
    position: {
      lat: 36.802011,
      lng: -84.83044
    },
    stateName: "Kentucky"
  },
  {
    id: "233",
    state: "21",
    name: "Webster County",
    position: {
      lat: 37.520021,
      lng: -87.685095
    },
    stateName: "Kentucky"
  },
  {
    id: "237",
    state: "21",
    name: "Wolfe County",
    position: {
      lat: 37.743774,
      lng: -83.495068
    },
    stateName: "Kentucky"
  },
  {
    id: "239",
    state: "21",
    name: "Woodford County",
    position: {
      lat: 38.042789,
      lng: -84.748147
    },
    stateName: "Kentucky"
  },
  {
    id: "005",
    state: "22",
    name: "Ascension County",
    postal: "Parish",
    position: {
      lat: 30.202946,
      lng: -90.910023
    },
    stateName: "Louisiana"
  },
  {
    id: "091",
    state: "22",
    name: "St. Helena County",
    postal: "Parish",
    position: {
      lat: 30.821423,
      lng: -90.70935
    },
    stateName: "Louisiana"
  },
  {
    id: "093",
    state: "22",
    name: "St. James County",
    postal: "Parish",
    position: {
      lat: 30.02482,
      lng: -90.793956
    },
    stateName: "Louisiana"
  },
  {
    id: "097",
    state: "22",
    name: "St. Landry County",
    postal: "Parish",
    position: {
      lat: 30.583441,
      lng: -91.989275
    },
    stateName: "Louisiana"
  },
  {
    id: "099",
    state: "22",
    name: "St. Martin County",
    postal: "Parish",
    position: {
      lat: 30.121433,
      lng: -91.611481
    },
    stateName: "Louisiana"
  },
  {
    id: "103",
    state: "22",
    name: "St. Tammany County",
    postal: "Parish",
    position: {
      lat: 30.410022,
      lng: -89.951962
    },
    stateName: "Louisiana"
  },
  {
    id: "113",
    state: "22",
    name: "Vermilion County",
    postal: "Parish",
    position: {
      lat: 29.786872,
      lng: -92.29009
    },
    stateName: "Louisiana"
  },
  {
    id: "117",
    state: "22",
    name: "Washington County",
    postal: "Parish",
    position: {
      lat: 30.852144,
      lng: -90.046253
    },
    stateName: "Louisiana"
  },
  {
    id: "011",
    state: "21",
    name: "Bath County",
    position: {
      lat: 38.152249,
      lng: -83.737641
    },
    stateName: "Kentucky"
  },
  {
    id: "013",
    state: "21",
    name: "Bell County",
    position: {
      lat: 36.72268,
      lng: -83.681046
    },
    stateName: "Kentucky"
  },
  {
    id: "015",
    state: "21",
    name: "Boone County",
    position: {
      lat: 38.974595,
      lng: -84.731444
    },
    stateName: "Kentucky"
  },
  {
    id: "027",
    state: "21",
    name: "Breckinridge County",
    position: {
      lat: 37.778109,
      lng: -86.432829
    },
    stateName: "Kentucky"
  },
  {
    id: "029",
    state: "21",
    name: "Bullitt County",
    position: {
      lat: 37.969572,
      lng: -85.703036
    },
    stateName: "Kentucky"
  },
  {
    id: "133",
    state: "17",
    name: "Monroe County",
    position: {
      lat: 38.277983,
      lng: -90.179078
    },
    stateName: "Illinois"
  },
  {
    id: "137",
    state: "17",
    name: "Morgan County",
    position: {
      lat: 39.716806,
      lng: -90.202277
    },
    stateName: "Illinois"
  },
  {
    id: "141",
    state: "17",
    name: "Ogle County",
    position: {
      lat: 42.041884,
      lng: -89.320176
    },
    stateName: "Illinois"
  },
  {
    id: "143",
    state: "17",
    name: "Peoria County",
    position: {
      lat: 40.785999,
      lng: -89.767358
    },
    stateName: "Illinois"
  },
  {
    id: "147",
    state: "17",
    name: "Piatt County",
    position: {
      lat: 40.009056,
      lng: -88.592328
    },
    stateName: "Illinois"
  },
  {
    id: "153",
    state: "17",
    name: "Pulaski County",
    position: {
      lat: 37.215615,
      lng: -89.127755
    },
    stateName: "Illinois"
  },
  {
    id: "159",
    state: "17",
    name: "Richland County",
    position: {
      lat: 38.71155,
      lng: -88.085698
    },
    stateName: "Illinois"
  },
  {
    id: "165",
    state: "17",
    name: "Saline County",
    position: {
      lat: 37.751653,
      lng: -88.545031
    },
    stateName: "Illinois"
  },
  {
    id: "167",
    state: "17",
    name: "Sangamon County",
    position: {
      lat: 39.756378,
      lng: -89.662311
    },
    stateName: "Illinois"
  },
  {
    id: "177",
    state: "17",
    name: "Stephenson County",
    position: {
      lat: 42.349726,
      lng: -89.665994
    },
    stateName: "Illinois"
  },
  {
    id: "183",
    state: "17",
    name: "Vermilion County",
    position: {
      lat: 40.18674,
      lng: -87.726772
    },
    stateName: "Illinois"
  },
  {
    id: "191",
    state: "17",
    name: "Wayne County",
    position: {
      lat: 38.431948,
      lng: -88.432129
    },
    stateName: "Illinois"
  },
  {
    id: "195",
    state: "17",
    name: "Whiteside County",
    position: {
      lat: 41.750571,
      lng: -89.910957
    },
    stateName: "Illinois"
  },
  {
    id: "197",
    state: "17",
    name: "Will County",
    position: {
      lat: 41.448474,
      lng: -87.978456
    },
    stateName: "Illinois"
  },
  {
    id: "001",
    state: "18",
    name: "Adams County",
    position: {
      lat: 40.745733,
      lng: -84.936131
    },
    stateName: "Indiana"
  },
  {
    id: "275",
    state: "13",
    name: "Thomas County",
    position: {
      lat: 30.864616,
      lng: -83.919783
    },
    stateName: "Georgia"
  },
  {
    id: "279",
    state: "13",
    name: "Toombs County",
    position: {
      lat: 32.126698,
      lng: -82.332071
    },
    stateName: "Georgia"
  },
  {
    id: "281",
    state: "13",
    name: "Towns County",
    position: {
      lat: 34.90265,
      lng: -83.732158
    },
    stateName: "Georgia"
  },
  {
    id: "289",
    state: "13",
    name: "Twiggs County",
    position: {
      lat: 32.665847,
      lng: -83.425879
    },
    stateName: "Georgia"
  },
  {
    id: "295",
    state: "13",
    name: "Walker County",
    position: {
      lat: 34.735827,
      lng: -85.305385
    },
    stateName: "Georgia"
  },
  {
    id: "301",
    state: "13",
    name: "Warren County",
    position: {
      lat: 33.419169,
      lng: -82.688012
    },
    stateName: "Georgia"
  },
  {
    id: "035",
    state: "20",
    name: "Cowley County",
    position: {
      lat: 37.234507,
      lng: -96.837247
    },
    stateName: "Kansas"
  },
  {
    id: "041",
    state: "20",
    name: "Dickinson County",
    position: {
      lat: 38.867735,
      lng: -97.157943
    },
    stateName: "Kansas"
  },
  {
    id: "047",
    state: "20",
    name: "Edwards County",
    position: {
      lat: 37.883595,
      lng: -99.304746
    },
    stateName: "Kansas"
  },
  {
    id: "051",
    state: "20",
    name: "Ellis County",
    position: {
      lat: 38.914596,
      lng: -99.317329
    },
    stateName: "Kansas"
  },
  {
    id: "053",
    state: "20",
    name: "Ellsworth County",
    position: {
      lat: 38.700845,
      lng: -98.205355
    },
    stateName: "Kansas"
  },
  {
    id: "057",
    state: "20",
    name: "Ford County",
    position: {
      lat: 37.688365,
      lng: -99.884734
    },
    stateName: "Kansas"
  },
  {
    id: "063",
    state: "20",
    name: "Gove County",
    position: {
      lat: 38.917239,
      lng: -100.48736
    },
    stateName: "Kansas"
  },
  {
    id: "073",
    state: "20",
    name: "Greenwood County",
    position: {
      lat: 37.878659,
      lng: -96.242232
    },
    stateName: "Kansas"
  },
  {
    id: "077",
    state: "20",
    name: "Harper County",
    position: {
      lat: 37.188184,
      lng: -98.06659
    },
    stateName: "Kansas"
  },
  {
    id: "079",
    state: "20",
    name: "Harvey County",
    position: {
      lat: 38.050144,
      lng: -97.436707
    },
    stateName: "Kansas"
  },
  {
    id: "087",
    state: "20",
    name: "Jefferson County",
    position: {
      lat: 39.239644,
      lng: -95.375314
    },
    stateName: "Kansas"
  },
  {
    id: "011",
    state: "22",
    name: "Beauregard County",
    postal: "Parish",
    position: {
      lat: 30.645018,
      lng: -93.340253
    },
    stateName: "Louisiana"
  },
  {
    id: "015",
    state: "22",
    name: "Bossier County",
    postal: "Parish",
    position: {
      lat: 32.696202,
      lng: -93.617977
    },
    stateName: "Louisiana"
  },
  {
    id: "017",
    state: "22",
    name: "Caddo County",
    postal: "Parish",
    position: {
      lat: 32.577195,
      lng: -93.882423
    },
    stateName: "Louisiana"
  },
  {
    id: "023",
    state: "22",
    name: "Cameron County",
    postal: "Parish",
    position: {
      lat: 29.871989,
      lng: -93.165437
    },
    stateName: "Louisiana"
  },
  {
    id: "031",
    state: "22",
    name: "De Soto County",
    postal: "Parish",
    position: {
      lat: 32.059248,
      lng: -93.740797
    },
    stateName: "Louisiana"
  },
  {
    id: "035",
    state: "22",
    name: "East Carroll County",
    postal: "Parish",
    position: {
      lat: 32.73017,
      lng: -91.234141
    },
    stateName: "Louisiana"
  },
  {
    id: "041",
    state: "22",
    name: "Franklin County",
    postal: "Parish",
    position: {
      lat: 32.138279,
      lng: -91.672101
    },
    stateName: "Louisiana"
  },
  {
    id: "043",
    state: "22",
    name: "Grant County",
    postal: "Parish",
    position: {
      lat: 31.597787,
      lng: -92.561716
    },
    stateName: "Louisiana"
  },
  {
    id: "049",
    state: "22",
    name: "Jackson County",
    postal: "Parish",
    position: {
      lat: 32.30448,
      lng: -92.556672
    },
    stateName: "Louisiana"
  },
  {
    id: "051",
    state: "22",
    name: "Jefferson County",
    postal: "Parish",
    position: {
      lat: 29.5033,
      lng: -90.036231
    },
    stateName: "Louisiana"
  },
  {
    id: "055",
    state: "22",
    name: "Lafayette County",
    postal: "Parish",
    position: {
      lat: 30.206507,
      lng: -92.06417
    },
    stateName: "Louisiana"
  },
  {
    id: "035",
    state: "21",
    name: "Calloway County",
    position: {
      lat: 36.620978,
      lng: -88.274086
    },
    stateName: "Kentucky"
  },
  {
    id: "039",
    state: "21",
    name: "Carlisle County",
    position: {
      lat: 36.857726,
      lng: -88.975757
    },
    stateName: "Kentucky"
  },
  {
    id: "041",
    state: "21",
    name: "Carroll County",
    position: {
      lat: 38.668284,
      lng: -85.124083
    },
    stateName: "Kentucky"
  },
  {
    id: "045",
    state: "21",
    name: "Casey County",
    position: {
      lat: 37.321962,
      lng: -84.92822
    },
    stateName: "Kentucky"
  },
  {
    id: "057",
    state: "21",
    name: "Cumberland County",
    position: {
      lat: 36.784227,
      lng: -85.393499
    },
    stateName: "Kentucky"
  },
  {
    id: "065",
    state: "21",
    name: "Estill County",
    position: {
      lat: 37.69246,
      lng: -83.963927
    },
    stateName: "Kentucky"
  },
  {
    id: "067",
    state: "21",
    name: "Fayette County",
    position: {
      lat: 38.040157,
      lng: -84.458443
    },
    stateName: "Kentucky"
  },
  {
    id: "071",
    state: "21",
    name: "Floyd County",
    position: {
      lat: 37.558283,
      lng: -82.740337
    },
    stateName: "Kentucky"
  },
  {
    id: "079",
    state: "21",
    name: "Garrard County",
    position: {
      lat: 37.630162,
      lng: -84.545856
    },
    stateName: "Kentucky"
  },
  {
    id: "083",
    state: "21",
    name: "Graves County",
    position: {
      lat: 36.723344,
      lng: -88.649897
    },
    stateName: "Kentucky"
  },
  {
    id: "085",
    state: "21",
    name: "Grayson County",
    position: {
      lat: 37.458577,
      lng: -86.344011
    },
    stateName: "Kentucky"
  },
  {
    id: "091",
    state: "21",
    name: "Hancock County",
    position: {
      lat: 37.843389,
      lng: -86.792773
    },
    stateName: "Kentucky"
  },
  {
    id: "097",
    state: "21",
    name: "Harrison County",
    position: {
      lat: 38.443489,
      lng: -84.332796
    },
    stateName: "Kentucky"
  },
  {
    id: "101",
    state: "21",
    name: "Henderson County",
    position: {
      lat: 37.792542,
      lng: -87.572577
    },
    stateName: "Kentucky"
  },
  {
    id: "107",
    state: "21",
    name: "Hopkins County",
    position: {
      lat: 37.31107,
      lng: -87.542196
    },
    stateName: "Kentucky"
  },
  {
    id: "267",
    state: "48",
    name: "Kimble County",
    position: {
      lat: 30.479472,
      lng: -99.746396
    },
    stateName: "Texas"
  },
  {
    id: "271",
    state: "48",
    name: "Kinney County",
    position: {
      lat: 29.347087,
      lng: -100.4177
    },
    stateName: "Texas"
  },
  {
    id: "277",
    state: "48",
    name: "Lamar County",
    position: {
      lat: 33.667263,
      lng: -95.570348
    },
    stateName: "Texas"
  },
  {
    id: "281",
    state: "48",
    name: "Lampasas County",
    position: {
      lat: 31.196731,
      lng: -98.240889
    },
    stateName: "Texas"
  },
  {
    id: "285",
    state: "48",
    name: "Lavaca County",
    position: {
      lat: 29.382578,
      lng: -96.923633
    },
    stateName: "Texas"
  },
  {
    id: "287",
    state: "48",
    name: "Lee County",
    position: {
      lat: 30.321105,
      lng: -96.976365
    },
    stateName: "Texas"
  },
  {
    id: "291",
    state: "48",
    name: "Liberty County",
    position: {
      lat: 30.162189,
      lng: -94.822682
    },
    stateName: "Texas"
  },
  {
    id: "293",
    state: "48",
    name: "Limestone County",
    position: {
      lat: 31.547543,
      lng: -96.593623
    },
    stateName: "Texas"
  },
  {
    id: "295",
    state: "48",
    name: "Lipscomb County",
    position: {
      lat: 36.2802,
      lng: -100.272683
    },
    stateName: "Texas"
  },
  {
    id: "301",
    state: "48",
    name: "Loving County",
    position: {
      lat: 31.844936,
      lng: -103.561229
    },
    stateName: "Texas"
  },
  {
    id: "305",
    state: "48",
    name: "Lynn County",
    position: {
      lat: 33.178412,
      lng: -101.818493
    },
    stateName: "Texas"
  },
  {
    id: "307",
    state: "48",
    name: "Mc Culloch County",
    position: {
      lat: 31.205477,
      lng: -99.359856
    },
    stateName: "Texas"
  },
  {
    id: "317",
    state: "48",
    name: "Martin County",
    position: {
      lat: 32.30983,
      lng: -101.961836
    },
    stateName: "Texas"
  },
  {
    id: "323",
    state: "48",
    name: "Maverick County",
    position: {
      lat: 28.745217,
      lng: -100.311368
    },
    stateName: "Texas"
  },
  {
    id: "327",
    state: "48",
    name: "Menard County",
    position: {
      lat: 30.883707,
      lng: -99.854936
    },
    stateName: "Texas"
  },
  {
    id: "331",
    state: "48",
    name: "Milam County",
    position: {
      lat: 30.791242,
      lng: -96.984395
    },
    stateName: "Texas"
  },
  {
    id: "333",
    state: "48",
    name: "Mills County",
    position: {
      lat: 31.494889,
      lng: -98.594623
    },
    stateName: "Texas"
  },
  {
    id: "800",
    state: "51",
    name: "Suffolk City County",
    postal: "city",
    position: {
      lat: 36.697157,
      lng: -76.634781
    },
    stateName: "Virginia"
  },
  {
    id: "810",
    state: "51",
    name: "Virginia Beach City County",
    postal: "city",
    position: {
      lat: 36.779322,
      lng: -76.02402
    },
    stateName: "Virginia"
  },
  {
    id: "840",
    state: "51",
    name: "Winchester City County",
    postal: "city",
    position: {
      lat: 39.173869,
      lng: -78.176356
    },
    stateName: "Virginia"
  },
  {
    id: "001",
    state: "53",
    name: "Adams County",
    position: {
      lat: 47.00484,
      lng: -118.533308
    },
    stateName: "Washington"
  },
  {
    id: "003",
    state: "53",
    name: "Asotin County",
    position: {
      lat: 46.181861,
      lng: -117.227781
    },
    stateName: "Washington"
  },
  {
    id: "005",
    state: "53",
    name: "Benton County",
    position: {
      lat: 46.228072,
      lng: -119.516864
    },
    stateName: "Washington"
  },
  {
    id: "015",
    state: "53",
    name: "Cowlitz County",
    position: {
      lat: 46.185923,
      lng: -122.658682
    },
    stateName: "Washington"
  },
  {
    id: "017",
    state: "53",
    name: "Douglas County",
    position: {
      lat: 47.735866,
      lng: -119.69588
    },
    stateName: "Washington"
  },
  {
    id: "019",
    state: "53",
    name: "Ferry County",
    position: {
      lat: 48.437246,
      lng: -118.517074
    },
    stateName: "Washington"
  },
  {
    id: "023",
    state: "53",
    name: "Garfield County",
    position: {
      lat: 46.429474,
      lng: -117.536715
    },
    stateName: "Washington"
  },
  {
    id: "025",
    state: "53",
    name: "Grant County",
    position: {
      lat: 47.213633,
      lng: -119.467788
    },
    stateName: "Washington"
  },
  {
    id: "600",
    state: "51",
    name: "Fairfax City County",
    postal: "city",
    position: {
      lat: 38.833743,
      lng: -77.276117
    },
    stateName: "Virginia"
  },
  {
    id: "101",
    state: "55",
    name: "Racine County",
    position: {
      lat: 42.754075,
      lng: -87.414676
    },
    stateName: "Wisconsin"
  },
  {
    id: "107",
    state: "55",
    name: "Rusk County",
    position: {
      lat: 45.472734,
      lng: -91.136745
    },
    stateName: "Wisconsin"
  },
  {
    id: "111",
    state: "55",
    name: "Sauk County",
    position: {
      lat: 43.427998,
      lng: -89.943329
    },
    stateName: "Wisconsin"
  },
  {
    id: "113",
    state: "55",
    name: "Sawyer County",
    position: {
      lat: 45.864913,
      lng: -91.14713
    },
    stateName: "Wisconsin"
  },
  {
    id: "119",
    state: "55",
    name: "Taylor County",
    position: {
      lat: 45.211656,
      lng: -90.504853
    },
    stateName: "Wisconsin"
  },
  {
    id: "123",
    state: "55",
    name: "Vernon County",
    position: {
      lat: 43.599858,
      lng: -90.815226
    },
    stateName: "Wisconsin"
  },
  {
    id: "125",
    state: "55",
    name: "Vilas County",
    position: {
      lat: 46.049848,
      lng: -89.501254
    },
    stateName: "Wisconsin"
  },
  {
    id: "133",
    state: "55",
    name: "Waukesha County",
    position: {
      lat: 43.019308,
      lng: -88.306707
    },
    stateName: "Wisconsin"
  },
  {
    id: "139",
    state: "55",
    name: "Winnebago County",
    position: {
      lat: 44.085707,
      lng: -88.668149
    },
    stateName: "Wisconsin"
  },
  {
    id: "003",
    state: "56",
    name: "Big Horn County",
    position: {
      lat: 44.525654,
      lng: -107.993321
    },
    stateName: "Wyoming"
  },
  {
    id: "009",
    state: "56",
    name: "Converse County",
    position: {
      lat: 42.972839,
      lng: -105.507367
    },
    stateName: "Wyoming"
  },
  {
    id: "017",
    state: "56",
    name: "Hot Springs County",
    position: {
      lat: 43.720871,
      lng: -108.435652
    },
    stateName: "Wyoming"
  },
  {
    id: "019",
    state: "56",
    name: "Johnson County",
    position: {
      lat: 44.044048,
      lng: -106.588541
    },
    stateName: "Wyoming"
  },
  {
    id: "023",
    state: "54",
    name: "Grant County",
    position: {
      lat: 39.105988,
      lng: -79.195064
    },
    stateName: "West Virginia"
  },
  {
    id: "025",
    state: "54",
    name: "Greenbrier County",
    position: {
      lat: 37.924418,
      lng: -80.45059
    },
    stateName: "West Virginia"
  },
  {
    id: "027",
    state: "54",
    name: "Hampshire County",
    position: {
      lat: 39.31214,
      lng: -78.611989
    },
    stateName: "West Virginia"
  },
  {
    id: "033",
    state: "54",
    name: "Harrison County",
    position: {
      lat: 39.279199,
      lng: -80.386487
    },
    stateName: "West Virginia"
  },
  {
    id: "037",
    state: "54",
    name: "Jefferson County",
    position: {
      lat: 39.307399,
      lng: -77.86322
    },
    stateName: "West Virginia"
  },
  {
    id: "045",
    state: "54",
    name: "Logan County",
    position: {
      lat: 37.830591,
      lng: -81.940853
    },
    stateName: "West Virginia"
  },
  {
    id: "051",
    state: "54",
    name: "Marshall County",
    position: {
      lat: 39.854717,
      lng: -80.671378
    },
    stateName: "West Virginia"
  },
  {
    id: "053",
    state: "54",
    name: "Mason County",
    position: {
      lat: 38.759288,
      lng: -82.024172
    },
    stateName: "West Virginia"
  },
  {
    id: "059",
    state: "54",
    name: "Mingo County",
    position: {
      lat: 37.721151,
      lng: -82.158993
    },
    stateName: "West Virginia"
  },
  {
    id: "065",
    state: "54",
    name: "Morgan County",
    position: {
      lat: 39.554835,
      lng: -78.257314
    },
    stateName: "West Virginia"
  },
  {
    id: "069",
    state: "54",
    name: "Ohio County",
    position: {
      lat: 40.098932,
      lng: -80.620728
    },
    stateName: "West Virginia"
  },
  {
    id: "075",
    state: "54",
    name: "Pocahontas County",
    position: {
      lat: 38.332513,
      lng: -80.012092
    },
    stateName: "West Virginia"
  },
  {
    id: "341",
    state: "48",
    name: "Moore County",
    position: {
      lat: 35.835676,
      lng: -101.890502
    },
    stateName: "Texas"
  },
  {
    id: "083",
    state: "54",
    name: "Randolph County",
    position: {
      lat: 38.781087,
      lng: -79.867783
    },
    stateName: "West Virginia"
  },
  {
    id: "087",
    state: "54",
    name: "Roane County",
    position: {
      lat: 38.743033,
      lng: -81.354502
    },
    stateName: "West Virginia"
  },
  {
    id: "091",
    state: "54",
    name: "Taylor County",
    position: {
      lat: 39.329072,
      lng: -80.045629
    },
    stateName: "West Virginia"
  },
  {
    id: "093",
    state: "54",
    name: "Tucker County",
    position: {
      lat: 39.111175,
      lng: -79.559968
    },
    stateName: "West Virginia"
  },
  {
    id: "097",
    state: "54",
    name: "Upshur County",
    position: {
      lat: 38.902537,
      lng: -80.231335
    },
    stateName: "West Virginia"
  },
  {
    id: "099",
    state: "54",
    name: "Wayne County",
    position: {
      lat: 38.145531,
      lng: -82.419698
    },
    stateName: "West Virginia"
  },
  {
    id: "103",
    state: "54",
    name: "Wetzel County",
    position: {
      lat: 39.596574,
      lng: -80.634394
    },
    stateName: "West Virginia"
  },
  {
    id: "105",
    state: "54",
    name: "Wirt County",
    position: {
      lat: 39.020034,
      lng: -81.382975
    },
    stateName: "West Virginia"
  },
  {
    id: "107",
    state: "54",
    name: "Wood County",
    position: {
      lat: 39.211679,
      lng: -81.515928
    },
    stateName: "West Virginia"
  },
  {
    id: "011",
    state: "55",
    name: "Buffalo County",
    position: {
      lat: 44.389759,
      lng: -91.758714
    },
    stateName: "Wisconsin"
  },
  {
    id: "013",
    state: "55",
    name: "Burnett County",
    position: {
      lat: 45.865255,
      lng: -92.367978
    },
    stateName: "Wisconsin"
  },
  {
    id: "019",
    state: "55",
    name: "Clark County",
    position: {
      lat: 44.733596,
      lng: -90.610201
    },
    stateName: "Wisconsin"
  },
  {
    id: "021",
    state: "55",
    name: "Columbia County",
    position: {
      lat: 43.471882,
      lng: -89.330472
    },
    stateName: "Wisconsin"
  },
  {
    id: "023",
    state: "55",
    name: "Crawford County",
    position: {
      lat: 43.24991,
      lng: -90.95123
    },
    stateName: "Wisconsin"
  },
  {
    id: "029",
    state: "53",
    name: "Island County",
    position: {
      lat: 48.158436,
      lng: -122.670503
    },
    stateName: "Washington"
  },
  {
    id: "039",
    state: "49",
    name: "Sanpete County",
    position: {
      lat: 39.380588,
      lng: -111.570451
    },
    stateName: "Utah"
  },
  {
    id: "043",
    state: "49",
    name: "Summit County",
    position: {
      lat: 40.87206,
      lng: -110.968486
    },
    stateName: "Utah"
  },
  {
    id: "045",
    state: "49",
    name: "Tooele County",
    position: {
      lat: 40.467692,
      lng: -113.124015
    },
    stateName: "Utah"
  },
  {
    id: "049",
    state: "49",
    name: "Utah County",
    position: {
      lat: 40.120409,
      lng: -111.668667
    },
    stateName: "Utah"
  },
  {
    id: "057",
    state: "49",
    name: "Weber County",
    position: {
      lat: 41.270355,
      lng: -111.875879
    },
    stateName: "Utah"
  },
  {
    id: "003",
    state: "50",
    name: "Bennington County",
    position: {
      lat: 43.035325,
      lng: -73.11146
    },
    stateName: "Vermont"
  },
  {
    id: "007",
    state: "50",
    name: "Chittenden County",
    position: {
      lat: 44.460676,
      lng: -73.070525
    },
    stateName: "Vermont"
  },
  {
    id: "011",
    state: "50",
    name: "Franklin County",
    position: {
      lat: 44.858964,
      lng: -72.909402
    },
    stateName: "Vermont"
  },
  {
    id: "015",
    state: "50",
    name: "Lamoille County",
    position: {
      lat: 44.603504,
      lng: -72.638356
    },
    stateName: "Vermont"
  },
  {
    id: "021",
    state: "50",
    name: "Rutland County",
    position: {
      lat: 43.580844,
      lng: -73.038196
    },
    stateName: "Vermont"
  },
  {
    id: "023",
    state: "56",
    name: "Lincoln County",
    position: {
      lat: 42.228788,
      lng: -110.679842
    },
    stateName: "Wyoming"
  },
  {
    id: "005",
    state: "51",
    name: "Alleghany County",
    position: {
      lat: 37.787905,
      lng: -80.008669
    },
    stateName: "Virginia"
  },
  {
    id: "011",
    state: "51",
    name: "Appomattox County",
    position: {
      lat: 37.370725,
      lng: -78.81094
    },
    stateName: "Virginia"
  },
  {
    id: "013",
    state: "51",
    name: "Arlington County",
    position: {
      lat: 38.878337,
      lng: -77.100703
    },
    stateName: "Virginia"
  },
  {
    id: "015",
    state: "51",
    name: "Augusta County",
    position: {
      lat: 38.167807,
      lng: -79.146682
    },
    stateName: "Virginia"
  },
  {
    id: "019",
    state: "51",
    name: "Bedford County",
    position: {
      lat: 37.312408,
      lng: -79.527947
    },
    stateName: "Virginia"
  },
  {
    id: "025",
    state: "51",
    name: "Brunswick County",
    position: {
      lat: 36.764531,
      lng: -77.860916
    },
    stateName: "Virginia"
  },
  {
    id: "031",
    state: "51",
    name: "Campbell County",
    position: {
      lat: 37.210151,
      lng: -79.095429
    },
    stateName: "Virginia"
  },
  {
    id: "033",
    state: "51",
    name: "Caroline County",
    position: {
      lat: 38.030319,
      lng: -77.352348
    },
    stateName: "Virginia"
  },
  {
    id: "036",
    state: "51",
    name: "Charles City County",
    position: {
      lat: 37.361048,
      lng: -77.054171
    },
    stateName: "Virginia"
  },
  {
    id: "045",
    state: "51",
    name: "Craig County",
    position: {
      lat: 37.473129,
      lng: -80.231734
    },
    stateName: "Virginia"
  },
  {
    id: "049",
    state: "51",
    name: "Cumberland County",
    position: {
      lat: 37.520189,
      lng: -78.252836
    },
    stateName: "Virginia"
  },
  {
    id: "051",
    state: "51",
    name: "Dickenson County",
    position: {
      lat: 37.137081,
      lng: -82.349208
    },
    stateName: "Virginia"
  },
  {
    id: "081",
    state: "54",
    name: "Raleigh County",
    position: {
      lat: 37.76247,
      lng: -81.264671
    },
    stateName: "West Virginia"
  },
  {
    id: "061",
    state: "51",
    name: "Fauquier County",
    position: {
      lat: 38.744103,
      lng: -77.821585
    },
    stateName: "Virginia"
  },
  {
    id: "067",
    state: "51",
    name: "Franklin County",
    position: {
      lat: 36.99235,
      lng: -79.883959
    },
    stateName: "Virginia"
  },
  {
    id: "069",
    state: "51",
    name: "Frederick County",
    position: {
      lat: 39.203637,
      lng: -78.263916
    },
    stateName: "Virginia"
  },
  {
    id: "073",
    state: "51",
    name: "Gloucester County",
    position: {
      lat: 37.403541,
      lng: -76.523505
    },
    stateName: "Virginia"
  },
  {
    id: "081",
    state: "51",
    name: "Greensville County",
    position: {
      lat: 36.680225,
      lng: -77.560261
    },
    stateName: "Virginia"
  },
  {
    id: "085",
    state: "51",
    name: "Hanover County",
    position: {
      lat: 37.760166,
      lng: -77.490992
    },
    stateName: "Virginia"
  },
  {
    id: "091",
    state: "51",
    name: "Highland County",
    position: {
      lat: 38.356672,
      lng: -79.567958
    },
    stateName: "Virginia"
  },
  {
    id: "095",
    state: "51",
    name: "James City County",
    position: {
      lat: 37.324427,
      lng: -76.778319
    },
    stateName: "Virginia"
  },
  {
    id: "103",
    state: "51",
    name: "Lancaster County",
    position: {
      lat: 37.703831,
      lng: -76.413199
    },
    stateName: "Virginia"
  },
  {
    id: "037",
    state: "49",
    name: "San Juan County",
    position: {
      lat: 37.623064,
      lng: -109.78932
    },
    stateName: "Utah"
  },
  {
    id: "119",
    state: "51",
    name: "Middlesex County",
    position: {
      lat: 37.606828,
      lng: -76.527958
    },
    stateName: "Virginia"
  },
  {
    id: "025",
    state: "50",
    name: "Windham County",
    position: {
      lat: 42.999143,
      lng: -72.716335
    },
    stateName: "Vermont"
  },
  {
    id: "037",
    state: "53",
    name: "Kittitas County",
    position: {
      lat: 47.124444,
      lng: -120.676714
    },
    stateName: "Washington"
  },
  {
    id: "039",
    state: "53",
    name: "Klickitat County",
    position: {
      lat: 45.869509,
      lng: -120.780117
    },
    stateName: "Washington"
  },
  {
    id: "059",
    state: "53",
    name: "Skamania County",
    position: {
      lat: 46.024782,
      lng: -121.953227
    },
    stateName: "Washington"
  },
  {
    id: "075",
    state: "53",
    name: "Whitman County",
    position: {
      lat: 46.903322,
      lng: -117.522962
    },
    stateName: "Washington"
  },
  {
    id: "017",
    state: "54",
    name: "Doddridge County",
    position: {
      lat: 39.263482,
      lng: -80.701147
    },
    stateName: "West Virginia"
  },
  {
    id: "029",
    state: "54",
    name: "Hancock County",
    position: {
      lat: 40.516931,
      lng: -80.570057
    },
    stateName: "West Virginia"
  },
  {
    id: "043",
    state: "54",
    name: "Lincoln County",
    position: {
      lat: 38.171651,
      lng: -82.077547
    },
    stateName: "West Virginia"
  },
  {
    id: "055",
    state: "54",
    name: "Mercer County",
    position: {
      lat: 37.403448,
      lng: -81.106456
    },
    stateName: "West Virginia"
  },
  {
    id: "073",
    state: "54",
    name: "Pleasants County",
    position: {
      lat: 39.368133,
      lng: -81.161172
    },
    stateName: "West Virginia"
  },
  {
    id: "085",
    state: "54",
    name: "Ritchie County",
    position: {
      lat: 39.177112,
      lng: -81.066317
    },
    stateName: "West Virginia"
  },
  {
    id: "095",
    state: "54",
    name: "Tyler County",
    position: {
      lat: 39.465981,
      lng: -80.879493
    },
    stateName: "West Virginia"
  },
  {
    id: "057",
    state: "51",
    name: "Essex County",
    position: {
      lat: 37.93948,
      lng: -76.941871
    },
    stateName: "Virginia"
  },
  {
    id: "115",
    state: "51",
    name: "Mathews County",
    position: {
      lat: 37.425348,
      lng: -76.268808
    },
    stateName: "Virginia"
  },
  {
    id: "095",
    state: "22",
    name: "St. John Baptist County",
    postal: "Parish",
    position: {
      lat: 30.117471,
      lng: -90.504677
    },
    stateName: "Louisiana"
  },
  {
    id: "111",
    state: "22",
    name: "Union County",
    postal: "Parish",
    position: {
      lat: 32.829349,
      lng: -92.37565
    },
    stateName: "Louisiana"
  },
  {
    id: "001",
    state: "21",
    name: "Adair County",
    position: {
      lat: 37.105541,
      lng: -85.281401
    },
    stateName: "Kentucky"
  },
  {
    id: "005",
    state: "21",
    name: "Anderson County",
    position: {
      lat: 38.005396,
      lng: -84.986417
    },
    stateName: "Kentucky"
  },
  {
    id: "007",
    state: "21",
    name: "Ballard County",
    position: {
      lat: 37.05112,
      lng: -89.009178
    },
    stateName: "Kentucky"
  },
  {
    id: "019",
    state: "21",
    name: "Boyd County",
    position: {
      lat: 38.360004,
      lng: -82.681406
    },
    stateName: "Kentucky"
  },
  {
    id: "037",
    state: "21",
    name: "Campbell County",
    position: {
      lat: 38.946981,
      lng: -84.379583
    },
    stateName: "Kentucky"
  },
  {
    id: "049",
    state: "21",
    name: "Clark County",
    position: {
      lat: 37.970133,
      lng: -84.144974
    },
    stateName: "Kentucky"
  },
  {
    id: "061",
    state: "21",
    name: "Edmonson County",
    position: {
      lat: 37.227856,
      lng: -86.217778
    },
    stateName: "Kentucky"
  },
  {
    id: "069",
    state: "21",
    name: "Fleming County",
    position: {
      lat: 38.368431,
      lng: -83.706152
    },
    stateName: "Kentucky"
  },
  {
    id: "081",
    state: "21",
    name: "Grant County",
    position: {
      lat: 38.64921,
      lng: -84.625946
    },
    stateName: "Kentucky"
  },
  {
    id: "093",
    state: "21",
    name: "Hardin County",
    position: {
      lat: 37.695836,
      lng: -85.963183
    },
    stateName: "Kentucky"
  },
  {
    id: "109",
    state: "21",
    name: "Jackson County",
    position: {
      lat: 37.40332,
      lng: -84.020686
    },
    stateName: "Kentucky"
  },
  {
    id: "125",
    state: "21",
    name: "Laurel County",
    position: {
      lat: 37.113268,
      lng: -84.119395
    },
    stateName: "Kentucky"
  },
  {
    id: "131",
    state: "21",
    name: "Leslie County",
    position: {
      lat: 37.087846,
      lng: -83.388616
    },
    stateName: "Kentucky"
  },
  {
    id: "037",
    state: "40",
    name: "Creek County",
    position: {
      lat: 35.907732,
      lng: -96.379793
    },
    stateName: "Oklahoma"
  },
  {
    id: "047",
    state: "40",
    name: "Garfield County",
    position: {
      lat: 36.378273,
      lng: -97.787729
    },
    stateName: "Oklahoma"
  },
  {
    id: "183",
    state: "37",
    name: "Wake County",
    position: {
      lat: 35.789846,
      lng: -78.650624
    },
    stateName: "North Carolina"
  },
  {
    id: "187",
    state: "37",
    name: "Washington County",
    position: {
      lat: 35.844589,
      lng: -76.572334
    },
    stateName: "North Carolina"
  },
  {
    id: "003",
    state: "38",
    name: "Barnes County",
    position: {
      lat: 46.94255,
      lng: -98.070195
    },
    stateName: "North Dakota"
  },
  {
    id: "013",
    state: "38",
    name: "Burke County",
    position: {
      lat: 48.786453,
      lng: -102.520087
    },
    stateName: "North Dakota"
  },
  {
    id: "025",
    state: "38",
    name: "Dunn County",
    position: {
      lat: 47.35461,
      lng: -102.612365
    },
    stateName: "North Dakota"
  },
  {
    id: "041",
    state: "38",
    name: "Hettinger County",
    position: {
      lat: 46.434939,
      lng: -102.458385
    },
    stateName: "North Dakota"
  },
  {
    id: "055",
    state: "38",
    name: "Mclean County",
    position: {
      lat: 47.653055,
      lng: -101.421794
    },
    stateName: "North Dakota"
  },
  {
    id: "069",
    state: "38",
    name: "Pierce County",
    position: {
      lat: 48.238883,
      lng: -99.966497
    },
    stateName: "North Dakota"
  },
  {
    id: "087",
    state: "38",
    name: "Slope County",
    position: {
      lat: 46.445834,
      lng: -103.462462
    },
    stateName: "North Dakota"
  },
  {
    id: "095",
    state: "38",
    name: "Towner County",
    position: {
      lat: 48.682183,
      lng: -99.248158
    },
    stateName: "North Dakota"
  },
  {
    id: "009",
    state: "39",
    name: "Athens County",
    position: {
      lat: 39.333848,
      lng: -82.046008
    },
    stateName: "Ohio"
  },
  {
    id: "021",
    state: "39",
    name: "Champaign County",
    position: {
      lat: 40.132759,
      lng: -83.767543
    },
    stateName: "Ohio"
  },
  {
    id: "109",
    state: "54",
    name: "Wyoming County",
    position: {
      lat: 37.603717,
      lng: -81.548884
    },
    stateName: "West Virginia"
  },
  {
    id: "015",
    state: "55",
    name: "Calumet County",
    position: {
      lat: 44.07841,
      lng: -88.212132
    },
    stateName: "Wisconsin"
  },
  {
    id: "027",
    state: "55",
    name: "Dodge County",
    position: {
      lat: 43.422706,
      lng: -88.704379
    },
    stateName: "Wisconsin"
  },
  {
    id: "027",
    state: "49",
    name: "Millard County",
    position: {
      lat: 39.09454,
      lng: -113.525014
    },
    stateName: "Utah"
  },
  {
    id: "033",
    state: "49",
    name: "Rich County",
    position: {
      lat: 41.627598,
      lng: -111.240227
    },
    stateName: "Utah"
  },
  {
    id: "041",
    state: "49",
    name: "Sevier County",
    position: {
      lat: 38.746825,
      lng: -111.81193
    },
    stateName: "Utah"
  },
  {
    id: "055",
    state: "49",
    name: "Wayne County",
    position: {
      lat: 38.261229,
      lng: -110.990323
    },
    stateName: "Utah"
  },
  {
    id: "013",
    state: "50",
    name: "Grand Isle County",
    position: {
      lat: 44.801788,
      lng: -73.300758
    },
    stateName: "Vermont"
  },
  {
    id: "003",
    state: "51",
    name: "Albemarle County",
    position: {
      lat: 38.024184,
      lng: -78.553506
    },
    stateName: "Virginia"
  },
  {
    id: "009",
    state: "51",
    name: "Amherst County",
    position: {
      lat: 37.630362,
      lng: -79.147848
    },
    stateName: "Virginia"
  },
  {
    id: "023",
    state: "51",
    name: "Botetourt County",
    position: {
      lat: 37.553193,
      lng: -79.805318
    },
    stateName: "Virginia"
  },
  {
    id: "035",
    state: "51",
    name: "Carroll County",
    position: {
      lat: 36.732426,
      lng: -80.728043
    },
    stateName: "Virginia"
  },
  {
    id: "053",
    state: "51",
    name: "Dinwiddie County",
    position: {
      lat: 37.073498,
      lng: -77.635492
    },
    stateName: "Virginia"
  },
  {
    id: "065",
    state: "51",
    name: "Fluvanna County",
    position: {
      lat: 37.830606,
      lng: -78.284445
    },
    stateName: "Virginia"
  },
  {
    id: "003",
    state: "01",
    name: "Baldwin County",
    position: {
      lat: 30.659218,
      lng: -87.746067
    },
    stateName: "Alabama"
  },
  {
    id: "027",
    state: "01",
    name: "Clay County",
    position: {
      lat: 33.267809,
      lng: -85.862051
    },
    stateName: "Alabama"
  },
  {
    id: "041",
    state: "01",
    name: "Crenshaw County",
    position: {
      lat: 31.732826,
      lng: -86.319222
    },
    stateName: "Alabama"
  },
  {
    id: "057",
    state: "01",
    name: "Fayette County",
    position: {
      lat: 33.732249,
      lng: -87.752049
    },
    stateName: "Alabama"
  },
  {
    id: "073",
    state: "01",
    name: "Jefferson County",
    position: {
      lat: 33.553444,
      lng: -86.896536
    },
    stateName: "Alabama"
  },
  {
    id: "091",
    state: "01",
    name: "Marengo County",
    position: {
      lat: 32.247591,
      lng: -87.791091
    },
    stateName: "Alabama"
  },
  {
    id: "103",
    state: "01",
    name: "Morgan County",
    position: {
      lat: 34.454484,
      lng: -86.846402
    },
    stateName: "Alabama"
  },
  {
    id: "115",
    state: "01",
    name: "St. Clair County",
    position: {
      lat: 33.712963,
      lng: -86.315663
    },
    stateName: "Alabama"
  },
  {
    id: "129",
    state: "01",
    name: "Washington County",
    position: {
      lat: 31.406974,
      lng: -88.202078
    },
    stateName: "Alabama"
  },
  {
    id: "147",
    state: "21",
    name: "Mc Creary County",
    position: {
      lat: 36.731136,
      lng: -84.491052
    },
    stateName: "Kentucky"
  },
  {
    id: "165",
    state: "21",
    name: "Menifee County",
    position: {
      lat: 37.950715,
      lng: -83.597345
    },
    stateName: "Kentucky"
  },
  {
    id: "181",
    state: "21",
    name: "Nicholas County",
    position: {
      lat: 38.337132,
      lng: -84.025554
    },
    stateName: "Kentucky"
  },
  {
    id: "041",
    state: "19",
    name: "Clay County",
    position: {
      lat: 43.079822,
      lng: -95.149726
    },
    stateName: "Iowa"
  },
  {
    id: "043",
    state: "19",
    name: "Clayton County",
    position: {
      lat: 42.840998,
      lng: -91.323511
    },
    stateName: "Iowa"
  },
  {
    id: "049",
    state: "19",
    name: "Dallas County",
    position: {
      lat: 41.685321,
      lng: -94.040707
    },
    stateName: "Iowa"
  },
  {
    id: "057",
    state: "19",
    name: "Des Moines County",
    position: {
      lat: 40.915339,
      lng: -91.186925
    },
    stateName: "Iowa"
  },
  {
    id: "073",
    state: "19",
    name: "Greene County",
    position: {
      lat: 42.042494,
      lng: -94.388703
    },
    stateName: "Iowa"
  },
  {
    id: "081",
    state: "19",
    name: "Hancock County",
    position: {
      lat: 43.075411,
      lng: -93.743697
    },
    stateName: "Iowa"
  },
  {
    id: "091",
    state: "19",
    name: "Humboldt County",
    position: {
      lat: 42.782221,
      lng: -94.202775
    },
    stateName: "Iowa"
  },
  {
    id: "099",
    state: "19",
    name: "Jasper County",
    position: {
      lat: 41.685686,
      lng: -93.052971
    },
    stateName: "Iowa"
  },
  {
    id: "115",
    state: "19",
    name: "Louisa County",
    position: {
      lat: 41.218211,
      lng: -91.256994
    },
    stateName: "Iowa"
  },
  {
    id: "131",
    state: "19",
    name: "Mitchell County",
    position: {
      lat: 43.348564,
      lng: -92.784466
    },
    stateName: "Iowa"
  },
  {
    id: "147",
    state: "19",
    name: "Palo Alto County",
    position: {
      lat: 43.075854,
      lng: -94.667297
    },
    stateName: "Iowa"
  },
  {
    id: "157",
    state: "19",
    name: "Poweshiek County",
    position: {
      lat: 41.684526,
      lng: -92.522882
    },
    stateName: "Iowa"
  },
  {
    id: "165",
    state: "19",
    name: "Shelby County",
    position: {
      lat: 41.679014,
      lng: -95.308917
    },
    stateName: "Iowa"
  },
  {
    id: "179",
    state: "19",
    name: "Wapello County",
    position: {
      lat: 41.031263,
      lng: -92.409482
    },
    stateName: "Iowa"
  },
  {
    id: "187",
    state: "19",
    name: "Webster County",
    position: {
      lat: 42.434397,
      lng: -94.179157
    },
    stateName: "Iowa"
  },
  {
    id: "197",
    state: "19",
    name: "Wright County",
    position: {
      lat: 42.733007,
      lng: -93.734735
    },
    stateName: "Iowa"
  },
  {
    id: "031",
    state: "39",
    name: "Coshocton County",
    position: {
      lat: 40.29672,
      lng: -81.930112
    },
    stateName: "Ohio"
  },
  {
    id: "045",
    state: "39",
    name: "Fairfield County",
    position: {
      lat: 39.752935,
      lng: -82.628276
    },
    stateName: "Ohio"
  },
  {
    id: "013",
    state: "35",
    name: "Dona Ana County",
    position: {
      lat: 32.350912,
      lng: -106.832182
    },
    stateName: "New Mexico"
  },
  {
    id: "017",
    state: "35",
    name: "Grant County",
    position: {
      lat: 32.732087,
      lng: -108.381504
    },
    stateName: "New Mexico"
  },
  {
    id: "037",
    state: "35",
    name: "Quay County",
    position: {
      lat: 35.107018,
      lng: -103.548071
    },
    stateName: "New Mexico"
  },
  {
    id: "059",
    state: "35",
    name: "Union County",
    position: {
      lat: 36.488085,
      lng: -103.475723
    },
    stateName: "New Mexico"
  },
  {
    id: "017",
    state: "36",
    name: "Chenango County",
    position: {
      lat: 42.489732,
      lng: -75.604905
    },
    stateName: "New York"
  },
  {
    id: "031",
    state: "36",
    name: "Essex County",
    position: {
      lat: 44.108971,
      lng: -73.777573
    },
    stateName: "New York"
  },
  {
    id: "049",
    state: "36",
    name: "Lewis County",
    position: {
      lat: 43.786397,
      lng: -75.442617
    },
    stateName: "New York"
  },
  {
    id: "065",
    state: "36",
    name: "Oneida County",
    position: {
      lat: 43.242727,
      lng: -75.434282
    },
    stateName: "New York"
  },
  {
    id: "077",
    state: "36",
    name: "Otsego County",
    position: {
      lat: 42.629776,
      lng: -75.028841
    },
    stateName: "New York"
  },
  {
    id: "079",
    state: "51",
    name: "Greene County",
    position: {
      lat: 38.297981,
      lng: -78.470163
    },
    stateName: "Virginia"
  },
  {
    id: "129",
    state: "12",
    name: "Wakulla County",
    position: {
      lat: 30.140378,
      lng: -84.375136
    },
    stateName: "Florida"
  },
  {
    id: "011",
    state: "13",
    name: "Banks County",
    position: {
      lat: 34.351922,
      lng: -83.498441
    },
    stateName: "Georgia"
  },
  {
    id: "019",
    state: "13",
    name: "Berrien County",
    position: {
      lat: 31.274308,
      lng: -83.231906
    },
    stateName: "Georgia"
  },
  {
    id: "027",
    state: "13",
    name: "Brooks County",
    position: {
      lat: 30.833696,
      lng: -83.588971
    },
    stateName: "Georgia"
  },
  {
    id: "033",
    state: "13",
    name: "Burke County",
    position: {
      lat: 33.063737,
      lng: -82.00389
    },
    stateName: "Georgia"
  },
  {
    id: "039",
    state: "13",
    name: "Camden County",
    position: {
      lat: 30.916358,
      lng: -81.636516
    },
    stateName: "Georgia"
  },
  {
    id: "051",
    state: "13",
    name: "Chatham County",
    position: {
      lat: 31.974756,
      lng: -81.091768
    },
    stateName: "Georgia"
  },
  {
    id: "061",
    state: "13",
    name: "Clay County",
    position: {
      lat: 31.619831,
      lng: -84.992583
    },
    stateName: "Georgia"
  },
  {
    id: "149",
    state: "05",
    name: "Yell County",
    position: {
      lat: 34.997713,
      lng: -93.408303
    },
    stateName: "Arkansas"
  },
  {
    id: "011",
    state: "06",
    name: "Colusa County",
    position: {
      lat: 39.177739,
      lng: -122.237563
    },
    stateName: "California"
  },
  {
    id: "027",
    state: "06",
    name: "Inyo County",
    position: {
      lat: 36.561977,
      lng: -117.403927
    },
    stateName: "California"
  },
  {
    id: "047",
    state: "06",
    name: "Merced County",
    position: {
      lat: 37.194806,
      lng: -120.722802
    },
    stateName: "California"
  },
  {
    id: "063",
    state: "06",
    name: "Plumas County",
    position: {
      lat: 39.99517,
      lng: -120.829516
    },
    stateName: "California"
  },
  {
    id: "077",
    state: "06",
    name: "San Joaquin County",
    position: {
      lat: 37.935034,
      lng: -121.272237
    },
    stateName: "California"
  },
  {
    id: "091",
    state: "06",
    name: "Sierra County",
    position: {
      lat: 39.576925,
      lng: -120.521993
    },
    stateName: "California"
  },
  {
    id: "107",
    state: "06",
    name: "Tulare County",
    position: {
      lat: 36.230453,
      lng: -118.780542
    },
    stateName: "California"
  },
  {
    id: "011",
    state: "08",
    name: "Bent County",
    position: {
      lat: 37.931891,
      lng: -103.077584
    },
    stateName: "Colorado"
  },
  {
    id: "019",
    state: "08",
    name: "Clear Creek County",
    position: {
      lat: 39.689403,
      lng: -105.670791
    },
    stateName: "Colorado"
  },
  {
    id: "027",
    state: "08",
    name: "Custer County",
    position: {
      lat: 38.101994,
      lng: -105.373515
    },
    stateName: "Colorado"
  },
  {
    id: "013",
    state: "20",
    name: "Brown County",
    position: {
      lat: 39.825931,
      lng: -95.569905
    },
    stateName: "Kansas"
  },
  {
    id: "023",
    state: "20",
    name: "Cheyenne County",
    position: {
      lat: 39.789256,
      lng: -101.727302
    },
    stateName: "Kansas"
  },
  {
    id: "071",
    state: "27",
    name: "Koochiching County",
    position: {
      lat: 48.24549,
      lng: -93.782842
    },
    stateName: "Minnesota"
  },
  {
    id: "079",
    state: "27",
    name: "Le Sueur County",
    position: {
      lat: 44.373397,
      lng: -93.73018
    },
    stateName: "Minnesota"
  },
  {
    id: "087",
    state: "27",
    name: "Mahnomen County",
    position: {
      lat: 47.325842,
      lng: -95.810703
    },
    stateName: "Minnesota"
  },
  {
    id: "101",
    state: "27",
    name: "Murray County",
    position: {
      lat: 44.015594,
      lng: -95.761581
    },
    stateName: "Minnesota"
  },
  {
    id: "107",
    state: "27",
    name: "Norman County",
    position: {
      lat: 47.32947,
      lng: -96.463807
    },
    stateName: "Minnesota"
  },
  {
    id: "125",
    state: "27",
    name: "Red Lake County",
    position: {
      lat: 47.865487,
      lng: -96.08718
    },
    stateName: "Minnesota"
  },
  {
    id: "131",
    state: "27",
    name: "Rice County",
    position: {
      lat: 44.350943,
      lng: -93.298503
    },
    stateName: "Minnesota"
  },
  {
    id: "143",
    state: "27",
    name: "Sibley County",
    position: {
      lat: 44.575734,
      lng: -94.230123
    },
    stateName: "Minnesota"
  },
  {
    id: "159",
    state: "27",
    name: "Wadena County",
    position: {
      lat: 46.586784,
      lng: -94.988331
    },
    stateName: "Minnesota"
  },
  {
    id: "169",
    state: "27",
    name: "Winona County",
    position: {
      lat: 43.982268,
      lng: -91.776708
    },
    stateName: "Minnesota"
  },
  {
    id: "007",
    state: "28",
    name: "Attala County",
    position: {
      lat: 33.09047,
      lng: -89.588622
    },
    stateName: "Mississippi"
  },
  {
    id: "023",
    state: "28",
    name: "Clarke County",
    position: {
      lat: 32.048448,
      lng: -88.685964
    },
    stateName: "Mississippi"
  },
  {
    id: "035",
    state: "28",
    name: "Forrest County",
    position: {
      lat: 31.18858,
      lng: -89.259447
    },
    stateName: "Mississippi"
  },
  {
    id: "043",
    state: "28",
    name: "Grenada County",
    position: {
      lat: 33.770031,
      lng: -89.80274
    },
    stateName: "Mississippi"
  },
  {
    id: "091",
    state: "36",
    name: "Saratoga County",
    position: {
      lat: 43.106135,
      lng: -73.855387
    },
    stateName: "New York"
  },
  {
    id: "101",
    state: "36",
    name: "Steuben County",
    position: {
      lat: 42.266725,
      lng: -77.385525
    },
    stateName: "New York"
  },
  {
    id: "113",
    state: "36",
    name: "Warren County",
    position: {
      lat: 43.555105,
      lng: -73.838139
    },
    stateName: "New York"
  },
  {
    id: "011",
    state: "42",
    name: "Berks County",
    position: {
      lat: 40.413957,
      lng: -75.92686
    },
    stateName: "Pennsylvania"
  },
  {
    id: "019",
    state: "42",
    name: "Butler County",
    position: {
      lat: 40.913834,
      lng: -79.91896
    },
    stateName: "Pennsylvania"
  },
  {
    id: "025",
    state: "42",
    name: "Carbon County",
    position: {
      lat: 40.917833,
      lng: -75.709428
    },
    stateName: "Pennsylvania"
  },
  {
    id: "037",
    state: "42",
    name: "Columbia County",
    position: {
      lat: 41.045517,
      lng: -76.40426
    },
    stateName: "Pennsylvania"
  },
  {
    id: "053",
    state: "42",
    name: "Forest County",
    position: {
      lat: 41.513304,
      lng: -79.249705
    },
    stateName: "Pennsylvania"
  },
  {
    id: "057",
    state: "42",
    name: "Fulton County",
    position: {
      lat: 39.910751,
      lng: -78.122617
    },
    stateName: "Pennsylvania"
  },
  {
    id: "075",
    state: "42",
    name: "Lebanon County",
    position: {
      lat: 40.367344,
      lng: -76.458009
    },
    stateName: "Pennsylvania"
  },
  {
    id: "081",
    state: "42",
    name: "Lycoming County",
    position: {
      lat: 41.343624,
      lng: -77.055253
    },
    stateName: "Pennsylvania"
  },
  {
    id: "099",
    state: "42",
    name: "Perry County",
    position: {
      lat: 40.39778,
      lng: -77.266328
    },
    stateName: "Pennsylvania"
  },
  {
    id: "117",
    state: "42",
    name: "Tioga County",
    position: {
      lat: 41.766859,
      lng: -77.257288
    },
    stateName: "Pennsylvania"
  },
  {
    id: "131",
    state: "42",
    name: "Wyoming County",
    position: {
      lat: 41.525137,
      lng: -76.00878
    },
    stateName: "Pennsylvania"
  },
  {
    id: "005",
    state: "01",
    name: "Barbour County",
    position: {
      lat: 31.87067,
      lng: -85.405456
    },
    stateName: "Alabama"
  },
  {
    id: "013",
    state: "01",
    name: "Butler County",
    position: {
      lat: 31.751667,
      lng: -86.681969
    },
    stateName: "Alabama"
  },
  {
    id: "019",
    state: "01",
    name: "Cherokee County",
    position: {
      lat: 34.069515,
      lng: -85.654242
    },
    stateName: "Alabama"
  },
  {
    id: "041",
    state: "06",
    name: "Marin County",
    position: {
      lat: 38.051817,
      lng: -122.745974
    },
    stateName: "California"
  },
  {
    id: "053",
    state: "06",
    name: "Monterey County",
    position: {
      lat: 36.240107,
      lng: -121.315573
    },
    stateName: "California"
  },
  {
    id: "061",
    state: "06",
    name: "Placer County",
    position: {
      lat: 39.062032,
      lng: -120.722718
    },
    stateName: "California"
  },
  {
    id: "041",
    state: "08",
    name: "El Paso County",
    position: {
      lat: 38.827383,
      lng: -104.527472
    },
    stateName: "Colorado"
  },
  {
    id: "049",
    state: "08",
    name: "Grand County",
    position: {
      lat: 40.123289,
      lng: -106.095876
    },
    stateName: "Colorado"
  },
  {
    id: "085",
    state: "12",
    name: "Martin County",
    position: {
      lat: 27.079954,
      lng: -80.398211
    },
    stateName: "Florida"
  },
  {
    id: "093",
    state: "12",
    name: "Okeechobee County",
    position: {
      lat: 27.385592,
      lng: -80.887388
    },
    stateName: "Florida"
  },
  {
    id: "105",
    state: "12",
    name: "Polk County",
    position: {
      lat: 27.953115,
      lng: -81.692783
    },
    stateName: "Florida"
  },
  {
    id: "121",
    state: "12",
    name: "Suwannee County",
    position: {
      lat: 30.189244,
      lng: -82.992754
    },
    stateName: "Florida"
  },
  {
    id: "133",
    state: "12",
    name: "Washington County",
    position: {
      lat: 30.602217,
      lng: -85.662797
    },
    stateName: "Florida"
  },
  {
    id: "007",
    state: "13",
    name: "Baker County",
    position: {
      lat: 31.319622,
      lng: -84.454857
    },
    stateName: "Georgia"
  },
  {
    id: "021",
    state: "13",
    name: "Bibb County",
    position: {
      lat: 32.808844,
      lng: -83.694193
    },
    stateName: "Georgia"
  },
  {
    id: "035",
    state: "13",
    name: "Butts County",
    position: {
      lat: 33.290355,
      lng: -83.958221
    },
    stateName: "Georgia"
  },
  {
    id: "047",
    state: "13",
    name: "Catoosa County",
    position: {
      lat: 34.899393,
      lng: -85.137353
    },
    stateName: "Georgia"
  },
  {
    id: "065",
    state: "13",
    name: "Clinch County",
    position: {
      lat: 30.917653,
      lng: -82.702614
    },
    stateName: "Georgia"
  },
  {
    id: "075",
    state: "13",
    name: "Cook County",
    position: {
      lat: 31.152935,
      lng: -83.429552
    },
    stateName: "Georgia"
  },
  {
    id: "057",
    state: "28",
    name: "Itawamba County",
    position: {
      lat: 34.281075,
      lng: -88.363127
    },
    stateName: "Mississippi"
  },
  {
    id: "069",
    state: "28",
    name: "Kemper County",
    position: {
      lat: 32.750136,
      lng: -88.625631
    },
    stateName: "Mississippi"
  },
  {
    id: "119",
    state: "22",
    name: "Webster County",
    postal: "Parish",
    position: {
      lat: 32.732152,
      lng: -93.339825
    },
    stateName: "Louisiana"
  },
  {
    id: "125",
    state: "22",
    name: "West Feliciana County",
    postal: "Parish",
    position: {
      lat: 30.872701,
      lng: -91.421008
    },
    stateName: "Louisiana"
  },
  {
    id: "017",
    state: "23",
    name: "Oxford County",
    position: {
      lat: 44.494585,
      lng: -70.734688
    },
    stateName: "Maine"
  },
  {
    id: "015",
    state: "24",
    name: "Cecil County",
    position: {
      lat: 39.562352,
      lng: -75.941584
    },
    stateName: "Maryland"
  },
  {
    id: "027",
    state: "24",
    name: "Howard County",
    position: {
      lat: 39.252264,
      lng: -76.924406
    },
    stateName: "Maryland"
  },
  {
    id: "001",
    state: "25",
    name: "Barnstable County",
    position: {
      lat: 41.798819,
      lng: -70.211083
    },
    stateName: "Massachusetts"
  },
  {
    id: "003",
    state: "25",
    name: "Berkshire County",
    position: {
      lat: 42.375314,
      lng: -73.213948
    },
    stateName: "Massachusetts"
  },
  {
    id: "027",
    state: "25",
    name: "Worcester County",
    position: {
      lat: 42.311693,
      lng: -71.940282
    },
    stateName: "Massachusetts"
  },
  {
    id: "025",
    state: "26",
    name: "Calhoun County",
    position: {
      lat: 42.24299,
      lng: -85.012385
    },
    stateName: "Michigan"
  },
  {
    id: "039",
    state: "26",
    name: "Crawford County",
    position: {
      lat: 44.680208,
      lng: -84.611132
    },
    stateName: "Michigan"
  },
  {
    id: "049",
    state: "26",
    name: "Genesee County",
    position: {
      lat: 43.021077,
      lng: -83.706372
    },
    stateName: "Michigan"
  },
  {
    id: "007",
    state: "44",
    name: "Providence County",
    position: {
      lat: 41.870488,
      lng: -71.578242
    },
    stateName: "Rhode Island"
  },
  {
    id: "021",
    state: "45",
    name: "Cherokee County",
    position: {
      lat: 35.049796,
      lng: -81.607647
    },
    stateName: "South Carolina"
  },
  {
    id: "033",
    state: "45",
    name: "Dillon County",
    position: {
      lat: 34.390172,
      lng: -79.374964
    },
    stateName: "South Carolina"
  },
  {
    id: "045",
    state: "45",
    name: "Greenville County",
    position: {
      lat: 34.892645,
      lng: -82.372077
    },
    stateName: "South Carolina"
  },
  {
    id: "009",
    state: "47",
    name: "Blount County",
    position: {
      lat: 35.688185,
      lng: -83.922973
    },
    stateName: "Tennessee"
  },
  {
    id: "019",
    state: "47",
    name: "Carter County",
    position: {
      lat: 36.284744,
      lng: -82.126593
    },
    stateName: "Tennessee"
  },
  {
    id: "037",
    state: "47",
    name: "Davidson County",
    position: {
      lat: 36.169129,
      lng: -86.78479
    },
    stateName: "Tennessee"
  },
  {
    id: "051",
    state: "47",
    name: "Franklin County",
    position: {
      lat: 35.155926,
      lng: -86.099203
    },
    stateName: "Tennessee"
  },
  {
    id: "063",
    state: "47",
    name: "Hamblen County",
    position: {
      lat: 36.218397,
      lng: -83.266071
    },
    stateName: "Tennessee"
  },
  {
    id: "075",
    state: "47",
    name: "Haywood County",
    position: {
      lat: 35.58669,
      lng: -89.282536
    },
    stateName: "Tennessee"
  },
  {
    id: "085",
    state: "47",
    name: "Humphreys County",
    position: {
      lat: 36.04044,
      lng: -87.790625
    },
    stateName: "Tennessee"
  },
  {
    id: "099",
    state: "47",
    name: "Lawrence County",
    position: {
      lat: 35.220476,
      lng: -87.396546
    },
    stateName: "Tennessee"
  },
  {
    id: "113",
    state: "47",
    name: "Madison County",
    position: {
      lat: 35.606056,
      lng: -88.833424
    },
    stateName: "Tennessee"
  },
  {
    id: "127",
    state: "47",
    name: "Moore County",
    position: {
      lat: 35.288889,
      lng: -86.358684
    },
    stateName: "Tennessee"
  },
  {
    id: "019",
    state: "06",
    name: "Fresno County",
    position: {
      lat: 36.761006,
      lng: -119.655019
    },
    stateName: "California"
  },
  {
    id: "023",
    state: "06",
    name: "Humboldt County",
    position: {
      lat: 40.706673,
      lng: -123.925818
    },
    stateName: "California"
  },
  {
    id: "035",
    state: "06",
    name: "Lassen County",
    position: {
      lat: 40.721089,
      lng: -120.629931
    },
    stateName: "California"
  },
  {
    id: "037",
    state: "06",
    name: "Los Angeles County",
    position: {
      lat: 34.196398,
      lng: -118.261862
    },
    stateName: "California"
  },
  {
    id: "085",
    state: "13",
    name: "Dawson County",
    position: {
      lat: 34.442842,
      lng: -84.173284
    },
    stateName: "Georgia"
  },
  {
    id: "099",
    state: "13",
    name: "Early County",
    position: {
      lat: 31.324191,
      lng: -84.906723
    },
    stateName: "Georgia"
  },
  {
    id: "117",
    state: "13",
    name: "Forsyth County",
    position: {
      lat: 34.225143,
      lng: -84.127336
    },
    stateName: "Georgia"
  },
  {
    id: "051",
    state: "08",
    name: "Gunnison County",
    position: {
      lat: 38.669679,
      lng: -107.078108
    },
    stateName: "Colorado"
  },
  {
    id: "055",
    state: "08",
    name: "Huerfano County",
    position: {
      lat: 37.687815,
      lng: -104.959928
    },
    stateName: "Colorado"
  },
  {
    id: "065",
    state: "08",
    name: "Lake County",
    position: {
      lat: 39.204316,
      lng: -106.349696
    },
    stateName: "Colorado"
  },
  {
    id: "079",
    state: "08",
    name: "Mineral County",
    position: {
      lat: 37.651478,
      lng: -106.9323
    },
    stateName: "Colorado"
  },
  {
    id: "089",
    state: "08",
    name: "Otero County",
    position: {
      lat: 37.88417,
      lng: -103.72126
    },
    stateName: "Colorado"
  },
  {
    id: "101",
    state: "08",
    name: "Pueblo County",
    position: {
      lat: 38.170658,
      lng: -104.489893
    },
    stateName: "Colorado"
  },
  {
    id: "117",
    state: "08",
    name: "Summit County",
    position: {
      lat: 39.621023,
      lng: -106.137555
    },
    stateName: "Colorado"
  },
  {
    id: "005",
    state: "09",
    name: "Litchfield County",
    position: {
      lat: 41.791897,
      lng: -73.235428
    },
    stateName: "Connecticut"
  },
  {
    id: "007",
    state: "12",
    name: "Bradford County",
    position: {
      lat: 29.946934,
      lng: -82.166796
    },
    stateName: "Florida"
  },
  {
    id: "019",
    state: "12",
    name: "Clay County",
    position: {
      lat: 29.987116,
      lng: -81.858147
    },
    stateName: "Florida"
  },
  {
    id: "027",
    state: "12",
    name: "De Soto County",
    position: {
      lat: 27.190581,
      lng: -81.806253
    },
    stateName: "Florida"
  },
  {
    id: "035",
    state: "12",
    name: "Flagler County",
    position: {
      lat: 29.474894,
      lng: -81.286362
    },
    stateName: "Florida"
  },
  {
    id: "059",
    state: "26",
    name: "Hillsdale County",
    position: {
      lat: 41.864475,
      lng: -84.642409
    },
    stateName: "Michigan"
  },
  {
    id: "063",
    state: "26",
    name: "Huron County",
    position: {
      lat: 43.907616,
      lng: -82.857045
    },
    stateName: "Michigan"
  },
  {
    id: "113",
    state: "26",
    name: "Missaukee County",
    position: {
      lat: 44.325424,
      lng: -85.085471
    },
    stateName: "Michigan"
  },
  {
    id: "119",
    state: "26",
    name: "Montmorency County",
    position: {
      lat: 45.024134,
      lng: -84.130107
    },
    stateName: "Michigan"
  },
  {
    id: "129",
    state: "26",
    name: "Ogemaw County",
    position: {
      lat: 44.33328,
      lng: -84.128073
    },
    stateName: "Michigan"
  },
  {
    id: "137",
    state: "26",
    name: "Otsego County",
    position: {
      lat: 45.021794,
      lng: -84.576597
    },
    stateName: "Michigan"
  },
  {
    id: "149",
    state: "26",
    name: "St. Joseph County",
    position: {
      lat: 41.911488,
      lng: -85.52287
    },
    stateName: "Michigan"
  },
  {
    id: "005",
    state: "27",
    name: "Becker County",
    position: {
      lat: 46.937629,
      lng: -95.741757
    },
    stateName: "Minnesota"
  },
  {
    id: "013",
    state: "27",
    name: "Blue Earth County",
    position: {
      lat: 44.038225,
      lng: -94.064071
    },
    stateName: "Minnesota"
  },
  {
    id: "023",
    state: "27",
    name: "Chippewa County",
    position: {
      lat: 45.028625,
      lng: -95.564108
    },
    stateName: "Minnesota"
  },
  {
    id: "033",
    state: "27",
    name: "Cottonwood County",
    position: {
      lat: 44.010711,
      lng: -95.18313
    },
    stateName: "Minnesota"
  },
  {
    id: "047",
    state: "27",
    name: "Freeborn County",
    position: {
      lat: 43.674202,
      lng: -93.350289
    },
    stateName: "Minnesota"
  },
  {
    id: "061",
    state: "27",
    name: "Itasca County",
    position: {
      lat: 47.490843,
      lng: -93.613128
    },
    stateName: "Minnesota"
  },
  {
    id: "095",
    state: "29",
    name: "Jackson County",
    position: {
      lat: 39.007233,
      lng: -94.342507
    },
    stateName: "Missouri"
  },
  {
    id: "133",
    state: "47",
    name: "Overton County",
    position: {
      lat: 36.34485,
      lng: -85.283076
    },
    stateName: "Tennessee"
  },
  {
    id: "145",
    state: "47",
    name: "Roane County",
    position: {
      lat: 35.847472,
      lng: -84.523861
    },
    stateName: "Tennessee"
  },
  {
    id: "159",
    state: "47",
    name: "Smith County",
    position: {
      lat: 36.25565,
      lng: -85.942078
    },
    stateName: "Tennessee"
  },
  {
    id: "165",
    state: "47",
    name: "Sumner County",
    position: {
      lat: 36.470015,
      lng: -86.458517
    },
    stateName: "Tennessee"
  },
  {
    id: "047",
    state: "45",
    name: "Greenwood County",
    position: {
      lat: 34.155796,
      lng: -82.127876
    },
    stateName: "South Carolina"
  },
  {
    id: "051",
    state: "45",
    name: "Horry County",
    position: {
      lat: 33.909269,
      lng: -78.976675
    },
    stateName: "South Carolina"
  },
  {
    id: "059",
    state: "45",
    name: "Laurens County",
    position: {
      lat: 34.483477,
      lng: -82.005657
    },
    stateName: "South Carolina"
  },
  {
    id: "069",
    state: "45",
    name: "Marlboro County",
    position: {
      lat: 34.601805,
      lng: -79.677942
    },
    stateName: "South Carolina"
  },
  {
    id: "087",
    state: "45",
    name: "Union County",
    position: {
      lat: 34.690514,
      lng: -81.615831
    },
    stateName: "South Carolina"
  },
  {
    id: "015",
    state: "46",
    name: "Brule County",
    position: {
      lat: 43.72988,
      lng: -99.092941
    },
    stateName: "South Dakota"
  },
  {
    id: "025",
    state: "46",
    name: "Clark County",
    position: {
      lat: 44.855211,
      lng: -97.724912
    },
    stateName: "South Dakota"
  },
  {
    id: "037",
    state: "46",
    name: "Day County",
    position: {
      lat: 45.362283,
      lng: -97.593734
    },
    stateName: "South Dakota"
  },
  {
    id: "049",
    state: "46",
    name: "Faulk County",
    position: {
      lat: 45.065476,
      lng: -99.153564
    },
    stateName: "South Dakota"
  },
  {
    id: "057",
    state: "46",
    name: "Hamlin County",
    position: {
      lat: 44.680619,
      lng: -97.178598
    },
    stateName: "South Dakota"
  },
  {
    id: "067",
    state: "46",
    name: "Hutchinson County",
    position: {
      lat: 43.33671,
      lng: -97.749383
    },
    stateName: "South Dakota"
  },
  {
    id: "077",
    state: "46",
    name: "Kingsbury County",
    position: {
      lat: 44.362969,
      lng: -97.499313
    },
    stateName: "South Dakota"
  },
  {
    id: "097",
    state: "51",
    name: "King And Queen County",
    position: {
      lat: 37.720995,
      lng: -76.89109
    },
    stateName: "Virginia"
  },
  {
    id: "109",
    state: "51",
    name: "Louisa County",
    position: {
      lat: 37.971681,
      lng: -77.959178
    },
    stateName: "Virginia"
  },
  {
    id: "035",
    state: "01",
    name: "Conecuh County",
    position: {
      lat: 31.428293,
      lng: -86.992029
    },
    stateName: "Alabama"
  },
  {
    id: "039",
    state: "01",
    name: "Covington County",
    position: {
      lat: 31.243987,
      lng: -86.448721
    },
    stateName: "Alabama"
  },
  {
    id: "049",
    state: "01",
    name: "De Kalb County",
    position: {
      lat: 34.460929,
      lng: -85.803992
    },
    stateName: "Alabama"
  },
  {
    id: "053",
    state: "01",
    name: "Escambia County",
    position: {
      lat: 31.121748,
      lng: -87.168429
    },
    stateName: "Alabama"
  },
  {
    id: "063",
    state: "01",
    name: "Greene County",
    position: {
      lat: 32.844497,
      lng: -87.964201
    },
    stateName: "Alabama"
  },
  {
    id: "069",
    state: "01",
    name: "Houston County",
    position: {
      lat: 31.158193,
      lng: -85.296398
    },
    stateName: "Alabama"
  },
  {
    id: "075",
    state: "01",
    name: "Lamar County",
    position: {
      lat: 33.787085,
      lng: -88.087431
    },
    stateName: "Alabama"
  },
  {
    id: "077",
    state: "01",
    name: "Lauderdale County",
    position: {
      lat: 34.904122,
      lng: -87.650997
    },
    stateName: "Alabama"
  },
  {
    id: "047",
    state: "12",
    name: "Hamilton County",
    position: {
      lat: 30.491102,
      lng: -82.951049
    },
    stateName: "Florida"
  },
  {
    id: "067",
    state: "12",
    name: "Lafayette County",
    position: {
      lat: 29.990066,
      lng: -83.17851
    },
    stateName: "Florida"
  },
  {
    id: "077",
    state: "12",
    name: "Liberty County",
    position: {
      lat: 30.259849,
      lng: -84.868581
    },
    stateName: "Florida"
  },
  {
    id: "001",
    state: "04",
    name: "Apache County",
    position: {
      lat: 35.385845,
      lng: -109.493747
    },
    stateName: "Arizona"
  },
  {
    id: "007",
    state: "04",
    name: "Gila County",
    position: {
      lat: 33.789618,
      lng: -110.81187
    },
    stateName: "Arizona"
  },
  {
    id: "019",
    state: "04",
    name: "Pima County",
    position: {
      lat: 32.128237,
      lng: -111.783018
    },
    stateName: "Arizona"
  },
  {
    id: "011",
    state: "05",
    name: "Bradley County",
    position: {
      lat: 33.463819,
      lng: -92.168164
    },
    stateName: "Arkansas"
  },
  {
    id: "023",
    state: "05",
    name: "Cleburne County",
    position: {
      lat: 35.566288,
      lng: -92.059974
    },
    stateName: "Arkansas"
  },
  {
    id: "037",
    state: "05",
    name: "Cross County",
    position: {
      lat: 35.291259,
      lng: -90.773894
    },
    stateName: "Arkansas"
  },
  {
    id: "039",
    state: "05",
    name: "Dallas County",
    position: {
      lat: 33.967823,
      lng: -92.653999
    },
    stateName: "Arkansas"
  },
  {
    id: "055",
    state: "05",
    name: "Greene County",
    position: {
      lat: 36.119922,
      lng: -90.565241
    },
    stateName: "Arkansas"
  },
  {
    id: "065",
    state: "05",
    name: "Izard County",
    position: {
      lat: 36.094879,
      lng: -91.913625
    },
    stateName: "Arkansas"
  },
  {
    id: "079",
    state: "05",
    name: "Lincoln County",
    position: {
      lat: 33.957665,
      lng: -91.727624
    },
    stateName: "Arkansas"
  },
  {
    id: "099",
    state: "29",
    name: "Jefferson County",
    position: {
      lat: 38.257414,
      lng: -90.543138
    },
    stateName: "Missouri"
  },
  {
    id: "115",
    state: "29",
    name: "Linn County",
    position: {
      lat: 39.86444,
      lng: -93.108019
    },
    stateName: "Missouri"
  },
  {
    id: "121",
    state: "29",
    name: "Macon County",
    position: {
      lat: 39.829795,
      lng: -92.56434
    },
    stateName: "Missouri"
  },
  {
    id: "135",
    state: "29",
    name: "Moniteau County",
    position: {
      lat: 38.633037,
      lng: -92.583642
    },
    stateName: "Missouri"
  },
  {
    id: "145",
    state: "29",
    name: "Newton County",
    position: {
      lat: 36.908017,
      lng: -94.334741
    },
    stateName: "Missouri"
  },
  {
    id: "163",
    state: "29",
    name: "Pike County",
    position: {
      lat: 39.344178,
      lng: -91.171042
    },
    stateName: "Missouri"
  },
  {
    id: "173",
    state: "29",
    name: "Ralls County",
    position: {
      lat: 39.553455,
      lng: -91.524787
    },
    stateName: "Missouri"
  },
  {
    id: "185",
    state: "29",
    name: "St. Clair County",
    position: {
      lat: 38.039069,
      lng: -93.773077
    },
    stateName: "Missouri"
  },
  {
    id: "201",
    state: "29",
    name: "Scott County",
    position: {
      lat: 37.047793,
      lng: -89.568098
    },
    stateName: "Missouri"
  },
  {
    id: "215",
    state: "29",
    name: "Texas County",
    position: {
      lat: 37.314257,
      lng: -91.964478
    },
    stateName: "Missouri"
  },
  {
    id: "223",
    state: "29",
    name: "Wayne County",
    position: {
      lat: 37.113825,
      lng: -90.460868
    },
    stateName: "Missouri"
  },
  {
    id: "007",
    state: "30",
    name: "Broadwater County",
    position: {
      lat: 46.334476,
      lng: -111.496103
    },
    stateName: "Montana"
  },
  {
    id: "021",
    state: "30",
    name: "Dawson County",
    position: {
      lat: 47.272425,
      lng: -104.901027
    },
    stateName: "Montana"
  },
  {
    id: "031",
    state: "30",
    name: "Gallatin County",
    position: {
      lat: 45.535559,
      lng: -111.173443
    },
    stateName: "Montana"
  },
  {
    id: "091",
    state: "46",
    name: "Marshall County",
    position: {
      lat: 45.737054,
      lng: -97.580884
    },
    stateName: "South Dakota"
  },
  {
    id: "107",
    state: "46",
    name: "Potter County",
    position: {
      lat: 45.064276,
      lng: -99.949631
    },
    stateName: "South Dakota"
  },
  {
    id: "119",
    state: "46",
    name: "Sully County",
    position: {
      lat: 44.722325,
      lng: -100.131399
    },
    stateName: "South Dakota"
  },
  {
    id: "129",
    state: "46",
    name: "Walworth County",
    position: {
      lat: 45.427605,
      lng: -100.027856
    },
    stateName: "South Dakota"
  },
  {
    id: "057",
    state: "40",
    name: "Harmon County",
    position: {
      lat: 34.745971,
      lng: -99.844194
    },
    stateName: "Oklahoma"
  },
  {
    id: "061",
    state: "40",
    name: "Haskell County",
    position: {
      lat: 35.232294,
      lng: -95.109578
    },
    stateName: "Oklahoma"
  },
  {
    id: "069",
    state: "40",
    name: "Johnston County",
    position: {
      lat: 34.313455,
      lng: -96.654255
    },
    stateName: "Oklahoma"
  },
  {
    id: "075",
    state: "40",
    name: "Kiowa County",
    position: {
      lat: 34.921489,
      lng: -98.981617
    },
    stateName: "Oklahoma"
  },
  {
    id: "093",
    state: "40",
    name: "Major County",
    position: {
      lat: 36.313119,
      lng: -98.542015
    },
    stateName: "Oklahoma"
  },
  {
    id: "103",
    state: "40",
    name: "Noble County",
    position: {
      lat: 36.384901,
      lng: -97.236335
    },
    stateName: "Oklahoma"
  },
  {
    id: "115",
    state: "40",
    name: "Ottawa County",
    position: {
      lat: 36.835764,
      lng: -94.802681
    },
    stateName: "Oklahoma"
  },
  {
    id: "133",
    state: "40",
    name: "Seminole County",
    position: {
      lat: 35.158366,
      lng: -96.602858
    },
    stateName: "Oklahoma"
  },
  {
    id: "141",
    state: "40",
    name: "Tillman County",
    position: {
      lat: 34.371085,
      lng: -98.931701
    },
    stateName: "Oklahoma"
  },
  {
    id: "009",
    state: "41",
    name: "Columbia County",
    position: {
      lat: 45.941932,
      lng: -123.081079
    },
    stateName: "Oregon"
  },
  {
    id: "067",
    state: "06",
    name: "Sacramento County",
    position: {
      lat: 38.450011,
      lng: -121.340441
    },
    stateName: "California"
  },
  {
    id: "071",
    state: "06",
    name: "San Bernardino County",
    position: {
      lat: 34.85722,
      lng: -116.181197
    },
    stateName: "California"
  },
  {
    id: "111",
    state: "06",
    name: "Ventura County",
    position: {
      lat: 34.358742,
      lng: -119.133143
    },
    stateName: "California"
  },
  {
    id: "005",
    state: "08",
    name: "Arapahoe County",
    position: {
      lat: 39.644632,
      lng: -104.331733
    },
    stateName: "Colorado"
  },
  {
    id: "109",
    state: "05",
    name: "Pike County",
    position: {
      lat: 34.158191,
      lng: -93.658659
    },
    stateName: "Arkansas"
  },
  {
    id: "117",
    state: "05",
    name: "Prairie County",
    position: {
      lat: 34.828225,
      lng: -91.5572
    },
    stateName: "Arkansas"
  },
  {
    id: "135",
    state: "05",
    name: "Sharp County",
    position: {
      lat: 36.173399,
      lng: -91.471069
    },
    stateName: "Arkansas"
  },
  {
    id: "307",
    state: "13",
    name: "Webster County",
    position: {
      lat: 32.046554,
      lng: -84.553218
    },
    stateName: "Georgia"
  },
  {
    id: "311",
    state: "13",
    name: "White County",
    position: {
      lat: 34.643677,
      lng: -83.743713
    },
    stateName: "Georgia"
  },
  {
    id: "315",
    state: "13",
    name: "Wilcox County",
    position: {
      lat: 31.962717,
      lng: -83.438262
    },
    stateName: "Georgia"
  },
  {
    id: "005",
    state: "16",
    name: "Bannock County",
    position: {
      lat: 42.692939,
      lng: -112.228986
    },
    stateName: "Idaho"
  },
  {
    id: "027",
    state: "16",
    name: "Canyon County",
    position: {
      lat: 43.623051,
      lng: -116.708527
    },
    stateName: "Idaho"
  },
  {
    id: "039",
    state: "16",
    name: "Elmore County",
    position: {
      lat: 43.394826,
      lng: -115.470751
    },
    stateName: "Idaho"
  },
  {
    id: "063",
    state: "16",
    name: "Lincoln County",
    position: {
      lat: 42.986181,
      lng: -114.153899
    },
    stateName: "Idaho"
  },
  {
    id: "075",
    state: "16",
    name: "Payette County",
    position: {
      lat: 44.00274,
      lng: -116.748655
    },
    stateName: "Idaho"
  },
  {
    id: "001",
    state: "17",
    name: "Adams County",
    position: {
      lat: 39.986053,
      lng: -91.194961
    },
    stateName: "Illinois"
  },
  {
    id: "017",
    state: "17",
    name: "Cass County",
    position: {
      lat: 39.969202,
      lng: -90.245705
    },
    stateName: "Illinois"
  },
  {
    id: "047",
    state: "30",
    name: "Lake County",
    position: {
      lat: 47.642901,
      lng: -114.083687
    },
    stateName: "Montana"
  },
  {
    id: "123",
    state: "31",
    name: "Morrill County",
    position: {
      lat: 41.732205,
      lng: -102.990599
    },
    stateName: "Nebraska"
  },
  {
    id: "127",
    state: "31",
    name: "Nemaha County",
    position: {
      lat: 40.387448,
      lng: -95.850954
    },
    stateName: "Nebraska"
  },
  {
    id: "143",
    state: "31",
    name: "Polk County",
    position: {
      lat: 41.187925,
      lng: -97.570663
    },
    stateName: "Nebraska"
  },
  {
    id: "153",
    state: "31",
    name: "Sarpy County",
    position: {
      lat: 41.115064,
      lng: -96.109125
    },
    stateName: "Nebraska"
  },
  {
    id: "167",
    state: "31",
    name: "Stanton County",
    position: {
      lat: 41.904793,
      lng: -97.174724
    },
    stateName: "Nebraska"
  },
  {
    id: "179",
    state: "31",
    name: "Wayne County",
    position: {
      lat: 42.210746,
      lng: -97.126243
    },
    stateName: "Nebraska"
  },
  {
    id: "185",
    state: "31",
    name: "York County",
    position: {
      lat: 40.873056,
      lng: -97.596742
    },
    stateName: "Nebraska"
  },
  {
    id: "011",
    state: "32",
    name: "Eureka County",
    position: {
      lat: 39.977788,
      lng: -116.272208
    },
    stateName: "Nevada"
  },
  {
    id: "027",
    state: "32",
    name: "Pershing County",
    position: {
      lat: 40.439639,
      lng: -118.409477
    },
    stateName: "Nevada"
  },
  {
    id: "005",
    state: "33",
    name: "Cheshire County",
    position: {
      lat: 42.925455,
      lng: -72.248217
    },
    stateName: "New Hampshire"
  },
  {
    id: "017",
    state: "33",
    name: "Strafford County",
    position: {
      lat: 43.293177,
      lng: -71.035927
    },
    stateName: "New Hampshire"
  },
  {
    id: "027",
    state: "34",
    name: "Morris County",
    position: {
      lat: 40.858581,
      lng: -74.547427
    },
    stateName: "New Jersey"
  },
  {
    id: "037",
    state: "34",
    name: "Sussex County",
    position: {
      lat: 41.137424,
      lng: -74.691855
    },
    stateName: "New Jersey"
  },
  {
    id: "023",
    state: "41",
    name: "Grant County",
    position: {
      lat: 44.496326,
      lng: -119.014057
    },
    stateName: "Oregon"
  },
  {
    id: "047",
    state: "41",
    name: "Marion County",
    position: {
      lat: 44.900898,
      lng: -122.57626
    },
    stateName: "Oregon"
  },
  {
    id: "061",
    state: "41",
    name: "Union County",
    position: {
      lat: 45.304047,
      lng: -117.999136
    },
    stateName: "Oregon"
  },
  {
    id: "009",
    state: "42",
    name: "Bedford County",
    position: {
      lat: 39.997115,
      lng: -78.48998
    },
    stateName: "Pennsylvania"
  },
  {
    id: "355",
    state: "48",
    name: "Nueces County",
    position: {
      lat: 27.739406,
      lng: -97.521643
    },
    stateName: "Texas"
  },
  {
    id: "363",
    state: "48",
    name: "Palo Pinto County",
    position: {
      lat: 32.75221,
      lng: -98.317974
    },
    stateName: "Texas"
  },
  {
    id: "373",
    state: "48",
    name: "Polk County",
    position: {
      lat: 30.784553,
      lng: -94.837338
    },
    stateName: "Texas"
  },
  {
    id: "389",
    state: "48",
    name: "Reeves County",
    position: {
      lat: 31.308366,
      lng: -103.712706
    },
    stateName: "Texas"
  },
  {
    id: "405",
    state: "48",
    name: "San Augustine County",
    position: {
      lat: 31.382449,
      lng: -94.16318
    },
    stateName: "Texas"
  },
  {
    id: "413",
    state: "48",
    name: "Schleicher County",
    position: {
      lat: 30.896233,
      lng: -100.527216
    },
    stateName: "Texas"
  },
  {
    id: "421",
    state: "48",
    name: "Sherman County",
    position: {
      lat: 36.277628,
      lng: -101.894716
    },
    stateName: "Texas"
  },
  {
    id: "439",
    state: "48",
    name: "Tarrant County",
    position: {
      lat: 32.77204,
      lng: -97.291291
    },
    stateName: "Texas"
  },
  {
    id: "455",
    state: "48",
    name: "Trinity County",
    position: {
      lat: 31.087483,
      lng: -95.153291
    },
    stateName: "Texas"
  },
  {
    id: "471",
    state: "48",
    name: "Walker County",
    position: {
      lat: 30.74309,
      lng: -95.569888
    },
    stateName: "Texas"
  },
  {
    id: "083",
    state: "13",
    name: "Dade County",
    position: {
      lat: 34.852424,
      lng: -85.506201
    },
    stateName: "Georgia"
  },
  {
    id: "089",
    state: "13",
    name: "De Kalb County",
    position: {
      lat: 33.770661,
      lng: -84.226343
    },
    stateName: "Georgia"
  },
  {
    id: "097",
    state: "13",
    name: "Douglas County",
    position: {
      lat: 33.699317,
      lng: -84.765944
    },
    stateName: "Georgia"
  },
  {
    id: "105",
    state: "13",
    name: "Elbert County",
    position: {
      lat: 34.115017,
      lng: -82.842057
    },
    stateName: "Georgia"
  },
  {
    id: "111",
    state: "13",
    name: "Fannin County",
    position: {
      lat: 34.863837,
      lng: -84.319287
    },
    stateName: "Georgia"
  },
  {
    id: "121",
    state: "13",
    name: "Fulton County",
    position: {
      lat: 33.790034,
      lng: -84.468182
    },
    stateName: "Georgia"
  },
  {
    id: "067",
    state: "08",
    name: "La Plata County",
    position: {
      lat: 37.287367,
      lng: -107.839718
    },
    stateName: "Colorado"
  },
  {
    id: "071",
    state: "08",
    name: "Las Animas County",
    position: {
      lat: 37.318831,
      lng: -104.04411
    },
    stateName: "Colorado"
  },
  {
    id: "095",
    state: "05",
    name: "Monroe County",
    position: {
      lat: 34.679513,
      lng: -91.203314
    },
    stateName: "Arkansas"
  },
  {
    id: "025",
    state: "17",
    name: "Clay County",
    position: {
      lat: 38.747312,
      lng: -88.483789
    },
    stateName: "Illinois"
  },
  {
    id: "039",
    state: "17",
    name: "De Witt County",
    position: {
      lat: 40.181499,
      lng: -88.901853
    },
    stateName: "Illinois"
  },
  {
    id: "045",
    state: "17",
    name: "Edgar County",
    position: {
      lat: 39.679037,
      lng: -87.74711
    },
    stateName: "Illinois"
  },
  {
    id: "061",
    state: "17",
    name: "Greene County",
    position: {
      lat: 39.355444,
      lng: -90.387757
    },
    stateName: "Illinois"
  },
  {
    id: "069",
    state: "17",
    name: "Hardin County",
    position: {
      lat: 37.517852,
      lng: -88.266148
    },
    stateName: "Illinois"
  },
  {
    id: "049",
    state: "18",
    name: "Fulton County",
    position: {
      lat: 41.050384,
      lng: -86.265006
    },
    stateName: "Indiana"
  },
  {
    id: "053",
    state: "18",
    name: "Grant County",
    position: {
      lat: 40.515758,
      lng: -85.654946
    },
    stateName: "Indiana"
  },
  {
    id: "063",
    state: "18",
    name: "Hendricks County",
    position: {
      lat: 39.768749,
      lng: -86.510287
    },
    stateName: "Indiana"
  },
  {
    id: "075",
    state: "18",
    name: "Jay County",
    position: {
      lat: 40.434972,
      lng: -85.00338
    },
    stateName: "Indiana"
  },
  {
    id: "093",
    state: "18",
    name: "Lawrence County",
    position: {
      lat: 38.839815,
      lng: -86.48782
    },
    stateName: "Indiana"
  },
  {
    id: "099",
    state: "18",
    name: "Marshall County",
    position: {
      lat: 41.325003,
      lng: -86.269036
    },
    stateName: "Indiana"
  },
  {
    id: "113",
    state: "18",
    name: "Noble County",
    position: {
      lat: 41.400794,
      lng: -85.41785
    },
    stateName: "Indiana"
  },
  {
    id: "119",
    state: "18",
    name: "Owen County",
    position: {
      lat: 39.317339,
      lng: -86.838845
    },
    stateName: "Indiana"
  },
  {
    id: "133",
    state: "18",
    name: "Putnam County",
    position: {
      lat: 39.665545,
      lng: -86.853325
    },
    stateName: "Indiana"
  },
  {
    id: "145",
    state: "18",
    name: "Shelby County",
    position: {
      lat: 39.524135,
      lng: -85.792174
    },
    stateName: "Indiana"
  },
  {
    id: "157",
    state: "18",
    name: "Tippecanoe County",
    position: {
      lat: 40.38926,
      lng: -86.893943
    },
    stateName: "Indiana"
  },
  {
    id: "011",
    state: "35",
    name: "De Baca County",
    position: {
      lat: 34.351429,
      lng: -104.401527
    },
    stateName: "New Mexico"
  },
  {
    id: "049",
    state: "30",
    name: "Lewis And Clark County",
    position: {
      lat: 47.122133,
      lng: -112.382954
    },
    stateName: "Montana"
  },
  {
    id: "053",
    state: "30",
    name: "Lincoln County",
    position: {
      lat: 48.542232,
      lng: -115.404343
    },
    stateName: "Montana"
  },
  {
    id: "075",
    state: "30",
    name: "Powder River County",
    position: {
      lat: 45.40889,
      lng: -105.555259
    },
    stateName: "Montana"
  },
  {
    id: "085",
    state: "30",
    name: "Roosevelt County",
    position: {
      lat: 48.282747,
      lng: -104.99517
    },
    stateName: "Montana"
  },
  {
    id: "001",
    state: "31",
    name: "Adams County",
    position: {
      lat: 40.520632,
      lng: -98.500044
    },
    stateName: "Nebraska"
  },
  {
    id: "005",
    state: "31",
    name: "Arthur County",
    position: {
      lat: 41.570856,
      lng: -101.697045
    },
    stateName: "Nebraska"
  },
  {
    id: "017",
    state: "31",
    name: "Brown County",
    position: {
      lat: 42.34939,
      lng: -99.923176
    },
    stateName: "Nebraska"
  },
  {
    id: "027",
    state: "31",
    name: "Cedar County",
    position: {
      lat: 42.604511,
      lng: -97.256824
    },
    stateName: "Nebraska"
  },
  {
    id: "041",
    state: "31",
    name: "Custer County",
    position: {
      lat: 41.393893,
      lng: -99.726866
    },
    stateName: "Nebraska"
  },
  {
    id: "059",
    state: "31",
    name: "Fillmore County",
    position: {
      lat: 40.52504,
      lng: -97.596705
    },
    stateName: "Nebraska"
  },
  {
    id: "069",
    state: "31",
    name: "Garden County",
    position: {
      lat: 41.659381,
      lng: -102.328229
    },
    stateName: "Nebraska"
  },
  {
    id: "077",
    state: "31",
    name: "Greeley County",
    position: {
      lat: 41.5676,
      lng: -98.530566
    },
    stateName: "Nebraska"
  },
  {
    id: "089",
    state: "31",
    name: "Holt County",
    position: {
      lat: 42.459287,
      lng: -98.784766
    },
    stateName: "Nebraska"
  },
  {
    id: "481",
    state: "48",
    name: "Wharton County",
    position: {
      lat: 29.278481,
      lng: -96.229675
    },
    stateName: "Texas"
  },
  {
    id: "493",
    state: "48",
    name: "Wilson County",
    position: {
      lat: 29.174303,
      lng: -98.085899
    },
    stateName: "Texas"
  },
  {
    id: "503",
    state: "48",
    name: "Young County",
    position: {
      lat: 33.158787,
      lng: -98.678267
    },
    stateName: "Texas"
  },
  {
    id: "005",
    state: "49",
    name: "Cache County",
    position: {
      lat: 41.734225,
      lng: -111.744581
    },
    stateName: "Utah"
  },
  {
    id: "017",
    state: "49",
    name: "Garfield County",
    position: {
      lat: 37.831622,
      lng: -111.450886
    },
    stateName: "Utah"
  },
  {
    id: "183",
    state: "47",
    name: "Weakley County",
    position: {
      lat: 36.303523,
      lng: -88.720785
    },
    stateName: "Tennessee"
  },
  {
    id: "187",
    state: "47",
    name: "Williamson County",
    position: {
      lat: 35.894972,
      lng: -86.896958
    },
    stateName: "Tennessee"
  },
  {
    id: "003",
    state: "48",
    name: "Andrews County",
    position: {
      lat: 32.312258,
      lng: -102.640206
    },
    stateName: "Texas"
  },
  {
    id: "019",
    state: "48",
    name: "Bandera County",
    position: {
      lat: 29.755748,
      lng: -99.260682
    },
    stateName: "Texas"
  },
  {
    id: "027",
    state: "48",
    name: "Bell County",
    position: {
      lat: 31.04211,
      lng: -97.481921
    },
    stateName: "Texas"
  },
  {
    id: "041",
    state: "48",
    name: "Brazos County",
    position: {
      lat: 30.656725,
      lng: -96.302389
    },
    stateName: "Texas"
  },
  {
    id: "059",
    state: "48",
    name: "Callahan County",
    position: {
      lat: 32.29315,
      lng: -99.372249
    },
    stateName: "Texas"
  },
  {
    id: "075",
    state: "48",
    name: "Childress County",
    position: {
      lat: 34.529337,
      lng: -100.208336
    },
    stateName: "Texas"
  },
  {
    id: "083",
    state: "48",
    name: "Coleman County",
    position: {
      lat: 31.914205,
      lng: -99.346622
    },
    stateName: "Texas"
  },
  {
    id: "089",
    state: "48",
    name: "Colorado County",
    position: {
      lat: 29.595908,
      lng: -96.508389
    },
    stateName: "Texas"
  },
  {
    id: "001",
    state: "06",
    name: "Alameda County",
    position: {
      lat: 37.648081,
      lng: -121.913304
    },
    stateName: "California"
  },
  {
    id: "105",
    state: "01",
    name: "Perry County",
    position: {
      lat: 32.639005,
      lng: -87.293827
    },
    stateName: "Alabama"
  },
  {
    id: "111",
    state: "01",
    name: "Randolph County",
    position: {
      lat: 33.296475,
      lng: -85.464068
    },
    stateName: "Alabama"
  },
  {
    id: "113",
    state: "01",
    name: "Russell County",
    position: {
      lat: 32.289811,
      lng: -85.18698
    },
    stateName: "Alabama"
  },
  {
    id: "123",
    state: "01",
    name: "Tallapoosa County",
    position: {
      lat: 32.863369,
      lng: -85.799553
    },
    stateName: "Alabama"
  },
  {
    id: "165",
    state: "18",
    name: "Vermillion County",
    position: {
      lat: 39.854045,
      lng: -87.462071
    },
    stateName: "Indiana"
  },
  {
    id: "003",
    state: "19",
    name: "Adams County",
    position: {
      lat: 41.021656,
      lng: -94.696906
    },
    stateName: "Iowa"
  },
  {
    id: "013",
    state: "19",
    name: "Black Hawk County",
    position: {
      lat: 42.472888,
      lng: -92.306059
    },
    stateName: "Iowa"
  },
  {
    id: "025",
    state: "19",
    name: "Calhoun County",
    position: {
      lat: 42.38617,
      lng: -94.643683
    },
    stateName: "Iowa"
  },
  {
    id: "035",
    state: "19",
    name: "Cherokee County",
    position: {
      lat: 42.742738,
      lng: -95.633262
    },
    stateName: "Iowa"
  },
  {
    id: "071",
    state: "17",
    name: "Henderson County",
    position: {
      lat: 40.815141,
      lng: -90.93848
    },
    stateName: "Illinois"
  },
  {
    id: "075",
    state: "17",
    name: "Iroquois County",
    position: {
      lat: 40.748867,
      lng: -87.833601
    },
    stateName: "Illinois"
  },
  {
    id: "087",
    state: "17",
    name: "Johnson County",
    position: {
      lat: 37.460815,
      lng: -88.882962
    },
    stateName: "Illinois"
  },
  {
    id: "095",
    state: "17",
    name: "Knox County",
    position: {
      lat: 40.930941,
      lng: -90.213761
    },
    stateName: "Illinois"
  },
  {
    id: "113",
    state: "17",
    name: "Mc Lean County",
    position: {
      lat: 40.494559,
      lng: -88.844539
    },
    stateName: "Illinois"
  },
  {
    id: "123",
    state: "17",
    name: "Marshall County",
    position: {
      lat: 41.031119,
      lng: -89.342371
    },
    stateName: "Illinois"
  },
  {
    id: "135",
    state: "17",
    name: "Montgomery County",
    position: {
      lat: 39.228092,
      lng: -89.478007
    },
    stateName: "Illinois"
  },
  {
    id: "151",
    state: "17",
    name: "Pope County",
    position: {
      lat: 37.417169,
      lng: -88.542374
    },
    stateName: "Illinois"
  },
  {
    id: "163",
    state: "17",
    name: "St. Clair County",
    position: {
      lat: 38.470198,
      lng: -89.928546
    },
    stateName: "Illinois"
  },
  {
    id: "187",
    state: "17",
    name: "Warren County",
    position: {
      lat: 40.850441,
      lng: -90.620223
    },
    stateName: "Illinois"
  },
  {
    id: "199",
    state: "17",
    name: "Williamson County",
    position: {
      lat: 37.730353,
      lng: -88.930018
    },
    stateName: "Illinois"
  },
  {
    id: "107",
    state: "31",
    name: "Knox County",
    position: {
      lat: 42.634405,
      lng: -97.891349
    },
    stateName: "Nebraska"
  },
  {
    id: "077",
    state: "28",
    name: "Lawrence County",
    position: {
      lat: 31.550009,
      lng: -90.10753
    },
    stateName: "Mississippi"
  },
  {
    id: "081",
    state: "28",
    name: "Lee County",
    position: {
      lat: 34.288965,
      lng: -88.680887
    },
    stateName: "Mississippi"
  },
  {
    id: "085",
    state: "28",
    name: "Lincoln County",
    position: {
      lat: 31.535216,
      lng: -90.453566
    },
    stateName: "Mississippi"
  },
  {
    id: "087",
    state: "28",
    name: "Lowndes County",
    position: {
      lat: 33.471424,
      lng: -88.439723
    },
    stateName: "Mississippi"
  },
  {
    id: "107",
    state: "28",
    name: "Panola County",
    position: {
      lat: 34.365205,
      lng: -89.963065
    },
    stateName: "Mississippi"
  },
  {
    id: "123",
    state: "28",
    name: "Scott County",
    position: {
      lat: 32.427127,
      lng: -89.533209
    },
    stateName: "Mississippi"
  },
  {
    id: "133",
    state: "28",
    name: "Sunflower County",
    position: {
      lat: 33.605529,
      lng: -90.59509
    },
    stateName: "Mississippi"
  },
  {
    id: "145",
    state: "28",
    name: "Union County",
    position: {
      lat: 34.489101,
      lng: -89.0023
    },
    stateName: "Mississippi"
  },
  {
    id: "159",
    state: "28",
    name: "Winston County",
    position: {
      lat: 33.078724,
      lng: -89.037391
    },
    stateName: "Mississippi"
  },
  {
    id: "003",
    state: "29",
    name: "Andrew County",
    position: {
      lat: 39.988863,
      lng: -94.803551
    },
    stateName: "Missouri"
  },
  {
    id: "015",
    state: "29",
    name: "Benton County",
    position: {
      lat: 38.301036,
      lng: -93.287942
    },
    stateName: "Missouri"
  },
  {
    id: "027",
    state: "29",
    name: "Callaway County",
    position: {
      lat: 38.835966,
      lng: -91.924089
    },
    stateName: "Missouri"
  },
  {
    id: "039",
    state: "29",
    name: "Cedar County",
    position: {
      lat: 37.733655,
      lng: -93.850014
    },
    stateName: "Missouri"
  },
  {
    id: "049",
    state: "29",
    name: "Clinton County",
    position: {
      lat: 39.608723,
      lng: -94.395803
    },
    stateName: "Missouri"
  },
  {
    id: "059",
    state: "29",
    name: "Dallas County",
    position: {
      lat: 37.683583,
      lng: -93.033812
    },
    stateName: "Missouri"
  },
  {
    id: "071",
    state: "29",
    name: "Franklin County",
    position: {
      lat: 38.408313,
      lng: -91.07341
    },
    stateName: "Missouri"
  },
  {
    id: "107",
    state: "48",
    name: "Crosby County",
    position: {
      lat: 33.609144,
      lng: -101.29871
    },
    stateName: "Texas"
  },
  {
    id: "119",
    state: "48",
    name: "Delta County",
    position: {
      lat: 33.385933,
      lng: -95.67335
    },
    stateName: "Texas"
  },
  {
    id: "131",
    state: "48",
    name: "Duval County",
    position: {
      lat: 27.681123,
      lng: -98.497393
    },
    stateName: "Texas"
  },
  {
    id: "143",
    state: "48",
    name: "Erath County",
    position: {
      lat: 32.238136,
      lng: -98.222377
    },
    stateName: "Texas"
  },
  {
    id: "153",
    state: "48",
    name: "Floyd County",
    position: {
      lat: 34.073731,
      lng: -101.303274
    },
    stateName: "Texas"
  },
  {
    id: "163",
    state: "48",
    name: "Frio County",
    position: {
      lat: 28.869334,
      lng: -99.108788
    },
    stateName: "Texas"
  },
  {
    id: "171",
    state: "48",
    name: "Gillespie County",
    position: {
      lat: 30.32639,
      lng: -98.942106
    },
    stateName: "Texas"
  },
  {
    id: "173",
    state: "48",
    name: "Glasscock County",
    position: {
      lat: 31.868591,
      lng: -101.528971
    },
    stateName: "Texas"
  },
  {
    id: "175",
    state: "48",
    name: "Goliad County",
    position: {
      lat: 28.6607,
      lng: -97.430415
    },
    stateName: "Texas"
  },
  {
    id: "183",
    state: "48",
    name: "Gregg County",
    position: {
      lat: 32.486397,
      lng: -94.816276
    },
    stateName: "Texas"
  },
  {
    id: "187",
    state: "48",
    name: "Guadalupe County",
    position: {
      lat: 29.583208,
      lng: -97.949027
    },
    stateName: "Texas"
  },
  {
    id: "205",
    state: "48",
    name: "Hartley County",
    position: {
      lat: 35.840244,
      lng: -102.610047
    },
    stateName: "Texas"
  },
  {
    id: "209",
    state: "48",
    name: "Hays County",
    position: {
      lat: 30.061225,
      lng: -98.029267
    },
    stateName: "Texas"
  },
  {
    id: "219",
    state: "48",
    name: "Hockley County",
    position: {
      lat: 33.605932,
      lng: -102.343398
    },
    stateName: "Texas"
  },
  {
    id: "231",
    state: "48",
    name: "Hunt County",
    position: {
      lat: 33.123438,
      lng: -96.083807
    },
    stateName: "Texas"
  },
  {
    id: "241",
    state: "48",
    name: "Jasper County",
    position: {
      lat: 30.752932,
      lng: -94.022294
    },
    stateName: "Texas"
  },
  {
    id: "255",
    state: "48",
    name: "Karnes County",
    position: {
      lat: 28.907618,
      lng: -97.860767
    },
    stateName: "Texas"
  },
  {
    id: "269",
    state: "48",
    name: "King County",
    position: {
      lat: 33.614159,
      lng: -100.250548
    },
    stateName: "Texas"
  },
  {
    id: "279",
    state: "48",
    name: "Lamb County",
    position: {
      lat: 34.068862,
      lng: -102.348018
    },
    stateName: "Texas"
  },
  {
    id: "289",
    state: "48",
    name: "Leon County",
    position: {
      lat: 31.300493,
      lng: -95.995622
    },
    stateName: "Texas"
  },
  {
    id: "033",
    state: "08",
    name: "Dolores County",
    position: {
      lat: 37.747602,
      lng: -108.530383
    },
    stateName: "Colorado"
  },
  {
    id: "039",
    state: "08",
    name: "Elbert County",
    position: {
      lat: 39.310817,
      lng: -104.117928
    },
    stateName: "Colorado"
  },
  {
    id: "045",
    state: "08",
    name: "Garfield County",
    position: {
      lat: 39.599352,
      lng: -107.90978
    },
    stateName: "Colorado"
  },
  {
    id: "079",
    state: "06",
    name: "San Luis Obispo County",
    position: {
      lat: 35.385227,
      lng: -120.44754
    },
    stateName: "California"
  },
  {
    id: "093",
    state: "06",
    name: "Siskiyou County",
    position: {
      lat: 41.587986,
      lng: -122.533287
    },
    stateName: "California"
  },
  {
    id: "097",
    state: "06",
    name: "Sonoma County",
    position: {
      lat: 38.532574,
      lng: -122.945194
    },
    stateName: "California"
  },
  {
    id: "105",
    state: "06",
    name: "Trinity County",
    position: {
      lat: 40.647724,
      lng: -123.114404
    },
    stateName: "California"
  },
  {
    id: "017",
    state: "06",
    name: "El Dorado County",
    position: {
      lat: 38.785532,
      lng: -120.534398
    },
    stateName: "California"
  },
  {
    id: "005",
    state: "18",
    name: "Bartholomew County",
    position: {
      lat: 39.205843,
      lng: -85.897999
    },
    stateName: "Indiana"
  },
  {
    id: "017",
    state: "18",
    name: "Cass County",
    position: {
      lat: 40.753799,
      lng: -86.355169
    },
    stateName: "Indiana"
  },
  {
    id: "027",
    state: "18",
    name: "Daviess County",
    position: {
      lat: 38.696155,
      lng: -87.076988
    },
    stateName: "Indiana"
  },
  {
    id: "041",
    state: "18",
    name: "Fayette County",
    position: {
      lat: 39.639655,
      lng: -85.185032
    },
    stateName: "Indiana"
  },
  {
    id: "043",
    state: "18",
    name: "Floyd County",
    position: {
      lat: 38.317937,
      lng: -85.911474
    },
    stateName: "Indiana"
  },
  {
    id: "127",
    state: "13",
    name: "Glynn County",
    position: {
      lat: 31.212747,
      lng: -81.496517
    },
    stateName: "Georgia"
  },
  {
    id: "133",
    state: "13",
    name: "Greene County",
    position: {
      lat: 33.576836,
      lng: -83.167103
    },
    stateName: "Georgia"
  },
  {
    id: "143",
    state: "13",
    name: "Haralson County",
    position: {
      lat: 33.795165,
      lng: -85.220062
    },
    stateName: "Georgia"
  },
  {
    id: "153",
    state: "13",
    name: "Houston County",
    position: {
      lat: 32.458381,
      lng: -83.662856
    },
    stateName: "Georgia"
  },
  {
    id: "163",
    state: "13",
    name: "Jefferson County",
    position: {
      lat: 33.051874,
      lng: -82.41905
    },
    stateName: "Georgia"
  },
  {
    id: "183",
    state: "13",
    name: "Long County",
    position: {
      lat: 31.749563,
      lng: -81.74287
    },
    stateName: "Georgia"
  },
  {
    id: "193",
    state: "13",
    name: "Macon County",
    position: {
      lat: 32.366216,
      lng: -84.052162
    },
    stateName: "Georgia"
  },
  {
    id: "207",
    state: "13",
    name: "Monroe County",
    position: {
      lat: 33.017435,
      lng: -83.922938
    },
    stateName: "Georgia"
  },
  {
    id: "219",
    state: "13",
    name: "Oconee County",
    position: {
      lat: 33.834125,
      lng: -83.437728
    },
    stateName: "Georgia"
  },
  {
    id: "227",
    state: "13",
    name: "Pickens County",
    position: {
      lat: 34.456621,
      lng: -84.490256
    },
    stateName: "Georgia"
  },
  {
    id: "237",
    state: "13",
    name: "Putnam County",
    position: {
      lat: 33.321061,
      lng: -83.37179
    },
    stateName: "Georgia"
  },
  {
    id: "249",
    state: "13",
    name: "Schley County",
    position: {
      lat: 32.263441,
      lng: -84.322724
    },
    stateName: "Georgia"
  },
  {
    id: "083",
    state: "29",
    name: "Henry County",
    position: {
      lat: 38.386491,
      lng: -93.792628
    },
    stateName: "Missouri"
  },
  {
    id: "093",
    state: "29",
    name: "Iron County",
    position: {
      lat: 37.62596,
      lng: -90.699627
    },
    stateName: "Missouri"
  },
  {
    id: "003",
    state: "37",
    name: "Alexander County",
    position: {
      lat: 35.920951,
      lng: -81.177467
    },
    stateName: "North Carolina"
  },
  {
    id: "007",
    state: "37",
    name: "Anson County",
    position: {
      lat: 34.974996,
      lng: -80.109959
    },
    stateName: "North Carolina"
  },
  {
    id: "009",
    state: "37",
    name: "Ashe County",
    position: {
      lat: 36.436305,
      lng: -81.49877
    },
    stateName: "North Carolina"
  },
  {
    id: "033",
    state: "37",
    name: "Caswell County",
    position: {
      lat: 36.393097,
      lng: -79.332546
    },
    stateName: "North Carolina"
  },
  {
    id: "045",
    state: "37",
    name: "Cleveland County",
    position: {
      lat: 35.33463,
      lng: -81.557115
    },
    stateName: "North Carolina"
  },
  {
    id: "063",
    state: "37",
    name: "Durham County",
    position: {
      lat: 36.036589,
      lng: -78.877919
    },
    stateName: "North Carolina"
  },
  {
    id: "073",
    state: "37",
    name: "Gates County",
    position: {
      lat: 36.442135,
      lng: -76.702355
    },
    stateName: "North Carolina"
  },
  {
    id: "085",
    state: "37",
    name: "Harnett County",
    position: {
      lat: 35.368635,
      lng: -78.87161
    },
    stateName: "North Carolina"
  },
  {
    id: "097",
    state: "37",
    name: "Iredell County",
    position: {
      lat: 35.806356,
      lng: -80.874545
    },
    stateName: "North Carolina"
  },
  {
    id: "109",
    state: "37",
    name: "Lincoln County",
    position: {
      lat: 35.487825,
      lng: -81.225176
    },
    stateName: "North Carolina"
  },
  {
    id: "123",
    state: "37",
    name: "Montgomery County",
    position: {
      lat: 35.338071,
      lng: -79.904196
    },
    stateName: "North Carolina"
  },
  {
    id: "133",
    state: "37",
    name: "Onslow County",
    position: {
      lat: 34.76346,
      lng: -77.503297
    },
    stateName: "North Carolina"
  },
  {
    id: "145",
    state: "37",
    name: "Person County",
    position: {
      lat: 36.386387,
      lng: -78.965471
    },
    stateName: "North Carolina"
  },
  {
    id: "299",
    state: "48",
    name: "Llano County",
    position: {
      lat: 30.707585,
      lng: -98.68469
    },
    stateName: "Texas"
  },
  {
    id: "313",
    state: "48",
    name: "Madison County",
    position: {
      lat: 30.966878,
      lng: -95.930372
    },
    stateName: "Texas"
  },
  {
    id: "325",
    state: "48",
    name: "Medina County",
    position: {
      lat: 29.353661,
      lng: -99.111085
    },
    stateName: "Texas"
  },
  {
    id: "335",
    state: "48",
    name: "Mitchell County",
    position: {
      lat: 32.303781,
      lng: -100.92458
    },
    stateName: "Texas"
  },
  {
    id: "343",
    state: "48",
    name: "Morris County",
    position: {
      lat: 33.116466,
      lng: -94.731265
    },
    stateName: "Texas"
  },
  {
    id: "127",
    state: "51",
    name: "New Kent County",
    position: {
      lat: 37.498974,
      lng: -76.993339
    },
    stateName: "Virginia"
  },
  {
    id: "133",
    state: "51",
    name: "Northumberlnd County",
    position: {
      lat: 37.856974,
      lng: -76.379687
    },
    stateName: "Virginia"
  },
  {
    id: "167",
    state: "51",
    name: "Russell County",
    position: {
      lat: 36.93342,
      lng: -82.095934
    },
    stateName: "Virginia"
  },
  {
    id: "179",
    state: "51",
    name: "Stafford County",
    position: {
      lat: 38.418933,
      lng: -77.459043
    },
    stateName: "Virginia"
  },
  {
    id: "515",
    state: "51",
    name: "Bedford County",
    postal: "city",
    position: {
      lat: 37.312408,
      lng: -79.527947
    },
    stateName: "Virginia"
  },
  {
    id: "590",
    state: "51",
    name: "Danville City County",
    postal: "city",
    position: {
      lat: 36.583334,
      lng: -79.408071
    },
    stateName: "Virginia"
  },
  {
    id: "620",
    state: "51",
    name: "Franklin City County",
    postal: "city",
    position: {
      lat: 36.99235,
      lng: -79.883959
    },
    stateName: "Virginia"
  },
  {
    id: "680",
    state: "51",
    name: "Lynchburg City County",
    postal: "city",
    position: {
      lat: 37.399016,
      lng: -79.195458
    },
    stateName: "Virginia"
  },
  {
    id: "720",
    state: "51",
    name: "Norton City County",
    postal: "city",
    position: {
      lat: 36.931549,
      lng: -82.625996
    },
    stateName: "Virginia"
  },
  {
    id: "740",
    state: "51",
    name: "Portsmouth City County",
    postal: "city",
    position: {
      lat: 36.85943,
      lng: -76.356269
    },
    stateName: "Virginia"
  },
  {
    id: "830",
    state: "51",
    name: "Williamsburg City County",
    postal: "city",
    position: {
      lat: 37.269293,
      lng: -76.706717
    },
    stateName: "Virginia"
  },
  {
    id: "086",
    state: "12",
    name: "Miami-dade County",
    position: {
      lat: 25.610494,
      lng: -80.499045
    },
    stateName: "Florida"
  },
  {
    id: "103",
    state: "12",
    name: "Pinellas County",
    position: {
      lat: 27.903122,
      lng: -82.739518
    },
    stateName: "Florida"
  },
  {
    id: "123",
    state: "12",
    name: "Taylor County",
    position: {
      lat: 30.016943,
      lng: -83.616417
    },
    stateName: "Florida"
  },
  {
    id: "259",
    state: "13",
    name: "Stewart County",
    position: {
      lat: 32.073225,
      lng: -84.834912
    },
    stateName: "Georgia"
  },
  {
    id: "271",
    state: "13",
    name: "Telfair County",
    position: {
      lat: 31.913639,
      lng: -82.931062
    },
    stateName: "Georgia"
  },
  {
    id: "283",
    state: "13",
    name: "Treutlen County",
    position: {
      lat: 32.409586,
      lng: -82.570882
    },
    stateName: "Georgia"
  },
  {
    id: "297",
    state: "13",
    name: "Walton County",
    position: {
      lat: 33.782649,
      lng: -83.734215
    },
    stateName: "Georgia"
  },
  {
    id: "025",
    state: "20",
    name: "Clark County",
    position: {
      lat: 37.233831,
      lng: -99.813869
    },
    stateName: "Kansas"
  },
  {
    id: "031",
    state: "20",
    name: "Coffey County",
    position: {
      lat: 38.23645,
      lng: -95.729137
    },
    stateName: "Kansas"
  },
  {
    id: "033",
    state: "20",
    name: "Comanche County",
    position: {
      lat: 37.189071,
      lng: -99.254089
    },
    stateName: "Kansas"
  },
  {
    id: "045",
    state: "20",
    name: "Douglas County",
    position: {
      lat: 38.896573,
      lng: -95.290529
    },
    stateName: "Kansas"
  },
  {
    id: "055",
    state: "20",
    name: "Finney County",
    position: {
      lat: 38.049855,
      lng: -100.739929
    },
    stateName: "Kansas"
  },
  {
    id: "069",
    state: "20",
    name: "Gray County",
    position: {
      lat: 37.744513,
      lng: -100.451716
    },
    stateName: "Kansas"
  },
  {
    id: "081",
    state: "20",
    name: "Haskell County",
    position: {
      lat: 37.55855,
      lng: -100.876869
    },
    stateName: "Kansas"
  },
  {
    id: "093",
    state: "20",
    name: "Kearny County",
    position: {
      lat: 37.994461,
      lng: -101.308136
    },
    stateName: "Kansas"
  },
  {
    id: "101",
    state: "20",
    name: "Lane County",
    position: {
      lat: 38.481286,
      lng: -100.466185
    },
    stateName: "Kansas"
  },
  {
    id: "111",
    state: "20",
    name: "Lyon County",
    position: {
      lat: 38.455498,
      lng: -96.161589
    },
    stateName: "Kansas"
  },
  {
    id: "123",
    state: "20",
    name: "Mitchell County",
    position: {
      lat: 39.393026,
      lng: -98.207362
    },
    stateName: "Kansas"
  },
  {
    id: "133",
    state: "20",
    name: "Neosho County",
    position: {
      lat: 37.564283,
      lng: -95.315683
    },
    stateName: "Kansas"
  },
  {
    id: "139",
    state: "20",
    name: "Osage County",
    position: {
      lat: 38.649706,
      lng: -95.727275
    },
    stateName: "Kansas"
  },
  {
    id: "151",
    state: "20",
    name: "Pratt County",
    position: {
      lat: 37.647594,
      lng: -98.74012
    },
    stateName: "Kansas"
  },
  {
    id: "159",
    state: "20",
    name: "Rice County",
    position: {
      lat: 38.347178,
      lng: -98.201415
    },
    stateName: "Kansas"
  },
  {
    id: "169",
    state: "20",
    name: "Saline County",
    position: {
      lat: 38.786327,
      lng: -97.650153
    },
    stateName: "Kansas"
  },
  {
    id: "157",
    state: "37",
    name: "Rockingham County",
    position: {
      lat: 36.380927,
      lng: -79.782889
    },
    stateName: "North Carolina"
  },
  {
    id: "173",
    state: "37",
    name: "Swain County",
    position: {
      lat: 35.568849,
      lng: -83.465614
    },
    stateName: "North Carolina"
  },
  {
    id: "179",
    state: "37",
    name: "Union County",
    position: {
      lat: 34.991501,
      lng: -80.530131
    },
    stateName: "North Carolina"
  },
  {
    id: "059",
    state: "39",
    name: "Guernsey County",
    position: {
      lat: 40.056665,
      lng: -81.497875
    },
    stateName: "Ohio"
  },
  {
    id: "069",
    state: "39",
    name: "Henry County",
    position: {
      lat: 41.335072,
      lng: -84.065882
    },
    stateName: "Ohio"
  },
  {
    id: "083",
    state: "39",
    name: "Knox County",
    position: {
      lat: 40.40362,
      lng: -82.422393
    },
    stateName: "Ohio"
  },
  {
    id: "091",
    state: "39",
    name: "Logan County",
    position: {
      lat: 40.387553,
      lng: -83.766343
    },
    stateName: "Ohio"
  },
  {
    id: "103",
    state: "39",
    name: "Medina County",
    position: {
      lat: 41.116051,
      lng: -81.899566
    },
    stateName: "Ohio"
  },
  {
    id: "115",
    state: "39",
    name: "Morgan County",
    position: {
      lat: 39.624946,
      lng: -81.861699
    },
    stateName: "Ohio"
  },
  {
    id: "127",
    state: "39",
    name: "Perry County",
    position: {
      lat: 39.743187,
      lng: -82.237953
    },
    stateName: "Ohio"
  },
  {
    id: "137",
    state: "39",
    name: "Putnam County",
    position: {
      lat: 41.024533,
      lng: -84.129879
    },
    stateName: "Ohio"
  },
  {
    id: "153",
    state: "39",
    name: "Summit County",
    position: {
      lat: 41.121851,
      lng: -81.534936
    },
    stateName: "Ohio"
  },
  {
    id: "165",
    state: "39",
    name: "Warren County",
    position: {
      lat: 39.425652,
      lng: -84.169906
    },
    stateName: "Ohio"
  },
  {
    id: "005",
    state: "40",
    name: "Atoka County",
    position: {
      lat: 34.374813,
      lng: -96.034705
    },
    stateName: "Oklahoma"
  },
  {
    id: "015",
    state: "40",
    name: "Caddo County",
    position: {
      lat: 35.16792,
      lng: -98.381045
    },
    stateName: "Oklahoma"
  },
  {
    id: "027",
    state: "40",
    name: "Cleveland County",
    position: {
      lat: 35.203117,
      lng: -97.328332
    },
    stateName: "Oklahoma"
  },
  {
    id: "011",
    state: "53",
    name: "Clark County",
    position: {
      lat: 45.771674,
      lng: -122.485903
    },
    stateName: "Washington"
  },
  {
    id: "035",
    state: "53",
    name: "Kitsap County",
    position: {
      lat: 47.639687,
      lng: -122.649636
    },
    stateName: "Washington"
  },
  {
    id: "053",
    state: "55",
    name: "Jackson County",
    position: {
      lat: 44.324895,
      lng: -90.806541
    },
    stateName: "Wisconsin"
  },
  {
    id: "067",
    state: "55",
    name: "Langlade County",
    position: {
      lat: 45.259204,
      lng: -89.06819
    },
    stateName: "Wisconsin"
  },
  {
    id: "078",
    state: "55",
    name: "Menomonee County",
    position: {
      lat: 44.991304,
      lng: -88.669251
    },
    stateName: "Wisconsin"
  },
  {
    id: "087",
    state: "55",
    name: "Outagamie County",
    position: {
      lat: 44.418226,
      lng: -88.464988
    },
    stateName: "Wisconsin"
  },
  {
    id: "099",
    state: "55",
    name: "Price County",
    position: {
      lat: 45.679072,
      lng: -90.35965
    },
    stateName: "Wisconsin"
  },
  {
    id: "109",
    state: "55",
    name: "St. Croix County",
    position: {
      lat: 45.028959,
      lng: -92.447284
    },
    stateName: "Wisconsin"
  },
  {
    id: "121",
    state: "55",
    name: "Trempealeau County",
    position: {
      lat: 44.30305,
      lng: -91.358867
    },
    stateName: "Wisconsin"
  },
  {
    id: "135",
    state: "55",
    name: "Waupaca County",
    position: {
      lat: 44.478004,
      lng: -88.967006
    },
    stateName: "Wisconsin"
  },
  {
    id: "005",
    state: "56",
    name: "Campbell County",
    position: {
      lat: 44.241321,
      lng: -105.552029
    },
    stateName: "Wyoming"
  },
  {
    id: "015",
    state: "56",
    name: "Goshen County",
    position: {
      lat: 42.08958,
      lng: -104.353482
    },
    stateName: "Wyoming"
  },
  {
    id: "041",
    state: "56",
    name: "Uinta County",
    position: {
      lat: 41.284726,
      lng: -110.558947
    },
    stateName: "Wyoming"
  },
  {
    id: "127",
    state: "01",
    name: "Walker County",
    position: {
      lat: 33.791571,
      lng: -87.301092
    },
    stateName: "Alabama"
  },
  {
    id: "133",
    state: "01",
    name: "Winston County",
    position: {
      lat: 34.155888,
      lng: -87.364147
    },
    stateName: "Alabama"
  },
  {
    id: "179",
    state: "20",
    name: "Sheridan County",
    position: {
      lat: 39.350543,
      lng: -100.441206
    },
    stateName: "Kansas"
  },
  {
    id: "191",
    state: "20",
    name: "Sumner County",
    position: {
      lat: 37.236663,
      lng: -97.493349
    },
    stateName: "Kansas"
  },
  {
    id: "209",
    state: "20",
    name: "Wyandotte County",
    position: {
      lat: 39.115384,
      lng: -94.763087
    },
    stateName: "Kansas"
  },
  {
    id: "185",
    state: "21",
    name: "Oldham County",
    position: {
      lat: 38.400046,
      lng: -85.456059
    },
    stateName: "Kentucky"
  },
  {
    id: "197",
    state: "21",
    name: "Powell County",
    position: {
      lat: 37.810379,
      lng: -83.826985
    },
    stateName: "Kentucky"
  },
  {
    id: "203",
    state: "21",
    name: "Rockcastle County",
    position: {
      lat: 37.36122,
      lng: -84.314419
    },
    stateName: "Kentucky"
  },
  {
    id: "215",
    state: "21",
    name: "Spencer County",
    position: {
      lat: 38.026976,
      lng: -85.321525
    },
    stateName: "Kentucky"
  },
  {
    id: "227",
    state: "21",
    name: "Warren County",
    position: {
      lat: 36.995634,
      lng: -86.423579
    },
    stateName: "Kentucky"
  },
  {
    id: "235",
    state: "21",
    name: "Whitley County",
    position: {
      lat: 36.758021,
      lng: -84.14455
    },
    stateName: "Kentucky"
  },
  {
    id: "007",
    state: "22",
    name: "Assumption County",
    postal: "Parish",
    position: {
      lat: 29.899884,
      lng: -91.050528
    },
    stateName: "Louisiana"
  },
  {
    id: "021",
    state: "22",
    name: "Caldwell County",
    postal: "Parish",
    position: {
      lat: 32.101244,
      lng: -92.11418
    },
    stateName: "Louisiana"
  },
  {
    id: "025",
    state: "22",
    name: "Catahoula County",
    postal: "Parish",
    position: {
      lat: 31.666517,
      lng: -91.846703
    },
    stateName: "Louisiana"
  },
  {
    id: "047",
    state: "22",
    name: "Iberville County",
    postal: "Parish",
    position: {
      lat: 30.288394,
      lng: -91.36315
    },
    stateName: "Louisiana"
  },
  {
    id: "059",
    state: "22",
    name: "Lasalle County",
    postal: "Parish",
    position: {
      lat: 31.680836,
      lng: -92.161809
    },
    stateName: "Louisiana"
  },
  {
    id: "067",
    state: "22",
    name: "Morehouse County",
    postal: "Parish",
    position: {
      lat: 32.820008,
      lng: -91.800399
    },
    stateName: "Louisiana"
  },
  {
    id: "083",
    state: "22",
    name: "Richland County",
    postal: "Parish",
    position: {
      lat: 32.418121,
      lng: -91.758375
    },
    stateName: "Louisiana"
  },
  {
    id: "101",
    state: "48",
    name: "Cottle County",
    position: {
      lat: 34.091906,
      lng: -100.276442
    },
    stateName: "Texas"
  },
  {
    id: "105",
    state: "48",
    name: "Crockett County",
    position: {
      lat: 30.717532,
      lng: -101.404211
    },
    stateName: "Texas"
  },
  {
    id: "109",
    state: "48",
    name: "Culberson County",
    position: {
      lat: 31.445909,
      lng: -104.526945
    },
    stateName: "Texas"
  },
  {
    id: "111",
    state: "48",
    name: "Dallam County",
    position: {
      lat: 36.28637,
      lng: -102.59402
    },
    stateName: "Texas"
  },
  {
    id: "113",
    state: "48",
    name: "Dallas County",
    position: {
      lat: 32.766987,
      lng: -96.778424
    },
    stateName: "Texas"
  },
  {
    id: "121",
    state: "48",
    name: "Denton County",
    position: {
      lat: 33.205005,
      lng: -97.119046
    },
    stateName: "Texas"
  },
  {
    id: "125",
    state: "48",
    name: "Dickens County",
    position: {
      lat: 33.614666,
      lng: -100.786095
    },
    stateName: "Texas"
  },
  {
    id: "133",
    state: "48",
    name: "Eastland County",
    position: {
      lat: 32.324645,
      lng: -98.83656
    },
    stateName: "Texas"
  },
  {
    id: "139",
    state: "48",
    name: "Ellis County",
    position: {
      lat: 32.347279,
      lng: -96.798336
    },
    stateName: "Texas"
  },
  {
    id: "145",
    state: "48",
    name: "Falls County",
    position: {
      lat: 31.25193,
      lng: -96.934128
    },
    stateName: "Texas"
  },
  {
    id: "147",
    state: "48",
    name: "Fannin County",
    position: {
      lat: 33.591161,
      lng: -96.104988
    },
    stateName: "Texas"
  },
  {
    id: "151",
    state: "48",
    name: "Fisher County",
    position: {
      lat: 32.740473,
      lng: -100.40312
    },
    stateName: "Texas"
  },
  {
    id: "157",
    state: "48",
    name: "Fort Bend County",
    position: {
      lat: 29.526602,
      lng: -95.771015
    },
    stateName: "Texas"
  },
  {
    id: "181",
    state: "48",
    name: "Grayson County",
    position: {
      lat: 33.624508,
      lng: -96.675699
    },
    stateName: "Texas"
  },
  {
    id: "189",
    state: "48",
    name: "Hale County",
    position: {
      lat: 34.068436,
      lng: -101.822888
    },
    stateName: "Texas"
  },
  {
    id: "193",
    state: "48",
    name: "Hamilton County",
    position: {
      lat: 31.706982,
      lng: -98.111794
    },
    stateName: "Texas"
  },
  {
    id: "345",
    state: "48",
    name: "Motley County",
    position: {
      lat: 34.058383,
      lng: -100.793696
    },
    stateName: "Texas"
  },
  {
    id: "347",
    state: "48",
    name: "Nacogdoches County",
    position: {
      lat: 31.62056,
      lng: -94.62025
    },
    stateName: "Texas"
  },
  {
    id: "351",
    state: "48",
    name: "Newton County",
    position: {
      lat: 30.786718,
      lng: -93.73925
    },
    stateName: "Texas"
  },
  {
    id: "135",
    state: "51",
    name: "Nottoway County",
    position: {
      lat: 37.143696,
      lng: -78.052162
    },
    stateName: "Virginia"
  },
  {
    id: "137",
    state: "51",
    name: "Orange County",
    position: {
      lat: 38.250439,
      lng: -78.00998
    },
    stateName: "Virginia"
  },
  {
    id: "139",
    state: "51",
    name: "Page County",
    position: {
      lat: 38.623751,
      lng: -78.490471
    },
    stateName: "Virginia"
  },
  {
    id: "143",
    state: "51",
    name: "Pittsylvania County",
    position: {
      lat: 36.821721,
      lng: -79.398502
    },
    stateName: "Virginia"
  },
  {
    id: "153",
    state: "51",
    name: "Prince William County",
    position: {
      lat: 38.702332,
      lng: -77.478887
    },
    stateName: "Virginia"
  },
  {
    id: "163",
    state: "51",
    name: "Rockbridge County",
    position: {
      lat: 37.814517,
      lng: -79.447754
    },
    stateName: "Virginia"
  },
  {
    id: "169",
    state: "51",
    name: "Scott County",
    position: {
      lat: 36.712778,
      lng: -82.613627
    },
    stateName: "Virginia"
  },
  {
    id: "171",
    state: "51",
    name: "Shenandoah County",
    position: {
      lat: 38.856204,
      lng: -78.573987
    },
    stateName: "Virginia"
  },
  {
    id: "181",
    state: "51",
    name: "Surry County",
    position: {
      lat: 37.119761,
      lng: -76.880172
    },
    stateName: "Virginia"
  },
  {
    id: "183",
    state: "51",
    name: "Sussex County",
    position: {
      lat: 36.926645,
      lng: -77.259732
    },
    stateName: "Virginia"
  },
  {
    id: "033",
    state: "53",
    name: "King County",
    position: {
      lat: 47.493554,
      lng: -121.832375
    },
    stateName: "Washington"
  },
  {
    id: "035",
    state: "55",
    name: "Eau Claire County",
    position: {
      lat: 44.726355,
      lng: -91.286414
    },
    stateName: "Wisconsin"
  },
  {
    id: "037",
    state: "55",
    name: "Florence County",
    position: {
      lat: 45.849646,
      lng: -88.400322
    },
    stateName: "Wisconsin"
  },
  {
    id: "041",
    state: "55",
    name: "Forest County",
    position: {
      lat: 45.666882,
      lng: -88.773225
    },
    stateName: "Wisconsin"
  },
  {
    id: "043",
    state: "55",
    name: "Grant County",
    position: {
      lat: 42.870062,
      lng: -90.695368
    },
    stateName: "Wisconsin"
  },
  {
    id: "047",
    state: "55",
    name: "Green Lake County",
    position: {
      lat: 43.76141,
      lng: -88.987228
    },
    stateName: "Wisconsin"
  },
  {
    id: "049",
    state: "55",
    name: "Iowa County",
    position: {
      lat: 43.001021,
      lng: -90.133692
    },
    stateName: "Wisconsin"
  },
  {
    id: "055",
    state: "55",
    name: "Jefferson County",
    position: {
      lat: 43.013807,
      lng: -88.773986
    },
    stateName: "Wisconsin"
  },
  {
    id: "057",
    state: "55",
    name: "Juneau County",
    position: {
      lat: 43.932836,
      lng: -90.113984
    },
    stateName: "Wisconsin"
  },
  {
    id: "059",
    state: "55",
    name: "Kenosha County",
    position: {
      lat: 42.579703,
      lng: -87.424898
    },
    stateName: "Wisconsin"
  },
  {
    id: "027",
    state: "56",
    name: "Niobrara County",
    position: {
      lat: 43.062159,
      lng: -104.468373
    },
    stateName: "Wyoming"
  },
  {
    id: "029",
    state: "56",
    name: "Park County",
    position: {
      lat: 44.492387,
      lng: -109.593598
    },
    stateName: "Wyoming"
  },
  {
    id: "035",
    state: "56",
    name: "Sublette County",
    position: {
      lat: 42.767928,
      lng: -109.91617
    },
    stateName: "Wyoming"
  },
  {
    id: "037",
    state: "56",
    name: "Sweetwater County",
    position: {
      lat: 41.660339,
      lng: -108.875676
    },
    stateName: "Wyoming"
  },
  {
    id: "043",
    state: "56",
    name: "Washakie County",
    position: {
      lat: 43.878831,
      lng: -107.669052
    },
    stateName: "Wyoming"
  },
  {
    id: "043",
    state: "53",
    name: "Lincoln County",
    position: {
      lat: 47.582718,
      lng: -118.417668
    },
    stateName: "Washington"
  },
  {
    id: "051",
    state: "53",
    name: "Pend Oreille County",
    position: {
      lat: 48.543877,
      lng: -117.232183
    },
    stateName: "Washington"
  },
  {
    id: "053",
    state: "53",
    name: "Pierce County",
    position: {
      lat: 47.040716,
      lng: -122.144709
    },
    stateName: "Washington"
  },
  {
    id: "197",
    state: "48",
    name: "Hardeman County",
    position: {
      lat: 34.289904,
      lng: -99.745697
    },
    stateName: "Texas"
  },
  {
    id: "199",
    state: "48",
    name: "Hardin County",
    position: {
      lat: 30.329612,
      lng: -94.393149
    },
    stateName: "Texas"
  },
  {
    id: "201",
    state: "48",
    name: "Harris County",
    position: {
      lat: 29.857273,
      lng: -95.393037
    },
    stateName: "Texas"
  },
  {
    id: "203",
    state: "48",
    name: "Harrison County",
    position: {
      lat: 32.547993,
      lng: -94.374425
    },
    stateName: "Texas"
  },
  {
    id: "211",
    state: "48",
    name: "Hemphill County",
    position: {
      lat: 35.816237,
      lng: -100.284807
    },
    stateName: "Texas"
  },
  {
    id: "213",
    state: "48",
    name: "Henderson County",
    position: {
      lat: 32.211633,
      lng: -95.853418
    },
    stateName: "Texas"
  },
  {
    id: "215",
    state: "48",
    name: "Hidalgo County",
    position: {
      lat: 26.396384,
      lng: -98.18099
    },
    stateName: "Texas"
  },
  {
    id: "223",
    state: "48",
    name: "Hopkins County",
    position: {
      lat: 33.148959,
      lng: -95.565194
    },
    stateName: "Texas"
  },
  {
    id: "235",
    state: "48",
    name: "Irion County",
    position: {
      lat: 31.303424,
      lng: -100.981304
    },
    stateName: "Texas"
  },
  {
    id: "237",
    state: "48",
    name: "Jack County",
    position: {
      lat: 33.232277,
      lng: -98.171902
    },
    stateName: "Texas"
  },
  {
    id: "239",
    state: "48",
    name: "Jackson County",
    position: {
      lat: 28.959802,
      lng: -96.58908
    },
    stateName: "Texas"
  },
  {
    id: "243",
    state: "48",
    name: "Jeff Davis County",
    position: {
      lat: 30.617087,
      lng: -104.18786
    },
    stateName: "Texas"
  },
  {
    id: "249",
    state: "48",
    name: "Jim Wells County",
    position: {
      lat: 27.733516,
      lng: -98.090814
    },
    stateName: "Texas"
  },
  {
    id: "253",
    state: "48",
    name: "Jones County",
    position: {
      lat: 32.743709,
      lng: -99.87443
    },
    stateName: "Texas"
  },
  {
    id: "259",
    state: "48",
    name: "Kendall County",
    position: {
      lat: 29.944524,
      lng: -98.711094
    },
    stateName: "Texas"
  },
  {
    id: "185",
    state: "51",
    name: "Tazewell County",
    position: {
      lat: 37.125395,
      lng: -81.562924
    },
    stateName: "Virginia"
  },
  {
    id: "191",
    state: "51",
    name: "Washington County",
    position: {
      lat: 36.747813,
      lng: -81.950291
    },
    stateName: "Virginia"
  },
  {
    id: "195",
    state: "51",
    name: "Wise County",
    position: {
      lat: 36.974561,
      lng: -82.62156
    },
    stateName: "Virginia"
  },
  {
    id: "520",
    state: "51",
    name: "Bristol City County",
    postal: "city",
    position: {
      lat: 36.616954,
      lng: -82.157564
    },
    stateName: "Virginia"
  },
  {
    id: "550",
    state: "51",
    name: "Chesapeake City County",
    postal: "city",
    position: {
      lat: 36.679376,
      lng: -76.301788
    },
    stateName: "Virginia"
  },
  {
    id: "580",
    state: "51",
    name: "Covington County",
    postal: "city",
    position: {
      lat: 37.778143,
      lng: -79.986039
    },
    stateName: "Virginia"
  },
  {
    id: "610",
    state: "51",
    name: "Falls Church City County",
    postal: "city",
    position: {
      lat: 38.883787,
      lng: -77.174639
    },
    stateName: "Virginia"
  },
  {
    id: "640",
    state: "51",
    name: "Galax City County",
    postal: "city",
    position: {
      lat: 36.66564,
      lng: -80.914308
    },
    stateName: "Virginia"
  },
  {
    id: "678",
    state: "51",
    name: "Lexington City County",
    postal: "city",
    position: {
      lat: 37.782332,
      lng: -79.44432
    },
    stateName: "Virginia"
  },
  {
    id: "683",
    state: "51",
    name: "Manassas City County",
    postal: "city",
    position: {
      lat: 38.747561,
      lng: -77.484727
    },
    stateName: "Virginia"
  },
  {
    id: "685",
    state: "51",
    name: "Manassas Park City County",
    postal: "city",
    position: {
      lat: 38.768991,
      lng: -77.448681
    },
    stateName: "Virginia"
  },
  {
    id: "710",
    state: "51",
    name: "Norfolk City County",
    postal: "city",
    position: {
      lat: 36.923015,
      lng: -76.244641
    },
    stateName: "Virginia"
  },
  {
    id: "730",
    state: "51",
    name: "Petersburg City County",
    postal: "city",
    position: {
      lat: 37.20473,
      lng: -77.392368
    },
    stateName: "Virginia"
  },
  {
    id: "750",
    state: "51",
    name: "Radford City County",
    postal: "city",
    position: {
      lat: 37.120036,
      lng: -80.557048
    },
    stateName: "Virginia"
  },
  {
    id: "790",
    state: "51",
    name: "Staunton City County",
    postal: "city",
    position: {
      lat: 38.158056,
      lng: -79.061501
    },
    stateName: "Virginia"
  },
  {
    id: "065",
    state: "55",
    name: "Lafayette County",
    position: {
      lat: 42.655578,
      lng: -90.130292
    },
    stateName: "Wisconsin"
  },
  {
    id: "075",
    state: "55",
    name: "Marinette County",
    position: {
      lat: 45.346899,
      lng: -87.991198
    },
    stateName: "Wisconsin"
  },
  {
    id: "077",
    state: "55",
    name: "Marquette County",
    position: {
      lat: 43.826053,
      lng: -89.409095
    },
    stateName: "Wisconsin"
  },
  {
    id: "083",
    state: "55",
    name: "Oconto County",
    position: {
      lat: 44.996575,
      lng: -88.206516
    },
    stateName: "Wisconsin"
  },
  {
    id: "085",
    state: "55",
    name: "Oneida County",
    position: {
      lat: 45.713791,
      lng: -89.536693
    },
    stateName: "Wisconsin"
  },
  {
    id: "091",
    state: "55",
    name: "Pepin County",
    position: {
      lat: 44.627436,
      lng: -91.83489
    },
    stateName: "Wisconsin"
  },
  {
    id: "093",
    state: "55",
    name: "Pierce County",
    position: {
      lat: 44.725337,
      lng: -92.426279
    },
    stateName: "Wisconsin"
  },
  {
    id: "097",
    state: "55",
    name: "Portage County",
    position: {
      lat: 44.476246,
      lng: -89.49807
    },
    stateName: "Wisconsin"
  },
  {
    id: "063",
    state: "53",
    name: "Spokane County",
    position: {
      lat: 47.620379,
      lng: -117.404392
    },
    stateName: "Washington"
  },
  {
    id: "067",
    state: "53",
    name: "Thurston County",
    position: {
      lat: 46.932598,
      lng: -122.829441
    },
    stateName: "Washington"
  },
  {
    id: "069",
    state: "53",
    name: "Wahkiakum County",
    position: {
      lat: 46.294638,
      lng: -123.424458
    },
    stateName: "Washington"
  },
  {
    id: "077",
    state: "53",
    name: "Yakima County",
    position: {
      lat: 46.456558,
      lng: -120.740145
    },
    stateName: "Washington"
  },
  {
    id: "003",
    state: "54",
    name: "Berkeley County",
    position: {
      lat: 39.457854,
      lng: -78.032338
    },
    stateName: "West Virginia"
  },
  {
    id: "005",
    state: "54",
    name: "Boone County",
    position: {
      lat: 38.022838,
      lng: -81.713314
    },
    stateName: "West Virginia"
  },
  {
    id: "009",
    state: "54",
    name: "Brooke County",
    position: {
      lat: 40.272645,
      lng: -80.578691
    },
    stateName: "West Virginia"
  },
  {
    id: "011",
    state: "54",
    name: "Cabell County",
    position: {
      lat: 38.41958,
      lng: -82.243392
    },
    stateName: "West Virginia"
  },
  {
    id: "015",
    state: "54",
    name: "Clay County",
    position: {
      lat: 38.459826,
      lng: -81.081866
    },
    stateName: "West Virginia"
  },
  {
    id: "019",
    state: "54",
    name: "Fayette County",
    position: {
      lat: 38.030933,
      lng: -81.086051
    },
    stateName: "West Virginia"
  },
  {
    id: "037",
    state: "12",
    name: "Franklin County",
    position: {
      lat: 29.810176,
      lng: -84.799174
    },
    stateName: "Florida"
  },
  {
    id: "057",
    state: "12",
    name: "Hillsborough County",
    position: {
      lat: 27.90659,
      lng: -82.349568
    },
    stateName: "Florida"
  },
  {
    id: "065",
    state: "12",
    name: "Jefferson County",
    position: {
      lat: 30.424558,
      lng: -83.890859
    },
    stateName: "Florida"
  },
  {
    id: "093",
    state: "08",
    name: "Park County",
    position: {
      lat: 39.118914,
      lng: -105.717648
    },
    stateName: "Colorado"
  },
  {
    id: "099",
    state: "08",
    name: "Prowers County",
    position: {
      lat: 37.958181,
      lng: -102.392161
    },
    stateName: "Colorado"
  },
  {
    id: "107",
    state: "08",
    name: "Routt County",
    position: {
      lat: 40.48316,
      lng: -106.985289
    },
    stateName: "Colorado"
  },
  {
    id: "113",
    state: "08",
    name: "San Miguel County",
    position: {
      lat: 38.009374,
      lng: -108.427326
    },
    stateName: "Colorado"
  },
  {
    id: "115",
    state: "08",
    name: "Sedgwick County",
    position: {
      lat: 40.871568,
      lng: -102.355358
    },
    stateName: "Colorado"
  },
  {
    id: "125",
    state: "05",
    name: "Saline County",
    position: {
      lat: 34.648525,
      lng: -92.674463
    },
    stateName: "Arkansas"
  },
  {
    id: "131",
    state: "05",
    name: "Sebastian County",
    position: {
      lat: 35.196981,
      lng: -94.274989
    },
    stateName: "Arkansas"
  },
  {
    id: "139",
    state: "05",
    name: "Union County",
    position: {
      lat: 33.168219,
      lng: -92.598145
    },
    stateName: "Arkansas"
  },
  {
    id: "171",
    state: "18",
    name: "Warren County",
    position: {
      lat: 40.352658,
      lng: -87.375847
    },
    stateName: "Indiana"
  },
  {
    id: "177",
    state: "18",
    name: "Wayne County",
    position: {
      lat: 39.863091,
      lng: -85.006735
    },
    stateName: "Indiana"
  },
  {
    id: "005",
    state: "19",
    name: "Allamakee County",
    position: {
      lat: 43.274964,
      lng: -91.382751
    },
    stateName: "Iowa"
  },
  {
    id: "059",
    state: "17",
    name: "Gallatin County",
    position: {
      lat: 37.768677,
      lng: -88.227964
    },
    stateName: "Illinois"
  },
  {
    id: "067",
    state: "17",
    name: "Hancock County",
    position: {
      lat: 40.405792,
      lng: -91.167988
    },
    stateName: "Illinois"
  },
  {
    id: "061",
    state: "18",
    name: "Harrison County",
    position: {
      lat: 38.18644,
      lng: -86.103681
    },
    stateName: "Indiana"
  },
  {
    id: "073",
    state: "18",
    name: "Jasper County",
    position: {
      lat: 41.017688,
      lng: -87.118814
    },
    stateName: "Indiana"
  },
  {
    id: "081",
    state: "18",
    name: "Johnson County",
    position: {
      lat: 39.495986,
      lng: -86.0946
    },
    stateName: "Indiana"
  },
  {
    id: "083",
    state: "18",
    name: "Knox County",
    position: {
      lat: 38.688663,
      lng: -87.420182
    },
    stateName: "Indiana"
  },
  {
    id: "089",
    state: "18",
    name: "Lake County",
    position: {
      lat: 41.472239,
      lng: -87.374337
    },
    stateName: "Indiana"
  },
  {
    id: "107",
    state: "18",
    name: "Montgomery County",
    position: {
      lat: 40.040296,
      lng: -86.892715
    },
    stateName: "Indiana"
  },
  {
    id: "121",
    state: "20",
    name: "Miami County",
    position: {
      lat: 38.566772,
      lng: -94.832963
    },
    stateName: "Kansas"
  },
  {
    id: "129",
    state: "20",
    name: "Morton County",
    position: {
      lat: 37.18525,
      lng: -101.809516
    },
    stateName: "Kansas"
  },
  {
    id: "137",
    state: "20",
    name: "Norton County",
    position: {
      lat: 39.783867,
      lng: -99.899235
    },
    stateName: "Kansas"
  },
  {
    id: "147",
    state: "20",
    name: "Phillips County",
    position: {
      lat: 39.784506,
      lng: -99.34215
    },
    stateName: "Kansas"
  },
  {
    id: "153",
    state: "20",
    name: "Rawlins County",
    position: {
      lat: 39.786198,
      lng: -101.076738
    },
    stateName: "Kansas"
  },
  {
    id: "161",
    state: "20",
    name: "Riley County",
    position: {
      lat: 39.291211,
      lng: -96.727489
    },
    stateName: "Kansas"
  },
  {
    id: "175",
    state: "20",
    name: "Seward County",
    position: {
      lat: 37.180585,
      lng: -100.854741
    },
    stateName: "Kansas"
  },
  {
    id: "065",
    state: "22",
    name: "Madison County",
    postal: "Parish",
    position: {
      lat: 32.365824,
      lng: -91.240729
    },
    stateName: "Louisiana"
  },
  {
    id: "001",
    state: "09",
    name: "Fairfield County",
    position: {
      lat: 41.228103,
      lng: -73.366757
    },
    stateName: "Connecticut"
  },
  {
    id: "021",
    state: "05",
    name: "Clay County",
    position: {
      lat: 36.367302,
      lng: -90.418704
    },
    stateName: "Arkansas"
  },
  {
    id: "119",
    state: "21",
    name: "Knott County",
    position: {
      lat: 37.354703,
      lng: -82.949138
    },
    stateName: "Kentucky"
  },
  {
    id: "127",
    state: "21",
    name: "Lawrence County",
    position: {
      lat: 38.074459,
      lng: -82.738305
    },
    stateName: "Kentucky"
  },
  {
    id: "133",
    state: "21",
    name: "Letcher County",
    position: {
      lat: 37.118503,
      lng: -82.861175
    },
    stateName: "Kentucky"
  },
  {
    id: "141",
    state: "21",
    name: "Logan County",
    position: {
      lat: 36.859829,
      lng: -86.881436
    },
    stateName: "Kentucky"
  },
  {
    id: "149",
    state: "21",
    name: "Mc Lean County",
    position: {
      lat: 37.530575,
      lng: -87.262931
    },
    stateName: "Kentucky"
  },
  {
    id: "155",
    state: "21",
    name: "Marion County",
    position: {
      lat: 37.552247,
      lng: -85.269242
    },
    stateName: "Kentucky"
  },
  {
    id: "015",
    state: "04",
    name: "Mohave County",
    position: {
      lat: 35.717705,
      lng: -113.749689
    },
    stateName: "Arizona"
  },
  {
    id: "163",
    state: "21",
    name: "Meade County",
    position: {
      lat: 37.967476,
      lng: -86.200863
    },
    stateName: "Kentucky"
  },
  {
    id: "177",
    state: "21",
    name: "Muhlenberg County",
    position: {
      lat: 37.213816,
      lng: -87.134092
    },
    stateName: "Kentucky"
  },
  {
    id: "051",
    state: "19",
    name: "Davis County",
    position: {
      lat: 40.748089,
      lng: -92.410345
    },
    stateName: "Iowa"
  },
  {
    id: "065",
    state: "19",
    name: "Fayette County",
    position: {
      lat: 42.86445,
      lng: -91.839373
    },
    stateName: "Iowa"
  },
  {
    id: "071",
    state: "19",
    name: "Fremont County",
    position: {
      lat: 40.743726,
      lng: -95.599516
    },
    stateName: "Iowa"
  },
  {
    id: "083",
    state: "19",
    name: "Hardin County",
    position: {
      lat: 42.389955,
      lng: -93.241081
    },
    stateName: "Iowa"
  },
  {
    id: "085",
    state: "19",
    name: "Harrison County",
    position: {
      lat: 41.688584,
      lng: -95.827149
    },
    stateName: "Iowa"
  },
  {
    id: "097",
    state: "19",
    name: "Jackson County",
    position: {
      lat: 42.164281,
      lng: -90.574597
    },
    stateName: "Iowa"
  },
  {
    id: "109",
    state: "19",
    name: "Kossuth County",
    position: {
      lat: 43.212478,
      lng: -94.213983
    },
    stateName: "Iowa"
  },
  {
    id: "111",
    state: "18",
    name: "Newton County",
    position: {
      lat: 40.962399,
      lng: -87.402172
    },
    stateName: "Indiana"
  },
  {
    id: "119",
    state: "19",
    name: "Lyon County",
    position: {
      lat: 43.38358,
      lng: -96.207201
    },
    stateName: "Iowa"
  },
  {
    id: "133",
    state: "19",
    name: "Monona County",
    position: {
      lat: 42.049432,
      lng: -95.956566
    },
    stateName: "Iowa"
  },
  {
    id: "143",
    state: "19",
    name: "Osceola County",
    position: {
      lat: 43.378542,
      lng: -95.633788
    },
    stateName: "Iowa"
  },
  {
    id: "167",
    state: "19",
    name: "Sioux County",
    position: {
      lat: 43.082854,
      lng: -96.177929
    },
    stateName: "Iowa"
  },
  {
    id: "173",
    state: "19",
    name: "Taylor County",
    position: {
      lat: 40.737949,
      lng: -94.697108
    },
    stateName: "Iowa"
  },
  {
    id: "185",
    state: "19",
    name: "Wayne County",
    position: {
      lat: 40.739983,
      lng: -93.332613
    },
    stateName: "Iowa"
  },
  {
    id: "191",
    state: "19",
    name: "Winneshiek County",
    position: {
      lat: 43.292989,
      lng: -91.850788
    },
    stateName: "Iowa"
  },
  {
    id: "001",
    state: "20",
    name: "Allen County",
    position: {
      lat: 37.884229,
      lng: -95.300945
    },
    stateName: "Kansas"
  },
  {
    id: "007",
    state: "20",
    name: "Barber County",
    position: {
      lat: 37.222906,
      lng: -98.685052
    },
    stateName: "Kansas"
  },
  {
    id: "011",
    state: "20",
    name: "Bourbon County",
    position: {
      lat: 37.8561,
      lng: -94.850928
    },
    stateName: "Kansas"
  },
  {
    id: "071",
    state: "22",
    name: "Orleans County",
    postal: "Parish",
    position: {
      lat: 30.068636,
      lng: -89.939007
    },
    stateName: "Louisiana"
  },
  {
    id: "075",
    state: "27",
    name: "Lake County",
    position: {
      lat: 47.517111,
      lng: -91.411704
    },
    stateName: "Minnesota"
  },
  {
    id: "161",
    state: "21",
    name: "Mason County",
    position: {
      lat: 38.594068,
      lng: -83.828052
    },
    stateName: "Kentucky"
  },
  {
    id: "123",
    state: "27",
    name: "Ramsey County",
    position: {
      lat: 45.01525,
      lng: -93.100141
    },
    stateName: "Minnesota"
  },
  {
    id: "135",
    state: "27",
    name: "Roseau County",
    position: {
      lat: 48.761066,
      lng: -95.82153
    },
    stateName: "Minnesota"
  },
  {
    id: "151",
    state: "27",
    name: "Swift County",
    position: {
      lat: 45.27545,
      lng: -95.690398
    },
    stateName: "Minnesota"
  },
  {
    id: "155",
    state: "27",
    name: "Traverse County",
    position: {
      lat: 45.76984,
      lng: -96.475049
    },
    stateName: "Minnesota"
  },
  {
    id: "165",
    state: "27",
    name: "Watonwan County",
    position: {
      lat: 43.978366,
      lng: -94.614128
    },
    stateName: "Minnesota"
  },
  {
    id: "167",
    state: "27",
    name: "Wilkin County",
    position: {
      lat: 46.362335,
      lng: -96.476657
    },
    stateName: "Minnesota"
  },
  {
    id: "173",
    state: "27",
    name: "Yellow Medcine County",
    position: {
      lat: 44.715736,
      lng: -95.862756
    },
    stateName: "Minnesota"
  },
  {
    id: "001",
    state: "28",
    name: "Adams County",
    position: {
      lat: 31.486218,
      lng: -91.351781
    },
    stateName: "Mississippi"
  },
  {
    id: "019",
    state: "28",
    name: "Choctaw County",
    position: {
      lat: 33.345964,
      lng: -89.251332
    },
    stateName: "Mississippi"
  },
  {
    id: "025",
    state: "28",
    name: "Clay County",
    position: {
      lat: 33.661734,
      lng: -88.783326
    },
    stateName: "Mississippi"
  },
  {
    id: "027",
    state: "28",
    name: "Coahoma County",
    position: {
      lat: 34.22867,
      lng: -90.603165
    },
    stateName: "Mississippi"
  },
  {
    id: "117",
    state: "19",
    name: "Lucas County",
    position: {
      lat: 41.033344,
      lng: -93.331467
    },
    stateName: "Iowa"
  },
  {
    id: "053",
    state: "37",
    name: "Currituck County",
    position: {
      lat: 36.372174,
      lng: -75.941224
    },
    stateName: "North Carolina"
  },
  {
    id: "079",
    state: "37",
    name: "Greene County",
    position: {
      lat: 35.481933,
      lng: -77.681667
    },
    stateName: "North Carolina"
  },
  {
    id: "089",
    state: "37",
    name: "Henderson County",
    position: {
      lat: 35.336424,
      lng: -82.479634
    },
    stateName: "North Carolina"
  },
  {
    id: "035",
    state: "42",
    name: "Clinton County",
    position: {
      lat: 41.245301,
      lng: -77.649141
    },
    stateName: "Pennsylvania"
  },
  {
    id: "041",
    state: "42",
    name: "Cumberland County",
    position: {
      lat: 40.164782,
      lng: -77.26344
    },
    stateName: "Pennsylvania"
  },
  {
    id: "021",
    state: "20",
    name: "Cherokee County",
    position: {
      lat: 37.169392,
      lng: -94.845698
    },
    stateName: "Kansas"
  },
  {
    id: "055",
    state: "28",
    name: "Issaquena County",
    position: {
      lat: 32.754977,
      lng: -90.988552
    },
    stateName: "Mississippi"
  },
  {
    id: "063",
    state: "28",
    name: "Jefferson County",
    position: {
      lat: 31.733633,
      lng: -91.043878
    },
    stateName: "Mississippi"
  },
  {
    id: "071",
    state: "28",
    name: "Lafayette County",
    position: {
      lat: 34.349298,
      lng: -89.485903
    },
    stateName: "Mississippi"
  },
  {
    id: "001",
    state: "23",
    name: "Androscoggin County",
    position: {
      lat: 44.167681,
      lng: -70.207435
    },
    stateName: "Maine"
  },
  {
    id: "003",
    state: "23",
    name: "Aroostook County",
    position: {
      lat: 46.727057,
      lng: -68.64941
    },
    stateName: "Maine"
  },
  {
    id: "087",
    state: "22",
    name: "St. Bernard County",
    postal: "Parish",
    position: {
      lat: 29.91811,
      lng: -89.263494
    },
    stateName: "Louisiana"
  },
  {
    id: "015",
    state: "38",
    name: "Burleigh County",
    position: {
      lat: 46.971843,
      lng: -100.462001
    },
    stateName: "North Dakota"
  },
  {
    id: "021",
    state: "38",
    name: "Dickey County",
    position: {
      lat: 46.107756,
      lng: -98.496518
    },
    stateName: "North Dakota"
  },
  {
    id: "023",
    state: "38",
    name: "Divide County",
    position: {
      lat: 48.818142,
      lng: -103.497653
    },
    stateName: "North Dakota"
  },
  {
    id: "033",
    state: "38",
    name: "Golden Valley County",
    position: {
      lat: 46.938924,
      lng: -103.844612
    },
    stateName: "North Dakota"
  },
  {
    id: "049",
    state: "38",
    name: "Mchenry County",
    position: {
      lat: 48.233842,
      lng: -100.633267
    },
    stateName: "North Dakota"
  },
  {
    id: "047",
    state: "28",
    name: "Harrison County",
    position: {
      lat: 30.416536,
      lng: -89.083376
    },
    stateName: "Mississippi"
  },
  {
    id: "105",
    state: "28",
    name: "Oktibbeha County",
    position: {
      lat: 33.422313,
      lng: -88.876151
    },
    stateName: "Mississippi"
  },
  {
    id: "111",
    state: "28",
    name: "Perry County",
    position: {
      lat: 31.169307,
      lng: -88.988755
    },
    stateName: "Mississippi"
  },
  {
    id: "113",
    state: "28",
    name: "Pike County",
    position: {
      lat: 31.177517,
      lng: -90.397725
    },
    stateName: "Mississippi"
  },
  {
    id: "125",
    state: "28",
    name: "Sharkey County",
    position: {
      lat: 32.89249,
      lng: -90.827388
    },
    stateName: "Mississippi"
  },
  {
    id: "131",
    state: "28",
    name: "Stone County",
    position: {
      lat: 30.790184,
      lng: -89.112297
    },
    stateName: "Mississippi"
  },
  {
    id: "047",
    state: "35",
    name: "San Miguel County",
    position: {
      lat: 35.476876,
      lng: -104.803515
    },
    stateName: "New Mexico"
  },
  {
    id: "051",
    state: "35",
    name: "Sierra County",
    position: {
      lat: 33.119479,
      lng: -107.188161
    },
    stateName: "New Mexico"
  },
  {
    id: "055",
    state: "35",
    name: "Taos County",
    position: {
      lat: 36.576529,
      lng: -105.637987
    },
    stateName: "New Mexico"
  },
  {
    id: "001",
    state: "36",
    name: "Albany County",
    position: {
      lat: 42.588271,
      lng: -73.974014
    },
    stateName: "New York"
  },
  {
    id: "005",
    state: "36",
    name: "Bronx County",
    position: {
      lat: 40.848711,
      lng: -73.852939
    },
    stateName: "New York"
  },
  {
    id: "043",
    state: "42",
    name: "Dauphin County",
    position: {
      lat: 40.412565,
      lng: -76.792634
    },
    stateName: "Pennsylvania"
  },
  {
    id: "059",
    state: "42",
    name: "Greene County",
    position: {
      lat: 39.848983,
      lng: -80.225694
    },
    stateName: "Pennsylvania"
  },
  {
    id: "065",
    state: "42",
    name: "Jefferson County",
    position: {
      lat: 41.130287,
      lng: -78.999044
    },
    stateName: "Pennsylvania"
  },
  {
    id: "071",
    state: "42",
    name: "Lancaster County",
    position: {
      lat: 40.041992,
      lng: -76.250198
    },
    stateName: "Pennsylvania"
  },
  {
    id: "077",
    state: "42",
    name: "Lehigh County",
    position: {
      lat: 40.614241,
      lng: -75.590627
    },
    stateName: "Pennsylvania"
  },
  {
    id: "083",
    state: "42",
    name: "Mc Kean County",
    position: {
      lat: 41.81459,
      lng: -78.572463
    },
    stateName: "Pennsylvania"
  },
  {
    id: "035",
    state: "41",
    name: "Klamath County",
    position: {
      lat: 42.683761,
      lng: -121.646168
    },
    stateName: "Oregon"
  },
  {
    id: "005",
    state: "23",
    name: "Cumberland County",
    position: {
      lat: 43.808348,
      lng: -70.330375
    },
    stateName: "Maine"
  },
  {
    id: "029",
    state: "45",
    name: "Colleton County",
    position: {
      lat: 32.83498,
      lng: -80.655345
    },
    stateName: "South Carolina"
  },
  {
    id: "041",
    state: "45",
    name: "Florence County",
    position: {
      lat: 34.028535,
      lng: -79.710233
    },
    stateName: "South Carolina"
  },
  {
    id: "043",
    state: "45",
    name: "Georgetown County",
    position: {
      lat: 33.41753,
      lng: -79.300812
    },
    stateName: "South Carolina"
  },
  {
    id: "023",
    state: "47",
    name: "Chester County",
    position: {
      lat: 35.416639,
      lng: -88.605505
    },
    stateName: "Tennessee"
  },
  {
    id: "051",
    state: "38",
    name: "Mcintosh County:",
    position: {
      lat: 46.117034,
      lng: -99.443041
    },
    stateName: "North Dakota"
  },
  {
    id: "057",
    state: "38",
    name: "Mercer County",
    position: {
      lat: 47.303279,
      lng: -101.820765
    },
    stateName: "North Dakota"
  },
  {
    id: "067",
    state: "40",
    name: "Jefferson County",
    position: {
      lat: 34.105092,
      lng: -97.838814
    },
    stateName: "Oklahoma"
  },
  {
    id: "077",
    state: "40",
    name: "Latimer County",
    position: {
      lat: 34.875137,
      lng: -95.272263
    },
    stateName: "Oklahoma"
  },
  {
    id: "079",
    state: "40",
    name: "Le Flore County",
    position: {
      lat: 34.899642,
      lng: -94.703491
    },
    stateName: "Oklahoma"
  },
  {
    id: "089",
    state: "40",
    name: "Mccurtain County",
    position: {
      lat: 34.117073,
      lng: -94.766086
    },
    stateName: "Oklahoma"
  },
  {
    id: "095",
    state: "40",
    name: "Marshall County",
    position: {
      lat: 34.027007,
      lng: -96.770533
    },
    stateName: "Oklahoma"
  },
  {
    id: "105",
    state: "40",
    name: "Nowata County",
    position: {
      lat: 36.789615,
      lng: -95.613312
    },
    stateName: "Oklahoma"
  },
  {
    id: "113",
    state: "40",
    name: "Osage County",
    position: {
      lat: 36.62468,
      lng: -96.408385
    },
    stateName: "Oklahoma"
  },
  {
    id: "015",
    state: "36",
    name: "Chemung County",
    position: {
      lat: 42.155281,
      lng: -76.747179
    },
    stateName: "New York"
  },
  {
    id: "023",
    state: "36",
    name: "Cortland County",
    position: {
      lat: 42.593824,
      lng: -76.076265
    },
    stateName: "New York"
  },
  {
    id: "025",
    state: "36",
    name: "Delaware County",
    position: {
      lat: 42.193987,
      lng: -74.966728
    },
    stateName: "New York"
  },
  {
    id: "029",
    state: "36",
    name: "Erie County",
    position: {
      lat: 42.752759,
      lng: -78.778192
    },
    stateName: "New York"
  },
  {
    id: "139",
    state: "40",
    name: "Texas County",
    position: {
      lat: 36.741964,
      lng: -101.488434
    },
    stateName: "Oklahoma"
  },
  {
    id: "151",
    state: "40",
    name: "Woods County",
    position: {
      lat: 36.765141,
      lng: -98.868967
    },
    stateName: "Oklahoma"
  },
  {
    id: "001",
    state: "41",
    name: "Baker County",
    position: {
      lat: 44.703427,
      lng: -117.691933
    },
    stateName: "Oregon"
  },
  {
    id: "007",
    state: "41",
    name: "Clatsop County",
    position: {
      lat: 46.024509,
      lng: -123.705014
    },
    stateName: "Oregon"
  },
  {
    id: "041",
    state: "41",
    name: "Lincoln County",
    position: {
      lat: 44.641076,
      lng: -123.911183
    },
    stateName: "Oregon"
  },
  {
    id: "139",
    state: "31",
    name: "Pierce County",
    position: {
      lat: 42.271413,
      lng: -97.610992
    },
    stateName: "Nebraska"
  },
  {
    id: "145",
    state: "31",
    name: "Red Willow County",
    position: {
      lat: 40.16942,
      lng: -100.468576
    },
    stateName: "Nebraska"
  },
  {
    id: "029",
    state: "19",
    name: "Cass County",
    position: {
      lat: 41.333824,
      lng: -94.933302
    },
    stateName: "Iowa"
  },
  {
    id: "037",
    state: "19",
    name: "Chickasaw County",
    position: {
      lat: 43.059741,
      lng: -92.31721
    },
    stateName: "Iowa"
  },
  {
    id: "083",
    state: "17",
    name: "Jersey County",
    position: {
      lat: 39.080192,
      lng: -90.361365
    },
    stateName: "Illinois"
  },
  {
    id: "085",
    state: "17",
    name: "Jo Daviess County",
    position: {
      lat: 42.362391,
      lng: -90.211471
    },
    stateName: "Illinois"
  },
  {
    id: "097",
    state: "17",
    name: "Lake County",
    position: {
      lat: 42.326444,
      lng: -87.436118
    },
    stateName: "Illinois"
  },
  {
    id: "115",
    state: "17",
    name: "Macon County",
    position: {
      lat: 39.860237,
      lng: -88.961529
    },
    stateName: "Illinois"
  },
  {
    id: "119",
    state: "17",
    name: "Madison County",
    position: {
      lat: 38.827082,
      lng: -89.900195
    },
    stateName: "Illinois"
  },
  {
    id: "039",
    state: "47",
    name: "Decatur County",
    position: {
      lat: 35.603422,
      lng: -88.107384
    },
    stateName: "Tennessee"
  },
  {
    id: "043",
    state: "47",
    name: "Dickson County",
    position: {
      lat: 36.145533,
      lng: -87.364155
    },
    stateName: "Tennessee"
  },
  {
    id: "053",
    state: "47",
    name: "Gibson County",
    position: {
      lat: 35.991694,
      lng: -88.933776
    },
    stateName: "Tennessee"
  },
  {
    id: "055",
    state: "47",
    name: "Giles County",
    position: {
      lat: 35.202723,
      lng: -87.035319
    },
    stateName: "Tennessee"
  },
  {
    id: "013",
    state: "23",
    name: "Knox County",
    position: {
      lat: 44.042045,
      lng: -69.038515
    },
    stateName: "Maine"
  },
  {
    id: "019",
    state: "23",
    name: "Penobscot County",
    position: {
      lat: 45.390602,
      lng: -68.657487
    },
    stateName: "Maine"
  },
  {
    id: "025",
    state: "23",
    name: "Somerset County",
    position: {
      lat: 45.507482,
      lng: -69.97604
    },
    stateName: "Maine"
  },
  {
    id: "121",
    state: "40",
    name: "Pittsburg County",
    position: {
      lat: 34.92554,
      lng: -95.74813
    },
    stateName: "Oklahoma"
  },
  {
    id: "129",
    state: "40",
    name: "Roger Mills County",
    position: {
      lat: 35.708554,
      lng: -99.741572
    },
    stateName: "Oklahoma"
  },
  {
    id: "045",
    state: "24",
    name: "Wicomico County",
    position: {
      lat: 38.367389,
      lng: -75.632206
    },
    stateName: "Maryland"
  },
  {
    id: "025",
    state: "25",
    name: "Suffolk County",
    position: {
      lat: 42.33196,
      lng: -71.020173
    },
    stateName: "Massachusetts"
  },
  {
    id: "001",
    state: "26",
    name: "Alcona County",
    position: {
      lat: 44.682535,
      lng: -82.83408
    },
    stateName: "Michigan"
  },
  {
    id: "015",
    state: "41",
    name: "Curry County",
    position: {
      lat: 42.466671,
      lng: -124.211407
    },
    stateName: "Oregon"
  },
  {
    id: "025",
    state: "41",
    name: "Harney County",
    position: {
      lat: 43.064355,
      lng: -118.985949
    },
    stateName: "Oregon"
  },
  {
    id: "089",
    state: "26",
    name: "Leelanau County",
    position: {
      lat: 45.146182,
      lng: -86.051574
    },
    stateName: "Michigan"
  },
  {
    id: "105",
    state: "26",
    name: "Mason County",
    position: {
      lat: 43.996636,
      lng: -86.750814
    },
    stateName: "Michigan"
  },
  {
    id: "147",
    state: "28",
    name: "Walthall County",
    position: {
      lat: 31.164492,
      lng: -90.103431
    },
    stateName: "Mississippi"
  },
  {
    id: "107",
    state: "42",
    name: "Schuylkill County",
    position: {
      lat: 40.70369,
      lng: -76.2178
    },
    stateName: "Pennsylvania"
  },
  {
    id: "127",
    state: "42",
    name: "Wayne County",
    position: {
      lat: 41.646589,
      lng: -75.292485
    },
    stateName: "Pennsylvania"
  },
  {
    id: "129",
    state: "42",
    name: "Westmoreland County",
    position: {
      lat: 40.311068,
      lng: -79.466688
    },
    stateName: "Pennsylvania"
  },
  {
    id: "015",
    state: "49",
    name: "Emery County",
    position: {
      lat: 39.009028,
      lng: -110.721111
    },
    stateName: "Utah"
  },
  {
    id: "021",
    state: "49",
    name: "Iron County",
    position: {
      lat: 37.882727,
      lng: -113.290059
    },
    stateName: "Utah"
  },
  {
    id: "025",
    state: "49",
    name: "Kane County",
    position: {
      lat: 37.275118,
      lng: -111.815413
    },
    stateName: "Utah"
  },
  {
    id: "029",
    state: "47",
    name: "Cocke County",
    position: {
      lat: 35.916198,
      lng: -83.119223
    },
    stateName: "Tennessee"
  },
  {
    id: "027",
    state: "23",
    name: "Waldo County",
    position: {
      lat: 44.505361,
      lng: -69.139678
    },
    stateName: "Maine"
  },
  {
    id: "157",
    state: "31",
    name: "Scott Bluff County",
    position: {
      lat: 41.851589,
      lng: -103.70586
    },
    stateName: "Nebraska"
  },
  {
    id: "165",
    state: "31",
    name: "Sioux County",
    position: {
      lat: 42.483806,
      lng: -103.742605
    },
    stateName: "Nebraska"
  },
  {
    id: "175",
    state: "31",
    name: "Valley County",
    position: {
      lat: 41.564094,
      lng: -98.983484
    },
    stateName: "Nebraska"
  },
  {
    id: "177",
    state: "31",
    name: "Washington County",
    position: {
      lat: 41.533979,
      lng: -96.224574
    },
    stateName: "Nebraska"
  },
  {
    id: "001",
    state: "32",
    name: "Churchill County",
    position: {
      lat: 39.525701,
      lng: -118.270419
    },
    stateName: "Nevada"
  },
  {
    id: "003",
    state: "32",
    name: "Clark County",
    position: {
      lat: 36.214236,
      lng: -115.013819
    },
    stateName: "Nevada"
  },
  {
    id: "011",
    state: "26",
    name: "Arenac County",
    position: {
      lat: 44.03687,
      lng: -83.740675
    },
    stateName: "Michigan"
  },
  {
    id: "067",
    state: "30",
    name: "Park County",
    position: {
      lat: 45.421905,
      lng: -110.532798
    },
    stateName: "Montana"
  },
  {
    id: "073",
    state: "30",
    name: "Pondera County",
    position: {
      lat: 48.230312,
      lng: -112.219928
    },
    stateName: "Montana"
  },
  {
    id: "079",
    state: "30",
    name: "Prairie County",
    position: {
      lat: 46.852434,
      lng: -105.375808
    },
    stateName: "Montana"
  },
  {
    id: "081",
    state: "30",
    name: "Ravalli County",
    position: {
      lat: 46.079298,
      lng: -114.119065
    },
    stateName: "Montana"
  },
  {
    id: "089",
    state: "30",
    name: "Sanders County",
    position: {
      lat: 47.756469,
      lng: -115.180225
    },
    stateName: "Montana"
  },
  {
    id: "095",
    state: "30",
    name: "Stillwater County",
    position: {
      lat: 45.663496,
      lng: -109.391752
    },
    stateName: "Montana"
  },
  {
    id: "109",
    state: "26",
    name: "Menominee County",
    position: {
      lat: 45.544174,
      lng: -87.509892
    },
    stateName: "Michigan"
  },
  {
    id: "149",
    state: "28",
    name: "Warren County",
    position: {
      lat: 32.357005,
      lng: -90.851791
    },
    stateName: "Mississippi"
  },
  {
    id: "161",
    state: "28",
    name: "Yalobusha County",
    position: {
      lat: 34.030669,
      lng: -89.703793
    },
    stateName: "Mississippi"
  },
  {
    id: "005",
    state: "29",
    name: "Atchison County",
    position: {
      lat: 40.431846,
      lng: -95.437555
    },
    stateName: "Missouri"
  },
  {
    id: "011",
    state: "29",
    name: "Barton County",
    position: {
      lat: 37.500799,
      lng: -94.344089
    },
    stateName: "Missouri"
  },
  {
    id: "019",
    state: "29",
    name: "Boone County",
    position: {
      lat: 38.989657,
      lng: -92.310779
    },
    stateName: "Missouri"
  },
  {
    id: "025",
    state: "29",
    name: "Caldwell County",
    position: {
      lat: 39.658998,
      lng: -93.979179
    },
    stateName: "Missouri"
  },
  {
    id: "085",
    state: "39",
    name: "Lake County",
    position: {
      lat: 41.924116,
      lng: -81.392643
    },
    stateName: "Ohio"
  },
  {
    id: "095",
    state: "39",
    name: "Lucas County",
    position: {
      lat: 41.682321,
      lng: -83.468867
    },
    stateName: "Ohio"
  },
  {
    id: "009",
    state: "44",
    name: "Washington County",
    position: {
      lat: 41.401162,
      lng: -71.617612
    },
    stateName: "Rhode Island"
  },
  {
    id: "011",
    state: "45",
    name: "Barnwell County",
    position: {
      lat: 33.26068,
      lng: -81.433753
    },
    stateName: "South Carolina"
  },
  {
    id: "013",
    state: "45",
    name: "Beaufort County",
    position: {
      lat: 32.358147,
      lng: -80.68932
    },
    stateName: "South Carolina"
  },
  {
    id: "015",
    state: "32",
    name: "Lander County",
    position: {
      lat: 39.900211,
      lng: -117.04724
    },
    stateName: "Nevada"
  },
  {
    id: "019",
    state: "32",
    name: "Lyon County",
    position: {
      lat: 39.022214,
      lng: -119.197417
    },
    stateName: "Nevada"
  },
  {
    id: "029",
    state: "32",
    name: "Storey County",
    position: {
      lat: 39.438385,
      lng: -119.524646
    },
    stateName: "Nevada"
  },
  {
    id: "111",
    state: "39",
    name: "Monroe County",
    position: {
      lat: 39.725969,
      lng: -81.090658
    },
    stateName: "Ohio"
  },
  {
    id: "113",
    state: "39",
    name: "Montgomery County",
    position: {
      lat: 39.755218,
      lng: -84.290546
    },
    stateName: "Ohio"
  },
  {
    id: "123",
    state: "39",
    name: "Ottawa County",
    position: {
      lat: 41.544467,
      lng: -83.009253
    },
    stateName: "Ohio"
  },
  {
    id: "145",
    state: "39",
    name: "Scioto County",
    position: {
      lat: 38.815019,
      lng: -82.999028
    },
    stateName: "Ohio"
  },
  {
    id: "155",
    state: "39",
    name: "Trumbull County",
    position: {
      lat: 41.308936,
      lng: -80.767656
    },
    stateName: "Ohio"
  },
  {
    id: "103",
    state: "30",
    name: "Treasure County",
    position: {
      lat: 46.209636,
      lng: -107.280494
    },
    stateName: "Montana"
  },
  {
    id: "105",
    state: "30",
    name: "Valley County",
    position: {
      lat: 48.365713,
      lng: -106.687883
    },
    stateName: "Montana"
  },
  {
    id: "077",
    state: "38",
    name: "Richland County",
    position: {
      lat: 46.265219,
      lng: -96.93796
    },
    stateName: "North Dakota"
  },
  {
    id: "085",
    state: "38",
    name: "Sioux County",
    position: {
      lat: 46.109282,
      lng: -101.047525
    },
    stateName: "North Dakota"
  },
  {
    id: "099",
    state: "38",
    name: "Walsh County",
    position: {
      lat: 48.376979,
      lng: -97.72223
    },
    stateName: "North Dakota"
  },
  {
    id: "105",
    state: "38",
    name: "Williams County",
    position: {
      lat: 48.345867,
      lng: -103.4874
    },
    stateName: "North Dakota"
  },
  {
    id: "031",
    state: "29",
    name: "Cape Girardeau County",
    position: {
      lat: 37.383882,
      lng: -89.684908
    },
    stateName: "Missouri"
  },
  {
    id: "037",
    state: "29",
    name: "Cass County",
    position: {
      lat: 38.647159,
      lng: -94.354242
    },
    stateName: "Missouri"
  },
  {
    id: "047",
    state: "36",
    name: "Kings County",
    position: {
      lat: 40.635133,
      lng: -73.950777
    },
    stateName: "New York"
  },
  {
    id: "053",
    state: "36",
    name: "Madison County",
    position: {
      lat: 42.910026,
      lng: -75.663575
    },
    stateName: "New York"
  },
  {
    id: "061",
    state: "36",
    name: "New York County",
    position: {
      lat: 40.776557,
      lng: -73.970174
    },
    stateName: "New York"
  },
  {
    id: "067",
    state: "36",
    name: "Onondaga County",
    position: {
      lat: 43.00653,
      lng: -76.196117
    },
    stateName: "New York"
  },
  {
    id: "045",
    state: "41",
    name: "Malheur County",
    position: {
      lat: 43.1877,
      lng: -117.603976
    },
    stateName: "Oregon"
  },
  {
    id: "055",
    state: "41",
    name: "Sherman County",
    position: {
      lat: 45.399216,
      lng: -120.678512
    },
    stateName: "Oregon"
  },
  {
    id: "057",
    state: "41",
    name: "Tillamook County",
    position: {
      lat: 45.455743,
      lng: -123.759327
    },
    stateName: "Oregon"
  },
  {
    id: "063",
    state: "41",
    name: "Wallowa County",
    position: {
      lat: 45.593753,
      lng: -117.18558
    },
    stateName: "Oregon"
  },
  {
    id: "067",
    state: "41",
    name: "Washington County",
    position: {
      lat: 45.553542,
      lng: -123.097615
    },
    stateName: "Oregon"
  },
  {
    id: "101",
    state: "29",
    name: "Johnson County",
    position: {
      lat: 38.74588,
      lng: -93.805999
    },
    stateName: "Missouri"
  },
  {
    id: "107",
    state: "29",
    name: "Lafayette County",
    position: {
      lat: 39.068705,
      lng: -93.802639
    },
    stateName: "Missouri"
  },
  {
    id: "113",
    state: "29",
    name: "Lincoln County",
    position: {
      lat: 39.058568,
      lng: -90.957771
    },
    stateName: "Missouri"
  },
  {
    id: "123",
    state: "29",
    name: "Madison County",
    position: {
      lat: 37.473235,
      lng: -90.345453
    },
    stateName: "Missouri"
  },
  {
    id: "127",
    state: "29",
    name: "Marion County",
    position: {
      lat: 39.807538,
      lng: -91.635379
    },
    stateName: "Missouri"
  },
  {
    id: "163",
    state: "39",
    name: "Vinton County",
    position: {
      lat: 39.252014,
      lng: -82.485961
    },
    stateName: "Ohio"
  },
  {
    id: "073",
    state: "47",
    name: "Hawkins County",
    position: {
      lat: 36.452206,
      lng: -82.931386
    },
    stateName: "Tennessee"
  },
  {
    id: "079",
    state: "47",
    name: "Henry County",
    position: {
      lat: 36.325398,
      lng: -88.300384
    },
    stateName: "Tennessee"
  },
  {
    id: "089",
    state: "47",
    name: "Jefferson County",
    position: {
      lat: 36.048479,
      lng: -83.440966
    },
    stateName: "Tennessee"
  },
  {
    id: "091",
    state: "47",
    name: "Johnson County",
    position: {
      lat: 36.453204,
      lng: -81.861237
    },
    stateName: "Tennessee"
  },
  {
    id: "097",
    state: "47",
    name: "Lauderdale County",
    position: {
      lat: 35.762951,
      lng: -89.627732
    },
    stateName: "Tennessee"
  },
  {
    id: "103",
    state: "47",
    name: "Lincoln County",
    position: {
      lat: 35.142532,
      lng: -86.593388
    },
    stateName: "Tennessee"
  },
  {
    id: "115",
    state: "47",
    name: "Marion County",
    position: {
      lat: 35.133422,
      lng: -85.618399
    },
    stateName: "Tennessee"
  },
  {
    id: "121",
    state: "47",
    name: "Meigs County",
    position: {
      lat: 35.503397,
      lng: -84.823888
    },
    stateName: "Tennessee"
  },
  {
    id: "123",
    state: "47",
    name: "Monroe County",
    position: {
      lat: 35.447666,
      lng: -84.249786
    },
    stateName: "Tennessee"
  },
  {
    id: "131",
    state: "47",
    name: "Obion County",
    position: {
      lat: 36.358175,
      lng: -89.150175
    },
    stateName: "Tennessee"
  },
  {
    id: "007",
    state: "39",
    name: "Ashtabula County",
    position: {
      lat: 41.906644,
      lng: -80.745641
    },
    stateName: "Ohio"
  },
  {
    id: "371",
    state: "48",
    name: "Pecos County",
    position: {
      lat: 30.770894,
      lng: -102.71986
    },
    stateName: "Texas"
  },
  {
    id: "377",
    state: "48",
    name: "Presidio County",
    position: {
      lat: 30.005891,
      lng: -104.261619
    },
    stateName: "Texas"
  },
  {
    id: "391",
    state: "48",
    name: "Refugio County",
    position: {
      lat: 28.312496,
      lng: -97.160479
    },
    stateName: "Texas"
  },
  {
    id: "401",
    state: "48",
    name: "Rusk County",
    position: {
      lat: 32.109423,
      lng: -94.756382
    },
    stateName: "Texas"
  },
  {
    id: "407",
    state: "48",
    name: "San Jacinto County",
    position: {
      lat: 30.574218,
      lng: -95.162852
    },
    stateName: "Texas"
  },
  {
    id: "409",
    state: "48",
    name: "San Patricio County",
    position: {
      lat: 28.011782,
      lng: -97.517165
    },
    stateName: "Texas"
  },
  {
    id: "079",
    state: "36",
    name: "Putnam County",
    position: {
      lat: 41.427904,
      lng: -73.743882
    },
    stateName: "New York"
  },
  {
    id: "443",
    state: "48",
    name: "Terrell County",
    position: {
      lat: 30.232332,
      lng: -102.072539
    },
    stateName: "Texas"
  },
  {
    id: "461",
    state: "48",
    name: "Upton County",
    position: {
      lat: 31.353849,
      lng: -102.042013
    },
    stateName: "Texas"
  },
  {
    id: "465",
    state: "48",
    name: "Val Verde County",
    position: {
      lat: 29.884961,
      lng: -101.146646
    },
    stateName: "Texas"
  },
  {
    id: "473",
    state: "48",
    name: "Waller County",
    position: {
      lat: 30.013578,
      lng: -95.982102
    },
    stateName: "Texas"
  },
  {
    id: "479",
    state: "48",
    name: "Webb County",
    position: {
      lat: 27.770584,
      lng: -99.326641
    },
    stateName: "Texas"
  },
  {
    id: "487",
    state: "48",
    name: "Wilbarger County",
    position: {
      lat: 34.08492,
      lng: -99.24244
    },
    stateName: "Texas"
  },
  {
    id: "495",
    state: "48",
    name: "Winkler County",
    position: {
      lat: 31.831416,
      lng: -103.055986
    },
    stateName: "Texas"
  },
  {
    id: "501",
    state: "48",
    name: "Yoakum County",
    position: {
      lat: 33.172397,
      lng: -102.823771
    },
    stateName: "Texas"
  },
  {
    id: "505",
    state: "48",
    name: "Zapata County",
    position: {
      lat: 26.996981,
      lng: -99.182603
    },
    stateName: "Texas"
  },
  {
    id: "003",
    state: "49",
    name: "Box Elder County",
    position: {
      lat: 41.476021,
      lng: -113.052922
    },
    stateName: "Utah"
  },
  {
    id: "005",
    state: "48",
    name: "Angelina County",
    position: {
      lat: 31.251951,
      lng: -94.611056
    },
    stateName: "Texas"
  },
  {
    id: "007",
    state: "48",
    name: "Aransas County",
    position: {
      lat: 28.104225,
      lng: -96.977983
    },
    stateName: "Texas"
  },
  {
    id: "037",
    state: "48",
    name: "Bowie County",
    position: {
      lat: 33.446051,
      lng: -94.422375
    },
    stateName: "Texas"
  },
  {
    id: "043",
    state: "48",
    name: "Brewster County",
    position: {
      lat: 29.808997,
      lng: -103.252458
    },
    stateName: "Texas"
  },
  {
    id: "183",
    state: "29",
    name: "St. Charles County",
    position: {
      lat: 38.781102,
      lng: -90.674915
    },
    stateName: "Missouri"
  },
  {
    id: "189",
    state: "29",
    name: "St. Louis County",
    position: {
      lat: 38.640702,
      lng: -90.445954
    },
    stateName: "Missouri"
  },
  {
    id: "199",
    state: "29",
    name: "Scotland County",
    position: {
      lat: 40.447686,
      lng: -92.142824
    },
    stateName: "Missouri"
  },
  {
    id: "005",
    state: "24",
    name: "Baltimore County",
    position: {
      lat: 39.443167,
      lng: -76.616569
    },
    stateName: "Maryland"
  },
  {
    id: "025",
    state: "24",
    name: "Harford County",
    position: {
      lat: 39.537429,
      lng: -76.299789
    },
    stateName: "Maryland"
  },
  {
    id: "013",
    state: "26",
    name: "Baraga County",
    position: {
      lat: 46.696068,
      lng: -88.356022
    },
    stateName: "Michigan"
  },
  {
    id: "029",
    state: "26",
    name: "Charlevoix County",
    position: {
      lat: 45.513164,
      lng: -85.450392
    },
    stateName: "Michigan"
  },
  {
    id: "111",
    state: "30",
    name: "Yellowstone County",
    position: {
      lat: 45.936987,
      lng: -108.276656
    },
    stateName: "Montana"
  },
  {
    id: "015",
    state: "31",
    name: "Boyd County",
    position: {
      lat: 42.894448,
      lng: -98.773022
    },
    stateName: "Nebraska"
  },
  {
    id: "021",
    state: "31",
    name: "Burt County",
    position: {
      lat: 41.854179,
      lng: -96.337746
    },
    stateName: "Nebraska"
  },
  {
    id: "121",
    state: "26",
    name: "Muskegon County",
    position: {
      lat: 43.289258,
      lng: -86.751892
    },
    stateName: "Michigan"
  },
  {
    id: "127",
    state: "26",
    name: "Oceana County",
    position: {
      lat: 43.647255,
      lng: -86.807575
    },
    stateName: "Michigan"
  },
  {
    id: "139",
    state: "26",
    name: "Ottawa County",
    position: {
      lat: 42.942346,
      lng: -86.655342
    },
    stateName: "Michigan"
  },
  {
    id: "141",
    state: "26",
    name: "Presque Isle County",
    position: {
      lat: 45.489515,
      lng: -83.384019
    },
    stateName: "Michigan"
  },
  {
    id: "151",
    state: "26",
    name: "Sanilac County",
    position: {
      lat: 43.449155,
      lng: -82.642815
    },
    stateName: "Michigan"
  },
  {
    id: "153",
    state: "26",
    name: "Schoolcraft County",
    position: {
      lat: 46.020758,
      lng: -86.199352
    },
    stateName: "Michigan"
  },
  {
    id: "019",
    state: "45",
    name: "Charleston County",
    position: {
      lat: 32.800458,
      lng: -79.94248
    },
    stateName: "South Carolina"
  },
  {
    id: "071",
    state: "46",
    name: "Jackson County",
    position: {
      lat: 43.677294,
      lng: -101.626455
    },
    stateName: "South Dakota"
  },
  {
    id: "081",
    state: "46",
    name: "Lawrence County",
    position: {
      lat: 44.353669,
      lng: -103.796528
    },
    stateName: "South Dakota"
  },
  {
    id: "083",
    state: "46",
    name: "Lincoln County",
    position: {
      lat: 43.27942,
      lng: -96.722286
    },
    stateName: "South Dakota"
  },
  {
    id: "089",
    state: "46",
    name: "Mc Pherson County",
    position: {
      lat: 45.784249,
      lng: -99.211421
    },
    stateName: "South Dakota"
  },
  {
    id: "099",
    state: "46",
    name: "Minnehaha County",
    position: {
      lat: 43.667472,
      lng: -96.795726
    },
    stateName: "South Dakota"
  },
  {
    id: "105",
    state: "46",
    name: "Perkins County",
    position: {
      lat: 45.483387,
      lng: -102.467995
    },
    stateName: "South Dakota"
  },
  {
    id: "109",
    state: "46",
    name: "Roberts County",
    position: {
      lat: 45.623397,
      lng: -96.947551
    },
    stateName: "South Dakota"
  },
  {
    id: "001",
    state: "33",
    name: "Belknap County",
    position: {
      lat: 43.519109,
      lng: -71.425366
    },
    stateName: "New Hampshire"
  },
  {
    id: "007",
    state: "33",
    name: "Coos County",
    position: {
      lat: 44.652419,
      lng: -71.289383
    },
    stateName: "New Hampshire"
  },
  {
    id: "011",
    state: "33",
    name: "Hillsborough County",
    position: {
      lat: 42.911643,
      lng: -71.723101
    },
    stateName: "New Hampshire"
  },
  {
    id: "013",
    state: "33",
    name: "Merrimack County",
    position: {
      lat: 43.299485,
      lng: -71.68013
    },
    stateName: "New Hampshire"
  },
  {
    id: "001",
    state: "34",
    name: "Atlantic County",
    position: {
      lat: 39.469354,
      lng: -74.633758
    },
    stateName: "New Jersey"
  },
  {
    id: "005",
    state: "34",
    name: "Burlington County",
    position: {
      lat: 39.875786,
      lng: -74.663006
    },
    stateName: "New Jersey"
  },
  {
    id: "011",
    state: "34",
    name: "Cumberland County",
    position: {
      lat: 39.328387,
      lng: -75.121644
    },
    stateName: "New Jersey"
  },
  {
    id: "031",
    state: "31",
    name: "Cherry County",
    position: {
      lat: 42.566985,
      lng: -101.072622
    },
    stateName: "Nebraska"
  },
  {
    id: "043",
    state: "31",
    name: "Dakota County",
    position: {
      lat: 42.390456,
      lng: -96.562549
    },
    stateName: "Nebraska"
  },
  {
    id: "049",
    state: "31",
    name: "Deuel County",
    position: {
      lat: 41.112988,
      lng: -102.332604
    },
    stateName: "Nebraska"
  },
  {
    id: "055",
    state: "31",
    name: "Douglas County",
    position: {
      lat: 41.297091,
      lng: -96.154066
    },
    stateName: "Nebraska"
  },
  {
    id: "061",
    state: "31",
    name: "Franklin County",
    position: {
      lat: 40.183203,
      lng: -98.96208
    },
    stateName: "Nebraska"
  },
  {
    id: "067",
    state: "31",
    name: "Gage County",
    position: {
      lat: 40.255234,
      lng: -96.683453
    },
    stateName: "Nebraska"
  },
  {
    id: "035",
    state: "39",
    name: "Cuyahoga County",
    position: {
      lat: 41.760392,
      lng: -81.724217
    },
    stateName: "Ohio"
  },
  {
    id: "051",
    state: "39",
    name: "Fulton County",
    position: {
      lat: 41.597264,
      lng: -84.124267
    },
    stateName: "Ohio"
  },
  {
    id: "053",
    state: "39",
    name: "Gallia County",
    position: {
      lat: 38.817046,
      lng: -82.301746
    },
    stateName: "Ohio"
  },
  {
    id: "063",
    state: "29",
    name: "De Kalb County",
    position: {
      lat: 39.894665,
      lng: -94.40719
    },
    stateName: "Missouri"
  },
  {
    id: "069",
    state: "29",
    name: "Dunklin County",
    position: {
      lat: 36.153025,
      lng: -90.062254
    },
    stateName: "Missouri"
  },
  {
    id: "081",
    state: "29",
    name: "Harrison County",
    position: {
      lat: 40.34562,
      lng: -93.992582
    },
    stateName: "Missouri"
  },
  {
    id: "089",
    state: "29",
    name: "Howard County",
    position: {
      lat: 39.143365,
      lng: -92.695926
    },
    stateName: "Missouri"
  },
  {
    id: "091",
    state: "29",
    name: "Howell County",
    position: {
      lat: 36.774369,
      lng: -91.887368
    },
    stateName: "Missouri"
  },
  {
    id: "015",
    state: "37",
    name: "Bertie County",
    position: {
      lat: 36.06133,
      lng: -76.962367
    },
    stateName: "North Carolina"
  },
  {
    id: "023",
    state: "37",
    name: "Burke County",
    position: {
      lat: 35.746182,
      lng: -81.70618
    },
    stateName: "North Carolina"
  },
  {
    id: "029",
    state: "37",
    name: "Camden County",
    position: {
      lat: 36.342344,
      lng: -76.162488
    },
    stateName: "North Carolina"
  },
  {
    id: "031",
    state: "37",
    name: "Carteret County",
    position: {
      lat: 34.858313,
      lng: -76.526967
    },
    stateName: "North Carolina"
  },
  {
    id: "133",
    state: "29",
    name: "Mississippi County",
    position: {
      lat: 36.826264,
      lng: -89.295929
    },
    stateName: "Missouri"
  },
  {
    id: "143",
    state: "29",
    name: "New Madrid County",
    position: {
      lat: 36.594261,
      lng: -89.655949
    },
    stateName: "Missouri"
  },
  {
    id: "149",
    state: "29",
    name: "Oregon County",
    position: {
      lat: 36.684867,
      lng: -91.402902
    },
    stateName: "Missouri"
  },
  {
    id: "155",
    state: "29",
    name: "Pemiscot County",
    position: {
      lat: 36.209916,
      lng: -89.785942
    },
    stateName: "Missouri"
  },
  {
    id: "165",
    state: "29",
    name: "Platte County",
    position: {
      lat: 39.378696,
      lng: -94.761472
    },
    stateName: "Missouri"
  },
  {
    id: "101",
    state: "37",
    name: "Johnston County",
    position: {
      lat: 35.513405,
      lng: -78.367267
    },
    stateName: "North Carolina"
  },
  {
    id: "107",
    state: "37",
    name: "Lenoir County",
    position: {
      lat: 35.238062,
      lng: -77.639023
    },
    stateName: "North Carolina"
  },
  {
    id: "113",
    state: "37",
    name: "Macon County",
    position: {
      lat: 35.152959,
      lng: -83.421901
    },
    stateName: "North Carolina"
  },
  {
    id: "119",
    state: "37",
    name: "Mecklenburg County",
    position: {
      lat: 35.246862,
      lng: -80.833832
    },
    stateName: "North Carolina"
  },
  {
    id: "125",
    state: "37",
    name: "Moore County",
    position: {
      lat: 35.310163,
      lng: -79.480664
    },
    stateName: "North Carolina"
  },
  {
    id: "001",
    state: "40",
    name: "Adair County",
    position: {
      lat: 35.8975,
      lng: -94.651671
    },
    stateName: "Oklahoma"
  },
  {
    id: "007",
    state: "40",
    name: "Beaver County",
    position: {
      lat: 36.748334,
      lng: -100.483056
    },
    stateName: "Oklahoma"
  },
  {
    id: "021",
    state: "40",
    name: "Cherokee County",
    position: {
      lat: 35.904367,
      lng: -94.996796
    },
    stateName: "Oklahoma"
  },
  {
    id: "023",
    state: "40",
    name: "Choctaw County",
    position: {
      lat: 34.027645,
      lng: -95.554208
    },
    stateName: "Oklahoma"
  },
  {
    id: "033",
    state: "40",
    name: "Cotton County",
    position: {
      lat: 34.290676,
      lng: -98.373438
    },
    stateName: "Oklahoma"
  },
  {
    id: "041",
    state: "40",
    name: "Delaware County",
    position: {
      lat: 36.393369,
      lng: -94.808206
    },
    stateName: "Oklahoma"
  },
  {
    id: "051",
    state: "40",
    name: "Grady County",
    position: {
      lat: 35.021058,
      lng: -97.88689
    },
    stateName: "Oklahoma"
  },
  {
    id: "189",
    state: "37",
    name: "Watauga County",
    position: {
      lat: 36.235371,
      lng: -81.709919
    },
    stateName: "North Carolina"
  },
  {
    id: "051",
    state: "48",
    name: "Burleson County",
    position: {
      lat: 30.493487,
      lng: -96.622091
    },
    stateName: "Texas"
  },
  {
    id: "023",
    state: "35",
    name: "Hidalgo County",
    position: {
      lat: 31.899658,
      lng: -108.745729
    },
    stateName: "New Mexico"
  },
  {
    id: "029",
    state: "35",
    name: "Luna County",
    position: {
      lat: 32.184482,
      lng: -107.746639
    },
    stateName: "New Mexico"
  },
  {
    id: "031",
    state: "35",
    name: "Mckinley County",
    position: {
      lat: 35.573732,
      lng: -108.255307
    },
    stateName: "New Mexico"
  },
  {
    id: "035",
    state: "35",
    name: "Otero County",
    position: {
      lat: 32.588776,
      lng: -105.781079
    },
    stateName: "New Mexico"
  },
  {
    id: "153",
    state: "47",
    name: "Sequatchie County",
    position: {
      lat: 35.372335,
      lng: -85.410344
    },
    stateName: "Tennessee"
  },
  {
    id: "157",
    state: "47",
    name: "Shelby County",
    position: {
      lat: 35.183794,
      lng: -89.895397
    },
    stateName: "Tennessee"
  },
  {
    id: "163",
    state: "47",
    name: "Sullivan County",
    position: {
      lat: 36.510212,
      lng: -82.299397
    },
    stateName: "Tennessee"
  },
  {
    id: "171",
    state: "47",
    name: "Unicoi County",
    position: {
      lat: 36.100215,
      lng: -82.418245
    },
    stateName: "Tennessee"
  },
  {
    id: "181",
    state: "47",
    name: "Wayne County",
    position: {
      lat: 35.242687,
      lng: -87.819703
    },
    stateName: "Tennessee"
  },
  {
    id: "057",
    state: "45",
    name: "Lancaster County",
    position: {
      lat: 34.686818,
      lng: -80.703689
    },
    stateName: "South Carolina"
  },
  {
    id: "085",
    state: "36",
    name: "Richmond County",
    position: {
      lat: 40.563855,
      lng: -74.137063
    },
    stateName: "New York"
  },
  {
    id: "097",
    state: "36",
    name: "Schuyler County",
    position: {
      lat: 42.419776,
      lng: -76.938604
    },
    stateName: "New York"
  },
  {
    id: "105",
    state: "36",
    name: "Sullivan County",
    position: {
      lat: 41.720176,
      lng: -74.76468
    },
    stateName: "New York"
  },
  {
    id: "111",
    state: "36",
    name: "Ulster County",
    position: {
      lat: 41.947232,
      lng: -74.265447
    },
    stateName: "New York"
  },
  {
    id: "115",
    state: "36",
    name: "Washington County",
    position: {
      lat: 43.312377,
      lng: -73.439428
    },
    stateName: "New York"
  },
  {
    id: "119",
    state: "36",
    name: "Westchester County",
    position: {
      lat: 41.15277,
      lng: -73.745912
    },
    stateName: "New York"
  },
  {
    id: "091",
    state: "45",
    name: "York County",
    position: {
      lat: 34.97019,
      lng: -81.183189
    },
    stateName: "South Carolina"
  },
  {
    id: "009",
    state: "46",
    name: "Bon Homme County",
    position: {
      lat: 42.986031,
      lng: -97.885613
    },
    stateName: "South Dakota"
  },
  {
    id: "013",
    state: "46",
    name: "Brown County",
    position: {
      lat: 45.589254,
      lng: -98.352175
    },
    stateName: "South Dakota"
  },
  {
    id: "021",
    state: "46",
    name: "Campbell County",
    position: {
      lat: 45.782241,
      lng: -100.027951
    },
    stateName: "South Dakota"
  },
  {
    id: "209",
    state: "29",
    name: "Stone County",
    position: {
      lat: 36.747857,
      lng: -93.467782
    },
    stateName: "Missouri"
  },
  {
    id: "217",
    state: "29",
    name: "Vernon County",
    position: {
      lat: 37.850196,
      lng: -94.341597
    },
    stateName: "Missouri"
  },
  {
    id: "227",
    state: "29",
    name: "Worth County",
    position: {
      lat: 40.480482,
      lng: -94.419129
    },
    stateName: "Missouri"
  },
  {
    id: "001",
    state: "30",
    name: "Beaverhead County",
    position: {
      lat: 45.133863,
      lng: -112.892869
    },
    stateName: "Montana"
  },
  {
    id: "171",
    state: "37",
    name: "Surry County",
    position: {
      lat: 36.415416,
      lng: -80.686463
    },
    stateName: "North Carolina"
  },
  {
    id: "177",
    state: "37",
    name: "Tyrrell County",
    position: {
      lat: 35.87042,
      lng: -76.165345
    },
    stateName: "North Carolina"
  },
  {
    id: "067",
    state: "39",
    name: "Harrison County",
    position: {
      lat: 40.292318,
      lng: -81.091565
    },
    stateName: "Ohio"
  },
  {
    id: "073",
    state: "39",
    name: "Hocking County",
    position: {
      lat: 39.490343,
      lng: -82.483445
    },
    stateName: "Ohio"
  },
  {
    id: "079",
    state: "39",
    name: "Jackson County",
    position: {
      lat: 39.013477,
      lng: -82.614142
    },
    stateName: "Ohio"
  },
  {
    id: "057",
    state: "48",
    name: "Calhoun County",
    position: {
      lat: 28.445366,
      lng: -96.5833
    },
    stateName: "Texas"
  },
  {
    id: "071",
    state: "48",
    name: "Chambers County",
    position: {
      lat: 29.703933,
      lng: -94.668875
    },
    stateName: "Texas"
  },
  {
    id: "093",
    state: "48",
    name: "Comanche County",
    position: {
      lat: 31.951645,
      lng: -98.549617
    },
    stateName: "Texas"
  },
  {
    id: "097",
    state: "48",
    name: "Cooke County",
    position: {
      lat: 33.639169,
      lng: -97.210372
    },
    stateName: "Texas"
  },
  {
    id: "510",
    state: "24",
    name: "Baltimore City County",
    postal: "city",
    position: {
      lat: 39.443167,
      lng: -76.616569
    },
    stateName: "Maryland"
  },
  {
    id: "063",
    state: "45",
    name: "Lexington County",
    position: {
      lat: 33.892554,
      lng: -81.272853
    },
    stateName: "South Carolina"
  },
  {
    id: "067",
    state: "45",
    name: "Marion County",
    position: {
      lat: 34.080701,
      lng: -79.362131
    },
    stateName: "South Carolina"
  },
  {
    id: "077",
    state: "45",
    name: "Pickens County",
    position: {
      lat: 34.887362,
      lng: -82.725368
    },
    stateName: "South Carolina"
  },
  {
    id: "053",
    state: "26",
    name: "Gogebic County",
    position: {
      lat: 46.488054,
      lng: -89.788314
    },
    stateName: "Michigan"
  },
  {
    id: "027",
    state: "46",
    name: "Clay County",
    position: {
      lat: 42.912997,
      lng: -96.979795
    },
    stateName: "South Dakota"
  },
  {
    id: "031",
    state: "46",
    name: "Corson County",
    position: {
      lat: 45.69834,
      lng: -101.176017
    },
    stateName: "South Dakota"
  },
  {
    id: "047",
    state: "46",
    name: "Fall River County",
    position: {
      lat: 43.221504,
      lng: -103.512102
    },
    stateName: "South Dakota"
  },
  {
    id: "053",
    state: "46",
    name: "Gregory County",
    position: {
      lat: 43.179094,
      lng: -99.202158
    },
    stateName: "South Dakota"
  },
  {
    id: "007",
    state: "27",
    name: "Beltrami County",
    position: {
      lat: 47.878825,
      lng: -94.986698
    },
    stateName: "Minnesota"
  },
  {
    id: "015",
    state: "27",
    name: "Brown County",
    position: {
      lat: 44.246542,
      lng: -94.733647
    },
    stateName: "Minnesota"
  },
  {
    id: "021",
    state: "27",
    name: "Cass County",
    position: {
      lat: 46.951427,
      lng: -94.333773
    },
    stateName: "Minnesota"
  },
  {
    id: "027",
    state: "27",
    name: "Clay County",
    position: {
      lat: 46.898377,
      lng: -96.494901
    },
    stateName: "Minnesota"
  },
  {
    id: "123",
    state: "46",
    name: "Tripp County",
    position: {
      lat: 43.349729,
      lng: -99.876219
    },
    stateName: "South Dakota"
  },
  {
    id: "135",
    state: "46",
    name: "Yankton County",
    position: {
      lat: 43.006607,
      lng: -97.388339
    },
    stateName: "South Dakota"
  },
  {
    id: "011",
    state: "30",
    name: "Carter County",
    position: {
      lat: 45.516825,
      lng: -104.515324
    },
    stateName: "Montana"
  },
  {
    id: "017",
    state: "30",
    name: "Custer County",
    position: {
      lat: 46.261481,
      lng: -105.550508
    },
    stateName: "Montana"
  },
  {
    id: "025",
    state: "30",
    name: "Fallon County",
    position: {
      lat: 46.318184,
      lng: -104.405718
    },
    stateName: "Montana"
  },
  {
    id: "027",
    state: "30",
    name: "Fergus County",
    position: {
      lat: 47.222997,
      lng: -109.22328
    },
    stateName: "Montana"
  },
  {
    id: "033",
    state: "30",
    name: "Garfield County",
    position: {
      lat: 47.281174,
      lng: -106.982212
    },
    stateName: "Montana"
  },
  {
    id: "039",
    state: "30",
    name: "Granite County",
    position: {
      lat: 46.395358,
      lng: -113.425683
    },
    stateName: "Montana"
  },
  {
    id: "045",
    state: "30",
    name: "Judith Basin County",
    position: {
      lat: 47.032558,
      lng: -110.30532
    },
    stateName: "Montana"
  },
  {
    id: "131",
    state: "31",
    name: "Otoe County",
    position: {
      lat: 40.637992,
      lng: -96.131027
    },
    stateName: "Nebraska"
  },
  {
    id: "005",
    state: "25",
    name: "Bristol County",
    position: {
      lat: 41.748576,
      lng: -71.087062
    },
    stateName: "Massachusetts"
  },
  {
    id: "009",
    state: "25",
    name: "Essex County",
    position: {
      lat: 42.642711,
      lng: -70.865107
    },
    stateName: "Massachusetts"
  },
  {
    id: "019",
    state: "25",
    name: "Nantucket County",
    position: {
      lat: 41.305878,
      lng: -70.14191
    },
    stateName: "Massachusetts"
  },
  {
    id: "023",
    state: "34",
    name: "Middlesex County",
    position: {
      lat: 40.439593,
      lng: -74.407585
    },
    stateName: "New Jersey"
  },
  {
    id: "055",
    state: "26",
    name: "Grand Traverse County",
    position: {
      lat: 44.718688,
      lng: -85.553848
    },
    stateName: "Michigan"
  },
  {
    id: "091",
    state: "31",
    name: "Hooker County",
    position: {
      lat: 41.9187,
      lng: -101.11681
    },
    stateName: "Nebraska"
  },
  {
    id: "095",
    state: "31",
    name: "Jefferson County",
    position: {
      lat: 40.175736,
      lng: -97.143103
    },
    stateName: "Nebraska"
  },
  {
    id: "103",
    state: "31",
    name: "Keya Paha County",
    position: {
      lat: 42.87548,
      lng: -99.718351
    },
    stateName: "Nebraska"
  },
  {
    id: "111",
    state: "31",
    name: "Lincoln County",
    position: {
      lat: 41.050322,
      lng: -100.744481
    },
    stateName: "Nebraska"
  },
  {
    id: "089",
    state: "28",
    name: "Madison County",
    position: {
      lat: 32.63437,
      lng: -90.03416
    },
    stateName: "Mississippi"
  },
  {
    id: "093",
    state: "28",
    name: "Marshall County",
    position: {
      lat: 34.76619,
      lng: -89.504231
    },
    stateName: "Mississippi"
  },
  {
    id: "031",
    state: "27",
    name: "Cook County",
    position: {
      lat: 47.538571,
      lng: -90.29019
    },
    stateName: "Minnesota"
  },
  {
    id: "049",
    state: "27",
    name: "Goodhue County",
    position: {
      lat: 44.406178,
      lng: -92.716
    },
    stateName: "Minnesota"
  },
  {
    id: "055",
    state: "27",
    name: "Houston County",
    position: {
      lat: 43.66699,
      lng: -91.501556
    },
    stateName: "Minnesota"
  },
  {
    id: "049",
    state: "37",
    name: "Craven County",
    position: {
      lat: 35.118179,
      lng: -77.082541
    },
    stateName: "North Carolina"
  },
  {
    id: "051",
    state: "37",
    name: "Cumberland County",
    position: {
      lat: 35.050192,
      lng: -78.828719
    },
    stateName: "North Carolina"
  },
  {
    id: "131",
    state: "37",
    name: "Northampton County",
    position: {
      lat: 36.421774,
      lng: -77.398352
    },
    stateName: "North Carolina"
  },
  {
    id: "137",
    state: "37",
    name: "Pamlico County",
    position: {
      lat: 35.147462,
      lng: -76.665069
    },
    stateName: "North Carolina"
  },
  {
    id: "149",
    state: "37",
    name: "Polk County",
    position: {
      lat: 35.278928,
      lng: -82.167667
    },
    stateName: "North Carolina"
  },
  {
    id: "155",
    state: "37",
    name: "Robeson County",
    position: {
      lat: 34.63921,
      lng: -79.100881
    },
    stateName: "North Carolina"
  },
  {
    id: "091",
    state: "42",
    name: "Montgomery County",
    position: {
      lat: 40.209999,
      lng: -75.370201
    },
    stateName: "Pennsylvania"
  },
  {
    id: "097",
    state: "42",
    name: "Northumberlnd County",
    position: {
      lat: 40.851524,
      lng: -76.709877
    },
    stateName: "Pennsylvania"
  },
  {
    id: "103",
    state: "42",
    name: "Pike County",
    position: {
      lat: 41.325949,
      lng: -75.031514
    },
    stateName: "Pennsylvania"
  },
  {
    id: "025",
    state: "34",
    name: "Monmouth County",
    position: {
      lat: 40.287056,
      lng: -74.152446
    },
    stateName: "New Jersey"
  },
  {
    id: "039",
    state: "34",
    name: "Union County",
    position: {
      lat: 40.659871,
      lng: -74.308696
    },
    stateName: "New Jersey"
  },
  {
    id: "003",
    state: "35",
    name: "Catron County",
    position: {
      lat: 33.901814,
      lng: -108.392097
    },
    stateName: "New Mexico"
  },
  {
    id: "005",
    state: "35",
    name: "Chaves County",
    position: {
      lat: 33.361605,
      lng: -104.469837
    },
    stateName: "New Mexico"
  },
  {
    id: "009",
    state: "35",
    name: "Curry County",
    position: {
      lat: 34.572984,
      lng: -103.346055
    },
    stateName: "New Mexico"
  },
  {
    id: "061",
    state: "30",
    name: "Mineral County",
    position: {
      lat: 47.155213,
      lng: -115.06455
    },
    stateName: "Montana"
  },
  {
    id: "001",
    state: "38",
    name: "Adams County",
    position: {
      lat: 46.096815,
      lng: -102.533198
    },
    stateName: "North Dakota"
  },
  {
    id: "011",
    state: "09",
    name: "New London County",
    position: {
      lat: 41.47863,
      lng: -72.103452
    },
    stateName: "Connecticut"
  },
  {
    id: "001",
    state: "10",
    name: "Kent County",
    position: {
      lat: 39.097088,
      lng: -75.502982
    },
    stateName: "Delaware"
  },
  {
    id: "003",
    state: "10",
    name: "New Castle County",
    position: {
      lat: 39.575915,
      lng: -75.644132
    },
    stateName: "Delaware"
  },
  {
    id: "009",
    state: "12",
    name: "Brevard County",
    position: {
      lat: 28.298276,
      lng: -80.700384
    },
    stateName: "Florida"
  },
  {
    id: "075",
    state: "12",
    name: "Levy County",
    position: {
      lat: 29.284409,
      lng: -82.783483
    },
    stateName: "Florida"
  },
  {
    id: "031",
    state: "05",
    name: "Craighead County",
    position: {
      lat: 35.828268,
      lng: -90.630411
    },
    stateName: "Arkansas"
  },
  {
    id: "035",
    state: "05",
    name: "Crittenden County",
    position: {
      lat: 35.211878,
      lng: -90.315331
    },
    stateName: "Arkansas"
  },
  {
    id: "041",
    state: "05",
    name: "Desha County",
    position: {
      lat: 33.828748,
      lng: -91.244427
    },
    stateName: "Arkansas"
  },
  {
    id: "057",
    state: "05",
    name: "Hempstead County",
    position: {
      lat: 33.728611,
      lng: -93.665809
    },
    stateName: "Arkansas"
  },
  {
    id: "063",
    state: "05",
    name: "Independence County",
    position: {
      lat: 35.737499,
      lng: -91.559942
    },
    stateName: "Arkansas"
  },
  {
    id: "069",
    state: "05",
    name: "Jefferson County",
    position: {
      lat: 34.277696,
      lng: -91.930701
    },
    stateName: "Arkansas"
  },
  {
    id: "075",
    state: "05",
    name: "Lawrence County",
    position: {
      lat: 36.041098,
      lng: -91.101153
    },
    stateName: "Arkansas"
  },
  {
    id: "077",
    state: "05",
    name: "Lee County",
    position: {
      lat: 34.77975,
      lng: -90.779288
    },
    stateName: "Arkansas"
  },
  {
    id: "025",
    state: "04",
    name: "Yavapai County",
    position: {
      lat: 34.630044,
      lng: -112.573745
    },
    stateName: "Arizona"
  },
  {
    id: "005",
    state: "05",
    name: "Baxter County",
    position: {
      lat: 36.28071,
      lng: -92.330438
    },
    stateName: "Arkansas"
  },
  {
    id: "007",
    state: "05",
    name: "Benton County",
    position: {
      lat: 36.337825,
      lng: -94.256187
    },
    stateName: "Arkansas"
  },
  {
    id: "073",
    state: "16",
    name: "Owyhee County",
    position: {
      lat: 42.593063,
      lng: -116.142655
    },
    stateName: "Idaho"
  },
  {
    id: "085",
    state: "16",
    name: "Valley County",
    position: {
      lat: 44.771532,
      lng: -115.637875
    },
    stateName: "Idaho"
  },
  {
    id: "087",
    state: "16",
    name: "Washington County",
    position: {
      lat: 44.448213,
      lng: -116.79783
    },
    stateName: "Idaho"
  },
  {
    id: "013",
    state: "17",
    name: "Calhoun County",
    position: {
      lat: 39.164262,
      lng: -90.666295
    },
    stateName: "Illinois"
  },
  {
    id: "021",
    state: "17",
    name: "Christian County",
    position: {
      lat: 39.545524,
      lng: -89.279593
    },
    stateName: "Illinois"
  },
  {
    id: "027",
    state: "17",
    name: "Clinton County",
    position: {
      lat: 38.606423,
      lng: -89.423136
    },
    stateName: "Illinois"
  },
  {
    id: "031",
    state: "16",
    name: "Cassia County",
    position: {
      lat: 42.288635,
      lng: -113.605498
    },
    stateName: "Idaho"
  },
  {
    id: "035",
    state: "16",
    name: "Clearwater County",
    position: {
      lat: 46.67257,
      lng: -115.6535
    },
    stateName: "Idaho"
  },
  {
    id: "037",
    state: "16",
    name: "Custer County",
    position: {
      lat: 44.273341,
      lng: -114.252251
    },
    stateName: "Idaho"
  },
  {
    id: "049",
    state: "16",
    name: "Idaho County",
    position: {
      lat: 45.849237,
      lng: -115.467376
    },
    stateName: "Idaho"
  },
  {
    id: "059",
    state: "16",
    name: "Lemhi County",
    position: {
      lat: 44.928789,
      lng: -113.887841
    },
    stateName: "Idaho"
  },
  {
    id: "023",
    state: "18",
    name: "Clinton County",
    position: {
      lat: 40.305944,
      lng: -86.477567
    },
    stateName: "Indiana"
  },
  {
    id: "021",
    state: "12",
    name: "Collier County",
    position: {
      lat: 26.118713,
      lng: -81.400884
    },
    stateName: "Florida"
  },
  {
    id: "033",
    state: "12",
    name: "Escambia County",
    position: {
      lat: 30.611664,
      lng: -87.33904
    },
    stateName: "Florida"
  },
  {
    id: "007",
    state: "16",
    name: "Bear Lake County",
    position: {
      lat: 42.285833,
      lng: -111.327445
    },
    stateName: "Idaho"
  },
  {
    id: "011",
    state: "16",
    name: "Bingham County",
    position: {
      lat: 43.216357,
      lng: -112.399206
    },
    stateName: "Idaho"
  },
  {
    id: "013",
    state: "16",
    name: "Blaine County",
    position: {
      lat: 43.394482,
      lng: -113.95529
    },
    stateName: "Idaho"
  },
  {
    id: "105",
    state: "05",
    name: "Perry County",
    position: {
      lat: 34.946363,
      lng: -92.926877
    },
    stateName: "Arkansas"
  },
  {
    id: "107",
    state: "05",
    name: "Phillips County",
    position: {
      lat: 34.425842,
      lng: -90.848386
    },
    stateName: "Arkansas"
  },
  {
    id: "119",
    state: "05",
    name: "Pulaski County",
    position: {
      lat: 34.773988,
      lng: -92.316515
    },
    stateName: "Arkansas"
  },
  {
    id: "031",
    state: "17",
    name: "Cook County",
    position: {
      lat: 41.894294,
      lng: -87.645455
    },
    stateName: "Illinois"
  },
  {
    id: "123",
    state: "18",
    name: "Perry County",
    position: {
      lat: 38.081436,
      lng: -86.62654
    },
    stateName: "Indiana"
  },
  {
    id: "129",
    state: "18",
    name: "Posey County",
    position: {
      lat: 38.027614,
      lng: -87.868653
    },
    stateName: "Indiana"
  },
  {
    id: "143",
    state: "18",
    name: "Scott County",
    position: {
      lat: 38.679431,
      lng: -85.751898
    },
    stateName: "Indiana"
  },
  {
    id: "147",
    state: "18",
    name: "Spencer County",
    position: {
      lat: 38.009789,
      lng: -87.010645
    },
    stateName: "Indiana"
  },
  {
    id: "155",
    state: "18",
    name: "Switzerland County",
    position: {
      lat: 38.825846,
      lng: -85.029679
    },
    stateName: "Indiana"
  },
  {
    id: "163",
    state: "18",
    name: "Vanderburgh County",
    position: {
      lat: 38.02007,
      lng: -87.586166
    },
    stateName: "Indiana"
  },
  {
    id: "029",
    state: "18",
    name: "Dearborn County",
    position: {
      lat: 39.151491,
      lng: -84.97346
    },
    stateName: "Indiana"
  },
  {
    id: "037",
    state: "18",
    name: "Dubois County",
    position: {
      lat: 38.373344,
      lng: -86.873385
    },
    stateName: "Indiana"
  },
  {
    id: "039",
    state: "18",
    name: "Elkhart County",
    position: {
      lat: 41.600693,
      lng: -85.863986
    },
    stateName: "Indiana"
  },
  {
    id: "135",
    state: "13",
    name: "Gwinnett County",
    position: {
      lat: 33.959101,
      lng: -84.022938
    },
    stateName: "Georgia"
  },
  {
    id: "145",
    state: "13",
    name: "Harris County",
    position: {
      lat: 32.731549,
      lng: -84.912432
    },
    stateName: "Georgia"
  },
  {
    id: "151",
    state: "13",
    name: "Henry County",
    position: {
      lat: 33.452881,
      lng: -84.15444
    },
    stateName: "Georgia"
  },
  {
    id: "161",
    state: "13",
    name: "Jeff Davis County",
    position: {
      lat: 31.811615,
      lng: -82.636825
    },
    stateName: "Georgia"
  },
  {
    id: "167",
    state: "13",
    name: "Johnson County",
    position: {
      lat: 32.689831,
      lng: -82.661354
    },
    stateName: "Georgia"
  },
  {
    id: "177",
    state: "13",
    name: "Lee County",
    position: {
      lat: 31.818419,
      lng: -84.146681
    },
    stateName: "Georgia"
  },
  {
    id: "179",
    state: "13",
    name: "Liberty County",
    position: {
      lat: 31.807245,
      lng: -81.457969
    },
    stateName: "Georgia"
  },
  {
    id: "107",
    state: "20",
    name: "Linn County",
    position: {
      lat: 38.216549,
      lng: -94.844932
    },
    stateName: "Kansas"
  },
  {
    id: "117",
    state: "20",
    name: "Marshall County",
    position: {
      lat: 39.782709,
      lng: -96.521243
    },
    stateName: "Kansas"
  },
  {
    id: "149",
    state: "17",
    name: "Pike County",
    position: {
      lat: 39.625106,
      lng: -90.889034
    },
    stateName: "Illinois"
  },
  {
    id: "157",
    state: "17",
    name: "Randolph County",
    position: {
      lat: 38.056515,
      lng: -89.82121
    },
    stateName: "Illinois"
  },
  {
    id: "161",
    state: "17",
    name: "Rock Island County",
    position: {
      lat: 41.468404,
      lng: -90.572203
    },
    stateName: "Illinois"
  },
  {
    id: "171",
    state: "17",
    name: "Scott County",
    position: {
      lat: 39.63698,
      lng: -90.477759
    },
    stateName: "Illinois"
  },
  {
    id: "173",
    state: "17",
    name: "Shelby County",
    position: {
      lat: 39.384926,
      lng: -88.798862
    },
    stateName: "Illinois"
  },
  {
    id: "181",
    state: "17",
    name: "Union County",
    position: {
      lat: 37.475104,
      lng: -89.252875
    },
    stateName: "Illinois"
  },
  {
    id: "185",
    state: "17",
    name: "Wabash County",
    position: {
      lat: 38.445821,
      lng: -87.839167
    },
    stateName: "Illinois"
  },
  {
    id: "193",
    state: "17",
    name: "White County",
    position: {
      lat: 38.087372,
      lng: -88.178585
    },
    stateName: "Illinois"
  },
  {
    id: "201",
    state: "17",
    name: "Winnebago County",
    position: {
      lat: 42.337396,
      lng: -89.161205
    },
    stateName: "Illinois"
  },
  {
    id: "287",
    state: "13",
    name: "Turner County",
    position: {
      lat: 31.718232,
      lng: -83.628567
    },
    stateName: "Georgia"
  },
  {
    id: "291",
    state: "13",
    name: "Union County",
    position: {
      lat: 34.83377,
      lng: -83.989573
    },
    stateName: "Georgia"
  },
  {
    id: "299",
    state: "13",
    name: "Ware County",
    position: {
      lat: 31.050881,
      lng: -82.421507
    },
    stateName: "Georgia"
  },
  {
    id: "105",
    state: "21",
    name: "Hickman County",
    position: {
      lat: 36.675916,
      lng: -88.97202
    },
    stateName: "Kentucky"
  },
  {
    id: "273",
    state: "48",
    name: "Kleberg County",
    position: {
      lat: 27.438735,
      lng: -97.66062
    },
    stateName: "Texas"
  },
  {
    id: "311",
    state: "48",
    name: "Mc Mullen County",
    position: {
      lat: 28.384922,
      lng: -98.578853
    },
    stateName: "Texas"
  },
  {
    id: "315",
    state: "48",
    name: "Marion County",
    position: {
      lat: 32.797757,
      lng: -94.357673
    },
    stateName: "Texas"
  },
  {
    id: "321",
    state: "48",
    name: "Matagorda County",
    position: {
      lat: 28.783341,
      lng: -95.997755
    },
    stateName: "Texas"
  },
  {
    id: "337",
    state: "48",
    name: "Montague County",
    position: {
      lat: 33.676289,
      lng: -97.724747
    },
    stateName: "Texas"
  },
  {
    id: "047",
    state: "54",
    name: "Mc Dowell County",
    position: {
      lat: 37.382663,
      lng: -81.658205
    },
    stateName: "West Virginia"
  },
  {
    id: "057",
    state: "54",
    name: "Mineral County",
    position: {
      lat: 39.405626,
      lng: -78.956581
    },
    stateName: "West Virginia"
  },
  {
    id: "063",
    state: "54",
    name: "Monroe County",
    position: {
      lat: 37.550353,
      lng: -80.547891
    },
    stateName: "West Virginia"
  },
  {
    id: "071",
    state: "54",
    name: "Pendleton County",
    position: {
      lat: 38.686836,
      lng: -79.333707
    },
    stateName: "West Virginia"
  },
  {
    id: "077",
    state: "54",
    name: "Preston County",
    position: {
      lat: 39.46903,
      lng: -79.668865
    },
    stateName: "West Virginia"
  },
  {
    id: "059",
    state: "51",
    name: "Fairfax County",
    position: {
      lat: 38.833743,
      lng: -77.276117
    },
    stateName: "Virginia"
  },
  {
    id: "071",
    state: "51",
    name: "Giles County",
    position: {
      lat: 37.31193,
      lng: -80.717178
    },
    stateName: "Virginia"
  },
  {
    id: "077",
    state: "51",
    name: "Grayson County",
    position: {
      lat: 36.656303,
      lng: -81.225337
    },
    stateName: "Virginia"
  },
  {
    id: "083",
    state: "51",
    name: "Halifax County",
    position: {
      lat: 36.766461,
      lng: -78.939614
    },
    stateName: "Virginia"
  },
  {
    id: "089",
    state: "51",
    name: "Henry County",
    position: {
      lat: 36.620593,
      lng: -79.980584
    },
    stateName: "Virginia"
  },
  {
    id: "093",
    state: "51",
    name: "Isle Of Wight County",
    position: {
      lat: 36.901418,
      lng: -76.707569
    },
    stateName: "Virginia"
  },
  {
    id: "099",
    state: "51",
    name: "King George County",
    position: {
      lat: 38.277179,
      lng: -77.162702
    },
    stateName: "Virginia"
  },
  {
    id: "105",
    state: "51",
    name: "Lee County",
    position: {
      lat: 36.702162,
      lng: -83.130334
    },
    stateName: "Virginia"
  },
  {
    id: "033",
    state: "21",
    name: "Caldwell County",
    position: {
      lat: 37.148643,
      lng: -87.87051
    },
    stateName: "Kentucky"
  },
  {
    id: "305",
    state: "13",
    name: "Wayne County",
    position: {
      lat: 31.547845,
      lng: -81.912376
    },
    stateName: "Georgia"
  },
  {
    id: "117",
    state: "51",
    name: "Mecklenburg County",
    position: {
      lat: 36.687256,
      lng: -78.368959
    },
    stateName: "Virginia"
  },
  {
    id: "007",
    state: "53",
    name: "Chelan County",
    position: {
      lat: 47.859892,
      lng: -120.618543
    },
    stateName: "Washington"
  },
  {
    id: "117",
    state: "48",
    name: "Deaf Smith County",
    position: {
      lat: 34.940766,
      lng: -102.607564
    },
    stateName: "Texas"
  },
  {
    id: "261",
    state: "48",
    name: "Kenedy County",
    position: {
      lat: 26.890232,
      lng: -97.591233
    },
    stateName: "Texas"
  },
  {
    id: "135",
    state: "48",
    name: "Ector County",
    position: {
      lat: 31.865301,
      lng: -102.542507
    },
    stateName: "Texas"
  },
  {
    id: "141",
    state: "48",
    name: "El Paso County",
    position: {
      lat: 31.766403,
      lng: -106.241391
    },
    stateName: "Texas"
  },
  {
    id: "155",
    state: "48",
    name: "Foard County",
    position: {
      lat: 33.980404,
      lng: -99.777427
    },
    stateName: "Texas"
  },
  {
    id: "161",
    state: "48",
    name: "Freestone County",
    position: {
      lat: 31.701654,
      lng: -96.144237
    },
    stateName: "Texas"
  },
  {
    id: "165",
    state: "48",
    name: "Gaines County",
    position: {
      lat: 32.743942,
      lng: -102.631562
    },
    stateName: "Texas"
  },
  {
    id: "107",
    state: "51",
    name: "Loudoun County",
    position: {
      lat: 39.08113,
      lng: -77.638857
    },
    stateName: "Virginia"
  },
  {
    id: "131",
    state: "51",
    name: "Northampton County",
    position: {
      lat: 37.302629,
      lng: -75.923868
    },
    stateName: "Virginia"
  },
  {
    id: "147",
    state: "51",
    name: "Prince Edward County",
    position: {
      lat: 37.224881,
      lng: -78.432957
    },
    stateName: "Virginia"
  },
  {
    id: "149",
    state: "51",
    name: "Prince George County",
    position: {
      lat: 37.187326,
      lng: -77.220993
    },
    stateName: "Virginia"
  },
  {
    id: "159",
    state: "51",
    name: "Richmond County",
    position: {
      lat: 37.942894,
      lng: -76.730561
    },
    stateName: "Virginia"
  },
  {
    id: "161",
    state: "51",
    name: "Roanoke County",
    position: {
      lat: 37.331077,
      lng: -80.19011
    },
    stateName: "Virginia"
  },
  {
    id: "165",
    state: "51",
    name: "Rockingham County",
    position: {
      lat: 38.511257,
      lng: -78.876307
    },
    stateName: "Virginia"
  },
  {
    id: "175",
    state: "51",
    name: "Southampton County",
    position: {
      lat: 36.720173,
      lng: -77.103856
    },
    stateName: "Virginia"
  },
  {
    id: "177",
    state: "51",
    name: "Spotsylvania County",
    position: {
      lat: 38.182311,
      lng: -77.65628
    },
    stateName: "Virginia"
  },
  {
    id: "061",
    state: "55",
    name: "Kewaunee County",
    position: {
      lat: 44.500949,
      lng: -87.161813
    },
    stateName: "Wisconsin"
  },
  {
    id: "039",
    state: "56",
    name: "Teton County",
    position: {
      lat: 44.049321,
      lng: -110.588102
    },
    stateName: "Wyoming"
  },
  {
    id: "047",
    state: "53",
    name: "Okanogan County",
    position: {
      lat: 48.550971,
      lng: -119.691035
    },
    stateName: "Washington"
  },
  {
    id: "129",
    state: "48",
    name: "Donley County",
    position: {
      lat: 34.955036,
      lng: -100.815846
    },
    stateName: "Texas"
  },
  {
    id: "167",
    state: "48",
    name: "Galveston County",
    position: {
      lat: 29.228706,
      lng: -94.894865
    },
    stateName: "Texas"
  },
  {
    id: "221",
    state: "48",
    name: "Hood County",
    position: {
      lat: 32.430149,
      lng: -97.831677
    },
    stateName: "Texas"
  },
  {
    id: "225",
    state: "48",
    name: "Houston County",
    position: {
      lat: 31.323036,
      lng: -95.4216
    },
    stateName: "Texas"
  },
  {
    id: "229",
    state: "48",
    name: "Hudspeth County",
    position: {
      lat: 31.450868,
      lng: -105.377549
    },
    stateName: "Texas"
  },
  {
    id: "245",
    state: "48",
    name: "Jefferson County",
    position: {
      lat: 29.854,
      lng: -94.149331
    },
    stateName: "Texas"
  },
  {
    id: "257",
    state: "48",
    name: "Kaufman County",
    position: {
      lat: 32.598944,
      lng: -96.288378
    },
    stateName: "Texas"
  },
  {
    id: "193",
    state: "51",
    name: "Westmoreland County",
    position: {
      lat: 38.109191,
      lng: -76.80417
    },
    stateName: "Virginia"
  },
  {
    id: "199",
    state: "51",
    name: "York County",
    position: {
      lat: 37.220914,
      lng: -76.395533
    },
    stateName: "Virginia"
  },
  {
    id: "031",
    state: "53",
    name: "Jefferson County",
    position: {
      lat: 47.802641,
      lng: -123.52181
    },
    stateName: "Washington"
  },
  {
    id: "031",
    state: "55",
    name: "Douglas County",
    position: {
      lat: 46.463316,
      lng: -91.89258
    },
    stateName: "Wisconsin"
  },
  {
    id: "051",
    state: "55",
    name: "Iron County",
    position: {
      lat: 46.32655,
      lng: -90.261299
    },
    stateName: "Wisconsin"
  },
  {
    id: "049",
    state: "53",
    name: "Pacific County",
    position: {
      lat: 46.556587,
      lng: -123.782419
    },
    stateName: "Washington"
  },
  {
    id: "570",
    state: "51",
    name: "Colonial Heights City County",
    postal: "city",
    position: {
      lat: 37.261685,
      lng: -77.396804
    },
    stateName: "Virginia"
  },
  {
    id: "630",
    state: "51",
    name: "Fredericksburg City County",
    postal: "city",
    position: {
      lat: 38.299272,
      lng: -77.486658
    },
    stateName: "Virginia"
  },
  {
    id: "650",
    state: "51",
    name: "Hampton City County",
    postal: "city",
    position: {
      lat: 37.04803,
      lng: -76.297149
    },
    stateName: "Virginia"
  },
  {
    id: "700",
    state: "51",
    name: "Newport News City County",
    postal: "city",
    position: {
      lat: 37.075978,
      lng: -76.521719
    },
    stateName: "Virginia"
  },
  {
    id: "735",
    state: "51",
    name: "Poquoson County",
    postal: "city",
    position: {
      lat: 37.12836,
      lng: -76.303534
    },
    stateName: "Virginia"
  },
  {
    id: "770",
    state: "51",
    name: "Roanoke City County",
    postal: "city",
    position: {
      lat: 37.331077,
      lng: -80.19011
    },
    stateName: "Virginia"
  },
  {
    id: "071",
    state: "55",
    name: "Manitowoc County",
    position: {
      lat: 44.105108,
      lng: -87.313828
    },
    stateName: "Wisconsin"
  },
  {
    id: "079",
    state: "55",
    name: "Milwaukee County",
    position: {
      lat: 43.017655,
      lng: -87.481575
    },
    stateName: "Wisconsin"
  },
  {
    id: "089",
    state: "55",
    name: "Ozaukee County",
    position: {
      lat: 43.360715,
      lng: -87.496553
    },
    stateName: "Wisconsin"
  },
  {
    id: "055",
    state: "53",
    name: "San Juan County",
    position: {
      lat: 48.508862,
      lng: -123.100616
    },
    stateName: "Washington"
  },
  {
    id: "061",
    state: "53",
    name: "Snohomish County",
    position: {
      lat: 48.054913,
      lng: -121.766412
    },
    stateName: "Washington"
  },
  {
    id: "071",
    state: "53",
    name: "Walla Walla County",
    position: {
      lat: 46.254606,
      lng: -118.480374
    },
    stateName: "Washington"
  },
  {
    id: "073",
    state: "53",
    name: "Whatcom County",
    position: {
      lat: 48.842653,
      lng: -121.836433
    },
    stateName: "Washington"
  },
  {
    id: "039",
    state: "22",
    name: "Evangeline County",
    postal: "Parish",
    position: {
      lat: 30.720693,
      lng: -92.404086
    },
    stateName: "Louisiana"
  },
  {
    id: "139",
    state: "27",
    name: "Scott County",
    position: {
      lat: 44.651932,
      lng: -93.534553
    },
    stateName: "Minnesota"
  },
  {
    id: "113",
    state: "31",
    name: "Logan County",
    position: {
      lat: 41.542156,
      lng: -100.443665
    },
    stateName: "Nebraska"
  },
  {
    id: "043",
    state: "37",
    name: "Clay County",
    position: {
      lat: 35.052997,
      lng: -83.752264
    },
    stateName: "North Carolina"
  },
  {
    id: "057",
    state: "39",
    name: "Greene County",
    position: {
      lat: 39.687479,
      lng: -83.894894
    },
    stateName: "Ohio"
  },
  {
    id: "009",
    state: "36",
    name: "Cattaraugus County",
    position: {
      lat: 42.244853,
      lng: -78.681006
    },
    stateName: "New York"
  },
  {
    id: "169",
    state: "47",
    name: "Trousdale County",
    position: {
      lat: 36.39303,
      lng: -86.156691
    },
    stateName: "Tennessee"
  },
  {
    id: "073",
    state: "46",
    name: "Jerauld County",
    position: {
      lat: 44.063416,
      lng: -98.623188
    },
    stateName: "South Dakota"
  },
  {
    id: "365",
    state: "48",
    name: "Panola County",
    position: {
      lat: 32.163978,
      lng: -94.305156
    },
    stateName: "Texas"
  },
  {
    id: "207",
    state: "48",
    name: "Haskell County",
    position: {
      lat: 33.175965,
      lng: -99.730773
    },
    stateName: "Texas"
  },
  {
    id: "690",
    state: "51",
    name: "Martinsville County",
    postal: "city",
    position: {
      lat: 36.683527,
      lng: -79.863648
    },
    stateName: "Virginia"
  },
  {
    id: "131",
    state: "55",
    name: "Washington County",
    position: {
      lat: 43.391156,
      lng: -88.232917
    },
    stateName: "Wisconsin"
  },
  {
    id: "067",
    state: "54",
    name: "Nicholas County",
    position: {
      lat: 38.291416,
      lng: -80.797516
    },
    stateName: "West Virginia"
  },
  {
    id: "087",
    state: "51",
    name: "Henrico County",
    position: {
      lat: 37.437521,
      lng: -77.300333
    },
    stateName: "Virginia"
  },
  {
    id: "043",
    state: "01",
    name: "Cullman County",
    position: {
      lat: 34.131923,
      lng: -86.869267
    },
    stateName: "Alabama"
  },
  {
    id: "109",
    state: "01",
    name: "Pike County",
    position: {
      lat: 31.798726,
      lng: -85.941997
    },
    stateName: "Alabama"
  },
  {
    id: "009",
    state: "09",
    name: "New Haven County",
    position: {
      lat: 41.349717,
      lng: -72.900204
    },
    stateName: "Connecticut"
  },
  {
    id: "049",
    state: "05",
    name: "Fulton County",
    position: {
      lat: 36.383443,
      lng: -91.819239
    },
    stateName: "Arkansas"
  },
  {
    id: "127",
    state: "05",
    name: "Scott County",
    position: {
      lat: 34.858869,
      lng: -94.063641
    },
    stateName: "Arkansas"
  },
  {
    id: "117",
    state: "21",
    name: "Kenton County",
    position: {
      lat: 38.930477,
      lng: -84.533492
    },
    stateName: "Kentucky"
  },
  {
    id: "123",
    state: "19",
    name: "Mahaska County",
    position: {
      lat: 41.330797,
      lng: -92.636366
    },
    stateName: "Iowa"
  },
  {
    id: "015",
    state: "47",
    name: "Cannon County",
    position: {
      lat: 35.808394,
      lng: -86.062404
    },
    stateName: "Tennessee"
  },
  {
    id: "011",
    state: "38",
    name: "Bowman County",
    position: {
      lat: 46.107807,
      lng: -103.506586
    },
    stateName: "North Dakota"
  },
  {
    id: "083",
    state: "38",
    name: "Sheridan County",
    position: {
      lat: 47.581373,
      lng: -100.341499
    },
    stateName: "North Dakota"
  },
  {
    id: "375",
    state: "48",
    name: "Potter County",
    position: {
      lat: 35.398675,
      lng: -101.893804
    },
    stateName: "Texas"
  },
  {
    id: "065",
    state: "31",
    name: "Furnas County",
    position: {
      lat: 40.191991,
      lng: -99.909661
    },
    stateName: "Nebraska"
  },
  {
    id: "135",
    state: "28",
    name: "Tallahatchie County",
    position: {
      lat: 33.955891,
      lng: -90.172833
    },
    stateName: "Mississippi"
  },
  {
    id: "019",
    state: "41",
    name: "Douglas County",
    position: {
      lat: 43.285904,
      lng: -123.15438
    },
    stateName: "Oregon"
  },
  {
    id: "013",
    state: "29",
    name: "Bates County",
    position: {
      lat: 38.257217,
      lng: -94.339246
    },
    stateName: "Missouri"
  },
  {
    id: "001",
    state: "55",
    name: "Adams County",
    position: {
      lat: 43.973763,
      lng: -89.767223
    },
    stateName: "Wisconsin"
  },
  {
    id: "057",
    state: "53",
    name: "Skagit County",
    position: {
      lat: 48.493066,
      lng: -121.816278
    },
    stateName: "Washington"
  },
  {
    id: "085",
    state: "06",
    name: "Santa Clara County",
    position: {
      lat: 37.220777,
      lng: -121.690622
    },
    stateName: "California"
  },
  {
    id: "017",
    state: "13",
    name: "Ben Hill County",
    position: {
      lat: 31.740776,
      lng: -83.14719
    },
    stateName: "Georgia"
  },
  {
    id: "047",
    state: "18",
    name: "Franklin County",
    position: {
      lat: 39.409762,
      lng: -85.066964
    },
    stateName: "Indiana"
  },
  {
    id: "123",
    state: "22",
    name: "West Carroll County",
    postal: "Parish",
    position: {
      lat: 32.79248,
      lng: -91.451998
    },
    stateName: "Louisiana"
  },
  {
    id: "067",
    state: "26",
    name: "Ionia County",
    position: {
      lat: 42.94465,
      lng: -85.073766
    },
    stateName: "Michigan"
  },
  {
    id: "185",
    state: "37",
    name: "Warren County",
    position: {
      lat: 36.397979,
      lng: -78.099924
    },
    stateName: "North Carolina"
  },
  {
    id: "015",
    state: "45",
    name: "Berkeley County",
    position: {
      lat: 33.2077,
      lng: -79.953655
    },
    stateName: "South Carolina"
  },
  {
    id: "045",
    state: "13",
    name: "Carroll County",
    position: {
      lat: 33.582237,
      lng: -85.080527
    },
    stateName: "Georgia"
  },
  {
    id: "093",
    state: "37",
    name: "Hoke County",
    position: {
      lat: 35.017233,
      lng: -79.241964
    },
    stateName: "North Carolina"
  },
  {
    id: "149",
    state: "20",
    name: "Pottawatomie County",
    position: {
      lat: 39.382187,
      lng: -96.337113
    },
    stateName: "Kansas"
  },
  {
    id: "033",
    state: "28",
    name: "De Soto County",
    position: {
      lat: 34.874266,
      lng: -89.99324
    },
    stateName: "Mississippi"
  },
  {
    id: "001",
    state: "42",
    name: "Adams County",
    position: {
      lat: 39.869471,
      lng: -77.21773
    },
    stateName: "Pennsylvania"
  },
  {
    id: "161",
    state: "37",
    name: "Rutherford County",
    position: {
      lat: 35.402747,
      lng: -81.919583
    },
    stateName: "North Carolina"
  },
  {
    id: "051",
    state: "21",
    name: "Clay County",
    position: {
      lat: 37.164466,
      lng: -83.710763
    },
    stateName: "Kentucky"
  },
  {
    id: "245",
    state: "13",
    name: "Richmond County",
    position: {
      lat: 33.361487,
      lng: -82.074998
    },
    stateName: "Georgia"
  },
  {
    id: "251",
    state: "13",
    name: "Screven County",
    position: {
      lat: 32.744751,
      lng: -81.617585
    },
    stateName: "Georgia"
  },
  {
    id: "261",
    state: "13",
    name: "Sumter County",
    position: {
      lat: 32.042203,
      lng: -84.204283
    },
    stateName: "Georgia"
  },
  {
    id: "263",
    state: "13",
    name: "Talbot County",
    position: {
      lat: 32.704603,
      lng: -84.530029
    },
    stateName: "Georgia"
  },
  {
    id: "273",
    state: "13",
    name: "Terrell County",
    position: {
      lat: 31.777191,
      lng: -84.439446
    },
    stateName: "Georgia"
  },
  {
    id: "193",
    state: "20",
    name: "Thomas County",
    position: {
      lat: 39.357706,
      lng: -101.083439
    },
    stateName: "Kansas"
  },
  {
    id: "199",
    state: "20",
    name: "Wallace County",
    position: {
      lat: 38.926626,
      lng: -101.771103
    },
    stateName: "Kansas"
  },
  {
    id: "201",
    state: "20",
    name: "Washington County",
    position: {
      lat: 39.776714,
      lng: -97.095611
    },
    stateName: "Kansas"
  },
  {
    id: "195",
    state: "21",
    name: "Pike County",
    position: {
      lat: 37.482067,
      lng: -82.402869
    },
    stateName: "Kentucky"
  },
  {
    id: "211",
    state: "21",
    name: "Shelby County",
    position: {
      lat: 38.239426,
      lng: -85.22836
    },
    stateName: "Kentucky"
  },
  {
    id: "213",
    state: "21",
    name: "Simpson County",
    position: {
      lat: 36.740863,
      lng: -86.581464
    },
    stateName: "Kentucky"
  },
  {
    id: "223",
    state: "21",
    name: "Trimble County",
    position: {
      lat: 38.618193,
      lng: -85.355171
    },
    stateName: "Kentucky"
  },
  {
    id: "225",
    state: "21",
    name: "Union County",
    position: {
      lat: 37.658029,
      lng: -87.95165
    },
    stateName: "Kentucky"
  },
  {
    id: "037",
    state: "20",
    name: "Crawford County",
    position: {
      lat: 37.505628,
      lng: -94.853941
    },
    stateName: "Kansas"
  },
  {
    id: "043",
    state: "20",
    name: "Doniphan County",
    position: {
      lat: 39.788502,
      lng: -95.147225
    },
    stateName: "Kansas"
  },
  {
    id: "061",
    state: "20",
    name: "Geary County",
    position: {
      lat: 39.002155,
      lng: -96.768038
    },
    stateName: "Kansas"
  },
  {
    id: "067",
    state: "20",
    name: "Grant County",
    position: {
      lat: 37.547537,
      lng: -101.299362
    },
    stateName: "Kansas"
  },
  {
    id: "071",
    state: "20",
    name: "Greeley County",
    position: {
      lat: 38.480408,
      lng: -101.805984
    },
    stateName: "Kansas"
  },
  {
    id: "083",
    state: "20",
    name: "Hodgeman County",
    position: {
      lat: 38.087493,
      lng: -99.898407
    },
    stateName: "Kansas"
  },
  {
    id: "089",
    state: "20",
    name: "Jewell County",
    position: {
      lat: 39.77701,
      lng: -98.222584
    },
    stateName: "Kansas"
  },
  {
    id: "019",
    state: "22",
    name: "Calcasieu County",
    postal: "Parish",
    position: {
      lat: 30.229559,
      lng: -93.358015
    },
    stateName: "Louisiana"
  },
  {
    id: "027",
    state: "22",
    name: "Claiborne County",
    postal: "Parish",
    position: {
      lat: 32.827585,
      lng: -92.991125
    },
    stateName: "Louisiana"
  },
  {
    id: "029",
    state: "22",
    name: "Concordia County",
    postal: "Parish",
    position: {
      lat: 31.469806,
      lng: -91.626314
    },
    stateName: "Louisiana"
  },
  {
    id: "037",
    state: "22",
    name: "East Feliciana County",
    postal: "Parish",
    position: {
      lat: 30.839784,
      lng: -91.043434
    },
    stateName: "Louisiana"
  },
  {
    id: "009",
    state: "53",
    name: "Clallam County",
    position: {
      lat: 48.113009,
      lng: -123.930611
    },
    stateName: "Washington"
  },
  {
    id: "027",
    state: "53",
    name: "Grays Harbor County",
    position: {
      lat: 47.142786,
      lng: -123.827043
    },
    stateName: "Washington"
  },
  {
    id: "105",
    state: "55",
    name: "Rock County",
    position: {
      lat: 42.669931,
      lng: -89.075119
    },
    stateName: "Wisconsin"
  },
  {
    id: "117",
    state: "55",
    name: "Sheboygan County",
    position: {
      lat: 43.746002,
      lng: -87.730546
    },
    stateName: "Wisconsin"
  },
  {
    id: "127",
    state: "55",
    name: "Walworth County",
    position: {
      lat: 42.66811,
      lng: -88.541731
    },
    stateName: "Wisconsin"
  },
  {
    id: "009",
    state: "55",
    name: "Brown County",
    position: {
      lat: 44.473961,
      lng: -87.995926
    },
    stateName: "Wisconsin"
  },
  {
    id: "047",
    state: "49",
    name: "Uintah County",
    position: {
      lat: 40.125887,
      lng: -109.517748
    },
    stateName: "Utah"
  },
  {
    id: "053",
    state: "49",
    name: "Washington County",
    position: {
      lat: 37.262531,
      lng: -113.4878
    },
    stateName: "Utah"
  },
  {
    id: "001",
    state: "50",
    name: "Addison County",
    position: {
      lat: 44.031248,
      lng: -73.141581
    },
    stateName: "Vermont"
  },
  {
    id: "009",
    state: "50",
    name: "Essex County",
    position: {
      lat: 44.724021,
      lng: -71.732736
    },
    stateName: "Vermont"
  },
  {
    id: "017",
    state: "50",
    name: "Orange County",
    position: {
      lat: 44.003392,
      lng: -72.369687
    },
    stateName: "Vermont"
  },
  {
    id: "023",
    state: "50",
    name: "Washington County",
    position: {
      lat: 44.274953,
      lng: -72.609475
    },
    stateName: "Vermont"
  },
  {
    id: "003",
    state: "22",
    name: "Allen County",
    postal: "Parish",
    position: {
      lat: 30.652744,
      lng: -92.819605
    },
    stateName: "Louisiana"
  },
  {
    id: "009",
    state: "22",
    name: "Avoyelles County",
    postal: "Parish",
    position: {
      lat: 31.085094,
      lng: -91.993798
    },
    stateName: "Louisiana"
  },
  {
    id: "101",
    state: "22",
    name: "St. Mary County",
    postal: "Parish",
    position: {
      lat: 29.629349,
      lng: -91.463804
    },
    stateName: "Louisiana"
  },
  {
    id: "107",
    state: "22",
    name: "Tensas County",
    postal: "Parish",
    position: {
      lat: 32.001489,
      lng: -91.342576
    },
    stateName: "Louisiana"
  },
  {
    id: "109",
    state: "22",
    name: "Terrebonne County",
    postal: "Parish",
    position: {
      lat: 29.333266,
      lng: -90.844191
    },
    stateName: "Louisiana"
  },
  {
    id: "021",
    state: "21",
    name: "Boyle County",
    position: {
      lat: 37.6182,
      lng: -84.873016
    },
    stateName: "Kentucky"
  },
  {
    id: "023",
    state: "21",
    name: "Bracken County",
    position: {
      lat: 38.678523,
      lng: -84.100355
    },
    stateName: "Kentucky"
  },
  {
    id: "045",
    state: "22",
    name: "Iberia County",
    postal: "Parish",
    position: {
      lat: 29.606013,
      lng: -91.842706
    },
    stateName: "Louisiana"
  },
  {
    id: "047",
    state: "21",
    name: "Christian County",
    position: {
      lat: 36.893388,
      lng: -87.493554
    },
    stateName: "Kentucky"
  },
  {
    id: "055",
    state: "21",
    name: "Crittenden County",
    position: {
      lat: 37.358149,
      lng: -88.10501
    },
    stateName: "Kentucky"
  },
  {
    id: "059",
    state: "21",
    name: "Daviess County",
    position: {
      lat: 37.731671,
      lng: -87.087139
    },
    stateName: "Kentucky"
  },
  {
    id: "073",
    state: "21",
    name: "Franklin County",
    position: {
      lat: 38.234919,
      lng: -84.868786
    },
    stateName: "Kentucky"
  },
  {
    id: "075",
    state: "21",
    name: "Fulton County",
    position: {
      lat: 36.55262,
      lng: -89.187723
    },
    stateName: "Kentucky"
  },
  {
    id: "089",
    state: "21",
    name: "Greenup County",
    position: {
      lat: 38.563596,
      lng: -82.933833
    },
    stateName: "Kentucky"
  },
  {
    id: "095",
    state: "21",
    name: "Harlan County",
    position: {
      lat: 36.859223,
      lng: -83.221497
    },
    stateName: "Kentucky"
  },
  {
    id: "137",
    state: "55",
    name: "Waushara County",
    position: {
      lat: 44.112825,
      lng: -89.239752
    },
    stateName: "Wisconsin"
  },
  {
    id: "001",
    state: "56",
    name: "Albany County",
    position: {
      lat: 41.665514,
      lng: -105.721883
    },
    stateName: "Wyoming"
  },
  {
    id: "007",
    state: "56",
    name: "Carbon County",
    position: {
      lat: 41.70359,
      lng: -106.933153
    },
    stateName: "Wyoming"
  },
  {
    id: "011",
    state: "56",
    name: "Crook County",
    position: {
      lat: 44.589266,
      lng: -104.567298
    },
    stateName: "Wyoming"
  },
  {
    id: "013",
    state: "56",
    name: "Fremont County",
    position: {
      lat: 43.055303,
      lng: -108.605531
    },
    stateName: "Wyoming"
  },
  {
    id: "021",
    state: "56",
    name: "Laramie County",
    position: {
      lat: 41.29283,
      lng: -104.660395
    },
    stateName: "Wyoming"
  },
  {
    id: "031",
    state: "54",
    name: "Hardy County",
    position: {
      lat: 39.010818,
      lng: -78.843641
    },
    stateName: "West Virginia"
  },
  {
    id: "035",
    state: "54",
    name: "Jackson County",
    position: {
      lat: 38.834234,
      lng: -81.677717
    },
    stateName: "West Virginia"
  },
  {
    id: "041",
    state: "54",
    name: "Lewis County",
    position: {
      lat: 38.988844,
      lng: -80.495518
    },
    stateName: "West Virginia"
  },
  {
    id: "027",
    state: "50",
    name: "Windsor County",
    position: {
      lat: 43.588143,
      lng: -72.591515
    },
    stateName: "Vermont"
  },
  {
    id: "001",
    state: "51",
    name: "Accomack County",
    position: {
      lat: 37.765944,
      lng: -75.757807
    },
    stateName: "Virginia"
  },
  {
    id: "021",
    state: "51",
    name: "Bland County",
    position: {
      lat: 37.130612,
      lng: -81.125853
    },
    stateName: "Virginia"
  },
  {
    id: "027",
    state: "51",
    name: "Buchanan County",
    position: {
      lat: 37.26812,
      lng: -82.038151
    },
    stateName: "Virginia"
  },
  {
    id: "037",
    state: "51",
    name: "Charlotte County",
    position: {
      lat: 37.014091,
      lng: -78.661424
    },
    stateName: "Virginia"
  },
  {
    id: "041",
    state: "51",
    name: "Chesterfield County",
    position: {
      lat: 37.378434,
      lng: -77.585847
    },
    stateName: "Virginia"
  },
  {
    id: "009",
    state: "15",
    name: "Maui County",
    position: {
      lat: 21.130997567024142,
      lng: -88.99419053680266
    },
    stateName: "Hawaii"
  },
  {
    id: "001",
    state: "15",
    name: "Hawaii County",
    position: {
      lat: 19.64642983822674,
      lng: -87.50345574840392
    },
    stateName: "Hawaii"
  },
  {
    id: "007",
    state: "15",
    name: "Kauai County",
    position: {
      lat: 21.92393847562807,
      lng: -92.15802632160735
    },
    stateName: "Hawaii"
  },
  {
    id: "005",
    state: "15",
    name: "Kalawao County",
    position: {
      lat: 21.169699389823858,
      lng: -88.9465626900327
    },
    stateName: "Hawaii"
  },
  {
    id: "003",
    state: "15",
    name: "Honolulu County",
    position: {
      lat: 21.523021092705456,
      lng: -90.02902865446357
    },
    stateName: "Hawaii"
  },
  {
    id: "013",
    state: "72",
    name: "Arecibo County",
    position: {
      lat: 23.401557509159662,
      lng: -79.69204995832828
    },
    stateName: "Puerto Rico"
  },
  {
    id: "019",
    state: "72",
    name: "Barranquitas County",
    position: {
      lat: 23.207183313132568,
      lng: -79.30369377136232
    },
    stateName: "Puerto Rico"
  },
  {
    id: "025",
    state: "72",
    name: "Caguas County",
    position: {
      lat: 23.225807267743473,
      lng: -79.04695362255613
    },
    stateName: "Puerto Rico"
  },
  {
    id: "031",
    state: "72",
    name: "Carolina County",
    position: {
      lat: 23.390530556025336,
      lng: -78.95816227296952
    },
    stateName: "Puerto Rico"
  },
  {
    id: "045",
    state: "72",
    name: "Comerio County",
    position: {
      lat: 23.234411566015613,
      lng: -79.21618682874819
    },
    stateName: "Puerto Rico"
  },
  {
    id: "054",
    state: "72",
    name: "Florida County",
    position: {
      lat: 23.373176083153716,
      lng: -79.5626330817916
    },
    stateName: "Puerto Rico"
  },
  {
    id: "061",
    state: "72",
    name: "Guaynabo County",
    position: {
      lat: 23.35938244992981,
      lng: -79.1170120239258
    },
    stateName: "Puerto Rico"
  },
  {
    id: "069",
    state: "72",
    name: "Humacao County",
    position: {
      lat: 23.1422031627776,
      lng: -78.80594853082101
    },
    stateName: "Puerto Rico"
  },
  {
    id: "083",
    state: "72",
    name: "Las Marias County",
    position: {
      lat: 23.237704518183598,
      lng: -79.99378065827479
    },
    stateName: "Puerto Rico"
  },
  {
    id: "093",
    state: "72",
    name: "Maricao County",
    position: {
      lat: 23.164605199252843,
      lng: -79.96604919433595
    },
    stateName: "Puerto Rico"
  },
  {
    id: "105",
    state: "72",
    name: "Naranjito County",
    position: {
      lat: 23.293829537688243,
      lng: -79.24714943790066
    },
    stateName: "Puerto Rico"
  },
  {
    id: "115",
    state: "72",
    name: "Quebradillas County",
    position: {
      lat: 23.434722372923382,
      lng: -79.91949558305733
    },
    stateName: "Puerto Rico"
  },
  {
    id: "129",
    state: "72",
    name: "San Lorenzo County",
    position: {
      lat: 23.158895369813045,
      lng: -78.97384643554688
    },
    stateName: "Puerto Rico"
  },
  {
    id: "135",
    state: "72",
    name: "Toa Alta County",
    position: {
      lat: 23.360723303598327,
      lng: -79.24289202411839
    },
    stateName: "Puerto Rico"
  },
  {
    id: "145",
    state: "72",
    name: "Vega Baja County",
    position: {
      lat: 23.448263924714283,
      lng: -79.39682006835938
    },
    stateName: "Puerto Rico"
  },
  {
    id: "027",
    state: "72",
    name: "Camuy County",
    position: {
      lat: 23.423490439303162,
      lng: -79.86689273529956
    },
    stateName: "Puerto Rico"
  },
  {
    id: "035",
    state: "72",
    name: "Cayey County",
    position: {
      lat: 23.10871037640263,
      lng: -79.13030876706601
    },
    stateName: "Puerto Rico"
  },
  {
    id: "041",
    state: "72",
    name: "Cidra County",
    position: {
      lat: 23.17592267463533,
      lng: -79.16954040527345
    },
    stateName: "Puerto Rico"
  },
  {
    id: "043",
    state: "72",
    name: "Coamo County",
    position: {
      lat: 23.101447343631285,
      lng: -79.35173932612845
    },
    stateName: "Puerto Rico"
  },
  {
    id: "053",
    state: "72",
    name: "Fajardo County",
    position: {
      lat: 23.308394780814137,
      lng: -78.67185547226164
    },
    stateName: "Puerto Rico"
  },
  {
    id: "055",
    state: "72",
    name: "Guanica County",
    position: {
      lat: 22.997899144050866,
      lng: -79.92263565699965
    },
    stateName: "Puerto Rico"
  },
  {
    id: "059",
    state: "72",
    name: "Guayanilla County",
    position: {
      lat: 23.055161179907888,
      lng: -79.79974851121337
    },
    stateName: "Puerto Rico"
  },
  {
    id: "067",
    state: "72",
    name: "Hormigueros County",
    position: {
      lat: 23.131137539287284,
      lng: -80.10837266378797
    },
    stateName: "Puerto Rico"
  },
  {
    id: "073",
    state: "72",
    name: "Jayuya County",
    position: {
      lat: 23.21760419257985,
      lng: -79.58714122676966
    },
    stateName: "Puerto Rico"
  },
  {
    id: "005",
    state: "72",
    name: "Aguadilla County",
    position: {
      lat: 23.454725463530515,
      lng: -80.12155792585182
    },
    stateName: "Puerto Rico"
  },
  {
    id: "079",
    state: "72",
    name: "Lajas County",
    position: {
      lat: 23.00911000421926,
      lng: -80.04771502446687
    },
    stateName: "Puerto Rico"
  },
  {
    id: "081",
    state: "72",
    name: "Lares County",
    position: {
      lat: 23.274755534399482,
      lng: -79.86099243164064
    },
    stateName: "Puerto Rico"
  },
  {
    id: "089",
    state: "72",
    name: "Luquillo County",
    position: {
      lat: 23.34562928216131,
      lng: -78.72493743896486
    },
    stateName: "Puerto Rico"
  },
  {
    id: "095",
    state: "72",
    name: "Maunabo County",
    position: {
      lat: 23.020755550416666,
      lng: -78.91202472707401
    },
    stateName: "Puerto Rico"
  },
  {
    id: "101",
    state: "72",
    name: "Morovis County",
    position: {
      lat: 23.324588405694062,
      lng: -79.43201814882448
    },
    stateName: "Puerto Rico"
  },
  {
    id: "103",
    state: "72",
    name: "Maguabo County",
    position: {
      lat: 23.225198765846628,
      lng: -78.74550313163219
    },
    stateName: "Puerto Rico"
  },
  {
    id: "109",
    state: "72",
    name: "Patillas County",
    position: {
      lat: 23.02795048323024,
      lng: -78.99736704473794
    },
    stateName: "Puerto Rico"
  },
  {
    id: "117",
    state: "72",
    name: "Rincon County",
    position: {
      lat: 23.338331085129266,
      lng: -80.234743344971
    },
    stateName: "Puerto Rico"
  },
  {
    id: "119",
    state: "72",
    name: "Rio Grande County",
    position: {
      lat: 23.3478023467565,
      lng: -78.8017041700938
    },
    stateName: "Puerto Rico"
  },
  {
    id: "125",
    state: "72",
    name: "San German County",
    position: {
      lat: 23.108473006290914,
      lng: -80.04501342773439
    },
    stateName: "Puerto Rico"
  },
  {
    id: "131",
    state: "72",
    name: "San Sebastian County",
    position: {
      lat: 23.376041175751208,
      lng: -79.98608901767373
    },
    stateName: "Puerto Rico"
  },
  {
    id: "137",
    state: "72",
    name: "Toa Baja County",
    position: {
      lat: 23.435585967482425,
      lng: -79.19803619384767
    },
    stateName: "Puerto Rico"
  },
  {
    id: "143",
    state: "72",
    name: "Vega Alta County",
    position: {
      lat: 23.40141143512007,
      lng: -79.33911934908382
    },
    stateName: "Puerto Rico"
  },
  {
    id: "075",
    state: "72",
    name: "Juana Diaz County",
    position: {
      lat: 23.047834008314,
      lng: -79.51492309570314
    },
    stateName: "Puerto Rico"
  },
  {
    id: "147",
    state: "72",
    name: "Vieques County",
    position: {
      lat: 23.127535713128193,
      lng: -78.42195510864259
    },
    stateName: "Puerto Rico"
  },
  {
    id: "151",
    state: "72",
    name: "Yabucoa County",
    position: {
      lat: 23.06558725952808,
      lng: -78.87843126517744
    },
    stateName: "Puerto Rico"
  },
  {
    id: "153",
    state: "72",
    name: "Yauco County",
    position: {
      lat: 23.097347857538374,
      lng: -79.86442589356716
    },
    stateName: "Puerto Rico"
  },
  {
    id: "149",
    state: "72",
    name: "Villalba County",
    position: {
      lat: 23.12463080435912,
      lng: -79.47039245178983
    },
    stateName: "Puerto Rico"
  },
  {
    id: "017",
    state: "72",
    name: "Barceloneta County",
    position: {
      lat: 23.44774551252408,
      lng: -79.56785643301801
    },
    stateName: "Puerto Rico"
  },
  {
    id: "021",
    state: "72",
    name: "Bayamon County",
    position: {
      lat: 23.351365755120018,
      lng: -79.17013320000882
    },
    stateName: "Puerto Rico"
  },
  {
    id: "029",
    state: "72",
    name: "Canovanas County",
    position: {
      lat: 23.330932605348185,
      lng: -78.88440580119394
    },
    stateName: "Puerto Rico"
  },
  {
    id: "039",
    state: "72",
    name: "Ciales County",
    position: {
      lat: 23.305122507072323,
      lng: -79.51592570054346
    },
    stateName: "Puerto Rico"
  },
  {
    id: "051",
    state: "72",
    name: "Dorado County",
    position: {
      lat: 23.43452359527437,
      lng: -79.27391052246095
    },
    stateName: "Puerto Rico"
  },
  {
    id: "063",
    state: "72",
    name: "Gurabo County",
    position: {
      lat: 23.269150553459987,
      lng: -78.97892079957055
    },
    stateName: "Puerto Rico"
  },
  {
    id: "077",
    state: "72",
    name: "Juncos County",
    position: {
      lat: 23.22520150590922,
      lng: -78.9156673869743
    },
    stateName: "Puerto Rico"
  },
  {
    id: "085",
    state: "72",
    name: "Las Piedras County",
    position: {
      lat: 23.181878907598062,
      lng: -78.86705333275755
    },
    stateName: "Puerto Rico"
  },
  {
    id: "099",
    state: "72",
    name: "Moca County",
    position: {
      lat: 23.367384696525185,
      lng: -80.087466040102
    },
    stateName: "Puerto Rico"
  },
  {
    id: "111",
    state: "72",
    name: "Penuelas County",
    position: {
      lat: 23.075678990080945,
      lng: -79.74014282226564
    },
    stateName: "Puerto Rico"
  },
  {
    id: "123",
    state: "72",
    name: "Salinas County",
    position: {
      lat: 23.01634975258128,
      lng: -79.24604701837954
    },
    stateName: "Puerto Rico"
  },
  {
    id: "139",
    state: "72",
    name: "Trujillo Alto County",
    position: {
      lat: 23.329191214240975,
      lng: -78.99117487205956
    },
    stateName: "Puerto Rico"
  },
  {
    id: "001",
    state: "72",
    name: "Adjuntas County",
    position: {
      lat: 23.18871854801026,
      lng: -79.76646911685293
    },
    stateName: "Puerto Rico"
  },
  {
    id: "007",
    state: "72",
    name: "Aguas Buenas County",
    position: {
      lat: 23.251642947009007,
      lng: -79.13280487060548
    },
    stateName: "Puerto Rico"
  },
  {
    id: "011",
    state: "72",
    name: "Anasco County",
    position: {
      lat: 23.289984680381497,
      lng: -80.12901033463795
    },
    stateName: "Puerto Rico"
  },
  {
    id: "023",
    state: "72",
    name: "Cabo Rojo County",
    position: {
      lat: 23.05429753815439,
      lng: -80.15680615760085
    },
    stateName: "Puerto Rico"
  },
  {
    id: "033",
    state: "72",
    name: "Catano County",
    position: {
      lat: 23.438975543000172,
      lng: -79.1414366999585
    },
    stateName: "Puerto Rico"
  },
  {
    id: "037",
    state: "72",
    name: "Ceiba County",
    position: {
      lat: 23.25313896533081,
      lng: -78.67147907865845
    },
    stateName: "Puerto Rico"
  },
  {
    id: "047",
    state: "72",
    name: "Corozal County",
    position: {
      lat: 23.30578245923949,
      lng: -79.33330535888672
    },
    stateName: "Puerto Rico"
  },
  {
    id: "049",
    state: "72",
    name: "Culebra County",
    position: {
      lat: 23.319531623903625,
      lng: -78.2875442504883
    },
    stateName: "Puerto Rico"
  },
  {
    id: "057",
    state: "72",
    name: "Guayama County",
    position: {
      lat: 23.01129582577386,
      lng: -79.12656742907244
    },
    stateName: "Puerto Rico"
  },
  {
    id: "065",
    state: "72",
    name: "Hatillo County",
    position: {
      lat: 23.41491853692306,
      lng: -79.7995327927803
    },
    stateName: "Puerto Rico"
  },
  {
    id: "087",
    state: "72",
    name: "Loiza County",
    position: {
      lat: 23.42007604409862,
      lng: -78.89583221049864
    },
    stateName: "Puerto Rico"
  },
  {
    id: "091",
    state: "72",
    name: "Manati County",
    position: {
      lat: 23.427540721965837,
      lng: -79.49498752429675
    },
    stateName: "Puerto Rico"
  },
  {
    id: "097",
    state: "72",
    name: "Mayaguez County",
    position: {
      lat: 23.217869295326132,
      lng: -80.12377524483871
    },
    stateName: "Puerto Rico"
  },
  {
    id: "107",
    state: "72",
    name: "Orocovis County",
    position: {
      lat: 23.211410192937507,
      lng: -79.44666771287109
    },
    stateName: "Puerto Rico"
  },
  {
    id: "113",
    state: "72",
    name: "Ponce County",
    position: {
      lat: 23.067472086805715,
      lng: -79.61860656738283
    },
    stateName: "Puerto Rico"
  },
  {
    id: "121",
    state: "72",
    name: "Sabana Grande County",
    position: {
      lat: 23.093203416563703,
      lng: -79.94549812711944
    },
    stateName: "Puerto Rico"
  },
  {
    id: "127",
    state: "72",
    name: "San Juan County",
    position: {
      lat: 23.4066795149363,
      lng: -79.06449606290042
    },
    stateName: "Puerto Rico"
  },
  {
    id: "133",
    state: "72",
    name: "Santa Isabel County",
    position: {
      lat: 23.00289321561318,
      lng: -79.38808726877723
    },
    stateName: "Puerto Rico"
  },
  {
    id: "141",
    state: "72",
    name: "Utuado County",
    position: {
      lat: 23.270814867528046,
      lng: -79.69264580210199
    },
    stateName: "Puerto Rico"
  },
  {
    id: "071",
    state: "72",
    name: "Isabela County",
    position: {
      lat: 23.44863631229933,
      lng: -79.9944150974458
    },
    stateName: "Puerto Rico"
  },
  {
    id: "003",
    state: "72",
    name: "Aguada County",
    position: {
      lat: 23.36152622306958,
      lng: -80.17874219683537
    },
    stateName: "Puerto Rico"
  },
  {
    id: "009",
    state: "72",
    name: "Aibonito County",
    position: {
      lat: 23.133816062716388,
      lng: -79.26300511253329
    },
    stateName: "Puerto Rico"
  },
  {
    id: "015",
    state: "72",
    name: "Arroyo County",
    position: {
      lat: 22.99944011299678,
      lng: -79.06096330928365
    },
    stateName: "Puerto Rico"
  }
];
var countyMarkers_default = {
  countyMarkers
};

// src/components/MapComponent/MapContext/index.tsx
import { createContext as createContext5, useContext as useContext5, useEffect as useEffect8, useState as useState10 } from "react";

// src/components/MapComponent/config.ts
var DEFAULT_MAP_OPTIONS = {
  center: {
    lat: 38.32999644906359,
    lng: -100
  },
  zoom: 4.4,
  zoomSnap: 0.2,
  zoomControl: false,
  scrollWheelZoom: true,
  attributionControl: false
};
var DEFAULT_MAP_STYLE = {
  height: "60vh",
  width: "100%",
  background: "#ddd",
  backgroundColor: chakraTheme.colors?.tableHeaderBlue
};
var DEFAULT_HEATMAP_COLORS = [
  "#D6E6ED",
  "#B6DBE9",
  "#98D1E6",
  "#7AC7E2",
  "#5DBEDF",
  "#41B5DB",
  "#26ACD8",
  "#07A1D4",
  "#0192C3",
  "#0181AE",
  "#01739E",
  "#01658D",
  "#01567A",
  "#014667",
  "#013856"
];
var DEFAULT_STATE_LAYER_STYLE = {
  stroke: true,
  weight: 0.8,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: true,
  fillColor: chakraTheme.colors?.explorerBackground,
  fillOpacity: 1
};
var DEFAULT_COUNTY_LAYER_STYLE = {
  stroke: true,
  weight: 0.3,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: true,
  fillColor: chakraTheme.colors?.explorerBackground,
  fillOpacity: 1
};
var STATE_LAYER_STYLE_WHEN_COUNTY_LAYER_ACTIVE = {
  stroke: true,
  weight: 1,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: false,
  fillOpacity: 0
};
var SELECTED_COUNTY_LAYER_STYLE = {
  stroke: true,
  weight: 2,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: true,
  fillColor: chakraTheme.colors?.explorerBackground,
  fillOpacity: 1
};
var SELECTED_STATE_LAYER_STYLE = {
  stroke: true,
  weight: 2,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: true,
  fillColor: chakraTheme.colors?.explorerBackground,
  fillOpacity: 1
};
var NO_INFORMATION_STATE_LAYER_STYLE = {
  stroke: true,
  weight: 0.8,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: true,
  fillColor: chakraTheme.colors?.greyDarkDisable,
  fillOpacity: 1
};
var NO_INFORMATION_COUNTY_LAYER_STYLE = {
  stroke: true,
  weight: 0.3,
  color: chakraTheme.colors?.darkBlue,
  opacity: 1,
  fill: true,
  fillColor: chakraTheme.colors?.greyDarkDisable,
  fillOpacity: 1
};
var DEFAULT_STATE_MARKER_STYLE = {
  w: "20px",
  textStyle: "regular-12-14",
  color: chakraTheme.colors?.darkBlue
};
var SELECTED_STATE_MARKER_STYLE = {
  w: "20px",
  textStyle: "semiBold-12-14",
  color: chakraTheme.colors?.white
};
var NO_INFORMATION_STATE_MARKER_STYLE = {
  w: "20px",
  textStyle: "regular-12-14",
  color: chakraTheme.colors?.greyDarkDisable
};
var DEFAULT_LAYER_STYLES = {
  defaultStateLayerStyle: DEFAULT_STATE_LAYER_STYLE,
  defaultCountyLayerStyle: DEFAULT_COUNTY_LAYER_STYLE,
  stateLayerStyleWhenCountyLayerActive: STATE_LAYER_STYLE_WHEN_COUNTY_LAYER_ACTIVE,
  selectedCountyLayerStyle: SELECTED_COUNTY_LAYER_STYLE,
  selectedStateLayerStyle: SELECTED_STATE_LAYER_STYLE,
  noInformationStateLayerStyle: NO_INFORMATION_STATE_LAYER_STYLE,
  noInformationCountyLayerStyle: NO_INFORMATION_COUNTY_LAYER_STYLE,
  stateMarkerStyle: DEFAULT_STATE_MARKER_STYLE,
  selectedStateMarkerStyle: SELECTED_STATE_MARKER_STYLE,
  noInformationMarkerStyle: NO_INFORMATION_STATE_MARKER_STYLE
};
var OVERRIDE_LEAFLET_STYLE = {
  ".leaflet-popup": {
    marginBottom: "0px",
    boxShadow: chakraTheme.shadow?.mediumShadow
  },
  ".leaflet-popup-content p": {
    margin: "0 0"
  },
  ".leaflet-popup-content-wrapper": {
    padding: "0px",
    borderRadius: "3px"
  },
  ".leaflet-popup-content": {
    margin: "0px 0px 0px 0px"
  },
  ".leaflet-container a.leaflet-popup-close-button": {
    display: "none"
  },
  "g:focus": {
    outline: "none"
  },
  "path:focus": {
    outline: "none"
  }
};

// src/components/MapComponent/MapContext/utils.ts
var getScale = (max, heatMapColors, min) => {
  const scale = [min];
  if (max) {
    const divided = (max - min) / (heatMapColors.length - 1);
    for (let i = 1; i < heatMapColors.length - 1; i++) {
      scale.push(i * divided + min);
    }
    scale.push(max);
  }
  return scale.length ? scale : [0];
};
var getColor2 = (value, scale, colors2) => {
  const index = scale.reduce((idx, scaleValue, scaleIndex) => {
    return value >= scaleValue ? scaleIndex : idx;
  }, 0);
  return colors2[index];
};

// src/components/MapComponent/MapContext/index.tsx
import { jsx as jsx209 } from "react/jsx-runtime";
var MapComponentContext = createContext5(null);
var MapComponentContextProvider = ({
  children,
  mapOptions,
  mapStyle,
  layerStyles,
  activeLayer,
  setActiveLayer,
  isStateLayerDisabled,
  isCountyLayerDisabled,
  showControls,
  mapInformation,
  mapSelectedLocations,
  heatProps,
  paintUsingSelectedStates,
  paintUsingSelectedCounties,
  startScaleFromZero,
  onClickState,
  onClickCounty,
  flyToLocation
}) => {
  const [selectedHeatKey, setSelectedHeatKey] = useState10(
    Object.keys(heatProps)[0]
  );
  useEffect8(() => {
    setSelectedHeatKey(Object.keys(heatProps)[0]);
  }, [heatProps]);
  const [scaleMaxState, setScaleMaxState] = useState10(0);
  const [scaleMinState, setScaleMinState] = useState10(0);
  const [scaleMaxCounty, setScaleMaxCounty] = useState10(0);
  const [scaleMinCounty, setScaleMinCounty] = useState10(0);
  const [stateScale, setStateScale] = useState10([0]);
  const [countyScale, setCountyScale] = useState10([0]);
  useEffect8(() => {
    const statesPaintInfo = mapInformation.states.filter(
      (currentState) => mapSelectedLocations.states.includes(currentState.name) || !paintUsingSelectedStates
    ).map(
      (currentState) => currentState.information[selectedHeatKey]
    );
    const countiesPaintInfo = mapInformation.counties.filter(
      (currentCounty) => !!mapSelectedLocations.counties.find(
        (c) => c.name === currentCounty.name && c.state === currentCounty.state
      ) || !paintUsingSelectedCounties
    ).map(
      (currentCounty) => currentCounty.information[selectedHeatKey]
    );
    const maxStateNumber = Math.max(...statesPaintInfo);
    const minStateNumber = startScaleFromZero ? 0 : Math.min(...statesPaintInfo);
    const maxCountyNumber = Math.max(...countiesPaintInfo);
    const minCountyNumber = startScaleFromZero ? 0 : Math.min(...countiesPaintInfo);
    const heatMapColors = heatProps[selectedHeatKey].heatMapColors || DEFAULT_HEATMAP_COLORS;
    const newStateScale = getScale(
      maxStateNumber,
      heatMapColors,
      minStateNumber
    );
    const newCountyScale = getScale(
      maxCountyNumber,
      heatMapColors,
      minCountyNumber
    );
    setScaleMaxState(maxStateNumber);
    setScaleMinState(minStateNumber);
    setScaleMaxCounty(maxCountyNumber);
    setScaleMinCounty(minCountyNumber);
    setStateScale(newStateScale);
    setCountyScale(newCountyScale);
  }, [mapInformation, mapSelectedLocations, startScaleFromZero]);
  const context = {
    mapOptions,
    mapStyle,
    activeLayer,
    setActiveLayer,
    layerStyles,
    isStateLayerDisabled,
    isCountyLayerDisabled,
    showControls,
    mapInformation,
    mapSelectedLocations,
    heatProps,
    paintUsingSelectedStates,
    paintUsingSelectedCounties,
    startScaleFromZero,
    onClickState,
    onClickCounty,
    flyToLocation,
    // Internal states of the map
    selectedHeatKey,
    setSelectedHeatKey,
    scaleMaxState,
    scaleMinState,
    scaleMaxCounty,
    scaleMinCounty,
    stateScale,
    countyScale
  };
  return /* @__PURE__ */ jsx209(MapComponentContext.Provider, { value: context, children });
};
var useMapComponentCtx = () => {
  const context = MapComponentContext;
  if (!context) {
    console.error("There is an error with map component context");
  }
  return useContext5(context);
};

// src/components/MapComponent/MapControls/LayerSelector/LayerSelector.tsx
import { Divider as Divider2, Flex as Flex17, Text as Text14, useTheme as useTheme50 } from "@chakra-ui/react";

// src/components/MapComponent/MapControls/LayerSelector/config.ts
var STATE_TEXT = "State";
var COUNTY_TEXT = "County";

// src/components/MapComponent/MapControls/LayerSelector/LayerSelectorTooltip.tsx
import { useTheme as useTheme49 } from "@chakra-ui/react";
import { jsx as jsx210 } from "react/jsx-runtime";
var LayerSelectorTooltip = ({
  children,
  ...props
}) => {
  const theme = useTheme49();
  return /* @__PURE__ */ jsx210(
    Tooltip_default,
    {
      w: "121px",
      h: "42px",
      shouldWrapChildren: true,
      arrowSize: 12,
      padding: "0px",
      bg: theme.colors.white,
      labelProps: {
        color: theme.colors?.mediumGrey,
        textStyle: "regular-8-10",
        padding: "10px"
      },
      ...props,
      children
    }
  );
};

// src/components/MapComponent/MapControls/LayerSelector/LayerSelector.tsx
import { jsx as jsx211, jsxs as jsxs114 } from "react/jsx-runtime";
var LayerSelector = () => {
  const theme = useTheme50();
  const {
    activeLayer,
    setActiveLayer,
    isCountyLayerDisabled,
    isStateLayerDisabled
  } = useMapComponentCtx();
  return /* @__PURE__ */ jsxs114(
    Flex17,
    {
      position: "absolute",
      bottom: "30px",
      right: "88px",
      zIndex: "1000",
      w: "150px",
      h: "36px",
      backgroundColor: theme.colors?.white,
      boxShadow: theme.shadow.lg,
      borderRadius: "6px",
      justifyContent: "center",
      alignItems: "center",
      cursor: "pointer",
      children: [
        /* @__PURE__ */ jsx211(
          LayerSelectorTooltip,
          {
            label: "We don\u2019t have county level information.",
            isDisabled: !isCountyLayerDisabled,
            children: /* @__PURE__ */ jsx211(
              Flex17,
              {
                w: "75px",
                h: "18px",
                justifyContent: "center",
                alignItems: "center",
                onClick: () => !isCountyLayerDisabled && setActiveLayer("county"),
                "data-testid": "layer-selector--county-container",
                children: /* @__PURE__ */ jsx211(
                  Text14,
                  {
                    textStyle: activeLayer === "county" ? "bold-14-19" : "regular-14-19",
                    color: isCountyLayerDisabled ? theme.colors?.lighterGrey : activeLayer === "county" ? theme.colors?.blueOne : theme.colors?.mediumGrey,
                    "data-testid": "layer-selector--county-text",
                    children: COUNTY_TEXT
                  }
                )
              }
            )
          }
        ),
        /* @__PURE__ */ jsx211(
          Divider2,
          {
            orientation: "vertical",
            borderTopWidth: "1px",
            borderColor: theme.colors?.lighterGrey
          }
        ),
        /* @__PURE__ */ jsx211(
          LayerSelectorTooltip,
          {
            label: "We don\u2019t have state level information.",
            isDisabled: !isStateLayerDisabled,
            children: /* @__PURE__ */ jsx211(
              Flex17,
              {
                w: "75px",
                h: "18px",
                justifyContent: "center",
                alignItems: "center",
                onClick: () => !isStateLayerDisabled && setActiveLayer("state"),
                "data-testid": "layer-selector--state-container",
                children: /* @__PURE__ */ jsx211(
                  Text14,
                  {
                    textStyle: activeLayer === "state" ? "bold-14-19" : "regular-14-19",
                    color: isStateLayerDisabled ? theme.colors?.lighterGrey : activeLayer === "state" ? theme.colors?.blueOne : theme.colors?.mediumGrey,
                    "data-testid": "layer-selector--state-text",
                    children: STATE_TEXT
                  }
                )
              }
            )
          }
        )
      ]
    }
  );
};
var LayerSelector_default = LayerSelector;

// src/components/MapComponent/MapControls/Zoom/Zoom.tsx
import { Divider as Divider3, Flex as Flex18, useTheme as useTheme51 } from "@chakra-ui/react";
import { useLeaflet } from "react-leaflet";
import { jsx as jsx212, jsxs as jsxs115 } from "react/jsx-runtime";
var Zoom = () => {
  const theme = useTheme51();
  const { map } = useLeaflet();
  return /* @__PURE__ */ jsxs115(
    Flex18,
    {
      position: "absolute",
      bottom: "30px",
      right: "30px",
      zIndex: "1000",
      w: "28px",
      h: "57px",
      backgroundColor: theme.colors?.white,
      boxShadow: theme.shadow.lg,
      borderRadius: "6px",
      direction: "column",
      justifyContent: "flex-start",
      alignItems: "center",
      cursor: "pointer",
      children: [
        /* @__PURE__ */ jsx212(
          Flex18,
          {
            w: "28px",
            h: "28px",
            justifyContent: "center",
            alignItems: "center",
            onClick: () => map?.zoomIn(),
            borderTopRadius: "6px",
            children: /* @__PURE__ */ jsx212(PlusIcon_default, { cursor: "pointer", width: 10, height: 10 })
          }
        ),
        /* @__PURE__ */ jsx212(
          Divider3,
          {
            w: "28px",
            borderTopWidth: "2px",
            borderColor: theme.colors?.lighterGrey
          }
        ),
        /* @__PURE__ */ jsx212(
          Flex18,
          {
            w: "28px",
            h: "28px",
            justifyContent: "center",
            alignItems: "center",
            onClick: () => map?.zoomOut(),
            borderBottomRadius: "6px",
            children: /* @__PURE__ */ jsx212(MinusIcon_default, { cursor: "pointer", width: 10, height: 1.7 })
          }
        )
      ]
    }
  );
};
var Zoom_default = Zoom;

// src/components/MapComponent/MapControls/Legend/Legend.tsx
import { Box as Box37, Flex as Flex19, Text as Text15, useTheme as useTheme52 } from "@chakra-ui/react";
import { jsx as jsx213, jsxs as jsxs116 } from "react/jsx-runtime";
var Legend = () => {
  const theme = useTheme52();
  const {
    heatProps,
    selectedHeatKey,
    activeLayer,
    scaleMaxState,
    scaleMinState,
    scaleMaxCounty,
    scaleMinCounty,
    layerStyles
  } = useMapComponentCtx();
  return /* @__PURE__ */ jsxs116(
    Flex19,
    {
      position: "absolute",
      bottom: "30px",
      left: "30px",
      zIndex: "1000",
      p: "10px",
      w: "262px",
      h: "72px",
      backgroundColor: theme.colors?.white,
      boxShadow: theme.shadow.lg,
      borderRadius: "6px",
      direction: "column",
      justifyContent: "center",
      alignItems: "flex-start",
      children: [
        /* @__PURE__ */ jsx213(
          Text15,
          {
            noOfLines: 1,
            textStyle: "semiBold-10-12",
            color: theme.colors?.mediumGrey,
            children: heatProps[selectedHeatKey].heatLegendLabel
          }
        ),
        /* @__PURE__ */ jsxs116(Flex19, { w: "100%", justifyContent: "space-between", children: [
          /* @__PURE__ */ jsxs116(Box37, { children: [
            /* @__PURE__ */ jsx213(Flex19, { w: "210px", marginTop: "5px", children: heatProps[selectedHeatKey].heatMapColors?.map((color, idx) => {
              const heatColorsLength = heatProps[selectedHeatKey].heatMapColors?.length || 0;
              return /* @__PURE__ */ jsx213(
                Box37,
                {
                  h: "17px",
                  w: `${210 / (heatColorsLength || DEFAULT_HEATMAP_COLORS.length)}px`,
                  backgroundColor: color,
                  borderLeftRadius: idx === 0 ? "5px" : 0,
                  borderRightRadius: idx === heatColorsLength - 1 ? "5px" : 0
                },
                color
              );
            }) }),
            /* @__PURE__ */ jsxs116(
              Flex19,
              {
                w: "210px",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: "2px",
                children: [
                  /* @__PURE__ */ jsx213(Text15, { textStyle: "regular-12-16", color: theme.colors?.mediumGrey, children: activeLayer === "state" ? scaleMinState.toLocaleString() : scaleMinCounty.toLocaleString() }),
                  /* @__PURE__ */ jsx213(Text15, { textStyle: "regular-12-16", color: theme.colors?.mediumGrey, children: activeLayer === "state" ? scaleMaxState.toLocaleString() : scaleMaxCounty.toLocaleString() })
                ]
              }
            )
          ] }),
          /* @__PURE__ */ jsxs116(
            Flex19,
            {
              direction: "column",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "5px",
              children: [
                /* @__PURE__ */ jsx213(
                  Box37,
                  {
                    h: "17px",
                    w: "15px",
                    backgroundColor: activeLayer === "state" ? layerStyles.noInformationStateLayerStyle.fillColor : layerStyles.noInformationCountyLayerStyle.fillColor,
                    borderRadius: "5px"
                  }
                ),
                /* @__PURE__ */ jsx213(
                  Text15,
                  {
                    textStyle: "regular-10-14",
                    color: theme.colors?.mediumGrey,
                    marginTop: "2px",
                    children: "N/A"
                  }
                )
              ]
            }
          )
        ] })
      ]
    }
  );
};
var Legend_default = Legend;

// src/components/MapComponent/MapLayers/StateMarkers/StateMarkers.tsx
import { Text as Text16 } from "@chakra-ui/react";
import { Marker } from "react-leaflet";
import L from "leaflet";
import ReactDOMServer from "react-dom/server";
import { Fragment as Fragment18, jsx as jsx214 } from "react/jsx-runtime";
var StateMarkers = () => {
  const { mapSelectedLocations, mapInformation, layerStyles } = useMapComponentCtx();
  return /* @__PURE__ */ jsx214(Fragment18, { children: stateMarkers_default.stateMarkers.map(({ id, name, postal, position }) => {
    const selectedState = mapSelectedLocations.states.find(
      (state) => state === name
    );
    const stateInformation = mapInformation.states.find(
      (state) => state.name === name
    );
    const getTextProps = () => {
      if (!stateInformation) {
        return layerStyles.noInformationMarkerStyle;
      }
      if (selectedState) {
        return layerStyles.selectedStateMarkerStyle;
      }
      return layerStyles.stateMarkerStyle;
    };
    return /* @__PURE__ */ jsx214(
      Marker,
      {
        position,
        icon: L.divIcon({
          className: "map-component--map-layers--state-marker",
          html: ReactDOMServer.renderToString(
            /* @__PURE__ */ jsx214(
              Text16,
              {
                ...getTextProps(),
                "data-testid": `state-marker-layer--${postal}`,
                children: postal
              }
            )
          )
        })
      },
      id
    );
  }) });
};
var StateMarkers_default = StateMarkers;

// src/components/MapComponent/MapLayers/CountyNameTooltip/CountyNameTooltip.tsx
import { Text as Text17, useTheme as useTheme53 } from "@chakra-ui/react";
import { jsx as jsx215 } from "react/jsx-runtime";
var CountyNameTooltip = ({ countyName }) => {
  const theme = useTheme53();
  return /* @__PURE__ */ jsx215(Text17, { textStyle: "regular-10-10", color: theme.colors?.mediumGrey, children: countyName });
};
var CountyNameTooltip_default = CountyNameTooltip;

// src/components/MapComponent/MapLayers/MapLayers.tsx
import { Fragment as Fragment19, jsx as jsx216, jsxs as jsxs117 } from "react/jsx-runtime";
var MapLayers = () => {
  const {
    activeLayer,
    layerStyles,
    showControls,
    mapInformation,
    mapSelectedLocations,
    selectedHeatKey,
    stateScale,
    countyScale,
    heatProps,
    onClickState,
    onClickCounty,
    flyToLocation
  } = useMapComponentCtx();
  const { map } = useLeaflet2();
  useEffect9(() => {
    return () => {
      map?.clearAllEventListeners();
    };
  }, []);
  useEffect9(() => {
    if (flyToLocation) {
      const { county, state, zoom, options } = flyToLocation;
      if (!county && state) {
        const statesMarker = stateMarkers_default.stateMarkers.find(
          (marker) => marker.name === state
        );
        if (state === "center") {
          map?.flyTo(
            DEFAULT_MAP_OPTIONS.center,
            DEFAULT_MAP_OPTIONS.zoom,
            options
          );
        } else {
          map?.flyTo(
            statesMarker?.position || { lat: 0, lng: 0 },
            zoom,
            options
          );
        }
      }
      if (county && state) {
        const countyMarker = countyMarkers_default.countyMarkers.find(
          (marker) => marker.name === county && marker.stateName === state
        );
        map?.flyTo(countyMarker?.position || { lat: 0, lng: 0 }, zoom, options);
      }
    }
  }, [flyToLocation]);
  const handlePaintStates = (feature) => {
    const stateInformation = mapInformation.states.find(
      (state) => state.name === feature?.properties?.name
    );
    const selectedState = mapSelectedLocations.states.find(
      (state) => state === feature?.properties?.name
    );
    if (activeLayer === "county") {
      if (selectedState) {
        return {
          ...layerStyles.stateLayerStyleWhenCountyLayerActive,
          weight: layerStyles.selectedStateLayerStyle.weight,
          color: layerStyles.selectedStateLayerStyle.color
        };
      } else {
        return layerStyles.stateLayerStyleWhenCountyLayerActive;
      }
    } else {
      if (selectedState) {
        if (stateInformation) {
          const heatMapColors = heatProps[selectedHeatKey].heatMapColors || DEFAULT_HEATMAP_COLORS;
          const color = getColor2(
            stateInformation.information[selectedHeatKey],
            stateScale,
            heatMapColors
          );
          return { ...layerStyles.defaultStateLayerStyle, fillColor: color };
        } else {
          return layerStyles.noInformationStateLayerStyle;
        }
      } else {
        if (stateInformation) {
          return layerStyles.defaultStateLayerStyle;
        } else {
          return layerStyles.noInformationStateLayerStyle;
        }
      }
    }
  };
  const handlePaintCounties = (feature) => {
    if (activeLayer === "county") {
      const countyInformation = mapInformation.counties.find(
        (county) => county.name === feature?.properties?.name && county.state === feature?.properties?.stateName
      );
      const selectedCounty = mapSelectedLocations.counties.find(
        (county) => county.name === feature?.properties?.name && county.state === feature?.properties?.stateName
      );
      if (selectedCounty) {
        if (countyInformation) {
          const heatMapColors = heatProps[selectedHeatKey].heatMapColors || DEFAULT_HEATMAP_COLORS;
          const color = getColor2(
            countyInformation.information[selectedHeatKey],
            countyScale,
            heatMapColors
          );
          return { ...layerStyles.defaultStateLayerStyle, fillColor: color };
        } else {
          return layerStyles.noInformationCountyLayerStyle;
        }
      } else {
        if (countyInformation) {
          return layerStyles.defaultCountyLayerStyle;
        } else {
          return layerStyles.noInformationCountyLayerStyle;
        }
      }
    }
    return layerStyles.defaultCountyLayerStyle;
  };
  const onEachState = (feature, layer) => {
    layer.on({
      click: (e) => {
        const stateInformation = mapInformation.states.find(
          (state2) => state2.name === feature?.properties?.name
        );
        const selectedState = mapSelectedLocations.states.find(
          (state2) => state2 === feature?.properties?.name
        );
        const statesMarker = stateMarkers_default.stateMarkers.find(
          (state2) => state2.name === feature?.properties?.name
        );
        const state = {
          name: feature.properties.name,
          state: feature.properties.name,
          hasInformation: !!stateInformation,
          information: stateInformation?.information || {},
          isSelected: !!selectedState,
          center: statesMarker?.position || { lat: 0, lng: 0 }
        };
        onClickState(state, layer, e, map);
      }
    });
  };
  const onEachCounty = (feature, layer) => {
    if (feature?.properties?.name) {
      layer.bindTooltip(
        ReactDOMServer2.renderToString(
          /* @__PURE__ */ jsx216(CountyNameTooltip_default, { countyName: feature.properties.name })
        )
      );
    }
    layer.on({
      click: (e) => {
        const countyInformation = mapInformation.counties.find(
          (county2) => county2.name === feature?.properties?.name && county2.state === feature?.properties?.stateName
        );
        const selectedCounty = mapSelectedLocations.counties.find(
          (county2) => county2.name === feature?.properties?.name && county2.state === feature?.properties?.stateName
        );
        const countyMarker = countyMarkers_default.countyMarkers.find(
          (county2) => county2.name === feature?.properties?.name && county2.stateName === feature?.properties?.stateName
        );
        const county = {
          name: feature.properties.name,
          state: feature.properties.stateName,
          hasInformation: !!countyInformation,
          information: countyInformation?.information || {},
          isSelected: !!selectedCounty,
          center: countyMarker?.position || { lat: 0, lng: 0 }
        };
        onClickCounty(county, layer, e, map);
      }
    });
  };
  return /* @__PURE__ */ jsxs117(Fragment19, { children: [
    activeLayer === "county" && /* @__PURE__ */ jsx216(
      GeoJSON,
      {
        style: (feature) => handlePaintCounties(feature),
        data: counties_default,
        onEachFeature: onEachCounty,
        interactive: activeLayer === "county"
      },
      `counties-${activeLayer}-${mapInformation.counties.length}-${mapSelectedLocations.counties.length}`
    ),
    /* @__PURE__ */ jsx216(
      GeoJSON,
      {
        style: (feature) => handlePaintStates(feature),
        data: states_default,
        onEachFeature: onEachState,
        interactive: activeLayer === "state"
      },
      `states-${activeLayer}${mapInformation.states.length}-${mapSelectedLocations.states.length}`
    ),
    activeLayer === "state" && /* @__PURE__ */ jsx216(StateMarkers_default, {}),
    showControls && /* @__PURE__ */ jsxs117(Fragment19, { children: [
      (activeLayer === "state" && !!mapSelectedLocations.states.length && !!mapInformation.states.length || activeLayer === "county" && !!mapSelectedLocations.counties.length && !!mapInformation.counties.length) && /* @__PURE__ */ jsx216(Legend_default, {}),
      /* @__PURE__ */ jsx216(LayerSelector_default, {}),
      /* @__PURE__ */ jsx216(Zoom_default, {})
    ] })
  ] });
};
var MapLayers_default = MapLayers;

// src/components/MapComponent/MapComponent.tsx
import { jsx as jsx217, jsxs as jsxs118 } from "react/jsx-runtime";
var MapComponent = ({ children }) => {
  const { mapOptions, mapStyle } = useMapComponentCtx();
  return /* @__PURE__ */ jsxs118(
    Map,
    {
      style: mapStyle,
      ...mapOptions,
      children: [
        /* @__PURE__ */ jsx217(MapLayers_default, {}),
        children
      ]
    },
    `mapContainer-${Object.keys(mapStyle).length}`
  );
};
var MapComponent_default = MapComponent;

// src/components/MapComponent/index.tsx
import { Box as Box38 } from "@chakra-ui/react";
import { jsx as jsx218 } from "react/jsx-runtime";
var MapComponetWrapper = ({
  children,
  activeLayer,
  setActiveLayer,
  mapInformation,
  mapSelectedLocations,
  heatProps,
  mapOptions,
  mapStyle,
  layerStyles,
  isStateLayerDisabled = false,
  isCountyLayerDisabled = false,
  showControls = false,
  paintUsingSelectedStates = false,
  paintUsingSelectedCounties = true,
  startScaleFromZero = true,
  onClickState,
  onClickCounty,
  flyToLocation
}) => {
  const newMapOptions = { ...DEFAULT_MAP_OPTIONS, ...mapOptions };
  const newMapStyle = { ...DEFAULT_MAP_STYLE, ...mapStyle };
  const newLayerStyles = {
    defaultStateLayerStyle: layerStyles?.defaultStateLayerStyle || DEFAULT_LAYER_STYLES.defaultStateLayerStyle,
    defaultCountyLayerStyle: layerStyles?.defaultCountyLayerStyle || DEFAULT_LAYER_STYLES.defaultCountyLayerStyle,
    stateLayerStyleWhenCountyLayerActive: layerStyles?.stateLayerStyleWhenCountyLayerActive || DEFAULT_LAYER_STYLES.stateLayerStyleWhenCountyLayerActive,
    selectedCountyLayerStyle: layerStyles?.selectedCountyLayerStyle || DEFAULT_LAYER_STYLES.selectedCountyLayerStyle,
    selectedStateLayerStyle: layerStyles?.selectedStateLayerStyle || DEFAULT_LAYER_STYLES.selectedStateLayerStyle,
    noInformationStateLayerStyle: layerStyles?.noInformationStateLayerStyle || DEFAULT_LAYER_STYLES.noInformationStateLayerStyle,
    noInformationCountyLayerStyle: layerStyles?.noInformationCountyLayerStyle || DEFAULT_LAYER_STYLES.noInformationCountyLayerStyle,
    stateMarkerStyle: layerStyles?.stateMarkerStyle || DEFAULT_LAYER_STYLES.stateMarkerStyle,
    selectedStateMarkerStyle: layerStyles?.selectedStateMarkerStyle || DEFAULT_LAYER_STYLES.selectedStateMarkerStyle,
    noInformationMarkerStyle: layerStyles?.noInformationMarkerStyle || DEFAULT_LAYER_STYLES.noInformationMarkerStyle
  };
  return /* @__PURE__ */ jsx218(
    MapComponentContextProvider,
    {
      mapOptions: newMapOptions,
      mapStyle: newMapStyle,
      layerStyles: newLayerStyles,
      activeLayer,
      setActiveLayer,
      isStateLayerDisabled,
      isCountyLayerDisabled,
      showControls,
      mapInformation,
      mapSelectedLocations,
      heatProps,
      paintUsingSelectedStates,
      paintUsingSelectedCounties,
      startScaleFromZero,
      onClickState,
      onClickCounty,
      flyToLocation,
      children: /* @__PURE__ */ jsx218(Box38, { width: "100%", sx: OVERRIDE_LEAFLET_STYLE, children: /* @__PURE__ */ jsx218(MapComponent_default, { children }) })
    }
  );
};
var MapComponent_default2 = MapComponetWrapper;

// src/components/MapFilterTag/index.tsx
import { Tag as Tag2, TagCloseButton as TagCloseButton2, TagLabel as TagLabel2, Text as Text18 } from "@chakra-ui/react";
import { jsx as jsx219, jsxs as jsxs119 } from "react/jsx-runtime";
var MapFilterTag = ({
  textContent,
  onClose,
  backgroundColor,
  color,
  closeColor = "rgba(255, 255, 255, 0.75)"
}) => /* @__PURE__ */ jsxs119(
  Tag2,
  {
    size: "md",
    borderRadius: "full",
    variant: "solid",
    backgroundColor,
    "data-testid": "column-filter-tag-id",
    minHeight: "unset",
    height: "24px",
    paddingRight: "0",
    paddingLeft: "0",
    children: [
      /* @__PURE__ */ jsx219(TagLabel2, { display: "flex", children: /* @__PURE__ */ jsx219(
        Text18,
        {
          textStyle: "inter-medium-12-14",
          margin: "0 5px 0 7px",
          color,
          userSelect: "none",
          children: textContent.toLocaleUpperCase()
        }
      ) }),
      /* @__PURE__ */ jsx219(
        TagCloseButton2,
        {
          width: "14px",
          height: "14px",
          color: backgroundColor,
          background: closeColor,
          onClick: onClose,
          "data-testid": "tag-close-button",
          margin: "2px 2px 2px auto"
        }
      )
    ]
  },
  `tag-${textContent}`
);
var MapFilterTag_default = MapFilterTag;

// src/components/Marker/index.tsx
import { Fragment as Fragment20 } from "react";
import { Box as Box39, useTheme as useTheme54 } from "@chakra-ui/react";
import { jsx as jsx220 } from "react/jsx-runtime";
var Marker2 = ({
  outlined,
  backgroundColor,
  color,
  fontFamily,
  fontSize,
  lineHeight,
  textTransform,
  children,
  ...props
}) => {
  const theme = useTheme54();
  const [markerLabelRef, overflowActive] = useEllipsisTooltip();
  const hasTooltip = (overflowActive || props.alwaysShowTooltip) && props.tooltipId;
  const WrapperComponent = hasTooltip ? Tooltip_default : Fragment20;
  const wrapperComponentProps = hasTooltip ? {
    label: props.dataTip || null,
    placement: props.dataPlace || "right",
    dataTestId: props.tooltipId
  } : {};
  return /* @__PURE__ */ jsx220(
    Box39,
    {
      "data-testid": "marker",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: "3px",
      width: "max-content",
      textAlign: "center",
      margin: "3px 0",
      padding: "2px 7px",
      maxWidth: "100%",
      background: outlined ? "transparent" : backgroundColor || "#3992C6",
      border: outlined ? `1px solid ${backgroundColor || "#3992C6"}` : "none",
      cursor: "default",
      ...props,
      children: /* @__PURE__ */ jsx220(WrapperComponent, { ...wrapperComponentProps, children: /* @__PURE__ */ jsx220(
        Box39,
        {
          as: "label",
          color: color || "#ffffff",
          fontFamily: fontFamily || theme.fonts.regular,
          fontSize: fontSize || "10px",
          lineHeight: lineHeight || "14px",
          textTransform: textTransform || "uppercase",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "inline-block",
          ref: markerLabelRef,
          children
        }
      ) })
    }
  );
};
var Marker_default = Marker2;

// src/components/Pagination/index.tsx
import _2 from "lodash";

// src/components/Pagination/AngleIcon/index.tsx
import { jsx as jsx221 } from "react/jsx-runtime";
var AngleSelect = ({
  width = 14,
  height = 9,
  color = "#58585A",
  margin = "0"
}) => /* @__PURE__ */ jsx221(
  "svg",
  {
    viewBox: "0 0 14 9",
    width,
    height,
    style: { margin },
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    children: /* @__PURE__ */ jsx221(
      "path",
      {
        d: "M6.307 8.203l-6.02-6.02A.98.98 0 111.674.797L7 6.123 12.326.797a.98.98 0 011.387 1.387l-6.02 6.02a.977.977 0 01-1.386 0z",
        fill: color
      }
    )
  }
);
var AngleIcon_default2 = AngleSelect;

// src/components/Pagination/Page/index.tsx
import { Button as Button3 } from "@chakra-ui/react";
import { jsx as jsx222 } from "react/jsx-runtime";
var Page = ({
  pageNumber,
  onClickPage,
  isActive,
  testId,
  color,
  backgroundColor
}) => {
  const getWidth = () => {
    if (pageNumber.toString().length > 3) {
      return "60px";
    } else if (pageNumber.toString().length === 3) {
      return "55px";
    } else {
      return "42px";
    }
  };
  return /* @__PURE__ */ jsx222(
    Button3,
    {
      textStyle: "semiBold-16-22",
      borderRadius: "3",
      variant: isActive ? "solid" : "ghost",
      onClick: () => onClickPage(pageNumber),
      width: getWidth(),
      "data-testid": testId || "pagination-page-id",
      color,
      backgroundColor,
      colorScheme: pageNumber === -1 || isActive ? void 0 : "gray",
      children: pageNumber !== -1 ? pageNumber : "..."
    }
  );
};
var Page_default = Page;

// src/components/Pagination/index.tsx
import { Stack, Button as Button4, useTheme as useTheme55, Box as Box40 } from "@chakra-ui/react";
import { Fragment as Fragment21, jsx as jsx223, jsxs as jsxs120 } from "react/jsx-runtime";
var Pagination = ({
  currentPage,
  totalRecords,
  setPage,
  pageSize,
  maxNumberOfPagesToShow,
  nextText,
  previousText,
  backgroundColor
}) => {
  const chakraTheme2 = useTheme55();
  const handlePageClick = (index) => {
    window.scrollTo(0, 0);
    setPage(index);
  };
  const handlePreviousClick = () => {
    window.scrollTo(0, 0);
    setPage(currentPage - 1);
  };
  const handleNextClick = () => {
    window.scrollTo(0, 0);
    setPage(currentPage + 1);
  };
  const range = (start, stop, step) => {
    if (typeof stop === "undefined") {
      stop = start;
      start = 0;
    }
    if (typeof step === "undefined") {
      step = 1;
    }
    if (step > 0 && start >= stop || step < 0 && start <= stop) {
      return [];
    }
    const result = [];
    for (let i = start; step > 0 ? i < stop : i > stop; i += step) {
      result.push(i);
    }
    return result;
  };
  const numPages = Math.ceil(totalRecords / pageSize);
  let pagesStart;
  let pagesEnd;
  if (currentPage <= maxNumberOfPagesToShow) {
    pagesStart = 1;
  } else {
    pagesStart = maxNumberOfPagesToShow * Math.floor(currentPage / maxNumberOfPagesToShow);
  }
  if (numPages <= maxNumberOfPagesToShow) {
    pagesEnd = numPages + 1;
  } else {
    const pageSpanEnd = pagesStart + maxNumberOfPagesToShow;
    pagesEnd = pageSpanEnd <= numPages ? pageSpanEnd : numPages + 1;
  }
  const pagesArray = range(pagesStart, pagesEnd, 1);
  const pagesList = pagesArray.map(
    (number) => numPages !== number && /* @__PURE__ */ jsx223(
      Page_default,
      {
        testId: "page-element",
        onClickPage: handlePageClick,
        pageNumber: number,
        isActive: number === currentPage,
        color: number === currentPage ? "white" : "arcadiaMainGrey",
        backgroundColor: number === currentPage && (backgroundColor || chakraTheme2.colors.greenExplorer)
      },
      number
    )
  );
  return /* @__PURE__ */ jsxs120(Stack, { direction: "row", align: "center", "data-testid": "pagination-container", children: [
    numPages > 1 && currentPage > 1 && /* @__PURE__ */ jsx223(
      "div",
      {
        "data-testid": "pagination-previous-container",
        onClick: handlePreviousClick,
        children: /* @__PURE__ */ jsx223(
          Button4,
          {
            color: "arcadiaMainGrey",
            textStyle: "inter-semiBold-16-20",
            variant: "ghost",
            paddingLeft: 0,
            leftIcon: /* @__PURE__ */ jsx223(Box40, { margin: "0 10px", transform: "rotate(90deg)", children: /* @__PURE__ */ jsx223(AngleIcon_default2, { height: 12, width: 22 }) }),
            children: previousText
          }
        )
      }
    ),
    /* @__PURE__ */ jsxs120(Stack, { spacing: "10px", direction: "row", align: "center", children: [
      numPages > maxNumberOfPagesToShow ? /* @__PURE__ */ jsxs120(Fragment21, { children: [
        currentPage > maxNumberOfPagesToShow && /* @__PURE__ */ jsxs120(Fragment21, { children: [
          /* @__PURE__ */ jsx223(
            Page_default,
            {
              onClickPage: handlePageClick,
              pageNumber: 1,
              isActive: currentPage === 1,
              color: currentPage === 1 ? "white" : "arcadiaMainGrey"
            }
          ),
          /* @__PURE__ */ jsx223(
            Page_default,
            {
              pageNumber: -1,
              testId: "index-beginning-ellipsis",
              isActive: false,
              onClickPage: () => null
            }
          )
        ] }),
        pagesList,
        pagesList.length === maxNumberOfPagesToShow && /* @__PURE__ */ jsx223(Fragment21, { children: (_2.last(pagesArray) !== numPages || !pagesArray.includes(numPages)) && /* @__PURE__ */ jsx223(
          Page_default,
          {
            pageNumber: -1,
            testId: "index-ending-ellipsis",
            isActive: false,
            onClickPage: () => null
          }
        ) })
      ] }) : /* @__PURE__ */ jsx223(Fragment21, { children: pagesList }),
      /* @__PURE__ */ jsx223(
        Page_default,
        {
          testId: "page-element",
          onClickPage: handlePageClick,
          pageNumber: numPages,
          isActive: numPages === currentPage,
          color: numPages === currentPage ? "white" : "arcadiaMainGrey",
          backgroundColor: numPages === currentPage && (backgroundColor || chakraTheme2.colors.greenExplorer)
        }
      )
    ] }),
    numPages > 1 && currentPage !== numPages && /* @__PURE__ */ jsx223("div", { "data-testid": "pagination-next-container", onClick: handleNextClick, children: /* @__PURE__ */ jsx223(
      Button4,
      {
        color: "arcadiaMainGrey",
        textStyle: "inter-semiBold-16-20",
        paddingRight: 0,
        variant: "ghost",
        rightIcon: /* @__PURE__ */ jsx223(Box40, { margin: "0 10px", transform: "rotate(270deg)", children: /* @__PURE__ */ jsx223(AngleIcon_default2, { height: 12, width: 22 }) }),
        children: nextText
      }
    ) })
  ] });
};
var Pagination_default = Pagination;

// src/components/RadioGroupWithIcon/index.tsx
import {
  Flex as Flex20,
  Radio,
  RadioGroup,
  Stack as Stack2,
  Text as Text19,
  useTheme as useTheme56
} from "@chakra-ui/react";
import { useState as useState11 } from "react";
import { jsx as jsx224, jsxs as jsxs121 } from "react/jsx-runtime";
var RadioGroupWithIcon = ({
  options,
  handleSelection
}) => {
  const [value, setValue] = useState11(options?.[0].id);
  const theme = useTheme56();
  return /* @__PURE__ */ jsx224(
    RadioGroup,
    {
      onChange: (val) => {
        setValue(val);
        handleSelection(val);
      },
      value,
      children: /* @__PURE__ */ jsx224(Stack2, { direction: "row", children: options.map(({ title, description, id, icon: Icon }) => /* @__PURE__ */ jsxs121(
        Flex20,
        {
          flexDirection: "column",
          textAlign: "center",
          alignItems: "center",
          gap: 4,
          borderRadius: "7px",
          _hover: { boxShadow: theme.shadow?.md },
          p: 4,
          w: "50%",
          ...id === value ? {
            border: `1px solid ${theme.colors?.blueOne}`
          } : { border: `1px solid ${theme.colors?.lightGrey}` },
          children: [
            /* @__PURE__ */ jsx224(
              Icon,
              {
                ...id !== value ? { color: theme.colors?.lightGrey } : { color: theme.colors?.blueOne }
              }
            ),
            /* @__PURE__ */ jsx224(Radio, { value: id, checked: id === "zip", children: /* @__PURE__ */ jsx224(Text19, { textStyle: "semiBold-12-14", fontWeight: 500, children: title }) }),
            /* @__PURE__ */ jsx224(
              Text19,
              {
                textStyle: "semiBold-10-12",
                fontWeight: 500,
                color: theme.colors?.mediumGrey,
                children: description
              }
            )
          ]
        },
        id
      )) })
    }
  );
};
var RadioGroupWithIcon_default = RadioGroupWithIcon;

// src/components/ReportCard/index.tsx
import { Flex as Flex21, Text as Text20, useTheme as useTheme57 } from "@chakra-ui/react";
import { useEffect as useEffect10, useRef as useRef7, useState as useState12 } from "react";

// src/components/ReportCard/config.tsx
var DATA_SOURCE_TEXT = "DATA SOURCE:";
var ATTRIBUTION_METHODOLOGY_TEXT = "Attribution Methodology:";
var CONTENT_AREA_TEXT = "Content Area:";
var NO_ACCESS_TEXT = "To get access, please contact our member services team at support@arcadiasolutions.com";

// src/components/ReportCard/index.tsx
import { Fragment as Fragment22, jsx as jsx225, jsxs as jsxs122 } from "react/jsx-runtime";
var ReportCard = ({
  variant = "default",
  title,
  description,
  dataSource,
  customDataSourceIcon,
  attributionMethodology,
  contentArea,
  onClick,
  disabled = false,
  dataTestId = "report-card"
}) => {
  const theme = useTheme57();
  const titleRef = useRef7(null);
  const [attributionMethodologyTags, setAttributionMethodologyTags] = useState12(
    attributionMethodology || []
  );
  const descriptionRef = useRef7(null);
  const [showDescriptionTooltip, setShowDescriptionTooltip] = useState12(false);
  const attributionMethodologyContainerRef = useRef7(null);
  const [
    showAttributionMethodologyTooltip,
    setShowAttributionMethodologyTooltip
  ] = useState12(false);
  const NUMBER_ATTRIBUTION_METHODOLOGY = variant === "rectangular" ? 4 : 2;
  const textIsOverflowing = (textElement) => textElement.clientHeight - textElement.scrollHeight < 0;
  const getHiddenAttributionMethodologies = () => {
    if (attributionMethodology && attributionMethodology.length > NUMBER_ATTRIBUTION_METHODOLOGY) {
      const attributionMethodologyCopy = attributionMethodology ? [...attributionMethodology] : [];
      const hiddenAttributionMethodologies = attributionMethodologyCopy.splice(
        NUMBER_ATTRIBUTION_METHODOLOGY
      );
      setAttributionMethodologyTags([
        ...attributionMethodologyCopy,
        {
          label: `+${hiddenAttributionMethodologies.length}`,
          color: theme.colors?.blueThree
        }
      ]);
    }
  };
  useEffect10(() => {
    const descriptionText = descriptionRef?.current;
    setShowDescriptionTooltip(textIsOverflowing(descriptionText));
    const attributionMethodologyItemContainer = attributionMethodologyContainerRef?.current;
    if (attributionMethodology && attributionMethodology.length > NUMBER_ATTRIBUTION_METHODOLOGY || attributionMethodologyItemContainer && [...attributionMethodologyItemContainer?.children]?.some(
      (child) => textIsOverflowing(child)
    )) {
      getHiddenAttributionMethodologies();
      setShowAttributionMethodologyTooltip(true);
    }
  }, []);
  const hasMultipleContentArea = contentArea?.[1];
  const titleLines = titleRef.current?.offsetHeight && titleRef.current?.offsetHeight / 18;
  return /* @__PURE__ */ jsx225(
    Tooltip_default,
    {
      isDisabled: !disabled,
      hasArrow: true,
      label: /* @__PURE__ */ jsx225(
        Text20,
        {
          textStyle: "inter-regular-12-14",
          color: theme.colors.arcadiaMainGrey,
          children: NO_ACCESS_TEXT
        }
      ),
      placement: "top",
      bg: theme.colors?.white,
      color: theme.colors?.darkGrey2,
      children: /* @__PURE__ */ jsxs122(
        Flex21,
        {
          height: variant === "default" ? "204px" : "100%",
          justifyContent: "flex-start",
          alignItems: "center",
          p: "15px",
          w: variant === "default" ? "400px" : "100%",
          backgroundColor: disabled ? theme.colors.bgGrey2 : theme.colors?.white,
          boxShadow: "none",
          border: `solid 1px ${disabled ? theme.colors.bgGrey2 : theme.colors.lightGrey}`,
          borderRadius: "10px",
          gap: "15px",
          "data-testid": dataTestId,
          flexWrap: "wrap",
          cursor: disabled ? "not-allowed" : "pointer",
          onClick: disabled ? void 0 : onClick,
          opacity: disabled ? 0.6 : 1,
          _hover: {
            border: disabled ? `solid 1px ${theme.colors.bgGrey2}` : `solid 1px ${theme.colors.darkBlue}`,
            transition: "all ease 0.2s"
          },
          children: [
            /* @__PURE__ */ jsxs122(
              Flex21,
              {
                direction: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                w: variant === "rectangular" ? "48%" : "100%",
                "data-testid": "report-card--first-section",
                gap: "12px",
                children: [
                  /* @__PURE__ */ jsx225(Flex21, { w: "100%", children: /* @__PURE__ */ jsx225(
                    Text20,
                    {
                      ref: titleRef,
                      textStyle: "inter-medium-14-17",
                      color: theme.colors?.darkBlue,
                      "data-testid": "report-card--title",
                      children: title
                    }
                  ) }),
                  /* @__PURE__ */ jsx225(
                    Tooltip_default,
                    {
                      isDisabled: !showDescriptionTooltip,
                      hasArrow: true,
                      maxWidth: "360px",
                      label: /* @__PURE__ */ jsx225(
                        Text20,
                        {
                          "data-testid": "report-card--description-tooltip",
                          textStyle: "inter-regular-12-16",
                          color: theme.colors.arcadiaMainGrey,
                          children: description
                        }
                      ),
                      placement: "right",
                      openDelay: 500,
                      bg: theme.colors?.white,
                      color: theme.colors?.darkGrey2,
                      children: /* @__PURE__ */ jsx225(
                        Text20,
                        {
                          ref: descriptionRef,
                          textStyle: "inter-regular-12-16",
                          color: theme.colors.arcadiaMainGrey,
                          noOfLines: titleLines === 1 ? 3 : 2,
                          w: "100%",
                          "data-testid": "report-card--description",
                          children: description
                        }
                      )
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsxs122(
              Flex21,
              {
                direction: "column",
                justifyContent: "center",
                alignItems: "flex-start",
                w: variant === "rectangular" ? "50%" : "100%",
                borderRadius: "6px",
                backgroundColor: theme.colors?.bgGrey2,
                p: "5px 10px",
                gap: "10px",
                "data-testid": "report-card--second-section",
                children: [
                  dataSource && dataSource?.length && /* @__PURE__ */ jsxs122(Flex21, { w: "100%", justifyContent: "flex-start", alignItems: "center", children: [
                    /* @__PURE__ */ jsx225(
                      Text20,
                      {
                        textStyle: "inter-bold-10-10",
                        textTransform: "uppercase",
                        color: theme.colors.arcadiaMainGrey,
                        "data-testid": "report-card--data-source-label",
                        children: DATA_SOURCE_TEXT
                      }
                    ),
                    customDataSourceIcon || /* @__PURE__ */ jsx225(DataSourcesIcon_default, { margin: "0 0 0 5px", cursor: "pointer" }),
                    /* @__PURE__ */ jsx225(
                      Text20,
                      {
                        marginLeft: "5px",
                        textStyle: "inter-regular-12-14",
                        color: theme.colors.arcadiaMainGrey,
                        "data-testid": "report-card--data-source-text",
                        children: dataSource
                      }
                    )
                  ] }),
                  !!attributionMethodologyTags?.length && /* @__PURE__ */ jsxs122(Flex21, { w: "100%", justifyContent: "flex-start", alignItems: "center", children: [
                    /* @__PURE__ */ jsx225(
                      Text20,
                      {
                        noOfLines: 1,
                        textStyle: "inter-bold-10-10",
                        textTransform: "uppercase",
                        color: theme.colors.arcadiaMainGrey,
                        "data-testid": "report-card--attribution-methodology-label",
                        display: "flex",
                        whiteSpace: "nowrap",
                        ...variant !== "rectangular" && { width: "fit-content" },
                        children: ATTRIBUTION_METHODOLOGY_TEXT
                      }
                    ),
                    /* @__PURE__ */ jsx225(
                      Tooltip_default,
                      {
                        isDisabled: !showAttributionMethodologyTooltip,
                        hasArrow: true,
                        maxWidth: "360px",
                        label: /* @__PURE__ */ jsx225(
                          Text20,
                          {
                            textStyle: "inter-regular-12-14",
                            color: theme.colors.arcadiaMainGrey,
                            "data-testid": "report-card--content-area-text-tooltip",
                            children: attributionMethodology?.map(({ label }) => label).join(", ")
                          }
                        ),
                        placement: "right",
                        openDelay: 500,
                        bg: theme.colors?.white,
                        color: theme.colors?.darkGrey2,
                        children: /* @__PURE__ */ jsx225(
                          Flex21,
                          {
                            marginLeft: "5px",
                            ...variant !== "rectangular" && {
                              maxWidth: "195px",
                              w: "100%"
                            },
                            gap: "10px",
                            "data-testid": "report-card--attribution-methodology-badge-container",
                            ref: attributionMethodologyContainerRef,
                            children: attributionMethodologyTags.length ? attributionMethodologyTags.map(
                              ({ label, color }, index) => /* @__PURE__ */ jsx225(
                                Text20,
                                {
                                  noOfLines: 1,
                                  textStyle: "inter-bold-10-10",
                                  backgroundColor: theme.colors?.tableHeaderBlue || color,
                                  textTransform: "uppercase",
                                  borderRadius: "3px",
                                  p: "2px 7px",
                                  color: theme.colors.blueOne,
                                  "data-testid": "report-card--attribution-methodology-badge",
                                  children: label
                                },
                                index + title
                              )
                            ) : /* @__PURE__ */ jsx225(Fragment22, {})
                          }
                        )
                      }
                    )
                  ] }),
                  contentArea?.length && /* @__PURE__ */ jsxs122(Flex21, { w: "100%", justifyContent: "flex-start", alignItems: "center", children: [
                    /* @__PURE__ */ jsx225(
                      Text20,
                      {
                        ...variant !== "rectangular" && { width: "106px" },
                        whiteSpace: "nowrap",
                        textStyle: "inter-bold-10-10",
                        textTransform: "uppercase",
                        color: theme.colors.arcadiaMainGrey,
                        "data-testid": "report-card--content-area-label",
                        children: CONTENT_AREA_TEXT
                      }
                    ),
                    /* @__PURE__ */ jsx225(
                      Tooltip_default,
                      {
                        isDisabled: hasMultipleContentArea ? contentArea.length < 3 : contentArea[0].split(", ").length < 3,
                        hasArrow: true,
                        maxWidth: "360px",
                        label: /* @__PURE__ */ jsx225(
                          Text20,
                          {
                            textStyle: "inter-regular-12-14",
                            color: theme.colors.arcadiaMainGrey,
                            "data-testid": "report-card--content-area-text-tooltip",
                            children: hasMultipleContentArea ? contentArea.join(", ") : contentArea
                          }
                        ),
                        placement: "right",
                        openDelay: 500,
                        bg: theme.colors?.white,
                        color: theme.colors?.darkGrey2,
                        children: /* @__PURE__ */ jsx225(
                          Text20,
                          {
                            w: "100%",
                            textStyle: "inter-regular-12-14",
                            color: theme.colors.arcadiaMainGrey,
                            noOfLines: 1,
                            marginLeft: "5px",
                            "data-testid": "report-card--content-area-text",
                            children: contentArea.join(" \u2022 ")
                          }
                        )
                      }
                    )
                  ] })
                ]
              }
            )
          ]
        }
      )
    }
  );
};
var ReportCard_default = ReportCard;

// src/components/SimpleRadialBarChart/index.tsx
import { Fragment as Fragment23 } from "react";
import { ResponsiveRadialBar } from "@nivo/radial-bar";
import { useTheme as useTheme59 } from "@chakra-ui/react";

// src/components/SimpleRadialBarChart/PercentageLabel/index.tsx
import { useTheme as useTheme58 } from "@chakra-ui/react";
import { jsxs as jsxs123 } from "react/jsx-runtime";
var PercentageLabel = ({ outerRadius, bars }) => {
  const chakraTheme2 = useTheme58();
  const [secondaryBar, mainBar] = bars;
  const fontSize = Math.min(18, outerRadius / 5.5);
  const textSize = secondaryBar.value < 100 ? secondaryBar.value < 10 ? 20 : 28 : 36;
  return /* @__PURE__ */ jsxs123("g", { children: [
    /* @__PURE__ */ jsxs123(
      "text",
      {
        x: 1.5 * outerRadius - textSize,
        y: outerRadius + 20,
        style: {
          fontFamily: chakraTheme2.fonts.bold,
          fill: secondaryBar.color,
          fontSize
        },
        "data-testid": "simple-rad-bar--label-text-b",
        children: [
          secondaryBar.value,
          "%"
        ]
      }
    ),
    /* @__PURE__ */ jsxs123(
      "text",
      {
        x: 2 * outerRadius + 5,
        y: outerRadius + 20,
        style: {
          fontFamily: chakraTheme2.fonts.bold,
          fill: mainBar.color,
          fontSize
        },
        "data-testid": "simple-rad-bar--label-text-a",
        children: [
          mainBar.value,
          "%"
        ]
      }
    )
  ] });
};
var PercentageLabel_default = PercentageLabel;

// src/components/SimpleRadialBarChart/index.tsx
import { jsx as jsx226 } from "react/jsx-runtime";
var getData = (mainValue, secondaryValue) => [
  {
    id: "A",
    data: [
      {
        x: "A",
        y: secondaryValue
      }
    ]
  },
  {
    id: "B",
    data: [
      {
        x: "B",
        y: mainValue
      }
    ]
  }
];
var SimpleRadialBarChart = ({
  mainValue,
  secondaryValue,
  mainColor,
  secondaryColor
}) => {
  const chakraTheme2 = useTheme59();
  const data = getData(mainValue, secondaryValue);
  mainColor || (mainColor = chakraTheme2.colors.blueTwo);
  secondaryColor || (secondaryColor = chakraTheme2.colors.gold);
  return /* @__PURE__ */ jsx226(
    ResponsiveRadialBar,
    {
      data,
      valueFormat: ">-.2f",
      endAngle: 360,
      innerRadius: 0.6,
      padding: 0,
      cornerRadius: 12,
      maxValue: 100,
      margin: { top: 10, right: 40, bottom: 10, left: 10 },
      enableTracks: false,
      enableRadialGrid: false,
      enableCircularGrid: false,
      circularAxisOuter: null,
      radialAxisStart: null,
      tooltip: () => /* @__PURE__ */ jsx226(Fragment23, {}),
      colors: [secondaryColor || "", mainColor || ""],
      layers: ["grid", "bars", PercentageLabel_default]
    }
  );
};
var SimpleRadialBarChart_default = SimpleRadialBarChart;

// src/components/ChipsSkeleton/index.tsx
import { Flex as Flex22, Skeleton as Skeleton2 } from "@chakra-ui/react";
import { jsx as jsx227 } from "react/jsx-runtime";
var ChipsSkeleton = ({
  numberOfChips = 2,
  chipsWidth = "90px",
  chipsHeight = "18px",
  dataTestId = "chips-skeleton-container"
}) => {
  return /* @__PURE__ */ jsx227(Flex22, { width: "fit-content", gap: "5px", "data-testid": dataTestId, children: [...Array(numberOfChips)].map((_chip, i) => /* @__PURE__ */ jsx227(
    Skeleton2,
    {
      "data-testid": "skeleton-chip",
      height: chipsHeight,
      width: chipsWidth,
      borderRadius: "9999px"
    },
    `chip-skeleton-${i}`
  )) });
};
var ChipsSkeleton_default = ChipsSkeleton;

// src/components/SwitchCheckbox/ArrowIcon/index.tsx
import { Box as Box41, keyframes } from "@chakra-ui/react";
import { Fragment as Fragment24, jsx as jsx228, jsxs as jsxs124 } from "react/jsx-runtime";
var DEFAULT_COLOR = "#222B45";
var ArrowIcon2 = ({
  checked,
  arrowColor = DEFAULT_COLOR,
  arrowColorChecked = DEFAULT_COLOR,
  heigth = 10,
  width = 10
}) => {
  const animationArrowRight = keyframes`
    from {right: 40%;}
    to {right: 5px;}
  `;
  const animationArrowLeft = keyframes`
    from {left: 40%;}
    to {left: 5px;}
`;
  return /* @__PURE__ */ jsx228(Fragment24, { children: checked ? /* @__PURE__ */ jsxs124(
    Box41,
    {
      as: "svg",
      position: "absolute",
      zIndex: 1,
      right: "5px",
      animation: `${animationArrowRight} 0.3s`,
      width,
      height: heigth,
      viewBox: "0 0 10 10",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": "arrow-right",
      children: [
        /* @__PURE__ */ jsx228(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M4.16646 7.91664C4.07229 7.91664 3.97771 7.88497 3.89979 7.81997C3.72312 7.67289 3.69937 7.40997 3.84646 7.2333L5.71146 4.99539L3.91354 2.76122C3.76937 2.58205 3.79771 2.31955 3.97687 2.17539C4.15646 2.03122 4.41854 2.05955 4.56312 2.23872L6.57479 4.73872C6.69896 4.8933 6.69729 5.11414 6.57021 5.26664L4.48687 7.76664C4.40437 7.86539 4.28604 7.91664 4.16646 7.91664Z",
            fill: arrowColorChecked,
            "data-testid": "arrow-right-path"
          }
        ),
        /* @__PURE__ */ jsx228(
          "mask",
          {
            id: "mask0",
            maskUnits: "userSpaceOnUse",
            x: "3",
            y: "2",
            width: "4",
            height: "6",
            children: /* @__PURE__ */ jsx228(
              "path",
              {
                fillRule: "evenodd",
                clipRule: "evenodd",
                d: "M4.16646 7.91664C4.07229 7.91664 3.97771 7.88497 3.89979 7.81997C3.72312 7.67289 3.69937 7.40997 3.84646 7.2333L5.71146 4.99539L3.91354 2.76122C3.76937 2.58205 3.79771 2.31955 3.97687 2.17539C4.15646 2.03122 4.41854 2.05955 4.56312 2.23872L6.57479 4.73872C6.69896 4.8933 6.69729 5.11414 6.57021 5.26664L4.48687 7.76664C4.40437 7.86539 4.28604 7.91664 4.16646 7.91664Z",
                fill: "white"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx228("g", { mask: "url(#mask0)" })
      ]
    }
  ) : /* @__PURE__ */ jsxs124(
    Box41,
    {
      as: "svg",
      position: "absolute",
      zIndex: 1,
      left: "5px",
      animation: `${animationArrowLeft} 0.3s`,
      width,
      height: heigth,
      viewBox: "0 0 10 10",
      fill: "none",
      xmlns: "http://www.w3.org/2000/svg",
      "data-testid": "arrow-left",
      children: [
        /* @__PURE__ */ jsx228(
          "path",
          {
            fillRule: "evenodd",
            clipRule: "evenodd",
            d: "M5.83354 7.91664C5.92771 7.91664 6.02229 7.88497 6.10021 7.81997C6.27688 7.67289 6.30063 7.40997 6.15354 7.2333L4.28854 4.99539L6.08646 2.76122C6.23063 2.58205 6.20229 2.31955 6.02313 2.17539C5.84354 2.03122 5.58146 2.05955 5.43688 2.23872L3.42521 4.73872C3.30104 4.8933 3.30271 5.11414 3.42979 5.26664L5.51313 7.76664C5.59563 7.86539 5.71396 7.91664 5.83354 7.91664Z",
            fill: arrowColor,
            "data-testid": "arrow-left-path"
          }
        ),
        /* @__PURE__ */ jsx228(
          "mask",
          {
            id: "mask0",
            "mask-type": "alpha",
            maskUnits: "userSpaceOnUse",
            x: "3",
            y: "2",
            width: "4",
            height: "6",
            children: /* @__PURE__ */ jsx228(
              "path",
              {
                fillRule: "evenodd",
                clipRule: "evenodd",
                d: "M5.83354 7.91664C5.92771 7.91664 6.02229 7.88497 6.10021 7.81997C6.27688 7.67289 6.30063 7.40997 6.15354 7.2333L4.28854 4.99539L6.08646 2.76122C6.23063 2.58205 6.20229 2.31955 6.02313 2.17539C5.84354 2.03122 5.58146 2.05955 5.43688 2.23872L3.42521 4.73872C3.30104 4.8933 3.30271 5.11414 3.42979 5.26664L5.51313 7.76664C5.59563 7.86539 5.71396 7.91664 5.83354 7.91664Z",
                fill: "white"
              }
            )
          }
        ),
        /* @__PURE__ */ jsx228("g", { mask: "url(#mask0)" })
      ]
    }
  ) });
};
var ArrowIcon_default2 = ArrowIcon2;

// src/components/SwitchCheckbox/SwitchCheckbox.tsx
import { FormControl, FormLabel, Input as Input2, useTheme as useTheme60 } from "@chakra-ui/react";
import { jsx as jsx229, jsxs as jsxs125 } from "react/jsx-runtime";
var SwitchCheckbox = ({
  checked,
  handleChange,
  id,
  withArrow,
  arrowColor,
  arrowColorChecked,
  circleColor,
  checkedColor,
  uncheckedColor,
  dataTestId,
  ...styles
}) => {
  const chakraTheme2 = useTheme60();
  const DEFAULT_COLOR2 = "#222B45";
  const { height, width } = styles;
  return /* @__PURE__ */ jsxs125(
    FormControl,
    {
      width: "auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      children: [
        /* @__PURE__ */ jsx229(
          Input2,
          {
            type: "checkbox",
            id,
            checked,
            onChange: handleChange,
            "data-testid": dataTestId || "switch-checkbox-id",
            height: 0,
            width: 0,
            margin: 0,
            border: 0,
            padding: 0,
            visibility: "hidden"
          }
        ),
        /* @__PURE__ */ jsx229(
          FormLabel,
          {
            htmlFor: id,
            background: checked ? checkedColor || chakraTheme2.colors.blueTwo : uncheckedColor || chakraTheme2.colors.lightGrey,
            "data-testid": checked ? "switch-checkbox-active" : "switch-checkbox-inactive",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            borderRadius: "100px",
            position: "relative",
            margin: 0,
            _after: {
              content: '""',
              position: "absolute",
              top: "1px",
              left: "2px",
              width: width ? `${width / 2}px` : "21px",
              height: height ? `${height - 2}px` : "18px",
              borderRadius: "90px",
              transition: "0.3s",
              background: circleColor || chakraTheme2.colors.white,
              transform: checked ? width ? `translateX(${width / 2 - 3}px)` : "translateX(19px)" : "initial"
            },
            ...styles,
            width: styles.width || width ? `${width}px` : "44px",
            height: styles.height || height ? `${height}px` : "20px",
            children: withArrow && /* @__PURE__ */ jsx229(
              ArrowIcon_default2,
              {
                checked,
                arrowColor: arrowColor || DEFAULT_COLOR2,
                arrowColorChecked: arrowColorChecked || arrowColor || DEFAULT_COLOR2,
                heigth: height ? height / 4 : 10,
                width: width ? width / 4 : 10
              }
            )
          }
        )
      ]
    }
  );
};
var SwitchCheckbox_default = SwitchCheckbox;

// src/components/HomeTile/HomeTile.tsx
import { useState as useState13, cloneElement } from "react";
import { Box as Box42, Flex as Flex23, Tag as Tag3, Text as Text21, useTheme as useTheme61 } from "@chakra-ui/react";
import { jsx as jsx230, jsxs as jsxs126 } from "react/jsx-runtime";
var HomeTile = ({
  title,
  description,
  tags,
  color,
  icon,
  height,
  handleClick,
  width = "362px",
  dataTestId = "home-tile",
  ctaText,
  isNewDesign,
  module,
  isDisabled,
  tagBackgroundColor
}) => {
  const [isHovered, setIsHovered] = useState13(false);
  const theme = useTheme61();
  const showDisabledTooltip = isDisabled && isHovered && isNewDesign;
  const showCta = !isDisabled && isNewDesign && isHovered || !isNewDesign && isHovered;
  const borderWidth = "2px";
  return /* @__PURE__ */ jsxs126(
    Flex23,
    {
      position: "relative",
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => setIsHovered(false),
      onClick: () => handleClick(),
      width,
      height,
      minHeight: "123px",
      alignItems: "flex-start",
      flexDirection: "column",
      justifyContent: "space-between",
      padding: "12px",
      borderRadius: "6px",
      transition: "all ease 0.1s",
      background: theme.colors?.white,
      border: `${borderWidth} solid ${theme.colors.lightGrey}`,
      _hover: {
        border: isDisabled && isNewDesign ? "" : `${borderWidth} solid ${color}`
      },
      cursor: "pointer",
      boxShadow: "none",
      "data-testid": dataTestId,
      boxSizing: "border-box",
      children: [
        showDisabledTooltip && /* @__PURE__ */ jsx230(
          Flex23,
          {
            position: "absolute",
            inset: "0",
            alignItems: "center",
            justifyContent: "center",
            css: { backdropFilter: "blur(3px)" },
            children: /* @__PURE__ */ jsxs126(
              Flex23,
              {
                flexDirection: "column",
                "data-testid": "no-access-popup",
                alignItems: "center",
                padding: "15px",
                borderRadius: "10px",
                maxWidth: "225px",
                backgroundColor: theme.colors.white,
                border: "1px solid",
                borderColor: theme.colors.lightGrey,
                boxShadow: "none",
                children: [
                  /* @__PURE__ */ jsx230(
                    LockIcon_default,
                    {
                      color: theme.colors.lightGrey,
                      height: 26,
                      width: 26,
                      margin: "5px 0"
                    }
                  ),
                  /* @__PURE__ */ jsx230(
                    Text21,
                    {
                      textStyle: "inter-medium-12-14",
                      marginBottom: "10px",
                      textAlign: "center",
                      children: "We are sorry, but you don\u2019t have access to this module"
                    }
                  ),
                  /* @__PURE__ */ jsx230(Text21, { textStyle: "inter-regular-10-10", textAlign: "center", children: "Please contact CareJourney's Member Services team to learn how to get access." })
                ]
              }
            )
          }
        ),
        /* @__PURE__ */ jsxs126(
          Flex23,
          {
            w: "100%",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "column",
            children: [
              /* @__PURE__ */ jsxs126(
                Flex23,
                {
                  w: "100%",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "6px",
                  children: [
                    /* @__PURE__ */ jsxs126(Flex23, { alignItems: "center", gap: "10px", children: [
                      /* @__PURE__ */ jsx230(Box42, { css: showDisabledTooltip ? { filter: "blur(3px)" } : {}, children: icon ? cloneElement(icon, {
                        color: isDisabled && isNewDesign ? theme.colors?.mediumGrey : color
                      }) : null }),
                      isNewDesign && module && /* @__PURE__ */ jsx230(
                        Text21,
                        {
                          textStyle: "inter-regular-10-10",
                          color: isDisabled ? theme.colors?.lightGrey : color || theme.colors.gold,
                          children: module.toUpperCase()
                        }
                      )
                    ] }),
                    showCta && /* @__PURE__ */ jsxs126(
                      Flex23,
                      {
                        zIndex: 2340,
                        alignItems: "center",
                        color: color || theme.colors?.blueOne,
                        children: [
                          /* @__PURE__ */ jsx230(
                            Text21,
                            {
                              "data-testid": "cta-text",
                              marginRight: "8px",
                              textStyle: "inter-medium-12-14",
                              children: ctaText
                            }
                          ),
                          /* @__PURE__ */ jsx230(
                            ArrowIcon_default,
                            {
                              width: 12,
                              height: 12,
                              direction: "right",
                              color: color || theme.colors?.blueOne
                            }
                          )
                        ]
                      }
                    )
                  ]
                }
              ),
              /* @__PURE__ */ jsxs126(Box42, { w: "100%", children: [
                /* @__PURE__ */ jsx230(Box42, { marginBottom: "8px", "data-testid": "tile-title", children: /* @__PURE__ */ jsx230(
                  Text21,
                  {
                    "data-testid": "tile-title",
                    color: isDisabled && isNewDesign ? theme.colors.lightGrey : color || theme.colors.gold,
                    textStyle: "inter-bold-16-20",
                    children: title
                  }
                ) }),
                /* @__PURE__ */ jsx230(Box42, { width: "100%", "data-testid": "tile-description", children: /* @__PURE__ */ jsx230(
                  Text21,
                  {
                    textStyle: "inter-regular-12-14",
                    color: theme.colors?.mediumGrey,
                    children: description
                  }
                ) })
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx230(Flex23, { flexDirection: "row", gap: "5px", flexWrap: "wrap", marginTop: "10px", children: !!tags?.length && tags.map((tag, i) => /* @__PURE__ */ jsx230(
          Tag3,
          {
            textTransform: "uppercase",
            borderRadius: "3px",
            size: "sm",
            backgroundColor: isDisabled && isNewDesign ? theme.colors.lightGrey : !isDisabled && isNewDesign ? tagBackgroundColor : color || theme.colors.gold,
            "data-testid": "tag-element",
            children: /* @__PURE__ */ jsx230(
              Text21,
              {
                textStyle: "inter-semiBold-10-10",
                color: isDisabled ? theme.colors?.white : color,
                children: tag
              }
            )
          },
          `tag-${i}`
        )) })
      ]
    }
  );
};
var HomeTile_default = HomeTile;

// src/components/TabNav/TabNav.tsx
import {
  Tabs,
  TabList,
  Tab,
  TabPanel,
  TabPanels,
  useTheme as useTheme62,
  Box as Box43,
  Text as Text22,
  Flex as Flex24
} from "@chakra-ui/react";

// src/components/TabNav/config.ts
var TAB_INDEX_FALLBACK = 0;

// src/components/TabNav/TabNav.tsx
import { Fragment as Fragment25, jsx as jsx231, jsxs as jsxs127 } from "react/jsx-runtime";
var TabNav = ({
  tabs,
  expandTabs,
  content,
  selectedTab = 0,
  setSelectedTab,
  NOT_APPLICABLE_TEXT = "N/A",
  variant = "primary",
  justifyContent,
  dataTestId = "tabnav--container",
  disabledTabIndexes = [],
  tabFontSize = 16,
  secondaryLabelFontSize = 12
}) => {
  const chakraTheme2 = useTheme62();
  const customStyles = {
    _hover: {},
    cursor: "normal"
  };
  const tabFontColor = chakraTheme2.colors.arcadiaMainGrey;
  return /* @__PURE__ */ jsxs127(
    Tabs,
    {
      "data-testid": dataTestId,
      index: selectedTab || TAB_INDEX_FALLBACK,
      onChange: (index) => setSelectedTab(index),
      variant,
      isLazy: true,
      children: [
        /* @__PURE__ */ jsx231(
          TabList,
          {
            "data-testid": "tab-list--container",
            ...justifyContent ? { justifyContent } : {},
            children: tabs.map((tab) => /* @__PURE__ */ jsx231(
              Tab,
              {
                sx: disabledTabIndexes.includes(tab.index) ? customStyles : {},
                isDisabled: disabledTabIndexes.includes(tab.index),
                "data-testid": "tab--container",
                ...expandTabs ? {
                  minWidth: "fit-content",
                  justifyContent: "center",
                  padding: "5px 20px"
                } : {},
                color: disabledTabIndexes.includes(tab.index) ? chakraTheme2?.colors?.lightGrey : tabFontColor,
                children: tab.tooltipText ? /* @__PURE__ */ jsx231(
                  Tooltip_default,
                  {
                    label: /* @__PURE__ */ jsx231(Fragment25, { children: /* @__PURE__ */ jsx231(Box43, { children: /* @__PURE__ */ jsx231(
                      Text22,
                      {
                        textStyle: "inter-semiBold-12-14",
                        whiteSpace: "pre-wrap",
                        children: tab.tooltipText
                      }
                    ) }) }),
                    placement: "top",
                    bg: chakraTheme2.colors?.smoky,
                    dataTestId: "year-disclaimer--tooltip",
                    children: /* @__PURE__ */ jsx231(Text22, { fontSize: `${tabFontSize}px`, children: tab.label })
                  }
                ) : /* @__PURE__ */ jsxs127(Flex24, { alignItems: "center", gap: "4px", children: [
                  /* @__PURE__ */ jsx231(Text22, { fontSize: `${tabFontSize}px`, children: tab.label }),
                  /* @__PURE__ */ jsx231(
                    Text22,
                    {
                      textStyle: "inter-regular-12-14",
                      fontSize: `${secondaryLabelFontSize}px`,
                      children: tab.secondaryLabel && tab.secondaryLabel
                    }
                  )
                ] })
              },
              tab.index
            ))
          }
        ),
        /* @__PURE__ */ jsx231(TabPanels, { "data-testid": "tabnav--panels", children: tabs.map((tab) => /* @__PURE__ */ jsx231(TabPanel, { padding: "0", children: content[tab.index] || NOT_APPLICABLE_TEXT }, tab.index)) })
      ]
    }
  );
};
var TabNav_default = TabNav;

// src/components/TrendLine/index.tsx
import { ResponsiveLine } from "@nivo/line";
import { Box as Box44 } from "@chakra-ui/react";

// src/components/TrendLine/Label/Label.tsx
import { useTheme as useTheme63 } from "@chakra-ui/react";
import { Fragment as Fragment26, jsx as jsx232 } from "react/jsx-runtime";
var Label2 = (yearsWithLabel, metricsWithLabel = [], metricsWithLabelAreDollars = [], pointsMaximumFractionDigits, pointsMinimumFractionDigits, style) => (props) => {
  const labelYMargin = 10;
  const theme = useTheme63();
  const labelStyle = {
    fontFamily: theme.fonts.semiBold,
    fill: theme.colors.mediumGrey,
    fontSize: 10,
    ...style
  };
  return /* @__PURE__ */ jsx232(Fragment26, { children: props.points.map(({ id, data, x, y }, i) => {
    const dataYear = data?.x?.toString();
    const pointMatchWithYear = Array.isArray(yearsWithLabel) ? yearsWithLabel.includes(dataYear) : dataYear === yearsWithLabel?.toString();
    const pointMatchWithMetric = !metricsWithLabel.length || metricsWithLabel.includes(id.split(".")[0]);
    const pointIsDollar = metricsWithLabelAreDollars.includes(
      id.split(".")[0]
    );
    if (pointMatchWithYear && pointMatchWithMetric) {
      const textValue = `${pointIsDollar ? "$" : ""}${Number(
        data.y
      ).toLocaleString(void 0, {
        maximumFractionDigits: pointsMaximumFractionDigits,
        minimumFractionDigits: pointsMinimumFractionDigits
      })}`;
      return /* @__PURE__ */ jsx232(Fragment26, { children: /* @__PURE__ */ jsx232(
        "g",
        {
          transform: `translate(${x}, ${y - labelYMargin})`,
          width: 50,
          children: /* @__PURE__ */ jsx232(
            "text",
            {
              x: 0,
              y: 0,
              textAnchor: "middle",
              alignmentBaseline: "central",
              style: labelStyle,
              children: textValue
            }
          )
        },
        `${data.x}-${i}`
      ) });
    }
    return null;
  }) });
};
var Label_default2 = Label2;

// src/components/TrendLine/VariationLabel/VariationLabel.tsx
import { useTheme as useTheme64 } from "@chakra-ui/react";
import { Fragment as Fragment27, jsx as jsx233, jsxs as jsxs128 } from "react/jsx-runtime";
var VariationLabel = (yearsWithVariationLabel, metricsWithLabel = []) => (props) => {
  const labelYMargin = 40;
  const theme = useTheme64();
  const labelStyle = {
    fontFamily: theme.fonts.bold,
    fill: "white",
    fontSize: 12
  };
  const percentageFormatter = (number, option) => {
    const defaultOption = {
      style: "percent",
      signDisplay: "auto",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    };
    const formatter = new Intl.NumberFormat("en-US", option || defaultOption);
    const percentageFormatted = formatter.format(number);
    return percentageFormatted;
  };
  return /* @__PURE__ */ jsx233(Fragment27, { children: props.points.map(({ id, data, x, y }, i) => {
    const dataYear = data?.x?.toString();
    const pointMatchWithYear = Array.isArray(yearsWithVariationLabel) ? yearsWithVariationLabel.includes(dataYear) : dataYear === yearsWithVariationLabel?.toString();
    const pointMatchWithMetric = !metricsWithLabel.length || metricsWithLabel.includes(id.split(".")[0]);
    if (pointMatchWithYear && pointMatchWithMetric) {
      const textValue = data.variation ? `${percentageFormatter(data.variation / 100, {
        style: "percent",
        signDisplay: "always",
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      })}` : "";
      const isGreaterThanZero = Boolean(
        data.variation && data.variation > 0
      );
      const greaterThanZeroColor = "#39C671";
      const lowerThanZeroColor = "#AE2824";
      const selectedColor = isGreaterThanZero ? greaterThanZeroColor : lowerThanZeroColor;
      return /* @__PURE__ */ jsx233(Fragment27, { children: /* @__PURE__ */ jsxs128(
        "g",
        {
          transform: `translate(${x}, ${y - labelYMargin})`,
          width: 50,
          children: [
            /* @__PURE__ */ jsx233(
              "rect",
              {
                width: "49",
                height: "18",
                x: "-25",
                y: "-8",
                rx: "5",
                ry: "5",
                fill: selectedColor
              }
            ),
            /* @__PURE__ */ jsx233(
              "text",
              {
                x: 0,
                y: 0,
                textAnchor: "middle",
                alignmentBaseline: "central",
                style: labelStyle,
                children: textValue
              }
            )
          ]
        },
        `${data.x}-${i}`
      ) });
    }
    return null;
  }) });
};
var VariationLabel_default = VariationLabel;

// src/components/TrendLine/CJAxisBottom/CJAxisBottom.tsx
import { useTheme as useTheme65 } from "@chakra-ui/react";
import { Fragment as Fragment28, jsx as jsx234, jsxs as jsxs129 } from "react/jsx-runtime";
var CJAxisBottom = (props) => {
  const theme = useTheme65();
  const { highLightedValue, padding: cjAxisPadding } = props.cjAxisBottom;
  const valueSelected = highLightedValue?.value?.toString();
  const findValueSelected = props.points.find(({ data }) => {
    return data.x?.toString() === valueSelected;
  });
  const filteredPoints = props.points.reduce(
    (filteredPoints2, currPoint) => {
      if (!filteredPoints2.some((p) => p.data.x === currPoint.data.x)) {
        filteredPoints2.push(currPoint);
      }
      return filteredPoints2;
    },
    []
  );
  return /* @__PURE__ */ jsxs129(Fragment28, { children: [
    /* @__PURE__ */ jsxs129(
      "g",
      {
        transform: `translate(0, ${props.innerHeight + (cjAxisPadding || 0)})`,
        "data-testid": "custom-axis-bottom-border-line",
        children: [
          /* @__PURE__ */ jsx234(
            "line",
            {
              opacity: "1",
              x1: 0,
              x2: props.innerWidth,
              y1: 0,
              y2: 0,
              stroke: "#dddddd",
              strokeWidth: "1"
            }
          ),
          props.cjAxisBottom?.showLabel && /* @__PURE__ */ jsx234("g", { transform: "translate(0,15)", children: filteredPoints.map(({ data, x }, i) => {
            const textValue = Number(data.x);
            const pointMatchWithValue = textValue?.toString() === valueSelected;
            const svgWidth = 40;
            const svgHeight = 20;
            return /* @__PURE__ */ jsxs129(
              "svg",
              {
                x: x - svgWidth / 2,
                y: 0 - svgHeight / 2,
                height: 20,
                width: 40,
                viewBox: "0 0 40 20",
                style: {
                  overflow: "visible"
                },
                children: [
                  pointMatchWithValue && highLightedValue?.backgroundColor && /* @__PURE__ */ jsx234(
                    "rect",
                    {
                      x: 0,
                      y: 0,
                      width: "100%",
                      height: "100%",
                      fill: highLightedValue?.backgroundColor,
                      rx: 3
                    }
                  ),
                  /* @__PURE__ */ jsx234(
                    "text",
                    {
                      x: "50%",
                      y: "50%",
                      textAnchor: "middle",
                      alignmentBaseline: "central",
                      style: {
                        fontFamily: pointMatchWithValue ? theme.fonts.bold : theme.fonts.regular,
                        fill: theme.colors.mediumGrey,
                        fontSize: 10
                      },
                      children: textValue
                    }
                  )
                ]
              },
              `${data.x}-${i}`
            );
          }) })
        ]
      }
    ),
    findValueSelected && highLightedValue?.showMesh && /* @__PURE__ */ jsx234(
      "rect",
      {
        x: Number(findValueSelected?.x) - 20,
        y: 0,
        width: 40,
        height: props.innerHeight,
        fill: highLightedValue?.backgroundColor,
        opacity: 0.4
      }
    )
  ] });
};
var CJAxisBottom_default = CJAxisBottom;

// src/components/TrendLine/index.tsx
import { jsx as jsx235 } from "react/jsx-runtime";
var DEFAULT_MARGIN2 = { top: 20, right: 20, bottom: 20, left: 20 };
var TrendLine = ({
  data,
  curve = "natural",
  margin = DEFAULT_MARGIN2,
  enablePoints = false,
  enableGridX = false,
  enableGridY = false,
  axisLeft = null,
  axisBottom = null,
  pointProps,
  width,
  height,
  backgroundColor,
  marginContainer,
  stacked = true,
  yearsWithLabel = void 0,
  yearsWithVariationLabel = void 0,
  metricsWithLabel = [],
  metricsWithLabelAreDollars = [],
  pointsMinimumFractionDigits = 0,
  pointsMaximumFractionDigits = 0,
  cjAxisBottom = void 0,
  labelStyle,
  fill = [],
  defs = [],
  customLayer,
  ...props
}) => {
  return /* @__PURE__ */ jsx235(
    Box44,
    {
      width,
      height,
      backgroundColor,
      margin: marginContainer,
      "data-testid": "trendline-chart",
      children: /* @__PURE__ */ jsx235(
        ResponsiveLine,
        {
          colors: { datum: "color" },
          data,
          curve,
          enablePoints,
          enableGridX,
          enableGridY,
          axisLeft,
          axisBottom,
          margin,
          xScale: { type: "point" },
          yScale: {
            type: "linear",
            min: "auto",
            max: "auto",
            stacked,
            reverse: false
          },
          yFormat: " >-.2f",
          useMesh: true,
          defs,
          fill,
          layers: [
            (props2) => cjAxisBottom ? /* @__PURE__ */ jsx235(CJAxisBottom_default, { ...props2, cjAxisBottom }) : null,
            "grid",
            "markers",
            "axes",
            "areas",
            "crosshair",
            "lines",
            "points",
            "slices",
            "mesh",
            "legends",
            customLayer || (() => null),
            (props2) => yearsWithLabel ? Label_default2(
              yearsWithLabel,
              metricsWithLabel,
              metricsWithLabelAreDollars,
              pointsMinimumFractionDigits,
              pointsMaximumFractionDigits,
              labelStyle
            )(props2) : null,
            (props2) => yearsWithVariationLabel ? VariationLabel_default(yearsWithVariationLabel, metricsWithLabel)(props2) : null
          ],
          ...enablePoints && { ...pointProps },
          ...props
        }
      )
    }
  );
};
var TrendLine_default = TrendLine;

// src/components/USBaseMap/index.tsx
import { Box as Box45, useTheme as useTheme66 } from "@chakra-ui/react";
import { useEffect as useEffect11, useRef as useRef8 } from "react";

// src/components/USBaseMap/config.ts
var overrideMapStyles = (chakraTheme2) => ({
  ".leaflet-tooltip": {
    background: chakraTheme2.colors.white,
    backgroundColor: "transparent !important",
    border: "none !important",
    padding: "0px !important",
    borderRadius: "0px !important",
    boxShadow: "none !important"
  },
  ".leaflet-pane": {
    zIndex: "9 !important"
  },
  "div.leaflet-control-zoom.leaflet-bar.leaflet-control": {
    border: "none"
  },
  "a.leaflet-control-zoom-in": {
    borderTopLeftRadius: "6px !important",
    borderTopRightRadius: "6px !important",
    color: chakraTheme2.colors.mediumGrey,
    fontFamily: chakraTheme2.fonts.regular
  },
  "a.leaflet-control-zoom-out": {
    borderBottomLeftRadius: "6px !important",
    borderBottomRightRadius: "6px !important",
    color: chakraTheme2.colors.mediumGrey,
    fontFamily: chakraTheme2.fonts.regular
  },
  "div.leaflet-popup-content-wrapper": {
    borderRadius: "4px !important"
  },
  "div.leaflet-popup-content": {
    width: "161px"
  }
});
var MAX_ATTEMPS = 5;

// src/components/USBaseMap/index.tsx
import L2 from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-boundary-canvas";
import { jsx as jsx236 } from "react/jsx-runtime";
var USBaseMap = ({
  width,
  height,
  mapOptions,
  zoomControlPosition,
  geoJsonDataLayers,
  overrideMapStyle,
  children,
  whenGeoJsonLayersReady
}) => {
  const chakraTheme2 = useTheme66();
  let hashId;
  const mapRef = useRef8();
  const addGeoJsonLayerToMap = (map, geojson, options) => {
    return L2.geoJSON(geojson, options).addTo(map);
  };
  const generateHashKey = () => {
    const generator = () => Math.random().toString(36).substring(2) + (/* @__PURE__ */ new Date()).getTime().toString(36);
    hashId = generator();
    let ATTEMPS = 0;
    while (document.getElementById(`map${hashId}`) && ATTEMPS < MAX_ATTEMPS) {
      hashId = generator();
      ATTEMPS++;
    }
    return hashId;
  };
  useEffect11(() => {
    const container = L2.DomUtil.get(`map${hashId}`);
    if (container) container._leaflet_id = null;
    mapRef.current = L2.map(`map${hashId}`, mapOptions);
    const geoJsonInstances = geoJsonDataLayers && geoJsonDataLayers.map(
      ({ geojson, options }) => addGeoJsonLayerToMap(mapRef.current, geojson, options)
    );
    L2.control.zoom({
      position: zoomControlPosition
    }).addTo(mapRef.current);
    whenGeoJsonLayersReady && whenGeoJsonLayersReady(geoJsonInstances, mapRef.current);
    return () => {
      mapRef.current?.off();
      mapRef.current?.remove();
    };
  }, []);
  return /* @__PURE__ */ jsx236(
    Box45,
    {
      "data-testid": "basemap",
      sx: overrideMapStyle || overrideMapStyles(chakraTheme2),
      width,
      height,
      id: `map${generateHashKey()}`,
      background: "#ddd",
      backgroundColor: chakraTheme2.colors.graphic04,
      children
    }
  );
};
var USBaseMap_default = USBaseMap;

// src/components/USBaseMap/BaseMapPopup/index.tsx
import { createPortal } from "react-dom";
import L3 from "leaflet";
import { Fragment as Fragment29, jsx as jsx237 } from "react/jsx-runtime";
var BaseMapPopup = ({ children, ...props }) => {
  const { map, position, ...others } = props;
  return /* @__PURE__ */ jsx237(Fragment29, { children: createPortal(
    children,
    L3.popup(others).setLatLng(position).addTo(map)._contentNode
  ) });
};
var BaseMapPopup_default = BaseMapPopup;

// src/components/CJPieGraph/CJPieGraph.tsx
import {
  ResponsivePie
} from "@nivo/pie";
import { useTheme as useTheme67 } from "@chakra-ui/react";
import { jsx as jsx238 } from "react/jsx-runtime";
var CJPieGraph = ({
  data,
  CenteredMetric,
  ...props
}) => {
  const theme = useTheme67();
  return /* @__PURE__ */ jsx238(
    ResponsivePie,
    {
      data,
      margin: { top: 40, right: 80, bottom: 80, left: 80 },
      innerRadius: 0.55,
      padAngle: 1,
      cornerRadius: 3,
      arcLinkLabel: "label",
      activeOuterRadiusOffset: 8,
      arcLinkLabelsSkipAngle: 10,
      arcLinkLabelsTextColor: theme?.colors?.black,
      arcLinkLabelsThickness: 2,
      arcLinkLabelsColor: { from: "color" },
      arcLabelsSkipAngle: 10,
      arcLabelsTextColor: {
        from: "color",
        modifiers: [["brighter", 2]]
      },
      colors: { datum: "data.color" },
      layers: [
        "arcs",
        "arcLabels",
        "arcLinkLabels",
        "legends",
        (props2) => CenteredMetric ? /* @__PURE__ */ jsx238(CenteredMetric, { ...props2 }) : null
      ],
      tooltip: void 0,
      legends: void 0,
      ...props
    }
  );
};
var CJPieGraph_default = CJPieGraph;

// src/components/RelatedReportList/index.tsx
import axios from "axios";
import { Grid, GridItem } from "@chakra-ui/react";
import { useEffect as useEffect12, useState as useState15 } from "react";

// src/hooks/useWindowSize/index.ts
import { useState as useState14 } from "react";
import { useEventListener, useIsomorphicLayoutEffect } from "usehooks-ts";
var useWindowSize = () => {
  const [windowSize, setWindowSize] = useState14({
    width: 0,
    isLg: false,
    isMd: false,
    isSm: false,
    height: 0
  });
  const handleSize = () => {
    setWindowSize({
      width: window.innerWidth,
      isLg: window.innerWidth >= 1440,
      isMd: window.innerWidth > 1143 && window.innerWidth < 1440,
      // 1143 is the breakpoint for 1024
      isSm: window.innerWidth <= 1143,
      // 1143 is the breakpoint for 1024
      height: window.innerHeight
    });
  };
  useEventListener("resize", handleSize);
  useIsomorphicLayoutEffect(() => {
    handleSize();
  }, []);
  return windowSize;
};

// src/components/RelatedReportList/index.tsx
import { jsx as jsx239 } from "react/jsx-runtime";
var RelatedReportList = ({
  tile,
  dashboard,
  baseAppUrl,
  configAPIUrl,
  token,
  largeReportCardWidth = void 0,
  handleClickReport,
  useArcadiaLogo = false
}) => {
  const { isLg } = useWindowSize();
  const [isLoadingRelatedReports, setIsLoadingRelatedReports] = useState15(false);
  const [relatedReports, setRelatedReports] = useState15({});
  let reports = [];
  if (relatedReports?.[tile]?.[dashboard]) {
    reports = relatedReports[tile][dashboard].reports;
  }
  const getRelatedReports = async () => {
    const url = `${configAPIUrl}/common/relatedReports`;
    const config = {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
    setIsLoadingRelatedReports(true);
    try {
      const res = await axios.get(url, config);
      setRelatedReports(res.data);
    } catch (e) {
      console.error("There was an error retrieving relatedReports");
    } finally {
      setIsLoadingRelatedReports(false);
    }
  };
  useEffect12(() => {
    if (token) {
      getRelatedReports();
    }
  }, [token]);
  const listReports = (myReports) => {
    return myReports.map((report) => {
      const {
        name,
        description,
        details: { dataSources, attributionMethodologies, contentAreas }
      } = report;
      const url = report.url || `${baseAppUrl}${report.path}`;
      return /* @__PURE__ */ jsx239(GridItem, { children: /* @__PURE__ */ jsx239(
        ReportCard_default,
        {
          onClick: handleClickReport?.({ name, url }),
          variant: "full-width",
          title: name,
          description,
          dataSource: dataSources,
          attributionMethodology: attributionMethodologies,
          contentArea: contentAreas
        }
      ) }, `${name}-key`);
    });
  };
  const repeatWidth = largeReportCardWidth || "1fr";
  return /* @__PURE__ */ jsx239(
    Grid,
    {
      "data-testid": "related-report-list--container",
      templateColumns: isLoadingRelatedReports ? "repeat(1, 1)" : `repeat(${isLg ? 3 : 2}, ${isLg ? repeatWidth : "1fr"})`,
      gap: "30px",
      mb: "35px",
      children: isLoadingRelatedReports ? /* @__PURE__ */ jsx239(GridItem, { children: useArcadiaLogo ? /* @__PURE__ */ jsx239(ArcadiaLoadingIcon_default, { width: 200, height: 200, margin: "auto" }) : /* @__PURE__ */ jsx239(CJLoadingIcon_default, { width: 200, height: 200, margin: "auto" }) }, "related-reports-list--loading-key") : listReports(reports)
    }
  );
};
var RelatedReportList_default = RelatedReportList;

// src/components/Accordion/Accordion.tsx
import { Box as Box46, Flex as Flex25, Img, Text as Text23, useTheme as useTheme68 } from "@chakra-ui/react";
import * as DOMPurify from "dompurify";
import { Fragment as Fragment30, jsx as jsx240, jsxs as jsxs130 } from "react/jsx-runtime";
var DVAccordion = ({
  name,
  descriptions,
  color,
  icon,
  groupItemsLabel,
  secondDescription,
  expandButtonText = "List of Reports",
  isOpen,
  onToggle,
  children,
  accordionId
}) => {
  const theme = useTheme68();
  const applyOpacityToHex = (hexColor, opacity) => {
    const hex = hexColor.replace(/^#/, "");
    const alphaHex = Math.round(opacity * 255).toString(16).padStart(2, "0");
    return `#${hex}${alphaHex}`;
  };
  return /* @__PURE__ */ jsxs130(
    Flex25,
    {
      flexDirection: "column",
      "data-testid": "accordion",
      borderRadius: "10px",
      padding: "20px",
      backgroundColor: theme.colors?.white,
      boxShadow: "none",
      border: `1px solid ${theme.colors?.lightGrey}`,
      id: accordionId,
      children: [
        /* @__PURE__ */ jsxs130(Flex25, { "data-tesid": "accordion-header", gap: "24px", alignItems: "center", children: [
          icon && /* @__PURE__ */ jsx240(Box46, { width: "55px", height: "55px", children: /* @__PURE__ */ jsx240(Img, { src: icon, objectFit: "contain", "data-testid": "accordion-icon" }) }),
          /* @__PURE__ */ jsxs130(
            Flex25,
            {
              justifyContent: "space-between",
              alignItems: "center",
              flex: "1",
              paddingBottom: "12px",
              borderBottom: `solid 1px ${color}`,
              children: [
                /* @__PURE__ */ jsx240(Text23, { color, textStyle: "inter-semiBold-16-20", children: name }),
                groupItemsLabel && /* @__PURE__ */ jsx240(
                  Text23,
                  {
                    padding: "2px 7px",
                    color,
                    backgroundColor: applyOpacityToHex(color, 0.1),
                    borderRadius: "3px",
                    textStyle: "inter-semiBold-10-12",
                    children: groupItemsLabel
                  }
                )
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx240(Flex25, { gap: "30px", padding: "20px 0px", "data-testid": "descriptions-container", children: descriptions.map((description, idx) => {
          const sanitized = DOMPurify.sanitize(description.html);
          return /* @__PURE__ */ jsx240(
            Box46,
            {
              flex: "1",
              fontFamily: theme.fonts?.interRegular,
              css: {
                fontSize: "14px",
                lineHeight: "20px"
              },
              dangerouslySetInnerHTML: { __html: sanitized }
            },
            `description-${idx}`
          );
        }) }),
        isOpen && /* @__PURE__ */ jsxs130(Fragment30, { children: [
          secondDescription && /* @__PURE__ */ jsx240(
            Text23,
            {
              "data-testid": "long-description",
              textStyle: "inter-regular-12-16",
              mb: "20px",
              children: secondDescription
            }
          ),
          children
        ] }),
        /* @__PURE__ */ jsxs130(
          Flex25,
          {
            alignSelf: "flex-end",
            alignItems: "center",
            gap: "8px",
            padding: "12px 12px 0px 12px",
            cursor: "pointer",
            "data-testid": "expand-button",
            onClick: onToggle,
            children: [
              /* @__PURE__ */ jsx240(Text23, { textStyle: "inter-semiBold-14-17", color, children: expandButtonText }),
              /* @__PURE__ */ jsx240(
                AngleIcon_default,
                {
                  direction: isOpen ? "up" : "down",
                  color,
                  dataTestId: "angle-icon"
                }
              )
            ]
          }
        )
      ]
    }
  );
};
var Accordion_default = DVAccordion;

// src/components/DataverseReportCard/DataverseReportCard.tsx
import { Box as Box47, Flex as Flex26, Text as Text24, Tooltip as Tooltip3, useTheme as useTheme69 } from "@chakra-ui/react";
import { useState as useState16, useEffect as useEffect13, useRef as useRef9 } from "react";
import { Fragment as Fragment31, jsx as jsx241, jsxs as jsxs131 } from "react/jsx-runtime";
var DataverseReportCard = ({
  color,
  name,
  description,
  dataSource,
  attributionMethodology,
  contentArea,
  id,
  onClickHandler,
  isFavorite,
  showStarIcon,
  addFavoriteHandler,
  isCardDisabled
}) => {
  const [
    showAttributionMethodologyTooltip,
    setShowAttributionMethodologyTooltip
  ] = useState16(false);
  const [attributionMethodologyTags, setAttributionMethodologyTags] = useState16(
    attributionMethodology || []
  );
  const theme = useTheme69();
  const attributionMethodologyContainerRef = useRef9(null);
  const titleRef = useRef9(null);
  const isDisabled = (contentArea2) => {
    const contentWidth = contentArea2.join(", ").length * 8;
    return contentWidth <= 280;
  };
  const textIsOverflowing = (textElement) => textElement.clientHeight - textElement.scrollHeight < 0;
  const getHiddenAttributionMethodologies = () => {
    if (attributionMethodology && attributionMethodology.length > 3) {
      const attributionMethodologyCopy = attributionMethodology ? [...attributionMethodology] : [];
      const hiddenAttributionMethodologies = attributionMethodologyCopy.splice(2);
      setAttributionMethodologyTags([
        ...attributionMethodologyCopy,
        `+${hiddenAttributionMethodologies.length}`
      ]);
    }
  };
  useEffect13(() => {
    const attributionMethodologyItemContainer = attributionMethodologyContainerRef?.current;
    if (attributionMethodology && attributionMethodology.length > 3 || attributionMethodologyItemContainer && [...attributionMethodologyItemContainer?.children]?.some(
      (child) => textIsOverflowing(child)
    )) {
      getHiddenAttributionMethodologies();
      setShowAttributionMethodologyTooltip(true);
    }
  }, []);
  const applyOpacityToHex = (hexColor, opacity) => {
    const hex = hexColor.replace(/^#/, "");
    const alphaHex = Math.round(opacity * 255).toString(16).padStart(2, "0");
    return `#${hex}${alphaHex}`;
  };
  return /* @__PURE__ */ jsxs131(
    Flex26,
    {
      cursor: "pointer",
      onClick: () => {
        if (isCardDisabled) {
          return;
        }
        onClickHandler(id);
      },
      padding: "10px 15px",
      borderRadius: "10px",
      backgroundColor: theme.colors?.newBackgroundGrey,
      gap: "15px",
      "data-testid": "dv-report-card",
      border: `1px solid ${theme.colors?.lightGrey}`,
      children: [
        /* @__PURE__ */ jsxs131(
          Flex26,
          {
            opacity: isCardDisabled ? 0.5 : 1,
            flexDirection: "column",
            gap: "6px",
            flex: "1",
            children: [
              /* @__PURE__ */ jsxs131(Flex26, { alignItems: "center", children: [
                showStarIcon && !isCardDisabled && /* @__PURE__ */ jsx241(Box47, { position: "relative", children: /* @__PURE__ */ jsx241(
                  StarIcon_default,
                  {
                    height: 14,
                    cursor: "pointer",
                    backgroundColor: isFavorite ? color : "transparent",
                    color: isFavorite ? color : theme.colors?.lightGrey,
                    width: 14,
                    margin: "0 5px 0 0",
                    dataTestId: "star-icon",
                    handleClick: (e) => {
                      e.stopPropagation();
                      if (!addFavoriteHandler) return;
                      addFavoriteHandler(id);
                    }
                  }
                ) }),
                isCardDisabled && /* @__PURE__ */ jsx241(
                  Tooltip3,
                  {
                    hasArrow: true,
                    bg: theme.colors?.white,
                    color: theme.colors?.darkGrey2,
                    label: /* @__PURE__ */ jsx241(
                      Text24,
                      {
                        textStyle: "inter-regular-11-14",
                        color: theme.colors?.darkGrey2,
                        children: "To learn about how you can get access to this report, please contact CareJourney\u2019s Member Service team."
                      }
                    ),
                    placement: "top",
                    children: /* @__PURE__ */ jsx241(Box47, { children: /* @__PURE__ */ jsx241(
                      LockIcon_default,
                      {
                        height: 14,
                        width: 14,
                        color,
                        margin: "0 5px 0 0"
                      }
                    ) })
                  }
                ),
                /* @__PURE__ */ jsx241(Text24, { ref: titleRef, textStyle: "inter-semiBold-14-18", color, children: name })
              ] }),
              /* @__PURE__ */ jsx241(Text24, { noOfLines: 3, textStyle: "inter-regular-11-16", children: description })
            ]
          }
        ),
        /* @__PURE__ */ jsxs131(
          Flex26,
          {
            flexDirection: "column",
            opacity: isCardDisabled ? 0.5 : 1,
            justifyContent: "space-between",
            gap: "9px",
            flex: "1",
            backgroundColor: theme.colors?.white,
            borderRadius: "6px",
            padding: "5px 10px",
            children: [
              !!dataSource?.length && /* @__PURE__ */ jsxs131(Flex26, { alignItems: "center", gap: "6px", children: [
                /* @__PURE__ */ jsx241(Text24, { textStyle: "inter-semiBold-10-16", minWidth: "fit-content", children: "DATA SOURCE:" }),
                /* @__PURE__ */ jsx241(Text24, { textStyle: "inter-regular-11-14", children: dataSource.join(", ") })
              ] }),
              !!attributionMethodologyTags?.length && /* @__PURE__ */ jsx241(Flex26, { alignItems: "center", gap: "6px", children: /* @__PURE__ */ jsxs131(Fragment31, { children: [
                /* @__PURE__ */ jsx241(Text24, { textStyle: "inter-semiBold-10-16", minWidth: "fit-content", children: "ATTRIBUTION METHODOLOGY:" }),
                /* @__PURE__ */ jsx241(
                  Tooltip3,
                  {
                    hasArrow: true,
                    isDisabled: !showAttributionMethodologyTooltip,
                    maxWidth: "360px",
                    bg: theme.colors?.white,
                    color: theme.colors?.darkGrey2,
                    label: /* @__PURE__ */ jsx241(
                      Text24,
                      {
                        textStyle: "inter-regular-11-14",
                        color: theme.colors?.darkGrey2,
                        children: attributionMethodologyTags?.join(", ")
                      }
                    ),
                    children: /* @__PURE__ */ jsx241(
                      Flex26,
                      {
                        alignItems: "center",
                        gap: "6px",
                        maxWidth: "255px",
                        ref: attributionMethodologyContainerRef,
                        children: attributionMethodologyTags.map((methodology) => {
                          return /* @__PURE__ */ jsx241(
                            Text24,
                            {
                              textStyle: "inter-regular-11-14",
                              padding: "2px 7px",
                              borderRadius: "3px",
                              backgroundColor: applyOpacityToHex(color, 0.1),
                              textTransform: "uppercase",
                              noOfLines: 1,
                              fontWeight: "600",
                              color,
                              children: methodology
                            },
                            methodology + name
                          );
                        })
                      }
                    )
                  }
                )
              ] }) }),
              !!contentArea?.length && /* @__PURE__ */ jsxs131(Flex26, { alignItems: "center", gap: "6px", children: [
                /* @__PURE__ */ jsx241(
                  Text24,
                  {
                    textStyle: "inter-semiBold-10-16",
                    noOfLines: 1,
                    minWidth: "fit-content",
                    children: "CONTENT AREA:"
                  }
                ),
                /* @__PURE__ */ jsx241(
                  Tooltip3,
                  {
                    hasArrow: true,
                    bg: theme.colors?.white,
                    color: theme.colors?.darkGrey2,
                    isDisabled: isDisabled(contentArea),
                    maxWidth: "360px",
                    label: /* @__PURE__ */ jsx241(Text24, { textStyle: "inter-regular-11-14", children: contentArea.join(", ") }),
                    children: /* @__PURE__ */ jsx241(Text24, { noOfLines: 1, textStyle: "inter-regular-11-14", children: contentArea.join(", ") })
                  }
                )
              ] })
            ]
          }
        )
      ]
    }
  );
};
var DataverseReportCard_default = DataverseReportCard;

// src/helpers/calculateMean.ts
var calculateMean = (numbers) => {
  if (numbers.length === 0) {
    throw new Error("Input array is empty, cannot calculate mean.");
  }
  const totalSum = numbers.reduce((sum, num) => sum + num, 0);
  const mean = totalSum / numbers.length;
  return Math.round(mean);
};

// src/components/Range/BoxesRange.tsx
import { Box as Box49, Flex as Flex28, Text as Text26, useTheme as useTheme71 } from "@chakra-ui/react";

// src/helpers/hexToRgb.ts
var hexToRgb = (hex) => {
  const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  const hexExp = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  let result = hexExp.exec(hex);
  if (!result) {
    result = shorthandRegex.exec(hex);
    if (result) {
      return `rgb(${parseInt(result[1], 16)}, ${parseInt(
        result[2],
        16
      )}, ${parseInt(result[3], 16)})`;
    }
  } else {
    return `rgb(${parseInt(result[1], 16)}, ${parseInt(
      result[2],
      16
    )}, ${parseInt(result[3], 16)})`;
  }
  return "rgb(0, 0, 0)";
};

// src/components/Range/config.ts
var HIGHLIGHTED_BOXES_COLORS = [
  "#E10401",
  "#E25202",
  "#E36A02",
  "#E38502",
  "#E4A302",
  "#D6B202",
  "#BBB002",
  "#9AAE02",
  "#72AC02",
  "#1BA701"
];
var getLabelsConfig = ({ min, max, mean, meanPostition = "50%" }) => ({
  start: {
    position: "calc(0% - 1px)",
    label: "MIN",
    value: min,
    textAlign: "left",
    placementCorrection: 0
  },
  mean: {
    position: meanPostition,
    label: "MEAN",
    value: mean,
    textAlign: "center",
    placementCorrection: "-6px"
  },
  end: {
    position: "calc(100% - 1px)",
    label: "MAX",
    value: max,
    textAlign: "right",
    placementCorrection: "-60px"
  }
});
var interpolateColor = (colorStart, colorEnd, position, gradientColors) => {
  const startColor = colorStart && colorStart.startsWith("#") ? hexToRgb(colorStart) : colorStart;
  const endColor = colorEnd && colorEnd.startsWith("#") ? hexToRgb(colorEnd) : colorEnd;
  const startArray = startColor?.match(/\d+/g)?.map(Number) || [];
  const endArray = endColor?.match(/\d+/g)?.map(Number) || [];
  if (startArray.length === 0 || endArray.length === 0) {
    return hexToRgb(gradientColors.at(-1) || "#000000");
  }
  const result = startArray.map(
    (value, index) => Math.round(value + (endArray[index] - value) * position)
  );
  return `rgb(${result.join(",")})`;
};

// src/components/Range/RangeLabels.tsx
import { Flex as Flex27, Text as Text25, Box as Box48, useTheme as useTheme70 } from "@chakra-ui/react";
import { Fragment as Fragment32, jsx as jsx242, jsxs as jsxs132 } from "react/jsx-runtime";
var RangeLabels = ({ type, labelTypes, variant, showMinMaxOnSides, flipTriangleIndicator = false, hasLimits = false }) => {
  const theme = useTheme70();
  return /* @__PURE__ */ jsxs132(Fragment32, { children: [
    variant === "gradientRange" && ((!showMinMaxOnSides || showMinMaxOnSides && type !== "start" && type !== "end") && /* @__PURE__ */ jsx242(
      Box48,
      {
        top: "0px",
        left: labelTypes[type].position,
        position: "absolute",
        backgroundColor: "#CFCFCD",
        height: "8px",
        width: "1.6px"
      }
    )),
    /* @__PURE__ */ jsx242(
      Flex27,
      {
        direction: "column",
        position: "absolute",
        top: variant === "gradientRange" ? "10px" : "30px",
        left: labelTypes[type].position,
        children: /* @__PURE__ */ jsx242(
          Box48,
          {
            textAlign: labelTypes[type].textAlign,
            marginLeft: labelTypes[type].placementCorrection,
            children: showMinMaxOnSides && !hasLimits && (type === "start" || type === "end") ? null : /* @__PURE__ */ jsx242(
              Text25,
              {
                color: theme.colors.smoky,
                opacity: 0.5,
                textStyle: flipTriangleIndicator ? "regular-10-12" : "regular-12-12",
                children: flipTriangleIndicator && type === "mean" ? `AVG: ${labelTypes[type].value} ` : labelTypes[type].value
              }
            )
          }
        )
      }
    )
  ] });
};
var RangeLabels_default = RangeLabels;

// src/components/Range/BoxesRange.tsx
import { jsx as jsx243, jsxs as jsxs133 } from "react/jsx-runtime";
var HighlightedBoxes = ({
  value,
  max,
  min,
  mean,
  showLabels,
  showStats,
  variant,
  percentilValue,
  width,
  compact,
  percentilePosition,
  showPercentileText,
  percentileValueColor,
  reverseOrder = false,
  rangeColor = HIGHLIGHTED_BOXES_COLORS,
  justifyContent = "none"
}) => {
  const theme = useTheme71();
  const rangeStep = (max - min) / 10;
  const highlightedBox = value >= max ? 9 : Math.floor((value - min) / rangeStep);
  const boxes = Array.from({ length: 10 }, (_3, index) => {
    const boxValue = min + index * rangeStep;
    const isHighlighted = index === highlightedBox;
    return { boxValue, isHighlighted, highlightedBox };
  });
  const labelTypes = getLabelsConfig({ min, max, mean });
  const calculatedWidth = width ? `${(width - 140.2) / 10}px` : "25px";
  const calculatedWidthNoLabel = width ? `${(width - 27) / 10}px` : "32.5px";
  if (compact) {
    return /* @__PURE__ */ jsxs133(
      Flex28,
      {
        maxW: "fit-content",
        gap: "2px",
        alignItems: "center",
        flexDirection: "column",
        "data-testid": "highlighted-boxes-component-compact",
        children: [
          showLabels && /* @__PURE__ */ jsx243(
            Text26,
            {
              fontWeight: 600,
              fontSize: "8px",
              color: rangeColor[highlightedBox],
              children: percentilValue
            }
          ),
          /* @__PURE__ */ jsx243(
            Box49,
            {
              width: "40px",
              height: "5px",
              borderRadius: "6px",
              backgroundColor: rangeColor[highlightedBox]
            }
          )
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs133(
    Flex28,
    {
      alignItems: "center",
      gap: "16px",
      "data-testid": "highlighted-boxes-component",
      maxW: width,
      justifyContent,
      children: [
        percentilePosition === "left" && showLabels && /* @__PURE__ */ jsx243(
          Text26,
          {
            textStyle: "inter-medium-14-16",
            color: percentileValueColor || rangeColor[highlightedBox],
            children: percentilValue
          }
        ),
        showPercentileText && showLabels && /* @__PURE__ */ jsx243(Text26, { textStyle: "regular-10-10", color: theme.colors?.darkGrey2, children: "PERCENTILE" }),
        /* @__PURE__ */ jsxs133(Flex28, { gap: "3px", alignItems: "flex-end", position: "relative", children: [
          showStats && !showLabels && Object.keys(labelTypes).map((label) => /* @__PURE__ */ jsx243(
            RangeLabels_default,
            {
              labelTypes,
              type: label,
              variant
            },
            label
          )),
          (reverseOrder ? boxes.reverse() : boxes).map(
            ({ isHighlighted, highlightedBox: highlightedBox2 }, index) => /* @__PURE__ */ jsxs133(
              Flex28,
              {
                flexDirection: "column",
                alignItems: "center",
                gap: "5px",
                children: [
                  !showLabels && index === highlightedBox2 && /* @__PURE__ */ jsx243(
                    Text26,
                    {
                      textStyle: "inter-medium-14-16",
                      color: rangeColor[highlightedBox2],
                      children: percentilValue
                    }
                  ),
                  /* @__PURE__ */ jsx243(
                    Box49,
                    {
                      width: showLabels ? calculatedWidth : calculatedWidthNoLabel,
                      height: showLabels ? "12px" : "9px",
                      borderRadius: "6px",
                      backgroundColor: isHighlighted ? rangeColor[highlightedBox2] : "#EAEBEC"
                    }
                  )
                ]
              },
              index
            )
          )
        ] }),
        percentilePosition === "right" && showLabels && /* @__PURE__ */ jsx243(
          Text26,
          {
            textStyle: "inter-medium-14-16",
            color: percentileValueColor || rangeColor[highlightedBox],
            children: percentilValue
          }
        )
      ]
    }
  );
};

// src/components/Range/GradientRange.tsx
import { useState as useState17, useRef as useRef10, useEffect as useEffect14 } from "react";
import { Box as Box50, Flex as Flex29, Text as Text27, useTheme as useTheme72 } from "@chakra-ui/react";
import { jsx as jsx244, jsxs as jsxs134 } from "react/jsx-runtime";
var GradientRange = ({
  value,
  max,
  min,
  gradientColors = ["#E10401", "#E4A302", "#1BA701"],
  mean,
  variant,
  tooltip,
  limits,
  width = 450,
  showMinMaxOnSides = false,
  flipTriangleIndicator = false
}) => {
  const theme = useTheme72();
  const [showTooltip, setShowTooltip] = useState17(false);
  const [containerWidth, setContainerWidth] = useState17(0);
  const tooltipContainerRef = useRef10(null);
  const percentage = (value - min) / (max - min) * width;
  const startIndex = Math.floor(
    percentage / width * (gradientColors.length - 1)
  );
  const meanPostition = `${(mean - min) / (max - min) * 100}%`;
  let minMeanPositionPercent = (min - limits[0]) / (limits[1] - limits[0]);
  let maxMeanPositionPercent = (limits[1] - max) / (limits[1] - limits[0]);
  const valueOverTwentyPercent = Boolean(
    minMeanPositionPercent > 0.2 || maxMeanPositionPercent > 0.2
  );
  if (minMeanPositionPercent > 0.2) minMeanPositionPercent = 0.2;
  if (maxMeanPositionPercent > 0.2) maxMeanPositionPercent = 0.2;
  const hasLimits = limits.length === 2;
  const mainBarWidth = hasLimits ? width * (1 - (maxMeanPositionPercent + minMeanPositionPercent)) : width;
  const lowerLimitBarWidth = hasLimits ? width * minMeanPositionPercent : 0;
  const upperLimitBarWidth = hasLimits ? width * maxMeanPositionPercent : 0;
  const startColor = gradientColors[startIndex];
  const endColor = gradientColors[startIndex + 1];
  let indicatorPosition = (value - min) / (max - min);
  let indicatorColor = interpolateColor(
    startColor,
    endColor,
    percentage % (width / (gradientColors.length - 1)) / (width / (gradientColors.length - 1)),
    gradientColors
  );
  if (value < min) {
    indicatorColor = gradientColors[0];
  } else if (value > max) {
    indicatorColor = gradientColors[gradientColors.length - 1];
  }
  if (valueOverTwentyPercent) {
    if (value < min) {
      indicatorPosition = lowerLimitBarWidth * (value - limits[0]) / (min - limits[0]) / width;
    } else if (value > max) {
      indicatorPosition = (upperLimitBarWidth * ((value - max) / (limits[1] - max)) + lowerLimitBarWidth + mainBarWidth) / width;
    } else {
      indicatorPosition = (mainBarWidth * (value - min) / (max - min) + lowerLimitBarWidth) / width;
    }
  }
  indicatorPosition = indicatorPosition > 1 ? "100%" : indicatorPosition < 0 ? "0%" : `${indicatorPosition * 100}%`;
  const LABEL_TYPES = getLabelsConfig({
    min,
    max,
    mean,
    meanPostition
  });
  useEffect14(() => {
    if (tooltipContainerRef.current) {
      setContainerWidth(tooltipContainerRef.current.offsetWidth / 2);
    }
  }, [tooltipContainerRef.current]);
  const limitTrianglePosition = (percent) => {
    if (parseInt(percent) > 98) {
      return "98%";
    }
    if (parseInt(percent) < 2) {
      return "2%";
    }
    return percent;
  };
  return /* @__PURE__ */ jsxs134(Box50, { display: "flex", children: [
    showMinMaxOnSides && /* @__PURE__ */ jsx244(
      Text27,
      {
        margin: "3px",
        color: theme.colors.smoky,
        opacity: 0.5,
        textStyle: "regular-12-12",
        children: hasLimits ? limits[0] : min
      }
    ),
    /* @__PURE__ */ jsxs134(
      Box50,
      {
        height: "26",
        "data-testid": "gradient-range",
        onMouseEnter: () => {
          if (flipTriangleIndicator) setShowTooltip(true);
        },
        onMouseLeave: () => {
          if (flipTriangleIndicator) setShowTooltip(false);
        },
        children: [
          /* @__PURE__ */ jsx244(Box50, { width: `${width}px`, height: "5px", position: "relative", children: /* @__PURE__ */ jsxs134(
            Flex29,
            {
              marginLeft: flipTriangleIndicator ? "-8px" : "-4px",
              width: flipTriangleIndicator ? "30px" : "unset",
              flexDirection: "column",
              "data-testid": "gradient-indicator",
              position: "absolute",
              justifyContent: "center",
              bottom: "7px",
              left: flipTriangleIndicator ? limitTrianglePosition(indicatorPosition) : indicatorPosition,
              transform: "rotate(180deg) translateX(8px)",
              onMouseEnter: () => {
                if (!flipTriangleIndicator) setShowTooltip(true);
              },
              onMouseLeave: () => {
                if (!flipTriangleIndicator) setShowTooltip(false);
              },
              children: [
                Boolean(showTooltip && tooltip) && /* @__PURE__ */ jsx244(
                  Box50,
                  {
                    ref: tooltipContainerRef,
                    transform: "rotate(180deg)",
                    position: "absolute",
                    zIndex: "10",
                    top: "20px",
                    right: `-${containerWidth}px`,
                    children: tooltip
                  }
                ),
                /* @__PURE__ */ jsx244(
                  TriangleIcon_default,
                  {
                    direction: flipTriangleIndicator ? "down" : "up",
                    color: indicatorColor,
                    width: flipTriangleIndicator ? 10 : 16,
                    height: flipTriangleIndicator ? 10 : 16,
                    margin: flipTriangleIndicator ? "-10px 0 0 25%" : "0"
                  }
                ),
                flipTriangleIndicator && /* @__PURE__ */ jsx244(Text27, { marginLeft: "-2px", textAlign: "center", textStyle: "semiBold-10-16", transform: "rotate(180deg)", color: "#59595B", children: value })
              ]
            }
          ) }),
          hasLimits && /* @__PURE__ */ jsx244(
            Box50,
            {
              width: `${width * minMeanPositionPercent}px`,
              height: "1px",
              top: "2.5px",
              backgroundColor: [gradientColors[0]],
              position: "relative",
              children: /* @__PURE__ */ jsx244(
                Box50,
                {
                  top: "-2px",
                  left: "0px",
                  position: "absolute",
                  backgroundColor: "#CFCFCD",
                  height: "8px",
                  width: "1.6px"
                }
              )
            }
          ),
          /* @__PURE__ */ jsx244(
            Box50,
            {
              width: `${mainBarWidth}px`,
              left: `${width * minMeanPositionPercent}px`,
              height: "5px",
              backgroundImage: `linear-gradient(to right, ${gradientColors.join(
                ", "
              )})`,
              position: "relative",
              children: Object.keys(LABEL_TYPES).map((label) => /* @__PURE__ */ jsx244(
                RangeLabels_default,
                {
                  labelTypes: LABEL_TYPES,
                  type: label,
                  variant,
                  showMinMaxOnSides,
                  flipTriangleIndicator,
                  hasLimits
                },
                label
              ))
            }
          ),
          hasLimits && /* @__PURE__ */ jsx244(
            Box50,
            {
              width: `${width * maxMeanPositionPercent}px`,
              left: width * minMeanPositionPercent + mainBarWidth,
              top: "-2.5px",
              height: "1px",
              backgroundColor: [gradientColors[gradientColors.length - 1]],
              position: "relative",
              children: /* @__PURE__ */ jsx244(
                Box50,
                {
                  top: "-2px",
                  left: `${width * maxMeanPositionPercent}px`,
                  position: "absolute",
                  backgroundColor: "#CFCFCD",
                  height: "8px",
                  width: "1.6px"
                }
              )
            }
          )
        ]
      }
    ),
    showMinMaxOnSides && /* @__PURE__ */ jsx244(
      Text27,
      {
        margin: "3px",
        color: theme.colors.smoky,
        opacity: 0.5,
        textStyle: "regular-12-12",
        children: hasLimits ? limits[1] : max
      }
    )
  ] });
};

// src/components/Range/index.tsx
import { jsx as jsx245 } from "react/jsx-runtime";
var RangeComponent = (props) => {
  const {
    variant,
    rangeValues,
    value,
    width,
    limits = [],
    middleValue,
    showMinMaxOnSides,
    flipTriangleIndicator,
    compact,
    percentilePosition,
    showPercentileText,
    reverseOrder,
    rangeColor,
    percentileValueColor,
    justifyContent
  } = props;
  const min = Math.min(...rangeValues);
  const max = Math.max(...rangeValues);
  const mean = middleValue || calculateMean(rangeValues);
  const componentProps = { min, max, mean, value };
  if (variant === "highlightedBox") {
    const { showLabels, showStats, percentilValue } = props;
    return /* @__PURE__ */ jsx245(
      HighlightedBoxes,
      {
        ...componentProps,
        variant,
        compact,
        showLabels,
        showStats,
        percentilValue,
        width,
        limits,
        percentilePosition,
        showPercentileText,
        reverseOrder,
        rangeColor,
        percentileValueColor,
        justifyContent
      }
    );
  }
  const { gradientColors, tooltip } = props;
  return /* @__PURE__ */ jsx245(
    GradientRange,
    {
      ...componentProps,
      width,
      variant,
      gradientColors,
      tooltip,
      limits,
      showMinMaxOnSides,
      flipTriangleIndicator
    }
  );
};
var Range_default = RangeComponent;

// src/components/Sunburst/index.tsx
import { Sunburst, defaultProps as defaultProps2 } from "@nivo/sunburst";

// src/components/Sunburst/useSunburst.tsx
import { useMemo } from "react";
import {
  partition as d3Partition,
  hierarchy as d3Hierarchy
} from "d3-hierarchy";
import cloneDeep from "lodash/cloneDeep";
import sortBy from "lodash/sortBy";
import { usePropertyAccessor, useTheme as useTheme73, useValueFormatter } from "@nivo/core";
import { useArcGenerator } from "@nivo/arcs";
import {
  useOrdinalColorScale,
  useInheritedColor
} from "@nivo/colors";
import {
  defaultProps
} from "@nivo/sunburst";
var useSunburst = ({
  data,
  id = defaultProps.id,
  value = defaultProps.value,
  valueFormat,
  radius,
  arcsWidth,
  cornerRadius = defaultProps.cornerRadius,
  colors: colors2 = defaultProps.colors,
  colorBy = defaultProps.colorBy,
  inheritColorFromParent = defaultProps.inheritColorFromParent,
  childColor = defaultProps.childColor
}) => {
  const theme = useTheme73();
  const getColor3 = useOrdinalColorScale(colors2, colorBy);
  const getChildColor = useInheritedColor(
    childColor,
    theme
  );
  const getId2 = usePropertyAccessor(id);
  const getValue = usePropertyAccessor(value);
  const formatValue = useValueFormatter(valueFormat);
  const nodes = useMemo(() => {
    const clonedData = cloneDeep(data);
    const hierarchy = d3Hierarchy(clonedData).sum(getValue);
    const partition = d3Partition().size([
      2 * Math.PI,
      radius * radius
    ]);
    const descendants = partition(hierarchy).descendants().slice(1);
    const total = hierarchy.value ?? 0;
    const sortedNodes = sortBy(descendants, "depth");
    return sortedNodes.reduce((acc, descendant) => {
      const id2 = getId2(descendant.data);
      const value2 = descendant.value;
      const percentage = 100 * value2 / total;
      const path = descendant.ancestors().map((ancestor) => getId2(ancestor.data));
      const arc = {
        startAngle: descendant.x0,
        endAngle: descendant.x1,
        innerRadius: Math.sqrt(descendant.y0) + arcsWidth * (descendant.depth - 1),
        outerRadius: Math.sqrt(descendant.y1) + arcsWidth * descendant.depth - (descendant.depth - 1) * 5
      };
      let parent;
      if (descendant.parent) {
        parent = acc.find((node) => node.id === getId2(descendant.parent.data));
      }
      const normalizedNode = {
        id: id2,
        path,
        value: value2,
        percentage,
        formattedValue: valueFormat ? formatValue(value2) : `${percentage.toFixed(2)}%`,
        color: "",
        arc,
        data: descendant.data,
        depth: descendant.depth,
        height: descendant.height
      };
      if (inheritColorFromParent && parent && normalizedNode.depth > 1) {
        normalizedNode.color = getChildColor(parent, normalizedNode);
      } else {
        normalizedNode.color = getColor3(normalizedNode);
      }
      return [...acc, normalizedNode];
    }, []);
  }, [
    data,
    radius,
    getValue,
    getId2,
    valueFormat,
    formatValue,
    getColor3,
    inheritColorFromParent,
    getChildColor
  ]);
  const arcGenerator = useArcGenerator({ cornerRadius });
  return { arcGenerator, nodes };
};

// src/components/Sunburst/Arc.tsx
import { createElement as createElement2, useMemo as useMemo2 } from "react";

// src/components/Sunburst/ArcsLayer.tsx
import { createElement } from "react";
import { useTheme as useTheme74 } from "@nivo/core";
import { useInheritedColor as useInheritedColor2 } from "@nivo/colors";
import {
  ArcShape
} from "@nivo/arcs";

// src/components/Sunburst/useArcsTransition.tsx
import { useTransition } from "@react-spring/web";
import {
  useArcTransitionMode,
  interpolateArc
} from "@nivo/arcs";
function easing(value) {
  return value;
}
var useArcsTransition = (data, mode = "innerRadius", extra) => {
  const animate = false;
  const springConfig = {
    tension: 0,
    friction: 0,
    frequency: 0,
    damping: 0,
    mass: 0,
    velocity: 0,
    restVelocity: 0,
    precision: 0,
    progress: 0,
    duration: 0,
    easing: easing(0),
    clamp: true,
    bounce: 0,
    decay: true,
    round: 0
  };
  const phases = useArcTransitionMode(mode, extra);
  const transition = useTransition(data, {
    keys: (datum) => datum.id,
    initial: phases.update,
    from: phases.enter,
    enter: phases.update,
    update: phases.update,
    leave: phases.leave,
    config: springConfig,
    immediate: !animate
  });
  return {
    transition,
    interpolate: interpolateArc
  };
};

// src/components/Sunburst/ArcsLayer.tsx
import { jsx as jsx246 } from "react/jsx-runtime";
var ArcsLayer = ({
  center,
  data,
  arcGenerator,
  borderWidth,
  borderColor,
  onClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  component = ArcShape
}) => {
  const theme = useTheme74();
  const getBorderColor = useInheritedColor2(borderColor, theme);
  const { transition, interpolate } = useArcsTransition(data, void 0, {
    enter: (datum) => ({
      opacity: 0,
      color: datum.color,
      borderColor: getBorderColor(datum)
    }),
    update: (datum) => ({
      opacity: 1,
      color: datum.color,
      borderColor: getBorderColor(datum)
    }),
    leave: (datum) => ({
      opacity: 0,
      color: datum.color,
      borderColor: getBorderColor(datum)
    })
  });
  const Arc3 = component;
  return /* @__PURE__ */ jsx246("g", { transform: `translate(${center[0]},${center[1]})`, children: transition((transitionProps, datum) => {
    return createElement(Arc3, {
      key: datum.id,
      datum,
      style: {
        ...transitionProps,
        borderWidth,
        path: interpolate(
          transitionProps.startAngle,
          transitionProps.endAngle,
          transitionProps.innerRadius,
          transitionProps.outerRadius,
          arcGenerator
        )
      },
      onClick,
      onMouseEnter,
      onMouseMove,
      onMouseLeave
    });
  }) });
};

// src/components/Sunburst/Arc.tsx
import { useTooltip as useTooltip2 } from "@nivo/tooltip";
import { jsx as jsx247 } from "react/jsx-runtime";
var Arc2 = ({
  center,
  data,
  arcGenerator,
  borderWidth,
  borderColor,
  isInteractive,
  onClick,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  tooltip,
  transitionMode
}) => {
  const { showTooltipFromEvent, hideTooltip } = useTooltip2();
  const handleClick = useMemo2(() => {
    if (!isInteractive) return void 0;
    return (datum, event) => {
      onClick?.(datum, event);
    };
  }, [isInteractive, onClick]);
  const handleMouseEnter = useMemo2(() => {
    if (!isInteractive) return void 0;
    return (datum, event) => {
      showTooltipFromEvent(createElement2(tooltip, datum), event);
      onMouseEnter?.(datum, event);
    };
  }, [isInteractive, showTooltipFromEvent, tooltip, onMouseEnter]);
  const handleMouseMove = useMemo2(() => {
    if (!isInteractive) return void 0;
    return (datum, event) => {
      showTooltipFromEvent(createElement2(tooltip, datum), event);
      onMouseMove?.(datum, event);
    };
  }, [isInteractive, showTooltipFromEvent, tooltip, onMouseMove]);
  const handleMouseLeave = useMemo2(() => {
    if (!isInteractive) return void 0;
    return (datum, event) => {
      hideTooltip();
      onMouseLeave?.(datum, event);
    };
  }, [isInteractive, hideTooltip, onMouseLeave]);
  return /* @__PURE__ */ jsx247(
    ArcsLayer,
    {
      center,
      data,
      arcGenerator,
      borderWidth,
      borderColor,
      transitionMode,
      onClick: handleClick,
      onMouseEnter: handleMouseEnter,
      onMouseMove: handleMouseMove,
      onMouseLeave: handleMouseLeave
    }
  );
};

// src/components/Sunburst/index.tsx
import { Flex as Flex32 } from "@chakra-ui/react";

// src/components/Sunburst/SunburstLegend.tsx
import { Box as Box51, Flex as Flex30, Text as Text28, useTheme as useTheme75 } from "@chakra-ui/react";
import { jsx as jsx248, jsxs as jsxs135 } from "react/jsx-runtime";
var SunburstLegend = ({ data }) => {
  const theme = useTheme75();
  const getLegendsElements = (legends, deepness = 0) => /* @__PURE__ */ jsx248(Flex30, { flexDirection: "column", children: Boolean(legends.children && legends.children.length) && legends.children.map((legend, i) => /* @__PURE__ */ jsxs135(Box51, { children: [
    /* @__PURE__ */ jsxs135(
      Flex30,
      {
        marginLeft: `${10 * deepness}px`,
        marginTop: deepness === 0 && i !== 0 ? "15px" : "5px",
        alignItems: "baseline",
        fontSize: "12px",
        children: [
          /* @__PURE__ */ jsx248(
            DotIcon_default,
            {
              color: legend.color,
              width: 10 - 2 * deepness,
              height: 10 - 2 * deepness
            }
          ),
          /* @__PURE__ */ jsx248(
            Text28,
            {
              fontSize: 12 - 2 * deepness,
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              maxWidth: "fit-content",
              marginLeft: "4px",
              fontFamily: theme.fonts?.interRegular,
              children: `${legend.name}: `
            }
          ),
          /* @__PURE__ */ jsx248(
            Text28,
            {
              fontSize: 12 - 2 * deepness,
              fontFamily: theme.fonts?.interSemiBold,
              fontWeight: 600,
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              maxWidth: "fit-content",
              marginLeft: "5px",
              children: `${legend.percentage.toLocaleString(void 0, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
                trailingZeroDisplay: "stripIfInteger"
              })}%`
            }
          )
        ]
      }
    ),
    getLegendsElements(legend, deepness + 1)
  ] }, `${legend.name}-${deepness}`)) });
  return /* @__PURE__ */ jsx248(Box51, { children: getLegendsElements(data) });
};
var SunburstLegend_default = SunburstLegend;

// src/components/Sunburst/ArcLabelsLayer.tsx
import { createElement as createElement3, useMemo as useMemo5 } from "react";
import { radiansToDegrees as radiansToDegrees2, useTheme as useTheme76 } from "@nivo/core";
import { useInheritedColor as useInheritedColor3 } from "@nivo/colors";

// src/components/Sunburst/centers.tsx
import { useMemo as useMemo4 } from "react";
import { useTransition as useTransition2, to } from "@react-spring/web";
import {
  midAngle,
  positionFromAngle as positionFromAngle2
} from "@nivo/core";

// src/components/Sunburst/utils.tsx
import { useMemo as useMemo3 } from "react";
import {
  radiansToDegrees,
  positionFromAngle,
  degreesToRadians
} from "@nivo/core";

// src/components/Sunburst/centers.tsx
import {
  useArcTransitionMode as useArcTransitionMode2
} from "@nivo/arcs";
var computeArcCenter = (arc, offset) => {
  const angle = midAngle(arc) - Math.PI / 2;
  const radius = arc.innerRadius + (arc.outerRadius - arc.innerRadius) * offset;
  return positionFromAngle2(angle, radius);
};
var interpolateArcCenter = (offset) => (startAngleValue, endAngleValue, innerRadiusValue, outerRadiusValue) => to(
  [startAngleValue, endAngleValue, innerRadiusValue, outerRadiusValue],
  (startAngle, endAngle, innerRadius, outerRadius) => {
    const centroid = computeArcCenter(
      { startAngle, endAngle, innerRadius, outerRadius },
      offset
    );
    return `translate(${centroid.x},${centroid.y})`;
  }
);
function easing2(value) {
  return value;
}
var useArcCentersTransition = (data, offset = 0.5, mode = "innerRadius", extra) => {
  const animate = false;
  const springConfig = {
    tension: 0,
    friction: 0,
    frequency: 0,
    damping: 0,
    mass: 0,
    velocity: 0,
    restVelocity: 0,
    precision: 0,
    progress: 0,
    duration: 0,
    easing: easing2(0),
    clamp: true,
    bounce: 0,
    decay: true,
    round: 0
  };
  const phases = useArcTransitionMode2(mode, extra);
  const transition = useTransition2(data, {
    keys: (datum) => datum.id,
    initial: phases.update,
    from: phases.enter,
    enter: phases.update,
    update: phases.update,
    leave: phases.leave,
    config: springConfig,
    immediate: !animate
  });
  return {
    transition,
    interpolate: interpolateArcCenter(offset)
  };
};

// src/components/Sunburst/ArcLabel.tsx
import { animated } from "@react-spring/web";
import { jsx as jsx249 } from "react/jsx-runtime";
var staticStyle = {
  pointerEvents: "none"
};
function convertPercentageFormat(inputString) {
  const resultString = inputString.replace(/\.\d\d(%|$)/, "$1");
  return resultString;
}
var ArcLabel = ({
  label,
  style
}) => {
  const formattedLabel = convertPercentageFormat(label);
  return /* @__PURE__ */ jsx249(
    animated.g,
    {
      transform: style.transform,
      opacity: style.progress,
      style: staticStyle,
      children: /* @__PURE__ */ jsx249(
        animated.text,
        {
          textAnchor: "middle",
          dominantBaseline: "central",
          style: {
            fontSize: "12px",
            fontWeight: 600,
            fill: style.textColor
          },
          children: formattedLabel
        }
      )
    }
  );
};

// src/components/Sunburst/ArcLabelsLayer.tsx
import { jsx as jsx250 } from "react/jsx-runtime";
var ArcLabelsLayer = ({
  center,
  data,
  transitionMode,
  radiusOffset,
  skipAngle,
  textColor,
  component = ArcLabel
}) => {
  const theme = useTheme76();
  const getTextColor = useInheritedColor3(textColor, theme);
  const filteredData = useMemo5(
    () => data.filter((datum) => {
      return Math.abs(
        radiansToDegrees2(datum.arc.endAngle - datum.arc.startAngle)
      ) >= skipAngle;
    }),
    [data, skipAngle]
  );
  const { transition, interpolate } = useArcCentersTransition(
    filteredData,
    radiusOffset,
    transitionMode
  );
  const Label3 = component;
  return /* @__PURE__ */ jsx250("g", { transform: `translate(${center[0]},${center[1]})`, children: transition((transitionProps, datum) => {
    return createElement3(Label3, {
      key: datum.id,
      datum,
      // @ts-ignore
      label: `${datum.percentage.toLocaleString(void 0, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      })}%`,
      style: {
        ...transitionProps,
        transform: interpolate(
          transitionProps.startAngle,
          transitionProps.endAngle,
          transitionProps.innerRadius,
          transitionProps.outerRadius
        ),
        textColor: getTextColor(datum)
      }
    });
  }) });
};

// src/components/Sunburst/Tooltip.tsx
import { memo as memo2 } from "react";
import { useValueFormatter as useValueFormatter2 } from "@nivo/core";
import { Flex as Flex31, Box as Box52, useTheme as useTheme77 } from "@chakra-ui/react";
import { jsx as jsx251, jsxs as jsxs136 } from "react/jsx-runtime";
var BasicTooltip = memo2(
  ({
    id,
    value: _value,
    percentage,
    format,
    enableChip = false,
    color,
    tooltipHideValue,
    tooltipHidePercentage,
    renderContent
  }) => {
    const theme = useTheme77();
    const formatValue = useValueFormatter2(format);
    const Chip2 = memo2(({ size = 12, color: color2, style = {} }) => /* @__PURE__ */ jsx251(
      "span",
      {
        style: {
          display: "block",
          width: size,
          height: size,
          borderRadius: "3px",
          background: color2,
          ...style
        }
      }
    ));
    let content;
    if (typeof renderContent === "function") {
      content = renderContent();
    } else {
      let value = _value;
      if (formatValue !== void 0 && value !== void 0) {
        value = formatValue(value);
      }
      content = /* @__PURE__ */ jsx251(Box52, { "data-testId": "sunburst-basic-tooltip-container", color: "#59595B", children: value !== void 0 ? /* @__PURE__ */ jsxs136("div", { children: [
        /* @__PURE__ */ jsxs136(Flex31, { fontSize: "12px", alignItems: "center", children: [
          enableChip && /* @__PURE__ */ jsx251(Chip2, { color, style: { marginRight: "7px" } }),
          /* @__PURE__ */ jsx251("strong", { children: id })
        ] }),
        !tooltipHideValue && /* @__PURE__ */ jsxs136(Flex31, { fontSize: "12px", children: [
          "Population: ",
          /* @__PURE__ */ jsxs136("strong", { children: [
            "\xA0",
            `${value}`
          ] })
        ] }),
        !tooltipHidePercentage && /* @__PURE__ */ jsxs136(Flex31, { fontSize: "12px", children: [
          "Market Share: ",
          /* @__PURE__ */ jsxs136("strong", { children: [
            "\xA0",
            `${percentage.toLocaleString(void 0, {
              minimumFractionDigits: 1,
              maximumFractionDigits: 1,
              // @ts-ignore
              trailingZeroDisplay: "stripIfInteger"
            })}%`
          ] })
        ] })
      ] }) : id });
    }
    return /* @__PURE__ */ jsx251(
      "div",
      {
        style: {
          background: "white",
          borderRadius: "2px",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
          color: "inherit",
          fontSize: "inherit",
          padding: "5px 9px",
          fontFamily: theme.fonts?.interRegular
        },
        children: content
      }
    );
  }
);

// src/components/Sunburst/index.tsx
import { jsx as jsx252, jsxs as jsxs137 } from "react/jsx-runtime";
var SunburstComp = ({
  info,
  enableLegends = false,
  margin = { top: 10, right: 10, bottom: 10, left: 10 },
  width = 220,
  height = 249,
  radius = 70,
  borderWidth = 3,
  cornerRadius = 6,
  arcsWidth = 23,
  skipAngle = defaultProps2.arcLabelsSkipAngle,
  labelColor = "#FFFFFF",
  value,
  id,
  tooltipHideValue,
  tooltipHidePercentage,
  ...props
}) => {
  const { data } = info || {};
  const total = data.children.reduce((acc, curr) => {
    let valueToAdd = 0;
    if (curr.children && curr.children.length) {
      valueToAdd = curr.children.reduce(
        (accum, current) => accum + current[value],
        0
      );
    } else {
      valueToAdd = curr[value];
    }
    return acc + valueToAdd;
  }, 0);
  function formatData(data2, total2) {
    data2.children.forEach((child) => {
      if (child.children && child.children.length) {
        child.percentage = child.children.reduce(
          (accum, current) => accum + current[value],
          0
        ) / total2 * 100;
        formatData(child, total2);
      } else {
        child.percentage = child[value] / total2 * 100;
      }
    });
  }
  formatData(data, total);
  function extractDataColors(children) {
    const firstLevelColors = [];
    const secondLevelColors = [];
    children.forEach((child) => {
      firstLevelColors.push(child.color);
      if (child.children && child.children.length) {
        child.children.forEach((secondLevelChild) => {
          secondLevelColors.push(secondLevelChild.color);
        });
      }
    });
    return [...firstLevelColors, ...secondLevelColors];
  }
  const colors2 = extractDataColors(data.children);
  const { arcGenerator, nodes } = useSunburst({
    data,
    id,
    value,
    radius,
    cornerRadius,
    colors: colors2,
    arcsWidth,
    inheritColorFromParent: false
  });
  const tooltip = (arg) => {
    return /* @__PURE__ */ jsx252(
      BasicTooltip,
      {
        id: arg.id,
        value: arg.value.toLocaleString(void 0, {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }),
        percentage: arg.percentage,
        enableChip: true,
        color: arg.color,
        tooltipHideValue,
        tooltipHidePercentage
      }
    );
  };
  const CustomArcs = ({ centerX, centerY }) => {
    return /* @__PURE__ */ jsx252(
      Arc2,
      {
        center: [centerX, centerY],
        data: nodes,
        arcGenerator,
        borderWidth,
        borderColor: defaultProps2.borderColor,
        transitionMode: "centerRadius",
        tooltip,
        isInteractive: true
      },
      "arc"
    );
  };
  const CustomArcLabels = ({ centerX, centerY }) => {
    return /* @__PURE__ */ jsx252(
      ArcLabelsLayer,
      {
        center: [centerX, centerY],
        data: nodes,
        label: defaultProps2.arcLabel,
        radiusOffset: defaultProps2.arcLabelsRadiusOffset,
        skipAngle,
        textColor: labelColor,
        transitionMode: defaultProps2.transitionMode
      },
      "arcLabels"
    );
  };
  return /* @__PURE__ */ jsxs137(Flex32, { children: [
    /* @__PURE__ */ jsx252(
      Sunburst,
      {
        data,
        height,
        width,
        margin,
        value,
        id,
        valueFormat: (value2) => `${value2}%`,
        animate: false,
        isInteractive: true,
        layers: [CustomArcs, CustomArcLabels],
        ...props
      }
    ),
    Boolean(enableLegends) && /* @__PURE__ */ jsx252(
      Flex32,
      {
        flexDirection: "column",
        justifyContent: "center",
        borderRadius: "8px",
        padding: "15px",
        backgroundColor: "#F6F8FA",
        maxW: "160px",
        marginLeft: "15px 13px",
        children: /* @__PURE__ */ jsx252(SunburstLegend_default, { data })
      }
    )
  ] });
};
var Sunburst_default = SunburstComp;

// src/components/SelectableBranchesSankey/index.tsx
import { createElement as createElement6, Fragment as Fragment37 } from "react";
import uniq from "lodash/uniq";
import { SvgWrapper, useDimensions, Container } from "@nivo/core";

// src/components/SelectableBranchesSankey/props.ts
import { sankeyCenter, sankeyJustify, sankeyLeft, sankeyRight } from "d3-sankey";

// src/components/SelectableBranchesSankey/SankeyNodeTooltip.tsx
import { BasicTooltip as BasicTooltip2 } from "@nivo/tooltip";
import { jsx as jsx253 } from "react/jsx-runtime";
var SankeyNodeTooltip = ({
  node
}) => /* @__PURE__ */ jsx253(BasicTooltip2, { id: node.label, enableChip: true, color: node.color });

// src/components/SelectableBranchesSankey/SankeyLinkTooltip.tsx
import { BasicTooltip as BasicTooltip3, Chip } from "@nivo/tooltip";
import { jsx as jsx254, jsxs as jsxs138 } from "react/jsx-runtime";
var tooltipStyles = {
  container: {
    display: "flex",
    alignItems: "center"
  },
  sourceChip: {
    marginRight: 7
  },
  targetChip: {
    marginLeft: 7,
    marginRight: 7
  }
};
var SankeyLinkTooltip = ({
  link
}) => /* @__PURE__ */ jsx254(
  BasicTooltip3,
  {
    id: /* @__PURE__ */ jsxs138("span", { style: tooltipStyles.container, children: [
      /* @__PURE__ */ jsx254(Chip, { color: link.source.color, style: tooltipStyles.sourceChip }),
      /* @__PURE__ */ jsx254("strong", { children: link.source.label }),
      " > ",
      /* @__PURE__ */ jsx254("strong", { children: link.target.label }),
      /* @__PURE__ */ jsx254(Chip, { color: link.target.color, style: tooltipStyles.targetChip }),
      /* @__PURE__ */ jsx254("strong", { children: link.formattedValue })
    ] })
  }
);

// src/components/SelectableBranchesSankey/props.ts
var sankeyAlignmentPropMapping = {
  center: sankeyCenter,
  justify: sankeyJustify,
  start: sankeyLeft,
  end: sankeyRight
};
var sankeyAlignmentPropKeys = Object.keys(sankeyAlignmentPropMapping);
var sankeyAlignmentFromProp = (prop) => sankeyAlignmentPropMapping[prop];
var svgDefaultProps = {
  layout: "horizontal",
  align: "center",
  sort: "auto",
  colors: { scheme: "nivo" },
  nodeOpacity: 0.75,
  nodeHoverOpacity: 1,
  nodeHoverOthersOpacity: 0.15,
  nodeThickness: 12,
  nodeSpacing: 12,
  nodeInnerPadding: 0,
  nodeBorderWidth: 1,
  nodeBorderColor: { from: "color", modifiers: [["darker", 0.5]] },
  nodeBorderRadius: 0,
  linkOpacity: 0.25,
  linkHoverOpacity: 0.6,
  linkHoverOthersOpacity: 0.15,
  linkContract: 0,
  linkBlendMode: "multiply",
  enableLinkGradient: false,
  enableLabels: true,
  label: "id",
  labelPosition: "inside",
  labelPadding: 9,
  labelOrientation: "horizontal",
  labelTextColor: { from: "color", modifiers: [["darker", 0.8]] },
  isInteractive: true,
  nodeTooltip: SankeyNodeTooltip,
  linkTooltip: SankeyLinkTooltip,
  legends: [],
  layers: ["links", "nodes", "labels", "legends"],
  role: "img",
  animate: true,
  motionConfig: "gentle"
};

// src/components/SelectableBranchesSankey/hooks.tsx
import { useState as useState18, useMemo as useMemo6 } from "react";
import cloneDeep2 from "lodash/cloneDeep";
import { sankey as d3Sankey } from "d3-sankey";
import { useTheme as useTheme78, usePropertyAccessor as usePropertyAccessor2, useValueFormatter as useValueFormatter3 } from "@nivo/core";
import { useOrdinalColorScale as useOrdinalColorScale2, useInheritedColor as useInheritedColor4 } from "@nivo/colors";
var getId = (node) => node.id;
var computeNodeAndLinks = ({
  data: _data,
  formatValue,
  alignFunction,
  sortFunction,
  linkSortMode,
  nodeThickness,
  nodeSpacing,
  nodeInnerPadding,
  width,
  height,
  getColor: getColor3,
  getLabel
}) => {
  const sankey = d3Sankey().nodeAlign(alignFunction).nodeSort(sortFunction).linkSort(linkSortMode).nodeWidth(nodeThickness).nodePadding(nodeSpacing).size([width, height]).nodeId(getId);
  const data = cloneDeep2(_data);
  sankey(data);
  const nodesByLayers = data.nodes.reduce(
    (acc, node) => {
      if (!acc[node.layer]) {
        acc[node.layer] = [];
      }
      acc[node.layer].push(node);
      return acc;
    },
    []
  );
  const linksByLayers = data.links.reduce(
    (acc, link) => {
      if (!acc[link.source.layer]) {
        acc[link.source.layer] = [];
      }
      acc[link.source.layer].push(link);
      return acc;
    },
    []
  );
  const highestLayerIndex = nodesByLayers[nodesByLayers.length - 1][0].layer;
  for (let i = nodesByLayers.length - 1; i >= 0; i--) {
    const currentLayerNodes = nodesByLayers[i];
    const currentLayerLinks = linksByLayers[i - 1];
    let accLayerLinksThickness = [];
    let accNodeHeights = 0;
    let accGaps = 0;
    currentLayerNodes.forEach((node) => {
      node.color = getColor3(node);
      node.label = getLabel(node);
      node.formattedValue = formatValue(node.value);
      node.width = 8;
      node.x = node.x0 + nodeInnerPadding / 2 + 3;
      node.x0 = node.x0 + nodeInnerPadding / 2;
      node.x1 = node.x0 + 8 + 6;
      node.height = Math.max(node.y1 - node.y0, 1);
    });
    if (i === highestLayerIndex) {
      accLayerLinksThickness = [];
      currentLayerNodes.sort((a, b) => {
        if (a.id === "Cell-size excluded facilities") return 1;
        if (b.id === "Cell-size excluded facilities") return -1;
        if (a.id === "Others") return 1;
        if (b.id === "Others") return -1;
        if (a.id === "Other Specialties") return 1;
        if (b.id === "Other Specialties") return -1;
        return b.value - a.value;
      }).forEach((node) => {
        const shouldReduce = node.height > 25;
        node.height = shouldReduce ? 25 : node.height;
        const subIndex = currentLayerNodes.findIndex(
          (innerNode) => innerNode.id === node.id
        );
        const nextNodeExists = Boolean(currentLayerNodes[subIndex + 1]);
        let calculatedGap = 0;
        if (nextNodeExists) {
          let nextNodeHeight = Math.max(
            currentLayerNodes[subIndex + 1].y1 - currentLayerNodes[subIndex + 1].y0,
            1
          );
          const shouldReduceNextNode = nextNodeHeight > 25;
          nextNodeHeight = shouldReduceNextNode ? 25 : nextNodeHeight;
          calculatedGap = nodeSpacing - node.height / 2 - nextNodeHeight / 2;
        }
        node.y0 = accNodeHeights + accGaps;
        node.y1 = node.y0 + 25;
        node.y = node.y0;
        node.nextGap = calculatedGap;
        accNodeHeights += node.height;
        accGaps += calculatedGap;
      });
      currentLayerLinks.sort((a, b) => {
        if (a.target.id === "Cell-size excluded facilities") return 1;
        if (b.target.id === "Cell-size excluded facilities") return -1;
        if (a.target.id === "Others") return 1;
        if (b.target.id === "Others") return -1;
        if (a.target.id === "Other Specialties") return 1;
        if (b.target.id === "Other Specialties") return -1;
        return b.value - a.value;
      }).forEach((link) => {
        const secondLayerNodeSubIndex = nodesByLayers[i - 1].findIndex(
          (innerNode) => innerNode.id === link.source.id
        );
        if (!accLayerLinksThickness[secondLayerNodeSubIndex]) {
          accLayerLinksThickness[secondLayerNodeSubIndex] = 0;
        }
        const subIndex = nodesByLayers[i].findIndex(
          (innerNode) => innerNode.id === link.target.id
        );
        link.thickness = currentLayerNodes[subIndex].height;
        link.pos0 = nodesByLayers[i - 1][secondLayerNodeSubIndex].y0 + accLayerLinksThickness[secondLayerNodeSubIndex] + link.thickness / 2;
        link.pos1 = subIndex === 0 ? currentLayerNodes[subIndex].y + link.thickness / 2 : currentLayerNodes[subIndex].y + link.thickness / 2;
        accLayerLinksThickness[secondLayerNodeSubIndex] += currentLayerNodes[subIndex].height;
      });
    } else if (i === 0) {
      currentLayerNodes.forEach((node) => {
        node.height = node.sourceLinks.reduce((acc, link) => {
          return acc + link.target.height;
        }, 0);
        node.y0 = height / 2 - node.height / 2;
        node.y = node.y0;
        node.y1 = node.y0 + node.height;
      });
    } else {
      accLayerLinksThickness = [];
      currentLayerNodes.forEach((node) => {
        node.height = node.sourceLinks.reduce((acc, link) => {
          return acc + link.target.height;
        }, 0);
        if (node.height < 12) {
          node.height = 12;
        }
        node.y = node.y0;
        node.y1 = node.y0 + node.height;
      });
      if (i === 1) {
        currentLayerLinks.sort((a, b) => {
          return b.value - a.value;
        }).forEach((link) => {
          const firstLayerNodeSubIndex = nodesByLayers[0].findIndex(
            (innerNode) => innerNode.id === link.source.id
          );
          if (!accLayerLinksThickness[firstLayerNodeSubIndex]) {
            accLayerLinksThickness[firstLayerNodeSubIndex] = 0;
          }
          link.thickness = link.target.height;
          link.pos0 = height / 2 + accLayerLinksThickness[firstLayerNodeSubIndex] + link.thickness / 2 - currentLayerLinks.reduce((acc, link2) => {
            return acc + link2.target.height;
          }, 0) / 2;
          link.pos1 = link.target.y0 + link.target.height / 2;
          accLayerLinksThickness[firstLayerNodeSubIndex] += link.target.height;
        });
      } else {
        currentLayerLinks.forEach((link) => {
          link.thickness = link.target.height;
          link.pos0 = link.source.y0 + link.thickness / 2;
          link.pos1 = link.target.y0 + link.target.height / 2;
        });
      }
    }
  }
  const formattedNodes = nodesByLayers.flat();
  const formattedLinks = linksByLayers.flat();
  return {
    nodes: formattedNodes,
    links: formattedLinks
  };
};
var useSankey = ({
  data,
  valueFormat,
  layout,
  width,
  height,
  sort,
  align,
  colors: colors2,
  nodeThickness,
  nodeSpacing,
  nodeInnerPadding,
  nodeBorderColor,
  label,
  labelTextColor
}) => {
  const [currentNode, setCurrentNode] = useState18(
    null
  );
  const [currentLink, setCurrentLink] = useState18(
    null
  );
  const sortFunction = useMemo6(() => {
    if (sort === "auto") return void 0;
    if (sort === "input") return null;
    if (sort === "ascending") {
      return (a, b) => {
        if (a.id === "Others" || a.id === "Other Specialties") {
          if (b.id === "Cell-size excluded facilities") {
            return -1;
          }
          return 1;
        }
        return a.value - b.value;
      };
    }
    if (sort === "descending") {
      return (a, b) => {
        if (a.id === "Others" || a.id === "Other Specialties") {
          if (b.id === "Cell-size excluded facilities") {
            return -1;
          }
          return 1;
        }
        return b.value - a.value;
      };
    }
    return sort;
  }, [sort]);
  const linkSortMode = sort === "input" ? null : void 0;
  const alignFunction = useMemo6(() => {
    if (typeof align === "function") return align;
    return sankeyAlignmentFromProp(align);
  }, [align]);
  const theme = useTheme78();
  const getColor3 = useOrdinalColorScale2(colors2, "id");
  const getNodeBorderColor = useInheritedColor4(nodeBorderColor, theme);
  const getLabel = usePropertyAccessor2(label);
  const getLabelTextColor = useInheritedColor4(labelTextColor, theme);
  const formatValue = useValueFormatter3(valueFormat);
  const { nodes, links } = useMemo6(
    () => computeNodeAndLinks({
      data,
      formatValue,
      layout,
      alignFunction,
      sortFunction,
      linkSortMode,
      nodeThickness,
      nodeSpacing,
      nodeInnerPadding,
      width,
      height,
      getColor: getColor3,
      getLabel
    }),
    [
      data,
      formatValue,
      layout,
      alignFunction,
      sortFunction,
      linkSortMode,
      nodeThickness,
      nodeSpacing,
      nodeInnerPadding,
      width,
      height,
      getColor3,
      getLabel
    ]
  );
  const legendData = useMemo6(
    () => nodes.map((node) => ({
      id: node.id,
      label: node.label,
      color: node.color
    })),
    [nodes]
  );
  return {
    nodes,
    links,
    legendData,
    getNodeBorderColor,
    currentNode,
    setCurrentNode,
    currentLink,
    setCurrentLink,
    getLabelTextColor
  };
};

// src/components/SelectableBranchesSankey/SankeyNodesItem.tsx
import { createElement as createElement4, useCallback } from "react";
import { useSpring, animated as animated2 } from "@react-spring/web";
import { useMotionConfig } from "@nivo/core";
import { useTooltip as useTooltip3 } from "@nivo/tooltip";
import { jsx as jsx255 } from "react/jsx-runtime";
var SankeyNodesItem = ({
  node,
  x,
  y,
  width,
  height,
  color,
  opacity,
  borderWidth,
  borderColor,
  borderRadius,
  setCurrent,
  isInteractive,
  onClick,
  tooltip,
  layer
}) => {
  const { animate, config: springConfig } = useMotionConfig();
  const animatedProps = useSpring({
    x,
    y,
    width,
    height,
    opacity,
    color,
    config: springConfig,
    immediate: !animate
  });
  const { showTooltipFromEvent, hideTooltip } = useTooltip3();
  const handleMouseEnter = useCallback(
    (event) => {
      setCurrent(node);
      showTooltipFromEvent(createElement4(tooltip, { node }), event, "left");
    },
    [setCurrent, node, showTooltipFromEvent, tooltip]
  );
  const handleMouseMove = useCallback(
    (event) => {
      showTooltipFromEvent(createElement4(tooltip, { node }), event, "left");
    },
    [showTooltipFromEvent, node, tooltip]
  );
  const handleMouseLeave = useCallback(() => {
    setCurrent(null);
    hideTooltip();
  }, [setCurrent, hideTooltip]);
  const handleClick = useCallback(
    (event) => {
      onClick?.(node, event);
    },
    [onClick, node]
  );
  const enableCursorPointer = layer === 1 && node.id !== "Other Specialties" ? "pointer" : "initial";
  return /* @__PURE__ */ jsx255(
    animated2.rect,
    {
      cursor: enableCursorPointer,
      x: animatedProps.x,
      y: animatedProps.y,
      rx: borderRadius,
      ry: borderRadius,
      width: animatedProps.width.to((v) => Math.max(v, 0)),
      height: animatedProps.height.to((v) => Math.max(v, 0)),
      fill: animatedProps.color,
      fillOpacity: animatedProps.opacity,
      strokeWidth: borderWidth,
      stroke: borderColor,
      strokeOpacity: opacity,
      onMouseEnter: isInteractive ? handleMouseEnter : void 0,
      onMouseMove: isInteractive ? handleMouseMove : void 0,
      onMouseLeave: isInteractive ? handleMouseLeave : void 0,
      onClick: isInteractive ? handleClick : void 0
    }
  );
};

// src/components/SelectableBranchesSankey/SankeyNodes.tsx
import { Fragment as Fragment33, jsx as jsx256 } from "react/jsx-runtime";
var SankeyNodes = ({
  nodes,
  nodeOpacity,
  nodeHoverOpacity,
  nodeHoverOthersOpacity,
  borderWidth,
  getBorderColor,
  borderRadius,
  setCurrentNode,
  currentNode,
  currentLink,
  isCurrentNode,
  isInteractive,
  onClick,
  tooltip
}) => {
  const getOpacity = (node) => {
    if (!currentNode && !currentLink) return nodeOpacity;
    if (isCurrentNode(node)) return nodeHoverOpacity;
    return nodeHoverOthersOpacity;
  };
  return /* @__PURE__ */ jsx256(Fragment33, { children: nodes.map((node) => /* @__PURE__ */ jsx256(
    SankeyNodesItem,
    {
      node,
      x: node.x,
      y: node.y,
      width: node.width,
      height: node.height,
      color: node.color,
      opacity: getOpacity(node),
      borderWidth,
      borderColor: getBorderColor(node),
      borderRadius,
      setCurrent: setCurrentNode,
      isInteractive,
      onClick,
      tooltip,
      layer: node.layer
    },
    node.id
  )) });
};

// src/components/SelectableBranchesSankey/links.ts
import { line, curveMonotoneX, curveMonotoneY } from "d3-shape";
var sankeyLinkHorizontal = () => {
  const lineGenerator = line().curve(curveMonotoneX);
  return (link, contract) => {
    const thickness = Math.max(1, link.thickness - contract * 2);
    const halfThickness = thickness / 2;
    const linkLength = link.target.x0 - link.source.x1;
    const padLength = linkLength * 0.12;
    const dots = [
      [link.source.x1, link.pos0 - halfThickness],
      [link.source.x1 + padLength, link.pos0 - halfThickness],
      [link.target.x0 - padLength, link.pos1 - halfThickness],
      [link.target.x0, link.pos1 - halfThickness],
      [link.target.x0, link.pos1 + halfThickness],
      [link.target.x0 - padLength, link.pos1 + halfThickness],
      [link.source.x1 + padLength, link.pos0 + halfThickness],
      [link.source.x1, link.pos0 + halfThickness],
      [link.source.x1, link.pos0 - halfThickness]
    ];
    return lineGenerator(dots) + "Z";
  };
};

// src/components/SelectableBranchesSankey/SankeyLinksItem.tsx
import { createElement as createElement5, useCallback as useCallback2 } from "react";
import { useSpring as useSpring2, animated as animated3 } from "@react-spring/web";
import { useAnimatedPath, useMotionConfig as useMotionConfig2 } from "@nivo/core";
import { useTooltip as useTooltip4 } from "@nivo/tooltip";

// src/components/SelectableBranchesSankey/SankeyLinkGradient.tsx
import { jsx as jsx257, jsxs as jsxs139 } from "react/jsx-runtime";
var SankeyLinkGradient = ({
  id,
  startColor,
  endColor
}) => {
  const gradientProps = {
    x1: "0%",
    x2: "100%",
    y1: "0%",
    y2: "0%"
  };
  return /* @__PURE__ */ jsxs139("linearGradient", { id, spreadMethod: "pad", ...gradientProps, children: [
    /* @__PURE__ */ jsx257("stop", { offset: "0%", stopColor: startColor }),
    /* @__PURE__ */ jsx257("stop", { offset: "100%", stopColor: endColor })
  ] });
};

// src/components/SelectableBranchesSankey/SankeyLinksItem.tsx
import { Fragment as Fragment34, jsx as jsx258, jsxs as jsxs140 } from "react/jsx-runtime";
var SankeyLinksItem = ({
  link,
  layout,
  path,
  color,
  opacity,
  blendMode,
  enableGradient,
  setCurrent,
  tooltip,
  isInteractive,
  onClick,
  layer
}) => {
  const linkId = `${link.source.id}.${link.target.id}.${link.index}`;
  const { animate, config: springConfig } = useMotionConfig2();
  const animatedPath = useAnimatedPath(path);
  const animatedProps = useSpring2({
    color,
    opacity,
    config: springConfig,
    immediate: !animate
  });
  const { showTooltipFromEvent, hideTooltip } = useTooltip4();
  const handleMouseEnter = useCallback2(
    (event) => {
      setCurrent(link);
      showTooltipFromEvent(createElement5(tooltip, { link }), event, "left");
    },
    [setCurrent, link, showTooltipFromEvent, tooltip]
  );
  const handleMouseMove = useCallback2(
    (event) => {
      showTooltipFromEvent(createElement5(tooltip, { link }), event, "left");
    },
    [showTooltipFromEvent, link, tooltip]
  );
  const handleMouseLeave = useCallback2(() => {
    setCurrent(null);
    hideTooltip();
  }, [setCurrent, hideTooltip]);
  const handleClick = useCallback2(
    (event) => {
      onClick?.(link, event);
    },
    [onClick, link]
  );
  const enableCursorPointer = layer === 0 && link.target.id !== "Other Specialties" ? "pointer" : "initial";
  return /* @__PURE__ */ jsxs140(Fragment34, { children: [
    enableGradient && /* @__PURE__ */ jsx258(
      SankeyLinkGradient,
      {
        id: linkId,
        layout,
        startColor: link.startColor || link.source.color,
        endColor: link.endColor || link.target.color
      }
    ),
    /* @__PURE__ */ jsx258(
      animated3.path,
      {
        cursor: enableCursorPointer,
        fill: enableGradient ? `url("#${encodeURI(linkId)}")` : animatedProps.color,
        d: animatedPath,
        fillOpacity: animatedProps.opacity,
        onMouseEnter: isInteractive ? handleMouseEnter : void 0,
        onMouseMove: isInteractive ? handleMouseMove : void 0,
        onMouseLeave: isInteractive ? handleMouseLeave : void 0,
        onClick: isInteractive ? handleClick : void 0,
        style: { mixBlendMode: blendMode }
      }
    )
  ] });
};

// src/components/SelectableBranchesSankey/SankeyLinks.tsx
import { useMemo as useMemo7 } from "react";
import { Fragment as Fragment35, jsx as jsx259 } from "react/jsx-runtime";
var SankeyLinks = ({
  links,
  layout,
  linkOpacity,
  linkHoverOpacity,
  linkHoverOthersOpacity,
  linkContract,
  linkBlendMode,
  enableLinkGradient,
  setCurrentLink,
  currentLink,
  currentNode,
  isCurrentLink,
  isInteractive,
  onClick,
  tooltip
}) => {
  const getOpacity = (link) => {
    if (!currentNode && !currentLink) return linkOpacity;
    if (isCurrentLink(link)) return linkHoverOpacity;
    return linkHoverOthersOpacity;
  };
  const getLinkPath = useMemo7(() => sankeyLinkHorizontal(), [layout]);
  return /* @__PURE__ */ jsx259(Fragment35, { children: links.map((link) => /* @__PURE__ */ jsx259(
    SankeyLinksItem,
    {
      link,
      layout,
      path: getLinkPath(link, linkContract),
      color: link.color,
      opacity: getOpacity(link),
      blendMode: linkBlendMode,
      enableGradient: enableLinkGradient,
      setCurrent: setCurrentLink,
      isInteractive,
      onClick,
      tooltip,
      layer: link.source.layer
    },
    `${link.source.id}.${link.target.id}.${link.index}`
  )) });
};

// src/components/SelectableBranchesSankey/SankeyLabels.tsx
import { useSprings, animated as animated4 } from "@react-spring/web";
import { useTheme as useTheme79, useMotionConfig as useMotionConfig3 } from "@nivo/core";
import { useLayoutEffect as useLayoutEffect3, useRef as useRef11, useState as useState19 } from "react";
import { Fragment as Fragment36, jsx as jsx260, jsxs as jsxs141 } from "react/jsx-runtime";
var SankeyLabels = ({
  nodes,
  width,
  labelPadding,
  getLabelTextColor,
  valueFormat,
  hideTotal = false
}) => {
  const [textWidth, setTextWidth] = useState19(0);
  const textRef = useRef11(null);
  const theme = useTheme79();
  useLayoutEffect3(() => {
    if (textRef.current && textRef.current.getBBox) {
      const bbox = textRef.current.getBBox();
      setTextWidth(bbox.width);
    }
  }, []);
  const labelRotation = 0;
  const labels = nodes.map((node) => {
    let x;
    let textAnchor;
    const y = node.y + node.height / 2;
    if (node.x < width / 2) {
      x = node.x - labelPadding;
      textAnchor = "end";
    } else {
      x = node.x1 + labelPadding;
      textAnchor = "start";
    }
    return {
      id: node.id,
      label: node.layer !== 2 ? node.label : "",
      layer: node.layer,
      secondaryLabel: node.layer === 1 ? node.value : "",
      x,
      y,
      textAnchor,
      color: getLabelTextColor(node)
    };
  });
  const { animate, config: springConfig } = useMotionConfig3();
  const springs = useSprings(
    labels.length,
    labels.map((label) => ({
      transform: `translate(${label.x}, ${label.y}) rotate(${labelRotation})`,
      color: label.color,
      config: springConfig,
      immediate: !animate
    }))
  );
  const currencyFormatter = (value) => Intl.NumberFormat("en", {
    currency: "USD",
    style: "currency",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
  const formatValue = (value) => {
    if (typeof valueFormat === "function") {
      return valueFormat(value);
    }
    return currencyFormatter(value);
  };
  const splitLabel = (label) => {
    const words = label.split(" ");
    const groupedWords = [];
    for (let i = 0; i < words.length; i += 3) {
      const group = words.slice(i, i + 3).join(" ");
      groupedWords.push(group);
    }
    return groupedWords;
  };
  const INITIAL_LABEL_OFFSET = -18;
  const LINE_OFFSET = 12;
  return /* @__PURE__ */ jsx260(Fragment36, { children: springs.map((animatedProps, index) => {
    const label = labels[index];
    const splittedLabel = splitLabel(label.label);
    return /* @__PURE__ */ jsxs141(
      animated4.g,
      {
        transform: animatedProps.transform,
        style: {
          fill: "#EBF7FC",
          color: "#EBF7FC",
          backgroundColor: "#EBF7FC"
        },
        children: [
          splittedLabel.map((phrase, index2) => /* @__PURE__ */ jsx260(
            animated4.text,
            {
              dominantBaseline: "central",
              textAnchor: label.textAnchor,
              x: 2,
              y: INITIAL_LABEL_OFFSET * splittedLabel.length + index2 * LINE_OFFSET,
              style: {
                ...theme.labels.text,
                fontSize: 11,
                fontWeight: "bold",
                fill: "#59595B",
                backgroundColor: "#EBF7FC",
                pointerEvents: "none"
              },
              children: phrase
            },
            phrase
          )),
          label.layer === 1 && !hideTotal && /* @__PURE__ */ jsxs141(animated4.g, { children: [
            /* @__PURE__ */ jsx260(
              animated4.rect,
              {
                fill: "#EBF7FC",
                height: 16,
                width: textWidth + 20,
                rx: 5,
                ry: 5,
                y: -8,
                x: -textWidth - 13
              }
            ),
            /* @__PURE__ */ jsx260(
              animated4.text,
              {
                ref: textRef,
                dominantBaseline: "central",
                textAnchor: label.textAnchor,
                style: {
                  ...theme.labels.text,
                  fontSize: 11,
                  fontWeight: "bold",
                  fill: "#59595B",
                  backgroundColor: "#EBF7FC",
                  pointerEvents: "none"
                },
                children: formatValue(+label.secondaryLabel)
              }
            )
          ] })
        ]
      },
      label.id
    );
  }) });
};

// src/components/SelectableBranchesSankey/index.tsx
import { jsx as jsx261 } from "react/jsx-runtime";
var InnerSankey = ({
  data,
  valueFormat,
  layout = svgDefaultProps.layout,
  sort = svgDefaultProps.sort,
  align = svgDefaultProps.align,
  width,
  height,
  margin: partialMargin,
  colors: colors2 = svgDefaultProps.colors,
  nodeThickness = svgDefaultProps.nodeThickness,
  nodeSpacing = svgDefaultProps.nodeThickness,
  nodeInnerPadding = svgDefaultProps.nodeInnerPadding,
  nodeBorderColor = svgDefaultProps.nodeBorderColor,
  nodeOpacity = svgDefaultProps.nodeOpacity,
  nodeHoverOpacity = svgDefaultProps.nodeHoverOpacity,
  nodeHoverOthersOpacity = svgDefaultProps.nodeHoverOthersOpacity,
  nodeBorderWidth = svgDefaultProps.nodeBorderWidth,
  nodeBorderRadius = svgDefaultProps.nodeBorderRadius,
  linkOpacity = svgDefaultProps.linkOpacity,
  linkHoverOpacity = svgDefaultProps.linkHoverOpacity,
  linkHoverOthersOpacity = svgDefaultProps.linkHoverOthersOpacity,
  linkContract = svgDefaultProps.linkContract,
  linkBlendMode = svgDefaultProps.linkBlendMode,
  enableLinkGradient = svgDefaultProps.enableLinkGradient,
  labelPosition = svgDefaultProps.labelPosition,
  labelPadding = svgDefaultProps.labelPadding,
  labelOrientation = svgDefaultProps.labelOrientation,
  label = svgDefaultProps.label,
  labelTextColor = svgDefaultProps.labelTextColor,
  nodeTooltip = svgDefaultProps.nodeTooltip,
  linkTooltip = svgDefaultProps.linkTooltip,
  isInteractive,
  onClick,
  layers = svgDefaultProps.layers,
  role = svgDefaultProps.role,
  ariaLabel,
  ariaLabelledBy,
  ariaDescribedBy,
  hideTotalsLabels = false
}) => {
  const { margin, innerWidth, innerHeight, outerWidth, outerHeight } = useDimensions(width, height, partialMargin);
  const {
    nodes,
    links,
    getNodeBorderColor,
    currentNode,
    setCurrentNode,
    currentLink,
    setCurrentLink,
    getLabelTextColor
  } = useSankey({
    data,
    valueFormat,
    layout,
    width: innerWidth,
    height: innerHeight,
    sort,
    align,
    colors: colors2,
    nodeThickness,
    nodeSpacing,
    nodeInnerPadding,
    nodeBorderColor,
    label,
    labelTextColor
  });
  let isCurrentNode = () => false;
  let isCurrentLink = () => false;
  if (currentLink) {
    isCurrentNode = ({ id }) => id === currentLink.source.id || id === currentLink.target.id;
    isCurrentLink = ({ source, target }) => source.id === currentLink.source.id && target.id === currentLink.target.id;
  }
  if (currentNode) {
    let currentNodeIds = [currentNode.id];
    links.filter(
      ({ source, target }) => source.id === currentNode.id || target.id === currentNode.id
    ).forEach(({ source, target }) => {
      currentNodeIds.push(source.id);
      currentNodeIds.push(target.id);
    });
    currentNodeIds = uniq(currentNodeIds);
    isCurrentNode = ({ id }) => currentNodeIds.includes(id);
    isCurrentLink = ({ source, target }) => source.id === currentNode.id || target.id === currentNode.id;
  }
  const layerProps = {
    links,
    nodes,
    margin,
    width,
    height,
    outerWidth,
    outerHeight
  };
  const layerById = {
    links: null,
    nodes: null,
    labels: null,
    legends: null
  };
  layerById.links = /* @__PURE__ */ jsx261(
    SankeyLinks,
    {
      links,
      layout,
      linkContract,
      linkOpacity,
      linkHoverOpacity,
      linkHoverOthersOpacity,
      linkBlendMode,
      enableLinkGradient,
      setCurrentLink,
      currentNode,
      currentLink,
      isCurrentLink,
      isInteractive: isInteractive || false,
      onClick,
      tooltip: linkTooltip
    },
    "links"
  );
  layerById.nodes = /* @__PURE__ */ jsx261(
    SankeyNodes,
    {
      nodes,
      nodeOpacity,
      nodeHoverOpacity,
      nodeHoverOthersOpacity,
      borderWidth: nodeBorderWidth,
      borderRadius: nodeBorderRadius,
      getBorderColor: getNodeBorderColor,
      setCurrentNode,
      currentNode,
      currentLink,
      isCurrentNode,
      isInteractive: isInteractive || false,
      onClick,
      tooltip: nodeTooltip
    },
    "nodes"
  );
  layerById.labels = /* @__PURE__ */ jsx261(
    SankeyLabels,
    {
      nodes,
      layout,
      width: innerWidth,
      height: innerHeight,
      labelPosition,
      labelPadding,
      labelOrientation,
      getLabelTextColor,
      valueFormat,
      hideTotal: hideTotalsLabels
    },
    "labels"
  );
  return /* @__PURE__ */ jsx261(
    SvgWrapper,
    {
      width: outerWidth,
      height: outerHeight,
      margin,
      role,
      ariaLabel,
      ariaLabelledBy,
      ariaDescribedBy,
      children: layers.map((layer, i) => {
        if (typeof layer === "function") {
          return /* @__PURE__ */ jsx261(Fragment37, { children: createElement6(layer, layerProps) }, i);
        }
        return layerById?.[layer] ?? null;
      })
    }
  );
};
var Sankey = ({
  isInteractive = svgDefaultProps.isInteractive,
  animate = false,
  theme,
  renderWrapper,
  ...otherProps
}) => /* @__PURE__ */ jsx261(
  Container,
  {
    ...{
      animate,
      isInteractive,
      renderWrapper,
      theme
    },
    children: /* @__PURE__ */ jsx261(InnerSankey, { isInteractive, ...otherProps })
  }
);

// src/components/SpecialtyReportsList/index.tsx
import { Grid as Grid3, GridItem as GridItem3 } from "@chakra-ui/react";

// src/components/RelatedReportGroupTiles/index.tsx
import { Flex as Flex33, Text as Text29, Button as Button5, useTheme as useTheme80 } from "@chakra-ui/react";
import { jsx as jsx262, jsxs as jsxs142 } from "react/jsx-runtime";
var RelatedReportGroupTiles = ({ category, description = "", icon, handleOpenModal, relatedReports }) => {
  const theme = useTheme80();
  return /* @__PURE__ */ jsxs142(
    Flex33,
    {
      borderRadius: "8px",
      overflow: "hidden",
      minWidth: "372px",
      p: "25px 15px 15px 15px",
      bg: "white",
      height: "100%",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "all ease 0.2s",
      boxShadow: "none",
      border: `2px solid ${theme.colors.lightGrey}`,
      _hover: {
        borderColor: theme.colors.blueOne
      },
      children: [
        /* @__PURE__ */ jsxs142(
          Flex33,
          {
            alignItems: description ? "flex-start" : "center",
            marginBottom: "6px",
            children: [
              /* @__PURE__ */ jsx262(Flex33, { width: "34px", height: "34px", children: icon }),
              /* @__PURE__ */ jsxs142(Flex33, { flexDirection: "column", gap: "10px", children: [
                /* @__PURE__ */ jsx262(
                  Text29,
                  {
                    marginLeft: "6px",
                    color: theme.colors?.blueOne,
                    textStyle: "inter-medium-16-20",
                    children: category
                  }
                ),
                Boolean(description) && /* @__PURE__ */ jsx262(
                  Text29,
                  {
                    marginLeft: "6px",
                    color: theme.colors?.mediumGrey,
                    fontSize: "14px",
                    fontWeight: 400,
                    children: description
                  }
                )
              ] })
            ]
          }
        ),
        /* @__PURE__ */ jsx262(
          Button5,
          {
            marginTop: "10px",
            width: "100%",
            height: theme.sizes[9],
            padding: "0",
            backgroundColor: theme.colors?.blueTwo,
            color: theme.colors?.white,
            transition: "all ease 0.2s",
            _hover: {
              backgroundColor: theme.colors.darkBlue
            },
            onClick: () => {
              handleOpenModal({ category, icon, relatedReports });
            },
            children: /* @__PURE__ */ jsxs142(Flex33, { alignItems: "center", gap: "6px", children: [
              /* @__PURE__ */ jsxs142(Text29, { textStyle: "inter-semiBold-14-17", fontSize: "14px", children: [
                " ",
                "Related Reports",
                " "
              ] }),
              /* @__PURE__ */ jsx262(AngleIcon_default, { color: theme.colors?.white, direction: "right" })
            ] }, category)
          }
        )
      ]
    }
  );
};
var RelatedReportGroupTiles_default = RelatedReportGroupTiles;

// src/components/RelatedReportsModal/index.tsx
import {
  Modal as Modal3,
  ModalContent as ModalContent3,
  ModalHeader as ModalHeader2,
  ModalBody as ModalBody2,
  Text as Text30,
  Flex as Flex34,
  GridItem as GridItem2,
  Grid as Grid2,
  ModalOverlay as ModalOverlay3,
  useTheme as useTheme81,
  ModalCloseButton as ModalCloseButton2
} from "@chakra-ui/react";
import { jsx as jsx263, jsxs as jsxs143 } from "react/jsx-runtime";
var RelatedReportsModal = ({
  isOpen,
  handleCloseModal,
  specialtyData: { icon, category, relatedReports },
  clickOnReportTileHandler
}) => {
  const theme = useTheme81();
  return /* @__PURE__ */ jsxs143(Modal3, { isCentered: true, onClose: handleCloseModal, isOpen, children: [
    /* @__PURE__ */ jsx263(ModalOverlay3, {}),
    /* @__PURE__ */ jsxs143(ModalContent3, { width: "fit-content", minWidth: "860px", minHeight: "530px", children: [
      /* @__PURE__ */ jsx263(ModalHeader2, { pb: "0", children: /* @__PURE__ */ jsxs143(Flex34, { alignItems: "center", gap: "8px", children: [
        icon,
        /* @__PURE__ */ jsxs143(Flex34, { alignItems: "baseline", gap: "8px", children: [
          /* @__PURE__ */ jsx263(Text30, { color: theme.colors?.blueOne, textStyle: "inter-semiBold-24-30", children: category }),
          /* @__PURE__ */ jsx263(
            Text30,
            {
              textStyle: "inter-semiBold-16-16",
              color: theme.colors?.darkBlue,
              children: "Reports"
            }
          )
        ] }),
        /* @__PURE__ */ jsx263(ModalCloseButton2, {})
      ] }) }),
      /* @__PURE__ */ jsx263(ModalBody2, { p: "18px", bgColor: "transparent", children: /* @__PURE__ */ jsx263(
        Grid2,
        {
          maxH: "660px",
          gridTemplateColumns: "auto 2fr",
          gridGap: "12px",
          maxHeight: "530px",
          overflowY: "auto",
          p: "12px 6px",
          css: {
            "&::-webkit-scrollbar": {
              width: 0,
              height: 0
            }
          },
          children: relatedReports?.map((report, idx) => {
            return /* @__PURE__ */ jsx263(GridItem2, { children: /* @__PURE__ */ jsx263(
              ReportCard_default,
              {
                variant: "default",
                ...report,
                onClick: () => clickOnReportTileHandler(report.url, report.title)
              }
            ) }, idx);
          })
        }
      ) })
    ] })
  ] });
};
var RelatedReportsModal_default = RelatedReportsModal;

// src/components/SpecialtyReportsList/index.tsx
import { Fragment as Fragment38, jsx as jsx264, jsxs as jsxs144 } from "react/jsx-runtime";
var SpecialtyReportList = ({
  relatedReportsList,
  handleOpenModal,
  isOpen,
  handleCloseModal,
  modalData,
  clickOnReportTileHandler
}) => {
  const openModalHandler = (args) => {
    handleOpenModal(args);
  };
  const finalTiles = relatedReportsList?.filter(
    (category) => category.relatedReports?.length
  );
  return /* @__PURE__ */ jsxs144(Fragment38, { children: [
    /* @__PURE__ */ jsx264(
      RelatedReportsModal_default,
      {
        isOpen,
        specialtyData: modalData,
        handleCloseModal,
        clickOnReportTileHandler
      }
    ),
    /* @__PURE__ */ jsx264(Grid3, { templateColumns: "repeat(3, 1fr)", gridGap: "30px", children: finalTiles?.map((category) => /* @__PURE__ */ jsx264(GridItem3, { children: /* @__PURE__ */ jsx264(
      RelatedReportGroupTiles_default,
      {
        description: category.description,
        relatedReports: category.relatedReports || [],
        category: category.category || "N/A",
        icon: category.icon || null,
        handleOpenModal: openModalHandler
      },
      category.category
    ) }, category.category)) })
  ] });
};
var SpecialtyReportsList_default = SpecialtyReportList;

// src/components/DoubleFillChart/DoubleFillChart.tsx
import { ResponsiveBar as ResponsiveBar3 } from "@nivo/bar";
import { ResponsiveStream } from "@nivo/stream";

// src/components/DoubleFillChart/labeledArrow.tsx
import { Box as Box53, Flex as Flex35, Text as Text31, useTheme as useTheme82 } from "@chakra-ui/react";
import { jsx as jsx265, jsxs as jsxs145 } from "react/jsx-runtime";
var LabeledArrow = ({
  rightText,
  leftText,
  above,
  height,
  stroke,
  leftTextFontSize = "14px",
  rightTextFontSize = "14px"
}) => {
  const theme = useTheme82();
  return /* @__PURE__ */ jsxs145(Box53, { width: "100%", position: "relative", children: [
    above && /* @__PURE__ */ jsxs145(Flex35, { justify: "space-between", fontStyle: "italic", mb: "4px", children: [
      /* @__PURE__ */ jsx265(
        Text31,
        {
          fontFamily: "Inter Medium",
          fontSize: leftTextFontSize,
          color: theme?.colors?.arcadiaMainGrey,
          children: leftText
        }
      ),
      /* @__PURE__ */ jsx265(
        Text31,
        {
          fontFamily: "Inter Medium",
          fontSize: rightTextFontSize,
          color: theme?.colors?.arcadiaMainGrey,
          children: rightText
        }
      )
    ] }),
    /* @__PURE__ */ jsxs145(
      "svg",
      {
        width: "100%",
        height,
        viewBox: `0 0 1000 ${height}`,
        preserveAspectRatio: "none",
        children: [
          /* @__PURE__ */ jsxs145("defs", { children: [
            /* @__PURE__ */ jsx265(
              "marker",
              {
                id: "arrowheadEnd",
                markerWidth: "8",
                markerHeight: "6",
                refX: "8",
                refY: "3",
                orient: "auto",
                markerUnits: "strokeWidth",
                children: /* @__PURE__ */ jsx265("polygon", { points: "0 0, 8 3, 0 6", fill: stroke })
              }
            ),
            /* @__PURE__ */ jsx265(
              "marker",
              {
                id: "arrowheadStart",
                markerWidth: "8",
                markerHeight: "6",
                refX: "0",
                refY: "3",
                orient: "auto",
                markerUnits: "strokeWidth",
                children: /* @__PURE__ */ jsx265("polygon", { points: "8 0, 0 3, 8 6", fill: stroke })
              }
            )
          ] }),
          /* @__PURE__ */ jsx265(
            "line",
            {
              x1: "0",
              y1: height / 2,
              x2: "1000",
              y2: height / 2,
              stroke,
              strokeWidth: "1.5",
              markerStart: "url(#arrowheadStart)",
              markerEnd: "url(#arrowheadEnd)"
            }
          )
        ]
      }
    ),
    !above && /* @__PURE__ */ jsxs145(Flex35, { justify: "space-between", fontStyle: "italic", mt: "4px", children: [
      /* @__PURE__ */ jsx265(
        Text31,
        {
          fontFamily: "Inter Medium",
          fontSize: leftTextFontSize,
          color: theme?.colors?.arcadiaMainGrey,
          children: leftText
        }
      ),
      /* @__PURE__ */ jsx265(
        Text31,
        {
          fontFamily: "Inter Medium",
          fontSize: rightTextFontSize,
          color: theme?.colors?.arcadiaMainGrey,
          children: rightText
        }
      )
    ] })
  ] });
};
var labeledArrow_default = LabeledArrow;

// src/components/DoubleFillChart/DoubleFillChart.tsx
import { useTheme as useTheme84, VStack as VStack3, Box as Box55 } from "@chakra-ui/react";

// src/components/DoubleFillChart/CustomTooltip.tsx
import { Box as Box54, Text as Text32, VStack as VStack2, HStack as HStack2, useTheme as useTheme83 } from "@chakra-ui/react";
import { jsx as jsx266, jsxs as jsxs146 } from "react/jsx-runtime";
var CustomTooltip = ({ data, title }) => {
  const theme = useTheme83();
  return /* @__PURE__ */ jsxs146(
    Box54,
    {
      bg: "white",
      border: "1px solid #ccc",
      borderRadius: "8px",
      p: "10px 14px",
      boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
      fontSize: "14px",
      color: "#000",
      children: [
        /* @__PURE__ */ jsx266(
          Text32,
          {
            fontWeight: "bold",
            mb: "6px",
            fontFamily: "Inter Medium",
            fontSize: "14px",
            color: theme?.colors?.arcadiaMainGrey,
            children: title
          }
        ),
        /* @__PURE__ */ jsxs146(VStack2, { align: "flex-start", spacing: 1, children: [
          /* @__PURE__ */ jsxs146(HStack2, { children: [
            /* @__PURE__ */ jsxs146(
              Text32,
              {
                fontFamily: "Inter Medium",
                fontSize: "12px",
                color: theme?.colors?.arcadiaMainGrey,
                children: [
                  data.value,
                  "%:"
                ]
              }
            ),
            /* @__PURE__ */ jsx266(
              Text32,
              {
                fontWeight: "bold",
                fontFamily: "Inter Medium",
                fontSize: "12px",
                color: theme?.colors?.arcadiaMainGrey,
                children: Number(data.valueTotal).toLocaleString()
              }
            )
          ] }),
          /* @__PURE__ */ jsxs146(HStack2, { children: [
            /* @__PURE__ */ jsxs146(
              Text32,
              {
                fontFamily: "Inter Medium",
                fontSize: "12px",
                color: theme?.colors?.arcadiaMainGrey,
                children: [
                  data.rest,
                  "%:"
                ]
              }
            ),
            /* @__PURE__ */ jsx266(
              Text32,
              {
                fontWeight: "bold",
                fontFamily: "Inter Medium",
                fontSize: "12px",
                color: theme?.colors?.arcadiaMainGrey,
                children: Number(data.restTotal).toLocaleString()
              }
            )
          ] })
        ] })
      ]
    }
  );
};
var CustomTooltip_default = CustomTooltip;

// src/components/DoubleFillChart/DoubleFillChart.tsx
import { jsx as jsx267, jsxs as jsxs147 } from "react/jsx-runtime";
var generateArray = (size, startDecrease, endDecrease) => {
  const result = new Array(size).fill({
    id: "value",
    value: 0
  });
  for (let i = 0; i < startDecrease; i++) {
    result[i] = {
      id: "value",
      value: 100
    };
  }
  const step = 100 / (endDecrease - startDecrease);
  for (let i = startDecrease; i < endDecrease; i++) {
    result[i] = {
      id: "value",
      value: 100 - step * (i - startDecrease)
    };
  }
  return result;
};
var DoubleFillChart = ({
  topBeneficiariesPercentage = 25,
  representOpenGapsPercentage,
  totalBeneficiaries,
  totalOpenGaps,
  top25Beneficiaries,
  representOpenGaps,
  width = "100%"
}) => {
  const chakraTheme2 = useTheme84();
  const topChart = [
    {
      id: "filled",
      value: topBeneficiariesPercentage,
      rest: Number((100 - topBeneficiariesPercentage).toFixed(2)),
      valueTotal: top25Beneficiaries,
      restTotal: totalBeneficiaries - top25Beneficiaries
    }
  ];
  const bottomChart = [
    {
      id: "empty",
      value: representOpenGapsPercentage,
      rest: Number((100 - representOpenGapsPercentage).toFixed(2)),
      valueTotal: representOpenGaps,
      restTotal: totalOpenGaps - representOpenGaps
    }
  ];
  const gradientDef = [
    {
      colors: [
        { offset: 0, color: chakraTheme2.colors.darkBlue },
        { offset: 100, color: chakraTheme2.colors.blueOne }
      ],
      id: "gradientA",
      gradientTransform: "rotate(-90 0.5 0.5)",
      type: "linearGradient"
    },
    {
      colors: [
        { offset: 0, color: chakraTheme2.colors.blueOne },
        { offset: 100, color: chakraTheme2.colors.lightBlue4 }
      ],
      id: "gradientB",
      gradientTransform: "rotate(-90 0.5 0.5)",
      type: "linearGradient"
    }
  ];
  const dataStream = generateArray(
    1e3,
    topChart[0].value * 10,
    bottomChart[0].value * 10
  );
  return /* @__PURE__ */ jsxs147(VStack3, { spacing: 0, width, "data-testid": "double-chart-container", children: [
    /* @__PURE__ */ jsx267(
      labeledArrow_default,
      {
        above: true,
        height: 20,
        stroke: "#ccc",
        leftText: "Higher HCC Beneficiaries",
        rightText: "Lower HCC Beneficiaries",
        leftTextFontSize: "12px",
        rightTextFontSize: "12px"
      }
    ),
    /* @__PURE__ */ jsx267(Box55, { height: "27px", width: "100%", children: /* @__PURE__ */ jsx267(
      ResponsiveBar3,
      {
        data: topChart,
        keys: ["value", "rest"],
        indexBy: "id",
        layout: "horizontal",
        enableGridY: false,
        axisLeft: null,
        axisBottom: null,
        padding: 0,
        borderRadius: 4,
        label: (d) => d.value === topBeneficiariesPercentage ? `${d.value}%` : "",
        labelTextColor: chakraTheme2.colors.white,
        theme: {
          fontSize: 12,
          fontFamily: chakraTheme2.fonts.body,
          labels: {
            text: {
              fontStyle: "italic",
              fontWeight: "bold"
            }
          }
        },
        innerPadding: 3,
        defs: gradientDef,
        fill: [
          { match: { value: topBeneficiariesPercentage }, id: "gradientA" },
          { match: "*", id: "gradientB" }
        ],
        tooltip: () => /* @__PURE__ */ jsx267(CustomTooltip_default, { data: topChart[0], title: "Beneficiaries" })
      }
    ) }),
    /* @__PURE__ */ jsx267(Box55, { height: "27px", width: "100%", padding: 1, children: /* @__PURE__ */ jsx267(
      ResponsiveStream,
      {
        data: dataStream,
        keys: ["value"],
        enableGridX: false,
        enableGridY: false,
        curve: "stepBefore",
        offsetType: "diverging",
        isInteractive: false,
        colors: [chakraTheme2.colors.tableHeaderBlue]
      }
    ) }),
    /* @__PURE__ */ jsx267(Box55, { height: "27px", width: "100%", children: /* @__PURE__ */ jsx267(
      ResponsiveBar3,
      {
        data: bottomChart,
        keys: ["value", "rest"],
        indexBy: "id",
        layout: "horizontal",
        colors: { scheme: "nivo" },
        enableGridY: false,
        axisLeft: null,
        axisBottom: null,
        padding: 0,
        borderRadius: 4,
        label: (d) => d.value === representOpenGapsPercentage ? `${d.value}%` : "",
        labelTextColor: chakraTheme2.colors.white,
        theme: {
          fontSize: 12,
          fontFamily: chakraTheme2.fonts.body,
          labels: {
            text: {
              fontStyle: "italic",
              fontWeight: "bold"
            }
          }
        },
        innerPadding: 3,
        defs: gradientDef,
        fill: [
          { match: { value: representOpenGapsPercentage }, id: "gradientA" },
          { match: "*", id: "gradientB" }
        ],
        tooltip: () => /* @__PURE__ */ jsx267(CustomTooltip_default, { data: bottomChart[0], title: "Open Gaps" })
      }
    ) }),
    /* @__PURE__ */ jsx267(
      labeledArrow_default,
      {
        above: false,
        height: 20,
        stroke: "#ccc",
        leftText: "More Open Gaps by Beneficiary",
        rightText: "Less Open Gaps by Beneficiary",
        leftTextFontSize: "12px",
        rightTextFontSize: "12px"
      }
    )
  ] });
};
var DoubleFillChart_default = DoubleFillChart;

// src/components/DualBar/index.tsx
import { Box as Box56, Text as Text33, Flex as Flex36 } from "@chakra-ui/react";
import { useState as useState20 } from "react";
import { jsx as jsx268, jsxs as jsxs148 } from "react/jsx-runtime";
var DualBar = ({
  topValue,
  bottomValue,
  maxValue,
  width = "100%",
  TopTooltipComponent,
  BottomTooltipComponent
}) => {
  const [showTopTooltip, setShowTopTooltip] = useState20(false);
  const [showBottomTooltip, setShowBottomTooltip] = useState20(false);
  const handleMouseEnter = (setShow) => {
    setShow(true);
  };
  const topPercentage = topValue / maxValue * 100;
  const bottomPercentage = bottomValue / maxValue * 100;
  return /* @__PURE__ */ jsxs148(Box56, { width, "data-testid": "dual-bar-container", children: [
    /* @__PURE__ */ jsxs148(Flex36, { align: "center", mb: 2, position: "relative", children: [
      /* @__PURE__ */ jsx268(
        Box56,
        {
          height: "15px",
          width: `${topPercentage}%`,
          bgImage: "repeating-linear-gradient(-45deg, #e3e3e5, #e3e3e5 6px, #e3e3e5 1px, #cbcbcc 9px)",
          mr: 2,
          borderRadius: "5px",
          "data-testid": "dual-bar-top",
          onMouseEnter: () => TopTooltipComponent && handleMouseEnter(setShowTopTooltip),
          onMouseLeave: () => setShowTopTooltip(false)
        }
      ),
      showTopTooltip && TopTooltipComponent && /* @__PURE__ */ jsx268(Box56, { position: "relative", children: /* @__PURE__ */ jsx268(TopTooltipComponent, {}) }),
      /* @__PURE__ */ jsx268(
        Text33,
        {
          fontSize: "14px",
          fontFamily: chakraTheme.fonts?.interMedium,
          color: chakraTheme.colors?.mediumGrey2,
          fontWeight: 500,
          lineHeight: "100%",
          letterSpacing: "0%",
          textAlign: "center",
          "data-testid": "dual-bar-top-value",
          children: topValue
        }
      )
    ] }),
    /* @__PURE__ */ jsxs148(Flex36, { align: "center", position: "relative", children: [
      /* @__PURE__ */ jsx268(
        Box56,
        {
          height: "15px",
          width: `${bottomPercentage}%`,
          bg: "#26ACD8",
          mr: 2,
          borderRadius: "5px",
          "data-testid": "dual-bar-bottom",
          onMouseEnter: () => BottomTooltipComponent && handleMouseEnter(setShowBottomTooltip),
          onMouseLeave: () => setShowBottomTooltip(false)
        }
      ),
      showBottomTooltip && BottomTooltipComponent && /* @__PURE__ */ jsx268(Box56, { position: "relative", children: /* @__PURE__ */ jsx268(BottomTooltipComponent, {}) }),
      /* @__PURE__ */ jsx268(
        Text33,
        {
          fontSize: "14px",
          fontFamily: chakraTheme.fonts?.interMedium,
          color: chakraTheme.colors?.arcadiaMainGrey,
          fontWeight: 500,
          lineHeight: "100%",
          letterSpacing: "0%",
          textAlign: "center",
          "data-testid": "dual-bar-bottom-value",
          children: bottomValue
        }
      )
    ] })
  ] });
};
var DualBar_default = DualBar;

// src/components/RecaptureBar/index.tsx
import { Box as Box57, Text as Text34, Flex as Flex37 } from "@chakra-ui/react";
import { useState as useState21 } from "react";
import { jsx as jsx269, jsxs as jsxs149 } from "react/jsx-runtime";
var RecaptureBar = ({
  recaptured,
  postChartRecaptured,
  notRecaptured,
  width = "100%",
  RecapturedTooltip,
  PostChartTooltip,
  NotRecapturedTooltip
}) => {
  const [showTooltip, setShowTooltip] = useState21("none");
  const handleMouseEnter = (section) => {
    setShowTooltip(section);
  };
  const total = recaptured + postChartRecaptured + notRecaptured;
  const recapturedPercentage = recaptured / total * 100;
  const postChartPercentage = postChartRecaptured / total * 100;
  const notRecapturedPercentage = notRecaptured / total * 100;
  return /* @__PURE__ */ jsxs149(
    Box57,
    {
      width,
      "data-testid": "recapture-bar-container",
      position: "relative",
      children: [
        /* @__PURE__ */ jsxs149(Flex37, { align: "center", height: "10px", gap: "1px", children: [
          /* @__PURE__ */ jsx269(
            Box57,
            {
              height: "100%",
              width: `${recapturedPercentage}%`,
              bgImage: "repeating-linear-gradient(-45deg, #B6E8CE, #B6E8CE 6px, #b0d3c1 1px, #b0d3c1 8px)",
              borderRadius: "5px",
              "data-testid": "recapture-bar-recaptured",
              onMouseEnter: () => handleMouseEnter("recaptured"),
              onMouseLeave: () => setShowTooltip("none"),
              position: "relative"
            }
          ),
          /* @__PURE__ */ jsx269(
            Box57,
            {
              height: "100%",
              width: `${postChartPercentage}%`,
              bg: "#B6E8CE",
              borderRadius: "5px",
              "data-testid": "recapture-bar-post-chart",
              onMouseEnter: () => handleMouseEnter("postChart"),
              onMouseLeave: () => setShowTooltip("none"),
              position: "relative"
            }
          ),
          /* @__PURE__ */ jsx269(
            Box57,
            {
              height: "100%",
              width: `${notRecapturedPercentage}%`,
              bg: "#EDC5CE",
              borderRadius: "5px",
              "data-testid": "recapture-bar-not-recaptured",
              onMouseEnter: () => handleMouseEnter("notRecaptured"),
              onMouseLeave: () => setShowTooltip("none"),
              position: "relative"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs149(
          Text34,
          {
            position: "absolute",
            left: "0",
            top: "-20px",
            fontSize: "12px",
            fontFamily: chakraTheme.fonts?.interMedium,
            color: chakraTheme.colors?.arcadiaMainGrey,
            children: [
              "Total recaptured: ",
              /* @__PURE__ */ jsx269("strong", { children: `${Math.round(recapturedPercentage)}%` })
            ]
          }
        ),
        /* @__PURE__ */ jsxs149(
          Text34,
          {
            position: "absolute",
            right: "0",
            top: "-20px",
            fontSize: "12px",
            fontFamily: chakraTheme.fonts?.interMedium,
            color: chakraTheme.colors?.arcadiaMainGrey,
            children: [
              "Not recaptured: ",
              /* @__PURE__ */ jsx269("strong", { children: `${Math.round(notRecapturedPercentage)}%` })
            ]
          }
        ),
        /* @__PURE__ */ jsx269(
          Text34,
          {
            position: "absolute",
            left: "0",
            bottom: "-20px",
            fontSize: "12px",
            fontFamily: chakraTheme.fonts?.interMedium,
            color: chakraTheme.colors?.arcadiaMainGrey,
            children: `$${notRecaptured.toLocaleString()}`
          }
        ),
        /* @__PURE__ */ jsx269(
          Text34,
          {
            position: "absolute",
            right: "0",
            bottom: "-20px",
            fontSize: "12px",
            fontFamily: chakraTheme.fonts?.interMedium,
            color: chakraTheme.colors?.arcadiaMainGrey,
            children: `$${total.toLocaleString()}`
          }
        ),
        showTooltip !== "none" && /* @__PURE__ */ jsxs149(Box57, { position: "relative", children: [
          showTooltip === "recaptured" && RecapturedTooltip && /* @__PURE__ */ jsx269(RecapturedTooltip, {}),
          showTooltip === "postChart" && PostChartTooltip && /* @__PURE__ */ jsx269(PostChartTooltip, {}),
          showTooltip === "notRecaptured" && NotRecapturedTooltip && /* @__PURE__ */ jsx269(NotRecapturedTooltip, {})
        ] })
      ]
    }
  );
};
var RecaptureBar_default = RecaptureBar;

// src/pages/ErrorPage/ErrorPage.tsx
import { Flex as Flex38, Text as Text35, useTheme as useTheme85 } from "@chakra-ui/react";
import { useEffect as useEffect15 } from "react";

// src/pages/ErrorPage/config.ts
var SOMETHING_WRONG_TEXT = "Something went wrong";
var PLEASE_CONTACT_MEMBER_SERVICES_TEXT = "We are experiencing some issues. If the problem persists, please contact CareJourney's Member Services team.";
var GO_TO_CJ_TEXT = "Go to CareJourney Home";

// src/pages/ErrorPage/ErrorPage.tsx
import { jsx as jsx270, jsxs as jsxs150 } from "react/jsx-runtime";
var ErrorPage = ({
  handleTrack,
  handleClick,
  errorTitle,
  errorSubtitle,
  useArcadiaLogo = false
}) => {
  const theme = useTheme85();
  useEffect15(() => {
    if (handleTrack) {
      handleTrack();
    }
  }, []);
  const handleButtonClick = () => {
    if (handleClick) {
      handleClick();
    }
  };
  return /* @__PURE__ */ jsx270(
    Flex38,
    {
      height: "calc(100vh - 78px)",
      width: "100%",
      backgroundColor: theme.colors?.explorerBackground,
      alignItems: "center",
      justifyContent: "space-between",
      direction: "column",
      children: /* @__PURE__ */ jsxs150(
        Flex38,
        {
          height: "calc(100vh - 47px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          children: [
            useArcadiaLogo ? /* @__PURE__ */ jsx270(ArcadiaLogoIcon_default, { dataTestId: "error--arcadia-logo-icon" }) : /* @__PURE__ */ jsx270(CJIcon_default, { dataTestId: "error--cj-icon" }),
            /* @__PURE__ */ jsx270(
              Text35,
              {
                marginTop: "30px",
                textStyle: "regular-24-30",
                textAlign: "center",
                color: theme.colors?.blueOne,
                "data-testid": "error--title",
                children: errorTitle || SOMETHING_WRONG_TEXT
              }
            ),
            /* @__PURE__ */ jsx270(
              NoConnectionIcon_default,
              {
                dataTestId: "error--no-connection-icon",
                margin: "30px 0"
              }
            ),
            /* @__PURE__ */ jsx270(
              Text35,
              {
                width: "421px",
                textStyle: "regular-14-16",
                textAlign: "center",
                color: theme.colors?.mediumGrey,
                "data-testid": "error--subtitle",
                children: errorSubtitle || PLEASE_CONTACT_MEMBER_SERVICES_TEXT
              }
            ),
            /* @__PURE__ */ jsx270(
              Button_default,
              {
                marginTop: "30px",
                width: "192px",
                height: "28px",
                backgroundColor: theme.colors?.blueOne,
                textStyle: "regular-14-14",
                "data-testid": "error--home-button",
                onClick: handleButtonClick,
                children: GO_TO_CJ_TEXT
              }
            )
          ]
        }
      )
    }
  );
};
var ErrorPage_default = ErrorPage;

// src/pages/LoadingPage/LoadingPage.tsx
import { Flex as Flex39 } from "@chakra-ui/react";
import { jsx as jsx271 } from "react/jsx-runtime";
var LoadingPage = ({
  useArcadiaLogo = false
}) => /* @__PURE__ */ jsx271(
  Flex39,
  {
    justifyContent: "center",
    alignItems: "center",
    "data-testid": "loading--container",
    minHeight: "calc(100vh - 72px)",
    children: useArcadiaLogo ? /* @__PURE__ */ jsx271(ArcadiaLoadingIcon_default, { height: 75, dataTestId: "arcadia-loading-icon" }) : /* @__PURE__ */ jsx271(
      CJLoadingIcon_default,
      {
        width: 200,
        height: 200,
        dataTestId: "loading--cj-loading-icon"
      }
    )
  }
);
var LoadingPage_default = LoadingPage;

// src/pages/NoAccessPage/NoAccessPage.tsx
import { Flex as Flex40, Text as Text36, useTheme as useTheme86 } from "@chakra-ui/react";
import { useEffect as useEffect16 } from "react";

// src/pages/NoAccessPage/config.ts
var NO_PERMISSION_TEXT = "We are sorry, but you do not have permission to view this page.";
var PLEASE_CONTACT_MEMBER_SERVICES_TEXT2 = "Please contact us to learn how to get access.";
var CONTACT_US_TEXT = "Contact Us";

// src/pages/NoAccessPage/NoAccessPage.tsx
import { jsx as jsx272, jsxs as jsxs151 } from "react/jsx-runtime";
var NoAccessPage = ({ handleTrack, handleClick, useArcadiaLogo = false }) => {
  const theme = useTheme86();
  useEffect16(() => {
    if (handleTrack) {
      handleTrack();
    }
  }, []);
  const handleButtonClick = () => {
    if (handleClick) {
      handleClick();
    }
  };
  return /* @__PURE__ */ jsx272(
    Flex40,
    {
      w: "100%",
      h: "calc(100vh - 78px)",
      direction: "column",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: theme.colors?.explorerBackground,
      children: /* @__PURE__ */ jsxs151(
        Flex40,
        {
          height: "calc(100vh - 47px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          children: [
            useArcadiaLogo ? /* @__PURE__ */ jsx272(
              ArcadiaLogoIcon_default,
              {
                margin: "30px 0",
                dataTestId: "no-access--arcadia-logo-icon"
              }
            ) : /* @__PURE__ */ jsx272(
              CJIconByArcadia_default,
              {
                width: 292,
                height: 42,
                margin: "30px 0",
                dataTestId: "no-access--cj-icon"
              }
            ),
            /* @__PURE__ */ jsx272(
              Text36,
              {
                width: "498px",
                color: theme.colors?.blueOne,
                textStyle: "regular-24-30",
                "data-testid": "no-access--title",
                textAlign: "center",
                children: NO_PERMISSION_TEXT
              }
            ),
            /* @__PURE__ */ jsx272(
              ExpirationIcon_default,
              {
                margin: "30px 0",
                dataTestId: "no-access--expiration-icon"
              }
            ),
            /* @__PURE__ */ jsx272(
              Text36,
              {
                width: "421px",
                color: theme.colors?.mediumGrey,
                textStyle: "regular-14-16",
                "data-testid": "no-access--subtitle",
                textAlign: "center",
                children: PLEASE_CONTACT_MEMBER_SERVICES_TEXT2
              }
            ),
            /* @__PURE__ */ jsx272(
              Button_default,
              {
                width: "106px",
                height: "28px",
                backgroundColor: theme.colors?.blueTwo,
                margin: "30px 0",
                dataTestId: "no-access--home-button",
                onClick: handleButtonClick,
                children: CONTACT_US_TEXT
              }
            )
          ]
        }
      )
    }
  );
};
var NoAccessPage_default = NoAccessPage;

// src/pages/NotFoundPage/NotFoundPage.tsx
import { Box as Box58, Flex as Flex41, Text as Text37, useTheme as useTheme87 } from "@chakra-ui/react";
import { useEffect as useEffect17 } from "react";

// src/pages/NotFoundPage/config.ts
var FOUR_0_FOUR_TEXT = "404";
var NOT_FOUND_TEXT = "Page not found";
var PAGE_NOT_FOUND_TEXT = "Sorry! The page you are looking for was not found.";
var BACK_TO_CAREJOURNEY_ARCADIA_TEXT = "Back to CareJourney by Arcadia Home";

// src/pages/NotFoundPage/NotFoundPage.tsx
import { jsx as jsx273, jsxs as jsxs152 } from "react/jsx-runtime";
var NotFoundPage = ({ handleTrack, handleClick }) => {
  const theme = useTheme87();
  useEffect17(() => {
    if (handleTrack) {
      handleTrack();
    }
  }, []);
  const handleButtonClick = () => {
    if (handleClick) {
      handleClick();
    }
  };
  return /* @__PURE__ */ jsx273(
    Flex41,
    {
      w: "100%",
      h: "calc(100vh - 78px)",
      direction: "column",
      justifyContent: "space-between",
      alignItems: "center",
      backgroundColor: theme.colors?.newBackgroundGrey,
      children: /* @__PURE__ */ jsxs152(
        Flex41,
        {
          height: "calc(100vh - 47px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          children: [
            /* @__PURE__ */ jsx273(Box58, { position: "absolute", children: /* @__PURE__ */ jsx273(
              Text37,
              {
                position: "relative",
                top: "-120px",
                textStyle: "inter-medium-250-340",
                color: "rgba(234, 235, 236, 0.5);",
                "data-testid": "not-found--404",
                textAlign: "center",
                children: FOUR_0_FOUR_TEXT
              }
            ) }),
            /* @__PURE__ */ jsx273(
              Text37,
              {
                color: theme.colors?.darkBlue,
                textStyle: "inter-semiBold-40-40",
                "data-testid": "not-found--title",
                marginTop: "150px",
                zIndex: "1",
                textAlign: "center",
                children: NOT_FOUND_TEXT
              }
            ),
            /* @__PURE__ */ jsx273(
              NoResultsIcon_default,
              {
                margin: "45px 0",
                dataTestId: "not-found--no-results-icon"
              }
            ),
            /* @__PURE__ */ jsx273(
              Text37,
              {
                color: theme.colors?.mediumGrey,
                textStyle: "inter-regular-14-17",
                "data-testid": "not-found--subtitle",
                textAlign: "center",
                children: PAGE_NOT_FOUND_TEXT
              }
            ),
            /* @__PURE__ */ jsx273(
              Button_default,
              {
                width: "100%",
                height: "32px",
                borderRadius: "6px",
                backgroundColor: theme.colors?.blueTwo,
                margin: "37px 0 30px 0",
                dataTestId: "not-found--home-button",
                onClick: handleButtonClick,
                fontFamily: theme.fonts.interSemibold,
                children: BACK_TO_CAREJOURNEY_ARCADIA_TEXT
              }
            )
          ]
        }
      )
    }
  );
};
var NotFoundPage_default = NotFoundPage;

// src/pages/PasswordExpiredPage/PasswordExpiredPage.tsx
import { Flex as Flex42, Text as Text38, useTheme as useTheme88 } from "@chakra-ui/react";
import { useEffect as useEffect18 } from "react";

// src/pages/PasswordExpiredPage/config.ts
var PASSWORD_EXPIRED_TEXT = "Your password has expired";
var CHECK_INBOX_TEXT = "Please check your inbox for an email to reset your password.";
var CHECK_SPAM_TEXT = "In case you have not received the email, please check your spam folder or contact your member services representative.";
var LOGIN_TO_CJ_TEXT = "Login to CareJourney Home";

// src/pages/PasswordExpiredPage/PasswordExpiredPage.tsx
import { jsx as jsx274, jsxs as jsxs153 } from "react/jsx-runtime";
var PasswordExpiredPage = ({
  handleTrack,
  handleClick,
  useArcadiaLogo = false
}) => {
  const theme = useTheme88();
  useEffect18(() => {
    if (handleTrack) {
      handleTrack();
    }
  }, []);
  const handleButtonClick = () => {
    if (handleClick) {
      handleClick();
    }
  };
  return /* @__PURE__ */ jsx274(
    Flex42,
    {
      height: "calc(100vh - 78px)",
      width: "100%",
      backgroundColor: theme.colors?.newBackgroundGrey,
      alignItems: "center",
      justifyContent: "space-between",
      direction: "column",
      children: /* @__PURE__ */ jsxs153(
        Flex42,
        {
          height: "calc(100vh - 47px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          children: [
            useArcadiaLogo ? /* @__PURE__ */ jsx274(ArcadiaLogoIcon_default, { dataTestId: "password-expired--arcadia-logo-icon" }) : /* @__PURE__ */ jsx274(CJIcon_default, { dataTestId: "password-expired--cj-icon" }),
            /* @__PURE__ */ jsx274(
              Text38,
              {
                marginTop: "30px",
                textStyle: "inter-regular-24-30",
                textAlign: "center",
                color: theme.colors?.blueOne,
                "data-testid": "password-expired--title",
                children: PASSWORD_EXPIRED_TEXT
              }
            ),
            /* @__PURE__ */ jsx274(
              ExpiredIcon_default,
              {
                dataTestId: "password-expired--expired-icon",
                margin: "30px 0"
              }
            ),
            /* @__PURE__ */ jsx274(
              Text38,
              {
                textStyle: "inter-medium-14-17",
                textAlign: "center",
                color: theme.colors?.mediumGrey,
                "data-testid": "password-expired--subtitle",
                children: CHECK_INBOX_TEXT
              }
            ),
            /* @__PURE__ */ jsx274(
              Text38,
              {
                width: "421px",
                marginTop: "19px",
                textStyle: "inter-regular-12-16",
                textAlign: "center",
                color: theme.colors?.mediumGrey,
                "data-testid": "password-expired--check-spam",
                children: CHECK_SPAM_TEXT
              }
            ),
            /* @__PURE__ */ jsx274(
              Button_default,
              {
                marginTop: "30px",
                width: "100%",
                backgroundColor: theme.colors?.blueTwo,
                textStyle: "inter-semiBold-14-17",
                "data-testid": "password-expired--home-button",
                onClick: handleButtonClick,
                height: "32px",
                borderRadius: "6px",
                fontFamily: theme.fonts.interSemibold,
                children: LOGIN_TO_CJ_TEXT
              }
            )
          ]
        }
      )
    }
  );
};
var PasswordExpiredPage_default = PasswordExpiredPage;

// src/hooks/useActive/index.ts
import { useRef as useRef12, useEffect as useEffect19, useState as useState22 } from "react";
var useActive = () => {
  const [value, setValue] = useState22(false);
  const ref = useRef12(null);
  const handleMouseDown = () => setValue(true);
  const handleMouseUp = () => setValue(false);
  useEffect19(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener("mousedown", handleMouseDown);
      node.addEventListener("mouseup", handleMouseUp);
      return () => {
        node.removeEventListener("mousedown", handleMouseDown);
        node.removeEventListener("mouseup", handleMouseUp);
      };
    } else {
      return void 0;
    }
  }, [ref.current]);
  return [ref, value];
};

// src/hooks/useHover/index.ts
import { useRef as useRef13, useEffect as useEffect20, useState as useState23 } from "react";
var useHover = () => {
  const [value, setValue] = useState23(false);
  const ref = useRef13(null);
  const handleMouseOver = () => setValue(true);
  const handleMouseLeave = () => setValue(false);
  useEffect20(() => {
    const node = ref.current;
    if (node) {
      node.addEventListener("mouseover", handleMouseOver);
      node.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        node.removeEventListener("mouseover", handleMouseOver);
        node.removeEventListener("mouseleave", handleMouseLeave);
      };
    } else {
      return void 0;
    }
  }, [ref.current]);
  return [ref, value];
};

// src/index.tsx
import {
  Marker as Marker3,
  Popup,
  Tooltip as Tooltip4
} from "react-leaflet";
export {
  AlertTriangleFillIcon_default as AlertTriangleFillIcon,
  AngleIcon_default as AngleIcon,
  ArcadiaFooterIcon_default as ArcadiaFooterIcon,
  ArcadiaIcon_default as ArcadiaIcon,
  ArcadiaLoadingIcon_default as ArcadiaLoadingIcon,
  ArcadiaLogoIcon_default as ArcadiaLogoIcon,
  ArcadiaMenuIcon_default as ArcadiaMenuIcon,
  ArcadiaVerticalLogoIcon_default as ArcadiaVerticalLogoIcon,
  ArrowIcon_default as ArrowIcon,
  BarGraphic_default as BarGraphic,
  BaseMapPopup_default as BaseMapPopup,
  Breadcrumbs_default as Breadcrumbs,
  BuildANetworkIcon_default as BuildANetworkIcon,
  Button_default as Button,
  CBSAIcon_default as CBSAIcon,
  CJAnimatedLogoBreath_default as CJAnimatedLogoBreath,
  CJIcon_default as CJIcon,
  CJIconByArcadia_default as CJIconByArcadia,
  CJLoadingIcon_default as CJLoadingIcon,
  CJLogoIcon_default as CJLogoIcon,
  CJPieGraph_default as CJPieGraph,
  CardiologyIcon_default as CardiologyIcon,
  CareAcceleratorIcon_default as CareAcceleratorIcon,
  CareRetentionIcon_default as CareRetentionIcon,
  CheckIcon_default as CheckIcon,
  CheckSquareIcon_default as CheckSquareIcon,
  CheckUpMedicalReportClipBoardIcon_default as CheckUpMedicalReportClipBoardIcon,
  Checkbox_default as Checkbox,
  CheckboxFilter_default as CheckboxFilter,
  ChipsSkeleton_default as ChipsSkeleton,
  CircularDollarSignIcon_default as CircularDollarSignIcon,
  CityIcon_default as CityIcon,
  ClassicIcon_default as ClassicIcon,
  ClinicalPerformanceManagementIcon_default as ClinicalPerformanceManagementIcon,
  CloseIcon_default as CloseIcon,
  CloseSquareIcon_default as CloseSquareIcon,
  CodingIcon_default as CodingIcon,
  CohortsIcon_default as CohortsIcon,
  CommunityIcon_default as CommunityIcon,
  ComparisonBar_default as ComparisonBar,
  CountyIcon_default as CountyIcon,
  CSVModal_default as CsvModal,
  Accordion_default as DVAccordion,
  DashboardIcon_default as DashboardIcon,
  DataGrid_default as DataGrid,
  DataGridSkeleton_default as DataGridSkeleton,
  DataIcon_default as DataIcon,
  DataSourcesIcon_default as DataSourcesIcon,
  DataVisualizationIcon_default as DataVisualizationIcon,
  DataverseReportCard_default as DataverseReportCard,
  DataverseYourDataIcon_default as DataverseYourDataIcon,
  DetailMarker_default as DetailMarker,
  DoctorMaleIcon_default as DoctorMaleIcon,
  DotIcon_default as DotIcon,
  DoubleFillChart_default as DoubleFillChart,
  DualBar_default as DualBar,
  EmptyFolderError_default as EmptyFolderError,
  EmptyFolderIcon_default as EmptyFolderIcon,
  EmptyStateIcon_default as EmptyStateIcon,
  ErrorPage_default as ErrorPage,
  ExpirationIcon_default as ExpirationIcon,
  ExpiredIcon_default as ExpiredIcon,
  EyeIcon_default as EyeIcon,
  FilterChip_default as FilterChip,
  FilterIcon_default as FilterIcon,
  FilterPlusIcon_default as FilterPlusIcon,
  FinancialBenchmarkingIcon_default as FinancialBenchmarkingIcon,
  Footer_default as Footer,
  Glossary_default as Glossary,
  GzipFileIcon_default as GzipFileIcon,
  HomeTile_default as HomeTile,
  HospitalSignIcon_default as HospitalSignIcon,
  InfoIcon_default as InfoIcon,
  JointsIcon_default as JointsIcon,
  KidneysIcon_default as KidneysIcon,
  L4 as L,
  LifeScienceStrategyIcon_default as LifeScienceStrategyIcon,
  ListIcon_default as ListIcon,
  LoadingOverlay_default as LoadingOverlay,
  LoadingPage_default as LoadingPage,
  LocationIcon_default2 as LocationIcon,
  LockIcon_default as LockIcon,
  LogoutIcon_default as LogoutIcon,
  MapComponent_default2 as MapComponent,
  Marker3 as MapComponentMarker,
  Popup as MapComponentPopup,
  Tooltip4 as MapComponentTooltip,
  MapFilterTag_default as MapFilterTag,
  Marker_default as Marker,
  MarketIntelligenceIcon_default as MarketIntelligenceIcon,
  MinusIcon_default as MinusIcon,
  NetworkIcon_default as NetworkIcon,
  NetworkManagementIcon_default as NetworkManagementIcon,
  NoAccessPage_default as NoAccessPage,
  NoConnectionIcon_default as NoConnectionIcon,
  NoResultsIcon_default as NoResultsIcon,
  NotFoundPage_default as NotFoundPage,
  Pagination_default as Pagination,
  PasswordExpiredPage_default as PasswordExpiredPage,
  PencilIcon_default as PencilIcon,
  PlusIcon_default as PlusIcon,
  PracticeGroupIcon_default as PracticeGroupIcon,
  ProviderPerformanceIcon_default as ProviderPerformanceIcon,
  QuestionIcon_default as QuestionIcon,
  RadioGroupWithIcon_default as RadioGroupWithIcon,
  Range_default as RangeComponent,
  RecaptureBar_default as RecaptureBar,
  RedirectIcon_default as RedirectIcon,
  RelatedReportList_default as RelatedReportList,
  RepeatIcon_default as RepeatIcon,
  ReportCard_default as ReportCard,
  RibbonIcon_default as RibbonIcon,
  RiskContractIcon_default as RiskContractIcon,
  Sankey,
  ScoreTableIcon_default as ScoreTableIcon,
  SearchIcon_default as SearchIcon,
  Select_default as Select,
  SimpleRadialBarChart_default as SimpleRadialBarChart,
  SortColumnIcon_default as SortColumnIcon,
  SpecialtyReportsList_default as SpecialtyReportsList,
  StarIcon_default as StarIcon,
  StateIcon_default as StateIcon,
  StethoscopeIcon_default as StethoscopeIcon,
  StethoscopeIconSolid_default as StethoscopeIconSolid,
  StomachIcon_default as StomachIcon,
  SuiteIcon_default as SuiteIcon,
  Sunburst_default as Sunburst,
  SwitchCheckbox_default as SwitchCheckbox,
  TabNav_default as TabNav,
  Table_default as Table,
  TableBody_default as TableBody,
  TableCell_default as TableCell,
  TableHead_default as TableHead,
  TableRow_default as TableRow,
  TableTitle_default as TableTitle,
  Tooltip_default as Tooltip,
  TrendLine_default as TrendLine,
  TriangleIcon_default as TriangleIcon,
  USBaseMap_default as USBaseMap,
  UploadIcon_default as UploadIcon,
  UserGroupIcon_default as UserGroupIcon,
  UserIcon_default as UserIcon,
  WarningIcon_default as WarningIcon,
  WarningTriangleIcon_default as WarningTriangleIcon,
  WebinarIcon_default as WebinarIcon,
  ZipCodeIcon_default as ZipCodeIcon,
  ZipFileIcon_default as ZipFileIcon,
  chakraTheme,
  countyMarkers,
  stateMarkers,
  styledComponentsTheme,
  useActive,
  useClickAway,
  useEllipsisTooltip,
  useHover,
  useWindowSize
};
